/*!
 * jQuery JavaScript Library v3.5.1
 * https://jquery.com/
 *
 * Includes Sizzle.js
 * https://sizzlejs.com/
 *
 * Copyright JS Foundation and other contributors
 * Released under the MIT license
 * https://jquery.org/license
 *
 * Date: 2020-05-04T22:49Z
 */
(function (global, factory) {
  'use strict';

  if (typeof module === 'object' && typeof module.exports === 'object') {
    // For CommonJS and CommonJS-like environments where a proper `window`
    // is present, execute the factory and get jQuery.
    // For environments that do not have a `window` with a `document`
    // (such as Node.js), expose a factory as module.exports.
    // This accentuates the need for the creation of a real `window`.
    // e.g. var jQuery = require("jquery")(window);
    // See ticket #14549 for more info.
    module.exports = global.document
      ? factory(global, true)
      : function (w) {
          if (!w.document) {
            throw new Error('jQuery requires a window with a document');
          }
          return factory(w);
        };
  } else {
    factory(global);
  }

  // Pass this if window is not defined yet
})(typeof window !== 'undefined' ? window : this, function (window, noGlobal) {
  // Edge <= 12 - 13+, Firefox <=18 - 45+, IE 10 - 11, Safari 5.1 - 9+, iOS 6 - 9.1
  // throw exceptions when non-strict code (e.g., ASP.NET 4.5) accesses strict mode
  // arguments.callee.caller (trac-13335). But as of jQuery 3.0 (2016), strict mode should be common
  // enough that all such attempts are guarded in a try block.
  'use strict';

  var arr = [];

  var getProto = Object.getPrototypeOf;

  var slice = arr.slice;

  var flat = arr.flat
    ? function (array) {
        return arr.flat.call(array);
      }
    : function (array) {
        return arr.concat.apply([], array);
      };

  var push = arr.push;

  var indexOf = arr.indexOf;

  var class2type = {};

  var toString = class2type.toString;

  var hasOwn = class2type.hasOwnProperty;

  var fnToString = hasOwn.toString;

  var ObjectFunctionString = fnToString.call(Object);

  var support = {};

  var isFunction = function isFunction(obj) {
    // Support: Chrome <=57, Firefox <=52
    // In some browsers, typeof returns "function" for HTML <object> elements
    // (i.e., `typeof document.createElement( "object" ) === "function"`).
    // We don't want to classify *any* DOM node as a function.
    return typeof obj === 'function' && typeof obj.nodeType !== 'number';
  };

  var isWindow = function isWindow(obj) {
    return obj != null && obj === obj.window;
  };

  var document = window.document;

  var preservedScriptAttributes = {
    type: true,
    src: true,
    nonce: true,
    noModule: true
  };

  function DOMEval(code, node, doc) {
    doc = doc || document;

    var i,
      val,
      script = doc.createElement('script');

    script.text = code;
    if (node) {
      for (i in preservedScriptAttributes) {
        // Support: Firefox 64+, Edge 18+
        // Some browsers don't support the "nonce" property on scripts.
        // On the other hand, just using `getAttribute` is not enough as
        // the `nonce` attribute is reset to an empty string whenever it
        // becomes browsing-context connected.
        // See https://github.com/whatwg/html/issues/2369
        // See https://html.spec.whatwg.org/#nonce-attributes
        // The `node.getAttribute` check was added for the sake of
        // `jQuery.globalEval` so that it can fake a nonce-containing node
        // via an object.
        val = node[i] || (node.getAttribute && node.getAttribute(i));
        if (val) {
          script.setAttribute(i, val);
        }
      }
    }
    doc.head.appendChild(script).parentNode.removeChild(script);
  }

  function toType(obj) {
    if (obj == null) {
      return obj + '';
    }

    // Support: Android <=2.3 only (functionish RegExp)
    return typeof obj === 'object' || typeof obj === 'function' ? class2type[toString.call(obj)] || 'object' : typeof obj;
  }
  /* global Symbol */
  // Defining this global in .eslintrc.json would create a danger of using the global
  // unguarded in another place, it seems safer to define global only for this module

  var version = '3.5.1',
    // Define a local copy of jQuery
    jQuery = function (selector, context) {
      // The jQuery object is actually just the init constructor 'enhanced'
      // Need init if jQuery is called (just allow error to be thrown if not included)
      return new jQuery.fn.init(selector, context);
    };

  jQuery.fn = jQuery.prototype = {
    // The current version of jQuery being used
    jquery: version,

    constructor: jQuery,

    // The default length of a jQuery object is 0
    length: 0,

    toArray: function () {
      return slice.call(this);
    },

    // Get the Nth element in the matched element set OR
    // Get the whole matched element set as a clean array
    get: function (num) {
      // Return all the elements in a clean array
      if (num == null) {
        return slice.call(this);
      }

      // Return just the one element from the set
      return num < 0 ? this[num + this.length] : this[num];
    },

    // Take an array of elements and push it onto the stack
    // (returning the new matched element set)
    pushStack: function (elems) {
      // Build a new jQuery matched element set
      var ret = jQuery.merge(this.constructor(), elems);

      // Add the old object onto the stack (as a reference)
      ret.prevObject = this;

      // Return the newly-formed element set
      return ret;
    },

    // Execute a callback for every element in the matched set.
    each: function (callback) {
      return jQuery.each(this, callback);
    },

    map: function (callback) {
      return this.pushStack(
        jQuery.map(this, function (elem, i) {
          return callback.call(elem, i, elem);
        })
      );
    },

    slice: function () {
      return this.pushStack(slice.apply(this, arguments));
    },

    first: function () {
      return this.eq(0);
    },

    last: function () {
      return this.eq(-1);
    },

    even: function () {
      return this.pushStack(
        jQuery.grep(this, function (_elem, i) {
          return (i + 1) % 2;
        })
      );
    },

    odd: function () {
      return this.pushStack(
        jQuery.grep(this, function (_elem, i) {
          return i % 2;
        })
      );
    },

    eq: function (i) {
      var len = this.length,
        j = +i + (i < 0 ? len : 0);
      return this.pushStack(j >= 0 && j < len ? [this[j]] : []);
    },

    end: function () {
      return this.prevObject || this.constructor();
    },

    // For internal use only.
    // Behaves like an Array's method, not like a jQuery method.
    push: push,
    sort: arr.sort,
    splice: arr.splice
  };

  jQuery.extend = jQuery.fn.extend = function () {
    var options,
      name,
      src,
      copy,
      copyIsArray,
      clone,
      target = arguments[0] || {},
      i = 1,
      length = arguments.length,
      deep = false;

    // Handle a deep copy situation
    if (typeof target === 'boolean') {
      deep = target;

      // Skip the boolean and the target
      target = arguments[i] || {};
      i++;
    }

    // Handle case when target is a string or something (possible in deep copy)
    if (typeof target !== 'object' && !isFunction(target)) {
      target = {};
    }

    // Extend jQuery itself if only one argument is passed
    if (i === length) {
      target = this;
      i--;
    }

    for (; i < length; i++) {
      // Only deal with non-null/undefined values
      if ((options = arguments[i]) != null) {
        // Extend the base object
        for (name in options) {
          copy = options[name];

          // Prevent Object.prototype pollution
          // Prevent never-ending loop
          if (name === '__proto__' || target === copy) {
            continue;
          }

          // Recurse if we're merging plain objects or arrays
          if (deep && copy && (jQuery.isPlainObject(copy) || (copyIsArray = Array.isArray(copy)))) {
            src = target[name];

            // Ensure proper type for the source value
            if (copyIsArray && !Array.isArray(src)) {
              clone = [];
            } else if (!copyIsArray && !jQuery.isPlainObject(src)) {
              clone = {};
            } else {
              clone = src;
            }
            copyIsArray = false;

            // Never move original objects, clone them
            target[name] = jQuery.extend(deep, clone, copy);

            // Don't bring in undefined values
          } else if (copy !== undefined) {
            target[name] = copy;
          }
        }
      }
    }

    // Return the modified object
    return target;
  };

  jQuery.extend({
    // Unique for each copy of jQuery on the page
    expando: 'jQuery' + (version + Math.random()).replace(/\D/g, ''),

    // Assume jQuery is ready without the ready module
    isReady: true,

    error: function (msg) {
      throw new Error(msg);
    },

    noop: function () {},

    isPlainObject: function (obj) {
      var proto, Ctor;

      // Detect obvious negatives
      // Use toString instead of jQuery.type to catch host objects
      if (!obj || toString.call(obj) !== '[object Object]') {
        return false;
      }

      proto = getProto(obj);

      // Objects with no prototype (e.g., `Object.create( null )`) are plain
      if (!proto) {
        return true;
      }

      // Objects with prototype are plain iff they were constructed by a global Object function
      Ctor = hasOwn.call(proto, 'constructor') && proto.constructor;
      return typeof Ctor === 'function' && fnToString.call(Ctor) === ObjectFunctionString;
    },

    isEmptyObject: function (obj) {
      var name;

      for (name in obj) {
        return false;
      }
      return true;
    },

    // Evaluates a script in a provided context; falls back to the global one
    // if not specified.
    globalEval: function (code, options, doc) {
      DOMEval(code, { nonce: options && options.nonce }, doc);
    },

    each: function (obj, callback) {
      var length,
        i = 0;

      if (isArrayLike(obj)) {
        length = obj.length;
        for (; i < length; i++) {
          if (callback.call(obj[i], i, obj[i]) === false) {
            break;
          }
        }
      } else {
        for (i in obj) {
          if (callback.call(obj[i], i, obj[i]) === false) {
            break;
          }
        }
      }

      return obj;
    },

    // results is for internal usage only
    makeArray: function (arr, results) {
      var ret = results || [];

      if (arr != null) {
        if (isArrayLike(Object(arr))) {
          jQuery.merge(ret, typeof arr === 'string' ? [arr] : arr);
        } else {
          push.call(ret, arr);
        }
      }

      return ret;
    },

    inArray: function (elem, arr, i) {
      return arr == null ? -1 : indexOf.call(arr, elem, i);
    },

    // Support: Android <=4.0 only, PhantomJS 1 only
    // push.apply(_, arraylike) throws on ancient WebKit
    merge: function (first, second) {
      var len = +second.length,
        j = 0,
        i = first.length;

      for (; j < len; j++) {
        first[i++] = second[j];
      }

      first.length = i;

      return first;
    },

    grep: function (elems, callback, invert) {
      var callbackInverse,
        matches = [],
        i = 0,
        length = elems.length,
        callbackExpect = !invert;

      // Go through the array, only saving the items
      // that pass the validator function
      for (; i < length; i++) {
        callbackInverse = !callback(elems[i], i);
        if (callbackInverse !== callbackExpect) {
          matches.push(elems[i]);
        }
      }

      return matches;
    },

    // arg is for internal usage only
    map: function (elems, callback, arg) {
      var length,
        value,
        i = 0,
        ret = [];

      // Go through the array, translating each of the items to their new values
      if (isArrayLike(elems)) {
        length = elems.length;
        for (; i < length; i++) {
          value = callback(elems[i], i, arg);

          if (value != null) {
            ret.push(value);
          }
        }

        // Go through every key on the object,
      } else {
        for (i in elems) {
          value = callback(elems[i], i, arg);

          if (value != null) {
            ret.push(value);
          }
        }
      }

      // Flatten any nested arrays
      return flat(ret);
    },

    // A global GUID counter for objects
    guid: 1,

    // jQuery.support is not used in Core but other projects attach their
    // properties to it so it needs to exist.
    support: support
  });

  if (typeof Symbol === 'function') {
    jQuery.fn[Symbol.iterator] = arr[Symbol.iterator];
  }

  // Populate the class2type map
  jQuery.each('Boolean Number String Function Array Date RegExp Object Error Symbol'.split(' '), function (_i, name) {
    class2type['[object ' + name + ']'] = name.toLowerCase();
  });

  function isArrayLike(obj) {
    // Support: real iOS 8.2 only (not reproducible in simulator)
    // `in` check used to prevent JIT error (gh-2145)
    // hasOwn isn't used here due to false negatives
    // regarding Nodelist length in IE
    var length = !!obj && 'length' in obj && obj.length,
      type = toType(obj);

    if (isFunction(obj) || isWindow(obj)) {
      return false;
    }

    return type === 'array' || length === 0 || (typeof length === 'number' && length > 0 && length - 1 in obj);
  }
  var Sizzle =
    /*!
     * Sizzle CSS Selector Engine v2.3.5
     * https://sizzlejs.com/
     *
     * Copyright JS Foundation and other contributors
     * Released under the MIT license
     * https://js.foundation/
     *
     * Date: 2020-03-14
     */
    (function (window) {
      var i,
        support,
        Expr,
        getText,
        isXML,
        tokenize,
        compile,
        select,
        outermostContext,
        sortInput,
        hasDuplicate,
        // Local document vars
        setDocument,
        document,
        docElem,
        documentIsHTML,
        rbuggyQSA,
        rbuggyMatches,
        matches,
        contains,
        // Instance-specific data
        expando = 'sizzle' + 1 * new Date(),
        preferredDoc = window.document,
        dirruns = 0,
        done = 0,
        classCache = createCache(),
        tokenCache = createCache(),
        compilerCache = createCache(),
        nonnativeSelectorCache = createCache(),
        sortOrder = function (a, b) {
          if (a === b) {
            hasDuplicate = true;
          }
          return 0;
        },
        // Instance methods
        hasOwn = {}.hasOwnProperty,
        arr = [],
        pop = arr.pop,
        pushNative = arr.push,
        push = arr.push,
        slice = arr.slice,
        // Use a stripped-down indexOf as it's faster than native
        // https://jsperf.com/thor-indexof-vs-for/5
        indexOf = function (list, elem) {
          var i = 0,
            len = list.length;
          for (; i < len; i++) {
            if (list[i] === elem) {
              return i;
            }
          }
          return -1;
        },
        booleans = 'checked|selected|async|autofocus|autoplay|controls|defer|disabled|hidden|' + 'ismap|loop|multiple|open|readonly|required|scoped',
        // Regular expressions

        // http://www.w3.org/TR/css3-selectors/#whitespace
        whitespace = '[\\x20\\t\\r\\n\\f]',
        // https://www.w3.org/TR/css-syntax-3/#ident-token-diagram
        identifier = '(?:\\\\[\\da-fA-F]{1,6}' + whitespace + '?|\\\\[^\\r\\n\\f]|[\\w-]|[^\0-\\x7f])+',
        // Attribute selectors: http://www.w3.org/TR/selectors/#attribute-selectors
        attributes =
          '\\[' +
          whitespace +
          '*(' +
          identifier +
          ')(?:' +
          whitespace +
          // Operator (capture 2)
          '*([*^$|!~]?=)' +
          whitespace +
          // "Attribute values must be CSS identifiers [capture 5]
          // or strings [capture 3 or capture 4]"
          '*(?:\'((?:\\\\.|[^\\\\\'])*)\'|"((?:\\\\.|[^\\\\"])*)"|(' +
          identifier +
          '))|)' +
          whitespace +
          '*\\]',
        pseudos =
          ':(' +
          identifier +
          ')(?:\\((' +
          // To reduce the number of selectors needing tokenize in the preFilter, prefer arguments:
          // 1. quoted (capture 3; capture 4 or capture 5)
          '(\'((?:\\\\.|[^\\\\\'])*)\'|"((?:\\\\.|[^\\\\"])*)")|' +
          // 2. simple (capture 6)
          '((?:\\\\.|[^\\\\()[\\]]|' +
          attributes +
          ')*)|' +
          // 3. anything else (capture 2)
          '.*' +
          ')\\)|)',
        // Leading and non-escaped trailing whitespace, capturing some non-whitespace characters preceding the latter
        rwhitespace = new RegExp(whitespace + '+', 'g'),
        rtrim = new RegExp('^' + whitespace + '+|((?:^|[^\\\\])(?:\\\\.)*)' + whitespace + '+$', 'g'),
        rcomma = new RegExp('^' + whitespace + '*,' + whitespace + '*'),
        rcombinators = new RegExp('^' + whitespace + '*([>+~]|' + whitespace + ')' + whitespace + '*'),
        rdescend = new RegExp(whitespace + '|>'),
        rpseudo = new RegExp(pseudos),
        ridentifier = new RegExp('^' + identifier + '$'),
        matchExpr = {
          ID: new RegExp('^#(' + identifier + ')'),
          CLASS: new RegExp('^\\.(' + identifier + ')'),
          TAG: new RegExp('^(' + identifier + '|[*])'),
          ATTR: new RegExp('^' + attributes),
          PSEUDO: new RegExp('^' + pseudos),
          CHILD: new RegExp(
            '^:(only|first|last|nth|nth-last)-(child|of-type)(?:\\(' +
              whitespace +
              '*(even|odd|(([+-]|)(\\d*)n|)' +
              whitespace +
              '*(?:([+-]|)' +
              whitespace +
              '*(\\d+)|))' +
              whitespace +
              '*\\)|)',
            'i'
          ),
          bool: new RegExp('^(?:' + booleans + ')$', 'i'),

          // For use in libraries implementing .is()
          // We use this for POS matching in `select`
          needsContext: new RegExp(
            '^' + whitespace + '*[>+~]|:(even|odd|eq|gt|lt|nth|first|last)(?:\\(' + whitespace + '*((?:-\\d)?\\d*)' + whitespace + '*\\)|)(?=[^-]|$)',
            'i'
          )
        },
        rhtml = /HTML$/i,
        rinputs = /^(?:input|select|textarea|button)$/i,
        rheader = /^h\d$/i,
        rnative = /^[^{]+\{\s*\[native \w/,
        // Easily-parseable/retrievable ID or TAG or CLASS selectors
        rquickExpr = /^(?:#([\w-]+)|(\w+)|\.([\w-]+))$/,
        rsibling = /[+~]/,
        // CSS escapes
        // http://www.w3.org/TR/CSS21/syndata.html#escaped-characters
        runescape = new RegExp('\\\\[\\da-fA-F]{1,6}' + whitespace + '?|\\\\([^\\r\\n\\f])', 'g'),
        funescape = function (escape, nonHex) {
          var high = '0x' + escape.slice(1) - 0x10000;

          return nonHex
            ? // Strip the backslash prefix from a non-hex escape sequence
              nonHex
            : // Replace a hexadecimal escape sequence with the encoded Unicode code point
              // Support: IE <=11+
              // For values outside the Basic Multilingual Plane (BMP), manually construct a
              // surrogate pair
              high < 0
              ? String.fromCharCode(high + 0x10000)
              : String.fromCharCode((high >> 10) | 0xd800, (high & 0x3ff) | 0xdc00);
        },
        // CSS string/identifier serialization
        // https://drafts.csswg.org/cssom/#common-serializing-idioms
        rcssescape = /([\0-\x1f\x7f]|^-?\d)|^-$|[^\0-\x1f\x7f-\uFFFF\w-]/g,
        fcssescape = function (ch, asCodePoint) {
          if (asCodePoint) {
            // U+0000 NULL becomes U+FFFD REPLACEMENT CHARACTER
            if (ch === '\0') {
              return '\uFFFD';
            }

            // Control characters and (dependent upon position) numbers get escaped as code points
            return ch.slice(0, -1) + '\\' + ch.charCodeAt(ch.length - 1).toString(16) + ' ';
          }

          // Other potentially-special ASCII characters get backslash-escaped
          return '\\' + ch;
        },
        // Used for iframes
        // See setDocument()
        // Removing the function wrapper causes a "Permission Denied"
        // error in IE
        unloadHandler = function () {
          setDocument();
        },
        inDisabledFieldset = addCombinator(
          function (elem) {
            return elem.disabled === true && elem.nodeName.toLowerCase() === 'fieldset';
          },
          { dir: 'parentNode', next: 'legend' }
        );

      // Optimize for push.apply( _, NodeList )
      try {
        push.apply((arr = slice.call(preferredDoc.childNodes)), preferredDoc.childNodes);

        // Support: Android<4.0
        // Detect silently failing push.apply
        // eslint-disable-next-line no-unused-expressions
        arr[preferredDoc.childNodes.length].nodeType;
      } catch (e) {
        push = {
          apply: arr.length
            ? // Leverage slice if possible
              function (target, els) {
                pushNative.apply(target, slice.call(els));
              }
            : // Support: IE<9
              // Otherwise append directly
              function (target, els) {
                var j = target.length,
                  i = 0;

                // Can't trust NodeList.length
                while ((target[j++] = els[i++])) {}
                target.length = j - 1;
              }
        };
      }

      function Sizzle(selector, context, results, seed) {
        var m,
          i,
          elem,
          nid,
          match,
          groups,
          newSelector,
          newContext = context && context.ownerDocument,
          // nodeType defaults to 9, since context defaults to document
          nodeType = context ? context.nodeType : 9;

        results = results || [];

        // Return early from calls with invalid selector or context
        if (typeof selector !== 'string' || !selector || (nodeType !== 1 && nodeType !== 9 && nodeType !== 11)) {
          return results;
        }

        // Try to shortcut find operations (as opposed to filters) in HTML documents
        if (!seed) {
          setDocument(context);
          context = context || document;

          if (documentIsHTML) {
            // If the selector is sufficiently simple, try using a "get*By*" DOM method
            // (excepting DocumentFragment context, where the methods don't exist)
            if (nodeType !== 11 && (match = rquickExpr.exec(selector))) {
              // ID selector
              if ((m = match[1])) {
                // Document context
                if (nodeType === 9) {
                  if ((elem = context.getElementById(m))) {
                    // Support: IE, Opera, Webkit
                    // TODO: identify versions
                    // getElementById can match elements by name instead of ID
                    if (elem.id === m) {
                      results.push(elem);
                      return results;
                    }
                  } else {
                    return results;
                  }

                  // Element context
                } else {
                  // Support: IE, Opera, Webkit
                  // TODO: identify versions
                  // getElementById can match elements by name instead of ID
                  if (newContext && (elem = newContext.getElementById(m)) && contains(context, elem) && elem.id === m) {
                    results.push(elem);
                    return results;
                  }
                }

                // Type selector
              } else if (match[2]) {
                push.apply(results, context.getElementsByTagName(selector));
                return results;

                // Class selector
              } else if ((m = match[3]) && support.getElementsByClassName && context.getElementsByClassName) {
                push.apply(results, context.getElementsByClassName(m));
                return results;
              }
            }

            // Take advantage of querySelectorAll
            if (
              support.qsa &&
              !nonnativeSelectorCache[selector + ' '] &&
              (!rbuggyQSA || !rbuggyQSA.test(selector)) &&
              // Support: IE 8 only
              // Exclude object elements
              (nodeType !== 1 || context.nodeName.toLowerCase() !== 'object')
            ) {
              newSelector = selector;
              newContext = context;

              // qSA considers elements outside a scoping root when evaluating child or
              // descendant combinators, which is not what we want.
              // In such cases, we work around the behavior by prefixing every selector in the
              // list with an ID selector referencing the scope context.
              // The technique has to be used as well when a leading combinator is used
              // as such selectors are not recognized by querySelectorAll.
              // Thanks to Andrew Dupont for this technique.
              if (nodeType === 1 && (rdescend.test(selector) || rcombinators.test(selector))) {
                // Expand context for sibling selectors
                newContext = (rsibling.test(selector) && testContext(context.parentNode)) || context;

                // We can use :scope instead of the ID hack if the browser
                // supports it & if we're not changing the context.
                if (newContext !== context || !support.scope) {
                  // Capture the context ID, setting it first if necessary
                  if ((nid = context.getAttribute('id'))) {
                    nid = nid.replace(rcssescape, fcssescape);
                  } else {
                    context.setAttribute('id', (nid = expando));
                  }
                }

                // Prefix every selector in the list
                groups = tokenize(selector);
                i = groups.length;
                while (i--) {
                  groups[i] = (nid ? '#' + nid : ':scope') + ' ' + toSelector(groups[i]);
                }
                newSelector = groups.join(',');
              }

              try {
                push.apply(results, newContext.querySelectorAll(newSelector));
                return results;
              } catch (qsaError) {
                nonnativeSelectorCache(selector, true);
              } finally {
                if (nid === expando) {
                  context.removeAttribute('id');
                }
              }
            }
          }
        }

        // All others
        return select(selector.replace(rtrim, '$1'), context, results, seed);
      }

      /**
       * Create key-value caches of limited size
       * @returns {function(string, object)} Returns the Object data after storing it on itself with
       *	property name the (space-suffixed) string and (if the cache is larger than Expr.cacheLength)
       *	deleting the oldest entry
       */
      function createCache() {
        var keys = [];

        function cache(key, value) {
          // Use (key + " ") to avoid collision with native prototype properties (see Issue #157)
          if (keys.push(key + ' ') > Expr.cacheLength) {
            // Only keep the most recent entries
            delete cache[keys.shift()];
          }
          return (cache[key + ' '] = value);
        }
        return cache;
      }

      /**
       * Mark a function for special use by Sizzle
       * @param {Function} fn The function to mark
       */
      function markFunction(fn) {
        fn[expando] = true;
        return fn;
      }

      /**
       * Support testing using an element
       * @param {Function} fn Passed the created element and returns a boolean result
       */
      function assert(fn) {
        var el = document.createElement('fieldset');

        try {
          return !!fn(el);
        } catch (e) {
          return false;
        } finally {
          // Remove from its parent by default
          if (el.parentNode) {
            el.parentNode.removeChild(el);
          }

          // release memory in IE
          el = null;
        }
      }

      /**
       * Adds the same handler for all of the specified attrs
       * @param {String} attrs Pipe-separated list of attributes
       * @param {Function} handler The method that will be applied
       */
      function addHandle(attrs, handler) {
        var arr = attrs.split('|'),
          i = arr.length;

        while (i--) {
          Expr.attrHandle[arr[i]] = handler;
        }
      }

      /**
       * Checks document order of two siblings
       * @param {Element} a
       * @param {Element} b
       * @returns {Number} Returns less than 0 if a precedes b, greater than 0 if a follows b
       */
      function siblingCheck(a, b) {
        var cur = b && a,
          diff = cur && a.nodeType === 1 && b.nodeType === 1 && a.sourceIndex - b.sourceIndex;

        // Use IE sourceIndex if available on both nodes
        if (diff) {
          return diff;
        }

        // Check if b follows a
        if (cur) {
          while ((cur = cur.nextSibling)) {
            if (cur === b) {
              return -1;
            }
          }
        }

        return a ? 1 : -1;
      }

      /**
       * Returns a function to use in pseudos for input types
       * @param {String} type
       */
      function createInputPseudo(type) {
        return function (elem) {
          var name = elem.nodeName.toLowerCase();
          return name === 'input' && elem.type === type;
        };
      }

      /**
       * Returns a function to use in pseudos for buttons
       * @param {String} type
       */
      function createButtonPseudo(type) {
        return function (elem) {
          var name = elem.nodeName.toLowerCase();
          return (name === 'input' || name === 'button') && elem.type === type;
        };
      }

      /**
       * Returns a function to use in pseudos for :enabled/:disabled
       * @param {Boolean} disabled true for :disabled; false for :enabled
       */
      function createDisabledPseudo(disabled) {
        // Known :disabled false positives: fieldset[disabled] > legend:nth-of-type(n+2) :can-disable
        return function (elem) {
          // Only certain elements can match :enabled or :disabled
          // https://html.spec.whatwg.org/multipage/scripting.html#selector-enabled
          // https://html.spec.whatwg.org/multipage/scripting.html#selector-disabled
          if ('form' in elem) {
            // Check for inherited disabledness on relevant non-disabled elements:
            // * listed form-associated elements in a disabled fieldset
            //   https://html.spec.whatwg.org/multipage/forms.html#category-listed
            //   https://html.spec.whatwg.org/multipage/forms.html#concept-fe-disabled
            // * option elements in a disabled optgroup
            //   https://html.spec.whatwg.org/multipage/forms.html#concept-option-disabled
            // All such elements have a "form" property.
            if (elem.parentNode && elem.disabled === false) {
              // Option elements defer to a parent optgroup if present
              if ('label' in elem) {
                if ('label' in elem.parentNode) {
                  return elem.parentNode.disabled === disabled;
                } else {
                  return elem.disabled === disabled;
                }
              }

              // Support: IE 6 - 11
              // Use the isDisabled shortcut property to check for disabled fieldset ancestors
              return (
                elem.isDisabled === disabled ||
                // Where there is no isDisabled, check manually
                /* jshint -W018 */
                (elem.isDisabled !== !disabled && inDisabledFieldset(elem) === disabled)
              );
            }

            return elem.disabled === disabled;

            // Try to winnow out elements that can't be disabled before trusting the disabled property.
            // Some victims get caught in our net (label, legend, menu, track), but it shouldn't
            // even exist on them, let alone have a boolean value.
          } else if ('label' in elem) {
            return elem.disabled === disabled;
          }

          // Remaining elements are neither :enabled nor :disabled
          return false;
        };
      }

      /**
       * Returns a function to use in pseudos for positionals
       * @param {Function} fn
       */
      function createPositionalPseudo(fn) {
        return markFunction(function (argument) {
          argument = +argument;
          return markFunction(function (seed, matches) {
            var j,
              matchIndexes = fn([], seed.length, argument),
              i = matchIndexes.length;

            // Match elements found at the specified indexes
            while (i--) {
              if (seed[(j = matchIndexes[i])]) {
                seed[j] = !(matches[j] = seed[j]);
              }
            }
          });
        });
      }

      /**
       * Checks a node for validity as a Sizzle context
       * @param {Element|Object=} context
       * @returns {Element|Object|Boolean} The input node if acceptable, otherwise a falsy value
       */
      function testContext(context) {
        return context && typeof context.getElementsByTagName !== 'undefined' && context;
      }

      // Expose support vars for convenience
      support = Sizzle.support = {};

      /**
       * Detects XML nodes
       * @param {Element|Object} elem An element or a document
       * @returns {Boolean} True iff elem is a non-HTML XML node
       */
      isXML = Sizzle.isXML = function (elem) {
        var namespace = elem.namespaceURI,
          docElem = (elem.ownerDocument || elem).documentElement;

        // Support: IE <=8
        // Assume HTML when documentElement doesn't yet exist, such as inside loading iframes
        // https://bugs.jquery.com/ticket/4833
        return !rhtml.test(namespace || (docElem && docElem.nodeName) || 'HTML');
      };

      /**
       * Sets document-related variables once based on the current document
       * @param {Element|Object} [doc] An element or document object to use to set the document
       * @returns {Object} Returns the current document
       */
      setDocument = Sizzle.setDocument = function (node) {
        var hasCompare,
          subWindow,
          doc = node ? node.ownerDocument || node : preferredDoc;

        // Return early if doc is invalid or already selected
        // Support: IE 11+, Edge 17 - 18+
        // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
        // two documents; shallow comparisons work.
        // eslint-disable-next-line eqeqeq
        if (doc == document || doc.nodeType !== 9 || !doc.documentElement) {
          return document;
        }

        // Update global variables
        document = doc;
        docElem = document.documentElement;
        documentIsHTML = !isXML(document);

        // Support: IE 9 - 11+, Edge 12 - 18+
        // Accessing iframe documents after unload throws "permission denied" errors (jQuery #13936)
        // Support: IE 11+, Edge 17 - 18+
        // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
        // two documents; shallow comparisons work.
        // eslint-disable-next-line eqeqeq
        if (preferredDoc != document && (subWindow = document.defaultView) && subWindow.top !== subWindow) {
          // Support: IE 11, Edge
          if (subWindow.addEventListener) {
            subWindow.addEventListener('unload', unloadHandler, false);

            // Support: IE 9 - 10 only
          } else if (subWindow.attachEvent) {
            subWindow.attachEvent('onunload', unloadHandler);
          }
        }

        // Support: IE 8 - 11+, Edge 12 - 18+, Chrome <=16 - 25 only, Firefox <=3.6 - 31 only,
        // Safari 4 - 5 only, Opera <=11.6 - 12.x only
        // IE/Edge & older browsers don't support the :scope pseudo-class.
        // Support: Safari 6.0 only
        // Safari 6.0 supports :scope but it's an alias of :root there.
        support.scope = assert(function (el) {
          docElem.appendChild(el).appendChild(document.createElement('div'));
          return typeof el.querySelectorAll !== 'undefined' && !el.querySelectorAll(':scope fieldset div').length;
        });

        /* Attributes
				---------------------------------------------------------------------- */

        // Support: IE<8
        // Verify that getAttribute really returns attributes and not properties
        // (excepting IE8 booleans)
        support.attributes = assert(function (el) {
          el.className = 'i';
          return !el.getAttribute('className');
        });

        /* getElement(s)By*
				---------------------------------------------------------------------- */

        // Check if getElementsByTagName("*") returns only elements
        support.getElementsByTagName = assert(function (el) {
          el.appendChild(document.createComment(''));
          return !el.getElementsByTagName('*').length;
        });

        // Support: IE<9
        support.getElementsByClassName = rnative.test(document.getElementsByClassName);

        // Support: IE<10
        // Check if getElementById returns elements by name
        // The broken getElementById methods don't pick up programmatically-set names,
        // so use a roundabout getElementsByName test
        support.getById = assert(function (el) {
          docElem.appendChild(el).id = expando;
          return !document.getElementsByName || !document.getElementsByName(expando).length;
        });

        // ID filter and find
        if (support.getById) {
          Expr.filter['ID'] = function (id) {
            var attrId = id.replace(runescape, funescape);
            return function (elem) {
              return elem.getAttribute('id') === attrId;
            };
          };
          Expr.find['ID'] = function (id, context) {
            if (typeof context.getElementById !== 'undefined' && documentIsHTML) {
              var elem = context.getElementById(id);
              return elem ? [elem] : [];
            }
          };
        } else {
          Expr.filter['ID'] = function (id) {
            var attrId = id.replace(runescape, funescape);
            return function (elem) {
              var node = typeof elem.getAttributeNode !== 'undefined' && elem.getAttributeNode('id');
              return node && node.value === attrId;
            };
          };

          // Support: IE 6 - 7 only
          // getElementById is not reliable as a find shortcut
          Expr.find['ID'] = function (id, context) {
            if (typeof context.getElementById !== 'undefined' && documentIsHTML) {
              var node,
                i,
                elems,
                elem = context.getElementById(id);

              if (elem) {
                // Verify the id attribute
                node = elem.getAttributeNode('id');
                if (node && node.value === id) {
                  return [elem];
                }

                // Fall back on getElementsByName
                elems = context.getElementsByName(id);
                i = 0;
                while ((elem = elems[i++])) {
                  node = elem.getAttributeNode('id');
                  if (node && node.value === id) {
                    return [elem];
                  }
                }
              }

              return [];
            }
          };
        }

        // Tag
        Expr.find['TAG'] = support.getElementsByTagName
          ? function (tag, context) {
              if (typeof context.getElementsByTagName !== 'undefined') {
                return context.getElementsByTagName(tag);

                // DocumentFragment nodes don't have gEBTN
              } else if (support.qsa) {
                return context.querySelectorAll(tag);
              }
            }
          : function (tag, context) {
              var elem,
                tmp = [],
                i = 0,
                // By happy coincidence, a (broken) gEBTN appears on DocumentFragment nodes too
                results = context.getElementsByTagName(tag);

              // Filter out possible comments
              if (tag === '*') {
                while ((elem = results[i++])) {
                  if (elem.nodeType === 1) {
                    tmp.push(elem);
                  }
                }

                return tmp;
              }
              return results;
            };

        // Class
        Expr.find['CLASS'] =
          support.getElementsByClassName &&
          function (className, context) {
            if (typeof context.getElementsByClassName !== 'undefined' && documentIsHTML) {
              return context.getElementsByClassName(className);
            }
          };

        /* QSA/matchesSelector
				---------------------------------------------------------------------- */

        // QSA and matchesSelector support

        // matchesSelector(:active) reports false when true (IE9/Opera 11.5)
        rbuggyMatches = [];

        // qSa(:focus) reports false when true (Chrome 21)
        // We allow this because of a bug in IE8/9 that throws an error
        // whenever `document.activeElement` is accessed on an iframe
        // So, we allow :focus to pass through QSA all the time to avoid the IE error
        // See https://bugs.jquery.com/ticket/13378
        rbuggyQSA = [];

        if ((support.qsa = rnative.test(document.querySelectorAll))) {
          // Build QSA regex
          // Regex strategy adopted from Diego Perini
          assert(function (el) {
            var input;

            // Select is set to empty string on purpose
            // This is to test IE's treatment of not explicitly
            // setting a boolean content attribute,
            // since its presence should be enough
            // https://bugs.jquery.com/ticket/12359
            docElem.appendChild(el).innerHTML =
              "<a id='" + expando + "'></a>" + "<select id='" + expando + "-\r\\' msallowcapture=''>" + "<option selected=''></option></select>";

            // Support: IE8, Opera 11-12.16
            // Nothing should be selected when empty strings follow ^= or $= or *=
            // The test attribute must be unknown in Opera but "safe" for WinRT
            // https://msdn.microsoft.com/en-us/library/ie/hh465388.aspx#attribute_section
            if (el.querySelectorAll("[msallowcapture^='']").length) {
              rbuggyQSA.push('[*^$]=' + whitespace + '*(?:\'\'|"")');
            }

            // Support: IE8
            // Boolean attributes and "value" are not treated correctly
            if (!el.querySelectorAll('[selected]').length) {
              rbuggyQSA.push('\\[' + whitespace + '*(?:value|' + booleans + ')');
            }

            // Support: Chrome<29, Android<4.4, Safari<7.0+, iOS<7.0+, PhantomJS<1.9.8+
            if (!el.querySelectorAll('[id~=' + expando + '-]').length) {
              rbuggyQSA.push('~=');
            }

            // Support: IE 11+, Edge 15 - 18+
            // IE 11/Edge don't find elements on a `[name='']` query in some cases.
            // Adding a temporary attribute to the document before the selection works
            // around the issue.
            // Interestingly, IE 10 & older don't seem to have the issue.
            input = document.createElement('input');
            input.setAttribute('name', '');
            el.appendChild(input);
            if (!el.querySelectorAll("[name='']").length) {
              rbuggyQSA.push('\\[' + whitespace + '*name' + whitespace + '*=' + whitespace + '*(?:\'\'|"")');
            }

            // Webkit/Opera - :checked should return selected option elements
            // http://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
            // IE8 throws error here and will not see later tests
            if (!el.querySelectorAll(':checked').length) {
              rbuggyQSA.push(':checked');
            }

            // Support: Safari 8+, iOS 8+
            // https://bugs.webkit.org/show_bug.cgi?id=136851
            // In-page `selector#id sibling-combinator selector` fails
            if (!el.querySelectorAll('a#' + expando + '+*').length) {
              rbuggyQSA.push('.#.+[+~]');
            }

            // Support: Firefox <=3.6 - 5 only
            // Old Firefox doesn't throw on a badly-escaped identifier.
            el.querySelectorAll('\\\f');
            rbuggyQSA.push('[\\r\\n\\f]');
          });

          assert(function (el) {
            el.innerHTML = "<a href='' disabled='disabled'></a>" + "<select disabled='disabled'><option/></select>";

            // Support: Windows 8 Native Apps
            // The type and name attributes are restricted during .innerHTML assignment
            var input = document.createElement('input');
            input.setAttribute('type', 'hidden');
            el.appendChild(input).setAttribute('name', 'D');

            // Support: IE8
            // Enforce case-sensitivity of name attribute
            if (el.querySelectorAll('[name=d]').length) {
              rbuggyQSA.push('name' + whitespace + '*[*^$|!~]?=');
            }

            // FF 3.5 - :enabled/:disabled and hidden elements (hidden elements are still enabled)
            // IE8 throws error here and will not see later tests
            if (el.querySelectorAll(':enabled').length !== 2) {
              rbuggyQSA.push(':enabled', ':disabled');
            }

            // Support: IE9-11+
            // IE's :disabled selector does not pick up the children of disabled fieldsets
            docElem.appendChild(el).disabled = true;
            if (el.querySelectorAll(':disabled').length !== 2) {
              rbuggyQSA.push(':enabled', ':disabled');
            }

            // Support: Opera 10 - 11 only
            // Opera 10-11 does not throw on post-comma invalid pseudos
            el.querySelectorAll('*,:x');
            rbuggyQSA.push(',.*:');
          });
        }

        if (
          (support.matchesSelector = rnative.test(
            (matches = docElem.matches || docElem.webkitMatchesSelector || docElem.mozMatchesSelector || docElem.oMatchesSelector || docElem.msMatchesSelector)
          ))
        ) {
          assert(function (el) {
            // Check to see if it's possible to do matchesSelector
            // on a disconnected node (IE 9)
            support.disconnectedMatch = matches.call(el, '*');

            // This should fail with an exception
            // Gecko does not error, returns false instead
            matches.call(el, "[s!='']:x");
            rbuggyMatches.push('!=', pseudos);
          });
        }

        rbuggyQSA = rbuggyQSA.length && new RegExp(rbuggyQSA.join('|'));
        rbuggyMatches = rbuggyMatches.length && new RegExp(rbuggyMatches.join('|'));

        /* Contains
				---------------------------------------------------------------------- */
        hasCompare = rnative.test(docElem.compareDocumentPosition);

        // Element contains another
        // Purposefully self-exclusive
        // As in, an element does not contain itself
        contains =
          hasCompare || rnative.test(docElem.contains)
            ? function (a, b) {
                var adown = a.nodeType === 9 ? a.documentElement : a,
                  bup = b && b.parentNode;
                return (
                  a === bup ||
                  !!(bup && bup.nodeType === 1 && (adown.contains ? adown.contains(bup) : a.compareDocumentPosition && a.compareDocumentPosition(bup) & 16))
                );
              }
            : function (a, b) {
                if (b) {
                  while ((b = b.parentNode)) {
                    if (b === a) {
                      return true;
                    }
                  }
                }
                return false;
              };

        /* Sorting
				---------------------------------------------------------------------- */

        // Document order sorting
        sortOrder = hasCompare
          ? function (a, b) {
              // Flag for duplicate removal
              if (a === b) {
                hasDuplicate = true;
                return 0;
              }

              // Sort on method existence if only one input has compareDocumentPosition
              var compare = !a.compareDocumentPosition - !b.compareDocumentPosition;
              if (compare) {
                return compare;
              }

              // Calculate position if both inputs belong to the same document
              // Support: IE 11+, Edge 17 - 18+
              // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
              // two documents; shallow comparisons work.
              // eslint-disable-next-line eqeqeq
              compare =
                (a.ownerDocument || a) == (b.ownerDocument || b)
                  ? a.compareDocumentPosition(b)
                  : // Otherwise we know they are disconnected
                    1;

              // Disconnected nodes
              if (compare & 1 || (!support.sortDetached && b.compareDocumentPosition(a) === compare)) {
                // Choose the first element that is related to our preferred document
                // Support: IE 11+, Edge 17 - 18+
                // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
                // two documents; shallow comparisons work.
                // eslint-disable-next-line eqeqeq
                if (a == document || (a.ownerDocument == preferredDoc && contains(preferredDoc, a))) {
                  return -1;
                }

                // Support: IE 11+, Edge 17 - 18+
                // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
                // two documents; shallow comparisons work.
                // eslint-disable-next-line eqeqeq
                if (b == document || (b.ownerDocument == preferredDoc && contains(preferredDoc, b))) {
                  return 1;
                }

                // Maintain original order
                return sortInput ? indexOf(sortInput, a) - indexOf(sortInput, b) : 0;
              }

              return compare & 4 ? -1 : 1;
            }
          : function (a, b) {
              // Exit early if the nodes are identical
              if (a === b) {
                hasDuplicate = true;
                return 0;
              }

              var cur,
                i = 0,
                aup = a.parentNode,
                bup = b.parentNode,
                ap = [a],
                bp = [b];

              // Parentless nodes are either documents or disconnected
              if (!aup || !bup) {
                // Support: IE 11+, Edge 17 - 18+
                // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
                // two documents; shallow comparisons work.
                /* eslint-disable eqeqeq */
                return a == document
                  ? -1
                  : b == document
                    ? 1
                    : /* eslint-enable eqeqeq */
                      aup
                      ? -1
                      : bup
                        ? 1
                        : sortInput
                          ? indexOf(sortInput, a) - indexOf(sortInput, b)
                          : 0;

                // If the nodes are siblings, we can do a quick check
              } else if (aup === bup) {
                return siblingCheck(a, b);
              }

              // Otherwise we need full lists of their ancestors for comparison
              cur = a;
              while ((cur = cur.parentNode)) {
                ap.unshift(cur);
              }
              cur = b;
              while ((cur = cur.parentNode)) {
                bp.unshift(cur);
              }

              // Walk down the tree looking for a discrepancy
              while (ap[i] === bp[i]) {
                i++;
              }

              return i
                ? // Do a sibling check if the nodes have a common ancestor
                  siblingCheck(ap[i], bp[i])
                : // Otherwise nodes in our document sort first
                  // Support: IE 11+, Edge 17 - 18+
                  // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
                  // two documents; shallow comparisons work.
                  /* eslint-disable eqeqeq */
                  ap[i] == preferredDoc
                  ? -1
                  : bp[i] == preferredDoc
                    ? 1
                    : /* eslint-enable eqeqeq */
                      0;
            };

        return document;
      };

      Sizzle.matches = function (expr, elements) {
        return Sizzle(expr, null, null, elements);
      };

      Sizzle.matchesSelector = function (elem, expr) {
        setDocument(elem);

        if (
          support.matchesSelector &&
          documentIsHTML &&
          !nonnativeSelectorCache[expr + ' '] &&
          (!rbuggyMatches || !rbuggyMatches.test(expr)) &&
          (!rbuggyQSA || !rbuggyQSA.test(expr))
        ) {
          try {
            var ret = matches.call(elem, expr);

            // IE 9's matchesSelector returns false on disconnected nodes
            if (
              ret ||
              support.disconnectedMatch ||
              // As well, disconnected nodes are said to be in a document
              // fragment in IE 9
              (elem.document && elem.document.nodeType !== 11)
            ) {
              return ret;
            }
          } catch (e) {
            nonnativeSelectorCache(expr, true);
          }
        }

        return Sizzle(expr, document, null, [elem]).length > 0;
      };

      Sizzle.contains = function (context, elem) {
        // Set document vars if needed
        // Support: IE 11+, Edge 17 - 18+
        // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
        // two documents; shallow comparisons work.
        // eslint-disable-next-line eqeqeq
        if ((context.ownerDocument || context) != document) {
          setDocument(context);
        }
        return contains(context, elem);
      };

      Sizzle.attr = function (elem, name) {
        // Set document vars if needed
        // Support: IE 11+, Edge 17 - 18+
        // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
        // two documents; shallow comparisons work.
        // eslint-disable-next-line eqeqeq
        if ((elem.ownerDocument || elem) != document) {
          setDocument(elem);
        }

        var fn = Expr.attrHandle[name.toLowerCase()],
          // Don't get fooled by Object.prototype properties (jQuery #13807)
          val = fn && hasOwn.call(Expr.attrHandle, name.toLowerCase()) ? fn(elem, name, !documentIsHTML) : undefined;

        return val !== undefined
          ? val
          : support.attributes || !documentIsHTML
            ? elem.getAttribute(name)
            : (val = elem.getAttributeNode(name)) && val.specified
              ? val.value
              : null;
      };

      Sizzle.escape = function (sel) {
        return (sel + '').replace(rcssescape, fcssescape);
      };

      Sizzle.error = function (msg) {
        throw new Error('Syntax error, unrecognized expression: ' + msg);
      };

      /**
       * Document sorting and removing duplicates
       * @param {ArrayLike} results
       */
      Sizzle.uniqueSort = function (results) {
        var elem,
          duplicates = [],
          j = 0,
          i = 0;

        // Unless we *know* we can detect duplicates, assume their presence
        hasDuplicate = !support.detectDuplicates;
        sortInput = !support.sortStable && results.slice(0);
        results.sort(sortOrder);

        if (hasDuplicate) {
          while ((elem = results[i++])) {
            if (elem === results[i]) {
              j = duplicates.push(i);
            }
          }
          while (j--) {
            results.splice(duplicates[j], 1);
          }
        }

        // Clear input after sorting to release objects
        // See https://github.com/jquery/sizzle/pull/225
        sortInput = null;

        return results;
      };

      /**
       * Utility function for retrieving the text value of an array of DOM nodes
       * @param {Array|Element} elem
       */
      getText = Sizzle.getText = function (elem) {
        var node,
          ret = '',
          i = 0,
          nodeType = elem.nodeType;

        if (!nodeType) {
          // If no nodeType, this is expected to be an array
          while ((node = elem[i++])) {
            // Do not traverse comment nodes
            ret += getText(node);
          }
        } else if (nodeType === 1 || nodeType === 9 || nodeType === 11) {
          // Use textContent for elements
          // innerText usage removed for consistency of new lines (jQuery #11153)
          if (typeof elem.textContent === 'string') {
            return elem.textContent;
          } else {
            // Traverse its children
            for (elem = elem.firstChild; elem; elem = elem.nextSibling) {
              ret += getText(elem);
            }
          }
        } else if (nodeType === 3 || nodeType === 4) {
          return elem.nodeValue;
        }

        // Do not include comment or processing instruction nodes

        return ret;
      };

      Expr = Sizzle.selectors = {
        // Can be adjusted by the user
        cacheLength: 50,

        createPseudo: markFunction,

        match: matchExpr,

        attrHandle: {},

        find: {},

        relative: {
          '>': { dir: 'parentNode', first: true },
          ' ': { dir: 'parentNode' },
          '+': { dir: 'previousSibling', first: true },
          '~': { dir: 'previousSibling' }
        },

        preFilter: {
          ATTR: function (match) {
            match[1] = match[1].replace(runescape, funescape);

            // Move the given value to match[3] whether quoted or unquoted
            match[3] = (match[3] || match[4] || match[5] || '').replace(runescape, funescape);

            if (match[2] === '~=') {
              match[3] = ' ' + match[3] + ' ';
            }

            return match.slice(0, 4);
          },

          CHILD: function (match) {
            /* matches from matchExpr["CHILD"]
							1 type (only|nth|...)
							2 what (child|of-type)
							3 argument (even|odd|\d*|\d*n([+-]\d+)?|...)
							4 xn-component of xn+y argument ([+-]?\d*n|)
							5 sign of xn-component
							6 x of xn-component
							7 sign of y-component
							8 y of y-component
						*/
            match[1] = match[1].toLowerCase();

            if (match[1].slice(0, 3) === 'nth') {
              // nth-* requires argument
              if (!match[3]) {
                Sizzle.error(match[0]);
              }

              // numeric x and y parameters for Expr.filter.CHILD
              // remember that false/true cast respectively to 0/1
              match[4] = +(match[4] ? match[5] + (match[6] || 1) : 2 * (match[3] === 'even' || match[3] === 'odd'));
              match[5] = +(match[7] + match[8] || match[3] === 'odd');

              // other types prohibit arguments
            } else if (match[3]) {
              Sizzle.error(match[0]);
            }

            return match;
          },

          PSEUDO: function (match) {
            var excess,
              unquoted = !match[6] && match[2];

            if (matchExpr['CHILD'].test(match[0])) {
              return null;
            }

            // Accept quoted arguments as-is
            if (match[3]) {
              match[2] = match[4] || match[5] || '';

              // Strip excess characters from unquoted arguments
            } else if (
              unquoted &&
              rpseudo.test(unquoted) &&
              // Get excess from tokenize (recursively)
              (excess = tokenize(unquoted, true)) &&
              // advance to the next closing parenthesis
              (excess = unquoted.indexOf(')', unquoted.length - excess) - unquoted.length)
            ) {
              // excess is a negative index
              match[0] = match[0].slice(0, excess);
              match[2] = unquoted.slice(0, excess);
            }

            // Return only captures needed by the pseudo filter method (type and argument)
            return match.slice(0, 3);
          }
        },

        filter: {
          TAG: function (nodeNameSelector) {
            var nodeName = nodeNameSelector.replace(runescape, funescape).toLowerCase();
            return nodeNameSelector === '*'
              ? function () {
                  return true;
                }
              : function (elem) {
                  return elem.nodeName && elem.nodeName.toLowerCase() === nodeName;
                };
          },

          CLASS: function (className) {
            var pattern = classCache[className + ' '];

            return (
              pattern ||
              ((pattern = new RegExp('(^|' + whitespace + ')' + className + '(' + whitespace + '|$)')) &&
                classCache(className, function (elem) {
                  return pattern.test(
                    (typeof elem.className === 'string' && elem.className) || (typeof elem.getAttribute !== 'undefined' && elem.getAttribute('class')) || ''
                  );
                }))
            );
          },

          ATTR: function (name, operator, check) {
            return function (elem) {
              var result = Sizzle.attr(elem, name);

              if (result == null) {
                return operator === '!=';
              }
              if (!operator) {
                return true;
              }

              result += '';

              /* eslint-disable max-len */

              return operator === '='
                ? result === check
                : operator === '!='
                  ? result !== check
                  : operator === '^='
                    ? check && result.indexOf(check) === 0
                    : operator === '*='
                      ? check && result.indexOf(check) > -1
                      : operator === '$='
                        ? check && result.slice(-check.length) === check
                        : operator === '~='
                          ? (' ' + result.replace(rwhitespace, ' ') + ' ').indexOf(check) > -1
                          : operator === '|='
                            ? result === check || result.slice(0, check.length + 1) === check + '-'
                            : false;
              /* eslint-enable max-len */
            };
          },

          CHILD: function (type, what, _argument, first, last) {
            var simple = type.slice(0, 3) !== 'nth',
              forward = type.slice(-4) !== 'last',
              ofType = what === 'of-type';

            return first === 1 && last === 0
              ? // Shortcut for :nth-*(n)
                function (elem) {
                  return !!elem.parentNode;
                }
              : function (elem, _context, xml) {
                  var cache,
                    uniqueCache,
                    outerCache,
                    node,
                    nodeIndex,
                    start,
                    dir = simple !== forward ? 'nextSibling' : 'previousSibling',
                    parent = elem.parentNode,
                    name = ofType && elem.nodeName.toLowerCase(),
                    useCache = !xml && !ofType,
                    diff = false;

                  if (parent) {
                    // :(first|last|only)-(child|of-type)
                    if (simple) {
                      while (dir) {
                        node = elem;
                        while ((node = node[dir])) {
                          if (ofType ? node.nodeName.toLowerCase() === name : node.nodeType === 1) {
                            return false;
                          }
                        }

                        // Reverse direction for :only-* (if we haven't yet done so)
                        start = dir = type === 'only' && !start && 'nextSibling';
                      }
                      return true;
                    }

                    start = [forward ? parent.firstChild : parent.lastChild];

                    // non-xml :nth-child(...) stores cache data on `parent`
                    if (forward && useCache) {
                      // Seek `elem` from a previously-cached index

                      // ...in a gzip-friendly way
                      node = parent;
                      outerCache = node[expando] || (node[expando] = {});

                      // Support: IE <9 only
                      // Defend against cloned attroperties (jQuery gh-1709)
                      uniqueCache = outerCache[node.uniqueID] || (outerCache[node.uniqueID] = {});

                      cache = uniqueCache[type] || [];
                      nodeIndex = cache[0] === dirruns && cache[1];
                      diff = nodeIndex && cache[2];
                      node = nodeIndex && parent.childNodes[nodeIndex];

                      while (
                        (node =
                          (++nodeIndex && node && node[dir]) ||
                          // Fallback to seeking `elem` from the start
                          (diff = nodeIndex = 0) ||
                          start.pop())
                      ) {
                        // When found, cache indexes on `parent` and break
                        if (node.nodeType === 1 && ++diff && node === elem) {
                          uniqueCache[type] = [dirruns, nodeIndex, diff];
                          break;
                        }
                      }
                    } else {
                      // Use previously-cached element index if available
                      if (useCache) {
                        // ...in a gzip-friendly way
                        node = elem;
                        outerCache = node[expando] || (node[expando] = {});

                        // Support: IE <9 only
                        // Defend against cloned attroperties (jQuery gh-1709)
                        uniqueCache = outerCache[node.uniqueID] || (outerCache[node.uniqueID] = {});

                        cache = uniqueCache[type] || [];
                        nodeIndex = cache[0] === dirruns && cache[1];
                        diff = nodeIndex;
                      }

                      // xml :nth-child(...)
                      // or :nth-last-child(...) or :nth(-last)?-of-type(...)
                      if (diff === false) {
                        // Use the same loop as above to seek `elem` from the start
                        while ((node = (++nodeIndex && node && node[dir]) || (diff = nodeIndex = 0) || start.pop())) {
                          if ((ofType ? node.nodeName.toLowerCase() === name : node.nodeType === 1) && ++diff) {
                            // Cache the index of each encountered element
                            if (useCache) {
                              outerCache = node[expando] || (node[expando] = {});

                              // Support: IE <9 only
                              // Defend against cloned attroperties (jQuery gh-1709)
                              uniqueCache = outerCache[node.uniqueID] || (outerCache[node.uniqueID] = {});

                              uniqueCache[type] = [dirruns, diff];
                            }

                            if (node === elem) {
                              break;
                            }
                          }
                        }
                      }
                    }

                    // Incorporate the offset, then check against cycle size
                    diff -= last;
                    return diff === first || (diff % first === 0 && diff / first >= 0);
                  }
                };
          },

          PSEUDO: function (pseudo, argument) {
            // pseudo-class names are case-insensitive
            // http://www.w3.org/TR/selectors/#pseudo-classes
            // Prioritize by case sensitivity in case custom pseudos are added with uppercase letters
            // Remember that setFilters inherits from pseudos
            var args,
              fn = Expr.pseudos[pseudo] || Expr.setFilters[pseudo.toLowerCase()] || Sizzle.error('unsupported pseudo: ' + pseudo);

            // The user may use createPseudo to indicate that
            // arguments are needed to create the filter function
            // just as Sizzle does
            if (fn[expando]) {
              return fn(argument);
            }

            // But maintain support for old signatures
            if (fn.length > 1) {
              args = [pseudo, pseudo, '', argument];
              return Expr.setFilters.hasOwnProperty(pseudo.toLowerCase())
                ? markFunction(function (seed, matches) {
                    var idx,
                      matched = fn(seed, argument),
                      i = matched.length;
                    while (i--) {
                      idx = indexOf(seed, matched[i]);
                      seed[idx] = !(matches[idx] = matched[i]);
                    }
                  })
                : function (elem) {
                    return fn(elem, 0, args);
                  };
            }

            return fn;
          }
        },

        pseudos: {
          // Potentially complex pseudos
          not: markFunction(function (selector) {
            // Trim the selector passed to compile
            // to avoid treating leading and trailing
            // spaces as combinators
            var input = [],
              results = [],
              matcher = compile(selector.replace(rtrim, '$1'));

            return matcher[expando]
              ? markFunction(function (seed, matches, _context, xml) {
                  var elem,
                    unmatched = matcher(seed, null, xml, []),
                    i = seed.length;

                  // Match elements unmatched by `matcher`
                  while (i--) {
                    if ((elem = unmatched[i])) {
                      seed[i] = !(matches[i] = elem);
                    }
                  }
                })
              : function (elem, _context, xml) {
                  input[0] = elem;
                  matcher(input, null, xml, results);

                  // Don't keep the element (issue #299)
                  input[0] = null;
                  return !results.pop();
                };
          }),

          has: markFunction(function (selector) {
            return function (elem) {
              return Sizzle(selector, elem).length > 0;
            };
          }),

          contains: markFunction(function (text) {
            text = text.replace(runescape, funescape);
            return function (elem) {
              return (elem.textContent || getText(elem)).indexOf(text) > -1;
            };
          }),

          // "Whether an element is represented by a :lang() selector
          // is based solely on the element's language value
          // being equal to the identifier C,
          // or beginning with the identifier C immediately followed by "-".
          // The matching of C against the element's language value is performed case-insensitively.
          // The identifier C does not have to be a valid language name."
          // http://www.w3.org/TR/selectors/#lang-pseudo
          lang: markFunction(function (lang) {
            // lang value must be a valid identifier
            if (!ridentifier.test(lang || '')) {
              Sizzle.error('unsupported lang: ' + lang);
            }
            lang = lang.replace(runescape, funescape).toLowerCase();
            return function (elem) {
              var elemLang;
              do {
                if ((elemLang = documentIsHTML ? elem.lang : elem.getAttribute('xml:lang') || elem.getAttribute('lang'))) {
                  elemLang = elemLang.toLowerCase();
                  return elemLang === lang || elemLang.indexOf(lang + '-') === 0;
                }
              } while ((elem = elem.parentNode) && elem.nodeType === 1);
              return false;
            };
          }),

          // Miscellaneous
          target: function (elem) {
            var hash = window.location && window.location.hash;
            return hash && hash.slice(1) === elem.id;
          },

          root: function (elem) {
            return elem === docElem;
          },

          focus: function (elem) {
            return elem === document.activeElement && (!document.hasFocus || document.hasFocus()) && !!(elem.type || elem.href || ~elem.tabIndex);
          },

          // Boolean properties
          enabled: createDisabledPseudo(false),
          disabled: createDisabledPseudo(true),

          checked: function (elem) {
            // In CSS3, :checked should return both checked and selected elements
            // http://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
            var nodeName = elem.nodeName.toLowerCase();
            return (nodeName === 'input' && !!elem.checked) || (nodeName === 'option' && !!elem.selected);
          },

          selected: function (elem) {
            // Accessing this property makes selected-by-default
            // options in Safari work properly
            if (elem.parentNode) {
              // eslint-disable-next-line no-unused-expressions
              elem.parentNode.selectedIndex;
            }

            return elem.selected === true;
          },

          // Contents
          empty: function (elem) {
            // http://www.w3.org/TR/selectors/#empty-pseudo
            // :empty is negated by element (1) or content nodes (text: 3; cdata: 4; entity ref: 5),
            //   but not by others (comment: 8; processing instruction: 7; etc.)
            // nodeType < 6 works because attributes (2) do not appear as children
            for (elem = elem.firstChild; elem; elem = elem.nextSibling) {
              if (elem.nodeType < 6) {
                return false;
              }
            }
            return true;
          },

          parent: function (elem) {
            return !Expr.pseudos['empty'](elem);
          },

          // Element/input types
          header: function (elem) {
            return rheader.test(elem.nodeName);
          },

          input: function (elem) {
            return rinputs.test(elem.nodeName);
          },

          button: function (elem) {
            var name = elem.nodeName.toLowerCase();
            return (name === 'input' && elem.type === 'button') || name === 'button';
          },

          text: function (elem) {
            var attr;
            return (
              elem.nodeName.toLowerCase() === 'input' &&
              elem.type === 'text' &&
              // Support: IE<8
              // New HTML5 attribute values (e.g., "search") appear with elem.type === "text"
              ((attr = elem.getAttribute('type')) == null || attr.toLowerCase() === 'text')
            );
          },

          // Position-in-collection
          first: createPositionalPseudo(function () {
            return [0];
          }),

          last: createPositionalPseudo(function (_matchIndexes, length) {
            return [length - 1];
          }),

          eq: createPositionalPseudo(function (_matchIndexes, length, argument) {
            return [argument < 0 ? argument + length : argument];
          }),

          even: createPositionalPseudo(function (matchIndexes, length) {
            var i = 0;
            for (; i < length; i += 2) {
              matchIndexes.push(i);
            }
            return matchIndexes;
          }),

          odd: createPositionalPseudo(function (matchIndexes, length) {
            var i = 1;
            for (; i < length; i += 2) {
              matchIndexes.push(i);
            }
            return matchIndexes;
          }),

          lt: createPositionalPseudo(function (matchIndexes, length, argument) {
            var i = argument < 0 ? argument + length : argument > length ? length : argument;
            for (; --i >= 0; ) {
              matchIndexes.push(i);
            }
            return matchIndexes;
          }),

          gt: createPositionalPseudo(function (matchIndexes, length, argument) {
            var i = argument < 0 ? argument + length : argument;
            for (; ++i < length; ) {
              matchIndexes.push(i);
            }
            return matchIndexes;
          })
        }
      };

      Expr.pseudos['nth'] = Expr.pseudos['eq'];

      // Add button/input type pseudos
      for (i in { radio: true, checkbox: true, file: true, password: true, image: true }) {
        Expr.pseudos[i] = createInputPseudo(i);
      }
      for (i in { submit: true, reset: true }) {
        Expr.pseudos[i] = createButtonPseudo(i);
      }

      // Easy API for creating new setFilters
      function setFilters() {}
      setFilters.prototype = Expr.filters = Expr.pseudos;
      Expr.setFilters = new setFilters();

      tokenize = Sizzle.tokenize = function (selector, parseOnly) {
        var matched,
          match,
          tokens,
          type,
          soFar,
          groups,
          preFilters,
          cached = tokenCache[selector + ' '];

        if (cached) {
          return parseOnly ? 0 : cached.slice(0);
        }

        soFar = selector;
        groups = [];
        preFilters = Expr.preFilter;

        while (soFar) {
          // Comma and first run
          if (!matched || (match = rcomma.exec(soFar))) {
            if (match) {
              // Don't consume trailing commas as valid
              soFar = soFar.slice(match[0].length) || soFar;
            }
            groups.push((tokens = []));
          }

          matched = false;

          // Combinators
          if ((match = rcombinators.exec(soFar))) {
            matched = match.shift();
            tokens.push({
              value: matched,

              // Cast descendant combinators to space
              type: match[0].replace(rtrim, ' ')
            });
            soFar = soFar.slice(matched.length);
          }

          // Filters
          for (type in Expr.filter) {
            if ((match = matchExpr[type].exec(soFar)) && (!preFilters[type] || (match = preFilters[type](match)))) {
              matched = match.shift();
              tokens.push({
                value: matched,
                type: type,
                matches: match
              });
              soFar = soFar.slice(matched.length);
            }
          }

          if (!matched) {
            break;
          }
        }

        // Return the length of the invalid excess
        // if we're just parsing
        // Otherwise, throw an error or return tokens
        return parseOnly
          ? soFar.length
          : soFar
            ? Sizzle.error(selector)
            : // Cache the tokens
              tokenCache(selector, groups).slice(0);
      };

      function toSelector(tokens) {
        var i = 0,
          len = tokens.length,
          selector = '';
        for (; i < len; i++) {
          selector += tokens[i].value;
        }
        return selector;
      }

      function addCombinator(matcher, combinator, base) {
        var dir = combinator.dir,
          skip = combinator.next,
          key = skip || dir,
          checkNonElements = base && key === 'parentNode',
          doneName = done++;

        return combinator.first
          ? // Check against closest ancestor/preceding element
            function (elem, context, xml) {
              while ((elem = elem[dir])) {
                if (elem.nodeType === 1 || checkNonElements) {
                  return matcher(elem, context, xml);
                }
              }
              return false;
            }
          : // Check against all ancestor/preceding elements
            function (elem, context, xml) {
              var oldCache,
                uniqueCache,
                outerCache,
                newCache = [dirruns, doneName];

              // We can't set arbitrary data on XML nodes, so they don't benefit from combinator caching
              if (xml) {
                while ((elem = elem[dir])) {
                  if (elem.nodeType === 1 || checkNonElements) {
                    if (matcher(elem, context, xml)) {
                      return true;
                    }
                  }
                }
              } else {
                while ((elem = elem[dir])) {
                  if (elem.nodeType === 1 || checkNonElements) {
                    outerCache = elem[expando] || (elem[expando] = {});

                    // Support: IE <9 only
                    // Defend against cloned attroperties (jQuery gh-1709)
                    uniqueCache = outerCache[elem.uniqueID] || (outerCache[elem.uniqueID] = {});

                    if (skip && skip === elem.nodeName.toLowerCase()) {
                      elem = elem[dir] || elem;
                    } else if ((oldCache = uniqueCache[key]) && oldCache[0] === dirruns && oldCache[1] === doneName) {
                      // Assign to newCache so results back-propagate to previous elements
                      return (newCache[2] = oldCache[2]);
                    } else {
                      // Reuse newcache so results back-propagate to previous elements
                      uniqueCache[key] = newCache;

                      // A match means we're done; a fail means we have to keep checking
                      if ((newCache[2] = matcher(elem, context, xml))) {
                        return true;
                      }
                    }
                  }
                }
              }
              return false;
            };
      }

      function elementMatcher(matchers) {
        return matchers.length > 1
          ? function (elem, context, xml) {
              var i = matchers.length;
              while (i--) {
                if (!matchers[i](elem, context, xml)) {
                  return false;
                }
              }
              return true;
            }
          : matchers[0];
      }

      function multipleContexts(selector, contexts, results) {
        var i = 0,
          len = contexts.length;
        for (; i < len; i++) {
          Sizzle(selector, contexts[i], results);
        }
        return results;
      }

      function condense(unmatched, map, filter, context, xml) {
        var elem,
          newUnmatched = [],
          i = 0,
          len = unmatched.length,
          mapped = map != null;

        for (; i < len; i++) {
          if ((elem = unmatched[i])) {
            if (!filter || filter(elem, context, xml)) {
              newUnmatched.push(elem);
              if (mapped) {
                map.push(i);
              }
            }
          }
        }

        return newUnmatched;
      }

      function setMatcher(preFilter, selector, matcher, postFilter, postFinder, postSelector) {
        if (postFilter && !postFilter[expando]) {
          postFilter = setMatcher(postFilter);
        }
        if (postFinder && !postFinder[expando]) {
          postFinder = setMatcher(postFinder, postSelector);
        }
        return markFunction(function (seed, results, context, xml) {
          var temp,
            i,
            elem,
            preMap = [],
            postMap = [],
            preexisting = results.length,
            // Get initial elements from seed or context
            elems = seed || multipleContexts(selector || '*', context.nodeType ? [context] : context, []),
            // Prefilter to get matcher input, preserving a map for seed-results synchronization
            matcherIn = preFilter && (seed || !selector) ? condense(elems, preMap, preFilter, context, xml) : elems,
            matcherOut = matcher
              ? // If we have a postFinder, or filtered seed, or non-seed postFilter or preexisting results,
                postFinder || (seed ? preFilter : preexisting || postFilter)
                ? // ...intermediate processing is necessary
                  []
                : // ...otherwise use results directly
                  results
              : matcherIn;

          // Find primary matches
          if (matcher) {
            matcher(matcherIn, matcherOut, context, xml);
          }

          // Apply postFilter
          if (postFilter) {
            temp = condense(matcherOut, postMap);
            postFilter(temp, [], context, xml);

            // Un-match failing elements by moving them back to matcherIn
            i = temp.length;
            while (i--) {
              if ((elem = temp[i])) {
                matcherOut[postMap[i]] = !(matcherIn[postMap[i]] = elem);
              }
            }
          }

          if (seed) {
            if (postFinder || preFilter) {
              if (postFinder) {
                // Get the final matcherOut by condensing this intermediate into postFinder contexts
                temp = [];
                i = matcherOut.length;
                while (i--) {
                  if ((elem = matcherOut[i])) {
                    // Restore matcherIn since elem is not yet a final match
                    temp.push((matcherIn[i] = elem));
                  }
                }
                postFinder(null, (matcherOut = []), temp, xml);
              }

              // Move matched elements from seed to results to keep them synchronized
              i = matcherOut.length;
              while (i--) {
                if ((elem = matcherOut[i]) && (temp = postFinder ? indexOf(seed, elem) : preMap[i]) > -1) {
                  seed[temp] = !(results[temp] = elem);
                }
              }
            }

            // Add elements to results, through postFinder if defined
          } else {
            matcherOut = condense(matcherOut === results ? matcherOut.splice(preexisting, matcherOut.length) : matcherOut);
            if (postFinder) {
              postFinder(null, results, matcherOut, xml);
            } else {
              push.apply(results, matcherOut);
            }
          }
        });
      }

      function matcherFromTokens(tokens) {
        var checkContext,
          matcher,
          j,
          len = tokens.length,
          leadingRelative = Expr.relative[tokens[0].type],
          implicitRelative = leadingRelative || Expr.relative[' '],
          i = leadingRelative ? 1 : 0,
          // The foundational matcher ensures that elements are reachable from top-level context(s)
          matchContext = addCombinator(
            function (elem) {
              return elem === checkContext;
            },
            implicitRelative,
            true
          ),
          matchAnyContext = addCombinator(
            function (elem) {
              return indexOf(checkContext, elem) > -1;
            },
            implicitRelative,
            true
          ),
          matchers = [
            function (elem, context, xml) {
              var ret =
                (!leadingRelative && (xml || context !== outermostContext)) ||
                ((checkContext = context).nodeType ? matchContext(elem, context, xml) : matchAnyContext(elem, context, xml));

              // Avoid hanging onto element (issue #299)
              checkContext = null;
              return ret;
            }
          ];

        for (; i < len; i++) {
          if ((matcher = Expr.relative[tokens[i].type])) {
            matchers = [addCombinator(elementMatcher(matchers), matcher)];
          } else {
            matcher = Expr.filter[tokens[i].type].apply(null, tokens[i].matches);

            // Return special upon seeing a positional matcher
            if (matcher[expando]) {
              // Find the next relative operator (if any) for proper handling
              j = ++i;
              for (; j < len; j++) {
                if (Expr.relative[tokens[j].type]) {
                  break;
                }
              }
              return setMatcher(
                i > 1 && elementMatcher(matchers),
                i > 1 &&
                  toSelector(
                    // If the preceding token was a descendant combinator, insert an implicit any-element `*`
                    tokens.slice(0, i - 1).concat({ value: tokens[i - 2].type === ' ' ? '*' : '' })
                  ).replace(rtrim, '$1'),
                matcher,
                i < j && matcherFromTokens(tokens.slice(i, j)),
                j < len && matcherFromTokens((tokens = tokens.slice(j))),
                j < len && toSelector(tokens)
              );
            }
            matchers.push(matcher);
          }
        }

        return elementMatcher(matchers);
      }

      function matcherFromGroupMatchers(elementMatchers, setMatchers) {
        var bySet = setMatchers.length > 0,
          byElement = elementMatchers.length > 0,
          superMatcher = function (seed, context, xml, results, outermost) {
            var elem,
              j,
              matcher,
              matchedCount = 0,
              i = '0',
              unmatched = seed && [],
              setMatched = [],
              contextBackup = outermostContext,
              // We must always have either seed elements or outermost context
              elems = seed || (byElement && Expr.find['TAG']('*', outermost)),
              // Use integer dirruns iff this is the outermost matcher
              dirrunsUnique = (dirruns += contextBackup == null ? 1 : Math.random() || 0.1),
              len = elems.length;

            if (outermost) {
              // Support: IE 11+, Edge 17 - 18+
              // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
              // two documents; shallow comparisons work.
              // eslint-disable-next-line eqeqeq
              outermostContext = context == document || context || outermost;
            }

            // Add elements passing elementMatchers directly to results
            // Support: IE<9, Safari
            // Tolerate NodeList properties (IE: "length"; Safari: <number>) matching elements by id
            for (; i !== len && (elem = elems[i]) != null; i++) {
              if (byElement && elem) {
                j = 0;

                // Support: IE 11+, Edge 17 - 18+
                // IE/Edge sometimes throw a "Permission denied" error when strict-comparing
                // two documents; shallow comparisons work.
                // eslint-disable-next-line eqeqeq
                if (!context && elem.ownerDocument != document) {
                  setDocument(elem);
                  xml = !documentIsHTML;
                }
                while ((matcher = elementMatchers[j++])) {
                  if (matcher(elem, context || document, xml)) {
                    results.push(elem);
                    break;
                  }
                }
                if (outermost) {
                  dirruns = dirrunsUnique;
                }
              }

              // Track unmatched elements for set filters
              if (bySet) {
                // They will have gone through all possible matchers
                if ((elem = !matcher && elem)) {
                  matchedCount--;
                }

                // Lengthen the array for every element, matched or not
                if (seed) {
                  unmatched.push(elem);
                }
              }
            }

            // `i` is now the count of elements visited above, and adding it to `matchedCount`
            // makes the latter nonnegative.
            matchedCount += i;

            // Apply set filters to unmatched elements
            // NOTE: This can be skipped if there are no unmatched elements (i.e., `matchedCount`
            // equals `i`), unless we didn't visit _any_ elements in the above loop because we have
            // no element matchers and no seed.
            // Incrementing an initially-string "0" `i` allows `i` to remain a string only in that
            // case, which will result in a "00" `matchedCount` that differs from `i` but is also
            // numerically zero.
            if (bySet && i !== matchedCount) {
              j = 0;
              while ((matcher = setMatchers[j++])) {
                matcher(unmatched, setMatched, context, xml);
              }

              if (seed) {
                // Reintegrate element matches to eliminate the need for sorting
                if (matchedCount > 0) {
                  while (i--) {
                    if (!(unmatched[i] || setMatched[i])) {
                      setMatched[i] = pop.call(results);
                    }
                  }
                }

                // Discard index placeholder values to get only actual matches
                setMatched = condense(setMatched);
              }

              // Add matches to results
              push.apply(results, setMatched);

              // Seedless set matches succeeding multiple successful matchers stipulate sorting
              if (outermost && !seed && setMatched.length > 0 && matchedCount + setMatchers.length > 1) {
                Sizzle.uniqueSort(results);
              }
            }

            // Override manipulation of globals by nested matchers
            if (outermost) {
              dirruns = dirrunsUnique;
              outermostContext = contextBackup;
            }

            return unmatched;
          };

        return bySet ? markFunction(superMatcher) : superMatcher;
      }

      compile = Sizzle.compile = function (selector, match /* Internal Use Only */) {
        var i,
          setMatchers = [],
          elementMatchers = [],
          cached = compilerCache[selector + ' '];

        if (!cached) {
          // Generate a function of recursive functions that can be used to check each element
          if (!match) {
            match = tokenize(selector);
          }
          i = match.length;
          while (i--) {
            cached = matcherFromTokens(match[i]);
            if (cached[expando]) {
              setMatchers.push(cached);
            } else {
              elementMatchers.push(cached);
            }
          }

          // Cache the compiled function
          cached = compilerCache(selector, matcherFromGroupMatchers(elementMatchers, setMatchers));

          // Save selector and tokenization
          cached.selector = selector;
        }
        return cached;
      };

      /**
       * A low-level selection function that works with Sizzle's compiled
       *  selector functions
       * @param {String|Function} selector A selector or a pre-compiled
       *  selector function built with Sizzle.compile
       * @param {Element} context
       * @param {Array} [results]
       * @param {Array} [seed] A set of elements to match against
       */
      select = Sizzle.select = function (selector, context, results, seed) {
        var i,
          tokens,
          token,
          type,
          find,
          compiled = typeof selector === 'function' && selector,
          match = !seed && tokenize((selector = compiled.selector || selector));

        results = results || [];

        // Try to minimize operations if there is only one selector in the list and no seed
        // (the latter of which guarantees us context)
        if (match.length === 1) {
          // Reduce context if the leading compound selector is an ID
          tokens = match[0] = match[0].slice(0);
          if (tokens.length > 2 && (token = tokens[0]).type === 'ID' && context.nodeType === 9 && documentIsHTML && Expr.relative[tokens[1].type]) {
            context = (Expr.find['ID'](token.matches[0].replace(runescape, funescape), context) || [])[0];
            if (!context) {
              return results;

              // Precompiled matchers will still verify ancestry, so step up a level
            } else if (compiled) {
              context = context.parentNode;
            }

            selector = selector.slice(tokens.shift().value.length);
          }

          // Fetch a seed set for right-to-left matching
          i = matchExpr['needsContext'].test(selector) ? 0 : tokens.length;
          while (i--) {
            token = tokens[i];

            // Abort if we hit a combinator
            if (Expr.relative[(type = token.type)]) {
              break;
            }
            if ((find = Expr.find[type])) {
              // Search, expanding context for leading sibling combinators
              if (
                (seed = find(token.matches[0].replace(runescape, funescape), (rsibling.test(tokens[0].type) && testContext(context.parentNode)) || context))
              ) {
                // If seed is empty or no tokens remain, we can return early
                tokens.splice(i, 1);
                selector = seed.length && toSelector(tokens);
                if (!selector) {
                  push.apply(results, seed);
                  return results;
                }

                break;
              }
            }
          }
        }

        // Compile and execute a filtering function if one is not provided
        // Provide `match` to avoid retokenization if we modified the selector above
        (compiled || compile(selector, match))(
          seed,
          context,
          !documentIsHTML,
          results,
          !context || (rsibling.test(selector) && testContext(context.parentNode)) || context
        );
        return results;
      };

      // One-time assignments

      // Sort stability
      support.sortStable = expando.split('').sort(sortOrder).join('') === expando;

      // Support: Chrome 14-35+
      // Always assume duplicates if they aren't passed to the comparison function
      support.detectDuplicates = !!hasDuplicate;

      // Initialize against the default document
      setDocument();

      // Support: Webkit<537.32 - Safari 6.0.3/Chrome 25 (fixed in Chrome 27)
      // Detached nodes confoundingly follow *each other*
      support.sortDetached = assert(function (el) {
        // Should return 1, but returns 4 (following)
        return el.compareDocumentPosition(document.createElement('fieldset')) & 1;
      });

      // Support: IE<8
      // Prevent attribute/property "interpolation"
      // https://msdn.microsoft.com/en-us/library/ms536429%28VS.85%29.aspx
      if (
        !assert(function (el) {
          el.innerHTML = "<a href='#'></a>";
          return el.firstChild.getAttribute('href') === '#';
        })
      ) {
        addHandle('type|href|height|width', function (elem, name, isXML) {
          if (!isXML) {
            return elem.getAttribute(name, name.toLowerCase() === 'type' ? 1 : 2);
          }
        });
      }

      // Support: IE<9
      // Use defaultValue in place of getAttribute("value")
      if (
        !support.attributes ||
        !assert(function (el) {
          el.innerHTML = '<input/>';
          el.firstChild.setAttribute('value', '');
          return el.firstChild.getAttribute('value') === '';
        })
      ) {
        addHandle('value', function (elem, _name, isXML) {
          if (!isXML && elem.nodeName.toLowerCase() === 'input') {
            return elem.defaultValue;
          }
        });
      }

      // Support: IE<9
      // Use getAttributeNode to fetch booleans when getAttribute lies
      if (
        !assert(function (el) {
          return el.getAttribute('disabled') == null;
        })
      ) {
        addHandle(booleans, function (elem, name, isXML) {
          var val;
          if (!isXML) {
            return elem[name] === true ? name.toLowerCase() : (val = elem.getAttributeNode(name)) && val.specified ? val.value : null;
          }
        });
      }

      return Sizzle;
    })(window);

  jQuery.find = Sizzle;
  jQuery.expr = Sizzle.selectors;

  // Deprecated
  jQuery.expr[':'] = jQuery.expr.pseudos;
  jQuery.uniqueSort = jQuery.unique = Sizzle.uniqueSort;
  jQuery.text = Sizzle.getText;
  jQuery.isXMLDoc = Sizzle.isXML;
  jQuery.contains = Sizzle.contains;
  jQuery.escapeSelector = Sizzle.escape;

  var dir = function (elem, dir, until) {
    var matched = [],
      truncate = until !== undefined;

    while ((elem = elem[dir]) && elem.nodeType !== 9) {
      if (elem.nodeType === 1) {
        if (truncate && jQuery(elem).is(until)) {
          break;
        }
        matched.push(elem);
      }
    }
    return matched;
  };

  var siblings = function (n, elem) {
    var matched = [];

    for (; n; n = n.nextSibling) {
      if (n.nodeType === 1 && n !== elem) {
        matched.push(n);
      }
    }

    return matched;
  };

  var rneedsContext = jQuery.expr.match.needsContext;

  function nodeName(elem, name) {
    return elem.nodeName && elem.nodeName.toLowerCase() === name.toLowerCase();
  }
  var rsingleTag = /^<([a-z][^\/\0>:\x20\t\r\n\f]*)[\x20\t\r\n\f]*\/?>(?:<\/\1>|)$/i;

  // Implement the identical functionality for filter and not
  function winnow(elements, qualifier, not) {
    if (isFunction(qualifier)) {
      return jQuery.grep(elements, function (elem, i) {
        return !!qualifier.call(elem, i, elem) !== not;
      });
    }

    // Single element
    if (qualifier.nodeType) {
      return jQuery.grep(elements, function (elem) {
        return (elem === qualifier) !== not;
      });
    }

    // Arraylike of elements (jQuery, arguments, Array)
    if (typeof qualifier !== 'string') {
      return jQuery.grep(elements, function (elem) {
        return indexOf.call(qualifier, elem) > -1 !== not;
      });
    }

    // Filtered directly for both simple and complex selectors
    return jQuery.filter(qualifier, elements, not);
  }

  jQuery.filter = function (expr, elems, not) {
    var elem = elems[0];

    if (not) {
      expr = ':not(' + expr + ')';
    }

    if (elems.length === 1 && elem.nodeType === 1) {
      return jQuery.find.matchesSelector(elem, expr) ? [elem] : [];
    }

    return jQuery.find.matches(
      expr,
      jQuery.grep(elems, function (elem) {
        return elem.nodeType === 1;
      })
    );
  };

  jQuery.fn.extend({
    find: function (selector) {
      var i,
        ret,
        len = this.length,
        self = this;

      if (typeof selector !== 'string') {
        return this.pushStack(
          jQuery(selector).filter(function () {
            for (i = 0; i < len; i++) {
              if (jQuery.contains(self[i], this)) {
                return true;
              }
            }
          })
        );
      }

      ret = this.pushStack([]);

      for (i = 0; i < len; i++) {
        jQuery.find(selector, self[i], ret);
      }

      return len > 1 ? jQuery.uniqueSort(ret) : ret;
    },
    filter: function (selector) {
      return this.pushStack(winnow(this, selector || [], false));
    },
    not: function (selector) {
      return this.pushStack(winnow(this, selector || [], true));
    },
    is: function (selector) {
      return !!winnow(
        this,

        // If this is a positional/relative selector, check membership in the returned set
        // so $("p:first").is("p:last") won't return true for a doc with two "p".
        typeof selector === 'string' && rneedsContext.test(selector) ? jQuery(selector) : selector || [],
        false
      ).length;
    }
  });

  // Initialize a jQuery object

  // A central reference to the root jQuery(document)
  var rootjQuery,
    // A simple way to check for HTML strings
    // Prioritize #id over <tag> to avoid XSS via location.hash (#9521)
    // Strict HTML recognition (#11290: must start with <)
    // Shortcut simple #id case for speed
    rquickExpr = /^(?:\s*(<[\w\W]+>)[^>]*|#([\w-]+))$/,
    init = (jQuery.fn.init = function (selector, context, root) {
      var match, elem;

      // HANDLE: $(""), $(null), $(undefined), $(false)
      if (!selector) {
        return this;
      }

      // Method init() accepts an alternate rootjQuery
      // so migrate can support jQuery.sub (gh-2101)
      root = root || rootjQuery;

      // Handle HTML strings
      if (typeof selector === 'string') {
        if (selector[0] === '<' && selector[selector.length - 1] === '>' && selector.length >= 3) {
          // Assume that strings that start and end with <> are HTML and skip the regex check
          match = [null, selector, null];
        } else {
          match = rquickExpr.exec(selector);
        }

        // Match html or make sure no context is specified for #id
        if (match && (match[1] || !context)) {
          // HANDLE: $(html) -> $(array)
          if (match[1]) {
            context = context instanceof jQuery ? context[0] : context;

            // Option to run scripts is true for back-compat
            // Intentionally let the error be thrown if parseHTML is not present
            jQuery.merge(this, jQuery.parseHTML(match[1], context && context.nodeType ? context.ownerDocument || context : document, true));

            // HANDLE: $(html, props)
            if (rsingleTag.test(match[1]) && jQuery.isPlainObject(context)) {
              for (match in context) {
                // Properties of context are called as methods if possible
                if (isFunction(this[match])) {
                  this[match](context[match]);

                  // ...and otherwise set as attributes
                } else {
                  this.attr(match, context[match]);
                }
              }
            }

            return this;

            // HANDLE: $(#id)
          } else {
            elem = document.getElementById(match[2]);

            if (elem) {
              // Inject the element directly into the jQuery object
              this[0] = elem;
              this.length = 1;
            }
            return this;
          }

          // HANDLE: $(expr, $(...))
        } else if (!context || context.jquery) {
          return (context || root).find(selector);

          // HANDLE: $(expr, context)
          // (which is just equivalent to: $(context).find(expr)
        } else {
          return this.constructor(context).find(selector);
        }

        // HANDLE: $(DOMElement)
      } else if (selector.nodeType) {
        this[0] = selector;
        this.length = 1;
        return this;

        // HANDLE: $(function)
        // Shortcut for document ready
      } else if (isFunction(selector)) {
        return root.ready !== undefined
          ? root.ready(selector)
          : // Execute immediately if ready is not present
            selector(jQuery);
      }

      return jQuery.makeArray(selector, this);
    });

  // Give the init function the jQuery prototype for later instantiation
  init.prototype = jQuery.fn;

  // Initialize central reference
  rootjQuery = jQuery(document);

  var rparentsprev = /^(?:parents|prev(?:Until|All))/,
    // Methods guaranteed to produce a unique set when starting from a unique set
    guaranteedUnique = {
      children: true,
      contents: true,
      next: true,
      prev: true
    };

  jQuery.fn.extend({
    has: function (target) {
      var targets = jQuery(target, this),
        l = targets.length;

      return this.filter(function () {
        var i = 0;
        for (; i < l; i++) {
          if (jQuery.contains(this, targets[i])) {
            return true;
          }
        }
      });
    },

    closest: function (selectors, context) {
      var cur,
        i = 0,
        l = this.length,
        matched = [],
        targets = typeof selectors !== 'string' && jQuery(selectors);

      // Positional selectors never match, since there's no _selection_ context
      if (!rneedsContext.test(selectors)) {
        for (; i < l; i++) {
          for (cur = this[i]; cur && cur !== context; cur = cur.parentNode) {
            // Always skip document fragments
            if (
              cur.nodeType < 11 &&
              (targets
                ? targets.index(cur) > -1
                : // Don't pass non-elements to Sizzle
                  cur.nodeType === 1 && jQuery.find.matchesSelector(cur, selectors))
            ) {
              matched.push(cur);
              break;
            }
          }
        }
      }

      return this.pushStack(matched.length > 1 ? jQuery.uniqueSort(matched) : matched);
    },

    // Determine the position of an element within the set
    index: function (elem) {
      // No argument, return index in parent
      if (!elem) {
        return this[0] && this[0].parentNode ? this.first().prevAll().length : -1;
      }

      // Index in selector
      if (typeof elem === 'string') {
        return indexOf.call(jQuery(elem), this[0]);
      }

      // Locate the position of the desired element
      return indexOf.call(
        this,

        // If it receives a jQuery object, the first element is used
        elem.jquery ? elem[0] : elem
      );
    },

    add: function (selector, context) {
      return this.pushStack(jQuery.uniqueSort(jQuery.merge(this.get(), jQuery(selector, context))));
    },

    addBack: function (selector) {
      return this.add(selector == null ? this.prevObject : this.prevObject.filter(selector));
    }
  });

  function sibling(cur, dir) {
    while ((cur = cur[dir]) && cur.nodeType !== 1) {}
    return cur;
  }

  jQuery.each(
    {
      parent: function (elem) {
        var parent = elem.parentNode;
        return parent && parent.nodeType !== 11 ? parent : null;
      },
      parents: function (elem) {
        return dir(elem, 'parentNode');
      },
      parentsUntil: function (elem, _i, until) {
        return dir(elem, 'parentNode', until);
      },
      next: function (elem) {
        return sibling(elem, 'nextSibling');
      },
      prev: function (elem) {
        return sibling(elem, 'previousSibling');
      },
      nextAll: function (elem) {
        return dir(elem, 'nextSibling');
      },
      prevAll: function (elem) {
        return dir(elem, 'previousSibling');
      },
      nextUntil: function (elem, _i, until) {
        return dir(elem, 'nextSibling', until);
      },
      prevUntil: function (elem, _i, until) {
        return dir(elem, 'previousSibling', until);
      },
      siblings: function (elem) {
        return siblings((elem.parentNode || {}).firstChild, elem);
      },
      children: function (elem) {
        return siblings(elem.firstChild);
      },
      contents: function (elem) {
        if (
          elem.contentDocument != null &&
          // Support: IE 11+
          // <object> elements with no `data` attribute has an object
          // `contentDocument` with a `null` prototype.
          getProto(elem.contentDocument)
        ) {
          return elem.contentDocument;
        }

        // Support: IE 9 - 11 only, iOS 7 only, Android Browser <=4.3 only
        // Treat the template element as a regular one in browsers that
        // don't support it.
        if (nodeName(elem, 'template')) {
          elem = elem.content || elem;
        }

        return jQuery.merge([], elem.childNodes);
      }
    },
    function (name, fn) {
      jQuery.fn[name] = function (until, selector) {
        var matched = jQuery.map(this, fn, until);

        if (name.slice(-5) !== 'Until') {
          selector = until;
        }

        if (selector && typeof selector === 'string') {
          matched = jQuery.filter(selector, matched);
        }

        if (this.length > 1) {
          // Remove duplicates
          if (!guaranteedUnique[name]) {
            jQuery.uniqueSort(matched);
          }

          // Reverse order for parents* and prev-derivatives
          if (rparentsprev.test(name)) {
            matched.reverse();
          }
        }

        return this.pushStack(matched);
      };
    }
  );
  var rnothtmlwhite = /[^\x20\t\r\n\f]+/g;

  // Convert String-formatted options into Object-formatted ones
  function createOptions(options) {
    var object = {};
    jQuery.each(options.match(rnothtmlwhite) || [], function (_, flag) {
      object[flag] = true;
    });
    return object;
  }

  /*
   * Create a callback list using the following parameters:
   *
   *	options: an optional list of space-separated options that will change how
   *			the callback list behaves or a more traditional option object
   *
   * By default a callback list will act like an event callback list and can be
   * "fired" multiple times.
   *
   * Possible options:
   *
   *	once:			will ensure the callback list can only be fired once (like a Deferred)
   *
   *	memory:			will keep track of previous values and will call any callback added
   *					after the list has been fired right away with the latest "memorized"
   *					values (like a Deferred)
   *
   *	unique:			will ensure a callback can only be added once (no duplicate in the list)
   *
   *	stopOnFalse:	interrupt callings when a callback returns false
   *
   */
  jQuery.Callbacks = function (options) {
    // Convert options from String-formatted to Object-formatted if needed
    // (we check in cache first)
    options = typeof options === 'string' ? createOptions(options) : jQuery.extend({}, options);

    var // Flag to know if list is currently firing
      firing,
      // Last fire value for non-forgettable lists
      memory,
      // Flag to know if list was already fired
      fired,
      // Flag to prevent firing
      locked,
      // Actual callback list
      list = [],
      // Queue of execution data for repeatable lists
      queue = [],
      // Index of currently firing callback (modified by add/remove as needed)
      firingIndex = -1,
      // Fire callbacks
      fire = function () {
        // Enforce single-firing
        locked = locked || options.once;

        // Execute callbacks for all pending executions,
        // respecting firingIndex overrides and runtime changes
        fired = firing = true;
        for (; queue.length; firingIndex = -1) {
          memory = queue.shift();
          while (++firingIndex < list.length) {
            // Run callback and check for early termination
            if (list[firingIndex].apply(memory[0], memory[1]) === false && options.stopOnFalse) {
              // Jump to end and forget the data so .add doesn't re-fire
              firingIndex = list.length;
              memory = false;
            }
          }
        }

        // Forget the data if we're done with it
        if (!options.memory) {
          memory = false;
        }

        firing = false;

        // Clean up if we're done firing for good
        if (locked) {
          // Keep an empty list if we have data for future add calls
          if (memory) {
            list = [];

            // Otherwise, this object is spent
          } else {
            list = '';
          }
        }
      },
      // Actual Callbacks object
      self = {
        // Add a callback or a collection of callbacks to the list
        add: function () {
          if (list) {
            // If we have memory from a past run, we should fire after adding
            if (memory && !firing) {
              firingIndex = list.length - 1;
              queue.push(memory);
            }

            (function add(args) {
              jQuery.each(args, function (_, arg) {
                if (isFunction(arg)) {
                  if (!options.unique || !self.has(arg)) {
                    list.push(arg);
                  }
                } else if (arg && arg.length && toType(arg) !== 'string') {
                  // Inspect recursively
                  add(arg);
                }
              });
            })(arguments);

            if (memory && !firing) {
              fire();
            }
          }
          return this;
        },

        // Remove a callback from the list
        remove: function () {
          jQuery.each(arguments, function (_, arg) {
            var index;
            while ((index = jQuery.inArray(arg, list, index)) > -1) {
              list.splice(index, 1);

              // Handle firing indexes
              if (index <= firingIndex) {
                firingIndex--;
              }
            }
          });
          return this;
        },

        // Check if a given callback is in the list.
        // If no argument is given, return whether or not list has callbacks attached.
        has: function (fn) {
          return fn ? jQuery.inArray(fn, list) > -1 : list.length > 0;
        },

        // Remove all callbacks from the list
        empty: function () {
          if (list) {
            list = [];
          }
          return this;
        },

        // Disable .fire and .add
        // Abort any current/pending executions
        // Clear all callbacks and values
        disable: function () {
          locked = queue = [];
          list = memory = '';
          return this;
        },
        disabled: function () {
          return !list;
        },

        // Disable .fire
        // Also disable .add unless we have memory (since it would have no effect)
        // Abort any pending executions
        lock: function () {
          locked = queue = [];
          if (!memory && !firing) {
            list = memory = '';
          }
          return this;
        },
        locked: function () {
          return !!locked;
        },

        // Call all callbacks with the given context and arguments
        fireWith: function (context, args) {
          if (!locked) {
            args = args || [];
            args = [context, args.slice ? args.slice() : args];
            queue.push(args);
            if (!firing) {
              fire();
            }
          }
          return this;
        },

        // Call all the callbacks with the given arguments
        fire: function () {
          self.fireWith(this, arguments);
          return this;
        },

        // To know if the callbacks have already been called at least once
        fired: function () {
          return !!fired;
        }
      };

    return self;
  };

  function Identity(v) {
    return v;
  }
  function Thrower(ex) {
    throw ex;
  }

  function adoptValue(value, resolve, reject, noValue) {
    var method;

    try {
      // Check for promise aspect first to privilege synchronous behavior
      if (value && isFunction((method = value.promise))) {
        method.call(value).done(resolve).fail(reject);

        // Other thenables
      } else if (value && isFunction((method = value.then))) {
        method.call(value, resolve, reject);

        // Other non-thenables
      } else {
        // Control `resolve` arguments by letting Array#slice cast boolean `noValue` to integer:
        // * false: [ value ].slice( 0 ) => resolve( value )
        // * true: [ value ].slice( 1 ) => resolve()
        resolve.apply(undefined, [value].slice(noValue));
      }

      // For Promises/A+, convert exceptions into rejections
      // Since jQuery.when doesn't unwrap thenables, we can skip the extra checks appearing in
      // Deferred#then to conditionally suppress rejection.
    } catch (value) {
      // Support: Android 4.0 only
      // Strict mode functions invoked without .call/.apply get global-object context
      reject.apply(undefined, [value]);
    }
  }

  jQuery.extend({
    Deferred: function (func) {
      var tuples = [
          // action, add listener, callbacks,
          // ... .then handlers, argument index, [final state]
          ['notify', 'progress', jQuery.Callbacks('memory'), jQuery.Callbacks('memory'), 2],
          ['resolve', 'done', jQuery.Callbacks('once memory'), jQuery.Callbacks('once memory'), 0, 'resolved'],
          ['reject', 'fail', jQuery.Callbacks('once memory'), jQuery.Callbacks('once memory'), 1, 'rejected']
        ],
        state = 'pending',
        promise = {
          state: function () {
            return state;
          },
          always: function () {
            deferred.done(arguments).fail(arguments);
            return this;
          },
          catch: function (fn) {
            return promise.then(null, fn);
          },

          // Keep pipe for back-compat
          pipe: function (/* fnDone, fnFail, fnProgress */) {
            var fns = arguments;

            return jQuery
              .Deferred(function (newDefer) {
                jQuery.each(tuples, function (_i, tuple) {
                  // Map tuples (progress, done, fail) to arguments (done, fail, progress)
                  var fn = isFunction(fns[tuple[4]]) && fns[tuple[4]];

                  // deferred.progress(function() { bind to newDefer or newDefer.notify })
                  // deferred.done(function() { bind to newDefer or newDefer.resolve })
                  // deferred.fail(function() { bind to newDefer or newDefer.reject })
                  deferred[tuple[1]](function () {
                    var returned = fn && fn.apply(this, arguments);
                    if (returned && isFunction(returned.promise)) {
                      returned.promise().progress(newDefer.notify).done(newDefer.resolve).fail(newDefer.reject);
                    } else {
                      newDefer[tuple[0] + 'With'](this, fn ? [returned] : arguments);
                    }
                  });
                });
                fns = null;
              })
              .promise();
          },
          then: function (onFulfilled, onRejected, onProgress) {
            var maxDepth = 0;
            function resolve(depth, deferred, handler, special) {
              return function () {
                var that = this,
                  args = arguments,
                  mightThrow = function () {
                    var returned, then;

                    // Support: Promises/A+ section 2.3.3.3.3
                    // https://promisesaplus.com/#point-59
                    // Ignore double-resolution attempts
                    if (depth < maxDepth) {
                      return;
                    }

                    returned = handler.apply(that, args);

                    // Support: Promises/A+ section 2.3.1
                    // https://promisesaplus.com/#point-48
                    if (returned === deferred.promise()) {
                      throw new TypeError('Thenable self-resolution');
                    }

                    // Support: Promises/A+ sections 2.3.3.1, 3.5
                    // https://promisesaplus.com/#point-54
                    // https://promisesaplus.com/#point-75
                    // Retrieve `then` only once
                    then =
                      returned &&
                      // Support: Promises/A+ section 2.3.4
                      // https://promisesaplus.com/#point-64
                      // Only check objects and functions for thenability
                      (typeof returned === 'object' || typeof returned === 'function') &&
                      returned.then;

                    // Handle a returned thenable
                    if (isFunction(then)) {
                      // Special processors (notify) just wait for resolution
                      if (special) {
                        then.call(returned, resolve(maxDepth, deferred, Identity, special), resolve(maxDepth, deferred, Thrower, special));

                        // Normal processors (resolve) also hook into progress
                      } else {
                        // ...and disregard older resolution values
                        maxDepth++;

                        then.call(
                          returned,
                          resolve(maxDepth, deferred, Identity, special),
                          resolve(maxDepth, deferred, Thrower, special),
                          resolve(maxDepth, deferred, Identity, deferred.notifyWith)
                        );
                      }

                      // Handle all other returned values
                    } else {
                      // Only substitute handlers pass on context
                      // and multiple values (non-spec behavior)
                      if (handler !== Identity) {
                        that = undefined;
                        args = [returned];
                      }

                      // Process the value(s)
                      // Default process is resolve
                      (special || deferred.resolveWith)(that, args);
                    }
                  },
                  // Only normal processors (resolve) catch and reject exceptions
                  process = special
                    ? mightThrow
                    : function () {
                        try {
                          mightThrow();
                        } catch (e) {
                          if (jQuery.Deferred.exceptionHook) {
                            jQuery.Deferred.exceptionHook(e, process.stackTrace);
                          }

                          // Support: Promises/A+ section 2.3.3.3.4.1
                          // https://promisesaplus.com/#point-61
                          // Ignore post-resolution exceptions
                          if (depth + 1 >= maxDepth) {
                            // Only substitute handlers pass on context
                            // and multiple values (non-spec behavior)
                            if (handler !== Thrower) {
                              that = undefined;
                              args = [e];
                            }

                            deferred.rejectWith(that, args);
                          }
                        }
                      };

                // Support: Promises/A+ section 2.3.3.3.1
                // https://promisesaplus.com/#point-57
                // Re-resolve promises immediately to dodge false rejection from
                // subsequent errors
                if (depth) {
                  process();
                } else {
                  // Call an optional hook to record the stack, in case of exception
                  // since it's otherwise lost when execution goes async
                  if (jQuery.Deferred.getStackHook) {
                    process.stackTrace = jQuery.Deferred.getStackHook();
                  }
                  window.setTimeout(process);
                }
              };
            }

            return jQuery
              .Deferred(function (newDefer) {
                // progress_handlers.add( ... )
                tuples[0][3].add(resolve(0, newDefer, isFunction(onProgress) ? onProgress : Identity, newDefer.notifyWith));

                // fulfilled_handlers.add( ... )
                tuples[1][3].add(resolve(0, newDefer, isFunction(onFulfilled) ? onFulfilled : Identity));

                // rejected_handlers.add( ... )
                tuples[2][3].add(resolve(0, newDefer, isFunction(onRejected) ? onRejected : Thrower));
              })
              .promise();
          },

          // Get a promise for this deferred
          // If obj is provided, the promise aspect is added to the object
          promise: function (obj) {
            return obj != null ? jQuery.extend(obj, promise) : promise;
          }
        },
        deferred = {};

      // Add list-specific methods
      jQuery.each(tuples, function (i, tuple) {
        var list = tuple[2],
          stateString = tuple[5];

        // promise.progress = list.add
        // promise.done = list.add
        // promise.fail = list.add
        promise[tuple[1]] = list.add;

        // Handle state
        if (stateString) {
          list.add(
            function () {
              // state = "resolved" (i.e., fulfilled)
              // state = "rejected"
              state = stateString;
            },

            // rejected_callbacks.disable
            // fulfilled_callbacks.disable
            tuples[3 - i][2].disable,

            // rejected_handlers.disable
            // fulfilled_handlers.disable
            tuples[3 - i][3].disable,

            // progress_callbacks.lock
            tuples[0][2].lock,

            // progress_handlers.lock
            tuples[0][3].lock
          );
        }

        // progress_handlers.fire
        // fulfilled_handlers.fire
        // rejected_handlers.fire
        list.add(tuple[3].fire);

        // deferred.notify = function() { deferred.notifyWith(...) }
        // deferred.resolve = function() { deferred.resolveWith(...) }
        // deferred.reject = function() { deferred.rejectWith(...) }
        deferred[tuple[0]] = function () {
          deferred[tuple[0] + 'With'](this === deferred ? undefined : this, arguments);
          return this;
        };

        // deferred.notifyWith = list.fireWith
        // deferred.resolveWith = list.fireWith
        // deferred.rejectWith = list.fireWith
        deferred[tuple[0] + 'With'] = list.fireWith;
      });

      // Make the deferred a promise
      promise.promise(deferred);

      // Call given func if any
      if (func) {
        func.call(deferred, deferred);
      }

      // All done!
      return deferred;
    },

    // Deferred helper
    when: function (singleValue) {
      var // count of uncompleted subordinates
        remaining = arguments.length,
        // count of unprocessed arguments
        i = remaining,
        // subordinate fulfillment data
        resolveContexts = Array(i),
        resolveValues = slice.call(arguments),
        // the master Deferred
        master = jQuery.Deferred(),
        // subordinate callback factory
        updateFunc = function (i) {
          return function (value) {
            resolveContexts[i] = this;
            resolveValues[i] = arguments.length > 1 ? slice.call(arguments) : value;
            if (!--remaining) {
              master.resolveWith(resolveContexts, resolveValues);
            }
          };
        };

      // Single- and empty arguments are adopted like Promise.resolve
      if (remaining <= 1) {
        adoptValue(singleValue, master.done(updateFunc(i)).resolve, master.reject, !remaining);

        // Use .then() to unwrap secondary thenables (cf. gh-3000)
        if (master.state() === 'pending' || isFunction(resolveValues[i] && resolveValues[i].then)) {
          return master.then();
        }
      }

      // Multiple arguments are aggregated like Promise.all array elements
      while (i--) {
        adoptValue(resolveValues[i], updateFunc(i), master.reject);
      }

      return master.promise();
    }
  });

  // These usually indicate a programmer mistake during development,
  // warn about them ASAP rather than swallowing them by default.
  var rerrorNames = /^(Eval|Internal|Range|Reference|Syntax|Type|URI)Error$/;

  jQuery.Deferred.exceptionHook = function (error, stack) {
    // Support: IE 8 - 9 only
    // Console exists when dev tools are open, which can happen at any time
    if (window.console && window.console.warn && error && rerrorNames.test(error.name)) {
      window.console.warn('jQuery.Deferred exception: ' + error.message, error.stack, stack);
    }
  };

  jQuery.readyException = function (error) {
    window.setTimeout(function () {
      throw error;
    });
  };

  // The deferred used on DOM ready
  var readyList = jQuery.Deferred();

  jQuery.fn.ready = function (fn) {
    readyList
      .then(fn)

      // Wrap jQuery.readyException in a function so that the lookup
      // happens at the time of error handling instead of callback
      // registration.
      .catch(function (error) {
        jQuery.readyException(error);
      });

    return this;
  };

  jQuery.extend({
    // Is the DOM ready to be used? Set to true once it occurs.
    isReady: false,

    // A counter to track how many items to wait for before
    // the ready event fires. See #6781
    readyWait: 1,

    // Handle when the DOM is ready
    ready: function (wait) {
      // Abort if there are pending holds or we're already ready
      if (wait === true ? --jQuery.readyWait : jQuery.isReady) {
        return;
      }

      // Remember that the DOM is ready
      jQuery.isReady = true;

      // If a normal DOM Ready event fired, decrement, and wait if need be
      if (wait !== true && --jQuery.readyWait > 0) {
        return;
      }

      // If there are functions bound, to execute
      readyList.resolveWith(document, [jQuery]);
    }
  });

  jQuery.ready.then = readyList.then;

  // The ready event handler and self cleanup method
  function completed() {
    document.removeEventListener('DOMContentLoaded', completed);
    window.removeEventListener('load', completed);
    jQuery.ready();
  }

  // Catch cases where $(document).ready() is called
  // after the browser event has already occurred.
  // Support: IE <=9 - 10 only
  // Older IE sometimes signals "interactive" too soon
  if (document.readyState === 'complete' || (document.readyState !== 'loading' && !document.documentElement.doScroll)) {
    // Handle it asynchronously to allow scripts the opportunity to delay ready
    window.setTimeout(jQuery.ready);
  } else {
    // Use the handy event callback
    document.addEventListener('DOMContentLoaded', completed);

    // A fallback to window.onload, that will always work
    window.addEventListener('load', completed);
  }

  // Multifunctional method to get and set values of a collection
  // The value/s can optionally be executed if it's a function
  var access = function (elems, fn, key, value, chainable, emptyGet, raw) {
    var i = 0,
      len = elems.length,
      bulk = key == null;

    // Sets many values
    if (toType(key) === 'object') {
      chainable = true;
      for (i in key) {
        access(elems, fn, i, key[i], true, emptyGet, raw);
      }

      // Sets one value
    } else if (value !== undefined) {
      chainable = true;

      if (!isFunction(value)) {
        raw = true;
      }

      if (bulk) {
        // Bulk operations run against the entire set
        if (raw) {
          fn.call(elems, value);
          fn = null;

          // ...except when executing function values
        } else {
          bulk = fn;
          fn = function (elem, _key, value) {
            return bulk.call(jQuery(elem), value);
          };
        }
      }

      if (fn) {
        for (; i < len; i++) {
          fn(elems[i], key, raw ? value : value.call(elems[i], i, fn(elems[i], key)));
        }
      }
    }

    if (chainable) {
      return elems;
    }

    // Gets
    if (bulk) {
      return fn.call(elems);
    }

    return len ? fn(elems[0], key) : emptyGet;
  };

  // Matches dashed string for camelizing
  var rmsPrefix = /^-ms-/,
    rdashAlpha = /-([a-z])/g;

  // Used by camelCase as callback to replace()
  function fcamelCase(_all, letter) {
    return letter.toUpperCase();
  }

  // Convert dashed to camelCase; used by the css and data modules
  // Support: IE <=9 - 11, Edge 12 - 15
  // Microsoft forgot to hump their vendor prefix (#9572)
  function camelCase(string) {
    return string.replace(rmsPrefix, 'ms-').replace(rdashAlpha, fcamelCase);
  }
  var acceptData = function (owner) {
    // Accepts only:
    //  - Node
    //    - Node.ELEMENT_NODE
    //    - Node.DOCUMENT_NODE
    //  - Object
    //    - Any
    return owner.nodeType === 1 || owner.nodeType === 9 || !+owner.nodeType;
  };

  function Data() {
    this.expando = jQuery.expando + Data.uid++;
  }

  Data.uid = 1;

  Data.prototype = {
    cache: function (owner) {
      // Check if the owner object already has a cache
      var value = owner[this.expando];

      // If not, create one
      if (!value) {
        value = {};

        // We can accept data for non-element nodes in modern browsers,
        // but we should not, see #8335.
        // Always return an empty object.
        if (acceptData(owner)) {
          // If it is a node unlikely to be stringify-ed or looped over
          // use plain assignment
          if (owner.nodeType) {
            owner[this.expando] = value;

            // Otherwise secure it in a non-enumerable property
            // configurable must be true to allow the property to be
            // deleted when data is removed
          } else {
            Object.defineProperty(owner, this.expando, {
              value: value,
              configurable: true
            });
          }
        }
      }

      return value;
    },
    set: function (owner, data, value) {
      var prop,
        cache = this.cache(owner);

      // Handle: [ owner, key, value ] args
      // Always use camelCase key (gh-2257)
      if (typeof data === 'string') {
        cache[camelCase(data)] = value;

        // Handle: [ owner, { properties } ] args
      } else {
        // Copy the properties one-by-one to the cache object
        for (prop in data) {
          cache[camelCase(prop)] = data[prop];
        }
      }
      return cache;
    },
    get: function (owner, key) {
      return key === undefined
        ? this.cache(owner)
        : // Always use camelCase key (gh-2257)
          owner[this.expando] && owner[this.expando][camelCase(key)];
    },
    access: function (owner, key, value) {
      // In cases where either:
      //
      //   1. No key was specified
      //   2. A string key was specified, but no value provided
      //
      // Take the "read" path and allow the get method to determine
      // which value to return, respectively either:
      //
      //   1. The entire cache object
      //   2. The data stored at the key
      //
      if (key === undefined || (key && typeof key === 'string' && value === undefined)) {
        return this.get(owner, key);
      }

      // When the key is not a string, or both a key and value
      // are specified, set or extend (existing objects) with either:
      //
      //   1. An object of properties
      //   2. A key and value
      //
      this.set(owner, key, value);

      // Since the "set" path can have two possible entry points
      // return the expected data based on which path was taken[*]
      return value !== undefined ? value : key;
    },
    remove: function (owner, key) {
      var i,
        cache = owner[this.expando];

      if (cache === undefined) {
        return;
      }

      if (key !== undefined) {
        // Support array or space separated string of keys
        if (Array.isArray(key)) {
          // If key is an array of keys...
          // We always set camelCase keys, so remove that.
          key = key.map(camelCase);
        } else {
          key = camelCase(key);

          // If a key with the spaces exists, use it.
          // Otherwise, create an array by matching non-whitespace
          key = key in cache ? [key] : key.match(rnothtmlwhite) || [];
        }

        i = key.length;

        while (i--) {
          delete cache[key[i]];
        }
      }

      // Remove the expando if there's no more data
      if (key === undefined || jQuery.isEmptyObject(cache)) {
        // Support: Chrome <=35 - 45
        // Webkit & Blink performance suffers when deleting properties
        // from DOM nodes, so set to undefined instead
        // https://bugs.chromium.org/p/chromium/issues/detail?id=378607 (bug restricted)
        if (owner.nodeType) {
          owner[this.expando] = undefined;
        } else {
          delete owner[this.expando];
        }
      }
    },
    hasData: function (owner) {
      var cache = owner[this.expando];
      return cache !== undefined && !jQuery.isEmptyObject(cache);
    }
  };
  var dataPriv = new Data();

  var dataUser = new Data();

  //	Implementation Summary
  //
  //	1. Enforce API surface and semantic compatibility with 1.9.x branch
  //	2. Improve the module's maintainability by reducing the storage
  //		paths to a single mechanism.
  //	3. Use the same single mechanism to support "private" and "user" data.
  //	4. _Never_ expose "private" data to user code (TODO: Drop _data, _removeData)
  //	5. Avoid exposing implementation details on user objects (eg. expando properties)
  //	6. Provide a clear path for implementation upgrade to WeakMap in 2014

  var rbrace = /^(?:\{[\w\W]*\}|\[[\w\W]*\])$/,
    rmultiDash = /[A-Z]/g;

  function getData(data) {
    if (data === 'true') {
      return true;
    }

    if (data === 'false') {
      return false;
    }

    if (data === 'null') {
      return null;
    }

    // Only convert to a number if it doesn't change the string
    if (data === +data + '') {
      return +data;
    }

    if (rbrace.test(data)) {
      return JSON.parse(data);
    }

    return data;
  }

  function dataAttr(elem, key, data) {
    var name;

    // If nothing was found internally, try to fetch any
    // data from the HTML5 data-* attribute
    if (data === undefined && elem.nodeType === 1) {
      name = 'data-' + key.replace(rmultiDash, '-$&').toLowerCase();
      data = elem.getAttribute(name);

      if (typeof data === 'string') {
        try {
          data = getData(data);
        } catch (e) {}

        // Make sure we set the data so it isn't changed later
        dataUser.set(elem, key, data);
      } else {
        data = undefined;
      }
    }
    return data;
  }

  jQuery.extend({
    hasData: function (elem) {
      return dataUser.hasData(elem) || dataPriv.hasData(elem);
    },

    data: function (elem, name, data) {
      return dataUser.access(elem, name, data);
    },

    removeData: function (elem, name) {
      dataUser.remove(elem, name);
    },

    // TODO: Now that all calls to _data and _removeData have been replaced
    // with direct calls to dataPriv methods, these can be deprecated.
    _data: function (elem, name, data) {
      return dataPriv.access(elem, name, data);
    },

    _removeData: function (elem, name) {
      dataPriv.remove(elem, name);
    }
  });

  jQuery.fn.extend({
    data: function (key, value) {
      var i,
        name,
        data,
        elem = this[0],
        attrs = elem && elem.attributes;

      // Gets all values
      if (key === undefined) {
        if (this.length) {
          data = dataUser.get(elem);

          if (elem.nodeType === 1 && !dataPriv.get(elem, 'hasDataAttrs')) {
            i = attrs.length;
            while (i--) {
              // Support: IE 11 only
              // The attrs elements can be null (#14894)
              if (attrs[i]) {
                name = attrs[i].name;
                if (name.indexOf('data-') === 0) {
                  name = camelCase(name.slice(5));
                  dataAttr(elem, name, data[name]);
                }
              }
            }
            dataPriv.set(elem, 'hasDataAttrs', true);
          }
        }

        return data;
      }

      // Sets multiple values
      if (typeof key === 'object') {
        return this.each(function () {
          dataUser.set(this, key);
        });
      }

      return access(
        this,
        function (value) {
          var data;

          // The calling jQuery object (element matches) is not empty
          // (and therefore has an element appears at this[ 0 ]) and the
          // `value` parameter was not undefined. An empty jQuery object
          // will result in `undefined` for elem = this[ 0 ] which will
          // throw an exception if an attempt to read a data cache is made.
          if (elem && value === undefined) {
            // Attempt to get data from the cache
            // The key will always be camelCased in Data
            data = dataUser.get(elem, key);
            if (data !== undefined) {
              return data;
            }

            // Attempt to "discover" the data in
            // HTML5 custom data-* attrs
            data = dataAttr(elem, key);
            if (data !== undefined) {
              return data;
            }

            // We tried really hard, but the data doesn't exist.
            return;
          }

          // Set the data...
          this.each(function () {
            // We always store the camelCased key
            dataUser.set(this, key, value);
          });
        },
        null,
        value,
        arguments.length > 1,
        null,
        true
      );
    },

    removeData: function (key) {
      return this.each(function () {
        dataUser.remove(this, key);
      });
    }
  });

  jQuery.extend({
    queue: function (elem, type, data) {
      var queue;

      if (elem) {
        type = (type || 'fx') + 'queue';
        queue = dataPriv.get(elem, type);

        // Speed up dequeue by getting out quickly if this is just a lookup
        if (data) {
          if (!queue || Array.isArray(data)) {
            queue = dataPriv.access(elem, type, jQuery.makeArray(data));
          } else {
            queue.push(data);
          }
        }
        return queue || [];
      }
    },

    dequeue: function (elem, type) {
      type = type || 'fx';

      var queue = jQuery.queue(elem, type),
        startLength = queue.length,
        fn = queue.shift(),
        hooks = jQuery._queueHooks(elem, type),
        next = function () {
          jQuery.dequeue(elem, type);
        };

      // If the fx queue is dequeued, always remove the progress sentinel
      if (fn === 'inprogress') {
        fn = queue.shift();
        startLength--;
      }

      if (fn) {
        // Add a progress sentinel to prevent the fx queue from being
        // automatically dequeued
        if (type === 'fx') {
          queue.unshift('inprogress');
        }

        // Clear up the last queue stop function
        delete hooks.stop;
        fn.call(elem, next, hooks);
      }

      if (!startLength && hooks) {
        hooks.empty.fire();
      }
    },

    // Not public - generate a queueHooks object, or return the current one
    _queueHooks: function (elem, type) {
      var key = type + 'queueHooks';
      return (
        dataPriv.get(elem, key) ||
        dataPriv.access(elem, key, {
          empty: jQuery.Callbacks('once memory').add(function () {
            dataPriv.remove(elem, [type + 'queue', key]);
          })
        })
      );
    }
  });

  jQuery.fn.extend({
    queue: function (type, data) {
      var setter = 2;

      if (typeof type !== 'string') {
        data = type;
        type = 'fx';
        setter--;
      }

      if (arguments.length < setter) {
        return jQuery.queue(this[0], type);
      }

      return data === undefined
        ? this
        : this.each(function () {
            var queue = jQuery.queue(this, type, data);

            // Ensure a hooks for this queue
            jQuery._queueHooks(this, type);

            if (type === 'fx' && queue[0] !== 'inprogress') {
              jQuery.dequeue(this, type);
            }
          });
    },
    dequeue: function (type) {
      return this.each(function () {
        jQuery.dequeue(this, type);
      });
    },
    clearQueue: function (type) {
      return this.queue(type || 'fx', []);
    },

    // Get a promise resolved when queues of a certain type
    // are emptied (fx is the type by default)
    promise: function (type, obj) {
      var tmp,
        count = 1,
        defer = jQuery.Deferred(),
        elements = this,
        i = this.length,
        resolve = function () {
          if (!--count) {
            defer.resolveWith(elements, [elements]);
          }
        };

      if (typeof type !== 'string') {
        obj = type;
        type = undefined;
      }
      type = type || 'fx';

      while (i--) {
        tmp = dataPriv.get(elements[i], type + 'queueHooks');
        if (tmp && tmp.empty) {
          count++;
          tmp.empty.add(resolve);
        }
      }
      resolve();
      return defer.promise(obj);
    }
  });
  var pnum = /[+-]?(?:\d*\.|)\d+(?:[eE][+-]?\d+|)/.source;

  var rcssNum = new RegExp('^(?:([+-])=|)(' + pnum + ')([a-z%]*)$', 'i');

  var cssExpand = ['Top', 'Right', 'Bottom', 'Left'];

  var documentElement = document.documentElement;

  var isAttached = function (elem) {
      return jQuery.contains(elem.ownerDocument, elem);
    },
    composed = { composed: true };

  // Support: IE 9 - 11+, Edge 12 - 18+, iOS 10.0 - 10.2 only
  // Check attachment across shadow DOM boundaries when possible (gh-3504)
  // Support: iOS 10.0-10.2 only
  // Early iOS 10 versions support `attachShadow` but not `getRootNode`,
  // leading to errors. We need to check for `getRootNode`.
  if (documentElement.getRootNode) {
    isAttached = function (elem) {
      return jQuery.contains(elem.ownerDocument, elem) || elem.getRootNode(composed) === elem.ownerDocument;
    };
  }
  var isHiddenWithinTree = function (elem, el) {
    // isHiddenWithinTree might be called from jQuery#filter function;
    // in that case, element will be second argument
    elem = el || elem;

    // Inline style trumps all
    return (
      elem.style.display === 'none' ||
      (elem.style.display === '' &&
        // Otherwise, check computed style
        // Support: Firefox <=43 - 45
        // Disconnected elements can have computed display: none, so first confirm that elem is
        // in the document.
        isAttached(elem) &&
        jQuery.css(elem, 'display') === 'none')
    );
  };

  function adjustCSS(elem, prop, valueParts, tween) {
    var adjusted,
      scale,
      maxIterations = 20,
      currentValue = tween
        ? function () {
            return tween.cur();
          }
        : function () {
            return jQuery.css(elem, prop, '');
          },
      initial = currentValue(),
      unit = (valueParts && valueParts[3]) || (jQuery.cssNumber[prop] ? '' : 'px'),
      // Starting value computation is required for potential unit mismatches
      initialInUnit = elem.nodeType && (jQuery.cssNumber[prop] || (unit !== 'px' && +initial)) && rcssNum.exec(jQuery.css(elem, prop));

    if (initialInUnit && initialInUnit[3] !== unit) {
      // Support: Firefox <=54
      // Halve the iteration target value to prevent interference from CSS upper bounds (gh-2144)
      initial = initial / 2;

      // Trust units reported by jQuery.css
      unit = unit || initialInUnit[3];

      // Iteratively approximate from a nonzero starting point
      initialInUnit = +initial || 1;

      while (maxIterations--) {
        // Evaluate and update our best guess (doubling guesses that zero out).
        // Finish if the scale equals or crosses 1 (making the old*new product non-positive).
        jQuery.style(elem, prop, initialInUnit + unit);
        if ((1 - scale) * (1 - (scale = currentValue() / initial || 0.5)) <= 0) {
          maxIterations = 0;
        }
        initialInUnit = initialInUnit / scale;
      }

      initialInUnit = initialInUnit * 2;
      jQuery.style(elem, prop, initialInUnit + unit);

      // Make sure we update the tween properties later on
      valueParts = valueParts || [];
    }

    if (valueParts) {
      initialInUnit = +initialInUnit || +initial || 0;

      // Apply relative offset (+=/-=) if specified
      adjusted = valueParts[1] ? initialInUnit + (valueParts[1] + 1) * valueParts[2] : +valueParts[2];
      if (tween) {
        tween.unit = unit;
        tween.start = initialInUnit;
        tween.end = adjusted;
      }
    }
    return adjusted;
  }

  var defaultDisplayMap = {};

  function getDefaultDisplay(elem) {
    var temp,
      doc = elem.ownerDocument,
      nodeName = elem.nodeName,
      display = defaultDisplayMap[nodeName];

    if (display) {
      return display;
    }

    temp = doc.body.appendChild(doc.createElement(nodeName));
    display = jQuery.css(temp, 'display');

    temp.parentNode.removeChild(temp);

    if (display === 'none') {
      display = 'block';
    }
    defaultDisplayMap[nodeName] = display;

    return display;
  }

  function showHide(elements, show) {
    var display,
      elem,
      values = [],
      index = 0,
      length = elements.length;

    // Determine new display value for elements that need to change
    for (; index < length; index++) {
      elem = elements[index];
      if (!elem.style) {
        continue;
      }

      display = elem.style.display;
      if (show) {
        // Since we force visibility upon cascade-hidden elements, an immediate (and slow)
        // check is required in this first loop unless we have a nonempty display value (either
        // inline or about-to-be-restored)
        if (display === 'none') {
          values[index] = dataPriv.get(elem, 'display') || null;
          if (!values[index]) {
            elem.style.display = '';
          }
        }
        if (elem.style.display === '' && isHiddenWithinTree(elem)) {
          values[index] = getDefaultDisplay(elem);
        }
      } else {
        if (display !== 'none') {
          values[index] = 'none';

          // Remember what we're overwriting
          dataPriv.set(elem, 'display', display);
        }
      }
    }

    // Set the display of the elements in a second loop to avoid constant reflow
    for (index = 0; index < length; index++) {
      if (values[index] != null) {
        elements[index].style.display = values[index];
      }
    }

    return elements;
  }

  jQuery.fn.extend({
    show: function () {
      return showHide(this, true);
    },
    hide: function () {
      return showHide(this);
    },
    toggle: function (state) {
      if (typeof state === 'boolean') {
        return state ? this.show() : this.hide();
      }

      return this.each(function () {
        if (isHiddenWithinTree(this)) {
          jQuery(this).show();
        } else {
          jQuery(this).hide();
        }
      });
    }
  });
  var rcheckableType = /^(?:checkbox|radio)$/i;

  var rtagName = /<([a-z][^\/\0>\x20\t\r\n\f]*)/i;

  var rscriptType = /^$|^module$|\/(?:java|ecma)script/i;

  (function () {
    var fragment = document.createDocumentFragment(),
      div = fragment.appendChild(document.createElement('div')),
      input = document.createElement('input');

    // Support: Android 4.0 - 4.3 only
    // Check state lost if the name is set (#11217)
    // Support: Windows Web Apps (WWA)
    // `name` and `type` must use .setAttribute for WWA (#14901)
    input.setAttribute('type', 'radio');
    input.setAttribute('checked', 'checked');
    input.setAttribute('name', 't');

    div.appendChild(input);

    // Support: Android <=4.1 only
    // Older WebKit doesn't clone checked state correctly in fragments
    support.checkClone = div.cloneNode(true).cloneNode(true).lastChild.checked;

    // Support: IE <=11 only
    // Make sure textarea (and checkbox) defaultValue is properly cloned
    div.innerHTML = '<textarea>x</textarea>';
    support.noCloneChecked = !!div.cloneNode(true).lastChild.defaultValue;

    // Support: IE <=9 only
    // IE <=9 replaces <option> tags with their contents when inserted outside of
    // the select element.
    div.innerHTML = '<option></option>';
    support.option = !!div.lastChild;
  })();

  // We have to close these tags to support XHTML (#13200)
  var wrapMap = {
    // XHTML parsers do not magically insert elements in the
    // same way that tag soup parsers do. So we cannot shorten
    // this by omitting <tbody> or other required elements.
    thead: [1, '<table>', '</table>'],
    col: [2, '<table><colgroup>', '</colgroup></table>'],
    tr: [2, '<table><tbody>', '</tbody></table>'],
    td: [3, '<table><tbody><tr>', '</tr></tbody></table>'],

    _default: [0, '', '']
  };

  wrapMap.tbody = wrapMap.tfoot = wrapMap.colgroup = wrapMap.caption = wrapMap.thead;
  wrapMap.th = wrapMap.td;

  // Support: IE <=9 only
  if (!support.option) {
    wrapMap.optgroup = wrapMap.option = [1, "<select multiple='multiple'>", '</select>'];
  }

  function getAll(context, tag) {
    // Support: IE <=9 - 11 only
    // Use typeof to avoid zero-argument method invocation on host objects (#15151)
    var ret;

    if (typeof context.getElementsByTagName !== 'undefined') {
      ret = context.getElementsByTagName(tag || '*');
    } else if (typeof context.querySelectorAll !== 'undefined') {
      ret = context.querySelectorAll(tag || '*');
    } else {
      ret = [];
    }

    if (tag === undefined || (tag && nodeName(context, tag))) {
      return jQuery.merge([context], ret);
    }

    return ret;
  }

  // Mark scripts as having already been evaluated
  function setGlobalEval(elems, refElements) {
    var i = 0,
      l = elems.length;

    for (; i < l; i++) {
      dataPriv.set(elems[i], 'globalEval', !refElements || dataPriv.get(refElements[i], 'globalEval'));
    }
  }

  var rhtml = /<|&#?\w+;/;

  function buildFragment(elems, context, scripts, selection, ignored) {
    var elem,
      tmp,
      tag,
      wrap,
      attached,
      j,
      fragment = context.createDocumentFragment(),
      nodes = [],
      i = 0,
      l = elems.length;

    for (; i < l; i++) {
      elem = elems[i];

      if (elem || elem === 0) {
        // Add nodes directly
        if (toType(elem) === 'object') {
          // Support: Android <=4.0 only, PhantomJS 1 only
          // push.apply(_, arraylike) throws on ancient WebKit
          jQuery.merge(nodes, elem.nodeType ? [elem] : elem);

          // Convert non-html into a text node
        } else if (!rhtml.test(elem)) {
          nodes.push(context.createTextNode(elem));

          // Convert html into DOM nodes
        } else {
          tmp = tmp || fragment.appendChild(context.createElement('div'));

          // Deserialize a standard representation
          tag = (rtagName.exec(elem) || ['', ''])[1].toLowerCase();
          wrap = wrapMap[tag] || wrapMap._default;
          tmp.innerHTML = wrap[1] + jQuery.htmlPrefilter(elem) + wrap[2];

          // Descend through wrappers to the right content
          j = wrap[0];
          while (j--) {
            tmp = tmp.lastChild;
          }

          // Support: Android <=4.0 only, PhantomJS 1 only
          // push.apply(_, arraylike) throws on ancient WebKit
          jQuery.merge(nodes, tmp.childNodes);

          // Remember the top-level container
          tmp = fragment.firstChild;

          // Ensure the created nodes are orphaned (#12392)
          tmp.textContent = '';
        }
      }
    }

    // Remove wrapper from fragment
    fragment.textContent = '';

    i = 0;
    while ((elem = nodes[i++])) {
      // Skip elements already in the context collection (trac-4087)
      if (selection && jQuery.inArray(elem, selection) > -1) {
        if (ignored) {
          ignored.push(elem);
        }
        continue;
      }

      attached = isAttached(elem);

      // Append to fragment
      tmp = getAll(fragment.appendChild(elem), 'script');

      // Preserve script evaluation history
      if (attached) {
        setGlobalEval(tmp);
      }

      // Capture executables
      if (scripts) {
        j = 0;
        while ((elem = tmp[j++])) {
          if (rscriptType.test(elem.type || '')) {
            scripts.push(elem);
          }
        }
      }
    }

    return fragment;
  }

  var rkeyEvent = /^key/,
    rmouseEvent = /^(?:mouse|pointer|contextmenu|drag|drop)|click/,
    rtypenamespace = /^([^.]*)(?:\.(.+)|)/;

  function returnTrue() {
    return true;
  }

  function returnFalse() {
    return false;
  }

  // Support: IE <=9 - 11+
  // focus() and blur() are asynchronous, except when they are no-op.
  // So expect focus to be synchronous when the element is already active,
  // and blur to be synchronous when the element is not already active.
  // (focus and blur are always synchronous in other supported browsers,
  // this just defines when we can count on it).
  function expectSync(elem, type) {
    return (elem === safeActiveElement()) === (type === 'focus');
  }

  // Support: IE <=9 only
  // Accessing document.activeElement can throw unexpectedly
  // https://bugs.jquery.com/ticket/13393
  function safeActiveElement() {
    try {
      return document.activeElement;
    } catch (err) {}
  }

  function on(elem, types, selector, data, fn, one) {
    var origFn, type;

    // Types can be a map of types/handlers
    if (typeof types === 'object') {
      // ( types-Object, selector, data )
      if (typeof selector !== 'string') {
        // ( types-Object, data )
        data = data || selector;
        selector = undefined;
      }
      for (type in types) {
        on(elem, type, selector, data, types[type], one);
      }
      return elem;
    }

    if (data == null && fn == null) {
      // ( types, fn )
      fn = selector;
      data = selector = undefined;
    } else if (fn == null) {
      if (typeof selector === 'string') {
        // ( types, selector, fn )
        fn = data;
        data = undefined;
      } else {
        // ( types, data, fn )
        fn = data;
        data = selector;
        selector = undefined;
      }
    }
    if (fn === false) {
      fn = returnFalse;
    } else if (!fn) {
      return elem;
    }

    if (one === 1) {
      origFn = fn;
      fn = function (event) {
        // Can use an empty set, since event contains the info
        jQuery().off(event);
        return origFn.apply(this, arguments);
      };

      // Use same guid so caller can remove using origFn
      fn.guid = origFn.guid || (origFn.guid = jQuery.guid++);
    }
    return elem.each(function () {
      jQuery.event.add(this, types, fn, data, selector);
    });
  }

  /*
   * Helper functions for managing events -- not part of the public interface.
   * Props to Dean Edwards' addEvent library for many of the ideas.
   */
  jQuery.event = {
    global: {},

    add: function (elem, types, handler, data, selector) {
      var handleObjIn,
        eventHandle,
        tmp,
        events,
        t,
        handleObj,
        special,
        handlers,
        type,
        namespaces,
        origType,
        elemData = dataPriv.get(elem);

      // Only attach events to objects that accept data
      if (!acceptData(elem)) {
        return;
      }

      // Caller can pass in an object of custom data in lieu of the handler
      if (handler.handler) {
        handleObjIn = handler;
        handler = handleObjIn.handler;
        selector = handleObjIn.selector;
      }

      // Ensure that invalid selectors throw exceptions at attach time
      // Evaluate against documentElement in case elem is a non-element node (e.g., document)
      if (selector) {
        jQuery.find.matchesSelector(documentElement, selector);
      }

      // Make sure that the handler has a unique ID, used to find/remove it later
      if (!handler.guid) {
        handler.guid = jQuery.guid++;
      }

      // Init the element's event structure and main handler, if this is the first
      if (!(events = elemData.events)) {
        events = elemData.events = Object.create(null);
      }
      if (!(eventHandle = elemData.handle)) {
        eventHandle = elemData.handle = function (e) {
          // Discard the second event of a jQuery.event.trigger() and
          // when an event is called after a page has unloaded
          return typeof jQuery !== 'undefined' && jQuery.event.triggered !== e.type ? jQuery.event.dispatch.apply(elem, arguments) : undefined;
        };
      }

      // Handle multiple events separated by a space
      types = (types || '').match(rnothtmlwhite) || [''];
      t = types.length;
      while (t--) {
        tmp = rtypenamespace.exec(types[t]) || [];
        type = origType = tmp[1];
        namespaces = (tmp[2] || '').split('.').sort();

        // There *must* be a type, no attaching namespace-only handlers
        if (!type) {
          continue;
        }

        // If event changes its type, use the special event handlers for the changed type
        special = jQuery.event.special[type] || {};

        // If selector defined, determine special event api type, otherwise given type
        type = (selector ? special.delegateType : special.bindType) || type;

        // Update special based on newly reset type
        special = jQuery.event.special[type] || {};

        // handleObj is passed to all event handlers
        handleObj = jQuery.extend(
          {
            type: type,
            origType: origType,
            data: data,
            handler: handler,
            guid: handler.guid,
            selector: selector,
            needsContext: selector && jQuery.expr.match.needsContext.test(selector),
            namespace: namespaces.join('.')
          },
          handleObjIn
        );

        // Init the event handler queue if we're the first
        if (!(handlers = events[type])) {
          handlers = events[type] = [];
          handlers.delegateCount = 0;

          // Only use addEventListener if the special events handler returns false
          if (!special.setup || special.setup.call(elem, data, namespaces, eventHandle) === false) {
            if (elem.addEventListener) {
              elem.addEventListener(type, eventHandle);
            }
          }
        }

        if (special.add) {
          special.add.call(elem, handleObj);

          if (!handleObj.handler.guid) {
            handleObj.handler.guid = handler.guid;
          }
        }

        // Add to the element's handler list, delegates in front
        if (selector) {
          handlers.splice(handlers.delegateCount++, 0, handleObj);
        } else {
          handlers.push(handleObj);
        }

        // Keep track of which events have ever been used, for event optimization
        jQuery.event.global[type] = true;
      }
    },

    // Detach an event or set of events from an element
    remove: function (elem, types, handler, selector, mappedTypes) {
      var j,
        origCount,
        tmp,
        events,
        t,
        handleObj,
        special,
        handlers,
        type,
        namespaces,
        origType,
        elemData = dataPriv.hasData(elem) && dataPriv.get(elem);

      if (!elemData || !(events = elemData.events)) {
        return;
      }

      // Once for each type.namespace in types; type may be omitted
      types = (types || '').match(rnothtmlwhite) || [''];
      t = types.length;
      while (t--) {
        tmp = rtypenamespace.exec(types[t]) || [];
        type = origType = tmp[1];
        namespaces = (tmp[2] || '').split('.').sort();

        // Unbind all events (on this namespace, if provided) for the element
        if (!type) {
          for (type in events) {
            jQuery.event.remove(elem, type + types[t], handler, selector, true);
          }
          continue;
        }

        special = jQuery.event.special[type] || {};
        type = (selector ? special.delegateType : special.bindType) || type;
        handlers = events[type] || [];
        tmp = tmp[2] && new RegExp('(^|\\.)' + namespaces.join('\\.(?:.*\\.|)') + '(\\.|$)');

        // Remove matching events
        origCount = j = handlers.length;
        while (j--) {
          handleObj = handlers[j];

          if (
            (mappedTypes || origType === handleObj.origType) &&
            (!handler || handler.guid === handleObj.guid) &&
            (!tmp || tmp.test(handleObj.namespace)) &&
            (!selector || selector === handleObj.selector || (selector === '**' && handleObj.selector))
          ) {
            handlers.splice(j, 1);

            if (handleObj.selector) {
              handlers.delegateCount--;
            }
            if (special.remove) {
              special.remove.call(elem, handleObj);
            }
          }
        }

        // Remove generic event handler if we removed something and no more handlers exist
        // (avoids potential for endless recursion during removal of special event handlers)
        if (origCount && !handlers.length) {
          if (!special.teardown || special.teardown.call(elem, namespaces, elemData.handle) === false) {
            jQuery.removeEvent(elem, type, elemData.handle);
          }

          delete events[type];
        }
      }

      // Remove data and the expando if it's no longer used
      if (jQuery.isEmptyObject(events)) {
        dataPriv.remove(elem, 'handle events');
      }
    },

    dispatch: function (nativeEvent) {
      var i,
        j,
        ret,
        matched,
        handleObj,
        handlerQueue,
        args = new Array(arguments.length),
        // Make a writable jQuery.Event from the native event object
        event = jQuery.event.fix(nativeEvent),
        handlers = (dataPriv.get(this, 'events') || Object.create(null))[event.type] || [],
        special = jQuery.event.special[event.type] || {};

      // Use the fix-ed jQuery.Event rather than the (read-only) native event
      args[0] = event;

      for (i = 1; i < arguments.length; i++) {
        args[i] = arguments[i];
      }

      event.delegateTarget = this;

      // Call the preDispatch hook for the mapped type, and let it bail if desired
      if (special.preDispatch && special.preDispatch.call(this, event) === false) {
        return;
      }

      // Determine handlers
      handlerQueue = jQuery.event.handlers.call(this, event, handlers);

      // Run delegates first; they may want to stop propagation beneath us
      i = 0;
      while ((matched = handlerQueue[i++]) && !event.isPropagationStopped()) {
        event.currentTarget = matched.elem;

        j = 0;
        while ((handleObj = matched.handlers[j++]) && !event.isImmediatePropagationStopped()) {
          // If the event is namespaced, then each handler is only invoked if it is
          // specially universal or its namespaces are a superset of the event's.
          if (!event.rnamespace || handleObj.namespace === false || event.rnamespace.test(handleObj.namespace)) {
            event.handleObj = handleObj;
            event.data = handleObj.data;

            ret = ((jQuery.event.special[handleObj.origType] || {}).handle || handleObj.handler).apply(matched.elem, args);

            if (ret !== undefined) {
              if ((event.result = ret) === false) {
                event.preventDefault();
                event.stopPropagation();
              }
            }
          }
        }
      }

      // Call the postDispatch hook for the mapped type
      if (special.postDispatch) {
        special.postDispatch.call(this, event);
      }

      return event.result;
    },

    handlers: function (event, handlers) {
      var i,
        handleObj,
        sel,
        matchedHandlers,
        matchedSelectors,
        handlerQueue = [],
        delegateCount = handlers.delegateCount,
        cur = event.target;

      // Find delegate handlers
      if (
        delegateCount &&
        // Support: IE <=9
        // Black-hole SVG <use> instance trees (trac-13180)
        cur.nodeType &&
        // Support: Firefox <=42
        // Suppress spec-violating clicks indicating a non-primary pointer button (trac-3861)
        // https://www.w3.org/TR/DOM-Level-3-Events/#event-type-click
        // Support: IE 11 only
        // ...but not arrow key "clicks" of radio inputs, which can have `button` -1 (gh-2343)
        !(event.type === 'click' && event.button >= 1)
      ) {
        for (; cur !== this; cur = cur.parentNode || this) {
          // Don't check non-elements (#13208)
          // Don't process clicks on disabled elements (#6911, #8165, #11382, #11764)
          if (cur.nodeType === 1 && !(event.type === 'click' && cur.disabled === true)) {
            matchedHandlers = [];
            matchedSelectors = {};
            for (i = 0; i < delegateCount; i++) {
              handleObj = handlers[i];

              // Don't conflict with Object.prototype properties (#13203)
              sel = handleObj.selector + ' ';

              if (matchedSelectors[sel] === undefined) {
                matchedSelectors[sel] = handleObj.needsContext ? jQuery(sel, this).index(cur) > -1 : jQuery.find(sel, this, null, [cur]).length;
              }
              if (matchedSelectors[sel]) {
                matchedHandlers.push(handleObj);
              }
            }
            if (matchedHandlers.length) {
              handlerQueue.push({ elem: cur, handlers: matchedHandlers });
            }
          }
        }
      }

      // Add the remaining (directly-bound) handlers
      cur = this;
      if (delegateCount < handlers.length) {
        handlerQueue.push({ elem: cur, handlers: handlers.slice(delegateCount) });
      }

      return handlerQueue;
    },

    addProp: function (name, hook) {
      Object.defineProperty(jQuery.Event.prototype, name, {
        enumerable: true,
        configurable: true,

        get: isFunction(hook)
          ? function () {
              if (this.originalEvent) {
                return hook(this.originalEvent);
              }
            }
          : function () {
              if (this.originalEvent) {
                return this.originalEvent[name];
              }
            },

        set: function (value) {
          Object.defineProperty(this, name, {
            enumerable: true,
            configurable: true,
            writable: true,
            value: value
          });
        }
      });
    },

    fix: function (originalEvent) {
      return originalEvent[jQuery.expando] ? originalEvent : new jQuery.Event(originalEvent);
    },

    special: {
      load: {
        // Prevent triggered image.load events from bubbling to window.load
        noBubble: true
      },
      click: {
        // Utilize native event to ensure correct state for checkable inputs
        setup: function (data) {
          // For mutual compressibility with _default, replace `this` access with a local var.
          // `|| data` is dead code meant only to preserve the variable through minification.
          var el = this || data;

          // Claim the first handler
          if (rcheckableType.test(el.type) && el.click && nodeName(el, 'input')) {
            // dataPriv.set( el, "click", ... )
            leverageNative(el, 'click', returnTrue);
          }

          // Return false to allow normal processing in the caller
          return false;
        },
        trigger: function (data) {
          // For mutual compressibility with _default, replace `this` access with a local var.
          // `|| data` is dead code meant only to preserve the variable through minification.
          var el = this || data;

          // Force setup before triggering a click
          if (rcheckableType.test(el.type) && el.click && nodeName(el, 'input')) {
            leverageNative(el, 'click');
          }

          // Return non-false to allow normal event-path propagation
          return true;
        },

        // For cross-browser consistency, suppress native .click() on links
        // Also prevent it if we're currently inside a leveraged native-event stack
        _default: function (event) {
          var target = event.target;
          return (rcheckableType.test(target.type) && target.click && nodeName(target, 'input') && dataPriv.get(target, 'click')) || nodeName(target, 'a');
        }
      },

      beforeunload: {
        postDispatch: function (event) {
          // Support: Firefox 20+
          // Firefox doesn't alert if the returnValue field is not set.
          if (event.result !== undefined && event.originalEvent) {
            event.originalEvent.returnValue = event.result;
          }
        }
      }
    }
  };

  // Ensure the presence of an event listener that handles manually-triggered
  // synthetic events by interrupting progress until reinvoked in response to
  // *native* events that it fires directly, ensuring that state changes have
  // already occurred before other listeners are invoked.
  function leverageNative(el, type, expectSync) {
    // Missing expectSync indicates a trigger call, which must force setup through jQuery.event.add
    if (!expectSync) {
      if (dataPriv.get(el, type) === undefined) {
        jQuery.event.add(el, type, returnTrue);
      }
      return;
    }

    // Register the controller as a special universal handler for all event namespaces
    dataPriv.set(el, type, false);
    jQuery.event.add(el, type, {
      namespace: false,
      handler: function (event) {
        var notAsync,
          result,
          saved = dataPriv.get(this, type);

        if (event.isTrigger & 1 && this[type]) {
          // Interrupt processing of the outer synthetic .trigger()ed event
          // Saved data should be false in such cases, but might be a leftover capture object
          // from an async native handler (gh-4350)
          if (!saved.length) {
            // Store arguments for use when handling the inner native event
            // There will always be at least one argument (an event object), so this array
            // will not be confused with a leftover capture object.
            saved = slice.call(arguments);
            dataPriv.set(this, type, saved);

            // Trigger the native event and capture its result
            // Support: IE <=9 - 11+
            // focus() and blur() are asynchronous
            notAsync = expectSync(this, type);
            this[type]();
            result = dataPriv.get(this, type);
            if (saved !== result || notAsync) {
              dataPriv.set(this, type, false);
            } else {
              result = {};
            }
            if (saved !== result) {
              // Cancel the outer synthetic event
              event.stopImmediatePropagation();
              event.preventDefault();
              return result.value;
            }

            // If this is an inner synthetic event for an event with a bubbling surrogate
            // (focus or blur), assume that the surrogate already propagated from triggering the
            // native event and prevent that from happening again here.
            // This technically gets the ordering wrong w.r.t. to `.trigger()` (in which the
            // bubbling surrogate propagates *after* the non-bubbling base), but that seems
            // less bad than duplication.
          } else if ((jQuery.event.special[type] || {}).delegateType) {
            event.stopPropagation();
          }

          // If this is a native event triggered above, everything is now in order
          // Fire an inner synthetic event with the original arguments
        } else if (saved.length) {
          // ...and capture the result
          dataPriv.set(this, type, {
            value: jQuery.event.trigger(
              // Support: IE <=9 - 11+
              // Extend with the prototype to reset the above stopImmediatePropagation()
              jQuery.extend(saved[0], jQuery.Event.prototype),
              saved.slice(1),
              this
            )
          });

          // Abort handling of the native event
          event.stopImmediatePropagation();
        }
      }
    });
  }

  jQuery.removeEvent = function (elem, type, handle) {
    // This "if" is needed for plain objects
    if (elem.removeEventListener) {
      elem.removeEventListener(type, handle);
    }
  };

  jQuery.Event = function (src, props) {
    // Allow instantiation without the 'new' keyword
    if (!(this instanceof jQuery.Event)) {
      return new jQuery.Event(src, props);
    }

    // Event object
    if (src && src.type) {
      this.originalEvent = src;
      this.type = src.type;

      // Events bubbling up the document may have been marked as prevented
      // by a handler lower down the tree; reflect the correct value.
      this.isDefaultPrevented =
        src.defaultPrevented ||
        (src.defaultPrevented === undefined &&
          // Support: Android <=2.3 only
          src.returnValue === false)
          ? returnTrue
          : returnFalse;

      // Create target properties
      // Support: Safari <=6 - 7 only
      // Target should not be a text node (#504, #13143)
      this.target = src.target && src.target.nodeType === 3 ? src.target.parentNode : src.target;

      this.currentTarget = src.currentTarget;
      this.relatedTarget = src.relatedTarget;

      // Event type
    } else {
      this.type = src;
    }

    // Put explicitly provided properties onto the event object
    if (props) {
      jQuery.extend(this, props);
    }

    // Create a timestamp if incoming event doesn't have one
    this.timeStamp = (src && src.timeStamp) || Date.now();

    // Mark it as fixed
    this[jQuery.expando] = true;
  };

  // jQuery.Event is based on DOM3 Events as specified by the ECMAScript Language Binding
  // https://www.w3.org/TR/2003/WD-DOM-Level-3-Events-20030331/ecma-script-binding.html
  jQuery.Event.prototype = {
    constructor: jQuery.Event,
    isDefaultPrevented: returnFalse,
    isPropagationStopped: returnFalse,
    isImmediatePropagationStopped: returnFalse,
    isSimulated: false,

    preventDefault: function () {
      var e = this.originalEvent;

      this.isDefaultPrevented = returnTrue;

      if (e && !this.isSimulated) {
        e.preventDefault();
      }
    },
    stopPropagation: function () {
      var e = this.originalEvent;

      this.isPropagationStopped = returnTrue;

      if (e && !this.isSimulated) {
        e.stopPropagation();
      }
    },
    stopImmediatePropagation: function () {
      var e = this.originalEvent;

      this.isImmediatePropagationStopped = returnTrue;

      if (e && !this.isSimulated) {
        e.stopImmediatePropagation();
      }

      this.stopPropagation();
    }
  };

  // Includes all common event props including KeyEvent and MouseEvent specific props
  jQuery.each(
    {
      altKey: true,
      bubbles: true,
      cancelable: true,
      changedTouches: true,
      ctrlKey: true,
      detail: true,
      eventPhase: true,
      metaKey: true,
      pageX: true,
      pageY: true,
      shiftKey: true,
      view: true,
      char: true,
      code: true,
      charCode: true,
      key: true,
      keyCode: true,
      button: true,
      buttons: true,
      clientX: true,
      clientY: true,
      offsetX: true,
      offsetY: true,
      pointerId: true,
      pointerType: true,
      screenX: true,
      screenY: true,
      targetTouches: true,
      toElement: true,
      touches: true,

      which: function (event) {
        var button = event.button;

        // Add which for key events
        if (event.which == null && rkeyEvent.test(event.type)) {
          return event.charCode != null ? event.charCode : event.keyCode;
        }

        // Add which for click: 1 === left; 2 === middle; 3 === right
        if (!event.which && button !== undefined && rmouseEvent.test(event.type)) {
          if (button & 1) {
            return 1;
          }

          if (button & 2) {
            return 3;
          }

          if (button & 4) {
            return 2;
          }

          return 0;
        }

        return event.which;
      }
    },
    jQuery.event.addProp
  );

  jQuery.each({ focus: 'focusin', blur: 'focusout' }, function (type, delegateType) {
    jQuery.event.special[type] = {
      // Utilize native event if possible so blur/focus sequence is correct
      setup: function () {
        // Claim the first handler
        // dataPriv.set( this, "focus", ... )
        // dataPriv.set( this, "blur", ... )
        leverageNative(this, type, expectSync);

        // Return false to allow normal processing in the caller
        return false;
      },
      trigger: function () {
        // Force setup before trigger
        leverageNative(this, type);

        // Return non-false to allow normal event-path propagation
        return true;
      },

      delegateType: delegateType
    };
  });

  // Create mouseenter/leave events using mouseover/out and event-time checks
  // so that event delegation works in jQuery.
  // Do the same for pointerenter/pointerleave and pointerover/pointerout
  //
  // Support: Safari 7 only
  // Safari sends mouseenter too often; see:
  // https://bugs.chromium.org/p/chromium/issues/detail?id=470258
  // for the description of the bug (it existed in older Chrome versions as well).
  jQuery.each(
    {
      mouseenter: 'mouseover',
      mouseleave: 'mouseout',
      pointerenter: 'pointerover',
      pointerleave: 'pointerout'
    },
    function (orig, fix) {
      jQuery.event.special[orig] = {
        delegateType: fix,
        bindType: fix,

        handle: function (event) {
          var ret,
            target = this,
            related = event.relatedTarget,
            handleObj = event.handleObj;

          // For mouseenter/leave call the handler if related is outside the target.
          // NB: No relatedTarget if the mouse left/entered the browser window
          if (!related || (related !== target && !jQuery.contains(target, related))) {
            event.type = handleObj.origType;
            ret = handleObj.handler.apply(this, arguments);
            event.type = fix;
          }
          return ret;
        }
      };
    }
  );

  jQuery.fn.extend({
    on: function (types, selector, data, fn) {
      return on(this, types, selector, data, fn);
    },
    one: function (types, selector, data, fn) {
      return on(this, types, selector, data, fn, 1);
    },
    off: function (types, selector, fn) {
      var handleObj, type;
      if (types && types.preventDefault && types.handleObj) {
        // ( event )  dispatched jQuery.Event
        handleObj = types.handleObj;
        jQuery(types.delegateTarget).off(
          handleObj.namespace ? handleObj.origType + '.' + handleObj.namespace : handleObj.origType,
          handleObj.selector,
          handleObj.handler
        );
        return this;
      }
      if (typeof types === 'object') {
        // ( types-object [, selector] )
        for (type in types) {
          this.off(type, selector, types[type]);
        }
        return this;
      }
      if (selector === false || typeof selector === 'function') {
        // ( types [, fn] )
        fn = selector;
        selector = undefined;
      }
      if (fn === false) {
        fn = returnFalse;
      }
      return this.each(function () {
        jQuery.event.remove(this, types, fn, selector);
      });
    }
  });

  var // Support: IE <=10 - 11, Edge 12 - 13 only
    // In IE/Edge using regex groups here causes severe slowdowns.
    // See https://connect.microsoft.com/IE/feedback/details/1736512/
    rnoInnerhtml = /<script|<style|<link/i,
    // checked="checked" or checked
    rchecked = /checked\s*(?:[^=]|=\s*.checked.)/i,
    rcleanScript = /^\s*<!(?:\[CDATA\[|--)|(?:\]\]|--)>\s*$/g;

  // Prefer a tbody over its parent table for containing new rows
  function manipulationTarget(elem, content) {
    if (nodeName(elem, 'table') && nodeName(content.nodeType !== 11 ? content : content.firstChild, 'tr')) {
      return jQuery(elem).children('tbody')[0] || elem;
    }

    return elem;
  }

  // Replace/restore the type attribute of script elements for safe DOM manipulation
  function disableScript(elem) {
    elem.type = (elem.getAttribute('type') !== null) + '/' + elem.type;
    return elem;
  }
  function restoreScript(elem) {
    if ((elem.type || '').slice(0, 5) === 'true/') {
      elem.type = elem.type.slice(5);
    } else {
      elem.removeAttribute('type');
    }

    return elem;
  }

  function cloneCopyEvent(src, dest) {
    var i, l, type, pdataOld, udataOld, udataCur, events;

    if (dest.nodeType !== 1) {
      return;
    }

    // 1. Copy private data: events, handlers, etc.
    if (dataPriv.hasData(src)) {
      pdataOld = dataPriv.get(src);
      events = pdataOld.events;

      if (events) {
        dataPriv.remove(dest, 'handle events');

        for (type in events) {
          for (i = 0, l = events[type].length; i < l; i++) {
            jQuery.event.add(dest, type, events[type][i]);
          }
        }
      }
    }

    // 2. Copy user data
    if (dataUser.hasData(src)) {
      udataOld = dataUser.access(src);
      udataCur = jQuery.extend({}, udataOld);

      dataUser.set(dest, udataCur);
    }
  }

  // Fix IE bugs, see support tests
  function fixInput(src, dest) {
    var nodeName = dest.nodeName.toLowerCase();

    // Fails to persist the checked state of a cloned checkbox or radio button.
    if (nodeName === 'input' && rcheckableType.test(src.type)) {
      dest.checked = src.checked;

      // Fails to return the selected option to the default selected state when cloning options
    } else if (nodeName === 'input' || nodeName === 'textarea') {
      dest.defaultValue = src.defaultValue;
    }
  }

  function domManip(collection, args, callback, ignored) {
    // Flatten any nested arrays
    args = flat(args);

    var fragment,
      first,
      scripts,
      hasScripts,
      node,
      doc,
      i = 0,
      l = collection.length,
      iNoClone = l - 1,
      value = args[0],
      valueIsFunction = isFunction(value);

    // We can't cloneNode fragments that contain checked, in WebKit
    if (valueIsFunction || (l > 1 && typeof value === 'string' && !support.checkClone && rchecked.test(value))) {
      return collection.each(function (index) {
        var self = collection.eq(index);
        if (valueIsFunction) {
          args[0] = value.call(this, index, self.html());
        }
        domManip(self, args, callback, ignored);
      });
    }

    if (l) {
      fragment = buildFragment(args, collection[0].ownerDocument, false, collection, ignored);
      first = fragment.firstChild;

      if (fragment.childNodes.length === 1) {
        fragment = first;
      }

      // Require either new content or an interest in ignored elements to invoke the callback
      if (first || ignored) {
        scripts = jQuery.map(getAll(fragment, 'script'), disableScript);
        hasScripts = scripts.length;

        // Use the original fragment for the last item
        // instead of the first because it can end up
        // being emptied incorrectly in certain situations (#8070).
        for (; i < l; i++) {
          node = fragment;

          if (i !== iNoClone) {
            node = jQuery.clone(node, true, true);

            // Keep references to cloned scripts for later restoration
            if (hasScripts) {
              // Support: Android <=4.0 only, PhantomJS 1 only
              // push.apply(_, arraylike) throws on ancient WebKit
              jQuery.merge(scripts, getAll(node, 'script'));
            }
          }

          callback.call(collection[i], node, i);
        }

        if (hasScripts) {
          doc = scripts[scripts.length - 1].ownerDocument;

          // Reenable scripts
          jQuery.map(scripts, restoreScript);

          // Evaluate executable scripts on first document insertion
          for (i = 0; i < hasScripts; i++) {
            node = scripts[i];
            if (rscriptType.test(node.type || '') && !dataPriv.access(node, 'globalEval') && jQuery.contains(doc, node)) {
              if (node.src && (node.type || '').toLowerCase() !== 'module') {
                // Optional AJAX dependency, but won't run scripts if not present
                if (jQuery._evalUrl && !node.noModule) {
                  jQuery._evalUrl(
                    node.src,
                    {
                      nonce: node.nonce || node.getAttribute('nonce')
                    },
                    doc
                  );
                }
              } else {
                DOMEval(node.textContent.replace(rcleanScript, ''), node, doc);
              }
            }
          }
        }
      }
    }

    return collection;
  }

  function remove(elem, selector, keepData) {
    var node,
      nodes = selector ? jQuery.filter(selector, elem) : elem,
      i = 0;

    for (; (node = nodes[i]) != null; i++) {
      if (!keepData && node.nodeType === 1) {
        jQuery.cleanData(getAll(node));
      }

      if (node.parentNode) {
        if (keepData && isAttached(node)) {
          setGlobalEval(getAll(node, 'script'));
        }
        node.parentNode.removeChild(node);
      }
    }

    return elem;
  }

  jQuery.extend({
    htmlPrefilter: function (html) {
      return html;
    },

    clone: function (elem, dataAndEvents, deepDataAndEvents) {
      var i,
        l,
        srcElements,
        destElements,
        clone = elem.cloneNode(true),
        inPage = isAttached(elem);

      // Fix IE cloning issues
      if (!support.noCloneChecked && (elem.nodeType === 1 || elem.nodeType === 11) && !jQuery.isXMLDoc(elem)) {
        // We eschew Sizzle here for performance reasons: https://jsperf.com/getall-vs-sizzle/2
        destElements = getAll(clone);
        srcElements = getAll(elem);

        for (i = 0, l = srcElements.length; i < l; i++) {
          fixInput(srcElements[i], destElements[i]);
        }
      }

      // Copy the events from the original to the clone
      if (dataAndEvents) {
        if (deepDataAndEvents) {
          srcElements = srcElements || getAll(elem);
          destElements = destElements || getAll(clone);

          for (i = 0, l = srcElements.length; i < l; i++) {
            cloneCopyEvent(srcElements[i], destElements[i]);
          }
        } else {
          cloneCopyEvent(elem, clone);
        }
      }

      // Preserve script evaluation history
      destElements = getAll(clone, 'script');
      if (destElements.length > 0) {
        setGlobalEval(destElements, !inPage && getAll(elem, 'script'));
      }

      // Return the cloned set
      return clone;
    },

    cleanData: function (elems) {
      var data,
        elem,
        type,
        special = jQuery.event.special,
        i = 0;

      for (; (elem = elems[i]) !== undefined; i++) {
        if (acceptData(elem)) {
          if ((data = elem[dataPriv.expando])) {
            if (data.events) {
              for (type in data.events) {
                if (special[type]) {
                  jQuery.event.remove(elem, type);

                  // This is a shortcut to avoid jQuery.event.remove's overhead
                } else {
                  jQuery.removeEvent(elem, type, data.handle);
                }
              }
            }

            // Support: Chrome <=35 - 45+
            // Assign undefined instead of using delete, see Data#remove
            elem[dataPriv.expando] = undefined;
          }
          if (elem[dataUser.expando]) {
            // Support: Chrome <=35 - 45+
            // Assign undefined instead of using delete, see Data#remove
            elem[dataUser.expando] = undefined;
          }
        }
      }
    }
  });

  jQuery.fn.extend({
    detach: function (selector) {
      return remove(this, selector, true);
    },

    remove: function (selector) {
      return remove(this, selector);
    },

    text: function (value) {
      return access(
        this,
        function (value) {
          return value === undefined
            ? jQuery.text(this)
            : this.empty().each(function () {
                if (this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9) {
                  this.textContent = value;
                }
              });
        },
        null,
        value,
        arguments.length
      );
    },

    append: function () {
      return domManip(this, arguments, function (elem) {
        if (this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9) {
          var target = manipulationTarget(this, elem);
          target.appendChild(elem);
        }
      });
    },

    prepend: function () {
      return domManip(this, arguments, function (elem) {
        if (this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9) {
          var target = manipulationTarget(this, elem);
          target.insertBefore(elem, target.firstChild);
        }
      });
    },

    before: function () {
      return domManip(this, arguments, function (elem) {
        if (this.parentNode) {
          this.parentNode.insertBefore(elem, this);
        }
      });
    },

    after: function () {
      return domManip(this, arguments, function (elem) {
        if (this.parentNode) {
          this.parentNode.insertBefore(elem, this.nextSibling);
        }
      });
    },

    empty: function () {
      var elem,
        i = 0;

      for (; (elem = this[i]) != null; i++) {
        if (elem.nodeType === 1) {
          // Prevent memory leaks
          jQuery.cleanData(getAll(elem, false));

          // Remove any remaining nodes
          elem.textContent = '';
        }
      }

      return this;
    },

    clone: function (dataAndEvents, deepDataAndEvents) {
      dataAndEvents = dataAndEvents == null ? false : dataAndEvents;
      deepDataAndEvents = deepDataAndEvents == null ? dataAndEvents : deepDataAndEvents;

      return this.map(function () {
        return jQuery.clone(this, dataAndEvents, deepDataAndEvents);
      });
    },

    html: function (value) {
      return access(
        this,
        function (value) {
          var elem = this[0] || {},
            i = 0,
            l = this.length;

          if (value === undefined && elem.nodeType === 1) {
            return elem.innerHTML;
          }

          // See if we can take a shortcut and just use innerHTML
          if (typeof value === 'string' && !rnoInnerhtml.test(value) && !wrapMap[(rtagName.exec(value) || ['', ''])[1].toLowerCase()]) {
            value = jQuery.htmlPrefilter(value);

            try {
              for (; i < l; i++) {
                elem = this[i] || {};

                // Remove element nodes and prevent memory leaks
                if (elem.nodeType === 1) {
                  jQuery.cleanData(getAll(elem, false));
                  elem.innerHTML = value;
                }
              }

              elem = 0;

              // If using innerHTML throws an exception, use the fallback method
            } catch (e) {}
          }

          if (elem) {
            this.empty().append(value);
          }
        },
        null,
        value,
        arguments.length
      );
    },

    replaceWith: function () {
      var ignored = [];

      // Make the changes, replacing each non-ignored context element with the new content
      return domManip(
        this,
        arguments,
        function (elem) {
          var parent = this.parentNode;

          if (jQuery.inArray(this, ignored) < 0) {
            jQuery.cleanData(getAll(this));
            if (parent) {
              parent.replaceChild(elem, this);
            }
          }

          // Force callback invocation
        },
        ignored
      );
    }
  });

  jQuery.each(
    {
      appendTo: 'append',
      prependTo: 'prepend',
      insertBefore: 'before',
      insertAfter: 'after',
      replaceAll: 'replaceWith'
    },
    function (name, original) {
      jQuery.fn[name] = function (selector) {
        var elems,
          ret = [],
          insert = jQuery(selector),
          last = insert.length - 1,
          i = 0;

        for (; i <= last; i++) {
          elems = i === last ? this : this.clone(true);
          jQuery(insert[i])[original](elems);

          // Support: Android <=4.0 only, PhantomJS 1 only
          // .get() because push.apply(_, arraylike) throws on ancient WebKit
          push.apply(ret, elems.get());
        }

        return this.pushStack(ret);
      };
    }
  );
  var rnumnonpx = new RegExp('^(' + pnum + ')(?!px)[a-z%]+$', 'i');

  var getStyles = function (elem) {
    // Support: IE <=11 only, Firefox <=30 (#15098, #14150)
    // IE throws on elements created in popups
    // FF meanwhile throws on frame elements through "defaultView.getComputedStyle"
    var view = elem.ownerDocument.defaultView;

    if (!view || !view.opener) {
      view = window;
    }

    return view.getComputedStyle(elem);
  };

  var swap = function (elem, options, callback) {
    var ret,
      name,
      old = {};

    // Remember the old values, and insert the new ones
    for (name in options) {
      old[name] = elem.style[name];
      elem.style[name] = options[name];
    }

    ret = callback.call(elem);

    // Revert the old values
    for (name in options) {
      elem.style[name] = old[name];
    }

    return ret;
  };

  var rboxStyle = new RegExp(cssExpand.join('|'), 'i');

  (function () {
    // Executing both pixelPosition & boxSizingReliable tests require only one layout
    // so they're executed at the same time to save the second computation.
    function computeStyleTests() {
      // This is a singleton, we need to execute it only once
      if (!div) {
        return;
      }

      container.style.cssText = 'position:absolute;left:-11111px;width:60px;' + 'margin-top:1px;padding:0;border:0';
      div.style.cssText = 'position:relative;display:block;box-sizing:border-box;overflow:scroll;' + 'margin:auto;border:1px;padding:1px;' + 'width:60%;top:1%';
      documentElement.appendChild(container).appendChild(div);

      var divStyle = window.getComputedStyle(div);
      pixelPositionVal = divStyle.top !== '1%';

      // Support: Android 4.0 - 4.3 only, Firefox <=3 - 44
      reliableMarginLeftVal = roundPixelMeasures(divStyle.marginLeft) === 12;

      // Support: Android 4.0 - 4.3 only, Safari <=9.1 - 10.1, iOS <=7.0 - 9.3
      // Some styles come back with percentage values, even though they shouldn't
      div.style.right = '60%';
      pixelBoxStylesVal = roundPixelMeasures(divStyle.right) === 36;

      // Support: IE 9 - 11 only
      // Detect misreporting of content dimensions for box-sizing:border-box elements
      boxSizingReliableVal = roundPixelMeasures(divStyle.width) === 36;

      // Support: IE 9 only
      // Detect overflow:scroll screwiness (gh-3699)
      // Support: Chrome <=64
      // Don't get tricked when zoom affects offsetWidth (gh-4029)
      div.style.position = 'absolute';
      scrollboxSizeVal = roundPixelMeasures(div.offsetWidth / 3) === 12;

      documentElement.removeChild(container);

      // Nullify the div so it wouldn't be stored in the memory and
      // it will also be a sign that checks already performed
      div = null;
    }

    function roundPixelMeasures(measure) {
      return Math.round(parseFloat(measure));
    }

    var pixelPositionVal,
      boxSizingReliableVal,
      scrollboxSizeVal,
      pixelBoxStylesVal,
      reliableTrDimensionsVal,
      reliableMarginLeftVal,
      container = document.createElement('div'),
      div = document.createElement('div');

    // Finish early in limited (non-browser) environments
    if (!div.style) {
      return;
    }

    // Support: IE <=9 - 11 only
    // Style of cloned element affects source element cloned (#8908)
    div.style.backgroundClip = 'content-box';
    div.cloneNode(true).style.backgroundClip = '';
    support.clearCloneStyle = div.style.backgroundClip === 'content-box';

    jQuery.extend(support, {
      boxSizingReliable: function () {
        computeStyleTests();
        return boxSizingReliableVal;
      },
      pixelBoxStyles: function () {
        computeStyleTests();
        return pixelBoxStylesVal;
      },
      pixelPosition: function () {
        computeStyleTests();
        return pixelPositionVal;
      },
      reliableMarginLeft: function () {
        computeStyleTests();
        return reliableMarginLeftVal;
      },
      scrollboxSize: function () {
        computeStyleTests();
        return scrollboxSizeVal;
      },

      // Support: IE 9 - 11+, Edge 15 - 18+
      // IE/Edge misreport `getComputedStyle` of table rows with width/height
      // set in CSS while `offset*` properties report correct values.
      // Behavior in IE 9 is more subtle than in newer versions & it passes
      // some versions of this test; make sure not to make it pass there!
      reliableTrDimensions: function () {
        var table, tr, trChild, trStyle;
        if (reliableTrDimensionsVal == null) {
          table = document.createElement('table');
          tr = document.createElement('tr');
          trChild = document.createElement('div');

          table.style.cssText = 'position:absolute;left:-11111px';
          tr.style.height = '1px';
          trChild.style.height = '9px';

          documentElement.appendChild(table).appendChild(tr).appendChild(trChild);

          trStyle = window.getComputedStyle(tr);
          reliableTrDimensionsVal = parseInt(trStyle.height) > 3;

          documentElement.removeChild(table);
        }
        return reliableTrDimensionsVal;
      }
    });
  })();

  function curCSS(elem, name, computed) {
    var width,
      minWidth,
      maxWidth,
      ret,
      // Support: Firefox 51+
      // Retrieving style before computed somehow
      // fixes an issue with getting wrong values
      // on detached elements
      style = elem.style;

    computed = computed || getStyles(elem);

    // getPropertyValue is needed for:
    //   .css('filter') (IE 9 only, #12537)
    //   .css('--customProperty) (#3144)
    if (computed) {
      ret = computed.getPropertyValue(name) || computed[name];

      if (ret === '' && !isAttached(elem)) {
        ret = jQuery.style(elem, name);
      }

      // A tribute to the "awesome hack by Dean Edwards"
      // Android Browser returns percentage for some values,
      // but width seems to be reliably pixels.
      // This is against the CSSOM draft spec:
      // https://drafts.csswg.org/cssom/#resolved-values
      if (!support.pixelBoxStyles() && rnumnonpx.test(ret) && rboxStyle.test(name)) {
        // Remember the original values
        width = style.width;
        minWidth = style.minWidth;
        maxWidth = style.maxWidth;

        // Put in the new values to get a computed value out
        style.minWidth = style.maxWidth = style.width = ret;
        ret = computed.width;

        // Revert the changed values
        style.width = width;
        style.minWidth = minWidth;
        style.maxWidth = maxWidth;
      }
    }

    return ret !== undefined
      ? // Support: IE <=9 - 11 only
        // IE returns zIndex value as an integer.
        ret + ''
      : ret;
  }

  function addGetHookIf(conditionFn, hookFn) {
    // Define the hook, we'll check on the first run if it's really needed.
    return {
      get: function () {
        if (conditionFn()) {
          // Hook not needed (or it's not possible to use it due
          // to missing dependency), remove it.
          delete this.get;
          return;
        }

        // Hook needed; redefine it so that the support test is not executed again.
        return (this.get = hookFn).apply(this, arguments);
      }
    };
  }

  var cssPrefixes = ['Webkit', 'Moz', 'ms'],
    emptyStyle = document.createElement('div').style,
    vendorProps = {};

  // Return a vendor-prefixed property or undefined
  function vendorPropName(name) {
    // Check for vendor prefixed names
    var capName = name[0].toUpperCase() + name.slice(1),
      i = cssPrefixes.length;

    while (i--) {
      name = cssPrefixes[i] + capName;
      if (name in emptyStyle) {
        return name;
      }
    }
  }

  // Return a potentially-mapped jQuery.cssProps or vendor prefixed property
  function finalPropName(name) {
    var final = jQuery.cssProps[name] || vendorProps[name];

    if (final) {
      return final;
    }
    if (name in emptyStyle) {
      return name;
    }
    return (vendorProps[name] = vendorPropName(name) || name);
  }

  var // Swappable if display is none or starts with table
    // except "table", "table-cell", or "table-caption"
    // See here for display values: https://developer.mozilla.org/en-US/docs/CSS/display
    rdisplayswap = /^(none|table(?!-c[ea]).+)/,
    rcustomProp = /^--/,
    cssShow = { position: 'absolute', visibility: 'hidden', display: 'block' },
    cssNormalTransform = {
      letterSpacing: '0',
      fontWeight: '400'
    };

  function setPositiveNumber(_elem, value, subtract) {
    // Any relative (+/-) values have already been
    // normalized at this point
    var matches = rcssNum.exec(value);
    return matches
      ? // Guard against undefined "subtract", e.g., when used as in cssHooks
        Math.max(0, matches[2] - (subtract || 0)) + (matches[3] || 'px')
      : value;
  }

  function boxModelAdjustment(elem, dimension, box, isBorderBox, styles, computedVal) {
    var i = dimension === 'width' ? 1 : 0,
      extra = 0,
      delta = 0;

    // Adjustment may not be necessary
    if (box === (isBorderBox ? 'border' : 'content')) {
      return 0;
    }

    for (; i < 4; i += 2) {
      // Both box models exclude margin
      if (box === 'margin') {
        delta += jQuery.css(elem, box + cssExpand[i], true, styles);
      }

      // If we get here with a content-box, we're seeking "padding" or "border" or "margin"
      if (!isBorderBox) {
        // Add padding
        delta += jQuery.css(elem, 'padding' + cssExpand[i], true, styles);

        // For "border" or "margin", add border
        if (box !== 'padding') {
          delta += jQuery.css(elem, 'border' + cssExpand[i] + 'Width', true, styles);

          // But still keep track of it otherwise
        } else {
          extra += jQuery.css(elem, 'border' + cssExpand[i] + 'Width', true, styles);
        }

        // If we get here with a border-box (content + padding + border), we're seeking "content" or
        // "padding" or "margin"
      } else {
        // For "content", subtract padding
        if (box === 'content') {
          delta -= jQuery.css(elem, 'padding' + cssExpand[i], true, styles);
        }

        // For "content" or "padding", subtract border
        if (box !== 'margin') {
          delta -= jQuery.css(elem, 'border' + cssExpand[i] + 'Width', true, styles);
        }
      }
    }

    // Account for positive content-box scroll gutter when requested by providing computedVal
    if (!isBorderBox && computedVal >= 0) {
      // offsetWidth/offsetHeight is a rounded sum of content, padding, scroll gutter, and border
      // Assuming integer scroll gutter, subtract the rest and round down
      delta +=
        Math.max(
          0,
          Math.ceil(
            elem['offset' + dimension[0].toUpperCase() + dimension.slice(1)] - computedVal - delta - extra - 0.5

            // If offsetWidth/offsetHeight is unknown, then we can't determine content-box scroll gutter
            // Use an explicit zero to avoid NaN (gh-3964)
          )
        ) || 0;
    }

    return delta;
  }

  function getWidthOrHeight(elem, dimension, extra) {
    // Start with computed style
    var styles = getStyles(elem),
      // To avoid forcing a reflow, only fetch boxSizing if we need it (gh-4322).
      // Fake content-box until we know it's needed to know the true value.
      boxSizingNeeded = !support.boxSizingReliable() || extra,
      isBorderBox = boxSizingNeeded && jQuery.css(elem, 'boxSizing', false, styles) === 'border-box',
      valueIsBorderBox = isBorderBox,
      val = curCSS(elem, dimension, styles),
      offsetProp = 'offset' + dimension[0].toUpperCase() + dimension.slice(1);

    // Support: Firefox <=54
    // Return a confounding non-pixel value or feign ignorance, as appropriate.
    if (rnumnonpx.test(val)) {
      if (!extra) {
        return val;
      }
      val = 'auto';
    }

    // Support: IE 9 - 11 only
    // Use offsetWidth/offsetHeight for when box sizing is unreliable.
    // In those cases, the computed value can be trusted to be border-box.
    if (
      ((!support.boxSizingReliable() && isBorderBox) ||
        // Support: IE 10 - 11+, Edge 15 - 18+
        // IE/Edge misreport `getComputedStyle` of table rows with width/height
        // set in CSS while `offset*` properties report correct values.
        // Interestingly, in some cases IE 9 doesn't suffer from this issue.
        (!support.reliableTrDimensions() && nodeName(elem, 'tr')) ||
        // Fall back to offsetWidth/offsetHeight when value is "auto"
        // This happens for inline elements with no explicit setting (gh-3571)
        val === 'auto' ||
        // Support: Android <=4.1 - 4.3 only
        // Also use offsetWidth/offsetHeight for misreported inline dimensions (gh-3602)
        (!parseFloat(val) && jQuery.css(elem, 'display', false, styles) === 'inline')) &&
      // Make sure the element is visible & connected
      elem.getClientRects().length
    ) {
      isBorderBox = jQuery.css(elem, 'boxSizing', false, styles) === 'border-box';

      // Where available, offsetWidth/offsetHeight approximate border box dimensions.
      // Where not available (e.g., SVG), assume unreliable box-sizing and interpret the
      // retrieved value as a content box dimension.
      valueIsBorderBox = offsetProp in elem;
      if (valueIsBorderBox) {
        val = elem[offsetProp];
      }
    }

    // Normalize "" and auto
    val = parseFloat(val) || 0;

    // Adjust for the element's box model
    return (
      val +
      boxModelAdjustment(
        elem,
        dimension,
        extra || (isBorderBox ? 'border' : 'content'),
        valueIsBorderBox,
        styles,

        // Provide the current computed size to request scroll gutter calculation (gh-3589)
        val
      ) +
      'px'
    );
  }

  jQuery.extend({
    // Add in style property hooks for overriding the default
    // behavior of getting and setting a style property
    cssHooks: {
      opacity: {
        get: function (elem, computed) {
          if (computed) {
            // We should always get a number back from opacity
            var ret = curCSS(elem, 'opacity');
            return ret === '' ? '1' : ret;
          }
        }
      }
    },

    // Don't automatically add "px" to these possibly-unitless properties
    cssNumber: {
      animationIterationCount: true,
      columnCount: true,
      fillOpacity: true,
      flexGrow: true,
      flexShrink: true,
      fontWeight: true,
      gridArea: true,
      gridColumn: true,
      gridColumnEnd: true,
      gridColumnStart: true,
      gridRow: true,
      gridRowEnd: true,
      gridRowStart: true,
      lineHeight: true,
      opacity: true,
      order: true,
      orphans: true,
      widows: true,
      zIndex: true,
      zoom: true
    },

    // Add in properties whose names you wish to fix before
    // setting or getting the value
    cssProps: {},

    // Get and set the style property on a DOM Node
    style: function (elem, name, value, extra) {
      // Don't set styles on text and comment nodes
      if (!elem || elem.nodeType === 3 || elem.nodeType === 8 || !elem.style) {
        return;
      }

      // Make sure that we're working with the right name
      var ret,
        type,
        hooks,
        origName = camelCase(name),
        isCustomProp = rcustomProp.test(name),
        style = elem.style;

      // Make sure that we're working with the right name. We don't
      // want to query the value if it is a CSS custom property
      // since they are user-defined.
      if (!isCustomProp) {
        name = finalPropName(origName);
      }

      // Gets hook for the prefixed version, then unprefixed version
      hooks = jQuery.cssHooks[name] || jQuery.cssHooks[origName];

      // Check if we're setting a value
      if (value !== undefined) {
        type = typeof value;

        // Convert "+=" or "-=" to relative numbers (#7345)
        if (type === 'string' && (ret = rcssNum.exec(value)) && ret[1]) {
          value = adjustCSS(elem, name, ret);

          // Fixes bug #9237
          type = 'number';
        }

        // Make sure that null and NaN values aren't set (#7116)
        if (value == null || value !== value) {
          return;
        }

        // If a number was passed in, add the unit (except for certain CSS properties)
        // The isCustomProp check can be removed in jQuery 4.0 when we only auto-append
        // "px" to a few hardcoded values.
        if (type === 'number' && !isCustomProp) {
          value += (ret && ret[3]) || (jQuery.cssNumber[origName] ? '' : 'px');
        }

        // background-* props affect original clone's values
        if (!support.clearCloneStyle && value === '' && name.indexOf('background') === 0) {
          style[name] = 'inherit';
        }

        // If a hook was provided, use that value, otherwise just set the specified value
        if (!hooks || !('set' in hooks) || (value = hooks.set(elem, value, extra)) !== undefined) {
          if (isCustomProp) {
            style.setProperty(name, value);
          } else {
            style[name] = value;
          }
        }
      } else {
        // If a hook was provided get the non-computed value from there
        if (hooks && 'get' in hooks && (ret = hooks.get(elem, false, extra)) !== undefined) {
          return ret;
        }

        // Otherwise just get the value from the style object
        return style[name];
      }
    },

    css: function (elem, name, extra, styles) {
      var val,
        num,
        hooks,
        origName = camelCase(name),
        isCustomProp = rcustomProp.test(name);

      // Make sure that we're working with the right name. We don't
      // want to modify the value if it is a CSS custom property
      // since they are user-defined.
      if (!isCustomProp) {
        name = finalPropName(origName);
      }

      // Try prefixed name followed by the unprefixed name
      hooks = jQuery.cssHooks[name] || jQuery.cssHooks[origName];

      // If a hook was provided get the computed value from there
      if (hooks && 'get' in hooks) {
        val = hooks.get(elem, true, extra);
      }

      // Otherwise, if a way to get the computed value exists, use that
      if (val === undefined) {
        val = curCSS(elem, name, styles);
      }

      // Convert "normal" to computed value
      if (val === 'normal' && name in cssNormalTransform) {
        val = cssNormalTransform[name];
      }

      // Make numeric if forced or a qualifier was provided and val looks numeric
      if (extra === '' || extra) {
        num = parseFloat(val);
        return extra === true || isFinite(num) ? num || 0 : val;
      }

      return val;
    }
  });

  jQuery.each(['height', 'width'], function (_i, dimension) {
    jQuery.cssHooks[dimension] = {
      get: function (elem, computed, extra) {
        if (computed) {
          // Certain elements can have dimension info if we invisibly show them
          // but it must have a current display style that would benefit
          return rdisplayswap.test(jQuery.css(elem, 'display')) &&
            // Support: Safari 8+
            // Table columns in Safari have non-zero offsetWidth & zero
            // getBoundingClientRect().width unless display is changed.
            // Support: IE <=11 only
            // Running getBoundingClientRect on a disconnected node
            // in IE throws an error.
            (!elem.getClientRects().length || !elem.getBoundingClientRect().width)
            ? swap(elem, cssShow, function () {
                return getWidthOrHeight(elem, dimension, extra);
              })
            : getWidthOrHeight(elem, dimension, extra);
        }
      },

      set: function (elem, value, extra) {
        var matches,
          styles = getStyles(elem),
          // Only read styles.position if the test has a chance to fail
          // to avoid forcing a reflow.
          scrollboxSizeBuggy = !support.scrollboxSize() && styles.position === 'absolute',
          // To avoid forcing a reflow, only fetch boxSizing if we need it (gh-3991)
          boxSizingNeeded = scrollboxSizeBuggy || extra,
          isBorderBox = boxSizingNeeded && jQuery.css(elem, 'boxSizing', false, styles) === 'border-box',
          subtract = extra ? boxModelAdjustment(elem, dimension, extra, isBorderBox, styles) : 0;

        // Account for unreliable border-box dimensions by comparing offset* to computed and
        // faking a content-box to get border and padding (gh-3699)
        if (isBorderBox && scrollboxSizeBuggy) {
          subtract -= Math.ceil(
            elem['offset' + dimension[0].toUpperCase() + dimension.slice(1)] -
              parseFloat(styles[dimension]) -
              boxModelAdjustment(elem, dimension, 'border', false, styles) -
              0.5
          );
        }

        // Convert to pixels if value adjustment is needed
        if (subtract && (matches = rcssNum.exec(value)) && (matches[3] || 'px') !== 'px') {
          elem.style[dimension] = value;
          value = jQuery.css(elem, dimension);
        }

        return setPositiveNumber(elem, value, subtract);
      }
    };
  });

  jQuery.cssHooks.marginLeft = addGetHookIf(support.reliableMarginLeft, function (elem, computed) {
    if (computed) {
      return (
        (parseFloat(curCSS(elem, 'marginLeft')) ||
          elem.getBoundingClientRect().left -
            swap(elem, { marginLeft: 0 }, function () {
              return elem.getBoundingClientRect().left;
            })) + 'px'
      );
    }
  });

  // These hooks are used by animate to expand properties
  jQuery.each(
    {
      margin: '',
      padding: '',
      border: 'Width'
    },
    function (prefix, suffix) {
      jQuery.cssHooks[prefix + suffix] = {
        expand: function (value) {
          var i = 0,
            expanded = {},
            // Assumes a single number if not a string
            parts = typeof value === 'string' ? value.split(' ') : [value];

          for (; i < 4; i++) {
            expanded[prefix + cssExpand[i] + suffix] = parts[i] || parts[i - 2] || parts[0];
          }

          return expanded;
        }
      };

      if (prefix !== 'margin') {
        jQuery.cssHooks[prefix + suffix].set = setPositiveNumber;
      }
    }
  );

  jQuery.fn.extend({
    css: function (name, value) {
      return access(
        this,
        function (elem, name, value) {
          var styles,
            len,
            map = {},
            i = 0;

          if (Array.isArray(name)) {
            styles = getStyles(elem);
            len = name.length;

            for (; i < len; i++) {
              map[name[i]] = jQuery.css(elem, name[i], false, styles);
            }

            return map;
          }

          return value !== undefined ? jQuery.style(elem, name, value) : jQuery.css(elem, name);
        },
        name,
        value,
        arguments.length > 1
      );
    }
  });

  function Tween(elem, options, prop, end, easing) {
    return new Tween.prototype.init(elem, options, prop, end, easing);
  }
  jQuery.Tween = Tween;

  Tween.prototype = {
    constructor: Tween,
    init: function (elem, options, prop, end, easing, unit) {
      this.elem = elem;
      this.prop = prop;
      this.easing = easing || jQuery.easing._default;
      this.options = options;
      this.start = this.now = this.cur();
      this.end = end;
      this.unit = unit || (jQuery.cssNumber[prop] ? '' : 'px');
    },
    cur: function () {
      var hooks = Tween.propHooks[this.prop];

      return hooks && hooks.get ? hooks.get(this) : Tween.propHooks._default.get(this);
    },
    run: function (percent) {
      var eased,
        hooks = Tween.propHooks[this.prop];

      if (this.options.duration) {
        this.pos = eased = jQuery.easing[this.easing](percent, this.options.duration * percent, 0, 1, this.options.duration);
      } else {
        this.pos = eased = percent;
      }
      this.now = (this.end - this.start) * eased + this.start;

      if (this.options.step) {
        this.options.step.call(this.elem, this.now, this);
      }

      if (hooks && hooks.set) {
        hooks.set(this);
      } else {
        Tween.propHooks._default.set(this);
      }
      return this;
    }
  };

  Tween.prototype.init.prototype = Tween.prototype;

  Tween.propHooks = {
    _default: {
      get: function (tween) {
        var result;

        // Use a property on the element directly when it is not a DOM element,
        // or when there is no matching style property that exists.
        if (tween.elem.nodeType !== 1 || (tween.elem[tween.prop] != null && tween.elem.style[tween.prop] == null)) {
          return tween.elem[tween.prop];
        }

        // Passing an empty string as a 3rd parameter to .css will automatically
        // attempt a parseFloat and fallback to a string if the parse fails.
        // Simple values such as "10px" are parsed to Float;
        // complex values such as "rotate(1rad)" are returned as-is.
        result = jQuery.css(tween.elem, tween.prop, '');

        // Empty strings, null, undefined and "auto" are converted to 0.
        return !result || result === 'auto' ? 0 : result;
      },
      set: function (tween) {
        // Use step hook for back compat.
        // Use cssHook if its there.
        // Use .style if available and use plain properties where available.
        if (jQuery.fx.step[tween.prop]) {
          jQuery.fx.step[tween.prop](tween);
        } else if (tween.elem.nodeType === 1 && (jQuery.cssHooks[tween.prop] || tween.elem.style[finalPropName(tween.prop)] != null)) {
          jQuery.style(tween.elem, tween.prop, tween.now + tween.unit);
        } else {
          tween.elem[tween.prop] = tween.now;
        }
      }
    }
  };

  // Support: IE <=9 only
  // Panic based approach to setting things on disconnected nodes
  Tween.propHooks.scrollTop = Tween.propHooks.scrollLeft = {
    set: function (tween) {
      if (tween.elem.nodeType && tween.elem.parentNode) {
        tween.elem[tween.prop] = tween.now;
      }
    }
  };

  jQuery.easing = {
    linear: function (p) {
      return p;
    },
    swing: function (p) {
      return 0.5 - Math.cos(p * Math.PI) / 2;
    },
    _default: 'swing'
  };

  jQuery.fx = Tween.prototype.init;

  // Back compat <1.8 extension point
  jQuery.fx.step = {};

  var fxNow,
    inProgress,
    rfxtypes = /^(?:toggle|show|hide)$/,
    rrun = /queueHooks$/;

  function schedule() {
    if (inProgress) {
      if (document.hidden === false && window.requestAnimationFrame) {
        window.requestAnimationFrame(schedule);
      } else {
        window.setTimeout(schedule, jQuery.fx.interval);
      }

      jQuery.fx.tick();
    }
  }

  // Animations created synchronously will run synchronously
  function createFxNow() {
    window.setTimeout(function () {
      fxNow = undefined;
    });
    return (fxNow = Date.now());
  }

  // Generate parameters to create a standard animation
  function genFx(type, includeWidth) {
    var which,
      i = 0,
      attrs = { height: type };

    // If we include width, step value is 1 to do all cssExpand values,
    // otherwise step value is 2 to skip over Left and Right
    includeWidth = includeWidth ? 1 : 0;
    for (; i < 4; i += 2 - includeWidth) {
      which = cssExpand[i];
      attrs['margin' + which] = attrs['padding' + which] = type;
    }

    if (includeWidth) {
      attrs.opacity = attrs.width = type;
    }

    return attrs;
  }

  function createTween(value, prop, animation) {
    var tween,
      collection = (Animation.tweeners[prop] || []).concat(Animation.tweeners['*']),
      index = 0,
      length = collection.length;
    for (; index < length; index++) {
      if ((tween = collection[index].call(animation, prop, value))) {
        // We're done with this property
        return tween;
      }
    }
  }

  function defaultPrefilter(elem, props, opts) {
    var prop,
      value,
      toggle,
      hooks,
      oldfire,
      propTween,
      restoreDisplay,
      display,
      isBox = 'width' in props || 'height' in props,
      anim = this,
      orig = {},
      style = elem.style,
      hidden = elem.nodeType && isHiddenWithinTree(elem),
      dataShow = dataPriv.get(elem, 'fxshow');

    // Queue-skipping animations hijack the fx hooks
    if (!opts.queue) {
      hooks = jQuery._queueHooks(elem, 'fx');
      if (hooks.unqueued == null) {
        hooks.unqueued = 0;
        oldfire = hooks.empty.fire;
        hooks.empty.fire = function () {
          if (!hooks.unqueued) {
            oldfire();
          }
        };
      }
      hooks.unqueued++;

      anim.always(function () {
        // Ensure the complete handler is called before this completes
        anim.always(function () {
          hooks.unqueued--;
          if (!jQuery.queue(elem, 'fx').length) {
            hooks.empty.fire();
          }
        });
      });
    }

    // Detect show/hide animations
    for (prop in props) {
      value = props[prop];
      if (rfxtypes.test(value)) {
        delete props[prop];
        toggle = toggle || value === 'toggle';
        if (value === (hidden ? 'hide' : 'show')) {
          // Pretend to be hidden if this is a "show" and
          // there is still data from a stopped show/hide
          if (value === 'show' && dataShow && dataShow[prop] !== undefined) {
            hidden = true;

            // Ignore all other no-op show/hide data
          } else {
            continue;
          }
        }
        orig[prop] = (dataShow && dataShow[prop]) || jQuery.style(elem, prop);
      }
    }

    // Bail out if this is a no-op like .hide().hide()
    propTween = !jQuery.isEmptyObject(props);
    if (!propTween && jQuery.isEmptyObject(orig)) {
      return;
    }

    // Restrict "overflow" and "display" styles during box animations
    if (isBox && elem.nodeType === 1) {
      // Support: IE <=9 - 11, Edge 12 - 15
      // Record all 3 overflow attributes because IE does not infer the shorthand
      // from identically-valued overflowX and overflowY and Edge just mirrors
      // the overflowX value there.
      opts.overflow = [style.overflow, style.overflowX, style.overflowY];

      // Identify a display type, preferring old show/hide data over the CSS cascade
      restoreDisplay = dataShow && dataShow.display;
      if (restoreDisplay == null) {
        restoreDisplay = dataPriv.get(elem, 'display');
      }
      display = jQuery.css(elem, 'display');
      if (display === 'none') {
        if (restoreDisplay) {
          display = restoreDisplay;
        } else {
          // Get nonempty value(s) by temporarily forcing visibility
          showHide([elem], true);
          restoreDisplay = elem.style.display || restoreDisplay;
          display = jQuery.css(elem, 'display');
          showHide([elem]);
        }
      }

      // Animate inline elements as inline-block
      if (display === 'inline' || (display === 'inline-block' && restoreDisplay != null)) {
        if (jQuery.css(elem, 'float') === 'none') {
          // Restore the original display value at the end of pure show/hide animations
          if (!propTween) {
            anim.done(function () {
              style.display = restoreDisplay;
            });
            if (restoreDisplay == null) {
              display = style.display;
              restoreDisplay = display === 'none' ? '' : display;
            }
          }
          style.display = 'inline-block';
        }
      }
    }

    if (opts.overflow) {
      style.overflow = 'hidden';
      anim.always(function () {
        style.overflow = opts.overflow[0];
        style.overflowX = opts.overflow[1];
        style.overflowY = opts.overflow[2];
      });
    }

    // Implement show/hide animations
    propTween = false;
    for (prop in orig) {
      // General show/hide setup for this element animation
      if (!propTween) {
        if (dataShow) {
          if ('hidden' in dataShow) {
            hidden = dataShow.hidden;
          }
        } else {
          dataShow = dataPriv.access(elem, 'fxshow', { display: restoreDisplay });
        }

        // Store hidden/visible for toggle so `.stop().toggle()` "reverses"
        if (toggle) {
          dataShow.hidden = !hidden;
        }

        // Show elements before animating them
        if (hidden) {
          showHide([elem], true);
        }

        /* eslint-disable no-loop-func */

        anim.done(function () {
          /* eslint-enable no-loop-func */

          // The final step of a "hide" animation is actually hiding the element
          if (!hidden) {
            showHide([elem]);
          }
          dataPriv.remove(elem, 'fxshow');
          for (prop in orig) {
            jQuery.style(elem, prop, orig[prop]);
          }
        });
      }

      // Per-property setup
      propTween = createTween(hidden ? dataShow[prop] : 0, prop, anim);
      if (!(prop in dataShow)) {
        dataShow[prop] = propTween.start;
        if (hidden) {
          propTween.end = propTween.start;
          propTween.start = 0;
        }
      }
    }
  }

  function propFilter(props, specialEasing) {
    var index, name, easing, value, hooks;

    // camelCase, specialEasing and expand cssHook pass
    for (index in props) {
      name = camelCase(index);
      easing = specialEasing[name];
      value = props[index];
      if (Array.isArray(value)) {
        easing = value[1];
        value = props[index] = value[0];
      }

      if (index !== name) {
        props[name] = value;
        delete props[index];
      }

      hooks = jQuery.cssHooks[name];
      if (hooks && 'expand' in hooks) {
        value = hooks.expand(value);
        delete props[name];

        // Not quite $.extend, this won't overwrite existing keys.
        // Reusing 'index' because we have the correct "name"
        for (index in value) {
          if (!(index in props)) {
            props[index] = value[index];
            specialEasing[index] = easing;
          }
        }
      } else {
        specialEasing[name] = easing;
      }
    }
  }

  function Animation(elem, properties, options) {
    var result,
      stopped,
      index = 0,
      length = Animation.prefilters.length,
      deferred = jQuery.Deferred().always(function () {
        // Don't match elem in the :animated selector
        delete tick.elem;
      }),
      tick = function () {
        if (stopped) {
          return false;
        }
        var currentTime = fxNow || createFxNow(),
          remaining = Math.max(0, animation.startTime + animation.duration - currentTime),
          // Support: Android 2.3 only
          // Archaic crash bug won't allow us to use `1 - ( 0.5 || 0 )` (#12497)
          temp = remaining / animation.duration || 0,
          percent = 1 - temp,
          index = 0,
          length = animation.tweens.length;

        for (; index < length; index++) {
          animation.tweens[index].run(percent);
        }

        deferred.notifyWith(elem, [animation, percent, remaining]);

        // If there's more to do, yield
        if (percent < 1 && length) {
          return remaining;
        }

        // If this was an empty animation, synthesize a final progress notification
        if (!length) {
          deferred.notifyWith(elem, [animation, 1, 0]);
        }

        // Resolve the animation and report its conclusion
        deferred.resolveWith(elem, [animation]);
        return false;
      },
      animation = deferred.promise({
        elem: elem,
        props: jQuery.extend({}, properties),
        opts: jQuery.extend(
          true,
          {
            specialEasing: {},
            easing: jQuery.easing._default
          },
          options
        ),
        originalProperties: properties,
        originalOptions: options,
        startTime: fxNow || createFxNow(),
        duration: options.duration,
        tweens: [],
        createTween: function (prop, end) {
          var tween = jQuery.Tween(elem, animation.opts, prop, end, animation.opts.specialEasing[prop] || animation.opts.easing);
          animation.tweens.push(tween);
          return tween;
        },
        stop: function (gotoEnd) {
          var index = 0,
            // If we are going to the end, we want to run all the tweens
            // otherwise we skip this part
            length = gotoEnd ? animation.tweens.length : 0;
          if (stopped) {
            return this;
          }
          stopped = true;
          for (; index < length; index++) {
            animation.tweens[index].run(1);
          }

          // Resolve when we played the last frame; otherwise, reject
          if (gotoEnd) {
            deferred.notifyWith(elem, [animation, 1, 0]);
            deferred.resolveWith(elem, [animation, gotoEnd]);
          } else {
            deferred.rejectWith(elem, [animation, gotoEnd]);
          }
          return this;
        }
      }),
      props = animation.props;

    propFilter(props, animation.opts.specialEasing);

    for (; index < length; index++) {
      result = Animation.prefilters[index].call(animation, elem, props, animation.opts);
      if (result) {
        if (isFunction(result.stop)) {
          jQuery._queueHooks(animation.elem, animation.opts.queue).stop = result.stop.bind(result);
        }
        return result;
      }
    }

    jQuery.map(props, createTween, animation);

    if (isFunction(animation.opts.start)) {
      animation.opts.start.call(elem, animation);
    }

    // Attach callbacks from options
    animation.progress(animation.opts.progress).done(animation.opts.done, animation.opts.complete).fail(animation.opts.fail).always(animation.opts.always);

    jQuery.fx.timer(
      jQuery.extend(tick, {
        elem: elem,
        anim: animation,
        queue: animation.opts.queue
      })
    );

    return animation;
  }

  jQuery.Animation = jQuery.extend(Animation, {
    tweeners: {
      '*': [
        function (prop, value) {
          var tween = this.createTween(prop, value);
          adjustCSS(tween.elem, prop, rcssNum.exec(value), tween);
          return tween;
        }
      ]
    },

    tweener: function (props, callback) {
      if (isFunction(props)) {
        callback = props;
        props = ['*'];
      } else {
        props = props.match(rnothtmlwhite);
      }

      var prop,
        index = 0,
        length = props.length;

      for (; index < length; index++) {
        prop = props[index];
        Animation.tweeners[prop] = Animation.tweeners[prop] || [];
        Animation.tweeners[prop].unshift(callback);
      }
    },

    prefilters: [defaultPrefilter],

    prefilter: function (callback, prepend) {
      if (prepend) {
        Animation.prefilters.unshift(callback);
      } else {
        Animation.prefilters.push(callback);
      }
    }
  });

  jQuery.speed = function (speed, easing, fn) {
    var opt =
      speed && typeof speed === 'object'
        ? jQuery.extend({}, speed)
        : {
            complete: fn || (!fn && easing) || (isFunction(speed) && speed),
            duration: speed,
            easing: (fn && easing) || (easing && !isFunction(easing) && easing)
          };

    // Go to the end state if fx are off
    if (jQuery.fx.off) {
      opt.duration = 0;
    } else {
      if (typeof opt.duration !== 'number') {
        if (opt.duration in jQuery.fx.speeds) {
          opt.duration = jQuery.fx.speeds[opt.duration];
        } else {
          opt.duration = jQuery.fx.speeds._default;
        }
      }
    }

    // Normalize opt.queue - true/undefined/null -> "fx"
    if (opt.queue == null || opt.queue === true) {
      opt.queue = 'fx';
    }

    // Queueing
    opt.old = opt.complete;

    opt.complete = function () {
      if (isFunction(opt.old)) {
        opt.old.call(this);
      }

      if (opt.queue) {
        jQuery.dequeue(this, opt.queue);
      }
    };

    return opt;
  };

  jQuery.fn.extend({
    fadeTo: function (speed, to, easing, callback) {
      // Show any hidden elements after setting opacity to 0
      return (
        this.filter(isHiddenWithinTree)
          .css('opacity', 0)
          .show()

          // Animate to the value specified
          .end()
          .animate({ opacity: to }, speed, easing, callback)
      );
    },
    animate: function (prop, speed, easing, callback) {
      var empty = jQuery.isEmptyObject(prop),
        optall = jQuery.speed(speed, easing, callback),
        doAnimation = function () {
          // Operate on a copy of prop so per-property easing won't be lost
          var anim = Animation(this, jQuery.extend({}, prop), optall);

          // Empty animations, or finishing resolves immediately
          if (empty || dataPriv.get(this, 'finish')) {
            anim.stop(true);
          }
        };
      doAnimation.finish = doAnimation;

      return empty || optall.queue === false ? this.each(doAnimation) : this.queue(optall.queue, doAnimation);
    },
    stop: function (type, clearQueue, gotoEnd) {
      var stopQueue = function (hooks) {
        var stop = hooks.stop;
        delete hooks.stop;
        stop(gotoEnd);
      };

      if (typeof type !== 'string') {
        gotoEnd = clearQueue;
        clearQueue = type;
        type = undefined;
      }
      if (clearQueue) {
        this.queue(type || 'fx', []);
      }

      return this.each(function () {
        var dequeue = true,
          index = type != null && type + 'queueHooks',
          timers = jQuery.timers,
          data = dataPriv.get(this);

        if (index) {
          if (data[index] && data[index].stop) {
            stopQueue(data[index]);
          }
        } else {
          for (index in data) {
            if (data[index] && data[index].stop && rrun.test(index)) {
              stopQueue(data[index]);
            }
          }
        }

        for (index = timers.length; index--; ) {
          if (timers[index].elem === this && (type == null || timers[index].queue === type)) {
            timers[index].anim.stop(gotoEnd);
            dequeue = false;
            timers.splice(index, 1);
          }
        }

        // Start the next in the queue if the last step wasn't forced.
        // Timers currently will call their complete callbacks, which
        // will dequeue but only if they were gotoEnd.
        if (dequeue || !gotoEnd) {
          jQuery.dequeue(this, type);
        }
      });
    },
    finish: function (type) {
      if (type !== false) {
        type = type || 'fx';
      }
      return this.each(function () {
        var index,
          data = dataPriv.get(this),
          queue = data[type + 'queue'],
          hooks = data[type + 'queueHooks'],
          timers = jQuery.timers,
          length = queue ? queue.length : 0;

        // Enable finishing flag on private data
        data.finish = true;

        // Empty the queue first
        jQuery.queue(this, type, []);

        if (hooks && hooks.stop) {
          hooks.stop.call(this, true);
        }

        // Look for any active animations, and finish them
        for (index = timers.length; index--; ) {
          if (timers[index].elem === this && timers[index].queue === type) {
            timers[index].anim.stop(true);
            timers.splice(index, 1);
          }
        }

        // Look for any animations in the old queue and finish them
        for (index = 0; index < length; index++) {
          if (queue[index] && queue[index].finish) {
            queue[index].finish.call(this);
          }
        }

        // Turn off finishing flag
        delete data.finish;
      });
    }
  });

  jQuery.each(['toggle', 'show', 'hide'], function (_i, name) {
    var cssFn = jQuery.fn[name];
    jQuery.fn[name] = function (speed, easing, callback) {
      return speed == null || typeof speed === 'boolean' ? cssFn.apply(this, arguments) : this.animate(genFx(name, true), speed, easing, callback);
    };
  });

  // Generate shortcuts for custom animations
  jQuery.each(
    {
      slideDown: genFx('show'),
      slideUp: genFx('hide'),
      slideToggle: genFx('toggle'),
      fadeIn: { opacity: 'show' },
      fadeOut: { opacity: 'hide' },
      fadeToggle: { opacity: 'toggle' }
    },
    function (name, props) {
      jQuery.fn[name] = function (speed, easing, callback) {
        return this.animate(props, speed, easing, callback);
      };
    }
  );

  jQuery.timers = [];
  jQuery.fx.tick = function () {
    var timer,
      i = 0,
      timers = jQuery.timers;

    fxNow = Date.now();

    for (; i < timers.length; i++) {
      timer = timers[i];

      // Run the timer and safely remove it when done (allowing for external removal)
      if (!timer() && timers[i] === timer) {
        timers.splice(i--, 1);
      }
    }

    if (!timers.length) {
      jQuery.fx.stop();
    }
    fxNow = undefined;
  };

  jQuery.fx.timer = function (timer) {
    jQuery.timers.push(timer);
    jQuery.fx.start();
  };

  jQuery.fx.interval = 13;
  jQuery.fx.start = function () {
    if (inProgress) {
      return;
    }

    inProgress = true;
    schedule();
  };

  jQuery.fx.stop = function () {
    inProgress = null;
  };

  jQuery.fx.speeds = {
    slow: 600,
    fast: 200,

    // Default speed
    _default: 400
  };

  // Based off of the plugin by Clint Helfers, with permission.
  // https://web.archive.org/web/20100324014747/http://blindsignals.com/index.php/2009/07/jquery-delay/
  jQuery.fn.delay = function (time, type) {
    time = jQuery.fx ? jQuery.fx.speeds[time] || time : time;
    type = type || 'fx';

    return this.queue(type, function (next, hooks) {
      var timeout = window.setTimeout(next, time);
      hooks.stop = function () {
        window.clearTimeout(timeout);
      };
    });
  };

  (function () {
    var input = document.createElement('input'),
      select = document.createElement('select'),
      opt = select.appendChild(document.createElement('option'));

    input.type = 'checkbox';

    // Support: Android <=4.3 only
    // Default value for a checkbox should be "on"
    support.checkOn = input.value !== '';

    // Support: IE <=11 only
    // Must access selectedIndex to make default options select
    support.optSelected = opt.selected;

    // Support: IE <=11 only
    // An input loses its value after becoming a radio
    input = document.createElement('input');
    input.value = 't';
    input.type = 'radio';
    support.radioValue = input.value === 't';
  })();

  var boolHook,
    attrHandle = jQuery.expr.attrHandle;

  jQuery.fn.extend({
    attr: function (name, value) {
      return access(this, jQuery.attr, name, value, arguments.length > 1);
    },

    removeAttr: function (name) {
      return this.each(function () {
        jQuery.removeAttr(this, name);
      });
    }
  });

  jQuery.extend({
    attr: function (elem, name, value) {
      var ret,
        hooks,
        nType = elem.nodeType;

      // Don't get/set attributes on text, comment and attribute nodes
      if (nType === 3 || nType === 8 || nType === 2) {
        return;
      }

      // Fallback to prop when attributes are not supported
      if (typeof elem.getAttribute === 'undefined') {
        return jQuery.prop(elem, name, value);
      }

      // Attribute hooks are determined by the lowercase version
      // Grab necessary hook if one is defined
      if (nType !== 1 || !jQuery.isXMLDoc(elem)) {
        hooks = jQuery.attrHooks[name.toLowerCase()] || (jQuery.expr.match.bool.test(name) ? boolHook : undefined);
      }

      if (value !== undefined) {
        if (value === null) {
          jQuery.removeAttr(elem, name);
          return;
        }

        if (hooks && 'set' in hooks && (ret = hooks.set(elem, value, name)) !== undefined) {
          return ret;
        }

        elem.setAttribute(name, value + '');
        return value;
      }

      if (hooks && 'get' in hooks && (ret = hooks.get(elem, name)) !== null) {
        return ret;
      }

      ret = jQuery.find.attr(elem, name);

      // Non-existent attributes return null, we normalize to undefined
      return ret == null ? undefined : ret;
    },

    attrHooks: {
      type: {
        set: function (elem, value) {
          if (!support.radioValue && value === 'radio' && nodeName(elem, 'input')) {
            var val = elem.value;
            elem.setAttribute('type', value);
            if (val) {
              elem.value = val;
            }
            return value;
          }
        }
      }
    },

    removeAttr: function (elem, value) {
      var name,
        i = 0,
        // Attribute names can contain non-HTML whitespace characters
        // https://html.spec.whatwg.org/multipage/syntax.html#attributes-2
        attrNames = value && value.match(rnothtmlwhite);

      if (attrNames && elem.nodeType === 1) {
        while ((name = attrNames[i++])) {
          elem.removeAttribute(name);
        }
      }
    }
  });

  // Hooks for boolean attributes
  boolHook = {
    set: function (elem, value, name) {
      if (value === false) {
        // Remove boolean attributes when set to false
        jQuery.removeAttr(elem, name);
      } else {
        elem.setAttribute(name, name);
      }
      return name;
    }
  };

  jQuery.each(jQuery.expr.match.bool.source.match(/\w+/g), function (_i, name) {
    var getter = attrHandle[name] || jQuery.find.attr;

    attrHandle[name] = function (elem, name, isXML) {
      var ret,
        handle,
        lowercaseName = name.toLowerCase();

      if (!isXML) {
        // Avoid an infinite loop by temporarily removing this function from the getter
        handle = attrHandle[lowercaseName];
        attrHandle[lowercaseName] = ret;
        ret = getter(elem, name, isXML) != null ? lowercaseName : null;
        attrHandle[lowercaseName] = handle;
      }
      return ret;
    };
  });

  var rfocusable = /^(?:input|select|textarea|button)$/i,
    rclickable = /^(?:a|area)$/i;

  jQuery.fn.extend({
    prop: function (name, value) {
      return access(this, jQuery.prop, name, value, arguments.length > 1);
    },

    removeProp: function (name) {
      return this.each(function () {
        delete this[jQuery.propFix[name] || name];
      });
    }
  });

  jQuery.extend({
    prop: function (elem, name, value) {
      var ret,
        hooks,
        nType = elem.nodeType;

      // Don't get/set properties on text, comment and attribute nodes
      if (nType === 3 || nType === 8 || nType === 2) {
        return;
      }

      if (nType !== 1 || !jQuery.isXMLDoc(elem)) {
        // Fix name and attach hooks
        name = jQuery.propFix[name] || name;
        hooks = jQuery.propHooks[name];
      }

      if (value !== undefined) {
        if (hooks && 'set' in hooks && (ret = hooks.set(elem, value, name)) !== undefined) {
          return ret;
        }

        return (elem[name] = value);
      }

      if (hooks && 'get' in hooks && (ret = hooks.get(elem, name)) !== null) {
        return ret;
      }

      return elem[name];
    },

    propHooks: {
      tabIndex: {
        get: function (elem) {
          // Support: IE <=9 - 11 only
          // elem.tabIndex doesn't always return the
          // correct value when it hasn't been explicitly set
          // https://web.archive.org/web/20141116233347/http://fluidproject.org/blog/2008/01/09/getting-setting-and-removing-tabindex-values-with-javascript/
          // Use proper attribute retrieval(#12072)
          var tabindex = jQuery.find.attr(elem, 'tabindex');

          if (tabindex) {
            return parseInt(tabindex, 10);
          }

          if (rfocusable.test(elem.nodeName) || (rclickable.test(elem.nodeName) && elem.href)) {
            return 0;
          }

          return -1;
        }
      }
    },

    propFix: {
      for: 'htmlFor',
      class: 'className'
    }
  });

  // Support: IE <=11 only
  // Accessing the selectedIndex property
  // forces the browser to respect setting selected
  // on the option
  // The getter ensures a default option is selected
  // when in an optgroup
  // eslint rule "no-unused-expressions" is disabled for this code
  // since it considers such accessions noop
  if (!support.optSelected) {
    jQuery.propHooks.selected = {
      get: function (elem) {
        /* eslint no-unused-expressions: "off" */

        var parent = elem.parentNode;
        if (parent && parent.parentNode) {
          parent.parentNode.selectedIndex;
        }
        return null;
      },
      set: function (elem) {
        /* eslint no-unused-expressions: "off" */

        var parent = elem.parentNode;
        if (parent) {
          parent.selectedIndex;

          if (parent.parentNode) {
            parent.parentNode.selectedIndex;
          }
        }
      }
    };
  }

  jQuery.each(
    ['tabIndex', 'readOnly', 'maxLength', 'cellSpacing', 'cellPadding', 'rowSpan', 'colSpan', 'useMap', 'frameBorder', 'contentEditable'],
    function () {
      jQuery.propFix[this.toLowerCase()] = this;
    }
  );

  // Strip and collapse whitespace according to HTML spec
  // https://infra.spec.whatwg.org/#strip-and-collapse-ascii-whitespace
  function stripAndCollapse(value) {
    var tokens = value.match(rnothtmlwhite) || [];
    return tokens.join(' ');
  }

  function getClass(elem) {
    return (elem.getAttribute && elem.getAttribute('class')) || '';
  }

  function classesToArray(value) {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      return value.match(rnothtmlwhite) || [];
    }
    return [];
  }

  jQuery.fn.extend({
    addClass: function (value) {
      var classes,
        elem,
        cur,
        curValue,
        clazz,
        j,
        finalValue,
        i = 0;

      if (isFunction(value)) {
        return this.each(function (j) {
          jQuery(this).addClass(value.call(this, j, getClass(this)));
        });
      }

      classes = classesToArray(value);

      if (classes.length) {
        while ((elem = this[i++])) {
          curValue = getClass(elem);
          cur = elem.nodeType === 1 && ' ' + stripAndCollapse(curValue) + ' ';

          if (cur) {
            j = 0;
            while ((clazz = classes[j++])) {
              if (cur.indexOf(' ' + clazz + ' ') < 0) {
                cur += clazz + ' ';
              }
            }

            // Only assign if different to avoid unneeded rendering.
            finalValue = stripAndCollapse(cur);
            if (curValue !== finalValue) {
              elem.setAttribute('class', finalValue);
            }
          }
        }
      }

      return this;
    },

    removeClass: function (value) {
      var classes,
        elem,
        cur,
        curValue,
        clazz,
        j,
        finalValue,
        i = 0;

      if (isFunction(value)) {
        return this.each(function (j) {
          jQuery(this).removeClass(value.call(this, j, getClass(this)));
        });
      }

      if (!arguments.length) {
        return this.attr('class', '');
      }

      classes = classesToArray(value);

      if (classes.length) {
        while ((elem = this[i++])) {
          curValue = getClass(elem);

          // This expression is here for better compressibility (see addClass)
          cur = elem.nodeType === 1 && ' ' + stripAndCollapse(curValue) + ' ';

          if (cur) {
            j = 0;
            while ((clazz = classes[j++])) {
              // Remove *all* instances
              while (cur.indexOf(' ' + clazz + ' ') > -1) {
                cur = cur.replace(' ' + clazz + ' ', ' ');
              }
            }

            // Only assign if different to avoid unneeded rendering.
            finalValue = stripAndCollapse(cur);
            if (curValue !== finalValue) {
              elem.setAttribute('class', finalValue);
            }
          }
        }
      }

      return this;
    },

    toggleClass: function (value, stateVal) {
      var type = typeof value,
        isValidValue = type === 'string' || Array.isArray(value);

      if (typeof stateVal === 'boolean' && isValidValue) {
        return stateVal ? this.addClass(value) : this.removeClass(value);
      }

      if (isFunction(value)) {
        return this.each(function (i) {
          jQuery(this).toggleClass(value.call(this, i, getClass(this), stateVal), stateVal);
        });
      }

      return this.each(function () {
        var className, i, self, classNames;

        if (isValidValue) {
          // Toggle individual class names
          i = 0;
          self = jQuery(this);
          classNames = classesToArray(value);

          while ((className = classNames[i++])) {
            // Check each className given, space separated list
            if (self.hasClass(className)) {
              self.removeClass(className);
            } else {
              self.addClass(className);
            }
          }

          // Toggle whole class name
        } else if (value === undefined || type === 'boolean') {
          className = getClass(this);
          if (className) {
            // Store className if set
            dataPriv.set(this, '__className__', className);
          }

          // If the element has a class name or if we're passed `false`,
          // then remove the whole classname (if there was one, the above saved it).
          // Otherwise bring back whatever was previously saved (if anything),
          // falling back to the empty string if nothing was stored.
          if (this.setAttribute) {
            this.setAttribute('class', className || value === false ? '' : dataPriv.get(this, '__className__') || '');
          }
        }
      });
    },

    hasClass: function (selector) {
      var className,
        elem,
        i = 0;

      className = ' ' + selector + ' ';
      while ((elem = this[i++])) {
        if (elem.nodeType === 1 && (' ' + stripAndCollapse(getClass(elem)) + ' ').indexOf(className) > -1) {
          return true;
        }
      }

      return false;
    }
  });

  var rreturn = /\r/g;

  jQuery.fn.extend({
    val: function (value) {
      var hooks,
        ret,
        valueIsFunction,
        elem = this[0];

      if (!arguments.length) {
        if (elem) {
          hooks = jQuery.valHooks[elem.type] || jQuery.valHooks[elem.nodeName.toLowerCase()];

          if (hooks && 'get' in hooks && (ret = hooks.get(elem, 'value')) !== undefined) {
            return ret;
          }

          ret = elem.value;

          // Handle most common string cases
          if (typeof ret === 'string') {
            return ret.replace(rreturn, '');
          }

          // Handle cases where value is null/undef or number
          return ret == null ? '' : ret;
        }

        return;
      }

      valueIsFunction = isFunction(value);

      return this.each(function (i) {
        var val;

        if (this.nodeType !== 1) {
          return;
        }

        if (valueIsFunction) {
          val = value.call(this, i, jQuery(this).val());
        } else {
          val = value;
        }

        // Treat null/undefined as ""; convert numbers to string
        if (val == null) {
          val = '';
        } else if (typeof val === 'number') {
          val += '';
        } else if (Array.isArray(val)) {
          val = jQuery.map(val, function (value) {
            return value == null ? '' : value + '';
          });
        }

        hooks = jQuery.valHooks[this.type] || jQuery.valHooks[this.nodeName.toLowerCase()];

        // If set returns undefined, fall back to normal setting
        if (!hooks || !('set' in hooks) || hooks.set(this, val, 'value') === undefined) {
          this.value = val;
        }
      });
    }
  });

  jQuery.extend({
    valHooks: {
      option: {
        get: function (elem) {
          var val = jQuery.find.attr(elem, 'value');
          return val != null
            ? val
            : // Support: IE <=10 - 11 only
              // option.text throws exceptions (#14686, #14858)
              // Strip and collapse whitespace
              // https://html.spec.whatwg.org/#strip-and-collapse-whitespace
              stripAndCollapse(jQuery.text(elem));
        }
      },
      select: {
        get: function (elem) {
          var value,
            option,
            i,
            options = elem.options,
            index = elem.selectedIndex,
            one = elem.type === 'select-one',
            values = one ? null : [],
            max = one ? index + 1 : options.length;

          if (index < 0) {
            i = max;
          } else {
            i = one ? index : 0;
          }

          // Loop through all the selected options
          for (; i < max; i++) {
            option = options[i];

            // Support: IE <=9 only
            // IE8-9 doesn't update selected after form reset (#2551)
            if (
              (option.selected || i === index) &&
              // Don't return options that are disabled or in a disabled optgroup
              !option.disabled &&
              (!option.parentNode.disabled || !nodeName(option.parentNode, 'optgroup'))
            ) {
              // Get the specific value for the option
              value = jQuery(option).val();

              // We don't need an array for one selects
              if (one) {
                return value;
              }

              // Multi-Selects return an array
              values.push(value);
            }
          }

          return values;
        },

        set: function (elem, value) {
          var optionSet,
            option,
            options = elem.options,
            values = jQuery.makeArray(value),
            i = options.length;

          while (i--) {
            option = options[i];

            /* eslint-disable no-cond-assign */

            if ((option.selected = jQuery.inArray(jQuery.valHooks.option.get(option), values) > -1)) {
              optionSet = true;
            }

            /* eslint-enable no-cond-assign */
          }

          // Force browsers to behave consistently when non-matching value is set
          if (!optionSet) {
            elem.selectedIndex = -1;
          }
          return values;
        }
      }
    }
  });

  // Radios and checkboxes getter/setter
  jQuery.each(['radio', 'checkbox'], function () {
    jQuery.valHooks[this] = {
      set: function (elem, value) {
        if (Array.isArray(value)) {
          return (elem.checked = jQuery.inArray(jQuery(elem).val(), value) > -1);
        }
      }
    };
    if (!support.checkOn) {
      jQuery.valHooks[this].get = function (elem) {
        return elem.getAttribute('value') === null ? 'on' : elem.value;
      };
    }
  });

  // Return jQuery for attributes-only inclusion

  support.focusin = 'onfocusin' in window;

  var rfocusMorph = /^(?:focusinfocus|focusoutblur)$/,
    stopPropagationCallback = function (e) {
      e.stopPropagation();
    };

  jQuery.extend(jQuery.event, {
    trigger: function (event, data, elem, onlyHandlers) {
      var i,
        cur,
        tmp,
        bubbleType,
        ontype,
        handle,
        special,
        lastElement,
        eventPath = [elem || document],
        type = hasOwn.call(event, 'type') ? event.type : event,
        namespaces = hasOwn.call(event, 'namespace') ? event.namespace.split('.') : [];

      cur = lastElement = tmp = elem = elem || document;

      // Don't do events on text and comment nodes
      if (elem.nodeType === 3 || elem.nodeType === 8) {
        return;
      }

      // focus/blur morphs to focusin/out; ensure we're not firing them right now
      if (rfocusMorph.test(type + jQuery.event.triggered)) {
        return;
      }

      if (type.indexOf('.') > -1) {
        // Namespaced trigger; create a regexp to match event type in handle()
        namespaces = type.split('.');
        type = namespaces.shift();
        namespaces.sort();
      }
      ontype = type.indexOf(':') < 0 && 'on' + type;

      // Caller can pass in a jQuery.Event object, Object, or just an event type string
      event = event[jQuery.expando] ? event : new jQuery.Event(type, typeof event === 'object' && event);

      // Trigger bitmask: & 1 for native handlers; & 2 for jQuery (always true)
      event.isTrigger = onlyHandlers ? 2 : 3;
      event.namespace = namespaces.join('.');
      event.rnamespace = event.namespace ? new RegExp('(^|\\.)' + namespaces.join('\\.(?:.*\\.|)') + '(\\.|$)') : null;

      // Clean up the event in case it is being reused
      event.result = undefined;
      if (!event.target) {
        event.target = elem;
      }

      // Clone any incoming data and prepend the event, creating the handler arg list
      data = data == null ? [event] : jQuery.makeArray(data, [event]);

      // Allow special events to draw outside the lines
      special = jQuery.event.special[type] || {};
      if (!onlyHandlers && special.trigger && special.trigger.apply(elem, data) === false) {
        return;
      }

      // Determine event propagation path in advance, per W3C events spec (#9951)
      // Bubble up to document, then to window; watch for a global ownerDocument var (#9724)
      if (!onlyHandlers && !special.noBubble && !isWindow(elem)) {
        bubbleType = special.delegateType || type;
        if (!rfocusMorph.test(bubbleType + type)) {
          cur = cur.parentNode;
        }
        for (; cur; cur = cur.parentNode) {
          eventPath.push(cur);
          tmp = cur;
        }

        // Only add window if we got to document (e.g., not plain obj or detached DOM)
        if (tmp === (elem.ownerDocument || document)) {
          eventPath.push(tmp.defaultView || tmp.parentWindow || window);
        }
      }

      // Fire handlers on the event path
      i = 0;
      while ((cur = eventPath[i++]) && !event.isPropagationStopped()) {
        lastElement = cur;
        event.type = i > 1 ? bubbleType : special.bindType || type;

        // jQuery handler
        handle = (dataPriv.get(cur, 'events') || Object.create(null))[event.type] && dataPriv.get(cur, 'handle');
        if (handle) {
          handle.apply(cur, data);
        }

        // Native handler
        handle = ontype && cur[ontype];
        if (handle && handle.apply && acceptData(cur)) {
          event.result = handle.apply(cur, data);
          if (event.result === false) {
            event.preventDefault();
          }
        }
      }
      event.type = type;

      // If nobody prevented the default action, do it now
      if (!onlyHandlers && !event.isDefaultPrevented()) {
        if ((!special._default || special._default.apply(eventPath.pop(), data) === false) && acceptData(elem)) {
          // Call a native DOM method on the target with the same name as the event.
          // Don't do default actions on window, that's where global variables be (#6170)
          if (ontype && isFunction(elem[type]) && !isWindow(elem)) {
            // Don't re-trigger an onFOO event when we call its FOO() method
            tmp = elem[ontype];

            if (tmp) {
              elem[ontype] = null;
            }

            // Prevent re-triggering of the same event, since we already bubbled it above
            jQuery.event.triggered = type;

            if (event.isPropagationStopped()) {
              lastElement.addEventListener(type, stopPropagationCallback);
            }

            elem[type]();

            if (event.isPropagationStopped()) {
              lastElement.removeEventListener(type, stopPropagationCallback);
            }

            jQuery.event.triggered = undefined;

            if (tmp) {
              elem[ontype] = tmp;
            }
          }
        }
      }

      return event.result;
    },

    // Piggyback on a donor event to simulate a different one
    // Used only for `focus(in | out)` events
    simulate: function (type, elem, event) {
      var e = jQuery.extend(new jQuery.Event(), event, {
        type: type,
        isSimulated: true
      });

      jQuery.event.trigger(e, null, elem);
    }
  });

  jQuery.fn.extend({
    trigger: function (type, data) {
      return this.each(function () {
        jQuery.event.trigger(type, data, this);
      });
    },
    triggerHandler: function (type, data) {
      var elem = this[0];
      if (elem) {
        return jQuery.event.trigger(type, data, elem, true);
      }
    }
  });

  // Support: Firefox <=44
  // Firefox doesn't have focus(in | out) events
  // Related ticket - https://bugzilla.mozilla.org/show_bug.cgi?id=687787
  //
  // Support: Chrome <=48 - 49, Safari <=9.0 - 9.1
  // focus(in | out) events fire after focus & blur events,
  // which is spec violation - http://www.w3.org/TR/DOM-Level-3-Events/#events-focusevent-event-order
  // Related ticket - https://bugs.chromium.org/p/chromium/issues/detail?id=449857
  if (!support.focusin) {
    jQuery.each({ focus: 'focusin', blur: 'focusout' }, function (orig, fix) {
      // Attach a single capturing handler on the document while someone wants focusin/focusout
      var handler = function (event) {
        jQuery.event.simulate(fix, event.target, jQuery.event.fix(event));
      };

      jQuery.event.special[fix] = {
        setup: function () {
          // Handle: regular nodes (via `this.ownerDocument`), window
          // (via `this.document`) & document (via `this`).
          var doc = this.ownerDocument || this.document || this,
            attaches = dataPriv.access(doc, fix);

          if (!attaches) {
            doc.addEventListener(orig, handler, true);
          }
          dataPriv.access(doc, fix, (attaches || 0) + 1);
        },
        teardown: function () {
          var doc = this.ownerDocument || this.document || this,
            attaches = dataPriv.access(doc, fix) - 1;

          if (!attaches) {
            doc.removeEventListener(orig, handler, true);
            dataPriv.remove(doc, fix);
          } else {
            dataPriv.access(doc, fix, attaches);
          }
        }
      };
    });
  }
  var location = window.location;

  var nonce = { guid: Date.now() };

  var rquery = /\?/;

  // Cross-browser xml parsing
  jQuery.parseXML = function (data) {
    var xml;
    if (!data || typeof data !== 'string') {
      return null;
    }

    // Support: IE 9 - 11 only
    // IE throws on parseFromString with invalid input.
    try {
      xml = new window.DOMParser().parseFromString(data, 'text/xml');
    } catch (e) {
      xml = undefined;
    }

    if (!xml || xml.getElementsByTagName('parsererror').length) {
      jQuery.error('Invalid XML: ' + data);
    }
    return xml;
  };

  var rbracket = /\[\]$/,
    rCRLF = /\r?\n/g,
    rsubmitterTypes = /^(?:submit|button|image|reset|file)$/i,
    rsubmittable = /^(?:input|select|textarea|keygen)/i;

  function buildParams(prefix, obj, traditional, add) {
    var name;

    if (Array.isArray(obj)) {
      // Serialize array item.
      jQuery.each(obj, function (i, v) {
        if (traditional || rbracket.test(prefix)) {
          // Treat each array item as a scalar.
          add(prefix, v);
        } else {
          // Item is non-scalar (array or object), encode its numeric index.
          buildParams(prefix + '[' + (typeof v === 'object' && v != null ? i : '') + ']', v, traditional, add);
        }
      });
    } else if (!traditional && toType(obj) === 'object') {
      // Serialize object item.
      for (name in obj) {
        buildParams(prefix + '[' + name + ']', obj[name], traditional, add);
      }
    } else {
      // Serialize scalar item.
      add(prefix, obj);
    }
  }

  // Serialize an array of form elements or a set of
  // key/values into a query string
  jQuery.param = function (a, traditional) {
    var prefix,
      s = [],
      add = function (key, valueOrFunction) {
        // If value is a function, invoke it and use its return value
        var value = isFunction(valueOrFunction) ? valueOrFunction() : valueOrFunction;

        s[s.length] = encodeURIComponent(key) + '=' + encodeURIComponent(value == null ? '' : value);
      };

    if (a == null) {
      return '';
    }

    // If an array was passed in, assume that it is an array of form elements.
    if (Array.isArray(a) || (a.jquery && !jQuery.isPlainObject(a))) {
      // Serialize the form elements
      jQuery.each(a, function () {
        add(this.name, this.value);
      });
    } else {
      // If traditional, encode the "old" way (the way 1.3.2 or older
      // did it), otherwise encode params recursively.
      for (prefix in a) {
        buildParams(prefix, a[prefix], traditional, add);
      }
    }

    // Return the resulting serialization
    return s.join('&');
  };

  jQuery.fn.extend({
    serialize: function () {
      return jQuery.param(this.serializeArray());
    },
    serializeArray: function () {
      return this.map(function () {
        // Can add propHook for "elements" to filter or add form elements
        var elements = jQuery.prop(this, 'elements');
        return elements ? jQuery.makeArray(elements) : this;
      })
        .filter(function () {
          var type = this.type;

          // Use .is( ":disabled" ) so that fieldset[disabled] works
          return (
            this.name &&
            !jQuery(this).is(':disabled') &&
            rsubmittable.test(this.nodeName) &&
            !rsubmitterTypes.test(type) &&
            (this.checked || !rcheckableType.test(type))
          );
        })
        .map(function (_i, elem) {
          var val = jQuery(this).val();

          if (val == null) {
            return null;
          }

          if (Array.isArray(val)) {
            return jQuery.map(val, function (val) {
              return { name: elem.name, value: val.replace(rCRLF, '\r\n') };
            });
          }

          return { name: elem.name, value: val.replace(rCRLF, '\r\n') };
        })
        .get();
    }
  });

  var r20 = /%20/g,
    rhash = /#.*$/,
    rantiCache = /([?&])_=[^&]*/,
    rheaders = /^(.*?):[ \t]*([^\r\n]*)$/gm,
    // #7653, #8125, #8152: local protocol detection
    rlocalProtocol = /^(?:about|app|app-storage|.+-extension|file|res|widget):$/,
    rnoContent = /^(?:GET|HEAD)$/,
    rprotocol = /^\/\//,
    /* Prefilters
     * 1) They are useful to introduce custom dataTypes (see ajax/jsonp.js for an example)
     * 2) These are called:
     *    - BEFORE asking for a transport
     *    - AFTER param serialization (s.data is a string if s.processData is true)
     * 3) key is the dataType
     * 4) the catchall symbol "*" can be used
     * 5) execution will start with transport dataType and THEN continue down to "*" if needed
     */
    prefilters = {},
    /* Transports bindings
     * 1) key is the dataType
     * 2) the catchall symbol "*" can be used
     * 3) selection will start with transport dataType and THEN go to "*" if needed
     */
    transports = {},
    // Avoid comment-prolog char sequence (#10098); must appease lint and evade compression
    allTypes = '*/'.concat('*'),
    // Anchor tag for parsing the document origin
    originAnchor = document.createElement('a');
  originAnchor.href = location.href;

  // Base "constructor" for jQuery.ajaxPrefilter and jQuery.ajaxTransport
  function addToPrefiltersOrTransports(structure) {
    // dataTypeExpression is optional and defaults to "*"
    return function (dataTypeExpression, func) {
      if (typeof dataTypeExpression !== 'string') {
        func = dataTypeExpression;
        dataTypeExpression = '*';
      }

      var dataType,
        i = 0,
        dataTypes = dataTypeExpression.toLowerCase().match(rnothtmlwhite) || [];

      if (isFunction(func)) {
        // For each dataType in the dataTypeExpression
        while ((dataType = dataTypes[i++])) {
          // Prepend if requested
          if (dataType[0] === '+') {
            dataType = dataType.slice(1) || '*';
            (structure[dataType] = structure[dataType] || []).unshift(func);

            // Otherwise append
          } else {
            (structure[dataType] = structure[dataType] || []).push(func);
          }
        }
      }
    };
  }

  // Base inspection function for prefilters and transports
  function inspectPrefiltersOrTransports(structure, options, originalOptions, jqXHR) {
    var inspected = {},
      seekingTransport = structure === transports;

    function inspect(dataType) {
      var selected;
      inspected[dataType] = true;
      jQuery.each(structure[dataType] || [], function (_, prefilterOrFactory) {
        var dataTypeOrTransport = prefilterOrFactory(options, originalOptions, jqXHR);
        if (typeof dataTypeOrTransport === 'string' && !seekingTransport && !inspected[dataTypeOrTransport]) {
          options.dataTypes.unshift(dataTypeOrTransport);
          inspect(dataTypeOrTransport);
          return false;
        } else if (seekingTransport) {
          return !(selected = dataTypeOrTransport);
        }
      });
      return selected;
    }

    return inspect(options.dataTypes[0]) || (!inspected['*'] && inspect('*'));
  }

  // A special extend for ajax options
  // that takes "flat" options (not to be deep extended)
  // Fixes #9887
  function ajaxExtend(target, src) {
    var key,
      deep,
      flatOptions = jQuery.ajaxSettings.flatOptions || {};

    for (key in src) {
      if (src[key] !== undefined) {
        (flatOptions[key] ? target : deep || (deep = {}))[key] = src[key];
      }
    }
    if (deep) {
      jQuery.extend(true, target, deep);
    }

    return target;
  }

  /* Handles responses to an ajax request:
   * - finds the right dataType (mediates between content-type and expected dataType)
   * - returns the corresponding response
   */
  function ajaxHandleResponses(s, jqXHR, responses) {
    var ct,
      type,
      finalDataType,
      firstDataType,
      contents = s.contents,
      dataTypes = s.dataTypes;

    // Remove auto dataType and get content-type in the process
    while (dataTypes[0] === '*') {
      dataTypes.shift();
      if (ct === undefined) {
        ct = s.mimeType || jqXHR.getResponseHeader('Content-Type');
      }
    }

    // Check if we're dealing with a known content-type
    if (ct) {
      for (type in contents) {
        if (contents[type] && contents[type].test(ct)) {
          dataTypes.unshift(type);
          break;
        }
      }
    }

    // Check to see if we have a response for the expected dataType
    if (dataTypes[0] in responses) {
      finalDataType = dataTypes[0];
    } else {
      // Try convertible dataTypes
      for (type in responses) {
        if (!dataTypes[0] || s.converters[type + ' ' + dataTypes[0]]) {
          finalDataType = type;
          break;
        }
        if (!firstDataType) {
          firstDataType = type;
        }
      }

      // Or just use first one
      finalDataType = finalDataType || firstDataType;
    }

    // If we found a dataType
    // We add the dataType to the list if needed
    // and return the corresponding response
    if (finalDataType) {
      if (finalDataType !== dataTypes[0]) {
        dataTypes.unshift(finalDataType);
      }
      return responses[finalDataType];
    }
  }

  /* Chain conversions given the request and the original response
   * Also sets the responseXXX fields on the jqXHR instance
   */
  function ajaxConvert(s, response, jqXHR, isSuccess) {
    var conv2,
      current,
      conv,
      tmp,
      prev,
      converters = {},
      // Work with a copy of dataTypes in case we need to modify it for conversion
      dataTypes = s.dataTypes.slice();

    // Create converters map with lowercased keys
    if (dataTypes[1]) {
      for (conv in s.converters) {
        converters[conv.toLowerCase()] = s.converters[conv];
      }
    }

    current = dataTypes.shift();

    // Convert to each sequential dataType
    while (current) {
      if (s.responseFields[current]) {
        jqXHR[s.responseFields[current]] = response;
      }

      // Apply the dataFilter if provided
      if (!prev && isSuccess && s.dataFilter) {
        response = s.dataFilter(response, s.dataType);
      }

      prev = current;
      current = dataTypes.shift();

      if (current) {
        // There's only work to do if current dataType is non-auto
        if (current === '*') {
          current = prev;

          // Convert response if prev dataType is non-auto and differs from current
        } else if (prev !== '*' && prev !== current) {
          // Seek a direct converter
          conv = converters[prev + ' ' + current] || converters['* ' + current];

          // If none found, seek a pair
          if (!conv) {
            for (conv2 in converters) {
              // If conv2 outputs current
              tmp = conv2.split(' ');
              if (tmp[1] === current) {
                // If prev can be converted to accepted input
                conv = converters[prev + ' ' + tmp[0]] || converters['* ' + tmp[0]];
                if (conv) {
                  // Condense equivalence converters
                  if (conv === true) {
                    conv = converters[conv2];

                    // Otherwise, insert the intermediate dataType
                  } else if (converters[conv2] !== true) {
                    current = tmp[0];
                    dataTypes.unshift(tmp[1]);
                  }
                  break;
                }
              }
            }
          }

          // Apply converter (if not an equivalence)
          if (conv !== true) {
            // Unless errors are allowed to bubble, catch and return them
            if (conv && s.throws) {
              response = conv(response);
            } else {
              try {
                response = conv(response);
              } catch (e) {
                return {
                  state: 'parsererror',
                  error: conv ? e : 'No conversion from ' + prev + ' to ' + current
                };
              }
            }
          }
        }
      }
    }

    return { state: 'success', data: response };
  }

  jQuery.extend({
    // Counter for holding the number of active queries
    active: 0,

    // Last-Modified header cache for next request
    lastModified: {},
    etag: {},

    ajaxSettings: {
      url: location.href,
      type: 'GET',
      isLocal: rlocalProtocol.test(location.protocol),
      global: true,
      processData: true,
      async: true,
      contentType: 'application/x-www-form-urlencoded; charset=UTF-8',

      /*
			timeout: 0,
			data: null,
			dataType: null,
			username: null,
			password: null,
			cache: null,
			throws: false,
			traditional: false,
			headers: {},
			*/

      accepts: {
        '*': allTypes,
        text: 'text/plain',
        html: 'text/html',
        xml: 'application/xml, text/xml',
        json: 'application/json, text/javascript'
      },

      contents: {
        xml: /\bxml\b/,
        html: /\bhtml/,
        json: /\bjson\b/
      },

      responseFields: {
        xml: 'responseXML',
        text: 'responseText',
        json: 'responseJSON'
      },

      // Data converters
      // Keys separate source (or catchall "*") and destination types with a single space
      converters: {
        // Convert anything to text
        '* text': String,

        // Text to html (true = no transformation)
        'text html': true,

        // Evaluate text as a json expression
        'text json': JSON.parse,

        // Parse text as xml
        'text xml': jQuery.parseXML
      },

      // For options that shouldn't be deep extended:
      // you can add your own custom options here if
      // and when you create one that shouldn't be
      // deep extended (see ajaxExtend)
      flatOptions: {
        url: true,
        context: true
      }
    },

    // Creates a full fledged settings object into target
    // with both ajaxSettings and settings fields.
    // If target is omitted, writes into ajaxSettings.
    ajaxSetup: function (target, settings) {
      return settings
        ? // Building a settings object
          ajaxExtend(ajaxExtend(target, jQuery.ajaxSettings), settings)
        : // Extending ajaxSettings
          ajaxExtend(jQuery.ajaxSettings, target);
    },

    ajaxPrefilter: addToPrefiltersOrTransports(prefilters),
    ajaxTransport: addToPrefiltersOrTransports(transports),

    // Main method
    ajax: function (url, options) {
      // If url is an object, simulate pre-1.5 signature
      if (typeof url === 'object') {
        options = url;
        url = undefined;
      }

      // Force options to be an object
      options = options || {};

      var transport,
        // URL without anti-cache param
        cacheURL,
        // Response headers
        responseHeadersString,
        responseHeaders,
        // timeout handle
        timeoutTimer,
        // Url cleanup var
        urlAnchor,
        // Request state (becomes false upon send and true upon completion)
        completed,
        // To know if global events are to be dispatched
        fireGlobals,
        // Loop variable
        i,
        // uncached part of the url
        uncached,
        // Create the final options object
        s = jQuery.ajaxSetup({}, options),
        // Callbacks context
        callbackContext = s.context || s,
        // Context for global events is callbackContext if it is a DOM node or jQuery collection
        globalEventContext = s.context && (callbackContext.nodeType || callbackContext.jquery) ? jQuery(callbackContext) : jQuery.event,
        // Deferreds
        deferred = jQuery.Deferred(),
        completeDeferred = jQuery.Callbacks('once memory'),
        // Status-dependent callbacks
        statusCode = s.statusCode || {},
        // Headers (they are sent all at once)
        requestHeaders = {},
        requestHeadersNames = {},
        // Default abort message
        strAbort = 'canceled',
        // Fake xhr
        jqXHR = {
          readyState: 0,

          // Builds headers hashtable if needed
          getResponseHeader: function (key) {
            var match;
            if (completed) {
              if (!responseHeaders) {
                responseHeaders = {};
                while ((match = rheaders.exec(responseHeadersString))) {
                  responseHeaders[match[1].toLowerCase() + ' '] = (responseHeaders[match[1].toLowerCase() + ' '] || []).concat(match[2]);
                }
              }
              match = responseHeaders[key.toLowerCase() + ' '];
            }
            return match == null ? null : match.join(', ');
          },

          // Raw string
          getAllResponseHeaders: function () {
            return completed ? responseHeadersString : null;
          },

          // Caches the header
          setRequestHeader: function (name, value) {
            if (completed == null) {
              name = requestHeadersNames[name.toLowerCase()] = requestHeadersNames[name.toLowerCase()] || name;
              requestHeaders[name] = value;
            }
            return this;
          },

          // Overrides response content-type header
          overrideMimeType: function (type) {
            if (completed == null) {
              s.mimeType = type;
            }
            return this;
          },

          // Status-dependent callbacks
          statusCode: function (map) {
            var code;
            if (map) {
              if (completed) {
                // Execute the appropriate callbacks
                jqXHR.always(map[jqXHR.status]);
              } else {
                // Lazy-add the new callbacks in a way that preserves old ones
                for (code in map) {
                  statusCode[code] = [statusCode[code], map[code]];
                }
              }
            }
            return this;
          },

          // Cancel the request
          abort: function (statusText) {
            var finalText = statusText || strAbort;
            if (transport) {
              transport.abort(finalText);
            }
            done(0, finalText);
            return this;
          }
        };

      // Attach deferreds
      deferred.promise(jqXHR);

      // Add protocol if not provided (prefilters might expect it)
      // Handle falsy url in the settings object (#10093: consistency with old signature)
      // We also use the url parameter if available
      s.url = ((url || s.url || location.href) + '').replace(rprotocol, location.protocol + '//');

      // Alias method option to type as per ticket #12004
      s.type = options.method || options.type || s.method || s.type;

      // Extract dataTypes list
      s.dataTypes = (s.dataType || '*').toLowerCase().match(rnothtmlwhite) || [''];

      // A cross-domain request is in order when the origin doesn't match the current origin.
      if (s.crossDomain == null) {
        urlAnchor = document.createElement('a');

        // Support: IE <=8 - 11, Edge 12 - 15
        // IE throws exception on accessing the href property if url is malformed,
        // e.g. http://example.com:80x/
        try {
          urlAnchor.href = s.url;

          // Support: IE <=8 - 11 only
          // Anchor's host property isn't correctly set when s.url is relative
          urlAnchor.href = urlAnchor.href;
          s.crossDomain = originAnchor.protocol + '//' + originAnchor.host !== urlAnchor.protocol + '//' + urlAnchor.host;
        } catch (e) {
          // If there is an error parsing the URL, assume it is crossDomain,
          // it can be rejected by the transport if it is invalid
          s.crossDomain = true;
        }
      }

      // Convert data if not already a string
      if (s.data && s.processData && typeof s.data !== 'string') {
        s.data = jQuery.param(s.data, s.traditional);
      }

      // Apply prefilters
      inspectPrefiltersOrTransports(prefilters, s, options, jqXHR);

      // If request was aborted inside a prefilter, stop there
      if (completed) {
        return jqXHR;
      }

      // We can fire global events as of now if asked to
      // Don't fire events if jQuery.event is undefined in an AMD-usage scenario (#15118)
      fireGlobals = jQuery.event && s.global;

      // Watch for a new set of requests
      if (fireGlobals && jQuery.active++ === 0) {
        jQuery.event.trigger('ajaxStart');
      }

      // Uppercase the type
      s.type = s.type.toUpperCase();

      // Determine if request has content
      s.hasContent = !rnoContent.test(s.type);

      // Save the URL in case we're toying with the If-Modified-Since
      // and/or If-None-Match header later on
      // Remove hash to simplify url manipulation
      cacheURL = s.url.replace(rhash, '');

      // More options handling for requests with no content
      if (!s.hasContent) {
        // Remember the hash so we can put it back
        uncached = s.url.slice(cacheURL.length);

        // If data is available and should be processed, append data to url
        if (s.data && (s.processData || typeof s.data === 'string')) {
          cacheURL += (rquery.test(cacheURL) ? '&' : '?') + s.data;

          // #9682: remove data so that it's not used in an eventual retry
          delete s.data;
        }

        // Add or update anti-cache param if needed
        if (s.cache === false) {
          cacheURL = cacheURL.replace(rantiCache, '$1');
          uncached = (rquery.test(cacheURL) ? '&' : '?') + '_=' + nonce.guid++ + uncached;
        }

        // Put hash and anti-cache on the URL that will be requested (gh-1732)
        s.url = cacheURL + uncached;

        // Change '%20' to '+' if this is encoded form body content (gh-2658)
      } else if (s.data && s.processData && (s.contentType || '').indexOf('application/x-www-form-urlencoded') === 0) {
        s.data = s.data.replace(r20, '+');
      }

      // Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
      if (s.ifModified) {
        if (jQuery.lastModified[cacheURL]) {
          jqXHR.setRequestHeader('If-Modified-Since', jQuery.lastModified[cacheURL]);
        }
        if (jQuery.etag[cacheURL]) {
          jqXHR.setRequestHeader('If-None-Match', jQuery.etag[cacheURL]);
        }
      }

      // Set the correct header, if data is being sent
      if ((s.data && s.hasContent && s.contentType !== false) || options.contentType) {
        jqXHR.setRequestHeader('Content-Type', s.contentType);
      }

      // Set the Accepts header for the server, depending on the dataType
      jqXHR.setRequestHeader(
        'Accept',
        s.dataTypes[0] && s.accepts[s.dataTypes[0]] ? s.accepts[s.dataTypes[0]] + (s.dataTypes[0] !== '*' ? ', ' + allTypes + '; q=0.01' : '') : s.accepts['*']
      );

      // Check for headers option
      for (i in s.headers) {
        jqXHR.setRequestHeader(i, s.headers[i]);
      }

      // Allow custom headers/mimetypes and early abort
      if (s.beforeSend && (s.beforeSend.call(callbackContext, jqXHR, s) === false || completed)) {
        // Abort if not done already and return
        return jqXHR.abort();
      }

      // Aborting is no longer a cancellation
      strAbort = 'abort';

      // Install callbacks on deferreds
      completeDeferred.add(s.complete);
      jqXHR.done(s.success);
      jqXHR.fail(s.error);

      // Get transport
      transport = inspectPrefiltersOrTransports(transports, s, options, jqXHR);

      // If no transport, we auto-abort
      if (!transport) {
        done(-1, 'No Transport');
      } else {
        jqXHR.readyState = 1;

        // Send global event
        if (fireGlobals) {
          globalEventContext.trigger('ajaxSend', [jqXHR, s]);
        }

        // If request was aborted inside ajaxSend, stop there
        if (completed) {
          return jqXHR;
        }

        // Timeout
        if (s.async && s.timeout > 0) {
          timeoutTimer = window.setTimeout(function () {
            jqXHR.abort('timeout');
          }, s.timeout);
        }

        try {
          completed = false;
          transport.send(requestHeaders, done);
        } catch (e) {
          // Rethrow post-completion exceptions
          if (completed) {
            throw e;
          }

          // Propagate others as results
          done(-1, e);
        }
      }

      // Callback for when everything is done
      function done(status, nativeStatusText, responses, headers) {
        var isSuccess,
          success,
          error,
          response,
          modified,
          statusText = nativeStatusText;

        // Ignore repeat invocations
        if (completed) {
          return;
        }

        completed = true;

        // Clear timeout if it exists
        if (timeoutTimer) {
          window.clearTimeout(timeoutTimer);
        }

        // Dereference transport for early garbage collection
        // (no matter how long the jqXHR object will be used)
        transport = undefined;

        // Cache response headers
        responseHeadersString = headers || '';

        // Set readyState
        jqXHR.readyState = status > 0 ? 4 : 0;

        // Determine if successful
        isSuccess = (status >= 200 && status < 300) || status === 304;

        // Get response data
        if (responses) {
          response = ajaxHandleResponses(s, jqXHR, responses);
        }

        // Use a noop converter for missing script
        if (!isSuccess && jQuery.inArray('script', s.dataTypes) > -1) {
          s.converters['text script'] = function () {};
        }

        // Convert no matter what (that way responseXXX fields are always set)
        response = ajaxConvert(s, response, jqXHR, isSuccess);

        // If successful, handle type chaining
        if (isSuccess) {
          // Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
          if (s.ifModified) {
            modified = jqXHR.getResponseHeader('Last-Modified');
            if (modified) {
              jQuery.lastModified[cacheURL] = modified;
            }
            modified = jqXHR.getResponseHeader('etag');
            if (modified) {
              jQuery.etag[cacheURL] = modified;
            }
          }

          // if no content
          if (status === 204 || s.type === 'HEAD') {
            statusText = 'nocontent';

            // if not modified
          } else if (status === 304) {
            statusText = 'notmodified';

            // If we have data, let's convert it
          } else {
            statusText = response.state;
            success = response.data;
            error = response.error;
            isSuccess = !error;
          }
        } else {
          // Extract error from statusText and normalize for non-aborts
          error = statusText;
          if (status || !statusText) {
            statusText = 'error';
            if (status < 0) {
              status = 0;
            }
          }
        }

        // Set data for the fake xhr object
        jqXHR.status = status;
        jqXHR.statusText = (nativeStatusText || statusText) + '';

        // Success/Error
        if (isSuccess) {
          deferred.resolveWith(callbackContext, [success, statusText, jqXHR]);
        } else {
          deferred.rejectWith(callbackContext, [jqXHR, statusText, error]);
        }

        // Status-dependent callbacks
        jqXHR.statusCode(statusCode);
        statusCode = undefined;

        if (fireGlobals) {
          globalEventContext.trigger(isSuccess ? 'ajaxSuccess' : 'ajaxError', [jqXHR, s, isSuccess ? success : error]);
        }

        // Complete
        completeDeferred.fireWith(callbackContext, [jqXHR, statusText]);

        if (fireGlobals) {
          globalEventContext.trigger('ajaxComplete', [jqXHR, s]);

          // Handle the global AJAX counter
          if (!--jQuery.active) {
            jQuery.event.trigger('ajaxStop');
          }
        }
      }

      return jqXHR;
    },

    getJSON: function (url, data, callback) {
      return jQuery.get(url, data, callback, 'json');
    },

    getScript: function (url, callback) {
      return jQuery.get(url, undefined, callback, 'script');
    }
  });

  jQuery.each(['get', 'post'], function (_i, method) {
    jQuery[method] = function (url, data, callback, type) {
      // Shift arguments if data argument was omitted
      if (isFunction(data)) {
        type = type || callback;
        callback = data;
        data = undefined;
      }

      // The url can be an options object (which then must have .url)
      return jQuery.ajax(
        jQuery.extend(
          {
            url: url,
            type: method,
            dataType: type,
            data: data,
            success: callback
          },
          jQuery.isPlainObject(url) && url
        )
      );
    };
  });

  jQuery.ajaxPrefilter(function (s) {
    var i;
    for (i in s.headers) {
      if (i.toLowerCase() === 'content-type') {
        s.contentType = s.headers[i] || '';
      }
    }
  });

  jQuery._evalUrl = function (url, options, doc) {
    return jQuery.ajax({
      url: url,

      // Make this explicit, since user can override this through ajaxSetup (#11264)
      type: 'GET',
      dataType: 'script',
      cache: true,
      async: false,
      global: false,

      // Only evaluate the response if it is successful (gh-4126)
      // dataFilter is not invoked for failure responses, so using it instead
      // of the default converter is kludgy but it works.
      converters: {
        'text script': function () {}
      },
      dataFilter: function (response) {
        jQuery.globalEval(response, options, doc);
      }
    });
  };

  jQuery.fn.extend({
    wrapAll: function (html) {
      var wrap;

      if (this[0]) {
        if (isFunction(html)) {
          html = html.call(this[0]);
        }

        // The elements to wrap the target around
        wrap = jQuery(html, this[0].ownerDocument).eq(0).clone(true);

        if (this[0].parentNode) {
          wrap.insertBefore(this[0]);
        }

        wrap
          .map(function () {
            var elem = this;

            while (elem.firstElementChild) {
              elem = elem.firstElementChild;
            }

            return elem;
          })
          .append(this);
      }

      return this;
    },

    wrapInner: function (html) {
      if (isFunction(html)) {
        return this.each(function (i) {
          jQuery(this).wrapInner(html.call(this, i));
        });
      }

      return this.each(function () {
        var self = jQuery(this),
          contents = self.contents();

        if (contents.length) {
          contents.wrapAll(html);
        } else {
          self.append(html);
        }
      });
    },

    wrap: function (html) {
      var htmlIsFunction = isFunction(html);

      return this.each(function (i) {
        jQuery(this).wrapAll(htmlIsFunction ? html.call(this, i) : html);
      });
    },

    unwrap: function (selector) {
      this.parent(selector)
        .not('body')
        .each(function () {
          jQuery(this).replaceWith(this.childNodes);
        });
      return this;
    }
  });

  jQuery.expr.pseudos.hidden = function (elem) {
    return !jQuery.expr.pseudos.visible(elem);
  };
  jQuery.expr.pseudos.visible = function (elem) {
    return !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
  };

  jQuery.ajaxSettings.xhr = function () {
    try {
      return new window.XMLHttpRequest();
    } catch (e) {}
  };

  var xhrSuccessStatus = {
      // File protocol always yields status code 0, assume 200
      0: 200,

      // Support: IE <=9 only
      // #1450: sometimes IE returns 1223 when it should be 204
      1223: 204
    },
    xhrSupported = jQuery.ajaxSettings.xhr();

  support.cors = !!xhrSupported && 'withCredentials' in xhrSupported;
  support.ajax = xhrSupported = !!xhrSupported;

  jQuery.ajaxTransport(function (options) {
    var callback, errorCallback;

    // Cross domain only allowed if supported through XMLHttpRequest
    if (support.cors || (xhrSupported && !options.crossDomain)) {
      return {
        send: function (headers, complete) {
          var i,
            xhr = options.xhr();

          xhr.open(options.type, options.url, options.async, options.username, options.password);

          // Apply custom fields if provided
          if (options.xhrFields) {
            for (i in options.xhrFields) {
              xhr[i] = options.xhrFields[i];
            }
          }

          // Override mime type if needed
          if (options.mimeType && xhr.overrideMimeType) {
            xhr.overrideMimeType(options.mimeType);
          }

          // X-Requested-With header
          // For cross-domain requests, seeing as conditions for a preflight are
          // akin to a jigsaw puzzle, we simply never set it to be sure.
          // (it can always be set on a per-request basis or even using ajaxSetup)
          // For same-domain requests, won't change header if already provided.
          if (!options.crossDomain && !headers['X-Requested-With']) {
            headers['X-Requested-With'] = 'XMLHttpRequest';
          }

          // Set headers
          for (i in headers) {
            xhr.setRequestHeader(i, headers[i]);
          }

          // Callback
          callback = function (type) {
            return function () {
              if (callback) {
                callback = errorCallback = xhr.onload = xhr.onerror = xhr.onabort = xhr.ontimeout = xhr.onreadystatechange = null;

                if (type === 'abort') {
                  xhr.abort();
                } else if (type === 'error') {
                  // Support: IE <=9 only
                  // On a manual native abort, IE9 throws
                  // errors on any property access that is not readyState
                  if (typeof xhr.status !== 'number') {
                    complete(0, 'error');
                  } else {
                    complete(
                      // File: protocol always yields status 0; see #8605, #14207
                      xhr.status,
                      xhr.statusText
                    );
                  }
                } else {
                  complete(
                    xhrSuccessStatus[xhr.status] || xhr.status,
                    xhr.statusText,

                    // Support: IE <=9 only
                    // IE9 has no XHR2 but throws on binary (trac-11426)
                    // For XHR2 non-text, let the caller handle it (gh-2498)
                    (xhr.responseType || 'text') !== 'text' || typeof xhr.responseText !== 'string' ? { binary: xhr.response } : { text: xhr.responseText },
                    xhr.getAllResponseHeaders()
                  );
                }
              }
            };
          };

          // Listen to events
          xhr.onload = callback();
          errorCallback = xhr.onerror = xhr.ontimeout = callback('error');

          // Support: IE 9 only
          // Use onreadystatechange to replace onabort
          // to handle uncaught aborts
          if (xhr.onabort !== undefined) {
            xhr.onabort = errorCallback;
          } else {
            xhr.onreadystatechange = function () {
              // Check readyState before timeout as it changes
              if (xhr.readyState === 4) {
                // Allow onerror to be called first,
                // but that will not handle a native abort
                // Also, save errorCallback to a variable
                // as xhr.onerror cannot be accessed
                window.setTimeout(function () {
                  if (callback) {
                    errorCallback();
                  }
                });
              }
            };
          }

          // Create the abort callback
          callback = callback('abort');

          try {
            // Do send the request (this may raise an exception)
            xhr.send((options.hasContent && options.data) || null);
          } catch (e) {
            // #14683: Only rethrow if this hasn't been notified as an error yet
            if (callback) {
              throw e;
            }
          }
        },

        abort: function () {
          if (callback) {
            callback();
          }
        }
      };
    }
  });

  // Prevent auto-execution of scripts when no explicit dataType was provided (See gh-2432)
  jQuery.ajaxPrefilter(function (s) {
    if (s.crossDomain) {
      s.contents.script = false;
    }
  });

  // Install script dataType
  jQuery.ajaxSetup({
    accepts: {
      script: 'text/javascript, application/javascript, ' + 'application/ecmascript, application/x-ecmascript'
    },
    contents: {
      script: /\b(?:java|ecma)script\b/
    },
    converters: {
      'text script': function (text) {
        jQuery.globalEval(text);
        return text;
      }
    }
  });

  // Handle cache's special case and crossDomain
  jQuery.ajaxPrefilter('script', function (s) {
    if (s.cache === undefined) {
      s.cache = false;
    }
    if (s.crossDomain) {
      s.type = 'GET';
    }
  });

  // Bind script tag hack transport
  jQuery.ajaxTransport('script', function (s) {
    // This transport only deals with cross domain or forced-by-attrs requests
    if (s.crossDomain || s.scriptAttrs) {
      var script, callback;
      return {
        send: function (_, complete) {
          script = jQuery('<script>')
            .attr(s.scriptAttrs || {})
            .prop({ charset: s.scriptCharset, src: s.url })
            .on(
              'load error',
              (callback = function (evt) {
                script.remove();
                callback = null;
                if (evt) {
                  complete(evt.type === 'error' ? 404 : 200, evt.type);
                }
              })
            );

          // Use native DOM manipulation to avoid our domManip AJAX trickery
          document.head.appendChild(script[0]);
        },
        abort: function () {
          if (callback) {
            callback();
          }
        }
      };
    }
  });

  var oldCallbacks = [],
    rjsonp = /(=)\?(?=&|$)|\?\?/;

  // Default jsonp settings
  jQuery.ajaxSetup({
    jsonp: 'callback',
    jsonpCallback: function () {
      var callback = oldCallbacks.pop() || jQuery.expando + '_' + nonce.guid++;
      this[callback] = true;
      return callback;
    }
  });

  // Detect, normalize options and install callbacks for jsonp requests
  jQuery.ajaxPrefilter('json jsonp', function (s, originalSettings, jqXHR) {
    var callbackName,
      overwritten,
      responseContainer,
      jsonProp =
        s.jsonp !== false &&
        (rjsonp.test(s.url)
          ? 'url'
          : typeof s.data === 'string' && (s.contentType || '').indexOf('application/x-www-form-urlencoded') === 0 && rjsonp.test(s.data) && 'data');

    // Handle iff the expected data type is "jsonp" or we have a parameter to set
    if (jsonProp || s.dataTypes[0] === 'jsonp') {
      // Get callback name, remembering preexisting value associated with it
      callbackName = s.jsonpCallback = isFunction(s.jsonpCallback) ? s.jsonpCallback() : s.jsonpCallback;

      // Insert callback into url or form data
      if (jsonProp) {
        s[jsonProp] = s[jsonProp].replace(rjsonp, '$1' + callbackName);
      } else if (s.jsonp !== false) {
        s.url += (rquery.test(s.url) ? '&' : '?') + s.jsonp + '=' + callbackName;
      }

      // Use data converter to retrieve json after script execution
      s.converters['script json'] = function () {
        if (!responseContainer) {
          jQuery.error(callbackName + ' was not called');
        }
        return responseContainer[0];
      };

      // Force json dataType
      s.dataTypes[0] = 'json';

      // Install callback
      overwritten = window[callbackName];
      window[callbackName] = function () {
        responseContainer = arguments;
      };

      // Clean-up function (fires after converters)
      jqXHR.always(function () {
        // If previous value didn't exist - remove it
        if (overwritten === undefined) {
          jQuery(window).removeProp(callbackName);

          // Otherwise restore preexisting value
        } else {
          window[callbackName] = overwritten;
        }

        // Save back as free
        if (s[callbackName]) {
          // Make sure that re-using the options doesn't screw things around
          s.jsonpCallback = originalSettings.jsonpCallback;

          // Save the callback name for future use
          oldCallbacks.push(callbackName);
        }

        // Call if it was a function and we have a response
        if (responseContainer && isFunction(overwritten)) {
          overwritten(responseContainer[0]);
        }

        responseContainer = overwritten = undefined;
      });

      // Delegate to script
      return 'script';
    }
  });

  // Support: Safari 8 only
  // In Safari 8 documents created via document.implementation.createHTMLDocument
  // collapse sibling forms: the second one becomes a child of the first one.
  // Because of that, this security measure has to be disabled in Safari 8.
  // https://bugs.webkit.org/show_bug.cgi?id=137337
  support.createHTMLDocument = (function () {
    var body = document.implementation.createHTMLDocument('').body;
    body.innerHTML = '<form></form><form></form>';
    return body.childNodes.length === 2;
  })();

  // Argument "data" should be string of html
  // context (optional): If specified, the fragment will be created in this context,
  // defaults to document
  // keepScripts (optional): If true, will include scripts passed in the html string
  jQuery.parseHTML = function (data, context, keepScripts) {
    if (typeof data !== 'string') {
      return [];
    }
    if (typeof context === 'boolean') {
      keepScripts = context;
      context = false;
    }

    var base, parsed, scripts;

    if (!context) {
      // Stop scripts or inline event handlers from being executed immediately
      // by using document.implementation
      if (support.createHTMLDocument) {
        context = document.implementation.createHTMLDocument('');

        // Set the base href for the created document
        // so any parsed elements with URLs
        // are based on the document's URL (gh-2965)
        base = context.createElement('base');
        base.href = document.location.href;
        context.head.appendChild(base);
      } else {
        context = document;
      }
    }

    parsed = rsingleTag.exec(data);
    scripts = !keepScripts && [];

    // Single tag
    if (parsed) {
      return [context.createElement(parsed[1])];
    }

    parsed = buildFragment([data], context, scripts);

    if (scripts && scripts.length) {
      jQuery(scripts).remove();
    }

    return jQuery.merge([], parsed.childNodes);
  };

  /**
   * Load a url into a page
   */
  jQuery.fn.load = function (url, params, callback) {
    var selector,
      type,
      response,
      self = this,
      off = url.indexOf(' ');

    if (off > -1) {
      selector = stripAndCollapse(url.slice(off));
      url = url.slice(0, off);
    }

    // If it's a function
    if (isFunction(params)) {
      // We assume that it's the callback
      callback = params;
      params = undefined;

      // Otherwise, build a param string
    } else if (params && typeof params === 'object') {
      type = 'POST';
    }

    // If we have elements to modify, make the request
    if (self.length > 0) {
      jQuery
        .ajax({
          url: url,

          // If "type" variable is undefined, then "GET" method will be used.
          // Make value of this field explicit since
          // user can override it through ajaxSetup method
          type: type || 'GET',
          dataType: 'html',
          data: params
        })
        .done(function (responseText) {
          // Save response for use in complete callback
          response = arguments;

          self.html(
            selector
              ? // If a selector was specified, locate the right elements in a dummy div
                // Exclude scripts to avoid IE 'Permission Denied' errors
                jQuery('<div>').append(jQuery.parseHTML(responseText)).find(selector)
              : // Otherwise use the full result
                responseText
          );

          // If the request succeeds, this function gets "data", "status", "jqXHR"
          // but they are ignored because response was set above.
          // If it fails, this function gets "jqXHR", "status", "error"
        })
        .always(
          callback &&
            function (jqXHR, status) {
              self.each(function () {
                callback.apply(this, response || [jqXHR.responseText, status, jqXHR]);
              });
            }
        );
    }

    return this;
  };

  jQuery.expr.pseudos.animated = function (elem) {
    return jQuery.grep(jQuery.timers, function (fn) {
      return elem === fn.elem;
    }).length;
  };

  jQuery.offset = {
    setOffset: function (elem, options, i) {
      var curPosition,
        curLeft,
        curCSSTop,
        curTop,
        curOffset,
        curCSSLeft,
        calculatePosition,
        position = jQuery.css(elem, 'position'),
        curElem = jQuery(elem),
        props = {};

      // Set position first, in-case top/left are set even on static elem
      if (position === 'static') {
        elem.style.position = 'relative';
      }

      curOffset = curElem.offset();
      curCSSTop = jQuery.css(elem, 'top');
      curCSSLeft = jQuery.css(elem, 'left');
      calculatePosition = (position === 'absolute' || position === 'fixed') && (curCSSTop + curCSSLeft).indexOf('auto') > -1;

      // Need to be able to calculate position if either
      // top or left is auto and position is either absolute or fixed
      if (calculatePosition) {
        curPosition = curElem.position();
        curTop = curPosition.top;
        curLeft = curPosition.left;
      } else {
        curTop = parseFloat(curCSSTop) || 0;
        curLeft = parseFloat(curCSSLeft) || 0;
      }

      if (isFunction(options)) {
        // Use jQuery.extend here to allow modification of coordinates argument (gh-1848)
        options = options.call(elem, i, jQuery.extend({}, curOffset));
      }

      if (options.top != null) {
        props.top = options.top - curOffset.top + curTop;
      }
      if (options.left != null) {
        props.left = options.left - curOffset.left + curLeft;
      }

      if ('using' in options) {
        options.using.call(elem, props);
      } else {
        if (typeof props.top === 'number') {
          props.top += 'px';
        }
        if (typeof props.left === 'number') {
          props.left += 'px';
        }
        curElem.css(props);
      }
    }
  };

  jQuery.fn.extend({
    // offset() relates an element's border box to the document origin
    offset: function (options) {
      // Preserve chaining for setter
      if (arguments.length) {
        return options === undefined
          ? this
          : this.each(function (i) {
              jQuery.offset.setOffset(this, options, i);
            });
      }

      var rect,
        win,
        elem = this[0];

      if (!elem) {
        return;
      }

      // Return zeros for disconnected and hidden (display: none) elements (gh-2310)
      // Support: IE <=11 only
      // Running getBoundingClientRect on a
      // disconnected node in IE throws an error
      if (!elem.getClientRects().length) {
        return { top: 0, left: 0 };
      }

      // Get document-relative position by adding viewport scroll to viewport-relative gBCR
      rect = elem.getBoundingClientRect();
      win = elem.ownerDocument.defaultView;
      return {
        top: rect.top + win.pageYOffset,
        left: rect.left + win.pageXOffset
      };
    },

    // position() relates an element's margin box to its offset parent's padding box
    // This corresponds to the behavior of CSS absolute positioning
    position: function () {
      if (!this[0]) {
        return;
      }

      var offsetParent,
        offset,
        doc,
        elem = this[0],
        parentOffset = { top: 0, left: 0 };

      // position:fixed elements are offset from the viewport, which itself always has zero offset
      if (jQuery.css(elem, 'position') === 'fixed') {
        // Assume position:fixed implies availability of getBoundingClientRect
        offset = elem.getBoundingClientRect();
      } else {
        offset = this.offset();

        // Account for the *real* offset parent, which can be the document or its root element
        // when a statically positioned element is identified
        doc = elem.ownerDocument;
        offsetParent = elem.offsetParent || doc.documentElement;
        while (offsetParent && (offsetParent === doc.body || offsetParent === doc.documentElement) && jQuery.css(offsetParent, 'position') === 'static') {
          offsetParent = offsetParent.parentNode;
        }
        if (offsetParent && offsetParent !== elem && offsetParent.nodeType === 1) {
          // Incorporate borders into its offset, since they are outside its content origin
          parentOffset = jQuery(offsetParent).offset();
          parentOffset.top += jQuery.css(offsetParent, 'borderTopWidth', true);
          parentOffset.left += jQuery.css(offsetParent, 'borderLeftWidth', true);
        }
      }

      // Subtract parent offsets and element margins
      return {
        top: offset.top - parentOffset.top - jQuery.css(elem, 'marginTop', true),
        left: offset.left - parentOffset.left - jQuery.css(elem, 'marginLeft', true)
      };
    },

    // This method will return documentElement in the following cases:
    // 1) For the element inside the iframe without offsetParent, this method will return
    //    documentElement of the parent window
    // 2) For the hidden or detached element
    // 3) For body or html element, i.e. in case of the html node - it will return itself
    //
    // but those exceptions were never presented as a real life use-cases
    // and might be considered as more preferable results.
    //
    // This logic, however, is not guaranteed and can change at any point in the future
    offsetParent: function () {
      return this.map(function () {
        var offsetParent = this.offsetParent;

        while (offsetParent && jQuery.css(offsetParent, 'position') === 'static') {
          offsetParent = offsetParent.offsetParent;
        }

        return offsetParent || documentElement;
      });
    }
  });

  // Create scrollLeft and scrollTop methods
  jQuery.each({ scrollLeft: 'pageXOffset', scrollTop: 'pageYOffset' }, function (method, prop) {
    var top = 'pageYOffset' === prop;

    jQuery.fn[method] = function (val) {
      return access(
        this,
        function (elem, method, val) {
          // Coalesce documents and windows
          var win;
          if (isWindow(elem)) {
            win = elem;
          } else if (elem.nodeType === 9) {
            win = elem.defaultView;
          }

          if (val === undefined) {
            return win ? win[prop] : elem[method];
          }

          if (win) {
            win.scrollTo(!top ? val : win.pageXOffset, top ? val : win.pageYOffset);
          } else {
            elem[method] = val;
          }
        },
        method,
        val,
        arguments.length
      );
    };
  });

  // Support: Safari <=7 - 9.1, Chrome <=37 - 49
  // Add the top/left cssHooks using jQuery.fn.position
  // Webkit bug: https://bugs.webkit.org/show_bug.cgi?id=29084
  // Blink bug: https://bugs.chromium.org/p/chromium/issues/detail?id=589347
  // getComputedStyle returns percent when specified for top/left/bottom/right;
  // rather than make the css module depend on the offset module, just check for it here
  jQuery.each(['top', 'left'], function (_i, prop) {
    jQuery.cssHooks[prop] = addGetHookIf(support.pixelPosition, function (elem, computed) {
      if (computed) {
        computed = curCSS(elem, prop);

        // If curCSS returns percentage, fallback to offset
        return rnumnonpx.test(computed) ? jQuery(elem).position()[prop] + 'px' : computed;
      }
    });
  });

  // Create innerHeight, innerWidth, height, width, outerHeight and outerWidth methods
  jQuery.each({ Height: 'height', Width: 'width' }, function (name, type) {
    jQuery.each({ padding: 'inner' + name, content: type, '': 'outer' + name }, function (defaultExtra, funcName) {
      // Margin is only for outerHeight, outerWidth
      jQuery.fn[funcName] = function (margin, value) {
        var chainable = arguments.length && (defaultExtra || typeof margin !== 'boolean'),
          extra = defaultExtra || (margin === true || value === true ? 'margin' : 'border');

        return access(
          this,
          function (elem, type, value) {
            var doc;

            if (isWindow(elem)) {
              // $( window ).outerWidth/Height return w/h including scrollbars (gh-1729)
              return funcName.indexOf('outer') === 0 ? elem['inner' + name] : elem.document.documentElement['client' + name];
            }

            // Get document width or height
            if (elem.nodeType === 9) {
              doc = elem.documentElement;

              // Either scroll[Width/Height] or offset[Width/Height] or client[Width/Height],
              // whichever is greatest
              return Math.max(elem.body['scroll' + name], doc['scroll' + name], elem.body['offset' + name], doc['offset' + name], doc['client' + name]);
            }

            return value === undefined
              ? // Get width or height on the element, requesting but not forcing parseFloat
                jQuery.css(elem, type, extra)
              : // Set width or height on the element
                jQuery.style(elem, type, value, extra);
          },
          type,
          chainable ? margin : undefined,
          chainable
        );
      };
    });
  });

  jQuery.each(['ajaxStart', 'ajaxStop', 'ajaxComplete', 'ajaxError', 'ajaxSuccess', 'ajaxSend'], function (_i, type) {
    jQuery.fn[type] = function (fn) {
      return this.on(type, fn);
    };
  });

  jQuery.fn.extend({
    bind: function (types, data, fn) {
      return this.on(types, null, data, fn);
    },
    unbind: function (types, fn) {
      return this.off(types, null, fn);
    },

    delegate: function (selector, types, data, fn) {
      return this.on(types, selector, data, fn);
    },
    undelegate: function (selector, types, fn) {
      // ( namespace ) or ( selector, types [, fn] )
      return arguments.length === 1 ? this.off(selector, '**') : this.off(types, selector || '**', fn);
    },

    hover: function (fnOver, fnOut) {
      return this.mouseenter(fnOver).mouseleave(fnOut || fnOver);
    }
  });

  jQuery.each(
    (
      'blur focus focusin focusout resize scroll click dblclick ' +
      'mousedown mouseup mousemove mouseover mouseout mouseenter mouseleave ' +
      'change select submit keydown keypress keyup contextmenu'
    ).split(' '),
    function (_i, name) {
      // Handle event binding
      jQuery.fn[name] = function (data, fn) {
        return arguments.length > 0 ? this.on(name, null, data, fn) : this.trigger(name);
      };
    }
  );

  // Support: Android <=4.0 only
  // Make sure we trim BOM and NBSP
  var rtrim = /^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g;

  // Bind a function to a context, optionally partially applying any
  // arguments.
  // jQuery.proxy is deprecated to promote standards (specifically Function#bind)
  // However, it is not slated for removal any time soon
  jQuery.proxy = function (fn, context) {
    var tmp, args, proxy;

    if (typeof context === 'string') {
      tmp = fn[context];
      context = fn;
      fn = tmp;
    }

    // Quick check to determine if target is callable, in the spec
    // this throws a TypeError, but we will just return undefined.
    if (!isFunction(fn)) {
      return undefined;
    }

    // Simulated bind
    args = slice.call(arguments, 2);
    proxy = function () {
      return fn.apply(context || this, args.concat(slice.call(arguments)));
    };

    // Set the guid of unique handler to the same of original handler, so it can be removed
    proxy.guid = fn.guid = fn.guid || jQuery.guid++;

    return proxy;
  };

  jQuery.holdReady = function (hold) {
    if (hold) {
      jQuery.readyWait++;
    } else {
      jQuery.ready(true);
    }
  };
  jQuery.isArray = Array.isArray;
  jQuery.parseJSON = JSON.parse;
  jQuery.nodeName = nodeName;
  jQuery.isFunction = isFunction;
  jQuery.isWindow = isWindow;
  jQuery.camelCase = camelCase;
  jQuery.type = toType;

  jQuery.now = Date.now;

  jQuery.isNumeric = function (obj) {
    // As of jQuery 3.0, isNumeric is limited to
    // strings and numbers (primitives or objects)
    // that can be coerced to finite numbers (gh-2662)
    var type = jQuery.type(obj);
    return (
      (type === 'number' || type === 'string') &&
      // parseFloat NaNs numeric-cast false positives ("")
      // ...but misinterprets leading-number strings, particularly hex literals ("0x...")
      // subtraction forces infinities to NaN
      !isNaN(obj - parseFloat(obj))
    );
  };

  jQuery.trim = function (text) {
    return text == null ? '' : (text + '').replace(rtrim, '');
  };

  // Register as a named AMD module, since jQuery can be concatenated with other
  // files that may use define, but not via a proper concatenation script that
  // understands anonymous AMD modules. A named AMD is safest and most robust
  // way to register. Lowercase jquery is used because AMD module names are
  // derived from file names, and jQuery is normally delivered in a lowercase
  // file name. Do this after creating the global so that if an AMD module wants
  // to call noConflict to hide this version of jQuery, it will work.

  // Note that for maximum portability, libraries that are not jQuery should
  // declare themselves as anonymous modules, and avoid setting a global if an
  // AMD loader is present. jQuery is a special case. For more information, see
  // https://github.com/jrburke/requirejs/wiki/Updating-existing-libraries#wiki-anon

  if (typeof define === 'function' && define.amd) {
    define('jquery', [], function () {
      return jQuery;
    });
  }

  var // Map over jQuery in case of overwrite
    _jQuery = window.jQuery,
    // Map over the $ in case of overwrite
    _$ = window.$;

  jQuery.noConflict = function (deep) {
    if (window.$ === jQuery) {
      window.$ = _$;
    }

    if (deep && window.jQuery === jQuery) {
      window.jQuery = _jQuery;
    }

    return jQuery;
  };

  // Expose jQuery and $ identifiers, even in AMD
  // (#7102#comment:10, https://github.com/jquery/jquery/pull/557)
  // and CommonJS for browser emulators (#13566)
  if (typeof noGlobal === 'undefined') {
    window.jQuery = window.$ = jQuery;
  }

  return jQuery;
}); /**
 * Copyright 2015 Tim Down.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * log4javascript
 *
 * log4javascript is a logging framework for JavaScript based on log4j
 * for Java. This file contains all core log4javascript code and is the only
 * file required to use log4javascript, unless you require support for
 * document.domain, in which case you will also need console.html, which must be
 * stored in the same directory as the main log4javascript.js file.
 *
 * Author: Tim Down <tim@log4javascript.org>
 * Version: 1.4.13
 * Edition: log4javascript
 * Build date: 23 May 2015
 * Website: http://log4javascript.org
 */

(function (factory, root) {
  if (typeof define == 'function' && define.amd) {
    // AMD. Register as an anonymous module.
    define(factory);
  } else if (typeof module != 'undefined' && typeof exports == 'object') {
    // Node/CommonJS style
    module.exports = factory();
  } else {
    // No AMD or CommonJS support so we place log4javascript in (probably) the global variable
    root.log4javascript = factory();
  }
})(function () {
  // Array-related stuff. Next three methods are solely for IE5, which is missing them
  if (!Array.prototype.push) {
    Array.prototype.push = function () {
      for (var i = 0, len = arguments.length; i < len; i++) {
        this[this.length] = arguments[i];
      }
      return this.length;
    };
  }

  if (!Array.prototype.shift) {
    Array.prototype.shift = function () {
      if (this.length > 0) {
        var firstItem = this[0];
        for (var i = 0, len = this.length - 1; i < len; i++) {
          this[i] = this[i + 1];
        }
        this.length = this.length - 1;
        return firstItem;
      }
    };
  }

  if (!Array.prototype.splice) {
    Array.prototype.splice = function (startIndex, deleteCount) {
      var itemsAfterDeleted = this.slice(startIndex + deleteCount);
      var itemsDeleted = this.slice(startIndex, startIndex + deleteCount);
      this.length = startIndex;
      // Copy the arguments into a proper Array object
      var argumentsArray = [];
      for (var i = 0, len = arguments.length; i < len; i++) {
        argumentsArray[i] = arguments[i];
      }
      var itemsToAppend = argumentsArray.length > 2 ? (itemsAfterDeleted = argumentsArray.slice(2).concat(itemsAfterDeleted)) : itemsAfterDeleted;
      for (i = 0, len = itemsToAppend.length; i < len; i++) {
        this.push(itemsToAppend[i]);
      }
      return itemsDeleted;
    };
  }

  /* ---------------------------------------------------------------------- */

  function isUndefined(obj) {
    return typeof obj == 'undefined';
  }

  /* ---------------------------------------------------------------------- */
  // Custom event support

  function EventSupport() {}

  EventSupport.prototype = {
    eventTypes: [],
    eventListeners: {},
    setEventTypes: function (eventTypesParam) {
      if (eventTypesParam instanceof Array) {
        this.eventTypes = eventTypesParam;
        this.eventListeners = {};
        for (var i = 0, len = this.eventTypes.length; i < len; i++) {
          this.eventListeners[this.eventTypes[i]] = [];
        }
      } else {
        handleError('log4javascript.EventSupport [' + this + ']: setEventTypes: eventTypes parameter must be an Array');
      }
    },

    addEventListener: function (eventType, listener) {
      if (typeof listener == 'function') {
        if (!array_contains(this.eventTypes, eventType)) {
          handleError('log4javascript.EventSupport [' + this + "]: addEventListener: no event called '" + eventType + "'");
        }
        this.eventListeners[eventType].push(listener);
      } else {
        handleError('log4javascript.EventSupport [' + this + ']: addEventListener: listener must be a function');
      }
    },

    removeEventListener: function (eventType, listener) {
      if (typeof listener == 'function') {
        if (!array_contains(this.eventTypes, eventType)) {
          handleError('log4javascript.EventSupport [' + this + "]: removeEventListener: no event called '" + eventType + "'");
        }
        array_remove(this.eventListeners[eventType], listener);
      } else {
        handleError('log4javascript.EventSupport [' + this + ']: removeEventListener: listener must be a function');
      }
    },

    dispatchEvent: function (eventType, eventArgs) {
      if (array_contains(this.eventTypes, eventType)) {
        var listeners = this.eventListeners[eventType];
        for (var i = 0, len = listeners.length; i < len; i++) {
          listeners[i](this, eventType, eventArgs);
        }
      } else {
        handleError('log4javascript.EventSupport [' + this + "]: dispatchEvent: no event called '" + eventType + "'");
      }
    }
  };

  /* -------------------------------------------------------------------------- */

  var applicationStartDate = new Date();
  var uniqueId = 'log4javascript_' + applicationStartDate.getTime() + '_' + Math.floor(Math.random() * 100000000);
  var emptyFunction = function () {};
  var newLine = '\r\n';
  var pageLoaded = false;

  // Create main log4javascript object; this will be assigned public properties
  function Log4JavaScript() {}
  Log4JavaScript.prototype = new EventSupport();

  var log4javascript = new Log4JavaScript();
  log4javascript.version = '1.4.13';
  log4javascript.edition = 'log4javascript';

  /* -------------------------------------------------------------------------- */
  // Utility functions

  function toStr(obj) {
    if (obj && obj.toString) {
      return obj.toString();
    } else {
      return String(obj);
    }
  }

  function getExceptionMessage(ex) {
    if (ex.message) {
      return ex.message;
    } else if (ex.description) {
      return ex.description;
    } else {
      return toStr(ex);
    }
  }

  // Gets the portion of the URL after the last slash
  function getUrlFileName(url) {
    var lastSlashIndex = Math.max(url.lastIndexOf('/'), url.lastIndexOf('\\'));
    return url.substr(lastSlashIndex + 1);
  }

  // Returns a nicely formatted representation of an error
  function getExceptionStringRep(ex) {
    if (ex) {
      var exStr = 'Exception: ' + getExceptionMessage(ex);
      try {
        if (ex.lineNumber) {
          exStr += ' on line number ' + ex.lineNumber;
        }
        if (ex.fileName) {
          exStr += ' in file ' + getUrlFileName(ex.fileName);
        }
      } catch (localEx) {
        logLog.warn('Unable to obtain file and line information for error');
      }
      if (showStackTraces && ex.stack) {
        exStr += newLine + 'Stack trace:' + newLine + ex.stack;
      }
      return exStr;
    }
    return null;
  }

  function bool(obj) {
    return Boolean(obj);
  }

  function trim(str) {
    return str.replace(/^\s+/, '').replace(/\s+$/, '');
  }

  function splitIntoLines(text) {
    // Ensure all line breaks are \n only
    var text2 = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    return text2.split('\n');
  }

  var urlEncode =
    typeof window.encodeURIComponent != 'undefined'
      ? function (str) {
          return encodeURIComponent(str);
        }
      : function (str) {
          return escape(str).replace(/\+/g, '%2B').replace(/"/g, '%22').replace(/'/g, '%27').replace(/\//g, '%2F').replace(/=/g, '%3D');
        };

  function array_remove(arr, val) {
    var index = -1;
    for (var i = 0, len = arr.length; i < len; i++) {
      if (arr[i] === val) {
        index = i;
        break;
      }
    }
    if (index >= 0) {
      arr.splice(index, 1);
      return true;
    } else {
      return false;
    }
  }

  function array_contains(arr, val) {
    for (var i = 0, len = arr.length; i < len; i++) {
      if (arr[i] == val) {
        return true;
      }
    }
    return false;
  }

  function extractBooleanFromParam(param, defaultValue) {
    if (isUndefined(param)) {
      return defaultValue;
    } else {
      return bool(param);
    }
  }

  function extractStringFromParam(param, defaultValue) {
    if (isUndefined(param)) {
      return defaultValue;
    } else {
      return String(param);
    }
  }

  function extractIntFromParam(param, defaultValue) {
    if (isUndefined(param)) {
      return defaultValue;
    } else {
      try {
        var value = parseInt(param, 10);
        return isNaN(value) ? defaultValue : value;
      } catch (ex) {
        logLog.warn('Invalid int param ' + param, ex);
        return defaultValue;
      }
    }
  }

  function extractFunctionFromParam(param, defaultValue) {
    if (typeof param == 'function') {
      return param;
    } else {
      return defaultValue;
    }
  }

  function isError(err) {
    return err instanceof Error;
  }

  if (!Function.prototype.apply) {
    Function.prototype.apply = function (obj, args) {
      var methodName = '__apply__';
      if (typeof obj[methodName] != 'undefined') {
        methodName += String(Math.random()).substr(2);
      }
      obj[methodName] = this;

      var argsStrings = [];
      for (var i = 0, len = args.length; i < len; i++) {
        argsStrings[i] = 'args[' + i + ']';
      }
      var script = 'obj.' + methodName + '(' + argsStrings.join(',') + ')';
      var returnValue = eval(script);
      delete obj[methodName];
      return returnValue;
    };
  }

  if (!Function.prototype.call) {
    Function.prototype.call = function (obj) {
      var args = [];
      for (var i = 1, len = arguments.length; i < len; i++) {
        args[i - 1] = arguments[i];
      }
      return this.apply(obj, args);
    };
  }

  /* ---------------------------------------------------------------------- */
  // Simple logging for log4javascript itself

  var logLog = {
    quietMode: false,

    debugMessages: [],

    setQuietMode: function (quietMode) {
      this.quietMode = bool(quietMode);
    },

    numberOfErrors: 0,

    alertAllErrors: false,

    setAlertAllErrors: function (alertAllErrors) {
      this.alertAllErrors = alertAllErrors;
    },

    debug: function (message) {
      this.debugMessages.push(message);
    },

    displayDebug: function () {
      alert(this.debugMessages.join(newLine));
    },

    warn: function (message, exception) {},

    error: function (message, exception) {
      if (++this.numberOfErrors == 1 || this.alertAllErrors) {
        if (!this.quietMode) {
          var alertMessage = 'log4javascript error: ' + message;
          if (exception) {
            alertMessage += newLine + newLine + 'Original error: ' + getExceptionStringRep(exception);
          }
          alert(alertMessage);
        }
      }
    }
  };
  log4javascript.logLog = logLog;

  log4javascript.setEventTypes(['load', 'error']);

  function handleError(message, exception) {
    logLog.error(message, exception);
    log4javascript.dispatchEvent('error', { message: message, exception: exception });
  }

  log4javascript.handleError = handleError;

  /* ---------------------------------------------------------------------- */

  var enabled = !(typeof log4javascript_disabled != 'undefined' && log4javascript_disabled);

  log4javascript.setEnabled = function (enable) {
    enabled = bool(enable);
  };

  log4javascript.isEnabled = function () {
    return enabled;
  };

  var useTimeStampsInMilliseconds = true;

  log4javascript.setTimeStampsInMilliseconds = function (timeStampsInMilliseconds) {
    useTimeStampsInMilliseconds = bool(timeStampsInMilliseconds);
  };

  log4javascript.isTimeStampsInMilliseconds = function () {
    return useTimeStampsInMilliseconds;
  };

  // This evaluates the given expression in the current scope, thus allowing
  // scripts to access private variables. Particularly useful for testing
  log4javascript.evalInScope = function (expr) {
    return eval(expr);
  };

  var showStackTraces = false;

  log4javascript.setShowStackTraces = function (show) {
    showStackTraces = bool(show);
  };

  /* ---------------------------------------------------------------------- */
  // Levels

  var Level = function (level, name) {
    this.level = level;
    this.name = name;
  };

  Level.prototype = {
    toString: function () {
      return this.name;
    },
    equals: function (level) {
      return this.level == level.level;
    },
    isGreaterOrEqual: function (level) {
      return this.level >= level.level;
    }
  };

  Level.ALL = new Level(Number.MIN_VALUE, 'ALL');
  Level.TRACE = new Level(10000, 'TRACE');
  Level.DEBUG = new Level(20000, 'DEBUG');
  Level.INFO = new Level(30000, 'INFO');
  Level.WARN = new Level(40000, 'WARN');
  Level.ERROR = new Level(50000, 'ERROR');
  Level.FATAL = new Level(60000, 'FATAL');
  Level.OFF = new Level(Number.MAX_VALUE, 'OFF');

  log4javascript.Level = Level;

  /* ---------------------------------------------------------------------- */
  // Timers

  function Timer(name, level) {
    this.name = name;
    this.level = isUndefined(level) ? Level.INFO : level;
    this.start = new Date();
  }

  Timer.prototype.getElapsedTime = function () {
    return new Date().getTime() - this.start.getTime();
  };

  /* ---------------------------------------------------------------------- */
  // Loggers

  var anonymousLoggerName = '[anonymous]';
  var defaultLoggerName = '[default]';
  var nullLoggerName = '[null]';
  var rootLoggerName = 'root';

  function Logger(name) {
    this.name = name;
    this.parent = null;
    this.children = [];

    var appenders = [];
    var loggerLevel = null;
    var isRoot = this.name === rootLoggerName;
    var isNull = this.name === nullLoggerName;

    var appenderCache = null;
    var appenderCacheInvalidated = false;

    this.addChild = function (childLogger) {
      this.children.push(childLogger);
      childLogger.parent = this;
      childLogger.invalidateAppenderCache();
    };

    // Additivity
    var additive = true;
    this.getAdditivity = function () {
      return additive;
    };

    this.setAdditivity = function (additivity) {
      var valueChanged = additive != additivity;
      additive = additivity;
      if (valueChanged) {
        this.invalidateAppenderCache();
      }
    };

    // Create methods that use the appenders variable in this scope
    this.addAppender = function (appender) {
      if (isNull) {
        handleError('Logger.addAppender: you may not add an appender to the null logger');
      } else {
        if (appender instanceof log4javascript.Appender) {
          if (!array_contains(appenders, appender)) {
            appenders.push(appender);
            appender.setAddedToLogger(this);
            this.invalidateAppenderCache();
          }
        } else {
          handleError("Logger.addAppender: appender supplied ('" + toStr(appender) + "') is not a subclass of Appender");
        }
      }
    };

    this.removeAppender = function (appender) {
      array_remove(appenders, appender);
      appender.setRemovedFromLogger(this);
      this.invalidateAppenderCache();
    };

    this.removeAllAppenders = function () {
      var appenderCount = appenders.length;
      if (appenderCount > 0) {
        for (var i = 0; i < appenderCount; i++) {
          appenders[i].setRemovedFromLogger(this);
        }
        appenders.length = 0;
        this.invalidateAppenderCache();
      }
    };

    this.getEffectiveAppenders = function () {
      if (appenderCache === null || appenderCacheInvalidated) {
        // Build appender cache
        var parentEffectiveAppenders = isRoot || !this.getAdditivity() ? [] : this.parent.getEffectiveAppenders();
        appenderCache = parentEffectiveAppenders.concat(appenders);
        appenderCacheInvalidated = false;
      }
      return appenderCache;
    };

    this.invalidateAppenderCache = function () {
      appenderCacheInvalidated = true;
      for (var i = 0, len = this.children.length; i < len; i++) {
        this.children[i].invalidateAppenderCache();
      }
    };

    this.log = function (level, params) {
      if (enabled && level.isGreaterOrEqual(this.getEffectiveLevel())) {
        // Check whether last param is an exception
        var exception;
        var finalParamIndex = params.length - 1;
        var lastParam = params[finalParamIndex];
        if (params.length > 1 && isError(lastParam)) {
          exception = lastParam;
          finalParamIndex--;
        }

        // Construct genuine array for the params
        var messages = [];
        for (var i = 0; i <= finalParamIndex; i++) {
          messages[i] = params[i];
        }

        var loggingEvent = new LoggingEvent(this, new Date(), level, messages, exception);

        this.callAppenders(loggingEvent);
      }
    };

    this.callAppenders = function (loggingEvent) {
      var effectiveAppenders = this.getEffectiveAppenders();
      for (var i = 0, len = effectiveAppenders.length; i < len; i++) {
        effectiveAppenders[i].doAppend(loggingEvent);
      }
    };

    this.setLevel = function (level) {
      // Having a level of null on the root logger would be very bad.
      if (isRoot && level === null) {
        handleError('Logger.setLevel: you cannot set the level of the root logger to null');
      } else if (level instanceof Level) {
        loggerLevel = level;
      } else {
        handleError('Logger.setLevel: level supplied to logger ' + this.name + ' is not an instance of log4javascript.Level');
      }
    };

    this.getLevel = function () {
      return loggerLevel;
    };

    this.getEffectiveLevel = function () {
      for (var logger = this; logger !== null; logger = logger.parent) {
        var level = logger.getLevel();
        if (level !== null) {
          return level;
        }
      }
    };

    this.group = function (name, initiallyExpanded) {
      if (enabled) {
        var effectiveAppenders = this.getEffectiveAppenders();
        for (var i = 0, len = effectiveAppenders.length; i < len; i++) {
          effectiveAppenders[i].group(name, initiallyExpanded);
        }
      }
    };

    this.groupEnd = function () {
      if (enabled) {
        var effectiveAppenders = this.getEffectiveAppenders();
        for (var i = 0, len = effectiveAppenders.length; i < len; i++) {
          effectiveAppenders[i].groupEnd();
        }
      }
    };

    var timers = {};

    this.time = function (name, level) {
      if (enabled) {
        if (isUndefined(name)) {
          handleError('Logger.time: a name for the timer must be supplied');
        } else if (level && !(level instanceof Level)) {
          handleError('Logger.time: level supplied to timer ' + name + ' is not an instance of log4javascript.Level');
        } else {
          timers[name] = new Timer(name, level);
        }
      }
    };

    this.timeEnd = function (name) {
      if (enabled) {
        if (isUndefined(name)) {
          handleError('Logger.timeEnd: a name for the timer must be supplied');
        } else if (timers[name]) {
          var timer = timers[name];
          var milliseconds = timer.getElapsedTime();
          this.log(timer.level, ['Timer ' + toStr(name) + ' completed in ' + milliseconds + 'ms']);
          delete timers[name];
        } else {
          logLog.warn('Logger.timeEnd: no timer found with name ' + name);
        }
      }
    };

    this.assert = function (expr) {
      if (enabled && !expr) {
        var args = [];
        for (var i = 1, len = arguments.length; i < len; i++) {
          args.push(arguments[i]);
        }
        args = args.length > 0 ? args : ['Assertion Failure'];
        args.push(newLine);
        args.push(expr);
        this.log(Level.ERROR, args);
      }
    };

    this.toString = function () {
      return 'Logger[' + this.name + ']';
    };
  }

  Logger.prototype = {
    trace: function () {
      this.log(Level.TRACE, arguments);
    },

    debug: function () {
      this.log(Level.DEBUG, arguments);
    },

    info: function () {
      this.log(Level.INFO, arguments);
    },

    warn: function () {
      this.log(Level.WARN, arguments);
    },

    error: function () {
      this.log(Level.ERROR, arguments);
    },

    fatal: function () {
      this.log(Level.FATAL, arguments);
    },

    isEnabledFor: function (level) {
      return level.isGreaterOrEqual(this.getEffectiveLevel());
    },

    isTraceEnabled: function () {
      return this.isEnabledFor(Level.TRACE);
    },

    isDebugEnabled: function () {
      return this.isEnabledFor(Level.DEBUG);
    },

    isInfoEnabled: function () {
      return this.isEnabledFor(Level.INFO);
    },

    isWarnEnabled: function () {
      return this.isEnabledFor(Level.WARN);
    },

    isErrorEnabled: function () {
      return this.isEnabledFor(Level.ERROR);
    },

    isFatalEnabled: function () {
      return this.isEnabledFor(Level.FATAL);
    }
  };

  Logger.prototype.trace.isEntryPoint = true;
  Logger.prototype.debug.isEntryPoint = true;
  Logger.prototype.info.isEntryPoint = true;
  Logger.prototype.warn.isEntryPoint = true;
  Logger.prototype.error.isEntryPoint = true;
  Logger.prototype.fatal.isEntryPoint = true;

  /* ---------------------------------------------------------------------- */
  // Logger access methods

  // Hashtable of loggers keyed by logger name
  var loggers = {};
  var loggerNames = [];

  var ROOT_LOGGER_DEFAULT_LEVEL = Level.DEBUG;
  var rootLogger = new Logger(rootLoggerName);
  rootLogger.setLevel(ROOT_LOGGER_DEFAULT_LEVEL);

  log4javascript.getRootLogger = function () {
    return rootLogger;
  };

  log4javascript.getLogger = function (loggerName) {
    // Use default logger if loggerName is not specified or invalid
    if (typeof loggerName != 'string') {
      loggerName = anonymousLoggerName;
      logLog.warn('log4javascript.getLogger: non-string logger name ' + toStr(loggerName) + ' supplied, returning anonymous logger');
    }

    // Do not allow retrieval of the root logger by name
    if (loggerName == rootLoggerName) {
      handleError('log4javascript.getLogger: root logger may not be obtained by name');
    }

    // Create the logger for this name if it doesn't already exist
    if (!loggers[loggerName]) {
      var logger = new Logger(loggerName);
      loggers[loggerName] = logger;
      loggerNames.push(loggerName);

      // Set up parent logger, if it doesn't exist
      var lastDotIndex = loggerName.lastIndexOf('.');
      var parentLogger;
      if (lastDotIndex > -1) {
        var parentLoggerName = loggerName.substring(0, lastDotIndex);
        parentLogger = log4javascript.getLogger(parentLoggerName); // Recursively sets up grandparents etc.
      } else {
        parentLogger = rootLogger;
      }
      parentLogger.addChild(logger);
    }
    return loggers[loggerName];
  };

  var defaultLogger = null;
  log4javascript.getDefaultLogger = function () {
    if (!defaultLogger) {
      defaultLogger = createDefaultLogger();
    }
    return defaultLogger;
  };

  var nullLogger = null;
  log4javascript.getNullLogger = function () {
    if (!nullLogger) {
      nullLogger = new Logger(nullLoggerName);
      nullLogger.setLevel(Level.OFF);
    }
    return nullLogger;
  };

  // Destroys all loggers
  log4javascript.resetConfiguration = function () {
    rootLogger.setLevel(ROOT_LOGGER_DEFAULT_LEVEL);
    loggers = {};
  };

  /* ---------------------------------------------------------------------- */
  // Logging events

  var LoggingEvent = function (logger, timeStamp, level, messages, exception) {
    this.logger = logger;
    this.timeStamp = timeStamp;
    this.timeStampInMilliseconds = timeStamp.getTime();
    this.timeStampInSeconds = Math.floor(this.timeStampInMilliseconds / 1000);
    this.milliseconds = this.timeStamp.getMilliseconds();
    this.level = level;
    this.messages = messages;
    this.exception = exception;
  };

  LoggingEvent.prototype = {
    getThrowableStrRep: function () {
      return this.exception ? getExceptionStringRep(this.exception) : '';
    },
    getCombinedMessages: function () {
      return this.messages.length == 1 ? this.messages[0] : this.messages.join(newLine);
    },
    toString: function () {
      return 'LoggingEvent[' + this.level + ']';
    }
  };

  log4javascript.LoggingEvent = LoggingEvent;

  /* ---------------------------------------------------------------------- */
  // Layout prototype

  var Layout = function () {};

  Layout.prototype = {
    defaults: {
      loggerKey: 'logger',
      timeStampKey: 'timestamp',
      millisecondsKey: 'milliseconds',
      levelKey: 'level',
      messageKey: 'message',
      exceptionKey: 'exception',
      urlKey: 'url'
    },
    loggerKey: 'logger',
    timeStampKey: 'timestamp',
    millisecondsKey: 'milliseconds',
    levelKey: 'level',
    messageKey: 'message',
    exceptionKey: 'exception',
    urlKey: 'url',
    batchHeader: '',
    batchFooter: '',
    batchSeparator: '',
    returnsPostData: false,
    overrideTimeStampsSetting: false,
    useTimeStampsInMilliseconds: null,

    format: function () {
      handleError('Layout.format: layout supplied has no format() method');
    },

    ignoresThrowable: function () {
      handleError('Layout.ignoresThrowable: layout supplied has no ignoresThrowable() method');
    },

    getContentType: function () {
      return 'text/plain';
    },

    allowBatching: function () {
      return true;
    },

    setTimeStampsInMilliseconds: function (timeStampsInMilliseconds) {
      this.overrideTimeStampsSetting = true;
      this.useTimeStampsInMilliseconds = bool(timeStampsInMilliseconds);
    },

    isTimeStampsInMilliseconds: function () {
      return this.overrideTimeStampsSetting ? this.useTimeStampsInMilliseconds : useTimeStampsInMilliseconds;
    },

    getTimeStampValue: function (loggingEvent) {
      return this.isTimeStampsInMilliseconds() ? loggingEvent.timeStampInMilliseconds : loggingEvent.timeStampInSeconds;
    },

    getDataValues: function (loggingEvent, combineMessages) {
      var dataValues = [
        [this.loggerKey, loggingEvent.logger.name],
        [this.timeStampKey, this.getTimeStampValue(loggingEvent)],
        [this.levelKey, loggingEvent.level.name],
        [this.urlKey, window.location.href],
        [this.messageKey, combineMessages ? loggingEvent.getCombinedMessages() : loggingEvent.messages]
      ];
      if (!this.isTimeStampsInMilliseconds()) {
        dataValues.push([this.millisecondsKey, loggingEvent.milliseconds]);
      }
      if (loggingEvent.exception) {
        dataValues.push([this.exceptionKey, getExceptionStringRep(loggingEvent.exception)]);
      }
      if (this.hasCustomFields()) {
        for (var i = 0, len = this.customFields.length; i < len; i++) {
          var val = this.customFields[i].value;

          // Check if the value is a function. If so, execute it, passing it the
          // current layout and the logging event
          if (typeof val === 'function') {
            val = val(this, loggingEvent);
          }
          dataValues.push([this.customFields[i].name, val]);
        }
      }
      return dataValues;
    },

    setKeys: function (loggerKey, timeStampKey, levelKey, messageKey, exceptionKey, urlKey, millisecondsKey) {
      this.loggerKey = extractStringFromParam(loggerKey, this.defaults.loggerKey);
      this.timeStampKey = extractStringFromParam(timeStampKey, this.defaults.timeStampKey);
      this.levelKey = extractStringFromParam(levelKey, this.defaults.levelKey);
      this.messageKey = extractStringFromParam(messageKey, this.defaults.messageKey);
      this.exceptionKey = extractStringFromParam(exceptionKey, this.defaults.exceptionKey);
      this.urlKey = extractStringFromParam(urlKey, this.defaults.urlKey);
      this.millisecondsKey = extractStringFromParam(millisecondsKey, this.defaults.millisecondsKey);
    },

    setCustomField: function (name, value) {
      var fieldUpdated = false;
      for (var i = 0, len = this.customFields.length; i < len; i++) {
        if (this.customFields[i].name === name) {
          this.customFields[i].value = value;
          fieldUpdated = true;
        }
      }
      if (!fieldUpdated) {
        this.customFields.push({ name: name, value: value });
      }
    },

    hasCustomFields: function () {
      return this.customFields.length > 0;
    },

    formatWithException: function (loggingEvent) {
      var formatted = this.format(loggingEvent);
      if (loggingEvent.exception && this.ignoresThrowable()) {
        formatted += loggingEvent.getThrowableStrRep();
      }
      return formatted;
    },

    toString: function () {
      handleError('Layout.toString: all layouts must override this method');
    }
  };

  log4javascript.Layout = Layout;

  /* ---------------------------------------------------------------------- */
  // Appender prototype

  var Appender = function () {};

  Appender.prototype = new EventSupport();

  Appender.prototype.layout = new PatternLayout();
  Appender.prototype.threshold = Level.ALL;
  Appender.prototype.loggers = [];

  // Performs threshold checks before delegating actual logging to the
  // subclass's specific append method.
  Appender.prototype.doAppend = function (loggingEvent) {
    if (enabled && loggingEvent.level.level >= this.threshold.level) {
      this.append(loggingEvent);
    }
  };

  Appender.prototype.append = function (loggingEvent) {};

  Appender.prototype.setLayout = function (layout) {
    if (layout instanceof Layout) {
      this.layout = layout;
    } else {
      handleError('Appender.setLayout: layout supplied to ' + this.toString() + ' is not a subclass of Layout');
    }
  };

  Appender.prototype.getLayout = function () {
    return this.layout;
  };

  Appender.prototype.setThreshold = function (threshold) {
    if (threshold instanceof Level) {
      this.threshold = threshold;
    } else {
      handleError('Appender.setThreshold: threshold supplied to ' + this.toString() + ' is not a subclass of Level');
    }
  };

  Appender.prototype.getThreshold = function () {
    return this.threshold;
  };

  Appender.prototype.setAddedToLogger = function (logger) {
    this.loggers.push(logger);
  };

  Appender.prototype.setRemovedFromLogger = function (logger) {
    array_remove(this.loggers, logger);
  };

  Appender.prototype.group = emptyFunction;
  Appender.prototype.groupEnd = emptyFunction;

  Appender.prototype.toString = function () {
    handleError('Appender.toString: all appenders must override this method');
  };

  log4javascript.Appender = Appender;

  /* ---------------------------------------------------------------------- */
  // SimpleLayout

  function SimpleLayout() {
    this.customFields = [];
  }

  SimpleLayout.prototype = new Layout();

  SimpleLayout.prototype.format = function (loggingEvent) {
    return loggingEvent.level.name + ' - ' + loggingEvent.getCombinedMessages();
  };

  SimpleLayout.prototype.ignoresThrowable = function () {
    return true;
  };

  SimpleLayout.prototype.toString = function () {
    return 'SimpleLayout';
  };

  log4javascript.SimpleLayout = SimpleLayout;
  /* ----------------------------------------------------------------------- */
  // NullLayout

  function NullLayout() {
    this.customFields = [];
  }

  NullLayout.prototype = new Layout();

  NullLayout.prototype.format = function (loggingEvent) {
    return loggingEvent.messages;
  };

  NullLayout.prototype.ignoresThrowable = function () {
    return true;
  };

  NullLayout.prototype.formatWithException = function (loggingEvent) {
    var messages = loggingEvent.messages,
      ex = loggingEvent.exception;
    return ex ? messages.concat([ex]) : messages;
  };

  NullLayout.prototype.toString = function () {
    return 'NullLayout';
  };

  log4javascript.NullLayout = NullLayout;
  /* ---------------------------------------------------------------------- */
  // XmlLayout

  function XmlLayout(combineMessages) {
    this.combineMessages = extractBooleanFromParam(combineMessages, true);
    this.customFields = [];
  }

  XmlLayout.prototype = new Layout();

  XmlLayout.prototype.isCombinedMessages = function () {
    return this.combineMessages;
  };

  XmlLayout.prototype.getContentType = function () {
    return 'text/xml';
  };

  XmlLayout.prototype.escapeCdata = function (str) {
    return str.replace(/\]\]>/, ']]>]]&gt;<![CDATA[');
  };

  XmlLayout.prototype.format = function (loggingEvent) {
    var layout = this;
    var i, len;
    function formatMessage(message) {
      message = typeof message === 'string' ? message : toStr(message);
      return '<log4javascript:message><![CDATA[' + layout.escapeCdata(message) + ']]></log4javascript:message>';
    }

    var str = '<log4javascript:event logger="' + loggingEvent.logger.name + '" timestamp="' + this.getTimeStampValue(loggingEvent) + '"';
    if (!this.isTimeStampsInMilliseconds()) {
      str += ' milliseconds="' + loggingEvent.milliseconds + '"';
    }
    str += ' level="' + loggingEvent.level.name + '">' + newLine;
    if (this.combineMessages) {
      str += formatMessage(loggingEvent.getCombinedMessages());
    } else {
      str += '<log4javascript:messages>' + newLine;
      for (i = 0, len = loggingEvent.messages.length; i < len; i++) {
        str += formatMessage(loggingEvent.messages[i]) + newLine;
      }
      str += '</log4javascript:messages>' + newLine;
    }
    if (this.hasCustomFields()) {
      for (i = 0, len = this.customFields.length; i < len; i++) {
        str +=
          '<log4javascript:customfield name="' +
          this.customFields[i].name +
          '"><![CDATA[' +
          this.customFields[i].value.toString() +
          ']]></log4javascript:customfield>' +
          newLine;
      }
    }
    if (loggingEvent.exception) {
      str += '<log4javascript:exception><![CDATA[' + getExceptionStringRep(loggingEvent.exception) + ']]></log4javascript:exception>' + newLine;
    }
    str += '</log4javascript:event>' + newLine + newLine;
    return str;
  };

  XmlLayout.prototype.ignoresThrowable = function () {
    return false;
  };

  XmlLayout.prototype.toString = function () {
    return 'XmlLayout';
  };

  log4javascript.XmlLayout = XmlLayout;
  /* ---------------------------------------------------------------------- */
  // JsonLayout related

  function escapeNewLines(str) {
    return str.replace(/\r\n|\r|\n/g, '\\r\\n');
  }

  function JsonLayout(readable, combineMessages) {
    this.readable = extractBooleanFromParam(readable, false);
    this.combineMessages = extractBooleanFromParam(combineMessages, true);
    this.batchHeader = this.readable ? '[' + newLine : '[';
    this.batchFooter = this.readable ? ']' + newLine : ']';
    this.batchSeparator = this.readable ? ',' + newLine : ',';
    this.setKeys();
    this.colon = this.readable ? ': ' : ':';
    this.tab = this.readable ? '\t' : '';
    this.lineBreak = this.readable ? newLine : '';
    this.customFields = [];
  }

  /* ---------------------------------------------------------------------- */
  // JsonLayout

  JsonLayout.prototype = new Layout();

  JsonLayout.prototype.isReadable = function () {
    return this.readable;
  };

  JsonLayout.prototype.isCombinedMessages = function () {
    return this.combineMessages;
  };

  JsonLayout.prototype.format = function (loggingEvent) {
    var layout = this;
    var dataValues = this.getDataValues(loggingEvent, this.combineMessages);
    var str = '{' + this.lineBreak;
    var i, len;

    function formatValue(val, prefix, expand) {
      // Check the type of the data value to decide whether quotation marks
      // or expansion are required
      var formattedValue;
      var valType = typeof val;
      if (val instanceof Date) {
        formattedValue = String(val.getTime());
      } else if (expand && val instanceof Array) {
        formattedValue = '[' + layout.lineBreak;
        for (var i = 0, len = val.length; i < len; i++) {
          var childPrefix = prefix + layout.tab;
          formattedValue += childPrefix + formatValue(val[i], childPrefix, false);
          if (i < val.length - 1) {
            formattedValue += ',';
          }
          formattedValue += layout.lineBreak;
        }
        formattedValue += prefix + ']';
      } else if (valType !== 'number' && valType !== 'boolean') {
        formattedValue = '"' + escapeNewLines(toStr(val).replace(/\"/g, '\\"')) + '"';
      } else {
        formattedValue = val;
      }
      return formattedValue;
    }

    for (i = 0, len = dataValues.length - 1; i <= len; i++) {
      str += this.tab + '"' + dataValues[i][0] + '"' + this.colon + formatValue(dataValues[i][1], this.tab, true);
      if (i < len) {
        str += ',';
      }
      str += this.lineBreak;
    }

    str += '}' + this.lineBreak;
    return str;
  };

  JsonLayout.prototype.ignoresThrowable = function () {
    return false;
  };

  JsonLayout.prototype.toString = function () {
    return 'JsonLayout';
  };

  JsonLayout.prototype.getContentType = function () {
    return 'application/json';
  };

  log4javascript.JsonLayout = JsonLayout;
  /* ---------------------------------------------------------------------- */
  // HttpPostDataLayout

  function HttpPostDataLayout() {
    this.setKeys();
    this.customFields = [];
    this.returnsPostData = true;
  }

  HttpPostDataLayout.prototype = new Layout();

  // Disable batching
  HttpPostDataLayout.prototype.allowBatching = function () {
    return false;
  };

  HttpPostDataLayout.prototype.format = function (loggingEvent) {
    var dataValues = this.getDataValues(loggingEvent);
    var queryBits = [];
    for (var i = 0, len = dataValues.length; i < len; i++) {
      var val = dataValues[i][1] instanceof Date ? String(dataValues[i][1].getTime()) : dataValues[i][1];
      queryBits.push(urlEncode(dataValues[i][0]) + '=' + urlEncode(val));
    }
    return queryBits.join('&');
  };

  HttpPostDataLayout.prototype.ignoresThrowable = function (loggingEvent) {
    return false;
  };

  HttpPostDataLayout.prototype.toString = function () {
    return 'HttpPostDataLayout';
  };

  log4javascript.HttpPostDataLayout = HttpPostDataLayout;
  /* ---------------------------------------------------------------------- */
  // formatObjectExpansion

  function formatObjectExpansion(obj, depth, indentation) {
    var objectsExpanded = [];

    function doFormat(obj, depth, indentation) {
      var i, len, childDepth, childIndentation, childLines, expansion, childExpansion;

      if (!indentation) {
        indentation = '';
      }

      function formatString(text) {
        var lines = splitIntoLines(text);
        for (var j = 1, jLen = lines.length; j < jLen; j++) {
          lines[j] = indentation + lines[j];
        }
        return lines.join(newLine);
      }

      if (obj === null) {
        return 'null';
      } else if (typeof obj == 'undefined') {
        return 'undefined';
      } else if (typeof obj == 'string') {
        return formatString(obj);
      } else if (typeof obj == 'object' && array_contains(objectsExpanded, obj)) {
        try {
          expansion = toStr(obj);
        } catch (ex) {
          expansion = 'Error formatting property. Details: ' + getExceptionStringRep(ex);
        }
        return expansion + ' [already expanded]';
      } else if (obj instanceof Array && depth > 0) {
        objectsExpanded.push(obj);
        expansion = '[' + newLine;
        childDepth = depth - 1;
        childIndentation = indentation + '  ';
        childLines = [];
        for (i = 0, len = obj.length; i < len; i++) {
          try {
            childExpansion = doFormat(obj[i], childDepth, childIndentation);
            childLines.push(childIndentation + childExpansion);
          } catch (ex) {
            childLines.push(childIndentation + 'Error formatting array member. Details: ' + getExceptionStringRep(ex) + '');
          }
        }
        expansion += childLines.join(',' + newLine) + newLine + indentation + ']';
        return expansion;
      } else if (Object.prototype.toString.call(obj) == '[object Date]') {
        return obj.toString();
      } else if (typeof obj == 'object' && depth > 0) {
        objectsExpanded.push(obj);
        expansion = '{' + newLine;
        childDepth = depth - 1;
        childIndentation = indentation + '  ';
        childLines = [];
        for (i in obj) {
          try {
            childExpansion = doFormat(obj[i], childDepth, childIndentation);
            childLines.push(childIndentation + i + ': ' + childExpansion);
          } catch (ex) {
            childLines.push(childIndentation + i + ': Error formatting property. Details: ' + getExceptionStringRep(ex));
          }
        }
        expansion += childLines.join(',' + newLine) + newLine + indentation + '}';
        return expansion;
      } else {
        return formatString(toStr(obj));
      }
    }
    return doFormat(obj, depth, indentation);
  }
  /* ---------------------------------------------------------------------- */
  // Date-related stuff

  var SimpleDateFormat;

  (function () {
    var regex = /('[^']*')|(G+|y+|M+|w+|W+|D+|d+|F+|E+|a+|H+|k+|K+|h+|m+|s+|S+|Z+)|([a-zA-Z]+)|([^a-zA-Z']+)/;
    var monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    var dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var TEXT2 = 0,
      TEXT3 = 1,
      NUMBER = 2,
      YEAR = 3,
      MONTH = 4,
      TIMEZONE = 5;
    var types = {
      G: TEXT2,
      y: YEAR,
      M: MONTH,
      w: NUMBER,
      W: NUMBER,
      D: NUMBER,
      d: NUMBER,
      F: NUMBER,
      E: TEXT3,
      a: TEXT2,
      H: NUMBER,
      k: NUMBER,
      K: NUMBER,
      h: NUMBER,
      m: NUMBER,
      s: NUMBER,
      S: NUMBER,
      Z: TIMEZONE
    };
    var ONE_DAY = 24 * 60 * 60 * 1000;
    var ONE_WEEK = 7 * ONE_DAY;
    var DEFAULT_MINIMAL_DAYS_IN_FIRST_WEEK = 1;

    var newDateAtMidnight = function (year, month, day) {
      var d = new Date(year, month, day, 0, 0, 0);
      d.setMilliseconds(0);
      return d;
    };

    Date.prototype.getDifference = function (date) {
      return this.getTime() - date.getTime();
    };

    Date.prototype.isBefore = function (d) {
      return this.getTime() < d.getTime();
    };

    Date.prototype.getUTCTime = function () {
      return Date.UTC(this.getFullYear(), this.getMonth(), this.getDate(), this.getHours(), this.getMinutes(), this.getSeconds(), this.getMilliseconds());
    };

    Date.prototype.getTimeSince = function (d) {
      return this.getUTCTime() - d.getUTCTime();
    };

    Date.prototype.getPreviousSunday = function () {
      // Using midday avoids any possibility of DST messing things up
      var midday = new Date(this.getFullYear(), this.getMonth(), this.getDate(), 12, 0, 0);
      var previousSunday = new Date(midday.getTime() - this.getDay() * ONE_DAY);
      return newDateAtMidnight(previousSunday.getFullYear(), previousSunday.getMonth(), previousSunday.getDate());
    };

    Date.prototype.getWeekInYear = function (minimalDaysInFirstWeek) {
      if (isUndefined(this.minimalDaysInFirstWeek)) {
        minimalDaysInFirstWeek = DEFAULT_MINIMAL_DAYS_IN_FIRST_WEEK;
      }
      var previousSunday = this.getPreviousSunday();
      var startOfYear = newDateAtMidnight(this.getFullYear(), 0, 1);
      var numberOfSundays = previousSunday.isBefore(startOfYear) ? 0 : 1 + Math.floor(previousSunday.getTimeSince(startOfYear) / ONE_WEEK);
      var numberOfDaysInFirstWeek = 7 - startOfYear.getDay();
      var weekInYear = numberOfSundays;
      if (numberOfDaysInFirstWeek < minimalDaysInFirstWeek) {
        weekInYear--;
      }
      return weekInYear;
    };

    Date.prototype.getWeekInMonth = function (minimalDaysInFirstWeek) {
      if (isUndefined(this.minimalDaysInFirstWeek)) {
        minimalDaysInFirstWeek = DEFAULT_MINIMAL_DAYS_IN_FIRST_WEEK;
      }
      var previousSunday = this.getPreviousSunday();
      var startOfMonth = newDateAtMidnight(this.getFullYear(), this.getMonth(), 1);
      var numberOfSundays = previousSunday.isBefore(startOfMonth) ? 0 : 1 + Math.floor(previousSunday.getTimeSince(startOfMonth) / ONE_WEEK);
      var numberOfDaysInFirstWeek = 7 - startOfMonth.getDay();
      var weekInMonth = numberOfSundays;
      if (numberOfDaysInFirstWeek >= minimalDaysInFirstWeek) {
        weekInMonth++;
      }
      return weekInMonth;
    };

    Date.prototype.getDayInYear = function () {
      var startOfYear = newDateAtMidnight(this.getFullYear(), 0, 1);
      return 1 + Math.floor(this.getTimeSince(startOfYear) / ONE_DAY);
    };

    /* ------------------------------------------------------------------ */

    SimpleDateFormat = function (formatString) {
      this.formatString = formatString;
    };

    /**
     * Sets the minimum number of days in a week in order for that week to
     * be considered as belonging to a particular month or year
     */
    SimpleDateFormat.prototype.setMinimalDaysInFirstWeek = function (days) {
      this.minimalDaysInFirstWeek = days;
    };

    SimpleDateFormat.prototype.getMinimalDaysInFirstWeek = function () {
      return isUndefined(this.minimalDaysInFirstWeek) ? DEFAULT_MINIMAL_DAYS_IN_FIRST_WEEK : this.minimalDaysInFirstWeek;
    };

    var padWithZeroes = function (str, len) {
      while (str.length < len) {
        str = '0' + str;
      }
      return str;
    };

    var formatText = function (data, numberOfLetters, minLength) {
      return numberOfLetters >= 4 ? data : data.substr(0, Math.max(minLength, numberOfLetters));
    };

    var formatNumber = function (data, numberOfLetters) {
      var dataString = '' + data;
      // Pad with 0s as necessary
      return padWithZeroes(dataString, numberOfLetters);
    };

    SimpleDateFormat.prototype.format = function (date) {
      var formattedString = '';
      var result;
      var searchString = this.formatString;
      while ((result = regex.exec(searchString))) {
        var quotedString = result[1];
        var patternLetters = result[2];
        var otherLetters = result[3];
        var otherCharacters = result[4];

        // If the pattern matched is quoted string, output the text between the quotes
        if (quotedString) {
          if (quotedString == "''") {
            formattedString += "'";
          } else {
            formattedString += quotedString.substring(1, quotedString.length - 1);
          }
        } else if (otherLetters) {
          // Swallow non-pattern letters by doing nothing here
        } else if (otherCharacters) {
          // Simply output other characters
          formattedString += otherCharacters;
        } else if (patternLetters) {
          // Replace pattern letters
          var patternLetter = patternLetters.charAt(0);
          var numberOfLetters = patternLetters.length;
          var rawData = '';
          switch (patternLetter) {
            case 'G':
              rawData = 'AD';
              break;
            case 'y':
              rawData = date.getFullYear();
              break;
            case 'M':
              rawData = date.getMonth();
              break;
            case 'w':
              rawData = date.getWeekInYear(this.getMinimalDaysInFirstWeek());
              break;
            case 'W':
              rawData = date.getWeekInMonth(this.getMinimalDaysInFirstWeek());
              break;
            case 'D':
              rawData = date.getDayInYear();
              break;
            case 'd':
              rawData = date.getDate();
              break;
            case 'F':
              rawData = 1 + Math.floor((date.getDate() - 1) / 7);
              break;
            case 'E':
              rawData = dayNames[date.getDay()];
              break;
            case 'a':
              rawData = date.getHours() >= 12 ? 'PM' : 'AM';
              break;
            case 'H':
              rawData = date.getHours();
              break;
            case 'k':
              rawData = date.getHours() || 24;
              break;
            case 'K':
              rawData = date.getHours() % 12;
              break;
            case 'h':
              rawData = date.getHours() % 12 || 12;
              break;
            case 'm':
              rawData = date.getMinutes();
              break;
            case 's':
              rawData = date.getSeconds();
              break;
            case 'S':
              rawData = date.getMilliseconds();
              break;
            case 'Z':
              rawData = date.getTimezoneOffset(); // This returns the number of minutes since GMT was this time.
              break;
          }
          // Format the raw data depending on the type
          switch (types[patternLetter]) {
            case TEXT2:
              formattedString += formatText(rawData, numberOfLetters, 2);
              break;
            case TEXT3:
              formattedString += formatText(rawData, numberOfLetters, 3);
              break;
            case NUMBER:
              formattedString += formatNumber(rawData, numberOfLetters);
              break;
            case YEAR:
              if (numberOfLetters <= 3) {
                // Output a 2-digit year
                var dataString = '' + rawData;
                formattedString += dataString.substr(2, 2);
              } else {
                formattedString += formatNumber(rawData, numberOfLetters);
              }
              break;
            case MONTH:
              if (numberOfLetters >= 3) {
                formattedString += formatText(monthNames[rawData], numberOfLetters, numberOfLetters);
              } else {
                // NB. Months returned by getMonth are zero-based
                formattedString += formatNumber(rawData + 1, numberOfLetters);
              }
              break;
            case TIMEZONE:
              var isPositive = rawData > 0;
              // The following line looks like a mistake but isn't
              // because of the way getTimezoneOffset measures.
              var prefix = isPositive ? '-' : '+';
              var absData = Math.abs(rawData);

              // Hours
              var hours = '' + Math.floor(absData / 60);
              hours = padWithZeroes(hours, 2);
              // Minutes
              var minutes = '' + (absData % 60);
              minutes = padWithZeroes(minutes, 2);

              formattedString += prefix + hours + minutes;
              break;
          }
        }
        searchString = searchString.substr(result.index + result[0].length);
      }
      return formattedString;
    };
  })();

  log4javascript.SimpleDateFormat = SimpleDateFormat;

  /* ---------------------------------------------------------------------- */
  // PatternLayout

  function PatternLayout(pattern) {
    if (pattern) {
      this.pattern = pattern;
    } else {
      this.pattern = PatternLayout.DEFAULT_CONVERSION_PATTERN;
    }
    this.customFields = [];
  }

  PatternLayout.TTCC_CONVERSION_PATTERN = '%r %p %c - %m%n';
  PatternLayout.DEFAULT_CONVERSION_PATTERN = '%m%n';
  PatternLayout.ISO8601_DATEFORMAT = 'yyyy-MM-dd HH:mm:ss,SSS';
  PatternLayout.DATETIME_DATEFORMAT = 'dd MMM yyyy HH:mm:ss,SSS';
  PatternLayout.ABSOLUTETIME_DATEFORMAT = 'HH:mm:ss,SSS';

  PatternLayout.prototype = new Layout();

  PatternLayout.prototype.format = function (loggingEvent) {
    var regex = /%(-?[0-9]+)?(\.?[0-9]+)?([acdfmMnpr%])(\{([^\}]+)\})?|([^%]+)/;
    var formattedString = '';
    var result;
    var searchString = this.pattern;

    // Cannot use regex global flag since it doesn't work with exec in IE5
    while ((result = regex.exec(searchString))) {
      var matchedString = result[0];
      var padding = result[1];
      var truncation = result[2];
      var conversionCharacter = result[3];
      var specifier = result[5];
      var text = result[6];

      // Check if the pattern matched was just normal text
      if (text) {
        formattedString += '' + text;
      } else {
        // Create a raw replacement string based on the conversion
        // character and specifier
        var replacement = '';
        switch (conversionCharacter) {
          case 'a': // Array of messages
          case 'm': // Message
            var depth = 0;
            if (specifier) {
              depth = parseInt(specifier, 10);
              if (isNaN(depth)) {
                handleError(
                  "PatternLayout.format: invalid specifier '" + specifier + "' for conversion character '" + conversionCharacter + "' - should be a number"
                );
                depth = 0;
              }
            }
            var messages = conversionCharacter === 'a' ? loggingEvent.messages[0] : loggingEvent.messages;
            for (var i = 0, len = messages.length; i < len; i++) {
              if (i > 0 && replacement.charAt(replacement.length - 1) !== ' ') {
                replacement += ' ';
              }
              if (depth === 0) {
                replacement += messages[i];
              } else {
                replacement += formatObjectExpansion(messages[i], depth);
              }
            }
            break;
          case 'c': // Logger name
            var loggerName = loggingEvent.logger.name;
            if (specifier) {
              var precision = parseInt(specifier, 10);
              var loggerNameBits = loggingEvent.logger.name.split('.');
              if (precision >= loggerNameBits.length) {
                replacement = loggerName;
              } else {
                replacement = loggerNameBits.slice(loggerNameBits.length - precision).join('.');
              }
            } else {
              replacement = loggerName;
            }
            break;
          case 'd': // Date
            var dateFormat = PatternLayout.ISO8601_DATEFORMAT;
            if (specifier) {
              dateFormat = specifier;
              // Pick up special cases
              if (dateFormat == 'ISO8601') {
                dateFormat = PatternLayout.ISO8601_DATEFORMAT;
              } else if (dateFormat == 'ABSOLUTE') {
                dateFormat = PatternLayout.ABSOLUTETIME_DATEFORMAT;
              } else if (dateFormat == 'DATE') {
                dateFormat = PatternLayout.DATETIME_DATEFORMAT;
              }
            }
            // Format the date
            replacement = new SimpleDateFormat(dateFormat).format(loggingEvent.timeStamp);
            break;
          case 'f': // Custom field
            if (this.hasCustomFields()) {
              var fieldIndex = 0;
              if (specifier) {
                fieldIndex = parseInt(specifier, 10);
                if (isNaN(fieldIndex)) {
                  handleError("PatternLayout.format: invalid specifier '" + specifier + "' for conversion character 'f' - should be a number");
                } else if (fieldIndex === 0) {
                  handleError("PatternLayout.format: invalid specifier '" + specifier + "' for conversion character 'f' - must be greater than zero");
                } else if (fieldIndex > this.customFields.length) {
                  handleError(
                    "PatternLayout.format: invalid specifier '" + specifier + "' for conversion character 'f' - there aren't that many custom fields"
                  );
                } else {
                  fieldIndex = fieldIndex - 1;
                }
              }
              var val = this.customFields[fieldIndex].value;
              if (typeof val == 'function') {
                val = val(this, loggingEvent);
              }
              replacement = val;
            }
            break;
          case 'n': // New line
            replacement = newLine;
            break;
          case 'p': // Level
            replacement = loggingEvent.level.name;
            break;
          case 'r': // Milliseconds since log4javascript startup
            replacement = '' + loggingEvent.timeStamp.getDifference(applicationStartDate);
            break;
          case '%': // Literal % sign
            replacement = '%';
            break;
          default:
            replacement = matchedString;
            break;
        }
        // Format the replacement according to any padding or
        // truncation specified
        var l;

        // First, truncation
        if (truncation) {
          l = parseInt(truncation.substr(1), 10);
          var strLen = replacement.length;
          if (l < strLen) {
            replacement = replacement.substring(strLen - l, strLen);
          }
        }
        // Next, padding
        if (padding) {
          if (padding.charAt(0) == '-') {
            l = parseInt(padding.substr(1), 10);
            // Right pad with spaces
            while (replacement.length < l) {
              replacement += ' ';
            }
          } else {
            l = parseInt(padding, 10);
            // Left pad with spaces
            while (replacement.length < l) {
              replacement = ' ' + replacement;
            }
          }
        }
        formattedString += replacement;
      }
      searchString = searchString.substr(result.index + result[0].length);
    }
    return formattedString;
  };

  PatternLayout.prototype.ignoresThrowable = function () {
    return true;
  };

  PatternLayout.prototype.toString = function () {
    return 'PatternLayout';
  };

  log4javascript.PatternLayout = PatternLayout;
  /* ---------------------------------------------------------------------- */
  // AlertAppender

  function AlertAppender() {}

  AlertAppender.prototype = new Appender();

  AlertAppender.prototype.layout = new SimpleLayout();

  AlertAppender.prototype.append = function (loggingEvent) {
    alert(this.getLayout().formatWithException(loggingEvent));
  };

  AlertAppender.prototype.toString = function () {
    return 'AlertAppender';
  };

  log4javascript.AlertAppender = AlertAppender;
  /* ---------------------------------------------------------------------- */
  // BrowserConsoleAppender (only works in Opera and Safari and Firefox with
  // Firebug extension)

  function BrowserConsoleAppender() {}

  BrowserConsoleAppender.prototype = new log4javascript.Appender();
  BrowserConsoleAppender.prototype.layout = new NullLayout();
  BrowserConsoleAppender.prototype.threshold = Level.DEBUG;

  BrowserConsoleAppender.prototype.append = function (loggingEvent) {
    var appender = this;

    var getFormattedMessage = function (concatenate) {
      var formattedMessage = appender.getLayout().formatWithException(loggingEvent);
      return typeof formattedMessage == 'string'
        ? concatenate
          ? formattedMessage
          : [formattedMessage]
        : concatenate
          ? formattedMessage.join(' ')
          : formattedMessage;
    };

    var console = window.console;

    if (console && console.log) {
      // Log to Firebug or the browser console using specific logging
      // methods or revert to console.log otherwise
      var consoleMethodName;

      if (console.debug && Level.DEBUG.isGreaterOrEqual(loggingEvent.level)) {
        consoleMethodName = 'debug';
      } else if (console.info && Level.INFO.equals(loggingEvent.level)) {
        consoleMethodName = 'info';
      } else if (console.warn && Level.WARN.equals(loggingEvent.level)) {
        consoleMethodName = 'warn';
      } else if (console.error && loggingEvent.level.isGreaterOrEqual(Level.ERROR)) {
        consoleMethodName = 'error';
      } else {
        consoleMethodName = 'log';
      }

      if (typeof console[consoleMethodName].apply == 'function') {
        console[consoleMethodName].apply(console, getFormattedMessage(false));
      } else {
        console[consoleMethodName](getFormattedMessage(true));
      }
    } else if (typeof opera != 'undefined' && opera.postError) {
      // Opera
      opera.postError(getFormattedMessage(true));
    }
  };

  BrowserConsoleAppender.prototype.group = function (name) {
    if (window.console && window.console.group) {
      window.console.group(name);
    }
  };

  BrowserConsoleAppender.prototype.groupEnd = function () {
    if (window.console && window.console.groupEnd) {
      window.console.groupEnd();
    }
  };

  BrowserConsoleAppender.prototype.toString = function () {
    return 'BrowserConsoleAppender';
  };

  log4javascript.BrowserConsoleAppender = BrowserConsoleAppender;
  /* ---------------------------------------------------------------------- */
  // AjaxAppender related

  var xhrFactory = function () {
    return new XMLHttpRequest();
  };
  var xmlHttpFactories = [
    xhrFactory,
    function () {
      return new ActiveXObject('Msxml2.XMLHTTP');
    },
    function () {
      return new ActiveXObject('Microsoft.XMLHTTP');
    }
  ];

  var withCredentialsSupported = false;
  var getXmlHttp = function (errorHandler) {
    // This is only run the first time; the value of getXmlHttp gets
    // replaced with the factory that succeeds on the first run
    var xmlHttp = null,
      factory;
    for (var i = 0, len = xmlHttpFactories.length; i < len; i++) {
      factory = xmlHttpFactories[i];
      try {
        xmlHttp = factory();
        withCredentialsSupported = factory == xhrFactory && 'withCredentials' in xmlHttp;
        getXmlHttp = factory;
        return xmlHttp;
      } catch (e) {}
    }
    // If we're here, all factories have failed, so throw an error
    if (errorHandler) {
      errorHandler();
    } else {
      handleError('getXmlHttp: unable to obtain XMLHttpRequest object');
    }
  };

  function isHttpRequestSuccessful(xmlHttp) {
    return isUndefined(xmlHttp.status) || xmlHttp.status === 0 || (xmlHttp.status >= 200 && xmlHttp.status < 300) || xmlHttp.status == 1223 /* Fix for IE */;
  }

  /* ---------------------------------------------------------------------- */
  // AjaxAppender

  function AjaxAppender(url, withCredentials) {
    var appender = this;
    var isSupported = true;
    if (!url) {
      handleError('AjaxAppender: URL must be specified in constructor');
      isSupported = false;
    }

    var timed = this.defaults.timed;
    var waitForResponse = this.defaults.waitForResponse;
    var batchSize = this.defaults.batchSize;
    var timerInterval = this.defaults.timerInterval;
    var requestSuccessCallback = this.defaults.requestSuccessCallback;
    var failCallback = this.defaults.failCallback;
    var postVarName = this.defaults.postVarName;
    var sendAllOnUnload = this.defaults.sendAllOnUnload;
    var contentType = this.defaults.contentType;
    var sessionId = null;

    var queuedLoggingEvents = [];
    var queuedRequests = [];
    var headers = [];
    var sending = false;
    var initialized = false;

    // Configuration methods. The function scope is used to prevent
    // direct alteration to the appender configuration properties.
    function checkCanConfigure(configOptionName) {
      if (initialized) {
        handleError("AjaxAppender: configuration option '" + configOptionName + "' may not be set after the appender has been initialized");
        return false;
      }
      return true;
    }

    this.getSessionId = function () {
      return sessionId;
    };
    this.setSessionId = function (sessionIdParam) {
      sessionId = extractStringFromParam(sessionIdParam, null);
      this.layout.setCustomField('sessionid', sessionId);
    };

    this.setLayout = function (layoutParam) {
      if (checkCanConfigure('layout')) {
        this.layout = layoutParam;
        // Set the session id as a custom field on the layout, if not already present
        if (sessionId !== null) {
          this.setSessionId(sessionId);
        }
      }
    };

    this.isTimed = function () {
      return timed;
    };
    this.setTimed = function (timedParam) {
      if (checkCanConfigure('timed')) {
        timed = bool(timedParam);
      }
    };

    this.getTimerInterval = function () {
      return timerInterval;
    };
    this.setTimerInterval = function (timerIntervalParam) {
      if (checkCanConfigure('timerInterval')) {
        timerInterval = extractIntFromParam(timerIntervalParam, timerInterval);
      }
    };

    this.isWaitForResponse = function () {
      return waitForResponse;
    };
    this.setWaitForResponse = function (waitForResponseParam) {
      if (checkCanConfigure('waitForResponse')) {
        waitForResponse = bool(waitForResponseParam);
      }
    };

    this.getBatchSize = function () {
      return batchSize;
    };
    this.setBatchSize = function (batchSizeParam) {
      if (checkCanConfigure('batchSize')) {
        batchSize = extractIntFromParam(batchSizeParam, batchSize);
      }
    };

    this.isSendAllOnUnload = function () {
      return sendAllOnUnload;
    };
    this.setSendAllOnUnload = function (sendAllOnUnloadParam) {
      if (checkCanConfigure('sendAllOnUnload')) {
        sendAllOnUnload = extractBooleanFromParam(sendAllOnUnloadParam, sendAllOnUnload);
      }
    };

    this.setRequestSuccessCallback = function (requestSuccessCallbackParam) {
      requestSuccessCallback = extractFunctionFromParam(requestSuccessCallbackParam, requestSuccessCallback);
    };

    this.setFailCallback = function (failCallbackParam) {
      failCallback = extractFunctionFromParam(failCallbackParam, failCallback);
    };

    this.getPostVarName = function () {
      return postVarName;
    };
    this.setPostVarName = function (postVarNameParam) {
      if (checkCanConfigure('postVarName')) {
        postVarName = extractStringFromParam(postVarNameParam, postVarName);
      }
    };

    this.getHeaders = function () {
      return headers;
    };
    this.addHeader = function (name, value) {
      if (name.toLowerCase() == 'content-type') {
        contentType = value;
      } else {
        headers.push({ name: name, value: value });
      }
    };

    // Internal functions
    function sendAll() {
      if (isSupported && enabled) {
        sending = true;
        var currentRequestBatch;
        if (waitForResponse) {
          // Send the first request then use this function as the callback once
          // the response comes back
          if (queuedRequests.length > 0) {
            currentRequestBatch = queuedRequests.shift();
            sendRequest(preparePostData(currentRequestBatch), sendAll);
          } else {
            sending = false;
            if (timed) {
              scheduleSending();
            }
          }
        } else {
          // Rattle off all the requests without waiting to see the response
          while ((currentRequestBatch = queuedRequests.shift())) {
            sendRequest(preparePostData(currentRequestBatch));
          }
          sending = false;
          if (timed) {
            scheduleSending();
          }
        }
      }
    }

    this.sendAll = sendAll;

    // Called when the window unloads. At this point we're past caring about
    // waiting for responses or timers or incomplete batches - everything
    // must go, now
    function sendAllRemaining() {
      var sendingAnything = false;
      if (isSupported && enabled) {
        // Create requests for everything left over, batched as normal
        var actualBatchSize = appender.getLayout().allowBatching() ? batchSize : 1;
        var currentLoggingEvent;
        var batchedLoggingEvents = [];
        while ((currentLoggingEvent = queuedLoggingEvents.shift())) {
          batchedLoggingEvents.push(currentLoggingEvent);
          if (queuedLoggingEvents.length >= actualBatchSize) {
            // Queue this batch of log entries
            queuedRequests.push(batchedLoggingEvents);
            batchedLoggingEvents = [];
          }
        }
        // If there's a partially completed batch, add it
        if (batchedLoggingEvents.length > 0) {
          queuedRequests.push(batchedLoggingEvents);
        }
        sendingAnything = queuedRequests.length > 0;
        waitForResponse = false;
        timed = false;
        sendAll();
      }
      return sendingAnything;
    }

    this.sendAllRemaining = sendAllRemaining;

    function preparePostData(batchedLoggingEvents) {
      // Format the logging events
      var formattedMessages = [];
      var currentLoggingEvent;
      var postData = '';
      while ((currentLoggingEvent = batchedLoggingEvents.shift())) {
        formattedMessages.push(appender.getLayout().formatWithException(currentLoggingEvent));
      }
      // Create the post data string
      if (batchedLoggingEvents.length == 1) {
        postData = formattedMessages.join('');
      } else {
        postData = appender.getLayout().batchHeader + formattedMessages.join(appender.getLayout().batchSeparator) + appender.getLayout().batchFooter;
      }
      if (contentType == appender.defaults.contentType) {
        postData = appender.getLayout().returnsPostData ? postData : urlEncode(postVarName) + '=' + urlEncode(postData);
        // Add the layout name to the post data
        if (postData.length > 0) {
          postData += '&';
        }
        postData += 'layout=' + urlEncode(appender.getLayout().toString());
      }
      return postData;
    }

    function scheduleSending() {
      window.setTimeout(sendAll, timerInterval);
    }

    function xmlHttpErrorHandler() {
      var msg = 'AjaxAppender: could not create XMLHttpRequest object. AjaxAppender disabled';
      handleError(msg);
      isSupported = false;
      if (failCallback) {
        failCallback(msg);
      }
    }

    function sendRequest(postData, successCallback) {
      try {
        var xmlHttp = getXmlHttp(xmlHttpErrorHandler);
        if (isSupported) {
          xmlHttp.onreadystatechange = function () {
            if (xmlHttp.readyState == 4) {
              if (isHttpRequestSuccessful(xmlHttp)) {
                if (requestSuccessCallback) {
                  requestSuccessCallback(xmlHttp);
                }
                if (successCallback) {
                  successCallback(xmlHttp);
                }
              } else {
                var msg = 'AjaxAppender.append: XMLHttpRequest request to URL ' + url + ' returned status code ' + xmlHttp.status;
                handleError(msg);
                if (failCallback) {
                  failCallback(msg);
                }
              }
              xmlHttp.onreadystatechange = emptyFunction;
              xmlHttp = null;
            }
          };
          xmlHttp.open('POST', url, true);
          // Add withCredentials to facilitate CORS requests with cookies
          if (withCredentials && withCredentialsSupported) {
            xmlHttp.withCredentials = true;
          }
          try {
            for (var i = 0, header; (header = headers[i++]); ) {
              xmlHttp.setRequestHeader(header.name, header.value);
            }
            xmlHttp.setRequestHeader('Content-Type', contentType);
          } catch (headerEx) {
            var msg =
              "AjaxAppender.append: your browser's XMLHttpRequest implementation" +
              ' does not support setRequestHeader, therefore cannot post data. AjaxAppender disabled';
            handleError(msg);
            isSupported = false;
            if (failCallback) {
              failCallback(msg);
            }
            return;
          }
          xmlHttp.send(postData);
        }
      } catch (ex) {
        var errMsg = 'AjaxAppender.append: error sending log message to ' + url;
        handleError(errMsg, ex);
        isSupported = false;
        if (failCallback) {
          failCallback(errMsg + '. Details: ' + getExceptionStringRep(ex));
        }
      }
    }

    this.append = function (loggingEvent) {
      if (isSupported) {
        if (!initialized) {
          init();
        }
        queuedLoggingEvents.push(loggingEvent);
        var actualBatchSize = this.getLayout().allowBatching() ? batchSize : 1;

        if (queuedLoggingEvents.length >= actualBatchSize) {
          var currentLoggingEvent;
          var batchedLoggingEvents = [];
          while ((currentLoggingEvent = queuedLoggingEvents.shift())) {
            batchedLoggingEvents.push(currentLoggingEvent);
          }
          // Queue this batch of log entries
          queuedRequests.push(batchedLoggingEvents);

          // If using a timer, the queue of requests will be processed by the
          // timer function, so nothing needs to be done here.
          if (!timed && (!waitForResponse || (waitForResponse && !sending))) {
            sendAll();
          }
        }
      }
    };

    function init() {
      initialized = true;
      // Add unload event to send outstanding messages
      if (sendAllOnUnload) {
        var oldBeforeUnload = window.onbeforeunload;
        window.onbeforeunload = function () {
          if (oldBeforeUnload) {
            oldBeforeUnload();
          }
          sendAllRemaining();
        };
      }
      // Start timer
      if (timed) {
        scheduleSending();
      }
    }
  }

  AjaxAppender.prototype = new Appender();

  AjaxAppender.prototype.defaults = {
    waitForResponse: false,
    timed: false,
    timerInterval: 1000,
    batchSize: 1,
    sendAllOnUnload: false,
    requestSuccessCallback: null,
    failCallback: null,
    postVarName: 'data',
    contentType: 'application/x-www-form-urlencoded'
  };

  AjaxAppender.prototype.layout = new HttpPostDataLayout();

  AjaxAppender.prototype.toString = function () {
    return 'AjaxAppender';
  };

  log4javascript.AjaxAppender = AjaxAppender;
  /* ---------------------------------------------------------------------- */
  // PopUpAppender and InPageAppender related

  function setCookie(name, value, days, path) {
    var expires;
    path = path ? '; path=' + path : '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toGMTString();
    } else {
      expires = '';
    }
    document.cookie = escape(name) + '=' + escape(value) + expires + path;
  }

  function getCookie(name) {
    var nameEquals = escape(name) + '=';
    var ca = document.cookie.split(';');
    for (var i = 0, len = ca.length; i < len; i++) {
      var c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEquals) === 0) {
        return unescape(c.substring(nameEquals.length, c.length));
      }
    }
    return null;
  }

  // Gets the base URL of the location of the log4javascript script.
  // This is far from infallible.
  function getBaseUrl() {
    var scripts = document.getElementsByTagName('script');
    for (var i = 0, len = scripts.length; i < len; ++i) {
      if (scripts[i].src.indexOf('log4javascript') != -1) {
        var lastSlash = scripts[i].src.lastIndexOf('/');
        return lastSlash == -1 ? '' : scripts[i].src.substr(0, lastSlash + 1);
      }
    }
    return null;
  }

  function isLoaded(win) {
    try {
      return bool(win.loaded);
    } catch (ex) {
      return false;
    }
  }

  /* ---------------------------------------------------------------------- */
  // ConsoleAppender (prototype for PopUpAppender and InPageAppender)

  var ConsoleAppender;

  // Create an anonymous function to protect base console methods
  (function () {
    var getConsoleHtmlLines = function () {
      return [
        '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Strict//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-strict.dtd">',
        '<html xmlns="http://www.w3.org/1999/xhtml" lang="en" xml:lang="en">',
        '	<head>',
        '		<title>log4javascript</title>',
        '		<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />',
        '		<!-- Make IE8 behave like IE7, having gone to all the trouble of making IE work -->',
        '		<meta http-equiv="X-UA-Compatible" content="IE=7" />',
        '		<script type="text/javascript">var isIe = false, isIePre7 = false;</script>',
        '		<!--[if IE]><script type="text/javascript">isIe = true</script><![endif]-->',
        '		<!--[if lt IE 7]><script type="text/javascript">isIePre7 = true</script><![endif]-->',
        '		<script type="text/javascript">',
        '			//<![CDATA[',
        '			var loggingEnabled = true;',
        '			var logQueuedEventsTimer = null;',
        '			var logEntries = [];',
        '			var logEntriesAndSeparators = [];',
        '			var logItems = [];',
        '			var renderDelay = 100;',
        '			var unrenderedLogItemsExist = false;',
        '			var rootGroup, currentGroup = null;',
        '			var loaded = false;',
        '			var currentLogItem = null;',
        '			var logMainContainer;',
        '',
        '			function copyProperties(obj, props) {',
        '				for (var i in props) {',
        '					obj[i] = props[i];',
        '				}',
        '			}',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function LogItem() {',
        '			}',
        '',
        '			LogItem.prototype = {',
        '				mainContainer: null,',
        '				wrappedContainer: null,',
        '				unwrappedContainer: null,',
        '				group: null,',
        '',
        '				appendToLog: function() {',
        '					for (var i = 0, len = this.elementContainers.length; i < len; i++) {',
        '						this.elementContainers[i].appendToLog();',
        '					}',
        '					this.group.update();',
        '				},',
        '',
        '				doRemove: function(doUpdate, removeFromGroup) {',
        '					if (this.rendered) {',
        '						for (var i = 0, len = this.elementContainers.length; i < len; i++) {',
        '							this.elementContainers[i].remove();',
        '						}',
        '						this.unwrappedElementContainer = null;',
        '						this.wrappedElementContainer = null;',
        '						this.mainElementContainer = null;',
        '					}',
        '					if (this.group && removeFromGroup) {',
        '						this.group.removeChild(this, doUpdate);',
        '					}',
        '					if (this === currentLogItem) {',
        '						currentLogItem = null;',
        '					}',
        '				},',
        '',
        '				remove: function(doUpdate, removeFromGroup) {',
        '					this.doRemove(doUpdate, removeFromGroup);',
        '				},',
        '',
        '				render: function() {},',
        '',
        '				accept: function(visitor) {',
        '					visitor.visit(this);',
        '				},',
        '',
        '				getUnwrappedDomContainer: function() {',
        '					return this.group.unwrappedElementContainer.contentDiv;',
        '				},',
        '',
        '				getWrappedDomContainer: function() {',
        '					return this.group.wrappedElementContainer.contentDiv;',
        '				},',
        '',
        '				getMainDomContainer: function() {',
        '					return this.group.mainElementContainer.contentDiv;',
        '				}',
        '			};',
        '',
        '			LogItem.serializedItemKeys = {LOG_ENTRY: 0, GROUP_START: 1, GROUP_END: 2};',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function LogItemContainerElement() {',
        '			}',
        '',
        '			LogItemContainerElement.prototype = {',
        '				appendToLog: function() {',
        '					var insertBeforeFirst = (newestAtTop && this.containerDomNode.hasChildNodes());',
        '					if (insertBeforeFirst) {',
        '						this.containerDomNode.insertBefore(this.mainDiv, this.containerDomNode.firstChild);',
        '					} else {',
        '						this.containerDomNode.appendChild(this.mainDiv);',
        '					}',
        '				}',
        '			};',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function SeparatorElementContainer(containerDomNode) {',
        '				this.containerDomNode = containerDomNode;',
        '				this.mainDiv = document.createElement("div");',
        '				this.mainDiv.className = "separator";',
        '				this.mainDiv.innerHTML = "&nbsp;";',
        '			}',
        '',
        '			SeparatorElementContainer.prototype = new LogItemContainerElement();',
        '',
        '			SeparatorElementContainer.prototype.remove = function() {',
        '				this.mainDiv.parentNode.removeChild(this.mainDiv);',
        '				this.mainDiv = null;',
        '			};',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function Separator() {',
        '				this.rendered = false;',
        '			}',
        '',
        '			Separator.prototype = new LogItem();',
        '',
        '			copyProperties(Separator.prototype, {',
        '				render: function() {',
        '					var containerDomNode = this.group.contentDiv;',
        '					if (isIe) {',
        '						this.unwrappedElementContainer = new SeparatorElementContainer(this.getUnwrappedDomContainer());',
        '						this.wrappedElementContainer = new SeparatorElementContainer(this.getWrappedDomContainer());',
        '						this.elementContainers = [this.unwrappedElementContainer, this.wrappedElementContainer];',
        '					} else {',
        '						this.mainElementContainer = new SeparatorElementContainer(this.getMainDomContainer());',
        '						this.elementContainers = [this.mainElementContainer];',
        '					}',
        '					this.content = this.formattedMessage;',
        '					this.rendered = true;',
        '				}',
        '			});',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function GroupElementContainer(group, containerDomNode, isRoot, isWrapped) {',
        '				this.group = group;',
        '				this.containerDomNode = containerDomNode;',
        '				this.isRoot = isRoot;',
        '				this.isWrapped = isWrapped;',
        '				this.expandable = false;',
        '',
        '				if (this.isRoot) {',
        '					if (isIe) {',
        '						this.contentDiv = logMainContainer.appendChild(document.createElement("div"));',
        '						this.contentDiv.id = this.isWrapped ? "log_wrapped" : "log_unwrapped";',
        '					} else {',
        '						this.contentDiv = logMainContainer;',
        '					}',
        '				} else {',
        '					var groupElementContainer = this;',
        '',
        '					this.mainDiv = document.createElement("div");',
        '					this.mainDiv.className = "group";',
        '',
        '					this.headingDiv = this.mainDiv.appendChild(document.createElement("div"));',
        '					this.headingDiv.className = "groupheading";',
        '',
        '					this.expander = this.headingDiv.appendChild(document.createElement("span"));',
        '					this.expander.className = "expander unselectable greyedout";',
        '					this.expander.unselectable = true;',
        '					var expanderText = this.group.expanded ? "-" : "+";',
        '					this.expanderTextNode = this.expander.appendChild(document.createTextNode(expanderText));',
        '',
        '					this.headingDiv.appendChild(document.createTextNode(" " + this.group.name));',
        '',
        '					this.contentDiv = this.mainDiv.appendChild(document.createElement("div"));',
        '					var contentCssClass = this.group.expanded ? "expanded" : "collapsed";',
        '					this.contentDiv.className = "groupcontent " + contentCssClass;',
        '',
        '					this.expander.onclick = function() {',
        '						if (groupElementContainer.group.expandable) {',
        '							groupElementContainer.group.toggleExpanded();',
        '						}',
        '					};',
        '				}',
        '			}',
        '',
        '			GroupElementContainer.prototype = new LogItemContainerElement();',
        '',
        '			copyProperties(GroupElementContainer.prototype, {',
        '				toggleExpanded: function() {',
        '					if (!this.isRoot) {',
        '						var oldCssClass, newCssClass, expanderText;',
        '						if (this.group.expanded) {',
        '							newCssClass = "expanded";',
        '							oldCssClass = "collapsed";',
        '							expanderText = "-";',
        '						} else {',
        '							newCssClass = "collapsed";',
        '							oldCssClass = "expanded";',
        '							expanderText = "+";',
        '						}',
        '						replaceClass(this.contentDiv, newCssClass, oldCssClass);',
        '						this.expanderTextNode.nodeValue = expanderText;',
        '					}',
        '				},',
        '',
        '				remove: function() {',
        '					if (!this.isRoot) {',
        '						this.headingDiv = null;',
        '						this.expander.onclick = null;',
        '						this.expander = null;',
        '						this.expanderTextNode = null;',
        '						this.contentDiv = null;',
        '						this.containerDomNode = null;',
        '						this.mainDiv.parentNode.removeChild(this.mainDiv);',
        '						this.mainDiv = null;',
        '					}',
        '				},',
        '',
        '				reverseChildren: function() {',
        '					// Invert the order of the log entries',
        '					var node = null;',
        '',
        '					// Remove all the log container nodes',
        '					var childDomNodes = [];',
        '					while ((node = this.contentDiv.firstChild)) {',
        '						this.contentDiv.removeChild(node);',
        '						childDomNodes.push(node);',
        '					}',
        '',
        '					// Put them all back in reverse order',
        '					while ((node = childDomNodes.pop())) {',
        '						this.contentDiv.appendChild(node);',
        '					}',
        '				},',
        '',
        '				update: function() {',
        '					if (!this.isRoot) {',
        '						if (this.group.expandable) {',
        '							removeClass(this.expander, "greyedout");',
        '						} else {',
        '							addClass(this.expander, "greyedout");',
        '						}',
        '					}',
        '				},',
        '',
        '				clear: function() {',
        '					if (this.isRoot) {',
        '						this.contentDiv.innerHTML = "";',
        '					}',
        '				}',
        '			});',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function Group(name, isRoot, initiallyExpanded) {',
        '				this.name = name;',
        '				this.group = null;',
        '				this.isRoot = isRoot;',
        '				this.initiallyExpanded = initiallyExpanded;',
        '				this.elementContainers = [];',
        '				this.children = [];',
        '				this.expanded = initiallyExpanded;',
        '				this.rendered = false;',
        '				this.expandable = false;',
        '			}',
        '',
        '			Group.prototype = new LogItem();',
        '',
        '			copyProperties(Group.prototype, {',
        '				addChild: function(logItem) {',
        '					this.children.push(logItem);',
        '					logItem.group = this;',
        '				},',
        '',
        '				render: function() {',
        '					if (isIe) {',
        '						var unwrappedDomContainer, wrappedDomContainer;',
        '						if (this.isRoot) {',
        '							unwrappedDomContainer = logMainContainer;',
        '							wrappedDomContainer = logMainContainer;',
        '						} else {',
        '							unwrappedDomContainer = this.getUnwrappedDomContainer();',
        '							wrappedDomContainer = this.getWrappedDomContainer();',
        '						}',
        '						this.unwrappedElementContainer = new GroupElementContainer(this, unwrappedDomContainer, this.isRoot, false);',
        '						this.wrappedElementContainer = new GroupElementContainer(this, wrappedDomContainer, this.isRoot, true);',
        '						this.elementContainers = [this.unwrappedElementContainer, this.wrappedElementContainer];',
        '					} else {',
        '						var mainDomContainer = this.isRoot ? logMainContainer : this.getMainDomContainer();',
        '						this.mainElementContainer = new GroupElementContainer(this, mainDomContainer, this.isRoot, false);',
        '						this.elementContainers = [this.mainElementContainer];',
        '					}',
        '					this.rendered = true;',
        '				},',
        '',
        '				toggleExpanded: function() {',
        '					this.expanded = !this.expanded;',
        '					for (var i = 0, len = this.elementContainers.length; i < len; i++) {',
        '						this.elementContainers[i].toggleExpanded();',
        '					}',
        '				},',
        '',
        '				expand: function() {',
        '					if (!this.expanded) {',
        '						this.toggleExpanded();',
        '					}',
        '				},',
        '',
        '				accept: function(visitor) {',
        '					visitor.visitGroup(this);',
        '				},',
        '',
        '				reverseChildren: function() {',
        '					if (this.rendered) {',
        '						for (var i = 0, len = this.elementContainers.length; i < len; i++) {',
        '							this.elementContainers[i].reverseChildren();',
        '						}',
        '					}',
        '				},',
        '',
        '				update: function() {',
        '					var previouslyExpandable = this.expandable;',
        '					this.expandable = (this.children.length !== 0);',
        '					if (this.expandable !== previouslyExpandable) {',
        '						for (var i = 0, len = this.elementContainers.length; i < len; i++) {',
        '							this.elementContainers[i].update();',
        '						}',
        '					}',
        '				},',
        '',
        '				flatten: function() {',
        '					var visitor = new GroupFlattener();',
        '					this.accept(visitor);',
        '					return visitor.logEntriesAndSeparators;',
        '				},',
        '',
        '				removeChild: function(child, doUpdate) {',
        '					array_remove(this.children, child);',
        '					child.group = null;',
        '					if (doUpdate) {',
        '						this.update();',
        '					}',
        '				},',
        '',
        '				remove: function(doUpdate, removeFromGroup) {',
        '					for (var i = 0, len = this.children.length; i < len; i++) {',
        '						this.children[i].remove(false, false);',
        '					}',
        '					this.children = [];',
        '					this.update();',
        '					if (this === currentGroup) {',
        '						currentGroup = this.group;',
        '					}',
        '					this.doRemove(doUpdate, removeFromGroup);',
        '				},',
        '',
        '				serialize: function(items) {',
        '					items.push([LogItem.serializedItemKeys.GROUP_START, this.name]);',
        '					for (var i = 0, len = this.children.length; i < len; i++) {',
        '						this.children[i].serialize(items);',
        '					}',
        '					if (this !== currentGroup) {',
        '						items.push([LogItem.serializedItemKeys.GROUP_END]);',
        '					}',
        '				},',
        '',
        '				clear: function() {',
        '					for (var i = 0, len = this.elementContainers.length; i < len; i++) {',
        '						this.elementContainers[i].clear();',
        '					}',
        '				}',
        '			});',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function LogEntryElementContainer() {',
        '			}',
        '',
        '			LogEntryElementContainer.prototype = new LogItemContainerElement();',
        '',
        '			copyProperties(LogEntryElementContainer.prototype, {',
        '				remove: function() {',
        '					this.doRemove();',
        '				},',
        '',
        '				doRemove: function() {',
        '					this.mainDiv.parentNode.removeChild(this.mainDiv);',
        '					this.mainDiv = null;',
        '					this.contentElement = null;',
        '					this.containerDomNode = null;',
        '				},',
        '',
        '				setContent: function(content, wrappedContent) {',
        '					if (content === this.formattedMessage) {',
        '						this.contentElement.innerHTML = "";',
        '						this.contentElement.appendChild(document.createTextNode(this.formattedMessage));',
        '					} else {',
        '						this.contentElement.innerHTML = content;',
        '					}',
        '				},',
        '',
        '				setSearchMatch: function(isMatch) {',
        '					var oldCssClass = isMatch ? "searchnonmatch" : "searchmatch";',
        '					var newCssClass = isMatch ? "searchmatch" : "searchnonmatch";',
        '					replaceClass(this.mainDiv, newCssClass, oldCssClass);',
        '				},',
        '',
        '				clearSearch: function() {',
        '					removeClass(this.mainDiv, "searchmatch");',
        '					removeClass(this.mainDiv, "searchnonmatch");',
        '				}',
        '			});',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function LogEntryWrappedElementContainer(logEntry, containerDomNode) {',
        '				this.logEntry = logEntry;',
        '				this.containerDomNode = containerDomNode;',
        '				this.mainDiv = document.createElement("div");',
        '				this.mainDiv.appendChild(document.createTextNode(this.logEntry.formattedMessage));',
        '				this.mainDiv.className = "logentry wrapped " + this.logEntry.level;',
        '				this.contentElement = this.mainDiv;',
        '			}',
        '',
        '			LogEntryWrappedElementContainer.prototype = new LogEntryElementContainer();',
        '',
        '			LogEntryWrappedElementContainer.prototype.setContent = function(content, wrappedContent) {',
        '				if (content === this.formattedMessage) {',
        '					this.contentElement.innerHTML = "";',
        '					this.contentElement.appendChild(document.createTextNode(this.formattedMessage));',
        '				} else {',
        '					this.contentElement.innerHTML = wrappedContent;',
        '				}',
        '			};',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function LogEntryUnwrappedElementContainer(logEntry, containerDomNode) {',
        '				this.logEntry = logEntry;',
        '				this.containerDomNode = containerDomNode;',
        '				this.mainDiv = document.createElement("div");',
        '				this.mainDiv.className = "logentry unwrapped " + this.logEntry.level;',
        '				this.pre = this.mainDiv.appendChild(document.createElement("pre"));',
        '				this.pre.appendChild(document.createTextNode(this.logEntry.formattedMessage));',
        '				this.pre.className = "unwrapped";',
        '				this.contentElement = this.pre;',
        '			}',
        '',
        '			LogEntryUnwrappedElementContainer.prototype = new LogEntryElementContainer();',
        '',
        '			LogEntryUnwrappedElementContainer.prototype.remove = function() {',
        '				this.doRemove();',
        '				this.pre = null;',
        '			};',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function LogEntryMainElementContainer(logEntry, containerDomNode) {',
        '				this.logEntry = logEntry;',
        '				this.containerDomNode = containerDomNode;',
        '				this.mainDiv = document.createElement("div");',
        '				this.mainDiv.className = "logentry nonielogentry " + this.logEntry.level;',
        '				this.contentElement = this.mainDiv.appendChild(document.createElement("span"));',
        '				this.contentElement.appendChild(document.createTextNode(this.logEntry.formattedMessage));',
        '			}',
        '',
        '			LogEntryMainElementContainer.prototype = new LogEntryElementContainer();',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function LogEntry(level, formattedMessage) {',
        '				this.level = level;',
        '				this.formattedMessage = formattedMessage;',
        '				this.rendered = false;',
        '			}',
        '',
        '			LogEntry.prototype = new LogItem();',
        '',
        '			copyProperties(LogEntry.prototype, {',
        '				render: function() {',
        '					var logEntry = this;',
        '					var containerDomNode = this.group.contentDiv;',
        '',
        '					// Support for the CSS attribute white-space in IE for Windows is',
        '					// non-existent pre version 6 and slightly odd in 6, so instead',
        '					// use two different HTML elements',
        '					if (isIe) {',
        '						this.formattedMessage = this.formattedMessage.replace(/\\r\\n/g, "\\r"); // Workaround for IE\'s treatment of white space',
        '						this.unwrappedElementContainer = new LogEntryUnwrappedElementContainer(this, this.getUnwrappedDomContainer());',
        '						this.wrappedElementContainer = new LogEntryWrappedElementContainer(this, this.getWrappedDomContainer());',
        '						this.elementContainers = [this.unwrappedElementContainer, this.wrappedElementContainer];',
        '					} else {',
        '						this.mainElementContainer = new LogEntryMainElementContainer(this, this.getMainDomContainer());',
        '						this.elementContainers = [this.mainElementContainer];',
        '					}',
        '					this.content = this.formattedMessage;',
        '					this.rendered = true;',
        '				},',
        '',
        '				setContent: function(content, wrappedContent) {',
        '					if (content != this.content) {',
        '						if (isIe && (content !== this.formattedMessage)) {',
        '							content = content.replace(/\\r\\n/g, "\\r"); // Workaround for IE\'s treatment of white space',
        '						}',
        '						for (var i = 0, len = this.elementContainers.length; i < len; i++) {',
        '							this.elementContainers[i].setContent(content, wrappedContent);',
        '						}',
        '						this.content = content;',
        '					}',
        '				},',
        '',
        '				getSearchMatches: function() {',
        '					var matches = [];',
        '					var i, len;',
        '					if (isIe) {',
        '						var unwrappedEls = getElementsByClass(this.unwrappedElementContainer.mainDiv, "searchterm", "span");',
        '						var wrappedEls = getElementsByClass(this.wrappedElementContainer.mainDiv, "searchterm", "span");',
        '						for (i = 0, len = unwrappedEls.length; i < len; i++) {',
        '							matches[i] = new Match(this.level, null, unwrappedEls[i], wrappedEls[i]);',
        '						}',
        '					} else {',
        '						var els = getElementsByClass(this.mainElementContainer.mainDiv, "searchterm", "span");',
        '						for (i = 0, len = els.length; i < len; i++) {',
        '							matches[i] = new Match(this.level, els[i]);',
        '						}',
        '					}',
        '					return matches;',
        '				},',
        '',
        '				setSearchMatch: function(isMatch) {',
        '					for (var i = 0, len = this.elementContainers.length; i < len; i++) {',
        '						this.elementContainers[i].setSearchMatch(isMatch);',
        '					}',
        '				},',
        '',
        '				clearSearch: function() {',
        '					for (var i = 0, len = this.elementContainers.length; i < len; i++) {',
        '						this.elementContainers[i].clearSearch();',
        '					}',
        '				},',
        '',
        '				accept: function(visitor) {',
        '					visitor.visitLogEntry(this);',
        '				},',
        '',
        '				serialize: function(items) {',
        '					items.push([LogItem.serializedItemKeys.LOG_ENTRY, this.level, this.formattedMessage]);',
        '				}',
        '			});',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function LogItemVisitor() {',
        '			}',
        '',
        '			LogItemVisitor.prototype = {',
        '				visit: function(logItem) {',
        '				},',
        '',
        '				visitParent: function(logItem) {',
        '					if (logItem.group) {',
        '						logItem.group.accept(this);',
        '					}',
        '				},',
        '',
        '				visitChildren: function(logItem) {',
        '					for (var i = 0, len = logItem.children.length; i < len; i++) {',
        '						logItem.children[i].accept(this);',
        '					}',
        '				},',
        '',
        '				visitLogEntry: function(logEntry) {',
        '					this.visit(logEntry);',
        '				},',
        '',
        '				visitSeparator: function(separator) {',
        '					this.visit(separator);',
        '				},',
        '',
        '				visitGroup: function(group) {',
        '					this.visit(group);',
        '				}',
        '			};',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function GroupFlattener() {',
        '				this.logEntriesAndSeparators = [];',
        '			}',
        '',
        '			GroupFlattener.prototype = new LogItemVisitor();',
        '',
        '			GroupFlattener.prototype.visitGroup = function(group) {',
        '				this.visitChildren(group);',
        '			};',
        '',
        '			GroupFlattener.prototype.visitLogEntry = function(logEntry) {',
        '				this.logEntriesAndSeparators.push(logEntry);',
        '			};',
        '',
        '			GroupFlattener.prototype.visitSeparator = function(separator) {',
        '				this.logEntriesAndSeparators.push(separator);',
        '			};',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function scrollLogContainer(evt) {',
        '				$("log").scrollBy(0, evt.deltaY);',
        '			};',
        '',
        '			window.onload = function() {',
        '				// Sort out document.domain',
        '				if (location.search) {',
        '					var queryBits = unescape(location.search).substr(1).split("&"), nameValueBits;',
        '					for (var i = 0, len = queryBits.length; i < len; i++) {',
        '						nameValueBits = queryBits[i].split("=");',
        '						if (nameValueBits[0] == "log4javascript_domain") {',
        '							document.domain = nameValueBits[1];',
        '							break;',
        '						}',
        '					}',
        '				}',
        '',
        '				// Create DOM objects',
        '				logMainContainer = $("log");',
        '				if (isIePre7) {',
        '					addClass(logMainContainer, "oldIe");',
        '				}',
        '',
        '				rootGroup = new Group("root", true);',
        '				rootGroup.render();',
        '				currentGroup = rootGroup;',
        '',
        '				setCommandInputWidth();',
        '				setLogContainerHeight();',
        '				toggleLoggingEnabled();',
        '				toggleSearchEnabled();',
        '				toggleSearchFilter();',
        '				toggleSearchHighlight();',
        '				applyFilters();',
        '				checkAllLevels();',
        '				toggleWrap();',
        '				toggleNewestAtTop();',
        '				toggleScrollToLatest();',
        '				renderQueuedLogItems();',
        '				loaded = true;',
        '				$("command").value = "";',
        '				$("command").autocomplete = "off";',
        '				$("command").onkeydown = function(evt) {',
        '					evt = getEvent(evt);',
        '					if (evt.keyCode == 10 || evt.keyCode == 13) { // Return/Enter',
        '						evalCommandLine();',
        '						stopPropagation(evt);',
        '					} else if (evt.keyCode == 27) { // Escape',
        '						this.value = "";',
        '						this.focus();',
        '					} else if (evt.keyCode == 38 && commandHistory.length > 0) { // Up',
        '						currentCommandIndex = Math.max(0, currentCommandIndex - 1);',
        '						this.value = commandHistory[currentCommandIndex];',
        '						moveCaretToEnd(this);',
        '					} else if (evt.keyCode == 40 && commandHistory.length > 0) { // Down',
        '						currentCommandIndex = Math.min(commandHistory.length - 1, currentCommandIndex + 1);',
        '						this.value = commandHistory[currentCommandIndex];',
        '						moveCaretToEnd(this);',
        '					}',
        '				};',
        '',
        '				// Prevent the keypress moving the caret in Firefox',
        '				$("command").onkeypress = function(evt) {',
        '					evt = getEvent(evt);',
        '					if (evt.keyCode == 38 && commandHistory.length > 0 && evt.preventDefault) { // Up',
        '						evt.preventDefault();',
        '					}',
        '				};',
        '',
        '				// Prevent the keyup event blurring the input in Opera',
        '				$("command").onkeyup = function(evt) {',
        '					evt = getEvent(evt);',
        '					if (evt.keyCode == 27 && evt.preventDefault) { // Up',
        '						evt.preventDefault();',
        '						this.focus();',
        '					}',
        '				};',
        '',
        '				// Add document keyboard shortcuts',
        '				document.onkeydown = function keyEventHandler(evt) {',
        '					evt = getEvent(evt);',
        '					switch (evt.keyCode) {',
        '						case 69: // Ctrl + shift + E: re-execute last command',
        '							if (evt.shiftKey && (evt.ctrlKey || evt.metaKey)) {',
        '								evalLastCommand();',
        '								cancelKeyEvent(evt);',
        '								return false;',
        '							}',
        '							break;',
        '						case 75: // Ctrl + shift + K: focus search',
        '							if (evt.shiftKey && (evt.ctrlKey || evt.metaKey)) {',
        '								focusSearch();',
        '								cancelKeyEvent(evt);',
        '								return false;',
        '							}',
        '							break;',
        '						case 40: // Ctrl + shift + down arrow: focus command line',
        '						case 76: // Ctrl + shift + L: focus command line',
        '							if (evt.shiftKey && (evt.ctrlKey || evt.metaKey)) {',
        '								focusCommandLine();',
        '								cancelKeyEvent(evt);',
        '								return false;',
        '							}',
        '							break;',
        '					}',
        '				};',
        '',
        '				logMainContainer.addEventListener("mousewheel", scrollLogContainer, {passive: true});',
        '',
        '				// Workaround to make sure log div starts at the correct size',
        '				setTimeout(setLogContainerHeight, 20);',
        '',
        '				setShowCommandLine(showCommandLine);',
        '				doSearch();',
        '			};',
        '',
        '			window.onunload = function() {',
        '				logMainContainer.removeEventListener("mousewheel", scrollLogContainer, {passive: true});',
        '				if (mainWindowExists()) {',
        '					appender.unload();',
        '				}',
        '				appender = null;',
        '			};',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function toggleLoggingEnabled() {',
        '				setLoggingEnabled($("enableLogging").checked);',
        '			}',
        '',
        '			function setLoggingEnabled(enable) {',
        '				loggingEnabled = enable;',
        '			}',
        '',
        '			var appender = null;',
        '',
        '			function setAppender(appenderParam) {',
        '				appender = appenderParam;',
        '			}',
        '',
        '			function setShowCloseButton(showCloseButton) {',
        '				$("closeButton").style.display = showCloseButton ? "inline" : "none";',
        '			}',
        '',
        '			function setShowHideButton(showHideButton) {',
        '				$("hideButton").style.display = showHideButton ? "inline" : "none";',
        '			}',
        '',
        '			var newestAtTop = false;',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function LogItemContentReverser() {',
        '			}',
        '',
        '			LogItemContentReverser.prototype = new LogItemVisitor();',
        '',
        '			LogItemContentReverser.prototype.visitGroup = function(group) {',
        '				group.reverseChildren();',
        '				this.visitChildren(group);',
        '			};',
        '',
        '			/*----------------------------------------------------------------*/',
        '',
        '			function setNewestAtTop(isNewestAtTop) {',
        '				var oldNewestAtTop = newestAtTop;',
        '				var i, iLen, j, jLen;',
        '				newestAtTop = Boolean(isNewestAtTop);',
        '				if (oldNewestAtTop != newestAtTop) {',
        '					var visitor = new LogItemContentReverser();',
        '					rootGroup.accept(visitor);',
        '',
        '					// Reassemble the matches array',
        '					if (currentSearch) {',
        '						var currentMatch = currentSearch.matches[currentMatchIndex];',
        '						var matchIndex = 0;',
        '						var matches = [];',
        '						var actOnLogEntry = function(logEntry) {',
        '							var logEntryMatches = logEntry.getSearchMatches();',
        '							for (j = 0, jLen = logEntryMatches.length; j < jLen; j++) {',
        '								matches[matchIndex] = logEntryMatches[j];',
        '								if (currentMatch && logEntryMatches[j].equals(currentMatch)) {',
        '									currentMatchIndex = matchIndex;',
        '								}',
        '								matchIndex++;',
        '							}',
        '						};',
        '						if (newestAtTop) {',
        '							for (i = logEntries.length - 1; i >= 0; i--) {',
        '								actOnLogEntry(logEntries[i]);',
        '							}',
        '						} else {',
        '							for (i = 0, iLen = logEntries.length; i < iLen; i++) {',
        '								actOnLogEntry(logEntries[i]);',
        '							}',
        '						}',
        '						currentSearch.matches = matches;',
        '						if (currentMatch) {',
        '							currentMatch.setCurrent();',
        '						}',
        '					} else if (scrollToLatest) {',
        '						doScrollToLatest();',
        '					}',
        '				}',
        '				$("newestAtTop").checked = isNewestAtTop;',
        '			}',
        '',
        '			function toggleNewestAtTop() {',
        '				var isNewestAtTop = $("newestAtTop").checked;',
        '				setNewestAtTop(isNewestAtTop);',
        '			}',
        '',
        '			var scrollToLatest = true;',
        '',
        '			function setScrollToLatest(isScrollToLatest) {',
        '				scrollToLatest = isScrollToLatest;',
        '				if (scrollToLatest) {',
        '					doScrollToLatest();',
        '				}',
        '				$("scrollToLatest").checked = isScrollToLatest;',
        '			}',
        '',
        '			function toggleScrollToLatest() {',
        '				var isScrollToLatest = $("scrollToLatest").checked;',
        '				setScrollToLatest(isScrollToLatest);',
        '			}',
        '',
        '			function doScrollToLatest() {',
        '				var l = logMainContainer;',
        '				if (typeof l.scrollTop != "undefined") {',
        '					if (newestAtTop) {',
        '						l.scrollTop = 0;',
        '					} else {',
        '						var latestLogEntry = l.lastChild;',
        '						if (latestLogEntry) {',
        '							l.scrollTop = l.scrollHeight;',
        '						}',
        '					}',
        '				}',
        '			}',
        '',
        '			var closeIfOpenerCloses = true;',
        '',
        '			function setCloseIfOpenerCloses(isCloseIfOpenerCloses) {',
        '				closeIfOpenerCloses = isCloseIfOpenerCloses;',
        '			}',
        '',
        '			var maxMessages = null;',
        '',
        '			function setMaxMessages(max) {',
        '				maxMessages = max;',
        '				pruneLogEntries();',
        '			}',
        '',
        '			var showCommandLine = false;',
        '',
        '			function setShowCommandLine(isShowCommandLine) {',
        '				showCommandLine = isShowCommandLine;',
        '				if (loaded) {',
        '					$("commandLine").style.display = showCommandLine ? "block" : "none";',
        '					setCommandInputWidth();',
        '					setLogContainerHeight();',
        '				}',
        '			}',
        '',
        '			function focusCommandLine() {',
        '				if (loaded) {',
        '					$("command").focus();',
        '				}',
        '			}',
        '',
        '			function focusSearch() {',
        '				if (loaded) {',
        '					$("searchBox").focus();',
        '				}',
        '			}',
        '',
        '			function getLogItems() {',
        '				var items = [];',
        '				for (var i = 0, len = logItems.length; i < len; i++) {',
        '					logItems[i].serialize(items);',
        '				}',
        '				return items;',
        '			}',
        '',
        '			function setLogItems(items) {',
        '				var loggingReallyEnabled = loggingEnabled;',
        '				// Temporarily turn logging on',
        '				loggingEnabled = true;',
        '				for (var i = 0, len = items.length; i < len; i++) {',
        '					switch (items[i][0]) {',
        '						case LogItem.serializedItemKeys.LOG_ENTRY:',
        '							log(items[i][1], items[i][2]);',
        '							break;',
        '						case LogItem.serializedItemKeys.GROUP_START:',
        '							group(items[i][1]);',
        '							break;',
        '						case LogItem.serializedItemKeys.GROUP_END:',
        '							groupEnd();',
        '							break;',
        '					}',
        '				}',
        '				loggingEnabled = loggingReallyEnabled;',
        '			}',
        '',
        '			function log(logLevel, formattedMessage) {',
        '				if (loggingEnabled) {',
        '					var logEntry = new LogEntry(logLevel, formattedMessage);',
        '					logEntries.push(logEntry);',
        '					logEntriesAndSeparators.push(logEntry);',
        '					logItems.push(logEntry);',
        '					currentGroup.addChild(logEntry);',
        '					if (loaded) {',
        '						if (logQueuedEventsTimer !== null) {',
        '							clearTimeout(logQueuedEventsTimer);',
        '						}',
        '						logQueuedEventsTimer = setTimeout(renderQueuedLogItems, renderDelay);',
        '						unrenderedLogItemsExist = true;',
        '					}',
        '				}',
        '			}',
        '',
        '			function renderQueuedLogItems() {',
        '				logQueuedEventsTimer = null;',
        '				var pruned = pruneLogEntries();',
        '',
        '				// Render any unrendered log entries and apply the current search to them',
        '				var initiallyHasMatches = currentSearch ? currentSearch.hasMatches() : false;',
        '				for (var i = 0, len = logItems.length; i < len; i++) {',
        '					if (!logItems[i].rendered) {',
        '						logItems[i].render();',
        '						logItems[i].appendToLog();',
        '						if (currentSearch && (logItems[i] instanceof LogEntry)) {',
        '							currentSearch.applyTo(logItems[i]);',
        '						}',
        '					}',
        '				}',
        '				if (currentSearch) {',
        '					if (pruned) {',
        '						if (currentSearch.hasVisibleMatches()) {',
        '							if (currentMatchIndex === null) {',
        '								setCurrentMatchIndex(0);',
        '							}',
        '							displayMatches();',
        '						} else {',
        '							displayNoMatches();',
        '						}',
        '					} else if (!initiallyHasMatches && currentSearch.hasVisibleMatches()) {',
        '						setCurrentMatchIndex(0);',
        '						displayMatches();',
        '					}',
        '				}',
        '				if (scrollToLatest) {',
        '					doScrollToLatest();',
        '				}',
        '				unrenderedLogItemsExist = false;',
        '			}',
        '',
        '			function pruneLogEntries() {',
        '				if ((maxMessages !== null) && (logEntriesAndSeparators.length > maxMessages)) {',
        '					var numberToDelete = logEntriesAndSeparators.length - maxMessages;',
        '					var prunedLogEntries = logEntriesAndSeparators.slice(0, numberToDelete);',
        '					if (currentSearch) {',
        '						currentSearch.removeMatches(prunedLogEntries);',
        '					}',
        '					var group;',
        '					for (var i = 0; i < numberToDelete; i++) {',
        '						group = logEntriesAndSeparators[i].group;',
        '						array_remove(logItems, logEntriesAndSeparators[i]);',
        '						array_remove(logEntries, logEntriesAndSeparators[i]);',
        '						logEntriesAndSeparators[i].remove(true, true);',
        '						if (group.children.length === 0 && group !== currentGroup && group !== rootGroup) {',
        '							array_remove(logItems, group);',
        '							group.remove(true, true);',
        '						}',
        '					}',
        '					logEntriesAndSeparators = array_removeFromStart(logEntriesAndSeparators, numberToDelete);',
        '					return true;',
        '				}',
        '				return false;',
        '			}',
        '',
        '			function group(name, startExpanded) {',
        '				if (loggingEnabled) {',
        '					initiallyExpanded = (typeof startExpanded === "undefined") ? true : Boolean(startExpanded);',
        '					var newGroup = new Group(name, false, initiallyExpanded);',
        '					currentGroup.addChild(newGroup);',
        '					currentGroup = newGroup;',
        '					logItems.push(newGroup);',
        '					if (loaded) {',
        '						if (logQueuedEventsTimer !== null) {',
        '							clearTimeout(logQueuedEventsTimer);',
        '						}',
        '						logQueuedEventsTimer = setTimeout(renderQueuedLogItems, renderDelay);',
        '						unrenderedLogItemsExist = true;',
        '					}',
        '				}',
        '			}',
        '',
        '			function groupEnd() {',
        '				currentGroup = (currentGroup === rootGroup) ? rootGroup : currentGroup.group;',
        '			}',
        '',
        '			function mainPageReloaded() {',
        '				currentGroup = rootGroup;',
        '				var separator = new Separator();',
        '				logEntriesAndSeparators.push(separator);',
        '				logItems.push(separator);',
        '				currentGroup.addChild(separator);',
        '			}',
        '',
        '			function closeWindow() {',
        '				if (appender && mainWindowExists()) {',
        '					appender.close(true);',
        '				} else {',
        '					window.close();',
        '				}',
        '			}',
        '',
        '			function hide() {',
        '				if (appender && mainWindowExists()) {',
        '					appender.hide();',
        '				}',
        '			}',
        '',
        '			var mainWindow = window;',
        '			var windowId = "log4javascriptConsoleWindow_" + new Date().getTime() + "_" + ("" + Math.random()).substr(2);',
        '',
        '			function setMainWindow(win) {',
        '				mainWindow = win;',
        '				mainWindow[windowId] = window;',
        "				// If this is a pop-up, poll the opener to see if it's closed",
        '				if (opener && closeIfOpenerCloses) {',
        '					pollOpener();',
        '				}',
        '			}',
        '',
        '			function pollOpener() {',
        '				if (closeIfOpenerCloses) {',
        '					if (mainWindowExists()) {',
        '						setTimeout(pollOpener, 500);',
        '					} else {',
        '						closeWindow();',
        '					}',
        '				}',
        '			}',
        '',
        '			function mainWindowExists() {',
        '				try {',
        '					return (mainWindow && !mainWindow.closed &&',
        '						mainWindow[windowId] == window);',
        '				} catch (ex) {}',
        '				return false;',
        '			}',
        '',
        '			var logLevels = ["TRACE", "DEBUG", "INFO", "WARN", "ERROR", "FATAL"];',
        '',
        '			function getCheckBox(logLevel) {',
        '				return $("switch_" + logLevel);',
        '			}',
        '',
        '			function getIeWrappedLogContainer() {',
        '				return $("log_wrapped");',
        '			}',
        '',
        '			function getIeUnwrappedLogContainer() {',
        '				return $("log_unwrapped");',
        '			}',
        '',
        '			function applyFilters() {',
        '				for (var i = 0; i < logLevels.length; i++) {',
        '					if (getCheckBox(logLevels[i]).checked) {',
        '						addClass(logMainContainer, logLevels[i]);',
        '					} else {',
        '						removeClass(logMainContainer, logLevels[i]);',
        '					}',
        '				}',
        '				updateSearchFromFilters();',
        '			}',
        '',
        '			function toggleAllLevels() {',
        '				var turnOn = $("switch_ALL").checked;',
        '				for (var i = 0; i < logLevels.length; i++) {',
        '					getCheckBox(logLevels[i]).checked = turnOn;',
        '					if (turnOn) {',
        '						addClass(logMainContainer, logLevels[i]);',
        '					} else {',
        '						removeClass(logMainContainer, logLevels[i]);',
        '					}',
        '				}',
        '			}',
        '',
        '			function checkAllLevels() {',
        '				for (var i = 0; i < logLevels.length; i++) {',
        '					if (!getCheckBox(logLevels[i]).checked) {',
        '						getCheckBox("ALL").checked = false;',
        '						return;',
        '					}',
        '				}',
        '				getCheckBox("ALL").checked = true;',
        '			}',
        '',
        '			function clearLog() {',
        '				rootGroup.clear();',
        '				currentGroup = rootGroup;',
        '				logEntries = [];',
        '				logItems = [];',
        '				logEntriesAndSeparators = [];',
        ' 				doSearch();',
        '			}',
        '',
        '			function toggleWrap() {',
        '				var enable = $("wrap").checked;',
        '				if (enable) {',
        '					addClass(logMainContainer, "wrap");',
        '				} else {',
        '					removeClass(logMainContainer, "wrap");',
        '				}',
        '				refreshCurrentMatch();',
        '			}',
        '',
        '			/* ------------------------------------------------------------------- */',
        '',
        '			// Search',
        '',
        '			var searchTimer = null;',
        '',
        '			function scheduleSearch() {',
        '				try {',
        '					clearTimeout(searchTimer);',
        '				} catch (ex) {',
        '					// Do nothing',
        '				}',
        '				searchTimer = setTimeout(doSearch, 500);',
        '			}',
        '',
        '			function Search(searchTerm, isRegex, searchRegex, isCaseSensitive) {',
        '				this.searchTerm = searchTerm;',
        '				this.isRegex = isRegex;',
        '				this.searchRegex = searchRegex;',
        '				this.isCaseSensitive = isCaseSensitive;',
        '				this.matches = [];',
        '			}',
        '',
        '			Search.prototype = {',
        '				hasMatches: function() {',
        '					return this.matches.length > 0;',
        '				},',
        '',
        '				hasVisibleMatches: function() {',
        '					if (this.hasMatches()) {',
        '						for (var i = 0; i < this.matches.length; i++) {',
        '							if (this.matches[i].isVisible()) {',
        '								return true;',
        '							}',
        '						}',
        '					}',
        '					return false;',
        '				},',
        '',
        '				match: function(logEntry) {',
        '					var entryText = String(logEntry.formattedMessage);',
        '					var matchesSearch = false;',
        '					if (this.isRegex) {',
        '						matchesSearch = this.searchRegex.test(entryText);',
        '					} else if (this.isCaseSensitive) {',
        '						matchesSearch = (entryText.indexOf(this.searchTerm) > -1);',
        '					} else {',
        '						matchesSearch = (entryText.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1);',
        '					}',
        '					return matchesSearch;',
        '				},',
        '',
        '				getNextVisibleMatchIndex: function() {',
        '					for (var i = currentMatchIndex + 1; i < this.matches.length; i++) {',
        '						if (this.matches[i].isVisible()) {',
        '							return i;',
        '						}',
        '					}',
        '					// Start again from the first match',
        '					for (i = 0; i <= currentMatchIndex; i++) {',
        '						if (this.matches[i].isVisible()) {',
        '							return i;',
        '						}',
        '					}',
        '					return -1;',
        '				},',
        '',
        '				getPreviousVisibleMatchIndex: function() {',
        '					for (var i = currentMatchIndex - 1; i >= 0; i--) {',
        '						if (this.matches[i].isVisible()) {',
        '							return i;',
        '						}',
        '					}',
        '					// Start again from the last match',
        '					for (var i = this.matches.length - 1; i >= currentMatchIndex; i--) {',
        '						if (this.matches[i].isVisible()) {',
        '							return i;',
        '						}',
        '					}',
        '					return -1;',
        '				},',
        '',
        '				applyTo: function(logEntry) {',
        '					var doesMatch = this.match(logEntry);',
        '					if (doesMatch) {',
        '						logEntry.group.expand();',
        '						logEntry.setSearchMatch(true);',
        '						var logEntryContent;',
        '						var wrappedLogEntryContent;',
        '						var searchTermReplacementStartTag = "<span class=\\\"searchterm\\\">";',
        '						var searchTermReplacementEndTag = "<" + "/span>";',
        '						var preTagName = isIe ? "pre" : "span";',
        '						var preStartTag = "<" + preTagName + " class=\\\"pre\\\">";',
        '						var preEndTag = "<" + "/" + preTagName + ">";',
        '						var startIndex = 0;',
        '						var searchIndex, matchedText, textBeforeMatch;',
        '						if (this.isRegex) {',
        '							var flags = this.isCaseSensitive ? "g" : "gi";',
        '							var capturingRegex = new RegExp("(" + this.searchRegex.source + ")", flags);',
        '',
        '							// Replace the search term with temporary tokens for the start and end tags',
        '							var rnd = ("" + Math.random()).substr(2);',
        '							var startToken = "%%s" + rnd + "%%";',
        '							var endToken = "%%e" + rnd + "%%";',
        '							logEntryContent = logEntry.formattedMessage.replace(capturingRegex, startToken + "$1" + endToken);',
        '',
        '							// Escape the HTML to get rid of angle brackets',
        '							logEntryContent = escapeHtml(logEntryContent);',
        '',
        '							// Substitute the proper HTML back in for the search match',
        '							var result;',
        '							var searchString = logEntryContent;',
        '							logEntryContent = "";',
        '							wrappedLogEntryContent = "";',
        '							while ((searchIndex = searchString.indexOf(startToken, startIndex)) > -1) {',
        '								var endTokenIndex = searchString.indexOf(endToken, searchIndex);',
        '								matchedText = searchString.substring(searchIndex + startToken.length, endTokenIndex);',
        '								textBeforeMatch = searchString.substring(startIndex, searchIndex);',
        '								logEntryContent += preStartTag + textBeforeMatch + preEndTag;',
        '								logEntryContent += searchTermReplacementStartTag + preStartTag + matchedText +',
        '									preEndTag + searchTermReplacementEndTag;',
        '								if (isIe) {',
        '									wrappedLogEntryContent += textBeforeMatch + searchTermReplacementStartTag +',
        '										matchedText + searchTermReplacementEndTag;',
        '								}',
        '								startIndex = endTokenIndex + endToken.length;',
        '							}',
        '							logEntryContent += preStartTag + searchString.substr(startIndex) + preEndTag;',
        '							if (isIe) {',
        '								wrappedLogEntryContent += searchString.substr(startIndex);',
        '							}',
        '						} else {',
        '							logEntryContent = "";',
        '							wrappedLogEntryContent = "";',
        '							var searchTermReplacementLength = searchTermReplacementStartTag.length +',
        '								this.searchTerm.length + searchTermReplacementEndTag.length;',
        '							var searchTermLength = this.searchTerm.length;',
        '							var searchTermLowerCase = this.searchTerm.toLowerCase();',
        '							var logTextLowerCase = logEntry.formattedMessage.toLowerCase();',
        '							while ((searchIndex = logTextLowerCase.indexOf(searchTermLowerCase, startIndex)) > -1) {',
        '								matchedText = escapeHtml(logEntry.formattedMessage.substr(searchIndex, this.searchTerm.length));',
        '								textBeforeMatch = escapeHtml(logEntry.formattedMessage.substring(startIndex, searchIndex));',
        '								var searchTermReplacement = searchTermReplacementStartTag +',
        '									preStartTag + matchedText + preEndTag + searchTermReplacementEndTag;',
        '								logEntryContent += preStartTag + textBeforeMatch + preEndTag + searchTermReplacement;',
        '								if (isIe) {',
        '									wrappedLogEntryContent += textBeforeMatch + searchTermReplacementStartTag +',
        '										matchedText + searchTermReplacementEndTag;',
        '								}',
        '								startIndex = searchIndex + searchTermLength;',
        '							}',
        '							var textAfterLastMatch = escapeHtml(logEntry.formattedMessage.substr(startIndex));',
        '							logEntryContent += preStartTag + textAfterLastMatch + preEndTag;',
        '							if (isIe) {',
        '								wrappedLogEntryContent += textAfterLastMatch;',
        '							}',
        '						}',
        '						logEntry.setContent(logEntryContent, wrappedLogEntryContent);',
        '						var logEntryMatches = logEntry.getSearchMatches();',
        '						this.matches = this.matches.concat(logEntryMatches);',
        '					} else {',
        '						logEntry.setSearchMatch(false);',
        '						logEntry.setContent(logEntry.formattedMessage, logEntry.formattedMessage);',
        '					}',
        '					return doesMatch;',
        '				},',
        '',
        '				removeMatches: function(logEntries) {',
        '					var matchesToRemoveCount = 0;',
        '					var currentMatchRemoved = false;',
        '					var matchesToRemove = [];',
        '					var i, iLen, j, jLen;',
        '',
        '					// Establish the list of matches to be removed',
        '					for (i = 0, iLen = this.matches.length; i < iLen; i++) {',
        '						for (j = 0, jLen = logEntries.length; j < jLen; j++) {',
        '							if (this.matches[i].belongsTo(logEntries[j])) {',
        '								matchesToRemove.push(this.matches[i]);',
        '								if (i === currentMatchIndex) {',
        '									currentMatchRemoved = true;',
        '								}',
        '							}',
        '						}',
        '					}',
        '',
        '					// Set the new current match index if the current match has been deleted',
        '					// This will be the first match that appears after the first log entry being',
        "					// deleted, if one exists; otherwise, it's the first match overall",
        '					var newMatch = currentMatchRemoved ? null : this.matches[currentMatchIndex];',
        '					if (currentMatchRemoved) {',
        '						for (i = currentMatchIndex, iLen = this.matches.length; i < iLen; i++) {',
        '							if (this.matches[i].isVisible() && !array_contains(matchesToRemove, this.matches[i])) {',
        '								newMatch = this.matches[i];',
        '								break;',
        '							}',
        '						}',
        '					}',
        '',
        '					// Remove the matches',
        '					for (i = 0, iLen = matchesToRemove.length; i < iLen; i++) {',
        '						array_remove(this.matches, matchesToRemove[i]);',
        '						matchesToRemove[i].remove();',
        '					}',
        '',
        '					// Set the new match, if one exists',
        '					if (this.hasVisibleMatches()) {',
        '						if (newMatch === null) {',
        '							setCurrentMatchIndex(0);',
        '						} else {',
        '							// Get the index of the new match',
        '							var newMatchIndex = 0;',
        '							for (i = 0, iLen = this.matches.length; i < iLen; i++) {',
        '								if (newMatch === this.matches[i]) {',
        '									newMatchIndex = i;',
        '									break;',
        '								}',
        '							}',
        '							setCurrentMatchIndex(newMatchIndex);',
        '						}',
        '					} else {',
        '						currentMatchIndex = null;',
        '						displayNoMatches();',
        '					}',
        '				}',
        '			};',
        '',
        '			function getPageOffsetTop(el, container) {',
        '				var currentEl = el;',
        '				var y = 0;',
        '				while (currentEl && currentEl != container) {',
        '					y += currentEl.offsetTop;',
        '					currentEl = currentEl.offsetParent;',
        '				}',
        '				return y;',
        '			}',
        '',
        '			function scrollIntoView(el) {',
        '				var logContainer = logMainContainer;',
        '				// Check if the whole width of the element is visible and centre if not',
        '				if (!$("wrap").checked) {',
        '					var logContainerLeft = logContainer.scrollLeft;',
        '					var logContainerRight = logContainerLeft  + logContainer.offsetWidth;',
        '					var elLeft = el.offsetLeft;',
        '					var elRight = elLeft + el.offsetWidth;',
        '					if (elLeft < logContainerLeft || elRight > logContainerRight) {',
        '						logContainer.scrollLeft = elLeft - (logContainer.offsetWidth - el.offsetWidth) / 2;',
        '					}',
        '				}',
        '				// Check if the whole height of the element is visible and centre if not',
        '				var logContainerTop = logContainer.scrollTop;',
        '				var logContainerBottom = logContainerTop  + logContainer.offsetHeight;',
        '				var elTop = getPageOffsetTop(el) - getToolBarsHeight();',
        '				var elBottom = elTop + el.offsetHeight;',
        '				if (elTop < logContainerTop || elBottom > logContainerBottom) {',
        '					logContainer.scrollTop = elTop - (logContainer.offsetHeight - el.offsetHeight) / 2;',
        '				}',
        '			}',
        '',
        '			function Match(logEntryLevel, spanInMainDiv, spanInUnwrappedPre, spanInWrappedDiv) {',
        '				this.logEntryLevel = logEntryLevel;',
        '				this.spanInMainDiv = spanInMainDiv;',
        '				if (isIe) {',
        '					this.spanInUnwrappedPre = spanInUnwrappedPre;',
        '					this.spanInWrappedDiv = spanInWrappedDiv;',
        '				}',
        '				this.mainSpan = isIe ? spanInUnwrappedPre : spanInMainDiv;',
        '			}',
        '',
        '			Match.prototype = {',
        '				equals: function(match) {',
        '					return this.mainSpan === match.mainSpan;',
        '				},',
        '',
        '				setCurrent: function() {',
        '					if (isIe) {',
        '						addClass(this.spanInUnwrappedPre, "currentmatch");',
        '						addClass(this.spanInWrappedDiv, "currentmatch");',
        '						// Scroll the visible one into view',
        '						var elementToScroll = $("wrap").checked ? this.spanInWrappedDiv : this.spanInUnwrappedPre;',
        '						scrollIntoView(elementToScroll);',
        '					} else {',
        '						addClass(this.spanInMainDiv, "currentmatch");',
        '						scrollIntoView(this.spanInMainDiv);',
        '					}',
        '				},',
        '',
        '				belongsTo: function(logEntry) {',
        '					if (isIe) {',
        '						return isDescendant(this.spanInUnwrappedPre, logEntry.unwrappedPre);',
        '					} else {',
        '						return isDescendant(this.spanInMainDiv, logEntry.mainDiv);',
        '					}',
        '				},',
        '',
        '				setNotCurrent: function() {',
        '					if (isIe) {',
        '						removeClass(this.spanInUnwrappedPre, "currentmatch");',
        '						removeClass(this.spanInWrappedDiv, "currentmatch");',
        '					} else {',
        '						removeClass(this.spanInMainDiv, "currentmatch");',
        '					}',
        '				},',
        '',
        '				isOrphan: function() {',
        '					return isOrphan(this.mainSpan);',
        '				},',
        '',
        '				isVisible: function() {',
        '					return getCheckBox(this.logEntryLevel).checked;',
        '				},',
        '',
        '				remove: function() {',
        '					if (isIe) {',
        '						this.spanInUnwrappedPre = null;',
        '						this.spanInWrappedDiv = null;',
        '					} else {',
        '						this.spanInMainDiv = null;',
        '					}',
        '				}',
        '			};',
        '',
        '			var currentSearch = null;',
        '			var currentMatchIndex = null;',
        '',
        '			function doSearch() {',
        '				var searchBox = $("searchBox");',
        '				var searchTerm = searchBox.value;',
        '				var isRegex = $("searchRegex").checked;',
        '				var isCaseSensitive = $("searchCaseSensitive").checked;',
        '				var i;',
        '',
        '				if (searchTerm === "") {',
        '					$("searchReset").disabled = true;',
        '					$("searchNav").style.display = "none";',
        '					removeClass(document.body, "searching");',
        '					removeClass(searchBox, "hasmatches");',
        '					removeClass(searchBox, "nomatches");',
        '					for (i = 0; i < logEntries.length; i++) {',
        '						logEntries[i].clearSearch();',
        '						logEntries[i].setContent(logEntries[i].formattedMessage, logEntries[i].formattedMessage);',
        '					}',
        '					currentSearch = null;',
        '					setLogContainerHeight();',
        '				} else {',
        '					$("searchReset").disabled = false;',
        '					$("searchNav").style.display = "block";',
        '					var searchRegex;',
        '					var regexValid;',
        '					if (isRegex) {',
        '						try {',
        '							searchRegex = isCaseSensitive ? new RegExp(searchTerm, "g") : new RegExp(searchTerm, "gi");',
        '							regexValid = true;',
        '							replaceClass(searchBox, "validregex", "invalidregex");',
        '							searchBox.title = "Valid regex";',
        '						} catch (ex) {',
        '							regexValid = false;',
        '							replaceClass(searchBox, "invalidregex", "validregex");',
        '							searchBox.title = "Invalid regex: " + (ex.message ? ex.message : (ex.description ? ex.description : "unknown error"));',
        '							return;',
        '						}',
        '					} else {',
        '						searchBox.title = "";',
        '						removeClass(searchBox, "validregex");',
        '						removeClass(searchBox, "invalidregex");',
        '					}',
        '					addClass(document.body, "searching");',
        '					currentSearch = new Search(searchTerm, isRegex, searchRegex, isCaseSensitive);',
        '					for (i = 0; i < logEntries.length; i++) {',
        '						currentSearch.applyTo(logEntries[i]);',
        '					}',
        '					setLogContainerHeight();',
        '',
        '					// Highlight the first search match',
        '					if (currentSearch.hasVisibleMatches()) {',
        '						setCurrentMatchIndex(0);',
        '						displayMatches();',
        '					} else {',
        '						displayNoMatches();',
        '					}',
        '				}',
        '			}',
        '',
        '			function updateSearchFromFilters() {',
        '				if (currentSearch) {',
        '					if (currentSearch.hasMatches()) {',
        '						if (currentMatchIndex === null) {',
        '							currentMatchIndex = 0;',
        '						}',
        '						var currentMatch = currentSearch.matches[currentMatchIndex];',
        '						if (currentMatch.isVisible()) {',
        '							displayMatches();',
        '							setCurrentMatchIndex(currentMatchIndex);',
        '						} else {',
        '							currentMatch.setNotCurrent();',
        '							// Find the next visible match, if one exists',
        '							var nextVisibleMatchIndex = currentSearch.getNextVisibleMatchIndex();',
        '							if (nextVisibleMatchIndex > -1) {',
        '								setCurrentMatchIndex(nextVisibleMatchIndex);',
        '								displayMatches();',
        '							} else {',
        '								displayNoMatches();',
        '							}',
        '						}',
        '					} else {',
        '						displayNoMatches();',
        '					}',
        '				}',
        '			}',
        '',
        '			function refreshCurrentMatch() {',
        '				if (currentSearch && currentSearch.hasVisibleMatches()) {',
        '					setCurrentMatchIndex(currentMatchIndex);',
        '				}',
        '			}',
        '',
        '			function displayMatches() {',
        '				replaceClass($("searchBox"), "hasmatches", "nomatches");',
        '				$("searchBox").title = "" + currentSearch.matches.length + " matches found";',
        '				$("searchNav").style.display = "block";',
        '				setLogContainerHeight();',
        '			}',
        '',
        '			function displayNoMatches() {',
        '				replaceClass($("searchBox"), "nomatches", "hasmatches");',
        '				$("searchBox").title = "No matches found";',
        '				$("searchNav").style.display = "none";',
        '				setLogContainerHeight();',
        '			}',
        '',
        '			function toggleSearchEnabled(enable) {',
        '				enable = (typeof enable == "undefined") ? !$("searchDisable").checked : enable;',
        '				$("searchBox").disabled = !enable;',
        '				$("searchReset").disabled = !enable;',
        '				$("searchRegex").disabled = !enable;',
        '				$("searchNext").disabled = !enable;',
        '				$("searchPrevious").disabled = !enable;',
        '				$("searchCaseSensitive").disabled = !enable;',
        '				$("searchNav").style.display = (enable && ($("searchBox").value !== "") &&',
        '						currentSearch && currentSearch.hasVisibleMatches()) ?',
        '					"block" : "none";',
        '				if (enable) {',
        '					removeClass($("search"), "greyedout");',
        '					addClass(document.body, "searching");',
        '					if ($("searchHighlight").checked) {',
        '						addClass(logMainContainer, "searchhighlight");',
        '					} else {',
        '						removeClass(logMainContainer, "searchhighlight");',
        '					}',
        '					if ($("searchFilter").checked) {',
        '						addClass(logMainContainer, "searchfilter");',
        '					} else {',
        '						removeClass(logMainContainer, "searchfilter");',
        '					}',
        '					$("searchDisable").checked = !enable;',
        '				} else {',
        '					addClass($("search"), "greyedout");',
        '					removeClass(document.body, "searching");',
        '					removeClass(logMainContainer, "searchhighlight");',
        '					removeClass(logMainContainer, "searchfilter");',
        '				}',
        '				setLogContainerHeight();',
        '			}',
        '',
        '			function toggleSearchFilter() {',
        '				var enable = $("searchFilter").checked;',
        '				if (enable) {',
        '					addClass(logMainContainer, "searchfilter");',
        '				} else {',
        '					removeClass(logMainContainer, "searchfilter");',
        '				}',
        '				refreshCurrentMatch();',
        '			}',
        '',
        '			function toggleSearchHighlight() {',
        '				var enable = $("searchHighlight").checked;',
        '				if (enable) {',
        '					addClass(logMainContainer, "searchhighlight");',
        '				} else {',
        '					removeClass(logMainContainer, "searchhighlight");',
        '				}',
        '			}',
        '',
        '			function clearSearch() {',
        '				$("searchBox").value = "";',
        '				doSearch();',
        '			}',
        '',
        '			function searchNext() {',
        '				if (currentSearch !== null && currentMatchIndex !== null) {',
        '					currentSearch.matches[currentMatchIndex].setNotCurrent();',
        '					var nextMatchIndex = currentSearch.getNextVisibleMatchIndex();',
        '					if (nextMatchIndex > currentMatchIndex || confirm("Reached the end of the page. Start from the top?")) {',
        '						setCurrentMatchIndex(nextMatchIndex);',
        '					}',
        '				}',
        '			}',
        '',
        '			function searchPrevious() {',
        '				if (currentSearch !== null && currentMatchIndex !== null) {',
        '					currentSearch.matches[currentMatchIndex].setNotCurrent();',
        '					var previousMatchIndex = currentSearch.getPreviousVisibleMatchIndex();',
        '					if (previousMatchIndex < currentMatchIndex || confirm("Reached the start of the page. Continue from the bottom?")) {',
        '						setCurrentMatchIndex(previousMatchIndex);',
        '					}',
        '				}',
        '			}',
        '',
        '			function setCurrentMatchIndex(index) {',
        '				currentMatchIndex = index;',
        '				currentSearch.matches[currentMatchIndex].setCurrent();',
        '			}',
        '',
        '			/* ------------------------------------------------------------------------- */',
        '',
        '			// CSS Utilities',
        '',
        '			function addClass(el, cssClass) {',
        '				if (!hasClass(el, cssClass)) {',
        '					if (el.className) {',
        '						el.className += " " + cssClass;',
        '					} else {',
        '						el.className = cssClass;',
        '					}',
        '				}',
        '			}',
        '',
        '			function hasClass(el, cssClass) {',
        '				if (el.className) {',
        '					var classNames = el.className.split(" ");',
        '					return array_contains(classNames, cssClass);',
        '				}',
        '				return false;',
        '			}',
        '',
        '			function removeClass(el, cssClass) {',
        '				if (hasClass(el, cssClass)) {',
        '					// Rebuild the className property',
        '					var existingClasses = el.className.split(" ");',
        '					var newClasses = [];',
        '					for (var i = 0, len = existingClasses.length; i < len; i++) {',
        '						if (existingClasses[i] != cssClass) {',
        '							newClasses[newClasses.length] = existingClasses[i];',
        '						}',
        '					}',
        '					el.className = newClasses.join(" ");',
        '				}',
        '			}',
        '',
        '			function replaceClass(el, newCssClass, oldCssClass) {',
        '				removeClass(el, oldCssClass);',
        '				addClass(el, newCssClass);',
        '			}',
        '',
        '			/* ------------------------------------------------------------------------- */',
        '',
        '			// Other utility functions',
        '',
        '			function getElementsByClass(el, cssClass, tagName) {',
        '				var elements = el.getElementsByTagName(tagName);',
        '				var matches = [];',
        '				for (var i = 0, len = elements.length; i < len; i++) {',
        '					if (hasClass(elements[i], cssClass)) {',
        '						matches.push(elements[i]);',
        '					}',
        '				}',
        '				return matches;',
        '			}',
        '',
        '			// Syntax borrowed from Prototype library',
        '			function $(id) {',
        '				return document.getElementById(id);',
        '			}',
        '',
        '			function isDescendant(node, ancestorNode) {',
        '				while (node != null) {',
        '					if (node === ancestorNode) {',
        '						return true;',
        '					}',
        '					node = node.parentNode;',
        '				}',
        '				return false;',
        '			}',
        '',
        '			function isOrphan(node) {',
        '				var currentNode = node;',
        '				while (currentNode) {',
        '					if (currentNode == document.body) {',
        '						return false;',
        '					}',
        '					currentNode = currentNode.parentNode;',
        '				}',
        '				return true;',
        '			}',
        '',
        '			function escapeHtml(str) {',
        '				return str.replace(/&/g, "&amp;").replace(/[<]/g, "&lt;").replace(/>/g, "&gt;");',
        '			}',
        '',
        '			function getWindowWidth() {',
        '				if (window.innerWidth) {',
        '					return window.innerWidth;',
        '				} else if (document.documentElement && document.documentElement.clientWidth) {',
        '					return document.documentElement.clientWidth;',
        '				} else if (document.body) {',
        '					return document.body.clientWidth;',
        '				}',
        '				return 0;',
        '			}',
        '',
        '			function getWindowHeight() {',
        '				if (window.innerHeight) {',
        '					return window.innerHeight;',
        '				} else if (document.documentElement && document.documentElement.clientHeight) {',
        '					return document.documentElement.clientHeight;',
        '				} else if (document.body) {',
        '					return document.body.clientHeight;',
        '				}',
        '				return 0;',
        '			}',
        '',
        '			function getToolBarsHeight() {',
        '				return $("switches").offsetHeight;',
        '			}',
        '',
        '			function getChromeHeight() {',
        '				var height = getToolBarsHeight();',
        '				if (showCommandLine) {',
        '					height += $("commandLine").offsetHeight;',
        '				}',
        '				return height;',
        '			}',
        '',
        '			function setLogContainerHeight() {',
        '				if (logMainContainer) {',
        '					var windowHeight = getWindowHeight();',
        '					$("body").style.height = getWindowHeight() + "px";',
        '					logMainContainer.style.height = "" +',
        '						Math.max(0, windowHeight - getChromeHeight()) + "px";',
        '				}',
        '			}',
        '',
        '			function setCommandInputWidth() {',
        '				if (showCommandLine) {',
        '					$("command").style.width = "" + Math.max(0, $("commandLineContainer").offsetWidth -',
        '						($("evaluateButton").offsetWidth + 13)) + "px";',
        '				}',
        '			}',
        '',
        '			window.onresize = function() {',
        '				setCommandInputWidth();',
        '				setLogContainerHeight();',
        '			};',
        '',
        '			if (!Array.prototype.push) {',
        '				Array.prototype.push = function() {',
        '					for (var i = 0, len = arguments.length; i < len; i++){',
        '						this[this.length] = arguments[i];',
        '					}',
        '					return this.length;',
        '				};',
        '			}',
        '',
        '			if (!Array.prototype.pop) {',
        '				Array.prototype.pop = function() {',
        '					if (this.length > 0) {',
        '						var val = this[this.length - 1];',
        '						this.length = this.length - 1;',
        '						return val;',
        '					}',
        '				};',
        '			}',
        '',
        '			if (!Array.prototype.shift) {',
        '				Array.prototype.shift = function() {',
        '					if (this.length > 0) {',
        '						var firstItem = this[0];',
        '						for (var i = 0, len = this.length - 1; i < len; i++) {',
        '							this[i] = this[i + 1];',
        '						}',
        '						this.length = this.length - 1;',
        '						return firstItem;',
        '					}',
        '				};',
        '			}',
        '',
        '			if (!Array.prototype.splice) {',
        '				Array.prototype.splice = function(startIndex, deleteCount) {',
        '					var itemsAfterDeleted = this.slice(startIndex + deleteCount);',
        '					var itemsDeleted = this.slice(startIndex, startIndex + deleteCount);',
        '					this.length = startIndex;',
        '					// Copy the arguments into a proper Array object',
        '					var argumentsArray = [];',
        '					for (var i = 0, len = arguments.length; i < len; i++) {',
        '						argumentsArray[i] = arguments[i];',
        '					}',
        '					var itemsToAppend = (argumentsArray.length > 2) ?',
        '						itemsAfterDeleted = argumentsArray.slice(2).concat(itemsAfterDeleted) : itemsAfterDeleted;',
        '					for (i = 0, len = itemsToAppend.length; i < len; i++) {',
        '						this.push(itemsToAppend[i]);',
        '					}',
        '					return itemsDeleted;',
        '				};',
        '			}',
        '',
        '			function array_remove(arr, val) {',
        '				var index = -1;',
        '				for (var i = 0, len = arr.length; i < len; i++) {',
        '					if (arr[i] === val) {',
        '						index = i;',
        '						break;',
        '					}',
        '				}',
        '				if (index >= 0) {',
        '					arr.splice(index, 1);',
        '					return index;',
        '				} else {',
        '					return false;',
        '				}',
        '			}',
        '',
        '			function array_removeFromStart(array, numberToRemove) {',
        '				if (Array.prototype.splice) {',
        '					array.splice(0, numberToRemove);',
        '				} else {',
        '					for (var i = numberToRemove, len = array.length; i < len; i++) {',
        '						array[i - numberToRemove] = array[i];',
        '					}',
        '					array.length = array.length - numberToRemove;',
        '				}',
        '				return array;',
        '			}',
        '',
        '			function array_contains(arr, val) {',
        '				for (var i = 0, len = arr.length; i < len; i++) {',
        '					if (arr[i] == val) {',
        '						return true;',
        '					}',
        '				}',
        '				return false;',
        '			}',
        '',
        '			function getErrorMessage(ex) {',
        '				if (ex.message) {',
        '					return ex.message;',
        '				} else if (ex.description) {',
        '					return ex.description;',
        '				}',
        '				return "" + ex;',
        '			}',
        '',
        '			function moveCaretToEnd(input) {',
        '				if (input.setSelectionRange) {',
        '					input.focus();',
        '					var length = input.value.length;',
        '					input.setSelectionRange(length, length);',
        '				} else if (input.createTextRange) {',
        '					var range = input.createTextRange();',
        '					range.collapse(false);',
        '					range.select();',
        '				}',
        '				input.focus();',
        '			}',
        '',
        '			function stopPropagation(evt) {',
        '				if (evt.stopPropagation) {',
        '					evt.stopPropagation();',
        '				} else if (typeof evt.cancelBubble != "undefined") {',
        '					evt.cancelBubble = true;',
        '				}',
        '			}',
        '',
        '			function getEvent(evt) {',
        '				return evt ? evt : event;',
        '			}',
        '',
        '			function getTarget(evt) {',
        '				return evt.target ? evt.target : evt.srcElement;',
        '			}',
        '',
        '			function getRelatedTarget(evt) {',
        '				if (evt.relatedTarget) {',
        '					return evt.relatedTarget;',
        '				} else if (evt.srcElement) {',
        '					switch(evt.type) {',
        '						case "mouseover":',
        '							return evt.fromElement;',
        '						case "mouseout":',
        '							return evt.toElement;',
        '						default:',
        '							return evt.srcElement;',
        '					}',
        '				}',
        '			}',
        '',
        '			function cancelKeyEvent(evt) {',
        '				evt.returnValue = false;',
        '				stopPropagation(evt);',
        '			}',
        '',
        '			function evalCommandLine() {',
        '				var expr = $("command").value;',
        '				evalCommand(expr);',
        '				$("command").value = "";',
        '			}',
        '',
        '			function evalLastCommand() {',
        '				if (lastCommand != null) {',
        '					evalCommand(lastCommand);',
        '				}',
        '			}',
        '',
        '			var lastCommand = null;',
        '			var commandHistory = [];',
        '			var currentCommandIndex = 0;',
        '',
        '			function evalCommand(expr) {',
        '				if (appender) {',
        '					appender.evalCommandAndAppend(expr);',
        '				} else {',
        '					var prefix = ">>> " + expr + "\\r\\n";',
        '					try {',
        '						log("INFO", prefix + eval(expr));',
        '					} catch (ex) {',
        '						log("ERROR", prefix + "Error: " + getErrorMessage(ex));',
        '					}',
        '				}',
        '				// Update command history',
        '				if (expr != commandHistory[commandHistory.length - 1]) {',
        '					commandHistory.push(expr);',
        '					// Update the appender',
        '					if (appender) {',
        '						appender.storeCommandHistory(commandHistory);',
        '					}',
        '				}',
        '				currentCommandIndex = (expr == commandHistory[currentCommandIndex]) ? currentCommandIndex + 1 : commandHistory.length;',
        '				lastCommand = expr;',
        '			}',
        '			//]]>',
        '		</script>',
        '		<style type="text/css">',
        '			body {',
        '				background-color: white;',
        '				color: black;',
        '				padding: 0;',
        '				margin: 0;',
        '				font-family: tahoma, verdana, arial, helvetica, sans-serif;',
        '				overflow: hidden;',
        '			}',
        '',
        '			div#switchesContainer input {',
        '				margin-bottom: 0;',
        '			}',
        '',
        '			div.toolbar {',
        '				border-top: solid #ffffff 1px;',
        '				border-bottom: solid #aca899 1px;',
        '				background-color: #f1efe7;',
        '				padding: 3px 5px;',
        '				font-size: 68.75%;',
        '			}',
        '',
        '			div.toolbar, div#search input {',
        '				font-family: tahoma, verdana, arial, helvetica, sans-serif;',
        '			}',
        '',
        '			div.toolbar input.button {',
        '				padding: 0 5px;',
        '				font-size: 100%;',
        '			}',
        '',
        '			div.toolbar input.hidden {',
        '				display: none;',
        '			}',
        '',
        '			div#switches input#clearButton {',
        '				margin-left: 20px;',
        '			}',
        '',
        '			div#levels label {',
        '				font-weight: bold;',
        '			}',
        '',
        '			div#levels label, div#options label {',
        '				margin-right: 5px;',
        '			}',
        '',
        '			div#levels label#wrapLabel {',
        '				font-weight: normal;',
        '			}',
        '',
        '			div#search label {',
        '				margin-right: 10px;',
        '			}',
        '',
        '			div#search label.searchboxlabel {',
        '				margin-right: 0;',
        '			}',
        '',
        '			div#search input {',
        '				font-size: 100%;',
        '			}',
        '',
        '			div#search input.validregex {',
        '				color: green;',
        '			}',
        '',
        '			div#search input.invalidregex {',
        '				color: red;',
        '			}',
        '',
        '			div#search input.nomatches {',
        '				color: white;',
        '				background-color: #ff6666;',
        '			}',
        '',
        '			div#search input.nomatches {',
        '				color: white;',
        '				background-color: #ff6666;',
        '			}',
        '',
        '			div#searchNav {',
        '				display: none;',
        '			}',
        '',
        '			div#commandLine {',
        '				display: none;',
        '			}',
        '',
        '			div#commandLine input#command {',
        '				font-size: 100%;',
        '				font-family: Courier New, Courier;',
        '			}',
        '',
        '			div#commandLine input#evaluateButton {',
        '			}',
        '',
        '			*.greyedout {',
        '				color: gray !important;',
        '				border-color: gray !important;',
        '			}',
        '',
        '			*.greyedout *.alwaysenabled { color: black; }',
        '',
        '			*.unselectable {',
        '				-khtml-user-select: none;',
        '				-moz-user-select: none;',
        '				user-select: none;',
        '			}',
        '',
        '			div#log {',
        '				font-family: Courier New, Courier;',
        '				font-size: 75%;',
        '				width: 100%;',
        '				overflow: auto;',
        '				clear: both;',
        '				position: relative;',
        '			}',
        '',
        '			div.group {',
        '				border-color: #cccccc;',
        '				border-style: solid;',
        '				border-width: 1px 0 1px 1px;',
        '				overflow: visible;',
        '			}',
        '',
        '			div.oldIe div.group, div.oldIe div.group *, div.oldIe *.logentry {',
        '				height: 1%;',
        '			}',
        '',
        '			div.group div.groupheading span.expander {',
        '				border: solid black 1px;',
        '				font-family: Courier New, Courier;',
        '				font-size: 0.833em;',
        '				background-color: #eeeeee;',
        '				position: relative;',
        '				top: -1px;',
        '				color: black;',
        '				padding: 0 2px;',
        '				cursor: pointer;',
        '				cursor: hand;',
        '				height: 1%;',
        '			}',
        '',
        '			div.group div.groupcontent {',
        '				margin-left: 10px;',
        '				padding-bottom: 2px;',
        '				overflow: visible;',
        '			}',
        '',
        '			div.group div.expanded {',
        '				display: block;',
        '			}',
        '',
        '			div.group div.collapsed {',
        '				display: none;',
        '			}',
        '',
        '			*.logentry {',
        '				overflow: visible;',
        '				display: none;',
        '				white-space: pre;',
        '			}',
        '',
        '			span.pre {',
        '				white-space: pre;',
        '			}',
        '',
        '			pre.unwrapped {',
        '				display: inline !important;',
        '			}',
        '',
        '			pre.unwrapped pre.pre, div.wrapped pre.pre {',
        '				display: inline;',
        '			}',
        '',
        '			div.wrapped pre.pre {',
        '				white-space: normal;',
        '			}',
        '',
        '			div.wrapped {',
        '				display: none;',
        '			}',
        '',
        '			body.searching *.logentry span.currentmatch {',
        '				color: white !important;',
        '				background-color: green !important;',
        '			}',
        '',
        '			body.searching div.searchhighlight *.logentry span.searchterm {',
        '				color: black;',
        '				background-color: yellow;',
        '			}',
        '',
        '			div.wrap *.logentry {',
        '				white-space: normal !important;',
        '				border-width: 0 0 1px 0;',
        '				border-color: #dddddd;',
        '				border-style: dotted;',
        '			}',
        '',
        '			div.wrap #log_wrapped, #log_unwrapped {',
        '				display: block;',
        '			}',
        '',
        '			div.wrap #log_unwrapped, #log_wrapped {',
        '				display: none;',
        '			}',
        '',
        '			div.wrap *.logentry span.pre {',
        '				overflow: visible;',
        '				white-space: normal;',
        '			}',
        '',
        '			div.wrap *.logentry pre.unwrapped {',
        '				display: none;',
        '			}',
        '',
        '			div.wrap *.logentry span.wrapped {',
        '				display: inline;',
        '			}',
        '',
        '			div.searchfilter *.searchnonmatch {',
        '				display: none !important;',
        '			}',
        '',
        '			div#log *.TRACE, label#label_TRACE {',
        '				color: #666666;',
        '			}',
        '',
        '			div#log *.DEBUG, label#label_DEBUG {',
        '				color: green;',
        '			}',
        '',
        '			div#log *.INFO, label#label_INFO {',
        '				color: #000099;',
        '			}',
        '',
        '			div#log *.WARN, label#label_WARN {',
        '				color: #999900;',
        '			}',
        '',
        '			div#log *.ERROR, label#label_ERROR {',
        '				color: red;',
        '			}',
        '',
        '			div#log *.FATAL, label#label_FATAL {',
        '				color: #660066;',
        '			}',
        '',
        '			div.TRACE#log *.TRACE,',
        '			div.DEBUG#log *.DEBUG,',
        '			div.INFO#log *.INFO,',
        '			div.WARN#log *.WARN,',
        '			div.ERROR#log *.ERROR,',
        '			div.FATAL#log *.FATAL {',
        '				display: block;',
        '			}',
        '',
        '			div#log div.separator {',
        '				background-color: #cccccc;',
        '				margin: 5px 0;',
        '				line-height: 1px;',
        '			}',
        '		</style>',
        '	</head>',
        '',
        '	<body id="body">',
        '		<div id="switchesContainer">',
        '			<div id="switches">',
        '				<div id="levels" class="toolbar">',
        '					Filters:',
        '					<input type="checkbox" id="switch_TRACE" onclick="applyFilters(); checkAllLevels()" checked="checked" title="Show/hide trace messages" /><label for="switch_TRACE" id="label_TRACE">trace</label>',
        '					<input type="checkbox" id="switch_DEBUG" onclick="applyFilters(); checkAllLevels()" checked="checked" title="Show/hide debug messages" /><label for="switch_DEBUG" id="label_DEBUG">debug</label>',
        '					<input type="checkbox" id="switch_INFO" onclick="applyFilters(); checkAllLevels()" checked="checked" title="Show/hide info messages" /><label for="switch_INFO" id="label_INFO">info</label>',
        '					<input type="checkbox" id="switch_WARN" onclick="applyFilters(); checkAllLevels()" checked="checked" title="Show/hide warn messages" /><label for="switch_WARN" id="label_WARN">warn</label>',
        '					<input type="checkbox" id="switch_ERROR" onclick="applyFilters(); checkAllLevels()" checked="checked" title="Show/hide error messages" /><label for="switch_ERROR" id="label_ERROR">error</label>',
        '					<input type="checkbox" id="switch_FATAL" onclick="applyFilters(); checkAllLevels()" checked="checked" title="Show/hide fatal messages" /><label for="switch_FATAL" id="label_FATAL">fatal</label>',
        '					<input type="checkbox" id="switch_ALL" onclick="toggleAllLevels(); applyFilters()" checked="checked" title="Show/hide all messages" /><label for="switch_ALL" id="label_ALL">all</label>',
        '				</div>',
        '				<div id="search" class="toolbar">',
        '					<label for="searchBox" class="searchboxlabel">Search:</label> <input type="text" id="searchBox" onclick="toggleSearchEnabled(true)" onkeyup="scheduleSearch()" size="20" />',
        '					<input type="button" id="searchReset" disabled="disabled" value="Reset" onclick="clearSearch()" class="button" title="Reset the search" />',
        '					<input type="checkbox" id="searchRegex" onclick="doSearch()" title="If checked, search is treated as a regular expression" /><label for="searchRegex">Regex</label>',
        '					<input type="checkbox" id="searchCaseSensitive" onclick="doSearch()" title="If checked, search is case sensitive" /><label for="searchCaseSensitive">Match case</label>',
        '					<input type="checkbox" id="searchDisable" onclick="toggleSearchEnabled()" title="Enable/disable search" /><label for="searchDisable" class="alwaysenabled">Disable</label>',
        '					<div id="searchNav">',
        '						<input type="button" id="searchNext" disabled="disabled" value="Next" onclick="searchNext()" class="button" title="Go to the next matching log entry" />',
        '						<input type="button" id="searchPrevious" disabled="disabled" value="Previous" onclick="searchPrevious()" class="button" title="Go to the previous matching log entry" />',
        '						<input type="checkbox" id="searchFilter" onclick="toggleSearchFilter()" title="If checked, non-matching log entries are filtered out" /><label for="searchFilter">Filter</label>',
        '						<input type="checkbox" id="searchHighlight" onclick="toggleSearchHighlight()" title="Highlight matched search terms" /><label for="searchHighlight" class="alwaysenabled">Highlight all</label>',
        '					</div>',
        '				</div>',
        '				<div id="options" class="toolbar">',
        '					Options:',
        '					<input type="checkbox" id="enableLogging" onclick="toggleLoggingEnabled()" checked="checked" title="Enable/disable logging" /><label for="enableLogging" id="enableLoggingLabel">Log</label>',
        '					<input type="checkbox" id="wrap" onclick="toggleWrap()" title="Enable / disable word wrap" /><label for="wrap" id="wrapLabel">Wrap</label>',
        '					<input type="checkbox" id="newestAtTop" onclick="toggleNewestAtTop()" title="If checked, causes newest messages to appear at the top" /><label for="newestAtTop" id="newestAtTopLabel">Newest at the top</label>',
        '					<input type="checkbox" id="scrollToLatest" onclick="toggleScrollToLatest()" checked="checked" title="If checked, window automatically scrolls to a new message when it is added" /><label for="scrollToLatest" id="scrollToLatestLabel">Scroll to latest</label>',
        '					<input type="button" id="clearButton" value="Clear" onclick="clearLog()" class="button" title="Clear all log messages"  />',
        '					<input type="button" id="hideButton" value="Hide" onclick="hide()" class="hidden button" title="Hide the console" />',
        '					<input type="button" id="closeButton" value="Close" onclick="closeWindow()" class="hidden button" title="Close the window" />',
        '				</div>',
        '			</div>',
        '		</div>',
        '		<div id="log" class="TRACE DEBUG INFO WARN ERROR FATAL"></div>',
        '		<div id="commandLine" class="toolbar">',
        '			<div id="commandLineContainer">',
        '				<input type="text" id="command" title="Enter a JavaScript command here and hit return or press \'Evaluate\'" />',
        '				<input type="button" id="evaluateButton" value="Evaluate" class="button" title="Evaluate the command" onclick="evalCommandLine()" />',
        '			</div>',
        '		</div>',
        '	</body>',
        '</html>',
        ''
      ];
    };

    var defaultCommandLineFunctions = [];

    ConsoleAppender = function () {};

    var consoleAppenderIdCounter = 1;
    ConsoleAppender.prototype = new Appender();

    ConsoleAppender.prototype.create = function (inPage, container, lazyInit, initiallyMinimized, useDocumentWrite, width, height, focusConsoleWindow) {
      var appender = this;

      // Common properties
      var initialized = false;
      var consoleWindowCreated = false;
      var consoleWindowLoaded = false;
      var consoleClosed = false;

      var queuedLoggingEvents = [];
      var isSupported = true;
      var consoleAppenderId = consoleAppenderIdCounter++;

      // Local variables
      initiallyMinimized = extractBooleanFromParam(initiallyMinimized, this.defaults.initiallyMinimized);
      lazyInit = extractBooleanFromParam(lazyInit, this.defaults.lazyInit);
      useDocumentWrite = extractBooleanFromParam(useDocumentWrite, this.defaults.useDocumentWrite);
      var newestMessageAtTop = this.defaults.newestMessageAtTop;
      var scrollToLatestMessage = this.defaults.scrollToLatestMessage;
      width = width ? width : this.defaults.width;
      height = height ? height : this.defaults.height;
      var maxMessages = this.defaults.maxMessages;
      var showCommandLine = this.defaults.showCommandLine;
      var commandLineObjectExpansionDepth = this.defaults.commandLineObjectExpansionDepth;
      var showHideButton = this.defaults.showHideButton;
      var showCloseButton = this.defaults.showCloseButton;

      this.setLayout(this.defaults.layout);

      // Functions whose implementations vary between subclasses
      var init, createWindow, safeToAppend, getConsoleWindow, open;

      // Configuration methods. The function scope is used to prevent
      // direct alteration to the appender configuration properties.
      var appenderName = inPage ? 'InPageAppender' : 'PopUpAppender';
      var checkCanConfigure = function (configOptionName) {
        if (consoleWindowCreated) {
          handleError(appenderName + ": configuration option '" + configOptionName + "' may not be set after the appender has been initialized");
          return false;
        }
        return true;
      };

      var consoleWindowExists = function () {
        return consoleWindowLoaded && isSupported && !consoleClosed;
      };

      this.isNewestMessageAtTop = function () {
        return newestMessageAtTop;
      };
      this.setNewestMessageAtTop = function (newestMessageAtTopParam) {
        newestMessageAtTop = bool(newestMessageAtTopParam);
        if (consoleWindowExists()) {
          getConsoleWindow().setNewestAtTop(newestMessageAtTop);
        }
      };

      this.isScrollToLatestMessage = function () {
        return scrollToLatestMessage;
      };
      this.setScrollToLatestMessage = function (scrollToLatestMessageParam) {
        scrollToLatestMessage = bool(scrollToLatestMessageParam);
        if (consoleWindowExists()) {
          getConsoleWindow().setScrollToLatest(scrollToLatestMessage);
        }
      };

      this.getWidth = function () {
        return width;
      };
      this.setWidth = function (widthParam) {
        if (checkCanConfigure('width')) {
          width = extractStringFromParam(widthParam, width);
        }
      };

      this.getHeight = function () {
        return height;
      };
      this.setHeight = function (heightParam) {
        if (checkCanConfigure('height')) {
          height = extractStringFromParam(heightParam, height);
        }
      };

      this.getMaxMessages = function () {
        return maxMessages;
      };
      this.setMaxMessages = function (maxMessagesParam) {
        maxMessages = extractIntFromParam(maxMessagesParam, maxMessages);
        if (consoleWindowExists()) {
          getConsoleWindow().setMaxMessages(maxMessages);
        }
      };

      this.isShowCommandLine = function () {
        return showCommandLine;
      };
      this.setShowCommandLine = function (showCommandLineParam) {
        showCommandLine = bool(showCommandLineParam);
        if (consoleWindowExists()) {
          getConsoleWindow().setShowCommandLine(showCommandLine);
        }
      };

      this.isShowHideButton = function () {
        return showHideButton;
      };
      this.setShowHideButton = function (showHideButtonParam) {
        showHideButton = bool(showHideButtonParam);
        if (consoleWindowExists()) {
          getConsoleWindow().setShowHideButton(showHideButton);
        }
      };

      this.isShowCloseButton = function () {
        return showCloseButton;
      };
      this.setShowCloseButton = function (showCloseButtonParam) {
        showCloseButton = bool(showCloseButtonParam);
        if (consoleWindowExists()) {
          getConsoleWindow().setShowCloseButton(showCloseButton);
        }
      };

      this.getCommandLineObjectExpansionDepth = function () {
        return commandLineObjectExpansionDepth;
      };
      this.setCommandLineObjectExpansionDepth = function (commandLineObjectExpansionDepthParam) {
        commandLineObjectExpansionDepth = extractIntFromParam(commandLineObjectExpansionDepthParam, commandLineObjectExpansionDepth);
      };

      var minimized = initiallyMinimized;
      this.isInitiallyMinimized = function () {
        return initiallyMinimized;
      };
      this.setInitiallyMinimized = function (initiallyMinimizedParam) {
        if (checkCanConfigure('initiallyMinimized')) {
          initiallyMinimized = bool(initiallyMinimizedParam);
          minimized = initiallyMinimized;
        }
      };

      this.isUseDocumentWrite = function () {
        return useDocumentWrite;
      };
      this.setUseDocumentWrite = function (useDocumentWriteParam) {
        if (checkCanConfigure('useDocumentWrite')) {
          useDocumentWrite = bool(useDocumentWriteParam);
        }
      };

      // Common methods
      function QueuedLoggingEvent(loggingEvent, formattedMessage) {
        this.loggingEvent = loggingEvent;
        this.levelName = loggingEvent.level.name;
        this.formattedMessage = formattedMessage;
      }

      QueuedLoggingEvent.prototype.append = function () {
        getConsoleWindow().log(this.levelName, this.formattedMessage);
      };

      function QueuedGroup(name, initiallyExpanded) {
        this.name = name;
        this.initiallyExpanded = initiallyExpanded;
      }

      QueuedGroup.prototype.append = function () {
        getConsoleWindow().group(this.name, this.initiallyExpanded);
      };

      function QueuedGroupEnd() {}

      QueuedGroupEnd.prototype.append = function () {
        getConsoleWindow().groupEnd();
      };

      var checkAndAppend = function () {
        // Next line forces a check of whether the window has been closed
        safeToAppend();
        if (!initialized) {
          init();
        } else if (consoleClosed && reopenWhenClosed) {
          createWindow();
        }
        if (safeToAppend()) {
          appendQueuedLoggingEvents();
        }
      };

      this.append = function (loggingEvent) {
        if (isSupported) {
          // Format the message
          var formattedMessage = appender.getLayout().formatWithException(loggingEvent);
          queuedLoggingEvents.push(new QueuedLoggingEvent(loggingEvent, formattedMessage));
          checkAndAppend();
        }
      };

      this.group = function (name, initiallyExpanded) {
        if (isSupported) {
          queuedLoggingEvents.push(new QueuedGroup(name, initiallyExpanded));
          checkAndAppend();
        }
      };

      this.groupEnd = function () {
        if (isSupported) {
          queuedLoggingEvents.push(new QueuedGroupEnd());
          checkAndAppend();
        }
      };

      var appendQueuedLoggingEvents = function () {
        while (queuedLoggingEvents.length > 0) {
          queuedLoggingEvents.shift().append();
        }
        if (focusConsoleWindow) {
          getConsoleWindow().focus();
        }
      };

      this.setAddedToLogger = function (logger) {
        this.loggers.push(logger);
        if (enabled && !lazyInit) {
          init();
        }
      };

      this.clear = function () {
        if (consoleWindowExists()) {
          getConsoleWindow().clearLog();
        }
        queuedLoggingEvents.length = 0;
      };

      this.focus = function () {
        if (consoleWindowExists()) {
          getConsoleWindow().focus();
        }
      };

      this.focusCommandLine = function () {
        if (consoleWindowExists()) {
          getConsoleWindow().focusCommandLine();
        }
      };

      this.focusSearch = function () {
        if (consoleWindowExists()) {
          getConsoleWindow().focusSearch();
        }
      };

      var commandWindow = window;

      this.getCommandWindow = function () {
        return commandWindow;
      };
      this.setCommandWindow = function (commandWindowParam) {
        commandWindow = commandWindowParam;
      };

      this.executeLastCommand = function () {
        if (consoleWindowExists()) {
          getConsoleWindow().evalLastCommand();
        }
      };

      var commandLayout = new PatternLayout('%m');
      this.getCommandLayout = function () {
        return commandLayout;
      };
      this.setCommandLayout = function (commandLayoutParam) {
        commandLayout = commandLayoutParam;
      };

      this.evalCommandAndAppend = function (expr) {
        var commandReturnValue = { appendResult: true, isError: false };
        var commandOutput = '';
        // Evaluate the command
        try {
          var result, i;
          // The next three lines constitute a workaround for IE. Bizarrely, iframes seem to have no
          // eval method on the window object initially, but once execScript has been called on
          // it once then the eval method magically appears. See http://www.thismuchiknow.co.uk/?p=25
          if (!commandWindow.eval && commandWindow.execScript) {
            commandWindow.execScript('null');
          }

          var commandLineFunctionsHash = {};
          for (i = 0, len = commandLineFunctions.length; i < len; i++) {
            commandLineFunctionsHash[commandLineFunctions[i][0]] = commandLineFunctions[i][1];
          }

          // Keep an array of variables that are being changed in the command window so that they
          // can be restored to their original values afterwards
          var objectsToRestore = [];
          var addObjectToRestore = function (name) {
            objectsToRestore.push([name, commandWindow[name]]);
          };

          addObjectToRestore('appender');
          commandWindow.appender = appender;

          addObjectToRestore('commandReturnValue');
          commandWindow.commandReturnValue = commandReturnValue;

          addObjectToRestore('commandLineFunctionsHash');
          commandWindow.commandLineFunctionsHash = commandLineFunctionsHash;

          var addFunctionToWindow = function (name) {
            addObjectToRestore(name);
            commandWindow[name] = function () {
              return this.commandLineFunctionsHash[name](appender, arguments, commandReturnValue);
            };
          };

          for (i = 0, len = commandLineFunctions.length; i < len; i++) {
            addFunctionToWindow(commandLineFunctions[i][0]);
          }

          // Another bizarre workaround to get IE to eval in the global scope
          if (commandWindow === window && commandWindow.execScript) {
            addObjectToRestore('evalExpr');
            addObjectToRestore('result');
            window.evalExpr = expr;
            commandWindow.execScript('window.result=eval(window.evalExpr);');
            result = window.result;
          } else {
            result = commandWindow.eval(expr);
          }
          commandOutput = isUndefined(result) ? result : formatObjectExpansion(result, commandLineObjectExpansionDepth);

          // Restore variables in the command window to their original state
          for (i = 0, len = objectsToRestore.length; i < len; i++) {
            commandWindow[objectsToRestore[i][0]] = objectsToRestore[i][1];
          }
        } catch (ex) {
          commandOutput = 'Error evaluating command: ' + getExceptionStringRep(ex);
          commandReturnValue.isError = true;
        }
        // Append command output
        if (commandReturnValue.appendResult) {
          var message = '>>> ' + expr;
          if (!isUndefined(commandOutput)) {
            message += newLine + commandOutput;
          }
          var level = commandReturnValue.isError ? Level.ERROR : Level.INFO;
          var loggingEvent = new LoggingEvent(null, new Date(), level, [message], null);
          var mainLayout = this.getLayout();
          this.setLayout(commandLayout);
          this.append(loggingEvent);
          this.setLayout(mainLayout);
        }
      };

      var commandLineFunctions = defaultCommandLineFunctions.concat([]);

      this.addCommandLineFunction = function (functionName, commandLineFunction) {
        commandLineFunctions.push([functionName, commandLineFunction]);
      };

      var commandHistoryCookieName = 'log4javascriptCommandHistory';
      this.storeCommandHistory = function (commandHistory) {
        setCookie(commandHistoryCookieName, commandHistory.join(','));
      };

      var writeHtml = function (doc) {
        var lines = getConsoleHtmlLines();
        doc.open();
        for (var i = 0, len = lines.length; i < len; i++) {
          doc.writeln(lines[i]);
        }
        doc.close();
      };

      // Set up event listeners
      this.setEventTypes(['load', 'unload']);

      var consoleWindowLoadHandler = function () {
        var win = getConsoleWindow();
        win.setAppender(appender);
        win.setNewestAtTop(newestMessageAtTop);
        win.setScrollToLatest(scrollToLatestMessage);
        win.setMaxMessages(maxMessages);
        win.setShowCommandLine(showCommandLine);
        win.setShowHideButton(showHideButton);
        win.setShowCloseButton(showCloseButton);
        win.setMainWindow(window);

        // Restore command history stored in cookie
        var storedValue = getCookie(commandHistoryCookieName);
        if (storedValue) {
          win.commandHistory = storedValue.split(',');
          win.currentCommandIndex = win.commandHistory.length;
        }

        appender.dispatchEvent('load', { win: win });
      };

      this.unload = function () {
        logLog.debug('unload ' + this + ', caller: ' + this.unload.caller);
        if (!consoleClosed) {
          logLog.debug('really doing unload ' + this);
          consoleClosed = true;
          consoleWindowLoaded = false;
          consoleWindowCreated = false;
          appender.dispatchEvent('unload', {});
        }
      };

      var pollConsoleWindow = function (windowTest, interval, successCallback, errorMessage) {
        function doPoll() {
          try {
            // Test if the console has been closed while polling
            if (consoleClosed) {
              clearInterval(poll);
            }
            if (windowTest(getConsoleWindow())) {
              clearInterval(poll);
              successCallback();
            }
          } catch (ex) {
            clearInterval(poll);
            isSupported = false;
            handleError(errorMessage, ex);
          }
        }

        // Poll the pop-up since the onload event is not reliable
        var poll = setInterval(doPoll, interval);
      };

      var getConsoleUrl = function () {
        var documentDomainSet = document.domain != location.hostname;
        return useDocumentWrite
          ? ''
          : getBaseUrl() + 'console_uncompressed.html' + (documentDomainSet ? '?log4javascript_domain=' + escape(document.domain) : '');
      };

      // Define methods and properties that vary between subclasses
      if (inPage) {
        // InPageAppender

        var containerElement = null;

        // Configuration methods. The function scope is used to prevent
        // direct alteration to the appender configuration properties.
        var cssProperties = [];
        this.addCssProperty = function (name, value) {
          if (checkCanConfigure('cssProperties')) {
            cssProperties.push([name, value]);
          }
        };

        // Define useful variables
        var windowCreationStarted = false;
        var iframeContainerDiv;
        var iframeId = uniqueId + '_InPageAppender_' + consoleAppenderId;

        this.hide = function () {
          if (initialized && consoleWindowCreated) {
            if (consoleWindowExists()) {
              getConsoleWindow().$('command').blur();
            }
            iframeContainerDiv.style.display = 'none';
            minimized = true;
          }
        };

        this.show = function () {
          if (initialized) {
            if (consoleWindowCreated) {
              iframeContainerDiv.style.display = 'block';
              this.setShowCommandLine(showCommandLine); // Force IE to update
              minimized = false;
            } else if (!windowCreationStarted) {
              createWindow(true);
            }
          }
        };

        this.isVisible = function () {
          return !minimized && !consoleClosed;
        };

        this.close = function (fromButton) {
          if (
            !consoleClosed &&
            (!fromButton || confirm('This will permanently remove the console from the page. No more messages will be logged. Do you wish to continue?'))
          ) {
            iframeContainerDiv.parentNode.removeChild(iframeContainerDiv);
            this.unload();
          }
        };

        // Create open, init, getConsoleWindow and safeToAppend functions
        open = function () {
          var initErrorMessage = 'InPageAppender.open: unable to create console iframe';

          function finalInit() {
            try {
              if (!initiallyMinimized) {
                appender.show();
              }
              consoleWindowLoadHandler();
              consoleWindowLoaded = true;
              appendQueuedLoggingEvents();
            } catch (ex) {
              isSupported = false;
              handleError(initErrorMessage, ex);
            }
          }

          function writeToDocument() {
            try {
              var windowTest = function (win) {
                return isLoaded(win);
              };
              if (useDocumentWrite) {
                writeHtml(getConsoleWindow().document);
              }
              if (windowTest(getConsoleWindow())) {
                finalInit();
              } else {
                pollConsoleWindow(windowTest, 100, finalInit, initErrorMessage);
              }
            } catch (ex) {
              isSupported = false;
              handleError(initErrorMessage, ex);
            }
          }

          minimized = false;
          iframeContainerDiv = containerElement.appendChild(document.createElement('div'));

          iframeContainerDiv.style.width = width;
          iframeContainerDiv.style.height = height;
          iframeContainerDiv.style.border = 'solid gray 1px';

          for (var i = 0, len = cssProperties.length; i < len; i++) {
            iframeContainerDiv.style[cssProperties[i][0]] = cssProperties[i][1];
          }

          var iframeSrc = useDocumentWrite ? '' : " src='" + getConsoleUrl() + "'";

          // Adding an iframe using the DOM would be preferable, but it doesn't work
          // in IE5 on Windows, or in Konqueror prior to version 3.5 - in Konqueror
          // it creates the iframe fine but I haven't been able to find a way to obtain
          // the iframe's window object
          iframeContainerDiv.innerHTML =
            "<iframe id='" + iframeId + "' name='" + iframeId + "' width='100%' height='100%' frameborder='0'" + iframeSrc + " scrolling='no'></iframe>";
          consoleClosed = false;

          // Write the console HTML to the iframe
          var iframeDocumentExistsTest = function (win) {
            try {
              return bool(win) && bool(win.document);
            } catch (ex) {
              return false;
            }
          };
          if (iframeDocumentExistsTest(getConsoleWindow())) {
            writeToDocument();
          } else {
            pollConsoleWindow(iframeDocumentExistsTest, 100, writeToDocument, initErrorMessage);
          }
          consoleWindowCreated = true;
        };

        createWindow = function (show) {
          if (show || !initiallyMinimized) {
            var pageLoadHandler = function () {
              if (!container) {
                // Set up default container element
                containerElement = document.createElement('div');
                containerElement.style.position = 'fixed';
                containerElement.style.left = '0';
                containerElement.style.right = '0';
                containerElement.style.bottom = '0';
                document.body.appendChild(containerElement);
                appender.addCssProperty('borderWidth', '1px 0 0 0');
                appender.addCssProperty('zIndex', 1000000); // Can't find anything authoritative that says how big z-index can be
                open();
              } else {
                try {
                  var el = document.getElementById(container);
                  if (el.nodeType == 1) {
                    containerElement = el;
                  }
                  open();
                } catch (ex) {
                  handleError("InPageAppender.init: invalid container element '" + container + "' supplied", ex);
                }
              }
            };

            // Test the type of the container supplied. First, check if it's an element
            if (pageLoaded && container && container.appendChild) {
              containerElement = container;
              open();
            } else if (pageLoaded) {
              pageLoadHandler();
            } else {
              log4javascript.addEventListener('load', pageLoadHandler);
            }
            windowCreationStarted = true;
          }
        };

        init = function () {
          createWindow();
          initialized = true;
        };

        getConsoleWindow = function () {
          var iframe = window.frames[iframeId];
          if (iframe) {
            return iframe;
          }
        };

        safeToAppend = function () {
          if (isSupported && !consoleClosed) {
            if (consoleWindowCreated && !consoleWindowLoaded && getConsoleWindow() && isLoaded(getConsoleWindow())) {
              consoleWindowLoaded = true;
            }
            return consoleWindowLoaded;
          }
          return false;
        };
      } else {
        // PopUpAppender

        // Extract params
        var useOldPopUp = appender.defaults.useOldPopUp;
        var complainAboutPopUpBlocking = appender.defaults.complainAboutPopUpBlocking;
        var reopenWhenClosed = this.defaults.reopenWhenClosed;

        // Configuration methods. The function scope is used to prevent
        // direct alteration to the appender configuration properties.
        this.isUseOldPopUp = function () {
          return useOldPopUp;
        };
        this.setUseOldPopUp = function (useOldPopUpParam) {
          if (checkCanConfigure('useOldPopUp')) {
            useOldPopUp = bool(useOldPopUpParam);
          }
        };

        this.isComplainAboutPopUpBlocking = function () {
          return complainAboutPopUpBlocking;
        };
        this.setComplainAboutPopUpBlocking = function (complainAboutPopUpBlockingParam) {
          if (checkCanConfigure('complainAboutPopUpBlocking')) {
            complainAboutPopUpBlocking = bool(complainAboutPopUpBlockingParam);
          }
        };

        this.isFocusPopUp = function () {
          return focusConsoleWindow;
        };
        this.setFocusPopUp = function (focusPopUpParam) {
          // This property can be safely altered after logging has started
          focusConsoleWindow = bool(focusPopUpParam);
        };

        this.isReopenWhenClosed = function () {
          return reopenWhenClosed;
        };
        this.setReopenWhenClosed = function (reopenWhenClosedParam) {
          // This property can be safely altered after logging has started
          reopenWhenClosed = bool(reopenWhenClosedParam);
        };

        this.close = function () {
          logLog.debug('close ' + this);
          try {
            popUp.close();
            this.unload();
          } catch (ex) {
            // Do nothing
          }
        };

        this.hide = function () {
          logLog.debug('hide ' + this);
          if (consoleWindowExists()) {
            this.close();
          }
        };

        this.show = function () {
          logLog.debug('show ' + this);
          if (!consoleWindowCreated) {
            open();
          }
        };

        this.isVisible = function () {
          return safeToAppend();
        };

        // Define useful variables
        var popUp;

        // Create open, init, getConsoleWindow and safeToAppend functions
        open = function () {
          var windowProperties = 'width=' + width + ',height=' + height + ',status,resizable';
          var frameInfo = '';
          try {
            var frameEl = window.frameElement;
            if (frameEl) {
              frameInfo = '_' + frameEl.tagName + '_' + (frameEl.name || frameEl.id || '');
            }
          } catch (e) {
            frameInfo = '_inaccessibleParentFrame';
          }
          var windowName = 'PopUp_' + location.host.replace(/[^a-z0-9]/gi, '_') + '_' + consoleAppenderId + frameInfo;
          if (!useOldPopUp || !useDocumentWrite) {
            // Ensure a previous window isn't used by using a unique name
            windowName = windowName + '_' + uniqueId;
          }

          var checkPopUpClosed = function (win) {
            if (consoleClosed) {
              return true;
            } else {
              try {
                return bool(win) && win.closed;
              } catch (ex) {}
            }
            return false;
          };

          var popUpClosedCallback = function () {
            if (!consoleClosed) {
              appender.unload();
            }
          };

          function finalInit() {
            getConsoleWindow().setCloseIfOpenerCloses(!useOldPopUp || !useDocumentWrite);
            consoleWindowLoadHandler();
            consoleWindowLoaded = true;
            appendQueuedLoggingEvents();
            pollConsoleWindow(checkPopUpClosed, 500, popUpClosedCallback, 'PopUpAppender.checkPopUpClosed: error checking pop-up window');
          }

          try {
            popUp = window.open(getConsoleUrl(), windowName, windowProperties);
            consoleClosed = false;
            consoleWindowCreated = true;
            if (popUp && popUp.document) {
              if (useDocumentWrite && useOldPopUp && isLoaded(popUp)) {
                popUp.mainPageReloaded();
                finalInit();
              } else {
                if (useDocumentWrite) {
                  writeHtml(popUp.document);
                }
                // Check if the pop-up window object is available
                var popUpLoadedTest = function (win) {
                  return bool(win) && isLoaded(win);
                };
                if (isLoaded(popUp)) {
                  finalInit();
                } else {
                  pollConsoleWindow(popUpLoadedTest, 100, finalInit, 'PopUpAppender.init: unable to create console window');
                }
              }
            } else {
              isSupported = false;
              logLog.warn('PopUpAppender.init: pop-ups blocked, please unblock to use PopUpAppender');
              if (complainAboutPopUpBlocking) {
                handleError('log4javascript: pop-up windows appear to be blocked. Please unblock them to use pop-up logging.');
              }
            }
          } catch (ex) {
            handleError('PopUpAppender.init: error creating pop-up', ex);
          }
        };

        createWindow = function () {
          if (!initiallyMinimized) {
            open();
          }
        };

        init = function () {
          createWindow();
          initialized = true;
        };

        getConsoleWindow = function () {
          return popUp;
        };

        safeToAppend = function () {
          if (isSupported && !isUndefined(popUp) && !consoleClosed) {
            if (popUp.closed || (consoleWindowLoaded && isUndefined(popUp.closed))) {
              // Extra check for Opera
              appender.unload();
              logLog.debug('PopUpAppender: pop-up closed');
              return false;
            }
            if (!consoleWindowLoaded && isLoaded(popUp)) {
              consoleWindowLoaded = true;
            }
          }
          return isSupported && consoleWindowLoaded && !consoleClosed;
        };
      }

      // Expose getConsoleWindow so that automated tests can check the DOM
      this.getConsoleWindow = getConsoleWindow;
    };

    ConsoleAppender.addGlobalCommandLineFunction = function (functionName, commandLineFunction) {
      defaultCommandLineFunctions.push([functionName, commandLineFunction]);
    };

    /* ------------------------------------------------------------------ */

    function PopUpAppender(lazyInit, initiallyMinimized, useDocumentWrite, width, height) {
      this.create(false, null, lazyInit, initiallyMinimized, useDocumentWrite, width, height, this.defaults.focusPopUp);
    }

    PopUpAppender.prototype = new ConsoleAppender();

    PopUpAppender.prototype.defaults = {
      layout: new PatternLayout('%d{HH:mm:ss} %-5p - %m{1}%n'),
      initiallyMinimized: false,
      focusPopUp: false,
      lazyInit: true,
      useOldPopUp: true,
      complainAboutPopUpBlocking: true,
      newestMessageAtTop: false,
      scrollToLatestMessage: true,
      width: '600',
      height: '400',
      reopenWhenClosed: false,
      maxMessages: null,
      showCommandLine: true,
      commandLineObjectExpansionDepth: 1,
      showHideButton: false,
      showCloseButton: true,
      useDocumentWrite: true
    };

    PopUpAppender.prototype.toString = function () {
      return 'PopUpAppender';
    };

    log4javascript.PopUpAppender = PopUpAppender;

    /* ------------------------------------------------------------------ */

    function InPageAppender(container, lazyInit, initiallyMinimized, useDocumentWrite, width, height) {
      this.create(true, container, lazyInit, initiallyMinimized, useDocumentWrite, width, height, false);
    }

    InPageAppender.prototype = new ConsoleAppender();

    InPageAppender.prototype.defaults = {
      layout: new PatternLayout('%d{HH:mm:ss} %-5p - %m{1}%n'),
      initiallyMinimized: false,
      lazyInit: true,
      newestMessageAtTop: false,
      scrollToLatestMessage: true,
      width: '100%',
      height: '220px',
      maxMessages: null,
      showCommandLine: true,
      commandLineObjectExpansionDepth: 1,
      showHideButton: false,
      showCloseButton: false,
      showLogEntryDeleteButtons: true,
      useDocumentWrite: true
    };

    InPageAppender.prototype.toString = function () {
      return 'InPageAppender';
    };

    log4javascript.InPageAppender = InPageAppender;

    // Next line for backwards compatibility
    log4javascript.InlineAppender = InPageAppender;
  })();
  /* ---------------------------------------------------------------------- */
  // Console extension functions

  function padWithSpaces(str, len) {
    if (str.length < len) {
      var spaces = [];
      var numberOfSpaces = Math.max(0, len - str.length);
      for (var i = 0; i < numberOfSpaces; i++) {
        spaces[i] = ' ';
      }
      str += spaces.join('');
    }
    return str;
  }

  (function () {
    function dir(obj) {
      var maxLen = 0;
      // Obtain the length of the longest property name
      for (var p in obj) {
        maxLen = Math.max(toStr(p).length, maxLen);
      }
      // Create the nicely formatted property list
      var propList = [];
      for (p in obj) {
        var propNameStr = '  ' + padWithSpaces(toStr(p), maxLen + 2);
        var propVal;
        try {
          propVal = splitIntoLines(toStr(obj[p])).join(padWithSpaces(newLine, maxLen + 6));
        } catch (ex) {
          propVal = '[Error obtaining property. Details: ' + getExceptionMessage(ex) + ']';
        }
        propList.push(propNameStr + propVal);
      }
      return propList.join(newLine);
    }

    var nodeTypes = {
      ELEMENT_NODE: 1,
      ATTRIBUTE_NODE: 2,
      TEXT_NODE: 3,
      CDATA_SECTION_NODE: 4,
      ENTITY_REFERENCE_NODE: 5,
      ENTITY_NODE: 6,
      PROCESSING_INSTRUCTION_NODE: 7,
      COMMENT_NODE: 8,
      DOCUMENT_NODE: 9,
      DOCUMENT_TYPE_NODE: 10,
      DOCUMENT_FRAGMENT_NODE: 11,
      NOTATION_NODE: 12
    };

    var preFormattedElements = ['script', 'pre'];

    // This should be the definitive list, as specified by the XHTML 1.0 Transitional DTD
    var emptyElements = ['br', 'img', 'hr', 'param', 'link', 'area', 'input', 'col', 'base', 'meta'];
    var indentationUnit = '  ';

    // Create and return an XHTML string from the node specified
    function getXhtml(rootNode, includeRootNode, indentation, startNewLine, preformatted) {
      includeRootNode = typeof includeRootNode == 'undefined' ? true : !!includeRootNode;
      if (typeof indentation != 'string') {
        indentation = '';
      }
      startNewLine = !!startNewLine;
      preformatted = !!preformatted;
      var xhtml;

      function isWhitespace(node) {
        return node.nodeType == nodeTypes.TEXT_NODE && /^[ \t\r\n]*$/.test(node.nodeValue);
      }

      function fixAttributeValue(attrValue) {
        return attrValue.toString().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
      }

      function getStyleAttributeValue(el) {
        var stylePairs = el.style.cssText.split(';');
        var styleValue = '';
        for (var j = 0, len = stylePairs.length; j < len; j++) {
          var nameValueBits = stylePairs[j].split(':');
          var props = [];
          if (!/^\s*$/.test(nameValueBits[0])) {
            props.push(trim(nameValueBits[0]).toLowerCase() + ':' + trim(nameValueBits[1]));
          }
          styleValue = props.join(';');
        }
        return styleValue;
      }

      function getNamespace(el) {
        if (el.prefix) {
          return el.prefix;
        } else if (el.outerHTML) {
          var regex = new RegExp('<([^:]+):' + el.tagName + '[^>]*>', 'i');
          if (regex.test(el.outerHTML)) {
            return RegExp.$1.toLowerCase();
          }
        }
        return '';
      }

      var lt = '<';
      var gt = '>';
      var i, len;

      if (includeRootNode && rootNode.nodeType != nodeTypes.DOCUMENT_FRAGMENT_NODE) {
        switch (rootNode.nodeType) {
          case nodeTypes.ELEMENT_NODE:
            var tagName = rootNode.tagName.toLowerCase();
            xhtml = startNewLine ? newLine + indentation : '';
            xhtml += lt;
            // Allow for namespaces, where present
            var prefix = getNamespace(rootNode);
            var hasPrefix = !!prefix;
            if (hasPrefix) {
              xhtml += prefix + ':';
            }
            xhtml += tagName;
            for (i = 0, len = rootNode.attributes.length; i < len; i++) {
              var currentAttr = rootNode.attributes[i];
              // Check the attribute is valid.
              if (
                !currentAttr.specified ||
                currentAttr.nodeValue === null ||
                currentAttr.nodeName.toLowerCase() === 'style' ||
                typeof currentAttr.nodeValue !== 'string' ||
                currentAttr.nodeName.indexOf('_moz') === 0
              ) {
                continue;
              }
              xhtml += ' ' + currentAttr.nodeName.toLowerCase() + '="';
              xhtml += fixAttributeValue(currentAttr.nodeValue);
              xhtml += '"';
            }
            // Style needs to be done separately as it is not reported as an
            // attribute in IE
            if (rootNode.style.cssText) {
              var styleValue = getStyleAttributeValue(rootNode);
              if (styleValue !== '') {
                xhtml += ' style="' + getStyleAttributeValue(rootNode) + '"';
              }
            }
            if (array_contains(emptyElements, tagName) || (hasPrefix && !rootNode.hasChildNodes())) {
              xhtml += '/' + gt;
            } else {
              xhtml += gt;
              // Add output for childNodes collection (which doesn't include attribute nodes)
              var childStartNewLine = !(rootNode.childNodes.length === 1 && rootNode.childNodes[0].nodeType === nodeTypes.TEXT_NODE);
              var childPreformatted = array_contains(preFormattedElements, tagName);
              for (i = 0, len = rootNode.childNodes.length; i < len; i++) {
                xhtml += getXhtml(rootNode.childNodes[i], true, indentation + indentationUnit, childStartNewLine, childPreformatted);
              }
              // Add the end tag
              var endTag = lt + '/' + tagName + gt;
              xhtml += childStartNewLine ? newLine + indentation + endTag : endTag;
            }
            return xhtml;
          case nodeTypes.TEXT_NODE:
            if (isWhitespace(rootNode)) {
              xhtml = '';
            } else {
              if (preformatted) {
                xhtml = rootNode.nodeValue;
              } else {
                // Trim whitespace from each line of the text node
                var lines = splitIntoLines(trim(rootNode.nodeValue));
                var trimmedLines = [];
                for (i = 0, len = lines.length; i < len; i++) {
                  trimmedLines[i] = trim(lines[i]);
                }
                xhtml = trimmedLines.join(newLine + indentation);
              }
              if (startNewLine) {
                xhtml = newLine + indentation + xhtml;
              }
            }
            return xhtml;
          case nodeTypes.CDATA_SECTION_NODE:
            return '<![CDA' + 'TA[' + rootNode.nodeValue + ']' + ']>' + newLine;
          case nodeTypes.DOCUMENT_NODE:
            xhtml = '';
            // Add output for childNodes collection (which doesn't include attribute nodes)
            for (i = 0, len = rootNode.childNodes.length; i < len; i++) {
              xhtml += getXhtml(rootNode.childNodes[i], true, indentation);
            }
            return xhtml;
          default:
            return '';
        }
      } else {
        xhtml = '';
        // Add output for childNodes collection (which doesn't include attribute nodes)
        for (i = 0, len = rootNode.childNodes.length; i < len; i++) {
          xhtml += getXhtml(rootNode.childNodes[i], true, indentation + indentationUnit);
        }
        return xhtml;
      }
    }

    function createCommandLineFunctions() {
      ConsoleAppender.addGlobalCommandLineFunction('$', function (appender, args, returnValue) {
        return document.getElementById(args[0]);
      });

      ConsoleAppender.addGlobalCommandLineFunction('dir', function (appender, args, returnValue) {
        var lines = [];
        for (var i = 0, len = args.length; i < len; i++) {
          lines[i] = dir(args[i]);
        }
        return lines.join(newLine + newLine);
      });

      ConsoleAppender.addGlobalCommandLineFunction('dirxml', function (appender, args, returnValue) {
        var lines = [];
        for (var i = 0, len = args.length; i < len; i++) {
          lines[i] = getXhtml(args[i]);
        }
        return lines.join(newLine + newLine);
      });

      ConsoleAppender.addGlobalCommandLineFunction('cd', function (appender, args, returnValue) {
        var win, message;
        if (args.length === 0 || args[0] === '') {
          win = window;
          message = 'Command line set to run in main window';
        } else {
          if (args[0].window == args[0]) {
            win = args[0];
            message = "Command line set to run in frame '" + args[0].name + "'";
          } else {
            win = window.frames[args[0]];
            if (win) {
              message = "Command line set to run in frame '" + args[0] + "'";
            } else {
              returnValue.isError = true;
              message = "Frame '" + args[0] + "' does not exist";
              win = appender.getCommandWindow();
            }
          }
        }
        appender.setCommandWindow(win);
        return message;
      });

      ConsoleAppender.addGlobalCommandLineFunction('clear', function (appender, args, returnValue) {
        returnValue.appendResult = false;
        appender.clear();
      });

      ConsoleAppender.addGlobalCommandLineFunction('keys', function (appender, args, returnValue) {
        var keys = [];
        for (var k in args[0]) {
          keys.push(k);
        }
        return keys;
      });

      ConsoleAppender.addGlobalCommandLineFunction('values', function (appender, args, returnValue) {
        var values = [];
        for (var k in args[0]) {
          try {
            values.push(args[0][k]);
          } catch (ex) {
            logLog.warn('values(): Unable to obtain value for key ' + k + '. Details: ' + getExceptionMessage(ex));
          }
        }
        return values;
      });

      ConsoleAppender.addGlobalCommandLineFunction('expansionDepth', function (appender, args, returnValue) {
        var expansionDepth = parseInt(args[0], 10);
        if (isNaN(expansionDepth) || expansionDepth < 0) {
          returnValue.isError = true;
          return '' + args[0] + ' is not a valid expansion depth';
        } else {
          appender.setCommandLineObjectExpansionDepth(expansionDepth);
          return 'Object expansion depth set to ' + expansionDepth;
        }
      });
    }

    function init() {
      // Add command line functions
      createCommandLineFunctions();
    }

    /* ------------------------------------------------------------------ */

    init();
  })();

  /* ---------------------------------------------------------------------- */

  function createDefaultLogger() {
    var logger = log4javascript.getLogger(defaultLoggerName);
    var a = new log4javascript.PopUpAppender();
    logger.addAppender(a);
    return logger;
  }

  /* ---------------------------------------------------------------------- */
  // Main load

  log4javascript.setDocumentReady = function () {
    pageLoaded = true;
    log4javascript.dispatchEvent('load', {});
  };

  if (window.addEventListener) {
    window.addEventListener('load', log4javascript.setDocumentReady, false);
  } else if (window.attachEvent) {
    window.attachEvent('onload', log4javascript.setDocumentReady);
  } else {
    var oldOnload = window.onload;
    if (typeof window.onload != 'function') {
      window.onload = log4javascript.setDocumentReady;
    } else {
      window.onload = function (evt) {
        if (oldOnload) {
          oldOnload(evt);
        }
        log4javascript.setDocumentReady();
      };
    }
  }

  return log4javascript;
}, this);
(function (f) {
  if (typeof exports === 'object' && typeof module !== 'undefined') {
    module.exports = f();
  } else if (typeof define === 'function' && define.amd) {
    define([], f);
  } else {
    var g;
    if (typeof window !== 'undefined') {
      g = window;
    } else if (typeof global !== 'undefined') {
      g = global;
    } else if (typeof self !== 'undefined') {
      g = self;
    } else {
      g = this;
    }
    g.libphonenumber = f();
  }
})(function () {
  var define, module, exports;
  return (function () {
    function r(e, n, t) {
      function o(i, f) {
        if (!n[i]) {
          if (!e[i]) {
            var c = 'function' == typeof require && require;
            if (!f && c) return c(i, !0);
            if (u) return u(i, !0);
            var a = new Error("Cannot find module '" + i + "'");
            throw ((a.code = 'MODULE_NOT_FOUND'), a);
          }
          var p = (n[i] = { exports: {} });
          e[i][0].call(
            p.exports,
            function (r) {
              var n = e[i][1][r];
              return o(n || r);
            },
            p,
            p.exports,
            r,
            e,
            n,
            t
          );
        }
        return n[i].exports;
      }
      for (var u = 'function' == typeof require && require, i = 0; i < t.length; i++) o(t[i]);
      return o;
    }
    return r;
  })()(
    {
      1: [
        function (require, module, exports) {
          (function (global) {
            (function () {
              /*

 Copyright The Closure Library Authors.
 SPDX-License-Identifier: Apache-2.0
*/
              var $jscomp = $jscomp || {};
              $jscomp.scope = {};
              $jscomp.createTemplateTagFirstArg = function (a) {
                return (a.raw = a);
              };
              $jscomp.createTemplateTagFirstArgWithRaw = function (a, b) {
                a.raw = b;
                return a;
              };
              $jscomp.arrayIteratorImpl = function (a) {
                var b = 0;
                return function () {
                  return b < a.length ? { done: !1, value: a[b++] } : { done: !0 };
                };
              };
              $jscomp.arrayIterator = function (a) {
                return { next: $jscomp.arrayIteratorImpl(a) };
              };
              $jscomp.makeIterator = function (a) {
                var b = 'undefined' != typeof Symbol && Symbol.iterator && a[Symbol.iterator];
                return b ? b.call(a) : $jscomp.arrayIterator(a);
              };
              $jscomp.arrayFromIterator = function (a) {
                for (var b, c = []; !(b = a.next()).done; ) c.push(b.value);
                return c;
              };
              $jscomp.arrayFromIterable = function (a) {
                return a instanceof Array ? a : $jscomp.arrayFromIterator($jscomp.makeIterator(a));
              };
              $jscomp.checkStringArgs = function (a, b, c) {
                if (null == a) throw new TypeError("The 'this' value for String.prototype." + c + ' must not be null or undefined');
                if (b instanceof RegExp) throw new TypeError('First argument to String.prototype.' + c + ' must not be a regular expression');
                return a + '';
              };
              $jscomp.ASSUME_ES5 = !1;
              $jscomp.ASSUME_NO_NATIVE_MAP = !1;
              $jscomp.ASSUME_NO_NATIVE_SET = !1;
              $jscomp.SIMPLE_FROUND_POLYFILL = !1;
              $jscomp.ISOLATE_POLYFILLS = !1;
              $jscomp.FORCE_POLYFILL_PROMISE = !1;
              $jscomp.FORCE_POLYFILL_PROMISE_WHEN_NO_UNHANDLED_REJECTION = !1;
              $jscomp.defineProperty =
                $jscomp.ASSUME_ES5 || 'function' == typeof Object.defineProperties
                  ? Object.defineProperty
                  : function (a, b, c) {
                      if (a == Array.prototype || a == Object.prototype) return a;
                      a[b] = c.value;
                      return a;
                    };
              $jscomp.getGlobal = function (a) {
                a = [
                  'object' == typeof globalThis && globalThis,
                  a,
                  'object' == typeof window && window,
                  'object' == typeof self && self,
                  'object' == typeof global && global
                ];
                for (var b = 0; b < a.length; ++b) {
                  var c = a[b];
                  if (c && c.Math == Math) return c;
                }
                throw Error('Cannot find global object');
              };
              $jscomp.global = $jscomp.getGlobal(this);
              $jscomp.IS_SYMBOL_NATIVE = 'function' === typeof Symbol && 'symbol' === typeof Symbol('x');
              $jscomp.TRUST_ES6_POLYFILLS = !$jscomp.ISOLATE_POLYFILLS || $jscomp.IS_SYMBOL_NATIVE;
              $jscomp.polyfills = {};
              $jscomp.propertyToPolyfillSymbol = {};
              $jscomp.POLYFILL_PREFIX = '$jscp$';
              var $jscomp$lookupPolyfilledValue = function (a, b) {
                var c = $jscomp.propertyToPolyfillSymbol[b];
                if (null == c) return a[b];
                c = a[c];
                return void 0 !== c ? c : a[b];
              };
              $jscomp.polyfill = function (a, b, c, d) {
                b && ($jscomp.ISOLATE_POLYFILLS ? $jscomp.polyfillIsolated(a, b, c, d) : $jscomp.polyfillUnisolated(a, b, c, d));
              };
              $jscomp.polyfillUnisolated = function (a, b, c, d) {
                c = $jscomp.global;
                a = a.split('.');
                for (d = 0; d < a.length - 1; d++) {
                  var e = a[d];
                  if (!(e in c)) return;
                  c = c[e];
                }
                a = a[a.length - 1];
                d = c[a];
                b = b(d);
                b != d && null != b && $jscomp.defineProperty(c, a, { configurable: !0, writable: !0, value: b });
              };
              $jscomp.polyfillIsolated = function (a, b, c, d) {
                var e = a.split('.');
                a = 1 === e.length;
                d = e[0];
                d = !a && d in $jscomp.polyfills ? $jscomp.polyfills : $jscomp.global;
                for (var f = 0; f < e.length - 1; f++) {
                  var g = e[f];
                  if (!(g in d)) return;
                  d = d[g];
                }
                e = e[e.length - 1];
                c = $jscomp.IS_SYMBOL_NATIVE && 'es6' === c ? d[e] : null;
                b = b(c);
                null != b &&
                  (a
                    ? $jscomp.defineProperty($jscomp.polyfills, e, { configurable: !0, writable: !0, value: b })
                    : b !== c &&
                      (($jscomp.propertyToPolyfillSymbol[e] = $jscomp.IS_SYMBOL_NATIVE ? $jscomp.global.Symbol(e) : $jscomp.POLYFILL_PREFIX + e),
                      (e = $jscomp.propertyToPolyfillSymbol[e]),
                      $jscomp.defineProperty(d, e, { configurable: !0, writable: !0, value: b })));
              };
              $jscomp.polyfill(
                'String.prototype.endsWith',
                function (a) {
                  return a
                    ? a
                    : function (b, c) {
                        var d = $jscomp.checkStringArgs(this, b, 'endsWith');
                        b += '';
                        void 0 === c && (c = d.length);
                        c = Math.max(0, Math.min(c | 0, d.length));
                        for (var e = b.length; 0 < e && 0 < c; ) if (d[--c] != b[--e]) return !1;
                        return 0 >= e;
                      };
                },
                'es6',
                'es3'
              );
              $jscomp.findInternal = function (a, b, c) {
                a instanceof String && (a = String(a));
                for (var d = a.length, e = 0; e < d; e++) {
                  var f = a[e];
                  if (b.call(c, f, e, a)) return { i: e, v: f };
                }
                return { i: -1, v: void 0 };
              };
              $jscomp.polyfill(
                'Array.prototype.find',
                function (a) {
                  return a
                    ? a
                    : function (b, c) {
                        return $jscomp.findInternal(this, b, c).v;
                      };
                },
                'es6',
                'es3'
              );
              $jscomp.polyfill(
                'String.prototype.startsWith',
                function (a) {
                  return a
                    ? a
                    : function (b, c) {
                        var d = $jscomp.checkStringArgs(this, b, 'startsWith');
                        b += '';
                        var e = d.length,
                          f = b.length;
                        c = Math.max(0, Math.min(c | 0, d.length));
                        for (var g = 0; g < f && c < e; ) if (d[c++] != b[g++]) return !1;
                        return g >= f;
                      };
                },
                'es6',
                'es3'
              );
              $jscomp.polyfill(
                'String.prototype.repeat',
                function (a) {
                  return a
                    ? a
                    : function (b) {
                        var c = $jscomp.checkStringArgs(this, null, 'repeat');
                        if (0 > b || 1342177279 < b) throw new RangeError('Invalid count value');
                        b |= 0;
                        for (var d = ''; b; ) if ((b & 1 && (d += c), (b >>>= 1))) c += c;
                        return d;
                      };
                },
                'es6',
                'es3'
              );
              $jscomp.initSymbol = function () {};
              $jscomp.polyfill(
                'Symbol',
                function (a) {
                  if (a) return a;
                  var b = function (e, f) {
                    this.$jscomp$symbol$id_ = e;
                    $jscomp.defineProperty(this, 'description', { configurable: !0, writable: !0, value: f });
                  };
                  b.prototype.toString = function () {
                    return this.$jscomp$symbol$id_;
                  };
                  var c = 0,
                    d = function (e) {
                      if (this instanceof d) throw new TypeError('Symbol is not a constructor');
                      return new b('jscomp_symbol_' + (e || '') + '_' + c++, e);
                    };
                  return d;
                },
                'es6',
                'es3'
              );
              $jscomp.polyfill(
                'Symbol.iterator',
                function (a) {
                  if (a) return a;
                  a = Symbol('Symbol.iterator');
                  for (
                    var b = 'Array Int8Array Uint8Array Uint8ClampedArray Int16Array Uint16Array Int32Array Uint32Array Float32Array Float64Array'.split(' '),
                      c = 0;
                    c < b.length;
                    c++
                  ) {
                    var d = $jscomp.global[b[c]];
                    'function' === typeof d &&
                      'function' != typeof d.prototype[a] &&
                      $jscomp.defineProperty(d.prototype, a, {
                        configurable: !0,
                        writable: !0,
                        value: function () {
                          return $jscomp.iteratorPrototype($jscomp.arrayIteratorImpl(this));
                        }
                      });
                  }
                  return a;
                },
                'es6',
                'es3'
              );
              $jscomp.iteratorPrototype = function (a) {
                a = { next: a };
                a[Symbol.iterator] = function () {
                  return this;
                };
                return a;
              };
              $jscomp.iteratorFromArray = function (a, b) {
                a instanceof String && (a += '');
                var c = 0,
                  d = !1,
                  e = {
                    next: function () {
                      if (!d && c < a.length) {
                        var f = c++;
                        return { value: b(f, a[f]), done: !1 };
                      }
                      d = !0;
                      return { done: !0, value: void 0 };
                    }
                  };
                e[Symbol.iterator] = function () {
                  return e;
                };
                return e;
              };
              $jscomp.polyfill(
                'Array.prototype.keys',
                function (a) {
                  return a
                    ? a
                    : function () {
                        return $jscomp.iteratorFromArray(this, function (b) {
                          return b;
                        });
                      };
                },
                'es6',
                'es3'
              );
              $jscomp.polyfill(
                'Object.is',
                function (a) {
                  return a
                    ? a
                    : function (b, c) {
                        return b === c ? 0 !== b || 1 / b === 1 / c : b !== b && c !== c;
                      };
                },
                'es6',
                'es3'
              );
              $jscomp.polyfill(
                'Array.prototype.includes',
                function (a) {
                  return a
                    ? a
                    : function (b, c) {
                        var d = this;
                        d instanceof String && (d = String(d));
                        var e = d.length;
                        c = c || 0;
                        for (0 > c && (c = Math.max(c + e, 0)); c < e; c++) {
                          var f = d[c];
                          if (f === b || Object.is(f, b)) return !0;
                        }
                        return !1;
                      };
                },
                'es7',
                'es3'
              );
              $jscomp.polyfill(
                'String.prototype.includes',
                function (a) {
                  return a
                    ? a
                    : function (b, c) {
                        return -1 !== $jscomp.checkStringArgs(this, b, 'includes').indexOf(b, c || 0);
                      };
                },
                'es6',
                'es3'
              );
              var COMPILED = !0,
                goog = goog || {};
              goog.global = this || self;
              goog.exportPath_ = function (a, b, c, d) {
                a = a.split('.');
                d = d || goog.global;
                a[0] in d || 'undefined' == typeof d.execScript || d.execScript('var ' + a[0]);
                for (var e; a.length && (e = a.shift()); )
                  if (a.length || void 0 === b) d = d[e] && d[e] !== Object.prototype[e] ? d[e] : (d[e] = {});
                  else if (!c && goog.isObject(b) && goog.isObject(d[e])) for (var f in b) b.hasOwnProperty(f) && (d[e][f] = b[f]);
                  else d[e] = b;
              };
              goog.define = function (a, b) {
                if (!COMPILED) {
                  var c = goog.global.CLOSURE_UNCOMPILED_DEFINES,
                    d = goog.global.CLOSURE_DEFINES;
                  c && void 0 === c.nodeType && Object.prototype.hasOwnProperty.call(c, a)
                    ? (b = c[a])
                    : d && void 0 === d.nodeType && Object.prototype.hasOwnProperty.call(d, a) && (b = d[a]);
                }
                return b;
              };
              goog.FEATURESET_YEAR = 2012;
              goog.DEBUG = !0;
              goog.LOCALE = 'en';
              goog.TRUSTED_SITE = !0;
              goog.DISALLOW_TEST_ONLY_CODE = COMPILED && !goog.DEBUG;
              goog.ENABLE_CHROME_APP_SAFE_SCRIPT_LOADING = !1;
              goog.provide = function (a) {
                if (goog.isInModuleLoader_()) throw Error('goog.provide cannot be used within a module.');
                if (!COMPILED && goog.isProvided_(a)) throw Error('Namespace "' + a + '" already declared.');
                goog.constructNamespace_(a);
              };
              goog.constructNamespace_ = function (a, b, c) {
                if (!COMPILED) {
                  delete goog.implicitNamespaces_[a];
                  for (var d = a; (d = d.substring(0, d.lastIndexOf('.'))) && !goog.getObjectByName(d); ) goog.implicitNamespaces_[d] = !0;
                }
                goog.exportPath_(a, b, c);
              };
              goog.getScriptNonce = function (a) {
                if (a && a != goog.global) return goog.getScriptNonce_(a.document);
                null === goog.cspNonce_ && (goog.cspNonce_ = goog.getScriptNonce_(goog.global.document));
                return goog.cspNonce_;
              };
              goog.NONCE_PATTERN_ = /^[\w+/_-]+[=]{0,2}$/;
              goog.cspNonce_ = null;
              goog.getScriptNonce_ = function (a) {
                return (a = a.querySelector && a.querySelector('script[nonce]')) && (a = a.nonce || a.getAttribute('nonce')) && goog.NONCE_PATTERN_.test(a)
                  ? a
                  : '';
              };
              goog.VALID_MODULE_RE_ = /^[a-zA-Z_$][a-zA-Z0-9._$]*$/;
              goog.module = function (a) {
                if ('string' !== typeof a || !a || -1 == a.search(goog.VALID_MODULE_RE_)) throw Error('Invalid module identifier');
                if (!goog.isInGoogModuleLoader_())
                  throw Error(
                    'Module ' +
                      a +
                      " has been loaded incorrectly. Note, modules cannot be loaded as normal scripts. They require some kind of pre-processing step. You're likely trying to load a module via a script tag or as a part of a concatenated bundle without rewriting the module. For more info see: https://github.com/google/closure-library/wiki/goog.module:-an-ES6-module-like-alternative-to-goog.provide."
                  );
                if (goog.moduleLoaderState_.moduleName) throw Error('goog.module may only be called once per module.');
                goog.moduleLoaderState_.moduleName = a;
                if (!COMPILED) {
                  if (goog.isProvided_(a)) throw Error('Namespace "' + a + '" already declared.');
                  delete goog.implicitNamespaces_[a];
                }
              };
              goog.module.get = function (a) {
                return goog.module.getInternal_(a);
              };
              goog.module.getInternal_ = function (a) {
                if (!COMPILED) {
                  if (a in goog.loadedModules_) return goog.loadedModules_[a].exports;
                  if (!goog.implicitNamespaces_[a]) return ((a = goog.getObjectByName(a)), null != a ? a : null);
                }
                return null;
              };
              goog.ModuleType = { ES6: 'es6', GOOG: 'goog' };
              goog.moduleLoaderState_ = null;
              goog.isInModuleLoader_ = function () {
                return goog.isInGoogModuleLoader_() || goog.isInEs6ModuleLoader_();
              };
              goog.isInGoogModuleLoader_ = function () {
                return !!goog.moduleLoaderState_ && goog.moduleLoaderState_.type == goog.ModuleType.GOOG;
              };
              goog.isInEs6ModuleLoader_ = function () {
                if (goog.moduleLoaderState_ && goog.moduleLoaderState_.type == goog.ModuleType.ES6) return !0;
                var a = goog.global.$jscomp;
                return a ? ('function' != typeof a.getCurrentModulePath ? !1 : !!a.getCurrentModulePath()) : !1;
              };
              goog.module.declareLegacyNamespace = function () {
                if (!COMPILED && !goog.isInGoogModuleLoader_()) throw Error('goog.module.declareLegacyNamespace must be called from within a goog.module');
                if (!COMPILED && !goog.moduleLoaderState_.moduleName) throw Error('goog.module must be called prior to goog.module.declareLegacyNamespace.');
                goog.moduleLoaderState_.declareLegacyNamespace = !0;
              };
              goog.declareModuleId = function (a) {
                if (!COMPILED) {
                  if (!goog.isInEs6ModuleLoader_()) throw Error('goog.declareModuleId may only be called from within an ES6 module');
                  if (goog.moduleLoaderState_ && goog.moduleLoaderState_.moduleName) throw Error('goog.declareModuleId may only be called once per module.');
                  if (a in goog.loadedModules_) throw Error('Module with namespace "' + a + '" already exists.');
                }
                if (goog.moduleLoaderState_) goog.moduleLoaderState_.moduleName = a;
                else {
                  var b = goog.global.$jscomp;
                  if (!b || 'function' != typeof b.getCurrentModulePath) throw Error('Module with namespace "' + a + '" has been loaded incorrectly.');
                  b = b.require(b.getCurrentModulePath());
                  goog.loadedModules_[a] = { exports: b, type: goog.ModuleType.ES6, moduleId: a };
                }
              };
              goog.setTestOnly = function (a) {
                if (goog.DISALLOW_TEST_ONLY_CODE) throw ((a = a || ''), Error('Importing test-only code into non-debug environment' + (a ? ': ' + a : '.')));
              };
              goog.forwardDeclare = function (a) {};
              COMPILED ||
                ((goog.isProvided_ = function (a) {
                  return a in goog.loadedModules_ || (!goog.implicitNamespaces_[a] && null != goog.getObjectByName(a));
                }),
                (goog.implicitNamespaces_ = { 'goog.module': !0 }));
              goog.getObjectByName = function (a, b) {
                a = a.split('.');
                b = b || goog.global;
                for (var c = 0; c < a.length; c++) if (((b = b[a[c]]), null == b)) return null;
                return b;
              };
              goog.addDependency = function (a, b, c, d) {
                !COMPILED && goog.DEPENDENCIES_ENABLED && goog.debugLoader_.addDependency(a, b, c, d);
              };
              goog.ENABLE_DEBUG_LOADER = !0;
              goog.logToConsole_ = function (a) {
                goog.global.console && goog.global.console.error(a);
              };
              goog.require = function (a) {
                if (!COMPILED) {
                  goog.ENABLE_DEBUG_LOADER && goog.debugLoader_.requested(a);
                  if (goog.isProvided_(a)) {
                    if (goog.isInModuleLoader_()) return goog.module.getInternal_(a);
                  } else if (goog.ENABLE_DEBUG_LOADER) {
                    var b = goog.moduleLoaderState_;
                    goog.moduleLoaderState_ = null;
                    try {
                      goog.debugLoader_.load_(a);
                    } finally {
                      goog.moduleLoaderState_ = b;
                    }
                  }
                  return null;
                }
              };
              goog.requireType = function (a) {
                return {};
              };
              goog.basePath = '';
              goog.nullFunction = function () {};
              goog.abstractMethod = function () {
                throw Error('unimplemented abstract method');
              };
              goog.addSingletonGetter = function (a) {
                a.instance_ = void 0;
                a.getInstance = function () {
                  if (a.instance_) return a.instance_;
                  goog.DEBUG && (goog.instantiatedSingletons_[goog.instantiatedSingletons_.length] = a);
                  return (a.instance_ = new a());
                };
              };
              goog.instantiatedSingletons_ = [];
              goog.LOAD_MODULE_USING_EVAL = !0;
              goog.SEAL_MODULE_EXPORTS = goog.DEBUG;
              goog.loadedModules_ = {};
              goog.DEPENDENCIES_ENABLED = !COMPILED && goog.ENABLE_DEBUG_LOADER;
              goog.TRANSPILE = 'detect';
              goog.ASSUME_ES_MODULES_TRANSPILED = !1;
              goog.TRANSPILE_TO_LANGUAGE = '';
              goog.TRANSPILER = 'transpile.js';
              goog.hasBadLetScoping = null;
              goog.useSafari10Workaround = function () {
                if (null == goog.hasBadLetScoping) {
                  try {
                    var a = !eval('"use strict";let x = 1; function f() { return typeof x; };f() == "number";');
                  } catch (b) {
                    a = !1;
                  }
                  goog.hasBadLetScoping = a;
                }
                return goog.hasBadLetScoping;
              };
              goog.workaroundSafari10EvalBug = function (a) {
                return '(function(){' + a + '\n;})();\n';
              };
              goog.loadModule = function (a) {
                var b = goog.moduleLoaderState_;
                try {
                  goog.moduleLoaderState_ = { moduleName: '', declareLegacyNamespace: !1, type: goog.ModuleType.GOOG };
                  var c = {},
                    d = c;
                  if ('function' === typeof a) d = a.call(void 0, d);
                  else if ('string' === typeof a)
                    (goog.useSafari10Workaround() && (a = goog.workaroundSafari10EvalBug(a)), (d = goog.loadModuleFromSource_.call(void 0, d, a)));
                  else throw Error('Invalid module definition');
                  var e = goog.moduleLoaderState_.moduleName;
                  if ('string' === typeof e && e)
                    (goog.moduleLoaderState_.declareLegacyNamespace
                      ? goog.constructNamespace_(e, d, c !== d)
                      : goog.SEAL_MODULE_EXPORTS && Object.seal && 'object' == typeof d && null != d && Object.seal(d),
                      (goog.loadedModules_[e] = { exports: d, type: goog.ModuleType.GOOG, moduleId: goog.moduleLoaderState_.moduleName }));
                  else throw Error('Invalid module name "' + e + '"');
                } finally {
                  goog.moduleLoaderState_ = b;
                }
              };
              goog.loadModuleFromSource_ = function (a, b) {
                eval(b);
                return a;
              };
              goog.normalizePath_ = function (a) {
                a = a.split('/');
                for (var b = 0; b < a.length; ) '.' == a[b] ? a.splice(b, 1) : b && '..' == a[b] && a[b - 1] && '..' != a[b - 1] ? a.splice(--b, 2) : b++;
                return a.join('/');
              };
              goog.loadFileSync_ = function (a) {
                if (goog.global.CLOSURE_LOAD_FILE_SYNC) return goog.global.CLOSURE_LOAD_FILE_SYNC(a);
                try {
                  var b = new goog.global.XMLHttpRequest();
                  b.open('get', a, !1);
                  b.send();
                  return 0 == b.status || 200 == b.status ? b.responseText : null;
                } catch (c) {
                  return null;
                }
              };
              goog.transpile_ = function (a, b, c) {
                var d = goog.global.$jscomp;
                d || (goog.global.$jscomp = d = {});
                var e = d.transpile;
                if (!e) {
                  var f = goog.basePath + goog.TRANSPILER,
                    g = goog.loadFileSync_(f);
                  if (g) {
                    (function () {
                      (0, eval)(g + '\n//# sourceURL=' + f);
                    }).call(goog.global);
                    if (goog.global.$gwtExport && goog.global.$gwtExport.$jscomp && !goog.global.$gwtExport.$jscomp.transpile)
                      throw Error('The transpiler did not properly export the "transpile" method. $gwtExport: ' + JSON.stringify(goog.global.$gwtExport));
                    goog.global.$jscomp.transpile = goog.global.$gwtExport.$jscomp.transpile;
                    d = goog.global.$jscomp;
                    e = d.transpile;
                  }
                }
                e ||
                  (e = d.transpile =
                    function (h, k) {
                      goog.logToConsole_(k + ' requires transpilation but no transpiler was found.');
                      return h;
                    });
                return e(a, b, c);
              };
              goog.typeOf = function (a) {
                var b = typeof a;
                return 'object' != b ? b : a ? (Array.isArray(a) ? 'array' : b) : 'null';
              };
              goog.isArrayLike = function (a) {
                var b = goog.typeOf(a);
                return 'array' == b || ('object' == b && 'number' == typeof a.length);
              };
              goog.isDateLike = function (a) {
                return goog.isObject(a) && 'function' == typeof a.getFullYear;
              };
              goog.isObject = function (a) {
                var b = typeof a;
                return ('object' == b && null != a) || 'function' == b;
              };
              goog.getUid = function (a) {
                return (Object.prototype.hasOwnProperty.call(a, goog.UID_PROPERTY_) && a[goog.UID_PROPERTY_]) || (a[goog.UID_PROPERTY_] = ++goog.uidCounter_);
              };
              goog.hasUid = function (a) {
                return !!a[goog.UID_PROPERTY_];
              };
              goog.removeUid = function (a) {
                null !== a && 'removeAttribute' in a && a.removeAttribute(goog.UID_PROPERTY_);
                try {
                  delete a[goog.UID_PROPERTY_];
                } catch (b) {}
              };
              goog.UID_PROPERTY_ = 'closure_uid_' + ((1e9 * Math.random()) >>> 0);
              goog.uidCounter_ = 0;
              goog.cloneObject = function (a) {
                var b = goog.typeOf(a);
                if ('object' == b || 'array' == b) {
                  if ('function' === typeof a.clone) return a.clone();
                  b = 'array' == b ? [] : {};
                  for (var c in a) b[c] = goog.cloneObject(a[c]);
                  return b;
                }
                return a;
              };
              goog.bindNative_ = function (a, b, c) {
                return a.call.apply(a.bind, arguments);
              };
              goog.bindJs_ = function (a, b, c) {
                if (!a) throw Error();
                if (2 < arguments.length) {
                  var d = Array.prototype.slice.call(arguments, 2);
                  return function () {
                    var e = Array.prototype.slice.call(arguments);
                    Array.prototype.unshift.apply(e, d);
                    return a.apply(b, e);
                  };
                }
                return function () {
                  return a.apply(b, arguments);
                };
              };
              goog.bind = function (a, b, c) {
                Function.prototype.bind && -1 != Function.prototype.bind.toString().indexOf('native code')
                  ? (goog.bind = goog.bindNative_)
                  : (goog.bind = goog.bindJs_);
                return goog.bind.apply(null, arguments);
              };
              goog.partial = function (a, b) {
                var c = Array.prototype.slice.call(arguments, 1);
                return function () {
                  var d = c.slice();
                  d.push.apply(d, arguments);
                  return a.apply(this, d);
                };
              };
              goog.mixin = function (a, b) {
                for (var c in b) a[c] = b[c];
              };
              goog.now = function () {
                return Date.now();
              };
              goog.globalEval = function (a) {
                (0, eval)(a);
              };
              goog.getCssName = function (a, b) {
                if ('.' == String(a).charAt(0)) throw Error('className passed in goog.getCssName must not start with ".". You passed: ' + a);
                var c = function (e) {
                    return goog.cssNameMapping_[e] || e;
                  },
                  d = function (e) {
                    e = e.split('-');
                    for (var f = [], g = 0; g < e.length; g++) f.push(c(e[g]));
                    return f.join('-');
                  };
                d = goog.cssNameMapping_
                  ? 'BY_WHOLE' == goog.cssNameMappingStyle_
                    ? c
                    : d
                  : function (e) {
                      return e;
                    };
                a = b ? a + '-' + d(b) : d(a);
                return goog.global.CLOSURE_CSS_NAME_MAP_FN ? goog.global.CLOSURE_CSS_NAME_MAP_FN(a) : a;
              };
              goog.setCssNameMapping = function (a, b) {
                goog.cssNameMapping_ = a;
                goog.cssNameMappingStyle_ = b;
              };
              !COMPILED && goog.global.CLOSURE_CSS_NAME_MAPPING && (goog.cssNameMapping_ = goog.global.CLOSURE_CSS_NAME_MAPPING);
              goog.getMsg = function (a, b, c) {
                c && c.html && (a = a.replace(/</g, '&lt;'));
                c &&
                  c.unescapeHtmlEntities &&
                  (a = a
                    .replace(/&lt;/g, '<')
                    .replace(/&gt;/g, '>')
                    .replace(/&apos;/g, "'")
                    .replace(/&quot;/g, '"')
                    .replace(/&amp;/g, '&'));
                b &&
                  (a = a.replace(/\{\$([^}]+)}/g, function (d, e) {
                    return null != b && e in b ? b[e] : d;
                  }));
                return a;
              };
              goog.getMsgWithFallback = function (a, b) {
                return a;
              };
              goog.exportSymbol = function (a, b, c) {
                goog.exportPath_(a, b, !0, c);
              };
              goog.exportProperty = function (a, b, c) {
                a[b] = c;
              };
              goog.inherits = function (a, b) {
                function c() {}
                c.prototype = b.prototype;
                a.superClass_ = b.prototype;
                a.prototype = new c();
                a.prototype.constructor = a;
                a.base = function (d, e, f) {
                  for (var g = Array(arguments.length - 2), h = 2; h < arguments.length; h++) g[h - 2] = arguments[h];
                  return b.prototype[e].apply(d, g);
                };
              };
              goog.scope = function (a) {
                if (goog.isInModuleLoader_()) throw Error('goog.scope is not supported within a module.');
                a.call(goog.global);
              };
              COMPILED || (goog.global.COMPILED = COMPILED);
              goog.defineClass = function (a, b) {
                var c = b.constructor,
                  d = b.statics;
                (c && c != Object.prototype.constructor) ||
                  (c = function () {
                    throw Error('cannot instantiate an interface (no constructor defined).');
                  });
                c = goog.defineClass.createSealingConstructor_(c, a);
                a && goog.inherits(c, a);
                delete b.constructor;
                delete b.statics;
                goog.defineClass.applyProperties_(c.prototype, b);
                null != d && (d instanceof Function ? d(c) : goog.defineClass.applyProperties_(c, d));
                return c;
              };
              goog.defineClass.SEAL_CLASS_INSTANCES = goog.DEBUG;
              goog.defineClass.createSealingConstructor_ = function (a, b) {
                return goog.defineClass.SEAL_CLASS_INSTANCES
                  ? function () {
                      var c = a.apply(this, arguments) || this;
                      c[goog.UID_PROPERTY_] = c[goog.UID_PROPERTY_];
                      return c;
                    }
                  : a;
              };
              goog.defineClass.OBJECT_PROTOTYPE_FIELDS_ = 'constructor hasOwnProperty isPrototypeOf propertyIsEnumerable toLocaleString toString valueOf'.split(
                ' '
              );
              goog.defineClass.applyProperties_ = function (a, b) {
                for (var c in b) Object.prototype.hasOwnProperty.call(b, c) && (a[c] = b[c]);
                for (var d = 0; d < goog.defineClass.OBJECT_PROTOTYPE_FIELDS_.length; d++)
                  ((c = goog.defineClass.OBJECT_PROTOTYPE_FIELDS_[d]), Object.prototype.hasOwnProperty.call(b, c) && (a[c] = b[c]));
              };
              !COMPILED &&
                goog.DEPENDENCIES_ENABLED &&
                ((goog.inHtmlDocument_ = function () {
                  var a = goog.global.document;
                  return null != a && 'write' in a;
                }),
                (goog.isDocumentLoading_ = function () {
                  var a = goog.global.document;
                  return a.attachEvent ? 'complete' != a.readyState : 'loading' == a.readyState;
                }),
                (goog.findBasePath_ = function () {
                  if (void 0 != goog.global.CLOSURE_BASE_PATH && 'string' === typeof goog.global.CLOSURE_BASE_PATH)
                    goog.basePath = goog.global.CLOSURE_BASE_PATH;
                  else if (goog.inHtmlDocument_()) {
                    var a = goog.global.document,
                      b = a.currentScript;
                    a = b ? [b] : a.getElementsByTagName('SCRIPT');
                    for (b = a.length - 1; 0 <= b; --b) {
                      var c = a[b].src,
                        d = c.lastIndexOf('?');
                      d = -1 == d ? c.length : d;
                      if ('base.js' == c.substr(d - 7, 7)) {
                        goog.basePath = c.substr(0, d - 7);
                        break;
                      }
                    }
                  }
                }),
                goog.findBasePath_(),
                (goog.Transpiler = function () {
                  this.requiresTranspilation_ = null;
                  this.transpilationTarget_ = goog.TRANSPILE_TO_LANGUAGE;
                }),
                (goog.Transpiler.prototype.createRequiresTranspilation_ = function () {
                  function a(g, h) {
                    e ? (d[g] = !0) : h() ? ((c = g), (d[g] = !1)) : (e = d[g] = !0);
                  }
                  function b(g) {
                    try {
                      return !!eval(g);
                    } catch (h) {
                      return !1;
                    }
                  }
                  var c = 'es3',
                    d = { es3: !1 },
                    e = !1,
                    f = goog.global.navigator && goog.global.navigator.userAgent ? goog.global.navigator.userAgent : '';
                  a('es5', function () {
                    return b('[1,].length==1');
                  });
                  a('es6', function () {
                    return f.match(/Edge\/(\d+)(\.\d)*/i)
                      ? !1
                      : b(
                          '(()=>{"use strict";class X{constructor(){if(new.target!=String)throw 1;this.x=42}}let q=Reflect.construct(X,[],String);if(q.x!=42||!(q instanceof String))throw 1;for(const a of[2,3]){if(a==2)continue;function f(z={a}){let a=0;return z.a}{function f(){return 0;}}return f()==3}})()'
                        );
                  });
                  a('es7', function () {
                    return b('2 ** 2 == 4');
                  });
                  a('es8', function () {
                    return b('async () => 1, true');
                  });
                  a('es9', function () {
                    return b('({...rest} = {}), true');
                  });
                  a('es_next', function () {
                    return !1;
                  });
                  return { target: c, map: d };
                }),
                (goog.Transpiler.prototype.needsTranspile = function (a, b) {
                  if ('always' == goog.TRANSPILE) return !0;
                  if ('never' == goog.TRANSPILE) return !1;
                  if (!this.requiresTranspilation_) {
                    var c = this.createRequiresTranspilation_();
                    this.requiresTranspilation_ = c.map;
                    this.transpilationTarget_ = this.transpilationTarget_ || c.target;
                  }
                  if (a in this.requiresTranspilation_)
                    return this.requiresTranspilation_[a]
                      ? !0
                      : !goog.inHtmlDocument_() || 'es6' != b || 'noModule' in goog.global.document.createElement('script')
                        ? !1
                        : !0;
                  throw Error('Unknown language mode: ' + a);
                }),
                (goog.Transpiler.prototype.transpile = function (a, b) {
                  return goog.transpile_(a, b, this.transpilationTarget_);
                }),
                (goog.transpiler_ = new goog.Transpiler()),
                (goog.protectScriptTag_ = function (a) {
                  return a.replace(/<\/(SCRIPT)/gi, '\\x3c/$1');
                }),
                (goog.DebugLoader_ = function () {
                  this.dependencies_ = {};
                  this.idToPath_ = {};
                  this.written_ = {};
                  this.loadingDeps_ = [];
                  this.depsToLoad_ = [];
                  this.paused_ = !1;
                  this.factory_ = new goog.DependencyFactory(goog.transpiler_);
                  this.deferredCallbacks_ = {};
                  this.deferredQueue_ = [];
                }),
                (goog.DebugLoader_.prototype.bootstrap = function (a, b) {
                  function c() {
                    d && (goog.global.setTimeout(d, 0), (d = null));
                  }
                  var d = b;
                  if (a.length) {
                    b = [];
                    for (var e = 0; e < a.length; e++) {
                      var f = this.getPathFromDeps_(a[e]);
                      if (!f) throw Error('Unregonized namespace: ' + a[e]);
                      b.push(this.dependencies_[f]);
                    }
                    f = goog.require;
                    var g = 0;
                    for (e = 0; e < a.length; e++)
                      (f(a[e]),
                        b[e].onLoad(function () {
                          ++g == a.length && c();
                        }));
                  } else c();
                }),
                (goog.DebugLoader_.prototype.loadClosureDeps = function () {
                  this.depsToLoad_.push(this.factory_.createDependency(goog.normalizePath_(goog.basePath + 'deps.js'), 'deps.js', [], [], {}, !1));
                  this.loadDeps_();
                }),
                (goog.DebugLoader_.prototype.requested = function (a, b) {
                  (a = this.getPathFromDeps_(a)) &&
                    (b || this.areDepsLoaded_(this.dependencies_[a].requires)) &&
                    (b = this.deferredCallbacks_[a]) &&
                    (delete this.deferredCallbacks_[a], b());
                }),
                (goog.DebugLoader_.prototype.setDependencyFactory = function (a) {
                  this.factory_ = a;
                }),
                (goog.DebugLoader_.prototype.load_ = function (a) {
                  if (this.getPathFromDeps_(a)) {
                    var b = this,
                      c = [],
                      d = function (e) {
                        var f = b.getPathFromDeps_(e);
                        if (!f) throw Error('Bad dependency path or symbol: ' + e);
                        if (!b.written_[f]) {
                          b.written_[f] = !0;
                          e = b.dependencies_[f];
                          for (f = 0; f < e.requires.length; f++) goog.isProvided_(e.requires[f]) || d(e.requires[f]);
                          c.push(e);
                        }
                      };
                    d(a);
                    a = !!this.depsToLoad_.length;
                    this.depsToLoad_ = this.depsToLoad_.concat(c);
                    this.paused_ || a || this.loadDeps_();
                  } else throw ((a = 'goog.require could not find: ' + a), goog.logToConsole_(a), Error(a));
                }),
                (goog.DebugLoader_.prototype.loadDeps_ = function () {
                  for (var a = this, b = this.paused_; this.depsToLoad_.length && !b; )
                    (function () {
                      var c = !1,
                        d = a.depsToLoad_.shift(),
                        e = !1;
                      a.loading_(d);
                      var f = {
                        pause: function () {
                          if (c) throw Error('Cannot call pause after the call to load.');
                          b = !0;
                        },
                        resume: function () {
                          c ? a.resume_() : (b = !1);
                        },
                        loaded: function () {
                          if (e) throw Error('Double call to loaded.');
                          e = !0;
                          a.loaded_(d);
                        },
                        pending: function () {
                          for (var g = [], h = 0; h < a.loadingDeps_.length; h++) g.push(a.loadingDeps_[h]);
                          return g;
                        },
                        setModuleState: function (g) {
                          goog.moduleLoaderState_ = { type: g, moduleName: '', declareLegacyNamespace: !1 };
                        },
                        registerEs6ModuleExports: function (g, h, k) {
                          k && (goog.loadedModules_[k] = { exports: h, type: goog.ModuleType.ES6, moduleId: k || '' });
                        },
                        registerGoogModuleExports: function (g, h) {
                          goog.loadedModules_[g] = { exports: h, type: goog.ModuleType.GOOG, moduleId: g };
                        },
                        clearModuleState: function () {
                          goog.moduleLoaderState_ = null;
                        },
                        defer: function (g) {
                          if (c) throw Error('Cannot register with defer after the call to load.');
                          a.defer_(d, g);
                        },
                        areDepsLoaded: function () {
                          return a.areDepsLoaded_(d.requires);
                        }
                      };
                      try {
                        d.load(f);
                      } finally {
                        c = !0;
                      }
                    })();
                  b && this.pause_();
                }),
                (goog.DebugLoader_.prototype.pause_ = function () {
                  this.paused_ = !0;
                }),
                (goog.DebugLoader_.prototype.resume_ = function () {
                  this.paused_ && ((this.paused_ = !1), this.loadDeps_());
                }),
                (goog.DebugLoader_.prototype.loading_ = function (a) {
                  this.loadingDeps_.push(a);
                }),
                (goog.DebugLoader_.prototype.loaded_ = function (a) {
                  for (var b = 0; b < this.loadingDeps_.length; b++)
                    if (this.loadingDeps_[b] == a) {
                      this.loadingDeps_.splice(b, 1);
                      break;
                    }
                  for (b = 0; b < this.deferredQueue_.length; b++)
                    if (this.deferredQueue_[b] == a.path) {
                      this.deferredQueue_.splice(b, 1);
                      break;
                    }
                  if (this.loadingDeps_.length == this.deferredQueue_.length && !this.depsToLoad_.length)
                    for (; this.deferredQueue_.length; ) this.requested(this.deferredQueue_.shift(), !0);
                  a.loaded();
                }),
                (goog.DebugLoader_.prototype.areDepsLoaded_ = function (a) {
                  for (var b = 0; b < a.length; b++) {
                    var c = this.getPathFromDeps_(a[b]);
                    if (!c || !(c in this.deferredCallbacks_ || goog.isProvided_(a[b]))) return !1;
                  }
                  return !0;
                }),
                (goog.DebugLoader_.prototype.getPathFromDeps_ = function (a) {
                  return a in this.idToPath_ ? this.idToPath_[a] : a in this.dependencies_ ? a : null;
                }),
                (goog.DebugLoader_.prototype.defer_ = function (a, b) {
                  this.deferredCallbacks_[a.path] = b;
                  this.deferredQueue_.push(a.path);
                }),
                (goog.LoadController = function () {}),
                (goog.LoadController.prototype.pause = function () {}),
                (goog.LoadController.prototype.resume = function () {}),
                (goog.LoadController.prototype.loaded = function () {}),
                (goog.LoadController.prototype.pending = function () {}),
                (goog.LoadController.prototype.registerEs6ModuleExports = function (a, b, c) {}),
                (goog.LoadController.prototype.setModuleState = function (a) {}),
                (goog.LoadController.prototype.clearModuleState = function () {}),
                (goog.LoadController.prototype.defer = function (a) {}),
                (goog.LoadController.prototype.areDepsLoaded = function () {}),
                (goog.Dependency = function (a, b, c, d, e) {
                  this.path = a;
                  this.relativePath = b;
                  this.provides = c;
                  this.requires = d;
                  this.loadFlags = e;
                  this.loaded_ = !1;
                  this.loadCallbacks_ = [];
                }),
                (goog.Dependency.prototype.getPathName = function () {
                  var a = this.path,
                    b = a.indexOf('://');
                  0 <= b && ((a = a.substring(b + 3)), (b = a.indexOf('/')), 0 <= b && (a = a.substring(b + 1)));
                  return a;
                }),
                (goog.Dependency.prototype.onLoad = function (a) {
                  this.loaded_ ? a() : this.loadCallbacks_.push(a);
                }),
                (goog.Dependency.prototype.loaded = function () {
                  this.loaded_ = !0;
                  var a = this.loadCallbacks_;
                  this.loadCallbacks_ = [];
                  for (var b = 0; b < a.length; b++) a[b]();
                }),
                (goog.Dependency.defer_ = !1),
                (goog.Dependency.callbackMap_ = {}),
                (goog.Dependency.registerCallback_ = function (a) {
                  var b = Math.random().toString(32);
                  goog.Dependency.callbackMap_[b] = a;
                  return b;
                }),
                (goog.Dependency.unregisterCallback_ = function (a) {
                  delete goog.Dependency.callbackMap_[a];
                }),
                (goog.Dependency.callback_ = function (a, b) {
                  if (a in goog.Dependency.callbackMap_) {
                    for (var c = goog.Dependency.callbackMap_[a], d = [], e = 1; e < arguments.length; e++) d.push(arguments[e]);
                    c.apply(void 0, d);
                  } else throw Error('Callback key ' + a + ' does not exist (was base.js loaded more than once?).');
                }),
                (goog.Dependency.prototype.load = function (a) {
                  if (goog.global.CLOSURE_IMPORT_SCRIPT) goog.global.CLOSURE_IMPORT_SCRIPT(this.path) ? a.loaded() : a.pause();
                  else if (goog.inHtmlDocument_()) {
                    var b = goog.global.document;
                    if ('complete' == b.readyState && !goog.ENABLE_CHROME_APP_SAFE_SCRIPT_LOADING) {
                      if (/\bdeps.js$/.test(this.path)) {
                        a.loaded();
                        return;
                      }
                      throw Error('Cannot write "' + this.path + '" after document load');
                    }
                    var c = goog.getScriptNonce();
                    if (!goog.ENABLE_CHROME_APP_SAFE_SCRIPT_LOADING && goog.isDocumentLoading_()) {
                      var d = function (h) {
                        h.readyState && 'complete' != h.readyState ? (h.onload = d) : (goog.Dependency.unregisterCallback_(e), a.loaded());
                      };
                      var e = goog.Dependency.registerCallback_(d);
                      c = c ? ' nonce="' + c + '"' : '';
                      var f = '<script src="' + this.path + '"' + c + (goog.Dependency.defer_ ? ' defer' : '') + ' id="script-' + e + '">\x3c/script>';
                      f += '<script' + c + '>';
                      f = goog.Dependency.defer_
                        ? f + ("document.getElementById('script-" + e + "').onload = function() {\n  goog.Dependency.callback_('" + e + "', this);\n};\n")
                        : f + ("goog.Dependency.callback_('" + e + "', document.getElementById('script-" + e + "'));");
                      f += '\x3c/script>';
                      b.write(goog.TRUSTED_TYPES_POLICY_ ? goog.TRUSTED_TYPES_POLICY_.createHTML(f) : f);
                    } else {
                      var g = b.createElement('script');
                      g.defer = goog.Dependency.defer_;
                      g.async = !1;
                      c && (g.nonce = c);
                      goog.DebugLoader_.IS_OLD_IE_
                        ? (a.pause(),
                          (g.onreadystatechange = function () {
                            if ('loaded' == g.readyState || 'complete' == g.readyState) (a.loaded(), a.resume());
                          }))
                        : (g.onload = function () {
                            g.onload = null;
                            a.loaded();
                          });
                      g.src = goog.TRUSTED_TYPES_POLICY_ ? goog.TRUSTED_TYPES_POLICY_.createScriptURL(this.path) : this.path;
                      b.head.appendChild(g);
                    }
                  } else
                    (goog.logToConsole_('Cannot use default debug loader outside of HTML documents.'),
                      'deps.js' == this.relativePath
                        ? (goog.logToConsole_('Consider setting CLOSURE_IMPORT_SCRIPT before loading base.js, or setting CLOSURE_NO_DEPS to true.'), a.loaded())
                        : a.pause());
                }),
                (goog.Es6ModuleDependency = function (a, b, c, d, e) {
                  goog.Dependency.call(this, a, b, c, d, e);
                }),
                goog.inherits(goog.Es6ModuleDependency, goog.Dependency),
                (goog.Es6ModuleDependency.prototype.load = function (a) {
                  function b(l, p) {
                    var m = '',
                      n = goog.getScriptNonce();
                    n && (m = ' nonce="' + n + '"');
                    l = p
                      ? '<script type="module" crossorigin' + m + '>' + p + '\x3c/script>'
                      : '<script type="module" crossorigin src="' + l + '"' + m + '>\x3c/script>';
                    d.write(goog.TRUSTED_TYPES_POLICY_ ? goog.TRUSTED_TYPES_POLICY_.createHTML(l) : l);
                  }
                  function c(l, p) {
                    var m = d.createElement('script');
                    m.defer = !0;
                    m.async = !1;
                    m.type = 'module';
                    m.setAttribute('crossorigin', !0);
                    var n = goog.getScriptNonce();
                    n && (m.nonce = n);
                    p
                      ? (m.text = goog.TRUSTED_TYPES_POLICY_ ? goog.TRUSTED_TYPES_POLICY_.createScript(p) : p)
                      : (m.src = goog.TRUSTED_TYPES_POLICY_ ? goog.TRUSTED_TYPES_POLICY_.createScriptURL(l) : l);
                    d.head.appendChild(m);
                  }
                  if (goog.global.CLOSURE_IMPORT_SCRIPT) goog.global.CLOSURE_IMPORT_SCRIPT(this.path) ? a.loaded() : a.pause();
                  else if (goog.inHtmlDocument_()) {
                    var d = goog.global.document,
                      e = this;
                    if (goog.isDocumentLoading_()) {
                      var f = b;
                      goog.Dependency.defer_ = !0;
                    } else f = c;
                    var g = goog.Dependency.registerCallback_(function () {
                      goog.Dependency.unregisterCallback_(g);
                      a.setModuleState(goog.ModuleType.ES6);
                    });
                    f(void 0, 'goog.Dependency.callback_("' + g + '")');
                    f(this.path, void 0);
                    var h = goog.Dependency.registerCallback_(function (l) {
                      goog.Dependency.unregisterCallback_(h);
                      a.registerEs6ModuleExports(e.path, l, goog.moduleLoaderState_.moduleName);
                    });
                    f(void 0, 'import * as m from "' + this.path + '"; goog.Dependency.callback_("' + h + '", m)');
                    var k = goog.Dependency.registerCallback_(function () {
                      goog.Dependency.unregisterCallback_(k);
                      a.clearModuleState();
                      a.loaded();
                    });
                    f(void 0, 'goog.Dependency.callback_("' + k + '")');
                  } else (goog.logToConsole_('Cannot use default debug loader outside of HTML documents.'), a.pause());
                }),
                (goog.TransformedDependency = function (a, b, c, d, e) {
                  goog.Dependency.call(this, a, b, c, d, e);
                  this.contents_ = null;
                  this.lazyFetch_ = !goog.inHtmlDocument_() || !('noModule' in goog.global.document.createElement('script'));
                }),
                goog.inherits(goog.TransformedDependency, goog.Dependency),
                (goog.TransformedDependency.prototype.load = function (a) {
                  function b() {
                    e.contents_ = goog.loadFileSync_(e.path);
                    e.contents_ && ((e.contents_ = e.transform(e.contents_)), e.contents_ && (e.contents_ += '\n//# sourceURL=' + e.path));
                  }
                  function c() {
                    e.lazyFetch_ && b();
                    if (e.contents_) {
                      f && a.setModuleState(goog.ModuleType.ES6);
                      try {
                        var p = e.contents_;
                        e.contents_ = null;
                        goog.globalEval(p);
                        if (f) var m = goog.moduleLoaderState_.moduleName;
                      } finally {
                        f && a.clearModuleState();
                      }
                      f &&
                        goog.global.$jscomp.require.ensure([e.getPathName()], function () {
                          a.registerEs6ModuleExports(e.path, goog.global.$jscomp.require(e.getPathName()), m);
                        });
                      a.loaded();
                    }
                  }
                  function d() {
                    var p = goog.global.document,
                      m = goog.Dependency.registerCallback_(function () {
                        goog.Dependency.unregisterCallback_(m);
                        c();
                      }),
                      n = goog.getScriptNonce();
                    n = '<script' + (n ? ' nonce="' + n + '"' : '') + '>' + goog.protectScriptTag_('goog.Dependency.callback_("' + m + '");') + '\x3c/script>';
                    p.write(goog.TRUSTED_TYPES_POLICY_ ? goog.TRUSTED_TYPES_POLICY_.createHTML(n) : n);
                  }
                  var e = this;
                  if (goog.global.CLOSURE_IMPORT_SCRIPT)
                    (b(), this.contents_ && goog.global.CLOSURE_IMPORT_SCRIPT('', this.contents_) ? ((this.contents_ = null), a.loaded()) : a.pause());
                  else {
                    var f = this.loadFlags.module == goog.ModuleType.ES6;
                    this.lazyFetch_ || b();
                    var g = 1 < a.pending().length,
                      h = g && goog.DebugLoader_.IS_OLD_IE_;
                    g = goog.Dependency.defer_ && (g || goog.isDocumentLoading_());
                    if (h || g)
                      a.defer(function () {
                        c();
                      });
                    else {
                      var k = goog.global.document;
                      h = goog.inHtmlDocument_() && 'ActiveXObject' in goog.global;
                      if (f && goog.inHtmlDocument_() && goog.isDocumentLoading_() && !h) {
                        goog.Dependency.defer_ = !0;
                        a.pause();
                        var l = k.onreadystatechange;
                        k.onreadystatechange = function () {
                          'interactive' == k.readyState && ((k.onreadystatechange = l), c(), a.resume());
                          'function' === typeof l && l.apply(void 0, arguments);
                        };
                      } else !goog.DebugLoader_.IS_OLD_IE_ && goog.inHtmlDocument_() && goog.isDocumentLoading_() ? d() : c();
                    }
                  }
                }),
                (goog.TransformedDependency.prototype.transform = function (a) {}),
                (goog.TranspiledDependency = function (a, b, c, d, e, f) {
                  goog.TransformedDependency.call(this, a, b, c, d, e);
                  this.transpiler = f;
                }),
                goog.inherits(goog.TranspiledDependency, goog.TransformedDependency),
                (goog.TranspiledDependency.prototype.transform = function (a) {
                  return this.transpiler.transpile(a, this.getPathName());
                }),
                (goog.PreTranspiledEs6ModuleDependency = function (a, b, c, d, e) {
                  goog.TransformedDependency.call(this, a, b, c, d, e);
                }),
                goog.inherits(goog.PreTranspiledEs6ModuleDependency, goog.TransformedDependency),
                (goog.PreTranspiledEs6ModuleDependency.prototype.transform = function (a) {
                  return a;
                }),
                (goog.GoogModuleDependency = function (a, b, c, d, e, f, g) {
                  goog.TransformedDependency.call(this, a, b, c, d, e);
                  this.needsTranspile_ = f;
                  this.transpiler_ = g;
                }),
                goog.inherits(goog.GoogModuleDependency, goog.TransformedDependency),
                (goog.GoogModuleDependency.prototype.transform = function (a) {
                  this.needsTranspile_ && (a = this.transpiler_.transpile(a, this.getPathName()));
                  return goog.LOAD_MODULE_USING_EVAL && void 0 !== goog.global.JSON
                    ? 'goog.loadModule(' + goog.global.JSON.stringify(a + '\n//# sourceURL=' + this.path + '\n') + ');'
                    : 'goog.loadModule(function(exports) {"use strict";' + a + '\n;return exports});\n//# sourceURL=' + this.path + '\n';
                }),
                (goog.DebugLoader_.IS_OLD_IE_ = !(goog.global.atob || !goog.global.document || !goog.global.document.all)),
                (goog.DebugLoader_.prototype.addDependency = function (a, b, c, d) {
                  b = b || [];
                  a = a.replace(/\\/g, '/');
                  var e = goog.normalizePath_(goog.basePath + a);
                  (d && 'boolean' !== typeof d) || (d = d ? { module: goog.ModuleType.GOOG } : {});
                  c = this.factory_.createDependency(e, a, b, c, d, goog.transpiler_.needsTranspile(d.lang || 'es3', d.module));
                  this.dependencies_[e] = c;
                  for (c = 0; c < b.length; c++) this.idToPath_[b[c]] = e;
                  this.idToPath_[a] = e;
                }),
                (goog.DependencyFactory = function (a) {
                  this.transpiler = a;
                }),
                (goog.DependencyFactory.prototype.createDependency = function (a, b, c, d, e, f) {
                  return e.module == goog.ModuleType.GOOG
                    ? new goog.GoogModuleDependency(a, b, c, d, e, f, this.transpiler)
                    : f
                      ? new goog.TranspiledDependency(a, b, c, d, e, this.transpiler)
                      : e.module == goog.ModuleType.ES6
                        ? 'never' == goog.TRANSPILE && goog.ASSUME_ES_MODULES_TRANSPILED
                          ? new goog.PreTranspiledEs6ModuleDependency(a, b, c, d, e)
                          : new goog.Es6ModuleDependency(a, b, c, d, e)
                        : new goog.Dependency(a, b, c, d, e);
                }),
                (goog.debugLoader_ = new goog.DebugLoader_()),
                (goog.loadClosureDeps = function () {
                  goog.debugLoader_.loadClosureDeps();
                }),
                (goog.setDependencyFactory = function (a) {
                  goog.debugLoader_.setDependencyFactory(a);
                }),
                (goog.TRUSTED_TYPES_POLICY_ = goog.TRUSTED_TYPES_POLICY_NAME ? goog.createTrustedTypesPolicy(goog.TRUSTED_TYPES_POLICY_NAME + '#base') : null),
                goog.global.CLOSURE_NO_DEPS || goog.debugLoader_.loadClosureDeps(),
                (goog.bootstrap = function (a, b) {
                  goog.debugLoader_.bootstrap(a, b);
                }));
              goog.TRUSTED_TYPES_POLICY_NAME = 'goog';
              goog.identity_ = function (a) {
                return a;
              };
              goog.createTrustedTypesPolicy = function (a) {
                var b = null,
                  c = goog.global.trustedTypes;
                if (!c || !c.createPolicy) return b;
                try {
                  b = c.createPolicy(a, { createHTML: goog.identity_, createScript: goog.identity_, createScriptURL: goog.identity_ });
                } catch (d) {
                  goog.logToConsole_(d.message);
                }
                return b;
              };
              goog.debug = {};
              function module$contents$goog$debug$Error_DebugError(a) {
                if (Error.captureStackTrace) Error.captureStackTrace(this, module$contents$goog$debug$Error_DebugError);
                else {
                  var b = Error().stack;
                  b && (this.stack = b);
                }
                a && (this.message = String(a));
                this.reportErrorToServer = !0;
              }
              goog.inherits(module$contents$goog$debug$Error_DebugError, Error);
              module$contents$goog$debug$Error_DebugError.prototype.name = 'CustomError';
              goog.debug.Error = module$contents$goog$debug$Error_DebugError;
              goog.dom = {};
              goog.dom.NodeType = {
                ELEMENT: 1,
                ATTRIBUTE: 2,
                TEXT: 3,
                CDATA_SECTION: 4,
                ENTITY_REFERENCE: 5,
                ENTITY: 6,
                PROCESSING_INSTRUCTION: 7,
                COMMENT: 8,
                DOCUMENT: 9,
                DOCUMENT_TYPE: 10,
                DOCUMENT_FRAGMENT: 11,
                NOTATION: 12
              };
              goog.asserts = {};
              goog.asserts.ENABLE_ASSERTS = goog.DEBUG;
              goog.asserts.AssertionError = function (a, b) {
                module$contents$goog$debug$Error_DebugError.call(this, goog.asserts.subs_(a, b));
                this.messagePattern = a;
              };
              goog.inherits(goog.asserts.AssertionError, module$contents$goog$debug$Error_DebugError);
              goog.asserts.AssertionError.prototype.name = 'AssertionError';
              goog.asserts.DEFAULT_ERROR_HANDLER = function (a) {
                throw a;
              };
              goog.asserts.errorHandler_ = goog.asserts.DEFAULT_ERROR_HANDLER;
              goog.asserts.subs_ = function (a, b) {
                a = a.split('%s');
                for (var c = '', d = a.length - 1, e = 0; e < d; e++) c += a[e] + (e < b.length ? b[e] : '%s');
                return c + a[d];
              };
              goog.asserts.doAssertFailure_ = function (a, b, c, d) {
                var e = 'Assertion failed';
                if (c) {
                  e += ': ' + c;
                  var f = d;
                } else a && ((e += ': ' + a), (f = b));
                a = new goog.asserts.AssertionError('' + e, f || []);
                goog.asserts.errorHandler_(a);
              };
              goog.asserts.setErrorHandler = function (a) {
                goog.asserts.ENABLE_ASSERTS && (goog.asserts.errorHandler_ = a);
              };
              goog.asserts.assert = function (a, b, c) {
                goog.asserts.ENABLE_ASSERTS && !a && goog.asserts.doAssertFailure_('', null, b, Array.prototype.slice.call(arguments, 2));
                return a;
              };
              goog.asserts.assertExists = function (a, b, c) {
                goog.asserts.ENABLE_ASSERTS &&
                  null == a &&
                  goog.asserts.doAssertFailure_('Expected to exist: %s.', [a], b, Array.prototype.slice.call(arguments, 2));
                return a;
              };
              goog.asserts.fail = function (a, b) {
                goog.asserts.ENABLE_ASSERTS &&
                  goog.asserts.errorHandler_(new goog.asserts.AssertionError('Failure' + (a ? ': ' + a : ''), Array.prototype.slice.call(arguments, 1)));
              };
              goog.asserts.assertNumber = function (a, b, c) {
                goog.asserts.ENABLE_ASSERTS &&
                  'number' !== typeof a &&
                  goog.asserts.doAssertFailure_('Expected number but got %s: %s.', [goog.typeOf(a), a], b, Array.prototype.slice.call(arguments, 2));
                return a;
              };
              goog.asserts.assertString = function (a, b, c) {
                goog.asserts.ENABLE_ASSERTS &&
                  'string' !== typeof a &&
                  goog.asserts.doAssertFailure_('Expected string but got %s: %s.', [goog.typeOf(a), a], b, Array.prototype.slice.call(arguments, 2));
                return a;
              };
              goog.asserts.assertFunction = function (a, b, c) {
                goog.asserts.ENABLE_ASSERTS &&
                  'function' !== typeof a &&
                  goog.asserts.doAssertFailure_('Expected function but got %s: %s.', [goog.typeOf(a), a], b, Array.prototype.slice.call(arguments, 2));
                return a;
              };
              goog.asserts.assertObject = function (a, b, c) {
                goog.asserts.ENABLE_ASSERTS &&
                  !goog.isObject(a) &&
                  goog.asserts.doAssertFailure_('Expected object but got %s: %s.', [goog.typeOf(a), a], b, Array.prototype.slice.call(arguments, 2));
                return a;
              };
              goog.asserts.assertArray = function (a, b, c) {
                goog.asserts.ENABLE_ASSERTS &&
                  !Array.isArray(a) &&
                  goog.asserts.doAssertFailure_('Expected array but got %s: %s.', [goog.typeOf(a), a], b, Array.prototype.slice.call(arguments, 2));
                return a;
              };
              goog.asserts.assertBoolean = function (a, b, c) {
                goog.asserts.ENABLE_ASSERTS &&
                  'boolean' !== typeof a &&
                  goog.asserts.doAssertFailure_('Expected boolean but got %s: %s.', [goog.typeOf(a), a], b, Array.prototype.slice.call(arguments, 2));
                return a;
              };
              goog.asserts.assertElement = function (a, b, c) {
                !goog.asserts.ENABLE_ASSERTS ||
                  (goog.isObject(a) && a.nodeType == goog.dom.NodeType.ELEMENT) ||
                  goog.asserts.doAssertFailure_('Expected Element but got %s: %s.', [goog.typeOf(a), a], b, Array.prototype.slice.call(arguments, 2));
                return a;
              };
              goog.asserts.assertInstanceof = function (a, b, c, d) {
                !goog.asserts.ENABLE_ASSERTS ||
                  a instanceof b ||
                  goog.asserts.doAssertFailure_(
                    'Expected instanceof %s but got %s.',
                    [goog.asserts.getType_(b), goog.asserts.getType_(a)],
                    c,
                    Array.prototype.slice.call(arguments, 3)
                  );
                return a;
              };
              goog.asserts.assertFinite = function (a, b, c) {
                !goog.asserts.ENABLE_ASSERTS ||
                  ('number' == typeof a && isFinite(a)) ||
                  goog.asserts.doAssertFailure_('Expected %s to be a finite number but it is not.', [a], b, Array.prototype.slice.call(arguments, 2));
                return a;
              };
              goog.asserts.assertObjectPrototypeIsIntact = function () {
                for (var a in Object.prototype) goog.asserts.fail(a + ' should not be enumerable in Object.prototype.');
              };
              goog.asserts.getType_ = function (a) {
                return a instanceof Function
                  ? a.displayName || a.name || 'unknown type name'
                  : a instanceof Object
                    ? a.constructor.displayName || a.constructor.name || Object.prototype.toString.call(a)
                    : null === a
                      ? 'null'
                      : typeof a;
              };
              goog.array = {};
              goog.NATIVE_ARRAY_PROTOTYPES = goog.TRUSTED_SITE;
              var module$contents$goog$array_ASSUME_NATIVE_FUNCTIONS = 2012 < goog.FEATURESET_YEAR;
              goog.array.ASSUME_NATIVE_FUNCTIONS = module$contents$goog$array_ASSUME_NATIVE_FUNCTIONS;
              function module$contents$goog$array_peek(a) {
                return a[a.length - 1];
              }
              goog.array.peek = module$contents$goog$array_peek;
              goog.array.last = module$contents$goog$array_peek;
              var module$contents$goog$array_indexOf =
                goog.NATIVE_ARRAY_PROTOTYPES && (module$contents$goog$array_ASSUME_NATIVE_FUNCTIONS || Array.prototype.indexOf)
                  ? function (a, b, c) {
                      goog.asserts.assert(null != a.length);
                      return Array.prototype.indexOf.call(a, b, c);
                    }
                  : function (a, b, c) {
                      c = null == c ? 0 : 0 > c ? Math.max(0, a.length + c) : c;
                      if ('string' === typeof a) return 'string' !== typeof b || 1 != b.length ? -1 : a.indexOf(b, c);
                      for (; c < a.length; c++) if (c in a && a[c] === b) return c;
                      return -1;
                    };
              goog.array.indexOf = module$contents$goog$array_indexOf;
              var module$contents$goog$array_lastIndexOf =
                goog.NATIVE_ARRAY_PROTOTYPES && (module$contents$goog$array_ASSUME_NATIVE_FUNCTIONS || Array.prototype.lastIndexOf)
                  ? function (a, b, c) {
                      goog.asserts.assert(null != a.length);
                      return Array.prototype.lastIndexOf.call(a, b, null == c ? a.length - 1 : c);
                    }
                  : function (a, b, c) {
                      c = null == c ? a.length - 1 : c;
                      0 > c && (c = Math.max(0, a.length + c));
                      if ('string' === typeof a) return 'string' !== typeof b || 1 != b.length ? -1 : a.lastIndexOf(b, c);
                      for (; 0 <= c; c--) if (c in a && a[c] === b) return c;
                      return -1;
                    };
              goog.array.lastIndexOf = module$contents$goog$array_lastIndexOf;
              var module$contents$goog$array_forEach =
                goog.NATIVE_ARRAY_PROTOTYPES && (module$contents$goog$array_ASSUME_NATIVE_FUNCTIONS || Array.prototype.forEach)
                  ? function (a, b, c) {
                      goog.asserts.assert(null != a.length);
                      Array.prototype.forEach.call(a, b, c);
                    }
                  : function (a, b, c) {
                      for (var d = a.length, e = 'string' === typeof a ? a.split('') : a, f = 0; f < d; f++) f in e && b.call(c, e[f], f, a);
                    };
              goog.array.forEach = module$contents$goog$array_forEach;
              function module$contents$goog$array_forEachRight(a, b, c) {
                var d = a.length,
                  e = 'string' === typeof a ? a.split('') : a;
                for (--d; 0 <= d; --d) d in e && b.call(c, e[d], d, a);
              }
              goog.array.forEachRight = module$contents$goog$array_forEachRight;
              var module$contents$goog$array_filter =
                goog.NATIVE_ARRAY_PROTOTYPES && (module$contents$goog$array_ASSUME_NATIVE_FUNCTIONS || Array.prototype.filter)
                  ? function (a, b, c) {
                      goog.asserts.assert(null != a.length);
                      return Array.prototype.filter.call(a, b, c);
                    }
                  : function (a, b, c) {
                      for (var d = a.length, e = [], f = 0, g = 'string' === typeof a ? a.split('') : a, h = 0; h < d; h++)
                        if (h in g) {
                          var k = g[h];
                          b.call(c, k, h, a) && (e[f++] = k);
                        }
                      return e;
                    };
              goog.array.filter = module$contents$goog$array_filter;
              var module$contents$goog$array_map =
                goog.NATIVE_ARRAY_PROTOTYPES && (module$contents$goog$array_ASSUME_NATIVE_FUNCTIONS || Array.prototype.map)
                  ? function (a, b, c) {
                      goog.asserts.assert(null != a.length);
                      return Array.prototype.map.call(a, b, c);
                    }
                  : function (a, b, c) {
                      for (var d = a.length, e = Array(d), f = 'string' === typeof a ? a.split('') : a, g = 0; g < d; g++)
                        g in f && (e[g] = b.call(c, f[g], g, a));
                      return e;
                    };
              goog.array.map = module$contents$goog$array_map;
              var module$contents$goog$array_reduce =
                goog.NATIVE_ARRAY_PROTOTYPES && (module$contents$goog$array_ASSUME_NATIVE_FUNCTIONS || Array.prototype.reduce)
                  ? function (a, b, c, d) {
                      goog.asserts.assert(null != a.length);
                      d && (b = goog.bind(b, d));
                      return Array.prototype.reduce.call(a, b, c);
                    }
                  : function (a, b, c, d) {
                      var e = c;
                      module$contents$goog$array_forEach(a, function (f, g) {
                        e = b.call(d, e, f, g, a);
                      });
                      return e;
                    };
              goog.array.reduce = module$contents$goog$array_reduce;
              var module$contents$goog$array_reduceRight =
                goog.NATIVE_ARRAY_PROTOTYPES && (module$contents$goog$array_ASSUME_NATIVE_FUNCTIONS || Array.prototype.reduceRight)
                  ? function (a, b, c, d) {
                      goog.asserts.assert(null != a.length);
                      goog.asserts.assert(null != b);
                      d && (b = goog.bind(b, d));
                      return Array.prototype.reduceRight.call(a, b, c);
                    }
                  : function (a, b, c, d) {
                      var e = c;
                      module$contents$goog$array_forEachRight(a, function (f, g) {
                        e = b.call(d, e, f, g, a);
                      });
                      return e;
                    };
              goog.array.reduceRight = module$contents$goog$array_reduceRight;
              var module$contents$goog$array_some =
                goog.NATIVE_ARRAY_PROTOTYPES && (module$contents$goog$array_ASSUME_NATIVE_FUNCTIONS || Array.prototype.some)
                  ? function (a, b, c) {
                      goog.asserts.assert(null != a.length);
                      return Array.prototype.some.call(a, b, c);
                    }
                  : function (a, b, c) {
                      for (var d = a.length, e = 'string' === typeof a ? a.split('') : a, f = 0; f < d; f++) if (f in e && b.call(c, e[f], f, a)) return !0;
                      return !1;
                    };
              goog.array.some = module$contents$goog$array_some;
              var module$contents$goog$array_every =
                goog.NATIVE_ARRAY_PROTOTYPES && (module$contents$goog$array_ASSUME_NATIVE_FUNCTIONS || Array.prototype.every)
                  ? function (a, b, c) {
                      goog.asserts.assert(null != a.length);
                      return Array.prototype.every.call(a, b, c);
                    }
                  : function (a, b, c) {
                      for (var d = a.length, e = 'string' === typeof a ? a.split('') : a, f = 0; f < d; f++) if (f in e && !b.call(c, e[f], f, a)) return !1;
                      return !0;
                    };
              goog.array.every = module$contents$goog$array_every;
              function module$contents$goog$array_count(a, b, c) {
                var d = 0;
                module$contents$goog$array_forEach(
                  a,
                  function (e, f, g) {
                    b.call(c, e, f, g) && ++d;
                  },
                  c
                );
                return d;
              }
              goog.array.count = module$contents$goog$array_count;
              function module$contents$goog$array_find(a, b, c) {
                b = module$contents$goog$array_findIndex(a, b, c);
                return 0 > b ? null : 'string' === typeof a ? a.charAt(b) : a[b];
              }
              goog.array.find = module$contents$goog$array_find;
              function module$contents$goog$array_findIndex(a, b, c) {
                for (var d = a.length, e = 'string' === typeof a ? a.split('') : a, f = 0; f < d; f++) if (f in e && b.call(c, e[f], f, a)) return f;
                return -1;
              }
              goog.array.findIndex = module$contents$goog$array_findIndex;
              function module$contents$goog$array_findRight(a, b, c) {
                b = module$contents$goog$array_findIndexRight(a, b, c);
                return 0 > b ? null : 'string' === typeof a ? a.charAt(b) : a[b];
              }
              goog.array.findRight = module$contents$goog$array_findRight;
              function module$contents$goog$array_findIndexRight(a, b, c) {
                var d = a.length,
                  e = 'string' === typeof a ? a.split('') : a;
                for (--d; 0 <= d; d--) if (d in e && b.call(c, e[d], d, a)) return d;
                return -1;
              }
              goog.array.findIndexRight = module$contents$goog$array_findIndexRight;
              function module$contents$goog$array_contains(a, b) {
                return 0 <= module$contents$goog$array_indexOf(a, b);
              }
              goog.array.contains = module$contents$goog$array_contains;
              function module$contents$goog$array_isEmpty(a) {
                return 0 == a.length;
              }
              goog.array.isEmpty = module$contents$goog$array_isEmpty;
              function module$contents$goog$array_clear(a) {
                if (!Array.isArray(a)) for (var b = a.length - 1; 0 <= b; b--) delete a[b];
                a.length = 0;
              }
              goog.array.clear = module$contents$goog$array_clear;
              function module$contents$goog$array_insert(a, b) {
                module$contents$goog$array_contains(a, b) || a.push(b);
              }
              goog.array.insert = module$contents$goog$array_insert;
              function module$contents$goog$array_insertAt(a, b, c) {
                module$contents$goog$array_splice(a, c, 0, b);
              }
              goog.array.insertAt = module$contents$goog$array_insertAt;
              function module$contents$goog$array_insertArrayAt(a, b, c) {
                goog.partial(module$contents$goog$array_splice, a, c, 0).apply(null, b);
              }
              goog.array.insertArrayAt = module$contents$goog$array_insertArrayAt;
              function module$contents$goog$array_insertBefore(a, b, c) {
                var d;
                2 == arguments.length || 0 > (d = module$contents$goog$array_indexOf(a, c)) ? a.push(b) : module$contents$goog$array_insertAt(a, b, d);
              }
              goog.array.insertBefore = module$contents$goog$array_insertBefore;
              function module$contents$goog$array_remove(a, b) {
                b = module$contents$goog$array_indexOf(a, b);
                var c;
                (c = 0 <= b) && module$contents$goog$array_removeAt(a, b);
                return c;
              }
              goog.array.remove = module$contents$goog$array_remove;
              function module$contents$goog$array_removeLast(a, b) {
                b = module$contents$goog$array_lastIndexOf(a, b);
                return 0 <= b ? (module$contents$goog$array_removeAt(a, b), !0) : !1;
              }
              goog.array.removeLast = module$contents$goog$array_removeLast;
              function module$contents$goog$array_removeAt(a, b) {
                goog.asserts.assert(null != a.length);
                return 1 == Array.prototype.splice.call(a, b, 1).length;
              }
              goog.array.removeAt = module$contents$goog$array_removeAt;
              function module$contents$goog$array_removeIf(a, b, c) {
                b = module$contents$goog$array_findIndex(a, b, c);
                return 0 <= b ? (module$contents$goog$array_removeAt(a, b), !0) : !1;
              }
              goog.array.removeIf = module$contents$goog$array_removeIf;
              function module$contents$goog$array_removeAllIf(a, b, c) {
                var d = 0;
                module$contents$goog$array_forEachRight(a, function (e, f) {
                  b.call(c, e, f, a) && module$contents$goog$array_removeAt(a, f) && d++;
                });
                return d;
              }
              goog.array.removeAllIf = module$contents$goog$array_removeAllIf;
              function module$contents$goog$array_concat(a) {
                return Array.prototype.concat.apply([], arguments);
              }
              goog.array.concat = module$contents$goog$array_concat;
              function module$contents$goog$array_join(a) {
                return Array.prototype.concat.apply([], arguments);
              }
              goog.array.join = module$contents$goog$array_join;
              function module$contents$goog$array_toArray(a) {
                var b = a.length;
                if (0 < b) {
                  for (var c = Array(b), d = 0; d < b; d++) c[d] = a[d];
                  return c;
                }
                return [];
              }
              var module$contents$goog$array_clone = (goog.array.toArray = module$contents$goog$array_toArray);
              goog.array.clone = module$contents$goog$array_clone;
              function module$contents$goog$array_extend(a, b) {
                for (var c = 1; c < arguments.length; c++) {
                  var d = arguments[c];
                  if (goog.isArrayLike(d)) {
                    var e = a.length || 0,
                      f = d.length || 0;
                    a.length = e + f;
                    for (var g = 0; g < f; g++) a[e + g] = d[g];
                  } else a.push(d);
                }
              }
              goog.array.extend = module$contents$goog$array_extend;
              function module$contents$goog$array_splice(a, b, c, d) {
                goog.asserts.assert(null != a.length);
                return Array.prototype.splice.apply(a, module$contents$goog$array_slice(arguments, 1));
              }
              goog.array.splice = module$contents$goog$array_splice;
              function module$contents$goog$array_slice(a, b, c) {
                goog.asserts.assert(null != a.length);
                return 2 >= arguments.length ? Array.prototype.slice.call(a, b) : Array.prototype.slice.call(a, b, c);
              }
              goog.array.slice = module$contents$goog$array_slice;
              function module$contents$goog$array_removeDuplicates(a, b, c) {
                b = b || a;
                var d = function (k) {
                  return goog.isObject(k) ? 'o' + goog.getUid(k) : (typeof k).charAt(0) + k;
                };
                c = c || d;
                d = {};
                for (var e = 0, f = 0; f < a.length; ) {
                  var g = a[f++],
                    h = c(g);
                  Object.prototype.hasOwnProperty.call(d, h) || ((d[h] = !0), (b[e++] = g));
                }
                b.length = e;
              }
              goog.array.removeDuplicates = module$contents$goog$array_removeDuplicates;
              function module$contents$goog$array_binarySearch(a, b, c) {
                return module$contents$goog$array_binarySearch_(a, c || module$contents$goog$array_defaultCompare, !1, b);
              }
              goog.array.binarySearch = module$contents$goog$array_binarySearch;
              function module$contents$goog$array_binarySelect(a, b, c) {
                return module$contents$goog$array_binarySearch_(a, b, !0, void 0, c);
              }
              goog.array.binarySelect = module$contents$goog$array_binarySelect;
              function module$contents$goog$array_binarySearch_(a, b, c, d, e) {
                for (var f = 0, g = a.length, h; f < g; ) {
                  var k = f + ((g - f) >>> 1);
                  var l = c ? b.call(e, a[k], k, a) : b(d, a[k]);
                  0 < l ? (f = k + 1) : ((g = k), (h = !l));
                }
                return h ? f : -f - 1;
              }
              function module$contents$goog$array_sort(a, b) {
                a.sort(b || module$contents$goog$array_defaultCompare);
              }
              goog.array.sort = module$contents$goog$array_sort;
              function module$contents$goog$array_stableSort(a, b) {
                for (var c = Array(a.length), d = 0; d < a.length; d++) c[d] = { index: d, value: a[d] };
                var e = b || module$contents$goog$array_defaultCompare;
                module$contents$goog$array_sort(c, function (f, g) {
                  return e(f.value, g.value) || f.index - g.index;
                });
                for (d = 0; d < a.length; d++) a[d] = c[d].value;
              }
              goog.array.stableSort = module$contents$goog$array_stableSort;
              function module$contents$goog$array_sortByKey(a, b, c) {
                var d = c || module$contents$goog$array_defaultCompare;
                module$contents$goog$array_sort(a, function (e, f) {
                  return d(b(e), b(f));
                });
              }
              goog.array.sortByKey = module$contents$goog$array_sortByKey;
              function module$contents$goog$array_sortObjectsByKey(a, b, c) {
                module$contents$goog$array_sortByKey(
                  a,
                  function (d) {
                    return d[b];
                  },
                  c
                );
              }
              goog.array.sortObjectsByKey = module$contents$goog$array_sortObjectsByKey;
              function module$contents$goog$array_isSorted(a, b, c) {
                b = b || module$contents$goog$array_defaultCompare;
                for (var d = 1; d < a.length; d++) {
                  var e = b(a[d - 1], a[d]);
                  if (0 < e || (0 == e && c)) return !1;
                }
                return !0;
              }
              goog.array.isSorted = module$contents$goog$array_isSorted;
              function module$contents$goog$array_equals(a, b, c) {
                if (!goog.isArrayLike(a) || !goog.isArrayLike(b) || a.length != b.length) return !1;
                var d = a.length;
                c = c || module$contents$goog$array_defaultCompareEquality;
                for (var e = 0; e < d; e++) if (!c(a[e], b[e])) return !1;
                return !0;
              }
              goog.array.equals = module$contents$goog$array_equals;
              function module$contents$goog$array_compare3(a, b, c) {
                c = c || module$contents$goog$array_defaultCompare;
                for (var d = Math.min(a.length, b.length), e = 0; e < d; e++) {
                  var f = c(a[e], b[e]);
                  if (0 != f) return f;
                }
                return module$contents$goog$array_defaultCompare(a.length, b.length);
              }
              goog.array.compare3 = module$contents$goog$array_compare3;
              function module$contents$goog$array_defaultCompare(a, b) {
                return a > b ? 1 : a < b ? -1 : 0;
              }
              goog.array.defaultCompare = module$contents$goog$array_defaultCompare;
              function module$contents$goog$array_inverseDefaultCompare(a, b) {
                return -module$contents$goog$array_defaultCompare(a, b);
              }
              goog.array.inverseDefaultCompare = module$contents$goog$array_inverseDefaultCompare;
              function module$contents$goog$array_defaultCompareEquality(a, b) {
                return a === b;
              }
              goog.array.defaultCompareEquality = module$contents$goog$array_defaultCompareEquality;
              function module$contents$goog$array_binaryInsert(a, b, c) {
                c = module$contents$goog$array_binarySearch(a, b, c);
                return 0 > c ? (module$contents$goog$array_insertAt(a, b, -(c + 1)), !0) : !1;
              }
              goog.array.binaryInsert = module$contents$goog$array_binaryInsert;
              function module$contents$goog$array_binaryRemove(a, b, c) {
                b = module$contents$goog$array_binarySearch(a, b, c);
                return 0 <= b ? module$contents$goog$array_removeAt(a, b) : !1;
              }
              goog.array.binaryRemove = module$contents$goog$array_binaryRemove;
              function module$contents$goog$array_bucket(a, b, c) {
                for (var d = {}, e = 0; e < a.length; e++) {
                  var f = a[e],
                    g = b.call(c, f, e, a);
                  void 0 !== g && (d[g] || (d[g] = [])).push(f);
                }
                return d;
              }
              goog.array.bucket = module$contents$goog$array_bucket;
              function module$contents$goog$array_toObject(a, b, c) {
                var d = {};
                module$contents$goog$array_forEach(a, function (e, f) {
                  d[b.call(c, e, f, a)] = e;
                });
                return d;
              }
              goog.array.toObject = module$contents$goog$array_toObject;
              function module$contents$goog$array_range(a, b, c) {
                var d = [],
                  e = 0,
                  f = a;
                c = c || 1;
                void 0 !== b && ((e = a), (f = b));
                if (0 > c * (f - e)) return [];
                if (0 < c) for (a = e; a < f; a += c) d.push(a);
                else for (a = e; a > f; a += c) d.push(a);
                return d;
              }
              goog.array.range = module$contents$goog$array_range;
              function module$contents$goog$array_repeat(a, b) {
                for (var c = [], d = 0; d < b; d++) c[d] = a;
                return c;
              }
              goog.array.repeat = module$contents$goog$array_repeat;
              function module$contents$goog$array_flatten(a) {
                for (var b = [], c = 0; c < arguments.length; c++) {
                  var d = arguments[c];
                  if (Array.isArray(d))
                    for (var e = 0; e < d.length; e += 8192) {
                      var f = module$contents$goog$array_slice(d, e, e + 8192);
                      f = module$contents$goog$array_flatten.apply(null, f);
                      for (var g = 0; g < f.length; g++) b.push(f[g]);
                    }
                  else b.push(d);
                }
                return b;
              }
              goog.array.flatten = module$contents$goog$array_flatten;
              function module$contents$goog$array_rotate(a, b) {
                goog.asserts.assert(null != a.length);
                a.length &&
                  ((b %= a.length), 0 < b ? Array.prototype.unshift.apply(a, a.splice(-b, b)) : 0 > b && Array.prototype.push.apply(a, a.splice(0, -b)));
                return a;
              }
              goog.array.rotate = module$contents$goog$array_rotate;
              function module$contents$goog$array_moveItem(a, b, c) {
                goog.asserts.assert(0 <= b && b < a.length);
                goog.asserts.assert(0 <= c && c < a.length);
                b = Array.prototype.splice.call(a, b, 1);
                Array.prototype.splice.call(a, c, 0, b[0]);
              }
              goog.array.moveItem = module$contents$goog$array_moveItem;
              function module$contents$goog$array_zip(a) {
                if (!arguments.length) return [];
                for (var b = [], c = arguments[0].length, d = 1; d < arguments.length; d++) arguments[d].length < c && (c = arguments[d].length);
                for (d = 0; d < c; d++) {
                  for (var e = [], f = 0; f < arguments.length; f++) e.push(arguments[f][d]);
                  b.push(e);
                }
                return b;
              }
              goog.array.zip = module$contents$goog$array_zip;
              function module$contents$goog$array_shuffle(a, b) {
                b = b || Math.random;
                for (var c = a.length - 1; 0 < c; c--) {
                  var d = Math.floor(b() * (c + 1)),
                    e = a[c];
                  a[c] = a[d];
                  a[d] = e;
                }
              }
              goog.array.shuffle = module$contents$goog$array_shuffle;
              function module$contents$goog$array_copyByIndex(a, b) {
                var c = [];
                module$contents$goog$array_forEach(b, function (d) {
                  c.push(a[d]);
                });
                return c;
              }
              goog.array.copyByIndex = module$contents$goog$array_copyByIndex;
              function module$contents$goog$array_concatMap(a, b, c) {
                return module$contents$goog$array_concat.apply([], module$contents$goog$array_map(a, b, c));
              }
              goog.array.concatMap = module$contents$goog$array_concatMap;
              goog.dom.asserts = {};
              goog.dom.asserts.assertIsLocation = function (a) {
                if (goog.asserts.ENABLE_ASSERTS) {
                  var b = goog.dom.asserts.getWindow_(a);
                  b &&
                    (!a || (!(a instanceof b.Location) && a instanceof b.Element)) &&
                    goog.asserts.fail('Argument is not a Location (or a non-Element mock); got: %s', goog.dom.asserts.debugStringForType_(a));
                }
                return a;
              };
              goog.dom.asserts.assertIsElementType_ = function (a, b) {
                if (goog.asserts.ENABLE_ASSERTS) {
                  var c = goog.dom.asserts.getWindow_(a);
                  c &&
                    'undefined' != typeof c[b] &&
                    ((a && (a instanceof c[b] || !(a instanceof c.Location || a instanceof c.Element))) ||
                      goog.asserts.fail('Argument is not a %s (or a non-Element, non-Location mock); got: %s', b, goog.dom.asserts.debugStringForType_(a)));
                }
                return a;
              };
              goog.dom.asserts.assertIsHTMLAnchorElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLAnchorElement');
              };
              goog.dom.asserts.assertIsHTMLButtonElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLButtonElement');
              };
              goog.dom.asserts.assertIsHTMLLinkElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLLinkElement');
              };
              goog.dom.asserts.assertIsHTMLImageElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLImageElement');
              };
              goog.dom.asserts.assertIsHTMLAudioElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLAudioElement');
              };
              goog.dom.asserts.assertIsHTMLVideoElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLVideoElement');
              };
              goog.dom.asserts.assertIsHTMLInputElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLInputElement');
              };
              goog.dom.asserts.assertIsHTMLTextAreaElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLTextAreaElement');
              };
              goog.dom.asserts.assertIsHTMLCanvasElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLCanvasElement');
              };
              goog.dom.asserts.assertIsHTMLEmbedElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLEmbedElement');
              };
              goog.dom.asserts.assertIsHTMLFormElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLFormElement');
              };
              goog.dom.asserts.assertIsHTMLFrameElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLFrameElement');
              };
              goog.dom.asserts.assertIsHTMLIFrameElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLIFrameElement');
              };
              goog.dom.asserts.assertIsHTMLObjectElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLObjectElement');
              };
              goog.dom.asserts.assertIsHTMLScriptElement = function (a) {
                return goog.dom.asserts.assertIsElementType_(a, 'HTMLScriptElement');
              };
              goog.dom.asserts.debugStringForType_ = function (a) {
                if (goog.isObject(a))
                  try {
                    return a.constructor.displayName || a.constructor.name || Object.prototype.toString.call(a);
                  } catch (b) {
                    return '<object could not be stringified>';
                  }
                else return void 0 === a ? 'undefined' : null === a ? 'null' : typeof a;
              };
              goog.dom.asserts.getWindow_ = function (a) {
                try {
                  var b = a && a.ownerDocument,
                    c = b && (b.defaultView || b.parentWindow);
                  c = c || goog.global;
                  if (c.Element && c.Location) return c;
                } catch (d) {}
                return null;
              };
              goog.dom.HtmlElement = function () {};
              goog.functions = {};
              goog.functions.constant = function (a) {
                return function () {
                  return a;
                };
              };
              goog.functions.FALSE = function () {
                return !1;
              };
              goog.functions.TRUE = function () {
                return !0;
              };
              goog.functions.NULL = function () {
                return null;
              };
              goog.functions.identity = function (a, b) {
                return a;
              };
              goog.functions.error = function (a) {
                return function () {
                  throw Error(a);
                };
              };
              goog.functions.fail = function (a) {
                return function () {
                  throw a;
                };
              };
              goog.functions.lock = function (a, b) {
                b = b || 0;
                return function () {
                  return a.apply(this, Array.prototype.slice.call(arguments, 0, b));
                };
              };
              goog.functions.nth = function (a) {
                return function () {
                  return arguments[a];
                };
              };
              goog.functions.partialRight = function (a, b) {
                var c = Array.prototype.slice.call(arguments, 1);
                return function () {
                  var d = this;
                  d === goog.global && (d = void 0);
                  var e = Array.prototype.slice.call(arguments);
                  e.push.apply(e, c);
                  return a.apply(d, e);
                };
              };
              goog.functions.withReturnValue = function (a, b) {
                return goog.functions.sequence(a, goog.functions.constant(b));
              };
              goog.functions.equalTo = function (a, b) {
                return function (c) {
                  return b ? a == c : a === c;
                };
              };
              goog.functions.compose = function (a, b) {
                var c = arguments,
                  d = c.length;
                return function () {
                  var e;
                  d && (e = c[d - 1].apply(this, arguments));
                  for (var f = d - 2; 0 <= f; f--) e = c[f].call(this, e);
                  return e;
                };
              };
              goog.functions.sequence = function (a) {
                var b = arguments,
                  c = b.length;
                return function () {
                  for (var d, e = 0; e < c; e++) d = b[e].apply(this, arguments);
                  return d;
                };
              };
              goog.functions.and = function (a) {
                var b = arguments,
                  c = b.length;
                return function () {
                  for (var d = 0; d < c; d++) if (!b[d].apply(this, arguments)) return !1;
                  return !0;
                };
              };
              goog.functions.or = function (a) {
                var b = arguments,
                  c = b.length;
                return function () {
                  for (var d = 0; d < c; d++) if (b[d].apply(this, arguments)) return !0;
                  return !1;
                };
              };
              goog.functions.not = function (a) {
                return function () {
                  return !a.apply(this, arguments);
                };
              };
              goog.functions.create = function (a, b) {
                var c = function () {};
                c.prototype = a.prototype;
                c = new c();
                a.apply(c, Array.prototype.slice.call(arguments, 1));
                return c;
              };
              goog.functions.CACHE_RETURN_VALUE = !0;
              goog.functions.cacheReturnValue = function (a) {
                var b = !1,
                  c;
                return function () {
                  if (!goog.functions.CACHE_RETURN_VALUE) return a();
                  b || ((c = a()), (b = !0));
                  return c;
                };
              };
              goog.functions.once = function (a) {
                var b = a;
                return function () {
                  if (b) {
                    var c = b;
                    b = null;
                    c();
                  }
                };
              };
              goog.functions.debounce = function (a, b, c) {
                var d = 0;
                return function (e) {
                  goog.global.clearTimeout(d);
                  var f = arguments;
                  d = goog.global.setTimeout(function () {
                    a.apply(c, f);
                  }, b);
                };
              };
              goog.functions.throttle = function (a, b, c) {
                var d = 0,
                  e = !1,
                  f = [],
                  g = function () {
                    d = 0;
                    e && ((e = !1), h());
                  },
                  h = function () {
                    d = goog.global.setTimeout(g, b);
                    a.apply(c, f);
                  };
                return function (k) {
                  f = arguments;
                  d ? (e = !0) : h();
                };
              };
              goog.functions.rateLimit = function (a, b, c) {
                var d = 0,
                  e = function () {
                    d = 0;
                  };
                return function (f) {
                  d || ((d = goog.global.setTimeout(e, b)), a.apply(c, arguments));
                };
              };
              goog.functions.isFunction = function (a) {
                return 'function' === typeof a;
              };
              goog.dom.TagName = function () {};
              goog.dom.TagName.cast = function (a, b) {
                return a;
              };
              goog.dom.TagName.prototype.toString = function () {};
              goog.dom.TagName.A = 'A';
              goog.dom.TagName.ABBR = 'ABBR';
              goog.dom.TagName.ACRONYM = 'ACRONYM';
              goog.dom.TagName.ADDRESS = 'ADDRESS';
              goog.dom.TagName.APPLET = 'APPLET';
              goog.dom.TagName.AREA = 'AREA';
              goog.dom.TagName.ARTICLE = 'ARTICLE';
              goog.dom.TagName.ASIDE = 'ASIDE';
              goog.dom.TagName.AUDIO = 'AUDIO';
              goog.dom.TagName.B = 'B';
              goog.dom.TagName.BASE = 'BASE';
              goog.dom.TagName.BASEFONT = 'BASEFONT';
              goog.dom.TagName.BDI = 'BDI';
              goog.dom.TagName.BDO = 'BDO';
              goog.dom.TagName.BIG = 'BIG';
              goog.dom.TagName.BLOCKQUOTE = 'BLOCKQUOTE';
              goog.dom.TagName.BODY = 'BODY';
              goog.dom.TagName.BR = 'BR';
              goog.dom.TagName.BUTTON = 'BUTTON';
              goog.dom.TagName.CANVAS = 'CANVAS';
              goog.dom.TagName.CAPTION = 'CAPTION';
              goog.dom.TagName.CENTER = 'CENTER';
              goog.dom.TagName.CITE = 'CITE';
              goog.dom.TagName.CODE = 'CODE';
              goog.dom.TagName.COL = 'COL';
              goog.dom.TagName.COLGROUP = 'COLGROUP';
              goog.dom.TagName.COMMAND = 'COMMAND';
              goog.dom.TagName.DATA = 'DATA';
              goog.dom.TagName.DATALIST = 'DATALIST';
              goog.dom.TagName.DD = 'DD';
              goog.dom.TagName.DEL = 'DEL';
              goog.dom.TagName.DETAILS = 'DETAILS';
              goog.dom.TagName.DFN = 'DFN';
              goog.dom.TagName.DIALOG = 'DIALOG';
              goog.dom.TagName.DIR = 'DIR';
              goog.dom.TagName.DIV = 'DIV';
              goog.dom.TagName.DL = 'DL';
              goog.dom.TagName.DT = 'DT';
              goog.dom.TagName.EM = 'EM';
              goog.dom.TagName.EMBED = 'EMBED';
              goog.dom.TagName.FIELDSET = 'FIELDSET';
              goog.dom.TagName.FIGCAPTION = 'FIGCAPTION';
              goog.dom.TagName.FIGURE = 'FIGURE';
              goog.dom.TagName.FONT = 'FONT';
              goog.dom.TagName.FOOTER = 'FOOTER';
              goog.dom.TagName.FORM = 'FORM';
              goog.dom.TagName.FRAME = 'FRAME';
              goog.dom.TagName.FRAMESET = 'FRAMESET';
              goog.dom.TagName.H1 = 'H1';
              goog.dom.TagName.H2 = 'H2';
              goog.dom.TagName.H3 = 'H3';
              goog.dom.TagName.H4 = 'H4';
              goog.dom.TagName.H5 = 'H5';
              goog.dom.TagName.H6 = 'H6';
              goog.dom.TagName.HEAD = 'HEAD';
              goog.dom.TagName.HEADER = 'HEADER';
              goog.dom.TagName.HGROUP = 'HGROUP';
              goog.dom.TagName.HR = 'HR';
              goog.dom.TagName.HTML = 'HTML';
              goog.dom.TagName.I = 'I';
              goog.dom.TagName.IFRAME = 'IFRAME';
              goog.dom.TagName.IMG = 'IMG';
              goog.dom.TagName.INPUT = 'INPUT';
              goog.dom.TagName.INS = 'INS';
              goog.dom.TagName.ISINDEX = 'ISINDEX';
              goog.dom.TagName.KBD = 'KBD';
              goog.dom.TagName.KEYGEN = 'KEYGEN';
              goog.dom.TagName.LABEL = 'LABEL';
              goog.dom.TagName.LEGEND = 'LEGEND';
              goog.dom.TagName.LI = 'LI';
              goog.dom.TagName.LINK = 'LINK';
              goog.dom.TagName.MAIN = 'MAIN';
              goog.dom.TagName.MAP = 'MAP';
              goog.dom.TagName.MARK = 'MARK';
              goog.dom.TagName.MATH = 'MATH';
              goog.dom.TagName.MENU = 'MENU';
              goog.dom.TagName.MENUITEM = 'MENUITEM';
              goog.dom.TagName.META = 'META';
              goog.dom.TagName.METER = 'METER';
              goog.dom.TagName.NAV = 'NAV';
              goog.dom.TagName.NOFRAMES = 'NOFRAMES';
              goog.dom.TagName.NOSCRIPT = 'NOSCRIPT';
              goog.dom.TagName.OBJECT = 'OBJECT';
              goog.dom.TagName.OL = 'OL';
              goog.dom.TagName.OPTGROUP = 'OPTGROUP';
              goog.dom.TagName.OPTION = 'OPTION';
              goog.dom.TagName.OUTPUT = 'OUTPUT';
              goog.dom.TagName.P = 'P';
              goog.dom.TagName.PARAM = 'PARAM';
              goog.dom.TagName.PICTURE = 'PICTURE';
              goog.dom.TagName.PRE = 'PRE';
              goog.dom.TagName.PROGRESS = 'PROGRESS';
              goog.dom.TagName.Q = 'Q';
              goog.dom.TagName.RP = 'RP';
              goog.dom.TagName.RT = 'RT';
              goog.dom.TagName.RTC = 'RTC';
              goog.dom.TagName.RUBY = 'RUBY';
              goog.dom.TagName.S = 'S';
              goog.dom.TagName.SAMP = 'SAMP';
              goog.dom.TagName.SCRIPT = 'SCRIPT';
              goog.dom.TagName.SECTION = 'SECTION';
              goog.dom.TagName.SELECT = 'SELECT';
              goog.dom.TagName.SMALL = 'SMALL';
              goog.dom.TagName.SOURCE = 'SOURCE';
              goog.dom.TagName.SPAN = 'SPAN';
              goog.dom.TagName.STRIKE = 'STRIKE';
              goog.dom.TagName.STRONG = 'STRONG';
              goog.dom.TagName.STYLE = 'STYLE';
              goog.dom.TagName.SUB = 'SUB';
              goog.dom.TagName.SUMMARY = 'SUMMARY';
              goog.dom.TagName.SUP = 'SUP';
              goog.dom.TagName.SVG = 'SVG';
              goog.dom.TagName.TABLE = 'TABLE';
              goog.dom.TagName.TBODY = 'TBODY';
              goog.dom.TagName.TD = 'TD';
              goog.dom.TagName.TEMPLATE = 'TEMPLATE';
              goog.dom.TagName.TEXTAREA = 'TEXTAREA';
              goog.dom.TagName.TFOOT = 'TFOOT';
              goog.dom.TagName.TH = 'TH';
              goog.dom.TagName.THEAD = 'THEAD';
              goog.dom.TagName.TIME = 'TIME';
              goog.dom.TagName.TITLE = 'TITLE';
              goog.dom.TagName.TR = 'TR';
              goog.dom.TagName.TRACK = 'TRACK';
              goog.dom.TagName.TT = 'TT';
              goog.dom.TagName.U = 'U';
              goog.dom.TagName.UL = 'UL';
              goog.dom.TagName.VAR = 'VAR';
              goog.dom.TagName.VIDEO = 'VIDEO';
              goog.dom.TagName.WBR = 'WBR';
              goog.object = {};
              goog.object.forEach = function (a, b, c) {
                for (var d in a) b.call(c, a[d], d, a);
              };
              goog.object.filter = function (a, b, c) {
                var d = {},
                  e;
                for (e in a) b.call(c, a[e], e, a) && (d[e] = a[e]);
                return d;
              };
              goog.object.map = function (a, b, c) {
                var d = {},
                  e;
                for (e in a) d[e] = b.call(c, a[e], e, a);
                return d;
              };
              goog.object.some = function (a, b, c) {
                for (var d in a) if (b.call(c, a[d], d, a)) return !0;
                return !1;
              };
              goog.object.every = function (a, b, c) {
                for (var d in a) if (!b.call(c, a[d], d, a)) return !1;
                return !0;
              };
              goog.object.getCount = function (a) {
                var b = 0,
                  c;
                for (c in a) b++;
                return b;
              };
              goog.object.getAnyKey = function (a) {
                for (var b in a) return b;
              };
              goog.object.getAnyValue = function (a) {
                for (var b in a) return a[b];
              };
              goog.object.contains = function (a, b) {
                return goog.object.containsValue(a, b);
              };
              goog.object.getValues = function (a) {
                var b = [],
                  c = 0,
                  d;
                for (d in a) b[c++] = a[d];
                return b;
              };
              goog.object.getKeys = function (a) {
                var b = [],
                  c = 0,
                  d;
                for (d in a) b[c++] = d;
                return b;
              };
              goog.object.getValueByKeys = function (a, b) {
                var c = goog.isArrayLike(b),
                  d = c ? b : arguments;
                for (c = c ? 0 : 1; c < d.length; c++) {
                  if (null == a) return;
                  a = a[d[c]];
                }
                return a;
              };
              goog.object.containsKey = function (a, b) {
                return null !== a && b in a;
              };
              goog.object.containsValue = function (a, b) {
                for (var c in a) if (a[c] == b) return !0;
                return !1;
              };
              goog.object.findKey = function (a, b, c) {
                for (var d in a) if (b.call(c, a[d], d, a)) return d;
              };
              goog.object.findValue = function (a, b, c) {
                return (b = goog.object.findKey(a, b, c)) && a[b];
              };
              goog.object.isEmpty = function (a) {
                for (var b in a) return !1;
                return !0;
              };
              goog.object.clear = function (a) {
                for (var b in a) delete a[b];
              };
              goog.object.remove = function (a, b) {
                var c;
                (c = b in a) && delete a[b];
                return c;
              };
              goog.object.add = function (a, b, c) {
                if (null !== a && b in a) throw Error('The object already contains the key "' + b + '"');
                goog.object.set(a, b, c);
              };
              goog.object.get = function (a, b, c) {
                return null !== a && b in a ? a[b] : c;
              };
              goog.object.set = function (a, b, c) {
                a[b] = c;
              };
              goog.object.setIfUndefined = function (a, b, c) {
                return b in a ? a[b] : (a[b] = c);
              };
              goog.object.setWithReturnValueIfNotSet = function (a, b, c) {
                if (b in a) return a[b];
                c = c();
                return (a[b] = c);
              };
              goog.object.equals = function (a, b) {
                for (var c in a) if (!(c in b) || a[c] !== b[c]) return !1;
                for (var d in b) if (!(d in a)) return !1;
                return !0;
              };
              goog.object.clone = function (a) {
                var b = {},
                  c;
                for (c in a) b[c] = a[c];
                return b;
              };
              goog.object.unsafeClone = function (a) {
                if (!a || 'object' !== typeof a) return a;
                if ('function' === typeof a.clone) return a.clone();
                var b = Array.isArray(a)
                    ? []
                    : 'function' !== typeof ArrayBuffer || 'function' !== typeof ArrayBuffer.isView || !ArrayBuffer.isView(a) || a instanceof DataView
                      ? {}
                      : new a.constructor(a.length),
                  c;
                for (c in a) b[c] = goog.object.unsafeClone(a[c]);
                return b;
              };
              goog.object.transpose = function (a) {
                var b = {},
                  c;
                for (c in a) b[a[c]] = c;
                return b;
              };
              goog.object.PROTOTYPE_FIELDS_ = 'constructor hasOwnProperty isPrototypeOf propertyIsEnumerable toLocaleString toString valueOf'.split(' ');
              goog.object.extend = function (a, b) {
                for (var c, d, e = 1; e < arguments.length; e++) {
                  d = arguments[e];
                  for (c in d) a[c] = d[c];
                  for (var f = 0; f < goog.object.PROTOTYPE_FIELDS_.length; f++)
                    ((c = goog.object.PROTOTYPE_FIELDS_[f]), Object.prototype.hasOwnProperty.call(d, c) && (a[c] = d[c]));
                }
              };
              goog.object.create = function (a) {
                var b = arguments.length;
                if (1 == b && Array.isArray(arguments[0])) return goog.object.create.apply(null, arguments[0]);
                if (b % 2) throw Error('Uneven number of arguments');
                for (var c = {}, d = 0; d < b; d += 2) c[arguments[d]] = arguments[d + 1];
                return c;
              };
              goog.object.createSet = function (a) {
                var b = arguments.length;
                if (1 == b && Array.isArray(arguments[0])) return goog.object.createSet.apply(null, arguments[0]);
                for (var c = {}, d = 0; d < b; d++) c[arguments[d]] = !0;
                return c;
              };
              goog.object.createImmutableView = function (a) {
                var b = a;
                Object.isFrozen && !Object.isFrozen(a) && ((b = Object.create(a)), Object.freeze(b));
                return b;
              };
              goog.object.isImmutableView = function (a) {
                return !!Object.isFrozen && Object.isFrozen(a);
              };
              goog.object.getAllPropertyNames = function (a, b, c) {
                if (!a) return [];
                if (!Object.getOwnPropertyNames || !Object.getPrototypeOf) return goog.object.getKeys(a);
                for (var d = {}; a && (a !== Object.prototype || b) && (a !== Function.prototype || c); ) {
                  for (var e = Object.getOwnPropertyNames(a), f = 0; f < e.length; f++) d[e[f]] = !0;
                  a = Object.getPrototypeOf(a);
                }
                return goog.object.getKeys(d);
              };
              goog.object.getSuperClass = function (a) {
                return (a = Object.getPrototypeOf(a.prototype)) && a.constructor;
              };
              goog.dom.tags = {};
              goog.dom.tags.VOID_TAGS_ = {
                area: !0,
                base: !0,
                br: !0,
                col: !0,
                command: !0,
                embed: !0,
                hr: !0,
                img: !0,
                input: !0,
                keygen: !0,
                link: !0,
                meta: !0,
                param: !0,
                source: !0,
                track: !0,
                wbr: !0
              };
              goog.dom.tags.isVoidTag = function (a) {
                return !0 === goog.dom.tags.VOID_TAGS_[a];
              };
              goog.string = {};
              goog.string.TypedString = function () {};
              goog.string.Const = function (a, b) {
                this.stringConstValueWithSecurityContract__googStringSecurityPrivate_ =
                  (a === goog.string.Const.GOOG_STRING_CONSTRUCTOR_TOKEN_PRIVATE_ && b) || '';
                this.STRING_CONST_TYPE_MARKER__GOOG_STRING_SECURITY_PRIVATE_ = goog.string.Const.TYPE_MARKER_;
              };
              goog.string.Const.prototype.implementsGoogStringTypedString = !0;
              goog.string.Const.prototype.getTypedStringValue = function () {
                return this.stringConstValueWithSecurityContract__googStringSecurityPrivate_;
              };
              goog.DEBUG &&
                (goog.string.Const.prototype.toString = function () {
                  return 'Const{' + this.stringConstValueWithSecurityContract__googStringSecurityPrivate_ + '}';
                });
              goog.string.Const.unwrap = function (a) {
                if (
                  a instanceof goog.string.Const &&
                  a.constructor === goog.string.Const &&
                  a.STRING_CONST_TYPE_MARKER__GOOG_STRING_SECURITY_PRIVATE_ === goog.string.Const.TYPE_MARKER_
                )
                  return a.stringConstValueWithSecurityContract__googStringSecurityPrivate_;
                goog.asserts.fail("expected object of type Const, got '" + a + "'");
                return 'type_error:Const';
              };
              goog.string.Const.from = function (a) {
                return new goog.string.Const(goog.string.Const.GOOG_STRING_CONSTRUCTOR_TOKEN_PRIVATE_, a);
              };
              goog.string.Const.TYPE_MARKER_ = {};
              goog.string.Const.GOOG_STRING_CONSTRUCTOR_TOKEN_PRIVATE_ = {};
              goog.string.Const.EMPTY = goog.string.Const.from('');
              goog.html = {};
              goog.html.trustedtypes = {};
              goog.html.trustedtypes.getPolicyPrivateDoNotAccessOrElse = function () {
                if (!goog.TRUSTED_TYPES_POLICY_NAME) return null;
                void 0 === goog.html.trustedtypes.cachedPolicy_ &&
                  (goog.html.trustedtypes.cachedPolicy_ = goog.createTrustedTypesPolicy(goog.TRUSTED_TYPES_POLICY_NAME + '#html'));
                return goog.html.trustedtypes.cachedPolicy_;
              };
              var module$contents$goog$html$SafeScript_CONSTRUCTOR_TOKEN_PRIVATE = {},
                module$contents$goog$html$SafeScript_SafeScript = function (a, b) {
                  this.privateDoNotAccessOrElseSafeScriptWrappedValue_ = b === module$contents$goog$html$SafeScript_CONSTRUCTOR_TOKEN_PRIVATE ? a : '';
                  this.implementsGoogStringTypedString = !0;
                };
              module$contents$goog$html$SafeScript_SafeScript.fromConstant = function (a) {
                a = goog.string.Const.unwrap(a);
                return 0 === a.length
                  ? module$contents$goog$html$SafeScript_SafeScript.EMPTY
                  : module$contents$goog$html$SafeScript_SafeScript.createSafeScriptSecurityPrivateDoNotAccessOrElse(a);
              };
              module$contents$goog$html$SafeScript_SafeScript.fromConstantAndArgs = function (a, b) {
                for (var c = [], d = 1; d < arguments.length; d++) c.push(module$contents$goog$html$SafeScript_SafeScript.stringify_(arguments[d]));
                return module$contents$goog$html$SafeScript_SafeScript.createSafeScriptSecurityPrivateDoNotAccessOrElse(
                  '(' + goog.string.Const.unwrap(a) + ')(' + c.join(', ') + ');'
                );
              };
              module$contents$goog$html$SafeScript_SafeScript.fromJson = function (a) {
                return module$contents$goog$html$SafeScript_SafeScript.createSafeScriptSecurityPrivateDoNotAccessOrElse(
                  module$contents$goog$html$SafeScript_SafeScript.stringify_(a)
                );
              };
              module$contents$goog$html$SafeScript_SafeScript.prototype.getTypedStringValue = function () {
                return this.privateDoNotAccessOrElseSafeScriptWrappedValue_.toString();
              };
              module$contents$goog$html$SafeScript_SafeScript.unwrap = function (a) {
                return module$contents$goog$html$SafeScript_SafeScript.unwrapTrustedScript(a).toString();
              };
              module$contents$goog$html$SafeScript_SafeScript.unwrapTrustedScript = function (a) {
                if (a instanceof module$contents$goog$html$SafeScript_SafeScript && a.constructor === module$contents$goog$html$SafeScript_SafeScript)
                  return a.privateDoNotAccessOrElseSafeScriptWrappedValue_;
                (0, goog.asserts.fail)("expected object of type SafeScript, got '" + a + "' of type " + goog.typeOf(a));
                return 'type_error:SafeScript';
              };
              module$contents$goog$html$SafeScript_SafeScript.stringify_ = function (a) {
                return JSON.stringify(a).replace(/</g, '\\x3c');
              };
              module$contents$goog$html$SafeScript_SafeScript.createSafeScriptSecurityPrivateDoNotAccessOrElse = function (a) {
                var b = goog.html.trustedtypes.getPolicyPrivateDoNotAccessOrElse();
                a = b ? b.createScript(a) : a;
                return new module$contents$goog$html$SafeScript_SafeScript(a, module$contents$goog$html$SafeScript_CONSTRUCTOR_TOKEN_PRIVATE);
              };
              goog.DEBUG &&
                (module$contents$goog$html$SafeScript_SafeScript.prototype.toString = function () {
                  return 'SafeScript{' + this.privateDoNotAccessOrElseSafeScriptWrappedValue_ + '}';
                });
              module$contents$goog$html$SafeScript_SafeScript.EMPTY = (function () {
                return module$contents$goog$html$SafeScript_SafeScript.createSafeScriptSecurityPrivateDoNotAccessOrElse('');
              })();
              goog.html.SafeScript = module$contents$goog$html$SafeScript_SafeScript;
              goog.fs = {};
              goog.fs.url = {};
              goog.fs.url.createObjectUrl = function (a) {
                return goog.fs.url.getUrlObject_().createObjectURL(a);
              };
              goog.fs.url.revokeObjectUrl = function (a) {
                goog.fs.url.getUrlObject_().revokeObjectURL(a);
              };
              goog.fs.url.UrlObject_ = function () {};
              goog.fs.url.UrlObject_.prototype.createObjectURL = function (a) {};
              goog.fs.url.UrlObject_.prototype.revokeObjectURL = function (a) {};
              goog.fs.url.getUrlObject_ = function () {
                var a = goog.fs.url.findUrlObject_();
                if (null != a) return a;
                throw Error("This browser doesn't seem to support blob URLs");
              };
              goog.fs.url.findUrlObject_ = function () {
                return void 0 !== goog.global.URL && void 0 !== goog.global.URL.createObjectURL
                  ? goog.global.URL
                  : void 0 !== goog.global.webkitURL && void 0 !== goog.global.webkitURL.createObjectURL
                    ? goog.global.webkitURL
                    : void 0 !== goog.global.createObjectURL
                      ? goog.global
                      : null;
              };
              goog.fs.url.browserSupportsObjectUrls = function () {
                return null != goog.fs.url.findUrlObject_();
              };
              goog.fs.blob = {};
              goog.fs.blob.getBlob = function (a) {
                var b = goog.global.BlobBuilder || goog.global.WebKitBlobBuilder;
                if (void 0 !== b) {
                  b = new b();
                  for (var c = 0; c < arguments.length; c++) b.append(arguments[c]);
                  return b.getBlob();
                }
                return goog.fs.blob.getBlobWithProperties(module$contents$goog$array_toArray(arguments));
              };
              goog.fs.blob.getBlobWithProperties = function (a, b, c) {
                var d = goog.global.BlobBuilder || goog.global.WebKitBlobBuilder;
                if (void 0 !== d) {
                  d = new d();
                  for (var e = 0; e < a.length; e++) d.append(a[e], c);
                  return d.getBlob(b);
                }
                if (void 0 !== goog.global.Blob) return ((d = {}), b && (d.type = b), c && (d.endings = c), new Blob(a, d));
                throw Error("This browser doesn't seem to support creating Blobs");
              };
              goog.i18n = {};
              goog.i18n.bidi = {};
              goog.i18n.bidi.FORCE_RTL = !1;
              goog.i18n.bidi.IS_RTL =
                goog.i18n.bidi.FORCE_RTL ||
                (('ar' == goog.LOCALE.substring(0, 2).toLowerCase() ||
                  'fa' == goog.LOCALE.substring(0, 2).toLowerCase() ||
                  'he' == goog.LOCALE.substring(0, 2).toLowerCase() ||
                  'iw' == goog.LOCALE.substring(0, 2).toLowerCase() ||
                  'ps' == goog.LOCALE.substring(0, 2).toLowerCase() ||
                  'sd' == goog.LOCALE.substring(0, 2).toLowerCase() ||
                  'ug' == goog.LOCALE.substring(0, 2).toLowerCase() ||
                  'ur' == goog.LOCALE.substring(0, 2).toLowerCase() ||
                  'yi' == goog.LOCALE.substring(0, 2).toLowerCase()) &&
                  (2 == goog.LOCALE.length || '-' == goog.LOCALE.substring(2, 3) || '_' == goog.LOCALE.substring(2, 3))) ||
                (3 <= goog.LOCALE.length &&
                  'ckb' == goog.LOCALE.substring(0, 3).toLowerCase() &&
                  (3 == goog.LOCALE.length || '-' == goog.LOCALE.substring(3, 4) || '_' == goog.LOCALE.substring(3, 4))) ||
                (7 <= goog.LOCALE.length &&
                  ('-' == goog.LOCALE.substring(2, 3) || '_' == goog.LOCALE.substring(2, 3)) &&
                  ('adlm' == goog.LOCALE.substring(3, 7).toLowerCase() ||
                    'arab' == goog.LOCALE.substring(3, 7).toLowerCase() ||
                    'hebr' == goog.LOCALE.substring(3, 7).toLowerCase() ||
                    'nkoo' == goog.LOCALE.substring(3, 7).toLowerCase() ||
                    'rohg' == goog.LOCALE.substring(3, 7).toLowerCase() ||
                    'thaa' == goog.LOCALE.substring(3, 7).toLowerCase())) ||
                (8 <= goog.LOCALE.length &&
                  ('-' == goog.LOCALE.substring(3, 4) || '_' == goog.LOCALE.substring(3, 4)) &&
                  ('adlm' == goog.LOCALE.substring(4, 8).toLowerCase() ||
                    'arab' == goog.LOCALE.substring(4, 8).toLowerCase() ||
                    'hebr' == goog.LOCALE.substring(4, 8).toLowerCase() ||
                    'nkoo' == goog.LOCALE.substring(4, 8).toLowerCase() ||
                    'rohg' == goog.LOCALE.substring(4, 8).toLowerCase() ||
                    'thaa' == goog.LOCALE.substring(4, 8).toLowerCase()));
              goog.i18n.bidi.Format = { LRE: '\u202a', RLE: '\u202b', PDF: '\u202c', LRM: '\u200e', RLM: '\u200f' };
              goog.i18n.bidi.Dir = { LTR: 1, RTL: -1, NEUTRAL: 0 };
              goog.i18n.bidi.RIGHT = 'right';
              goog.i18n.bidi.LEFT = 'left';
              goog.i18n.bidi.I18N_RIGHT = goog.i18n.bidi.IS_RTL ? goog.i18n.bidi.LEFT : goog.i18n.bidi.RIGHT;
              goog.i18n.bidi.I18N_LEFT = goog.i18n.bidi.IS_RTL ? goog.i18n.bidi.RIGHT : goog.i18n.bidi.LEFT;
              goog.i18n.bidi.toDir = function (a, b) {
                return 'number' == typeof a
                  ? 0 < a
                    ? goog.i18n.bidi.Dir.LTR
                    : 0 > a
                      ? goog.i18n.bidi.Dir.RTL
                      : b
                        ? null
                        : goog.i18n.bidi.Dir.NEUTRAL
                  : null == a
                    ? null
                    : a
                      ? goog.i18n.bidi.Dir.RTL
                      : goog.i18n.bidi.Dir.LTR;
              };
              goog.i18n.bidi.ltrChars_ =
                'A-Za-z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u02b8\u0300-\u0590\u0900-\u1fff\u200e\u2c00-\ud801\ud804-\ud839\ud83c-\udbff\uf900-\ufb1c\ufe00-\ufe6f\ufefd-\uffff';
              goog.i18n.bidi.rtlChars_ = '\u0591-\u06ef\u06fa-\u08ff\u200f\ud802-\ud803\ud83a-\ud83b\ufb1d-\ufdff\ufe70-\ufefc';
              goog.i18n.bidi.htmlSkipReg_ = /<[^>]*>|&[^;]+;/g;
              goog.i18n.bidi.stripHtmlIfNeeded_ = function (a, b) {
                return b ? a.replace(goog.i18n.bidi.htmlSkipReg_, '') : a;
              };
              goog.i18n.bidi.rtlCharReg_ = new RegExp('[' + goog.i18n.bidi.rtlChars_ + ']');
              goog.i18n.bidi.ltrCharReg_ = new RegExp('[' + goog.i18n.bidi.ltrChars_ + ']');
              goog.i18n.bidi.hasAnyRtl = function (a, b) {
                return goog.i18n.bidi.rtlCharReg_.test(goog.i18n.bidi.stripHtmlIfNeeded_(a, b));
              };
              goog.i18n.bidi.hasRtlChar = goog.i18n.bidi.hasAnyRtl;
              goog.i18n.bidi.hasAnyLtr = function (a, b) {
                return goog.i18n.bidi.ltrCharReg_.test(goog.i18n.bidi.stripHtmlIfNeeded_(a, b));
              };
              goog.i18n.bidi.ltrRe_ = new RegExp('^[' + goog.i18n.bidi.ltrChars_ + ']');
              goog.i18n.bidi.rtlRe_ = new RegExp('^[' + goog.i18n.bidi.rtlChars_ + ']');
              goog.i18n.bidi.isRtlChar = function (a) {
                return goog.i18n.bidi.rtlRe_.test(a);
              };
              goog.i18n.bidi.isLtrChar = function (a) {
                return goog.i18n.bidi.ltrRe_.test(a);
              };
              goog.i18n.bidi.isNeutralChar = function (a) {
                return !goog.i18n.bidi.isLtrChar(a) && !goog.i18n.bidi.isRtlChar(a);
              };
              goog.i18n.bidi.ltrDirCheckRe_ = new RegExp('^[^' + goog.i18n.bidi.rtlChars_ + ']*[' + goog.i18n.bidi.ltrChars_ + ']');
              goog.i18n.bidi.rtlDirCheckRe_ = new RegExp('^[^' + goog.i18n.bidi.ltrChars_ + ']*[' + goog.i18n.bidi.rtlChars_ + ']');
              goog.i18n.bidi.startsWithRtl = function (a, b) {
                return goog.i18n.bidi.rtlDirCheckRe_.test(goog.i18n.bidi.stripHtmlIfNeeded_(a, b));
              };
              goog.i18n.bidi.isRtlText = goog.i18n.bidi.startsWithRtl;
              goog.i18n.bidi.startsWithLtr = function (a, b) {
                return goog.i18n.bidi.ltrDirCheckRe_.test(goog.i18n.bidi.stripHtmlIfNeeded_(a, b));
              };
              goog.i18n.bidi.isLtrText = goog.i18n.bidi.startsWithLtr;
              goog.i18n.bidi.isRequiredLtrRe_ = /^http:\/\/.*/;
              goog.i18n.bidi.isNeutralText = function (a, b) {
                a = goog.i18n.bidi.stripHtmlIfNeeded_(a, b);
                return goog.i18n.bidi.isRequiredLtrRe_.test(a) || (!goog.i18n.bidi.hasAnyLtr(a) && !goog.i18n.bidi.hasAnyRtl(a));
              };
              goog.i18n.bidi.ltrExitDirCheckRe_ = new RegExp('[' + goog.i18n.bidi.ltrChars_ + '][^' + goog.i18n.bidi.rtlChars_ + ']*$');
              goog.i18n.bidi.rtlExitDirCheckRe_ = new RegExp('[' + goog.i18n.bidi.rtlChars_ + '][^' + goog.i18n.bidi.ltrChars_ + ']*$');
              goog.i18n.bidi.endsWithLtr = function (a, b) {
                return goog.i18n.bidi.ltrExitDirCheckRe_.test(goog.i18n.bidi.stripHtmlIfNeeded_(a, b));
              };
              goog.i18n.bidi.isLtrExitText = goog.i18n.bidi.endsWithLtr;
              goog.i18n.bidi.endsWithRtl = function (a, b) {
                return goog.i18n.bidi.rtlExitDirCheckRe_.test(goog.i18n.bidi.stripHtmlIfNeeded_(a, b));
              };
              goog.i18n.bidi.isRtlExitText = goog.i18n.bidi.endsWithRtl;
              goog.i18n.bidi.rtlLocalesRe_ =
                /^(ar|ckb|dv|he|iw|fa|nqo|ps|sd|ug|ur|yi|.*[-_](Adlm|Arab|Hebr|Nkoo|Rohg|Thaa))(?!.*[-_](Latn|Cyrl)($|-|_))($|-|_)/i;
              goog.i18n.bidi.isRtlLanguage = function (a) {
                return goog.i18n.bidi.rtlLocalesRe_.test(a);
              };
              goog.i18n.bidi.bracketGuardTextRe_ = /(\(.*?\)+)|(\[.*?\]+)|(\{.*?\}+)|(<.*?>+)/g;
              goog.i18n.bidi.guardBracketInText = function (a, b) {
                b = (void 0 === b ? goog.i18n.bidi.hasAnyRtl(a) : b) ? goog.i18n.bidi.Format.RLM : goog.i18n.bidi.Format.LRM;
                return a.replace(goog.i18n.bidi.bracketGuardTextRe_, b + '$&' + b);
              };
              goog.i18n.bidi.enforceRtlInHtml = function (a) {
                return '<' == a.charAt(0) ? a.replace(/<\w+/, '$& dir=rtl') : '\n<span dir=rtl>' + a + '</span>';
              };
              goog.i18n.bidi.enforceRtlInText = function (a) {
                return goog.i18n.bidi.Format.RLE + a + goog.i18n.bidi.Format.PDF;
              };
              goog.i18n.bidi.enforceLtrInHtml = function (a) {
                return '<' == a.charAt(0) ? a.replace(/<\w+/, '$& dir=ltr') : '\n<span dir=ltr>' + a + '</span>';
              };
              goog.i18n.bidi.enforceLtrInText = function (a) {
                return goog.i18n.bidi.Format.LRE + a + goog.i18n.bidi.Format.PDF;
              };
              goog.i18n.bidi.dimensionsRe_ = /:\s*([.\d][.\w]*)\s+([.\d][.\w]*)\s+([.\d][.\w]*)\s+([.\d][.\w]*)/g;
              goog.i18n.bidi.leftRe_ = /left/gi;
              goog.i18n.bidi.rightRe_ = /right/gi;
              goog.i18n.bidi.tempRe_ = /%%%%/g;
              goog.i18n.bidi.mirrorCSS = function (a) {
                return a
                  .replace(goog.i18n.bidi.dimensionsRe_, ':$1 $4 $3 $2')
                  .replace(goog.i18n.bidi.leftRe_, '%%%%')
                  .replace(goog.i18n.bidi.rightRe_, goog.i18n.bidi.LEFT)
                  .replace(goog.i18n.bidi.tempRe_, goog.i18n.bidi.RIGHT);
              };
              goog.i18n.bidi.doubleQuoteSubstituteRe_ = /([\u0591-\u05f2])"/g;
              goog.i18n.bidi.singleQuoteSubstituteRe_ = /([\u0591-\u05f2])'/g;
              goog.i18n.bidi.normalizeHebrewQuote = function (a) {
                return a.replace(goog.i18n.bidi.doubleQuoteSubstituteRe_, '$1\u05f4').replace(goog.i18n.bidi.singleQuoteSubstituteRe_, '$1\u05f3');
              };
              goog.i18n.bidi.wordSeparatorRe_ = /\s+/;
              goog.i18n.bidi.hasNumeralsRe_ = /[\d\u06f0-\u06f9]/;
              goog.i18n.bidi.rtlDetectionThreshold_ = 0.4;
              goog.i18n.bidi.estimateDirection = function (a, b) {
                var c = 0,
                  d = 0,
                  e = !1;
                a = goog.i18n.bidi.stripHtmlIfNeeded_(a, b).split(goog.i18n.bidi.wordSeparatorRe_);
                for (b = 0; b < a.length; b++) {
                  var f = a[b];
                  goog.i18n.bidi.startsWithRtl(f)
                    ? (c++, d++)
                    : goog.i18n.bidi.isRequiredLtrRe_.test(f)
                      ? (e = !0)
                      : goog.i18n.bidi.hasAnyLtr(f)
                        ? d++
                        : goog.i18n.bidi.hasNumeralsRe_.test(f) && (e = !0);
                }
                return 0 == d
                  ? e
                    ? goog.i18n.bidi.Dir.LTR
                    : goog.i18n.bidi.Dir.NEUTRAL
                  : c / d > goog.i18n.bidi.rtlDetectionThreshold_
                    ? goog.i18n.bidi.Dir.RTL
                    : goog.i18n.bidi.Dir.LTR;
              };
              goog.i18n.bidi.detectRtlDirectionality = function (a, b) {
                return goog.i18n.bidi.estimateDirection(a, b) == goog.i18n.bidi.Dir.RTL;
              };
              goog.i18n.bidi.setElementDirAndAlign = function (a, b) {
                a &&
                  (b = goog.i18n.bidi.toDir(b)) &&
                  ((a.style.textAlign = b == goog.i18n.bidi.Dir.RTL ? goog.i18n.bidi.RIGHT : goog.i18n.bidi.LEFT),
                  (a.dir = b == goog.i18n.bidi.Dir.RTL ? 'rtl' : 'ltr'));
              };
              goog.i18n.bidi.setElementDirByTextDirectionality = function (a, b) {
                switch (goog.i18n.bidi.estimateDirection(b)) {
                  case goog.i18n.bidi.Dir.LTR:
                    'ltr' !== a.dir && (a.dir = 'ltr');
                    break;
                  case goog.i18n.bidi.Dir.RTL:
                    'rtl' !== a.dir && (a.dir = 'rtl');
                    break;
                  default:
                    a.removeAttribute('dir');
                }
              };
              goog.i18n.bidi.DirectionalString = function () {};
              goog.html.TrustedResourceUrl = function (a, b) {
                this.privateDoNotAccessOrElseTrustedResourceUrlWrappedValue_ = b === goog.html.TrustedResourceUrl.CONSTRUCTOR_TOKEN_PRIVATE_ ? a : '';
              };
              goog.html.TrustedResourceUrl.prototype.implementsGoogStringTypedString = !0;
              goog.html.TrustedResourceUrl.prototype.getTypedStringValue = function () {
                return this.privateDoNotAccessOrElseTrustedResourceUrlWrappedValue_.toString();
              };
              goog.html.TrustedResourceUrl.prototype.implementsGoogI18nBidiDirectionalString = !0;
              goog.html.TrustedResourceUrl.prototype.getDirection = function () {
                return goog.i18n.bidi.Dir.LTR;
              };
              goog.html.TrustedResourceUrl.prototype.cloneWithParams = function (a, b) {
                var c = goog.html.TrustedResourceUrl.unwrap(this);
                c = goog.html.TrustedResourceUrl.URL_PARAM_PARSER_.exec(c);
                var d = c[3] || '';
                return goog.html.TrustedResourceUrl.createTrustedResourceUrlSecurityPrivateDoNotAccessOrElse(
                  c[1] + goog.html.TrustedResourceUrl.stringifyParams_('?', c[2] || '', a) + goog.html.TrustedResourceUrl.stringifyParams_('#', d, b)
                );
              };
              goog.DEBUG &&
                (goog.html.TrustedResourceUrl.prototype.toString = function () {
                  return 'TrustedResourceUrl{' + this.privateDoNotAccessOrElseTrustedResourceUrlWrappedValue_ + '}';
                });
              goog.html.TrustedResourceUrl.unwrap = function (a) {
                return goog.html.TrustedResourceUrl.unwrapTrustedScriptURL(a).toString();
              };
              goog.html.TrustedResourceUrl.unwrapTrustedScriptURL = function (a) {
                if (a instanceof goog.html.TrustedResourceUrl && a.constructor === goog.html.TrustedResourceUrl)
                  return a.privateDoNotAccessOrElseTrustedResourceUrlWrappedValue_;
                goog.asserts.fail("expected object of type TrustedResourceUrl, got '" + a + "' of type " + goog.typeOf(a));
                return 'type_error:TrustedResourceUrl';
              };
              goog.html.TrustedResourceUrl.format = function (a, b) {
                var c = goog.string.Const.unwrap(a);
                if (!goog.html.TrustedResourceUrl.BASE_URL_.test(c)) throw Error('Invalid TrustedResourceUrl format: ' + c);
                a = c.replace(goog.html.TrustedResourceUrl.FORMAT_MARKER_, function (d, e) {
                  if (!Object.prototype.hasOwnProperty.call(b, e))
                    throw Error('Found marker, "' + e + '", in format string, "' + c + '", but no valid label mapping found in args: ' + JSON.stringify(b));
                  d = b[e];
                  return d instanceof goog.string.Const ? goog.string.Const.unwrap(d) : encodeURIComponent(String(d));
                });
                return goog.html.TrustedResourceUrl.createTrustedResourceUrlSecurityPrivateDoNotAccessOrElse(a);
              };
              goog.html.TrustedResourceUrl.FORMAT_MARKER_ = /%{(\w+)}/g;
              goog.html.TrustedResourceUrl.BASE_URL_ = /^((https:)?\/\/[0-9a-z.:[\]-]+\/|\/[^/\\]|[^:/\\%]+\/|[^:/\\%]*[?#]|about:blank#)/i;
              goog.html.TrustedResourceUrl.URL_PARAM_PARSER_ = /^([^?#]*)(\?[^#]*)?(#[\s\S]*)?/;
              goog.html.TrustedResourceUrl.formatWithParams = function (a, b, c, d) {
                return goog.html.TrustedResourceUrl.format(a, b).cloneWithParams(c, d);
              };
              goog.html.TrustedResourceUrl.fromConstant = function (a) {
                return goog.html.TrustedResourceUrl.createTrustedResourceUrlSecurityPrivateDoNotAccessOrElse(goog.string.Const.unwrap(a));
              };
              goog.html.TrustedResourceUrl.fromConstants = function (a) {
                for (var b = '', c = 0; c < a.length; c++) b += goog.string.Const.unwrap(a[c]);
                return goog.html.TrustedResourceUrl.createTrustedResourceUrlSecurityPrivateDoNotAccessOrElse(b);
              };
              goog.html.TrustedResourceUrl.fromSafeScript = function (a) {
                a = goog.fs.blob.getBlobWithProperties([module$contents$goog$html$SafeScript_SafeScript.unwrap(a)], 'text/javascript');
                a = goog.fs.url.createObjectUrl(a);
                return goog.html.TrustedResourceUrl.createTrustedResourceUrlSecurityPrivateDoNotAccessOrElse(a);
              };
              goog.html.TrustedResourceUrl.CONSTRUCTOR_TOKEN_PRIVATE_ = {};
              goog.html.TrustedResourceUrl.createTrustedResourceUrlSecurityPrivateDoNotAccessOrElse = function (a) {
                var b = goog.html.trustedtypes.getPolicyPrivateDoNotAccessOrElse();
                a = b ? b.createScriptURL(a) : a;
                return new goog.html.TrustedResourceUrl(a, goog.html.TrustedResourceUrl.CONSTRUCTOR_TOKEN_PRIVATE_);
              };
              goog.html.TrustedResourceUrl.stringifyParams_ = function (a, b, c) {
                if (null == c) return b;
                if ('string' === typeof c) return c ? a + encodeURIComponent(c) : '';
                for (var d in c)
                  if (Object.prototype.hasOwnProperty.call(c, d)) {
                    var e = c[d];
                    e = Array.isArray(e) ? e : [e];
                    for (var f = 0; f < e.length; f++) {
                      var g = e[f];
                      null != g && (b || (b = a), (b += (b.length > a.length ? '&' : '') + encodeURIComponent(d) + '=' + encodeURIComponent(String(g))));
                    }
                  }
                return b;
              };
              goog.string.internal = {};
              goog.string.internal.startsWith = function (a, b) {
                return 0 == a.lastIndexOf(b, 0);
              };
              goog.string.internal.endsWith = function (a, b) {
                var c = a.length - b.length;
                return 0 <= c && a.indexOf(b, c) == c;
              };
              goog.string.internal.caseInsensitiveStartsWith = function (a, b) {
                return 0 == goog.string.internal.caseInsensitiveCompare(b, a.substr(0, b.length));
              };
              goog.string.internal.caseInsensitiveEndsWith = function (a, b) {
                return 0 == goog.string.internal.caseInsensitiveCompare(b, a.substr(a.length - b.length, b.length));
              };
              goog.string.internal.caseInsensitiveEquals = function (a, b) {
                return a.toLowerCase() == b.toLowerCase();
              };
              goog.string.internal.isEmptyOrWhitespace = function (a) {
                return /^[\s\xa0]*$/.test(a);
              };
              goog.string.internal.trim =
                goog.TRUSTED_SITE && String.prototype.trim
                  ? function (a) {
                      return a.trim();
                    }
                  : function (a) {
                      return /^[\s\xa0]*([\s\S]*?)[\s\xa0]*$/.exec(a)[1];
                    };
              goog.string.internal.caseInsensitiveCompare = function (a, b) {
                a = String(a).toLowerCase();
                b = String(b).toLowerCase();
                return a < b ? -1 : a == b ? 0 : 1;
              };
              goog.string.internal.newLineToBr = function (a, b) {
                return a.replace(/(\r\n|\r|\n)/g, b ? '<br />' : '<br>');
              };
              goog.string.internal.htmlEscape = function (a, b) {
                if (b)
                  a = a
                    .replace(goog.string.internal.AMP_RE_, '&amp;')
                    .replace(goog.string.internal.LT_RE_, '&lt;')
                    .replace(goog.string.internal.GT_RE_, '&gt;')
                    .replace(goog.string.internal.QUOT_RE_, '&quot;')
                    .replace(goog.string.internal.SINGLE_QUOTE_RE_, '&#39;')
                    .replace(goog.string.internal.NULL_RE_, '&#0;');
                else {
                  if (!goog.string.internal.ALL_RE_.test(a)) return a;
                  -1 != a.indexOf('&') && (a = a.replace(goog.string.internal.AMP_RE_, '&amp;'));
                  -1 != a.indexOf('<') && (a = a.replace(goog.string.internal.LT_RE_, '&lt;'));
                  -1 != a.indexOf('>') && (a = a.replace(goog.string.internal.GT_RE_, '&gt;'));
                  -1 != a.indexOf('"') && (a = a.replace(goog.string.internal.QUOT_RE_, '&quot;'));
                  -1 != a.indexOf("'") && (a = a.replace(goog.string.internal.SINGLE_QUOTE_RE_, '&#39;'));
                  -1 != a.indexOf('\x00') && (a = a.replace(goog.string.internal.NULL_RE_, '&#0;'));
                }
                return a;
              };
              goog.string.internal.AMP_RE_ = /&/g;
              goog.string.internal.LT_RE_ = /</g;
              goog.string.internal.GT_RE_ = />/g;
              goog.string.internal.QUOT_RE_ = /"/g;
              goog.string.internal.SINGLE_QUOTE_RE_ = /'/g;
              goog.string.internal.NULL_RE_ = /\x00/g;
              goog.string.internal.ALL_RE_ = /[\x00&<>"']/;
              goog.string.internal.whitespaceEscape = function (a, b) {
                return goog.string.internal.newLineToBr(a.replace(/  /g, ' &#160;'), b);
              };
              goog.string.internal.contains = function (a, b) {
                return -1 != a.indexOf(b);
              };
              goog.string.internal.caseInsensitiveContains = function (a, b) {
                return goog.string.internal.contains(a.toLowerCase(), b.toLowerCase());
              };
              goog.string.internal.compareVersions = function (a, b) {
                var c = 0;
                a = goog.string.internal.trim(String(a)).split('.');
                b = goog.string.internal.trim(String(b)).split('.');
                for (var d = Math.max(a.length, b.length), e = 0; 0 == c && e < d; e++) {
                  var f = a[e] || '',
                    g = b[e] || '';
                  do {
                    f = /(\d*)(\D*)(.*)/.exec(f) || ['', '', '', ''];
                    g = /(\d*)(\D*)(.*)/.exec(g) || ['', '', '', ''];
                    if (0 == f[0].length && 0 == g[0].length) break;
                    c = 0 == f[1].length ? 0 : parseInt(f[1], 10);
                    var h = 0 == g[1].length ? 0 : parseInt(g[1], 10);
                    c =
                      goog.string.internal.compareElements_(c, h) ||
                      goog.string.internal.compareElements_(0 == f[2].length, 0 == g[2].length) ||
                      goog.string.internal.compareElements_(f[2], g[2]);
                    f = f[3];
                    g = g[3];
                  } while (0 == c);
                }
                return c;
              };
              goog.string.internal.compareElements_ = function (a, b) {
                return a < b ? -1 : a > b ? 1 : 0;
              };
              goog.html.SafeUrl = function (a, b) {
                this.privateDoNotAccessOrElseSafeUrlWrappedValue_ = b === goog.html.SafeUrl.CONSTRUCTOR_TOKEN_PRIVATE_ ? a : '';
              };
              goog.html.SafeUrl.INNOCUOUS_STRING = 'about:invalid#zClosurez';
              goog.html.SafeUrl.prototype.implementsGoogStringTypedString = !0;
              goog.html.SafeUrl.prototype.getTypedStringValue = function () {
                return this.privateDoNotAccessOrElseSafeUrlWrappedValue_.toString();
              };
              goog.html.SafeUrl.prototype.implementsGoogI18nBidiDirectionalString = !0;
              goog.html.SafeUrl.prototype.getDirection = function () {
                return goog.i18n.bidi.Dir.LTR;
              };
              goog.DEBUG &&
                (goog.html.SafeUrl.prototype.toString = function () {
                  return 'SafeUrl{' + this.privateDoNotAccessOrElseSafeUrlWrappedValue_ + '}';
                });
              goog.html.SafeUrl.unwrap = function (a) {
                if (a instanceof goog.html.SafeUrl && a.constructor === goog.html.SafeUrl) return a.privateDoNotAccessOrElseSafeUrlWrappedValue_;
                goog.asserts.fail("expected object of type SafeUrl, got '" + a + "' of type " + goog.typeOf(a));
                return 'type_error:SafeUrl';
              };
              goog.html.SafeUrl.fromConstant = function (a) {
                return goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(goog.string.Const.unwrap(a));
              };
              goog.html.SAFE_MIME_TYPE_PATTERN_ =
                /^(?:audio\/(?:3gpp2|3gpp|aac|L16|midi|mp3|mp4|mpeg|oga|ogg|opus|x-m4a|x-matroska|x-wav|wav|webm)|font\/\w+|image\/(?:bmp|gif|jpeg|jpg|png|tiff|webp|x-icon)|video\/(?:mpeg|mp4|ogg|webm|quicktime|x-matroska))(?:;\w+=(?:\w+|"[\w;,= ]+"))*$/i;
              goog.html.SafeUrl.isSafeMimeType = function (a) {
                return goog.html.SAFE_MIME_TYPE_PATTERN_.test(a);
              };
              goog.html.SafeUrl.fromBlob = function (a) {
                a = goog.html.SafeUrl.isSafeMimeType(a.type) ? goog.fs.url.createObjectUrl(a) : goog.html.SafeUrl.INNOCUOUS_STRING;
                return goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(a);
              };
              goog.html.SafeUrl.revokeObjectUrl = function (a) {
                a = a.getTypedStringValue();
                a !== goog.html.SafeUrl.INNOCUOUS_STRING && goog.fs.url.revokeObjectUrl(a);
              };
              goog.html.SafeUrl.fromMediaSource = function (a) {
                goog.asserts.assert('MediaSource' in goog.global, 'No support for MediaSource');
                a = a instanceof MediaSource ? goog.fs.url.createObjectUrl(a) : goog.html.SafeUrl.INNOCUOUS_STRING;
                return goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(a);
              };
              goog.html.DATA_URL_PATTERN_ = /^data:(.*);base64,[a-z0-9+\/]+=*$/i;
              goog.html.SafeUrl.tryFromDataUrl = function (a) {
                a = String(a);
                a = a.replace(/(%0A|%0D)/g, '');
                var b = a.match(goog.html.DATA_URL_PATTERN_);
                return b && goog.html.SafeUrl.isSafeMimeType(b[1]) ? goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(a) : null;
              };
              goog.html.SafeUrl.fromDataUrl = function (a) {
                return goog.html.SafeUrl.tryFromDataUrl(a) || goog.html.SafeUrl.INNOCUOUS_URL;
              };
              goog.html.SafeUrl.fromTelUrl = function (a) {
                goog.string.internal.caseInsensitiveStartsWith(a, 'tel:') || (a = goog.html.SafeUrl.INNOCUOUS_STRING);
                return goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(a);
              };
              goog.html.SIP_URL_PATTERN_ = /^sip[s]?:[+a-z0-9_.!$%&'*\/=^`{|}~-]+@([a-z0-9-]+\.)+[a-z0-9]{2,63}$/i;
              goog.html.SafeUrl.fromSipUrl = function (a) {
                goog.html.SIP_URL_PATTERN_.test(decodeURIComponent(a)) || (a = goog.html.SafeUrl.INNOCUOUS_STRING);
                return goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(a);
              };
              goog.html.SafeUrl.fromFacebookMessengerUrl = function (a) {
                goog.string.internal.caseInsensitiveStartsWith(a, 'fb-messenger://share') || (a = goog.html.SafeUrl.INNOCUOUS_STRING);
                return goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(a);
              };
              goog.html.SafeUrl.fromWhatsAppUrl = function (a) {
                goog.string.internal.caseInsensitiveStartsWith(a, 'whatsapp://send') || (a = goog.html.SafeUrl.INNOCUOUS_STRING);
                return goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(a);
              };
              goog.html.SafeUrl.fromSmsUrl = function (a) {
                (goog.string.internal.caseInsensitiveStartsWith(a, 'sms:') && goog.html.SafeUrl.isSmsUrlBodyValid_(a)) ||
                  (a = goog.html.SafeUrl.INNOCUOUS_STRING);
                return goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(a);
              };
              goog.html.SafeUrl.isSmsUrlBodyValid_ = function (a) {
                var b = a.indexOf('#');
                0 < b && (a = a.substring(0, b));
                b = a.match(/[?&]body=/gi);
                if (!b) return !0;
                if (1 < b.length) return !1;
                a = a.match(/[?&]body=([^&]*)/)[1];
                if (!a) return !0;
                try {
                  decodeURIComponent(a);
                } catch (c) {
                  return !1;
                }
                return /^(?:[a-z0-9\-_.~]|%[0-9a-f]{2})+$/i.test(a);
              };
              goog.html.SafeUrl.fromSshUrl = function (a) {
                goog.string.internal.caseInsensitiveStartsWith(a, 'ssh://') || (a = goog.html.SafeUrl.INNOCUOUS_STRING);
                return goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(a);
              };
              goog.html.SafeUrl.sanitizeChromeExtensionUrl = function (a, b) {
                return goog.html.SafeUrl.sanitizeExtensionUrl_(/^chrome-extension:\/\/([^\/]+)\//, a, b);
              };
              goog.html.SafeUrl.sanitizeFirefoxExtensionUrl = function (a, b) {
                return goog.html.SafeUrl.sanitizeExtensionUrl_(/^moz-extension:\/\/([^\/]+)\//, a, b);
              };
              goog.html.SafeUrl.sanitizeEdgeExtensionUrl = function (a, b) {
                return goog.html.SafeUrl.sanitizeExtensionUrl_(/^ms-browser-extension:\/\/([^\/]+)\//, a, b);
              };
              goog.html.SafeUrl.sanitizeExtensionUrl_ = function (a, b, c) {
                (a = a.exec(b))
                  ? ((a = a[1]),
                    -1 ==
                      (c instanceof goog.string.Const
                        ? [goog.string.Const.unwrap(c)]
                        : c.map(function (d) {
                            return goog.string.Const.unwrap(d);
                          })
                      ).indexOf(a) && (b = goog.html.SafeUrl.INNOCUOUS_STRING))
                  : (b = goog.html.SafeUrl.INNOCUOUS_STRING);
                return goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(b);
              };
              goog.html.SafeUrl.fromTrustedResourceUrl = function (a) {
                return goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(goog.html.TrustedResourceUrl.unwrap(a));
              };
              goog.html.SAFE_URL_PATTERN_ = /^(?:(?:https?|mailto|ftp):|[^:/?#]*(?:[/?#]|$))/i;
              goog.html.SafeUrl.SAFE_URL_PATTERN = goog.html.SAFE_URL_PATTERN_;
              goog.html.SafeUrl.trySanitize = function (a) {
                if (a instanceof goog.html.SafeUrl) return a;
                a = 'object' == typeof a && a.implementsGoogStringTypedString ? a.getTypedStringValue() : String(a);
                return goog.html.SAFE_URL_PATTERN_.test(a)
                  ? goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(a)
                  : goog.html.SafeUrl.tryFromDataUrl(a);
              };
              goog.html.SafeUrl.sanitize = function (a) {
                return goog.html.SafeUrl.trySanitize(a) || goog.html.SafeUrl.INNOCUOUS_URL;
              };
              goog.html.SafeUrl.sanitizeAssertUnchanged = function (a, b) {
                if (a instanceof goog.html.SafeUrl) return a;
                a = 'object' == typeof a && a.implementsGoogStringTypedString ? a.getTypedStringValue() : String(a);
                if (b && /^data:/i.test(a) && ((b = goog.html.SafeUrl.fromDataUrl(a)), b.getTypedStringValue() == a)) return b;
                goog.asserts.assert(goog.html.SAFE_URL_PATTERN_.test(a), '%s does not match the safe URL pattern', a) ||
                  (a = goog.html.SafeUrl.INNOCUOUS_STRING);
                return goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(a);
              };
              goog.html.SafeUrl.CONSTRUCTOR_TOKEN_PRIVATE_ = {};
              goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse = function (a) {
                return new goog.html.SafeUrl(a, goog.html.SafeUrl.CONSTRUCTOR_TOKEN_PRIVATE_);
              };
              goog.html.SafeUrl.INNOCUOUS_URL = goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(goog.html.SafeUrl.INNOCUOUS_STRING);
              goog.html.SafeUrl.ABOUT_BLANK = goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse('about:blank');
              goog.html.SafeStyle = function (a, b) {
                this.privateDoNotAccessOrElseSafeStyleWrappedValue_ = b === goog.html.SafeStyle.CONSTRUCTOR_TOKEN_PRIVATE_ ? a : '';
              };
              goog.html.SafeStyle.prototype.implementsGoogStringTypedString = !0;
              goog.html.SafeStyle.fromConstant = function (a) {
                a = goog.string.Const.unwrap(a);
                if (0 === a.length) return goog.html.SafeStyle.EMPTY;
                goog.asserts.assert(goog.string.internal.endsWith(a, ';'), "Last character of style string is not ';': " + a);
                goog.asserts.assert(
                  goog.string.internal.contains(a, ':'),
                  'Style string must contain at least one \':\', to specify a "name: value" pair: ' + a
                );
                return goog.html.SafeStyle.createSafeStyleSecurityPrivateDoNotAccessOrElse(a);
              };
              goog.html.SafeStyle.prototype.getTypedStringValue = function () {
                return this.privateDoNotAccessOrElseSafeStyleWrappedValue_;
              };
              goog.DEBUG &&
                (goog.html.SafeStyle.prototype.toString = function () {
                  return 'SafeStyle{' + this.privateDoNotAccessOrElseSafeStyleWrappedValue_ + '}';
                });
              goog.html.SafeStyle.unwrap = function (a) {
                if (a instanceof goog.html.SafeStyle && a.constructor === goog.html.SafeStyle) return a.privateDoNotAccessOrElseSafeStyleWrappedValue_;
                goog.asserts.fail("expected object of type SafeStyle, got '" + a + "' of type " + goog.typeOf(a));
                return 'type_error:SafeStyle';
              };
              goog.html.SafeStyle.CONSTRUCTOR_TOKEN_PRIVATE_ = {};
              goog.html.SafeStyle.createSafeStyleSecurityPrivateDoNotAccessOrElse = function (a) {
                return new goog.html.SafeStyle(a, goog.html.SafeStyle.CONSTRUCTOR_TOKEN_PRIVATE_);
              };
              goog.html.SafeStyle.EMPTY = goog.html.SafeStyle.createSafeStyleSecurityPrivateDoNotAccessOrElse('');
              goog.html.SafeStyle.INNOCUOUS_STRING = 'zClosurez';
              goog.html.SafeStyle.create = function (a) {
                var b = '',
                  c;
                for (c in a)
                  if (Object.prototype.hasOwnProperty.call(a, c)) {
                    if (!/^[-_a-zA-Z0-9]+$/.test(c)) throw Error('Name allows only [-_a-zA-Z0-9], got: ' + c);
                    var d = a[c];
                    null != d &&
                      ((d = Array.isArray(d)
                        ? module$contents$goog$array_map(d, goog.html.SafeStyle.sanitizePropertyValue_).join(' ')
                        : goog.html.SafeStyle.sanitizePropertyValue_(d)),
                      (b += c + ':' + d + ';'));
                  }
                return b ? goog.html.SafeStyle.createSafeStyleSecurityPrivateDoNotAccessOrElse(b) : goog.html.SafeStyle.EMPTY;
              };
              goog.html.SafeStyle.sanitizePropertyValue_ = function (a) {
                if (a instanceof goog.html.SafeUrl) return 'url("' + goog.html.SafeUrl.unwrap(a).replace(/</g, '%3c').replace(/[\\"]/g, '\\$&') + '")';
                a = a instanceof goog.string.Const ? goog.string.Const.unwrap(a) : goog.html.SafeStyle.sanitizePropertyValueString_(String(a));
                if (/[{;}]/.test(a)) throw new goog.asserts.AssertionError('Value does not allow [{;}], got: %s.', [a]);
                return a;
              };
              goog.html.SafeStyle.sanitizePropertyValueString_ = function (a) {
                var b = a
                  .replace(goog.html.SafeStyle.FUNCTIONS_RE_, '$1')
                  .replace(goog.html.SafeStyle.FUNCTIONS_RE_, '$1')
                  .replace(goog.html.SafeStyle.URL_RE_, 'url');
                if (goog.html.SafeStyle.VALUE_RE_.test(b)) {
                  if (goog.html.SafeStyle.COMMENT_RE_.test(a))
                    return (goog.asserts.fail('String value disallows comments, got: ' + a), goog.html.SafeStyle.INNOCUOUS_STRING);
                  if (!goog.html.SafeStyle.hasBalancedQuotes_(a))
                    return (goog.asserts.fail('String value requires balanced quotes, got: ' + a), goog.html.SafeStyle.INNOCUOUS_STRING);
                  if (!goog.html.SafeStyle.hasBalancedSquareBrackets_(a))
                    return (
                      goog.asserts.fail('String value requires balanced square brackets and one identifier per pair of brackets, got: ' + a),
                      goog.html.SafeStyle.INNOCUOUS_STRING
                    );
                } else
                  return (
                    goog.asserts.fail('String value allows only ' + goog.html.SafeStyle.VALUE_ALLOWED_CHARS_ + ' and simple functions, got: ' + a),
                    goog.html.SafeStyle.INNOCUOUS_STRING
                  );
                return goog.html.SafeStyle.sanitizeUrl_(a);
              };
              goog.html.SafeStyle.hasBalancedQuotes_ = function (a) {
                for (var b = !0, c = !0, d = 0; d < a.length; d++) {
                  var e = a.charAt(d);
                  "'" == e && c ? (b = !b) : '"' == e && b && (c = !c);
                }
                return b && c;
              };
              goog.html.SafeStyle.hasBalancedSquareBrackets_ = function (a) {
                for (var b = !0, c = /^[-_a-zA-Z0-9]$/, d = 0; d < a.length; d++) {
                  var e = a.charAt(d);
                  if (']' == e) {
                    if (b) return !1;
                    b = !0;
                  } else if ('[' == e) {
                    if (!b) return !1;
                    b = !1;
                  } else if (!b && !c.test(e)) return !1;
                }
                return b;
              };
              goog.html.SafeStyle.VALUE_ALLOWED_CHARS_ = '[-,."\'%_!# a-zA-Z0-9\\[\\]]';
              goog.html.SafeStyle.VALUE_RE_ = new RegExp('^' + goog.html.SafeStyle.VALUE_ALLOWED_CHARS_ + '+$');
              goog.html.SafeStyle.URL_RE_ = /\b(url\([ \t\n]*)('[ -&(-\[\]-~]*'|"[ !#-\[\]-~]*"|[!#-&*-\[\]-~]*)([ \t\n]*\))/g;
              goog.html.SafeStyle.ALLOWED_FUNCTIONS_ =
                'calc cubic-bezier fit-content hsl hsla linear-gradient matrix minmax repeat rgb rgba (rotate|scale|translate)(X|Y|Z|3d)?'.split(' ');
              goog.html.SafeStyle.FUNCTIONS_RE_ = new RegExp('\\b(' + goog.html.SafeStyle.ALLOWED_FUNCTIONS_.join('|') + ')\\([-+*/0-9a-z.%\\[\\], ]+\\)', 'g');
              goog.html.SafeStyle.COMMENT_RE_ = /\/\*/;
              goog.html.SafeStyle.sanitizeUrl_ = function (a) {
                return a.replace(goog.html.SafeStyle.URL_RE_, function (b, c, d, e) {
                  var f = '';
                  d = d.replace(/^(['"])(.*)\1$/, function (g, h, k) {
                    f = h;
                    return k;
                  });
                  b = goog.html.SafeUrl.sanitize(d).getTypedStringValue();
                  return c + f + b + f + e;
                });
              };
              goog.html.SafeStyle.concat = function (a) {
                var b = '',
                  c = function (d) {
                    Array.isArray(d) ? module$contents$goog$array_forEach(d, c) : (b += goog.html.SafeStyle.unwrap(d));
                  };
                module$contents$goog$array_forEach(arguments, c);
                return b ? goog.html.SafeStyle.createSafeStyleSecurityPrivateDoNotAccessOrElse(b) : goog.html.SafeStyle.EMPTY;
              };
              var module$contents$goog$html$SafeStyleSheet_CONSTRUCTOR_TOKEN_PRIVATE = {},
                module$contents$goog$html$SafeStyleSheet_SafeStyleSheet = function (a, b) {
                  this.privateDoNotAccessOrElseSafeStyleSheetWrappedValue_ = b === module$contents$goog$html$SafeStyleSheet_CONSTRUCTOR_TOKEN_PRIVATE ? a : '';
                  this.implementsGoogStringTypedString = !0;
                };
              module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.createRule = function (a, b) {
                if ((0, goog.string.internal.contains)(a, '<')) throw Error("Selector does not allow '<', got: " + a);
                var c = a.replace(/('|")((?!\1)[^\r\n\f\\]|\\[\s\S])*\1/g, '');
                if (!/^[-_a-zA-Z0-9#.:* ,>+~[\]()=^$|]+$/.test(c)) throw Error('Selector allows only [-_a-zA-Z0-9#.:* ,>+~[\\]()=^$|] and strings, got: ' + a);
                if (!module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.hasBalancedBrackets_(c))
                  throw Error('() and [] in selector must be balanced, got: ' + a);
                b instanceof goog.html.SafeStyle || (b = goog.html.SafeStyle.create(b));
                a = a + '{' + goog.html.SafeStyle.unwrap(b).replace(/</g, '\\3C ') + '}';
                return module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.createSafeStyleSheetSecurityPrivateDoNotAccessOrElse(a);
              };
              module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.hasBalancedBrackets_ = function (a) {
                for (var b = { '(': ')', '[': ']' }, c = [], d = 0; d < a.length; d++) {
                  var e = a[d];
                  if (b[e]) c.push(b[e]);
                  else if (goog.object.contains(b, e) && c.pop() != e) return !1;
                }
                return 0 == c.length;
              };
              module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.concat = function (a) {
                var b = '',
                  c = function (d) {
                    Array.isArray(d) ? module$contents$goog$array_forEach(d, c) : (b += module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.unwrap(d));
                  };
                module$contents$goog$array_forEach(arguments, c);
                return module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.createSafeStyleSheetSecurityPrivateDoNotAccessOrElse(b);
              };
              module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.fromConstant = function (a) {
                a = goog.string.Const.unwrap(a);
                if (0 === a.length) return module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.EMPTY;
                (0, goog.asserts.assert)(!(0, goog.string.internal.contains)(a, '<'), "Forbidden '<' character in style sheet string: " + a);
                return module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.createSafeStyleSheetSecurityPrivateDoNotAccessOrElse(a);
              };
              module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.prototype.getTypedStringValue = function () {
                return this.privateDoNotAccessOrElseSafeStyleSheetWrappedValue_;
              };
              module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.unwrap = function (a) {
                if (
                  a instanceof module$contents$goog$html$SafeStyleSheet_SafeStyleSheet &&
                  a.constructor === module$contents$goog$html$SafeStyleSheet_SafeStyleSheet
                )
                  return a.privateDoNotAccessOrElseSafeStyleSheetWrappedValue_;
                (0, goog.asserts.fail)("expected object of type SafeStyleSheet, got '" + a + "' of type " + goog.typeOf(a));
                return 'type_error:SafeStyleSheet';
              };
              module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.createSafeStyleSheetSecurityPrivateDoNotAccessOrElse = function (a) {
                return new module$contents$goog$html$SafeStyleSheet_SafeStyleSheet(a, module$contents$goog$html$SafeStyleSheet_CONSTRUCTOR_TOKEN_PRIVATE);
              };
              goog.DEBUG &&
                (module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.prototype.toString = function () {
                  return 'SafeStyleSheet{' + this.privateDoNotAccessOrElseSafeStyleSheetWrappedValue_ + '}';
                });
              module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.EMPTY =
                module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.createSafeStyleSheetSecurityPrivateDoNotAccessOrElse('');
              goog.html.SafeStyleSheet = module$contents$goog$html$SafeStyleSheet_SafeStyleSheet;
              goog.labs = {};
              goog.labs.userAgent = {};
              goog.labs.userAgent.util = {};
              goog.labs.userAgent.util.getNativeUserAgentString_ = function () {
                var a = goog.labs.userAgent.util.getNavigator_();
                return a && (a = a.userAgent) ? a : '';
              };
              goog.labs.userAgent.util.getNavigator_ = function () {
                return goog.global.navigator;
              };
              goog.labs.userAgent.util.userAgent_ = goog.labs.userAgent.util.getNativeUserAgentString_();
              goog.labs.userAgent.util.setUserAgent = function (a) {
                goog.labs.userAgent.util.userAgent_ = a || goog.labs.userAgent.util.getNativeUserAgentString_();
              };
              goog.labs.userAgent.util.getUserAgent = function () {
                return goog.labs.userAgent.util.userAgent_;
              };
              goog.labs.userAgent.util.matchUserAgent = function (a) {
                var b = goog.labs.userAgent.util.getUserAgent();
                return goog.string.internal.contains(b, a);
              };
              goog.labs.userAgent.util.matchUserAgentIgnoreCase = function (a) {
                var b = goog.labs.userAgent.util.getUserAgent();
                return goog.string.internal.caseInsensitiveContains(b, a);
              };
              goog.labs.userAgent.util.extractVersionTuples = function (a) {
                for (var b = /(\w[\w ]+)\/([^\s]+)\s*(?:\((.*?)\))?/g, c = [], d; (d = b.exec(a)); ) c.push([d[1], d[2], d[3] || void 0]);
                return c;
              };
              goog.labs.userAgent.browser = {};
              goog.labs.userAgent.browser.matchOpera_ = function () {
                return goog.labs.userAgent.util.matchUserAgent('Opera');
              };
              goog.labs.userAgent.browser.matchIE_ = function () {
                return goog.labs.userAgent.util.matchUserAgent('Trident') || goog.labs.userAgent.util.matchUserAgent('MSIE');
              };
              goog.labs.userAgent.browser.matchEdgeHtml_ = function () {
                return goog.labs.userAgent.util.matchUserAgent('Edge');
              };
              goog.labs.userAgent.browser.matchEdgeChromium_ = function () {
                return goog.labs.userAgent.util.matchUserAgent('Edg/');
              };
              goog.labs.userAgent.browser.matchOperaChromium_ = function () {
                return goog.labs.userAgent.util.matchUserAgent('OPR');
              };
              goog.labs.userAgent.browser.matchFirefox_ = function () {
                return goog.labs.userAgent.util.matchUserAgent('Firefox') || goog.labs.userAgent.util.matchUserAgent('FxiOS');
              };
              goog.labs.userAgent.browser.matchSafari_ = function () {
                return (
                  goog.labs.userAgent.util.matchUserAgent('Safari') &&
                  !(
                    goog.labs.userAgent.browser.matchChrome_() ||
                    goog.labs.userAgent.browser.matchCoast_() ||
                    goog.labs.userAgent.browser.matchOpera_() ||
                    goog.labs.userAgent.browser.matchEdgeHtml_() ||
                    goog.labs.userAgent.browser.matchEdgeChromium_() ||
                    goog.labs.userAgent.browser.matchOperaChromium_() ||
                    goog.labs.userAgent.browser.matchFirefox_() ||
                    goog.labs.userAgent.browser.isSilk() ||
                    goog.labs.userAgent.util.matchUserAgent('Android')
                  )
                );
              };
              goog.labs.userAgent.browser.matchCoast_ = function () {
                return goog.labs.userAgent.util.matchUserAgent('Coast');
              };
              goog.labs.userAgent.browser.matchIosWebview_ = function () {
                return (
                  (goog.labs.userAgent.util.matchUserAgent('iPad') || goog.labs.userAgent.util.matchUserAgent('iPhone')) &&
                  !goog.labs.userAgent.browser.matchSafari_() &&
                  !goog.labs.userAgent.browser.matchChrome_() &&
                  !goog.labs.userAgent.browser.matchCoast_() &&
                  !goog.labs.userAgent.browser.matchFirefox_() &&
                  goog.labs.userAgent.util.matchUserAgent('AppleWebKit')
                );
              };
              goog.labs.userAgent.browser.matchChrome_ = function () {
                return (
                  (goog.labs.userAgent.util.matchUserAgent('Chrome') || goog.labs.userAgent.util.matchUserAgent('CriOS')) &&
                  !goog.labs.userAgent.browser.matchEdgeHtml_()
                );
              };
              goog.labs.userAgent.browser.matchAndroidBrowser_ = function () {
                return (
                  goog.labs.userAgent.util.matchUserAgent('Android') &&
                  !(
                    goog.labs.userAgent.browser.isChrome() ||
                    goog.labs.userAgent.browser.isFirefox() ||
                    goog.labs.userAgent.browser.isOpera() ||
                    goog.labs.userAgent.browser.isSilk()
                  )
                );
              };
              goog.labs.userAgent.browser.isOpera = goog.labs.userAgent.browser.matchOpera_;
              goog.labs.userAgent.browser.isIE = goog.labs.userAgent.browser.matchIE_;
              goog.labs.userAgent.browser.isEdge = goog.labs.userAgent.browser.matchEdgeHtml_;
              goog.labs.userAgent.browser.isEdgeChromium = goog.labs.userAgent.browser.matchEdgeChromium_;
              goog.labs.userAgent.browser.isOperaChromium = goog.labs.userAgent.browser.matchOperaChromium_;
              goog.labs.userAgent.browser.isFirefox = goog.labs.userAgent.browser.matchFirefox_;
              goog.labs.userAgent.browser.isSafari = goog.labs.userAgent.browser.matchSafari_;
              goog.labs.userAgent.browser.isCoast = goog.labs.userAgent.browser.matchCoast_;
              goog.labs.userAgent.browser.isIosWebview = goog.labs.userAgent.browser.matchIosWebview_;
              goog.labs.userAgent.browser.isChrome = goog.labs.userAgent.browser.matchChrome_;
              goog.labs.userAgent.browser.isAndroidBrowser = goog.labs.userAgent.browser.matchAndroidBrowser_;
              goog.labs.userAgent.browser.isSilk = function () {
                return goog.labs.userAgent.util.matchUserAgent('Silk');
              };
              goog.labs.userAgent.browser.getVersion = function () {
                function a(e) {
                  e = module$contents$goog$array_find(e, d);
                  return c[e] || '';
                }
                var b = goog.labs.userAgent.util.getUserAgent();
                if (goog.labs.userAgent.browser.isIE()) return goog.labs.userAgent.browser.getIEVersion_(b);
                b = goog.labs.userAgent.util.extractVersionTuples(b);
                var c = {};
                module$contents$goog$array_forEach(b, function (e) {
                  c[e[0]] = e[1];
                });
                var d = goog.partial(goog.object.containsKey, c);
                return goog.labs.userAgent.browser.isOpera()
                  ? a(['Version', 'Opera'])
                  : goog.labs.userAgent.browser.isEdge()
                    ? a(['Edge'])
                    : goog.labs.userAgent.browser.isEdgeChromium()
                      ? a(['Edg'])
                      : goog.labs.userAgent.browser.isChrome()
                        ? a(['Chrome', 'CriOS', 'HeadlessChrome'])
                        : ((b = b[2]) && b[1]) || '';
              };
              goog.labs.userAgent.browser.isVersionOrHigher = function (a) {
                return 0 <= goog.string.internal.compareVersions(goog.labs.userAgent.browser.getVersion(), a);
              };
              goog.labs.userAgent.browser.getIEVersion_ = function (a) {
                var b = /rv: *([\d\.]*)/.exec(a);
                if (b && b[1]) return b[1];
                b = '';
                var c = /MSIE +([\d\.]+)/.exec(a);
                if (c && c[1])
                  if (((a = /Trident\/(\d.\d)/.exec(a)), '7.0' == c[1]))
                    if (a && a[1])
                      switch (a[1]) {
                        case '4.0':
                          b = '8.0';
                          break;
                        case '5.0':
                          b = '9.0';
                          break;
                        case '6.0':
                          b = '10.0';
                          break;
                        case '7.0':
                          b = '11.0';
                      }
                    else b = '7.0';
                  else b = c[1];
                return b;
              };
              goog.html.SafeHtml = function (a, b, c) {
                this.privateDoNotAccessOrElseSafeHtmlWrappedValue_ = c === goog.html.SafeHtml.CONSTRUCTOR_TOKEN_PRIVATE_ ? a : '';
                this.dir_ = b;
              };
              goog.html.SafeHtml.ENABLE_ERROR_MESSAGES = goog.DEBUG;
              goog.html.SafeHtml.SUPPORT_STYLE_ATTRIBUTE = !0;
              goog.html.SafeHtml.prototype.implementsGoogI18nBidiDirectionalString = !0;
              goog.html.SafeHtml.prototype.getDirection = function () {
                return this.dir_;
              };
              goog.html.SafeHtml.prototype.implementsGoogStringTypedString = !0;
              goog.html.SafeHtml.prototype.getTypedStringValue = function () {
                return this.privateDoNotAccessOrElseSafeHtmlWrappedValue_.toString();
              };
              goog.DEBUG &&
                (goog.html.SafeHtml.prototype.toString = function () {
                  return 'SafeHtml{' + this.privateDoNotAccessOrElseSafeHtmlWrappedValue_ + '}';
                });
              goog.html.SafeHtml.unwrap = function (a) {
                return goog.html.SafeHtml.unwrapTrustedHTML(a).toString();
              };
              goog.html.SafeHtml.unwrapTrustedHTML = function (a) {
                if (a instanceof goog.html.SafeHtml && a.constructor === goog.html.SafeHtml) return a.privateDoNotAccessOrElseSafeHtmlWrappedValue_;
                goog.asserts.fail("expected object of type SafeHtml, got '" + a + "' of type " + goog.typeOf(a));
                return 'type_error:SafeHtml';
              };
              goog.html.SafeHtml.htmlEscape = function (a) {
                if (a instanceof goog.html.SafeHtml) return a;
                var b = 'object' == typeof a,
                  c = null;
                b && a.implementsGoogI18nBidiDirectionalString && (c = a.getDirection());
                a = b && a.implementsGoogStringTypedString ? a.getTypedStringValue() : String(a);
                return goog.html.SafeHtml.createSafeHtmlSecurityPrivateDoNotAccessOrElse(goog.string.internal.htmlEscape(a), c);
              };
              goog.html.SafeHtml.htmlEscapePreservingNewlines = function (a) {
                if (a instanceof goog.html.SafeHtml) return a;
                a = goog.html.SafeHtml.htmlEscape(a);
                return goog.html.SafeHtml.createSafeHtmlSecurityPrivateDoNotAccessOrElse(
                  goog.string.internal.newLineToBr(goog.html.SafeHtml.unwrap(a)),
                  a.getDirection()
                );
              };
              goog.html.SafeHtml.htmlEscapePreservingNewlinesAndSpaces = function (a) {
                if (a instanceof goog.html.SafeHtml) return a;
                a = goog.html.SafeHtml.htmlEscape(a);
                return goog.html.SafeHtml.createSafeHtmlSecurityPrivateDoNotAccessOrElse(
                  goog.string.internal.whitespaceEscape(goog.html.SafeHtml.unwrap(a)),
                  a.getDirection()
                );
              };
              goog.html.SafeHtml.from = goog.html.SafeHtml.htmlEscape;
              goog.html.SafeHtml.comment = function (a) {
                return goog.html.SafeHtml.createSafeHtmlSecurityPrivateDoNotAccessOrElse('\x3c!--' + goog.string.internal.htmlEscape(a) + '--\x3e', null);
              };
              goog.html.SafeHtml.VALID_NAMES_IN_TAG_ = /^[a-zA-Z0-9-]+$/;
              goog.html.SafeHtml.URL_ATTRIBUTES_ = { action: !0, cite: !0, data: !0, formaction: !0, href: !0, manifest: !0, poster: !0, src: !0 };
              goog.html.SafeHtml.NOT_ALLOWED_TAG_NAMES_ = goog.object.createSet(
                goog.dom.TagName.APPLET,
                goog.dom.TagName.BASE,
                goog.dom.TagName.EMBED,
                goog.dom.TagName.IFRAME,
                goog.dom.TagName.LINK,
                goog.dom.TagName.MATH,
                goog.dom.TagName.META,
                goog.dom.TagName.OBJECT,
                goog.dom.TagName.SCRIPT,
                goog.dom.TagName.STYLE,
                goog.dom.TagName.SVG,
                goog.dom.TagName.TEMPLATE
              );
              goog.html.SafeHtml.create = function (a, b, c) {
                goog.html.SafeHtml.verifyTagName(String(a));
                return goog.html.SafeHtml.createSafeHtmlTagSecurityPrivateDoNotAccessOrElse(String(a), b, c);
              };
              goog.html.SafeHtml.verifyTagName = function (a) {
                if (!goog.html.SafeHtml.VALID_NAMES_IN_TAG_.test(a))
                  throw Error(goog.html.SafeHtml.ENABLE_ERROR_MESSAGES ? 'Invalid tag name <' + a + '>.' : '');
                if (a.toUpperCase() in goog.html.SafeHtml.NOT_ALLOWED_TAG_NAMES_)
                  throw Error(goog.html.SafeHtml.ENABLE_ERROR_MESSAGES ? 'Tag name <' + a + '> is not allowed for SafeHtml.' : '');
              };
              goog.html.SafeHtml.createIframe = function (a, b, c, d) {
                a && goog.html.TrustedResourceUrl.unwrap(a);
                var e = {};
                e.src = a || null;
                e.srcdoc = b && goog.html.SafeHtml.unwrap(b);
                a = goog.html.SafeHtml.combineAttributes(e, { sandbox: '' }, c);
                return goog.html.SafeHtml.createSafeHtmlTagSecurityPrivateDoNotAccessOrElse('iframe', a, d);
              };
              goog.html.SafeHtml.createSandboxIframe = function (a, b, c, d) {
                if (!goog.html.SafeHtml.canUseSandboxIframe())
                  throw Error(goog.html.SafeHtml.ENABLE_ERROR_MESSAGES ? 'The browser does not support sandboxed iframes.' : '');
                var e = {};
                e.src = a ? goog.html.SafeUrl.unwrap(goog.html.SafeUrl.sanitize(a)) : null;
                e.srcdoc = b || null;
                e.sandbox = '';
                a = goog.html.SafeHtml.combineAttributes(e, {}, c);
                return goog.html.SafeHtml.createSafeHtmlTagSecurityPrivateDoNotAccessOrElse('iframe', a, d);
              };
              goog.html.SafeHtml.canUseSandboxIframe = function () {
                return goog.global.HTMLIFrameElement && 'sandbox' in goog.global.HTMLIFrameElement.prototype;
              };
              goog.html.SafeHtml.createScriptSrc = function (a, b) {
                goog.html.TrustedResourceUrl.unwrap(a);
                a = goog.html.SafeHtml.combineAttributes({ src: a }, {}, b);
                return goog.html.SafeHtml.createSafeHtmlTagSecurityPrivateDoNotAccessOrElse('script', a);
              };
              goog.html.SafeHtml.createScript = function (a, b) {
                for (var c in b)
                  if (Object.prototype.hasOwnProperty.call(b, c)) {
                    var d = c.toLowerCase();
                    if ('language' == d || 'src' == d || 'text' == d || 'type' == d)
                      throw Error(goog.html.SafeHtml.ENABLE_ERROR_MESSAGES ? 'Cannot set "' + d + '" attribute' : '');
                  }
                c = '';
                a = module$contents$goog$array_concat(a);
                for (d = 0; d < a.length; d++) c += module$contents$goog$html$SafeScript_SafeScript.unwrap(a[d]);
                a = goog.html.SafeHtml.createSafeHtmlSecurityPrivateDoNotAccessOrElse(c, goog.i18n.bidi.Dir.NEUTRAL);
                return goog.html.SafeHtml.createSafeHtmlTagSecurityPrivateDoNotAccessOrElse('script', b, a);
              };
              goog.html.SafeHtml.createStyle = function (a, b) {
                b = goog.html.SafeHtml.combineAttributes({ type: 'text/css' }, {}, b);
                var c = '';
                a = module$contents$goog$array_concat(a);
                for (var d = 0; d < a.length; d++) c += module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.unwrap(a[d]);
                a = goog.html.SafeHtml.createSafeHtmlSecurityPrivateDoNotAccessOrElse(c, goog.i18n.bidi.Dir.NEUTRAL);
                return goog.html.SafeHtml.createSafeHtmlTagSecurityPrivateDoNotAccessOrElse('style', b, a);
              };
              goog.html.SafeHtml.createMetaRefresh = function (a, b) {
                a = goog.html.SafeUrl.unwrap(goog.html.SafeUrl.sanitize(a));
                (goog.labs.userAgent.browser.isIE() || goog.labs.userAgent.browser.isEdge()) &&
                  goog.string.internal.contains(a, ';') &&
                  (a = "'" + a.replace(/'/g, '%27') + "'");
                return goog.html.SafeHtml.createSafeHtmlTagSecurityPrivateDoNotAccessOrElse('meta', {
                  'http-equiv': 'refresh',
                  content: (b || 0) + '; url=' + a
                });
              };
              goog.html.SafeHtml.getAttrNameAndValue_ = function (a, b, c) {
                if (c instanceof goog.string.Const) c = goog.string.Const.unwrap(c);
                else if ('style' == b.toLowerCase())
                  if (goog.html.SafeHtml.SUPPORT_STYLE_ATTRIBUTE) c = goog.html.SafeHtml.getStyleValue_(c);
                  else throw Error(goog.html.SafeHtml.ENABLE_ERROR_MESSAGES ? 'Attribute "style" not supported.' : '');
                else {
                  if (/^on/i.test(b))
                    throw Error(goog.html.SafeHtml.ENABLE_ERROR_MESSAGES ? 'Attribute "' + b + '" requires goog.string.Const value, "' + c + '" given.' : '');
                  if (b.toLowerCase() in goog.html.SafeHtml.URL_ATTRIBUTES_)
                    if (c instanceof goog.html.TrustedResourceUrl) c = goog.html.TrustedResourceUrl.unwrap(c);
                    else if (c instanceof goog.html.SafeUrl) c = goog.html.SafeUrl.unwrap(c);
                    else if ('string' === typeof c) c = goog.html.SafeUrl.sanitize(c).getTypedStringValue();
                    else
                      throw Error(
                        goog.html.SafeHtml.ENABLE_ERROR_MESSAGES
                          ? 'Attribute "' + b + '" on tag "' + a + '" requires goog.html.SafeUrl, goog.string.Const, or string, value "' + c + '" given.'
                          : ''
                      );
                }
                c.implementsGoogStringTypedString && (c = c.getTypedStringValue());
                goog.asserts.assert('string' === typeof c || 'number' === typeof c, 'String or number value expected, got ' + typeof c + ' with value: ' + c);
                return b + '="' + goog.string.internal.htmlEscape(String(c)) + '"';
              };
              goog.html.SafeHtml.getStyleValue_ = function (a) {
                if (!goog.isObject(a))
                  throw Error(
                    goog.html.SafeHtml.ENABLE_ERROR_MESSAGES
                      ? 'The "style" attribute requires goog.html.SafeStyle or map of style properties, ' + typeof a + ' given: ' + a
                      : ''
                  );
                a instanceof goog.html.SafeStyle || (a = goog.html.SafeStyle.create(a));
                return goog.html.SafeStyle.unwrap(a);
              };
              goog.html.SafeHtml.createWithDir = function (a, b, c, d) {
                b = goog.html.SafeHtml.create(b, c, d);
                b.dir_ = a;
                return b;
              };
              goog.html.SafeHtml.join = function (a, b) {
                a = goog.html.SafeHtml.htmlEscape(a);
                var c = a.getDirection(),
                  d = [],
                  e = function (f) {
                    Array.isArray(f)
                      ? module$contents$goog$array_forEach(f, e)
                      : ((f = goog.html.SafeHtml.htmlEscape(f)),
                        d.push(goog.html.SafeHtml.unwrap(f)),
                        (f = f.getDirection()),
                        c == goog.i18n.bidi.Dir.NEUTRAL ? (c = f) : f != goog.i18n.bidi.Dir.NEUTRAL && c != f && (c = null));
                  };
                module$contents$goog$array_forEach(b, e);
                return goog.html.SafeHtml.createSafeHtmlSecurityPrivateDoNotAccessOrElse(d.join(goog.html.SafeHtml.unwrap(a)), c);
              };
              goog.html.SafeHtml.concat = function (a) {
                return goog.html.SafeHtml.join(goog.html.SafeHtml.EMPTY, Array.prototype.slice.call(arguments));
              };
              goog.html.SafeHtml.concatWithDir = function (a, b) {
                var c = goog.html.SafeHtml.concat(module$contents$goog$array_slice(arguments, 1));
                c.dir_ = a;
                return c;
              };
              goog.html.SafeHtml.CONSTRUCTOR_TOKEN_PRIVATE_ = {};
              goog.html.SafeHtml.createSafeHtmlSecurityPrivateDoNotAccessOrElse = function (a, b) {
                var c = goog.html.trustedtypes.getPolicyPrivateDoNotAccessOrElse();
                a = c ? c.createHTML(a) : a;
                return new goog.html.SafeHtml(a, b, goog.html.SafeHtml.CONSTRUCTOR_TOKEN_PRIVATE_);
              };
              goog.html.SafeHtml.createSafeHtmlTagSecurityPrivateDoNotAccessOrElse = function (a, b, c) {
                var d = null;
                var e = '<' + a + goog.html.SafeHtml.stringifyAttributes(a, b);
                null == c ? (c = []) : Array.isArray(c) || (c = [c]);
                goog.dom.tags.isVoidTag(a.toLowerCase())
                  ? (goog.asserts.assert(!c.length, 'Void tag <' + a + '> does not allow content.'), (e += '>'))
                  : ((d = goog.html.SafeHtml.concat(c)), (e += '>' + goog.html.SafeHtml.unwrap(d) + '</' + a + '>'), (d = d.getDirection()));
                (a = b && b.dir) && (d = /^(ltr|rtl|auto)$/i.test(a) ? goog.i18n.bidi.Dir.NEUTRAL : null);
                return goog.html.SafeHtml.createSafeHtmlSecurityPrivateDoNotAccessOrElse(e, d);
              };
              goog.html.SafeHtml.stringifyAttributes = function (a, b) {
                var c = '';
                if (b)
                  for (var d in b)
                    if (Object.prototype.hasOwnProperty.call(b, d)) {
                      if (!goog.html.SafeHtml.VALID_NAMES_IN_TAG_.test(d))
                        throw Error(goog.html.SafeHtml.ENABLE_ERROR_MESSAGES ? 'Invalid attribute name "' + d + '".' : '');
                      var e = b[d];
                      null != e && (c += ' ' + goog.html.SafeHtml.getAttrNameAndValue_(a, d, e));
                    }
                return c;
              };
              goog.html.SafeHtml.combineAttributes = function (a, b, c) {
                var d = {},
                  e;
                for (e in a) Object.prototype.hasOwnProperty.call(a, e) && (goog.asserts.assert(e.toLowerCase() == e, 'Must be lower case'), (d[e] = a[e]));
                for (e in b) Object.prototype.hasOwnProperty.call(b, e) && (goog.asserts.assert(e.toLowerCase() == e, 'Must be lower case'), (d[e] = b[e]));
                if (c)
                  for (e in c)
                    if (Object.prototype.hasOwnProperty.call(c, e)) {
                      var f = e.toLowerCase();
                      if (f in a)
                        throw Error(
                          goog.html.SafeHtml.ENABLE_ERROR_MESSAGES ? 'Cannot override "' + f + '" attribute, got "' + e + '" with value "' + c[e] + '"' : ''
                        );
                      f in b && delete d[f];
                      d[e] = c[e];
                    }
                return d;
              };
              goog.html.SafeHtml.DOCTYPE_HTML = (function () {
                return goog.html.SafeHtml.createSafeHtmlSecurityPrivateDoNotAccessOrElse('<!DOCTYPE html>', goog.i18n.bidi.Dir.NEUTRAL);
              })();
              goog.html.SafeHtml.EMPTY = new goog.html.SafeHtml(
                (goog.global.trustedTypes && goog.global.trustedTypes.emptyHTML) || '',
                goog.i18n.bidi.Dir.NEUTRAL,
                goog.html.SafeHtml.CONSTRUCTOR_TOKEN_PRIVATE_
              );
              goog.html.SafeHtml.BR = (function () {
                return goog.html.SafeHtml.createSafeHtmlSecurityPrivateDoNotAccessOrElse('<br>', goog.i18n.bidi.Dir.NEUTRAL);
              })();
              goog.html.uncheckedconversions = {};
              goog.html.uncheckedconversions.safeHtmlFromStringKnownToSatisfyTypeContract = function (a, b, c) {
                goog.asserts.assertString(goog.string.Const.unwrap(a), 'must provide justification');
                goog.asserts.assert(!goog.string.internal.isEmptyOrWhitespace(goog.string.Const.unwrap(a)), 'must provide non-empty justification');
                return goog.html.SafeHtml.createSafeHtmlSecurityPrivateDoNotAccessOrElse(b, c || null);
              };
              goog.html.uncheckedconversions.safeScriptFromStringKnownToSatisfyTypeContract = function (a, b) {
                goog.asserts.assertString(goog.string.Const.unwrap(a), 'must provide justification');
                goog.asserts.assert(!goog.string.internal.isEmptyOrWhitespace(goog.string.Const.unwrap(a)), 'must provide non-empty justification');
                return module$contents$goog$html$SafeScript_SafeScript.createSafeScriptSecurityPrivateDoNotAccessOrElse(b);
              };
              goog.html.uncheckedconversions.safeStyleFromStringKnownToSatisfyTypeContract = function (a, b) {
                goog.asserts.assertString(goog.string.Const.unwrap(a), 'must provide justification');
                goog.asserts.assert(!goog.string.internal.isEmptyOrWhitespace(goog.string.Const.unwrap(a)), 'must provide non-empty justification');
                return goog.html.SafeStyle.createSafeStyleSecurityPrivateDoNotAccessOrElse(b);
              };
              goog.html.uncheckedconversions.safeStyleSheetFromStringKnownToSatisfyTypeContract = function (a, b) {
                goog.asserts.assertString(goog.string.Const.unwrap(a), 'must provide justification');
                goog.asserts.assert(!goog.string.internal.isEmptyOrWhitespace(goog.string.Const.unwrap(a)), 'must provide non-empty justification');
                return module$contents$goog$html$SafeStyleSheet_SafeStyleSheet.createSafeStyleSheetSecurityPrivateDoNotAccessOrElse(b);
              };
              goog.html.uncheckedconversions.safeUrlFromStringKnownToSatisfyTypeContract = function (a, b) {
                goog.asserts.assertString(goog.string.Const.unwrap(a), 'must provide justification');
                goog.asserts.assert(!goog.string.internal.isEmptyOrWhitespace(goog.string.Const.unwrap(a)), 'must provide non-empty justification');
                return goog.html.SafeUrl.createSafeUrlSecurityPrivateDoNotAccessOrElse(b);
              };
              goog.html.uncheckedconversions.trustedResourceUrlFromStringKnownToSatisfyTypeContract = function (a, b) {
                goog.asserts.assertString(goog.string.Const.unwrap(a), 'must provide justification');
                goog.asserts.assert(!goog.string.internal.isEmptyOrWhitespace(goog.string.Const.unwrap(a)), 'must provide non-empty justification');
                return goog.html.TrustedResourceUrl.createTrustedResourceUrlSecurityPrivateDoNotAccessOrElse(b);
              };
              goog.dom.safe = {};
              goog.dom.safe.InsertAdjacentHtmlPosition = { AFTERBEGIN: 'afterbegin', AFTEREND: 'afterend', BEFOREBEGIN: 'beforebegin', BEFOREEND: 'beforeend' };
              goog.dom.safe.insertAdjacentHtml = function (a, b, c) {
                a.insertAdjacentHTML(b, goog.html.SafeHtml.unwrapTrustedHTML(c));
              };
              goog.dom.safe.SET_INNER_HTML_DISALLOWED_TAGS_ = { MATH: !0, SCRIPT: !0, STYLE: !0, SVG: !0, TEMPLATE: !0 };
              goog.dom.safe.isInnerHtmlCleanupRecursive_ = goog.functions.cacheReturnValue(function () {
                if (goog.DEBUG && 'undefined' === typeof document) return !1;
                var a = document.createElement('div'),
                  b = document.createElement('div');
                b.appendChild(document.createElement('div'));
                a.appendChild(b);
                if (goog.DEBUG && !a.firstChild) return !1;
                b = a.firstChild.firstChild;
                a.innerHTML = goog.html.SafeHtml.unwrapTrustedHTML(goog.html.SafeHtml.EMPTY);
                return !b.parentElement;
              });
              goog.dom.safe.unsafeSetInnerHtmlDoNotUseOrElse = function (a, b) {
                if (goog.dom.safe.isInnerHtmlCleanupRecursive_()) for (; a.lastChild; ) a.removeChild(a.lastChild);
                a.innerHTML = goog.html.SafeHtml.unwrapTrustedHTML(b);
              };
              goog.dom.safe.setInnerHtml = function (a, b) {
                if (goog.asserts.ENABLE_ASSERTS && a.tagName) {
                  var c = a.tagName.toUpperCase();
                  if (goog.dom.safe.SET_INNER_HTML_DISALLOWED_TAGS_[c])
                    throw Error('goog.dom.safe.setInnerHtml cannot be used to set content of ' + a.tagName + '.');
                }
                goog.dom.safe.unsafeSetInnerHtmlDoNotUseOrElse(a, b);
              };
              goog.dom.safe.setInnerHtmlFromConstant = function (a, b) {
                goog.dom.safe.setInnerHtml(
                  a,
                  goog.html.uncheckedconversions.safeHtmlFromStringKnownToSatisfyTypeContract(
                    goog.string.Const.from('Constant HTML to be immediatelly used.'),
                    goog.string.Const.unwrap(b)
                  )
                );
              };
              goog.dom.safe.setOuterHtml = function (a, b) {
                a.outerHTML = goog.html.SafeHtml.unwrapTrustedHTML(b);
              };
              goog.dom.safe.setFormElementAction = function (a, b) {
                b = b instanceof goog.html.SafeUrl ? b : goog.html.SafeUrl.sanitizeAssertUnchanged(b);
                goog.dom.asserts.assertIsHTMLFormElement(a).action = goog.html.SafeUrl.unwrap(b);
              };
              goog.dom.safe.setButtonFormAction = function (a, b) {
                b = b instanceof goog.html.SafeUrl ? b : goog.html.SafeUrl.sanitizeAssertUnchanged(b);
                goog.dom.asserts.assertIsHTMLButtonElement(a).formAction = goog.html.SafeUrl.unwrap(b);
              };
              goog.dom.safe.setInputFormAction = function (a, b) {
                b = b instanceof goog.html.SafeUrl ? b : goog.html.SafeUrl.sanitizeAssertUnchanged(b);
                goog.dom.asserts.assertIsHTMLInputElement(a).formAction = goog.html.SafeUrl.unwrap(b);
              };
              goog.dom.safe.setStyle = function (a, b) {
                a.style.cssText = goog.html.SafeStyle.unwrap(b);
              };
              goog.dom.safe.documentWrite = function (a, b) {
                a.write(goog.html.SafeHtml.unwrapTrustedHTML(b));
              };
              goog.dom.safe.setAnchorHref = function (a, b) {
                goog.dom.asserts.assertIsHTMLAnchorElement(a);
                b = b instanceof goog.html.SafeUrl ? b : goog.html.SafeUrl.sanitizeAssertUnchanged(b);
                a.href = goog.html.SafeUrl.unwrap(b);
              };
              goog.dom.safe.setImageSrc = function (a, b) {
                goog.dom.asserts.assertIsHTMLImageElement(a);
                if (!(b instanceof goog.html.SafeUrl)) {
                  var c = /^data:image\//i.test(b);
                  b = goog.html.SafeUrl.sanitizeAssertUnchanged(b, c);
                }
                a.src = goog.html.SafeUrl.unwrap(b);
              };
              goog.dom.safe.setAudioSrc = function (a, b) {
                goog.dom.asserts.assertIsHTMLAudioElement(a);
                if (!(b instanceof goog.html.SafeUrl)) {
                  var c = /^data:audio\//i.test(b);
                  b = goog.html.SafeUrl.sanitizeAssertUnchanged(b, c);
                }
                a.src = goog.html.SafeUrl.unwrap(b);
              };
              goog.dom.safe.setVideoSrc = function (a, b) {
                goog.dom.asserts.assertIsHTMLVideoElement(a);
                if (!(b instanceof goog.html.SafeUrl)) {
                  var c = /^data:video\//i.test(b);
                  b = goog.html.SafeUrl.sanitizeAssertUnchanged(b, c);
                }
                a.src = goog.html.SafeUrl.unwrap(b);
              };
              goog.dom.safe.setEmbedSrc = function (a, b) {
                goog.dom.asserts.assertIsHTMLEmbedElement(a);
                a.src = goog.html.TrustedResourceUrl.unwrapTrustedScriptURL(b);
              };
              goog.dom.safe.setFrameSrc = function (a, b) {
                goog.dom.asserts.assertIsHTMLFrameElement(a);
                a.src = goog.html.TrustedResourceUrl.unwrap(b);
              };
              goog.dom.safe.setIframeSrc = function (a, b) {
                goog.dom.asserts.assertIsHTMLIFrameElement(a);
                a.src = goog.html.TrustedResourceUrl.unwrap(b);
              };
              goog.dom.safe.setIframeSrcdoc = function (a, b) {
                goog.dom.asserts.assertIsHTMLIFrameElement(a);
                a.srcdoc = goog.html.SafeHtml.unwrapTrustedHTML(b);
              };
              goog.dom.safe.setLinkHrefAndRel = function (a, b, c) {
                goog.dom.asserts.assertIsHTMLLinkElement(a);
                a.rel = c;
                goog.string.internal.caseInsensitiveContains(c, 'stylesheet')
                  ? (goog.asserts.assert(b instanceof goog.html.TrustedResourceUrl, 'URL must be TrustedResourceUrl because "rel" contains "stylesheet"'),
                    (a.href = goog.html.TrustedResourceUrl.unwrap(b)))
                  : (a.href =
                      b instanceof goog.html.TrustedResourceUrl
                        ? goog.html.TrustedResourceUrl.unwrap(b)
                        : b instanceof goog.html.SafeUrl
                          ? goog.html.SafeUrl.unwrap(b)
                          : goog.html.SafeUrl.unwrap(goog.html.SafeUrl.sanitizeAssertUnchanged(b)));
              };
              goog.dom.safe.setObjectData = function (a, b) {
                goog.dom.asserts.assertIsHTMLObjectElement(a);
                a.data = goog.html.TrustedResourceUrl.unwrapTrustedScriptURL(b);
              };
              goog.dom.safe.setScriptSrc = function (a, b) {
                goog.dom.asserts.assertIsHTMLScriptElement(a);
                a.src = goog.html.TrustedResourceUrl.unwrapTrustedScriptURL(b);
                goog.dom.safe.setNonceForScriptElement_(a);
              };
              goog.dom.safe.setScriptContent = function (a, b) {
                goog.dom.asserts.assertIsHTMLScriptElement(a);
                a.textContent = module$contents$goog$html$SafeScript_SafeScript.unwrapTrustedScript(b);
                goog.dom.safe.setNonceForScriptElement_(a);
              };
              goog.dom.safe.setNonceForScriptElement_ = function (a) {
                var b = goog.getScriptNonce(a.ownerDocument && a.ownerDocument.defaultView);
                b && a.setAttribute('nonce', b);
              };
              goog.dom.safe.setLocationHref = function (a, b) {
                goog.dom.asserts.assertIsLocation(a);
                b = b instanceof goog.html.SafeUrl ? b : goog.html.SafeUrl.sanitizeAssertUnchanged(b);
                a.href = goog.html.SafeUrl.unwrap(b);
              };
              goog.dom.safe.assignLocation = function (a, b) {
                goog.dom.asserts.assertIsLocation(a);
                b = b instanceof goog.html.SafeUrl ? b : goog.html.SafeUrl.sanitizeAssertUnchanged(b);
                a.assign(goog.html.SafeUrl.unwrap(b));
              };
              goog.dom.safe.replaceLocation = function (a, b) {
                b = b instanceof goog.html.SafeUrl ? b : goog.html.SafeUrl.sanitizeAssertUnchanged(b);
                a.replace(goog.html.SafeUrl.unwrap(b));
              };
              goog.dom.safe.openInWindow = function (a, b, c, d, e) {
                a = a instanceof goog.html.SafeUrl ? a : goog.html.SafeUrl.sanitizeAssertUnchanged(a);
                b = b || goog.global;
                c = c instanceof goog.string.Const ? goog.string.Const.unwrap(c) : c || '';
                return b.open(goog.html.SafeUrl.unwrap(a), c, d, e);
              };
              goog.dom.safe.parseFromStringHtml = function (a, b) {
                return goog.dom.safe.parseFromString(a, b, 'text/html');
              };
              goog.dom.safe.parseFromString = function (a, b, c) {
                return a.parseFromString(goog.html.SafeHtml.unwrapTrustedHTML(b), c);
              };
              goog.dom.safe.createImageFromBlob = function (a) {
                if (!/^image\/.*/g.test(a.type)) throw Error('goog.dom.safe.createImageFromBlob only accepts MIME type image/.*.');
                var b = goog.global.URL.createObjectURL(a);
                a = new goog.global.Image();
                a.onload = function () {
                  goog.global.URL.revokeObjectURL(b);
                };
                goog.dom.safe.setImageSrc(
                  a,
                  goog.html.uncheckedconversions.safeUrlFromStringKnownToSatisfyTypeContract(goog.string.Const.from('Image blob URL.'), b)
                );
                return a;
              };
              goog.string.DETECT_DOUBLE_ESCAPING = !1;
              goog.string.FORCE_NON_DOM_HTML_UNESCAPING = !1;
              goog.string.Unicode = { NBSP: '\u00a0' };
              goog.string.startsWith = goog.string.internal.startsWith;
              goog.string.endsWith = goog.string.internal.endsWith;
              goog.string.caseInsensitiveStartsWith = goog.string.internal.caseInsensitiveStartsWith;
              goog.string.caseInsensitiveEndsWith = goog.string.internal.caseInsensitiveEndsWith;
              goog.string.caseInsensitiveEquals = goog.string.internal.caseInsensitiveEquals;
              goog.string.subs = function (a, b) {
                for (var c = a.split('%s'), d = '', e = Array.prototype.slice.call(arguments, 1); e.length && 1 < c.length; ) d += c.shift() + e.shift();
                return d + c.join('%s');
              };
              goog.string.collapseWhitespace = function (a) {
                return a.replace(/[\s\xa0]+/g, ' ').replace(/^\s+|\s+$/g, '');
              };
              goog.string.isEmptyOrWhitespace = goog.string.internal.isEmptyOrWhitespace;
              goog.string.isEmptyString = function (a) {
                return 0 == a.length;
              };
              goog.string.isEmpty = goog.string.isEmptyOrWhitespace;
              goog.string.isEmptyOrWhitespaceSafe = function (a) {
                return goog.string.isEmptyOrWhitespace(goog.string.makeSafe(a));
              };
              goog.string.isEmptySafe = goog.string.isEmptyOrWhitespaceSafe;
              goog.string.isBreakingWhitespace = function (a) {
                return !/[^\t\n\r ]/.test(a);
              };
              goog.string.isAlpha = function (a) {
                return !/[^a-zA-Z]/.test(a);
              };
              goog.string.isNumeric = function (a) {
                return !/[^0-9]/.test(a);
              };
              goog.string.isAlphaNumeric = function (a) {
                return !/[^a-zA-Z0-9]/.test(a);
              };
              goog.string.isSpace = function (a) {
                return ' ' == a;
              };
              goog.string.isUnicodeChar = function (a) {
                return (1 == a.length && ' ' <= a && '~' >= a) || ('\u0080' <= a && '\ufffd' >= a);
              };
              goog.string.stripNewlines = function (a) {
                return a.replace(/(\r\n|\r|\n)+/g, ' ');
              };
              goog.string.canonicalizeNewlines = function (a) {
                return a.replace(/(\r\n|\r|\n)/g, '\n');
              };
              goog.string.normalizeWhitespace = function (a) {
                return a.replace(/\xa0|\s/g, ' ');
              };
              goog.string.normalizeSpaces = function (a) {
                return a.replace(/\xa0|[ \t]+/g, ' ');
              };
              goog.string.collapseBreakingSpaces = function (a) {
                return a.replace(/[\t\r\n ]+/g, ' ').replace(/^[\t\r\n ]+|[\t\r\n ]+$/g, '');
              };
              goog.string.trim = goog.string.internal.trim;
              goog.string.trimLeft = function (a) {
                return a.replace(/^[\s\xa0]+/, '');
              };
              goog.string.trimRight = function (a) {
                return a.replace(/[\s\xa0]+$/, '');
              };
              goog.string.caseInsensitiveCompare = goog.string.internal.caseInsensitiveCompare;
              goog.string.numberAwareCompare_ = function (a, b, c) {
                if (a == b) return 0;
                if (!a) return -1;
                if (!b) return 1;
                for (var d = a.toLowerCase().match(c), e = b.toLowerCase().match(c), f = Math.min(d.length, e.length), g = 0; g < f; g++) {
                  c = d[g];
                  var h = e[g];
                  if (c != h) return ((a = parseInt(c, 10)), !isNaN(a) && ((b = parseInt(h, 10)), !isNaN(b) && a - b) ? a - b : c < h ? -1 : 1);
                }
                return d.length != e.length ? d.length - e.length : a < b ? -1 : 1;
              };
              goog.string.intAwareCompare = function (a, b) {
                return goog.string.numberAwareCompare_(a, b, /\d+|\D+/g);
              };
              goog.string.floatAwareCompare = function (a, b) {
                return goog.string.numberAwareCompare_(a, b, /\d+|\.\d+|\D+/g);
              };
              goog.string.numerateCompare = goog.string.floatAwareCompare;
              goog.string.urlEncode = function (a) {
                return encodeURIComponent(String(a));
              };
              goog.string.urlDecode = function (a) {
                return decodeURIComponent(a.replace(/\+/g, ' '));
              };
              goog.string.newLineToBr = goog.string.internal.newLineToBr;
              goog.string.htmlEscape = function (a, b) {
                a = goog.string.internal.htmlEscape(a, b);
                goog.string.DETECT_DOUBLE_ESCAPING && (a = a.replace(goog.string.E_RE_, '&#101;'));
                return a;
              };
              goog.string.E_RE_ = /e/g;
              goog.string.unescapeEntities = function (a) {
                return goog.string.contains(a, '&')
                  ? !goog.string.FORCE_NON_DOM_HTML_UNESCAPING && 'document' in goog.global
                    ? goog.string.unescapeEntitiesUsingDom_(a)
                    : goog.string.unescapePureXmlEntities_(a)
                  : a;
              };
              goog.string.unescapeEntitiesWithDocument = function (a, b) {
                return goog.string.contains(a, '&') ? goog.string.unescapeEntitiesUsingDom_(a, b) : a;
              };
              goog.string.unescapeEntitiesUsingDom_ = function (a, b) {
                var c = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"' };
                var d = b ? b.createElement('div') : goog.global.document.createElement('div');
                return a.replace(goog.string.HTML_ENTITY_PATTERN_, function (e, f) {
                  var g = c[e];
                  if (g) return g;
                  '#' == f.charAt(0) && ((f = Number('0' + f.substr(1))), isNaN(f) || (g = String.fromCharCode(f)));
                  g ||
                    (goog.dom.safe.setInnerHtml(
                      d,
                      goog.html.uncheckedconversions.safeHtmlFromStringKnownToSatisfyTypeContract(goog.string.Const.from('Single HTML entity.'), e + ' ')
                    ),
                    (g = d.firstChild.nodeValue.slice(0, -1)));
                  return (c[e] = g);
                });
              };
              goog.string.unescapePureXmlEntities_ = function (a) {
                return a.replace(/&([^;]+);/g, function (b, c) {
                  switch (c) {
                    case 'amp':
                      return '&';
                    case 'lt':
                      return '<';
                    case 'gt':
                      return '>';
                    case 'quot':
                      return '"';
                    default:
                      return '#' != c.charAt(0) || ((c = Number('0' + c.substr(1))), isNaN(c)) ? b : String.fromCharCode(c);
                  }
                });
              };
              goog.string.HTML_ENTITY_PATTERN_ = /&([^;\s<&]+);?/g;
              goog.string.whitespaceEscape = function (a, b) {
                return goog.string.newLineToBr(a.replace(/  /g, ' &#160;'), b);
              };
              goog.string.preserveSpaces = function (a) {
                return a.replace(/(^|[\n ]) /g, '$1' + goog.string.Unicode.NBSP);
              };
              goog.string.stripQuotes = function (a, b) {
                for (var c = b.length, d = 0; d < c; d++) {
                  var e = 1 == c ? b : b.charAt(d);
                  if (a.charAt(0) == e && a.charAt(a.length - 1) == e) return a.substring(1, a.length - 1);
                }
                return a;
              };
              goog.string.truncate = function (a, b, c) {
                c && (a = goog.string.unescapeEntities(a));
                a.length > b && (a = a.substring(0, b - 3) + '...');
                c && (a = goog.string.htmlEscape(a));
                return a;
              };
              goog.string.truncateMiddle = function (a, b, c, d) {
                c && (a = goog.string.unescapeEntities(a));
                if (d && a.length > b) {
                  d > b && (d = b);
                  var e = a.length - d;
                  a = a.substring(0, b - d) + '...' + a.substring(e);
                } else a.length > b && ((d = Math.floor(b / 2)), (e = a.length - d), (a = a.substring(0, d + (b % 2)) + '...' + a.substring(e)));
                c && (a = goog.string.htmlEscape(a));
                return a;
              };
              goog.string.specialEscapeChars_ = {
                '\x00': '\\0',
                '\b': '\\b',
                '\f': '\\f',
                '\n': '\\n',
                '\r': '\\r',
                '\t': '\\t',
                '\x0B': '\\x0B',
                '"': '\\"',
                '\\': '\\\\',
                '<': '\\u003C'
              };
              goog.string.jsEscapeCache_ = { "'": "\\'" };
              goog.string.quote = function (a) {
                a = String(a);
                for (var b = ['"'], c = 0; c < a.length; c++) {
                  var d = a.charAt(c),
                    e = d.charCodeAt(0);
                  b[c + 1] = goog.string.specialEscapeChars_[d] || (31 < e && 127 > e ? d : goog.string.escapeChar(d));
                }
                b.push('"');
                return b.join('');
              };
              goog.string.escapeString = function (a) {
                for (var b = [], c = 0; c < a.length; c++) b[c] = goog.string.escapeChar(a.charAt(c));
                return b.join('');
              };
              goog.string.escapeChar = function (a) {
                if (a in goog.string.jsEscapeCache_) return goog.string.jsEscapeCache_[a];
                if (a in goog.string.specialEscapeChars_) return (goog.string.jsEscapeCache_[a] = goog.string.specialEscapeChars_[a]);
                var b = a.charCodeAt(0);
                if (31 < b && 127 > b) var c = a;
                else {
                  if (256 > b) {
                    if (((c = '\\x'), 16 > b || 256 < b)) c += '0';
                  } else ((c = '\\u'), 4096 > b && (c += '0'));
                  c += b.toString(16).toUpperCase();
                }
                return (goog.string.jsEscapeCache_[a] = c);
              };
              goog.string.contains = goog.string.internal.contains;
              goog.string.caseInsensitiveContains = goog.string.internal.caseInsensitiveContains;
              goog.string.countOf = function (a, b) {
                return a && b ? a.split(b).length - 1 : 0;
              };
              goog.string.removeAt = function (a, b, c) {
                var d = a;
                0 <= b && b < a.length && 0 < c && (d = a.substr(0, b) + a.substr(b + c, a.length - b - c));
                return d;
              };
              goog.string.remove = function (a, b) {
                return a.replace(b, '');
              };
              goog.string.removeAll = function (a, b) {
                b = new RegExp(goog.string.regExpEscape(b), 'g');
                return a.replace(b, '');
              };
              goog.string.replaceAll = function (a, b, c) {
                b = new RegExp(goog.string.regExpEscape(b), 'g');
                return a.replace(b, c.replace(/\$/g, '$$$$'));
              };
              goog.string.regExpEscape = function (a) {
                return String(a)
                  .replace(/([-()\[\]{}+?*.$\^|,:#<!\\])/g, '\\$1')
                  .replace(/\x08/g, '\\x08');
              };
              goog.string.repeat = String.prototype.repeat
                ? function (a, b) {
                    return a.repeat(b);
                  }
                : function (a, b) {
                    return Array(b + 1).join(a);
                  };
              goog.string.padNumber = function (a, b, c) {
                a = void 0 !== c ? a.toFixed(c) : String(a);
                c = a.indexOf('.');
                -1 == c && (c = a.length);
                return goog.string.repeat('0', Math.max(0, b - c)) + a;
              };
              goog.string.makeSafe = function (a) {
                return null == a ? '' : String(a);
              };
              goog.string.buildString = function (a) {
                return Array.prototype.join.call(arguments, '');
              };
              goog.string.getRandomString = function () {
                return Math.floor(2147483648 * Math.random()).toString(36) + Math.abs(Math.floor(2147483648 * Math.random()) ^ goog.now()).toString(36);
              };
              goog.string.compareVersions = goog.string.internal.compareVersions;
              goog.string.hashCode = function (a) {
                for (var b = 0, c = 0; c < a.length; ++c) b = (31 * b + a.charCodeAt(c)) >>> 0;
                return b;
              };
              goog.string.uniqueStringCounter_ = (2147483648 * Math.random()) | 0;
              goog.string.createUniqueString = function () {
                return 'goog_' + goog.string.uniqueStringCounter_++;
              };
              goog.string.toNumber = function (a) {
                var b = Number(a);
                return 0 == b && goog.string.isEmptyOrWhitespace(a) ? NaN : b;
              };
              goog.string.isLowerCamelCase = function (a) {
                return /^[a-z]+([A-Z][a-z]*)*$/.test(a);
              };
              goog.string.isUpperCamelCase = function (a) {
                return /^([A-Z][a-z]*)+$/.test(a);
              };
              goog.string.toCamelCase = function (a) {
                return String(a).replace(/\-([a-z])/g, function (b, c) {
                  return c.toUpperCase();
                });
              };
              goog.string.toSelectorCase = function (a) {
                return String(a)
                  .replace(/([A-Z])/g, '-$1')
                  .toLowerCase();
              };
              goog.string.toTitleCase = function (a, b) {
                b = 'string' === typeof b ? goog.string.regExpEscape(b) : '\\s';
                return a.replace(new RegExp('(^' + (b ? '|[' + b + ']+' : '') + ')([a-z])', 'g'), function (c, d, e) {
                  return d + e.toUpperCase();
                });
              };
              goog.string.capitalize = function (a) {
                return String(a.charAt(0)).toUpperCase() + String(a.substr(1)).toLowerCase();
              };
              goog.string.parseInt = function (a) {
                isFinite(a) && (a = String(a));
                return 'string' === typeof a ? (/^\s*-?0x/i.test(a) ? parseInt(a, 16) : parseInt(a, 10)) : NaN;
              };
              goog.string.splitLimit = function (a, b, c) {
                a = a.split(b);
                for (var d = []; 0 < c && a.length; ) (d.push(a.shift()), c--);
                a.length && d.push(a.join(b));
                return d;
              };
              goog.string.lastComponent = function (a, b) {
                if (b) 'string' == typeof b && (b = [b]);
                else return a;
                for (var c = -1, d = 0; d < b.length; d++)
                  if ('' != b[d]) {
                    var e = a.lastIndexOf(b[d]);
                    e > c && (c = e);
                  }
                return -1 == c ? a : a.slice(c + 1);
              };
              goog.string.editDistance = function (a, b) {
                var c = [],
                  d = [];
                if (a == b) return 0;
                if (!a.length || !b.length) return Math.max(a.length, b.length);
                for (var e = 0; e < b.length + 1; e++) c[e] = e;
                for (e = 0; e < a.length; e++) {
                  d[0] = e + 1;
                  for (var f = 0; f < b.length; f++) d[f + 1] = Math.min(d[f] + 1, c[f + 1] + 1, c[f] + Number(a[e] != b[f]));
                  for (f = 0; f < c.length; f++) c[f] = d[f];
                }
                return d[b.length];
              };
              goog.proto2 = {};
              goog.proto2.Descriptor = function (a, b, c) {
                this.messageType_ = a;
                this.name_ = b.name || null;
                this.fullName_ = b.fullName || null;
                this.containingType_ = b.containingType;
                this.fields_ = {};
                for (a = 0; a < c.length; a++) ((b = c[a]), (this.fields_[b.getTag()] = b));
              };
              goog.proto2.Descriptor.prototype.getName = function () {
                return this.name_;
              };
              goog.proto2.Descriptor.prototype.getFullName = function () {
                return this.fullName_;
              };
              goog.proto2.Descriptor.prototype.getContainingType = function () {
                return this.containingType_ ? this.containingType_.getDescriptor() : null;
              };
              goog.proto2.Descriptor.prototype.getFields = function () {
                var a = goog.object.getValues(this.fields_);
                module$contents$goog$array_sort(a, function (b, c) {
                  return b.getTag() - c.getTag();
                });
                return a;
              };
              goog.proto2.Descriptor.prototype.getFieldsMap = function () {
                return this.fields_;
              };
              goog.proto2.Descriptor.prototype.findFieldByName = function (a) {
                return (
                  goog.object.findValue(this.fields_, function (b, c, d) {
                    return b.getName() == a;
                  }) || null
                );
              };
              goog.proto2.Descriptor.prototype.findFieldByTag = function (a) {
                goog.asserts.assert(goog.string.isNumeric(a));
                return this.fields_[parseInt(a, 10)] || null;
              };
              goog.proto2.Descriptor.prototype.createMessageInstance = function () {
                return new this.messageType_();
              };
              goog.proto2.FieldDescriptor = function (a, b, c) {
                this.parent_ = a;
                goog.asserts.assert(goog.string.isNumeric(b));
                this.tag_ = b;
                this.name_ = c.name;
                this.isPacked_ = !!c.packed;
                this.isRepeated_ = !!c.repeated;
                this.isRequired_ = !!c.required;
                this.fieldType_ = c.fieldType;
                this.nativeType_ = c.type;
                this.deserializationConversionPermitted_ = !1;
                switch (this.fieldType_) {
                  case goog.proto2.FieldDescriptor.FieldType.INT64:
                  case goog.proto2.FieldDescriptor.FieldType.UINT64:
                  case goog.proto2.FieldDescriptor.FieldType.FIXED64:
                  case goog.proto2.FieldDescriptor.FieldType.SFIXED64:
                  case goog.proto2.FieldDescriptor.FieldType.SINT64:
                  case goog.proto2.FieldDescriptor.FieldType.FLOAT:
                  case goog.proto2.FieldDescriptor.FieldType.DOUBLE:
                    this.deserializationConversionPermitted_ = !0;
                }
                this.defaultValue_ = c.defaultValue;
              };
              goog.proto2.FieldDescriptor.FieldType = {
                DOUBLE: 1,
                FLOAT: 2,
                INT64: 3,
                UINT64: 4,
                INT32: 5,
                FIXED64: 6,
                FIXED32: 7,
                BOOL: 8,
                STRING: 9,
                GROUP: 10,
                MESSAGE: 11,
                BYTES: 12,
                UINT32: 13,
                ENUM: 14,
                SFIXED32: 15,
                SFIXED64: 16,
                SINT32: 17,
                SINT64: 18
              };
              goog.proto2.FieldDescriptor.prototype.getTag = function () {
                return this.tag_;
              };
              goog.proto2.FieldDescriptor.prototype.getContainingType = function () {
                return this.parent_.prototype.getDescriptor();
              };
              goog.proto2.FieldDescriptor.prototype.getName = function () {
                return this.name_;
              };
              goog.proto2.FieldDescriptor.prototype.getDefaultValue = function () {
                if (void 0 === this.defaultValue_) {
                  var a = this.nativeType_;
                  if (a === Boolean) this.defaultValue_ = !1;
                  else if (a === Number) this.defaultValue_ = 0;
                  else if (a === String) this.defaultValue_ = this.deserializationConversionPermitted_ ? '0' : '';
                  else return new a();
                }
                return this.defaultValue_;
              };
              goog.proto2.FieldDescriptor.prototype.getFieldType = function () {
                return this.fieldType_;
              };
              goog.proto2.FieldDescriptor.prototype.getNativeType = function () {
                return this.nativeType_;
              };
              goog.proto2.FieldDescriptor.prototype.deserializationConversionPermitted = function () {
                return this.deserializationConversionPermitted_;
              };
              goog.proto2.FieldDescriptor.prototype.getFieldMessageType = function () {
                return this.nativeType_.prototype.getDescriptor();
              };
              goog.proto2.FieldDescriptor.prototype.isCompositeType = function () {
                return this.fieldType_ == goog.proto2.FieldDescriptor.FieldType.MESSAGE || this.fieldType_ == goog.proto2.FieldDescriptor.FieldType.GROUP;
              };
              goog.proto2.FieldDescriptor.prototype.isPacked = function () {
                return this.isPacked_;
              };
              goog.proto2.FieldDescriptor.prototype.isRepeated = function () {
                return this.isRepeated_;
              };
              goog.proto2.FieldDescriptor.prototype.isRequired = function () {
                return this.isRequired_;
              };
              goog.proto2.FieldDescriptor.prototype.isOptional = function () {
                return !this.isRepeated_ && !this.isRequired_;
              };
              goog.proto2.Message = function () {
                this.values_ = {};
                this.fields_ = this.getDescriptor().getFieldsMap();
                this.deserializedFields_ = this.lazyDeserializer_ = null;
              };
              goog.proto2.Message.FieldType = {
                DOUBLE: 1,
                FLOAT: 2,
                INT64: 3,
                UINT64: 4,
                INT32: 5,
                FIXED64: 6,
                FIXED32: 7,
                BOOL: 8,
                STRING: 9,
                GROUP: 10,
                MESSAGE: 11,
                BYTES: 12,
                UINT32: 13,
                ENUM: 14,
                SFIXED32: 15,
                SFIXED64: 16,
                SINT32: 17,
                SINT64: 18
              };
              goog.proto2.Message.prototype.initializeForLazyDeserializer = function (a, b) {
                this.lazyDeserializer_ = a;
                this.values_ = b;
                this.deserializedFields_ = {};
              };
              goog.proto2.Message.prototype.setUnknown = function (a, b) {
                goog.asserts.assert(!this.fields_[a], 'Field is not unknown in this message');
                goog.asserts.assert(1 <= a, 'Tag ' + a + ' has value "' + b + '" in descriptor ' + this.getDescriptor().getName());
                goog.asserts.assert(null !== b, 'Value cannot be null');
                this.values_[a] = b;
                this.deserializedFields_ && delete this.deserializedFields_[a];
              };
              goog.proto2.Message.prototype.forEachUnknown = function (a, b) {
                b = b || this;
                for (var c in this.values_) {
                  var d = Number(c);
                  this.fields_[d] || a.call(b, d, this.values_[c]);
                }
              };
              goog.proto2.Message.prototype.getDescriptor = goog.abstractMethod;
              goog.proto2.Message.prototype.has = function (a) {
                goog.asserts.assert(a.getContainingType() == this.getDescriptor(), 'The current message does not contain the given field');
                return this.has$Value(a.getTag());
              };
              goog.proto2.Message.prototype.arrayOf = function (a) {
                goog.asserts.assert(a.getContainingType() == this.getDescriptor(), 'The current message does not contain the given field');
                return this.array$Values(a.getTag());
              };
              goog.proto2.Message.prototype.countOf = function (a) {
                goog.asserts.assert(a.getContainingType() == this.getDescriptor(), 'The current message does not contain the given field');
                return this.count$Values(a.getTag());
              };
              goog.proto2.Message.prototype.get = function (a, b) {
                goog.asserts.assert(a.getContainingType() == this.getDescriptor(), 'The current message does not contain the given field');
                return this.get$Value(a.getTag(), b);
              };
              goog.proto2.Message.prototype.getOrDefault = function (a, b) {
                goog.asserts.assert(a.getContainingType() == this.getDescriptor(), 'The current message does not contain the given field');
                return this.get$ValueOrDefault(a.getTag(), b);
              };
              goog.proto2.Message.prototype.set = function (a, b) {
                goog.asserts.assert(a.getContainingType() == this.getDescriptor(), 'The current message does not contain the given field');
                this.set$Value(a.getTag(), b);
              };
              goog.proto2.Message.prototype.add = function (a, b) {
                goog.asserts.assert(a.getContainingType() == this.getDescriptor(), 'The current message does not contain the given field');
                this.add$Value(a.getTag(), b);
              };
              goog.proto2.Message.prototype.clear = function (a) {
                goog.asserts.assert(a.getContainingType() == this.getDescriptor(), 'The current message does not contain the given field');
                this.clear$Field(a.getTag());
              };
              goog.proto2.Message.prototype.equals = function (a) {
                if (!a || this.constructor != a.constructor) return !1;
                for (var b = this.getDescriptor().getFields(), c = 0; c < b.length; c++) {
                  var d = b[c],
                    e = d.getTag();
                  if (this.has$Value(e) != a.has$Value(e)) return !1;
                  if (this.has$Value(e)) {
                    var f = d.isCompositeType(),
                      g = this.getValueForTag_(e);
                    e = a.getValueForTag_(e);
                    if (d.isRepeated()) {
                      if (g.length != e.length) return !1;
                      for (d = 0; d < g.length; d++) {
                        var h = g[d],
                          k = e[d];
                        if (f ? !h.equals(k) : h != k) return !1;
                      }
                    } else if (f ? !g.equals(e) : g != e) return !1;
                  }
                }
                return !0;
              };
              goog.proto2.Message.prototype.copyFrom = function (a) {
                goog.asserts.assert(this.constructor == a.constructor, 'The source message must have the same type.');
                this != a && ((this.values_ = {}), this.deserializedFields_ && (this.deserializedFields_ = {}), this.mergeFrom(a));
              };
              goog.proto2.Message.prototype.mergeFrom = function (a) {
                goog.asserts.assert(this.constructor == a.constructor, 'The source message must have the same type.');
                for (var b = this.getDescriptor().getFields(), c = 0; c < b.length; c++) {
                  var d = b[c],
                    e = d.getTag();
                  if (a.has$Value(e)) {
                    this.deserializedFields_ && delete this.deserializedFields_[d.getTag()];
                    var f = d.isCompositeType();
                    if (d.isRepeated()) {
                      d = a.array$Values(e);
                      for (var g = 0; g < d.length; g++) this.add$Value(e, f ? d[g].clone() : d[g]);
                    } else
                      ((d = a.getValueForTag_(e)), f ? ((f = this.getValueForTag_(e)) ? f.mergeFrom(d) : this.set$Value(e, d.clone())) : this.set$Value(e, d));
                  }
                }
              };
              goog.proto2.Message.prototype.clone = function () {
                var a = new this.constructor();
                a.copyFrom(this);
                return a;
              };
              goog.proto2.Message.prototype.initDefaults = function (a) {
                for (var b = this.getDescriptor().getFields(), c = 0; c < b.length; c++) {
                  var d = b[c],
                    e = d.getTag(),
                    f = d.isCompositeType();
                  this.has$Value(e) || d.isRepeated() || (f ? (this.values_[e] = new (d.getNativeType())()) : a && (this.values_[e] = d.getDefaultValue()));
                  if (f)
                    if (d.isRepeated()) for (d = this.array$Values(e), e = 0; e < d.length; e++) d[e].initDefaults(a);
                    else this.get$Value(e).initDefaults(a);
                }
              };
              goog.proto2.Message.prototype.has$Value = function (a) {
                return null != this.values_[a];
              };
              goog.proto2.Message.prototype.getValueForTag_ = function (a) {
                var b = this.values_[a];
                return null == b
                  ? null
                  : this.lazyDeserializer_
                    ? a in this.deserializedFields_
                      ? this.deserializedFields_[a]
                      : ((b = this.lazyDeserializer_.deserializeField(this, this.fields_[a], b)), (this.deserializedFields_[a] = b))
                    : b;
              };
              goog.proto2.Message.prototype.get$Value = function (a, b) {
                var c = this.getValueForTag_(a);
                return this.fields_[a].isRepeated()
                  ? ((a = b || 0),
                    goog.asserts.assert(0 <= a && a < c.length, 'Given index %s is out of bounds.  Repeated field length: %s', a, c.length),
                    c[a])
                  : c;
              };
              goog.proto2.Message.prototype.get$ValueOrDefault = function (a, b) {
                return this.has$Value(a) ? this.get$Value(a, b) : this.fields_[a].getDefaultValue();
              };
              goog.proto2.Message.prototype.array$Values = function (a) {
                return this.getValueForTag_(a) || [];
              };
              goog.proto2.Message.prototype.count$Values = function (a) {
                return this.fields_[a].isRepeated() ? (this.has$Value(a) ? this.values_[a].length : 0) : this.has$Value(a) ? 1 : 0;
              };
              goog.proto2.Message.prototype.set$Value = function (a, b) {
                goog.asserts.ENABLE_ASSERTS && this.checkFieldType_(this.fields_[a], b);
                this.values_[a] = b;
                this.deserializedFields_ && (this.deserializedFields_[a] = b);
              };
              goog.proto2.Message.prototype.add$Value = function (a, b) {
                goog.asserts.ENABLE_ASSERTS && this.checkFieldType_(this.fields_[a], b);
                this.values_[a] || (this.values_[a] = []);
                this.values_[a].push(b);
                this.deserializedFields_ && delete this.deserializedFields_[a];
              };
              goog.proto2.Message.prototype.checkFieldType_ = function (a, b) {
                a.getFieldType() == goog.proto2.FieldDescriptor.FieldType.ENUM
                  ? goog.asserts.assertNumber(b)
                  : goog.asserts.assert(Object(b).constructor == a.getNativeType());
              };
              goog.proto2.Message.prototype.clear$Field = function (a) {
                delete this.values_[a];
                this.deserializedFields_ && delete this.deserializedFields_[a];
              };
              goog.proto2.Message.createDescriptor = function (a, b) {
                var c = [],
                  d = b[0],
                  e;
                for (e in b) 0 != e && c.push(new goog.proto2.FieldDescriptor(a, e, b[e]));
                return new goog.proto2.Descriptor(a, d, c);
              };
              goog.proto2.Serializer = function () {};
              goog.proto2.Serializer.DECODE_SYMBOLIC_ENUMS = !1;
              goog.proto2.Serializer.prototype.serialize = goog.abstractMethod;
              goog.proto2.Serializer.prototype.getSerializedValue = function (a, b) {
                return a.isCompositeType() ? this.serialize(b) : 'number' !== typeof b || isFinite(b) ? b : b.toString();
              };
              goog.proto2.Serializer.prototype.deserialize = function (a, b) {
                a = a.createMessageInstance();
                this.deserializeTo(a, b);
                goog.asserts.assert(a instanceof goog.proto2.Message);
                return a;
              };
              goog.proto2.Serializer.prototype.deserializeTo = goog.abstractMethod;
              goog.proto2.Serializer.prototype.getDeserializedValue = function (a, b) {
                if (a.isCompositeType()) return b instanceof goog.proto2.Message ? b : this.deserialize(a.getFieldMessageType(), b);
                if (a.getFieldType() == goog.proto2.FieldDescriptor.FieldType.ENUM)
                  return goog.proto2.Serializer.DECODE_SYMBOLIC_ENUMS && 'string' === typeof b && ((a = a.getNativeType()), a.hasOwnProperty(b))
                    ? a[b]
                    : 'string' === typeof b && goog.proto2.Serializer.INTEGER_REGEX.test(b) && ((a = Number(b)), 0 < a)
                      ? a
                      : b;
                if (!a.deserializationConversionPermitted()) return b;
                a = a.getNativeType();
                if (a === String) {
                  if ('number' === typeof b) return String(b);
                } else if (
                  a === Number &&
                  'string' === typeof b &&
                  ('Infinity' === b || '-Infinity' === b || 'NaN' === b || goog.proto2.Serializer.INTEGER_REGEX.test(b))
                )
                  return Number(b);
                return b;
              };
              goog.proto2.Serializer.INTEGER_REGEX = /^-?[0-9]+$/;
              goog.proto2.LazyDeserializer = function () {};
              goog.inherits(goog.proto2.LazyDeserializer, goog.proto2.Serializer);
              goog.proto2.LazyDeserializer.prototype.deserialize = function (a, b) {
                a = a.createMessageInstance();
                a.initializeForLazyDeserializer(this, b);
                goog.asserts.assert(a instanceof goog.proto2.Message);
                return a;
              };
              goog.proto2.LazyDeserializer.prototype.deserializeTo = function (a, b) {
                throw Error('Unimplemented');
              };
              goog.proto2.LazyDeserializer.prototype.deserializeField = goog.abstractMethod;
              goog.proto2.PbLiteSerializer = function () {};
              goog.inherits(goog.proto2.PbLiteSerializer, goog.proto2.LazyDeserializer);
              goog.proto2.PbLiteSerializer.prototype.zeroIndexing_ = !1;
              goog.proto2.PbLiteSerializer.prototype.setZeroIndexed = function (a) {
                this.zeroIndexing_ = a;
              };
              goog.proto2.PbLiteSerializer.prototype.serialize = function (a) {
                for (var b = a.getDescriptor().getFields(), c = [], d = this.zeroIndexing_, e = 0; e < b.length; e++) {
                  var f = b[e];
                  if (a.has(f)) {
                    var g = f.getTag();
                    g = d ? g - 1 : g;
                    if (f.isRepeated()) {
                      c[g] = [];
                      for (var h = 0; h < a.countOf(f); h++) c[g][h] = this.getSerializedValue(f, a.get(f, h));
                    } else c[g] = this.getSerializedValue(f, a.get(f));
                  }
                }
                a.forEachUnknown(function (k, l) {
                  c[d ? k - 1 : k] = l;
                });
                return c;
              };
              goog.proto2.PbLiteSerializer.prototype.deserializeField = function (a, b, c) {
                if (null == c) return c;
                if (b.isRepeated()) {
                  a = [];
                  goog.asserts.assert(Array.isArray(c), 'Value must be array: %s', c);
                  for (var d = 0; d < c.length; d++) a[d] = this.getDeserializedValue(b, c[d]);
                  return a;
                }
                return this.getDeserializedValue(b, c);
              };
              goog.proto2.PbLiteSerializer.prototype.getSerializedValue = function (a, b) {
                return a.getFieldType() == goog.proto2.FieldDescriptor.FieldType.BOOL
                  ? b
                    ? 1
                    : 0
                  : goog.proto2.Serializer.prototype.getSerializedValue.apply(this, arguments);
              };
              goog.proto2.PbLiteSerializer.prototype.getDeserializedValue = function (a, b) {
                return a.getFieldType() == goog.proto2.FieldDescriptor.FieldType.BOOL
                  ? (goog.asserts.assert('number' === typeof b || 'boolean' === typeof b, 'Value is expected to be a number or boolean'), !!b)
                  : goog.proto2.Serializer.prototype.getDeserializedValue.apply(this, arguments);
              };
              goog.proto2.PbLiteSerializer.prototype.deserialize = function (a, b) {
                var c = b;
                if (this.zeroIndexing_) {
                  c = [];
                  for (var d in b) c[parseInt(d, 10) + 1] = b[d];
                }
                return goog.proto2.PbLiteSerializer.superClass_.deserialize.call(this, a, c);
              };
              goog.string.StringBuffer = function (a, b) {
                null != a && this.append.apply(this, arguments);
              };
              goog.string.StringBuffer.prototype.buffer_ = '';
              goog.string.StringBuffer.prototype.set = function (a) {
                this.buffer_ = '' + a;
              };
              goog.string.StringBuffer.prototype.append = function (a, b, c) {
                this.buffer_ += String(a);
                if (null != b) for (var d = 1; d < arguments.length; d++) this.buffer_ += arguments[d];
                return this;
              };
              goog.string.StringBuffer.prototype.clear = function () {
                this.buffer_ = '';
              };
              goog.string.StringBuffer.prototype.getLength = function () {
                return this.buffer_.length;
              };
              goog.string.StringBuffer.prototype.toString = function () {
                return this.buffer_;
              }; /*

 Protocol Buffer 2 Copyright 2008 Google Inc.
 All other code copyright its respective owners.
 Copyright (C) 2010 The Libphonenumber Authors

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

 http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.
*/
              var i18n = { phonenumbers: {} };
              i18n.phonenumbers.NumberFormat = function () {
                goog.proto2.Message.call(this);
              };
              goog.inherits(i18n.phonenumbers.NumberFormat, goog.proto2.Message);
              i18n.phonenumbers.NumberFormat.descriptor_ = null;
              i18n.phonenumbers.NumberFormat.prototype.getPattern = function () {
                return this.get$Value(1);
              };
              i18n.phonenumbers.NumberFormat.prototype.getPatternOrDefault = function () {
                return this.get$ValueOrDefault(1);
              };
              i18n.phonenumbers.NumberFormat.prototype.setPattern = function (a) {
                this.set$Value(1, a);
              };
              i18n.phonenumbers.NumberFormat.prototype.hasPattern = function () {
                return this.has$Value(1);
              };
              i18n.phonenumbers.NumberFormat.prototype.patternCount = function () {
                return this.count$Values(1);
              };
              i18n.phonenumbers.NumberFormat.prototype.clearPattern = function () {
                this.clear$Field(1);
              };
              i18n.phonenumbers.NumberFormat.prototype.getFormat = function () {
                return this.get$Value(2);
              };
              i18n.phonenumbers.NumberFormat.prototype.getFormatOrDefault = function () {
                return this.get$ValueOrDefault(2);
              };
              i18n.phonenumbers.NumberFormat.prototype.setFormat = function (a) {
                this.set$Value(2, a);
              };
              i18n.phonenumbers.NumberFormat.prototype.hasFormat = function () {
                return this.has$Value(2);
              };
              i18n.phonenumbers.NumberFormat.prototype.formatCount = function () {
                return this.count$Values(2);
              };
              i18n.phonenumbers.NumberFormat.prototype.clearFormat = function () {
                this.clear$Field(2);
              };
              i18n.phonenumbers.NumberFormat.prototype.getLeadingDigitsPattern = function (a) {
                return this.get$Value(3, a);
              };
              i18n.phonenumbers.NumberFormat.prototype.getLeadingDigitsPatternOrDefault = function (a) {
                return this.get$ValueOrDefault(3, a);
              };
              i18n.phonenumbers.NumberFormat.prototype.addLeadingDigitsPattern = function (a) {
                this.add$Value(3, a);
              };
              i18n.phonenumbers.NumberFormat.prototype.leadingDigitsPatternArray = function () {
                return this.array$Values(3);
              };
              i18n.phonenumbers.NumberFormat.prototype.hasLeadingDigitsPattern = function () {
                return this.has$Value(3);
              };
              i18n.phonenumbers.NumberFormat.prototype.leadingDigitsPatternCount = function () {
                return this.count$Values(3);
              };
              i18n.phonenumbers.NumberFormat.prototype.clearLeadingDigitsPattern = function () {
                this.clear$Field(3);
              };
              i18n.phonenumbers.NumberFormat.prototype.getNationalPrefixFormattingRule = function () {
                return this.get$Value(4);
              };
              i18n.phonenumbers.NumberFormat.prototype.getNationalPrefixFormattingRuleOrDefault = function () {
                return this.get$ValueOrDefault(4);
              };
              i18n.phonenumbers.NumberFormat.prototype.setNationalPrefixFormattingRule = function (a) {
                this.set$Value(4, a);
              };
              i18n.phonenumbers.NumberFormat.prototype.hasNationalPrefixFormattingRule = function () {
                return this.has$Value(4);
              };
              i18n.phonenumbers.NumberFormat.prototype.nationalPrefixFormattingRuleCount = function () {
                return this.count$Values(4);
              };
              i18n.phonenumbers.NumberFormat.prototype.clearNationalPrefixFormattingRule = function () {
                this.clear$Field(4);
              };
              i18n.phonenumbers.NumberFormat.prototype.getNationalPrefixOptionalWhenFormatting = function () {
                return this.get$Value(6);
              };
              i18n.phonenumbers.NumberFormat.prototype.getNationalPrefixOptionalWhenFormattingOrDefault = function () {
                return this.get$ValueOrDefault(6);
              };
              i18n.phonenumbers.NumberFormat.prototype.setNationalPrefixOptionalWhenFormatting = function (a) {
                this.set$Value(6, a);
              };
              i18n.phonenumbers.NumberFormat.prototype.hasNationalPrefixOptionalWhenFormatting = function () {
                return this.has$Value(6);
              };
              i18n.phonenumbers.NumberFormat.prototype.nationalPrefixOptionalWhenFormattingCount = function () {
                return this.count$Values(6);
              };
              i18n.phonenumbers.NumberFormat.prototype.clearNationalPrefixOptionalWhenFormatting = function () {
                this.clear$Field(6);
              };
              i18n.phonenumbers.NumberFormat.prototype.getDomesticCarrierCodeFormattingRule = function () {
                return this.get$Value(5);
              };
              i18n.phonenumbers.NumberFormat.prototype.getDomesticCarrierCodeFormattingRuleOrDefault = function () {
                return this.get$ValueOrDefault(5);
              };
              i18n.phonenumbers.NumberFormat.prototype.setDomesticCarrierCodeFormattingRule = function (a) {
                this.set$Value(5, a);
              };
              i18n.phonenumbers.NumberFormat.prototype.hasDomesticCarrierCodeFormattingRule = function () {
                return this.has$Value(5);
              };
              i18n.phonenumbers.NumberFormat.prototype.domesticCarrierCodeFormattingRuleCount = function () {
                return this.count$Values(5);
              };
              i18n.phonenumbers.NumberFormat.prototype.clearDomesticCarrierCodeFormattingRule = function () {
                this.clear$Field(5);
              };
              i18n.phonenumbers.PhoneNumberDesc = function () {
                goog.proto2.Message.call(this);
              };
              goog.inherits(i18n.phonenumbers.PhoneNumberDesc, goog.proto2.Message);
              i18n.phonenumbers.PhoneNumberDesc.descriptor_ = null;
              i18n.phonenumbers.PhoneNumberDesc.prototype.getNationalNumberPattern = function () {
                return this.get$Value(2);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.getNationalNumberPatternOrDefault = function () {
                return this.get$ValueOrDefault(2);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.setNationalNumberPattern = function (a) {
                this.set$Value(2, a);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.hasNationalNumberPattern = function () {
                return this.has$Value(2);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.nationalNumberPatternCount = function () {
                return this.count$Values(2);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.clearNationalNumberPattern = function () {
                this.clear$Field(2);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.getPossibleLength = function (a) {
                return this.get$Value(9, a);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.getPossibleLengthOrDefault = function (a) {
                return this.get$ValueOrDefault(9, a);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.addPossibleLength = function (a) {
                this.add$Value(9, a);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.possibleLengthArray = function () {
                return this.array$Values(9);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.hasPossibleLength = function () {
                return this.has$Value(9);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.possibleLengthCount = function () {
                return this.count$Values(9);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.clearPossibleLength = function () {
                this.clear$Field(9);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.getPossibleLengthLocalOnly = function (a) {
                return this.get$Value(10, a);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.getPossibleLengthLocalOnlyOrDefault = function (a) {
                return this.get$ValueOrDefault(10, a);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.addPossibleLengthLocalOnly = function (a) {
                this.add$Value(10, a);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.possibleLengthLocalOnlyArray = function () {
                return this.array$Values(10);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.hasPossibleLengthLocalOnly = function () {
                return this.has$Value(10);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.possibleLengthLocalOnlyCount = function () {
                return this.count$Values(10);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.clearPossibleLengthLocalOnly = function () {
                this.clear$Field(10);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.getExampleNumber = function () {
                return this.get$Value(6);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.getExampleNumberOrDefault = function () {
                return this.get$ValueOrDefault(6);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.setExampleNumber = function (a) {
                this.set$Value(6, a);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.hasExampleNumber = function () {
                return this.has$Value(6);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.exampleNumberCount = function () {
                return this.count$Values(6);
              };
              i18n.phonenumbers.PhoneNumberDesc.prototype.clearExampleNumber = function () {
                this.clear$Field(6);
              };
              i18n.phonenumbers.PhoneMetadata = function () {
                goog.proto2.Message.call(this);
              };
              goog.inherits(i18n.phonenumbers.PhoneMetadata, goog.proto2.Message);
              i18n.phonenumbers.PhoneMetadata.descriptor_ = null;
              i18n.phonenumbers.PhoneMetadata.prototype.getGeneralDesc = function () {
                return this.get$Value(1);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getGeneralDescOrDefault = function () {
                return this.get$ValueOrDefault(1);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setGeneralDesc = function (a) {
                this.set$Value(1, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasGeneralDesc = function () {
                return this.has$Value(1);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.generalDescCount = function () {
                return this.count$Values(1);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearGeneralDesc = function () {
                this.clear$Field(1);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getFixedLine = function () {
                return this.get$Value(2);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getFixedLineOrDefault = function () {
                return this.get$ValueOrDefault(2);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setFixedLine = function (a) {
                this.set$Value(2, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasFixedLine = function () {
                return this.has$Value(2);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.fixedLineCount = function () {
                return this.count$Values(2);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearFixedLine = function () {
                this.clear$Field(2);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getMobile = function () {
                return this.get$Value(3);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getMobileOrDefault = function () {
                return this.get$ValueOrDefault(3);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setMobile = function (a) {
                this.set$Value(3, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasMobile = function () {
                return this.has$Value(3);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.mobileCount = function () {
                return this.count$Values(3);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearMobile = function () {
                this.clear$Field(3);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getTollFree = function () {
                return this.get$Value(4);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getTollFreeOrDefault = function () {
                return this.get$ValueOrDefault(4);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setTollFree = function (a) {
                this.set$Value(4, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasTollFree = function () {
                return this.has$Value(4);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.tollFreeCount = function () {
                return this.count$Values(4);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearTollFree = function () {
                this.clear$Field(4);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getPremiumRate = function () {
                return this.get$Value(5);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getPremiumRateOrDefault = function () {
                return this.get$ValueOrDefault(5);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setPremiumRate = function (a) {
                this.set$Value(5, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasPremiumRate = function () {
                return this.has$Value(5);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.premiumRateCount = function () {
                return this.count$Values(5);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearPremiumRate = function () {
                this.clear$Field(5);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getSharedCost = function () {
                return this.get$Value(6);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getSharedCostOrDefault = function () {
                return this.get$ValueOrDefault(6);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setSharedCost = function (a) {
                this.set$Value(6, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasSharedCost = function () {
                return this.has$Value(6);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.sharedCostCount = function () {
                return this.count$Values(6);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearSharedCost = function () {
                this.clear$Field(6);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getPersonalNumber = function () {
                return this.get$Value(7);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getPersonalNumberOrDefault = function () {
                return this.get$ValueOrDefault(7);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setPersonalNumber = function (a) {
                this.set$Value(7, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasPersonalNumber = function () {
                return this.has$Value(7);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.personalNumberCount = function () {
                return this.count$Values(7);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearPersonalNumber = function () {
                this.clear$Field(7);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getVoip = function () {
                return this.get$Value(8);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getVoipOrDefault = function () {
                return this.get$ValueOrDefault(8);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setVoip = function (a) {
                this.set$Value(8, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasVoip = function () {
                return this.has$Value(8);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.voipCount = function () {
                return this.count$Values(8);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearVoip = function () {
                this.clear$Field(8);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getPager = function () {
                return this.get$Value(21);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getPagerOrDefault = function () {
                return this.get$ValueOrDefault(21);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setPager = function (a) {
                this.set$Value(21, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasPager = function () {
                return this.has$Value(21);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.pagerCount = function () {
                return this.count$Values(21);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearPager = function () {
                this.clear$Field(21);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getUan = function () {
                return this.get$Value(25);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getUanOrDefault = function () {
                return this.get$ValueOrDefault(25);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setUan = function (a) {
                this.set$Value(25, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasUan = function () {
                return this.has$Value(25);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.uanCount = function () {
                return this.count$Values(25);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearUan = function () {
                this.clear$Field(25);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getEmergency = function () {
                return this.get$Value(27);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getEmergencyOrDefault = function () {
                return this.get$ValueOrDefault(27);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setEmergency = function (a) {
                this.set$Value(27, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasEmergency = function () {
                return this.has$Value(27);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.emergencyCount = function () {
                return this.count$Values(27);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearEmergency = function () {
                this.clear$Field(27);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getVoicemail = function () {
                return this.get$Value(28);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getVoicemailOrDefault = function () {
                return this.get$ValueOrDefault(28);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setVoicemail = function (a) {
                this.set$Value(28, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasVoicemail = function () {
                return this.has$Value(28);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.voicemailCount = function () {
                return this.count$Values(28);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearVoicemail = function () {
                this.clear$Field(28);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getShortCode = function () {
                return this.get$Value(29);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getShortCodeOrDefault = function () {
                return this.get$ValueOrDefault(29);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setShortCode = function (a) {
                this.set$Value(29, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasShortCode = function () {
                return this.has$Value(29);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.shortCodeCount = function () {
                return this.count$Values(29);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearShortCode = function () {
                this.clear$Field(29);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getStandardRate = function () {
                return this.get$Value(30);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getStandardRateOrDefault = function () {
                return this.get$ValueOrDefault(30);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setStandardRate = function (a) {
                this.set$Value(30, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasStandardRate = function () {
                return this.has$Value(30);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.standardRateCount = function () {
                return this.count$Values(30);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearStandardRate = function () {
                this.clear$Field(30);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getCarrierSpecific = function () {
                return this.get$Value(31);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getCarrierSpecificOrDefault = function () {
                return this.get$ValueOrDefault(31);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setCarrierSpecific = function (a) {
                this.set$Value(31, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasCarrierSpecific = function () {
                return this.has$Value(31);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.carrierSpecificCount = function () {
                return this.count$Values(31);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearCarrierSpecific = function () {
                this.clear$Field(31);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getSmsServices = function () {
                return this.get$Value(33);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getSmsServicesOrDefault = function () {
                return this.get$ValueOrDefault(33);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setSmsServices = function (a) {
                this.set$Value(33, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasSmsServices = function () {
                return this.has$Value(33);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.smsServicesCount = function () {
                return this.count$Values(33);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearSmsServices = function () {
                this.clear$Field(33);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getNoInternationalDialling = function () {
                return this.get$Value(24);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getNoInternationalDiallingOrDefault = function () {
                return this.get$ValueOrDefault(24);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setNoInternationalDialling = function (a) {
                this.set$Value(24, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasNoInternationalDialling = function () {
                return this.has$Value(24);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.noInternationalDiallingCount = function () {
                return this.count$Values(24);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearNoInternationalDialling = function () {
                this.clear$Field(24);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getId = function () {
                return this.get$Value(9);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getIdOrDefault = function () {
                return this.get$ValueOrDefault(9);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setId = function (a) {
                this.set$Value(9, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasId = function () {
                return this.has$Value(9);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.idCount = function () {
                return this.count$Values(9);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearId = function () {
                this.clear$Field(9);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getCountryCode = function () {
                return this.get$Value(10);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getCountryCodeOrDefault = function () {
                return this.get$ValueOrDefault(10);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setCountryCode = function (a) {
                this.set$Value(10, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasCountryCode = function () {
                return this.has$Value(10);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.countryCodeCount = function () {
                return this.count$Values(10);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearCountryCode = function () {
                this.clear$Field(10);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getInternationalPrefix = function () {
                return this.get$Value(11);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getInternationalPrefixOrDefault = function () {
                return this.get$ValueOrDefault(11);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setInternationalPrefix = function (a) {
                this.set$Value(11, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasInternationalPrefix = function () {
                return this.has$Value(11);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.internationalPrefixCount = function () {
                return this.count$Values(11);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearInternationalPrefix = function () {
                this.clear$Field(11);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getPreferredInternationalPrefix = function () {
                return this.get$Value(17);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getPreferredInternationalPrefixOrDefault = function () {
                return this.get$ValueOrDefault(17);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setPreferredInternationalPrefix = function (a) {
                this.set$Value(17, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasPreferredInternationalPrefix = function () {
                return this.has$Value(17);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.preferredInternationalPrefixCount = function () {
                return this.count$Values(17);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearPreferredInternationalPrefix = function () {
                this.clear$Field(17);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getNationalPrefix = function () {
                return this.get$Value(12);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getNationalPrefixOrDefault = function () {
                return this.get$ValueOrDefault(12);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setNationalPrefix = function (a) {
                this.set$Value(12, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasNationalPrefix = function () {
                return this.has$Value(12);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.nationalPrefixCount = function () {
                return this.count$Values(12);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearNationalPrefix = function () {
                this.clear$Field(12);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getPreferredExtnPrefix = function () {
                return this.get$Value(13);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getPreferredExtnPrefixOrDefault = function () {
                return this.get$ValueOrDefault(13);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setPreferredExtnPrefix = function (a) {
                this.set$Value(13, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasPreferredExtnPrefix = function () {
                return this.has$Value(13);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.preferredExtnPrefixCount = function () {
                return this.count$Values(13);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearPreferredExtnPrefix = function () {
                this.clear$Field(13);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getNationalPrefixForParsing = function () {
                return this.get$Value(15);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getNationalPrefixForParsingOrDefault = function () {
                return this.get$ValueOrDefault(15);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setNationalPrefixForParsing = function (a) {
                this.set$Value(15, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasNationalPrefixForParsing = function () {
                return this.has$Value(15);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.nationalPrefixForParsingCount = function () {
                return this.count$Values(15);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearNationalPrefixForParsing = function () {
                this.clear$Field(15);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getNationalPrefixTransformRule = function () {
                return this.get$Value(16);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getNationalPrefixTransformRuleOrDefault = function () {
                return this.get$ValueOrDefault(16);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setNationalPrefixTransformRule = function (a) {
                this.set$Value(16, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasNationalPrefixTransformRule = function () {
                return this.has$Value(16);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.nationalPrefixTransformRuleCount = function () {
                return this.count$Values(16);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearNationalPrefixTransformRule = function () {
                this.clear$Field(16);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getSameMobileAndFixedLinePattern = function () {
                return this.get$Value(18);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getSameMobileAndFixedLinePatternOrDefault = function () {
                return this.get$ValueOrDefault(18);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setSameMobileAndFixedLinePattern = function (a) {
                this.set$Value(18, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasSameMobileAndFixedLinePattern = function () {
                return this.has$Value(18);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.sameMobileAndFixedLinePatternCount = function () {
                return this.count$Values(18);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearSameMobileAndFixedLinePattern = function () {
                this.clear$Field(18);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getNumberFormat = function (a) {
                return this.get$Value(19, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getNumberFormatOrDefault = function (a) {
                return this.get$ValueOrDefault(19, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.addNumberFormat = function (a) {
                this.add$Value(19, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.numberFormatArray = function () {
                return this.array$Values(19);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasNumberFormat = function () {
                return this.has$Value(19);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.numberFormatCount = function () {
                return this.count$Values(19);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearNumberFormat = function () {
                this.clear$Field(19);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getIntlNumberFormat = function (a) {
                return this.get$Value(20, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getIntlNumberFormatOrDefault = function (a) {
                return this.get$ValueOrDefault(20, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.addIntlNumberFormat = function (a) {
                this.add$Value(20, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.intlNumberFormatArray = function () {
                return this.array$Values(20);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasIntlNumberFormat = function () {
                return this.has$Value(20);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.intlNumberFormatCount = function () {
                return this.count$Values(20);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearIntlNumberFormat = function () {
                this.clear$Field(20);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getMainCountryForCode = function () {
                return this.get$Value(22);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getMainCountryForCodeOrDefault = function () {
                return this.get$ValueOrDefault(22);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setMainCountryForCode = function (a) {
                this.set$Value(22, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasMainCountryForCode = function () {
                return this.has$Value(22);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.mainCountryForCodeCount = function () {
                return this.count$Values(22);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearMainCountryForCode = function () {
                this.clear$Field(22);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getLeadingDigits = function () {
                return this.get$Value(23);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.getLeadingDigitsOrDefault = function () {
                return this.get$ValueOrDefault(23);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.setLeadingDigits = function (a) {
                this.set$Value(23, a);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.hasLeadingDigits = function () {
                return this.has$Value(23);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.leadingDigitsCount = function () {
                return this.count$Values(23);
              };
              i18n.phonenumbers.PhoneMetadata.prototype.clearLeadingDigits = function () {
                this.clear$Field(23);
              };
              i18n.phonenumbers.PhoneMetadataCollection = function () {
                goog.proto2.Message.call(this);
              };
              goog.inherits(i18n.phonenumbers.PhoneMetadataCollection, goog.proto2.Message);
              i18n.phonenumbers.PhoneMetadataCollection.descriptor_ = null;
              i18n.phonenumbers.PhoneMetadataCollection.prototype.getMetadata = function (a) {
                return this.get$Value(1, a);
              };
              i18n.phonenumbers.PhoneMetadataCollection.prototype.getMetadataOrDefault = function (a) {
                return this.get$ValueOrDefault(1, a);
              };
              i18n.phonenumbers.PhoneMetadataCollection.prototype.addMetadata = function (a) {
                this.add$Value(1, a);
              };
              i18n.phonenumbers.PhoneMetadataCollection.prototype.metadataArray = function () {
                return this.array$Values(1);
              };
              i18n.phonenumbers.PhoneMetadataCollection.prototype.hasMetadata = function () {
                return this.has$Value(1);
              };
              i18n.phonenumbers.PhoneMetadataCollection.prototype.metadataCount = function () {
                return this.count$Values(1);
              };
              i18n.phonenumbers.PhoneMetadataCollection.prototype.clearMetadata = function () {
                this.clear$Field(1);
              };
              i18n.phonenumbers.NumberFormat.prototype.getDescriptor = function () {
                var a = i18n.phonenumbers.NumberFormat.descriptor_;
                a ||
                  (i18n.phonenumbers.NumberFormat.descriptor_ = a =
                    goog.proto2.Message.createDescriptor(i18n.phonenumbers.NumberFormat, {
                      0: { name: 'NumberFormat', fullName: 'i18n.phonenumbers.NumberFormat' },
                      1: { name: 'pattern', required: !0, fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      2: { name: 'format', required: !0, fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      3: { name: 'leading_digits_pattern', repeated: !0, fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      4: { name: 'national_prefix_formatting_rule', fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      6: { name: 'national_prefix_optional_when_formatting', fieldType: goog.proto2.Message.FieldType.BOOL, defaultValue: !1, type: Boolean },
                      5: { name: 'domestic_carrier_code_formatting_rule', fieldType: goog.proto2.Message.FieldType.STRING, type: String }
                    }));
                return a;
              };
              i18n.phonenumbers.NumberFormat.getDescriptor = i18n.phonenumbers.NumberFormat.prototype.getDescriptor;
              i18n.phonenumbers.PhoneNumberDesc.prototype.getDescriptor = function () {
                var a = i18n.phonenumbers.PhoneNumberDesc.descriptor_;
                a ||
                  (i18n.phonenumbers.PhoneNumberDesc.descriptor_ = a =
                    goog.proto2.Message.createDescriptor(i18n.phonenumbers.PhoneNumberDesc, {
                      0: { name: 'PhoneNumberDesc', fullName: 'i18n.phonenumbers.PhoneNumberDesc' },
                      2: { name: 'national_number_pattern', fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      9: { name: 'possible_length', repeated: !0, fieldType: goog.proto2.Message.FieldType.INT32, type: Number },
                      10: { name: 'possible_length_local_only', repeated: !0, fieldType: goog.proto2.Message.FieldType.INT32, type: Number },
                      6: { name: 'example_number', fieldType: goog.proto2.Message.FieldType.STRING, type: String }
                    }));
                return a;
              };
              i18n.phonenumbers.PhoneNumberDesc.getDescriptor = i18n.phonenumbers.PhoneNumberDesc.prototype.getDescriptor;
              i18n.phonenumbers.PhoneMetadata.prototype.getDescriptor = function () {
                var a = i18n.phonenumbers.PhoneMetadata.descriptor_;
                a ||
                  (i18n.phonenumbers.PhoneMetadata.descriptor_ = a =
                    goog.proto2.Message.createDescriptor(i18n.phonenumbers.PhoneMetadata, {
                      0: { name: 'PhoneMetadata', fullName: 'i18n.phonenumbers.PhoneMetadata' },
                      1: { name: 'general_desc', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      2: { name: 'fixed_line', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      3: { name: 'mobile', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      4: { name: 'toll_free', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      5: { name: 'premium_rate', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      6: { name: 'shared_cost', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      7: { name: 'personal_number', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      8: { name: 'voip', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      21: { name: 'pager', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      25: { name: 'uan', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      27: { name: 'emergency', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      28: { name: 'voicemail', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      29: { name: 'short_code', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      30: { name: 'standard_rate', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      31: { name: 'carrier_specific', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      33: { name: 'sms_services', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      24: { name: 'no_international_dialling', fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneNumberDesc },
                      9: { name: 'id', required: !0, fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      10: { name: 'country_code', fieldType: goog.proto2.Message.FieldType.INT32, type: Number },
                      11: { name: 'international_prefix', fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      17: { name: 'preferred_international_prefix', fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      12: { name: 'national_prefix', fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      13: { name: 'preferred_extn_prefix', fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      15: { name: 'national_prefix_for_parsing', fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      16: { name: 'national_prefix_transform_rule', fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      18: { name: 'same_mobile_and_fixed_line_pattern', fieldType: goog.proto2.Message.FieldType.BOOL, defaultValue: !1, type: Boolean },
                      19: { name: 'number_format', repeated: !0, fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.NumberFormat },
                      20: { name: 'intl_number_format', repeated: !0, fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.NumberFormat },
                      22: { name: 'main_country_for_code', fieldType: goog.proto2.Message.FieldType.BOOL, defaultValue: !1, type: Boolean },
                      23: { name: 'leading_digits', fieldType: goog.proto2.Message.FieldType.STRING, type: String }
                    }));
                return a;
              };
              i18n.phonenumbers.PhoneMetadata.getDescriptor = i18n.phonenumbers.PhoneMetadata.prototype.getDescriptor;
              i18n.phonenumbers.PhoneMetadataCollection.prototype.getDescriptor = function () {
                var a = i18n.phonenumbers.PhoneMetadataCollection.descriptor_;
                a ||
                  (i18n.phonenumbers.PhoneMetadataCollection.descriptor_ = a =
                    goog.proto2.Message.createDescriptor(i18n.phonenumbers.PhoneMetadataCollection, {
                      0: { name: 'PhoneMetadataCollection', fullName: 'i18n.phonenumbers.PhoneMetadataCollection' },
                      1: { name: 'metadata', repeated: !0, fieldType: goog.proto2.Message.FieldType.MESSAGE, type: i18n.phonenumbers.PhoneMetadata }
                    }));
                return a;
              };
              i18n.phonenumbers.PhoneMetadataCollection.getDescriptor = i18n.phonenumbers.PhoneMetadataCollection.prototype.getDescriptor;
              i18n.phonenumbers.PhoneNumber = function () {
                goog.proto2.Message.call(this);
              };
              goog.inherits(i18n.phonenumbers.PhoneNumber, goog.proto2.Message);
              i18n.phonenumbers.PhoneNumber.descriptor_ = null;
              i18n.phonenumbers.PhoneNumber.prototype.getCountryCode = function () {
                return this.get$Value(1);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getCountryCodeOrDefault = function () {
                return this.get$ValueOrDefault(1);
              };
              i18n.phonenumbers.PhoneNumber.prototype.setCountryCode = function (a) {
                this.set$Value(1, a);
              };
              i18n.phonenumbers.PhoneNumber.prototype.hasCountryCode = function () {
                return this.has$Value(1);
              };
              i18n.phonenumbers.PhoneNumber.prototype.countryCodeCount = function () {
                return this.count$Values(1);
              };
              i18n.phonenumbers.PhoneNumber.prototype.clearCountryCode = function () {
                this.clear$Field(1);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getNationalNumber = function () {
                return this.get$Value(2);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getNationalNumberOrDefault = function () {
                return this.get$ValueOrDefault(2);
              };
              i18n.phonenumbers.PhoneNumber.prototype.setNationalNumber = function (a) {
                this.set$Value(2, a);
              };
              i18n.phonenumbers.PhoneNumber.prototype.hasNationalNumber = function () {
                return this.has$Value(2);
              };
              i18n.phonenumbers.PhoneNumber.prototype.nationalNumberCount = function () {
                return this.count$Values(2);
              };
              i18n.phonenumbers.PhoneNumber.prototype.clearNationalNumber = function () {
                this.clear$Field(2);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getExtension = function () {
                return this.get$Value(3);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getExtensionOrDefault = function () {
                return this.get$ValueOrDefault(3);
              };
              i18n.phonenumbers.PhoneNumber.prototype.setExtension = function (a) {
                this.set$Value(3, a);
              };
              i18n.phonenumbers.PhoneNumber.prototype.hasExtension = function () {
                return this.has$Value(3);
              };
              i18n.phonenumbers.PhoneNumber.prototype.extensionCount = function () {
                return this.count$Values(3);
              };
              i18n.phonenumbers.PhoneNumber.prototype.clearExtension = function () {
                this.clear$Field(3);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getItalianLeadingZero = function () {
                return this.get$Value(4);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getItalianLeadingZeroOrDefault = function () {
                return this.get$ValueOrDefault(4);
              };
              i18n.phonenumbers.PhoneNumber.prototype.setItalianLeadingZero = function (a) {
                this.set$Value(4, a);
              };
              i18n.phonenumbers.PhoneNumber.prototype.hasItalianLeadingZero = function () {
                return this.has$Value(4);
              };
              i18n.phonenumbers.PhoneNumber.prototype.italianLeadingZeroCount = function () {
                return this.count$Values(4);
              };
              i18n.phonenumbers.PhoneNumber.prototype.clearItalianLeadingZero = function () {
                this.clear$Field(4);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getNumberOfLeadingZeros = function () {
                return this.get$Value(8);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getNumberOfLeadingZerosOrDefault = function () {
                return this.get$ValueOrDefault(8);
              };
              i18n.phonenumbers.PhoneNumber.prototype.setNumberOfLeadingZeros = function (a) {
                this.set$Value(8, a);
              };
              i18n.phonenumbers.PhoneNumber.prototype.hasNumberOfLeadingZeros = function () {
                return this.has$Value(8);
              };
              i18n.phonenumbers.PhoneNumber.prototype.numberOfLeadingZerosCount = function () {
                return this.count$Values(8);
              };
              i18n.phonenumbers.PhoneNumber.prototype.clearNumberOfLeadingZeros = function () {
                this.clear$Field(8);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getRawInput = function () {
                return this.get$Value(5);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getRawInputOrDefault = function () {
                return this.get$ValueOrDefault(5);
              };
              i18n.phonenumbers.PhoneNumber.prototype.setRawInput = function (a) {
                this.set$Value(5, a);
              };
              i18n.phonenumbers.PhoneNumber.prototype.hasRawInput = function () {
                return this.has$Value(5);
              };
              i18n.phonenumbers.PhoneNumber.prototype.rawInputCount = function () {
                return this.count$Values(5);
              };
              i18n.phonenumbers.PhoneNumber.prototype.clearRawInput = function () {
                this.clear$Field(5);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getCountryCodeSource = function () {
                return this.get$Value(6);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getCountryCodeSourceOrDefault = function () {
                return this.get$ValueOrDefault(6);
              };
              i18n.phonenumbers.PhoneNumber.prototype.setCountryCodeSource = function (a) {
                this.set$Value(6, a);
              };
              i18n.phonenumbers.PhoneNumber.prototype.hasCountryCodeSource = function () {
                return this.has$Value(6);
              };
              i18n.phonenumbers.PhoneNumber.prototype.countryCodeSourceCount = function () {
                return this.count$Values(6);
              };
              i18n.phonenumbers.PhoneNumber.prototype.clearCountryCodeSource = function () {
                this.clear$Field(6);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getPreferredDomesticCarrierCode = function () {
                return this.get$Value(7);
              };
              i18n.phonenumbers.PhoneNumber.prototype.getPreferredDomesticCarrierCodeOrDefault = function () {
                return this.get$ValueOrDefault(7);
              };
              i18n.phonenumbers.PhoneNumber.prototype.setPreferredDomesticCarrierCode = function (a) {
                this.set$Value(7, a);
              };
              i18n.phonenumbers.PhoneNumber.prototype.hasPreferredDomesticCarrierCode = function () {
                return this.has$Value(7);
              };
              i18n.phonenumbers.PhoneNumber.prototype.preferredDomesticCarrierCodeCount = function () {
                return this.count$Values(7);
              };
              i18n.phonenumbers.PhoneNumber.prototype.clearPreferredDomesticCarrierCode = function () {
                this.clear$Field(7);
              };
              i18n.phonenumbers.PhoneNumber.CountryCodeSource = {
                UNSPECIFIED: 0,
                FROM_NUMBER_WITH_PLUS_SIGN: 1,
                FROM_NUMBER_WITH_IDD: 5,
                FROM_NUMBER_WITHOUT_PLUS_SIGN: 10,
                FROM_DEFAULT_COUNTRY: 20
              };
              i18n.phonenumbers.PhoneNumber.prototype.getDescriptor = function () {
                var a = i18n.phonenumbers.PhoneNumber.descriptor_;
                a ||
                  (i18n.phonenumbers.PhoneNumber.descriptor_ = a =
                    goog.proto2.Message.createDescriptor(i18n.phonenumbers.PhoneNumber, {
                      0: { name: 'PhoneNumber', fullName: 'i18n.phonenumbers.PhoneNumber' },
                      1: { name: 'country_code', required: !0, fieldType: goog.proto2.Message.FieldType.INT32, type: Number },
                      2: { name: 'national_number', required: !0, fieldType: goog.proto2.Message.FieldType.UINT64, type: Number },
                      3: { name: 'extension', fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      4: { name: 'italian_leading_zero', fieldType: goog.proto2.Message.FieldType.BOOL, type: Boolean },
                      8: { name: 'number_of_leading_zeros', fieldType: goog.proto2.Message.FieldType.INT32, defaultValue: 1, type: Number },
                      5: { name: 'raw_input', fieldType: goog.proto2.Message.FieldType.STRING, type: String },
                      6: {
                        name: 'country_code_source',
                        fieldType: goog.proto2.Message.FieldType.ENUM,
                        defaultValue: i18n.phonenumbers.PhoneNumber.CountryCodeSource.UNSPECIFIED,
                        type: i18n.phonenumbers.PhoneNumber.CountryCodeSource
                      },
                      7: { name: 'preferred_domestic_carrier_code', fieldType: goog.proto2.Message.FieldType.STRING, type: String }
                    }));
                return a;
              };
              i18n.phonenumbers.PhoneNumber.ctor = i18n.phonenumbers.PhoneNumber;
              i18n.phonenumbers.PhoneNumber.ctor.getDescriptor = i18n.phonenumbers.PhoneNumber.prototype.getDescriptor; /*

 Copyright (C) 2010 The Libphonenumber Authors

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

 http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.
*/
              i18n.phonenumbers.metadata = {};
              i18n.phonenumbers.metadata.countryCodeToRegionCodeMap = {
                1: 'US AG AI AS BB BM BS CA DM DO GD GU JM KN KY LC MP MS PR SX TC TT VC VG VI'.split(' '),
                7: ['RU', 'KZ'],
                20: ['EG'],
                27: ['ZA'],
                30: ['GR'],
                31: ['NL'],
                32: ['BE'],
                33: ['FR'],
                34: ['ES'],
                36: ['HU'],
                39: ['IT', 'VA'],
                40: ['RO'],
                41: ['CH'],
                43: ['AT'],
                44: ['GB', 'GG', 'IM', 'JE'],
                45: ['DK'],
                46: ['SE'],
                47: ['NO', 'SJ'],
                48: ['PL'],
                49: ['DE'],
                51: ['PE'],
                52: ['MX'],
                53: ['CU'],
                54: ['AR'],
                55: ['BR'],
                56: ['CL'],
                57: ['CO'],
                58: ['VE'],
                60: ['MY'],
                61: ['AU', 'CC', 'CX'],
                62: ['ID'],
                63: ['PH'],
                64: ['NZ'],
                65: ['SG'],
                66: ['TH'],
                81: ['JP'],
                82: ['KR'],
                84: ['VN'],
                86: ['CN'],
                90: ['TR'],
                91: ['IN'],
                92: ['PK'],
                93: ['AF'],
                94: ['LK'],
                95: ['MM'],
                98: ['IR'],
                211: ['SS'],
                212: ['MA', 'EH'],
                213: ['DZ'],
                216: ['TN'],
                218: ['LY'],
                220: ['GM'],
                221: ['SN'],
                222: ['MR'],
                223: ['ML'],
                224: ['GN'],
                225: ['CI'],
                226: ['BF'],
                227: ['NE'],
                228: ['TG'],
                229: ['BJ'],
                230: ['MU'],
                231: ['LR'],
                232: ['SL'],
                233: ['GH'],
                234: ['NG'],
                235: ['TD'],
                236: ['CF'],
                237: ['CM'],
                238: ['CV'],
                239: ['ST'],
                240: ['GQ'],
                241: ['GA'],
                242: ['CG'],
                243: ['CD'],
                244: ['AO'],
                245: ['GW'],
                246: ['IO'],
                247: ['AC'],
                248: ['SC'],
                249: ['SD'],
                250: ['RW'],
                251: ['ET'],
                252: ['SO'],
                253: ['DJ'],
                254: ['KE'],
                255: ['TZ'],
                256: ['UG'],
                257: ['BI'],
                258: ['MZ'],
                260: ['ZM'],
                261: ['MG'],
                262: ['RE', 'YT'],
                263: ['ZW'],
                264: ['NA'],
                265: ['MW'],
                266: ['LS'],
                267: ['BW'],
                268: ['SZ'],
                269: ['KM'],
                290: ['SH', 'TA'],
                291: ['ER'],
                297: ['AW'],
                298: ['FO'],
                299: ['GL'],
                350: ['GI'],
                351: ['PT'],
                352: ['LU'],
                353: ['IE'],
                354: ['IS'],
                355: ['AL'],
                356: ['MT'],
                357: ['CY'],
                358: ['FI', 'AX'],
                359: ['BG'],
                370: ['LT'],
                371: ['LV'],
                372: ['EE'],
                373: ['MD'],
                374: ['AM'],
                375: ['BY'],
                376: ['AD'],
                377: ['MC'],
                378: ['SM'],
                380: ['UA'],
                381: ['RS'],
                382: ['ME'],
                383: ['XK'],
                385: ['HR'],
                386: ['SI'],
                387: ['BA'],
                389: ['MK'],
                420: ['CZ'],
                421: ['SK'],
                423: ['LI'],
                500: ['FK'],
                501: ['BZ'],
                502: ['GT'],
                503: ['SV'],
                504: ['HN'],
                505: ['NI'],
                506: ['CR'],
                507: ['PA'],
                508: ['PM'],
                509: ['HT'],
                590: ['GP', 'BL', 'MF'],
                591: ['BO'],
                592: ['GY'],
                593: ['EC'],
                594: ['GF'],
                595: ['PY'],
                596: ['MQ'],
                597: ['SR'],
                598: ['UY'],
                599: ['CW', 'BQ'],
                670: ['TL'],
                672: ['NF'],
                673: ['BN'],
                674: ['NR'],
                675: ['PG'],
                676: ['TO'],
                677: ['SB'],
                678: ['VU'],
                679: ['FJ'],
                680: ['PW'],
                681: ['WF'],
                682: ['CK'],
                683: ['NU'],
                685: ['WS'],
                686: ['KI'],
                687: ['NC'],
                688: ['TV'],
                689: ['PF'],
                690: ['TK'],
                691: ['FM'],
                692: ['MH'],
                800: ['001'],
                808: ['001'],
                850: ['KP'],
                852: ['HK'],
                853: ['MO'],
                855: ['KH'],
                856: ['LA'],
                870: ['001'],
                878: ['001'],
                880: ['BD'],
                881: ['001'],
                882: ['001'],
                883: ['001'],
                886: ['TW'],
                888: ['001'],
                960: ['MV'],
                961: ['LB'],
                962: ['JO'],
                963: ['SY'],
                964: ['IQ'],
                965: ['KW'],
                966: ['SA'],
                967: ['YE'],
                968: ['OM'],
                970: ['PS'],
                971: ['AE'],
                972: ['IL'],
                973: ['BH'],
                974: ['QA'],
                975: ['BT'],
                976: ['MN'],
                977: ['NP'],
                979: ['001'],
                992: ['TJ'],
                993: ['TM'],
                994: ['AZ'],
                995: ['GE'],
                996: ['KG'],
                998: ['UZ']
              };
              i18n.phonenumbers.metadata.countryToMetadata = {
                AC: [
                  ,
                  [, , '(?:[01589]\\d|[46])\\d{4}', , , , , , , [5, 6]],
                  [, , '6[2-467]\\d{3}', , , , '62889', , , [5]],
                  [, , '4\\d{4}', , , , '40123', , , [5]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'AC',
                  247,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '(?:0[1-9]|[1589]\\d)\\d{4}', , , , '542011', , , [6]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AD: [
                  ,
                  [, , '(?:1|6\\d)\\d{7}|[135-9]\\d{5}', , , , , , , [6, 8, 9]],
                  [, , '[78]\\d{5}', , , , '712345', , , [6]],
                  [, , '690\\d{6}|[356]\\d{5}', , , , '312345', , , [6, 9]],
                  [, , '180[02]\\d{4}', , , , '18001234', , , [8]],
                  [, , '[19]\\d{5}', , , , '912345', , , [6]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'AD',
                  376,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3})', '$1 $2', ['[135-9]']],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['1']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['6']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '1800\\d{4}', , , , , , , [8]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AE: [
                  ,
                  [, , '(?:[4-7]\\d|9[0-689])\\d{7}|800\\d{2,9}|[2-4679]\\d{7}', , , , , , , [5, 6, 7, 8, 9, 10, 11, 12]],
                  [, , '[2-4679][2-8]\\d{6}', , , , '22345678', , , [8], [7]],
                  [, , '5[024-68]\\d{7}', , , , '501234567', , , [9]],
                  [, , '400\\d{6}|800\\d{2,9}', , , , '800123456'],
                  [, , '900[02]\\d{5}', , , , '900234567', , , [9]],
                  [, , '700[05]\\d{5}', , , , '700012345', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'AE',
                  971,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{2,9})', '$1 $2', ['60|8']],
                    [, '(\\d)(\\d{3})(\\d{4})', '$1 $2 $3', ['[236]|[479][2-8]'], '0$1'],
                    [, '(\\d{3})(\\d)(\\d{5})', '$1 $2 $3', ['[479]']],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['5'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '600[25]\\d{5}', , , , '600212345', , , [9]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AF: [
                  ,
                  [, , '[2-7]\\d{8}', , , , , , , [9], [7]],
                  [, , '(?:[25][0-8]|[34][0-4]|6[0-5])[2-9]\\d{6}', , , , '234567890', , , , [7]],
                  [, , '7\\d{8}', , , , '701234567', , , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'AF',
                  93,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['[1-9]']],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[2-7]'], '0$1']
                  ],
                  [[, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[2-7]'], '0$1']],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AG: [
                  ,
                  [, , '(?:268|[58]\\d\\d|900)\\d{7}', , , , , , , [10], [7]],
                  [, , '268(?:4(?:6[0-38]|84)|56[0-2])\\d{4}', , , , '2684601234', , , , [7]],
                  [, , '268(?:464|7(?:1[3-9]|[28]\\d|3[0246]|64|7[0-689]))\\d{4}', , , , '2684641234', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , '26848[01]\\d{4}', , , , '2684801234', , , , [7]],
                  'AG',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([457]\\d{6})$|1',
                  '268$1',
                  ,
                  ,
                  ,
                  ,
                  [, , '26840[69]\\d{4}', , , , '2684061234', , , , [7]],
                  ,
                  '268',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AI: [
                  ,
                  [, , '(?:264|[58]\\d\\d|900)\\d{7}', , , , , , , [10], [7]],
                  [, , '264(?:292|4(?:6[12]|9[78]))\\d{4}', , , , '2644612345', , , , [7]],
                  [, , '264(?:235|4(?:69|76)|5(?:3[6-9]|8[1-4])|7(?:29|72))\\d{4}', , , , '2642351234', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'AI',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2457]\\d{6})$|1',
                  '264$1',
                  ,
                  ,
                  ,
                  ,
                  [, , '264724\\d{4}', , , , '2647241234', , , , [7]],
                  ,
                  '264',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AL: [
                  ,
                  [, , '(?:700\\d\\d|900)\\d{3}|8\\d{5,7}|(?:[2-5]|6\\d)\\d{7}', , , , , , , [6, 7, 8, 9], [5]],
                  [
                    ,
                    ,
                    '4505[0-2]\\d{3}|(?:[2358][16-9]\\d[2-9]|4410)\\d{4}|(?:[2358][2-5][2-9]|4(?:[2-57-9][2-9]|6\\d))\\d{5}',
                    ,
                    ,
                    ,
                    '22345678',
                    ,
                    ,
                    [8],
                    [5, 6, 7]
                  ],
                  [, , '6(?:[78][2-9]|9\\d)\\d{6}', , , , '672123456', , , [9]],
                  [, , '800\\d{4}', , , , '8001234', , , [7]],
                  [, , '900[1-9]\\d\\d', , , , '900123', , , [6]],
                  [, , '808[1-9]\\d\\d', , , , '808123', , , [6]],
                  [, , '700[2-9]\\d{4}', , , , '70021234', , , [8]],
                  [, , , , , , , , , [-1]],
                  'AL',
                  355,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3,4})', '$1 $2', ['80|9'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{4})', '$1 $2 $3', ['4[2-6]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['[2358][2-5]|4'], '0$1'],
                    [, '(\\d{3})(\\d{5})', '$1 $2', ['[23578]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['6'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AM: [
                  ,
                  [, , '(?:[1-489]\\d|55|60|77)\\d{6}', , , , , , , [8], [5, 6]],
                  [, , '(?:(?:1[0-25]|47)\\d|2(?:2[2-46]|3[1-8]|4[2-69]|5[2-7]|6[1-9]|8[1-7])|3[12]2)\\d{5}', , , , '10123456', , , , [5, 6]],
                  [, , '(?:33|4[1349]|55|77|88|9[13-9])\\d{6}', , , , '77123456'],
                  [, , '800\\d{5}', , , , '80012345'],
                  [, , '90[016]\\d{5}', , , , '90012345'],
                  [, , '80[1-4]\\d{5}', , , , '80112345'],
                  [, , , , , , , , , [-1]],
                  [, , '60(?:2[78]|3[5-9]|4[02-9]|5[0-46-9]|[6-8]\\d|9[0-2])\\d{4}', , , , '60271234'],
                  'AM',
                  374,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{2})(\\d{3})', '$1 $2 $3', ['[89]0'], '0 $1'],
                    [, '(\\d{3})(\\d{5})', '$1 $2', ['2|3[12]'], '(0$1)'],
                    [, '(\\d{2})(\\d{6})', '$1 $2', ['1|47'], '(0$1)'],
                    [, '(\\d{2})(\\d{6})', '$1 $2', ['[3-9]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AO: [
                  ,
                  [, , '[29]\\d{8}', , , , , , , [9]],
                  [, , '2\\d(?:[0134][25-9]|[25-9]\\d)\\d{5}', , , , '222123456'],
                  [, , '9[1-79]\\d{7}', , , , '923123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'AO',
                  244,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[29]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AR: [
                  ,
                  [, , '(?:11|[89]\\d\\d)\\d{8}|[2368]\\d{9}', , , , , , , [10, 11], [6, 7, 8]],
                  [
                    ,
                    ,
                    '3(?:7(?:1[15]|81)|8(?:21|4[16]|69|9[12]))[46]\\d{5}|(?:2(?:2(?:2[59]|44|52)|3(?:26|44)|47[35]|9(?:[07]2|2[26]|34|46))|3327)[45]\\d{5}|(?:2(?:657|9(?:54|66))|3(?:48[27]|7(?:55|77)|8(?:65|78)))[2-8]\\d{5}|(?:2(?:284|3(?:02|23)|477|622|920)|3(?:4(?:46|89|92)|541))[2-7]\\d{5}|(?:(?:11[1-8]|670)\\d|2(?:2(?:0[45]|1[2-6]|3[3-6])|3(?:[06]4|7[45])|494|6(?:04|1[2-8]|[36][45]|4[3-6])|80[45]|9(?:[17][4-6]|[48][45]|9[3-6]))|3(?:364|4(?:1[2-8]|[25][4-6]|3[3-6]|84)|5(?:1[2-9]|[38][4-6])|6(?:2[45]|44)|7[069][45]|8(?:0[45]|1[2-7]|3[4-6]|5[3-6]|7[2-6]|8[3-68])))\\d{6}|(?:2(?:2(?:62|81)|320|9(?:42|83))|3(?:329|4(?:62|7[16])|5(?:43|64)|7(?:18|5[17])))[2-6]\\d{5}|2(?:2(?:21|4[23]|6[145]|7[1-4]|8[356]|9[267])|3(?:16|3[13-8]|43|5[346-8]|9[3-5])|6(?:2[46]|4[78]|5[1568])|9(?:03|2[1457-9]|3[1356]|4[08]|[56][23]|82))4\\d{5}|(?:2(?:257|3(?:24|46|92)|9(?:01|23|64))|3(?:4(?:42|64)|5(?:25|37|4[47]|71)|7(?:35|72)|825))[3-6]\\d{5}|(?:2(?:2(?:02|2[3467]|4[156]|5[45]|6[6-8]|91)|3(?:1[47]|25|[45][25]|96)|47[48]|625|932)|3(?:38[2578]|4(?:0[0-24-9]|3[78]|4[457]|58|6[035-9]|72|83|9[136-8])|5(?:2[124]|[368][23]|4[2689]|7[2-6])|7(?:16|2[15]|3[14]|4[13]|5[468]|7[3-5]|8[26])|8(?:2[67]|3[278]|4[3-5]|5[78]|6[1-378]|[78]7|94)))[4-6]\\d{5}',
                    ,
                    ,
                    ,
                    '1123456789',
                    ,
                    ,
                    [10],
                    [6, 7, 8]
                  ],
                  [
                    ,
                    ,
                    '93(?:7(?:1[15]|81)|8(?:21|4[16]|69|9[12]))[46]\\d{5}|9(?:2(?:2(?:2[59]|44|52)|3(?:26|44)|47[35]|9(?:[07]2|2[26]|34|46))|3327)[45]\\d{5}|9(?:2(?:657|9(?:54|66))|3(?:48[27]|7(?:55|77)|8(?:65|78)))[2-8]\\d{5}|9(?:2(?:284|3(?:02|23)|477|622|920)|3(?:4(?:46|89|92)|541))[2-7]\\d{5}|(?:675\\d|9(?:11[1-8]\\d|2(?:2(?:0[45]|1[2-6]|3[3-6])|3(?:[06]4|7[45])|494|6(?:04|1[2-8]|[36][45]|4[3-6])|80[45]|9(?:[17][4-6]|[48][45]|9[3-6]))|3(?:364|4(?:1[2-8]|[25][4-6]|3[3-6]|84)|5(?:1[2-9]|[38][4-6])|6(?:2[45]|44)|7[069][45]|8(?:0[45]|1[2-7]|3[4-6]|5[3-6]|7[2-6]|8[3-68]))))\\d{6}|9(?:2(?:2(?:62|81)|320|9(?:42|83))|3(?:329|4(?:62|7[16])|5(?:43|64)|7(?:18|5[17])))[2-6]\\d{5}|92(?:2(?:21|4[23]|6[145]|7[1-4]|8[356]|9[267])|3(?:16|3[13-8]|43|5[346-8]|9[3-5])|6(?:2[46]|4[78]|5[1568])|9(?:03|2[1457-9]|3[1356]|4[08]|[56][23]|82))4\\d{5}|9(?:2(?:257|3(?:24|46|92)|9(?:01|23|64))|3(?:4(?:42|64)|5(?:25|37|4[47]|71)|7(?:35|72)|825))[3-6]\\d{5}|9(?:2(?:2(?:02|2[3467]|4[156]|5[45]|6[6-8]|91)|3(?:1[47]|25|[45][25]|96)|47[48]|625|932)|3(?:38[2578]|4(?:0[0-24-9]|3[78]|4[457]|58|6[035-9]|72|83|9[136-8])|5(?:2[124]|[368][23]|4[2689]|7[2-6])|7(?:16|2[15]|3[14]|4[13]|5[468]|7[3-5]|8[26])|8(?:2[67]|3[278]|4[3-5]|5[78]|6[1-378]|[78]7|94)))[4-6]\\d{5}',
                    ,
                    ,
                    ,
                    '91123456789',
                    ,
                    ,
                    ,
                    [6, 7, 8]
                  ],
                  [, , '800\\d{7,8}', , , , '8001234567'],
                  [, , '60[04579]\\d{7}', , , , '6001234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'AR',
                  54,
                  '00',
                  '0',
                  ,
                  ,
                  '0?(?:(11|2(?:2(?:02?|[13]|2[13-79]|4[1-6]|5[2457]|6[124-8]|7[1-4]|8[13-6]|9[1267])|3(?:02?|1[467]|2[03-6]|3[13-8]|[49][2-6]|5[2-8]|[67])|4(?:7[3-578]|9)|6(?:[0136]|2[24-6]|4[6-8]?|5[15-8])|80|9(?:0[1-3]|[19]|2\\d|3[1-6]|4[02568]?|5[2-4]|6[2-46]|72?|8[23]?))|3(?:3(?:2[79]|6|8[2578])|4(?:0[0-24-9]|[12]|3[5-8]?|4[24-7]|5[4-68]?|6[02-9]|7[126]|8[2379]?|9[1-36-8])|5(?:1|2[1245]|3[237]?|4[1-46-9]|6[2-4]|7[1-6]|8[2-5]?)|6[24]|7(?:[069]|1[1568]|2[15]|3[145]|4[13]|5[14-8]|7[2-57]|8[126])|8(?:[01]|2[15-7]|3[2578]?|4[13-6]|5[4-8]?|6[1-357-9]|7[36-8]?|8[5-8]?|9[124])))15)?',
                  '9$1',
                  ,
                  ,
                  [
                    [, '(\\d{3})', '$1', ['0|1(?:0[0-35-7]|1[02-5]|2[015]|3[47]|4[478])|911']],
                    [, '(\\d{2})(\\d{4})', '$1-$2', ['[1-9]']],
                    [, '(\\d{3})(\\d{4})', '$1-$2', ['[2-9]']],
                    [, '(\\d{4})(\\d{4})', '$1-$2', ['[1-8]']],
                    [
                      ,
                      '(\\d{4})(\\d{2})(\\d{4})',
                      '$1 $2-$3',
                      [
                        '2(?:2[024-9]|3[0-59]|47|6[245]|9[02-8])|3(?:3[28]|4[03-9]|5[2-46-8]|7[1-578]|8[2-9])',
                        '2(?:[23]02|6(?:[25]|4[6-8])|9(?:[02356]|4[02568]|72|8[23]))|3(?:3[28]|4(?:[04679]|3[5-8]|5[4-68]|8[2379])|5(?:[2467]|3[237]|8[2-5])|7[1-578]|8(?:[2469]|3[2578]|5[4-8]|7[36-8]|8[5-8]))|2(?:2[24-9]|3[1-59]|47)',
                        '2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3[78]|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8[23])|7[1-578]|8(?:[2469]|3[278]|5[56][46]|86[3-6]))|2(?:2[24-9]|3[1-59]|47)|38(?:[58][78]|7[378])|3(?:4[35][56]|58[45]|8(?:[38]5|54|76))[4-6]',
                        '2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3(?:5(?:4[0-25689]|[56])|[78])|58|8[2379])|5(?:[2467]|3[237]|8(?:[23]|4(?:[45]|60)|5(?:4[0-39]|5|64)))|7[1-578]|8(?:[2469]|3[278]|54(?:4|5[13-7]|6[89])|86[3-6]))|2(?:2[24-9]|3[1-59]|47)|38(?:[58][78]|7[378])|3(?:454|85[56])[46]|3(?:4(?:36|5[56])|8(?:[38]5|76))[4-6]'
                      ],
                      '0$1',
                      ,
                      1
                    ],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1 $2-$3', ['1'], '0$1', , 1],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1-$2-$3', ['[68]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2-$3', ['[23]'], '0$1', , 1],
                    [
                      ,
                      '(\\d)(\\d{4})(\\d{2})(\\d{4})',
                      '$2 15-$3-$4',
                      [
                        '9(?:2[2-469]|3[3-578])',
                        '9(?:2(?:2[024-9]|3[0-59]|47|6[245]|9[02-8])|3(?:3[28]|4[03-9]|5[2-46-8]|7[1-578]|8[2-9]))',
                        '9(?:2(?:[23]02|6(?:[25]|4[6-8])|9(?:[02356]|4[02568]|72|8[23]))|3(?:3[28]|4(?:[04679]|3[5-8]|5[4-68]|8[2379])|5(?:[2467]|3[237]|8[2-5])|7[1-578]|8(?:[2469]|3[2578]|5[4-8]|7[36-8]|8[5-8])))|92(?:2[24-9]|3[1-59]|47)',
                        '9(?:2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3[78]|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8[23])|7[1-578]|8(?:[2469]|3[278]|5(?:[56][46]|[78])|7[378]|8(?:6[3-6]|[78]))))|92(?:2[24-9]|3[1-59]|47)|93(?:4[35][56]|58[45]|8(?:[38]5|54|76))[4-6]',
                        '9(?:2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3(?:5(?:4[0-25689]|[56])|[78])|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8(?:[23]|4(?:[45]|60)|5(?:4[0-39]|5|64)))|7[1-578]|8(?:[2469]|3[278]|5(?:4(?:4|5[13-7]|6[89])|[56][46]|[78])|7[378]|8(?:6[3-6]|[78]))))|92(?:2[24-9]|3[1-59]|47)|93(?:4(?:36|5[56])|8(?:[38]5|76))[4-6]'
                      ],
                      '0$1'
                    ],
                    [, '(\\d)(\\d{2})(\\d{4})(\\d{4})', '$2 15-$3-$4', ['91'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{5})', '$1-$2-$3', ['8'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{3})(\\d{4})', '$2 15-$3-$4', ['9'], '0$1']
                  ],
                  [
                    [
                      ,
                      '(\\d{4})(\\d{2})(\\d{4})',
                      '$1 $2-$3',
                      [
                        '2(?:2[024-9]|3[0-59]|47|6[245]|9[02-8])|3(?:3[28]|4[03-9]|5[2-46-8]|7[1-578]|8[2-9])',
                        '2(?:[23]02|6(?:[25]|4[6-8])|9(?:[02356]|4[02568]|72|8[23]))|3(?:3[28]|4(?:[04679]|3[5-8]|5[4-68]|8[2379])|5(?:[2467]|3[237]|8[2-5])|7[1-578]|8(?:[2469]|3[2578]|5[4-8]|7[36-8]|8[5-8]))|2(?:2[24-9]|3[1-59]|47)',
                        '2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3[78]|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8[23])|7[1-578]|8(?:[2469]|3[278]|5[56][46]|86[3-6]))|2(?:2[24-9]|3[1-59]|47)|38(?:[58][78]|7[378])|3(?:4[35][56]|58[45]|8(?:[38]5|54|76))[4-6]',
                        '2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3(?:5(?:4[0-25689]|[56])|[78])|58|8[2379])|5(?:[2467]|3[237]|8(?:[23]|4(?:[45]|60)|5(?:4[0-39]|5|64)))|7[1-578]|8(?:[2469]|3[278]|54(?:4|5[13-7]|6[89])|86[3-6]))|2(?:2[24-9]|3[1-59]|47)|38(?:[58][78]|7[378])|3(?:454|85[56])[46]|3(?:4(?:36|5[56])|8(?:[38]5|76))[4-6]'
                      ],
                      '0$1',
                      ,
                      1
                    ],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1 $2-$3', ['1'], '0$1', , 1],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1-$2-$3', ['[68]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2-$3', ['[23]'], '0$1', , 1],
                    [
                      ,
                      '(\\d)(\\d{4})(\\d{2})(\\d{4})',
                      '$1 $2 $3-$4',
                      [
                        '9(?:2[2-469]|3[3-578])',
                        '9(?:2(?:2[024-9]|3[0-59]|47|6[245]|9[02-8])|3(?:3[28]|4[03-9]|5[2-46-8]|7[1-578]|8[2-9]))',
                        '9(?:2(?:[23]02|6(?:[25]|4[6-8])|9(?:[02356]|4[02568]|72|8[23]))|3(?:3[28]|4(?:[04679]|3[5-8]|5[4-68]|8[2379])|5(?:[2467]|3[237]|8[2-5])|7[1-578]|8(?:[2469]|3[2578]|5[4-8]|7[36-8]|8[5-8])))|92(?:2[24-9]|3[1-59]|47)',
                        '9(?:2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3[78]|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8[23])|7[1-578]|8(?:[2469]|3[278]|5(?:[56][46]|[78])|7[378]|8(?:6[3-6]|[78]))))|92(?:2[24-9]|3[1-59]|47)|93(?:4[35][56]|58[45]|8(?:[38]5|54|76))[4-6]',
                        '9(?:2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3(?:5(?:4[0-25689]|[56])|[78])|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8(?:[23]|4(?:[45]|60)|5(?:4[0-39]|5|64)))|7[1-578]|8(?:[2469]|3[278]|5(?:4(?:4|5[13-7]|6[89])|[56][46]|[78])|7[378]|8(?:6[3-6]|[78]))))|92(?:2[24-9]|3[1-59]|47)|93(?:4(?:36|5[56])|8(?:[38]5|76))[4-6]'
                      ]
                    ],
                    [, '(\\d)(\\d{2})(\\d{4})(\\d{4})', '$1 $2 $3-$4', ['91']],
                    [, '(\\d{3})(\\d{3})(\\d{5})', '$1-$2-$3', ['8'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3-$4', ['9']]
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '810\\d{7}', , , , , , , [10]],
                  [, , '810\\d{7}', , , , '8101234567', , , [10]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AS: [
                  ,
                  [, , '(?:[58]\\d\\d|684|900)\\d{7}', , , , , , , [10], [7]],
                  [, , '6846(?:22|33|44|55|77|88|9[19])\\d{4}', , , , '6846221234', , , , [7]],
                  [, , '684(?:2(?:48|5[2468]|7[26])|7(?:3[13]|70|82))\\d{4}', , , , '6847331234', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'AS',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([267]\\d{6})$|1',
                  '684$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '684',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AT: [
                  ,
                  [
                    ,
                    ,
                    '1\\d{3,12}|2\\d{6,12}|43(?:(?:0\\d|5[02-9])\\d{3,9}|2\\d{4,5}|[3467]\\d{4}|8\\d{4,6}|9\\d{4,7})|5\\d{4,12}|8\\d{7,12}|9\\d{8,12}|(?:[367]\\d|4[0-24-9])\\d{4,11}',
                    ,
                    ,
                    ,
                    ,
                    ,
                    ,
                    [4, 5, 6, 7, 8, 9, 10, 11, 12, 13],
                    [3]
                  ],
                  [
                    ,
                    ,
                    '1(?:11\\d|[2-9]\\d{3,11})|(?:316|463)\\d{3,10}|648[34]\\d{3,9}|(?:51|66|73)2\\d{3,10}|(?:2(?:1[467]|2[13-8]|5[2357]|6[1-46-8]|7[1-8]|8[124-7]|9[1458])|3(?:1[1-578]|3[23568]|4[5-7]|5[1378]|6[1-38]|8[3-68])|4(?:2[1-8]|35|7[1368]|8[2457])|5(?:2[1-8]|3[357]|4[147]|5[12578]|6[37])|6(?:13|2[1-47]|4[135-7]|5[468])|7(?:2[1-8]|35|4[13478]|5[68]|6[16-8]|7[1-6]|9[45]))\\d{4,10}',
                    ,
                    ,
                    ,
                    '1234567890',
                    ,
                    ,
                    ,
                    [3]
                  ],
                  [, , '6(?:485|(?:5[0-3579]|6[013-9]|[7-9]\\d)\\d)\\d{3,9}', , , , '664123456', , , [7, 8, 9, 10, 11, 12, 13]],
                  [, , '800\\d{6,10}', , , , '800123456', , , [9, 10, 11, 12, 13]],
                  [, , '(?:8[69][2-68]|9(?:0[01]|3[019]))\\d{6,10}', , , , '900123456', , , [9, 10, 11, 12, 13]],
                  [, , '8(?:10|2[018])\\d{6,10}|828\\d{5}', , , , '810123456', , , [8, 9, 10, 11, 12, 13]],
                  [, , , , , , , , , [-1]],
                  [, , '5(?:0[1-9]|17|[79]\\d)\\d{2,10}|7[28]0\\d{6,10}', , , , '780123456', , , [5, 6, 7, 8, 9, 10, 11, 12, 13]],
                  'AT',
                  43,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})', '$1', ['14']],
                    [, '(\\d)(\\d{3,12})', '$1 $2', ['1(?:11|[2-9])'], '0$1'],
                    [, '(\\d{3})(\\d{2})', '$1 $2', ['517'], '0$1'],
                    [, '(\\d{2})(\\d{3,5})', '$1 $2', ['5[079]'], '0$1'],
                    [, '(\\d{6})', '$1', ['[18]']],
                    [
                      ,
                      '(\\d{3})(\\d{3,10})',
                      '$1 $2',
                      ['(?:31|4)6|51|6(?:48|5[0-3579]|[6-9])|7(?:20|32|8)|[89]', '(?:31|4)6|51|6(?:485|5[0-3579]|[6-9])|7(?:20|32|8)|[89]'],
                      '0$1'
                    ],
                    [, '(\\d{4})(\\d{3,9})', '$1 $2', ['[2-467]|5[2-6]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['5'], '0$1'],
                    [, '(\\d{2})(\\d{4})(\\d{4,7})', '$1 $2 $3', ['5'], '0$1']
                  ],
                  [
                    [, '(\\d)(\\d{3,12})', '$1 $2', ['1(?:11|[2-9])'], '0$1'],
                    [, '(\\d{3})(\\d{2})', '$1 $2', ['517'], '0$1'],
                    [, '(\\d{2})(\\d{3,5})', '$1 $2', ['5[079]'], '0$1'],
                    [
                      ,
                      '(\\d{3})(\\d{3,10})',
                      '$1 $2',
                      ['(?:31|4)6|51|6(?:48|5[0-3579]|[6-9])|7(?:20|32|8)|[89]', '(?:31|4)6|51|6(?:485|5[0-3579]|[6-9])|7(?:20|32|8)|[89]'],
                      '0$1'
                    ],
                    [, '(\\d{4})(\\d{3,9})', '$1 $2', ['[2-467]|5[2-6]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['5'], '0$1'],
                    [, '(\\d{2})(\\d{4})(\\d{4,7})', '$1 $2 $3', ['5'], '0$1']
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AU: [
                  ,
                  [, , '1(?:[0-79]\\d{7}(?:\\d(?:\\d{2})?)?|8[0-24-9]\\d{7})|[2-478]\\d{8}|1\\d{4,7}', , , , , , , [5, 6, 7, 8, 9, 10, 12]],
                  [
                    ,
                    ,
                    '(?:(?:2(?:(?:[0-26-9]\\d|3[0-8]|5[0135-9])\\d|4(?:[02-9]\\d|10))|3(?:(?:[0-3589]\\d|6[1-9]|7[0-35-9])\\d|4(?:[0-578]\\d|90))|7(?:[013-57-9]\\d|2[0-8])\\d)\\d\\d|8(?:51(?:0(?:0[03-9]|[12479]\\d|3[2-9]|5[0-8]|6[1-9]|8[0-7])|1(?:[0235689]\\d|1[0-69]|4[0-589]|7[0-47-9])|2(?:0[0-79]|[18][13579]|2[14-9]|3[0-46-9]|[4-6]\\d|7[89]|9[0-4])|[34]\\d\\d)|(?:6[0-8]|[78]\\d)\\d{3}|9(?:[02-9]\\d{3}|1(?:(?:[0-58]\\d|6[0135-9])\\d|7(?:0[0-24-9]|[1-9]\\d)|9(?:[0-46-9]\\d|5[0-79])))))\\d{3}',
                    ,
                    ,
                    ,
                    '212345678',
                    ,
                    ,
                    [9],
                    [8]
                  ],
                  [, , '4(?:79[01]|83[0-389]|94[0-478])\\d{5}|4(?:[0-36]\\d|4[047-9]|[58][0-24-9]|7[02-8]|9[0-37-9])\\d{6}', , , , '412345678', , , [9]],
                  [, , '180(?:0\\d{3}|2)\\d{3}', , , , '1800123456', , , [7, 10]],
                  [, , '190[0-26]\\d{6}', , , , '1900123456', , , [10]],
                  [, , '13(?:00\\d{6}(?:\\d{2})?|45[0-4]\\d{3})|13\\d{4}', , , , '1300123456', , , [6, 8, 10, 12]],
                  [, , , , , , , , , [-1]],
                  [, , '14(?:5(?:1[0458]|[23][458])|71\\d)\\d{4}', , , , '147101234', , , [9]],
                  'AU',
                  61,
                  '001[14-689]|14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011',
                  '0',
                  ,
                  ,
                  '(183[12])|0',
                  ,
                  '0011',
                  ,
                  [
                    [, '(\\d{2})(\\d{3,4})', '$1 $2', ['16'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3', ['13']],
                    [, '(\\d{3})(\\d{3})', '$1 $2', ['19']],
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['180', '1802']],
                    [, '(\\d{4})(\\d{3,4})', '$1 $2', ['19']],
                    [, '(\\d{2})(\\d{3})(\\d{2,4})', '$1 $2 $3', ['16'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['14|4'], '0$1'],
                    [, '(\\d)(\\d{4})(\\d{4})', '$1 $2 $3', ['[2378]'], '(0$1)', '$CC ($1)'],
                    [, '(\\d{4})(\\d{3})(\\d{3})', '$1 $2 $3', ['1(?:30|[89])']],
                    [, '(\\d{4})(\\d{4})(\\d{4})', '$1 $2 $3', ['130']]
                  ],
                  [
                    [, '(\\d{2})(\\d{3,4})', '$1 $2', ['16'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2,4})', '$1 $2 $3', ['16'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['14|4'], '0$1'],
                    [, '(\\d)(\\d{4})(\\d{4})', '$1 $2 $3', ['[2378]'], '(0$1)', '$CC ($1)'],
                    [, '(\\d{4})(\\d{3})(\\d{3})', '$1 $2 $3', ['1(?:30|[89])']]
                  ],
                  [, , '163\\d{2,6}', , , , '1631234', , , [5, 6, 7, 8, 9]],
                  1,
                  ,
                  [, , '1(?:3(?:00\\d{5}|45[0-4])|802)\\d{3}|1[38]00\\d{6}|13\\d{4}', , , , , , , [6, 7, 8, 10, 12]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AW: [
                  ,
                  [, , '(?:[25-79]\\d\\d|800)\\d{4}', , , , , , , [7]],
                  [, , '5(?:2\\d|8[1-9])\\d{4}', , , , '5212345'],
                  [, , '(?:290|5[69]\\d|6(?:[03]0|22|4[0-2]|[69]\\d)|7(?:[34]\\d|7[07])|9(?:6[45]|9[4-8]))\\d{4}', , , , '5601234'],
                  [, , '800\\d{4}', , , , '8001234'],
                  [, , '900\\d{4}', , , , '9001234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '(?:28\\d|501)\\d{4}', , , , '5011234'],
                  'AW',
                  297,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{4})', '$1 $2', ['[25-9]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AX: [
                  ,
                  [, , '2\\d{4,9}|35\\d{4,5}|(?:60\\d\\d|800)\\d{4,6}|7\\d{5,11}|(?:[14]\\d|3[0-46-9]|50)\\d{4,8}', , , , , , , [5, 6, 7, 8, 9, 10, 11, 12]],
                  [, , '18[1-8]\\d{3,6}', , , , '181234567', , , [6, 7, 8, 9]],
                  [, , '4946\\d{2,6}|(?:4[0-8]|50)\\d{4,8}', , , , '412345678', , , [6, 7, 8, 9, 10]],
                  [, , '800\\d{4,6}', , , , '800123456', , , [7, 8, 9]],
                  [, , '[67]00\\d{5,6}', , , , '600123456', , , [8, 9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'AX',
                  358,
                  '00|99(?:[01469]|5(?:[14]1|3[23]|5[59]|77|88|9[09]))',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  '00',
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '18',
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '20\\d{4,8}|60[12]\\d{5,6}|7(?:099\\d{4,5}|5[03-9]\\d{3,7})|20[2-59]\\d\\d|(?:606|7(?:0[78]|1|3\\d))\\d{7}|(?:10|29|3[09]|70[1-5]\\d)\\d{4,8}',
                    ,
                    ,
                    ,
                    '10112345'
                  ],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AZ: [
                  ,
                  [, , '365\\d{6}|(?:[124579]\\d|60|88)\\d{7}', , , , , , , [9], [7]],
                  [
                    ,
                    ,
                    '(?:2[12]428|3655[02])\\d{4}|(?:2(?:22[0-79]|63[0-28])|3654)\\d{5}|(?:(?:1[28]|46)\\d|2(?:[014-6]2|[23]3))\\d{6}',
                    ,
                    ,
                    ,
                    '123123456',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '36554\\d{4}|(?:[16]0|4[04]|5[015]|7[07]|99)\\d{7}', , , , '401234567'],
                  [, , '88\\d{7}', , , , '881234567'],
                  [, , '900200\\d{3}', , , , '900200123'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'AZ',
                  994,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3', ['[1-9]']],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['90'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['1[28]|2|365|46', '1[28]|2|365[45]|46', '1[28]|2|365(?:4|5[02])|46'], '(0$1)'],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[13-9]'], '0$1']
                  ],
                  [
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['90'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['1[28]|2|365|46', '1[28]|2|365[45]|46', '1[28]|2|365(?:4|5[02])|46'], '(0$1)'],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[13-9]'], '0$1']
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BA: [
                  ,
                  [, , '6\\d{8}|(?:[35689]\\d|49|70)\\d{6}', , , , , , , [8, 9], [6]],
                  [
                    ,
                    ,
                    '(?:3(?:[05-79][2-9]|1[4579]|[23][24-9]|4[2-4689]|8[2457-9])|49[2-579]|5(?:0[2-49]|[13][2-9]|[268][2-4679]|4[4689]|5[2-79]|7[2-69]|9[2-4689]))\\d{5}',
                    ,
                    ,
                    ,
                    '30212345',
                    ,
                    ,
                    [8],
                    [6]
                  ],
                  [, , '6040\\d{5}|6(?:03|[1-356]|44|7\\d)\\d{6}', , , , '61123456'],
                  [, , '8[08]\\d{6}', , , , '80123456', , , [8]],
                  [, , '9[0246]\\d{6}', , , , '90123456', , , [8]],
                  [, , '8[12]\\d{6}', , , , '82123456', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'BA',
                  387,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3})', '$1-$2', ['[2-9]']],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['6[1-3]|[7-9]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2-$3', ['[3-5]|6[56]'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{3})', '$1 $2 $3 $4', ['6'], '0$1']
                  ],
                  [
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['6[1-3]|[7-9]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2-$3', ['[3-5]|6[56]'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{3})', '$1 $2 $3 $4', ['6'], '0$1']
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '703[235]0\\d{3}|70(?:2[0-5]|3[0146]|[56]0)\\d{4}', , , , '70341234', , , [8]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BB: [
                  ,
                  [, , '(?:246|[58]\\d\\d|900)\\d{7}', , , , , , , [10], [7]],
                  [
                    ,
                    ,
                    '246521[0369]\\d{3}|246(?:2(?:2[78]|7[0-4])|4(?:1[024-6]|2\\d|3[2-9])|5(?:20|[34]\\d|54|7[1-3])|6(?:2\\d|38)|7[35]7|9(?:1[89]|63))\\d{4}',
                    ,
                    ,
                    ,
                    '2464123456',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [
                    ,
                    ,
                    '246(?:(?:2(?:[3568]\\d|4[0-57-9])|3(?:5[2-9]|6[0-6])|4(?:46|5\\d)|69[5-7]|8(?:[2-5]\\d|83))\\d|52(?:1[147]|20))\\d{3}',
                    ,
                    ,
                    ,
                    '2462501234',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '(?:246976|900[2-9]\\d\\d)\\d{4}', , , , '9002123456', , , , [7]],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , '24631\\d{5}', , , , '2463101234', , , , [7]],
                  'BB',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-9]\\d{6})$|1',
                  '246$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '246',
                  [, , , , , , , , , [-1]],
                  [, , '246(?:292|367|4(?:1[7-9]|3[01]|4[47-9]|67)|7(?:1[2-9]|2\\d|3[016]|53))\\d{4}', , , , '2464301234', , , , [7]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BD: [
                  ,
                  [, , '[1-469]\\d{9}|8[0-79]\\d{7,8}|[2-79]\\d{8}|[2-9]\\d{7}|[3-9]\\d{6}|[57-9]\\d{5}', , , , , , , [6, 7, 8, 9, 10]],
                  [
                    ,
                    ,
                    '(?:4(?:31\\d\\d|423)|5222)\\d{3}(?:\\d{2})?|8332[6-9]\\d\\d|(?:3(?:03[56]|224)|4(?:22[25]|653))\\d{3,4}|(?:3(?:42[47]|529|823)|4(?:027|525|65(?:28|8))|562|6257|7(?:1(?:5[3-5]|6[12]|7[156]|89)|22[589]56|32|42675|52(?:[25689](?:56|8)|[347]8)|71(?:6[1267]|75|89)|92374)|82(?:2[59]|32)56|9(?:03[23]56|23(?:256|373)|31|5(?:1|2[4589]56)))\\d{3}|(?:3(?:02[348]|22[35]|324|422)|4(?:22[67]|32[236-9]|6(?:2[46]|5[57])|953)|5526|6(?:024|6655)|81)\\d{4,5}|(?:2(?:7(?:1[0-267]|2[0-289]|3[0-29]|4[01]|5[1-3]|6[013]|7[0178]|91)|8(?:0[125]|1[1-6]|2[0157-9]|3[1-69]|41|6[1-35]|7[1-5]|8[1-8]|9[0-6])|9(?:0[0-2]|1[0-4]|2[568]|3[3-6]|5[5-7]|6[0136-9]|7[0-7]|8[014-9]))|3(?:0(?:2[025-79]|3[2-4])|181|22[12]|32[2356]|824)|4(?:02[09]|22[348]|32[045]|523|6(?:27|54))|666(?:22|53)|7(?:22[57-9]|42[56]|82[35])8|8(?:0[124-9]|2(?:181|2[02-4679]8)|4[12]|[5-7]2)|9(?:[04]2|2(?:2|328)|81))\\d{4}|(?:2(?:[23]\\d|[45])\\d\\d|3(?:1(?:2[5-7]|[5-7])|425|822)|4(?:033|1\\d|[257]1|332|4(?:2[246]|5[25])|6(?:2[35]|56|62)|8(?:23|54)|92[2-5])|5(?:02[03489]|22[457]|32[35-79]|42[46]|6(?:[18]|53)|724|826)|6(?:023|2(?:2[2-5]|5[3-5]|8)|32[3478]|42[34]|52[47]|6(?:[18]|6(?:2[34]|5[24]))|[78]2[2-5]|92[2-6])|7(?:02|21\\d|[3-589]1|6[12]|72[24])|8(?:217|3[12]|[5-7]1)|9[24]1)\\d{5}|(?:(?:3[2-8]|5[2-57-9]|6[03-589])1|4[4689][18])\\d{5}|[59]1\\d{5}',
                    ,
                    ,
                    ,
                    '27111234'
                  ],
                  [, , '(?:1[13-9]\\d|644)\\d{7}|(?:3[78]|44|66)[02-9]\\d{7}', , , , '1812345678', , , [10]],
                  [, , '80[03]\\d{7}', , , , '8001234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '96(?:0[469]|1[0-47]|3[389]|43|6[69]|7[78])\\d{6}', , , , '9604123456', , , [10]],
                  'BD',
                  880,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{4,6})', '$1-$2', ['31[5-8]|[459]1'], '0$1'],
                    [
                      ,
                      '(\\d{3})(\\d{3,7})',
                      '$1-$2',
                      [
                        '3(?:[67]|8[013-9])|4(?:6[168]|7|[89][18])|5(?:6[128]|9)|6(?:[15]|28|4[14])|7[2-589]|8(?:0[014-9]|[12])|9[358]|(?:3[2-5]|4[235]|5[2-578]|6[0389]|76|8[3-7]|9[24])1|(?:44|66)[01346-9]'
                      ],
                      '0$1'
                    ],
                    [, '(\\d{4})(\\d{3,6})', '$1-$2', ['[13-9]|2[23]'], '0$1'],
                    [, '(\\d)(\\d{7,8})', '$1-$2', ['2'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BE: [
                  ,
                  [, , '4\\d{8}|[1-9]\\d{7}', , , , , , , [8, 9]],
                  [, , '80[2-8]\\d{5}|(?:1[0-69]|[23][2-8]|4[23]|5\\d|6[013-57-9]|71|8[1-79]|9[2-4])\\d{6}', , , , '12345678', , , [8]],
                  [, , '4[5-9]\\d{7}', , , , '470123456', , , [9]],
                  [, , '800[1-9]\\d{4}', , , , '80012345', , , [8]],
                  [, , '(?:70(?:2[0-57]|3[04-7]|44|6[04-69]|7[0579])|90\\d\\d)\\d{4}', , , , '90012345', , , [8]],
                  [, , '7879\\d{4}', , , , '78791234', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'BE',
                  32,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{2})(\\d{3})', '$1 $2 $3', ['(?:80|9)0'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[239]|4[23]'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[15-8]'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['4'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '78(?:0[578]|1[014-8]|2[25]|3[15-8]|48|5[05]|60|7[06-8]|9\\d)\\d{4}', , , , '78102345', , , [8]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BF: [
                  ,
                  [, , '(?:[025-7]\\d|44)\\d{6}', , , , , , , [8]],
                  [, , '2(?:0(?:49|5[23]|6[5-7]|9[016-9])|4(?:4[569]|5[4-6]|6[5-7]|7[0179])|5(?:[34]\\d|50|6[5-7]))\\d{4}', , , , '20491234'],
                  [, , '(?:0[1-7]|44|5[0-8]|[67]\\d)\\d{6}', , , , '70123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'BF',
                  226,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[024-7]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BG: [
                  ,
                  [, , '00800\\d{7}|[2-7]\\d{6,7}|[89]\\d{6,8}|2\\d{5}', , , , , , , [6, 7, 8, 9, 12], [4, 5]],
                  [, , '2\\d{5,7}|(?:43[1-6]|70[1-9])\\d{4,5}|(?:[36]\\d|4[124-7]|[57][1-9]|8[1-6]|9[1-7])\\d{5,6}', , , , '2123456', , , [6, 7, 8], [4, 5]],
                  [, , '(?:43[07-9]|99[69]\\d)\\d{5}|(?:8[7-9]|98)\\d{7}', , , , '43012345', , , [8, 9]],
                  [, , '(?:00800\\d\\d|800)\\d{5}', , , , '80012345', , , [8, 12]],
                  [, , '90\\d{6}', , , , '90123456', , , [8]],
                  [, , '700\\d{5}', , , , '70012345', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'BG',
                  359,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{6})', '$1', ['1']],
                    [, '(\\d)(\\d)(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['2'], '0$1'],
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['43[1-6]|70[1-9]'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{3,4})', '$1 $2 $3', ['2'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2,3})', '$1 $2 $3', ['[356]|4[124-7]|7[1-9]|8[1-6]|9[1-7]'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{3})', '$1 $2 $3', ['(?:70|8)0'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{2})', '$1 $2 $3', ['43[1-7]|7'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[48]|9[08]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['9'], '0$1']
                  ],
                  [
                    [, '(\\d)(\\d)(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['2'], '0$1'],
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['43[1-6]|70[1-9]'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{3,4})', '$1 $2 $3', ['2'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2,3})', '$1 $2 $3', ['[356]|4[124-7]|7[1-9]|8[1-6]|9[1-7]'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{3})', '$1 $2 $3', ['(?:70|8)0'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{2})', '$1 $2 $3', ['43[1-7]|7'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[48]|9[08]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['9'], '0$1']
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BH: [
                  ,
                  [, , '[136-9]\\d{7}', , , , , , , [8]],
                  [
                    ,
                    ,
                    '(?:1(?:3[1356]|6[0156]|7\\d)\\d|6(?:1[16]\\d|500|6(?:0\\d|3[12]|44|55|7[7-9]|88)|9[69][69])|7(?:[07]\\d\\d|1(?:11|78)))\\d{4}',
                    ,
                    ,
                    ,
                    '17001234'
                  ],
                  [, , '(?:3(?:[0-79]\\d|8[0-57-9])\\d|6(?:3(?:00|33|6[16])|441|6(?:3[03-9]|[69]\\d|7[0-689])))\\d{4}', , , , '36001234'],
                  [, , '8[02369]\\d{6}', , , , '80123456'],
                  [, , '(?:87|9[0-8])\\d{6}', , , , '90123456'],
                  [, , '84\\d{6}', , , , '84123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'BH',
                  973,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{4})(\\d{4})', '$1 $2', ['[13679]|8[02-4679]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BI: [
                  ,
                  [, , '(?:[267]\\d|31)\\d{6}', , , , , , , [8]],
                  [, , '(?:22|31)\\d{6}', , , , '22201234'],
                  [, , '(?:29|6[124-9]|7[125-9])\\d{6}', , , , '79561234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'BI',
                  257,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[2367]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BJ: [
                  ,
                  [, , '(?:01\\d|[24-689])\\d{7}', , , , , , , [8, 10]],
                  [, , '2090\\d{4}|(?:012\\d\\d|2(?:02|1[037]|2[45]|3[68]|4\\d))\\d{5}', , , , '0120211234'],
                  [, , '(?:01(?:2[5-9]|[4-69]\\d)|4[0-8]|[56]\\d|9[013-9])\\d{6}', , , , '0195123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '857[58]\\d{4}', , , , '85751234', , , [8]],
                  'BJ',
                  229,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[24-689]']],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4 $5', ['0']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '81\\d{6}', , , , '81123456', , , [8]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BL: [
                  ,
                  [, , '(?:590\\d|7090)\\d{5}|(?:69|80|9\\d)\\d{7}', , , , , , , [9]],
                  [, , '590(?:2[7-9]|3[3-7]|5[12]|87)\\d{4}', , , , '590271234'],
                  [, , '(?:69(?:0\\d\\d|1(?:2[2-9]|3[0-5])|4(?:0[89]|1[2-6]|9\\d)|6(?:1[016-9]|5[0-4]|[67]\\d))|7090[0-4])\\d{4}', , , , '690001234'],
                  [, , '80[0-5]\\d{6}', , , , '800012345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '9(?:(?:39[5-7]|76[018])\\d|475[0-6])\\d{4}', , , , '976012345'],
                  'BL',
                  590,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BM: [
                  ,
                  [, , '(?:441|[58]\\d\\d|900)\\d{7}', , , , , , , [10], [7]],
                  [, , '441(?:[46]\\d\\d|5(?:4\\d|60|89))\\d{4}', , , , '4414123456', , , , [7]],
                  [, , '441(?:[2378]\\d|5[0-39]|9[02])\\d{5}', , , , '4413701234', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'BM',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-9]\\d{6})$|1',
                  '441$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '441',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BN: [
                  ,
                  [, , '[2-578]\\d{6}', , , , , , , [7]],
                  [, , '22[0-7]\\d{4}|(?:2[013-9]|[34]\\d|5[0-25-9])\\d{5}', , , , '2345678'],
                  [, , '(?:22[89]|[78]\\d\\d)\\d{4}', , , , '7123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '5[34]\\d{5}', , , , '5345678'],
                  'BN',
                  673,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{4})', '$1 $2', ['[2-578]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BO: [
                  ,
                  [, , '8001\\d{5}|(?:[2-467]\\d|50)\\d{6}', , , , , , , [8, 9], [7]],
                  [
                    ,
                    ,
                    '(?:2(?:2\\d\\d|5(?:11|[258]\\d|9[67])|6(?:12|2\\d|9[34])|8(?:2[34]|39|62))|3(?:3\\d\\d|4(?:6\\d|8[24])|8(?:25|42|5[257]|86|9[25])|9(?:[27]\\d|3[2-4]|4[248]|5[24]|6[2-6]))|4(?:4\\d\\d|6(?:11|[24689]\\d|72)))\\d{4}',
                    ,
                    ,
                    ,
                    '22123456',
                    ,
                    ,
                    [8],
                    [7]
                  ],
                  [, , '[67]\\d{7}', , , , '71234567', , , [8]],
                  [, , '8001[07]\\d{4}', , , , '800171234', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '50\\d{6}', , , , '50123456', , , [8], [7]],
                  'BO',
                  591,
                  '00(?:1\\d)?',
                  '0',
                  ,
                  ,
                  '0(1\\d)?',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{7})', '$1 $2', ['[235]|4[46]'], , '0$CC $1'],
                    [, '(\\d{8})', '$1', ['[67]'], , '0$CC $1'],
                    [, '(\\d{3})(\\d{2})(\\d{4})', '$1 $2 $3', ['8'], , '0$CC $1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '8001[07]\\d{4}', , , , , , , [9]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BQ: [
                  ,
                  [, , '(?:[34]1|7\\d)\\d{5}', , , , , , , [7]],
                  [, , '(?:318[023]|41(?:6[023]|70)|7(?:1[578]|2[05]|50)\\d)\\d{3}', , , , '7151234'],
                  [, , '(?:31(?:8[14-8]|9[14578])|416[14-9]|7(?:0[01]|7[07]|8\\d|9[056])\\d)\\d{3}', , , , '3181234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'BQ',
                  599,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '[347]',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BR: [
                  ,
                  [, , '[1-467]\\d{9,10}|55[0-46-9]\\d{8}|[34]\\d{7}|55\\d{7,8}|(?:5[0-46-9]|[89]\\d)\\d{7,9}', , , , , , , [8, 9, 10, 11]],
                  [, , '(?:[14689][1-9]|2[12478]|3[1-578]|5[13-5]|7[13-579])[2-5]\\d{7}', , , , '1123456789', , , [10], [8]],
                  [, , '(?:[14689][1-9]|2[12478]|3[1-578]|5[13-5]|7[13-579])(?:7|9\\d)\\d{7}', , , , '11961234567', , , [10, 11], [8, 9]],
                  [, , '800\\d{6,7}', , , , '800123456', , , [9, 10]],
                  [, , '[59]00\\d{6,7}', , , , '500123456', , , [9, 10]],
                  [, , '(?:30[03]\\d{3}|4(?:0(?:0\\d|20)|370|864))\\d{4}|300\\d{5}', , , , '40041234', , , [8, 10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'BR',
                  55,
                  '00(?:1[245]|2[1-35]|31|4[13]|[56]5|99)',
                  '0',
                  ,
                  ,
                  '(?:0|90)(?:(1[245]|2[1-35]|31|4[13]|[56]5|99)(\\d{10,11}))?',
                  '$2',
                  ,
                  ,
                  [
                    [, '(\\d{3,6})', '$1', ['1(?:1[25-8]|2[357-9]|3[02-68]|4[12568]|5|6[0-8]|8[015]|9[0-47-9])|321|610']],
                    [, '(\\d{4})(\\d{4})', '$1-$2', ['300|4(?:0[02]|37|86)', '300|4(?:0(?:0|20)|370|864)']],
                    [, '(\\d{4})(\\d{4})', '$1-$2', ['[2-57]', '[2357]|4(?:[0-24-9]|3(?:[0-689]|7[1-9]))']],
                    [, '(\\d{3})(\\d{2,3})(\\d{4})', '$1 $2 $3', ['(?:[358]|90)0'], '0$1'],
                    [, '(\\d{5})(\\d{4})', '$1-$2', ['9']],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1 $2-$3', ['(?:[14689][1-9]|2[12478]|3[1-578]|5[13-5]|7[13-579])[2-57]'], '($1)', '0 $CC ($1)'],
                    [, '(\\d{2})(\\d{5})(\\d{4})', '$1 $2-$3', ['[16][1-9]|[2-57-9]'], '($1)', '0 $CC ($1)']
                  ],
                  [
                    [, '(\\d{4})(\\d{4})', '$1-$2', ['300|4(?:0[02]|37|86)', '300|4(?:0(?:0|20)|370|864)']],
                    [, '(\\d{3})(\\d{2,3})(\\d{4})', '$1 $2 $3', ['(?:[358]|90)0'], '0$1'],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1 $2-$3', ['(?:[14689][1-9]|2[12478]|3[1-578]|5[13-5]|7[13-579])[2-57]'], '($1)', '0 $CC ($1)'],
                    [, '(\\d{2})(\\d{5})(\\d{4})', '$1 $2-$3', ['[16][1-9]|[2-57-9]'], '($1)', '0 $CC ($1)']
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '(?:30[03]\\d{3}|4(?:0(?:0\\d|20)|864))\\d{4}|800\\d{6,7}|300\\d{5}', , , , , , , [8, 9, 10]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BS: [
                  ,
                  [, , '(?:242|[58]\\d\\d|900)\\d{7}', , , , , , , [10], [7]],
                  [
                    ,
                    ,
                    '242(?:3(?:02|[236][1-9]|4[0-24-9]|5[0-68]|7[347]|8[0-4]|9[2-467])|461|502|6(?:0[1-5]|12|2[013]|[45]0|7[67]|8[78]|9[89])|7(?:02|88))\\d{4}',
                    ,
                    ,
                    ,
                    '2423456789',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [
                    ,
                    ,
                    '242(?:3(?:5[79]|7[56]|95)|4(?:[23][1-9]|4[1-35-9]|5[1-8]|6[2-8]|7\\d|81)|5(?:2[45]|3[35]|44|5[1-46-9]|65|77)|6[34]6|7(?:27|38)|8(?:0[1-9]|1[02-9]|2\\d|3[0-4]|[89]9))\\d{4}',
                    ,
                    ,
                    ,
                    '2423591234',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '242300\\d{4}|8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456', , , , [7]],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'BS',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([3-8]\\d{6})$|1',
                  '242$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '242',
                  [, , , , , , , , , [-1]],
                  [, , '242225\\d{4}', , , , '2422250123'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BT: [
                  ,
                  [, , '[178]\\d{7}|[2-8]\\d{6}', , , , , , , [7, 8], [6]],
                  [, , '(?:2[3-6]|[34][5-7]|5[236]|6[2-46]|7[246]|8[2-4])\\d{5}', , , , '2345678', , , [7], [6]],
                  [, , '(?:1[67]|[78]7)\\d{6}', , , , '17123456', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'BT',
                  975,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3})', '$1 $2', ['[2-7]']],
                    [, '(\\d)(\\d{3})(\\d{3})', '$1 $2 $3', ['[2-6]|7[246]|8[2-4]']],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['1[67]|[78]']]
                  ],
                  [
                    [, '(\\d)(\\d{3})(\\d{3})', '$1 $2 $3', ['[2-6]|7[246]|8[2-4]']],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['1[67]|[78]']]
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BW: [
                  ,
                  [, , '(?:0800|(?:[37]|800)\\d)\\d{6}|(?:[2-6]\\d|90)\\d{5}', , , , , , , [7, 8, 10]],
                  [
                    ,
                    ,
                    '(?:2(?:4[0-48]|6[0-24]|9[0578])|3(?:1[0-35-9]|55|[69]\\d|7[013]|81)|4(?:6[03]|7[1267]|9[0-5])|5(?:3[03489]|4[0489]|7[1-47]|88|9[0-49])|6(?:2[1-35]|5[149]|8[013467]))\\d{4}',
                    ,
                    ,
                    ,
                    '2401234',
                    ,
                    ,
                    [7]
                  ],
                  [, , '(?:321|7[1-8]\\d)\\d{5}', , , , '71123456', , , [8]],
                  [, , '(?:0800|800\\d)\\d{6}', , , , '0800012345', , , [10]],
                  [, , '90\\d{5}', , , , '9012345', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '79(?:1(?:[0-2]\\d|3[0-8])|2[0-7]\\d)\\d{3}', , , , '79101234', , , [8]],
                  'BW',
                  267,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{5})', '$1 $2', ['90']],
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['[24-6]|3[15-9]']],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['[37]']],
                    [, '(\\d{4})(\\d{3})(\\d{3})', '$1 $2 $3', ['0']],
                    [, '(\\d{3})(\\d{4})(\\d{3})', '$1 $2 $3', ['8']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BY: [
                  ,
                  [
                    ,
                    ,
                    '(?:[12]\\d|33|44|902)\\d{7}|8(?:0[0-79]\\d{5,7}|[1-7]\\d{9})|8(?:1[0-489]|[5-79]\\d)\\d{7}|8[1-79]\\d{6,7}|8[0-79]\\d{5}|8\\d{5}',
                    ,
                    ,
                    ,
                    ,
                    ,
                    ,
                    [6, 7, 8, 9, 10, 11],
                    [5]
                  ],
                  [
                    ,
                    ,
                    '(?:1(?:5(?:1[1-5]|[24]\\d|6[2-4]|9[1-7])|6(?:[235]\\d|4[1-7])|7\\d\\d)|2(?:1(?:[246]\\d|3[0-35-9]|5[1-9])|2(?:[235]\\d|4[0-8])|3(?:[26]\\d|3[02-79]|4[024-7]|5[03-7])))\\d{5}',
                    ,
                    ,
                    ,
                    '152450911',
                    ,
                    ,
                    [9],
                    [5, 6, 7]
                  ],
                  [, , '(?:2(?:5[5-79]|9[1-9])|(?:33|44)\\d)\\d{6}', , , , '294911911', , , [9]],
                  [, , '800\\d{3,7}|8(?:0[13]|20\\d)\\d{7}', , , , '8011234567'],
                  [, , '(?:810|902)\\d{7}', , , , '9021234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '249\\d{6}', , , , '249123456', , , [9]],
                  'BY',
                  375,
                  '810',
                  '8',
                  ,
                  ,
                  '0|80?',
                  ,
                  '8~10',
                  ,
                  [
                    [, '(\\d{3})(\\d{3})', '$1 $2', ['800'], '8 $1'],
                    [, '(\\d{3})(\\d{2})(\\d{2,4})', '$1 $2 $3', ['800'], '8 $1'],
                    [
                      ,
                      '(\\d{4})(\\d{2})(\\d{3})',
                      '$1 $2-$3',
                      [
                        '1(?:5[169]|6[3-5]|7[179])|2(?:1[35]|2[34]|3[3-5])',
                        '1(?:5[169]|6(?:3[1-3]|4|5[125])|7(?:1[3-9]|7[0-24-6]|9[2-7]))|2(?:1[35]|2[34]|3[3-5])'
                      ],
                      '8 0$1'
                    ],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2-$3-$4', ['1(?:[56]|7[467])|2[1-3]'], '8 0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2-$3-$4', ['[1-4]'], '8 0$1'],
                    [, '(\\d{3})(\\d{3,4})(\\d{4})', '$1 $2 $3', ['[89]'], '8 $1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '800\\d{3,7}|(?:8(?:0[13]|10|20\\d)|902)\\d{7}'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BZ: [
                  ,
                  [, , '(?:0800\\d|[2-8])\\d{6}', , , , , , , [7, 11]],
                  [, , '(?:2(?:[02]\\d|36|[68]0)|[3-58](?:[02]\\d|[68]0)|7(?:[02]\\d|32|[68]0))\\d{4}', , , , '2221234', , , [7]],
                  [, , '6[0-35-7]\\d{5}', , , , '6221234', , , [7]],
                  [, , '0800\\d{7}', , , , '08001234123', , , [11]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'BZ',
                  501,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1-$2', ['[2-8]']],
                    [, '(\\d)(\\d{3})(\\d{4})(\\d{3})', '$1-$2-$3-$4', ['0']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CA: [
                  ,
                  [, , '[2-9]\\d{9}|3\\d{6}', , , , , , , [7, 10]],
                  [
                    ,
                    ,
                    '(?:2(?:04|[23]6|[48]9|5[07]|63)|3(?:06|43|54|6[578]|82)|4(?:03|1[68]|[26]8|3[178]|50|74)|5(?:06|1[49]|48|79|8[147])|6(?:04|[18]3|39|47|72)|7(?:0[59]|42|53|78|8[02])|8(?:[06]7|19|25|7[39])|9(?:0[25]|42))[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5062345678',
                    ,
                    ,
                    [10],
                    [7]
                  ],
                  [
                    ,
                    ,
                    '(?:2(?:04|[23]6|[48]9|5[07]|63)|3(?:06|43|54|6[578]|82)|4(?:03|1[68]|[26]8|3[178]|50|74)|5(?:06|1[49]|48|79|8[147])|6(?:04|[18]3|39|47|72)|7(?:0[59]|42|53|78|8[02])|8(?:[06]7|19|25|7[39])|9(?:0[25]|42))[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5062345678',
                    ,
                    ,
                    [10],
                    [7]
                  ],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456', , , [10]],
                  [, , '900[2-9]\\d{6}', , , , '9002123456', , , [10]],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|(?:5(?:2[125-9]|33|44|66|77|88)|6(?:22|33))[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5219023456',
                    ,
                    ,
                    [10]
                  ],
                  [, , '600[2-9]\\d{6}', , , , '6002012345', , , [10]],
                  'CA',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '1',
                  ,
                  ,
                  1,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '310\\d{4}', , , , '3101234', , , [7]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CC: [
                  ,
                  [, , '1(?:[0-79]\\d{8}(?:\\d{2})?|8[0-24-9]\\d{7})|[148]\\d{8}|1\\d{5,7}', , , , , , , [6, 7, 8, 9, 10, 12]],
                  [
                    ,
                    ,
                    '8(?:51(?:0(?:02|31|60|89)|1(?:18|76)|223)|91(?:0(?:1[0-2]|29)|1(?:[28]2|50|79)|2(?:10|64)|3(?:[06]8|22)|4[29]8|62\\d|70[23]|959))\\d{3}',
                    ,
                    ,
                    ,
                    '891621234',
                    ,
                    ,
                    [9],
                    [8]
                  ],
                  [, , '4(?:79[01]|83[0-389]|94[0-478])\\d{5}|4(?:[0-36]\\d|4[047-9]|[58][0-24-9]|7[02-8]|9[0-37-9])\\d{6}', , , , '412345678', , , [9]],
                  [, , '180(?:0\\d{3}|2)\\d{3}', , , , '1800123456', , , [7, 10]],
                  [, , '190[0-26]\\d{6}', , , , '1900123456', , , [10]],
                  [, , '13(?:00\\d{6}(?:\\d{2})?|45[0-4]\\d{3})|13\\d{4}', , , , '1300123456', , , [6, 8, 10, 12]],
                  [, , , , , , , , , [-1]],
                  [, , '14(?:5(?:1[0458]|[23][458])|71\\d)\\d{4}', , , , '147101234', , , [9]],
                  'CC',
                  61,
                  '001[14-689]|14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011',
                  '0',
                  ,
                  ,
                  '([59]\\d{7})$|0',
                  '8$1',
                  '0011',
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CD: [
                  ,
                  [, , '(?:(?:[189]|5\\d)\\d|2)\\d{7}|[1-68]\\d{6}', , , , , , , [7, 8, 9, 10]],
                  [, , '(?:(?:12|573)\\d\\d|276)\\d{5}|[1-6]\\d{6}', , , , '1234567'],
                  [, , '88\\d{5}|(?:8[0-69]|9[017-9])\\d{7}', , , , '991234567', , , [7, 9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'CD',
                  243,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d{3})', '$1 $2 $3', ['88'], '0$1'],
                    [, '(\\d{2})(\\d{5})', '$1 $2', ['[1-6]'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{4})', '$1 $2 $3', ['2'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['1'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[89]'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['5'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CF: [
                  ,
                  [, , '(?:[27]\\d{3}|8776)\\d{4}', , , , , , , [8]],
                  [, , '2[12]\\d{6}', , , , '21612345'],
                  [, , '7[024-7]\\d{6}', , , , '70012345'],
                  [, , , , , , , , , [-1]],
                  [, , '8776\\d{4}', , , , '87761234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'CF',
                  236,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[278]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CG: [
                  ,
                  [, , '222\\d{6}|(?:0\\d|80)\\d{7}', , , , , , , [9]],
                  [, , '222[1-589]\\d{5}', , , , '222123456'],
                  [, , '026(?:1[0-5]|6[6-9])\\d{4}|0(?:[14-6]\\d\\d|2(?:40|5[5-8]|6[07-9]))\\d{5}', , , , '061234567'],
                  [, , , , , , , , , [-1]],
                  [, , '80[0-2]\\d{6}', , , , '800123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'CG',
                  242,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{4})(\\d{4})', '$1 $2 $3', ['8']],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[02]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CH: [
                  ,
                  [, , '8\\d{11}|[2-9]\\d{8}', , , , , , , [9, 12]],
                  [, , '(?:2[12467]|3[1-4]|4[134]|5[256]|6[12]|[7-9]1)\\d{7}', , , , '212345678', , , [9]],
                  [, , '(?:6[89]|7[235-9])\\d{7}', , , , '781234567', , , [9]],
                  [, , '800\\d{6}', , , , '800123456', , , [9]],
                  [, , '90[016]\\d{6}', , , , '900123456', , , [9]],
                  [, , '84[0248]\\d{6}', , , , '840123456', , , [9]],
                  [, , '878\\d{6}', , , , '878123456', , , [9]],
                  [, , , , , , , , , [-1]],
                  'CH',
                  41,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['8[047]|90'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[2-79]|81'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4 $5', ['8'], '0$1']
                  ],
                  ,
                  [, , '74[0248]\\d{6}', , , , '740123456', , , [9]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '5[18]\\d{7}', , , , '581234567', , , [9]],
                  ,
                  ,
                  [, , '860\\d{9}', , , , '860123456789', , , [12]]
                ],
                CI: [
                  ,
                  [, , '[02]\\d{9}', , , , , , , [10]],
                  [, , '2(?:[15]\\d{3}|7(?:2(?:0[23]|1[2357]|2[245]|3[45]|4[3-5])|3(?:06|1[69]|[2-6]7)))\\d{5}', , , , '2123456789'],
                  [, , '0[157]\\d{8}', , , , '0123456789'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'CI',
                  225,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d)(\\d{5})', '$1 $2 $3 $4', ['2']],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{4})', '$1 $2 $3 $4', ['0']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CK: [
                  ,
                  [, , '[2-578]\\d{4}', , , , , , , [5]],
                  [, , '(?:2\\d|3[13-7]|4[1-5])\\d{3}', , , , '21234'],
                  [, , '[578]\\d{4}', , , , '71234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'CK',
                  682,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{3})', '$1 $2', ['[2-578]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CL: [
                  ,
                  [, , '12300\\d{6}|6\\d{9,10}|[2-9]\\d{8}', , , , , , , [9, 10, 11]],
                  [
                    ,
                    ,
                    '2(?:1982[0-6]|3314[05-9])\\d{3}|(?:2(?:1(?:160|962)|2\\d{3}|3(?:(?:2\\d|50)\\d|3(?:[03467]\\d|1[0-35-9]|2[1-9]|5[0-24-9]|8[0-389]|9[0-8])|600)|646[59])|(?:(?:3[2-5]|[47][1-35]|5[1-3578])\\d|6(?:00|[13-57]\\d)|8(?:0[1-9]|[1-9]\\d))\\d\\d|9(?:(?:10[01]|(?:[2458]\\d|7[1-9])\\d)\\d|3(?:[0-57-9]\\d\\d|6(?:0[02-9]|[1-9]\\d))|6(?:[0-8]\\d\\d|9(?:[02-79]\\d|1[05-9]))|9(?:[03-9]\\d\\d|1(?:[0235-9]\\d|4[0-24-9])|2(?:[0-79]\\d|8[0-46-9]))))\\d{4}',
                    ,
                    ,
                    ,
                    '600123456',
                    ,
                    ,
                    [9]
                  ],
                  [
                    ,
                    ,
                    '2(?:1982[0-6]|3314[05-9])\\d{3}|(?:2(?:1(?:160|962)|2\\d{3}|3(?:(?:2\\d|50)\\d|3(?:[03467]\\d|1[0-35-9]|2[1-9]|5[0-24-9]|8[0-389]|9[0-8])|600)|646[59])|(?:(?:3[2-5]|[47][1-35]|5[1-3578]|6[13-57])\\d|8(?:0[1-8]|[1-9]\\d))\\d\\d|9(?:(?:10[01]|(?:[2458]\\d|7[1-9])\\d)\\d|3(?:[0-57-9]\\d\\d|6(?:0[02-9]|[1-9]\\d))|6(?:[0-8]\\d\\d|9(?:[02-79]\\d|1[05-9]))|9(?:[03-9]\\d\\d|1(?:[0235-9]\\d|4[0-24-9])|2(?:[0-79]\\d|8[0-46-9]))))\\d{4}',
                    ,
                    ,
                    ,
                    '221234567',
                    ,
                    ,
                    [9]
                  ],
                  [, , '(?:123|8)00\\d{6}', , , , '800123456', , , [9, 11]],
                  [, , , , , , , , , [-1]],
                  [, , '600\\d{7,8}', , , , '6001234567', , , [10, 11]],
                  [, , , , , , , , , [-1]],
                  [, , '44\\d{7}', , , , '441234567', , , [9]],
                  'CL',
                  56,
                  '(?:0|1(?:1[0-69]|2[02-5]|5[13-58]|69|7[0167]|8[018]))0',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})', '$1', ['1(?:[03-589]|21)|[29]0|78']],
                    [, '(\\d{5})(\\d{4})', '$1 $2', ['219', '2196'], '($1)'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['60|809']],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['44']],
                    [, '(\\d)(\\d{4})(\\d{4})', '$1 $2 $3', ['2[1-36]'], '($1)'],
                    [, '(\\d)(\\d{4})(\\d{4})', '$1 $2 $3', ['9(?:10|[2-9])']],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['3[2-5]|[47]|5[1-3578]|6[13-57]|8(?:0[1-8]|[1-9])'], '($1)'],
                    [, '(\\d{3})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['60|8']],
                    [, '(\\d{4})(\\d{3})(\\d{4})', '$1 $2 $3', ['1']],
                    [, '(\\d{3})(\\d{3})(\\d{2})(\\d{3})', '$1 $2 $3 $4', ['60']]
                  ],
                  [
                    [, '(\\d{5})(\\d{4})', '$1 $2', ['219', '2196'], '($1)'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['60|809']],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['44']],
                    [, '(\\d)(\\d{4})(\\d{4})', '$1 $2 $3', ['2[1-36]'], '($1)'],
                    [, '(\\d)(\\d{4})(\\d{4})', '$1 $2 $3', ['9(?:10|[2-9])']],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['3[2-5]|[47]|5[1-3578]|6[13-57]|8(?:0[1-8]|[1-9])'], '($1)'],
                    [, '(\\d{3})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['60|8']],
                    [, '(\\d{4})(\\d{3})(\\d{4})', '$1 $2 $3', ['1']],
                    [, '(\\d{3})(\\d{3})(\\d{2})(\\d{3})', '$1 $2 $3 $4', ['60']]
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '600\\d{7,8}', , , , , , , [10, 11]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CM: [
                  ,
                  [, , '[26]\\d{8}|88\\d{6,7}', , , , , , , [8, 9]],
                  [, , '2(?:22|33)\\d{6}', , , , '222123456', , , [9]],
                  [, , '(?:24[23]|6(?:[25-9]\\d|40))\\d{6}', , , , '671234567', , , [9]],
                  [, , '88\\d{6,7}', , , , '88012345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'CM',
                  237,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['88']],
                    [, '(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4 $5', ['[26]|88']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CN: [
                  ,
                  [
                    ,
                    ,
                    '(?:(?:1[03-689]|2\\d)\\d\\d|6)\\d{8}|1\\d{10}|[126]\\d{6}(?:\\d(?:\\d{2})?)?|86\\d{5,6}|(?:[3-579]\\d|8[0-57-9])\\d{5,9}',
                    ,
                    ,
                    ,
                    ,
                    ,
                    ,
                    [7, 8, 9, 10, 11, 12],
                    [5, 6]
                  ],
                  [
                    ,
                    ,
                    '(?:10(?:[02-79]\\d\\d|[18](?:0[1-9]|[1-9]\\d))|2(?:[02-57-9]\\d{3}|1(?:[18](?:0[1-9]|[1-9]\\d)|[2-79]\\d\\d))|(?:41[03]|8078|9(?:78|94))\\d\\d)\\d{5}|(?:10|2[0-57-9])(?:1(?:00|23)\\d\\d|95\\d{3,4})|(?:41[03]|9(?:78|94))(?:100\\d\\d|95\\d{3,4})|8078123|(?:43[35]|754|851)\\d{7,8}|(?:43[35]|754|851)(?:1(?:00\\d|23)\\d|95\\d{3,4})|(?:3(?:11|7[179])|4(?:[15]1|3[12])|5(?:1\\d|2[37]|3[12]|51|7[13-79]|9[15])|7(?:[39]1|5[57]|6[09])|8(?:71|98))(?:[02-8]\\d{7}|1(?:0(?:0\\d\\d(?:\\d{3})?|[1-9]\\d{5})|[13-9]\\d{6}|2(?:[0-24-9]\\d{5}|3\\d(?:\\d{4})?))|9(?:[0-46-9]\\d{6}|5\\d{3}(?:\\d(?:\\d{2})?)?))|(?:3(?:1[02-9]|35|49|5\\d|7[02-68]|9[1-68])|4(?:1[24-9]|2[179]|3[46-9]|5[2-9]|6[47-9]|7\\d|8[23])|5(?:3[03-9]|4[36]|5[02-9]|6[1-46]|7[028]|80|9[2-46-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[17]\\d|2[248]|3[04-9]|4[3-6]|5[0-3689]|6[2368]|9[02-9])|8(?:1[236-8]|2[5-7]|3\\d|5[2-9]|7[02-9]|8[36-8]|9[1-7])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))(?:[02-8]\\d{6}|1(?:0(?:0\\d\\d(?:\\d{2})?|[1-9]\\d{4})|[13-9]\\d{5}|2(?:[0-24-9]\\d{4}|3\\d(?:\\d{3})?))|9(?:[0-46-9]\\d{5}|5\\d{3,5}))',
                    ,
                    ,
                    ,
                    '1012345678',
                    ,
                    ,
                    [7, 8, 9, 10, 11],
                    [5, 6]
                  ],
                  [, , '1740[0-5]\\d{6}|1(?:[38]\\d|4[57]|[59][0-35-9]|6[25-7]|7[0-35-8])\\d{8}', , , , '13123456789', , , [11]],
                  [, , '(?:(?:10|21)8|8)00\\d{7}', , , , '8001234567', , , [10, 12]],
                  [, , '16[08]\\d{5}', , , , '16812345', , , [8]],
                  [
                    ,
                    ,
                    '10(?:10\\d{4}|96\\d{3,4})|400\\d{7}|950\\d{7,8}|(?:2[0-57-9]|3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))96\\d{3,4}',
                    ,
                    ,
                    ,
                    '4001234567',
                    ,
                    ,
                    [7, 8, 9, 10, 11],
                    [5, 6]
                  ],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'CN',
                  86,
                  '00|1(?:[12]\\d|79)\\d\\d00',
                  '0',
                  ,
                  ,
                  '(1(?:[12]\\d|79)\\d\\d)|0',
                  ,
                  '00',
                  ,
                  [
                    [, '(\\d{5,6})', '$1', ['1(?:00|2[13])|9[56]', '1(?:00|2(?:1|39))|9[56]', '1(?:00|2(?:1|395))|9[56]']],
                    [, '(\\d{5,6})', '$1', ['1(?:0|23)|781|[1-9]12', '1(?:0|23)|7812|[1-9]123', '1(?:0|23(?:[0-8]|9[0-46-9]))|78123|[1-9]123']],
                    [
                      ,
                      '(\\d{2})(\\d{5,6})',
                      '$1 $2',
                      [
                        '(?:10|2[0-57-9])[19]|3(?:[157]|35|49|9[1-68])|4(?:1[124-9]|2[179]|6[47-9]|7|8[23])|5(?:[1357]|2[37]|4[36]|6[1-46]|80)|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:07|1[236-8]|2[5-7]|[37]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3|4[13]|5[1-5]|7[0-79]|9[0-35-9])|(?:4[35]|59|85)[1-9]',
                        '(?:10|2[0-57-9])(?:1[02]|9[56])|8078|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))1',
                        '10(?:1(?:0|23)|9[56])|2[0-57-9](?:1(?:00|23)|9[56])|80781|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))12',
                        '10(?:1(?:0|23)|9[56])|2[0-57-9](?:1(?:00|23)|9[56])|807812|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))123',
                        '10(?:1(?:0|23)|9[56])|2[0-57-9](?:1(?:00|23)|9[56])|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:078|1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))123'
                      ],
                      '0$1',
                      '$CC $1'
                    ],
                    [
                      ,
                      '(\\d{3})(\\d{4})',
                      '$1 $2',
                      [
                        '[1-9]',
                        '1[1-9]|26|[3-9]|(?:10|2[0-57-9])(?:[0-8]|9[0-47-9])',
                        '1(?:0(?:[02-8]|1(?:[013-9]|2[0-24-9])|9[0-47-9])|[1-9])|2(?:[0-57-9](?:[02-8]|1(?:0[1-9]|[13-9]|2[0-24-9])|9[0-47-9])|6)|[3-9]',
                        '1(?:0(?:[02-8]|1(?:[013-9]|2[0-24-9])|9[0-47-9])|[1-9])|2(?:[0-57-9](?:[02-8]|1(?:0[1-9]|[13-9]|2[0-24-9])|9[0-47-9])|6)|3(?:[0268]|3[0-46-9]|4[0-8]|9[079])|4(?:[049]|1[03]|2[02-68]|[35]0|6[0-356]|8[014-9])|5(?:0|2[0-24-689]|4[0-2457-9]|6[057-9]|8[1-9]|90)|6(?:[0-24578]|3[06-9]|6[14-79]|9[03-9])|7(?:0[02-9]|2[0135-79]|3[23]|4[0-27-9]|6[1457]|8)|8(?:[046]|1[01459]|2[0-489]|50|8[0-2459]|9[09])|9(?:0[0457]|1[08]|[268]|4[024-9]|5[06-9]|78|94)|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))(?:[02-9]|1(?:[013-9]|2[0-24-9]))',
                        '1(?:0(?:[02-8]|1(?:[013-9]|2[0-24-9])|9[0-47-9])|[1-9])|2(?:[0-57-9](?:[02-8]|1(?:0[1-9]|[13-9]|2[0-24-9])|9[0-47-9])|6)|3(?:[0268]|3[0-46-9]|4[0-8]|9[079])|4(?:[049]|1[03]|2[02-68]|[35]0|6[0-356]|8[014-9])|5(?:0|2[0-24-689]|4[0-2457-9]|6[057-9]|8[1-9]|90)|6(?:[0-24578]|3[06-9]|6[14-79]|9[03-9])|7(?:0[02-9]|2[0135-79]|3[23]|4[0-27-9]|6[1457]|8)|8(?:0(?:[0-689]|7[0-79])|1[01459]|2[0-489]|[46]|50|8[0-2459]|9[09])|9(?:0[0457]|1[08]|[268]|4[024-9]|5[06-9]|78|94)|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:078|1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))(?:[02-9]|1(?:[013-9]|2[0-24-9]))'
                      ]
                    ],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['16[08]']],
                    [
                      ,
                      '(\\d{3})(\\d{5,6})',
                      '$1 $2',
                      [
                        '3(?:[157]|35|49|9[1-68])|4(?:[17]|2[179]|6[47-9]|8[23])|5(?:[1357]|2[37]|4[36]|6[1-46]|80)|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]|4[13]|5[1-5])|(?:4[35]|59|85)[1-9]',
                        '(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))[19]',
                        '85[23](?:10|95)|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))(?:10|9[56])',
                        '85[23](?:100|95)|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))(?:100|9[56])'
                      ],
                      '0$1',
                      '$CC $1'
                    ],
                    [
                      ,
                      '(\\d{4})(\\d{4})',
                      '$1 $2',
                      [
                        '[1-9]',
                        '1(?:0(?:[02-8]|1[1-9]|9[0-47-9])|[1-9])|2(?:[0-57-9](?:[0-8]|9[0-47-9])|6)|[3-9]',
                        '1(?:0(?:[02-8]|1[1-9]|9[0-47-9])|[1-9])|26|3(?:[0268]|4[0-8]|9[079])|4(?:[049]|2[02-68]|[35]0|6[0-356]|8[014-9])|5(?:0|2[0-24-689]|4[0-2457-9]|6[057-9]|8[1-9]|90)|6(?:[0-24578]|3[06-9]|6[14-79]|9[03-9])|7(?:0[02-9]|2[0135-79]|3[23]|4[0-27-9]|6[1457]|8)|8(?:[046]|1[01459]|2[0-489]|5(?:0|[23][0-8])|8[0-2459]|9[09])|9(?:0[0457]|1[08]|[268]|4[024-9]|5[06-9])|(?:33|85[23]9)[0-46-9]|(?:2[0-57-9]|3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))(?:[0-8]|9[0-47-9])',
                        '1(?:0[02-8]|[1-9])|2(?:[0-57-9][0-8]|6)|3(?:[0268]|3[0-46-9]|4[0-8]|9[079])|4(?:[049]|2[02-68]|[35]0|6[0-356]|8[014-9])|5(?:0|2[0-24-689]|4[0-2457-9]|6[057-9]|90)|6(?:[0-24578]|3[06-9]|6[14-79]|9[03-9])|7(?:0[02-9]|2[0135-79]|3[23]|4[0-27-9]|6[1457]|8)|8(?:[046]|1[01459]|2[0-489]|5(?:0|[23](?:[02-8]|1[1-9]|9[0-46-9]))|8[0-2459]|9[09])|9(?:0[0457]|1[08]|[268]|4[024-9]|5[06-9])|(?:10|2[0-57-9])9[0-47-9]|(?:101|58|85[23]10)[1-9]|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))(?:[02-8]|1(?:0[1-9]|[1-9])|9[0-47-9])'
                      ]
                    ],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['(?:4|80)0']],
                    [
                      ,
                      '(\\d{2})(\\d{4})(\\d{4})',
                      '$1 $2 $3',
                      ['10|2(?:[02-57-9]|1[1-9])', '10|2(?:[02-57-9]|1[1-9])', '10[0-79]|2(?:[02-57-9]|1[1-79])|(?:10|21)8(?:0[1-9]|[1-9])'],
                      '0$1',
                      '$CC $1',
                      1
                    ],
                    [
                      ,
                      '(\\d{3})(\\d{3})(\\d{4})',
                      '$1 $2 $3',
                      [
                        '3(?:[3-59]|7[02-68])|4(?:[26-8]|3[3-9]|5[2-9])|5(?:3[03-9]|[468]|7[028]|9[2-46-9])|6|7(?:[0-247]|3[04-9]|5[0-4689]|6[2368])|8(?:[1-358]|9[1-7])|9(?:[013479]|5[1-5])|(?:[34]1|55|79|87)[02-9]'
                      ],
                      '0$1',
                      '$CC $1',
                      1
                    ],
                    [, '(\\d{3})(\\d{7,8})', '$1 $2', ['9']],
                    [, '(\\d{4})(\\d{3})(\\d{4})', '$1 $2 $3', ['80'], '0$1', '$CC $1', 1],
                    [, '(\\d{3})(\\d{4})(\\d{4})', '$1 $2 $3', ['[3-578]'], '0$1', '$CC $1', 1],
                    [, '(\\d{3})(\\d{4})(\\d{4})', '$1 $2 $3', ['1[3-9]'], , '$CC $1'],
                    [, '(\\d{2})(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3 $4', ['[12]'], '0$1', , 1]
                  ],
                  [
                    [
                      ,
                      '(\\d{2})(\\d{5,6})',
                      '$1 $2',
                      [
                        '(?:10|2[0-57-9])[19]|3(?:[157]|35|49|9[1-68])|4(?:1[124-9]|2[179]|6[47-9]|7|8[23])|5(?:[1357]|2[37]|4[36]|6[1-46]|80)|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:07|1[236-8]|2[5-7]|[37]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3|4[13]|5[1-5]|7[0-79]|9[0-35-9])|(?:4[35]|59|85)[1-9]',
                        '(?:10|2[0-57-9])(?:1[02]|9[56])|8078|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))1',
                        '10(?:1(?:0|23)|9[56])|2[0-57-9](?:1(?:00|23)|9[56])|80781|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))12',
                        '10(?:1(?:0|23)|9[56])|2[0-57-9](?:1(?:00|23)|9[56])|807812|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))123',
                        '10(?:1(?:0|23)|9[56])|2[0-57-9](?:1(?:00|23)|9[56])|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:078|1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))123'
                      ],
                      '0$1',
                      '$CC $1'
                    ],
                    [
                      ,
                      '(\\d{3})(\\d{5,6})',
                      '$1 $2',
                      [
                        '3(?:[157]|35|49|9[1-68])|4(?:[17]|2[179]|6[47-9]|8[23])|5(?:[1357]|2[37]|4[36]|6[1-46]|80)|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]|4[13]|5[1-5])|(?:4[35]|59|85)[1-9]',
                        '(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))[19]',
                        '85[23](?:10|95)|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))(?:10|9[56])',
                        '85[23](?:100|95)|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))(?:100|9[56])'
                      ],
                      '0$1',
                      '$CC $1'
                    ],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['(?:4|80)0']],
                    [
                      ,
                      '(\\d{2})(\\d{4})(\\d{4})',
                      '$1 $2 $3',
                      ['10|2(?:[02-57-9]|1[1-9])', '10|2(?:[02-57-9]|1[1-9])', '10[0-79]|2(?:[02-57-9]|1[1-79])|(?:10|21)8(?:0[1-9]|[1-9])'],
                      '0$1',
                      '$CC $1',
                      1
                    ],
                    [
                      ,
                      '(\\d{3})(\\d{3})(\\d{4})',
                      '$1 $2 $3',
                      [
                        '3(?:[3-59]|7[02-68])|4(?:[26-8]|3[3-9]|5[2-9])|5(?:3[03-9]|[468]|7[028]|9[2-46-9])|6|7(?:[0-247]|3[04-9]|5[0-4689]|6[2368])|8(?:[1-358]|9[1-7])|9(?:[013479]|5[1-5])|(?:[34]1|55|79|87)[02-9]'
                      ],
                      '0$1',
                      '$CC $1',
                      1
                    ],
                    [, '(\\d{3})(\\d{7,8})', '$1 $2', ['9']],
                    [, '(\\d{4})(\\d{3})(\\d{4})', '$1 $2 $3', ['80'], '0$1', '$CC $1', 1],
                    [, '(\\d{3})(\\d{4})(\\d{4})', '$1 $2 $3', ['[3-578]'], '0$1', '$CC $1', 1],
                    [, '(\\d{3})(\\d{4})(\\d{4})', '$1 $2 $3', ['1[3-9]'], , '$CC $1'],
                    [, '(\\d{2})(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3 $4', ['[12]'], '0$1', , 1]
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '(?:(?:10|21)8|[48])00\\d{7}|950\\d{7,8}', , , , , , , [10, 11, 12]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CO: [
                  ,
                  [, , '(?:46|60\\d\\d)\\d{6}|(?:1\\d|[39])\\d{9}', , , , , , , [8, 10, 11], [4, 7]],
                  [
                    ,
                    ,
                    '601055(?:[0-4]\\d|50)\\d\\d|6010(?:[0-4]\\d|5[0-4])\\d{4}|(?:46|60(?:[18][1-9]|[24-7][2-9]))\\d{6}',
                    ,
                    ,
                    ,
                    '6012345678',
                    ,
                    ,
                    [8, 10],
                    [4, 7]
                  ],
                  [
                    ,
                    ,
                    '333301[0-5]\\d{3}|3333(?:00|2[5-9]|[3-9]\\d)\\d{4}|(?:3(?:(?:0[0-5]|1\\d|5[01]|70)\\d|2(?:[0-3]\\d|4[1-9])|3(?:00|3[0-24-9]))|9(?:101|408))\\d{6}',
                    ,
                    ,
                    ,
                    '3211234567',
                    ,
                    ,
                    [10]
                  ],
                  [, , '1800\\d{7}', , , , '18001234567', , , [11]],
                  [, , '(?:19(?:0[01]|4[78])|901)\\d{7}', , , , '19001234567', , , [10, 11]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'CO',
                  57,
                  '00(?:4(?:[14]4|56)|[579])',
                  '0',
                  ,
                  ,
                  '0([3579]|4(?:[14]4|56))?',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['46']],
                    [, '(\\d{3})(\\d{7})', '$1 $2', ['6|90'], '($1)', '0$CC $1'],
                    [, '(\\d{3})(\\d{7})', '$1 $2', ['3[0-357]|9[14]'], , '0$CC $1'],
                    [, '(\\d)(\\d{3})(\\d{7})', '$1-$2-$3', ['1'], '0$1']
                  ],
                  [
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['46']],
                    [, '(\\d{3})(\\d{7})', '$1 $2', ['6|90'], '($1)', '0$CC $1'],
                    [, '(\\d{3})(\\d{7})', '$1 $2', ['3[0-357]|9[14]'], , '0$CC $1'],
                    [, '(\\d)(\\d{3})(\\d{7})', '$1 $2 $3', ['1']]
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CR: [
                  ,
                  [, , '(?:8\\d|90)\\d{8}|(?:[24-8]\\d{3}|3005)\\d{4}', , , , , , , [8, 10]],
                  [, , '210[7-9]\\d{4}|2(?:[024-7]\\d|1[1-9])\\d{5}', , , , '22123456', , , [8]],
                  [, , '(?:3005\\d|6500[01])\\d{3}|(?:5[07]|6[0-4]|7[0-3]|8[3-9])\\d{6}', , , , '83123456', , , [8]],
                  [, , '800\\d{7}', , , , '8001234567', , , [10]],
                  [, , '90[059]\\d{7}', , , , '9001234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '(?:210[0-6]|4\\d{3}|5100)\\d{4}', , , , '40001234', , , [8]],
                  'CR',
                  506,
                  '00',
                  ,
                  ,
                  ,
                  '(19(?:0[0-2468]|1[09]|20|66|77|99))',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[2-7]|8[3-9]'], , '$CC $1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1-$2-$3', ['[89]'], , '$CC $1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CU: [
                  ,
                  [, , '(?:[2-7]|8\\d\\d)\\d{7}|[2-47]\\d{6}|[34]\\d{5}', , , , , , , [6, 7, 8, 10], [4, 5]],
                  [, , '(?:3[23]|4[89])\\d{4,6}|(?:31|4[36]|8(?:0[25]|78)\\d)\\d{6}|(?:2[1-4]|4[1257]|7\\d)\\d{5,6}', , , , '71234567', , , , [4, 5]],
                  [, , '(?:5\\d|6[2-4])\\d{6}', , , , '51234567', , , [8]],
                  [, , '800\\d{7}', , , , '8001234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , '807\\d{7}', , , , '8071234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'CU',
                  53,
                  '119',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{4,6})', '$1 $2', ['2[1-4]|[34]'], '(0$1)'],
                    [, '(\\d)(\\d{6,7})', '$1 $2', ['7'], '(0$1)'],
                    [, '(\\d)(\\d{7})', '$1 $2', ['[56]'], '0$1'],
                    [, '(\\d{3})(\\d{7})', '$1 $2', ['8'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CV: [
                  ,
                  [, , '(?:[2-59]\\d\\d|800)\\d{4}', , , , , , , [7]],
                  [, , '2(?:2[1-7]|3[0-8]|4[12]|5[1256]|6\\d|7[1-3]|8[1-5])\\d{4}', , , , '2211234'],
                  [, , '(?:36|5[1-389]|9\\d)\\d{5}', , , , '9911234'],
                  [, , '800\\d{4}', , , , '8001234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '(?:3[3-5]|4[356])\\d{5}', , , , '3401234'],
                  'CV',
                  238,
                  '0',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3', ['[2-589]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CW: [
                  ,
                  [, , '(?:[34]1|60|(?:7|9\\d)\\d)\\d{5}', , , , , , , [7, 8]],
                  [
                    ,
                    ,
                    '9(?:4(?:3[0-5]|4[14]|6\\d)|50\\d|7(?:2[014]|3[02-9]|4[4-9]|6[357]|77|8[7-9])|8(?:3[39]|[46]\\d|7[01]|8[57-9]))\\d{4}',
                    ,
                    ,
                    ,
                    '94351234'
                  ],
                  [, , '953[01]\\d{4}|9(?:5[12467]|6[5-9])\\d{5}', , , , '95181234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '60[0-2]\\d{4}', , , , '6001234', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'CW',
                  599,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['[3467]']],
                    [, '(\\d)(\\d{3})(\\d{4})', '$1 $2 $3', ['9[4-8]']]
                  ],
                  ,
                  [, , '955\\d{5}', , , , '95581234', , , [8]],
                  1,
                  '[69]',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CX: [
                  ,
                  [, , '1(?:[0-79]\\d{8}(?:\\d{2})?|8[0-24-9]\\d{7})|[148]\\d{8}|1\\d{5,7}', , , , , , , [6, 7, 8, 9, 10, 12]],
                  [
                    ,
                    ,
                    '8(?:51(?:0(?:01|30|59|88)|1(?:17|46|75)|2(?:22|35))|91(?:00[6-9]|1(?:[28]1|49|78)|2(?:09|63)|3(?:12|26|75)|4(?:56|97)|64\\d|7(?:0[01]|1[0-2])|958))\\d{3}',
                    ,
                    ,
                    ,
                    '891641234',
                    ,
                    ,
                    [9],
                    [8]
                  ],
                  [, , '4(?:79[01]|83[0-389]|94[0-478])\\d{5}|4(?:[0-36]\\d|4[047-9]|[58][0-24-9]|7[02-8]|9[0-37-9])\\d{6}', , , , '412345678', , , [9]],
                  [, , '180(?:0\\d{3}|2)\\d{3}', , , , '1800123456', , , [7, 10]],
                  [, , '190[0-26]\\d{6}', , , , '1900123456', , , [10]],
                  [, , '13(?:00\\d{6}(?:\\d{2})?|45[0-4]\\d{3})|13\\d{4}', , , , '1300123456', , , [6, 8, 10, 12]],
                  [, , , , , , , , , [-1]],
                  [, , '14(?:5(?:1[0458]|[23][458])|71\\d)\\d{4}', , , , '147101234', , , [9]],
                  'CX',
                  61,
                  '001[14-689]|14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011',
                  '0',
                  ,
                  ,
                  '([59]\\d{7})$|0',
                  '8$1',
                  '0011',
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CY: [
                  ,
                  [, , '(?:[279]\\d|[58]0)\\d{6}', , , , , , , [8]],
                  [, , '2[2-6]\\d{6}', , , , '22345678'],
                  [, , '9(?:10|[4-79]\\d)\\d{5}', , , , '96123456'],
                  [, , '800\\d{5}', , , , '80001234'],
                  [, , '90[09]\\d{5}', , , , '90012345'],
                  [, , '80[1-9]\\d{5}', , , , '80112345'],
                  [, , '700\\d{5}', , , , '70012345'],
                  [, , , , , , , , , [-1]],
                  'CY',
                  357,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{6})', '$1 $2', ['[257-9]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '(?:50|77)\\d{6}', , , , '77123456'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CZ: [
                  ,
                  [, , '(?:[2-578]\\d|60)\\d{7}|9\\d{8,11}', , , , , , , [9, 10, 11, 12]],
                  [, , '(?:2\\d|3[1257-9]|4[16-9]|5[13-9])\\d{7}', , , , '212345678', , , [9]],
                  [, , '7(?:060\\d|19(?:[0-4]\\d|50))\\d{4}|(?:60[1-8]|7(?:0[2-5]|[2379]\\d))\\d{6}', , , , '601123456', , , [9]],
                  [, , '800\\d{6}', , , , '800123456', , , [9]],
                  [, , '9(?:0[05689]|76)\\d{6}', , , , '900123456', , , [9]],
                  [, , '8[134]\\d{7}', , , , '811234567', , , [9]],
                  [, , '70[01]\\d{6}', , , , '700123456', , , [9]],
                  [, , '9[17]0\\d{6}', , , , '910123456', , , [9]],
                  'CZ',
                  420,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[2-8]|9[015-7]']],
                    [, '(\\d{2})(\\d{3})(\\d{3})(\\d{2})', '$1 $2 $3 $4', ['96']],
                    [, '(\\d{2})(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['9']],
                    [, '(\\d{3})(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['9']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '9(?:5\\d|7[2-4])\\d{6}', , , , '972123456', , , [9]],
                  ,
                  ,
                  [, , '9(?:3\\d{9}|6\\d{7,10})', , , , '93123456789']
                ],
                DE: [
                  ,
                  [
                    ,
                    ,
                    '[2579]\\d{5,14}|49(?:[34]0|69|8\\d)\\d\\d?|49(?:37|49|60|7[089]|9\\d)\\d{1,3}|49(?:2[024-9]|3[2-689]|7[1-7])\\d{1,8}|(?:1|[368]\\d|4[0-8])\\d{3,13}|49(?:[015]\\d|2[13]|31|[46][1-8])\\d{1,9}',
                    ,
                    ,
                    ,
                    ,
                    ,
                    ,
                    [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                    [2, 3]
                  ],
                  [
                    ,
                    ,
                    '32\\d{9,11}|49[1-6]\\d{10}|322\\d{6}|49[0-7]\\d{3,9}|(?:[34]0|[68]9)\\d{3,13}|(?:2(?:0[1-689]|[1-3569]\\d|4[0-8]|7[1-7]|8[0-7])|3(?:[3569]\\d|4[0-79]|7[1-7]|8[1-8])|4(?:1[02-9]|[2-48]\\d|5[0-6]|6[0-8]|7[0-79])|5(?:0[2-8]|[124-6]\\d|[38][0-8]|[79][0-7])|6(?:0[02-9]|[1-358]\\d|[47][0-8]|6[1-9])|7(?:0[2-8]|1[1-9]|[27][0-7]|3\\d|[4-6][0-8]|8[0-5]|9[013-7])|8(?:0[2-9]|1[0-79]|2\\d|3[0-46-9]|4[0-6]|5[013-9]|6[1-8]|7[0-8]|8[0-24-6])|9(?:0[6-9]|[1-4]\\d|[589][0-7]|6[0-8]|7[0-467]))\\d{3,12}',
                    ,
                    ,
                    ,
                    '30123456',
                    ,
                    ,
                    [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                    [2, 3, 4]
                  ],
                  [, , '1(?:(?:5(?:[0-25-9]\\d\\d|3(?:10|33))|7[26-9]\\d\\d)\\d{6}|6[023]\\d{7,8})|17\\d{8}', , , , '15123456789', , , [10, 11]],
                  [, , '800\\d{7,12}', , , , '8001234567890', , , [10, 11, 12, 13, 14, 15]],
                  [, , '(?:137[7-9]|900(?:[135]|9\\d))\\d{6}', , , , '9001234567', , , [10, 11]],
                  [, , '180\\d{5,11}|13(?:7[1-6]\\d\\d|8)\\d{4}', , , , '18012345', , , [7, 8, 9, 10, 11, 12, 13, 14]],
                  [, , '700\\d{8}', , , , '70012345678', , , [11]],
                  [, , , , , , , , , [-1]],
                  'DE',
                  49,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3,13})', '$1 $2', ['3[02]|40|[68]9'], '0$1'],
                    [, '(\\d{6})', '$1', ['227', '2277']],
                    [
                      ,
                      '(\\d{3})(\\d{3,12})',
                      '$1 $2',
                      [
                        '2(?:0[1-389]|1[124]|2[18]|3[14])|3(?:[35-9][15]|4[015])|906|(?:2[4-9]|4[2-9]|[579][1-9]|[68][1-8])1',
                        '2(?:0[1-389]|12[0-8])|3(?:[35-9][15]|4[015])|906|2(?:[13][14]|2[18])|(?:2[4-9]|4[2-9]|[579][1-9]|[68][1-8])1'
                      ],
                      '0$1'
                    ],
                    [
                      ,
                      '(\\d{4})(\\d{2,11})',
                      '$1 $2',
                      [
                        '[24-6]|3(?:[3569][02-46-9]|4[2-4679]|7[2-467]|8[2-46-8])|70[2-8]|8(?:0[2-9]|[1-8])|90[7-9]|[79][1-9]',
                        '[24-6]|3(?:3(?:0[1-467]|2[127-9]|3[124578]|7[1257-9]|8[1256]|9[145])|4(?:2[135]|4[13578]|9[1346])|5(?:0[14]|2[1-3589]|6[1-4]|7[13468]|8[13568])|6(?:2[1-489]|3[124-6]|6[13]|7[12579]|8[1-356]|9[135])|7(?:2[1-7]|4[145]|6[1-5]|7[1-4])|8(?:21|3[1468]|6|7[1467]|8[136])|9(?:0[12479]|2[1358]|4[134679]|6[1-9]|7[136]|8[147]|9[1468]))|70[2-8]|8(?:0[2-9]|[1-8])|90[7-9]|[79][1-9]|3[68]4[1347]|3(?:47|60)[1356]|3(?:3[46]|46|5[49])[1246]|3[4579]3[1357]'
                      ],
                      '0$1'
                    ],
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['138'], '0$1'],
                    [, '(\\d{5})(\\d{2,10})', '$1 $2', ['3'], '0$1'],
                    [, '(\\d{3})(\\d{5,11})', '$1 $2', ['181'], '0$1'],
                    [, '(\\d{3})(\\d)(\\d{4,10})', '$1 $2 $3', ['1(?:3|80)|9'], '0$1'],
                    [, '(\\d{3})(\\d{7,8})', '$1 $2', ['1[67]'], '0$1'],
                    [, '(\\d{3})(\\d{7,12})', '$1 $2', ['8'], '0$1'],
                    [, '(\\d{5})(\\d{6})', '$1 $2', ['185', '1850', '18500'], '0$1'],
                    [, '(\\d{3})(\\d{4})(\\d{4})', '$1 $2 $3', ['7'], '0$1'],
                    [, '(\\d{4})(\\d{7})', '$1 $2', ['18[68]'], '0$1'],
                    [, '(\\d{4})(\\d{7})', '$1 $2', ['15[1279]'], '0$1'],
                    [, '(\\d{5})(\\d{6})', '$1 $2', ['15[03568]', '15(?:[0568]|3[13])'], '0$1'],
                    [, '(\\d{3})(\\d{8})', '$1 $2', ['18'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{7,8})', '$1 $2 $3', ['1(?:6[023]|7)'], '0$1'],
                    [, '(\\d{4})(\\d{2})(\\d{7})', '$1 $2 $3', ['15[279]'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{8})', '$1 $2 $3', ['15'], '0$1']
                  ],
                  [
                    [, '(\\d{2})(\\d{3,13})', '$1 $2', ['3[02]|40|[68]9'], '0$1'],
                    [
                      ,
                      '(\\d{3})(\\d{3,12})',
                      '$1 $2',
                      [
                        '2(?:0[1-389]|1[124]|2[18]|3[14])|3(?:[35-9][15]|4[015])|906|(?:2[4-9]|4[2-9]|[579][1-9]|[68][1-8])1',
                        '2(?:0[1-389]|12[0-8])|3(?:[35-9][15]|4[015])|906|2(?:[13][14]|2[18])|(?:2[4-9]|4[2-9]|[579][1-9]|[68][1-8])1'
                      ],
                      '0$1'
                    ],
                    [
                      ,
                      '(\\d{4})(\\d{2,11})',
                      '$1 $2',
                      [
                        '[24-6]|3(?:[3569][02-46-9]|4[2-4679]|7[2-467]|8[2-46-8])|70[2-8]|8(?:0[2-9]|[1-8])|90[7-9]|[79][1-9]',
                        '[24-6]|3(?:3(?:0[1-467]|2[127-9]|3[124578]|7[1257-9]|8[1256]|9[145])|4(?:2[135]|4[13578]|9[1346])|5(?:0[14]|2[1-3589]|6[1-4]|7[13468]|8[13568])|6(?:2[1-489]|3[124-6]|6[13]|7[12579]|8[1-356]|9[135])|7(?:2[1-7]|4[145]|6[1-5]|7[1-4])|8(?:21|3[1468]|6|7[1467]|8[136])|9(?:0[12479]|2[1358]|4[134679]|6[1-9]|7[136]|8[147]|9[1468]))|70[2-8]|8(?:0[2-9]|[1-8])|90[7-9]|[79][1-9]|3[68]4[1347]|3(?:47|60)[1356]|3(?:3[46]|46|5[49])[1246]|3[4579]3[1357]'
                      ],
                      '0$1'
                    ],
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['138'], '0$1'],
                    [, '(\\d{5})(\\d{2,10})', '$1 $2', ['3'], '0$1'],
                    [, '(\\d{3})(\\d{5,11})', '$1 $2', ['181'], '0$1'],
                    [, '(\\d{3})(\\d)(\\d{4,10})', '$1 $2 $3', ['1(?:3|80)|9'], '0$1'],
                    [, '(\\d{3})(\\d{7,8})', '$1 $2', ['1[67]'], '0$1'],
                    [, '(\\d{3})(\\d{7,12})', '$1 $2', ['8'], '0$1'],
                    [, '(\\d{5})(\\d{6})', '$1 $2', ['185', '1850', '18500'], '0$1'],
                    [, '(\\d{3})(\\d{4})(\\d{4})', '$1 $2 $3', ['7'], '0$1'],
                    [, '(\\d{4})(\\d{7})', '$1 $2', ['18[68]'], '0$1'],
                    [, '(\\d{4})(\\d{7})', '$1 $2', ['15[1279]'], '0$1'],
                    [, '(\\d{5})(\\d{6})', '$1 $2', ['15[03568]', '15(?:[0568]|3[13])'], '0$1'],
                    [, '(\\d{3})(\\d{8})', '$1 $2', ['18'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{7,8})', '$1 $2 $3', ['1(?:6[023]|7)'], '0$1'],
                    [, '(\\d{4})(\\d{2})(\\d{7})', '$1 $2 $3', ['15[279]'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{8})', '$1 $2 $3', ['15'], '0$1']
                  ],
                  [, , '16(?:4\\d{1,10}|[89]\\d{1,11})', , , , '16412345', , , [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '18(?:1\\d{5,11}|[2-9]\\d{8})', , , , '18500123456', , , [8, 9, 10, 11, 12, 13, 14]],
                  ,
                  ,
                  [
                    ,
                    ,
                    '1(?:6(?:013|255|399)|7(?:(?:[015]1|[69]3)3|[2-4]55|[78]99))\\d{7,8}|15(?:(?:[03-68]00|113)\\d|2\\d55|7\\d99|9\\d33)\\d{7}',
                    ,
                    ,
                    ,
                    '177991234567',
                    ,
                    ,
                    [12, 13]
                  ]
                ],
                DJ: [
                  ,
                  [, , '(?:2\\d|77)\\d{6}', , , , , , , [8]],
                  [, , '2(?:1[2-5]|7[45])\\d{5}', , , , '21360003'],
                  [, , '77\\d{6}', , , , '77831001'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'DJ',
                  253,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[27]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                DK: [
                  ,
                  [, , '[2-9]\\d{7}', , , , , , , [8]],
                  [
                    ,
                    ,
                    '(?:2(?:[0-59][1-9]|[6-8]\\d)|3(?:[0-3][1-9]|4[13]|5[1-58]|6[1347-9]|7\\d|8[1-8]|9[1-79])|4(?:[0-25][1-9]|[34][2-9]|6[13-579]|7[13579]|8[1-47]|9[127])|5(?:[0-36][1-9]|4[146-9]|5[3-57-9]|7[568]|8[1-358]|9[1-69])|6(?:[0135][1-9]|2[1-68]|4[2-8]|6[1689]|[78]\\d|9[15689])|7(?:[0-69][1-9]|7[3-9]|8[147])|8(?:[16-9][1-9]|2[1-58])|9(?:[1-47-9][1-9]|6\\d))\\d{5}',
                    ,
                    ,
                    ,
                    '32123456'
                  ],
                  [, , '(?:2[6-8]|37|6[78]|96)\\d{6}|(?:2[0-59]|3[0-689]|[457]\\d|6[0-69]|8[126-9]|9[1-47-9])[1-9]\\d{5}', , , , '34412345'],
                  [, , '80\\d{6}', , , , '80123456'],
                  [, , '90\\d{6}', , , , '90123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'DK',
                  45,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[2-9]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                DM: [
                  ,
                  [, , '(?:[58]\\d\\d|767|900)\\d{7}', , , , , , , [10], [7]],
                  [, , '767(?:2(?:55|66)|4(?:2[01]|4[0-25-9])|50[0-4])\\d{4}', , , , '7674201234', , , , [7]],
                  [, , '767(?:2(?:[2-4689]5|7[5-7])|31[5-7]|61[1-8]|70[1-6])\\d{4}', , , , '7672251234', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'DM',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-7]\\d{6})$|1',
                  '767$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '767',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                DO: [
                  ,
                  [, , '(?:[58]\\d\\d|900)\\d{7}', , , , , , , [10], [7]],
                  [
                    ,
                    ,
                    '8(?:[04]9[2-9]\\d\\d|29(?:2(?:[0-59]\\d|6[04-9]|7[0-27]|8[0237-9])|3(?:[0-35-9]\\d|4[7-9])|[45]\\d\\d|6(?:[0-27-9]\\d|[3-5][1-9]|6[0135-8])|7(?:0[013-9]|[1-37]\\d|4[1-35689]|5[1-4689]|6[1-57-9]|8[1-79]|9[1-8])|8(?:0[146-9]|1[0-48]|[248]\\d|3[1-79]|5[01589]|6[013-68]|7[124-8]|9[0-8])|9(?:[0-24]\\d|3[02-46-9]|5[0-79]|60|7[0169]|8[57-9]|9[02-9])))\\d{4}',
                    ,
                    ,
                    ,
                    '8092345678',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '8[024]9[2-9]\\d{6}', , , , '8092345678', , , , [7]],
                  [, , '800(?:14|[2-9]\\d)\\d{5}|8[024]9[01]\\d{6}|8(?:33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'DO',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '1',
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '8001|8[024]9',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                DZ: [
                  ,
                  [, , '(?:[1-4]|[5-79]\\d|80)\\d{7}', , , , , , , [8, 9]],
                  [, , '9619\\d{5}|(?:1\\d|2[013-79]|3[0-8]|4[013-689])\\d{6}', , , , '12345678'],
                  [, , '(?:5(?:4[0-29]|5\\d|6[0-3])|6(?:[569]\\d|7[0-6])|7[7-9]\\d)\\d{6}', , , , '551234567', , , [9]],
                  [, , '800\\d{6}', , , , '800123456', , , [9]],
                  [, , '80[3-689]1\\d{5}', , , , '808123456', , , [9]],
                  [, , '80[12]1\\d{5}', , , , '801123456', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , '98[23]\\d{6}', , , , '983123456', , , [9]],
                  'DZ',
                  213,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[1-4]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['9'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[5-8]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                EC: [
                  ,
                  [, , '1\\d{9,10}|(?:[2-7]|9\\d)\\d{7}', , , , , , , [8, 9, 10, 11], [7]],
                  [, , '[2-7][2-7]\\d{6}', , , , '22123456', , , [8], [7]],
                  [, , '964[0-2]\\d{5}|9(?:39|[57][89]|6[0-36-9]|[89]\\d)\\d{6}', , , , '991234567', , , [9]],
                  [, , '1800\\d{7}|1[78]00\\d{6}', , , , '18001234567', , , [10, 11]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '[2-7]890\\d{4}', , , , '28901234', , , [8]],
                  'EC',
                  593,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1-$2', ['[2-7]']],
                    [, '(\\d)(\\d{3})(\\d{4})', '$1 $2-$3', ['[2-7]'], '(0$1)'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['9'], '0$1'],
                    [, '(\\d{4})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['1']]
                  ],
                  [
                    [, '(\\d)(\\d{3})(\\d{4})', '$1-$2-$3', ['[2-7]']],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['9'], '0$1'],
                    [, '(\\d{4})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['1']]
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                EE: [
                  ,
                  [, , '8\\d{9}|[4578]\\d{7}|(?:[3-8]\\d|90)\\d{5}', , , , , , , [7, 8, 10]],
                  [, , '(?:3[23589]|4[3-8]|6\\d|7[1-9]|88)\\d{5}', , , , '3212345', , , [7]],
                  [
                    ,
                    ,
                    '(?:5\\d{5}|8(?:1(?:0(?:0(?:00|[178]\\d)|[3-9]\\d\\d)|(?:1(?:0[2-6]|1\\d)|(?:2[0-59]|[3-79]\\d)\\d)\\d)|2(?:0(?:0(?:00|4\\d)|(?:19|[2-7]\\d)\\d)|(?:(?:[124-69]\\d|3[5-9])\\d|7(?:[0-79]\\d|8[13-9])|8(?:[2-6]\\d|7[01]))\\d)|[349]\\d{4}))\\d\\d|5(?:(?:[02]\\d|5[0-478])\\d|1(?:[0-8]\\d|95)|6(?:4[0-4]|5[1-589]))\\d{3}',
                    ,
                    ,
                    ,
                    '51234567',
                    ,
                    ,
                    [7, 8]
                  ],
                  [, , '800(?:(?:0\\d\\d|1)\\d|[2-9])\\d{3}', , , , '80012345'],
                  [, , '(?:40\\d\\d|900)\\d{4}', , , , '9001234', , , [7, 8]],
                  [, , , , , , , , , [-1]],
                  [, , '70[0-2]\\d{5}', , , , '70012345', , , [8]],
                  [, , , , , , , , , [-1]],
                  'EE',
                  372,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [
                      ,
                      '(\\d{3})(\\d{4})',
                      '$1 $2',
                      ['[369]|4[3-8]|5(?:[0-2]|5[0-478]|6[45])|7[1-9]|88', '[369]|4[3-8]|5(?:[02]|1(?:[0-8]|95)|5[0-478]|6(?:4[0-4]|5[1-589]))|7[1-9]|88']
                    ],
                    [, '(\\d{4})(\\d{3,4})', '$1 $2', ['[45]|8(?:00|[1-49])', '[45]|8(?:00[1-9]|[1-49])']],
                    [, '(\\d{2})(\\d{2})(\\d{4})', '$1 $2 $3', ['7']],
                    [, '(\\d{4})(\\d{3})(\\d{3})', '$1 $2 $3', ['8']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '800[2-9]\\d{3}', , , , , , , [7]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                EG: [
                  ,
                  [, , '[189]\\d{8,9}|[24-6]\\d{8}|[135]\\d{7}', , , , , , , [8, 9, 10], [6, 7]],
                  [, , '13[23]\\d{6}|(?:15|57)\\d{6,7}|(?:2\\d|3|4[05-8]|5[05]|6[24-689]|8[2468]|9[235-7])\\d{7}', , , , '234567890', , , [8, 9], [6, 7]],
                  [, , '1[0-25]\\d{8}', , , , '1001234567', , , [10]],
                  [, , '800\\d{7}', , , , '8001234567', , , [10]],
                  [, , '900\\d{7}', , , , '9001234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'EG',
                  20,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{7,8})', '$1 $2', ['[23]'], '0$1'],
                    [, '(\\d{2})(\\d{6,7})', '$1 $2', ['1[35]|[4-6]|8[2468]|9[235-7]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['[89]'], '0$1'],
                    [, '(\\d{2})(\\d{8})', '$1 $2', ['1'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                EH: [
                  ,
                  [, , '[5-8]\\d{8}', , , , , , , [9]],
                  [, , '528[89]\\d{5}', , , , '528812345'],
                  [, , '(?:6(?:[0-79]\\d|8[0-247-9])|7(?:[0167]\\d|2[0-8]|5[0-5]|8[0-7]))\\d{6}', , , , '650123456'],
                  [, , '80[0-7]\\d{6}', , , , '801234567'],
                  [, , '89\\d{7}', , , , '891234567'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '(?:592(?:4[0-2]|93)|80[89]\\d\\d)\\d{4}', , , , '592401234'],
                  'EH',
                  212,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '528[89]',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ER: [
                  ,
                  [, , '[178]\\d{6}', , , , , , , [7], [6]],
                  [, , '(?:1(?:1[12568]|[24]0|55|6[146])|8\\d\\d)\\d{4}', , , , '8370362', , , , [6]],
                  [, , '(?:17[1-3]|7\\d\\d)\\d{4}', , , , '7123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'ER',
                  291,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [[, '(\\d)(\\d{3})(\\d{3})', '$1 $2 $3', ['[178]'], '0$1']],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ES: [
                  ,
                  [, , '[5-9]\\d{8}', , , , , , , [9]],
                  [
                    ,
                    ,
                    '96906(?:0[0-8]|1[1-9]|[2-9]\\d)\\d\\d|9(?:69(?:0[0-57-9]|[1-9]\\d)|73(?:[0-8]\\d|9[1-9]))\\d{4}|(?:8(?:[1356]\\d|[28][0-8]|[47][1-9])|9(?:[135]\\d|[268][0-8]|4[1-9]|7[124-9]))\\d{6}',
                    ,
                    ,
                    ,
                    '810123456'
                  ],
                  [, , '(?:590[16]00\\d|9(?:6906(?:09|10)|7390\\d\\d))\\d\\d|(?:6\\d|7[1-48])\\d{7}', , , , '612345678'],
                  [, , '[89]00\\d{6}', , , , '800123456'],
                  [, , '80[367]\\d{6}', , , , '803123456'],
                  [, , '90[12]\\d{6}', , , , '901123456'],
                  [, , '70\\d{7}', , , , '701234567'],
                  [, , , , , , , , , [-1]],
                  'ES',
                  34,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})', '$1', ['905']],
                    [, '(\\d{6})', '$1', ['[79]9']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[89]00']],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[5-9]']]
                  ],
                  [
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[89]00']],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[5-9]']]
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '51\\d{7}', , , , '511234567'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ET: [
                  ,
                  [, , '(?:11|[2-579]\\d)\\d{7}', , , , , , , [9], [7]],
                  [
                    ,
                    ,
                    '11667[01]\\d{3}|(?:11(?:1(?:1[1-468]|2[2-7]|3[1-5]|5[5-8]|8[6-8])|2(?:13|3[6-8]|5[89]|7[05-9]|8[2-6])|3(?:2[01]|3[0-289]|4[1289]|7[1-4]|87)|4(?:1[69]|3[2-49]|4[0-3]|6[5-8]|7\\d)|5(?:1[578]|44|5[0-4])|6(?:1[578]|2[69]|39|4[5-7]|5[0-5]|6[0-59]|8[015-8]))|2(?:2(?:11[1-9]|22[0-7]|33\\d|44[1467]|66[1-68])|5(?:11[124-6]|33[2-8]|44[1467]|55[14]|66[1-3679]|77[124-79]|880))|3(?:3(?:11[0-46-8]|(?:22|55)[0-6]|33[0134689]|44[04]|66[01467])|4(?:44[0-8]|55[0-69]|66[0-3]|77[1-5]))|4(?:6(?:119|22[0-24-7]|33[1-5]|44[13-69]|55[14-689]|660|88[1-4])|7(?:(?:11|22)[1-9]|33[13-7]|44[13-6]|55[1-689]))|5(?:7(?:227|55[05]|(?:66|77)[14-8])|8(?:11[149]|22[013-79]|33[0-68]|44[013-8]|550|66[1-5]|77\\d)))\\d{4}',
                    ,
                    ,
                    ,
                    '111112345',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '700[1-9]\\d{5}|(?:7(?:0[1-9]|1[0-8]|22|77|86|99)|9\\d\\d)\\d{6}', , , , '911234567'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'ET',
                  251,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[1-579]'], '0$1']],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                FI: [
                  ,
                  [, , '[1-35689]\\d{4}|7\\d{10,11}|(?:[124-7]\\d|3[0-46-9])\\d{8}|[1-9]\\d{5,8}', , , , , , , [5, 6, 7, 8, 9, 10, 11, 12]],
                  [, , '1[3-7][1-8]\\d{3,6}|(?:19[1-8]|[23568][1-8]\\d|9(?:00|[1-8]\\d))\\d{2,6}', , , , '131234567', , , [5, 6, 7, 8, 9]],
                  [, , '4946\\d{2,6}|(?:4[0-8]|50)\\d{4,8}', , , , '412345678', , , [6, 7, 8, 9, 10]],
                  [, , '800\\d{4,6}', , , , '800123456', , , [7, 8, 9]],
                  [, , '[67]00\\d{5,6}', , , , '600123456', , , [8, 9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'FI',
                  358,
                  '00|99(?:[01469]|5(?:[14]1|3[23]|5[59]|77|88|9[09]))',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  '00',
                  ,
                  [
                    [, '(\\d{5})', '$1', ['75[12]'], '0$1'],
                    [, '(\\d{5})', '$1', ['20[2-59]'], '0$1'],
                    [, '(\\d{6})', '$1', ['11']],
                    [, '(\\d{3})(\\d{3,7})', '$1 $2', ['(?:[1-3]0|[68])0|70[07-9]'], '0$1'],
                    [, '(\\d{2})(\\d{4,8})', '$1 $2', ['[14]|2[09]|50|7[135]'], '0$1'],
                    [, '(\\d{2})(\\d{6,10})', '$1 $2', ['7'], '0$1'],
                    [, '(\\d)(\\d{4,9})', '$1 $2', ['(?:19|[2568])[1-8]|3(?:0[1-9]|[1-9])|9'], '0$1']
                  ],
                  [
                    [, '(\\d{5})', '$1', ['20[2-59]'], '0$1'],
                    [, '(\\d{3})(\\d{3,7})', '$1 $2', ['(?:[1-3]0|[68])0|70[07-9]'], '0$1'],
                    [, '(\\d{2})(\\d{4,8})', '$1 $2', ['[14]|2[09]|50|7[135]'], '0$1'],
                    [, '(\\d{2})(\\d{6,10})', '$1 $2', ['7'], '0$1'],
                    [, '(\\d)(\\d{4,9})', '$1 $2', ['(?:19|[2568])[1-8]|3(?:0[1-9]|[1-9])|9'], '0$1']
                  ],
                  [, , , , , , , , , [-1]],
                  1,
                  '1[03-79]|[2-9]',
                  [, , '20(?:2[023]|9[89])\\d{1,6}|(?:60[12]\\d|7099)\\d{4,5}|(?:606|7(?:0[78]|1|3\\d))\\d{7}|(?:[1-3]00|7(?:0[1-5]\\d\\d|5[03-9]))\\d{3,7}'],
                  [
                    ,
                    ,
                    '20\\d{4,8}|60[12]\\d{5,6}|7(?:099\\d{4,5}|5[03-9]\\d{3,7})|20[2-59]\\d\\d|(?:606|7(?:0[78]|1|3\\d))\\d{7}|(?:10|29|3[09]|70[1-5]\\d)\\d{4,8}',
                    ,
                    ,
                    ,
                    '10112345'
                  ],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                FJ: [
                  ,
                  [, , '45\\d{5}|(?:0800\\d|[235-9])\\d{6}', , , , , , , [7, 11]],
                  [, , '603\\d{4}|(?:3[0-5]|6[25-7]|8[58])\\d{5}', , , , '3212345', , , [7]],
                  [, , '(?:[279]\\d|45|5[01568]|8[034679])\\d{5}', , , , '7012345', , , [7]],
                  [, , '0800\\d{7}', , , , '08001234567', , , [11]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'FJ',
                  679,
                  '0(?:0|52)',
                  ,
                  ,
                  ,
                  ,
                  ,
                  '00',
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['[235-9]|45']],
                    [, '(\\d{4})(\\d{3})(\\d{4})', '$1 $2 $3', ['0']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                FK: [
                  ,
                  [, , '[2-7]\\d{4}', , , , , , , [5]],
                  [, , '[2-47]\\d{4}', , , , '31234'],
                  [, , '[56]\\d{4}', , , , '51234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'FK',
                  500,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                FM: [
                  ,
                  [, , '(?:[39]\\d\\d|820)\\d{4}', , , , , , , [7]],
                  [, , '31(?:00[67]|208|309)\\d\\d|(?:3(?:[2357]0[1-9]|602|804|905)|(?:820|9[2-6]\\d)\\d)\\d{3}', , , , '3201234'],
                  [, , '31(?:00[67]|208|309)\\d\\d|(?:3(?:[2357]0[1-9]|602|804|905)|(?:820|9[2-7]\\d)\\d)\\d{3}', , , , '3501234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'FM',
                  691,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{4})', '$1 $2', ['[389]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                FO: [
                  ,
                  [, , '[2-9]\\d{5}', , , , , , , [6]],
                  [, , '(?:20|[34]\\d|8[19])\\d{4}', , , , '201234'],
                  [, , '(?:[27][1-9]|5\\d|9[16])\\d{4}', , , , '211234'],
                  [, , '80[257-9]\\d{3}', , , , '802123'],
                  [, , '90(?:[13-5][15-7]|2[125-7]|9\\d)\\d\\d', , , , '901123'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '(?:6[0-36]|88)\\d{4}', , , , '601234'],
                  'FO',
                  298,
                  '00',
                  ,
                  ,
                  ,
                  '(10(?:01|[12]0|88))',
                  ,
                  ,
                  ,
                  [[, '(\\d{6})', '$1', ['[2-9]'], , '$CC $1']],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                FR: [
                  ,
                  [, , '[1-9]\\d{8}', , , , , , , [9]],
                  [, , '(?:26[013-9]|59[1-35-9])\\d{6}|(?:[13]\\d|2[0-57-9]|4[1-9]|5[0-8])\\d{7}', , , , '123456789'],
                  [, , '(?:6(?:[0-24-8]\\d|3[0-8]|9[589])|7[3-9]\\d)\\d{6}', , , , '612345678'],
                  [, , '80[0-5]\\d{6}', , , , '801234567'],
                  [, , '836(?:0[0-36-9]|[1-9]\\d)\\d{4}|8(?:1[2-9]|2[2-47-9]|3[0-57-9]|[569]\\d|8[0-35-9])\\d{6}', , , , '891123456'],
                  [, , '8(?:1[01]|2[0156]|4[024]|84)\\d{6}', , , , '884012345'],
                  [, , , , , , , , , [-1]],
                  [, , '9\\d{8}', , , , '912345678'],
                  'FR',
                  33,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})', '$1', ['10']],
                    [, '(\\d{3})(\\d{3})', '$1 $2', ['1']],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['8'], '0 $1'],
                    [, '(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4 $5', ['[1-79]'], '0$1']
                  ],
                  [
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['8'], '0 $1'],
                    [, '(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4 $5', ['[1-79]'], '0$1']
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '80[6-9]\\d{6}', , , , '806123456'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GA: [
                  ,
                  [, , '(?:[067]\\d|11)\\d{6}|[2-7]\\d{6}', , , , , , , [7, 8]],
                  [, , '[01]1\\d{6}', , , , '01441234', , , [8]],
                  [, , '(?:(?:0[2-7]|7[467])\\d|6(?:0[0-4]|10|[256]\\d))\\d{5}|[2-7]\\d{6}', , , , '06031234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'GA',
                  241,
                  '00',
                  ,
                  ,
                  ,
                  '0(11\\d{6}|60\\d{6}|61\\d{6}|6[256]\\d{6}|7[467]\\d{6})',
                  '$1',
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[2-7]'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['0']],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['11|[67]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GB: [
                  ,
                  [, , '[1-357-9]\\d{9}|[18]\\d{8}|8\\d{6}', , , , , , , [7, 9, 10], [4, 5, 6, 8]],
                  [
                    ,
                    ,
                    '(?:1(?:1(?:3(?:[0-58]\\d\\d|73[0-35])|4(?:(?:[0-5]\\d|70)\\d|69[7-9])|(?:(?:5[0-26-9]|[78][0-49])\\d|6(?:[0-4]\\d|50))\\d)|(?:2(?:(?:0[024-9]|2[3-9]|3[3-79]|4[1-689]|[58][02-9]|6[0-47-9]|7[013-9]|9\\d)\\d|1(?:[0-7]\\d|8[0-3]))|(?:3(?:0\\d|1[0-8]|[25][02-9]|3[02-579]|[468][0-46-9]|7[1-35-79]|9[2-578])|4(?:0[03-9]|[137]\\d|[28][02-57-9]|4[02-69]|5[0-8]|[69][0-79])|5(?:0[1-35-9]|[16]\\d|2[024-9]|3[015689]|4[02-9]|5[03-9]|7[0-35-9]|8[0-468]|9[0-57-9])|6(?:0[034689]|1\\d|2[0-35689]|[38][013-9]|4[1-467]|5[0-69]|6[13-9]|7[0-8]|9[0-24578])|7(?:0[0246-9]|2\\d|3[0236-8]|4[03-9]|5[0-46-9]|6[013-9]|7[0-35-9]|8[024-9]|9[02-9])|8(?:0[35-9]|2[1-57-9]|3[02-578]|4[0-578]|5[124-9]|6[2-69]|7\\d|8[02-9]|9[02569])|9(?:0[02-589]|[18]\\d|2[02-689]|3[1-57-9]|4[2-9]|5[0-579]|6[2-47-9]|7[0-24578]|9[2-57]))\\d)\\d)|2(?:0[013478]|3[0189]|4[017]|8[0-46-9]|9[0-2])\\d{3})\\d{4}|1(?:2(?:0(?:46[1-4]|87[2-9])|545[1-79]|76(?:2\\d|3[1-8]|6[1-6])|9(?:7(?:2[0-4]|3[2-5])|8(?:2[2-8]|7[0-47-9]|8[3-5])))|3(?:6(?:38[2-5]|47[23])|8(?:47[04-9]|64[0157-9]))|4(?:044[1-7]|20(?:2[23]|8\\d)|6(?:0(?:30|5[2-57]|6[1-8]|7[2-8])|140)|8(?:052|87[1-3]))|5(?:2(?:4(?:3[2-79]|6\\d)|76\\d)|6(?:26[06-9]|686))|6(?:06(?:4\\d|7[4-79])|295[5-7]|35[34]\\d|47(?:24|61)|59(?:5[08]|6[67]|74)|9(?:55[0-4]|77[23]))|7(?:26(?:6[13-9]|7[0-7])|(?:442|688)\\d|50(?:2[0-3]|[3-68]2|76))|8(?:27[56]\\d|37(?:5[2-5]|8[239])|843[2-58])|9(?:0(?:0(?:6[1-8]|85)|52\\d)|3583|4(?:66[1-8]|9(?:2[01]|81))|63(?:23|3[1-4])|9561))\\d{3}',
                    ,
                    ,
                    ,
                    '1212345678',
                    ,
                    ,
                    [9, 10],
                    [4, 5, 6, 7, 8]
                  ],
                  [
                    ,
                    ,
                    '7(?:457[0-57-9]|700[01]|911[028])\\d{5}|7(?:[1-3]\\d\\d|4(?:[0-46-9]\\d|5[0-689])|5(?:0[0-8]|[13-9]\\d|2[0-35-9])|7(?:0[1-9]|[1-7]\\d|8[02-9]|9[0-689])|8(?:[014-9]\\d|[23][0-8])|9(?:[024-9]\\d|1[02-9]|3[0-689]))\\d{6}',
                    ,
                    ,
                    ,
                    '7400123456',
                    ,
                    ,
                    [10]
                  ],
                  [, , '80[08]\\d{7}|800\\d{6}|8001111', , , , '8001234567'],
                  [, , '(?:8(?:4[2-5]|7[0-3])|9(?:[01]\\d|8[2-49]))\\d{7}|845464\\d', , , , '9012345678', , , [7, 10]],
                  [, , , , , , , , , [-1]],
                  [, , '70\\d{8}', , , , '7012345678', , , [10]],
                  [, , '56\\d{8}', , , , '5612345678', , , [10]],
                  'GB',
                  44,
                  '00',
                  '0',
                  ' x',
                  ,
                  '0|180020',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['800', '8001', '80011', '800111', '8001111'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3', ['845', '8454', '84546', '845464'], '0$1'],
                    [, '(\\d{3})(\\d{6})', '$1 $2', ['800'], '0$1'],
                    [
                      ,
                      '(\\d{5})(\\d{4,5})',
                      '$1 $2',
                      ['1(?:38|5[23]|69|76|94)', '1(?:(?:38|69)7|5(?:24|39)|768|946)', '1(?:3873|5(?:242|39[4-6])|(?:697|768)[347]|9467)'],
                      '0$1'
                    ],
                    [, '(\\d{4})(\\d{5,6})', '$1 $2', ['1(?:[2-69][02-9]|[78])'], '0$1'],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1 $2 $3', ['[25]|7(?:0|6[02-9])', '[25]|7(?:0|6(?:[03-9]|2[356]))'], '0$1'],
                    [, '(\\d{4})(\\d{6})', '$1 $2', ['7'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['[1389]'], '0$1']
                  ],
                  ,
                  [, , '76(?:464|652)\\d{5}|76(?:0[0-28]|2[356]|34|4[01347]|5[49]|6[0-369]|77|8[14]|9[139])\\d{6}', , , , '7640123456', , , [10]],
                  1,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '(?:3[0347]|55)\\d{8}', , , , '5512345678', , , [10]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GD: [
                  ,
                  [, , '(?:473|[58]\\d\\d|900)\\d{7}', , , , , , , [10], [7]],
                  [
                    ,
                    ,
                    '473(?:2(?:3[0-2]|69)|3(?:2[89]|86)|4(?:[06]8|3[5-9]|4[0-4]|5[59]|73|90)|63[68]|7(?:58|84)|800|938)\\d{4}',
                    ,
                    ,
                    ,
                    '4732691234',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '473(?:4(?:0[2-79]|1[04-9]|2[0-5]|49|5[6-8])|5(?:2[01]|3[3-8])|901)\\d{4}', , , , '4734031234', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'GD',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-9]\\d{6})$|1',
                  '473$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '473',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GE: [
                  ,
                  [, , '(?:[3-57]\\d\\d|800)\\d{6}', , , , , , , [9], [6, 7]],
                  [, , '(?:3(?:[256]\\d|4[124-9]|7[0-4])|4(?:1\\d|2[2-7]|3[1-79]|4[2-8]|7[239]|9[1-7]))\\d{6}', , , , '322123456', , , , [6, 7]],
                  [
                    ,
                    ,
                    '5(?:(?:(?:0555|1(?:[17]77|555))[5-9]|757(?:7[7-9]|8[01]))\\d|22252[0-4])\\d\\d|5(?:0(?:0[17]0|505)|1(?:0[01]0|1(?:07|33|51))|2(?:0[02]0|2[25]2)|3(?:0[03]0|3[35]3)|(?:40[04]|900)0|5222)[0-4]\\d{3}|(?:5(?:0(?:0(?:0\\d|11|22|3[0-6]|44|5[05]|77|88|9[09])|(?:[14]\\d|77)\\d|22[02])|1(?:1(?:[03][01]|[124]\\d|5[2-6]|7[0-4])|4\\d\\d)|[23]555|4(?:4\\d\\d|555)|5(?:[0157-9]\\d\\d|200|333|444)|6[89]\\d\\d|7(?:[0147-9]\\d\\d|5(?:00|[57]5))|8(?:0(?:[018]\\d|2[0-4])|5(?:55|8[89])|8(?:55|88))|9(?:090|[1-35-9]\\d\\d))|790\\d\\d)\\d{4}',
                    ,
                    ,
                    ,
                    '555123456'
                  ],
                  [, , '800\\d{6}', , , , '800123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '70[67]\\d{6}', , , , '706123456'],
                  'GE',
                  995,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['70'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['32'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[57]']],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[348]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '70[67]\\d{6}'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GF: [
                  ,
                  [, , '(?:[56]94\\d|7093)\\d{5}|(?:80|9\\d)\\d{7}', , , , , , , [9]],
                  [, , '594(?:[02-49]\\d|1[0-5]|5[6-9]|6[0-3]|80)\\d{4}', , , , '594101234'],
                  [, , '(?:694(?:[0-249]\\d|3[0-8])|7093[0-3])\\d{4}', , , , '694201234'],
                  [, , '80[0-5]\\d{6}', , , , '800012345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '9(?:(?:396|76\\d)\\d|476[0-6])\\d{4}', , , , '976012345'],
                  'GF',
                  594,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[5-7]|9[47]'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[89]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GG: [
                  ,
                  [, , '(?:1481|[357-9]\\d{3})\\d{6}|8\\d{6}(?:\\d{2})?', , , , , , , [7, 9, 10], [6]],
                  [, , '1481[25-9]\\d{5}', , , , '1481256789', , , [10], [6]],
                  [, , '7(?:(?:781|839)\\d|911[17])\\d{5}', , , , '7781123456', , , [10]],
                  [, , '80[08]\\d{7}|800\\d{6}|8001111', , , , '8001234567'],
                  [, , '(?:8(?:4[2-5]|7[0-3])|9(?:[01]\\d|8[0-3]))\\d{7}|845464\\d', , , , '9012345678', , , [7, 10]],
                  [, , , , , , , , , [-1]],
                  [, , '70\\d{8}', , , , '7012345678', , , [10]],
                  [, , '56\\d{8}', , , , '5612345678', , , [10]],
                  'GG',
                  44,
                  '00',
                  '0',
                  ,
                  ,
                  '([25-9]\\d{5})$|0|180020',
                  '1481$1',
                  ,
                  ,
                  ,
                  ,
                  [, , '76(?:464|652)\\d{5}|76(?:0[0-28]|2[356]|34|4[01347]|5[49]|6[0-369]|77|8[14]|9[139])\\d{6}', , , , '7640123456', , , [10]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '(?:3[0347]|55)\\d{8}', , , , '5512345678', , , [10]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GH: [
                  ,
                  [, , '(?:[235]\\d{3}|800)\\d{5}', , , , , , , [8, 9], [7]],
                  [
                    ,
                    ,
                    '3082[0-5]\\d{4}|3(?:0(?:[237]\\d|8[01])|[167](?:2[0-6]|7\\d|80)|2(?:2[0-5]|7\\d|80)|3(?:2[0-3]|7\\d|80)|4(?:2[013-9]|3[01]|7\\d|80)|5(?:2[0-7]|7\\d|80)|8(?:2[0-2]|7\\d|80)|9(?:[28]0|7\\d))\\d{5}',
                    ,
                    ,
                    ,
                    '302345678',
                    ,
                    ,
                    [9],
                    [7]
                  ],
                  [, , '(?:2(?:[0346-9]\\d|5[67])|5(?:[03-7]\\d|9[1-9]))\\d{6}', , , , '231234567', , , [9]],
                  [, , '800\\d{5}', , , , '80012345', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'GH',
                  233,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['[237]|8[0-2]']],
                    [, '(\\d{3})(\\d{5})', '$1 $2', ['8'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[235]'], '0$1']
                  ],
                  [
                    [, '(\\d{3})(\\d{5})', '$1 $2', ['8'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[235]'], '0$1']
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '800\\d{5}', , , , , , , [8]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GI: [
                  ,
                  [, , '(?:[25]\\d|60)\\d{6}', , , , , , , [8]],
                  [, , '2190[0-2]\\d{3}|2(?:0(?:[02]\\d|3[01])|16[24-9]|2[2-5]\\d)\\d{4}', , , , '20012345'],
                  [, , '5251[0-4]\\d{3}|(?:5(?:[146-8]\\d\\d|250)|60(?:1[01]|6\\d))\\d{4}', , , , '57123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'GI',
                  350,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{5})', '$1 $2', ['2']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GL: [
                  ,
                  [, , '(?:19|[2-689]\\d|70)\\d{4}', , , , , , , [6]],
                  [, , '(?:19|3[1-7]|[68][1-9]|70|9\\d)\\d{4}', , , , '321000'],
                  [, , '[245]\\d{5}', , , , '221234'],
                  [, , '80\\d{4}', , , , '801234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '3[89]\\d{4}', , , , '381234'],
                  'GL',
                  299,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3', ['19|[2-9]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GM: [
                  ,
                  [, , '[2-9]\\d{6}', , , , , , , [7]],
                  [
                    ,
                    ,
                    '(?:4(?:[23]\\d\\d|4(?:1[024679]|[6-9]\\d))|5(?:5(?:3\\d|4[0-7])|6[67]\\d|7(?:1[04]|2[035]|3[58]|48))|8[0-589]\\d\\d)\\d{3}',
                    ,
                    ,
                    ,
                    '5661234'
                  ],
                  [, , '556\\d{4}|(?:[23679]\\d|4[015]|5[0-489]|8[67])\\d{5}', , , , '3012345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'GM',
                  220,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{4})', '$1 $2', ['[2-9]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GN: [
                  ,
                  [, , '722\\d{6}|(?:3|6\\d)\\d{7}', , , , , , , [8, 9]],
                  [, , '3(?:0(?:24|3[12]|4[1-35-7]|5[13]|6[189]|[78]1|9[1478])|1\\d\\d)\\d{4}', , , , '30241234', , , [8]],
                  [, , '6[0-356]\\d{7}', , , , '601123456', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '722\\d{6}', , , , '722123456', , , [9]],
                  'GN',
                  224,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['3']],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[67]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GP: [
                  ,
                  [, , '(?:590\\d|7090)\\d{5}|(?:69|80|9\\d)\\d{7}', , , , , , , [9]],
                  [, , '590(?:0[1-68]|[14][0-24-9]|2[0-68]|3[1-9]|5[3-579]|[68][0-689]|7[08]|9\\d)\\d{4}', , , , '590201234'],
                  [, , '(?:69(?:0\\d\\d|1(?:2[2-9]|3[0-5])|4(?:0[89]|1[2-6]|9\\d)|6(?:1[016-9]|5[0-4]|[67]\\d))|7090[0-4])\\d{4}', , , , '690001234'],
                  [, , '80[0-5]\\d{6}', , , , '800012345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '9(?:(?:39[5-7]|76[018])\\d|475[0-6])\\d{4}', , , , '976012345'],
                  'GP',
                  590,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[5-79]'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['8'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  1,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GQ: [
                  ,
                  [, , '222\\d{6}|(?:3\\d|55|[89]0)\\d{7}', , , , , , , [9]],
                  [, , '33[0-24-9]\\d[46]\\d{4}|3(?:33|5\\d)\\d[7-9]\\d{4}', , , , '333091234'],
                  [, , '(?:222|55\\d)\\d{6}', , , , '222123456'],
                  [, , '80\\d[1-9]\\d{5}', , , , '800123456'],
                  [, , '90\\d[1-9]\\d{5}', , , , '900123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'GQ',
                  240,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[235]']],
                    [, '(\\d{3})(\\d{6})', '$1 $2', ['[89]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GR: [
                  ,
                  [, , '5005000\\d{3}|8\\d{9,11}|(?:[269]\\d|70)\\d{8}', , , , , , , [10, 11, 12]],
                  [
                    ,
                    ,
                    '2(?:1\\d\\d|2(?:2[1-46-9]|[36][1-8]|4[1-7]|5[1-4]|7[1-5]|[89][1-9])|3(?:1\\d|2[1-57]|[35][1-3]|4[13]|7[1-7]|8[124-6]|9[1-79])|4(?:1\\d|2[1-8]|3[1-4]|4[13-5]|6[1-578]|9[1-5])|5(?:1\\d|[29][1-4]|3[1-5]|4[124]|5[1-6])|6(?:1\\d|[269][1-6]|3[1245]|4[1-7]|5[13-9]|7[14]|8[1-5])|7(?:1\\d|2[1-5]|3[1-6]|4[1-7]|5[1-57]|6[135]|9[125-7])|8(?:1\\d|2[1-5]|[34][1-4]|9[1-57]))\\d{6}',
                    ,
                    ,
                    ,
                    '2123456789',
                    ,
                    ,
                    [10]
                  ],
                  [, , '68[57-9]\\d{7}|(?:69|94)\\d{8}', , , , '6912345678', , , [10]],
                  [, , '800\\d{7,9}', , , , '8001234567'],
                  [, , '90[19]\\d{7}', , , , '9091234567', , , [10]],
                  [, , '8(?:0[16]|12|[27]5|50)\\d{7}', , , , '8011234567', , , [10]],
                  [, , '70\\d{8}', , , , '7012345678', , , [10]],
                  [, , , , , , , , , [-1]],
                  'GR',
                  30,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1 $2 $3', ['21|7']],
                    [, '(\\d{4})(\\d{6})', '$1 $2', ['2(?:2|3[2-57-9]|4[2-469]|5[2-59]|6[2-9]|7[2-69]|8[2-49])|5']],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['[2689]']],
                    [, '(\\d{3})(\\d{3,4})(\\d{5})', '$1 $2 $3', ['8']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '5005000\\d{3}', , , , '5005000123', , , [10]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GT: [
                  ,
                  [, , '80\\d{6}|(?:1\\d{3}|[2-7])\\d{7}', , , , , , , [8, 11]],
                  [, , '[267][2-9]\\d{6}', , , , '22456789', , , [8]],
                  [, , '(?:[3-5]\\d\\d|80[0-4])\\d{5}', , , , '51234567', , , [8]],
                  [, , '18[01]\\d{8}', , , , '18001112222', , , [11]],
                  [, , '19\\d{9}', , , , '19001112222', , , [11]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'GT',
                  502,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[2-8]']],
                    [, '(\\d{4})(\\d{3})(\\d{4})', '$1 $2 $3', ['1']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GU: [
                  ,
                  [, , '(?:[58]\\d\\d|671|900)\\d{7}', , , , , , , [10], [7]],
                  [
                    ,
                    ,
                    '671(?:2\\d\\d|3(?:00|3[39]|4[349]|55|6[26])|4(?:00|56|7[1-9]|8[02-9])|5(?:55|6[2-5]|88)|6(?:3[2-578]|4[24-9]|5[34]|78|8[235-9])|7(?:[0479]7|2[0167]|3[45]|8[7-9])|8(?:[2-57-9]8|6[478])|9(?:2[29]|6[79]|7[1279]|8[7-9]|9[78]))\\d{4}',
                    ,
                    ,
                    ,
                    '6713001234',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [
                    ,
                    ,
                    '671(?:2\\d\\d|3(?:00|3[39]|4[349]|55|6[26])|4(?:00|56|7[1-9]|8[02-9])|5(?:55|6[2-5]|88)|6(?:3[2-578]|4[24-9]|5[34]|78|8[235-9])|7(?:[0479]7|2[0167]|3[45]|8[7-9])|8(?:[2-57-9]8|6[478])|9(?:2[29]|6[79]|7[1279]|8[7-9]|9[78]))\\d{4}',
                    ,
                    ,
                    ,
                    '6713001234',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'GU',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-9]\\d{6})$|1',
                  '671$1',
                  ,
                  1,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '671',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GW: [
                  ,
                  [, , '[49]\\d{8}|4\\d{6}', , , , , , , [7, 9]],
                  [, , '443\\d{6}', , , , '443201234', , , [9]],
                  [, , '9(?:5\\d|6[569]|77)\\d{6}', , , , '955012345', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '40\\d{5}', , , , '4012345', , , [7]],
                  'GW',
                  245,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['40']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[49]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GY: [
                  ,
                  [, , '(?:[2-8]\\d{3}|9008)\\d{3}', , , , , , , [7]],
                  [, , '(?:2(?:1[6-9]|2[0-35-9]|3[1-4]|5[3-9]|6\\d|7[0-79])|3(?:2[25-9]|3\\d)|4(?:4[0-24]|5[56])|50[0-6]|77[1-57])\\d{4}', , , , '2201234'],
                  [, , '(?:51[01]|6\\d\\d|7(?:[0-5]\\d|6[0-39]|70))\\d{4}', , , , '6091234'],
                  [, , '(?:289|8(?:00|6[28]|88|99))\\d{4}', , , , '2891234'],
                  [, , '9008\\d{3}', , , , '9008123'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '515\\d{4}', , , , '5151234'],
                  'GY',
                  592,
                  '001',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{4})', '$1 $2', ['[2-9]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                HK: [
                  ,
                  [, , '8[0-46-9]\\d{6,7}|9\\d{4,7}|(?:[2-7]|9\\d{3})\\d{7}', , , , , , , [5, 6, 7, 8, 9, 11]],
                  [
                    ,
                    ,
                    '(?:2(?:[13-9]\\d|2[013-9])\\d|3(?:(?:[1569][0-24-9]|4[0-246-9]|7[0-24-69])\\d|8(?:4[0-8]|[579]\\d|6[0-5]))|58(?:0[1-9]|1[2-9]))\\d{4}',
                    ,
                    ,
                    ,
                    '21234567',
                    ,
                    ,
                    [8]
                  ],
                  [
                    ,
                    ,
                    '(?:4(?:44[0-35-9]|6(?:4[0-57-9]|6[0-4])|7(?:3[0-4]|4[0-48]|6[0-5]))|5(?:35[4-8]|73[0-6]|95[0-8])|6(?:26[013-8]|(?:66|78)[0-5])|70(?:7[1-8]|8[0-8])|84(?:4[0-2]|8[0-35-9])|9(?:29[013-9]|39[014-9]|59[0-4]|899))\\d{4}|(?:4(?:4[0-35-9]|6[0-357-9]|7[0-25])|5(?:[1-59][0-46-9]|6[0-4689]|7[0-246-9])|6(?:0[1-9]|[13-59]\\d|[268][0-57-9]|7[0-79])|70[1-59]|84[0-39]|9(?:0[1-9]|1[02-9]|[2358][0-8]|[467]\\d))\\d{5}',
                    ,
                    ,
                    ,
                    '51234567',
                    ,
                    ,
                    [8]
                  ],
                  [, , '800\\d{6}', , , , '800123456', , , [9]],
                  [, , '900(?:[0-24-9]\\d{7}|3\\d{1,4})', , , , '90012345678', , , [5, 6, 7, 8, 11]],
                  [, , , , , , , , , [-1]],
                  [, , '8(?:1[0-4679]\\d|2(?:[0-36]\\d|7[0-4])|3(?:[034]\\d|2[09]|70))\\d{4}', , , , '81123456', , , [8]],
                  [, , , , , , , , , [-1]],
                  'HK',
                  852,
                  '00(?:30|5[09]|[126-9]?)',
                  ,
                  ,
                  ,
                  ,
                  ,
                  '00',
                  ,
                  [
                    [, '(\\d{3})(\\d{2,5})', '$1 $2', ['900', '9003']],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[2-7]|8[1-4]|9(?:0[1-9]|[1-8])']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['8']],
                    [, '(\\d{3})(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['9']]
                  ],
                  ,
                  [
                    ,
                    ,
                    '7(?:1(?:0[0-38]|1[0-3679]|3[013]|69|9[0136])|2(?:[02389]\\d|1[18]|7[27-9])|3(?:[0-38]\\d|7[0-369]|9[2357-9])|47\\d|5(?:[178]\\d|5[0-5])|6(?:0[0-7]|2[236-9]|[35]\\d)|7(?:[27]\\d|8[7-9])|8(?:[23689]\\d|7[1-9])|9(?:[025]\\d|6[0-246-8]|7[0-36-9]|8[238]))\\d{4}',
                    ,
                    ,
                    ,
                    '71123456',
                    ,
                    ,
                    [8]
                  ],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '30(?:0[1-9]|[15-7]\\d|2[047]|89)\\d{4}', , , , '30161234', , , [8]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                HN: [
                  ,
                  [, , '8\\d{10}|[237-9]\\d{7}', , , , , , , [8, 11]],
                  [
                    ,
                    ,
                    '2(?:2(?:0[0-59]|1[1-9]|[23]\\d|4[02-7]|5[57]|6[245]|7[0135689]|8[01346-9]|9[0-2])|4(?:0[578]|2[3-59]|3[13-9]|4[0-68]|5[1-3589])|5(?:0[2357-9]|1[1-356]|4[03-5]|5\\d|6[014-69]|7[04]|80)|6(?:[056]\\d|17|2[067]|3[047]|4[0-378]|[78][0-8]|9[01])|7(?:0[5-79]|6[46-9]|7[02-9]|8[034]|91)|8(?:79|8[0-357-9]|9[1-57-9]))\\d{4}',
                    ,
                    ,
                    ,
                    '22123456',
                    ,
                    ,
                    [8]
                  ],
                  [, , '[37-9]\\d{7}', , , , '91234567', , , [8]],
                  [, , '8002\\d{7}', , , , '80021234567', , , [11]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'HN',
                  504,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})(\\d{4})', '$1-$2', ['[237-9]']],
                    [, '(\\d{3})(\\d{4})(\\d{4})', '$1 $2 $3', ['8']]
                  ],
                  [[, '(\\d{4})(\\d{4})', '$1-$2', ['[237-9]']]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '8002\\d{7}', , , , , , , [11]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                HR: [
                  ,
                  [, , '[2-69]\\d{8}|80\\d{5,7}|[1-79]\\d{7}|6\\d{6}', , , , , , , [7, 8, 9], [6]],
                  [, , '1\\d{7}|(?:2[0-3]|3[1-5]|4[02-47-9]|5[1-3])\\d{6,7}', , , , '12345678', , , [8, 9], [6, 7]],
                  [, , '9(?:(?:0[1-9]|[12589]\\d)\\d\\d|7(?:[0679]\\d\\d|5(?:[01]\\d|44|55|77|9[5-79])))\\d{4}|98\\d{6}', , , , '921234567', , , [8, 9]],
                  [, , '80\\d{5,7}', , , , '800123456'],
                  [, , '6[01459]\\d{6}|6[01]\\d{5}', , , , '6001234', , , [7, 8]],
                  [, , , , , , , , , [-1]],
                  [, , '7[45]\\d{6}', , , , '74123456', , , [8]],
                  [, , , , , , , , , [-1]],
                  'HR',
                  385,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d{3})', '$1 $2 $3', ['6[01]'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{2,3})', '$1 $2 $3', ['8'], '0$1'],
                    [, '(\\d)(\\d{4})(\\d{3})', '$1 $2 $3', ['1'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['6|7[245]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['9'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[2-57]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['8'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '62\\d{6,7}|72\\d{6}', , , , '62123456', , , [8, 9]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                HT: [
                  ,
                  [, , '[2-589]\\d{7}', , , , , , , [8]],
                  [, , '2(?:2\\d|5[1-5]|81|9[149])\\d{5}', , , , '22453300'],
                  [, , '(?:[34]\\d|5[56])\\d{6}', , , , '34101234'],
                  [, , '8\\d{7}', , , , '80012345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '9(?:[67][0-4]|8[0-3589]|9\\d)\\d{5}', , , , '98901234'],
                  'HT',
                  509,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{2})(\\d{4})', '$1 $2 $3', ['[2-589]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                HU: [
                  ,
                  [, , '[235-7]\\d{8}|[1-9]\\d{7}', , , , , , , [8, 9], [6, 7]],
                  [, , '(?:1\\d|[27][2-9]|3[2-7]|4[24-9]|5[2-79]|6[23689]|8[2-57-9]|9[2-69])\\d{6}', , , , '12345678', , , [8], [6, 7]],
                  [, , '(?:[257]0|3[01])\\d{7}', , , , '201234567', , , [9]],
                  [, , '(?:[48]0\\d|680[29])\\d{5}', , , , '80123456'],
                  [, , '9[01]\\d{6}', , , , '90123456', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '21\\d{7}', , , , '211234567', , , [9]],
                  'HU',
                  36,
                  '00',
                  '06',
                  ,
                  ,
                  '06',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{4})', '$1 $2 $3', ['1'], '(06 $1)'],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['[27][2-9]|3[2-7]|4[24-9]|5[2-79]|6|8[2-57-9]|9[2-69]'], '(06 $1)'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[2-9]'], '06 $1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '(?:[48]0\\d|680[29])\\d{5}'],
                  [, , '38\\d{7}', , , , '381234567', , , [9]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ID: [
                  ,
                  [
                    ,
                    ,
                    '00[1-9]\\d{9,14}|(?:[1-36]|8\\d{5})\\d{6}|00\\d{9}|[1-9]\\d{8,10}|[2-9]\\d{7}',
                    ,
                    ,
                    ,
                    ,
                    ,
                    ,
                    [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17],
                    [5, 6]
                  ],
                  [
                    ,
                    ,
                    '2[124]\\d{7,8}|619\\d{8}|2(?:1(?:14|500)|2\\d{3})\\d{3}|61\\d{5,8}|(?:2(?:[35][1-4]|6[0-8]|7[1-6]|8\\d|9[1-8])|3(?:1|[25][1-8]|3[1-68]|4[1-3]|6[1-3568]|7[0-469]|8\\d)|4(?:0[1-589]|1[01347-9]|2[0-36-8]|3[0-24-68]|43|5[1-378]|6[1-5]|7[134]|8[1245])|5(?:1[1-35-9]|2[25-8]|3[124-9]|4[1-3589]|5[1-46]|6[1-8])|6(?:[25]\\d|3[1-69]|4[1-6])|7(?:02|[125][1-9]|[36]\\d|4[1-8]|7[0-36-9])|9(?:0[12]|1[013-8]|2[0-479]|5[125-8]|6[23679]|7[159]|8[01346]))\\d{5,8}',
                    ,
                    ,
                    ,
                    '218350123',
                    ,
                    ,
                    [7, 8, 9, 10, 11],
                    [5, 6]
                  ],
                  [, , '8[1-35-9]\\d{7,10}', , , , '812345678', , , [9, 10, 11, 12]],
                  [, , '00(?:1803\\d{5,11}|7803\\d{7})|(?:177\\d|800)\\d{5,7}', , , , '8001234567', , , [8, 9, 10, 11, 12, 13, 14, 15, 16, 17]],
                  [, , '809\\d{7}', , , , '8091234567', , , [10]],
                  [, , '804\\d{7}', , , , '8041234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'ID',
                  62,
                  '00[89]',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{3})', '$1 $2 $3', ['15']],
                    [, '(\\d{2})(\\d{5,9})', '$1 $2', ['2[124]|[36]1'], '(0$1)'],
                    [, '(\\d{3})(\\d{5,7})', '$1 $2', ['800'], '0$1'],
                    [, '(\\d{3})(\\d{5,8})', '$1 $2', ['[2-79]'], '(0$1)'],
                    [, '(\\d{3})(\\d{3,4})(\\d{3})', '$1-$2-$3', ['8[1-35-9]'], '0$1'],
                    [, '(\\d{3})(\\d{6,8})', '$1 $2', ['1'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['804'], '0$1'],
                    [, '(\\d{3})(\\d)(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['80'], '0$1'],
                    [, '(\\d{3})(\\d{4})(\\d{4,5})', '$1-$2-$3', ['8'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})(\\d{2,8})', '$1 $2 $3 $4', ['001']],
                    [, '(\\d{2})(\\d{4})(\\d{3})(\\d{4})', '$1 $2 $3 $4', ['0']]
                  ],
                  [
                    [, '(\\d)(\\d{3})(\\d{3})', '$1 $2 $3', ['15']],
                    [, '(\\d{2})(\\d{5,9})', '$1 $2', ['2[124]|[36]1'], '(0$1)'],
                    [, '(\\d{3})(\\d{5,7})', '$1 $2', ['800'], '0$1'],
                    [, '(\\d{3})(\\d{5,8})', '$1 $2', ['[2-79]'], '(0$1)'],
                    [, '(\\d{3})(\\d{3,4})(\\d{3})', '$1-$2-$3', ['8[1-35-9]'], '0$1'],
                    [, '(\\d{3})(\\d{6,8})', '$1 $2', ['1'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['804'], '0$1'],
                    [, '(\\d{3})(\\d)(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['80'], '0$1'],
                    [, '(\\d{3})(\\d{4})(\\d{4,5})', '$1-$2-$3', ['8'], '0$1']
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '001803\\d{5,11}|(?:007803\\d|8071)\\d{6}', , , , , , , [10, 11, 12, 13, 14, 15, 16, 17]],
                  [, , '(?:1500|8071\\d{3})\\d{3}', , , , '8071123456', , , [7, 10]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                IE: [
                  ,
                  [, , '(?:1\\d|[2569])\\d{6,8}|4\\d{6,9}|7\\d{8}|8\\d{8,9}', , , , , , , [7, 8, 9, 10], [5, 6]],
                  [
                    ,
                    ,
                    '(?:1\\d|21)\\d{6,7}|(?:2[24-9]|4(?:0[24]|5\\d|7)|5(?:0[45]|1\\d|8)|6(?:1\\d|[237-9])|9(?:1\\d|[35-9]))\\d{5}|(?:23|4(?:[1-469]|8\\d)|5[23679]|6[4-6]|7[14]|9[04])\\d{7}',
                    ,
                    ,
                    ,
                    '2212345',
                    ,
                    ,
                    ,
                    [5, 6]
                  ],
                  [, , '8(?:22|[35-9]\\d)\\d{6}', , , , '850123456', , , [9]],
                  [, , '1800\\d{6}', , , , '1800123456', , , [10]],
                  [, , '15(?:1[2-8]|[2-8]0|9[089])\\d{6}', , , , '1520123456', , , [10]],
                  [, , '18[59]0\\d{6}', , , , '1850123456', , , [10]],
                  [, , '700\\d{6}', , , , '700123456', , , [9]],
                  [, , '76\\d{7}', , , , '761234567', , , [9]],
                  'IE',
                  353,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{5})', '$1 $2', ['2[24-9]|47|58|6[237-9]|9[35-9]'], '(0$1)'],
                    [, '(\\d{3})(\\d{5})', '$1 $2', ['[45]0'], '(0$1)'],
                    [, '(\\d)(\\d{3,4})(\\d{4})', '$1 $2 $3', ['1'], '(0$1)'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[2569]|4[1-69]|7[14]'], '(0$1)'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['70'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['81'], '(0$1)'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[78]'], '0$1'],
                    [, '(\\d{4})(\\d{3})(\\d{3})', '$1 $2 $3', ['1']],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1 $2 $3', ['4'], '(0$1)'],
                    [, '(\\d{2})(\\d)(\\d{3})(\\d{4})', '$1 $2 $3 $4', ['8'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '18[59]0\\d{6}', , , , , , , [10]],
                  [, , '818\\d{6}', , , , '818123456', , , [9]],
                  ,
                  ,
                  [, , '88210[1-9]\\d{4}|8(?:[35-79]5\\d\\d|8(?:[013-9]\\d\\d|2(?:[01][1-9]|[2-9]\\d)))\\d{5}', , , , '8551234567', , , [10]]
                ],
                IL: [
                  ,
                  [, , '1\\d{6}(?:\\d{3,5})?|[57]\\d{8}|[1-489]\\d{7}', , , , , , , [7, 8, 9, 10, 11, 12]],
                  [, , '153\\d{8,9}|29[1-9]\\d{5}|(?:2[0-8]|[3489]\\d)\\d{6}', , , , '21234567', , , [8, 11, 12], [7]],
                  [
                    ,
                    ,
                    '55(?:4(?:0[01]|10|5[0-7])|57[0-289])\\d{4}|5(?:(?:[0-2][02-9]|[36]\\d|[49][2-9]|8[3-7])\\d|5(?:01|2\\d|3[0-3]|4[34]|5[0-25689]|6[6-8]|7[0-267]|8[7-9]|9[1-9]))\\d{5}',
                    ,
                    ,
                    ,
                    '502345678',
                    ,
                    ,
                    [9]
                  ],
                  [, , '1(?:255|80[019]\\d{3})\\d{3}', , , , '1800123456', , , [7, 10]],
                  [, , '1212\\d{4}|1(?:200|9(?:0[0-2]|19))\\d{6}', , , , '1919123456', , , [8, 10]],
                  [, , '1700\\d{6}', , , , '1700123456', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , '7(?:38(?:[05]\\d|8[018])|8(?:33|55|77|81)\\d)\\d{4}|7(?:18|2[23]|3[237]|47|6[258]|7\\d|82|9[2-9])\\d{6}', , , , '771234567', , , [9]],
                  'IL',
                  972,
                  '0(?:0|1[2-9])',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})(\\d{3})', '$1-$2', ['125']],
                    [, '(\\d{4})(\\d{2})(\\d{2})', '$1-$2-$3', ['121']],
                    [, '(\\d)(\\d{3})(\\d{4})', '$1-$2-$3', ['[2-489]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1-$2-$3', ['[57]'], '0$1'],
                    [, '(\\d{4})(\\d{3})(\\d{3})', '$1-$2-$3', ['12']],
                    [, '(\\d{4})(\\d{6})', '$1-$2', ['159']],
                    [, '(\\d)(\\d{3})(\\d{3})(\\d{3})', '$1-$2-$3-$4', ['1[7-9]']],
                    [, '(\\d{3})(\\d{1,2})(\\d{3})(\\d{4})', '$1-$2 $3-$4', ['15']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '1700\\d{6}', , , , , , , [10]],
                  [, , '1599\\d{6}', , , , '1599123456', , , [10]],
                  ,
                  ,
                  [, , '151\\d{8,9}', , , , '15112340000', , , [11, 12]]
                ],
                IM: [
                  ,
                  [, , '1624\\d{6}|(?:[3578]\\d|90)\\d{8}', , , , , , , [10], [6]],
                  [, , '1624(?:230|[5-8]\\d\\d)\\d{3}', , , , '1624756789', , , , [6]],
                  [, , '76245[06]\\d{4}|7(?:4576|[59]24\\d|624[0-4689])\\d{5}', , , , '7924123456'],
                  [, , '808162\\d{4}', , , , '8081624567'],
                  [, , '8(?:440[49]06|72299\\d)\\d{3}|(?:8(?:45|70)|90[0167])624\\d{4}', , , , '9016247890'],
                  [, , , , , , , , , [-1]],
                  [, , '70\\d{8}', , , , '7012345678'],
                  [, , '56\\d{8}', , , , '5612345678'],
                  'IM',
                  44,
                  '00',
                  '0',
                  ,
                  ,
                  '([25-8]\\d{5})$|0|180020',
                  '1624$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '74576|(?:16|7[56])24',
                  [, , , , , , , , , [-1]],
                  [, , '3440[49]06\\d{3}|(?:3(?:08162|3\\d{4}|45624|7(?:0624|2299))|55\\d{4})\\d{4}', , , , '5512345678'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                IN: [
                  ,
                  [, , '(?:000800|[2-9]\\d\\d)\\d{7}|1\\d{7,12}', , , , , , , [8, 9, 10, 11, 12, 13], [6, 7]],
                  [
                    ,
                    ,
                    '2717(?:[2-7]\\d|95)\\d{4}|(?:271[0-689]|782[0-6])[2-7]\\d{5}|(?:170[24]|2(?:(?:[02][2-79]|90)\\d|80[13468])|(?:3(?:23|80)|683|79[1-7])\\d|4(?:20[24]|72[2-8])|552[1-7])\\d{6}|(?:11|33|4[04]|80)[2-7]\\d{7}|(?:342|674|788)(?:[0189][2-7]|[2-7]\\d)\\d{5}|(?:1(?:2[0-249]|3[0-25]|4[145]|[59][14]|6[014]|7[1257]|8[01346])|2(?:1[257]|3[013]|4[01]|5[0137]|6[0158]|78|8[1568]|9[14])|3(?:26|4[13]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[014-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|[578]1|9[15])|6(?:12|[2-47]1|5[17]|6[13]|80)|7(?:12|2[14]|3[134]|4[47]|5[15]|[67]1)|8(?:16|2[014]|3[126]|6[136]|7[078]|8[34]|91))[2-7]\\d{6}|(?:1(?:2[35-8]|3[346-9]|4[236-9]|[59][0235-9]|6[235-9]|7[34689]|8[257-9])|2(?:1[134689]|3[24-8]|4[2-8]|5[25689]|6[2-4679]|7[3-79]|8[2-479]|9[235-9])|3(?:01|1[79]|2[1245]|4[5-8]|5[125689]|6[235-7]|7[157-9]|8[2-46-8])|4(?:1[14578]|2[5689]|3[2-467]|5[4-7]|6[35]|73|8[2689]|9[2389])|5(?:[16][146-9]|2[14-8]|3[1346]|4[14-69]|5[46]|7[2-4]|8[2-8]|9[246])|6(?:1[1358]|2[2457]|3[2-4]|4[235-7]|5[2-689]|6[24578]|7[235689]|8[124-6])|7(?:1[013-9]|2[0235-9]|3[2679]|4[1-35689]|5[2-46-9]|[67][02-9]|8[013-7]|9[089])|8(?:1[1357-9]|2[235-8]|3[03-57-9]|4[0-24-9]|5\\d|6[2457-9]|7[1-6]|8[1256]|9[2-4]))\\d[2-7]\\d{5}',
                    ,
                    ,
                    ,
                    '7410410123',
                    ,
                    ,
                    [10],
                    [6, 7, 8]
                  ],
                  [
                    ,
                    ,
                    '(?:61279|7(?:887[02-9]|9(?:313|79[07-9]))|8(?:079[04-9]|(?:84|91)7[02-8]))\\d{5}|(?:6(?:12|[2-47]1|5[17]|6[13]|80)[0189]|7(?:1(?:2[0189]|9[0-5])|2(?:[14][017-9]|8[0-59])|3(?:2[5-8]|[34][017-9]|9[016-9])|4(?:1[015-9]|[29][89]|39|8[389])|5(?:[15][017-9]|2[04-9]|9[7-9])|6(?:0[0-47]|1[0-257-9]|2[0-4]|3[19]|5[4589])|70[0289]|88[089]|97[02-8])|8(?:0(?:6[67]|7[02-8])|70[017-9]|84[01489]|91[0-289]))\\d{6}|(?:7(?:31|4[47])|8(?:16|2[014]|3[126]|6[136]|7[78]|83))(?:[0189]\\d|7[02-8])\\d{5}|(?:6(?:[09]\\d|1[04679]|2[03689]|3[05-9]|4[0489]|50|6[069]|7[07]|8[7-9])|7(?:0\\d|2[0235-79]|3[05-8]|40|5[0346-8]|6[6-9]|7[1-9]|8[0-79]|9[089])|8(?:0[01589]|1[0-57-9]|2[235-9]|3[03-57-9]|[45]\\d|6[02457-9]|7[1-69]|8[0-25-9]|9[02-9])|9\\d\\d)\\d{7}|(?:6(?:(?:1[1358]|2[2457]|3[2-4]|4[235-7]|5[2-689]|6[24578]|8[124-6])\\d|7(?:[235689]\\d|4[0189]))|7(?:1(?:[013-8]\\d|9[6-9])|28[6-8]|3(?:2[0-49]|9[2-5])|4(?:1[2-4]|[29][0-7]|3[0-8]|[56]\\d|8[0-24-7])|5(?:2[1-3]|9[0-6])|6(?:0[5689]|2[5-9]|3[02-8]|4\\d|5[0-367])|70[13-7]|881))[0189]\\d{5}',
                    ,
                    ,
                    ,
                    '8123456789',
                    ,
                    ,
                    [10]
                  ],
                  [, , '000800\\d{7}|1(?:600\\d{6}|80(?:0\\d{4,9}|3\\d{9}))', , , , '1800123456'],
                  [, , '186[12]\\d{9}', , , , '1861123456789', , , [13]],
                  [, , '1860\\d{7}', , , , '18603451234', , , [11]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'IN',
                  91,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{7})', '$1', ['575']],
                    [
                      ,
                      '(\\d{8})',
                      '$1',
                      ['5(?:0|2[23]|3[03]|[67]1|88)', '5(?:0|2(?:21|3)|3(?:0|3[23])|616|717|888)', '5(?:0|2(?:21|3)|3(?:0|3[23])|616|717|8888)'],
                      ,
                      ,
                      1
                    ],
                    [, '(\\d{4})(\\d{4,5})', '$1 $2', ['180', '1800'], , , 1],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['140'], , , 1],
                    [
                      ,
                      '(\\d{2})(\\d{4})(\\d{4})',
                      '$1 $2 $3',
                      [
                        '11|2[02]|33|4[04]|79[1-7]|80[2-46]',
                        '11|2[02]|33|4[04]|79(?:[1-6]|7[19])|80(?:[2-4]|6[0-589])',
                        '11|2[02]|33|4[04]|79(?:[124-6]|3(?:[02-9]|1[0-24-9])|7(?:1|9[1-6]))|80(?:[2-4]|6[0-589])'
                      ],
                      '0$1',
                      ,
                      1
                    ],
                    [
                      ,
                      '(\\d{3})(\\d{3})(\\d{4})',
                      '$1 $2 $3',
                      [
                        '1(?:2[0-249]|3[0-25]|4[145]|[68]|7[1257])|2(?:1[257]|3[013]|4[01]|5[0137]|6[0158]|78|8[1568])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|5[12]|[78]1)|6(?:12|[2-4]1|5[17]|6[13]|80)|7(?:12|3[134]|4[47]|61|88)|8(?:16|2[014]|3[126]|6[136]|7[078]|8[34]|91)|(?:43|59|75)[15]|(?:1[59]|29|67|72)[14]',
                        '1(?:2[0-24]|3[0-25]|4[145]|[59][14]|6[1-9]|7[1257]|8[1-57-9])|2(?:1[257]|3[013]|4[01]|5[0137]|6[058]|78|8[1568]|9[14])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|[578]1|9[15])|674|7(?:(?:2[14]|3[34]|5[15])[2-6]|61[346]|88[0-8])|8(?:70[2-6]|84[235-7]|91[3-7])|(?:1(?:29|60|8[06])|261|552|6(?:12|[2-47]1|5[17]|6[13]|80)|7(?:12|31|4[47])|8(?:16|2[014]|3[126]|6[136]|7[78]|83))[2-7]',
                        '1(?:2[0-24]|3[0-25]|4[145]|[59][14]|6[1-9]|7[1257]|8[1-57-9])|2(?:1[257]|3[013]|4[01]|5[0137]|6[058]|78|8[1568]|9[14])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|[578]1|9[15])|6(?:12(?:[2-6]|7[0-8])|74[2-7])|7(?:(?:2[14]|5[15])[2-6]|3171|61[346]|88(?:[2-7]|82))|8(?:70[2-6]|84(?:[2356]|7[19])|91(?:[3-6]|7[19]))|73[134][2-6]|(?:74[47]|8(?:16|2[014]|3[126]|6[136]|7[78]|83))(?:[2-6]|7[19])|(?:1(?:29|60|8[06])|261|552|6(?:[2-4]1|5[17]|6[13]|7(?:1|4[0189])|80)|7(?:12|88[01]))[2-7]'
                      ],
                      '0$1',
                      ,
                      1
                    ],
                    [
                      ,
                      '(\\d{4})(\\d{3})(\\d{3})',
                      '$1 $2 $3',
                      [
                        '1(?:[2-479]|5[0235-9])|[2-5]|6(?:1[1358]|2[2457-9]|3[2-5]|4[235-7]|5[2-689]|6[24578]|7[235689]|8[1-6])|7(?:1[013-9]|28|3[129]|4[1-35689]|5[29]|6[02-5]|70)|807',
                        '1(?:[2-479]|5[0235-9])|[2-5]|6(?:1[1358]|2(?:[2457]|84|95)|3(?:[2-4]|55)|4[235-7]|5[2-689]|6[24578]|7[235689]|8[1-6])|7(?:1(?:[013-8]|9[6-9])|28[6-8]|3(?:17|2[0-49]|9[2-57])|4(?:1[2-4]|[29][0-7]|3[0-8]|[56]|8[0-24-7])|5(?:2[1-3]|9[0-6])|6(?:0[5689]|2[5-9]|3[02-8]|4|5[0-367])|70[13-7])|807[19]',
                        '1(?:[2-479]|5(?:[0236-9]|5[013-9]))|[2-5]|6(?:2(?:84|95)|355|83)|73179|807(?:1|9[1-3])|(?:1552|6(?:1[1358]|2[2457]|3[2-4]|4[235-7]|5[2-689]|6[24578]|7[235689]|8[124-6])\\d|7(?:1(?:[013-8]\\d|9[6-9])|28[6-8]|3(?:2[0-49]|9[2-57])|4(?:1[2-4]|[29][0-7]|3[0-8]|[56]\\d|8[0-24-7])|5(?:2[1-3]|9[0-6])|6(?:0[5689]|2[5-9]|3[02-8]|4\\d|5[0-367])|70[13-7]))[2-7]'
                      ],
                      '0$1',
                      ,
                      1
                    ],
                    [, '(\\d{5})(\\d{5})', '$1 $2', ['[6-9]'], '0$1', , 1],
                    [, '(\\d{4})(\\d{2,4})(\\d{4})', '$1 $2 $3', ['1(?:6|8[06])', '1(?:6|8[06]0)'], , , 1],
                    [, '(\\d{3})(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3 $4', ['0']],
                    [, '(\\d{4})(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['18'], , , 1]
                  ],
                  [
                    [
                      ,
                      '(\\d{8})',
                      '$1',
                      ['5(?:0|2[23]|3[03]|[67]1|88)', '5(?:0|2(?:21|3)|3(?:0|3[23])|616|717|888)', '5(?:0|2(?:21|3)|3(?:0|3[23])|616|717|8888)'],
                      ,
                      ,
                      1
                    ],
                    [, '(\\d{4})(\\d{4,5})', '$1 $2', ['180', '1800'], , , 1],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['140'], , , 1],
                    [
                      ,
                      '(\\d{2})(\\d{4})(\\d{4})',
                      '$1 $2 $3',
                      [
                        '11|2[02]|33|4[04]|79[1-7]|80[2-46]',
                        '11|2[02]|33|4[04]|79(?:[1-6]|7[19])|80(?:[2-4]|6[0-589])',
                        '11|2[02]|33|4[04]|79(?:[124-6]|3(?:[02-9]|1[0-24-9])|7(?:1|9[1-6]))|80(?:[2-4]|6[0-589])'
                      ],
                      '0$1',
                      ,
                      1
                    ],
                    [
                      ,
                      '(\\d{3})(\\d{3})(\\d{4})',
                      '$1 $2 $3',
                      [
                        '1(?:2[0-249]|3[0-25]|4[145]|[68]|7[1257])|2(?:1[257]|3[013]|4[01]|5[0137]|6[0158]|78|8[1568])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|5[12]|[78]1)|6(?:12|[2-4]1|5[17]|6[13]|80)|7(?:12|3[134]|4[47]|61|88)|8(?:16|2[014]|3[126]|6[136]|7[078]|8[34]|91)|(?:43|59|75)[15]|(?:1[59]|29|67|72)[14]',
                        '1(?:2[0-24]|3[0-25]|4[145]|[59][14]|6[1-9]|7[1257]|8[1-57-9])|2(?:1[257]|3[013]|4[01]|5[0137]|6[058]|78|8[1568]|9[14])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|[578]1|9[15])|674|7(?:(?:2[14]|3[34]|5[15])[2-6]|61[346]|88[0-8])|8(?:70[2-6]|84[235-7]|91[3-7])|(?:1(?:29|60|8[06])|261|552|6(?:12|[2-47]1|5[17]|6[13]|80)|7(?:12|31|4[47])|8(?:16|2[014]|3[126]|6[136]|7[78]|83))[2-7]',
                        '1(?:2[0-24]|3[0-25]|4[145]|[59][14]|6[1-9]|7[1257]|8[1-57-9])|2(?:1[257]|3[013]|4[01]|5[0137]|6[058]|78|8[1568]|9[14])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|[578]1|9[15])|6(?:12(?:[2-6]|7[0-8])|74[2-7])|7(?:(?:2[14]|5[15])[2-6]|3171|61[346]|88(?:[2-7]|82))|8(?:70[2-6]|84(?:[2356]|7[19])|91(?:[3-6]|7[19]))|73[134][2-6]|(?:74[47]|8(?:16|2[014]|3[126]|6[136]|7[78]|83))(?:[2-6]|7[19])|(?:1(?:29|60|8[06])|261|552|6(?:[2-4]1|5[17]|6[13]|7(?:1|4[0189])|80)|7(?:12|88[01]))[2-7]'
                      ],
                      '0$1',
                      ,
                      1
                    ],
                    [
                      ,
                      '(\\d{4})(\\d{3})(\\d{3})',
                      '$1 $2 $3',
                      [
                        '1(?:[2-479]|5[0235-9])|[2-5]|6(?:1[1358]|2[2457-9]|3[2-5]|4[235-7]|5[2-689]|6[24578]|7[235689]|8[1-6])|7(?:1[013-9]|28|3[129]|4[1-35689]|5[29]|6[02-5]|70)|807',
                        '1(?:[2-479]|5[0235-9])|[2-5]|6(?:1[1358]|2(?:[2457]|84|95)|3(?:[2-4]|55)|4[235-7]|5[2-689]|6[24578]|7[235689]|8[1-6])|7(?:1(?:[013-8]|9[6-9])|28[6-8]|3(?:17|2[0-49]|9[2-57])|4(?:1[2-4]|[29][0-7]|3[0-8]|[56]|8[0-24-7])|5(?:2[1-3]|9[0-6])|6(?:0[5689]|2[5-9]|3[02-8]|4|5[0-367])|70[13-7])|807[19]',
                        '1(?:[2-479]|5(?:[0236-9]|5[013-9]))|[2-5]|6(?:2(?:84|95)|355|83)|73179|807(?:1|9[1-3])|(?:1552|6(?:1[1358]|2[2457]|3[2-4]|4[235-7]|5[2-689]|6[24578]|7[235689]|8[124-6])\\d|7(?:1(?:[013-8]\\d|9[6-9])|28[6-8]|3(?:2[0-49]|9[2-57])|4(?:1[2-4]|[29][0-7]|3[0-8]|[56]\\d|8[0-24-7])|5(?:2[1-3]|9[0-6])|6(?:0[5689]|2[5-9]|3[02-8]|4\\d|5[0-367])|70[13-7]))[2-7]'
                      ],
                      '0$1',
                      ,
                      1
                    ],
                    [, '(\\d{5})(\\d{5})', '$1 $2', ['[6-9]'], '0$1', , 1],
                    [, '(\\d{4})(\\d{2,4})(\\d{4})', '$1 $2 $3', ['1(?:6|8[06])', '1(?:6|8[06]0)'], , , 1],
                    [, '(\\d{4})(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['18'], , , 1]
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '1(?:600\\d{6}|800\\d{4,9})|(?:000800|18(?:03\\d\\d|6(?:0|[12]\\d\\d)))\\d{7}'],
                  [, , '140\\d{7}', , , , '1409305260', , , [10]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                IO: [
                  ,
                  [, , '3\\d{6}', , , , , , , [7]],
                  [, , '37\\d{5}', , , , '3709100'],
                  [, , '38\\d{5}', , , , '3801234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'IO',
                  246,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{4})', '$1 $2', ['3']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                IQ: [
                  ,
                  [, , '(?:1|7\\d\\d)\\d{7}|[2-6]\\d{7,8}', , , , , , , [8, 9, 10], [6, 7]],
                  [, , '1\\d{7}|(?:2[13-5]|3[02367]|4[023]|5[03]|6[026])\\d{6,7}', , , , '12345678', , , [8, 9], [6, 7]],
                  [, , '7[3-9]\\d{8}', , , , '7912345678', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'IQ',
                  964,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{4})', '$1 $2 $3', ['1'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[2-6]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['7'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                IR: [
                  ,
                  [, , '[1-9]\\d{9}|(?:[1-8]\\d\\d|9)\\d{3,4}', , , , , , , [4, 5, 6, 7, 10], [8]],
                  [
                    ,
                    ,
                    '(?:1[137]|2[13-68]|3[1458]|4[145]|5[1468]|6[16]|7[1467]|8[13467])(?:[03-57]\\d{7}|[16]\\d{3}(?:\\d{4})?|[289]\\d{3}(?:\\d(?:\\d{3})?)?)|94(?:000[09]|(?:12\\d|30[0-2])\\d|2(?:121|[2689]0\\d)|4(?:111|40\\d))\\d{4}',
                    ,
                    ,
                    ,
                    '2123456789',
                    ,
                    ,
                    [6, 7, 10],
                    [4, 5, 8]
                  ],
                  [
                    ,
                    ,
                    '9(?:(?:0[0-5]|[13]\\d|2[0-3])\\d\\d|9(?:[0-46]\\d\\d|5(?:10|5\\d)|8(?:[12]\\d|88)|9(?:0[0-3]|[19]\\d|21|69|77|8[7-9])))\\d{5}',
                    ,
                    ,
                    ,
                    '9123456789',
                    ,
                    ,
                    [10]
                  ],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'IR',
                  98,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4,5})', '$1', ['96'], '0$1'],
                    [, '(\\d{2})(\\d{4,5})', '$1 $2', ['(?:1[137]|2[13-68]|3[1458]|4[145]|5[1468]|6[16]|7[1467]|8[13467])[12689]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['9'], '0$1'],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1 $2 $3', ['[1-8]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '9(?:4440\\d{5}|6(?:0[12]|2[16-8]|3(?:08|[14]5|[23]|66)|4(?:0|80)|5[01]|6[89]|86|9[19]))', , , , , , , [4, 5, 10]],
                  [, , '96(?:0[12]|2[16-8]|3(?:08|[14]5|[23]|66)|4(?:0|80)|5[01]|6[89]|86|9[19])', , , , '9601', , , [4, 5]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                IS: [
                  ,
                  [, , '(?:38\\d|[4-9])\\d{6}', , , , , , , [7, 9]],
                  [
                    ,
                    ,
                    '(?:4(?:1[0-24-69]|2[0-7]|[37][0-8]|4[0-24589]|5[0-68]|6\\d|8[0-36-8])|5(?:05|[156]\\d|2[02578]|3[0-579]|4[03-7]|7[0-2578]|8[0-35-9]|9[013-689])|872)\\d{4}',
                    ,
                    ,
                    ,
                    '4101234',
                    ,
                    ,
                    [7]
                  ],
                  [
                    ,
                    ,
                    '(?:38[589]\\d\\d|6(?:1[1-8]|2[0-6]|3[026-9]|4[014679]|5[0159]|6[0-69]|70|8[06-8]|9\\d)|7(?:5[057]|[6-9]\\d)|8(?:2[0-59]|[3-69]\\d|8[238]))\\d{4}',
                    ,
                    ,
                    ,
                    '6111234'
                  ],
                  [, , '80[0-8]\\d{4}', , , , '8001234', , , [7]],
                  [, , '90(?:0\\d|1[5-79]|2[015-79]|3[135-79]|4[125-7]|5[25-79]|7[1-37]|8[0-35-7])\\d{3}', , , , '9001234', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '49[0-24-79]\\d{4}', , , , '4921234', , , [7]],
                  'IS',
                  354,
                  '00|1(?:0(?:01|[12]0)|100)',
                  ,
                  ,
                  ,
                  ,
                  ,
                  '00',
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['[4-9]']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['3']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '809\\d{4}', , , , '8091234', , , [7]],
                  ,
                  ,
                  [, , '(?:689|8(?:7[18]|80)|95[48])\\d{4}', , , , '6891234', , , [7]]
                ],
                IT: [
                  ,
                  [, , '0\\d{5,11}|1\\d{8,10}|3(?:[0-8]\\d{7,10}|9\\d{7,8})|(?:43|55|70)\\d{8}|8\\d{5}(?:\\d{2,4})?', , , , , , , [6, 7, 8, 9, 10, 11, 12]],
                  [
                    ,
                    ,
                    '0(?:669[0-79]\\d{1,6}|831\\d{2,8})|0(?:1(?:[0159]\\d|[27][1-5]|31|4[1-4]|6[1356]|8[2-57])|2\\d\\d|3(?:[0159]\\d|2[1-4]|3[12]|[48][1-6]|6[2-59]|7[1-7])|4(?:[0159]\\d|[23][1-9]|4[245]|6[1-5]|7[1-4]|81)|5(?:[0159]\\d|2[1-5]|3[2-6]|4[1-79]|6[4-6]|7[1-578]|8[3-8])|6(?:[0-57-9]\\d|6[0-8])|7(?:[0159]\\d|2[12]|3[1-7]|4[2-46]|6[13569]|7[13-6]|8[1-59])|8(?:[0159]\\d|2[3-578]|3[2356]|[6-8][1-5])|9(?:[0159]\\d|[238][1-5]|4[12]|6[1-8]|7[1-6]))\\d{2,7}',
                    ,
                    ,
                    ,
                    '0212345678'
                  ],
                  [, , '3[2-9]\\d{7,8}|(?:31|43)\\d{8}', , , , '3123456789', , , [9, 10]],
                  [, , '80(?:0\\d{3}|3)\\d{3}', , , , '800123456', , , [6, 9]],
                  [
                    ,
                    ,
                    '(?:0878\\d{3}|89(?:2\\d|3[04]|4(?:[0-4]|[5-9]\\d\\d)|5[0-4]))\\d\\d|(?:1(?:44|6[346])|89(?:38|5[5-9]|9))\\d{6}',
                    ,
                    ,
                    ,
                    '899123456',
                    ,
                    ,
                    [6, 8, 9, 10]
                  ],
                  [, , '84(?:[08]\\d{3}|[17])\\d{3}', , , , '848123456', , , [6, 9]],
                  [, , '1(?:78\\d|99)\\d{6}', , , , '1781234567', , , [9, 10]],
                  [, , '55\\d{8}', , , , '5512345678', , , [10]],
                  'IT',
                  39,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4,5})', '$1', ['1(?:0|9[246])', '1(?:0|9(?:2[2-9]|[46]))']],
                    [, '(\\d{6})', '$1', ['1(?:1|92)']],
                    [, '(\\d{2})(\\d{4,6})', '$1 $2', ['0[26]']],
                    [, '(\\d{3})(\\d{3,6})', '$1 $2', ['0[13-57-9][0159]|8(?:03|4[17]|9[2-5])', '0[13-57-9][0159]|8(?:03|4[17]|9(?:2|3[04]|[45][0-4]))']],
                    [, '(\\d{4})(\\d{2,6})', '$1 $2', ['0(?:[13-579][2-46-8]|8[236-8])']],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['894']],
                    [, '(\\d{2})(\\d{3,4})(\\d{4})', '$1 $2 $3', ['0[26]|5']],
                    [, '(\\d{3})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['1(?:44|[679])|[378]|43']],
                    [, '(\\d{3})(\\d{3,4})(\\d{4})', '$1 $2 $3', ['0[13-57-9][0159]|14']],
                    [, '(\\d{2})(\\d{4})(\\d{5})', '$1 $2 $3', ['0[26]']],
                    [, '(\\d{4})(\\d{3})(\\d{4})', '$1 $2 $3', ['0']],
                    [, '(\\d{3})(\\d{4})(\\d{4,5})', '$1 $2 $3', ['[03]']]
                  ],
                  [
                    [, '(\\d{2})(\\d{4,6})', '$1 $2', ['0[26]']],
                    [, '(\\d{3})(\\d{3,6})', '$1 $2', ['0[13-57-9][0159]|8(?:03|4[17]|9[2-5])', '0[13-57-9][0159]|8(?:03|4[17]|9(?:2|3[04]|[45][0-4]))']],
                    [, '(\\d{4})(\\d{2,6})', '$1 $2', ['0(?:[13-579][2-46-8]|8[236-8])']],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['894']],
                    [, '(\\d{2})(\\d{3,4})(\\d{4})', '$1 $2 $3', ['0[26]|5']],
                    [, '(\\d{3})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['1(?:44|[679])|[378]|43']],
                    [, '(\\d{3})(\\d{3,4})(\\d{4})', '$1 $2 $3', ['0[13-57-9][0159]|14']],
                    [, '(\\d{2})(\\d{4})(\\d{5})', '$1 $2 $3', ['0[26]']],
                    [, '(\\d{4})(\\d{3})(\\d{4})', '$1 $2 $3', ['0']],
                    [, '(\\d{3})(\\d{4})(\\d{4,5})', '$1 $2 $3', ['[03]']]
                  ],
                  [, , , , , , , , , [-1]],
                  1,
                  ,
                  [, , '848\\d{6}', , , , , , , [9]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '3[2-8]\\d{9,10}', , , , '33101234501', , , [11, 12]]
                ],
                JE: [
                  ,
                  [, , '1534\\d{6}|(?:[3578]\\d|90)\\d{8}', , , , , , , [10], [6]],
                  [, , '1534[0-24-8]\\d{5}', , , , '1534456789', , , , [6]],
                  [, , '7(?:(?:(?:50|82)9|937)\\d|7(?:00[378]|97\\d))\\d{5}', , , , '7797712345'],
                  [, , '80(?:07(?:35|81)|8901)\\d{4}', , , , '8007354567'],
                  [, , '(?:8(?:4(?:4(?:4(?:05|42|69)|703)|5(?:041|800))|7(?:0002|1206))|90(?:066[59]|1810|71(?:07|55)))\\d{4}', , , , '9018105678'],
                  [, , , , , , , , , [-1]],
                  [, , '701511\\d{4}', , , , '7015115678'],
                  [, , '56\\d{8}', , , , '5612345678'],
                  'JE',
                  44,
                  '00',
                  '0',
                  ,
                  ,
                  '([0-24-8]\\d{5})$|0|180020',
                  '1534$1',
                  ,
                  ,
                  ,
                  ,
                  [, , '76(?:464|652)\\d{5}|76(?:0[0-28]|2[356]|34|4[01347]|5[49]|6[0-369]|77|8[14]|9[139])\\d{6}', , , , '7640123456'],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '(?:3(?:0(?:07(?:35|81)|8901)|3\\d{4}|4(?:4(?:4(?:05|42|69)|703)|5(?:041|800))|7(?:0002|1206))|55\\d{4})\\d{4}', , , , '5512345678'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                JM: [
                  ,
                  [, , '(?:[58]\\d\\d|658|900)\\d{7}', , , , , , , [10], [7]],
                  [
                    ,
                    ,
                    '8766060\\d{3}|(?:658(?:2(?:[0-8]\\d|9[0-46-9])|[3-9]\\d\\d)|876(?:52[35]|6(?:0[1-3579]|1[0235-9]|[23]\\d|40|5[06]|6[2-589]|7[0-25-9]|8[04]|9[4-9])|7(?:0[2-689]|[1-6]\\d|8[056]|9[45])|9(?:0[1-8]|1[02378]|[2-8]\\d|9[2-468])))\\d{4}',
                    ,
                    ,
                    ,
                    '8765230123',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [
                    ,
                    ,
                    '(?:658295|876(?:2(?:0[1-9]|[13-9]\\d|2[013-9])|[348]\\d\\d|5(?:0[1-9]|[1-9]\\d)|6(?:4[89]|6[67])|7(?:0[07]|7\\d|8[1-47-9]|9[0-36-9])|9(?:[01]9|9[0579])))\\d{4}',
                    ,
                    ,
                    ,
                    '8762101234',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'JM',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '1',
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '658|876',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                JO: [
                  ,
                  [, , '(?:(?:[2689]|7\\d)\\d|32|53)\\d{6}', , , , , , , [8, 9]],
                  [
                    ,
                    ,
                    '87(?:000|90[01])\\d{3}|(?:2(?:6(?:2[0-35-9]|3[0-578]|4[24-7]|5[0-24-8]|[6-8][023]|9[0-3])|7(?:0[1-79]|10|2[014-7]|3[0-689]|4[019]|5[0-3578]))|32(?:0[1-69]|1[1-35-7]|2[024-7]|3\\d|4[0-3]|[5-7][023])|53(?:0[0-3]|[13][023]|2[0-59]|49|5[0-35-9]|6[15]|7[45]|8[1-6]|9[0-36-9])|6(?:2(?:[05]0|22)|3(?:00|33)|4(?:0[0-25]|1[2-7]|2[0569]|[38][07-9]|4[025689]|6[0-589]|7\\d|9[0-2])|5(?:[01][056]|2[034]|3[0-57-9]|4[178]|5[0-69]|6[0-35-9]|7[1-379]|8[0-68]|9[0239]))|87(?:20|7[078]|99))\\d{4}',
                    ,
                    ,
                    ,
                    '62001234',
                    ,
                    ,
                    [8]
                  ],
                  [, , '7(?:[78][0-25-9]|9\\d)\\d{6}', , , , '790123456', , , [9]],
                  [, , '80\\d{6}', , , , '80012345', , , [8]],
                  [, , '9\\d{7}', , , , '90012345', , , [8]],
                  [, , '85\\d{6}', , , , '85012345', , , [8]],
                  [, , '70\\d{7}', , , , '700123456', , , [9]],
                  [, , , , , , , , , [-1]],
                  'JO',
                  962,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{4})', '$1 $2 $3', ['[2356]|87'], '(0$1)'],
                    [, '(\\d{3})(\\d{5,6})', '$1 $2', ['[89]'], '0$1'],
                    [, '(\\d{2})(\\d{7})', '$1 $2', ['70'], '0$1'],
                    [, '(\\d)(\\d{4})(\\d{4})', '$1 $2 $3', ['7'], '0$1']
                  ],
                  ,
                  [, , '74(?:66|77)\\d{5}', , , , '746612345', , , [9]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '8(?:10|8\\d)\\d{5}', , , , '88101234', , , [8]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                JP: [
                  ,
                  [, , '00[1-9]\\d{6,14}|[25-9]\\d{9}|(?:00|[1-9]\\d\\d)\\d{6}', , , , , , , [8, 9, 10, 11, 12, 13, 14, 15, 16, 17]],
                  [
                    ,
                    ,
                    '(?:1(?:1[235-8]|2[3-6]|3[3-9]|4[2-6]|[58][2-8]|6[2-7]|7[2-9]|9[1-9])|(?:2[2-9]|[36][1-9])\\d|4(?:[2-578]\\d|6[02-8]|9[2-59])|5(?:[2-589]\\d|6[1-9]|7[2-8])|7(?:[25-9]\\d|3[4-9]|4[02-9])|8(?:[2679]\\d|3[2-9]|4[5-9]|5[1-9]|8[03-9])|9(?:[2-58]\\d|[679][1-9]))\\d{6}',
                    ,
                    ,
                    ,
                    '312345678',
                    ,
                    ,
                    [9]
                  ],
                  [, , '(?:601[0-4]0|[7-9]0[1-9]\\d\\d)\\d{5}', , , , '9012345678', , , [10]],
                  [, , '00777(?:[01]|5\\d)\\d\\d|(?:00(?:7778|882[1245])|(?:120|800\\d)\\d\\d)\\d{4}|00(?:37|66|78)\\d{6,13}', , , , '120123456'],
                  [, , '990\\d{6}', , , , '990123456', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , '60\\d{7}', , , , '601234567', , , [9]],
                  [, , '50[1-9]\\d{7}', , , , '5012345678', , , [10]],
                  'JP',
                  81,
                  '010',
                  '0',
                  ,
                  ,
                  '(000[2569]\\d{4,6})$|(?:(?:003768)0?)|0',
                  '$1',
                  ,
                  ,
                  [
                    [, '(\\d{4})(\\d{4})', '$1-$2', ['007', '0077', '00777', '00777[01]']],
                    [, '(\\d{8,10})', '$1', ['000']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1-$2-$3', ['(?:12|57|99)0'], '0$1'],
                    [
                      ,
                      '(\\d{4})(\\d)(\\d{4})',
                      '$1-$2-$3',
                      [
                        '1(?:26|3[79]|4[56]|5[4-68]|6[3-5])|499|5(?:76|97)|746|8(?:3[89]|47|51)|9(?:80|9[16])',
                        '1(?:267|3(?:7[247]|9[278])|466|5(?:47|58|64)|6(?:3[245]|48|5[4-68]))|499[2468]|5(?:76|97)9|7468|8(?:3(?:8[7-9]|96)|477|51[2-9])|9(?:802|9(?:1[23]|69))|1(?:45|58)[67]',
                        '1(?:267|3(?:7[247]|9[278])|466|5(?:47|58|64)|6(?:3[245]|48|5[4-68]))|499[2468]|5(?:769|979[2-69])|7468|8(?:3(?:8[7-9]|96[2457-9])|477|51[2-9])|9(?:802|9(?:1[23]|69))|1(?:45|58)[67]'
                      ],
                      '0$1'
                    ],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1-$2-$3', ['60'], '0$1'],
                    [, '(\\d)(\\d{4})(\\d{4})', '$1-$2-$3', ['3|4(?:2[09]|7[01])|6[1-9]', '3|4(?:2(?:0|9[02-69])|7(?:0[019]|1))|6[1-9]'], '0$1'],
                    [
                      ,
                      '(\\d{2})(\\d{3})(\\d{4})',
                      '$1-$2-$3',
                      [
                        '1(?:1|5[45]|77|88|9[69])|2(?:2[1-37]|3[0-269]|4[59]|5|6[24]|7[1-358]|8[1369]|9[0-38])|4(?:[28][1-9]|3[0-57]|[45]|6[248]|7[2-579]|9[29])|5(?:2|3[0459]|4[0-369]|5[29]|8[02389]|9[0-389])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9[2-6])|8(?:2[124589]|3[26-9]|49|51|6|7[0-468]|8[68]|9[019])|9(?:[23][1-9]|4[15]|5[138]|6[1-3]|7[156]|8[189]|9[1-489])',
                        '1(?:1|5(?:4[018]|5[017])|77|88|9[69])|2(?:2(?:[127]|3[014-9])|3[0-269]|4[59]|5(?:[1-3]|5[0-69]|9[19])|62|7(?:[1-35]|8[0189])|8(?:[16]|3[0134]|9[0-5])|9(?:[028]|17))|4(?:2(?:[13-79]|8[014-6])|3[0-57]|[45]|6[248]|7[2-47]|8[1-9]|9[29])|5(?:2|3(?:[045]|9[0-8])|4[0-369]|5[29]|8[02389]|9[0-3])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9(?:[23]|4[0-59]|5[01569]|6[0167]))|8(?:2(?:[1258]|4[0-39]|9[0-2469])|3(?:[29]|60)|49|51|6(?:[0-24]|36|5[0-3589]|7[23]|9[01459])|7[0-468]|8[68])|9(?:[23][1-9]|4[15]|5[138]|6[1-3]|7[156]|8[189]|9(?:[1289]|3[34]|4[0178]))|(?:264|837)[016-9]|2(?:57|93)[015-9]|(?:25[0468]|422|838)[01]|(?:47[59]|59[89]|8(?:6[68]|9))[019]',
                        '1(?:1|5(?:4[018]|5[017])|77|88|9[69])|2(?:2[127]|3[0-269]|4[59]|5(?:[1-3]|5[0-69]|9(?:17|99))|6(?:2|4[016-9])|7(?:[1-35]|8[0189])|8(?:[16]|3[0134]|9[0-5])|9(?:[028]|17))|4(?:2(?:[13-79]|8[014-6])|3[0-57]|[45]|6[248]|7[2-47]|9[29])|5(?:2|3(?:[045]|9(?:[0-58]|6[4-9]|7[0-35689]))|4[0-369]|5[29]|8[02389]|9[0-3])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9(?:[23]|4[0-59]|5[01569]|6[0167]))|8(?:2(?:[1258]|4[0-39]|9[0169])|3(?:[29]|60|7(?:[017-9]|6[6-8]))|49|51|6(?:[0-24]|36[2-57-9]|5(?:[0-389]|5[23])|6(?:[01]|9[178])|7(?:2[2-468]|3[78])|9[0145])|7[0-468]|8[68])|9(?:4[15]|5[138]|7[156]|8[189]|9(?:[1289]|3(?:31|4[357])|4[0178]))|(?:8294|96)[1-3]|2(?:57|93)[015-9]|(?:223|8699)[014-9]|(?:25[0468]|422|838)[01]|(?:48|8292|9[23])[1-9]|(?:47[59]|59[89]|8(?:68|9))[019]'
                      ],
                      '0$1'
                    ],
                    [, '(\\d{3})(\\d{2})(\\d{4})', '$1-$2-$3', ['[14]|[289][2-9]|5[3-9]|7[2-4679]'], '0$1'],
                    [, '(\\d{4})(\\d{2})(\\d{3,4})', '$1-$2-$3', ['007', '0077']],
                    [, '(\\d{4})(\\d{2})(\\d{4})', '$1-$2-$3', ['008']],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1-$2-$3', ['800'], '0$1'],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1-$2-$3', ['[25-9]'], '0$1'],
                    [, '(\\d{4})(\\d{3})(\\d{3,4})', '$1-$2-$3', ['0']],
                    [, '(\\d{4})(\\d{4})(\\d{4,5})', '$1-$2-$3', ['0']],
                    [, '(\\d{4})(\\d{5})(\\d{5,6})', '$1-$2-$3', ['0']],
                    [, '(\\d{4})(\\d{6})(\\d{6,7})', '$1-$2-$3', ['0']]
                  ],
                  [
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1-$2-$3', ['(?:12|57|99)0'], '0$1'],
                    [
                      ,
                      '(\\d{4})(\\d)(\\d{4})',
                      '$1-$2-$3',
                      [
                        '1(?:26|3[79]|4[56]|5[4-68]|6[3-5])|499|5(?:76|97)|746|8(?:3[89]|47|51)|9(?:80|9[16])',
                        '1(?:267|3(?:7[247]|9[278])|466|5(?:47|58|64)|6(?:3[245]|48|5[4-68]))|499[2468]|5(?:76|97)9|7468|8(?:3(?:8[7-9]|96)|477|51[2-9])|9(?:802|9(?:1[23]|69))|1(?:45|58)[67]',
                        '1(?:267|3(?:7[247]|9[278])|466|5(?:47|58|64)|6(?:3[245]|48|5[4-68]))|499[2468]|5(?:769|979[2-69])|7468|8(?:3(?:8[7-9]|96[2457-9])|477|51[2-9])|9(?:802|9(?:1[23]|69))|1(?:45|58)[67]'
                      ],
                      '0$1'
                    ],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1-$2-$3', ['60'], '0$1'],
                    [, '(\\d)(\\d{4})(\\d{4})', '$1-$2-$3', ['3|4(?:2[09]|7[01])|6[1-9]', '3|4(?:2(?:0|9[02-69])|7(?:0[019]|1))|6[1-9]'], '0$1'],
                    [
                      ,
                      '(\\d{2})(\\d{3})(\\d{4})',
                      '$1-$2-$3',
                      [
                        '1(?:1|5[45]|77|88|9[69])|2(?:2[1-37]|3[0-269]|4[59]|5|6[24]|7[1-358]|8[1369]|9[0-38])|4(?:[28][1-9]|3[0-57]|[45]|6[248]|7[2-579]|9[29])|5(?:2|3[0459]|4[0-369]|5[29]|8[02389]|9[0-389])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9[2-6])|8(?:2[124589]|3[26-9]|49|51|6|7[0-468]|8[68]|9[019])|9(?:[23][1-9]|4[15]|5[138]|6[1-3]|7[156]|8[189]|9[1-489])',
                        '1(?:1|5(?:4[018]|5[017])|77|88|9[69])|2(?:2(?:[127]|3[014-9])|3[0-269]|4[59]|5(?:[1-3]|5[0-69]|9[19])|62|7(?:[1-35]|8[0189])|8(?:[16]|3[0134]|9[0-5])|9(?:[028]|17))|4(?:2(?:[13-79]|8[014-6])|3[0-57]|[45]|6[248]|7[2-47]|8[1-9]|9[29])|5(?:2|3(?:[045]|9[0-8])|4[0-369]|5[29]|8[02389]|9[0-3])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9(?:[23]|4[0-59]|5[01569]|6[0167]))|8(?:2(?:[1258]|4[0-39]|9[0-2469])|3(?:[29]|60)|49|51|6(?:[0-24]|36|5[0-3589]|7[23]|9[01459])|7[0-468]|8[68])|9(?:[23][1-9]|4[15]|5[138]|6[1-3]|7[156]|8[189]|9(?:[1289]|3[34]|4[0178]))|(?:264|837)[016-9]|2(?:57|93)[015-9]|(?:25[0468]|422|838)[01]|(?:47[59]|59[89]|8(?:6[68]|9))[019]',
                        '1(?:1|5(?:4[018]|5[017])|77|88|9[69])|2(?:2[127]|3[0-269]|4[59]|5(?:[1-3]|5[0-69]|9(?:17|99))|6(?:2|4[016-9])|7(?:[1-35]|8[0189])|8(?:[16]|3[0134]|9[0-5])|9(?:[028]|17))|4(?:2(?:[13-79]|8[014-6])|3[0-57]|[45]|6[248]|7[2-47]|9[29])|5(?:2|3(?:[045]|9(?:[0-58]|6[4-9]|7[0-35689]))|4[0-369]|5[29]|8[02389]|9[0-3])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9(?:[23]|4[0-59]|5[01569]|6[0167]))|8(?:2(?:[1258]|4[0-39]|9[0169])|3(?:[29]|60|7(?:[017-9]|6[6-8]))|49|51|6(?:[0-24]|36[2-57-9]|5(?:[0-389]|5[23])|6(?:[01]|9[178])|7(?:2[2-468]|3[78])|9[0145])|7[0-468]|8[68])|9(?:4[15]|5[138]|7[156]|8[189]|9(?:[1289]|3(?:31|4[357])|4[0178]))|(?:8294|96)[1-3]|2(?:57|93)[015-9]|(?:223|8699)[014-9]|(?:25[0468]|422|838)[01]|(?:48|8292|9[23])[1-9]|(?:47[59]|59[89]|8(?:68|9))[019]'
                      ],
                      '0$1'
                    ],
                    [, '(\\d{3})(\\d{2})(\\d{4})', '$1-$2-$3', ['[14]|[289][2-9]|5[3-9]|7[2-4679]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1-$2-$3', ['800'], '0$1'],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1-$2-$3', ['[25-9]'], '0$1']
                  ],
                  [, , '20\\d{8}', , , , '2012345678', , , [10]],
                  ,
                  ,
                  [, , '00(?:777(?:[01]|(?:5|8\\d)\\d)|882[1245]\\d\\d)\\d\\d|00(?:37|66|78)\\d{6,13}'],
                  [, , '570\\d{6}', , , , '570123456', , , [9]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KE: [
                  ,
                  [, , '(?:[17]\\d\\d|900)\\d{6}|(?:2|80)0\\d{6,7}|[4-6]\\d{6,8}', , , , , , , [7, 8, 9, 10]],
                  [, , '(?:4[245]|5[1-79]|6[01457-9])\\d{5,7}|(?:4[136]|5[08]|62)\\d{7}|(?:[24]0|66)\\d{6,7}', , , , '202012345', , , [7, 8, 9]],
                  [, , '(?:1(?:0[0-8]|1[0-7]|2[014]|30)|7\\d\\d)\\d{6}', , , , '712123456', , , [9]],
                  [, , '800[02-8]\\d{5,6}', , , , '800223456', , , [9, 10]],
                  [, , '900[02-9]\\d{5}', , , , '900223456', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'KE',
                  254,
                  '000',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{5,7})', '$1 $2', ['[24-6]'], '0$1'],
                    [, '(\\d{3})(\\d{6})', '$1 $2', ['[17]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[89]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KG: [
                  ,
                  [, , '8\\d{9}|[235-9]\\d{8}', , , , , , , [9, 10], [5, 6]],
                  [
                    ,
                    ,
                    '312(?:5[0-79]\\d|9(?:[0-689]\\d|7[0-24-9]))\\d{3}|(?:3(?:1(?:2[0-46-8]|3[1-9]|47|[56]\\d)|2(?:22|3[0-479]|6[0-7])|4(?:22|5[6-9]|6\\d)|5(?:22|3[4-7]|59|6\\d)|6(?:22|5[35-7]|6\\d)|7(?:22|3[468]|4[1-9]|59|[67]\\d)|9(?:22|4[1-8]|6\\d))|6(?:09|12|2[2-4])\\d)\\d{5}',
                    ,
                    ,
                    ,
                    '312123456',
                    ,
                    ,
                    [9],
                    [5, 6]
                  ],
                  [
                    ,
                    ,
                    '312(?:58\\d|973)\\d{3}|(?:2(?:0[0-35]|2\\d)|5[0-24-7]\\d|600|7(?:[07]\\d|55)|88[08]|9(?:12|9[05-9]))\\d{6}',
                    ,
                    ,
                    ,
                    '700123456',
                    ,
                    ,
                    [9]
                  ],
                  [, , '800\\d{6,7}', , , , '800123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'KG',
                  996,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})(\\d{5})', '$1 $2', ['3(?:1[346]|[24-79])'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[235-79]|88'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d)(\\d{2,3})', '$1 $2 $3 $4', ['8'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KH: [
                  ,
                  [, , '1\\d{9}|[1-9]\\d{7,8}', , , , , , , [8, 9, 10], [6, 7]],
                  [
                    ,
                    ,
                    '23(?:4(?:[2-4]|[56]\\d)|[568]\\d\\d)\\d{4}|23[236-9]\\d{5}|(?:2[4-6]|3[2-6]|4[2-4]|[5-7][2-5])(?:(?:[237-9]|4[56]|5\\d)\\d{5}|6\\d{5,6})',
                    ,
                    ,
                    ,
                    '23756789',
                    ,
                    ,
                    [8, 9],
                    [6, 7]
                  ],
                  [
                    ,
                    ,
                    '(?:(?:1[28]|3[18]|9[67])\\d|6[016-9]|7(?:[07-9]|[16]\\d)|8(?:[013-79]|8\\d))\\d{6}|(?:1\\d|9[0-57-9])\\d{6}|(?:2[3-6]|3[2-6]|4[2-4]|[5-7][2-5])48\\d{5}',
                    ,
                    ,
                    ,
                    '91234567',
                    ,
                    ,
                    [8, 9]
                  ],
                  [, , '1800(?:1\\d|2[019])\\d{4}', , , , '1800123456', , , [10]],
                  [, , '1900(?:1\\d|2[09])\\d{4}', , , , '1900123456', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'KH',
                  855,
                  '00[14-9]',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[1-9]'], '0$1'],
                    [, '(\\d{4})(\\d{3})(\\d{3})', '$1 $2 $3', ['1']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KI: [
                  ,
                  [, , '(?:[37]\\d|6[0-79])\\d{6}|(?:[2-48]\\d|50)\\d{3}', , , , , , , [5, 8]],
                  [
                    ,
                    ,
                    '(?:[24]\\d|3[1-9]|50|65(?:02[12]|12[56]|22[89]|[3-5]00)|7(?:27\\d\\d|3100|5(?:02[12]|12[56]|22[89]|[34](?:00|81)|500))|8[0-5])\\d{3}',
                    ,
                    ,
                    ,
                    '31234'
                  ],
                  [
                    ,
                    ,
                    '(?:6200[01]|7(?:310[1-9]|5(?:02[03-9]|12[0-47-9]|22[0-7]|[34](?:0[1-9]|8[02-9])|50[1-9])))\\d{3}|(?:63\\d\\d|7(?:(?:[0146-9]\\d|2[0-689])\\d|3(?:[02-9]\\d|1[1-9])|5(?:[0-2][013-9]|[34][1-79]|5[1-9]|[6-9]\\d)))\\d{4}',
                    ,
                    ,
                    ,
                    '72001234',
                    ,
                    ,
                    [8]
                  ],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '30(?:0[01]\\d\\d|12(?:11|20))\\d\\d', , , , '30010000', , , [8]],
                  'KI',
                  686,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KM: [
                  ,
                  [, , '[3478]\\d{6}', , , , , , , [7], [4]],
                  [, , '7[4-7]\\d{5}', , , , '7712345', , , , [4]],
                  [, , '[34]\\d{6}', , , , '3212345'],
                  [, , , , , , , , , [-1]],
                  [, , '8\\d{6}', , , , '8001234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'KM',
                  269,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3', ['[3478]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KN: [
                  ,
                  [, , '(?:[58]\\d\\d|900)\\d{7}', , , , , , , [10], [7]],
                  [, , '869(?:2(?:29|36)|302|4(?:6[015-9]|70)|56[5-7])\\d{4}', , , , '8692361234', , , , [7]],
                  [, , '869(?:48[89]|55[6-8]|66\\d|76[02-7])\\d{4}', , , , '8697652917', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'KN',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-7]\\d{6})$|1',
                  '869$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '869',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KP: [
                  ,
                  [, , '85\\d{6}|(?:19\\d|[2-7])\\d{7}', , , , , , , [8, 10], [6, 7]],
                  [, , '(?:(?:195|2)\\d|3[19]|4[159]|5[37]|6[17]|7[39]|85)\\d{6}', , , , '21234567', , , , [6, 7]],
                  [, , '19[1-3]\\d{7}', , , , '1921234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'KP',
                  850,
                  '00|99',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['8'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{4})', '$1 $2 $3', ['[2-7]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['1'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '238[02-9]\\d{4}|2(?:[0-24-9]\\d|3[0-79])\\d{5}', , , , , , , [8]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KR: [
                  ,
                  [
                    ,
                    ,
                    '00[1-9]\\d{8,11}|(?:[12]|5\\d{3})\\d{7}|[13-6]\\d{9}|(?:[1-6]\\d|80)\\d{7}|[3-6]\\d{4,5}|(?:00|7)0\\d{8}',
                    ,
                    ,
                    ,
                    ,
                    ,
                    ,
                    [5, 6, 8, 9, 10, 11, 12, 13, 14],
                    [3, 4, 7]
                  ],
                  [, , '(?:2|3[1-3]|[46][1-4]|5[1-5])[1-9]\\d{6,7}|(?:3[1-3]|[46][1-4]|5[1-5])1\\d{2,3}', , , , '22123456', , , [5, 6, 8, 9, 10], [3, 4, 7]],
                  [, , '1(?:05(?:[0-8]\\d|9[0-6])|22[13]\\d)\\d{4,5}|1(?:0[0-46-9]|[16-9]\\d|2[013-9])\\d{6,7}', , , , '1020000000', , , [9, 10]],
                  [, , '00(?:308\\d{6,7}|798\\d{7,9})|(?:00368|[38]0)\\d{7}', , , , '801234567', , , [9, 11, 12, 13, 14]],
                  [, , '60[2-9]\\d{6}', , , , '602345678', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , '50\\d{8,9}', , , , '5012345678', , , [10, 11]],
                  [, , '70\\d{8}', , , , '7012345678', , , [10]],
                  'KR',
                  82,
                  '00(?:[125689]|3(?:[46]5|91)|7(?:00|27|3|55|6[126]))',
                  '0',
                  ,
                  ,
                  '0(8(?:[1-46-8]|5\\d\\d))?',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{5})', '$1', ['1[016-9]1', '1[016-9]11', '1[016-9]114'], '0$1'],
                    [, '(\\d{2})(\\d{3,4})', '$1-$2', ['(?:3[1-3]|[46][1-4]|5[1-5])1'], '0$1', '0$CC-$1'],
                    [, '(\\d{4})(\\d{4})', '$1-$2', ['1']],
                    [, '(\\d)(\\d{3,4})(\\d{4})', '$1-$2-$3', ['2'], '0$1', '0$CC-$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1-$2-$3', ['[36]0|8'], '0$1', '0$CC-$1'],
                    [, '(\\d{2})(\\d{3,4})(\\d{4})', '$1-$2-$3', ['[1346]|5[1-5]'], '0$1', '0$CC-$1'],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1-$2-$3', ['[57]'], '0$1', '0$CC-$1'],
                    [, '(\\d{5})(\\d{3})(\\d{3})', '$1 $2 $3', ['003', '0030']],
                    [, '(\\d{2})(\\d{5})(\\d{4})', '$1-$2-$3', ['5'], '0$1', '0$CC-$1'],
                    [, '(\\d{5})(\\d{3,4})(\\d{4})', '$1 $2 $3', ['0']],
                    [, '(\\d{5})(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3 $4', ['0']]
                  ],
                  [
                    [, '(\\d{2})(\\d{3,4})', '$1-$2', ['(?:3[1-3]|[46][1-4]|5[1-5])1'], '0$1', '0$CC-$1'],
                    [, '(\\d{4})(\\d{4})', '$1-$2', ['1']],
                    [, '(\\d)(\\d{3,4})(\\d{4})', '$1-$2-$3', ['2'], '0$1', '0$CC-$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1-$2-$3', ['[36]0|8'], '0$1', '0$CC-$1'],
                    [, '(\\d{2})(\\d{3,4})(\\d{4})', '$1-$2-$3', ['[1346]|5[1-5]'], '0$1', '0$CC-$1'],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1-$2-$3', ['[57]'], '0$1', '0$CC-$1'],
                    [, '(\\d{2})(\\d{5})(\\d{4})', '$1-$2-$3', ['5'], '0$1', '0$CC-$1']
                  ],
                  [, , '15\\d{7,8}', , , , '1523456789', , , [9, 10]],
                  ,
                  ,
                  [, , '00(?:3(?:08\\d{6,7}|68\\d{7})|798\\d{7,9})', , , , , , , [11, 12, 13, 14]],
                  [, , '1(?:5(?:22|33|44|66|77|88|99)|6(?:[07]0|44|6[0168]|88)|8(?:00|33|55|77|99))\\d{4}', , , , '15441234', , , [8]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KW: [
                  ,
                  [, , '18\\d{5}|(?:[2569]\\d|41)\\d{6}', , , , , , , [7, 8]],
                  [, , '2(?:[23]\\d\\d|4(?:[1-35-9]\\d|44)|5(?:0[034]|[2-46]\\d|5[1-3]|7[1-7]))\\d{4}', , , , '22345678', , , [8]],
                  [
                    ,
                    ,
                    '(?:41\\d\\d|5(?:(?:[05]\\d|1[0-7]|6[56])\\d|2(?:22|5[25])|7(?:55|77)|88[58])|6(?:(?:0[034679]|5[015-9]|6\\d)\\d|1(?:00|11|6[16])|2[26]2|3[36]3|4[46]4|7(?:0[013-9]|[67]\\d)|8[68]8|9(?:[069]\\d|3[039]))|9(?:(?:[04679]\\d|8[057-9])\\d|1(?:00|1[01]|99)|2(?:00|2\\d)|3(?:00|3[03])|5(?:00|5\\d)))\\d{4}',
                    ,
                    ,
                    ,
                    '50012345',
                    ,
                    ,
                    [8]
                  ],
                  [, , '18\\d{5}', , , , '1801234', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'KW',
                  965,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})(\\d{3,4})', '$1 $2', ['[169]|2(?:[235]|4[1-35-9])|52']],
                    [, '(\\d{3})(\\d{5})', '$1 $2', ['[245]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KY: [
                  ,
                  [, , '(?:345|[58]\\d\\d|900)\\d{7}', , , , , , , [10], [7]],
                  [
                    ,
                    ,
                    '345(?:2(?:22|3[23]|44|66)|333|444|6(?:23|38|40)|7(?:30|4[35-79]|6[6-9]|77)|8(?:00|1[45]|4[89]|88)|9(?:14|4[035-9]))\\d{4}',
                    ,
                    ,
                    ,
                    '3452221234',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '345(?:32[1-9]|42[0-4]|5(?:1[67]|2[5-79]|4[6-9]|50|76)|649|82[56]|9(?:1[679]|2[2-9]|3[06-9]|90))\\d{4}', , , , '3453231234', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002345678'],
                  [, , '(?:345976|900[2-9]\\d\\d)\\d{4}', , , , '9002345678'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'KY',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-9]\\d{6})$|1',
                  '345$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '345',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KZ: [
                  ,
                  [, , '(?:33622|8\\d{8})\\d{5}|[78]\\d{9}', , , , , , , [10, 14], [5, 6, 7]],
                  [
                    ,
                    ,
                    '(?:33622|7(?:1(?:0(?:[23]\\d|4[0-3]|59|63)|1(?:[23]\\d|4[0-79]|59)|2(?:[23]\\d|59)|3(?:2\\d|3[0-79]|4[0-35-9]|59)|4(?:[24]\\d|3[013-9]|5[1-9]|97)|5(?:2\\d|3[1-9]|4[0-7]|59)|6(?:[2-4]\\d|5[19]|61)|72\\d|8(?:[27]\\d|3[1-46-9]|4[0-5]|59))|2(?:1(?:[23]\\d|4[46-9]|5[3469])|2(?:2\\d|3[0679]|46|5[12679])|3(?:[2-4]\\d|5[139])|4(?:2\\d|3[1-35-9]|59)|5(?:[23]\\d|4[0-8]|59|61)|6(?:2\\d|3[1-9]|4[0-4]|59)|7(?:[2379]\\d|40|5[279])|8(?:[23]\\d|4[0-3]|59)|9(?:2\\d|3[124578]|59))))\\d{5}',
                    ,
                    ,
                    ,
                    '7123456789',
                    ,
                    ,
                    [10],
                    [5, 6, 7]
                  ],
                  [, , '7(?:0[0-25-8]|47|6[0-4]|7[15-8]|85)\\d{7}', , , , '7710009998', , , [10]],
                  [, , '8(?:00|108\\d{3})\\d{7}', , , , '8001234567'],
                  [, , '809\\d{7}', , , , '8091234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , '808\\d{7}', , , , '8081234567', , , [10]],
                  [, , '751\\d{7}', , , , '7511234567', , , [10]],
                  'KZ',
                  7,
                  '810',
                  '8',
                  ,
                  ,
                  '8',
                  ,
                  '8~10',
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '33622|7',
                  [, , '751\\d{7}', , , , , , , [10]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LA: [
                  ,
                  [, , '[23]\\d{9}|3\\d{8}|(?:[235-8]\\d|41)\\d{6}', , , , , , , [8, 9, 10], [6]],
                  [, , '(?:2[13]|[35-7][14]|41|8[1468])\\d{6}', , , , '21212862', , , [8], [6]],
                  [, , '(?:20(?:[23579]\\d|8[78])|30[24]\\d)\\d{6}|30\\d{7}', , , , '2023123456', , , [9, 10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'LA',
                  856,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['2[13]|3[14]|[4-8]'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{3})', '$1 $2 $3 $4', ['3'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['[23]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LB: [
                  ,
                  [, , '[27-9]\\d{7}|[13-9]\\d{6}', , , , , , , [7, 8]],
                  [, , '7(?:62|8[0-6]|9[04-9])\\d{4}|(?:[14-69]\\d|2(?:[14-69]\\d|[78][1-9])|7[2-57]|8[02-9])\\d{5}', , , , '1123456'],
                  [, , '(?:(?:3|81)\\d|7(?:[01]\\d|6[013-9]|8[7-9]|9[1-3]))\\d{5}', , , , '71123456'],
                  [, , , , , , , , , [-1]],
                  [, , '9[01]\\d{6}', , , , '90123456', , , [8]],
                  [, , '80\\d{6}', , , , '80123456', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'LB',
                  961,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{3})', '$1 $2 $3', ['[13-69]|7(?:[2-57]|62|8[0-6]|9[04-9])|8[02-9]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['[27-9]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LC: [
                  ,
                  [, , '(?:[58]\\d\\d|758|900)\\d{7}', , , , , , , [10], [7]],
                  [, , '758(?:234|4(?:30|5\\d|6[2-9]|8[0-2])|57[0-2]|(?:63|75)8)\\d{4}', , , , '7584305678', , , , [7]],
                  [, , '758(?:28[4-7]|384|4(?:6[01]|8[4-9])|5(?:1[89]|20|84)|7(?:1[2-9]|2\\d|3[0-3])|812)\\d{4}', , , , '7582845678', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'LC',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-8]\\d{6})$|1',
                  '758$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '758',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LI: [
                  ,
                  [, , '[68]\\d{8}|(?:[2378]\\d|90)\\d{5}', , , , , , , [7, 9]],
                  [, , '(?:2(?:01|1[27]|2[024]|3\\d|6[02-578]|96)|3(?:[24]0|33|7[0135-7]|8[048]|9[0269]))\\d{4}', , , , '2345678', , , [7]],
                  [, , '(?:6(?:(?:4[5-9]|5\\d)\\d|6(?:[024-6]\\d|[17]0|3[7-9]))\\d|7(?:[37-9]\\d|42|56))\\d{4}', , , , '660234567'],
                  [, , '8002[28]\\d\\d|80(?:05\\d|9)\\d{4}', , , , '8002222'],
                  [, , '90(?:02[258]|1(?:23|3[14])|66[136])\\d\\d', , , , '9002222', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'LI',
                  423,
                  '00',
                  '0',
                  ,
                  ,
                  '(1001)|0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3', ['[2379]|8(?:0[09]|7)', '[2379]|8(?:0(?:02|9)|7)'], , '$CC $1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['8']],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['69'], , '$CC $1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['6'], , '$CC $1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '870(?:28|87)\\d\\d', , , , '8702812', , , [7]],
                  ,
                  ,
                  [, , '697(?:42|56|[78]\\d)\\d{4}', , , , '697861234', , , [9]]
                ],
                LK: [
                  ,
                  [, , '[1-9]\\d{8}', , , , , , , [9], [7]],
                  [
                    ,
                    ,
                    '(?:12[2-9]|602|8[12]\\d|9(?:1\\d|22|9[245]))\\d{6}|(?:11|2[13-7]|3[1-8]|4[157]|5[12457]|6[35-7])[2-57]\\d{6}',
                    ,
                    ,
                    ,
                    '112345678',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '7(?:[0-25-8]\\d|4[0-4])\\d{6}', , , , '712345678'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'LK',
                  94,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['7'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[1-689]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '1973\\d{5}', , , , '197312345'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LR: [
                  ,
                  [, , '(?:[2457]\\d|33|88)\\d{7}|(?:2\\d|[4-6])\\d{6}', , , , , , , [7, 8, 9]],
                  [, , '2\\d{7}', , , , '21234567', , , [8]],
                  [, , '(?:(?:(?:22|33)0|555|7(?:6[01]|7\\d)|88\\d)\\d|4(?:240|[67]))\\d{5}|[56]\\d{6}', , , , '770123456', , , [7, 9]],
                  [, , , , , , , , , [-1]],
                  [, , '332(?:02|[34]\\d)\\d{4}', , , , '332021234', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'LR',
                  231,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{3})', '$1 $2 $3', ['4[67]|[56]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['2'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[2-578]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LS: [
                  ,
                  [, , '(?:[256]\\d\\d|800)\\d{5}', , , , , , , [8]],
                  [, , '2\\d{7}', , , , '22123456'],
                  [, , '[56]\\d{7}', , , , '50123456'],
                  [, , '800[1256]\\d{4}', , , , '80021234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'LS',
                  266,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{4})(\\d{4})', '$1 $2', ['[2568]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LT: [
                  ,
                  [, , '(?:[3469]\\d|52|[78]0)\\d{6}', , , , , , , [8]],
                  [, , '(?:3[1478]|4[124-6]|52)\\d{6}', , , , '31234567'],
                  [, , '6\\d{7}', , , , '61234567'],
                  [, , '80[02]\\d{5}', , , , '80012345'],
                  [, , '9(?:0[0239]|10)\\d{5}', , , , '90012345'],
                  [, , '808\\d{5}', , , , '80812345'],
                  [, , '70[05]\\d{5}', , , , '70012345'],
                  [, , '[89]01\\d{5}', , , , '80123456'],
                  'LT',
                  370,
                  '00',
                  '0',
                  ,
                  ,
                  '[08]',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{4})', '$1 $2 $3', ['52[0-7]'], '(0-$1)', , 1],
                    [, '(\\d{3})(\\d{2})(\\d{3})', '$1 $2 $3', ['[7-9]'], '0 $1', , 1],
                    [, '(\\d{2})(\\d{6})', '$1 $2', ['37|4(?:[15]|6[1-8])'], '(0-$1)', , 1],
                    [, '(\\d{3})(\\d{5})', '$1 $2', ['[3-6]'], '(0-$1)', , 1]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '70[67]\\d{5}', , , , '70712345'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LU: [
                  ,
                  [, , '35[013-9]\\d{4,8}|6\\d{8}|35\\d{2,4}|(?:[2457-9]\\d|3[0-46-9])\\d{2,9}', , , , , , , [4, 5, 6, 7, 8, 9, 10, 11]],
                  [, , '(?:35[013-9]|80[2-9]|90[89])\\d{1,8}|(?:2[2-9]|3[0-46-9]|[457]\\d|8[13-9]|9[2-579])\\d{2,9}', , , , '27123456'],
                  [, , '6(?:[269][18]|5[1568]|7[189]|81)\\d{6}', , , , '628123456', , , [9]],
                  [, , '800\\d{5}', , , , '80012345', , , [8]],
                  [, , '90[015]\\d{5}', , , , '90012345', , , [8]],
                  [, , '801\\d{5}', , , , '80112345', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , '20(?:1\\d{5}|[2-689]\\d{1,7})', , , , '20201234', , , [4, 5, 6, 7, 8, 9, 10]],
                  'LU',
                  352,
                  '00',
                  ,
                  ,
                  ,
                  '(15(?:0[06]|1[12]|[35]5|4[04]|6[26]|77|88|99)\\d)',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3})', '$1 $2', ['2(?:0[2-689]|[2-9])|[3-57]|8(?:0[2-9]|[13-9])|9(?:0[89]|[2-579])'], , '$CC $1'],
                    [, '(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3', ['2(?:0[2-689]|[2-9])|[3-57]|8(?:0[2-9]|[13-9])|9(?:0[89]|[2-579])'], , '$CC $1'],
                    [, '(\\d{2})(\\d{2})(\\d{3})', '$1 $2 $3', ['20[2-689]'], , '$CC $1'],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{1,2})', '$1 $2 $3 $4', ['2(?:[0367]|4[3-8])'], , '$CC $1'],
                    [, '(\\d{3})(\\d{2})(\\d{3})', '$1 $2 $3', ['80[01]|90[015]'], , '$CC $1'],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{3})', '$1 $2 $3 $4', ['20'], , '$CC $1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['6'], , '$CC $1'],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})(\\d{1,2})', '$1 $2 $3 $4 $5', ['2(?:[0367]|4[3-8])'], , '$CC $1'],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{1,5})', '$1 $2 $3 $4', ['[3-57]|8[13-9]|9(?:0[89]|[2-579])|(?:2|80)[2-9]'], , '$CC $1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LV: [
                  ,
                  [, , '(?:[268]\\d|78|90)\\d{6}', , , , , , , [8]],
                  [, , '6\\d{7}', , , , '63123456'],
                  [, , '2333[0-8]\\d{3}|2(?:[0-24-9]\\d\\d|3(?:0[07]|[14-9]\\d|2[02-9]|3[0-24-9]))\\d{4}', , , , '21234567'],
                  [, , '80\\d{6}', , , , '80123456'],
                  [, , '90\\d{6}', , , , '90123456'],
                  [, , '81\\d{6}', , , , '81123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'LV',
                  371,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['[2679]|8[01]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LY: [
                  ,
                  [, , '[2-9]\\d{8}', , , , , , , [9], [7]],
                  [
                    ,
                    ,
                    '(?:2(?:0[56]|[1-6]\\d|7[124579]|8[124])|3(?:1\\d|2[2356])|4(?:[17]\\d|2[1-357]|5[2-4]|8[124])|5(?:[1347]\\d|2[1-469]|5[13-5]|8[1-4])|6(?:[1-479]\\d|5[2-57]|8[1-5])|7(?:[13]\\d|2[13-79])|8(?:[124]\\d|5[124]|84))\\d{6}',
                    ,
                    ,
                    ,
                    '212345678',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '9[1-6]\\d{7}', , , , '912345678'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'LY',
                  218,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{7})', '$1-$2', ['[2-9]'], '0$1']],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MA: [
                  ,
                  [, , '[5-8]\\d{8}', , , , , , , [9]],
                  [
                    ,
                    ,
                    '5(?:2(?:[0-25-79]\\d|3[1-578]|4[02-46-8]|8[0235-7])|3(?:[0-47]\\d|5[02-9]|6[02-8]|8[014-9]|9[3-9])|(?:4[067]|5[03])\\d)\\d{5}',
                    ,
                    ,
                    ,
                    '520123456'
                  ],
                  [, , '(?:6(?:[0-79]\\d|8[0-247-9])|7(?:[0167]\\d|2[0-8]|5[0-5]|8[0-7]))\\d{6}', , , , '650123456'],
                  [, , '80[0-7]\\d{6}', , , , '801234567'],
                  [, , '89\\d{7}', , , , '891234567'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '(?:592(?:4[0-2]|93)|80[89]\\d\\d)\\d{4}', , , , '592401234'],
                  'MA',
                  212,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['5[45]'], '0$1'],
                    [, '(\\d{4})(\\d{5})', '$1-$2', ['5(?:2[2-46-9]|3[3-9]|9)|8(?:0[89]|92)'], '0$1'],
                    [, '(\\d{2})(\\d{7})', '$1-$2', ['8'], '0$1'],
                    [, '(\\d{3})(\\d{6})', '$1-$2', ['[5-7]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  1,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MC: [
                  ,
                  [, , '(?:[3489]|6\\d)\\d{7}', , , , , , , [8, 9]],
                  [, , '(?:870|9[2-47-9]\\d)\\d{5}', , , , '99123456', , , [8]],
                  [, , '4(?:[469]\\d|5[1-9])\\d{5}|(?:3|6\\d)\\d{7}', , , , '612345678'],
                  [, , '(?:800|90\\d)\\d{5}', , , , '90123456', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'MC',
                  377,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3})(\\d{2})', '$1 $2 $3', ['87']],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['4'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[389]']],
                    [, '(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4 $5', ['6'], '0$1']
                  ],
                  [
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['4'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[389]']],
                    [, '(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4 $5', ['6'], '0$1']
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '8[07]0\\d{5}', , , , , , , [8]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MD: [
                  ,
                  [, , '(?:[235-7]\\d|[89]0)\\d{6}', , , , , , , [8]],
                  [, , '(?:(?:2[1-9]|3[1-79])\\d|5(?:33|5[257]))\\d{5}', , , , '22212345'],
                  [, , '562\\d{5}|(?:6\\d|7[16-9])\\d{6}', , , , '62112345'],
                  [, , '800\\d{5}', , , , '80012345'],
                  [, , '90[056]\\d{5}', , , , '90012345'],
                  [, , '808\\d{5}', , , , '80812345'],
                  [, , , , , , , , , [-1]],
                  [, , '3[08]\\d{6}', , , , '30123456'],
                  'MD',
                  373,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{5})', '$1 $2', ['[89]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['22|3'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{3})', '$1 $2 $3', ['[25-7]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '803\\d{5}', , , , '80312345'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ME: [
                  ,
                  [, , '(?:20|[3-79]\\d)\\d{6}|80\\d{6,7}', , , , , , , [8, 9], [6]],
                  [, , '(?:20[2-8]|3(?:[0-2][2-7]|3[24-7])|4(?:0[2-467]|1[2467])|5(?:0[2467]|1[24-7]|2[2-467]))\\d{5}', , , , '30234567', , , [8], [6]],
                  [, , '6(?:[07-9]\\d|3[024]|6[0-25])\\d{5}', , , , '60123456', , , [8]],
                  [, , '80(?:[0-2578]|9\\d)\\d{5}', , , , '80080002'],
                  [, , '9(?:4[1568]|5[178])\\d{5}', , , , '94515151', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '78[1-49]\\d{5}', , , , '78108780', , , [8]],
                  'ME',
                  382,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[2-9]'], '0$1']],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '77[1-9]\\d{5}', , , , '77273012', , , [8]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MF: [
                  ,
                  [, , '(?:590\\d|7090)\\d{5}|(?:69|80|9\\d)\\d{7}', , , , , , , [9]],
                  [, , '590(?:0[079]|[14]3|[27][79]|3[03-7]|5[0-268]|87)\\d{4}', , , , '590271234'],
                  [, , '(?:69(?:0\\d\\d|1(?:2[2-9]|3[0-5])|4(?:0[89]|1[2-6]|9\\d)|6(?:1[016-9]|5[0-4]|[67]\\d))|7090[0-4])\\d{4}', , , , '690001234'],
                  [, , '80[0-5]\\d{6}', , , , '800012345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '9(?:(?:39[5-7]|76[018])\\d|475[0-6])\\d{4}', , , , '976012345'],
                  'MF',
                  590,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MG: [
                  ,
                  [, , '[23]\\d{8}', , , , , , , [9], [7]],
                  [, , '2072[29]\\d{4}|20(?:2\\d|4[47]|5[3467]|6[279]|7[356]|8[268]|9[2457])\\d{5}', , , , '202123456', , , , [7]],
                  [, , '3[2-47-9]\\d{7}', , , , '321234567'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '22\\d{7}', , , , '221234567'],
                  'MG',
                  261,
                  '00',
                  '0',
                  ,
                  ,
                  '([24-9]\\d{6})$|0',
                  '20$1',
                  ,
                  ,
                  [[, '(\\d{2})(\\d{2})(\\d{3})(\\d{2})', '$1 $2 $3 $4', ['[23]'], '0$1']],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MH: [
                  ,
                  [, , '329\\d{4}|(?:[256]\\d|45)\\d{5}', , , , , , , [7]],
                  [, , '(?:247|528|625)\\d{4}', , , , '2471234'],
                  [, , '(?:(?:23|54)5|329|45[35-8])\\d{4}', , , , '2351234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '635\\d{4}', , , , '6351234'],
                  'MH',
                  692,
                  '011',
                  '1',
                  ,
                  ,
                  '1',
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{4})', '$1-$2', ['[2-6]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MK: [
                  ,
                  [, , '[2-578]\\d{7}', , , , , , , [8], [6, 7]],
                  [
                    ,
                    ,
                    '(?:(?:2(?:62|77)0|3444)\\d|4[56]440)\\d{3}|(?:34|4[357])700\\d{3}|(?:2(?:[0-3]\\d|5[0-578]|6[01]|82)|3(?:1[3-68]|[23][2-68]|4[23568])|4(?:[23][2-68]|4[3-68]|5[2568]|6[25-8]|7[24-68]|8[4-68]))\\d{5}',
                    ,
                    ,
                    ,
                    '22012345',
                    ,
                    ,
                    ,
                    [6, 7]
                  ],
                  [
                    ,
                    ,
                    '7(?:3555|(?:474|9[019]7)7)\\d{3}|7(?:[0-25-8]\\d\\d|3(?:[1-478]\\d|6[01])|4(?:2\\d|60|7[01578])|9(?:[2-4]\\d|5[01]|7[015]))\\d{4}',
                    ,
                    ,
                    ,
                    '72345678'
                  ],
                  [, , '800\\d{5}', , , , '80012345'],
                  [, , '5\\d{7}', , , , '50012345'],
                  [, , '8(?:0[1-9]|[1-9]\\d)\\d{5}', , , , '80123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'MK',
                  389,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{4})', '$1 $2 $3', ['2|34[47]|4(?:[37]7|5[47]|64)'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['[347]'], '0$1'],
                    [, '(\\d{3})(\\d)(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[58]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ML: [
                  ,
                  [, , '[24-9]\\d{7}', , , , , , , [8]],
                  [, , '2(?:07[0-8]|12[67])\\d{4}|(?:2(?:02|1[4-689])|4(?:0[0-4]|4[1-59]))\\d{5}', , , , '20212345'],
                  [, , '2(?:0(?:01|79)|17\\d)\\d{4}|(?:5[0-3]|[679]\\d|8[2-59])\\d{6}', , , , '65012345'],
                  [, , '80\\d{6}', , , , '80012345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'ML',
                  223,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})', '$1', ['67[057-9]|74[045]', '67(?:0[09]|[59]9|77|8[89])|74(?:0[02]|44|55)']],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[24-9]']]
                  ],
                  [[, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[24-9]']]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '80\\d{6}'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MM: [
                  ,
                  [, , '1\\d{5,7}|95\\d{6}|(?:[4-7]|9[0-46-9])\\d{6,8}|(?:2|8\\d)\\d{5,8}', , , , , , , [6, 7, 8, 9, 10], [5]],
                  [
                    ,
                    ,
                    '(?:1(?:(?:12|[28]\\d|3[56]|7[3-6]|9[0-6])\\d|4(?:2[29]|7[0-2]|83)|6)|2(?:2(?:00|8[34])|4(?:0\\d|22|7[0-2]|83)|51\\d\\d)|4(?:2(?:2\\d\\d|48[013])|3(?:20\\d|4(?:70|83)|56)|420\\d|5(?:2\\d|470))|6(?:0(?:[23]|88\\d)|(?:124|[56]2\\d)\\d|2472|3(?:20\\d|470)|4(?:2[04]\\d|472)|7(?:3\\d\\d|4[67]0|8(?:[01459]\\d|8))))\\d{4}|5(?:2(?:2\\d{5,6}|47[02]\\d{4})|(?:3472|4(?:2(?:1|86)|470)|522\\d|6(?:20\\d|483)|7(?:20\\d|48[01])|8(?:20\\d|47[02])|9(?:20\\d|470))\\d{4})|7(?:(?:0470|4(?:25\\d|470)|5(?:202|470|96\\d))\\d{4}|1(?:20\\d{4,5}|4(?:70|83)\\d{4}))|8(?:1(?:2\\d{5,6}|4(?:10|7[01]\\d)\\d{3})|2(?:2\\d{5,6}|(?:320|490\\d)\\d{3})|(?:3(?:2\\d\\d|470)|4[24-7]|5(?:(?:2\\d|51)\\d|4(?:[1-35-9]\\d|4[0-57-9]))|6[23])\\d{4})|(?:1[2-6]\\d|4(?:2[24-8]|3[2-7]|[46][2-6]|5[3-5])|5(?:[27][2-8]|3[2-68]|4[24-8]|5[23]|6[2-4]|8[24-7]|9[2-7])|6(?:[19]20|42[03-6]|(?:52|7[45])\\d)|7(?:[04][24-8]|[15][2-7]|22|3[2-4])|8(?:1[2-689]|2[2-8]|(?:[35]2|64)\\d))\\d{4}|25\\d{5,6}|(?:2[2-9]|6(?:1[2356]|[24][2-6]|3[24-6]|5[2-4]|6[2-8]|7[235-7]|8[245]|9[24])|8(?:3[24]|5[245]))\\d{4}',
                    ,
                    ,
                    ,
                    '1234567',
                    ,
                    ,
                    [6, 7, 8, 9],
                    [5]
                  ],
                  [
                    ,
                    ,
                    '(?:17[01]|9(?:2(?:[0-4]|[56]\\d\\d)|(?:3(?:[0-36]|4\\d)|(?:6\\d|8[89]|9[4-8])\\d|7(?:3|40|[5-9]\\d))\\d|4(?:(?:[0245]\\d|[1379])\\d|88)|5[0-6])\\d)\\d{4}|9[69]1\\d{6}|9(?:[68]\\d|9[089])\\d{5}',
                    ,
                    ,
                    ,
                    '92123456',
                    ,
                    ,
                    [7, 8, 9, 10]
                  ],
                  [, , '80080(?:0[1-9]|2\\d)\\d{3}', , , , '8008001234', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '1333\\d{4}', , , , '13331234', , , [8]],
                  'MM',
                  95,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{2})(\\d{3})', '$1 $2 $3', ['16|2'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{3})', '$1 $2 $3', ['4(?:[2-46]|5[3-5])|5|6(?:[1-689]|7[235-7])|7(?:[0-4]|5[2-7])|8[1-5]|(?:60|86)[23]'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[12]|452|678|86', '[12]|452|6788|86'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[4-7]|8[1-35]'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{4,6})', '$1 $2 $3', ['9(?:2[0-4]|[35-9]|4[137-9])'], '0$1'],
                    [, '(\\d)(\\d{4})(\\d{4})', '$1 $2 $3', ['2'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['8'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['92'], '0$1'],
                    [, '(\\d)(\\d{5})(\\d{4})', '$1 $2 $3', ['9'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MN: [
                  ,
                  [, , '[12]\\d{7,9}|[5-9]\\d{7}', , , , , , , [8, 9, 10], [4, 5, 6]],
                  [
                    ,
                    ,
                    '[12]2[1-3]\\d{5,6}|(?:(?:[12](?:1|27)|5[368])\\d\\d|7(?:0(?:[0-5]\\d|7[078]|80)|128))\\d{4}|[12](?:3[2-8]|4[2-68]|5[1-4689])\\d{6,7}',
                    ,
                    ,
                    ,
                    '53123456',
                    ,
                    ,
                    ,
                    [4, 5, 6]
                  ],
                  [, , '92[0139]\\d{5}|(?:5[05]|6[069]|7[28]|8[0135689]|9[013-9])\\d{6}', , , , '88123456', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '712[0-79]\\d{4}|7(?:1[013-9]|[5-79]\\d)\\d{5}', , , , '75123456', , , [8]],
                  'MN',
                  976,
                  '001',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d{4})', '$1 $2 $3', ['[12]1'], '0$1'],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[5-9]']],
                    [, '(\\d{3})(\\d{5,6})', '$1 $2', ['[12]2[1-3]'], '0$1'],
                    [, '(\\d{4})(\\d{5,6})', '$1 $2', ['[12](?:27|3[2-8]|4[2-68]|5[1-4689])', '[12](?:27|3[2-8]|4[2-68]|5[1-4689])[0-3]'], '0$1'],
                    [, '(\\d{5})(\\d{4,5})', '$1 $2', ['[12]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MO: [
                  ,
                  [, , '0800\\d{3}|(?:28|[68]\\d)\\d{6}', , , , , , , [7, 8]],
                  [, , '(?:28[2-9]|8(?:11|[2-57-9]\\d))\\d{5}', , , , '28212345', , , [8]],
                  [, , '6800[0-79]\\d{3}|6(?:[235]\\d\\d|6(?:0[0-5]|[1-9]\\d)|8(?:0[1-9]|[14-8]\\d|2[5-9]|[39][0-4]))\\d{4}', , , , '66123456', , , [8]],
                  [, , '0800\\d{3}', , , , '0800501', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'MO',
                  853,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})(\\d{3})', '$1 $2', ['0']],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[268]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MP: [
                  ,
                  [, , '[58]\\d{9}|(?:67|90)0\\d{7}', , , , , , , [10], [7]],
                  [
                    ,
                    ,
                    '670(?:2(?:3[3-7]|56|8[4-8])|32[1-38]|4(?:33|8[348])|5(?:32|55|88)|6(?:64|70|82)|78[3589]|8[3-9]8|989)\\d{4}',
                    ,
                    ,
                    ,
                    '6702345678',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [
                    ,
                    ,
                    '670(?:2(?:3[3-7]|56|8[4-8])|32[1-38]|4(?:33|8[348])|5(?:32|55|88)|6(?:64|70|82)|78[3589]|8[3-9]8|989)\\d{4}',
                    ,
                    ,
                    ,
                    '6702345678',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'MP',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-9]\\d{6})$|1',
                  '670$1',
                  ,
                  1,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '670',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MQ: [
                  ,
                  [, , '(?:596\\d|7091)\\d{5}|(?:69|[89]\\d)\\d{7}', , , , , , , [9]],
                  [, , '(?:596(?:[03-7]\\d|1[05]|2[7-9]|8[0-39]|9[04-9])|80[6-9]\\d\\d|9(?:477[6-9]|767[4589]))\\d{4}', , , , '596301234'],
                  [, , '(?:69[67]\\d\\d|7091[0-3])\\d{4}', , , , '696201234'],
                  [, , '80[0-5]\\d{6}', , , , '800012345'],
                  [, , '8[129]\\d{7}', , , , '810123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '9(?:397[0-3]|477[0-5]|76(?:6\\d|7[0-367]))\\d{4}', , , , '976612345'],
                  'MQ',
                  596,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[5-79]|8(?:0[6-9]|[36])'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['8'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MR: [
                  ,
                  [, , '(?:[2-4]\\d\\d|800)\\d{5}', , , , , , , [8]],
                  [, , '(?:25[08]|35\\d|45[1-7])\\d{5}', , , , '35123456'],
                  [, , '[2-4][0-46-9]\\d{6}', , , , '22123456'],
                  [, , '800\\d{5}', , , , '80012345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'MR',
                  222,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[2-48]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MS: [
                  ,
                  [, , '(?:[58]\\d\\d|664|900)\\d{7}', , , , , , , [10], [7]],
                  [, , '6644(?:1[0-3]|91)\\d{4}', , , , '6644912345', , , , [7]],
                  [, , '664(?:3(?:49|9[1-6])|49[2-6])\\d{4}', , , , '6644923456', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'MS',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([34]\\d{6})$|1',
                  '664$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '664',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MT: [
                  ,
                  [, , '3550\\d{4}|(?:[2579]\\d\\d|800)\\d{5}', , , , , , , [8]],
                  [, , '20(?:3[1-4]|6[059])\\d{4}|2(?:0[19]|[1-357]\\d|60)\\d{5}', , , , '21001234'],
                  [, , '(?:7(?:210|[79]\\d\\d)|9(?:[29]\\d\\d|69[67]|8(?:1[1-3]|89|97)))\\d{4}', , , , '96961234'],
                  [, , '800(?:02|[3467]\\d)\\d{3}', , , , '80071234'],
                  [, , '5(?:0(?:0(?:37|43)|(?:6\\d|70|9[0168])\\d)|[12]\\d0[1-5])\\d{3}', , , , '50037123'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '3550\\d{4}', , , , '35501234'],
                  'MT',
                  356,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{4})(\\d{4})', '$1 $2', ['[2357-9]']]],
                  ,
                  [, , '7117\\d{4}', , , , '71171234'],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '501\\d{5}', , , , '50112345'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MU: [
                  ,
                  [, , '(?:[57]|8\\d\\d)\\d{7}|[2-468]\\d{6}', , , , , , , [7, 8, 10]],
                  [
                    ,
                    ,
                    '(?:2(?:[0346-8]\\d|1[0-8])|4(?:[013568]\\d|2[4-8]|71|90)|54(?:[3-5]\\d|71)|6\\d\\d|8(?:14|3[129]))\\d{4}',
                    ,
                    ,
                    ,
                    '54480123',
                    ,
                    ,
                    [7, 8]
                  ],
                  [
                    ,
                    ,
                    '5(?:4(?:2[1-389]|7[1-9])|87[15-8])\\d{4}|(?:5(?:2[5-9]|4[3-689]|[57]\\d|8[0-689]|9[0-8])|7(?:0[0-6]|3[013]))\\d{5}',
                    ,
                    ,
                    ,
                    '52512345',
                    ,
                    ,
                    [8]
                  ],
                  [, , '802\\d{7}|80[0-2]\\d{4}', , , , '8001234', , , [7, 10]],
                  [, , '30\\d{5}', , , , '3012345', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '3(?:20|9\\d)\\d{4}', , , , '3201234', , , [7]],
                  'MU',
                  230,
                  '0(?:0|[24-7]0|3[03])',
                  ,
                  ,
                  ,
                  ,
                  ,
                  '020',
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['[2-46]|8[013]']],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[57]']],
                    [, '(\\d{5})(\\d{5})', '$1 $2', ['8']]
                  ],
                  ,
                  [, , '219\\d{4}', , , , '2190123', , , [7]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MV: [
                  ,
                  [, , '(?:800|9[0-57-9]\\d)\\d{7}|[34679]\\d{6}', , , , , , , [7, 10]],
                  [, , '(?:3(?:0[0-4]|3[0-59])|6(?:[58][024689]|6[024-68]|7[02468]))\\d{4}', , , , '6701234', , , [7]],
                  [, , '(?:46[46]|[79]\\d\\d)\\d{4}', , , , '7712345', , , [7]],
                  [, , '800\\d{7}', , , , '8001234567', , , [10]],
                  [, , '900\\d{7}', , , , '9001234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'MV',
                  960,
                  '0(?:0|19)',
                  ,
                  ,
                  ,
                  ,
                  ,
                  '00',
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1-$2', ['[34679]']],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['[89]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '4(?:0[01]|50)\\d{4}', , , , '4001234', , , [7]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MW: [
                  ,
                  [, , '(?:[1289]\\d|31|77)\\d{7}|1\\d{6}', , , , , , , [7, 9]],
                  [, , '(?:1[2-9]|2[12]\\d\\d)\\d{5}', , , , '1234567'],
                  [, , '111\\d{6}|(?:31|77|[89][89])\\d{7}', , , , '991234567', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'MW',
                  265,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{3})', '$1 $2 $3', ['1[2-9]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['2'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[137-9]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MX: [
                  ,
                  [, , '[2-9]\\d{9}', , , , , , , [10], [7, 8]],
                  [
                    ,
                    ,
                    '(?:2(?:0[01]|2\\d|3[1-35-8]|4[13-9]|7[1-689]|8[1-578]|9[467])|3(?:1[1-79]|[2458][1-9]|3\\d|7[1-8]|9[1-5])|4(?:1[1-57-9]|[267][1-9]|3[1-8]|[45]\\d|8[1-35-9]|9[2-689])|5(?:[56]\\d|88|9[1-79])|6(?:1[2-68]|[2-4][1-9]|5[1-36-9]|6[0-57-9]|7[1-7]|8[67]|9[4-8])|7(?:[1346][1-9]|[27]\\d|5[13-9]|8[1-69]|9[17])|8(?:1\\d|2[13-689]|3[1-6]|4[124-6]|6[1246-9]|7[0-378]|9[12479])|9(?:1[346-9]|2[1-4]|3[2-46-8]|5[1348]|[69]\\d|7[12]|8[1-8]))\\d{7}',
                    ,
                    ,
                    ,
                    '2001234567',
                    ,
                    ,
                    ,
                    [7, 8]
                  ],
                  [
                    ,
                    ,
                    '(?:2(?:2\\d|3[1-35-8]|4[13-9]|7[1-689]|8[1-578]|9[467])|3(?:1[1-79]|[2458][1-9]|3\\d|7[1-8]|9[1-5])|4(?:1[1-57-9]|[267][1-9]|3[1-8]|[45]\\d|8[1-35-9]|9[2-689])|5(?:[56]\\d|88|9[1-79])|6(?:1[2-68]|[2-4][1-9]|5[1-36-9]|6[0-57-9]|7[1-7]|8[67]|9[4-8])|7(?:[1346][1-9]|[27]\\d|5[13-9]|8[1-69]|9[17])|8(?:1\\d|2[13-689]|3[1-6]|4[124-6]|6[1246-9]|7[0-378]|9[12479])|9(?:1[346-9]|2[1-4]|3[2-46-8]|5[1348]|[69]\\d|7[12]|8[1-8]))\\d{7}',
                    ,
                    ,
                    ,
                    '2221234567',
                    ,
                    ,
                    ,
                    [7, 8]
                  ],
                  [, , '8(?:00|88)\\d{7}', , , , '8001234567'],
                  [, , '900\\d{7}', , , , '9001234567'],
                  [, , '300\\d{7}', , , , '3001234567'],
                  [, , '500\\d{7}', , , , '5001234567'],
                  [, , , , , , , , , [-1]],
                  'MX',
                  52,
                  '0[09]',
                  ,
                  ,
                  ,
                  ,
                  ,
                  '00',
                  ,
                  [
                    [, '(\\d{5})', '$1', ['53']],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1 $2 $3', ['33|5[56]|81']],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['[2-9]']]
                  ],
                  [
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1 $2 $3', ['33|5[56]|81']],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['[2-9]']]
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MY: [
                  ,
                  [, , '1\\d{8,9}|(?:3\\d|[4-9])\\d{7}', , , , , , , [8, 9, 10], [6, 7]],
                  [
                    ,
                    ,
                    '427[01]\\d{4}|(?:3(?:2[0-36-9]|3[0-368]|4[0-278]|5[0-24-8]|6[0-467]|7[1246-9]|8\\d|9[0-57])\\d|4(?:2[0-689]|[3-79]\\d|8[1-35689])|5(?:2[0-589]|[3468]\\d|5[0-489]|7[1-9]|9[23])|6(?:2[2-9]|3[1357-9]|[46]\\d|5[0-6]|7[0-35-9]|85|9[015-8])|7(?:[2579]\\d|3[03-68]|4[0-8]|6[5-9]|8[0-35-9])|8(?:[24][2-8]|3[2-5]|5[2-7]|6[2-589]|7[2-578]|[89][2-9])|9(?:0[57]|13|[25-7]\\d|[3489][0-8]))\\d{5}',
                    ,
                    ,
                    ,
                    '323856789',
                    ,
                    ,
                    [8, 9],
                    [6, 7]
                  ],
                  [
                    ,
                    ,
                    '1(?:1888[689]|4400|8(?:47|8[27])[0-4])\\d{4}|1(?:0(?:[23568]\\d|4[0-6]|7[016-9]|9[0-8])|1(?:[1-5]\\d\\d|6(?:0[5-9]|[1-9]\\d)|7(?:[0-4]\\d|5[0-7]))|(?:[269]\\d|[37][1-9]|4[235-9])\\d|5(?:31|9\\d\\d)|8(?:1[23]|[236]\\d|4[06]|5(?:46|[7-9])|7[016-9]|8[01]|9[0-8]))\\d{5}',
                    ,
                    ,
                    ,
                    '123456789',
                    ,
                    ,
                    [9, 10]
                  ],
                  [, , '1[378]00\\d{6}', , , , '1300123456', , , [10]],
                  [, , '1600\\d{6}', , , , '1600123456', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '15(?:4(?:6[0-4]\\d|8(?:0[125]|[17]\\d|21|3[01]|4[01589]|5[014]|6[02]))|6(?:32[0-6]|78\\d))\\d{4}', , , , '1546012345', , , [10]],
                  'MY',
                  60,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{4})', '$1-$2 $3', ['[4-79]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1-$2 $3', ['1(?:[02469]|[378][1-9]|53)|8', '1(?:[02469]|[37][1-9]|53|8(?:[1-46-9]|5[7-9]))|8'], '0$1'],
                    [, '(\\d)(\\d{4})(\\d{4})', '$1-$2 $3', ['3'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{2})(\\d{4})', '$1-$2-$3-$4', ['1(?:[367]|80)']],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1-$2 $3', ['15'], '0$1'],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1-$2 $3', ['1'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MZ: [
                  ,
                  [, , '(?:2|8\\d)\\d{7}', , , , , , , [8, 9]],
                  [, , '2(?:[1346]\\d|5[0-2]|[78][12]|93)\\d{5}', , , , '21123456', , , [8]],
                  [, , '8[2-79]\\d{7}', , , , '821234567', , , [9]],
                  [, , '800\\d{6}', , , , '800123456', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'MZ',
                  258,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['2|8[2-79]']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['8']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NA: [
                  ,
                  [, , '[68]\\d{7,8}', , , , , , , [8, 9]],
                  [
                    ,
                    ,
                    '64426\\d{3}|6(?:1(?:2[2-7]|3[01378]|4[0-4])|254|32[0237]|4(?:27|41|5[25])|52[236-8]|626|7(?:2[2-4]|30))\\d{4,5}|6(?:1(?:(?:0\\d|2[0189]|3[24-69]|4[5-9])\\d|17|69|7[014])|2(?:17|5[0-36-8]|69|70)|3(?:17|2[14-689]|34|6[289]|7[01]|81)|4(?:17|2[0-2]|4[06]|5[0137]|69|7[01])|5(?:17|2[0459]|69|7[01])|6(?:17|25|38|42|69|7[01])|7(?:17|2[569]|3[13]|6[89]|7[01]))\\d{4}',
                    ,
                    ,
                    ,
                    '61221234'
                  ],
                  [, , '(?:60|8[1245])\\d{7}', , , , '811234567', , , [9]],
                  [, , '80\\d{7}', , , , '800123456', , , [9]],
                  [, , '8701\\d{5}', , , , '870123456', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '8(?:3\\d\\d|86)\\d{5}', , , , '88612345'],
                  'NA',
                  264,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['88'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['6'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['87'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['8'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NC: [
                  ,
                  [, , '(?:050|[2-57-9]\\d\\d)\\d{3}', , , , , , , [6]],
                  [, , '(?:2[03-9]|3[0-5]|4[1-7]|88)\\d{4}', , , , '201234'],
                  [, , '(?:[579]\\d|8[0-79])\\d{4}', , , , '751234'],
                  [, , '050\\d{3}', , , , '050012'],
                  [, , '36\\d{4}', , , , '366711'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'NC',
                  687,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})', '$1', ['5[6-8]']],
                    [, '(\\d{2})(\\d{2})(\\d{2})', '$1.$2.$3', ['[02-57-9]']]
                  ],
                  [[, '(\\d{2})(\\d{2})(\\d{2})', '$1.$2.$3', ['[02-57-9]']]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NE: [
                  ,
                  [, , '[027-9]\\d{7}', , , , , , , [8]],
                  [, , '2(?:0(?:20|3[1-8]|4[13-5]|5[14]|6[14578]|7[1-578])|1(?:4[145]|5[14]|6[14-68]|7[169]|88))\\d{4}', , , , '20201234'],
                  [, , '(?:23|7[0467]|[89]\\d)\\d{6}', , , , '93123456'],
                  [, , '08\\d{6}', , , , '08123456'],
                  [, , '09\\d{6}', , , , '09123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'NE',
                  227,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['08']],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[089]|2[013]|7[0467]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NF: [
                  ,
                  [, , '[13]\\d{5}', , , , , , , [6], [5]],
                  [, , '(?:1(?:06|17|28|39)|3[0-2]\\d)\\d{3}', , , , '106609', , , , [5]],
                  [, , '(?:14|3[58])\\d{4}', , , , '381234', , , , [5]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'NF',
                  672,
                  '00',
                  ,
                  ,
                  ,
                  '([0-258]\\d{4})$',
                  '3$1',
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{4})', '$1 $2', ['1[0-3]']],
                    [, '(\\d)(\\d{5})', '$1 $2', ['[13]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NG: [
                  ,
                  [, , '(?:20|9\\d)\\d{8}|[78]\\d{9,13}', , , , , , , [10, 11, 12, 13, 14], [6, 7]],
                  [, , '20(?:[1259]\\d|3[013-9]|4[1-8]|6[024-689]|7[1-79]|8[2-9])\\d{6}', , , , '2033123456', , , [10], [6, 7]],
                  [, , '(?:702[0-24-9]|819[01])\\d{6}|(?:7(?:0[13-9]|[12]\\d)|8(?:0[1-9]|1[0-8])|9(?:0[1-9]|1[1-6]))\\d{7}', , , , '8021234567', , , [10]],
                  [, , '800\\d{7,11}', , , , '80017591759'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'NG',
                  234,
                  '009',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[7-9]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['20[129]'], '0$1'],
                    [, '(\\d{4})(\\d{2})(\\d{4})', '$1 $2 $3', ['2'], '0$1'],
                    [, '(\\d{3})(\\d{4})(\\d{4,5})', '$1 $2 $3', ['[78]'], '0$1'],
                    [, '(\\d{3})(\\d{5})(\\d{5,6})', '$1 $2 $3', ['[78]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '700\\d{7,11}', , , , '7001234567'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NI: [
                  ,
                  [, , '(?:1800|[25-8]\\d{3})\\d{4}', , , , , , , [8]],
                  [, , '2\\d{7}', , , , '21234567'],
                  [, , '(?:5(?:5[0-7]|[78]\\d)|6(?:20|3[035]|4[045]|5[05]|77|8[1-9]|9[059])|(?:7[5-8]|8\\d)\\d)\\d{5}', , , , '81234567'],
                  [, , '1800\\d{4}', , , , '18001234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'NI',
                  505,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{4})(\\d{4})', '$1 $2', ['[125-8]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NL: [
                  ,
                  [, , '(?:[124-7]\\d\\d|3(?:[02-9]\\d|1[0-8]))\\d{6}|8\\d{6,9}|9\\d{6,10}|1\\d{4,5}', , , , , , , [5, 6, 7, 8, 9, 10, 11]],
                  [
                    ,
                    ,
                    '(?:1(?:[035]\\d|1[13-578]|6[124-8]|7[24]|8[0-467])|2(?:[0346]\\d|2[2-46-9]|5[125]|9[479])|3(?:[03568]\\d|1[3-8]|2[01]|4[1-8])|4(?:[0356]\\d|1[1-368]|7[58]|8[15-8]|9[23579])|5(?:[0358]\\d|[19][1-9]|2[1-57-9]|4[13-8]|6[126]|7[0-3578])|7\\d\\d)\\d{6}',
                    ,
                    ,
                    ,
                    '101234567',
                    ,
                    ,
                    [9]
                  ],
                  [, , '(?:6[1-58]|970\\d)\\d{7}', , , , '612345678', , , [9, 11]],
                  [, , '800\\d{4,7}', , , , '8001234', , , [7, 8, 9, 10]],
                  [, , '90[069]\\d{4,7}', , , , '9061234', , , [7, 8, 9, 10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '(?:85|91)\\d{7}', , , , '851234567', , , [9]],
                  'NL',
                  31,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})', '$1', ['1[238]|[34]']],
                    [, '(\\d{2})(\\d{3,4})', '$1 $2', ['14']],
                    [, '(\\d{6})', '$1', ['1']],
                    [, '(\\d{3})(\\d{4,7})', '$1 $2', ['[89]0'], '0$1'],
                    [, '(\\d{2})(\\d{7})', '$1 $2', ['66'], '0$1'],
                    [, '(\\d)(\\d{8})', '$1 $2', ['6'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['1[16-8]|2[259]|3[124]|4[17-9]|5[124679]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[1-578]|91'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{5})', '$1 $2 $3', ['9'], '0$1']
                  ],
                  [
                    [, '(\\d{3})(\\d{4,7})', '$1 $2', ['[89]0'], '0$1'],
                    [, '(\\d{2})(\\d{7})', '$1 $2', ['66'], '0$1'],
                    [, '(\\d)(\\d{8})', '$1 $2', ['6'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['1[16-8]|2[259]|3[124]|4[17-9]|5[124679]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[1-578]|91'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{5})', '$1 $2 $3', ['9'], '0$1']
                  ],
                  [, , '66\\d{7}', , , , '662345678', , , [9]],
                  ,
                  ,
                  [, , '140(?:1[035]|2[0346]|3[03568]|4[0356]|5[0358]|8[458])|140(?:1[16-8]|2[259]|3[124]|4[17-9]|5[124679]|7)\\d', , , , , , , [5, 6]],
                  [
                    ,
                    ,
                    '140(?:1[035]|2[0346]|3[03568]|4[0356]|5[0358]|8[458])|(?:140(?:1[16-8]|2[259]|3[124]|4[17-9]|5[124679]|7)|8[478]\\d{6})\\d',
                    ,
                    ,
                    ,
                    '14020',
                    ,
                    ,
                    [5, 6, 9]
                  ],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NO: [
                  ,
                  [, , '(?:0|[2-9]\\d{3})\\d{4}', , , , , , , [5, 8]],
                  [, , '(?:2[1-4]|3[1-3578]|5[1-35-7]|6[1-4679]|7[0-8])\\d{6}', , , , '21234567', , , [8]],
                  [, , '(?:4[015-8]|9\\d)\\d{6}', , , , '40612345', , , [8]],
                  [, , '80[01]\\d{5}', , , , '80012345', , , [8]],
                  [, , '82[09]\\d{5}', , , , '82012345', , , [8]],
                  [, , '810(?:0[0-6]|[2-8]\\d)\\d{3}', , , , '81021234', , , [8]],
                  [, , '880\\d{5}', , , , '88012345', , , [8]],
                  [, , '85[0-5]\\d{5}', , , , '85012345', , , [8]],
                  'NO',
                  47,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{2})(\\d{3})', '$1 $2 $3', ['8']],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[2-79]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  1,
                  '[02-689]|7[0-8]',
                  [, , , , , , , , , [-1]],
                  [, , '(?:0[235-9]|81(?:0(?:0[7-9]|1\\d)|5\\d\\d))\\d{3}', , , , '02000'],
                  ,
                  ,
                  [, , '81[23]\\d{5}', , , , '81212345', , , [8]]
                ],
                NP: [
                  ,
                  [, , '(?:1\\d|9)\\d{9}|[1-9]\\d{7}', , , , , , , [8, 10, 11], [6, 7]],
                  [
                    ,
                    ,
                    '(?:1[0-6]\\d|99[02-6])\\d{5}|(?:2[13-79]|3[135-8]|4[146-9]|5[135-7]|6[13-9]|7[15-9]|8[1-46-9]|9[1-7])[2-6]\\d{5}',
                    ,
                    ,
                    ,
                    '14567890',
                    ,
                    ,
                    [8],
                    [6, 7]
                  ],
                  [, , '9(?:00|6[0-3]|7[0-24-6]|8[0-24-68])\\d{7}', , , , '9841234567', , , [10]],
                  [, , '1(?:66001|800\\d\\d)\\d{5}', , , , '16600101234', , , [11]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'NP',
                  977,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{7})', '$1-$2', ['1[2-6]'], '0$1'],
                    [, '(\\d{2})(\\d{6})', '$1-$2', ['1[01]|[2-8]|9(?:[1-59]|[67][2-6])'], '0$1'],
                    [, '(\\d{3})(\\d{7})', '$1-$2', ['9']],
                    [, '(\\d{4})(\\d{2})(\\d{5})', '$1-$2-$3', ['1']]
                  ],
                  [
                    [, '(\\d)(\\d{7})', '$1-$2', ['1[2-6]'], '0$1'],
                    [, '(\\d{2})(\\d{6})', '$1-$2', ['1[01]|[2-8]|9(?:[1-59]|[67][2-6])'], '0$1'],
                    [, '(\\d{3})(\\d{7})', '$1-$2', ['9']]
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NR: [
                  ,
                  [, , '(?:222|444|(?:55|8\\d)\\d|666|777|999)\\d{4}', , , , , , , [7]],
                  [, , '444\\d{4}', , , , '4441234'],
                  [, , '(?:222|55[3-9]|666|777|8\\d\\d|999)\\d{4}', , , , '5551234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'NR',
                  674,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{4})', '$1 $2', ['[24-9]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NU: [
                  ,
                  [, , '(?:[4-7]|888\\d)\\d{3}', , , , , , , [4, 7]],
                  [, , '[47]\\d{3}', , , , '7012', , , [4]],
                  [, , '(?:[56]|888[1-9])\\d{3}', , , , '8884012'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'NU',
                  683,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{4})', '$1 $2', ['8']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NZ: [
                  ,
                  [, , '[1289]\\d{9}|50\\d{5}(?:\\d{2,3})?|[27-9]\\d{7,8}|(?:[34]\\d|6[0-35-9])\\d{6}|8\\d{4,6}', , , , , , , [5, 6, 7, 8, 9, 10]],
                  [, , '240\\d{5}|(?:3[2-79]|[49][2-9]|6[235-9]|7[2-57-9])\\d{6}', , , , '32345678', , , [8], [7]],
                  [, , '2(?:[0-27-9]\\d|6)\\d{6,7}|2(?:1\\d|75)\\d{5}', , , , '211234567', , , [8, 9, 10]],
                  [, , '508\\d{6,7}|80\\d{6,8}', , , , '800123456', , , [8, 9, 10]],
                  [, , '(?:1[13-57-9]\\d{5}|50(?:0[08]|30|66|77|88))\\d{3}|90\\d{6,8}', , , , '900123456', , , [7, 8, 9, 10]],
                  [, , , , , , , , , [-1]],
                  [, , '70\\d{7}', , , , '701234567', , , [9]],
                  [, , , , , , , , , [-1]],
                  'NZ',
                  64,
                  '0(?:0|161)',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  '00',
                  ,
                  [
                    [, '(\\d{2})(\\d{3,8})', '$1 $2', ['8[1-79]'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{2,3})', '$1 $2 $3', ['50[036-8]|8|90', '50(?:[0367]|88)|8|90'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{4})', '$1 $2 $3', ['24|[346]|7[2-57-9]|9[2-9]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['2(?:10|74)|[589]'], '0$1'],
                    [, '(\\d{2})(\\d{3,4})(\\d{4})', '$1 $2 $3', ['1|2[028]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,5})', '$1 $2 $3', ['2(?:[169]|7[0-35-9])|7'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '8(?:1[16-9]|22|3\\d|4[045]|5[459]|6[235-9]|7[0-3579]|90)\\d{2,7}', , , , '83012378'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                OM: [
                  ,
                  [, , '(?:1505|[279]\\d{3}|500)\\d{4}|800\\d{5,6}', , , , , , , [7, 8, 9]],
                  [, , '2[1-6]\\d{6}', , , , '23123456', , , [8]],
                  [, , '1505\\d{4}|(?:7(?:[125-9]\\d|41)|9(?:0[1-9]|[1-9]\\d))\\d{5}', , , , '92123456', , , [8]],
                  [, , '8007\\d{4,5}|(?:500|800[05])\\d{4}', , , , '80071234'],
                  [, , '900\\d{5}', , , , '90012345', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'OM',
                  968,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{4,6})', '$1 $2', ['[58]']],
                    [, '(\\d{2})(\\d{6})', '$1 $2', ['2']],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[179]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PA: [
                  ,
                  [, , '(?:00800|8\\d{3})\\d{6}|[68]\\d{7}|[1-57-9]\\d{6}', , , , , , , [7, 8, 10, 11]],
                  [
                    ,
                    ,
                    '(?:1(?:0\\d|1[0479]|2[37]|3[0137]|4[17]|5[05]|6[058]|7[0167]|8[2358]|9[1389])|2(?:[0235-79]\\d|1[0-7]|4[013-9]|8[02-9])|3(?:[047-9]\\d|1[0-8]|2[0-5]|33|5[0-35]|6[068])|4(?:00|3[0-579]|4\\d|7[0-57-9])|5(?:[01]\\d|2[0-7]|[56]0|79)|7(?:0[09]|2[0-26-8]|3[03]|4[04]|5[05-9]|6[0156]|7[0-24-9]|8[4-9]|90)|8(?:09|2[89]|3\\d|4[0-24-689]|5[014]|8[02])|9(?:0[5-9]|1[0135-8]|2[036-9]|3[35-79]|40|5[0457-9]|6[05-9]|7[04-9]|8[35-8]|9\\d))\\d{4}',
                    ,
                    ,
                    ,
                    '2001234',
                    ,
                    ,
                    [7]
                  ],
                  [, , '(?:1[16]1|21[89]|6\\d{3}|8(?:1[01]|7[23]))\\d{4}', , , , '61234567', , , [7, 8]],
                  [, , '800\\d{4,5}|(?:00800|800\\d)\\d{6}', , , , '8001234'],
                  [, , '(?:8(?:22|55|60|7[78]|86)|9(?:00|81))\\d{4}', , , , '8601234', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'PA',
                  507,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1-$2', ['[1-57-9]']],
                    [, '(\\d{4})(\\d{4})', '$1-$2', ['[68]']],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['8']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PE: [
                  ,
                  [, , '(?:[14-8]|9\\d)\\d{7}', , , , , , , [8, 9], [6, 7]],
                  [
                    ,
                    ,
                    '(?:(?:(?:4[34]|5[14])[0-8]|687)\\d|7(?:173|(?:3[0-8]|55)\\d)|8(?:10[05689]|6(?:0[06-9]|1[6-9]|29)|7(?:0[0569]|[56]0)))\\d{4}|(?:1[0-8]|4[12]|5[236]|6[1-7]|7[246]|8[2-4])\\d{6}',
                    ,
                    ,
                    ,
                    '11234567',
                    ,
                    ,
                    [8],
                    [6, 7]
                  ],
                  [, , '9\\d{8}', , , , '912345678', , , [9]],
                  [, , '800\\d{5}', , , , '80012345', , , [8]],
                  [, , '805\\d{5}', , , , '80512345', , , [8]],
                  [, , '801\\d{5}', , , , '80112345', , , [8]],
                  [, , '80[24]\\d{5}', , , , '80212345', , , [8]],
                  [, , , , , , , , , [-1]],
                  'PE',
                  51,
                  '00|19(?:1[124]|77|90)00',
                  '0',
                  ' Anexo ',
                  ,
                  '0',
                  ,
                  '00',
                  ,
                  [
                    [, '(\\d{3})(\\d{5})', '$1 $2', ['80'], '(0$1)'],
                    [, '(\\d)(\\d{7})', '$1 $2', ['1'], '(0$1)'],
                    [, '(\\d{2})(\\d{6})', '$1 $2', ['[4-8]'], '(0$1)'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['9']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PF: [
                  ,
                  [, , '4\\d{5}(?:\\d{2})?|8\\d{7,8}', , , , , , , [6, 8, 9]],
                  [, , '4(?:0[4-689]|9[4-68])\\d{5}', , , , '40412345', , , [8]],
                  [, , '8[7-9]\\d{6}', , , , '87123456', , , [8]],
                  [, , '80[0-5]\\d{6}', , , , '800012345', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '499\\d{5}', , , , '49901234', , , [8]],
                  'PF',
                  689,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3', ['44']],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['4|8[7-9]']],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['8']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '44\\d{4}', , , , , , , [6]],
                  [, , '44\\d{4}', , , , '440123', , , [6]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PG: [
                  ,
                  [, , '(?:180|[78]\\d{3})\\d{4}|(?:[2-589]\\d|64)\\d{5}', , , , , , , [7, 8]],
                  [, , '(?:(?:3[0-2]|4[257]|5[34]|9[78])\\d|64[1-9]|85[02-46-9])\\d{4}', , , , '3123456', , , [7]],
                  [, , '(?:7\\d|8[1-48])\\d{6}', , , , '70123456', , , [8]],
                  [, , '180\\d{4}', , , , '1801234', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '2(?:0[0-57]|7[568])\\d{4}', , , , '2751234', , , [7]],
                  'PG',
                  675,
                  '00|140[1-3]',
                  ,
                  ,
                  ,
                  ,
                  ,
                  '00',
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['18|[2-69]|85']],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[78]']]
                  ],
                  ,
                  [, , '27[01]\\d{4}', , , , '2700123', , , [7]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PH: [
                  ,
                  [, , '(?:[2-7]|9\\d)\\d{8}|2\\d{5}|(?:1800|8)\\d{7,9}', , , , , , , [6, 8, 9, 10, 11, 12, 13], [4, 5, 7]],
                  [
                    ,
                    ,
                    '(?:(?:2[3-8]|3[2-68]|4[2-9]|5[2-6]|6[2-58]|7[24578])\\d{3}|88(?:22\\d\\d|42))\\d{4}|(?:2|8[2-8]\\d\\d)\\d{5}',
                    ,
                    ,
                    ,
                    '232345678',
                    ,
                    ,
                    [6, 8, 9, 10],
                    [4, 5, 7]
                  ],
                  [, , '(?:8(?:1[37]|9[5-8])|9(?:0[5-9]|1[0-24-9]|[235-7]\\d|4[2-9]|8[135-9]|9[1-9]))\\d{7}', , , , '9051234567', , , [10]],
                  [, , '1800\\d{7,9}', , , , '180012345678', , , [11, 12, 13]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'PH',
                  63,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{5})', '$1 $2', ['2'], '(0$1)'],
                    [
                      ,
                      '(\\d{4})(\\d{4,6})',
                      '$1 $2',
                      [
                        '3(?:23|39|46)|4(?:2[3-6]|[35]9|4[26]|76)|544|88[245]|(?:52|64|86)2',
                        '3(?:230|397|461)|4(?:2(?:35|[46]4|51)|396|4(?:22|63)|59[347]|76[15])|5(?:221|446)|642[23]|8(?:622|8(?:[24]2|5[13]))'
                      ],
                      '(0$1)'
                    ],
                    [, '(\\d{5})(\\d{4})', '$1 $2', ['346|4(?:27|9[35])|883', '3469|4(?:279|9(?:30|56))|8834'], '(0$1)'],
                    [, '(\\d)(\\d{4})(\\d{4})', '$1 $2 $3', ['2'], '(0$1)'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[3-7]|8[2-8]'], '(0$1)'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['[89]'], '0$1'],
                    [, '(\\d{4})(\\d{3})(\\d{4})', '$1 $2 $3', ['1']],
                    [, '(\\d{4})(\\d{1,2})(\\d{3})(\\d{4})', '$1 $2 $3 $4', ['1']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PK: [
                  ,
                  [
                    ,
                    ,
                    '122\\d{6}|[24-8]\\d{10,11}|9(?:[013-9]\\d{8,10}|2(?:[01]\\d\\d|2(?:[06-8]\\d|1[01]))\\d{7})|(?:[2-8]\\d{3}|92(?:[0-7]\\d|8[1-9]))\\d{6}|[24-9]\\d{8}|[89]\\d{7}',
                    ,
                    ,
                    ,
                    ,
                    ,
                    ,
                    [8, 9, 10, 11, 12],
                    [5, 6, 7]
                  ],
                  [
                    ,
                    ,
                    '(?:(?:21|42)[2-9]|58[126])\\d{7}|(?:2[25]|4[0146-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91)[2-9]\\d{6,7}|(?:2(?:3[2358]|4[2-4]|9[2-8])|45[3479]|54[2-467]|60[468]|72[236]|8(?:2[2-689]|3[23578]|4[3478]|5[2356])|9(?:2[2-8]|3[27-9]|4[2-6]|6[3569]|9[25-8]))[2-9]\\d{5,6}',
                    ,
                    ,
                    ,
                    '2123456789',
                    ,
                    ,
                    [9, 10],
                    [5, 6, 7, 8]
                  ],
                  [, , '3(?:[0-247]\\d|3[0-79]|55|64)\\d{7}', , , , '3012345678', , , [10]],
                  [, , '800\\d{5}(?:\\d{3})?', , , , '80012345', , , [8, 11]],
                  [, , '900\\d{5}', , , , '90012345', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , '122\\d{6}', , , , '122044444', , , [9]],
                  [, , , , , , , , , [-1]],
                  'PK',
                  92,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3})(\\d{2,7})', '$1 $2 $3', ['[89]0'], '0$1'],
                    [, '(\\d{4})(\\d{5})', '$1 $2', ['1']],
                    [
                      ,
                      '(\\d{3})(\\d{6,7})',
                      '$1 $2',
                      [
                        '2(?:3[2358]|4[2-4]|9[2-8])|45[3479]|54[2-467]|60[468]|72[236]|8(?:2[2-689]|3[23578]|4[3478]|5[2356])|9(?:2[2-8]|3[27-9]|4[2-6]|6[3569]|9[25-8])',
                        '9(?:2[3-8]|98)|(?:2(?:3[2358]|4[2-4]|9[2-8])|45[3479]|54[2-467]|60[468]|72[236]|8(?:2[2-689]|3[23578]|4[3478]|5[2356])|9(?:22|3[27-9]|4[2-6]|6[3569]|9[25-7]))[2-9]'
                      ],
                      '(0$1)'
                    ],
                    [, '(\\d{2})(\\d{7,8})', '$1 $2', ['(?:2[125]|4[0-246-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91)[2-9]'], '(0$1)'],
                    [, '(\\d{5})(\\d{5})', '$1 $2', ['58'], '(0$1)'],
                    [, '(\\d{3})(\\d{7})', '$1 $2', ['3'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['2[125]|4[0-246-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91'], '(0$1)'],
                    [, '(\\d{3})(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['[24-9]'], '(0$1)']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '(?:2(?:[125]|3[2358]|4[2-4]|9[2-8])|4(?:[0-246-9]|5[3479])|5(?:[1-35-7]|4[2-467])|6(?:0[468]|[1-8])|7(?:[14]|2[236])|8(?:[16]|2[2-689]|3[23578]|4[3478]|5[2356])|9(?:1|22|3[27-9]|4[2-6]|6[3569]|9[2-7]))111\\d{6}',
                    ,
                    ,
                    ,
                    '21111825888',
                    ,
                    ,
                    [11, 12]
                  ],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PL: [
                  ,
                  [, , '(?:6|8\\d\\d)\\d{7}|[1-9]\\d{6}(?:\\d{2})?|[26]\\d{5}', , , , , , , [6, 7, 8, 9, 10]],
                  [
                    ,
                    ,
                    '47\\d{7}|(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])(?:[02-9]\\d{6}|1(?:[0-8]\\d{5}|9\\d{3}(?:\\d{2})?))',
                    ,
                    ,
                    ,
                    '123456789',
                    ,
                    ,
                    [7, 9]
                  ],
                  [, , '2131[89]\\d{4}|21(?:1[013-5]|2\\d|3[2-9])\\d{5}|(?:45|5[0137]|6[069]|7[2389]|88)\\d{7}', , , , '512345678', , , [9]],
                  [, , '800\\d{6,7}', , , , '800123456', , , [9, 10]],
                  [, , '70[01346-8]\\d{6}', , , , '701234567', , , [9]],
                  [, , '801\\d{6}', , , , '801234567', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , '39\\d{7}', , , , '391234567', , , [9]],
                  'PL',
                  48,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{5})', '$1', ['19']],
                    [, '(\\d{3})(\\d{3})', '$1 $2', ['11|20|64']],
                    [
                      ,
                      '(\\d{2})(\\d{2})(\\d{3})',
                      '$1 $2 $3',
                      [
                        '(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])1',
                        '(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])19'
                      ]
                    ],
                    [, '(\\d{3})(\\d{2})(\\d{2,3})', '$1 $2 $3', ['64']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['21|39|45|5[0137]|6[0469]|7[02389]|8(?:0[14]|8)']],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['1[2-8]|[2-7]|8[1-79]|9[145]']],
                    [, '(\\d{3})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['8']]
                  ],
                  ,
                  [, , '64\\d{4,7}', , , , '641234567', , , [6, 7, 8, 9]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '804\\d{6}', , , , '804123456', , , [9]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PM: [
                  ,
                  [, , '[45]\\d{5}|(?:708|8\\d\\d)\\d{6}', , , , , , , [6, 9]],
                  [, , '(?:4[1-35-9]|5[0-47-9]|80[6-9]\\d\\d)\\d{4}', , , , '430123'],
                  [, , '(?:4[02-489]|5[02-9]|708(?:4[0-5]|5[0-6]))\\d{4}', , , , '551234'],
                  [, , '80[0-5]\\d{6}', , , , '800012345', , , [9]],
                  [, , '8[129]\\d{7}', , , , '810123456', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'PM',
                  508,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3', ['[45]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['7']],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['8'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PR: [
                  ,
                  [, , '(?:[589]\\d\\d|787)\\d{7}', , , , , , , [10], [7]],
                  [, , '(?:787|939)[2-9]\\d{6}', , , , '7872345678', , , , [7]],
                  [, , '(?:787|939)[2-9]\\d{6}', , , , '7872345678', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002345678'],
                  [, , '900[2-9]\\d{6}', , , , '9002345678'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'PR',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '1',
                  ,
                  ,
                  1,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '787|939',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PS: [
                  ,
                  [, , '[2489]2\\d{6}|(?:1\\d|5)\\d{8}', , , , , , , [8, 9, 10], [7]],
                  [, , '(?:22[2-47-9]|42[45]|82[014-68]|92[3569])\\d{5}', , , , '22234567', , , [8], [7]],
                  [, , '5[69]\\d{7}', , , , '599123456', , , [9]],
                  [, , '1800\\d{6}', , , , '1800123456', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , '1700\\d{6}', , , , '1700123456', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'PS',
                  970,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{4})', '$1 $2 $3', ['[2489]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['5'], '0$1'],
                    [, '(\\d{4})(\\d{3})(\\d{3})', '$1 $2 $3', ['1']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PT: [
                  ,
                  [, , '1693\\d{5}|(?:[26-9]\\d|30)\\d{7}', , , , , , , [9]],
                  [, , '2(?:[12]\\d|3[1-689]|4[1-59]|[57][1-9]|6[1-35689]|8[1-69]|9[1256])\\d{6}', , , , '212345678'],
                  [, , '6(?:[06]92(?:30|9\\d)|[35]92(?:[049]\\d|3[034]))\\d{3}|(?:(?:16|6[0356])93|9(?:[1-36]\\d\\d|480))\\d{5}', , , , '912345678'],
                  [, , '80[02]\\d{6}', , , , '800123456'],
                  [, , '(?:6(?:0[178]|4[68])\\d|76(?:0[1-57]|1[2-47]|2[237]))\\d{5}', , , , '760123456'],
                  [, , '80(?:8\\d|9[1579])\\d{5}', , , , '808123456'],
                  [, , '884[0-4689]\\d{5}', , , , '884123456'],
                  [, , '30\\d{7}', , , , '301234567'],
                  'PT',
                  351,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['2[12]']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['16|[236-9]']]
                  ],
                  ,
                  [, , '6(?:222\\d|89(?:00|88|99))\\d{4}', , , , '622212345'],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '70(?:38[01]|596|(?:7\\d|8[17])\\d)\\d{4}', , , , '707123456'],
                  ,
                  ,
                  [, , '600\\d{6}|6[06]92(?:0\\d|3[349]|49)\\d{3}', , , , '600110000']
                ],
                PW: [
                  ,
                  [, , '(?:[24-8]\\d\\d|345|900)\\d{4}', , , , , , , [7]],
                  [, , '(?:2(?:55|77)|345|488|5(?:35|44|87)|6(?:22|54|79)|7(?:33|47)|8(?:24|55|76)|900)\\d{4}', , , , '2771234'],
                  [, , '(?:(?:46|83)[0-5]|(?:6[2-4689]|78)0)\\d{4}|(?:45|77|88)\\d{5}', , , , '6201234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'PW',
                  680,
                  '01[12]',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{4})', '$1 $2', ['[2-9]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PY: [
                  ,
                  [, , '59\\d{4,6}|9\\d{5,10}|(?:[2-46-8]\\d|5[0-8])\\d{4,7}', , , , , , , [6, 7, 8, 9, 10, 11], [5]],
                  [
                    ,
                    ,
                    '(?:[26]1|3[289]|4[1246-8]|7[1-3]|8[1-36])\\d{5,7}|(?:2(?:2[4-68]|[4-68]\\d|7[15]|9[1-5])|3(?:18|3[167]|4[2357]|51|[67]\\d)|4(?:3[12]|5[13]|9[1-47])|5(?:[1-4]\\d|5[02-4])|6(?:3[1-3]|44|7[1-8])|7(?:4[0-4]|5\\d|6[1-578]|75|8[0-8])|858)\\d{5,6}',
                    ,
                    ,
                    ,
                    '212345678',
                    ,
                    ,
                    [7, 8, 9],
                    [5, 6]
                  ],
                  [, , '9(?:51|6[129]|7[1-6]|8[1-7]|9[1-5])\\d{6}', , , , '961456789', , , [9]],
                  [, , '9800\\d{5,7}', , , , '98000123456', , , [9, 10, 11]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '8700[0-4]\\d{4}', , , , '870012345', , , [9]],
                  'PY',
                  595,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3,6})', '$1 $2', ['[2-9]0'], '0$1'],
                    [, '(\\d{2})(\\d{5})', '$1 $2', ['[26]1|3[289]|4[1246-8]|7[1-3]|8[1-36]'], '(0$1)'],
                    [, '(\\d{3})(\\d{4,5})', '$1 $2', ['2[279]|3[13-5]|4[359]|5|6(?:[34]|7[1-46-8])|7[46-8]|85'], '(0$1)'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['2[14-68]|3[26-9]|4[1246-8]|6(?:1|75)|7[1-35]|8[1-36]'], '(0$1)'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['87']],
                    [, '(\\d{3})(\\d{6})', '$1 $2', ['9(?:[5-79]|8[1-7])'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[2-8]'], '0$1'],
                    [, '(\\d{4})(\\d{3})(\\d{4})', '$1 $2 $3', ['9']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '[2-9]0\\d{4,7}', , , , '201234567', , , [6, 7, 8, 9]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                QA: [
                  ,
                  [, , '800\\d{4}|(?:2|800)\\d{6}|(?:0080|[3-7])\\d{7}', , , , , , , [7, 8, 9, 11]],
                  [, , '4(?:1111|2022)\\d{3}|4(?:[04]\\d\\d|14[0-6]|999)\\d{4}', , , , '44123456', , , [8]],
                  [, , '[35-7]\\d{7}', , , , '33123456', , , [8]],
                  [, , '800\\d{4}|(?:0080[01]|800)\\d{6}', , , , '8001234', , , [7, 9, 11]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'QA',
                  974,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['2[136]|8']],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[3-7]']]
                  ],
                  ,
                  [, , '2[136]\\d{5}', , , , '2123456', , , [7]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                RE: [
                  ,
                  [, , '709\\d{6}|(?:26|[689]\\d)\\d{7}', , , , , , , [9]],
                  [, , '26(?:2\\d\\d|3(?:0\\d|1[0-6]))\\d{4}', , , , '262161234'],
                  [, , '(?:69(?:2\\d\\d|3(?:[06][0-6]|1[0-3]|2[0-2]|3[0-39]|4\\d|5[0-5]|7[0-37]|8[0-8]|9[0-479]))|7092[0-3])\\d{4}', , , , '692123456'],
                  [, , '80\\d{7}', , , , '801234567'],
                  [, , '89[1-37-9]\\d{6}', , , , '891123456'],
                  [, , '8(?:1[019]|2[0156]|84|90)\\d{6}', , , , '810123456'],
                  [, , , , , , , , , [-1]],
                  [, , '9(?:399[0-3]|479[0-6]|76(?:2[278]|3[0-37]))\\d{4}', , , , '939901234'],
                  'RE',
                  262,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[26-9]'], '0$1']],
                  ,
                  [, , , , , , , , , [-1]],
                  1,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                RO: [
                  ,
                  [, , '(?:[236-8]\\d|90)\\d{7}|[23]\\d{5}', , , , , , , [6, 9]],
                  [, , '[23][13-6]\\d{7}|(?:2(?:19\\d|[3-6]\\d9)|31\\d\\d)\\d\\d', , , , '211234567'],
                  [, , '(?:630|702)0\\d{5}|(?:6(?:00|2\\d)|7(?:0[013-9]|1[0-3]|[2-7]\\d|8[03-8]|9[0-39]))\\d{6}', , , , '712034567', , , [9]],
                  [, , '800\\d{6}', , , , '800123456', , , [9]],
                  [, , '90[0136]\\d{6}', , , , '900123456', , , [9]],
                  [, , '801\\d{6}', , , , '801123456', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'RO',
                  40,
                  '00',
                  '0',
                  ' int ',
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3})', '$1 $2', ['2[3-6]', '2[3-6]\\d9'], '0$1'],
                    [, '(\\d{2})(\\d{4})', '$1 $2', ['219|31'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[23]1'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[236-9]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '(?:37\\d|80[578])\\d{6}', , , , '372123456', , , [9]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                RS: [
                  ,
                  [
                    ,
                    ,
                    '38[02-9]\\d{6,9}|6\\d{7,9}|90\\d{4,8}|38\\d{5,6}|(?:7\\d\\d|800)\\d{3,9}|(?:[12]\\d|3[0-79])\\d{5,10}',
                    ,
                    ,
                    ,
                    ,
                    ,
                    ,
                    [6, 7, 8, 9, 10, 11, 12],
                    [4, 5]
                  ],
                  [
                    ,
                    ,
                    '(?:11[1-9]\\d|(?:2[389]|39)(?:0[2-9]|[2-9]\\d))\\d{3,8}|(?:1[02-9]|2[0-24-7]|3[0-8])[2-9]\\d{4,9}',
                    ,
                    ,
                    ,
                    '10234567',
                    ,
                    ,
                    [7, 8, 9, 10, 11, 12],
                    [4, 5, 6]
                  ],
                  [, , '6(?:[0-689]|7\\d)\\d{6,7}', , , , '601234567', , , [8, 9, 10]],
                  [, , '800\\d{3,9}', , , , '80012345'],
                  [, , '(?:78\\d|90[0169])\\d{3,7}', , , , '90012345', , , [6, 7, 8, 9, 10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'RS',
                  381,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3,9})', '$1 $2', ['(?:2[389]|39)0|[7-9]'], '0$1'],
                    [, '(\\d{2})(\\d{5,10})', '$1 $2', ['[1-36]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '7[06]\\d{4,10}', , , , '700123456'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                RU: [
                  ,
                  [, , '8\\d{13}|[347-9]\\d{9}', , , , , , , [10, 14], [7]],
                  [
                    ,
                    ,
                    '336(?:[013-9]\\d|2[013-9])\\d{5}|(?:3(?:0[12]|4[1-35-79]|5[1-3]|65|8[1-58]|9[0145])|4(?:01|1[1356]|2[13467]|7[1-5]|8[1-7]|9[1-689])|8(?:1[1-8]|2[01]|3[13-6]|4[0-8]|5[15-7]|6[0-35-79]|7[1-37-9]))\\d{7}',
                    ,
                    ,
                    ,
                    '3011234567',
                    ,
                    ,
                    [10],
                    [7]
                  ],
                  [, , '9\\d{9}', , , , '9123456789', , , [10]],
                  [, , '8(?:0[04]|108\\d{3})\\d{7}', , , , '8001234567'],
                  [, , '80[39]\\d{7}', , , , '8091234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , '808\\d{7}', , , , '8081234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  'RU',
                  7,
                  '810',
                  '8',
                  ,
                  ,
                  '8',
                  ,
                  '8~10',
                  ,
                  [
                    [, '(\\d{3})(\\d{2})(\\d{2})', '$1-$2-$3', ['[0-79]']],
                    [
                      ,
                      '(\\d{4})(\\d{2})(\\d{2})(\\d{2})',
                      '$1 $2 $3 $4',
                      [
                        '7(?:1[0-8]|2[1-9])',
                        '7(?:1(?:[0-356]2|4[29]|7|8[27])|2(?:1[23]|[2-9]2))',
                        '7(?:1(?:[0-356]2|4[29]|7|8[27])|2(?:13[03-69]|62[013-9]))|72[1-57-9]2'
                      ],
                      '8 ($1)',
                      ,
                      1
                    ],
                    [
                      ,
                      '(\\d{5})(\\d)(\\d{2})(\\d{2})',
                      '$1 $2 $3 $4',
                      [
                        '7(?:1[0-68]|2[1-9])',
                        '7(?:1(?:[06][3-6]|[18]|2[35]|[3-5][3-5])|2(?:[13][3-5]|[24-689]|7[457]))',
                        '7(?:1(?:0(?:[356]|4[023])|[18]|2(?:3[013-9]|5)|3[45]|43[013-79]|5(?:3[1-8]|4[1-7]|5)|6(?:3[0-35-9]|[4-6]))|2(?:1(?:3[178]|[45])|[24-689]|3[35]|7[457]))|7(?:14|23)4[0-8]|71(?:33|45)[1-79]'
                      ],
                      '8 ($1)',
                      ,
                      1
                    ],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['7'], '8 ($1)', , 1],
                    [, '(\\d{3})(\\d{3})(\\d{2})(\\d{2})', '$1 $2-$3-$4', ['[349]|8(?:[02-7]|1[1-8])'], '8 ($1)', , 1],
                    [, '(\\d{4})(\\d{4})(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['8'], '8 ($1)']
                  ],
                  [
                    [
                      ,
                      '(\\d{4})(\\d{2})(\\d{2})(\\d{2})',
                      '$1 $2 $3 $4',
                      [
                        '7(?:1[0-8]|2[1-9])',
                        '7(?:1(?:[0-356]2|4[29]|7|8[27])|2(?:1[23]|[2-9]2))',
                        '7(?:1(?:[0-356]2|4[29]|7|8[27])|2(?:13[03-69]|62[013-9]))|72[1-57-9]2'
                      ],
                      '8 ($1)',
                      ,
                      1
                    ],
                    [
                      ,
                      '(\\d{5})(\\d)(\\d{2})(\\d{2})',
                      '$1 $2 $3 $4',
                      [
                        '7(?:1[0-68]|2[1-9])',
                        '7(?:1(?:[06][3-6]|[18]|2[35]|[3-5][3-5])|2(?:[13][3-5]|[24-689]|7[457]))',
                        '7(?:1(?:0(?:[356]|4[023])|[18]|2(?:3[013-9]|5)|3[45]|43[013-79]|5(?:3[1-8]|4[1-7]|5)|6(?:3[0-35-9]|[4-6]))|2(?:1(?:3[178]|[45])|[24-689]|3[35]|7[457]))|7(?:14|23)4[0-8]|71(?:33|45)[1-79]'
                      ],
                      '8 ($1)',
                      ,
                      1
                    ],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['7'], '8 ($1)', , 1],
                    [, '(\\d{3})(\\d{3})(\\d{2})(\\d{2})', '$1 $2-$3-$4', ['[349]|8(?:[02-7]|1[1-8])'], '8 ($1)', , 1],
                    [, '(\\d{4})(\\d{4})(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['8'], '8 ($1)']
                  ],
                  [, , , , , , , , , [-1]],
                  1,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                RW: [
                  ,
                  [, , '(?:06|[27]\\d\\d|[89]00)\\d{6}', , , , , , , [8, 9]],
                  [, , '(?:06|2[23568]\\d)\\d{6}', , , , '250123456'],
                  [, , '7[237-9]\\d{7}', , , , '720123456', , , [9]],
                  [, , '800\\d{6}', , , , '800123456', , , [9]],
                  [, , '900\\d{6}', , , , '900123456', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'RW',
                  250,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['0']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['2']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[7-9]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SA: [
                  ,
                  [, , '(?:[15]\\d|800|92)\\d{7}', , , , , , , [9, 10], [7]],
                  [, , '1(?:1\\d|2[24-8]|3[35-8]|4[3-68]|6[2-5]|7[235-7])\\d{6}', , , , '112345678', , , [9], [7]],
                  [, , '579[01]\\d{5}|5(?:[013-689]\\d|7[0-8])\\d{6}', , , , '512345678', , , [9]],
                  [, , '800\\d{7}', , , , '8001234567', , , [10]],
                  [, , '925\\d{6}', , , , '925012345', , , [9]],
                  [, , '920\\d{6}', , , , '920012345', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'SA',
                  966,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})(\\d{5})', '$1 $2', ['9']],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['1'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['5'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['8']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SB: [
                  ,
                  [, , '[6-9]\\d{6}|[1-6]\\d{4}', , , , , , , [5, 7]],
                  [, , '(?:1[4-79]|[23]\\d|4[0-2]|5[03]|6[0-37])\\d{3}', , , , '40123', , , [5]],
                  [, , '48\\d{3}|(?:(?:6[89]|7[1-9]|8[4-9])\\d|9(?:1[2-9]|2[013-9]|3[0-2]|[46]\\d|5[0-46-9]|7[0-689]|8[0-79]|9[0-8]))\\d{4}', , , , '7421234'],
                  [, , '1[38]\\d{3}', , , , '18123', , , [5]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '5[12]\\d{3}', , , , '51123', , , [5]],
                  'SB',
                  677,
                  '0[01]',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{5})', '$1 $2', ['6[89]|7|8[4-9]|9(?:[1-8]|9[0-8])']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SC: [
                  ,
                  [, , '(?:[2489]\\d|64)\\d{5}', , , , , , , [7]],
                  [, , '4[2-46]\\d{5}', , , , '4217123'],
                  [, , '2[125-8]\\d{5}', , , , '2510123'],
                  [, , '800[08]\\d{3}', , , , '8000000'],
                  [, , '85\\d{5}', , , , '8512345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '971\\d{4}|(?:64|95)\\d{5}', , , , '6412345'],
                  'SC',
                  248,
                  '010|0[0-2]',
                  ,
                  ,
                  ,
                  ,
                  ,
                  '00',
                  ,
                  [[, '(\\d)(\\d{3})(\\d{3})', '$1 $2 $3', ['[246]|9[57]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SD: [
                  ,
                  [, , '[19]\\d{8}', , , , , , , [9]],
                  [, , '1(?:5\\d|8[35-7])\\d{6}', , , , '153123456'],
                  [, , '(?:1[0-2]|9[0-3569])\\d{7}', , , , '911231234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'SD',
                  249,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[19]'], '0$1']],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SE: [
                  ,
                  [, , '(?:[26]\\d\\d|9)\\d{9}|[1-9]\\d{8}|[1-689]\\d{7}|[1-4689]\\d{6}|2\\d{5}', , , , , , , [6, 7, 8, 9, 10, 12]],
                  [
                    ,
                    ,
                    '(?:(?:[12][136]|3[356]|4[0246]|6[03]|8\\d)\\d|90[1-9])\\d{4,6}|(?:1(?:2[0-35]|4[0-4]|5[0-25-9]|7[13-6]|[89]\\d)|2(?:2[0-7]|4[0136-8]|5[0138]|7[018]|8[01]|9[0-57])|3(?:0[0-4]|1\\d|2[0-25]|4[056]|7[0-2]|8[0-3]|9[023])|4(?:1[013-8]|3[0135]|5[14-79]|7[0-246-9]|8[0156]|9[0-689])|5(?:0[0-6]|[15][0-5]|2[0-68]|3[0-4]|4\\d|6[03-5]|7[013]|8[0-79]|9[01])|6(?:1[1-3]|2[0-4]|4[02-57]|5[0-37]|6[0-3]|7[0-2]|8[0247]|9[0-356])|9(?:1[0-68]|2\\d|3[02-5]|4[0-3]|5[0-4]|[68][01]|7[0135-8]))\\d{5,6}',
                    ,
                    ,
                    ,
                    '8123456',
                    ,
                    ,
                    [7, 8, 9]
                  ],
                  [, , '7[02369]\\d{7}', , , , '701234567', , , [9]],
                  [, , '20\\d{4,7}', , , , '20123456', , , [6, 7, 8, 9]],
                  [, , '649\\d{6}|99[1-59]\\d{4}(?:\\d{3})?|9(?:00|39|44)[1-8]\\d{3,6}', , , , '9001234567', , , [7, 8, 9, 10]],
                  [, , '77[0-7]\\d{6}', , , , '771234567', , , [9]],
                  [, , '75[1-8]\\d{6}', , , , '751234567', , , [9]],
                  [, , , , , , , , , [-1]],
                  'SE',
                  46,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2,3})(\\d{2})', '$1-$2 $3', ['20'], '0$1'],
                    [, '(\\d{3})(\\d{4})', '$1-$2', ['9(?:00|39|44|9)'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2})', '$1-$2 $3', ['[12][136]|3[356]|4[0246]|6[03]|90[1-9]'], '0$1'],
                    [, '(\\d)(\\d{2,3})(\\d{2})(\\d{2})', '$1-$2 $3 $4', ['8'], '0$1'],
                    [
                      ,
                      '(\\d{3})(\\d{2,3})(\\d{2})',
                      '$1-$2 $3',
                      ['1[2457]|2(?:[247-9]|5[0138])|3[0247-9]|4[1357-9]|5[0-35-9]|6(?:[125689]|4[02-57]|7[0-2])|9(?:[125-8]|3[02-5]|4[0-3])'],
                      '0$1'
                    ],
                    [, '(\\d{3})(\\d{2,3})(\\d{3})', '$1-$2 $3', ['9(?:00|39|44)'], '0$1'],
                    [, '(\\d{2})(\\d{2,3})(\\d{2})(\\d{2})', '$1-$2 $3 $4', ['1[13689]|2[0136]|3[1356]|4[0246]|54|6[03]|90[1-9]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1-$2 $3 $4', ['10|7'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{3})(\\d{2})', '$1-$2 $3 $4', ['8'], '0$1'],
                    [
                      ,
                      '(\\d{3})(\\d{2})(\\d{2})(\\d{2})',
                      '$1-$2 $3 $4',
                      ['[13-5]|2(?:[247-9]|5[0138])|6(?:[124-689]|7[0-2])|9(?:[125-8]|3[02-5]|4[0-3])'],
                      '0$1'
                    ],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{3})', '$1-$2 $3 $4', ['9'], '0$1'],
                    [, '(\\d{3})(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1-$2 $3 $4 $5', ['[26]'], '0$1']
                  ],
                  [
                    [, '(\\d{2})(\\d{2,3})(\\d{2})', '$1 $2 $3', ['20']],
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['9(?:00|39|44|9)']],
                    [, '(\\d{2})(\\d{3})(\\d{2})', '$1 $2 $3', ['[12][136]|3[356]|4[0246]|6[03]|90[1-9]']],
                    [, '(\\d)(\\d{2,3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['8']],
                    [
                      ,
                      '(\\d{3})(\\d{2,3})(\\d{2})',
                      '$1 $2 $3',
                      ['1[2457]|2(?:[247-9]|5[0138])|3[0247-9]|4[1357-9]|5[0-35-9]|6(?:[125689]|4[02-57]|7[0-2])|9(?:[125-8]|3[02-5]|4[0-3])']
                    ],
                    [, '(\\d{3})(\\d{2,3})(\\d{3})', '$1 $2 $3', ['9(?:00|39|44)']],
                    [, '(\\d{2})(\\d{2,3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['1[13689]|2[0136]|3[1356]|4[0246]|54|6[03]|90[1-9]']],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['10|7']],
                    [, '(\\d)(\\d{3})(\\d{3})(\\d{2})', '$1 $2 $3 $4', ['8']],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[13-5]|2(?:[247-9]|5[0138])|6(?:[124-689]|7[0-2])|9(?:[125-8]|3[02-5]|4[0-3])']],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{3})', '$1 $2 $3 $4', ['9']],
                    [, '(\\d{3})(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4 $5', ['[26]']]
                  ],
                  [, , '74[02-9]\\d{6}', , , , '740123456', , , [9]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '10[1-8]\\d{6}', , , , '102345678', , , [9]],
                  ,
                  ,
                  [, , '(?:25[245]|67[3-68])\\d{9}', , , , '254123456789', , , [12]]
                ],
                SG: [
                  ,
                  [, , '(?:(?:1\\d|8)\\d\\d|7000)\\d{7}|[3689]\\d{7}', , , , , , , [8, 10, 11]],
                  [, , '662[0-24-9]\\d{4}|6(?:[0-578]\\d|6[013-57-9]|9[0-35-9])\\d{5}', , , , '61234567', , , [8]],
                  [, , '898[02-8]\\d{4}|(?:8(?:0[1-9]|[1-8]\\d|9[0-7])|9[0-8]\\d)\\d{5}', , , , '81234567', , , [8]],
                  [, , '(?:18|8)00\\d{7}', , , , '18001234567', , , [10, 11]],
                  [, , '1900\\d{7}', , , , '19001234567', , , [11]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '(?:3[12]\\d|666)\\d{5}', , , , '31234567', , , [8]],
                  'SG',
                  65,
                  '0[0-3]\\d',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4,5})', '$1', ['1[013-9]|77', '1(?:[013-8]|9(?:0[1-9]|[1-9]))|77']],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[369]|8(?:0[1-9]|[1-9])']],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['8']],
                    [, '(\\d{4})(\\d{4})(\\d{3})', '$1 $2 $3', ['7']],
                    [, '(\\d{4})(\\d{3})(\\d{4})', '$1 $2 $3', ['1']]
                  ],
                  [
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[369]|8(?:0[1-9]|[1-9])']],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['8']],
                    [, '(\\d{4})(\\d{4})(\\d{3})', '$1 $2 $3', ['7']],
                    [, '(\\d{4})(\\d{3})(\\d{4})', '$1 $2 $3', ['1']]
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '7000\\d{7}', , , , '70001234567', , , [11]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SH: [
                  ,
                  [, , '(?:[256]\\d|8)\\d{3}', , , , , , , [4, 5]],
                  [, , '2(?:[0-57-9]\\d|6[4-9])\\d\\d', , , , '22158'],
                  [, , '[56]\\d{4}', , , , '51234', , , [5]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '262\\d\\d', , , , '26212', , , [5]],
                  'SH',
                  290,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  1,
                  '[256]',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SI: [
                  ,
                  [, , '[1-7]\\d{7}|8\\d{4,7}|90\\d{4,6}', , , , , , , [5, 6, 7, 8]],
                  [, , '(?:[1-357][2-8]|4[24-8])\\d{6}', , , , '12345678', , , [8], [7]],
                  [, , '65(?:[178]\\d|5[56]|6[01])\\d{4}|(?:[37][01]|4[0139]|51|6[489])\\d{6}', , , , '31234567', , , [8]],
                  [, , '80\\d{4,6}', , , , '80123456', , , [6, 7, 8]],
                  [, , '89[1-3]\\d{2,5}|90\\d{4,6}', , , , '90123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '(?:59\\d\\d|8(?:1(?:[67]\\d|8[0-589])|2(?:0\\d|2[0-37-9]|8[0-2489])|3[389]\\d))\\d{4}', , , , '59012345', , , [8]],
                  'SI',
                  386,
                  '00|10(?:22|66|88|99)',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  '00',
                  ,
                  [
                    [, '(\\d{2})(\\d{3,6})', '$1 $2', ['8[09]|9'], '0$1'],
                    [, '(\\d{3})(\\d{5})', '$1 $2', ['59|8'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['[37][01]|4[0139]|51|6'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[1-57]'], '(0$1)']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SJ: [
                  ,
                  [, , '0\\d{4}|(?:[489]\\d|79)\\d{6}', , , , , , , [5, 8]],
                  [, , '79\\d{6}', , , , '79123456', , , [8]],
                  [, , '(?:4[015-8]|9\\d)\\d{6}', , , , '41234567', , , [8]],
                  [, , '80[01]\\d{5}', , , , '80012345', , , [8]],
                  [, , '82[09]\\d{5}', , , , '82012345', , , [8]],
                  [, , '810(?:0[0-6]|[2-8]\\d)\\d{3}', , , , '81021234', , , [8]],
                  [, , '880\\d{5}', , , , '88012345', , , [8]],
                  [, , '85[0-5]\\d{5}', , , , '85012345', , , [8]],
                  'SJ',
                  47,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '79',
                  [, , , , , , , , , [-1]],
                  [, , '(?:0[235-9]|81(?:0(?:0[7-9]|1\\d)|5\\d\\d))\\d{3}', , , , '02000'],
                  ,
                  ,
                  [, , '81[23]\\d{5}', , , , '81212345', , , [8]]
                ],
                SK: [
                  ,
                  [, , '[2-689]\\d{8}|[2-59]\\d{6}|[2-5]\\d{5}', , , , , , , [6, 7, 9]],
                  [
                    ,
                    ,
                    '(?:2(?:16|[2-9]\\d{3})|(?:(?:[3-5][1-8]\\d|819)\\d|601[1-5])\\d)\\d{4}|(?:2|[3-5][1-8])1[67]\\d{3}|[3-5][1-8]16\\d\\d',
                    ,
                    ,
                    ,
                    '221234567'
                  ],
                  [, , '909[1-9]\\d{5}|9(?:0[1-8]|1[0-24-9]|4[03-57-9]|5\\d)\\d{6}', , , , '912123456', , , [9]],
                  [, , '800\\d{6}', , , , '800123456', , , [9]],
                  [, , '9(?:00|[78]\\d)\\d{6}', , , , '900123456', , , [9]],
                  [, , '8[5-9]\\d{7}', , , , '850123456', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , '6(?:02|5[0-4]|9[0-6])\\d{6}', , , , '690123456', , , [9]],
                  'SK',
                  421,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{2})(\\d{3,4})', '$1 $2 $3', ['21'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{2,3})', '$1 $2 $3', ['[3-5][1-8]1', '[3-5][1-8]1[67]'], '0$1'],
                    [, '(\\d{4})(\\d{3})', '$1 $2', ['909', '9090'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{3})(\\d{2})', '$1/$2 $3 $4', ['2'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[689]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1/$2 $3 $4', ['[3-5]'], '0$1']
                  ],
                  [
                    [, '(\\d)(\\d{2})(\\d{3,4})', '$1 $2 $3', ['21'], '0$1'],
                    [, '(\\d{2})(\\d{2})(\\d{2,3})', '$1 $2 $3', ['[3-5][1-8]1', '[3-5][1-8]1[67]'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{3})(\\d{2})', '$1/$2 $3 $4', ['2'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[689]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1/$2 $3 $4', ['[3-5]'], '0$1']
                  ],
                  [, , '9090\\d{3}', , , , '9090123', , , [7]],
                  ,
                  ,
                  [, , '9090\\d{3}|(?:602|8(?:00|[5-9]\\d)|9(?:00|[78]\\d))\\d{6}', , , , , , , [7, 9]],
                  [, , '96\\d{7}', , , , '961234567', , , [9]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SL: [
                  ,
                  [, , '(?:[237-9]\\d|66)\\d{6}', , , , , , , [8], [6]],
                  [, , '22[2-4][2-9]\\d{4}', , , , '22221234', , , , [6]],
                  [, , '(?:25|3[0-5]|66|7[1-9]|8[08]|9[09])\\d{6}', , , , '25123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'SL',
                  232,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{6})', '$1 $2', ['[236-9]'], '(0$1)']],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SM: [
                  ,
                  [, , '(?:0549|[5-7]\\d)\\d{6}', , , , , , , [8, 10], [6]],
                  [, , '0549(?:8[0157-9]|9\\d)\\d{4}', , , , '0549886377', , , [10], [6]],
                  [, , '6[16]\\d{6}', , , , '66661212', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , '7[178]\\d{6}', , , , '71123456', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '5[158]\\d{6}', , , , '58001110', , , [8]],
                  'SM',
                  378,
                  '00',
                  ,
                  ,
                  ,
                  '([89]\\d{5})$',
                  '0549$1',
                  ,
                  ,
                  [
                    [, '(\\d{6})', '$1', ['[89]']],
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[5-7]']],
                    [, '(\\d{4})(\\d{6})', '$1 $2', ['0']]
                  ],
                  [
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[5-7]']],
                    [, '(\\d{4})(\\d{6})', '$1 $2', ['0']]
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SN: [
                  ,
                  [, , '(?:[378]\\d|93)\\d{7}', , , , , , , [9]],
                  [, , '3(?:0(?:1[0-2]|80)|282|3(?:8[1-9]|9[3-9])|611)\\d{5}', , , , '301012345'],
                  [, , '7(?:(?:[06-8]\\d|[19]0|21)\\d|5(?:0[01]|[19]0|2[25]|3[356]|[4-7]\\d|8[35]))\\d{5}', , , , '701234567'],
                  [, , '800\\d{6}', , , , '800123456'],
                  [, , '88[4689]\\d{6}', , , , '884123456'],
                  [, , '81[02468]\\d{6}', , , , '810123456'],
                  [, , , , , , , , , [-1]],
                  [, , '(?:3(?:392|9[01]\\d)\\d|93(?:3[13]0|929))\\d{4}', , , , '933301234'],
                  'SN',
                  221,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['8']],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[379]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SO: [
                  ,
                  [, , '[346-9]\\d{8}|[12679]\\d{7}|[1-5]\\d{6}|[1348]\\d{5}', , , , , , , [6, 7, 8, 9]],
                  [, , '(?:1\\d|2[0-79]|3[0-46-8]|4[0-7]|5[57-9])\\d{5}|(?:[134]\\d|8[125])\\d{4}', , , , '4012345', , , [6, 7]],
                  [
                    ,
                    ,
                    '(?:(?:15|(?:3[59]|4[89]|6\\d|7[679]|8[08])\\d|9(?:0\\d|[2-9]))\\d|2(?:4\\d|8))\\d{5}|(?:[67]\\d\\d|904)\\d{5}',
                    ,
                    ,
                    ,
                    '71123456',
                    ,
                    ,
                    [7, 8, 9]
                  ],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'SO',
                  252,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{4})', '$1 $2', ['8[125]']],
                    [, '(\\d{6})', '$1', ['[134]']],
                    [, '(\\d)(\\d{6})', '$1 $2', ['[15]|2[0-79]|3[0-46-8]|4[0-7]']],
                    [, '(\\d)(\\d{7})', '$1 $2', ['(?:2|90)4|[67]']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[348]|64|79|90']],
                    [, '(\\d{2})(\\d{5,7})', '$1 $2', ['1|28|6[0-35-9]|7[67]|9[2-9]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SR: [
                  ,
                  [, , '(?:[2-5]|[6-8]\\d|90)\\d{5}', , , , , , , [6, 7]],
                  [, , '(?:2[1-3]|3[0-7]|4\\d|5[2-58])\\d{4}', , , , '211234', , , [6]],
                  [, , '(?:6[08]|7[124-7]|8[1-9])\\d{5}', , , , '7412345', , , [7]],
                  [, , '80\\d{5}', , , , '8012345', , , [7]],
                  [, , '90\\d{5}', , , , '9012345', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '56\\d{4}', , , , '561234', , , [6]],
                  'SR',
                  597,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d{2})', '$1-$2-$3', ['56']],
                    [, '(\\d{3})(\\d{3})', '$1-$2', ['[2-5]']],
                    [, '(\\d{3})(\\d{4})', '$1-$2', ['[6-9]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SS: [
                  ,
                  [, , '[19]\\d{8}', , , , , , , [9]],
                  [, , '1[89]\\d{7}', , , , '181234567'],
                  [, , '(?:12|9[1257-9])\\d{7}', , , , '977123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'SS',
                  211,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[19]'], '0$1']],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ST: [
                  ,
                  [, , '(?:22|9\\d)\\d{5}', , , , , , , [7]],
                  [, , '22\\d{5}', , , , '2221234'],
                  [, , '900[5-9]\\d{3}|9(?:0[1-9]|[89]\\d)\\d{4}', , , , '9812345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'ST',
                  239,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{4})', '$1 $2', ['[29]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SV: [
                  ,
                  [, , '[267]\\d{7}|(?:80\\d|900)\\d{4}(?:\\d{4})?', , , , , , , [7, 8, 11]],
                  [, , '2(?:79(?:0[0347-9]|[1-9]\\d)|89(?:0[024589]|[1-9]\\d))\\d{3}|2(?:[1-69]\\d|[78][0-8])\\d{5}', , , , '21234567', , , [8]],
                  [, , '[67]\\d{7}', , , , '70123456', , , [8]],
                  [, , '800\\d{8}|80[01]\\d{4}', , , , '8001234', , , [7, 11]],
                  [, , '900\\d{4}(?:\\d{4})?', , , , '9001234', , , [7, 11]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'SV',
                  503,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['[89]']],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[267]']],
                    [, '(\\d{3})(\\d{4})(\\d{4})', '$1 $2 $3', ['[89]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SX: [
                  ,
                  [, , '7215\\d{6}|(?:[58]\\d\\d|900)\\d{7}', , , , , , , [10], [7]],
                  [, , '7215(?:4[2-8]|8[239]|9[056])\\d{4}', , , , '7215425678', , , , [7]],
                  [, , '7215(?:1[02]|2\\d|5[034679]|8[014-8])\\d{4}', , , , '7215205678', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002123456'],
                  [, , '900[2-9]\\d{6}', , , , '9002123456'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'SX',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '(5\\d{6})$|1',
                  '721$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '721',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SY: [
                  ,
                  [, , '[1-359]\\d{8}|[1-5]\\d{7}', , , , , , , [8, 9], [6, 7]],
                  [, , '21\\d{6,7}|(?:1(?:[14]\\d|[2356])|2[235]|3(?:[13]\\d|4)|4[134]|5[1-3])\\d{6}', , , , '112345678', , , , [6, 7]],
                  [, , '(?:50|9[1-9])\\d{7}', , , , '944567890', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'SY',
                  963,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[1-4]|5[1-3]'], '0$1', , 1],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[59]'], '0$1', , 1]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SZ: [
                  ,
                  [, , '0800\\d{4}|(?:[237]\\d|900)\\d{6}', , , , , , , [8, 9]],
                  [, , '[23][2-5]\\d{6}', , , , '22171234', , , [8]],
                  [, , '7[5-9]\\d{6}', , , , '76123456', , , [8]],
                  [, , '0800\\d{4}', , , , '08001234', , , [8]],
                  [, , '900\\d{6}', , , , '900012345', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '70\\d{6}', , , , '70012345', , , [8]],
                  'SZ',
                  268,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[0237]']],
                    [, '(\\d{5})(\\d{4})', '$1 $2', ['9']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '0800\\d{4}', , , , , , , [8]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TA: [
                  ,
                  [, , '8\\d{3}', , , , , , , [4]],
                  [, , '8\\d{3}', , , , '8999'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'TA',
                  290,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '8',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TC: [
                  ,
                  [, , '(?:[58]\\d\\d|649|900)\\d{7}', , , , , , , [10], [7]],
                  [, , '649(?:266|712|9(?:4\\d|50))\\d{4}', , , , '6497121234', , , , [7]],
                  [, , '649(?:2(?:3[129]|4[1-79])|3\\d\\d|4[34][1-3])\\d{4}', , , , '6492311234', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002345678'],
                  [, , '900[2-9]\\d{6}', , , , '9002345678'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , '649(?:71[01]|966)\\d{4}', , , , '6497101234', , , , [7]],
                  'TC',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-479]\\d{6})$|1',
                  '649$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '649',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TD: [
                  ,
                  [, , '(?:22|30|[689]\\d|77)\\d{6}', , , , , , , [8]],
                  [, , '22(?:[37-9]0|5[0-5]|6[89])\\d{4}', , , , '22501234'],
                  [, , '(?:30|[69]\\d|77|8[56])\\d{6}', , , , '63012345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'TD',
                  235,
                  '00|16',
                  ,
                  ,
                  ,
                  ,
                  ,
                  '00',
                  ,
                  [[, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[236-9]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TG: [
                  ,
                  [, , '[279]\\d{7}', , , , , , , [8]],
                  [, , '2(?:2[2-7]|3[23]|4[45]|55|6[67]|77)\\d{5}', , , , '22212345'],
                  [, , '(?:7[0-29]|9[0-36-9])\\d{6}', , , , '90112345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'TG',
                  228,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[279]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TH: [
                  ,
                  [, , '(?:001800|[2-57]|[689]\\d)\\d{7}|1\\d{7,9}', , , , , , , [8, 9, 10, 13]],
                  [, , '(?:1[0689]|2\\d|3[2-9]|4[2-5]|5[2-6]|7[3-7])\\d{6}', , , , '21234567', , , [8]],
                  [, , '67(?:1[0-8]|2[4-7])\\d{5}|(?:14|6[1-6]|[89]\\d)\\d{7}', , , , '812345678', , , [9]],
                  [, , '(?:001800\\d|1800)\\d{6}', , , , '1800123456', , , [10, 13]],
                  [, , '1900\\d{6}', , , , '1900123456', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '6[08]\\d{7}', , , , '601234567', , , [9]],
                  'TH',
                  66,
                  '00[1-9]',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{4})', '$1 $2 $3', ['2'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[13-9]'], '0$1'],
                    [, '(\\d{4})(\\d{3})(\\d{3})', '$1 $2 $3', ['1']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TJ: [
                  ,
                  [, , '[0-57-9]\\d{8}', , , , , , , [9], [3, 5, 6, 7]],
                  [, , '(?:3(?:1[3-5]|2[245]|3[12]|4[24-7]|5[25]|72)|4(?:46|74|87))\\d{6}', , , , '372123456', , , , [3, 5, 6, 7]],
                  [, , '(?:33[03-9]|4(?:1[18]|4[02-479])|81[1-9])\\d{6}|(?:[09]\\d|1[0-27-9]|2[0-27]|[34]0|5[05]|7[01578]|8[078])\\d{7}', , , , '917123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'TJ',
                  992,
                  '810',
                  ,
                  ,
                  ,
                  ,
                  ,
                  '8~10',
                  ,
                  [
                    [, '(\\d{6})(\\d)(\\d{2})', '$1 $2 $3', ['331', '3317']],
                    [, '(\\d{3})(\\d{2})(\\d{4})', '$1 $2 $3', ['44[02-479]|[34]7']],
                    [, '(\\d{4})(\\d)(\\d{4})', '$1 $2 $3', ['3(?:[1245]|3[12])']],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[0-57-9]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TK: [
                  ,
                  [, , '[2-47]\\d{3,6}', , , , , , , [4, 5, 6, 7]],
                  [, , '(?:2[2-4]|[34]\\d)\\d{2,5}', , , , '3101'],
                  [, , '7[2-4]\\d{2,5}', , , , '7290'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'TK',
                  690,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TL: [
                  ,
                  [, , '7\\d{7}|(?:[2-47]\\d|[89]0)\\d{5}', , , , , , , [7, 8]],
                  [, , '(?:2[1-5]|3[1-9]|4[1-4])\\d{5}', , , , '2112345', , , [7]],
                  [, , '7[2-8]\\d{6}', , , , '77212345', , , [8]],
                  [, , '80\\d{5}', , , , '8012345', , , [7]],
                  [, , '90\\d{5}', , , , '9012345', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , '70\\d{5}', , , , '7012345', , , [7]],
                  [, , , , , , , , , [-1]],
                  'TL',
                  670,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['[2-489]|70']],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['7']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TM: [
                  ,
                  [, , '(?:[1-6]\\d|71)\\d{6}', , , , , , , [8]],
                  [, , '(?:1(?:2\\d|3[1-9])|2(?:22|4[0-35-8])|3(?:22|4[03-9])|4(?:22|3[128]|4\\d|6[15])|5(?:22|5[7-9]|6[014-689]))\\d{5}', , , , '12345678'],
                  [, , '(?:6\\d|71)\\d{6}', , , , '66123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'TM',
                  993,
                  '810',
                  '8',
                  ,
                  ,
                  '8',
                  ,
                  '8~10',
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d{2})(\\d{2})', '$1 $2-$3-$4', ['12'], '(8 $1)'],
                    [, '(\\d{3})(\\d)(\\d{2})(\\d{2})', '$1 $2-$3-$4', ['[1-5]'], '(8 $1)'],
                    [, '(\\d{2})(\\d{6})', '$1 $2', ['[67]'], '8 $1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TN: [
                  ,
                  [, , '[2-57-9]\\d{7}', , , , , , , [8]],
                  [, , '81200\\d{3}|(?:3[0-2]|7\\d)\\d{6}', , , , '30010123'],
                  [, , '3(?:001|[12]40)\\d{4}|(?:(?:[259]\\d|4[0-8])\\d|3(?:1[1-35]|6[0-4]|91))\\d{5}', , , , '20123456'],
                  [, , '8010\\d{4}', , , , '80101234'],
                  [, , '88\\d{6}', , , , '88123456'],
                  [, , '8[12]10\\d{4}', , , , '81101234'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'TN',
                  216,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['[2-57-9]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TO: [
                  ,
                  [, , '(?:0800|(?:[5-8]\\d\\d|999)\\d)\\d{3}|[2-8]\\d{4}', , , , , , , [5, 7]],
                  [, , '(?:2\\d|3[0-8]|4[0-4]|50|6[09]|7[0-24-69]|8[05])\\d{3}', , , , '20123', , , [5]],
                  [, , '(?:5(?:4[0-5]|5[4-6])|6(?:[09]\\d|3[02]|8[15-9])|(?:7\\d|8[46-9])\\d|999)\\d{4}', , , , '7715123', , , [7]],
                  [, , '0800\\d{3}', , , , '0800222', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '55[0-37-9]\\d{4}', , , , '5510123', , , [7]],
                  'TO',
                  676,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3})', '$1-$2', ['[2-4]|50|6[09]|7[0-24-69]|8[05]']],
                    [, '(\\d{4})(\\d{3})', '$1 $2', ['0']],
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['[5-9]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TR: [
                  ,
                  [, , '4\\d{6}|8\\d{11,12}|(?:[2-58]\\d\\d|900)\\d{7}', , , , , , , [7, 10, 12, 13]],
                  [
                    ,
                    ,
                    '(?:2(?:[13][26]|[28][2468]|[45][268]|[67][246])|3(?:[13][28]|[24-6][2468]|[78][02468]|92)|4(?:[16][246]|[23578][2468]|4[26]))\\d{7}',
                    ,
                    ,
                    ,
                    '2123456789',
                    ,
                    ,
                    [10]
                  ],
                  [, , '561(?:011|61\\d)\\d{4}|5(?:0[15-7]|1[06]|24|[34]\\d|5[1-59]|9[46])\\d{7}', , , , '5012345678', , , [10]],
                  [, , '8(?:00\\d{7}(?:\\d{2,3})?|11\\d{7})', , , , '8001234567', , , [10, 12, 13]],
                  [, , '(?:8[89]8|900)\\d{7}', , , , '9001234567', , , [10]],
                  [, , , , , , , , , [-1]],
                  [, , '592(?:21[12]|461)\\d{4}', , , , '5922121234', , , [10]],
                  [, , '850\\d{7}', , , , '8500123456', , , [10]],
                  'TR',
                  90,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d)(\\d{3})', '$1 $2 $3', ['444'], , , 1],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['512|8[01589]|90'], '0$1', , 1],
                    [, '(\\d{3})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['5(?:[0-59]|61)', '5(?:[0-59]|61[06])', '5(?:[0-59]|61[06]1)'], '0$1', , 1],
                    [, '(\\d{3})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[24][1-8]|3[1-9]'], '(0$1)', , 1],
                    [, '(\\d{3})(\\d{3})(\\d{6,7})', '$1 $2 $3', ['80'], '0$1', , 1]
                  ],
                  [
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['512|8[01589]|90'], '0$1', , 1],
                    [, '(\\d{3})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['5(?:[0-59]|61)', '5(?:[0-59]|61[06])', '5(?:[0-59]|61[06]1)'], '0$1', , 1],
                    [, '(\\d{3})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[24][1-8]|3[1-9]'], '(0$1)', , 1],
                    [, '(\\d{3})(\\d{3})(\\d{6,7})', '$1 $2 $3', ['80'], '0$1', , 1]
                  ],
                  [, , '512\\d{7}', , , , '5123456789', , , [10]],
                  ,
                  ,
                  [, , '(?:444|811\\d{3})\\d{4}', , , , , , , [7, 10]],
                  [, , '444\\d{4}', , , , '4441444', , , [7]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TT: [
                  ,
                  [, , '(?:[58]\\d\\d|900)\\d{7}', , , , , , , [10], [7]],
                  [, , '868(?:2(?:01|1[5-9]|[23]\\d|4[0-2])|6(?:0[7-9]|1[02-8]|2[1-9]|[3-69]\\d|7[0-79])|82[124])\\d{4}', , , , '8682211234', , , , [7]],
                  [, , '868(?:(?:2[5-9]|3\\d)\\d|4(?:3[0-6]|[6-9]\\d)|6(?:20|78|8\\d)|7(?:0[1-9]|1[02-9]|[2-9]\\d))\\d{4}', , , , '8682911234', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002345678'],
                  [, , '900[2-9]\\d{6}', , , , '9002345678'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'TT',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-46-8]\\d{6})$|1',
                  '868$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '868',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '868619\\d{4}', , , , '8686191234', , , , [7]]
                ],
                TV: [
                  ,
                  [, , '(?:2|7\\d\\d|90)\\d{4}', , , , , , , [5, 6, 7]],
                  [, , '2[02-9]\\d{3}', , , , '20123', , , [5]],
                  [, , '(?:7[01]\\d|90)\\d{4}', , , , '901234', , , [6, 7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'TV',
                  688,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3})', '$1 $2', ['2']],
                    [, '(\\d{2})(\\d{4})', '$1 $2', ['90']],
                    [, '(\\d{2})(\\d{5})', '$1 $2', ['7']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TW: [
                  ,
                  [, , '[2-689]\\d{8}|7\\d{9,10}|[2-8]\\d{7}|2\\d{6}', , , , , , , [7, 8, 9, 10, 11]],
                  [
                    ,
                    ,
                    '(?:2[2-8]\\d|370|55[01]|7[1-9])\\d{6}|4(?:(?:0(?:0[1-9]|[2-48]\\d)|1[023]\\d)\\d{4,5}|(?:[239]\\d\\d|4(?:0[56]|12|49))\\d{5})|6(?:[01]\\d{7}|4(?:0[56]|12|24|4[09])\\d{4,5})|8(?:(?:2(?:3\\d|4[0-269]|[578]0|66)|36[24-9]|90\\d\\d)\\d{4}|4(?:0[56]|12|24|4[09])\\d{4,5})|(?:2(?:2(?:0\\d\\d|4(?:0[68]|[249]0|3[0-467]|5[0-25-9]|6[0235689]))|(?:3(?:[09]\\d|1[0-4])|(?:4\\d|5[0-49]|6[0-29]|7[0-5])\\d)\\d)|(?:(?:3[2-9]|5[2-8]|6[0-35-79]|8[7-9])\\d\\d|4(?:2(?:[089]\\d|7[1-9])|(?:3[0-4]|[78]\\d|9[01])\\d))\\d)\\d{3}',
                    ,
                    ,
                    ,
                    '221234567',
                    ,
                    ,
                    [8, 9]
                  ],
                  [, , '(?:40001[0-2]|9[0-8]\\d{4})\\d{3}', , , , '912345678', , , [9]],
                  [, , '80[0-79]\\d{6}|800\\d{5}', , , , '800123456', , , [8, 9]],
                  [, , '20(?:[013-9]\\d\\d|2)\\d{4}', , , , '203123456', , , [7, 9]],
                  [, , , , , , , , , [-1]],
                  [, , '99\\d{7}', , , , '990123456', , , [9]],
                  [, , '7010(?:[0-2679]\\d|3[0-7]|8[0-5])\\d{5}|70\\d{8}', , , , '7012345678', , , [10, 11]],
                  'TW',
                  886,
                  '0(?:0[25-79]|19)',
                  '0',
                  '#',
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d)(\\d{4})', '$1 $2 $3', ['202'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[258]0'], '0$1'],
                    [, '(\\d)(\\d{3,4})(\\d{4})', '$1 $2 $3', ['[23568]|4(?:0[02-48]|[1-47-9])|7[1-9]', '[23568]|4(?:0[2-48]|[1-47-9])|(?:400|7)[1-9]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[49]'], '0$1'],
                    [, '(\\d{2})(\\d{4})(\\d{4,5})', '$1 $2 $3', ['7'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '50[0-46-9]\\d{6}', , , , '500123456', , , [9]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TZ: [
                  ,
                  [, , '(?:[25-8]\\d|41|90)\\d{7}', , , , , , , [9]],
                  [, , '2[2-8]\\d{7}', , , , '222345678'],
                  [, , '(?:6[125-9]|7[13-9])\\d{7}', , , , '621234567'],
                  [, , '80[08]\\d{6}', , , , '800123456'],
                  [, , '90\\d{7}', , , , '900123456'],
                  [, , '8(?:40|6[01])\\d{6}', , , , '840123456'],
                  [, , , , , , , , , [-1]],
                  [, , '41\\d{7}', , , , '412345678'],
                  'TZ',
                  255,
                  '00[056]',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{2})(\\d{4})', '$1 $2 $3', ['[89]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[24]'], '0$1'],
                    [, '(\\d{2})(\\d{7})', '$1 $2', ['5']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[67]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '(?:8(?:[04]0|6[01])|90\\d)\\d{6}'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                UA: [
                  ,
                  [, , '[89]\\d{9}|[3-9]\\d{8}', , , , , , , [9, 10], [5, 6, 7]],
                  [, , '(?:3[1-8]|4[13-8]|5[1-7]|6[12459])\\d{7}', , , , '311234567', , , [9], [5, 6, 7]],
                  [, , '790\\d{6}|(?:39|50|6[36-8]|7[1-357]|9[1-9])\\d{7}', , , , '501234567', , , [9]],
                  [, , '800[1-8]\\d{5,6}', , , , '800123456'],
                  [, , '900[239]\\d{5,6}', , , , '900212345'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '89[1-579]\\d{6}', , , , '891234567', , , [9]],
                  'UA',
                  380,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  '0~0',
                  ,
                  [
                    [
                      ,
                      '(\\d{3})(\\d{3})(\\d{3})',
                      '$1 $2 $3',
                      [
                        '6[12][29]|(?:3[1-8]|4[136-8]|5[12457]|6[49])2|(?:56|65)[24]',
                        '6[12][29]|(?:35|4[1378]|5[12457]|6[49])2|(?:56|65)[24]|(?:3[1-46-8]|46)2[013-9]'
                      ],
                      '0$1'
                    ],
                    [
                      ,
                      '(\\d{4})(\\d{5})',
                      '$1 $2',
                      [
                        '3[1-8]|4(?:[1367]|[45][6-9]|8[4-6])|5(?:[1-5]|6[0135689]|7[4-6])|6(?:[12][3-7]|[459])',
                        '3[1-8]|4(?:[1367]|[45][6-9]|8[4-6])|5(?:[1-5]|6(?:[015689]|3[02389])|7[4-6])|6(?:[12][3-7]|[459])'
                      ],
                      '0$1'
                    ],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[3-7]|89|9[1-9]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[89]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                UG: [
                  ,
                  [, , '800\\d{6}|(?:[29]0|[347]\\d)\\d{7}', , , , , , , [9], [5, 6, 7]],
                  [
                    ,
                    ,
                    '20(?:(?:240|30[67])\\d|6(?:00[0-2]|30[0-4]))\\d{3}|(?:20(?:[017]\\d|2[5-9]|3[1-4]|5[0-4]|6[15-9])|[34]\\d{3})\\d{5}',
                    ,
                    ,
                    ,
                    '312345678',
                    ,
                    ,
                    ,
                    [5, 6, 7]
                  ],
                  [, , '72[48]0\\d{5}|7(?:[014-8]\\d|2[067]|36|9[0189])\\d{6}', , , , '712345678'],
                  [, , '800[1-3]\\d{5}', , , , '800123456'],
                  [, , '90[1-3]\\d{6}', , , , '901123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'UG',
                  256,
                  '00[057]',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{4})(\\d{5})', '$1 $2', ['202', '2024'], '0$1'],
                    [, '(\\d{3})(\\d{6})', '$1 $2', ['[27-9]|4(?:6[45]|[7-9])'], '0$1'],
                    [, '(\\d{2})(\\d{7})', '$1 $2', ['[34]'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                US: [
                  ,
                  [, , '[2-9]\\d{9}|3\\d{6}', , , , , , , [10], [7]],
                  [
                    ,
                    ,
                    '(?:3052(?:0[0-8]|[1-9]\\d)|5056(?:[0-35-9]\\d|4[0-68]))\\d{4}|(?:2742|305[3-9]|(?:472|983)[2-47-9]|505[2-57-9])\\d{6}|(?:2(?:0[1-35-9]|1[02-9]|2[03-57-9]|3[1459]|4[08]|5[1-46]|6[0279]|7[0269]|8[13])|3(?:0[1-47-9]|1[02-9]|2[0135-79]|3[0-24679]|4[167]|5[0-2]|6[01349]|8[056])|4(?:0[124-9]|1[02-579]|2[3-5]|3[0245]|4[023578]|58|6[349]|7[0589]|8[04])|5(?:0[1-47-9]|1[0235-8]|20|3[0149]|4[01]|5[179]|6[1-47]|7[0-5]|8[0256])|6(?:0[1-35-9]|1[024-9]|2[03689]|3[016]|4[0156]|5[01679]|6[0-279]|78|8[0-269])|7(?:0[1-46-8]|1[2-9]|2[04-8]|3[0-247]|4[0378]|5[47]|6[02359]|7[0-59]|8[156])|8(?:0[1-68]|1[02-8]|2[0168]|3[0-2589]|4[03578]|5[046-9]|6[02-5]|7[028])|9(?:0[1346-9]|1[02-9]|2[0589]|3[0146-8]|4[01357-9]|5[12469]|7[0-3589]|8[04-69]))[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '2015550123',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [
                    ,
                    ,
                    '(?:3052(?:0[0-8]|[1-9]\\d)|5056(?:[0-35-9]\\d|4[0-68]))\\d{4}|(?:2742|305[3-9]|(?:472|983)[2-47-9]|505[2-57-9])\\d{6}|(?:2(?:0[1-35-9]|1[02-9]|2[03-57-9]|3[1459]|4[08]|5[1-46]|6[0279]|7[0269]|8[13])|3(?:0[1-47-9]|1[02-9]|2[0135-79]|3[0-24679]|4[167]|5[0-2]|6[01349]|8[056])|4(?:0[124-9]|1[02-579]|2[3-5]|3[0245]|4[023578]|58|6[349]|7[0589]|8[04])|5(?:0[1-47-9]|1[0235-8]|20|3[0149]|4[01]|5[179]|6[1-47]|7[0-5]|8[0256])|6(?:0[1-35-9]|1[024-9]|2[03689]|3[016]|4[0156]|5[01679]|6[0-279]|78|8[0-269])|7(?:0[1-46-8]|1[2-9]|2[04-8]|3[0-247]|4[0378]|5[47]|6[02359]|7[0-59]|8[156])|8(?:0[1-68]|1[02-8]|2[0168]|3[0-2589]|4[03578]|5[046-9]|6[02-5]|7[028])|9(?:0[1346-9]|1[02-9]|2[0589]|3[0146-8]|4[01357-9]|5[12469]|7[0-3589]|8[04-69]))[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '2015550123',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002345678'],
                  [, , '900[2-9]\\d{6}', , , , '9002345678'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , '305209\\d{4}', , , , '3052090123', , , , [7]],
                  'US',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '1',
                  ,
                  ,
                  1,
                  [
                    [, '(\\d{3})(\\d{4})', '$1-$2', ['310'], , , 1],
                    [, '(\\d{3})(\\d{4})', '$1-$2', ['[24-9]|3(?:[02-9]|1[1-9])']],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '($1) $2-$3', ['[2-9]'], , , 1]
                  ],
                  [
                    [, '(\\d{3})(\\d{4})', '$1-$2', ['310'], , , 1],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1-$2-$3', ['[2-9]']]
                  ],
                  [, , , , , , , , , [-1]],
                  1,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                UY: [
                  ,
                  [, , '0004\\d{2,9}|[1249]\\d{7}|2\\d{3,4}|(?:[49]\\d|80)\\d{5}', , , , , , , [4, 5, 6, 7, 8, 9, 10, 11, 12, 13]],
                  [, , '(?:1(?:770|9(?:20|[89]7))|(?:2\\d|4[2-7])\\d\\d)\\d{4}', , , , '21231234', , , [8], [7]],
                  [, , '9[1-9]\\d{6}', , , , '94231234', , , [8]],
                  [, , '0004\\d{2,9}|(?:405|80[05])\\d{4}', , , , '8001234', , , [6, 7, 8, 9, 10, 11, 12, 13]],
                  [, , '90[0-8]\\d{4}', , , , '9001234', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'UY',
                  598,
                  '0(?:0|1[3-9]\\d)',
                  '0',
                  ' int. ',
                  ,
                  '0',
                  ,
                  '00',
                  ,
                  [
                    [, '(\\d{4,5})', '$1', ['21']],
                    [, '(\\d{3})(\\d{3,4})', '$1 $2', ['0']],
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['[49]0|8'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['9'], '0$1'],
                    [, '(\\d{4})(\\d{4})', '$1 $2', ['[124]']],
                    [, '(\\d{3})(\\d{3})(\\d{2,4})', '$1 $2 $3', ['0']],
                    [, '(\\d{3})(\\d{3})(\\d{3})(\\d{2,4})', '$1 $2 $3 $4', ['0']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '21\\d{2,3}', , , , '21123', , , [4, 5]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                UZ: [
                  ,
                  [, , '(?:20|33|[5-9]\\d)\\d{7}', , , , , , , [9]],
                  [
                    ,
                    ,
                    '(?:55\\d\\d|6(?:1(?:22|3[124]|4[1-4]|5[1-3578]|64)|2(?:22|3[0-57-9]|41)|5(?:22|3[3-7]|5[024-8])|[69]\\d\\d|7(?:[23]\\d|7[69]))|7(?:0(?:5[4-9]|6[0146]|7[124-6]|9[135-8])|[168]\\d\\d|2(?:22|3[13-57-9]|4[1-3579]|5[14])|3(?:2\\d|3[1578]|4[1-35-7]|5[1-57]|61)|4(?:2\\d|3[1-579]|7[1-79])|5(?:22|5[1-9]|6[1457])|9(?:22|5[1-9])))\\d{5}',
                    ,
                    ,
                    ,
                    '669050123'
                  ],
                  [
                    ,
                    ,
                    '(?:(?:[25]0|33|8[78]|9[0-57-9])\\d{3}|6(?:1(?:2(?:2[01]|98)|35[0-4]|50\\d|61[23]|7(?:[01][017]|4\\d|55|9[5-9]))|2(?:(?:11|7\\d)\\d|2(?:[12]1|9[01379])|5(?:[126]\\d|3[0-4]))|5(?:19[01]|2(?:27|9[26])|(?:30|59|7\\d)\\d)|6(?:2(?:1[5-9]|2[0367]|38|41|52|60)|(?:3[79]|9[0-3])\\d|4(?:56|83)|7(?:[07]\\d|1[017]|3[07]|4[047]|5[057]|67|8[0178]|9[79]))|7(?:2(?:24|3[237]|4[5-9]|7[15-8])|5(?:7[12]|8[0589])|7(?:0\\d|[39][07])|9(?:0\\d|7[079])))|7(?:[07]\\d{3}|2(?:2(?:2[79]|95)|3(?:2[5-9]|6[0-6])|57\\d|7(?:0\\d|1[17]|2[27]|3[37]|44|5[057]|66|88))|3(?:2(?:1[0-6]|21|3[469]|7[159])|(?:33|9[4-6])\\d|5(?:0[0-4]|5[579]|9\\d)|7(?:[0-3579]\\d|4[0467]|6[67]|8[078]))|4(?:2(?:29|5[0257]|6[0-7]|7[1-57])|5(?:1[0-4]|8\\d|9[5-9])|7(?:0\\d|1[024589]|2[0-27]|3[0137]|[46][07]|5[01]|7[5-9]|9[079])|9(?:7[015-9]|[89]\\d))|5(?:112|2(?:0\\d|2[29]|[49]4)|3[1568]\\d|52[6-9]|7(?:0[01578]|1[017]|[23]7|4[047]|[5-7]\\d|8[78]|9[079]))|9(?:22[128]|3(?:2[0-4]|7\\d)|57[02569]|7(?:2[05-9]|3[37]|4\\d|60|7[2579]|87|9[07]))))\\d{4}',
                    ,
                    ,
                    ,
                    '912345678'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'UZ',
                  998,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['[235-9]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                VA: [
                  ,
                  [, , '0\\d{5,10}|3[0-8]\\d{7,10}|55\\d{8}|8\\d{5}(?:\\d{2,4})?|(?:1\\d|39)\\d{7,8}', , , , , , , [6, 7, 8, 9, 10, 11, 12]],
                  [, , '06698\\d{1,6}', , , , '0669812345', , , [6, 7, 8, 9, 10, 11]],
                  [, , '3[1-9]\\d{8}|3[2-9]\\d{7}', , , , '3123456789', , , [9, 10]],
                  [, , '80(?:0\\d{3}|3)\\d{3}', , , , '800123456', , , [6, 9]],
                  [
                    ,
                    ,
                    '(?:0878\\d{3}|89(?:2\\d|3[04]|4(?:[0-4]|[5-9]\\d\\d)|5[0-4]))\\d\\d|(?:1(?:44|6[346])|89(?:38|5[5-9]|9))\\d{6}',
                    ,
                    ,
                    ,
                    '899123456',
                    ,
                    ,
                    [6, 8, 9, 10]
                  ],
                  [, , '84(?:[08]\\d{3}|[17])\\d{3}', , , , '848123456', , , [6, 9]],
                  [, , '1(?:78\\d|99)\\d{6}', , , , '1781234567', , , [9, 10]],
                  [, , '55\\d{8}', , , , '5512345678', , , [10]],
                  'VA',
                  39,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '06698',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '3[2-8]\\d{9,10}', , , , '33101234501', , , [11, 12]]
                ],
                VC: [
                  ,
                  [, , '(?:[58]\\d\\d|784|900)\\d{7}', , , , , , , [10], [7]],
                  [, , '784(?:266|3(?:6[6-9]|7\\d|8[0-6])|4(?:38|5[0-36-8]|8[0-8])|5(?:55|7[0-2]|93)|638|784)\\d{4}', , , , '7842661234', , , , [7]],
                  [, , '784(?:4(?:3[0-5]|5[45]|89|9[0-8])|5(?:2[6-9]|3[0-4])|720)\\d{4}', , , , '7844301234', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002345678'],
                  [, , '900[2-9]\\d{6}', , , , '9002345678'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , '78451[0-2]\\d{4}', , , , '7845101234', , , , [7]],
                  'VC',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-7]\\d{6})$|1',
                  '784$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '784',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                VE: [
                  ,
                  [, , '[68]00\\d{7}|(?:[24]\\d|[59]0)\\d{8}', , , , , , , [10], [7]],
                  [, , '(?:2(?:12|3[457-9]|[467]\\d|[58][1-9]|9[1-6])|[4-6]00)\\d{7}', , , , '2121234567', , , , [7]],
                  [, , '4(?:1[24-8]|2[246])\\d{7}', , , , '4121234567'],
                  [, , '800\\d{7}', , , , '8001234567'],
                  [, , '90[01]\\d{7}', , , , '9001234567'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'VE',
                  58,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{7})', '$1-$2', ['[24-689]'], '0$1', '$CC $1']],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '501\\d{7}', , , , '5010123456', , , , [7]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                VG: [
                  ,
                  [, , '(?:284|[58]\\d\\d|900)\\d{7}', , , , , , , [10], [7]],
                  [, , '284(?:229|4(?:22|9[45])|774|8(?:52|6[459]))\\d{4}', , , , '2842291234', , , , [7]],
                  [, , '284(?:245|3(?:0[0-3]|4[0-7]|68|9[34])|4(?:4[0-6]|68|9[69])|5(?:4[0-7]|68|9[69]))\\d{4}', , , , '2843001234', , , , [7]],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002345678'],
                  [, , '900[2-9]\\d{6}', , , , '9002345678'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'VG',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-578]\\d{6})$|1',
                  '284$1',
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '284',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                VI: [
                  ,
                  [, , '[58]\\d{9}|(?:34|90)0\\d{7}', , , , , , , [10], [7]],
                  [
                    ,
                    ,
                    '340(?:2(?:0\\d|10|2[06-8]|4[49]|77)|3(?:32|44)|4(?:2[23]|44|7[34]|89)|5(?:1[34]|55)|6(?:2[56]|4[23]|77|9[023])|7(?:1[2-57-9]|2[57]|7\\d)|884|998)\\d{4}',
                    ,
                    ,
                    ,
                    '3406421234',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [
                    ,
                    ,
                    '340(?:2(?:0\\d|10|2[06-8]|4[49]|77)|3(?:32|44)|4(?:2[23]|44|7[34]|89)|5(?:1[34]|55)|6(?:2[56]|4[23]|77|9[023])|7(?:1[2-57-9]|2[57]|7\\d)|884|998)\\d{4}',
                    ,
                    ,
                    ,
                    '3406421234',
                    ,
                    ,
                    ,
                    [7]
                  ],
                  [, , '8(?:00|33|44|55|66|77|88)[2-9]\\d{6}', , , , '8002345678'],
                  [, , '900[2-9]\\d{6}', , , , '9002345678'],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}',
                    ,
                    ,
                    ,
                    '5002345678'
                  ],
                  [, , , , , , , , , [-1]],
                  'VI',
                  1,
                  '011',
                  '1',
                  ,
                  ,
                  '([2-9]\\d{6})$|1',
                  '340$1',
                  ,
                  1,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  '340',
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                VN: [
                  ,
                  [, , '[12]\\d{9}|[135-9]\\d{8}|[16]\\d{7}|[16-8]\\d{6}', , , , , , , [7, 8, 9, 10]],
                  [, , '2(?:0[3-9]|1[0-689]|2[0-25-9]|[38][2-9]|4[2-8]|5[124-9]|6[0-39]|7[0-7]|9[0-4679])\\d{7}', , , , '2101234567', , , [10]],
                  [, , '(?:5(?:2[238]|59)|89[6-9]|99[013-9])\\d{6}|(?:3\\d|5[1689]|7[06-9]|8[1-8]|9[0-8])\\d{7}', , , , '912345678', , , [9]],
                  [, , '1800\\d{4,6}|12(?:0[13]|28)\\d{4}', , , , '1800123456', , , [8, 9, 10]],
                  [, , '1900\\d{4,6}', , , , '1900123456', , , [8, 9, 10]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '672\\d{6}', , , , '672012345', , , [9]],
                  'VN',
                  84,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['[17]99'], '0$1', , 1],
                    [, '(\\d{2})(\\d{5})', '$1 $2', ['80'], '0$1', , 1],
                    [, '(\\d{3})(\\d{4,5})', '$1 $2', ['69'], '0$1', , 1],
                    [, '(\\d{4})(\\d{4,6})', '$1 $2', ['1'], , , 1],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['6'], '0$1', , 1],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[357-9]'], '0$1', , 1],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1 $2 $3', ['2[48]'], '0$1', , 1],
                    [, '(\\d{3})(\\d{4})(\\d{3})', '$1 $2 $3', ['2'], '0$1', , 1]
                  ],
                  [
                    [, '(\\d{2})(\\d{5})', '$1 $2', ['80'], '0$1', , 1],
                    [, '(\\d{4})(\\d{4,6})', '$1 $2', ['1'], , , 1],
                    [, '(\\d{2})(\\d{3})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['6'], '0$1', , 1],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[357-9]'], '0$1', , 1],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1 $2 $3', ['2[48]'], '0$1', , 1],
                    [, '(\\d{3})(\\d{4})(\\d{3})', '$1 $2 $3', ['2'], '0$1', , 1]
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '[17]99\\d{4}|69\\d{5,6}', , , , , , , [7, 8]],
                  [, , '(?:[17]99|80\\d)\\d{4}|69\\d{5,6}', , , , '1992000', , , [7, 8]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                VU: [
                  ,
                  [, , '[57-9]\\d{6}|(?:[238]\\d|48)\\d{3}', , , , , , , [5, 7]],
                  [, , '(?:38[0-8]|48[4-9])\\d\\d|(?:2[02-9]|3[4-7]|88)\\d{3}', , , , '22123', , , [5]],
                  [, , '(?:[58]\\d|7[013-7])\\d{5}', , , , '5912345', , , [7]],
                  [, , '81[18]\\d\\d', , , , '81123', , , [5]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '9(?:0[1-9]|1[01])\\d{4}', , , , '9010123', , , [7]],
                  'VU',
                  678,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{4})', '$1 $2', ['[57-9]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '(?:3[03]|900\\d)\\d{3}', , , , '30123'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                WF: [
                  ,
                  [, , '(?:40|72|8\\d{4})\\d{4}|[89]\\d{5}', , , , , , , [6, 9]],
                  [, , '72\\d{4}', , , , '721234', , , [6]],
                  [, , '(?:72|8[23])\\d{4}', , , , '821234', , , [6]],
                  [, , '80[0-5]\\d{6}', , , , '800012345', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '9[23]\\d{4}', , , , '921234', , , [6]],
                  'WF',
                  681,
                  '00',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3', ['[47-9]']],
                    [, '(\\d{3})(\\d{2})(\\d{2})(\\d{2})', '$1 $2 $3 $4', ['8']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '[48]0\\d{4}', , , , '401234', , , [6]]
                ],
                WS: [
                  ,
                  [, , '(?:[2-6]|8\\d{5})\\d{4}|[78]\\d{6}|[68]\\d{5}', , , , , , , [5, 6, 7, 10]],
                  [, , '6[1-9]\\d{3}|(?:[2-5]|60)\\d{4}', , , , '22123', , , [5, 6]],
                  [, , '(?:7[1-35-8]|8(?:[3-7]|9\\d{3}))\\d{5}', , , , '7212345', , , [7, 10]],
                  [, , '800\\d{3}', , , , '800123', , , [6]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'WS',
                  685,
                  '0',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{5})', '$1', ['[2-5]|6[1-9]']],
                    [, '(\\d{3})(\\d{3,7})', '$1 $2', ['[68]']],
                    [, '(\\d{2})(\\d{5})', '$1 $2', ['7']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                XK: [
                  ,
                  [, , '2\\d{7,8}|3\\d{7,11}|(?:4\\d\\d|[89]00)\\d{5}', , , , , , , [8, 9, 10, 11, 12]],
                  [, , '38\\d{6,10}|(?:2[89]|39)(?:0\\d{5,6}|[1-9]\\d{5})', , , , '28012345'],
                  [, , '4[3-9]\\d{6}', , , , '43201234', , , [8]],
                  [, , '800\\d{5}', , , , '80001234', , , [8]],
                  [, , '900\\d{5}', , , , '90001234', , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'XK',
                  383,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{5})', '$1 $2', ['[89]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{3})', '$1 $2 $3', ['[2-4]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['2|39'], '0$1'],
                    [, '(\\d{2})(\\d{7,10})', '$1 $2', ['3'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                YE: [
                  ,
                  [, , '(?:1|7\\d)\\d{7}|[1-7]\\d{6}', , , , , , , [7, 8, 9], [6]],
                  [, , '78[0-7]\\d{4}|17\\d{6}|(?:[12][2-68]|3[2358]|4[2-58]|5[2-6]|6[3-58]|7[24-6])\\d{5}', , , , '1234567', , , [7, 8], [6]],
                  [, , '7[01378]\\d{7}', , , , '712345678', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  'YE',
                  967,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{3,4})', '$1 $2 $3', ['[1-6]|7(?:[24-6]|8[0-7])'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['7'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                YT: [
                  ,
                  [, , '7093\\d{5}|(?:80|9\\d)\\d{7}|(?:26|63)9\\d{6}', , , , , , , [9]],
                  [, , '269(?:0[0-467]|15|5[0-4]|6\\d|[78]0)\\d{4}', , , , '269601234'],
                  [, , '(?:639(?:0[0-79]|1[019]|[267]\\d|3[09]|40|5[05-9]|9[04-79])|7093[5-7])\\d{4}', , , , '639012345'],
                  [, , '80\\d{7}', , , , '801234567'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '9(?:(?:39|47)8[01]|769\\d)\\d{4}', , , , '939801234'],
                  'YT',
                  262,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ZA: [
                  ,
                  [, , '[1-79]\\d{8}|8\\d{4,9}', , , , , , , [5, 6, 7, 8, 9, 10]],
                  [, , '(?:2(?:0330|4302)|52087)0\\d{3}|(?:1[0-8]|2[1-378]|3[1-69]|4\\d|5[1346-8])\\d{7}', , , , '101234567', , , [9]],
                  [
                    ,
                    ,
                    '(?:1(?:3492[0-25]|4495[0235]|549(?:20|5[01]))|4[34]492[01])\\d{3}|8[1-4]\\d{3,7}|(?:2[27]|47|54)4950\\d{3}|(?:1(?:049[2-4]|9[12]\\d\\d)|(?:50[0-2]|6\\d\\d|7(?:[0-46-9]\\d|5[0-4]))\\d\\d|8(?:5\\d{3}|7(?:08[67]|158|28[5-9]|310)))\\d{4}|(?:1[6-8]|28|3[2-69]|4[025689]|5[36-8])4920\\d{3}|(?:12|[2-5]1)492\\d{4}',
                    ,
                    ,
                    ,
                    '711234567',
                    ,
                    ,
                    [5, 6, 7, 8, 9]
                  ],
                  [, , '80\\d{7}', , , , '801234567', , , [9]],
                  [, , '(?:86[2-9]|9[0-2]\\d)\\d{6}', , , , '862345678', , , [9]],
                  [, , '860\\d{6}', , , , '860123456', , , [9]],
                  [, , , , , , , , , [-1]],
                  [, , '87(?:08[0-589]|15[0-79]|28[0-4]|31[1-9])\\d{4}|87(?:[02][0-79]|1[0-46-9]|3[02-9]|[4-9]\\d)\\d{5}', , , , '871234567', , , [9]],
                  'ZA',
                  27,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{3,4})', '$1 $2', ['8[1-4]'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{2,3})', '$1 $2 $3', ['8[1-4]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['860'], '0$1'],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['[1-9]'], '0$1'],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['8'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '861\\d{6,7}', , , , '861123456', , , [9, 10]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ZM: [
                  ,
                  [, , '800\\d{6}|(?:21|[579]\\d|63)\\d{7}', , , , , , , [9], [6]],
                  [, , '21[1-8]\\d{6}', , , , '211234567', , , , [6]],
                  [, , '(?:[59][5-8]|7[5-9])\\d{7}', , , , '955123456'],
                  [, , '800\\d{6}', , , , '800123456'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '63\\d{7}', , , , '630123456'],
                  'ZM',
                  260,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3})', '$1 $2', ['[1-9]']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[28]'], '0$1'],
                    [, '(\\d{2})(\\d{7})', '$1 $2', ['[579]'], '0$1']
                  ],
                  [
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[28]'], '0$1'],
                    [, '(\\d{2})(\\d{7})', '$1 $2', ['[579]'], '0$1']
                  ],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ZW: [
                  ,
                  [
                    ,
                    ,
                    '2(?:[0-57-9]\\d{6,8}|6[0-24-9]\\d{6,7})|[38]\\d{9}|[35-8]\\d{8}|[3-6]\\d{7}|[1-689]\\d{6}|[1-3569]\\d{5}|[1356]\\d{4}',
                    ,
                    ,
                    ,
                    ,
                    ,
                    ,
                    [5, 6, 7, 8, 9, 10],
                    [3, 4]
                  ],
                  [
                    ,
                    ,
                    '(?:1(?:(?:3\\d|9)\\d|[4-8])|2(?:(?:(?:0(?:2[014]|5)|(?:2[0157]|31|84|9)\\d\\d|[56](?:[14]\\d\\d|20)|7(?:[089]|2[03]|[35]\\d\\d))\\d|4(?:2\\d\\d|8))\\d|1(?:2|[39]\\d{4}))|3(?:(?:123|(?:29\\d|92)\\d)\\d\\d|7(?:[19]|[56]\\d))|5(?:0|1[2-478]|26|[37]2|4(?:2\\d{3}|83)|5(?:25\\d\\d|[78])|[689]\\d)|6(?:(?:[16-8]21|28|52[013])\\d\\d|[39])|8(?:[1349]28|523)\\d\\d)\\d{3}|(?:4\\d\\d|9[2-9])\\d{4,5}|(?:(?:2(?:(?:(?:0|8[146])\\d|7[1-7])\\d|2(?:[278]\\d|92)|58(?:2\\d|3))|3(?:[26]|9\\d{3})|5(?:4\\d|5)\\d\\d)\\d|6(?:(?:(?:[0-246]|[78]\\d)\\d|37)\\d|5[2-8]))\\d\\d|(?:2(?:[569]\\d|8[2-57-9])|3(?:[013-59]\\d|8[37])|6[89]8)\\d{3}',
                    ,
                    ,
                    ,
                    '1312345',
                    ,
                    ,
                    ,
                    [3, 4]
                  ],
                  [, , '7(?:[1278]\\d|3[1-9])\\d{6}', , , , '712345678', , , [9]],
                  [, , '80(?:[01]\\d|20|8[0-8])\\d{3}', , , , '8001234', , , [7]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '86(?:1[12]|22|30|44|55|77|8[368])\\d{6}', , , , '8686123456', , , [10]],
                  'ZW',
                  263,
                  '00',
                  '0',
                  ,
                  ,
                  '0',
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{3})(\\d{3,5})', '$1 $2', ['2(?:0[45]|2[278]|[49]8)|3(?:[09]8|17)|6(?:[29]8|37|75)|[23][78]|(?:33|5[15]|6[68])[78]'], '0$1'],
                    [, '(\\d)(\\d{3})(\\d{2,4})', '$1 $2 $3', ['[49]'], '0$1'],
                    [, '(\\d{3})(\\d{4})', '$1 $2', ['80'], '0$1'],
                    [
                      ,
                      '(\\d{2})(\\d{7})',
                      '$1 $2',
                      ['24|8[13-59]|(?:2[05-79]|39|5[45]|6[15-8])2', '2(?:02[014]|4|[56]20|[79]2)|392|5(?:42|525)|6(?:[16-8]21|52[013])|8[13-59]'],
                      '(0$1)'
                    ],
                    [, '(\\d{2})(\\d{3})(\\d{4})', '$1 $2 $3', ['7'], '0$1'],
                    [
                      ,
                      '(\\d{3})(\\d{3})(\\d{3,4})',
                      '$1 $2 $3',
                      ['2(?:1[39]|2[0157]|[378]|[56][14])|3(?:12|29)', '2(?:1[39]|2[0157]|[378]|[56][14])|3(?:123|29)'],
                      '0$1'
                    ],
                    [, '(\\d{4})(\\d{6})', '$1 $2', ['8'], '0$1'],
                    [
                      ,
                      '(\\d{2})(\\d{3,5})',
                      '$1 $2',
                      [
                        '1|2(?:0[0-36-9]|12|29|[56])|3(?:1[0-689]|[24-6])|5(?:[0236-9]|1[2-4])|6(?:[013-59]|7[0-46-9])|(?:33|55|6[68])[0-69]|(?:29|3[09]|62)[0-79]'
                      ],
                      '0$1'
                    ],
                    [, '(\\d{2})(\\d{3})(\\d{3,4})', '$1 $2 $3', ['29[013-9]|39|54'], '0$1'],
                    [, '(\\d{4})(\\d{3,5})', '$1 $2', ['(?:25|54)8', '258|5483'], '0$1']
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                800: [
                  ,
                  [, , '(?:00|[1-9]\\d)\\d{6}', , , , , , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '(?:00|[1-9]\\d)\\d{6}', , , , '12345678'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  '001',
                  800,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  1,
                  [[, '(\\d{4})(\\d{4})', '$1 $2', ['\\d']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                808: [
                  ,
                  [, , '[1-9]\\d{7}', , , , , , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '[1-9]\\d{7}', , , , '12345678'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  '001',
                  808,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  1,
                  [[, '(\\d{4})(\\d{4})', '$1 $2', ['[1-9]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                870: [
                  ,
                  [, , '7\\d{11}|[235-7]\\d{8}', , , , , , , [9, 12]],
                  [, , , , , , , , , [-1]],
                  [, , '(?:[356]|774[45])\\d{8}|7[6-8]\\d{7}', , , , '301234567'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '2\\d{8}', , , , '201234567', , , [9]],
                  '001',
                  870,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [[, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['[235-7]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                878: [
                  ,
                  [, , '10\\d{10}', , , , , , , [12]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '10\\d{10}', , , , '101234567890'],
                  '001',
                  878,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  1,
                  [[, '(\\d{2})(\\d{5})(\\d{5})', '$1 $2 $3', ['1']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                881: [
                  ,
                  [, , '6\\d{9}|[0-36-9]\\d{8}', , , , , , , [9, 10]],
                  [, , , , , , , , , [-1]],
                  [, , '6\\d{9}|[0-36-9]\\d{8}', , , , '612345678'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  '001',
                  881,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d)(\\d{3})(\\d{5})', '$1 $2 $3', ['[0-37-9]']],
                    [, '(\\d)(\\d{3})(\\d{5,6})', '$1 $2 $3', ['6']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                882: [
                  ,
                  [, , '[13]\\d{6}(?:\\d{2,5})?|[19]\\d{7}|(?:[25]\\d\\d|4)\\d{7}(?:\\d{2})?', , , , , , , [7, 8, 9, 10, 11, 12]],
                  [, , , , , , , , , [-1]],
                  [, , '342\\d{4}|(?:337|49)\\d{6}|(?:3(?:2|47|7\\d{3})|50\\d{3})\\d{7}', , , , '3421234', , , [7, 8, 9, 10, 12]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '1(?:3(?:0[0347]|[13][0139]|2[035]|4[013568]|6[0459]|7[06]|8[15-8]|9[0689])\\d{4}|6\\d{5,10})|(?:345\\d|9[89])\\d{6}|(?:10|2(?:3|85\\d)|3(?:[15]|[69]\\d\\d)|4[15-8]|51)\\d{8}',
                    ,
                    ,
                    ,
                    '390123456789'
                  ],
                  '001',
                  882,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [
                    [, '(\\d{2})(\\d{5})', '$1 $2', ['16|342']],
                    [, '(\\d{2})(\\d{6})', '$1 $2', ['49']],
                    [, '(\\d{2})(\\d{2})(\\d{4})', '$1 $2 $3', ['1[36]|9']],
                    [, '(\\d{2})(\\d{4})(\\d{3})', '$1 $2 $3', ['3[23]']],
                    [, '(\\d{2})(\\d{3,4})(\\d{4})', '$1 $2 $3', ['16']],
                    [, '(\\d{2})(\\d{4})(\\d{4})', '$1 $2 $3', ['10|23|3(?:[15]|4[57])|4|51']],
                    [, '(\\d{3})(\\d{4})(\\d{4})', '$1 $2 $3', ['34']],
                    [, '(\\d{2})(\\d{4,5})(\\d{5})', '$1 $2 $3', ['[1-35]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , '348[57]\\d{7}', , , , '34851234567', , , [11]]
                ],
                883: [
                  ,
                  [, , '(?:[1-4]\\d|51)\\d{6,10}', , , , , , , [8, 9, 10, 11, 12]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '(?:2(?:00\\d\\d|10)|(?:370[1-9]|51\\d0)\\d)\\d{7}|51(?:00\\d{5}|[24-9]0\\d{4,7})|(?:1[0-79]|2[24-689]|3[02-689]|4[0-4])0\\d{5,9}',
                    ,
                    ,
                    ,
                    '510012345'
                  ],
                  '001',
                  883,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  1,
                  [
                    [, '(\\d{3})(\\d{3})(\\d{2,8})', '$1 $2 $3', ['[14]|2[24-689]|3[02-689]|51[24-9]']],
                    [, '(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3', ['510']],
                    [, '(\\d{3})(\\d{3})(\\d{4})', '$1 $2 $3', ['21']],
                    [, '(\\d{4})(\\d{4})(\\d{4})', '$1 $2 $3', ['51[13]']],
                    [, '(\\d{3})(\\d{3})(\\d{3})(\\d{3})', '$1 $2 $3 $4', ['[235]']]
                  ],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                888: [
                  ,
                  [, , '\\d{11}', , , , , , , [11]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  '001',
                  888,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  1,
                  [[, '(\\d{3})(\\d{3})(\\d{5})', '$1 $2 $3']],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , '\\d{11}', , , , '12345678901'],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ],
                979: [
                  ,
                  [, , '[1359]\\d{8}', , , , , , , [9], [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , '[1359]\\d{8}', , , , '123456789', , , , [8]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  '001',
                  979,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  1,
                  [[, '(\\d)(\\d{4})(\\d{4})', '$1 $2 $3', ['[1359]']]],
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  [, , , , , , , , , [-1]]
                ]
              }; /*

 Copyright (C) 2010 The Libphonenumber Authors.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

 http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.
*/
              i18n.phonenumbers.PhoneNumberUtil = function () {
                this.regionToMetadataMap = {};
              };
              goog.addSingletonGetter(i18n.phonenumbers.PhoneNumberUtil);
              i18n.phonenumbers.Error = {
                INVALID_COUNTRY_CODE: 'Invalid country calling code',
                NOT_A_NUMBER: 'The string supplied did not seem to be a phone number',
                TOO_SHORT_AFTER_IDD: 'Phone number too short after IDD',
                TOO_SHORT_NSN: 'The string supplied is too short to be a phone number',
                TOO_LONG: 'The string supplied is too long to be a phone number'
              };
              i18n.phonenumbers.PhoneNumberUtil.NANPA_COUNTRY_CODE_ = 1;
              i18n.phonenumbers.PhoneNumberUtil.MIN_LENGTH_FOR_NSN_ = 2;
              i18n.phonenumbers.PhoneNumberUtil.MAX_LENGTH_FOR_NSN_ = 17;
              i18n.phonenumbers.PhoneNumberUtil.MAX_LENGTH_COUNTRY_CODE_ = 3;
              i18n.phonenumbers.PhoneNumberUtil.MAX_INPUT_STRING_LENGTH_ = 250;
              i18n.phonenumbers.PhoneNumberUtil.UNKNOWN_REGION_ = 'ZZ';
              i18n.phonenumbers.PhoneNumberUtil.MOBILE_TOKEN_MAPPINGS_ = { 54: '9' };
              i18n.phonenumbers.PhoneNumberUtil.GEO_MOBILE_COUNTRIES_ = [52, 54, 55];
              i18n.phonenumbers.PhoneNumberUtil.PLUS_SIGN = '+';
              i18n.phonenumbers.PhoneNumberUtil.STAR_SIGN_ = '*';
              i18n.phonenumbers.PhoneNumberUtil.RFC3966_EXTN_PREFIX_ = ';ext=';
              i18n.phonenumbers.PhoneNumberUtil.RFC3966_PREFIX_ = 'tel:';
              i18n.phonenumbers.PhoneNumberUtil.RFC3966_PHONE_CONTEXT_ = ';phone-context=';
              i18n.phonenumbers.PhoneNumberUtil.RFC3966_ISDN_SUBADDRESS_ = ';isub=';
              i18n.phonenumbers.PhoneNumberUtil.DIGIT_MAPPINGS = {
                0: '0',
                1: '1',
                2: '2',
                3: '3',
                4: '4',
                5: '5',
                6: '6',
                7: '7',
                8: '8',
                9: '9',
                '\uff10': '0',
                '\uff11': '1',
                '\uff12': '2',
                '\uff13': '3',
                '\uff14': '4',
                '\uff15': '5',
                '\uff16': '6',
                '\uff17': '7',
                '\uff18': '8',
                '\uff19': '9',
                '\u0660': '0',
                '\u0661': '1',
                '\u0662': '2',
                '\u0663': '3',
                '\u0664': '4',
                '\u0665': '5',
                '\u0666': '6',
                '\u0667': '7',
                '\u0668': '8',
                '\u0669': '9',
                '\u06f0': '0',
                '\u06f1': '1',
                '\u06f2': '2',
                '\u06f3': '3',
                '\u06f4': '4',
                '\u06f5': '5',
                '\u06f6': '6',
                '\u06f7': '7',
                '\u06f8': '8',
                '\u06f9': '9'
              };
              i18n.phonenumbers.PhoneNumberUtil.DIALLABLE_CHAR_MAPPINGS_ = {
                0: '0',
                1: '1',
                2: '2',
                3: '3',
                4: '4',
                5: '5',
                6: '6',
                7: '7',
                8: '8',
                9: '9',
                '+': i18n.phonenumbers.PhoneNumberUtil.PLUS_SIGN,
                '*': '*',
                '#': '#'
              };
              i18n.phonenumbers.PhoneNumberUtil.ALPHA_MAPPINGS_ = {
                A: '2',
                B: '2',
                C: '2',
                D: '3',
                E: '3',
                F: '3',
                G: '4',
                H: '4',
                I: '4',
                J: '5',
                K: '5',
                L: '5',
                M: '6',
                N: '6',
                O: '6',
                P: '7',
                Q: '7',
                R: '7',
                S: '7',
                T: '8',
                U: '8',
                V: '8',
                W: '9',
                X: '9',
                Y: '9',
                Z: '9'
              };
              i18n.phonenumbers.PhoneNumberUtil.ALL_NORMALIZATION_MAPPINGS_ = {
                0: '0',
                1: '1',
                2: '2',
                3: '3',
                4: '4',
                5: '5',
                6: '6',
                7: '7',
                8: '8',
                9: '9',
                '\uff10': '0',
                '\uff11': '1',
                '\uff12': '2',
                '\uff13': '3',
                '\uff14': '4',
                '\uff15': '5',
                '\uff16': '6',
                '\uff17': '7',
                '\uff18': '8',
                '\uff19': '9',
                '\u0660': '0',
                '\u0661': '1',
                '\u0662': '2',
                '\u0663': '3',
                '\u0664': '4',
                '\u0665': '5',
                '\u0666': '6',
                '\u0667': '7',
                '\u0668': '8',
                '\u0669': '9',
                '\u06f0': '0',
                '\u06f1': '1',
                '\u06f2': '2',
                '\u06f3': '3',
                '\u06f4': '4',
                '\u06f5': '5',
                '\u06f6': '6',
                '\u06f7': '7',
                '\u06f8': '8',
                '\u06f9': '9',
                A: '2',
                B: '2',
                C: '2',
                D: '3',
                E: '3',
                F: '3',
                G: '4',
                H: '4',
                I: '4',
                J: '5',
                K: '5',
                L: '5',
                M: '6',
                N: '6',
                O: '6',
                P: '7',
                Q: '7',
                R: '7',
                S: '7',
                T: '8',
                U: '8',
                V: '8',
                W: '9',
                X: '9',
                Y: '9',
                Z: '9'
              };
              i18n.phonenumbers.PhoneNumberUtil.ALL_PLUS_NUMBER_GROUPING_SYMBOLS_ = {
                0: '0',
                1: '1',
                2: '2',
                3: '3',
                4: '4',
                5: '5',
                6: '6',
                7: '7',
                8: '8',
                9: '9',
                A: 'A',
                B: 'B',
                C: 'C',
                D: 'D',
                E: 'E',
                F: 'F',
                G: 'G',
                H: 'H',
                I: 'I',
                J: 'J',
                K: 'K',
                L: 'L',
                M: 'M',
                N: 'N',
                O: 'O',
                P: 'P',
                Q: 'Q',
                R: 'R',
                S: 'S',
                T: 'T',
                U: 'U',
                V: 'V',
                W: 'W',
                X: 'X',
                Y: 'Y',
                Z: 'Z',
                a: 'A',
                b: 'B',
                c: 'C',
                d: 'D',
                e: 'E',
                f: 'F',
                g: 'G',
                h: 'H',
                i: 'I',
                j: 'J',
                k: 'K',
                l: 'L',
                m: 'M',
                n: 'N',
                o: 'O',
                p: 'P',
                q: 'Q',
                r: 'R',
                s: 'S',
                t: 'T',
                u: 'U',
                v: 'V',
                w: 'W',
                x: 'X',
                y: 'Y',
                z: 'Z',
                '-': '-',
                '\uff0d': '-',
                '\u2010': '-',
                '\u2011': '-',
                '\u2012': '-',
                '\u2013': '-',
                '\u2014': '-',
                '\u2015': '-',
                '\u2212': '-',
                '/': '/',
                '\uff0f': '/',
                ' ': ' ',
                '\u3000': ' ',
                '\u2060': ' ',
                '.': '.',
                '\uff0e': '.'
              };
              i18n.phonenumbers.PhoneNumberUtil.SINGLE_INTERNATIONAL_PREFIX_ = /[\d]+(?:[~\u2053\u223C\uFF5E][\d]+)?/;
              i18n.phonenumbers.PhoneNumberUtil.VALID_PUNCTUATION =
                '-x\u2010-\u2015\u2212\u30fc\uff0d-\uff0f \u00a0\u00ad\u200b\u2060\u3000()\uff08\uff09\uff3b\uff3d.\\[\\]/~\u2053\u223c\uff5e';
              i18n.phonenumbers.PhoneNumberUtil.VALID_DIGITS_ = '0-9\uff10-\uff19\u0660-\u0669\u06f0-\u06f9';
              i18n.phonenumbers.PhoneNumberUtil.VALID_ALPHA_ = 'A-Za-z';
              i18n.phonenumbers.PhoneNumberUtil.PLUS_CHARS_ = '+\uff0b';
              i18n.phonenumbers.PhoneNumberUtil.PLUS_CHARS_PATTERN = new RegExp('[' + i18n.phonenumbers.PhoneNumberUtil.PLUS_CHARS_ + ']+');
              i18n.phonenumbers.PhoneNumberUtil.LEADING_PLUS_CHARS_PATTERN = new RegExp('^[' + i18n.phonenumbers.PhoneNumberUtil.PLUS_CHARS_ + ']+');
              i18n.phonenumbers.PhoneNumberUtil.SEPARATOR_PATTERN_ = '[' + i18n.phonenumbers.PhoneNumberUtil.VALID_PUNCTUATION + ']+';
              i18n.phonenumbers.PhoneNumberUtil.CAPTURING_DIGIT_PATTERN = new RegExp('([' + i18n.phonenumbers.PhoneNumberUtil.VALID_DIGITS_ + '])');
              i18n.phonenumbers.PhoneNumberUtil.VALID_START_CHAR_PATTERN_ = new RegExp(
                '[' + i18n.phonenumbers.PhoneNumberUtil.PLUS_CHARS_ + i18n.phonenumbers.PhoneNumberUtil.VALID_DIGITS_ + ']'
              );
              i18n.phonenumbers.PhoneNumberUtil.SECOND_NUMBER_START_PATTERN_ = /[\\\/] *x/;
              i18n.phonenumbers.PhoneNumberUtil.UNWANTED_END_CHAR_PATTERN_ = new RegExp(
                '[^' + i18n.phonenumbers.PhoneNumberUtil.VALID_DIGITS_ + i18n.phonenumbers.PhoneNumberUtil.VALID_ALPHA_ + '#]+$'
              );
              i18n.phonenumbers.PhoneNumberUtil.VALID_ALPHA_PHONE_PATTERN_ = /(?:.*?[A-Za-z]){3}.*/;
              i18n.phonenumbers.PhoneNumberUtil.MIN_LENGTH_PHONE_NUMBER_PATTERN_ =
                '[' + i18n.phonenumbers.PhoneNumberUtil.VALID_DIGITS_ + ']{' + i18n.phonenumbers.PhoneNumberUtil.MIN_LENGTH_FOR_NSN_ + '}';
              i18n.phonenumbers.PhoneNumberUtil.VALID_PHONE_NUMBER_ =
                '[' +
                i18n.phonenumbers.PhoneNumberUtil.PLUS_CHARS_ +
                ']*(?:[' +
                i18n.phonenumbers.PhoneNumberUtil.VALID_PUNCTUATION +
                i18n.phonenumbers.PhoneNumberUtil.STAR_SIGN_ +
                ']*[' +
                i18n.phonenumbers.PhoneNumberUtil.VALID_DIGITS_ +
                ']){3,}[' +
                i18n.phonenumbers.PhoneNumberUtil.VALID_PUNCTUATION +
                i18n.phonenumbers.PhoneNumberUtil.STAR_SIGN_ +
                i18n.phonenumbers.PhoneNumberUtil.VALID_ALPHA_ +
                i18n.phonenumbers.PhoneNumberUtil.VALID_DIGITS_ +
                ']*';
              i18n.phonenumbers.PhoneNumberUtil.DEFAULT_EXTN_PREFIX_ = ' ext. ';
              i18n.phonenumbers.PhoneNumberUtil.RFC3966_VISUAL_SEPARATOR_ = '[\\-\\.\\(\\)]?';
              i18n.phonenumbers.PhoneNumberUtil.RFC3966_PHONE_DIGIT_ =
                '([' + i18n.phonenumbers.PhoneNumberUtil.VALID_DIGITS_ + ']|' + i18n.phonenumbers.PhoneNumberUtil.RFC3966_VISUAL_SEPARATOR_ + ')';
              i18n.phonenumbers.PhoneNumberUtil.RFC3966_GLOBAL_NUMBER_DIGITS_ =
                '^\\' +
                i18n.phonenumbers.PhoneNumberUtil.PLUS_SIGN +
                i18n.phonenumbers.PhoneNumberUtil.RFC3966_PHONE_DIGIT_ +
                '*[' +
                i18n.phonenumbers.PhoneNumberUtil.VALID_DIGITS_ +
                ']' +
                i18n.phonenumbers.PhoneNumberUtil.RFC3966_PHONE_DIGIT_ +
                '*$';
              i18n.phonenumbers.PhoneNumberUtil.RFC3966_GLOBAL_NUMBER_DIGITS_PATTERN_ = new RegExp(
                i18n.phonenumbers.PhoneNumberUtil.RFC3966_GLOBAL_NUMBER_DIGITS_
              );
              i18n.phonenumbers.PhoneNumberUtil.ALPHANUM_ = i18n.phonenumbers.PhoneNumberUtil.VALID_ALPHA_ + i18n.phonenumbers.PhoneNumberUtil.VALID_DIGITS_;
              i18n.phonenumbers.PhoneNumberUtil.RFC3966_DOMAINLABEL_ =
                '[' + i18n.phonenumbers.PhoneNumberUtil.ALPHANUM_ + ']+((\\-)*[' + i18n.phonenumbers.PhoneNumberUtil.ALPHANUM_ + '])*';
              i18n.phonenumbers.PhoneNumberUtil.RFC3966_TOPLABEL_ =
                '[' + i18n.phonenumbers.PhoneNumberUtil.VALID_ALPHA_ + ']+((\\-)*[' + i18n.phonenumbers.PhoneNumberUtil.ALPHANUM_ + '])*';
              i18n.phonenumbers.PhoneNumberUtil.RFC3966_DOMAINNAME_ =
                '^(' + i18n.phonenumbers.PhoneNumberUtil.RFC3966_DOMAINLABEL_ + '\\.)*' + i18n.phonenumbers.PhoneNumberUtil.RFC3966_TOPLABEL_ + '\\.?$';
              i18n.phonenumbers.PhoneNumberUtil.RFC3966_DOMAINNAME_PATTERN_ = new RegExp(i18n.phonenumbers.PhoneNumberUtil.RFC3966_DOMAINNAME_);
              i18n.phonenumbers.PhoneNumberUtil.extnDigits_ = function (a) {
                return '([' + i18n.phonenumbers.PhoneNumberUtil.VALID_DIGITS_ + ']{1,' + a + '})';
              };
              i18n.phonenumbers.PhoneNumberUtil.createExtnPattern_ = function () {
                var a = i18n.phonenumbers.PhoneNumberUtil.RFC3966_EXTN_PREFIX_ + i18n.phonenumbers.PhoneNumberUtil.extnDigits_('20'),
                  b =
                    '[ \u00a0\\t,]*(?:e?xt(?:ensi(?:o\u0301?|\u00f3))?n?|\uff45?\uff58\uff54\uff4e?|\u0434\u043e\u0431|anexo)[:\\.\uff0e]?[ \u00a0\\t,-]*' +
                    i18n.phonenumbers.PhoneNumberUtil.extnDigits_('20') +
                    '#?',
                  c =
                    '[ \u00a0\\t,]*(?:[x\uff58#\uff03~\uff5e]|int|\uff49\uff4e\uff54)[:\\.\uff0e]?[ \u00a0\\t,-]*' +
                    i18n.phonenumbers.PhoneNumberUtil.extnDigits_('9') +
                    '#?',
                  d = '[- ]+' + i18n.phonenumbers.PhoneNumberUtil.extnDigits_('6') + '#',
                  e = '[ \u00a0\\t]*(?:,{2}|;)[:\\.\uff0e]?[ \u00a0\\t,-]*' + i18n.phonenumbers.PhoneNumberUtil.extnDigits_('15') + '#?',
                  f = '[ \u00a0\\t]*(?:,)+[:\\.\uff0e]?[ \u00a0\\t,-]*' + i18n.phonenumbers.PhoneNumberUtil.extnDigits_('9') + '#?';
                return a + '|' + b + '|' + c + '|' + d + '|' + e + '|' + f;
              };
              i18n.phonenumbers.PhoneNumberUtil.EXTN_PATTERN_ = new RegExp('(?:' + i18n.phonenumbers.PhoneNumberUtil.createExtnPattern_() + ')$', 'i');
              i18n.phonenumbers.PhoneNumberUtil.VALID_PHONE_NUMBER_PATTERN_ = new RegExp(
                '^' +
                  i18n.phonenumbers.PhoneNumberUtil.MIN_LENGTH_PHONE_NUMBER_PATTERN_ +
                  '$|^' +
                  i18n.phonenumbers.PhoneNumberUtil.VALID_PHONE_NUMBER_ +
                  '(?:' +
                  i18n.phonenumbers.PhoneNumberUtil.createExtnPattern_() +
                  ')?$',
                'i'
              );
              i18n.phonenumbers.PhoneNumberUtil.NON_DIGITS_PATTERN_ = /\D+/;
              i18n.phonenumbers.PhoneNumberUtil.FIRST_GROUP_PATTERN_ = /(\$\d)/;
              i18n.phonenumbers.PhoneNumberUtil.NP_PATTERN_ = /\$NP/;
              i18n.phonenumbers.PhoneNumberUtil.FG_PATTERN_ = /\$FG/;
              i18n.phonenumbers.PhoneNumberUtil.CC_PATTERN_ = /\$CC/;
              i18n.phonenumbers.PhoneNumberUtil.FIRST_GROUP_ONLY_PREFIX_PATTERN_ = /^\(?\$1\)?$/;
              i18n.phonenumbers.PhoneNumberUtil.REGION_CODE_FOR_NON_GEO_ENTITY = '001';
              i18n.phonenumbers.PhoneNumberFormat = { E164: 0, INTERNATIONAL: 1, NATIONAL: 2, RFC3966: 3 };
              i18n.phonenumbers.PhoneNumberType = {
                FIXED_LINE: 0,
                MOBILE: 1,
                FIXED_LINE_OR_MOBILE: 2,
                TOLL_FREE: 3,
                PREMIUM_RATE: 4,
                SHARED_COST: 5,
                VOIP: 6,
                PERSONAL_NUMBER: 7,
                PAGER: 8,
                UAN: 9,
                VOICEMAIL: 10,
                UNKNOWN: -1
              };
              i18n.phonenumbers.PhoneNumberUtil.MatchType = { NOT_A_NUMBER: 0, NO_MATCH: 1, SHORT_NSN_MATCH: 2, NSN_MATCH: 3, EXACT_MATCH: 4 };
              i18n.phonenumbers.PhoneNumberUtil.ValidationResult = {
                IS_POSSIBLE: 0,
                IS_POSSIBLE_LOCAL_ONLY: 4,
                INVALID_COUNTRY_CODE: 1,
                TOO_SHORT: 2,
                INVALID_LENGTH: 5,
                TOO_LONG: 3
              };
              i18n.phonenumbers.PhoneNumberUtil.extractPossibleNumber = function (a) {
                var b = a.search(i18n.phonenumbers.PhoneNumberUtil.VALID_START_CHAR_PATTERN_);
                0 <= b
                  ? ((a = a.substring(b)),
                    (a = a.replace(i18n.phonenumbers.PhoneNumberUtil.UNWANTED_END_CHAR_PATTERN_, '')),
                    (b = a.search(i18n.phonenumbers.PhoneNumberUtil.SECOND_NUMBER_START_PATTERN_)),
                    0 <= b && (a = a.substring(0, b)))
                  : (a = '');
                return a;
              };
              i18n.phonenumbers.PhoneNumberUtil.isViablePhoneNumber = function (a) {
                return a.length < i18n.phonenumbers.PhoneNumberUtil.MIN_LENGTH_FOR_NSN_
                  ? !1
                  : i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(i18n.phonenumbers.PhoneNumberUtil.VALID_PHONE_NUMBER_PATTERN_, a);
              };
              i18n.phonenumbers.PhoneNumberUtil.normalize = function (a) {
                return i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(i18n.phonenumbers.PhoneNumberUtil.VALID_ALPHA_PHONE_PATTERN_, a)
                  ? i18n.phonenumbers.PhoneNumberUtil.normalizeHelper_(a, i18n.phonenumbers.PhoneNumberUtil.ALL_NORMALIZATION_MAPPINGS_, !0)
                  : i18n.phonenumbers.PhoneNumberUtil.normalizeDigitsOnly(a);
              };
              i18n.phonenumbers.PhoneNumberUtil.normalizeSB_ = function (a) {
                var b = i18n.phonenumbers.PhoneNumberUtil.normalize(a.toString());
                a.clear();
                a.append(b);
              };
              i18n.phonenumbers.PhoneNumberUtil.normalizeDigitsOnly = function (a) {
                return i18n.phonenumbers.PhoneNumberUtil.normalizeHelper_(a, i18n.phonenumbers.PhoneNumberUtil.DIGIT_MAPPINGS, !0);
              };
              i18n.phonenumbers.PhoneNumberUtil.normalizeDiallableCharsOnly = function (a) {
                return i18n.phonenumbers.PhoneNumberUtil.normalizeHelper_(a, i18n.phonenumbers.PhoneNumberUtil.DIALLABLE_CHAR_MAPPINGS_, !0);
              };
              i18n.phonenumbers.PhoneNumberUtil.convertAlphaCharactersInNumber = function (a) {
                return i18n.phonenumbers.PhoneNumberUtil.normalizeHelper_(a, i18n.phonenumbers.PhoneNumberUtil.ALL_NORMALIZATION_MAPPINGS_, !1);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getLengthOfGeographicalAreaCode = function (a) {
                var b = this.getMetadataForRegion(this.getRegionCodeForNumber(a));
                return null != b && (b.hasNationalPrefix() || a.hasItalianLeadingZero()) && this.isNumberGeographical(a)
                  ? this.getLengthOfNationalDestinationCode(a)
                  : 0;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getLengthOfNationalDestinationCode = function (a) {
                if (a.hasExtension()) {
                  var b = a.clone();
                  b.clearExtension();
                } else b = a;
                b = this.format(b, i18n.phonenumbers.PhoneNumberFormat.INTERNATIONAL).split(i18n.phonenumbers.PhoneNumberUtil.NON_DIGITS_PATTERN_);
                0 == b[0].length && b.shift();
                return 2 >= b.length
                  ? 0
                  : this.getNumberType(a) == i18n.phonenumbers.PhoneNumberType.MOBILE &&
                      ((a = i18n.phonenumbers.PhoneNumberUtil.getCountryMobileToken(a.getCountryCodeOrDefault())), '' != a)
                    ? b[2].length + a.length
                    : b[1].length;
              };
              i18n.phonenumbers.PhoneNumberUtil.getCountryMobileToken = function (a) {
                return i18n.phonenumbers.PhoneNumberUtil.MOBILE_TOKEN_MAPPINGS_[a] || '';
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getSupportedRegions = function () {
                return Object.keys(i18n.phonenumbers.metadata.countryToMetadata).filter(function (a) {
                  return isNaN(a);
                });
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getSupportedGlobalNetworkCallingCodes = function () {
                return Object.keys(i18n.phonenumbers.metadata.countryToMetadata)
                  .filter(function (a) {
                    return !isNaN(a);
                  })
                  .map(function (a) {
                    return parseInt(a, 10);
                  });
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getSupportedCallingCodes = function () {
                var a = Object.keys(i18n.phonenumbers.metadata.countryCodeToRegionCodeMap);
                return [].concat(
                  $jscomp.arrayFromIterable(this.getSupportedGlobalNetworkCallingCodes()),
                  $jscomp.arrayFromIterable(
                    a.map(function (b) {
                      return parseInt(b, 10);
                    })
                  )
                );
              };
              i18n.phonenumbers.PhoneNumberUtil.descHasPossibleNumberData_ = function (a) {
                return null != a && (1 != a.possibleLengthCount() || -1 != a.possibleLengthArray()[0]);
              };
              i18n.phonenumbers.PhoneNumberUtil.descHasData_ = function (a) {
                return null != a && (a.hasExampleNumber() || i18n.phonenumbers.PhoneNumberUtil.descHasPossibleNumberData_(a) || a.hasNationalNumberPattern());
              };
              i18n.phonenumbers.PhoneNumberUtil.getSupportedTypesForMetadata_ = function (a) {
                var b = [];
                goog.object.forEach(i18n.phonenumbers.PhoneNumberType, function (c) {
                  if (c != i18n.phonenumbers.PhoneNumberType.FIXED_LINE_OR_MOBILE && c != i18n.phonenumbers.PhoneNumberType.UNKNOWN) {
                    var d = i18n.phonenumbers.PhoneNumberUtil.getNumberDescByType_(a, c);
                    i18n.phonenumbers.PhoneNumberUtil.descHasData_(d) && b.push(c);
                  }
                });
                return b;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getSupportedTypesForRegion = function (a) {
                return this.isValidRegionCode_(a) ? i18n.phonenumbers.PhoneNumberUtil.getSupportedTypesForMetadata_(this.getMetadataForRegion(a)) : [];
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getSupportedTypesForNonGeoEntity = function (a) {
                a = this.getMetadataForNonGeographicalRegion(a);
                return null == a ? [] : i18n.phonenumbers.PhoneNumberUtil.getSupportedTypesForMetadata_(a);
              };
              i18n.phonenumbers.PhoneNumberUtil.normalizeHelper_ = function (a, b, c) {
                for (var d = new goog.string.StringBuffer(), e, f, g = a.length, h = 0; h < g; ++h)
                  ((e = a.charAt(h)), (f = b[e.toUpperCase()]), null != f ? d.append(f) : c || d.append(e));
                return d.toString();
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.formattingRuleHasFirstGroupOnly = function (a) {
                return 0 == a.length || i18n.phonenumbers.PhoneNumberUtil.FIRST_GROUP_ONLY_PREFIX_PATTERN_.test(a);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isNumberGeographical = function (a) {
                var b = this.getNumberType(a);
                return (
                  b == i18n.phonenumbers.PhoneNumberType.FIXED_LINE ||
                  b == i18n.phonenumbers.PhoneNumberType.FIXED_LINE_OR_MOBILE ||
                  (i18n.phonenumbers.PhoneNumberUtil.GEO_MOBILE_COUNTRIES_.includes(a.getCountryCodeOrDefault()) &&
                    b == i18n.phonenumbers.PhoneNumberType.MOBILE)
                );
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isValidRegionCode_ = function (a) {
                return null != a && isNaN(a) && a.toUpperCase() in i18n.phonenumbers.metadata.countryToMetadata;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.hasValidCountryCallingCode_ = function (a) {
                return a in i18n.phonenumbers.metadata.countryCodeToRegionCodeMap;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.format = function (a, b) {
                if (0 == a.getNationalNumber() && a.hasRawInput()) {
                  var c = a.getRawInputOrDefault();
                  if (0 < c.length) return c;
                }
                c = a.getCountryCodeOrDefault();
                var d = this.getNationalSignificantNumber(a);
                if (b == i18n.phonenumbers.PhoneNumberFormat.E164)
                  return this.prefixNumberWithCountryCallingCode_(c, i18n.phonenumbers.PhoneNumberFormat.E164, d, '');
                if (!this.hasValidCountryCallingCode_(c)) return d;
                var e = this.getRegionCodeForCountryCode(c);
                e = this.getMetadataForRegionOrCallingCode_(c, e);
                a = this.maybeGetFormattedExtension_(a, e, b);
                d = this.formatNsn_(d, e, b);
                return this.prefixNumberWithCountryCallingCode_(c, b, d, a);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.formatByPattern = function (a, b, c) {
                var d = a.getCountryCodeOrDefault(),
                  e = this.getNationalSignificantNumber(a);
                if (!this.hasValidCountryCallingCode_(d)) return e;
                var f = this.getRegionCodeForCountryCode(d);
                f = this.getMetadataForRegionOrCallingCode_(d, f);
                var g = this.chooseFormattingPatternForNumber_(c, e);
                if (null != g) {
                  c = g.clone();
                  g = g.getNationalPrefixFormattingRuleOrDefault();
                  if (0 < g.length) {
                    var h = f.getNationalPrefixOrDefault();
                    0 < h.length
                      ? ((g = g.replace(i18n.phonenumbers.PhoneNumberUtil.NP_PATTERN_, h).replace(i18n.phonenumbers.PhoneNumberUtil.FG_PATTERN_, '$1')),
                        c.setNationalPrefixFormattingRule(g))
                      : c.clearNationalPrefixFormattingRule();
                  }
                  e = this.formatNsnUsingPattern_(e, c, b);
                }
                a = this.maybeGetFormattedExtension_(a, f, b);
                return this.prefixNumberWithCountryCallingCode_(d, b, e, a);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.formatNationalNumberWithCarrierCode = function (a, b) {
                var c = a.getCountryCodeOrDefault(),
                  d = this.getNationalSignificantNumber(a);
                if (!this.hasValidCountryCallingCode_(c)) return d;
                var e = this.getRegionCodeForCountryCode(c);
                e = this.getMetadataForRegionOrCallingCode_(c, e);
                a = this.maybeGetFormattedExtension_(a, e, i18n.phonenumbers.PhoneNumberFormat.NATIONAL);
                b = this.formatNsn_(d, e, i18n.phonenumbers.PhoneNumberFormat.NATIONAL, b);
                return this.prefixNumberWithCountryCallingCode_(c, i18n.phonenumbers.PhoneNumberFormat.NATIONAL, b, a);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getMetadataForRegionOrCallingCode_ = function (a, b) {
                return i18n.phonenumbers.PhoneNumberUtil.REGION_CODE_FOR_NON_GEO_ENTITY == b
                  ? this.getMetadataForNonGeographicalRegion(a)
                  : this.getMetadataForRegion(b);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.formatNationalNumberWithPreferredCarrierCode = function (a, b) {
                return this.formatNationalNumberWithCarrierCode(
                  a,
                  0 < a.getPreferredDomesticCarrierCodeOrDefault().length ? a.getPreferredDomesticCarrierCodeOrDefault() : b
                );
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.formatNumberForMobileDialing = function (a, b, c) {
                var d = a.getCountryCodeOrDefault();
                if (!this.hasValidCountryCallingCode_(d)) return a.hasRawInput() ? a.getRawInputOrDefault() : '';
                var e = '';
                a = a.clone();
                a.clearExtension();
                var f = this.getRegionCodeForCountryCode(d),
                  g = this.getNumberType(a),
                  h = g != i18n.phonenumbers.PhoneNumberType.UNKNOWN;
                if (b == f)
                  ((e =
                    g == i18n.phonenumbers.PhoneNumberType.FIXED_LINE ||
                    g == i18n.phonenumbers.PhoneNumberType.MOBILE ||
                    g == i18n.phonenumbers.PhoneNumberType.FIXED_LINE_OR_MOBILE),
                    'BR' == f && e
                      ? (e = 0 < a.getPreferredDomesticCarrierCodeOrDefault().length ? this.formatNationalNumberWithPreferredCarrierCode(a, '') : '')
                      : d == i18n.phonenumbers.PhoneNumberUtil.NANPA_COUNTRY_CODE_
                        ? ((b = this.getMetadataForRegion(b)),
                          (e =
                            this.canBeInternationallyDialled(a) &&
                            this.testNumberLength_(this.getNationalSignificantNumber(a), b) != i18n.phonenumbers.PhoneNumberUtil.ValidationResult.TOO_SHORT
                              ? this.format(a, i18n.phonenumbers.PhoneNumberFormat.INTERNATIONAL)
                              : this.format(a, i18n.phonenumbers.PhoneNumberFormat.NATIONAL)))
                        : (e =
                            (f == i18n.phonenumbers.PhoneNumberUtil.REGION_CODE_FOR_NON_GEO_ENTITY || (('MX' == f || 'CL' == f || 'UZ' == f) && e)) &&
                            this.canBeInternationallyDialled(a)
                              ? this.format(a, i18n.phonenumbers.PhoneNumberFormat.INTERNATIONAL)
                              : this.format(a, i18n.phonenumbers.PhoneNumberFormat.NATIONAL)));
                else if (h && this.canBeInternationallyDialled(a))
                  return c ? this.format(a, i18n.phonenumbers.PhoneNumberFormat.INTERNATIONAL) : this.format(a, i18n.phonenumbers.PhoneNumberFormat.E164);
                return c ? e : i18n.phonenumbers.PhoneNumberUtil.normalizeDiallableCharsOnly(e);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.formatOutOfCountryCallingNumber = function (a, b) {
                if (!this.isValidRegionCode_(b)) return this.format(a, i18n.phonenumbers.PhoneNumberFormat.INTERNATIONAL);
                var c = a.getCountryCodeOrDefault(),
                  d = this.getNationalSignificantNumber(a);
                if (!this.hasValidCountryCallingCode_(c)) return d;
                if (c == i18n.phonenumbers.PhoneNumberUtil.NANPA_COUNTRY_CODE_) {
                  if (this.isNANPACountry(b)) return c + ' ' + this.format(a, i18n.phonenumbers.PhoneNumberFormat.NATIONAL);
                } else if (c == this.getCountryCodeForValidRegion_(b)) return this.format(a, i18n.phonenumbers.PhoneNumberFormat.NATIONAL);
                var e = this.getMetadataForRegion(b),
                  f = e.getInternationalPrefixOrDefault();
                b = '';
                e.hasPreferredInternationalPrefix()
                  ? (b = e.getPreferredInternationalPrefixOrDefault())
                  : i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(i18n.phonenumbers.PhoneNumberUtil.SINGLE_INTERNATIONAL_PREFIX_, f) && (b = f);
                e = this.getRegionCodeForCountryCode(c);
                e = this.getMetadataForRegionOrCallingCode_(c, e);
                d = this.formatNsn_(d, e, i18n.phonenumbers.PhoneNumberFormat.INTERNATIONAL);
                a = this.maybeGetFormattedExtension_(a, e, i18n.phonenumbers.PhoneNumberFormat.INTERNATIONAL);
                return 0 < b.length
                  ? b + ' ' + c + ' ' + d + a
                  : this.prefixNumberWithCountryCallingCode_(c, i18n.phonenumbers.PhoneNumberFormat.INTERNATIONAL, d, a);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.formatInOriginalFormat = function (a, b) {
                if (a.hasRawInput() && !this.hasFormattingPatternForNumber_(a)) return a.getRawInputOrDefault();
                if (!a.hasCountryCodeSource()) return this.format(a, i18n.phonenumbers.PhoneNumberFormat.NATIONAL);
                switch (a.getCountryCodeSource()) {
                  case i18n.phonenumbers.PhoneNumber.CountryCodeSource.FROM_NUMBER_WITH_PLUS_SIGN:
                    b = this.format(a, i18n.phonenumbers.PhoneNumberFormat.INTERNATIONAL);
                    break;
                  case i18n.phonenumbers.PhoneNumber.CountryCodeSource.FROM_NUMBER_WITH_IDD:
                    b = this.formatOutOfCountryCallingNumber(a, b);
                    break;
                  case i18n.phonenumbers.PhoneNumber.CountryCodeSource.FROM_NUMBER_WITHOUT_PLUS_SIGN:
                    b = this.format(a, i18n.phonenumbers.PhoneNumberFormat.INTERNATIONAL).substring(1);
                    break;
                  default:
                    var c = this.getRegionCodeForCountryCode(a.getCountryCodeOrDefault()),
                      d = this.getNddPrefixForRegion(c, !0);
                    b = this.format(a, i18n.phonenumbers.PhoneNumberFormat.NATIONAL);
                    if (
                      null != d &&
                      0 != d.length &&
                      !this.rawInputContainsNationalPrefix_(a.getRawInputOrDefault(), d, c) &&
                      ((c = this.getMetadataForRegion(c)),
                      (d = this.getNationalSignificantNumber(a)),
                      (c = this.chooseFormattingPatternForNumber_(c.numberFormatArray(), d)),
                      null != c)
                    ) {
                      d = c.getNationalPrefixFormattingRuleOrDefault();
                      var e = d.indexOf('$1');
                      0 >= e ||
                        ((d = d.substring(0, e)),
                        (d = i18n.phonenumbers.PhoneNumberUtil.normalizeDigitsOnly(d)),
                        0 != d.length &&
                          ((b = c.clone()),
                          b.clearNationalPrefixFormattingRule(),
                          (b = this.formatByPattern(a, i18n.phonenumbers.PhoneNumberFormat.NATIONAL, [b]))));
                    }
                }
                a = a.getRawInputOrDefault();
                null != b &&
                  0 < a.length &&
                  ((c = i18n.phonenumbers.PhoneNumberUtil.normalizeDiallableCharsOnly(b)),
                  (d = i18n.phonenumbers.PhoneNumberUtil.normalizeDiallableCharsOnly(a)),
                  c != d && (b = a));
                return b;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.rawInputContainsNationalPrefix_ = function (a, b, c) {
                a = i18n.phonenumbers.PhoneNumberUtil.normalizeDigitsOnly(a);
                if (goog.string.startsWith(a, b))
                  try {
                    return this.isValidNumber(this.parse(a.substring(b.length), c));
                  } catch (d) {}
                return !1;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.hasFormattingPatternForNumber_ = function (a) {
                var b = a.getCountryCodeOrDefault(),
                  c = this.getRegionCodeForCountryCode(b);
                b = this.getMetadataForRegionOrCallingCode_(b, c);
                if (null == b) return !1;
                a = this.getNationalSignificantNumber(a);
                return null != this.chooseFormattingPatternForNumber_(b.numberFormatArray(), a);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.formatOutOfCountryKeepingAlphaChars = function (a, b) {
                var c = a.getRawInputOrDefault();
                if (0 == c.length) return this.formatOutOfCountryCallingNumber(a, b);
                var d = a.getCountryCodeOrDefault();
                if (!this.hasValidCountryCallingCode_(d)) return c;
                c = i18n.phonenumbers.PhoneNumberUtil.normalizeHelper_(c, i18n.phonenumbers.PhoneNumberUtil.ALL_PLUS_NUMBER_GROUPING_SYMBOLS_, !0);
                var e = this.getNationalSignificantNumber(a);
                if (3 < e.length) {
                  var f = c.indexOf(e.substring(0, 3));
                  -1 != f && (c = c.substring(f));
                }
                f = this.getMetadataForRegion(b);
                if (d == i18n.phonenumbers.PhoneNumberUtil.NANPA_COUNTRY_CODE_) {
                  if (this.isNANPACountry(b)) return d + ' ' + c;
                } else if (null != f && d == this.getCountryCodeForValidRegion_(b)) {
                  d = this.chooseFormattingPatternForNumber_(f.numberFormatArray(), e);
                  if (null == d) return c;
                  d = d.clone();
                  d.setPattern('(\\d+)(.*)');
                  d.setFormat('$1$2');
                  return this.formatNsnUsingPattern_(c, d, i18n.phonenumbers.PhoneNumberFormat.NATIONAL);
                }
                b = '';
                null != f &&
                  ((b = f.getInternationalPrefixOrDefault()),
                  (b = i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(i18n.phonenumbers.PhoneNumberUtil.SINGLE_INTERNATIONAL_PREFIX_, b)
                    ? b
                    : f.getPreferredInternationalPrefixOrDefault()));
                f = this.getRegionCodeForCountryCode(d);
                f = this.getMetadataForRegionOrCallingCode_(d, f);
                a = this.maybeGetFormattedExtension_(a, f, i18n.phonenumbers.PhoneNumberFormat.INTERNATIONAL);
                return 0 < b.length
                  ? b + ' ' + d + ' ' + c + a
                  : this.prefixNumberWithCountryCallingCode_(d, i18n.phonenumbers.PhoneNumberFormat.INTERNATIONAL, c, a);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getNationalSignificantNumber = function (a) {
                if (!a.hasNationalNumber()) return '';
                var b = '' + a.getNationalNumber();
                return a.hasItalianLeadingZero() && a.getItalianLeadingZero() && 0 < a.getNumberOfLeadingZerosOrDefault()
                  ? Array(a.getNumberOfLeadingZerosOrDefault() + 1).join('0') + b
                  : b;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.prefixNumberWithCountryCallingCode_ = function (a, b, c, d) {
                switch (b) {
                  case i18n.phonenumbers.PhoneNumberFormat.E164:
                    return i18n.phonenumbers.PhoneNumberUtil.PLUS_SIGN + a + c + d;
                  case i18n.phonenumbers.PhoneNumberFormat.INTERNATIONAL:
                    return i18n.phonenumbers.PhoneNumberUtil.PLUS_SIGN + a + ' ' + c + d;
                  case i18n.phonenumbers.PhoneNumberFormat.RFC3966:
                    return i18n.phonenumbers.PhoneNumberUtil.RFC3966_PREFIX_ + i18n.phonenumbers.PhoneNumberUtil.PLUS_SIGN + a + '-' + c + d;
                  default:
                    return c + d;
                }
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.formatNsn_ = function (a, b, c, d) {
                b =
                  0 == b.intlNumberFormatArray().length || c == i18n.phonenumbers.PhoneNumberFormat.NATIONAL
                    ? b.numberFormatArray()
                    : b.intlNumberFormatArray();
                b = this.chooseFormattingPatternForNumber_(b, a);
                return null == b ? a : this.formatNsnUsingPattern_(a, b, c, d);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.chooseFormattingPatternForNumber_ = function (a, b) {
                for (var c, d = a.length, e = 0; e < d; ++e) {
                  c = a[e];
                  var f = c.leadingDigitsPatternCount();
                  if (0 == f || 0 == b.search(c.getLeadingDigitsPattern(f - 1)))
                    if (((f = new RegExp(c.getPattern())), i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(f, b))) return c;
                }
                return null;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.formatNsnUsingPattern_ = function (a, b, c, d) {
                var e = b.getFormatOrDefault(),
                  f = new RegExp(b.getPattern()),
                  g = b.getDomesticCarrierCodeFormattingRuleOrDefault();
                c == i18n.phonenumbers.PhoneNumberFormat.NATIONAL && null != d && 0 < d.length && 0 < g.length
                  ? ((b = g.replace(i18n.phonenumbers.PhoneNumberUtil.CC_PATTERN_, d)),
                    (e = e.replace(i18n.phonenumbers.PhoneNumberUtil.FIRST_GROUP_PATTERN_, b)),
                    (a = a.replace(f, e)))
                  : ((b = b.getNationalPrefixFormattingRuleOrDefault()),
                    (a =
                      c == i18n.phonenumbers.PhoneNumberFormat.NATIONAL && null != b && 0 < b.length
                        ? a.replace(f, e.replace(i18n.phonenumbers.PhoneNumberUtil.FIRST_GROUP_PATTERN_, b))
                        : a.replace(f, e)));
                c == i18n.phonenumbers.PhoneNumberFormat.RFC3966 &&
                  ((a = a.replace(new RegExp('^' + i18n.phonenumbers.PhoneNumberUtil.SEPARATOR_PATTERN_), '')),
                  (a = a.replace(new RegExp(i18n.phonenumbers.PhoneNumberUtil.SEPARATOR_PATTERN_, 'g'), '-')));
                return a;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getExampleNumber = function (a) {
                return this.getExampleNumberForType(a, i18n.phonenumbers.PhoneNumberType.FIXED_LINE);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getExampleNumberForType = function (a, b) {
                if (!this.isValidRegionCode_(a)) return null;
                b = i18n.phonenumbers.PhoneNumberUtil.getNumberDescByType_(this.getMetadataForRegion(a), b);
                try {
                  if (b.hasExampleNumber()) return this.parse(b.getExampleNumber(), a);
                } catch (c) {}
                return null;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getExampleNumberForNonGeoEntity = function (a) {
                var b = this.getMetadataForNonGeographicalRegion(a);
                if (
                  null != b &&
                  ((b = [b.getMobile(), b.getTollFree(), b.getSharedCost(), b.getVoip(), b.getVoicemail(), b.getUan(), b.getPremiumRate()].find(
                    function (c, d) {
                      return c.hasExampleNumber();
                    }
                  )),
                  void 0 !== b)
                )
                  try {
                    return this.parse('+' + a + b.getExampleNumber(), 'ZZ');
                  } catch (c) {}
                return null;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.maybeGetFormattedExtension_ = function (a, b, c) {
                return a.hasExtension() && 0 != a.getExtension().length
                  ? c == i18n.phonenumbers.PhoneNumberFormat.RFC3966
                    ? i18n.phonenumbers.PhoneNumberUtil.RFC3966_EXTN_PREFIX_ + a.getExtension()
                    : b.hasPreferredExtnPrefix()
                      ? b.getPreferredExtnPrefix() + a.getExtensionOrDefault()
                      : i18n.phonenumbers.PhoneNumberUtil.DEFAULT_EXTN_PREFIX_ + a.getExtensionOrDefault()
                  : '';
              };
              i18n.phonenumbers.PhoneNumberUtil.getNumberDescByType_ = function (a, b) {
                switch (b) {
                  case i18n.phonenumbers.PhoneNumberType.PREMIUM_RATE:
                    return a.getPremiumRate();
                  case i18n.phonenumbers.PhoneNumberType.TOLL_FREE:
                    return a.getTollFree();
                  case i18n.phonenumbers.PhoneNumberType.MOBILE:
                    return a.getMobile();
                  case i18n.phonenumbers.PhoneNumberType.FIXED_LINE:
                  case i18n.phonenumbers.PhoneNumberType.FIXED_LINE_OR_MOBILE:
                    return a.getFixedLine();
                  case i18n.phonenumbers.PhoneNumberType.SHARED_COST:
                    return a.getSharedCost();
                  case i18n.phonenumbers.PhoneNumberType.VOIP:
                    return a.getVoip();
                  case i18n.phonenumbers.PhoneNumberType.PERSONAL_NUMBER:
                    return a.getPersonalNumber();
                  case i18n.phonenumbers.PhoneNumberType.PAGER:
                    return a.getPager();
                  case i18n.phonenumbers.PhoneNumberType.UAN:
                    return a.getUan();
                  case i18n.phonenumbers.PhoneNumberType.VOICEMAIL:
                    return a.getVoicemail();
                  default:
                    return a.getGeneralDesc();
                }
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getNumberType = function (a) {
                var b = this.getRegionCodeForNumber(a);
                b = this.getMetadataForRegionOrCallingCode_(a.getCountryCodeOrDefault(), b);
                if (null == b) return i18n.phonenumbers.PhoneNumberType.UNKNOWN;
                a = this.getNationalSignificantNumber(a);
                return this.getNumberTypeHelper_(a, b);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getNumberTypeHelper_ = function (a, b) {
                return this.isNumberMatchingDesc_(a, b.getGeneralDesc())
                  ? this.isNumberMatchingDesc_(a, b.getPremiumRate())
                    ? i18n.phonenumbers.PhoneNumberType.PREMIUM_RATE
                    : this.isNumberMatchingDesc_(a, b.getTollFree())
                      ? i18n.phonenumbers.PhoneNumberType.TOLL_FREE
                      : this.isNumberMatchingDesc_(a, b.getSharedCost())
                        ? i18n.phonenumbers.PhoneNumberType.SHARED_COST
                        : this.isNumberMatchingDesc_(a, b.getVoip())
                          ? i18n.phonenumbers.PhoneNumberType.VOIP
                          : this.isNumberMatchingDesc_(a, b.getPersonalNumber())
                            ? i18n.phonenumbers.PhoneNumberType.PERSONAL_NUMBER
                            : this.isNumberMatchingDesc_(a, b.getPager())
                              ? i18n.phonenumbers.PhoneNumberType.PAGER
                              : this.isNumberMatchingDesc_(a, b.getUan())
                                ? i18n.phonenumbers.PhoneNumberType.UAN
                                : this.isNumberMatchingDesc_(a, b.getVoicemail())
                                  ? i18n.phonenumbers.PhoneNumberType.VOICEMAIL
                                  : this.isNumberMatchingDesc_(a, b.getFixedLine())
                                    ? b.getSameMobileAndFixedLinePattern() || this.isNumberMatchingDesc_(a, b.getMobile())
                                      ? i18n.phonenumbers.PhoneNumberType.FIXED_LINE_OR_MOBILE
                                      : i18n.phonenumbers.PhoneNumberType.FIXED_LINE
                                    : !b.getSameMobileAndFixedLinePattern() && this.isNumberMatchingDesc_(a, b.getMobile())
                                      ? i18n.phonenumbers.PhoneNumberType.MOBILE
                                      : i18n.phonenumbers.PhoneNumberType.UNKNOWN
                  : i18n.phonenumbers.PhoneNumberType.UNKNOWN;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getMetadataForRegion = function (a) {
                if (null == a) return null;
                a = a.toUpperCase();
                var b = this.regionToMetadataMap[a];
                if (null == b) {
                  b = new goog.proto2.PbLiteSerializer();
                  var c = i18n.phonenumbers.metadata.countryToMetadata[a];
                  if (null == c) return null;
                  b = b.deserialize(i18n.phonenumbers.PhoneMetadata.getDescriptor(), c);
                  this.regionToMetadataMap[a] = b;
                }
                return b;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getMetadataForNonGeographicalRegion = function (a) {
                return this.getMetadataForRegion('' + a);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isNumberMatchingDesc_ = function (a, b) {
                var c = a.length;
                return 0 < b.possibleLengthCount() && -1 == b.possibleLengthArray().indexOf(c)
                  ? !1
                  : i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(b.getNationalNumberPatternOrDefault(), a);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isValidNumber = function (a) {
                var b = this.getRegionCodeForNumber(a);
                return this.isValidNumberForRegion(a, b);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isValidNumberForRegion = function (a, b) {
                var c = a.getCountryCodeOrDefault(),
                  d = this.getMetadataForRegionOrCallingCode_(c, b);
                if (null == d || (i18n.phonenumbers.PhoneNumberUtil.REGION_CODE_FOR_NON_GEO_ENTITY != b && c != this.getCountryCodeForValidRegion_(b)))
                  return !1;
                a = this.getNationalSignificantNumber(a);
                return this.getNumberTypeHelper_(a, d) != i18n.phonenumbers.PhoneNumberType.UNKNOWN;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getRegionCodeForNumber = function (a) {
                if (null == a) return null;
                var b = a.getCountryCodeOrDefault();
                b = i18n.phonenumbers.metadata.countryCodeToRegionCodeMap[b];
                return null == b ? null : 1 == b.length ? b[0] : this.getRegionCodeForNumberFromRegionList_(a, b);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getRegionCodeForNumberFromRegionList_ = function (a, b) {
                a = this.getNationalSignificantNumber(a);
                for (var c, d = b.length, e = 0; e < d; e++) {
                  c = b[e];
                  var f = this.getMetadataForRegion(c);
                  if (f.hasLeadingDigits()) {
                    if (0 == a.search(f.getLeadingDigits())) return c;
                  } else if (this.getNumberTypeHelper_(a, f) != i18n.phonenumbers.PhoneNumberType.UNKNOWN) return c;
                }
                return null;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getRegionCodeForCountryCode = function (a) {
                a = i18n.phonenumbers.metadata.countryCodeToRegionCodeMap[a];
                return null == a ? i18n.phonenumbers.PhoneNumberUtil.UNKNOWN_REGION_ : a[0];
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getRegionCodesForCountryCode = function (a) {
                a = i18n.phonenumbers.metadata.countryCodeToRegionCodeMap[a];
                return null == a ? [] : a;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getCountryCodeForRegion = function (a) {
                return this.isValidRegionCode_(a) ? this.getCountryCodeForValidRegion_(a) : 0;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getCountryCodeForValidRegion_ = function (a) {
                var b = this.getMetadataForRegion(a);
                if (null == b) throw Error('Invalid region code: ' + a);
                return b.getCountryCodeOrDefault();
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.getNddPrefixForRegion = function (a, b) {
                a = this.getMetadataForRegion(a);
                if (null == a) return null;
                a = a.getNationalPrefixOrDefault();
                if (0 == a.length) return null;
                b && (a = a.replace('~', ''));
                return a;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isNANPACountry = function (a) {
                return (
                  null != a &&
                  i18n.phonenumbers.metadata.countryCodeToRegionCodeMap[i18n.phonenumbers.PhoneNumberUtil.NANPA_COUNTRY_CODE_].includes(a.toUpperCase())
                );
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isAlphaNumber = function (a) {
                if (!i18n.phonenumbers.PhoneNumberUtil.isViablePhoneNumber(a)) return !1;
                a = new goog.string.StringBuffer(a);
                this.maybeStripExtension(a);
                return i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(i18n.phonenumbers.PhoneNumberUtil.VALID_ALPHA_PHONE_PATTERN_, a.toString());
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isPossibleNumber = function (a) {
                a = this.isPossibleNumberWithReason(a);
                return (
                  a == i18n.phonenumbers.PhoneNumberUtil.ValidationResult.IS_POSSIBLE ||
                  a == i18n.phonenumbers.PhoneNumberUtil.ValidationResult.IS_POSSIBLE_LOCAL_ONLY
                );
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isPossibleNumberForType = function (a, b) {
                a = this.isPossibleNumberForTypeWithReason(a, b);
                return (
                  a == i18n.phonenumbers.PhoneNumberUtil.ValidationResult.IS_POSSIBLE ||
                  a == i18n.phonenumbers.PhoneNumberUtil.ValidationResult.IS_POSSIBLE_LOCAL_ONLY
                );
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.testNumberLength_ = function (a, b) {
                return this.testNumberLengthForType_(a, b, i18n.phonenumbers.PhoneNumberType.UNKNOWN);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.testNumberLengthForType_ = function (a, b, c) {
                var d = i18n.phonenumbers.PhoneNumberUtil.getNumberDescByType_(b, c),
                  e = 0 == d.possibleLengthCount() ? b.getGeneralDesc().possibleLengthArray() : d.possibleLengthArray();
                d = d.possibleLengthLocalOnlyArray();
                if (c == i18n.phonenumbers.PhoneNumberType.FIXED_LINE_OR_MOBILE)
                  if (
                    i18n.phonenumbers.PhoneNumberUtil.descHasPossibleNumberData_(
                      i18n.phonenumbers.PhoneNumberUtil.getNumberDescByType_(b, i18n.phonenumbers.PhoneNumberType.FIXED_LINE)
                    )
                  )
                    ((c = i18n.phonenumbers.PhoneNumberUtil.getNumberDescByType_(b, i18n.phonenumbers.PhoneNumberType.MOBILE)),
                      i18n.phonenumbers.PhoneNumberUtil.descHasPossibleNumberData_(c) &&
                        ((e = e.concat(0 == c.possibleLengthCount() ? b.getGeneralDesc().possibleLengthArray() : c.possibleLengthArray())),
                        e.sort(),
                        0 == d.length ? (d = c.possibleLengthLocalOnlyArray()) : ((d = d.concat(c.possibleLengthLocalOnlyArray())), d.sort())));
                  else return this.testNumberLengthForType_(a, b, i18n.phonenumbers.PhoneNumberType.MOBILE);
                if (-1 == e[0]) return i18n.phonenumbers.PhoneNumberUtil.ValidationResult.INVALID_LENGTH;
                a = a.length;
                if (-1 < d.indexOf(a)) return i18n.phonenumbers.PhoneNumberUtil.ValidationResult.IS_POSSIBLE_LOCAL_ONLY;
                b = e[0];
                return b == a
                  ? i18n.phonenumbers.PhoneNumberUtil.ValidationResult.IS_POSSIBLE
                  : b > a
                    ? i18n.phonenumbers.PhoneNumberUtil.ValidationResult.TOO_SHORT
                    : e[e.length - 1] < a
                      ? i18n.phonenumbers.PhoneNumberUtil.ValidationResult.TOO_LONG
                      : -1 < e.indexOf(a, 1)
                        ? i18n.phonenumbers.PhoneNumberUtil.ValidationResult.IS_POSSIBLE
                        : i18n.phonenumbers.PhoneNumberUtil.ValidationResult.INVALID_LENGTH;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isPossibleNumberWithReason = function (a) {
                return this.isPossibleNumberForTypeWithReason(a, i18n.phonenumbers.PhoneNumberType.UNKNOWN);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isPossibleNumberForTypeWithReason = function (a, b) {
                var c = this.getNationalSignificantNumber(a);
                a = a.getCountryCodeOrDefault();
                if (!this.hasValidCountryCallingCode_(a)) return i18n.phonenumbers.PhoneNumberUtil.ValidationResult.INVALID_COUNTRY_CODE;
                var d = this.getRegionCodeForCountryCode(a);
                a = this.getMetadataForRegionOrCallingCode_(a, d);
                return this.testNumberLengthForType_(c, a, b);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isPossibleNumberString = function (a, b) {
                try {
                  return this.isPossibleNumber(this.parse(a, b));
                } catch (c) {
                  return !1;
                }
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.truncateTooLongNumber = function (a) {
                if (this.isValidNumber(a)) return !0;
                var b = a.clone(),
                  c = a.getNationalNumberOrDefault();
                do
                  if (
                    ((c = Math.floor(c / 10)),
                    b.setNationalNumber(c),
                    0 == c || this.isPossibleNumberWithReason(b) == i18n.phonenumbers.PhoneNumberUtil.ValidationResult.TOO_SHORT)
                  )
                    return !1;
                while (!this.isValidNumber(b));
                a.setNationalNumber(c);
                return !0;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.extractCountryCode = function (a, b) {
                a = a.toString();
                if (0 == a.length || '0' == a.charAt(0)) return 0;
                for (var c, d = a.length, e = 1; e <= i18n.phonenumbers.PhoneNumberUtil.MAX_LENGTH_COUNTRY_CODE_ && e <= d; ++e)
                  if (((c = parseInt(a.substring(0, e), 10)), c in i18n.phonenumbers.metadata.countryCodeToRegionCodeMap)) return (b.append(a.substring(e)), c);
                return 0;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.maybeExtractCountryCode = function (a, b, c, d, e) {
                if (0 == a.length) return 0;
                a = new goog.string.StringBuffer(a);
                var f;
                null != b && (f = b.getInternationalPrefix());
                null == f && (f = 'NonMatch');
                f = this.maybeStripInternationalPrefixAndNormalize(a, f);
                d && e.setCountryCodeSource(f);
                if (f != i18n.phonenumbers.PhoneNumber.CountryCodeSource.FROM_DEFAULT_COUNTRY) {
                  if (a.getLength() <= i18n.phonenumbers.PhoneNumberUtil.MIN_LENGTH_FOR_NSN_) throw Error(i18n.phonenumbers.Error.TOO_SHORT_AFTER_IDD);
                  b = this.extractCountryCode(a, c);
                  if (0 != b) return (e.setCountryCode(b), b);
                  throw Error(i18n.phonenumbers.Error.INVALID_COUNTRY_CODE);
                }
                if (null != b) {
                  f = b.getCountryCodeOrDefault();
                  var g = '' + f,
                    h = a.toString();
                  if (
                    goog.string.startsWith(h, g) &&
                    ((g = new goog.string.StringBuffer(h.substring(g.length))),
                    (h = b.getGeneralDesc()),
                    (h = new RegExp(h.getNationalNumberPatternOrDefault())),
                    this.maybeStripNationalPrefixAndCarrierCode(g, b, null),
                    (g = g.toString()),
                    (!i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(h, a.toString()) && i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(h, g)) ||
                      this.testNumberLength_(a.toString(), b) == i18n.phonenumbers.PhoneNumberUtil.ValidationResult.TOO_LONG)
                  )
                    return (
                      c.append(g),
                      d && e.setCountryCodeSource(i18n.phonenumbers.PhoneNumber.CountryCodeSource.FROM_NUMBER_WITHOUT_PLUS_SIGN),
                      e.setCountryCode(f),
                      f
                    );
                }
                e.setCountryCode(0);
                return 0;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.parsePrefixAsIdd_ = function (a, b) {
                var c = b.toString();
                if (0 == c.search(a)) {
                  a = c.match(a)[0].length;
                  var d = c.substring(a).match(i18n.phonenumbers.PhoneNumberUtil.CAPTURING_DIGIT_PATTERN);
                  if (d && null != d[1] && 0 < d[1].length && '0' == i18n.phonenumbers.PhoneNumberUtil.normalizeDigitsOnly(d[1])) return !1;
                  b.clear();
                  b.append(c.substring(a));
                  return !0;
                }
                return !1;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.maybeStripInternationalPrefixAndNormalize = function (a, b) {
                var c = a.toString();
                if (0 == c.length) return i18n.phonenumbers.PhoneNumber.CountryCodeSource.FROM_DEFAULT_COUNTRY;
                if (i18n.phonenumbers.PhoneNumberUtil.LEADING_PLUS_CHARS_PATTERN.test(c))
                  return (
                    (c = c.replace(i18n.phonenumbers.PhoneNumberUtil.LEADING_PLUS_CHARS_PATTERN, '')),
                    a.clear(),
                    a.append(i18n.phonenumbers.PhoneNumberUtil.normalize(c)),
                    i18n.phonenumbers.PhoneNumber.CountryCodeSource.FROM_NUMBER_WITH_PLUS_SIGN
                  );
                b = new RegExp(b);
                i18n.phonenumbers.PhoneNumberUtil.normalizeSB_(a);
                return this.parsePrefixAsIdd_(b, a)
                  ? i18n.phonenumbers.PhoneNumber.CountryCodeSource.FROM_NUMBER_WITH_IDD
                  : i18n.phonenumbers.PhoneNumber.CountryCodeSource.FROM_DEFAULT_COUNTRY;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.maybeStripNationalPrefixAndCarrierCode = function (a, b, c) {
                var d = a.toString(),
                  e = d.length,
                  f = b.getNationalPrefixForParsing();
                if (0 == e || null == f || 0 == f.length) return !1;
                var g = new RegExp('^(?:' + f + ')');
                if ((e = g.exec(d))) {
                  f = new RegExp(b.getGeneralDesc().getNationalNumberPatternOrDefault());
                  var h = i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(f, d),
                    k = e.length - 1;
                  b = b.getNationalPrefixTransformRule();
                  if (null == b || 0 == b.length || null == e[k] || 0 == e[k].length) {
                    if (h && !i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(f, d.substring(e[0].length))) return !1;
                    null != c && 0 < k && null != e[k] && c.append(e[1]);
                    a.set(d.substring(e[0].length));
                  } else {
                    d = d.replace(g, b);
                    if (h && !i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(f, d)) return !1;
                    null != c && 0 < k && c.append(e[1]);
                    a.set(d);
                  }
                  return !0;
                }
                return !1;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.maybeStripExtension = function (a) {
                var b = a.toString(),
                  c = b.search(i18n.phonenumbers.PhoneNumberUtil.EXTN_PATTERN_);
                if (0 <= c && i18n.phonenumbers.PhoneNumberUtil.isViablePhoneNumber(b.substring(0, c)))
                  for (var d = b.match(i18n.phonenumbers.PhoneNumberUtil.EXTN_PATTERN_), e = d.length, f = 1; f < e; ++f)
                    if (null != d[f] && 0 < d[f].length) return (a.clear(), a.append(b.substring(0, c)), d[f]);
                return '';
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.checkRegionForParsing_ = function (a, b) {
                return this.isValidRegionCode_(b) || (null != a && 0 < a.length && i18n.phonenumbers.PhoneNumberUtil.LEADING_PLUS_CHARS_PATTERN.test(a));
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.parse = function (a, b) {
                return this.parseHelper_(a, b, !1, !0);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.parseAndKeepRawInput = function (a, b) {
                if (!this.isValidRegionCode_(b) && 0 < a.length && a.charAt(0) != i18n.phonenumbers.PhoneNumberUtil.PLUS_SIGN)
                  throw Error(i18n.phonenumbers.Error.INVALID_COUNTRY_CODE);
                return this.parseHelper_(a, b, !0, !0);
              };
              i18n.phonenumbers.PhoneNumberUtil.setItalianLeadingZerosForPhoneNumber_ = function (a, b) {
                if (1 < a.length && '0' == a.charAt(0)) {
                  b.setItalianLeadingZero(!0);
                  for (var c = 1; c < a.length - 1 && '0' == a.charAt(c); ) c++;
                  1 != c && b.setNumberOfLeadingZeros(c);
                }
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.parseHelper_ = function (a, b, c, d) {
                if (null == a) throw Error(i18n.phonenumbers.Error.NOT_A_NUMBER);
                if (a.length > i18n.phonenumbers.PhoneNumberUtil.MAX_INPUT_STRING_LENGTH_) throw Error(i18n.phonenumbers.Error.TOO_LONG);
                var e = new goog.string.StringBuffer();
                this.buildNationalNumberForParsing_(a, e);
                if (!i18n.phonenumbers.PhoneNumberUtil.isViablePhoneNumber(e.toString())) throw Error(i18n.phonenumbers.Error.NOT_A_NUMBER);
                if (d && !this.checkRegionForParsing_(e.toString(), b)) throw Error(i18n.phonenumbers.Error.INVALID_COUNTRY_CODE);
                d = new i18n.phonenumbers.PhoneNumber();
                c && d.setRawInput(a);
                a = this.maybeStripExtension(e);
                0 < a.length && d.setExtension(a);
                var f = this.getMetadataForRegion(b);
                a = new goog.string.StringBuffer();
                var g = 0,
                  h = e.toString();
                try {
                  g = this.maybeExtractCountryCode(h, f, a, c, d);
                } catch (k) {
                  if (k.message == i18n.phonenumbers.Error.INVALID_COUNTRY_CODE && i18n.phonenumbers.PhoneNumberUtil.LEADING_PLUS_CHARS_PATTERN.test(h)) {
                    if (
                      ((h = h.replace(i18n.phonenumbers.PhoneNumberUtil.LEADING_PLUS_CHARS_PATTERN, '')),
                      (g = this.maybeExtractCountryCode(h, f, a, c, d)),
                      0 == g)
                    )
                      throw k;
                  } else throw k;
                }
                0 != g
                  ? ((e = this.getRegionCodeForCountryCode(g)), e != b && (f = this.getMetadataForRegionOrCallingCode_(g, e)))
                  : (i18n.phonenumbers.PhoneNumberUtil.normalizeSB_(e),
                    a.append(e.toString()),
                    null != b ? ((g = f.getCountryCodeOrDefault()), d.setCountryCode(g)) : c && d.clearCountryCodeSource());
                if (a.getLength() < i18n.phonenumbers.PhoneNumberUtil.MIN_LENGTH_FOR_NSN_) throw Error(i18n.phonenumbers.Error.TOO_SHORT_NSN);
                null != f &&
                  ((b = new goog.string.StringBuffer()),
                  (e = new goog.string.StringBuffer(a.toString())),
                  this.maybeStripNationalPrefixAndCarrierCode(e, f, b),
                  (f = this.testNumberLength_(e.toString(), f)),
                  (g = i18n.phonenumbers.PhoneNumberUtil.ValidationResult),
                  f != g.TOO_SHORT &&
                    f != g.IS_POSSIBLE_LOCAL_ONLY &&
                    f != g.INVALID_LENGTH &&
                    ((a = e), c && 0 < b.toString().length && d.setPreferredDomesticCarrierCode(b.toString())));
                c = a.toString();
                a = c.length;
                if (a < i18n.phonenumbers.PhoneNumberUtil.MIN_LENGTH_FOR_NSN_) throw Error(i18n.phonenumbers.Error.TOO_SHORT_NSN);
                if (a > i18n.phonenumbers.PhoneNumberUtil.MAX_LENGTH_FOR_NSN_) throw Error(i18n.phonenumbers.Error.TOO_LONG);
                i18n.phonenumbers.PhoneNumberUtil.setItalianLeadingZerosForPhoneNumber_(c, d);
                d.setNationalNumber(parseInt(c, 10));
                return d;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.extractPhoneContext_ = function (a) {
                var b = a.indexOf(i18n.phonenumbers.PhoneNumberUtil.RFC3966_PHONE_CONTEXT_);
                if (-1 === b) return null;
                b += i18n.phonenumbers.PhoneNumberUtil.RFC3966_PHONE_CONTEXT_.length;
                if (b >= a.length) return '';
                var c = a.indexOf(';', b);
                return -1 !== c ? a.substring(b, c) : a.substring(b);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isPhoneContextValid_ = function (a) {
                if (null == a) return !0;
                if (0 === a.length) return !1;
                var b = i18n.phonenumbers.PhoneNumberUtil.RFC3966_GLOBAL_NUMBER_DIGITS_PATTERN_.exec(a);
                a = i18n.phonenumbers.PhoneNumberUtil.RFC3966_DOMAINNAME_PATTERN_.exec(a);
                return null !== b || null !== a;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.buildNationalNumberForParsing_ = function (a, b) {
                var c = i18n.phonenumbers.PhoneNumberUtil.prototype.extractPhoneContext_(a);
                if (!i18n.phonenumbers.PhoneNumberUtil.prototype.isPhoneContextValid_(c)) throw Error(i18n.phonenumbers.Error.NOT_A_NUMBER);
                if (null != c) {
                  c.charAt(0) === i18n.phonenumbers.PhoneNumberUtil.PLUS_SIGN && b.append(c);
                  c = a.indexOf(i18n.phonenumbers.PhoneNumberUtil.RFC3966_PREFIX_);
                  c = 0 <= c ? c + i18n.phonenumbers.PhoneNumberUtil.RFC3966_PREFIX_.length : 0;
                  var d = a.indexOf(i18n.phonenumbers.PhoneNumberUtil.RFC3966_PHONE_CONTEXT_);
                  b.append(a.substring(c, d));
                } else b.append(i18n.phonenumbers.PhoneNumberUtil.extractPossibleNumber(null != a ? a : ''));
                a = b.toString();
                c = a.indexOf(i18n.phonenumbers.PhoneNumberUtil.RFC3966_ISDN_SUBADDRESS_);
                0 < c && (b.clear(), b.append(a.substring(0, c)));
              };
              i18n.phonenumbers.PhoneNumberUtil.copyCoreFieldsOnly_ = function (a) {
                var b = new i18n.phonenumbers.PhoneNumber();
                b.setCountryCode(a.getCountryCodeOrDefault());
                b.setNationalNumber(a.getNationalNumberOrDefault());
                0 < a.getExtensionOrDefault().length && b.setExtension(a.getExtensionOrDefault());
                a.getItalianLeadingZero() && (b.setItalianLeadingZero(!0), b.setNumberOfLeadingZeros(a.getNumberOfLeadingZerosOrDefault()));
                return b;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isNumberMatch = function (a, b) {
                if ('string' == typeof a)
                  try {
                    var c = this.parse(a, i18n.phonenumbers.PhoneNumberUtil.UNKNOWN_REGION_);
                  } catch (f) {
                    if (f.message != i18n.phonenumbers.Error.INVALID_COUNTRY_CODE) return i18n.phonenumbers.PhoneNumberUtil.MatchType.NOT_A_NUMBER;
                    if ('string' != typeof b) {
                      var d = this.getRegionCodeForCountryCode(b.getCountryCodeOrDefault());
                      if (d != i18n.phonenumbers.PhoneNumberUtil.UNKNOWN_REGION_) {
                        try {
                          c = this.parse(a, d);
                        } catch (g) {
                          return i18n.phonenumbers.PhoneNumberUtil.MatchType.NOT_A_NUMBER;
                        }
                        var e = this.isNumberMatch(c, b);
                        return e == i18n.phonenumbers.PhoneNumberUtil.MatchType.EXACT_MATCH ? i18n.phonenumbers.PhoneNumberUtil.MatchType.NSN_MATCH : e;
                      }
                    }
                    try {
                      c = this.parseHelper_(a, null, !1, !1);
                    } catch (g) {
                      return i18n.phonenumbers.PhoneNumberUtil.MatchType.NOT_A_NUMBER;
                    }
                  }
                else c = a.clone();
                if ('string' == typeof b)
                  try {
                    return ((e = this.parse(b, i18n.phonenumbers.PhoneNumberUtil.UNKNOWN_REGION_)), this.isNumberMatch(a, e));
                  } catch (f) {
                    return f.message != i18n.phonenumbers.Error.INVALID_COUNTRY_CODE
                      ? i18n.phonenumbers.PhoneNumberUtil.MatchType.NOT_A_NUMBER
                      : this.isNumberMatch(b, c);
                  }
                else e = b.clone();
                a = i18n.phonenumbers.PhoneNumberUtil.copyCoreFieldsOnly_(c);
                e = i18n.phonenumbers.PhoneNumberUtil.copyCoreFieldsOnly_(e);
                if (a.hasExtension() && e.hasExtension() && a.getExtension() != e.getExtension()) return i18n.phonenumbers.PhoneNumberUtil.MatchType.NO_MATCH;
                b = a.getCountryCodeOrDefault();
                c = e.getCountryCodeOrDefault();
                if (0 != b && 0 != c)
                  return a.equals(e)
                    ? i18n.phonenumbers.PhoneNumberUtil.MatchType.EXACT_MATCH
                    : b == c && this.isNationalNumberSuffixOfTheOther_(a, e)
                      ? i18n.phonenumbers.PhoneNumberUtil.MatchType.SHORT_NSN_MATCH
                      : i18n.phonenumbers.PhoneNumberUtil.MatchType.NO_MATCH;
                a.setCountryCode(0);
                e.setCountryCode(0);
                return a.equals(e)
                  ? i18n.phonenumbers.PhoneNumberUtil.MatchType.NSN_MATCH
                  : this.isNationalNumberSuffixOfTheOther_(a, e)
                    ? i18n.phonenumbers.PhoneNumberUtil.MatchType.SHORT_NSN_MATCH
                    : i18n.phonenumbers.PhoneNumberUtil.MatchType.NO_MATCH;
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.isNationalNumberSuffixOfTheOther_ = function (a, b) {
                a = '' + a.getNationalNumber();
                b = '' + b.getNationalNumber();
                return goog.string.endsWith(a, b) || goog.string.endsWith(b, a);
              };
              i18n.phonenumbers.PhoneNumberUtil.prototype.canBeInternationallyDialled = function (a) {
                var b = this.getMetadataForRegion(this.getRegionCodeForNumber(a));
                if (null == b) return !0;
                a = this.getNationalSignificantNumber(a);
                return !this.isNumberMatchingDesc_(a, b.getNoInternationalDialling());
              };
              i18n.phonenumbers.PhoneNumberUtil.matchesEntirely = function (a, b) {
                return (a = b.match(new RegExp('^(?:' + ('string' == typeof a ? a : a.source) + ')$', 'i'))) && a[0].length == b.length ? !0 : !1;
              };
              i18n.phonenumbers.PhoneNumberUtil.matchesPrefix = function (a, b) {
                return (a = 'string' == typeof a ? b.match('^(?:' + a + ')') : b.match(a)) && goog.string.startsWith(b, a[0]) ? !0 : !1;
              };
              i18n.phonenumbers.shortnumbermetadata = {};
              i18n.phonenumbers.shortnumbermetadata.countryCodeToRegionCodeMap = {
                0: 'AC AD AE AF AG AI AL AM AO AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GR GT GU GW GY HK HN HR HT HU ID IE IL IM IN IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW'.split(
                  ' '
                )
              };
              i18n.phonenumbers.shortnumbermetadata.countryToMetadata = {
                AC: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AC',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:11|99)', , , , '911'],
                  ,
                  [, , '9(?:11|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AD: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[0268]', , , , '110'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AD',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[0268]', , , , '110'],
                  ,
                  [, , '11[0268]', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AE: [
                  ,
                  [, , '[149]\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '112|99[7-9]', , , , '112', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|99[7-9]', , , , '112', , , [3]],
                  ,
                  [, , '112|445[16]|99[7-9]', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '445\\d', , , , '4450', , , [4]]
                ],
                AF: [
                  ,
                  [, , '[14]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '1(?:0[02]|19)', , , , '100', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AF',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:0[02]|19)', , , , '100', , , [3]],
                  ,
                  [, , '1(?:0[02]|19)|40404', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , '404\\d\\d', , , , '40400', , , [5]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                AG: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AG',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:11|99)', , , , '911'],
                  ,
                  [, , '176|9(?:11|88|99)', , , , '176'],
                  [, , , , , , , , , [-1]],
                  [, , '176', , , , '176'],
                  ,
                  [, , '176', , , , '176']
                ],
                AI: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AI',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '911', , , , '911'],
                  ,
                  [, , '176|9(?:11|88)', , , , '176'],
                  [, , , , , , , , , [-1]],
                  [, , '176', , , , '176'],
                  ,
                  [, , '176', , , , '176']
                ],
                AL: [
                  ,
                  [, , '[15]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:1(?:2|6[01]\\d\\d)|2[7-9]|3[15]|41)', , , , '112', , , [3, 6]],
                  [, , '5\\d{4}', , , , '50000', , , [5]],
                  ,
                  ,
                  ,
                  'AL',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|2[7-9])', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:6(?:000|1(?:06|11|23))|8\\d\\d)|65\\d|89[12])|5\\d{4}|1(?:[1349]\\d|2[2-9])', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , '123', , , , '123', , , [3]],
                  ,
                  [, , '131|5\\d{4}', , , , '131', , , [3, 5]]
                ],
                AM: [
                  ,
                  [, , '[148]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '10[1-3]', , , , '101', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '10[1-3]', , , , '101', , , [3]],
                  ,
                  [, , '(?:1|8[1-7])\\d\\d|40404', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , '404\\d\\d', , , , '40400', , , [5]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                AO: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[235]', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AO',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[235]', , , , '112'],
                  ,
                  [, , '11[235]', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AR: [
                  ,
                  [, , '[01389]\\d{1,4}', , , , , , , [2, 3, 4, 5]],
                  ,
                  ,
                  [, , '000|1(?:0[0-35-7]|1[0245]|2[015]|3[47]|4[478]|9)|911', , , , '19', , , [2, 3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '10[017]|911', , , , '100', , , [3]],
                  ,
                  [, , '000|1(?:0[0-35-7]|1[02-5]|2[015]|3[47]|4[478]|9)|3372|89338|911', , , , '19'],
                  [, , , , , , , , , [-1]],
                  [, , '893\\d\\d', , , , '89300', , , [5]],
                  ,
                  [, , '(?:337|893\\d)\\d', , , , '3370', , , [4, 5]]
                ],
                AS: [
                  ,
                  [, , '[49]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '9(?:11|88)', , , , '911', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AS',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '911', , , , '911', , , [3]],
                  ,
                  [, , '40404|9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                AT: [
                  ,
                  [, , '[1268]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:12|2[0238]|3[03]|4[0-247])|1(?:16\\d\\d|4[58])\\d', , , , '112', , , [3, 4, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AT',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:[12]2|33|44)', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:2|6(?:00[06]|1(?:17|23)))|2[0238]|3[03]|4(?:[0-247]|5[05]|84))|(?:2(?:066|20)|61|8108[1-3])0', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , '220\\d|610|(?:206|810\\d)\\d\\d', , , , '610'],
                  ,
                  [, , '206\\d\\d', , , , '20600', , , [5]]
                ],
                AU: [
                  ,
                  [, , '[0-27]\\d{2,7}', , , , , , , [3, 4, 5, 6, 7, 8]],
                  ,
                  ,
                  [, , '000|1(?:06|12|258885|55\\d)|733', , , , '000', , , [3, 4, 7]],
                  [, , '1(?:2(?:34|456)|9\\d{4,6})', , , , '1234', , , [4, 5, 6, 7, 8]],
                  ,
                  ,
                  ,
                  'AU',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '000|1(?:06|12)', , , , '000', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '000|1(?:06|1(?:00|2|9[46])|2(?:014[1-3]|[23]\\d|(?:4|5\\d)\\d{2,3}|68[689]|72(?:20|3\\d\\d)|8(?:[013-9]\\d|2))|555|9\\d{4,6})|225|7(?:33|67)',
                    ,
                    ,
                    ,
                    '000'
                  ],
                  [, , '1(?:1[09]\\d|24733)|225|767', , , , '225', , , [3, 4, 6]],
                  [, , '1(?:258885|55\\d)', , , , '1550', , , [4, 7]],
                  ,
                  [, , '19\\d{4,6}', , , , '190000', , , [6, 7, 8]]
                ],
                AW: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '100|911', , , , '100'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AW',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '100|911', , , , '100'],
                  ,
                  [, , '1(?:00|18|76)|91[13]', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , '176', , , , '176'],
                  ,
                  [, , '176', , , , '176']
                ],
                AX: [
                  ,
                  [, , '[17]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '112', , , , '112', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AX',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112', , , , '112', , , [3]],
                  ,
                  [, , '112|75[12]\\d\\d', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                AZ: [
                  ,
                  [, , '[148]\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:0[1-3]|12)', , , , '101', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'AZ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:0[1-3]|12)', , , , '101', , , [3]],
                  ,
                  [, , '1(?:0[1-3]|12)|(?:404|880)0', , , , '101'],
                  [, , , , , , , , , [-1]],
                  [, , '(?:404|880)\\d', , , , '4040', , , [4]],
                  ,
                  [, , '(?:404|880)\\d', , , , '4040', , , [4]]
                ],
                BA: [
                  ,
                  [, , '1\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:16\\d{3}|2[2-4])', , , , '122', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BA',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '12[2-4]', , , , '122', , , [3]],
                  ,
                  [, , '1(?:16(?:00[06]|1(?:1[17]|23))|2(?:0[0-7]|[2-5]|6[0-26])|(?:[3-5]|7\\d)\\d\\d)|1(?:18|2[78])\\d\\d?', , , , '122'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BB: [
                  ,
                  [, , '[2-689]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '988|[2359]11', , , , '211'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BB',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '[2359]11', , , , '211'],
                  ,
                  [, , '988|[2-689]11', , , , '211'],
                  [, , , , , , , , , [-1]],
                  [, , '[468]11', , , , '411'],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BD: [
                  ,
                  [, , '[1579]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '10[0-26]|[19]99', , , , '100', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BD',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '10[0-2]|[19]99', , , , '100', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0(?:[0-369]|5[1-4]|7[0-4]|8[0-29])|1[16-9]|2(?:[134]|2[0-5])|3(?:1\\d?|6[3-6])|5[2-9])|5012|786|9594|[19]99|1(?:0(?:50|6\\d)|33|4(?:0|1\\d))\\d',
                    ,
                    ,
                    ,
                    '100'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , '1(?:11|2[13])|(?:501|959)\\d|786', , , , '111', , , [3, 4]],
                  ,
                  [, , '959\\d', , , , '9590', , , [4]]
                ],
                BE: [
                  ,
                  [, , '[1-9]\\d\\d(?:\\d(?:\\d{2})?)?', , , , , , , [3, 4, 6]],
                  ,
                  ,
                  [, , '1(?:0[0-35-8]|1[0269]|7(?:12|77)|813)|(?:116|8)\\d{3}', , , , '100'],
                  [, , '1(?:2[03]|40)4|(?:1(?:[24]1|3[01])|[2-79]\\d\\d)\\d', , , , '1204', , , [4]],
                  ,
                  ,
                  ,
                  'BE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:0[01]|12)', , , , '100', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0[0-8]|16117|2(?:12|3[0-24])|313|414|5(?:1[05]|5[15]|66|95)|6(?:1[167]|36|6[16])|7(?:[07][017]|1[27-9]|22|33|65)|81[39])|[2-9]\\d{3}|11[02679]|1(?:1600|45)0|1(?:[2-4]9|78)9|1[2-4]0[47]',
                    ,
                    ,
                    ,
                    '100'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '[2-9]\\d{3}', , , , '2000', , , [4]]
                ],
                BF: [
                  ,
                  [, , '1\\d', , , , , , , [2]],
                  ,
                  ,
                  [, , '1[78]', , , , '17'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BF',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[78]', , , , '17'],
                  ,
                  [, , '1[78]', , , , '17'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BG: [
                  ,
                  [, , '1\\d\\d(?:\\d{3})?', , , , , , , [3, 6]],
                  ,
                  ,
                  [, , '1(?:1(?:2|6\\d{3})|50|6[06])', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BG',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|50|6[06])', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:2|6(?:000|111))|50|6[06])', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BH: [
                  ,
                  [, , '[0189]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '(?:0[167]|81)\\d{3}|[19]99', , , , '199'],
                  [, , '9[148]\\d{3}', , , , '91000', , , [5]],
                  ,
                  ,
                  ,
                  'BH',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '[19]99', , , , '199', , , [3]],
                  ,
                  [, , '1(?:[02]\\d|12|4[01]|51|8[18]|9[169])|99[02489]|(?:0[167]|8[158]|9[148])\\d{3}', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , '0[67]\\d{3}|88000|98555', , , , '06000', , , [5]],
                  ,
                  [, , '88000|98555', , , , '88000', , , [5]]
                ],
                BI: [
                  ,
                  [, , '[16-9]\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '11[237]|611', , , , '112', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BI',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[237]', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1\\d|5[2-9]|6[0-256])|611|7(?:10|77|979)|8[28]8|900', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , '611|7(?:10|77)|888|900', , , , '611', , , [3]],
                  ,
                  [, , '(?:71|90)0', , , , '710', , , [3]]
                ],
                BJ: [
                  ,
                  [, , '[17]\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:1[246-8]|3[68]|6[06])|7[3-5]\\d\\d', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BJ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[246-8]', , , , '112', , , [3]],
                  ,
                  [, , '1(?:05|1[24-8]|2[02-5]|3[126-8]|5[05]|6[06]|89)|7[0-5]\\d\\d', , , , '105'],
                  [, , , , , , , , , [-1]],
                  [, , '12[02-5]', , , , '120', , , [3]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BL: [
                  ,
                  [, , '1\\d', , , , , , , [2]],
                  ,
                  ,
                  [, , '18', , , , '18'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BL',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '18', , , , '18'],
                  ,
                  [, , '18', , , , '18'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BM: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '911', , , , '911'],
                  ,
                  [, , '176|9(?:11|88)', , , , '176'],
                  [, , , , , , , , , [-1]],
                  [, , '176', , , , '176'],
                  ,
                  [, , '176', , , , '176']
                ],
                BN: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '99[135]', , , , '991'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BN',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '99[135]', , , , '991'],
                  ,
                  [, , '99[135]', , , , '991'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BO: [
                  ,
                  [, , '[14]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '11[089]', , , , '110', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BO',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[089]', , , , '110', , , [3]],
                  ,
                  [, , '11[089]|40404', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                BQ: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '112|911', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BQ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|911', , , , '112'],
                  ,
                  [, , '1(?:12|76)|911', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , '176', , , , '176'],
                  ,
                  [, , '176', , , , '176']
                ],
                BR: [
                  ,
                  [, , '[1-69]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:00|12|28|8[015]|9[0-47-9])|4(?:57|82\\d)|911', , , , '100', , , [3, 4]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|28|9[023])|911', , , , '112', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0(?:[02]|3(?:1[2-579]|2[13-9]|3[124-9]|4[1-3578]|5[1-468]|6[139]|8[149]|9[168])|5[0-35-9]|6(?:0|1[0-35-8]?|2[0145]|3[0137]?|4[37-9]?|5[0-35]|6[016]?|7[137]?|8[5-8]|9[1359]))|1[25-8]|2[357-9]|3[024-68]|4[12568]|5\\d|6[0-8]|8[015]|9[0-47-9])|2(?:7(?:330|878)|85959?)|(?:32|91)1|4(?:0404?|57|828)|55555|6(?:0\\d{4}|10000)|(?:133|411)[12]',
                    ,
                    ,
                    ,
                    '100'
                  ],
                  [, , '102|273\\d\\d|321', , , , '102', , , [3, 5]],
                  [, , '151|(?:278|555)\\d\\d|4(?:04\\d\\d?|11\\d|57)', , , , '151', , , [3, 4, 5]],
                  ,
                  [, , '285\\d{2,3}|321|40404|(?:27[38]\\d|482)\\d|6(?:0\\d|10)\\d{3}', , , , '321']
                ],
                BS: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:1[19]|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BS',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '91[19]', , , , '911'],
                  ,
                  [, , '9(?:1[19]|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BT: [
                  ,
                  [, , '[14]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '11[023]', , , , '110', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BT',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[023]', , , , '110', , , [3]],
                  ,
                  [, , '11[0-6]|40404', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                BW: [
                  ,
                  [, , '[19]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '9(?:11|9[7-9])', , , , '911', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BW',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:11|9[7-9])', , , , '911', , , [3]],
                  ,
                  [, , '1(?:1[26]|3123)|9(?:1[14]|9[1-57-9])', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , '131\\d\\d', , , , '13100', , , [5]],
                  ,
                  [, , '131\\d\\d', , , , '13100', , , [5]]
                ],
                BY: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '1(?:0[1-3]|12)', , , , '101'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BY',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:0[1-3]|12)', , , , '101'],
                  ,
                  [, , '1(?:0[1-79]|1[246]|35|5[1-35]|6[89]|7[5-7]|8[58]|9[1-7])', , , , '101'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                BZ: [
                  ,
                  [, , '9\\d\\d?', , , , , , , [2, 3]],
                  ,
                  ,
                  [, , '9(?:0|11|22|66|77|9[09])', , , , '90'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'BZ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:0|11|90)', , , , '90'],
                  ,
                  [, , '9(?:0|11|22|66|77|9[09])', , , , '90'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CA: [
                  ,
                  [, , '[1-9]\\d\\d(?:\\d{2,3})?', , , , , , , [3, 5, 6]],
                  ,
                  ,
                  [, , '112|988|[29]11', , , , '112', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CA',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|911', , , , '112', , , [3]],
                  ,
                  [, , '[1-35-9]\\d{4,5}|112|[2-8]11|9(?:11|88)', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , '[235-7]11', , , , '211', , , [3]],
                  ,
                  [, , '[1-35-9]\\d{4,5}', , , , '10000', , , [5, 6]]
                ],
                CC: [
                  ,
                  [, , '[01]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '000|112', , , , '000'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CC',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '000|112', , , , '000'],
                  ,
                  [, , '000|112', , , , '000'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CD: [
                  ,
                  [, , '[14]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '1(?:1[348]|77|88)', , , , '113', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CD',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[348]|77|88)', , , , '113', , , [3]],
                  ,
                  [, , '1(?:1[348]|23|77|88)|40404', , , , '113'],
                  [, , , , , , , , , [-1]],
                  [, , '404\\d\\d', , , , '40400', , , [5]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                CF: [
                  ,
                  [, , '1\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:1[78]|22\\d)', , , , '117'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CF',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[78]|220)', , , , '117'],
                  ,
                  [, , '1(?:1[478]|220)', , , , '114'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CG: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[178]', , , , '111'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CG',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[78]', , , , '117'],
                  ,
                  [, , '11[126-8]', , , , '111'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CH: [
                  ,
                  [, , '[1-9]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:1(?:[278]|6\\d{3})|4[47])|5200', , , , '112', , , [3, 4, 6]],
                  [, , '1(?:14|8[0-2589])\\d|543|83111', , , , '543', , , [3, 4, 5]],
                  ,
                  ,
                  ,
                  'CH',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[278]|44)', , , , '112', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0[78]\\d\\d|1(?:[278]|45|6(?:000|111))|4(?:[03-57]|1[0145])|6(?:00|[1-46])|8(?:02|1[189]|[25]0|7|8[08]|99))|[2-9]\\d{2,4}',
                    ,
                    ,
                    ,
                    '112'
                  ],
                  [, , '1(?:4[035]|6[1-46])|1(?:41|60)\\d', , , , '140', , , [3, 4]],
                  [, , '5(?:200|35)', , , , '535', , , [3, 4]],
                  ,
                  [, , '[2-9]\\d{2,4}', , , , '200', , , [3, 4, 5]]
                ],
                CI: [
                  ,
                  [, , '[14]\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:1[01]|[78]0)', , , , '110', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CI',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[01]|[78]0)', , , , '110', , , [3]],
                  ,
                  [, , '1(?:1[01]|[78]0)|4443', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , '444\\d', , , , '4440', , , [4]],
                  ,
                  [, , '444\\d', , , , '4440', , , [4]]
                ],
                CK: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '99[689]', , , , '996'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CK',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '99[689]', , , , '996'],
                  ,
                  [, , '99[689]', , , , '996'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CL: [
                  ,
                  [, , '[1-9]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '1(?:213|3[1-3])|434\\d|911', , , , '131', , , [3, 4]],
                  [
                    ,
                    ,
                    '1(?:211|3(?:13|[348]0|5[01]))|(?:1(?:[05]6|[48]1|9[18])|2(?:01\\d|[23]2|77|88)|3(?:0[59]|13|3[279]|66)|4(?:[12]4|36\\d|4[017]|55)|5(?:00|41\\d|5[67]|99)|6(?:07\\d|13|22|3[06]|50|69)|787|8(?:[01]1|[48]8)|9(?:01|[12]0|33))\\d',
                    ,
                    ,
                    ,
                    '1060',
                    ,
                    ,
                    [4, 5]
                  ],
                  ,
                  ,
                  ,
                  'CL',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '13[1-3]|911', , , , '131', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:00|21[13]|3(?:13|[348]0|5[01])|4(?:0[02-6]|17|[379])|818|919)|2(?:0(?:01|122)|22[47]|323|777|882)|3(?:0(?:51|99)|132|3(?:29|[37]7)|665)|43656|5(?:(?:00|415)4|5(?:66|77)|995)|6(?:131|222|366|699)|7878|8(?:011|11[28]|482|889)|9(?:01|1)1|13\\d|4(?:[13]42|243|4(?:02|15|77)|554)|(?:1(?:[05]6|98)|339|6(?:07|[35])0|9(?:[12]0|33))0',
                    ,
                    ,
                    ,
                    '100'
                  ],
                  [, , '(?:200|333)\\d', , , , '2000', , , [4]],
                  [, , , , , , , , , [-1]],
                  ,
                  [
                    ,
                    ,
                    '13(?:13|[348]0|5[01])|(?:1(?:[05]6|[28]1|4[01]|9[18])|2(?:0(?:0|1\\d)|[23]2|77|88)|3(?:0[59]|13|3[2379]|66)|436\\d|5(?:00|41\\d|5[67]|99)|6(?:07\\d|13|22|3[06]|50|69)|787|8(?:[01]1|[48]8)|9(?:01|[12]0|33))\\d|4(?:[1-3]4|4[017]|55)\\d',
                    ,
                    ,
                    ,
                    '1060',
                    ,
                    ,
                    [4, 5]
                  ]
                ],
                CM: [
                  ,
                  [, , '[18]\\d{1,3}', , , , , , , [2, 3, 4]],
                  ,
                  ,
                  [, , '1(?:1[37]|[37])', , , , '13', , , [2, 3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[37]|[37])', , , , '13', , , [2, 3]],
                  ,
                  [, , '1(?:1[37]|[37])|8711', , , , '13'],
                  [, , , , , , , , , [-1]],
                  [, , '871\\d', , , , '8710', , , [4]],
                  ,
                  [, , '871\\d', , , , '8710', , , [4]]
                ],
                CN: [
                  ,
                  [, , '[19]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:1[09]|2(?:[02]|1\\d\\d|395))', , , , '110', , , [3, 5]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CN',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[09]|20)', , , , '110', , , [3]],
                  ,
                  [, , '1(?:00|1[0249]|2395|6[08])|9[56]\\d{3,4}|12[023]|1(?:0(?:[0-26]\\d|8)|21\\d)\\d', , , , '100'],
                  [, , '1(?:0(?:[0-26]\\d|8)\\d|1[24]|23|6[08])|9[56]\\d{3,4}|100', , , , '100'],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '12110', , , , '12110', , , [5]]
                ],
                CO: [
                  ,
                  [, , '[1-589]\\d\\d(?:\\d{2,3})?', , , , , , , [3, 5, 6]],
                  ,
                  ,
                  [, , '1(?:1[29]|23|32|56)', , , , '112', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CO',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[29]|23|32|56)', , , , '112', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:06|1[2-9]|2[35-7]|3[27]|4[467]|5[36]|6[4-7]|95)|(?:29002|39003)9|40404|5930\\d\\d|85432|(?:[2359][57]|8(?:7|9\\d))\\d{3}',
                    ,
                    ,
                    ,
                    '106'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , '(?:40|85)4\\d\\d', , , , '40400', , , [5]],
                  ,
                  [, , '(?:40|85)4\\d\\d', , , , '40400', , , [5]]
                ],
                CR: [
                  ,
                  [, , '[1359]\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '112|911', , , , '112', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|911', , , , '112', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0(?:00|15|2[2-4679])|1(?:1[0-35-9]|2|37|[46]6|7[57]|8[79]|9[0-379])|2(?:00|[12]2|34|55)|3(?:21|33)|4(?:0[06]|1[4-6])|5(?:15|5[15])|693|7(?:00|1[7-9]|2[02]|[67]7)|975)|3855|5(?:0(?:30|49)|510)|911',
                    ,
                    ,
                    ,
                    '112'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '(?:385|5(?:0[34]|51))\\d', , , , '3850', , , [4]]
                ],
                CU: [
                  ,
                  [, , '[12]\\d\\d(?:\\d{3,4})?', , , , , , , [3, 6, 7]],
                  ,
                  ,
                  [, , '10[4-7]|(?:116|204\\d)\\d{3}', , , , '104'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CU',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '10[4-6]', , , , '104', , , [3]],
                  ,
                  [, , '1(?:0[4-7]|1(?:6111|8)|40)|2045252', , , , '104'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CV: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '13[0-2]', , , , '130'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CV',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '13[0-2]', , , , '130'],
                  ,
                  [, , '13[0-2]', , , , '130'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CW: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '112|911', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CW',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|911', , , , '112'],
                  ,
                  [, , '1(?:12|76)|911', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , '176', , , , '176'],
                  ,
                  [, , '176', , , , '176']
                ],
                CX: [
                  ,
                  [, , '[01]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '000|112', , , , '000'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CX',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '000|112', , , , '000'],
                  ,
                  [, , '000|112', , , , '000'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CY: [
                  ,
                  [, , '1\\d\\d(?:\\d{3})?', , , , , , , [3, 6]],
                  ,
                  ,
                  [, , '1(?:1(?:2|6\\d{3})|99)', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CY',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|99)', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:2|6(?:000|111))|99)', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                CZ: [
                  ,
                  [, , '1\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:1(?:2|6(?:00[06]|1(?:11|23)))|5\\d)', , , , '112', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'CZ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|5\\d)', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:2|8\\d)|(?:2|3\\d)\\d{2,3}|5\\d|99)|1(?:16|4)\\d{3}', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                DE: [
                  ,
                  [, , '[1-478]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '11(?:[02]|6\\d{3})', , , , '110', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'DE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[02]', , , , '110', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '11(?:[025]|6(?:00[06]|1(?:1[167]|23))|800\\d)|22(?:044|5(?:43|80)|7700|922)|33(?:11|3[34])|44844|7(?:0\\d{3}|464)|80808|118\\d\\d',
                    ,
                    ,
                    ,
                    '110'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , '(?:33[13]|746)\\d|(?:22(?:[059]|7\\d)|(?:44|80)8|70\\d)\\d\\d', , , , '3310', , , [4, 5, 6]],
                  ,
                  [, , '(?:333|746)\\d|(?:22(?:[059]|7\\d)|(?:44|80)8|70\\d)\\d\\d', , , , '3330', , , [4, 5, 6]]
                ],
                DJ: [
                  ,
                  [, , '1\\d', , , , , , , [2]],
                  ,
                  ,
                  [, , '1[78]', , , , '17'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'DJ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[78]', , , , '17'],
                  ,
                  [, , '1[78]', , , , '17'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                DK: [
                  ,
                  [, , '1\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '11(?:[24]|6\\d{3})', , , , '112', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'DK',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[24]', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:[248]|6(?:00[06]|111))|619[0-2]|8(?:01|1[0238]|28|30|5[13]|8[18]))', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                DM: [
                  ,
                  [, , '[39]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '333|9(?:11|88|99)', , , , '333'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'DM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '333|9(?:11|99)', , , , '333'],
                  ,
                  [, , '333|9(?:11|88|99)', , , , '333'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                DO: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '112|9(?:11|88)', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'DO',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|911', , , , '112'],
                  ,
                  [, , '112|9(?:11|88)', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                DZ: [
                  ,
                  [, , '[17]\\d{1,3}', , , , , , , [2, 3, 4]],
                  ,
                  ,
                  [, , '1(?:12|[47]|54\\d)', , , , '14'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'DZ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|[47])', , , , '14', , , [2, 3]],
                  ,
                  [, , '1(?:055|12|[47]|548)|730', , , , '14'],
                  [, , , , , , , , , [-1]],
                  [, , '730', , , , '730', , , [3]],
                  ,
                  [, , '730', , , , '730', , , [3]]
                ],
                EC: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '1(?:0[12]|12)|911', , , , '101'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'EC',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:0[12]|12)|911', , , , '101'],
                  ,
                  [, , '1(?:0[12]|12)|911', , , , '101'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                EE: [
                  ,
                  [, , '1\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [
                    ,
                    ,
                    '1(?:1(?:[02]|6\\d{3})|2(?:05|28)|3(?:014|3(?:21|5\\d?)|660)|492|5(?:1[03]|410|501)|6(?:112|333|644)|7(?:012|127|89)|8(?:10|8[57])|9(?:0[134]|14))',
                    ,
                    ,
                    ,
                    '110'
                  ],
                  [
                    ,
                    ,
                    '1(?:18(?:00|[12458]\\d?)|2(?:0(?:[02-46-8]\\d?|1[0-36])|1(?:[0-4]\\d?|6[06])|2(?:[0-4]\\d?|5[25])|[367]|4(?:0[04]|[12]\\d?|4[24]|54)|55[12457])|3(?:0(?:[02]\\d?|1[13578]|3[356])|1[1347]|2[02-5]|3(?:[01347]\\d?|2[023]|88)|4(?:[35]\\d?|4[34])|5(?:3[134]|5[035])|666)|4(?:2(?:00|4\\d?)|4(?:0[01358]|1[024]|50|7\\d?)|900)|5(?:0[0-35]|1(?:[1267]\\d?|5[0-7]|82)|2(?:[014-6]\\d?|22)|330|4(?:[35]\\d?|44)|5(?:00|[1-69]\\d?)|9(?:[159]\\d?|[38]0|77))|6(?:1(?:00|1[19]|[35-9]\\d?)|2(?:2[26]|[68]\\d?)|3(?:22|36|6[36])|5|6(?:[0-359]\\d?|6[0-26])|7(?:00|55|7\\d?|8[89])|9(?:00|1\\d?|69))|7(?:0(?:[023]\\d?|1[0578])|1(?:00|2[034]|[4-9]\\d?)|2(?:[07]\\d?|20|44)|7(?:[0-57]\\d?|9[79])|8(?:0[08]|2\\d?|8[0178])|9(?:00|97))|8(?:1[127]|8[1268]|9[269])|9(?:0(?:[02]\\d?|69|9[0269])|1[1-3689]|21))',
                    ,
                    ,
                    ,
                    '123',
                    ,
                    ,
                    [3, 4, 5]
                  ],
                  ,
                  ,
                  ,
                  'EE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[02]', , , , '110', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:1(?:[02-579]|6(?:000|111)|8(?:[09]\\d|[1-8]))|2[36-9]|3[7-9]|4[05-7]|5[6-8]|6[05]|7[3-6]|8[02-7]|9[3-9])|1(?:2[0-245]|3[0-6]|4[1-489]|5[0-59]|6[1-46-9]|7[0-27-9]|8[189]|9[0-2])\\d\\d?',
                    ,
                    ,
                    ,
                    '110'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [
                    ,
                    ,
                    '1(?:18[1258]|2(?:0(?:1[036]|[46]\\d?)|166|21|4(?:0[04]|1\\d?|5[47])|[67])|3(?:0(?:1[13-578]|2\\d?|3[56])|1[15]|2[045]|3(?:[13]\\d?|2[13])|43|5(?:00|3[34]|53))|44(?:0[0135]|14|50|7\\d?)|5(?:05|1(?:[12]\\d?|5[1246]|8[12])|2(?:[01]\\d?|22)|3(?:00|3[03])|4(?:15|5\\d?)|500|9(?:5\\d?|77|80))|6(?:1[35-8]|226|3(?:22|3[36]|66)|644|7(?:00|7\\d?|89)|9(?:00|69))|7(?:01[258]|1(?:00|[15]\\d?)|2(?:44|7\\d?)|8(?:00|87|9\\d?))|8(?:1[128]|8[56]|9(?:[26]\\d?|77))|90(?:2\\d?|69|92))',
                    ,
                    ,
                    ,
                    '126',
                    ,
                    ,
                    [3, 4, 5]
                  ]
                ],
                EG: [
                  ,
                  [, , '[13]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '1(?:2[23]|80)', , , , '122', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'EG',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:2[23]|80)', , , , '122', , , [3]],
                  ,
                  [, , '1(?:2[23]|[679]\\d{3}|80)|34400', , , , '122'],
                  [, , , , , , , , , [-1]],
                  [, , '344\\d\\d', , , , '34400', , , [5]],
                  ,
                  [, , '344\\d\\d', , , , '34400', , , [5]]
                ],
                EH: [
                  ,
                  [, , '1\\d\\d?', , , , , , , [2, 3]],
                  ,
                  ,
                  [, , '1(?:[59]|77)', , , , '15'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'EH',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:[59]|77)', , , , '15'],
                  ,
                  [, , '1(?:[59]|77)', , , , '15'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ER: [
                  ,
                  [, , '[12]\\d\\d(?:\\d{3})?', , , , , , , [3, 6]],
                  ,
                  ,
                  [, , '11[2-46]|(?:12[47]|20[12])\\d{3}', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'ER',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[2-46]|24422)|20(?:1(?:606|917)|2914)|(?:1277|2020)99', , , , '112'],
                  ,
                  [, , '1(?:1[2-6]|24422)|20(?:1(?:606|917)|2914)|(?:1277|2020)99', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ES: [
                  ,
                  [, , '[0-379]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '0(?:16|6[57]|8[58])|1(?:006|12|[3-7]\\d\\d)|(?:116|20\\d)\\d{3}', , , , '016', , , [3, 4, 6]],
                  [, , '[12]2\\d{1,4}|90(?:5\\d|7)|(?:118|2(?:[357]\\d|80)|3[357]\\d)\\d\\d|[79]9[57]\\d{3}', , , , '120'],
                  ,
                  ,
                  ,
                  'ES',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '08[58]|112', , , , '085', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '0(?:1[0-26]|6[0-257]|8[058]|9[12])|1(?:0[03-57]\\d{1,3}|1(?:2|6(?:000|111)|8\\d\\d)|2\\d{1,4}|[3-9]\\d\\d)|2(?:2\\d{1,4}|80\\d\\d)|90(?:5[124578]|7)|1(?:3[34]|77)|(?:2[01]\\d|[79]9[57])\\d{3}|[23][357]\\d{3}',
                    ,
                    ,
                    ,
                    '010'
                  ],
                  [, , '0(?:[16][0-2]|80|9[12])|21\\d{4}', , , , '010', , , [3, 6]],
                  [, , '1(?:3[34]|77)|[12]2\\d{1,4}', , , , '120'],
                  ,
                  [, , '(?:2[0-2]\\d|3[357]|[79]9[57])\\d{3}|2(?:[2357]\\d|80)\\d\\d', , , , '22000', , , [5, 6]]
                ],
                ET: [
                  ,
                  [, , '9\\d\\d?', , , , , , , [2, 3]],
                  ,
                  ,
                  [, , '9(?:07|11?|2|39?|9[17])', , , , '91'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'ET',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:11?|2|39?|9[17])', , , , '91'],
                  ,
                  [, , '9(?:07|11?|2|39?|45|9[17])', , , , '91'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                FI: [
                  ,
                  [, , '[17]\\d\\d(?:\\d{2,3})?', , , , , , , [3, 5, 6]],
                  ,
                  ,
                  [, , '11(?:2|6\\d{3})', , , , '112', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'FI',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112', , , , '112', , , [3]],
                  ,
                  [, , '11(?:2|6(?:00[06]|1(?:1[17]|23)))|(?:1[2-8]\\d|75[12])\\d\\d', , , , '112'],
                  [, , '1[2-8]\\d{3}', , , , '12000', , , [5]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                FJ: [
                  ,
                  [, , '[0-579]\\d(?:\\d(?:\\d{2})?)?', , , , , , , [2, 3, 5]],
                  ,
                  ,
                  [, , '91[17]', , , , '911', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'FJ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '91[17]', , , , '911', , , [3]],
                  ,
                  [, , '0(?:1[34]|8[1-4])|1(?:0[1-3]|[25]9)|2[289]|30|40404|91[137]|[45]4|75', , , , '22'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                FK: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '999', , , , '999'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'FK',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '999', , , , '999'],
                  ,
                  [, , '1\\d\\d|999', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                FM: [
                  ,
                  [, , '[39]\\d\\d(?:\\d{3})?', , , , , , , [3, 6]],
                  ,
                  ,
                  [, , '320\\d{3}|911', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'FM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '(?:32022|91)1', , , , '911'],
                  ,
                  [, , '(?:32022|91)1', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                FO: [
                  ,
                  [, , '1\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:1[24]|81\\d)', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'FO',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[24]', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1[248]|819)|1(?:4[124]|71|8[7-9])\\d', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                FR: [
                  ,
                  [, , '[1-8]\\d{1,5}', , , , , , , [2, 3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:1[02459]|[578]|9[167])|224|(?:3370|74)0|(?:116\\d|3[01])\\d\\d', , , , '15'],
                  [, , '(?:1(?:0|18\\d)|366|[4-8]\\d\\d)\\d\\d|3[2-9]\\d\\d', , , , '1000', , , [4, 5, 6]],
                  ,
                  ,
                  ,
                  'FR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|[578])', , , , '15', , , [2, 3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0\\d\\d|1(?:[02459]|6(?:000|111)|8\\d{3})|[578]|9[167])|2(?:0(?:00|2)0|24)|[3-8]\\d{4}|3\\d{3}|6(?:1[14]|34)|7(?:0[06]|22|40)',
                    ,
                    ,
                    ,
                    '15'
                  ],
                  [, , '202\\d|6(?:1[14]|34)|70[06]', , , , '611', , , [3, 4]],
                  [, , '118777|224|6(?:1[14]|34)|7(?:0[06]|22|40)|20(?:0\\d|2)\\d', , , , '224', , , [3, 4, 5, 6]],
                  ,
                  [, , '114|[3-8]\\d{4}', , , , '114', , , [3, 5]]
                ],
                GA: [
                  ,
                  [, , '1\\d(?:\\d{2})?', , , , , , , [2, 4]],
                  ,
                  ,
                  [, , '18|1(?:3\\d|73)\\d', , , , '18'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GA',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:3\\d\\d|730|8)', , , , '18'],
                  ,
                  [, , '1(?:3\\d\\d|730|8)', , , , '18'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GB: [
                  ,
                  [, , '[1-46-9]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:05|1(?:[29]|6\\d{3})|7[56]\\d|8000)|2(?:20\\d|48)|4444|999', , , , '105'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GB',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|999', , , , '112', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0[015]|1(?:[129]|6(?:000|1(?:11|23))|8\\d{3})|2(?:[1-3]|50)|33|4(?:1|7\\d)|571|7(?:0\\d|[56]0)|800\\d|9[15])|2(?:0202|1300|2(?:02|11)|3(?:02|336|45)|4(?:25|8))|3[13]3|4(?:0[02]|35[01]|44[45]|5\\d)|(?:[68]\\d|7[089])\\d{3}|15\\d|2[02]2|650|789|9(?:01|99)',
                    ,
                    ,
                    ,
                    '100'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , '1(?:(?:25|7[56])\\d|571)|2(?:02(?:\\d{2})?|[13]3\\d\\d|48)|4444|901', , , , '202', , , [3, 4, 5]],
                  ,
                  [, , '(?:125|2(?:020|13\\d)|(?:7[089]|8[01])\\d\\d)\\d', , , , '1250', , , [4, 5]]
                ],
                GD: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GD',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '911', , , , '911'],
                  ,
                  [, , '176|9(?:11|88)', , , , '176'],
                  [, , , , , , , , , [-1]],
                  [, , '176', , , , '176'],
                  ,
                  [, , '176', , , , '176']
                ],
                GE: [
                  ,
                  [, , '[014]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '0(?:11|33)|11[1-3]|[01]22', , , , '011', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '0(?:11|33)|11[1-3]|[01]22', , , , '011', , , [3]],
                  ,
                  [, , '0(?:11|33)|11[1-3]|40404|[01]22', , , , '011'],
                  [, , , , , , , , , [-1]],
                  [, , '404\\d\\d', , , , '40400', , , [5]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                GF: [
                  ,
                  [, , '1\\d', , , , , , , [2]],
                  ,
                  ,
                  [, , '1[578]', , , , '15'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GF',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[578]', , , , '15'],
                  ,
                  [, , '1[578]', , , , '15'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GG: [
                  ,
                  [, , '[19]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '112|999', , , , '112', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GG',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|999', , , , '112', , , [3]],
                  ,
                  [, , '1(?:0[01]|1[12]|23|41|55|9[05])|999|1(?:1[68]\\d\\d|47|800)\\d', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GH: [
                  ,
                  [, , '[14589]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '19[1-3]|999', , , , '191', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GH',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '19[1-3]|999', , , , '191', , , [3]],
                  ,
                  [, , '19[1-3]|40404|(?:54|83)00|999', , , , '191'],
                  [, , , , , , , , , [-1]],
                  [, , '404\\d\\d|(?:54|83)0\\d', , , , '5400', , , [4, 5]],
                  ,
                  [, , '404\\d\\d|(?:54|83)0\\d', , , , '5400', , , [4, 5]]
                ],
                GI: [
                  ,
                  [, , '[158]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:00|1[25]|23|4(?:1|7\\d)|5[15]|9[02-49])|555|(?:116\\d|80)\\d\\d', , , , '100', , , [3, 4, 6]],
                  [, , '8[1-69]\\d\\d', , , , '8100', , , [4]],
                  ,
                  ,
                  ,
                  'GI',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|9[09])', , , , '112', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:00|1(?:[25]|6(?:00[06]|1(?:1[17]|23))|8\\d\\d)|23|4(?:1|7[014])|5[015]|9[02-49])|555|8[0-79]\\d\\d|8(?:00|4[0-2]|8[0-589])',
                    ,
                    ,
                    ,
                    '100'
                  ],
                  [, , '150|87\\d\\d', , , , '150', , , [3, 4]],
                  [, , '1(?:00|1(?:5|8\\d\\d)|23|51|9[2-4])|555|8(?:00|4[0-2]|8[0-589])', , , , '100', , , [3, 5]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GL: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '112', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GL',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112', , , , '112'],
                  ,
                  [, , '1[1-8]\\d', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GM: [
                  ,
                  [, , '1\\d\\d?', , , , , , , [2, 3]],
                  ,
                  ,
                  [, , '1(?:1[6-8]|[6-8])', , , , '16'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[6-8]|[6-8])', , , , '16'],
                  ,
                  [, , '1(?:1[6-8]|[6-8])', , , , '16'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GN: [
                  ,
                  [, , '[14]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GN',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '12\\d|40404', , , , '120'],
                  [, , , , , , , , , [-1]],
                  [, , '404\\d\\d', , , , '40400', , , [5]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                GP: [
                  ,
                  [, , '1\\d', , , , , , , [2]],
                  ,
                  ,
                  [, , '1[578]', , , , '15'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GP',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[578]', , , , '15'],
                  ,
                  [, , '1[578]', , , , '15'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GR: [
                  ,
                  [, , '1\\d\\d(?:\\d{2,3})?', , , , , , , [3, 5, 6]],
                  ,
                  ,
                  [, , '1(?:0[089]|1(?:2|6\\d{3})|66|99)', , , , '100', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:00|12|66|99)', , , , '100', , , [3]],
                  ,
                  [, , '1(?:0[089]|1(?:2|320|6(?:000|1(?:1[17]|23)))|(?:389|9)9|66)', , , , '100'],
                  [, , '113\\d\\d', , , , '11300', , , [5]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GT: [
                  ,
                  [, , '[14]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '1(?:10|2[03])', , , , '110', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GT',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:10|2[03])', , , , '110', , , [3]],
                  ,
                  [, , '110|40404|1(?:2|[57]\\d)\\d', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , '404\\d\\d', , , , '40400', , , [5]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                GU: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GU',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '911', , , , '911'],
                  ,
                  [, , '9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GW: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[378]', , , , '113'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GW',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[378]', , , , '113'],
                  ,
                  [, , '11[378]', , , , '113'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                GY: [
                  ,
                  [, , '[019]\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '91[1-3]', , , , '911', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'GY',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '91[1-3]', , , , '911', , , [3]],
                  ,
                  [, , '0(?:02|(?:17|80)1|444|7(?:[67]7|9)|9(?:0[78]|[2-47]))|1(?:443|5[568])|91[1-3]', , , , '002'],
                  [, , , , , , , , , [-1]],
                  [, , '144\\d', , , , '1440', , , [4]],
                  ,
                  [, , '144\\d', , , , '1440', , , [4]]
                ],
                HK: [
                  ,
                  [, , '[19]\\d{2,6}', , , , , , , [3, 4, 5, 6, 7]],
                  ,
                  ,
                  [, , '112|99[29]', , , , '112', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'HK',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|99[29]', , , , '112', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0(?:(?:[0136]\\d|2[14])\\d{0,3}|8[138])|12|2(?:[0-3]\\d{0,4}|(?:58|8[13])\\d{0,3})|7(?:[135-9]\\d{0,4}|219\\d{0,2})|8(?:0(?:(?:[13]|60\\d)\\d|8)|1(?:0\\d|[2-8])|2(?:0[5-9]|(?:18|2)2|3|8[128])|(?:(?:3[0-689]\\d|7(?:2[1-389]|8[0235-9]|93))\\d|8)\\d|50[138]|6(?:1(?:11|86)|8)))|99[29]|10[0139]',
                    ,
                    ,
                    ,
                    '100'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , '109|1(?:08|85\\d)\\d', , , , '109', , , [3, 4, 5]],
                  ,
                  [, , '992', , , , '992', , , [3]]
                ],
                HN: [
                  ,
                  [, , '[14]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '199', , , , '199', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'HN',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '199', , , , '199', , , [3]],
                  ,
                  [, , '199|40404', , , , '199'],
                  [, , , , , , , , , [-1]],
                  [, , '404\\d\\d', , , , '40400', , , [5]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                HR: [
                  ,
                  [, , '[016-9]\\d{1,5}', , , , , , , [2, 3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:12|9[2-4])|9[34]|1(?:16\\d|39)\\d\\d', , , , '93', , , [2, 3, 5, 6]],
                  [, , '06\\d|(?:118|[6-8]\\d{3})\\d\\d', , , , '060', , , [3, 5, 6]],
                  ,
                  ,
                  ,
                  'HR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|9[2-4])|9[34]', , , , '93', , , [2, 3]],
                  ,
                  [, , '(?:06|[6-8]\\d{4})\\d|1(?:1(?:2|6(?:00[06]|1(?:1[17]|23))|8\\d\\d)|3977|9(?:[2-5]|87))|9[34]', , , , '93'],
                  [, , , , , , , , , [-1]],
                  [, , '139\\d\\d', , , , '13900', , , [5]],
                  ,
                  [, , '139\\d\\d', , , , '13900', , , [5]]
                ],
                HT: [
                  ,
                  [, , '[14]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '11[48]', , , , '114', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'HT',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[48]', , , , '114', , , [3]],
                  ,
                  [, , '11[48]|40404', , , , '114'],
                  [, , , , , , , , , [-1]],
                  [, , '404\\d\\d', , , , '40400', , , [5]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                HU: [
                  ,
                  [, , '1\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:0[457]|12|4[0-4]\\d)|1(?:16\\d|37|45)\\d\\d', , , , '104'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'HU',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:0[457]|12)', , , , '104', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0[457]|1(?:2|6(?:000|1(?:11|23))|800)|2(?:0[0-4]|1[013489]|2[0-5]|3[0-46]|4[0-24-68]|5[0-2568]|6[06]|7[0-25-7]|8[028]|9[08])|37(?:00|37|7[07])|4(?:0[0-5]|1[013-8]|2[034]|3[23]|4[02-9]|5(?:00|41|67))|777|8(?:1[27-9]|2[04]|40|[589]))',
                    ,
                    ,
                    ,
                    '104'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , '1(?:4[0-4]|77)\\d|1(?:18|2|45)\\d\\d', , , , '1200', , , [4, 5]],
                  ,
                  [, , '184\\d', , , , '1840', , , [4]]
                ],
                ID: [
                  ,
                  [, , '[178]\\d\\d(?:\\d{2,3})?', , , , , , , [3, 5, 6]],
                  ,
                  ,
                  [, , '11[02389]', , , , '110', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'ID',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[02389]', , , , '110', , , [3]],
                  ,
                  [, , '1(?:1[02389]|40\\d\\d|50264)|71400|89887', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , '(?:714|898)\\d\\d', , , , '71400', , , [5]],
                  ,
                  [, , '714\\d\\d', , , , '71400', , , [5]]
                ],
                IE: [
                  ,
                  [, , '[159]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '11(?:2|6\\d{3})|999', , , , '112', , , [3, 6]],
                  [, , '5[37]\\d{3}', , , , '53000', , , [5]],
                  ,
                  ,
                  ,
                  'IE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|999', , , , '112', , , [3]],
                  ,
                  [, , '11(?:2|6(?:00[06]|1(?:1[17]|23)))|999|(?:1(?:18|9)|5[0137]\\d)\\d\\d', , , , '112'],
                  [, , '51\\d{3}', , , , '51000', , , [5]],
                  [, , '51210', , , , '51210', , , [5]],
                  ,
                  [, , '51210|(?:118|5[037]\\d)\\d\\d', , , , '11800', , , [5]]
                ],
                IL: [
                  ,
                  [, , '[12]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '1(?:0[0-2]|12)', , , , '100', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'IL',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:0[0-2]|12)', , , , '100', , , [3]],
                  ,
                  [, , '1(?:0[0-2]|1(?:[013-9]\\d|2)|[2-9]\\d\\d)|2407|(?:104|27)00', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , '104\\d\\d', , , , '10400', , , [5]],
                  ,
                  [, , '104\\d\\d', , , , '10400', , , [5]]
                ],
                IM: [
                  ,
                  [, , '[189]\\d\\d(?:\\d{2,3})?', , , , , , , [3, 5, 6]],
                  ,
                  ,
                  [, , '999', , , , '999', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'IM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '999', , , , '999', , , [3]],
                  ,
                  [, , '1\\d\\d(?:\\d{3})?|8(?:6444|9887)|999', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '8(?:64|98)\\d\\d', , , , '86400', , , [5]]
                ],
                IN: [
                  ,
                  [, , '[12578]\\d{2,8}', , , , , , , [3, 4, 5, 6, 7, 8, 9]],
                  ,
                  ,
                  [
                    ,
                    ,
                    '1(?:0[0-248]|1[289]|21|[39][89]|4[01]|6(?:1|6\\d?)|8[12])|777|800|1[05]5\\d|1(?:07|51|94)\\d\\d?|(?:1(?:[05]5\\d|70)\\d|261)\\d|1(?:0[369]|10|29|3[126]|9[0-256])\\d',
                    ,
                    ,
                    ,
                    '100',
                    ,
                    ,
                    [3, 4, 5, 6]
                  ],
                  [, , '11[67]\\d{4}|56161561', , , , '1160000', , , [7, 8]],
                  ,
                  ,
                  ,
                  'IN',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:0[0-28]|12|298)|2611', , , , '100', , , [3, 4]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0(?:[0-248]|3[39]|5(?:010|6)|6[3468]|7(?:[01357]|[28]0?|4[01])|9[0135-9])|1(?:00|[289])|2(?:1|98)|3(?:11|2[0-2]|63|[89])|4[01]|5(?:1(?:0[0-36]|[127])|54)|6(?:1|6[01]?)|7000|8[12]|9(?:0[013-59]|12|25|4[4-9]\\d?|50|6[1347]|[89]))|2611|5(?:0(?:0(?:0\\d|1|20?)|325|5[2-79]\\d{3,5})|1(?:234|555|717|818|96[49])|2(?:0(?:0[01]|[14]0)|151|555|666|888|9(?:06|99\\d?))|3(?:0[01]0|131|553|(?:66|77)6)|(?:464|55[05])\\d{1,3}|6(?:070|3[68]|43)|717\\d)|777|800|5(?:05(?:0|1\\d)|221|3(?:03|3[23]))\\d{1,4}|5(?:(?:04|88)0|2(?:2[0267]|3[16])|4(?:1[04]|20|3[02])|5(?:3[16]|67)|6(?:06|[67]\\d)|787|9(?:64|90))\\d\\d?|(?:1(?:05[79]|(?:1[67][0-2]|802)\\d|55[23])\\d|5(?:(?:00(?:0\\d|1)|(?:304|616)\\d\\d)\\d|1(?:0[12]|4[2-4])|2(?:2[3589]|3(?:1\\d{3}|2)|4[04]|7[78])|4(?:[02]4|32\\d{4}|4[04]|99)|5(?:1[25]|[36]5|4[45]|93)|7(?:(?:17\\d|57)\\d\\d|[27]7|88)|8(?:3[4-69]|4[01]|5[58]|88(?:8\\d\\d|9)|99)|9(?:0(?:0|2\\d{3})|55|6[67]|77|88)))\\d',
                    ,
                    ,
                    ,
                    '100'
                  ],
                  [, , '5(?:14(?:2[5-9]|[34]\\d)|757555)', , , , '51425', , , [5, 7]],
                  [, , '1(?:(?:1[67]\\d\\d|70)\\d\\d|55330|909)|5(?:300\\d|6161(?:17[89]|561))|1(?:[19][89]|21|4[01])', , , , '118', , , [3, 4, 5, 6, 7, 8]],
                  ,
                  [, , '1(?:39|90[019])|5(?:14(?:2[5-9]|[34]\\d)|6161(?:17[89]|561)|757555)', , , , '139', , , [3, 4, 5, 7, 8]]
                ],
                IQ: [
                  ,
                  [, , '[1479]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '1(?:0[04]|15|22)', , , , '100', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'IQ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:0[04]|15|22)', , , , '100', , , [3]],
                  ,
                  [, , '1(?:0[04]|15|22)|4432|71117|9988', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , '(?:443|711\\d|998)\\d', , , , '4430', , , [4, 5]],
                  ,
                  [, , '(?:443|711\\d|998)\\d', , , , '4430', , , [4, 5]]
                ],
                IR: [
                  ,
                  [, , '[129]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:1[0-68]|2[0-59]|9[0-579])|911', , , , '110', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'IR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[025]|25)|911', , , , '110', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:1[0-68]|2[0-59]|3[346-8]|4(?:[0147]|[289]0)|5(?:0[14]|1[02479]|2[0-3]|39|[49]0|65)|6(?:[16]6|[27]|90)|8(?:03|1[18]|22|3[37]|4[28]|88|99)|9[0-579])|20(?:[09]0|1(?:[038]|1[079]|26|9[69])|2[01])|9(?:11|9(?:0009|90))',
                    ,
                    ,
                    ,
                    '110'
                  ],
                  [, , '1(?:5[0-469]|8[0-489])\\d', , , , '1500', , , [4]],
                  [, , '(?:1(?:5[0-469]|8[0-489])|99(?:0\\d\\d|9))\\d', , , , '1500', , , [4, 6]],
                  ,
                  [, , '990\\d{3}', , , , '990000', , , [6]]
                ],
                IS: [
                  ,
                  [, , '1\\d\\d(?:\\d(?:\\d{2})?)?', , , , , , , [3, 4, 6]],
                  ,
                  ,
                  [, , '1(?:12|71\\d)', , , , '112', , , [3, 4]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'IS',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:[28]|61(?:16|23))|4(?:00|1[145]|4[0146])|55|7(?:00|17|7[07-9])|8(?:[02]0|1[16-9]|88)|900)', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , '14(?:0\\d|41)', , , , '1400', , , [4]],
                  ,
                  [, , '1(?:415|90\\d)', , , , '1415', , , [4]]
                ],
                IT: [
                  ,
                  [, , '[14]\\d{2,6}', , , , , , , [3, 4, 5, 6, 7]],
                  ,
                  ,
                  [, , '1(?:1(?:[2358]|6\\d{3})|87)', , , , '112', , , [3, 6]],
                  [, , '(?:12|4(?:[478](?:[0-4]|[5-9]\\d\\d)|55))\\d\\d', , , , '1200', , , [4, 5, 7]],
                  ,
                  ,
                  ,
                  'IT',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[2358]', , , , '112', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0\\d{2,3}|1(?:[2-57-9]|6(?:000|111))|3[39]|4(?:82|9\\d{1,3})|5(?:00|1[58]|2[25]|3[03]|44|[59])|60|8[67]|9(?:[01]|2[2-9]|4\\d|696))|4(?:2323|5045)|(?:1(?:2|92[01])|4(?:3(?:[01]|[45]\\d\\d)|[478](?:[0-4]|[5-9]\\d\\d)|55))\\d\\d',
                    ,
                    ,
                    ,
                    '112'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '4(?:3(?:[01]|[45]\\d\\d)|[478](?:[0-4]|[5-9]\\d\\d)|5[05])\\d\\d', , , , '43000', , , [5, 7]]
                ],
                JE: [
                  ,
                  [, , '[129]\\d\\d(?:\\d(?:\\d{2})?)?', , , , , , , [3, 4, 6]],
                  ,
                  ,
                  [, , '112|999', , , , '112', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'JE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|999', , , , '112', , , [3]],
                  ,
                  [, , '1(?:00|1(?:2|8\\d{3})|23|4(?:[14]|28|7\\d)|5\\d|7(?:0[12]|[128]|35?)|808|9[0135])|23[2-4]|999', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                JM: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[029]|9(?:11|88)', , , , '110'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'JM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[029]|911', , , , '110'],
                  ,
                  [, , '1(?:1[029]|76)|9(?:11|88)', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , '176', , , , '176'],
                  ,
                  [, , '176', , , , '176']
                ],
                JO: [
                  ,
                  [, , '[19]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '1(?:0[235]|1[2-6]|9[127])|911', , , , '102', , , [3]],
                  [, , '9[0-4689]\\d{3}', , , , '90000', , , [5]],
                  ,
                  ,
                  ,
                  'JO',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|9[127])|911', , , , '112', , , [3]],
                  ,
                  [, , '1(?:0[2359]|1[0-68]|9[0-24-79])|9[0-4689]\\d{3}|911', , , , '102'],
                  [, , , , , , , , , [-1]],
                  [, , '9[0-4689]\\d{3}', , , , '90000', , , [5]],
                  ,
                  [, , '9[0-4689]\\d{3}', , , , '90000', , , [5]]
                ],
                JP: [
                  ,
                  [, , '[01]\\d\\d(?:\\d{5,7})?', , , , , , , [3, 8, 9, 10]],
                  ,
                  ,
                  [, , '11[089]', , , , '110', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'JP',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[09]', , , , '110', , , [3]],
                  ,
                  [, , '000[2569]\\d{4,6}|1(?:0[24]|1[089]|44|89)', , , , '102'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '000[2569]\\d{4,6}', , , , '00020000', , , [8, 9, 10]]
                ],
                KE: [
                  ,
                  [, , '[1-9]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '1(?:1(?:[246]|9\\d)|5(?:01|2[127]|6[26]\\d))|999', , , , '112'],
                  [, , '909\\d\\d', , , , '90900', , , [5]],
                  ,
                  ,
                  ,
                  'KE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[24]|999', , , , '112', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0(?:[07-9]|1[0-25]|400)|1(?:[024-6]|9[0-579])|2[1-3]|3[01]|4[14]|5(?:[01][01]|2[0-24-79]|33|4[05]|5[59]|6(?:00|29|6[67]))|(?:6[035]\\d|[78])\\d|9(?:[02-9]\\d\\d|19))|(?:(?:2[0-79]|[37][0-29]|4[0-4]|6[2357]|8\\d)\\d|5(?:[0-7]\\d|99))\\d\\d|9(?:09\\d\\d|99)|8988',
                    ,
                    ,
                    ,
                    '100'
                  ],
                  [, , , , , , , , , [-1]],
                  [
                    ,
                    ,
                    '1(?:(?:04|6[35])\\d\\d|3[01]|4[14]|5(?:1\\d|2[25]))|(?:(?:2[0-79]|[37][0-29]|4[0-4]|6[2357]|8\\d)\\d|5(?:[0-7]\\d|99)|909)\\d\\d|898\\d',
                    ,
                    ,
                    ,
                    '130'
                  ],
                  ,
                  [, , '1(?:(?:04|6[035])\\d\\d|4[14]|5(?:01|55|6[26]\\d))|40404|8988|909\\d\\d', , , , '141']
                ],
                KG: [
                  ,
                  [, , '[14]\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '10[1-3]', , , , '101', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'KG',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '10[1-3]', , , , '101', , , [3]],
                  ,
                  [, , '10[1-3]|4040', , , , '101'],
                  [, , , , , , , , , [-1]],
                  [, , '404\\d', , , , '4040', , , [4]],
                  ,
                  [, , '404\\d', , , , '4040', , , [4]]
                ],
                KH: [
                  ,
                  [, , '[146]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '11[7-9]|666', , , , '117', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'KH',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[7-9]|666', , , , '117', , , [3]],
                  ,
                  [, , '11[7-9]|40404|666', , , , '117'],
                  [, , , , , , , , , [-1]],
                  [, , '404\\d\\d', , , , '40400', , , [5]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                KI: [
                  ,
                  [, , '[179]\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '19[2-5]|99[2-4]', , , , '192', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'KI',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '19[2-5]|99[2-4]', , , , '192', , , [3]],
                  ,
                  [, , '1(?:05[0-259]|88|9[2-5])|777|99[2-4]|10[0-8]', , , , '100'],
                  [, , '103', , , , '103', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KM: [
                  ,
                  [, , '1\\d', , , , , , , [2]],
                  ,
                  ,
                  [, , '1[78]', , , , '17'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'KM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[78]', , , , '17'],
                  ,
                  [, , '1[78]', , , , '17'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KN: [
                  ,
                  [, , '[39]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '333|9(?:11|88|99)', , , , '333'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'KN',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '333|9(?:11|99)', , , , '333'],
                  ,
                  [, , '333|9(?:11|88|99)', , , , '333'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KP: [
                  ,
                  [, , '[18]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[29]|819', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'KP',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[29]|819', , , , '112'],
                  ,
                  [, , '11[29]|819', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KR: [
                  ,
                  [, , '1\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '1(?:1[27-9]|28|330|82)', , , , '112', , , [3, 4]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'KR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[29]', , , , '112', , , [3]],
                  ,
                  [, , '1(?:[016-9]114|3(?:0[01]|2|3[0-35-9]|45?|5[057]|6[569]|7[79]|8[2589]|9[0189]))|1(?:0[015]|1\\d|2[01357-9]|41|8[28])', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , '1(?:0[01]|1[4-6]|41)|1(?:[06-9]1\\d|111)\\d', , , , '100', , , [3, 5]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KW: [
                  ,
                  [, , '[18]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '112', , , , '112', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'KW',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112', , , , '112', , , [3]],
                  ,
                  [, , '1[0-7]\\d|89887', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , '898\\d\\d', , , , '89800', , , [5]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KY: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'KY',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '911', , , , '911'],
                  ,
                  [, , '9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                KZ: [
                  ,
                  [, , '[1-4]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '1(?:0[1-3]|12)|212\\d', , , , '101', , , [3, 4]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'KZ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:0[1-3]|12)', , , , '101', , , [3]],
                  ,
                  [, , '1(?:0[1-4]|12)|2121|(?:3040|404)0', , , , '101'],
                  [, , , , , , , , , [-1]],
                  [, , '(?:304\\d|404)\\d', , , , '4040', , , [4, 5]],
                  ,
                  [, , '(?:304\\d|404)\\d', , , , '4040', , , [4, 5]]
                ],
                LA: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '19[015]', , , , '190'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'LA',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '19[015]', , , , '190'],
                  ,
                  [, , '19[015]', , , , '190'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LB: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '1(?:12|40|75)|999', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'LB',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|40|75)|999', , , , '112'],
                  ,
                  [, , '1(?:12|40|75)|999', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LC: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'LC',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:11|99)', , , , '911'],
                  ,
                  [, , '9(?:11|88|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LI: [
                  ,
                  [, , '1\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:1[278]|44)', , , , '112', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'LI',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[278]|44)', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:[278]|45)|4[3-57]|50|75|81[18])', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LK: [
                  ,
                  [, , '1\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '11[02689]', , , , '110', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'LK',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[02689]', , , , '110', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:1[024-9]|3(?:00|1[2-49]|2[23]|3[1-3]|44|5[07]|[67]9|88|9[039])|9(?:0[0-2589]|1[0-357-9]|2[0-25689]|3[0389]|4[0489]|5[014-69]|6[0-2689]|7[03579]|8[02457-9]|9[0-2569]))',
                    ,
                    ,
                    ,
                    '110'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LR: [
                  ,
                  [, , '[3489]\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '355|911', , , , '355', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'LR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '355|911', , , , '355', , , [3]],
                  ,
                  [, , '355|4040|8(?:400|933)|911', , , , '355'],
                  [, , , , , , , , , [-1]],
                  [, , '(?:404|8(?:40|93))\\d', , , , '4040', , , [4]],
                  ,
                  [, , '(?:404|8(?:40|93))\\d', , , , '4040', , , [4]]
                ],
                LS: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[257]', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'LS',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[257]', , , , '112'],
                  ,
                  [, , '11[257]', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LT: [
                  ,
                  [, , '[01]\\d(?:\\d(?:\\d{3})?)?', , , , , , , [2, 3, 6]],
                  ,
                  ,
                  [, , '0(?:11?|22?|33?)|1(?:0[1-3]|1(?:2|6111))|116(?:0\\d|12)\\d', , , , '01'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'LT',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '0(?:11?|22?|33?)|1(?:0[1-3]|12)', , , , '01', , , [2, 3]],
                  ,
                  [, , '0(?:11?|22?|33?)|1(?:0[1-3]|1(?:[27-9]|6(?:000|1(?:1[17]|23))))', , , , '01'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LU: [
                  ,
                  [, , '1\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '11(?:[23]|6\\d{3})', , , , '112', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'LU',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[23]', , , , '112', , , [3]],
                  ,
                  [, , '11(?:[23]|6(?:000|111))|1(?:18|[25]\\d|3)\\d\\d', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LV: [
                  ,
                  [, , '[018]\\d{1,5}', , , , , , , [2, 3, 4, 5, 6]],
                  ,
                  ,
                  [, , '0[1-3]|11(?:[023]|6\\d{3})', , , , '01', , , [2, 3, 6]],
                  [, , '1180|821\\d\\d', , , , '1180', , , [4, 5]],
                  ,
                  ,
                  ,
                  'LV',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '0[1-3]|11[023]', , , , '01', , , [2, 3]],
                  ,
                  [, , '0[1-4]|1(?:1(?:[02-4]|6(?:000|111)|8[0189])|(?:5|65)5|77)|821[57]4', , , , '01'],
                  [, , '1181', , , , '1181', , , [4]],
                  [, , '165\\d', , , , '1650', , , [4]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                LY: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '19[013]', , , , '190'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'LY',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '19[013]', , , , '190'],
                  ,
                  [, , '19[013]', , , , '190'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MA: [
                  ,
                  [, , '1\\d\\d?', , , , , , , [2, 3]],
                  ,
                  ,
                  [, , '1(?:[59]|77)', , , , '15'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MA',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:[59]|77)', , , , '15'],
                  ,
                  [, , '1(?:[59]|77)', , , , '15'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MC: [
                  ,
                  [, , '1\\d\\d?', , , , , , , [2, 3]],
                  ,
                  ,
                  [, , '1(?:12|[578])', , , , '15'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MC',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|[578])', , , , '15'],
                  ,
                  [, , '1(?:12|41|[578])', , , , '15'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MD: [
                  ,
                  [, , '[19]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '11(?:2|6(?:000|1(?:11|2\\d)))|90[1-3]', , , , '112', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MD',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|90[1-3]', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:2|6(?:00[06]|1(?:1[17]|23))|8\\d\\d?|99)|90[04-9])|90[1-3]|1(?:4\\d\\d|6[0-389]|9[1-4])\\d', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ME: [
                  ,
                  [, , '1\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:12|2[2-4])', , , , '112', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'ME',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|2[2-4])', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:(?:[013-57-9]|6\\d\\d)\\d|2)|[249]\\d{3}|5999|8(?:0[089]|1[0-8]|888))|1(?:[02-5]\\d\\d|60[06]|700)|12\\d', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MF: [
                  ,
                  [, , '1\\d', , , , , , , [2]],
                  ,
                  ,
                  [, , '1[578]', , , , '15'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MF',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[578]', , , , '15'],
                  ,
                  [, , '1[578]', , , , '15'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MG: [
                  ,
                  [, , '1\\d\\d?', , , , , , , [2, 3]],
                  ,
                  ,
                  [, , '1(?:1[78]|[78])', , , , '17'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MG',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[78]|[78])', , , , '17'],
                  ,
                  [, , '1(?:1[78]|[78])', , , , '17'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MH: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '911', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MH',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '911', , , , '911'],
                  ,
                  [, , '911', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MK: [
                  ,
                  [, , '1\\d\\d(?:\\d(?:\\d{2})?)?', , , , , , , [3, 4, 6]],
                  ,
                  ,
                  [, , '1(?:1(?:2|6\\d{3})|9[2-4])', , , , '112', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MK',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|9[2-4])', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:2|8\\d)|3\\d|9[2-4])|1(?:16|2\\d)\\d{3}', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ML: [
                  ,
                  [, , '[136-8]\\d{1,4}', , , , , , , [2, 3, 4, 5]],
                  ,
                  ,
                  [, , '1[578]|(?:352|67)00|7402|(?:677|744|8000)\\d', , , , '15', , , [2, 4, 5]],
                  [, , '(?:12|800)2\\d|3(?:52(?:11|2[02]|3[04-6]|99)|7574)', , , , '1220', , , [4, 5]],
                  ,
                  ,
                  ,
                  'ML',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[578]', , , , '15', , , [2]],
                  ,
                  [
                    ,
                    ,
                    '1(?:1(?:[013-9]\\d|2)|2(?:1[02-469]|2[13])|[578])|350(?:35|57)|67(?:0[09]|[59]9|77|8[89])|74(?:0[02]|44|55)|800[0-2][12]|3(?:52|[67]\\d)\\d\\d',
                    ,
                    ,
                    ,
                    '15'
                  ],
                  [, , '37(?:433|575)|7400|8001\\d', , , , '7400', , , [4, 5]],
                  [, , '3503\\d|(?:3[67]\\d|800)\\d\\d', , , , '35030', , , [5]],
                  ,
                  [, , '374(?:0[24-9]|[1-9]\\d)|7400|3(?:6\\d|75)\\d\\d', , , , '7400', , , [4, 5]]
                ],
                MM: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '199', , , , '199'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '199', , , , '199'],
                  ,
                  [, , '199', , , , '199'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MN: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '10[0-35]', , , , '100'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MN',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '10[0-35]', , , , '100'],
                  ,
                  [, , '10[0-35]', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MO: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '999', , , , '999'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MO',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '999', , , , '999'],
                  ,
                  [, , '999', , , , '999'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MP: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MP',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '911', , , , '911'],
                  ,
                  [, , '9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MQ: [
                  ,
                  [, , '[13]\\d(?:\\d(?:\\d(?:\\d{2})?)?)?', , , , , , , [2, 3, 4, 6]],
                  ,
                  ,
                  [, , '1(?:12|[578])|3[01]\\d\\d', , , , '15', , , [2, 3, 4]],
                  [, , '3[2469]\\d\\d', , , , '3200', , , [4]],
                  ,
                  ,
                  ,
                  'MQ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|[578])', , , , '15', , , [2, 3]],
                  ,
                  [, , '1(?:12|[578])|(?:118[02-9]|3[0-2469])\\d\\d', , , , '15'],
                  [, , '118\\d{3}', , , , '118000', , , [6]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MR: [
                  ,
                  [, , '1\\d', , , , , , , [2]],
                  ,
                  ,
                  [, , '1[78]', , , , '17'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[78]', , , , '17'],
                  ,
                  [, , '1[78]', , , , '17'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MS: [
                  ,
                  [, , '[29]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MS',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:11|99)', , , , '911'],
                  ,
                  [, , '211|9(?:11|88|99)', , , , '211'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MT: [
                  ,
                  [, , '1\\d\\d(?:\\d{3})?', , , , , , , [3, 6]],
                  ,
                  ,
                  [, , '11(?:2|6\\d{3})', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MT',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112', , , , '112', , , [3]],
                  ,
                  [, , '11(?:2|6(?:000|1(?:11|23)))', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MU: [
                  ,
                  [, , '[189]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '11[45]|99[59]', , , , '114', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MU',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[45]|99[59]', , , , '114', , , [3]],
                  ,
                  [, , '1\\d{2,4}|(?:8\\d\\d|99)\\d', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MV: [
                  ,
                  [, , '[14]\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:02|1[89])', , , , '102', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MV',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:02|1[89])', , , , '102', , , [3]],
                  ,
                  [, , '1(?:[0-37-9]|[4-6]\\d)\\d|4040|1[45]1', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , '1[45]1', , , , '141', , , [3]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MW: [
                  ,
                  [, , '[189]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '199|99[7-9]', , , , '199', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MW',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '199|99[7-9]', , , , '199', , , [3]],
                  ,
                  [, , '199|80400|99[7-9]', , , , '199'],
                  [, , , , , , , , , [-1]],
                  [, , '804\\d\\d', , , , '80400', , , [5]],
                  ,
                  [, , '804\\d\\d', , , , '80400', , , [5]]
                ],
                MX: [
                  ,
                  [, , '[0579]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '0(?:6[0568]|80)|911', , , , '060', , , [3]],
                  [, , '(?:530\\d|776)\\d', , , , '7760', , , [4, 5]],
                  ,
                  ,
                  ,
                  'MX',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '0(?:6[0568]|80)|911', , , , '060', , , [3]],
                  ,
                  [, , '0[1-9]\\d|53053|7766|911', , , , '010'],
                  [, , , , , , , , , [-1]],
                  [, , '0(?:[249]0|[35][01])', , , , '020', , , [3]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                MY: [
                  ,
                  [, , '[1369]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '112|999', , , , '112', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MY',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|999', , , , '112', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0[01348]|1(?:[02]|1[128]|311)|2(?:0[125]|[13-6]|2\\d{0,2})|(?:3[1-35-79]|7[45])\\d\\d?|5(?:454|5\\d\\d?|77|888|999?)|8(?:18?|2|8[18])|9(?:[124]\\d?|68|71|9[0679]))|66628|99[1-469]|13[5-7]|(?:1(?:0[569]|309|5[12]|7[136-9]|9[03])|3[23679]\\d\\d)\\d',
                    ,
                    ,
                    ,
                    '100'
                  ],
                  [, , '666\\d\\d', , , , '66600', , , [5]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '(?:3[23679]\\d|666)\\d\\d', , , , '32000', , , [5]]
                ],
                MZ: [
                  ,
                  [, , '1\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:1[79]|9[78])', , , , '117', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'MZ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[79]|9[78])', , , , '117', , , [3]],
                  ,
                  [, , '1(?:[02-5]\\d\\d|1[79]|9[78])', , , , '117'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NA: [
                  ,
                  [, , '[19]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '10111', , , , '10111', , , [5]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'NA',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '10111', , , , '10111', , , [5]],
                  ,
                  [, , '(?:10|93)111|(?:1\\d|9)\\d\\d', , , , '900'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NC: [
                  ,
                  [, , '[135]\\d{1,3}', , , , , , , [2, 3, 4]],
                  ,
                  ,
                  [, , '1(?:0(?:00|1[23]|3[0-2]|8\\d)|[5-8])|363\\d|577', , , , '15'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'NC',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[5-8]', , , , '15', , , [2]],
                  ,
                  [, , '1(?:0(?:0[06]|1[02-46]|20|3[0-25]|42|5[058]|77|88)|[5-8])|3631|5[6-8]\\d', , , , '15'],
                  [, , '5(?:67|88)', , , , '567', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NE: [
                  ,
                  [, , '[1-3578]\\d(?:\\d(?:\\d{3})?)?', , , , , , , [2, 3, 6]],
                  ,
                  ,
                  [, , '1(?:18|[578])|723\\d{3}', , , , '15'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'NE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:18|[578])|723141', , , , '15'],
                  ,
                  [, , '1(?:0[01]|1[128]|2[034]|3[013]|[46]0|55?|[78])|222|333|555|723141|888', , , , '15'],
                  [, , , , , , , , , [-1]],
                  [, , '1(?:0[01]|1[12]|2[034]|3[013]|[46]0|55)|222|333|555|888', , , , '100', , , [3]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NF: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|55|77)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'NF',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:11|55|77)', , , , '911'],
                  ,
                  [, , '9(?:11|55|77)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NG: [
                  ,
                  [, , '[14]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '199', , , , '199', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'NG',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '199', , , , '199', , , [3]],
                  ,
                  [, , '199|40700', , , , '199'],
                  [, , , , , , , , , [-1]],
                  [, , '407\\d\\d', , , , '40700', , , [5]],
                  ,
                  [, , '407\\d\\d', , , , '40700', , , [5]]
                ],
                NI: [
                  ,
                  [, , '[12467]\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:1[58]|2[08])|737\\d', , , , '115'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'NI',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[58]|2[08])', , , , '115', , , [3]],
                  ,
                  [, , '1(?:1[58]|200)|4878|7(?:010|373)|12[0158]|(?:19|[267]1)00', , , , '115'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NL: [
                  ,
                  [, , '[1349]\\d\\d(?:\\d(?:\\d{2})?)?', , , , , , , [3, 4, 6]],
                  ,
                  ,
                  [, , '11(?:2|6\\d{3})|911', , , , '112', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'NL',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|911', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:2|6(?:00[06]|1(?:11|23)))|2(?:0[0-4]|3[34]|44)|3[03-9]\\d|400|8(?:[02-9]\\d|1[0-79]))|[34]000|911', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , '120\\d', , , , '1200', , , [4]],
                  ,
                  [, , '[34]00\\d', , , , '3000', , , [4]]
                ],
                NO: [
                  ,
                  [, , '[01]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '11(?:[023]|6\\d{3})', , , , '110', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'NO',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[023]', , , , '110', , , [3]],
                  ,
                  [, , '04\\d{3}|1(?:1(?:[0239]|61(?:1[17]|23))|2[048]|4(?:12|[59])|7[57]|8\\d\\d|90)', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '04\\d{3}', , , , '04000', , , [5]]
                ],
                NP: [
                  ,
                  [, , '1\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:0[0-36]|12)|1(?:09|11)\\d', , , , '100'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'NP',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:0[0-3]|12)', , , , '100', , , [3]],
                  ,
                  [, , '1(?:0(?:[0-36]|98)|1(?:1[1-4]|2))', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NR: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[0-2]', , , , '110'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'NR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[0-2]', , , , '110'],
                  ,
                  [, , '1(?:1[0-2]|23|92)', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NU: [
                  ,
                  [, , '[019]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '999', , , , '999'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'NU',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '999', , , , '999'],
                  ,
                  [, , '01[05]|101|999', , , , '010'],
                  [, , , , , , , , , [-1]],
                  [, , '010', , , , '010'],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                NZ: [
                  ,
                  [, , '\\d{3,4}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '111', , , , '111', , , [3]],
                  [, , '018', , , , '018', , , [3]],
                  ,
                  ,
                  ,
                  'NZ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '111', , , , '111', , , [3]],
                  ,
                  [, , '018|1(?:(?:1|37)1|(?:23|94)4|7[03]7)|[2-57-9]\\d{2,3}|6(?:161|26[0-3]|742)', , , , '018'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '018|(?:1(?:23|37|7[03]|94)|6(?:[12]6|74))\\d|[2-57-9]\\d{2,3}', , , , '018']
                ],
                OM: [
                  ,
                  [, , '[19]\\d{3}', , , , , , , [4]],
                  ,
                  ,
                  [, , '1444|999\\d', , , , '1444'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'OM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1444|9999', , , , '1444'],
                  ,
                  [, , '1(?:111|222|4(?:4[0-5]|50|66|7[7-9])|51[0-8])|9999|1(?:2[3-5]|3[0-2]|50)\\d', , , , '1111'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PA: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '911', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'PA',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '911', , , , '911'],
                  ,
                  [, , '10[2-4]|911', , , , '102'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PE: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '1(?:05|1[67])', , , , '105'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'PE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:05|1[67])', , , , '105'],
                  ,
                  [, , '1(?:05|1[67])', , , , '105'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PF: [
                  ,
                  [, , '1\\d', , , , , , , [2]],
                  ,
                  ,
                  [, , '1[578]', , , , '15'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'PF',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[578]', , , , '15'],
                  ,
                  [, , '1[578]', , , , '15'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PG: [
                  ,
                  [, , '[01]\\d{2,6}', , , , , , , [3, 4, 5, 6, 7]],
                  ,
                  ,
                  [, , '000|11[01]', , , , '000', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'PG',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '000|11[01]', , , , '000', , , [3]],
                  ,
                  [, , '000|1(?:1[01]|5\\d\\d|6\\d{2,5})', , , , '000'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '16\\d{2,5}', , , , '1600', , , [4, 5, 6, 7]]
                ],
                PH: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[27]|911', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'PH',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[27]|911', , , , '112'],
                  ,
                  [, , '11[27]|911', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PK: [
                  ,
                  [, , '1\\d{1,3}', , , , , , , [2, 3, 4]],
                  ,
                  ,
                  [, , '1(?:1(?:2\\d?|5)|[56])', , , , '15'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'PK',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1(?:22?|5)|[56])', , , , '15'],
                  ,
                  [, , '1(?:122|3[014]|[56])|11[2457-9]', , , , '15'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PL: [
                  ,
                  [, , '[19]\\d\\d(?:\\d{2,3})?', , , , , , , [3, 5, 6]],
                  ,
                  ,
                  [, , '11(?:2|6\\d{3})|99[7-9]', , , , '112', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'PL',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|99[7-9]', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:2|61(?:11|23)|891[23])|9\\d{3})|9(?:8[4-7]|9[1-9])|11[68]000', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PM: [
                  ,
                  [, , '[13]\\d(?:\\d\\d(?:\\d{2})?)?', , , , , , , [2, 4, 6]],
                  ,
                  ,
                  [, , '1[578]|3(?:0\\d|1[689])\\d', , , , '15', , , [2, 4]],
                  [, , '3[2469]\\d\\d', , , , '3200', , , [4]],
                  ,
                  ,
                  ,
                  'PM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[578]', , , , '15', , , [2]],
                  ,
                  [, , '1[578]|31(?:03|[689]\\d)|(?:118[02-9]|3[02469])\\d\\d', , , , '15'],
                  [, , '118\\d{3}', , , , '118000', , , [6]],
                  [, , '310\\d', , , , '3100', , , [4]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PR: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'PR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '911', , , , '911'],
                  ,
                  [, , '9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PS: [
                  ,
                  [, , '1\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:0[0-2]|66)', , , , '100', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'PS',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '10[0-2]', , , , '100', , , [3]],
                  ,
                  [, , '1(?:0[0-2]|122|44|66|99)', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , '112\\d', , , , '1120', , , [4]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PT: [
                  ,
                  [, , '1\\d\\d(?:\\d(?:\\d{2})?)?', , , , , , , [3, 4, 6]],
                  ,
                  ,
                  [, , '11[257]|1(?:16\\d\\d|5[1589]|8[279])\\d', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'PT',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[25]', , , , '112', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:0(?:45|5[01])|1(?:[2578]|600[06])|4(?:1[45]|4)|583|6(?:1[0236]|3[02]|9[169]))|1(?:1611|59)1|1[068]78|1[08]9[16]|1(?:0[1-38]|40|5[15]|6[258]|82)0',
                    ,
                    ,
                    ,
                    '112'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PW: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '911', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'PW',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '911', , , , '911'],
                  ,
                  [, , '911', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                PY: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '128|911', , , , '128'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'PY',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '128|911', , , , '128'],
                  ,
                  [, , '1[1-9]\\d|911', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                QA: [
                  ,
                  [, , '[129]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '99\\d', , , , '990', , , [3]],
                  [, , '900', , , , '900', , , [3]],
                  ,
                  ,
                  ,
                  'QA',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '999', , , , '999', , , [3]],
                  ,
                  [, , '9(?:00|[19]\\d)|(?:1|20|9[27]\\d)\\d\\d', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                RE: [
                  ,
                  [, , '1\\d\\d?', , , , , , , [2, 3]],
                  ,
                  ,
                  [, , '1(?:12|[578])', , , , '15'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'RE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|[578])', , , , '15'],
                  ,
                  [, , '1(?:12|[578])', , , , '15'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                RO: [
                  ,
                  [, , '[18]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '11(?:2|6\\d{3})', , , , '112', , , [3, 6]],
                  [, , '(?:1(?:18[39]|[24])|8[48])\\d\\d', , , , '1200', , , [4, 6]],
                  ,
                  ,
                  ,
                  'RO',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112', , , , '112', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:1(?:2|6(?:000|1(?:11|23))|8(?:(?:01|8[18])1|119|[23]00|932))|[24]\\d\\d|9(?:0(?:00|19)|1[19]|21|3[02]|5[178]))|8[48]\\d\\d',
                    ,
                    ,
                    ,
                    '112'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '(?:1[24]|8[48])\\d\\d', , , , '1200', , , [4]]
                ],
                RS: [
                  ,
                  [, , '[19]\\d{1,5}', , , , , , , [2, 3, 4, 5, 6]],
                  ,
                  ,
                  [, , '112|9[2-4]', , , , '92', , , [2, 3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'RS',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|9[2-4]', , , , '92', , , [2, 3]],
                  ,
                  [, , '1[189]\\d{1,4}|9[2-4]', , , , '92'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                RU: [
                  ,
                  [, , '[01]\\d\\d?', , , , , , , [2, 3]],
                  ,
                  ,
                  [, , '112|(?:0|10)[1-3]', , , , '01'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'RU',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|(?:0|10)[1-3]', , , , '01'],
                  ,
                  [, , '112|(?:0|10)[1-4]', , , , '01'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                RW: [
                  ,
                  [, , '[14]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[1245]', , , , '111'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'RW',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[12]', , , , '111'],
                  ,
                  [, , '1(?:0[0-2]|1[0-24-6]|2[13]|70|99)|456', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SA: [
                  ,
                  [, , '[19]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '11(?:2|6\\d{3})|9(?:11|37|9[7-9])', , , , '112', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SA',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|9(?:11|9[79])', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:00|2|6111)|410|9(?:00|1[89]|9(?:099|22|9[0-3])))|9(?:0[24-79]|11|3[379]|40|66|8[5-9]|9[02-9])', , , , '112'],
                  [, , '141\\d', , , , '1410', , , [4]],
                  [, , '1(?:10|41)\\d|90[24679]', , , , '902', , , [3, 4]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SB: [
                  ,
                  [, , '[127-9]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '999', , , , '999'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SB',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '999', , , , '999'],
                  ,
                  [, , '1(?:[02]\\d|1[12]|[35][01]|[49][1-9]|6[2-9]|7[7-9]|8[0-8])|269|777|835|9(?:[01]1|22|33|55|77|88|99)', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SC: [
                  ,
                  [, , '[19]\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:1[1468]|60)|999', , , , '111', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SC',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '999', , , , '999', , , [3]],
                  ,
                  [, , '1(?:[06]\\d|1[0-246-8]|2[0-8]|3[13]|4[0-2]|5[15]|7[124-6]|8[158]|9[015])|9(?:6\\d\\d|99)', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SD: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '999', , , , '999'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SD',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '999', , , , '999'],
                  ,
                  [, , '999', , , , '999'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SE: [
                  ,
                  [, , '[1-37-9]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '11(?:2|(?:3|6\\d)\\d\\d|414|77)|900\\d\\d', , , , '112'],
                  [, , '11811[89]|72\\d{3}', , , , '72000', , , [5, 6]],
                  ,
                  ,
                  ,
                  'SE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|90000', , , , '112', , , [3, 5]],
                  ,
                  [
                    ,
                    ,
                    '11(?:[25]|313|6(?:00[06]|1(?:1[17]|23))|7[0-8])|2(?:2[02358]|33|4[01]|50|6[1-4])|32[13]|8(?:22|88)|9(?:0(?:00|51)0|12)|(?:11(?:4|8[02-46-9])|7\\d\\d|90[2-4])\\d\\d|(?:118|90)1(?:[02-9]\\d|1[013-9])',
                    ,
                    ,
                    ,
                    '112'
                  ],
                  [, , , , , , , , , [-1]],
                  [, , '2(?:2[02358]|33|4[01]|50|6[1-4])|32[13]|8(?:22|88)|912', , , , '220', , , [3]],
                  ,
                  [, , '7\\d{4}', , , , '70000', , , [5]]
                ],
                SG: [
                  ,
                  [, , '[179]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '99[359]', , , , '993', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SG',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '99[359]', , , , '993', , , [3]],
                  ,
                  [, , '1(?:(?:[01368]\\d|44)\\d|[57]\\d{2,3}|9(?:0[1-9]|[1-9]\\d))|77222|99[02-9]|100', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '772\\d\\d', , , , '77200', , , [5]]
                ],
                SH: [
                  ,
                  [, , '[129]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '9(?:11|99)', , , , '911', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SH',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:11|99)', , , , '911', , , [3]],
                  ,
                  [, , '1\\d{2,3}|26[01]\\d\\d|9(?:11|99)', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SI: [
                  ,
                  [, , '1\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '11(?:(?:0|6\\d)\\d\\d|[23]|8\\d\\d?)', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SI',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[23]', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:00[146]|[23]|6(?:000|1(?:11|23))|8(?:[08]|99))|9(?:059|1(?:0[12]|16)|5|70|87|9(?:00|[149])))|19(?:08|81)[09]', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SJ: [
                  ,
                  [, , '[01]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '11[023]', , , , '110', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SJ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[023]', , , , '110', , , [3]],
                  ,
                  [, , '04\\d{3}|11[023]', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , '04\\d{3}', , , , '04000', , , [5]]
                ],
                SK: [
                  ,
                  [, , '1\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:1(?:2|6\\d{3})|5[058])', , , , '112', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SK',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|5[058])', , , , '112', , , [3]],
                  ,
                  [, , '1(?:1(?:2|6(?:000|111)|8[0-8])|[248]\\d{3}|5[0589])', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SL: [
                  ,
                  [, , '[069]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '(?:01|99)9', , , , '019', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SL',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '(?:01|99)9', , , , '019', , , [3]],
                  ,
                  [, , '(?:01|99)9|60400', , , , '019'],
                  [, , , , , , , , , [-1]],
                  [, , '604\\d\\d', , , , '60400', , , [5]],
                  ,
                  [, , '604\\d\\d', , , , '60400', , , [5]]
                ],
                SM: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[358]', , , , '113'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[358]', , , , '113'],
                  ,
                  [, , '11[358]', , , , '113'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SN: [
                  ,
                  [, , '[12]\\d{1,5}', , , , , , , [2, 3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:515|[78])|2(?:00|1)\\d{3}', , , , '17', , , [2, 4, 5, 6]],
                  [, , '2(?:0[246]|[468])\\d{3}', , , , '24000', , , [5, 6]],
                  ,
                  ,
                  ,
                  'SN',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[78]', , , , '17', , , [2]],
                  ,
                  [, , '1(?:1[69]|(?:[246]\\d|51)\\d)|2(?:0[0-246]|[12468])\\d{3}|1[278]', , , , '12'],
                  [, , '2(?:01|2)\\d{3}', , , , '22000', , , [5, 6]],
                  [, , '1[46]\\d\\d', , , , '1400', , , [4]],
                  ,
                  [, , '2[468]\\d{3}', , , , '24000', , , [5]]
                ],
                SO: [
                  ,
                  [, , '[57-9]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '555|888|999', , , , '555'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SO',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '555|888|999', , , , '555'],
                  ,
                  [, , '555|777|888|999', , , , '555'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SR: [
                  ,
                  [, , '1\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '115', , , , '115', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '115', , , , '115', , , [3]],
                  ,
                  [, , '1\\d{2,3}', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SS: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '999', , , , '999'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SS',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '999', , , , '999'],
                  ,
                  [, , '999', , , , '999'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ST: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '112', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'ST',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112', , , , '112'],
                  ,
                  [, , '112', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SV: [
                  ,
                  [, , '[149]\\d\\d(?:\\d{2,3})?', , , , , , , [3, 5, 6]],
                  ,
                  ,
                  [, , '116\\d{3}|911', , , , '911', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SV',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '91[13]', , , , '911', , , [3]],
                  ,
                  [, , '1(?:1(?:2|6111)|2[136-8]|3[0-6]|9[05])|40404|9(?:1\\d|29)', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , '404\\d\\d', , , , '40400', , , [5]],
                  ,
                  [, , '404\\d\\d', , , , '40400', , , [5]]
                ],
                SX: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:19|88)', , , , '919'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SX',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '919', , , , '919'],
                  ,
                  [, , '9(?:19|88)', , , , '919'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SY: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[023]', , , , '110'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SY',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[023]', , , , '110'],
                  ,
                  [, , '11[023]', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                SZ: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '999', , , , '999'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'SZ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '999', , , , '999'],
                  ,
                  [, , '999', , , , '999'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TC: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'TC',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:11|99)', , , , '911'],
                  ,
                  [, , '9(?:11|88|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TD: [
                  ,
                  [, , '1\\d', , , , , , , [2]],
                  ,
                  ,
                  [, , '1[78]', , , , '17'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'TD',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[78]', , , , '17'],
                  ,
                  [, , '1[78]', , , , '17'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TG: [
                  ,
                  [, , '1\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:1[78]|7[127])', , , , '117', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'TG',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[78]|7[127])', , , , '117', , , [3]],
                  ,
                  [, , '1(?:011|1[078]|7[127])', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TH: [
                  ,
                  [, , '1\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '1(?:1(?:00|2[03]|3[3479]|7[67]|9[0246])|578|6(?:44|6[79]|88|9[16])|88\\d|9[19])|1[15]55', , , , '191'],
                  [, , '1(?:113|2[23]\\d|5(?:09|56))', , , , '1113', , , [4]],
                  ,
                  ,
                  ,
                  'TH',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:669|9[19])', , , , '191'],
                  ,
                  [
                    ,
                    ,
                    '1(?:0[0-2]|1(?:0[03]|1[1-35]|2[0358]|3[03-79]|4[02-489]|5[04-9]|6[04-79]|7[03-9]|8[027-9]|9[02-9])|2(?:22|3[89]|66)|3(?:18|2[23]|3[013]|5[56]|6[45]|73)|477|5(?:0\\d|4[0-37-9]|5[1-8]|6[01679]|7[12568]|8[0-24589]|9[013589])|6(?:0[0-29]|2[03]|4[3-6]|6[1-9]|7[0257-9]|8[0158]|9[014-9])|7(?:[14]9|7[27]|90)|888|9[19])',
                    ,
                    ,
                    ,
                    '100'
                  ],
                  [
                    ,
                    ,
                    '1(?:1(?:03|1[15]|2[58]|3[056]|4[02-49]|5[046-9]|7[03-589]|9[57-9])|5(?:0[0-8]|4[0-378]|5[1-478]|7[156])|6(?:20|4[356]|6[1-68]|7[057-9]|8[015]|9[0457-9]))|1(?:1[68]|26|3[1-35]|5[689]|60|7[17])\\d',
                    ,
                    ,
                    ,
                    '1103',
                    ,
                    ,
                    [4]
                  ],
                  [, , '114[89]', , , , '1148', , , [4]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TJ: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '1(?:0[1-3]|12)', , , , '101'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'TJ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:0[1-3]|12)', , , , '101'],
                  ,
                  [, , '1(?:0[1-3]|12)', , , , '101'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TL: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[25]', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'TL',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[25]', , , , '112'],
                  ,
                  [, , '1(?:0[02]|1[25]|2[0138]|72|9[07])', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TM: [
                  ,
                  [, , '0\\d', , , , , , , [2]],
                  ,
                  ,
                  [, , '0[1-49]', , , , '01'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'TM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '0[1-3]', , , , '01'],
                  ,
                  [, , '0[1-49]', , , , '01'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TN: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '19[078]', , , , '190'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'TN',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '19[078]', , , , '190'],
                  ,
                  [, , '19[078]', , , , '190'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TO: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|22|33|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'TO',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:11|22|33|99)', , , , '911'],
                  ,
                  [, , '9(?:11|22|33|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TR: [
                  ,
                  [, , '[1-9]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '1(?:1[02]|22|3[126]|4[04]|5[15-9]|6[18]|77|83)', , , , '110', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'TR',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:1[02]|55)', , , , '110', , , [3]],
                  ,
                  [
                    ,
                    ,
                    '1(?:1(?:[02-79]|8(?:1[018]|2[0245]|3[2-4]|42|5[058]|6[06]|7[07]|8[01389]|9[089]))|3(?:37|[58]6|65)|471|5(?:07|78)|6(?:[02]6|99)|8(?:63|95))|2(?:077|268|4(?:17|23)|5(?:7[26]|82)|6[14]4|8\\d\\d|9(?:30|89))|3(?:0(?:05|72)|353|4(?:06|30|64)|502|674|747|851|9(?:1[29]|60))|4(?:0(?:25|3[12]|[47]2)|3(?:3[13]|[89]1)|439|5(?:43|55)|717|832)|5(?:145|290|[4-6]\\d\\d|772|833|9(?:[06]1|92))|6(?:236|6(?:12|39|8[59])|769)|7890|8(?:688|7(?:28|65)|85[06])|9(?:159|290)|1[2-9]\\d',
                    ,
                    ,
                    ,
                    '110'
                  ],
                  [, , '(?:285|542)0', , , , '2850', , , [4]],
                  [, , , , , , , , , [-1]],
                  ,
                  [
                    ,
                    ,
                    '1(?:3(?:37|[58]6|65)|4(?:4|71)|5(?:07|78)|6(?:[02]6|99)|8(?:3|63|95))|(?:2(?:07|26|4[12]|5[78]|6[14]|8\\d|9[38])|3(?:0[07]|[38]5|4[036]|50|67|74|9[16])|4(?:0[2-47]|3[389]|[48]3|5[45]|71)|5(?:14|29|[4-6]\\d|77|83|9[069])|6(?:23|6[138]|76)|789|8(?:68|7[26]|85)|9(?:15|29))\\d',
                    ,
                    ,
                    ,
                    '144',
                    ,
                    ,
                    [3, 4]
                  ]
                ],
                TT: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:88|9[09])', , , , '988'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'TT',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '99[09]', , , , '990'],
                  ,
                  [, , '9(?:88|9[09])', , , , '988'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TV: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '911', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'TV',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '911', , , , '911'],
                  ,
                  [, , '1\\d\\d|911', , , , '100'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TW: [
                  ,
                  [, , '1\\d{2,3}', , , , , , , [3, 4]],
                  ,
                  ,
                  [, , '11[0289]|1(?:81|92)\\d', , , , '110'],
                  [, , '10[56]', , , , '105', , , [3]],
                  ,
                  ,
                  ,
                  'TW',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[029]', , , , '110', , , [3]],
                  ,
                  [, , '1(?:0[04-6]|1[0237-9]|3[389]|6[05-8]|7[07]|8(?:0|11)|9(?:19|22|5[057]|68|8[05]|9[15689]))', , , , '100'],
                  [, , '1(?:65|9(?:1\\d|50|85|98))', , , , '165'],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                TZ: [
                  ,
                  [, , '[149]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '1(?:1[0-79]|9[09])|999', , , , '110', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'TZ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[0-245]|999', , , , '110', , , [3]],
                  ,
                  [, , '1(?:1\\d|9[09])|46400|999', , , , '110'],
                  [, , , , , , , , , [-1]],
                  [, , '464\\d\\d', , , , '46400', , , [5]],
                  ,
                  [, , '464\\d\\d', , , , '46400', , , [5]]
                ],
                UA: [
                  ,
                  [, , '[189]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '1(?:0[1-3]|1(?:2|6\\d{3}))', , , , '101', , , [3, 6]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'UA',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:0[1-3]|12)', , , , '101', , , [3]],
                  ,
                  [, , '1(?:0[1-49]|1(?:2|6(?:000|1(?:11|23))|8\\d\\d?)|(?:[278]|5\\d)\\d)|[89]00\\d\\d?|151|1(?:06|4\\d|6)\\d\\d', , , , '101'],
                  [, , , , , , , , , [-1]],
                  [, , '(?:118|[89]00)\\d\\d?', , , , '1180', , , [4, 5]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                UG: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '999', , , , '999'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'UG',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '999', , , , '999'],
                  ,
                  [, , '999', , , , '999'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                US: [
                  ,
                  [, , '[1-9]\\d{2,5}', , , , , , , [3, 4, 5, 6]],
                  ,
                  ,
                  [, , '112|611|9(?:11|33|88)', , , , '112', , , [3]],
                  [
                    ,
                    ,
                    '24280|(?:381|968)35|4(?:3355|7553|8221)|5(?:(?:489|934)2|5928)|72078|(?:323|960)40|(?:276|414)63|(?:2(?:520|744)|7390|9968)9|(?:693|732|976)88|(?:3(?:556|825)|5294|8623|9729)4|(?:3378|4136|7642|8961|9979)6|(?:4(?:6(?:15|32)|827)|(?:591|720)8|9529)7',
                    ,
                    ,
                    ,
                    '24280',
                    ,
                    ,
                    [5]
                  ],
                  ,
                  ,
                  ,
                  'US',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|911', , , , '112', , , [3]],
                  ,
                  [, , '11(?:2|5[1-47]|[68]\\d|7[0-57]|98)|[2-9]\\d{3,5}|[2-8]11|9(?:11|33|88)', , , , '112'],
                  [
                    ,
                    ,
                    '2(?:3333|(?:4224|7562|900)2|56447|6688)|3(?:1010|2665|7404)|40404|560560|6(?:0060|22639|5246|7622)|7(?:0701|3822|4666)|8(?:(?:3825|7226)5|4816)|99099',
                    ,
                    ,
                    ,
                    '23333',
                    ,
                    ,
                    [5, 6]
                  ],
                  [, , '336\\d\\d|[2-9]\\d{3}|[2356]11', , , , '211', , , [3, 4, 5]],
                  ,
                  [, , '[2-9]\\d{4,5}', , , , '20000', , , [5, 6]]
                ],
                UY: [
                  ,
                  [, , '[19]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '128|911', , , , '128', , , [3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'UY',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '128|911', , , , '128', , , [3]],
                  ,
                  [, , '1(?:0[4-9]|1[2368]|2[0-3568]|787|997\\d?)|911', , , , '104'],
                  [, , '178\\d', , , , '1780', , , [4]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                UZ: [
                  ,
                  [, , '[04]\\d(?:\\d(?:\\d{2})?)?', , , , , , , [2, 3, 5]],
                  ,
                  ,
                  [, , '0(?:0[1-3]|[1-3]|50)', , , , '01', , , [2, 3]],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'UZ',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '0(?:0[1-3]|[1-3]|50)', , , , '01', , , [2, 3]],
                  ,
                  [, , '0(?:0[1-3]|[1-3]|50)|45400', , , , '01'],
                  [, , , , , , , , , [-1]],
                  [, , '454\\d\\d', , , , '45400', , , [5]],
                  ,
                  [, , '454\\d\\d', , , , '45400', , , [5]]
                ],
                VA: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[2358]', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'VA',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[2358]', , , , '112'],
                  ,
                  [, , '11[2358]', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                VC: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'VC',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:11|99)', , , , '911'],
                  ,
                  [, , '9(?:11|88|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                VE: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '1(?:12|71)|911', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'VE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|71)|911', , , , '112'],
                  ,
                  [, , '1(?:12|71)|911', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                VG: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'VG',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:11|99)', , , , '911'],
                  ,
                  [, , '9(?:11|88|99)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                VI: [
                  ,
                  [, , '9\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'VI',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '911', , , , '911'],
                  ,
                  [, , '9(?:11|88)', , , , '911'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                VN: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '11[3-5]', , , , '113'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'VN',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '11[3-5]', , , , '113'],
                  ,
                  [, , '11[3-5]', , , , '113'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                VU: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '112', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'VU',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112', , , , '112'],
                  ,
                  [, , '112', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                WF: [
                  ,
                  [, , '1\\d', , , , , , , [2]],
                  ,
                  ,
                  [, , '1[578]', , , , '15'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'WF',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1[578]', , , , '15'],
                  ,
                  [, , '1[578]', , , , '15'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                WS: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '9(?:11|9[4-69])', , , , '911'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'WS',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '9(?:11|9[4-69])', , , , '911'],
                  ,
                  [, , '1(?:1[12]|2[0-6]|[39]0)|9(?:11|9[4-79])', , , , '111'],
                  [, , , , , , , , , [-1]],
                  [, , '12[0-6]', , , , '120'],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                XK: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '1(?:12|9[2-4])', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'XK',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|9[2-4])', , , , '112'],
                  ,
                  [, , '1(?:12|9[2-4])', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                YE: [
                  ,
                  [, , '1\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '19[1459]', , , , '191'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'YE',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '19[1459]', , , , '191'],
                  ,
                  [, , '19[1459]', , , , '191'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                YT: [
                  ,
                  [, , '1\\d\\d?', , , , , , , [2, 3]],
                  ,
                  ,
                  [, , '1(?:12|5)', , , , '15'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'YT',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:12|5)', , , , '15'],
                  ,
                  [, , '1(?:12|5)', , , , '15'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ZA: [
                  ,
                  [, , '[134]\\d{2,4}', , , , , , , [3, 4, 5]],
                  ,
                  ,
                  [, , '1(?:01\\d\\d|12)', , , , '112', , , [3, 5]],
                  [, , '41(?:348|851)', , , , '41348', , , [5]],
                  ,
                  ,
                  ,
                  'ZA',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '1(?:01(?:11|77)|12)', , , , '112', , , [3, 5]],
                  ,
                  [, , '1(?:0(?:1(?:11|77)|20|7)|1[12]|77(?:3[237]|[45]7|6[279]|9[26]))|[34]\\d{4}', , , , '107'],
                  [, , '3(?:078[23]|7(?:064|567)|8126)|4(?:394[16]|7751|8837)|4[23]699', , , , '30782', , , [5]],
                  [, , '111', , , , '111', , , [3]],
                  ,
                  [, , '[34]\\d{4}', , , , '30000', , , [5]]
                ],
                ZM: [
                  ,
                  [, , '[19]\\d\\d', , , , , , , [3]],
                  ,
                  ,
                  [, , '112|99[139]', , , , '112'],
                  [, , , , , , , , , [-1]],
                  ,
                  ,
                  ,
                  'ZM',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|99[139]', , , , '112'],
                  ,
                  [, , '112|99[139]', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , , , , , , , , [-1]],
                  ,
                  [, , , , , , , , , [-1]]
                ],
                ZW: [
                  ,
                  [, , '[139]\\d\\d(?:\\d{2})?', , , , , , , [3, 5]],
                  ,
                  ,
                  [, , '112|9(?:5[023]|61|9[3-59])', , , , '112', , , [3]],
                  [, , '3[013-57-9]\\d{3}', , , , '30000', , , [5]],
                  ,
                  ,
                  ,
                  'ZW',
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  ,
                  [, , '112|99[3-59]', , , , '112', , , [3]],
                  ,
                  [, , '11[2469]|3[013-57-9]\\d{3}|9(?:5[023]|6[0-25]|9[3-59])', , , , '112'],
                  [, , , , , , , , , [-1]],
                  [, , '114|9(?:5[023]|6[0-25])', , , , '114', , , [3]],
                  ,
                  [, , , , , , , , , [-1]]
                ]
              }; /*

 Copyright (C) 2018 The Libphonenumber Authors.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

 http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.
*/
              i18n.phonenumbers.ShortNumberInfo = function () {
                this.regionToMetadataMap = {};
              };
              goog.addSingletonGetter(i18n.phonenumbers.ShortNumberInfo);
              i18n.phonenumbers.ShortNumberInfo.REGIONS_WHERE_EMERGENCY_NUMBERS_MUST_BE_EXACT_ = ['BR', 'CL', 'NI'];
              i18n.phonenumbers.ShortNumberInfo.ShortNumberCost = { TOLL_FREE: 0, STANDARD_RATE: 1, PREMIUM_RATE: 2, UNKNOWN_COST: 3 };
              i18n.phonenumbers.ShortNumberInfo.prototype.getRegionCodesForCountryCode_ = function (a) {
                return (a = i18n.phonenumbers.metadata.countryCodeToRegionCodeMap[a]) ? a : [];
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.regionDialingFromMatchesNumber_ = function (a, b) {
                a = this.getRegionCodesForCountryCode_(a.getCountryCodeOrDefault());
                return null != b && a.includes(b);
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.isPossibleShortNumberForRegion = function (a, b) {
                if (!this.regionDialingFromMatchesNumber_(a, b)) return !1;
                b = this.getMetadataForRegion_(b);
                if (!b) return !1;
                a = this.getNationalSignificantNumber_(a).length;
                return b.getGeneralDesc().possibleLengthArray().includes(a);
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.isPossibleShortNumber = function (a) {
                var b = this.getRegionCodesForCountryCode_(a.getCountryCodeOrDefault());
                a = this.getNationalSignificantNumber_(a).length;
                for (var c = 0; c < b.length; c++) {
                  var d = this.getMetadataForRegion_(b[c]);
                  if (d && d.getGeneralDesc().possibleLengthArray().includes(a)) return !0;
                }
                return !1;
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.isValidShortNumberForRegion = function (a, b) {
                if (!this.regionDialingFromMatchesNumber_(a, b)) return !1;
                b = this.getMetadataForRegion_(b);
                if (!b) return !1;
                a = this.getNationalSignificantNumber_(a);
                var c = b.getGeneralDesc();
                if (!this.matchesPossibleNumberAndNationalNumber_(a, c)) return !1;
                b = b.getShortCode();
                return this.matchesPossibleNumberAndNationalNumber_(a, b);
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.isValidShortNumber = function (a) {
                var b = this.getRegionCodesForCountryCode_(a.getCountryCodeOrDefault()),
                  c = this.getRegionCodeForShortNumberFromRegionList_(a, b);
                return 1 < b.length && null != c ? !0 : this.isValidShortNumberForRegion(a, c);
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.getExpectedCostForRegion = function (a, b) {
                var c = i18n.phonenumbers.ShortNumberInfo.ShortNumberCost;
                if (!this.regionDialingFromMatchesNumber_(a, b)) return c.UNKNOWN_COST;
                var d = this.getMetadataForRegion_(b);
                if (!d) return c.UNKNOWN_COST;
                a = this.getNationalSignificantNumber_(a);
                return d.getGeneralDesc().possibleLengthArray().includes(a.length)
                  ? this.matchesPossibleNumberAndNationalNumber_(a, d.getPremiumRate())
                    ? c.PREMIUM_RATE
                    : this.matchesPossibleNumberAndNationalNumber_(a, d.getStandardRate())
                      ? c.STANDARD_RATE
                      : this.matchesPossibleNumberAndNationalNumber_(a, d.getTollFree()) || this.isEmergencyNumber(a, b)
                        ? c.TOLL_FREE
                        : c.UNKNOWN_COST
                  : c.UNKNOWN_COST;
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.getExpectedCost = function (a) {
                var b = i18n.phonenumbers.ShortNumberInfo.ShortNumberCost,
                  c = this.getRegionCodesForCountryCode_(a.getCountryCodeOrDefault());
                if (0 === c.length) return b.UNKNOWN_COST;
                if (1 === c.length) return this.getExpectedCostForRegion(a, c[0]);
                for (var d = b.TOLL_FREE, e = 0; e < c.length; e++) {
                  var f = this.getExpectedCostForRegion(a, c[e]);
                  switch (f) {
                    case b.PREMIUM_RATE:
                      return b.PREMIUM_RATE;
                    case b.UNKNOWN_COST:
                      d = b.UNKNOWN_COST;
                      break;
                    case b.STANDARD_RATE:
                      d !== b.UNKNOWN_COST && (d = b.STANDARD_RATE);
                      break;
                    case b.TOLL_FREE:
                      break;
                    default:
                      throw Error('Unrecognized cost for region: ' + f);
                  }
                }
                return d;
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.getRegionCodeForShortNumberFromRegionList_ = function (a, b) {
                if (0 === b.length) return null;
                if (1 === b.length) return b[0];
                a = this.getNationalSignificantNumber_(a);
                for (var c = 0; c < b.length; c++) {
                  var d = b[c],
                    e = this.getMetadataForRegion_(d);
                  if (e && this.matchesPossibleNumberAndNationalNumber_(a, e.getShortCode())) return d;
                }
                return null;
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.getSupportedRegions = function () {
                return Object.keys(i18n.phonenumbers.shortnumbermetadata.countryToMetadata).filter(function (a) {
                  return isNaN(a);
                });
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.getExampleShortNumber = function (a) {
                a = this.getMetadataForRegion_(a);
                if (!a) return '';
                a = a.getShortCode();
                return a.hasExampleNumber() ? a.getExampleNumber() || '' : '';
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.getExampleShortNumberForCost = function (a, b) {
                a = this.getMetadataForRegion_(a);
                if (!a) return '';
                var c = i18n.phonenumbers.ShortNumberInfo.ShortNumberCost,
                  d = null;
                switch (b) {
                  case c.TOLL_FREE:
                    d = a.getTollFree();
                    break;
                  case c.STANDARD_RATE:
                    d = a.getStandardRate();
                    break;
                  case c.PREMIUM_RATE:
                    d = a.getPremiumRate();
                }
                return d && d.hasExampleNumber() ? d.getExampleNumber() || '' : '';
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.connectsToEmergencyNumber = function (a, b) {
                return this.matchesEmergencyNumberHelper_(a, b, !0);
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.isEmergencyNumber = function (a, b) {
                return this.matchesEmergencyNumberHelper_(a, b, !1);
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.getMetadataForRegion_ = function (a) {
                if (!a) return null;
                a = a.toUpperCase();
                var b = this.regionToMetadataMap[a];
                if (null == b) {
                  b = new goog.proto2.PbLiteSerializer();
                  var c = i18n.phonenumbers.shortnumbermetadata.countryToMetadata[a];
                  if (null == c) return null;
                  b = b.deserialize(i18n.phonenumbers.PhoneMetadata.getDescriptor(), c);
                  this.regionToMetadataMap[a] = b;
                }
                return b;
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.matchesEmergencyNumberHelper_ = function (a, b, c) {
                var d = i18n.phonenumbers.PhoneNumberUtil.extractPossibleNumber(a);
                if (i18n.phonenumbers.PhoneNumberUtil.LEADING_PLUS_CHARS_PATTERN.test(d)) return !1;
                a = this.getMetadataForRegion_(b);
                if (null == a || !a.hasEmergency()) return !1;
                d = i18n.phonenumbers.PhoneNumberUtil.normalizeDigitsOnly(d);
                b = c && !i18n.phonenumbers.ShortNumberInfo.REGIONS_WHERE_EMERGENCY_NUMBERS_MUST_BE_EXACT_.includes(b);
                c = a.getEmergency().getNationalNumberPatternOrDefault();
                return i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(c, d) || (b && i18n.phonenumbers.PhoneNumberUtil.matchesPrefix(c, d));
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.isCarrierSpecific = function (a) {
                var b = this.getRegionCodesForCountryCode_(a.getCountryCodeOrDefault());
                b = this.getRegionCodeForShortNumberFromRegionList_(a, b);
                a = this.getNationalSignificantNumber_(a);
                b = this.getMetadataForRegion_(b);
                return !!b && this.matchesPossibleNumberAndNationalNumber_(a, b.getCarrierSpecific());
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.isCarrierSpecificForRegion = function (a, b) {
                if (!this.regionDialingFromMatchesNumber_(a, b)) return !1;
                a = this.getNationalSignificantNumber_(a);
                b = this.getMetadataForRegion_(b);
                return !!b && this.matchesPossibleNumberAndNationalNumber_(a, b.getCarrierSpecific());
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.isSmsServiceForRegion = function (a, b) {
                if (!this.regionDialingFromMatchesNumber_(a, b)) return !1;
                b = this.getMetadataForRegion_(b);
                a = this.getNationalSignificantNumber_(a);
                return !!b && this.matchesPossibleNumberAndNationalNumber_(a, b.getSmsServices());
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.getNationalSignificantNumber_ = function (a) {
                if (!a.hasNationalNumber()) return '';
                var b = '' + a.getNationalNumber();
                return a.hasItalianLeadingZero() && a.getItalianLeadingZero() && 0 < a.getNumberOfLeadingZerosOrDefault()
                  ? Array(a.getNumberOfLeadingZerosOrDefault() + 1).join('0') + b
                  : b;
              };
              i18n.phonenumbers.ShortNumberInfo.prototype.matchesPossibleNumberAndNationalNumber_ = function (a, b) {
                return 0 < b.possibleLengthArray().length && !b.possibleLengthArray().includes(a.length)
                  ? !1
                  : i18n.phonenumbers.PhoneNumberUtil.matchesEntirely(b.getNationalNumberPatternOrDefault(), a.toString());
              };
              i18n.phonenumbers.AsYouTypeFormatter = function (a) {
                this.DIGIT_PLACEHOLDER_ = '\u2008';
                this.DIGIT_PATTERN_ = new RegExp(this.DIGIT_PLACEHOLDER_);
                this.currentOutput_ = '';
                this.formattingTemplate_ = new goog.string.StringBuffer();
                this.currentFormattingPattern_ = '';
                this.accruedInput_ = new goog.string.StringBuffer();
                this.accruedInputWithoutFormatting_ = new goog.string.StringBuffer();
                this.ableToFormat_ = !0;
                this.isExpectingCountryCallingCode_ = this.isCompleteNumber_ = this.inputHasFormatting_ = !1;
                this.phoneUtil_ = i18n.phonenumbers.PhoneNumberUtil.getInstance();
                this.positionToRemember_ = this.originalPosition_ = this.lastMatchPosition_ = 0;
                this.prefixBeforeNationalNumber_ = new goog.string.StringBuffer();
                this.shouldAddSpaceAfterNationalPrefix_ = !1;
                this.extractedNationalPrefix_ = '';
                this.nationalNumber_ = new goog.string.StringBuffer();
                this.possibleFormats_ = [];
                this.defaultCountry_ = a;
                this.defaultMetadata_ = this.currentMetadata_ = this.getMetadataForRegion_(this.defaultCountry_);
              };
              i18n.phonenumbers.AsYouTypeFormatter.SEPARATOR_BEFORE_NATIONAL_NUMBER_ = ' ';
              i18n.phonenumbers.AsYouTypeFormatter.EMPTY_METADATA_ = new i18n.phonenumbers.PhoneMetadata();
              i18n.phonenumbers.AsYouTypeFormatter.EMPTY_METADATA_.setInternationalPrefix('NA');
              i18n.phonenumbers.AsYouTypeFormatter.ELIGIBLE_FORMAT_PATTERN_ = new RegExp(
                '^[' +
                  i18n.phonenumbers.PhoneNumberUtil.VALID_PUNCTUATION +
                  ']*\\$1[' +
                  i18n.phonenumbers.PhoneNumberUtil.VALID_PUNCTUATION +
                  ']*(\\$\\d[' +
                  i18n.phonenumbers.PhoneNumberUtil.VALID_PUNCTUATION +
                  ']*)*$'
              );
              i18n.phonenumbers.AsYouTypeFormatter.NATIONAL_PREFIX_SEPARATORS_PATTERN_ = /[- ]/;
              i18n.phonenumbers.AsYouTypeFormatter.MIN_LEADING_DIGITS_LENGTH_ = 3;
              i18n.phonenumbers.AsYouTypeFormatter.prototype.getMetadataForRegion_ = function (a) {
                a = this.phoneUtil_.getCountryCodeForRegion(a);
                a = this.phoneUtil_.getRegionCodeForCountryCode(a);
                a = this.phoneUtil_.getMetadataForRegion(a);
                return null != a ? a : i18n.phonenumbers.AsYouTypeFormatter.EMPTY_METADATA_;
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.maybeCreateNewTemplate_ = function () {
                for (var a = this.possibleFormats_.length, b = 0; b < a; ++b) {
                  var c = this.possibleFormats_[b],
                    d = c.getPatternOrDefault();
                  if (this.currentFormattingPattern_ == d) return !1;
                  if (this.createFormattingTemplate_(c))
                    return (
                      (this.currentFormattingPattern_ = d),
                      (this.shouldAddSpaceAfterNationalPrefix_ = i18n.phonenumbers.AsYouTypeFormatter.NATIONAL_PREFIX_SEPARATORS_PATTERN_.test(
                        c.getNationalPrefixFormattingRule()
                      )),
                      (this.lastMatchPosition_ = 0),
                      !0
                    );
                }
                return (this.ableToFormat_ = !1);
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.getAvailableFormats_ = function (a) {
                for (
                  var b =
                      this.isCompleteNumber_ && 0 == this.extractedNationalPrefix_.length && 0 < this.currentMetadata_.intlNumberFormatCount()
                        ? this.currentMetadata_.intlNumberFormatArray()
                        : this.currentMetadata_.numberFormatArray(),
                    c = b.length,
                    d = 0;
                  d < c;
                  ++d
                ) {
                  var e = b[d];
                  (0 < this.extractedNationalPrefix_.length &&
                    this.phoneUtil_.formattingRuleHasFirstGroupOnly(e.getNationalPrefixFormattingRuleOrDefault()) &&
                    !e.getNationalPrefixOptionalWhenFormatting() &&
                    !e.hasDomesticCarrierCodeFormattingRule()) ||
                    ((0 != this.extractedNationalPrefix_.length ||
                      this.isCompleteNumber_ ||
                      this.phoneUtil_.formattingRuleHasFirstGroupOnly(e.getNationalPrefixFormattingRuleOrDefault()) ||
                      e.getNationalPrefixOptionalWhenFormatting()) &&
                      i18n.phonenumbers.AsYouTypeFormatter.ELIGIBLE_FORMAT_PATTERN_.test(e.getFormatOrDefault()) &&
                      this.possibleFormats_.push(e));
                }
                this.narrowDownPossibleFormats_(a);
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.narrowDownPossibleFormats_ = function (a) {
                for (
                  var b = [], c = a.length - i18n.phonenumbers.AsYouTypeFormatter.MIN_LEADING_DIGITS_LENGTH_, d = this.possibleFormats_.length, e = 0;
                  e < d;
                  ++e
                ) {
                  var f = this.possibleFormats_[e];
                  if (0 == f.leadingDigitsPatternCount()) b.push(this.possibleFormats_[e]);
                  else {
                    var g = Math.min(c, f.leadingDigitsPatternCount() - 1);
                    f = f.getLeadingDigitsPattern(g);
                    0 == a.search(f) && b.push(this.possibleFormats_[e]);
                  }
                }
                this.possibleFormats_ = b;
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.createFormattingTemplate_ = function (a) {
                var b = a.getPatternOrDefault();
                this.formattingTemplate_.clear();
                a = this.getFormattingTemplate_(b, a.getFormatOrDefault());
                return 0 < a.length ? (this.formattingTemplate_.append(a), !0) : !1;
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.getFormattingTemplate_ = function (a, b) {
                var c = '999999999999999'.match(a)[0];
                if (c.length < this.nationalNumber_.getLength()) return '';
                a = c.replace(new RegExp(a, 'g'), b);
                return (a = a.replace(/9/g, this.DIGIT_PLACEHOLDER_));
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.clear = function () {
                this.currentOutput_ = '';
                this.accruedInput_.clear();
                this.accruedInputWithoutFormatting_.clear();
                this.formattingTemplate_.clear();
                this.lastMatchPosition_ = 0;
                this.currentFormattingPattern_ = '';
                this.prefixBeforeNationalNumber_.clear();
                this.extractedNationalPrefix_ = '';
                this.nationalNumber_.clear();
                this.ableToFormat_ = !0;
                this.inputHasFormatting_ = !1;
                this.originalPosition_ = this.positionToRemember_ = 0;
                this.isExpectingCountryCallingCode_ = this.isCompleteNumber_ = !1;
                this.possibleFormats_ = [];
                this.shouldAddSpaceAfterNationalPrefix_ = !1;
                this.currentMetadata_ != this.defaultMetadata_ && (this.currentMetadata_ = this.getMetadataForRegion_(this.defaultCountry_));
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.inputDigit = function (a) {
                return (this.currentOutput_ = this.inputDigitWithOptionToRememberPosition_(a, !1));
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.inputDigitAndRememberPosition = function (a) {
                return (this.currentOutput_ = this.inputDigitWithOptionToRememberPosition_(a, !0));
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.inputDigitWithOptionToRememberPosition_ = function (a, b) {
                this.accruedInput_.append(a);
                b && (this.originalPosition_ = this.accruedInput_.getLength());
                this.isDigitOrLeadingPlusSign_(a)
                  ? (a = this.normalizeAndAccrueDigitsAndPlusSign_(a, b))
                  : ((this.ableToFormat_ = !1), (this.inputHasFormatting_ = !0));
                if (!this.ableToFormat_) {
                  if (!this.inputHasFormatting_)
                    if (this.attemptToExtractIdd_()) {
                      if (this.attemptToExtractCountryCallingCode_()) return this.attemptToChoosePatternWithPrefixExtracted_();
                    } else if (this.ableToExtractLongerNdd_())
                      return (
                        this.prefixBeforeNationalNumber_.append(i18n.phonenumbers.AsYouTypeFormatter.SEPARATOR_BEFORE_NATIONAL_NUMBER_),
                        this.attemptToChoosePatternWithPrefixExtracted_()
                      );
                  return this.accruedInput_.toString();
                }
                switch (this.accruedInputWithoutFormatting_.getLength()) {
                  case 0:
                  case 1:
                  case 2:
                    return this.accruedInput_.toString();
                  case 3:
                    if (this.attemptToExtractIdd_()) this.isExpectingCountryCallingCode_ = !0;
                    else return ((this.extractedNationalPrefix_ = this.removeNationalPrefixFromNationalNumber_()), this.attemptToChooseFormattingPattern_());
                  default:
                    if (this.isExpectingCountryCallingCode_)
                      return (
                        this.attemptToExtractCountryCallingCode_() && (this.isExpectingCountryCallingCode_ = !1),
                        this.prefixBeforeNationalNumber_.toString() + this.nationalNumber_.toString()
                      );
                    if (0 < this.possibleFormats_.length) {
                      a = this.inputDigitHelper_(a);
                      b = this.attemptToFormatAccruedDigits_();
                      if (0 < b.length) return b;
                      this.narrowDownPossibleFormats_(this.nationalNumber_.toString());
                      return this.maybeCreateNewTemplate_()
                        ? this.inputAccruedNationalNumber_()
                        : this.ableToFormat_
                          ? this.appendNationalNumber_(a)
                          : this.accruedInput_.toString();
                    }
                    return this.attemptToChooseFormattingPattern_();
                }
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.attemptToChoosePatternWithPrefixExtracted_ = function () {
                this.ableToFormat_ = !0;
                this.isExpectingCountryCallingCode_ = !1;
                this.possibleFormats_ = [];
                this.lastMatchPosition_ = 0;
                this.formattingTemplate_.clear();
                this.currentFormattingPattern_ = '';
                return this.attemptToChooseFormattingPattern_();
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.getExtractedNationalPrefix_ = function () {
                return this.extractedNationalPrefix_;
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.ableToExtractLongerNdd_ = function () {
                if (0 < this.extractedNationalPrefix_.length) {
                  var a = this.nationalNumber_.toString();
                  this.nationalNumber_.clear();
                  this.nationalNumber_.append(this.extractedNationalPrefix_);
                  this.nationalNumber_.append(a);
                  a = this.prefixBeforeNationalNumber_.toString();
                  var b = a.lastIndexOf(this.extractedNationalPrefix_);
                  this.prefixBeforeNationalNumber_.clear();
                  this.prefixBeforeNationalNumber_.append(a.substring(0, b));
                }
                return this.extractedNationalPrefix_ != this.removeNationalPrefixFromNationalNumber_();
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.isDigitOrLeadingPlusSign_ = function (a) {
                return (
                  i18n.phonenumbers.PhoneNumberUtil.CAPTURING_DIGIT_PATTERN.test(a) ||
                  (1 == this.accruedInput_.getLength() && i18n.phonenumbers.PhoneNumberUtil.PLUS_CHARS_PATTERN.test(a))
                );
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.attemptToFormatAccruedDigits_ = function () {
                for (var a = this.nationalNumber_.toString(), b = this.possibleFormats_.length, c = 0; c < b; ++c) {
                  var d = this.possibleFormats_[c],
                    e = d.getPatternOrDefault();
                  if (
                    new RegExp('^(?:' + e + ')$').test(a) &&
                    ((this.shouldAddSpaceAfterNationalPrefix_ = i18n.phonenumbers.AsYouTypeFormatter.NATIONAL_PREFIX_SEPARATORS_PATTERN_.test(
                      d.getNationalPrefixFormattingRule()
                    )),
                    (d = a.replace(new RegExp(e, 'g'), d.getFormat())),
                    (d = this.appendNationalNumber_(d)),
                    i18n.phonenumbers.PhoneNumberUtil.normalizeDiallableCharsOnly(d) == this.accruedInputWithoutFormatting_)
                  )
                    return d;
                }
                return '';
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.appendNationalNumber_ = function (a) {
                var b = this.prefixBeforeNationalNumber_.getLength();
                return this.shouldAddSpaceAfterNationalPrefix_ &&
                  0 < b &&
                  this.prefixBeforeNationalNumber_.toString().charAt(b - 1) != i18n.phonenumbers.AsYouTypeFormatter.SEPARATOR_BEFORE_NATIONAL_NUMBER_
                  ? this.prefixBeforeNationalNumber_ + i18n.phonenumbers.AsYouTypeFormatter.SEPARATOR_BEFORE_NATIONAL_NUMBER_ + a
                  : this.prefixBeforeNationalNumber_ + a;
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.getRememberedPosition = function () {
                if (!this.ableToFormat_) return this.originalPosition_;
                for (
                  var a = 0, b = 0, c = this.accruedInputWithoutFormatting_.toString(), d = this.currentOutput_.toString();
                  a < this.positionToRemember_ && b < d.length;

                )
                  (c.charAt(a) == d.charAt(b) && a++, b++);
                return b;
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.attemptToChooseFormattingPattern_ = function () {
                var a = this.nationalNumber_.toString();
                return a.length >= i18n.phonenumbers.AsYouTypeFormatter.MIN_LEADING_DIGITS_LENGTH_
                  ? (this.getAvailableFormats_(a),
                    (a = this.attemptToFormatAccruedDigits_()),
                    0 < a.length ? a : this.maybeCreateNewTemplate_() ? this.inputAccruedNationalNumber_() : this.accruedInput_.toString())
                  : this.appendNationalNumber_(a);
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.inputAccruedNationalNumber_ = function () {
                var a = this.nationalNumber_.toString(),
                  b = a.length;
                if (0 < b) {
                  for (var c = '', d = 0; d < b; d++) c = this.inputDigitHelper_(a.charAt(d));
                  return this.ableToFormat_ ? this.appendNationalNumber_(c) : this.accruedInput_.toString();
                }
                return this.prefixBeforeNationalNumber_.toString();
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.isNanpaNumberWithNationalPrefix_ = function () {
                if (1 != this.currentMetadata_.getCountryCode()) return !1;
                var a = this.nationalNumber_.toString();
                return '1' == a.charAt(0) && '0' != a.charAt(1) && '1' != a.charAt(1);
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.removeNationalPrefixFromNationalNumber_ = function () {
                var a = this.nationalNumber_.toString(),
                  b = 0;
                if (this.isNanpaNumberWithNationalPrefix_())
                  ((b = 1),
                    this.prefixBeforeNationalNumber_.append('1').append(i18n.phonenumbers.AsYouTypeFormatter.SEPARATOR_BEFORE_NATIONAL_NUMBER_),
                    (this.isCompleteNumber_ = !0));
                else if (this.currentMetadata_.hasNationalPrefixForParsing()) {
                  var c = new RegExp('^(?:' + this.currentMetadata_.getNationalPrefixForParsing() + ')');
                  c = a.match(c);
                  null != c &&
                    null != c[0] &&
                    0 < c[0].length &&
                    ((this.isCompleteNumber_ = !0), (b = c[0].length), this.prefixBeforeNationalNumber_.append(a.substring(0, b)));
                }
                this.nationalNumber_.clear();
                this.nationalNumber_.append(a.substring(b));
                return a.substring(0, b);
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.attemptToExtractIdd_ = function () {
                var a = this.accruedInputWithoutFormatting_.toString(),
                  b = new RegExp('^(?:\\' + i18n.phonenumbers.PhoneNumberUtil.PLUS_SIGN + '|' + this.currentMetadata_.getInternationalPrefix() + ')');
                b = a.match(b);
                return null != b && null != b[0] && 0 < b[0].length
                  ? ((this.isCompleteNumber_ = !0),
                    (b = b[0].length),
                    this.nationalNumber_.clear(),
                    this.nationalNumber_.append(a.substring(b)),
                    this.prefixBeforeNationalNumber_.clear(),
                    this.prefixBeforeNationalNumber_.append(a.substring(0, b)),
                    a.charAt(0) != i18n.phonenumbers.PhoneNumberUtil.PLUS_SIGN &&
                      this.prefixBeforeNationalNumber_.append(i18n.phonenumbers.AsYouTypeFormatter.SEPARATOR_BEFORE_NATIONAL_NUMBER_),
                    !0)
                  : !1;
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.attemptToExtractCountryCallingCode_ = function () {
                if (0 == this.nationalNumber_.getLength()) return !1;
                var a = new goog.string.StringBuffer(),
                  b = this.phoneUtil_.extractCountryCode(this.nationalNumber_, a);
                if (0 == b) return !1;
                this.nationalNumber_.clear();
                this.nationalNumber_.append(a.toString());
                a = this.phoneUtil_.getRegionCodeForCountryCode(b);
                i18n.phonenumbers.PhoneNumberUtil.REGION_CODE_FOR_NON_GEO_ENTITY == a
                  ? (this.currentMetadata_ = this.phoneUtil_.getMetadataForNonGeographicalRegion(b))
                  : a != this.defaultCountry_ && (this.currentMetadata_ = this.getMetadataForRegion_(a));
                this.prefixBeforeNationalNumber_.append('' + b).append(i18n.phonenumbers.AsYouTypeFormatter.SEPARATOR_BEFORE_NATIONAL_NUMBER_);
                this.extractedNationalPrefix_ = '';
                return !0;
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.normalizeAndAccrueDigitsAndPlusSign_ = function (a, b) {
                if (a == i18n.phonenumbers.PhoneNumberUtil.PLUS_SIGN) {
                  var c = a;
                  this.accruedInputWithoutFormatting_.append(a);
                } else
                  ((c = i18n.phonenumbers.PhoneNumberUtil.DIGIT_MAPPINGS[a]), this.accruedInputWithoutFormatting_.append(c), this.nationalNumber_.append(c));
                b && (this.positionToRemember_ = this.accruedInputWithoutFormatting_.getLength());
                return c;
              };
              i18n.phonenumbers.AsYouTypeFormatter.prototype.inputDigitHelper_ = function (a) {
                var b = this.formattingTemplate_.toString();
                if (0 <= b.substring(this.lastMatchPosition_).search(this.DIGIT_PATTERN_)) {
                  var c = b.search(this.DIGIT_PATTERN_);
                  a = b.replace(this.DIGIT_PATTERN_, a);
                  this.formattingTemplate_.clear();
                  this.formattingTemplate_.append(a);
                  this.lastMatchPosition_ = c;
                  return a.substring(0, this.lastMatchPosition_ + 1);
                }
                1 == this.possibleFormats_.length && (this.ableToFormat_ = !1);
                this.currentFormattingPattern_ = '';
                return this.accruedInput_.toString();
              };
              Object.defineProperty(exports, '__esModule', { value: !0 });
              exports.default = i18n.phonenumbers;
              module.exports = exports['default'];
            }).call(this);
          }).call(this, typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : typeof window !== 'undefined' ? window : {});
        },
        {}
      ]
    },
    {},
    [1]
  )(1);
}); /*
CryptoJS v3.1.2
code.google.com/p/crypto-js
(c) 2009-2013 by Jeff Mott. All rights reserved.
code.google.com/p/crypto-js/wiki/License
*/
var CryptoJS =
  CryptoJS ||
  (function (u, p) {
    var d = {},
      l = (d.lib = {}),
      s = function () {},
      t = (l.Base = {
        extend: function (a) {
          s.prototype = this;
          var c = new s();
          a && c.mixIn(a);
          c.hasOwnProperty('init') ||
            (c.init = function () {
              c.$super.init.apply(this, arguments);
            });
          c.init.prototype = c;
          c.$super = this;
          return c;
        },
        create: function () {
          var a = this.extend();
          a.init.apply(a, arguments);
          return a;
        },
        init: function () {},
        mixIn: function (a) {
          for (var c in a) a.hasOwnProperty(c) && (this[c] = a[c]);
          a.hasOwnProperty('toString') && (this.toString = a.toString);
        },
        clone: function () {
          return this.init.prototype.extend(this);
        }
      }),
      r = (l.WordArray = t.extend({
        init: function (a, c) {
          a = this.words = a || [];
          this.sigBytes = c != p ? c : 4 * a.length;
        },
        toString: function (a) {
          return (a || v).stringify(this);
        },
        concat: function (a) {
          var c = this.words,
            e = a.words,
            j = this.sigBytes;
          a = a.sigBytes;
          this.clamp();
          if (j % 4) for (var k = 0; k < a; k++) c[(j + k) >>> 2] |= ((e[k >>> 2] >>> (24 - 8 * (k % 4))) & 255) << (24 - 8 * ((j + k) % 4));
          else if (65535 < e.length) for (k = 0; k < a; k += 4) c[(j + k) >>> 2] = e[k >>> 2];
          else c.push.apply(c, e);
          this.sigBytes += a;
          return this;
        },
        clamp: function () {
          var a = this.words,
            c = this.sigBytes;
          a[c >>> 2] &= 4294967295 << (32 - 8 * (c % 4));
          a.length = u.ceil(c / 4);
        },
        clone: function () {
          var a = t.clone.call(this);
          a.words = this.words.slice(0);
          return a;
        },
        random: function (a) {
          for (var c = [], e = 0; e < a; e += 4) c.push((4294967296 * u.random()) | 0);
          return new r.init(c, a);
        }
      })),
      w = (d.enc = {}),
      v = (w.Hex = {
        stringify: function (a) {
          var c = a.words;
          a = a.sigBytes;
          for (var e = [], j = 0; j < a; j++) {
            var k = (c[j >>> 2] >>> (24 - 8 * (j % 4))) & 255;
            e.push((k >>> 4).toString(16));
            e.push((k & 15).toString(16));
          }
          return e.join('');
        },
        parse: function (a) {
          for (var c = a.length, e = [], j = 0; j < c; j += 2) e[j >>> 3] |= parseInt(a.substr(j, 2), 16) << (24 - 4 * (j % 8));
          return new r.init(e, c / 2);
        }
      }),
      b = (w.Latin1 = {
        stringify: function (a) {
          var c = a.words;
          a = a.sigBytes;
          for (var e = [], j = 0; j < a; j++) e.push(String.fromCharCode((c[j >>> 2] >>> (24 - 8 * (j % 4))) & 255));
          return e.join('');
        },
        parse: function (a) {
          for (var c = a.length, e = [], j = 0; j < c; j++) e[j >>> 2] |= (a.charCodeAt(j) & 255) << (24 - 8 * (j % 4));
          return new r.init(e, c);
        }
      }),
      x = (w.Utf8 = {
        stringify: function (a) {
          try {
            return decodeURIComponent(escape(b.stringify(a)));
          } catch (c) {
            throw Error('Malformed UTF-8 data');
          }
        },
        parse: function (a) {
          return b.parse(unescape(encodeURIComponent(a)));
        }
      }),
      q = (l.BufferedBlockAlgorithm = t.extend({
        reset: function () {
          this._data = new r.init();
          this._nDataBytes = 0;
        },
        _append: function (a) {
          'string' == typeof a && (a = x.parse(a));
          this._data.concat(a);
          this._nDataBytes += a.sigBytes;
        },
        _process: function (a) {
          var c = this._data,
            e = c.words,
            j = c.sigBytes,
            k = this.blockSize,
            b = j / (4 * k),
            b = a ? u.ceil(b) : u.max((b | 0) - this._minBufferSize, 0);
          a = b * k;
          j = u.min(4 * a, j);
          if (a) {
            for (var q = 0; q < a; q += k) this._doProcessBlock(e, q);
            q = e.splice(0, a);
            c.sigBytes -= j;
          }
          return new r.init(q, j);
        },
        clone: function () {
          var a = t.clone.call(this);
          a._data = this._data.clone();
          return a;
        },
        _minBufferSize: 0
      }));
    l.Hasher = q.extend({
      cfg: t.extend(),
      init: function (a) {
        this.cfg = this.cfg.extend(a);
        this.reset();
      },
      reset: function () {
        q.reset.call(this);
        this._doReset();
      },
      update: function (a) {
        this._append(a);
        this._process();
        return this;
      },
      finalize: function (a) {
        a && this._append(a);
        return this._doFinalize();
      },
      blockSize: 16,
      _createHelper: function (a) {
        return function (b, e) {
          return new a.init(e).finalize(b);
        };
      },
      _createHmacHelper: function (a) {
        return function (b, e) {
          return new n.HMAC.init(a, e).finalize(b);
        };
      }
    });
    var n = (d.algo = {});
    return d;
  })(Math);
(function () {
  var u = CryptoJS,
    p = u.lib.WordArray;
  u.enc.Base64 = {
    stringify: function (d) {
      var l = d.words,
        p = d.sigBytes,
        t = this._map;
      d.clamp();
      d = [];
      for (var r = 0; r < p; r += 3)
        for (
          var w =
              (((l[r >>> 2] >>> (24 - 8 * (r % 4))) & 255) << 16) |
              (((l[(r + 1) >>> 2] >>> (24 - 8 * ((r + 1) % 4))) & 255) << 8) |
              ((l[(r + 2) >>> 2] >>> (24 - 8 * ((r + 2) % 4))) & 255),
            v = 0;
          4 > v && r + 0.75 * v < p;
          v++
        )
          d.push(t.charAt((w >>> (6 * (3 - v))) & 63));
      if ((l = t.charAt(64))) for (; d.length % 4; ) d.push(l);
      return d.join('');
    },
    parse: function (d) {
      var l = d.length,
        s = this._map,
        t = s.charAt(64);
      t && ((t = d.indexOf(t)), -1 != t && (l = t));
      for (var t = [], r = 0, w = 0; w < l; w++)
        if (w % 4) {
          var v = s.indexOf(d.charAt(w - 1)) << (2 * (w % 4)),
            b = s.indexOf(d.charAt(w)) >>> (6 - 2 * (w % 4));
          t[r >>> 2] |= (v | b) << (24 - 8 * (r % 4));
          r++;
        }
      return p.create(t, r);
    },
    _map: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/='
  };
})();
(function (u) {
  function p(b, n, a, c, e, j, k) {
    b = b + ((n & a) | (~n & c)) + e + k;
    return ((b << j) | (b >>> (32 - j))) + n;
  }
  function d(b, n, a, c, e, j, k) {
    b = b + ((n & c) | (a & ~c)) + e + k;
    return ((b << j) | (b >>> (32 - j))) + n;
  }
  function l(b, n, a, c, e, j, k) {
    b = b + (n ^ a ^ c) + e + k;
    return ((b << j) | (b >>> (32 - j))) + n;
  }
  function s(b, n, a, c, e, j, k) {
    b = b + (a ^ (n | ~c)) + e + k;
    return ((b << j) | (b >>> (32 - j))) + n;
  }
  for (var t = CryptoJS, r = t.lib, w = r.WordArray, v = r.Hasher, r = t.algo, b = [], x = 0; 64 > x; x++) b[x] = (4294967296 * u.abs(u.sin(x + 1))) | 0;
  r = r.MD5 = v.extend({
    _doReset: function () {
      this._hash = new w.init([1732584193, 4023233417, 2562383102, 271733878]);
    },
    _doProcessBlock: function (q, n) {
      for (var a = 0; 16 > a; a++) {
        var c = n + a,
          e = q[c];
        q[c] = (((e << 8) | (e >>> 24)) & 16711935) | (((e << 24) | (e >>> 8)) & 4278255360);
      }
      var a = this._hash.words,
        c = q[n + 0],
        e = q[n + 1],
        j = q[n + 2],
        k = q[n + 3],
        z = q[n + 4],
        r = q[n + 5],
        t = q[n + 6],
        w = q[n + 7],
        v = q[n + 8],
        A = q[n + 9],
        B = q[n + 10],
        C = q[n + 11],
        u = q[n + 12],
        D = q[n + 13],
        E = q[n + 14],
        x = q[n + 15],
        f = a[0],
        m = a[1],
        g = a[2],
        h = a[3],
        f = p(f, m, g, h, c, 7, b[0]),
        h = p(h, f, m, g, e, 12, b[1]),
        g = p(g, h, f, m, j, 17, b[2]),
        m = p(m, g, h, f, k, 22, b[3]),
        f = p(f, m, g, h, z, 7, b[4]),
        h = p(h, f, m, g, r, 12, b[5]),
        g = p(g, h, f, m, t, 17, b[6]),
        m = p(m, g, h, f, w, 22, b[7]),
        f = p(f, m, g, h, v, 7, b[8]),
        h = p(h, f, m, g, A, 12, b[9]),
        g = p(g, h, f, m, B, 17, b[10]),
        m = p(m, g, h, f, C, 22, b[11]),
        f = p(f, m, g, h, u, 7, b[12]),
        h = p(h, f, m, g, D, 12, b[13]),
        g = p(g, h, f, m, E, 17, b[14]),
        m = p(m, g, h, f, x, 22, b[15]),
        f = d(f, m, g, h, e, 5, b[16]),
        h = d(h, f, m, g, t, 9, b[17]),
        g = d(g, h, f, m, C, 14, b[18]),
        m = d(m, g, h, f, c, 20, b[19]),
        f = d(f, m, g, h, r, 5, b[20]),
        h = d(h, f, m, g, B, 9, b[21]),
        g = d(g, h, f, m, x, 14, b[22]),
        m = d(m, g, h, f, z, 20, b[23]),
        f = d(f, m, g, h, A, 5, b[24]),
        h = d(h, f, m, g, E, 9, b[25]),
        g = d(g, h, f, m, k, 14, b[26]),
        m = d(m, g, h, f, v, 20, b[27]),
        f = d(f, m, g, h, D, 5, b[28]),
        h = d(h, f, m, g, j, 9, b[29]),
        g = d(g, h, f, m, w, 14, b[30]),
        m = d(m, g, h, f, u, 20, b[31]),
        f = l(f, m, g, h, r, 4, b[32]),
        h = l(h, f, m, g, v, 11, b[33]),
        g = l(g, h, f, m, C, 16, b[34]),
        m = l(m, g, h, f, E, 23, b[35]),
        f = l(f, m, g, h, e, 4, b[36]),
        h = l(h, f, m, g, z, 11, b[37]),
        g = l(g, h, f, m, w, 16, b[38]),
        m = l(m, g, h, f, B, 23, b[39]),
        f = l(f, m, g, h, D, 4, b[40]),
        h = l(h, f, m, g, c, 11, b[41]),
        g = l(g, h, f, m, k, 16, b[42]),
        m = l(m, g, h, f, t, 23, b[43]),
        f = l(f, m, g, h, A, 4, b[44]),
        h = l(h, f, m, g, u, 11, b[45]),
        g = l(g, h, f, m, x, 16, b[46]),
        m = l(m, g, h, f, j, 23, b[47]),
        f = s(f, m, g, h, c, 6, b[48]),
        h = s(h, f, m, g, w, 10, b[49]),
        g = s(g, h, f, m, E, 15, b[50]),
        m = s(m, g, h, f, r, 21, b[51]),
        f = s(f, m, g, h, u, 6, b[52]),
        h = s(h, f, m, g, k, 10, b[53]),
        g = s(g, h, f, m, B, 15, b[54]),
        m = s(m, g, h, f, e, 21, b[55]),
        f = s(f, m, g, h, v, 6, b[56]),
        h = s(h, f, m, g, x, 10, b[57]),
        g = s(g, h, f, m, t, 15, b[58]),
        m = s(m, g, h, f, D, 21, b[59]),
        f = s(f, m, g, h, z, 6, b[60]),
        h = s(h, f, m, g, C, 10, b[61]),
        g = s(g, h, f, m, j, 15, b[62]),
        m = s(m, g, h, f, A, 21, b[63]);
      a[0] = (a[0] + f) | 0;
      a[1] = (a[1] + m) | 0;
      a[2] = (a[2] + g) | 0;
      a[3] = (a[3] + h) | 0;
    },
    _doFinalize: function () {
      var b = this._data,
        n = b.words,
        a = 8 * this._nDataBytes,
        c = 8 * b.sigBytes;
      n[c >>> 5] |= 128 << (24 - (c % 32));
      var e = u.floor(a / 4294967296);
      n[(((c + 64) >>> 9) << 4) + 15] = (((e << 8) | (e >>> 24)) & 16711935) | (((e << 24) | (e >>> 8)) & 4278255360);
      n[(((c + 64) >>> 9) << 4) + 14] = (((a << 8) | (a >>> 24)) & 16711935) | (((a << 24) | (a >>> 8)) & 4278255360);
      b.sigBytes = 4 * (n.length + 1);
      this._process();
      b = this._hash;
      n = b.words;
      for (a = 0; 4 > a; a++) ((c = n[a]), (n[a] = (((c << 8) | (c >>> 24)) & 16711935) | (((c << 24) | (c >>> 8)) & 4278255360)));
      return b;
    },
    clone: function () {
      var b = v.clone.call(this);
      b._hash = this._hash.clone();
      return b;
    }
  });
  t.MD5 = v._createHelper(r);
  t.HmacMD5 = v._createHmacHelper(r);
})(Math);
(function () {
  var u = CryptoJS,
    p = u.lib,
    d = p.Base,
    l = p.WordArray,
    p = u.algo,
    s = (p.EvpKDF = d.extend({
      cfg: d.extend({ keySize: 4, hasher: p.MD5, iterations: 1 }),
      init: function (d) {
        this.cfg = this.cfg.extend(d);
      },
      compute: function (d, r) {
        for (var p = this.cfg, s = p.hasher.create(), b = l.create(), u = b.words, q = p.keySize, p = p.iterations; u.length < q; ) {
          n && s.update(n);
          var n = s.update(d).finalize(r);
          s.reset();
          for (var a = 1; a < p; a++) ((n = s.finalize(n)), s.reset());
          b.concat(n);
        }
        b.sigBytes = 4 * q;
        return b;
      }
    }));
  u.EvpKDF = function (d, l, p) {
    return s.create(p).compute(d, l);
  };
})();
CryptoJS.lib.Cipher ||
  (function (u) {
    var p = CryptoJS,
      d = p.lib,
      l = d.Base,
      s = d.WordArray,
      t = d.BufferedBlockAlgorithm,
      r = p.enc.Base64,
      w = p.algo.EvpKDF,
      v = (d.Cipher = t.extend({
        cfg: l.extend(),
        createEncryptor: function (e, a) {
          return this.create(this._ENC_XFORM_MODE, e, a);
        },
        createDecryptor: function (e, a) {
          return this.create(this._DEC_XFORM_MODE, e, a);
        },
        init: function (e, a, b) {
          this.cfg = this.cfg.extend(b);
          this._xformMode = e;
          this._key = a;
          this.reset();
        },
        reset: function () {
          t.reset.call(this);
          this._doReset();
        },
        process: function (e) {
          this._append(e);
          return this._process();
        },
        finalize: function (e) {
          e && this._append(e);
          return this._doFinalize();
        },
        keySize: 4,
        ivSize: 4,
        _ENC_XFORM_MODE: 1,
        _DEC_XFORM_MODE: 2,
        _createHelper: function (e) {
          return {
            encrypt: function (b, k, d) {
              return ('string' == typeof k ? c : a).encrypt(e, b, k, d);
            },
            decrypt: function (b, k, d) {
              return ('string' == typeof k ? c : a).decrypt(e, b, k, d);
            }
          };
        }
      }));
    d.StreamCipher = v.extend({
      _doFinalize: function () {
        return this._process(!0);
      },
      blockSize: 1
    });
    var b = (p.mode = {}),
      x = function (e, a, b) {
        var c = this._iv;
        c ? (this._iv = u) : (c = this._prevBlock);
        for (var d = 0; d < b; d++) e[a + d] ^= c[d];
      },
      q = (d.BlockCipherMode = l.extend({
        createEncryptor: function (e, a) {
          return this.Encryptor.create(e, a);
        },
        createDecryptor: function (e, a) {
          return this.Decryptor.create(e, a);
        },
        init: function (e, a) {
          this._cipher = e;
          this._iv = a;
        }
      })).extend();
    q.Encryptor = q.extend({
      processBlock: function (e, a) {
        var b = this._cipher,
          c = b.blockSize;
        x.call(this, e, a, c);
        b.encryptBlock(e, a);
        this._prevBlock = e.slice(a, a + c);
      }
    });
    q.Decryptor = q.extend({
      processBlock: function (e, a) {
        var b = this._cipher,
          c = b.blockSize,
          d = e.slice(a, a + c);
        b.decryptBlock(e, a);
        x.call(this, e, a, c);
        this._prevBlock = d;
      }
    });
    b = b.CBC = q;
    q = (p.pad = {}).Pkcs7 = {
      pad: function (a, b) {
        for (var c = 4 * b, c = c - (a.sigBytes % c), d = (c << 24) | (c << 16) | (c << 8) | c, l = [], n = 0; n < c; n += 4) l.push(d);
        c = s.create(l, c);
        a.concat(c);
      },
      unpad: function (a) {
        a.sigBytes -= a.words[(a.sigBytes - 1) >>> 2] & 255;
      }
    };
    d.BlockCipher = v.extend({
      cfg: v.cfg.extend({ mode: b, padding: q }),
      reset: function () {
        v.reset.call(this);
        var a = this.cfg,
          b = a.iv,
          a = a.mode;
        if (this._xformMode == this._ENC_XFORM_MODE) var c = a.createEncryptor;
        else ((c = a.createDecryptor), (this._minBufferSize = 1));
        this._mode = c.call(a, this, b && b.words);
      },
      _doProcessBlock: function (a, b) {
        this._mode.processBlock(a, b);
      },
      _doFinalize: function () {
        var a = this.cfg.padding;
        if (this._xformMode == this._ENC_XFORM_MODE) {
          a.pad(this._data, this.blockSize);
          var b = this._process(!0);
        } else ((b = this._process(!0)), a.unpad(b));
        return b;
      },
      blockSize: 4
    });
    var n = (d.CipherParams = l.extend({
        init: function (a) {
          this.mixIn(a);
        },
        toString: function (a) {
          return (a || this.formatter).stringify(this);
        }
      })),
      b = ((p.format = {}).OpenSSL = {
        stringify: function (a) {
          var b = a.ciphertext;
          a = a.salt;
          return (a ? s.create([1398893684, 1701076831]).concat(a).concat(b) : b).toString(r);
        },
        parse: function (a) {
          a = r.parse(a);
          var b = a.words;
          if (1398893684 == b[0] && 1701076831 == b[1]) {
            var c = s.create(b.slice(2, 4));
            b.splice(0, 4);
            a.sigBytes -= 16;
          }
          return n.create({ ciphertext: a, salt: c });
        }
      }),
      a = (d.SerializableCipher = l.extend({
        cfg: l.extend({ format: b }),
        encrypt: function (a, b, c, d) {
          d = this.cfg.extend(d);
          var l = a.createEncryptor(c, d);
          b = l.finalize(b);
          l = l.cfg;
          return n.create({ ciphertext: b, key: c, iv: l.iv, algorithm: a, mode: l.mode, padding: l.padding, blockSize: a.blockSize, formatter: d.format });
        },
        decrypt: function (a, b, c, d) {
          d = this.cfg.extend(d);
          b = this._parse(b, d.format);
          return a.createDecryptor(c, d).finalize(b.ciphertext);
        },
        _parse: function (a, b) {
          return 'string' == typeof a ? b.parse(a, this) : a;
        }
      })),
      p = ((p.kdf = {}).OpenSSL = {
        execute: function (a, b, c, d) {
          d || (d = s.random(8));
          a = w.create({ keySize: b + c }).compute(a, d);
          c = s.create(a.words.slice(b), 4 * c);
          a.sigBytes = 4 * b;
          return n.create({ key: a, iv: c, salt: d });
        }
      }),
      c = (d.PasswordBasedCipher = a.extend({
        cfg: a.cfg.extend({ kdf: p }),
        encrypt: function (b, c, d, l) {
          l = this.cfg.extend(l);
          d = l.kdf.execute(d, b.keySize, b.ivSize);
          l.iv = d.iv;
          b = a.encrypt.call(this, b, c, d.key, l);
          b.mixIn(d);
          return b;
        },
        decrypt: function (b, c, d, l) {
          l = this.cfg.extend(l);
          c = this._parse(c, l.format);
          d = l.kdf.execute(d, b.keySize, b.ivSize, c.salt);
          l.iv = d.iv;
          return a.decrypt.call(this, b, c, d.key, l);
        }
      }));
  })();
(function () {
  for (
    var u = CryptoJS, p = u.lib.BlockCipher, d = u.algo, l = [], s = [], t = [], r = [], w = [], v = [], b = [], x = [], q = [], n = [], a = [], c = 0;
    256 > c;
    c++
  )
    a[c] = 128 > c ? c << 1 : (c << 1) ^ 283;
  for (var e = 0, j = 0, c = 0; 256 > c; c++) {
    var k = j ^ (j << 1) ^ (j << 2) ^ (j << 3) ^ (j << 4),
      k = (k >>> 8) ^ (k & 255) ^ 99;
    l[e] = k;
    s[k] = e;
    var z = a[e],
      F = a[z],
      G = a[F],
      y = (257 * a[k]) ^ (16843008 * k);
    t[e] = (y << 24) | (y >>> 8);
    r[e] = (y << 16) | (y >>> 16);
    w[e] = (y << 8) | (y >>> 24);
    v[e] = y;
    y = (16843009 * G) ^ (65537 * F) ^ (257 * z) ^ (16843008 * e);
    b[k] = (y << 24) | (y >>> 8);
    x[k] = (y << 16) | (y >>> 16);
    q[k] = (y << 8) | (y >>> 24);
    n[k] = y;
    e ? ((e = z ^ a[a[a[G ^ z]]]), (j ^= a[a[j]])) : (e = j = 1);
  }
  var H = [0, 1, 2, 4, 8, 16, 32, 64, 128, 27, 54],
    d = (d.AES = p.extend({
      _doReset: function () {
        for (var a = this._key, c = a.words, d = a.sigBytes / 4, a = 4 * ((this._nRounds = d + 6) + 1), e = (this._keySchedule = []), j = 0; j < a; j++)
          if (j < d) e[j] = c[j];
          else {
            var k = e[j - 1];
            j % d
              ? 6 < d && 4 == j % d && (k = (l[k >>> 24] << 24) | (l[(k >>> 16) & 255] << 16) | (l[(k >>> 8) & 255] << 8) | l[k & 255])
              : ((k = (k << 8) | (k >>> 24)),
                (k = (l[k >>> 24] << 24) | (l[(k >>> 16) & 255] << 16) | (l[(k >>> 8) & 255] << 8) | l[k & 255]),
                (k ^= H[(j / d) | 0] << 24));
            e[j] = e[j - d] ^ k;
          }
        c = this._invKeySchedule = [];
        for (d = 0; d < a; d++)
          ((j = a - d),
            (k = d % 4 ? e[j] : e[j - 4]),
            (c[d] = 4 > d || 4 >= j ? k : b[l[k >>> 24]] ^ x[l[(k >>> 16) & 255]] ^ q[l[(k >>> 8) & 255]] ^ n[l[k & 255]]));
      },
      encryptBlock: function (a, b) {
        this._doCryptBlock(a, b, this._keySchedule, t, r, w, v, l);
      },
      decryptBlock: function (a, c) {
        var d = a[c + 1];
        a[c + 1] = a[c + 3];
        a[c + 3] = d;
        this._doCryptBlock(a, c, this._invKeySchedule, b, x, q, n, s);
        d = a[c + 1];
        a[c + 1] = a[c + 3];
        a[c + 3] = d;
      },
      _doCryptBlock: function (a, b, c, d, e, j, l, f) {
        for (var m = this._nRounds, g = a[b] ^ c[0], h = a[b + 1] ^ c[1], k = a[b + 2] ^ c[2], n = a[b + 3] ^ c[3], p = 4, r = 1; r < m; r++)
          var q = d[g >>> 24] ^ e[(h >>> 16) & 255] ^ j[(k >>> 8) & 255] ^ l[n & 255] ^ c[p++],
            s = d[h >>> 24] ^ e[(k >>> 16) & 255] ^ j[(n >>> 8) & 255] ^ l[g & 255] ^ c[p++],
            t = d[k >>> 24] ^ e[(n >>> 16) & 255] ^ j[(g >>> 8) & 255] ^ l[h & 255] ^ c[p++],
            n = d[n >>> 24] ^ e[(g >>> 16) & 255] ^ j[(h >>> 8) & 255] ^ l[k & 255] ^ c[p++],
            g = q,
            h = s,
            k = t;
        q = ((f[g >>> 24] << 24) | (f[(h >>> 16) & 255] << 16) | (f[(k >>> 8) & 255] << 8) | f[n & 255]) ^ c[p++];
        s = ((f[h >>> 24] << 24) | (f[(k >>> 16) & 255] << 16) | (f[(n >>> 8) & 255] << 8) | f[g & 255]) ^ c[p++];
        t = ((f[k >>> 24] << 24) | (f[(n >>> 16) & 255] << 16) | (f[(g >>> 8) & 255] << 8) | f[h & 255]) ^ c[p++];
        n = ((f[n >>> 24] << 24) | (f[(g >>> 16) & 255] << 16) | (f[(h >>> 8) & 255] << 8) | f[k & 255]) ^ c[p++];
        a[b] = q;
        a[b + 1] = s;
        a[b + 2] = t;
        a[b + 3] = n;
      },
      keySize: 8
    }));
  u.AES = p._createHelper(d);
})(); // Encapsulate SDK under Plantronics object

/*exported Plantronics*/

/*jshint bitwise: false*/
/*eslint-disable no-bitwise*/

var Plantronics = {};

// eslint-disable-next-line max-lines-per-function
Plantronics.init = function () {
  // NOSONAR
  'use strict';

  if (Plantronics.Spokes) {
    // Already initialized
    return;
  }

  // Ensure jquery is loaded
  if (typeof jQuery === 'undefined') {
    throw new Error('Spokes Error: jQuery must be loaded before spokes Javascript');
  }

  /////////////////////////////////////////////////////////////////////////////
  // Logger
  /////////////////////////////////////////////////////////////////////////////
  // Default logger
  /* eslint-disable no-console */
  var logger = {
    debug: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console)
  };
  /* eslint-enable no-console */

  // Allow application to define own logger
  function setLogger(appLogger) {
    appLogger = appLogger || {};
    var logDebug = appLogger.debug || appLogger.log || function () {};
    var logInfo = appLogger.info || logDebug;
    var logWarn = appLogger.warn || appLogger.warning || logInfo;
    var logError = appLogger.error || logInfo;

    logger.debug = function () {
      logDebug.apply(appLogger, Array.prototype.slice.apply(arguments));
    };
    logger.info = function () {
      logInfo.apply(appLogger, Array.prototype.slice.apply(arguments));
    };
    logger.warn = function () {
      logWarn.apply(appLogger, Array.prototype.slice.apply(arguments));
    };
    logger.error = function (error, obj) {
      var args = [(error && error.stack) || error];
      obj = (obj && obj.stack) || obj;
      if (obj) {
        args.push(obj);
      }
      logError.apply(appLogger, args);
    };
  }

  /////////////////////////////////////////////////////////////////////////////
  // Helper functions
  /////////////////////////////////////////////////////////////////////////////
  function createLookup(obj) {
    var keys = Object.keys(obj);
    obj.Lookup = [];
    keys.forEach(function (key) {
      obj.Lookup[obj[key]] = key;
    });
  }

  // Compresses an array of queue items down to a single number
  function compressQueueList(queue) {
    if (!Array.isArray(queue)) {
      return queue;
    }

    // We have an array, go through it and or everthing together
    return queue.reduce(function (accumulator, currentValue) {
      return typeof currentValue === 'number' ? accumulator | currentValue : accumulator;
    }, 0);
  }

  function sendRequest(requestUrl, responseType, callback, suppressLog) {
    !suppressLog && logger.debug('[Spokes]: Send REST request: ', requestUrl);
    $.getJSON(requestUrl, function (data) {
      // Create a nice object, and ensure its of the right type
      var resp = new SpokesResponse(data);
      if (resp.Type !== responseType) {
        resp.isValid = false;
      }
      !suppressLog && logger.debug('[Spokes]: Received REST response: ', resp);

      if (typeof callback === 'function') {
        callback(resp);
      }
    }).fail(function (err) {
      logger.warn('[Spokes] Failed to send request. ', err ? err.status + ' ' + err.statusText : '');

      if (typeof callback === 'function') {
        callback(
          new SpokesResponse({
            Description: '',
            Err: {
              Description: 'No response. Server appears to be offline.',
              Error_Code: 0,
              Type: 4
            },
            Result: null,
            Type: 0,
            Type_Name: 'Unknown',
            isError: true
          })
        );
      }
    });
    return true;
  }

  function isCallId(callID) {
    return !!(callID && typeof callID.getName === 'function' && callID.getName() === 'SpokesCallId');
  }

  function isContact(contact) {
    return !!(contact && typeof contact.getName === 'function' && contact.getName() === 'SpokesContact');
  }

  /////////////////////////////////////////////////////////////////////////////
  // Definitions
  /////////////////////////////////////////////////////////////////////////////
  var SessionCallState = {
    Unknown: 0,
    AcceptCall: 1,
    TerminateCall: 2,
    HoldCall: 3,
    Resumecall: 4,
    Flash: 5,
    CallInProgress: 6,
    CallRinging: 7,
    CallEnded: 8,
    TransferToHeadSet: 9,
    TransferToSpeaker: 10,
    MuteON: 11,
    MuteOFF: 12,
    MobileCallRinging: 13,
    MobileCallInProgress: 14,
    MobileCallEnded: 15,
    Don: 16,
    Doff: 17,
    CallIdle: 18,
    Play: 19,
    Pause: 20,
    Stop: 21,
    DTMFKey: 22,
    RejectCall: 23
  };
  createLookup(SessionCallState);

  var SpokesAudioType = {
    MonoOn: 1,
    MonoOff: 2,
    StereoOn: 3,
    StereoOff: 4,
    MonoOnWait: 5,
    StereoOnWait: 6
  };
  createLookup(SpokesAudioType);

  var SpokesResponseType = {
    Unknown: 0,
    Error: 1,
    Bool: 2,
    Integer: 3,
    DeviceInfo: 4,
    DeviceInfoArray: 5,
    DeviceEventArray: 6,
    SessionHash: 7,
    String: 8,
    CallManagerState: 9,
    CallStateArray: 10,
    ContactArray: 11,
    StringArray: 12
  };

  // Constructor for Response object
  function SpokesResponse(obj) {
    // Copy the JSON data into this object
    this.Type = obj.Type;
    this.Type_Name = obj.Type_Name;
    this.Description = obj.Description;
    this.isError = obj.isError;
    this.isValid = true;
    this.Err = null;
    this.Result = null;

    // If we have an error, null the result and populate error, else opposite
    if (this.isError) {
      this.Err = new SpokesError(obj.Err);
      return;
    }

    // Store a user result object
    var data;
    switch (this.Type) {
      // Object types that JS knows about
      case SpokesResponseType.Bool:
      case SpokesResponseType.Integer:
      case SpokesResponseType.SessionHash:
      case SpokesResponseType.String:
      case SpokesResponseType.CallManagerState:
        this.Result = obj.Result;
        break;

      // Create a device info object
      case SpokesResponseType.DeviceInfo:
        this.Result = new SpokesDeviceInfo(obj.Result);
        break;

      // Create an array of device info objects
      case SpokesResponseType.DeviceInfoArray:
        data = Array.isArray(obj.Result) ? obj.Result : [];
        this.Result = data.map(function (deviceInfo) {
          return new SpokesDeviceInfo(deviceInfo);
        });
        break;

      // Create an array of events
      case SpokesResponseType.DeviceEventArray:
        data = Array.isArray(obj.Result) ? obj.Result : [];
        this.Result = data.map(function (event) {
          return new SpokesEvent(event);
        });
        break;

      case SpokesResponseType.CallStateArray:
      case SpokesResponseType.StringArray:
        this.Result = Array.isArray(obj.Result) ? obj.Result : [];
        break;

      // Create an array of contacts
      case SpokesResponseType.ContactArray:
        data = Array.isArray(obj.Result) ? obj.Result : [];
        this.Result = data.map(function (contact) {
          return new SpokesContact(contact);
        });
        break;

      default:
        logger.error('[Spokes]: Invalid response type received: ', this.Type);
        break;
    }
  }
  SpokesResponse.prototype.getName = function () {
    return 'SpokesResponse';
  };

  // Constructor for Device Info object
  function SpokesDeviceInfo(obj) {
    this.Uid = obj.Uid;
    this.DevicePath = obj.DevicePath;
    this.InternalName = obj.InternalName;
    this.IsAttached = obj.IsAttached;
    this.ManufacturerName = obj.ManufacturerName;
    this.ProductId = obj.ProductId;
    this.ProductName = obj.ProductName;
    this.SerialNumber = obj.SerialNumber;
    this.VendorId = obj.VendorId;
    this.VersionNumber = obj.VersionNumber;
    this.USBVersionNumber = obj.USBVersionNumber;
    this.BaseFirmwareVersion = obj.BaseFirmwareVersion;
    this.BluetoothFirmwareVersion = obj.BluetoothFirmwareVersion;
    this.RemoteFirmwareVersion = obj.RemoteFirmwareVersion;
    this.BaseSerialNumber = obj.BaseSerialNumber;
    this.HeadsetSerialNumber = obj.HeadsetSerialNumber;
  }
  SpokesDeviceInfo.prototype.getName = function () {
    return 'SpokesDeviceInfo';
  };

  // Stores the spokes event types that can exist
  var SpokesEventType = {
    DeviceStateChange: 1,
    HeadsetStateChange: 2,
    HeadsetButtonPressed: 4,
    BaseStateChange: 8,
    BaseButtonPressed: 16,
    CallStateChange: 32,
    ATDStateChange: 64
  };

  // Constructor for Event object
  function SpokesEvent(obj) {
    this.Event_Log_Type_Name = obj.Event_Log_Type_Name;
    this.Event_Log_Type_Id = obj.Event_Log_Type_Id;
    this.Event_Name = obj.Event_Name;
    this.Event_Id = obj.Event_Id;
    this.Timestamp = obj.Timestamp;
    this.Age = obj.Age;
  }
  SpokesEvent.prototype.getName = function () {
    return 'SpokesEvent';
  };

  // Defines a spokes error
  var SpokesErrorType = {
    Unknown: 0,
    Invalid_Uid: 1,
    Exception: 2,
    Invalid_Session: 3,
    Server_Offline: 4
  };

  // Constructor for Error object
  function SpokesError(obj) {
    this.Type = obj.Type;
    this.Description = obj.Description;
    this.Error_Code = obj.Error_Code;
  }
  SpokesError.prototype.getName = function () {
    return 'SpokesError';
  };

  // Constructor for Contact object
  function SpokesContact(obj) {
    this.Id = obj.Id;
    this.Name = obj.Name;
    this.Email = obj.Email;
    this.Phone = obj.Phone;
    this.SipUri = obj.SipUri;
    this.WorkPhone = obj.WorkPhone;
    this.HomePhone = obj.HomePhone;
    this.MobilePhone = obj.MobilePhone;
    this.FriendlyName = obj.FriendlyName;
  }
  SpokesContact.prototype.getName = function () {
    return 'SpokesContact';
  };

  // Constructor for CallId object
  function SpokesCallId(obj) {
    this.Id = obj.Id;
    this.InConference = obj.InConference;
    this.ConferenceId = obj.ConferenceId;
  }
  SpokesCallId.prototype.getName = function () {
    return 'SpokesCallId';
  };

  /////////////////////////////////////////////////////////////////////////////
  // Device object
  /////////////////////////////////////////////////////////////////////////////
  function Device(path) {
    this.Path = path;
    this.Device_Id = '';
    this.Sess_Id = '';
    this.isAttached = false;
  }

  // Connect to a device
  Device.prototype.attach = function (uid, callback) {
    var url = this.Path + '/DeviceServices/Attach?uid=' + uid + '&callback=?';
    return sendRequest(
      url,
      SpokesResponseType.SessionHash,
      function (resp) {
        if (resp.isValid && !resp.isError) {
          // Store my session and set that we are attached
          this.Sess_Id = resp.Result;
          this.isAttached = true;
          logger.info('[Spokes]: Connected to Device ', this);
        }
        callback(resp);
      }.bind(this)
    );
  };

  // Release my session from a device
  Device.prototype.release = function (callback) {
    // Can't release, we aren't attached
    if (!this.isAttached) {
      return false;
    }

    var url = this.Path + '/DeviceServices/Release?sess=' + this.Sess_Id + '&callback=?';
    return sendRequest(
      url,
      SpokesResponseType.Bool,
      function (resp) {
        // Set that we aren't attached anymore, even if this failed
        this.Sess_Id = '';
        this.isAttached = false;
        logger.info('[Spokes]: Released from Device ', this);
        callback(resp);
      }.bind(this)
    );
  };

  // Returns the info for our connected device
  Device.prototype.deviceInfo = function (callback) {
    if (typeof callback !== 'function') {
      return false;
    }

    var url = this.Path + '/DeviceServices/Info?callback=?';
    return sendRequest(
      url,
      SpokesResponseType.DeviceInfo,
      function (resp) {
        if (resp.isValid && !resp.isError) {
          // Store my device uid
          this.Device_Id = resp.Result.Uid;
        }
        callback(resp);
      }.bind(this)
    );
  };

  Device.prototype.atdMobileCallerId = function (callback) {
    if (typeof callback !== 'function') {
      return false;
    }
    var url = this.Path + '/DeviceServices/ATDMobileCallerId?sess=' + this.Sess_Id + '&callback=?';
    return sendRequest(url, SpokesResponseType.String, callback);
  };

  Device.prototype.proximity = function (enabled, callback) {
    if (typeof enabled !== 'boolean') {
      return false;
    }
    var url = this.Path + '/DeviceServices/Proximity?sess=' + this.Sess_Id + '&enabled=' + enabled + '&callback=?';
    return sendRequest(url, SpokesResponseType.String, callback);
  };

  // Returns all the valid events that have happened since last call
  Device.prototype.events = function (queue, callback) {
    // Check if I was only given one argument
    if (typeof queue === 'function') {
      callback = queue;
      queue = 0;
    }

    if (typeof callback !== 'function') {
      return false;
    }

    // If they gave me an array of queue items, compress them down to an int
    queue = compressQueueList(queue);

    var url = this.Path + '/DeviceServices/Events?sess=' + this.Sess_Id + '&queue=' + queue + '&callback=?';
    return sendRequest(url, SpokesResponseType.DeviceEventArray, callback);
  };

  // Returns specific event queues
  Device.prototype.headsetEvents = function (callback) {
    var evt = SpokesEventType.HeadsetButtonPressed | SpokesEventType.HeadsetStateChange;
    return this.events(evt, callback);
  };

  // Returns specific event queues
  Device.prototype.baseEvents = function (callback) {
    var evt = SpokesEventType.BaseButtonPressed | SpokesEventType.BaseStateChange;
    return this.events(evt, callback);
  };

  // Returns specific event queues
  Device.prototype.atdEvents = function (callback) {
    return this.events(SpokesEventType.ATDStateChange, callback);
  };

  // Starts or stops the ringer in the headest
  Device.prototype.ring = function (enabled, callback) {
    if (typeof enabled !== 'boolean') {
      return false;
    }
    var url = this.Path + '/DeviceServices/Ring?enabled=' + enabled + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  // Sets the audio state, for wireless headsets MonoOn/Off turns them on and off
  Device.prototype.audioState = function (state, callback) {
    if (typeof state !== 'number') {
      return false;
    }
    // Convert the audio state into the textual name
    state = SpokesAudioType.Lookup[state];

    var url = this.Path + '/DeviceServices/AudioState?state=' + state + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  ///////////////////////////////////////////////////////////////////////////
  // Event Manager

  // Returns the queues we are registered for
  Device.prototype.getEventRegistry = function (callback) {
    if (typeof callback !== 'function') {
      return false;
    }
    var url = this.Path + '/EventManager/GetRegistry?sess=' + this.Sess_Id + '&callback=?';
    return sendRequest(url, SpokesResponseType.Integer, callback);
  };

  // Defines a list of queues we are registered for
  Device.prototype.setEventRegistry = function (queue, callback) {
    // If they gave me an array of queue items, compress them down to an int
    queue = compressQueueList(queue) || 0;

    var url = this.Path + '/EventManager/SetRegistry?sess=' + this.Sess_Id + '&queue=' + queue + '&callback=?';
    return sendRequest(url, SpokesResponseType.Integer, callback);
  };

  // Adds a list of queues from our registry
  Device.prototype.addEventRegistry = function (queue, callback) {
    // If they gave me an array of queue items, compress them down to an int
    queue = compressQueueList(queue) || 0;

    var url = this.Path + '/EventManager/AddRegistry?sess=' + this.Sess_Id + '&queue=' + queue + '&callback=?';
    return sendRequest(url, SpokesResponseType.Integer, callback);
  };

  // Removes a list of queues from our registry
  Device.prototype.removeEventRegistry = function (queue, callback) {
    // If they gave me an array of queue items, compress them down to an int
    queue = compressQueueList(queue) || 0;

    var url = this.Path + '/EventManager/RemoveRegistry?sess=' + this.Sess_Id + '&queue=' + queue + '&callback=?';
    return sendRequest(url, SpokesResponseType.Integer, callback);
  };

  // Sets the time to live for all event queue messages
  Device.prototype.setGlobalTTL = function (ttl, callback) {
    var url = this.Path + '/EventManager/GlobalTTL?sess=' + this.Sess_Id + '&ttl=' + ttl + '&callback=?';
    return sendRequest(url, SpokesResponseType.Integer, callback);
  };

  // Sets the max number of events per queue, for all queues
  Device.prototype.setGlobalMaxEvents = function (max, callback) {
    var url = this.Path + '/EventManager/GlobalMaxCount?sess=' + this.Sess_Id + '&max=' + max + '&callback=?';
    return sendRequest(url, SpokesResponseType.Integer, callback);
  };

  // Sets the time to live for a list of queues
  Device.prototype.setQueueTTL = function (queue, ttl, callback) {
    // If they gave me an array of queue items, compress them down to an int
    queue = compressQueueList(queue) || 0;

    var url = this.Path + '/EventManager/TTL?sess=' + this.Sess_Id + '&queue=' + queue + '&ttl=' + ttl + '&callback=?';
    return sendRequest(url, SpokesResponseType.Integer, callback);
  };

  // Sets the max number of events per queue, for the list of queues given
  Device.prototype.setQueueMaxEvents = function (queue, max, callback) {
    // If they gave me an array of queue items, compress them down to an int
    queue = compressQueueList(queue) || 0;

    var url = this.Path + '/EventManager/MaxCount?sess=' + this.Sess_Id + '&queue=' + queue + '&max=' + max + '&callback=?';
    return sendRequest(url, SpokesResponseType.Integer, callback);
  };

  /////////////////////////////////////////////////////////////////////////////
  // Plugin object
  /////////////////////////////////////////////////////////////////////////////
  function Plugin(path) {
    this.Path = path;
    this.Sess_Id = '';
    this.isAttached = false;
  }

  /////////////////////////////////////////////////////////////////////////////
  // SessionManager

  // List out all plugins
  Plugin.prototype.pluginList = function (callback) {
    var url = this.Path + '/SessionManager/PluginList?callback=?';
    return sendRequest(url, SpokesResponseType.StringArray, callback);
  };

  // Register a plugin
  Plugin.prototype.register = function (name, callback) {
    var url = this.Path + '/SessionManager/Register?name=' + name + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  // Unregister a plugin
  Plugin.prototype.unRegister = function (name, callback) {
    var url = this.Path + '/SessionManager/UnRegister?name=' + name + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  // Set that the plugin is active
  Plugin.prototype.isActive = function (name, active, callback) {
    var url = this.Path + '/SessionManager/IsActive?name=' + name + '&active=' + active + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  /////////////////////////////////////////////////////////////////////////////
  // CallServices

  // Get the call manager state
  Plugin.prototype.callManagerState = function (callback) {
    var url = this.Path + '/CallServices/CallManagerState?callback=?';
    return sendRequest(url, SpokesResponseType.CallManagerState, callback);
  };

  // Session events
  Plugin.prototype.sessionEvents = function (callback, suppressLog) {
    var url = this.Path + '/CallServices/Events?callback=?';
    return sendRequest(url, SpokesResponseType.StringArray, callback, suppressLog);
  };

  // Session manager call events
  Plugin.prototype.sessionCallEvents = function (name, callback, suppressLog) {
    var url = this.Path + '/CallServices/SessionManagerCallEvents?name=' + name + '&callback=?';
    return sendRequest(url, SpokesResponseType.StringArray, callback, suppressLog);
  };

  // Call Events
  Plugin.prototype.callEvents = function (name, callback, suppressLog) {
    var url = this.Path + '/CallServices/CallEvents?name=' + name + '&callback=?';
    return sendRequest(url, SpokesResponseType.CallStateArray, callback, suppressLog);
  };

  // Call Requests
  Plugin.prototype.callRequests = function (name, callback) {
    var url = this.Path + '/CallServices/CallRequests?name=' + name + '&callback=?';
    return sendRequest(url, SpokesResponseType.ContactArray, callback);
  };

  Plugin.prototype.invokeCallControl = function (api, name, data, callback) {
    var callID, contact;

    if (data.hasOwnProperty('callID')) {
      if (isCallId(data.callID)) {
        callID = JSON.stringify(data.callID);
      } else {
        logger.warn('[Spokes]: ' + api + ' API was invoked with invalid callID');
        return false;
      }
    }

    if (data.hasOwnProperty('contact')) {
      if (isContact(data.contact)) {
        contact = JSON.stringify(data.contact);
      } else {
        logger.warn('[Spokes]: ' + api + ' API was invoked with invalid contact');
        return false;
      }
    }

    var url =
      this.Path +
      '/CallServices/' +
      api +
      '?name=' +
      name +
      (callID ? '&callID=' + callID : '') +
      (contact ? '&contact=' + contact : '') +
      (data.hasOwnProperty('tones') ? '&tones=' + data.tones : '') +
      (data.hasOwnProperty('route') ? '&route=' + data.route : '') +
      (data.hasOwnProperty('muted') ? '&muted=' + data.muted : '') +
      '&callback=?';

    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  // Incoming Call
  Plugin.prototype.incomingCall = function (name, callID, contact, tones, route, callback) {
    var data = {
      callID: callID,
      contact: contact,
      tones: tones,
      route: route
    };
    return this.invokeCallControl('IncomingCall', name, data, callback);
  };

  // Outgoing Call
  Plugin.prototype.outgoingCall = function (name, callID, contact, route, callback) {
    var data = {
      callID: callID,
      contact: contact,
      route: route
    };
    return this.invokeCallControl('OutgoingCall', name, data, callback);
  };

  // Terminate Call
  Plugin.prototype.terminateCall = function (name, callID, callback) {
    return this.invokeCallControl('TerminateCall', name, { callID: callID }, callback);
  };

  // Answer Call
  Plugin.prototype.answerCall = function (name, callID, callback) {
    return this.invokeCallControl('AnswerCall', name, { callID: callID }, callback);
  };

  // Hold Call
  Plugin.prototype.holdCall = function (name, callID, callback) {
    return this.invokeCallControl('HoldCall', name, { callID: callID }, callback);
  };

  // Resume Call
  Plugin.prototype.resumeCall = function (name, callID, callback) {
    return this.invokeCallControl('ResumeCall', name, { callID: callID }, callback);
  };

  // Mute Call
  Plugin.prototype.muteCall = function (name, muted, callback) {
    return this.invokeCallControl('MuteCall', name, { muted: !!muted }, callback);
  };

  // Insert Call
  Plugin.prototype.insertCall = function (name, callID, contact, callback) {
    var data = {
      callID: callID,
      contact: contact
    };
    return this.invokeCallControl('InsertCall', name, data, callback);
  };

  // Set Audio Route
  Plugin.prototype.setAudioRoute = function (name, callID, route, callback) {
    var data = {
      callID: callID,
      route: route
    };
    return this.invokeCallControl('SetAudioRoute', name, data, callback);
  };

  // Set Conference Id
  Plugin.prototype.setConferenceId = function (name, callID, callback) {
    return this.invokeCallControl('SetConferenceId', name, { callID: callID }, callback);
  };

  // Make Call
  Plugin.prototype.makeCall = function (name, contact, callback) {
    return this.invokeCallControl('MakeCall', name, { contact: contact }, callback);
  };

  /////////////////////////////////////////////////////////////////////////////
  // UserPreference object
  /////////////////////////////////////////////////////////////////////////////
  function UserPreference(path) {
    this.Path = path;
  }

  UserPreference.prototype.getDefaultSoftphone = function (callback) {
    var url = this.Path + '/UserPreference/GetDefaultSoftPhone?callback=?';
    return sendRequest(url, SpokesResponseType.String, callback);
  };

  UserPreference.prototype.getEscalateToVoiceSoftPhone = function (callback) {
    var url = this.Path + '/UserPreference/GetEscalateToVoiceSoftPhone?callback=?';
    return sendRequest(url, SpokesResponseType.String, callback);
  };

  UserPreference.prototype.getMediaPlayerActionIncomingCall = function (callback) {
    var url = this.Path + '/UserPreference/GetMediaPlayerActionIncomingCall?callback=?';
    return sendRequest(url, SpokesResponseType.String, callback);
  };

  UserPreference.prototype.getMediaPlayerActionEndedCall = function (callback) {
    var url = this.Path + '/UserPreference/GetMediaPlayerActionEndedCall?callback=?';
    return sendRequest(url, SpokesResponseType.String, callback);
  };

  UserPreference.prototype.getAutoPresence = function (callback) {
    var url = this.Path + '/UserPreference/GetAutoPresence?callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  UserPreference.prototype.getDoffAction = function (callback) {
    var url = this.Path + '/UserPreference/GetDoffAction?callback=?';
    return sendRequest(url, SpokesResponseType.String, callback);
  };

  UserPreference.prototype.getDonAction = function (callback) {
    var url = this.Path + '/UserPreference/GetDonAction?callback=?';
    return sendRequest(url, SpokesResponseType.String, callback);
  };

  UserPreference.prototype.getKeepLinkUp = function (callback) {
    var url = this.Path + '/UserPreference/GetKeepLinkUp?callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  UserPreference.prototype.getRingPCAndHS = function (callback) {
    var url = this.Path + '/UserPreference/GetRingPCAndHS?callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  UserPreference.prototype.setDefaultSoftphone = function (name, callback) {
    var url = this.Path + '/UserPreference/SetDefaultSoftPhone?name=' + name + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  UserPreference.prototype.setEscalateToVoiceSoftPhone = function (name, callback) {
    var url = this.Path + '/UserPreference/SetEscalateToVoiceSoftPhone?name=' + name + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  UserPreference.prototype.setMediaPlayerActionIncomingCall = function (actionIncoming, callback) {
    var url = this.Path + '/UserPreference/SetMediaPlayerActionIncomingCall?actionIncoming=' + actionIncoming + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  UserPreference.prototype.setMediaPlayerActionEndedCall = function (actionEnded, callback) {
    var url = this.Path + '/UserPreference/SetMediaPlayerActionEndedCall?actionEnded=' + actionEnded + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  UserPreference.prototype.setAutoPresence = function (autoPresence, callback) {
    var url = this.Path + '/UserPreference/SetAutoPresence?autoPresence=' + autoPresence + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  UserPreference.prototype.setDoffAction = function (doffAction, callback) {
    var url = this.Path + '/UserPreference/SetDoffAction?doffAction=' + doffAction + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  UserPreference.prototype.setDonAction = function (donAction, callback) {
    var url = this.Path + '/UserPreference/SetDonAction?donAction=' + donAction + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  UserPreference.prototype.setKeepLinkUp = function (keepLinkUp, callback) {
    var url = this.Path + '/UserPreference/SetKeepLinkUp?keepLinkUp=' + keepLinkUp + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  UserPreference.prototype.setRingPCAndHS = function (ringPcAndHS, callback) {
    var url = this.Path + '/UserPreference/SetRingPCAndHS?ringPcAndHS=' + ringPcAndHS + '&callback=?';
    return sendRequest(url, SpokesResponseType.Bool, callback);
  };

  /////////////////////////////////////////////////////////////////////////////
  // Spokes object
  /////////////////////////////////////////////////////////////////////////////
  function Spokes(url) {
    this.Path = url || 'https://127.0.0.1:32018';
    this.Device = new Device(this.Path);
    this.Plugin = new Plugin(this.Path);
    this.UserPreference = new UserPreference(this.Path);
  }

  // Expose public interfaces
  Plantronics.setLogger = setLogger;

  Plantronics.Spokes = Spokes;
  Plantronics.SpokesCallId = SpokesCallId;
  Plantronics.SpokesContact = SpokesContact;
  Plantronics.SessionCallState = SessionCallState;
  Plantronics.SpokesErrorType = SpokesErrorType;
}; // Base64 JavaScript decoder

// Copyright (c) 2008-2014 Lapo Luchini <lapo@lapo.it>

// Permission to use, copy, modify, and/or distribute this software for any
// purpose with or without fee is hereby granted, provided that the above
// copyright notice and this permission notice appear in all copies.
//
// THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
// WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
// MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
// ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
// WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
// ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
// OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.

/*jshint browser: true, strict: true, immed: true, latedef: true, undef: true, regexdash: false */
(function () {
  'use strict';

  var Base64 = {},
    decoder;

  Base64.decode = function (a) {
    var i;
    if (typeof decoder === 'undefined') {
      var b64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/',
        ignore = '= \f\n\r\t\u00A0\u2028\u2029';
      decoder = {};
      for (i = 0; i < 64; ++i) decoder[b64.charAt(i)] = i;
      for (i = 0; i < ignore.length; ++i) decoder[ignore.charAt(i)] = -1;
    }
    var out = [];
    var bits = 0,
      char_count = 0;
    for (i = 0; i < a.length; ++i) {
      var c = a.charAt(i);
      if (c == '=') break;
      c = decoder[c];
      if (c == -1) continue;
      if (typeof c === 'undefined') throw 'Illegal character at offset ' + i;
      bits |= c;
      if (++char_count >= 4) {
        out[out.length] = bits >> 16;
        out[out.length] = (bits >> 8) & 0xff;
        out[out.length] = bits & 0xff;
        bits = 0;
        char_count = 0;
      } else {
        bits <<= 6;
      }
    }
    switch (char_count) {
      case 1:
        throw 'Base64 encoding incomplete: at least 2 bits missing';
      case 2:
        out[out.length] = bits >> 10;
        break;
      case 3:
        out[out.length] = bits >> 16;
        out[out.length] = (bits >> 8) & 0xff;
        break;
    }
    return out;
  };

  Base64.re = /-----BEGIN [^-]+-----([A-Za-z0-9+\/=\s]+)-----END [^-]+-----|begin-base64[^\n]+\n([A-Za-z0-9+\/=\s]+)====/;
  Base64.unarmor = function (a) {
    var cert = Base64.removeBeginEnd(a);
    return Base64.decode(cert);
  };

  Base64.removeBeginEnd = function (a) {
    var m = Base64.re.exec(a);
    if (m) {
      if (m[1]) a = m[1];
      else if (m[2]) a = m[2];
      else throw 'RegExp out of sync';
    }

    a = a.replace('\n', '');
    a = a.replace('\n', '');
    return a;
  };

  ///////////////////////////////////////////////////////////////////////////
  // UnifyPhone: Extend Base64 object with methods from base64-js Node module
  var lookup = [];
  var revLookup = [];
  var Arr = typeof Uint8Array !== 'undefined' ? Uint8Array : Array;

  var code = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  for (var i = 0, len = code.length; i < len; ++i) {
    lookup[i] = code[i];
    revLookup[code.charCodeAt(i)] = i;
  }

  // Support decoding URL-safe base64 strings, as Node.js does.
  // See: https://en.wikipedia.org/wiki/Base64#URL_applications
  revLookup['-'.charCodeAt(0)] = 62;
  revLookup['_'.charCodeAt(0)] = 63;

  function getLens(b64) {
    var len = b64.length;

    if (len % 4 > 0) {
      throw new Error('Invalid string. Length must be a multiple of 4');
    }

    // Trim off extra bytes after placeholder bytes are found
    // See: https://github.com/beatgammit/base64-js/issues/42
    var validLen = b64.indexOf('=');
    if (validLen === -1) validLen = len;

    var placeHoldersLen = validLen === len ? 0 : 4 - (validLen % 4);

    return [validLen, placeHoldersLen];
  }

  // base64 is 4/3 + up to two characters of the original data
  Base64.byteLength = function (b64) {
    var lens = getLens(b64);
    var validLen = lens[0];
    var placeHoldersLen = lens[1];
    return ((validLen + placeHoldersLen) * 3) / 4 - placeHoldersLen;
  };

  function _byteLength(b64, validLen, placeHoldersLen) {
    return ((validLen + placeHoldersLen) * 3) / 4 - placeHoldersLen;
  }

  Base64.toByteArray = function (b64) {
    var tmp;
    var lens = getLens(b64);
    var validLen = lens[0];
    var placeHoldersLen = lens[1];

    var arr = new Arr(_byteLength(b64, validLen, placeHoldersLen));

    var curByte = 0;

    // if there are placeholders, only get up to the last complete 4 chars
    var len = placeHoldersLen > 0 ? validLen - 4 : validLen;

    for (var i = 0; i < len; i += 4) {
      tmp =
        (revLookup[b64.charCodeAt(i)] << 18) |
        (revLookup[b64.charCodeAt(i + 1)] << 12) |
        (revLookup[b64.charCodeAt(i + 2)] << 6) |
        revLookup[b64.charCodeAt(i + 3)];
      arr[curByte++] = (tmp >> 16) & 0xff;
      arr[curByte++] = (tmp >> 8) & 0xff;
      arr[curByte++] = tmp & 0xff;
    }

    if (placeHoldersLen === 2) {
      tmp = (revLookup[b64.charCodeAt(i)] << 2) | (revLookup[b64.charCodeAt(i + 1)] >> 4);
      arr[curByte++] = tmp & 0xff;
    }

    if (placeHoldersLen === 1) {
      tmp = (revLookup[b64.charCodeAt(i)] << 10) | (revLookup[b64.charCodeAt(i + 1)] << 4) | (revLookup[b64.charCodeAt(i + 2)] >> 2);
      arr[curByte++] = (tmp >> 8) & 0xff;
      arr[curByte++] = tmp & 0xff;
    }

    return arr;
  };

  function tripletToBase64(num) {
    return lookup[(num >> 18) & 0x3f] + lookup[(num >> 12) & 0x3f] + lookup[(num >> 6) & 0x3f] + lookup[num & 0x3f];
  }

  function encodeChunk(uint8, start, end) {
    var tmp;
    var output = [];
    for (var i = start; i < end; i += 3) {
      tmp = ((uint8[i] << 16) & 0xff0000) + ((uint8[i + 1] << 8) & 0xff00) + (uint8[i + 2] & 0xff);
      output.push(tripletToBase64(tmp));
    }
    return output.join('');
  }

  Base64.fromByteArray = function (uint8) {
    var tmp;
    var len = uint8.length;
    var extraBytes = len % 3; // if we have 1 byte left, pad 2 bytes
    var parts = [];
    var maxChunkLength = 16383; // must be multiple of 3

    // go through the array every three bytes, we'll deal with trailing stuff later
    for (var i = 0, len2 = len - extraBytes; i < len2; i += maxChunkLength) {
      parts.push(encodeChunk(uint8, i, i + maxChunkLength > len2 ? len2 : i + maxChunkLength));
    }

    // pad the end with zeros, but make sure to not forget the extra bytes
    if (extraBytes === 1) {
      tmp = uint8[len - 1];
      parts.push(lookup[tmp >> 2] + lookup[(tmp << 4) & 0x3f] + '==');
    } else if (extraBytes === 2) {
      tmp = (uint8[len - 2] << 8) + uint8[len - 1];
      parts.push(lookup[tmp >> 10] + lookup[(tmp >> 4) & 0x3f] + lookup[(tmp << 2) & 0x3f] + '=');
    }

    return parts.join('');
  };

  // export globals
  window.Base64 = Base64;
  // UnifyPhone: Change to always expose on window scope, even for Electron
})(); // Define external globals for JSHint

/*global module, Uint8Array, CryptoJS*/
/*eslint-disable no-bitwise*/

//----------------------------------------------------------------------
// AuthenticationContext enhanced based on AdalJS v1.0.14 library
// from
// https://github.com/AzureAD/azure-activedirectory-library-for-js
// Added code to support acquiring access_token directly on login
// and to collect the renewed token directly from the iFrame used to renew
//----------------------------------------------------------------------

//----------------------------------------------------------------------
// AdalJS v1.0.14
// @preserve Copyright (c) Microsoft Open Technologies, Inc.
// All Rights Reserved
// Apache License 2.0
//
// Licensed under the Apache License, Version 2.0 (the 'License');
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an 'AS IS' BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
//----------------------------------------------------------------------

var AuthenticationContext = (function () {
  'use strict';

  /**
   * Configuration options for Authentication Context.
   * @class config
   *  @property {string} tenant - Your target tenant.
   *  @property {string} clientID - Client ID assigned to your app by Azure Active Directory.
   *  @property {string} redirectUri - Endpoint at which you expect to receive tokens.Defaults to `window.location.href`.
   *  @property {string} instance - Azure Active Directory Instance.Defaults to `https://login.microsoftonline.com/`.
   *  @property {Array} endpoints - Collection of {Endpoint-ResourceId} used for automatically attaching tokens in webApi calls.
   *  @property {Boolean} popUp - Set this to true to enable login in a popup winodow instead of a full redirect.Defaults to `false`.
   *  @property {string} localLoginUrl - Set this to redirect the user to a custom login page.
   *  @property {function} displayCall - User defined function of handling the navigation to Azure AD authorization endpoint in case of login. Defaults to 'null'.
   *  @property {string} postLogoutRedirectUri - Redirects the user to postLogoutRedirectUri after logout. Defaults is 'redirectUri'.
   *  @property {string} cacheLocation - Sets browser storage to either 'localStorage' or sessionStorage'. Defaults to 'sessionStorage'.
   *  @property {Array.<string>} anonymousEndpoints Array of keywords or URI's. Adal will not attach a token to outgoing requests that have these keywords or uri.
   *  Defaults to 'null'.
   *  @property {number} expireOffsetSeconds If the cached token is about to be expired in the expireOffsetSeconds (in seconds), Adal will renew the token instead
   *  of using the cached token. Defaults to 120 seconds.
   *  @property {string} correlationId Unique identifier used to map the request with the response. Defaults to RFC4122 version 4 guid (128 bits).
   */

  /**
   * Creates a new AuthenticationContext object.
   * @constructor
   * @param {config}  config               Configuration options for AuthenticationContext
   */

  AuthenticationContext = function (config) {
    if (AuthenticationContext.prototype._singletonInstance) {
      var that = AuthenticationContext.prototype._singletonInstance;
      that._getAuthToken();
      that.verbose('AuthenticationContext return singletonInstance');
      return that;
    }
    AuthenticationContext.prototype._singletonInstance = this;

    /**
     * Enum for request type
     * @enum {string}
     */
    this.REQUEST_TYPE = {
      LOGIN: 'LOGIN',
      RENEW_TOKEN: 'RENEW_TOKEN',
      REQUEST_TOKEN: 'REQUEST_TOKEN',
      UNKNOWN: 'UNKNOWN'
    };

    /**
     * Enum for storage constants
     * @enum {string}
     */
    this.CONSTANTS = {
      AUTH_CODE: 'code',
      ACCESS_TOKEN: 'access_token',
      ID_TOKEN: 'id_token',
      REFRESH_TOKEN: 'refresh_token',
      EXPIRES_IN: 'expires_in',
      ERROR_DESCRIPTION: 'error_description',
      SESSION_STATE: 'session_state',
      STORAGE: {
        TOKEN_KEYS: 'adal.token.keys',
        AUTHTOKEN_KEY: 'adal.authtoken.key',
        ACCESS_TOKEN_KEY: 'adal.access.token.key',
        AUTH_CODE_KEY: 'adal.auth.code.key',
        EXPIRATION_KEY: 'adal.expiration.key',
        STATE_LOGIN: 'adal.state.login',
        STATE_RENEW: 'adal.state.renew',
        NONCE_IDTOKEN: 'adal.nonce.idtoken',
        SESSION_STATE: 'adal.session.state',
        USERNAME: 'adal.username',
        IDTOKEN: 'adal.idtoken',
        ERROR: 'adal.error',
        ERROR_DESCRIPTION: 'adal.error.description',
        LOGIN_REQUEST: 'adal.login.request',
        LOGIN_ERROR: 'adal.login.error',
        RENEW_STATUS: 'adal.token.renew.status',
        REFRESH_TOKEN_KEY: 'adal.refresh.token.key',
        RESOURCE_KEY: 'adal.resource.key',
        TOKEN_TYPE_KEY: 'adal.token.type.key'
      },
      RESOURCE_DELIMETER: '|',
      LOADFRAME_TIMEOUT: '6000',
      TOKEN_RENEW_STATUS_CANCELED: 'Canceled',
      TOKEN_RENEW_STATUS_COMPLETED: 'Completed',
      TOKEN_RENEW_STATUS_IN_PROGRESS: 'In Progress',
      LOGGING_LEVEL: {
        ERROR: 0,
        WARN: 1,
        INFO: 2,
        VERBOSE: 3
      },
      LEVEL_STRING_MAP: {
        0: 'ERROR:',
        1: 'WARNING:',
        2: 'INFO:',
        3: 'VERBOSE:'
      },
      POPUP_WIDTH: 483,
      POPUP_HEIGHT: 600
    };

    // public
    this.instance = 'https://login.microsoftonline.com/';
    this.config = {};
    this.callback = null;
    this.popUp = false;
    this.isAngular = false;
    this.authToken = {};

    // private
    this._user = null;
    this._activeRenewals = {};
    this._loginInProgress = false;
    this._getAuthorizationCode = false;
    this._renewStates = [];

    window.callBackMappedToRenewStates = {};
    window.callBacksMappedToRenewStates = {};

    // validate before constructor assignments
    if (config.displayCall && typeof config.displayCall !== 'function') {
      throw new Error('displayCall is not a function');
    }

    if (!config.clientId) {
      throw new Error('clientId is required');
    }

    this.config = this._cloneConfig(config);

    if (this.config.navigateToLoginRequestUrl === undefined) {
      this.config.navigateToLoginRequestUrl = true;
    }

    if (this.config.popUp) {
      this.popUp = true;
    }

    if (this.config.callback && typeof this.config.callback === 'function') {
      this.callback = this.config.callback;
    }

    if (this.config.instance) {
      this.instance = this.config.instance;
    }

    // App can request idtoken for itself using clientid as resource
    if (!this.config.loginResource) {
      this.config.loginResource = this.config.clientId;
    }

    // redirect and logout_redirect are set to current location by default
    if (!this.config.redirectUri) {
      // strip off query parameters or hashes from the redirect uri as AAD does not allow those.
      this.config.redirectUri = window.location.href.split('?')[0].split('#')[0];
    }

    if (!this.config.postLogoutRedirectUri) {
      // strip off query parameters or hashes from the post logout redirect uri as AAD does not allow those.
      this.config.postLogoutRedirectUri = window.location.href.split('?')[0].split('#')[0];
    }

    if (!this.config.anonymousEndpoints) {
      this.config.anonymousEndpoints = [];
    }

    if (this.config.isAngular) {
      this.isAngular = this.config.isAngular;
    }

    this.authToken = this._getAuthToken();
    this.verbose('AuthenticationContext initialized');
    return this;
  };

  window.Logging = {
    level: 0,
    log: function () {}
  };

  AuthenticationContext.prototype.setInstance = function (instance) {
    if (this.config) {
      this.config.instance = instance;
    }
    this.instance = instance;
  };

  AuthenticationContext.prototype.setClientId = function (clientId) {
    if (this.config) {
      this.config.clientId = clientId;
    }
  };

  AuthenticationContext.prototype.getAuthorizationCode = function (resource) {
    if (this._loginInProgress) {
      this.verbose('Login in progress');
      return;
    }
    var expectedState = this._guid();
    this.config.state = expectedState;
    this.config.resource = resource;
    this._idTokenNonce = this._guid();
    this.verbose('getAuthorizationCode expected state: ' + expectedState + ' startPage:' + window.location.href);
    this._saveItem(this.CONSTANTS.STORAGE.LOGIN_REQUEST, window.location.href);
    this._saveItem(this.CONSTANTS.STORAGE.LOGIN_ERROR, '');
    this._saveItem(this.CONSTANTS.STORAGE.STATE_LOGIN, expectedState);
    this._saveItem(this.CONSTANTS.STORAGE.NONCE_IDTOKEN, this._idTokenNonce);
    this._saveItem(this.CONSTANTS.STORAGE.ERROR, '');
    this._saveItem(this.CONSTANTS.STORAGE.ERROR_DESCRIPTION, '');
    var urlNavigate = this._getNavigateUrl('code', resource) + '&nonce=' + encodeURIComponent(this._idTokenNonce);
    this._loginInProgress = true;
    this._getAuthorizationCode = true;
    if (this.config.displayCall) {
      // User defined way of handling the navigation
      this.config.displayCall(urlNavigate);
    } else if (this.popUp) {
      this._loginPopup(urlNavigate);
    } else {
      this.promptUser(urlNavigate);
    }
  };

  /**
   * Initiates the login process by redirecting the user to Azure AD authorization endpoint.
   */
  AuthenticationContext.prototype.login = function (resource, loginStartPage, prompt, code) {
    // Token is not present and user needs to login
    if (this._loginInProgress) {
      this.verbose('Login in progress');
      return;
    }
    var expectedState = this._guid();
    this.config.state = expectedState;
    this.config.resource = resource;
    this._idTokenNonce = this._guid();
    this.verbose('Expected state: ' + expectedState + ' startPage:' + window.location.href);
    this._saveItem(this.CONSTANTS.STORAGE.LOGIN_REQUEST, loginStartPage || window.location.href);
    this._saveItem(this.CONSTANTS.STORAGE.LOGIN_ERROR, '');
    this._saveItem(this.CONSTANTS.STORAGE.STATE_LOGIN, expectedState);
    this._saveItem(this.CONSTANTS.STORAGE.NONCE_IDTOKEN, this._idTokenNonce);
    this._saveItem(this.CONSTANTS.STORAGE.ERROR, '');
    this._saveItem(this.CONSTANTS.STORAGE.ERROR_DESCRIPTION, '');

    var responseType;
    if (resource) {
      responseType = code ? 'code' : 'token';
    } else {
      responseType = 'id_token';
    }
    var urlNavigate = this._getNavigateUrl(responseType, resource) + '&nonce=' + encodeURIComponent(this._idTokenNonce);
    urlNavigate = urlNavigate + (prompt || '');

    this._loginInProgress = true;
    if (this.config.displayCall) {
      // User defined way of handling the navigation
      this.config.displayCall(urlNavigate);
    } else if (this.popUp) {
      this._loginPopup(urlNavigate);
    } else {
      this.promptUser(urlNavigate);
    }
  };

  /**
   * Configures popup window for login.
   * @ignore
   */
  AuthenticationContext.prototype._openPopup = function (urlNavigate, title, popUpWidth, popUpHeight, hidden) {
    try {
      /**
       * adding winLeft and winTop to account for dual monitor
       * using screenLeft and screenTop for IE8 and earlier
       */
      var winLeft = window.screenLeft ? window.screenLeft : window.screenX;
      var winTop = window.screenTop ? window.screenTop : window.screenY;
      /**
       * window.innerWidth displays browser window's height and width excluding toolbars
       * using document.documentElement.clientWidth for IE8 and earlier
       */
      var width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
      var height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
      var left = width / 2 - popUpWidth / 2 + winLeft;
      var top = height / 2 - popUpHeight / 2 + winTop;

      var spec = 'width=' + popUpWidth + ', height=' + popUpHeight + ', top=' + top + ', left=' + left;
      if (hidden) {
        spec = spec + ', toolbar=no, status=no';
      }
      if (this.config.openPopup) {
        this.config.openPopup({ newWindowUrl: urlNavigate, width: popUpWidth, height: popUpHeight });
      } else {
        var popupWindow = window.open(urlNavigate, title, spec);
        if (popupWindow.focus) {
          popupWindow.focus();
        }
        return popupWindow;
      }
    } catch (e) {
      this.warn('Error opening popup, ' + e.message);
      this._loginInProgress = false;
    }
    return null;
  };

  /**
   * After authorization, the user will be sent to your specified redirect_uri with the user's bearer token
   * attached to the URI fragment as an id_token field. It closes popup window after redirection.
   * @ignore
   */
  AuthenticationContext.prototype._loginPopup = function (urlNavigate) {
    var that = this;
    if (this.config.openExternalPopup) {
      this.config
        .openExternalPopup({ newWindowUrl: urlNavigate, width: this.CONSTANTS.POPUP_WIDTH, height: this.CONSTANTS.POPUP_HEIGHT, title: 'login' })
        .then(function (hash) {
          that.handleWindowCallback(hash);
          if (that._getAuthorizationCode) {
            that._onGetAuthorizationCodeCallback();
          } else {
            that._onLoginCallback();
          }
          that._loginInProgress = false;
        })
        .catch(function () {
          that.warn('Error when login via External popup');
          that._loginInProgress = false;
        });
    } else {
      var popupWindow = this._openPopup(urlNavigate, 'login', this.CONSTANTS.POPUP_WIDTH, this.CONSTANTS.POPUP_HEIGHT);
      if (popupWindow === null) {
        this.warn('Popup Window is null. This can happen if you are using IE');
        this._saveItem(this.CONSTANTS.STORAGE.ERROR, 'Error opening popup');
        this._saveItem(this.CONSTANTS.STORAGE.ERROR_DESCRIPTION, 'Popup Window is null. This can happen if you are using IE');
        this._saveItem(this.CONSTANTS.STORAGE.LOGIN_ERROR, 'Popup Window is null. This can happen if you are using IE');
        if (this.callback) {
          this.callback(this._getItem(this.CONSTANTS.STORAGE.LOGIN_ERROR), null, this._getItem(this.CONSTANTS.STORAGE.ERROR));
        }
        return;
      }
      var registeredRedirectUri;
      if (this.config.redirectUri.indexOf('#') !== -1) {
        registeredRedirectUri = this.config.redirectUri.split('#')[0];
      } else {
        registeredRedirectUri = this.config.redirectUri;
      }
      var pollTimer = window.setInterval(function () {
        if (!popupWindow || popupWindow.closed || popupWindow.closed === undefined) {
          that._loginInProgress = false;
          window.clearInterval(pollTimer);
        }
        try {
          if (popupWindow.location.href.indexOf(registeredRedirectUri) !== -1) {
            var response = popupWindow.location.hash ? popupWindow.location.hash : popupWindow.location.search;
            if (that.isAngular) {
              that._onPopUpHashChanged(response);
            } else {
              that.handleWindowCallback(response);
              if (that._getAuthorizationCode) {
                that._onGetAuthorizationCodeCallback();
              } else {
                that._onLoginCallback();
              }
            }
            window.clearInterval(pollTimer);
            that._loginInProgress = false;
            that.info('Closing popup window');
            popupWindow.close();
          }
        } catch (e) {}
      }, 20);
    }
  };

  AuthenticationContext.prototype._renewPopup = function (urlNavigate) {
    var popupWindow = this._openPopup(urlNavigate, 'renew', 1, 1, true);
    if (!popupWindow) {
      this.warn('Popup Window is null. This can happen if you are using IE');
      this._saveItem(this.CONSTANTS.STORAGE.ERROR, 'Error opening popup');
      this._saveItem(this.CONSTANTS.STORAGE.ERROR_DESCRIPTION, 'Popup Window is null. This can happen if you are using IE');
      this._saveItem(this.CONSTANTS.STORAGE.LOGIN_ERROR, 'Popup Window is null. This can happen if you are using IE');
      if (this.callback) {
        this.callback(this._getItem(this.CONSTANTS.STORAGE.LOGIN_ERROR), null, this._getItem(this.CONSTANTS.STORAGE.ERROR));
      }
      return;
    }
    var registeredRedirectUri;
    if (this.config.redirectUri.indexOf('#') !== -1) {
      registeredRedirectUri = this.config.redirectUri.split('#')[0];
    } else {
      registeredRedirectUri = this.config.redirectUri;
    }
    var that = this;
    var pollTimer = window.setInterval(function () {
      if (!popupWindow || popupWindow.closed || popupWindow.closed === undefined) {
        that._loginInProgress = false;
        window.clearInterval(pollTimer);
      }
      try {
        if (popupWindow.location.href.indexOf(registeredRedirectUri) !== -1) {
          if (that.isAngular) {
            that._onPopUpHashChanged(popupWindow.location.hash);
          } else {
            that.handleWindowCallback(popupWindow.location.hash, popupWindow);
          }
          window.clearInterval(pollTimer);
          that._loginInProgress = false;
          that.info('Closing popup window');
          popupWindow.close();
        }
      } catch (e) {}
    }, 20);
  };

  /**
   * Constructs the authorization endpoint URL and returns it.
   * @ignore
   */
  AuthenticationContext.prototype._getNavigateUrl = function (responseType, resource, action, grantType, code) {
    var urlNavigate = this._getUrl(action) + '?' + this._getParams(responseType, resource, grantType, code) + this._addLibMetadata();
    this.verbose('Navigate url: ' + this.obfuscateUrlParam(urlNavigate, ['code', 'refresh_token', 'client_id']));
    return urlNavigate;
  };

  /**
   * Constructs the authorization endpoint URL and returns it.
   * @ignore
   */
  AuthenticationContext.prototype._getUrl = function (action) {
    var tenant = 'common';
    if (this.config.tenant) {
      tenant = this.config.tenant;
    }
    if (!action) {
      action = 'authorize';
    }

    var url = this.instance + tenant + '/oauth2/' + action;
    return url;
  };

  AuthenticationContext.prototype._isAboutToExpire = function (expiry) {
    this.verbose('isAboutToExpire: ' + expiry);
    // If expiration is within offset, it will force renew
    var offset = this.config.expireOffsetSeconds || 300;

    return !(expiry && expiry > this._now() + offset);
  };

  /**
   * Return the number of milliseconds since 1970/01/01
   * @ignore
   */
  AuthenticationContext.prototype._now = function () {
    return Math.round(new Date().getTime() / 1000.0);
  };

  AuthenticationContext.prototype._getParams = function (responseType, resource, grantType, code, refreshToken) {
    if (code && this.config) {
      this.config.code = code;
    }
    return this._serialize(responseType, this.config, resource, grantType, refreshToken);
  };

  AuthenticationContext.prototype.getAccessToken = function (cb) {
    this.verbose('getAccessToken');
    if (!this.authToken || (!this.authToken.access_token && !this.authToken.refresh_token)) {
      this.warn('getAccessToken: access_token not available');
      cb && cb(null, 'AccessToken not requested');
    } else if (this._isAboutToExpire(this.authToken.expires_on) || !this.authToken.access_token) {
      this.requestAccessToken(this.authToken.resource, 'refresh_token', null, this.authToken.refresh_token, function (authToken, err) {
        if (err) {
          cb && cb(null, err);
        } else if (authToken && authToken.access_token) {
          cb && cb(authToken);
        } else {
          cb && cb(null, 'AccessToken not received');
        }
      });
    } else {
      cb && cb(this.authToken);
    }
  };

  AuthenticationContext.prototype._saveAuthToken = function (authToken) {
    if (!authToken) {
      this.verbose('_saveAuthToken received an invalid authToken');
      return;
    }

    this._saveItem(this.CONSTANTS.STORAGE.ACCESS_TOKEN_KEY, authToken.access_token);
    this._saveItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY, authToken.expires_on);
    this._saveItem(this.CONSTANTS.STORAGE.REFRESH_TOKEN_KEY, authToken.refresh_token);
    this._saveItem(this.CONSTANTS.STORAGE.RESOURCE_KEY, authToken.resource);
    this._saveItem(this.CONSTANTS.STORAGE.TOKEN_TYPE_KEY, authToken.token_type);
    this._logAuthToken('_saveAuthToken', authToken);
  };

  AuthenticationContext.prototype._getAuthToken = function () {
    var authToken = {};
    authToken.access_token = this._getItem(this.CONSTANTS.STORAGE.ACCESS_TOKEN_KEY);
    authToken.expires_on = this._getItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY);
    authToken.refresh_token = this._getItem(this.CONSTANTS.STORAGE.REFRESH_TOKEN_KEY);
    authToken.resource = this._getItem(this.CONSTANTS.STORAGE.RESOURCE_KEY);
    authToken.token_type = this._getItem(this.CONSTANTS.STORAGE.TOKEN_TYPE_KEY);

    this.authToken = authToken;
    this._logAuthToken('_getAuthToken', authToken);
    return authToken;
  };

  AuthenticationContext.prototype._logAuthToken = function (prefix, authToken) {
    if (authToken) {
      this.verbose(
        prefix +
          ' authToken: {resource: ' +
          authToken.resource +
          ', access_token: ' +
          this.obfuscateToken(authToken.access_token) +
          ', expires_on: ' +
          authToken.expires_on +
          ', refresh_token: ' +
          this.obfuscateToken(authToken.refresh_token) +
          ', token_type: ' +
          authToken.token_type +
          '}'
      );
    }
  };

  AuthenticationContext.prototype.requestAccessToken = function (resource, grant_type, code, refreshToken, cb) {
    this.verbose(
      'requestAccessToken - resource: ' +
        resource +
        ', grant_type: ' +
        grant_type +
        ', code: ' +
        this.obfuscateToken(code) +
        ', refreshToken: ' +
        this.obfuscateToken(refreshToken)
    );
    var that = this;
    var url = this._getNavigateUrl(null, resource, 'token', grant_type, code, refreshToken);
    var params = this._getParams(null, resource, grant_type, code, refreshToken);
    this._sendPostHttpRequest(url, params, function (response, err) {
      try {
        if (err) {
          that.error('requestAccessToken received an error from post http request: ' + err);
          that.error('requestAccessToken sent url: ' + url);
          that.error('requestAccessToken sent params: ' + that.obfuscateUrlParam(params, ['code', 'refresh_token', 'client_id']));
          cb && cb(null, err);
        } else if (!response) {
          that.error('requestAccessToken received invalid token response from ' + resource);
          that.error('requestAccessToken sent url: ' + url);
          that.error('requestAccessToken sent params: ' + that.obfuscateUrlParam(params, ['code', 'refresh_token', 'client_id']));
          cb && cb(null, 'Invalid token response');
        } else {
          var newAuthToken = JSON.parse(response);
          if (newAuthToken) {
            that.authToken = newAuthToken;
            that._saveAuthToken(that.authToken);
            that.verbose('requestAccessToken received token');
            cb && cb(that.authToken);
          }
        }
      } catch (e) {
        that.error('requestAccessToken error ' + e);
        cb && cb(null, e);
      }
    });
  };

  AuthenticationContext.prototype.obfuscateUrlParam = function (url, paramName) {
    if (!url) {
      return '';
    }
    var params = typeof paramName === 'string' ? [paramName] : paramName;
    var urlRet = url;
    params.forEach(function (element) {
      var match = url.match(element + '=(.*?)&');
      if (match && match.length > 1) {
        urlRet = urlRet.replace(match[1], AuthenticationContext.prototype.obfuscateToken(match[1]));
      }
    });
    return urlRet;
  };

  AuthenticationContext.prototype.obfuscateToken = function (token) {
    if (!token) {
      return '';
    }
    var strToken = typeof token === 'string' ? token : token.accessToken;
    var cut = 6;
    if (!strToken || strToken.length <= 10) {
      return '*****';
    } else if (strToken <= 30) {
      cut = 4;
    }
    return strToken.substring(0, cut) + '...' + strToken.substring(strToken.length - cut, strToken.length);
  };

  var TIMEOUT = 30000;

  function sendRequest(xmlHttp, data, timeout) {
    var that = this;
    xmlHttp.unifyPhoneSentAt = Date.now();
    xmlHttp.send(data);

    var token = window.setTimeout(function () {
      if (xmlHttp.readyState !== 4) {
        that.verbose('HTTP request timeout expired. readyState = ' + xmlHttp.readyState);
        xmlHttp.unifyPhoneTimedOut = true;
        xmlHttp.abort();
      }
    }, timeout || TIMEOUT);
    return token;
  }

  AuthenticationContext.prototype._renewHttpRequest = function (urlNavigate) {
    var that = this;
    try {
      var xmlHttp = new XMLHttpRequest();
      xmlHttp.open('GET', urlNavigate, true);

      var onRequestFailed = function () {
        that.error('Failed to renew token for url=' + urlNavigate + ', status = ' + xmlHttp.status + ', statusText = ' + xmlHttp.statusText);
      };

      xmlHttp.onreadystatechange = function () {
        if (xmlHttp.readyState !== 4) {
          return;
        }
        if (xmlHttp.status === 200) {
          that.handleWindowCallback(xmlHttp.url);
          return;
        }
        if (xmlHttp.status === 401 || xmlHttp.status === 400) {
          onRequestFailed();
        }
      };
      sendRequest(xmlHttp);
    } catch (e) {
      that.error('Error renewing token using httprequest: ' + e);
    }
  };

  AuthenticationContext.prototype._onPopUpHashChanged = function (hash) {
    var evt = new CustomEvent('adal:popUpHashChanged', { detail: hash });
    window.dispatchEvent(evt);
  };

  AuthenticationContext.prototype._onLoginCallback = function () {
    var evt = new CustomEvent('adal:loginCallback');
    window.dispatchEvent(evt);
  };

  AuthenticationContext.prototype._onGetAuthorizationCodeCallback = function () {
    var evt = new CustomEvent('adal:getAuthorizationCodeCallback');
    window.dispatchEvent(evt);
  };

  AuthenticationContext.prototype.loginInProgress = function () {
    return this._loginInProgress;
  };

  /**
   * Checks for the resource in the cache. By default, cache location is Session Storage
   * @ignore
   * @returns {Boolean} 'true' if login is in progress, else returns 'false'.
   */
  AuthenticationContext.prototype._hasResource = function (key) {
    var keys = this._getItem(this.CONSTANTS.STORAGE.TOKEN_KEYS);
    return keys && !this._isEmpty(keys) && keys.indexOf(key + this.CONSTANTS.RESOURCE_DELIMETER) > -1;
  };

  /**
   * Gets authorization code  for the specified resource from the cache.
   * @param {string}   resource A URI that identifies the resource for which the code is requested.
   * @returns {string} authorization code if if it exists, otherwise null.
   */
  AuthenticationContext.prototype.getCachedAuthCode = function (resource) {
    if (!this._hasResource(resource)) {
      return null;
    }

    var code = this._getItem(this.CONSTANTS.STORAGE.AUTH_CODE_KEY + resource);
    return code;
  };

  /**
   * Gets token for the specified resource from the cache.
   * @param {string}   resource A URI that identifies the resource for which the token is requested.
   * @returns {string} token if if it exists and not expired, otherwise null.
   */
  AuthenticationContext.prototype.getCachedToken = function (resource) {
    this.verbose('getCachedToken ' + resource);
    if (!this._hasResource(resource)) {
      this.warn('resource does not exist');
      return null;
    }

    var token = this._getItem(this.CONSTANTS.STORAGE.ACCESS_TOKEN_KEY + resource);
    var expiry = this._getItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY + resource);

    // If expiration is within offset, it will force renew
    var offset = this.config.expireOffsetSeconds || 300;

    if (expiry && expiry > this._now() + offset) {
      return token;
    } else {
      this._saveItem(this.CONSTANTS.STORAGE.ACCESS_TOKEN_KEY + resource, '');
      this._saveItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY + resource, 0);
      this.warn('access_token removed');
      return null;
    }
  };

  AuthenticationContext.prototype.getTokenExpiry = function (resource) {
    if (!this._hasResource(resource)) {
      return null;
    }

    return this._getItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY + resource);
  };

  /**
   * Gets the timeout to trigger a renew token from now.
   * @param {string}   resource A URI that identifies the resource for which the token is requested.
   * @returns {long} the remaining time to trigger the token renew in seconds,  otherwise null.
   */
  AuthenticationContext.prototype.getTokenExpiryTimeout = function (resource) {
    if (!this._hasResource(resource)) {
      return null;
    }

    var expiry = this._getItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY + resource);
    // If expiration is within offset, it will force renew
    var offset = this.config.expireOffsetSeconds || 300;
    return expiry - (this._now() + offset);
  };

  /**
   * User information from idtoken.
   *  @class User
   *  @property {string} userName - username assigned from upn or email.
   *  @property {object} profile - properties parsed from idtoken.
   */

  /**
   * If user object exists, returns it. Else creates a new user object by decoding id_token from the cache.
   * @returns {User} user object
   */
  AuthenticationContext.prototype.getCachedUser = function (resource) {
    if (this._user) {
      return this._user;
    }

    var idtoken = this._getItem(this.CONSTANTS.STORAGE.IDTOKEN);
    this._user = this._createUser(idtoken, resource);
    return this._user;
  };

  /**
   * Adds the passed callback to the array of callbacks for the specified resource and puts the array on the window object.
   * @param {string}   resource A URI that identifies the resource for which the token is requested.
   * @param {string}   expectedState A unique identifier (guid).
   * @param {tokenCallback} callback - The callback provided by the caller. It will be called with token or error.
   */
  AuthenticationContext.prototype.registerCallback = function (expectedState, resource, callback) {
    this._activeRenewals[resource] = expectedState;
    if (!window.callBacksMappedToRenewStates[expectedState]) {
      window.callBacksMappedToRenewStates[expectedState] = [];
    }
    var self = this;
    window.callBacksMappedToRenewStates[expectedState].push(callback);
    if (!window.callBackMappedToRenewStates[expectedState]) {
      window.callBackMappedToRenewStates[expectedState] = function (errorDesc, token, error) {
        for (var i = 0; i < window.callBacksMappedToRenewStates[expectedState].length; ++i) {
          try {
            window.callBacksMappedToRenewStates[expectedState][i](errorDesc, token, error);
          } catch (e) {
            self.warn(e);
          }
        }
        self._activeRenewals[resource] = null;
        window.callBacksMappedToRenewStates[expectedState] = null;
        window.callBackMappedToRenewStates[expectedState] = null;
      };
    }
  };

  AuthenticationContext.prototype._authCodeUrl = function (code) {
    var urlNavigate = this._getNavigateUrl('', this.config.resource, 'token', 'authorization_code', code);
    return urlNavigate;
  };

  AuthenticationContext.prototype._renewUrl = function (resource) {
    var urlNavigate = this._getNavigateUrl('token', resource) + '&prompt=none';
    urlNavigate = this._addHintParameters(urlNavigate);
    return urlNavigate;
  };

  // var errorResponse = {error:'', error_description:''};
  // var token = 'string token';
  // callback(errorResponse, token)
  // with callback
  /**
   * Acquires access token with hidden iframe
   * @ignore
   */
  AuthenticationContext.prototype._renewToken = function (resource, callback) {
    // use iframe to try refresh token
    // use given resource to create new authz url
    this.verbose('renewToken is called for resource:' + resource);

    var expectedState = this._guid() + '|' + resource;
    this.config.state = expectedState;
    // renew happens in iframe, so it keeps javascript context
    this._renewStates.push(expectedState);

    this.verbose('Renew token Expected state: ' + expectedState);
    var urlNavigate = this._getNavigateUrl('token', resource) + '&prompt=none';
    urlNavigate = this._addHintParameters(urlNavigate);

    this.registerCallback(expectedState, resource, callback);
    this.verbose('Navigate to:' + urlNavigate);

    var that = this;
    if (this.config.openExternalPopup) {
      this.config
        .openExternalPopup({
          hidden: true,
          newWindowUrl: urlNavigate,
          width: this.CONSTANTS.POPUP_WIDTH,
          height: this.CONSTANTS.POPUP_HEIGHT,
          title: 'renewToken'
        })
        .then(function (hash) {
          that.handleWindowCallback(hash, that.config.openExternalPopup);
        })
        .catch(function () {
          that.warn('Error when login via External popup');
        });
    } else {
      var frameHandle = this._addAdalFrame('adalRenewFrame' + resource);
      frameHandle.src = 'about:blank';

      frameHandle.onload = function () {
        if (
          frameHandle.contentDocument &&
          frameHandle.contentDocument.location &&
          frameHandle.contentDocument.location.hash &&
          frameHandle.contentDocument.location.hash.match(/access_token/i)
        ) {
          that.handleWindowCallback(frameHandle.contentDocument.location.hash, frameHandle);
        }
      };
      this._loadFrameTimeout(urlNavigate, 'adalRenewFrame' + resource, resource);
    }
  };

  /**
   * Renews idtoken for app's own backend when resource is clientId and calls the callback with token/error
   * @ignore
   */
  AuthenticationContext.prototype._renewIdToken = function (callback) {
    // use iframe to try refresh token
    this.verbose('renewIdToken is called');
    var frameHandle = this._addAdalFrame('adalIdTokenFrame');
    var expectedState = this._guid() + '|' + this.config.clientId;
    this._idTokenNonce = this._guid();
    this._saveItem(this.CONSTANTS.STORAGE.NONCE_IDTOKEN, this._idTokenNonce);
    this.config.state = expectedState;
    // renew happens in iframe, so it keeps javascript context
    this._renewStates.push(expectedState);

    this.verbose('Renew Idtoken Expected state: ' + expectedState);
    var urlNavigate = this._getNavigateUrl('id_token', null) + '&prompt=none';
    urlNavigate = this._addHintParameters(urlNavigate);

    urlNavigate += '&nonce=' + encodeURIComponent(this._idTokenNonce);
    this.registerCallback(expectedState, this.config.clientId, callback);
    this.idTokenNonce = null;
    this.verbose('Navigate to:' + urlNavigate);
    frameHandle.src = 'about:blank';
    this._loadFrameTimeout(urlNavigate, 'adalIdTokenFrame', this.config.clientId);
  };

  /**
   * Checks if the authorization endpoint URL contains query string parameters
   * @ignore
   */
  AuthenticationContext.prototype._urlContainsQueryStringParameter = function (name, url) {
    // regex to detect pattern of a ? or & followed by the name parameter and an equals character
    var regex = new RegExp('[\\?&]' + name + '=');
    return regex.test(url);
  };

  // Calling _loadFrame but with a timeout to signal failure in loadframeStatus. Callbacks are left
  // registered when network errors occur and subsequent token requests for same resource are registered to the pending request
  /**
   * @ignore
   */
  AuthenticationContext.prototype._loadFrameTimeout = function (urlNavigation, frameName, resource) {
    //set iframe session to pending
    this.verbose('Set loading state to pending for: ' + resource);
    this._saveItem(this.CONSTANTS.STORAGE.RENEW_STATUS + resource, this.CONSTANTS.TOKEN_RENEW_STATUS_IN_PROGRESS);
    this._loadFrame(urlNavigation, frameName);
    var self = this;
    window.setTimeout(function () {
      if (self._getItem(self.CONSTANTS.STORAGE.RENEW_STATUS + resource) === self.CONSTANTS.TOKEN_RENEW_STATUS_IN_PROGRESS) {
        // fail the iframe session if it's in pending state
        self.verbose('Loading frame has timed out after: ' + self.CONSTANTS.LOADFRAME_TIMEOUT / 1000 + ' seconds for resource ' + resource);
        var expectedState = self._activeRenewals[resource];
        if (expectedState && window.callBackMappedToRenewStates[expectedState]) {
          window.callBackMappedToRenewStates[expectedState]('Token renewal operation failed due to timeout', null, 'Token Renewal Failed');
        }

        self._saveItem(self.CONSTANTS.STORAGE.RENEW_STATUS + resource, self.CONSTANTS.TOKEN_RENEW_STATUS_CANCELED);
      }
    }, self.CONSTANTS.LOADFRAME_TIMEOUT);
  };

  /**
   * Loads iframe with authorization endpoint URL
   * @ignore
   */
  AuthenticationContext.prototype._loadFrame = function (urlNavigate, frameName) {
    // This trick overcomes iframe navigation in IE
    // IE does not load the page consistently in iframe
    var self = this;
    self.info('LoadFrame: ' + frameName);
    var frameCheck = frameName;
    window.setTimeout(function () {
      var frameHandle = self._addAdalFrame(frameCheck);
      if (frameHandle.src === '' || frameHandle.src === 'about:blank') {
        frameHandle.src = urlNavigate;
        self._loadFrame(urlNavigate, frameCheck);
      }
    }, 500);
  };

  AuthenticationContext.prototype.getAuthCode = function (resource, callback) {
    if (this._isEmpty(resource)) {
      this.warn('resource is required');
      callback('resource is required', null, 'resource is required');
      return;
    }

    var authCode = this.getCachedAuthCode(resource);

    if (authCode) {
      this.verbose('AuthCode is already in cache for resource:' + resource);
      callback(null, { authCode: authCode, config: this.config }, null);
    } else {
      callback('authCode not cached', null, 'authCode not cached');
    }
  };

  /**
   * @callback tokenCallback
   * @param {string} error_description error description returned from AAD if token request fails.
   * @param {string} token token returned from AAD if token request is successful.
   * @param {string} error error message returned from AAD if token request fails.
   */

  /**
   * Acquires token from the cache if it is not expired. Otherwise sends request to AAD to obtain a new token.
   * @param {string}   resource  ResourceUri identifying the target resource
   * @param {tokenCallback} callback -  The callback provided by the caller. It will be called with token or error.
   */
  AuthenticationContext.prototype.acquireToken = function (resource, callback) {
    this.verbose('acquireToken ' + resource);
    if (this._isEmpty(resource)) {
      this.warn('resource is required');
      callback('resource is required', null, 'resource is required');
      return;
    }

    var token = this.getCachedToken(resource);
    this.getCachedUser(resource);

    if (token) {
      this.verbose('Token is already in cache for resource:' + resource);
      callback(null, token, null);
      return;
    }

    if (!this._user) {
      this.warn('User login is required');
      callback('User login is required', null, 'login required');
      return;
    }

    // refresh attept with iframe
    //Already renewing for this resource, callback when we get the token.
    if (this._activeRenewals[resource]) {
      //Active renewals contains the state for each renewal.
      this.registerCallback(this._activeRenewals[resource], resource, callback);
    } else if (resource === this.config.clientId) {
      // App uses idtoken to send to api endpoints
      // Default resource is tracked as clientid to store this token
      this.verbose('renewing idtoken');
      this._renewIdToken(callback);
    } else {
      this._renewToken(resource, callback);
    }
  };

  /**
   * Redirects the browser to Azure AD authorization endpoint.
   * @param {string}   urlNavigate  Url of the authorization endpoint.
   */
  AuthenticationContext.prototype.promptUser = function (urlNavigate) {
    if (urlNavigate) {
      this.verbose('Navigate to:' + urlNavigate);
      window.location.replace(urlNavigate);
    } else {
      this.verbose('Navigate url is empty');
    }
  };

  /**
   * Clears cache items.
   */
  AuthenticationContext.prototype.clearCache = function () {
    this.verbose('clearCache');
    this._saveItem(this.CONSTANTS.STORAGE.ACCESS_TOKEN_KEY, '');
    this._saveItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY, 0);
    this._saveItem(this.CONSTANTS.STORAGE.SESSION_STATE, '');
    this._saveItem(this.CONSTANTS.STORAGE.STATE_LOGIN, '');
    this._renewStates = [];
    this._saveItem(this.CONSTANTS.STORAGE.USERNAME, '');
    this._saveItem(this.CONSTANTS.STORAGE.IDTOKEN, '');
    this._saveItem(this.CONSTANTS.STORAGE.ERROR, '');
    this._saveItem(this.CONSTANTS.STORAGE.ERROR_DESCRIPTION, '');
    var keys = this._getItem(this.CONSTANTS.STORAGE.TOKEN_KEYS);

    if (!this._isEmpty(keys)) {
      keys = keys.split(this.CONSTANTS.RESOURCE_DELIMETER);
      for (var i = 0; i < keys.length; i++) {
        this._saveItem(this.CONSTANTS.STORAGE.ACCESS_TOKEN_KEY + keys[i], '');
        this._saveItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY + keys[i], 0);
      }
    }
    this._saveItem(this.CONSTANTS.STORAGE.TOKEN_KEYS, '');
    this._getAuthToken();
  };

  /**
   * Clears cache items for a given resource.
   * @param {string}  resource a URI that identifies the resource.
   */
  AuthenticationContext.prototype.clearCacheForResource = function (resource) {
    this.verbose('clearCacheForResource ' + resource);
    this._saveItem(this.CONSTANTS.STORAGE.STATE_RENEW, '');
    this._saveItem(this.CONSTANTS.STORAGE.ERROR, '');
    this._saveItem(this.CONSTANTS.STORAGE.ERROR_DESCRIPTION, '');
    if (this._hasResource(resource)) {
      this.verbose('access_token removed for ' + resource);
      this._saveItem(this.CONSTANTS.STORAGE.ACCESS_TOKEN_KEY + resource, '');
      this._saveItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY + resource, 0);
    }
  };

  /**
   * Redirects user to logout endpoint.
   * After logout, it will redirect to postLogoutRedirectUri if added as a property on the config object.
   */
  AuthenticationContext.prototype.logOut = function () {
    this.clearCache();
    this._user = null;
    var urlNavigate;

    if (this.config.logOutUri) {
      urlNavigate = this.config.logOutUri;
    } else {
      var tenant = 'common';
      var logout = '';

      if (this.config.tenant) {
        tenant = this.config.tenant;
      }

      if (this.config.postLogoutRedirectUri) {
        logout = 'post_logout_redirect_uri=' + encodeURIComponent(this.config.postLogoutRedirectUri);
      }

      urlNavigate = this.instance + tenant + '/oauth2/logout?' + logout;
    }

    this.verbose('Logout navigate to: ' + urlNavigate);
    this.promptUser(urlNavigate);
  };

  AuthenticationContext.prototype._isEmpty = function (str) {
    return typeof str === 'undefined' || !str || !str.length;
  };

  /**
   * @callback userCallback
   * @param {string} error error message if user info is not available.
   * @param {User} user user object retrieved from the cache.
   */

  /**
   * Calls the passed in callback with the user object or error message related to the user.
   * @param {userCallback} callback - The callback provided by the caller. It will be called with user or error.
   */
  AuthenticationContext.prototype.getUser = function (callback) {
    // IDToken is first call
    if (typeof callback !== 'function') {
      throw new Error('callback is not a function');
    }

    // user in memory
    if (this._user) {
      callback(null, this._user);
      return;
    }

    // frame is used to get idtoken
    var idtoken = this._getItem(this.CONSTANTS.STORAGE.IDTOKEN);
    if (!this._isEmpty(idtoken)) {
      this.verbose('User exists in cache: ');
      this._user = this._createUser(idtoken);
      callback(null, this._user);
    } else {
      this.warn('User information is not available');
      callback('User information is not available', null);
    }
  };

  /**
   * Adds login_hint to authorization URL which is used to pre-fill the username field of sign in page for the user if known ahead of time.
   * domain_hint can be one of users/organisations which when added skips the email based discovery process of the user.
   * @ignore
   */
  AuthenticationContext.prototype._addHintParameters = function (urlNavigate) {
    // include hint params only if upn is present
    if (this._user && this._user.profile && this._user.profile.hasOwnProperty('upn')) {
      // add login_hint
      urlNavigate += '&login_hint=' + encodeURIComponent(this._user.profile.upn);

      // don't add domain_hint twice if user provided it in the extraQueryParameter value
      if (!this._urlContainsQueryStringParameter('domain_hint', urlNavigate) && this._user.profile.upn.indexOf('@') > -1) {
        var parts = this._user.profile.upn.split('@');
        // local part can include @ in quotes. Sending last part handles that.
        urlNavigate += '&domain_hint=' + encodeURIComponent(parts[parts.length - 1]);
      }
    }

    return urlNavigate;
  };

  /**
   * Creates a user object by decoding the id_token
   * @ignore
   */
  AuthenticationContext.prototype._createUser = function (idToken, resource) {
    var user = null;
    var parsedJson = this._extractIdToken(idToken);
    if (parsedJson && parsedJson.hasOwnProperty('aud')) {
      if (
        (resource && parsedJson.aud.toLowerCase() === resource.toLowerCase()) ||
        (this.config.clientId && parsedJson.aud.toLowerCase() === this.config.clientId.toLowerCase()) ||
        (this.config.resource && parsedJson.aud.toLowerCase() === this.config.resource.toLowerCase())
      ) {
        user = {
          userName: '',
          profile: parsedJson
        };

        if (parsedJson.hasOwnProperty('upn')) {
          user.userName = parsedJson.upn;
        } else if (parsedJson.hasOwnProperty('email')) {
          user.userName = parsedJson.email;
        }
      } else {
        this.warn('IdToken has invalid aud field');
      }
    }

    return user;
  };

  /**
   * Returns the anchor part(#) or search part(?) of the URL
   * @ignore
   */
  AuthenticationContext.prototype._getHash = function (hash) {
    if (hash.indexOf('#/') > -1) {
      hash = hash.substring(hash.indexOf('#/') + 2);
    } else if (hash.indexOf('#') > -1) {
      hash = hash.substring(1);
    } else if (hash.indexOf('?/') > -1) {
      hash = hash.substring(hash.indexOf('?/') + 2);
    } else if (hash.indexOf('?') > -1) {
      hash = hash.substring(1);
    }

    return hash;
  };

  /**
   * Checks if the URL fragment contains access token, id token or error_description.
   * @param {string} hash  -  Hash passed from redirect page
   * @returns {Boolean} true if response contains id_token, access_token or error, false otherwise.
   */
  AuthenticationContext.prototype.isCallback = function (hash) {
    hash = this._getHash(hash);
    var parameters = this._deserialize(hash);
    return (
      parameters.hasOwnProperty(this.CONSTANTS.ERROR_DESCRIPTION) ||
      parameters.hasOwnProperty(this.CONSTANTS.ACCESS_TOKEN) ||
      parameters.hasOwnProperty(this.CONSTANTS.ID_TOKEN) ||
      parameters.hasOwnProperty(this.CONSTANTS.AUTH_CODE)
    );
  };

  /**
   * Gets login error
   * @returns {string} error message related to login.
   */
  AuthenticationContext.prototype.getLoginError = function () {
    return this._getItem(this.CONSTANTS.STORAGE.LOGIN_ERROR);
  };

  /**
   * Request info object created from the response received from AAD.
   *  @class RequestInfo
   *  @property {object} parameters - object comprising of fields such as id_token/error, session_state, state, e.t.c.
   *  @property {REQUEST_TYPE} requestType - either LOGIN, RENEW_TOKEN or UNKNOWN.
   *  @property {boolean} stateMatch - true if state is valid, false otherwise.
   *  @property {string} stateResponse - unique guid used to match the response with the request.
   *  @property {boolean} valid - true if requestType contains id_token, access_token or error, false otherwise.
   */

  /**
   * Creates a requestInfo object from the URL fragment and returns it.
   * @returns {RequestInfo} an object created from the redirect response from AAD comprising of the keys - parameters, requestType, stateMatch, stateResponse and valid.
   */
  AuthenticationContext.prototype.getRequestInfo = function (hash) {
    hash = this._getHash(hash);
    var parameters = this._deserialize(hash);
    var requestInfo = {
      valid: false,
      parameters: {},
      stateMatch: false,
      stateResponse: '',
      requestType: this.REQUEST_TYPE.UNKNOWN
    };
    if (parameters) {
      requestInfo.parameters = parameters;
      if (
        parameters.hasOwnProperty(this.CONSTANTS.ERROR_DESCRIPTION) ||
        parameters.hasOwnProperty(this.CONSTANTS.ACCESS_TOKEN) ||
        parameters.hasOwnProperty(this.CONSTANTS.AUTH_CODE) ||
        parameters.hasOwnProperty(this.CONSTANTS.ID_TOKEN)
      ) {
        requestInfo.valid = true;

        // which call
        var stateResponse = '';
        if (parameters.hasOwnProperty('state')) {
          this.verbose('State: ' + parameters.state);
          stateResponse = parameters.state;
        } else {
          this.warn('No state returned');
          return requestInfo;
        }

        requestInfo.stateResponse = stateResponse;

        // async calls can fire iframe and login request at the same time if developer does not use the API as expected
        // incoming callback needs to be looked up to find the request type
        if (stateResponse === this._getItem(this.CONSTANTS.STORAGE.STATE_LOGIN)) {
          requestInfo.requestType = this.REQUEST_TYPE.LOGIN;
          requestInfo.stateMatch = true;
          return requestInfo;
        }

        // external api requests may have many renewtoken requests for different resource
        if (!requestInfo.stateMatch && window.parent && window.parent.AuthenticationContext) {
          var statesInParentContext = window.parent.AuthenticationContext()._renewStates;
          for (var i = 0; i < statesInParentContext.length; i++) {
            if (statesInParentContext[i] === requestInfo.stateResponse) {
              requestInfo.requestType = this.REQUEST_TYPE.RENEW_TOKEN;
              requestInfo.stateMatch = true;
              break;
            }
          }
        }
      }
    }

    return requestInfo;
  };

  /**
   * Extracts resource value from state.
   * @ignore
   */
  AuthenticationContext.prototype._getResourceFromState = function (state) {
    if (state) {
      var splitIndex = state.indexOf('|');
      if (splitIndex > -1 && splitIndex + 1 < state.length) {
        return state.substring(splitIndex + 1);
      }
    }

    return '';
  };

  /**
   * Saves token or error received in the response from AAD in the cache. In case of id_token, it also creates the user object.
   */
  AuthenticationContext.prototype.saveTokenFromHash = function (requestInfo) {
    this.verbose('State status:' + requestInfo.stateMatch + '; Request type:' + requestInfo.requestType);
    this._saveItem(this.CONSTANTS.STORAGE.ERROR, '');
    this._saveItem(this.CONSTANTS.STORAGE.ERROR_DESCRIPTION, '');

    var resource = this._getResourceFromState(requestInfo.stateResponse);

    // Record error
    if (requestInfo.parameters.hasOwnProperty(this.CONSTANTS.ERROR_DESCRIPTION)) {
      this.verbose('Error :' + requestInfo.parameters.error + '; Error description:' + requestInfo.parameters[this.CONSTANTS.ERROR_DESCRIPTION]);
      this._saveItem(this.CONSTANTS.STORAGE.ERROR, requestInfo.parameters.error);
      this._saveItem(this.CONSTANTS.STORAGE.ERROR_DESCRIPTION, requestInfo.parameters[this.CONSTANTS.ERROR_DESCRIPTION]);

      if (requestInfo.requestType === this.REQUEST_TYPE.LOGIN) {
        this._loginInProgress = false;
        this._saveItem(this.CONSTANTS.STORAGE.LOGIN_ERROR, requestInfo.parameters.error_description);
      }
    } else {
      this.verbose('Verify state from redirect');
      if (requestInfo.stateMatch) {
        // record tokens to storage if exists
        this.verbose('State is right');
        if (requestInfo.parameters.hasOwnProperty(this.CONSTANTS.SESSION_STATE)) {
          this._saveItem(this.CONSTANTS.STORAGE.SESSION_STATE, requestInfo.parameters[this.CONSTANTS.SESSION_STATE]);
        }

        var keys;

        if (requestInfo.parameters.hasOwnProperty(this.CONSTANTS.ACCESS_TOKEN)) {
          this.verbose('Fragment has access token');
          this._loginInProgress = false;

          this._loginInProgress = false;

          this._user = this._createUser(requestInfo.parameters[this.CONSTANTS.ACCESS_TOKEN]);

          if (this._user && this._user.profile) {
            resource = resource || this.config.resource;
            this._saveItem(this.CONSTANTS.STORAGE.IDTOKEN, requestInfo.parameters[this.CONSTANTS.ACCESS_TOKEN]);

            if (!this._hasResource(resource)) {
              keys = this._getItem(this.CONSTANTS.STORAGE.TOKEN_KEYS) || '';
              this._saveItem(this.CONSTANTS.STORAGE.TOKEN_KEYS, keys + resource + this.CONSTANTS.RESOURCE_DELIMETER);
            }
            // save token with related resource
            this.verbose('saveTokenFromHash for ' + resource);
            this._saveItem(this.CONSTANTS.STORAGE.ACCESS_TOKEN_KEY + resource, requestInfo.parameters[this.CONSTANTS.ACCESS_TOKEN]);
            this._saveItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY + resource, this._expiresIn(requestInfo.parameters[this.CONSTANTS.EXPIRES_IN]));
          } else {
            this._saveItem(this.CONSTANTS.STORAGE.ERROR, 'invalid access_token');
            this._saveItem(this.CONSTANTS.STORAGE.ERROR_DESCRIPTION, 'Invalid access_token. id_token: ' + requestInfo.parameters[this.CONSTANTS.ACCESS_TOKEN]);
          }
        }

        if (requestInfo.parameters.hasOwnProperty(this.CONSTANTS.AUTH_CODE)) {
          this.verbose('Fragment has authorization code');
          this._loginInProgress = false;

          resource = resource || this.config.resource;
          this._saveItem(this.CONSTANTS.STORAGE.AUTH_CODE_KEY, requestInfo.parameters[this.CONSTANTS.AUTH_CODE]);

          if (!this._hasResource(resource)) {
            keys = this._getItem(this.CONSTANTS.STORAGE.TOKEN_KEYS) || '';
            this._saveItem(this.CONSTANTS.STORAGE.TOKEN_KEYS, keys + resource + this.CONSTANTS.RESOURCE_DELIMETER);
          }
          // save token with related resource
          this._saveItem(this.CONSTANTS.STORAGE.AUTH_CODE_KEY + resource, requestInfo.parameters[this.CONSTANTS.AUTH_CODE]);

          this.verbose('Clear AccessToken. Fragment has authorization code');
          this._saveItem(this.CONSTANTS.STORAGE.ACCESS_TOKEN_KEY + resource, '');
          this._saveItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY + resource, 0);
        }

        if (requestInfo.parameters.hasOwnProperty(this.CONSTANTS.REFRESH_TOKEN)) {
          this.verbose('Fragment has refresh token');
          this._loginInProgress = false;

          resource = resource || this.config.resource;
          this._saveItem(this.CONSTANTS.STORAGE.REFRESH_TOKEN, requestInfo.parameters[this.CONSTANTS.REFRESH_TOKEN]);

          if (!this._hasResource(resource)) {
            keys = this._getItem(this.CONSTANTS.STORAGE.TOKEN_KEYS) || '';
            this._saveItem(this.CONSTANTS.STORAGE.TOKEN_KEYS, keys + resource + this.CONSTANTS.RESOURCE_DELIMETER);
          }
          // save token with related resource
          this._saveItem(this.CONSTANTS.STORAGE.AUTH_CODE_KEY + resource, requestInfo.parameters[this.CONSTANTS.REFRESH_TOKEN]);
        }

        if (requestInfo.parameters.hasOwnProperty(this.CONSTANTS.ID_TOKEN)) {
          this.verbose('Fragment has id token');
          this._loginInProgress = false;

          this._user = this._createUser(requestInfo.parameters[this.CONSTANTS.ID_TOKEN]);

          if (this._user && this._user.profile) {
            if (this._user.profile.nonce !== this._getItem(this.CONSTANTS.STORAGE.NONCE_IDTOKEN)) {
              this._user = null;
              this._saveItem(this.CONSTANTS.STORAGE.LOGIN_ERROR, 'Nonce is not same as ' + this._idTokenNonce);
            } else {
              this._saveItem(this.CONSTANTS.STORAGE.IDTOKEN, requestInfo.parameters[this.CONSTANTS.ID_TOKEN]);

              // Save idtoken as access token for app itself
              resource = this.config.loginResource ? this.config.loginResource : this.config.clientId;

              if (!this._hasResource(resource)) {
                keys = this._getItem(this.CONSTANTS.STORAGE.TOKEN_KEYS) || '';
                this._saveItem(this.CONSTANTS.STORAGE.TOKEN_KEYS, keys + resource + this.CONSTANTS.RESOURCE_DELIMETER);
              }
              this._saveItem(this.CONSTANTS.STORAGE.ACCESS_TOKEN_KEY + resource, requestInfo.parameters[this.CONSTANTS.ID_TOKEN]);
              this._saveItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY + resource, this._user.profile.exp);
            }
          } else {
            this._saveItem(this.CONSTANTS.STORAGE.ERROR, 'invalid id_token');
            this._saveItem(this.CONSTANTS.STORAGE.ERROR_DESCRIPTION, 'Invalid id_token. id_token: ' + requestInfo.parameters[this.CONSTANTS.ID_TOKEN]);
          }
        }
      } else {
        this._saveItem(this.CONSTANTS.STORAGE.ERROR, 'Invalid_state');
        this._saveItem(this.CONSTANTS.STORAGE.ERROR_DESCRIPTION, 'Invalid_state. state: ' + requestInfo.stateResponse);
      }
    }
    this._saveItem(this.CONSTANTS.STORAGE.RENEW_STATUS + resource, this.CONSTANTS.TOKEN_RENEW_STATUS_COMPLETED);
  };

  /**
   * Gets resource for given endpoint if mapping is provided with config.
   * @param {string} endpoint  -  The URI for which the resource Id is requested.
   * @returns {string} resource for this API endpoint.
   */
  AuthenticationContext.prototype.getResourceForEndpoint = function (endpoint) {
    // if user specified list of anonymous endpoints, no need to send token to these endpoints, return null.
    if (this.config && this.config.anonymousEndpoints) {
      for (var i = 0; i < this.config.anonymousEndpoints.length; i++) {
        if (endpoint.indexOf(this.config.anonymousEndpoints[i]) > -1) {
          return null;
        }
      }
    }

    if (this.config && this.config.endpoints) {
      for (var configEndpoint in this.config.endpoints) {
        // configEndpoint is like /api/Todo requested endpoint can be /api/Todo/1
        if (endpoint.indexOf(configEndpoint) > -1) {
          return this.config.endpoints[configEndpoint];
        }
      }
    }

    // default resource will be clientid if nothing specified
    // App will use idtoken for calls to itself
    // check if it's staring from http or https, needs to match with app host
    if (endpoint.indexOf('http://') > -1 || endpoint.indexOf('https://') > -1) {
      if (this._getHostFromUri(endpoint) === this._getHostFromUri(this.config.redirectUri)) {
        return this.config.loginResource;
      }
    } else {
      // in angular level, the url for $http interceptor call could be relative url,
      // if it's relative call, we'll treat it as app backend call.
      return this.config.loginResource;
    }

    // if not the app's own backend or not a domain listed in the endpoints structure
    return null;
  };

  /**
   * Strips the protocol part of the URL and returns it.
   * @ignore
   */
  AuthenticationContext.prototype._getHostFromUri = function (uri) {
    // remove http:// or https:// from uri
    var extractedUri = String(uri).replace(/^(https?:)\/\//, '');

    extractedUri = extractedUri.split('/')[0];
    return extractedUri;
  };

  /**
   * This method must be called for processing the response received from AAD. It extracts the hash, processes the token or error,
   * saves it in the cache and calls the registered callbacks with the result.
   * @param {string} [hash=window.location.hash] - Hash fragment of Url.
   */
  AuthenticationContext.prototype.handleWindowCallback = function (hash, otherWindow) {
    // This is for regular javascript usage for redirect handling
    // need to make sure this is for callback
    if (hash === null) {
      hash = window.location.hash;
    }
    if (this.isCallback(hash)) {
      var requestInfo = this.getRequestInfo(hash);
      this.saveTokenFromHash(requestInfo);
      var token = null,
        callback = null;
      if ((requestInfo.requestType === this.REQUEST_TYPE.RENEW_TOKEN && window.parent && window.parent !== window) || otherWindow) {
        // iframe call but same single page
        this.verbose('Window is in iframe');
        callback = window.parent.callBackMappedToRenewStates[requestInfo.stateResponse];
        token = requestInfo.parameters[this.CONSTANTS.ACCESS_TOKEN] || requestInfo.parameters[this.CONSTANTS.ID_TOKEN];
      } else if (requestInfo.requestType === this.REQUEST_TYPE.LOGIN) {
        callback = this.callback;
        token =
          requestInfo.parameters[this.CONSTANTS.AUTH_CODE] ||
          requestInfo.parameters[this.CONSTANTS.ACCESS_TOKEN] ||
          requestInfo.parameters[this.CONSTANTS.ID_TOKEN];
      } else if (requestInfo.requestType === this.REQUEST_TYPE.REQUEST_TOKEN) {
        callback = this.callback;
        token = requestInfo.parameters[this.CONSTANTS.ACCESS_TOKEN] || requestInfo.parameters[this.CONSTANTS.ID_TOKEN];
      }
      try {
        if (callback) {
          callback(this._getItem(this.CONSTANTS.STORAGE.ERROR_DESCRIPTION), token, this._getItem(this.CONSTANTS.STORAGE.ERROR));
        }
      } catch (err) {
        this.error('Error occurred in user defined callback function', err);
      }
      if (!this.popUp) {
        window.location.hash = '';
        if (this.config.navigateToLoginRequestUrl && window.location.href.replace('#', '') !== this._getItem(this.CONSTANTS.STORAGE.LOGIN_REQUEST)) {
          window.location.href = this._getItem(this.CONSTANTS.STORAGE.LOGIN_REQUEST);
        }
      }
    }
  };

  /**
   * Returns the decoded id_token.
   * @ignore
   */
  AuthenticationContext.prototype._extractIdToken = function (encodedIdToken) {
    // id token will be decoded to get the username
    var decodedToken = this._decodeJwt(encodedIdToken);
    if (!decodedToken) {
      return null;
    }

    try {
      var base64IdToken = decodedToken.JWSPayload;
      var base64Decoded = this._base64DecodeStringUrlSafe(base64IdToken);
      if (!base64Decoded) {
        this.verbose('The returned id_token could not be base64 url safe decoded.');
        return null;
      }

      // ECMA script has JSON built-in support
      return JSON.parse(base64Decoded);
    } catch (err) {
      this.error('The returned id_token could not be decoded', err);
    }

    return null;
  };

  /**
   * Decodes a string of data which has been encoded using base-64 encoding.
   * @ignore
   */
  AuthenticationContext.prototype._base64DecodeStringUrlSafe = function (base64IdToken) {
    // html5 should support atob function for decoding
    base64IdToken = base64IdToken.replace(/-/g, '+').replace(/_/g, '/');
    if (window.atob) {
      return decodeURIComponent(escape(window.atob(base64IdToken))); // jshint ignore:line
    } else {
      return decodeURIComponent(escape(this._decode(base64IdToken)));
    }
  };

  //Take https://cdnjs.cloudflare.com/ajax/libs/Base64/0.3.0/base64.js and https://en.wikipedia.org/wiki/Base64 as reference.
  AuthenticationContext.prototype._decode = function (base64IdToken) {
    var codes = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
    base64IdToken = String(base64IdToken).replace(/=+$/, '');

    var length = base64IdToken.length;
    if (length % 4 === 1) {
      throw new Error('The token to be decoded is not correctly encoded.');
    }

    var h1,
      h2,
      h3,
      h4,
      bits,
      c1,
      c2,
      c3,
      decoded = '';
    for (var i = 0; i < length; i += 4) {
      //Every 4 base64 encoded character will be converted to 3 byte string, which is 24 bits
      // then 6 bits per base64 encoded character
      h1 = codes.indexOf(base64IdToken.charAt(i));
      h2 = codes.indexOf(base64IdToken.charAt(i + 1));
      h3 = codes.indexOf(base64IdToken.charAt(i + 2));
      h4 = codes.indexOf(base64IdToken.charAt(i + 3));

      // For padding, if last two are '='
      if (i + 2 === length - 1) {
        bits = (h1 << 18) | (h2 << 12) | (h3 << 6);
        c1 = (bits >> 16) & 255;
        c2 = (bits >> 8) & 255;
        decoded += String.fromCharCode(c1, c2);
        break;
        // if last one is '='
      } else if (i + 1 === length - 1) {
        bits = (h1 << 18) | (h2 << 12);
        c1 = (bits >> 16) & 255;
        decoded += String.fromCharCode(c1);
        break;
      }

      bits = (h1 << 18) | (h2 << 12) | (h3 << 6) | h4;

      // then convert to 3 byte chars
      c1 = (bits >> 16) & 255;
      c2 = (bits >> 8) & 255;
      c3 = bits & 255;

      decoded += String.fromCharCode(c1, c2, c3);
    }

    return decoded;
  };

  /**
   * Decodes an id token into an object with header, payload and signature fields.
   * @ignore
   */
  // Adal.node js crack function
  AuthenticationContext.prototype._decodeJwt = function (jwtToken) {
    if (this._isEmpty(jwtToken)) {
      return null;
    }

    var idTokenPartsRegex = /^([^.\s]*)\.([^.\s]+)\.([^.\s]*)$/;

    var matches = idTokenPartsRegex.exec(jwtToken);
    if (!matches || matches.length < 4) {
      this.warn('The returned id_token is not parseable.');
      return null;
    }

    var crackedToken = {
      header: matches[1],
      JWSPayload: matches[2],
      JWSSig: matches[3]
    };

    return crackedToken;
  };

  /**
   * Converts string to represent binary data in ASCII string format by translating it into a radix-64 representation and returns it
   * @ignore
   */
  AuthenticationContext.prototype._convertUrlSafeToRegularBase64EncodedString = function (str) {
    return str.replace('-', '+').replace('_', '/');
  };

  /**
   * Serializes the parameters for the authorization endpoint URL and returns the serialized uri string.
   * @ignore
   */
  AuthenticationContext.prototype._serialize = function (responseType, obj, resource, grantType, refreshToken) {
    var str = [];
    if (obj !== null) {
      if (responseType) {
        str.push('response_type=' + responseType);
      }
      if (grantType) {
        str.push('grant_type=' + grantType);
      }
      if (refreshToken) {
        str.push('refresh_token=' + refreshToken);
      }
      if (resource) {
        str.push('resource=' + encodeURIComponent(resource));
      }
      if (obj) {
        if (obj.code) {
          str.push('code=' + encodeURIComponent(obj.code));
        }
        if (obj.clientId) {
          str.push('client_id=' + encodeURIComponent(obj.clientId));
        }
        if (obj.redirectUri) {
          str.push('redirect_uri=' + encodeURIComponent(obj.redirectUri));
        }
        if (obj.state) {
          str.push('state=' + encodeURIComponent(obj.state));
        }

        if (obj.hasOwnProperty('slice')) {
          str.push('slice=' + encodeURIComponent(obj.slice));
        }

        if (obj.hasOwnProperty('extraQueryParameter')) {
          str.push(obj.extraQueryParameter);
        }

        var correlationId = obj.correlationId ? obj.correlationId : this._guid();
        str.push('client-request-id=' + encodeURIComponent(correlationId));
      }
    }

    return str.join('&');
  };

  /**
   * Parses the query string parameters into a key-value pair object.
   * @ignore
   */
  AuthenticationContext.prototype._deserialize = function (query) {
    var match,
      pl = /\+/g, // Regex for replacing addition symbol with a space
      search = /([^&=]+)=([^&]*)/g,
      decode = function (s) {
        return decodeURIComponent(s.replace(pl, ' '));
      },
      obj = {};
    match = search.exec(query);
    while (match) {
      obj[decode(match[1])] = decode(match[2]);
      match = search.exec(query);
    }

    return obj;
  };

  /**
   * Converts decimal value to hex equivalent
   * @ignore
   */
  AuthenticationContext.prototype._decimalToHex = function (number) {
    var hex = number.toString(16);
    while (hex.length < 2) {
      hex = '0' + hex;
    }
    return hex;
  };

  /**
   * Generates RFC4122 version 4 guid (128 bits)
   * @ignore
   */
  /* jshint ignore:start */
  AuthenticationContext.prototype._guid = function () {
    // RFC4122: The version 4 UUID is meant for generating UUIDs from truly-random or
    // pseudo-random numbers.
    // The algorithm is as follows:
    //     Set the two most significant bits (bits 6 and 7) of the
    //        clock_seq_hi_and_reserved to zero and one, respectively.
    //     Set the four most significant bits (bits 12 through 15) of the
    //        time_hi_and_version field to the 4-bit version number from
    //        Section 4.1.3. Version4
    //     Set all the other bits to randomly (or pseudo-randomly) chosen
    //     values.
    // UUID                   = time-low '-' time-mid '-'time-high-and-version '-'clock-seq-reserved and low(2hexOctet)'-' node
    // time-low               = 4hexOctet
    // time-mid               = 2hexOctet
    // time-high-and-version  = 2hexOctet
    // clock-seq-and-reserved = hexOctet:
    // clock-seq-low          = hexOctet
    // node                   = 6hexOctet
    // Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
    // y could be 1000, 1001, 1010, 1011 since most significant two bits needs to be 10
    // y values are 8, 9, A, B
    var cryptoObj = window.crypto || window.msCrypto; // for IE 11
    if (cryptoObj && cryptoObj.getRandomValues) {
      var buffer = new Uint8Array(16);
      cryptoObj.getRandomValues(buffer);
      // buffer[6] and buffer[7] represents the time_hi_and_version field. We will set the four most significant bits (4 through 7)
      // of buffer[6] to represent decimal number 4 (UUID version number).
      buffer[6] |= 0x40; //buffer[6] | 01000000 will set the 6 bit to 1.
      buffer[6] &= 0x4f; //buffer[6] & 01001111 will set the 4, 5, and 7 bit to 0 such that bits 4-7 == 0100 = '4'.
      // buffer[8] represents the clock_seq_hi_and_reserved field. We will set the two most significant bits (6 and 7) of the
      // clock_seq_hi_and_reserved to zero and one, respectively.
      buffer[8] |= 0x80; //buffer[8] | 10000000 will set the 7 bit to 1.
      buffer[8] &= 0xbf; //buffer[8] & 10111111 will set the 6 bit to 0.
      return (
        this._decimalToHex(buffer[0]) +
        this._decimalToHex(buffer[1]) +
        this._decimalToHex(buffer[2]) +
        this._decimalToHex(buffer[3]) +
        '-' +
        this._decimalToHex(buffer[4]) +
        this._decimalToHex(buffer[5]) +
        '-' +
        this._decimalToHex(buffer[6]) +
        this._decimalToHex(buffer[7]) +
        '-' +
        this._decimalToHex(buffer[8]) +
        this._decimalToHex(buffer[9]) +
        '-' +
        this._decimalToHex(buffer[10]) +
        this._decimalToHex(buffer[11]) +
        this._decimalToHex(buffer[12]) +
        this._decimalToHex(buffer[13]) +
        this._decimalToHex(buffer[14]) +
        this._decimalToHex(buffer[15])
      );
    } else {
      var guidHolder = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
      var hex = '0123456789abcdef';
      var r = 0;
      var guidResponse = '';
      for (var i = 0; i < 36; i++) {
        if (guidHolder[i] !== '-' && guidHolder[i] !== '4') {
          // each x and y needs to be random
          r = (Math.random() * 16) | 0;
        }
        if (guidHolder[i] === 'x') {
          guidResponse += hex[r];
        } else if (guidHolder[i] === 'y') {
          // clock-seq-and-reserved first hex is filtered and remaining hex values are random
          r &= 0x3; // bit and with 0011 to set pos 2 to zero ?0??
          r |= 0x8; // set pos 3 to 1 as 1???
          guidResponse += hex[r];
        } else {
          guidResponse += guidHolder[i];
        }
      }
      return guidResponse;
    }
  };
  /* jshint ignore:end */

  /**
   * Calculates the expires in value in milliseconds for the acquired token
   * @ignore
   */
  AuthenticationContext.prototype._expiresIn = function (expires) {
    // if AAD did not send 'expires_in' property, use default expiration of 3599 seconds, for some reason AAD sends 3599 as 'expires_in' value instead of 3600
    if (!expires) {
      expires = 3599;
    }
    return this._now() + parseInt(expires, 10);
  };

  /**
   * Return the number of milliseconds since 1970/01/01
   * @ignore
   */
  AuthenticationContext.prototype._now = function () {
    return Math.round(new Date().getTime() / 1000.0);
  };

  /**
   * Adds the hidden iframe for silent token renewal
   * @ignore
   */
  AuthenticationContext.prototype._addAdalFrame = function (iframeId) {
    if (typeof iframeId === 'undefined') {
      return null;
    }

    this.verbose('Add adal frame to document:' + iframeId);
    var adalFrame = document.getElementById(iframeId);

    if (!adalFrame) {
      if (document.createElement && document.documentElement && (window.opera || window.navigator.userAgent.indexOf('MSIE 5.0') === -1)) {
        var ifr = document.createElement('iframe');
        ifr.setAttribute('id', iframeId);
        ifr.setAttribute('name', 'disable-x-frame-options');
        ifr.style.visibility = 'hidden';
        ifr.style.position = 'absolute';
        ifr.style.width = ifr.style.height = ifr.borderWidth = '0px';

        adalFrame = document.getElementsByTagName('body')[0].appendChild(ifr);
      } else if (document.body && document.body.insertAdjacentHTML) {
        document.body.insertAdjacentHTML('beforeEnd', '<iframe name="' + iframeId + '" id="' + iframeId + '" style="display:none"></iframe>');
      }
      if (window.frames && window.frames[iframeId]) {
        adalFrame = window.frames[iframeId];
      }
    }

    return adalFrame;
  };

  AuthenticationContext.prototype._saveItemToStorage = function (storage, key, obj) {
    var cryptedKey = key + '.cr';
    try {
      if (this.config.encryptionKey) {
        storage.setItem(cryptedKey, CryptoJS.AES.encrypt(obj, this.config.encryptionKey));
        storage.removeItem(key);
        return storage.getItem(cryptedKey);
      } else {
        this.verbose('_setItem ' + key + ' NOT encrypted');
        storage.setItem(key, obj);
        storage.removeItem(cryptedKey);
        return storage.getItem(key);
      }
    } catch (e) {
      this.verbose('_saveItemToStorage: CryptoJS thrown an exception. item: ' + obj + 'key: ' + this.config ? this.config.encryptionKey : 'config is NULL');
    }
    return null;
  };

  /**
   * Saves the key-value pair in the cache
   * @ignore
   */
  AuthenticationContext.prototype._saveItem = function (key, obj) {
    if (this.config && this.config.cacheLocation && this.config.cacheLocation.match(/localstorage/i)) {
      if (!this._supportsLocalStorage()) {
        this.error('Local storage is not supported');
        return false;
      }
      return this._saveItemToStorage(localStorage, key, obj);
    }

    // Default as session storage
    if (!this._supportsSessionStorage()) {
      this.error('Session storage is not supported');
      return false;
    }
    return this._saveItemToStorage(sessionStorage, key, obj);
  };

  AuthenticationContext.prototype._getCryptedItem = function (storage, key) {
    var cryptedKey = key + '.cr';
    var item = localStorage.getItem(cryptedKey);
    if (!item) {
      item = localStorage.getItem(key);
      if (item) {
        item = this._saveItemToStorage(storage, key, item);
      }
    }
    return item;
  };

  /**
   * Searches the value for the given key in the cache
   * @ignore
   */
  AuthenticationContext.prototype._getItem = function (key) {
    var item = null;
    if (this.config && this.config.cacheLocation && this.config.cacheLocation.match(/localstorage/i)) {
      if (!this._supportsLocalStorage()) {
        this.verbose('Local storage is not supported');
        return null;
      }
      item = this._getCryptedItem(localStorage, key);
    } else if (!this._supportsSessionStorage()) {
      this.verbose('Session storage is not supported');
      return null;
    } else {
      item = this._getCryptedItem(sessionStorage, key);
    }

    try {
      if (this.config.encryptionKey && item) {
        return CryptoJS.AES.decrypt(item, this.config.encryptionKey).toString(CryptoJS.enc.Utf8);
      } else {
        return item;
      }
    } catch (e) {
      this.verbose('_getItem: CryptoJS thrown an exception. item: ' + item + 'key: ' + this.config && !!this.config.encryptionKey);
      return null;
    }
  };

  /**
   * Returns true if browser supports localStorage, false otherwise.
   * @ignore
   */
  AuthenticationContext.prototype._supportsLocalStorage = function () {
    try {
      var supportsLocalStorage = 'localStorage' in window && window.localStorage;
      if (supportsLocalStorage) {
        window.localStorage.setItem('storageTest', '');
        window.localStorage.removeItem('storageTest');
      }
      return supportsLocalStorage;
    } catch (e) {
      return false;
    }
  };

  /**
   * Returns true if browser supports sessionStorage, false otherwise.
   * @ignore
   */
  AuthenticationContext.prototype._supportsSessionStorage = function () {
    try {
      var supportsSessionStorage = 'sessionStorage' in window && window.sessionStorage;
      if (supportsSessionStorage) {
        window.sessionStorage.setItem('storageTest', '');
        window.sessionStorage.removeItem('storageTest');
      }
      return supportsSessionStorage;
    } catch (e) {
      return false;
    }
  };

  AuthenticationContext.prototype._sendPostHttpRequest = function (url, data, cb) {
    try {
      var xmlHttp = new XMLHttpRequest();
      xmlHttp.open('POST', url, true);
      xmlHttp.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');

      var onRequestFailed = function () {
        var err =
          'Failed to renew token for url=' +
          url +
          ', status = ' +
          xmlHttp.status +
          ', statusText = ' +
          xmlHttp.statusText +
          ', responseType = ' +
          xmlHttp.responseType +
          ', responseText = ' +
          xmlHttp.responseText;
        cb(null, err);
      };

      xmlHttp.onreadystatechange = function () {
        if (xmlHttp.readyState !== 4) {
          return;
        }
        if (xmlHttp.status === 200) {
          cb && cb(xmlHttp.responseText);
          return;
        }
        if (xmlHttp.status === 400 || xmlHttp.status === 401) {
          onRequestFailed();
        }
      };
      sendRequest(xmlHttp, data);
    } catch (e) {
      this.error('Error sending httprequest: ' + e);
    }
  };

  /**
   * Returns a cloned copy of the passed object.
   * @ignore
   */
  AuthenticationContext.prototype._cloneConfig = function (obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    var copy = {};
    for (var attr in obj) {
      if (obj.hasOwnProperty(attr)) {
        copy[attr] = obj[attr];
      }
    }
    return copy;
  };

  /**
   * Adds the library version and returns it.
   * @ignore
   */
  AuthenticationContext.prototype._addLibMetadata = function () {
    // x-client-SKU
    // x-client-Ver
    return '&x-client-SKU=Js&x-client-Ver=' + this._libVersion();
  };

  /**
   * Checks the Logging Level, constructs the Log message and logs it. Users need to implement/override this method to turn on Logging.
   * @param {number} level  -  Level can be set 0,1,2 and 3 which turns on 'error', 'warning', 'info' or 'verbose' level logging respectively.
   * @param {string} message  -  Message to log.
   * @param {string} error  -  Error to log.
   */
  AuthenticationContext.prototype.log = function (level, message, error) {
    var formattedMessage = '';
    if (this.config.logging && level <= this.config.logging.level) {
      formattedMessage = message + (error && error.stack ? '\nstack:\n' + error.stack : '');

      switch (level) {
        case this.CONSTANTS.LOGGING_LEVEL.ERROR:
          this.config.logging.log.error('[AdalJS]: ', formattedMessage);
          break;
        case this.CONSTANTS.LOGGING_LEVEL.WARN:
          this.config.logging.log.warn('[AdalJS]: ', formattedMessage);
          break;
        case this.CONSTANTS.LOGGING_LEVEL.INFO:
          this.config.logging.log.info('[AdalJS]: ', formattedMessage);
          break;
        case this.CONSTANTS.LOGGING_LEVEL.VERBOSE:
        default:
          this.config.logging.log.debug('[AdalJS]: ', formattedMessage);
          break;
      }
    } else if (level <= window.Logging.level) {
      var timestamp = new Date().toUTCString();

      if (this.config.correlationId) {
        formattedMessage =
          timestamp + ':' + this.config.correlationId + '-' + this._libVersion() + '-' + this.CONSTANTS.LEVEL_STRING_MAP[level] + ' ' + message;
      } else {
        formattedMessage = timestamp + ':' + this._libVersion() + '-' + this.CONSTANTS.LEVEL_STRING_MAP[level] + ' ' + message;
      }

      if (error) {
        formattedMessage += '\nstack:\n' + error.stack;
      }

      window.Logging.log(formattedMessage);
    }
  };

  /**
   * Logs messages when Logging Level is set to 0.
   * @param {string} message  -  Message to log.
   * @param {string} error  -  Error to log.
   */
  AuthenticationContext.prototype.error = function (message, error) {
    this.log(this.CONSTANTS.LOGGING_LEVEL.ERROR, message, error);
  };

  /**
   * Logs messages when Logging Level is set to 1.
   * @param {string} message  -  Message to log.
   */
  AuthenticationContext.prototype.warn = function (message) {
    this.log(this.CONSTANTS.LOGGING_LEVEL.WARN, message, null);
  };

  /**
   * Logs messages when Logging Level is set to 2.
   * @param {string} message  -  Message to log.
   */
  AuthenticationContext.prototype.info = function (message) {
    this.log(this.CONSTANTS.LOGGING_LEVEL.INFO, message, null);
  };

  /**
   * Logs messages when Logging Level is set to 3.
   * @param {string} message  -  Message to log.
   */
  AuthenticationContext.prototype.verbose = function (message) {
    this.log(this.CONSTANTS.LOGGING_LEVEL.VERBOSE, message, null);
  };

  /**
   * Returns the library version.
   * @ignore
   */
  AuthenticationContext.prototype._libVersion = function () {
    return '1.0.14';
  };

  /**
   * Returns a reference of Authentication Context as a result of a require call.
   * @ignore
   */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = AuthenticationContext;
    module.exports.inject = function (conf) {
      return new AuthenticationContext(conf);
    };
  }

  return AuthenticationContext;
})();

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  /**
   * A UnifyPhone error object derived from the JavaScript error object.
   *
   * @class Error
   * @constructor
   * @param {String} code Error code
   * @param {String|Object} err The error message, or a detailed error object.
   */
  unifyPhone.Error = function (code, err) {
    this.code = code || 'UNEXPECTED_ERROR';

    if (typeof err === 'string') {
      this.message = err;
    } else if (typeof err === 'object') {
      this.errObj = err;
      if (err.info && !err.message) {
        err.message = err.info;
      }
    }
    this.stack = Error().stack;
  };
  unifyPhone.Error.prototype = Object.create(Error.prototype);
  unifyPhone.Error.prototype.name = 'Error';

  /**
   * Error message
   *
   * @property message
   * @type String
   */

  /**
   * Error code as defined in UnifyPhone.Constants.ReturnCode or UnifyPhone.Constants.ErrorCode.
   * E.g. `UNEXPECTED_ERROR`
   *
   * @property code
   * @type String
   */

  /**
   * Detailed error object containing detailed information that can be used when debugging. The object
   * is different depending on the type of error.
   *
   * @property errObj
   * @type Object
   */
  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Define the object
  var storage = null;

  try {
    storage = window.localStorage || storage;
  } catch (e) {
    // If access to the localStorage is denied use sessionStorage
    // This fixes problem with IE on Windows 8
    storage = window.sessionStorage || storage;
  }

  function invokeMethod(method, args) {
    try {
      return storage ? storage[method].apply(storage, args) : null;
    } catch (e) {
      return null;
    }
  }

  var storeManager = {
    getItem: function (key) {
      return invokeMethod('getItem', [key]);
    },
    setItem: function (key, value) {
      return invokeMethod('setItem', [key, value]);
    },
    removeItem: function (key) {
      return invokeMethod('removeItem', [key]);
    }
  };

  // Exports
  unifyPhone.storeManager = storeManager;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
// Define external globals for JSHint

/*global module*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Handle object that had to be written in log files.
  var LogObfuscator = function () {
    ///////////////////////////////////////////////////////////////////////////
    // Local variables
    ///////////////////////////////////////////////////////////////////////////
    var OMMIT_KEY = 'keysToOmitFromLogging';

    ///////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////
    function shallowCopy(src) {
      if (!src || typeof src !== 'object') {
        return src;
      }

      if (Array.isArray(src)) {
        return src.slice();
      }

      var obj = {};
      for (var key in src) {
        if (src.hasOwnProperty(key)) {
          obj[key] = src[key];
        }
      }
      return obj;
    }

    function validatePaths(obj, paths) {
      var subPaths;
      var idx = paths.indexOf('[]');
      if (idx !== -1 && idx < paths.length - 1) {
        // Do not use splice since that would affect the original paths array
        subPaths = paths.slice(idx + 1);
        paths = paths.slice(0, idx + 1);
      }
      return paths.every(function (node) {
        if (node === '[]') {
          if (!Array.isArray(obj)) {
            return false;
          }
          if (!subPaths) {
            return true;
          }
          return obj.some(function (elem) {
            return validatePaths(elem, subPaths);
          });
        }
        obj = obj[node];
        return !!obj;
      });
    }

    function ommitKeys(obj) {
      if (!obj || typeof obj !== 'object' || !obj.hasOwnProperty(OMMIT_KEY)) {
        return obj;
      }
      var origObj = obj; // Keep a reference to the original object
      obj = shallowCopy(obj); // Create a shallow copy for the modifications
      origObj[OMMIT_KEY].forEach(function (key) {
        var paths = key.split('.');
        if (!validatePaths(obj, paths)) {
          return;
        }
        var root = obj;
        var origRoot = origObj;
        var lastIdx = paths.length - 1;
        paths.every(function (node, idx) {
          if (node === '[]') {
            if (Array.isArray(root)) {
              var subKey = idx < lastIdx ? paths.slice(idx + 1).join('.') : null;
              root.forEach(function (origElem, elemIdx) {
                if (!subKey) {
                  root[elemIdx] = '******';
                  return;
                }
                origElem[OMMIT_KEY] = [subKey];
                root[elemIdx] = ommitKeys(origElem);
                delete origElem[OMMIT_KEY];
              });
            }
            return false;
          }
          if (!root[node]) {
            // The key to be omitted does not exist
            return false;
          }
          if (idx === lastIdx) {
            // This is the last element
            root[node] = '******';
          } else {
            if (typeof root[node] !== 'object') {
              // Cannot continue
              return false;
            }

            // Shallow copy only if have not done so
            if (root[node] === origRoot[node]) {
              root[node] = shallowCopy(root[node]);
            }
            // Navigate to the next level
            origRoot = origRoot[node];
            root = root[node];
          }
          return true;
        });
      });
      // Remove the OMMIT_KEY property from the copy
      delete obj[OMMIT_KEY];
      return obj;
    }

    ///////////////////////////////////////////////////////////////////////////
    // Public interface
    ///////////////////////////////////////////////////////////////////////////
    /**
     * Transform object to string that ommit not required keys from logging.
     *
     * @param {Object} obj: object that had to be logged
     * @param {Function} replacerCb: A function that alters the behavior of the stringification process
     * @returns {string} String that will be added to log.
     */
    this.objToString = function (obj, replacerCb) {
      if (obj === undefined) {
        return '';
      }
      if (!obj) {
        return obj;
      }
      if (typeof obj === 'function') {
        return '[function]';
      }
      if (typeof obj !== 'object') {
        return obj;
      }

      var replacer = typeof replacerCb === 'function' ? replacerCb : null;

      obj = ommitKeys(obj);
      return JSON.stringify(obj, replacer, 3).replace(/\\r\\n/g, '\x0A');
    };
  };

  if (typeof module !== 'undefined' && module.exports) {
    // Supports exports (CommonJS)
    module.exports = new LogObfuscator();
  }
  unifyPhone.LogObfuscator = new LogObfuscator();

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global log4javascript*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var storeManager = unifyPhone.storeManager;
  var logObfuscator = unifyPhone.LogObfuscator;

  var LogLevel = Object.freeze({
    MAXIMUM: { level: log4javascript.Level.DEBUG.level, name: 'Maximum' },
    MEDIUM: { level: log4javascript.Level.INFO.level, name: 'Medium' },
    MINIMUM: { level: log4javascript.Level.WARN.level, name: 'Minimum' },
    ERROR: { level: log4javascript.Level.ERROR.level, name: 'Error' }
  });

  function BaseLogger() {
    ///////////////////////////////////////////////////////////////////////////
    // Local variables
    ///////////////////////////////////////////////////////////////////////////
    var LOG_STORAGE_KEY = 'loggingLevel';

    var _url = '';
    var _clientLogger = new log4javascript.getLogger();
    var _logLevel = LogLevel.MAXIMUM;

    ///////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////
    // Get log level from localStorage
    function retrieveLogLevel() {
      try {
        var levelTag = storeManager.getItem(LOG_STORAGE_KEY);
        if (levelTag) {
          if (LogLevel[levelTag]) {
            _logLevel = LogLevel[levelTag];
            return;
          }
          // Something is not right with the current entry
          storeManager.removeItem(LOG_STORAGE_KEY);
        }
      } catch (e) {}

      // Fallback to default
      _logLevel = LogLevel.MAXIMUM;
    }

    function init() {
      // Set the level for the log4javascript object to DEBUG
      // The control between MAXIMUM, MEDIUM, MINIMUM and ERROR is done locally.
      _clientLogger.setLevel(log4javascript.Level.DEBUG);
      retrieveLogLevel();
    }

    function checkLevel(level) {
      if (_logLevel === LogLevel.ERROR) {
        return false;
      }

      if (_url !== location.href) {
        // Current URL has changed. Log an entry for that independent of log level
        _url = location.href;
        _clientLogger.info('Current URL has changed to ', _url);
      }

      return level.level >= _logLevel.level;
    }

    ///////////////////////////////////////////////////////////////////////////
    // Public interface
    ///////////////////////////////////////////////////////////////////////////
    this.getLevel = function () {
      return _logLevel;
    };

    this.setLevel = function (newLevel) {
      if (!newLevel || !newLevel.level || newLevel.level === _logLevel.level) {
        return;
      }
      Object.keys(LogLevel).some(function (key) {
        if (newLevel.level === LogLevel[key].level) {
          _logLevel = LogLevel[key];
          storeManager.setItem(LOG_STORAGE_KEY, key);
          _clientLogger.info('[logger]: Logging level has been updated to: ', key);
          return true;
        }
        return false;
      });
    };

    this.debug = function (txt, obj) {
      if (checkLevel(LogLevel.MAXIMUM)) {
        _clientLogger.debug(txt, logObfuscator.objToString(obj));
      }
    };

    this.info = function (txt, obj) {
      if (checkLevel(LogLevel.MEDIUM)) {
        _clientLogger.info(txt, logObfuscator.objToString(obj));
      }
    };

    this.msgSend = function (txt, msg) {
      if (checkLevel(LogLevel.MINIMUM)) {
        _clientLogger.info('SEND: ' + txt, logObfuscator.objToString(msg));
      }
    };

    this.msgRcvd = function (txt, msg) {
      if (checkLevel(LogLevel.MINIMUM)) {
        _clientLogger.info('RECV: ' + txt, logObfuscator.objToString(msg));
      }
    };

    this.warning = function (txt, obj) {
      if (checkLevel(LogLevel.MINIMUM)) {
        _clientLogger.warn(txt, logObfuscator.objToString(obj));
      }
    };

    this.warn = this.warning;

    this.error = function (error, obj) {
      // Always log errors
      error = (error && error.stack) || error;
      obj = (obj && obj.stack) || obj;

      var data = logObfuscator.objToString(obj);
      _clientLogger.error(error, data);
    };

    this.logMsg = function (level, messages) {
      if (messages && checkLevel(level)) {
        // Generic log message function, useful when you have the log msg level
        // as a parameter.
        if (!Array.isArray(messages)) {
          messages = [messages];
        }
        var levelObj = log4javascript.Level.DEBUG;
        if (level && level.level && level.name) {
          levelObj = new log4javascript.Level(level.level, level.name);
        }
        _clientLogger.log(
          levelObj,
          messages.map(function (obj) {
            return logObfuscator.objToString(obj);
          })
        );
      }
    };

    this.removeAllAppenders = function () {
      _clientLogger.removeAllAppenders();
    };

    this.addAppender = function (appender) {
      _clientLogger.addAppender(appender);
    };

    this.removeAppender = function (appender) {
      _clientLogger.removeAppender(appender);
    };

    ///////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////
    init();
  }

  // Exports
  unifyPhone.BaseLogger = BaseLogger;
  unifyPhone.LogLevel = unifyPhone.LogLevel || LogLevel;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/* global log4javascript */

/* eslint-disable no-console */

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var BaseLogger = unifyPhone.BaseLogger;
  var storeManager = unifyPhone.storeManager;

  // eslint-disable-next-line max-lines-per-function
  function Logger() {
    // NOSONAR
    // Singleton pattern
    if (Logger.prototype._singletonInstance) {
      return Logger.prototype._singletonInstance;
    }
    Logger.prototype._singletonInstance = this;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var ENABLE_POPUP_LOG_KEY = 'enablePopupLog';
    var REQUEST_FILE_SYSTEM_ERROR = 'REQUEST_FILE_SYSTEM_ERROR';

    ///////////////////////////////////////////////////////////////////////////////////////
    // Worker Thread
    ///////////////////////////////////////////////////////////////////////////////////////
    var LogFileWorkerMsgType = {
      // This must be in synch with the definition in logFileWorker.js
      START: 'start',
      LOG: 'log',
      GET_LOG_FILES: 'getLogFiles',
      REMOVE_LOG_FILES: 'removeLogFiles',
      USER_INFO: 'userInfo',
      EXTENSION_VERSION: 'extensionVersion',
      LOG_ERROR: 'logError'
    };

    var _logFileWorker = new Worker('./logFileWorker.js');

    ///////////////////////////////////////////////////////////////////////////
    // Local variables
    ///////////////////////////////////////////////////////////////////////////
    var _that = this;
    var _popAppender = null;
    var _isFileLogAvailable = true;

    ///////////////////////////////////////////////////////////////////////////////////////
    // File Appender
    ///////////////////////////////////////////////////////////////////////////////////////
    function FileAppender() {}
    FileAppender.prototype = new log4javascript.Appender();
    FileAppender.prototype.layout = new log4javascript.PatternLayout('%d{yy-MM-dd HH:mm:ss.SSS} %p %m');
    FileAppender.prototype.threshold = log4javascript.Level.TRACE;
    FileAppender.prototype.append = function (loggingEvent) {
      var appender = this;

      var getFormattedMessage = function () {
        var layout = appender.getLayout();
        var formattedMessage = layout.format(loggingEvent);
        if (layout.ignoresThrowable() && loggingEvent.exception) {
          formattedMessage += loggingEvent.getThrowableStrRep();
        }
        return formattedMessage;
      };

      var formattedMessage = getFormattedMessage();
      appender.writeToFile(formattedMessage);
    };
    FileAppender.prototype.startLogger = function (isGuest, clientVersion, isC4O365) {
      _logFileWorker &&
        _logFileWorker.postMessage({
          type: LogFileWorkerMsgType.START,
          isGuest: !!isGuest,
          isC4O365: !!isC4O365,
          clientVersion: clientVersion
        });
    };
    FileAppender.prototype.writeToFile = function (message) {
      // Send log message to the worker thread (asynchronously)
      _logFileWorker &&
        _logFileWorker.postMessage({
          type: LogFileWorkerMsgType.LOG,
          log: message
        });
    };
    FileAppender.prototype.errorHandler = function (e) {
      console.log('File appender error: ', e.toString()); //NOSONAR
    };
    FileAppender.prototype.removeLogFiles = function (cb) {
      _logFileWorker &&
        _logFileWorker.postMessage({
          type: LogFileWorkerMsgType.REMOVE_LOG_FILES
        });
      this.getLogFilesCb = cb;
    };
    FileAppender.prototype.getLogFiles = function (cb) {
      if (!_isFileLogAvailable) {
        cb && cb([]);
        return;
      }
      _logFileWorker &&
        _logFileWorker.postMessage({
          type: LogFileWorkerMsgType.GET_LOG_FILES
        });
      this.getLogFilesCb = cb;
    };
    FileAppender.prototype.setUserInfo = function (userInfo, isCachingDisabled) {
      _logFileWorker &&
        _logFileWorker.postMessage({
          type: LogFileWorkerMsgType.USER_INFO,
          userInfo: userInfo,
          isCachingDisabled: !!isCachingDisabled
        });
    };
    FileAppender.prototype.setExtensionVersion = function (extensionVersion) {
      _logFileWorker &&
        _logFileWorker.postMessage({
          type: LogFileWorkerMsgType.EXTENSION_VERSION,
          extensionVersion: extensionVersion
        });
    };
    FileAppender.prototype.toString = function () {
      return 'FileAppender';
    };
    log4javascript.FileAppender = FileAppender;

    var _fileAppender = new log4javascript.FileAppender();

    ///////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////
    function addPopupAppender() {
      if (!_popAppender) {
        storeManager.setItem(ENABLE_POPUP_LOG_KEY, JSON.stringify(true));

        _popAppender = new log4javascript.PopUpAppender();
        var popLayout = new log4javascript.PatternLayout('%d{yyyy MMM dd HH:mm:ss,SSS} %p %m');

        _popAppender.setLayout(popLayout);
        _popAppender.setInitiallyMinimized(true);
        _popAppender.setMaxMessages(5000);
        _popAppender.setUseOldPopUp(false);

        _that.addAppender(_popAppender);
      }
    }

    function removePopupAppender() {
      if (_popAppender) {
        storeManager.setItem(ENABLE_POPUP_LOG_KEY, JSON.stringify(false));

        _popAppender.close();
        _that.removeAppender(_popAppender);
        _popAppender = null;
      }
    }

    function initAppenders() {
      _that.removeAllAppenders();

      // Add file appender
      _that.addAppender(_fileAppender);

      // Check if we are supposed to enable the popup appender
      var enablePopup = storeManager.getItem(ENABLE_POPUP_LOG_KEY);
      if (enablePopup && JSON.parse(enablePopup)) {
        addPopupAppender();
      }
    }

    ///////////////////////////////////////////////////////////////////////////
    // Event handlers
    ///////////////////////////////////////////////////////////////////////////
    // Interface to receive asynchronous data from _logFileWorker
    _logFileWorker.addEventListener(
      'message',
      function (e) {
        var data = e.data;

        if (data.logFiles) {
          if (_fileAppender.getLogFilesCb) {
            _fileAppender.getLogFilesCb(data.logFiles);
          }
          _fileAppender.getLogFilesCb = null;
        }
        if (data.error) {
          console.log('logFileWorker: ', data.error); //NOSONAR
          if (data.error.indexOf(REQUEST_FILE_SYSTEM_ERROR) !== -1) {
            _isFileLogAvailable = false;
            _logFileWorker.terminate();
            _logFileWorker = null;
          }
        }
      },
      false
    );

    // Terminate the worker when user reloads the page.
    window.addEventListener('beforeunload', function () {
      _logFileWorker && _logFileWorker.terminate();
    });

    ///////////////////////////////////////////////////////////////////////////
    // Public interface
    ///////////////////////////////////////////////////////////////////////////
    this.show = function () {
      _popAppender && _popAppender.show();
    };

    this.hide = function () {
      _popAppender && _popAppender.hide();
    };

    this.enablePopup = function () {
      addPopupAppender();
    };

    this.disablePopup = function () {
      removePopupAppender();
    };

    this.isPopupEnabled = function () {
      return !!_popAppender;
    };

    this.isFileLogAvailable = function () {
      return _isFileLogAvailable;
    };

    this.startFileLogger = function (isGuest, clientVersion, isC4O365) {
      _fileAppender.startLogger(isGuest, clientVersion, isC4O365);
    };

    this.getLogFiles = function (cb) {
      _fileAppender.getLogFiles(cb);
    };

    this.removeLogFiles = function (cb) {
      _fileAppender.removeLogFiles(cb);
    };

    this.setUserInfo = function (userInfo, isCachingDisabled) {
      _fileAppender.setUserInfo(userInfo, isCachingDisabled);
    };

    this.setExtensionVersion = function (extensionVersion) {
      _fileAppender.setExtensionVersion(extensionVersion);
    };

    ///////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////
    initAppenders();

    return this;
  }
  Logger.prototype = new BaseLogger();
  Logger.prototype.constructor = Logger;

  // Exports
  // In case a logger is already defined keep that one
  unifyPhone.Logger = unifyPhone.Logger || Logger;
  unifyPhone.logger = unifyPhone.logger || new Logger();

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global __productName, __productBrand, libphonenumber, process, require, he*/

// eslint-disable-next-line complexity
var UnifyPhone = (function (unifyPhone) {
  // NOSONAR
  'use strict';

  // Variables used for electron app and for cross domain registration.
  unifyPhone.__server = '';
  unifyPhone.__domain = '';
  unifyPhone.__path = '';
  unifyPhone.__wsOverrideImpl = null;

  // Imports
  var logger = unifyPhone.logger;

  unifyPhone.productName = typeof __productName === 'string' ? __productName : 'UnifyPhone';
  unifyPhone.productBrand = typeof __productBrand === 'string' ? __productBrand : '';

  // Constants
  var HTTPS = 'https://';
  var EMOTICON_REGEX = /<(img[^>|<]*(?:shortcut|abbr)="(\S*)"[^/?>]*\/?)>/gi;
  // SSL proxy to overcome mixed-content warning due to loading unsecure preview images
  var SSL_IMAGE_PROXY = 'https://embed-proxy.unifyPhone.com/?target=';
  var MAX_NUM_OF_PREFERRED_DEVICES = 5;

  /**
   * Static utility functions
   * @class Utils
   * @static
   */
  var Utils = unifyPhone.Utils || {};

  // Returns set of symbols that is used for url regular expressions
  function getUrlSymbols() {
    var urlSymbolLast = "a-z\\u00a1-\\uffff0-9@'^=$%&amp;\\/~+_\\{\\}\\-\\|";
    var urlSymbol = urlSymbolLast + '.,?!:\\[\\]';
    return '(?:[' + urlSymbol + '#]*[' + urlSymbolLast + '#]|\\([' + urlSymbol + ']*\\))*';
  }
  var URL_SYMBOLS = getUrlSymbols();

  // Conversation fields which need to be trimmed from the conversation objects
  // sent to the mobile clients.
  var TRIM_FOR_MOBILE_GET_LISTS = ['items', 'parents', 'peerUsers', 'previousMembers'];

  Utils.trimCallForMobile = function (call) {
    var mobileCall;
    if (call) {
      mobileCall = Utils.flattenObj(call);
      // TODO: Need to enhance iOS and Android apps before we can trim the fields.
      // TRIM_FOR_MOBILE_GET_LISTS.forEach(function (field) {
      //     delete mobileCall[field];
      // });
    }
    return mobileCall;
  };

  Utils.trimConvForMobile = function (conv) {
    var mobileConv;
    if (conv) {
      mobileConv = Utils.flattenObj(conv);
      TRIM_FOR_MOBILE_GET_LISTS.forEach(function (field) {
        delete mobileConv[field];
      });
      mobileConv.call = Utils.trimCallForMobile(conv.call);
    }
    return mobileConv;
  };

  Utils.isPromise = function (obj) {
    return !!(obj && typeof obj.then === 'function');
  };

  /**
   * Converts the given version string into an version number.
   *
   * @param {String} version The client version in a 'x.xx.xxxx[-SNAPSHOT]' format.
   */
  Utils.convertVersionToNumber = function (version) {
    if (typeof version !== 'string') {
      return 0;
    }
    var splitVersion = version.split('.');
    var release = parseInt(splitVersion[0], 10) || 0;
    var major = parseInt(splitVersion[1], 10) || 0;
    major = Math.min(major, 99);
    var minor = parseInt(splitVersion[2], 10) || 0;
    minor = Math.min(minor, 9999);

    return minor + major * 1e4 + release * 1e6;
  };

  /**
   * Trims a text, given a selected length, from beginning, adding ellipsis.
   */
  Utils.trimTextWithEmoticonsBeforeMatch = function (text, textToMatch, maxCharactersBeforeMatch) {
    var PREFIX = '...';
    var matchedIndex = text.indexOf(textToMatch);
    // contains the matched term and the text on its right
    var afterMatchingPart = text.slice(matchedIndex);
    // text before matchmatchedIndex
    var beforeMatchingPart = text.slice(0, matchedIndex);

    var LT = String.fromCharCode(17); // Temporary mapping using non-printable ascii code x11 (DEC 17)
    var GT = String.fromCharCode(18); // Temporary mapping using non-printable ascii code x12 (DEC 18)
    var content = beforeMatchingPart.replace(EMOTICON_REGEX, function (a, b) {
      return LT + b + GT;
    });

    // We run the beforeMatchingText in inverse order so we can locate
    // the non printable ending character
    var isCounting = true;
    var trimingIndex = content.length - 1;
    var loopIndex = content.length - 1;
    var splitIndex = loopIndex;

    // We iterate the String from end. We skip characters that are between non-printable characters
    // and update non-printable chars on the fly, in order to generate a valid html with emoticons.
    while (beforeMatchingPart.length - 1 - trimingIndex <= maxCharactersBeforeMatch && loopIndex >= 0) {
      if (content.charCodeAt(loopIndex) === 18) {
        content = content.replaceAt(loopIndex, '>');
        isCounting = false;
      } else if (content.charCodeAt(loopIndex) === 17) {
        content = content.replaceAt(loopIndex, '<');
        isCounting = true;
      }

      splitIndex = loopIndex;
      loopIndex--;
      if (isCounting) {
        trimingIndex--;
      }
    }

    // Prefix will be added only in case we trim text from begining.
    beforeMatchingPart = splitIndex > 0 ? PREFIX + content.slice(splitIndex) : content;

    return beforeMatchingPart + afterMatchingPart;
  };

  /**
   * Removes restrictions from the encodeURIComponent() function
   */
  Utils.enhanceEncodeURI = function (s) {
    // mappings to replace '!,(,),#,{,},' with proper encoding
    var charactersMap = {
      '!': '%21',
      '(': '%28',
      ')': '%29',
      '#': '%23',
      '{': '%7B',
      '}': '%7D'
    };

    return encodeURIComponent(s).replace(/[!()#{}]/gi, function (matched) {
      return charactersMap[matched];
    });
  };

  Utils.normalizeDn = function (dn) {
    if (!dn) {
      return '';
    }
    return dn.replace('+', '');
  };

  Utils.cleanPhoneNumber = function (number) {
    return number ? number.replace(/[^*#\d+]/g, '') : '';
  };

  Utils.cleanMiVBPhoneNumber = function (number) {
    return number ? number.replace(/[^A-Za-z*#\d+.-]/g, '') : '';
  };

  Utils.cleanPhoneNumberDigitsWithPin = function (number) {
    return number ? number.replace(/[^*#\d+,]/g, '') : '';
  };

  /**
   * Checks the meta tags for a given Html input to determine if the html data source is a MS document.
   * Used when pasting in the conversation input.
   */
  Utils.isMSDoc = function (content) {
    // FF and Chrome paste tag with 'WordDocument like '<m:WordDocument>'.
    // IE pastes html with styles begin with 'mso-', e.g. '<b style="mso-bidi-font-weight: normal;">'.
    // From Microsoft OneNote html is pasted with tag <meta content="Microsoft OneNote">
    return content.search(/<\S*?WordDocument>/i) >= 0 || content.search(/<[^>]*?style=[^>]*?mso-[^<]+?>/i) >= 0 || content.search(/<meta[^>]*?Microsoft/i) >= 0;
  };

  /**
   * Checks the meta tags for a given Html input to determine if the html data source is a MS document.
   * Used when pasting in the conversation input.
   */
  Utils.isXCodeDoc = function (content) {
    // Contain meta tag with attribute contain="Cocoa HTML Writer"
    return content.search(/<meta[^>]*?Cocoa[^<]+?>/) >= 0;
  };

  var productionDomains = [
    'eu.yourcircuit.com',
    'na.yourcircuit.com',
    'beta.circuit.com',
    'circuit.siemens.com',
    'partner.circuit.siemens.com',
    'teams.univergeblue.com',
    'bluebeta.circuit.com',
    'gt-connect.circuit.atos-services.net',
    'uc.redsara.es'
  ];
  Utils.isProductionDomain = function () {
    var location = window.location.host;
    return productionDomains.indexOf(location) !== -1;
  };

  /**
   * Checks whether integrations is supported on the current domain.
   */
  var validIntegrationDomains = [
    'circuitdev.unify.com', // Development environment
    'phonedev.unify.com',
    'tandi.circuitsandbox.net',
    'adfs01.circuitsandbox.net',
    'circuitsandbox.net',
    'beta.circuit.com',
    'eu.yourcircuit.com',
    'na.yourcircuit.com',
    'gt-connect.circuit.atos-services.net',
    'uc.redsara.es'
  ];
  Utils.isIntegrationsSupported = function () {
    var location = window.location.hostname;
    return validIntegrationDomains.includes(location);
  };

  /**
   * HTML Sanitizer. For web only. Web needs to set this
   * to the Angular sanitizer.
   */
  Utils.sanitizer = null;

  /**
   * Sanitize function.
   * Bypass style attribute for Ansible rich text highlighting (not search highlighting)
   */
  Utils.sanitize = function (content) {
    if (!content || !Utils.sanitizer) {
      return content || '';
    }

    try {
      // Angular sanitize does not allow style tags, so replace it with a class.
      // NOTE: If the class name changes, be sure to also change in ConversationItemMessageCell.m for iOS client.
      // Used for post messages
      content = content.replace(/style="background-color: rgb\(212, 239, 181\);"/gi, 'class="rich-text-highlight"');

      // Used for pasting highlighted content
      content = content.replace(/<[^>]+background-color: rgb\(212, 239, 181\);.*?>/gi, function (matchTag) {
        if (matchTag.includes('class=')) {
          // Check if class attribute is already exists
          return matchTag.replace(/class=".*?"/gi, function (matchClass) {
            if (matchClass.includes('rich-text-highlight')) {
              // Return if highlight class is already exists
              return matchClass;
            } else {
              // Add highlight class
              return matchClass.replace(/[^=]\s*(.*)/gi, function (match) {
                return match.slice(0, -1) + ' rich-text-highlight';
              });
            }
          });
        } else {
          // If no class attribute exists, add highlight class to the end of the tag
          return matchTag.replace('>', function (match) {
            return ' class="rich-text-highlight" ' + match;
          });
        }
      });

      // Remove image tags that are not emoticon ones (emoticon ones are e.g. <img class="emoticon-icon smile" abbr=":)">
      // or emoji ones
      content = content.replace(/<img(?![^>]*class="(emoticon-icon|emojione-icon))[^>]*>/gi, '');

      // Remove <title> tag because sanitize extracts title content as text, which can't be identified and
      // removed later and can't be pasted inside content as well.
      content = content.replace(/<title[^>]*>([\s\S]*?)<\/title>/gi, '');

      // Remove <font> tags
      content = content.replace(/<font[^>]*>|<\/font>/gi, '');

      // Normalize <hr>,<hr></hr> to <hr/>
      content = content.replace(/(<\s*hr\s*><\s*\/hr\s*>|<\s*hr\s*>)/gi, '<hr/>');

      // Remove \n and \r inside tags
      content = content.replace(/(\r|\n)+(?=[^<]*?>)/gi, ' ');

      // If any \r or \n characters exist (due to paste actions etc, convert them to <br> so they can be handled properly)
      content = content.replace(/\r\n|\r|\n/g, '<br>');

      if (typeof Utils.sanitizer === 'function') {
        content = Utils.sanitizer(content);
      }

      // Tokenize HTML in div/p elements. Removes unnecessary divs, added from RICH TEXT editor, without losing/adding new lines.
      var tokens = content.split(/<\/?div.*?>|<\/?p.*?>/g).filter(function (token) {
        return !!token;
      });
      var tokensLen = tokens.length;

      var tempContent = '';
      if (tokensLen > 1) {
        // rich
        // Find only last <br> tag:
        // '...text<br>'
        // '...<b>text<br></b>'
        var lastBrTag = /<br>(?![\s\S]*<br>)(?=(<[^/][^>]*><\/[^>]*>)*$|(<\/[^>]*>)*$|$)/i;

        var curr, prev;
        for (var i = 0; i < tokensLen; i++) {
          // <br>'s in bold, italics are removed in sanitization, so we need to replace them with single <br>
          curr = tokens[i].replace(/((<span( class="[\S]*")*>)|(<b( class="[\S]*")*>)|(<i( class="[\S]*")*>))*(<br>|<br\/>)(<\/span>|<\/b>|<\/i>)*/g, '<br>');

          if (prev && !prev.endsWith('</ul>') && !prev.endsWith('</ol>') && !lastBrTag.test(prev)) {
            // The previous token does not end with a <br>, so we need to insert one
            tempContent += '<br>';
          }
          tempContent += curr;
          prev = curr;
        }
      } else {
        // simple
        tempContent = tokens[0];
      }
      content = tempContent;
    } catch (e) {
      logger.error('[Utils]: Error sanitizing content. ', e);
      logger.error('[Utils]: Unexpected content: ', content);
      return '';
    }

    return content || '';
  };

  Utils.sanitizeSymbols = function (content) {
    // Available only to mobile clients who have he.js library loaded
    if (!Utils.isMobile() || typeof he === 'undefined') {
      return content;
    }
    // Convert symbols to html entities so they can be read in a uniform way. Also we should send encoded escaped html content in the backend.
    // (but currently only emoji symbols need encoding)
    // Used HE encoder/decoder to support all possible symbol encoding, https://github.com/mathiasbynens/he
    // Other possible solution would be for mobiles to manually detect symbols when sending and convert them to html entities
    // e.g. not send ✌ (victory hand emoji character) but &#9996; Because newer emoji chars have more bytes and are combined with some non-printable
    // modificator characters (e.g. black victory hand) we need a library to detect all manually, so we use he.js
    // Encode only non-Ascii chars ('allowUnsafeSymbols = true' means leave &, <, >, ", ', and backtick unencoded if sent by mobiles)
    // and convert it using decimal html entity to be alligned with web client (default in he.js is hex - it works in either case)
    return content && he.encode(content, { allowUnsafeSymbols: true, decimal: true });
  };

  Utils.toPlainText = function (content, type) {
    if (content) {
      // If type parameter is omitted or is 'RICH', convert to plain
      if (!type || type === 'RICH') {
        var elem = document.createElement('div');
        elem.innerHTML = content.replace(/<(br[/]?|\/li|hr[/]?)>/gi, '&nbsp;');
        return elem.textContent;
      }
    }
    return content; // In any other case (type parameter set but not 'RICH' - e.g. 'PLAIN'), don't convert
  };

  // Truncates big file names and puts ellipsis in the middle
  Utils.truncateFileName = function (fileName) {
    if (!fileName || fileName.length <= 22) {
      return fileName;
    }
    return fileName.substring(0, 10) + ' ...' + fileName.substring(fileName.length - 8, fileName.length);
  };

  Utils.DEFAULT_HELP_URL = 'https://www.circuit.com/support';

  Utils.DEFAULT_FAQ_URL = 'https://www.circuit.com/unifyportalfaqdetail';

  // Symbols that are used to separate words, including white space characters.
  Utils.SEPARATORS_PATTERN = '!-/:-@[-^{-~\\s';

  // Pattern for numbers only (used in Cloud Telephony)
  Utils.NUMERIC_PATTERN = '^[0-9]*$';

  // Pattern for numbers with * and + signs at the beginning of number(used in Cloud Telephony)
  Utils.NUMERIC_SIGN_PATTERN = '^[*+]?[0-9]*$';

  // AlphaNumeric Pattern
  Utils.SSO_ISSUER_PATTERN = '^[a-zA-Z0-9@#&_-]*$';

  // Patern for unicode letters, numbers, spaces and specific special characters
  Utils.TENANT_PATTERN = new RegExp('^[\\p{L}0-9\\s\\+\\.,/@#&_-]*$', 'gu');

  // Patern for unicode letters, numbers, spaces and specific special characters
  Utils.IDP_ALIAS_PATTERN = new RegExp('^[\\p{L}0-9_-]*$', 'gu');

  // Patern for unicode letters, numbers, spaces and specific special characters
  Utils.IDP_ENTITY_ID_PATTERN = new RegExp('^[\\p{L}0-9\\s\\./_:%-]*$', 'gu');

  // Helper pattern for finding urls.
  Utils.URL_SEPARATORS_PATTERN = /(?=[.,;!]?\s+|[.,;!]$)/;

  // http, https, ftp, circuit url pattern
  Utils.URL_PATTERN = new RegExp('(http|ftp|https|circuit):\\/\\/[\\w-]+?((\\.|:)[\\w-]+?)*?' + URL_SYMBOLS, 'gim');

  Utils.HTTPS_URL_PATTERN = new RegExp('https:\\/\\/[\\w-]+?((\\.|:)[\\w-]+?)*?' + URL_SYMBOLS, 'gim');

  // Matches URL_PATTERN (exact match)
  Utils.EXACT_URL_PATTERN = new RegExp('^' + Utils.URL_PATTERN.source + '$', 'i');

  // starting with www. sans http:// or https://
  Utils.PSEUDO_URL_PATTERN = new RegExp('www\\.+[\\w-]+?(\\.[\\w-]+?)+?' + URL_SYMBOLS, 'gim');

  // String starts with http, https, ftp protocol
  Utils.PROTOCOL_PATTERN = /^(?:http|https|ftp):\/\//;

  // Query expression retrieved from ui-bootstrap returning all tabbable elements
  Utils.TABBABLE_ELEMENTS_PATTERN =
    "a[href], area[href], input:not([disabled]):not([tabindex='-1']), " +
    "button:not([disabled]):not([tabindex='-1']),select:not([disabled]):not([tabindex='-1']), " +
    "textarea:not([disabled]):not([tabindex='-1']), " +
    "iframe, object, embed, *[tabindex]:not([tabindex='-1']), *[contenteditable=true]";

  Utils.RICH_TEXT_LINK_CLASS = 'text-linkDarkGreen';

  Utils.ABBR_PATTERN = /abbr=["'](.*?)["']/;

  Utils.SPACE_HASHTAG_PATTERN = /#([_a-zA-Z0-9\u0080-\u009f\u00c0-\uffff]+)/g;

  /**
   * Email address patttern. `/^[_a-z0-9-\+]+(?:\.[_a-z0-9-\+]+)*@[a-z0-9-]+(?:\.[a-z0-9-]+)*(?:\.[a-z][a-z]+)$/im`
   * @property EMAIL_ADDRESS_PATTERN
   * @type string
   * @memberOf UnifyPhone.Utils
   * @static
   * @final
   */
  // Email addresses(es) included in text
  Utils.EMAIL_ADDRESS_INCLUDED_PATTERN = /[\w*+\-$?^{!#%&'/=`|}~]+?(?:\.[\w*+\-$?^{!#%&'/=`|}~]+?)*?@[a-z\d-]+?(?:\.[a-z\d-]{2,})+/gim;
  Utils.EMAIL_ADDRESS_PATTERN = new RegExp('^' + Utils.EMAIL_ADDRESS_INCLUDED_PATTERN.source + '$', 'i');

  // Pattern to match unifyPhone conversation hash
  Utils.UNIFY_PHONE_CONVERSATION_HASH_PATTERN = /#\/(conversation|open|muted)\/[0-9\-a-z]+?(\?((user=(\d)+?)|(item=([0-9\-a-z])+?(&user=(\d)+?)?)))?$/gim;

  // Pattern to match unifyPhone user, user profile and phone hash
  Utils.UNIFY_PHONE_LINKS_HASH_PATTERN = /#\/(profile|phone|user|search)\/[0-9\-a-z]+?$/gim;

  // Pattern to match hash tag search urls
  Utils.UNIFY_PHONE_HASH_TAG_SEARCH_PATTERN = /#\/search\/[a-zA-Z\u0080-\u009f\u00a1-\uffff0-9_]+/gim;

  // Pattern to match unifyPhone email hash
  Utils.UNIFY_PHONE_EMAIL_HASH_PATTERN = new RegExp('#\\/email\\/' + Utils.EMAIL_ADDRESS_INCLUDED_PATTERN.source + '$', 'gim');

  // Phone numbers. Examples:
  //   15615551234
  //   1.561.555.1234
  //   +15615551234
  //   +1-561-555-1234
  //   +1(561)555-1234
  Utils.PHONE_PATTERN = /^(\+\s*)?\d+(\s*\(\s*\d+\s*\)\s*\d+)?((\s*-?\s*\d+)*|(\s*\.\s*\d+)*)$/;

  Utils.GNF_PHONE_PATTERN = /^((\+)|(\+\s*\d+(\s*\(\s*\d+\s*\)\s*\d+)?((\s*-?\s*\d+)*|(\s*\.\s*\d+)*)))$/;

  Utils.E164_PHONE_PATTERN = /^\+?[1-9]\d{1,14}$/; // optional +, (country code starts with 1-9), max 15 digits total

  // Prefix Access Code: must start with # or *, followed by digits, *, #, (), -, + or white spaces
  // We don't check if parenthesis are properly opened and closed (like in other phone patterns)
  Utils.PHONE_PAC = /^([#*]+[#*()\-\s+\d]*)$/;

  Utils.PHONE_DIAL_PATTERN = /^([#*()\\/\-.\s+\d,]*)$/;
  Utils.PHONE_DIAL_WITH_PIN_PATTERN = /^([#*()\\/\-.\s+\d]*)(,+[,\d#*]+)$/;

  // Same as PHONE_DIAL_PATTERN but with an extension at the end (e.g.: +1 (231) 344-3455 x 1324)
  Utils.PHONE_WITH_EXTENSION_PATTERN = /^([#*()\\/\-.\s+\d]*)(\s*(x|X|ext\.)\s*\d+)$/;

  // Pattern for verifying time format(HH:mm or hh:mm a)
  Utils.TIME_PATTERN = /^((([0-1]?[0-9]|2[0-3]):[0-5][0-9]\s*)|((0?[1-9]|1[0-2]):[0-5][0-9]\s*(PM|AM)))$/;

  // Pattern for verifying an IPv4 Address
  Utils.IPV4_ADDRESS_PATTERN = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;

  Utils.TCP_PORT_NUMBERS_PATTERN = /^(0|([1-9]\d{0,3}|[1-5]\d{4}|[6][0-5][0-5]([0-2]\d|[3][0-5])))$/;

  Utils.FQDN_PATTERN = /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9])$/;

  Utils.matchPhonePattern = function (str) {
    if (!str || typeof str !== 'string') {
      return null;
    }
    return str.match(Utils.PHONE_PATTERN);
  };

  Utils.matchUrlPattern = function (str) {
    if (!str || typeof str !== 'string') {
      return null;
    }
    return str.match(Utils.URL_PATTERN) || str.match(Utils.PSEUDO_URL_PATTERN);
  };

  Utils.matchEmailPattern = function (str) {
    if (!str || typeof str !== 'string') {
      return null;
    }
    return str.match(Utils.EMAIL_ADDRESS_INCLUDED_PATTERN);
  };

  Utils.getEmails = function (str) {
    var emails = str && str.split(/[;,]\s*|\s+/i);

    emails =
      emails &&
      emails
        .map(function (email) {
          var match = email.match(Utils.EMAIL_ADDRESS_INCLUDED_PATTERN);
          return match && match[0];
        })
        .filter(function (email) {
          return !!email;
        });

    return emails || [];
  };

  Utils.getTags = function (str) {
    return (
      str &&
      str.split(/[;,]\s*/i).reduce(function (tags, tag) {
        var tagTrimmed = tag.trim();
        if (tagTrimmed.length) {
          tags.push(tagTrimmed);
        }
        return tags;
      }, [])
    );
  };

  Utils.matchNames = function (str, query, flag) {
    if (!str || typeof str !== 'string' || !query) {
      return false;
    }
    var reName = new RegExp('^' + RegExp.escape(query), flag || 'i');
    return (
      reName.test(str) ||
      str.split(' ').some(function (name) {
        return reName.test(name);
      })
    );
  };

  Utils.matchIpOrFqdnPattern = function (str) {
    if (!str || typeof str !== 'string') {
      return null;
    }
    return str.match(Utils.IPV4_ADDRESS_PATTERN) || str.match(Utils.FQDN_PATTERN);
  };

  // Compares strings case-insensitive
  Utils.compareStrings = function (str1, str2) {
    if (typeof str1 !== 'string' || typeof str2 !== 'string') {
      return false;
    }
    var reCompare = new RegExp('^' + RegExp.escape(str1) + '$', 'i');
    return reCompare.test(str2);
  };

  Utils.rstring = function () {
    return Math.floor(Math.random() * 1e9).toString();
  };

  Utils.randomNumber = function (min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  Utils.randomBoolean = function () {
    return Math.random() < 0.5;
  };

  Utils.createTransactionId = function () {
    var s = [];
    var hexDigits = '0123456789abcdef';
    for (var i = 0; i < 36; i++) {
      s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
    }
    s[14] = '4'; // bits 12-15 of the time_hi_and_version field to 0010
    s[19] = hexDigits.substr((s[19] && 0x3) || 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
    s[8] = s[13] = s[18] = s[23] = '-';

    return s.join('');
  };

  // Get the legal region ('us', 'uk', 'de') associated to the regionId ('americas', 'apac', 'europe') provided
  Utils.getLegalRegion = function (regionId) {
    switch (regionId) {
      case 'europe':
        var languageSplit = (navigator.language || navigator.browserLanguage).split('-'); //supports different browsers (Chrome/IE)
        var country = Utils.lastArrayElement(languageSplit); //supports locale with and without country
        return country.toLowerCase() === 'de' ? 'de' : 'uk';
      case 'americas':
      case 'apac':
        return 'us';
      default:
        return 'us';
    }
  };

  /**
   * Provides browser type and version information.
   * @method getBrowserInfo
   * @returns {Object} Object with type, version and browser attributes
   * @memberof UnifyPhone.Utils
   */
  // eslint-disable-next-line complexity
  Utils.getBrowserInfo = function () {
    if (window.navigator.platform === 'iOS') {
      return {
        ios: true,
        type: 'ios'
      };
    } else if (window.navigator.platform === 'Android') {
      return {
        android: true,
        type: 'android'
      };
    } else if (window.navigator.platform === 'node') {
      return {
        node: true,
        type: 'node'
      };
    } else if (window.cordova && window.cordova.platformId === 'ios') {
      return {
        cordovaios: true,
        type: 'cordovaios'
      };
    }

    var browserData = {};
    var ua = navigator.userAgent.toLowerCase();
    // WKWebView (Tauri on macOS): UA contains 'applewebkit' but NOT 'chrome' and NOT 'version/X safari'
    // which causes all regex patterns below to fail, resulting in the dummy WebRTC adapter being used.
    if (Utils.isTauri() && /applewebkit/.test(ua) && !/chrome/.test(ua) && !/version\/[\d.]+ safari/.test(ua)) {
      browserData.safari = true;
      browserData.type = 'safari';
      browserData.version = (/applewebkit\/([\d.]+)/.exec(ua) || [])[1] || 0;
      return browserData;
    }
    var match =
      /(msie|trident)(?: |.*rv:)([\d.]+)/.exec(ua) ||
      /(opera|opr)\/(?:.*version\/|)([\d.]+)/.exec(ua) ||
      /(firefox)\/([\d.]+)/.exec(ua) ||
      /(edge)\/([\d.]+)/.exec(ua) || // Legacy Edge
      /(edg)\/([\d.]+)/.exec(ua) || // New Edge based off Chrome
      /(chrome)\/([\d.]+)/.exec(ua) ||
      /version\/([\d.]+).*(safari)/.exec(ua) ||
      /(phantomjs)\/([\d.]+)/.exec(ua) || // Used for unit test (shouldn't impact performance here)
      [];
    var browser = match[1] || '';
    var version = match[2] || 0;
    var subType;

    // Swap matches for Safari because of inverted user agent
    if (version === 'safari') {
      browser = match[2];
      version = match[1];
    } else {
      switch (browser) {
        case 'trident':
          // IE no longer reported as MSIE starting from 11 version
          browser = 'msie';
          break;
        case 'opr':
          // Correctly define Opera
          browser = 'opera';
          break;
        case 'edg':
          // Mark new Edge (based off Chrome) as Chrome, but with subType='edge'
          // The version field contains the Edge version
          browser = 'chrome';
          subType = 'edge';
          break;
        case '':
        case 'chrome':
          // Check if this is mobile safari/chrome
          match = /(mobile)\/([\d.]+)|\bmobile\b/.exec(ua) || [];
          if (match.length > 0) {
            subType = 'webview';
            if (browser === '') {
              browser = match[1] || '';
              version = match[2] || 0;
              var iosWebView = /iphone|ipod|ipad/.test(ua);
              if (iosWebView) {
                browser = 'safari';
              }
              break;
            }
          }
          break;
      }
    }

    browserData[browser] = true;
    browserData.type = browser;
    browserData.version = version;

    if (subType) {
      browserData.subType = subType;
    }

    return browserData;
  };

  // Define the possible OS types for Utils.getOSInfo()
  var OS_TYPES = [
    { s: 'Windows 10', r: /(Windows.* 10|Windows 10.0|Windows[ _]NT.* 10.0)/ },
    { s: 'Windows 8.1', r: /(Windows.* 8.1|Windows[ _]NT.* 6.3)/ },
    { s: 'Windows 8', r: /(Windows.* 8|Windows[ _]NT.* 6.2)/ },
    { s: 'Windows 7', r: /(Windows.* 7|Windows[ _]NT.* 6.1)/ },
    { s: 'Windows Vista', r: /(Windows.* Vista|Windows[ _]NT.* 6.0)/ },
    { s: 'Windows Server 2003', r: /(Windows Server 2003|Windows[ _]NT.* 5.2)/ },
    { s: 'Windows XP', r: /(Windows[ _]NT.* 5.1|Windows XP)/ },
    { s: 'Windows 2000', r: /(Windows[ _]NT.* 5.0|Windows 2000)/ },
    { s: 'Windows ME', r: /(Win 9x 4.90|Windows ME)/ },
    { s: 'Windows 98', r: /(Windows 98|Win98)/ },
    { s: 'Windows 95', r: /(Windows 95|Win95|Windows_95)/ },
    { s: 'Windows NT 4.0', r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/ },
    { s: 'Windows CE', r: /Windows CE/ },
    { s: 'Windows 3.11', r: /Windows 3.11|Win16/ },
    { s: 'Android', r: /Android/ },
    { s: 'Open BSD', r: /Open BSD|OpenBSD/ },
    { s: 'Sun OS', r: /Sun OS|SunOS/ },
    { s: 'Linux', r: /(Linux|X11)/ },
    { s: 'iOS', r: /(iOS|iPhone|iPad|iPod)/ },
    { s: 'Mac OS X', r: /Mac OS X/ },
    { s: 'Mac OS', r: /(Mac OS|MacPPC|MacIntel|Mac_PowerPC|Macintosh|Darwin)/ },
    { s: 'QNX', r: /QNX/ },
    { s: 'UNIX', r: /UNIX/ },
    { s: 'BeOS', r: /BeOS/ },
    { s: 'OS/2', r: /OS\/2/ },
    { s: 'Search Bot', r: /(Search Bot|nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/ }
  ];

  Utils.getOSInfo = function (osVersion) {
    osVersion = osVersion || window.navigator.userAgent;

    var info = osVersion;
    var type = '';
    var version = '';

    var inList = OS_TYPES.some(function (os) {
      if (os.r.test(osVersion)) {
        info = os.s;
        return true;
      }
      return false;
    });

    // Get OS version info from navigator
    if (inList && /Windows/.test(info)) {
      version = /Windows (.*)/.exec(info)[1];
      type = 'Windows';
    } else {
      type = info;

      try {
        switch (info) {
          case 'Mac OS X':
            version = /Mac OS X (10[._\d]+)/.exec(osVersion)[1];
            break;

          case 'Android':
            version = /Android ([._\d]+)/.exec(osVersion)[1];
            break;
        }
      } catch (e) {
        logger.debug('[Utils]: osVersion does not contain version on it. ', osVersion);
      }
    }

    return {
      type: type,
      version: version,
      info: info
    };
  };

  Utils.checkUserDomain = function (domain, startsWith) {
    var hostname = window.location.hostname;
    if (startsWith) {
      return hostname.startsWith(domain);
    }
    return hostname.endsWith(domain);
  };

  /**
   * Parses an string with IP+port (IPv4 or IPv6) and returns an object with ipAddress and port properties.
   * If parsing is not successful, null is returned.
   * Input examples:
   * IPv4: '172.20.41.178:3823'
   * IPv6: '[2001:db8:a0b:12f0::1]:3422'
   */
  Utils.parseIpWithPort = function (ip) {
    if (!ip || typeof ip !== 'string') {
      return null;
    }
    if (ip[0] === '[') {
      // IPV6+port
      var ipv6 = /\[(.*)\]:(\d*)/.exec(ip); // Extract the IPV6 and port (we're not validating the IP address)
      if (ipv6 && ipv6.length === 3) {
        return {
          ipAddress: ipv6[1],
          port: ipv6[2]
        };
      }
    } else if (ip.includes(':')) {
      var split = ip.split(':');
      if (split.length === 2) {
        return {
          ipAddress: split[0],
          port: split[1]
        };
      }
    }
    return null;
  };

  Utils.isDialableNumber = function (number) {
    return !!number && (Utils.PHONE_PATTERN.test(number) || Utils.PHONE_PAC.test(number));
  };

  Utils.isEmptyObject = function (obj) {
    return Object.keys(obj).length === 0;
  };

  // Get the prototypical base object (__proto__) and optionally update the base
  // object with properties set on the object itself.
  // Note: Only first-level properties are updated, no recursion.
  Utils.getBaseObject = function (obj, updateBaseObj) {
    if (!obj || typeof obj !== 'object') {
      return null;
    }

    if (Array.isArray(obj)) {
      return obj;
    }

    var base = Object.getPrototypeOf(obj);

    if (Utils.isEmptyObject(base)) {
      // obj was constructed directly from Object
      return obj;
    }

    if (updateBaseObj !== false) {
      Object.getOwnPropertyNames(base).forEach(function (name) {
        if (typeof base[name] !== 'function' && obj.hasOwnProperty(name)) {
          base[name] = obj[name];
        }
      });
    }
    return base;
  };

  // Get the prototypical base object (__proto__), updates the base object with
  // properties set on the object itself, and finally deletes those properties
  // from the object itself.
  // Note: Only first-level properties are updated, no recursion.
  Utils.syncBaseObject = function (obj) {
    var base = Utils.getBaseObject(obj, true);
    if (!base || base === obj) {
      return;
    }
    Object.getOwnPropertyNames(base).forEach(function (name) {
      if (obj.hasOwnProperty(name)) {
        delete obj[name];
      }
    });
  };

  // Flattens prototyped objects, so JSON.stringify will also include the prototype's properties.
  Utils.flattenObj = function (obj) {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return obj;
    }

    var base = Object.getPrototypeOf(obj);
    if (Utils.isEmptyObject(base)) {
      // obj was constructed directly from Object
      return obj;
    }

    var tmp = {};
    for (var key in obj) {
      if (typeof obj[key] !== 'function') {
        tmp[key] = obj[key];
      }
    }
    return tmp;
  };

  // eslint-disable-next-line complexity
  Utils.getNormalizedFileExtension = function (file) {
    var fileName = file && (file.fileName || file.name);
    if (!fileName) {
      return null;
    }

    if (/audio/i.test(file.mimeType)) {
      return 'audio';
    }

    if (/video/i.test(file.mimeType)) {
      return 'video';
    }

    var fileExt = fileName.split('.').pop();
    if (fileExt) {
      switch (fileExt.toLowerCase()) {
        case 'doc':
        case 'docx':
          return 'doc';
        case 'ppt':
        case 'pptx':
          return 'ppt';
        case 'pdf':
          return 'pdf';
        case 'xls':
        case 'xlsx':
        case 'xlsm':
          return 'xls';
        case 'gz':
        case 'zip':
          return 'zip';
        case 'html':
          return 'html';
        case 'csv':
          return 'csv';
        case 'png':
          return 'png';
        case 'jpg':
        case 'jpeg':
          return 'jpg';
        case 'log':
          return 'log';
        case 'flv':
        case 'mp4':
        case 'ogv':
        case 'webm':
          return 'video';
        case 'wav':
        case 'mp3':
        case 'ogg':
          return 'audio';
        case 'vtt':
          return 'vtt';
      }
    }

    return 'def';
  };

  Utils.getImageFileExtension = function (file) {
    if (file.type) {
      switch (file.type.toLowerCase()) {
        case 'image/png':
          return 'png';
        case 'image/jpeg':
          return 'jpg';
        case 'image/gif':
          return 'gif';
        case 'image/bmp':
          return 'bmp';
        case 'image/svg+xml':
          return 'svg';
      }
    }
    return null;
  };
  //CQ:CQ00283052
  Utils.getMimeTypes = function (fileName) {
    if (!fileName) {
      return null;
    }

    var fileExt = fileName.split('.').pop();
    if (fileExt) {
      switch (fileExt.toLowerCase()) {
        case 'xlsx':
          return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        case 'xltx':
          return 'application/vnd.openxmlformats-officedocument.spreadsheetml.template';
        case 'potx':
          return 'application/vnd.openxmlformats-officedocument.presentationml.template';
        case 'ppsx':
          return 'application/vnd.openxmlformats-officedocument.presentationml.slideshow';
        case 'pptx':
          return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        case 'sldx':
          return 'application/vnd.openxmlformats-officedocument.presentationml.slide';
        case 'docx':
          return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        case 'dotx':
          return 'application/vnd.openxmlformats-officedocument.wordprocessingml.template';
        case 'xlam':
          return 'application/vnd.ms-excel.addin.macroEnabled.12';
        case 'xlsb':
          return 'application/vnd.ms-excel.sheet.binary.macroEnabled.12';
      }
    }

    return 'application/octet-stream';
  };

  /**
   * Utility function to escape text as html, but keep line changes.
   * @method textToHtmlEscaped
   * @param {String} str Text content to escape
   * @param {Boolean} [spaceForNewlines] If true, newlines (\n) are converted to &nbsp; instead of <br>
   * @returns {String} Escaped string
   * @example
   *     UnifyPhone.Utils.textToHtmlEscaped('<b>bold</b>\ntest');
   *     // returns &lt;b&gt;bold&lt;/b&gt;<br>test
   *
   *     UnifyPhone.Utils.textToHtmlEscaped('<b>bold</b>\ntest', true);
   *     // returns &lt;b&gt;bold&lt;/b&gt;&nbsp;test
   */
  Utils.textToHtmlEscaped = function (str, handleTextNewLines) {
    // Method to escape text as html but keep line changes
    if (!str) {
      return '';
    }
    var newLinesReplace = handleTextNewLines ? '&nbsp;' : '<br>'; // In selector we want spaces instead of new \n or \r
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&#34;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\r\n|\r|\n/gm, newLinesReplace);
  };

  Utils.escapedHtmlToText = function (str) {
    // Method to transform escaped html to text
    if (!str) {
      return '';
    }
    return str
      .replace(/&amp;/g, '&')
      .replace(/&#34;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ')
      .replace(/<br\/?>/g, '\r\n');
  };

  Utils.stripParenthesis = function (token) {
    // The method removes only open and close parenthesis
    token = token.trim(); //linkified text adds empty characters at the end of token
    var startPos = 0;
    while (token[startPos] === '(' && token[token.length - startPos - 1] === ')') {
      startPos++;
    }
    return token.slice(startPos, token.length - startPos);
  };

  function linkifyHtml(content) {
    if (!content) {
      return '';
    }
    var inputDOM = document.createElement('div');
    var linkifiedDOM = document.createElement('div');
    // After optimizing sanitization we don't need to (.replace(/(<br\/>|<hr\/>)/g, '\n');)
    // Need to replace $ chars with $$ so they are displayed correctly ($ is an escape char so if we have $$ only one is displayed)
    content = content.replace(/\$/gm, '$$$$');
    // Add a link to user profile/hashtag search in span with mention/hashtag, ignore all other spans that can be inside the mention/hashtag
    // Add attribute fr-omit-link to prevent sending link to backend, in case of paste
    var isReplace = 0;
    var linkIndex = 0;
    content = content.replace(/(<span.*?>)|(<\/span>)/gi, function (match, openTag, closeTag) {
      if (openTag) {
        isReplace++;
        if (openTag.match(/class="mention"/i)) {
          linkIndex = isReplace;
          return match.replace(/.*abbr="(.*?)".*?>/gi, '<span class="mention" abbr="$1"><a href="#/profile/$1" fr-omit-link>');
        } else if (openTag.match(/class="hashtag"/i)) {
          linkIndex = isReplace;
          return match.replace(/.*abbr="(.*?)".*?>/gi, '<span class="hashtag" abbr="$1"><a href="#/search/$1" fr-omit-link>');
        } else {
          return openTag;
        }
      }
      if (closeTag) {
        var linkedCloseTag = '';
        if (isReplace === linkIndex) {
          linkedCloseTag = '</a></span>';
        }
        isReplace--;
        return linkedCloseTag || closeTag;
      }
      return '';
    });

    inputDOM.innerHTML = content;
    return linkifyNode(inputDOM, linkifiedDOM).innerHTML.replace(/&nbsp;/gi, ' ');
  }

  function getOrigin() {
    return window.location && window.location.origin;
  }

  Utils.linkifyText = function (text, keepNewlines) {
    if (!keepNewlines) {
      // eslint-disable-next-line no-control-regex
      text = text.replace(/\xA0|&#160;|\x0A|\x0D/gim, ' ');
    }
    var LT = String.fromCharCode(17); // Temporary mapping using non-printable ascii code x11 (DEC 17) for char "<" replacements
    var GT = String.fromCharCode(18); // Temporary mapping using non-printable ascii code x12 (DEC 18) for char ">" replacements
    var QUOT = String.fromCharCode(19); // Temporary mapping using non-printable ascii code x13 (DEC 19) for char "'" replacements

    var checkForUnifyPhoneLink = function (match) {
      var target = '';
      var rel = '';
      var origin = getOrigin();
      var url = Utils.getUnifyPhoneRelativeLink(origin, match);
      if (!url) {
        var createNewPage = true;
        var openInFrame = false;
        var makeHrefEmpty = false;
        url = match;
        if (!Utils.PROTOCOL_PATTERN.exec(match)) {
          // Make sure that protocol is specified
          url = 'http://' + url;
        }

        if (makeHrefEmpty) {
          return LT + 'a' + GT + match + LT + '/a' + GT;
        }

        if (createNewPage) {
          target = ' target=' + QUOT + '_blank' + QUOT;
        } else if (openInFrame) {
          target = ' target=' + QUOT + 'unifyPhoneProtocolFrame' + QUOT;
        }
        rel = ' rel=' + QUOT + 'noopener noreferrer' + QUOT;
      }
      return LT + 'a href=' + QUOT + url + QUOT + target + rel + GT + match + LT + '/a' + GT;
    };

    // Splits text on white spaces or punctuation marks with white spaces. Use Positive Lookahead to keep characters in place.
    var tokens = text.split(Utils.URL_SEPARATORS_PATTERN);
    for (var i = 0; i < tokens.length; i++) {
      // order of replacements matters.
      var token = tokens[i];
      var strippedText = Utils.stripParenthesis(token);
      var linkifiedText = strippedText;
      // Perform simply search at first, in order not to use more complex regular expressions if it is not needed.
      if (linkifiedText.search(/http|ftp|https|circuit/gim) >= 0 && linkifiedText.search(Utils.URL_PATTERN) >= 0) {
        linkifiedText = linkifiedText.replace(Utils.URL_PATTERN, checkForUnifyPhoneLink);
      } else if (linkifiedText.search(/www\./gim) >= 0 && linkifiedText.search(Utils.PSEUDO_URL_PATTERN) >= 0) {
        linkifiedText = linkifiedText.replace(Utils.PSEUDO_URL_PATTERN, checkForUnifyPhoneLink);
      } else if (linkifiedText.search(/\S@\S/gim) >= 0 && linkifiedText.search(Utils.EMAIL_ADDRESS_INCLUDED_PATTERN) >= 0) {
        linkifiedText = linkifiedText.replace(
          Utils.EMAIL_ADDRESS_INCLUDED_PATTERN,
          LT +
            'a href=' +
            QUOT +
            'mailto:$&' +
            QUOT +
            ' target=' +
            QUOT +
            '_blank' +
            QUOT +
            ' rel=' +
            QUOT +
            'noopener noreferrer' +
            QUOT +
            GT +
            '$&' +
            LT +
            '/a' +
            GT
        );
      }

      token = token.replace(strippedText, linkifiedText);
      tokens[i] = token;
    }
    text = tokens.join('');

    /* eslint-disable no-control-regex */
    // This temporary replacement using the non-printable chars is done in order to
    // exclude the chars used to compose an href tag (<,>,') from the html escape process when in plain text mode.
    return Utils.textToHtmlEscaped(text)
      .replace(/\x11/gi, '<') // Linkify was done, replace them back
      .replace(/\x12/gi, '>')
      .replace(/\x13/gi, "'");
    /* eslint-enable no-control-regex */
  };

  function linkifyNode(startNode, linkifiedDOM) {
    // Node traversing method in order to find text nodes and linkify them
    if (!startNode || !startNode.childNodes || !startNode.childNodes.length) {
      return startNode;
    }

    var currentNode;
    for (var i = 0, len = startNode.childNodes.length; i < len; i++) {
      currentNode = startNode.childNodes[i];
      switch (currentNode.nodeType) {
        case 1: // ELEMENT_NODE
          if (currentNode.nodeName === 'A') {
            // Add target="_blank" if not a UnifyPhone link. SDK can send a tag without target. Or remove origin if UnifyPhone link.
            var origin = getOrigin();
            var refHref = Utils.getUnifyPhoneRelativeLink(origin, currentNode.href);
            if (!refHref) {
              currentNode.target = '_blank';
              currentNode.rel = 'noopener noreferrer';
            } else {
              currentNode.removeAttribute('target');
              currentNode.href = refHref;
            }
          }
          // Don't linkify link preview. E.g. description may have a url
          if (currentNode.classList.contains('link-preview') || currentNode.classList.contains('fr-link')) {
            break;
          }
          linkifyNode(currentNode, linkifiedDOM);
          break;
        case 3: // TEXT_NODE
          linkifiedDOM.innerHTML = Utils.linkifyText(currentNode.textContent, currentNode.parentNode.tagName === 'PRE');
          i += linkifiedDOM.childNodes.length - 1;
          while (linkifiedDOM.childNodes.length) {
            startNode.insertBefore(linkifiedDOM.childNodes[0], currentNode);
          }
          startNode.removeChild(currentNode);
          len = startNode.childNodes.length;
      }
    }
    return startNode;
  }

  Utils.linkifyContent = function (content, contentType) {
    if (!content) {
      return '';
    }
    var MAX_TEXT_CONTENT_SIZE = 50000;
    if (content.length > MAX_TEXT_CONTENT_SIZE) {
      // Do not convert large text items due to performance
      return content;
    }

    if (contentType === 'PLAIN') {
      // When content is plain text, html tags are escaped and content is linkified as text
      return Utils.linkifyText(content);
    }
    // Assume RICH text by default
    // When content is html, parsing is done node-wise, and every text nodes is parsed for links
    return linkifyHtml(content);
  };

  Utils.invalidFileTypes = [
    'ade',
    'adp',
    'app',
    'asp',
    'bas',
    'bat',
    'cer',
    'chm',
    'cmd',
    'cnt',
    'com',
    'cpl',
    'crt',
    'csh',
    'der',
    'exe',
    'fxp',
    'gadget',
    'hlp',
    'hpj',
    'hta',
    'inf',
    'ins',
    'isp',
    'its',
    'js',
    'jse',
    'ksh',
    'lnk',
    'mad',
    'maf',
    'mag',
    'mam',
    'maq',
    'mar',
    'mas',
    'mat',
    'mau',
    'mav',
    'maw',
    'mda',
    'mdb',
    'mde',
    'mdt',
    'mdw',
    'mdz',
    'msc',
    'msh',
    'msh1',
    'msh1xml',
    'msh2',
    'msh2xml',
    'mshxml',
    'msi',
    'msp',
    'mst',
    'ops',
    'osd',
    'pcd',
    'pif',
    'plg',
    'prf',
    'prg',
    'ps1',
    'ps1xml',
    'ps2',
    'ps2xml',
    'psc1',
    'psc2',
    'pst',
    'reg',
    'scf',
    'scr',
    'sct',
    'shb',
    'shs',
    'tmp',
    'url',
    'vb',
    'vbe',
    'vbp',
    'vbs',
    'vsmacros',
    'vsw',
    'ws',
    'wsc',
    'wsf',
    'wsh',
    'xnk'
  ];

  /**
   * Check if container has a scrollbar
   */
  Utils.hasScrollbar = function (container) {
    if (container && container.length > 0) {
      var elm = container[0];
      return !!elm && elm.scrollHeight > elm.clientHeight;
    }
    return false;
  };
  /**
   * Returns true if child element is either equal the ancestor element or a nested descendant of the ancestor element.
   */
  Utils.isDescendantOrEqual = function (child, ancestor) {
    if (child === ancestor) {
      return true;
    }
    var _parent = child;
    while (_parent) {
      _parent = _parent.parentElement;
      if (_parent === ancestor) {
        return true;
      }
    }
    return false;
  };

  Utils.isSupportedImage = function (mimeType) {
    var regExp = /^image\/(jpeg|gif|bmp|png)$/i;
    return regExp.test(mimeType); // Accept only image/jpeg, image/gif, image/bmp and image/png MIME types
  };

  /**
   * We handle video types that we can play in embeddedPlayer.
   * Handling done based on MIME type.
   * Note: Probably more types should be added on the future.
   */
  Utils.isVideoSupportedByBrowser = function (mimeType) {
    var regExp = /^video\/(quicktime|mp4|webm)$/i;
    return regExp.test(mimeType);
  };

  /**
   * Returns a copy of the source object
   */
  Utils.shallowCopy = function (src) {
    if (!src || typeof src !== 'object') {
      return src;
    }

    if (Array.isArray(src)) {
      return src.slice();
    }

    var flattenSrc = Utils.flattenObj(src);
    var obj = {};
    for (var key in flattenSrc) {
      if (flattenSrc.hasOwnProperty(key)) {
        obj[key] = flattenSrc[key];
      }
    }
    return obj;
  };

  /**
   * Returns the normalized locale string ('en-US' to 'EN_US' or 'de-DE' to 'DE_DE' or 'DE_DE' to 'DE_DE') or
   * undefined if the parameter is not valid
   */
  Utils.normalizeLocaleProto = function (locale) {
    var proto;
    if (locale && locale.length >= 5) {
      proto = locale.replace(/-/g, '_').toUpperCase();
    }
    return proto;
  };

  /**
   * Returns the normalized locale string ('EN_US' to 'en-US' or 'DE_DE' to 'de-DE' or 'de-DE' to 'de-DE') or
   * undefined if the parameter is not valid
   */
  Utils.normalizeLocale = function (proto) {
    var locale;
    if (proto && proto.length >= 5) {
      locale = proto.substr(0, 2).toLowerCase() + proto.substr(2, proto.length - 2).replace(/_/g, '-');
    }
    return locale;
  };

  Utils.getTimezoneName = function (date) {
    date = date || new Date();
    // eslint-disable-next-line newline-per-chained-call
    return date.toLocaleDateString('en-US', { timeZoneName: 'long' }).split(', ').pop();
  };

  /**
   * Return a timezone string from a given timezone offset
   */
  Utils.getTimezone = function (timeZoneOffset) {
    if (typeof timeZoneOffset !== 'number') {
      timeZoneOffset = new Date().getTimezoneOffset();
    }
    var hour = Math.floor(Math.abs(timeZoneOffset) / 60);
    var min = Math.floor(Math.abs(timeZoneOffset) % 60) + ''; // Convert to string so we can invoke padStart
    return (timeZoneOffset > 0 ? '-' : '+') + hour + min.padStart(2, '0');
  };

  /**
   * Returns new calculated timestamp instead of provided one when delayMS (milliseconds) is correctly set
   */
  Utils.delayOrTimestamp = function (delayMS, timestamp) {
    return delayMS > 0 ? Date.now() + delayMS : timestamp;
  };

  /**
   * Build the attachmentMetaData array used in the client API from a given list of uploaded files
   */
  Utils.buildAttachmentMetaData = function (files) {
    var buildMetaData = function (attachment) {
      var res = {
        fileId: attachment.fileId,
        fileName: attachment.fileName,
        itemId: attachment.itemId,
        mimeType: attachment.mimeType,
        size: attachment.size
      };
      if (attachment.thumbnail && attachment.thumbnail.fileId) {
        res.thumbnailId = attachment.thumbnail.fileId;
      }
      if (attachment.inlineUsage) {
        res.inlineUsage = attachment.inlineUsage;
      }
      return res;
    };

    var attachments = [];
    files &&
      files.forEach(function (file) {
        attachments.push(buildMetaData(file));
        if (file.thumbnail) {
          attachments.push(buildMetaData(file.thumbnail));
        }
      });

    return {
      attachments: attachments
    };
  };

  /*
   * Pseudo-classical OOP pattern
   * Child and Parent must be constructor function
   */
  Utils.inherit = function (Child, Parent) {
    function F() {}
    F.prototype = Parent.prototype;
    Child.prototype = new F();
    Child.prototype.constructor = Child;
    Child.parent = Parent.prototype;
  };

  Utils.bytesToSize = function (bytes, precision) {
    // Make sure we are working with an integer
    bytes = parseInt(bytes, 10);
    if (!bytes) {
      return '0 Bytes';
    }
    var k = 1024;
    var sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    var i = Math.floor(Math.log(bytes) / Math.log(k));
    return Number((bytes / Math.pow(k, i)).toPrecision(precision || 3)) + ' ' + sizes[i];
  };

  // Check if Mac OS, default false
  Utils.isMacOs =
    window.navigator && window.navigator.userAgent
      ? window.navigator.userAgent.indexOf('Mac') !== -1 || window.navigator.userAgent.indexOf('Darwin') !== -1
      : false;

  // Check if Windows, default false
  Utils.isWindowsOs = window.navigator && window.navigator.platform ? window.navigator.platform.substring(0, 3).toLowerCase() === 'win' : false;

  /**
   * Function used to determine if client is mobile
   */
  Utils.isMobile = function () {
    return window.navigator.platform === 'iOS' || window.navigator.platform === 'Android';
  };

  /**
   * Function used to determine if client is iOS
   */
  Utils.isIOS = function () {
    return window.navigator.platform === 'iOS';
  };

  /**
   * Function used to determine if the app is running inside a Tauri desktop shell.
   * @method isTauri
   * @returns {Boolean} true if running in Tauri
   * @memberof UnifyPhone.Utils
   */
  Utils.isTauri = function () {
    return !!(window.__TAURI__ || window.__TAURI_INTERNALS__);
  };

  /**
   * Format PIN to readable string:
   * tenant PIN, space, session PIN separated every SESSION_PIN_CHUNK_LEN
   * every session PIN chunck should be smaller than SESSION_PIN_CHUNK_MIN_LEN
   * set noDelimiters to true if the string shouldn't have any delimiters (spaces)
   */
  Utils.formatPIN = function (tenantPIN, sessionPIN, noDelimiters) {
    if (!sessionPIN) {
      return '';
    }
    if (noDelimiters) {
      return (tenantPIN || '') + sessionPIN + '#';
    }

    // initial block chunck length
    var SESSION_PIN_FIRST_CHUNK_LEN = 4;
    // session PIN chunck length
    var SESSION_PIN_CHUNK_LEN = 3;
    // session PIN chunck minimum length
    var SESSION_PIN_CHUNK_MIN_LEN = 2;

    var pin = [];
    var chunk;
    // during PIN migration assure that format always looks the same for
    // new (10 digit access code, no TIN) and old (TIN + 6 digit access code)
    var chunkLen = tenantPIN ? SESSION_PIN_CHUNK_LEN : SESSION_PIN_FIRST_CHUNK_LEN;
    for (var i = 0; i < sessionPIN.length; i += chunkLen) {
      if (i > 0) {
        chunkLen = SESSION_PIN_CHUNK_LEN;
      }
      chunk = sessionPIN.substr(i, chunkLen);
      if (chunk.length >= SESSION_PIN_CHUNK_MIN_LEN || pin.length === 0) {
        pin.push(chunk);
      } else {
        pin[pin.length - 1] += chunk;
      }
    }
    if (tenantPIN) {
      pin.unshift(tenantPIN);
    }
    pin.push('#');
    return pin.join(' ');
  };

  Utils.CountriesArray = [
    { name: 'res_countryName_Andorra', code: 'AD' },
    { name: 'res_countryName_United_Arab_Emirates', code: 'AE' },
    { name: 'res_countryName_Afghanistan', code: 'AF' },
    { name: 'res_countryName_Antigua_and_Barbuda', code: 'AG' },
    { name: 'res_countryName_Anguilla', code: 'AI' },
    { name: 'res_countryName_Albania', code: 'AL' },
    { name: 'res_countryName_Armenia', code: 'AM' },
    { name: 'res_countryName_Netherland_Antilles', code: 'AN' },
    { name: 'res_countryName_Angola', code: 'AO' },
    { name: 'res_countryName_Antarctic', code: 'AQ' },
    { name: 'res_countryName_Argentina', code: 'AR' },
    { name: 'res_countryName_American_Samoa', code: 'AS' },
    { name: 'res_countryName_Austria', code: 'AT' },
    { name: 'res_countryName_Australia', code: 'AU' },
    { name: 'res_countryName_Aruba', code: 'AW' },
    { name: 'res_countryName_Azerbaijan', code: 'AZ' },
    { name: 'res_countryName_Bosnia_and_Herzegovina', code: 'BA' },
    { name: 'res_countryName_Barbados', code: 'BB' },
    { name: 'res_countryName_Bangladesh', code: 'BD' },
    { name: 'res_countryName_Belgium', code: 'BE' },
    { name: 'res_countryName_Burkina_Faso', code: 'BF' },
    { name: 'res_countryName_Bulgaria', code: 'BG' },
    { name: 'res_countryName_Bahrain', code: 'BH' },
    { name: 'res_countryName_Burundi', code: 'BI' },
    { name: 'res_countryName_Benin', code: 'BJ' },
    { name: 'res_countryName_Bermuda', code: 'BM' },
    { name: 'res_countryName_Brunei_Darussalam', code: 'BN' },
    { name: 'res_countryName_Bolivia', code: 'BO' },
    { name: 'res_countryName_Brazil', code: 'BR' },
    { name: 'res_countryName_Bahamas', code: 'BS' },
    { name: 'res_countryName_Bhutan', code: 'BT' },
    { name: 'res_countryName_Botswana', code: 'BW' },
    { name: 'res_countryName_Belarus', code: 'BY' },
    { name: 'res_countryName_Belize', code: 'BZ' },
    { name: 'res_countryName_Canada', code: 'CA' },
    { name: 'res_countryName_Dem_Republic_Congo', code: 'CD' },
    { name: 'res_countryName_Central_African_Republik', code: 'CF' },
    { name: 'res_countryName_Congo', code: 'CG' },
    { name: 'res_countryName_Switzerland', code: 'CH' },
    { name: 'res_countryName_Cote_d_Ivoire', code: 'CI' },
    { name: 'res_countryName_Chile', code: 'CL' },
    { name: 'res_countryName_Cameroon', code: 'CM' },
    { name: 'res_countryName_China_Peoples_Republic', code: 'CN' },
    { name: 'res_countryName_Colombia', code: 'CO' },
    { name: 'res_countryName_Costa_Rica', code: 'CR' },
    { name: 'res_countryName_Cuba', code: 'CU' },
    { name: 'res_countryName_Cap_Verde', code: 'CV' },
    { name: 'res_countryName_Cyprus', code: 'CY' },
    { name: 'res_countryName_Czech_Republik', code: 'CZ' },
    { name: 'res_countryName_Germany', code: 'DE' },
    { name: 'res_countryName_Djibouti', code: 'DJ' },
    { name: 'res_countryName_Denmark', code: 'DK' },
    { name: 'res_countryName_Dominica', code: 'DM' },
    { name: 'res_countryName_Dominican_Republik', code: 'DO' },
    { name: 'res_countryName_Algeria', code: 'DZ' },
    { name: 'res_countryName_Ecuador', code: 'EC' },
    { name: 'res_countryName_Estonia', code: 'EE' },
    { name: 'res_countryName_Egypt', code: 'EG' },
    { name: 'res_countryName_Westsahara', code: 'EH' },
    { name: 'res_countryName_Eritrea', code: 'ER' },
    { name: 'res_countryName_Spain', code: 'ES' },
    { name: 'res_countryName_Ethiopia', code: 'ET' },
    { name: 'res_countryName_Finland', code: 'FI' },
    { name: 'res_countryName_Fiji', code: 'FJ' },
    { name: 'res_countryName_Falkland_Islands', code: 'FK' },
    { name: 'res_countryName_Micronesia_Fed_States', code: 'FM' },
    { name: 'res_countryName_Faroer', code: 'FO' },
    { name: 'res_countryName_France', code: 'FR' },
    { name: 'res_countryName_Gaboon', code: 'GA' },
    { name: 'res_countryName_United_Kingdom', code: 'GB' },
    { name: 'res_countryName_United_Kingdom', code: 'UK' },
    { name: 'res_countryName_Grenada', code: 'GD' },
    { name: 'res_countryName_Georgia', code: 'GE' },
    { name: 'res_countryName_French_Guayana', code: 'GF' },
    { name: 'res_countryName_Ghana', code: 'GH' },
    { name: 'res_countryName_Gibraltar', code: 'GI' },
    { name: 'res_countryName_Greenland', code: 'GL' },
    { name: 'res_countryName_Gambia', code: 'GM' },
    { name: 'res_countryName_Guinea', code: 'GN' },
    { name: 'res_countryName_Guadeloupe', code: 'GP' },
    { name: 'res_countryName_Equatorial_Guinea', code: 'GQ' },
    { name: 'res_countryName_Greece', code: 'GR' },
    { name: 'res_countryName_South_Georgia_South_Sandwichisland', code: 'GS' },
    { name: 'res_countryName_Guatemala', code: 'GT' },
    { name: 'res_countryName_Guam', code: 'GU' },
    { name: 'res_countryName_Guinea_Bissau', code: 'GW' },
    { name: 'res_countryName_Guyana', code: 'GY' },
    { name: 'res_countryName_Hong_Kong', code: 'HK' },
    { name: 'res_countryName_Honduras', code: 'HN' },
    { name: 'res_countryName_Croatia', code: 'HR' },
    { name: 'res_countryName_Haiti', code: 'HT' },
    { name: 'res_countryName_Hungary', code: 'HU' },
    { name: 'res_countryName_Canary_Island', code: 'IC' },
    { name: 'res_countryName_Indonesia', code: 'ID' },
    { name: 'res_countryName_Ireland', code: 'IE' },
    { name: 'res_countryName_Israel', code: 'IL' },
    { name: 'res_countryName_India', code: 'IN' },
    { name: 'res_countryName_British_Indian_Ocean_Territory', code: 'IO' },
    { name: 'res_countryName_Iraq', code: 'IQ' },
    { name: 'res_countryName_Iran_Islamic_Republic', code: 'IR' },
    { name: 'res_countryName_Iceland', code: 'IS' },
    { name: 'res_countryName_Italy', code: 'IT' },
    { name: 'res_countryName_Jamaica', code: 'JM' },
    { name: 'res_countryName_Jordan', code: 'JO' },
    { name: 'res_countryName_Japan', code: 'JP' },
    { name: 'res_countryName_Kenya', code: 'KE' },
    { name: 'res_countryName_Kyrgyzstan', code: 'KG' },
    { name: 'res_countryName_Cambodia', code: 'KH' },
    { name: 'res_countryName_Kiribati', code: 'KI' },
    { name: 'res_countryName_Comoros', code: 'KM' },
    { name: 'res_countryName_St_Kitts_and_Nevis', code: 'KN' },
    { name: 'res_countryName_Korea_Dem_Peoples_Rep_North_Korea', code: 'KP' },
    { name: 'res_countryName_Korea_South', code: 'KR' },
    { name: 'res_countryName_Kuwait', code: 'KW' },
    { name: 'res_countryName_Cayman_Islands', code: 'KY' },
    { name: 'res_countryName_Kazakhstan', code: 'KZ' },
    { name: 'res_countryName_Laos_Democratic_Peoples_Republic', code: 'LA' },
    { name: 'res_countryName_Lebanon', code: 'LB' },
    { name: 'res_countryName_Saint_Lucia', code: 'LC' },
    { name: 'res_countryName_Liechtenstein', code: 'LI' },
    { name: 'res_countryName_Sri_Lanka', code: 'LK' },
    { name: 'res_countryName_Liberia', code: 'LR' },
    { name: 'res_countryName_Lesotho', code: 'LS' },
    { name: 'res_countryName_Lithuania', code: 'LT' },
    { name: 'res_countryName_Luxembourg', code: 'LU' },
    { name: 'res_countryName_Latvia', code: 'LV' },
    { name: 'res_countryName_Libya_Libyan_Arabian_Dschamahirija', code: 'LY' },
    { name: 'res_countryName_Morocco', code: 'MA' },
    { name: 'res_countryName_Monaco', code: 'MC' },
    { name: 'res_countryName_Moldova_Republic', code: 'MD' },
    { name: 'res_countryName_Montenegro', code: 'ME' },
    { name: 'res_countryName_Madagascar', code: 'MG' },
    { name: 'res_countryName_Marshall_Islands', code: 'MH' },
    { name: 'res_countryName_Mazedonia_former_Rep_of_Yugoslavia', code: 'MK' },
    { name: 'res_countryName_Mali', code: 'ML' },
    { name: 'res_countryName_Myanmar_Burma', code: 'MM' },
    { name: 'res_countryName_Mongolia', code: 'MN' },
    { name: 'res_countryName_Macau', code: 'MO' },
    { name: 'res_countryName_Northern_Marians', code: 'MP' },
    { name: 'res_countryName_Martinique', code: 'MQ' },
    { name: 'res_countryName_Mauretania', code: 'MR' },
    { name: 'res_countryName_Montserrat', code: 'MS' },
    { name: 'res_countryName_Malta', code: 'MT' },
    { name: 'res_countryName_Mauritius', code: 'MU' },
    { name: 'res_countryName_Maledives', code: 'MV' },
    { name: 'res_countryName_Malawi', code: 'MW' },
    { name: 'res_countryName_Mexico', code: 'MX' },
    { name: 'res_countryName_Malaysia', code: 'MY' },
    { name: 'res_countryName_Mozambique', code: 'MZ' },
    { name: 'res_countryName_Namibia', code: 'NA' },
    { name: 'res_countryName_New_Caledonia', code: 'NC' },
    { name: 'res_countryName_Niger', code: 'NE' },
    { name: 'res_countryName_Norfolk_Islands', code: 'NF' },
    { name: 'res_countryName_Nigeria', code: 'NG' },
    { name: 'res_countryName_Nicaragua', code: 'NI' },
    { name: 'res_countryName_Netherlands', code: 'NL' },
    { name: 'res_countryName_Norway', code: 'NO' },
    { name: 'res_countryName_Nepal', code: 'NP' },
    { name: 'res_countryName_Nauru', code: 'NR' },
    { name: 'res_countryName_New_Zealand', code: 'NZ' },
    { name: 'res_countryName_Oman', code: 'OM' },
    { name: 'res_countryName_Panama', code: 'PA' },
    { name: 'res_countryName_Peru', code: 'PE' },
    { name: 'res_countryName_French_Polynesia', code: 'PF' },
    { name: 'res_countryName_Papua_New_Guinea', code: 'PG' },
    { name: 'res_countryName_Philippines', code: 'PH' },
    { name: 'res_countryName_Pakistan', code: 'PK' },
    { name: 'res_countryName_Poland', code: 'PL' },
    { name: 'res_countryName_Saint_Pierre_and_Miquelon', code: 'PM' },
    { name: 'res_countryName_Pitcairn_Island', code: 'PN' },
    { name: 'res_countryName_Puerto_Rico', code: 'PR' },
    { name: 'res_countryName_Palestine_regions_occupied', code: 'PS' },
    { name: 'res_countryName_Portugal', code: 'PT' },
    { name: 'res_countryName_Palau', code: 'PW' },
    { name: 'res_countryName_Paraguay', code: 'PY' },
    { name: 'res_countryName_Quatar', code: 'QA' },
    { name: 'res_countryName_Reunion', code: 'RE' },
    { name: 'res_countryName_Romania', code: 'RO' },
    { name: 'res_countryName_Russian_Federation', code: 'RU' },
    { name: 'res_countryName_Rwanda', code: 'RW' },
    { name: 'res_countryName_Saudi_Arabia', code: 'SA' },
    { name: 'res_countryName_Solomonen', code: 'SB' },
    { name: 'res_countryName_Seychelles', code: 'SC' },
    { name: 'res_countryName_Sudan', code: 'SD' },
    { name: 'res_countryName_Sweden', code: 'SE' },
    { name: 'res_countryName_Singapore', code: 'SG' },
    { name: 'res_countryName_Saint_Helena', code: 'SH' },
    { name: 'res_countryName_Slovenia', code: 'SI' },
    { name: 'res_countryName_Spitzbergen', code: 'SJ' },
    { name: 'res_countryName_Slovakia', code: 'SK' },
    { name: 'res_countryName_Sierra_Leone', code: 'SL' },
    { name: 'res_countryName_San_Marino', code: 'SM' },
    { name: 'res_countryName_Senegal', code: 'SN' },
    { name: 'res_countryName_Somalia', code: 'SO' },
    { name: 'res_countryName_Suriname', code: 'SR' },
    { name: 'res_countryName_South_Sudan_Republic_of', code: 'SS' },
    { name: 'res_countryName_Sao_Tome_and_Principe', code: 'ST' },
    { name: 'res_countryName_El_Salvador', code: 'SV' },
    { name: 'res_countryName_Syria_Arabian_Republic', code: 'SY' },
    { name: 'res_countryName_Swaziland', code: 'SZ' },
    { name: 'res_countryName_Turks_and_Caicos_Islands', code: 'TC' },
    { name: 'res_countryName_Chad', code: 'TD' },
    { name: 'res_countryName_Togo', code: 'TG' },
    { name: 'res_countryName_Thailand', code: 'TH' },
    { name: 'res_countryName_Tajikstan', code: 'TJ' },
    { name: 'res_countryName_Tokelau', code: 'TK' },
    { name: 'res_countryName_Timor_Leste', code: 'TL' },
    { name: 'res_countryName_Turkmenistan', code: 'TM' },
    { name: 'res_countryName_Tunisia', code: 'TN' },
    { name: 'res_countryName_Tonga', code: 'TO' },
    { name: 'res_countryName_Turkey', code: 'TR' },
    { name: 'res_countryName_Trinidad_and_Tobago', code: 'TT' },
    { name: 'res_countryName_Tuvalu', code: 'TV' },
    { name: 'res_countryName_Taiwan', code: 'TW' },
    { name: 'res_countryName_Tanzania_United_Republic', code: 'TZ' },
    { name: 'res_countryName_Ukraine', code: 'UA' },
    { name: 'res_countryName_Uganda', code: 'UG' },
    { name: 'res_countryName_Minor_American_Oversea_Islands', code: 'UM' },
    { name: 'res_countryName_United_States_of_America', code: 'US' },
    { name: 'res_countryName_Uruguay', code: 'UY' },
    { name: 'res_countryName_Uzbekistan', code: 'UZ' },
    { name: 'res_countryName_Vatican_City', code: 'VA' },
    { name: 'res_countryName_Saint_Vincent_and_Grenadines', code: 'VC' },
    { name: 'res_countryName_Venezuela', code: 'VE' },
    { name: 'res_countryName_British_Virgin_Islands', code: 'VG' },
    { name: 'res_countryName_American_Virgin_Islands', code: 'VI' },
    { name: 'res_countryName_Vietnam', code: 'VN' },
    { name: 'res_countryName_Vanuatu', code: 'VU' },
    { name: 'res_countryName_Wallis_and_Futuna', code: 'WF' },
    { name: 'res_countryName_Samoa', code: 'WS' },
    { name: 'res_countryName_Kosovo', code: 'XK' },
    { name: 'res_countryName_Serbia', code: 'XS' },
    { name: 'res_countryName_Yemen', code: 'YE' },
    { name: 'res_countryName_Mayotte', code: 'YT' },
    { name: 'res_countryName_South_Africa', code: 'ZA' },
    { name: 'res_countryName_Zambia', code: 'ZM' },
    { name: 'res_countryName_Zimbabwe', code: 'ZW' }
  ];

  Utils.CountryCodeToCountryNameResourceMap = {};

  Utils.CountriesArray.forEach(function (country) {
    Utils.CountryCodeToCountryNameResourceMap[country.code] = country.name;
  });

  /**
   * Returns relative link if passed url is an accepted UnifyPhone link, otherwise returns empty string.
   * Currently accepted links are:
   *  - {host}/#/conversation/{convId}
   *  - {host}/#/conversation/{convId}?user={userId}
   *  - {host}/#/conversation/{convId}?item={itemId}
   *  - {host}/#/conversation/{convId}?item={itemId}&user={userId}
   *  - same for /open and /muted
   *  @param {String} host The host to test against, with or without protocol. If omitted, current origin is used.
   *  @param {String} link The link to test if it belongs to the given UnifyPhone host
   *  @returns {String} Relative link or empty string
   */
  Utils.getUnifyPhoneRelativeLink = function (host, link) {
    if (!link) {
      return '';
    }

    var PARTNER_SUBDOMAIN = 'partner.';
    host = host || getOrigin();

    if (link.startsWith('#/')) {
      // relative links
      return link;
    }
    // Normalization of params - failsafe
    if (!Utils.PROTOCOL_PATTERN.exec(host)) {
      host = HTTPS + host;
    }
    if (!host.endsWith('/')) {
      host = host + '/';
    }
    if (!Utils.PROTOCOL_PATTERN.exec(link)) {
      link = HTTPS + link;
    }
    if (!link.startsWith(host)) {
      // https://host.com and https://partner.host.com systems are the same,
      // it means that the links should lead to the same host which user is logged into (ANS-50527)
      var partnerDomain = HTTPS + PARTNER_SUBDOMAIN;
      var linkDomain = host.startsWith(partnerDomain) ? host.replace(partnerDomain, HTTPS) : host.replace(HTTPS, partnerDomain);
      if (link.startsWith(linkDomain)) {
        link = link.replace(linkDomain, host);
      }
    }

    if (link.length > host.length && link.startsWith(host)) {
      var urlHash = link.slice(host.length);
      var res = urlHash.match(Utils.UNIFY_PHONE_CONVERSATION_HASH_PATTERN);
      if (res) {
        // If conversation is muted, return it as normal so other users can see it if I share its link to them.
        // We also still need to support old links to open conversations, but we must replace with a normal link.
        return res[0].replace(/\/(muted|open)/gim, '/conversation');
      }
      var decodedURL = urlHash;
      try {
        decodedURL = decodeURI(decodedURL);
      } catch (e) {}

      // Check if this is a user, user profile, email and phone links (e.g. mention)
      res =
        urlHash.match(Utils.UNIFY_PHONE_LINKS_HASH_PATTERN) ||
        urlHash.match(Utils.UNIFY_PHONE_EMAIL_HASH_PATTERN) ||
        decodedURL.match(Utils.UNIFY_PHONE_HASH_TAG_SEARCH_PATTERN);
      return res && res[0];
    }
    return '';
  };

  /**
   * Function that tests if a given url is an accepted UnifyPhone link so it can be opened within unifyPhone.
   * Currently accepted links are listed in Utils.getUnifyPhoneRelativeLink.
   *  @param {String} host The host to test against, with or without protocol. If omitted, current origin is used.
   *  @param {String} link The link to test if it belongs to the given UnifyPhone host
   *  @returns {Boolean}
   */
  Utils.isUnifyPhoneLink = function (host, link) {
    return !!Utils.getUnifyPhoneRelativeLink(host, link);
  };

  /**
   * Gratefully borrowed from Underscore. http://underscorejs.org/#throttle
   *
   * Creates and returns a new, throttled version of the passed function, that,
   * when invoked repeatedly, will only actually call the original function at
   * most once per every wait milliseconds. Useful for rate-limiting events that
   * occur faster than you can keep up with.
   */
  Utils.throttle = function (func, wait, options) {
    var context, args, result;
    var timeout = null;
    var previous = 0;
    options = options || {};

    var later = function () {
      previous = options.leading === false ? 0 : Date.now();
      timeout = null;
      result = func.apply(context, args);
      context = args = null;
    };
    return function () {
      var now = Date.now();
      if (!previous && options.leading === false) {
        previous = now;
      }
      var remaining = wait - (now - previous);
      context = this;
      args = arguments;
      if (remaining <= 0) {
        if (timeout) {
          window.clearTimeout(timeout);
          timeout = null;
        }
        previous = now;
        result = func.apply(context, args);
        context = args = null;
      } else if (!timeout && options.trailing !== false) {
        timeout = window.setTimeout(later, remaining);
      }
      return result;
    };
  };

  Utils.hasEmptyPrototype = function (obj) {
    if (!obj || typeof obj !== 'object') {
      return false;
    }
    return Utils.isEmptyObject(Object.getPrototypeOf(obj));
  };

  Utils.compareElements = function (el1, el2) {
    if (el1 === el2) {
      return true;
    }
    if (!el1 || !el2) {
      return false;
    }
    if (typeof el1 !== 'object' || typeof el2 !== 'object') {
      return false;
    }

    if (el1 instanceof Array && el2 instanceof Array) {
      if (el1.length !== el2.length) {
        return false;
      }

      // Create a copy of arr2
      var arr2 = el2.slice();

      return el1.every(function (elem1) {
        return arr2.some(function (elem2, idx2) {
          if (Utils.compareElements(elem1, elem2)) {
            arr2.splice(idx2, 1);
            return true;
          }
          return false;
        });
      });
    }

    if (!Utils.hasEmptyPrototype(el1) || !Utils.hasEmptyPrototype(el2)) {
      return false;
    }

    for (var key in el1) {
      // Don't compare inheritable or angular properties
      if (el1.hasOwnProperty(key) && key.indexOf('$$') !== 0) {
        if (!Utils.compareElements(el1[key], el2[key])) {
          return false;
        }
      }
    }
    return true;
  };

  Utils.copyToClipboard = function (copyText, cb, asText) {
    // Do not use document.queryCommandSupported('copy') to check if copy is enabled - seems it's buggy now
    // https://code.google.com/p/chromium/issues/detail?id=476508
    var selection;
    var range;
    var buffer;
    if (!copyText) {
      return;
    }

    try {
      range = document.createRange();
      selection = window.getSelection();

      buffer = asText ? document.createElement('textarea') : document.createElement('div');

      if (!asText) {
        // Replace new line with <br> for correct view in Outlook
        copyText = copyText.replace(/\r\n/gi, '<br>');
      }

      // Note: Chrome doesn't override the bg-color with transparent in Outlook, so use the #fff;
      buffer.style.backgroundColor = '#fff';
      buffer.style.font = 'normal normal 15px Calibri, sans-serif';
      buffer.style['white-space'] = 'pre-wrap';
      if (asText) {
        buffer.value = copyText;
        buffer.setAttribute('readonly', '');
        // Hide and append the element
        buffer.style.position = 'absolute';
        buffer.style.left = '-9999px';
        document.body.appendChild(buffer);

        selection.removeAllRanges();
        buffer.select();
        buffer.setSelectionRange(0, copyText.length);
      } else {
        buffer.innerHTML = copyText;
        document.body.appendChild(buffer);

        range.selectNode(buffer);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      var successful = document.execCommand('copy');
      if (successful) {
        cb && cb();
      } else {
        cb && cb('Failed to copy');
      }
    } catch (e) {
      logger.error('[Utils]: Copy to clipboard failed');
      cb && cb('Internal error');
    } finally {
      if (selection) {
        if (typeof selection.removeRange === 'function' && !asText) {
          selection.removeRange(range);
        } else {
          selection.removeAllRanges();
        }
      }

      if (buffer) {
        document.body.removeChild(buffer);
      }
    }
  };

  Utils.toCamelCase = function (str) {
    if (!str || typeof str !== 'string') {
      return '';
    }
    var arr = str.toLowerCase().split(/[_-]/);
    for (var i = 1; i < arr.length; i++) {
      arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1);
    }
    return arr.join('');
  };

  Utils.sslProxify = function (url, protocol) {
    if (url && !Utils.PROTOCOL_PATTERN.exec(url)) {
      url = url.startsWith('//') ? url.replace('//', protocol) : protocol + url;
    }
    if (url && url.startsWith('http://')) {
      return SSL_IMAGE_PROXY + encodeURIComponent(url);
    }
    return url;
  };

  Utils.parsePreviewObject = function (url, obj) {
    var previewURL = Utils.PROTOCOL_PATTERN.test(url) ? url : 'http://' + url;

    var res = {
      preview: {
        type: obj.type,
        title: obj.title || '',
        description: obj.description || '',
        provider: obj.provider_name || '',
        srcURL: url,
        previewURL: previewURL,
        imageURI: obj.thumbnail_url
      }
    };

    res.previewImageURISecure = Utils.sslProxify(res.preview.imageURI, Utils.getUrlProtocol(previewURL));

    switch (obj.type) {
      case 'photo':
        res.previewImageURISecure = Utils.sslProxify(url, Utils.getUrlProtocol(url));
        break;
      case 'video':
        res.preview.html = obj.html;
        break;
      case 'rich':
        res.preview.type = 'link';
        break;
    }

    return res;
  };

  Utils.appIsFocused = function () {
    return document.hasFocus();
  };

  Utils.validatePreviewProtocol = function (html) {
    if (!html) {
      return '';
    }
    if (html.match(/https:/)) {
      return html;
    }
    return html.replace('src="//', 'src="https://');
  };

  Utils.getUrlProtocol = function (url) {
    if (url && Utils.PROTOCOL_PATTERN.exec(url)) {
      return url.split('/')[0] + '//';
    }
    return 'http://';
  };

  // Used for switching between tabs (feed, details, questions...)
  // only one name can be selected (=true)
  // selecting: tabSelector.[tab-name] = true (setter)
  // checking:  tabSelector.[tab-name]        (getter)
  // tabSelector.selected : returns last selected tab name
  // first tab name during initialization will be selected automatically
  Utils.TabSelector = function (tabNames) {
    if (!Array.isArray(tabNames) || tabNames.length === 0) {
      return;
    }

    var _that = this;
    var _tabs = {};
    var _selected = '';

    function reset() {
      Object.keys(_tabs).forEach(function (name) {
        _tabs[name] = false;
      });
    }

    tabNames.forEach(function (name, idx) {
      if (idx === 0) {
        _tabs[name] = true;
        _selected = name;
      } else {
        _tabs[name] = false;
      }
      Object.defineProperty(_that, name, {
        get: function () {
          return _tabs[name];
        },
        set: function (value) {
          if (value) {
            reset();
            _tabs[name] = true;
            _selected = name;
          }
        },
        enumerable: true,
        configurable: false
      });
    });

    Object.defineProperty(this, 'selected', {
      get: function () {
        return _selected;
      },
      enumerable: true,
      configurable: false
    });

    Object.defineProperty(this, 'selectedIdx', {
      get: function () {
        return tabNames.indexOf(_selected);
      },
      enumerable: true,
      configurable: false
    });
  };

  /**
   * Get current origin, e.g. https://eu.yourcircuit.com
   */
  Utils.getOrigin = getOrigin;

  /**
   * Function to parse a querystring
   */
  Utils.parseQS = function (qs) {
    var params = {};
    qs = qs.substring(1);
    var regex = /([^&=]+)=([^&]*)/g;
    var m = regex.exec(qs);
    while (m) {
      params[decodeURIComponent(m[1])] = decodeURIComponent(m[2]);
      m = regex.exec(qs);
    }
    return params;
  };

  /**
   * Function to build a querystring
   */
  Utils.toQS = function (obj) {
    var str = [];
    for (var p in obj) {
      if (obj.hasOwnProperty(p) && obj[p] !== undefined) {
        var pValue = obj[p];
        if (!Array.isArray(pValue)) {
          str.push(encodeURIComponent(p) + '=' + encodeURIComponent(pValue));
        } else {
          pValue.forEach(function (elem) {
            str.push(encodeURIComponent(p) + '=' + encodeURIComponent(elem));
          });
        }
      }
    }
    return str.join('&');
  };

  Utils.extractConfigurableText = function (configurableTexts, type, language) {
    var requestedTranslations =
      configurableTexts &&
      configurableTexts.find(function (configurableText) {
        return configurableText.type === type;
      });
    if (!requestedTranslations || !requestedTranslations.texts) {
      return '';
    }

    var findText = function (lang) {
      return requestedTranslations.texts.find(function (t) {
        return t.language === lang;
      });
    };

    var text;
    if (language) {
      // Look for a translation for the given language (e.g. 'en-US')
      text = findText(language);
      if (!text) {
        // Remove the country code and look for a translation for the given language (e.g. 'en')
        language = language.split('-', 1)[0];
        text = findText(language);
      }
    }
    if (!text) {
      // Look for a default translation
      text = findText('default');
    }
    return text && text.translation;
  };

  /**
   * Use this function to select one device from the preferred list based on the list of
   * available devices
   *
   * @param {Array} available - List of available devices
   * @param {Array} preferred - List of preferred devices (in order of preference)
   * @param {Boolean} matchByLabel - (Optional) Match devices by label if matching by ID fails
   * @returns {Object} - Selected device object or null if no preferred devices were found
   */
  Utils.selectMediaDevice = function (available, preferred, matchByLabel) {
    if (!available || !preferred) {
      return null;
    }
    var found;
    preferred.some(function (p) {
      if (typeof p.id === 'undefined') {
        // There should be only 1 "idless" device and it's the OS default device
        found = p;
      } else {
        found = available.find(function (a) {
          return p.id === a.id;
        });
      }
      return !!found;
    });
    if (!found && matchByLabel) {
      preferred.some(function (p) {
        found = available.find(function (a) {
          return p.label === a.label;
        });
        return !!found;
      });
    }
    return found;
  };

  Utils.addDeviceToPreferredList = function (preferred, device) {
    if (!device) {
      return preferred || [];
    }
    if (!preferred) {
      return [device];
    }

    // First check if device is already in the preferred list
    // Find by ID
    var foundIndex = preferred.findIndex(function (d) {
      return d.id === device.id;
    });
    if (foundIndex !== -1) {
      // If found, remove it from the list
      preferred.splice(foundIndex, 1);
    }
    preferred.unshift(device); // Save as first element
    preferred = preferred.slice(0, MAX_NUM_OF_PREFERRED_DEVICES); // Limit the number of saved devices
    return preferred;
  };

  /**
   * Remove a single element from an array.
   * @param {Array} array - Array of which to remove an element
   * @param {any} elem - Element to remove
   */
  Utils.removeArrayElement = function (array, elem) {
    if (Array.isArray(array) && typeof elem !== 'undefined' && elem) {
      var index = array.indexOf(elem);
      if (index > -1) {
        array.splice(index, 1);
      }
    }
  };

  function binaryInsert(array, arrayElement, compareValueFunction, idField, leftIndex, rightIndex) {
    if (!arrayElement || !Array.isArray(array)) {
      return -1;
    }
    if (array.length === 0) {
      array.push(arrayElement);
      return 0;
    }
    leftIndex = leftIndex || 0;
    rightIndex = rightIndex || array.length - 1;

    if (rightIndex - leftIndex <= 1) {
      if (compareValueFunction(arrayElement, array[leftIndex]) < 0) {
        array.splice(leftIndex, 0, arrayElement);
        return leftIndex;
      } else if (compareValueFunction(arrayElement, array[rightIndex]) > 0) {
        array.splice(rightIndex + 1, 0, arrayElement);
        return rightIndex + 1;
      } else {
        // Make sure the arrayElement is not already in the array
        if (arrayElement[idField] === array[leftIndex][idField]) {
          array[leftIndex] = arrayElement;
          return leftIndex;
        }
        if (arrayElement[idField] === array[rightIndex][idField]) {
          array[rightIndex] = arrayElement;
          return rightIndex;
        }
        array.splice(rightIndex, 0, arrayElement);
        return rightIndex;
      }
    }
    var midIndex = Math.floor((rightIndex - leftIndex) / 2) + leftIndex;

    if (compareValueFunction(arrayElement, array[midIndex]) < 0) {
      return binaryInsert(array, arrayElement, compareValueFunction, idField, leftIndex, midIndex);
    } else {
      return binaryInsert(array, arrayElement, compareValueFunction, idField, midIndex, rightIndex);
    }
  }

  Utils.binaryInsert = function (array, arrayElement, compareValueFunction, idField) {
    return binaryInsert(array, arrayElement, compareValueFunction, idField || 'id');
  };

  Utils.isEmptyArray = function (array) {
    return array.length === 0;
  };

  Utils.lastArrayElement = function (array) {
    if (array.length > 0) {
      return array[array.length - 1];
    }
    return undefined;
  };

  Utils.shuffleArray = function (array) {
    if (array.length) {
      var l = array.length;
      for (var i = 0; i < l; i++) {
        var r = Math.floor(Math.random() * l);
        var o = array[r];
        array[r] = array[i];
        array[i] = o;
      }
    }
    return array;
  };

  Utils.randomArrayCopy = function (array, numElems) {
    if (Array.isArray(array)) {
      var copy = array.slice(0);
      Utils.shuffleArray(copy);
      return copy.splice(0, numElems);
    }
    return null;
  };

  // Move one element from old position to new position
  Utils.moveArrayElement = function (array, oldPos, newPos) {
    if (typeof oldPos !== 'number' || typeof newPos !== 'number' || oldPos < 0 || oldPos >= array.length || newPos < 0 || oldPos === newPos) {
      return;
    }
    for (var k = array.length; k <= newPos; k++) {
      array.push(undefined);
    }
    array.splice(newPos, 0, array.splice(oldPos, 1)[0]);
  };

  // Empties an array. The most performant way to achieve this.
  Utils.emptyArray = function (array) {
    if (!array.length) {
      return;
    }
    while (array.length > 0) {
      array.pop();
    }
  };

  Utils.getAvatarImageFormat = function (image) {
    var regex = /[\w]+:[\w]+\/png/;
    return regex.exec(image) ? 'png' : 'jpeg';
  };

  Utils.displayNameToInitials = function (displayName) {
    if (!displayName) {
      return '';
    }
    var atPos = displayName.indexOf('@'); // EMail address ?
    var names = atPos >= 0 ? displayName.slice(0, atPos).split('.') : displayName.split(/[ ]+/);
    return ((names[0][0] || '') + (names[1] ? names[1][0] || '' : '')).toUpperCase();
  };

  var AVAILABLE_EXTENSIONS = ['pdf', 'doc', 'xls', 'ppt', 'html', 'csv', 'jpg', 'png', 'zip', 'log', 'video', 'audio'];
  Utils.getIconFile = function (extension) {
    return AVAILABLE_EXTENSIONS.includes(extension) ? 'icon-file-' + extension : 'icon-file-generic';
  };

  ///////////////////////////////////////////////////////////////////////////////////////////////////
  //
  // NGTC Utils
  //
  ///////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   *
   * @description
   * Deserializes a JSON string. Replacement of angular.fromJson
   *
   * @param {string} json JSON string to deserialize.
   * @returns {Object|Array|string|number} Deserialized JSON string.
   */
  Utils.fromJson = function fromJson(json) {
    return typeof json === 'string' // isString(json)
      ? JSON.parse(json)
      : json;
  };

  ///////////////////////////////////////////////////////////////////////////////////////////////////
  //
  // Polyfills for Object
  //
  ///////////////////////////////////////////////////////////////////////////////////////////////////

  if (typeof Object.values !== 'function') {
    Object.values = function (obj) {
      return Object.keys(obj).map(function (key) {
        return obj[key];
      });
    };
  }

  if (typeof Object.assign !== 'function') {
    Object.assign = function assign(target) {
      if (target === null || target === undefined) {
        throw new TypeError('Cannot convert undefined or null to object');
      }

      var to = Object(target);

      for (var index = 1; index < arguments.length; index++) {
        var nextSource = arguments[index];

        if (nextSource) {
          for (var nextKey in nextSource) {
            // Avoid bugs when hasOwnProperty is shadowed
            if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
              to[nextKey] = nextSource[nextKey];
            }
          }
        }
      }
      return to;
    };
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////
  //
  // Polyfills for String objects
  //
  ///////////////////////////////////////////////////////////////////////////////////////////////////

  // Simplified polyfill for ECMAScript 2015 String.prototype.startsWith().
  if (!String.prototype.startsWith) {
    Object.defineProperty(String.prototype, 'startsWith', {
      value: function (searchString, position) {
        if (searchString === null || searchString === undefined) {
          return false;
        }
        searchString = searchString.toString();
        position = Math.floor(position) || 0;
        return this.substr(position, searchString.length) === searchString;
      }
    });
  }

  // Simplified polyfill for ECMAScript 2015 String.prototype.endsWith().
  if (!String.prototype.endsWith) {
    Object.defineProperty(String.prototype, 'endsWith', {
      value: function (searchString, position) {
        if (searchString === null || searchString === undefined) {
          return false;
        }
        if (position === undefined) {
          position = this.length;
        } else {
          position = Math.min(Math.floor(position) || 0, this.length);
        }
        searchString = searchString.toString();
        position -= searchString.length;
        if (position < 0) {
          return false;
        }
        return this.substr(position, searchString.length) === searchString;
      }
    });
  }

  // Simplified polyfill for ECMAScript 2015 String.prototype.includes().
  if (!String.prototype.includes) {
    Object.defineProperty(String.prototype, 'includes', {
      value: function (value) {
        return this.indexOf(value) !== -1;
      }
    });
  }

  // Polyfill for ECMAScript 2015 String.prototype.repeat()
  // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/repeat
  if (!String.prototype.repeat) {
    Object.defineProperty(String.prototype, 'repeat', {
      value: function (count) {
        if (this === null) {
          throw new TypeError("can't convert " + this + ' to object');
        }
        var str = '' + this;
        count = +count;
        if (isNaN(count)) {
          count = 0;
        }
        if (count < 0) {
          throw new RangeError('repeat count must be non-negative');
        }
        if (count === Infinity) {
          throw new RangeError('repeat count must be less than infinity');
        }
        count = Math.floor(count);
        if (str.length === 0 || count === 0) {
          return '';
        }
        // Ensuring count is a 31-bit integer allows us to heavily optimize the
        // main part. But anyway, most current (August 2014) browsers can't handle
        // strings 1 << 28 chars or longer, so:

        // eslint-disable-next-line no-bitwise
        if (str.length * count >= 1 << 28) {
          throw new RangeError('repeat count must not overflow maximum string size');
        }
        var rpt = '';
        for (var i = 0; i < count; i++) {
          rpt += str;
        }
        return rpt;
      }
    });
  }

  // Polyfill for ECMAScript 2017 String.prototype.padStart()
  if (!String.prototype.padStart) {
    Object.defineProperty(String.prototype, 'padStart', {
      value: function padStart(targetLength, padString) {
        // eslint-disable-next-line no-bitwise
        targetLength = targetLength >> 0; // Floor if number or convert non-number to 0;
        padString = String(typeof padString !== 'undefined' ? padString : ' ');
        if (this.length > targetLength) {
          return String(this);
        } else {
          targetLength = targetLength - this.length;
          if (targetLength > padString.length) {
            padString += padString.repeat(targetLength / padString.length); // append to original to ensure we are longer than needed
          }
          return padString.slice(0, targetLength) + String(this);
        }
      }
    });
  }

  // Polyfill for ECMAScript 2017 String.prototype.padEnd()
  if (!String.prototype.padEnd) {
    Object.defineProperty(String.prototype, 'padEnd', {
      value: function padEnd(targetLength, padString) {
        // eslint-disable-next-line no-bitwise
        targetLength = targetLength >> 0; // Floor if number or convert non-number to 0;
        padString = String(typeof padString !== 'undefined' ? padString : ' ');
        if (this.length > targetLength) {
          return String(this);
        } else {
          targetLength = targetLength - this.length;
          if (targetLength > padString.length) {
            padString += padString.repeat(targetLength / padString.length); // append to original to ensure we are longer than needed
          }
          return String(this) + padString.slice(0, targetLength);
        }
      }
    });
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////
  //
  // New APIs for String objects
  //
  ///////////////////////////////////////////////////////////////////////////////////////////////////

  // Equivalent of C# String.Format method
  Object.defineProperty(String.prototype, 'format', {
    value: function () {
      var args = arguments;
      return this.replace(/{(\d+)}/g, function (match, index) {
        if (args[index] !== undefined && args[index] !== null) {
          return args[index];
        }
        return match;
      });
    }
  });

  // Replaces character at given index
  Object.defineProperty(String.prototype, 'replaceAt', {
    value: function (index, character) {
      return this.substr(0, index) + character + this.substr(index + character.length);
    }
  });

  Object.defineProperty(String.prototype, 'randomCharacter', {
    value: function () {
      return this[Math.floor(this.length * Math.random())];
    }
  });

  ///////////////////////////////////////////////////////////////////////////////////////////////////
  //
  // Polyfills for Array objects
  //
  ///////////////////////////////////////////////////////////////////////////////////////////////////

  // Production steps of ECMA-262, Edition 6, 22.1.2.1
  if (!Array.from) {
    Array.from = (function () {
      var toStr = Object.prototype.toString;
      var isCallable = function (fn) {
        return typeof fn === 'function' || toStr.call(fn) === '[object Function]';
      };
      var toInteger = function (value) {
        var number = Number(value);
        if (isNaN(number)) {
          return 0;
        }
        if (number === 0 || !isFinite(number)) {
          return number;
        }
        return (number > 0 ? 1 : -1) * Math.floor(Math.abs(number));
      };
      var maxSafeInteger = Math.pow(2, 53) - 1;
      var toLength = function (value) {
        var len = toInteger(value);
        return Math.min(Math.max(len, 0), maxSafeInteger);
      };

      // The length property of the from method is 1.
      return function from(arrayLike /*, mapFn, thisArg */) {
        // 1. Let C be the this value.
        var C = this;

        // 2. Let items be ToObject(arrayLike).
        var items = Object(arrayLike);

        // 3. ReturnIfAbrupt(items).
        if (arrayLike === null) {
          throw new TypeError('Array.from requires an array-like object - not null or undefined');
        }

        // 4. If mapfn is undefined, then let mapping be false.
        var mapFn = arguments.length > 1 ? arguments[1] : void undefined;
        var T;
        if (typeof mapFn !== 'undefined') {
          // 5. else
          // 5. a If IsCallable(mapfn) is false, throw a TypeError exception.
          if (!isCallable(mapFn)) {
            throw new TypeError('Array.from: when provided, the second argument must be a function');
          }

          // 5. b. If thisArg was supplied, let T be thisArg; else let T be undefined.
          if (arguments.length > 2) {
            T = arguments[2];
          }
        }

        // 10. Let lenValue be Get(items, "length").
        // 11. Let len be ToLength(lenValue).
        var len = toLength(items.length);

        // 13. If IsConstructor(C) is true, then
        // 13. a. Let A be the result of calling the [[Construct]] internal method
        // of C with an argument list containing the single item len.
        // 14. a. Else, Let A be ArrayCreate(len).
        var A = isCallable(C) ? Object(new C(len)) : new Array(len);

        // 16. Let k be 0.
        var k = 0;
        // 17. Repeat, while k < len… (also steps a - h)
        var kValue;
        while (k < len) {
          kValue = items[k];
          if (mapFn) {
            A[k] = typeof T === 'undefined' ? mapFn(kValue, k) : mapFn.call(T, kValue, k);
          } else {
            A[k] = kValue;
          }
          k += 1;
        }
        // 18. Let putStatus be Put(A, "length", len, true).
        A.length = len;
        // 20. Return A.
        return A;
      };
    })();
  }

  // Simplified polyfill for ECMAScript 2016 Array.prototype.includes().
  if (!Array.prototype.includes) {
    Object.defineProperty(Array.prototype, 'includes', {
      value: function (value) {
        return this.indexOf(value) !== -1;
      }
    });
  }

  // Polyfill for ECMAScript 2015 (ES6) Array.prototype.find().
  // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/find
  if (!Array.prototype.find) {
    Object.defineProperty(Array.prototype, 'find', {
      value: function (predicate) {
        if (this === null || this === undefined) {
          throw new TypeError('Array.prototype.find called on null or undefined');
        }
        if (typeof predicate !== 'function') {
          throw new TypeError('predicate must be a function');
        }
        var list = Object(this);
        // eslint-disable-next-line no-bitwise
        var length = list.length >>> 0;
        var thisArg = arguments[1];
        var value;

        for (var i = 0; i < length; i++) {
          value = list[i];
          if (predicate.call(thisArg, value, i, list)) {
            return value;
          }
        }
        return undefined;
      }
    });
  }

  // Polyfill for ECMAScript 2015 (ES6) Array.prototype.findIndex().
  // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/findIndex
  if (!Array.prototype.findIndex) {
    Object.defineProperty(Array.prototype, 'findIndex', {
      value: function (predicate) {
        if (this === null || this === undefined) {
          throw new TypeError('Array.prototype.findIndex called on null or undefined');
        }
        if (typeof predicate !== 'function') {
          throw new TypeError('predicate must be a function');
        }
        var list = Object(this);
        // eslint-disable-next-line no-bitwise
        var length = list.length >>> 0;
        var thisArg = arguments[1];
        var value;

        for (var i = 0; i < length; i++) {
          value = list[i];
          if (predicate.call(thisArg, value, i, list)) {
            return i;
          }
        }
        return -1;
      }
    });
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////
  //
  // Polyfills for Number object
  //
  ///////////////////////////////////////////////////////////////////////////////////////////////////
  if (!Number.isInteger) {
    // Number.isInteger() method
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/isInteger
    Number.isInteger = function (value) {
      return typeof value === 'number' && isFinite(value) && Math.floor(value) === value;
    };
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////
  //
  // New static APIs for RegExp type
  //
  ///////////////////////////////////////////////////////////////////////////////////////////////////
  RegExp.escape = function (str) {
    if (!str) {
      return '';
    }
    return str.replace(/[.*+?|()[\]{}\\$^]/g, '\\$&');
  };

  ///////////////////////////////////////////////////////////////////////////////////////////////////
  //
  // New APIs for Date objects
  //
  ///////////////////////////////////////////////////////////////////////////////////////////////////
  Date.prototype.moveToEndOfDay = function () {
    return this.set({ hour: 23, minute: 59, second: 59 });
  };

  Date.prototype.moveToFirstDayOfYear = function () {
    return this.set({ day: 1, month: 0 });
  };

  Date.prototype.moveToLastDayOfYear = function () {
    return this.set({ month: 11 }).moveToLastDayOfMonth();
  };

  ///////////////////////////////////////////////////////////////////////////////////////////////////
  //
  // Wrapper for the libphonenumber object
  //
  ///////////////////////////////////////////////////////////////////////////////////////////////////
  var PhoneNumberFormatter = {};
  /**
   * Formats phone number (e.g: +15619231287 -> +1 561 923-1287)
   *
   * @param {String} phoneNumber Phone number to be formatted. It must be in FQN format, otherwise this
   * method will just return the original unformatted number.
   * @param {Boolean} [stripDtmf] If true, the DTMF digits (preceded by ,) will be stripped out
   */
  PhoneNumberFormatter.format = function (phoneNumber, stripDtmf) {
    if (typeof libphonenumber !== 'undefined' && phoneNumber && phoneNumber.charAt(0) === '+') {
      try {
        var match = phoneNumber.match(Utils.PHONE_DIAL_WITH_PIN_PATTERN);
        var append = '';
        if (match) {
          phoneNumber = match[1];
          append = stripDtmf ? '' : match[2];
        }
        var phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
        var number = phoneUtil.parseAndKeepRawInput(phoneNumber, null);
        if (phoneUtil.isValidNumber(number)) {
          phoneNumber = phoneUtil.format(number, libphonenumber.PhoneNumberFormat.INTERNATIONAL) + append;
        }
      } catch (e) {}
    }
    return phoneNumber;
  };

  // Exports
  unifyPhone.Utils = Utils;
  unifyPhone.PhoneNumberFormatter = PhoneNumberFormatter;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
// Define globals for JSHint

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  var Constants = Object.freeze({
    WSMessageType: {
      REQUEST: 'REQUEST',
      RESPONSE: 'RESPONSE',
      EVENT: 'EVENT'
    },

    ContentType: {
      SYSTEM: 'SYSTEM',
      NONE: 'NONE',
      EXTENSION: 'EXTENSION', // Not used by client
      CONVERSATION: 'CONVERSATION',
      USER: 'USER',
      RTC_SESSION: 'RTC_SESSION',
      RTC_CALL: 'RTC_CALL',
      SEARCH: 'SEARCH',
      KEEPALIVE: 'KEEPALIVE', // Not used by client
      INSTRUMENTATION: 'INSTRUMENTATION',
      GUEST: 'GUEST',
      THIRDPARTYCONNECTORS: 'THIRDPARTYCONNECTORS',
      USER_TO_USER: 'USER_TO_USER',
      VERSION: 'VERSION',
      ACCOUNT: 'ACCOUNT',
      CONTACT: 'CONTACT',

      // Content type used exclusively between client and access server
      USER_DATA: 'USER_DATA',
      CTI_CALL: 'CTI_CALL'
    },

    ReturnCode: {
      // Client API Errors
      OK: 'OK',
      AUTHORIZATION_FAILED: 'AUTHORIZATION_FAILED',
      AUTHORIZATION_REQUIRED: 'AUTHORIZATION_REQUIRED',
      SERVICE_EXCEPTION: 'SERVICE_EXCEPTION',
      PROTOBUF_CONVERSION_EXCEPTION: 'PROTOBUF_CONVERSION_EXCEPTION', // Client API V1
      CONVERSION_EXCEPTION: 'CONVERSION_EXCEPTION',
      NO_MESSAGE_HANDLER: 'NO_MESSAGE_HANDLER',
      INVALID_CLIENT: 'INVALID_CLIENT',
      NO_RESULT: 'NO_RESULT',
      OPERATION_NOT_SUPPORTED: 'OPERATION_NOT_SUPPORTED',
      ENTITY_ALREADY_EXISTS: 'ENTITY_ALREADY_EXISTS',
      FRAME_SIZE_TO_LONG: 'FRAME_SIZE_TO_LONG',
      BACKEND_UNAVAILABLE: 'BACKEND_UNAVAILABLE',
      OVERLOADED: 'OVERLOADED',
      OLD_VERSION: 'OLD_VERSION',
      STORAGE_OVERLOADED: 'STORAGE_OVERLOADED',
      USER_NOT_FOUND: 'USER_NOT_FOUND',
      MAX_FAVORITES_REACHED: 'MAX_FAVORITES_REACHED',
      MAX_CONTACTS_REACHED: 'MAX_CONTACTS_REACHED',

      // Access Server Errors
      SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

      // Internal Client Errors
      REQUEST_TIMEOUT: 'REQUEST_TIMEOUT',
      DISCONNECTED: 'DISCONNECTED',
      INVALID_MESSAGE: 'INVALID_MESSAGE',
      FAILED_TO_SEND: 'FAILED_TO_SEND',
      UNEXPECTED_RESPONSE: 'UNEXPECTED_RESPONSE',
      MISSING_REQUIRED_PARAMETER: 'MISSING_REQUIRED_PARAMETER',
      CHOOSE_DESKTOP_MEDIA_CANCELLED: 'CHOOSE_DESKTOP_MEDIA_CANCELLED',
      REQUEST_CANCELED: 'REQUEST_CANCELED',

      // SDK Errors
      SDK_ERROR: 'SDK_ERROR'
    },

    // Error codes used when return code is SERVICE_EXCEPTION
    ErrorCode: {
      UNEXPECTED_ERROR: 'UNEXPECTED_ERROR',
      CONSTRAINT_VIOLATION: 'CONSTRAINT_VIOLATION',
      MAX_NUM_CONV_PARTICIPANTS: 'MAX_NUM_CONV_PARTICIPANTS',
      THIRDPARTY_ERROR: 'THIRDPARTY_ERROR',
      AUTHORIZATION_FAILED: 'AUTHORIZATION_FAILED',
      PERMISSION_DENIED: 'PERMISSION_DENIED',
      RTC_NO_MEDIA_NODES_AVAILABLE: 'RTC_NO_MEDIA_NODES_AVAILABLE',
      RTC_CONCURRENT_INCOMING_CALL: 'RTC_CONCURRENT_INCOMING_CALL',
      DUPLICATED_ENTITY_ERROR: 'DUPLICATED_ENTITY_ERROR',
      RTC_MEDIA_NODE_UNREACHABLE: 'RTC_MEDIA_NODE_UNREACHABLE',
      CLOUD_TELEPHONY_ERROR: 'CLOUD_TELEPHONY_ERROR',
      UNIFY_PHONE_EXCEPTION: 'UNIFY_PHONE_EXCEPTION'
    },

    // Different types of UNIFY_PHONE_EXCEPTION errorCode
    UnifyPhoneExceptionErrorCode: {
      INWEB_00000C: 'INWEB-00000C', // For illegal arguments (should usually not happen, just during development)
      INWEB_00001C: 'INWEB-00001C', // Webhook does not exist
      INWEB_00002C: 'INWEB-00002C', // Conversation does not exist for the webhook
      INWEB_00003C: 'INWEB-00003C', // The creation of the webhook with the given parameters is not possible
      INWEB_00004C: 'INWEB-00004C', // The maximum number of webhooks for a tenant has been reached
      INWEB_00005C: 'INWEB-00005C' // The maximum number of webhooks for a conversation has been reached
    },

    ThirdPartyError: {
      UNSUPPORTED_PROVIDER: 'UNSUPPORTED_PROVIDER', // Unknown provider
      BAD_REQUEST: 'BAD_REQUEST', // A bad request was send to the provider server
      GENERAL_ERROR: 'GENERAL_ERROR', // A general Error occured
      INSUFFICIENT_PERMISSION: 'INSUFFICIENT_PERMISSION', // There are insufficient Permission accessing a resource
      NOT_AUTHORIZED: 'NOT_AUTHORIZED', // User is not authorized
      RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND', // Resource could not be found
      SERVER_DOWN: 'SERVER_DOWN', // Server is down. No response from server
      SESSION_EXPIRED: 'SESSION_EXPIRED', // Session has been expired
      UNSUPPORTED_FILE_TYPE: 'UNSUPPORTED_FILE_TYPE', // The file is not supported for online editing
      WRONG_SERVER: 'WRONG_SERVER', // The server of the file attached, is different from the server the user is connected to
      DUPLICATE_RESOURCE: 'DUPLICATE_RESOURCE', // Move conversation failed because of duplicate name
      PARTIALLY_COMPLETED: 'PARTIALLY_COMPLETED', // Move conversation created, members partially added and/or post message failed

      // Internal client errors
      EMPTY_SETTINGS: 'EMPTY_SETTINGS', // Settings not provided (validation error)
      EMPTY_USERNAME: 'EMPTY_USERNAME', // Username not provided (validation error)
      EMPTY_PASSWORD: 'EMPTY_PASSWORD', // Password not provided (validation error)
      EMPTY_SERVER_ADDRESS: 'EMPTY_SERVER_ADDRESS', // Server address not provided (validation error)
      INVALID_SERVER_ADDRESS: 'INVALID_SERVER_ADDRESS', // Invalid server url (validation error)
      NOT_CONNECTED: 'NOT_CONNECTED', // Not connected to a thirdparty provider
      ALREADY_CONNECTED: 'ALREADY_CONNECTED', // Already connected to a thirdparty provider
      INVALID_ITEMIDS: 'INVALID_ITEMIDS',
      INVALID_DRIVEID: 'INVALID_DRIVEID',
      TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS' // The server rejected the request due to its rate limit (too many requests per second)
    },

    ClientCapability: {
      // This is a bitwise enum for client capabilities. Used between client and access.
      MUTABLE_APN: 0x01,
      IOS13_VOIP_APN: 0x02,
      DISABLE_VOIP_NOTIFICATIONS: 0x04
    },

    ///////////////////////////////////////////////////////////////////////////
    // Common types
    ///////////////////////////////////////////////////////////////////////////
    SortOrder: {
      ASCENDING: 'ASCENDING',
      DESCENDING: 'DESCENDING'
    },

    TimeRange: {
      ONE_HOUR: 3600000,
      ONE_DAY: 86400000,
      ONE_WEEK: 7 * 86400000,
      ONE_MONTH: 30 * 86400000
    },

    ///////////////////////////////////////////////////////////////////////////
    // Conversation Action
    ///////////////////////////////////////////////////////////////////////////
    ConversationActionType: {
      ADD_JOURNAL_ENTRY: 'ADD_JOURNAL_ENTRY',
      DELETE_ALL_JOURNAL_ENTRIES: 'DELETE_ALL_JOURNAL_ENTRIES',
      DELETE_JOURNAL_ENTRY: 'DELETE_JOURNAL_ENTRY',
      GET_JOURNAL_ENTRIES: 'GET_JOURNAL_ENTRIES',
      GET_TELEPHONY_CONVERSATION_ID: 'GET_TELEPHONY_CONVERSATION_ID',
      GET_UNHEARD_VOICEMAILS_COUNT: 'GET_UNHEARD_VOICEMAILS_COUNT',
      SET_RECORDING_DELETION_DELAY: 'SET_RECORDING_DELETION_DELAY',
      SET_READ_POINTER: 'SET_READ_POINTER',
      SET_VOICEMAIL_HEARD: 'SET_VOICEMAIL_HEARD',
      UPDATE_RTC_ITEM_ATTACHMENTS: 'UPDATE_RTC_ITEM_ATTACHMENTS'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Conversation Event
    ///////////////////////////////////////////////////////////////////////////
    ConversationEventType: {
      CREATE: 'CREATE',
      UPDATE: 'UPDATE',
      DELETE: 'DELETE',
      ADD_ITEM: 'ADD_ITEM',
      UPDATE_ITEM: 'UPDATE_ITEM',
      READ_ITEMS: 'READ_ITEMS',
      TYPING: 'TYPING',
      DRAFT_MESSAGE_SAVED: 'DRAFT_MESSAGE_SAVED',
      DRAFT_MESSAGE_DISCARDED: 'DRAFT_MESSAGE_DISCARDED',
      DRAFT_MESSAGE_PUBLISHED: 'DRAFT_MESSAGE_PUBLISHED',
      FLAG_ITEM: 'FLAG_ITEM',
      CLEAR_FLAGGED_ITEM: 'CLEAR_FLAGGED_ITEM',
      CONVERSATION_MARKED: 'CONVERSATION_MARKED',
      CONVERSATION_UNMARKED: 'CONVERSATION_UNMARKED',
      FAVORITE_POSITION_CHANGED: 'FAVORITE_POSITION_CHANGED',
      USER_DATA_CHANGED: 'USER_DATA_CHANGED',
      JOURNAL_ENTRY_DELETED: 'JOURNAL_ENTRY_DELETED',
      ALL_JOURNAL_ENTRIES_DELETED: 'ALL_JOURNAL_ENTRIES_DELETED'
    },

    ///////////////////////////////////////////////////////////////////////////
    // UI Setttings
    ///////////////////////////////////////////////////////////////////////////
    UISettings: {
      // Conversation sidebar settings
      INFO: 'INFO',
      DETAILS: 'DETAILS',
      // Selector panel settings
      CONVERSATION_FAVORITES: 'CONVERSATION_FAVORITES'
    },

    DefaultUISettingsValues: {
      INFO: true,
      DETAILS: true,
      CONVERSATION_FAVORITES: true
    },

    // Options for busy settings
    UserBusyHandlingOptions: {
      DefaultRouting: 'DefaultRouting',
      BusySignal: 'BusySignal',
      SendToAlternativeNumber: 'SendToAlternativeNumber',
      SendToVm: 'SendToVm'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Conversation Data
    ///////////////////////////////////////////////////////////////////////////
    RetentionPolicyStatus: {
      ENABLED: 'ENABLED',
      USER_DISABLED: 'USER_DISABLED',
      LEGALHOLD_DISABLED: 'LEGALHOLD_DISABLED',
      USER_LEGALHOLD_DISABLED: 'USER_LEGALHOLD_DISABLED'
    },

    SearchDirection: {
      AFTER: 'AFTER',
      BEFORE: 'BEFORE',
      BOTH: 'BOTH'
    },

    // General filter that defines which items to retrieve.
    TimestampFilter: {
      MODIFICATION: 'MODIFICATION',
      CREATION: 'CREATION'
    },

    // General filter that defines the order in which the items will be retrieved.
    OrderBy: {
      TIMESTAMP_ASC: 'TIMESTAMP_ASC',
      TIMESTAMP_DESC: 'TIMESTAMP_DESC'
    },

    OrderType: {
      MODIFICATION: 'MODIFICATION',
      CREATION: 'CREATION'
    },

    ConversationType: {
      DIRECT: 'DIRECT',
      GROUP: 'GROUP',
      OPEN: 'OPEN',
      LARGE: 'LARGE'
    },

    ConversationItemType: {
      TEXT: 'TEXT',
      RTC: 'RTC',
      SYSTEM: 'SYSTEM'
    },

    ConversationItemTypeMapping: {
      RTC: 'phone',
      CALL_SUMMARY: 'call-summary',
      TEXT: 'text',
      SYSTEM: 'notification',
      VOICEMAIL: 'vm',
      MARKER: 'marker',
      DIVIDER: 'divider',
      GAP: 'gap'
    },

    ConversationFilter: {
      ALL: 'ALL',
      OPEN_MEMBER: 'OPEN_MEMBER',
      MARKED: 'MARKED',
      ARCHIVED: 'ARCHIVED',
      UNARCHIVED: 'UNARCHIVED'
    },

    ConversationMarkFilter: {
      MUTE: 'MUTE',
      FAVORITE: 'FAVORITE'
    },

    GetConversationParticipantCriteria: {
      DISPLAY_NAME: 'DISPLAY_NAME',
      TYPE: 'TYPE'
    },

    ConversationParticipantType: {
      ACTIVE: 'ACTIVE',
      REGULAR: 'REGULAR',
      FORMER: 'FORMER',
      MODERATOR: 'MODERATOR',
      GUEST: 'GUEST'
    },

    DataRetentionState: {
      UNTOUCHED: 'UNTOUCHED',
      OUTDATED: 'OUTDATED',
      DELETED: 'DELETED'
    },

    RetrieveAction: {
      CONVERSATIONS: 'CONVERSATIONS',
      CONVERSATION_IDS: 'CONVERSATION_IDS',
      CONVERSATIONS_AND_IDS: 'CONVERSATIONS_AND_IDS'
    },

    RTCItemType: {
      STARTED: 'STARTED',
      ENDED: 'ENDED',
      MISSED: 'MISSED',
      PARTICIPANT_JOINED: 'PARTICIPANT_JOINED',
      PARTICIPANT_LEFT: 'PARTICIPANT_LEFT',
      MOVED: 'MOVED'
    },

    RTCItemMissed: {
      UNREACHABLE: 'UNREACHABLE',
      TIMEOUT: 'TIMEOUT',
      USER_BUSY: 'USER_BUSY',
      DECLINED: 'DECLINED',
      CANCELED: 'CANCELED',
      INVALID_NUMBER: 'INVALID_NUMBER',
      TEMPORARILY_UNAVAILABLE: 'TEMPORARILY_UNAVAILABLE'
    },

    RTCItemMoved: {
      MOVED_FROM: 'MOVED_FROM',
      MOVED_TO: 'MOVED_TO'
    },

    SortingType: {
      RECENT_ACTIVITY_ASC: 'RECENT_ACTIVITY_ASC',
      RECENT_ACTIVITY_DESC: 'RECENT_ACTIVITY_DESC',
      ALPHABETICALLY_ASC: 'ALPHABETICALLY_ASC',
      ALPHABETICALLY_DESC: 'ALPHABETICALLY_DESC',
      POPULARITY_ASC: 'POPULARITY_ASC',
      POPULARITY_DESC: 'POPULARITY_DESC',
      TIMESTAMP_ASC: 'TIMESTAMP_ASC'
    },

    TextItemContentType: {
      PLAIN: 'PLAIN',
      RICH: 'RICH'
    },

    TextItemState: {
      CREATED: 'CREATED',
      EDITED: 'EDITED',
      DELETED: 'DELETED',
      CLUSTERED: 'CLUSTERED'
    },

    SystemItemType: {
      CONVERSATION_CREATED: 'CONVERSATION_CREATED',
      PARTICIPANT_ADDED: 'PARTICIPANT_ADDED',
      PARTICIPANT_REMOVED: 'PARTICIPANT_REMOVED',
      CONVERSATION_RENAMED: 'CONVERSATION_RENAMED',
      CONFERENCE_DETAILS_CHANGED: 'CONFERENCE_DETAILS_CHANGED',
      CONVERSATION_MODERATED: 'CONVERSATION_MODERATED',
      MODERATOR_RIGHTS_GRANTED: 'MODERATOR_RIGHTS_GRANTED',
      MODERATOR_RIGHTS_DROPPED: 'MODERATOR_RIGHTS_DROPPED',
      GUEST_ACCESS_ENABLED: 'GUEST_ACCESS_ENABLED',
      GUEST_ACCESS_DISABLED: 'GUEST_ACCESS_DISABLED',
      AVATAR_CHANGED: 'AVATAR_CHANGED',
      RETENTION_POLICY_CHANGED: 'RETENTION_POLICY_CHANGED',
      PARTICIPANT_HARD_REMOVED: 'PARTICIPANT_HARD_REMOVED',

      // Internal client types
      RETENTION_ITEMS_REMOVED: 'RETENTION_ITEMS_REMOVED'
    },

    // Attachments data
    AttachmentOrderType: {
      CREATION: 'CREATION',
      FILENAME: 'FILENAME',
      SIZE: 'SIZE',
      MIMETYPE: 'MIMETYPE',
      FILEEXTENSION: 'FILEEXTENSION'
    },

    AttachmentOrderDirection: {
      ASC: 'ASC',
      DSC: 'DSC'
    },

    BridgeLocale: {
      EN_US: 'EN_US',
      EN_GB: 'EN_GB',
      DE_DE: 'DE_DE',
      ES_ES: 'ES_ES',
      FR_FR: 'FR_FR',
      IT_IT: 'IT_IT',
      RU_RU: 'RU_RU',
      ZH_HANS_CN: 'ZH_HANS_CN',
      PT_BR: 'PT_BR',
      NL_NL: 'NL_NL',
      CA_ES: 'CA_ES',
      EU_ES: 'EU_ES'
    },

    BridgeNumberType: {
      TOLL: 'TOLL',
      TOLL_FREE: 'TOLL_FREE',
      LOCAL: 'LOCAL'
    },

    FilterTarget: {
      CONVERSATION_TYPE: 'CONVERSATION_TYPE',
      LABEL_ID: 'LABEL_ID',
      SYSTEM_TAG: 'SYSTEM_TAG'
    },

    FilterTargetComparator: {
      EQ: 'EQ',
      NOT: 'NOT'
    },

    FilterBoolConnector: {
      AND: 'AND',
      OR: 'OR'
    },

    SystemLabel: {
      ARCHIVED: 'ARCHIVED',
      FAVORITE: 'FAVORITE',
      MODERATOR: 'MODERATOR'
    },

    IncludedUserData: {
      LAST_READ_TS: 'LASTREADTS',
      UNREAD_COUNT: 'UNREADCOUNT',
      LABEL_IDS: 'LABELIDS',
      SYSTEM_LABEL_IDS: 'SYSTEMLABELIDS'
    },

    ChangedUserData: {
      LAST_READ_TS: 'LASTREADTS',
      LABEL_IDS: 'LABELIDS',
      SYSTEM_LABEL_IDS: 'SYSTEMLABELIDS'
    },

    JournalFilter: {
      ALL: 'ALL',
      MISSED: 'MISSED',
      DIALED: 'DIALED',
      RECEIVED: 'RECEIVED',
      DIVERTED: 'DIVERTED',
      VOICEMAILS: 'VOICEMAILS',
      UNHEARD_VOICEMAILS: 'UNHEARD_VOICEMAILS'
    },

    AddLabelsResult: {
      OK: 'OK',
      LABEL_LIMIT_REACHED: 'LABEL_LIMIT_REACHED',
      LABEL_ALREADY_EXISTS: 'LABEL_ALREADY_EXISTS'
    },

    SystemTag: {
      IC: 'IC'
    },

    ContainerType: {
      CONVERSATION: 'CONVERSATION'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Instrumentation Action
    ///////////////////////////////////////////////////////////////////////////
    InstrumentationActionType: {
      SUBMIT_CLIENT_DATA: 'SUBMIT_CLIENT_DATA',
      SUBMIT_QOS_DATA: 'SUBMIT_QOS_DATA'
    },

    SubmitDataResult: {
      UNKNOWN: 'UNKNOWN',
      OK: 'OK',
      NO_USERID: 'NO_USERID',
      NO_CLIENTID: 'NO_CLIENTID',
      NO_DATA: 'NO_DATA',
      PROCESSING_ERROR: 'PROCESSING_ERROR'
    },

    QOSMediaType: {
      AUDIO: 'AUDIO',
      VIDEO: 'VIDEO',
      SCREEN_SHARE: 'SCREEN_SHARE'
    },

    ///////////////////////////////////////////////////////////////////////////
    // CTI Call Action
    ///////////////////////////////////////////////////////////////////////////
    CTICallActionType: {
      HOLD: 'HOLD',
      RETRIEVE: 'RETRIEVE',
      MERGE: 'MERGE',
      MUTE: 'MUTE',
      SWAP: 'SWAP',
      CONSULT: 'CONSULT',
      TRANSFER: 'TRANSFER',
      DTMF: 'DTMF',
      ANSWER_ON_OTHER: 'ANSWER_ON_OTHER',
      HANGUP: 'HANGUP',
      PUSH_WEBRTC_CALL: 'PUSH_WEBRTC_CALL',
      SEND_RESPONSE: 'SEND_RESPONSE',
      DIAL_ON_OTHER: 'DIAL_ON_OTHER'
    },

    ///////////////////////////////////////////////////////////////////////////
    // CTI Call Event
    ///////////////////////////////////////////////////////////////////////////
    CTICallEventType: {
      HOLD: 'HOLD',
      RETRIEVE: 'RETRIEVE',
      MUTE: 'MUTE',
      MERGE: 'MERGE',
      SWAP: 'SWAP',
      CONSULT: 'CONSULT',
      TRANSFER: 'TRANSFER',
      DTMF: 'DTMF',
      ANSWER_ON_OTHER: 'ANSWER_ON_OTHER',
      HANGUP: 'HANGUP',
      PUSH_WEBRTC_CALL: 'PUSH_WEBRTC_CALL',
      DIAL_ON_OTHER: 'DIAL_ON_OTHER'
    },

    ///////////////////////////////////////////////////////////////////////////
    // RTC Call Action
    ///////////////////////////////////////////////////////////////////////////
    RTCCallActionType: {
      JOIN: 'JOIN',
      ANSWER: 'ANSWER',
      ICE_CANDIDATES: 'ICE_CANDIDATES',
      LEAVE: 'LEAVE',
      TERMINATE: 'TERMINATE',
      CHANGE_MEDIA_TYPE: 'CHANGE_MEDIA_TYPE',
      INVITE_REJECT: 'INVITE_REJECT',
      CHANGE_MEDIA_ACCEPT: 'CHANGE_MEDIA_ACCEPT',
      CHANGE_MEDIA_REJECT: 'CHANGE_MEDIA_REJECT',
      PREPARE: 'PREPARE',
      RENEW_TURN_CREDENTIALS: 'RENEW_TURN_CREDENTIALS',
      SEND_PROGRESS: 'SEND_PROGRESS',
      SUBMIT_RTC_QUALITY_RATING: 'SUBMIT_RTC_QUALITY_RATING',
      MERGE: 'MERGE'
    },

    ///////////////////////////////////////////////////////////////////////////
    // RTC Call Event
    ///////////////////////////////////////////////////////////////////////////
    RTCCallEventType: {
      SDP_ANSWER: 'SDP_ANSWER',
      SDP_FAILED: 'SDP_FAILED',
      INVITE: 'INVITE',
      INVITE_CANCEL: 'INVITE_CANCEL',
      INVITE_FAILED: 'INVITE_FAILED',
      CHANGE_MEDIA_TYPE_REQUESTED: 'CHANGE_MEDIA_TYPE_REQUESTED',
      PROGRESS: 'PROGRESS',
      CHANGE_MEDIA_TYPE_FORCED: 'CHANGE_MEDIA_TYPE_FORCED',
      ICE_CANDIDATES: 'ICE_CANDIDATES',
      RTC_QUALITY_RATING_EVENT: 'RTC_QUALITY_RATING_EVENT'
    },

    ///////////////////////////////////////////////////////////////////////////
    // RTC Call Data
    ///////////////////////////////////////////////////////////////////////////
    SdpFailedCause: {
      SESSION_STARTED_FAILED: 'SESSION_STARTED_FAILED',
      CONNECTION_LOST: 'CONNECTION_LOST',
      SERVICE_ERROR: 'SERVICE_ERROR',
      CHANGE_MEDIA_REJECT: 'CHANGE_MEDIA_REJECT',
      JOIN_FORBIDDEN: 'JOIN_FORBIDDEN',
      SESSION_TERMINATED: 'SESSION_TERMINATED',
      MAX_PARTICIPANTS_REACHED: 'MAX_PARTICIPANTS_REACHED'
    },

    InviteCancelCause: {
      ACCEPT: 'ACCEPT',
      BUSY: 'BUSY',
      DECLINE: 'DECLINE',
      REVOKED: 'REVOKED'
    },

    InviteFailedCause: {
      BUSY: 'BUSY',
      DECLINE: 'DECLINE',
      TIMEOUT: 'TIMEOUT',
      NOT_REACHABLE: 'NOT_REACHABLE',
      INVALID_NUMBER: 'INVALID_NUMBER',
      TEMPORARILY_UNAVAILABLE: 'TEMPORARILY_UNAVAILABLE',
      REVOKED: 'REVOKED'
    },

    InviteRejectCause: {
      BUSY: 'BUSY',
      DECLINE: 'DECLINE',
      TIMEOUT: 'TIMEOUT',
      INVALID_NUMBER: 'INVALID_NUMBER',
      TEMPORARILY_UNAVAILABLE: 'TEMPORARILY_UNAVAILABLE',
      NOT_REACHABLE: 'NOT_REACHABLE'
    },

    RTCProgressType: {
      ALERTING: 'ALERTING',
      PROGRESS: 'PROGRESS',
      EARLY_CONNECT: 'EARLY_CONNECT'
    },

    DisconnectCause: {
      HANGUP: 'HANGUP',
      CONNECTION_LOST: 'CONNECTION_LOST',
      NEGOTIATION_FAILED: 'NEGOTIATION_FAILED',
      TRANSPORT_NEGOTIATION_FAILED: 'TRANSPORT_NEGOTIATION_FAILED',
      SECURITY_NEGOTIATION_FAILED: 'SECURITY_NEGOTIATION_FAILED',
      REMOTE_SDP_FAILED: 'REMOTE_SDP_FAILED',
      STREAM_LOST: 'STREAM_LOST',
      CALL_SETUP_FAILED: 'CALL_SETUP_FAILED'
    },

    DisconnectReason: {
      PAGE_UNLOADED: 'PAGE_UNLOADED',
      CALL_LOST: 'CALL_LOST',
      LOGOUT: 'LOGOUT'
    },

    TerminateCause: {
      COMPLETED_ELSEWHERE: 'COMPLETED_ELSEWHERE',
      PICKED_UP: 'PICKED_UP'
    },

    ///////////////////////////////////////////////////////////////////////////
    // RTC Session Action
    ///////////////////////////////////////////////////////////////////////////
    RTCSessionActionType: {
      LOCK: 'LOCK',
      MUTE_SESSION: 'MUTE_SESSION',
      MUTE: 'MUTE',
      ADD_PARTICIPANT: 'ADD_PARTICIPANT',
      REMOVE_PARTICIPANT: 'REMOVE_PARTICIPANT',
      GET_ACTIVE_SESSIONS: 'GET_ACTIVE_SESSIONS',
      GET_SESSION: 'GET_SESSION',
      MOVE: 'MOVE',
      START_RECORDING: 'START_RECORDING',
      STOP_RECORDING: 'STOP_RECORDING',
      GET_SESSIONID_BY_PIN: 'GET_SESSIONID_BY_PIN',
      RAISE_QUESTION: 'RAISE_QUESTION',
      INVITE_TO_STAGE: 'INVITE_TO_STAGE',
      INVITE_TO_STAGE_ANSWER: 'INVITE_TO_STAGE_ANSWER',
      UPDATE_QUESTION_STATE: 'UPDATE_QUESTION_STATE',
      INVITE_TO_STAGE_CANCEL: 'INVITE_TO_STAGE_CANCEL',
      REMOVE_FROM_STAGE: 'REMOVE_FROM_STAGE',
      GET_QUESTIONS: 'GET_QUESTIONS',
      OPEN_CURTAIN: 'OPEN_CURTAIN',
      CLOSE_CURTAIN: 'CLOSE_CURTAIN',
      GET_NODE_STATE: 'GET_NODE_STATE',
      SEND_CLIENT_INFO: 'SEND_CLIENT_INFO',
      SWITCH_RECORDING_LAYOUT: 'SWITCH_RECORDING_LAYOUT',
      SET_VIDEO_RECEIVER_CONFIGURATION: 'SET_VIDEO_RECEIVER_CONFIGURATION'
    },

    ///////////////////////////////////////////////////////////////////////////
    // RTC Session Event
    ///////////////////////////////////////////////////////////////////////////
    RTCSessionEventType: {
      SESSION_STARTED: 'SESSION_STARTED',
      SESSION_TERMINATED: 'SESSION_TERMINATED',
      SESSION_UPDATED: 'SESSION_UPDATED',
      SESSION_MOVED: 'SESSION_MOVED',
      PARTICIPANT_JOINED: 'PARTICIPANT_JOINED',
      PARTICIPANT_LEFT: 'PARTICIPANT_LEFT',
      PARTICIPANT_UPDATED: 'PARTICIPANT_UPDATED',
      ACTIVE_SPEAKER: 'ACTIVE_SPEAKER',
      VIDEO_ACTIVE_SPEAKER: 'VIDEO_ACTIVE_SPEAKER',
      SESSION_RECORDING_INFO: 'SESSION_RECORDING_INFO',
      SESSION_RECORDING_STARTED: 'SESSION_RECORDING_STARTED',
      SESSION_RECORDING_STOPPED: 'SESSION_RECORDING_STOPPED',
      SESSION_RECORDING_FAILED: 'SESSION_RECORDING_FAILED',
      SESSION_TERMINATION_TIMER_STARTED: 'SESSION_TERMINATION_TIMER_STARTED',
      SESSION_TERMINATION_TIMER_CANCELLED: 'SESSION_TERMINATION_TIMER_CANCELLED',
      LIVE_TRANSCRIPTION: 'LIVE_TRANSCRIPTION'
    },

    ///////////////////////////////////////////////////////////////////////////
    // RTC Session Data
    ///////////////////////////////////////////////////////////////////////////
    RTCSessionTerminatedCause: {
      FORCED: 'FORCED',
      NO_USERS_LEFT: 'NO_USERS_LEFT',
      CONDITIONAL: 'CONDITIONAL'
    },

    RTCSessionParticipantLeftCause: {
      LEFT: 'LEFT',
      CONNECTION_LOST: 'CONNECTION_LOST',
      REMOVED: 'REMOVED',
      CONDITIONAL_TERMINATE: 'CONDITIONAL_TERMINATE',
      FORCED_TERMINATE: 'FORCED_TERMINATE',
      MAX_PARTICIPANTS_REACHED: 'MAX_PARTICIPANTS_REACHED',
      USER_LEFT_STAGE: 'USER_LEFT_STAGE',
      NEGOTIATION_FAILED: 'NEGOTIATION_FAILED',
      OFFER_NOT_ACCEPTED: 'OFFER_NOT_ACCEPTED',
      STREAM_LOST: 'STREAM_LOST',
      TRANSPORT_NEGOTIATION_FAILED: 'TRANSPORT_NEGOTIATION_FAILED',
      SECURITY_NEGOTIATION_FAILED: 'SECURITY_NEGOTIATION_FAILED',
      CALL_SETUP_FAILED: 'CALL_SETUP_FAILED',
      REMOTE_SDP_FAILED: 'REMOTE_SDP_FAILED'
    },

    RTCSessionParticipantFlag: {
      CPU_LOAD: 'CPU_LOAD', // VRS video composer disabled
      CPU_LOAD_TRANSCODE: 'CPU_LOAD_TRANSCODE', // VRS video encoder disabled
      NO_LICENSE: 'NO_LICENSE', // VRS licence limit reached
      MAX_COUNT: 'MAX_COUNT' // VRS max count
    },

    RtcSupportedFeatures: {
      UPGRADE_TO_CONFERENCE: 'UPGRADE_TO_CONFERENCE',
      SCREEN_SHARE_AND_ACTIVE_SPEAKER_VIDEO: 'SCREEN_SHARE_AND_ACTIVE_SPEAKER_VIDEO',
      MID_STREAM_MAPPING: 'MID_STREAM_MAPPING',
      CALL_PICKUP: 'CALL_PICKUP'
    },

    RealtimeMediaType: {
      AUDIO: 'AUDIO',
      VIDEO: 'VIDEO',
      DESKTOP_SHARING: 'DESKTOP_SHARING'
    },

    RecordingMediaType: {
      AUDIO: 'AUDIO',
      VIDEO: 'VIDEO',
      TEXT: 'TEXT'
    },

    RTCParticipantType: {
      USER: 'USER', // UnifyPhone users
      EXTERNAL: 'EXTERNAL', // PSTN dial-in participants
      TELEPHONY: 'TELEPHONY', // Telephony connector users (dial in or dial out)
      SESSION_GUEST: 'SESSION_GUEST', // Session guests
      VOICE_MAIL: 'VOICE_MAIL', // Video/voicemail
      TEST_CALL: 'TEST_CALL' // Test call "echo" participant
    },

    QuestionState: {
      RAISED: 'RAISED',
      ANSWERED: 'ANSWERED',
      DISCARDED: 'DISCARDED',
      ANSWER_LATER: 'ANSWER_LATER'
    },

    StageState: {
      INITIAL: 'INITIAL',
      INVITED: 'INVITED',
      REJECTED: 'REJECTED',
      ACCEPTED: 'ACCEPTED',
      WAS_ON_STAGE: 'WAS_ON_STAGE',
      TIMEOUT: 'TIMEOUT',
      LEFT_CONFERENCE: 'LEFT_CONFERENCE'
    },

    QuestionEventType: {
      CREATED: 'CREATED',
      UPDATED: 'UPDATED',
      REVOKED: 'REVOKED'
    },

    StageAction: {
      USER_ENTERED_STAGE: 'USER_ENTERED_STAGE',
      USER_LEFT_STAGE: 'USER_LEFT_STAGE'
    },

    NodeType: {
      MEDIA_ACCESS: 'MEDIA_ACCESS',
      APPLICATION: 'APPLICATION',
      STORAGE: 'STORAGE',
      ACCESS: 'ACCESS',
      LOAD_BALANCER: 'LOAD_BALANCER',
      ZOOKEEPER: 'ZOOKEEPER',
      MANAGEMENT: 'MANAGEMENT',
      OPERATIONS: 'OPERATIONS'
    },

    NodeState: {
      UP: 'UP',
      DOWN: 'DOWN',
      OVERLOAD: 'OVERLOAD',
      MAINTENANCE: 'MAINTENANCE'
    },

    RecordingInfoState: {
      INITIAL: 'INITIAL', // no recording was ever started
      START_PENDING: 'START_PENDING', // recording was started, but recorder was disabled(e.g. by curtain). recording is not running
      STARTED: 'STARTED', // recording was started and is running currently
      STOPPED: 'STOPPED', // recording was stopped
      FINISHED: 'FINISHED' // recording was finally stopped(on session terminate)
    },

    RecordingInfoReason: {
      NONE: 'NONE',
      STOPPED_MANUALLY: 'STOPPED_MANUALLY',
      STOPPED_AUTOMATICALLY: 'STOPPED_AUTOMATICALLY',
      NO_INPUT_TIMEOUT: 'NO_INPUT_TIMEOUT',
      MAX_INPUT_TIMEOUT: 'MAX_INPUT_TIMEOUT',
      NO_STREAMING_DATA: 'NO_STREAMING_DATA',
      LENGTH_LIMIT_REACHED: 'LENGTH_LIMIT_REACHED',
      NO_MORE_DISK_SPACE_LEFT: 'NO_MORE_DISK_SPACE_LEFT',
      UNKNOWN_ERROR: 'UNKNOWN_ERROR'
    },

    RecordingTriggerReason: {
      USER: 'USER',
      CURTAIN: 'CURTAIN'
    },

    RecordingVideoLayoutName: {
      SINGLE: 'SINGLE',
      VIDEO_SCREEN_50_50: 'VIDEO_SCREEN_50_50',
      VIDEO_SCREEN_75_25: 'VIDEO_SCREEN_75_25',
      VIDEO_SCREEN_25_75: 'VIDEO_SCREEN_25_75',
      SINGLE_VIDEO: 'SINGLE_VIDEO'
    },

    RtcClientInfoType: {
      OFFLINE_JOIN_FAILURE: 'OFFLINE_JOIN_FAILURE',
      DEVICE_DIAGNOSTICS: 'DEVICE_DIAGNOSTICS',
      CALL_CONNECTED: 'CALL_CONNECTED'
    },

    RtcClientInfoReason: {
      NO_NETWORK_CONNECTION: 'NO_NETWORK_CONNECTION',
      DISCONNECTED: 'DISCONNECTED',
      FAILED_TO_SEND: 'FAILED_TO_SEND',
      REQUEST_TIMEOUT: 'REQUEST_TIMEOUT',
      JOIN_DELAY: 'JOIN_DELAY',
      CALL_CONNECTED: 'CALL_CONNECTED'
    },

    RtcActionInfoType: {
      REQUEST_RESPONSE: 'REQUEST_RESPONSE',
      EVENT: 'EVENT'
    },

    RtcDiagnosticsAction: {
      // REQUEST_RESPONSE
      RENEW_TURN_CREDENTIALS: 'RENEW_TURN_CREDENTIALS',
      PREPARE: 'PREPARE',
      JOIN: 'JOIN',
      ICE_CANDIDATES: 'ICE_CANDIDATES',

      // EVENT
      SDP_CONNECTED: 'SDP_CONNECTED',
      SDP_ANSWER: 'SDP_ANSWER',
      REMOTE_ICE_CANDIDATES: 'REMOTE_ICE_CANDIDATES'
    },

    IncomingVideoContentType: {
      VIDEO: 'VIDEO',
      SCREEN: 'SCREEN'
    },

    VideoQuality: {
      LOW: 'LOW',
      NORMAL: 'NORMAL',
      HIGH: 'HIGH'
    },

    AudioQualityParam: {
      ECHO: 'ECHO',
      JITTER: 'JITTER',
      NO_PACKETS_RECV: 'NO_PACKETS_RECV',
      NO_PACKETS_SENT: 'NO_PACKETS_SENT',
      PL_RECV: 'PL_RECV',
      PL_SEND: 'PL_SEND',
      RTT: 'RTT'
    },

    VideoQualityParam: {
      BW_SEND: 'BW_SEND',
      FRAME_HEIGHT_SENT: 'FRAME_HEIGHT_SENT',
      FRAME_RATE_SENT: 'FRAME_RATE_SENT',
      FRAME_WIDTH_SENT: 'FRAME_WIDTH_SENT',
      PL_RECV: 'PL_RECV',
      PL_SEND: 'PL_SEND',
      RTT: 'RTT',
      TRX_BITRATE: 'TRX_BITRATE'
    },

    WhiteboardErrorCause: {
      EXTERNAL_URL_NOT_ALLOWED: 'EXTERNAL_URL_NOT_ALLOWED', // Failed to add not allowed external url for image
      IMAGE_SIZE_TOO_LARGE: 'IMAGE_SIZE_TOO_LARGE', // An image was too large to be handled for PNG creation.
      IMAGE_DOWNLOAD_FAILED: 'IMAGE_DOWNLOAD_FAILED' // An image could not be downloaded.
    },

    ///////////////////////////////////////////////////////////////////////////
    // Search Action
    ///////////////////////////////////////////////////////////////////////////
    SearchActionType: {
      START_ADVANCED_USER_SEARCH: 'START_ADVANCED_USER_SEARCH',
      START_BASIC_SEARCH: 'START_BASIC_SEARCH',
      START_DETAIL_SEARCH: 'START_DETAIL_SEARCH',
      START_USER_SEARCH: 'START_USER_SEARCH',
      CANCEL_SEARCH: 'CANCEL_SEARCH',
      ADD_RECENT_SEARCH: 'ADD_RECENT_SEARCH',
      SEARCH_CONVERSATION_PARTICIPANTS: 'SEARCH_CONVERSATION_PARTICIPANTS'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Search Event
    ///////////////////////////////////////////////////////////////////////////
    SearchEventType: {
      BASIC_SEARCH_RESULT: 'BASIC_SEARCH_RESULT',
      DETAILED_SEARCH_RESULT: 'DETAILED_SEARCH_RESULT',
      SEARCH_STATUS: 'SEARCH_STATUS',
      RECENT_SEARCH_ADDED: 'RECENT_SEARCH_ADDED'
    },

    ///////////////////////////////////////////////////////////////////////////
    // System Action
    ///////////////////////////////////////////////////////////////////////////
    SystemActionType: {
      GET_GLOBAL_PROPERTY: 'GET_GLOBAL_PROPERTY',
      GET_GLOBAL_PROPERTIES: 'GET_GLOBAL_PROPERTIES'
    },

    ///////////////////////////////////////////////////////////////////////////
    // System Event
    ///////////////////////////////////////////////////////////////////////////
    SystemEventType: {
      BROADCAST: 'BROADCAST',
      BROADCAST_REMOVED: 'BROADCAST_REMOVED',
      MAINTENANCE: 'MAINTENANCE',
      MAINTENANCE_REMOVED: 'MAINTENANCE_REMOVED',
      MAINTENANCE_ADDED: 'MAINTENANCE_ADDED'
    },

    ///////////////////////////////////////////////////////////////////////////
    // System Data
    ///////////////////////////////////////////////////////////////////////////
    GlobalPropertyName: {
      ATTACHMENT_UPLOAD_MAX_SIZE: 'ATTACHMENT_UPLOAD_MAX_SIZE',
      CONV_PARTICIPANTS_MAX_SIZE: 'CONV_PARTICIPANTS_MAX_SIZE',
      CONV_PARTICIPANTS_ADD_LIMIT: 'CONV_PARTICIPANTS_ADD_LIMIT',
      XMPP_FEDERATION_ENABLED: 'XMPP_FEDERATION_ENABLED',
      CLIENT_AUTOUPDATE_MAX_TIME: 'CLIENT_AUTOUPDATE_MAX_TIME',
      ADD_TELEPHONY_CONNECTOR_DISABLED: 'ADD_TELEPHONY_CONNECTOR_DISABLED',
      CHROME_EXTENSION_ID: 'CHROME_EXTENSION_ID',
      EDGE_EXTENSION_ID: 'EDGE_EXTENSION_ID',
      CONV_INCOMING_WEBHOOKS_MAX_SIZE: 'CONV_INCOMING_WEBHOOKS_MAX_SIZE',
      SHOW_PRESENCE_ON_ADD_USER: 'SHOW_PRESENCE_ON_ADD_USER',
      EXCHANGE_ONLINE_CONFIG: 'EXCHANGE_ONLINE_CONFIG',
      BASE_URL: 'BASE_URL',
      MAX_FAVORITES: 'MAX_FAVORITES'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Search Data
    ///////////////////////////////////////////////////////////////////////////
    SearchTypes: {
      CONVERSATION: 'CONVERSATION',
      USER: 'USER'
    },

    SearchStatusCode: {
      ERROR: 'ERROR',
      CANCELED: 'CANCELED',
      FINISHED: 'FINISHED',
      TIMEOUT: 'TIMEOUT',
      NO_RESULT: 'NO_RESULT',
      MORE_RESULT: 'MORE_RESULT'
    },

    SearchResultType: {
      TOPIC_MATCH: 'TOPIC_MATCH',
      USER_MATCH: 'USER_MATCH',
      ATTACHMENT_MATCH: 'ATTACHMENT_MATCH',
      ITEM_MATCH: 'ITEM_MATCH'
    },

    SearchScope: {
      ALL: 'ALL',
      PEOPLE: 'PEOPLE',
      MEMBERS: 'MEMBERS',
      MESSAGES: 'MESSAGES', // not supported anymore but still needed for saved recent searches
      CONVERSATIONS: 'CONVERSATIONS',
      FILES: 'FILES',
      SENT_BY: 'SENT_BY',
      DATE: 'DATE',
      LABEL: 'LABEL',
      FILTER: 'FILTER'
    },

    SpaceSearchScope: {
      ALL: 'ALL',
      SPACES: 'SPACES',
      TOPICBY: 'TOPICBY',
      FILES: 'FILES',
      TAGS: 'TAGS',
      LABEL: 'LABEL',
      DATE: 'DATE'
    },

    SearchContext: {
      USER: 'USER'
    },

    ///////////////////////////////////////////////////////////////////////////
    // User Action
    ///////////////////////////////////////////////////////////////////////////
    UserActionType: {
      LOGON: 'LOGON',
      LOGOUT: 'LOGOUT',
      RENEW_TOKEN: 'RENEW_TOKEN',
      GET_USER_BY_ID: 'GET_USER_BY_ID',
      UPDATE: 'UPDATE',
      GET_LOGGED_ON: 'GET_LOGGED_ON',
      GET_STUFF: 'GET_STUFF',
      SET_PRESENCE: 'SET_PRESENCE',
      GET_PRESENCE: 'GET_PRESENCE',
      SUBSCRIBE_PRESENCE: 'SUBSCRIBE_PRESENCE',
      UNSUBSCRIBE_PRESENCE: 'UNSUBSCRIBE_PRESENCE',
      CHANGE_PASSWORD: 'CHANGE_PASSWORD',
      GET_USERS_BY_IDS: 'GET_USERS_BY_IDS',
      GET_DEVICES: 'GET_DEVICES',
      GET_USER_SETTINGS: 'GET_USER_SETTINGS',
      SET_USER_SETTINGS: 'SET_USER_SETTINGS',
      GET_USER_BY_MAIL: 'GET_USER_BY_MAIL',
      GET_USERS_BY_MAILS: 'GET_USERS_BY_MAILS',
      EMAIL_UPDATE: 'EMAIL_UPDATE',
      GENERATE_EMAIL_UPDATE_TOKEN: 'GENERATE_EMAIL_UPDATE_TOKEN',
      GET_TELEPHONY_DATA: 'GET_TELEPHONY_DATA',
      GET_USER_EXTENSIONS: 'GET_USER_EXTENSIONS',
      SET_USER_EXTENSION_STATE: 'SET_USER_EXTENSION_STATE',
      GET_LDAP_TECHNICAL_USER_PRESENCE: 'GET_LDAP_TECHNICAL_USER_PRESENCE',
      GET_ATTACHMENTS: 'GET_ATTACHMENTS',
      SET_PASSWORD: 'SET_PASSWORD',
      GET_TENANT_SETTINGS: 'GET_TENANT_SETTINGS',
      OAUTH_GET_GRANTED_ACCESS_TOKENS: 'OAUTH_GET_GRANTED_ACCESS_TOKENS',
      OAUTH_REVOKE_ACCESS_TOKEN: 'OAUTH_REVOKE_ACCESS_TOKEN',
      REVOKE_MANAGED_DEVICES: 'REVOKE_MANAGED_DEVICES',
      GET_SECURITY_TOKEN_INFO: 'GET_SECURITY_TOKEN_INFO',
      REVOKE_SECURITY_TOKEN: 'REVOKE_SECURITY_TOKEN',
      RESET_OPENSCAPE_DEVICE_PINS: 'RESET_OPENSCAPE_DEVICE_PINS',
      SUBSCRIBE_TENANT_PRESENCE: 'SUBSCRIBE_TENANT_PRESENCE',
      UNSUBSCRIBE_TENANT_PRESENCE: 'UNSUBSCRIBE_TENANT_PRESENCE',
      GET_TENANT_ID_TO_NAME_MAP: 'GET_TENANT_ID_TO_NAME_MAP',
      ADD_RECENT_USED_CMR: 'ADD_RECENT_USED_CMR',
      GET_RECENT_USED_CMRS: 'GET_RECENT_USED_CMRS',
      IS_ALLOWED_TO_ADD_CMR: 'IS_ALLOWED_TO_ADD_CMR',
      GET_SUPPORT_DATA: 'GET_SUPPORT_DATA',

      // Used between clients and Access Server. Not part of official API.
      WAKE_UP: 'WAKE_UP',
      GO_TO_SLEEP: 'GO_TO_SLEEP',
      SET_CLIENT_CAPABILITIES: 'SET_CLIENT_CAPABILITIES',
      CHECK_CONNECTIVITY: 'CHECK_CONNECTIVITY'
    },

    ///////////////////////////////////////////////////////////////////////////
    // User Event
    ///////////////////////////////////////////////////////////////////////////
    UserEventType: {
      ATC_REFRESH_FAILED: 'ATC_REFRESH_FAILED', // Used between clients and Access Server. Not part of official API.
      USER_PRESENCE_CHANGE: 'USER_PRESENCE_CHANGE',
      USER_UPDATED: 'USER_UPDATED',
      SESSION_EXPIRES: 'SESSION_EXPIRES',
      SESSION_EXPIRING: 'SESSION_EXPIRING',
      SESSION_CLOSED: 'SESSION_CLOSED',
      PASSWORD_CHANGED: 'PASSWORD_CHANGED',
      USER_SETTING_UPDATED: 'USER_SETTING_UPDATED',
      TELEPHONY_DATA: 'TELEPHONY_DATA',
      DEVICE_LIST_CHANGED: 'DEVICE_LIST_CHANGED',
      NOTIFICATION_SUBSCRIPTION_CHANGE: 'NOTIFICATION_SUBSCRIPTION_CHANGE',
      TENANT_CONFIGURATION_UPDATED: 'TENANT_CONFIGURATION_UPDATED',
      UCAAS_DEVICE_UPDATED: 'UCAAS_DEVICE_UPDATED',
      TENANT_PRESENCE_CHANGE: 'TENANT_PRESENCE_CHANGE',
      TELEPHONY_CHANGED: 'TELEPHONY_CHANGED',
      EAGROUP_STATUS_CHANGED: 'EAGROUP_STATUS_CHANGED'
    },

    ///////////////////////////////////////////////////////////////////////////
    // User Data
    ///////////////////////////////////////////////////////////////////////////
    GetStuffType: {
      USER: 'USER',
      ACCOUNTS: 'ACCOUNTS',
      PRESENCE_STATE: 'PRESENCE_STATE',
      SETTINGS: 'SETTINGS',
      TELEPHONY_CONVERSATION_ID: 'TELEPHONY_CONVERSATION_ID',
      GLOBAL_PROPERTIES: 'GLOBAL_PROPERTIES',
      TENANT: 'TENANT',
      SUPPORT_INFO: 'SUPPORT_INFO'
    },

    IdleState: {
      Idle: 'Idle',
      Active: 'Active'
    },

    NotificationSubscriptionType: {
      ONLINE_STATUS: 'ONLINE_STATUS'
    },

    NotificationSubscriptionAction: {
      SUBSCRIBE: 'SUBSCRIBE',
      UNSUBSCRIBE: 'UNSUBSCRIBE'
    },

    DeviceType: {
      PHONE: 'PHONE',
      WEB: 'WEB',
      APPLICATION: 'APPLICATION',
      MOBILE: 'MOBILE',
      SDK: 'SDK',
      UNKNOWN: 'UNKNOWN',
      SESSION_GUEST: 'SESSION_GUEST'
    },

    PresenceState: {
      AVAILABLE: 'AVAILABLE',
      OFFLINE: 'OFFLINE',
      AWAY: 'AWAY',
      BUSY: 'BUSY',
      DND: 'DND',
      INVISIBLE: 'INVISIBLE',
      BUSY_IN_A_CALL: 'BUSY_IN_A_CALL',
      BUSY_IN_AN_EXTERNAL_CALL: 'BUSY_IN_AN_EXTERNAL_CALL'
    },

    ChangePasswordResult: {
      OK: 'OK',
      NEW_INVALID: 'NEW_INVALID',
      OLD_INVALID: 'OLD_INVALID',
      ALREADY_CHANGED: 'ALREADY_CHANGED',
      // Used when the CHANGE_PASSWORD action returns an error
      OPERATION_ERROR: 'OPERATION_ERROR'
    },

    SetPasswordResult: {
      OK: 'OK',
      NOT_OK: 'NOT_OK'
    },

    SessionClosedReason: {
      NEW_CONNECTION_DETECTED: 'NEW_CONNECTION_DETECTED',
      UPGRADE_REQUIRED: 'UPGRADE_REQUIRED',
      SUSPENDED: 'SUSPENDED',
      DELETED: 'DELETED',
      TENANT_DELETED: 'TENANT_DELETED',
      TENANT_SUSPENDED: 'TENANT_SUSPENDED',
      REVOKED: 'REVOKED'
    },

    // Reason in SESSION_RECORDING_STOPPED event
    RecordingStoppedReason: {
      LENGTH_LIMIT_REACHED: 'LENGTH_LIMIT_REACHED',
      NO_INPUT: 'NO_INPUT'
    },

    RecordingUploadState: {
      IN_PROGRESS: 'IN_PROGRESS',
      RETRYING: 'RETRYING',
      FINISHED: 'FINISHED',
      FAILED: 'FAILED',
      DELETED: 'DELETED'
    },

    UserRole: {
      GUEST: 'GUEST',
      USER: 'USER',
      TENANT_ADMIN: 'TENANT_ADMIN',
      SUPPORT: 'SUPPORT',
      SYSTEM_ADMIN: 'SYSTEM_ADMIN',
      TELEPHONY_CONNECTOR: 'TELEPHONY_CONNECTOR',
      VIRTUAL_TELEPHONY_CONNECTOR: 'VIRTUAL_TELEPHONY_CONNECTOR',
      SESSION_GUEST: 'SESSION_GUEST',
      BOT: 'BOT'
    },

    UserType: {
      REGULAR: 'REGULAR',
      GUEST: 'GUEST',
      SUPPORT: 'SUPPORT',
      TELEPHONY: 'TELEPHONY',
      SESSION_GUEST: 'SESSION_GUEST',
      TECHNICAL: 'TECHNICAL',
      BOT: 'BOT',
      XMPP: 'XMPP'
    },

    UserState: {
      /* User is just created and doesn't have a phone number */
      INITIAL: 'INITIAL',
      /* User has a phone number and has received an invitation to register */
      INVITED: 'INVITED',
      /* User is just created or deleted */
      INACTIVE: 'INACTIVE',
      /* User is active (but may still have to register) */
      ACTIVE: 'ACTIVE',
      /* User is suspended */
      SUSPENDED: 'SUSPENDED',
      /* User is deleted */
      DELETED: 'DELETED',
      /* User is deleted and user data has been purged from DB */
      PURGED: 'PURGED'
    },

    Locale: {
      EN_US: 'EN_US',
      DE_DE: 'DE_DE',
      ES_ES: 'ES_ES',
      FR_FR: 'FR_FR',
      IT_IT: 'IT_IT',
      RU_RU: 'RU_RU',
      ZH_HANS_CN: 'ZH_HANS_CN',
      PT_BR: 'PT_BR',
      NL_NL: 'NL_NL',
      CA_ES: 'CA_ES',
      EU_ES: 'EU_ES'
    },

    PhoneNumberType: {
      WORK: 'WORK',
      MOBILE: 'MOBILE',
      HOME: 'HOME',
      FAX: 'FAX',
      OTHER: 'OTHER',
      UCAAS: 'UCAAS'
    },

    PhoneNumberNpi: {
      PUBLIC: 'PUBLIC',
      PRIVATE: 'PRIVATE'
    },

    EmailAddressType: {
      WORK: 'WORK',
      HOME: 'HOME',
      OTHER: 'OTHER'
    },

    UserSettingArea: {
      ALL: 'ALL',
      EMAIL: 'EMAIL',
      DESKTOP: 'DESKTOP',
      PRIVACY: 'PRIVACY',
      CLIENT: 'CLIENT'
    },

    UserSettingDataType: {
      BOOLEAN: 'BOOLEAN',
      NUMBER: 'NUMBER',
      STRING: 'STRING',
      TIMESTAMP: 'TIMESTAMP'
    },

    UserSettingKey: {
      EMAIL_INVITES: 'EMAIL_INVITES',
      EMAIL_MISSED_RTC: 'EMAIL_MISSED_RTC',
      EMAIL_SUMMARY: 'EMAIL_SUMMARY',
      EMAIL_UPDATE: 'EMAIL_UPDATE',
      DESKTOP_RTC: 'DESKTOP_RTC',
      DESKTOP_MISSED_MESSAGE: 'DESKTOP_MISSED_MESSAGE',
      DESKTOP_CONVERSATION_CHANGE: 'DESKTOP_CONVERSATION_CHANGE',
      DESKTOP_SYSINFO: 'DESKTOP_SYSINFO',
      SHARE_LOCATION: 'SHARE_LOCATION',
      RELEASE_NOTES_WEB_VERSION: 'RELEASE_NOTES_WEB_VERSION',
      RELEASE_NOTES_MOBILE_VERSION: 'RELEASE_NOTES_MOBILE_VERSION',
      AUDIO_WEB_SOUNDS: 'AUDIO_WEB_SOUNDS',
      MOBILE_ENABLE_VIDEO_WIFI_ONLY: 'MOBILE_ENABLE_VIDEO_WIFI_ONLY',
      FIRST_WEB_LOGIN_TIMESTAMP: 'FIRST_WEB_LOGIN_TIMESTAMP',
      VOICEMAIL_ENABLED: 'VOICEMAIL_ENABLED',
      VOICEMAIL_TIMEOUT: 'VOICEMAIL_TIMEOUT',
      STATUS_MESSAGE_TEXT: 'STATUS_MESSAGE_TEXT',
      OPT_OUT_PRESENCE: 'OPT_OUT_PRESENCE',
      SECOND_TELEPHONY_CALL_ROUTING: 'SECOND_TELEPHONY_CALL_ROUTING',
      MS_TEAMS_CALL_INCOMING_CALL_ROUTING: 'MS_TEAMS_CALL_INCOMING_CALL_ROUTING',
      ATC_ROUTING: 'ATC_ROUTING',
      VOICEMAIL_CUSTOMGREETING_ENABLED: 'VOICEMAIL_CUSTOMGREETING_ENABLED',
      VOICEMAIL_CUSTOMGREETING_URI: 'VOICEMAIL_CUSTOMGREETING_URI',
      DESKTOP_MESSAGE_NOTIFICATIONS_SETTING: 'DESKTOP_MESSAGE_NOTIFICATIONS_SETTING',
      MOBILE_MESSAGE_NOTIFICATIONS_SETTING: 'MOBILE_MESSAGE_NOTIFICATIONS_SETTING',
      PLAY_SOUND_MESSAGE_NOTIFICATIONS: 'PLAY_SOUND_MESSAGE_NOTIFICATIONS',
      PLAY_SOUND_RTC: 'PLAY_SOUND_RTC',
      PLAY_SYSTEM_SOUNDS: 'PLAY_SYSTEM_SOUNDS',
      ENHANCED_DESKTOP_MESSAGE_NOTIFICATION: 'ENHANCED_DESKTOP_MESSAGE_NOTIFICATION',
      ENHANCED_MOBILE_MESSAGE_NOTIFICATION: 'ENHANCED_MOBILE_MESSAGE_NOTIFICATION',
      HIDE_PROFILE_EXTERNAL_ENABLED: 'HIDE_PROFILE_EXTERNAL_ENABLED',
      PLAY_PICKUP_SOUND: 'PLAY_PICKUP_SOUND',
      KEYBOARD_SHORTCUTS_ENABLED: 'KEYBOARD_SHORTCUTS_ENABLED',
      ANONYMOUS_RTC_PARTICIPANT: 'ANONYMOUS_RTC_PARTICIPANT',
      VOICEMAIL_NO_RECORDING: 'VOICEMAIL_NO_RECORDING',
      VOICEMAIL_CUSTOMGREETINGS: 'VOICEMAIL_CUSTOMGREETINGS',
      EMERGENCY_CALL_DISCLAIMER: 'EMERGENCY_CALL_DISCLAIMER',
      MSTEAMS_APPLICATION_ID: 'MSTEAMS_APPLICATION_ID'
    },

    OAuthScope: {
      ALL: 'ALL',
      READ_USER_PROFILE: 'READ_USER_PROFILE',
      WRITE_USER_PROFILE: 'WRITE_USER_PROFILE',
      READ_USER: 'READ_USER',
      CALLS: 'CALLS',
      MANAGE_PRESENCE: 'MANAGE_PRESENCE',
      USER_TO_USER: 'USER_TO_USER'
    },

    OAuthGrantTypes: {
      AUTHORIZATION_CODE: 'AUTHORIZATION_CODE',
      IMPLICIT: 'IMPLICIT',
      CLIENT_CREDENTIALS: 'CLIENT_CREDENTIALS',
      RESOURCE_OWNER_CREDENTIALS: 'RESOURCE_OWNER_CREDENTIALS',
      RESOURCE_OWNER: 'RESOURCE_OWNER'
    },

    OAuthSubtype: {
      NONE: 'NONE',
      INCOMING_WEBHOOK: 'INCOMING_WEBHOOK'
    },

    MessageNotificationsSetting: {
      ALL: 0,
      DIRECT_AND_MENTIONS: 1,
      NONE: 2
    },

    EnhancedMessageNotificationsSetting: {
      ALL: -1,
      NONE: 0,
      DIRECT: 1,
      MENTION: 2,
      FAVORITE: 4
    },

    TelephonyChangeType: {
      ADDED: 'ADDED',
      UPDATED: 'UPDATED',
      REMOVED: 'REMOVED'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Administration Data
    ///////////////////////////////////////////////////////////////////////////
    SupportType: {
      CONVERSATION: 'CONVERSATION',
      NO_SUPPORT: 'NO_SUPPORT',
      EMAIL: 'EMAIL',
      PARTNER_ADMIN_EMAIL: 'PARTNER_ADMIN_EMAIL'
    },

    TenantMode: {
      STANDALONE: 'STANDALONE',
      RINGCENTRAL: 'RINGCENTRAL',
      CLOUDLINK: 'CLOUDLINK',
      STANDALONE_WITH_CLOUDLINK_FEAT: 'STANDALONE_WITH_CLOUDLINK_FEAT'
    },

    TenantManagedBy: {
      CLOUDLINK: 'CLOUDLINK',
      UMC: 'UMC'
    },

    TenantSettingsType: {
      CREDENTIALS_LOGIN_ENABLED: 'CREDENTIALS_LOGIN_ENABLED',
      TELEPHONY_CONFIG_CONVERSATION: 'TELEPHONY_CONFIG_CONVERSATION',
      CLIENT_AUTO_UPDATE: 'CLIENT_AUTO_UPDATE',
      HELP_URL: 'HELP_URL',
      SDK_USAGE_ENABLED: 'SDK_USAGE_ENABLED',
      OAUTH_ACCESS_ENABLED: 'OAUTH_ACCESS_ENABLED',
      LOGIN_METHODS_AVAILABLE: 'LOGIN_METHODS_AVAILABLE',
      DATA_RETENTION_PERIOD: 'DATA_RETENTION_PERIOD',
      DATA_RETENTION_PERIOD_DAYS: 'DATA_RETENTION_PERIOD_DAYS',
      PLUGIN_SELF_MANAGED_ENABLED: 'PLUGIN_SELF_MANAGED_ENABLED',
      PLUGIN_DESKTOP_APP_ENABLED: 'PLUGIN_DESKTOP_APP_ENABLED',
      PLUGIN_IE_PLUGIN_ENABLED: 'PLUGIN_IE_PLUGIN_ENABLED',
      PLUGIN_MICROSOFT_OUTLOOK_INTEGRATION_ENABLED: 'PLUGIN_MICROSOFT_OUTLOOK_INTEGRATION_ENABLED',
      PLUGIN_CHROME_EXTENSION_ENABLED: 'PLUGIN_CHROME_EXTENSION_ENABLED',
      EXTENSION_MICROSOFT_EXCHANGE_ENABLED: 'EXTENSION_MICROSOFT_EXCHANGE_ENABLED',
      EXTENSION_BOX_ENABLED: 'EXTENSION_BOX_ENABLED',
      EXTENSION_GOOGLE_DRIVE_ENABLED: 'EXTENSION_GOOGLE_DRIVE_ENABLED',
      EXTENSION_SYNCPLICITY_ENABLED: 'EXTENSION_SYNCPLICITY_ENABLED',
      EXTENSION_JABRA_ENABLED: 'EXTENSION_JABRA_ENABLED',
      EXTENSION_OPEN_XCHANGE_ENABLED: 'EXTENSION_OPEN_XCHANGE_ENABLED',
      EXTENSION_PLANTRONICS_ENABLED: 'EXTENSION_PLANTRONICS_ENABLED',
      LOOKUP_API_BLACKLISTED_DOMAINS: 'LOOKUP_API_BLACKLISTED_DOMAINS',
      ROOT_DEVICES_ALLOWED: 'ROOT_DEVICES_ALLOWED',
      DT_LEGAL_INFORMATION: 'DT_LEGAL_INFORMATION',
      SSO_REDIRECT_URL: 'SSO_REDIRECT_URL',
      EXTENSION_ONE_DRIVE_ENABLED: 'EXTENSION_ONE_DRIVE_ENABLED',
      EXTENSION_SKYPE_FOR_BUSINESS_ENABLED: 'EXTENSION_SKYPE_FOR_BUSINESS_ENABLED',
      SKYPE4B_CLIENT_ID: 'SKYPE4B_CLIENT_ID',
      SKYPE4B_ROOT_URL: 'SKYPE4B_ROOT_URL',
      C4O_AUTO_UPDATE: 'C4O_AUTO_UPDATE',
      DA_AUTO_UPDATE: 'DA_AUTO_UPDATE',
      EXTENSION_SENNHEISER_ENABLED: 'EXTENSION_SENNHEISER_ENABLED',
      MUTE_ON_ENTRY: 'MUTE_ON_ENTRY',
      MUTE_ON_ENTRY_CMR: 'MUTE_ON_ENTRY_CMR',
      MUTE_ON_ENTRY_OUTCALL: 'MUTE_ON_ENTRY_OUTCALL',
      C4O_PRESENCE_SETTINGS: 'C4O_PRESENCE_SETTINGS',
      EXTENSION_ZAPIER_ENABLED: 'EXTENSION_ZAPIER_ENABLED',
      CLOUD_ISTP_DRAFT_NUMBER_LIMIT_ENABLED: 'CLOUD_ISTP_DRAFT_NUMBER_LIMIT_ENABLED',
      REMOTE_CONTROL_ENABLED: 'REMOTE_CONTROL_ENABLED',
      REMOTE_CONTROL_EXTERNAL_ENABLED: 'REMOTE_CONTROL_EXTERNAL_ENABLED',
      EXTENSION_EMBRAVA_ENABLED: 'EXTENSION_EMBRAVA_ENABLED',
      EXTENSION_CONCEPTBOARD_ENABLED: 'EXTENSION_CONCEPTBOARD_ENABLED',
      DATA_RETENTION_STRATEGY: 'DATA_RETENTION_STRATEGY',
      PLANTRONICS_HUB_DOWNLOAD_URL: 'PLANTRONICS_HUB_DOWNLOAD_URL',
      EPOS_CONNECT_DOWNLOAD_URL: 'EPOS_CONNECT_DOWNLOAD_URL',
      INCOMING_WEBHOOKS_ENABLED: 'INCOMING_WEBHOOKS_ENABLED',
      CONVERSATION_RETENTION_POLICY_CHANGEABLE: 'CONVERSATION_RETENTION_POLICY_CHANGEABLE',
      EXTENSION_GOOGLE_CONTACTS_ENABLED: 'EXTENSION_GOOGLE_CONTACTS_ENABLED',
      EXTENSION_JPL_ENABLED: 'EXTENSION_JPL_ENABLED',
      SCREENSHARE_DISABLED: 'SCREENSHARE_DISABLED',
      FILESHARE_DISABLED: 'FILESHARE_DISABLED',
      TRANSCRIPTION_ENABLED: 'TRANSCRIPTION_ENABLED',
      TRANSCRIPTION_GOOGLE_API_KEY: 'TRANSCRIPTION_GOOGLE_API_KEY',
      USER_CONTROLLED_WEBRTC_POLICY_ENABLED: 'USER_CONTROLLED_WEBRTC_POLICY_ENABLED',
      DATA_RETENTION_RECORDING_PERIOD: 'DATA_RETENTION_RECORDING_PERIOD',
      DATA_RETENTION_EXTENDED_RECORDING_PERIOD: 'DATA_RETENTION_EXTENDED_RECORDING_PERIOD',
      GLOBAL_SPACE_CREATION_ENABLED: 'GLOBAL_SPACE_CREATION_ENABLED',
      ALLOW_EXTERNAL_SPACES: 'ALLOW_EXTERNAL_SPACES',
      TRANSCRIPTION_LOCALES: 'TRANSCRIPTION_LOCALES',
      EXTENSION_MEISTERTASK_ENABLED: 'EXTENSION_MEISTERTASK_ENABLED',
      MASS_OPENSCAPE_USER_MANAGEMENT: 'MASS_OPENSCAPE_USER_MANAGEMENT',
      FAQ_URL: 'FAQ_URL',
      SPACE_DEFAULT_TYPE: 'SPACE_DEFAULT_TYPE',
      ANONYMOUS_PARTICIPANTS_ENABLED: 'ANONYMOUS_PARTICIPANTS_ENABLED',
      EXTENSION_KUANDO_ENABLED: 'EXTENSION_KUANDO_ENABLED',
      EXTENSION_C4G_ENABLED: 'EXTENSION_C4G_ENABLED',
      PASSWORD_POLICY_CONFIG_JSON: 'PASSWORD_POLICY_CONFIG_JSON',
      PASSWORD_UPDATE_DAILY_LIMIT: 'PASSWORD_UPDATE_DAILY_LIMIT',
      PASSWORD_HISTORY_COUNT: 'PASSWORD_HISTORY_COUNT',
      IC_SUPPORT_PHONE_NUMBER: 'IC_SUPPORT_PHONE_NUMBER',
      IC_SUPPORT_EMAIL_ADDRESS: 'IC_SUPPORT_EMAIL_ADDRESS',
      IC_SUPPORT_OPERATING_HOURS_FROM: 'IC_SUPPORT_OPERATING_HOURS_FROM',
      IC_SUPPORT_OPERATING_HOURS_TO: 'IC_SUPPORT_OPERATING_HOURS_TO',
      IC_SUPPORT_OPERATING_HOURS_TIMEZONE: 'IC_SUPPORT_OPERATING_HOURS_TIMEZONE',
      IC_USER_MANUAL_URL: 'IC_USER_MANUAL_URL',
      IC_SAFETY_NOTICE_URL: 'IC_SAFETY_NOTICE_URL',
      IC_ADDITIONAL_TERMS_OF_SERVICE_URL: 'IC_ADDITIONAL_TERMS_OF_SERVICE_URL',
      EXTENSION_C4C_ENABLED: 'EXTENSION_C4C_ENABLED',
      EXTENSION_C4O365_ENABLED: 'EXTENSION_C4O365_ENABLED',
      SPACE_FEDERATION: 'SPACE_FEDERATION',
      CONVERSATION_FEDERATION: 'CONVERSATION_FEDERATION',
      PRESENCE_FEDERATION: 'PRESENCE_FEDERATION',
      EXTERNAL_COMMUNICATION_BLOCKED: 'EXTERNAL_COMMUNICATION_BLOCKED',
      EVENT_ORGANIZER_CONFIG: 'EVENT_ORGANIZER_CONFIG',
      CALL_QUALITY_RATING_PERCENTAGE: 'CALL_QUALITY_RATING_PERCENTAGE',
      EXTENSION_PLANTRONICS_STATUS_ENABLED: 'EXTENSION_PLANTRONICS_STATUS_ENABLED',
      USER_BY_EMAIL_FEDERATION: 'USER_BY_EMAIL_FEDERATION',
      EXTENSION_C4TEAMS_ENABLED: 'EXTENSION_C4TEAMS_ENABLED',
      EXTENSION_MICROSOFT_SHAREPOINT_ENABLED: 'EXTENSION_MICROSOFT_SHAREPOINT_ENABLED',
      EXTENSION_LOGITECH_ENABLED: 'EXTENSION_LOGITECH_ENABLED',
      SPACE_STATISTIC_LINK_ENABLED: 'SPACE_STATISTIC_LINK_ENABLED',
      EXTENSION_PLATHOSYS_ENABLED: 'EXTENSION_PLATHOSYS_ENABLED',
      UNIFY_OFFICE_MIGRATION: 'UNIFY_OFFICE_MIGRATION',
      EXTENSION_GIGASET_ION_ENABLED: 'EXTENSION_GIGASET_ION_ENABLED',
      READ_ONLY_MODE: 'READ_ONLY_MODE',
      EXCHANGE_ONLINE_CLIENT_ID: 'EXCHANGE_ONLINE_CLIENT_ID',
      EXCHANGE_ONLINE_REDIRECT_URI: 'EXCHANGE_ONLINE_REDIRECT_URI',
      GOOGLE_CONNECTOR_CLIENT_ID: 'GOOGLE_CONNECTOR_CLIENT_ID',
      GOOGLE_CONNECTOR_REDIRECT_URI: 'GOOGLE_CONNECTOR_REDIRECT_URI',
      COMFORT_NOISE_DISABLED: 'COMFORT_NOISE_DISABLED',
      EXTENSION_YEALINK_ENABLED: 'EXTENSION_YEALINK_ENABLED',
      EXTENSION_VBET_ENABLED: 'EXTENSION_VBET_ENABLED',
      PRESENCE_HIDDEN: 'PRESENCE_HIDDEN',
      CLOUDLINK_CHAT_ENABLED: 'CLOUDLINK_CHAT_ENABLED',
      CHAT_ATTACHMENT_UPLOAD_DISABLED: 'CHAT_ATTACHMENT_UPLOAD_DISABLED'
    },

    TenantConfigurableTextType: {
      GUEST_ACCESS_ACCEPTANCE_TEXT: 'GUEST_ACCESS_ACCEPTANCE_TEXT',
      USER_INVITE_ACCEPTANCE_TEXT: 'USER_INVITE_ACCEPTANCE_TEXT'
    },

    TrunkState: {
      UP: 'UP',
      DOWN: 'DOWN',
      NOT_REQUESTED: 'NOT_REQUESTED',
      REQUESTED: 'REQUESTED',
      SUSPENDING: 'SUSPENDING'
    },

    GtcTrunkType: {
      GTC_TRUNK_TYPE: 'GTC_TRUNK_TYPE',
      ETC_TRUNK_TYPE: 'ETC_TRUNK_TYPE',
      ATC_TRUNK_TYPE: 'ATC_TRUNK_TYPE',
      EMBEDDED_TC_TRUNK_TYPE: 'EMBEDDED_TC_TRUNK_TYPE',
      OSBIZ_TRUNK_TYPE: 'OSBIZ_TRUNK_TYPE',
      MiV5000_TRUNK_TYPE: 'MiV5000_TRUNK_TYPE',
      MiVB_TRUNK_TYPE: 'MiVB_TRUNK_TYPE',
      SUB_TRUNK_TYPE: 'SUB_TRUNK_TYPE'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Third Party Connectors
    ///////////////////////////////////////////////////////////////////////////
    ThirdPartyConnectorsActionType: {
      GET_SETTINGS: 'GET_SETTINGS',
      SAVE_SETTINGS: 'SAVE_SETTINGS',
      GET_CONNECTION_STATUS: 'GET_CONNECTION_STATUS'
    },

    ThirdPartyConnectorType: {
      EXCHANGE_CONNECTOR: 'EXCHANGE_CONNECTOR',
      GOOGLE_CONNECTOR: 'GOOGLE_CONNECTOR'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Third Party Content types
    ///////////////////////////////////////////////////////////////////////////
    MsGraphDataTypes: {
      FILE: 'file',
      FOLDER: 'folder',
      DRIVE: 'drive',
      SITE: 'site',
      CONTENT: 'content',
      SUBSITES: 'subsites'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Contacts
    ///////////////////////////////////////////////////////////////////////////
    ContactActionType: {
      ADD_FAVORITE: 'ADD_FAVORITE',
      GET_CONTACTS: 'GET_CONTACTS',
      GET_CONTACTS_BY_IDS: 'GET_CONTACTS_BY_IDS',
      GET_FAVORITES: 'GET_FAVORITES',
      REMOVE_FAVORITE: 'REMOVE_FAVORITE',
      CREATE_CONTACT: 'CREATE_CONTACT',
      DELETE_CONTACT: 'DELETE_CONTACT',
      DELETE_CONTACTS_FAVORITES_BY_ORIGIN_TYPE: 'DELETE_CONTACTS_FAVORITES_BY_ORIGIN_TYPE',
      SET_FAVORITE_POSITION: 'SET_FAVORITE_POSITION'
    },

    ContactEventType: {
      FAVORITE_ADDED: 'FAVORITE_ADDED',
      FAVORITE_REMOVED: 'FAVORITE_REMOVED',
      CONTACT_CREATED: 'CONTACT_CREATED',
      CONTACT_DELETED: 'CONTACT_DELETED',
      CONTACTS_FAVORITES_DELETED_BY_ORIGIN_TYPE: 'CONTACTS_FAVORITES_DELETED_BY_ORIGIN_TYPE',
      FAVORITE_POSITION_CHANGED: 'FAVORITE_POSITION_CHANGED'
    },

    ContactOriginType: {
      UNIFY_PHONE: 'UNIFY_PHONE',
      EXCHANGE_ONLINE: 'EXCHANGE_ONLINE',
      EXCHANGE_ONPREM: 'EXCHANGE_ONPREM',
      GOOGLE_CONTACTS: 'GOOGLE_CONTACTS',
      OSBIZ_CONTACTS: 'OSBIZ_CONTACTS',
      MiV5000_CONTACTS: 'MiV5000_CONTACTS',
      DEVICE: 'DEVICE',
      PRIVATE: 'PRIVATE'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Guest Action
    ///////////////////////////////////////////////////////////////////////////
    GuestActionType: {
      VALIDATE_SESSION_INVITE_TOKEN: 'VALIDATE_SESSION_INVITE_TOKEN',
      REGISTER_SESSION_GUEST: 'REGISTER_SESSION_GUEST',
      GET_REGIONS: 'GET_REGIONS',
      GET_JOINING_INSTRUCTIONS: 'GET_JOINING_INSTRUCTIONS',
      VALIDATE_SESSION_PIN: 'VALIDATE_SESSION_PIN'
    },

    SessionInviteInfoResult: {
      INVALID: 'INVALID',
      VALID_NOSESSION: 'VALID_NOSESSION',
      VALID_SESSION: 'VALID_SESSION',
      VALID_NO_GUEST_ACCESS: 'VALID_NO_GUEST_ACCESS'
    },

    ///////////////////////////////////////////////////////////////////////////
    // User to User info Action
    ///////////////////////////////////////////////////////////////////////////
    UserRoutingMessageType: {
      ATC: 'ATC',
      CMP: 'CMP',
      CONTACT_CARD: 'CONTACT_CARD',
      DESKTOP_APP: 'DESKTOP_APP',
      SDK: 'SDK',
      STC: 'STC',
      TEAM_PICKUP: 'TEAM_PICKUP'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Version Action
    ///////////////////////////////////////////////////////////////////////////
    VersionActionType: {
      GET_VERSION: 'GET_VERSION' // Message between client and access server
    },

    ///////////////////////////////////////////////////////////////////////////
    // Account Action
    ///////////////////////////////////////////////////////////////////////////
    AccountActionType: {
      GET_PACKAGE_TEMPLATES: 'GET_PACKAGE_TEMPLATES',
      GET_ASSIGNED_PACKAGES: 'GET_ASSIGNED_PACKAGES',
      VALIDATE_ASSIGN_ACCOUNT_TEMPLATE: 'VALIDATE_ASSIGN_ACCOUNT_TEMPLATE',
      ASSIGN_ACCOUNT_TEMPLATE: 'ASSIGN_ACCOUNT_TEMPLATE',
      VALIDATE_MIGRATE_ACCOUNT: 'VALIDATE_MIGRATE_ACCOUNT',
      MIGRATE_ACCOUNT: 'MIGRATE_ACCOUNT',
      MIGRATE_MULTIPLE_USERS: 'MIGRATE_MULTIPLE_USERS',
      GET_ACCOUNTS: 'GET_ACCOUNTS',
      GET_SEARCH_TAGS: 'GET_SEARCH_TAGS',
      SUSPEND_ACCOUNT: 'SUSPEND_ACCOUNT',
      UNSUSPEND_ACCOUNT: 'UNSUSPEND_ACCOUNT',
      ASSIGN_TELEPHONY_CONFIGURATION: 'ASSIGN_TELEPHONY_CONFIGURATION',
      DELETE_TELEPHONY_CONFIGURATION: 'DELETE_TELEPHONY_CONFIGURATION',
      GET_ACCOUNT_BY_ID: 'GET_ACCOUNT_BY_ID',
      GET_ACCOUNT_BY_USER_ID: 'GET_ACCOUNT_BY_USER_ID',
      GET_TECHNICAL_ADMIN_USER_ID: 'GET_TECHNICAL_ADMIN_USER_ID',
      RENEW_ASSOCIATED_TELEPHONY_USER: 'RENEW_ASSOCIATED_TELEPHONY_USER',
      ADD_ACCOUNT_PERMISSION: 'ADD_ACCOUNT_PERMISSION',
      REMOVE_ACCOUNT_PERMISSION: 'REMOVE_ACCOUNT_PERMISSION',
      REMOVE_ALTERNATIVE_NUMBER: 'REMOVE_ALTERNATIVE_NUMBER'
    },

    ///////////////////////////////////////////////////////////////////////////
    // UserData Action
    ///////////////////////////////////////////////////////////////////////////
    UserDataActionType: {
      GET_USER_DATA: 'GET_USER_DATA'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Account Event
    ///////////////////////////////////////////////////////////////////////////
    AccountEventType: {
      MIGRATE_MULTIPLE_USERS: 'MIGRATE_MULTIPLE_USERS',
      TELEPHONY_CONFIGURATION_UPDATED: 'TELEPHONY_CONFIGURATION_UPDATED',
      TC_BALANCING_NEEDED: 'TC_BALANCING_NEEDED',
      TC_START_REASSIGNMENT: 'TC_START_REASSIGNMENT',
      TELEPHONY_PREVIOUS_ALTERNATIVE_NUMBERS_UPDATED: 'TELEPHONY_PREVIOUS_ALTERNATIVE_NUMBERS_UPDATED'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Account Data
    ///////////////////////////////////////////////////////////////////////////

    // The templateName is defined as a string in the protobuf.
    // The definition below only includes the core template names that the client
    // needs to know about. The USER and ADMIN account names have been created
    // for backwards compatibility to match with the previous roles concept. The
    // clients should avoid adding any logic based on these template names since they
    // could change in the future.
    AccountTemplateName: {
      USER: 'USER',
      ADMIN: 'ADMIN'
    },

    AccountStatus: {
      NONE: 'NONE',
      CREATED: 'CREATED',
      INITIAL: 'INITIAL',
      INVITED: 'INVITED',
      ACTIVE: 'ACTIVE',
      SUSPENDED: 'SUSPENDED',
      DELETED: 'DELETED'
    },

    GetAccountsSorting: {
      BY_FIRST_NAME: 'BY_FIRST_NAME',
      BY_LAST_NAME: 'BY_LAST_NAME',
      BY_NAME: 'BY_NAME',
      BY_PRIMARY_EMAIL: 'BY_PRIMARY_EMAIL',
      BY_PHONE_NUMBER: 'BY_PHONE_NUMBER',
      BY_LAST_LOGIN: 'BY_LAST_LOGIN',
      BY_LOCATION: 'BY_LOCATION'
    },

    GetAccountsFilterCriteria: {
      PRIMARY_EMAIL: 'PRIMARY_EMAIL',
      FIRST_NAME: 'FIRST_NAME',
      LAST_NAME: 'LAST_NAME',
      DISPLAY_NAME: 'DISPLAY_NAME',
      STATUS: 'STATUS',
      TAG: 'TAG',
      TELEPHONY_USER_ID: 'TELEPHONY_USER_ID',
      TELEPHONY_TRUNK_GROUP_ID: 'TELEPHONY_TRUNK_GROUP_ID'
    },

    SystemPermission: {
      IGNORE: 'IGNORE',
      INVITE_USER: 'INVITE_USER',
      ROLE_TELEPHONY_CONNECTOR: 'ROLE_TELEPHONY_CONNECTOR',
      ROLE_VIRTUAL_TELEPHONY_CONNECTOR: 'ROLE_VIRTUAL_TELEPHONY_CONNECTOR',
      ROLE_TECHNICAL: 'ROLE_TECHNICAL',
      ROLE_SESSION_GUEST: 'ROLE_SESSION_GUEST',
      ROLE_USER: 'ROLE_USER',
      ROLE_TENANT_ADMIN: 'ROLE_TENANT_ADMIN',
      ROLE_SUPPORT: 'ROLE_SUPPORT',
      ROLE_SYSTEM_ADMIN: 'ROLE_SYSTEM_ADMIN',
      ROLE_SYSTEM_USER: 'ROLE_SYSTEM_USER',
      ROLE_SYSTEM: 'ROLE_SYSTEM',
      MANAGE_TENANT: 'MANAGE_TENANT',
      TELEPHONY: 'TELEPHONY',
      RECORDING: 'RECORDING',
      GUEST_ACCESS: 'GUEST_ACCESS',
      MAX_PACKAGE_ACCOUNTS: 'MAX_PACKAGE_ACCOUNTS',
      FREE_TRIAL: 'FREE_TRIAL',
      USER_STORAGE: 'USER_STORAGE',
      INTEGRATIONS: 'INTEGRATIONS',
      RTC_PARTICIPANTS: 'RTC_PARTICIPANTS',
      ACCOUNTS: 'ACCOUNTS',
      IE_PLUGIN_SUPPORT: 'IE_PLUGIN_SUPPORT',
      SUPPORT_CONVERSATION: 'SUPPORT_CONVERSATION',
      SUPPORT_FORUM: 'SUPPORT_FORUM',
      OUTLOOK_PLUGIN: 'OUTLOOK_PLUGIN',
      TENANT_STORAGE: 'TENANT_STORAGE',
      LDAP_AGENT: 'LDAP_AGENT',
      INVITE_PARTNER: 'INVITE_PARTNER',
      LARGE_CONFERENCE: 'LARGE_CONFERENCE',
      CREATE_COMMUNITY: 'CREATE_COMMUNITY',
      CONTENT_MODERATOR: 'CONTENT_MODERATOR',
      SDK_ACCESS: 'SDK_ACCESS',
      OAUTH_ACCESS_ENABLED: 'OAUTH_ACCESS_ENABLED',
      SDK_USAGE_ENABLED: 'SDK_USAGE_ENABLED',
      BASIC_AUTHENTICATION: 'BASIC_AUTHENTICATION',
      ENTITY_ACCESS_DENIED: 'ENTITY_ACCESS_DENIED',
      ROLE_BOT: 'ROLE_BOT',
      NO_SECURITY_CONTEXT: 'NO_SECURITY_CONTEXT',
      NO_OAUTH_SCOPE_ACCESS: 'NO_OAUTH_SCOPE_ACCESS',
      RTC_WHITE_BOARD: 'RTC_WHITE_BOARD',
      SYSTEM_ANALYTICS: 'SYSTEM_ANALYTICS',
      VENDOR_ANALYTICS: 'VENDOR_ANALYTICS',
      TENANT_ANALYTICS: 'TENANT_ANALYTICS',
      USER_ANALYTICS: 'USER_ANALYTICS',
      UCAAS: 'UCAAS',
      DEVELOPER_CONSOLE_ACCESS: 'DEVELOPER_CONSOLE_ACCESS',
      DEVELOPER_CONSOLE_VIEW: 'DEVELOPER_CONSOLE_VIEW',
      DEVELOPER_CONSOLE_MANAGE_USER: 'DEVELOPER_CONSOLE_MANAGE_USER',
      S4B_INTEGRATION: 'S4B_INTEGRATION',
      TENANT_PRESENCE_SUBSCRIPTION: 'TENANT_PRESENCE_SUBSCRIPTION',
      CONCURRENT_VIDEO_CHANNELS: 'CONCURRENT_VIDEO_CHANNELS',
      WORKFLOW_INTEGRATIONS: 'WORKFLOW_INTEGRATIONS',
      CLV_SUPERADMIN: 'CLV_SUPERADMIN',
      UCAAS_MLHG_QUEUING: 'UCAAS_MLHG_QUEUING',
      DISTRIBUTOR_ANALYTICS: 'DISTRIBUTOR_ANALYTICS',
      RESELLER_ANALYTICS: 'RESELLER_ANALYTICS',
      CLV_ACCESS: 'CLV_ACCESS',
      MAX_OAUTH_APPS: 'MAX_OAUTH_APPS',
      MAX_OAUTH_BOTS: 'MAX_OAUTH_BOTS',
      CREATE_TENANT: 'CREATE_TENANT',
      MAX_INCOMING_WEBHOOKS: 'MAX_INCOMING_WEBHOOKS',
      HD_VIDEO: 'HD_VIDEO',
      HD_SCREENSHARE: 'HD_SCREENSHARE',
      MAX_PRESENCE_SUBSCRIPTIONS: 'MAX_PRESENCE_SUBSCRIPTIONS',
      SPACE_CREATION: 'SPACE_CREATION',
      SPACE_MASTER: 'SPACE_MASTER',
      UCAAS_EA: 'UCAAS_EA',
      ANALYTICS: 'ANALYTICS',
      VIEW_OPEN_CONVERSATION: 'VIEW_OPEN_CONVERSATION',
      IE_PLUGIN_SUPPORT_LIMITATION: 'IE_PLUGIN_SUPPORT_LIMITATION',
      ROLE_MEETING_POINT: 'ROLE_MEETING_POINT',
      EVENT_ORGANIZER_BASIC: 'EVENT_ORGANIZER_BASIC',
      ORGANIZE_EVENTS: 'ORGANIZE_EVENTS',
      EVENT_ORGANIZER_XS: 'EVENT_ORGANIZER_XS',
      EVENT_ORGANIZER_S: 'EVENT_ORGANIZER_S',
      EVENT_ORGANIZER_M: 'EVENT_ORGANIZER_M',
      EVENT_ORGANIZER_L: 'EVENT_ORGANIZER_L',
      REST_ADMIN_API: 'REST_ADMIN_API',
      VOICEMAIL: 'VOICEMAIL',
      TRANSCRIPTION: 'TRANSCRIPTION'
    },

    PermissionType: {
      TENANT_QUOTA: 'TENANT_QUOTA',
      USER_QUOTA: 'USER_QUOTA',
      FEATURE: 'FEATURE',
      OVERALL_PACKAGE_QUOTA: 'OVERALL_PACKAGE_QUOTA',
      OVERALL_PACKAGE_FEATURE: 'OVERALL_PACKAGE_FEATURE',
      RESTRICTION: 'RESTRICTION'
    },

    MigrateMultipleStatusCode: {
      RUNNING: 'RUNNING',
      FINISHED: 'FINISHED'
    },

    MigrateResponseCode: {
      NULL_PARAMETER: 'NULL_PARAMETER',
      ACCOUNT_DOES_NOT_EXIST: 'ACCOUNT_DOES_NOT_EXIST',
      TECHNICAL_ACCOUNT_NOT_ALLOWED: 'TECHNICAL_ACCOUNT_NOT_ALLOWED',
      ACCOUNT_TEMPLATE_DOES_NOT_EXIST: 'ACCOUNT_TEMPLATE_DOES_NOT_EXIST',
      ILLEGAL_ACCOUNT_TEMPLATE_ASSIGNMENT: 'ILLEGAL_ACCOUNT_TEMPLATE_ASSIGNMENT',
      MAXIMUM_ACCOUNTS_REACHED: 'MAXIMUM_ACCOUNTS_REACHED',
      ERROR: 'ERROR',
      OK: 'OK',
      OK_NO_CHANGES: 'OK_NO_CHANGES'
    },

    FilesSortBy: {
      FILE_NAME: 'FILE_NAME',
      CREATION_TIME: 'CREATION_TIME',
      FILE_SIZE: 'FILE_SIZE',
      MIME_TYPE: 'MIME_TYPE',
      FILE_EXTENSION: 'FILE_EXTENSION'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Integrations framework (CPaaS area)
    ///////////////////////////////////////////////////////////////////////////
    TenantIntegrationState: {
      ACTIVE: 'ACTIVE',
      DISABLED: 'DISABLED',
      SHUTDOWN: 'SHUTDOWN',
      STARTUP: 'STARTUP'
    },

    IntegrationKeys: {
      MEISTERTASK: 'MEISTERTASK',
      INCOMING_WEBHOOKS: 'INCOMING-WEBHOOKS'
    },

    MeistertaskIntegrationType: {
      GET_USER_PROJECTS: 'GET_USER_PROJECTS'
    },

    ///////////////////////////////////////////////////////////////////////////
    // Internal definitions
    ///////////////////////////////////////////////////////////////////////////
    LabFeatureName: {
      MS_TEAMS_PLUGIN: 'MS_TEAMS_PLUGIN'
    },

    AvatarBadgeType: {
      COMMUNITY: 'COMMUNITY',
      EVENT: 'EVENT',
      EXPERT: 'EXPERT'
    },

    // Device categories for device selection
    AudioVideoDeviceCategories: {
      AUDIO_OUTPUT: 'AUDIO_OUTPUT',
      RINGING_OUTPUT: 'RINGING_OUTPUT',
      AUDIO_INPUT: 'AUDIO_INPUT',
      AUDIO_DEVICE: 'AUDIO_DEVICE',
      VIDEO_INPUT: 'VIDEO_INPUT'
    },

    // Transcription
    RecordingState: {
      IDLE: 'IDLE',
      LISTENING: 'LISTENING',
      PROCESSING: 'PROCESSING'
    },

    TranscriptionError: {
      RECORDING_NOT_SUPPORTED: 'RECORDING_NOT_SUPPORTED',
      RECORDING_NOT_STARTED: 'RECORDING_NOT_STARTED',
      RECORDING_IN_PROGRESS: 'RECORDING_IN_PROGRESS',
      RECORDING_ABORTED: 'RECORDING_ABORTED',
      INVALID_CALL: 'INVALID_CALL',
      INVALID_PROVIDER: 'INVALID_PROVIDER'
    },

    ///////////////////////////////////////////////////////////////////////////
    // ATC capabilities
    ///////////////////////////////////////////////////////////////////////////
    AtcCapabilities: {
      EXTENDED_ALERTING: 'EXTENDED_ALERTING',
      CLEAR_CONNECTION_BUSY: 'CLEAR_CONNECTION_BUSY',
      PBX_CALL_LOG: 'PBX_CALL_LOG'
    },

    ///////////////////////////////////////////////////////////////////////////
    // STC capabilities
    ///////////////////////////////////////////////////////////////////////////
    StcCapabilities: {
      STC_TRANSFER: 'STC_TRANSFER',
      STC_MERGE: 'STC_MERGE'
    },

    ///////////////////////////////////////////////////////////////////////////
    // TC available features
    ///////////////////////////////////////////////////////////////////////////
    TcAvailableFeatures: {
      HOLD: 'HOLD',
      CONSULTATION: 'CONSULTATION',
      MERGE: 'MERGE',
      TRANSFER: 'TRANSFER'
    }
  });

  var Proto = {
    requestNr: 0,
    Request: function (contentType, content, tenantContext) {
      Proto.requestNr++;

      var msg = {
        msgType: Constants.WSMessageType.REQUEST,
        request: {
          requestId: Proto.requestNr,
          type: contentType
        }
      };
      if (tenantContext) {
        msg.request.tenantContext = tenantContext;
      }

      switch (contentType) {
        case Constants.ContentType.CONVERSATION:
          msg.request.conversation = content;
          break;
        case Constants.ContentType.USER:
          msg.request.user = content;
          break;
        case Constants.ContentType.CONTACT:
          msg.request.contact = content;
          break;
        case Constants.ContentType.RTC_CALL:
          msg.request.rtcCall = content;
          break;
        case Constants.ContentType.RTC_SESSION:
          msg.request.rtcSession = content;
          break;
        case Constants.ContentType.SEARCH:
          msg.request.search = content;
          break;
        case Constants.ContentType.INSTRUMENTATION:
          msg.request.instrumentation = content;
          break;
        case Constants.ContentType.SYSTEM:
          msg.request.system = content;
          break;
        case Constants.ContentType.THIRDPARTYCONNECTORS:
          msg.request.thirdpartyconnectors = content;
          break;
        case Constants.ContentType.GUEST:
          msg.request.guest = content;
          break;
        case Constants.ContentType.USER_TO_USER:
          msg.request.userToUser = content;
          break;
        case Constants.ContentType.VERSION:
          msg.request.version = content;
          break;
        case Constants.ContentType.ACCOUNT:
          msg.request.account = content;
          break;
        case Constants.ContentType.USER_DATA:
          msg.request.userData = content;
          break;
        case Constants.ContentType.CTI_CALL:
          msg.request.ctiCall = content;
          break;
      }

      return msg;
    },
    // ParseEvent parses the event from the server to:
    // a) build the event name the services subscribed to, and
    // b) flatten the event object a bit to better suit the services
    // eslint-disable-next-line complexity, max-lines-per-function
    ParseEvent: function (event) {
      // NOSONAR
      var evtName;
      var evtData = {};

      switch (event.type) {
        // CONVERSATION
        case Constants.ContentType.CONVERSATION:
          evtName = 'Conversation.' + event.conversation.type;
          switch (event.conversation.type) {
            case Constants.ConversationEventType.CREATE:
              evtData = event.conversation.create;
              break;
            case Constants.ConversationEventType.UPDATE:
              evtData = event.conversation.update;
              break;
            case Constants.ConversationEventType.ADD_ITEM:
              evtData = event.conversation.addItem;
              break;
            case Constants.ConversationEventType.ADD_PARTICIPANT:
              evtData = event.conversation.addParticipant;
              break;
            case Constants.ConversationEventType.UPDATE_ITEM:
              evtData = event.conversation.updateItem;
              break;
            case Constants.ConversationEventType.READ_ITEMS:
              evtData = event.conversation.readItems;
              break;
            case Constants.ConversationEventType.TYPING:
              evtData = event.conversation.typing;
              break;
            case Constants.ConversationEventType.DRAFT_MESSAGE_SAVED:
              evtData = event.conversation.draftMessage;
              break;
            case Constants.ConversationEventType.DRAFT_MESSAGE_DISCARDED:
              evtData = event.conversation.draftMessage;
              break;
            case Constants.ConversationEventType.DRAFT_MESSAGE_PUBLISHED:
              evtData = event.conversation.draftMessage;
              break;
            case Constants.ConversationEventType.FLAG_ITEM:
              evtData = event.conversation.setFlagItem;
              break;
            case Constants.ConversationEventType.CLEAR_FLAGGED_ITEM:
              evtData = event.conversation.clearFlaggedItem;
              break;
            case Constants.ConversationEventType.CONVERSATION_MARKED:
              evtData = event.conversation.conversationMarked;
              break;
            case Constants.ConversationEventType.CONVERSATION_UNMARKED:
              evtData = event.conversation.conversationUnmarked;
              break;
            case Constants.ConversationEventType.FAVORITE_POSITION_CHANGED:
              evtData = event.conversation.favoritePositionChangedEvent;
              break;
            case Constants.ConversationEventType.USER_DATA_CHANGED:
              evtData = event.conversation.userDataChangedEvent;
              break;
            case Constants.ConversationEventType.JOURNAL_ENTRY_DELETED:
              evtData = event.conversation.journalEntryDeletedEvent;
              break;
            case Constants.ConversationEventType.ALL_JOURNAL_ENTRIES_DELETED:
              evtData = event.conversation.allJournalEntriesDeleted;
              break;
          }
          evtData.convId = event.conversation.convId;
          break;

        // USER
        case Constants.ContentType.USER:
          evtName = 'User.' + event.user.type;
          switch (event.user.type) {
            case Constants.UserEventType.USER_PRESENCE_CHANGE:
              evtData = event.user.presenceChanged;
              break;
            case Constants.UserEventType.USER_UPDATED:
              evtData = event.user.userUpdated;
              break;
            case Constants.UserEventType.SESSION_CLOSED:
              evtData = event.user.sessionClosed;
              break;
            case Constants.UserEventType.PASSWORD_CHANGED:
              evtData = event.user.passwordChanged;
              break;
            case Constants.UserEventType.USER_SETTING_UPDATED:
              evtData = event.user.userSettingUpdated;
              break;
            case Constants.UserEventType.TELEPHONY_DATA:
              evtData = event.user.telephonyData;
              break;
            case Constants.UserEventType.DEVICE_LIST_CHANGED:
              evtData = event.user.deviceListChangedEvent;
              break;
            case Constants.UserEventType.NOTIFICATION_SUBSCRIPTION_CHANGE:
              evtData = event.user.notificationSubscriptionChange;
              break;
            case Constants.UserEventType.TENANT_CONFIGURATION_UPDATED:
              evtData = event.user.tenantConfigurationUpdatedEvent;
              break;
            case Constants.UserEventType.TENANT_PRESENCE_CHANGE:
              evtData = event.user.tenantPresenceChanged;
              break;
            case Constants.UserEventType.TELEPHONY_CHANGED:
              evtData = event.user.telephonyChangedEvent;
              break;
          }
          evtData.userId = event.user.userId;
          break;

        // CTI_CALL
        case Constants.ContentType.CTI_CALL:
          evtName = 'CTICall.' + event.ctiCall.type;
          switch (event.ctiCall.type) {
            case Constants.CTICallEventType.HOLD:
              evtData = event.ctiCall.data;
              break;
            case Constants.CTICallEventType.RETRIEVE:
              evtData = event.ctiCall.data;
              break;
            case Constants.CTICallEventType.MUTE:
              evtData = event.ctiCall.data;
              break;
            case Constants.CTICallEventType.MERGE:
              evtData = event.ctiCall.data;
              break;
            case Constants.CTICallEventType.SWAP:
              evtData = event.ctiCall.data;
              break;
            case Constants.CTICallEventType.CONSULT:
              evtData = event.ctiCall.data;
              break;
            case Constants.CTICallEventType.TRANSFER:
              evtData = event.ctiCall.data;
              break;
            case Constants.CTICallEventType.DTMF:
              evtData = event.ctiCall.data;
              break;
            case Constants.CTICallEventType.ANSWER_ON_OTHER:
              evtData = event.ctiCall.data;
              break;
            case Constants.CTICallEventType.HANGUP:
              evtData = event.ctiCall.data;
              break;
            case Constants.CTICallEventType.PUSH_WEBRTC_CALL:
              evtData = event.ctiCall.data;
              break;
            case Constants.CTICallEventType.DIAL_ON_OTHER:
              evtData = event.ctiCall.data;
              break;
          }
          break;

        // RTC_CALL
        case Constants.ContentType.RTC_CALL:
          evtName = 'RTCCall.' + event.rtcCall.type;
          switch (event.rtcCall.type) {
            case Constants.RTCCallEventType.SDP_ANSWER:
              evtData = event.rtcCall.sdpAnswer;
              break;
            case Constants.RTCCallEventType.SDP_FAILED:
              evtData = event.rtcCall.sdpFailed;
              break;
            case Constants.RTCCallEventType.INVITE:
              evtData = event.rtcCall.invite;
              break;
            case Constants.RTCCallEventType.INVITE_CANCEL:
              evtData = event.rtcCall.inviteCancel;
              break;
            case Constants.RTCCallEventType.INVITE_FAILED:
              evtData = event.rtcCall.inviteFailed;
              break;
            case Constants.RTCCallEventType.CHANGE_MEDIA_TYPE_REQUESTED:
              evtData = event.rtcCall.changeMediaRequested;
              break;
            case Constants.RTCCallEventType.PROGRESS:
              evtData = event.rtcCall.participantProgress;
              break;
            case Constants.RTCCallEventType.CHANGE_MEDIA_TYPE_FORCED:
              evtData = event.rtcCall.changeMediaTypeForcedEvent;
              break;
            case Constants.RTCCallEventType.ICE_CANDIDATES:
              evtData = event.rtcCall.iceCandidates;
              break;
            case Constants.RTCCallEventType.RTC_QUALITY_RATING_EVENT:
              evtData = event.rtcCall.ratingEvent;
              break;
          }
          evtData.sessionId = event.rtcCall.sessionId;
          evtData.instanceId = event.rtcCall.instanceId;
          break;

        // RTC_SESSION
        case Constants.ContentType.RTC_SESSION:
          evtName = 'RTCSession.' + event.rtcSession.type;
          switch (
            event.rtcSession.type // NOSONAR
          ) {
            case Constants.RTCSessionEventType.SESSION_STARTED:
              evtData = event.rtcSession.sessionStarted;
              break;
            case Constants.RTCSessionEventType.SESSION_TERMINATED:
              evtData = event.rtcSession.sessionTerminated;
              break;
            case Constants.RTCSessionEventType.SESSION_UPDATED:
              evtData = event.rtcSession.sessionUpdated;
              break;
            case Constants.RTCSessionEventType.SESSION_MOVED:
              evtData = event.rtcSession.sessionMoved;
              break;
            case Constants.RTCSessionEventType.PARTICIPANT_JOINED:
              evtData = event.rtcSession.participantJoined;
              break;
            case Constants.RTCSessionEventType.PARTICIPANT_LEFT:
              evtData = event.rtcSession.participantLeft;
              break;
            case Constants.RTCSessionEventType.PARTICIPANT_UPDATED:
              evtData = event.rtcSession.participantUpdated;
              break;
            case Constants.RTCSessionEventType.ACTIVE_SPEAKER:
              evtData = event.rtcSession.speaker;
              break;
            case Constants.RTCSessionEventType.VIDEO_ACTIVE_SPEAKER:
              evtData = event.rtcSession.videoSpeaker;
              break;
            case Constants.RTCSessionEventType.SESSION_RECORDING_INFO:
              evtData = event.rtcSession.sessionRecordingInfo;
              break;
            case Constants.RTCSessionEventType.SESSION_RECORDING_STARTED:
              evtData = event.rtcSession.sessionRecordingStarted;
              break;
            case Constants.RTCSessionEventType.SESSION_RECORDING_STOPPED:
              evtData = event.rtcSession.sessionRecordingStopped;
              break;
            case Constants.RTCSessionEventType.SESSION_RECORDING_FAILED:
              evtData = event.rtcSession.sessionRecordingFailed;
              break;
            case Constants.RTCSessionEventType.SESSION_TERMINATION_TIMER_STARTED:
              evtData = event.rtcSession.sessionTerminationTimerStarted;
              break;
            case Constants.RTCSessionEventType.SESSION_TERMINATION_TIMER_CANCELLED:
              evtData = event.rtcSession.sessionTerminationTimerCancelled;
              break;
            case Constants.RTCSessionEventType.LIVE_TRANSCRIPTION:
              evtData = event.rtcSession.liveTranscriptionEvent;
              break;
          }
          evtData.sessionId = event.rtcSession.sessionId;
          break;

        // SEARCH
        case Constants.ContentType.SEARCH:
          evtName = 'Search.' + event.search.type;
          switch (event.search.type) {
            case Constants.SearchEventType.BASIC_SEARCH_RESULT:
              evtData = event.search.basicSearchResult;
              break;
            case Constants.SearchEventType.DETAILED_SEARCH_RESULT:
              evtData = event.search.detailSearchResult;
              break;
            case Constants.SearchEventType.SEARCH_STATUS:
              evtData = event.search.searchStatus;
              break;
            case Constants.SearchEventType.RECENT_SEARCH_ADDED:
              evtData = event.search.recentSearchAdded;
              break;
          }
          evtData.searchId = event.search.searchId;
          break;

        // SYSTEM
        case Constants.ContentType.SYSTEM:
          evtName = 'System.' + event.system.type;
          switch (event.system.type) {
            case Constants.SystemEventType.BROADCAST:
              evtData = event.system.systemNotificationEvent;
              evtData.eventId = event.system.eventId;
              break;
            case Constants.SystemEventType.MAINTENANCE:
            case Constants.SystemEventType.MAINTENANCE_ADDED:
              evtData = event.system.maintenance;
              evtData.eventId = event.system.eventId;
              break;
            case Constants.SystemEventType.BROADCAST_REMOVED:
            case Constants.SystemEventType.MAINTENANCE_REMOVED:
              // Event has no data
              evtData = event.system.eventId;
              break;
          }
          break;

        // USER_TO_USER
        case Constants.ContentType.USER_TO_USER:
          evtName = 'UserToUser.' + event.userToUser.userToUser.type;
          // The event handling is the same for all USER_TO_USER events
          evtData = event.userToUser.userToUser;
          evtData.routing = event.userToUser.routing;
          break;

        // ACCOUNT
        case Constants.ContentType.ACCOUNT:
          evtName = 'Account.' + event.account.type;
          switch (event.account.type) {
            case Constants.AccountEventType.MIGRATE_MULTIPLE_USERS:
              evtData = event.account.migrateMultipleUsers;
              break;
            case Constants.AccountEventType.TELEPHONY_CONFIGURATION_UPDATED:
              evtData = event.account.telephonyConfigurationUpdated;
              break;
            case Constants.AccountEventType.TC_START_REASSIGNMENT:
              evtData = event.account.tcStartReassignment;
              break;
            case Constants.AccountEventType.TELEPHONY_PREVIOUS_ALTERNATIVE_NUMBERS_UPDATED:
              evtData = event.account.previousAlternativeNumbersUpdated;
              break;
          }
          break;

        // CONTACT
        case Constants.ContentType.CONTACT:
          evtName = 'Contact.' + event.contact.type;
          switch (event.contact.type) {
            case Constants.ContactEventType.FAVORITE_ADDED:
              evtData = event.contact.addedFavorite;
              break;
            case Constants.ContactEventType.FAVORITE_REMOVED:
              evtData = event.contact.removedFavorite;
              break;
            case Constants.ContactEventType.CONTACT_CREATED:
              evtData = event.contact.createdContact;
              break;
            case Constants.ContactEventType.CONTACT_DELETED:
              evtData = event.contact.deletedContact;
              break;
            case Constants.ContactEventType.CONTACTS_FAVORITES_DELETED_BY_ORIGIN_TYPE:
              evtData = event.contact.deletedContactsFavoritesByOriginType;
              break;
            case Constants.ContactEventType.FAVORITE_POSITION_CHANGED:
              evtData = event.contact.favoritePositionChangedEvent;
          }
          break;

        default:
          return null;
      }

      return { name: evtName, data: evtData };
    },
    getMediaType: function (data) {
      if (!data) {
        return { audio: false, video: false, desktop: false };
      }

      // data is an array of RealtimeMediaType ('AUDIO', 'VIDEO', 'DESKTOP_SHARING')
      var mediaType = { audio: false, video: false, desktop: false };
      data.forEach(function (elem) {
        switch (elem) {
          case Constants.RealtimeMediaType.AUDIO:
            mediaType.audio = true;
            break;
          case Constants.RealtimeMediaType.VIDEO:
            mediaType.video = true;
            break;
          case Constants.RealtimeMediaType.DESKTOP_SHARING:
            mediaType.desktop = true;
            break;
        }
      });
      return mediaType;
    },
    isOfflineFailure: function (err) {
      return err === Constants.ReturnCode.DISCONNECTED || err === Constants.ReturnCode.FAILED_TO_SEND || err === Constants.ReturnCode.REQUEST_TIMEOUT;
    }
  };

  // Exports
  unifyPhone.Constants = Constants;
  unifyPhone.Proto = Proto;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
// Define global variables for JSHint

/*exported ControllerRouteError, ScrollDirection, ScrollPosition, UIEventType*/

/*
    UIEventType used in UISvc and uiDirectives.
**/
var UIEventType = Object.freeze({
  FOCUS: 'uFocus',
  FOCUS_ELEMENT: 'uFocusElement',
  BLUR: 'uBlur',
  SCROLL_TO_ITEM: 'uScrollItem',
  SCROLL_TO_OFFSET: '', // used in existing uScrollContainer directive
  SCROLL_TO_ELEMENT: 'scrollToElement',
  ADD_ITEM: 'addItem',
  CHECK_ITEM_VISIBILITY: 'checkItemVisibility',
  CHECK_THREAD_VISIBILITY: 'checkThreadVisibility',
  SCROLL_DIRECTION_CHANGED: 'scrollDirectionChanged',
  CLEAR_SEARCH: 'clearSearch',
  SHOW_FLOATER: 'showFloater',
  NOTIFY_LEARNING_MARKS: 'notifyLearningMarks',
  CLOSE_LEARNING_MARKS: 'closeLearningMarks',
  TRIGGER_EMAIL_SEARCH: 'triggerEmailSearch',
  SHOW_OVERLAY: 'showOverlay',
  HIDE_OVERLAY: 'hideOverlay',
  ADJUST_ITEMS_SCROLL: 'adjustItemsScroll',
  ADJUST_REMOVED_THREAD_SCROLL: 'adjustRemovedThreadScroll',
  IS_FIELD_EMPTY: 'isFieldEmpty',
  GET_THREAD_ITEM_HEIGHT: 'getThreadItemHeight',
  GET_THREAD_HEIGHT: 'getThreadHeight',
  CONTACT_SELECTED: 'contactSelected',
  CONTACT_UNSELECTED: 'contactUnselected',
  SYNC_SELECTED_CONTACTS: 'syncSelectedContacts',
  CUSTOM_DRAG_LEAVE: 'dragleave',
  CUSTOM_DRAG_ENTER: 'dragenter',
  SHOW_VIDEO: 'showVideo',
  RESET_INFINITE_SCROLL: 'resetInfiniteScroll'
});

/*
    Enum for scroll positions
**/
var ScrollPosition = Object.freeze({
  TOP: 'top',
  BOTTOM: 'bottom',
  MIDDLE: 'middle',
  NONE: 'none' // no scrollbar
});

/*
    Enum for scroll direction
**/
var ScrollDirection = Object.freeze({
  UP: 'up',
  DOWN: 'down',
  NONE: 'none'
});

/*
    Enum Controller Route Errors
**/
var ControllerRouteError = Object.freeze({
  CONVERSATION_NOT_FOUND: 'CONVERSATION_NOT_FOUND',
  DRAFT_NOT_FOUND: 'DRAFT_NOT_FOUND',
  GENERIC_ERROR: 'GENERIC_ERROR',
  INSUFFICIENT_ACCESS_RIGHTS: 'INSUFFICIENT_ACCESS_RIGHTS',
  NO_ACCESS_OPEN_CONVERSATIONS: 'NO_ACCESS_OPEN_CONVERSATIONS',
  USER_NOT_FOUND: 'USER_NOT_FOUND'
}); /* global UnifyPhone */

/*exported PasswordMgmt*/

var PasswordMgmt = {};

(function () {
  'use strict';

  var logger = UnifyPhone.logger;

  var DEFAULT_POLICY = {
    // Max password length - Not enforced at the moment
    maxLength: -1,
    // Min password length
    minLength: 8,
    // Number of the last passwords that cannot be matched
    numberOfLastPasswords: 8,
    // Number of required special characters
    numberOfSpecialChars: 1,
    // Number of required upper characters
    numberOfUpperChars: 1,
    // Number of required lower characters
    numberOfLowerChars: 1,
    // Number of required digits
    numberOfDigits: 1,
    // Max number of allowed repeated characters
    maxNumberOfRepeatedChars: 2,
    // Password is allowed to contains a space.
    // Regardless of this setting it is not allowed to start or end a password with a whitespace
    isSpaceAllowed: true
  };

  PasswordMgmt.ErrorType = Object.freeze({
    IMMEDIATE: 'IMMEDIATE',
    DELAYED: 'DELAYED',
    SERVER: 'SERVER'
  });

  PasswordMgmt.policyConfig = Object.assign({}, DEFAULT_POLICY);

  PasswordMgmt.setPolicy = function (policy) {
    PasswordMgmt.policyConfig = Object.assign({}, DEFAULT_POLICY, policy || {});
    logger.info('[PasswordMgmt]: Set password policy config to ', PasswordMgmt.policyConfig);
  };

  PasswordMgmt.getPolicy = function () {
    // API for mobile clients
    return PasswordMgmt.policyConfig;
  };

  PasswordMgmt.getInstructions = function (i18n) {
    var policy = PasswordMgmt.policyConfig;

    var res = 'res_PasswordInstructions';
    var params = [policy.minLength, policy.numberOfUpperChars, policy.numberOfLowerChars, policy.numberOfDigits, policy.numberOfSpecialChars];
    if (i18n) {
      return i18n.localize(res, params);
    } else {
      return {
        res: res,
        params: params
      };
    }
  };

  PasswordMgmt.validate = function (password, attrs) {
    attrs = attrs || {};
    password = password || '';
    var upper = password.toUpperCase();
    var lower = password.toLowerCase();
    var firstName = attrs.firstName && attrs.firstName.toLowerCase();
    var lastName = attrs.lastName && attrs.lastName.toLowerCase();
    var email = attrs.email && attrs.email.toLowerCase();

    var policy = PasswordMgmt.policyConfig;
    var errors = [];

    // Must contain at least MIN characters
    if (policy.minLength > 0) {
      errors[0] = password.length < policy.minLength;
    }

    var hasSpace = false;
    var numSpecial = 0;
    var numUpper = 0;
    var numLower = 0;
    var numDigits = 0;
    var numRepeated = 0;
    var maxRepeated = 0;
    var prevChar = '';

    for (var i = 0; i < password.length; i++) {
      var currChar = password.charAt(i);
      if (currChar === prevChar) {
        numRepeated++;
        maxRepeated = Math.max(maxRepeated, numRepeated);
      } else {
        prevChar = currChar;
        numRepeated = 1;
      }

      if (currChar === ' ') {
        hasSpace = true;
      } else if (/^\d$/.test(currChar)) {
        numDigits++;
      } else if (upper.charAt(i) === lower.charAt(i)) {
        // If there is no different between uppercase and lowercase it must be a special character
        numSpecial++;
      } else if (upper.charAt(i) === currChar) {
        numUpper++;
      } else {
        numLower++;
      }
    }

    // Check number of special characters, upper-case, lower-case and digits
    errors[1] =
      numSpecial < policy.numberOfSpecialChars ||
      numUpper < policy.numberOfUpperChars ||
      numLower < policy.numberOfLowerChars ||
      numDigits < policy.numberOfDigits;

    // If password includes a space check if it is allowed
    errors[2] = hasSpace && !policy.isSpaceAllowed;

    // Check if there are more than X repeated characters
    errors[3] = maxRepeated > policy.maxNumberOfRepeatedChars;

    // Check if user put his own name or family name anywhere in the password
    errors[4] = !!((firstName && lower.indexOf(firstName) !== -1) || (lastName && lower.indexOf(lastName) !== -1));

    // Check if user put his own email
    errors[5] = !!(email && lower.indexOf(email) !== -1);

    // Check if user put leading or trailing spaces
    errors[6] = password !== password.trim();
    return errors;
  };

  PasswordMgmt.validateAndReturnError = function (password, attrs, i18n) {
    var policy = PasswordMgmt.policyConfig;
    var errors = PasswordMgmt.validate(password, attrs);

    var res, params, errorType;

    if (errors[2]) {
      // No spaces
      res = 'res_errorPasswordRule02';
      errorType = PasswordMgmt.ErrorType.IMMEDIATE;
    } else if (errors[3]) {
      // Max repeated characters
      var num = policy.maxNumberOfRepeatedChars + 1;
      res = 'res_errorPasswordRule03';
      params = [num, 'a'.repeat(num)];
      errorType = PasswordMgmt.ErrorType.IMMEDIATE;
    } else if (errors[4]) {
      // Cannot use first and last name
      res = 'res_errorPasswordRule04';
      errorType = PasswordMgmt.ErrorType.IMMEDIATE;
    } else if (errors[5]) {
      // Cannot user email
      res = 'res_errorPasswordRule05';
      errorType = PasswordMgmt.ErrorType.IMMEDIATE;
    } else if (errors[6]) {
      // No leading or trailing spaces
      res = 'res_errorPasswordRule06';
      errorType = PasswordMgmt.ErrorType.IMMEDIATE;
    } else if (errors[0] && errors[1]) {
      // Show instructions
      var data = PasswordMgmt.getInstructions();
      res = data.res;
      params = data.params;
      errorType = PasswordMgmt.ErrorType.DELAYED;
    } else if (errors[0]) {
      // Min length
      res = 'res_errorPasswordRule00';
      params = [policy.minLength];
      errorType = PasswordMgmt.ErrorType.DELAYED;
    } else if (errors[1]) {
      res = 'res_errorPasswordRule01';
      params = [policy.numberOfUpperChars, policy.numberOfLowerChars, policy.numberOfDigits, policy.numberOfSpecialChars];
      errorType = PasswordMgmt.ErrorType.DELAYED;
    } else {
      // No errors
      return null;
    }

    if (i18n) {
      return {
        text: i18n.localize(res, params),
        errorType: errorType
      };
    } else {
      return {
        res: res,
        params: params,
        errorType: errorType
      };
    }
  };
})(); // The code in this module is based on the sip.js module and has been

// modified for Ansible

/* sip.js *********************************************************************

Copyright (c) 2010 Kirill Mikhailov (kirill.mikhailov@gmail.com)

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.

******************************************************************************/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  var Utils = unifyPhone.Utils;

  // Empirical priority ranges collected from logs. These values are subject to change.
  var WebRtcRelayIcePriority = Object.freeze({
    TLS: { name: 'tls', start: 0, end: 11000000 },
    TCP: { name: 'tcp', start: 12000000, end: 31000000 },
    UDP: { name: 'udp', start: 35000000, end: 60000000 }
  });

  // List of video codecs that can be set as preferred
  var VideoCodecs = Object.freeze({
    VP8: 'VP8',
    VP9: 'VP9',
    H264: 'H264'
  });

  /*
   * IceCandidate Object
   */
  function IceCandidate(candidate) {
    /*******************
        // candidate-attribute   = "candidate" ":" foundation ' ' component-id ' '
        //                         transport ' '
        //                         priority ' '
        //                         connection-address ' '     ;from RFC 4566
        //                         port         ;port from RFC 4566
        //                         ' ' cand-type
        //                         [' ' rel-addr]
        //                         [' ' rel-port]
        //                         *(' ' extension-att-name ' '
        //                         extension-att-value)
        //
        // foundation            = 1*32ice-char
        // component-id          = 1*5DIGIT
        // transport             = "UDP" / transport-extension
        // transport-extension   = token              ; from RFC 3261
        // priority              = 1*10DIGIT
        // cand-type             = "typ" ' ' candidate-types
        // candidate-types       = "host" / "srflx" / "prflx" / "relay" / token
        // rel-addr              = "raddr" ' ' connection-address
        // rel-port              = "rport" ' ' port
        // extension-att-name    = byte-string    ;from RFC 4566
        // extension-att-value   = byte-string
        // ice-char              = ALPHA / DIGIT / "+" / "/"
         *******************/

    if (candidate) {
      candidate = candidate.replace(/(a=)*candidate:([^\r]+)(\r\n)*/i, '$2');
      var tmp = candidate.split(' ');
      if (tmp.length < 7) {
        throw new Error('Invalid candidate: ' + candidate);
      }
      this.foundation = parseInt(tmp[0], 10);
      this.componentId = parseInt(tmp[1], 10);
      this.transport = tmp[2].toLowerCase();
      this.priority = parseInt(tmp[3], 10);
      this.address = tmp[4];
      this.port = parseInt(tmp[5], 10);
      this.typ = tmp[7];

      this.optional = [];
      for (var idx = 8; idx < tmp.length; idx += 2) {
        this.optional.push(tmp[idx]);
        this[tmp[idx]] = tmp[idx + 1];
      }
    } else {
      this.foundation = 1;
      this.componentId = 1; // 1 = RTP, 2 = RTCP
      this.transport = 'udp';
      this.priority = 1;
      this.address = '0.0.0.0';
      this.port = 1;
      this.typ = 'host';
      this.generation = 0;

      this.optional = [];
    }
  }

  IceCandidate.prototype.equals = function (other) {
    other = !other || typeof other === 'string' ? new IceCandidate(other) : other;
    return (
      this.foundation === other.foundation &&
      this.componentId === other.componentId &&
      this.transport === other.transport &&
      this.priority === other.priority &&
      this.address === other.address &&
      this.port === other.port &&
      this.typ === other.typ
    );
  };

  IceCandidate.prototype.isRtp = function () {
    return this.componentId === 1;
  };

  IceCandidate.prototype.isRtcp = function () {
    return this.componentId === 2;
  };

  IceCandidate.prototype.isTcpTlsRelay = function () {
    // Check if this is a relay candidate with a TCP or TLS connection to the TURN server.
    // For now, the only way of doing that is by checking in which range the priority value is.
    return this.typ === 'relay' && this.priority <= WebRtcRelayIcePriority.TCP.end;
  };

  IceCandidate.prototype.getRelayClientTransportType = function () {
    var type = '';
    if (this.typ === 'relay') {
      var p = this.priority;
      [WebRtcRelayIcePriority.TLS, WebRtcRelayIcePriority.TCP, WebRtcRelayIcePriority.UDP].some(function (t) {
        if (p >= t.start && p <= t.end) {
          type = t.name;
          return true;
        }
        return false;
      });
    }
    return type;
  };

  IceCandidate.prototype.getValue = function () {
    var str =
      this.foundation + ' ' + this.componentId + ' ' + this.transport + ' ' + this.priority + ' ' + this.address + ' ' + this.port + ' ' + 'typ ' + this.typ;

    for (var idx = 0; idx < this.optional.length; idx++) {
      var param = this.optional[idx];
      str += ' ' + param + ' ' + this[param];
    }
    return str;
  };

  IceCandidate.prototype.getParsedAttribute = function () {
    return {
      field: 'candidate',
      value: this.getValue()
    };
  };

  IceCandidate.prototype.toString = function () {
    return 'candidate:' + this.getValue();
  };

  var sdpParser = (function () {
    // Imports
    var logger = UnifyPhone.logger;

    function parseO(o) {
      var t = o.split(/\s+/);
      return { username: t[0], id: t[1], version: t[2], nettype: t[3], addrtype: t[4], address: t[5] };
    }

    function parseC(c) {
      var t = c.split(/\s+/);
      return { nettype: t[0], addrtype: t[1], address: t[2] };
    }

    function parseA(a) {
      var tmp = /^([^:]+)(?::(.*))?/.exec(a);
      return { field: tmp[1], value: tmp[2] };
    }

    function parseFmt(fmt) {
      var num = +fmt;
      return isNaN(num) ? fmt : num;
    }

    // Example m-lines:
    // m=audio 9 UDP/TLS/RTP/SAVPF 111 103 104 9 0 8 106 105 13 110 112 113 126
    // m=application 49444 UDP/DTLS/SCTP webrtc-datachannel (data-channel new)
    // m=application 49694 DTLS/SCTP 5000 (data-channel obsolete)
    //
    // RFC4566
    // m=<media> <port> <proto> <fmt> ...
    // m=<media> <port>/<number of ports> <proto> <fmt> ...
    //
    // ABNF definition
    // %x6d "=" media SP port ["/" integer] SP proto 1*(SP fmt) CRLF
    //
    // media =  token
    //          ;typically "audio", "video", "text", or "application"
    //
    // fmt =    token
    //          ;typically an RTP payload type for audio and video media
    //
    // proto  = token *("/" token)
    //          ;typically "RTP/AVP" or "udp"
    //
    // port =   1*DIGIT
    //
    var reM = /^(\w+) +(\d+)(?:\/(\d))? +(\S+)(?: +(\S+(?: +\S+)*))?/;
    function parseM(m) {
      var tmp = reM.exec(m);
      return {
        media: tmp[1],
        port: +tmp[2],
        portnum: +(tmp[3] || 1),
        proto: tmp[4],
        fmt: tmp[5] ? tmp[5].split(/\s+/).map(parseFmt) : []
      };
    }

    function push(o, i, v) {
      switch (i) {
        case 'v':
        case 'o':
        case 's':
        case 'i':
        case 'u':
        case 'e':
        case 'p':
        case 'c':
          o[i] = v;
          break;
        default:
          if (o[i]) {
            o[i].push(v);
          } else {
            o[i] = [v];
          }
          break;
      }
    }

    var stringifiers = {
      o: function (o) {
        return [o.username || '-', o.id, o.version, o.nettype || 'IN', o.addrtype || 'IP4', o.address].join(' ');
      },
      c: function (c) {
        return [c.nettype || 'IN', c.addrtype || 'IP4', c.address].join(' ');
      },
      m: function (m) {
        var mArr = [m.media || 'audio', m.port || '0', m.proto || 'RTP/AVP'];
        if (m.fmt && m.fmt.length) {
          mArr.push(m.fmt.join(' '));
        }
        return mArr.join(' ');
      },
      a: function (a) {
        return a.value ? [a.field, a.value].join(':') : a.field;
      }
    };

    function stringifyParam(sdp, type, def) {
      if (sdp[type] !== undefined) {
        var stringifier = function (x) {
          return type + '=' + ((stringifiers[type] && stringifiers[type](x)) || x) + '\r\n';
        };

        if (Array.isArray(sdp[type])) {
          return sdp[type].map(stringifier).join('');
        }
        return stringifier(sdp[type]);
      }

      if (def) {
        return type + '=' + def + '\r\n';
      }
      return '';
    }

    function filterSdpAttr(sdpAttr) {
      return sdpAttr.filter(function (a) {
        switch (a.field) {
          case 'inactive':
          case 'recvonly':
          case 'sendonly':
          case 'sendrecv':
            return false;
        }
        return true;
      });
    }

    function parseSdp(sdp) {
      var tmpSdp = sdp.split(/\r\n/);
      var i, tmp;

      var result = {};

      // First parse the session level attributes
      for (i = 0; i < tmpSdp.length; ++i) {
        tmp = /^(\w)=(.*)/.exec(tmpSdp[i]);

        if (!tmp || tmp[1] === 'm') {
          break;
        } else if (tmp[1] === 'a' && tmp[2]) {
          push(result, tmp[1], parseA(tmp[2]));
        } else {
          push(result, tmp[1], tmp[2]);
        }
      }

      result.m = [];

      var mLine = null;
      // Now let's parse the media lines and their attributes
      for (; i < tmpSdp.length; ++i) {
        tmp = /(\w)=(.*)/.exec(tmpSdp[i]);

        if (!tmp) {
          break;
        }

        if (tmp[1] === 'm') {
          mLine = parseM(tmp[2]);
          result.m.push(mLine);
        } else if (tmp[1] === 'a' && tmp[2]) {
          push(mLine, tmp[1], parseA(tmp[2]));
        } else {
          push(mLine, tmp[1], tmp[2]);
        }
      }

      if (!result.s || result.s.trim() === '') {
        result.s = '-';
      }
      if (result.o) {
        result.o = parseO(result.o);
      }
      if (result.c) {
        result.c = parseC(result.c);
      }
      result.m.forEach(function (m) {
        if (m.c) {
          m.c = parseC(m.c);
        }
      });

      return result;
    }

    function getVideoConnectionModes(sdp) {
      var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
      var connModes = [];
      if (parsedSdp.m) {
        parsedSdp.m.forEach(function (m) {
          if (m.media !== 'video') {
            return;
          }
          m.a.some(function (a) {
            switch (a.field) {
              case 'sendrecv':
              case 'sendonly':
              case 'recvonly':
              case 'inactive':
                connModes.push(a.field);
                return true;
            }
            return false;
          });
        });
      }
      return connModes;
    }

    function getConnectionMode(sdp, option) {
      var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;

      option = option || {};
      // Default is sendrecv
      var connMode = 'sendrecv';

      if (option.mLineIndex !== undefined) {
        parsedSdp.m[option.mLineIndex].a.forEach(function (a) {
          switch (a.field) {
            case 'sendrecv':
            case 'sendonly':
            case 'recvonly':
            case 'inactive':
              connMode = a.field;
              break;
          }
        });
        return connMode;
      }

      if (!option.mediaType || option.mediaType === 'both') {
        option.mediaType = 'audio';
      }

      if (parsedSdp.a) {
        parsedSdp.a.forEach(function (a) {
          switch (a.field) {
            case 'sendonly':
            case 'recvonly':
            case 'inactive':
              connMode = a.field;
              break;
          }
        });
      }

      if (parsedSdp.m) {
        parsedSdp.m.forEach(function (m) {
          if (m.media === option.mediaType) {
            m.a.forEach(function (a) {
              switch (a.field) {
                case 'sendrecv':
                case 'sendonly':
                case 'recvonly':
                case 'inactive':
                  connMode = a.field;
                  break;
              }
            });
          }
        });
      }

      return connMode;
    }

    function isBandWidthASSet(b) {
      if (!Array.isArray(b)) {
        return false;
      }
      return b.some(function (v) {
        return v.includes('AS:');
      });
    }

    function isBandWidthTIASSet(b) {
      if (!Array.isArray(b)) {
        return false;
      }
      return b.some(function (v) {
        return v.includes('TIAS:');
      });
    }

    function validateMLine(m, index, removeUdpVideoIce, trickleIce) {
      if (m.port === 0) {
        // m-line is disabled. No validation is needed
        return true;
      }

      var numCandidates = 0;
      var removedValidCandidates = false;
      var relayRtpCandidate = null;
      var relayRtcpCandidate = null;

      // Remove ICE candidates as needed
      var attr = m.a.filter(function (a) {
        if (a.field !== 'candidate') {
          return true;
        }
        var candidate = new IceCandidate(a.value);
        // Remove any tcp candidates. It only returns UDP Ice Candidates
        if (candidate.transport !== 'udp') {
          return false;
        }

        if (removeUdpVideoIce && m.media === 'video') {
          if (candidate.typ === 'relay') {
            // For relay candidates, select only the ones that use TCP/TLS to the TURN server.
            if (candidate.isTcpTlsRelay()) {
              if (!relayRtpCandidate && candidate.isRtp()) {
                relayRtpCandidate = candidate;
              }
              if (!relayRtcpCandidate && candidate.isRtcp()) {
                relayRtcpCandidate = candidate;
              }
            } else {
              logger.debug('[SdpParser]: Removed UDP relay candidate: ', a.value);
              removedValidCandidates = true;
              return false;
            }
          } else {
            // We need to completely remove host and srflx candidates
            removedValidCandidates = true;
            return false;
          }
        }
        numCandidates++;
        return true;
      });

      if (!relayRtpCandidate && removedValidCandidates && !trickleIce) {
        // The SDP doesn't have TLS/TCP TURN candidates for RTP but has other valid candidates.
        // Try validating again with the removeUdpVideoIce flag turned off.
        logger.info('[SdpParser]: Media description does not have TCP/TLS relay candidates. Do not filter UDP candidates!');
        return validateMLine(m, index, false);
      }

      if (numCandidates === 0 && !trickleIce) {
        // The m-line does not have any valid candidates
        return false;
      }

      m.a = attr;
      if (relayRtcpCandidate) {
        // Update rtcp attribute with info from first relay candidate
        m.a.some(function (a) {
          if (a.field === 'rtcp') {
            // a=rtcp:1234 IN IP4 1.1.1.1
            a.value = relayRtcpCandidate.port + ' IN IP4 ' + relayRtcpCandidate.address;
            return true;
          }
          return false;
        });
      }
      if (relayRtpCandidate) {
        // Update media description port and connection address with info from first relay candidate
        m.port = relayRtpCandidate.port;
        if (m.c) {
          m.c.address = relayRtpCandidate.address;
        }
      }
      return true;
    }

    function findIdInMediaAttrs(parsedSdp, id) {
      return parsedSdp.m.findIndex(function (m) {
        return m.a.some(function (a) {
          return a.field === 'mid' && a.value === id;
        });
      });
    }

    return {
      parse: parseSdp,

      stringify: function (sdp) {
        var s = '';

        s += stringifyParam(sdp, 'v', 0);
        s += stringifyParam(sdp, 'o');
        s += stringifyParam(sdp, 's', '-');
        s += stringifyParam(sdp, 'i');
        s += stringifyParam(sdp, 'u');
        s += stringifyParam(sdp, 'e');
        s += stringifyParam(sdp, 'p');
        s += stringifyParam(sdp, 'c');
        s += stringifyParam(sdp, 'b');
        s += stringifyParam(sdp, 't', '0 0');
        s += stringifyParam(sdp, 'r');
        s += stringifyParam(sdp, 'z');
        s += stringifyParam(sdp, 'k');
        s += stringifyParam(sdp, 'a');
        sdp.m.forEach(function (m) {
          s += stringifyParam({ m: m }, 'm');
          s += stringifyParam(m, 'i');
          s += stringifyParam(m, 'c');
          s += stringifyParam(m, 'b');
          s += stringifyParam(m, 'k');
          s += stringifyParam(m, 'a');
        });

        return s;
      },

      // mediaType: 'audio', 'video' or 'both'
      getConnectionMode: getConnectionMode,
      getVideoConnectionModes: getVideoConnectionModes,

      // mediaType: 'audio', 'video' or 'both'
      // connectionMode: 'inactive', 'recvonly','sendonly' or 'sendrecv'
      setConnectionMode: function (sdp, mediaType, connectionMode) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        var media = !mediaType || mediaType === 'both' ? ['audio', 'video'] : [mediaType];

        if (mediaType === 'both' && parsedSdp.a) {
          parsedSdp.a = filterSdpAttr(parsedSdp.a);
          parsedSdp.a.push({ field: connectionMode });
        }

        if (parsedSdp.m) {
          parsedSdp.m.forEach(function (m) {
            if (media.includes(m.media)) {
              m.a = filterSdpAttr(m.a);
              m.a.push({ field: connectionMode });
            }
          });
        }
        return parsedSdp;
      },

      isHold: function (sdp) {
        var connMode = getConnectionMode(sdp);
        return connMode === 'inactive' || connMode === 'sendonly';
      },

      disableMultiplex: function (sdp) {
        // Disable the multiplex of audio and video RTP packets by removing the 'a=group:BUNDLE audio video' line.
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        if (parsedSdp.a) {
          parsedSdp.a = parsedSdp.a.filter(function (a) {
            return a.field !== 'group';
          });
        }
        return parsedSdp;
      },

      isMultiplexEnabled: function (sdp) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        if (parsedSdp.a) {
          return parsedSdp.a.some(function (a) {
            return a.field === 'group' && a.value && a.value.startsWith('BUNDLE');
          });
        }
        return false;
      },

      hasVideo: function (sdp) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;

        if (parsedSdp.m) {
          return parsedSdp.m.some(function (m, index) {
            if (m.media !== 'video' || m.port === 0 || m.fmt.length === 0) {
              return false;
            }

            if (getConnectionMode(parsedSdp, { mLineIndex: index }) === 'inactive') {
              return false;
            }

            // This is a valid video m-line. Make sure that it contains VP8 or VP9.
            return m.a.some(function (a) {
              return a.field === 'rtpmap' && /VP[89]/.test(a.value);
            });
          });
        }
        return false;
      },

      addEmptyVideoLine: function (sdp) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        var newMediaLine = {
          media: 'video',
          port: 0,
          proto: 'RTP/SAVPF',
          fmt: [100],
          c: {
            nettype: 'IN',
            addrtype: 'IP4',
            address: '0.0.0.0'
          },
          a: [{ field: 'inactive' }]
        };

        parsedSdp.m.push(newMediaLine);
        return parsedSdp;
      },

      removeVideo: function (sdp) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        if (parsedSdp.m) {
          parsedSdp.m.forEach(function (m) {
            if (m.media === 'video') {
              m.port = 0;
              m.c = {
                nettype: 'IN',
                addrtype: 'IP4',
                address: '0.0.0.0'
              };
              // Remove all attributes, except for a=mid
              m.a = m.a.filter(function (a) {
                return a.field === 'mid';
              });
              m.a.push({ field: 'inactive' });
            }
          });
        }
        return parsedSdp;
      },

      setOpusParameters: function (sdp, parameters) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        if (!parsedSdp.m || !parameters) {
          return parsedSdp;
        }
        // Create a shallow copy of parameters
        parameters = Utils.shallowCopy(parameters);

        parsedSdp.m.forEach(function (m) {
          if (m.media !== 'audio' || !m.a) {
            return;
          }
          var pt;
          m.a.some(function (a) {
            if (a.field === 'rtpmap' && a.value.includes('opus')) {
              pt = a.value.split(' ')[0];
              return true;
            }
            return false;
          });
          pt &&
            m.a.some(function (a) {
              if (a.field === 'fmtp' && a.value.includes(pt)) {
                var currentParams = a.value.substring(pt.length + 1).split(';');
                currentParams.forEach(function (param) {
                  var s = param.split('=');
                  var name = s[0].trim();
                  var value = s[1].trim();
                  if (!parameters.hasOwnProperty(name)) {
                    parameters[name] = value;
                  }
                });
                var attr = Object.keys(parameters).map(function (name) {
                  return name + '=' + parameters[name];
                });
                a.value = pt + ' ' + attr.join(';');
                return true;
              }
              return false;
            });
        });
        return parsedSdp;
      },

      setVideoBandwidth: function (sdp, bw, overwrite, mIndex) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        if (parsedSdp.m) {
          parsedSdp.m.forEach(function (m, index) {
            if (m.media === 'video') {
              if (mIndex >= 0 && mIndex !== index) {
                return;
              }
              if (overwrite) {
                if (bw && bw.valueOf() > 0) {
                  m.b = ['AS:' + bw, 'TIAS:' + bw * 1000];
                } else if (isBandWidthASSet(m.b) || isBandWidthTIASSet(m.b)) {
                  delete m.b;
                }
              } else {
                if (!isBandWidthASSet(m.b)) {
                  push(m, 'b', 'AS:' + bw);
                }
                if (!isBandWidthTIASSet(m.b)) {
                  push(m, 'b', 'TIAS:' + bw * 1000);
                }
              }
            }
          });
        }
        return parsedSdp;
      },

      setXGoogle: function (sdp, x, mIndex) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        if (parsedSdp.m && x) {
          parsedSdp.m.forEach(function (m, index) {
            if (m.media !== 'video' || !m.a || (mIndex >= 0 && mIndex !== index)) {
              return;
            }
            var pts = [];
            // Remove the existing parameters, regardless if we're setting it locally or not
            m.a = m.a.filter(function (a, i) {
              if (a.field === 'rtpmap' && /VP[89]/.test(a.value)) {
                // Found the VP8 or VP9 codec. Get the payload type
                pts.push({ idx: i + pts.length, pt: a.value.split(' ')[0] });
              }
              return (a.field !== 'fmtp' || (a.value && !a.value.includes('x-google'))) && a.field !== 'x-google-buffer-latency';
            });

            pts.forEach(function (element) {
              var xAttr = [];
              if (x.minBitRate && x.minBitRate.valueOf() > 0) {
                xAttr.push('x-google-min-bitrate=' + x.minBitRate);
              }
              if (x.maxBitRate && x.maxBitRate.valueOf() > 0) {
                xAttr.push('x-google-max-bitrate=' + x.maxBitRate);
              }
              if (x.startBitRate && x.startBitRate.valueOf() > 0) {
                xAttr.push('x-google-start-bitrate=' + x.startBitRate);
              }
              if (x.maxQuantization && x.maxQuantization.valueOf() > 0) {
                xAttr.push('x-google-max-quantization=' + x.maxQuantization);
              }
              if (xAttr.length > 0) {
                xAttr = element.pt + ' ' + xAttr.join('; ');
                m.a.splice(element.idx + 1, 0, { field: 'fmtp', value: xAttr });
              }
            });
          });
        }
        return parsedSdp;
      },

      setPreferredVideoCodec: function (sdp, codec) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        if (parsedSdp.m && codec) {
          parsedSdp.m.forEach(function (m, index) {
            if (m.media === 'video') {
              var codecFound = m.a.find(function (a) {
                return a.field === 'rtpmap' && a.value.includes(codec);
              });
              if (codecFound) {
                var pt = parseInt(codecFound.value.split(' ')[0], 10);
                var idx = m.fmt.indexOf(pt);
                if (idx > 0) {
                  m.fmt.splice(idx, 1);
                  m.fmt.unshift(pt);
                  logger.debug('[SdpParser]: Prefer ' + codec + ' format for video m-line #:', index);
                }
              }
            }
          });
        }
        return parsedSdp;
      },

      removeVideoBandwidth: function (sdp) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        if (parsedSdp.m) {
          parsedSdp.m.forEach(function (m) {
            if (m.media === 'video') {
              m.b =
                m.b &&
                m.b.filter(function (v) {
                  return !v.startsWith('AS:') && !v.startsWith('TIAS:');
                });
            }
          });
        }
        return parsedSdp;
      },

      /**
       * This method removes the ssrc-audio-level attribute (security issue).
       * At least until Chrome removes it.
       * See https://code.google.com/p/webrtc/issues/detail?id=3411
       */
      removeAudioLevelAttr: function (sdp) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        if (parsedSdp.m) {
          parsedSdp.m.forEach(function (m) {
            if (m.media === 'audio') {
              m.a =
                m.a &&
                m.a.filter(function (a) {
                  if (a.field === 'extmap' && a.value && a.value.includes('ssrc-audio-level')) {
                    return false;
                  }
                  return true;
                });
            }
          });
        }
        return parsedSdp;
      },

      /**
       * Checks for Trickle ICE indication in provided SDP.
       * @param {type} sdp The raw SDP string or the parsed SDP.
       * @returns {Boolean} true if "ice-options" attribute (at the session level or for every m-line) contains "trickle" value, otherwise false.
       */
      isTrickleIceOption: function (sdp) {
        var findTrickle = function (a) {
          return (a.field === 'ice-options' && a.value && a.value.includes('trickle')) || a.field === 'inactive';
        };
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        parsedSdp.a = parsedSdp.a || [];
        return (
          parsedSdp.a.some(findTrickle) ||
          (parsedSdp.m &&
            parsedSdp.m.length > 0 &&
            parsedSdp.m.every(function (m) {
              return m.a && m.a.some(findTrickle);
            }))
        );
      },

      /**
       * Adds "trickle" to ice-options if not generated by peer connection and Trickle ICE
       * is enabled. Also remove "trickle" from ice-options if generated by peer connection
       * and Trickle ICE is disabled.
       * @param {string|Object} sdp The raw SDP string of the parsed SDP.
       * @param {boolean} enabled true if Trickle ICE is enabled, otherwise false
       * @returns {Object} The modified SDP object with correct trickle ICE option.
       */
      fixTrickleIceOption: function (sdp, enabled) {
        var removeTrickle = function (a, idx, array) {
          if (a.field === 'ice-options') {
            a.value = a.value || '';
            a.value = a.value.replace(/trickle\s*/gi, '').trim();
            if (!a.value) {
              logger.debug('[SdpParser]: There are no other ice-options - remove the attribute.');
              array.splice(idx, 1);
            }
            return true;
          }
          return false;
        };

        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        parsedSdp.a = parsedSdp.a || [];
        var trickleIceIndication = this.isTrickleIceOption(parsedSdp);
        if (enabled && !trickleIceIndication) {
          logger.debug('[SdpParser]: Adding Trickle ICE indication');
          var found = parsedSdp.a.some(function (a) {
            if (a.field === 'ice-options') {
              a.value = (a.value || '') + ' trickle';
              return true;
            }
            return false;
          });
          if (!found) {
            parsedSdp.a.push({ field: 'ice-options', value: 'trickle' });
          }
        } else if (!enabled && trickleIceIndication) {
          logger.debug('[SdpParser]: Removing Trickle ICE indication');
          parsedSdp.a.some(removeTrickle);
          parsedSdp.m.forEach(function (m) {
            m.a && m.a.some(removeTrickle);
          });
        }
        return parsedSdp;
      },

      /**
       * Adds end-of-candidates attribute to all RTP streams (m-lines).
       * @param {string|Object} sdp The raw SDP string of the parsed SDP .
       * @returns {Object} The modified SDP object with the end-of-candidates attributes.
       */
      addEndOfCandidatesLine: function (sdp) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        if (parsedSdp.m) {
          parsedSdp.m.forEach(function (m) {
            m.a = m.a || [];
            var eoc = m.a.some(function (a) {
              return a.field === 'end-of-candidates';
            });
            if (!eoc) {
              m.a.push({ field: 'end-of-candidates' });
            }
          });
        }
        return parsedSdp;
      },

      /**
       * This method removes the end-of-candidates attribute
       * @param {string|Object} sdp The raw SDP string of the parsed SDP
       * @returns {Object} The modified SDP object without the end-of-candidate attributes.
       */
      removeEndOfCandidatesLine: function (sdp) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        if (parsedSdp.m) {
          parsedSdp.m.forEach(function (m) {
            m.a &&
              m.a.some(function (a, idx) {
                if (a.field === 'end-of-candidates') {
                  m.a.splice(idx, 1);
                  return true;
                }
                return false;
              });
          });
        }
        return parsedSdp;
      },

      /**
       * This method validate if all m lines contains at least one UDP IceCandidate
       * Any TCP IceCandidates is removed from the result
       * if SDP is not valid the method returns null otherwise
       * the method return the changed SDP
       */
      validateIceCandidates: function (sdp, removeUdpVideoIce, trickleIce) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        return !parsedSdp.m.some(function (m, index) {
          if (!validateMLine(m, index, removeUdpVideoIce, trickleIce)) {
            return true;
          }
          return false;
        });
      },

      isNoOfferSdp: function (rtcSdp) {
        return !!rtcSdp && rtcSdp.sdp === '' && rtcSdp.type === 'nooffer';
      },

      iceTotalCandidatesCount: function (sdp) {
        var count = 0;
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        parsedSdp.m.forEach(function (m) {
          m.a.forEach(function (a) {
            if (a.field === 'candidate') {
              count++;
            }
          });
        });
        return count;
      },

      getIceCandidates: function (sdp) {
        var candidates = [];
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        parsedSdp.m.forEach(function (m) {
          m.a.forEach(function (a) {
            if (a.field === 'candidate') {
              candidates.push(new IceCandidate(a.value));
            }
          });
        });
        return candidates;
      },
      /**
       * This method removes given codecs from SDP, e.g. CN. It will also
       * remove corresponding rtx entry (if existing) for the removed payload
       * type of the codec.             *
       */
      removeAudioCodecs: function (sdp, codecs) {
        // attribute fields to remove for the codecs
        var payloadFields = ['rtpmap', 'fmtp', 'rtcp-fb'];

        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        if (parsedSdp.m && codecs && codecs.length) {
          parsedSdp.m.forEach(function (m, index) {
            if (m.media === 'audio') {
              var filterPayloadFields = function (attr, pt) {
                return !(payloadFields.includes(attr.field) && attr.value && attr.value.startsWith(pt));
              };
              // Chrome adds retransmission payload types for all audio types, so it is required
              // to also remove them together with the codec itself.
              var findRtxPayloadTypeForCodec = function (codecPt) {
                var rtxFound = m.a.find(function (a) {
                  return a.field === 'fmtp' && a.value.includes('apt=' + codecPt);
                });
                return rtxFound && parseInt(rtxFound.value.split(' ')[0], 10);
              };
              var removePayloadType = function (pt, codec) {
                m.a = m.a.filter(function (a) {
                  return filterPayloadFields(a, pt);
                });
                var idx = m.fmt.indexOf(pt);
                if (idx !== -1) {
                  m.fmt.splice(idx, 1);
                  logger.debug('[SdpParser]: Removed ' + codec + ' with pt ' + pt + ' from format for audio m-line #:', index);
                }
              };

              codecs.forEach(function (codec) {
                var codecFound;
                do {
                  codecFound = m.a.find(function (a) {
                    return a.field === 'rtpmap' && a.value.includes(codec);
                  });
                  if (codecFound) {
                    var pt = parseInt(codecFound.value.split(' ')[0], 10);
                    removePayloadType(pt, codec);
                    var rtxPt = findRtxPayloadTypeForCodec(pt);
                    rtxPt && removePayloadType(rtxPt, 'rtx');
                  }
                } while (codecFound);
              });
            }
          });
        }
        return parsedSdp;
      },
      /**
       * This method removes given codecs from SDP, e.g. H264. It will also
       * remove corresponding rtx entry (if existing) for the removed payload
       * type of the codec.             *
       */
      removeVideoCodecs: function (sdp, codecs) {
        // attribute fields to remove for the codecs
        var payloadFields = ['rtpmap', 'fmtp', 'rtcp-fb'];

        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        if (parsedSdp.m && codecs && codecs.length) {
          parsedSdp.m.forEach(function (m, index) {
            if (m.media === 'video') {
              var filterPayloadFields = function (attr, pt) {
                return !(payloadFields.includes(attr.field) && attr.value && attr.value.startsWith(pt));
              };
              // Chrome adds retransmission payload types for all video types, so it is required
              // to also remove them together with the codec itself.
              var findRtxPayloadTypeForCodec = function (codecPt) {
                var rtxFound = m.a.find(function (a) {
                  return a.field === 'fmtp' && a.value.includes('apt=' + codecPt);
                });
                return rtxFound && parseInt(rtxFound.value.split(' ')[0], 10);
              };
              var removePayloadType = function (pt, codec) {
                m.a = m.a.filter(function (a) {
                  return filterPayloadFields(a, pt);
                });
                var idx = m.fmt.indexOf(pt);
                if (idx !== -1) {
                  m.fmt.splice(idx, 1);
                  logger.debug('[SdpParser]: Removed ' + codec + ' with pt ' + pt + ' from format for video m-line #:', index);
                }
              };

              codecs.forEach(function (codec) {
                var codecFound;
                do {
                  codecFound = m.a.find(function (a) {
                    return a.field === 'rtpmap' && a.value.includes(codec);
                  });
                  if (codecFound) {
                    var pt = parseInt(codecFound.value.split(' ')[0], 10);
                    removePayloadType(pt, codec);
                    var rtxPt = findRtxPayloadTypeForCodec(pt);
                    rtxPt && removePayloadType(rtxPt, 'rtx');
                  }
                } while (codecFound);
              });
            }
          });
        }
        return parsedSdp;
      },

      verifyFingerprintInMLines: function (sdp) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        parsedSdp.m &&
          parsedSdp.m.forEach(function (mline, index) {
            if (
              !mline.a.some(function (a) {
                return a.field === 'fingerprint';
              })
            ) {
              // Firefox (as of v39): no fingerprint is inside the m-line, so get it from the session
              if (
                !parsedSdp.a.some(function (a) {
                  if (a.field === 'fingerprint') {
                    mline.a.push(a);
                    return true;
                  }
                  return false;
                })
              ) {
                logger.debug('[SdpParser]: verifyFingerprintInMLines(): No session fingerprint found for video m-line #:', index);
              }
            }
          });
        return parsedSdp;
      },

      findMediaLineIndex: function (sdp, id) {
        if (!id) {
          return -1;
        }
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        return findIdInMediaAttrs(parsedSdp, id);
      },

      setMediaIdAttribute: function (sdp, mLineIndex, value, overwrite) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        var m = parsedSdp.m[mLineIndex];
        if (m) {
          if (
            !m.a.some(function (a) {
              if (a.field === 'mid') {
                if (overwrite) {
                  a.value = value;
                }
                return true;
              }
              return false;
            })
          ) {
            m.a.push({ field: 'mid', value: value });
          }
        }
      },

      getMediaIds: function (sdp) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        var mids = {};
        parsedSdp.m.forEach(function (m) {
          var mid, msid;
          if (
            m.a &&
            m.a.some(function (a) {
              if (a.field === 'mid') {
                mid = a.value;
              } else if (a.field === 'msid') {
                msid = a.value;
              }
              return mid && msid;
            })
          ) {
            mids[msid] = mid;
          }
        });
        return mids;
      },

      getOrigin: function (sdp) {
        if (typeof sdp === 'string') {
          var origin = /(o=[^\r]+)\r\n/i.exec(sdp);
          return (origin && origin[1]) || '';
        }
        return stringifyParam(sdp, 'o');
      },

      getSsrcList: function (sdp) {
        var parsedSdp = typeof sdp === 'string' ? parseSdp(sdp) : sdp;
        var list = [];
        parsedSdp.a.forEach(function (a) {
          if (a.field === 'ssrc') {
            list.push(a.value.split(' ')[0]);
          }
        });
        return list;
      }
    };
  })();

  // Exports
  unifyPhone.IceCandidate = IceCandidate;
  unifyPhone.sdpParser = sdpParser;
  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.VideoCodecs = VideoCodecs;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
///////////////////////////////////////////////////////////////////////////////

//--- WebSocket Connection Controller ---
///////////////////////////////////////////////////////////////////////////////

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var Utils = unifyPhone.Utils;

  // Connection states used by ConnectionHandler
  var ConnectionState = Object.freeze({
    Disconnected: 'Disconnected',
    Connecting: 'Connecting',
    Reconnecting: 'Reconnecting',
    Connected: 'Connected',
    Ready: 'Ready' // Used by SDK only
  });

  // eslint-disable-next-line max-lines-per-function
  function ConnectionController(config) {
    // NOSONAR
    var logger = unifyPhone.logger;

    ///////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////
    var RETRY_DELAY_NORMAL, RETRY_DELAY_PRIORITY, RETRY_INTERVAL, MAX_SHORT_ATTEMPTS, MAX_INTERVAL, MAX_ATTEMPTS;

    if (Utils.isMobile()) {
      RETRY_DELAY_PRIORITY = 0;
      RETRY_DELAY_NORMAL = 0;
      RETRY_INTERVAL = 0;
      MAX_INTERVAL = 0;
      MAX_ATTEMPTS = 0;
      MAX_SHORT_ATTEMPTS = 0;
    } else {
      RETRY_DELAY_PRIORITY = 0; // Priority reconnection occurs between RETRY_DELAY_PRIORITY and RETRY_DELAY_NORMAL
      RETRY_DELAY_NORMAL = 7000; // Normal reconnection occurs between RETRY_DELAY_NORMAL and RETRY_DELAY_NORMAL + RETRY_INTERVAL
      RETRY_INTERVAL = 8000; // Start with 8 seconds then double after MAX_SHORT_ATTEMPTS until it reaches MAX_INTERVAL
      MAX_INTERVAL = 64000; // Max retry interval (64 seconds)
      MAX_ATTEMPTS = 1350; // Approximately ~1 day, then it gives up. -1 means forever.
      MAX_SHORT_ATTEMPTS = 1; // Limit of short interval attempts. After this, we start doubling the intervals
    }

    var OFFLINE_DELAY = 4000; // Small Delay to handle Network Glitch

    // Set throttling variables to allow 40 messages each 4 seconds.
    // This should be enough to handle the initial burst after reconnection
    // and allow mobile clients to answer a push notifications call.
    var THROTTLE_MAX_MESSAGES = 40; // 40 messages each 4 seconds
    var THROTTLE_INTERVAL = 4000;
    var MIN_THROTTLE_DELAY = 200;
    var MAX_QUEUED_MESSAGES = 200;
    var MAX_DEQUEUED_MESSAGES = 5; // Dequeue up to 5 messages at a time

    ///////////////////////////////////////////////////////////////////////////
    // Local variables
    ///////////////////////////////////////////////////////////////////////////
    var _that = this;
    var _socket = null;
    var _errorCb = null;
    var _successCb = null;
    var _state = ConnectionState.Disconnected;
    var _retryInterval = 0;
    var _retryAttempts = 0;
    var _retryTimeout = null;
    var _reconnectTimeout = null;
    var _offlineTimeout = null;
    var _config = config || {};

    // The default WS target.
    var _defaultTarget = null;

    // Reverse proxy target. Used in scenarios where the WS should be opened via a reverse proxy target.
    // For instance, used by SAG clients in China.
    var _proxyTarget = null;

    // The current target that we are trying to connect to. If a reverse proxy is configured we first
    // try to connect to the reverse proxy and if that fails we fallback to the default target.
    var _currTarget = null;

    var _targetObfuscated = null;

    var _sentTimestamps = [];
    var _queuedMessages = [];
    var _throttleTimeout = null;

    var _hasActiveCall = null; // Injected function to verify if client has an active call

    /////////////////////////////////////////////////////////////////////////////
    // Event Senders
    /////////////////////////////////////////////////////////////////////////////
    function raiseEvent(eventHandler, event) {
      if (typeof eventHandler === 'function') {
        try {
          eventHandler(event);
        } catch (e) {
          logger.error(e);
        }
      }
    }

    function raiseStateChange(newState, oldState) {
      raiseEvent(_that.onStateChange, {
        newState: newState,
        oldState: oldState
      });
    }

    function raiseReconnectFailed() {
      raiseEvent(_that.onReconnectFailed);
    }

    function raiseMessage(msg) {
      raiseEvent(_that.onMessage, msg);
    }

    ///////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////
    function setTarget(url, proxyUrl) {
      _defaultTarget = url;
      _proxyTarget = proxyUrl;
    }

    function getObfuscatedUrl(url) {
      return url ? url.split('?')[0] : 'N/A';
    }

    function handleOnlineEvent() {
      logger.debug('[CONN]: online event received');

      if (_offlineTimeout) {
        logger.debug('[CONN]: Clear offline timeout');
        window.clearTimeout(_offlineTimeout);
        _offlineTimeout = null;
      }

      if (_state === ConnectionState.Reconnecting) {
        // Reset retry interval and try to reconnect
        _retryInterval = RETRY_INTERVAL;
        _that.reconnect(null, null, 1000);
      }
    }

    function handleOfflineEvent() {
      logger.debug('[CONN]: offline event received');

      if (_state !== ConnectionState.Connected) {
        return;
      }

      // Make sure we are handling the online event
      window.addEventListener('online', handleOnlineEvent);

      if (!_offlineTimeout) {
        _offlineTimeout = window.setTimeout(function () {
          _offlineTimeout = null;
          if (!window.navigator.onLine) {
            logger.debug('[CONN]: The client is offline. Change state to Reconnecting.');
            _that.reconnect();
          }
        }, OFFLINE_DELAY);
      }
    }

    function clearSocket() {
      if (_socket) {
        // Unregister events
        _socket.onopen = null;
        _socket.onclose = null;
        _socket.onmessage = null;
        _socket.onerror = null;

        _socket = null;
      }
    }

    function setState(newState) {
      if (newState !== _state) {
        logger.debug('[CONN]: Changed connection state from ' + _state + ' to ' + newState);
        logger.debug('[CONN]: Raising connectionStateChange event');

        var oldState = _state;
        _state = newState;

        // Dispatch the event asynchronously
        window.setTimeout(function () {
          raiseStateChange(newState, oldState);
        }, 0);
      }
    }

    function onError(error) {
      try {
        _errorCb && _errorCb(error);
      } catch (e) {
        logger.error('[CONN]: Exception on errorCallback. ', e);
      } finally {
        _errorCb = null;
        _successCb = null;
      }
    }

    function onSuccess() {
      try {
        logger.debug('[CONN]: Registered Successfully');
        _successCb && _successCb();
      } catch (e) {
        logger.error('[CONN]: Exception on successCallback. ', e);
      } finally {
        _errorCb = null;
        _successCb = null;
      }
    }

    function clearThrottlingData() {
      if (_throttleTimeout) {
        window.clearTimeout(_throttleTimeout);
        _throttleTimeout = null;
      }
      _queuedMessages = [];
      _sentTimestamps = [];
    }

    function disconnect(suppressStateChange, code) {
      if (_retryTimeout) {
        window.clearTimeout(_retryTimeout);
        _retryTimeout = null;
      }
      if (_reconnectTimeout) {
        window.clearTimeout(_reconnectTimeout);
        _reconnectTimeout = null;
      }
      clearThrottlingData();

      if (_socket) {
        logger.debug('[CONN]: Disconnecting from server');
        var activeSocket = _socket;
        clearSocket();
        if (code) {
          activeSocket.close(code);
        } else {
          activeSocket.close();
        }
      }

      if (!suppressStateChange) {
        if (window.removeEventListener) {
          window.removeEventListener('online', handleOnlineEvent);
          window.removeEventListener('offline', handleOfflineEvent);
        }
        setState(ConnectionState.Disconnected);
      }
    }

    function retryConnection() {
      if (Utils.isMobile()) {
        // For mobile clients we don't try to reconnect from javascript
        logger.warn('[CONN]: Mobile Client - Do not attempt to reconnect from JS');
        return;
      }

      if (MAX_ATTEMPTS > 0 && _retryAttempts >= MAX_ATTEMPTS) {
        logger.warn('[CONN]: Reached max number of attempts reconnecting to WebSocket');
        disconnect();
        return;
      }

      if (window.navigator.onLine === false) {
        // Only for browser clients that support window.navigator.onLine,
        // non-browser clients (e.g. mobile, node) will have window.navigator.onLine as 'undefind'
        logger.warn('[CONN]: Client is offline. Set retry timer to max value.');
        _retryInterval = MAX_INTERVAL;
      } else if (!_retryInterval) {
        // This is the first reconnection attempt.
        // Randonmly distribute the first reconnection so the clients won't swamp the Access Server all at once.
        if (_hasActiveCall && _hasActiveCall()) {
          logger.info('[CONN]: Client has an active call. Use priority reconnection timeout');
          _retryInterval = Utils.randomNumber(RETRY_DELAY_PRIORITY, RETRY_DELAY_NORMAL);
        } else {
          _retryInterval = RETRY_DELAY_NORMAL + Utils.randomNumber(0, RETRY_INTERVAL);
        }
      }

      logger.info('[CONN]: Retry to open socket in ' + _retryInterval / 1000 + ' seconds');
      _retryTimeout = window.setTimeout(function () {
        _retryTimeout = null;
        _currTarget = _proxyTarget || _defaultTarget;
        openSocket();
      }, _retryInterval);

      if (_retryInterval < MAX_INTERVAL) {
        // After the first attempt (random), set the interval to RETRY_INTERVAL
        // for MAX_SHORT_ATTEMPTS, then start doubling it up to MAX_INTERVAL
        _retryAttempts++;
        _retryInterval = _retryAttempts > MAX_SHORT_ATTEMPTS ? _retryInterval * 2 : RETRY_INTERVAL;
        _retryInterval = Math.min(_retryInterval, MAX_INTERVAL);
      }
    }

    function fallbackToDefaultTarget() {
      if (_currTarget !== _proxyTarget) {
        return false;
      }
      disconnect(true);
      window.setTimeout(function () {
        logger.info('[CONN]: Fallback to default target URL');
        _currTarget = _defaultTarget;
        openSocket();
      }, 0);
      return true;
    }

    // Common handler for connection close and error events
    function handleConnectionClosed() {
      clearSocket();
      clearThrottlingData();

      switch (_state) {
        case ConnectionState.Connected:
          setState(ConnectionState.Reconnecting);
          retryConnection();
          break;

        case ConnectionState.Reconnecting:
          if (!fallbackToDefaultTarget()) {
            raiseReconnectFailed();
            retryConnection();
          }
          break;

        case ConnectionState.Connecting:
          if (!fallbackToDefaultTarget()) {
            disconnect();
            logger.error('[CONN]: Failed to open WebSocket connection');
            onError('Failed to open WebSocket connection');
          }
          break;

        default:
          // Socket is already Disconnected
          break;
      }
    }

    function onSocketOpen(socket) {
      if (socket !== _socket) {
        return;
      }
      logger.info('[CONN]: WebSocket is opened');
      // Retry interval is set before first reconnection attempt
      _retryInterval = 0;
      _retryAttempts = 0;

      if (_state !== ConnectionState.Disconnected) {
        setState(ConnectionState.Connected);
        if (window.addEventListener) {
          window.addEventListener('offline', handleOfflineEvent);
          window.removeEventListener('online', handleOnlineEvent);
        }
        onSuccess();
      }
    }

    function onSocketClose(socket, evt) {
      if (socket !== _socket) {
        return;
      }
      evt = evt || {};
      logger.info('[CONN]: WebSocket is closed. Code: ' + evt.code + '. Reason:', evt.reason);
      handleConnectionClosed();
    }

    function onSocketMessage(socket, message) {
      if (socket !== _socket) {
        return;
      }
      try {
        if (!message.data || message.data === 'PING') {
          // Ignore PING request
          return;
        }

        var msg = message.data;
        if (!_config.doNotParseMessages) {
          try {
            msg = JSON.parse(msg);
          } catch (err) {
            logger.error('[CONN]: Message discarded (Cannot Parse): ', message.data);
            return;
          }
        }
        // Do not log message here since we don't know which fields must be suppressed
        raiseMessage(msg);
      } catch (e) {
        logger.error(e);
      }
    }

    function onSocketError(socket, error) {
      if (socket !== _socket) {
        return;
      }
      logger.error('[CONN]: WebSocket connection error. ', error);
      handleConnectionClosed();
    }

    function openSocket() {
      if (_state !== ConnectionState.Connecting && _state !== ConnectionState.Reconnecting) {
        // We are neither connecting nor reconnecting. Stop here.
        return;
      }

      // Clear handlers for previous socket
      clearSocket();
      _targetObfuscated = getObfuscatedUrl(_currTarget);
      logger.info('[CONN]: Opening WebSocket to ', _targetObfuscated);
      try {
        switch (window.navigator.platform) {
          case 'iOS':
            _socket = WebSocket.createWebSocket(_currTarget);
            break;
          case 'node':
            _socket = new WebSocket(_currTarget, _config.cookie);
            break;
          default:
            _socket = typeof unifyPhone.__wsOverrideImpl === 'function' ? unifyPhone.__wsOverrideImpl(_currTarget) : new WebSocket(_currTarget);
        }
      } catch (e) {
        logger.error('[CONN]: Failed to connect WebSocket. ', e);
        onError(e);
        return;
      }

      _socket.onopen = onSocketOpen.bind(null, _socket);
      _socket.onclose = onSocketClose.bind(null, _socket);
      _socket.onmessage = onSocketMessage.bind(null, _socket);
      _socket.onerror = onSocketError.bind(null, _socket);
    }

    function clearSentTimestamps() {
      var now = Date.now();
      var idx = _sentTimestamps.findIndex(function (timestamp) {
        return now - timestamp < THROTTLE_INTERVAL;
      });
      if (idx === -1) {
        // Empty the array
        _sentTimestamps = [];
      } else {
        _sentTimestamps.splice(0, idx);
      }
    }

    function canSendMessage() {
      if (_throttleTimeout) {
        return false;
      }
      if (_sentTimestamps.length >= THROTTLE_MAX_MESSAGES) {
        clearSentTimestamps();
      }
      return _sentTimestamps.length < THROTTLE_MAX_MESSAGES;
    }

    function startThrottleTimeout() {
      if (!_throttleTimeout) {
        var delay = MIN_THROTTLE_DELAY;
        if (_sentTimestamps.length >= THROTTLE_MAX_MESSAGES) {
          delay += THROTTLE_INTERVAL - (Date.now() - _sentTimestamps[0]);
        }
        logger.info('[CONN]: Start throttle timeout with delay = ', delay);
        _throttleTimeout = window.setTimeout(function () {
          _throttleTimeout = null;
          dequeueMessages();
        }, delay);
      }
    }

    function sendMessage(data) {
      try {
        var reqId = (data.request && data.request.requestId) || 'N/A';
        _socket.send(JSON.stringify(data));
        _sentTimestamps.push(Date.now());
        logger.debug('[CONN]: Sent message with requestId:', reqId);
      } catch (e) {
        logger.error('[CONN]: Failed to send message. ', e);
      }
    }

    function dequeueMessages() {
      logger.info('[CONN]: Dequeue throttled messages. Number of queued messages = ', _queuedMessages.length);
      clearSentTimestamps();

      var count = 0;
      while (_queuedMessages.length > 0 && _sentTimestamps.length < THROTTLE_MAX_MESSAGES && count < MAX_DEQUEUED_MESSAGES) {
        sendMessage(_queuedMessages.shift());
        count++;
      }
      if (_queuedMessages.length > 0) {
        logger.warn('[CONN]: Number of remaining queued messages = ', _queuedMessages.length);
        startThrottleTimeout();
      } else {
        logger.info('[CONN]: Dequeued all throttled messages');
      }
    }

    function queueMessage(data) {
      logger.warn('[CONN]: Throttling limit exceeded. Queue the message.');
      if (_queuedMessages.length >= MAX_QUEUED_MESSAGES) {
        logger.error('[CONN]: Throttling queue is full. Drop the message.');
      } else {
        // Queue the new message
        _queuedMessages.push(data);
        logger.info('[CONN]: Queued message #', _queuedMessages.length);
      }
    }

    function isLogoutRequest(data) {
      return !!(data.request && data.request.user && data.request.user.type === Constants.UserActionType.LOGOUT);
    }

    /////////////////////////////////////////////////////////////////////////////
    // Public Event Handlers
    /////////////////////////////////////////////////////////////////////////////
    this.onStateChange = null;
    this.onReconnectFailed = null;
    this.onMessage = null;

    /////////////////////////////////////////////////////////////////////////////
    // Public interfaces
    /////////////////////////////////////////////////////////////////////////////
    this.getState = function () {
      return _state;
    };

    this.isConnected = function () {
      return _state === ConnectionState.Connected;
    };

    this.connect = function (url, proxyUrl, successCallback, errorCallback) {
      logger.debug('[CONN]: Connect WebSocket: ', {
        url: getObfuscatedUrl(url),
        proxyUrl: getObfuscatedUrl(proxyUrl)
      });
      if (_state !== ConnectionState.Disconnected) {
        // Disconnect the socket but do not raise an event
        disconnect(true);
      }

      setTarget(url, proxyUrl);

      _successCb = successCallback || function () {};
      _errorCb = errorCallback || function () {};

      setState(ConnectionState.Connecting);
      // Use timeout to open socket after the connecting state is set
      window.setTimeout(function () {
        _currTarget = _proxyTarget || _defaultTarget;
        openSocket();
      }, 0);
    };

    this.disconnect = function () {
      logger.debug('[CONN]: Disconnect WebSocket from ', _targetObfuscated);
      disconnect();
    };

    this.reconnect = function (url, proxyUrl, delay, code) {
      logger.debug('[CONN]: Reconnect WebSocket: ', {
        url: getObfuscatedUrl(url),
        proxyUrl: getObfuscatedUrl(proxyUrl)
      });
      // First disconnect the socket (if opened).
      disconnect(true, code);

      // Update the url since the token uri parameter may have changed
      if (url) {
        setTarget(url, proxyUrl);
      }

      // Now start a small timeout to wait for the socket to close (if it was opened)
      // and then open it again.
      setState(ConnectionState.Reconnecting);
      _reconnectTimeout = window.setTimeout(function () {
        _reconnectTimeout = null;
        _currTarget = _proxyTarget || _defaultTarget;
        openSocket();
      }, delay || 50);
    };

    this.sendMessage = function (data, suppressLog) {
      if (_state !== ConnectionState.Connected) {
        logger.warn('[CONN]: WebSocket is not connected. Cannot send message. ', data);
        return false;
      }

      if (!data) {
        logger.error('[CONN]: Data is missing. Cannot send message.');
        return false;
      }

      if (!suppressLog) {
        logger.msgSend('[CONN]: ', data);
      }

      if (typeof data === 'string') {
        if (data === 'PING' && Utils.isMobile()) {
          _socket.ping(); // Mobile client's implementation
        } else {
          _socket.send(data);
        }
      } else {
        delete data.keysToOmitFromLogging;
        if (canSendMessage()) {
          sendMessage(data);
        } else if (isLogoutRequest(data)) {
          // Send LOGOUT and clear all queued requests since WS will be disconnected
          sendMessage(data);
          clearThrottlingData();
        } else {
          queueMessage(data);
          startThrottleTimeout();
        }
      }
      return true;
    };

    this.injectHasActiveCall = function (fn) {
      _hasActiveCall = typeof fn === 'function' ? fn : null;
    };
  }

  // Exports
  unifyPhone.ConnectionController = ConnectionController;
  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.ConnectionState = ConnectionState;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global cordova, mozRTCIceCandidate, mozRTCPeerConnection, mozRTCSessionDescription, Promise,
require, RTCPeerConnectionUnified, webkitMediaStream, webkitRTCPeerConnection*/

/////////////////////////////////////////////////////////////////////////////////////////
//
// The following table shows the different names used by Chrome and Firefox for the
// standard Media Capture and WebRTC APIs.
//
// W3C Standard           Chrome                   Firefox                    Mobile
// --------------------------------------------------------------------------------------
// getUserMedia           webkitGetUserMedia       getUserMedia               getUserMedia
// RTCPeerConnection      webkitRTCPeerConnection  RTCPeerConnection          RTCPeerConnection
// RTCSessionDescription  RTCSessionDescription    RTCSessionDescription      RTCPeerConnection
// RTCIceCandidate        RTCIceCandidate          RTCIceCandidate            RTCIceCandidate
//
//
// The WebRTCAdapter object helps insulate our apps from these differences.
//
/////////////////////////////////////////////////////////////////////////////////////////

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var logger = unifyPhone.logger;
  var Utils = unifyPhone.Utils;

  var DESKTOP_MAX_WIDTH = 1920;
  var DESKTOP_MAX_HEIGHT = 1080;
  var DESKTOP_DEFAULT_MAX_FRAMERATE = 10;
  var DESKTOP_DEFAULT_MIN_FRAMERATE = 5;

  var VideoResolutionLevel = {
    VIDEO_1080: '1080p',
    VIDEO_720: '720p',
    VIDEO_480: '480p'
  };

  var RtpQualityLevel = Object.freeze({
    RTP_QUALITY_POOR: { levelName: 'poor', value: 0, threshold: 0 }, // Level used only with Unified Plan
    RTP_QUALITY_LOW: { levelName: 'low', value: 1, threshold: 0.1 },
    RTP_QUALITY_MEDIUM: { levelName: 'medium', value: 2, threshold: 0.03 },
    RTP_QUALITY_HIGH: { levelName: 'high', value: 3, threshold: 0 },

    // Deprecated
    getLevel: function (packetsLost) {
      if (packetsLost > this.RTP_QUALITY_LOW.threshold) {
        return this.RTP_QUALITY_LOW;
      } else if (packetsLost > this.RTP_QUALITY_MEDIUM.threshold) {
        return this.RTP_QUALITY_MEDIUM;
      } else {
        return this.RTP_QUALITY_HIGH;
      }
    }
  });

  var RtpStatsConfig = {
    COLLECTION_INTERVAL: 5000,
    DELAY_UPON_ANSWER: Utils.isMobile() ? 0 : 5000,
    DELAY_RTT_PROCESSING: 3 // googRTT takes few seconds to settle, so skip 3 stat collection cycles
  };

  /////////////////////////////////////////////////////////////////////////////////
  // Helper functions
  /////////////////////////////////////////////////////////////////////////////////
  var getTracks = function (stream) {
    if (typeof stream.getTracks === 'function') {
      return stream.getTracks();
    }
    return stream.getAudioTracks().concat(stream.getVideoTracks());
  };

  var stopMediaStream = function (stream) {
    if (stream) {
      try {
        stream.oninactive = null;
        // In the latest W3C definition the stop function belongs in the MediaStreamTrack, not the MediaStream.
        // But if MediaStreamTrack.stop() is not present, fallback to MediaStream.stop().
        var tracks = getTracks(stream);
        if (tracks.length && tracks[0].stop) {
          tracks.forEach(function (t) {
            t.onended = null;
            t.stop();
            logger.debug('[WebRTCAdapter]: Media track has been stopped:', t.label);
          });
        } else if (stream.stop) {
          stream.stop();
        }
        logger.info('[WebRTCAdapter]: Media stream has been stopped. Stream ID:', stream.id);
      } catch (e) {
        logger.error('[WebRTCAdapter]: Failed to stop media stream. ', e);
      }
    }
  };

  var stopLocalVideoTrack = function (stream) {
    if (stream) {
      try {
        var tracks = stream.getVideoTracks();
        if (tracks) {
          tracks.forEach(function (track) {
            if (track.stop && track.kind === 'video' && !track.remote) {
              track.stop();
            }
          });
        } else {
          logger.info('[WebRTCAdapter]: No video tracks to be changed');
        }
      } catch (e) {
        logger.error('[WebRTCAdapter]: Failed to disable local video tracks. ', e);
      }
    }
  };

  var closePc = function (pc) {
    try {
      pc.close();
      logger.debug('[WebRTCAdapter]: RTCPeerConnection has been closed');
    } catch (e) {
      logger.error(e);
    }
  };

  var supportsAudioOutputSelection = function () {
    // Check whether MediaDevices' enumerateDevices and HTMLMediaElement's setSinkId APIs are available.
    // These are experimental APIs supported in Chrome 45.0.2441.x or later.
    // You can enable it by selecting "Enable experimental Web Platform features" in chrome://flags

    return (
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.enumerateDevices === 'function' &&
      typeof HTMLMediaElement !== 'undefined' &&
      typeof HTMLMediaElement.prototype.setSinkId === 'function'
    );
  };

  var pendingEnumerateDevicesRequests = [];
  var pendingEnumerateDevicesTimeout = null;
  var cachedDeviceInfos = null;
  var CACHED_DEVICE_INFOS_TIMEOUT = 60000; // Keep cached devices for 60 seconds
  var ENUMERATE_DEVICES_TIMEOUT = 3000;

  var clearEnumerateDevicesTimeout = function () {
    if (pendingEnumerateDevicesTimeout) {
      window.clearTimeout(pendingEnumerateDevicesTimeout);
      pendingEnumerateDevicesTimeout = null;
    }
  };

  var invokePendingCallbacks = function (audioSources, videoSources, audioOutputs) {
    clearEnumerateDevicesTimeout();
    var callbacks = pendingEnumerateDevicesRequests;
    pendingEnumerateDevicesRequests = [];

    callbacks.forEach(function (cb) {
      try {
        cb(audioSources || [], videoSources || [], audioOutputs || null);
      } catch (err) {}
    });
  };

  var splitDeviceInfos = function (cb) {
    if (typeof cb !== 'function') {
      return;
    }
    var audioSources, audioOutputs, videoSources;

    logger.debug('[WebRTCAdapter]: Get audio output, microphone and camera devices');

    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      audioSources = [{ kind: 'audio', id: 'default', label: '', facing: '' }];
      videoSources = [{ kind: 'video', id: 'default', label: '', facing: '' }];
      window.setTimeout(function () {
        cb(audioSources, videoSources, null);
      }, 0);
      return;
    }

    if (pendingEnumerateDevicesRequests.length > 0) {
      logger.debug('[WebRTCAdapter]: There is a pending MediaDevices.enumerateDevices() request');
      pendingEnumerateDevicesRequests.push(cb);
      return;
    }

    if (cachedDeviceInfos && cachedDeviceInfos.validTimestamp > Date.now()) {
      logger.debug('[WebRTCAdapter]: Return cached devices');
      audioSources = cachedDeviceInfos.audioSources;
      audioOutputs = cachedDeviceInfos.audioOutputs;
      videoSources = cachedDeviceInfos.videoSources;

      window.setTimeout(function () {
        cb(audioSources, videoSources, audioOutputs);
      }, 0);
      return;
    }

    var invokeCallbacks = invokePendingCallbacks.bind(null);

    pendingEnumerateDevicesRequests.push(cb);

    logger.debug('[WebRTCAdapter]: Invoke MediaDevices.enumerateDevices()');

    clearEnumerateDevicesTimeout();
    pendingEnumerateDevicesTimeout = window.setTimeout(function () {
      pendingEnumerateDevicesTimeout = null;
      if (!pendingEnumerateDevicesRequests.length) {
        return;
      }
      if (cachedDeviceInfos) {
        logger.error('[WebRTCAdapter]: MediaDevices.enumerateDevices timed out. Return cached devices.');
        audioSources = cachedDeviceInfos.audioSources;
        audioOutputs = cachedDeviceInfos.audioOutputs;
        videoSources = cachedDeviceInfos.videoSources;
      } else {
        logger.error('[WebRTCAdapter]: MediaDevices.enumerateDevices timed out. Device cache is empty, no devices available!');
      }
      invokeCallbacks(audioSources, videoSources, audioOutputs);
      invokeCallbacks = null;
    }, ENUMERATE_DEVICES_TIMEOUT);

    navigator.mediaDevices
      .enumerateDevices()
      .then(function (deviceInfos) {
        deviceInfos = deviceInfos || [];
        audioSources = [];
        audioOutputs = [];
        videoSources = [];

        logger.info('[WebRTCAdapter]: MediaDevices.enumerateDevices returned ' + deviceInfos.length + ' device(s)');

        deviceInfos.forEach(function (info) {
          var item = {
            id: info.deviceId,
            groupId: info.groupId,
            kind: info.kind,
            label: info.label,
            origLabel: info.label
          };

          if (item.label) {
            // Strip device vendor/model from label
            var match = item.label.match(/(.*)\s\(.*:.*\)/);
            item.label = (match && match[1]) || item.label;
          }

          switch (item.kind) {
            case 'audioinput':
              audioSources.push(item);
              break;
            case 'audiooutput':
              audioOutputs.push(item);
              break;
            case 'videoinput':
              videoSources.push(item);
              break;
          }
        });

        // Only return audioOutputs if there is at least one device.
        // Also make sure the browser supports the HTMLMediaElement setSinkId API.
        if (audioOutputs.length === 0 || !supportsAudioOutputSelection()) {
          audioOutputs = null;
        }

        logger.debug('[WebRTCAdapter]: Update DeviceInfos cache');
        cachedDeviceInfos = {
          audioSources: audioSources,
          audioOutputs: audioOutputs,
          videoSources: videoSources
        };

        if (!audioSources.length) {
          cachedDeviceInfos.validTimestamp = 0;
        } else if (
          !audioSources.some(function (source) {
            return source.label;
          }) ||
          (videoSources.length &&
            !videoSources.some(function (source) {
              return source.label;
            }))
        ) {
          logger.debug("[WebRTCAdapter]: User hasn't authorized access to audio and/or video input devices yet");
          // User still hasn't authorized access to audio and video sources, so mark the cached
          // devices as expired, so we'll attempt to retrieve them again next time around
          cachedDeviceInfos.validTimestamp = 0;
        } else {
          cachedDeviceInfos.validTimestamp = Date.now() + CACHED_DEVICE_INFOS_TIMEOUT;
        }

        invokeCallbacks && invokeCallbacks(audioSources, videoSources, audioOutputs);
      })
      .catch(function (e) {
        logger.error('[WebRTCAdapter]: Failed to enumerate devices. ', e);
        invokeCallbacks && invokeCallbacks();
      });
  };

  var clearDeviceInfoCache = function () {
    logger.debug('[WebRTCAdapter]: Cleared device info cache');
    cachedDeviceInfos = null;
  };

  var separateIceServers = function (config) {
    if (!config || !config.iceServers) {
      return;
    }
    var iceServers = [];
    config.iceServers.forEach(function (iceServer) {
      if (iceServer.url) {
        iceServers.push(iceServer);
      } else {
        iceServer.urls &&
          iceServer.urls.forEach(function (url) {
            iceServers.push({
              url: url,
              credential: iceServer.credential,
              username: iceServer.username
            });
          });
      }
    });
    config.iceServers = iceServers;
  };

  var getReducedDefaultAudioOptions = function (config, useNewSyntax) {
    config = config || {};

    var options = [
      { autoGainControl: !!config.enableAudioAGC },
      { echoCancellation: !!config.enableAudioEC },
      { comfortNoiseCancellation: !!config.disableAudioCN }
    ];
    return useNewSyntax ? { advanced: options } : { optional: options };
  };

  var getDefaultAudioOptions = function (config) {
    config = config || {};
    return {
      optional: [
        // Echo cancellation constraints
        { echoCancellation: !!config.enableAudioEC },
        { googEchoCancellation: !!config.enableAudioEC },
        { googEchoCancellation2: !!config.enableAudioEC },
        { autoGainControl: !!config.enableAudioAGC },
        { googAutoGainControl: !!config.enableAudioAGC },
        { googAutoGainControl2: !!config.enableAudioAGC },
        { noiseSuppression: true },
        { googNoiseSuppression: true },
        { googNoiseSuppression2: true },
        { googHighpassFilter: true },
        //Comfort noise constraint
        { comfortNoiseCancellation: !!config.disableAudioCN }
      ]
    };
  };

  var getDefaultVideoOptionsNewConstraint = function (config) {
    var options;
    if (config && config.videoResolution) {
      options = {
        aspectRatio: { min: 1.77, max: 1.78 },
        frameRate: { min: 24, max: 30 }
      };
      if (config.minFrameRate) {
        options.frameRate.min = Math.min(config.minFrameRate, options.frameRate.max);
      }
      if (config.sourceId) {
        options.deviceId = { exact: config.sourceId };
      }
      switch (config.videoResolution) {
        case VideoResolutionLevel.VIDEO_1080:
          // Full HD
          options.width = { min: 1920 };
          options.height = { min: 1080 };
          break;
        case VideoResolutionLevel.VIDEO_720:
          // HD
          options.width = { min: 1280 };
          options.height = { min: 720 };
          break;
        case VideoResolutionLevel.VIDEO_480:
          // SD
          options.width = { min: 854 };
          options.height = { min: 480 };
          break;
      }
      return options;
    }
    // return video in 16:9 aspect ratio by default
    options = {
      aspectRatio: { min: 1.77, max: 1.78 }
    };
    if (config.sourceId) {
      // If resolution is not set, deviceId is optional
      options.deviceId = config.sourceId;
    }
    return options;
  };

  var getDefaultVideoOptions = function (config) {
    if (unifyPhone.WebRTCAdapter.useNewConstraintSyntax) {
      return getDefaultVideoOptionsNewConstraint(config);
    }

    var options = {
      mandatory: {
        // set video in 16:9 aspect ratio by default
        minAspectRatio: 1.77,
        maxAspectRatio: 1.78
      },
      optional: [{ googNoiseReduction: true }]
    };

    if (config) {
      if (config.videoResolution) {
        // FrameRate
        options.mandatory.minFrameRate = 24;
        options.mandatory.maxFrameRate = 30;
        if (config.minFrameRate) {
          options.mandatory.minFrameRate = Math.min(config.minFrameRate, options.mandatory.maxFrameRate);
        }
        if (config.sourceId) {
          options.mandatory.sourceId = config.sourceId;
        }
        switch (config.videoResolution) {
          case VideoResolutionLevel.VIDEO_1080:
            // Full HD
            options.mandatory.minWidth = 1920;
            options.mandatory.maxWidth = 1920;
            options.mandatory.minHeight = 1080;
            options.mandatory.maxHeight = 1080;
            break;
          case VideoResolutionLevel.VIDEO_720:
            // HD
            options.mandatory.minWidth = 1280;
            options.mandatory.maxWidth = 1280;
            options.mandatory.minHeight = 720;
            options.mandatory.maxHeight = 720;
            break;
          case VideoResolutionLevel.VIDEO_480:
            // SD
            options.mandatory.minWidth = 853;
            options.mandatory.maxWidth = 854;
            options.mandatory.minHeight = 480;
            options.mandatory.maxHeight = 480;
            break;
        }
      } else if (config.sourceId) {
        // If resolution is not set, sourceId is optional
        options.optional.push({ sourceId: config.sourceId });
      }
    }

    return options;
  };

  var toggleAudio = function (stream, enable) {
    var audio = stream && stream.getAudioTracks();
    if (audio && audio[0]) {
      audio[0].enabled = !!enable;
      return true;
    }
    return false;
  };

  var attachSinkIdToAudioElement = function (audioElement, deviceList, cb) {
    cb = cb || function () {};

    logger.debug('[WebRTCAdapter]: Attach sinkId to audio element');

    if (!audioElement) {
      cb('Invalid parameters');
      return;
    }
    if (!deviceList || !deviceList.length) {
      logger.debug('[WebRTCAdapter]: There is no audio output device selected');
      cb();
      return;
    }
    if (typeof audioElement.sinkId !== 'undefined' && unifyPhone.WebRTCAdapter) {
      // In order to find out which device in deviceList can be used, we need to get the latest list of
      // devices that are plugged in
      logger.debug('[WebRTCAdapter]: Get media sources to find available device');
      unifyPhone.WebRTCAdapter.getMediaSources(function (audioSources, videoSources, audioOutputDevices) {
        var foundMatch = unifyPhone.Utils.selectMediaDevice(audioOutputDevices, deviceList);
        var deviceId = (foundMatch && foundMatch.id) || '';

        logger.debug('[WebRTCAdapter]: Found audio output device to attach as sinkId. deviceId = ', deviceId || 'NONE');

        if (audioElement.sinkId === deviceId) {
          // Sink ID already set
          logger.debug('[WebRTCAdapter]: Audio output device is already attached to element');
          cb();
          return;
        }

        if (!deviceId) {
          // We had another device configured as the sink ID and we are falling back to the default device
          logger.warn(
            "[WebRTCAdapter]: A previously selected output device could not be found. Fallback to browser's default. Device not found: ",
            audioElement.sinkId
          );
        }

        var timeout = window.setTimeout(function () {
          timeout = null;
          logger.error('[WebRTCAdapter]: Timed out attaching audio output device: ', deviceId || '<default>');
          cb('timed out');
        }, 3000);
        audioElement
          .setSinkId(deviceId)
          .then(function () {
            if (!timeout) {
              return;
            }
            window.clearTimeout(timeout);
            logger.debug('[WebRTCAdapter]: Success, audio output device attached: ', deviceId || '<default>');
            cb();
          })
          .catch(function (error) {
            if (!timeout) {
              return;
            }
            window.clearTimeout(timeout);
            error = error || {};
            var errorMessage = error.message || 'unknown error';
            if (error.name === 'SecurityError') {
              errorMessage = 'You need to use HTTPS for selecting audio output device.';
            }
            logger.error('[WebRTCAdapter]: Failed to attach output device. Device Id:' + deviceId + ' - ', errorMessage);
            cb(errorMessage);
          });
        logger.debug('[WebRTCAdapter]: setSinkId has been invoked');
      });
    } else {
      cb('setSinkId not available');
    }
  };

  var convertDeprecatedSdpConstraints = function (deprecatedConstraints) {
    var offerOptions = {
      offerToReceiveAudio: 1,
      offerToReceiveVideo: 1
    };
    if (deprecatedConstraints) {
      if (deprecatedConstraints.mandatory) {
        if (deprecatedConstraints.mandatory.hasOwnProperty('OfferToReceiveAudio')) {
          offerOptions.offerToReceiveAudio = deprecatedConstraints.mandatory.OfferToReceiveAudio ? 1 : 0;
        }
        if (deprecatedConstraints.mandatory.hasOwnProperty('OfferToReceiveVideo')) {
          offerOptions.offerToReceiveVideo = deprecatedConstraints.mandatory.OfferToReceiveVideo ? 1 : 0;
        }
      }
      if (deprecatedConstraints.optional) {
        deprecatedConstraints.optional.some(function (constraint) {
          if (constraint.hasOwnProperty('VoiceActivityDetection')) {
            offerOptions.voiceActivityDetection = !!constraint.VoiceActivityDetection;
            return true;
          }
          return false;
        });
      }
    }
    return offerOptions;
  };

  // Method to move a specific constraint parameter from mandatory to the optional section
  // This method handles both Chrome's (old) and FF's (new spec) style of constraints, that's why we need
  // to have both of them passed as parameters (chromeParam and specParam)
  var moveMandatoryToOptional = function (constraints, media, chromeParam, specParam) {
    if (!constraints || !constraints[media]) {
      return constraints;
    }
    var mediaConstraints = constraints[media];
    if (mediaConstraints.mandatory) {
      // Old style constraints (Chrome): move param from mandatory to optional array
      var required = mediaConstraints.mandatory[chromeParam];
      if (required) {
        // Clone constraints, so input constraint is not modified
        constraints = Object.create(constraints);
        var newMediaConstraints = constraints[media];
        newMediaConstraints.optional = newMediaConstraints.optional || [];
        var obj = {};
        obj[chromeParam] = required;
        newMediaConstraints.optional.push(obj);
        delete newMediaConstraints.mandatory;
      }
    } else {
      var paramConfig = mediaConstraints[specParam];
      if (paramConfig && paramConfig.exact) {
        // New style constraints (WebRTC spec). Just move the value from the exact property
        // directly to param (this will make it 'ideal', or optional)
        constraints = Object.create(constraints);
        constraints[media][specParam] = paramConfig.exact;
      }
    }
    return constraints;
  };

  var getUserMediaCommon = function (method, context, constraints, successCallback, errorCallback) {
    var onErrorCb = function (err) {
      var newConstraints = moveMandatoryToOptional(constraints, 'audio', 'sourceId', 'deviceId');
      if (newConstraints !== constraints) {
        logger.warn('[WebRTCAdapter]: First getUserMedia attempt with required audio sourceId/deviceId failed. Falling back to optional.');
        var sCb = function (stream) {
          // Pass the exception object along with the stream, so the caller knows we had an exception
          successCallback(stream, err);
        };
        // The getUserMedia method can be either a promise or callback version
        var promise = method.apply(context, [newConstraints, sCb, errorCallback]);
        if (promise && promise.then) {
          promise.then(sCb).catch(errorCallback);
        }
      } else {
        errorCallback && errorCallback(err);
      }
    };
    // The getUserMedia method can be either a promise or callback version
    var promise = method.apply(context, [constraints, successCallback, onErrorCb]);
    if (promise && promise.then) {
      promise.then(successCallback).catch(onErrorCb);
    }
  };

  var timedGetUserMedia = function (method, context, constraints, successCallback, errorCallback) {
    // Firefox has some bugs regarding 'getUserMedia' popup:
    // Bug 947266 - Navigator.getUserMedia should either remove or let webpages detect 'Not now' case
    // Bug 846584 - WebRTC getUserMedia doorhanger has 3 ways of rejecting the request, and there's no clear distinction between them
    //
    // Chrome also has some problems with very lengthy or unresponsive getUserMedia's
    // Desktop App doesn't need to request permission to the user, so the timeout is shorter (10s)
    //
    // Workaround by implementing timeout for recalling/reshowing 'getUserMedia' request

    var getUserMediaTimeout = null;
    var getUserMediaLimit = 3;
    var getUserMediaCounter = 0;

    function onSuccess(stream, exception) {
      logger.info('[WebRTCAdapter]: Get user media succeeded');
      if (successCallback) {
        successCallback(stream, exception);
      } else {
        // Stop the media stream since it won't be used anymore
        stopMediaStream(stream);
      }
      successCallback = null;
      errorCallback = null;
    }

    function onError(error) {
      logger.warn('[WebRTCAdapter]: Get user media failed: ', (error && error.name) || error);
      getUserMediaTimeout && window.clearTimeout(getUserMediaTimeout);
      getUserMediaTimeout = null;
      errorCallback && errorCallback(error);
      successCallback = null;
      errorCallback = null;
    }

    function getUserMediaHandler() {
      logger.info('[WebRTCAdapter]: Get user media attempt #:', getUserMediaCounter);
      getUserMediaTimeout = null;
      if (getUserMediaCounter === getUserMediaLimit) {
        onError('Timeout');
        return;
      }
      ++getUserMediaCounter;
      getUserMediaCommon(
        method,
        context,
        constraints,
        function (stream, exception) {
          onSuccess(stream, exception);
          getUserMediaTimeout && window.clearTimeout(getUserMediaTimeout);
          getUserMediaTimeout = null;
        },
        onError
      );

      getUserMediaTimeout = window.setTimeout(getUserMediaHandler, 10000);
    }

    getUserMediaHandler();
  };

  var copyTransportIdFromReceiverStats = function (sender, receiver) {
    receiver.some(function (r) {
      if (r.type === 'ssrc') {
        if (r.stats.transportId) {
          sender.some(function (s) {
            if (s.type === 'ssrc') {
              s.stats.transportId = r.stats.transportId;
              return true;
            }
            return false;
          });
        }
        return true;
      }
      return false;
    });
  };

  var setEncodingParameters = function (rtpTransceiver, parameters, maxBitrate, degradationPreference, scaleResolutionDownBy) {
    if (!rtpTransceiver || !parameters || !maxBitrate) {
      return;
    }

    var appliedParams = [];
    if (degradationPreference) {
      parameters.degradationPreference = degradationPreference;
      appliedParams.push('degradationPreference=' + degradationPreference);
    }
    if (parameters.encodings && parameters.encodings.length > 0) {
      parameters.encodings[0].maxBitrate = maxBitrate;
      appliedParams.push('maxBitrate=' + maxBitrate);
      if (scaleResolutionDownBy) {
        parameters.encodings[0].scaleResolutionDownBy = scaleResolutionDownBy;
        appliedParams.push('scaleResolutionDownBy=' + scaleResolutionDownBy);
      }
    } else {
      logger.error('[WebRTCAdapter]: Unable to set max bitrate due to read-only encoding parameters for RtpSender mid=' + rtpTransceiver.mid);
    }

    if (appliedParams.length) {
      rtpTransceiver.sender
        .setParameters(parameters)
        .then(function () {
          logger.debug('[WebRTCAdapter]: Applied parameters {' + appliedParams + '} to RtpSender mid=' + rtpTransceiver.mid);
        })
        .catch(function (err) {
          logger.error(
            '[WebRTCAdapter]: Could not apply parameters {' + appliedParams + '} to RtpSender mid=' + rtpTransceiver.mid + ': ',
            (err && err.message) || err
          );
        });
    }
  };

  /////////////////////////////////////////////////////////////////////////////////
  // Chrome
  /////////////////////////////////////////////////////////////////////////////////
  function initForChrome() {
    logger.info('Setting WebRTC APIs for Chrome');

    // Temporary workaround to solve Chrome issue #467490
    // https://code.google.com/p/chromium/issues/detail?id=467490
    RTCSessionDescription.prototype.toJSON = function () {
      return {
        type: this.type,
        sdp: this.sdp
      };
    };

    RTCIceCandidate.prototype.toJSON = function () {
      return {
        sdpMLineIndex: this.sdpMLineIndex,
        sdpMid: this.sdpMid,
        candidate: this.candidate
      };
    };

    unifyPhone.WebRTCAdapter = {
      enabled: true,
      browser: 'chrome',
      useNewConstraintSyntax: false,
      useReducedAudioConstraints: true,
      MediaStream: webkitMediaStream,
      PeerConnection: function (config, constraints) {
        var pc = new webkitRTCPeerConnection(config, constraints);
        pc.origCreateOffer = pc.createOffer;
        pc.createOffer = function (successCb, errorCb, offerConstraints) {
          var offerOptions;
          if (offerConstraints) {
            offerOptions = convertDeprecatedSdpConstraints(offerConstraints);
          }
          pc.origCreateOffer(offerOptions)
            .then(function (sdp) {
              successCb && successCb(sdp);
            })
            .catch(function (err) {
              errorCb && errorCb(err);
            });
        };
        pc.origCreateAnswer = pc.createAnswer;
        pc.createAnswer = function (successCb, errorCb, options) {
          var answerOptions = convertDeprecatedSdpConstraints(options);
          pc.origCreateAnswer(answerOptions)
            .then(function (sdp) {
              successCb && successCb(sdp);
            })
            .catch(function (err) {
              errorCb && errorCb(err);
            });
        };
        pc.origGetStats = pc.getStats;
        pc.getStats = function () {
          return new Promise(function (resolve, reject) {
            pc.origGetStats()
              .then(function (result) {
                resolve({ result: result, useStandardGetStats: true });
              })
              .catch(reject);
          });
        };
        return pc;
      },
      SessionDescription: RTCSessionDescription,
      IceCandidate: RTCIceCandidate,
      getUserMedia: function (constraints, successCallback, errorCallback) {
        timedGetUserMedia(navigator.mediaDevices.getUserMedia, navigator.mediaDevices, constraints, successCallback, errorCallback);
      },
      createObjectURL: URL.createObjectURL.bind(URL),
      getAudioStreamTrackId: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? stream.id + ' ' + audioTrack.id : '';
      },
      getVideoStreamTrackId: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? stream.id + ' ' + videoTrack.id : '';
      },
      getAudioTrackId: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? audioTrack.id : '';
      },
      getVideoTrackId: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? videoTrack.id : '';
      },
      getStreamId: function (stream) {
        return stream && stream.id;
      },
      getTrackId: function (track) {
        return track && track.id;
      },
      hasDeviceNameOnTrackLabel: true,
      getAudioTrackLabel: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? audioTrack.label : '';
      },
      getVideoTrackLabel: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? videoTrack.label : '';
      },
      stopMediaStream: stopMediaStream,
      stopLocalVideoTrack: stopLocalVideoTrack,
      closePc: closePc,
      getMediaSources: function (cb) {
        cb && splitDeviceInfos(cb);
      },
      clearMediaSourcesCache: function () {
        clearDeviceInfoCache();
      },
      getAudioOptions: function (config) {
        var audio = UnifyPhone.WebRTCAdapter.useReducedAudioConstraints ? getReducedDefaultAudioOptions(config, false) : getDefaultAudioOptions(config);

        if (config && config.sourceId) {
          // Start out sourceId as mandatory, it will be moved to optional if it fails
          audio.mandatory = audio.mandatory || {};
          audio.mandatory.sourceId = config.sourceId;
        }
        return audio;
      },
      getVideoOptions: getDefaultVideoOptions,
      getDesktopOptions: function (streamId, screenConstraint) {
        screenConstraint = screenConstraint || {};
        var fr = screenConstraint.frameRate || {};
        fr.min = fr.min || DESKTOP_DEFAULT_MIN_FRAMERATE;
        fr.max = fr.max || DESKTOP_DEFAULT_MAX_FRAMERATE;

        return {
          video: {
            width: { max: DESKTOP_MAX_WIDTH },
            height: { max: DESKTOP_MAX_HEIGHT },
            framerate: { min: fr.min, max: fr.max }
          }
        };
      },
      setEncodingParameters: function (rtpTransceiver, maxBitrate, degradationPreference, scaleResolutionDownBy) {
        var parameter = rtpTransceiver.sender.getParameters();
        setEncodingParameters(rtpTransceiver, parameter, maxBitrate, degradationPreference, scaleResolutionDownBy);
      },
      audioOutputSelectionSupported: supportsAudioOutputSelection(),
      groupPeerConnectionsSupported: true,
      getMediaSourcesSupported: true,
      attachSinkIdToAudioElement: attachSinkIdToAudioElement,
      toggleAudio: toggleAudio,
      getNetmaskMap: function () {
        return {};
      }
    };
  }

  /////////////////////////////////////////////////////////////////////////////////
  // Firefox
  /////////////////////////////////////////////////////////////////////////////////
  function normalizeCandidateInfo(stats) {
    // FFv65 changed from ipAddress to address and portNumber to port
    stats.ipAddress = stats.address;
    stats.portNumber = stats.port;
    stats.protocol = stats.protocol || stats.transport;
  }

  function createFirefoxStatsFn() {
    // Convert Firefox stats to Chrome's format
    var Stat = function (stats, kind, mid) {
      // We consider the first 2 m-lines as the main channel and it should always be in the QoS reports.
      var _isMainChannel = mid === '0' || mid === '1';

      this.stats = stats;
      this.id = stats.id;

      switch (stats.type) {
        case 'inboundrtp':
        case 'inbound-rtp':
          if (!stats.isRemote) {
            this.type = 'ssrc';
            this.stats.mid = mid;
            if (kind === 'audio') {
              this.stats.audioOutputLevel = true;
              this.stats.googJitterReceived = stats.jitter * 1000;
            } else {
              this.stats.googFrameHeightReceived = true;
            }
            if (_isMainChannel) {
              this.stats.googTrackId = kind === 'audio' ? 'audio_main' : 'video_main';
            }
          }
          break;
        case 'outboundrtp':
        case 'outbound-rtp':
          if (!stats.isRemote) {
            this.type = 'ssrc';
            this.stats.mid = mid;
            if (kind === 'audio') {
              this.stats.audioInputLevel = true;
            } else {
              this.stats.googFrameHeightSent = true;
              this.stats.googFrameRateSent = Math.round(stats.framerateMean);
              this.stats.googTransmitBitrate = Math.round(stats.bitrateMean);
            }
            if (_isMainChannel) {
              this.stats.googTrackId = kind === 'audio' ? 'audio_main' : 'video_main';
            }
          }
          break;
        case 'candidatepair':
        case 'candidate-pair':
          // Candidate pair info is now available in FF, but there's no indication of what
          // kind of media they belong to yet. Still waiting for more getStats improvements:
          // https://bugzilla.mozilla.org/showdependencytree.cgi?id=964161&hide_resolved=1
          this.type = 'googCandidatePair';
          if (typeof stats.selected === 'boolean' && stats.selected && stats.state === 'succeeded') {
            this.stats.googActiveConnection = 'true';
            this.stats.googChannelId = stats.transportId;
          } else {
            this.discard = true;
          }
          break;
        case 'local-candidate':
          this.type = 'localcandidate';
          normalizeCandidateInfo(this.stats);
          break;
        case 'remote-candidate':
          this.type = 'remotecandidate';
          normalizeCandidateInfo(this.stats);
          break;
        default:
          this.type = stats.type;
          break;
      }
    };
    Stat.prototype.names = function () {
      return Object.keys(this.stats);
    };
    Stat.prototype.stat = function (type) {
      return this.stats[type];
    };

    var convertStats = function (stats, kind, isMainChannel) {
      var items = [];
      var transportId, googRtt;
      stats.forEach(function (item) {
        var converted = new Stat(item, kind, isMainChannel);
        if (!converted.discard) {
          if (converted.type === 'googCandidatePair') {
            transportId = converted.stats.transportId;
          } else if (item.type === 'remote-inbound-rtp' && item.mediaType === 'video') {
            googRtt = converted.stats.roundTripTime * 1000;
          }
          items.push(converted);
        }
      });
      if (transportId || googRtt >= 0) {
        items.forEach(function (i) {
          if (i.type === 'ssrc' && transportId) {
            // Associate the ssrc with its candidate pairs through the transportId
            i.stats.transportId = transportId;
          } else if (i.type === 'googCandidatePair' && !i.discard && googRtt >= 0) {
            // Add the rtt from remote-inbound-rtp
            i.stats.googRtt = googRtt;
          }
        });
      }
      return items;
    };

    /**
     * Getting stats from Firefox consists of:
     * 1- invoke getStats() for each RTCRtpSender and RTCRtpReceiver
     * 2- if a RTCRtpSender and a RTCRtpReceiver are in the same Transceiver, they are using the
     *    same channel, so share the channel info between the 2 of them (needs more testing).
     * 3- combine all stats into a single array
     */
    return function (pc) {
      var combinedStats = [];
      var rtpTransceivers = pc.getTransceivers();
      var promises = [];
      rtpTransceivers.forEach(function (t) {
        if (t.receiver && t.receiver.track) {
          // First get the receiver stats
          promises.push(
            new Promise(function (resolve) {
              t.receiver
                .getStats()
                .then(function (receiverStats) {
                  var receiverConverted = convertStats(receiverStats, t.receiver.track.kind, t.mid);
                  if (t.sender && t.sender.track) {
                    // Get the sender stats, which we can't determine its transportId,
                    // so get it from the receiver stats and copy it to the sender's
                    t.sender
                      .getStats()
                      .then(function (stats) {
                        var senderConverted = convertStats(stats, t.sender.track.kind, t.mid);
                        copyTransportIdFromReceiverStats(senderConverted, receiverConverted);
                        Array.prototype.push.apply(combinedStats, receiverConverted);
                        Array.prototype.push.apply(combinedStats, senderConverted);
                        resolve();
                      })
                      .catch(function (error) {
                        logger.debug('[WebRTCAdapter]: Error collecting stats for track: ' + t.sender.track.label + ' . Error: ', error);
                        Array.prototype.push.apply(combinedStats, receiverConverted);
                        resolve();
                      });
                  } else {
                    Array.prototype.push.apply(combinedStats, receiverConverted);
                    resolve();
                  }
                })
                .catch(function (error) {
                  logger.debug('[WebRTCAdapter]: Error collecting stats for track: ' + t.sender.track.label + ' . Error: ', error);
                  resolve();
                });
            })
          );
        } else if (t.sender && t.sender.track) {
          promises.push(
            new Promise(function (resolve) {
              t.sender
                .getStats()
                .catch(function (error) {
                  logger.debug('[WebRTCAdapter]: Error collecting stats for track: ' + t.sender.track.label + ' . Error: ', error);
                })
                .then(function (stats) {
                  if (stats) {
                    Array.prototype.push.apply(combinedStats, convertStats(stats, t.sender.track.kind, t.mid));
                  }
                  resolve();
                });
            })
          );
        }
      });
      return Promise.all(promises).then(function () {
        return combinedStats;
      });
    };
  }

  function createFirefoxPC() {
    var PeerConnection = typeof RTCPeerConnection === 'undefined' ? mozRTCPeerConnection : RTCPeerConnection;
    var processGetStats = createFirefoxStatsFn();

    return function (configuration) {
      var pc = new PeerConnection(configuration);

      pc.origCreateOffer = pc.createOffer;
      pc.createOffer = function (successCb, errorCb) {
        // With Unified Plan we can't pass any parameter to origCreateOffer()
        pc.origCreateOffer()
          .then(function (sdp) {
            successCb && successCb(sdp);
          })
          .catch(function (err) {
            errorCb && errorCb(err);
          });
      };
      pc.origCreateAnswer = pc.createAnswer;
      pc.createAnswer = function (successCb, errorCb) {
        // With Unified Plan we can't pass any parameter to origCreateAnswer()
        pc.origCreateAnswer()
          .then(function (sdp) {
            successCb && successCb(sdp);
          })
          .catch(function (err) {
            errorCb && errorCb(err);
          });
      };

      pc.origGetStats = pc.getStats;
      pc.getStats = function () {
        return processGetStats(pc);
      };
      pc.removeStream = function () {};
      pc.createDTMFSender = function (audioTrack) {
        if (typeof pc.getSenders !== 'function') {
          return null;
        }
        var audioSender = pc.getSenders().find(function (sender) {
          return sender.track === audioTrack;
        });
        var dtmfSender = audioSender && audioSender.dtmf;
        if (!dtmfSender) {
          return null;
        }
        return dtmfSender;
      };
      // Override addStream with our own, which makes use of addTrack
      pc.addStream = function (stream) {
        var audio = stream.getAudioTracks()[0];
        if (audio) {
          pc.addTrack(audio, stream);
        }
        var video = stream.getVideoTracks()[0];
        if (video) {
          pc.addTrack(video, stream);
        }
      };
      // Override getRemoteStreams with our own, which makes use of RTCRtpTransceivers
      pc.getRemoteStreams = function () {
        var remoteStreams = [];
        var receivers = pc.getReceivers();
        receivers.forEach(function (r) {
          if (r.track && r.track.mid) {
            var stream = new MediaStream([r.track]);
            remoteStreams.push(stream);
          }
        });
        return remoteStreams;
      };
      // Override getLocalStreams with our own, which makes use of RTCRtpTransceivers
      pc.getLocalStreams = function () {
        var localStreams = [];
        var senders = pc.getSenders();
        senders.forEach(function (s) {
          if (s.track) {
            var stream = new MediaStream([s.track]);
            localStreams.push(stream);
          }
        });
        return localStreams;
      };

      // Override deprecated onaddstream property for Firefox v46 and newer
      Object.defineProperty(pc, 'onaddstream', {
        enumerable: true,
        get: function () {
          return pc.onaddstreamCb;
        },
        set: function (cb) {
          pc.onaddstreamCb = cb;
          if (cb) {
            // Use ontrack instead of onaddstream
            pc.ontrack = function (event) {
              var mid = event && event.transceiver && event.transceiver.mid;
              if (mid && event.track) {
                event.track.mid = mid;
              }
              cb({ stream: event && event.streams && event.streams[0] });
            };
          } else {
            pc.ontrack = null;
          }
        }
      });

      return pc;
    };
  }

  function initForFirefox() {
    logger.info('Setting WebRTC APIs for Firefox');

    // Firefox has deprecated the 'moz' prefix, but we still need with older versions
    var SessionDescription = typeof RTCSessionDescription === 'undefined' ? mozRTCSessionDescription : RTCSessionDescription;
    var IceCandidate = typeof RTCIceCandidate === 'undefined' ? mozRTCIceCandidate : RTCIceCandidate;

    unifyPhone.WebRTCAdapter = {
      enabled: true,
      browser: 'firefox',
      useNewConstraintSyntax: true,
      MediaStream: MediaStream,
      PeerConnection: createFirefoxPC(),
      SessionDescription: SessionDescription,
      IceCandidate: IceCandidate,
      getUserMedia: function (constraints, successCallback, errorCallback) {
        timedGetUserMedia(navigator.mediaDevices.getUserMedia, navigator.mediaDevices, constraints, successCallback, errorCallback);
      },
      createObjectURL: function (object) {
        return URL.createObjectURL(object);
      },
      getAudioStreamTrackId: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? stream.id + ' ' + (audioTrack.mid || audioTrack.id) : '';
      },
      getVideoStreamTrackId: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? stream.id + ' ' + (videoTrack.mid || videoTrack.id) : '';
      },
      getAudioTrackId: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? audioTrack.mid || audioTrack.id : '';
      },
      getVideoTrackId: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? videoTrack.mid || videoTrack.id : '';
      },
      getStreamId: function (stream) {
        return stream && stream.id;
      },
      getTrackId: function (track) {
        return track && track.id;
      },
      hasDeviceNameOnTrackLabel: false,
      getAudioTrackLabel: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? audioTrack.label : '';
      },
      getVideoTrackLabel: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? videoTrack.label : '';
      },
      stopMediaStream: stopMediaStream,
      stopLocalVideoTrack: stopLocalVideoTrack,
      closePc: function (pc) {
        // Wait 500ms before closing the peer connection to give time for the stats to be collected
        window.setTimeout(function () {
          closePc(pc);
        }, 500);
      },
      getMediaSources: function (cb) {
        cb && splitDeviceInfos(cb);
      },
      clearMediaSourcesCache: function () {
        clearDeviceInfoCache();
      },
      getAudioOptions: function (config) {
        var audio = getDefaultAudioOptions(config);
        if (config && config.sourceId) {
          // Start out deviceId as mandatory, it will be moved to optional if it fails
          audio.deviceId = { exact: config.sourceId };
        }
        audio.advanced = audio.optional;
        delete audio.optional;
        return audio;
      },
      getVideoOptions: getDefaultVideoOptions,
      getDesktopOptions: function (streamId, screenConstraint) {
        screenConstraint = screenConstraint || {};
        var fr = screenConstraint.frameRate || {};
        fr.min = fr.min || DESKTOP_DEFAULT_MIN_FRAMERATE;
        fr.max = fr.max || DESKTOP_DEFAULT_MAX_FRAMERATE;

        if (navigator.mediaDevices.getDisplayMedia) {
          // As of v72.0.2, these constraints don't work
          return {
            video: {
              width: { max: DESKTOP_MAX_WIDTH },
              height: { max: DESKTOP_MAX_HEIGHT },
              framerate: { min: fr.min, max: fr.max }
            }
          };
        }

        return {
          mozMediaSource: streamId || 'screen', // Deprecated
          mediaSource: streamId || 'screen',
          width: { max: DESKTOP_MAX_WIDTH },
          height: { max: DESKTOP_MAX_HEIGHT },
          advanced: [{ frameRate: fr }]
        };
      },
      setEncodingParameters: function (rtpTransceiver, maxBitrate, degradationPreference, scaleResolutionDownBy) {
        var parameters = { encodings: [{}] };
        setEncodingParameters(rtpTransceiver, parameters, maxBitrate, degradationPreference, scaleResolutionDownBy);
      },
      audioOutputSelectionSupported: supportsAudioOutputSelection(),
      groupPeerConnectionsSupported: true,
      getMediaSourcesSupported: true,
      attachSinkIdToAudioElement: attachSinkIdToAudioElement,
      iceTimeoutSafetyFactor: 0.5, // Wait 50% more when there are multiple peer connections
      toggleAudio: toggleAudio,
      getNetmaskMap: function () {
        return {};
      }
    };
  }

  /////////////////////////////////////////////////////////////////////////////////
  // Safari
  /////////////////////////////////////////////////////////////////////////////////
  function createSafariStatsFn() {
    // Convert stats to Chrome's format (this can be removed once we move Chrome to standard promise based getStats)
    var SafariStat = function (stats, kind, mid) {
      // We consider the first 2 m-lines as the main channel and it should always be
      // in the QoS reports
      var _isMainChannel = mid === '0' || mid === '1';

      this.stats = stats;
      this.id = stats.id;
      switch (stats.type) {
        case 'inboundrtp':
        case 'inbound-rtp':
          if (!stats.isRemote) {
            this.type = 'ssrc';
            this.stats.mid = mid;
            if (kind === 'audio') {
              this.stats.audioOutputLevel = true;
              this.stats.googJitterReceived = stats.jitter * 1000;
            } else {
              this.stats.googFrameHeightReceived = true;
            }
            if (_isMainChannel) {
              this.stats.googTrackId = kind === 'audio' ? 'audio_main' : 'video_main';
            }
          }
          break;
        case 'outboundrtp':
        case 'outbound-rtp':
          if (!stats.isRemote) {
            this.type = 'ssrc';
            this.stats.mid = mid;
            if (kind === 'audio') {
              this.stats.audioInputLevel = true;
            } else {
              this.stats.googFrameHeightSent = true;
              this.stats.googFrameRateSent = stats.framerateMean;
              this.stats.googTransmitBitrate = stats.bitrateMean;
            }
            if (_isMainChannel) {
              this.stats.googTrackId = kind === 'audio' ? 'audio_main' : 'video_main';
            }
          }
          break;
        case 'candidatepair':
        case 'candidate-pair':
          this.type = 'googCandidatePair';
          if (stats.state === 'succeeded') {
            this.stats.googActiveConnection = 'true';
            this.stats.googChannelId = stats.transportId;
            this.stats.googRtt = stats.currentRoundTripTime * 1000;
          } else {
            this.discard = true;
          }
          break;
        case 'local-candidate':
          this.type = 'localcandidate';
          stats.portNumber = stats.port;
          break;
        case 'remote-candidate':
          this.type = 'remotecandidate';
          stats.portNumber = stats.port;
          break;
        default:
          this.type = stats.type;
          break;
      }
    };
    SafariStat.prototype.names = function () {
      return Object.keys(this.stats);
    };
    SafariStat.prototype.stat = function (type) {
      return this.stats[type];
    };

    var convertSafariStats = function (stats, kind, isMainChannel) {
      var items = [];
      var transportId;
      stats.forEach(function (item) {
        var converted = new SafariStat(item, kind, isMainChannel);
        if (!converted.discard) {
          if (converted.type === 'googCandidatePair') {
            transportId = converted.stats.transportId;
          }
          items.push(converted);
        }
      });
      if (transportId) {
        // Associate ssrc's with their candidate pairs through the transportId
        items.some(function (i) {
          if (i.type === 'ssrc') {
            i.stats.transportId = transportId;
            return true;
          }
          return false;
        });
      }
      return items;
    };

    /**
     * Getting stats from Firefox consists of:
     * 1- invoke getStats() for each RTCRtpSender and RTCRtpReceiver
     * 2- if a RTCRtpSender and a RTCRtpReceiver are in the same Transceiver, they are using the
     *    same channel, so share the channel info between the 2 of them (needs more testing)
     * 3- combine all stats into a single array
     */
    return function (pc) {
      var combinedStats = [];
      var rtpTransceivers = pc.getTransceivers();
      var promises = [];
      rtpTransceivers.forEach(function (t) {
        if (t.receiver && t.receiver.track) {
          // First get the receiver stats
          promises.push(
            new Promise(function (resolve) {
              t.receiver
                .getStats()
                .then(function (receiverStats) {
                  var receiverConverted = convertSafariStats(receiverStats, t.receiver.track.kind, t.mid);
                  if (t.sender && t.sender.track) {
                    // Get the sender stats, which we can't determine its transportId,
                    // so get it from the receiver stats and copy it to the sender's
                    t.sender
                      .getStats()
                      .then(function (stats) {
                        var senderConverted = convertSafariStats(stats, t.sender.track.kind, t.mid);
                        copyTransportIdFromReceiverStats(senderConverted, receiverConverted);
                        Array.prototype.push.apply(combinedStats, receiverConverted);
                        Array.prototype.push.apply(combinedStats, senderConverted);
                        resolve();
                      })
                      .catch(function (error) {
                        logger.debug('[WebRTCAdapter]: Error collecting stats for track: ' + t.sender.track.label + ' . Error: ', error);
                        Array.prototype.push.apply(combinedStats, receiverConverted);
                        resolve();
                      });
                  } else {
                    Array.prototype.push.apply(combinedStats, receiverConverted);
                    resolve();
                  }
                })
                .catch(function (error) {
                  logger.debug('[WebRTCAdapter]: Error collecting stats for track: ' + t.sender.track.label + ' . Error: ', error);
                  resolve();
                });
            })
          );
        } else if (t.sender && t.sender.track) {
          promises.push(
            new Promise(function (resolve) {
              t.sender
                .getStats()
                .catch(function (error) {
                  logger.debug('[WebRTCAdapter]: Error collecting stats for track: ' + t.sender.track.label + ' . Error: ', error);
                })
                .then(function (stats) {
                  if (stats) {
                    Array.prototype.push.apply(combinedStats, convertSafariStats(stats, t.sender.track.kind, t.mid));
                  }
                  resolve();
                });
            })
          );
        }
      });
      return Promise.all(promises).then(function () {
        return combinedStats;
      });
    };
  }

  function initForSafari() {
    logger.info('Setting WebRTC APIs for Safari');

    var processSafariGetStats = createSafariStatsFn();

    unifyPhone.WebRTCAdapter = {
      enabled: true,
      browser: 'safari',
      useNewConstraintSyntax: true,
      useReducedAudioConstraints: true,
      MediaStream: MediaStream,
      PeerConnection: function (config, constraints) {
        var pc = new RTCPeerConnection(config, constraints);
        pc.origCreateOffer = pc.createOffer;
        pc.createOffer = function (successCb, errorCb, offerConstraints) {
          var offerOptions;
          if (offerConstraints) {
            offerOptions = convertDeprecatedSdpConstraints(offerConstraints);
          }
          pc.origCreateOffer(offerOptions)
            .then(function (sdp) {
              successCb && successCb(sdp);
            })
            .catch(function (err) {
              errorCb && errorCb(err);
            });
        };
        pc.origCreateAnswer = pc.createAnswer;
        pc.createAnswer = function (successCb, errorCb, options) {
          var answerOptions = convertDeprecatedSdpConstraints(options);
          pc.origCreateAnswer(answerOptions)
            .then(function (sdp) {
              successCb && successCb(sdp);
            })
            .catch(function (err) {
              errorCb && errorCb(err);
            });
        };
        pc.origSetLocalDescription = pc.setLocalDescription;
        pc.setLocalDescription = function (rtcSdp, successCb, errorCb) {
          pc.origSetLocalDescription(rtcSdp)
            .then(function () {
              successCb && successCb();
            })
            .catch(function (err) {
              errorCb && errorCb(err);
            });
        };
        pc.origSetRemoteDescription = pc.setRemoteDescription;
        pc.setRemoteDescription = function (rtcSdp, successCb, errorCb) {
          pc.origSetRemoteDescription(rtcSdp)
            .then(function () {
              successCb && successCb();
            })
            .catch(function (err) {
              errorCb && errorCb(err);
            });
        };
        pc.origGetStats = pc.getStats;
        pc.getStats = function () {
          return processSafariGetStats(pc);
        };
        pc.createDTMFSender = function (audioTrack) {
          if (typeof pc.getSenders !== 'function') {
            return null;
          }
          var audioSender = pc.getSenders().find(function (sender) {
            return sender.track === audioTrack;
          });
          var dtmfSender = audioSender && audioSender.dtmf;
          if (!dtmfSender) {
            return null;
          }
          return dtmfSender;
        };

        return pc;
      },
      SessionDescription: RTCSessionDescription,
      IceCandidate: RTCIceCandidate,
      getUserMedia: function (constraints, successCallback, errorCallback) {
        timedGetUserMedia(navigator.mediaDevices.getUserMedia, navigator.mediaDevices, constraints, successCallback, errorCallback);
      },
      createObjectURL: URL.createObjectURL.bind(URL),
      getAudioStreamTrackId: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? stream.id + ' ' + audioTrack.id : '';
      },
      getVideoStreamTrackId: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? stream.id + ' ' + videoTrack.id : '';
      },
      getAudioTrackId: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? audioTrack.id : '';
      },
      getVideoTrackId: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? videoTrack.id : '';
      },
      getStreamId: function (stream) {
        return stream && stream.id;
      },
      getTrackId: function (track) {
        return track && track.id;
      },
      hasDeviceNameOnTrackLabel: true,
      getAudioTrackLabel: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? audioTrack.label : '';
      },
      getVideoTrackLabel: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? videoTrack.label : '';
      },
      stopMediaStream: stopMediaStream,
      stopLocalVideoTrack: stopLocalVideoTrack,
      closePc: closePc,
      getMediaSources: function (cb) {
        cb && splitDeviceInfos(cb);
      },
      clearMediaSourcesCache: function () {
        clearDeviceInfoCache();
      },
      getAudioOptions: function (config) {
        var audio = getReducedDefaultAudioOptions(config, true);
        if (config && config.sourceId) {
          // Start out deviceId as mandatory, it will be moved to optional if it fails
          audio.deviceId = { exact: config.sourceId };
        }
        return audio;
      },
      getVideoOptions: getDefaultVideoOptions,
      getDesktopOptions: function (streamId, screenConstraint) {
        screenConstraint = screenConstraint || {};
        var fr = screenConstraint.frameRate || {};
        return {
          mandatory: {
            chromeMediaSource: 'desktop',
            chromeMediaSourceId: streamId,
            maxWidth: DESKTOP_MAX_WIDTH,
            maxHeight: DESKTOP_MAX_HEIGHT
          },
          optional: [
            { googNoiseReduction: true },
            { maxFrameRate: fr.max || DESKTOP_DEFAULT_MAX_FRAMERATE },
            { minFrameRate: fr.min || DESKTOP_DEFAULT_MIN_FRAMERATE }
          ]
        };
      },
      setEncodingParameters: function (rtpTransceiver, maxBitrate, degradationPreference) {
        var parameter = rtpTransceiver.sender.getParameters();
        setEncodingParameters(rtpTransceiver, parameter, maxBitrate, degradationPreference);
      },
      audioOutputSelectionSupported: supportsAudioOutputSelection(),
      groupPeerConnectionsSupported: true,
      getMediaSourcesSupported: true,
      attachSinkIdToAudioElement: attachSinkIdToAudioElement,
      toggleAudio: toggleAudio,
      getNetmaskMap: function () {
        return {};
      }
    };
  }

  /////////////////////////////////////////////////////////////////////////////////
  // iOS
  /////////////////////////////////////////////////////////////////////////////////
  function initForIOS() {
    logger.info('Setting WebRTC APIs for iOS client');

    var RTCStatsReport = function (iosReport) {
      this.id = iosReport.id;
      this.type = iosReport.type;
      this.timestamp = new Date(iosReport.timestamp);

      var _stats = {};
      Object.getOwnPropertyNames(iosReport.values).forEach(function (name) {
        _stats[name] = iosReport.values[name];
      });

      this.names = function () {
        return Object.getOwnPropertyNames(_stats);
      };

      this.stat = function (name) {
        return _stats[name];
      };

      iosReport = null;
    };

    var RTCStatsResponse = function (iosReports) {
      var _reports = iosReports.map(function (iosReport) {
        return new RTCStatsReport(iosReport);
      });

      this.result = function () {
        return _reports;
      };
    };

    unifyPhone.WebRTCAdapter = {
      enabled: true,
      browser: 'ios',
      MediaStream: function (tracks) {
        return navigator.createMediaStreamWithTracks(tracks);
      },
      PeerConnection: function (configuration, constraints) {
        separateIceServers(configuration);
        var pc = RTCPeerConnection.createRTCPeerConnection(configuration, constraints);
        pc.origGetStats = pc.getStats;
        pc.getStats = function () {
          return new Promise(function (resolve) {
            pc.origGetStats(function (iosStats) {
              // Convert stats to the same format as the webclient
              resolve(new RTCStatsResponse(iosStats));
            });
          });
        };

        // Renaming the folowing methods / attributes due to WebRTC ObJ-C interface name conflicts.
        pc.addIceCandidate = pc.addIceCandidateJS;

        Object.defineProperties(pc, {
          localDescription: {
            get: function () {
              return pc.getLocalDescriptionJS();
            },
            enumerable: true,
            configurable: false
          },
          remoteDescription: {
            get: function () {
              return pc.getRemoteDescriptionJS();
            },
            enumerable: true,
            configurable: false
          },
          signalingState: {
            get: function () {
              return pc.getSignalingStateJS();
            },
            enumerable: true,
            configurable: false
          },
          iceConnectionState: {
            get: function () {
              return pc.getIceConnectionStateJS();
            },
            enumerable: true,
            configurable: false
          },
          iceGatheringState: {
            get: function () {
              return pc.getIceGatheringStateJS();
            },
            enumerable: true,
            configurable: false
          }
        });

        return pc;
      },
      SessionDescription: function (descriptionInitDict) {
        return RTCSessionDescription.createRTCSessionDescription(descriptionInitDict);
      },
      IceCandidate: function (candidateInitDict) {
        return {
          sdpMLineIndex: candidateInitDict.sdpMLineIndex,
          sdpMid: candidateInitDict.sdpMid,
          candidate: candidateInitDict.candidate
        };
      },
      getUserMedia: navigator.getUserMedia.bind(navigator),
      createObjectURL: URL.createObjectURL.bind(URL),
      getAudioStreamTrackId: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? stream.streamId + ' ' + audioTrack.trackId : '';
      },
      getVideoStreamTrackId: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? stream.streamId + ' ' + videoTrack.trackId : '';
      },
      getAudioTrackId: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? audioTrack.trackId : '';
      },
      getVideoTrackId: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? videoTrack.trackId : '';
      },
      getStreamId: function (stream) {
        return stream && stream.streamId;
      },
      getTrackId: function (track) {
        return track && track.trackId;
      },
      hasDeviceNameOnTrackLabel: false,
      getAudioTrackLabel: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? audioTrack.label : '';
      },
      getVideoTrackLabel: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? videoTrack.label : '';
      },
      stopMediaStream: stopMediaStream,
      stopLocalVideoTrack: stopLocalVideoTrack,
      closePc: closePc,
      getMediaSources: function (cb) {
        cb &&
          cb(
            [],
            [
              { id: '1', label: 'front' },
              { id: '2', label: 'back' }
            ]
          );
      },
      clearMediaSourcesCache: function () {},
      getAudioOptions: getDefaultAudioOptions,
      getVideoOptions: getDefaultVideoOptions,
      getDesktopOptions: function () {
        return {
          mandatory: {
            iOSMediaSource: 'desktop'
          }
        };
      },
      setEncodingParameters: function (rtpTransceiver, maxBitrate) {
        if (!rtpTransceiver || !rtpTransceiver.sender || !maxBitrate) {
          return;
        }
        rtpTransceiver.sender.maxBitrate = maxBitrate;
        logger.debug('[WebRTCAdapter]: Applied maxBitrate {' + maxBitrate + '} to RtpSender mid=' + rtpTransceiver.mid);
      },
      audioOutputSelectionSupported: false,
      groupPeerConnectionsSupported: false,
      getMediaSourcesSupported: false,
      attachSinkIdToAudioElement: attachSinkIdToAudioElement,
      toggleAudio: toggleAudio,
      getNetmaskMap: function () {
        return {};
      }
    };
  }

  /////////////////////////////////////////////////////////////////////////////////
  // Android
  /////////////////////////////////////////////////////////////////////////////////
  function initForAndroid() {
    logger.info('Setting WebRTC APIs for Android client');

    var _helperPc;

    var AndroidRTCStatsReport = function (androidReport) {
      this.id = androidReport.id;
      this.type = androidReport.type;
      this.timestamp = new Date(androidReport.timestamp);

      var _stats = {};
      Object.getOwnPropertyNames(androidReport.values).forEach(function (name) {
        var statValue = androidReport.values[name];
        if (statValue.name) {
          _stats[statValue.name] = statValue.value;
        }
      });

      this.names = function () {
        return Object.getOwnPropertyNames(_stats);
      };

      this.stat = function (name) {
        return _stats[name];
      };

      androidReport = null;
    };

    var AndroidRTCStatsResponse = function (androidReports) {
      var _reports = androidReports.map(function (androidReport) {
        return new AndroidRTCStatsReport(androidReport);
      });

      this.result = function () {
        return _reports;
      };
    };

    unifyPhone.WebRTCAdapter = {
      enabled: true,
      browser: 'android',
      MediaStream: function (tracks) {
        if (!_helperPc) {
          var config = { sdpSemantics: 'unified-plan' };
          _helperPc = RTCPeerConnectionUnified(config);
        }
        var stream = _helperPc.createEmptyStream();
        Array.isArray(tracks) ? stream.addTracks(tracks) : stream.addTrack(tracks);
        return stream;
      },
      PeerConnection: function (configuration, constraints) {
        separateIceServers(configuration);

        var pc = RTCPeerConnectionUnified(configuration, constraints);
        pc.getStats = function () {
          return new Promise(function (resolve) {
            pc.origGetStats(function (androidStats) {
              resolve(new AndroidRTCStatsResponse(androidStats.data));
            });
          });
        };

        return pc;
      },
      SessionDescription: function (descriptionInitDict) {
        return {
          type: descriptionInitDict.type,
          sdp: descriptionInitDict.sdp
        };
      },
      IceCandidate: function (candidateInitDict) {
        return {
          sdpMLineIndex: candidateInitDict.sdpMLineIndex,
          sdpMid: candidateInitDict.sdpMid,
          candidate: candidateInitDict.candidate
        };
      },
      getUserMedia: navigator.getUserMedia.bind(navigator),
      createObjectURL: function () {
        return 'dummy';
      },
      getAudioStreamTrackId: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? stream.id + ' ' + audioTrack.id : '';
      },
      getVideoStreamTrackId: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? stream.id + ' ' + videoTrack.id : '';
      },
      getAudioTrackId: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? audioTrack.id : '';
      },
      getVideoTrackId: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? videoTrack.id : '';
      },
      getStreamId: function (stream) {
        return stream && stream.id;
      },
      getTrackId: function (track) {
        return track && track.id;
      },
      hasDeviceNameOnTrackLabel: false,
      getAudioTrackLabel: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? audioTrack.label : '';
      },
      getVideoTrackLabel: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? videoTrack.label : '';
      },
      stopMediaStream: stopMediaStream,
      stopLocalVideoTrack: stopLocalVideoTrack,
      closePc: closePc,
      getMediaSources: function (cb) {
        cb && cb([], []);
      },
      clearMediaSourcesCache: function () {},
      getAudioOptions: getDefaultAudioOptions,
      getVideoOptions: getDefaultVideoOptions,
      getDesktopOptions: function () {
        return {
          mandatory: {
            androidMediaSource: 'screen'
          }
        };
      },
      setEncodingParameters: function () {},
      audioOutputSelectionSupported: false,
      groupPeerConnectionsSupported: false,
      getMediaSourcesSupported: false,
      attachSinkIdToAudioElement: attachSinkIdToAudioElement,
      toggleAudio: toggleAudio,
      getNetmaskMap: function () {
        return {};
      }
    };
  }

  /////////////////////////////////////////////////////////////////////////////////
  // iOS Cordova (cordova-plugin-iosrtc). Standards compliant
  /////////////////////////////////////////////////////////////////////////////////
  function initForCordova() {
    logger.info('Setting WebRTC APIs for Cordova iOS');

    cordova.plugins.iosrtc.RTCSessionDescription.prototype.toJSON = function () {
      return {
        type: this.type,
        sdp: this.sdp
      };
    };

    cordova.plugins.iosrtc.RTCIceCandidate.prototype.toJSON = function () {
      return {
        sdpMLineIndex: this.sdpMLineIndex,
        sdpMid: this.sdpMid,
        candidate: this.candidate
      };
    };

    unifyPhone.WebRTCAdapter = {
      enabled: true,
      browser: 'cordovaios',
      useNewConstraintSyntax: true,
      MediaStream: cordova.plugins.iosrtc.MediaStream,
      PeerConnection: function (config, constraints) {
        var pc = new cordova.plugins.iosrtc.RTCPeerConnection(config, constraints);
        pc.origCreateOffer = pc.createOffer;
        pc.createOffer = function (successCb, errorCb, offerConstraints) {
          var offerOptions = convertDeprecatedSdpConstraints(offerConstraints);
          pc.origCreateOffer(offerOptions)
            .then(function (sdp) {
              successCb && successCb(sdp);
            })
            .catch(function (err) {
              errorCb && errorCb(err);
            });
        };
        pc.origCreateAnswer = pc.createAnswer;
        pc.createAnswer = function (successCb, errorCb, options) {
          var answerOptions = convertDeprecatedSdpConstraints(options);
          pc.origCreateAnswer(answerOptions)
            .then(function (sdp) {
              successCb && successCb(sdp);
            })
            .catch(function (err) {
              errorCb && errorCb(err);
            });
        };
        pc.origSetLocalDescription = pc.setLocalDescription;
        pc.setLocalDescription = function (rtcSdp, successCb, errorCb) {
          pc.origSetLocalDescription(rtcSdp)
            .then(function () {
              successCb && successCb();
            })
            .catch(function (err) {
              errorCb && errorCb(err);
            });
        };
        pc.origSetRemoteDescription = pc.setRemoteDescription;
        pc.setRemoteDescription = function (rtcSdp, successCb, errorCb) {
          pc.origSetRemoteDescription(rtcSdp)
            .then(function () {
              successCb && successCb();
            })
            .catch(function (err) {
              errorCb && errorCb(err);
            });
        };

        return pc;
      },
      SessionDescription: cordova.plugins.iosrtc.RTCSessionDescription,
      IceCandidate: cordova.plugins.iosrtc.RTCIceCandidate,
      getUserMedia: function (constraints, successCallback, errorCallback) {
        cordova.plugins.iosrtc.getUserMedia(constraints).then(successCallback).catch(errorCallback);
      },
      createObjectURL: URL.createObjectURL.bind(URL),
      getAudioStreamTrackId: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? stream.id + ' ' + audioTrack.id : '';
      },
      getVideoStreamTrackId: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? stream.id + ' ' + videoTrack.id : '';
      },
      getAudioTrackId: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? audioTrack.id : '';
      },
      getVideoTrackId: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? videoTrack.id : '';
      },
      getStreamId: function (stream) {
        return stream && stream.id;
      },
      getTrackId: function (track) {
        return track && track.id;
      },
      hasDeviceNameOnTrackLabel: true,
      getAudioTrackLabel: function (stream) {
        var audioTrack = stream && stream.getAudioTracks()[0];
        return audioTrack ? audioTrack.label : '';
      },
      getVideoTrackLabel: function (stream) {
        var videoTrack = stream && stream.getVideoTracks()[0];
        return videoTrack ? videoTrack.label : '';
      },
      stopMediaStream: stopMediaStream,
      stopLocalVideoTrack: stopLocalVideoTrack,
      closePc: closePc,
      getMediaSources: function (cb) {
        cb && splitDeviceInfos(cb);
      },
      clearMediaSourcesCache: function () {
        clearDeviceInfoCache();
      },
      getAudioOptions: function (config) {
        var audio = getDefaultAudioOptions(config);
        if (config && config.sourceId) {
          // Cordova doesn't support exact (mandatory) deviceId
          audio.deviceId = config.sourceId;
        }
        return audio;
      },
      getVideoOptions: function (config) {
        var video = getDefaultVideoOptions(config);
        if (config && config.sourceId) {
          // Cordova doesn't support exact (mandatory) deviceId
          video.deviceId = config.sourceId;
        }
        return video;
      },
      getDesktopOptions: function (streamId, screenConstraint) {
        screenConstraint = screenConstraint || {};
        var fr = screenConstraint.frameRate || {};
        return {
          mandatory: {
            chromeMediaSource: 'desktop',
            chromeMediaSourceId: streamId,
            maxWidth: DESKTOP_MAX_WIDTH,
            maxHeight: DESKTOP_MAX_HEIGHT
          },
          optional: [
            { googNoiseReduction: true },
            { maxFrameRate: fr.max || DESKTOP_DEFAULT_MAX_FRAMERATE },
            { minFrameRate: fr.min || DESKTOP_DEFAULT_MIN_FRAMERATE }
          ]
        };
      },
      setEncodingParameters: function () {},
      audioOutputSelectionSupported: supportsAudioOutputSelection(),
      groupPeerConnectionsSupported: false,
      getMediaSourcesSupported: true,
      attachSinkIdToAudioElement: attachSinkIdToAudioElement,
      toggleAudio: toggleAudio,
      getNetmaskMap: function () {
        return {};
      }
    };
  }

  /////////////////////////////////////////////////////////////////////////////////
  // Initialization
  /////////////////////////////////////////////////////////////////////////////////
  function initWebRTCAdapter() {
    var browser = unifyPhone.Utils.getBrowserInfo();
    try {
      if (browser.chrome) {
        initForChrome();
      } else if (browser.firefox) {
        initForFirefox();
      } else if (browser.safari) {
        initForSafari();
      } else if (browser.ios) {
        initForIOS();
      } else if (browser.android) {
        initForAndroid();
      } else if (browser.cordovaios) {
        initForCordova();
      } else {
        logger.info('WebRTC is not supported');
      }
    } catch (e) {
      logger.error('Failed to initialize WebRTCAdapter. ', e);
    }

    if (!unifyPhone.WebRTCAdapter) {
      // Initialize with dummy object
      unifyPhone.WebRTCAdapter = {
        enabled: false,
        browser: '',
        MediaStream: function () {},
        PeerConnection: function () {},
        SessionDescription: function () {},
        IceCandidate: function () {},
        getUserMedia: function (constraints, successCallback, errorCallback) {
          errorCallback && errorCallback('Not supported');
        },
        createObjectURL: function () {
          return '';
        },
        getAudioStreamTrackId: function () {
          return '';
        },
        getVideoStreamTrackId: function () {
          return '';
        },
        getAudioTrackId: function () {
          return '';
        },
        getVideoTrackId: function () {
          return '';
        },
        getStreamId: function () {
          return '';
        },
        getTrackId: function () {
          return '';
        },
        hasDeviceNameOnTrackLabel: false,
        getAudioTrackLabel: function () {
          return '';
        },
        getVideoTrackLabel: function () {
          return '';
        },
        stopMediaStream: function () {},
        stopLocalVideoTrack: function () {},
        closePc: function () {},
        getMediaSources: function (cb) {
          cb && cb([], []);
        },
        clearMediaSourcesCache: function () {},
        getAudioOptions: function () {
          return false;
        },
        getVideoOptions: function () {
          return false;
        },
        getDesktopOptions: function () {
          return false;
        },
        setEncodingParameters: function () {},
        audioOutputSelectionSupported: false,
        groupPeerConnectionsSupported: false,
        getMediaSourcesSupported: false,
        attachSinkIdToAudioElement: function (audioElement, sinkId, cb) {
          cb && cb();
        },
        toggleAudio: function () {
          return false;
        },
        getNetmaskMap: function () {
          return {};
        },
        init: initWebRTCAdapter
      };
    }
  }

  initWebRTCAdapter();

  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.VideoResolutionLevel = VideoResolutionLevel;
  unifyPhone.Enums.RtpQualityLevel = RtpQualityLevel;

  unifyPhone.RtpStatsConfig = RtpStatsConfig;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global Promise*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var logger = unifyPhone.logger;
  var RealtimeMediaType = unifyPhone.Constants.RealtimeMediaType;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-lines-per-function
  function RtcPeerConnectionsUnified(pcConfig, options) {
    // NOSONAR
    options = options || {};

    // The following imports need to be defined inside RtcPeerConnections due to JS-SDK
    var WebRTCAdapter = unifyPhone.WebRTCAdapter;

    ///////////////////////////////////////////////////////////////////////////
    // Local variables
    ///////////////////////////////////////////////////////////////////////////
    var _that = this;

    var _pcConfig = Utils.shallowCopy(pcConfig);

    var _pc = null;

    var _numOfVideoReceivers = 0;

    var _localDesktop = {};

    var _screenRemoteSsrc = [];

    /////////////////////////////////////////////////////////////////////////////
    // WebRTC 1.0: Real-time Communication Between Browsers
    /////////////////////////////////////////////////////////////////////////////
    function init() {
      _pcConfig.sdpSemantics = 'unified-plan';
      _pcConfig.bundlePolicy = 'max-compat';

      // Number of video receivers that we need
      _numOfVideoReceivers =
        options.extraVideoChannels +
        // options.createDesktopPc is used for backward compatibility. Once we completely move to Unified
        // Plan, only options.extraVideoChannels will be used
        (options.createDesktopPc ? 1 : 0) +
        // By default we have 1 video receiver. Do not include it if only screenshare is allowed from remote.
        (options.onlyRemoteScreenShare ? 0 : 1);

      _pcConfig.iceCandidatePoolSize = 1 /*audio*/ + _numOfVideoReceivers;

      logger.debug('[RTCPeerConnectionsUnified]: Create RTCPeerConnection with config: ', _pcConfig);
      _pc = new WebRTCAdapter.PeerConnection(_pcConfig);
      registerEvtHandlers(_pc);
    }

    function registerEvtHandlers(pc) {
      pc.onnegotiationneeded = handleNegotiationNeeded.bind(null, pc);
      pc.onicecandidate = handleIceCandidate.bind(null, pc);
      pc.onsignalingstatechange = handleSignalingStateChange.bind(null, pc);
      pc.oniceconnectionstatechange = handleIceConnectionStateChange.bind(null, pc);
      pc.ontrack = handleOnTrack.bind(null, pc);
    }

    function unregisterEvtHandlers(pc) {
      pc.onnegotiationneeded = null;
      pc.onicecandidate = null;
      pc.onsignalingstatechange = null;
      pc.oniceconnectionstatechange = null;
      pc.ontrack = null;
    }

    function isPcCurrent(pc) {
      return !!pc && pc === _pc;
    }

    function handleNegotiationNeeded(pc, event) {
      logger.debug('[RTCPeerConnectionsUnified]: onnegotiationneeded');
      if (!isPcCurrent(pc)) {
        logger.debug('[RTCPeerConnectionsUnified]: Event is from old Peer Connection');
        return;
      }
      sendNegotiationNeeded(event);
    }

    function handleIceCandidate(pc, event) {
      logger.debug('[RTCPeerConnectionsUnified]: onicecandidate: iceGatheringState = ', pc.iceGatheringState);
      if (!isPcCurrent(pc)) {
        logger.debug('[RTCPeerConnectionsUnified]: Event is from old Peer Connection');
        return;
      }
      if (!event.candidate) {
        logger.debug('[RTCPeerConnectionsUnified]: End of candidates');
      }
      sendIceCandidate(event);
    }

    function handleSignalingStateChange(pc, event) {
      logger.debug('[RTCPeerConnectionsUnified]: onsignalingstatechange: signalingState = ', pc.signalingState);
      if (!isPcCurrent(pc)) {
        logger.debug('[RTCPeerConnectionsUnified]:  Event is from old Peer Connection');
        return;
      }
      sendSignalingStateChange(event);
    }

    function handleIceConnectionStateChange(pc, event) {
      logger.debug('[RTCPeerConnectionsUnified]: oniceconnectionstatechange: iceConnectionState = ', pc.iceConnectionState);
      if (!isPcCurrent(pc)) {
        logger.debug('[RTCPeerConnectionsUnified]: Event is from old Peer Connection.');
        return;
      }
      sendIceConnectionStateChange(event);
    }

    function handleOnTrack(pc, event) {
      logger.debug('[RTCPeerConnectionsUnified]: ontrack:', event.track && event.track.label);
      if (!isPcCurrent(pc)) {
        logger.debug('[RTCPeerConnectionsUnified]: Event is from old Peer Connection.');
        return;
      }

      if (event.track) {
        // Find the mid attribute related to this track
        // There must be a better way of doing this... but for now we just
        // find the transceiver that contains this track and get the 'mid' from
        // the transceiver object
        var mid = null;
        var txrl = _pc.getTransceivers();
        txrl.some(function (t) {
          if (t.receiver && compareTracks(t.receiver.track, event.track)) {
            mid = t.mid;
            return true;
          }
          return false;
        });
        if (mid) {
          var stream = new WebRTCAdapter.MediaStream([event.track]);
          stream.mid = mid;
          logger.debug('[RTCPeerConnectionsUnified]: ontrack: mid: ', mid);
          sendAddStream({
            stream: stream
          });
        } else {
          logger.error("[RTCPeerConnectionsUnified]: ontrack: Could not find the mid attribute. This remote track won't be played: ", event.track.label);
        }
      }
    }

    /////////////////////////////////////////////////////////////////////////////
    // Internal functions
    /////////////////////////////////////////////////////////////////////////////
    function addTransceiversOffer(sdpConstraints, tOptions) {
      tOptions = tOptions || {};

      if (tOptions.audioInactive) {
        _pc.addTransceiver('audio', {
          direction: 'inactive'
        });
      } else if (sdpConstraints.mandatory && sdpConstraints.mandatory.OfferToReceiveAudio) {
        // We should have 1 audio receiver, so add one if needed
        var found = _pc.getReceivers().some(function (r) {
          return (r.track && r.track.kind) === 'audio';
        });
        if (!found) {
          logger.debug('[RTCPeerConnectionsUnified]: create local SDP: Adding 1 audio receiver');
          _pc.addTransceiver('audio', {
            direction: 'recvonly'
          });
        }
      }

      var receiveVideo = (sdpConstraints.mandatory && sdpConstraints.mandatory.OfferToReceiveVideo) || options.onlyRemoteScreenShare;
      var currNumOfRcvrs = 0;
      var toBeAdded = 0;
      if (receiveVideo) {
        // Count current number of receivers and add more if needed
        _pc.getReceivers().forEach(function (r) {
          if ((r.track && r.track.kind) === 'video') {
            currNumOfRcvrs++;
          }
        });
        toBeAdded = _numOfVideoReceivers - currNumOfRcvrs;
        if (_localDesktop.track && toBeAdded > 0) {
          // We'll be creating the screen receiver later
          currNumOfRcvrs++;
          toBeAdded = _numOfVideoReceivers - currNumOfRcvrs;
        } else {
          // No desktop receiver should be created
          receiveVideo = false;
        }
        if (toBeAdded >= 0) {
          logger.debug('[RTCPeerConnectionsUnified]: create local SDP: Adding ' + toBeAdded + ' video receivers');
          for (var i = 0; i < toBeAdded; i++) {
            _pc.addTransceiver('video', {
              direction: 'recvonly'
            });
          }
        } else {
          logger.warn('[RTCPeerConnectionsUnified]: create local SDP: Could not calculate number of video receivers: ', toBeAdded);
        }
      }
      logger.debug('[RTCPeerConnectionsUnified]: Total number of video receivers: ', currNumOfRcvrs + toBeAdded);
      if (_localDesktop.track) {
        // Add desktop transceiver here, because we want it to be the last m-line
        // If only remote screen share is allowed, make sure direction is sendonly
        _pc.addTransceiver(_localDesktop.track, {
          direction: receiveVideo && !options.onlyRemoteScreenShare ? 'sendrecv' : 'sendonly'
        });
      }
    }

    function addTransceiversAnswer(sdpConstraints, tOptions) {
      tOptions = tOptions || {};

      var receiveVideo = sdpConstraints.mandatory && sdpConstraints.mandatory.OfferToReceiveVideo;
      if (!receiveVideo || tOptions.audioInactive) {
        // In answer scenarios, the receivers are already created by the peer connection
        // so we need to disable them if needed
        _pc.getTransceivers().forEach(function (t) {
          if (!t.stopped) {
            var kind = t.receiver && t.receiver.track && t.receiver.track.kind;
            if (!receiveVideo && kind === 'video') {
              if (t.sender && t.sender.track) {
                // We're sending video, set it to 'sendonly'
                // If the remote SDP is also 'sendonly', then the peer connection will
                // automatically change it to 'inactive'
                t.direction = 'sendonly';
              } else {
                // We're not sending video, set to 'inactive'
                t.direction = 'inactive';
              }
            } else if (tOptions.audioInactive && kind === 'audio') {
              t.direction = 'inactive';
            }
          }
        });
      }

      if (_localDesktop.track) {
        // Add desktop transceiver here, because we want it to be the last m-line
        _pc.addTrack(_localDesktop.track, _localDesktop.stream);
      }
    }

    function compareTracks(t1, t2) {
      if (!t1 || !t2) {
        return false;
      }
      return WebRTCAdapter.getTrackId(t1) === WebRTCAdapter.getTrackId(t2);
    }

    function updateRemoteDescription(remoteSdp) {
      if (remoteSdp && remoteSdp.type === 'pranswer') {
        return new WebRTCAdapter.SessionDescription({
          // 'pranswer' is not implemented yet, so convert it to 'answer'
          type: 'answer',
          sdp: remoteSdp.sdp
        });
      }
      return remoteSdp;
    }

    function isVideo(track) {
      return !!(track && track.kind === 'video');
    }

    function isMediaType(mediaType, mid, screenMid) {
      switch (mediaType) {
        case Constants.RealtimeMediaType.VIDEO:
          return mid !== screenMid;
        case Constants.RealtimeMediaType.DESKTOP_SHARING:
          return mid === screenMid;
        default:
          return false;
      }
    }

    /////////////////////////////////////////////////////////////////////////////
    // Event Senders
    /////////////////////////////////////////////////////////////////////////////
    function sendEvent(eventHandler, event) {
      if (typeof eventHandler === 'function') {
        try {
          eventHandler(event);
        } catch (e) {
          logger.error(e);
        }
      }
    }

    function sendNegotiationNeeded(event) {
      sendEvent(_that.onnegotiationneeded, event);
    }

    function sendIceCandidate(event) {
      sendEvent(_that.onicecandidate, event);
    }

    function sendSignalingStateChange(event) {
      sendEvent(_that.onsignalingstatechange, event);
    }

    function sendAddStream(event) {
      sendEvent(_that.onaddstream, event);
    }

    function sendIceConnectionStateChange(event) {
      sendEvent(_that.oniceconnectionstatechange, event);
    }

    /////////////////////////////////////////////////////////////////////////////
    // Event Handlers - same interfaces as peer connection
    /////////////////////////////////////////////////////////////////////////////
    this.onnegotiationneeded = null;
    this.onicecandidate = null;
    this.onsignalingstatechange = null;
    this.onaddstream = null;
    this.onremovestream = null;
    this.oniceconnectionstatechange = null;

    this.createOffer = function (successCb, errorCb, offerConstraints, tOptions) {
      function onOfferCreate(rtcSdp) {
        logger.debug('[RTCPeerConnectionsUnified]: createOffer succeeded');
        successCb(rtcSdp);
      }
      addTransceiversOffer(offerConstraints, tOptions);
      _pc.createOffer(onOfferCreate, errorCb);
    };

    this.createAnswer = function (successCb, errorCb, answerConstraints, tOptions) {
      function onAnswerCreate(rtcSdp) {
        logger.debug('[RTCPeerConnectionsUnified]: createAnswer succeeded');
        successCb(rtcSdp);
      }
      addTransceiversAnswer(answerConstraints, tOptions);
      _pc.createAnswer(onAnswerCreate, errorCb);
    };

    this.setMainMediaLines = function () {};

    this.setLocalDescription = function (rtcSdp, successCb, errorCb) {
      _pc.setLocalDescription(rtcSdp, successCb, errorCb);
    };

    this.setRemoteDescription = function (rtcSdp, successCb, errorCb) {
      _pc.setRemoteDescription(updateRemoteDescription(rtcSdp), successCb, errorCb);
    };

    this.removeStream = function (localStream) {
      if (!localStream) {
        return;
      }

      // removeTrack accepts RtcRtpSender only, so we need to find the local sender
      // that contains the track(s) from the localStream parameter
      var senders = _pc.getSenders();
      var tracks = localStream.getTracks();
      tracks.forEach(function (t) {
        logger.debug('[RTCPeerConnectionsUnified]: Attempting to remove local track: ', t.label);
        senders.some(function (s) {
          if (compareTracks(s.track, t)) {
            logger.debug('[RTCPeerConnectionsUnified]: Found sender, track removed');
            if (compareTracks(_localDesktop.track, t)) {
              logger.debug('[RTCPeerConnectionsUnified]: Set local desktop track to null');
              _localDesktop = {};
            }
            _pc.removeTrack(s);
            return true;
          }
          return false;
        });
      });
    };

    this.addStream = function (localStream, receiveVideo, trackConstraints) {
      if (!localStream) {
        return;
      }
      if (_pc.remoteDescription && _pc.remoteDescription.type) {
        // In local answer scenarios, the peer connection already created transceivers after
        // setting the remote description, so just add local tracks
        localStream.getTracks().forEach(function (t) {
          if (localStream.isScreen) {
            // This will be added in addTransceivers()
            _localDesktop.track = t;
            _localDesktop.stream = localStream;
          } else {
            _pc.addTrack(t, localStream);
          }
        });
      } else {
        // Add audio track first so it will be the first m-line (backwards compatibility)
        localStream.getAudioTracks().forEach(function (t) {
          _pc.addTransceiver(t, {
            direction: 'sendrecv'
          });
        });
        localStream.getVideoTracks().forEach(function (t) {
          var trackId = WebRTCAdapter.getTrackId(t);
          var constraints = trackId && trackConstraints[trackId];
          var trackDirection = (constraints && constraints.direction) || 'sendrecv';
          var direction = receiveVideo && (!options.onlyRemoteScreenShare || localStream.isScreen) ? trackDirection : 'sendonly';
          if (localStream.isScreen) {
            // Store the desktop track but don't add its transceiver yet as we want it
            // to be the last m-line
            _localDesktop.track = t;
            _localDesktop.stream = localStream;
          } else {
            _pc.addTransceiver(t, {
              direction: direction
            });
          }
          logger.debug(
            '[RTCPeerConnectionsUnified]: Added local ' + (localStream.isScreen ? 'screen' : 'video') + ' track=' + t.label + ' direction=' + direction
          );
        });
      }
    };

    this.replaceTrack = function (oldTrack, newTrack) {
      if (!oldTrack || !newTrack) {
        return Promise.reject('Missing parameter(s)');
      }
      return new Promise(function (resolve, reject) {
        var found = _pc.getSenders().some(function (s) {
          if (oldTrack === s.track) {
            var oldLabel = s.track.label;
            s.replaceTrack(newTrack)
              .then(function () {
                logger.debug('[RTCPeerConnectionsUnified]: Successfully replaced ' + oldLabel + ' track with ', newTrack.label);
                resolve();
              })
              .catch(function (err) {
                logger.error('[RTCPeerConnectionsUnified]: Error replacing ' + oldLabel + ' track with ' + newTrack.label + '. ', err);
                reject(err);
              });
            return true;
          }
          return false;
        });
        if (!found) {
          logger.error('[RTCPeerConnectionsUnified]: Could not find track to be replaced');
          reject('Could not find track to be replaced');
        }
      });
    };

    this.getLocalStreams = function () {
      var localStreams = [];
      _pc.getSenders().forEach(function (s) {
        if (s.track) {
          localStreams.push(new WebRTCAdapter.MediaStream([s.track]));
        }
      });
      return localStreams;
    };

    this.getRemoteStreams = function () {
      var streams = [];
      _pc.getTransceivers().forEach(function (t) {
        if (t.receiver && t.receiver.track) {
          var s = new WebRTCAdapter.MediaStream([t.receiver.track]);
          s.mid = t.mid;
          streams.push(s);
        }
      });
      return streams;
    };

    this.addIceCandidate = function (iceCandidate) {
      if (!iceCandidate) {
        _pc.addIceCandidate();
        return;
      }
      _pc.addIceCandidate(new WebRTCAdapter.IceCandidate(iceCandidate));
    };

    this.close = function () {
      _pc.close();
      unregisterEvtHandlers(_pc);
    };

    this.createDTMFSender = function (audioTrack) {
      return _pc.createDTMFSender(audioTrack);
    };

    this.getMediaId = function (track) {
      if (track) {
        var trx = _pc.getTransceivers();
        if (trx.length) {
          var found = trx.find(function (t) {
            return t.sender && compareTracks(t.sender.track, track);
          });
          if (found) {
            return found.mid;
          }
        }
      }
      return null;
    };

    this.getScreenShareMediaId = function () {
      var trx = _pc.getTransceivers();
      if (trx.length) {
        if (_localDesktop.track) {
          // We have a local desktop track in a RtcRtpSender object, so find which
          // transceiver this track belongs to and get its mid attribute
          var found = trx.find(function (t) {
            if (t.sender && t.sender.track) {
              return compareTracks(t.sender.track, _localDesktop.track);
            }
            return false;
          });
          if (found) {
            return found.mid;
          } else {
            logger.warn('[RTCPeerConnectionsUnified]: Could not find desktop sender obj. Last video connection will be selected for screen share');
          }
        }

        if (options.createDesktopPc) {
          // Find last video receiver and return its Transceiver's mid attribute
          for (var i = trx.length - 1; i >= 0; i--) {
            var trans = trx[i];
            if (trans.receiver && trans.receiver.track && trans.receiver.track.kind === 'video') {
              return trans.mid;
            }
          }
        }
      }
      return null;
    };

    // We can't bind the 2 methods because _pc is instantiated at runtime
    this.getStats = function () {
      return _pc.getStats();
    };

    this.setMaxBitrate = function (trackConstraints) {
      _pc.getTransceivers().forEach(function (transceiver) {
        var track = transceiver.sender && transceiver.sender.track;
        var trackId = WebRTCAdapter.getTrackId(track);
        var constraints = trackId && trackConstraints[trackId];
        constraints &&
          WebRTCAdapter.setEncodingParameters(transceiver, constraints.maxBitrate, constraints.degradationPreference, constraints.scaleResolutionDownBy);
      });
    };

    this.getAvailableVideoReceivers = function (mediaType) {
      var screenMid = _that.getScreenShareMediaId();
      var videoReceivers = [];
      _pc.getTransceivers().forEach(function (transceiver) {
        var mid = transceiver.mid;
        var receiver = transceiver.receiver;
        if (receiver && isVideo(receiver.track) && isMediaType(mediaType, mid, screenMid)) {
          videoReceivers.push({ id: mid });
        }
      });
      return videoReceivers;
    };

    this.getAvailableVideoSenders = function (trackConstraints) {
      var screenMid = _that.getScreenShareMediaId();
      var videoSenders = [];
      _pc.getTransceivers().forEach(function (transceiver) {
        var track = transceiver.sender && transceiver.sender.track;
        if (isVideo(track) && isMediaType(RealtimeMediaType.VIDEO, transceiver.mid, screenMid)) {
          var trackId = WebRTCAdapter.getTrackId(track);
          var constraints = trackId && trackConstraints[trackId];
          constraints &&
            videoSenders.push({
              mid: transceiver.mid,
              quality: constraints.quality,
              mediaType: RealtimeMediaType.VIDEO
            });
        }
      });
      logger.info('[RTCPeerConnectionsUnified] Video senders: ', videoSenders);
      return videoSenders;
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Properties
    ///////////////////////////////////////////////////////////////////////////////////////
    // Define the read-only properties to access the internal variables
    Object.defineProperties(this, {
      localDescription: {
        get: function () {
          return _pc.localDescription;
        },
        enumerable: true,
        configurable: false
      },
      remoteDescription: {
        get: function () {
          return _pc.remoteDescription;
        },
        enumerable: true,
        configurable: false
      },
      signalingState: {
        get: function () {
          return _pc.signalingState;
        },
        enumerable: true,
        configurable: false
      },
      iceConnectionState: {
        get: function () {
          return _pc.iceConnectionState;
        },
        enumerable: true,
        configurable: false
      },
      mainPeerConnection: {
        get: function () {
          return _pc;
        },
        enumerable: false,
        configurable: false
      },
      groupPeerConnections: {
        get: function () {
          return [];
        },
        enumerable: true,
        configurable: false
      },
      desktopPeerConnection: {
        get: function () {
          return options.createDesktopPc;
        },
        enumerable: true,
        configurable: false
      },
      desktopRemoteSsrc: {
        get: function () {
          return _screenRemoteSsrc;
        },
        set: function (value) {
          _screenRemoteSsrc = value || [];
        },
        enumerable: true,
        configurable: false
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialize the RTCPeerConnections instances
    ///////////////////////////////////////////////////////////////////////////////////////
    init();
  }

  // Exports
  unifyPhone.RtcPeerConnectionsUnified = RtcPeerConnectionsUnified;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Utils = unifyPhone.Utils;

  // Participant States for RTC Sessions
  var ParticipantState = {
    Active: { name: 'Active', ui: '', established: true, css: 'active-call' },
    Busy: { name: 'Busy', ui: 'res_ParticipantState_busy', established: false, css: 'busy' },
    Calling: { name: 'Calling', ui: '', established: false, css: 'calling' },
    Connecting: { name: 'Connecting', ui: '', established: false, css: 'connecting' }, // outgoing call at caller side: state just before being active
    ConnectionLost: { name: 'ConnectionLost', ui: 'res_ParticipantState_connectionLost', established: false, css: 'connection-lost' },
    Declined: { name: 'Declined', ui: 'res_ParticipantState_declined', established: false, css: 'declined' },
    Delivered: { name: 'Delivered', ui: '', established: false, css: 'outgoing' },
    Idle: { name: 'Idle', ui: '', established: false, css: 'idle' },
    Inactive: { name: 'Inactive', ui: '', established: false, css: 'inactive' }, // participant hasn't answered yet, but at least one did
    Initiated: { name: 'Initiated', ui: '', established: false, css: 'outgoing' },
    Joined: { name: 'Joined', ui: 'res_ParticipantState_joined', established: true, css: 'joined-call' },
    Muted: { name: 'Muted', ui: 'res_ParticipantState_muted', established: true, css: 'muted' },
    Offline: { name: 'Offline', ui: 'res_ParticipantState_offline', established: false, css: 'offline' },
    OffStage: { name: 'OffStage', ui: 'res_ParticipantState_leftStage', established: false, css: 'declined' },
    OnStage: { name: 'OnStage', ui: 'res_ParticipantState_enteredStage', established: true, css: 'joined-call' },
    Removed: { name: 'Removed', ui: 'res_ParticipantState_removed', established: false, css: 'declined' },
    Ringing: { name: 'Ringing', ui: '', established: false, css: 'incoming' },
    Terminated: { name: 'Terminated', ui: 'res_ParticipantState_left', established: false, css: 'declined' },
    Timeout: { name: 'Timeout', ui: 'res_ParticipantState_missed', established: false, css: 'busy' }
  };

  var ParticipantAction = {
    Drop: { name: 'Drop', type: 'hangup', icon: '', localize: 'res_Hangup' },
    Mute: { name: 'Mute', type: 'microphone', icon: 'unmuted', localize: 'res_Mute' },
    Unmute: { name: 'Unmute', type: 'microphone', icon: 'muted', localize: 'res_Unmute' },
    StartVideo: { name: 'StartVideo', type: 'video', icon: 'inactive', localize: 'res_StartVideo' },
    StopVideo: { name: 'StopVideo', type: 'video', icon: 'active', localize: 'res_StopVideo' }
  };

  // Participant object for RTC Sessions
  var RtcParticipant = {};

  // Create participant object from a given UserProfile
  RtcParticipant.createFromUser = function (user, pcState) {
    if (!user) {
      return null;
    }

    var participant = Object.create(user);
    participant.pcState = pcState || ParticipantState.Idle;
    participant.streamId = null;
    participant.screenStreamId = null;
    participant.videoStream = null;
    participant.mediaType = { audio: false, video: false, desktop: false };
    participant.muted = false;
    participant.activeSpeaker = false;
    participant.isActive = RtcParticipant.isActive.bind(null, participant);
    participant.actions = [];
    participant.screenSharePointerSupported = false;

    participant.hasSameMediaType = RtcParticipant.hasSameMediaType.bind(null, participant);
    participant.hasVideoStream = RtcParticipant.hasVideoStream.bind(null, participant);
    participant.setActions = RtcParticipant.setActions.bind(null, participant);
    participant.rtcSupportedFeatures = [];
    participant.flags = [];

    // The extended User object overrides the toJSON function to flatten the object
    // Lets's override it again here so we don't do it for RTC participants.
    participant.toJSON = function () {
      return this;
    };

    // JSON.stringify does not include protototype's properties by default, so copy
    // the userId field to the inherithed object. Do not copy other fields since they
    // may become out of synch.
    participant.userId = user.userId;
    // Also copy first and last names since they are needed for VDI
    participant.firstName = user.firstName;
    participant.lastName = user.lastName;

    // Video URLs are deprecated, but we need to save it if a client requested it,
    // otherwise we would create a different URL every time the client accesses the videoUrl property
    var videoStream = null;
    var videoUrl = '';

    Object.defineProperty(participant, 'videoStream', {
      get: function () {
        return videoStream;
      },
      set: function (stream) {
        videoStream = stream;
        videoUrl = '';
      },
      enumerable: true,
      configurable: false
    });

    if (Utils.isMobile()) {
      // The following property has been deprecated for Web/DA and must only be defined for mobile clients
      Object.defineProperty(participant, 'videoUrl', {
        get: function () {
          if (videoStream) {
            videoUrl = videoUrl || unifyPhone.WebRTCAdapter.createObjectURL(videoStream);
          } else {
            videoUrl = '';
          }
          return videoUrl;
        },
        enumerable: true,
        configurable: false
      });
    }

    participant.streams = {};

    return participant;
  };

  RtcParticipant.isActive = function (participant) {
    switch (participant.pcState) {
      case ParticipantState.Active:
      case ParticipantState.Joined:
      case ParticipantState.Muted:
      case ParticipantState.OnStage:
        return true;
    }
    return false;
  };

  RtcParticipant.hasSameMediaType = function (p1, p2) {
    return !!(
      p1 &&
      p2 &&
      p1.mediaType &&
      p2.mediaType &&
      p1.mediaType.audio === p2.mediaType.audio &&
      p1.mediaType.video === p2.mediaType.video &&
      p1.mediaType.desktop === p2.mediaType.desktop
    );
  };

  RtcParticipant.hasVideoStream = function (participant) {
    return !!(
      participant.videoStream &&
      (participant.streamId || participant.screenStreamId) &&
      (participant.mediaType.video || participant.mediaType.desktop)
    );
  };

  RtcParticipant.setActions = function (participant, conversation, localUser) {
    participant.actions = [];

    if (conversation && conversation.isTemporary) {
      // Invited guests are not allowed any actions
      return;
    }

    if (!participant.pcState.established) {
      if (participant.pcState === ParticipantState.Initiated) {
        // Ringing participant added to call.
        participant.actions.push(ParticipantAction.Drop);
      }
      return;
    }

    if (participant.isMeetingPointInvitee) {
      participant.actions.push(participant.mediaType.video ? ParticipantAction.StopVideo : ParticipantAction.StartVideo);
      participant.actions.push(participant.muted ? ParticipantAction.Unmute : ParticipantAction.Mute);
      participant.actions.push(ParticipantAction.Drop);
    } else {
      var isConvModerated = !conversation || conversation.isModerated;
      var amIModerator = conversation && conversation.userIsModerator(localUser);
      var isModerator = conversation && conversation.userIsModerator(participant);

      if (!participant.muted && (!isConvModerated || !isModerator || amIModerator)) {
        // In a moderated conversation, non-moderators can only mute non-moderators
        participant.actions.push(ParticipantAction.Mute);
      }
      if (!isConvModerated || amIModerator) {
        // In a moderated conversation, only moderators can drop other participants
        participant.actions.push(ParticipantAction.Drop);
      }
    }
  };

  // Exports
  unifyPhone.RtcParticipant = RtcParticipant;
  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.ParticipantState = ParticipantState;
  unifyPhone.Enums.ParticipantAction = ParticipantAction;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var logger = unifyPhone.logger;
  var Utils = unifyPhone.Utils;

  // Define common targets for all call types.
  // This object may be extended with additional targets as required by the application.
  var Targets = {
    WebRTC: { name: 'WebRTC', ui: 'res_ProductNameClient', css: 'unifyPhone' },
    Cell: { name: 'Cell', ui: 'res_AlternativeNumber', css: 'mobile' },
    Desk: { name: 'Desk', ui: 'res_Deskphone', css: 'desk' },
    VM: { name: 'VM', ui: 'res_Voicemail', css: 'vm' },
    Other: { name: 'Other', ui: 'res_CallAtOther', css: '' }
  };

  var CstaCallState = Object.freeze({
    Idle: { name: 'Idle', ui: '', established: false },

    // Outgoing call states
    Initiated: { name: 'Initiated', established: false },
    Connecting: { name: 'Connecting', established: false },
    Delivered: { name: 'Delivered', established: false },
    Busy: { name: 'Busy', established: false },
    Offered: { name: 'Offered', established: false },

    // Failed call states
    Failed: { name: 'Failed', established: false },
    TransferFailed: { name: 'TransferFailed', established: false },

    // Incoming call states
    Ringing: { name: 'Ringing', established: false },
    ExtendedRinging: { name: 'ExtendedRinging', established: false },

    // Established call states
    Active: { name: 'Active', established: true },
    Held: { name: 'Held', established: true },
    Holding: { name: 'Holding', established: true },
    HoldOnHold: { name: 'HoldOnHold', established: true },
    Parked: { name: 'Parked', established: true },
    Conference: { name: 'Conference', established: true },
    ConferenceHolding: { name: 'ConferenceHolding', established: true },

    // Call has been terminated
    Terminated: { name: 'Terminated', established: false }
  });

  var BusyHandlingOptions = Object.freeze({
    DefaultRouting: { name: 'DefaultRouting', ui: 'res_BusyHandling_DefaultRouting' },
    BusySignal: { name: 'BusySignal', ui: 'res_BusyHandling_BusySignal' },
    SendToAlternativeNumber: { name: 'SendToAlternativeNumber', ui: 'res_BusyHandling_AlternativeNumber' },
    SendToVM: { name: 'SendToVM', ui: 'res_BusyHandling_Voicemail' }
  });

  var RoutingOptions = Object.freeze({
    DefaultRouting: {
      name: 'DefaultRouting',
      ui: 'res_BusyHandling_DefaultRouting',
      desc: 'res_DefaultRoutingDescription'
    },
    DeskPhone: {
      name: 'DeskPhone',
      ui: 'res_Deskphone',
      desc: 'res_DeskphoneRoutingDescription'
    },
    UnifyPhoneClient: {
      name: 'UnifyPhoneClient',
      ui: 'res_ProductNameClient',
      desc: 'res_ProductNameClientRoutingDescription'
    },
    AlternativeNumber: {
      name: 'AlternativeNumber',
      ui: 'res_BusyHandling_AlternativeNumber',
      desc: 'res_AlternativeNumberRoutingDescription'
    },
    VM: {
      name: 'VoiceMail',
      ui: 'res_Voicemail',
      desc: 'res_VoicemailRoutingDescription'
    },
    Other: {
      name: 'Other',
      ui: 'res_Other',
      desc: 'res_OtherRoutingDescription'
    }
  });

  var JournalEntryTypes = Object.freeze({
    REGULAR: 'REGULAR',
    MISSED: 'MISSED'
  });

  var MissedReasonTypes = Object.freeze({
    DEFAULT: 'DEFAULT',
    DEST_NOT_OBTAINABLE: 'DEST_NOT_OBTAINABLE',
    DEST_OUT_OF_ORDER: 'DEST_OUT_OF_ORDER',
    REORDER_TONE: 'REORDER_TONE',
    BUSY: 'BUSY',
    CANCELLED: 'CANCELLED',
    DECLINED: 'DECLINED',
    TRANSFERRED: 'TRANSFERRED'
  });

  var TransferCallFailedCauses = Object.freeze({
    Busy: { name: 'Busy', ui: 'res_TransferCallFailedCauseBusy' },
    Unreachable: { name: 'Unreachable', ui: 'res_TransferCallFailedCauseUnreachable' },
    DND: { name: 'Dnd', ui: 'res_TransferCallFailedCauseDND' }
  });

  var RedirectionTypes = Object.freeze({
    CallForward: { name: 'callForward', ui: 'res_ForwardedFrom' },
    CallPickupNotification: { name: 'callPickupNotification', ui: 'res_CallPickupNotification' },
    CallPickedUp: { name: 'callPickedUp', ui: 'res_CallPickedUp' },
    Dss: { name: 'dss', ui: 'res_CallPickedUp' }
  });

  var AgentState = Object.freeze({
    Ready: 'ready',
    NotReady: 'notReady'
  });

  var CallForwardingTypes = Object.freeze({
    Immediate: {
      name: 'Immediate',
      label: 'res_CallForwarding',
      labelMiV5000: 'res_CallForwardingMiV5000',
      description: 'res_CallForwardingDescription',
      descriptionMiV5000: 'res_CallForwardingDescriptionMiV5000',
      message: 'res_CallForwardingEnabled'
    },
    VM: {
      name: 'VM',
      label: '',
      description: '',
      message: 'res_VoicemailForwarded'
    },
    NoAnswer: {
      name: 'NoAnswer',
      label: 'res_CallForwardingNoAnswer',
      labelMiV5000: 'res_CallForwardingNoAnswerMiV5000',
      description: 'res_CallForwardingNoAnswerDescription',
      descriptionMiV5000: 'res_CallForwardingNoAnswerDescriptionMiV5000',
      message: 'res_CallForwardingNoAnswerEnabled'
    },
    VMNoAnswer: {
      name: 'VMNoAnswer',
      label: '',
      description: '',
      message: 'res_VoicemailForwarded'
    },
    Busy: {
      name: 'Busy',
      label: 'res_CallForwardingBusy',
      labelMiV5000: 'res_CallForwardingBusyMiV5000',
      description: 'res_CallForwardingBusyDescription',
      descriptionMiV5000: 'res_CallForwardingBusyDescriptionMiV5000',
      message: 'res_CallForwardingBusyEnabled'
    },
    VMBusy: {
      name: 'VMBusy',
      label: '',
      description: '',
      message: 'res_VoicemailForwarded'
    }
  });

  function isGNF(number) {
    return Utils.GNF_PHONE_PATTERN.test(number);
  }

  // eslint-disable-next-line max-lines-per-function
  function AtcCallInfo() {
    // NOSONAR

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    //
    // These variables can only be accessed via getters/setters.
    // Critical variables that cannot be changed directly must be defined here.
    ///////////////////////////////////////////////////////////////////////////////////////
    var _transferCallFailedCause = null;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Properties
    ///////////////////////////////////////////////////////////////////////////////////////
    this.cstaConn = null;
    this.cstaState = CstaCallState.Idle;
    this.cstaReconnectId = {};
    this.position = Targets.Other;
    this.servicesPermitted = {};
    this.peerFQN = '';
    this.peerDn = '';
    this.peerName = null;
    this.missedReason = '';
    this.ignoreCall = false;
    this.originalPartnerDisplay = {};
    this.handoverInProgress = false;
    this.holdInProgress = false;
    this.retrieveInProgress = false;
    this.outgoingCallCampedOn = false;
    this.redirectingUser = {};
    this.masterParallelHgCall = false;
    this.queuedIncomingCall = false;
    this.recalledCall = false;
    this.osbizCallOnAlternativeNumber = false;

    this.setPeerName = function (name) {
      this.peerName = name || '';
    };

    this.setPartnerDisplay = function (display) {
      if (display) {
        this.peerFQN = display.fqn || '';
        this.peerDn = display.dn || display.fqn || '';
        this.peerName = display.name || this.peerDn;

        if (!this.peerFQN && this.peerDn.startsWith('+')) {
          this.peerFQN = this.peerDn;
        }
      }
    };

    this.getPartnerDisplay = function () {
      return {
        dn: this.peerDn,
        fqn: this.peerFQN,
        name: this.peerName
      };
    };

    this.setOriginalPartnerDisplay = function (display) {
      if (display && (Utils.isEmptyObject(this.originalPartnerDisplay) || (!isGNF(this.originalPartnerDisplay.fqn) && isGNF(display.fqn)))) {
        // Only update the original partner display if the stored fqn is not GNF and the input fqn is GNF
        this.originalPartnerDisplay = display;
      }
    };

    // CSTA Methods
    this.setCstaConnection = function (conn) {
      this.cstaConn = conn;
      if (this.isRemote) {
        // Update the Call ID as well
        this.callId = conn.cID;
      }
    };

    this.getCstaConnection = function () {
      return this.cstaConn;
    };

    this.setCstaCallId = function (id) {
      if (this.cstaConn) {
        this.cstaConn.cID = id;
      }
    };

    this.getCstaCallId = function () {
      return this.cstaConn && this.cstaConn.cID;
    };

    this.getCstaDeviceId = function () {
      return this.cstaConn && this.cstaConn.dID;
    };

    this.setCstaReconnectId = function (reconnectId) {
      this.cstaReconnectId = reconnectId;
    };

    this.getCstaReconnectId = function () {
      return this.cstaReconnectId;
    };

    this.setOsbizCallOnAlternativeNumber = function () {
      this.osbizCallOnAlternativeNumber = true;
    };

    this.isOsbizCallOnAlternativeNumber = function () {
      return this.osbizCallOnAlternativeNumber;
    };

    this.setServicesPermitted = function (svcsPermitted) {
      // If no new SP are reported in event then client must maintain old ones
      if (!svcsPermitted || Utils.isEmptyObject(svcsPermitted)) {
        logger.info('[AtcCallInfo]: Empty services permitted, keeping last values');
        return;
      }
      if (svcsPermitted.rSP && svcsPermitted.rSP.cCSs !== undefined) {
        this.servicesPermitted.rSP = svcsPermitted.rSP;
      }
      if (svcsPermitted.eSP !== undefined) {
        this.servicesPermitted.eSP = svcsPermitted.eSP;
      }
      logger.info('[AtcCallInfo]: Updated services permitted: ', this.servicesPermitted);
    };

    this.clearServicesPermitted = function () {
      this.servicesPermitted = {};
    };

    this.getServicesPermitted = function () {
      return this.servicesPermitted;
    };

    this.isHandoverAllowed = function () {
      return (this.isSilentHandoverAllowed() || this.isSeamlessHandoverAllowed()) && !this.isHandoverInProgress() && !this.isOsbizCallOnAlternativeNumber();
    };

    this.isSeamlessHandoverAllowed = function () {
      return !!(this.servicesPermitted.eSP && this.servicesPermitted.eSP.zseHo) || !!(this.servicesPermitted.rSP && this.servicesPermitted.rSP.cCSs.zseHo);
    };

    this.isSilentHandoverAllowed = function () {
      return !!(this.servicesPermitted.eSP && this.servicesPermitted.eSP.zsiHo);
    };

    this.isTransferAllowed = function () {
      return !!(this.servicesPermitted.rSP && this.servicesPermitted.rSP.cCSs.sST && !this.isHandoverInProgress());
    };

    this.isAlternateAllowed = function () {
      return !!(this.servicesPermitted.rSP && this.servicesPermitted.rSP.cCSs.alC);
    };

    this.isAnswerAllowed = function () {
      if (this.servicesPermitted.rSP) {
        return !!this.servicesPermitted.rSP.cCSs.anC;
      }
      return this.cstaState === CstaCallState.Ringing;
    };

    this.isHangupAllowed = function () {
      return !!(this.servicesPermitted.rSP && this.servicesPermitted.rSP.cCSs.clC);
    };

    this.isCallBackAllowed = function () {
      return !!(this.servicesPermitted.rSP && this.servicesPermitted.rSP.cCSs.cB);
    };

    this.isConsultAllowed = function () {
      return !!(this.servicesPermitted.rSP && this.servicesPermitted.rSP.cCSs.csC);
    };

    this.isDeflectAllowed = function () {
      if (this.cstaState === CstaCallState.Parked || this.cstaState === CstaCallState.Ringing) {
        return true;
      }
      return !!(this.servicesPermitted.rSP && this.servicesPermitted.rSP.cCSs.dCl);
    };

    this.isGenerateDigitsAllowed = function () {
      return !!(this.servicesPermitted.rSP && this.servicesPermitted.rSP.cAS.gD);
    };

    this.isHoldAllowed = function () {
      if (this.isHandoverInProgress()) {
        return false;
      }
      if (this.servicesPermitted.rSP) {
        return !!this.servicesPermitted.rSP.cCSs.hCl;
      }
      switch (this.cstaState) {
        case CstaCallState.Active:
        case CstaCallState.Conference:
        case CstaCallState.Held:
          return true;

        default:
          return false;
      }
    };

    this.isConferenceCall = function () {
      return this.cstaState === CstaCallState.Conference || this.cstaState === CstaCallState.ConferenceHolding;
    };

    this.isMakeCallAllowed = function () {
      return !!(this.servicesPermitted.rSP && this.servicesPermitted.rSP.cCSs.mCl);
    };

    this.isReconnectAllowed = function () {
      return !!(this.servicesPermitted.rSP && this.servicesPermitted.rSP.cCSs.rC);
    };

    this.isRetrieveAllowed = function () {
      if (this.isHandoverInProgress()) {
        return false;
      }
      if (this.servicesPermitted.rSP) {
        return this.servicesPermitted.rSP.cCSs.reC;
      }
      switch (this.cstaState) {
        case CstaCallState.ConferenceHolding:
        case CstaCallState.Holding:
        case CstaCallState.HoldOnHold:
          return true;

        default:
          return false;
      }
    };

    this.isTransferCallAllowed = function () {
      return !!(this.servicesPermitted.rSP && this.servicesPermitted.rSP.cCSs.tCl && !this.isHandoverInProgress());
    };

    this.isConferenceCallAllowed = function () {
      return !!(this.servicesPermitted.rSP && this.servicesPermitted.rSP.cCSs.coC);
    };

    this.getPosition = function () {
      return this.position;
    };

    this.setPosition = function (p) {
      if (!p || p === this.position) {
        return;
      }
      this.position = p;
      logger.debug('[AtcCallInfo]: Set call position to ', p.name);
    };

    this.isAtPosition = function (p) {
      return this.position === p;
    };

    this.getMissedReason = function () {
      return this.missedReason;
    };

    this.setMissedReason = function (missedReason) {
      if (!missedReason || missedReason === this.missedReason) {
        return;
      }
      this.missedReason = missedReason;
      logger.debug('[AtcCallInfo]: Set missedReason to ', missedReason);
    };

    this.isQueuedIncomingCall = function () {
      return this.queuedIncomingCall;
    };

    this.setQueuedIncomingCall = function (queued) {
      this.queuedIncomingCall = queued;
    };

    this.isRecalledCall = function () {
      return this.recalledCall;
    };

    this.setRecalledCall = function (recalled) {
      this.recalledCall = recalled;
    };

    this.getIgnoreCall = function () {
      return this.ignoreCall || this.cstaState === CstaCallState.Offered;
    };

    this.setIgnoreCall = function (flag) {
      this.ignoreCall = flag;
    };

    this.isHandoverInProgress = function () {
      return this.handoverInProgress;
    };

    this.setHandoverInProgress = function () {
      this.handoverInProgress = true;
    };

    this.clearHandoverInProgress = function () {
      this.handoverInProgress = false;
    };

    this.isHoldInProgress = function () {
      return this.holdInProgress;
    };

    this.setHoldInProgress = function () {
      this.holdInProgress = true;
    };

    this.clearHoldInProgress = function () {
      this.holdInProgress = false;
    };

    this.isRetrieveInProgress = function () {
      return this.retrieveInProgress;
    };

    this.setRetrieveInProgress = function () {
      this.retrieveInProgress = true;
    };

    this.clearRetrieveInProgress = function () {
      this.retrieveInProgress = false;
    };

    this.isOutgoingCallCampedOn = function () {
      return this.outgoingCallCampedOn;
    };

    this.setOutgoingCallCampedOn = function () {
      this.outgoingCallCampedOn = true;
    };

    this.clearOutgoingCallCampedOn = function () {
      this.outgoingCallCampedOn = false;
    };

    this.setTransferCallFailedCause = function (cause) {
      if (this.cstaState === CstaCallState.TransferFailed) {
        switch (cause) {
          case 'busy':
            _transferCallFailedCause = TransferCallFailedCauses.Busy;
            break;
          case 'doNotDisturb':
            _transferCallFailedCause = TransferCallFailedCauses.DND;
            break;
          default:
            _transferCallFailedCause = TransferCallFailedCauses.Unreachable;
            break;
        }
      }
    };

    this.getTransferCallFailedCause = function () {
      if (this.cstaState === CstaCallState.TransferFailed) {
        return _transferCallFailedCause || TransferCallFailedCauses.Unreachable;
      }
      return null;
    };

    this.setRedirectingUser = function (phoneNumber, fqNumber, displayName, userId, redirectionType) {
      // Used for telephony calls
      redirectionType = redirectionType || this.redirectingUser.redirectionType;
      if (!this.redirectingUser || !this.redirectingUser.fqNumber || this.redirectingUser.fqNumber === fqNumber) {
        this.redirectingUser = {
          userId: userId || '',
          displayName: displayName,
          phoneNumber: phoneNumber,
          fqNumber: fqNumber,
          redirectionType: redirectionType
        };
      }
    };

    this.getRedirectingUser = function () {
      return this.redirectingUser;
    };

    this.setRedirectionType = function (type) {
      this.redirectingUser.redirectionType = type;
    };

    this.getRedirectionType = function () {
      return this.redirectingUser && this.redirectingUser.redirectionType;
    };

    this.isForwarded = function () {
      return !!this.redirectingUser && this.redirectingUser.redirectionType === RedirectionTypes.CallForward;
    };

    this.isPickupNotification = function () {
      return !!this.redirectingUser && this.redirectingUser.redirectionType === RedirectionTypes.CallPickupNotification;
    };

    this.isPickedUp = function () {
      return !!this.redirectingUser && this.redirectingUser.redirectionType === RedirectionTypes.CallPickedUp;
    };
  }

  // Exports
  unifyPhone.AtcCallInfo = AtcCallInfo;
  unifyPhone.BusyHandlingOptions = BusyHandlingOptions;
  unifyPhone.RoutingOptions = RoutingOptions;

  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.AgentState = AgentState;
  unifyPhone.Enums.CallForwardingTypes = CallForwardingTypes;
  unifyPhone.Enums.CstaCallState = CstaCallState;
  unifyPhone.Enums.Targets = Targets;
  unifyPhone.Enums.JournalEntryTypes = JournalEntryTypes;
  unifyPhone.Enums.MissedReasonTypes = MissedReasonTypes;
  unifyPhone.Enums.RedirectionTypes = RedirectionTypes;
  unifyPhone.Enums.TransferCallFailedCauses = TransferCallFailedCauses;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var PhoneNumberFormatter = unifyPhone.PhoneNumberFormatter;
  var Utils = unifyPhone.Utils;

  var DefaultAvatars = {
    SUPPORT: {
      avatar: 'content/images/icon-general-support-avatar.png',
      avatarLarge: 'content/images/icon-general-support-avatar-XL.png',
      hasAvatarPicture: false
    },
    TELEPHONY: {
      avatar: 'content/images/icon-general-phonecall-avatar.png',
      avatarLarge: 'content/images/icon-general-phonecall-avatar-XL.png',
      hasAvatarPicture: false
    },
    TELEPHONY_HOLD: {
      avatar: 'content/images/icon-general-hold-avatar.png',
      avatarLarge: 'content/images/icon-general-hold-avatar-XL.png',
      hasAvatarPicture: false
    },
    EXTENSIONS: {
      avatar: 'content/images/icon-bot-extension-default-avatar.png',
      avatarLarge: 'content/images/icon-bot-extension-default-avatar-XL.png',
      hasAvatarPicture: true // Need to change it to false if/when we switch it to SVG
    },
    APPLICATIONS: {
      avatar: 'content/images/icon-general-default-avatar-app.png',
      avatarLarge: 'content/images/icon-general-default-avatar-app-XL.png',
      hasAvatarPicture: true // Need to change it to false if/when we switch it to SVG
    },
    SESSION_GUEST: {
      avatar: 'content/images/icon-general-sessionguest-avatar-XL.png',
      avatarLarge: 'content/images/icon-general-sessionguest-avatar-XL.png',
      hasAvatarPicture: false
    },
    DIALIN: {
      avatar: 'content/images/icon-general-phonecall-dialin-avatar.png',
      avatarLarge: 'content/images/icon-general-phonecall-dialin-avatar-XL.png',
      hasAvatarPicture: false
    },
    INCOMING_WEBHOOK: {
      avatar: 'content/images/icon-webhook-default-avatar-XL.png',
      avatarLarge: 'content/images/icon-webhook-default-avatar-XL.png',
      hasAvatarPicture: true // Need to change it to false if/when we switch it to SVG
    }
  };
  // Default avatars are colorful. The default gray avatar is used when the downloading of real profile image fails
  var COLORS = ['blue', 'green', 'orange', 'yellow'];

  /*
   * Client Idle state
   * @readonly
   * @enum {String}
   * @property Idle - Client is idle
   * @property Active - Client is active
   */
  var IdleState = Object.freeze({
    Idle: 'Idle',
    Active: 'Active'
  });

  // Define the object
  var UserProfile = {};

  var DEFAULT_AVATAR = 'content/images/icon-general-default-avatar';
  var TELEPHONY_AVATAR = 'content/images/icon-general-phonecall';
  var TELEPHONY_HOLD_AVATAR = 'content/images/icon-general-hold';

  UserProfile.NOPIC = DEFAULT_AVATAR + '.png';
  UserProfile.NOPIC_LARGE = DEFAULT_AVATAR + '-XL.png';
  UserProfile.NOPIC_BOT = DefaultAvatars.EXTENSIONS.avatar;
  UserProfile.NOPIC_BOT_LARGE = DefaultAvatars.EXTENSIONS.avatarLarge;
  UserProfile.NOPIC_APP = DefaultAvatars.APPLICATIONS.avatar;
  UserProfile.NOPIC_APP_LARGE = DefaultAvatars.APPLICATIONS.avatarLarge;
  UserProfile.NOPIC_INCOMING_WEBHOOK = DefaultAvatars.INCOMING_WEBHOOK.avatar;
  UserProfile.NOPIC_INCOMING_WEBHOOK_LARGE = DefaultAvatars.INCOMING_WEBHOOK.avatarLarge;

  UserProfile.NO_NAME = '__UnDeFiNeD__';

  // Extend object via prototypical inheritance
  UserProfile.extend = function (obj) {
    if (!obj) {
      return null;
    }
    if (obj.isExtended) {
      // The given object is already an extended user profile
      return obj;
    }

    // userPresenceState will be removed from the base object. Save it and then add it to extended object.
    var userPresenceState = UserProfile.normalizeUserPresenceState(obj.userId, obj.userPresenceState);

    normalizeBaseObject(obj);

    // Create a derived object to be extended and returned.
    var user = Object.create(obj);
    user.isExtended = true;

    // Add the userPresenceState to the extended object
    user.userPresenceState = userPresenceState;

    // Now set the extended properties that need to be updated if
    // the base object (i.e. the server data) changes.
    setExtendedProperties(user);
    UserProfile.setAvatars(user);

    user.toJSON = function () {
      return Utils.flattenObj(this);
    };

    return user;
  };

  UserProfile.hasAvatar = function (user) {
    return !!(user && (user.extAvatarUri || (user.smallImageUri && user.largeImageUri)));
  };

  UserProfile.hasTelephonyAvatar = function (user) {
    return !!(user && user.avatar && (user.avatar.startsWith(TELEPHONY_AVATAR) || user.avatar.startsWith(TELEPHONY_HOLD_AVATAR)));
  };

  UserProfile.hasPermission = function (user, permission) {
    if (!user || !user.accounts || !permission) {
      return false;
    }
    var activeAccount = user.accounts[0];
    var permissions = activeAccount && activeAccount.permissions;
    var multi = permission[permission.length - 1] === '_'; // For multi permission check, example: 'EVENT_ORGANIZER_'...

    return (
      !!permissions &&
      permissions.some(function (permissionObject) {
        return multi ? permissionObject.systemPermission.startsWith(permission) : permissionObject.systemPermission === permission;
      })
    );
  };

  // Updates an existing UserProfile extended object with new received data from
  // the Client API.
  UserProfile.update = function (user, obj, skipNormalization) {
    if (!user || !obj) {
      return;
    }

    if (!user.isExtended) {
      user = UserProfile.extend(user);
    }
    if (obj.isExtended) {
      obj = Utils.getBaseObject(obj, true);
    } else if (!skipNormalization) {
      normalizeBaseObject(obj);
      obj.updatedTimeStamp = obj.updatedTimeStamp || Date.now();
    }

    for (var key in obj) {
      if (key !== 'userPresenceState' && obj.hasOwnProperty(key)) {
        user[key] = obj[key];
      }
    }
    Utils.syncBaseObject(user);

    setExtendedProperties(user);
    UserProfile.setAvatars(user);
  };

  UserProfile.contains = function (users, userId) {
    return users.some(function (user) {
      return user.userId === userId;
    });
  };

  UserProfile.getAvatarColor = function (user) {
    if (!user || !user.userId) {
      return null;
    }
    return getColor(user.userId);
  };

  UserProfile.setAvatars = function (user) {
    if (!user) {
      return;
    }
    if (!user.hasTelephonyRole) {
      user.avatarColor = UserProfile.getAvatarColor(user);
    }

    if (user.isPurged) {
      user.avatar = UnifyPhone.UserProfile.NOPIC;
      user.avatarLarge = UnifyPhone.UserProfile.NOPIC_LARGE;
      user.hasAvatarPicture = false;
      return;
    }

    if (user.extAvatarUri) {
      user.avatar = user.extAvatarUri;
      user.avatarLarge = user.extAvatarUri;
      user.hasAvatarPicture = true;
      return;
    }

    if (user.smallImageUri && user.largeImageUri) {
      user.avatar = unifyPhone.__domain + '/api' + unifyPhone.__path + '/fileApi/avatar/small/' + user.smallImageUri;
      user.avatarLarge = unifyPhone.__domain + '/api' + unifyPhone.__path + '/fileApi/avatar/large/' + user.largeImageUri;
      user.hasAvatarPicture = true;
      return;
    }
    if (user.hasBotRole) {
      Object.assign(user, DefaultAvatars.EXTENSIONS);
      return;
    }
    if (user.participantType === Constants.RTCParticipantType.TELEPHONY) {
      Object.assign(user, DefaultAvatars.DIALIN);
      return;
    }
    if (user.hasTelephonyRole) {
      Object.assign(user, DefaultAvatars.TELEPHONY);
      return;
    }

    var color = getColor(user.userId);

    // User does not have a profile picture. Let's assign a random one.
    user.avatar = DEFAULT_AVATAR + '-' + color + '.png';
    user.avatarLarge = DEFAULT_AVATAR + '-' + color + '-XL.png';
    user.hasAvatarPicture = false;
  };

  UserProfile.normalizeExchangeData = function (exchangeData) {
    if (!exchangeData) {
      return;
    }
    if (exchangeData.companyName) {
      exchangeData.company = exchangeData.companyName;
      delete exchangeData.companyName;
    }
    if (exchangeData.title) {
      exchangeData.jobTitle = exchangeData.title;
      delete exchangeData.title;
    }

    if (exchangeData.phoneNumbers) {
      exchangeData.phoneNumbers = exchangeData.phoneNumbers.filter(function (phoneNumber) {
        var number = phoneNumber.number || phoneNumber.phoneNumber;
        if (!number || !number.trim()) {
          return false;
        }
        if (!Utils.PHONE_DIAL_PATTERN.test(number) && !Utils.PHONE_WITH_EXTENSION_PATTERN.test(number)) {
          return false;
        }
        phoneNumber.number = PhoneNumberFormatter.format(number);
        if (phoneNumber.phoneNumber) {
          phoneNumber.phoneNumber = phoneNumber.number;
        }

        if (!phoneNumber.type || !Constants.PhoneNumberType[phoneNumber.type]) {
          phoneNumber.type = Constants.PhoneNumberType.OTHER;
        }

        return true;
      });
    }
  };

  UserProfile.mergeWithExchangeData = function (user, exchangeData) {
    if (user && exchangeData) {
      UserProfile.normalizeExchangeData(exchangeData);

      // Remove externally managed fields
      user.externallyManagedFields.forEach(function (field) {
        delete exchangeData[field];
      });

      if (exchangeData.phoneNumbers) {
        exchangeData.phoneNumbers = exchangeData.phoneNumbers.filter(function (phoneNumber) {
          return !(
            user.phoneNumbers &&
            user.phoneNumbers.some(function (p) {
              return p.phoneNumber === phoneNumber.number;
            })
          );
        });
      }

      if (exchangeData.emailAddresses) {
        exchangeData.emailAddresses = exchangeData.emailAddresses.filter(function (email) {
          var address = email.address;
          if (Utils.compareStrings(user.emailAddress, address)) {
            return false;
          }
          return !(
            user.emailAddresses &&
            user.emailAddresses.some(function (e) {
              return Utils.compareStrings(e.address, address);
            })
          );
        });
      }

      // Add the exchange data to the base object so it can be persisted in the local DB
      delete user.exchangeData;
      var baseObj = Utils.getBaseObject(user);
      baseObj.exchangeData = exchangeData;
    }
  };

  UserProfile.isPresenceAvailable = function (user) {
    return !!(
      user &&
      user.userPresenceState &&
      user.userPresenceState.showPresence &&
      (user.userPresenceState.state === Constants.PresenceState.AVAILABLE || user.userPresenceState.state === Constants.PresenceState.BUSY)
    );
  };

  UserProfile.isPresenceOffline = function (user) {
    return !!(user && user.userPresenceState && user.userPresenceState.showPresence && user.userPresenceState.state === Constants.PresenceState.OFFLINE);
  };

  /**
   * Filter out a users list based on their online status
   *
   * @param {Array<object>} users An Array of users filter
   */
  UserProfile.getAvailableParticipants = function (users) {
    var results = [];
    if (users && users.length > 0) {
      results = users.filter(function (user) {
        return UserProfile.isPresenceAvailable(user);
      });
    }
    return results;
  };

  //Finds common members between 2 user object arrays of a conversation
  UserProfile.findCommonMembers = function (previousMembers, newMembers) {
    var intersection = [];
    previousMembers &&
      newMembers &&
      previousMembers.length > 0 &&
      newMembers.length > 0 &&
      newMembers.forEach(function (newMember) {
        previousMembers.some(function (prevMember) {
          if (prevMember.user.userId === newMember.userId) {
            intersection.push(prevMember);
            return true;
          }
          return false;
        });
      });
    return intersection;
  };

  UserProfile.clearUserPresenceState = function (user) {
    if (user) {
      user.userPresenceState = UserProfile.normalizeUserPresenceState(user.userId);
    }
  };

  UserProfile.updateUserPresenceState = function (user, presenceState) {
    if (!user || !presenceState) {
      return false;
    }
    presenceState = UserProfile.normalizeUserPresenceState(user.userId, presenceState);

    var currPropertyNames = Object.getOwnPropertyNames(user.userPresenceState);
    var newPropertyNames = Object.getOwnPropertyNames(presenceState);

    // First check if there are any changes
    if (newPropertyNames.length === currPropertyNames.length) {
      var hasChanges = newPropertyNames.some(function (name) {
        return presenceState[name] !== user.userPresenceState[name];
      });
      if (!hasChanges) {
        return false;
      }
    }

    // We want to update the existing UserPresenceState object
    // First clear the current object
    currPropertyNames.forEach(function (name) {
      delete user.userPresenceState[name];
    });

    // Now copy all the fields from the new object
    newPropertyNames.forEach(function (name) {
      user.userPresenceState[name] = presenceState[name];
    });

    return true;
  };

  UserProfile.normalizeUserPresenceState = function (userId, userPresenceState) {
    if (userPresenceState) {
      // We are assuming that the mandatory fields (userId and state) have been populated
      userPresenceState.isOptedOut = !!userPresenceState.isOptedOut;
      userPresenceState.showPresence = !userPresenceState.isOptedOut;
      userPresenceState.statusMessage = userPresenceState.statusMessage || '';
      userPresenceState.isSystemDefined = !!userPresenceState.isSystemDefined;
    } else {
      userPresenceState = {
        userId: userId || '',
        state: Constants.PresenceState.OFFLINE,
        showPresence: false,
        statusMessage: ''
      };
    }
    return userPresenceState;
  };

  UserProfile.encodeNoName = function (userModel) {
    if (!userModel.firstName) {
      userModel.firstName = UserProfile.NO_NAME;
    }
    if (!userModel.lastName) {
      userModel.lastName = UserProfile.NO_NAME;
    }
  };

  UserProfile.decodeNoName = function (userModel) {
    if (userModel.firstName === UserProfile.NO_NAME) {
      userModel.firstName = '';
    }
    if (userModel.lastName === UserProfile.NO_NAME) {
      userModel.lastName = '';
    }
  };

  UserProfile.isExternal = function (user, conversation) {
    if (!user || user.noData || user.notFound || user.hasSupportRole || !UserProfile.localUser) {
      // There is no user OR we don't have the user information OR this is a support user.
      // In any of these case the user must not be marked as guest.
      return false;
    }

    if (UserProfile.localUser.hasSupportRole) {
      // If the local user is a suport user then don't show users from different tenants as guest
      return false;
    }

    if (conversation && conversation.isLocalUserExternal) {
      // I am a guest in this conversation, don't show anybody as guests
      return false;
    }

    return user.isExternal;
  };

  UserProfile.syncTelephonyConfig = function (user) {
    var account = user && user.accounts && user.accounts[0];
    var telConfig = account && account.telephonyConfiguration;
    if (telConfig) {
      telConfig.phoneNumber = PhoneNumberFormatter.format(telConfig.phoneNumber) || '';
      telConfig.callerId = PhoneNumberFormatter.format(telConfig.callerId) || '';
      telConfig.reroutingPhoneNumber = PhoneNumberFormatter.format(telConfig.reroutingPhoneNumber) || '';

      user.reroutingPhoneNumber = telConfig.reroutingPhoneNumber;
      user.ondSipAuthenticationHash = telConfig.ondSipAuthenticationHash;
      user.onsSipAuthenticationHash = telConfig.onsSipAuthenticationHash;
      user.associatedTelephonyUserID = telConfig.associatedTelephonyUserID;
      user.associatedTelephonyUserType = telConfig.associatedTelephonyUserType;
      user.associatedTelephonyTrunkGroupId = telConfig.associatedTelephonyTrunkGroupId;

      // Remove duplicate and invalid alternative numbers
      var numbers = (telConfig.previousAlternativeDevice || []).map(Utils.cleanPhoneNumber);
      var numbersHash = numbers.reduce(function (hash, number) {
        if (number.startsWith('+') && number !== '+') {
          hash[number] = true;
        }
        return hash;
      }, {});

      user.previousAlternativeNumbers = Object.keys(numbersHash);
      if (user.reroutingPhoneNumber) {
        var cleanReroutingNumber = Utils.cleanPhoneNumber(user.reroutingPhoneNumber);
        if (!user.previousAlternativeNumbers.includes(cleanReroutingNumber)) {
          user.previousAlternativeNumbers.unshift(cleanReroutingNumber);
        }
      }

      if (user.associatedTelephonyUserID) {
        var userTelephonyType = user.associatedTelephonyUserType;
        // Relevant when UC is enabled for an OSBiz client. Used to adjust some client behaviour for OSBiz
        user.isOsBizCTIEnabled = userTelephonyType === Constants.GtcTrunkType.OSBIZ_TRUNK_TYPE;
        // Flag for MiV5000
        user.isMiV5000 = userTelephonyType === Constants.GtcTrunkType.MiV5000_TRUNK_TYPE;
        user.isMiVB = userTelephonyType === Constants.GtcTrunkType.MiVB_TRUNK_TYPE;
        user.isATC = user.isOsBizCTIEnabled || user.isMiV5000 || user.isMiVB || userTelephonyType === Constants.GtcTrunkType.ATC_TRUNK_TYPE;
        user.isSTC = userTelephonyType === Constants.GtcTrunkType.SUB_TRUNK_TYPE;

        if (user.isATC || user.isSTC) {
          // Set associatedGTCUserId field for backwards compatibility until mobile clients remove all references to it
          user.associatedGTCUserId = user.associatedTelephonyUserID;
        }
      } else {
        user.isOsBizCTIEnabled = false;
        user.isATC = false;
        user.isSTC = false;
        user.isMiV5000 = false;
      }
    } else {
      delete user.associatedGTCUserId;
      delete user.reroutingPhoneNumber;
      delete user.ondSipAuthenticationHash;
      delete user.onsSipAuthenticationHash;
      delete user.associatedTelephonyUserID;
      delete user.associatedTelephonyUserType;
      delete user.associatedTelephonyTrunkGroupId;
      delete user.isATC;
      delete user.isSTC;
    }
    user.conferenceDialOutEnabled = user.conferenceDialOutEnabled === undefined ? true : user.conferenceDialOutEnabled;
    // initialize conferenceDialOutAvailable with same state as conferenceDialOutEnabled, this property will get updated for
    // ATC users by change of telephony data
    user.conferenceDialOutAvailable = user.conferenceDialOutEnabled;
  };

  UserProfile.updateAlternativeNumbers = function (user, data) {
    var numbers = (data.previousAlternativeNumbers || []).map(Utils.cleanPhoneNumber);
    var numbersHash = numbers.reduce(function (hash, number) {
      if (number.startsWith('+') && number !== '+') {
        hash[number] = true;
      }
      return hash;
    }, {});

    user.previousAlternativeNumbers = Object.keys(numbersHash);
  };

  UserProfile.filterUserByName = function (user, query) {
    if (!user) {
      return false;
    }
    if (!query) {
      // If query is empty, consider it a match
      return true;
    }
    return (
      !user.isSuspended &&
      !user.isDeleted &&
      !user.isPurged &&
      (localeCompare(user.firstName, query) || localeCompare(user.lastName, query) || localeCompare(user.displayName, query))
    );
  };

  UserProfile.filterUsersByName = function (users, query) {
    if (!Array.isArray(users) || users.length === 0) {
      return [];
    }
    if (!query) {
      // If query is empty string return entire user list
      return query === '' ? users : [];
    }
    return users.filter(function (user) {
      return UserProfile.filterUserByName(user, query);
    });
  };

  UserProfile.localUser = null;

  ///////////////////////////////////////////////////////////////////////////
  // Helper functions for UserProfile
  ///////////////////////////////////////////////////////////////////////////
  function getColor(userId) {
    var sum = 0;
    if (userId) {
      for (var idx = 0; idx < userId.length; idx++) {
        sum += userId.charCodeAt(idx);
      }
    }
    return COLORS[sum % COLORS.length];
  }

  // Optional User object fields
  var STRING_FIELDS = [
    'userId',
    'emailAddress',
    'lastName',
    'firstName',
    'displayName',
    'location',
    'largeImageUri',
    'smallImageUri',
    'extAvatarUri',
    'userType',
    'userState',
    'locale',
    'jobTitle',
    'company',
    'department',
    'federationEmailAddress'
  ];
  var BOOLEAN_FIELDS = ['ssoLogin'];
  var NUM_FIELDS = ['updatedTimeStamp'];
  var ARRAY_FIELDS = ['phoneNumbers', 'emailAddresses', 'externallyManagedFields', 'askMeAbouts', 'expertises', 'interests'];

  function normalizeBaseObject(obj) {
    // Handle backwards compatibility for isExternallyManaged field
    if (obj.isExternallyManaged && !obj.externallyManagedFields) {
      // We must be connected to an older backend. Set externallyManagedFields with the default values.
      obj.externallyManagedFields = ['emailAddress', 'lastName', 'firstName'];
    }

    // Remove deprecated fields from base object
    delete obj.tenantId;
    delete obj.roles;
    delete obj.accountTemplateType;
    delete obj.associatedGTCUserId;
    delete obj.reroutingPhoneNumber;
    delete obj.assignedPhoneNumbers;
    delete obj.userPresenceState;
    delete obj.isExternallyManaged;

    // Initialize fields on base object
    STRING_FIELDS.forEach(function (field) {
      obj[field] = obj[field] || '';
    });

    BOOLEAN_FIELDS.forEach(function (field) {
      obj[field] = obj[field] || false;
    });

    NUM_FIELDS.forEach(function (field) {
      obj[field] = obj[field] || 0;
    });

    ARRAY_FIELDS.forEach(function (field) {
      obj[field] = obj[field] || [];
    });

    // Pretty format of display name if it's an international number
    if (obj.displayName.startsWith('+')) {
      obj.displayName = PhoneNumberFormatter.format(obj.displayName);
    }
    // Pretty format of phone numbers (international only)
    if (obj.phoneNumber) {
      obj.phoneNumber = PhoneNumberFormatter.format(obj.phoneNumber);
    }
    obj.phoneNumbers.forEach(function (number) {
      number.phoneNumber = PhoneNumberFormatter.format(number.phoneNumber);
    });

    // Normalize email address as names
    if (obj.firstName === UserProfile.NO_NAME) {
      obj.firstName = '';
      obj.displayName = '';
    }
    if (obj.lastName === UserProfile.NO_NAME) {
      obj.lastName = '';
      obj.displayName = '';
    }

    // Make sure the display name is set correctly
    if (obj.displayName) {
      obj.displayName = obj.displayName.trim();
    } else if (!obj.firstName || !obj.lastName) {
      obj.displayName = obj.firstName || obj.lastName || obj.emailAddress; // When no names use email address for display
    } else {
      obj.displayName = (obj.firstName + ' ' + obj.lastName).trim();
    }

    // Normalize the locale
    obj.locale = Utils.normalizeLocale(obj.locale); // Do not default this

    // Delete Michael's minions and handle 'null' string as empty.
    if (!obj.smallImageUri || obj.smallImageUri.endsWith('/minions') || obj.smallImageUri === 'null') {
      obj.smallImageUri = '';
      obj.largeImageUri = '';
    }

    if (!obj.userState && obj.status) {
      obj.userState = obj.status;
    }

    // Workaround: Test if email starts with 'del_' until backend adds missing userState attribute
    if (!obj.userState && obj.emailAddress.startsWith('del_')) {
      obj.userState = Constants.UserState.DELETED;
    }

    if (obj.userState === Constants.UserState.DELETED) {
      // For deleted users the backend adds a dummy email address for the user
      obj.emailAddress = '';
    }

    UserProfile.normalizeExchangeData(obj.exchangeData);
  }

  function setUserInitials(user) {
    if (user.firstName || user.lastName) {
      user.initials = (user.firstName.substring(0, 1) + user.lastName.substring(0, 1)).toUpperCase();
    } else {
      user.initials = Utils.displayNameToInitials(user.emailAddress);
    }
  }

  function setExtendedProperties(user) {
    if (!Utils.isMobile()) {
      user.displayNameEscaped = Utils.textToHtmlEscaped(user.displayName);
    }

    user.isExternal = user.userType === Constants.UserType.GUEST;
    // Keep isGuest property for backwards compatibility with mobile clients.
    // Once mobile clients start using isExternal we can remove this field.
    user.isGuest = user.isExternal;

    user.hasSupportRole = user.userType === Constants.UserType.SUPPORT;
    user.hasTelephonyRole = user.userType === Constants.UserType.TELEPHONY;
    user.hasBotRole = user.userType === Constants.UserType.BOT;

    user.hasSpecialRole = user.hasTelephonyRole || user.hasSupportRole;

    if (!user.hasTelephonyRole) {
      // Do net set initials for special role to avoid IE bug (font size multiplication)
      setUserInitials(user);
    }

    user.isSuspended = user.userState === Constants.UserState.SUSPENDED;
    user.isPurged = user.userState === Constants.UserState.PURGED;
    user.isDeleted = user.isPurged || user.userState === Constants.UserState.DELETED;

    // The inactive state will be represented by "Invited" label both for Guest and Regular users
    user.isInactive = user.userState === Constants.UserState.INACTIVE;
    user.isActiveState = user.userState === Constants.UserState.ACTIVE;
    user.shortName = (user.firstName + (user.lastName.length > 0 ? ' ' + user.lastName[0] + '.' : '')).trim();

    // If the user has an accounts field, set some additional permission properties.
    // This is only available for the logged on user.
    if (user.accounts && user.accounts[0]) {
      var account = user.accounts[0];
      user.hasPermission = UserProfile.hasPermission.bind(null, user);
      user.hasTelephonyPermission = user.hasPermission(Constants.SystemPermission.TELEPHONY);
      user.hasVoicemailPermission = user.hasPermission(Constants.SystemPermission.VOICEMAIL);
      user.hasSupportPermission = user.hasPermission(Constants.SystemPermission.SUPPORT_CONVERSATION);

      // Not applicable for NGTC
      user.canUseHDVideo = false;
      user.canUseHDScreenShare = false;

      // Move data from accounts to the local user object
      user.tenantId = account.tenantId;

      UserProfile.syncTelephonyConfig(user);
    }
  }

  // Compare beginning of the string with query considering locales (e.g. 'ö' should be equal to 'o')
  function localeCompare(string, query) {
    if (string.length < query.length) {
      return false;
    }
    var result = string.slice(0, query.length).localeCompare(query, 'en', { sensitivity: 'base' });
    return result === 0;
  }

  // Exports
  unifyPhone.DefaultAvatars = DefaultAvatars;
  unifyPhone.UserProfile = UserProfile;
  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.IdleState = IdleState;
  unifyPhone.Enums.UserProfile = UserProfile;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var CstaCallState = unifyPhone.Enums.CstaCallState;
  var DefaultAvatars = unifyPhone.DefaultAvatars;
  var logger = unifyPhone.logger;
  var ParticipantState = unifyPhone.Enums.ParticipantState;
  var RtcParticipant = unifyPhone.RtcParticipant;
  var UserProfile = unifyPhone.UserProfile;
  var Utils = unifyPhone.Utils;

  /*
   * Enum for call direction
   * @readonly
   * @enum {String}
   * @property INCOMING
   * @property OUTGOING
   * @property NONE
   */
  var CallDirection = Object.freeze({
    INCOMING: 'incoming',
    OUTGOING: 'outgoing',
    NONE: ''
  });

  /*
   * Enum for call state
   * @readonly
   * @enum {String}
   * @property Idle
   * @property Initiated Outgoing call initiated
   * @property Connecting Outgoing call connecting
   * @property Delivered Outgoing call delivered
   * @property Busy Outgoing call with peer busy
   * @property Failed Failed call
   * @property Declined Declined call
   * @property NotAnswered Call not answered
   * @property Ringing Incoming call is ringing
   * @property Answering Incoming call being answered
   * @property Active Established active call
   * @property Held Established call being held
   * @property Holding Established call holding
   * @property HoldOnHold Established call hold on hold
   * @property Waiting Established call waiting
   * @property Started Conference call started
   * @property ActiveRemote Active call on one of user's other devices
   * @property Terminated Call has been terminated
   */
  var CallState = Object.freeze({
    Idle: { name: 'Idle', ui: '', established: false },

    // Outgoing call states
    Initiated: { name: 'Initiated', ui: 'res_Calling', established: false, css: 'outgoing' },
    Connecting: { name: 'Connecting', ui: '', established: false, css: 'connecting' },
    Delivered: { name: 'Delivered', ui: 'res_Calling', established: false, css: 'outgoing' },
    Busy: { name: 'Busy', ui: 'res_BusyCall', established: false, css: 'busy' },

    // Failed call states
    Failed: { name: 'Failed', ui: 'res_FailedCall', established: false, css: 'failed' },
    Declined: { name: 'Declined', ui: 'res_CallDeclined', established: false, css: 'declined' },
    NotAnswered: { name: 'NotAnswered', ui: 'res_CallNotAnswered', established: false, css: 'not-answered' },

    // Incoming call states
    Ringing: { name: 'Ringing', ui: 'res_IncomingCall', established: false, css: 'incoming' },
    Answering: { name: 'Answering', ui: 'res_IncomingCall', established: false, css: 'answering' }, // Call answered but not Active yet

    // Established call states
    Active: { name: 'Active', ui: '', established: true, css: 'active-call' },
    Held: { name: 'Held', ui: '', established: true, css: 'active-call' },
    Holding: { name: 'Holding', ui: 'res_OnHold', established: true, css: 'holding-call' },
    HoldOnHold: { name: 'HoldOnHold', ui: 'res_Holding', established: true, css: 'holding-call' },
    Waiting: { name: 'Waiting', ui: 'res_Waiting', established: true, css: 'waiting' },

    // Remote calls
    Started: { name: 'Started', ui: 'res_InProgress', established: false, css: 'started' },
    NotStarted: { name: 'NotStarted', ui: '', established: false, css: 'not-started' },

    // Remote call active on another client
    ActiveRemote: { name: 'ActiveRemote', ui: 'res_RemoteCall', established: true, css: 'active-call-remote' },

    // Call has been terminated
    Terminated: { name: 'Terminated', ui: 'res_Terminated', established: false, css: 'declined' }
  });

  // Define an abstract BaseCall class
  // eslint-disable-next-line max-lines-per-function
  function BaseCall(conversation, isRemote) {
    // NOSONAR

    if (!conversation) {
      throw new Error('Cannot create BaseCall object without a conversation');
    }

    // Make sure that isRemote is true or fase
    isRemote = !!isRemote;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    //
    // These variables can only be accessed via getters/setters.
    // Critical variables that cannot be changed directly must be defined here.
    ///////////////////////////////////////////////////////////////////////////////////////
    var _that = this;

    var _callId = null;
    var _instanceId = null;
    var _convId = null;
    var _convType = null;
    var _convTitle = null;
    var _convTitleEscaped = null;
    var _ownerId = null;
    var _transactionId = null;
    var _isDirect = false;
    var _isTelephonyCall = false;
    var _isTestCall = false;
    var _state = CallState.Idle;
    var _disconnectCause = null;
    var _isDirectUpgradedToConf = false; // Direct call upgraded to conference

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function setConversationData(conv) {
      _callId = conv.rtcSessionId;
      _convId = conv.convId;
      _convType = conv.type;
      _convTitle = conv.topic || conv.participantFirstNames || null;
      _convTitleEscaped = conv.topicEscaped || conv.participantFirstNamesEscaped || null;
      _ownerId = conv.creatorId;
      _isDirect = conv.type === Constants.ConversationType.DIRECT;
      _isTelephonyCall = !!conv.isTelephony;
      _isTestCall = !!conv.testCall;

      if (_isDirect) {
        _that.peerUser = conv.peerUser;
      } else {
        delete _that.peerUser;
      }

      _that.peerUsers = conv.peerUsers;
      _that.numConvParticipants = conv.participants.length;
      _that.defaultAvatarColor = conv.defaultAvatarColor;
      _that.avatar = conv.avatar;
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Properties
    ///////////////////////////////////////////////////////////////////////////////////////
    // Define the read-only properties to access the internal variables
    Object.defineProperties(this, {
      isRemote: {
        value: isRemote,
        writable: false,
        enumerable: true,
        configurable: false
      },
      callId: {
        get: function () {
          return _callId;
        },
        enumerable: true,
        configurable: false
      },
      cstaCallId: {
        get: function () {
          if (!this.atcCallInfo) {
            return null;
          }
          return this.atcCallInfo.getCstaCallId();
        },
        enumerable: true,
        configurable: false
      },
      instanceId: {
        get: function () {
          return _instanceId;
        },
        enumerable: true,
        configurable: false
      },
      transactionId: {
        get: function () {
          return _transactionId;
        },
        enumerable: true,
        configurable: false
      },
      state: {
        get: function () {
          return _state;
        },
        enumerable: true,
        configurable: false
      },
      convId: {
        get: function () {
          return _convId;
        },
        enumerable: true,
        configurable: false
      },
      convType: {
        get: function () {
          return _convType;
        },
        enumerable: true,
        configurable: false
      },
      convTitle: {
        get: function () {
          // Don't set the title for direct call in setConversationData, because peerUser can have no data in that moment.
          return _convTitle || (_isDirect ? this.peerUser.displayName : '');
        },
        enumerable: true,
        configurable: false
      },
      convTitleEscaped: {
        get: function () {
          return _convTitleEscaped || (_isDirect ? this.peerUser.displayNameEscaped : '');
        },
        enumerable: true,
        configurable: false
      },
      containsExternals: {
        get: function () {
          return this.participants.some(function (p) {
            return p.participantType === Constants.RTCParticipantType.SESSION_GUEST || p.participantType === Constants.RTCParticipantType.EXTERNAL;
          });
        },
        enumerable: false,
        configurable: false
      },
      ownerId: {
        get: function () {
          return _ownerId;
        },
        enumerable: true,
        configurable: false
      },
      isDirect: {
        get: function () {
          // Return false if this is a direct call that has been upgraded to a conference
          return (_isDirect && !_isDirectUpgradedToConf) || _isTelephonyCall;
        },
        enumerable: true,
        configurable: false
      },
      conferenceCall: {
        get: function () {
          // All group calls are conference calls.
          return !_isDirect || _isDirectUpgradedToConf;
        },
        enumerable: true,
        configurable: false
      },
      isGroupCallStarted: {
        get: function () {
          return !!(!this.isDirect && this.state === CallState.Started);
        },
        enumerable: true,
        configurable: false
      },
      isGroupCallInitiated: {
        get: function () {
          return !!(!this.isDirect && this.state === CallState.Initiated);
        },
        enumerable: true,
        configurable: false
      },
      isGroupCallActive: {
        get: function () {
          return !!(!this.isDirect && this.state === CallState.Active);
        },
        enumerable: true,
        configurable: false
      },
      isTelephonyCall: {
        get: function () {
          return _isTelephonyCall;
        },
        enumerable: true,
        configurable: false
      },
      isTestCall: {
        get: function () {
          return _isTestCall;
        },
        enumerable: true,
        configurable: false
      },
      conversationFeedView: {
        get: function () {
          return true;
        },
        enumerable: true,
        configurable: true // Let LocalCall object redefine this property
      },
      isPullAllowed: {
        value: false,
        enumerable: true,
        configurable: true // Let atcRemoteCall object redefine this property
      },
      isHandoverAllowed: {
        get: function () {
          if (!this.atcCallInfo) {
            return false;
          }
          return this.atcCallInfo.isHandoverAllowed();
        },
        enumerable: true,
        configurable: true
      },
      isHandoverInProgress: {
        get: function () {
          if (!this.atcCallInfo) {
            return false;
          }
          return this.atcCallInfo.isHandoverInProgress();
        },
        enumerable: true,
        configurable: true
      },
      redirectingUser: {
        get: function () {
          return this.getRedirectingUser();
        },
        enumerable: true,
        configurable: true
      },
      forwarded: {
        get: function () {
          return this.isForwarded();
        },
        enumerable: true,
        configurable: true
      },
      pickupNotification: {
        get: function () {
          return this.isPickupNotification();
        },
        enumerable: true,
        configurable: true
      },
      pickedUp: {
        get: function () {
          return this.isPickedUp();
        },
        enumerable: true,
        configurable: true
      },
      disconnectCause: {
        get: function () {
          return _disconnectCause;
        },
        set: function (cause) {
          _disconnectCause = cause;
        },
        enumerable: true,
        configurable: false
      },
      transferCallFailedCause: {
        get: function () {
          if (!this.atcCallInfo) {
            return null;
          }
          return this.atcCallInfo.getTransferCallFailedCause();
        },
        enumerable: true,
        configurable: false
      },
      isOutgoingCallCampedOn: {
        get: function () {
          if (!this.atcCallInfo) {
            return false;
          }
          return this.atcCallInfo.isOutgoingCallCampedOn();
        },
        enumerable: true,
        configurable: true
      },
      isATCCall: {
        get: function () {
          return !!this.atcCallInfo;
        },
        enumerable: true,
        configurable: false
      },
      isDtmfAllowed: {
        get: function () {
          return false;
        },
        enumerable: true,
        configurable: true
      },
      isDirectUpgradedToConf: {
        get: function () {
          return _isDirectUpgradedToConf;
        },
        enumerable: true,
        configurable: false
      },
      muted: {
        get: function () {
          return this.isMuted();
        },
        enumerable: true,
        configurable: false
      },
      holding: {
        get: function () {
          return this.isHolding();
        },
        enumerable: true,
        configurable: false
      }
    });

    // The media types for the local user
    this.localMediaType = { audio: false, video: false, desktop: false };

    // The media types for the call
    this.mediaType = { audio: false, video: false, desktop: false };

    // Remember the last media type before the call is terminated
    this.lastMediaType = this.mediaType;

    this.direction = CallDirection.NONE;
    this.secureCall = false;

    this.establishedTime = 0; // The timestamp when the call is established
    this.creationTime = Date.now(); // Client side creation time. Used for sorting.

    this.peerUsers = null;
    this.peerUser = null;
    this.numConvParticipants = 0;
    this.avatar = null;

    this.activeNotification = null;

    this.activeRTPStatsWarning = null;

    this.participants = []; // Array of RtcParticipants instances
    this.participantsHashTable = {};
    this.attendeeCount = 0; // Number of all guests in a large conference (non moderators)

    this.remotelyMuted = false; // true if local user is muted remotely
    this.sessionMuted = false;
    this.sessionLocked = false;

    this.recording = {
      state: Constants.RecordingInfoState.INITIAL,
      notifyByCurtain: false,
      notifyByUser: false,
      duration: 0,
      starter: { userId: '' },
      reason: Constants.RecordingInfoReason.NONE,
      resumeTime: 0, // internal value
      wasStarted: function () {
        // was started at some point
        return this.state !== Constants.RecordingInfoState.INITIAL;
      },
      isActive: function () {
        return this.state === Constants.RecordingInfoState.STARTED || this.state === Constants.RecordingInfoState.START_PENDING;
      },
      isPaused: function () {
        return this.state === Constants.RecordingInfoState.START_PENDING;
      },
      isFailed: function () {
        return (
          this.reason !== Constants.RecordingInfoReason.NONE &&
          this.reason !== Constants.RecordingInfoReason.STOPPED_MANUALLY &&
          this.reason !== Constants.RecordingInfoReason.STOPPED_AUTOMATICALLY &&
          (this.state === Constants.RecordingInfoState.INITIAL || this.state === Constants.RecordingInfoState.STOPPED)
        );
      }
    };

    this.transcription = {
      state: Constants.RecordingInfoState.INITIAL,
      wasStarted: function () {
        // was started at some point
        return this.state !== Constants.RecordingInfoState.INITIAL;
      },
      isActive: function () {
        return this.state === Constants.RecordingInfoState.STARTED || this.state === Constants.RecordingInfoState.START_PENDING;
      }
    };

    this.upgradeToConfSupported = false;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Members
    // (Only includes members which need to access private data)
    ///////////////////////////////////////////////////////////////////////////////////////
    this.setState = function (newState, bypassTerminate) {
      if (!isRemote && newState !== CallState.Terminated && this.checkCstaState(CstaCallState.Holding)) {
        newState = CallState.Holding;
      }

      // If it's an ATC call and the CSTA Established has not yet been received, don't set the established time,
      // unless the CSTA state is Initiated, which indicates that this is a feature activation call.
      if (
        !this.establishedTime &&
        newState.established &&
        (!this.atcCallInfo || this.atcCallInfo.cstaState.established || this.atcCallInfo.cstaState === CstaCallState.Initiated)
      ) {
        this.establishedTime = Date.now();
      }

      if (!newState || newState === _state) {
        return;
      }
      if (_state === CallState.Terminated) {
        logger.error('[BaseCall]: The call has already been terminated. Cannot set state to ', newState.name);
        return;
      }
      logger.debug('[BaseCall]: Changing call state from ' + _state.name + ' to ' + newState.name);

      _state = newState;

      if (this.isTelephonyCall) {
        this.avatar = newState === CallState.Holding ? DefaultAvatars.TELEPHONY_HOLD : DefaultAvatars.TELEPHONY;
      }

      if (_state === CallState.Terminated && !bypassTerminate) {
        this.terminate();
      }
    };

    this.checkState = function (states) {
      if (!Array.isArray(states)) {
        return states === _state;
      }
      return states.includes(_state);
    };

    this.updateCall = function (newConversation) {
      if (!newConversation) {
        return false;
      }
      setConversationData(newConversation);
      return true;
    };

    this.setPeerUser = function (phoneNumber, displayName, userId, fqnLookedUp, data) {
      var extAvatarUri, smallImageUri, largeImageUri;
      if (data) {
        extAvatarUri = data.extAvatarUri;
        smallImageUri = data.smallImageUri;
        largeImageUri = data.largeImageUri;
      }
      // Used for telephony calls
      if (
        this.peerUser.hasTelephonyRole ||
        !this.peerUser.userId ||
        (userId && userId !== this.peerUser.userId) ||
        !this.peerUser.phoneNumber ||
        Utils.cleanPhoneNumber(phoneNumber) !== Utils.cleanPhoneNumber(this.peerUser.phoneNumber) ||
        phoneNumber === 'anonymous'
      ) {
        this.peerUser = {
          userId: userId || '',
          firstName: null,
          lastName: null,
          displayName: displayName,
          phoneNumber: phoneNumber,
          fqnLookedUp: fqnLookedUp,
          extAvatarUri: extAvatarUri,
          smallImageUri: smallImageUri,
          largeImageUri: largeImageUri
        };
        if (UserProfile && UserProfile.extend) {
          this.peerUser = UserProfile.extend(this.peerUser);
        }
        _convTitle = displayName || phoneNumber || null;
        _convTitleEscaped = _convTitle ? Utils.textToHtmlEscaped(_convTitle) : null;
      }
    };

    this.setTransactionId = function (transactionId) {
      _transactionId = transactionId || Utils.createTransactionId();
    };

    this.clearTransactionId = function () {
      _transactionId = null;
    };

    this.setInstanceId = function (instanceId) {
      _instanceId = instanceId;
    };

    this.setCallIdForTelephony = function (callId) {
      if (_that.isTelephonyCall && callId && callId !== _callId) {
        logger.debug('[BaseCall]: Changing callId from ' + _callId + ' to ' + callId);
        _callId = callId;
      }
    };

    this.setDirectUpgradedToConf = function () {
      if (_isDirect) {
        _isDirectUpgradedToConf = true;
      }
    };

    this.clearDirectUpgradedToConf = function () {
      if (_isDirect) {
        _isDirectUpgradedToConf = false;
      }
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////////////////
    setConversationData(conversation);
  }

  BaseCall.prototype.sameAs = function (call) {
    return !!call && this.callId === call.callId && this.isRemote === call.isRemote;
  };

  BaseCall.prototype.terminate = function () {
    logger.debug('[BaseCall]: Terminating call with callId = ', this.callId);
    this.lastMediaType = this.mediaType;
    this.mediaType = { audio: false, video: false, desktop: false };
    this.setState(CallState.Terminated, true);
  };

  BaseCall.prototype.isEstablished = function () {
    return this.state.established;
  };

  BaseCall.prototype.isOutgoingState = function () {
    return this.state === CallState.Initiated || this.state === CallState.Connecting || this.state === CallState.Delivered || this.state === CallState.Busy;
  };

  BaseCall.prototype.isPresent = function () {
    return this.state !== CallState.Idle && this.state !== CallState.Terminated;
  };

  BaseCall.prototype.isHeld = function () {
    return this.state === CallState.Held;
  };

  BaseCall.prototype.hasRemoteMedia = function () {
    return false;
  };

  BaseCall.prototype.isCallingOut = function () {
    return (
      this.state === CallState.Waiting &&
      this.participants.length > 0 &&
      this.participants.every(function (p) {
        return p.pcState === ParticipantState.Initiated;
      })
    );
  };

  BaseCall.prototype.hasVideo = function () {
    // Screen share is currently handled as a video stream, so return true
    // if the media type includes either video or desktop (i.e. screen share)
    return !!(this.mediaType.video || this.mediaType.desktop);
  };

  BaseCall.prototype.updateSecurityStatus = function (status) {
    // Only update secureCall if status is explicitly set to true or false.
    // Other truthy or falsy values (e.g. undefined or null) must be ignored.
    if (status === true || status === false) {
      this.secureCall = status;
    }
  };

  // RTC methods
  BaseCall.prototype.canToggleVideo = function () {
    return !this.isRemote && this.checkState([CallState.Active, CallState.Waiting]);
  };

  BaseCall.prototype.hasRemoteVideo = function () {
    return false;
  };

  BaseCall.prototype.hasLocalVideo = function () {
    // Screen share is currently handled as a video stream, so return true
    // if the client is streaming either video or desktop (i.e. screen share).
    return this.localMediaType.video || this.localMediaType.desktop;
  };

  BaseCall.prototype.hasLocalScreenShare = function () {
    return this.localMediaType.desktop;
  };

  BaseCall.prototype.hasRemoteScreenShare = function () {
    return this.mediaType.desktop && !this.localMediaType.desktop;
  };

  BaseCall.prototype.isMuted = function () {
    return false;
  };

  BaseCall.prototype.mute = function () {
    logger.warning('[BaseCall]: Mute is not implemented in base object');
    return false;
  };

  BaseCall.prototype.unmute = function () {
    logger.warning('[BaseCall]: Unmute is not implemented in base object');
    return false;
  };

  BaseCall.prototype.toggleMute = function () {
    logger.warning('[BaseCall]: toggleMute is not implemented in base object');
    return false;
  };

  BaseCall.prototype.outgoingFailed = function () {
    return (
      !this.isRemote &&
      this.direction === CallDirection.OUTGOING &&
      this.checkState([CallState.Declined, CallState.Busy, CallState.NotAnswered, CallState.Failed])
    );
  };

  BaseCall.prototype.setPeerUsersAsParticipants = function () {
    // Populate participants for outgoing Group Calls (RTC.JOIN)
    if (this.direction !== CallDirection.OUTGOING || !this.peerUsers) {
      return;
    }

    this.participants = this.peerUsers.map(function (user) {
      // Create a new object using the member's UserProfile object as prototype
      return RtcParticipant.createFromUser(user, ParticipantState.Initiated);
    });
    this.participantsHashTable = this.participants.reduce(function (hash, p) {
      hash[p.userId] = p;
      return hash;
    }, {});

    logger.debug('[BaseCall]: Initialized participants: ', this.participants);
  };

  BaseCall.prototype.hasParticipant = function (userId) {
    return this.participants.some(function (p) {
      return p.userId === userId;
    });
  };

  BaseCall.prototype.getParticipant = function (userId) {
    return (
      this.participants.find(function (p) {
        return p.userId === userId;
      }) || null
    );
  };

  function checkParticipantPermissions(participant, call) {
    var supportedFeatures = participant.rtcSupportedFeatures || [];

    if (call.isDirect) {
      if (supportedFeatures.includes(Constants.RtcSupportedFeatures.UPGRADE_TO_CONFERENCE)) {
        logger.debug('[BaseCall]: Upgrade to conference is supported by peer client');
        call.upgradeToConfSupported = true;
      } else {
        logger.debug('[BaseCall]: Upgrade to conference is not supported by peer client');
        call.upgradeToConfSupported = false;
      }
    }
  }

  BaseCall.prototype.addParticipant = function (participant, pcState, update) {
    if (!participant || !participant.userId) {
      logger.warning('[BaseCall]: addParticipant called with invalid participant');
      return null;
    }

    logger.debug('[BaseCall]: Add participant with userId =', participant.userId);
    if (this.hasParticipant(participant.userId)) {
      if (update) {
        return this.updateParticipant(participant, pcState);
      }
      logger.warning('[BaseCall]: addParticipant called with existing participant. userId =', participant.userId);
      return null;
    }

    if (pcState) {
      participant.pcState = pcState;
    }
    this.participants.push(participant);
    this.participantsHashTable[participant.userId] = participant;
    checkParticipantPermissions(participant, this);

    logger.debug('[BaseCall]: Added participant to call: ', participant.userId);
    return participant;
  };

  BaseCall.prototype.updateParticipant = function (participant, pcState) {
    if (!participant || !participant.userId) {
      logger.warning('[BaseCall]: updateParticipant called with invalid participant');
      return null;
    }

    logger.debug('[BaseCall]: Update participant with userId =', participant.userId);
    var curr = this.getParticipant(participant.userId);
    if (!curr) {
      logger.warning('[BaseCall]: The given participant is not part of the call');
      return null;
    }

    pcState = pcState || participant.pcState;

    if (curr.isMeetingPointInvitee && curr.locallyMuted) {
      // CMR is locally muted
      curr.muted = true;
      curr.pcState = pcState !== ParticipantState.Active ? pcState : ParticipantState.Muted;
    } else {
      curr.muted = participant.muted;
      curr.pcState = pcState;
    }

    curr.streamId = participant.streamId;
    curr.screenStreamId = participant.screenStreamId;
    curr.mediaType = participant.mediaType;
    curr.screenSharePointerSupported = participant.screenSharePointerSupported;
    curr.isModerator = participant.isModerator;
    curr.isMeetingGuest = participant.isMeetingGuest;
    curr.rtcSupportedFeatures = participant.rtcSupportedFeatures;
    curr.flags = participant.flags;
    if (participant.participantType === Constants.RTCParticipantType.TELEPHONY) {
      curr.displayName = participant.displayName;
      curr.firstName = participant.firstName;
      curr.lastName = participant.lastName;
      curr.phoneNumber = participant.phoneNumber;
    }
    checkParticipantPermissions(curr, this);

    logger.debug('[BaseCall]: Updated participant data for ', curr.userId);
    return curr;
  };

  BaseCall.prototype.removeParticipant = function (userId) {
    delete this.participantsHashTable[userId];

    var participants = this.participants;
    return (
      participants.find(function (p, idx) {
        if (p.userId === userId) {
          // Reset the participant's video URL and media type
          p.videoStream = null;
          p.mediaType = { audio: false, video: false, desktop: false };

          participants.splice(idx, 1);
          logger.debug('[BaseCall]: Removed participant from call: ', p.userId);
          return true;
        }
        return false;
      }) || null
    );
  };

  BaseCall.prototype.setParticipantState = function (userId, state) {
    if (!state) {
      return;
    }
    this.participants.some(function (p) {
      if (p.userId === userId) {
        p.pcState = state;
        logger.debug('[BaseCall]: Set participant state to ' + state.name + ', userId =', p.userId);
        return true;
      }
      return false;
    });
  };

  BaseCall.prototype.hasOtherParticipants = function () {
    // For large conference there might be a single moderator and multiple attendees not included in participants field.
    // For direct or regular group call all other participants are available in the participants field.
    return this.participants.length > 0 || this.attendeeCount > 0;
  };

  BaseCall.prototype.updateMediaType = function () {
    var mediaType = {
      audio: this.localMediaType.audio,
      video: this.localMediaType.video,
      desktop: this.localMediaType.desktop
    };

    this.participants.forEach(function (p) {
      if (p.mediaType) {
        if (p.mediaType.audio) {
          mediaType.audio = true;
        }
        if (p.mediaType.video) {
          mediaType.video = true;
        }
        if (p.mediaType.desktop) {
          mediaType.desktop = true;
        }
      }
    });

    logger.debug('[BaseCall]: Updating call media type to ', mediaType);
    this.mediaType = mediaType;
  };

  BaseCall.prototype.callTypeCss = function () {
    if (this.isDirect) {
      return this.hasVideo() ? 'webrtc-video' : 'webrtc-audio';
    }
    if (this.isRemote) {
      return 'remote';
    }
    // Group call
    switch (this.state.css) {
      case 'outgoing':
        return 'ongoing';
      case 'incoming':
        return 'incoming';
      case 'answering':
        return 'answering';
      case 'active-call':
        return 'active';
      default:
        return '';
    }
  };

  BaseCall.prototype.toString = function () {
    return JSON.stringify(this, null, 3);
  };

  BaseCall.prototype.getCstaState = function () {
    return this.atcCallInfo ? this.atcCallInfo.cstaState : null;
  };

  BaseCall.prototype.setCstaState = function (state) {
    if (this.atcCallInfo) {
      logger.debug('[BaseCall]: Set csta state to ', state);
      this.atcCallInfo.cstaState = state;
      if (this.isAtcRemote) {
        this.avatar = state === CstaCallState.Holding ? DefaultAvatars.TELEPHONY_HOLD : DefaultAvatars.TELEPHONY;
      }
      this.consultation = this.consultation && !this.isHolding();
    }
  };

  BaseCall.prototype.checkCstaState = function (states) {
    if (!this.atcCallInfo) {
      return false;
    }
    if (!Array.isArray(states)) {
      return states === this.atcCallInfo.cstaState;
    }
    return states.includes(this.atcCallInfo.cstaState);
  };

  BaseCall.prototype.isHolding = function () {
    return this.checkCstaState([CstaCallState.Holding, CstaCallState.HoldOnHold, CstaCallState.ConferenceHolding]);
  };

  BaseCall.prototype.isHoldAllowed = function () {
    return this.atcCallInfo ? this.atcCallInfo.isHoldAllowed() : false;
  };

  BaseCall.prototype.isConsultAllowed = function () {
    return this.atcCallInfo ? this.atcCallInfo.isConsultAllowed() : false;
  };

  BaseCall.prototype.isReconnectAllowed = function () {
    return this.atcCallInfo ? this.atcCallInfo.isReconnectAllowed() : false;
  };

  BaseCall.prototype.isConferenceCallAllowed = function () {
    return this.atcCallInfo ? this.atcCallInfo.isConferenceCallAllowed() : false;
  };

  BaseCall.prototype.isMakeCallAllowed = function () {
    return this.atcCallInfo ? this.atcCallInfo.isMakeCallAllowed() : false;
  };

  BaseCall.prototype.isRetrieveAllowed = function () {
    return this.atcCallInfo ? this.atcCallInfo.isRetrieveAllowed() : false;
  };

  BaseCall.prototype.isTransferCallAllowed = function () {
    return this.atcCallInfo ? this.atcCallInfo.isTransferCallAllowed() : false;
  };

  BaseCall.prototype.isTransferAllowed = function () {
    return this.atcCallInfo ? this.atcCallInfo.isTransferAllowed() : false;
  };

  BaseCall.prototype.isHangupAllowed = function () {
    return this.atcCallInfo ? this.atcCallInfo.isHangupAllowed() : false;
  };

  BaseCall.prototype.getPosition = function () {
    return this.atcCallInfo ? this.atcCallInfo.getPosition() : null;
  };

  BaseCall.prototype.setPosition = function (p) {
    this.atcCallInfo && this.atcCallInfo.setPosition(p);
  };

  BaseCall.prototype.isAtPosition = function (p) {
    return this.atcCallInfo ? this.atcCallInfo.isAtPosition(p) : false;
  };

  BaseCall.prototype.setAtcHandoverInProgress = function () {
    if (this.atcCallInfo) {
      this.atcCallInfo.setHandoverInProgress();
    }
  };

  BaseCall.prototype.clearAtcHandoverInProgress = function () {
    if (this.atcCallInfo) {
      this.atcCallInfo.clearHandoverInProgress();
    }
  };

  BaseCall.prototype.isHoldInProgress = function () {
    return this.atcCallInfo ? this.atcCallInfo.isHoldInProgress() : false;
  };

  BaseCall.prototype.setHoldInProgress = function () {
    if (this.atcCallInfo) {
      this.atcCallInfo.setHoldInProgress();
    }
  };

  BaseCall.prototype.clearHoldInProgress = function () {
    if (this.atcCallInfo) {
      this.atcCallInfo.clearHoldInProgress();
    }
  };

  BaseCall.prototype.isRetrieveInProgress = function () {
    return this.atcCallInfo ? this.atcCallInfo.isRetrieveInProgress() : false;
  };

  BaseCall.prototype.setRetrieveInProgress = function () {
    if (this.atcCallInfo) {
      this.atcCallInfo.setRetrieveInProgress();
    }
  };

  BaseCall.prototype.clearRetrieveInProgress = function () {
    if (this.atcCallInfo) {
      this.atcCallInfo.clearRetrieveInProgress();
    }
  };

  BaseCall.prototype.isAtcConferenceCall = function () {
    return this.atcCallInfo ? this.atcCallInfo.isConferenceCall() : false;
  };

  BaseCall.prototype.setRedirectingUser = function (phoneNumber, fqNumber, displayName, userId, redirectionType) {
    if (this.atcCallInfo) {
      this.atcCallInfo.setRedirectingUser(phoneNumber, fqNumber, displayName, userId, redirectionType);
    }
  };

  BaseCall.prototype.getRedirectingUser = function () {
    return this.atcCallInfo ? this.atcCallInfo.getRedirectingUser() : null;
  };

  BaseCall.prototype.setRedirectionType = function (type) {
    this.atcCallInfo && this.atcCallInfo.setRedirectionType(type);
  };

  BaseCall.prototype.getRedirectionType = function () {
    return this.atcCallInfo ? this.atcCallInfo.getRedirectionType() : null;
  };

  BaseCall.prototype.isForwarded = function () {
    return this.atcCallInfo ? this.atcCallInfo.isForwarded() : false;
  };

  BaseCall.prototype.isPickupNotification = function () {
    return this.atcCallInfo ? this.atcCallInfo.isPickupNotification() : false;
  };

  BaseCall.prototype.isPickedUp = function () {
    return this.atcCallInfo ? this.atcCallInfo.isPickedUp() : false;
  };

  BaseCall.prototype.setDisconnectCause = function (cause, reason) {
    if (!this._disconnectCause) {
      this.disconnectCause = {
        cause: cause,
        reason: reason
      };
    }
  };

  // Exports
  unifyPhone.BaseCall = BaseCall;
  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.CallDirection = CallDirection;
  unifyPhone.Enums.CallState = CallState;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var logger = unifyPhone.logger;
  var BaseCall = unifyPhone.BaseCall;
  var CallState = unifyPhone.Enums.CallState;
  var Proto = unifyPhone.Proto;
  var Utils = unifyPhone.Utils;

  function RemoteCall(conversation) {
    // Imports (change to injected UnifyPhone)

    if (!conversation || !conversation.rtcSessionId) {
      throw new Error('Cannot create RemoteCall object without a valid conversation');
    }

    // Call the base constructor
    RemoteCall.parent.constructor.call(this, conversation, true);

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Properties
    ///////////////////////////////////////////////////////////////////////////////////////
    // Define the read-only properties to access the internal variables
    Object.defineProperties(this, {
      isPullAllowed: {
        get: function () {
          return !this.pullNotAllowed && this.state === CallState.ActiveRemote && !this.pullBlocked;
        },
        enumerable: true,
        configurable: false
      }
    });
    // Remote client on which the call is established
    this.activeClient = null;
    this.ctiMuted = false;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////////////////
    conversation = null; // Remove reference to conversation object

    if (this.isTelephonyCall) {
      // Init displayName to empty to avoid showing Phone calls conversation displayName (VGTC VGTC)
      this.peerUser.displayName = '';
    }
    logger.info('[RemoteCall]: Created new remote call with callID = ', this.callId);
  }

  Utils.inherit(RemoteCall, BaseCall);

  RemoteCall.prototype.setActiveClient = function (apiParticipant) {
    if (!apiParticipant) {
      return;
    }
    if (apiParticipant.userId) {
      this.activeClient = {
        clientId: apiParticipant.clientId,
        clientDisplayName: apiParticipant.clientDisplayName,
        mediaType: Proto.getMediaType(apiParticipant.mediaTypes)
      };
      // Check call permissions:
      // We need to explicitly check if the backend set the pullCall flag to false (for backwards compatibility)
      this.pullNotAllowed = apiParticipant.pullCall === false;
    } else {
      // Set data only. This is the case for remote calls where the user has been invited as guest.
      this.activeClient = apiParticipant;
    }
  };

  RemoteCall.prototype.isMuted = function () {
    return this.ctiMuted;
  };

  RemoteCall.prototype.mute = function () {
    if (this.checkState(CallState.Terminated)) {
      logger.error('[RemoteCall]: Cannot mute a call that has already been terminated');
      return false;
    }

    logger.debug('[RemoteCall]: Mute call with callId =', this.callId);
    this.ctiMuted = true;
    return this.ctiMuted;
  };

  RemoteCall.prototype.unmute = function () {
    if (this.checkState(CallState.Terminated)) {
      logger.error('[RemoteCall]: Cannot mute a call that has already been terminated');
      return false;
    }

    logger.debug('[RemoteCall]: Unmute call with callId =', this.callId);
    this.ctiMuted = false;
    return this.ctiMuted;
  };

  // Exports
  unifyPhone.RemoteCall = RemoteCall;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var Utils = unifyPhone.Utils;

  // Define the object
  var Conversation = {};

  ///////////////////////////////////////////////////////////////////////////
  // Helper functions for Conversation
  ///////////////////////////////////////////////////////////////////////////
  var DEFAULT_GROUP_CONV_AVATAR = 'content/images/icon-general-default-group-avatar';
  var COLORS = ['blue', 'green', 'orange', 'yellow'];

  function getColor(conv) {
    if (!conv || !conv.convId || conv.isDraft) {
      return 'grey';
    }

    var sum = 0;
    var convId = conv.convId;
    for (var idx = 0; idx < convId.length; idx++) {
      sum += convId.charCodeAt(idx);
    }
    return COLORS[sum % COLORS.length];
  }

  function isInitialized() {
    return (
      !!Conversation.localUser &&
      typeof Conversation.getExtendedUser === 'function' &&
      typeof Conversation.getUserFromCache === 'function' &&
      typeof Conversation.addUsersToCache === 'function'
    );
  }

  function setConvMethods(conv) {
    conv.contains = function (item, itemId) {
      var id = item ? item.itemId : itemId;
      return this.items.some(function (itm) {
        return id === itm.itemId;
      });
    };

    conv.userIsModerator = function (user) {
      if (!user || !this.isModerated) {
        return false;
      }
      var userId = typeof user === 'string' ? user : user.userId;
      return this.moderators.some(function (elem) {
        return elem.userId === userId;
      });
    };

    conv.hasRequiredModerationAccess = function (user) {
      return !this.isModerated || this.userIsModerator(user);
    };

    conv.sortThreads = function () {
      this.threads &&
        this.threads.sort(function (a, b) {
          return a.creationTime - b.creationTime;
        });
    };

    conv.canLeave = function () {
      // User can leave IF:
      // - User is a participant
      // - This is not the support conversation
      // - There is no active call or the user is not in the call
      // - This is an Open conversation OR user is not the last non-external participant
      return (
        this.hasJoined &&
        !this.isSupportConv &&
        (!this.call || this.call.isGroupCallStarted) &&
        (this.type === Constants.ConversationType.OPEN || this.isLocalUserExternal || Conversation.hasNonGuestParticipants(this))
      );
    };

    conv.canRemoveParticipant = function (participants, participant) {
      // This function assumes that the basic validation has already been done
      // (e.g. User is not localUser or guest, this is not a Support conversation, etc...).

      // Guests cannot remove the last regular user
      var conversationHasOtherUsers = participants.some(function (p) {
        return p.type === Constants.ConversationParticipantType.REGULAR && p.userId !== participant.userId;
      });
      var canRemoveLastUser = !this.isLocalUserExternal || participant.type !== Constants.ConversationParticipantType.REGULAR || conversationHasOtherUsers;

      // We cannot remove a participant while there is an ongoing call
      var hasActiveCall = !!(this.call && this.call.isPresent());

      return canRemoveLastUser && !hasActiveCall && this.hasRequiredModerationAccess(Conversation.localUser);
    };
  }

  function setPeerUsers(conv) {
    // Set peerUsers for all conversation types
    conv.peerUsers = [];
    conv.hasJoined = false;
    conv.participants.forEach(function (p) {
      if (Conversation.localUser.userId === p.userId) {
        conv.hasJoined = true; // there may be open-not-joined conversations
        return;
      }
      // Get the extended user object
      var pExt;
      if (!p.userId && p.notFound) {
        // Save invalid participant without changes for case when conversation is a draft
        pExt = p;
      } else {
        pExt = Conversation.getExtendedUser(p.userId);
      }
      conv.peerUsers.push(pExt);
    });

    // Set peerUser for DIRECT conversations
    if (conv.isDirect) {
      conv.peerUser = conv.peerUsers[0];
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////
  // Public interfaces
  ///////////////////////////////////////////////////////////////////////////////////////////////
  // Extend object via prototypical inheritance
  Conversation.extend = function (obj) {
    if (!isInitialized()) {
      if (UnifyPhone.isUnitTestRun) {
        return null;
      } else {
        throw new Error('The Conversation object is not ready for use');
      }
    }
    if (!obj) {
      return null;
    }
    if (obj.isExtended) {
      // The given object is already an extended conversation
      return obj;
    }
    // Participants object should contain an array
    obj.participants = obj.participants || [];
    // Make sure userData is a valid object, but do not initialize its fields
    obj.userData = obj.userData || {};

    // Create a derived object to be extended and returned (if not already done so)
    var conv = Object.create(obj);
    conv.isExtended = true;
    conv.isConversationObject = true;

    conv.isOpen = conv.type === Constants.ConversationType.OPEN;
    conv.isGroup = conv.type === Constants.ConversationType.GROUP;
    conv.isDirect = conv.type === Constants.ConversationType.DIRECT;
    conv.isExpert = !!conv.systemTags && conv.systemTags.includes(Constants.SystemTag.IC);

    conv.isDeleted = !!conv.isDeleted;
    conv.lastModification = Math.max(conv.modificationTime || 0, conv.lastItemModificationTime || 0);

    conv.sendingMessages = [];
    conv.muted = !!conv.muted; // non-joined conversations are not added to the cache, so we need to make sure muted is initialized
    conv.containsExternals = !!conv.containsExternals;

    conv.isModerated = !!conv.isModerated;
    conv.moderators = conv.moderators || [];

    conv.isGuestAccessDisabled = !!conv.isGuestAccessDisabled;

    conv.isLocalUserExternal = !conv.isDirect && conv.creatorTenantId !== Conversation.localUser.tenantId;

    // Sanitize the conversation topic and expose as new topicEscaped attribute
    if (conv.topic && navigator.platform !== 'iOS') {
      conv.topicEscaped = Utils.textToHtmlEscaped(conv.topic);
    }

    conv.draftType = obj.draftType;

    // Initialize internal arrays
    conv.items = [];
    conv.call = null;
    conv.parents = {}; // Hashtable. key:parentId, value: parentItem
    conv.threads = []; // Sorted array of parents (threads incl. system items)
    conv.clusteringData = {
      // Contains items queued for clustering
      lastSentTimeStamp: null,
      clusteringId: null,
      parentId: null,
      actualItemId: null
    };
    conv.hasOldestThread = false; // false until we have received all threads from server

    // Set the conversation creator
    conv.creator = Conversation.getExtendedUser(conv.creatorId);

    setPeerUsers(conv);

    // Number of active participants
    conv.participantCount = obj.participants.length;

    // Build left participant list and set conversation.leaveTime if I left this conversation
    conv.previousMembers = [];
    obj.formerParticipants &&
      obj.formerParticipants.forEach(function (p) {
        if (Conversation.localUser.userId === p.participant.userId) {
          conv.leaveTime = p.leaveTime;
          return;
        }
        // Get the extended user object
        var pExt = Conversation.getExtendedUser(p.participant.userId);
        conv.previousMembers.push({
          user: pExt,
          leaveTime: p.leaveTime
        });
      });

    Conversation.setConversationAvatar(conv);

    // Extend the topLevelItem (if there is one)
    if (obj.topLevelItem && !conv.isTelephony) {
      conv.topLevelItem = obj.topLevelItem;
    }

    // Methods
    setConvMethods(conv);

    return conv;
  };

  Conversation.getDefaultConversationAvatar = function (conv) {
    if (conv.isDirect) {
      // This is a direct conversation. We use the peer user's avatar.
      return null;
    }

    var color = '-' + getColor(conv);
    if (color === '-grey') {
      color = '';
    }

    return {
      avatar: DEFAULT_GROUP_CONV_AVATAR + color + '.png',
      avatarLarge: DEFAULT_GROUP_CONV_AVATAR + color + '-XL.png',
      hasAvatarPicture: false
    };
  };

  Conversation.setConversationAvatar = function (conv) {
    if (!conv) {
      return;
    }
    if (conv.isDirect) {
      conv.avatar = null;
      return;
    }

    conv.defaultAvatarColor = getColor(conv);

    if (conv.conversationAvatar && conv.conversationAvatar.smallPictureId && conv.conversationAvatar.largePictureId) {
      var url = unifyPhone.__domain + '/api' + unifyPhone.__path + '/avatar?convId=' + conv.convId + '&';
      conv.avatar = {
        avatar: url + 'type=small&fileid=' + conv.conversationAvatar.smallPictureId,
        avatarLarge: url + 'type=large&fileid=' + conv.conversationAvatar.largePictureId,
        hasAvatarPicture: true
      };
    } else {
      conv.avatar = Conversation.getDefaultConversationAvatar(conv);
    }

    if (conv.isOpen) {
      conv.avatar.badgeType = Constants.AvatarBadgeType.COMMUNITY;
    } else if (conv.isExpert) {
      conv.avatar.badgeType = Constants.AvatarBadgeType.EXPERT;
    }
  };

  Conversation.hasIncompleteUsers = function (conv) {
    if (!conv || !conv.peerUsers) {
      return false;
    }
    return conv.peerUsers.some(function (user) {
      return user.noData;
    });
  };

  Conversation.hasNonGuestParticipants = function (conv) {
    if (!conv || !conv.peerUsers) {
      return false;
    }
    return conv.peerUsers.some(function (p) {
      return p.userType === Constants.UserType.REGULAR;
    });
  };

  Conversation.canDelayRecordingDeletion = function (conv) {
    var user = Conversation.localUser;
    return !!(user && conv && conv.hasRequiredModerationAccess(user) && conv.hasJoined && !conv.isLocalUserExternal && user.recordingDeletionEnabledTenant);
  };

  Conversation.canAddParticipants = function (conv) {
    // User cannot add participants if any of the following conditions is true.
    // - Read-only mode is enabled for user's tenant
    // - User has left conversation or has been removed.
    // - User is not a participant in a community.
    // - This is a support conversation.
    // - This is a telephony conversation.
    // - The conversation is moderated and the user is not a moderator.
    // - This is a direct conversation and one of the users is leaving a Voicemail message
    if (
      Conversation.localUser.readOnlyModeEnabled ||
      conv.leaveTime ||
      !conv.hasJoined ||
      conv.isSupportConv ||
      conv.isTelephony ||
      (conv.isModerated && !conv.userIsModerator(Conversation.localUser)) ||
      (conv.call && conv.call.voicemailConnected)
    ) {
      return false;
    }
    return true;
  };

  // Must be set before initializing Conversation objects
  Conversation.getExtendedUser = null;
  Conversation.getUserFromCache = null;
  Conversation.addUsersToCache = null;
  Conversation.localUser = null;

  // Exports
  unifyPhone.Conversation = Conversation;
  unifyPhone.Enums = unifyPhone.Enums || {};

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var logger = unifyPhone.logger;
  var PhoneNumberFormatter = unifyPhone.PhoneNumberFormatter;
  var UserProfile = unifyPhone.UserProfile;
  var Utils = unifyPhone.Utils;

  var CallLogItemType = Object.freeze({
    BUSY: 'BUSY',
    DECLINED: 'DECLINED',
    FAILED: 'FAILED',
    INCOMING: 'INCOMING',
    MISSED: 'MISSED',
    NOT_ANSWERED: 'NOT_ANSWERED',
    OUTGOING: 'OUTGOING',
    REDIRECTED: 'REDIRECTED',
    UNKNOWN: 'UNKNOWN'
  });

  var RtcItemState = Object.freeze({
    NOT_ANSWERED: 'NOT_ANSWERED',
    MISSED: 'MISSED',
    DECLINED: 'DECLINED',
    ENDED: 'ENDED',
    FAILED: 'FAILED',
    MOVED: 'MOVED',
    BUSY: 'BUSY',
    INVALID_NUMBER: 'INVALID_NUMBER',
    TEMPORARILY_UNAVAILABLE: 'TEMPORARILY_UNAVAILABLE',
    UNKNOWN: 'UNKNOWN'
  });

  var EXTENDED_ATTRIBUTES = [
    'callLogType',
    'rtcState',
    'duration',
    'hasVoicemail',
    'isVoicemailUnheard',
    'i18nCallLogTypeRes',
    'peerUser',
    'redirectedToUser',
    'redirectingUser',
    'recordingUploadState'
  ];

  // Define the object
  var CallLogItem = {};

  // Extend object via prototypical inheritance
  CallLogItem.extend = function (obj) {
    if (typeof unifyPhone.Conversation.getExtendedUser !== 'function') {
      throw new Error('The Conversation object is not ready for use');
    }

    if (!obj) {
      logger.error('[CallLogItem]: CallLogItem.extend invoked with null object or null conversation');
      return null;
    }

    if (obj.type !== Constants.ConversationItemType.RTC) {
      logger.warn('[CallLogItem]: CallLogItem.extend invoked with non-RTC itme');
      return null;
    }

    if (obj.isExtended) {
      // The given object is already an extended conversation
      return obj;
    }

    normalizeBaseObj(obj);

    // Create a derived object to be extended and returned.
    var item = Object.create(obj);
    item.isExtended = true;

    // Was the call initiated by me?
    item.sentByMe = unifyPhone.Conversation.localUser.userId === obj.creatorId;

    item.callLogType = getCallLogType(item);
    item.rtcState = getRtcItemState(item.rtc, item.sentByMe);
    item.duration = item.rtc.ended && item.rtc.ended.duration ? Math.floor(item.rtc.ended.duration / 1000) : 0;
    item.hasVoicemail = !!item.attachments && item.attachments.length > 0;
    item.isVoicemailUnheard = item.hasVoicemail && !!item.rtc.unheardVoicemail;
    item.i18nCallLogTypeRes = 'res_CallHistoryType_' + item.callLogType;

    setTelephonyUsers(item);

    // Move recordingUploadState to base object
    item.recordingUploadState = item.rtc.recordingUploadState;

    return item;
  };

  // Update a dst CallLogItem with new information from src CallLogItem
  CallLogItem.update = function (dst, src) {
    if (!dst || !src || dst.itemId !== src.itemId || src.type !== Constants.ConversationItemType.RTC) {
      return;
    }
    if (!src.isExtended) {
      src = CallLogItem.extend(src);
      if (!src) {
        return;
      }
    }

    // Start by copying the base object fields that can change
    dst.modificationTime = src.modificationTime;
    dst.rtc = src.rtc;
    dst.attachments = src.attachments;

    EXTENDED_ATTRIBUTES.forEach(function (name) {
      dst[name] = src[name];
    });

    Utils.syncBaseObject(dst);
  };

  /**
   * Insert an item in chronologically order and return the index where the item was inserted.
   */
  CallLogItem.insertItem = function (items, item) {
    if (!item || !Array.isArray(items)) {
      return -1;
    }
    // The items must be ordered chronologically. Newest items go to the end.
    // Find the position in the items array.
    var length = items.length;

    if (length === 0) {
      items.push(item);
      return 0;
    }

    if (itemComparatorFn(item, items[0]) < 0) {
      // Insert at the front
      items.unshift(item);
      return 0;
    }

    if (itemComparatorFn(item, items[length - 1]) > 0) {
      // Insert at the end
      items.push(item);
      return length;
    }

    // The truth lies somewhere in between...
    return Utils.binaryInsert(items, item, itemComparatorFn, 'itemId');
  };

  /**
   * Update an item inside items with new information.
   * Returns the updated item if successful
   */
  CallLogItem.updateItem = function (conversation, newItem) {
    var items = conversation && conversation.items;
    if (!newItem || !items || !items.length) {
      return;
    }
    items.some(function (item, idx) {
      if (item.itemId === newItem.itemId) {
        items[idx] = newItem;
        return true;
      }
      return false;
    });
  };

  ///////////////////////////////////////////////////////////////////////////
  // Helper functions for CallLogItem.extend
  ///////////////////////////////////////////////////////////////////////////
  function itemComparatorFn(a, b) {
    if (a.creationTime < b.creationTime) {
      return -1;
    } else if (a.creationTime > b.creationTime) {
      return 1;
    }
    return 0;
  }

  function extendAttachment(a) {
    if (!a) {
      return;
    }

    a.isOld = true;

    // We need to build the URI as follows: "/fileapi?fileid=<File ID>&itemid=<Item ID>"
    a.uri = unifyPhone.__domain + '/api' + unifyPhone.__path + '/fileapi?fileid=' + a.fileId;
    // We also need to encode the single quote character so it works properly with the
    // ng-style directive
    a.uri = a.uri.replace(/'/g, '%27');

    a.deleteUrl = unifyPhone.__domain + '/api' + unifyPhone.__path + '/fileapi?fileid=' + a.fileId;
    if (a.itemId) {
      a.uri += '&itemid=' + a.itemId;
      a.deleteUrl += '&itemid=' + a.itemId;
    }

    a.sizeStr = Utils.bytesToSize(a.size);
    a.fileNameTruncated = Utils.truncateFileName(a.fileName);
  }

  function normalizeBaseObj(obj) {
    // Initialize some conversation item fields that are optional in the proto
    // files, but must be initialized for the clients.
    if (obj.dataRetentionState === Constants.DataRetentionState.UNTOUCHED) {
      // Since UNTOUCHED is the default value we can remove it to reduce memory utilization.
      delete obj.dataRetentionState;
    }

    switch (obj.rtc.type) {
      case Constants.RTCItemType.MISSED:
      case Constants.RTCItemType.STARTED:
      case Constants.RTCItemType.ENDED:
        // Call missed and call summary items may be updated with a voicemail or recording, so
        // ensure that the base object has an attachments property.
        obj.attachments = obj.attachments || [];
        obj.attachments.forEach(extendAttachment);
    }

    obj.modificationTime = obj.modificationTime || obj.creationTime;
  }

  function setTelephonyUsers(item) {
    var participants = item.rtc.rtcParticipants;
    if (!participants) {
      return;
    }

    var localUserParticipant;
    var otherParticipants = participants.filter(function (p) {
      var isLocalUser = p.userId === unifyPhone.Conversation.localUser.userId;
      if (isLocalUser) {
        p.resolvedUser = p.type !== Constants.RTCParticipantType.USER;
        if (p.telephonyInfo) {
          // When telephony info is included with localUser, telephonyInfo.redirectingUser
          // contains the actual Redirecting user.
          item.redirectingUser = extractRedirectionInfo(p);
        }
        localUserParticipant = p;
      }
      return !isLocalUser;
    });

    if (item.rtc.missed && item.rtc.missed.pickingUpParticipant) {
      item.rtc.missed.pickingUpParticipant.resolvedUser = true;
      item.redirectedToUser = getUser(item.rtc.missed.pickingUpParticipant);
    } else if (item.rtc.ended && item.rtc.ended.pickFromParticipant) {
      item.rtc.ended.pickFromParticipant.resolvedUser = true;
      item.redirectingUser = getUser(item.rtc.ended.pickFromParticipant);
    }

    if (otherParticipants.length === 0) {
      // For some scenarios, like when users calls themselves or OSBiz DSS call move,
      // the rtcParticipants only include the local user.
      item.peerUser = getUser(localUserParticipant);
      // In this case a redirecting user is actually a redirectedTo user
      if (item.redirectingUser) {
        item.redirectedToUser = item.redirectingUser;
        delete item.redirectingUser;
      }
      return;
    }

    // Currently we don't support call logs with more than one user, so just get the first participant
    var participant = otherParticipants[0];

    var peerUser = getUser(participant);
    // When telephony info is included with the other user, telephonyInfo.redirectingUser
    // contains the RedirectedTo user.
    var redirectedToUser = extractRedirectionInfo(participant);

    item.isRedirected = false;
    // NGTC-9266: From now on, even for Outgoing calls, we will show:
    // 1. The actual called user, as the main peer journal number.
    // 2. The "Redirected to" info (if exists), as the additional journal information.
    item.peerUser = peerUser;
    if (redirectedToUser) {
      // Local user has redirected the call
      item.isRedirected = true;
      item.redirectedToUser = redirectedToUser;
    }
  }

  function extractRedirectionInfo(participant) {
    if (participant.telephonyInfo) {
      return getUser(participant.telephonyInfo.redirectingUser);
    }
    return null;
  }

  function getUser(participant) {
    if (!participant) {
      return null;
    }
    var user;
    var resolvedUserId = participant.resolvedUserId || (participant.resolvedUser && participant.userId);
    if (resolvedUserId) {
      user = unifyPhone.Conversation.getExtendedUser(resolvedUserId);
      // Create a new object with user as prototype
      user = user && Object.create(user);
    } else {
      user = Object.assign({}, participant);
      if (user.displayName === user.phoneNumber) {
        // Remove the displayName
        user.displayName = '';
      }
      user.userType = Constants.UserType.TELEPHONY;
      user = UserProfile.extend(user);
    }
    if (user) {
      user.isDialable = Utils.PHONE_PATTERN.test(participant.phoneNumber) || Utils.PHONE_PAC.test(participant.phoneNumber);
      user.phoneNumber = PhoneNumberFormatter.format(participant.phoneNumber);
    }
    return user;
  }

  function getCallLogType(item) {
    if (item.isRedirected) {
      return CallLogItemType.REDIRECTED;
    }

    switch (item.rtc.type) {
      case Constants.RTCItemType.ENDED:
        return item.sentByMe ? CallLogItemType.OUTGOING : CallLogItemType.INCOMING;

      case Constants.RTCItemType.MISSED:
        if (item.rtc.missed) {
          switch (item.rtc.missed.status) {
            case Constants.RTCItemMissed.DECLINED:
              return CallLogItemType.DECLINED;

            case Constants.RTCItemMissed.INVALID_NUMBER:
            case Constants.RTCItemMissed.TEMPORARILY_UNAVAILABLE:
            case Constants.RTCItemMissed.UNREACHABLE:
              if (item.sentByMe) {
                return CallLogItemType.FAILED;
              }
              break;
            case Constants.RTCItemMissed.USER_BUSY:
              return CallLogItemType.BUSY;
          }
        }
        return item.sentByMe ? CallLogItemType.NOT_ANSWERED : CallLogItemType.MISSED;
    }
    return CallLogItemType.UNKNOWN;
  }

  function getRtcItemState(rtc, sentByMe) {
    switch (rtc.type) {
      case Constants.RTCItemType.ENDED:
        return RtcItemState.ENDED;

      case Constants.RTCItemType.MISSED:
        if (rtc.missed) {
          switch (rtc.missed.status) {
            case Constants.RTCItemMissed.DECLINED:
              return RtcItemState.DECLINED;
            case Constants.RTCItemMissed.USER_BUSY:
              return RtcItemState.BUSY;
            case Constants.RTCItemMissed.INVALID_NUMBER:
              return RtcItemState.INVALID_NUMBER;
            case Constants.RTCItemMissed.TEMPORARILY_UNAVAILABLE:
              return RtcItemState.TEMPORARILY_UNAVAILABLE;
            case Constants.RTCItemMissed.UNREACHABLE:
              if (sentByMe) {
                return RtcItemState.UNKNOWN;
              }
              break;
          }
        }
        return sentByMe ? RtcItemState.NOT_ANSWERED : RtcItemState.MISSED;

      case Constants.RTCItemType.MOVED:
        return RtcItemState.MOVED;
    }
    return RtcItemState.UNKNOWN;
  }

  // Exports
  unifyPhone.CallLogItem = CallLogItem;
  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.CallLogItemType = CallLogItemType;
  unifyPhone.Enums.RtcItemState = RtcItemState;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
// Define global variables for JSHint

/*exported RegistrationState*/

/*
 * Registration state of a client/user (or guest)
 * @readonly
 * @enum {String}
 * @property Disconnected WebSocket is not connected
 * @property Connecting WebSocket is in the progress of connecting to the server
 * @property Reconnecting WebSocket is reconnecting
 * @property Connected WebSocket is connected
 * @property Waiting Guest Waiting Room state
 * @property Registered User is registered
 * @property LoggingOut User is logging out
 */
var RegistrationState = Object.freeze({
  Disconnected: 'Disconnected',
  DisconnectedFromTestCall: 'DisconnectedFromTestCall',
  Connecting: 'Connecting',
  Reconnecting: 'Reconnecting',
  Connected: 'Connected',
  Waiting: 'Waiting',
  Registered: 'Registered',
  RegisteredForTestCall: 'RegisteredForTestCall',
  LoggingOut: 'LoggingOut'
});

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  /*
   * ATC registration state
   * @readonly
   * @enum {String}
   * @property Disconnected - Client is not associated with ATC
   * @property Unregistered - Client is not registered with ATC
   * @property Registering - Client is registering with ATC
   * @property Registered - Client is registered with ATC
   * @property ForcedUnregistered - Client is unregistered by ATC
   */
  var AtcRegistrationState = Object.freeze({
    Disconnected: 'Disconnected',
    Unregistered: 'Unregistered',
    Registering: 'Registering',
    Registered: 'Registered',
    ForcedUnregistered: 'ForcedUnregistered'
  });

  // Exports
  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.AtcRegistrationState = AtcRegistrationState;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global module*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  var ChromeExtension = {
    SIGNATURE: 'ansibleChromeExtensionSignature'
  };

  ChromeExtension.DOMEvent = Object.freeze({
    TO_EXTENSION: 'toExtension',
    FROM_EXTENSION: 'fromExtension'
  });

  // Used to define if we have a callback or not.
  ChromeExtension.BgMsgType = Object.freeze({
    REQUEST: 'request',
    RESPONSE: 'response',
    EVENT: 'event'
  });

  // Features (Targets). As a general rule, we have message types for each feature (Target)
  ChromeExtension.BgTarget = Object.freeze({
    INTERNAL: 'internal',
    LOG: 'log',
    SCREEN_SHARE: 'screenShare',
    EXCHANGE_CONNECTOR: 'exchange_connector',
    HEADSET_APP: 'headsetAppManager',
    PRIVACY_SETTINGS: 'privacySettings'
  });

  // Error reasons
  ChromeExtension.ResponseError = Object.freeze({
    UNREGISTERED: 'unregistered', // The content script failed to communicate with the extension and unregistered itself.
    TIMEOUT: 'timeout', // Timeout waiting for response from extension
    UNSUPPORTED_REQUEST: 'unsupportedRequest', // Extension doesn't support this request
    INTERNAL_ERROR: 'internalError' // There was an internal error processing the request
  });

  // Log events
  ChromeExtension.BgLogMsgType = Object.freeze({
    LOG: 'log'
  });

  // Internal Targets
  ChromeExtension.BgInternalMsgType = Object.freeze({
    INIT_MSG: 'initMsg',
    INIT_MSG_ACK: 'initMsgAck',
    INJECT_SIGNATURE: 'injectChromeAppSignature',
    IS_ALIVE: 'isAlive',
    GET_WINDOW_SIZE: 'getWindowSize',
    EXTENSION_DISCONNECTED: 'extensionDisconnected',
    BRING_TO_FRONT: 'bringToFront',
    GET_FILE: 'getFile'
  });

  ChromeExtension.BgScreenShareMsgType = Object.freeze({
    CHOOSE_DESKTOP_MEDIA: 'cdm',
    CHOOSE_DESKTOP_MEDIA_DONE: 'cdmDone',
    CHOOSE_DESKTOP_MEDIA_CANCELLED: 'cdmCancelled'
  });

  ChromeExtension.BgPrivacySettingsMsgType = Object.freeze({
    SET_IP_HANDLING_POLICY: 'setWebRTCIPHandlingPolicy',
    GET_IP_HANDLING_POLICY: 'getWebRTCIPHandlingPolicy',
    IP_HANDLING_POLICY_CHANGED: 'webRTCIPHandlingPolicyChanged'
  });

  ChromeExtension.BgPrivacySettingsError = Object.freeze({
    NOT_AUTHORIZED: 'notAuthorized', // Extension has not been authorized to control policy settings
    NOT_CONTROLLABLE: 'notControllable', // Setting is locked by enterprise policies
    CONTROLLED_BY_OTHER_EXTENSIONS: 'controlledByOtherExtensions' // Setting is being controlled by another extension
  });

  ChromeExtension.BgExchangeMsgType = Object.freeze({
    CONNECT: 'connect',
    DISCONNECT: 'disconnect',
    SEARCH_CONTACTS: 'searchContacts',
    AUTODISCOVER_GET_SERVER: 'autodiscoverGetServer',
    GET_CONNECTION_STATUS: 'getConnectionStatus',
    GET_CAPABILITIES: 'getCapabilities',
    GET_CONTACT: 'getContact',
    STORE_EXCH_CREDENTIALS: 'storeCredentials',
    CONNECTION_STATUS: 'connectionStatus',
    GET_ALL_PERSONAL_CONTACTS: 'getAllPersonalContacts',
    CONTACT_FOLDERS_SYNC_STATE: 'contactFoldersSyncState',
    SYNC_ALL_PERSONAL_CONTACTS: 'syncAllPersonalContacts',
    GET_APPOINTMENTS: 'getAppointments',
    GET_STORED_CREDENTIALS: 'getStoredCredentials',
    GET_RENEWED_TOKEN: 'getRenewedToken',
    ON_RENEWED_TOKEN: 'onRenewedToken',
    GET_OOO_MSG: 'getOooMsg',
    GET_USER_AVAILABILITY: 'getUserAvailability'
  });

  // Internal Targets
  ChromeExtension.BgHeadsetAppMsgType = Object.freeze({
    GET_HEADSET_APP_STATUS: 'getHeadsetAppStatus',
    LAUNCH_HEADSET_APP: 'launchHeadsetApp',
    HEADSET_APP_LAUNCHED: 'headsetAppLaunched',
    HEADSET_APP_UNINSTALLED: 'headsetAppUninstalled',
    RESTART_HEADSET_APP: 'restartHeadsetApp'
  });

  // Possible response values sent by Exchange Connector
  ChromeExtension.ExchangeConnResponse = Object.freeze({
    OK: 'ok',
    NO_RESULT: 'noResult',
    FAILED: 'failed',
    COULD_NOT_CONNECT: 'couldNotConnect',
    UNAUTHORIZED: 'unauthorized',
    UNSUPPORTED_METHOD: 'unsupportedMethod',
    TIMED_OUT: 'timedOut',
    MAILBOX_NOT_FOUND: 'mailboxNotFound',
    NTLM_NOT_SUPPORTED: 'ntlmNotSupported'
  });

  // Headset Integration App statuses
  ChromeExtension.HeadsetAppStatus = Object.freeze({
    NO_MANAGEMENT_PERMISSION: 'noManagementPermission',
    NOT_INSTALLED: 'notInstalled',
    INSTALLED: 'installed',
    DISABLED: 'disabled',
    UNKNOWN: 'unknown'
  });

  ChromeExtension.Exchange = Object.freeze({
    CONFIGURED_PASSWORD_MASKED: '$$$$'
  });

  ChromeExtension.PortMsg = Object.freeze({
    CONNECT: 'connect',
    DISCONNECT: 'disconnect',
    MSG: 'msg'
  });

  // Exports
  unifyPhone.ChromeExtension = ChromeExtension;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = ChromeExtension;
  }

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var logger = unifyPhone.logger;
  var BaseCall = unifyPhone.BaseCall;
  var CallState = unifyPhone.Enums.CallState;
  var Constants = unifyPhone.Constants;
  var CstaCallState = unifyPhone.Enums.CstaCallState;
  var IncomingVideoContentType = unifyPhone.Constants.IncomingVideoContentType;
  var ParticipantState = unifyPhone.Enums.ParticipantState;
  var RealtimeMediaType = unifyPhone.Constants.RealtimeMediaType;
  var Utils = unifyPhone.Utils;

  var SERVER_ENDED_PRFX = 'SERVER_ENDED:';
  var CallServerTerminatedReason = Object.freeze({
    SERVER_ENDED_PRFX: SERVER_ENDED_PRFX
  });

  var CLIENT_ENDED_PRFX = 'CLIENT_ENDED:';
  var CallClientTerminatedReason = Object.freeze({
    CLIENT_ENDED_PRFX: CLIENT_ENDED_PRFX,
    ANOTHER_CLIENT_ANSWERED: 'ANOTHER_CLIENT_ANSWERED',
    ANOTHER_CLIENT_REJECTED: 'ANOTHER_CLIENT_REJECTED',
    ANOTHER_CLIENT_PULLED_CALL: 'ANOTHER_CLIENT_PULLED_CALL',
    CALL_MOVED_TO_ANOTHER_CONV: 'CALL_MOVED_TO_ANOTHER_CONV',
    CALLER_LEFT_CONFERENCE: 'CALLER_LEFT_CONFERENCE',
    ENDED_BY_ANOTHER_USER: 'ENDED_BY_ANOTHER_USER',
    LOST_WEBSOCKET_CONNECTION: 'LOST_WEBSOCKET_CONNECTION',
    NO_USERS_LEFT: 'NO_USERS_LEFT',
    REQUEST_TO_SERVER_FAILED: 'REQUEST_TO_SERVER_FAILED',
    RTC_SESSION_START_FAILED: 'RTC_SESSION_START_FAILED',
    SET_REMOTE_SDP_FAILED: 'SET_REMOTE_SDP_FAILED',
    USER_ENDED: 'USER_ENDED',
    LOST_MEDIA_STREAM: 'LOST_MEDIA_STREAM',
    ICE_TIMED_OUT: 'ICE_TIMED_OUT',
    USER_LOGGED_OUT: 'USER_LOGGED_OUT',
    PAGE_UNLOADED: 'PAGE_UNLOADED',
    DISCONNECTED: 'WS_DISCONNECTED',
    FAILED_TO_SEND: 'WS_FAILED_TO_SEND',
    REQUEST_TIMEOUT: 'WS_REQUEST_TIMEOUT',
    MEDIA_RENEGOTIATION: 'MEDIA_RENEGOTIATION'
  });

  var _isMobile = Utils.isMobile();

  // eslint-disable-next-line max-lines-per-function
  function LocalCall(conversation, options) {
    // NOSONAR

    // The following imports need to be defined inside LocalCall due to JS-SDK
    var WebRTCAdapter = unifyPhone.WebRTCAdapter;

    options = options || {};

    // Import here due to Circular dependency
    var RtcSessionController = unifyPhone.RtcSessionController;

    if (!conversation || !conversation.rtcSessionId) {
      throw new Error('Cannot create LocalCall object without a valid conversation');
    }

    // Call the base constructor
    LocalCall.parent.constructor.call(this, conversation, false);

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    //
    // These variables can only be accessed via getters/setters.
    // Critical variables that cannot be changed directly must be defined here.
    ///////////////////////////////////////////////////////////////////////////////////////
    var _that = this;
    var _sessionCtrl = null;
    var _hasTerminated = false;
    var _ringingTimer = null;
    var _hasActiveRemoteVideo = false;
    var _hasActiveRemoteScreenShareOnly = false;
    var _remoteVideoDisabled = false;
    var _remoteVideoScreenOnlyAllowed = false;
    var _remoteAudioDisabled = false;
    var _hasUnmutedParticipants = false;
    var _isMocked = false;
    var _isMeetingPointInvited = false;
    var _playbackDevice = null;
    var _recordingDevice = null;
    var _videoDevice = null;
    var _atcAdvancing = false;
    var _playbackDeviceList = [];
    var _recordingDeviceList = [];
    var _videoDeviceList = [];

    // The blob URLs for local video and remote audio/video (these are being deprecated)
    var _localVideoUrl = '';
    var _remoteAudioUrl = '';

    // Remember the current view for the call.
    // true: call stage is collapsed and user is viewing the conversation feed or details
    // false: call stage is expanded and user is in the conference object view
    var _conversationFeedView = false;

    var _videoReceiverConfiguration = {};
    var _networkQuality = null;

    var DEFAULT_RINGING_TIME = 15000;
    var RING_ALL_MAX_NUM_OF_PEERS = 9;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function createSessionController() {
      if (_sessionCtrl) {
        return;
      }
      if (options.reuseSessionCtrl) {
        _sessionCtrl = options.reuseSessionCtrl;
        return;
      }
      _sessionCtrl = new RtcSessionController({
        callId: _that.callId,
        isDirectCall: _that.isDirect,
        isTelephonyCall: _that.isTelephonyCall,
        isAtcPullCall: options.isAtcPullCall,
        reuseDesktopStreamFrom: options.reuseDesktopStreamFrom,
        midMappingEnabled: options.midMappingEnabled,
        canReceiveHdVideo: options.canReceiveHdVideo,
        supportsVideoReceiverConfiguration: _that.supportsVideoReceiverConfiguration()
      });
      _sessionCtrl.onMediaUpdate = onMediaUpdate;
      _sessionCtrl.onLocalVideoStream = onLocalVideoStream;
      _sessionCtrl.onRemoteStreams = onRemoteStreams;
      _sessionCtrl.onScreenSharePointerStatus = onScreenSharePointerStatus;
    }

    function unregisterSessionController() {
      if (_sessionCtrl) {
        _sessionCtrl.onMediaUpdate = null;
        _sessionCtrl.onLocalVideo = null;
        _sessionCtrl.onRemoteStreams = null;
        _sessionCtrl.onScreenSharePointerStatus = null;
      }
    }

    function onMediaUpdate() {
      if (!_sessionCtrl) {
        logger.debug('[LocalCall]: session controller not available - onMediaUpdate: callId =', _that.callId);
        return;
      }

      logger.debug('[LocalCall]: RtcSessionController - onMediaUpdate: callId =', _that.callId);

      _that.activeMediaType = _sessionCtrl.getActiveMediaType();
      logger.debug('[LocalCall]: Set active media type to ', _that.activeMediaType);

      _that.localMediaType = _sessionCtrl.getMediaConstraints();
      logger.debug('[LocalCall]: Set local media type to ', _that.localMediaType);

      _that.updateMediaType();
      _that.updateCallState();

      setVideoReceivers(RealtimeMediaType.VIDEO);
      setVideoReceivers(RealtimeMediaType.DESKTOP_SHARING);
    }

    function onLocalVideoStream(event) {
      logger.debug('[LocalCall]: RtcSessionController - onLocalVideoStream: callId =', _that.callId);
      _that.localVideoStream = event.stream;
      _that.localStreams.desktop = event.desktopStream;
      _that.localStreams.video = event.videoStream;
      _localVideoUrl = '';
      logger.debug('[LocalCall]: Set local video stream to ', (event.videoStream && event.videoStream.id) || '<null>');
      logger.debug('[LocalCall]: Set local desktop stream to ', (event.desktopStream && event.desktopStream.id) || '<null>');
    }

    function onScreenSharePointerStatus(data) {
      logger.debug('[LocalCall]: RtcSessionController - onScreenSharePointerStatus', data);
      _that.pointer = {
        isSupported: data.isSupported,
        isEnabled: data.isEnabled
      };
    }

    function onRemoteStreams(event) {
      logger.debug('[LocalCall]: RtcSessionController - onRemoteStreams: callId =', _that.callId);

      _that.remoteAudioStream = (event.audio && event.audio.stream) || null;
      _remoteAudioUrl = '';
      logger.debug('[LocalCall]: Set remote audio stream to', (_that.remoteAudioStream && _that.remoteAudioStream.id) || '<empty>');

      _that.remoteVideoStreams = event.video || [];
      logger.debug('[LocalCall]: Set remote video streams to', event.video || '<empty>');

      // Set the remote video stream for all participants
      _that.participants.forEach(function (p) {
        _that.setParticipantRemoteVideoStream(p);
      });
      _that.checkForActiveRemoteVideo();
    }

    function setVideoReceivers(mediaType) {
      var videoReceivers = _sessionCtrl.getAvailableVideoReceivers(mediaType);
      if (videoReceivers) {
        var updatedConfigurations = [];
        var currentConfigurations = _videoReceiverConfiguration[mediaType] || [];
        videoReceivers.forEach(function (elem) {
          var videoReceiver = currentConfigurations.find(function (cfg) {
            return cfg.id === elem.id;
          });
          if (!videoReceiver) {
            // create default
            videoReceiver = elem;
            videoReceiver.pinnedUser = null;
            videoReceiver.quality = Constants.VideoQuality.NORMAL;
            videoReceiver.disabled = false;
          }
          updatedConfigurations.push(videoReceiver);
        });
        _videoReceiverConfiguration[mediaType] = updatedConfigurations;
        logger.debug('[LocalCall] Updated video receiver configuration for media type ' + mediaType + ' is ', updatedConfigurations);
      }
    }

    /**
     * Return the video receiver configuration with current incoming video setting
     * (incoming VIDEO disabled or VIDEO and SCREEN disabled).
     *
     * @returns {Array} The modified list of receiver configurations
     */
    function getReceiverConfiguration() {
      var result = [];
      Object.keys(_videoReceiverConfiguration).forEach(function (mediaType) {
        var configuration = _videoReceiverConfiguration[mediaType];
        if (configuration) {
          if (_remoteVideoDisabled || (mediaType === RealtimeMediaType.VIDEO && _remoteVideoScreenOnlyAllowed)) {
            // Aadapt disabled setting based on remote video setting without changing actual configuration
            configuration = JSON.parse(JSON.stringify(configuration)); // Deep copy
            configuration.forEach(function (conf) {
              conf.disabled = true;
            });
          }
          Array.prototype.push.apply(result, configuration);
        }
      });
      return result;
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Properties
    ///////////////////////////////////////////////////////////////////////////////////////
    // Define the read-only properties to access the internal variables
    Object.defineProperties(this, {
      sessionCtrl: {
        get: function () {
          return _sessionCtrl;
        },
        enumerable: true,
        configurable: false
      },
      locallyMuted: {
        get: function () {
          return !!(_sessionCtrl && _sessionCtrl.isMuted());
        },
        enumerable: true,
        configurable: false
      },
      remoteVideoDisabled: {
        get: function () {
          return _remoteVideoDisabled;
        },
        enumerable: true,
        configurable: false
      },
      remoteVideoScreenOnlyAllowed: {
        get: function () {
          return _remoteVideoScreenOnlyAllowed;
        },
        enumerable: true,
        configurable: false
      },
      remoteAudioDisabled: {
        get: function () {
          return _remoteAudioDisabled;
        },
        enumerable: true,
        configurable: false
      },
      isMocked: {
        get: function () {
          return _isMocked;
        },
        enumerable: true,
        configurable: false
      },
      isMeetingPointInvited: {
        get: function () {
          return _isMeetingPointInvited;
        },
        enumerable: true,
        configurable: false
      },
      hasActiveRemoteScreenShareOnly: {
        get: function () {
          return _hasActiveRemoteScreenShareOnly;
        },
        enumerable: true,
        configurable: false
      },
      ringAllAllowed: {
        get: function () {
          // This property only verifies the number of users in the conversation, not the call state
          return !!(this.peerUsers && this.peerUsers.length > 0 && this.peerUsers.length <= RING_ALL_MAX_NUM_OF_PEERS);
        },
        enumerable: true,
        configurable: false
      },
      ringAllMaxNumber: {
        get: function () {
          return RING_ALL_MAX_NUM_OF_PEERS;
        },
        enumerable: true,
        configurable: false
      },
      conversationFeedView: {
        get: function () {
          return _conversationFeedView || this.state === CallState.Ringing;
        },
        set: function (value) {
          _conversationFeedView = value;
        },
        enumerable: true,
        configurable: false
      },
      playbackDevice: {
        get: function () {
          return _playbackDevice;
        },
        enumerable: true,
        configurable: false
      },
      recordingDevice: {
        get: function () {
          return _recordingDevice;
        },
        enumerable: true,
        configurable: false
      },
      videoDevice: {
        get: function () {
          return _videoDevice;
        },
        enumerable: true,
        configurable: false
      },
      playbackDeviceList: {
        get: function () {
          return _playbackDeviceList;
        },
        enumerable: true,
        configurable: false
      },
      recordingDeviceList: {
        get: function () {
          return _recordingDeviceList;
        },
        enumerable: true,
        configurable: false
      },
      videoDeviceList: {
        get: function () {
          return _videoDeviceList;
        },
        enumerable: true,
        configurable: false
      },
      atcAdvancing: {
        get: function () {
          return _atcAdvancing;
        },
        set: function (value) {
          _atcAdvancing = !!value;
        },
        enumerable: true,
        configurable: false
      },
      savedRTPStats: {
        get: function () {
          if (this.sessionCtrl) {
            var stats = this.sessionCtrl.getLastSavedStats();
            if (stats) {
              // stats is an object with each ssrc stats as a property
              // Here we are organizing the ssrc's into audio and video groups:
              // {
              //    audio: { ssrc1: {...}, ssrc2: {...}},
              //    video: { ssrc3: {...}, ssrc4: {...}, bw: {...}}
              // }
              var organizedStats = {};
              Object.keys(stats).forEach(function (ssrcId) {
                var ssrc = stats[ssrcId];
                var media = ssrc.media; // audio or video
                if (media) {
                  if (!organizedStats[media]) {
                    organizedStats[media] = {};
                  }
                  organizedStats[media][ssrcId] = ssrc;
                }
              });
              return organizedStats;
            }
            return stats;
          }
          return null;
        },
        enumerable: true,
        configurable: false
      },
      isDtmfAllowed: {
        get: function () {
          if (this.checkState([CallState.Active, CallState.Delivered]) || this.isOsBizFirstCall) {
            if (this.sessionCtrl && !this.sessionCtrl.canSendDTMFDigits()) {
              if (this.checkState(CallState.Active)) {
                return !!this.atcCallInfo; // For ATC calls we can use CSTA to generate digits (only in Active state)
              }
              return false;
            }
            return true;
          }
          return false;
        },
        enumerable: true,
        configurable: false
      },
      networkQuality: {
        get: function () {
          return _networkQuality;
        },
        set: function (value) {
          _networkQuality = value;
        },
        enumerable: true,
        configurable: false
      }
    });

    if (_isMobile) {
      // The following properties have been deprecated for Web/DA and must only be defined for mobile clients
      Object.defineProperties(this, {
        localVideoUrl: {
          get: function () {
            if (_that.localVideoStream) {
              _localVideoUrl = _localVideoUrl || WebRTCAdapter.createObjectURL(_that.localVideoStream);
            } else {
              _localVideoUrl = '';
            }
            return _localVideoUrl;
          },
          enumerable: true,
          configurable: false
        },
        remoteAudioUrl: {
          get: function () {
            if (_that.remoteAudioStream) {
              _remoteAudioUrl = _remoteAudioUrl || WebRTCAdapter.createObjectURL(_that.remoteAudioStream);
            } else {
              _remoteAudioUrl = '';
            }
            return _remoteAudioUrl;
          },
          enumerable: true,
          configurable: false
        },
        remoteVideoUrlStreams: {
          get: function () {
            return _that.remoteVideoStreams.map(function (stream) {
              if (!stream.url) {
                stream.url = WebRTCAdapter.createObjectURL(stream.stream);
              }
              return {
                streamId: stream.streamId,
                url: stream.url
              };
            });
          },
          enumerable: true,
          configurable: false
        }
      });
    }

    // We should work with stream objects directly (as opposed to their URLs)
    this.localVideoStream = null;
    this.localStreams = {};
    this.remoteAudioStream = null;
    this.remoteVideoStreams = [];

    // Stores the media types negotiated at the RTC level.
    this.activeMediaType = { audio: false, video: false, desktop: false };
    this.ringingTimeout = DEFAULT_RINGING_TIME;

    // Is whiteboard enabled for the call
    this.whiteboardEnabled = false;
    // Object that contains whiteboard data
    this.whiteboard = null;

    // Display feature selected for CMR
    this.screenEnabledFeature = null;

    // Is conference poll enabled for the call
    this.pollEnabled = false;
    // Object that contains conference poll data
    this.poll = null;

    // Is screenshare pointing supported and enabled
    this.pointer = {
      isSupported: false,
      isEnabled: false
    };

    // Bad quality notification bar on call stage
    this.badQualityNotification = {
      detected: false,
      acted: false,
      dismissed: false
    };

    // Current client ID associated to this call
    this.clientId = options.clientId;

    // In case of OsBiz local call consultation or campON, these flags indicate the first and seconds calls
    this.isOsBizFirstCall = false;
    this.isOsBizSecondCall = false;

    this.holdInProgress = false;
    this.retrieveInProgress = false;

    this.isSTC = options.isSTC;
    this.stcCapabilities = options.stcCapabilities;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Members
    // (Only includes members which need to access private data)
    ///////////////////////////////////////////////////////////////////////////////////////
    this.terminate = function () {
      if (_hasTerminated) {
        return;
      }

      logger.debug('[LocalCall]: Terminating call with callId =', this.callId);
      if (this.isOsBizSecondCall) {
        _sessionCtrl = null;
      } else if (_sessionCtrl) {
        unregisterSessionController();
        _sessionCtrl.terminate();
      }

      _localVideoUrl = '';
      _remoteAudioUrl = '';
      this.localVideoStream = null;
      this.localStreams = {};
      this.remoteAudioStream = null;
      this.remoteVideoStreams = [];

      // Call the prototype's terminate() function
      LocalCall.prototype.terminate.call(this);

      this.stopRingingTimer();
      _hasTerminated = true;
    };

    this.setMockedCall = function () {
      logger.debug('[LocalCall]: Setting call as mocked');
      _isMocked = true;
      _sessionCtrl && _sessionCtrl.setMockedCall();

      this.participants.forEach(function (p) {
        p.streamId = p.mediaType.video ? 'mock' : '';
        p.screenStreamId = p.mediaType.desktop ? 'mock' : '';
      });

      // Initialize the remote stream after a 100 ms timeout to simulate
      // the delay in the real scenarios.
      window.setTimeout(function () {
        if (this.state !== CallState.Terminated) {
          logger.debug('[LocalCall]: Initialize remote mocked streams');

          onRemoteStreams({
            audio: null,
            video: [
              {
                streamId: 'mock',
                stream: 'content/images/mock/trailer.mp4'
              }
            ]
          });
          if (_sessionCtrl) {
            _sessionCtrl.onRemoteStreams = null;
          }
        }
      }, 100);
    };

    this.startRingingTimer = function (cb) {
      if (typeof cb !== 'function') {
        return;
      }

      logger.info('[LocalCall]: Starting ringing timer for callId =', _that.callId);
      _ringingTimer = window.setTimeout(function () {
        logger.info('[LocalCall]: Ringing timeout for callId =', _that.callId);
        _ringingTimer = null;
        cb();
      }, this.ringingTimeout);
    };

    this.stopRingingTimer = function () {
      if (_ringingTimer) {
        logger.info('[LocalCall]: Cancelling ringing timer for callId =', _that.callId);
        window.clearTimeout(_ringingTimer);
        _ringingTimer = null;
      }
    };

    // Override hasRemoteVideo to check if we have a valid remote video stream for at
    // least one participant
    this.hasRemoteVideo = function () {
      return _hasActiveRemoteVideo;
    };

    this.checkForActiveRemoteVideo = function () {
      if (_remoteVideoDisabled) {
        _hasActiveRemoteVideo = false;
        _hasActiveRemoteScreenShareOnly = false;
      } else {
        _hasActiveRemoteVideo = this.participants.some(function (p) {
          return !!p.videoStream;
        });
        if (_hasActiveRemoteVideo) {
          // Incoming video stream is active. Check if it is screen share only
          _hasActiveRemoteScreenShareOnly = !this.participants.some(function (p) {
            return p.mediaType.video;
          });
        } else {
          _hasActiveRemoteScreenShareOnly = false;
        }
      }
      logger.debug('[LocalCall]: Set hasActiveRemoteVideo to ', _hasActiveRemoteVideo);
    };

    this.enableRemoteVideoScreenOnly = function () {
      if (_remoteVideoDisabled) {
        return null;
      }
      _remoteVideoScreenOnlyAllowed = true;
      this.checkForActiveRemoteVideo();

      this.participants.forEach(function (p) {
        p.videoStream = null;
        p.streams = {};
        if (_that.isDirect) {
          p.streamId = '';
        }
      });
      this.sessionCtrl && this.sessionCtrl.enableRemoteVideoScreenOnly();
      logger.debug('[LocalCall]: Enabled receiving remote screen share only');
      return getReceiverConfiguration();
    };

    this.disableRemoteVideoScreenOnly = function () {
      _remoteVideoScreenOnlyAllowed = false;
      this.sessionCtrl && this.sessionCtrl.disableRemoteVideoScreenOnly();
      logger.debug('[LocalCall]: Disabled receiving remote screen share only');
      return getReceiverConfiguration();
    };

    this.enableRemoteVideo = function () {
      if (!_remoteVideoDisabled && !_remoteVideoScreenOnlyAllowed) {
        return null;
      }
      _remoteVideoDisabled = false;
      this.disableRemoteVideoScreenOnly();
      this.checkForActiveRemoteVideo();
      this.sessionCtrl && this.sessionCtrl.enableRemoteVideo();
      logger.debug('[LocalCall]: Enabled remote video');
      return getReceiverConfiguration();
    };

    this.disableRemoteVideo = function () {
      if (_remoteVideoDisabled) {
        return null;
      }

      _remoteVideoDisabled = true;
      _hasActiveRemoteVideo = false;
      this.disableRemoteVideoScreenOnly();

      this.participants.forEach(function (p) {
        p.videoStream = null;
        p.streams = {};
        if (_that.isDirect) {
          p.streamId = '';
        }
      });
      this.sessionCtrl && this.sessionCtrl.disableRemoteVideo();
      logger.debug('[LocalCall]: Disabled remote video');
      return getReceiverConfiguration();
    };

    this.enableRemoteAudio = function () {
      if (!_remoteAudioDisabled) {
        return;
      }
      _remoteAudioDisabled = false;
      logger.debug('[LocalCall]: Enabled remote audio.');
    };

    this.disableRemoteAudio = function () {
      if (_remoteAudioDisabled) {
        return;
      }
      _remoteAudioDisabled = true;
      logger.debug('[LocalCall]: Disabled remote audio.');
    };

    this.setMeetingPointInviteState = function (inviteState) {
      _isMeetingPointInvited = !!inviteState;
      logger.debug('[LocalCall]: Set UnifyPhone Meeting Point state to ', inviteState);
    };

    this.setHasUnmutedParticipants = function () {
      _hasUnmutedParticipants = this.participants.some(function (p) {
        return !p.muted;
      });
    };

    this.hasUnmutedParticipants = function () {
      return _hasUnmutedParticipants;
    };

    this.getUnmutedParticipantIds = function () {
      var unmutedParticipants = [];
      this.participants.forEach(function (p) {
        if (!p.muted) {
          unmutedParticipants.push(p.userId);
        }
      });
      return unmutedParticipants;
    };

    this.getMediaNode = function () {
      return this.isTestCall ? RtcSessionController.mediaNode : null;
    };

    this.updateMediaDevices = function () {
      var getDeviceLabels = function (devices) {
        if (!devices) {
          return [];
        }
        return devices
          .filter(function (d) {
            return d.id !== 'default' && d.id !== 'communications' && d.label;
          })
          .map(function (d) {
            return d.label;
          });
      };

      WebRTCAdapter.getMediaSources(function (audioSources, videoSources, audioOutputDevices) {
        if (WebRTCAdapter.getMediaSourcesSupported) {
          var dev = Utils.selectMediaDevice(audioOutputDevices, RtcSessionController.playbackDevices);
          _playbackDevice = dev && dev.label;
          _playbackDeviceList = getDeviceLabels(audioOutputDevices);
          dev = Utils.selectMediaDevice(audioSources, RtcSessionController.recordingDevices);
          _recordingDevice = dev && dev.label;
          _recordingDeviceList = getDeviceLabels(audioSources);
          dev = Utils.selectMediaDevice(videoSources, RtcSessionController.videoDevices);
          _videoDevice = dev && dev.label;
          _videoDeviceList = getDeviceLabels(videoSources);
        } else {
          // Mobile devices don't support getMediaSources, so get these devices through Audio object
          _playbackDevice = Audio.getPlaybackDevice && Audio.getPlaybackDevice();
          _recordingDevice = Audio.getRecordingDevice && Audio.getRecordingDevice();
          _videoDevice = Audio.getVideoDevice && Audio.getVideoDevice();
        }
        logger.debug('[LocalCall]: Updated media devices ', {
          callId: _that.callId,
          recording: _recordingDevice,
          playback: _playbackDevice,
          video: _videoDevice
        });
      });
    };

    this.setIncomingVideoStreams = function (configuration) {
      var videoConfiguration = _videoReceiverConfiguration[RealtimeMediaType.VIDEO];
      var screenConfiguration = _videoReceiverConfiguration[RealtimeMediaType.DESKTOP_SHARING];
      var preferredStreamIds = [];
      var videoContentArray = [];
      var showScreen = false;
      var disableVideoLines = true;
      if (!configuration) {
        // reset view so we just apply default configuration but with disabled flag to false
        disableVideoLines = false;
        showScreen = true;
      } else {
        configuration.forEach(function (c) {
          switch (c.contentType) {
            case IncomingVideoContentType.VIDEO:
              var userId = c.userId;
              var participant =
                userId &&
                _that.participants.find(function (p) {
                  return p.userId === userId;
                });
              var preferredId;
              if (participant && participant.streams.video) {
                // If participant video is already received keep participant on same video line to avoid flickering
                preferredId = participant.streams.video.mid;
                preferredStreamIds.push(preferredId);
              }
              videoContentArray.push({
                id: preferredId,
                quality: c.quality || Constants.VideoQuality.NORMAL,
                pinnedUser: userId || null
              });
              break;
            case IncomingVideoContentType.SCREEN:
              showScreen = true;
              break;
          }
        });
      }

      // reset all video lines
      if (videoConfiguration) {
        videoConfiguration.forEach(function (vc) {
          vc.pinnedUser = null;
          vc.quality = Constants.VideoQuality.NORMAL;
          vc.disabled = disableVideoLines;
        });

        // apply updated configurations
        videoContentArray.forEach(function (videoContent) {
          var id = videoContent.id;
          videoConfiguration.some(function (vc) {
            var vcId = vc.id;
            if ((id && vcId === id) || (!id && preferredStreamIds.indexOf(vcId) === -1)) {
              vc.pinnedUser = videoContent.pinnedUser;
              vc.quality = videoContent.quality;
              vc.disabled = false;
              if (!id) {
                preferredStreamIds.push(vcId);
              }
              return true;
            }
            return false;
          });
        });
      }

      // enable/disable screen share line
      screenConfiguration &&
        screenConfiguration.forEach(function (sc) {
          sc.quality = Constants.VideoQuality.NORMAL;
          sc.disabled = !showScreen;
        });

      return getReceiverConfiguration();
    };

    /**
     * Probe if video receiver configuration can be used.
     *
     * @returns {boolean} true if video receiver configuration can be used
     */
    this.supportsVideoReceiverConfiguration = function () {
      // disable video receiver API for direct calls and guests in Events
      return !_that.isDirect;
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////////////////
    createSessionController();
    _that.updateMediaDevices();

    logger.info('[LocalCall]: Created new WebRTC call with callID = ', this.callId);
  }

  Utils.inherit(LocalCall, BaseCall);

  function setMockStreamId(call, participant) {
    participant.streamId = '';
    participant.screenStreamId = '';
    if (participant.mediaType.desktop) {
      participant.screenStreamId = 'mock';
    }
    if (participant.mediaType.video) {
      participant.streamId = 'mock';
    }
  }

  LocalCall.prototype.hasRemoteMedia = function () {
    return !!(this.remoteAudioStream || this.remoteVideoStreams.length);
  };

  LocalCall.prototype.hasRemoteScreenShare = function () {
    return this.mediaType.desktop && !this.remoteVideoDisabled && (!this.localMediaType.desktop || (this.isDirect && this.participants[0].mediaType.desktop));
  };

  LocalCall.prototype.addParticipant = function (participant) {
    if (this.isMocked && participant && participant.mediaType) {
      setMockStreamId(this, participant);
    }
    var addedParticipant = LocalCall.parent.addParticipant.apply(this, arguments);
    if (addedParticipant) {
      this.setParticipantRemoteVideoStream(addedParticipant);
      this.checkForActiveRemoteVideo();
      this.setHasUnmutedParticipants();
    }
    return addedParticipant;
  };

  LocalCall.prototype.updateParticipant = function (participant) {
    if (this.isMocked && participant && participant.mediaType) {
      setMockStreamId(this, participant);
    }
    var updatedParticipant = LocalCall.parent.updateParticipant.apply(this, arguments);
    if (updatedParticipant) {
      this.setParticipantRemoteVideoStream(updatedParticipant);
      this.checkForActiveRemoteVideo();
      this.setHasUnmutedParticipants();
    }
    return updatedParticipant;
  };

  LocalCall.prototype.removeParticipant = function () {
    var removedParticipant = LocalCall.parent.removeParticipant.apply(this, arguments);
    if (removedParticipant) {
      this.setHasUnmutedParticipants();
      this.checkForActiveRemoteVideo();
    }
    return removedParticipant;
  };

  LocalCall.prototype.setParticipantRemoteVideoStream = function (participant) {
    participant.videoStream = null;
    participant.streams = {};

    if (this.isDirect) {
      participant.streamId = '';
    }

    if (this.remoteVideoDisabled) {
      return;
    }

    if (participant.mediaType && !participant.mediaType.video && !participant.mediaType.desktop) {
      logger.debug('[LocalCall]: The participant does not have video or screen-share. Do not set video stream. userId = ', participant.userId);
      return;
    }

    if (this.isDirect) {
      if (this.remoteVideoStreams && this.remoteVideoStreams.length) {
        var remoteStream = this.remoteVideoStreams[this.remoteVideoStreams.length - 1];
        participant.streamId = remoteStream.streamId;
        participant.videoStream = remoteStream.stream;
      }
    } else {
      if (!participant.streamId && !participant.screenStreamId) {
        logger.info('[LocalCall]: The participant does not have a video streamId. userId = ', participant.userId);
        return;
      }

      if (this.remoteVideoStreams && this.remoteVideoStreams.length) {
        this.remoteVideoStreams.forEach(function (u) {
          if (u.streamId === participant.streamId) {
            participant.streams.video = u.stream;
          } else if (u.streamId === participant.screenStreamId) {
            participant.streams.desktop = u.stream;
          }
        });
        participant.videoStream = participant.streams.desktop || participant.streams.video || null;
      }
    }

    if (!participant.videoStream) {
      logger.debug('[LocalCall]: We still do not have a video stream for the participant. ', participant);
    } else {
      // If the participant has a remote video stream, it must be Active
      participant.pcState = participant.muted ? ParticipantState.Muted : ParticipantState.Active;
      logger.debug('[LocalCall]: Set remote video stream for participant: ', participant);
    }
  };

  LocalCall.prototype.hasLocalScreenShare = function () {
    return this.sessionCtrl && this.sessionCtrl.hasScreenShare();
  };

  LocalCall.prototype.isMuted = function () {
    return this.remotelyMuted || this.locallyMuted;
  };

  LocalCall.prototype.mute = function (cb) {
    if (this.checkState(CallState.Terminated)) {
      logger.error('[LocalCall]: Cannot mute a call that has already been terminated');
      return false;
    }

    logger.debug('[LocalCall]: Mute call with callId =', this.callId);
    return this.sessionCtrl && this.sessionCtrl.mute(cb);
  };

  LocalCall.prototype.unmute = function (cb) {
    if (this.checkState(CallState.Terminated)) {
      logger.error('[LocalCall]: Cannot mute a call that has already been terminated');
      return false;
    }

    logger.debug('[LocalCall]: Unmute call with callId =', this.callId);
    return this.sessionCtrl && this.sessionCtrl.unmute(cb);
  };

  LocalCall.prototype.isLocalMuteAllowed = function () {
    return this.sessionCtrl && this.sessionCtrl.isLocalMuteAllowed();
  };

  LocalCall.prototype.toggleMute = function () {
    if (this.sessionCtrl && this.sessionCtrl.isMuted()) {
      return this.unmute();
    }
    return this.mute();
  };

  LocalCall.prototype.setActiveSpeakers = function (activeSpeakers) {
    if (!activeSpeakers) {
      return false;
    }
    if (!(activeSpeakers instanceof Array)) {
      activeSpeakers = [activeSpeakers];
    }
    var changed = false;
    this.participants.forEach(function (p) {
      var active = activeSpeakers.indexOf(p.userId) !== -1;
      if (p.activeSpeaker !== active) {
        p.activeSpeaker = active;
        changed = true;
      }
    });
    return changed;
  };

  LocalCall.prototype.updateCallState = function () {
    if (!this.state.established || !this.sessionCtrl) {
      // The call has not yet been established or sessionCtrl is not available
      return;
    }

    var newState;
    if (
      !this.sessionCtrl.isConnected() || // Media is not connected
      (!this.isDirect && !this.hasOtherParticipants()) || // Group conference with no other participants
      (this.isATCCall &&
        !this.getCstaState().established && // ATC call not established
        !this.checkCstaState([CstaCallState.Initiated, CstaCallState.Delivered]))
    ) {
      newState = CallState.Waiting;
    } else if (this.sessionCtrl.isHolding() || this.checkCstaState(CstaCallState.Holding)) {
      newState = CallState.Holding;
    } else if (this.sessionCtrl.isHeld()) {
      newState = CallState.Held;
    } else {
      newState = CallState.Active;
    }
    this.setState(newState);
  };

  LocalCall.prototype.isHolding = function () {
    if (this.atcCallInfo) {
      return LocalCall.parent.isHolding.apply(this, arguments);
    }
    return this.checkState([CallState.Holding, CallState.HoldOnHold]);
  };

  LocalCall.prototype.isConsultAllowed = function () {
    if (this.atcCallInfo) {
      return this.atcCallInfo.isConsultAllowed();
    }
    return this.state.established;
  };

  LocalCall.prototype.isHoldInProgress = function () {
    if (this.atcCallInfo) {
      return this.atcCallInfo.isHoldInProgress();
    }
    return this.holdInProgress;
  };

  LocalCall.prototype.setHoldInProgress = function () {
    if (this.atcCallInfo) {
      this.atcCallInfo.setHoldInProgress();
    }
    this.holdInProgress = true;
  };

  LocalCall.prototype.clearHoldInProgress = function () {
    if (this.atcCallInfo) {
      this.atcCallInfo.clearHoldInProgress();
    }
    this.holdInProgress = false;
  };

  LocalCall.prototype.isRetrieveInProgress = function () {
    if (this.atcCallInfo) {
      return this.atcCallInfo.isRetrieveInProgress();
    }
    return this.retrieveInProgress;
  };

  LocalCall.prototype.setRetrieveInProgress = function () {
    if (this.atcCallInfo) {
      this.atcCallInfo.setRetrieveInProgress();
    }
    this.retrieveInProgress = true;
  };

  LocalCall.prototype.clearRetrieveInProgress = function () {
    if (this.atcCallInfo) {
      this.atcCallInfo.clearRetrieveInProgress();
    }
    this.retrieveInProgress = false;
  };

  LocalCall.prototype.isTransferCallAllowed = function () {
    if (this.atcCallInfo) {
      return this.atcCallInfo.isTransferCallAllowed();
    }
    return (
      !!this.stcCapabilities &&
      this.stcCapabilities.includes(Constants.StcCapabilities.STC_TRANSFER) &&
      this.checkState([CallState.Active, CallState.Delivered])
    );
  };

  LocalCall.prototype.isTransferAllowed = function () {
    if (this.atcCallInfo) {
      return this.atcCallInfo.isTransferAllowed();
    }
    return (
      !!this.stcCapabilities && this.stcCapabilities.includes(Constants.StcCapabilities.STC_TRANSFER) && this.state.established && !this.isDirectUpgradedToConf
    );
  };

  LocalCall.prototype.isConferenceCallAllowed = function () {
    if (this.atcCallInfo) {
      return this.atcCallInfo.isConferenceCallAllowed();
    }
    return !!this.stcCapabilities && this.stcCapabilities.includes(Constants.StcCapabilities.STC_MERGE) && this.state.established;
  };

  // Exports
  unifyPhone.LocalCall = LocalCall;
  unifyPhone.Enums.CallServerTerminatedReason = CallServerTerminatedReason;
  unifyPhone.Enums.CallClientTerminatedReason = CallClientTerminatedReason;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

/* global Promise */

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var logger = unifyPhone.logger;
  var RtpQualityLevel = unifyPhone.Enums.RtpQualityLevel;
  var RtpStatsConfig = unifyPhone.RtpStatsConfig;
  var Utils = unifyPhone.Utils;
  var VideoResolutionLevel = unifyPhone.Enums.VideoResolutionLevel;

  var THRESHOLD_RECURRENCE = Utils.isMobile() ? 2 : 3;

  var ThresholdType = Object.freeze({
    MAX: 'MAX',
    MIN: 'MIN'
  });

  var RTPAudioStatType = Object.freeze({
    GOOG_JITTER_RECEIVED: {
      id: Constants.AudioQualityParam.JITTER,
      StatName: 'jr',
      StatText: 'googJitterReceived',
      Threshold: [50, 300, 500],
      ThresholdType: ThresholdType.MAX
    },
    GOOG_RTT: {
      id: Constants.AudioQualityParam.RTT,
      StatName: 'rtt',
      StatText: 'googRtt',
      Threshold: [300, 600, 800],
      ThresholdType: ThresholdType.MAX
    },
    PACKET_LOSS_RECV: {
      id: Constants.AudioQualityParam.PL_RECV,
      StatName: 'plr',
      StatText: 'packetLossRecv',
      Threshold: [0.05, 0.2, 0.3],
      ThresholdType: ThresholdType.MAX
    },
    PACKET_LOSS_SEND: {
      id: Constants.AudioQualityParam.PL_SEND,
      StatName: 'pll',
      StatText: 'packetLossSend',
      Threshold: [0.05, 0.2, 0.3],
      ThresholdType: ThresholdType.MAX
    },
    NO_PACKETS_RECV: {
      id: Constants.AudioQualityParam.NO_PACKETS_RECV,
      StatName: 'npr',
      StatText: 'noPacketsReceived',
      Threshold: [0],
      reportIssues: true
    },
    NO_PACKETS_SEND: {
      id: Constants.AudioQualityParam.NO_PACKETS_SENT,
      StatName: 'nps',
      StatText: 'noPacketsSent',
      Threshold: [0],
      reportIssues: true
    },
    ECHO_LIKELIHOOD: {
      // Deprecated (will be removed once we move to promise based stats)
      id: Constants.AudioQualityParam.ECHO,
      StatName: 'ecmax',
      StatText: 'googResidualEchoLikelihoodRecentMax',
      Threshold: [0.5],
      ThresholdType: ThresholdType.MAX
    }
  });

  var RTPVideoStatType = Object.freeze({
    GOOG_RTT: {
      id: Constants.VideoQualityParam.RTT,
      StatName: 'rtt',
      StatText: 'googRtt',
      Threshold: [300, 600, 800],
      ThresholdType: ThresholdType.MAX
    },
    PACKET_LOSS_RECV: {
      id: Constants.VideoQualityParam.PL_RECV,
      StatName: 'plr',
      StatText: 'packetLossRecv',
      Threshold: [0.05, 0.2, 0.3],
      ThresholdType: ThresholdType.MAX,
      StreamQualityThreshold: 0.1
    },
    PACKET_LOSS_SEND: {
      id: Constants.VideoQualityParam.PL_SEND,
      StatName: 'pll',
      StatText: 'packetLossSend',
      Threshold: [0.05, 0.2, 0.3],
      ThresholdType: ThresholdType.MAX
    }
  });

  // Dynamic, depends on the "desired" resolution
  var RTPHDVideoStatType = JSON.stringify({
    FRAME_HEIGHT_SENT: {
      id: Constants.VideoQualityParam.FRAME_HEIGHT_SENT,
      StatName: 'fhs',
      StatText: 'googFrameHeightSent',
      Threshold: [],
      ThresholdType: ThresholdType.MIN
    },
    FRAME_WIDTH_SENT: {
      id: Constants.VideoQualityParam.FRAME_WIDTH_SENT,
      StatName: 'fws',
      StatText: 'googFrameWidthSent',
      Threshold: [],
      ThresholdType: ThresholdType.MIN
    },
    FRAME_RATE_SENT: {
      id: Constants.VideoQualityParam.FRAME_RATE_SENT,
      StatName: 'frs',
      StatText: 'googFrameRateSent',
      Threshold: [],
      ThresholdType: ThresholdType.MIN
    },
    AVAIL_SEND_BW: {
      id: Constants.VideoQualityParam.BW_SEND,
      StatName: 'abw',
      StatText: 'googAvailableSendBandwidth',
      Threshold: [],
      ThresholdType: ThresholdType.MIN
    },
    TRANSMIT_BIT_RATE: {
      id: Constants.VideoQualityParam.TRX_BITRATE,
      StatName: 'tbr',
      StatText: 'googTransmitBitrate',
      Threshold: [],
      ThresholdType: ThresholdType.MIN
    }
  });

  var SsrcContents = Object.freeze({
    AUDIO: 'audio',
    VIDEO: 'video',
    SCREEN: 'screen'
  });

  var CallStats = Object.freeze({
    audioOutputLevel: 'aol',
    bytesReceived: 'or',
    bytesSent: 'os',
    googAvailableSendBandwidth: 'abw',
    /*
        googAvgEncodeMs: 'ae',
        googBandwidthLimitedResolution: 'blr',
        googCaptureJitterMs: 'cj',
        googCaptureQueueDelayMsPerS: 'cqd',
        googCpuLimitedResolution: 'clr',
        googCurrentDelayMs: 'cdm',
        googDecodeMs: 'dm',
        googEchoCancellationEchoDelayMedian: 'ecedm',
        googEchoCancellationEchoDelayStdDev: 'ecedsd',
        googEchoCancellationQualityMin: 'ecqm',
        googEchoCancellationReturnLoss: 'ecrl',
        googEchoCancellationReturnLossEnhancement: 'ecrle',
        */
    googResidualEchoLikelihoodRecentMax: 'ecmax',
    /*
        googEncodeUsagePercent: 'eup',
        googExpandRate: 'ert',
        googFirsReceived: 'fr',
        googFirsSent: 'fs',
        */
    googFrameHeightReceived: 'fhr',
    googFrameHeightSent: 'fhs',
    googFrameWidthReceived: 'fwr',
    googFrameWidthSent: 'fws',
    googFrameRateDecoded: 'frd',
    googFrameRateSent: 'frs',
    googFrameRateReceived: 'frr',
    /*
        googFrameRateInput: 'fri',
        googFrameRateOutput: 'fro',
        googJitterBufferMs: 'jb',
        */
    googJitterReceived: 'jr',
    /*
        googMaxDecodeMs: 'mdm',
        googMinPlayoutDelayMs: 'mpd',
        googNacksReceived: 'nr',
        googNacksSent: 'ns',
        googPlisReceived: 'plr',
        googPlisSent: 'pls',
        googPreferredJitterBufferMs: 'pjb',
        googRenderDelayMs: 'rd',
        */
    googRtt: 'rtt',
    /*
        googTargetDelayMs: 'td',
        */
    googTransmitBitrate: 'tbr',
    /*
        googViewLimitedResolution: 'vlr',
        */
    packetsLost: 'pl',
    packetsReceived: 'pr',
    packetsSent: 'ps'
  });

  var browserInfo = Utils.getBrowserInfo();

  // eslint-disable-next-line max-lines-per-function
  function CallStatsHandlerUnified(peerConnections, isDirectCall) {
    // NOSONAR
    if (!Array.isArray(peerConnections)) {
      throw new Error('peerConnections must be an array');
    }

    /////////////////////////////////////////////////////////////////////////////
    // Constants
    /////////////////////////////////////////////////////////////////////////////
    var LOG_SKIP_COUNTER = 6; // If there's no issues, log stats every 30 seconds
    var MAX_VIOLATIONS = 20; // Only log the violations up to this limit
    var STREAM_QUALITY_SUMMARY_ITERATIONS = 6;

    /////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    /////////////////////////////////////////////////////////////////////////////
    var _peerConnections = peerConnections.filter(function (pc) {
      return pc;
    });
    if (!peerConnections.length) {
      throw new Error('Cannot create CallStatsHandler object without at least one peer connection');
    }

    var _nics = unifyPhone.WebRTCAdapter.getNetmaskMap();

    var _isDirectCall = !!isDirectCall;
    var _options = {};

    var _rtpStatsDelayUponAnsTimer = null;
    var _rtpStatsCollectionTimer = null;
    var _rtpStatsCollectionCounter = 0;

    var _savedRTPStats = {};
    var _savedChannelInfo = {};
    var _violationsInProgress = {};
    var _videoIssues = {};
    var _channelIssues = {};
    var _qosCollected;
    var _consecutiveBadQualityCounter = 0;
    var _consecutiveGoodQualityCounter = 0;
    var _lowQualityVideoTracks = {};
    var _streamQualityData = null;
    var _tmpStreamQualityData = {
      audio: null
    };

    var _streamQualityStatsToCheck = [RTPAudioStatType.GOOG_JITTER_RECEIVED, RTPAudioStatType.PACKET_LOSS_RECV, RTPAudioStatType.GOOG_RTT];

    // Create copy of HD stat object so we can modify thresholds depending on video resolution
    var _rtpHDVideoStatType = JSON.parse(RTPHDVideoStatType);

    var _that = this;

    /////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    /////////////////////////////////////////////////////////////////////////////
    function normalizeCandidateType(type) {
      switch (type) {
        case 'local':
          return 'host';
        case 'stun':
        case 'serverreflexive':
          return 'srflx';
        case 'peerreflexive':
          return 'prflx';
        case 'relayed':
          return 'relay';
        default:
          return type;
      }
    }

    function getAddrByNetworkIdForQosReport(parsedMLine, selectedCandidate, type) {
      var addr;
      // Find corresponding address based on the type and network-id
      parsedMLine.a.some(function (a) {
        if (a.field === 'candidate' && a.value) {
          var candidate = new unifyPhone.IceCandidate(a.value);
          if (candidate.typ === type && candidate['network-id'] === selectedCandidate['network-id']) {
            addr = candidate.address;
            return true;
          }
        }
        return false;
      });
      return addr;
    }

    /**
     * Get ICE candidate info (hAddr, sAddr and tt)
     *
     * @param {Object} res - Stat resource containing the candidate info
     * @param {Object} sdp - Local SDP (needed for relay candidates)
     * @returns {Object} - Object containing the candidate's hAddr, sAddr and tt (if applicable)
     */
    function getIceCandidateInfo(res, sdp) {
      var info = {};

      if (!res || !sdp) {
        return info;
      }

      var port, ip;
      var type = normalizeCandidateType(res.stat('candidateType'));
      if (type === 'prflx') {
        // Peer reflexive candidates are not in the SDP, but we still need to collect
        // their hAddr. For that we use their connection IP address (sAddr) to find
        // the corresponding srflx candidate, then the host candidate (hAddr)
        ip = res.stat('ipAddress');
      } else {
        port = String(res.stat('portNumber'));
      }

      var parsed = typeof sdp === 'string' ? unifyPhone.sdpParser.parse(sdp) : sdp;
      // Find the selected candidate in the SDP
      parsed.m.some(function (m) {
        m.a.some(function (a) {
          if (a.field !== 'candidate') {
            return false;
          }
          // Only parse the ICE candidate if the port is found in it
          if (a.value) {
            var candidate;
            if (port && a.value.indexOf(port) >= 0) {
              candidate = new unifyPhone.IceCandidate(a.value);
              if (String(candidate.port) === port && type === candidate.typ) {
                info.value = a.value;
                if (type === 'host') {
                  info.hAddr = res.stat('ipAddress');
                  // ANS-51833: In some cases of VPN connected calls, selected candidate is host
                  // but, backend needs srflx host address for the QoS bandwidth calculations
                  info.sAddr = getAddrByNetworkIdForQosReport(m, candidate, 'srflx');
                } else {
                  if (type === 'srflx') {
                    info.sAddr = candidate.address;
                  } else {
                    info.sAddr = candidate.raddr;
                  }
                  // Get the corresponding host address for srflx/relay candidate
                  info.hAddr = getAddrByNetworkIdForQosReport(m, candidate, 'host');

                  if (type === 'relay') {
                    info.tt = candidate.getRelayClientTransportType();
                  }
                }
                return true;
              }
            }
            if (ip && a.value.indexOf('srflx') >= 0) {
              // We're using the ip to search for the candidate, which must be srflx
              candidate = candidate || new unifyPhone.IceCandidate(a.value);
              if (candidate.typ === 'srflx' && String(candidate.address) === ip) {
                info.sAddr = ip;
                // Now get the host address
                info.hAddr = getAddrByNetworkIdForQosReport(m, candidate, 'host');
                return true;
              }
            }
          }
          return false;
        });
        return false;
      });
      info.netmask = _nics[info.hAddr];
      return info;
    }

    function getChannelInfo(info, res) {
      // Get the local and remote IP addresses and port numbers
      var ipl = Utils.parseIpWithPort(res.stat('googLocalAddress')) || {};
      var ipr = Utils.parseIpWithPort(res.stat('googRemoteAddress')) || {};
      info.ipl = ipl.ipAddress;
      info.ptl = ipl.port;
      info.ipr = ipr.ipAddress;
      info.ptr = ipr.port;
      info.lct = normalizeCandidateType(res.stat('googLocalCandidateType'));
      info.rct = normalizeCandidateType(res.stat('googRemoteCandidateType'));
      info.tt = res.stat('googTransportType');
    }

    function getChannelInfoFromCandidates(info, local, remote) {
      info.ipl = local && local.stat('ipAddress');
      info.ptl = local && local.stat('portNumber');
      info.ipr = remote && remote.stat('ipAddress');
      info.ptr = remote && remote.stat('portNumber');
      info.lct = local && normalizeCandidateType(local.stat('candidateType'));
      info.rct = remote && normalizeCandidateType(remote.stat('candidateType'));
      info.tt = local && local.stat('protocol');
    }

    function processCandidatePair(res, formattedStats, candidateIds, includeQosData) {
      var channelId = res.stat('googChannelId');
      var channelInfo = {
        channelId: channelId
      };
      var rtt = parseInt(res.stat('googRtt'), 10);
      channelInfo.rtt = isNaN(rtt) ? -1 : rtt;
      if (includeQosData) {
        channelInfo.candidates = {
          local: res.stat('localCandidateId'),
          remote: res.stat('remoteCandidateId')
        };
        if (browserInfo.firefox || browserInfo.safari) {
          // These browser checks won't be needed anymore once we fully support
          // promise based getStats()
          candidateIds.remote = res.stat('remoteCandidateId');
        } else {
          getChannelInfo(channelInfo, res);
        }
      }
      formattedStats.channelInfo[channelId] = channelInfo;
    }

    function processSsrc(res, formattedStats, pc) {
      var statsPtr = null;
      var ssrc = res.stat('ssrc');
      var commonData = {
        id: res.id,
        ssrc: ssrc,
        content: res.stat('googContentType'), // 'realtime' or 'screen'
        channelId: res.stat('transportId'),
        trackId: res.stat('googTrackId')
      };
      // For lack of better method, we check for specific stats in each ssrc so we can
      // tell if it's audio or video (receive or transmit)
      if (res.stat('audioInputLevel')) {
        if (!formattedStats.audio.transmit[ssrc]) {
          formattedStats.audio.transmit[ssrc] = commonData;
        }
        statsPtr = formattedStats.audio.transmit[ssrc];
        statsPtr.content = SsrcContents.AUDIO;
      } else if (res.stat('audioOutputLevel')) {
        if (!formattedStats.audio.receive[ssrc]) {
          formattedStats.audio.receive[ssrc] = commonData;
        }
        statsPtr = formattedStats.audio.receive[ssrc];
        statsPtr.content = SsrcContents.AUDIO;
      } else if (res.stat('googFrameHeightSent')) {
        if (!formattedStats.video.transmit[ssrc]) {
          formattedStats.video.transmit[ssrc] = commonData;
        }
        if (browserInfo.firefox) {
          // In FF we don't have googContentType, but it provides the 'mid' attribute, which we can use to identify
          // the screen ssrc
          commonData.content = res.stat('mid') === pc.getScreenShareMediaId() ? SsrcContents.SCREEN : SsrcContents.VIDEO;
        } else if (commonData.content !== SsrcContents.SCREEN) {
          // googContentType has 'screen' if screen is being transmitted. If not, mark it as video
          commonData.content = SsrcContents.VIDEO;
        }
        statsPtr = formattedStats.video.transmit[ssrc];
      } else if (res.stat('googFrameHeightReceived')) {
        if (!formattedStats.video.receive[ssrc]) {
          formattedStats.video.receive[ssrc] = commonData;
        }
        // Check if this ssrc is listed under the screenshare m-line, otherwise consider it a video ssrc
        commonData.content = pc.desktopRemoteSsrc.includes(commonData.ssrc) ? SsrcContents.SCREEN : SsrcContents.VIDEO;
        statsPtr = formattedStats.video.receive[ssrc];
      }

      return statsPtr;
    }

    function convertLegacyStats(stats) {
      var results = [];
      var transport =
        Array.from(stats.values()).find(function (rep) {
          return rep.type === 'transport';
        }) || {};
      stats.forEach(function (report) {
        var legacyData = {
          id: report.id,
          timestamp: report.timestamp
        };
        legacyData.stat = function (name) {
          return legacyData[name];
        };
        legacyData.names = function () {
          return Object.keys(legacyData).filter(function (name) {
            return name !== 'stat' && name !== 'names';
          });
        };

        var isInbound = report.type === 'inbound-rtp';
        var isOutbound = report.type === 'outbound-rtp';
        var isLocalCandidate = report.type === 'local-candidate';
        var isRemoteCandidate = report.type === 'remote-candidate';
        var isCandidatePair = report.type === 'candidate-pair';
        var isVideo = report.kind === 'video';
        var shouldAdd = true;
        if (report.ssrc) {
          var mediaSource = report.mediaSourceId ? stats.get(report.mediaSourceId) : {};
          legacyData.type = 'ssrc';
          legacyData.ssrc = report.ssrc;
          isVideo && (legacyData.googContentType = report.contentType);
          legacyData.googTrackId = '';
          if (isInbound) {
            legacyData.googTrackId = report.trackIdentifier || '';
            legacyData.audioOutputLevel = report.audioLevel;
            var remoteOutbound = stats.get(report.remoteId) || {};
            legacyData.packetsSent = remoteOutbound.packetsSent || 0;
            legacyData.packetsReceived = report.packetsReceived || 0;
            legacyData.packetsLost = report.packetsLost || 0;
            if (report.jitter !== undefined) {
              legacyData.googJitterReceived = report.jitter * 1000;
            }
            isVideo && (legacyData.googFrameHeightReceived = report.frameHeight);
          } else if (isOutbound) {
            legacyData.audioInputLevel = mediaSource.audioLevel;
            var remoteInbound = stats.get(report.remoteId) || {};
            legacyData.packetsLost = remoteInbound.packetsLost || 0;
            legacyData.googTrackId = mediaSource.trackIdentifier || '';
            legacyData.packetsSent = report.packetsSent || 0;
            isVideo && (legacyData.googFrameHeightSent = report.frameHeight);
          }
          legacyData.transportId = report.transportId;
          var codec = report.codecId ? stats.get(report.codecId) : {};
          legacyData.googCodecName = codec.mimeType;
        } else if (isLocalCandidate || isRemoteCandidate) {
          legacyData.type = isLocalCandidate ? 'localcandidate' : 'remotecandidate';
          Object.assign(legacyData, report);
        } else if (isCandidatePair) {
          legacyData.type = 'googCandidatePair';
          legacyData.bytesReceived = report.bytesReceived;
          legacyData.bytesSent = report.bytesSent;
          legacyData.googChannelId = report.transportId;
          if (report.currentRoundTripTime !== undefined) {
            legacyData.googRtt = report.currentRoundTripTime * 1000;
          }
          legacyData.googActiveConnection = (report.id === transport.selectedCandidatePairId).toString();
          legacyData.localCandidateId = report.localCandidateId;
          legacyData.remoteCandidateId = report.remoteCandidateId;
          var localCandidate = stats.get(report.localCandidateId) || {};
          legacyData.googLocalAddress = localCandidate.address + ':' + localCandidate.port;
          legacyData.googLocalCandidateType = localCandidate.candidateType;
          var remoteCandidate = stats.get(report.remoteCandidateId) || {};
          legacyData.googRemoteAddress = remoteCandidate.address + ':' + remoteCandidate.port;
          legacyData.googRemoteCandidateType = remoteCandidate.candidateType;
          legacyData.googTransportType = localCandidate.protocol || remoteCandidate.protocol;
        } else {
          shouldAdd = false;
        }
        shouldAdd && results.push(legacyData);
      });
      return results;
    }

    function formatStatsForClientDiagnostics(startTime, stats, pc, pcType, includeQosData) {
      if (!stats || !pc) {
        return null;
      }

      var formattedStats = {
        startTime: startTime,
        pcType: pcType,
        audio: {
          transmit: {},
          receive: {}
        },
        video: {
          transmit: {},
          receive: {}
        },
        channelInfo: {},
        quality: { audio: {}, video: {} }
      };
      var candidateIds = { audio: {}, video: {} };
      var candidateList = {};
      var results;
      if (stats.useStandardGetStats) {
        results = convertLegacyStats(stats.result);
      } else {
        results = stats.result ? stats.result() : stats;
      }

      if (!Array.isArray(results)) {
        return null;
      }

      results.forEach(function (res) {
        if (!res) {
          return;
        }
        var statsPtr = null;
        switch (res.type) {
          case 'ssrc':
            statsPtr = processSsrc(res, formattedStats, pc);
            break;
          case 'VideoBwe':
            if (!formattedStats.video.bw) {
              formattedStats.video.bw = { id: res.id };
            }
            statsPtr = formattedStats.video.bw;
            break;
          case 'localcandidate':
          case 'remotecandidate':
            candidateList[res.id] = res;
            break;
        }

        // Now get only the stats that we are interested in
        if (res.names && statsPtr) {
          var resNames = res.names() || [];
          resNames.forEach(function (name) {
            if (CallStats[name]) {
              statsPtr[CallStats[name]] = parseFloat(res.stat(name));
            }
          });
          // The codec might change during the call and right now we're not keeping track of these changes...
          statsPtr.en = res.stat('googCodecName');
        }
      });

      results.forEach(function (res) {
        if (res && res.type === 'googCandidatePair' && res.stat('googActiveConnection') === 'true') {
          processCandidatePair(res, formattedStats, candidateIds, includeQosData);
        }
      });

      if (includeQosData) {
        var parsedLocalSdp;

        Object.keys(formattedStats.channelInfo).forEach(function (channelId) {
          var info = formattedStats.channelInfo[channelId];
          if (info) {
            var local = candidateList[info.candidates.local];
            if (local) {
              if (browserInfo.firefox || browserInfo.safari) {
                // These browser checks won't be needed anymore once we fully support promise based getStats()
                var remote = candidateList[info.candidates.remote];
                getChannelInfoFromCandidates(info, local, remote);
              }
              info.ni = local.stat('networkType');
              parsedLocalSdp = parsedLocalSdp || unifyPhone.sdpParser.parse(pc.localDescription && pc.localDescription.sdp);
              info.candidate = getIceCandidateInfo(local, parsedLocalSdp);
            }
          }
        });
      }
      return formattedStats;
    }

    function getPcStats(pc, pcName, startTime, includeQosData) {
      if (!pc.getStats) {
        return Promise.resolve();
      }

      return pc
        .getStats()
        .catch(function (err) {
          // Log that getStats for this particular peer connection failed, but don't reject the promise
          logger.warn('[CallStatsHandlerUnified]: getStats[' + pcName + '] failed with err = ', err);
        })
        .then(function (stats) {
          if (!stats) {
            logger.warn('[CallStatsHandlerUnified]: getStats[' + pcName + '] did not return a response');
          } else {
            stats = formatStatsForClientDiagnostics(startTime, stats, pc, pcName, includeQosData);
          }
          return stats;
        });
    }

    function getRTPStatsForClientDiagnostics(force) {
      // If QoS has already been collected, we don't need to collect it again
      var includeQosData = !_qosCollected;
      _qosCollected = _qosCollected || includeQosData;

      var mainPc = _peerConnections.find(function (p) {
        if (!p) {
          return false;
        }

        // Collect stats only if media is connected
        if (!force && p.iceConnectionState !== 'completed' && p.iceConnectionState !== 'connected') {
          logger.warn('[CallStatsHandlerUnified]: Do not collect stats. pc.iceConnectionState = ', p.iceConnectionState);
          return false;
        }

        return p.mainPeerConnection;
      });

      if (!mainPc) {
        return Promise.resolve();
      }

      return getPcStats(mainPc, 'AUDIO/VIDEO/SCREEN', mainPc.startTime, includeQosData).then(function (stats) {
        if (stats) {
          // Run some pre-processing of the media channels
          [SsrcContents.AUDIO, SsrcContents.VIDEO].forEach(function (media) {
            var mediaStats = stats[media];
            ['transmit', 'receive'].forEach(function (direction) {
              var directionStats = mediaStats[direction];
              Object.keys(directionStats).forEach(function (ssrc) {
                var trackInfo = directionStats[ssrc];
                if (!trackInfo.channelInfo) {
                  // Associate each SSRC to their channels
                  trackInfo.channelInfo = _savedChannelInfo[trackInfo.channelId] || stats.channelInfo[trackInfo.channelId] || {};
                }
                // Mark the channels that are receiving audio/video and is sending video
                if (media === SsrcContents.AUDIO && direction === 'receive') {
                  // Receiving audio (audio is also sent through this same channel, if applicable)
                  trackInfo.channelInfo.isAudioChannel = true;
                } else if (trackInfo.content === SsrcContents.VIDEO) {
                  if (direction === 'transmit') {
                    if (!_lowQualityVideoTracks[ssrc.trackId]) {
                      // Sending local video
                      trackInfo.channelInfo.isVideoSendChannel = true;
                    }
                  } else {
                    // Check if video packets are being received. This information is dynamic
                    var saved = _savedRTPStats[ssrc];
                    trackInfo.channelInfo.isVideoRecvChannel = saved ? trackInfo.pr > saved.pr : trackInfo.pr > 0;
                  }
                }
              });
            });
          });
        }
        return stats;
      });
    }

    function startCollectingRTPStats() {
      if (!_rtpStatsDelayUponAnsTimer && !_rtpStatsCollectionTimer) {
        logger.debug('[CallStatsHandlerUnified]: Waiting to start collecting RTPstats');

        _rtpStatsDelayUponAnsTimer = window.setTimeout(function () {
          logger.info('[CallStatsHandlerUnified]: Start collecting RTPStats');
          _rtpStatsDelayUponAnsTimer = null;
          _rtpStatsCollectionCounter = 0;
          getFormattedRTPStats();
          _rtpStatsCollectionTimer = window.setInterval(function () {
            getFormattedRTPStats();
          }, RtpStatsConfig.COLLECTION_INTERVAL);
        }, RtpStatsConfig.DELAY_UPON_ANSWER);
      }
    }

    function calculatePLSent(current, savedStats) {
      var previousPL = 0,
        previousPS = 0;
      if (savedStats) {
        previousPL = savedStats.pl || 0;
        previousPS = savedStats.ps || 0;
      }
      var currentPL = (current.pl - previousPL) / (current.ps - previousPS || 1);
      return Math.round(currentPL * 100) / 100;
    }

    function calculatePLReceived(current, savedStats) {
      var previousPL = 0,
        previousPR = 0;
      if (savedStats) {
        previousPL = savedStats.pl || 0;
        previousPR = savedStats.pr || 0;
      }
      var currentPL = (current.pl - previousPL) / (current.pr - previousPR + current.pl - previousPL || 1);
      return Math.round(currentPL * 100) / 100;
    }

    function calculateAverageAndMax(current, savedStats, field) {
      var sum = field + 'Sum';
      var counter = field + 'Count';
      var max = field + 'Max';
      if (savedStats) {
        if (savedStats[sum] !== undefined) {
          current[sum] = savedStats[sum] + (current[field] || 0);
          ++savedStats[counter];
          current[counter] = savedStats[counter];
        } else {
          current[sum] = current[field] || 0;
          current[counter] = 1;
        }
        if (savedStats[max] !== undefined) {
          current[max] = Math.max(savedStats[max], current[field] || 0);
        } else {
          current[max] = current[field] || 0;
        }
      } else {
        // First sample
        current[sum] = current[max] = current[field] || 0;
        current[counter] = 1;
      }
    }

    function logRawStats(s) {
      var log;

      function getValue(val) {
        if (val === undefined || val < 0) {
          return 'n/a';
        }
        return val;
      }

      Object.keys(s.channelInfo).forEach(function (id) {
        var channel = s.channelInfo[id];
        log = '[CallStatsHandlerUnified]: statsReport: channelRtt=' + (channel.rtt >= 0 ? channel.rtt : 'n/a');
        Object.keys(s.audio.receive).forEach(function (ssrc) {
          var r = s.audio.receive[ssrc];
          if (r.channelId === id) {
            log += '\n audioReceive:  id=' + r.id + ' pl=' + getValue(r.pl) + ' pr=' + getValue(r.pr) + ' or=' + getValue(r.or) + ' jr=' + getValue(r.jr);
          }
        });
        Object.keys(s.audio.transmit).forEach(function (ssrc) {
          var t = s.audio.transmit[ssrc];
          if (t.channelId === id) {
            log += '\n audioTransmit: id=' + t.id + ' pl=' + getValue(t.pl) + ' ps=' + getValue(t.ps) + ' os=' + getValue(t.os);
          }
        });
        Object.keys(s.video.receive).forEach(function (ssrc) {
          var r = s.video.receive[ssrc];
          if (r.channelId === id) {
            log +=
              '\n videoReceive:  id=' +
              r.id +
              ' pl=' +
              getValue(r.pl) +
              ' pr=' +
              getValue(r.pr) +
              ' or=' +
              getValue(r.or) +
              ' fwr=' +
              r.fwr +
              ' fhr=' +
              r.fhr +
              ' frd=' +
              r.frd +
              ' frr=' +
              r.frr;
          }
        });
        Object.keys(s.video.transmit).forEach(function (ssrc) {
          var t = s.video.transmit[ssrc];
          if (t.channelId === id) {
            log +=
              '\n videoTransmit: id=' +
              t.id +
              ' pl=' +
              getValue(t.pl) +
              ' ps=' +
              getValue(t.ps) +
              ' os=' +
              getValue(t.os) +
              ' fws=' +
              t.fws +
              ' fhs=' +
              t.fhs +
              ' frs=' +
              t.frs +
              ' abw=' +
              ((s.video.bw && s.video.bw.abw) || 'N/A');
          }
        });
        logger.debug(log);
      });
    }

    function checkViolations(stat, value, startTime, currentStats, savedStats) {
      var viStart = stat.StatText + 'LastViStart';
      var vi = stat.StatText + 'Violations';
      var viCounter = stat.StatText + 'ViCounter';
      // Propagate the violation records from the savedStats to the current
      currentStats[viStart] = savedStats && savedStats[viStart];
      currentStats[vi] = savedStats && savedStats[vi];
      currentStats[viCounter] = (savedStats && savedStats[viCounter]) || 0;
      if (value === -1) {
        // -1 means stat value is below threshold
        if (currentStats[viStart] >= 0) {
          // There's a violation in progress and now we should mark it as ended
          var viRecords = currentStats[vi];
          viRecords = viRecords ? viRecords + ',' : ''; // Add a coma
          currentStats[vi] = viRecords + '(' + currentStats[viStart] + ',' + parseInt((Date.now() - startTime) / 1000, 10) + ')'; // Add end time
          currentStats[viCounter]++;

          _violationsInProgress[stat.StatText] = 0;
          // Delete the start time, otherwise it will be recorded again
          delete currentStats[viStart];
          if (savedStats) {
            delete savedStats[viStart];
          }
        }
      } else if (currentStats[viStart] >= 0) {
        // Keep count of consecutive violations
        _violationsInProgress[stat.StatText]++;
      } else if (currentStats[viCounter] < MAX_VIOLATIONS) {
        // Start recording this violation
        currentStats[viStart] = parseInt((Date.now() - startTime) / 1000, 10);
        _violationsInProgress[stat.StatText] = 1;
      }
    }

    function compare(value1, value2, type) {
      return type === ThresholdType.MAX ? value1 >= value2 : value1 <= value2;
    }

    function getQualityLevel(statType, value) {
      if (compare(statType.Threshold[0], value, statType.ThresholdType)) {
        return RtpQualityLevel.RTP_QUALITY_HIGH;
      } else if (compare(statType.Threshold[1], value, statType.ThresholdType)) {
        return RtpQualityLevel.RTP_QUALITY_MEDIUM;
      } else if (compare(statType.Threshold[2], value, statType.ThresholdType)) {
        return RtpQualityLevel.RTP_QUALITY_LOW;
      } else {
        return RtpQualityLevel.RTP_QUALITY_POOR;
      }
    }

    function analyzeCallQuality(statsReport, statType, media, value) {
      if (value >= 0) {
        statsReport.quality.level = statsReport.quality.level || RtpQualityLevel.RTP_QUALITY_HIGH;
        // Only analyze if there are thresholds defined, otherwise just report HIGH and the stat value
        var level = statType.Threshold.length > 0 ? getQualityLevel(statType, value) : RtpQualityLevel.RTP_QUALITY_HIGH;
        statsReport.quality[media][statType.id] = {
          qualityLevel: level,
          value: value
        };

        // Group the stats by quality level, this makes it easier to calculate the overall quality later
        statsReport.quality[level.value] = statsReport.quality[level.value] || [];
        statsReport.quality[level.value].push(statType);

        if (level.value < statsReport.quality.level.value) {
          statsReport.quality.level = level; // Save the lowest level
        }
      }
    }

    function processTransmitAudioRTPStats(statsReport) {
      Object.keys(statsReport.audio.transmit).forEach(function (trackId) {
        var savedStats = _savedRTPStats[trackId] || {};
        var send = statsReport.audio.transmit[trackId];

        if (send) {
          send.media = SsrcContents.AUDIO;
          // Packet loss
          var statType = RTPAudioStatType.PACKET_LOSS_SEND;
          var currentPL = calculatePLSent(send, savedStats);
          if (send.pl && send.ps) {
            checkViolations(statType, currentPL >= statType.Threshold[0] ? currentPL : -1, statsReport.startTime, send, savedStats);
          }
          analyzeCallQuality(statsReport, statType, SsrcContents.AUDIO, currentPL);
          var packetsSent = (send.ps || 0) - (savedStats.ps || 0);
          checkViolations(RTPAudioStatType.NO_PACKETS_SEND, packetsSent > 0 ? -1 : true, statsReport.startTime, send, savedStats);

          // Echo likelihood
          statType = RTPAudioStatType.ECHO_LIKELIHOOD;
          calculateAverageAndMax(send, savedStats, RTPAudioStatType.ECHO_LIKELIHOOD.StatName);
          var statValue = send[statType.StatName] || 0;
          checkViolations(statType, statValue && statValue >= statType.Threshold[0] ? statValue : -1, statsReport.startTime, send, savedStats);
        }

        _savedRTPStats[trackId] = send;
      });
    }

    function processReceiveAudioRTPStats(statsReport) {
      Object.keys(statsReport.audio.receive).forEach(function (trackId) {
        var savedStats = _savedRTPStats[trackId] || {};
        var rcv = statsReport.audio.receive[trackId];
        var statType = RTPAudioStatType.PACKET_LOSS_RECV;

        if (rcv) {
          rcv.media = SsrcContents.AUDIO;
          var currentPL = calculatePLReceived(rcv, savedStats);
          if (rcv.pl && rcv.pr) {
            checkViolations(statType, currentPL >= statType.Threshold[0] ? currentPL : -1, statsReport.startTime, rcv, savedStats);
          }
          analyzeCallQuality(statsReport, statType, SsrcContents.AUDIO, currentPL);

          if (!_options.sendOnlyStream) {
            // Packets received - check if we're receiving packets. If not, assume that either we
            // lost connection or the other peer stopped responding
            checkViolations(RTPAudioStatType.NO_PACKETS_RECV, rcv.pr > savedStats.pr ? -1 : true, statsReport.startTime, rcv, savedStats);
          }

          // Jitter received
          statType = RTPAudioStatType.GOOG_JITTER_RECEIVED;
          var statValue;
          calculateAverageAndMax(rcv, savedStats, RTPAudioStatType.GOOG_JITTER_RECEIVED.StatName);
          statValue = rcv[statType.StatName];
          analyzeCallQuality(statsReport, statType, SsrcContents.AUDIO, statValue);
          checkViolations(statType, statValue && statValue >= statType.Threshold[0] ? statValue : -1, statsReport.startTime, rcv, savedStats);

          statsReport.quality.audio.trackId = trackId;
        }
        _savedRTPStats[trackId] = rcv;
      });
    }

    function processAudioRTPStats(statsReport) {
      if (!statsReport || !statsReport.audio) {
        return;
      }
      processTransmitAudioRTPStats(statsReport);
      processReceiveAudioRTPStats(statsReport);
    }

    function getHDVideoResolutionInfo(statsReport, fhs, fws) {
      var videoInfo = {};
      if (fhs >= 0 && fws >= 0) {
        videoInfo.resolution = {
          height: fhs,
          width: fws
        };
        var height = Math.min(fhs, fws);
        switch (height) {
          case 1080:
            videoInfo.hdVideo = VideoResolutionLevel.VIDEO_1080;
            break;
          case 720:
            videoInfo.hdVideo = VideoResolutionLevel.VIDEO_720;
            break;
          case 480:
            videoInfo.hdVideo = VideoResolutionLevel.VIDEO_480;
            break;
        }
      }
      statsReport.quality.videoInfo = videoInfo;
    }

    function processTransmitVideoRTPStats(statsReport) {
      Object.keys(statsReport.video.transmit).forEach(function (trackId) {
        var savedStats = _savedRTPStats[trackId] || {};
        var videoIssues = _videoIssues[trackId] || {};
        var send = statsReport.video.transmit[trackId];
        send.media = SsrcContents.VIDEO;

        // Packet loss
        var statType = RTPVideoStatType.PACKET_LOSS_SEND;
        if (send && !_lowQualityVideoTracks[send.trackId]) {
          var currentPL = calculatePLSent(send, savedStats);
          if (send.pl && send.ps) {
            checkViolations(statType, currentPL >= statType.Threshold[0] ? currentPL : -1, statsReport.startTime, send, savedStats);
          }
          if (send.content === SsrcContents.VIDEO) {
            // Only analyze quality if video (not screen) is being transmitted
            analyzeCallQuality(statsReport, statType, SsrcContents.VIDEO, currentPL);

            // Handle portrait and landscape videos
            var width = Math.max(send.fws, send.fhs) || send.fws;
            var height = Math.min(send.fws, send.fhs) || send.fhs;
            analyzeCallQuality(statsReport, _rtpHDVideoStatType.FRAME_HEIGHT_SENT, SsrcContents.VIDEO, height);
            analyzeCallQuality(statsReport, _rtpHDVideoStatType.FRAME_WIDTH_SENT, SsrcContents.VIDEO, width);
            analyzeCallQuality(statsReport, _rtpHDVideoStatType.FRAME_RATE_SENT, SsrcContents.VIDEO, send.frs);
            if (statsReport.video.bw) {
              analyzeCallQuality(statsReport, _rtpHDVideoStatType.TRANSMIT_BIT_RATE, SsrcContents.VIDEO, statsReport.video.bw.tbr);
              analyzeCallQuality(statsReport, _rtpHDVideoStatType.AVAIL_SEND_BW, SsrcContents.VIDEO, statsReport.video.bw.abw);
            }
            getHDVideoResolutionInfo(statsReport, send.fhs, send.fws);
          }
        }
        _savedRTPStats[trackId] = send;
        _videoIssues[trackId] = videoIssues;
      });
    }

    function processReceiveVideoRTPStats(statsReport) {
      var maxPlRecv = -1;
      Object.keys(statsReport.video.receive).forEach(function (trackId) {
        var savedStats = _savedRTPStats[trackId] || {};
        var videoIssues = _videoIssues[trackId] || {};
        var rcv = statsReport.video.receive[trackId];
        rcv.media = SsrcContents.VIDEO;
        // Packet loss
        var statType = RTPVideoStatType.PACKET_LOSS_RECV;
        if (rcv && rcv.pr) {
          var currentPL = calculatePLReceived(rcv, savedStats);
          if (rcv.pl) {
            checkViolations(statType, currentPL >= statType.Threshold[0] ? currentPL : -1, statsReport.startTime, rcv, savedStats);
          }
          // Analyze PL quality level only if the content is video (not screen) and packets are being received
          if (rcv.content === SsrcContents.VIDEO && (isNaN(savedStats.pr) || rcv.pr - savedStats.pr > 0)) {
            // Save the highest PL_RECV
            maxPlRecv = Math.max(maxPlRecv, currentPL);
          }
        }
        _savedRTPStats[trackId] = rcv;
        _videoIssues[trackId] = videoIssues;
      });
      if (maxPlRecv >= 0) {
        analyzeCallQuality(statsReport, RTPVideoStatType.PACKET_LOSS_RECV, SsrcContents.VIDEO, maxPlRecv);
      }
    }

    function processVideoRTPStats(statsReport) {
      if (!statsReport || !statsReport.video) {
        return;
      }
      _savedRTPStats.bw = statsReport.video.bw || {};
      _savedRTPStats.bw.media = SsrcContents.VIDEO;

      processTransmitVideoRTPStats(statsReport);
      processReceiveVideoRTPStats(statsReport);
    }

    function processRttStats(statsReport) {
      Object.keys(statsReport.channelInfo).forEach(function (channelId) {
        var saved = _savedChannelInfo[channelId] || {};
        var channelInfo = statsReport.channelInfo[channelId];
        calculateAverageAndMax(channelInfo, saved, RTPVideoStatType.GOOG_RTT.StatName);
        var statType = RTPAudioStatType.GOOG_RTT;
        var issues = _channelIssues[channelId] || {};
        checkViolations(statType, channelInfo.rtt >= statType.Threshold[0] ? channelInfo.rtt : -1, statsReport.startTime, channelInfo, saved);

        // Only analyze RTT if the channel has audio or is transmitting/receiving video.
        if (saved.isAudioChannel || channelInfo.isAudioChannel) {
          statType = RTPAudioStatType.GOOG_RTT;
          analyzeCallQuality(statsReport, statType, SsrcContents.AUDIO, channelInfo.rtt);
        } else if (saved.isVideoSendChannel || saved.isVideoRecvChannel || channelInfo.isVideoSendChannel || channelInfo.isVideoRecvChannel) {
          statType = RTPVideoStatType.GOOG_RTT;
          analyzeCallQuality(statsReport, statType, SsrcContents.VIDEO, channelInfo.rtt);
        }
        _channelIssues[channelId] = issues;
      });
    }

    function saveChannelInfo(statsReport) {
      Object.keys(statsReport.channelInfo).forEach(function (channelId) {
        var saved = _savedChannelInfo[channelId] || {};
        _savedChannelInfo[channelId] = Object.assign(saved, statsReport.channelInfo[channelId]);
      });
    }

    function logQualityStats(statsReport) {
      // Log the overall and quality level for all stats only when the overall quality has changed or
      // if any of the quality stats is (or was) not at HIGH
      var log = '[CallStatsHandlerUnified]: Network quality: overall=' + statsReport.quality.level.levelName;

      [SsrcContents.AUDIO, SsrcContents.VIDEO].forEach(function (media) {
        var logRef = statsReport.quality[media];
        var mediaLog = '';
        Object.keys(logRef).forEach(function (k) {
          if (k !== 'trackId') {
            mediaLog += ' ' + k + '=' + logRef[k].qualityLevel.levelName + '[' + logRef[k].value + ']';
          }
        });
        if (mediaLog) {
          log += '\n' + media + ':' + mediaLog;
        }
      });
      logger.debug(log);
    }

    function calculateOverallQuality(statsReport) {
      // Check how many stats we have in each quality level,
      // if a level has 2+ stats or 1+ PL (Packet Lost) stat, that's the overall level,
      statsReport.quality.level = RtpQualityLevel.RTP_QUALITY_HIGH;
      var singleStatCounter = 0;
      var singleStatScore = -1;
      var statsCounter = 0;
      var anythingLessThanHigh = false;
      // Analyze from POOR to HIGH
      Object.keys(RtpQualityLevel).some(function (k) {
        var level = RtpQualityLevel[k];
        var list = statsReport.quality[level.value] || [];
        statsCounter += list.length;
        if (level !== RtpQualityLevel.RTP_QUALITY_HIGH && list.length > 0) {
          var isPl = list.some(function (l) {
            return l.id === 'PL_SEND' || l.id === 'PL_RECV';
          });
          if (isPl || list.length > 1) {
            // If this level has 2+ stats or 1+ PL, that's the overall level
            statsReport.quality.level = level;
            return true;
          } else {
            // This level has a single stat, which will be analyzed later
            singleStatCounter++;
            singleStatScore += level.value + 1;
          }
          anythingLessThanHigh = true;
        }
        return false;
      });

      if (statsReport.quality.level === RtpQualityLevel.RTP_QUALITY_HIGH) {
        // Now check the levels that have single stats in them
        if (singleStatCounter > 2) {
          // Every level has a single stat: overall is LOW
          statsReport.quality.level = RtpQualityLevel.RTP_QUALITY_LOW;
        } else if (singleStatCounter > 1) {
          // 2 levels have single stats: overall is the highest of the 2 (either LOW or MEDIUM)
          statsReport.quality.level = singleStatScore > 2 ? RtpQualityLevel.RTP_QUALITY_MEDIUM : RtpQualityLevel.RTP_QUALITY_LOW;
        } else if (singleStatCounter === 1 && singleStatScore === 0) {
          // If there's only 1 poor stat and the rest is at HIGH, overall is MEDIUM
          statsReport.quality.level = RtpQualityLevel.RTP_QUALITY_MEDIUM;
        }
      }

      var oldLevel = _savedRTPStats.quality && _savedRTPStats.quality.level;

      if (statsCounter) {
        var anythingWasLessThanHigh = _savedRTPStats.quality && _savedRTPStats.quality.anythingLessThanHigh;
        // Stats must be saved before raising onNetworkQuality event
        _savedRTPStats.quality = {
          level: statsReport.quality.level,
          audio: statsReport.quality.audio,
          video: statsReport.quality.video,
          anythingLessThanHigh: anythingLessThanHigh
        };
        if (statsReport.quality.videoInfo) {
          _savedRTPStats.quality.videoInfo = statsReport.quality.videoInfo;
        }

        if (!oldLevel || oldLevel !== statsReport.quality.level) {
          // Send event on the first processing and every time the overall quality changes
          _that.onNetworkQuality && _that.onNetworkQuality(statsReport.quality);
          logQualityStats(statsReport);
          return true;
        } else if (anythingLessThanHigh || anythingWasLessThanHigh) {
          logQualityStats(statsReport);
        }
      } else {
        logger.debug('[CallStatsHandlerUnified]: calculateOverallQuality: No media stats available');
        _savedRTPStats.quality = {};
        if (oldLevel) {
          _that.onNetworkQuality && _that.onNetworkQuality(null);
        }
      }
      return false;
    }

    function analyzeThresholdRecurrences(statsReport) {
      // Trigger onThresholdExceeded event if we have consecutive good/bad quality readings
      if (statsReport.quality.level === RtpQualityLevel.RTP_QUALITY_POOR || statsReport.quality.level === RtpQualityLevel.RTP_QUALITY_LOW) {
        _consecutiveBadQualityCounter++;
        if (_consecutiveGoodQualityCounter < THRESHOLD_RECURRENCE) {
          _consecutiveGoodQualityCounter = 0;
        }
      } else if (_consecutiveBadQualityCounter) {
        if (_consecutiveBadQualityCounter < THRESHOLD_RECURRENCE) {
          _consecutiveBadQualityCounter = 0;
          _consecutiveGoodQualityCounter = 0;
        } else {
          _consecutiveGoodQualityCounter++;
        }
      }

      if (_consecutiveBadQualityCounter === THRESHOLD_RECURRENCE) {
        _that.onThresholdExceeded && _that.onThresholdExceeded();
      } else if (_consecutiveGoodQualityCounter >= THRESHOLD_RECURRENCE && _consecutiveBadQualityCounter > THRESHOLD_RECURRENCE) {
        // Reset everything and trigger a onThresholdExceeded cleared event
        _consecutiveBadQualityCounter = 0;
        _consecutiveGoodQualityCounter = 0;
        _that.onThresholdExceeded && _that.onThresholdExceeded(true);
      }

      if (_violationsInProgress.noPacketsSent >= THRESHOLD_RECURRENCE) {
        logger.warn('[CallStatsHandlerUnified]: No audio packets sent to remote peer');

        if (_violationsInProgress.noPacketsSent === THRESHOLD_RECURRENCE && _that.onNoOutgoingPackets) {
          // Send onNoOutgoingPackets event only once for this streak
          _that.onNoOutgoingPackets();
        }
      }
      if (_violationsInProgress.noPacketsReceived >= THRESHOLD_RECURRENCE) {
        // Create a log whenever we detect that audio packets were not received
        logger.debug('[CallStatsHandlerUnified]: No audio packets received from remote peer');
      }
    }

    function processRTPStats(statsReport) {
      if (!statsReport) {
        return;
      }
      processAudioRTPStats(statsReport);
      processVideoRTPStats(statsReport);
      processRttStats(statsReport);
      calculateOverallQuality(statsReport);
      analyzeThresholdRecurrences(statsReport);
      saveChannelInfo(statsReport);
      collectClvStats(statsReport.quality, SsrcContents.AUDIO);

      ++_rtpStatsCollectionCounter;
      if (_rtpStatsCollectionCounter % LOG_SKIP_COUNTER === 0) {
        // Log raw stats every 30 seconds
        logRawStats(statsReport);
      }
    }

    function getFormattedRTPStats() {
      getRTPStatsForClientDiagnostics().then(function (stats) {
        if (stats) {
          processRTPStats(stats);
        }
      });
    }

    function stopCollectingRTPStats() {
      logger.debug('[CallStatsHandlerUnified]: stopCollectingRTPStats ', _streamQualityData);

      var promise = getRTPStatsForClientDiagnostics(true);

      // Probably there are not yet averaged data so write them also
      writeClvStats(SsrcContents.AUDIO);
      if (_rtpStatsDelayUponAnsTimer) {
        logger.debug('[CallStatsHandlerUnified]: Cancel collecting RTP stats ');
        window.clearTimeout(_rtpStatsDelayUponAnsTimer);
        _rtpStatsDelayUponAnsTimer = null;
      } else if (_rtpStatsCollectionTimer) {
        logger.debug('[CallStatsHandlerUnified]: Stop collecting RTP stats');
        window.clearInterval(_rtpStatsCollectionTimer);
        _rtpStatsCollectionTimer = null;

        if (_savedRTPStats) {
          logger.debug('[CallStatsHandlerUnified]: savedRTPStats =', _savedRTPStats);
        }
        _that.onThresholdExceeded && _that.onThresholdExceeded(true);
        _consecutiveBadQualityCounter = 0;
        _consecutiveGoodQualityCounter = 0;
      }
      return promise;
    }

    function getOldStreamQualityValue(media, stat) {
      var data = _streamQualityData[media].qualityData;
      return (data && data.length && data[data.length - 1][stat.StatName]) || 0;
    }

    function getStreamQualityDataAverage(media) {
      var qualityData = _tmpStreamQualityData[media];
      _tmpStreamQualityData[media] = null;

      if (!qualityData || !qualityData.count) {
        return null;
      }

      var count = qualityData.count;
      delete qualityData.count;

      _streamQualityStatsToCheck.forEach(function (stat) {
        var value = qualityData[stat.StatName];
        if (stat.StatName === RTPAudioStatType.PACKET_LOSS_RECV.StatName) {
          value *= 100;
        }
        qualityData[stat.StatName] = Math.round(value / count);
      });
      qualityData.timestamp = Date.now();
      return qualityData;
    }

    function writeClvStats(media) {
      var data = getStreamQualityDataAverage(media);
      if (data) {
        _streamQualityStatsToCheck.some(function (stat) {
          var threshold = stat.StreamQualityThreshold;
          var newValue = data[stat.StatName] || 0;
          var oldValue = getOldStreamQualityValue(media, stat);
          // Write initial data or if threshold exceeded
          if (_streamQualityData[media].qualityData.length === 0 || Math.abs(oldValue - newValue) > threshold) {
            logger.debug('[CallStatsHandlerUnified]: Writing into clientStreamQualityData - ', data);
            _streamQualityData[media].qualityData.push(data);
            return true;
          }
          return false;
        });
      }
    }

    function writeTmpClvStats(quality, media) {
      var qualityData = _tmpStreamQualityData[media];
      if (!qualityData) {
        qualityData = { count: 0 };
        _streamQualityStatsToCheck.forEach(function (stat) {
          qualityData[stat.StatName] = 0;
        });
        _tmpStreamQualityData[media] = qualityData;
      }

      _streamQualityStatsToCheck.forEach(function (stat) {
        var value = (quality[media][stat.id] && quality[media][stat.id].value) || 0;
        qualityData[stat.StatName] += value;
      });
      qualityData.count++;

      if (_streamQualityData[media].qualityData.length === 0) {
        // write initial dataType
        writeClvStats(media);
      }
    }

    function collectClvStats(quality, media) {
      // Only collect for direct calls. In Group Calls CLV retrieves this information from the MediaServer
      if (!_isDirectCall) {
        return;
      }

      _streamQualityData = _streamQualityData || {};
      _streamQualityData[media] = _streamQualityData[media] || { qualityData: [] };

      _streamQualityData[media].trackId = quality[media].trackId;

      if (_tmpStreamQualityData[media] && _tmpStreamQualityData[media].count >= STREAM_QUALITY_SUMMARY_ITERATIONS) {
        writeClvStats(media);
      } else {
        writeTmpClvStats(quality, media);
      }
    }

    /////////////////////////////////////////////////////////////////////////////
    // Public interfaces
    /////////////////////////////////////////////////////////////////////////////
    this.start = function () {
      startCollectingRTPStats();
    };

    this.stop = function () {
      return stopCollectingRTPStats();
    };

    this.onThresholdExceeded = null;
    this.onNoOutgoingPackets = null;
    this.onNetworkQuality = null;

    this.setOptions = function (options) {
      if (options) {
        _options = options;
      }
    };

    this.getLastSavedStats = function () {
      _savedRTPStats.channelInfo = _savedChannelInfo;
      return _savedRTPStats;
    };

    this.getStreamQualityData = function () {
      return _streamQualityData;
    };

    this.setTransmitVideoParams = function (params) {
      if (!params) {
        return;
      }

      if (params.mediaConstraints && params.mediaConstraints.hdVideo) {
        var resolution = params.mediaConstraints.videoResolution || VideoResolutionLevel.VIDEO_1080;
        var height, width;
        switch (resolution) {
          case VideoResolutionLevel.VIDEO_1080:
            height = 1080;
            width = 1920;
            break;
          case VideoResolutionLevel.VIDEO_720:
            height = 720;
            width = 1280;
            break;
          default:
            height = 480;
            width = 853;
            break;
        }
        _rtpHDVideoStatType.FRAME_RATE_SENT.Threshold = [20, 8, 3];
        _rtpHDVideoStatType.FRAME_HEIGHT_SENT.Threshold = [0.9 * height, 0.75 * height, 0.5 * height];
        _rtpHDVideoStatType.FRAME_WIDTH_SENT.Threshold = [0.9 * width, 0.75 * width, 0.5 * width];

        var maxBitRate = (params.bitRates.maxBitRate || 0) * 1000;
        var minBitRate = (params.bitRates.minBitRate || 0) * 1000;
        var highThreshold = maxBitRate * 0.75;
        if (highThreshold < minBitRate) {
          // Min and max are too close. Use maxBitRate as threshold.
          highThreshold = maxBitRate;
        }
        _rtpHDVideoStatType.AVAIL_SEND_BW.Threshold = [highThreshold, minBitRate, 0.5 * minBitRate];
        _rtpHDVideoStatType.TRANSMIT_BIT_RATE.Threshold = _rtpHDVideoStatType.AVAIL_SEND_BW.Threshold;
      }

      if (params.trackConstraints) {
        // Find the low bandwidth/quality video sender(s). We don't report stats for them.
        Object.keys(params.trackConstraints).forEach(function (id) {
          var track = params.trackConstraints[id];
          if (track.quality === Constants.VideoQuality.LOW) {
            _lowQualityVideoTracks[id] = true;
          }
        });
      }
    };
  }

  // Exports
  unifyPhone.CallStatsHandlerUnified = CallStatsHandlerUnified;
  unifyPhone.CallStats = CallStats;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/* global Promise */

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var IceCandidate = unifyPhone.IceCandidate;
  var logger = unifyPhone.logger;
  var sdpParser = unifyPhone.sdpParser;
  var Utils = unifyPhone.Utils;
  var VideoCodecs = unifyPhone.Enums.VideoCodecs;
  var VideoResolutionLevel = unifyPhone.Enums.VideoResolutionLevel;

  // CallStatsHandler and RtcPeerConnections cannot be imported here since they need to be spied upon during unit tests.

  var SdpStatus = Object.freeze({
    None: 'None',
    AnswerPending: 'Answer-Pending',
    AnswerSent: 'Answer-Sent',
    AnswerApplied: 'Answer-Applied',
    OfferPending: 'Offer-Pending',
    OfferSent: 'Offer-Sent',
    OfferReceived: 'Offer-Received',
    Connected: 'Connected',
    PrAnswerReceived: 'PrAnswer-Received',
    PrAnswerApplied: 'PrAnswer-Applied',
    AnswerReceived: 'Answer-Received',
    Disconnected: 'Disconnected'
  });

  // Default timeout for waiting for candidates when Trickle ICE is not enabled
  // Increase ICE candidate collection timeout for mobile devices because there have been cases in the field
  // where it's taking more than the default 2s timeout.
  var DEFAULT_CANDIDATES_TIMEOUT = Utils.isMobile() ? 7000 : 2000;

  // Shorter timeout started when first relay candidate is received.
  // This is used exclusively for telephony calls which cannot support Trickle ICE.
  var SHORT_CANDIDATES_TIMEOUT = 500;

  var DEFAULT_TRICKLE_ICE_TIMEOUT = 200; // Default timeout waiting for candidates when Trickle ICE is enabled
  var DEFAULT_ENABLE_AUDIO_AGC = true;
  var DEFAULT_ENABLE_AUDIO_EC = true;
  var DEFAULT_DISABLE_AUDIO_CN = false;

  // Default upscale factor for remote screen share video
  var DEFAULT_UPSCALE_FACTOR = 1.5;

  // getUserMedia errors
  var GUM_CONSTRAINT_ERROR = 'ConstraintNotSatisfiedError';
  var GUM_OVERCONSTRAINT_ERROR = 'OverconstrainedError';
  var GUM_DEVICE_IN_USE_CHROME_ERROR = 'TrackStartError';
  var GUM_DEVICE_IN_USE_ERROR = 'NotReadableError';

  var GumPermissionDeniedErrors = ['PermissionDeniedError', 'SecurityError', 'NotAllowedError'];

  // Timeout waiting for relay candidates over a TURN TCP/TLS connection
  var DESKTOP_RELAY_CANDIDATES_TIMEOUT = 5000;

  // Timeout before raising onIceDisconnected event
  var ICE_DISCONNECT_DELAY = 1000;

  var DEFAULT_VIDEO_CODEC = VideoCodecs.VP8;

  var DEGRADATION_MODE = Object.freeze({
    BALANCED: 'balanced',
    MAINTAIN_RESOLUTION: 'maintain-resolution',
    MAINTAIN_FRAMERATE: 'maintain-framerate'
  });

  var DEFAULT_SDP_PARAMS = Object.freeze({
    audio: {
      maxplaybackrate: 24000,
      'sprop-maxcapturerate': 24000,
      maxaveragebitrate: 16000,
      usedtx: 1
    },
    lowVideo: {
      '1080p': {
        scaleFactor: 6 // 1920px to 320px
      },
      '720p': {
        scaleFactor: 4 // 1280px to 320px
      },
      '480p': {
        scaleFactor: 2.66875 // 854px to 320px
      },
      maxBw: 100,
      scaleFactor: 2 // 640px to 320px
    },
    video: {
      maxBw: 512
    },
    hdVideo: {
      '1080p': {
        maxBw: 2000
      },
      '720p': {
        maxBw: 1500
      },
      '480p': {
        maxBw: 512
      },
      maxBw: 2000 // Default
    },
    screenShare: {
      maxBw: 600,
      minFrameRate: 3,
      maxFrameRate: 5
    },
    hdScreenShare: {
      maxBw: 2000,
      minFrameRate: 3,
      maxFrameRate: 7
    },
    rcScreenShare: {
      maxBw: 300,
      minFrameRate: 5,
      maxFrameRate: 15
    },
    receiveVideo: {
      maxBwMobile: 512,
      maxBw: 2000
    },
    rtcpMuxPolicy: 'require',
    preferredVideoCodec: DEFAULT_VIDEO_CODEC
  });

  var DEFAULT_XGOOGLE = Object.freeze({
    video: {
      minBitRate: 60
    },
    hdVideo: {
      // [CMR] -> Tweaking BW settings based on Chromecast reverse engineering SDP
      // https://www.bountysource.com/issues/877114-chromecast-video-output
      '1080p': {
        minBitRate: 1200,
        maxBitRate: 2000
      },
      '720p': {
        minBitRate: 750,
        maxBitRate: 1500
      },
      '480p': {
        minBitRate: 500,
        maxBitRate: 512
      },
      startBitRate: 500,
      minBitRate: 1000,
      maxBitRate: 2000,
      maxQuantization: 56
    },
    screenShare: {
      startBitRate: 300,
      minBitRate: 300,
      maxBitRate: 600,
      maxQuantization: 50
    },
    hdScreenShare: {
      startBitRate: 1000,
      minBitRate: 1000,
      maxBitRate: 2000,
      maxQuantization: 40
    }
  });

  var LOCAL_STREAMS = Object.freeze({
    AUDIO_VIDEO: 0,
    DESKTOP: 1
  });

  var HD_VIDEO_RESOLUTIONS = Object.values(VideoResolutionLevel);

  // Additional peer connections count to receive group call videos
  var MAX_VIDEO_EXTRA_CHANNELS = 3;
  var DEFAULT_VIDEO_EXTRA_CHANNELS = 3;

  function normalizeMediaType(mediaType) {
    mediaType = mediaType || {};
    mediaType = {
      audio: !!mediaType.audio,
      video: !!mediaType.video,
      hdVideo: !!mediaType.hdVideo,
      videoResolution: mediaType.videoResolution || null, // HD video resolution. Default is 1080p.
      desktop: !!mediaType.desktop,
      hdDesktop: !!mediaType.hdDesktop,
      minFrameRate: mediaType.minFrameRate || null, // Enforced min frame rate to overwrite default of 24.
      remoteControlEnabled: false
    };

    if (mediaType.videoResolution && (!mediaType.hdVideo || !HD_VIDEO_RESOLUTIONS.includes(mediaType.videoResolution))) {
      // HD video not enabled or not a valid HD video resolution
      mediaType.videoResolution = null;
    }
    return mediaType;
  }

  function DisabledRemoteOfferMLines() {
    var _list = {};
    return {
      add: function (index, mline) {
        _list[index] = mline;
      },
      get: function (index) {
        return _list[index];
      },
      remove: function (index) {
        delete _list[index];
      },
      getAll: function () {
        return _list;
      },
      removeAll: function () {
        _list = {};
      }
    };
  }

  /////////////////////////////////////////////////////////////////////////////////////////
  // RtcSessionController
  //
  // This "class" encapsulates the WebRTC API calls and provides the common functionality
  // that is required by the UC Client Refresh and Ansible projects.
  //
  // In a high level, the RtcSessionController provides the following functionality:
  // - Gets and maintains the MediaStream object
  // - Encapsulates the calls to the RTCPeerConnection object
  // - Supports Trickle ICE (can be enabled or disabled)
  // - Handles Hold and Retrieve requests and maintains the holding/held status
  // - Handles Mute and Unmute requests and maintains current status
  // - Handles Add Video and Remove Video requests
  // - Handles media renegotiation requests
  // - Normalizes the local and remote session descriptions
  // - Keeps the local video URL
  // - Keeps the remote audio or video URL
  // - Provides an eventing interface similar to RTCPeerConnection
  //
  /////////////////////////////////////////////////////////////////////////////////////////
  // eslint-disable-next-line max-lines-per-function
  function RtcSessionController(options) {
    // NOSONAR

    // The following imports need to be defined inside RtcSessionController due to JS-SDK
    var WebRTCAdapter = unifyPhone.WebRTCAdapter;

    options = options || {};

    ///////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////
    var GUM_TIMEOUT = 120000; // Timeout waiting for mic or camera to respond
    var CONNECTED_TIMEOUT = 15000; // Timeout waiting for connection state 'connected' on all peer connections

    var MOCK_CONNECT_TIMEOUT = 1000; // Timeout to fake connection state 'connected' for mock scenarios

    var NEXT_PC_TIMEOUT = 30000; // Timeout to keep nextPc available

    var LOCAL_AUDIO_VIDEO = 0;
    var LOCAL_SCREEN_SHARE = 1;

    var DIRECT_CALL_SDP_M_LINE_INDEX = {
      audio: 0,
      video: 1,
      screen: 2
    };

    // Maximum bandwidth to use with AS parameter on remote SDP for Chrome WebRTC implementation
    var MAX_BANDWIDTH = Utils.isMobile() ? 2000 : 10000;

    ///////////////////////////////////////////////////////////////////////////
    // Local variables
    ///////////////////////////////////////////////////////////////////////////
    var _that = this;

    var _callId = options.callId || '';
    var _isDirectCall = !!options.isDirectCall;

    var _isTelephonyCall = !!options.isTelephonyCall;

    var _midMappingEnabled = !!options.midMappingEnabled;

    var _isMobile = Utils.isMobile();

    // Number of extra video channels which are negotiated
    var _numberOfExtraVideoChannels = 0;

    // Local Media Streams
    // Index 0 contains the Audio+Video stream
    // Index 1 contains the Desktop (screen-sharing) stream
    var _localStreams = [null, null];

    // RTCPeerConnections instance (audio/video streams)
    var _pc = null;
    var _nextPc = null;

    var _pcSettings = {
      // Audio/video and desktop (i.e. screenshare)
      main: {
        sdpStatus: SdpStatus.None,
        localSdpType: null,
        // Trickle ICE for Offer is enabled by default for all non telephony calls.
        trickleIceForOffer: !_isTelephonyCall && !RtcSessionController.disableTrickleIce && !options.disableTrickleIce,
        // Trickle ICE for Answer is enabled if the Offer indicates support for Trickle ICE.
        trickleIceForAnswer: false,
        // Timer to wait for candidates when Trickle ICE is not enabled
        candidatesTimer: null,
        // Shorter timer to wait for candidates when Trickle ICE is not enabled and first relay candidate is received
        shortCandidatesTimer: null,
        // Timer to wait for connection state to change to connected
        connectedTimer: null,
        // Contains the origin field of the local session description
        localOrigin: null,
        sentIceCandidates: [],
        // Disabled remote offer m-lines that need to be restored before sending the answer SDP back
        disabledRemoteOfferMLines: new DisabledRemoteOfferMLines(),
        // Keep track of when the local description was set in the pre-allocated peer connection
        nextPcLocalSdpSetAt: 0
      }
    };

    // Collect call statistics
    var _callStats = null;

    var _turnUris = [];
    var _turnCredentials = {};

    // Flag to indicate that a media renegotiation is in progress
    var _renegotiationInProgress = false;
    var _renegotiationStartedLocally = false;
    var _pendingAbort = false;

    // Callback function that must be invoked when the media renegotiation is completed
    var _renegotiationCb = null;

    // The previous MediaStreams and PC instances that are saved during media renegotiations
    var _oldLocalStreams = null;
    var _oldPC = null;
    var _oldCallStats = null;

    // Local and Remote streams
    var _localVideoStream = null;
    var _localDesktopStream = null;

    var _remoteVideoStreams = [];
    // Remote audio stream information (there's always only one)
    var _remoteAudioStream = null;

    // The negotiated media types. This variable only tracks audio & video since we
    // cannot differentiate video and desktop (screen share) from the SDP negotiation.
    var _activeMediaType = { audio: false, video: false };
    var _oldActiveMediaType = null;

    var _held = false;
    var _holding = false;
    var _holdInProgress = false;
    var _retrieveInProgress = false;
    var _isMuted = false;

    // Constraints
    var _mediaConstraints = normalizeMediaType();
    var _oldMediaConstraints = null;

    // The following variable is used to send DTMF digits using the RTCDTMFSender interface.
    var _dtmfSender = null;

    // Flag that indicates whether the session has been warmed-up.
    var _warmedUp = false;
    // The _warmedUpSdp is the SDP that has been passed as input to the warmup function
    var _warmedUpSdp = null;

    var _isMockedCall = false;

    // Flag to control whether or not to disable remote video streams
    var _remoteVideoDisabled = false;
    var _remoteVideoScreenOnlyAllowed = false;

    var _remoteStreams = [];

    var _screenShareParams = null;

    // For an ATC pull call scenario the first received SDP Offer is a dummy Offer which
    // will not be used for the actual connection. In this case we should not waste time
    // collecting candidates from a TURN server.
    var _ignoreTurnOnNextPC = !!options.isAtcPullCall;

    var _pendingRemoteSdp = null;
    var _pendingChangeMedia = null;
    var _sessionTerminated = false;

    // Flag used to indicate that the new SDP Offer/Answer exchange will be ignored, so we
    // don't need to waste time waiting for ICE candidates.
    var _ignoreNextConnection = false;

    // Indicates whether the controller is only being used for candidates collection test
    var _candidatesCollectionTest = !!options.candidatesCollectionTest;

    var _browser = Utils.getBrowserInfo();
    var _lastGUMAudioConstraints = {};

    var _isDirectUpgradingToConf = false;

    var _dontReuseAudioStream = false;

    // We're required to remember the a:mid attribute of the audio offer because
    // SBC won't send it in the answer. So we need to set it until SBC adds support for it.
    // It's required for Firefox v63+ and Chrome (only if using Unified SDP)
    var _lastAudioOfferMidAttr = '';

    // By default multiplex is disabled. But we enable it in certain call scenarios.
    // It's not being stored in _pcSettings because it needs to be set before the peer connections are created
    var _enableMultiplex = false;

    // Store video track constraints like quality, min and max bitrate, direction
    var _videoTrackConstraints = {};

    var _supportsVideoReceiverConfiguration = !!options.supportsVideoReceiverConfiguration;

    var _disableConnectedTimer = false;

    /////////////////////////////////////////////////////////////////////////////
    // Media Capture and Streams
    ////////////////////////////////////////////////////////////////////////////
    function watchStreamEnded(stream, reset) {
      if (_isMockedCall) {
        // Ignore event for mock calls
        return;
      }

      var handler = reset
        ? null
        : function () {
            var isDesktop = stream === _localStreams[LOCAL_SCREEN_SHARE];
            logger.warn('[RtcSessionController]: Local ' + (isDesktop ? 'Desktop' : 'Audio/Video') + ' Media Stream has ended unexpectedly');
            sendLocalStreamEnded(isDesktop);
          };
      // Register for onended event on each track in the stream
      // This event may be fired multiple times
      stream.getTracks().forEach(function (t) {
        t.onended = handler;
      });
    }

    function getAudioConstraints(audioSources, param) {
      var source = Utils.selectMediaDevice(audioSources, RtcSessionController.recordingDevices);
      var constraints = {
        enableAudioAGC: RtcSessionController.enableAudioAGC,
        enableAudioEC: RtcSessionController.enableAudioEC,
        disableAudioCN: RtcSessionController.disableAudioCN,
        sourceId: param.newInputDevice || (source && source.id)
      };
      if (
        !_dontReuseAudioStream &&
        _renegotiationInProgress &&
        (_browser.firefox || _browser.chrome) &&
        _mediaConstraints.audio === _oldMediaConstraints.audio &&
        _lastGUMAudioConstraints.enableAudioAGC === constraints.enableAudioAGC &&
        _lastGUMAudioConstraints.enableAudioEC === constraints.enableAudioEC &&
        _lastGUMAudioConstraints.disableAudioCN === constraints.disableAudioCN &&
        _lastGUMAudioConstraints.sourceId === constraints.sourceId
      ) {
        var av = _oldLocalStreams[LOCAL_AUDIO_VIDEO];
        var oldAudioTrack = av && av.getAudioTracks()[0];
        if (oldAudioTrack && oldAudioTrack.readyState !== 'ended') {
          // We can reuse the local audio stream from the old connection (Chrome and FF only)
          param.oldAudioTrack = oldAudioTrack;
          return null;
        }
      }
      logger.debug('[RtcSessionController]: Input device selected: ', source);
      return constraints;
    }

    function getUserMedia(successCb, errorCb) {
      logger.debug('[RtcSessionController]: Get User Media for ', _mediaConstraints);
      if (_mediaConstraints.audio || _mediaConstraints.video) {
        getUserMediaAudioVideo(function () {
          // Make sure there is no Desktop (i.e. screen-share) stream
          // and then invoke the success callback.
          setLocalStream(null, LOCAL_SCREEN_SHARE);
          successCb();
        }, errorCb);
      } else {
        window.setTimeout(successCb, 0);
      }
    }

    function getUserMediaAudioVideo(successCb, errorCb, newInputDevices) {
      var reuseLocalStream = function () {
        if (newInputDevices && _localStreams[LOCAL_AUDIO_VIDEO]) {
          // A new input device is being specified, so we can't reuse the current stream
          // Set the current stream to null, so it won't be stopped by setLocalStream()
          _that.setLocalStream(LOCAL_AUDIO_VIDEO, null);
          return null; // Don't reuse stream
        }
        // NOTE: 1. The check for IE can be removed once Temasys allows getting a
        // second media stream for the same camera (ticket #1519).
        // 2. FF doesn't call gerUserMedia callback if the browser is not focused (bug #1195654 on bugzilla).
        // Reuse stream when screensharing is switching off to prevent errors after the timeout expires.
        if (_localStreams[LOCAL_AUDIO_VIDEO]) {
          // Reuse stream from another call (e.g.: 1-1 call upgraded to group call)
          var av = _localStreams[LOCAL_AUDIO_VIDEO];
          watchStreamEnded(av);
          _localStreams[LOCAL_AUDIO_VIDEO] = null; // Set it to null first, otherwise setLocalStream won't do anything
          return av;
        }
        var streamCanBeUsed = !_renegotiationStartedLocally || (_browser.firefox && !_mediaConstraints.desktop && _oldMediaConstraints.desktop);

        if (
          _renegotiationInProgress &&
          streamCanBeUsed &&
          WebRTCAdapter.browser !== 'android' &&
          _mediaConstraints.audio === _oldMediaConstraints.audio &&
          _mediaConstraints.video === _oldMediaConstraints.video &&
          _mediaConstraints.hdVideo === _oldMediaConstraints.hdVideo &&
          _mediaConstraints.videoResolution === _oldMediaConstraints.videoResolution
        ) {
          // Reuse stream from same call (media renegotiation)
          return _oldLocalStreams[LOCAL_AUDIO_VIDEO];
        }
        return null;
      };

      logger.debug('[RtcSessionController]: getUserMediaAudioVideo()');
      var gumTimeout = null;
      try {
        var reuseAudioOnlyTrack;
        var reuseStream = reuseLocalStream();
        if (reuseStream) {
          logger.debug('[RtcSessionController]: getUserMediaAudioVideo(): Reusing audio/video stream ID: ', WebRTCAdapter.getStreamId(reuseStream));
          setLocalStream(reuseStream, LOCAL_AUDIO_VIDEO);
          successCb();
          return;
        }

        var constraints = {
          audio: false,
          video: false
        };

        // Get the up-to-date list of devices that are plugged in and select one based on the list of
        // devices previously selected by the user
        WebRTCAdapter.getMediaSources(function (audioSources, videoSources) {
          logger.debug('[RtcSessionController]: Retrieved media sources');
          var audioConstraints;
          if (_mediaConstraints.audio) {
            var param = {
              newInputDevice: newInputDevices && newInputDevices.audio
            };
            audioConstraints = getAudioConstraints(audioSources, param);
            if (!audioConstraints && param.oldAudioTrack) {
              // Reuse the old audio track
              logger.debug('[RtcSessionController]: Reusing audio track');
              // Clone the audio track so stopping the old stream won't affect the new stream
              reuseAudioOnlyTrack = param.oldAudioTrack.clone();
            } else {
              constraints.audio = WebRTCAdapter.getAudioOptions(audioConstraints);
            }
          }

          var videoDevice;
          var videoConfig = {};
          var hdResolutions = [];
          if (_mediaConstraints.video) {
            if (_mediaConstraints.hdVideo) {
              var resolutionIdx = _mediaConstraints.videoResolution ? HD_VIDEO_RESOLUTIONS.indexOf(_mediaConstraints.videoResolution) : 0;
              hdResolutions = HD_VIDEO_RESOLUTIONS.slice(resolutionIdx);
              // Get the first HD resolution
              videoConfig.videoResolution = hdResolutions.shift();
              updateVideoResolutionInMediaConstraints(videoConfig.videoResolution);
            }
            videoConfig.minFrameRate = _mediaConstraints.minFrameRate;
            videoDevice = Utils.selectMediaDevice(videoSources, RtcSessionController.videoDevices);
            videoConfig.sourceId = videoDevice && videoDevice.id;
            constraints.video = WebRTCAdapter.getVideoOptions(videoConfig);
          }

          // Stop local video if has to change the HD video or resolution
          if (
            _renegotiationInProgress &&
            _mediaConstraints.video &&
            _oldMediaConstraints.video &&
            (_mediaConstraints.hdVideo !== _oldMediaConstraints.hdVideo || _mediaConstraints.videoResolution !== _oldMediaConstraints.videoResolution)
          ) {
            WebRTCAdapter.stopLocalVideoTrack(_oldLocalStreams[LOCAL_AUDIO_VIDEO]);
          }

          if (reuseAudioOnlyTrack && !constraints.audio && !constraints.video) {
            // We're left with only the reused audio track, so no getUserMedia is needed.
            // Create a brand new MediaStream object with the reused audio track
            var tempStream = new WebRTCAdapter.MediaStream([reuseAudioOnlyTrack]);
            watchStreamEnded(tempStream);
            setLocalStream(tempStream, LOCAL_AUDIO_VIDEO);
            successCb();
            return;
          }

          gumTimeout = window.setTimeout(function () {
            gumTimeout = null;
            logger.warn('[RtcSessionController]: Failed to access local media: device unresponsive');
            errorCb('res_MediaInputDevicesUnresponsive');
          }, GUM_TIMEOUT);

          var getLocalMedia = function () {
            logger.info('[RtcSessionController]: Calling getUserMedia - constraints = ', constraints);
            WebRTCAdapter.getUserMedia(
              constraints,
              function (stream, exception) {
                if (audioConstraints) {
                  _lastGUMAudioConstraints = audioConstraints;
                }
                logger.info('[RtcSessionController]: User has granted access to local media');
                logger.debug('[RtcSessionController]: Local MediaStream: ', WebRTCAdapter.getStreamId(stream));

                if (gumTimeout) {
                  window.clearTimeout(gumTimeout);
                  gumTimeout = null;
                } else {
                  logger.info('[RtcSessionController]: GUM time out already fired');
                  stopStream(stream);
                  return;
                }
                if (_sessionTerminated) {
                  logger.info('[RtcSessionController]: Session already terminated. Stop audio/video MediaStream: ', WebRTCAdapter.getStreamId(stream));
                  stopStream(stream);
                  return;
                }

                if (_mediaConstraints.video && !stream.getVideoTracks().length) {
                  if (_renegotiationInProgress && !_oldMediaConstraints.video) {
                    errorCb('res_CameraAccessRestricted');
                    return;
                  }
                  _mediaConstraints.video = false;
                  logger.info('[RtcSessionController]: Failed to get access to video stream. Fallback to audio-only.');
                  sendAsyncWarning('res_AddVideoFailed');
                  logger.info('[RtcSessionController]: New getUserMedia - constraints = ', _mediaConstraints);
                }
                if (reuseAudioOnlyTrack) {
                  stream.addTrack(reuseAudioOnlyTrack);
                }
                setLocalStream(stream, LOCAL_AUDIO_VIDEO);
                watchStreamEnded(stream);
                successCb();
                if (exception) {
                  logger.warn('[RtcSessionController]: Get user media succeeded. But we had an exception: ', exception.name || '<unknown>');
                  sendEvent(_that.onGetUserMediaException, { info: exception });
                }
              },
              function (error) {
                var errorName = (error && error.name) || 'Unspecified';
                if (videoConfig.videoResolution && (errorName === GUM_CONSTRAINT_ERROR || errorName === GUM_OVERCONSTRAINT_ERROR)) {
                  logger.warn('[RtcSessionController]: Failed to access local media with HD constraints: ', constraints.video);
                  // Try the next HD video resolution (if there is one)
                  videoConfig.videoResolution = hdResolutions.shift();
                  updateVideoResolutionInMediaConstraints(videoConfig.videoResolution);
                  constraints.video = WebRTCAdapter.getVideoOptions(videoConfig);
                  getLocalMedia();
                  return;
                }

                logger.warn('[RtcSessionController]: Failed to access local media: ', errorName);
                if (gumTimeout) {
                  window.clearTimeout(gumTimeout);
                  gumTimeout = null;
                }
                var strError;
                if (GumPermissionDeniedErrors.includes(errorName)) {
                  strError = _mediaConstraints.video
                    ? 'res_AccessToMediaInputDevicesFailedPermissionDenied'
                    : 'res_AccessToAudioInputDeviceFailedPermissionDenied';
                } else if (errorName === GUM_DEVICE_IN_USE_CHROME_ERROR || errorName === GUM_DEVICE_IN_USE_ERROR) {
                  // This error is normally returned if another process/tab is using the mic/camera
                  // https://addpipe.com/blog/common-getusermedia-errors/
                  strError = _mediaConstraints.video ? 'res_AccessToMediaInputDevicesFailedInUse' : 'res_AccessToAudioInputDeviceFailedInUse';
                } else {
                  strError = _mediaConstraints.video ? 'res_AccessToMediaInputDevicesFailed' : 'res_AccessToAudioInputDeviceFailed';
                }

                errorCb(strError);
              }
            );
          };

          getLocalMedia();

          logger.debug('[RtcSessionController]: Requested access to Local Media');
        });
      } catch (e) {
        logger.error('[RtcSessionController]: Exception while handling getUserMedia: ', e);
        if (gumTimeout) {
          window.clearTimeout(gumTimeout);
          gumTimeout = null;
        }
        errorCb(_mediaConstraints.video ? 'res_AccessToMediaInputDevicesFailed' : 'res_AccessToAudioInputDeviceFailed');
      }
    }

    function getUserMediaDesktop(successCb, errorCb, screenShareParams) {
      logger.debug('[RtcSessionController]: getUserMediaDesktop()');
      try {
        var constraints = { audio: false };
        var screenConstraint = getScreenConstraint();
        constraints.video = WebRTCAdapter.getDesktopOptions(screenShareParams.streamId || screenShareParams.screenType, screenConstraint);

        logger.info('[RtcSessionController]: Calling getUserMedia - constraints = ', constraints);

        WebRTCAdapter.getUserMedia(
          constraints,
          function (stream) {
            logger.info('[RtcSessionController]: User has granted access for screen capture. Stream Id:', WebRTCAdapter.getStreamId(stream));

            if (_sessionTerminated) {
              logger.info('[RtcSessionController]: Session already terminated. Stop desktop MediaStream: ', WebRTCAdapter.getStreamId(stream));
              stopStream(stream);
              return;
            }

            if (screenShareParams) {
              sendScreenSharePointerStatus(screenShareParams.pointer);
              _screenShareParams = screenShareParams;
            }

            watchStreamEnded(stream);

            setLocalStream(stream, LOCAL_SCREEN_SHARE);
            successCb();
          },
          function (error) {
            var errorName = (error && error.name) || 'Unspecified';
            logger.warn('[RtcSessionController]: Failed to access local media: ', errorName);

            // If getUserMedia returns PermissionDeniedError/SecurityError, assume that user has cancelled screenshare,
            // Error message was replaced with NotAllowedError in FF >= 49.
            if (GumPermissionDeniedErrors.includes(errorName)) {
              errorCb(Constants.ReturnCode.CHOOSE_DESKTOP_MEDIA_CANCELLED);
            } else {
              errorCb('res_AccessToDesktopFailed');
            }
          }
        );
        logger.debug('[RtcSessionController]: Requested access for screen capture');
      } catch (e) {
        logger.error('[RtcSessionController]: Exception while handling getUserMedia: ', e);
        errorCb('res_AccessToDesktopFailed');
      }
    }

    function updateVideoResolutionInMediaConstraints(resolution) {
      if (!resolution) {
        _mediaConstraints.hdVideo = false;
      }
      _mediaConstraints.videoResolution = resolution;
    }

    function getScreenConstraint() {
      var sdpParams = _mediaConstraints.hdDesktop ? 'hdScreenShare' : 'screenShare';
      var screenConstraint = {
        frameRate: {
          min: RtcSessionController.sdpParameters[sdpParams].minFrameRate,
          max: RtcSessionController.sdpParameters[sdpParams].maxFrameRate
        }
      };
      // adapt min/max framerate in case of remote control, default values might be higher than remote control values as they are configurable
      if (_mediaConstraints.remoteControlEnabled) {
        screenConstraint.frameRate.min = Math.max(screenConstraint.frameRate.min, RtcSessionController.sdpParameters.rcScreenShare.minFrameRate);
        screenConstraint.frameRate.max = Math.max(screenConstraint.frameRate.max, RtcSessionController.sdpParameters.rcScreenShare.maxFrameRate);
      }
      return screenConstraint;
    }

    /////////////////////////////////////////////////////////////////////////////
    // WebRTC 1.0: Real-time Communication Between Browsers
    /////////////////////////////////////////////////////////////////////////////
    function createPeerConnection(createNext) {
      if (createNext) {
        if (_nextPc) {
          return true;
        }
      } else if (_pc) {
        // The RTCPeerConnection instance has already been created.
        return true;
      }

      var config = {
        keysToOmitFromLogging: ['iceServers.[].credential', 'iceServers.[].username']
      };

      if (_isTelephonyCall) {
        config.rtcpMuxPolicy = RtcSessionController.sdpParameters.rtcpMuxPolicy; // Configurable (default is require)
      }

      if (_turnUris.length && !_ignoreTurnOnNextPC) {
        config.iceServers = [
          {
            urls: _turnUris,
            credential: _turnCredentials.password,
            username: _turnCredentials.username
          }
        ];
        if (RtcSessionController.allowOnlyRelayCandidates) {
          config.iceTransportPolicy = 'relay';
        }
      } else {
        if (!_ignoreTurnOnNextPC) {
          logger.warn('[RtcSessionController]: No TURN server(s) provided.');
        }
        config.iceServers = [];
      }
      var connectionBypassed = !!_ignoreTurnOnNextPC;
      _ignoreTurnOnNextPC = false;

      if (RtcSessionController.webRTCIPHandlingPolicy) {
        // Log the webRTCIPHandlingPolicy value so we know how the browser/DA is configured
        logger.info('[RtcSessionController]: webRTCIPHandlingPolicy is set to ', RtcSessionController.webRTCIPHandlingPolicy);
      }

      logger.debug('[RtcSessionController]: Creating RtcPeerConnections. config: ', config);

      var pcConstraints = {
        optional: [
          { DtlsSrtpKeyAgreement: true },
          // Improved bandwidth calculation
          { googImprovedWifiBwe: true },
          // These are useful for very detailed (HD) video
          { googHighBitrate: true },
          { googVeryHighBitrate: true },
          // Enable DSCP support
          { googDscp: true }
        ]
      };

      logger.debug('[RtcSessionController]: Creating peer connections. constraints:', pcConstraints);

      setExtraVideoChannels();

      var extraVideoChannels = _numberOfExtraVideoChannels;

      var onlyRemoteScreenShare = _remoteVideoScreenOnlyAllowed && !_supportsVideoReceiverConfiguration;

      // If local video is enabled and we only allow remote screen share, then negotiate a sendOnly video channel
      // In case the call type supports video receiver configuration do not change the amount of video lines
      if (_mediaConstraints.video && onlyRemoteScreenShare) {
        logger.debug('[RtcSessionController]: Local video is enabled and we only allow remote screenshare. We need to setup an extra sendonly video channel.');
        extraVideoChannels = 1;
      }
      var pcOptions = {
        extraVideoChannels: extraVideoChannels,
        // Create desktop peer connection only if:
        // Feature is not disabled and...
        // ...it's a group call and...
        // ...if receiving remote video is enabled or we're sending screenshare or we are allowing remote screenshare
        createDesktopPc:
          !RtcSessionController.disableDesktopPc &&
          !_isDirectCall &&
          (_supportsVideoReceiverConfiguration || !_remoteVideoDisabled || _mediaConstraints.desktop || _remoteVideoScreenOnlyAllowed),
        onlyRemoteScreenShare: onlyRemoteScreenShare
      };

      logger.debug('[RtcSessionController]: Creating peer connections. options:', pcOptions);

      var pc;
      try {
        if (!createNext) {
          _remoteStreams = [];
        }

        pc = new unifyPhone.RtcPeerConnectionsUnified(config, pcOptions);
        pc.connectionBypassed = connectionBypassed;
        logger.debug('[RtcSessionController]: Created audio/video peer connections. Connection bypassed: ', connectionBypassed);
      } catch (e) {
        logger.error('[RtcSessionController]: Failed to create peer connections.', e);
        return false;
      }

      if (createNext) {
        _nextPc = pc;
      } else {
        _pc = pc;
        _callStats = createCallStatsHandler([_pc]);
        registerPcEvtHandlers(_pc, _callStats);
      }

      return true;
    }

    function isActivePc(pc) {
      return !!pc && pc === _pc;
    }

    function registerPcEvtHandlers(pc, stats) {
      if (pc) {
        pc.onicecandidate = handleIceCandidate.bind(null, pc);
        pc.oniceconnectionstatechange = handleConnectionStateChange.bind(null, pc);
        pc.onaddstream = handleAddStream.bind(null, pc);
        pc.onremovestream = handleRemoveStream.bind(null, pc);
      }
      if (stats) {
        stats.onThresholdExceeded = function (cleared) {
          sendEvent(_that.onStatsThresholdExceeded, { cleared: cleared });
        };
        stats.onNoOutgoingPackets = function () {
          sendEvent(_that.onStatsNoOutgoingPackets, {});
        };
        stats.onNetworkQuality = function (quality) {
          sendEvent(_that.onNetworkQuality, { quality: quality });
        };
      }
    }

    function unregisterPcEvtHandlers(pc, stats) {
      if (pc) {
        pc.onicecandidate = null;
        pc.oniceconnectionstatechange = null;
        pc.onaddstream = null;
        pc.onremovestream = null;
      }
      if (stats) {
        stats.onThresholdExceeded = null;
        stats.onNoOutgoingPackets = null;
        stats.onNetworkQuality = null;
      }
    }

    function createCallStatsHandler(pcList) {
      return new unifyPhone.CallStatsHandlerUnified(pcList, _isDirectCall);
    }

    function handleIceCandidate(pc, event) {
      if (!isActivePc(pc)) {
        // Event is for old peer connection. We should never get here, but...
        logWarning('Received icecandidate event for old PC');
        return;
      }

      // Avoid calling pc.localDescription since this makes a call to native code for the mobile clients

      var sdpStatus = getSetting(pc, 'sdpStatus');
      var sdpType = getSetting(pc, 'localSdpType');

      if (event.candidate) {
        if (!event.candidate.candidate) {
          logDebug('Ignore empty candidate for mid ' + event.candidate.sdpMid);
          return;
        }
        var candidate = new IceCandidate(event.candidate.candidate);
        if (candidate.transport !== 'udp') {
          logDebug('Ignore non UDP candidate');
          return;
        }

        if (pc === _pc && candidate.typ === 'relay') {
          pc.hasRelayCandidates = true;
        }
        logDebug('New ICE candidate: ', event.candidate);

        if (useTrickleIce(pc, sdpType)) {
          if (getSetting(pc, 'candidatesTimer') && (sdpStatus === SdpStatus.OfferPending || sdpStatus === SdpStatus.AnswerPending)) {
            ///////////////////////////////////////////////////////////////////////////////////////////
            // WORKAROUND!!!
            // When collecting host ICE candidates takes too long the client waits additionaly for the first ICE
            // candidate before sending the local session description.
            sendLocalDescription(pc, pc.localDescription);
            return;
          }

          var data = {
            candidate: event.candidate.candidate,
            sdpMid: event.candidate.sdpMid,
            sdpMLineIndex: event.candidate.sdpMLineIndex
          };
          if (checkRelayCandidate(pc, data)) {
            if (sdpStatus !== SdpStatus.None && sdpStatus !== SdpStatus.OfferPending && sdpStatus !== SdpStatus.AnswerPending) {
              collectCandidate(data, pc);
            } else {
              logDebug('Ignore ICE candidate in state ', sdpStatus);
            }
          }
        } else if (_isTelephonyCall && candidate.typ === 'relay' && !getSetting(pc, 'shortCandidatesTimer')) {
          logDebug('Received first relay candidate. Start short candidate colection timer.');
          var shortTimer = window.setTimeout(function () {
            setSetting(pc, 'shortCandidatesTimer', null);
            logDebug('Short candidate collection timer has expired. Send SDP.');
            sendLocalDescription(pc, pc.localDescription);
          }, SHORT_CANDIDATES_TIMEOUT);
          setSetting(pc, 'shortCandidatesTimer', shortTimer);
        }
      } else {
        logDebug('End of candidates. PC: audio/video');
        pc.endOfCandidates = true; // Mark this peer connection as finished and...

        if (isEndOfCandidates()) {
          var localDescription = pc.localDescription;
          if (!localDescription) {
            logError('End of candidates, but cannot access pc.localDescription');
            return;
          }

          if (useTrickleIce(pc, sdpType)) {
            if (sdpStatus !== SdpStatus.None && sdpStatus !== SdpStatus.OfferPending && sdpStatus !== SdpStatus.AnswerPending) {
              sendEndOfCandidates(pc);
            } else {
              sendLocalDescription(pc, localDescription);
            }
          } else {
            sendLocalDescription(pc, localDescription);
          }
        }
      }
    }

    function handleConnectionStateChange(pc) {
      if (!isActivePc(pc)) {
        // Event is for old peer connection. We should never get here, but...
        logWarning('Received iceconnectionstatechange event for old PC');
        return;
      }

      var pcType = 'audio/video';
      logDebug('Connection state change (audio/video): ', pc.iceConnectionState);

      if (
        _turnUris.length &&
        pc === _pc &&
        !pc.connectionBypassed &&
        (pc.iceConnectionState === 'completed' || pc.iceConnectionState === 'failed') &&
        !pc.hasRelayCandidates
      ) {
        // We finished collecting all candidates and no relay candidates were generated for the main peer connection.
        // This indicates connectivity problems between the client and the TURN server(s).
        logWarning('[RtcSessionController]: No relay candidates were collected, please check connectivity to the TURN servers');
      }

      var sdpStatus = getSetting(pc, 'sdpStatus');
      switch (sdpStatus) {
        case SdpStatus.Connected:
          switch (pc.iceConnectionState) {
            case 'connected':
            case 'completed':
              sendIceConnected(pc, pcType);
              break;
            case 'disconnected':
              if (!pc.connectionBypassed) {
                sendIceDisconnected(pc, pcType);
              }
              break;
            case 'failed':
              if (!pc.connectionBypassed) {
                setSdpStatus(SdpStatus.Disconnected, pc); // Mark it as disconnected, so we handle this failure only once
                logError('[RtcSessionController]: ICE connection has failed');
                handleFailure('res_CallMediaDisconnected');
              }
              break;
          }
          break;

        case SdpStatus.AnswerReceived:
        case SdpStatus.PrAnswerReceived:
        case SdpStatus.PrAnswerApplied:
        case SdpStatus.AnswerApplied:
        case SdpStatus.AnswerSent:
          switch (pc.iceConnectionState) {
            case 'connected':
            case 'completed':
              pc.connected = true;
              if (isPcConnected(pc)) {
                clearConnectedTimer(pc);
                setSdpStatus(SdpStatus.Connected, pc);
                sendIceConnected(pc, pcType);
              }
              break;
            case 'failed':
              clearConnectedTimer(pc);
              handleConnectionFailed();
              break;
          }
          break;
      }
    }

    function handleAddStream(pc, event) {
      logDebug('Added Remote Stream');
      if (_renegotiationInProgress) {
        // Ignore added streams while renegotiation is in progress.
        // The remote streams will be added once the renegotiation is successful.
        logDebug('Renegotiation is in progress. Ignore AddStream event.');
        return;
      }
      putRemoteStreams([event.stream]);
      sendRemoteStreamUpdated();
    }

    function handleRemoveStream(pc, event) {
      logWarning('Removed Remote Stream');
      // Remove stream from list
      deleteRemoteStream(event.stream);
      sendRemoteStreamUpdated();
    }

    function handleDTMFToneChange(pc, event) {
      logDebug('RTCDTMFSender - ontonechange', !event.tone ? ': finished' : '');
      sendDTMFToneChange(event.tone);
    }

    /////////////////////////////////////////////////////////////////////////////
    // Event Senders
    /////////////////////////////////////////////////////////////////////////////
    function sendEvent(eventHandler, event) {
      if (typeof eventHandler === 'function') {
        try {
          eventHandler(event || {});
        } catch (e) {
          logger.error(e);
        }
      }
    }

    function sendIceCandidates(candidates, pc) {
      var settings = getSettings(pc);
      if (!settings) {
        return;
      }
      if (candidates) {
        var sentIceCandidates = settings.sentIceCandidates;
        var endOfCandidates = false;
        candidates = candidates.filter(function (data) {
          if (data.candidate.startsWith('a=end-of-candidates')) {
            endOfCandidates = true;
            return true;
          }
          var candidate = data.candidate;
          var found = sentIceCandidates.some(function (c) {
            return c.equals(candidate);
          });
          if (found) {
            logWarning('Ignore duplicate ICE candidate: ', data.candidate);
          } else {
            sentIceCandidates.push(new IceCandidate(candidate));
          }
          return !found;
        });
        if (candidates.length > 0) {
          logger.debug('[RtcSessionController]: Send IceCandidate event');

          var origin = getSdpOrigin(pc);
          var evtData = {
            origin: origin,
            candidates: candidates,
            endOfCandidates: endOfCandidates,
            isAudioVideo: true
          };

          // Invoke onIceCandidate event handler
          sendEvent(_that.onIceCandidate, evtData);
          waitConnectedState(pc);
        }
      }
    }

    function sendIceConnected(pc, pcType) {
      if (pc.iceDisconnectTimeout) {
        window.clearTimeout(pc.iceDisconnectTimeout);
        delete pc.iceDisconnectTimeout;
      }
      logger.debug('[RtcSessionController]: send IceConnected event pcType:', pcType);
      sendEvent(_that.onIceConnected, { pcType: pcType });
    }

    function sendIceDisconnected(pc, pcType) {
      if (pc.iceDisconnectTimeout) {
        return;
      }

      // Delay the IceDisconnect event for transient cases
      pc.iceDisconnectTimeout = window.setTimeout(function () {
        delete pc.iceDisconnectTimeout;
        if (pc.iceConnectionState === 'disconnected') {
          logger.debug('[RtcSessionController]: send IceDisconnected event pcType:', pcType);
          sendEvent(_that.onIceDisconnected, { pcType: pcType });
        }
      }, ICE_DISCONNECT_DELAY);
    }

    function sendEndOfCandidates(pc) {
      var settings = getSettings(pc);
      if (!settings) {
        return;
      }
      var candidates = [];
      if (settings.pendingCandidates) {
        logger.info('[RtcSessionController]: Send all pending candidates.');
        candidates = settings.pendingCandidates;
      }
      if (settings.queuedCandidates) {
        logger.info('[RtcSessionController]: There was no relay candidate for TURN over TCP/TLS. Send all queued candidates.');
        Array.prototype.push.apply(candidates, settings.queuedCandidates);
      }
      candidates.push({ candidate: 'a=end-of-candidates\r\n' });
      clearIceProperties(pc);
      sendIceCandidates(candidates, pc);
    }

    function sendLocalDescription(pc, localDescription) {
      if (!pc || !localDescription) {
        logError('sendLocalDescription invoked without PC or SDP');
        return;
      }
      clearCandidatesTimer(pc);
      var sdpStatus = getSetting(pc, 'sdpStatus');
      if (localDescription.type === 'offer') {
        if (sdpStatus === SdpStatus.OfferPending) {
          sendSessionDescription(pc, SdpStatus.OfferSent);
        }
      } else if (sdpStatus === SdpStatus.AnswerPending) {
        sendSessionDescription(pc, SdpStatus.AnswerSent);
      }
    }

    function combineLocalSdp(pc) {
      // Combine all SDPs into one
      var localSdp = pc.localDescription;
      var parsed = sdpParser.parse(localSdp.sdp);

      if (localSdp.type === 'answer') {
        logger.info('[RtcSessionController]: combineLocalSdp - Processing answer SDP');
        var disabledOfferMLines = getSetting(_pc, 'disabledRemoteOfferMLines');
        if (!_pc.desktopPeerConnection && disabledOfferMLines.get(DIRECT_CALL_SDP_M_LINE_INDEX.video)) {
          // Include the disabled video m-line from the offer
          parsed.m.splice(DIRECT_CALL_SDP_M_LINE_INDEX.video, 0, disabledOfferMLines.get(DIRECT_CALL_SDP_M_LINE_INDEX.video));
          disabledOfferMLines.remove(DIRECT_CALL_SDP_M_LINE_INDEX.video);
        }
      }

      if (pc.desktopPeerConnection) {
        logger.info('[RtcSessionController]: combineLocalSdp - Adjust screenshare m-line');
        var desktopMLineIndex = sdpParser.findMediaLineIndex(parsed, pc.getScreenShareMediaId());
        if (desktopMLineIndex >= 0) {
          var desktopSdp = parsed.m[desktopMLineIndex];
          if (desktopSdp) {
            desktopSdp = { m: [desktopSdp] };

            ///////////////////////////////////////////////////////////////////////////////////////////
            // WORKAROUND!!!
            // To ensure better screenshare quality for calls that go through the media server, we need to
            // allow only relay candidates from TCP or TLS TURN Servers.
            var removeUdpVideoIce = !_isDirectCall && !RtcSessionController.allowAllIceCandidatesScreenShare;
            logger.info('[RtcSessionController]: Remove UDP video candidates for screenshare: ', removeUdpVideoIce);

            var trickleIce = useTrickleIce(_pc, localSdp.type) && !_pc.endOfCandidates;
            sdpParser.validateIceCandidates(desktopSdp, removeUdpVideoIce, trickleIce);
          }
        } else {
          logger.warn('[RtcSessionController]: Could not find screenshare m-line: no ICE validation was made.');
        }
      }
      return {
        type: localSdp.type,
        parsedSdp: parsed
      };
    }

    function sendSessionDescription(pc, newStatus) {
      if (!pc) {
        logWarning('Cannot send Session Description for non existing pc.');
        return;
      }
      logger.info('[RtcSessionController]: sendSessionDescription - PC: audio/video');

      if (_renegotiationInProgress && _pendingAbort) {
        renegotiationFailed('Renegotiation aborted');
        return;
      }

      var sdp = combineLocalSdp(pc);
      var trickleIceEnabled = useTrickleIce(pc, sdp.type);

      var parsedSdp = sdp.parsedSdp;

      // Remove the ssrc-audio-level attribute, which imposes a security risk.
      parsedSdp = sdpParser.removeAudioLevelAttr(parsedSdp);

      ///////////////////////////////////////////////////////////////////////////////////////////
      // Add or remove trickle value in ice-options depending on Trickle ICE setting
      parsedSdp = sdpParser.fixTrickleIceOption(parsedSdp, trickleIceEnabled);

      ///////////////////////////////////////////////////////////////////////////////////////////
      // Remove 'end-of-candidates' attribute if Trickle ICE is disabled otherwise add it if all
      // candidates have already been received and it is missing.
      if (!trickleIceEnabled) {
        parsedSdp = sdpParser.removeEndOfCandidatesLine(parsedSdp);
      } else if (isEndOfCandidates()) {
        parsedSdp = sdpParser.addEndOfCandidatesLine(parsedSdp);
      }

      ///////////////////////////////////////////////////////////////////////////////////////////
      // WORKAROUND!!!
      // If the audio connection mode was changed, we need to restore the original connection
      // before the SDP is published.
      if (_pc.restoreAudioConnectionMode) {
        sdpParser.setConnectionMode(parsedSdp, 'audio', _pc.restoreAudioConnectionMode);
      }

      // Add default max bandwidth to all video m-lines
      var bw = !_isMobile || options.canReceiveHdVideo ? 'maxBw' : 'maxBwMobile';
      bw = RtcSessionController.sdpParameters.receiveVideo[bw];
      if (bw) {
        sdpParser.setVideoBandwidth(parsedSdp, bw, true);
      }

      var ignoreNoCandidates = _ignoreNextConnection || _candidatesCollectionTest;

      if (sdpParser.iceTotalCandidatesCount(parsedSdp) === 0 && !ignoreNoCandidates) {
        ///////////////////////////////////////////////////////////////////////////////////////////
        // WORKAROUND!!!
        // When the network on the local computer changes (e.g.: VPN is connected/disconnected),
        // Browser sometimes stop collecting any ICE candidates until the browser is restarted!
        // So we have to return the appropriate error so the user can be alerted to restart the browser
        if (trickleIceEnabled) {
          var timeout = getCandidatesTimeout();
          logger.debug('[RtcSessionController]: No ICE candidates collected yet. Waiting ' + timeout + ' more milliseconds.');
          var candidatesTimer = window.setTimeout(function () {
            setSetting(pc, 'candidatesTimer', null);
            handleFailure('res_RestartBrowser');
          }, timeout);
          setSetting(pc, 'candidatesTimer', candidatesTimer);
        } else {
          handleFailure('res_RestartBrowser');
        }
      } else if (sdpParser.validateIceCandidates(parsedSdp, false, _enableMultiplex) || (trickleIceEnabled && !isEndOfCandidates()) || _ignoreNextConnection) {
        if (trickleIceEnabled && !isEndOfCandidates()) {
          var settings = getSettings(pc);
          if (settings) {
            settings.sentIceCandidates = sdpParser.getIceCandidates(parsedSdp);
          }
        }
        var finalSdp = {
          type: sdp.type,
          sdp: sdpParser.stringify(parsedSdp)
        };
        if (pc === _pc && pc.desktopPeerConnection) {
          finalSdp.screen = _pc.getScreenShareMediaId();
          if (_localStreams[LOCAL_AUDIO_VIDEO]) {
            // If there's a local stream to be transmitted, set its ID in the sdp video field
            var streamId = getVideoStreamId(LOCAL_AUDIO_VIDEO);
            logger.info('[RtcSessionController]: Video streamId is ', streamId);
            var mid = null;
            if (_midMappingEnabled && streamId) {
              mid = getVideoMid(LOCAL_AUDIO_VIDEO);
            }
            finalSdp.video = mid || streamId;
            if (isSimulcastEnabled()) {
              finalSdp.videoSenderConfiguration = _pc.getAvailableVideoSenders(_videoTrackConstraints);
            }
          }
        }
        ///////////////////////////////////////////////////////////////////////////////////////////
        // Invoke onSessionDescription event handler
        logger.debug('[RtcSessionController]: send SessionDescription event');
        _ignoreNextConnection = false;
        sendEvent(_that.onSessionDescription, { sdp: finalSdp });
        setSdpStatus(newStatus, pc);
      } else {
        // There are ICE candidates but at least one m-line has no valid candidates
        logger.error('[RtcSessionController]: Failure: SDP contains at least one m line without IceCandidates');
        handleFailure();
      }
    }

    function sendSdpConnected() {
      // Invoke onSdpConnected event handler for RTC (or Conversation) Handler
      logger.debug('[RtcSessionController]: send SdpConnected event');
      sendEvent(_that.onSdpConnected, { sdpOrigin: getSdpOrigin(_pc) });
    }

    function sendRemoteVideoAdded() {
      // Invoke onRemoteVideoAdded event handler for RTC (or Conversation) Handler
      logger.debug('[RtcSessionController]: send RemoteVideoAdded event');
      sendEvent(_that.onRemoteVideoAdded, {});
    }

    function sendRemoteVideoRemoved() {
      // Invoke onRemoteVideoRemoved event handler for RTC (or Conversation) Handler
      logger.debug('[RtcSessionController]: send RemoteVideoRemoved event');
      sendEvent(_that.onRemoteVideoRemoved, {});
    }

    function sendRemoteStreamUpdated() {
      // Invoke onRemoteVideoRemoved event handler for RTC (or Conversation) Handler
      logger.debug('[RtcSessionController]: send RemoteStreamUpdated event');
      sendEvent(_that.onRemoteStreamUpdated, {});
    }

    function sendDTMFToneChange(tone) {
      // Invoke onDTMFToneChange event handler
      logger.debug('[RtcSessionController]: send DTMFToneChanged event');
      sendEvent(_that.onDTMFToneChange, { tone: tone });
    }

    function sendClosed() {
      // Invoke onClosed event handler
      logger.debug('[RtcSessionController]: send Closed event');
      sendEvent(_that.onClosed, {});
    }

    function sendError(error) {
      // Invoke onRtcError event handler
      logger.debug('[RtcSessionController]: send Error event');
      sendEvent(_that.onRtcError, { error: error });
    }

    function sendAsyncError(error) {
      if (typeof _that.onRtcError === 'function') {
        window.setTimeout(function () {
          sendError(error);
        }, 0);
      }
    }

    function sendWarning(warning) {
      // Invoke onRtcWarning event handler
      logger.debug('[RtcSessionController]: send Warning event');
      sendEvent(_that.onRtcWarning, { warning: warning });
    }

    function sendAsyncWarning(warning) {
      if (typeof _that.onRtcWarning === 'function') {
        window.setTimeout(function () {
          sendWarning(warning);
        }, 0);
      }
    }

    function sendMediaUpdate() {
      // Invoke onMediaUpdate event handler for Call
      logger.debug('[RtcSessionController]: send MediaUpdate event');
      sendEvent(_that.onMediaUpdate, {});
    }

    function sendLocalVideoStream() {
      // Invoke onLocalVideoStream event handler
      logger.debug('[RtcSessionController]: send LocalVideoStream event');

      sendEvent(_that.onLocalVideoStream, {
        // The local desktop (screen share) has precedence over the local video stream.
        stream: _localDesktopStream || _localVideoStream,
        videoStream: _localVideoStream,
        desktopStream: _localDesktopStream
      });
      sendMediaUpdate();
    }

    function sendRemoteStreams() {
      // Invoke onRemoteStreams event handler
      logger.debug('[RtcSessionController]: send RemoteStreams event');
      sendEvent(_that.onRemoteStreams, {
        audio: _remoteAudioStream,
        video: _remoteVideoStreams
      });
    }

    function sendLocalStreamEnded(isDesktop) {
      // Invoke onLocalStreamEnded event handler
      logger.debug('[RtcSessionController]: send LocalStreamEnded event');
      sendEvent(_that.onLocalStreamEnded, { isDesktop: isDesktop });
    }

    function sendScreenSharePointerStatus(pointer) {
      // Invoke onScreenSharePointerStatus event handler
      logger.debug('[RtcSessionController]: send ScreenSharePointerStatus event');
      sendEvent(_that.onScreenSharePointerStatus, {
        isSupported: !!(pointer && pointer.isSupported),
        isEnabled: !!(pointer && pointer.isEnabled)
      });
    }

    function sendQosAvailable(qos, isRenegotiation, lastSavedStats, streamQualityData) {
      // Invoke onQosAvailable event handler
      logger.debug('[RtcSessionController]: send QosAvailable event');
      sendEvent(_that.onQosAvailable, {
        qos: qos,
        renegotiationInProgress: isRenegotiation,
        lastSavedStats: lastSavedStats,
        streamQualityData: streamQualityData
      });
    }

    /////////////////////////////////////////////////////////////////////////////
    // Internal functions
    /////////////////////////////////////////////////////////////////////////////
    function getSettings(pc) {
      if (!pc) {
        return null;
      }
      if (pc === _pc) {
        return _pcSettings.main;
      }
      return null;
    }

    function setSetting(pc, setting, value) {
      if (!pc) {
        return;
      }
      if (pc === _pc) {
        _pcSettings.main[setting] = value;
      }
    }

    function getSetting(pc, setting) {
      var settings = getSettings(pc);
      return settings && settings[setting];
    }

    function clearCandidatesTimer(pc) {
      var timer = getSetting(pc, 'candidatesTimer');
      if (timer) {
        window.clearTimeout(timer);
        setSetting(pc, 'candidatesTimer', null);
      }
      // Also clear the short candidates timer
      clearShortCandidatesTimer(pc);
    }

    function clearShortCandidatesTimer(pc) {
      var timer = getSetting(pc, 'shortCandidatesTimer');
      if (timer) {
        window.clearTimeout(timer);
        setSetting(pc, 'shortCandidatesTimer', null);
      }
    }

    function clearConnectedTimer(pc) {
      var timer = getSetting(pc, 'connectedTimer');
      if (timer) {
        window.clearTimeout(timer);
        setSetting(pc, 'connectedTimer', null);
      }
    }

    function clearIceProperties(pc) {
      var settings = getSettings(pc);
      if (!settings) {
        return;
      }
      if (settings.collectCandidatesTimeout) {
        window.clearTimeout(settings.collectCandidatesTimeout);
        delete settings.collectCandidatesTimeout;
      }
      if (settings.relayRtpCandidateTimeout) {
        window.clearTimeout(settings.relayRtpCandidateTimeout);
        delete settings.relayRtpCandidateTimeout;
      }
      settings.sentIceCandidates = [];
      if (settings.pendingCandidates) {
        delete settings.pendingCandidates;
      }
      if (settings.queuedCandidates) {
        delete settings.queuedCandidates;
      }
      settings.hasRelayRtpCandidate = false;
      settings.sendAllCandidates = false;
    }

    function logDebug(text, param) {
      logger.debug('[RtcSessionController][' + _callId + ']: ' + text, param);
    }

    function logWarning(text, param) {
      logger.warn('[RtcSessionController][' + _callId + ']: ' + text, param);
    }

    function logError(text, param) {
      logger.error('[RtcSessionController][' + _callId + ']: ' + text, param);
    }

    function useTrickleIce(pc, type) {
      if (!pc) {
        return false;
      }

      if (!type) {
        return getSetting(pc, 'trickleIceForOffer') || getSetting(pc, 'trickleIceForAnswer');
      }

      return (type === 'offer' && getSetting(pc, 'trickleIceForOffer')) || (type === 'answer' && getSetting(pc, 'trickleIceForAnswer'));
    }

    function isTrickleIceIndicated(type, sdp) {
      var trickleIceIndication = sdp && sdpParser.isTrickleIceOption(sdp);
      if (trickleIceIndication) {
        logger.debug('[RtcSessionController]: SDP ' + type + ' indicates support for Trickle-ICE.');
      }
      return !!trickleIceIndication;
    }

    function isPcConnected(pc) {
      return pc && pc.connected;
    }

    function isEndOfCandidates() {
      return !_pc || _pc.endOfCandidates;
    }

    function getSdpOrigin(pc) {
      if (!pc) {
        // pc should always be defined here
        return '';
      }
      var localOrigin = getSetting(pc, 'localOrigin');

      if (!localOrigin) {
        var localDescription = pc.localDescription;
        if (localDescription) {
          localOrigin = sdpParser.getOrigin(localDescription.sdp);
          setSetting(pc, 'localOrigin', localOrigin);
        }
      }
      return localOrigin;
    }

    function collectCandidate(data, pc) {
      var settings = getSettings(pc);
      if (!settings) {
        return;
      }
      if (!settings.pendingCandidates) {
        settings.pendingCandidates = [];
        logger.debug('[RtcSessionController]: Start timer to collect candidates before send.');
        settings.collectCandidatesTimeout = window.setTimeout(function () {
          logger.debug('[RtcSessionController]: Timer expired. Send all pending candidates.');
          delete settings.collectCandidatesTimeout;
          if (settings.pendingCandidates) {
            sendIceCandidates(settings.pendingCandidates, pc);
            delete settings.pendingCandidates;
          }
        }, DEFAULT_TRICKLE_ICE_TIMEOUT);
      }
      settings.pendingCandidates.push(data);
    }

    function checkRelayCandidate(pc, data) {
      pc = pc || _pc;
      var settings = getSettings(pc);
      if (!settings) {
        return false;
      }

      // We only need to check relay candidates for the screen share connection
      if (
        RtcSessionController.allowAllIceCandidatesScreenShare ||
        data.sdpMid !== _pc.getScreenShareMediaId() ||
        settings.hasRelayRtpCandidate ||
        settings.sendAllCandidates
      ) {
        return true;
      }
      var candidate = new IceCandidate(data.candidate);
      if (candidate.isTcpTlsRelay()) {
        if (candidate.isRtp()) {
          logger.info('[RtcSessionController]: Found local relay candidate for TURN over TCP/TLS. Remove queued candidates.');
          settings.hasRelayRtpCandidate = true;
          delete settings.queuedCandidates;
          if (settings.relayRtpCandidateTimeout) {
            window.clearTimeout(settings.relayRtpCandidateTimeout);
            delete settings.relayRtpCandidateTimeout;
          }
        }
        return true;
      }
      if (!settings.queuedCandidates) {
        settings.queuedCandidates = [];
        logger.debug('[RtcSessionController]: Start timer to wait for relay candidate for TURN over TCP/TLS.');
        settings.relayRtpCandidateTimeout = window.setTimeout(function () {
          logger.info('[RtcSessionController]: Timer expired. Stop waiting for relay candidate for TURN over TCP/TLS and send all pending candidates.');
          delete settings.relayRtpCandidateTimeout;
          if (settings.queuedCandidates) {
            sendIceCandidates(settings.queuedCandidates, pc);
            delete settings.queuedCandidates;
          }
          settings.sendAllCandidates = true;
        }, DESKTOP_RELAY_CANDIDATES_TIMEOUT);
      }
      // Save candidate in case we don't get any relay candidate for TURN over TCP/TLS
      settings.queuedCandidates.push(data);
      logger.debug('[RtcSessionController]: Queue candidate in case there is no TURN over TCP/TLS relay candidate');
      return false;
    }

    function waitConnectedState(pc) {
      pc = pc || _pc;

      if (!pc) {
        logWarning('Terminating waitConnectedState for non existing pc.');
        return;
      }

      clearConnectedTimer(pc);

      if (_disableConnectedTimer) {
        logWarning('ICE connected timer is disabled');
        return;
      }

      if (isPcConnected(pc)) {
        setSdpStatus(SdpStatus.Connected, pc);
        return;
      }

      // Wait until (ice)connectionstate is 'connected' or 'completed' and fail if timeout expired.
      // The timeout will be restarted on each ICE_CANDIDATES message or event.
      var timeout = _isMockedCall ? MOCK_CONNECT_TIMEOUT : CONNECTED_TIMEOUT;
      logger.info('[RtcSessionController]: Starting ICE connected timer');
      var connectedTimer = window.setTimeout(function () {
        setSetting(pc, 'connectedTimer', null);
        var sdpStatus = getSetting(pc, 'sdpStatus');
        if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
          // Sometimes we don't get the iceConnectionState=connected event, but proceed with the call if it's connected
          logger.debug('[RtcSessionController]: Missed iceConnectionState=connected event but media is connected');
          pc.connected = true;
          setSdpStatus(SdpStatus.Connected, pc);
          sendIceConnected(pc, 'audio/video');
          return;
        }
        if ((sdpStatus === SdpStatus.AnswerSent || sdpStatus === SdpStatus.AnswerReceived || sdpStatus === SdpStatus.AnswerApplied) && !isPcConnected(pc)) {
          if (_isMockedCall) {
            // Simulate SDP connected for the mock server.
            setSdpStatus(SdpStatus.Connected, pc);
          } else {
            logger.warn('[RtcSessionController]: Timed out waiting for connected state.');
            handleConnectionFailed();
          }
        }
      }, timeout);
      setSetting(pc, 'connectedTimer', connectedTimer);
    }

    function closeNextPC() {
      if (_nextPc) {
        logger.debug('[RtcSessionController]: Closing the pre-allocated peer connection');
        setSetting(_pc, 'nextPcLocalSdpSetAt', 0);
        closePC(_nextPc);
        _nextPc = null;
      }
    }

    function closePC(pc, stats) {
      unregisterPcEvtHandlers(pc, stats);
      if (pc) {
        if (pc.iceDisconnectTimeout) {
          window.clearTimeout(pc.iceDisconnectTimeout);
          delete pc.iceDisconnectTimeout;
        }
        WebRTCAdapter.closePc(pc);
      }
    }

    function addLocalStreams(pc) {
      if (!pc) {
        return false;
      }
      var pcStreams = pc.getLocalStreams();
      if (!Utils.isEmptyArray(pcStreams)) {
        return true;
      }
      logger.debug('[RtcSessionController]: Add local streams to ' + (pc === _pc ? 'existing' : 'next') + ' peer connection');
      try {
        _localStreams[LOCAL_AUDIO_VIDEO] && pc.addStream(_localStreams[LOCAL_AUDIO_VIDEO], getOfferToReceiveVideo(), _videoTrackConstraints);
        logger.debug('[RtcSessionController]: Added local audio/video stream');

        if (_localStreams[LOCAL_SCREEN_SHARE]) {
          _localStreams[LOCAL_SCREEN_SHARE].isScreen = true;
          pc.addStream(_localStreams[LOCAL_SCREEN_SHARE], getOfferToReceiveVideo(), _videoTrackConstraints);
          logger.debug('[RtcSessionController]: Added local screenshare stream');
        }
      } catch (e) {
        logger.error('[RtcSessionController]: Failed to add Local Streams ', e);
        return false;
      }
      return true;
    }

    function removeLocalStreams() {
      logger.debug('[RtcSessionController]: Remove local streams from existing PeerConnections');
      try {
        var pcs = [_pc];
        pcs.forEach(function (pc) {
          if (pc) {
            pc.getLocalStreams().forEach(function (s, i) {
              if (s) {
                pc.removeStream(s);
                logger.debug('[RtcSessionController]: Removed PeerConnection Stream  #', i);
              }
            });
          }
        });
      } catch (e) {
        logger.error('[RtcSessionController]: Failed to remove Local Streams ', e);
        return false;
      }
      return true;
    }

    function stopStream(stream) {
      if (!stream) {
        return;
      }
      watchStreamEnded(stream, true);
      if (stream.getVideoTracks().length > 0) {
        stream.getVideoTracks().forEach(function (videoTrack) {
          var trackId = WebRTCAdapter.getTrackId(videoTrack);
          trackId && delete _videoTrackConstraints[trackId];
        });
      }
      WebRTCAdapter.stopMediaStream(stream);
    }

    function stopStreams(streams) {
      var reusedStreams = streams === _oldLocalStreams ? _localStreams : _oldLocalStreams;
      streams &&
        streams.forEach(function (stream) {
          // Make sure the stream being stopped is not being reused. If it is, don't stop it!
          if (!reusedStreams || reusedStreams.indexOf(stream) === -1) {
            stopStream(stream);
          }
        });
    }

    function setLocalVideoStreams(streams) {
      // Index 0 is video stream. Index 1 is the desktop stream.
      var videoStreams = [null, null];

      streams &&
        streams.forEach(function (stream, idx) {
          if (stream && stream.getVideoTracks().length > 0) {
            videoStreams[idx] = stream;
          }
        });

      if (_localVideoStream !== videoStreams[0] || _localDesktopStream !== videoStreams[1]) {
        _localVideoStream = videoStreams[0];
        logger.debug('[RtcSessionController]: Set local video stream to ', WebRTCAdapter.getStreamId(_localVideoStream) || '<null>');

        _localDesktopStream = videoStreams[1];
        logger.debug('[RtcSessionController]: Set local desktop stream to ', WebRTCAdapter.getStreamId(_localDesktopStream) || '<null>');

        sendLocalVideoStream();
      }
    }

    function getScaleFactor(hdVideo, resolution) {
      var lowVideoSdpParams = RtcSessionController.sdpParameters.lowVideo;
      var scaleFactor;
      if (!hdVideo || !resolution) {
        scaleFactor = lowVideoSdpParams.scaleFactor;
      } else {
        scaleFactor = lowVideoSdpParams[resolution].scaleFactor;
      }
      logger.debug('[RtcSessionController]: scaleFactor is ' + scaleFactor + ' (hdVideo=' + hdVideo + ', resolution=' + resolution + ')');
      return scaleFactor;
    }

    function setLocalStream(stream, index) {
      // Index 0 is the Audio+Video stream
      // Index 1 is the Desktop (screen-sharing) stream
      if (index !== LOCAL_AUDIO_VIDEO && index !== LOCAL_SCREEN_SHARE) {
        logger.error('[RtcSessionController]: setLocalStream invoked with invalid index:', index);
        return;
      }

      if (_localStreams[index] === stream) {
        // No changes
        return;
      }

      if (_localStreams[index]) {
        // Stop the previous local stream
        stopStream(_localStreams[index]);
      }
      _localStreams[index] = stream;

      // apply max bitrate for video and screen share stream
      var maxBw, mediaType, quality;
      if (index === LOCAL_AUDIO_VIDEO && _mediaConstraints.video) {
        if (_mediaConstraints.hdVideo) {
          if (_mediaConstraints.videoResolution) {
            var hdVideo = RtcSessionController.sdpParameters.hdVideo[_mediaConstraints.videoResolution];
            maxBw = hdVideo && hdVideo.maxBw;
          }
          mediaType = 'hdVideo';
          quality = Constants.VideoQuality.HIGH;
        } else {
          mediaType = 'video';
          quality = Constants.VideoQuality.NORMAL;
        }
        if (!maxBw) {
          maxBw = RtcSessionController.sdpParameters[mediaType].maxBw;
        }
      } else if (index === LOCAL_SCREEN_SHARE && _mediaConstraints.desktop) {
        mediaType = _mediaConstraints.hdDesktop ? 'hdScreenShare' : 'screenShare';
        maxBw = RtcSessionController.sdpParameters[mediaType].maxBw;
        // adapt max bandwidth in case of remote control, default values might be higher than remote control values as they are configurable
        if (_mediaConstraints.remoteControlEnabled) {
          maxBw = Math.max(maxBw, RtcSessionController.sdpParameters.rcScreenShare.maxBw);
        }
      }
      if (stream && stream.getVideoTracks().length > 0) {
        var videoTrack = stream.getVideoTracks()[0];

        if (videoTrack) {
          var trackConstraint = {};
          if (maxBw) {
            trackConstraint.maxBitrate = maxBw * 1000;
          }

          // Only apply this for VIDEO not DESKTOP_SHARING
          if (index === LOCAL_AUDIO_VIDEO) {
            if (quality) {
              trackConstraint.quality = quality;
            }
            // Set degradationPreference for HD calls on web/DA/SDK
            if (_mediaConstraints.hdVideo && !_isMobile) {
              trackConstraint.degradationPreference = RtcSessionController.degradationPreference;
            }
            // Check if a low resolution video stream (simulcast) should be added (if not already added)
            if (isSimulcastEnabled() && stream.getVideoTracks().length < 2) {
              var scaleResolutionDownBy = getScaleFactor(_mediaConstraints.hdVideo, _mediaConstraints.videoResolution);
              var lowVideoTrack = videoTrack.clone();
              var lowTrackConstraint = {
                quality: Constants.VideoQuality.LOW,
                maxBitrate: RtcSessionController.sdpParameters.lowVideo.maxBw * 1000,
                scaleResolutionDownBy: scaleResolutionDownBy,
                direction: _isMobile ? 'sendonly' : 'sendrecv'
              };
              _videoTrackConstraints[WebRTCAdapter.getTrackId(lowVideoTrack)] = lowTrackConstraint;
              stream.addTrack(lowVideoTrack);
            }
          }
          _videoTrackConstraints[WebRTCAdapter.getTrackId(videoTrack)] = trackConstraint;
        }
      }
      setLocalVideoStreams(_localStreams);
    }

    function clearRemoteStreams() {
      if (!_remoteAudioStream && !_remoteVideoStreams.length) {
        return;
      }
      _remoteAudioStream = null;
      _remoteVideoStreams = [];
      sendRemoteStreams();
    }

    function putRemoteStreamUnified(stream) {
      var updated = false;
      var videoTracks = stream.getVideoTracks();
      if (videoTracks.length) {
        var found = _remoteVideoStreams.some(function (v) {
          if (v.streamId === stream.mid) {
            logger.debug('[RtcSessionController]: Updated remote video streamId=' + stream.mid);
            v.stream = stream;
            updated = true;
            return true;
          }
          return false;
        });
        if (!found) {
          logger.debug('[RtcSessionController]: New remote video streamId=' + stream.mid);
          _remoteVideoStreams.push({ streamId: stream.mid, stream: stream });
          updated = true;
        }
      } else {
        var audioTracks = stream.getAudioTracks();
        if (audioTracks.length) {
          if ((_remoteAudioStream && _remoteAudioStream.streamId) === stream.mid) {
            if (_remoteAudioStream.stream !== stream) {
              _remoteAudioStream.stream = stream;
              updated = true;
            }
          } else {
            _remoteAudioStream = { streamId: stream.mid, stream: stream };
            updated = true;
          }
        }
      }
      return updated;
    }

    function putRemoteStream(stream) {
      if (!stream) {
        return false;
      }
      return putRemoteStreamUnified(stream);
    }

    function putRemoteStreams(streams, clearExisting) {
      if (!streams) {
        return;
      }
      if (!(streams instanceof Array)) {
        streams = [streams];
      }
      var updated = false;

      if (clearExisting && (_remoteAudioStream || _remoteVideoStreams.length)) {
        updated = true;
        _remoteAudioStream = null;
        _remoteVideoStreams = [];
      }

      streams.forEach(function (s) {
        _remoteStreams.push(s);
        if (putRemoteStream(s)) {
          updated = true;
        }
      });
      if (updated) {
        sendRemoteStreams();
      }
    }

    function deleteRemoteStream(stream) {
      if (!stream) {
        return;
      }
      var updated = false;
      var videoTrackId = _midMappingEnabled ? WebRTCAdapter.getVideoTrackId(stream) : WebRTCAdapter.getVideoStreamTrackId(stream);
      if (videoTrackId) {
        _remoteVideoStreams.some(function (v, index) {
          if (v.streamId === videoTrackId) {
            _remoteVideoStreams.splice(index, 1);
            updated = true;
            return true;
          }
          return false;
        });
      }
      var audioTrackId = _midMappingEnabled ? WebRTCAdapter.getAudioTrackId(stream) : WebRTCAdapter.getAudioStreamTrackId(stream);
      if (_remoteAudioStream && _remoteAudioStream.streamId === audioTrackId) {
        _remoteAudioStream = null;
        updated = true;
      }

      if (updated) {
        sendRemoteStreams();
      }
    }

    function enableAudioTrack(enable) {
      // Enable/disable both _localStreams and _oldLocalStreams to ensure they
      // are in synch in case media renegotiation fails.
      var hasAudioTracks = false;
      if (_localStreams[LOCAL_AUDIO_VIDEO]) {
        hasAudioTracks = WebRTCAdapter.toggleAudio(_localStreams[LOCAL_AUDIO_VIDEO], enable);
      }
      _isMuted = !hasAudioTracks || !enable;

      if (_oldLocalStreams && _oldLocalStreams[LOCAL_AUDIO_VIDEO]) {
        WebRTCAdapter.toggleAudio(_oldLocalStreams[LOCAL_AUDIO_VIDEO], enable);
      }
      return hasAudioTracks;
    }

    function startRenegotiation(localRequest, renegotiationCb) {
      if (_renegotiationInProgress) {
        var sdpStatus = _pcSettings.main.sdpStatus;
        logger.debug("[RtcSessionController]: There's a renegotiation in progress, fail or succeed it based on current SdpStatus:", sdpStatus);
        if (sdpStatus === SdpStatus.AnswerReceived || sdpStatus === SdpStatus.AnswerApplied || sdpStatus === SdpStatus.AnswerSent) {
          renegotiationSuccessful();
        } else {
          renegotiationFailed('Renegotiation already in progress');
        }
      }

      if (_pc && _pc.iceDisconnectTimeout) {
        logger.info('[RtcSessionController]: Ignore previous ICE disconnection');
        window.clearTimeout(_pc.iceDisconnectTimeout);
        delete _pc.iceDisconnectTimeout;
      }

      // Save the current RTCPeerConnection and MediaStream objects in case
      // the renegotiation fails.
      _renegotiationInProgress = true;
      _renegotiationStartedLocally = !!localRequest;
      _renegotiationCb = renegotiationCb || null;

      _oldPC = _pc;
      _oldCallStats = _callStats;
      clearIceProperties(_oldPC);
      unregisterPcEvtHandlers(_oldPC, _oldCallStats);
      if (_nextPc) {
        if (localRequest && _pc.localDescription) {
          logger.debug('[RtcSessionController]: Using pre-allocated peer connection');
          _remoteStreams = [];
          _pc = _nextPc;
          _nextPc = null;
          _callStats = createCallStatsHandler([_pc]);
          registerPcEvtHandlers(_pc, _callStats);
        } else {
          // The pre-allocated peer connection cannot be used, discard it
          closeNextPC();
          _pc = null;
        }
      } else {
        _pc = null;
      }
      _oldLocalStreams = _localStreams;
      _localStreams = [null, null];
      _oldMediaConstraints = Utils.shallowCopy(_mediaConstraints);
      _oldActiveMediaType = Utils.shallowCopy(_activeMediaType);
      _pendingRemoteSdp = null;
      _pendingChangeMedia = null;

      _pcSettings.main.sdpStatus = SdpStatus.None;
      _pcSettings.main.localOrigin = null;
    }

    function invokeRenegotiationCb(error) {
      if (!_renegotiationCb) {
        return;
      }
      try {
        if (error && error !== Constants.ReturnCode.CHOOSE_DESKTOP_MEDIA_CANCELLED && (typeof error !== 'string' || !error.startsWith('res_'))) {
          error = 'res_ChangeMediaFailed';
        }
        _renegotiationCb(error);
      } catch (e) {
        logger.error(e);
      }
      _renegotiationCb = null;
    }

    function renegotiationSuccessful() {
      logger.info('[RtcSessionController]: The media renegotiation was successful');

      // Collect stats and close the old RTCPeerConnection object
      var oldCallStats = _oldCallStats;
      _oldCallStats = null;
      var oldPC = _oldPC;
      _oldPC = null;
      stopStats(oldCallStats, true, !oldPC.connectionBypassed)
        .catch(function () {
          logger.warn('[RtcSessionController]: Failed to stop stats for old RTCPeerConnection');
        })
        .then(function () {
          logger.info('[RtcSessionController]: Closing the old RTCPeerConnection');
          closePC(oldPC, oldCallStats);
        });

      // Stop the old MediaStream object
      logger.info('[RtcSessionController]: Stopping the old MediaStream');

      // Update the holding flag (it should already be set correctly, but...)
      if (_holdInProgress) {
        _holding = true;
      } else if (_retrieveInProgress) {
        _holding = false;
      }

      stopStreams(_oldLocalStreams);
      _oldLocalStreams = null;
      _oldMediaConstraints = null;
      _oldActiveMediaType = null;

      // Update the local and remote streams
      setLocalVideoStreams(_localStreams);
      var remoteStreams = _pc.getRemoteStreams();
      putRemoteStreams(remoteStreams, true);
      enableAudioTrack(!_isMuted);

      _holdInProgress = false;
      _retrieveInProgress = false;
      _renegotiationInProgress = false;
      _pendingAbort = false;
      _isDirectUpgradingToConf = false;

      // Reset the _dtmfSender object so that it's reinitialized when the canSendDTMFDigits() is called
      _dtmfSender = null;
      // Send the onMediaUpdate event for the Call object
      sendMediaUpdate();
      // Send onNetworkQuality event to indicate that there's no quaility info available
      // until the new CallStatsHandler collects the stats
      sendEvent(_that.onNetworkQuality, { quality: null });

      invokeRenegotiationCb();
    }

    function renegotiationFailed(error) {
      logger.info('[RtcSessionController]: The media renegotiation has failed: ', error);

      // Close the new RTCPeerConnection object
      clearIceProperties(_pc);
      closePC(_pc, _callStats);
      _pcSettings.main.sdpStatus = SdpStatus.Connected;
      _pc = _oldPC;
      _callStats = _oldCallStats;
      registerPcEvtHandlers(_oldPC);
      _oldPC = null;
      _oldCallStats = null;

      // Stop the new MediaStream object
      stopStreams(_localStreams);
      _localStreams = _oldLocalStreams;
      _oldLocalStreams = null;

      // Update the local and remote streams
      setLocalVideoStreams(_localStreams);

      //Restore media constraints and active media type
      _mediaConstraints = Utils.shallowCopy(_oldMediaConstraints);
      _activeMediaType = Utils.shallowCopy(_oldActiveMediaType);
      _oldMediaConstraints = null;
      _oldActiveMediaType = null;

      // Restore the holding flag if applicable
      if (_holdInProgress) {
        // Hold request failed
        _holding = false;
      } else if (_retrieveInProgress) {
        // Retrieve request failed. Call is still held.
        _holding = true;
      }

      _holdInProgress = false;
      _retrieveInProgress = false;
      _renegotiationInProgress = false;
      if (_isDirectUpgradingToConf) {
        _isDirectCall = true;
        _isDirectUpgradingToConf = false;
      }

      if (_pendingAbort) {
        if (typeof _pendingAbort === 'function') {
          _pendingAbort();
        }
        _pendingAbort = false;
      }

      // Send the onMediaUpdate event for the Call object
      sendMediaUpdate();

      invokeRenegotiationCb(error || 'res_ChangeMediaFailed');
    }

    function handleConnectionFailed() {
      handleFailure('res_CallMediaFailed');
    }

    function handleFailure(err, errCb) {
      if (err === Constants.ReturnCode.CHOOSE_DESKTOP_MEDIA_CANCELLED) {
        // Special case, the user cancelled the Get User Media
        logger.info('[RtcSessionController]: User cancelled the screen share request');
      } else if (err === 'res_RestartBrowser') {
        logger.error('[RtcSessionController]: Failure: No ICE candidates collected. Browser needs to be restarted');
      } else {
        err = err || 'res_RTCError';
        logger.warn('[RtcSessionController]: There was an internal failure. Send error event: ', err);
      }

      if (_renegotiationInProgress) {
        renegotiationFailed(err);
      } else if (errCb) {
        errCb(err);
      } else {
        sendError(err);
      }
    }

    function processPendingSdp() {
      if (_pendingChangeMedia) {
        logger.info('[RtcSessionController]: Process the pending changeMedia');
        changeMedia(_pendingChangeMedia.mediaType, _pendingChangeMedia.cb, _pendingChangeMedia.options);
        _pendingChangeMedia = null;
      } else if (_pendingRemoteSdp) {
        logger.info('[RtcSessionController]: Process the pending remote description');
        _that.setRemoteDescription(_pendingRemoteSdp.sdp, _pendingRemoteSdp.cb);
        _pendingRemoteSdp = null;
      }
    }

    function setSdpStatus(newStatus, pc) {
      pc = pc || _pc;
      if (!pc) {
        logWarning('Could not set SDP status for non existing pc.');
        return;
      }

      var sdpStatus = getSetting(pc, 'sdpStatus');
      if ((sdpStatus === SdpStatus.PrAnswerReceived || sdpStatus === SdpStatus.PrAnswerApplied) && newStatus === SdpStatus.Connected) {
        logger.debug('[RtcSessionController]: Cannot change SDP status to ' + newStatus + ' without final SDP answer.');
        return;
      }
      if (sdpStatus !== newStatus) {
        logger.debug('[RtcSessionController]: Changed SDP status from ' + sdpStatus + ' to ' + newStatus);
        setSetting(pc, 'sdpStatus', newStatus);

        if (pc === _pc) {
          switch (newStatus) {
            case SdpStatus.AnswerApplied:
              processPendingSdp();
              break;
            case SdpStatus.Connected:
              pc.setMaxBitrate(_videoTrackConstraints);
              if (_renegotiationInProgress) {
                renegotiationSuccessful();
              }
              // Send the onSdpConnected event for the RtcHandler
              sendSdpConnected();
              processPendingSdp();
              _callStats.start(); // Start collecting stats
              break;
            case SdpStatus.AnswerSent:
              waitConnectedState(pc);
              break;
          }
        }
        // Chrome 73+ sets the iceConnectionState=connected too early and we don't process it. So check it now
        handleConnectionStateChange(pc);
      }
    }

    function updateLocalDescription(pc, rtcSdp) {
      if (!pc) {
        logWarning('Cannot update local description for non existing pc.');
        return null;
      }
      logger.debug('[RtcSessionController]: updateLocalDescription with mediaConstraints=', _mediaConstraints);

      var hasVideo = false;
      var parsedSdp = sdpParser.parse(rtcSdp.sdp);

      if (!_enableMultiplex) {
        // By default browsers add a=group to the SDP, so remove it if multiplex is not enabled
        sdpParser.disableMultiplex(parsedSdp);
      } else {
        logger.debug('[RtcSessionController]: Media multiplex enabled!');
      }

      if (_isTelephonyCall) {
        if ((pc.connectionBypassed || _ignoreNextConnection) && _isMobile) {
          // For mobile clients, set connection mode to inactive just for the setLocalDescription. After it's applied
          // it should be restored in sendSessionDescription()
          pc.restoreAudioConnectionMode = sdpParser.getConnectionMode(parsedSdp, { mediaType: 'audio' });
          sdpParser.setConnectionMode(parsedSdp, 'audio', 'inactive');
        }

        if (rtcSdp.type === 'offer') {
          // SBC doesn't support a:mid attribute, so we have to save it and
          // add it back when applying the answer description
          var audioMLine = parsedSdp.m[0];
          if (audioMLine && audioMLine.media === 'audio') {
            audioMLine.a.some(function (a) {
              if (a.field === 'mid') {
                _lastAudioOfferMidAttr = a.value;
                return true;
              }
              return false;
            });
          }
        }
      }

      if (!_holding) {
        if (_mediaConstraints.video || _mediaConstraints.desktop) {
          hasVideo = sdpParser.hasVideo(parsedSdp);
        } else if (rtcSdp.type === 'answer') {
          hasVideo = sdpParser.hasVideo(parsedSdp);
          if (hasVideo && _remoteVideoDisabled) {
            // Disable video
            sdpParser.removeVideo(parsedSdp);
            hasVideo = false;
          }
        }
      }

      if (hasVideo) {
        if (_mediaConstraints.video) {
          var xGoogle = getXGoogle(_mediaConstraints.hdVideo ? 'hdVideo' : 'video', _mediaConstraints.videoResolution);
          sdpParser.setXGoogle(parsedSdp, xGoogle);
          _callStats.setTransmitVideoParams({
            mediaConstraints: _mediaConstraints,
            trackConstraints: _videoTrackConstraints,
            bitRates: xGoogle
          });
        }
        if (_mediaConstraints.desktop) {
          // Add xgoogle parameters to the last m-line (which should be the screenshare m-line)
          sdpParser.setXGoogle(parsedSdp, getXGoogle(_mediaConstraints.hdDesktop ? 'hdScreenShare' : 'screenShare'), parsedSdp.m.length - 1);
        }
      }
      sdpParser.setOpusParameters(parsedSdp, RtcSessionController.sdpParameters.audio);

      if (RtcSessionController.sdpParameters.preferredVideoCodec !== DEFAULT_VIDEO_CODEC && rtcSdp.type === 'offer') {
        sdpParser.setPreferredVideoCodec(parsedSdp, RtcSessionController.sdpParameters.preferredVideoCodec);
      }

      if (RtcSessionController.disableAudioCN !== DEFAULT_DISABLE_AUDIO_CN) {
        // NGTC-8941: NGTC-OSV : Loud disturbing ringing noise instead of Comfort Noise.
        // Remove CN codec
        sdpParser.removeAudioCodecs(parsedSdp, ['CN']);
      }

      if (rtcSdp.type === 'offer') {
        // Remove H.264 codec from offer as it is not required and decoder instances caused
        // issues on some hardware when GPU hardware acceleration is enabled
        sdpParser.removeVideoCodecs(parsedSdp, ['H264']);
      }

      if (rtcSdp.type !== 'answer') {
        _activeMediaType = { audio: true, video: hasVideo };
      }

      var newSdp = new WebRTCAdapter.SessionDescription({
        type: rtcSdp.type,
        sdp: sdpParser.stringify(parsedSdp)
      });

      logger.debug('[RtcSessionController]: Finished modifying local SDP');
      return newSdp;
    }

    function getXGoogle(mediaType, resolution) {
      var xGoogle = Utils.shallowCopy(RtcSessionController.xGoogle[mediaType]);
      if (xGoogle && mediaType === 'hdVideo' && resolution) {
        var xGoogleRes = RtcSessionController.xGoogle.hdVideo[resolution];
        xGoogleRes && Object.assign(xGoogle, xGoogleRes);
      }
      return xGoogle;
    }

    function setVideoSdpParameters(pc, parsedSdp, desktopSdpMline) {
      var maxBw, mediaType;
      if (_mediaConstraints.desktop && !pc.desktopPeerConnection) {
        // This is a direct call with local screenshare active
        mediaType = _mediaConstraints.hdDesktop ? 'hdScreenShare' : 'screenShare';
      } else if (_mediaConstraints.hdVideo) {
        if (_mediaConstraints.videoResolution) {
          var hdVideo = RtcSessionController.sdpParameters.hdVideo[_mediaConstraints.videoResolution];
          maxBw = hdVideo && hdVideo.maxBw;
        }
        mediaType = 'hdVideo';
      } else {
        mediaType = 'video';
      }

      var isChromeWebRTC = _browser.chrome || _isMobile;
      if (isChromeWebRTC) {
        // https://bugs.chromium.org/p/webrtc/issues/detail?id=10641
        // https://bugs.chromium.org/p/chromium/issues/detail?id=1001080
        // Chrome requires some upper limit for it max bandwidth as it fail when availableOutgoingBitrate is
        // around 50 mbps. Please note that this setting has nothing to do with the sent video stream, which
        // is configured via maxBitrate in send parameters of transceiver.
        // For Firefox we can use the configure maxBw values per m-line, like done for PlanB.
        maxBw = MAX_BANDWIDTH;
      } else if (!maxBw) {
        maxBw = RtcSessionController.sdpParameters[mediaType].maxBw;
      }

      sdpParser.setVideoBandwidth(parsedSdp, maxBw, true);

      // Set bandwidth and xgoogle parameters to all video m-lines
      var videoXGoogle = getXGoogle(mediaType, _mediaConstraints.videoResolution);

      var desktopMlineIndex, desktopXGoogle;
      if (_pc.desktopPeerConnection) {
        var desktopMode = _mediaConstraints.hdDesktop ? 'hdScreenShare' : 'screenShare';
        maxBw = isChromeWebRTC ? MAX_BANDWIDTH : RtcSessionController.sdpParameters[desktopMode].maxBw;
        desktopXGoogle = RtcSessionController.xGoogle[desktopMode];

        // Set bandwidth and xgoogle parameters only on the screenshare m-line
        desktopMlineIndex = desktopSdpMline && desktopSdpMline.index;
        sdpParser.setVideoBandwidth(parsedSdp, maxBw, true, desktopMlineIndex);
        // Use the higher X-Google settings as common setting for all m-lines
        if ((desktopXGoogle.minBitRate || 0) > (videoXGoogle.minBitRate || 0)) {
          videoXGoogle = desktopXGoogle;
          logger.debug('[RtcSessionController]: Using X-Google parameters from screenshare: ', videoXGoogle);
        }
      }

      sdpParser.setXGoogle(parsedSdp, videoXGoogle);
    }

    function handleDisabledRemoteOfferMLines(pc, parsedSdp, sdpType, desktopSdpMline) {
      if (sdpType === 'offer' && pc === _pc) {
        var disabledRemoteMLines = getSetting(pc, 'disabledRemoteOfferMLines');
        disabledRemoteMLines.removeAll();
        if (_isDirectCall && !pc.desktopPeerConnection && desktopSdpMline) {
          // This client only supports 1 peerconnection, so replace the video m-line with screenshare m-line
          // Set video m-line as inactive and save it for later. We need to send it back in the answer SDP.
          var disabledVideoMline = parsedSdp.m[DIRECT_CALL_SDP_M_LINE_INDEX.video];
          disabledVideoMline.port = 0;
          sdpParser.setConnectionMode({ m: [disabledVideoMline] }, 'video', 'inactive');
          disabledRemoteMLines.add(DIRECT_CALL_SDP_M_LINE_INDEX.video, disabledVideoMline);
          parsedSdp.m[DIRECT_CALL_SDP_M_LINE_INDEX.video] = desktopSdpMline.sdp;
          logger.debug('[RtcSessionController]: Disabled and replaced video m-line with screenshare m-line');
        }
      }
    }

    function updateRemoteDescription(pc, parsedSdp, sdpType, desktopSdpMline) {
      if (!pc) {
        logWarning('Cannot update remote description for non existing pc.');
        return null;
      }
      logger.debug('[RtcSessionController]: updateRemoteDescription with mediaConstraints=', _mediaConstraints);

      handleDisabledRemoteOfferMLines(pc, parsedSdp, sdpType, desktopSdpMline);

      var allVideoRecvOnly = false;
      var hasVideo = sdpParser.hasVideo(parsedSdp);
      if (hasVideo) {
        var videoModes = sdpParser.getVideoConnectionModes(parsedSdp);
        logger.info('[RtcSessionController]: Remote description has video modes set to ', videoModes);
        allVideoRecvOnly = videoModes.every(function (v) {
          return v === 'recvonly';
        });
        hasVideo = !allVideoRecvOnly;
        setVideoSdpParameters(pc, parsedSdp, desktopSdpMline);
      }

      if (!hasVideo) {
        // sdpParser.hasVideo(parsedSdp) accepts only V8 or V9 codec
        // if it is not the case we want to disable it in case there is a another codec
        sdpParser.removeVideo(parsedSdp);
      }
      sdpParser.setOpusParameters(parsedSdp, RtcSessionController.sdpParameters.audio);

      var oldHeld = _held;
      if (sdpParser.isHold(parsedSdp)) {
        // If this is an SDP Offer, that means the other party is putting the call on hold.
        // If this is an SDP Answer AND we are not holding the call AND we offered audio,
        // that also means the other party is holding the call.
        _held = sdpType === 'offer' || (!_holding && _mediaConstraints.audio);
      } else {
        _held = false;
      }
      if (oldHeld !== _held) {
        if (_held) {
          logger.info('[RtcSessionController]: The client has been held');
        } else {
          logger.info('[RtcSessionController]: The client has been retrieved');
        }
      }

      if (_isTelephonyCall) {
        var audioMLine = parsedSdp.m[0];
        if (audioMLine && audioMLine.media === 'audio') {
          var hasMid = audioMLine.a.some(function (a) {
            return a.field === 'mid';
          });
          if (!hasMid) {
            // SBC doesn't support a=mid attribute and it's required by Unified Plan SDP so we need to add it here.
            // Remember a previous a=mid from a local offer or default it to 'audio'.
            _lastAudioOfferMidAttr = _lastAudioOfferMidAttr || 'audio';
            logger.debug('[RtcSessionController]: Adding a=mid:' + _lastAudioOfferMidAttr + " to SBC's answer SDP");
            audioMLine.a.push({ field: 'mid', value: _lastAudioOfferMidAttr });
          }
        }
      }

      // Check if the remote party is adding or removing video
      var sdpStatus = getSetting(pc, 'sdpStatus');
      if (sdpType === 'offer' && sdpStatus === SdpStatus.Connected && !_holding && !_held) {
        if (_remoteVideoStreams.length && !hasVideo) {
          sendRemoteVideoRemoved();
        } else if (!_remoteVideoStreams.length && hasVideo) {
          sendRemoteVideoAdded();
        }
      }

      if (!_holding) {
        if (sdpType !== 'answer' || !allVideoRecvOnly) {
          _activeMediaType = { audio: true, video: hasVideo };
        }
      }

      // Enable multiplex if the incoming direct call has it enabled
      _enableMultiplex = _isDirectCall && sdpType === 'offer' && sdpParser.isMultiplexEnabled(parsedSdp);

      return parsedSdp;
    }

    function getCandidatesTimeout() {
      var timeout = RtcSessionController.candidatesTimeout || DEFAULT_CANDIDATES_TIMEOUT;
      if (_numberOfExtraVideoChannels > 0) {
        timeout += timeout * (WebRTCAdapter.iceTimeoutSafetyFactor || 0);
      }
      logger.debug('[RtcSessionController]: getCandidatesTimeout:', timeout);
      return timeout;
    }

    function getTrickleIceTimeout() {
      var timeout = RtcSessionController.trickleIceTimeout || DEFAULT_TRICKLE_ICE_TIMEOUT;
      logger.debug('[RtcSessionController]: getTrickleIceTimeout:', timeout);
      return timeout;
    }

    function localSdpSetAndSend(pc, sdpType) {
      if (!pc) {
        logWarning('Terminating localSdpSetAndSend for non existing pc.');
        return;
      }

      var timeout;
      logDebug('Local SDP set for the audio/video connection.');

      if (_renegotiationInProgress && _pendingAbort) {
        renegotiationFailed('Renegotiation aborted');
        return;
      }

      var trickleIce, sdpStatus, sdpStatusToSend;
      if (sdpType === 'offer') {
        trickleIce = getSetting(pc, 'trickleIceForOffer');
        sdpStatus = SdpStatus.OfferPending;
        sdpStatusToSend = SdpStatus.OfferSent;
      } else {
        trickleIce = getSetting(pc, 'trickleIceForAnswer');
        sdpStatus = SdpStatus.AnswerPending;
        sdpStatusToSend = SdpStatus.AnswerSent;
      }

      setSdpStatus(sdpStatus, pc);
      setSetting(pc, 'localSdpType', sdpType);

      if (trickleIce) {
        timeout = getTrickleIceTimeout();

        if (timeout > 0) {
          logDebug('Trickle ICE is enabled. Start a short timer before sending the audio/video SDP (' + timeout + 'ms).');

          window.setTimeout(function () {
            if (getSetting(pc, 'sdpStatus') === sdpStatus) {
              logDebug('Trickle ICE timer expired. Send the audio/video SDP');
              sendSessionDescription(pc, sdpStatusToSend);
            }
          }, timeout);
        } else {
          logDebug('Trickle ICE is enabled. Send the audio/video SDP');
          sendSessionDescription(pc, sdpStatusToSend);
        }
      } else {
        timeout = getCandidatesTimeout();
        var sdpPreset = getSetting(pc, 'nextPcLocalSdpSetAt');
        if (sdpPreset) {
          // This is a pre-allocated PC, check if all candidates have been collected.
          // If not, check how much time we still have to wait.
          setSetting(pc, 'nextPcLocalSdpSetAt', 0);
          if (pc.endOfCandidates) {
            logDebug('Already collected all ICE candidates for pre-allocated PC. Send the audio/video SDP');
            sendSessionDescription(pc, sdpStatusToSend);
            return;
          }
          var elapsed = Date.now() - sdpPreset;
          timeout = Math.max(timeout - elapsed, 0);
        }
        logDebug('Waiting for audio/video ICE candidates (' + timeout + 'ms).');

        var candidatesTimer = window.setTimeout(function () {
          logDebug('Timed out waiting for audio/video candidates.');
          if (!isActivePc(pc)) {
            logWarning('RTCPeerConnection instance for audio/video connection no longer exists.');
            return;
          }
          clearShortCandidatesTimer(pc);
          setSetting(pc, 'candidatesTimer', null);
          if (getSetting(pc, 'sdpStatus') === sdpStatus) {
            logDebug('[RtcSessionController]: Send the audio/video SDP');
            sendSessionDescription(pc, sdpStatusToSend);
          }
        }, timeout);
        setSetting(pc, 'candidatesTimer', candidatesTimer);
      }
    }

    function setLocalAndSendMessage(audioVideoSdp) {
      logger.debug('[RtcSessionController]: Successfully created local audio/video description:', audioVideoSdp);

      clearCandidatesTimer(_pc);

      if (_renegotiationInProgress && _pendingAbort) {
        renegotiationFailed('Renegotiation aborted');
        return;
      }

      var onLocalSdpSet = function (pc, err) {
        if (err) {
          logger.error('[RtcSessionController]: Failed to apply Local ' + pc + ' description:', err.message || err);
          handleFailure();
        } else {
          logger.debug('[RtcSessionController]: Local description was successfully applied. PC=', pc);
        }
        if (_pc && _ignoreNextConnection) {
          sendSessionDescription(_pc, SdpStatus.Connected);
          if (_pc) {
            _pc.connectionBypassed = true;
            unregisterPcEvtHandlers(_pc, _callStats);
          }
          return;
        }

        if (!_renegotiationInProgress) {
          sendMediaUpdate();
        }

        var sdpType = audioVideoSdp && audioVideoSdp.type;
        localSdpSetAndSend(_pc, sdpType);
      };
      try {
        if (_pc && audioVideoSdp) {
          var mainPcCb = onLocalSdpSet.bind(null, 'audio/video');
          audioVideoSdp = updateLocalDescription(_pc, audioVideoSdp);
          _pc.setLocalDescription(audioVideoSdp, mainPcCb, mainPcCb);
          _pc.startTime = Date.now();
        }
      } catch (e) {
        onLocalSdpSet('unknown', e || 'error');
      }
    }

    function onAnswerSdp() {
      if (_holdInProgress || (_holding && !_retrieveInProgress)) {
        _pc.oniceconnectionstatechange = null;
        logger.debug('[RtcSessionController]: Call is already held or is being put on hold. Stop monitoring iceConnectionState changes');
        setSdpStatus(SdpStatus.Connected, _pc);
      } else if (getSetting(_pc, 'sdpStatus') === SdpStatus.AnswerReceived) {
        setSdpStatus(SdpStatus.AnswerApplied, _pc);
      } else if (getSetting(_pc, 'sdpStatus') === SdpStatus.PrAnswerReceived) {
        setSdpStatus(SdpStatus.PrAnswerApplied, _pc);
      }
    }

    function getOfferToReceiveVideo() {
      return (_supportsVideoReceiverConfiguration || !_remoteVideoDisabled) && !_isTelephonyCall;
    }

    function updateTrickleIceAndStatus(pc, type, parsedSdp) {
      if (!pc) {
        logWarning('Terminating updateTrickleIceAndStatus for non existing pc.');
        return;
      }

      switch (type) {
        case 'offer':
          var trickleIce = !RtcSessionController.disableTrickleIce && isTrickleIceIndicated(type, parsedSdp);
          setSetting(pc, 'trickleIceForAnswer', trickleIce);
          if (trickleIce) {
            logger.info('[RtcSessionController]: Enabled Trickle-ICE for answer.');
          } else {
            logger.info('[RtcSessionController]: Disabled Trickle-ICE for answer.');
          }
          break;
        case 'pranswer':
        case 'answer':
          setSdpStatus(type === 'pranswer' ? SdpStatus.PrAnswerReceived : SdpStatus.AnswerReceived, pc);
          if (getSetting(pc, 'trickleIceForOffer')) {
            // We may need to disable trickle ICE for Offer if the peer doesn't support it.
            // This should never happen when connecting to other UnifyPhone clients.
            if (!isTrickleIceIndicated(type, parsedSdp)) {
              setSetting(pc, 'trickleIceForOffer', false);
              logger.warn('[RtcSessionController]: Disabled Trickle-ICE for offer.');
            }
          }
          break;
      }
    }

    function setRemoteDescription(rtcSdp, sendingEarlyMedia) {
      logger.debug('[RtcSessionController]: setRemoteDescription()');

      try {
        // Make sure that we already have an RTCPeerConnection object
        if (!createPeerConnection()) {
          handleFailure();
          return;
        }

        var localStreams = _pc.getLocalStreams();

        var parsedSdp = sdpParser.parse(rtcSdp.sdp);

        // Screenshare m-line (if available)
        var desktopSdpMline, desktopRemoteSsrc;
        if (rtcSdp.screen) {
          // Try to find the screenshare m-line indicated by the server
          var index = sdpParser.findMediaLineIndex(parsedSdp, rtcSdp.screen);
          if (index !== -1) {
            // Found it! Retrieve it and remove it from the 'main' sdp
            desktopSdpMline = {
              sdp: parsedSdp.m[index],
              index: index,
              mid: rtcSdp.screen
            };
            desktopRemoteSsrc = sdpParser.getSsrcList(desktopSdpMline.sdp);
          }
        }

        if (sendingEarlyMedia && rtcSdp.type === 'offer') {
          // Disable trickle ICE for early media
          logger.debug('[RtcSessionController]: Disable Trickle-ICE for early media');
          sdpParser.fixTrickleIceOption(parsedSdp, false);
        }

        updateTrickleIceAndStatus(_pc, rtcSdp.type, parsedSdp);

        var onRemoteSdpSet = function (pc, err) {
          if (err) {
            logger.error('[RtcSessionController]: Failed to apply Remote ' + pc + ' description:', err.message || err);
            handleFailure();
          } else {
            logger.debug('[RtcSessionController]: Remote description was successfully applied. PC=', pc);
          }

          logger.debug('[RtcSessionController]: Remote Descriptions were successfully applied.');
          if (!_renegotiationInProgress) {
            sendMediaUpdate();
          }

          /////////////////////////////////////////////////////////////////////////////
          // No RTP packets should be sent if:
          //   1. The client is holding the call
          //   2. SDP offer is inactive or sendonly (i.e. the client is being held)
          //
          // The browser will not send RTP packets if the RTCPeerConnection instance
          // does not have a local stream.
          /////////////////////////////////////////////////////////////////////////////

          switch (rtcSdp.type) {
            case 'offer':
              if (_holding || _held) {
                if (localStreams.length > 0) {
                  removeLocalStreams();
                  logger.debug('[RtcSessionController]: Removed Local Stream (hold)');
                } else {
                  logger.debug('[RtcSessionController]: Local Stream has not been set (hold)');
                }
              } else {
                addLocalStreams(_pc);
              }

              logger.debug('[RtcSessionController]: Creating Local Description Answer...');

              var sdpConstraints = {
                mandatory: {
                  OfferToReceiveAudio: true,
                  OfferToReceiveVideo: getOfferToReceiveVideo()
                }
              };

              _pc.createAnswer(
                function (mainSdp) {
                  setLocalAndSendMessage(mainSdp);
                },
                function (error) {
                  error = error || 'Unspecified';
                  logger.error('[RtcSessionController]: Failed to create Local Description: ', error.message || error);
                  handleFailure();
                },
                sdpConstraints,
                { audioInactive: _holding }
              );
              break;

            case 'pranswer':
              // Early Media applied
              logger.debug('[RtcSessionController]: Received pranswer, applying early media SDP');
              onAnswerSdp();
              waitConnectedState(_pc);
              // SDP status has already been set to SdpStatus.PrAnswerReceived
              break;

            case 'answer':
              onAnswerSdp();
              waitConnectedState(_pc);
              break;

            default:
              logger.error('[RtcSessionController]: Invalid SDP Type = ', rtcSdp.type);
              handleFailure();
              break;
          }
        };

        if (_pc) {
          parsedSdp = updateRemoteDescription(_pc, parsedSdp, rtcSdp.type, desktopSdpMline);
          var updatedMainSdp = new WebRTCAdapter.SessionDescription({
            type: rtcSdp.type,
            sdp: sdpParser.stringify(parsedSdp)
          });
          updatedMainSdp.screen = desktopSdpMline && desktopSdpMline.sdp;
          logger.debug('[RtcSessionController]: Modified remote audio/video SDP:', updatedMainSdp);

          var mainPcCb = onRemoteSdpSet.bind(null, 'audio/video');
          _pc.setRemoteDescription(updatedMainSdp, mainPcCb, mainPcCb);
          _pc.desktopRemoteSsrc = desktopRemoteSsrc;
          _pc.remoteOrigin = sdpParser.getOrigin(parsedSdp).trim();
          _pc.startTime = Date.now();
        }
      } catch (e) {
        logger.error(e);
        handleFailure();
      }
    }

    function discardPreAllocatedPc() {
      setSetting(_pc, 'nextPcLocalSdpSetAt', 0);
      closePC(_pc);
      _pc = null;
    }

    function renegotiateMedia() {
      logger.debug('[RtcSessionController]: renegotiateMedia()');

      if (_renegotiationInProgress && _pendingAbort) {
        renegotiationFailed('Renegotiation aborted');
        return;
      }

      // Make sure that we already have an RTCPeerConnection object
      if (!createPeerConnection()) {
        handleFailure();
        return;
      }

      // Do not add the local stream to the RTCPeerConnection instance if the
      // client is holding the call. No RTP packets are sent in this case.
      if (!_holding) {
        addLocalStreams(_pc);
      } else {
        logger.debug('[RtcSessionController]: Local Stream has not been set (holding)');
      }

      logger.debug('[RtcSessionController]: Creating Local Descriptions...');

      var offerConstraints = {
        mandatory: {
          OfferToReceiveAudio: true,
          OfferToReceiveVideo: getOfferToReceiveVideo()
        }
      };

      try {
        _enableMultiplex = false;

        var localOffer = _pc.localDescription;
        if (localOffer) {
          // Pre-allocated peer connection
          if (_holding) {
            // We need to update the Transceiver objects in the peer connection
            // so discard it and start over
            discardPreAllocatedPc();
            renegotiateMedia();
            return;
          } else {
            localSdpSetAndSend(_pc, localOffer.type);
          }
        } else {
          _pc.createOffer(
            function (mainSdp) {
              setLocalAndSendMessage(mainSdp);
            },
            function (error) {
              error = error || 'Unspecified';
              logger.error('[RtcSessionController]: Failed to create audio/video Local Description: ', error.message || error);
              handleFailure();
            },
            offerConstraints,
            { audioInactive: _holding }
          );
        }
      } catch (e) {
        logger.error(e);
        handleFailure();
      }
    }

    function compareMediaConstraints(c1, c2) {
      if (!c1 || !c2) {
        return false;
      }
      return Object.keys(c1).every(function (key) {
        return c1[key] === c2[key];
      });
    }

    function changeMedia(mediaType, cb, changeOptions) {
      if (_pc && getSetting(_pc, 'sdpStatus') === SdpStatus.AnswerReceived) {
        logger.warn('[RtcSessionController]: Still processing the previous answer remote description. Queue the changeMedia');
        _pendingChangeMedia = { mediaType: mediaType, cb: cb, options: changeOptions };
        return;
      }
      if (!isConnStable()) {
        sendAsyncCallback(cb, 'Connection not stable');
        return;
      }
      changeOptions = changeOptions || {};

      if (changeOptions.isDirectUpgradingToConf) {
        _isDirectCall = false;
        _isDirectUpgradingToConf = true;
      }

      var newConstraints = Utils.shallowCopy(_mediaConstraints);
      if (typeof mediaType.audio === 'boolean') {
        newConstraints.audio = mediaType.audio;
      }
      if (typeof mediaType.video === 'boolean') {
        newConstraints.video = mediaType.video;
      }
      if (typeof mediaType.hdVideo === 'boolean') {
        newConstraints.hdVideo = mediaType.hdVideo;
      }
      if (mediaType.videoResolution) {
        newConstraints.videoResolution = mediaType.videoResolution;
      }
      if (typeof mediaType.desktop === 'boolean') {
        newConstraints.desktop = mediaType.desktop;
      }
      if (typeof mediaType.hdDesktop === 'boolean') {
        newConstraints.hdDesktop = mediaType.hdDesktop;
      }
      if (mediaType.minFrameRate) {
        newConstraints.minFrameRate = mediaType.minFrameRate;
      }
      newConstraints = normalizeMediaType(newConstraints);
      if (_nextPc && (_dontReuseAudioStream || !compareMediaConstraints(_mediaConstraints, newConstraints))) {
        logger.info('[RtcSessionController]: Media constraints are changing. Discard pre-allocated connection.');
        closeNextPC();
      }

      startRenegotiation(true, cb);
      _mediaConstraints = newConstraints;

      if (_pc) {
        // This is the pre-allocated connection (i.e. _nextPc), so it already contains the local streams in it
        _localStreams = _oldLocalStreams;
        renegotiateMedia();
      } else {
        if (changeOptions.videoOptions && changeOptions.videoOptions.mediaStream) {
          addNewVideoStream(changeOptions.videoOptions.mediaStream, newConstraints);
        }
        getUserMedia(renegotiateMedia, handleFailure);
      }
    }

    function addNewVideoStream(newVideoStream, mediaType) {
      if (_oldLocalStreams[LOCAL_AUDIO_VIDEO]) {
        if (mediaType.audio) {
          var audioTracks = newVideoStream.getAudioTracks();
          if (!audioTracks.length) {
            // The new video stream doesn't have audio track(s) so grab them from the old stream
            var oldAudioTrack = _oldLocalStreams[LOCAL_AUDIO_VIDEO].getAudioTracks()[0];
            if (oldAudioTrack) {
              newVideoStream.addTrack(oldAudioTrack.clone());
            }
          }
        }
        // Setting the LOCAL_AUDIO_VIDEO stream here will make the getUserMedia use it instead of
        // getting new stream from the mic/camera
        _that.setLocalStream(LOCAL_AUDIO_VIDEO, newVideoStream);
      }
    }

    function closePeerConnections() {
      if (_oldPC) {
        logger.debug('[RtcSessionController]: Closing the old audio/video RTCPeerConnection');
        closePC(_oldPC, _oldCallStats);
        _oldPC = null;
      }

      if (_pc) {
        logger.debug('[RtcSessionController]: Closing the RTCPeerConnection');
        closePC(_pc, _callStats);
        _pc = null;
      }

      closeNextPC();
    }

    function close() {
      logger.debug('[RtcSessionController]: Closing the RTC session controller');
      _activeMediaType = { audio: false, video: false };

      if (_pc) {
        setSdpStatus(SdpStatus.None, _pc);
        clearCandidatesTimer(_pc);
        clearConnectedTimer(_pc);
        clearIceProperties(_pc);
      }

      // Stop call stats collection. If there is an Old PC then we should send the stats for
      // the Old PC since the new PC wouldn't be connected in this case.
      var callStats = _oldCallStats || _callStats;
      if (callStats) {
        var statsPc = _oldPC || _pc;
        stopStats(callStats, false, !!statsPc && !statsPc.connectionBypassed)
          .catch(function () {
            logger.warn('[RtcSessionController]: Failed to stop stats collection');
          })
          .then(function () {
            logger.info('[RtcSessionController]: Closing all RTCPeerConnection instances');
            closePeerConnections();
          });
      } else {
        closePeerConnections();
      }

      if (_oldLocalStreams) {
        logger.debug('[RtcSessionController]: Stopping the old MediaStream');
        stopStreams(_oldLocalStreams);
        _oldLocalStreams = null;
      }

      _pcSettings.main.localOrigin = null;

      _renegotiationInProgress = false;
      _pendingAbort = false;
      _holdInProgress = false;
      _retrieveInProgress = false;
      _holding = false;
      _held = false;
      _isMuted = false;
      _dtmfSender = null;
      _screenShareParams = null;

      setLocalStream(null, LOCAL_AUDIO_VIDEO);
      setLocalStream(null, LOCAL_SCREEN_SHARE);
      clearRemoteStreams();
      sendClosed();
      _sessionTerminated = true;

      // Unregister all event handlers, except onQosAvailable, which will be done later
      unregisterEventHandlers(['onQosAvailable']);
    }

    function enableDTMFSender() {
      if (!_pc) {
        logger.error('[RtcSessionController]: PeerConnection not setup');
        return false;
      }

      if (_dtmfSender) {
        logger.debug('[RtcSessionController]: DTMF Sender already enabled');
        return true;
      }

      var localStreams = _pc.getLocalStreams();
      if (localStreams.length > 0) {
        // [Pastro, Rodrigo] In our client we will always have a single stream, at least in the near future.
        var audioTracks = localStreams[LOCAL_AUDIO_VIDEO].getAudioTracks();
        if (audioTracks.length > 0) {
          _dtmfSender = _pc.createDTMFSender(audioTracks[0]);
          if (_dtmfSender) {
            _dtmfSender.ontonechange = handleDTMFToneChange.bind(null, _pc);
            logger.info('[RtcSessionController]: Created DTMF Sender using remote audio track: ' + audioTracks[0].label);
          } else {
            return false;
          }
        } else {
          logger.error('[RtcSessionController]: No Audio Track to create DTMF Sender');
          return false;
        }
      } else {
        logger.error('[RtcSessionController]: No Local Stream to create DTMF Sender');
        return false;
      }

      return true;
    }

    function startConn(remoteSdp, sendingEarlyMedia) {
      // Successfully got access to media input devices
      logger.debug('[RtcSessionController]: startConn()');
      if (remoteSdp) {
        _disableConnectedTimer = sendingEarlyMedia;
        setRemoteDescription(remoteSdp, sendingEarlyMedia);
      } else {
        addLocalStreams(_pc);

        logger.debug('[RtcSessionController]: Creating Local Description...');

        var offerConstraints = {
          mandatory: {
            OfferToReceiveAudio: true,
            OfferToReceiveVideo: getOfferToReceiveVideo()
          }
        };

        _enableMultiplex = false;

        _pc.createOffer(
          function (mainSdp) {
            setLocalAndSendMessage(mainSdp);
          },
          function (error) {
            error = error || 'Unspecified';
            logger.error('[RtcSessionController]: Failed to create Local Description: ', error.message || error);
            handleFailure();
          },
          offerConstraints,
          { audioInactive: _holding }
        );
      }
    }

    function isConnStable(pc) {
      pc = pc || _pc;
      if (!pc) {
        if (_renegotiationInProgress) {
          logger.warn('[RtcSessionController]: isConnStable: There is a pending media renegotiation');
        } else {
          logger.debug('[RtcSessionController]: isConnStable: There is no connection');
        }
        return false;
      }

      var sdpStatus = getSetting(pc, 'sdpStatus');
      if (sdpStatus === SdpStatus.AnswerReceived || sdpStatus === SdpStatus.AnswerApplied) {
        logger.debug('[RtcSessionController]: isConnStable: Processing remote answer SDP, the connection is considered stable');
        return true;
      }
      if (sdpStatus === SdpStatus.AnswerSent) {
        logger.debug('[RtcSessionController]: isConnStable: Answer SDP sent, the connection is considered stable');
        return true;
      }
      if (_renegotiationInProgress) {
        logger.warn('[RtcSessionController]: isConnStable: There is a pending media renegotiation');
        return false;
      }
      if (pc.signalingState !== 'stable' && !pc.connectionBypassed) {
        logger.warn('[RtcSessionController]: isConnStable: The connection is not established. signalingState =', pc.signalingState);
        if (!_isMockedCall) {
          return false;
        }
      }
      return true;
    }

    function sendAsyncCallback(cb, error) {
      if (typeof cb === 'function') {
        window.setTimeout(function () {
          cb(error);
        }, 0);
      }
    }

    function setExtraVideoChannels() {
      if (_isDirectCall) {
        logger.debug('[RtcSessionController]: Set number of extra video channels for direct calls to 0');
        _numberOfExtraVideoChannels = 0;
      } else if (_isMobile) {
        logger.debug('[RtcSessionController]: Set number of extra video channels for mobile clients to 0');
        _numberOfExtraVideoChannels = 0;
      } else if (!_supportsVideoReceiverConfiguration && (_remoteVideoDisabled || _remoteVideoScreenOnlyAllowed)) {
        logger.debug('[RtcSessionController]: Remote video is disabled. Set number of extra video channels to 0.');
        _numberOfExtraVideoChannels = 0;
      } else {
        if (typeof RtcSessionController.maxVideoExtraChannels === 'number') {
          _numberOfExtraVideoChannels = Math.max(RtcSessionController.maxVideoExtraChannels, 0);
          _numberOfExtraVideoChannels = Math.min(_numberOfExtraVideoChannels, MAX_VIDEO_EXTRA_CHANNELS);
        } else {
          _numberOfExtraVideoChannels = DEFAULT_VIDEO_EXTRA_CHANNELS;
        }
        logger.info('[RtcSessionController]: Set number of extra video channels to ', _numberOfExtraVideoChannels);
      }
    }

    function getVideoStreamId(streamIndex) {
      var result;
      var stream = _localStreams[streamIndex];
      if (stream && stream.getVideoTracks().length) {
        result = WebRTCAdapter.getVideoStreamTrackId(stream);
      }
      return result;
    }

    function getVideoMid(streamIndex) {
      var result;
      var stream = _localStreams[streamIndex];
      if (stream) {
        var track = stream.getVideoTracks()[0];
        if (track) {
          result = _pc && _pc.getMediaId(track);
        }
      }
      return result;
    }

    // Statistics are required if the main connection has already been established
    function isQosRequired(isRenegotiation) {
      var pc = _oldPC || _pc;
      var sdpStatus = getSetting(pc, 'sdpStatus');

      return (
        isRenegotiation || // Make sure that we have offer/answer exchanged before generating QoS reports
        (!!pc &&
          (sdpStatus === SdpStatus.AnswerReceived ||
            sdpStatus === SdpStatus.PrAnswerReceived ||
            sdpStatus === SdpStatus.AnswerApplied ||
            sdpStatus === SdpStatus.AnswerSent ||
            sdpStatus === SdpStatus.Connected ||
            sdpStatus === SdpStatus.None))
      );
    }

    function stopStats(callStats, isRenegotiation, publishQos) {
      if (callStats) {
        var qosRequired = isQosRequired(isRenegotiation);

        return callStats.stop().then(function (qos) {
          if (publishQos && qosRequired && qos) {
            // By the time the qos available, the _renegotiationInProgress flag is already cleared,
            // that's why we need to pass this isRenegotiation parameter around
            sendQosAvailable(qos, isRenegotiation, callStats.getLastSavedStats(), callStats.getStreamQualityData());
          }
          if (!isRenegotiation) {
            _that.onQosAvailable = null;
          }
        });
      } else if (!isRenegotiation) {
        _that.onQosAvailable = null;
      }
      return Promise.resolve();
    }

    function createNextPeerConnection() {
      if (_nextPc) {
        return;
      }
      logger.debug('[RtcSessionController]: Pre-allocate the next peer connection');
      if (createPeerConnection(true)) {
        var createdPc = _nextPc;

        createdPc.onicecandidate = function (event) {
          if (createdPc !== _nextPc) {
            return;
          }
          if (event.candidate) {
            if (!event.candidate.candidate) {
              return;
            }
            var candidate = new IceCandidate(event.candidate.candidate);
            if (candidate.transport !== 'udp') {
              return;
            }
            logger.debug('[RtcSessionController]:[Pre-allocated PC]: New ICE candidate: ', event.candidate);
            if (!createdPc.hasRelayCandidates && candidate.typ === 'relay') {
              createdPc.hasRelayCandidates = true;
              // Adjust the time, so we would wait at most SHORT_CANDIDATES_TIMEOUT for the remaining candidates collection
              setSetting(_pc, 'nextPcLocalSdpSetAt', Date.now() - getCandidatesTimeout() + SHORT_CANDIDATES_TIMEOUT);
            }
          } else {
            logger.debug('[RtcSessionController]:[Pre-allocated PC]: End of ICE candidates');
            createdPc.endOfCandidates = true;
          }
        };

        if (addLocalStreams(createdPc)) {
          var offerConstraints = {
            mandatory: {
              OfferToReceiveAudio: true,
              OfferToReceiveVideo: getOfferToReceiveVideo()
            }
          };
          createdPc.createOffer(
            function (offerSdp) {
              logger.debug('[RtcSessionController]:[Pre-allocated PC]: Local description was successfully created');
              offerSdp = updateLocalDescription(createdPc, offerSdp);
              createdPc.setLocalDescription(
                offerSdp,
                function () {
                  // Save when the local SDP was set, so we know how long we need to wait for the candidates collection
                  setSetting(_pc, 'nextPcLocalSdpSetAt', Date.now());
                  logger.debug('[RtcSessionController]:[Pre-allocated PC]: Local description was successfully applied');
                },
                function (error) {
                  logger.warn('[RtcSessionController]:[Pre-allocated PC]: Error setting local description: ', error);
                  closeNextPC();
                }
              );
              logger.debug('[RtcSessionController]:[Pre-allocated PC]: Created local offer');
            },
            function (error) {
              logger.warn('[RtcSessionController]:[Pre-allocated PC]: Error creating local offer: ', error);
              closeNextPC();
            },
            offerConstraints
          );

          window.setTimeout(function () {
            if (createdPc === _nextPc) {
              // This peer connection has not been used for 30s, discard it
              closeNextPC();
            }
          }, NEXT_PC_TIMEOUT);
        } else {
          logger.warn('[RtcSessionController]:[Pre-allocated PC]: Local streams could not be added. Closing peer connection.');
          closeNextPC();
        }
      } else {
        logger.warn('[RtcSessionController]:[Pre-allocated PC]: Peer connection could not be created.');
      }
    }

    function isSimulcastEnabled() {
      return !_isDirectCall && RtcSessionController.addAdditionalLowVideoStream;
    }

    /////////////////////////////////////////////////////////////////////////////
    // Event Handlers
    /////////////////////////////////////////////////////////////////////////////
    var _eventList = [
      'onIceCandidate',
      'onSessionDescription',
      'onSdpConnected',
      'onIceConnected',
      'onIceDisconnected',
      'onRemoteVideoAdded',
      'onRemoteVideoRemoved',
      'onRemoteStreamUpdated',
      'onDTMFToneChange',
      'onClosed',
      'onRtcError',
      'onRtcWarning',
      'onLocalStreamEnded',
      'onScreenSharePointerStatus',
      'onMediaUpdate',
      'onLocalVideoStream',
      'onRemoteStreams',
      'onGetUserMediaException',
      // CallStats events
      'onQosAvailable',
      'onStatsNoOutgoingPackets',
      'onStatsThresholdExceeded',
      'onNetworkQuality'
    ];

    // Initialize event handlers
    _eventList.forEach(function (eventName) {
      _that[eventName] = null;
    });

    function unregisterEventHandlers(exceptionList) {
      exceptionList = exceptionList || [];
      _eventList.forEach(function (eventName) {
        if (!exceptionList.includes(eventName)) {
          _that[eventName] = null;
        }
      });
    }

    /////////////////////////////////////////////////////////////////////////////
    // Public interfaces
    /////////////////////////////////////////////////////////////////////////////

    Object.defineProperty(this, 'dontReuseAudioStream', {
      get: function () {
        return _dontReuseAudioStream;
      },
      set: function (value) {
        logger.debug('[RtcSessionController]: Setting dontReuseAudioStream to:', !!value);
        _dontReuseAudioStream = !!value;
      },
      enumerable: true,
      configurable: false
    });

    this.setTurnCredentials = function (credentials) {
      _turnCredentials = credentials || {};
    };

    this.setTurnUris = function (uris) {
      if (!uris || !uris.length) {
        return;
      }

      if (RtcSessionController.customTurnServers.length) {
        // Add the custom TURN servers first
        logger.debug('[RtcSessionController]: Adding custom TURN servers to this session:', RtcSessionController.customTurnServers);
        _turnUris = RtcSessionController.customTurnServers.concat(uris);
      } else {
        _turnUris = uris;
      }
      // WORKAROUND!!!
      // Firefox suppors TURN TLS in version 53 and above, but throws exception for URLs with 'turns' scheme
      // and IP address instead of hostname. To avoid this just remove them from list.
      // See https://bugzilla.mozilla.org/show_bug.cgi?id=1056934
      if (_browser.firefox) {
        _turnUris = _turnUris.filter(function (uri) {
          return !/turns:\d{1,3}(\.\d{1,3}){3}/.test(uri);
        });
      }
    };

    this.getLocalVideoStream = function () {
      // The local desktop (screen share) stream has precedence over the local video stream
      return _localDesktopStream || _localVideoStream;
    };

    this.getLocalDesktopStream = function () {
      return _localDesktopStream;
    };

    this.getRemoteAudioStream = function () {
      return _remoteAudioStream;
    };

    this.getRemoteVideoStreams = function () {
      return _remoteVideoStreams;
    };

    this.getSdpStatus = function () {
      return _pcSettings.main.sdpStatus;
    };

    // This is the SDP that has been passed as input to the warmup function
    this.getWarmedUpSdp = function () {
      return _warmedUpSdp;
    };

    this.isHolding = function () {
      return (_holding && !_holdInProgress) || _retrieveInProgress;
    };

    this.isHoldInProgress = function () {
      return _holdInProgress;
    };

    this.isRetrieveInProgress = function () {
      return _retrieveInProgress;
    };

    this.isHeld = function () {
      return _held;
    };

    this.getActiveMediaType = function () {
      return Utils.shallowCopy(_activeMediaType);
    };

    this.getMediaConstraints = function () {
      return Utils.shallowCopy(_mediaConstraints);
    };

    this.getLocalMediaType = function () {
      // This is supposed to be the intersection of media constraints and the
      // active media type.
      return {
        audio: _mediaConstraints.audio && _activeMediaType.audio,
        video: _mediaConstraints.video && _activeMediaType.video,
        desktop: _mediaConstraints.desktop && _activeMediaType.video
      };
    };

    this.hasScreenShare = function () {
      return !!_localDesktopStream;
    };

    this.warmup = function (mediaType, remoteSdp, successCb, errorCb) {
      successCb = successCb || function () {};
      errorCb = errorCb || function () {};

      if (_sessionTerminated) {
        logger.error('[RtcSessionController]: Warmup Connection failed: Session already terminated');
        sendAsyncCallback(errorCb, 'res_UnexpectedError');
        return;
      }

      if (sdpParser.isNoOfferSdp(remoteSdp)) {
        remoteSdp = null;
      }
      // This function is used to 'warmup' the RTCPeerConnection prior to starting it.
      // This is the only function which uses success and error callbacks. All other
      // functions will asynchronously raise an onError event in case of errors.
      mediaType = normalizeMediaType(mediaType);

      logger.info('[RtcSessionController]: Warming up the connection - mediaType = ', mediaType);

      if (!WebRTCAdapter.enabled) {
        logger.warn('[RtcSessionController]: WebRTC not supported by browser');
        sendAsyncCallback(errorCb, 'res_NoWebRTC');
        return;
      }

      if (_pc && _pcSettings.main.sdpStatus !== SdpStatus.None) {
        logger.error('[RtcSessionController]: The connection has already been started');
        sendAsyncCallback(errorCb, 'res_UnexpectedError');
        return;
      }

      _mediaConstraints = mediaType;
      if (!_mediaConstraints.audio) {
        _isMuted = true;
      }

      getUserMedia(
        function () {
          // Successfully got access to media input devices
          if (remoteSdp) {
            _warmedUpSdp = remoteSdp;
          }
          _warmedUp = true;
          createPeerConnection(); // Create the peer connection(s) here so they can pre-allocate ICE candidates (before setLocalDescription)
          successCb();
        },
        function (err) {
          handleFailure(err, errorCb);
        }
      );
    };

    this.start = function (mediaType, remoteSdp, sendingEarlyMedia) {
      if (_sessionTerminated) {
        logger.error('[RtcSessionController]: Start Connection failed: Session already terminated');
        return false;
      }
      mediaType = normalizeMediaType(mediaType);
      logger.info('[RtcSessionController]: Start Connection - mediaType =', mediaType);

      if (!WebRTCAdapter.enabled) {
        logger.warn('[RtcSessionController]: WebRTC not supported by browser');
        sendAsyncError('res_NoWebRTC');
        return false;
      }

      if (_pc && _pcSettings.main.sdpStatus !== SdpStatus.None) {
        logger.error('[RtcSessionController]: The connection has already been started');
        sendAsyncError('res_UnexpectedError');
        return false;
      }

      logger.debug('[RtcSessionController]: Creating Peer Connection...');

      if (_warmedUp) {
        // The connection has already been warmed up
        remoteSdp = _warmedUpSdp;
        _warmedUpSdp = null;

        _isMuted = !mediaType.audio;
        if (!createPeerConnection()) {
          sendAsyncError('res_RTCError');
          return false;
        }
        if (
          mediaType.audio === _mediaConstraints.audio &&
          mediaType.video === _mediaConstraints.video &&
          mediaType.hdVideo === _mediaConstraints.hdVideo &&
          mediaType.desktop === _mediaConstraints.desktop
        ) {
          // We can continue using the MediaStream object allocated during the warmup.
          startConn(remoteSdp, !!sendingEarlyMedia);
          return true;
        } else {
          // Stop stream created during warmup to avoid it being accidentally reused
          setLocalStream(null, LOCAL_AUDIO_VIDEO);
        }
      }

      if (!createPeerConnection()) {
        sendAsyncError('res_RTCError');
        return false;
      }

      _mediaConstraints = mediaType;

      // Get access to local media
      getUserMedia(function () {
        // Successfully got access to media input devices
        startConn(remoteSdp, !!sendingEarlyMedia);
      }, handleFailure);

      return true;
    };

    this.startConnectedTimer = function () {
      if (_disableConnectedTimer) {
        _disableConnectedTimer = false;
        if (_pc && _pcSettings.main.sdpStatus === SdpStatus.AnswerSent) {
          waitConnectedState(_pc);
        }
      }
    };

    // The following function returns false in case of collision or improper call state.
    // Otherwise, it returns true.

    this.addIceCandidates = function (origin, candidates) {
      logger.info('[RtcSessionController]: Add remote ICE candidates');

      if (_pc && origin === _pc.remoteOrigin) {
        candidates.forEach(function (candidate) {
          try {
            candidate = JSON.parse(candidate);
            if (candidate.candidate) {
              candidate.candidate = candidate.candidate.replace(/(a=)?([^\r]+)(\r\n)?/i, '$2');
            }
            logger.debug('[RtcSessionController]: Remote ICE candidate', candidate);
            if (!candidate.candidate || candidate.candidate === 'end-of-candidates') {
              logger.debug('[RtcSessionController]: Ignoring end-of-remote-candidates indication.');
            } else {
              _pc.addIceCandidate(candidate);
            }
          } catch (err) {
            logger.error('Error parsing remote candidate. ', err);
          }
        });

        waitConnectedState(_pc);
      } else {
        logger.warn('[RtcSessionController]: Ignoring remote ICE candidate - No matching peer connection found.');
      }
    };

    /////////////////////////////////////////////////////////////////////////////
    // The following functions will trigger a new media renegotiation from
    // the client. All these functions are supposed to returns false in case
    // of collision or improper call state. Otherwise, they must returns true
    // and must raise an onRtcError event in case the renegotiation cannot be
    // completed.

    this.setRemoteDescription = function (remoteSdp, cb) {
      logger.info('[RtcSessionController]: Set Remote Description - type: ', remoteSdp.type);

      // Make sure the warmed-up SDP is null
      _warmedUpSdp = null;

      if (_pc && _pcSettings.main.sdpStatus === SdpStatus.AnswerReceived) {
        logger.warn('[RtcSessionController]: Still processing the previous answer remote description. Queue the new one');
        _pendingRemoteSdp = {
          sdp: remoteSdp,
          cb: cb
        };
        return;
      }

      if (_pc && _pcSettings.main.sdpStatus === SdpStatus.PrAnswerReceived) {
        // We can't reapply it so just ignore it.
        if (remoteSdp.type === 'answer') {
          setSdpStatus(SdpStatus.AnswerReceived, _pc);
          waitConnectedState(_pc);
        }
        logger.info('[RtcSessionController]: Provisional SDP already received. Ignoring any later remote SDPs');
        sendAsyncCallback(cb);
        return;
      }

      if (_pc && _pcSettings.main.sdpStatus === SdpStatus.PrAnswerApplied) {
        // We can't reapply it so just ignore it.
        if (remoteSdp.type === 'answer') {
          setSdpStatus(SdpStatus.AnswerApplied, _pc);
          waitConnectedState(_pc);
        }
        logger.info('[RtcSessionController]: Provisional SDP already applied. Ignoring any later remote SDPs');
        sendAsyncCallback(cb);
        return;
      }

      if (remoteSdp.type === 'offer') {
        // This is a media renegotiation started by the remote party.
        // Check the connection state.
        if (!isConnStable()) {
          sendAsyncCallback(cb, 'Connection not stable');
          return;
        }

        startRenegotiation(false, cb);
        getUserMedia(function () {
          setRemoteDescription(remoteSdp, false);
        }, handleFailure);
      } else {
        // This is the SDP Answer for the initial call setup or for a media
        // renegotiation started by the client.

        if (remoteSdp.sdp === 'sdp') {
          // This is a dummy SDP answer from the mock server.
          // If this is a media renegotiation just mark the renegotiation as successful.
          setSdpStatus(SdpStatus.AnswerReceived, _pc);
          _renegotiationInProgress && renegotiationSuccessful();
          waitConnectedState(_pc);
        } else {
          setRemoteDescription(remoteSdp, false);
        }
        sendAsyncCallback(cb);
      }
    };

    /**
     * Start a media renegotiation from the client (i.e. send a new SDP Offer from the client).
     *
     * @param {Function} [cb] Callback function that is invoked when media renegotiation has finished.
     * @param {Boolean} cb.success Indicates whether or not the media renegotiation was successful.
     * @return True if the request is accepted; False otherwise.
     */
    this.renegotiateMedia = function (cb) {
      logger.info('[RtcSessionController]: Start Media Renegotiation from client');
      if (!isConnStable()) {
        sendAsyncCallback(cb, 'Connection not stable');
      }
      startRenegotiation(true, cb);
      getUserMedia(renegotiateMedia, handleFailure);
    };

    this.enableRemoteVideo = function () {
      if (_remoteVideoDisabled) {
        logger.info('[RtcSessionController]: Enable receiving remote video');
        _remoteVideoDisabled = false;
      }
    };

    this.disableRemoteVideo = function () {
      if (!_remoteVideoDisabled) {
        logger.info('[RtcSessionController]: Disable receiving remote video');
        _remoteVideoDisabled = true;
      }
    };

    this.enableRemoteVideoScreenOnly = function () {
      if (!_remoteVideoScreenOnlyAllowed) {
        logger.info('[RtcSessionController]: Allow receiving only remote screen share');
        _remoteVideoScreenOnlyAllowed = true;
      }
    };

    this.disableRemoteVideoScreenOnly = function () {
      if (_remoteVideoScreenOnlyAllowed) {
        logger.info('[RtcSessionController]: Disable receiving only remote screen share');
        _remoteVideoScreenOnlyAllowed = false;
      }
    };

    /**
     * Add audio to the existing connection. This function initiates a new media renegotiation.
     *
     * @param {Function} [cb] Callback function that is invoked when media renegotiation has finished.
     * @param {Boolean} cb.success Indicates whether or not the media renegotiation was successful.
     * @return undefined.
     */
    this.addAudio = function (cb) {
      logger.info('[RtcSessionController]: Add Audio');
      changeMedia({ audio: true }, cb);
    };

    /**
     * Remove audio from the existing connection. This function initiates a new media renegotiation.
     *
     * @param {Function} [cb] Callback function that is invoked when media renegotiation has finished.
     * @param {Boolean} cb.success Indicates whether or not the media renegotiation was successful.
     * @return undefined.
     */
    this.removeAudio = function (cb) {
      logger.info('[RtcSessionController]: Remove Audio');
      changeMedia({ audio: false }, cb);
    };

    /**
     * Add video to the existing connection. This function initiates a new media renegotiation.
     *
     * @param {Function} [cb] Callback function that is invoked when media renegotiation has finished.
     * @param {Boolean} cb.success Indicates whether or not the media renegotiation was successful.
     * @return undefined.
     */
    this.addVideo = function (cb) {
      logger.info('[RtcSessionController]: Add Video');
      changeMedia({ video: true }, cb);
    };

    /**
     * Remove video from the existing connection. This function initiates a new media renegotiation.
     *
     * @param {Function} [cb] Callback function that is invoked when media renegotiation has finished.
     * @param {Boolean} cb.success Indicates whether or not the media renegotiation was successful.
     * @return undefined.
     */
    this.removeVideo = function (cb) {
      logger.info('[RtcSessionController]: Remove Video');
      changeMedia({ video: false }, cb);
    };

    /**
     * Changes the media type for the existing connection. This function initiates a new media renegotiation.
     *
     * @param {Object} mediaType The media type(s) which are supposed to be added or removed. For instance, use {video: true} to add video and {video: false} to remove it.
     * @param {Object} changeOptions Additional options associated with the change media request.
     * @param {Boolean} changeOptions.changeDesktopMedia Indicates if the desktop media also needs to change or not.
     * @param {Object} changeOptions.screenShareOptions Screenshare parameters.
     * @param {Boolean} changeOptions.isDirectUpgradingToConf Indicates if it's a direct call that is being upgraded to conference.
     * @param {Function} [cb] Callback function that is invoked when media renegotiation has finished.
     * @param {Boolean} cb.success Indicates whether or not the media renegotiation was successful.
     */
    this.changeMediaType = function (mediaType, changeOptions, cb) {
      logger.info('[RtcSessionController]: Change media type: ', mediaType);
      if (changeOptions) {
        logger.info('[RtcSessionController]: Change media options: ', changeOptions);
      }
      if (!mediaType) {
        logger.warn('[RtcSessionController]: Invalid media type');
        sendAsyncCallback(cb, 'Invalid media type');
        return;
      }
      changeMedia(mediaType, cb, changeOptions);
    };

    /**
     * Hold the existing call. This function initiates a new media renegotiation.
     *
     * @param {Function} [cb] Callback function that is invoked when media renegotiation has finished.
     * @param {Boolean} cb.success Indicates whether or not the media renegotiation was successful.
     * @return undefined.
     */
    this.holdCall = function (cb) {
      logger.info('[RtcSessionController]: Hold Call');
      if (!isConnStable()) {
        sendAsyncCallback(cb, 'Connection not stable');
        return;
      }

      if (_holding) {
        logger.warn('[RtcSessionController]: The call is already held');
        sendAsyncCallback(cb);
        return;
      }

      // Set _holding to true so we update the SDPs correctly
      _holding = true;
      // Set _holdInProgress to true so we can change _holding back to false in case the renegotiation fails.
      _holdInProgress = true;

      startRenegotiation(true, cb);
      getUserMedia(renegotiateMedia, handleFailure);
    };

    /**
     * Retrieves the existing call. This function initiates a new media renegotiation.
     *
     * @param {Function} [cb] Callback function that is invoked when media renegotiation has finished.
     * @param {Boolean} cb.success Indicates whether or not the media renegotiation was successful.
     * @return undefined.
     */
    this.retrieveCall = function (cb) {
      logger.info('[RtcSessionController]: Retrieve Call');

      if (!isConnStable()) {
        sendAsyncCallback(cb, 'Connection not stable');
        return;
      }
      if (!_holding) {
        logger.warn('[RtcSessionController]: The call is not held');
        sendAsyncCallback(cb);
        return;
      }

      // Set _holding to false so we update the SDPs correctly
      _holding = false;
      // Set _retrieveInProgress to true so we can change _holding back to true in case the renegotiation fails.
      _retrieveInProgress = true;

      startRenegotiation(true, cb);
      getUserMedia(renegotiateMedia, handleFailure);
    };

    //
    // End of media renegotiation functions
    /////////////////////////////////////////////////////////////////////////////

    this.renegotiationFailed = function (error) {
      if (_renegotiationInProgress) {
        if (!_renegotiationCb) {
          sendError('res_RTCError');
        }
        renegotiationFailed(error);
      }
    };

    this.mute = function (cb) {
      logger.info('[RtcSessionController]: Mute Call');
      enableAudioTrack(false);
      sendAsyncCallback(cb);
    };

    this.unmute = function (cb) {
      logger.info('[RtcSessionController]: Unmute Call');
      var error;
      if (!enableAudioTrack(true)) {
        // There's no audio track, return error
        error = 'res_UnmuteFailedNoMic';
      }
      sendAsyncCallback(cb, error);
    };

    this.isMuted = function () {
      return _isMuted;
    };

    this.isLocalMuteAllowed = function () {
      return _isMuted || (_localStreams[LOCAL_AUDIO_VIDEO] && _localStreams[LOCAL_AUDIO_VIDEO].getAudioTracks().length);
    };

    /***
     * Use this method to set options for call stats collection (e.g.: if it's a voicemail call, there's no incoming packets)
     */
    this.setCallStatsOptions = function (statsOptions) {
      if (_callStats && statsOptions) {
        logger.debug('[RtcSessionController]: Setting call stats options: ', statsOptions);
        _callStats.setOptions(statsOptions);
      }
    };

    this.terminate = function () {
      logger.info('[RtcSessionController]: Terminate the RTC session controller');
      close();
    };

    this.canSendDTMFDigits = function () {
      if (!_dtmfSender && !enableDTMFSender()) {
        return false;
      }
      return _dtmfSender && _dtmfSender.canInsertDTMF;
    };

    // when a key is pressed
    this.sendDTMFDigits = function (digits) {
      logger.info('[RtcSessionController]: Send DTMF Digits - num of digits: ', digits && digits.length);

      if (!isConnStable()) {
        return false;
      }

      if (!_dtmfSender && !enableDTMFSender()) {
        return false;
      }

      if (!_dtmfSender.canInsertDTMF) {
        logger.error('[RtcSessionController]: DTMF Sender cannot insert DTMF digits');
        return false;
      }

      try {
        _dtmfSender.insertDTMF(digits, 200, 50);
      } catch (e) {
        logger.error('[RtcSessionController]: Send DTMF Digits exception:', e.message);
        return false;
      }
      return true;
    };

    this.setMockedCall = function () {
      _isMockedCall = true;
    };

    this.getCurrentPeerConnection = function () {
      return _pc && _pc.mainPeerConnection;
    };

    this.getNumberOfExtraVideoChannels = function () {
      return _numberOfExtraVideoChannels;
    };

    this.isConnStable = function () {
      return isConnStable();
    };

    this.isConnected = function () {
      return _pcSettings.main.sdpStatus === SdpStatus.Connected || _renegotiationInProgress;
    };

    this.disableIncomingVideo = function () {
      _remoteStreams.forEach(function (s) {
        if (s.getVideoTracks().length > 0) {
          s.getVideoTracks()[0].enabled = false;
        }
      });
    };

    this.enableIncomingVideo = function () {
      _remoteStreams.forEach(function (s) {
        if (s.getVideoTracks().length > 0) {
          s.getVideoTracks()[0].enabled = true;
        }
      });
    };

    this.getLocalStream = function (id) {
      return _localStreams[id];
    };

    this.setLocalStream = function (id, stream) {
      _localStreams[id] = stream;
    };

    this.replaceLocalStream = function (id, stream) {
      if (_localStreams[id] && _localStreams[id] !== stream) {
        stopStream(_localStreams[id]);
      }
      _localStreams[id] = stream;
    };

    this.getRemoteStreams = function () {
      return _remoteStreams;
    };

    this.setIgnoreTurnOnNextPC = function () {
      logger.debug('[RtcSessionController]: setIgnoreTurnOnNextPC...');
      _ignoreTurnOnNextPC = true;
    };

    this.setIgnoreNextConnection = function () {
      logger.debug('[RtcSessionController]: setIgnoreNextConnection...');
      _ignoreNextConnection = true;
    };

    this.getLastSavedStats = function () {
      var stats = _oldCallStats || _callStats;
      return stats ? stats.getLastSavedStats() : null;
    };

    this.prepareForRenegotiation = function () {
      if (_isTelephonyCall) {
        createNextPeerConnection();
      }
    };

    this.changeInputDevices = function (newInputDevices) {
      logger.info('[RtcSessionController]: Change input devices to ', newInputDevices);
      if (!newInputDevices || (!newInputDevices.audio && !newInputDevices.video)) {
        // Nothing to do
        return Promise.reject('No new input devices specified');
      }
      return new Promise(function (resolve, reject) {
        var rejectHandler = function (err) {
          logger.error('[RtcSessionController]: Could not change input devices: ', err);
          reject(err);
        };
        var resolveHandler = function () {
          logger.info('[RtcSessionController]: Successfully changed input devices');
          resolve();
        };
        // Unified Plan allows us to replace tracks without the need of media renegotiations
        // We don't support changing video devices yet
        if (!newInputDevices.video) {
          if (_browser.firefox) {
            // Firefox won't let us get the stream of a new mic unless we stop
            // the old mic's stream. This means if the new getUserMedia fails,
            // we can't go back to the old stream.
            _that.replaceLocalStream(LOCAL_AUDIO_VIDEO, null);
          }
          logger.debug('[RtcSessionController]: Attempting to change input devices: ', newInputDevices);
          var oldStream = _localStreams[LOCAL_AUDIO_VIDEO];
          getUserMediaAudioVideo(
            function () {
              var stream = _localStreams[LOCAL_AUDIO_VIDEO];
              _pc.replaceTrack(oldStream.getAudioTracks()[0], stream.getAudioTracks()[0]).then(resolveHandler).catch(rejectHandler);
            },
            rejectHandler,
            newInputDevices
          );
        } else {
          // Trigger a media renegotiation
          _that.changeMediaType(_mediaConstraints, function (err) {
            err ? rejectHandler(err) : resolveHandler();
          });
        }
      });
    };

    this.adaptScreenShareForRemoteControl = function (remoteControlEnabled) {
      if (!_mediaConstraints.desktop || !_screenShareParams) {
        // There is no screenshare active
        return;
      }

      var oldScreenTrack;

      function onError(err) {
        logger.warn('[RtcSessionController]: Could not fetch new desktop stream with remoteControlEnabled=' + remoteControlEnabled + '. ', err);
        if (remoteControlEnabled) {
          // Go back to the old screen share mode
          _that.adaptScreenShareForRemoteControl(false);
        }
      }

      function onSuccess() {
        var newScreenStream = _that.getLocalStream(LOCAL_SCREEN_SHARE);
        if (newScreenStream && newScreenStream.getVideoTracks().length > 0) {
          _pc.replaceTrack(oldScreenTrack, newScreenStream.getVideoTracks()[0]);
        } else {
          onError('Missing new stream');
        }
      }

      remoteControlEnabled = !!remoteControlEnabled;
      if (_mediaConstraints.remoteControlEnabled !== remoteControlEnabled) {
        var oldScreenStream = _localStreams[LOCAL_SCREEN_SHARE];
        if (oldScreenStream && oldScreenStream.getVideoTracks().length > 0) {
          logger.debug('[RtcSessionController]: Calling adaptScreenShareForRemoteControl with remoteControlEnabled = ', remoteControlEnabled);
          oldScreenTrack = oldScreenStream.getVideoTracks()[0];
          _mediaConstraints.remoteControlEnabled = remoteControlEnabled;
          stopStream(oldScreenStream);
          getUserMediaDesktop(onSuccess, onError, _screenShareParams);
        } else {
          logger.warn('[RtcSessionController]: Cannot adapt screen share for remote control as there is no screen share stream');
        }
      }
    };

    this.getAvailableVideoReceivers = function (mediaType) {
      if (_renegotiationInProgress) {
        // do not update available video receivers during negotiation, since _pc might be null
        return null;
      }
      return (_pc && _pc.getAvailableVideoReceivers(mediaType)) || [];
    };

    this.canAbortRenegotiation = function () {
      return (
        _renegotiationInProgress &&
        _renegotiationStartedLocally &&
        (_pcSettings.main.sdpStatus === SdpStatus.None || _pcSettings.main.sdpStatus === SdpStatus.OfferPending)
      );
    };

    this.abortPendingNegotiation = function (cb) {
      if (_that.canAbortRenegotiation() && !_pendingAbort) {
        _pendingAbort = typeof cb === 'function' ? cb : true;
        return true;
      }
      return false;
    };

    logger.debug('[RtcSessionController]: Created new RtcSessionController instance. options = ', options);
  }

  // RTC session controller constants
  RtcSessionController.DEFAULT_CANDIDATES_TIMEOUT = DEFAULT_CANDIDATES_TIMEOUT;
  RtcSessionController.DEFAULT_TRICKLE_ICE_TIMEOUT = DEFAULT_TRICKLE_ICE_TIMEOUT;
  RtcSessionController.DEFAULT_ENABLE_AUDIO_AGC = DEFAULT_ENABLE_AUDIO_AGC;
  RtcSessionController.DEFAULT_ENABLE_AUDIO_EC = DEFAULT_ENABLE_AUDIO_EC;
  RtcSessionController.DEFAULT_DISABLE_AUDIO_CN = DEFAULT_DISABLE_AUDIO_CN;
  RtcSessionController.DEFAULT_UPSCALE_FACTOR = DEFAULT_UPSCALE_FACTOR;
  RtcSessionController.LOCAL_STREAMS = LOCAL_STREAMS;
  RtcSessionController.DEGRADATION_MODE = DEGRADATION_MODE;

  // RTC session controller settings which may be controlled by the application
  RtcSessionController.candidatesTimeout = DEFAULT_CANDIDATES_TIMEOUT; // Timeout waiting for candidates when Trickle ICE is not enabled
  RtcSessionController.trickleIceTimeout = DEFAULT_TRICKLE_ICE_TIMEOUT; // Timeout waiting for candidates when Trickle ICE is enabled
  RtcSessionController.enableAudioAGC = DEFAULT_ENABLE_AUDIO_AGC;
  RtcSessionController.enableAudioEC = DEFAULT_ENABLE_AUDIO_EC;
  RtcSessionController.disableAudioCN = DEFAULT_DISABLE_AUDIO_CN;
  RtcSessionController.screenshareUpscaleFactor = DEFAULT_UPSCALE_FACTOR;
  RtcSessionController.allowAllIceCandidatesScreenShare = true;
  RtcSessionController.disableTrickleIce = false;
  RtcSessionController.allowOnlyRelayCandidates = false;
  RtcSessionController.mediaNode = null;
  RtcSessionController.playbackDevices = [];
  RtcSessionController.recordingDevices = [];
  RtcSessionController.ringingDevices = [];
  RtcSessionController.videoDevices = [];
  RtcSessionController.sdpParameters = JSON.parse(JSON.stringify(DEFAULT_SDP_PARAMS)); // Deep copy
  RtcSessionController.xGoogle = JSON.parse(JSON.stringify(DEFAULT_XGOOGLE)); // Deep copy
  RtcSessionController.customTurnServers = [];
  RtcSessionController.maxVideoExtraChannels = null; // Defaults to DEFAULT_VIDEO_EXTRA_CHANNELS
  RtcSessionController.degradationPreference = DEGRADATION_MODE.MAINTAIN_RESOLUTION;
  RtcSessionController.addAdditionalLowVideoStream = !Utils.isMobile();

  // Allow application to disable separate media line for screenshare
  RtcSessionController.disableDesktopPc = false;

  // Used for logging purposes to identify how the browser/DA is configured.
  // This value needs to be retrieved from the application.
  RtcSessionController.webRTCIPHandlingPolicy = null;

  // Process the client settings returned by the access server
  RtcSessionController.processClientSettings = function (settings) {
    try {
      settings = settings || {};

      RtcSessionController.allowAllIceCandidatesScreenShare =
        settings.allowAllIceCandidatesScreenShare !== undefined ? !!settings.allowAllIceCandidatesScreenShare : true;

      RtcSessionController.disableTrickleIce = settings.disableTrickleIce !== undefined ? !!settings.disableTrickleIce : false;

      RtcSessionController.screenshareUpscaleFactor = parseFloat(settings.screenshareUpscaleFactor, 10) || RtcSessionController.DEFAULT_UPSCALE_FACTOR;

      var xGoogle = settings.xGoogle || {};
      Object.keys(DEFAULT_XGOOGLE).forEach(function (mediaType) {
        RtcSessionController.xGoogle[mediaType] =
          xGoogle[mediaType] !== undefined ? xGoogle[mediaType] : JSON.parse(JSON.stringify(DEFAULT_XGOOGLE[mediaType]));
      });

      var sdpParameters = settings.sdpParameters || {};
      Object.keys(DEFAULT_SDP_PARAMS).forEach(function (key) {
        RtcSessionController.sdpParameters[key] = sdpParameters[key] !== undefined ? sdpParameters[key] : JSON.parse(JSON.stringify(DEFAULT_SDP_PARAMS[key]));
      });

      RtcSessionController.degradationPreference =
        settings.degradationPreference !== undefined ? settings.degradationPreference : DEGRADATION_MODE.MAINTAIN_RESOLUTION;

      RtcSessionController.addAdditionalLowVideoStream =
        settings.addAdditionalLowVideoStream !== undefined ? settings.addAdditionalLowVideoStream : !Utils.isMobile();

      unifyPhone.WebRTCAdapter.useReducedAudioConstraints = settings.useReducedAudioConstraints !== undefined ? !!settings.useReducedAudioConstraints : true;

      logger.info('[RtcSessionController]: Updated RtcSessionController values: ', {
        allowAllIceCandidatesScreenShare: RtcSessionController.allowAllIceCandidatesScreenShare,
        disableTrickleIce: RtcSessionController.disableTrickleIce,
        screenshareUpscaleFactor: RtcSessionController.screenshareUpscaleFactor,
        xGoogle: RtcSessionController.xGoogle,
        sdpParameters: RtcSessionController.sdpParameters,
        useReducedAudioConstraints: unifyPhone.WebRTCAdapter.useReducedAudioConstraints
      });
    } catch (e) {
      logger.error('[RtcSessionController]: Error processing client settings. ', e);
    }
  };

  // Exports
  unifyPhone.RtcSessionController = RtcSessionController;
  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.SdpStatus = SdpStatus;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var AtcCallInfo = unifyPhone.AtcCallInfo;
  var BaseCall = unifyPhone.BaseCall;
  var CallState = unifyPhone.Enums.CallState;
  var CstaCallState = unifyPhone.Enums.CstaCallState;
  var logger = unifyPhone.logger;
  var Utils = unifyPhone.Utils;

  function AtcRemoteCall(conversation) {
    if (!conversation || !conversation.rtcSessionId) {
      throw new Error('Cannot create AtcRemoteCall object without a valid conversation');
    }

    // Call the base constructor
    AtcRemoteCall.parent.constructor.call(this, conversation, true);

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Properties
    ///////////////////////////////////////////////////////////////////////////////////////
    // Define the read-only properties to access the internal variables
    Object.defineProperties(this, {
      isPullAllowed: {
        get: function () {
          return this.atcCallInfo.isHandoverAllowed() && this.atcCallInfo.cstaState !== CstaCallState.Ringing;
        },
        enumerable: true,
        configurable: false
      },
      isDtmfAllowed: {
        get: function () {
          return this.checkState(CallState.ActiveRemote) && this.checkCstaState(CstaCallState.Active);
        },
        enumerable: true,
        configurable: false
      }
    });
    this.isAtcRemote = true;
    this.atcCallInfo = new AtcCallInfo();
    this.ctiMuted = false;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////////////////
    conversation = null; // Remove reference to conversation object

    logger.info('[AtcRemoteCall]: Created new ATC remote call with callID = ', this.callId);
  }

  Utils.inherit(AtcRemoteCall, BaseCall);

  AtcRemoteCall.prototype.isPresent = function () {
    return (
      !this.checkState([CallState.Idle, CallState.Terminated]) ||
      !this.checkCstaState([CstaCallState.Idle, CstaCallState.Failed, CstaCallState.TransferFailed, CstaCallState.Terminated])
    );
  };

  AtcRemoteCall.prototype.isMuted = function () {
    return this.ctiMuted;
  };

  AtcRemoteCall.prototype.mute = function () {
    if (this.checkState(CallState.Terminated)) {
      logger.error('[AtcRemoteCall]: Cannot mute a call that has already been terminated');
      return false;
    }

    logger.debug('[AtcRemoteCall]: Mute call with callId =', this.callId);
    this.ctiMuted = true;
    return this.ctiMuted;
  };

  AtcRemoteCall.prototype.unmute = function () {
    if (this.checkState(CallState.Terminated)) {
      logger.error('[AtcRemoteCall]: Cannot mute a call that has already been terminated');
      return false;
    }

    logger.debug('[AtcRemoteCall]: Unmute call with callId =', this.callId);
    this.ctiMuted = false;
    return this.ctiMuted;
  };

  // Exports
  unifyPhone.AtcRemoteCall = AtcRemoteCall;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global Promise, Uint8Array*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Utils = unifyPhone.Utils;

  ///////////////////////////////////////////////////////////////////////////
  // Constants
  ///////////////////////////////////////////////////////////////////////////
  var THUMB_BACKGROUND_COLOR = '#f0f0f0'; // Light-gray. Is needed for images with transparency.
  var MAX_FILE_COUNT = 10;
  var IMAGE_SMALL_HEIGHT = 312;
  var IMAGE_SMALL_WIDTH = 484;
  var COMPRESSED_IMG_PREFIX = 'tHuMbNaIl___'; // keep in sync with definition in app

  var UploadPromise;
  if (typeof Promise !== 'undefined') {
    // By default use the standard Promise object if supported by the browser
    UploadPromise = Promise;
  }

  ///////////////////////////////////////////////////////////////////////////
  // Helper functions
  ///////////////////////////////////////////////////////////////////////////
  // eslint-disable-next-line max-params
  function drawCanvasImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight) {
    // NOSONAR
    var canvas = document.createElement('canvas');
    var ctx = canvas.getContext('2d');
    canvas.width = dWidth;
    canvas.height = dHeight;
    ctx.fillStyle = THUMB_BACKGROUND_COLOR;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
    return canvas.toDataURL('image/jpeg'); // Convert canvas to jpeg url
  }

  // Resize image to a provided size, or to UnifyPhone thumbnail size
  function resizePicture(image, width, height) {
    width = width || IMAGE_SMALL_WIDTH;
    height = height || IMAGE_SMALL_HEIGHT;

    var imgWidth, imgHeight;
    imgHeight = image.height;
    imgWidth = image.width;

    if (imgHeight / imgWidth > height / width) {
      if (image.height > height) {
        imgWidth = (imgWidth * height) / imgHeight;
        imgHeight = height;
      }
    } else if (imgWidth > width) {
      imgHeight = (imgHeight * width) / imgWidth;
      imgWidth = width;
    }

    return drawCanvasImage(image, 0, 0, image.width, image.height, 0, 0, imgWidth, imgHeight);
  }

  // Create a blob from a dataURI
  function dataURItoBlob(dataURI, type) {
    var binary = atob(dataURI.split(',')[1]);
    var array = [];
    for (var i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type: type || 'image/jpeg' });
  }

  // Create a small image (thumbnail). Returns a promise.
  function createThumbnail(image, options) {
    options = options || {};
    return new UploadPromise(function (resolve /*, reject*/) {
      var reader = new FileReader();
      reader.onload = function (e) {
        var img = new Image();
        img.onload = function () {
          var resizeImg = resizePicture(this, options.width, options.height);
          var blob = dataURItoBlob(resizeImg);
          blob.name = (options.prefix || '') + (image.modifiedName || image.name);
          blob.lastModified = Math.round(Date.now() / 1000);
          resolve(blob);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(image);
    });
  }

  ///////////////////////////////////////////////////////////////////////////
  // FileUpload
  ///////////////////////////////////////////////////////////////////////////
  // eslint-disable-next-line max-lines-per-function
  function FileUpload(config) {
    // NOSONAR

    ///////////////////////////////////////////////////////////////////////////
    // Local variables and functions
    ///////////////////////////////////////////////////////////////////////////
    var _domain = config ? config.domain : null;
    var _nextReqId = 1;
    var _pendingUploads = {};

    ///////////////////////////////////////////////////////////////////////////
    // Local functions
    ///////////////////////////////////////////////////////////////////////////

    function deleteFiles(deleteUrls) {
      deleteUrls &&
        deleteUrls.forEach(function (url) {
          var req = new XMLHttpRequest();
          req.withCredentials = true;
          req.open('DELETE', url);
          req.send();
        });
    }

    // Upload a single file. Returns a promise.
    function uploadFile(reqId, file, url, onProgress, opts) {
      var clearPendingReq = function () {
        if (_pendingUploads[reqId]) {
          _pendingUploads[reqId].xhr = null;
        }
      };

      return new UploadPromise(function (resolve, reject) {
        var fileName = file.modifiedName || file.name;

        var req = new XMLHttpRequest();
        req.withCredentials = true;
        req.open('POST', url, true);
        req.setRequestHeader('Content-Type', file.type);
        req.setRequestHeader('Content-Disposition', 'attachment; filename="' + encodeURIComponent(fileName) + '"');
        if (opts) {
          opts.convId && req.setRequestHeader('x-conversation', opts.convId);
          opts.rtcSessionId && req.setRequestHeader('x-rtcsession', opts.rtcSessionId);
          opts.token && req.setRequestHeader('authorization', 'SecToken ' + opts.token);
        }

        if (typeof onProgress === 'function') {
          req.upload.onprogress = function (event) {
            if (event.total > 0) {
              onProgress(event.loaded, event.total, fileName);
            }
          };
        }

        req.onload = function () {
          clearPendingReq();
          if (req.status === 200) {
            resolve(req.response);
          } else {
            reject({
              filename: fileName,
              status: req.status,
              statusText: req.statusText,
              response: req.response,
              responseText: req.responseText
            });
          }
        };

        req.onerror = function () {
          clearPendingReq();
          reject({
            filename: fileName,
            status: 500,
            statusText: 'Connection Failed'
          });
        };

        req.onabort = function () {
          clearPendingReq();
          reject({
            filename: fileName,
            status: 0,
            statusText: 'abort'
          });
        };

        _pendingUploads[reqId].xhr = req;
        req.send(file);
      });
    }

    function getUploadPromise(file, options, onProgress) {
      onProgress = onProgress || null;

      if (!UploadPromise) {
        throw new Error('Promise support is required to use FileUpload');
      }

      var url = (_domain ? 'https://' + _domain : '') + '/api/fileapi';
      var reqId = _nextReqId;

      _nextReqId++;
      _pendingUploads[reqId] = {
        xhr: null,
        deleteUrls: []
      };
      var uploadPromise = uploadFile(reqId, file, url, onProgress, options)
        .then(function (res) {
          var resp = JSON.parse(res)[0];
          var fileId = resp.fileId || resp.id;
          var fileUrl = url + '?fileid=' + fileId;
          var fileName = file.modifiedName || file.name;
          var attachmentMetaData = {
            fileId: fileId,
            fileName: fileName,
            mimeType: resp.mimeType || file.type,
            size: file.size,
            url: fileUrl,
            deleteUrl: fileUrl
          };
          delete _pendingUploads[reqId];
          return UploadPromise.resolve(attachmentMetaData);
        })
        .catch(function (err) {
          delete _pendingUploads[reqId];
          return UploadPromise.reject(err);
        });

      uploadPromise.__uploadId = reqId;
      return uploadPromise;
    }

    ///////////////////////////////////////////////////////////////////////////
    // APIs
    ///////////////////////////////////////////////////////////////////////////

    // Upload multiple files including images. For images thumbnails are created
    // if needed. Returns a promise.
    // domain does not include the protocol. E.g. unifyPhonesandbox.net
    this.uploadFiles = function (files, domain, itemId, onProgress, noThumbnail) {
      if (!UploadPromise) {
        throw new Error('Promise support is required to use FileUpload');
      }

      if (files.length > MAX_FILE_COUNT) {
        return UploadPromise.reject('Exceeded maximum of 10 files per message');
      }

      domain = domain || _domain;

      var urlPrefix = domain ? 'https://' + domain : '';
      var result = [];
      var reqId = _nextReqId;

      _nextReqId++;
      _pendingUploads[reqId] = {
        xhr: null,
        deleteUrls: []
      };

      // Find large images that need to be resized
      var resizePromises = [];
      !noThumbnail &&
        files.forEach(function (file) {
          // Even create a thumbnail for small images
          if (Utils.isSupportedImage && Utils.isSupportedImage(file.type)) {
            resizePromises.push(
              createThumbnail(file, {
                prefix: COMPRESSED_IMG_PREFIX
              })
            );
          }
        });

      var uploadAllPromise = UploadPromise.all(resizePromises)
        .then(function (blobs) {
          // Add the blob's (thumbnails) to the list of files to be upoaded
          Array.prototype.push.apply(files, blobs);

          // Sequentially (but async) upload the files. Once all files are
          // uploaded, resolve the Promise passing the upload results.
          itemId = itemId || null;
          return files.reduce(function (sequence, file) {
            return sequence.then(function () {
              var url = urlPrefix + '/api/fileapi?itemid=' + (itemId || 'NULL');
              return uploadFile(reqId, file, url, onProgress).then(function (res) {
                var resp = JSON.parse(res)[0];
                itemId = itemId || resp.id;
                var fileId = resp.fileid[resp.fileid.length - 1];
                var fileName = file.modifiedName || file.name;
                var attachmentMetaData = {
                  fileId: fileId,
                  fileName: fileName,
                  itemId: itemId,
                  mimeType: resp.mimeType || file.type,
                  size: file.size,
                  url: urlPrefix + '/api/fileapi?fileid=' + fileId + '&itemid=' + itemId,
                  deleteUrl: urlPrefix + '/api/fileapi?fileid=' + fileId + '&itemid=' + itemId
                };
                result.push(attachmentMetaData);
                _pendingUploads[reqId].deleteUrls.push(attachmentMetaData.deleteUrl);
                return result;
              });
            });
          }, UploadPromise.resolve());
        })
        .then(function (uploadedFiles) {
          delete _pendingUploads[reqId];
          return UploadPromise.resolve(uploadedFiles);
        })
        .catch(function (err) {
          delete _pendingUploads[reqId];
          return UploadPromise.reject(err);
        });

      uploadAllPromise.__uploadId = reqId;
      return uploadAllPromise;
    };

    // Upload a single file (image) for the whiteboard
    this.uploadWhiteboardFile = function (file, rtcSessionId, token) {
      return getUploadPromise(file, {
        rtcSessionId: rtcSessionId,
        token: token
      });
    };

    // Upload an archive of CMR logs
    this.uploadCMRLogs = function (file) {
      return getUploadPromise(file);
    };

    // Upload mass import file
    this.uploadMassImportFile = function (file, inProgress) {
      return getUploadPromise(file, null, inProgress);
    };

    // Aborts the upload for given promise
    this.abort = function (promise) {
      if (promise && promise.__uploadId && _pendingUploads[promise.__uploadId]) {
        var pendingUpload = _pendingUploads[promise.__uploadId];
        delete _pendingUploads[promise.__uploadId];

        // Abort ongoing upload and delete uploaded files (if any)
        if (pendingUpload.xhr) {
          pendingUpload.xhr.abort();
        }
        deleteFiles(pendingUpload.deleteUrls);
      }
    };
  }

  FileUpload.COMPRESSED_IMG_PREFIX = COMPRESSED_IMG_PREFIX;
  FileUpload.dataURItoBlob = dataURItoBlob;
  FileUpload.drawCanvasImage = drawCanvasImage;

  FileUpload.overridePromise = function ($q) {
    UploadPromise = $q;
  };

  // Exports
  unifyPhone.FileUpload = FileUpload;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  var Utils = unifyPhone.Utils;

  var ShortcutActionTypes = {
    ANSWER_INCOMING_CALL: 'ANSWER_INCOMING_CALL',
    CLEAR_FOCUS: 'CLEAR_FOCUS',
    DECLINE_INCOMING_CALL: 'DECLINE_INCOMING_CALL',
    ENABLE_ALPHA_FEATURES: 'ENABLE_ALPHA_FEATURES',
    ENABLE_CLOSED_BETA_FEATURES: 'ENABLE_CLOSED_BETA_FEATURES',
    ENABLE_DEMO_FEATURES: 'ENABLE_DEMO_FEATURES',
    GO_TO_PREVIOUS_LIST_ITEM: 'GO_TO_PREVIOUS_LIST_ITEM',
    GO_TO_PREVIOUS_TAB: 'GO_TO_PREVIOUS_TAB',
    GO_TO_NEXT_LIST_ITEM: 'GO_TO_NEXT_LIST_ITEM',
    GO_TO_NEXT_TAB: 'GO_TO_NEXT_TAB',
    MUTE_ALL_PARTICIPANTS: 'MUTE_ALL_PARTICIPANTS',
    MUTE_UNMUTE_AUDIO: 'MUTE_UNMUTE_AUDIO',
    NAVIGATE_UNIFY_PHONE: 'NAVIGATE_UNIFY_PHONE',
    NEW_PHONE_CALL: 'NEW_PHONE_CALL',
    OPEN_LOG_MANAGEMENT_POPUP: 'OPEN_LOG_MANAGEMENT_POPUP',
    OPEN_SETTINGS: 'OPEN_SETTINGS',
    REPORT_ISSUE: 'REPORT_ISSUE',
    START_AUDIO_CALL: 'START_AUDIO_CALL',
    START_VIDEO_CALL: 'START_VIDEO_CALL',
    STOP_LEAVE_CALL: 'STOP_LEAVE_CALL',
    STOP_REMOTE_CONTROL: 'STOP_REMOTE_CONTROL',
    TEST_CANDIDATES_COLLECTION: 'TEST_CANDIDATES_COLLECTION',
    TOGGLE_SCREEN_SHARE_IN_CALL: 'TOGGLE_SCREEN_SHARE_IN_CALL',
    TOGGLE_SHORTCUTS_POPUP: 'TOGGLE_SHORTCUTS_POPUP',
    TOGGLE_VIDEO_IN_CALL: 'TOGGLE_VIDEO_IN_CALL'
  };

  var keys = {
    // The values (codes, keys, names) of the relevant keys of the supported actions
    TAB: { code: 9, key: 'TAB', name: 'res_ShortcutKeyTab' },
    ENTER: { code: 13, key: 'ENTER', name: 'res_ShortcutKeyEnter' },
    ESC: { code: 27, key: 'ESC', name: 'res_ShortcutKeyEscape' },
    UP: { code: 38, key: 'UP', name: 'res_ShortcutKeyArrowUp' },
    DOWN: { code: 40, key: 'DOWN', name: 'res_ShortcutKeyArrowDown' },
    F2: { code: 113, Key: 'F2', name: 'res_ShortcutKeyF2' },
    F11: { code: 122, Key: 'F11', name: 'res_ShortcutKeyF11' },
    NUM_0: { code: 48, key: '0' },
    NUM_1: { code: 49, key: '1' },
    NUM_2: { code: 50, key: '2' },
    NUM_3: { code: 51, key: '3' },
    NUM_4: { code: 52, key: '4' },
    NUM_5: { code: 53, key: '5' },
    NUM_6: { code: 54, key: '6' },
    NUM_7: { code: 55, key: '7' },
    NUM_8: { code: 56, key: '8' },
    NUM_9: { code: 57, key: '9' },
    A: { code: 65, key: 'A' },
    B: { code: 66, key: 'B' },
    C: { code: 67, key: 'C' },
    D: { code: 68, key: 'D' },
    F: { code: 70, key: 'F' },
    I: { code: 73, key: 'I' },
    K: { code: 75, key: 'K' },
    L: { code: 76, key: 'L' },
    M: { code: 77, key: 'M' },
    O: { code: 79, key: 'O' },
    P: { code: 80, key: 'P' },
    R: { code: 82, key: 'R' },
    S: { code: 83, key: 'S' },
    T: { code: 84, key: 'T' },
    U: { code: 85, key: 'U' },
    W: { code: 87, key: 'W' },
    X: { code: 88, key: 'X' }
  };

  var categories = {
    // The categories of the supported actions
    GLOBAL: { id: 'global', name: 'res_Global' },
    NAVIGATION: { id: 'navigation', name: 'res_ShortcutCategoryNavigation' },
    NOTIFICATION: { id: 'notification', name: 'res_ShortcutCategoryNotification' },
    CONVERSATION: { id: 'conversation', name: 'res_Conversation' },
    CALLING: { id: 'calling', name: 'res_ShortcutCategoryCalling' },
    SUPPORT: { id: 'support', name: 'res_Support' }
  };

  var ShortcutActions = [
    // The supported actions that can be triggered by a keyboard shortcut (sequence as on popup)
    {
      id: ShortcutActionTypes.TOGGLE_SHORTCUTS_POPUP,
      name: 'res_ShortcutActionToggleShortcutsPopup',
      category: categories.GLOBAL,
      fullScreen: true,
      key: keys.K,
      windows: {
        alt: true
      },
      mac: {
        shift: true,
        cmd: true
      }
    },
    {
      id: ShortcutActionTypes.NEW_PHONE_CALL,
      name: 'res_ShortcutActionNewPhoneCall',
      category: categories.GLOBAL,
      key: keys.U,
      windows: {
        alt: true
      },
      mac: {
        shift: true,
        cmd: true
      }
    },
    {
      id: ShortcutActionTypes.OPEN_SETTINGS,
      category: categories.GLOBAL,
      key: keys.I,
      windows: {
        alt: true
      },
      mac: {
        shift: true,
        cmd: true
      },
      hidden: true
    },
    {
      id: ShortcutActionTypes.CLEAR_FOCUS,
      name: 'res_ShortcutActionClearFocus',
      category: categories.GLOBAL,
      fullScreen: true,
      key: keys.ESC
    },
    {
      id: ShortcutActionTypes.NAVIGATE_UNIFY_PHONE,
      name: 'res_ShortcutActionNavigateUnifyPhone',
      category: categories.GLOBAL,
      fullScreen: true,
      key: keys.T,
      shift: true,
      alt: true
    },
    {
      id: ShortcutActionTypes.GO_TO_PREVIOUS_LIST_ITEM,
      name: 'res_ShortcutActionPreviousListItem',
      category: categories.NAVIGATION,
      fullScreen: true,
      key: keys.UP
    },
    {
      id: ShortcutActionTypes.GO_TO_NEXT_LIST_ITEM,
      name: 'res_ShortcutActionNextListItem',
      category: categories.NAVIGATION,
      fullScreen: true,
      key: keys.DOWN
    },
    {
      id: ShortcutActionTypes.GO_TO_PREVIOUS_TAB,
      name: 'res_ShortcutActionPreviousTab',
      category: categories.NAVIGATION,
      fullScreen: true,
      key: keys.TAB,
      shift: true
    },
    {
      id: ShortcutActionTypes.GO_TO_NEXT_TAB,
      name: 'res_ShortcutActionNextTab',
      category: categories.NAVIGATION,
      fullScreen: true,
      key: keys.TAB
    },
    {
      id: ShortcutActionTypes.ANSWER_INCOMING_CALL,
      name: 'res_ShortcutActionAnswerIncomingCall',
      category: categories.CALLING,
      key: keys.NUM_1,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      }
    },
    {
      id: ShortcutActionTypes.DECLINE_INCOMING_CALL,
      name: 'res_ShortcutActionDeclineCall',
      category: categories.CALLING,
      key: keys.NUM_2,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      }
    },
    {
      id: ShortcutActionTypes.START_AUDIO_CALL,
      name: 'res_ShortcutActionStartAudioCall',
      category: categories.CALLING,
      key: keys.NUM_3,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      }
    },
    {
      id: ShortcutActionTypes.START_VIDEO_CALL,
      name: 'res_ShortcutActionStartVideoCall',
      category: categories.CALLING,
      key: keys.NUM_4,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      }
    },
    {
      id: ShortcutActionTypes.TOGGLE_VIDEO_IN_CALL,
      category: categories.CALLING,
      fullScreen: true,
      key: keys.NUM_6,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      },
      hidden: true
    },
    {
      id: ShortcutActionTypes.TOGGLE_SCREEN_SHARE_IN_CALL,
      category: categories.CALLING,
      fullScreen: true,
      key: keys.NUM_7,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      },
      hidden: true
    },
    {
      id: ShortcutActionTypes.STOP_REMOTE_CONTROL,
      name: 'res_ShortcutActionStopRemoteControl',
      category: categories.CALLING,
      fullScreen: true,
      key: keys.NUM_8,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      }
    },
    {
      id: ShortcutActionTypes.MUTE_ALL_PARTICIPANTS,
      category: categories.CALLING,
      fullScreen: true,
      key: keys.NUM_9,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      },
      hidden: true
    },
    {
      id: ShortcutActionTypes.STOP_LEAVE_CALL,
      name: 'res_ShortcutActionStopLeaveCall',
      category: categories.CALLING,
      fullScreen: true,
      guest: true,
      key: keys.NUM_0,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      }
    },
    {
      id: ShortcutActionTypes.MUTE_UNMUTE_AUDIO,
      name: 'res_ShortcutActionMuteUnmuteAudio',
      category: categories.CALLING,
      fullScreen: true,
      guest: true,
      key: keys.M,
      windows: {
        ctrl: true
      },
      mac: {
        shift: true,
        alt: true
      }
    },
    {
      id: ShortcutActionTypes.ENABLE_ALPHA_FEATURES,
      category: categories.SUPPORT,
      fullScreen: true,
      key: keys.X,
      shift: true,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      },
      hidden: true,
      bypassSettings: true
    },
    {
      id: ShortcutActionTypes.ENABLE_DEMO_FEATURES,
      category: categories.SUPPORT,
      fullScreen: true,
      key: keys.D,
      shift: true,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      },
      hidden: true,
      bypassSettings: true
    },
    {
      id: ShortcutActionTypes.ENABLE_CLOSED_BETA_FEATURES,
      category: categories.SUPPORT,
      fullScreen: true,
      key: keys.B,
      shift: true,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      },
      hidden: true,
      bypassSettings: true
    },
    {
      id: ShortcutActionTypes.OPEN_LOG_MANAGEMENT_POPUP,
      category: categories.SUPPORT,
      guest: true,
      key: keys.L,
      shift: true,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      },
      hidden: true,
      bypassSettings: true
    },
    {
      id: ShortcutActionTypes.REPORT_ISSUE,
      category: categories.SUPPORT,
      key: keys.R,
      shift: true,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      },
      hidden: true,
      bypassSettings: true
    },
    {
      id: ShortcutActionTypes.TEST_CANDIDATES_COLLECTION,
      category: categories.SUPPORT,
      key: keys.T,
      shift: true,
      alt: true,
      windows: {
        ctrl: true
      },
      mac: {
        cmd: true
      },
      hidden: true,
      bypassSettings: true
    }
  ];

  // Set the proper values based on OS type
  ShortcutActions.forEach(function (action) {
    var valueSet = Utils.isMacOs ? action.mac : action.windows;
    if (valueSet) {
      Object.assign(action, valueSet);
    }
    action.shift = !!action.shift;
    action.ctrl = !!action.ctrl;
    action.cmd = !!action.cmd;
    action.alt = !!action.alt;
  });

  // Exports
  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.ShortcutActionTypes = ShortcutActionTypes;
  unifyPhone.ShortcutActions = ShortcutActions;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global Uint8Array*/

// This code is based on recorder.js from https://github.com/awslabs/aws-lex-browser-audio-capture

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var logger = UnifyPhone.logger;
  var RtcSessionController = UnifyPhone.RtcSessionController;
  var Utils = UnifyPhone.Utils;
  var WebRTCAdapter = UnifyPhone.WebRTCAdapter;

  var recorderWorker = null;
  var activeRecorder = null;

  var getMediaStream = function (successCb, errorCb) {
    // Get the up-to-date list of devices that are plugged in and select one based on the list of
    // devices previously selected by the user
    WebRTCAdapter.getMediaSources(function (audioSources) {
      var source = Utils.selectMediaDevice(audioSources, RtcSessionController.recordingDevices);
      var audioConstraints = {
        enableAudioAGC: RtcSessionController.enableAudioAGC,
        enableAudioEC: RtcSessionController.enableAudioEC,
        disableAudioCN: RtcSessionController.disableAudioCN,
        sourceId: source && source.id
      };
      logger.debug('[audioRecorder]: Get media stream with audio constraints: ', audioConstraints);
      var constraints = {
        audio: WebRTCAdapter.getAudioOptions(audioConstraints),
        video: false
      };
      WebRTCAdapter.getUserMedia(constraints, successCb, errorCb);
    });
  };

  /**
   * The Recorder object. Sets up the onaudioprocess callback and communicates with the Worker to perform audio actions.
   */
  // eslint-disable-next-line max-lines-per-function
  function Recorder(config) {
    // NOSONAR
    // Start the Web Worker if it hasn't been started yet
    if (!recorderWorker) {
      logger.debug('[audioRecorder]: Start the recorder web worker');
      recorderWorker = new Worker('./audioRecorderWorker.js');
    }

    config = config || {};
    config.silenceDetectionTime = config.silenceDetectionTime || 1500;
    config.silenceSuppressionAmplitude = config.silenceSuppressionAmplitude || 0.2;

    var recording = false;
    var pendingRecording = false;
    var destroyed = false;
    var startTime;
    var silenceCallback;
    var visualizationCallback;
    var audioStream, audioContext, analyser;

    var stopMediaStream = function () {
      if (audioStream && !config.stream) {
        WebRTCAdapter.stopMediaStream(audioStream);
        audioStream = null;
      }
      if (audioContext) {
        audioContext.close();
        audioContext = null;
      }
    };

    /**
     * Checks the time domain data to see if the amplitude of the audio waveform is more than
     * the silence threshold. If it is, "noise" has been detected and it resets the start time.
     * If the elapsed time reaches the time threshold the silence callback is called. If there is a
     * visualizationCallback it invokes the visualization callback with the time domain data.
     */
    var analyse = function () {
      analyser.fftSize = 2048;
      var bufferLength = analyser.frequencyBinCount;
      var dataArray = new Uint8Array(bufferLength);
      var amplitude = config.silenceSuppressionAmplitude;

      analyser.getByteTimeDomainData(dataArray);

      if (typeof visualizationCallback === 'function') {
        visualizationCallback(dataArray, bufferLength);
      }

      if (typeof silenceCallback !== 'function') {
        return;
      }

      for (var i = 0; i < bufferLength; i++) {
        // Normalize between -1 and 1.
        var currValueTime = dataArray[i] / 128 - 1.0;
        if (currValueTime > amplitude || currValueTime < -1 * amplitude) {
          startTime = Date.now();
          return;
        }
      }
      var elapsedTime = Date.now() - startTime;
      if (elapsedTime > config.silenceDetectionTime) {
        startTime = Date.now();
        silenceCallback();
      }
    };

    var init = function (stream) {
      if (destroyed) {
        WebRTCAdapter.stopMediaStream(stream);
        return;
      }
      logger.debug('[audioRecorder]: Got MediaStream object. Initialize recorder objects.');
      audioStream = stream;
      audioContext = new window.AudioContext();
      var source = audioContext.createMediaStreamSource(audioStream);

      // Create a ScriptProcessorNode with a bufferSize of 4096 and a single input and output channel
      var node = source.context.createScriptProcessor(4096, 1, 1);

      // Send init message to worker
      recorderWorker.postMessage({
        command: 'init',
        config: {
          sampleRate: source.context.sampleRate
        }
      });

      /**
       * The onaudioprocess event handler of the ScriptProcessorNode interface. It is the EventHandler to be
       * called for the audioprocess event that is dispatched to ScriptProcessorNode node types.
       * @param {AudioProcessingEvent} audioProcessingEvent - The audio processing event.
       */
      node.onaudioprocess = function (audioProcessingEvent) {
        if (!recording) {
          return;
        }
        recorderWorker.postMessage({
          command: 'record',
          buffer: [audioProcessingEvent.inputBuffer.getChannelData(0)]
        });
        analyse();
      };

      analyser = source.context.createAnalyser();
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;
      analyser.smoothingTimeConstant = 0.85;

      source.connect(analyser);
      analyser.connect(node);
      node.connect(source.context.destination);

      if (pendingRecording) {
        logger.debug('[audioRecorder]: Start the pending recording');
        pendingRecording = false;
        startTime = Date.now();
        recording = true;
      }
    };

    /**
     * Callback invoked when recorder detects silence.
     *
     * @callback onSilenceCallback
     */

    /**
     * Callback invoked with recording data for visualization.
     *
     * @callback visualizerCallback
     * @param {Uint8Array} dataArray
     * @param {number} bufferLength
     */

    /**
     * Sets the silence and viz callbacks, resets the silence start time, and sets recording to true.
     * @param {onSilenceCallback} onSilence - Called when silence is detected.
     * @param {visualizerCallback} visualizer - Can be used to visualize the captured buffer.
     */
    var record = function (onSilence, visualizer) {
      if (destroyed) {
        throw new Error('Recorder resources have already been released.');
      }
      silenceCallback = onSilence;
      visualizationCallback = visualizer;

      if (!audioStream) {
        logger.debug('[audioRecorder]: Waiting for MediaStream object before starting recording');
        pendingRecording = true;
      } else {
        logger.debug('[audioRecorder]: Start the recording');
        startTime = Date.now();
        recording = true;
      }
    };

    /**
     * Pauses the current recording
     */
    var pause = function () {
      if (destroyed) {
        return;
      }
      logger.debug('[audioRecorder]: Pause the recording');
      pendingRecording = false;
      recording = false;
    };

    /**
     * Callback invoked with the exported WAV file.
     *
     * @callback onExportCallback
     * @param {Blob} blob The exported audio as a Blob.
     */

    /**
     * Stops the current recording and export the recorded audio as a Blob.
     * @param {onExportCallback} onExport - Called when the export is complete.
     * @param {Number} sampleRate The sample rate to use in the export.
     */
    var exportWAV = function (onExport, sampleRate) {
      if (typeof onExport !== 'function') {
        throw new Error('Invalid input for onExport parameter.');
      }
      if (destroyed) {
        throw new Error('Recorder resources have already been released.');
      }
      logger.debug('[audioRecorder]: Stop the recording and export the recorded WAV file');

      recorderWorker.postMessage({
        command: 'export',
        sampleRate: sampleRate
      });

      recorderWorker.onmessage = function (message) {
        logger.debug('[audioRecorder]: Received exported WAV file from worker');
        onExport(message.data);
        recorderWorker.onmessage = null;
      };
    };

    /**
     * Releases objects allocated by the Recorder instance. Also posts "clear" message to the worker.
     */
    var stop = function () {
      if (destroyed) {
        return;
      }
      logger.debug('[audioRecorder]: Close the Recorder instance and release resources');
      stopMediaStream();
      activeRecorder = null;
      pendingRecording = false;
      recording = false;
      recorderWorker.postMessage({ command: 'clear' });
      destroyed = true;
    };

    /**
     * Clears the recorded data
     */
    var clear = function () {
      if (destroyed) {
        return;
      }
      logger.debug('[audioRecorder]: Clear the recording');
      recorderWorker.postMessage({ command: 'clear' });
    };

    // Recorder object's public interfaces
    this.record = record;
    this.pause = pause;
    this.exportWAV = exportWAV;
    this.stop = stop;
    this.clear = clear;

    // Initialize the media stream
    if (config.stream) {
      logger.error('[audioRecorder]: Use MediaStream object that has been provided');
      init(config.stream);
    } else {
      getMediaStream(init, function (error) {
        var errorName = (error && error.name) || 'Unspecified';
        logger.error('[audioRecorder]: Failed to get media stream: ', errorName);
      });
    }
  }

  /**
   * Audio recorder object. Handles setting up the audio context,
   * accessing the mike, and creating the Recorder object.
   */
  var AudioRecorder = {
    isSupported: function () {
      return typeof window.AudioContext !== 'undefined';
    },
    getActiveRecorder: function () {
      return activeRecorder;
    },
    createRecorder: function (config) {
      if (typeof window.AudioContext === 'undefined') {
        throw new Error('Audio recording is not supported');
      }
      logger.debug('[audioRecorder]: Create a new Recorder object with config: ', config);
      if (activeRecorder) {
        // There is already an active recorder
        logger.warn('[audioRecorder]: There is already an active Recorder. Cannot create new one.');
        return null;
      }
      activeRecorder = new Recorder(config);
      return activeRecorder;
    }
  };

  // Exports
  unifyPhone.AudioRecorder = AudioRecorder;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
// Define external globals for JSHint

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  function BaseEventTarget(logger) {
    var _listeners = {};

    ///////////////////////////////////////////////////////////////////////////
    // Public interfaces
    ///////////////////////////////////////////////////////////////////////////
    this.dispatch = function (event) {
      var type = event && event.type;
      if (!type) {
        logger && logger.error('[BaseEventTarget]: Attempted to dispatch a bad event');
        return;
      }

      if (_listeners[type]) {
        _listeners[type].forEach(function (listener) {
          try {
            listener(event);
          } catch (e) {
            logger && logger.error(e);
          }
        });
      } else {
        logger && logger.debug('[BaseEventTarget]: There is no listener for ' + type);
      }
    };

    this.addEventListener = function (type, listener) {
      if (!type || typeof listener !== 'function') {
        logger && logger.error('[BaseEventTarget]: addEventListener invoked with invalid arguments');
        return;
      }
      if (!_listeners[type]) {
        _listeners[type] = [];
      }
      if (_listeners[type].indexOf(listener) >= 0) {
        logger && logger.warning('[BaseEventTarget]: Attempted to add duplicate event listener');
        return;
      }
      _listeners[type].push(listener);
      logger && logger.debug('[BaseEventTarget]: Added event listener for ' + this.name + ', type = ' + type + ', count = ' + _listeners[type].length);
    };

    this.removeEventListener = function (type, listener) {
      if (_listeners[type]) {
        var idx = _listeners[type].indexOf(listener);
        if (idx >= 0) {
          _listeners[type].splice(idx, 1);
        }

        logger && logger.debug('[BaseEventTarget]: Removed event listener for ' + this.name + ', type = ' + type + ', count = ' + _listeners[type].length);
      }
    };

    this.hasEventListener = function (type) {
      return !!_listeners[type] && _listeners[type].length > 0;
    };

    // Needed for unit tests, since multiple instances of angular services
    // are created during test execution, so multiple listeners are added.
    // Also used by the SDK to since multiple users are logged in.
    // This API SHALL NOT be used by IMP client itself.
    this.removeAllListeners = function () {
      _listeners = {};
    };
  }

  BaseEventTarget.prototype.constructor = BaseEventTarget;

  // Helper function to create a generic event with no data
  BaseEventTarget.prototype.createEvent = function (type) {
    return { type: type };
  };

  // Helper function to dispatch a generic event with no data
  BaseEventTarget.prototype.dispatchEvent = function (type) {
    var evt = this.createEvent(type);
    this.dispatch(evt);
  };

  // Exports
  unifyPhone.BaseEventTarget = BaseEventTarget;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var BaseEventTarget = unifyPhone.BaseEventTarget;
  var ChromeExtension = unifyPhone.ChromeExtension;
  var logger = unifyPhone.logger;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-lines-per-function
  function ExtensionConnHandler() {
    // NOSONAR
    // Call the base constructor
    ExtensionConnHandler.parent.constructor.call(this, logger);

    ///////////////////////////////////////////////////////////////////////////
    // Local variables
    ///////////////////////////////////////////////////////////////////////////
    // Default response wait time
    var RESPONSE_TIMEOUT = 5000;
    // Response wait time for requests which may require the user to grant new permissions
    var LONG_RESPONSE_TIMEOUT = 180000;
    // Response wait time for requests to Exchange connector
    var EXCHANGE_RESPONSE_TIMEOUT = 65000;

    var _that = this;
    var _reqCallbacks = {};
    var _reqId = 0;
    var _extInitialized = false;

    ///////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////
    function getNextReqId() {
      _reqId++;
      return _reqId;
    }

    function addReqCallback(cb, cbTimeout) {
      cb = cb || function () {};

      var reqId = getNextReqId();
      var responseTimer = window.setTimeout(function () {
        if (_reqCallbacks[reqId]) {
          logger.error('[ExtensionConnHandler]: Timeout waiting for response. reqId:', reqId);
          delete _reqCallbacks[reqId];
          cb(ChromeExtension.ResponseError.TIMEOUT);
        }
      }, cbTimeout || RESPONSE_TIMEOUT);

      _reqCallbacks[reqId] = { cb: cb, timer: responseTimer };

      return reqId;
    }

    function unregisterReqCallback(reqId) {
      if (!reqId || !_reqCallbacks[reqId]) {
        return false;
      }
      delete _reqCallbacks[reqId];
      return true;
    }

    function dispatchInternalEvent(eventName, data) {
      logger.info('[ExtensionConnHandler]: Dispatching event: ', eventName);
      var newEvt = _that.createEvent(eventName);
      newEvt.data = data;
      _that.dispatch(newEvt);
    }

    function handleExtMessage(msg) {
      // Internal events are supposed to be handled by the ExtensionConnHandler instance.
      // All other events are published to the interested applications
      switch (msg.target) {
        case ChromeExtension.BgTarget.INTERNAL:
          switch (msg.data.type) {
            case ChromeExtension.BgInternalMsgType.INIT_MSG:
              logger.info('[ExtensionConnHandler]: Received INIT_MSG. Send an INIT_MSG_ACK to the extension.');
              _extInitialized = true;
              sendMessage({
                target: ChromeExtension.BgTarget.INTERNAL,
                data: {
                  type: ChromeExtension.BgInternalMsgType.INIT_MSG_ACK
                }
              });
              dispatchInternalEvent('extensionInitialized', msg.data);
              break;
            case ChromeExtension.BgInternalMsgType.EXTENSION_DISCONNECTED:
              logger.warning('[ExtensionConnHandler]: Received EXTENSION_DISCONNECTED from ansible-content');
              onExtensionUnregistered();
              break;
            default:
              logger.warning('[ExtensionConnHandler]: Received unknown INTERNAL msg type:', msg.data.type);
              break;
          }
          break;

        case ChromeExtension.BgTarget.HEADSET_APP:
          switch (msg.data.type) {
            case ChromeExtension.BgHeadsetAppMsgType.HEADSET_APP_LAUNCHED:
              dispatchInternalEvent('headsetAppLaunched', msg.data);
              break;
            case ChromeExtension.BgHeadsetAppMsgType.HEADSET_APP_UNINSTALLED:
              dispatchInternalEvent('headsetAppUninstalled', msg.data);
              break;
            default:
              logger.warning('[ExtensionConnHandler]: Received unknown HEADSET_APP msg type:', msg.data.type);
              break;
          }
          break;

        case ChromeExtension.BgTarget.PRIVACY_SETTINGS:
          if (msg.data.type === ChromeExtension.BgPrivacySettingsMsgType.IP_HANDLING_POLICY_CHANGED) {
            dispatchInternalEvent('webRTCIPHandlingPolicyChanged', msg.data);
          } else {
            logger.warning('[ExtensionConnHandler]: Received unknown PRIVACY_SETTINGS msg type:', msg.data.type);
          }
          break;

        default:
          if (!msg.suppressLog) {
            logger.info('[ExtensionConnHandler]: Dispatching event:', msg.target);
          }
          var newEvt = _that.createEvent(msg.target);
          newEvt.data = msg.data;
          newEvt.suppressLog = msg.suppressLog;
          // Just add the request id for an Extension request
          if (msg.reqId && msg.type === ChromeExtension.BgMsgType.REQUEST) {
            newEvt.reqId = msg.reqId;
          }
          _that.dispatch(newEvt);
          break;
      }
    }

    function handleExtResponse(resp) {
      if (_reqCallbacks[resp.reqId]) {
        var cbInfo = _reqCallbacks[resp.reqId];
        if (cbInfo.timer) {
          window.clearTimeout(cbInfo.timer);
        }
        delete _reqCallbacks[resp.reqId];

        // Invoke the callback function (error is always the 1st parameter)
        if (!resp.data || !resp.data.error) {
          cbInfo.cb(null, resp.data);
        } else {
          cbInfo.cb(resp.data.error);

          if (resp.data.error === ChromeExtension.ResponseError.UNREGISTERED) {
            onExtensionUnregistered();
          }
        }

        if (!resp.suppressLog) {
          logger.debug('[ExtensionConnHandler]: Remaining callbacks pending:', Object.keys(_reqCallbacks).length);
        }
      } else {
        logger.info('[ExtensionConnHandler]: No registered callback for reqId =', resp.reqId);
      }
    }

    // Send Messages (requests) to Chrome Extension
    function sendMessage(data, cb, cbTimeout, suppressLog) {
      if (!data) {
        logger.error('[ExtensionConnHandler]: Cannot send message. Invalid message.');
        cb &&
          window.setTimeout(function () {
            cb('Invalid message');
          }, 0);
        return false;
      }

      if (!_that.isExtensionRunning()) {
        logger.info('[ExtensionConnHandler]: Chrome extension is not running');
        cb &&
          window.setTimeout(function () {
            cb('Extension not running');
          }, 0);
        return false;
      }
      return dispatchEvent(data, cb, cbTimeout, suppressLog);
    }

    function dispatchEvent(data, cb, cbTimeout, suppressLog) {
      if (data.type === ChromeExtension.BgMsgType.REQUEST) {
        data.reqId = addReqCallback(cb, cbTimeout);
      }

      if (!suppressLog) {
        logger.msgSend('[ExtensionConnHandler]: ', data);
      }
      data.suppressLog = suppressLog;
      var msg = new CustomEvent(ChromeExtension.DOMEvent.TO_EXTENSION, { detail: JSON.stringify(data) });
      try {
        document && document.dispatchEvent(msg);
      } catch (e) {
        logger.warning('[ExtensionConnHandler]: Error sending message: ' + msg, e);
        return false;
      }
      return data.reqId;
    }

    function onExtensionUnregistered() {
      logger.warning('[ExtensionConnHandler]: The content script has unregistered itself. Chrome extension is no longer running.');
      _extInitialized = false;
      logger.info('[ExtensionConnHandler]: Dispatching event: extensionUnregistered');
      var newEvt = _that.createEvent('extensionUnregistered');
      _that.dispatch(newEvt);
    }

    ///////////////////////////////////////////////////////////////////////////
    // Event handlers
    ///////////////////////////////////////////////////////////////////////////
    function onMessage(msgObj) {
      if (msgObj.target === ChromeExtension.BgTarget.LOG) {
        logger.logMsg(msgObj.data.log.level, msgObj.data.log.messages);
        return;
      }

      if (!msgObj.suppressLog) {
        logger.msgRcvd('[ExtensionConnHandler]: ', msgObj);
      }

      switch (msgObj.type) {
        case ChromeExtension.BgMsgType.REQUEST:
        case ChromeExtension.BgMsgType.EVENT:
          handleExtMessage(msgObj);
          break;
        case ChromeExtension.BgMsgType.RESPONSE:
          handleExtResponse(msgObj);
          break;
        default:
          logger.msgRcvd('[ExtensionConnHandler]: Unexpected message type: ', msgObj.type);
          break;
      }
    }

    function onExtensionMessage(msg) {
      if (!msg || !msg.detail) {
        return;
      }
      try {
        var msgObj = JSON.parse(msg.detail);
        onMessage(msgObj);
      } catch (e) {
        logger.error('[ExtensionConnHandler]: Error parsing JSON object. ', e);
      }
    }

    ///////////////////////////////////////////////////////////////////////////
    // Public functions
    ///////////////////////////////////////////////////////////////////////////
    this.isExtensionRunning = function () {
      return _extInitialized;
    };

    /******************************************************
     * Sreenshare APIs (Used by RtcSessionController
     ******************************************************/
    this.getScreenShareUserMedia = function (cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.SCREEN_SHARE,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            type: ChromeExtension.BgScreenShareMsgType.CHOOSE_DESKTOP_MEDIA
          }
        },
        cb
      );
    };

    /******************************************************
     * Exchange Connector APIs
     ******************************************************/
    this.exchangeConnect = function (settings, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            method: ChromeExtension.BgExchangeMsgType.CONNECT,
            object: {
              settings: settings
            }
          },
          keysToOmitFromLogging: ['data.object.settings.exchInfo']
        },
        cb,
        LONG_RESPONSE_TIMEOUT
      );
    };

    this.exchangeDisconnect = function (cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            method: ChromeExtension.BgExchangeMsgType.DISCONNECT,
            object: {}
          }
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    this.exchangeGetConnectionStatus = function (key, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            key: key,
            method: ChromeExtension.BgExchangeMsgType.GET_CONNECTION_STATUS
          },
          keysToOmitFromLogging: ['data.key']
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    this.exchangeGetCapabilities = function (cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            method: ChromeExtension.BgExchangeMsgType.GET_CAPABILITIES
          }
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    /**
     * Cancel the callback for a specific request
     *
     * @function
     * @param {int} Request Id to cancel
     *
     * @returns {bool} Returns true if callback was canceled.
     */
    this.exchangeCancelReqCallback = function (reqId) {
      logger.debug('[ExtensionConnHandler]: Unregistering callback for reqId:', reqId);
      return unregisterReqCallback(reqId);
    };

    /**
     * Execute user search in Exchange server.
     *
     * @function
     * @param {key} Encryption key
     * @param {String} The query string to search.
     * @param {int} Amount of results to be retrieved from exchange server.
     * @param {function} Callback function
     *
     * @returns {int} Returns the request id generated for this search.  Used to cancel previous searches.
     */
    this.exchangeSearchContacts = function (key, searchStr, resCount, cb) {
      return sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            key: key,
            method: ChromeExtension.BgExchangeMsgType.SEARCH_CONTACTS,
            object: {
              searchString: searchStr,
              resultCount: resCount
            }
          },
          keysToOmitFromLogging: ['data.key']
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    this.exchangeResolveContact = function (key, contactData, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            key: key,
            method: ChromeExtension.BgExchangeMsgType.RESOLVE_CONTACT,
            object: contactData
          },
          keysToOmitFromLogging: ['data.key']
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    this.exchangeGetContact = function (key, exchangeEmail, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            key: key,
            method: ChromeExtension.BgExchangeMsgType.GET_CONTACT,
            object: {
              email: exchangeEmail
            }
          },
          keysToOmitFromLogging: ['data.key']
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    this.exchangeOnRenewedToken = function (key, reqId, token, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.RESPONSE,
          data: {
            key: key,
            method: ChromeExtension.BgExchangeMsgType.ON_RENEWED_TOKEN,
            object: {
              reqId: reqId,
              token: token
            }
          },
          keysToOmitFromLogging: ['data.key', 'data.object.token']
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    this.exchangeStoreCredentials = function (credentials, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            method: ChromeExtension.BgExchangeMsgType.STORE_EXCH_CREDENTIALS,
            object: credentials
          },
          keysToOmitFromLogging: ['data.object']
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    this.getAllPersonalContacts = function (key, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            key: key,
            method: ChromeExtension.BgExchangeMsgType.GET_ALL_PERSONAL_CONTACTS
          },
          keysToOmitFromLogging: ['data.key']
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    this.getAppointments = function (key, startDate, endDate, resCount, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            key: key,
            method: ChromeExtension.BgExchangeMsgType.GET_APPOINTMENTS,
            startDate: startDate,
            endDate: endDate,
            resCount: resCount
          },
          keysToOmitFromLogging: ['data.key']
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    this.syncAllPersonalContacts = function (key, syncState, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            key: key,
            method: ChromeExtension.BgExchangeMsgType.SYNC_ALL_PERSONAL_CONTACTS,
            syncState: syncState
          },
          keysToOmitFromLogging: ['data.key', 'data.syncState']
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    this.getStoredCredentials = function (key, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            key: key,
            method: ChromeExtension.BgExchangeMsgType.GET_STORED_CREDENTIALS
          },
          keysToOmitFromLogging: ['data.key']
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    this.getOooMsg = function (key, fromEmail, email, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            key: key,
            method: ChromeExtension.BgExchangeMsgType.GET_OOO_MSG,
            fromEmail: fromEmail,
            email: email
          },
          keysToOmitFromLogging: ['data.key']
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    this.getUserAvailability = function (key, email, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.EXCHANGE_CONNECTOR,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            key: key,
            method: ChromeExtension.BgExchangeMsgType.GET_USER_AVAILABILITY,
            email: email
          },
          keysToOmitFromLogging: ['data.key', 'data.email']
        },
        cb,
        EXCHANGE_RESPONSE_TIMEOUT
      );
    };

    /******************************************************
     * Headset App Manager APIs
     ******************************************************/
    this.getHeadsetIntegrationAppStatus = function (appId, cb) {
      if (typeof cb !== 'function') {
        return;
      }
      sendMessage(
        {
          target: ChromeExtension.BgTarget.HEADSET_APP,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            type: ChromeExtension.BgHeadsetAppMsgType.GET_HEADSET_APP_STATUS,
            appId: appId
          }
        },
        cb
      );
    };

    this.launchHeadsetIntegrationApp = function (appId, localizedStrings, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.HEADSET_APP,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            type: ChromeExtension.BgHeadsetAppMsgType.LAUNCH_HEADSET_APP,
            appId: appId,
            localizedStrings: localizedStrings
          }
        },
        cb,
        LONG_RESPONSE_TIMEOUT
      );
    };

    this.restartHeadsetApp = function (appId, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.HEADSET_APP,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            type: ChromeExtension.BgHeadsetAppMsgType.RESTART_HEADSET_APP,
            appId: appId
          }
        },
        cb
      );
    };

    /******************************************************
     * Privacy Settings APIs
     ******************************************************/
    this.setIPHandlingPolicy = function (policy, localizedStrings, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.PRIVACY_SETTINGS,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            type: ChromeExtension.BgPrivacySettingsMsgType.SET_IP_HANDLING_POLICY,
            policy: policy,
            localizedStrings: localizedStrings
          }
        },
        cb,
        LONG_RESPONSE_TIMEOUT
      );
    };

    this.getIPHandlingPolicy = function (cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.PRIVACY_SETTINGS,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            type: ChromeExtension.BgPrivacySettingsMsgType.GET_IP_HANDLING_POLICY
          }
        },
        cb
      );
    };

    /******************************************************
     * Internal APIs
     ******************************************************/
    this.bringToFront = function (cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.INTERNAL,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            type: ChromeExtension.BgInternalMsgType.BRING_TO_FRONT
          }
        },
        cb
      );
    };

    this.getFile = function (file, lang, cb) {
      sendMessage(
        {
          target: ChromeExtension.BgTarget.INTERNAL,
          type: ChromeExtension.BgMsgType.REQUEST,
          data: {
            type: ChromeExtension.BgInternalMsgType.GET_FILE,
            name: file,
            lang: lang
          }
        },
        cb
      );
    };

    ///////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////

    if (Utils.getBrowserInfo().chrome) {
      // Register for events from Chrome extension
      document && document.addEventListener(ChromeExtension.DOMEvent.FROM_EXTENSION, onExtensionMessage);
    }
  }

  Utils.inherit(ExtensionConnHandler, BaseEventTarget);
  ExtensionConnHandler.prototype.name = 'ExtensionConnHandler';

  // Exports
  unifyPhone.ExtensionConnHandler = ExtensionConnHandler;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  unifyPhone.ExtensionConnHandlerSingleton = (function () {
    var _extensionConnHandler;

    return {
      getInstance: function () {
        if (!_extensionConnHandler) {
          _extensionConnHandler = new unifyPhone.ExtensionConnHandler();
        }
        return _extensionConnHandler;
      }
    };
  })();

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global __clientVersion*/

///////////////////////////////////////////////////////////////////////////////
//--- Handles Connection to Access Server (inherits from BaseEventTarget) ---
///////////////////////////////////////////////////////////////////////////////

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var BaseEventTarget = unifyPhone.BaseEventTarget;
  var ConnectionState = unifyPhone.Enums.ConnectionState;
  var Constants = unifyPhone.Constants;
  var Proto = unifyPhone.Proto;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-lines-per-function
  function ConnectionHandler(config) {
    // NOSONAR
    var logger = unifyPhone.logger;
    config = config || {};

    // Call the base constructor
    ConnectionHandler.parent.constructor.call(this, logger);

    ///////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////
    var RESPONSE_TIMEOUT = 60000; // increased to 60s to conform real backend
    var PING_TIMEOUT = 10000;
    var DEFAULT_PING_INTERVAL = 300000; // 5 minutes
    var MIN_PING_INTERVAL = 30000; // 30 seconds

    // Delayed applied to ensure that client will process response prior to event
    // This is a workaround solution to address the issue that in most scenarios
    // the backend sends the event before the response.
    var EVENT_DELAY = 500;

    ///////////////////////////////////////////////////////////////////////////
    // Local variables
    ///////////////////////////////////////////////////////////////////////////
    var _that = this;
    var _evtCallbacks = {};
    var _reqCallbacks = {};
    var _delayedEvents = [];

    var _url = '/api/ws';
    var _server = window.location.host;
    var _proxyServer = null; // Reverse proxy server for WebSocket connection
    var _accessToken = null;
    var _clientInfo = null;
    var _validDeviceTypes = Object.keys(Constants.DeviceType);
    var _clientInfoTemplate = {
      deviceType: true,
      deviceSubtype: true,
      manufacturer: true,
      osVersion: true,
      clientVersion: true,
      hardwareModel: true,
      capabilities: true,
      client_id: true // The client ID assigned to the SDK application
    };
    var _clientApiVersion = 0;

    // Disable ping for mobile clients.
    var _pingIntervalTime = config.disablePing || Utils.isMobile() ? -1 : DEFAULT_PING_INTERVAL;
    var _pingInterval = null;
    var _pingInProgress = false;

    // Use the unifyPhone reference here instead of defining it in the imports so
    // that the Unit tests can mock the Connectioncontroller
    var _connCtrl = new unifyPhone.ConnectionController(config);

    ///////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////
    function getUrl(useProxy) {
      var obj = {};
      if (_accessToken) {
        obj.accessToken = _accessToken;
      }
      if (_clientInfo) {
        Object.assign(obj, _clientInfo);
      } else if (typeof __clientVersion !== 'undefined') {
        obj.clientVersion = __clientVersion;
      }
      if (useProxy && !_proxyServer) {
        return null;
      }
      var server = useProxy ? _proxyServer : _server;
      return 'wss://' + server + _url + (Object.keys(obj).length ? '?' + Utils.toQS(obj) : '');
    }

    function addReqCallback(requestId, cb, keysToOmitFromResponse, timeout) {
      if (!cb) {
        return;
      }

      var responseTimer = window.setTimeout(
        function (reqId) {
          logger.error('[ConnectionHandler]: Timeout waiting for response. RequestId:', reqId);
          if (_reqCallbacks[reqId]) {
            delete _reqCallbacks[reqId];
            cb(Constants.ReturnCode.REQUEST_TIMEOUT);

            var numPending = Object.keys(_reqCallbacks).length;
            logger.debug('[ConnectionHandler]: Remaining callbacks pending: ', numPending);
            if (numPending === 0) {
              raiseDelayedEvents();
            }

            // Check if websocket connection is still up
            _that.pingServer();
          }
        },
        timeout,
        requestId
      );

      if (!Array.isArray(keysToOmitFromResponse)) {
        keysToOmitFromResponse = null;
      }

      _reqCallbacks[requestId] = {
        cb: cb,
        timer: responseTimer,
        keysToOmitFromResponse: keysToOmitFromResponse
      };
    }

    function getCallback(msg) {
      var cbInfo;
      switch (msg.msgType) {
        case Constants.WSMessageType.RESPONSE:
          if (!msg.response || !msg.response.code) {
            return null;
          }
          if (msg.response.user) {
            var localUser;
            if (msg.response.user.type === 'GET_STUFF') {
              localUser = msg.response.user.getStuff.user;
            } else if (msg.response.user.type === 'GET_LOGGED_ON') {
              localUser = msg.response.user.getLoggedOn.user;
            }
            if (localUser) {
              localUser.clientId = msg.clientId;
              localUser.apiVersion = msg.apiVersion;
            }
          }
          if (_reqCallbacks[msg.response.requestId]) {
            cbInfo = _reqCallbacks[msg.response.requestId];
            delete _reqCallbacks[msg.response.requestId];

            if (cbInfo.timer) {
              window.clearTimeout(cbInfo.timer);
            }
            if (cbInfo.keysToOmitFromResponse) {
              msg.keysToOmitFromLogging = cbInfo.keysToOmitFromResponse;
            }
            return {
              type: 'ack',
              cb: cbInfo.cb,
              data: msg.response
            };
          }
          logger.debug('[ConnectionHandler]: No callback for response with requestId=', msg.response.requestId);
          break;

        case Constants.WSMessageType.EVENT:
          var evt = Proto.ParseEvent(msg.event, msg.apiVersion);
          if (!evt || !evt.name) {
            logger.error('[ConnectionHandler]: Failed to parse event: ', msg.event);
          } else if (_evtCallbacks[evt.name]) {
            cbInfo = _evtCallbacks[evt.name];
            logger.debug('[ConnectionHandler]: Parsed event: ', evt.name);

            if (_that.ommitKeysFromLog && cbInfo.keysToOmitFromEvent) {
              msg.keysToOmitFromLogging = cbInfo.keysToOmitFromEvent;
            }
            return {
              type: 'evt',
              cbList: cbInfo.cbList,
              name: evt.name,
              data: evt.data,
              suppressLog: cbInfo.suppressLog
            };
          } else {
            logger.debug('[ConnectionHandler]: No registered callback for event ', evt.name);
          }
          break;

        default:
          logger.error('[ConnectionHandler]: Unexpected message type: ', msg.msgType);
          break;
      }
      return null;
    }

    function raiseEvents(cbInfo) {
      logger.debug('[ConnectionHandler]: Raising event', cbInfo.name);
      cbInfo.cbList.forEach(function (cb) {
        try {
          cb(cbInfo.data);
        } catch (e) {
          logger.error('[ConnectionHandler]: Error raising event. ', e);
        }
      });
    }

    function raiseDelayedEvents() {
      if (_delayedEvents.length > 0) {
        logger.debug('[ConnectionHandler]: Raise all delayed events');
        _delayedEvents.forEach(function (cbInfo) {
          window.clearTimeout(cbInfo.timeoutId);
          raiseEvents(cbInfo);
        });
        _delayedEvents = [];
      }
    }

    ///////////////////////////////////////////////////////////////////////////
    // Event Handlers
    ///////////////////////////////////////////////////////////////////////////
    function onStateChange(evt) {
      logger.debug('[ConnectionHandler]: WebSocket connection state changed to ', evt.newState);
      logger.debug('[ConnectionHandler]: Raising connectionStateChange event');

      if (evt.newState === ConnectionState.Connected) {
        _clientApiVersion = 0;
        if (!_pingInterval && _pingIntervalTime > 0) {
          // Ping the server every interval time to detect network errors
          _pingInterval = window.setInterval(function () {
            _that.pingServer();
          }, _pingIntervalTime);
        }
      } else {
        if (_pingInterval) {
          window.clearInterval(_pingInterval);
          _pingInterval = null;
        }
        // Whenever we lose connection, all pending requests will fail (since we're getting
        // a new clientId). So fail all of them immediately (don't let them timeout).
        if (Object.keys(_reqCallbacks).length) {
          // Cleaning must be done only for pending callbacks, as _reqCallbacks will shortly store new callbacks.
          var pendingCallbacks = _reqCallbacks;
          _reqCallbacks = {}; // ready for new callbacks
          window.setTimeout(function () {
            Object.keys(pendingCallbacks).forEach(function (reqId) {
              var cbInfo = pendingCallbacks[reqId];
              if (cbInfo.timer) {
                window.clearTimeout(cbInfo.timer);
              }
              cbInfo.cb(Constants.ReturnCode.DISCONNECTED);
            });
            raiseDelayedEvents();
          }, 0);
        }
      }

      // Dispatch a 'connectionStateChange' event
      var newEvt = _that.createEvent('connectionStateChange');
      newEvt.newState = evt.newState;
      newEvt.oldState = evt.oldState;
      _that.dispatch(newEvt);
    }
    _connCtrl.onStateChange = onStateChange;

    function onReconnectFailed() {
      logger.debug('[ConnectionHandler]: WebSocket failed to reconnect. Raising reconnectFailed event');

      // Dispatch a 'reconnectFailed' event
      var newEvt = _that.createEvent('reconnectFailed');
      _that.dispatch(newEvt);
    }
    _connCtrl.onReconnectFailed = onReconnectFailed;

    // Receive Client API Messages from Access Server
    function onMessage(msg) {
      if (!_clientApiVersion && msg.apiVersion) {
        _clientApiVersion = Utils.convertVersionToNumber(msg.apiVersion);
      }

      // First check if there are registered callbacks
      var cbInfo = getCallback(msg);
      if (!cbInfo) {
        return;
      }
      if (!cbInfo.suppressLog) {
        logger.msgRcvd('[ConnectionHandler]: ', msg);
      }
      if (cbInfo.type === 'evt') {
        if (Object.keys(_reqCallbacks).length > 0 && /^(?:User|Conversation|Search|Space)\./.test(cbInfo.name)) {
          // The client is waiting for a response. Delay the event handling.
          logger.debug('[ConnectionHandler]: Delay event:', cbInfo.name);
          cbInfo.timeoutId = window.setTimeout(function () {
            raiseEvents(cbInfo);
            var idx = _delayedEvents.indexOf(cbInfo);
            if (idx !== -1) {
              _delayedEvents.splice(idx, 1);
            }
          }, EVENT_DELAY);
          // Add cbInfo to delayed events list
          _delayedEvents.push(cbInfo);
        } else {
          // There are no pending requests. Raise the event immediately.
          raiseEvents(cbInfo);
        }
      } else {
        var numPending = Object.keys(_reqCallbacks).length;
        logger.debug('[ConnectionHandler]: Remaining callbacks pending: ', numPending);

        // Invoke the callback function (error is always the 1st parameter)
        cbInfo.cb(null, cbInfo.data);

        if (numPending === 0) {
          // If there were no pending callbacks BEFORE the response was processed,
          // we can raise the delayed events.
          raiseDelayedEvents();
        }
      }
    }
    _connCtrl.onMessage = onMessage;

    /////////////////////////////////////////////////////////////////////////////
    // Public interfaces
    /////////////////////////////////////////////////////////////////////////////
    this.ommitKeysFromLog = true;

    // Expose onMessage to allow simulating incoming messages
    this.onMessage = onMessage;

    this.setRelativeUrl = function (url) {
      _url = url;
      logger.debug('[ConnectionHandler]: Set relative URL to ', _url);
    };

    this.setTarget = function (server) {
      // Used by Chrome App & mobile App to set the websocket target
      _server = server || window.location.host;
      logger.info('[ConnectionHandler]: Set target to ', _server);
      if (_server === _proxyServer) {
        _proxyServer = null;
        logger.info('[ConnectionHandler]: Set reverse proxy target to null');
      }
    };

    this.setProxyTarget = function (server) {
      // Used in case client is supposed to open the websocket via a reverse proxy
      // (e.g. clients in China at SAG)
      if (server === _server) {
        _proxyServer = null;
      } else {
        _proxyServer = server || null;
      }
      logger.info('[ConnectionHandler]: Set reverse proxy target to ', _proxyServer || 'null');
    };

    this.setClientInfo = function (clientInfo) {
      // Used by UnifyPhone Meeting Room and other apps to set the client info
      if (clientInfo) {
        if (!clientInfo.deviceSubtype || _validDeviceTypes.indexOf(clientInfo.deviceType) === -1) {
          logger.warn('[ConnectionHandler]: Attempted to set invalid clientInfo: ', clientInfo);
          return;
        }

        _clientInfo = {};
        // Copy only valid fields
        Object.keys(clientInfo).forEach(function (key) {
          if (_clientInfoTemplate[key]) {
            _clientInfo[key] = clientInfo[key];
          }
        });
        logger.debug('[ConnectionHandler]: Set clientInfo to ', _clientInfo);
      } else {
        logger.debug('[ConnectionHandler]: Clear clientInfo');
        _clientInfo = null;
      }
    };

    this.setToken = function (token) {
      // Used by SDK
      _accessToken = token;
    };

    this.getUrl = getUrl;

    this.getState = function () {
      return _connCtrl.getState();
    };

    this.connect = function (successCallback, errorCallback) {
      logger.debug('[ConnectionHandler]: Connect WebSocket to Access Server');
      _connCtrl.connect(getUrl(false), getUrl(true), successCallback, errorCallback);
    };

    this.disconnect = function () {
      logger.debug('[ConnectionHandler]: Disconnect WebSocket from Access Server');
      _connCtrl.disconnect();
    };

    this.reconnect = function (delay) {
      logger.debug('[ConnectionHandler]: Reconnect WebSocket to Access Server.');
      _connCtrl.reconnect(getUrl(false), getUrl(true), delay);
    };

    this.reconnectV2 = function (data) {
      var delay = data && data.delay;
      var code = data && data.code;
      logger.debug('[ConnectionHandler]: Reconnect (V2) WebSocket to Access Server.');
      _connCtrl.reconnect(getUrl(false), getUrl(true), delay, code);
    };

    this.sendMessage = function (data, cb, keysToOmitFromResponse, timeout) {
      if (!data || !data.request || !data.request.requestId) {
        logger.error('[ConnectionHandler]: Cannot send message. Invalid message: ', data);
        cb &&
          window.setTimeout(function () {
            cb(Constants.ReturnCode.INVALID_MESSAGE);
          }, 0);
        return false;
      }

      logger.debug('[ConnectionHandler]: Sending ' + data.request.type + ' request');

      if (!_connCtrl.sendMessage(data)) {
        logger.error('[ConnectionHandler]: Failed to send message');
        cb &&
          window.setTimeout(function () {
            cb(Constants.ReturnCode.FAILED_TO_SEND);
          }, 0);
        return false;
      }

      // Callback must be added in the callback array only if message is actually sent through the connection, not before
      cb && addReqCallback(data.request.requestId, cb, keysToOmitFromResponse, timeout || RESPONSE_TIMEOUT);
      return true;
    };

    this.pingServer = function () {
      if (!_connCtrl.isConnected()) {
        return;
      }
      if (_pingInProgress) {
        // Don't send multiple pings to the server
        return;
      }

      var msg = 'PING';
      var reqId;
      if (!Utils.isMobile()) {
        Proto.requestNr++;
        reqId = Proto.requestNr;
        msg += '|' + reqId;
      }

      _connCtrl.sendMessage(msg);

      if (reqId) {
        _pingInProgress = true;

        // Register callback and set timeout specific for PING messages
        var responseTimer = window.setTimeout(function () {
          _pingInProgress = false;
          logger.error('[ConnectionHandler]: Timed out waiting for ping response. RequestId:', reqId);
          delete _reqCallbacks[reqId];
          _connCtrl.reconnect(getUrl(false), getUrl(true));
        }, PING_TIMEOUT);

        _reqCallbacks[reqId] = {
          cb: function () {
            _pingInProgress = false;
          },
          timer: responseTimer
        };
      }
    };

    this.setPingInterval = function (interval) {
      if (typeof interval !== 'number') {
        return;
      }
      // Normalize the interval time
      interval = interval < 0 ? -1 : Math.max(Math.floor(interval), MIN_PING_INTERVAL);

      if (_pingIntervalTime === interval) {
        // No changes
        return;
      }
      _pingIntervalTime = interval;

      if (_pingInterval) {
        window.clearInterval(_pingInterval);
        _pingInterval = null;
      }

      if (_pingIntervalTime < 0) {
        logger.info('[ConnectionHandler]: Disable ping');
      } else {
        logger.info('[ConnectionHandler]: Set ping interval time to ', _pingIntervalTime);
        if (_connCtrl.isConnected()) {
          logger.info('[ConnectionHandler]: Restart ping interval');
          _pingInterval = window.setInterval(function () {
            _that.pingServer();
          }, _pingIntervalTime);
        }
      }
    };

    this.on = function (msgType, cb, keysToOmitFromEvent) {
      if (!msgType || !cb) {
        return;
      }

      var cbInfo = _evtCallbacks[msgType] || { cbList: [] };
      cbInfo.cbList.push(cb);
      if (keysToOmitFromEvent) {
        if (Array.isArray(keysToOmitFromEvent)) {
          cbInfo.keysToOmitFromEvent = keysToOmitFromEvent;
        } else if (keysToOmitFromEvent === '*') {
          cbInfo.suppressLog = true;
        }
      }
      _evtCallbacks[msgType] = cbInfo;
    };

    this.unsubscribeAll = function () {
      _evtCallbacks = {};
      _reqCallbacks = {};
      _delayedEvents = [];
      _that.removeAllListeners();
    };

    this.injectHasActiveCall = _connCtrl.injectHasActiveCall.bind(_connCtrl);
  }

  Utils.inherit(ConnectionHandler, BaseEventTarget);
  ConnectionHandler.prototype.name = 'ConnectionHandler';

  // Exports
  unifyPhone.ConnectionHandler = ConnectionHandler;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var Proto = unifyPhone.Proto;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-lines-per-function
  function ClientApiHandler(config) {
    // NOSONAR
    var logger = unifyPhone.logger;

    ///////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////
    var SP155_API_VERSION = 2090228; // 2.9.228-x

    var NOP = function () {};

    ///////////////////////////////////////////////////////////////////////////
    // Local variables
    ///////////////////////////////////////////////////////////////////////////
    var _connHandler = new unifyPhone.ConnectionHandler(config);
    var _developmentMode = false; // Assume production mode by default
    var _lastError;
    var _hasLastRequestBeenSanitized = false;
    var _clientApiVersion = 0;
    var _clientId;
    var _userId;
    var _connectedBackend; // Used for guest connections

    ///////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////
    function sendRequest(contentType, content, cb, keysToOmitFromRequest, keysToOmitFromResponse, tenantContext, respTimeout) {
      var reqMsg = Proto.Request(contentType, content, tenantContext);
      // Check if _developmentMode is ON to omit password for Request and Response
      if (_developmentMode) {
        keysToOmitFromResponse = null;
      } else if (keysToOmitFromRequest) {
        reqMsg.keysToOmitFromLogging = keysToOmitFromRequest;
      }

      _connHandler.sendMessage(
        reqMsg,
        function (err, respMsg) {
          try {
            cb(err, respMsg);
          } catch (ex) {
            logger.error('[ClientApiHandler]: Exception: ', ex);
          }
        },
        keysToOmitFromResponse || null,
        respTimeout
      );

      return reqMsg.request.requestId;
    }

    function setError(code, errObj) {
      if (!UnifyPhone.Error) {
        return;
      }
      _lastError = new UnifyPhone.Error(code);
      if (!errObj) {
        return;
      }
      _lastError.errObj = errObj;
      _lastError.message = errObj.info;
    }

    function isResponseValid(err, rsp, cb, treatNoResultAsEmptyList) {
      _lastError = null;
      _hasLastRequestBeenSanitized = false;

      if (err) {
        setError(err);
        cb(err);
        return false;
      }

      if (rsp.sanitization) {
        // Server's antisamy stripped content.
        _hasLastRequestBeenSanitized = true;
        logger.info('[ClientApiHandler]: Sanitization stripped content: ', rsp.sanitization && rsp.sanitization.sanitizedContent);
      }

      if (rsp.code === Constants.ReturnCode.NO_RESULT) {
        if (treatNoResultAsEmptyList) {
          cb(null, []);
        } else {
          setError(Constants.ReturnCode.NO_RESULT);
          cb(Constants.ReturnCode.NO_RESULT);
        }
        return false;
      }

      var res = rsp.code;
      var errObj;

      switch (rsp.code) {
        case Constants.ReturnCode.OK:
        case Constants.ReturnCode.ENTITY_ALREADY_EXISTS:
          return true;

        case Constants.ReturnCode.SERVICE_EXCEPTION:
          if (rsp.errorInfo && rsp.errorInfo.errorCode) {
            res = rsp.errorInfo.errorCode;
            if (res === Constants.ErrorCode.THIRDPARTY_ERROR && rsp.errorInfo.thirdpartyError) {
              res = rsp.errorInfo.thirdpartyError.type;
            } else if (res === Constants.ErrorCode.UNIFY_PHONE_EXCEPTION && rsp.errorInfo.unifyPhoneException) {
              res = rsp.errorInfo.unifyPhoneException;
            }
            errObj = rsp.errorInfo;
          }
          break;
      }

      res = res || Constants.ReturnCode.UNEXPECTED_RESPONSE;
      setError(res, errObj);
      cb(res);
      return false;
    }

    function sendAsyncResp(cb, error, data) {
      error && setError(error);
      if (cb && cb !== NOP) {
        window.setTimeout(function () {
          cb(error, data);
        }, 0);
      }
    }

    function getBooleanValue(value) {
      return typeof value === 'boolean' ? value : undefined;
    }

    function getArrayValue(array) {
      if (!array) {
        return undefined;
      }
      if (!Array.isArray(array)) {
        return [array];
      }
      return array.length ? array : undefined;
    }

    function convertMediaType(mediaType) {
      var rtMediaType = [];
      if (mediaType) {
        if (mediaType.audio) {
          rtMediaType.push(Constants.RealtimeMediaType.AUDIO);
        }
        if (mediaType.video) {
          rtMediaType.push(Constants.RealtimeMediaType.VIDEO);
        }
        if (mediaType.desktop) {
          rtMediaType.push(Constants.RealtimeMediaType.DESKTOP_SHARING);
        }
      } else {
        // Assume audio by default
        rtMediaType.push(Constants.RealtimeMediaType.AUDIO);
      }
      return rtMediaType;
    }

    function convertRecordingMediaType(mediaType) {
      var recMediaType = [];
      if (mediaType) {
        if (mediaType.audio) {
          recMediaType.push(Constants.RecordingMediaType.AUDIO);
        }
        if (mediaType.video) {
          recMediaType.push(Constants.RecordingMediaType.VIDEO);
        }
        if (mediaType.text) {
          recMediaType.push(Constants.RecordingMediaType.TEXT);
        }
      } else {
        // Assume audio by default
        recMediaType.push(Constants.RealtimeMediaType.AUDIO);
      }
      return recMediaType;
    }

    function getAttachmentData(a) {
      // Return only applicable fields for Attachment object in conversation_action.proto
      return (
        a && {
          fileId: a.fileId,
          fileName: a.fileName,
          mimeType: a.mimeType,
          size: a.size,
          itemId: a.itemId
        }
      );
    }

    function getAttachmentsData(attachments) {
      return attachments && attachments.map(getAttachmentData);
    }

    function createJoinData(data) {
      return {
        convId: data.convId,
        rtcSessionId: data.rtcSessionId,
        ownerId: data.ownerId,
        sdp: data.sdp,
        mediaType: convertMediaType(data.mediaType),
        callOut: !!data.callOut,
        handover: !!data.handover,
        sendInviteCancel: !!data.sendInviteCancel,
        peerUserId: data.peerUserId || undefined, // Direct Call Prototype - Not used by Client API
        replaces: data.replaces,
        from: data.fromDn || data.fromName ? { phoneNumber: data.fromDn, displayName: data.fromName, resolvedUserId: data.fromUserId } : undefined,
        to: data.dialedDn ? { phoneNumber: data.dialedDn, displayName: data.toName || undefined, resolvedUserId: data.toUserId || undefined } : undefined,
        isTelephonyConversation: !!data.isTelephonyConversation,
        displayName: data.displayName,
        transactionId: data.transactionId,
        screenSharePointerSupported: data.screenSharePointerSupported,
        asyncSupported: true,
        noCallLog: data.noCallLog || undefined,
        guestToken: data.guestToken || undefined,
        rtcSupportedFeatures: data.rtcSupportedFeatures || undefined,
        pickUpSession: data.pickUpSession || undefined,
        sipHeaders: data.sipHeaders
      };
    }

    function getApiVersion(cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getApiVersion...');

      var request = {
        type: Constants.VersionActionType.GET_VERSION
      };
      sendRequest(Constants.ContentType.VERSION, request, function (err, rsp) {
        if (!err && rsp.code === Constants.ReturnCode.OK) {
          var apiVersion = rsp.version.getVersion.version;

          // Save the Client API version that we are connected to
          logger.info('[ClientApiHandler]: Setting client API version to ', apiVersion);
          _clientApiVersion = Utils.convertVersionToNumber(apiVersion);
        } else {
          // Something went wrong here
          // The version will be initialized in the GET_STUFF response.
          _clientApiVersion = 0;
        }
        cb();
      });
    }

    function getStuff(types, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getStuff...');

      // Set all if request is empty
      if (!types || !types.length) {
        types = Object.keys(Constants.GetStuffType);
      }

      var request = {
        type: Constants.UserActionType.GET_STUFF,
        getStuff: {
          types: types
        }
      };

      var keysToOmitFromResponse = [
        'response.user.getStuff.accounts.[].telephonyConfiguration.onsSipAuthenticationHash',
        'response.user.getStuff.accounts.[].telephonyConfiguration.ondSipAuthenticationHash',
        'response.user.getStuff.pendingSystemNotifications.[].systemNotificationEvent.broadcastText',
        'response.user.getStuff.recentSearches',
        'response.user.getStuff.recentSpaceSearches',
        'response.user.getStuff.userPresenceState.longitude',
        'response.user.getStuff.userPresenceState.latitude',
        'response.user.getStuff.userPresenceState.locationText',
        'response.user.getStuff.userPresenceState.statusMessage'
      ];

      sendRequest(
        Constants.ContentType.USER,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            var stuff = rsp.user.getStuff;
            if (types.includes(Constants.GetStuffType.USER)) {
              stuff.user.userPresenceState = stuff.userPresenceState;
              stuff.user.accounts = stuff.accounts || [];

              // Update with API version returned in GET_STUFF
              _clientApiVersion = Utils.convertVersionToNumber(stuff.user.apiVersion);

              // Save the connected clientId and userId
              _clientId = stuff.user.clientId;
              _userId = stuff.user.userId;
            }

            cb(null, stuff);
          }
        },
        null,
        keysToOmitFromResponse
      );
    }

    function getRegions(token, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getRegions ...');

      var request = {
        type: Constants.GuestActionType.GET_REGIONS,
        getRegions: {
          token: { token: token }
        }
      };

      var keysToOmitFromResponse = ['response.guest.getRegions.convTopic'];

      sendRequest(
        Constants.ContentType.GUEST,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.guest.getRegions);
          }
        },
        null,
        keysToOmitFromResponse
      );
    }

    function getDefaultHandler(cb) {
      if (typeof cb !== 'function') {
        return NOP;
      }
      return function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null);
        }
      };
    }

    ///////////////////////////////////////////////////////////////////////////////
    // Public Interfaces
    ///////////////////////////////////////////////////////////////////////////////
    Object.defineProperties(this, {
      clientId: {
        enumerable: true,
        get: function () {
          return _clientId;
        },
        set: function (clientId) {
          // Only allow setting the clientId for Unit tests
          if (clientId === 'simulatedDirect') {
            _clientId = clientId;
          }
        }
      },
      clientApiVersion: {
        enumerable: true,
        get: function () {
          return _clientApiVersion;
        }
      },
      hasLastRequestBeenSanitized: {
        enumerable: true,
        get: function () {
          return _hasLastRequestBeenSanitized;
        }
      }
    });

    this.getLastError = function () {
      return _lastError;
    };

    this.connect = function (successCallback, errorCallback, server) {
      server && _connHandler.setTarget(server);
      _connHandler.connect(successCallback, errorCallback);
    };

    this.getConnState = _connHandler.getState.bind(_connHandler);
    this.disconnect = _connHandler.disconnect.bind(_connHandler);
    this.reconnect = _connHandler.reconnect.bind(_connHandler);
    this.reconnectV2 = _connHandler.reconnectV2.bind(_connHandler);
    this.pingServer = _connHandler.pingServer.bind(_connHandler);
    this.setPingInterval = _connHandler.setPingInterval.bind(_connHandler);
    this.setTarget = _connHandler.setTarget.bind(_connHandler);
    this.setProxyTarget = _connHandler.setProxyTarget.bind(_connHandler);
    this.setClientInfo = _connHandler.setClientInfo.bind(_connHandler);
    this.setToken = _connHandler.setToken.bind(_connHandler);
    this.setRelativeUrl = _connHandler.setRelativeUrl.bind(_connHandler);
    this.addEventListener = _connHandler.addEventListener.bind(_connHandler);
    this.removeEventListener = _connHandler.removeEventListener.bind(_connHandler);
    this.onMessage = _connHandler.onMessage.bind(_connHandler);
    this.injectHasActiveCall = _connHandler.injectHasActiveCall.bind(_connHandler);

    this.setDevelopmentMode = function (developmentMode) {
      _developmentMode = !!developmentMode;
      _connHandler.ommitKeysFromLog = !developmentMode;
    };

    this.simulateEvent = function (event) {
      var msg = {
        clientId: _clientId,
        msgType: Constants.WSMessageType.EVENT,
        event: event
      };
      _connHandler.onMessage(msg);
    };

    this.on = function (msgType, cb) {
      var keysToOmitFromEvent;
      switch (msgType) {
        case 'Account.TELEPHONY_CONFIGURATION_UPDATED':
          keysToOmitFromEvent = [
            'event.account.telephonyConfigurationUpdated.configuration.onsSipAuthenticationHash',
            'event.account.telephonyConfigurationUpdated.configuration.ondSipAuthenticationHash'
          ];
          break;
        case 'Account.TELEPHONY_PREVIOUS_ALTERNATIVE_NUMBERS_UPDATED':
          keysToOmitFromEvent = '*';
          break;
        case 'RTCSession.ACTIVE_SPEAKER':
          keysToOmitFromEvent = '*';
          break;
        case 'RTCSession.LIVE_TRANSCRIPTION':
          keysToOmitFromEvent = ['event.rtcSession.liveTranscriptionEvent.text'];
          break;
        case 'Search.BASIC_SEARCH_RESULT':
          keysToOmitFromEvent = ['event.search.basicSearchResult.searchResults'];
          break;
        case 'System.BROADCAST':
          keysToOmitFromEvent = ['event.system.systemNotificationEvent.broadcastText'];
          break;
        case 'Team.TEAM_CREATED':
          keysToOmitFromEvent = ['event.team.teamCreatedEvent.team.teamName'];
          break;
        case 'Team.TEAM_UPDATED':
          keysToOmitFromEvent = ['event.team.teamUpdatedEvent.team.teamName'];
          break;
        case 'User.USER_PRESENCE_CHANGE':
          keysToOmitFromEvent = '*';
          break;
        case 'UserToUser.ATC':
          keysToOmitFromEvent = ['event.userToUser.userToUser.data'];
          break;
      }

      _connHandler.on(msgType, cb, keysToOmitFromEvent);
    };

    this.unsubscribeAll = _connHandler.unsubscribeAll;

    this.isGigasetIntegrationSupported = function () {
      // Feature introduced in SP155
      return _clientApiVersion >= SP155_API_VERSION;
    };

    this.isPlathosysIntegrationSupported = function () {
      // Feature introduced in SP155
      return _clientApiVersion >= SP155_API_VERSION;
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Public User related Interfaces
    ///////////////////////////////////////////////////////////////////////////////
    this.getApiVersion = getApiVersion;

    this.logout = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: logout...');
      var request = {
        type: Constants.UserActionType.LOGOUT,
        logoff: { invalidate: true }
      };
      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.getUser = function (userId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getUser...');
      var request = {
        type: Constants.UserActionType.GET_USER_BY_ID,
        getById: { userId: userId }
      };
      sendRequest(Constants.ContentType.USER, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.user.getById.user);
        }
      });
    };

    this.updateUser = function (userData, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: updateUser...');
      var request = {
        type: Constants.UserActionType.UPDATE,
        update: userData
      };

      sendRequest(Constants.ContentType.USER, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.user.updateResult.user);
        }
      });
    };

    this.getLoggedOnUser = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getLoggedOnUser...');
      var request = {
        type: Constants.UserActionType.GET_LOGGED_ON
      };

      var keysToOmitFromResponse = [
        'response.user.getLoggedOn.user.userPresenceState.longitude',
        'response.user.getLoggedOn.user.userPresenceState.latitude',
        'response.user.getLoggedOn.user.userPresenceState.locationText',
        'response.user.getLoggedOn.user.userPresenceState.statusMessage',
        'response.user.getLoggedOn.userPresenceState.longitude',
        'response.user.getLoggedOn.userPresenceState.latitude',
        'response.user.getLoggedOn.userPresenceState.locationText',
        'response.user.getLoggedOn.userPresenceState.statusMessage'
      ];

      sendRequest(
        Constants.ContentType.USER,
        request,
        function (err, rsp) {
          var user, getLoggedOn;

          if (isResponseValid(err, rsp, cb)) {
            getLoggedOn = rsp.user.getLoggedOn;
            user = getLoggedOn.user;
            // Make sure userPresenceState is returned in both response and user object, for backwards compatibility and SDK
            getLoggedOn.userPresenceState = getLoggedOn.userPresenceState || user.userPresenceState;
            user.userPresenceState = getLoggedOn.userPresenceState;
            user.accounts = getLoggedOn.accounts || [];

            // Save the Client API version that we are connected to
            _clientApiVersion = Utils.convertVersionToNumber(user.apiVersion);

            // Save the connected clientId and userId
            _clientId = user.clientId;
            _userId = user.userId;

            cb(null, getLoggedOn);
          }
        },
        null,
        keysToOmitFromResponse
      );
    };

    this.getStuff = function (types, cb) {
      // IF we introduce new types on GET_STUFF which require backwards compatibility checks
      // THEN we need to enable the code below that sends a GET_VERSION request first.
      //getApiVersion(getStuff.bind(this, types, cb));
      getStuff(types, cb);
    };

    this.getSupportData = function (userId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getSupportData...');

      if (!userId) {
        logger.warn('[ClientApiHandler]: userId was not provided to getSupportData');
        sendAsyncResp(cb, Constants.ReturnCode.INVALID_MESSAGE);
        return;
      }

      var request = {
        type: Constants.UserActionType.GET_SUPPORT_DATA,
        getSupportData: {
          userId: userId
        }
      };

      sendRequest(Constants.ContentType.USER, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.user.getSupportData);
        }
      });
    };

    this.setPresence = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: setPresence...');
      var request = {
        type: Constants.UserActionType.SET_PRESENCE,
        presence: {
          state: data.state,
          inTransit: data.inTransit,
          longitude: data.longitude,
          latitude: data.latitude,
          locationText: data.locationText,
          isAccurate: data.isAccurate,
          timeZoneOffset: data.timeZoneOffset,
          mobile: data.mobile,
          dndUntil: data.dndUntil,
          statusMessage: data.statusMessage || '',
          skipBackup: data.skipBackup,
          isSystemDefined: data.isSystemDefined || false
        }
      };

      var keysToOmitFromRequest = [
        'request.user.presence.longitude',
        'request.user.presence.latitude',
        'request.user.presence.locationText',
        'request.user.presence.statusMessage'
      ];
      var keysToOmitFromResponse = [
        'response.user.setPresence.state.longitude',
        'response.user.setPresence.state.latitude',
        'response.user.setPresence.state.locationText',
        'response.user.setPresence.state.statusMessage'
      ];

      sendRequest(
        Constants.ContentType.USER,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.user.setPresence.state);
          }
        },
        keysToOmitFromRequest,
        keysToOmitFromResponse
      );
    };

    this.getPresence = function (userIds, full, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getPresence...');

      var keysToOmitFromResponse = ['response.user.getPresence.states'];

      var request = {
        type: Constants.UserActionType.GET_PRESENCE,
        getPresence: {
          userIds: userIds,
          full: !!full
        }
      };
      sendRequest(
        Constants.ContentType.USER,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.user.getPresence.states);
          }
        },
        null,
        keysToOmitFromResponse
      );
    };

    this.setReadPointer = function (lastReadTimestamp, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: setReadPointer...');
      var request = {
        type: Constants.ConversationActionType.SET_READ_POINTER,
        setReadPointer: {
          lastReadTimestamp: lastReadTimestamp
        }
      };
      sendRequest(Constants.ContentType.CONVERSATION, request, getDefaultHandler(cb));
    };

    this.subscribePresence = function (userIdsList, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: subscribePresence...');

      var keysToOmitFromResponse = ['response.user.subscribePresence.states'];

      var request = {
        type: Constants.UserActionType.SUBSCRIBE_PRESENCE,
        subscribePresence: { userIds: userIdsList }
      };
      sendRequest(
        Constants.ContentType.USER,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            // The subscribe request also returns the presence state in the response
            var presenceStates = rsp.user && rsp.user.subscribePresence && rsp.user.subscribePresence.states;
            cb(null, presenceStates);
          }
        },
        null,
        keysToOmitFromResponse
      );
    };

    this.unsubscribePresence = function (userIdsList, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: unsubscribePresence...');
      var request = {
        type: Constants.UserActionType.UNSUBSCRIBE_PRESENCE,
        unsubscribePresence: { userIds: userIdsList }
      };
      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.subscribeTenantPresence = function (tenantId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: subscribeTenantPresence...');

      var request = {
        type: Constants.UserActionType.SUBSCRIBE_TENANT_PRESENCE,
        subscribeTenantPresence: { tenantId: tenantId }
      };
      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.unsubscribeTenantPresence = function (tenantId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: unsubscribeTenantPresence...');

      var request = {
        type: Constants.UserActionType.UNSUBSCRIBE_TENANT_PRESENCE,
        unsubscribeTenantPresence: { tenantId: tenantId }
      };
      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.updateStatusSubscription = function (userIdsList, subscribe, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: updateStatusSubscription - subscribe = ', subscribe);

      var request;
      if (subscribe) {
        request = {
          type: Constants.UserActionType.SUBSCRIBE_PRESENCE,
          subscribePresence: {
            userIds: userIdsList,
            notificationSubscriptionType: Constants.NotificationSubscriptionType.ONLINE_STATUS
          }
        };
      } else {
        request = {
          type: Constants.UserActionType.UNSUBSCRIBE_PRESENCE,
          unsubscribePresence: {
            userIds: userIdsList,
            notificationSubscriptionType: Constants.NotificationSubscriptionType.ONLINE_STATUS
          }
        };
      }

      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.changePassword = function (oldPassword, newPassword, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: changePassword...');
      var request = {
        type: Constants.UserActionType.CHANGE_PASSWORD,
        changePassword: {
          oldPassword: oldPassword,
          newPassword: newPassword
        }
      };

      var keysToOmitFromRequest = ['request.user.changePassword.oldPassword', 'request.user.changePassword.newPassword'];

      sendRequest(
        Constants.ContentType.USER,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.user.changePasswordResult.changeResult);
          }
        },
        keysToOmitFromRequest
      );
    };

    /////////////////////////////////////////////////////////////////////////////
    // FRN8499 - Email Change
    /////////////////////////////////////////////////////////////////////////////
    this.requestEmailChange = function (userId, newEmail, cb) {
      logger.debug('[ClientApiHandler]: requestEmailChange');
      var request = {
        type: Constants.UserActionType.GENERATE_EMAIL_UPDATE_TOKEN,
        generateEmailUpdateToken: {
          userId: userId,
          emailAddress: newEmail
        }
      };

      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.getUsersByIds = function (userIds, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getUsersByIds...');
      var request = {
        type: Constants.UserActionType.GET_USERS_BY_IDS,
        usersByIds: {
          userIds: userIds,
          complete: true
        }
      };

      var keysToOmitFromResponse = ['response.user.usersByIds.user'];

      sendRequest(
        Constants.ContentType.USER,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb, null)) {
            cb(null, rsp.user.usersByIds.user);
          }
        },
        null,
        keysToOmitFromResponse
      );
    };

    this.getPartialUsersByIds = function (userIds, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getPartialUsersByIds...');
      var request = {
        type: Constants.UserActionType.GET_USERS_BY_IDS,
        usersByIds: {
          userIds: userIds,
          complete: false
        }
      };

      sendRequest(Constants.ContentType.USER, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.user.usersByIds.user);
        }
      });
    };

    this.getContacts = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getContacts...');

      var request = {
        type: Constants.ContactActionType.GET_CONTACTS
      };
      sendRequest(Constants.ContentType.CONTACT, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.contact.getContacts.contacts);
        }
      });
    };

    this.getContactsByIds = function (contactIds, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getContactsByIds...');
      var request = {
        type: Constants.ContactActionType.GET_CONTACTS_BY_IDS,
        contactsByIds: {
          contactIds: contactIds
        }
      };

      var keysToOmitFromResponse = ['response.contact.contactsByIds.contact'];

      sendRequest(
        Constants.ContentType.CONTACT,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb, null)) {
            cb(null, rsp.contact.contactsByIds.contact);
          }
        },
        null,
        keysToOmitFromResponse
      );
    };

    this.getFavorites = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getFavorites...');

      var request = {
        type: Constants.ContactActionType.GET_FAVORITES
      };
      sendRequest(Constants.ContentType.CONTACT, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.contact.getFavorites.favorites);
        }
      });
    };

    this.addFavorite = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: addFavorite...');

      if (!data) {
        logger.warn('[ClientApiHandler]: No data were provided');
        sendAsyncResp(cb, Constants.ReturnCode.INVALID_MESSAGE);
        return;
      }

      var request = {
        type: Constants.ContactActionType.ADD_FAVORITE,
        data: data
      };
      sendRequest(Constants.ContentType.CONTACT, request, getDefaultHandler(cb));
    };

    this.removeFavorite = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: removeFavorite...');

      if (!data) {
        logger.warn('[ClientApiHandler]: No data were provided');
        sendAsyncResp(cb, Constants.ReturnCode.INVALID_MESSAGE);
        return;
      }

      var request = {
        type: Constants.ContactActionType.REMOVE_FAVORITE,
        data: data
      };
      sendRequest(Constants.ContentType.CONTACT, request, getDefaultHandler(cb));
    };

    this.createContact = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: createContact...');

      if (!data) {
        logger.warn('[ClientApiHandler]: No data were provided');
        sendAsyncResp(cb, Constants.ReturnCode.INVALID_MESSAGE);
        return;
      }

      var request = {
        type: Constants.ContactActionType.CREATE_CONTACT,
        data: data
      };
      sendRequest(Constants.ContentType.CONTACT, request, getDefaultHandler(cb));
    };

    this.deleteContact = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: deleteContact...');

      if (!data) {
        logger.warn('[ClientApiHandler]: No data were provided');
        sendAsyncResp(cb, Constants.ReturnCode.INVALID_MESSAGE);
        return;
      }

      var request = {
        type: Constants.ContactActionType.DELETE_CONTACT,
        data: data
      };
      sendRequest(Constants.ContentType.CONTACT, request, getDefaultHandler(cb));
    };

    this.deleteContactDataByOriginType = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: deleteContactDataByOriginType...');

      if (!data) {
        logger.warn('[ClientApiHandler]: No data were provided');
        sendAsyncResp(cb, Constants.ReturnCode.INVALID_MESSAGE);
        return;
      }

      var request = {
        type: Constants.ContactActionType.DELETE_CONTACTS_FAVORITES_BY_ORIGIN_TYPE,
        data: data
      };
      sendRequest(Constants.ContentType.CONTACT, request, getDefaultHandler(cb));
    };

    this.renewToken = function (cb) {
      cb =
        cb ||
        function (err) {
          if (err) {
            logger.error('[ClientApiHandler]: Error renewing token. This client will be automatically logged out', err);
          } else {
            logger.debug('[ClientApiHandler]: Successfully renewed token.');
          }
        };
      logger.debug('[ClientApiHandler]: renewToken...');
      var request = {
        type: Constants.UserActionType.RENEW_TOKEN
      };
      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.getUserSettings = function (areas, cb) {
      cb = cb || NOP;
      areas = areas || Constants.UserSettingArea.ALL;
      logger.debug('[ClientApiHandler]: getUserSettings...');
      var request = {
        type: Constants.UserActionType.GET_USER_SETTINGS,
        getUserSettings: {
          areas: areas
        }
      };
      sendRequest(Constants.ContentType.USER, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.user.getUserSettings.settings);
        }
      });
    };

    this.setUserSettings = function (settings, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: setUserSettings...');

      settings = getArrayValue(settings);
      if (!settings) {
        logger.warn('[ClientApiHandler]: No settings were provided');
        sendAsyncResp(cb, Constants.ReturnCode.INVALID_MESSAGE);
        return;
      }

      var request = {
        type: Constants.UserActionType.SET_USER_SETTINGS,
        setUserSettings: {
          settings: settings
        }
      };
      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.getUserByEmail = function (emailAddress, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getUserByEmail...');

      if (!emailAddress || typeof emailAddress !== 'string') {
        logger.warn('[ClientApiHandler]: No emailAddress was provided');
        sendAsyncResp(cb, Constants.ReturnCode.INVALID_MESSAGE);
        return;
      }

      var request = {
        type: Constants.UserActionType.GET_USER_BY_MAIL,
        getUserByMail: {
          emailAddress: emailAddress,
          excludeRoles: [Constants.UserRole.SUPPORT, Constants.UserRole.SYSTEM_ADMIN]
        }
      };
      sendRequest(Constants.ContentType.USER, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.user.getUserByMail.user);
        }
      });
    };

    this.getUserByEmail2 = function (filter, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getUserByEmail2...');

      if (!filter || typeof filter !== 'object') {
        logger.warn('[ClientApiHandler]: No filter was provided');
        sendAsyncResp(cb, Constants.ReturnCode.INVALID_MESSAGE);
        return;
      }

      var request = {
        type: Constants.UserActionType.GET_USER_BY_MAIL,
        getUserByMail: {
          emailAddress: filter.emailAddress,
          excludeRoles: filter.excludeRoles || [Constants.UserRole.SUPPORT, Constants.UserRole.SYSTEM_ADMIN],
          complete: getBooleanValue(filter.complete),
          secondaryLookup: getBooleanValue(filter.secondaryLookup)
        }
      };
      sendRequest(Constants.ContentType.USER, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.user.getUserByMail.user);
        }
      });
    };

    /**
     *  Retrieves users with email addresses passed as parameter
     *
     *  @param {Object} filter
     *  @param {string[]} filter.emailAddresses - Array of email addresses to search for
     *  @param {boolean} [filter.xmppLookup] - Set to true to include XMPP users in the result
     *  @param {boolean} [filter.complete] - Set to false to return limited set of attributes
     *  @param {boolean} [filter.secondaryLookup] - Set to true to also lookup by alternate emails
     *  @param {Function} cb Callback returning error and user parameters
     */
    this.getUsersByEmails = function (filter, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getUsersByEmails...');

      var request = {
        type: Constants.UserActionType.GET_USERS_BY_MAILS,
        getUsersByMails: {
          emailAddresses: filter.emailAddresses,
          xmppLookup: getBooleanValue(filter.xmppLookup),
          complete: getBooleanValue(filter.complete),
          secondaryLookup: getBooleanValue(filter.secondaryLookup),
          excludeRoles: [Constants.UserRole.SUPPORT, Constants.UserRole.SYSTEM_ADMIN]
        }
      };
      sendRequest(Constants.ContentType.USER, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb, true)) {
          cb(null, rsp.user.getUsersByMails.users);
        }
      });
    };

    this.changeUserEmail = function (userId, emailAddress, cb, tenantContext) {
      cb = cb || NOP;
      var request = {
        type: Constants.UserActionType.EMAIL_UPDATE,
        emailUpdate: {
          userId: userId,
          emailAddress: emailAddress
        }
      };
      sendRequest(
        Constants.ContentType.USER,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.user.emailUpdate);
          }
        },
        null,
        null,
        tenantContext
      );
    };

    this.goToSleep = function (userId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: goToSleep...');

      var request = {
        type: Constants.UserActionType.GO_TO_SLEEP,
        userId: userId
      };
      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.wakeUp = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: wakeUp...');

      var request = {
        type: Constants.UserActionType.WAKE_UP
      };
      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.checkConnectivity = function (data, cb) {
      cb = cb || NOP;
      var timeout = (data && data.timeout) || null;
      logger.debug('[ClientApiHandler]: checkConnectivity (timeout = ' + timeout + ')...');

      var request = {
        type: Constants.UserActionType.CHECK_CONNECTIVITY
      };
      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb), null, null, null, timeout);
    };

    this.getDevices = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getDevices...');

      var request = {
        type: Constants.UserActionType.GET_DEVICES
      };
      sendRequest(Constants.ContentType.USER, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.user.getDevices.devices);
        }
      });
    };

    this.getTelephonyData = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getTelephonyData...');

      var request = {
        type: Constants.UserActionType.GET_TELEPHONY_DATA
      };

      sendRequest(Constants.ContentType.USER, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.user.getTelephonyData.data);
        }
      });
    };

    this.removeAlternativeNumberHandler = function (alternativeNumber, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: removeAlternativeNumberHandler...');

      var request = {
        type: Constants.AccountActionType.REMOVE_ALTERNATIVE_NUMBER,
        alternativeNumberToDelete: alternativeNumber
      };

      sendRequest(Constants.ContentType.ACCOUNT, request, getDefaultHandler(cb));
    };

    this.revokeAllManagedDevices = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: revokeAllManagedDevices...');

      var request = {
        type: Constants.UserActionType.REVOKE_MANAGED_DEVICES
      };

      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.getSecurityTokenInfo = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getSecurityTokenInfo...');

      var request = {
        type: Constants.UserActionType.GET_SECURITY_TOKEN_INFO
      };

      sendRequest(Constants.ContentType.USER, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb, true)) {
          cb(null, rsp.user.getSecurityTokenInfoResult.securityTokenInfo);
        }
      });
    };

    this.revokeSecurityToken = function (tokenIdHash, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: revokeSecurityToken...');

      var request = {
        type: Constants.UserActionType.REVOKE_SECURITY_TOKEN,
        revokeSecurityToken: {
          tokenIdHash: tokenIdHash
        }
      };

      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.getUserTenantSettings = function (cb, tenantContext) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getUserTenantSettings...');

      var request = {
        type: Constants.UserActionType.GET_TENANT_SETTINGS
      };

      sendRequest(
        Constants.ContentType.USER,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb, true)) {
            cb(null, rsp.user.getTenantSettings.tenantSettings);
          }
        },
        null,
        null,
        tenantContext
      );
    };

    this.resetOpenScapeDevicePins = function (unifyPhoneUserId, directoryNumber, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: resetOpenScapeDevicePins...');

      var request = {
        type: Constants.UserActionType.RESET_OPENSCAPE_DEVICE_PINS,
        resetOpenScapeDevicePins: {
          unifyPhoneUserId: unifyPhoneUserId,
          directoryNumber: directoryNumber
        }
      };

      var keysToOmitFromResponse = ['response.user.resetOpenScapeDevicePins.openScapeDevicePins'];

      sendRequest(
        Constants.ContentType.USER,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.user.resetOpenScapeDevicePins.openScapeDevicePins);
          }
        },
        null,
        keysToOmitFromResponse
      );
    };

    this.setClientCapabilities = function (capabilities, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: setClientCapabilities...');

      var request = {
        type: Constants.UserActionType.SET_CLIENT_CAPABILITIES,
        setClientCapabilities: {
          capabilities: capabilities
        }
      };

      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.getTenantIdToNameMap = function (secIds, cb, tenantContext) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getTenantIdToNameMap...');

      var request = {
        type: Constants.UserActionType.GET_TENANT_ID_TO_NAME_MAP,
        getTenantIdToNameMap: {
          secIds: secIds
        }
      };

      sendRequest(
        Constants.ContentType.USER,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb, true)) {
            cb(null, rsp.user.getTenantIdToNameMapResult.tenantIdToNameMap || []);
          }
        },
        null,
        null,
        tenantContext
      );
    };

    this.getRecentUsedCMRs = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getRecentUsedCMRs...');

      var request = {
        type: Constants.UserActionType.GET_RECENT_USED_CMRS
      };

      sendRequest(Constants.ContentType.USER, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb, true)) {
          cb(null, rsp.user.getRecentUsedCMRsResult.cmrUser || []);
        }
      });
    };

    this.addRecentUsedCMR = function (cmrUserId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: addRecentUsedCMR...');

      var request = {
        type: Constants.UserActionType.ADD_RECENT_USED_CMR,
        addRecentUsedCMR: { cmrUserId: cmrUserId }
      };

      sendRequest(Constants.ContentType.USER, request, getDefaultHandler(cb));
    };

    this.isAllowedToAddCmr = function (inviterUserId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: isAllowedToAddCmr...');

      var request = {
        type: Constants.UserActionType.IS_ALLOWED_TO_ADD_CMR,
        isAllowedToAddCMR: {
          inviterUserId: inviterUserId
        }
      };

      sendRequest(Constants.ContentType.USER, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, !!rsp.user.isAllowedToAddCmrResult.isAllowed);
        }
      });
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Public Search related interfaces
    ///////////////////////////////////////////////////////////////////////////////
    this.cancelSearch = function (searchId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: cancelSearch...');
      var request = {
        type: Constants.SearchActionType.CANCEL_SEARCH,
        cancelSearch: {
          searchId: searchId
        }
      };
      sendRequest(Constants.ContentType.SEARCH, request, getDefaultHandler(cb));
    };

    this.startUserSearch = function (constraints, cb) {
      cb = cb || NOP;
      var request = {
        type: Constants.SearchActionType.START_USER_SEARCH,
        startUserSearch: {
          query: constraints.query,
          reversePhoneNumberLookup: !!constraints.reversePhoneNumberLookup,
          skipExternalDirectorySearch: !!constraints.reversePhoneNumberLookup
        }
      };
      sendRequest(Constants.ContentType.SEARCH, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.search.startSearchResult.searchId);
        }
      });
    };

    this.startAdvancedUserSearch = function (constraints, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: startAdvancedUserSearch...');

      var request = {
        type: Constants.SearchActionType.START_ADVANCED_USER_SEARCH,
        startAdvancedUserSearch: {
          searchContext: constraints.searchContext,
          query: constraints.query,
          searchExactAssignedPhoneNumber: constraints.searchExactAssignedPhoneNumber,
          resultSetLimit: constraints.resultSetLimit
        }
      };
      sendRequest(Constants.ContentType.SEARCH, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.search.startSearchResult.searchId);
        }
      });
    };

    this.searchConversationParticipants = function (displayName, convId, cb) {
      cb = cb || NOP;
      var request = {
        type: Constants.SearchActionType.SEARCH_CONVERSATION_PARTICIPANTS,
        searchConversationParticipants: { displayName: displayName, convId: convId }
      };
      sendRequest(Constants.ContentType.SEARCH, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb, true)) {
          cb(null, rsp.search.searchConversationParticipantsResult.participants || []);
        }
      });
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Public Conversation related interfaces
    ///////////////////////////////////////////////////////////////////////////////
    this.updateRtcItemAttachments = function (rtcItemId, attachments, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: updateRtcItemAttachments...');

      var request = {
        type: Constants.ConversationActionType.UPDATE_RTC_ITEM_ATTACHMENTS,
        updateRtcItemAttachments: {
          itemId: rtcItemId,
          attachments: getAttachmentsData(attachments)
        }
      };

      sendRequest(Constants.ContentType.CONVERSATION, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.conversation.updateRtcItemAttachmentsResult.item);
        }
      });
    };

    /**
     * Get telephony conversation ID on the server. Empty conversation ID will be returned if telephony conversation has not been created.
     *
     * @param {function} cb Callback function which will be invoked with telephony conversation ID retrieved from server.
     */
    this.getTelephonyConversationId = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getTelephonyConversationId...');
      var request = {
        type: Constants.ConversationActionType.GET_TELEPHONY_CONVERSATION_ID
      };

      sendRequest(Constants.ContentType.CONVERSATION, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.conversation.getTelephonyConversationId.conversationId);
        }
      });
    };

    this.addJournalEntry = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: addJournalEntry...');

      var request = {
        type: Constants.ConversationActionType.ADD_JOURNAL_ENTRY,
        addJournalEntry: {
          convId: data.convId,
          starter: data.starter,
          source: data.source,
          destination: data.destination,
          startTime: data.startTime,
          duration: data.duration,
          type: data.type,
          participants: data.participants,
          missedReason: data.missedReason
        }
      };
      sendRequest(Constants.ContentType.CONVERSATION, request, getDefaultHandler(cb));
    };

    this.setFavoritePosition = function (favoriteId, position, cb) {
      cb = cb || NOP;

      logger.debug('[ClientApiHandler]: setFavoritePosition of favoriteId:', favoriteId, 'to position:', position);

      var request = {
        type: Constants.ContactActionType.SET_FAVORITE_POSITION,
        setFavoritePosition: {
          favoriteId: favoriteId,
          position: position
        }
      };

      sendRequest(Constants.ContentType.CONTACT, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp);
        }
      });
    };

    this.deleteCallLog = function (itemId, cb) {
      var request = {
        type: Constants.ConversationActionType.DELETE_JOURNAL_ENTRY,
        deleteJournalEntry: {
          journalEntryId: itemId
        }
      };
      sendRequest(Constants.ContentType.CONVERSATION, request, getDefaultHandler(cb));
    };

    this.getJournalEntries = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getJournalEntries...');

      var request = {
        type: Constants.ConversationActionType.GET_JOURNAL_ENTRIES,
        getJournalEntries: {
          telephonyConversationId: data.convId,
          timestamp: data.timestamp || undefined,
          direction: data.direction || undefined,
          numberOfEntries: data.numberOfEntries || undefined,
          journalFilter: data.journalFilter || undefined
        }
      };

      // Set keysToOmitFromResponse to obfuscate the required fields, but also to reduce
      // the amount of information that is logged.
      var keysToOmitFromResponse = [
        'response.conversation.getJournalEntriesResult.entries.[].rtc.participants',
        'response.conversation.getJournalEntriesResult.entries.[].rtc.rtcParticipants',
        'response.conversation.getJournalEntriesResult.entries.[].rtc.ended',
        'response.conversation.getJournalEntriesResult.entries.[].rtc.missed.pickingUpParticipant'
      ];

      sendRequest(
        Constants.ContentType.CONVERSATION,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb, true)) {
            cb(null, rsp.conversation.getJournalEntriesResult.entries);
          }
        },
        null,
        keysToOmitFromResponse
      );
    };

    this.setVoicemailAsHeard = function (itemId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: setVoicemailAsHeard...');

      var request = {
        type: Constants.ConversationActionType.SET_VOICEMAIL_HEARD,
        setVoicemailHeard: {
          journalEntryId: itemId
        }
      };
      sendRequest(Constants.ContentType.CONVERSATION, request, getDefaultHandler(cb));
    };

    this.getUnheardVoicemailsCount = function (convId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getUnheardVoicemailCount...');

      var request = {
        type: Constants.ConversationActionType.GET_UNHEARD_VOICEMAILS_COUNT,
        getUnheardVoicemailsCount: {
          conversationId: convId
        }
      };
      sendRequest(Constants.ContentType.CONVERSATION, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.conversation.getUnheardVoicemailsCountResult.unheardVoicemailsCount || 0);
        }
      });
    };

    this.setRecordingDeletionDelay = function (rtcItemId, extendedDelay, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: setRecordingDeletionDelay...');

      var request = {
        type: Constants.ConversationActionType.SET_RECORDING_DELETION_DELAY,
        setRecordingDeletionDelay: {
          itemId: rtcItemId,
          extendedDelay: !!extendedDelay
        }
      };
      sendRequest(Constants.ContentType.CONVERSATION, request, getDefaultHandler(cb));
    };

    this.deleteAllCallLogs = function (convId, journalFilter, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: deleteAllCallLogs...');

      var request = {
        type: Constants.ConversationActionType.DELETE_ALL_JOURNAL_ENTRIES,
        deleteAllJournalEntries: {
          telephonyConversationId: convId,
          journalFilter: journalFilter
        }
      };

      sendRequest(Constants.ContentType.CONVERSATION, request, getDefaultHandler(cb));
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Public CTI WebRTCCall related interfaces
    ///////////////////////////////////////////////////////////////////////////////
    this.ctiHoldCall = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: ctiHoldCall...');

      var call = data && data.call;
      if (!call) {
        cb('InvalidData');
        return;
      }

      var request = {
        type: Constants.CTICallActionType.HOLD,
        data: {
          callId: call.callId,
          activeClient: data.activeClient,
          cstaCallId: call.cstaCallId,
          hold: true
        }
      };
      sendRequest(Constants.ContentType.CTI_CALL, request, getDefaultHandler(cb));
    };

    this.ctiRetrieveCall = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: ctiRetrieveCall...');

      var call = data && data.call;
      if (!call) {
        cb('InvalidData');
        return;
      }

      var request = {
        type: Constants.CTICallActionType.RETRIEVE,
        data: {
          callId: call.callId,
          activeClient: data.activeClient,
          cstaCallId: call.cstaCallId,
          retrieve: true
        }
      };
      sendRequest(Constants.ContentType.CTI_CALL, request, getDefaultHandler(cb));
    };

    this.ctiMergeCall = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: ctiMergeCall...');

      var call = data && data.call;
      if (!call) {
        cb('InvalidData');
        return;
      }

      var request = {
        type: Constants.CTICallActionType.MERGE,
        data: {
          callId: call.callId,
          activeClient: data.activeClient,
          cstaCallId: call.cstaCallId,
          merge: true
        }
      };
      sendRequest(Constants.ContentType.CTI_CALL, request, getDefaultHandler(cb));
    };

    this.ctiMuteCall = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: ctiMuteCall...');
      var call = data && data.call;
      if (!call) {
        cb('InvalidData');
        return;
      }

      var request = {
        type: Constants.CTICallActionType.MUTE,
        data: {
          callId: call.callId,
          activeClient: data.activeClient,
          cstaCallId: call.cstaCallId,
          mute: true
        }
      };
      sendRequest(Constants.ContentType.CTI_CALL, request, getDefaultHandler(cb));
    };

    this.ctiSwapCall = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: ctiSwapCall...');

      var call = data && data.call;
      if (!call) {
        cb('InvalidData');
        return;
      }
      var request = {
        type: Constants.CTICallActionType.SWAP,
        data: {
          callId: call.callId,
          activeClient: data.activeClient,
          cstaCallId: call.cstaCallId,
          swap: true
        }
      };
      sendRequest(Constants.ContentType.CTI_CALL, request, getDefaultHandler(cb));
    };

    this.ctiConsultCall = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: ctiConsultCall...');

      var call = data && data.call;
      if (!call) {
        cb('InvalidData');
        return;
      }
      var request = {
        type: Constants.CTICallActionType.CONSULT,
        data: {
          callId: call.callId,
          activeClient: data.activeClient,
          cstaCallId: call.cstaCallId,
          deviceName: data.deviceName,
          number: data.number,
          consult: true
        }
      };
      sendRequest(Constants.ContentType.CTI_CALL, request, getDefaultHandler(cb));
    };

    this.ctiTransferCall = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: ctiTransferCall...');

      var call = data && data.call;
      if (!call) {
        cb('InvalidData');
        return;
      }
      var request = {
        type: Constants.CTICallActionType.TRANSFER,
        data: {
          callId: call.callId,
          activeClient: data.activeClient,
          cstaCallId: call.cstaCallId,
          number: data.number || undefined,
          transfer: true
        }
      };
      sendRequest(Constants.ContentType.CTI_CALL, request, getDefaultHandler(cb));
    };

    this.ctiDtmfCall = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: ctiDtmfCall...');

      var call = data && data.call;
      if (!call) {
        cb('InvalidData');
        return;
      }
      var request = {
        type: Constants.CTICallActionType.DTMF,
        data: {
          callId: call.callId,
          activeClient: data.activeClient,
          cstaCallId: call.cstaCallId,
          digits: data.digits,
          dtmf: true
        }
      };
      sendRequest(Constants.ContentType.CTI_CALL, request, getDefaultHandler(cb));
    };

    this.ctiDial = function (deviceName, number, name, userId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: ctiDial...');

      var request = {
        type: Constants.CTICallActionType.DIAL_ON_OTHER,
        data: {
          deviceName: deviceName,
          number: number,
          name: name,
          userId: userId,
          dial: true
        }
      };
      sendRequest(Constants.ContentType.CTI_CALL, request, getDefaultHandler(cb));
    };

    this.ctiAnswerCall = function (callId, deviceName, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: ctiAnswerCall...');

      var request = {
        type: Constants.CTICallActionType.ANSWER_ON_OTHER,
        data: {
          callId: callId,
          deviceName: deviceName,
          answer: true
        }
      };
      sendRequest(Constants.ContentType.CTI_CALL, request, getDefaultHandler(cb));
    };

    this.ctiHangupCall = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: ctiHangupCall...');

      var call = data && data.call;
      if (!call) {
        cb('InvalidData');
        return;
      }

      var request = {
        type: Constants.CTICallActionType.HANGUP,
        data: {
          callId: call.callId,
          activeClient: data.activeClient,
          cstaCallId: data.cstaCallId,
          hangup: true
        }
      };
      sendRequest(Constants.ContentType.CTI_CALL, request, getDefaultHandler(cb));
    };

    this.ctiPushCall = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: ctiPushCall...');

      var call = data && data.call;
      if (!call) {
        cb('InvalidData');
        return;
      }
      var request = {
        type: Constants.CTICallActionType.PUSH_WEBRTC_CALL,
        data: {
          callId: call.callId,
          activeClient: call.activeClient,
          cstaCallId: call.cstaCallId,
          deviceName: data.deviceName,
          push: true
        }
      };
      sendRequest(Constants.ContentType.CTI_CALL, request, getDefaultHandler(cb));
    };

    this.ctiSendResponse = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: ctiSendResponse...');

      var request = {
        type: Constants.CTICallActionType.SEND_RESPONSE,
        data: {
          err: data.err,
          ctiReqId: data.ctiReqId,
          initClientId: data.initClientId
        }
      };
      sendRequest(Constants.ContentType.CTI_CALL, request, getDefaultHandler(cb));
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Public RTCCall related interfaces
    ///////////////////////////////////////////////////////////////////////////////
    this.joinRtcCall = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: joinRtcCall...');

      var request = {
        type: Constants.RTCCallActionType.JOIN,
        join: createJoinData(data)
      };

      sendRequest(Constants.ContentType.RTC_CALL, request, getDefaultHandler(cb));
    };

    this.answerRtcCall = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: answerRtcCall...');

      var request = {
        type: Constants.RTCCallActionType.ANSWER,
        answer: createJoinData(data)
      };

      sendRequest(Constants.ContentType.RTC_CALL, request, getDefaultHandler(cb));
    };

    this.sendIceCandidates = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: sendIceCandidates...');

      var request = {
        type: Constants.RTCCallActionType.ICE_CANDIDATES,
        iceCandidates: {
          rtcSessionId: data.rtcSessionId,
          userId: data.userId,
          origin: data.origin,
          candidates: data.candidates
        }
      };

      sendRequest(Constants.ContentType.RTC_CALL, request, getDefaultHandler(cb));
    };

    this.leaveRtcCall = function (rtcSessionId, disconnectCause, cb) {
      disconnectCause = disconnectCause || {};
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: leaveRtcCall...');
      var request = {
        type: Constants.RTCCallActionType.LEAVE,
        leave: {
          rtcSessionId: rtcSessionId,
          disconnectCause: disconnectCause.cause,
          disconnectReason: disconnectCause.reason
        }
      };
      sendRequest(Constants.ContentType.RTC_CALL, request, getDefaultHandler(cb));
    };

    this.terminateRtcCall = function (rtcSessionId, disconnectCause, cb, pickupUserId) {
      disconnectCause = disconnectCause || {};
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: terminateRtcCall...');
      var request = {
        type: Constants.RTCCallActionType.TERMINATE,
        terminate: {
          rtcSessionId: rtcSessionId,
          disconnectCause: disconnectCause.cause,
          disconnectReason: disconnectCause.reason,
          pickUpUser: pickupUserId || undefined,
          cause: pickupUserId ? Constants.TerminateCause.PICKED_UP : undefined
        }
      };
      sendRequest(Constants.ContentType.RTC_CALL, request, getDefaultHandler(cb));
    };

    this.declineRtcCall = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: declineRtcCall...');
      var request = {
        type: Constants.RTCCallActionType.INVITE_REJECT,
        inviteReject: {
          convId: data.convId,
          rtcSessionId: data.rtcSessionId,
          cause: data.cause,
          transactionId: data.transactionId
        }
      };
      sendRequest(Constants.ContentType.RTC_CALL, request, getDefaultHandler(cb));
    };

    this.changeMediaType = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: changeMediaType...');

      var request = {
        type: Constants.RTCCallActionType.CHANGE_MEDIA_TYPE,
        changeMediaType: {
          rtcSessionId: data.rtcSessionId,
          sdp: data.sdp,
          mediaTypes: convertMediaType(data.mediaType),
          transactionId: data.transactionId,
          screenSharePointerSupported: data.screenSharePointerSupported,
          hosted: data.hosted,
          replaces: data.replaces,
          rtcSupportedFeatures: data.rtcSupportedFeatures
        }
      };

      sendRequest(Constants.ContentType.RTC_CALL, request, getDefaultHandler(cb));
    };

    this.changeMediaAccept = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: changeMediaAccept...');
      var request = {
        type: Constants.RTCCallActionType.CHANGE_MEDIA_ACCEPT,
        changeMediaAccept: {
          rtcSessionId: data.rtcSessionId,
          sdp: data.sdp,
          transactionId: data.transactionId
        }
      };
      sendRequest(Constants.ContentType.RTC_CALL, request, getDefaultHandler(cb));
    };

    this.changeMediaReject = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: changeMediaReject...');
      var request = {
        type: Constants.RTCCallActionType.CHANGE_MEDIA_REJECT,
        changeMediaReject: {
          rtcSessionId: data.rtcSessionId,
          transactionId: data.transactionId
        }
      };
      sendRequest(Constants.ContentType.RTC_CALL, request, getDefaultHandler(cb));
    };

    this.prepareSession = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: prepareSession... ');
      var request = {
        type: Constants.RTCCallActionType.PREPARE,
        prepare: {
          convId: data.convId,
          rtcSessionId: data.rtcSessionId,
          ownerId: data.ownerId,
          mediaNode: data.mediaNode,
          desiredRegion: data.desiredRegion || '',
          isTelephonyConversation: data.isTelephonyConversation,
          replaces: data.replaces
        }
      };

      var keysToOmitFromResponse = ['response.rtcCall.prepare.servers.[].username', 'response.rtcCall.prepare.servers.[].password'];

      sendRequest(
        Constants.ContentType.RTC_CALL,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.rtcCall.prepare.servers, rsp.rtcCall.prepare.newRtcSessionId);
          }
        },
        null,
        keysToOmitFromResponse
      );
    };

    this.renewTurnCredentials = function (rtcSessionId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: renewTurnCredentials... ');
      var request = {
        type: Constants.RTCCallActionType.RENEW_TURN_CREDENTIALS,
        renewTurnCredentials: {
          rtcSessionId: rtcSessionId
        }
      };

      var keysToOmitFromResponse = ['response.rtcCall.prepare.servers.[].username', 'response.rtcCall.prepare.servers.[].password'];

      sendRequest(
        Constants.ContentType.RTC_CALL,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.rtcCall.renewTurnCredentials.servers);
          }
        },
        null,
        keysToOmitFromResponse
      );
    };

    this.sendProgress = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: sendProgress... ');
      var request = {
        type: Constants.RTCCallActionType.SEND_PROGRESS,
        sendProgress: {
          rtcSessionId: data.rtcSessionId,
          userId: data.localUserId,
          invitingUser: data.invitingUserId || '',
          type: data.type || Constants.RTCProgressType.ALERTING,
          sdp: data.sdp || undefined
        }
      };
      sendRequest(Constants.ContentType.RTC_CALL, request, getDefaultHandler(cb));
    };

    this.submitCallQualityRating = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: submitCallQualityRating...');

      var request = {
        type: Constants.RTCCallActionType.SUBMIT_RTC_QUALITY_RATING,
        submitRtcQualityRating: {
          rating: data
        }
      };
      sendRequest(Constants.ContentType.RTC_CALL, request, getDefaultHandler(cb));
    };

    this.mergeRtcCalls = function (callId1, callId2, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: mergeRtcCalls ...');

      var request = {
        type: Constants.RTCCallActionType.MERGE,
        merge: {
          sourceRtcSessionId: callId1,
          targetRtcSessionId: callId2
        }
      };
      sendRequest(Constants.ContentType.RTC_CALL, request, getDefaultHandler(cb));
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Public RTCSession related interfaces
    ///////////////////////////////////////////////////////////////////////////////
    this.lockRtcSession = function (rtcSessionId, locked, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: lockRtcSession...');
      var request = {
        type: Constants.RTCSessionActionType.LOCK,
        lock: {
          rtcSessionId: rtcSessionId,
          locked: !!locked
        }
      };
      sendRequest(Constants.ContentType.RTC_SESSION, request, function (err, resp) {
        if (isResponseValid(err, resp, cb)) {
          cb(null);
        }
      });
    };

    this.muteRtcSession = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: muteRtcSession...');
      var request = {
        type: Constants.RTCSessionActionType.MUTE_SESSION,
        muteSession: {
          rtcSessionId: data.rtcSessionId,
          muted: !!data.muted,
          excludedUserIds: data.excludedUserIds || undefined
        }
      };
      sendRequest(Constants.ContentType.RTC_SESSION, request, getDefaultHandler(cb));
    };

    this.muteParticipant = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: muteParticipant...');
      var request = {
        type: Constants.RTCSessionActionType.MUTE,
        mute: {
          rtcSessionId: data.rtcSessionId,
          cstaCallId: data.cstaCallId,
          muted: !!data.muted,
          userIds: data.usersIds || undefined
        }
      };
      sendRequest(Constants.ContentType.RTC_SESSION, request, getDefaultHandler(cb));
    };

    this.addParticipantToRtcSession = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: addParticipantToRtcCall...');
      var request = {
        type: Constants.RTCSessionActionType.ADD_PARTICIPANT,
        addParticipant: {
          rtcSessionId: data.rtcSessionId,
          userId: data.userId,
          from: data.from,
          to: data.to,
          mediaType: convertMediaType(data.mediaType)
        }
      };
      sendRequest(Constants.ContentType.RTC_SESSION, request, getDefaultHandler(cb));
    };

    this.removeSessionParticipant = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: removeSessionParticipant...');
      var request = {
        type: Constants.RTCSessionActionType.REMOVE_PARTICIPANT,
        removeParticipant: {
          rtcSessionId: data.rtcSessionId,
          userId: data.userId
        }
      };
      sendRequest(Constants.ContentType.RTC_SESSION, request, getDefaultHandler(cb));
    };

    this.getActiveSessions = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getActiveSessions...');
      var request = {
        type: Constants.RTCSessionActionType.GET_ACTIVE_SESSIONS
      };
      sendRequest(Constants.ContentType.RTC_SESSION, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb, true)) {
          cb(null, rsp.rtcSession.activeSessions.session);
        }
      });
    };

    this.getSession = function (rtcSessionId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getSession...');
      var request = {
        type: Constants.RTCSessionActionType.GET_SESSION,
        getSession: {
          rtcSessionId: rtcSessionId
        }
      };
      sendRequest(Constants.ContentType.RTC_SESSION, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.rtcSession.getSession.session);
        }
      });
    };

    this.moveRtcSession = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: moveRtcSession...');
      var request = {
        type: Constants.RTCSessionActionType.MOVE,
        move: {
          oldRtcSessionId: data.sessionId,
          oldConversationId: data.conversationId,
          newConversationId: data.newConversationId,
          newRtcSessionId: data.newSessionId
        }
      };
      sendRequest(Constants.ContentType.RTC_SESSION, request, getDefaultHandler(cb));
    };

    this.startRecording = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: startRecording...');
      var request = {
        type: Constants.RTCSessionActionType.START_RECORDING,
        startRecording: {
          convId: data.convId,
          rtcSessionId: data.rtcSessionId,
          recordingMediaTypes: convertRecordingMediaType(data.recordingMediaTypes)
        }
      };

      if (data.videoLayout) {
        request.startRecording.layout = data.videoLayout;
      }

      sendRequest(Constants.ContentType.RTC_SESSION, request, getDefaultHandler(cb));
    };

    this.stopRecording = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: stopRecording...');
      var request = {
        type: Constants.RTCSessionActionType.STOP_RECORDING,
        stopRecording: {
          convId: data.convId,
          rtcSessionId: data.rtcSessionId,
          recordingMediaTypes: convertRecordingMediaType(data.recordingMediaTypes)
        }
      };

      sendRequest(Constants.ContentType.RTC_SESSION, request, getDefaultHandler(cb));
    };

    this.switchRecordingLayout = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: switchRecordingLayout...');

      var request = {
        type: Constants.RTCSessionActionType.SWITCH_RECORDING_LAYOUT,
        switchRecordingLayout: {
          rtcSessionId: data.rtcSessionId,
          layout: data.videoLayout
        }
      };
      sendRequest(Constants.ContentType.RTC_SESSION, request, getDefaultHandler(cb));
    };

    this.setVideoReceiverConfiguration = function (data, cb) {
      cb = cb || NOP;

      logger.debug('[ClientApiHandler]: setVideoReceiverConfiguration ...');
      var request = {
        type: Constants.RTCSessionActionType.SET_VIDEO_RECEIVER_CONFIGURATION,
        setVideoReceiverConfiguration: {
          rtcSessionId: data.rtcSessionId,
          configuration: data.configuration
        }
      };
      sendRequest(Constants.ContentType.RTC_SESSION, request, getDefaultHandler(cb));
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Get Node State API
    ///////////////////////////////////////////////////////////////////////////////
    this.getNodeState = function (data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getNodeState...');

      var request = {
        type: Constants.RTCSessionActionType.GET_NODE_STATE,
        getNodeState: data
      };
      sendRequest(Constants.ContentType.RTC_SESSION, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb, true)) {
          cb(null, rsp.rtcSession.getNodeState.nodeData);
        }
      });
    };

    this.sendClientInfo = function (clientInfo, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: sendClientInfo...');

      var request = {
        type: Constants.RTCSessionActionType.SEND_CLIENT_INFO,
        sendClientInfo: {
          clientInfo: clientInfo
        }
      };

      sendRequest(Constants.ContentType.RTC_SESSION, request, getDefaultHandler(cb));
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Public Instrumentation related Interfaces
    ///////////////////////////////////////////////////////////////////////////////
    this.submitClientData = function (instrumentationData, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: submitClientData...');
      var request = {
        type: Constants.InstrumentationActionType.SUBMIT_CLIENT_DATA,
        submitClientData: {
          instrumentationData: instrumentationData
        }
      };
      sendRequest(Constants.ContentType.INSTRUMENTATION, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          var res = rsp.instrumentation && rsp.instrumentation.submitClientData && rsp.instrumentation.submitClientData.submitDataResult;
          cb(null, res || Constants.SubmitDataResult.OK);
        }
      });
    };

    this.submitQOSData = function (qosData, streamQualityData, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: submitQOSData...');
      var request = {
        type: Constants.InstrumentationActionType.SUBMIT_QOS_DATA,
        submitQOSData: {
          clientTime: Date.now(), // Used by the Access Server to adjust the timestamps (if necessary)
          dataSets: qosData
        }
      };
      if (streamQualityData) {
        request.submitQOSData.clientStreamQuality = streamQualityData;
      }
      sendRequest(Constants.ContentType.INSTRUMENTATION, request, getDefaultHandler(cb));
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Public Third Party related Interfaces
    ///////////////////////////////////////////////////////////////////////////////
    this.getExchangeSettings = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getExchangeSettings...');

      var request = {
        type: Constants.ThirdPartyConnectorsActionType.GET_SETTINGS,
        getSettings: {
          type: Constants.ThirdPartyConnectorType.EXCHANGE_CONNECTOR
        }
      };

      // Omit all Exchnage info at logging, when we are in production mode
      var keysToOmitFromResponse = ['response.thirdpartyconnectors.getSettings.exchange'];

      sendRequest(
        Constants.ContentType.THIRDPARTYCONNECTORS,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.thirdpartyconnectors.getSettings.exchange);
          }
        },
        null,
        keysToOmitFromResponse
      );
    };

    this.saveExchangeSettings = function (settings, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: saveExchangeSettings...');
      var request = {
        type: Constants.ThirdPartyConnectorsActionType.SAVE_SETTINGS,
        saveSettings: {
          type: Constants.ThirdPartyConnectorType.EXCHANGE_CONNECTOR,
          exchange: settings
        }
      };

      // Omit all Exchnage info at logging, when we are in production mode
      var keysToOmitFromRequest = ['request.thirdpartyconnectors.saveSettings.exchange'];
      var keysToOmitFromResponse = ['response.thirdpartyconnectors.saveSettings.exchange'];

      sendRequest(
        Constants.ContentType.THIRDPARTYCONNECTORS,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.thirdpartyconnectors.saveSettings.exchange);
          }
        },
        keysToOmitFromRequest,
        keysToOmitFromResponse
      );
    };

    this.getConnectionStatus = function (provider, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getConnectionStatus ' + provider + '...');

      var request = {
        type: Constants.ThirdPartyConnectorsActionType.GET_CONNECTION_STATUS,
        getConnectionStatus: {
          type: provider
        }
      };

      sendRequest(Constants.ContentType.THIRDPARTYCONNECTORS, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.thirdpartyconnectors.getConnectionStatus.connected);
        }
      });
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Public System related Interfaces
    ///////////////////////////////////////////////////////////////////////////////
    this.getGlobalProperty = function (name, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getGlobalProperty...');

      var request = {
        type: Constants.SystemActionType.GET_GLOBAL_PROPERTY,
        getGlobalProperty: {
          globalPropertyName: name
        }
      };
      sendRequest(Constants.ContentType.SYSTEM, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.system.getGlobalPropertyResult.property);
        }
      });
    };

    this.getGlobalProperties = function (names, cb) {
      cb = cb || NOP;
      if (!(names instanceof Array)) {
        names = [names];
      }
      logger.debug('[ClientApiHandler]: getGlobalProperties...');

      var request = {
        type: Constants.SystemActionType.GET_GLOBAL_PROPERTIES,
        getGlobalProperties: {
          globalPropertyNames: names
        }
      };
      sendRequest(Constants.ContentType.SYSTEM, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.system.getGlobalPropertiesResult.property);
        }
      });
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Public Session Guest related interfaces
    ///////////////////////////////////////////////////////////////////////////////
    this.validateSessionInvite = function (token, textLanguage, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: validateSessionInvite...');
      var request = {
        type: Constants.GuestActionType.VALIDATE_SESSION_INVITE_TOKEN,
        validateSessionInviteToken: {
          token: { token: token },
          configurableTextsLanguage: textLanguage || undefined
        }
      };

      var keysToOmitFromResponse = ['response.guest.validateSessionInviteToken.convTopic'];

      sendRequest(
        Constants.ContentType.GUEST,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.guest.validateSessionInviteToken);
          }
        },
        null,
        keysToOmitFromResponse
      );
    };

    this.validateSessionPin = function (pin, textLanguage, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: validateSessionPin...');

      pin = pin && pin.replace(/[^\d]/g, '');
      if (!pin) {
        logger.warn('[ClientApiHandler]: Invalid PIN');
        sendAsyncResp(cb, Constants.ReturnCode.SERVICE_EXCEPTION);
        return;
      }

      var request = {
        type: Constants.GuestActionType.VALIDATE_SESSION_PIN,
        validateSessionPIN: {
          pin: pin,
          configurableTextLanguage: textLanguage || undefined
        }
      };

      var keysToOmitFromRequest = ['request.guest.validateSessionPIN.pin'];
      var keysToOmitFromResponse = ['response.guest.validateSessionInviteToken.convTopic'];

      sendRequest(
        Constants.ContentType.GUEST,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.guest.validateSessionInviteToken);
          }
        },
        keysToOmitFromRequest,
        keysToOmitFromResponse
      );
    };

    this.getRegions = function (token, cb) {
      getApiVersion(getRegions.bind(this, token, cb));
    };

    this.getJoiningInstructions = function (token, language, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getJoiningInstructions ...');

      var request = {
        type: Constants.GuestActionType.GET_JOINING_INSTRUCTIONS,
        getJoiningInstructions: {
          token: { token: token },
          language: language
        }
      };

      var keysToOmitFromResponse = ['response.guest.getJoiningInstructionsResult'];

      sendRequest(
        Constants.ContentType.GUEST,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.guest.getJoiningInstructionsResult);
          }
        },
        null,
        keysToOmitFromResponse
      );
    };

    this.registerSessionGuest = function (guestData, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: registerSessionGuest...');
      var request = {
        type: Constants.GuestActionType.REGISTER_SESSION_GUEST,
        registerSessionGuest: {
          token: { token: guestData.token },
          firstName: guestData.firstName,
          lastName: guestData.lastName,
          forTestCall: guestData.testCall,
          locale: guestData.locale,
          userId: _userId || undefined,
          // The following fields are used by the access server and are not
          // passed to the application node.
          oldClientId: _clientId || undefined,
          previousServer: _connectedBackend || undefined
        }
      };

      var keysToOmitFromResponse = ['response.guest.registerSessionGuestResult.guestContext'];

      sendRequest(
        Constants.ContentType.GUEST,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            var result = rsp.guest.registerSessionGuestResult;

            // Save the Client API version that we are connected to
            _clientApiVersion = Utils.convertVersionToNumber(result.apiVersion);
            _connectedBackend = result.connectedBackend;

            // Save the connected clientId and userId
            _clientId = result.clientId;
            _userId = result.userId;

            cb(null, result);
          }
        },
        null,
        keysToOmitFromResponse
      );
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Public User to User related interfaces
    ///////////////////////////////////////////////////////////////////////////////
    this.sendUserToUserRequest = function (type, data, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: sendUserToUserRequest, type: ' + type + ', data: ', data);

      var request = {
        type: Constants.ContentType.USER_TO_USER,
        userToUser: {
          type: type,
          routing: {
            destUserId: data.destUserId,
            srcUserId: _userId,
            destClientId: data.destClientId,
            srcClientId: _clientId
          },
          data: JSON.stringify(data.content)
        }
      };

      var keysToOmitFromRequest = ['request.userToUser.userToUser.data'];

      sendRequest(
        Constants.ContentType.USER_TO_USER,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.userToUser);
          }
        },
        keysToOmitFromRequest
      );
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Public Account related interfaces
    ///////////////////////////////////////////////////////////////////////////////
    this.getPackages = function (cb, tenantContext) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getPackages...');
      var request = {
        type: Constants.AccountActionType.GET_ASSIGNED_PACKAGES
      };
      sendRequest(
        Constants.ContentType.ACCOUNT,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.account.getAssignedPackages.packages || []);
          }
        },
        null,
        null,
        tenantContext
      );
    };

    /**
     * The replacement of getUsers
     * @param queryData
     * @param cb
     */
    this.getAccounts = function (queryData, cb, tenantContext) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getAccounts...');

      var request = {
        type: Constants.AccountActionType.GET_ACCOUNTS,
        getAccounts: {
          searchCriterias: queryData.searchCriterias,
          sorting: queryData.sorting,
          ordering: queryData.ordering,
          searchPointer: queryData.searchPointer,
          pageSize: queryData.pageSize,
          excludedTags: queryData.excludedTags
        }
      };

      var keysToOmitFromResponse = ['response.account.getAccounts.accounts'];

      sendRequest(
        Constants.ContentType.ACCOUNT,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.account.getAccounts);
          }
        },
        null,
        keysToOmitFromResponse,
        tenantContext
      );
    };

    /**
     * Get account by user id
     * @param user id
     * @param cb
     */
    this.getAccountByUserId = function (userId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getAccountByUserId...');

      var request = {
        type: Constants.AccountActionType.GET_ACCOUNT_BY_USER_ID,
        getAccountByUserId: {
          userId: userId
        }
      };

      sendRequest(Constants.ContentType.ACCOUNT, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.account.getAccountByUserId.account);
        }
      });
    };

    this.migrateAccount = function (params, cb, tenantContext) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: migrateAccount...');

      var request = {
        type: Constants.AccountActionType.MIGRATE_ACCOUNT,
        migrateAccount: {
          accountId: params.accountId,
          accountTemplateId: params.accountTemplateId
        }
      };

      sendRequest(
        Constants.ContentType.ACCOUNT,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.account.migrateAccount);
          }
        },
        null,
        null,
        tenantContext
      );
    };

    this.migrateMultipleUsers = function (params, cb, tenantContext) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: migrateMultipleUsers...');

      var request = {
        type: Constants.AccountActionType.MIGRATE_MULTIPLE_USERS,
        migrateMultipleUsers: {
          accountIds: params.accountIds,
          accountTemplateId: params.accountTemplateId
        }
      };
      sendRequest(
        Constants.ContentType.ACCOUNT,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp);
          }
        },
        null,
        null,
        tenantContext
      );
    };

    this.suspendAccount = function (accountId, cb, tenantContext) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: suspendAccount...');
      var request = {
        type: Constants.AccountActionType.SUSPEND_ACCOUNT,
        suspendAccount: {
          accountId: accountId
        }
      };
      sendRequest(Constants.ContentType.ACCOUNT, request, getDefaultHandler(cb), null, null, tenantContext);
    };

    this.unsuspendAccount = function (accountId, cb, tenantContext) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: unsuspendAccount...');
      var request = {
        type: Constants.AccountActionType.UNSUSPEND_ACCOUNT,
        unsuspendAccount: {
          accountId: accountId
        }
      };
      sendRequest(Constants.ContentType.ACCOUNT, request, getDefaultHandler(cb), null, null, tenantContext);
    };

    this.assignTelephonyConfiguration = function (accountId, configuration, cb, tenantContext) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: assignTelephonyConfiguration...');

      var request = {
        type: Constants.AccountActionType.ASSIGN_TELEPHONY_CONFIGURATION,
        assignTelephonyConfiguration: {
          accountId: accountId,
          configuration: {
            phoneNumber: Utils.cleanPhoneNumber(configuration.phoneNumber),
            callerId: Utils.cleanPhoneNumber(configuration.callerId),
            reroutingPhoneNumber: Utils.cleanPhoneNumber(configuration.reroutingPhoneNumber),
            onsSipAuthenticationHash: configuration.onsSipAuthenticationHash || '',
            ondSipAuthenticationHash: configuration.ondSipAuthenticationHash || '',
            associatedTelephonyUserID: configuration.associatedTelephonyUserID || '',
            associatedTelephonyTrunkGroupId: configuration.associatedTelephonyTrunkGroupId || '',
            associatedTelephonyUserType: configuration.associatedTelephonyUserType || undefined
          }
        }
      };

      var keysToOmitFromRequest = [
        'request.account.assignTelephonyConfiguration.configuration.onsSipAuthenticationHash',
        'request.account.assignTelephonyConfiguration.configuration.ondSipAuthenticationHash'
      ];

      sendRequest(Constants.ContentType.ACCOUNT, request, getDefaultHandler(cb), keysToOmitFromRequest, null, tenantContext);
    };

    this.renewAssociatedTelephonyUser = function (associatedTelephonyUserId, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: renewAssociatedTelephonyUser...');

      var request = {
        type: Constants.AccountActionType.RENEW_ASSOCIATED_TELEPHONY_USER,
        renewAssociatedTelephonyUser: {
          currentAssociatedTelephonyUserId: associatedTelephonyUserId
        }
      };

      sendRequest(Constants.ContentType.ACCOUNT, request, getDefaultHandler(cb));
    };

    this.getTechnicalAdminUserId = function (cb, tenantContext) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getTechnicalAdminUserId...');

      var request = {
        type: Constants.AccountActionType.GET_TECHNICAL_ADMIN_USER_ID
      };
      sendRequest(
        Constants.ContentType.ACCOUNT,
        request,
        function (err, rsp) {
          if (isResponseValid(err, rsp, cb)) {
            cb(null, rsp.account.getTechnicalAdminUserIdResult.technicalAdminUserId);
          }
        },
        null,
        null,
        tenantContext
      );
    };

    this.addAccountPermission = function (accountId, permissionName, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: addAccountPermission...');

      var request = {
        type: Constants.AccountActionType.ADD_ACCOUNT_PERMISSION,
        addAccountPermission: {
          accountId: accountId,
          permissionName: permissionName
        }
      };
      sendRequest(Constants.ContentType.ACCOUNT, request, getDefaultHandler(cb));
    };

    this.removeAccountPermission = function (accountId, permissionName, cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: removeAccountPermission...');

      var request = {
        type: Constants.AccountActionType.REMOVE_ACCOUNT_PERMISSION,
        removeAccountPermission: {
          accountId: accountId,
          permissionName: permissionName
        }
      };
      sendRequest(Constants.ContentType.ACCOUNT, request, getDefaultHandler(cb));
    };

    ///////////////////////////////////////////////////////////////////////////////
    // Public UserData request
    ///////////////////////////////////////////////////////////////////////////////
    this.getUserData = function (cb) {
      cb = cb || NOP;
      logger.debug('[ClientApiHandler]: getUserData...');

      var request = {
        type: Constants.UserDataActionType.GET_USER_DATA
      };
      sendRequest(Constants.ContentType.USER_DATA, request, function (err, rsp) {
        if (isResponseValid(err, rsp, cb)) {
          cb(null, rsp.userData.getUserData);
        }
      });
    };
  }

  ClientApiHandler.prototype.constructor = ClientApiHandler;
  ClientApiHandler.prototype.name = 'ClientApiHandler';

  // Exports
  unifyPhone.ClientApiHandler = ClientApiHandler;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  unifyPhone.ClientApiHandlerSingleton = (function () {
    var _clientApiHandler;
    return {
      getInstance: function () {
        if (!_clientApiHandler) {
          _clientApiHandler = new unifyPhone.ClientApiHandler();
        }
        return _clientApiHandler;
      }
    };
  })();

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var Constants = unifyPhone.Constants;
  var logger = unifyPhone.logger;
  var Utils = unifyPhone.Utils;

  ///////////////////////////////////////////////////////////////////////////////////////
  // Constants
  ///////////////////////////////////////////////////////////////////////////////////////
  var AtcMessage = Object.freeze({
    ADVANCING: 'ADVANCING',
    CSTA: 'CSTA',
    INFO: 'INFO',
    PRIMARYCLIENT: 'PRIMARYCLIENT',
    REGISTER: 'REGISTER',
    REGISTER_RESPONSE: 'REGISTER_RESPONSE',
    SETTINGS: 'SETTINGS',
    START_REASSIGNMENT: 'START_REASSIGNMENT',
    TALK: 'TALK',
    UNREGISTER: 'UNREGISTER',
    UNREGISTERED: 'UNREGISTERED',
    OS4K_CLASSIC_HUNT: 'OS4K_CLASSIC_HUNT',
    MUTE_ON_OTHER: 'MUTE_ON_OTHER'
  });

  var AtcInfoMessage = Object.freeze({
    GET_GTC_VERSION_REQUEST: 'GET_GTC_VERSION_REQUEST',
    SET_ROUTE_TO_DESK: 'SET_ROUTE_TO_DESK',
    ENABLE_PBX_CALL_LOG: 'ENABLE_PBX_CALL_LOG'
  });

  var StcMessage = Object.freeze({
    TRANSFER: 'TRANSFER'
  });

  var ContactCardMessage = Object.freeze({
    CLICK_TO_CALL_REQUEST: 'CLICK_TO_CALL_REQUEST',
    CLICK_TO_CALL_RESPONSE: 'CLICK_TO_CALL_RESPONSE',
    MUTE_DEVICE_REQUEST: 'MUTE_DEVICE_REQUEST',
    MUTE_DEVICE_RESPONSE: 'MUTE_DEVICE_RESPONSE',
    JOIN_CONFERENCE_REQUEST: 'JOIN_CONFERENCE_REQUEST',
    JOIN_CONFERENCE_RESPONSE: 'JOIN_CONFERENCE_RESPONSE',
    CONVERSATION_NAVIGATE_REQUEST: 'CONVERSATION_NAVIGATE_REQUEST',
    CONVERSATION_NAVIGATE_RESPONSE: 'CONVERSATION_NAVIGATE_RESPONSE',
    CLICK_TO_ANSWER_REQUEST: 'CLICK_TO_ANSWER_REQUEST',
    CLICK_TO_ANSWER_RESPONSE: 'CLICK_TO_ANSWER_RESPONSE',
    START_CONFERENCE_REQUEST: 'START_CONFERENCE_REQUEST',
    START_CONFERENCE_RESPONSE: 'START_CONFERENCE_RESPONSE',
    NAVIGATE_REQUEST: 'NAVIGATE_REQUEST',
    NAVIGATE_REQUEST_RESPONSE: 'NAVIGATE_REQUEST_RESPONSE'
  });

  var ContactCardResponseCode = Object.freeze({
    OK: 'OK',
    BUSY: 'BUSY',
    TELEPHONY_NOT_AVAILABLE: 'TELEPHONY_NOT_AVAILABLE',
    USER_NOT_FOUND: 'USER_NOT_FOUND',
    CONVERSATION_NOT_FOUND: 'CONVERSATION_NOT_FOUND',
    INVALID_CONVERSATION_TYPE: 'INVALID_CONVERSATION_TYPE',
    CALL_DECLINED: 'CALL_DECLINED',
    CALL_FAILED: 'CALL_FAILED',
    CALL_NOT_FOUND: 'CALL_NOT_FOUND',
    MUTE_DEVICE_FAILED: 'MUTE_DEVICE_FAILED',
    CONVERSATION_NAVIGATE_FAILED: 'CONVERSATION_NAVIGATE_FAILED',
    JOIN_CONFERENCE_FAILED: 'JOIN_CONFERENCE_FAILED',
    START_CONFERENCE_FAILED: 'START_CONFERENCE_FAILED',
    NAVIGATE_REQUEST_FAILED: 'NAVIGATE_REQUEST_FAILED'
  });

  var TeamPickupMessage = Object.freeze({
    TEAM_PICKUP: 'TEAM_PICKUP'
  });

  ///////////////////////////////////////////////////////////////////////////////////////
  // UserToUserHandler
  ///////////////////////////////////////////////////////////////////////////////////////

  // eslint-disable-next-line max-lines-per-function
  function UserToUserHandler(clientApiHandler) {
    // NOSONAR

    ///////////////////////////////////////////////////////////////////////////
    // Local variables
    ///////////////////////////////////////////////////////////////////////////
    var RESPONSE_TIMEOUT = 20000;
    var _reqCallBacks = {};
    var _evtCallBacks = {};

    var _clientApiHandler = clientApiHandler || ClientApiHandler.getInstance();

    ///////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////
    function addReqCallBack(requestId, cb) {
      if (!cb) {
        return;
      }

      var responseTimer = window.setTimeout(
        function (reqId) {
          logger.error('[UserToUserHandler]: Timeout waiting for response. RequestId:', reqId);
          if (_reqCallBacks[reqId]) {
            delete _reqCallBacks[reqId];
            cb(Constants.ReturnCode.REQUEST_TIMEOUT);

            logger.debug('[UserToUserHandler]: Remaining callbacks pending:', Object.keys(_reqCallBacks).length);
          }
        },
        RESPONSE_TIMEOUT,
        requestId
      );

      _reqCallBacks[requestId] = {
        cb: cb,
        timer: responseTimer
      };
    }

    function handleInvalidMessage(cb) {
      if (cb) {
        window.setTimeout(function () {
          cb(Constants.ReturnCode.INVALID_MESSAGE);
        }, 0);
      }
    }

    function sendUserToUserRequest(type, data, cb) {
      if (!data || !data.content || !data.content.type) {
        logger.error('[UserToUserHandler]: Invalid UserToUser request data - ', data);
        handleInvalidMessage(cb);
        return;
      }

      logger.debug('[UserToUserHandler]: Sending request ' + type + '.' + data.content.type);
      data.content.requestId = data.content.requestId || Utils.createTransactionId();

      if (_reqCallBacks[data.content.requestId]) {
        // There is already a pending request with the same ID
        logger.error('[UserToUserHandler]: UserToUser request has duplicate requestId');
        handleInvalidMessage(cb);
        return;
      }

      _clientApiHandler.sendUserToUserRequest(type, data, function (err) {
        if (err) {
          logger.error('[UserToUserHandler]: Failed to send request: ', err);
          cb && cb(err);
          return;
        }
        // For SDK UserToUser messages the application is supposed to handle requests/response.
        if (type === Constants.UserRoutingMessageType.SDK) {
          cb && cb();
        } else {
          addReqCallBack(data.content.requestId, cb);
        }
      });
    }

    function handleUserToUserEvent(type, evt) {
      try {
        // Ignore own messages
        if (evt.routing && evt.routing.srcClientId === _clientApiHandler.clientId) {
          logger.info('[UserToUserHandler]: Ignore own UserToUser event');
          return;
        }

        var data;
        try {
          data = JSON.parse(evt.data);
        } catch (err) {
          logger.error('[UserToUserHandler]: Event discarded (Cannot Parse)');
          return;
        }

        if (data.requestId) {
          var cbInfo = _reqCallBacks[data.requestId];
          if (cbInfo) {
            // This is a response to a previous request
            cbInfo.timer && window.clearTimeout(cbInfo.timer);
            delete _reqCallBacks[data.requestId];
            // Invoke the registered callback (if any)
            cbInfo.cb && cbInfo.cb(null, data);
            logger.debug('[UserToUserHandler]: Remaining callbacks pending:', Object.keys(_reqCallBacks).length);
            return;
          }
        }
        // Check if there is a registered callback for this event
        var evtType = type + '.' + data.type;
        var cbList = _evtCallBacks[evtType];
        if (!cbList || cbList.length === 0) {
          logger.info('[UserToUserHandler]: There is no registered event handler for ', evtType);
        } else {
          if (type === Constants.UserRoutingMessageType.SDK) {
            data = data.message;
          }
          cbList.forEach(function (cbElem) {
            cbElem.cb(data, evt.routing);
          });
        }
      } catch (e) {
        logger.error('[UserToUserHandler]: Exception handling UserToUser event. ', e);
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Client API Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    _clientApiHandler.on('UserToUser.ATC', function (evt) {
      logger.debug('[UserToUserHandler]: Received UserToUser.ATC event');
      handleUserToUserEvent(Constants.UserRoutingMessageType.ATC, evt);
    });

    _clientApiHandler.on('UserToUser.CONTACT_CARD', function (evt) {
      logger.debug('[UserToUserHandler]: Received UserToUser.CONTACT_CARD event: ', evt.data);
      handleUserToUserEvent(Constants.UserRoutingMessageType.CONTACT_CARD, evt);
    });

    _clientApiHandler.on('UserToUser.STC', function (evt) {
      logger.debug('[UserToUserHandler]: Received UserToUser.STC event: ', evt.data);
      handleUserToUserEvent(Constants.UserRoutingMessageType.STC, evt);
    });

    ///////////////////////////////////////////////////////////////////////////////
    // Public Interfaces
    ///////////////////////////////////////////////////////////////////////////////
    this.cancelReqCallback = function (reqId) {
      var cbInfo = _reqCallBacks[reqId];
      if (cbInfo) {
        // Clear the callback function, but keep the registration to handle the response
        cbInfo.cb = null;
      }
    };

    this.sendAtcRequest = function (data, cb) {
      sendUserToUserRequest(Constants.UserRoutingMessageType.ATC, data, cb);
    };

    this.sendContactCardRequest = function (data, cb) {
      sendUserToUserRequest(Constants.UserRoutingMessageType.CONTACT_CARD, data, cb);
    };

    this.sendStcRequest = function (data, cb) {
      sendUserToUserRequest(Constants.UserRoutingMessageType.STC, data, cb);
    };

    this.on = function (msgType, cb) {
      if (msgType && typeof cb === 'function') {
        _evtCallBacks[msgType] = _evtCallBacks[msgType] || [];
        // Make sure this is not a duplicate registration
        var isDuplicate = _evtCallBacks[msgType].some(function (cbInfo) {
          return cbInfo.cb === cb;
        });
        if (!isDuplicate) {
          _evtCallBacks[msgType].push({ cb: cb });
        }
      }
    };

    this.off = function (msgType, cb) {
      if (!msgType && !cb) {
        // Unregister all event handlers
        _evtCallBacks = {};
      } else if (!cb) {
        // Unregister the event handlers for the given message type
        delete _evtCallBacks[msgType];
      } else if (msgType && typeof cb === 'function') {
        // Unregister the given event handler
        var cbList = _evtCallBacks[msgType];
        cbList &&
          cbList.some(function (cbInfo, idx) {
            if (cbInfo.cb === cb) {
              cbList.splice(idx, 1);
              return true;
            }
            return false;
          });
      }
    };
  }

  UserToUserHandler.prototype.constructor = UserToUserHandler;
  UserToUserHandler.prototype.name = 'UserToUserHandler';

  // Exports
  unifyPhone.UserToUserHandler = UserToUserHandler;

  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.AtcInfoMessage = AtcInfoMessage;
  unifyPhone.Enums.AtcMessage = AtcMessage;
  unifyPhone.Enums.StcMessage = StcMessage;
  unifyPhone.Enums.ContactCardMessage = ContactCardMessage;
  unifyPhone.Enums.ContactCardResponseCode = ContactCardResponseCode;
  unifyPhone.Enums.TeamPickupMessage = TeamPickupMessage;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  unifyPhone.UserToUserHandlerSingleton = (function () {
    var _userToUserHandler;
    return {
      getInstance: function () {
        if (!_userToUserHandler) {
          _userToUserHandler = new unifyPhone.UserToUserHandler();
        }
        return _userToUserHandler;
      }
    };
  })();

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var logger = UnifyPhone.logger;

  // eslint-disable-next-line max-lines-per-function
  function CstaParser() {
    // NOSONAR

    /////////////////////////////////////////////////////////////////////////////
    // Public interfaces
    /////////////////////////////////////////////////////////////////////////////
    // eslint-disable-next-line complexity
    this.genCstaRequest = function (data) {
      // NOSONAR
      try {
        switch (
          data.request // NOSONAR
        ) {
          case 'AcceptCall':
            return genAcceptCall(data);
          case 'AlternateCall':
            return genAlternateRequest(data);
          case 'AnswerCall':
            return genAnswerRequest(data);
          case 'CallBackCallRelated':
            return genCallBackCallRelated(data);
          case 'CallBackNonCallRelated':
            return genCallBackNonCallRelated(data);
          case 'CallLogSnapShot':
            return genCallLogSnapShot(data);
          case 'CancelCallBack':
            return genCancelCallBack(data);
          case 'ClearConnection':
            return genClearConnectionRequest(data);
          case 'ConferenceCall':
            return genConferenceRequest(data);
          case 'ConsultationCall':
            return genConsultationCall(data);
          case 'DeflectCall':
            return genDeflectRequest(data);
          case 'GenerateDigits':
            return genGenerateDigitsRequest(data);
          case 'GenGetConfiguration':
            return genGetConfiguration();
          case 'GetAgentState':
            return genGetAgentState(data);
          case 'GetDoNotDisturb':
            return genGetDoNotDisturb(data);
          case 'GetForwarding':
            return genGetForwarding(data);
          case 'GetLogicalDeviceInformation':
            return genGetLogicalDeviceInformation(data);
          case 'GetMessageWaitingIndicator':
            return genGetMessageWaiting(data);
          case 'GetMicrophoneMute':
            return genGetMicrophoneMute(data);
          case 'GroupPickupCall':
            return genGroupPickupCall(data);
          case 'HoldCall':
            return genHoldRequest(data);
          case 'MakeCall':
            return genMakeCallRequest(data);
          case 'ReconnectCall':
            return genReconnectRequest(data);
          case 'RetrieveCall':
            return genRetrieveRequest(data);
          case 'SetAgentState':
            return genSetAgentState(data);
          case 'SetBusy':
            return genSetBusy(data);
          case 'SetDoNotDisturb':
            return genSetDoNotDisturb(data);
          case 'SetForwarding':
            return genSetForwarding(data);
          case 'SetMicrophoneMute':
            return genSetMicrophoneMute(data);
          case 'SingleStepTransferCall':
            return genSSTRequest(data);
          case 'SnapshotCall':
            return genSnapshotCall(data);
          case 'SnapshotDevice':
            return genSnapshotDevice(data);
          case 'TransferCall':
            return genTransferCall(data);
        }
        logger.error('CSTA: Unsupported CSTA request: ' + data.request);
      } catch (e) {
        logger.error(e);
      }
      return null;
    };

    // eslint-disable-next-line complexity
    this.parseEvent = function (cstaEvt) {
      // NOSONAR
      try {
        if (!cstaEvt) {
          logger.error('[CstaParser]: Invalid event');
          return null;
        }

        var eventTag = Object.keys(cstaEvt)[0];
        switch (eventTag) {
          case 'ABE':
            return parseAgentBusyEvent(cstaEvt);
          case 'ALOE':
            return parseAgentLoggedOffEvent(cstaEvt);
          case 'ALOEt':
            return parseAgentLoggedOnEvent(cstaEvt);
          case 'ANRE':
            return parseAgentNotReadyEvent(cstaEvt);
          case 'ARE':
            return parseAgentReadyEvent(cstaEvt);
          case 'AWAC':
            return parseAgentWorkingAfterCallEvent(cstaEvt);
          case 'BISE':
            return parseBackInServiceEvent(cstaEvt);
          case 'CBE':
            return parseCallBackEvent(cstaEvt);
          case 'CCEt':
            return parseConnectionClearedEvent(cstaEvt);
          case 'CIE':
            return parseCallInformationEvent(cstaEvt);
          case 'CLE':
            return parseCallLogEvent(cstaEvt);
          case 'CoE':
            return parseConferencedEvent(cstaEvt);
          case 'DCCE':
            return parseDeviceCapsChangedEvent(cstaEvt);
          case 'DE':
            return parseDeliveredEvent(cstaEvt);
          case 'DEt':
            return parseDivertedEvent(cstaEvt);
          case 'DGE':
            return parseDigitsGeneratedEvent(cstaEvt);
          case 'DNDE':
            return parseDoNotDisturbEvent(cstaEvt);
          case 'EEt':
            return parseEstablishedEvent(cstaEvt);
          case 'FE':
            return parseFailedEvent(cstaEvt);
          case 'FEt':
            return parseForwardingEvent(cstaEvt);
          case 'HE':
            return parseHeldEvent(cstaEvt);
          case 'MWE':
            return parseMessageWaitingEvent(cstaEvt);
          case 'OE':
            return parseOfferedEvent(cstaEvt);
          case 'OOSE':
            return parseOutOfServiceEvent(cstaEvt);
          case 'QE':
            return parseQueuedEvent(cstaEvt);
          case 'ReE':
            return parseRetrievedEvent(cstaEvt);
          case 'SCFE':
            return parseServiceCompletionFailureEvent(cstaEvt);
          case 'SIE':
            return parseServiceInitiatedEvent(cstaEvt);
          case 'TE':
            return parseTransferredEvent(cstaEvt);
          case 'MC':
            return parseMakeCall(cstaEvt);
          case 'CCn':
            return parseClearConnection(cstaEvt);
          case 'AnC':
            return parseAnswerCall(cstaEvt);
        }
        logger.info('[CstaParser]: Unsupported CSTA event: ', eventTag);
      } catch (e) {
        logger.error('[CstaParser]: Error parsing CSTA event: ', e);
      }
      return null;
    };

    // eslint-disable-next-line complexity
    this.parseResponse = function (request, cstaRs) {
      // NOSONAR
      try {
        if (!request || !cstaRs) {
          logger.error('[CstaParser]: Invalid response');
          return null;
        }

        if (cstaRs.CSTA) {
          cstaRs.CSTAErrorCode = cstaRs.CSTA;
        }

        if (cstaRs.CSTAErrorCode) {
          logger.debug('[CstaParser]: Received error response for: ', request);
          return parseErrorCode(cstaRs);
        } else if (cstaRs.ISEC) {
          logger.debug('[CstaParser]: Received internal service error response for: ', request);
          return parseInternalServiceErrorCode(cstaRs);
        }

        switch (
          request // NOSONAR
        ) {
          case 'AcceptCall':
            return parseAcceptCallResponse(cstaRs);
          case 'AlternateCall':
            return parseAlternateResponse(cstaRs);
          case 'AnswerCall':
            return parseAnswerResponse(cstaRs);
          case 'CallBackCallRelated':
            return parseCallBackResponse(cstaRs);
          case 'CallBackNonCallRelated':
            return parseCallBackNonCallReleatedResponse(cstaRs);
          case 'CallLogSnapShot':
            return parseCallLogSnapShotResponse(cstaRs);
          case 'CancelCallBack':
            return parseCancelCallBackResponse(cstaRs);
          case 'ClearConnection':
            return parseClearConnectionResponse(cstaRs);
          case 'ConferenceCall':
            return parseConferenceResponse(cstaRs);
          case 'ConsultationCall':
            return parseConsultationCallResponse(cstaRs);
          case 'DeflectCall':
            return parseDeflectResponse(cstaRs);
          case 'GenerateDigits':
            return parseGenDigitsResponse(cstaRs);
          case 'GetAgentState':
            return parseGetAgentStateResponse(cstaRs);
          case 'GetDoNotDisturb':
            return parseGetDoNotDisturbResponse(cstaRs);
          case 'GetForwarding':
            return parseGetForwardingResponse(cstaRs);
          case 'GetLogicalDeviceInformation':
            return parseGetLogicalDeviceInformationResponse(cstaRs);
          case 'GetMessageWaitingIndicator':
            return parseGetMessageWaitingResponse(cstaRs);
          case 'GetMicrophoneMute':
            return parseGetMicrophoneMuteResponse(cstaRs);
          case 'GroupPickupCall':
            return parseGroupPickupCallResponse(cstaRs);
          case 'HoldCall':
            return parseHoldResponse(cstaRs);
          case 'MakeCall':
            return parseMakeCallResponse(cstaRs);
          case 'ReconnectCall':
            return parseReconnectResponse(cstaRs);
          case 'RetrieveCall':
            return parseRetrieveResponse(cstaRs);
          case 'SetAgentState':
            return parseSetAgentStateResponse(cstaRs);
          case 'SetBusy':
            return parseSetBusyResponse(cstaRs);
          case 'SetDoNotDisturb':
            return parseSetDoNotDisturbResponse(cstaRs);
          case 'SetForwarding':
            return parseSetForwardingResponse(cstaRs);
          case 'SetMicrophoneMute':
            return parseSetMicrophoneMuteResponse(cstaRs);
          case 'SingleStepTransferCall':
            return parseSSTResponse(cstaRs);
          case 'SnapshotCall':
            return parseSnapshotCallResponse(cstaRs);
          case 'SnapshotDevice':
            return parseSnapshotDeviceResponse(cstaRs);
          case 'TransferCall':
            return parseTransferCallResponse(cstaRs);
        }
        logger.error('CSTA: Unsupported CSTA response for request: ', request);
      } catch (e) {
        logger.error(e);
      }
      return null;
    };

    /////////////////////////////////////////////////////////////////////////////
    // Internal functions
    /////////////////////////////////////////////////////////////////////////////
    function parseCallData(callData) {
      return {
        connectionId: callData.cIr,
        localCallState: callData.lCS && callData.lCS.cCSe && callData.lCS.cCSe.lCSe
      };
    }

    function parseEndpointData(endpointData) {
      return {
        deviceOnCall: endpointData.dOC.dIr || '',
        callIdentifier: endpointData.cI,
        localConnectionState: endpointData.lCI,
        servicesPermitted: parseServicesPermitted(endpointData)
      };
    }

    function parseErrorCode(rs) {
      if (!rs.CSTAErrorCode) {
        logger.error('[CstaParser]: The given response is not an error code: ', rs);
        return null;
      }

      var category = null;

      var shortTag = Object.keys(rs.CSTAErrorCode)[0];
      switch (shortTag) {
        case 'oprt':
        case 'operation':
          category = 'Operation Error';
          break;
        case 'scrt':
        case 'security':
          category = 'Security Error';
          break;
        case 'sIy':
        case 'stateIncompatibility':
          category = 'State Incompatibility Error';
          break;
        case 'sRAy':
        case 'systemResourceAvailability':
          category = 'System Resource Availability Error';
          break;
        case 'sRA':
        case 'subscribedResourceAvailability':
          category = 'Subscribed Resource Availability Error';
          break;
        case 'pM':
        case 'performanceManagement':
          category = 'Performance Management Error';
          break;
        case 'pDa':
        case 'privateDataInformation':
          category = 'Private Data Information Error';
          break;
      }

      if (!category) {
        logger.error('[CstaParser]: Unrecognized Error Category');
        return { error: true, errorCategory: 'Unknown', errorValue: 'Unknown' };
      }

      return {
        error: true,
        errorCategory: category,
        errorValue: rs.CSTAErrorCode[shortTag]
      };
    }

    function parseInternalServiceErrorCode(rs) {
      if (!rs.ISEC) {
        logger.error('[CstaParser]: The given response is not an internal service error code: ', rs);
        return null;
      }

      var shortTag = Object.keys(rs.ISEC)[0];
      if (shortTag !== 'code') {
        logger.error('[CstaParser]: Unrecognized Internal Service Error Category');
        return { error: true, errorCategory: 'Unknown', errorValue: 'Unknown' };
      }

      return {
        error: true,
        errorCategory: 'Code',
        errorValue: rs.ISEC.code
      };
    }

    function parseEpid(data) {
      var epid = null;
      if (data && data.extn && data.extn.pDa && data.extn.pDa.prvt) {
        epid = data.extn.pDa.prvt.zEpid;
      }
      return epid;
    }

    function normalizeServicesPermitted(sp) {
      if (sp) {
        Object.keys(sp).forEach(function (key) {
          sp[key] = sp[key] === '' || sp[key] === 'true';
        });
      }
    }

    function parseServicesPermitted(data) {
      if (!data) {
        return {};
      }
      var servicesPermitted = {};
      if (data.sP) {
        servicesPermitted.rSP = data.sP;
        normalizeServicesPermitted(servicesPermitted.rSP.cCSs);
      }
      if (data.extn && data.extn.pDa && data.extn.pDa.prvt && data.extn.pDa.prvt.zxSP !== undefined) {
        servicesPermitted.eSP = data.extn.pDa.prvt.zxSP;
        normalizeServicesPermitted(servicesPermitted.eSP);
      }
      return servicesPermitted;
    }

    function parseCallingDevicePrivate(data) {
      var callingDevicePrivate = null;
      if (data.extn && data.extn.pDa && data.extn.pDa.prvt && data.extn.pDa.prvt.pR1 && data.extn.pDa.prvt.pR1.dIr) {
        callingDevicePrivate = data.extn.pDa.prvt.pR1.dIr;
      }
      return callingDevicePrivate;
    }

    function parseCalledDevicePrivate(data) {
      var calledDevicePrivate = null;
      if (data.extn && data.extn.pDa && data.extn.pDa.prvt && data.extn.pDa.prvt.pR2 && data.extn.pDa.prvt.pR2.dIr) {
        calledDevicePrivate = data.extn.pDa.prvt.pR2.dIr;
      }
      return calledDevicePrivate;
    }

    function parseOtherDevicePrivate(data) {
      var otherDevicePrivate = null;
      if (data.extn && data.extn.pDa && data.extn.pDa.prvt && data.extn.pDa.prvt.pR13 && data.extn.pDa.prvt.pR13.dIr) {
        otherDevicePrivate = data.extn.pDa.prvt.pR13.dIr;
      }
      return otherDevicePrivate;
    }

    // Parse Responses
    function parseAlternateResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Alternate Call response: ', rs);
      return rs.ACRe;
    }

    function parseAnswerResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Answer Call response: ', rs);
      return rs.AnCR;
    }

    function parseAcceptCallResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Accept Call response: ', rs);
      return rs.ACR;
    }

    function parseClearConnectionResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Clear Connection response: ', rs);
      return rs.ClCR;
    }

    function parseDeflectResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Deflect Call response: ', rs);
      return rs.DCRe;
    }

    function parseGenDigitsResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Generate Digits response: ', rs);
      return rs.GDR;
    }

    function parseGetDoNotDisturbResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Get Do Not Disturb response: ', rs);
      var data = rs.GDNDe;
      return {
        doNotDisturbOn: data.dNDO === 'true'
      };
    }

    function parseGetLogicalDeviceInformationResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Get Logical Device Information response: ', rs);
      var data = rs.GLDIe;
      return {
        hasPhysicalDeviceInformation: data.hPE === 'true'
      };
    }

    function parseForwardListItemData(data) {
      return {
        forwardingType: data.fTe,
        forwardStatus: data.fS === 'true',
        forwardDN: data.fDNN
      };
    }

    function parseHuntGroupListEntry(entry) {
      return {
        name: entry.zgNM,
        pilotDn: entry.zgDN,
        available: entry.zMB === 'false'
      };
    }

    function parseHuntGroupList(data) {
      if (data.extn && data.extn.pDa && data.extn.pDa.prvt) {
        var huntGroupList = [];
        if (data.extn.pDa.prvt.zeMBSL) {
          if (Array.isArray(data.extn.pDa.prvt.zeMBSL.zeMBS)) {
            data.extn.pDa.prvt.zeMBSL.zeMBS.forEach(function (entry) {
              huntGroupList.push(parseHuntGroupListEntry(entry));
            });
          } else {
            huntGroupList.push(parseHuntGroupListEntry(data.extn.pDa.prvt.zeMBSL.zeMBS));
          }
          return huntGroupList;
        }
      }
      return null;
    }

    function parseGetForwardingResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Get Forwarding response: ', rs);
      // OSV sends the GetForwardingResponse with long tags OSV-3432
      if (rs.GetForwardingResponse) {
        return parseGetForwardingResponseLongTags(rs.GetForwardingResponse);
      }
      var data = rs.GFR;
      var parsedResp = {
        forwardList: []
      };
      if (data.fL.fLI.fS) {
        parsedResp.forwardList.push(parseForwardListItemData(data.fL.fLI));
      } else {
        // Multiple forwarding items
        data.fL.fLI.forEach(function (item) {
          parsedResp.forwardList.push(parseForwardListItemData(item));
        });
      }
      if (data.extn && data.extn.pDa && data.extn.pDa.prvt) {
        if (data.extn.pDa.prvt.zsOA) {
          parsedResp.staticOndActive = data.extn.pDa.prvt.zsOA === 'true';
          parsedResp.staticOndDN = data.extn.pDa.prvt.zsOD;
        }
        parsedResp.voicemailActive = data.extn.pDa.prvt.zvMA === 'true';
        if (data.extn.pDa.prvt.zoP) {
          parsedResp.overrideProfile = data.extn.pDa.prvt.zoP === 'true';
        }
        if (data.extn.pDa.prvt.zvMRD) {
          parsedResp.voicemailRingDuration = parseInt(data.extn.pDa.prvt.zvMRD, 10);
        }
        if (data.extn.pDa.prvt.zmRD) {
          parsedResp.mainRingDuration = parseInt(data.extn.pDa.prvt.zmRD, 10);
        }
        if (data.extn.pDa.prvt.zcRD) {
          parsedResp.clientRingDuration = parseInt(data.extn.pDa.prvt.zcRD, 10);
        }
        if (data.extn.pDa.prvt.zcpRD) {
          parsedResp.cellRingDuration = parseInt(data.extn.pDa.prvt.zcpRD, 10);
        }
        if (data.extn.pDa.prvt.hasOwnProperty('zcPN')) {
          parsedResp.alternativeNumber = data.extn.pDa.prvt.zcPN;
        }
        if (data.extn.pDa.prvt.hasOwnProperty('zrTCN')) {
          parsedResp.routeToCell = data.extn.pDa.prvt.zrTCN === 'true';
        }
      }
      return parsedResp;
    }

    function parseGetForwardingResponseLongTags(data) {
      var parsedResp = {
        forwardList: []
      };
      if (data.forwardingList.forwardListItem.forwardStatus) {
        data.forwardingList.forwardListItem.forwardStatus = data.forwardingList.forwardListItem.forwardStatus === 'true';
        parsedResp.forwardList.push(data.forwardingList.forwardListItem);
      } else {
        // Multiple forwarding items
        data.forwardingList.forwardListItem.forEach(function (item) {
          item.forwardStatus = item.forwardStatus === 'true';
          parsedResp.forwardList.push(item);
        });
      }
      if (data.extensions && data.extensions.privateData && data.extensions.privateData.private) {
        parsedResp.staticOndDN = data.extensions.privateData.private.staticOndDN;
        parsedResp.staticOndActive = data.extensions.privateData.private.staticOndActive === 'true';
      }
      return parsedResp;
    }

    function parseGetMessageWaitingResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Get Message Waiting response: ', rs);
      var data = rs.GMWIe;
      return {
        messageWaitingOn: data.mWO === 'true'
      };
    }

    function parseHoldResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Hold Call response: ', rs);
      return rs.HCR;
    }

    function parseReconnectResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Reconnect Call response: ', rs);
      return rs.RCR;
    }

    function parseRetrieveResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Retrieve Call response: ', rs);
      return rs.RCRe;
    }

    function parseSetBusyResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Set Busy response: ', rs);
      return rs.zSBNe;
    }

    function parseSetDoNotDisturbResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Set Do Not Disturb response: ', rs);
      return rs.SDNDe;
    }

    function parseSetForwardingResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Set Forwarding response: ', rs);
      return rs.hasOwnProperty('SFR') ? rs.SFR : rs.SetForwardingResponse;
    }

    function parseConsultationCallResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Consultation Call response: ', rs);
      return rs.CnCR;
    }

    function parseCallBackResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Call Back call related response: ', rs);
      return rs.CBR;
    }

    function parseCallBackNonCallReleatedResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Call Back non call related response: ', rs);
      return rs.CBNCe;
    }

    function parseCancelCallBackResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Cancel Call Back response: ', rs);
      return rs.CCBR;
    }

    function parseSnapshotCallResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Snapshot Call response: ', rs);
      var data = rs.SCR;
      var parsedResp = {
        endpoints: [],
        callingDevice: (data.caD && data.caD.dIr) || '',
        calledDevice: (data.cDe && data.cDe.dIr) || '',
        servicesPermitted: {}
      };

      var addParsedEp = function (endpointData) {
        var parsedEp = parseEndpointData(endpointData);
        parsedResp.endpoints.push(parsedEp);

        // OSbiz provides only calledDevice parameter
        if (!parsedResp.callingDevice && parsedResp.calledDevice && parsedEp.deviceOnCall !== parsedResp.calledDevice) {
          parsedResp.callingDevice = parsedEp.deviceOnCall;
        }
      };

      if (data.cRID.sDa.sCRIo) {
        if (data.cRID.sDa.sCRIo.dOC) {
          // There is only one endpoint in the call
          addParsedEp(data.cRID.sDa.sCRIo);
        } else {
          // There are multiple endpoints
          data.cRID.sDa.sCRIo.forEach(function (endpointData) {
            addParsedEp(endpointData);
          });
        }
      }
      parsedResp.servicesPermitted = parseServicesPermitted(data);
      parsedResp.epid = parseEpid(data);

      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedResp.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedResp.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }

      return parsedResp;
    }

    function parseSnapshotDeviceResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Snapshot Device response: ', rs);
      var data = rs.SnDR;
      var parsedResp = {
        activeCalls: []
      };

      function addToActiveCalls(callInfo) {
        var call = parseCallData(callInfo);
        if (call.connectionId && call.connectionId.cID) {
          parsedResp.activeCalls.push(call);
        }
      }

      if (data.cRID.sDa.sDRI) {
        if (data.cRID.sDa.sDRI.cIr) {
          // There is only one call
          addToActiveCalls(data.cRID.sDa.sDRI);
        } else {
          // There are multiple calls
          data.cRID.sDa.sDRI.forEach(function (callData) {
            addToActiveCalls(callData);
          });
        }
      }
      if (data.extn && data.extn.pDa && data.extn.pDa.prvt) {
        parsedResp.deskPhoneRegistered = data.extn.pDa.prvt.zdP === 'true';
      }
      return parsedResp;
    }

    function parseSSTResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA SST Call response: ', rs);
      var data = rs.SSTCe;
      var parsedResp = { transferredCall: data.trC };
      if (data.extn && data.extn.pDa && data.extn.pDa.prvt) {
        parsedResp.seamlessHandover = data.extn.pDa.prvt.zseHo === 'true';
      }
      return parsedResp;
    }

    function parseConferenceResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Conference Call response: ', rs);
      var data = rs.CoCR;
      return { conferenceCall: data.coC };
    }

    function parseTransferCallResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA TransferCall response: ', rs);
      var data = rs.TCR;
      return { transferredCall: data.trC };
    }

    function parseSetMicrophoneMuteResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA SetMicrophoneMute response', rs);
      var data = rs.SMMR;
      return { mute: data.mML.mMI.mMO === 'true' };
    }

    function parseGetMicrophoneMuteResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA GetMicrophoneMute response', rs);
      var data = rs.GMMR;
      return { mute: data.mML.mMI.mMO === 'true' };
    }

    function parseMakeCallResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA MakeCall response', rs);
      return rs.MCR;
    }

    function parseCallLogSnapShotResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA Call Log Snap Shot response: ', rs);
      return rs.CLSR;
    }

    function parseGetAgentStateResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA GetAgentState response', rs);
      var data = rs.GASR;
      return {
        loggedOnState: data.aSL && data.aSL.aSE && data.aSL.aSE.lOS === 'true',
        agentState: data.aSL && data.aSL.aSE && data.aSL.aSE.aIo && data.aSL.aSE.aIo.aII.aS === 'agentReady' ? 'ready' : 'notReady',
        huntGroupList: parseHuntGroupList(data)
      };
    }

    function parseSetAgentStateResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA SetAgentState response', rs);
      return rs.SASR;
    }

    function parseGroupPickupCallResponse(rs) {
      logger.debug('[CstaParser]: Received CSTA GroupPickupCall response', rs);
      return rs.GPCR;
    }

    // Parse Events
    function parseCallInformationEvent(event) {
      var data = event.CIE;
      var parsedEvent = {
        category: 'CallAssociatedFeature',
        name: 'CallInformationEvent',
        connection: data.cnncn,
        device: data.dvc.dIr || '',
        callingDevice: (data.caD && data.caD.dIr) || '',
        servicesPermitted: {}
      };
      parsedEvent.servicesPermitted = parseServicesPermitted(data);
      parsedEvent.epid = parseEpid(data);

      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedEvent.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedEvent.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }

      parsedEvent.pickupNotificationEarly = parsedEvent.device && parsedEvent.device.includes('Pickup Group Number');
      return parsedEvent;
    }

    function parseConferencedEvent(event) {
      var data = event.CoE;
      var parsedEvent = {
        category: 'CallControl',
        name: 'ConferencedEvent',
        primaryOldCall: data.pOC,
        secondaryOldCall: data.sOC,
        conferencingDevice: data.coD.dIr || '',
        addedParty: data.aPy.dIr || '',
        conferenceConnections: [],
        localConnectionInfo: data.lCI,
        cause: data.cs,
        servicesPermitted: {}
      };
      data.cnC.cLI.forEach(function (item) {
        var connListItem = {
          connection: item.nCn,
          endpoint: item.endp && (item.endp.dID || item.endp.endp || item.endp)
        };
        parsedEvent.conferenceConnections.push(connListItem);
      });
      parsedEvent.servicesPermitted = parseServicesPermitted(data);
      parsedEvent.epid = parseEpid(data);

      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedEvent.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedEvent.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }

      return parsedEvent;
    }

    function parseConnectionClearedEvent(event) {
      var data = event.CCEt;
      var parsedEvent = {
        category: 'CallControl',
        name: 'ConnectionClearedEvent',
        droppedConnection: data.drC,
        releasingDevice: data.rD.dIr || '',
        localConnectionInfo: data.lCI,
        cause: data.cs,
        servicesPermitted: {}
      };
      parsedEvent.servicesPermitted = parseServicesPermitted(data);
      parsedEvent.epid = parseEpid(data);

      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedEvent.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedEvent.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }

      parsedEvent.pickupNotificationTerminated = parsedEvent.releasingDevice && parsedEvent.releasingDevice.includes('Pickup Group Number');
      return parsedEvent;
    }

    function parseDeliveredEvent(event) {
      var data = event.DE;
      var parsedEvent = {
        category: 'CallControl',
        name: 'DeliveredEvent',
        connection: data.cnncn,
        alertingDevice: data.aDe.dIr || '',
        callingDevice: data.caD.dIr || '',
        networkCallingDevice: (data.ntCD && data.ntCD.dIr) || '',
        calledDevice: data.cDe.dIr || '',
        lastRedirectionDevice: (data.lRD && data.lRD.nDd) || '',
        localConnectionInfo: data.lCI,
        cause: data.cs
      };
      parsedEvent.servicesPermitted = parseServicesPermitted(data);
      parsedEvent.epid = parseEpid(data);

      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedEvent.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedEvent.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }

      var presentationRestrictedDevice13 = parseOtherDevicePrivate(data);
      if (presentationRestrictedDevice13) {
        parsedEvent.presentationRestrictedDevice13 = presentationRestrictedDevice13;
      }

      return parsedEvent;
    }

    function parseDigitsGeneratedEvent(event) {
      var data = event.DGE;
      return {
        category: 'CallAssociatedFeature',
        name: 'DigitsGeneratedEvent',
        connection: data.cnncn,
        digitGeneratedList: data.dGL,
        keysToOmitFromLogging: ['digitGeneratedList']
      };
    }

    function parseDivertedEvent(event) {
      var data = event.DEt;
      var parsedEvent = {
        category: 'CallControl',
        name: 'DivertedEvent',
        connection: data.cnncn,
        calledDevice: (data.cDe && data.cDe.dIr) || '',
        divertingDevice: data.dvD.dIr || '',
        newDestination: data.nD.dIr || '',
        callingDevice: (data.caD && data.caD.dIr) || '',
        lastRedirectionDevice: (data.lRD && data.lRD.nDd) || '',
        localConnectionInfo: data.lCI,
        cause: data.cs
      };
      parsedEvent.servicesPermitted = parseServicesPermitted(data);
      parsedEvent.epid = parseEpid(data);

      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedEvent.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedEvent.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }

      return parsedEvent;
    }

    function parseDoNotDisturbEvent(event) {
      var data = event.DNDE;
      return {
        category: 'LogicalDeviceFeature',
        name: 'DoNotDisturbEvent',
        device: data.dvc.dIr || '',
        doNotDisturbOn: data.dNDO === 'true'
      };
    }

    function parseEstablishedEvent(event) {
      var data = event.EEt;
      var parsedEvent = {
        category: 'CallControl',
        name: 'EstablishedEvent',
        establishedConnection: data.eCn,
        answeringDevice: data.anD.dIr || '',
        callingDevice: data.caD.dIr || '',
        networkCallingDevice: (data.ntCD && data.ntCD.dIr) || '',
        calledDevice: data.cDe.dIr || '',
        lastRedirectionDevice: (data.lRD && data.lRD.nDd) || '',
        localConnectionInfo: data.lCI,
        cause: data.cs,
        servicesPermitted: {}
      };
      parsedEvent.servicesPermitted = parseServicesPermitted(data);
      parsedEvent.epid = parseEpid(data);

      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedEvent.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedEvent.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }

      var presentationRestrictedDevice13 = parseOtherDevicePrivate(data);
      if (presentationRestrictedDevice13) {
        parsedEvent.presentationRestrictedDevice13 = presentationRestrictedDevice13;
      }

      return parsedEvent;
    }

    function parseFailedEvent(event) {
      var data = event.FE;
      var parsedEvent = {
        category: 'CallControl',
        name: 'FailedEvent',
        failedConnection: data.fC,
        failingDevice: data.fD.dIr || '',
        callingDevice: data.caD.dIr || '',
        networkCallingDevice: (data.ntCD && data.ntCD.dIr) || '',
        localConnectionInfo: data.lCI,
        calledDevice: data.cDe.dIr,
        cause: data.cs,
        servicesPermitted: {}
      };
      parsedEvent.servicesPermitted = parseServicesPermitted(data);
      parsedEvent.epid = parseEpid(data);

      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedEvent.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedEvent.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }

      return parsedEvent;
    }

    function parseForwardingEvent(event) {
      var data = event.FEt;
      var parsedEvent = {
        category: 'LogicalDeviceFeature',
        name: 'ForwardingEvent',
        device: data.dvc.dIr || '',
        forwardingType: data.fTe,
        forwardStatus: data.fS === 'true',
        forwardTo: data.fTo
      };
      if (data.extn && data.extn.pDa && data.extn.pDa.prvt) {
        if (data.extn.pDa.prvt.zsOA) {
          parsedEvent.staticOndActive = data.extn.pDa.prvt.zsOA === 'true';
          parsedEvent.staticOndDN = data.extn.pDa.prvt.zsOD;
        }
        if (data.extn.pDa.prvt.zvMA) {
          parsedEvent.voicemailActive = data.extn.pDa.prvt.zvMA === 'true';
        }
        if (data.extn.pDa.prvt.zoP) {
          parsedEvent.overrideProfile = data.extn.pDa.prvt.zoP === 'true';
        }
        if (data.extn.pDa.prvt.zvMRD) {
          parsedEvent.voicemailRingDuration = parseInt(data.extn.pDa.prvt.zvMRD, 10);
        }
        if (data.extn.pDa.prvt.zmRD) {
          parsedEvent.mainRingDuration = parseInt(data.extn.pDa.prvt.zmRD, 10);
        }
        if (data.extn.pDa.prvt.zcRD) {
          parsedEvent.clientRingDuration = parseInt(data.extn.pDa.prvt.zcRD, 10);
        }
        if (data.extn.pDa.prvt.zcpRD) {
          parsedEvent.cellRingDuration = parseInt(data.extn.pDa.prvt.zcpRD, 10);
        }
        if (data.extn.pDa.prvt.hasOwnProperty('zcPN')) {
          parsedEvent.alternativeNumber = data.extn.pDa.prvt.zcPN;
        }
        if (data.extn.pDa.prvt.hasOwnProperty('zrTCN')) {
          parsedEvent.routeToCell = data.extn.pDa.prvt.zrTCN === 'true';
        }
        if (data.extn.pDa.prvt.hasOwnProperty('zfCC')) {
          parsedEvent.forwardingCapsChanged = data.extn.pDa.prvt.zfCC === 'true';
        }
      }
      return parsedEvent;
    }

    function parseHeldEvent(event) {
      var data = event.HE;
      var parsedEvent = {
        category: 'CallControl',
        name: 'HeldEvent',
        heldConnection: data.hCn,
        holdingDevice: data.hD.dIr || '',
        localConnectionInfo: data.lCI,
        servicesPermitted: {}
      };
      parsedEvent.servicesPermitted = parseServicesPermitted(data);
      parsedEvent.epid = parseEpid(data);
      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedEvent.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedEvent.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }
      return parsedEvent;
    }

    function parseMessageWaitingEvent(event) {
      var data = event.MWE;
      return {
        category: 'PhysicalDeviceFeature',
        name: 'MessageWaitingEvent',
        device: data.tD.dIr || '',
        messageWaitingOn: data.mWO === 'true'
      };
    }

    function parseOfferedEvent(event) {
      var data = event.OE;
      var parsedEvent = {
        category: 'CallControl',
        name: 'OfferedEvent',
        offeredConnection: data.oCn,
        offeredDevice: data.oDe.dIr || '',
        callingDevice: data.caD.dIr || '',
        calledDevice: data.cDe.dIr || '',
        localConnectionInfo: data.lCI,
        lastRedirectionDevice: (data.lRD && data.lRD.nDd) || '',
        cause: data.cs
      };
      parsedEvent.servicesPermitted = parseServicesPermitted(data);
      parsedEvent.epid = parseEpid(data);

      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedEvent.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedEvent.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }

      return parsedEvent;
    }

    function parseQueuedEvent(event) {
      var data = event.QE;
      var parsedEvent = {
        category: 'CallControl',
        name: 'QueuedEvent',
        queuedConnection: data.qC,
        queue: data.q.dIr || '',
        callingDevice: data.caD.dIr || '',
        lastRedirectionDevice: (data.lRD && data.lRD.nDd) || '',
        localConnectionInfo: data.lCI,
        cause: data.cs
      };
      parsedEvent.servicesPermitted = parseServicesPermitted(data);
      parsedEvent.epid = parseEpid(data);

      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedEvent.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedEvent.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }

      return parsedEvent;
    }

    function parseRetrievedEvent(event) {
      var data = event.ReE;
      var parsedEvent = {
        category: 'CallControl',
        name: 'RetrievedEvent',
        retrievedConnection: data.rCn,
        retrievingDevice: data.reD.dIr || '',
        localConnectionInfo: data.lCI,
        servicesPermitted: {}
      };
      parsedEvent.servicesPermitted = parseServicesPermitted(data);
      parsedEvent.epid = parseEpid(data);
      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedEvent.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedEvent.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }
      return parsedEvent;
    }

    function parseServiceCompletionFailureEvent(event) {
      var data = event.SCFE;
      var parsedEvent = {
        category: 'CallAssociatedFeature',
        name: 'ServiceCompletionFailureEvent',
        primaryCall: {
          connectionID: data.prC.cIDD,
          localConnectionState: data.prC.lCSe
        },
        cause: data.cs
      };
      if (data.sC) {
        parsedEvent.secondaryCall = {
          connectionID: data.sC.cIDD,
          localConnectionState: data.sC.lCSe
        };
      }
      return parsedEvent;
    }

    function parseServiceInitiatedEvent(event) {
      var data = event.SIE;
      var parsedEvent = {
        category: 'CallControl',
        name: 'ServiceInitiatedEvent',
        initiatedConnection: data.iCn,
        initiatingDevice: data.iD.dIr || '',
        cause: data.cs
      };
      parsedEvent.servicesPermitted = parseServicesPermitted(data);
      parsedEvent.epid = parseEpid(data);
      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedEvent.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedEvent.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }
      return parsedEvent;
    }

    function parseMakeCall(event) {
      var data = event.MC;
      var parsedEvent = {
        category: 'CtiCallControl',
        name: 'MakeCall',
        callingDevice: data.caD,
        calledDevice: data.cDN,
        autoOriginate: data.aOe
      };
      return parsedEvent;
    }

    function parseClearConnection(event) {
      var data = event.CCn;
      var parsedEvent = {
        category: 'CtiCallControl',
        name: 'ClearConnection',
        connectionToBeCleared: data.coTBC.cID
      };
      return parsedEvent;
    }

    function parseAnswerCall(event) {
      var data = event.AnC;
      var parsedEvent = {
        category: 'CtiCallControl',
        name: 'AnswerCall',
        callToBeAnswered: data.cTBAd.cID
      };
      return parsedEvent;
    }

    function parseCallBackEvent(event) {
      var data = event.CBE;
      return {
        category: 'LogicalDeviceFeature',
        name: 'CallBackEvent',
        originatingDevice: data.orD.dIr,
        targetDevice: data.tD.dIr,
        callBackSetCanceled: data.cBSC
      };
    }

    function parseTransferredEvent(event) {
      var data = event.TE;
      var parsedEvent = {
        category: 'CallControl',
        name: 'TransferredEvent',
        primaryOldCall: data.pOC,
        secondaryOldCall: data.sOC,
        transferringDevice: data.tDe.dIr || '',
        transferredToDevice: data.tTDe.dIr || '',
        transferredConnections: [],
        localConnectionInfo: data.lCI,
        cause: data.cs,
        servicesPermitted: {}
      };
      var connections = data.tCs.cLI;
      if (!Array.isArray(connections)) {
        connections = [connections];
      }
      parsedEvent.transferredConnections = connections.map(function (item) {
        return {
          connection: item.nCn,
          endpoint: item.endp && (item.endp.dID || item.endp.endp || item.endp)
        };
      });
      parsedEvent.servicesPermitted = parseServicesPermitted(data);

      var presentationRestrictedDevice1 = parseCallingDevicePrivate(data);
      if (presentationRestrictedDevice1) {
        parsedEvent.presentationRestrictedDevice1 = presentationRestrictedDevice1;
      }

      var presentationRestrictedDevice2 = parseCalledDevicePrivate(data);
      if (presentationRestrictedDevice2) {
        parsedEvent.presentationRestrictedDevice2 = presentationRestrictedDevice2;
      }

      parsedEvent.epid = parseEpid(data);
      return parsedEvent;
    }

    function parseCallLogEntryData(data) {
      return {
        recordId: data.rID,
        callID: data.cID,
        recordType: data.rTp,
        createdTime: data.cTm,
        result: data.res,
        callDuration: data.cDr,
        callingDevice: data.clDv || '',
        calledDevice: data.cldDv || '',
        answeringDevice: data.aDv || ''
      };
    }

    function parseCallLogEvent(event) {
      var data = event.CLE;
      var parsedEvent = {
        category: 'LogicalDeviceFeature',
        name: 'CallLogEvent',
        callLogList: []
      };
      if (data.cLL && data.cLL.cLE) {
        if (data.cLL.cLE.rID) {
          //Only one call log entry in the callLog event
          parsedEvent.callLogList.push(parseCallLogEntryData(data.cLL.cLE));
        } else {
          //There are Multiple entries in the CallLog Event
          data.cLL.cLE.forEach(function (entry) {
            parsedEvent.callLogList.push(parseCallLogEntryData(entry));
          });
        }
      }
      return parsedEvent;
    }

    function parseBackInServiceEvent(event) {
      var data = event.BISE;
      return {
        category: 'DeviceMaintenance',
        name: 'BackInServiceEvent',
        backInService: true,
        device: data.dvc.dIr || ''
      };
    }

    function parseOutOfServiceEvent(event) {
      var data = event.OOSE;
      return {
        category: 'DeviceMaintenance',
        name: 'OutOfServiceEvent',
        outOfService: true,
        device: data.dvc.dIr || ''
      };
    }

    function parseAgentReadyEvent(data) {
      return {
        category: 'LogicalDeviceFeature',
        name: 'AgentReadyEvent',
        agentReady: true,
        huntGroupList: parseHuntGroupList(data.ARE),
        agentDevice: (data.ARE && data.ARE.aD && data.ARE.aD.dIr) || '',
        agentGroup: (data.ARE && data.ARE.aG) || ''
      };
    }

    function parseAgentNotReadyEvent(data) {
      return {
        category: 'LogicalDeviceFeature',
        name: 'AgentNotReadyEvent',
        agentReady: false,
        huntGroupList: parseHuntGroupList(data.ANRE),
        agentDevice: (data.ANRE && data.ANRE.aD && data.ANRE.aD.dIr) || '',
        agentGroup: (data.ANRE && data.ANRE.aG) || ''
      };
    }

    function parseAgentBusyEvent(data) {
      return {
        category: 'LogicalDeviceFeature',
        name: 'AgentBusyEvent',
        agentReady: false,
        huntGroupList: parseHuntGroupList(data.ABE)
      };
    }

    function parseAgentWorkingAfterCallEvent(data) {
      return {
        category: 'LogicalDeviceFeature',
        name: 'AgentWorkingAfterCallEvent',
        agentReady: false,
        huntGroupList: parseHuntGroupList(data.AWAC)
      };
    }

    function parseDeviceCapsChangedEvent(event) {
      var data = event.DCCE;
      var parsedEvent = {
        category: 'DeviceMaintenance'
      };
      if (data.extn && data.extn.pDa && data.extn.pDa.prvt && data.extn.pDa.prvt.zdP) {
        parsedEvent.name = data.extn.pDa.prvt.zdP === 'true' ? 'BackInServiceEvent' : 'OutOfServiceEvent';
      }
      return parsedEvent;
    }

    function parseAgentLoggedOffEvent(data) {
      return {
        category: 'LogicalDeviceFeature',
        name: 'AgentLoggedOffEvent',
        huntGroupList: parseHuntGroupList(data.ALOE)
      };
    }

    function parseAgentLoggedOnEvent(data) {
      return {
        category: 'LogicalDeviceFeature',
        name: 'AgentLoggedOnEvent',
        huntGroupList: parseHuntGroupList(data.ALOEt)
      };
    }

    // Generate Requests
    function genAlternateRequest(data) {
      return {
        ACl: {
          hC: {
            cID: data.heldCall.cID,
            dID: data.heldCall.dID
          },
          atC: {
            cID: data.activeCall.cID,
            dID: data.activeCall.dID
          }
        }
      };
    }

    function genConferenceRequest(data) {
      return {
        CoC: {
          hC: {
            cID: data.heldCall.cID,
            dID: data.heldCall.dID
          },
          atC: {
            cID: data.activeCall.cID,
            dID: data.activeCall.dID
          }
        }
      };
    }

    function genAnswerRequest(data) {
      return {
        AnC: {
          cTBAd: {
            cID: data.callToBeAnswered.cID,
            dID: data.callToBeAnswered.dID
          }
        }
      };
    }

    function genAcceptCall(data) {
      return {
        AC: {
          cTBA: {
            cID: data.callToBeAccepted.cID,
            dID: data.callToBeAccepted.dID
          }
        }
      };
    }

    function genGroupPickupCall(data) {
      return {
        GPC: {
          nD: data.newDestination
        }
      };
    }

    function genMakeCallRequest(data) {
      return {
        MC: {
          caD: data.callingDevice,
          cDN: data.calledDirectoryNumber,
          aOe: data.autoOriginate
        }
      };
    }

    function genClearConnectionRequest(data) {
      return {
        CCn: {
          coTBC: {
            cID: data.connectionToBeCleared.cID,
            dID: data.connectionToBeCleared.dID
          },
          rsn: data.reason
        }
      };
    }

    function genDeflectRequest(data) {
      var shortTagJson = {
        cTBD: {
          cID: data.callToBeDiverted.cID,
          dID: data.callToBeDiverted.dID
        },
        nD: data.newDestination
      };
      if (data.autoAnswer) {
        shortTagJson.extn = {
          pDa: {
            prvt: {
              zdT: 'Auto-Answer'
            }
          }
        };
      }
      return {
        DCl: shortTagJson
      };
    }

    function genGenerateDigitsRequest(data) {
      return {
        GD: {
          cTSD: {
            cID: data.connectionToSendDigits.cID,
            dID: data.connectionToSendDigits.dID
          },
          cTS: data.charactersToSend
        }
      };
    }

    function genGetDoNotDisturb(data) {
      return {
        GDND: {
          dvc: data.device
        }
      };
    }

    function genGetForwarding(data) {
      return {
        GF: {
          dvc: data.device
        }
      };
    }

    function genGetMessageWaiting(data) {
      return {
        GMWI: {
          dvc: data.device
        }
      };
    }

    function genHoldRequest(data) {
      var shortTagJson = {
        cTBH: {
          cID: data.callToBeHeld.cID,
          dID: data.callToBeHeld.dID
        }
      };

      if (data.holdOptions) {
        shortTagJson.extn = {
          zhOs: data.holdOptions
        };
      }

      return {
        HC: shortTagJson
      };
    }

    function genReconnectRequest(data) {
      return {
        RC: {
          hC: {
            cID: data.heldCall.cID,
            dID: data.heldCall.dID
          },
          atC: {
            cID: data.activeCall.cID || undefined,
            dID: data.activeCall.dID || undefined
          }
        }
      };
    }

    function genRetrieveRequest(data) {
      return {
        RCl: {
          cTBRd: {
            cID: data.callToBeRetrieved.cID,
            dID: data.callToBeRetrieved.dID
          }
        }
      };
    }

    function genSetBusy(data) {
      return {
        zSBN: {
          dvc: data.device,
          zbO: data.busyState
        }
      };
    }

    function genSetDoNotDisturb(data) {
      return {
        SDND: {
          dvc: data.device,
          dNDO: data.doNotDisturbOn
        }
      };
    }

    function genSetForwarding(data) {
      var shortTagJson = {
        SF: {
          dvc: data.device,
          aF: data.activateForward,
          fDNN: data.forwardDN,
          fTe: data.forwardingType,
          riC: data.ringCount
        }
      };
      var prvt = {};
      if (data.staticOndActive !== undefined) {
        prvt = {
          zsOA: data.staticOndActive === true ? 'true' : 'false',
          zsOD: data.staticOndDN || ''
        };
      } else if (data.voicemailActive !== undefined || data.voicemailRingDuration !== undefined) {
        prvt = {
          zvMA: typeof data.voicemailActive === 'boolean' ? data.voicemailActive.toString() : undefined,
          zvMRD: data.voicemailRingDuration ? data.voicemailRingDuration.toString() : undefined
        };
      } else {
        if (data.mainRingDuration !== undefined) {
          prvt.zmRD = data.mainRingDuration.toString() || '';
        }
        if (data.clientRingDuration !== undefined) {
          prvt.zcRD = data.clientRingDuration.toString() || '';
        }
        if (data.cellRingDuration !== undefined) {
          prvt.zcpRD = data.cellRingDuration.toString() || '';
        }
        if (data.alternativeNumber !== undefined) {
          prvt.zcPN = data.alternativeNumber;
        }
        if (data.routeToCell !== undefined) {
          prvt.zrTCN = data.routeToCell ? 'true' : 'false';
        }
      }
      if (Object.keys(prvt).length > 0) {
        shortTagJson.SF.extn = {
          pDa: {
            prvt: prvt
          }
        };
      }
      return shortTagJson;
    }

    function genSetMicrophoneMute(data) {
      return {
        SMM: {
          dvc: data.device,
          mMO: data.mute
        }
      };
    }

    function genGetMicrophoneMute(data) {
      return {
        GMM: {
          dvc: data.device
        }
      };
    }

    function genSnapshotCall(data) {
      return {
        SC: {
          sO: {
            cID: data.snapshotObject.cID,
            dID: data.snapshotObject.dID
          }
        }
      };
    }

    function genSnapshotDevice(data) {
      var shortTagJson = {
        sO: data.snapshotObject
      };
      if (data.osmo) {
        shortTagJson.extn = {
          pDa: {
            prvt: {
              zxsm: data.osmo
            }
          }
        };
      }
      return {
        SDe: shortTagJson
      };
    }

    function genGetLogicalDeviceInformation(data) {
      var shortTagJson = {
        dvc: data.device
      };
      return {
        GLDI: shortTagJson
      };
    }

    function genSSTRequest(data) {
      var shortTagJson = {
        atC: {
          cID: data.activeCall.cID,
          dID: data.activeCall.dID
        },
        tTo: data.transferredTo
      };
      if (data.autoAnswer || data.seamlessHandover) {
        var prvt = {};
        if (data.autoAnswer) {
          prvt.ztTgt = 'Auto-Answer';
        }
        if (data.seamlessHandover) {
          prvt.zseHo = 'true';
        }
        shortTagJson.extn = {
          pDa: {
            prvt: prvt
          }
        };
      }
      return {
        SSTC: shortTagJson
      };
    }

    function genConsultationCall(data) {
      return {
        CnC: {
          cnD: data.consultedDevice || undefined,
          eCl: {
            cID: data.existingCall.cID,
            dID: data.existingCall.dID
          }
        }
      };
    }

    function genCallBackCallRelated(data) {
      return {
        CB: {
          cC: {
            cID: data.callBackConnection.cID,
            dID: data.callBackConnection.dID
          }
        }
      };
    }

    function genCallBackNonCallRelated(data) {
      return {
        CBNC: {
          orD: data.orD,
          tD: data.tD
        }
      };
    }

    function genCancelCallBack(data) {
      return {
        CCB: {
          orD: data.orD,
          tD: data.tD
        }
      };
    }

    function genTransferCall(data) {
      return {
        TC: {
          hC: {
            cID: data.heldCall.cID,
            dID: data.heldCall.dID
          },
          atC: {
            cID: data.activeCall.cID,
            dID: data.activeCall.dID
          }
        }
      };
    }

    function genGetConfiguration() {
      return {
        zGCD: {
          zcCA: 'Internet',
          zdMO: 'Chrome',
          zdOS: 'WebRTC'
        }
      };
    }

    function genCallLogSnapShot(data) {
      return {
        CLS: {
          sub: data.device
        }
      };
    }

    function genGetAgentState(data) {
      var shortTagJson = {
        dvc: data.device
      };

      if (data.group) {
        shortTagJson.aG = data.group;
      }

      return {
        GAS: shortTagJson
      };
    }

    function genSetAgentState(data) {
      var shortTagJson = {
        dvc: data.device,
        rAS: data.state
      };

      if (data.group && data.localUser) {
        if (data.localUser.isOsBizCTIEnabled) {
          shortTagJson.grp = data.group;
        } else if (data.localUser.isOSV) {
          shortTagJson.extn = {
            pDa: {
              prvt: {
                zhG: data.group
              }
            }
          };
        }
      }

      return {
        SAS: shortTagJson
      };
    }
  }

  // Exports
  unifyPhone.CstaParser = CstaParser;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var logger = unifyPhone.logger;

  // eslint-disable-next-line max-params, max-lines-per-function
  function LogSvcImpl($rootScope, $timeout) {
    // NOSONAR

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var NOP = function () {};
    var TIMEOUT_GET_LOG_FILES = 5000;
    var BUTTON_PREFIX = 'Button press: '; // Standard prefix for logging actions initiated by a button, e.x call related buttons

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    this.debug = logger.debug;
    this.info = logger.info;
    this.warn = logger.warning;
    this.error = logger.error;
    this.msgSend = logger.msgSend || logger.info;
    this.msgRcvd = logger.msgRcvd || logger.info;

    this.logMsg = logger.logMsg || NOP;

    this.setLevel = logger.setLevel || NOP;
    this.getLevel = logger.getLevel || NOP;

    this.show = logger.show || NOP;
    this.hide = logger.hide || NOP;

    this.enablePopup = logger.enablePopup || NOP;
    this.disablePopup = logger.disablePopup || NOP;
    this.isPopupEnabled =
      logger.isPopupEnabled ||
      function () {
        return false;
      };

    this.isFileLogAvailable =
      logger.isFileLogAvailable ||
      function () {
        return false;
      };

    // This function is called to log actions initiated by button, e.x call related buttons
    this.buttonPressed = function (txt, param) {
      logger.info(BUTTON_PREFIX + txt, param);
    };

    // This function is called for both "Report Issue" and "Log file management" via Debug options. The 5 seconds timeout
    // would cause the log files not shown when a user selects "Log file management". So the input parameter (dontTimeOut) is used
    // to indicate if a timeout is needed. For the "Log file management" via Debug options case, dontTimeOut is set to true when this
    // function is called.
    this.getLogFilesForDownload = function (cb, dontTimeOut) {
      if (typeof cb !== 'function') {
        return;
      }
      if (typeof logger.getLogFiles !== 'function') {
        cb([]);
        return;
      }
      // Log the state of the UnifyPhone Lab features
      logger.info('[LogSvc]: Snapshot of UnifyPhone Lab features: ', $rootScope.unifyPhoneLabs);

      // Log the user's telephony data, if available
      if ($rootScope.localUser && $rootScope.localUser.telephonyConnectorData) {
        logger.info('[LogSvc]: User telephony data: ', $rootScope.localUser.telephonyConnectorData);
      }

      var timerPromise = null;
      if (!dontTimeOut) {
        timerPromise = $timeout(function () {
          timerPromise = null;
          cb([]);
        }, TIMEOUT_GET_LOG_FILES);
      }
      logger.getLogFiles(function (data) {
        if (!dontTimeOut && !timerPromise) {
          return;
        }
        if (timerPromise) {
          $timeout.cancel(timerPromise);
          timerPromise = null;
        }
        $rootScope.$apply(function () {
          cb(data);
        });
      }, !dontTimeOut);
    };

    this.startFileLogger = logger.startFileLogger || NOP;

    this.removeLogFiles = function (cb) {
      if (typeof logger.removeLogFiles !== 'function') {
        cb && cb(null);
        return;
      }
      var wrapperCb;
      if (typeof cb === 'function') {
        wrapperCb = function (logFiles) {
          $rootScope.$apply(function () {
            cb(logFiles);
          });
        };
      }
      logger.removeLogFiles(wrapperCb);
    };

    this.setUserInfo = logger.setUserInfo || NOP;

    this.setExtensionVersion = logger.setExtensionVersion || NOP;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.LogSvcImpl = LogSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Utils = unifyPhone.Utils;

  // FAQ article IDs
  var FaqArticle = {
    ACCESSIBILITY: '125328',
    AGC: '89415',
    DEVICE_ACCESS: '48742',
    JABRA: '112718',
    LOGIN: '37428',
    PLANTRONICS: '115017',
    SHORTCUTS: Utils.isMacOs ? '144499' : '144490',
    TELEPHONY: '103733'
  };

  // eslint-disable-next-line max-params, max-lines-per-function
  function UtilSvcImpl($rootScope, $q, $timeout, LogSvc) {
    // NOSONAR
    LogSvc.debug('New Service: UtilSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _self = this;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    /**
     * Transforms callback-based function -- func(arg1, arg2 .. argN, callback) -- into
     * an $q-compatible Promise. Promisify provides a default callback of the form (error, result)
     * and rejects when `error` is truthy. You can also supply `this` object as the second argument.
     *
     * @param {function} original - The function to promisify
     * @param {object} target - `this` object
     * @return {function} A promisified version of `original`
     */
    this.promisify = function (original, target) {
      return function () {
        var args = Array.prototype.slice.call(arguments);
        return new $q(function (resolve, reject) {
          args.push(function callback(err) {
            if (err) {
              reject(err);
              return;
            }
            var values = Array.prototype.slice.call(arguments);
            values.shift();
            switch (values.length) {
              case 0:
                resolve();
                break;
              case 1:
                resolve(values[0]);
                break;
              default:
                resolve(values);
                break;
            }
          });
          original.apply(target, args);
        });
      };
    };

    this.getFaqUrl = function (articleId) {
      if ($rootScope.localUser) {
        if ($rootScope.localUser.faqUrl && Utils.DEFAULT_FAQ_URL !== $rootScope.localUser.faqUrl) {
          // Use custom FAQ URL
          return $rootScope.localUser.faqUrl + (articleId || '');
        } else if ($rootScope.localUser.helpUrl && Utils.DEFAULT_HELP_URL !== $rootScope.localUser.helpUrl) {
          // Return custom help URL
          return $rootScope.localUser.helpUrl;
        }
      }
      if (articleId === 'HELP') {
        // Return default help URL
        return Utils.DEFAULT_HELP_URL;
      }
      // Use default FAQ URL
      return Utils.DEFAULT_FAQ_URL + (articleId ? '?articleId=' + articleId : '');
    };

    this.isTenantAdmin = function (cb, tenantContext) {
      if (!$rootScope.localUser) {
        return false;
      }
      if (tenantContext && tenantContext.tenantId !== $rootScope.localUser.tenantId) {
        return _self.isPartnerAdmin(cb);
      }
      if ($rootScope.localUser.isTenantAdmin) {
        return true;
      }

      LogSvc.warn('[UtilSvc]: Local user is not a tenant admin');
      cb &&
        $timeout(function () {
          cb('Not tenant admin');
        });
      return false;
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.UtilSvcImpl = UtilSvcImpl;

  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.FaqArticle = FaqArticle;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Import
  var BaseCall = unifyPhone.BaseCall;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-params, max-lines-per-function
  function PubSubSvcImpl(LogSvc) {
    // NOSONAR
    LogSvc.debug('New Service: PubSubSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _cache = {};
    var _cacheMobile = {};
    var _cacheOnce = {};
    var _cacheAll = [];

    function send(item, args) {
      try {
        if (args === undefined || args === null) {
          args = [];
        } else if (!Array.isArray(args)) {
          args = [args];
        }
        item.apply(null, args);
      } catch (e) {
        LogSvc.error(e);
      }
    }

    function publish(cache, topic, args) {
      // Loop through a copy of the array in case the
      // app unsubscribes while publishing the events.
      cache[topic] &&
        cache[topic].slice(0).forEach(function (item) {
          send(item, args);
        });
    }

    function publishMobile(topic, args) {
      if (_cacheMobile[topic]) {
        if (args === undefined || args === null) {
          args = [];
        } else if (!Array.isArray(args)) {
          args = [args];
        }
        args = args.map(function (arg) {
          if (arg && typeof arg === 'object') {
            // Return lightweight conversation and call objects without data that
            // is not needed by the mobile applications.
            if (arg.isConversationObject) {
              return Utils.trimConvForMobile(arg);
            }
            if (arg instanceof BaseCall) {
              return Utils.trimCallForMobile(arg);
            }
          }
          return arg;
        });

        // Loop through a copy of the array in case the
        // app unsubscribes while publishing the events.
        _cacheMobile[topic].slice(0).forEach(function (item) {
          send(item, args);
        });
      }
    }

    function publishAll(topic, args) {
      args = args ? Array.prototype.slice.call(args) : [];
      args.unshift(topic);
      // Loop through a copy of the array in case the
      // app unsubscribes while publishing the events.
      _cacheAll.slice(0).forEach(function (item) {
        send(item, args);
      });
    }

    function subscribe(cache, topic, callback) {
      if (!cache[topic]) {
        cache[topic] = [callback];
      } else if (!cache[topic].includes(callback)) {
        cache[topic].push(callback);
      }
    }

    function unsubscribe(cache, topic, callback) {
      if (cache[topic]) {
        var idx = cache[topic].indexOf(callback);
        if (idx >= 0) {
          cache[topic].splice(idx, 1);
        }
      }
    }

    function subscribeAll(callback) {
      if (!_cacheAll.includes(callback)) {
        _cacheAll.push(callback);
      }
    }

    function unsubscribeAll(callback) {
      var idx = _cacheAll.indexOf(callback);
      if (idx >= 0) {
        _cacheAll.splice(idx, 1);
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.publish = function (topic, args) {
      if (!_cache[topic] && !_cacheMobile[topic] && !_cacheOnce[topic] && !_cacheAll.length) {
        return;
      }

      LogSvc.debug('[PubSubSvc]: Publishing ' + topic + ' event');
      publish(_cache, topic, args);
      publishMobile(topic, args);
      publish(_cacheOnce, topic, args);
      publishAll(topic, args);

      delete _cacheOnce[topic];
    };

    this.subscribe = function (topic, callback) {
      subscribe(_cache, topic, callback);
    };

    this.unsubscribe = function (topic, callback) {
      unsubscribe(_cache, topic, callback);
    };

    this.subscribeOnce = function (topic, callback) {
      subscribe(_cacheOnce, topic, callback);
    };

    this.unsubscribeOnce = function (topic, callback) {
      unsubscribe(_cacheOnce, topic, callback);
    };

    this.subscribeMobile = function (topic, callback) {
      subscribe(_cacheMobile, topic, callback);
    };

    this.unsubscribeMobile = function (topic, callback) {
      unsubscribe(_cacheMobile, topic, callback);
    };

    this.subscribeAll = function (callback) {
      subscribeAll(callback);
    };

    this.unsubscribeAll = function (callback) {
      unsubscribeAll(callback);
    };

    this.resetSubscriptions = function () {
      _cache = {};
      _cacheMobile = {};
      _cacheOnce = {};
      _cacheAll = [];
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.PubSubSvcImpl = PubSubSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global require*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  ///////////////////////////////////////////////////////////////////////////////////////
  // HttpSvc Implementation
  // This is a separate service to allow the mobile client to injet their own
  // implementation.
  ///////////////////////////////////////////////////////////////////////////////////////

  // eslint-disable-next-line max-params, max-lines-per-function
  function HttpSvcImpl($rootScope, $window, $document, $timeout, $http, $q, LogSvc) {
    // NOSONAR
    LogSvc.debug('New Service: HttpSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var URL_PREFIX = 'resources/i18n/resources-locale_';
    var LOCALE_URL = '/api/data/acceptLanguage'; // Retrieve supported browser configured language through server

    function handleDownload(blob, file, options) {
      var url = $window.URL.createObjectURL(blob);
      var a = $document[0].createElement('a');
      var name = file.fileName;
      try {
        a.style.display = 'none';
        a.href = url;
        a.download = name;
        // we use target 'blank' for the web ms teams microsoft app
        if (options && options.isTeamsApp) {
          a.target = 'blank';
        }
        $document[0].body.appendChild(a);
        a.click();
      } catch (e) {
        LogSvc.error('[HttpSvc]: Error simulating anchor click. ', e);
      }

      // Do not revoke the object URL immediate as it causes download failure in some browsers.
      $timeout(
        function () {
          $window.URL.revokeObjectURL(url);
        },
        500,
        false
      );

      $document[0].body.removeChild(a);
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.retrieveLanguageMap = function (lang, successCb, errorCb) {
      lang = lang || 'default';

      // Build the url to retrieve the localized resource file
      var url = URL_PREFIX + lang + '.json';

      successCb = successCb || function () {};
      errorCb = errorCb || function () {};

      // Request the resource file
      LogSvc.debug('[HttpSvc]: Retrieving language map for ', lang);
      $http
        .get(url)
        .then(function (response) {
          successCb(response.data);
        })
        .catch(function (response) {
          errorCb(response.data, response.status);
        });
    };

    this.getBrowserLocale = function () {
      var deferred = $q.defer();
      LogSvc.debug('[HttpSvc]: Retrieving supported browser locale');
      $http
        .get(LOCALE_URL)
        .then(function (response) {
          var locale = response.data && response.data.locale;
          LogSvc.info('[HttpSvc]: Retrieved browser locale: ', locale);
          deferred.resolve(locale);
        })
        .catch(function (response) {
          LogSvc.error('[HttpSvc]: Could not retrieve browser locale: ', response.status);
          deferred.resolve();
        });
      return deferred.promise;
    };

    this.downloadFile = function (file, doneCb, progressCb, options) {
      LogSvc.debug('[HttpSvc]: Download file ', file.uri);

      var xhr = new XMLHttpRequest();
      xhr.open('GET', file.uri, true);
      xhr.responseType = 'blob';

      xhr.onreadystatechange = function () {
        if (xhr.readyState !== xhr.DONE) {
          return;
        }

        if (xhr.status === 200) {
          var blob = new Blob([xhr.response], { type: file.mimeType || 'text/plain' });
          // If can't return reference to file, polyfill return blob object.
          if (!blob) {
            blob = xhr.response;
          }

          if (blob) {
            handleDownload(blob, file, options);
          }
          if (doneCb) {
            $rootScope.$apply(function () {
              doneCb(null);
            });
          }
        } else {
          LogSvc.error('[HttpSvc]: Download failed with response code ', xhr.status);
          if (doneCb) {
            $rootScope.$apply(function () {
              doneCb('Download Failed');
            });
          }
        }
      };

      if (typeof progressCb === 'function') {
        xhr.addEventListener(
          'progress',
          function (e) {
            progressCb(e.loaded, xhr.readyState);
          },
          false
        );
      }

      xhr.send();
    };

    this.downloadObjectDataAsFile = function (data, file, type) {
      var blob = new Blob([data], { type: type || 'text/plain' });
      handleDownload(blob, file);
    };

    this.downloadBlobAsFile = function (blob, file) {
      handleDownload(blob, file);
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.HttpSvcImpl = HttpSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  ///////////////////////////////////////////////////////////////////////////////////////
  // FileApiSvc Implementation
  // Service to upload/delete files via fileapi
  ///////////////////////////////////////////////////////////////////////////////////////

  // eslint-disable-next-line max-params, max-lines-per-function
  function FileApiSvcImpl($http, $q, LogSvc) {
    // NOSONAR
    LogSvc.debug('New Service: FileApiSvcImpl');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    /**
     * Remove file using fileapi.
     *
     * @function
     * @returns {Object} Promise object.
     */
    this.deleteFile = function (fileId) {
      var deferred = $q.defer();
      if (!fileId) {
        return deferred.reject('missing parameters');
      }

      LogSvc.debug('[FileApiSvc]: Deleting file ', fileId);

      var deleteUrl = unifyPhone.__domain + '/api' + unifyPhone.__path + '/fileapi?fileid=' + fileId;

      $http
        .delete(deleteUrl)
        .then(function () {
          LogSvc.debug('[FileApiSvc]: File was successfully deleted - fileId = ', fileId);
          deferred.resolve();
        })
        .catch(function (res) {
          LogSvc.error('[FileApiSvc]: Failed to delete file, fileId = ' + fileId + '. ', res && res.status);
          deferred.reject(res.status);
        });
      return deferred.promise;
    };

    /**
     * Upload blob using fileapi.
     *
     * @function
     * @returns {Object} Promise object resolving a file ID.
     */
    this.uploadBlob = function (fileName, blob) {
      if (!fileName || !blob) {
        LogSvc.warn('[FileApiSvc]: uploadBlob - Missing filename or file');
        return $q.reject('Missing file parameter');
      } else if (blob.size <= 0) {
        LogSvc.warn('[FileApiSvc]: uploadBlob - Empty file');
        return $q.reject('The file is empty');
      }

      var deferred = $q.defer();
      LogSvc.debug('[FileApiSvc]: Uploading file (blob) ', { name: fileName, type: blob.type, size: blob.size });

      $http
        .post(unifyPhone.__domain + '/api' + unifyPhone.__path + '/fileapi', blob, {
          headers: {
            'Content-Type': blob.type,
            'Content-Disposition': 'attachment; filename="' + fileName + '"'
          },
          transformRequest: angular.identity
        })
        .then(function (res) {
          var id = res.data[0].id;
          if (id) {
            LogSvc.debug('[FileApiSvc]: Uploaded file - fileId = ', id);
            deferred.resolve(id);
          } else {
            deferred.reject('Upload failed. Missing file ID.');
          }
        })
        .catch(function (res) {
          LogSvc.error('[FileApiSvc]: Upload failed for ' + fileName + ' - error = ' + res.status + ' ' + res.statusText);
          deferred.reject(res.status);
        });
      return deferred.promise;
    };

    /**
     * Upload music on hold file to CMP-Next server
     *
     * @function
     * @returns {Object} Promise object resolving an interceptId
     */
    this.uploadMohFile = function (blob, shortTenantId) {
      return new $q(function (resolve, reject) {
        if (!blob || !shortTenantId) {
          reject('Missing required parameters.');
        } else {
          LogSvc.debug('[FileApiSvc]: Uploading music on hold file to CMP-Next. ', { type: blob.type, size: blob.size });
          $http
            .post(unifyPhone.__domain + '/api' + unifyPhone.__path + '/cmpnext', blob, {
              headers: {
                'Content-Type': blob.type,
                'Content-Disposition': 'attachment',
                'X-CmpNext': 'mlhg-music-on-hold',
                'X-CmpNext-TenantId': shortTenantId
              },
              transformRequest: angular.identity
            })
            .then(function (res) {
              var interceptId = res.data;
              if (interceptId) {
                LogSvc.debug('[FileApiSvc]: Successfully uploaded music on hold and fetched interceptId = ', interceptId);
                resolve(interceptId);
              } else {
                reject('Upload of music on hold file failed. Missing interceptId');
              }
            })
            .catch(function (res) {
              LogSvc.error('[FileApiSvc]: Upload of music on hold file failed. ', res.status + ' ' + res.statusText);
              reject(res.status);
            });
        }
      });
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////

    return this;
  }

  // Exports
  unifyPhone.FileApiSvcImpl = FileApiSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var Utils = unifyPhone.Utils;

  /*
   * Localization Service
   *
   * localize (value, params) : localizes a given value combined with parameters if any
   *       value : the key of the resource to be localized (must start with 'res_')
   *       params : array of string parameters to be appended on localized message.
   *                Order of parameters inside the array matters as the first will replace %%1 in the
   *                resource value, the second will replace %%2 etc.
   *  Example:
   *   - If we have a resource "res_keyToLocalize" : "This is the localization service" inside the resources file
   *  then a call to localize function would be
   *       localize('res_keyToLocalize')
   *  This would return "This is the localization service".
   *
   *  - If we have a resource "_keyToLocalizeWithParameters" : "Search returned %%1 results in %%2 seconds"
   *  then a call to localize function would be
   *       localize('res_keyToLocalizeWithParameters', [250,5])
   *  This would return "Search returned 250 results in 5 seconds".
   *
   *  Same result will appear if we use the '%##%' separator and call
   *       localize('res_keyToLocalizeWithParameters%##%250%##%5')
   *  This would return "Search returned 250 results in 5 seconds" again and is
   *  useful when we want to localize a msg that is returned in one string.
   *
   *  We can use both ('%##%' and params) if we want.
   *  The parameters following '%##%' will be appended to the params array
   *  e.g. "_keyToLocalizeWithParameters" : "Search returned %%1 results in %%2 minutes and %%3 seconds"
   *       localize('res_keyToLocalizeWithParameters%##%15', [250, 1])
   *  would return "Search returned 250 results in 1 minute 15 seconds"
   *
   *  If no localization key is found, the same key is returned
   *  If key given is invalid (null/undefined/not starting with 'res_') empty string is returned
   *
   */

  // eslint-disable-next-line max-params, max-lines-per-function
  function LocalizeSvcImpl($rootScope, $window, $locale, $q, LogSvc, UtilSvc, PubSubSvc, HttpSvc) {
    // NOSONAR
    LogSvc.debug('New Service: LocalizeSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var DEFAULT_LANGUAGE = 'en-US';
    var RESOURCE_VAR = '%%';
    var RESOURCE_PARAMS_VAR = '%##%';
    var FAQ_PARAM_REGEX = /%FAQ_([^%]+)%/g;

    var RELEASE_NOTES_URL_PREFIX = 'resources/releaseNotes/releaseNotes_';

    var _that = this;
    var _language = DEFAULT_LANGUAGE; // The active language
    var _default = {}; // The default resources
    var _resourcesInitialized = false;
    var _browserSettingsLocale; // Locale that is configured in browser settings, need to query Access Server to retrieve it

    var _languageList = [
      {
        value: 'en-US',
        shortValue: 'en',
        name: 'English',
        nativeName: 'English',
        resourceName: 'res_LangEnglish',
        locale: Constants.Locale.EN_US
      },
      {
        value: 'de-DE',
        shortValue: 'de',
        name: 'German',
        nativeName: 'Deutsch',
        resourceName: 'res_LangGerman',
        locale: Constants.Locale.DE_DE
      },
      {
        value: 'fr-FR',
        shortValue: 'fr',
        name: 'French',
        nativeName: 'Français',
        resourceName: 'res_LangFrench',
        locale: Constants.Locale.FR_FR
      },
      {
        value: 'es-ES',
        shortValue: 'es',
        name: 'Spanish',
        nativeName: 'Español',
        resourceName: 'res_LangSpanish',
        locale: Constants.Locale.ES_ES
      },
      {
        value: 'eu-ES',
        shortValue: 'eu',
        name: 'Basque',
        nativeName: 'Euskara',
        resourceName: 'res_LangBasque',
        locale: Constants.Locale.EU_ES
      },
      {
        value: 'ca-ES',
        shortValue: 'ca',
        name: 'Catalan',
        nativeName: 'Català',
        resourceName: 'res_LangCatalan',
        locale: Constants.Locale.CA_ES
      },
      {
        value: 'it-IT',
        shortValue: 'it',
        name: 'Italian',
        nativeName: 'Italiano',
        resourceName: 'res_LangItalian',
        locale: Constants.Locale.IT_IT
      },
      {
        value: 'nl-NL',
        shortValue: 'nl',
        name: 'Dutch',
        nativeName: 'Nederlands',
        resourceName: 'res_LangDutch',
        locale: Constants.Locale.NL_NL
      },
      {
        value: 'pt-BR',
        shortValue: 'pt',
        name: 'Portuguese (Brazil)',
        nativeName: 'Português (Brasil)',
        resourceName: 'res_LangBrazilianPortuguese',
        locale: Constants.Locale.PT_BR
      },
      {
        value: 'pt-PT',
        shortValue: 'pt',
        name: 'Portuguese',
        nativeName: 'Português',
        resourceName: 'res_LangPortuguese',
        locale: Constants.Locale.PT_PT
      }
    ];

    // Languages which are controlled via UnifyPhone Labs must start as disabled
    var _disabledLanguages = [];

    var _languages = {};
    var _languagesLocales = {};
    var _languageCodes = [];
    _languageList.forEach(function (lang) {
      _languages[lang.value] = lang;
      _languagesLocales[lang.locale] = lang;
      _languageCodes.push(lang.value);
    });
    _disabledLanguages.forEach(function (lang) {
      _languages[lang.value] = lang;
      _languagesLocales[lang.locale] = lang;
      _languageCodes.push(lang.value);
    });

    var _additionalSpokenLanguages = [
      {
        value: 'en-GB',
        shortValue: 'en',
        name: 'English (UK)',
        nativeName: 'English',
        resourceName: 'res_LangEnglishUK',
        locale: Constants.BridgeLocale.EN_GB
      }
    ];

    var _showResourceStrings = false;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function successCallback(data) {
      // store the returned array in the rootScope
      _resourcesInitialized = true;
      $rootScope.i18n.map = Utils.shallowCopy(_default);
      Object.assign($rootScope.i18n.map, data);
      prepareResources();

      LogSvc.debug('[LocalizeSvc]: Locale Loaded: ', _language);

      // Raise an event to allow other services to manipulate the resource map if needed
      LogSvc.debug('[LocalizeSvc]: Publish /i18n/resources/loaded event');
      PubSubSvc.publish('/i18n/resources/loaded');

      // Notify all interested parties that the language has been updated and the new resources are available
      raiseLanguageUpdate();
    }

    function initLocalizedResources() {
      if (_showResourceStrings) {
        // No need to load the actual text since we are not using it
        return;
      }

      if (_language === DEFAULT_LANGUAGE) {
        successCallback();
        return;
      }
      HttpSvc.retrieveLanguageMap(_language, successCallback, function () {
        // The request failed. Use the default resources.
        LogSvc.error('[LocalizeSvc]: Failed to load resources for ' + _language);
        _resourcesInitialized = true;
        $rootScope.i18n.map = _default;
        prepareResources();

        // Raise an event to allow other services to manipulate the resource map if needed
        LogSvc.debug('[LocalizeSvc]: Publish /i18n/resources/loaded event');
        PubSubSvc.publish('/i18n/resources/loaded');

        raiseLanguageUpdate();
      });
    }

    function setLanguageAndCountryNames() {
      _languageList.forEach(function (lang) {
        lang.name = $rootScope.i18n.map[lang.resourceName] || '';
      });
      _additionalSpokenLanguages.forEach(function (lang) {
        lang.name = $rootScope.i18n.map[lang.resourceName] || '';
      });
      if (Utils.CountriesArray) {
        // Set the country names
        Utils.CountriesArray.forEach(function (c) {
          c.displayName = $rootScope.i18n.map[c.name] || '';
        });
        // Now order the list alphabetically
        Utils.CountriesArray.sort(function (a, b) {
          return a.displayName.localeCompare(b.displayName);
        });
      }
    }

    function normalizeLanguageName(language) {
      if (!language) {
        return '';
      }

      var fullMatch, partialMatch;
      _languageCodes.some(function (supportedLang) {
        // First look for exact match
        if (language.toLowerCase() === supportedLang.toLowerCase()) {
          fullMatch = supportedLang;
          return true;
        }

        // Ignore the country and check if the language matches any supported language
        if (!partialMatch && language.slice(0, 2) === supportedLang.slice(0, 2)) {
          partialMatch = supportedLang;
        }
        return false;
      });
      return fullMatch || partialMatch || language;
    }

    function setDateFormats() {
      if (!$locale) {
        return;
      }
      $locale.DATETIME_FORMATS.DAY = [
        $rootScope.i18n.map.res_DateTimeWeekDaySunday,
        $rootScope.i18n.map.res_DateTimeWeekDayMonday,
        $rootScope.i18n.map.res_DateTimeWeekDayTuesday,
        $rootScope.i18n.map.res_DateTimeWeekDayWednesday,
        $rootScope.i18n.map.res_DateTimeWeekDayThursday,
        $rootScope.i18n.map.res_DateTimeWeekDayFriday,
        $rootScope.i18n.map.res_DateTimeWeekDaySaturday
      ];
      $locale.DATETIME_FORMATS.SHORTDAY = [
        $rootScope.i18n.map.res_DateTimeWeekDaySundayShort,
        $rootScope.i18n.map.res_DateTimeWeekDayMondayShort,
        $rootScope.i18n.map.res_DateTimeWeekDayTuesdayShort,
        $rootScope.i18n.map.res_DateTimeWeekDayWednesdayShort,
        $rootScope.i18n.map.res_DateTimeWeekDayThursdayShort,
        $rootScope.i18n.map.res_DateTimeWeekDayFridayShort,
        $rootScope.i18n.map.res_DateTimeWeekDaySaturdayShort
      ];
      $locale.DATETIME_FORMATS.AMPMS = [$rootScope.i18n.map.res_DateTimeAMPM_AM, $rootScope.i18n.map.res_DateTimeAMPM_PM];
      $locale.DATETIME_FORMATS.MONTH = [
        $rootScope.i18n.map.res_DateTimeMonthJanuary,
        $rootScope.i18n.map.res_DateTimeMonthFebruary,
        $rootScope.i18n.map.res_DateTimeMonthMarch,
        $rootScope.i18n.map.res_DateTimeMonthApril,
        $rootScope.i18n.map.res_DateTimeMonthMay,
        $rootScope.i18n.map.res_DateTimeMonthJune,
        $rootScope.i18n.map.res_DateTimeMonthJuly,
        $rootScope.i18n.map.res_DateTimeMonthAugust,
        $rootScope.i18n.map.res_DateTimeMonthSeptember,
        $rootScope.i18n.map.res_DateTimeMonthOctober,
        $rootScope.i18n.map.res_DateTimeMonthNovember,
        $rootScope.i18n.map.res_DateTimeMonthDecember
      ];
      $locale.DATETIME_FORMATS.SHORTMONTH = [
        $rootScope.i18n.map.res_DateTimeMonthJanuaryShort,
        $rootScope.i18n.map.res_DateTimeMonthFebruaryShort,
        $rootScope.i18n.map.res_DateTimeMonthMarchShort,
        $rootScope.i18n.map.res_DateTimeMonthAprilShort,
        $rootScope.i18n.map.res_DateTimeMonthMayShort,
        $rootScope.i18n.map.res_DateTimeMonthJuneShort,
        $rootScope.i18n.map.res_DateTimeMonthJulyShort,
        $rootScope.i18n.map.res_DateTimeMonthAugustShort,
        $rootScope.i18n.map.res_DateTimeMonthSeptemberShort,
        $rootScope.i18n.map.res_DateTimeMonthOctoberShort,
        $rootScope.i18n.map.res_DateTimeMonthNovemberShort,
        $rootScope.i18n.map.res_DateTimeMonthDecemberShort
      ];
    }

    function prepareResources() {
      var productName = unifyPhone.productName || 'Unify Phone';

      Object.keys($rootScope.i18n.map).forEach(function (key) {
        // Update product name
        $rootScope.i18n.map[key] = $rootScope.i18n.map[key].replace(/%PRODUCT_NAME%/g, productName);
      });

      setLanguageAndCountryNames();
      setDateFormats();
    }

    function raiseLanguageUpdate() {
      LogSvc.debug('[LocalizeSvc]: Publish /language/update event');
      PubSubSvc.publish('/language/update', [_language]);
    }

    function initLanguage() {
      if (!_browserSettingsLocale) {
        HttpSvc.getBrowserLocale()
          .then(function (locale) {
            _browserSettingsLocale = locale || $window.navigator.language || DEFAULT_LANGUAGE;
            LogSvc.debug('[LocalizeSvc]: Set browser language to ', _browserSettingsLocale);

            _that.setLanguage();
            PubSubSvc.publish('/language/initComplete', [_browserSettingsLocale]);
          })
          .catch(function () {
            LogSvc.error('[LocalizeSvc]: Unable to set browser language to ', _browserSettingsLocale);
          });
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.normalizeLanguageName = normalizeLanguageName;

    this.getLanguage = function () {
      return _language;
    };

    this.getLanguageOnly = function () {
      return _language && _language.substr(0, 2);
    };

    this.getLocale = function () {
      return _languages[_language].locale;
    };

    this.getLanguageName = function () {
      return _languages[_language].name;
    };

    this.getLanguageObject = function () {
      return _languages[_language];
    };

    this.getLanguageList = function () {
      return _languageList.slice();
    };

    this.getLanguagebyValue = function (value) {
      return _languages[value];
    };

    this.getBridgeLanguageByValue = function (value) {
      return (
        _languages[value] ||
        _additionalSpokenLanguages.find(function (lang) {
          return lang.value === value;
        })
      );
    };

    this.getLanguagebyLocale = function (locale) {
      if (locale === Constants.BridgeLocale.EN_GB) {
        locale = Constants.Locale.EN_US;
      }
      return _languagesLocales[locale];
    };

    this.getBridgeLanguagebyLocale = function (locale) {
      return (
        _languagesLocales[locale] ||
        _additionalSpokenLanguages.find(function (lang) {
          return lang.locale === locale;
        })
      );
    };

    /**
     * Sets language after localizeSvc is initialized with browser's locale
     */
    this.setLanguageAsync = function (value) {
      var deferred = $q.defer();
      if (_browserSettingsLocale) {
        deferred.resolve(_that.setLanguage(value));
      } else {
        PubSubSvc.subscribeOnce('/language/initComplete', function () {
          deferred.resolve(_that.setLanguage(value));
        });
      }
      return deferred.promise;
    };

    this.setLanguage = function (value) {
      if (!value) {
        // Use browser's language
        value = normalizeLanguageName(_browserSettingsLocale);
        if (!_languages[value]) {
          // Browser language is not supported. Fallback to default language.
          value = DEFAULT_LANGUAGE;
        }
      } else {
        value = normalizeLanguageName(value);
        var langObj = _languages[value];
        if (!langObj) {
          LogSvc.info('[LocalizeSvc]: Language is not supported: ', value);
          return _language;
        }
        if (_disabledLanguages.includes(langObj)) {
          LogSvc.info('[LocalizeSvc]: Language is disabled: ', value);
          return _language;
        }
      }

      if (_language === value) {
        // No changes
        if (!_resourcesInitialized) {
          _resourcesInitialized = true;
          raiseLanguageUpdate();
        }
        return _language;
      }
      _language = value;
      LogSvc.info('[LocalizeSvc]: Setting language to ', _language);
      initLocalizedResources();
      return _language;
    };

    this.getReleaseNotesFileUrl = function () {
      var suffix = 'default';
      if (_language !== DEFAULT_LANGUAGE) {
        suffix = _language;
      }
      return RELEASE_NOTES_URL_PREFIX + suffix + '.json';
    };

    this.showResourceStrings = function (show) {
      show = !!show;
      if (_showResourceStrings === show) {
        // No changes
        return true;
      }
      if (!_resourcesInitialized) {
        // Cannot change this setting until resources have been initialized
        return false;
      }
      _showResourceStrings = show;
      if (_showResourceStrings) {
        var newMap = {};
        Object.keys($rootScope.i18n.map).forEach(function (key) {
          if (key.startsWith('res_DateTime')) {
            newMap[key] = $rootScope.i18n.map[key];
          } else {
            newMap[key] = key;
            var params = $rootScope.i18n.map[key].match(/%%\d/g);
            if (params && params.length) {
              newMap[key] += ' [' + params.join(', ') + ']';
            }
          }
        });
        $rootScope.i18n.map = newMap;
        raiseLanguageUpdate();
      } else {
        initLocalizedResources();
      }
      return true;
    };

    this.disableLanguage = function (value) {
      if (value === DEFAULT_LANGUAGE) {
        LogSvc.warn('[LocalizeSvc]: Cannot disable default language');
        return false;
      }
      if (!_resourcesInitialized) {
        // Cannot disable language until resources have been initialized
        return false;
      }
      var langObj = _languages[value];
      if (!langObj || _disabledLanguages.includes(langObj)) {
        return !!langObj;
      }
      LogSvc.info('[LocalizeSvc]: Disable language ', value);

      _disabledLanguages.push(langObj);

      var idx = _languageList.indexOf(langObj);
      if (idx !== -1) {
        _languageList.splice(idx, 1);
        $rootScope.i18n.languages = _languageList.slice();
        $rootScope.i18n.spokenLanguages = _languageList.concat(_additionalSpokenLanguages);
      }

      if (_language === value) {
        _language = DEFAULT_LANGUAGE;
        LogSvc.info('[LocalizeSvc]: Setting language to ', _language);
        initLocalizedResources();
      }
      return true;
    };

    this.enableLanguage = function (value) {
      var langObj = _languages[value];
      if (!langObj || !_disabledLanguages.includes(langObj)) {
        return !!langObj;
      }
      LogSvc.info('[LocalizeSvc]: Enable language ', value);

      var idx = _disabledLanguages.indexOf(langObj);
      _disabledLanguages.splice(idx, 1);

      _languageList.push(langObj);
      $rootScope.i18n.languages = _languageList.slice();
      $rootScope.i18n.spokenLanguages = _languageList.concat(_additionalSpokenLanguages);
      return true;
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialize default resources
    ///////////////////////////////////////////////////////////////////////////////////////
    $rootScope.i18n = {
      languages: _languageList.slice(),
      spokenLanguages: _languageList.concat(_additionalSpokenLanguages),
      map: {},
      localize: function (value, params) {
        if (!value || value.length < 5 || value.substr(0, 4) !== 'res_') {
          return value;
        }
        if (_resourcesInitialized) {
          var tokens = value.split(RESOURCE_PARAMS_VAR); // Add variables using one string
          value = tokens[0]; // (used when localized text contains non localized data
          var str = $rootScope.i18n.map[value]; // e.g when it is appended with an error message returned from an external service )

          if (!str) {
            LogSvc.error('[LocalizeSvc]: Could not find localized key ' + value);
            return '';
          }

          // Replace any FAQ links
          str = str.replace(FAQ_PARAM_REGEX, function (match, articleId) {
            return UtilSvc.getFaqUrl(articleId);
          });

          if (tokens.length === 1 && !params) {
            // If no parameters given return resource found
            return str;
          }
          params = params || [];

          if (tokens.length > 1) {
            params = params.concat(tokens.slice(1)); // Extracted parameters are appended to the existing ones
          }

          params.forEach(function (param, index) {
            // Add variables using params. e.g replace %%1 with the first parameter given
            str = str.replace(new RegExp(RESOURCE_VAR + (index + 1), 'g'), param);
          });

          return str;
        }
        return '';
      }
    };

    $rootScope.localize = $rootScope.i18n.localize;

    // Retrieve the default resources
    HttpSvc.retrieveLanguageMap(null, function (data) {
      _default = data;
      $rootScope.i18n.map = data;
      prepareResources();
      LogSvc.debug('[LocalizeSvc]: Loaded default resources');

      // Raise an event to allow other services to manipulate the resource map if needed
      LogSvc.debug('[LocalizeSvc]: Publish /i18n/resources/loaded event');
      PubSubSvc.publish('/i18n/resources/loaded');

      // Initialize the language, using browser settings
      initLanguage();
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.LocalizeSvcImpl = LocalizeSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-params, max-lines-per-function
  function PopupSvcImpl($rootScope, $uibModal, $location, PubSubSvc, LogSvc) {
    // NOSONAR
    LogSvc.debug('New Service: PopupSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables and Initializations
    ///////////////////////////////////////////////////////////////////////////////////////
    var _self = this;

    function openModal(options) {
      if (options.templateUrl === 'views/modals/genericDialogModal.html' && !options.title) {
        // If the generic modal doesn't have a title then only populate aria-describedby attribute
        options.ariaData = { ariaDescribedBy: null };
      }

      var ariaData = options.ariaData;

      // We don't always want to fill in both aria properties (aria-labelledby and aria-describedby).
      // If we want to add only one then we need to pass an option property named ariaData.
      // e.g.: options.ariaData = {ariaLabelledBy: null};
      if (typeof ariaData === 'object') {
        ['ariaLabelledBy', 'ariaDescribedBy'].forEach(function (propertyName) {
          if (ariaData.hasOwnProperty(propertyName)) {
            // Use value from options.ariaData or options or autogenerate.
            options[propertyName] = ariaData[propertyName] || options[propertyName] || Utils.rstring();
          } else {
            delete options[propertyName];
          }
        });
      } else {
        // No other configuration found, populate both (original values or random strings)
        options.ariaLabelledBy = options.ariaLabelledBy || Utils.rstring();
        options.ariaDescribedBy = options.ariaDescribedBy || Utils.rstring();
      }

      var a11yAttrs = function () {
        return {
          ariaLabelledBy: options.ariaLabelledBy,
          ariaDescribedBy: options.ariaDescribedBy
        };
      };
      options.resolve = options.resolve || { a11yAttrs: a11yAttrs };
      if (!angular.isFunction(options.resolve.a11yAttrs)) {
        options.resolve.a11yAttrs = a11yAttrs;
      }

      var data = $uibModal.open(options);
      if (options.backdrop !== false) {
        data.opened.then(function () {
          PubSubSvc.publish('/popup/opened');
        });

        data.result
          .catch(function () {
            // Handle reject to prevent 'Possibly unhandled rejection' error
          })
          .finally(function () {
            PubSubSvc.publish('/popup/closed');
          });
      }
      return data;
    }

    function getClasslist(action, alert) {
      var classList = [];
      if (!action && !alert) {
        classList.push('read-only');
      }
      return classList.join(' ');
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    /**
     * Used to display custom modals.
     * @param options {object} Options to configure the modal. Same API as $dialog directly
     * @example: PopupSvc.openCustomModal({
     *     backdropClick: true,
     *     templateUrl: 'views/modals/releaseNotes.html',
     *     controller: 'ReleaseNotesCtrl',
     *     resolve = {
     *       releaseNotes: function () {
     *         return releaseNotes;
     *       }
     *     }
     *  });
     */
    this.openCustomModal = function (options) {
      if (!options.templateUrl && !options.component) {
        return null;
      }
      LogSvc.debug('PopupSvc.openCustomModal: ' + options.templateUrl);
      return openModal(options);
    };

    /**
     * Used to display generic modals.
     * @param options {object} Options to configure the modal.
     * @options.templateUrl {String} The template to be used by the modal.
     * @options.controller {String} The controller to be used by the modal.
     * @options.message {String} The message to be displayed in the modal body. This may also be a resource name.
     * @options.title {String} The title to be displayed in the modal header.
     * @options.messageParams {Array} Message-specific localization parameters.
     * @options.titleParams {Array} Title-specific localization parameters.
     * @options.size {String} The modal's size. Can be large, medium, small, mini.
     * @options.scrollBox {Boolean} True if the message should be displayed in the scrollbox area
     * @options.backdrop {Boolean} Controls presence of a backdrop.
     * @options.backdropClass {String} A custom backdrop class.
     * @example: PopupSvc.openGenericModal({
     *     title: 'res_TermsOfService',
     *     message: LegalTexts[$rootScope.localUser.locale].terms,
     *     size: 'large',
     *     scrollBox: true,
     *     backdrop: true
     *  });
     */
    this.openGenericModal = function (options) {
      if (!options.message) {
        return null;
      }
      var genericOptions = {
        templateUrl: options.templateUrl || 'views/modals/genericDialogModal.html',
        controller: options.controller || 'GenericDialogCtrl',
        title: options.title,
        titleParams: options.titleParams,
        message: options.message,
        messageParams: options.messageParams,
        type: 'generic',
        action: options.action || false, // indicates that the modal should display yes/no buttons
        yesLabel: options.yesLabel || undefined,
        noLabel: options.noLabel || undefined,
        size: options.size || 'large',
        scrollBox: options.scrollBox,
        backdrop: !options.hasOwnProperty('backdrop') || options.backdrop === 'true' ? 'modal-backdrop' : options.backdrop,
        backdropClass: options.backdropClass,
        messageClickAction: options.messageClickAction,
        modalClasslist: function () {
          return getClasslist(options.action, options.alert);
        },
        resolve: {
          options: function () {
            return genericOptions;
          }
        }
      };
      LogSvc.debug('PopupSvc.openGenericModal: ' + genericOptions.message);
      return openModal(genericOptions);
    };

    /**
     * Used to display info messages.
     * @param options {object} Options to configure the modal.
     * @options.id {String} Unique ID that identifies this message in case it needs to be closed
     * @options.message {String} The info message to be displayed in the modal body.
     * @options.messageParams {Array} Message-specific localization parameters.
     * @options.manual {Boolean} If true, then a dialog object will be returned.
     * @options.yesLabel {String} Text of additional button.
     * @options.noLabel {String} Text of additional button.
     * @options.infoYesClickAction {Function} Action performed on yesLabel click.
     * @options.infoNoClickAction {Function} Action performed on noLabel click.
     * @options.messageClickAction {Function} Action performed on link click inside popup.
     * @options.onManualClose {Function} Action performed when user closes popup.
     * @options.className {string} unique class name of the popup
     * @example PopupSvc.info({message: 'res_PoorConnectionQuality', manual: true});
     */
    this.info = function (options) {
      if (options.message) {
        PubSubSvc.publish('/infoMessage/show', [options]);
      }
    };

    this.closeInfo = function (messageId) {
      PubSubSvc.publish('/infoMessage/close', [messageId]);
    };

    /**
     * Used to display error messages (may be with confirm/cancel buttons).
     * @param options {object} Options to configure the modal.
     * @options.templateUrl {String} The template to be used by the modal.
     * @options.controller {String} The controller to be used by the modal.
     * @options.message {String} The error message to be displayed in the modal body.
     * @options.messageParams {Array} Message-specific localization parameters.
     * @options.title {String} The error title to be displayed in the modal header.
     * @options.titleParams {Array} Title-specific localization parameters.
     * @options.action {Boolean} Indicates that the modal should display yes/no buttons. Defaults to 'false'.
     * @options.yesLabel {String} Text (label) for the 'yes' button. Defaults to 'Yes'.
     * @options.noLabel {String} Text (label) for the 'no' button. Defaults to 'No'.
     * @options.size {String} The modal's size.
     * @options.backdropClass {String} A custom backdrop class.
     * @options.backdrop {String|Boolean} Controls presence of a backdrop.
     * @example PopupSvc.error({message: 'res_InvalidFileType', title: 'Invalid File Type Error', size:'medium', backdrop: false});
     */
    this.error = function (options) {
      if (!options.message) {
        return null;
      }
      var errorOptions = {
        templateUrl: options.templateUrl || 'views/modals/genericDialogModal.html',
        controller: options.controller || 'GenericDialogCtrl',
        windowClass: 'modal-error',
        title: options.title || 'res_ErrorTitle', // remove check when all title headers are implemented
        titleParams: options.titleParams,
        message: options.message,
        messageParams: options.messageParams,
        type: 'error', // needed in genericModal.html
        action: options.action || false, // indicates that the modal should display yes/no buttons
        yesLabel: options.yesLabel || 'res_Yes',
        noLabel: options.noLabel || (options.noLabel === undefined ? 'res_No' : null),
        size: options.size || 'small',
        backdropClass: options.backdropClass,
        backdrop: !options.hasOwnProperty('backdrop') || options.backdrop === 'true' ? 'modal-backdrop' : options.backdrop,
        messageClickAction: options.messageClickAction,
        modalClasslist: function () {
          return getClasslist(this.action, options.alert);
        },
        resolve: {
          options: function () {
            return errorOptions;
          }
        }
      };
      LogSvc.error('PopupSvc.error: ' + errorOptions.message);
      return openModal(errorOptions);
    };

    /**
         * Used to display confirm modals (modals with confrim/cancel buttons).
         * @param options {object} Options to configure the modal.
         * @options.templateUrl {String} The template to be used by the modal.
         * @options.controller {String} The controller to be used by the modal.
         * @options.message {String} The error message to be displayed in the modal body.
         * @options.title {String} The error title to be displayed in the modal header.
         * @options.messageParams {Array} Message-specific localization parameters.
         * @options.titleParams {Array} Title-specific localization parameters.
         * @options.size {String} The modal's size.
         * @options.yesLabel {String} Text (label) for the 'yes' button. Defaults to 'Yes'.
         * @options.noLabel {String} Text (label) for the 'no' button. Defaults to 'No'.
         * @options.scrollBox {Boolean} Controlls the rpesence of a scrollBox area in the modal.
         * @options.backdropClass {String} A custom backdrop class.
         * @options.backdrop {String|Boolean} Controls presence of a backdrop.
         * @example PopupSvc.confirm({
            message: 'res_UnloadActiveCall',
                yesLabel: 'res_Yes',
                noLabel: 'res_No',
            });
         */
    this.confirm = function (options) {
      if (!options.message) {
        return null;
      }
      var confirmOptions = {
        templateUrl: options.templateUrl || 'views/modals/genericDialogModal.html',
        controller: options.controller || 'GenericDialogCtrl',
        windowClass: options.windowClass,
        title: options.title,
        titleParams: options.titleParams,
        message: options.message,
        messageParams: options.messageParams,
        type: 'confirm',
        action: true, // indicates that the modal should display yes/no buttons
        size: options.size || 'medium',
        yesLabel: options.yesLabel || 'res_Yes',
        noLabel: options.noLabel || (options.noLabel === undefined ? 'res_No' : null),
        scrollBox: options.scrollBox,
        backdropClass: options.backdropClass,
        backdrop: !options.hasOwnProperty('backdrop') || options.backdrop === 'true' ? 'modal-backdrop' : options.backdrop,
        keyboard: options.keyboard || true,
        timeOut: options.timeOut,
        modalClasslist: function () {
          return getClasslist(this.action, options.alert);
        },
        resolve: {
          options: function () {
            return confirmOptions;
          }
        }
      };
      LogSvc.debug('PopupSvc.confirm: ', confirmOptions.message);
      return openModal(confirmOptions);
    };

    /**
         * Used to display alert modals (modals with ok button).
         * @param options {object} Options to configure the modal.
         * @options.templateUrl {String} The template to be used by the modal.
         * @options.controller {String} The controller to be used by the modal.
         * @options.message {String} The error message to be displayed in the modal body.
         * @options.title {String} The error title to be displayed in the modal header.
         * @options.messageParams {Array} Message-specific localization parameters.
         * @options.titleParams {Array} Title-specific localization parameters.
         * @options.size {String} The modal's size.
         * @options.btnok {Boolean} Controls the display of the ok button.
         * @options.okLabel {String} Text (label) for the 'ok' button. Defaults to 'Ok'.
         * @options.scrollBox {Boolean} Controlls the presence of a scrollBox area in the modal.
         * @options.backdropClass {String} A custom backdrop class.
         * @options.backdrop {String|Boolean} Controls presence of a backdrop.
         * @example PopupSvc.alert({
                title: 'res_SystemIssue',
                message: 'res_BackendThrottleError'
            });
         */
    this.alert = function (options) {
      if (!options.message) {
        return null;
      }
      var alertOptions = {
        templateUrl: options.templateUrl || 'views/modals/genericDialogModal.html',
        controller: options.controller || 'GenericDialogCtrl',
        title: options.title,
        titleParams: options.titleParams,
        message: options.message,
        messageParams: options.messageParams,
        type: 'generic',
        size: options.size || 'small',
        alert: true, // indicates that the modal should display ok button
        okLabel: options.okLabel || 'res_OK',
        scrollBox: options.scrollBox,
        backdropClass: options.backdropClass,
        backdrop: !options.hasOwnProperty('backdrop') || options.backdrop === 'true' ? 'modal-backdrop' : options.backdrop,
        modalClasslist: function () {
          return getClasslist(options.action, this.alert);
        },
        resolve: {
          options: function () {
            return alertOptions;
          }
        }
      };
      LogSvc.debug('PopupSvc.confirm: ', alertOptions.message);
      return $uibModal.open(alertOptions);
    };

    /**
     * Used as callback to most call control function to handle error and warning messages as needed.
     */
    this.handleCallError = function (error, warn) {
      if (!error) {
        if (warn) {
          _self.info({ message: warn, timeOut: 5000 });
        }
        return;
      }
      if (error === Constants.ReturnCode.CHOOSE_DESKTOP_MEDIA_CANCELLED) {
        // This is an internal error in case the user cancels the screen share media selection. Just ignore it.
        return;
      }
      if (error.startsWith('res_')) {
        var options = { message: error };
        if (error === 'res_RestartBrowser') {
          options.messageParams = [$location.absUrl()];
        } else if (error.startsWith('res_TransferCallFailedCause')) {
          options.title = 'res_TransferCallFailed';
        } else if (error.startsWith('res_PickUpCallFailedCause')) {
          options.title = 'res_PickUpCallFailed';
        }

        _self.error(options);
      } else {
        LogSvc.error('[PopupSvc]: Internal call error: ', error);
      }
    };

    this.handleCallInputAccessError = function (error, warning, onContinueCb, onErrorCb) {
      onErrorCb = onErrorCb || _self.handleCallError;

      // List of possible mic access error messages
      var micAccessErrorList = [
        'res_AccessToAudioInputDeviceFailedPermissionDenied',
        'res_AccessToAudioInputDeviceFailedInUse',
        'res_AccessToAudioInputDeviceFailed'
      ];
      if (onContinueCb && micAccessErrorList.includes(error)) {
        // Ask the user if they want to proceed without their mic
        _self
          .confirm({
            message: 'res_AccessToInputDeviceFailedPromptUserContinue',
            title: 'res_AccessToInputDeviceFailedPromptUserContinueTitle',
            yesLabel: 'res_Continue',
            noLabel: 'res_Cancel'
          })
          .result.then(function () {
            LogSvc.warn('[PopupSvc]: Error accessing microphone, user chose to proceed with call. Error: ', error);
            onContinueCb();
          })
          .catch(function () {
            // Handle reject to prevent 'Possibly unhandled rejection' error
          });
      } else {
        // It's not a mic related problem, use the supplied error handler or _self.handleCallError()
        onErrorCb(error, warning);
      }
    };

    this.confirmDeletion = function (message, messageParams) {
      return _self.confirm({
        message: message,
        messageParams: messageParams,
        title: 'res_ConfirmDeletion',
        yesLabel: 'res_Yes',
        noLabel: 'res_Cancel'
      }).result;
    };

    this.handleApiError = function (err) {
      if (!err) {
        return;
      }
      var title = 'res_ErrorTitle';
      var message;
      switch (err) {
        case Constants.ErrorCode.CONSTRAINT_VIOLATION:
          title = 'res_ActionDenied';
          message = 'res_ConstraintErrorMessage';
          break;
        case Constants.ReturnCode.DISCONNECTED:
        case Constants.ReturnCode.FAILED_TO_SEND:
          message = 'res_NotConnectedTryAgain';
          break;
        default:
          message = 'res_GenericError';
      }

      _self.error({
        title: title,
        message: message,
        size: 'mini'
      });
    };

    this.moveConversationModal = function (conversation) {
      var options = {
        component: 'uMoveUnifyOffice',
        ariaData: { ariaLabelledBy: null },
        resolve: {
          options: function () {
            return {
              conversation: conversation
            };
          }
        }
      };
      _self.openCustomModal(options);
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.PopupSvcImpl = PopupSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global CryptoJS*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Utils = unifyPhone.Utils;
  var storeManager = unifyPhone.storeManager;

  ///////////////////////////////////////////////////////////////////////////////////////
  // LocalStoreSvc Implementation
  // Local store service for data persistence
  ///////////////////////////////////////////////////////////////////////////////////////

  // eslint-disable-next-line max-params, max-lines-per-function
  function LocalStoreSvcImpl($rootScope, $timeout, $q, $window, LogSvc) {
    // NOSONAR
    LogSvc.debug('New Service: LocalStoreSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var INDEXED_DB_NAME = 'unify';
    var USER_STORE_NAME = 'users';
    var LOGGED_ON_USER_STORE_NAME = 'loggedOnUser';
    var EXCHANGE_CONTACTS_STORE_NAME = 'exchangeContacts';
    var EXCHANGE_ONLINE_CONTACTS_STORE_NAME = 'exchangeOnlineContacts';
    var EXCHANGE_ON_PREMISE_CONTACTS_STORE_NAME = 'exchangeOnPremiseContacts';
    var GOOGLE_CONTACTS_STORE_NAME = 'googleContacts';

    var MAGIC_PHRASE = 'VmFsYXIgTW9yZ2h1bGlz';

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _keys = {
      ACCESSIBILITY_ENABLED: 'accessibilityEnabled', // Controls accessibility enabled or not.
      AUDIOAGC: 'audioAGCEnabled', // Diagnostic settings
      AUDIOEC: 'audioECEnabled', // Audio echo cancellation setting
      CACHE_ENABLED: 'cacheEnabled', // Local setting for enabling cache for users that have logged in with SSO.
      CALL_ROUTING_OTHER_SET: 'callRoutingOtherSet', // Telephony call routing option 'Other' is set
      CALL_STAGE_WINDOW: 'callStageWindow', // Size & position data of popout call stage window
      UNIFY_PHONE_LABS: 'unifyPhoneLabs', // UnifyPhone Labs features
      CONCEPTBOARD_ORCHESTRATION: 'conceptboardOrchestration', // Conceptboard orchestration OAuth2 user connection data
      DA_AUTO_UPDATE: 'daAutoUpdate', // Tenant auto update setting
      DEFAULT_PHONE_CALL_DEVICE: 'defaultPhoneCallDevice', // Default device from where phone calls are originated
      DEFAULT_PUSH_CALL_DEVICE: 'defaultPushCallDevice', // Default device from where phone calls are originated
      DIAGNOSTICS: 'diagnosticsDisabled', // Diagnostic settings
      DOMAIN: 'domain', // Domain only for desktop app
      ENABLE_ALPHA_TESTING: 'enableAlphaTesting', // Enable testing of alpha features on production systems
      ENABLE_POPUP_LOG: 'enablePopupLog', // Controls whether or not the popup log window is enabled
      EXCHANGE_CONNECTOR: 'exchangeConnector', // Exchange Connector settings
      EXCHANGE_SYNC_STATE: 'exchangeSyncState', // State from last sync of personal contacts in Exchange
      GOOGLE_CONTACTS_AUTHDATA: 'googleContactsAuthData', // Google Contacts OAuth2 user connection data
      GOOGLE_CONNECTOR: 'googleConnector', // Google Connector settings
      GOOGLE_DRIVE_AUTHDATA: 'googleDriveAuthData', // Google Drive OAuth2 user connection data
      GOOGLE_SYNC_STATE: 'googleSyncState', // State from last sync of personal contacts in Exchange
      HEADSET_APP_ID: 'headsetAppId', // Chrome App ID for development UnifyPhone Headset Integration app.
      HEADSET_TYPES_ENABLED: 'headsetTypesEnabled', // List of headset types that have integration enabled.
      LAST_POSITION: 'lastPosition', // Last retrieved position
      LAST_SAVED_LOCATION: 'lastSavedLocation', // Last saved location data (position and location text)
      LAST_USERNAME: 'lastUserName', // The last user that logged on to UnifyPhone
      LOGGING: 'loggingLevel', // Logging settings
      MAINTENANCE: 'maintenance', // State of maintenance event (Used in CMR)
      MEETING_ROOM_NAME: 'meetingName', // Name of UnifyPhone Meeting Room (Used in CMR)
      MEDIA_DEVICE_LIST: 'mediaDeviceList',
      QOS_PENDING_RECORDS: 'qosPendingRecords', // QoS records that haven't been sent to the server yet
      REMEMBER_ME: 'rememberMe', // The "This is a private computer" setting in the login page
      REMINDER_SNOOZE_TIME: 'reminderSnoozeTime', // TimeStamp of the last snooze notification reminder displayed
      REMINDER_STATUS_TIME: 'reminderStatusTime', // TimeStamp of the last status message reminder displayed
      RINGTONE: 'ringtone', // Selected ringtone
      RTC_CLIENT_INFOS: 'rtcClientInfos', // Stored during join attempts with missing network connection
      SSOLOGIN: 'ssologin', // Local setting for keeping the ssologin value
      TEAMS_EXCHANGE_ONLINE_CONNECTOR: 'teamsExchangeOnlineConnector', // Local setting for keeping enable automatically connection for Ms Teams Exchange Connector
      // THEME: 'theme',                                          // Application theme
      THIRDPARTY_CONNECTIONS: 'thirdpartyConnections', // Third party connections status
      THIRDPARTY_CONTENT: 'thirdpartyContent', // Third party content configuration
      TRANSCRIPTION: 'transcriptionSettings', // Selected transcription provider and API key
      TRANSLATION: 'translationSettings', // Selected translation provider and API key
      UI_SETTINGS: 'uiSettings' // UI settings for in context sidebar
    };

    var _indexedDB = null;
    var _baseSecret = '';
    var _myKey = '';

    var _stores = [
      USER_STORE_NAME,
      LOGGED_ON_USER_STORE_NAME,
      EXCHANGE_ONLINE_CONTACTS_STORE_NAME,
      EXCHANGE_ON_PREMISE_CONTACTS_STORE_NAME,
      GOOGLE_CONTACTS_STORE_NAME
    ];

    // Keep track of exception DB stores that should not be recreated when client is upgraded
    var _upgradeExceptions = [LOGGED_ON_USER_STORE_NAME];

    var _cachingDisabled = false;

    // List of keys that can be saved in local storage even if caching is disabled
    var _cachingDisabledExceptions = [
      _keys.ACCESSIBILITY_ENABLED,
      _keys.AUDIOAGC,
      _keys.CACHE_ENABLED,
      _keys.CALL_ROUTING_OTHER_SET,
      _keys.CALL_STAGE_WINDOW,
      _keys.UNIFY_PHONE_LABS,
      _keys.DA_AUTO_UPDATE,
      _keys.DOMAIN,
      _keys.ENABLE_ALPHA_TESTING,
      _keys.EXCHANGE_CONNECTOR,
      _keys.HEADSET_APP_ID,
      _keys.HEADSET_TYPES_ENABLED,
      _keys.LOGGING,
      _keys.MEDIA_DEVICE_LIST,
      _keys.RINGTONE,
      _keys.QOS_PENDING_RECORDS,
      _keys.REMINDER_SNOOZE_TIME,
      _keys.REMINDER_STATUS_TIME,
      _keys.RTC_CLIENT_INFOS,
      _keys.SSOLOGIN,
      _keys.TEAMS_EXCHANGE_ONLINE_CONNECTOR,
      _keys.THIRDPARTY_CONNECTIONS,
      _keys.THIRDPARTY_CONTENT,
      _keys.UI_SETTINGS
    ];

    // List of keys that must be reset in case a new user signs in
    var _userSpecificKeys = [
      _keys.CONCEPTBOARD_ORCHESTRATION,
      _keys.DA_AUTO_UPDATE,
      _keys.ENABLE_ALPHA_TESTING,
      _keys.EXCHANGE_CONNECTOR,
      _keys.EXCHANGE_SYNC_STATE,
      _keys.GOOGLE_CONTACTS_AUTHDATA,
      _keys.GOOGLE_DRIVE_AUTHDATA,
      _keys.GOOGLE_SYNC_STATE,
      _keys.TEAMS_EXCHANGE_ONLINE_CONNECTOR,
      _keys.THIRDPARTY_CONNECTIONS,
      _keys.THIRDPARTY_CONTENT,
      _keys.TRANSCRIPTION,
      _keys.TRANSLATION,
      _keys.UI_SETTINGS
    ];

    // List of keys that must not be deleted when clearing the cache
    var _criticalKeys = [
      _keys.CACHE_ENABLED,
      _keys.CALL_ROUTING_OTHER_SET,
      _keys.UNIFY_PHONE_LABS,
      _keys.DOMAIN,
      _keys.EXCHANGE_CONNECTOR,
      _keys.HEADSET_APP_ID,
      _keys.HEADSET_TYPES_ENABLED,
      _keys.LAST_USERNAME,
      _keys.MEDIA_DEVICE_LIST,
      _keys.REMEMBER_ME,
      _keys.REMINDER_SNOOZE_TIME,
      _keys.REMINDER_STATUS_TIME,
      _keys.RINGTONE,
      _keys.SSOLOGIN,
      _keys.TEAMS_EXCHANGE_ONLINE_CONNECTOR,
      _keys.TRANSCRIPTION,
      _keys.TRANSLATION,
      _keys.UI_SETTINGS
    ];

    var _obsoleteKeys = [];

    var _md5 = CryptoJS.MD5.bind(CryptoJS); // Shortcut to hide 'CryptoJS.MD5' calls in obfuscating code

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function setItem(key, value) {
      if (!key) {
        LogSvc.warn('[LocalStoreSvc]: setItem invoked without key');
        return;
      }
      if (_cachingDisabled && _cachingDisabledExceptions.indexOf(key) === -1) {
        LogSvc.info('[LocalStoreSvc]: Caching is disabled. Cannot set item in local storage - key = ', key);
        return;
      }
      storeManager.setItem(key, value || '');
    }

    function getItem(key) {
      if (!key) {
        LogSvc.warn('[LocalStoreSvc]: getItem invoked without key');
        return null;
      }
      return storeManager.getItem(key);
    }

    function removeItem(key) {
      if (!key) {
        LogSvc.warn('[LocalStoreSvc]: removeItem invoked without key');
        return;
      }
      storeManager.removeItem(key);
    }

    function createObjectStore(db, storeName, keyPath) {
      if (db.objectStoreNames.contains(storeName)) {
        // Store alrteady exists
        return null;
      }
      return db.createObjectStore(storeName, { keyPath: keyPath });
    }

    function upgradeObjectStores(db) {
      _stores.forEach(function (storeName) {
        if (!_upgradeExceptions.includes(storeName) && db.objectStoreNames.contains(storeName)) {
          LogSvc.debug('[LocalStoreSvc]: Delete object store - ', storeName);
          db.deleteObjectStore(storeName);
        }
      });

      LogSvc.info('[LocalStoreSvc]: Recreate object stores');

      createObjectStore(db, LOGGED_ON_USER_STORE_NAME, 'userId');
      createObjectStore(db, USER_STORE_NAME, 'userId');
      createObjectStore(db, EXCHANGE_ONLINE_CONTACTS_STORE_NAME, 'id');
      createObjectStore(db, EXCHANGE_ON_PREMISE_CONTACTS_STORE_NAME, 'id');
      createObjectStore(db, GOOGLE_CONTACTS_STORE_NAME, 'id');
    }

    function removeIndexedDb() {
      var defer = $q.defer();
      var removeReq = $window.indexedDB.deleteDatabase(INDEXED_DB_NAME);
      removeReq.onsuccess = function () {
        LogSvc.info('[LocalStoreSvc]: Existing indexedDB removed');
        defer.resolve();
      };
      removeReq.onerror = function (event) {
        LogSvc.error('[LocalStoreSvc]: Failed to remove indexedDB. ', event);
        defer.reject(event);
      };
      removeReq.onblocked = function (event) {
        LogSvc.error('[LocalStoreSvc]: IndexedDB is blocked. ', event);
        defer.reject(event);
      };
      return defer.promise;
    }

    function checkDbStores(db) {
      var actualStores = Array.prototype.slice.call(db.objectStoreNames);
      LogSvc.debug('[LocalStoreSvc]: Expected stores = ', _stores);
      LogSvc.debug('[LocalStoreSvc]: Actual stores = ', actualStores);
      return (
        actualStores.length === _stores.length &&
        _stores.every(function (name) {
          return actualStores.includes(name);
        })
      );
    }

    function initIndexedDb(enableCache, defer, loggedOnUser) {
      var isFirstAttempt = !defer;
      var indexedDbAvailable = !!$window.indexedDB;

      // Start by clearing obsolete local storage keys
      _obsoleteKeys.forEach(function (key) {
        // NOSONAR
        removeItem(key);
      });

      defer = defer || $q.defer();
      _baseSecret = _md5(MAGIC_PHRASE).toString();
      _cachingDisabled = !indexedDbAvailable || !enableCache;
      if (_cachingDisabled) {
        LogSvc.info('[LocalStoreSvc]: Local caching disabled, clearing IndexedDB and Local Storage');
        clearLocalStorage(false);
        if (indexedDbAvailable) {
          removeIndexedDb().then(defer.resolve).catch(defer.resolve);
        } else {
          LogSvc.error('[LocalStoreSvc]: IndexedDB is not defined.');
          defer.resolve();
        }
      } else {
        var handleFailure = function () {
          LogSvc.error('[LocalStoreSvc]: Failed to recreate indexedDB, give up.');
          LogSvc.debug('[LocalStoreSvc]: Switch to caching disabled mode');
          if ($rootScope.ssoLogin) {
            setItem(_keys.CACHE_ENABLED, false);
          } else {
            setItem(_keys.REMEMBER_ME, false);
          }
          initIndexedDb(false, defer);
        };

        var recreateDb = function (user) {
          if (isFirstAttempt) {
            LogSvc.info('[LocalStoreSvc]: Remove existing indexedDB');
            removeIndexedDb()
              .then(function () {
                initIndexedDb(true, defer, user);
              })
              .catch(handleFailure);
          } else {
            handleFailure();
          }
        };

        LogSvc.info('[LocalStoreSvc]: Initializing LocalStoreSvc for client version ', $rootScope.clientVersion);
        var version = Utils.convertVersionToNumber($rootScope.clientVersion) || 1;
        LogSvc.info('[LocalStoreSvc]: Setting indexedDB version to ', version);
        var request = $window.indexedDB.open(INDEXED_DB_NAME, version);
        request.onupgradeneeded = function (event) {
          LogSvc.warn('[LocalStoreSvc]: Upgrade needed. Current Version: ' + event.oldVersion + ', New Version: ' + version);
          upgradeObjectStores(event.target.result);
        };
        request.onerror = function (event) {
          LogSvc.warn('[LocalStoreSvc]: Error opening indexedDB. ', event.target && event.target.error);
          // The DB may be corrupted. Remove the existing db and recreate a new one.
          recreateDb();
        };
        request.onsuccess = function (event) {
          LogSvc.debug('[LocalStoreSvc]: Opened indexedDB ', INDEXED_DB_NAME);
          var db = event.target.result;
          if (checkDbStores(db)) {
            _indexedDB = db;
            if (loggedOnUser) {
              addObjectsToStore(loggedOnUser, LOGGED_ON_USER_STORE_NAME).finally(function () {
                defer.resolve();
              });
            } else {
              defer.resolve();
            }
          } else {
            LogSvc.error('[LocalStoreSvc]: IndexedDB stores do not match. Need to upgrade.');
            var previousUser = null;
            getLoggedOnUser(db)
              .then(function (prevUser) {
                previousUser = prevUser;
              })
              .finally(function () {
                db.close();
                recreateDb(previousUser);
              });
          }
        };
      }

      return defer.promise;
    }

    function resolvePromise(defer, data) {
      if (defer) {
        defer.resolve(data);
        !$rootScope.$$phase && $rootScope.$digest();
      }
    }

    function rejectPromise(defer, data) {
      if (defer) {
        defer.reject(data);
        !$rootScope.$$phase && $rootScope.$digest();
      }
    }

    function clearObjectStores(clearLocalUser) {
      var defer = $q.defer();
      var transaction = _indexedDB.transaction(_stores, 'readwrite');
      transaction.oncomplete = function () {
        LogSvc.debug('[LocalStoreSvc]: Cleared indexedDB');
        resolvePromise(defer, null);
      };
      transaction.onerror = function (event) {
        LogSvc.error('[LocalStoreSvc]: Error clearing indexedDB ', event);
        rejectPromise(defer, event);
      };
      _stores.forEach(function (store) {
        if (store !== LOGGED_ON_USER_STORE_NAME || clearLocalUser) {
          transaction.objectStore(store).clear();
        }
      });
      return defer.promise;
    }

    function rejectInvalidInput() {
      var defer = $q.defer();
      defer.reject('Invalid input');
      !$rootScope.$$phase && $rootScope.$digest();
      return defer.promise;
    }

    function resolveDisabledCaching(isArray) {
      var defer = $q.defer();
      defer.resolve(isArray ? [] : null);
      !$rootScope.$$phase && $rootScope.$digest();
      return defer.promise;
    }

    function encryptData(objectStore, data) {
      if (!objectStore || !data || typeof data !== 'object') {
        return data;
      }

      // Get the property names for the key and indexes used for the given object store
      var specialProperties = [objectStore.keyPath];
      Array.prototype.push.apply(specialProperties, objectStore.indexNames);

      var encryptObject = function (object) {
        var encryptedObject = {};
        specialProperties.forEach(function (propertyName) {
          encryptedObject[propertyName] = object[propertyName];
        });
        var stringifiedObject = JSON.stringify(object);
        encryptedObject.encryptedBlob = CryptoJS.AES.encrypt(stringifiedObject, _myKey).toString();
        return encryptedObject;
      };

      return Array.isArray(data) ? data.map(encryptObject) : encryptObject(data);
    }

    function decryptData(data) {
      if (!data || typeof data !== 'object' || !data.encryptedBlob) {
        return data;
      }

      try {
        var stringifiedObject = CryptoJS.AES.decrypt(data.encryptedBlob, _myKey).toString(CryptoJS.enc.Utf8);
        return JSON.parse(stringifiedObject);
      } catch (e) {
        return null;
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // IndexedDB GET functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function getAll(storeName, reverse, indexName) {
      if (_cachingDisabled) {
        return resolveDisabledCaching(true);
      }
      if (!storeName) {
        return rejectInvalidInput();
      }
      var defer = $q.defer();
      LogSvc.debug('[LocalStoreSvc]: Get all objects from store:', storeName);

      var result = [];
      var transaction = _indexedDB.transaction(storeName);
      transaction.oncomplete = function () {
        LogSvc.debug('[LocalStoreSvc]: Finished retrieving objects from store:', storeName);
        resolvePromise(defer, result);
      };

      var objectStore = transaction.objectStore(storeName);
      if (indexName) {
        objectStore = objectStore.index(indexName);
      }
      var request;
      if (reverse) {
        request = objectStore.openCursor(null, 'prev');
      } else {
        request = objectStore.openCursor();
      }
      request.onsuccess = function () {
        var cursor = request.result;
        if (cursor) {
          var object = decryptData(cursor.value);
          object && result.push(object);
          cursor.continue();
        }
      };
      request.onerror = function () {
        // request.error is a DOMException so don't pass as parameter.
        // Otherwise logger tries to get the stack data.
        LogSvc.error('[LocalStoreSvc]: Error getting object. ' + request.error);
        rejectPromise(defer);
      };
      return defer.promise;
    }

    function getLoggedOnUser(db) {
      db = db || _indexedDB;

      var defer = $q.defer();
      LogSvc.debug('[LocalStoreSvc]: Get logged on user from store:', LOGGED_ON_USER_STORE_NAME);

      var result = null;
      var transaction = db.transaction(LOGGED_ON_USER_STORE_NAME);
      transaction.oncomplete = function () {
        LogSvc.debug('[LocalStoreSvc]: Finished retrieving objects from store:', LOGGED_ON_USER_STORE_NAME);
        resolvePromise(defer, result);
      };

      var objectStore = transaction.objectStore(LOGGED_ON_USER_STORE_NAME);
      var request = objectStore.openCursor();

      request.onsuccess = function () {
        var cursor = request.result;
        if (cursor) {
          result = cursor.value;
          cursor.continue();
        }
      };
      request.onerror = function () {
        // request.error is a DOMException so don't pass as parameter.
        // Otherwise logger tries to get the stack data.
        LogSvc.error('[LocalStoreSvc]: Error getting object. ' + request.error);
        rejectPromise(defer);
      };
      return defer.promise;
    }

    function getAllUsers() {
      return getAll(USER_STORE_NAME);
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // IndexedDB DELETE functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function deleteObjectsFromStore(dataArray, storeName) {
      if (_cachingDisabled) {
        return resolveDisabledCaching(dataArray instanceof Array);
      }
      if (!dataArray || dataArray.length === 0) {
        return rejectInvalidInput();
      }
      if (!(dataArray instanceof Array)) {
        dataArray = [dataArray];
      }
      var defer = $q.defer();
      try {
        var transaction = _indexedDB.transaction(storeName, 'readwrite');
        transaction.oncomplete = function () {
          LogSvc.debug('[LocalStoreSvc]: Finished deleting object(s) from', storeName);
          resolvePromise(defer, dataArray);
        };
        transaction.onerror = function () {
          $timeout(function () {
            // transaction.error is a DOMException so don't pass as parameter.
            // Otherwise logger tries to get the stack data.
            LogSvc.info('[LocalStoreSvc]: Transaction error deleting objects. ' + transaction.error);
            defer.reject();
          });
        };

        var objectStore = transaction.objectStore(storeName);
        dataArray.forEach(function (id) {
          var request = objectStore.delete(id);
          request.onerror = function () {
            // request.error is a DOMException so don't pass as parameter.
            // Otherwise logger tries to get the stack data.
            LogSvc.error('[LocalStoreSvc]: Error deleting object with id=' + id + '. ' + request.error);
          };
        });
      } catch (e) {
        LogSvc.error('[LocalStoreSvc]: Error in deleteObjectsFromStore. ', e);
        rejectPromise(defer);
      }
      return defer.promise;
    }

    function removeExchangeContacts(contactIds, storeName) {
      storeName = storeName || EXCHANGE_CONTACTS_STORE_NAME;
      LogSvc.debug('[LocalStoreSvc]: Delete contacts:', storeName, contactIds);
      return deleteObjectsFromStore(contactIds, storeName);
    }

    function removeAllExchangeContacts(storeName) {
      var defer = $q.defer();
      storeName = storeName || EXCHANGE_CONTACTS_STORE_NAME;
      var transaction = _indexedDB.transaction(storeName, 'readwrite');
      transaction.oncomplete = function () {
        LogSvc.debug('[LocalStoreSvc]: Cleared Exchange contacts indexedDB');
        resolvePromise(defer, null);
      };
      transaction.onerror = function (event) {
        LogSvc.error('[LocalStoreSvc]: Error clearing Exchange contacts indexedDB ', event);
        rejectPromise(defer, event);
      };
      transaction.objectStore(storeName).clear();
      return defer.promise;
    }

    function removeExchangeOnlineContacts(contactIds, storeName) {
      storeName = storeName || EXCHANGE_ONLINE_CONTACTS_STORE_NAME;
      LogSvc.debug('[LocalStoreSvc]: Delete contacts:', storeName, contactIds);
      return deleteObjectsFromStore(contactIds, storeName);
    }

    function removeAllExchangeOnlineContacts(storeName) {
      var defer = $q.defer();
      storeName = storeName || EXCHANGE_ONLINE_CONTACTS_STORE_NAME;
      var transaction = _indexedDB.transaction(storeName, 'readwrite');
      transaction.oncomplete = function () {
        LogSvc.debug('[LocalStoreSvc]: Cleared Exchange Online contacts indexedDB');
        resolvePromise(defer, null);
      };
      transaction.onerror = function (event) {
        LogSvc.error('[LocalStoreSvc]: Error clearing Exchange Online contacts indexedDB ', event);
        rejectPromise(defer, event);
      };
      transaction.objectStore(storeName).clear();
      return defer.promise;
    }

    function removeExchangeOnPremiseContacts(contactIds, storeName) {
      storeName = storeName || EXCHANGE_ON_PREMISE_CONTACTS_STORE_NAME;
      LogSvc.debug('[LocalStoreSvc]: Delete contacts:', storeName, contactIds);
      return deleteObjectsFromStore(contactIds, storeName);
    }

    function removeAllExchangeOnPremiseContacts(storeName) {
      var defer = $q.defer();
      storeName = storeName || EXCHANGE_ON_PREMISE_CONTACTS_STORE_NAME;
      var transaction = _indexedDB.transaction(storeName, 'readwrite');
      transaction.oncomplete = function () {
        LogSvc.debug('[LocalStoreSvc]: Cleared Exchange OnPremise contacts indexedDB');
        resolvePromise(defer, null);
      };
      transaction.onerror = function (event) {
        LogSvc.error('[LocalStoreSvc]: Error clearing Exchange OnPremise contacts indexedDB ', event);
        rejectPromise(defer, event);
      };
      transaction.objectStore(storeName).clear();
      return defer.promise;
    }

    function removeAllGoogleContacts(storeName) {
      var defer = $q.defer();
      storeName = storeName || GOOGLE_CONTACTS_STORE_NAME;
      var transaction = _indexedDB.transaction(storeName, 'readwrite');
      transaction.oncomplete = function () {
        LogSvc.debug('[LocalStoreSvc]: Cleared Google contacts indexedDB');
        resolvePromise(defer, null);
      };
      transaction.onerror = function (event) {
        LogSvc.error('[LocalStoreSvc]: Error clearing Googlee contacts indexedDB ', event);
        rejectPromise(defer, event);
      };
      transaction.objectStore(storeName).clear();
      return defer.promise;
    }
    ///////////////////////////////////////////////////////////////////////////////////////
    // IndexedDB PUT functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function putObjectsToStore(dataArray, storeName) {
      if (_cachingDisabled) {
        return resolveDisabledCaching(Array.isArray(dataArray));
      }

      var defer = $q.defer();
      try {
        var transaction = _indexedDB.transaction(storeName, 'readwrite');
        transaction.oncomplete = function () {
          resolvePromise(defer, dataArray);
        };
        transaction.onerror = function () {
          $timeout(function () {
            // transaction.error is a DOMException so don't pass as parameter.
            // Otherwise logger tries to get the stack data.
            LogSvc.error('[LocalStoreSvc]: Transaction error putting object(s). ' + transaction.error);
            defer.reject();
          });
        };

        var objectStore = transaction.objectStore(storeName);
        var encryptedArray = encryptData(objectStore, dataArray);

        encryptedArray.forEach(function (data) {
          var request = objectStore.put(data);
          request.onerror = function () {
            // request.error is a DOMException so don't pass as parameter.
            // Otherwise logger tries to get the stack data.
            LogSvc.error('[LocalStoreSvc]: There was an error putting an object. storeName = ', storeName);
            LogSvc.error('[LocalStoreSvc]: Error details - ' + request.error);
          };
        });
      } catch (e) {
        LogSvc.error('[LocalStoreSvc]: Error in putObjectsToStore. ', e);
        rejectPromise(defer);
      }
      return defer.promise;
    }

    function putUsers(users) {
      if (_cachingDisabled) {
        return resolveDisabledCaching(true);
      }
      if (!users) {
        return rejectInvalidInput();
      }
      if (!Array.isArray(users)) {
        users = [users];
      }
      // Remove the local user
      users.some(function (u, idx) {
        if ($rootScope.localUser.userId === u.userId) {
          users.splice(idx, 1);
          return true;
        }
        return false;
      });
      if (users.length === 0) {
        return rejectInvalidInput();
      }

      var userIds = users.map(function (u) {
        return u.userId;
      });
      LogSvc.debug('[LocalStoreSvc]: Put users:', userIds);

      return putObjectsToStore(users, USER_STORE_NAME);
    }

    function putExchangeContacts(contacts, storeName) {
      storeName = storeName || EXCHANGE_CONTACTS_STORE_NAME;
      if (_cachingDisabled) {
        return resolveDisabledCaching(true);
      }
      if (!contacts) {
        return rejectInvalidInput();
      }
      if (!Array.isArray(contacts)) {
        contacts = [contacts];
      }

      var msg = '';
      switch (storeName) {
        case EXCHANGE_ONLINE_CONTACTS_STORE_NAME:
          msg = 'Online ';
          break;
        case EXCHANGE_ON_PREMISE_CONTACTS_STORE_NAME:
          msg = 'OnPremise ';
          break;
      }
      LogSvc.debug('[LocalStoreSvc]: Put Exchange ' + msg + 'contacts. Count:', contacts.length);

      return putObjectsToStore(contacts, storeName);
    }

    function putGoogleContactsStore(contacts, storeName) {
      storeName = storeName || GOOGLE_CONTACTS_STORE_NAME;
      if (_cachingDisabled) {
        return resolveDisabledCaching(true);
      }
      if (!contacts) {
        return rejectInvalidInput();
      }
      if (!Array.isArray(contacts)) {
        contacts = [contacts];
      }

      LogSvc.debug('[LocalStoreSvc]: Put Google contacts. Count:', contacts.length);

      return putObjectsToStore(contacts, storeName);
    }

    function putExchangeOnlineContacts(contacts) {
      return putExchangeContacts(contacts, EXCHANGE_ONLINE_CONTACTS_STORE_NAME);
    }

    function putExchangeOnPremiseContacts(contacts) {
      return putExchangeContacts(contacts, EXCHANGE_ON_PREMISE_CONTACTS_STORE_NAME);
    }

    function putGoogleContacts(contacts) {
      return putGoogleContactsStore(contacts, GOOGLE_CONTACTS_STORE_NAME);
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // IndexedDB ADD functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function addObjectsToStore(dataArray, storeName) {
      if (_cachingDisabled) {
        return resolveDisabledCaching(Array.isArray(dataArray));
      }
      if (!dataArray || dataArray.length === 0) {
        return rejectInvalidInput();
      }
      if (!(dataArray instanceof Array)) {
        dataArray = [dataArray];
      }
      var defer = $q.defer();
      try {
        var transaction = _indexedDB.transaction(storeName, 'readwrite');
        transaction.oncomplete = function () {
          resolvePromise(defer, dataArray);
        };
        transaction.onerror = function () {
          $timeout(function () {
            // transaction.error is a DOMException so don't pass as parameter.
            // Otherwise logger tries to get the stack data.
            LogSvc.error('[LocalStoreSvc]: Transaction error adding objects. ' + transaction.error);
            defer.reject();
          });
        };
        transaction.onabort = function () {
          // Transaction is aborted (common reason: not enough Space on the disc)
          LogSvc.error('[LocalStoreSvc]: Transaction is aborted. Error: ', transaction.error.toString());
          _cachingDisabled = true;
          defer.reject();
        };

        var objectStore = transaction.objectStore(storeName);
        var encryptedArray = encryptData(objectStore, dataArray);

        encryptedArray.forEach(function (data) {
          var request = objectStore.add(data);
          request.onerror = function () {
            // request.error is a DOMException so don't pass as parameter.
            // Otherwise logger tries to get the stack data.
            LogSvc.error('[LocalStoreSvc]: Error adding object. ' + request.error);
            LogSvc.info('[LocalStoreSvc]: Request object =', data);
          };
        });
      } catch (e) {
        LogSvc.error('[LocalStoreSvc]: Error in addObjectsToStore. ', e);
        rejectPromise(defer);
      }
      return defer.promise;
    }

    function addUsers(users) {
      if (!users) {
        return rejectInvalidInput();
      }
      if (!Array.isArray(users)) {
        users = [users];
      }
      // Remove the local user
      users.some(function (u, idx) {
        if ($rootScope.localUser.userId === u.userId) {
          users.splice(idx, 1);
          return true;
        }
        return false;
      });
      if (users.length === 0) {
        return rejectInvalidInput();
      }

      var userIds = users.map(function (u) {
        return u.userId;
      });
      LogSvc.debug('[LocalStoreSvc]: Add users:', userIds);

      return addObjectsToStore(users, USER_STORE_NAME);
    }

    function clearPrivateDataAndSetLoggedOn(user) {
      var defer = $q.defer();

      _userSpecificKeys.forEach(function (key) {
        removeItem(key);
      });

      clearObjectStores(true)
        .then(
          function () {
            return addObjectsToStore(user, LOGGED_ON_USER_STORE_NAME);
          },
          function () {
            // Can't clear ObjectStores
            defer.reject('res_ErrorClearIndexedDb');
          }
        )
        .then(
          function () {
            LogSvc.debug('[LocalStoreSvc]: Added localUser to indexedDB');
            defer.resolve();
          },
          function () {
            // Not able to set logged on user, but should be OK
            LogSvc.info('[LocalStoreSvc]: Failed to add localUser to indexedDB');
            defer.resolve();
          }
        );
      return defer.promise;
    }

    function clearLocalStorage(clearAll) {
      var exceptionList = clearAll ? _criticalKeys : _cachingDisabledExceptions;
      Object.values(_keys).forEach(function (key) {
        if (!exceptionList.includes(key)) {
          removeItem(key);
        }
      });
    }

    function addExchangeContacts(contacts, storeName) {
      storeName = storeName || EXCHANGE_CONTACTS_STORE_NAME;
      if (!contacts) {
        return rejectInvalidInput();
      }
      if (!Array.isArray(contacts)) {
        contacts = [contacts];
      }
      if (contacts.length === 0) {
        return rejectInvalidInput();
      }

      LogSvc.debug('[LocalStoreSvc]: Adding Exchange contacts. Count:', contacts.length);

      return addObjectsToStore(contacts, storeName);
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.keys = _keys;

    /**
     * Initializes indexedDB.
     *
     * @function
     * @return {Promise} A promise that will be resolved when indexedDB is initialized.
     */
    this.initIndexedDb = function (enableCache) {
      return initIndexedDb(enableCache, null);
    };

    /**
     * Clears all keys from local storage.
     */
    this.clearLocalStorage = function () {
      clearLocalStorage(true);
    };

    /**
     * Set a key/value pair in localStorage.
     *
     * @param {String} key A unique string.
     * @param {Object} value An object to be stored as value.
     */
    this.setObjectSync = function (key, value) {
      setItem(key, JSON.stringify(value));
    };

    /**
     * Get an object specifying key.
     *
     * @param {String} key A unique string.
     */
    this.getObjectSync = function (key) {
      var obj = getItem(key);
      try {
        obj = Utils.fromJson(obj);
      } catch (e) {
        obj = null;
      }
      return obj;
    };

    /**
     * Set a key/value pair in localStorage.
     *
     * @param {String} key A unique string.
     * @param {String} value A string to be stored as value.
     */
    this.setString = function (key, value) {
      setItem(key, value);
    };

    /**
     * Get a string value specifying key synchronously.
     *
     * @param {String} key A unique string.
     */
    this.getStringSync = function (key) {
      return getItem(key);
    };

    /**
     * Set a key/value pair in localStorage.
     *
     * @param {String} key A unique string.
     * @param {Number} value A number to be stored as value.
     */
    this.setInt = function (key, value) {
      setItem(key, value);
    };

    /**
     * Get a numeric value specifying key.
     *
     * @param {String} key A unique string.
     * @param {function} cb Callback function that will receive the number mapped from given key.
     */
    this.getInt = function (key) {
      var val = getItem(key);
      return isNaN(parseInt(val, 10)) ? -1 : val;
    };

    /**
     * Remove a key/value pair from localStorage.
     *
     * @param {String} key A unique string.
     */
    this.removeItem = function (key) {
      removeItem(key);
    };

    /**
     * Get all users from indexedDB.
     *
     * @function
     * @return {Promise} A promise that will be resolved with an array of users.
     */
    this.getAllUsers = getAllUsers;

    /**
     * Add user(s) in indexedDB. If user(s) exists in indexedDB, the request will be rejected.
     *
     * @function
     * @param {(User|User[])} users A user or an array of users.
     * @return {Promise} A promise that will be resolved when user(s) are added.
     */
    this.addUsers = addUsers;

    /**
     * Add or modify user(s) in indexedDB. If user(s) exists in indexedDB, it will be modified.
     *
     * @function
     * @param {(User|User[])} users A user or an array of users.
     * @return {Promise} A promise that will be resolved when user(s) are added.
     */
    this.putUsers = putUsers;

    /**
     * Clear all object stores in indexedDB.
     *
     * @function
     * @return {Promise} A promise that will be resolved when indexedDB is cleared.
     */
    this.clearIndexedDb = function () {
      if (_cachingDisabled) {
        return $q.resolve();
      }
      return clearObjectStores(false);
    };

    /**
     * Set user as logged on user in indexedDB. If the user is different from previous logged on user, all object stores will be cleared.
     *
     * @param {User} user A user who just logged in.
     * @return {Promise} A promise that will be resolved when logged on user is set.
     */
    this.setLoggedOnUser = function (user) {
      var defer = $q.defer();
      if (_cachingDisabled || !user) {
        // If caching is disabled we cannot tell whether this is a new user or the
        // same user, so we must remove the old logs to ensure privacy.
        LogSvc.removeLogFiles();
        // Wait a bit so new log file is created correctly
        $timeout(defer.resolve, 100, false);
      } else {
        _myKey = _md5(user.emailAddress).toString();
        _myKey = _md5(_myKey + _baseSecret).toString();

        getLoggedOnUser()
          .then(function (prevUser) {
            if (prevUser) {
              if (prevUser.userId === user.userId) {
                // Same user as previously logged on
                LogSvc.debug('[LocalStoreSvc]: The same user is logging in again. Keep indexedDB data.');
                defer.resolve();
                return;
              }

              // A different user logged on. Remove old log files.
              LogSvc.removeLogFiles();
            } else {
              // A different user logged on. Remove old log files.
              // This is for web-client. When "Private computer" wasn't checked, we don't store user in indexedDB
              // and then when a new one is logged on, we should remove old log files
              LogSvc.removeLogFiles();
            }

            LogSvc.debug('[LocalStoreSvc]: A new user is logging in. Clear indexedDB.');
            clearPrivateDataAndSetLoggedOn(user)
              .then(function () {
                defer.resolve();
              })
              .catch(function (err) {
                defer.reject(err);
              });
          })
          .catch(function () {
            clearPrivateDataAndSetLoggedOn(user)
              .then(function () {
                defer.resolve();
              })
              .catch(function (err) {
                defer.reject(err);
              });
          });
      }
      return defer.promise;
    };

    /**
     * Returns if caching is disabled
     */
    this.isCachingDisabled = function () {
      return _cachingDisabled;
    };

    this.getAllExchangeContacts = function (storeName) {
      storeName = storeName || EXCHANGE_CONTACTS_STORE_NAME;
      return getAll(storeName);
    };

    this.getAllExchangeOnlineContacts = function (storeName) {
      storeName = storeName || EXCHANGE_ONLINE_CONTACTS_STORE_NAME;
      return getAll(storeName);
    };

    this.getAllGoogleContacts = function (storeName) {
      storeName = storeName || GOOGLE_CONTACTS_STORE_NAME;
      return getAll(storeName);
    };

    this.getAllExchangeOnPremiseContacts = function (storeName) {
      storeName = storeName || EXCHANGE_ON_PREMISE_CONTACTS_STORE_NAME;
      return getAll(storeName);
    };

    this.addExchangeContacts = addExchangeContacts;

    this.putExchangeContacts = putExchangeContacts;

    this.putExchangeOnlineContacts = putExchangeOnlineContacts;

    this.putExchangeOnPremiseContacts = putExchangeOnPremiseContacts;

    this.putGoogleContacts = putGoogleContacts;

    this.removeExchangeContacts = removeExchangeContacts;

    this.removeAllExchangeContacts = removeAllExchangeContacts;

    this.removeExchangeOnlineContacts = removeExchangeOnlineContacts;

    this.removeAllExchangeOnlineContacts = removeAllExchangeOnlineContacts;

    this.removeExchangeOnPremiseContacts = removeExchangeOnPremiseContacts;

    this.removeAllExchangeOnPremiseContacts = removeAllExchangeOnPremiseContacts;

    this.removeAllGoogleContacts = removeAllGoogleContacts;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.LocalStoreSvcImpl = LocalStoreSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global require*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var Utils = unifyPhone.Utils;

  ///////////////////////////////////////////////////////////////////////////////////////
  // UnifyPhoneLabSvc Implementation
  ///////////////////////////////////////////////////////////////////////////////////////
  // eslint-disable-next-line max-params, max-lines-per-function
  function UnifyPhoneLabSvcImpl($rootScope, $window, LogSvc, PubSubSvc, LocalStoreSvc, LocalizeSvc) {
    //NOSONAR
    LogSvc.debug('New Service: UnifyPhoneLabSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var ALPHA_FEATURES,
      DEMO_FEATURES,
      BETA_FEATURES,
      LAB_FEATURES,
      PRE_REQUISITES = {},
      DEPENDENCIES = {};

    // How to use the different levels:
    // - ALPHA_FEATURES: Features under development which SHOULD NOT be enabled outside of development.
    //   These features can be enabled on the webclient via the hidden debug menu.
    //
    // - DEMO_FEATURES: Demo features. Not meant for regular users.
    //   These features can be enabled on the webclient via the Ctrl+Shift+Alt+D combination.
    //
    // - BETA_FEATURES: Features available for a closed Beta evaluation.
    //   These features can be enabled on the webclient via the Ctrl+Shift+Alt+B combination.
    //
    // - LAB_FEATURES: Features available for evaluation by all users.
    //   These features are visible by default under the UnifyPhone Labs tab.

    if ($window.navigator.platform === 'iOS') {
      // iOS
      ALPHA_FEATURES = [];
      DEMO_FEATURES = [];
      BETA_FEATURES = [];
      LAB_FEATURES = [];
    } else if ($window.navigator.platform === 'Android') {
      // Android
      ALPHA_FEATURES = [];
      DEMO_FEATURES = [];
      BETA_FEATURES = [];
      LAB_FEATURES = [];
    } else if ($window.location.href.includes('/tenant')) {
      // Webclient, DA for tenant app
      ALPHA_FEATURES = [Constants.LabFeatureName.MS_TEAMS_PLUGIN];
      DEMO_FEATURES = [];
      BETA_FEATURES = [];
      LAB_FEATURES = [];
    } else if ($window.location.href.includes('/msteams')) {
      // Webclient, DA for msteams app
      ALPHA_FEATURES = [];
      DEMO_FEATURES = [];
      BETA_FEATURES = [];
      LAB_FEATURES = [];
    } else {
      // Webclient, DA for main app
      ALPHA_FEATURES = [];
      DEMO_FEATURES = [];
      BETA_FEATURES = [];
      LAB_FEATURES = [];
    }

    var ALL_FEATURES = ALPHA_FEATURES.slice(0);
    Array.prototype.push.apply(ALL_FEATURES, BETA_FEATURES);
    Array.prototype.push.apply(ALL_FEATURES, DEMO_FEATURES);
    Array.prototype.push.apply(ALL_FEATURES, LAB_FEATURES);

    // Resources that need to be replaced when a specific lab feature is enabled
    var REPLACEMENT_RESOURCES = {};

    // Specify pre-requisites and dependencies
    // DEPENDENCIES[Constants.LabFeatureName.xxx] = [Constants.LabFeatureName.yyy];
    // PRE_REQUISITES[Constants.LabFeatureName.yyy] = [Constants.LabFeatureName.xxx];

    var _self = this;
    var _unifyPhoneLabFeatures = {};
    var _isInitialized = false;
    var _featureList = [];

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function updateRootScope() {
      // Note that we cannot create a new object for $rootScope.unifyPhoneLabs since we may
      // have a reference to this object in some directives with isolated scope.
      Object.keys(_unifyPhoneLabFeatures).forEach(function (featureName) {
        $rootScope.unifyPhoneLabs[featureName] = _unifyPhoneLabFeatures[featureName];
      });
    }

    // eslint-disable-next-line complexity
    function isSupportedFeature(featureName) {
      // Make sure all pre-requisites are supported
      var preReq = PRE_REQUISITES[featureName];
      if (preReq && !preReq.every(isSupportedFeature)) {
        return false;
      }

      // Check which lab features are supported based on browser type and logged user
      switch (featureName) {
        case Constants.LabFeatureName.AGENT_STATUS_PER_GROUP:
          // This features is only applicable for OSV users
          return !!($rootScope.localUser && $rootScope.localUser.isOSV);

        case Constants.LabFeatureName.TENANT_ADMIN_USER_TABLE_ENHANCEMENT:
          // These features are only applicable for tenant administrators
          return !!($rootScope.localUser && $rootScope.localUser.isTenantAdmin);

        case Constants.LabFeatureName.CALL_FORWARD_NO_REPLY:
          // Available only for ATC
          return !!($rootScope.localUser && $rootScope.localUser.isATC && !$rootScope.localUser.isOsBizCTIEnabled);

        case Constants.LabFeatureName.TRANSCRIPTION_AND_TRANSLATION:
          // This feature is only available for Chrome and Desktop app.
          return !!$rootScope.browser.chrome;
      }

      // Feature is supported by default
      return true;
    }

    function isReloadRequired(featureName) {
      // Add a case statement for all features which require a client reload
      if (featureName === Constants.LabFeatureName.TRANSCRIPTION_AND_TRANSLATION) {
        return true;
      }

      if (!_unifyPhoneLabFeatures[featureName]) {
        // Feature is not enabled. Check if any of the pre-requisites require reload.
        var preReq = PRE_REQUISITES[featureName];
        if (
          preReq &&
          preReq.some(function (feature) {
            return !_unifyPhoneLabFeatures[feature] && isReloadRequired(feature);
          })
        ) {
          return true;
        }
      } else {
        // Feature is enabled. Check if any of the dependencies require reload.
        var dep = DEPENDENCIES[featureName];
        if (
          dep &&
          dep.some(function (feature) {
            return _unifyPhoneLabFeatures[feature] && isReloadRequired(feature);
          })
        ) {
          return true;
        }
      }

      return false;
    }

    function normalizeObject(features) {
      features = features || {};

      var normalized = {};
      ALL_FEATURES.forEach(function (featureName) {
        if (!$rootScope.enableAlphaFeatures && ALPHA_FEATURES.includes(featureName)) {
          normalized[featureName] = false;
        } else {
          normalized[featureName] = !!features[featureName];
        }
      });
      return normalized;
    }

    function reloadPage(message) {
      // Clear indexedDb before reloading the page
      LocalStoreSvc.clearIndexedDb()
        .then(function () {
          LogSvc.info('[UnifyPhoneLabSvc]: Cleared indexedDB');
        })
        .catch(function () {
          LogSvc.warn('[UnifyPhoneLabSvc]: Failed to clear indexedDB');
        })
        .then(function () {
          LogSvc.debug('[UnifyPhoneLabSvc] Publish /application/reload event');
          PubSubSvc.publish('/application/reload', [message, true]);
        });
    }

    function initFeatures() {
      LogSvc.debug('[UnifyPhoneLabSvc] Initialized features to ', _unifyPhoneLabFeatures);

      if (typeof _unifyPhoneLabFeatures.SIMULCAST === 'boolean') {
        LogSvc.info('[UnifyPhoneLabSvc]: Set RtcSessionController.addAdditionalLowVideoStream to ', _unifyPhoneLabFeatures.SIMULCAST);
        unifyPhone.RtcSessionController.addAdditionalLowVideoStream = _unifyPhoneLabFeatures.SIMULCAST;
      }

      LogSvc.debug('[UnifyPhoneLabSvc]: Publish /feature/state/initialized event');
      PubSubSvc.publish('/feature/state/initialized');
    }

    function processFeatureChanged(featureName, value) {
      if (featureName === Constants.LabFeatureName.SIMULCAST) {
        LogSvc.info('[UnifyPhoneLabSvc]: Set RtcSessionController.addAdditionalLowVideoStream to ', value);
        unifyPhone.RtcSessionController.addAdditionalLowVideoStream = value;
      }

      if (value && !LAB_FEATURES.includes(featureName)) {
        if (ALPHA_FEATURES.includes(featureName)) {
          if (!$rootScope.showAlphaFeatures) {
            LogSvc.info('[UnifyPhoneLabSvc]: An Alpha feature has been enabled. Set showAlphaFeatures to true.');
            $rootScope.showAlphaFeatures = true;
            $rootScope.showBetaFeatures = true;
          }
        } else if (BETA_FEATURES.includes(featureName)) {
          if (!$rootScope.showBetaFeatures) {
            LogSvc.info('[UnifyPhoneLabSvc]: A Beta feature has been enabled. Set showBetaFeatures to true.');
            $rootScope.showBetaFeatures = true;
          }
        } else if (DEMO_FEATURES.includes(featureName)) {
          if (!$rootScope.showDemoFeatures) {
            LogSvc.info('[UnifyPhoneLabSvc]: A Demo feature has been enabled. Set showDemoFeatures to true.');
            $rootScope.showDemoFeatures = true;
          }
        }
      }

      LogSvc.debug('[UnifyPhoneLabSvc] Publish /feature/state/changed for ', featureName);
      PubSubSvc.publish('/feature/state/changed', [featureName, value]);
    }

    function updateFeatures(oldValues) {
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.UNIFY_PHONE_LABS, _unifyPhoneLabFeatures);
      LogSvc.debug('[UnifyPhoneLabSvc] Set features to ', _unifyPhoneLabFeatures);
      updateRootScope();
      var hasChanges = false;
      ALL_FEATURES.forEach(function (featureName) {
        var value = _unifyPhoneLabFeatures[featureName];
        if (value !== oldValues[featureName]) {
          hasChanges = true;
          processFeatureChanged(featureName, value);
        }
      });

      if (hasChanges) {
        updateAllResources(true);
      }
    }

    function updateFeature(featureName, value) {
      if (value && !isSupportedFeature(featureName)) {
        LogSvc.warn('[UnifyPhoneLabSvc] Cannot enable unsupported feature: ', featureName);
        return;
      }
      LogSvc.debug('[UnifyPhoneLabSvc] Set ' + featureName + ' feature to ', value);
      _unifyPhoneLabFeatures[featureName] = value;

      var reloadPending = isReloadRequired(featureName);

      // Check for pre-requisites or dependencies
      (value ? enablePrerequisites : disableDependencies)(featureName, reloadPending);

      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.UNIFY_PHONE_LABS, _unifyPhoneLabFeatures);

      if (reloadPending) {
        LogSvc.info('[SettingsCtrl]: Toggled UnifyPhone Lab feature which requires client reload: ', featureName);
        var message = value ? 'res_LabsFeatureActivatedReload' : 'res_LabsFeatureDeactivatedReload';
        reloadPage(message);
      } else {
        updateRootScope();
        updateResourcesForFeature(featureName, true);
        // Process feature specific changes
        processFeatureChanged(featureName, value);
      }
    }

    function backupOriginalResources() {
      var map = $rootScope.i18n.map;
      Object.keys(REPLACEMENT_RESOURCES).forEach(function (featureName) {
        var resources = REPLACEMENT_RESOURCES[featureName];
        if (resources) {
          Object.keys(resources).forEach(function (resName) {
            if (map[resName]) {
              map['$$' + resName] = map[resName];
            }
          });
        }
      });
    }

    function enablePrerequisites(featureName, reloadPending) {
      var preReq = PRE_REQUISITES[featureName];
      preReq &&
        preReq.forEach(function (name) {
          if (!_unifyPhoneLabFeatures[name]) {
            if (reloadPending) {
              _unifyPhoneLabFeatures[name] = true;
              enablePrerequisites(name, true);
            } else {
              updateFeature(name, true);
            }
          }
        });
    }

    function disableDependencies(featureName, reloadPending) {
      var dep = DEPENDENCIES[featureName];
      dep &&
        dep.forEach(function (name) {
          if (_unifyPhoneLabFeatures[name]) {
            if (reloadPending) {
              _unifyPhoneLabFeatures[name] = false;
              disableDependencies(name, true);
            } else {
              updateFeature(name, false);
            }
          }
        });
    }

    function updateResourcesForFeature(featureName, raiseLanguageUpdate) {
      var resources = REPLACEMENT_RESOURCES[featureName];
      if (!resources) {
        return false;
      }
      var hasUpdates = false;
      var map = $rootScope.i18n.map;
      Object.keys(resources).forEach(function (resName) {
        if (!map[resName]) {
          return;
        }
        var replacementResName = _unifyPhoneLabFeatures[featureName] ? resources[resName] : '$$' + resName;
        if (map[replacementResName]) {
          map[resName] = map[replacementResName];
          hasUpdates = true;
        }
      });
      if (hasUpdates) {
        LogSvc.debug('[UnifyPhoneLabSvc]: Update resources associated with feature ', featureName);
        if (raiseLanguageUpdate) {
          // Publish a fake /language/update event to update
          LogSvc.debug('[UnifyPhoneLabSvc]: Publish /language/update event');
          PubSubSvc.publish('/language/update', [LocalizeSvc.getLanguage()]);
        }
      }
      return hasUpdates;
    }

    function updateAllResources(raiseLanguageUpdate) {
      var hasUpdates = false;
      Object.keys(REPLACEMENT_RESOURCES).forEach(function (featureName) {
        if (updateResourcesForFeature(featureName, false)) {
          hasUpdates = true;
        }
      });
      if (hasUpdates && raiseLanguageUpdate) {
        // Publish a fake /language/update event to update
        LogSvc.debug('[UnifyPhoneLabSvc]: Publish /language/update event');
        PubSubSvc.publish('/language/update', [LocalizeSvc.getLanguage()]);
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribeOnce('/localUser/init', function () {
      LogSvc.debug('[UnifyPhoneLabSvc]: Received /localUser/init event');
      // Initialize from local storage if not already initialized.
      if (!_isInitialized) {
        _self.initFromLocalStorage();
      }

      // Now that the local user has been initialized, disable any features which are not supported.
      var hasChanges = false;
      ALL_FEATURES.forEach(function (featureName) {
        if (_unifyPhoneLabFeatures[featureName] && !isSupportedFeature(featureName)) {
          LogSvc.debug('[UnifyPhoneLabSvc]: Disable not supported feature: ', featureName);
          _unifyPhoneLabFeatures[featureName] = false;
          hasChanges = true;
        }
      });

      if (hasChanges) {
        // Update the local storage with updated values
        LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.UNIFY_PHONE_LABS, _unifyPhoneLabFeatures);
        updateRootScope();
        updateAllResources(true);
      }

      initFeatures();
    });

    PubSubSvc.subscribe('/i18n/resources/loaded', function () {
      LogSvc.debug('[UnifyPhoneLabSvc]: Received /i18n/resources/loaded event');
      backupOriginalResources();
      updateAllResources(false);
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.getFeatureNames = function (includeAlpha) {
      return includeAlpha ? ALL_FEATURES.slice(0) : BETA_FEATURES.concat(LAB_FEATURES);
    };

    this.isSupportedFeature = isSupportedFeature;

    this.getSupportedFeatureList = function (ordered) {
      var supportedFeatures = _featureList.filter(function (feature) {
        return isSupportedFeature(feature.featureName);
      });

      if (ordered) {
        // Order by title according to selected language
        var map = $rootScope.i18n.map;
        supportedFeatures.sort(function (a, b) {
          return map[a.titleRes].localeCompare(map[b.titleRes]);
        });
      }
      return supportedFeatures;
    };

    this.setFeature = function (featureName, value) {
      if (!ALL_FEATURES.includes(featureName)) {
        LogSvc.warn('[UnifyPhoneLabSvc] setFeature invoked with unsupported feature name: ', featureName);
        return;
      }
      value = !!value;
      if (_unifyPhoneLabFeatures[featureName] !== value) {
        updateFeature(featureName, value);
      }
    };

    this.toggleFeature = function (featureName) {
      if (!ALL_FEATURES.includes(featureName)) {
        LogSvc.warn('[UnifyPhoneLabSvc] toggleFeature invoked with unsupported feature name: ', featureName);
        return;
      }
      var value = !_unifyPhoneLabFeatures[featureName];
      updateFeature(featureName, value);
    };

    this.isFeatureEnabled = function (featureName) {
      return !!_unifyPhoneLabFeatures[featureName];
    };

    this.isAlphaFeature = function (featureName) {
      return ALPHA_FEATURES.includes(featureName);
    };

    this.isDemoFeature = function (featureName) {
      return DEMO_FEATURES.includes(featureName);
    };

    this.isBetaFeature = function (featureName) {
      return BETA_FEATURES.includes(featureName);
    };

    this.isLabFeature = function (featureName) {
      return LAB_FEATURES.includes(featureName);
    };

    this.hasAlphaFeaturesEnabled = function () {
      return ALPHA_FEATURES.some(function (featureName) {
        return _unifyPhoneLabFeatures[featureName];
      });
    };

    this.hasDemoFeaturesEnabled = function () {
      return DEMO_FEATURES.some(function (featureName) {
        return _unifyPhoneLabFeatures[featureName];
      });
    };

    this.hasBetaFeaturesEnabled = function () {
      return BETA_FEATURES.some(function (featureName) {
        return _unifyPhoneLabFeatures[featureName];
      });
    };

    this.hasLabFeaturesEnabled = function () {
      return LAB_FEATURES.some(function (featureName) {
        return _unifyPhoneLabFeatures[featureName];
      });
    };

    this.showUnifyPhoneLabsFeature = function (featureName) {
      if (_self.isDemoFeature(featureName)) {
        return $rootScope.showDemoFeatures;
      }

      return $rootScope.showAlphaFeatures || _self.isLabFeature(featureName) || ($rootScope.showBetaFeatures && _self.isBetaFeature(featureName));
    };

    this.disableAlphaFeatures = function () {
      LogSvc.debug('[UnifyPhoneLabSvc] Disable alpha features');
      var oldValues = Utils.shallowCopy(_unifyPhoneLabFeatures);
      var reloadRequired = false;
      ALPHA_FEATURES.forEach(function (featureName) {
        if (_unifyPhoneLabFeatures[featureName] && isReloadRequired(featureName)) {
          LogSvc.info('[SettingsCtrl]: Toggled UnifyPhone Lab feature which requires client reload: ', featureName);
          reloadRequired = true;
        }
        _unifyPhoneLabFeatures[featureName] = false;
      });

      updateFeatures(oldValues);

      if (reloadRequired) {
        reloadPage('res_LabsAlphaDeactivatedReload');
      }
    };

    this.initFromLocalStorage = function () {
      if (_isInitialized) {
        return;
      }

      $rootScope.enableAlphaFeatures = !!LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.ENABLE_ALPHA_TESTING) || !Utils.isProductionDomain();
      LogSvc.info('[UnifyPhoneLabSvc] Alpha flags are ', $rootScope.enableAlphaFeatures ? 'enabled' : 'disabled');

      _isInitialized = true;
      _unifyPhoneLabFeatures = normalizeObject(LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.UNIFY_PHONE_LABS));
      LogSvc.info('[UnifyPhoneLabSvc] Retrieved UnifyPhone Labs features from local store: ', _unifyPhoneLabFeatures);

      // Update the local storage with normalized values
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.UNIFY_PHONE_LABS, _unifyPhoneLabFeatures);

      // Show alpha features in UnifyPhone Labs if at least one feature is enabled
      $rootScope.showAlphaFeatures = $rootScope.enableAlphaFeatures && this.hasAlphaFeaturesEnabled();
      if ($rootScope.showAlphaFeatures) {
        $rootScope.showBetaFeatures = true;
        LogSvc.warn('[UnifyPhoneLabSvc]: There is at least one Alpha feature enabled.');
      } else {
        // Show beta features in UnifyPhone Labs if at least one feature is enabled
        $rootScope.showBetaFeatures = this.hasBetaFeaturesEnabled();
        if ($rootScope.showBetaFeatures) {
          LogSvc.info('[UnifyPhoneLabSvc]: There is at least one Beta feature enabled.');
        }
      }

      $rootScope.showDemoFeatures = this.hasDemoFeaturesEnabled();
      if ($rootScope.showDemoFeatures) {
        LogSvc.info('[UnifyPhoneLabSvc]: There is at least one Demo feature enabled.');
      }

      updateRootScope();
      updateAllResources(true);
    };

    this.isReloadRequired = isReloadRequired;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////////////////
    $rootScope.unifyPhoneLabs = $rootScope.unifyPhoneLabs || {};
    // Do not initialize _unifyPhoneLabFeatures && $rootScope.unifyPhoneLabs yet.
    // These variables must only be initialized when initFromLocalStorage is invoked.
    // This is needed because we use bind-once for some features, so we can only set
    // the value for these features once.
    _featureList = ALL_FEATURES.map(function (featureName) {
      var isAlpha, isDemo, isClosedBeta, typeRes;
      if (ALPHA_FEATURES.includes(featureName)) {
        isAlpha = true;
        typeRes = 'res_FeatureAlpha';
      } else if (DEMO_FEATURES.includes(featureName)) {
        isDemo = true;
        typeRes = 'res_FeatureDemo';
      } else if (BETA_FEATURES.includes(featureName)) {
        isClosedBeta = true;
        typeRes = 'res_FeatureBeta';
      }

      return {
        featureName: featureName,
        titleRes: 'res_Labs_' + featureName + '_Title',
        detailsRes: 'res_Labs_' + featureName + '_Details',
        typeRes: typeRes,
        isAlpha: !!isAlpha,
        isDemo: !!isDemo,
        isClosedBeta: !!isClosedBeta
      };
    });

    backupOriginalResources();

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.UnifyPhoneLabSvcImpl = UnifyPhoneLabSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global RegistrationState*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var UserToUserHandler = unifyPhone.UserToUserHandlerSingleton;
  var Enums = unifyPhone.Enums;
  var Constants = unifyPhone.Constants;

  // eslint-disable-next-line max-params, max-lines-per-function
  function InstrumentationSvcImpl($rootScope, LogSvc, PubSubSvc, LocalStoreSvc) {
    // NOSONAR
    LogSvc.debug('New Service: InstrumentationSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _clientApiHandler = ClientApiHandler.getInstance();
    var _userToUserHandler = UserToUserHandler.getInstance();

    var NO_DATA_16_BIT = '65535';
    var NO_DATA_24_BIT = '16777215';
    var NO_DATA_STRING = '';
    var NO_DATA = 'N/A';

    var MAX_NUM_OF_CALLS_PENDING_QOS = 3; // Save up to 3 QoS reports that couldn't be submitted to the server

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function submitInstrData() {
      var records = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.QOS_PENDING_RECORDS);
      if (records && records.length) {
        LogSvc.debug('[InstrumentationSvc]: Submit pending QoS records. Num of records:', records.length);
        records.forEach(function (qos) {
          _clientApiHandler.submitQOSData(qos, null, function (err) {
            if (err) {
              // We won't try again
              LogSvc.warn('[InstrumentationSvc]: Error submitting pending QoS data for call instance ID: ' + qos[0] && qos[0].rtcInstanceId, err);
            }
          });
        });
        LocalStoreSvc.removeItem(LocalStoreSvc.keys.QOS_PENDING_RECORDS);
      }
    }

    function processPacketLost(pl) {
      if (pl === undefined || pl < 0 || pl > 65535) {
        return NO_DATA_16_BIT;
      }
      return pl;
    }

    function processAverage(field, lastReading, savedStats) {
      if (savedStats) {
        var sum = savedStats[field + 'Sum'];
        var count = savedStats[field + 'Count'];
        if (sum !== undefined && count) {
          if (lastReading) {
            // We have to include the last reading as well
            var last = lastReading[field];
            if (last !== undefined) {
              sum += last;
              count++;
            }
          }
          return sum / count;
        }
      }
      return undefined;
    }

    function getDeviceName(device) {
      if (!device || typeof device !== 'string') {
        return NO_DATA;
      }
      if (device.startsWith('res_')) {
        return $rootScope.i18n.localize(device) || NO_DATA;
      }
      return device;
    }

    function addDeviceInfo(qosData, mediaType, call) {
      // UD (User Device)
      qosData.push({
        name: 'UD',
        value: window.navigator.platform
      });
      if (mediaType === Constants.QOSMediaType.AUDIO) {
        qosData.push({
          name: 'PBD',
          value: getDeviceName(call.playbackDevice)
        });
        qosData.push({
          name: 'RECD',
          value: getDeviceName(call.recordingDevice)
        });

        // Send Device List parameter only if list is available
        if (call.playbackDeviceList.length) {
          qosData.push({
            name: 'PBDL',
            value: call.playbackDeviceList.join('||')
          });
        }
        if (call.recordingDeviceList.length) {
          qosData.push({
            name: 'RECDL',
            value: call.recordingDeviceList.join('||')
          });
        }
      } else if (mediaType === Constants.QOSMediaType.VIDEO) {
        qosData.push({
          name: 'VIDD',
          value: getDeviceName(call.videoDevice)
        });

        // Send Device List parameter only if list is available
        if (call.videoDeviceList.length) {
          qosData.push({
            name: 'VIDDL',
            value: call.videoDeviceList.join('||')
          });
        }
      }
    }

    function addChannelInfo(stats, qosData) {
      if (!stats.channelInfo) {
        stats.channelInfo = {};
      }
      // IPL (IP Address, local)
      qosData.push({
        name: 'IPL',
        value: stats.channelInfo.ipl || NO_DATA_STRING
      });
      // PTL (Port #, local)
      qosData.push({
        name: 'PTL',
        value: stats.channelInfo.ptl || NO_DATA_16_BIT
      });
      // IPR (IP Address, remote)
      qosData.push({
        name: 'IPR',
        value: stats.channelInfo.ipr || NO_DATA_STRING
      });
      // PTR (Port #, remote)
      qosData.push({
        name: 'PTR',
        value: stats.channelInfo.ptr || NO_DATA_16_BIT
      });
      // LCT (Local Candidate Type)
      qosData.push({
        name: 'LCT',
        value: stats.channelInfo.lct || NO_DATA_STRING
      });
      // RCT (Remote Candidate Type)
      qosData.push({
        name: 'RCT',
        value: stats.channelInfo.rct || NO_DATA_STRING
      });
      // TT (Transport Type)
      qosData.push({
        name: 'TT',
        value: stats.channelInfo.tt || NO_DATA_STRING
      });
      // NI (Network Interface)
      qosData.push({
        name: 'NI',
        value: !stats.channelInfo.ni || stats.channelInfo.ni === 'unknown' ? NO_DATA_STRING : stats.channelInfo.ni
      });
      if (stats.channelInfo.candidate) {
        // SADDR (Source Address - same as IPL for host candidates, raddr for relay/srflx candidates)
        qosData.push({
          name: 'SADDR',
          value: stats.channelInfo.candidate.sAddr || NO_DATA_STRING
        });

        // HADDR (Host Address - same as IPL for host candidates, associated host candidate for relay/srflx candidates)
        qosData.push({
          name: 'HADDR',
          value: stats.channelInfo.candidate.hAddr || NO_DATA_STRING
        });

        // HADDR_netmask (netmask associated with the HADDR)
        if (stats.channelInfo.candidate.netmask) {
          qosData.push({
            name: 'HADDR_netmask',
            value: stats.channelInfo.candidate.netmask
          });
        }

        // TURN_TT (Relay candidate local transport type)
        if (stats.channelInfo.candidate.tt) {
          qosData.push({
            name: 'TURN_TT',
            value: stats.channelInfo.candidate.tt
          });
        }
      }
    }

    function addCallInfo(call, stats, qosData) {
      // RTC Session Instance ID
      qosData.push({
        name: 'RID',
        value: call.instanceId || NO_DATA
      });
      // CT (Conversation type)
      qosData.push({
        name: 'CT',
        value: call.isDirect ? 'direct' : 'group'
      });
      var terminateReason = call.terminateReason || '';
      if (terminateReason) {
        if (!terminateReason.startsWith(Enums.CallServerTerminatedReason.SERVER_ENDED_PRFX)) {
          terminateReason = Enums.CallClientTerminatedReason.CLIENT_ENDED_PRFX + terminateReason;
        }
      } else {
        terminateReason = NO_DATA;
      }
      qosData.push({
        name: 'INFO',
        value: terminateReason
      });
    }

    function addTransmitStats(stats, qosData) {
      if (!stats.transmit) {
        stats.transmit = {};
      }
      // SRCS (SSRC sending stream)
      qosData.push({
        name: 'SRCS',
        value: stats.transmit.ssrc || NO_DATA
      });
      // PS (Packets sent)
      qosData.push({
        name: 'PS',
        value: stats.transmit.ps >= 0 ? stats.transmit.ps : NO_DATA_24_BIT
      });
      // OR (Octets received)
      qosData.push({
        name: 'OS',
        value: stats.transmit.os >= 0 ? stats.transmit.os : NO_DATA_24_BIT
      });
      // Deprecated: EN (Encoding codec payload type)
      // Information not available through webRTC stats
      qosData.push({
        name: 'EN',
        value: NO_DATA
      });
      // Deprecated: ST (Encoding codec payload subtype - name)
      qosData.push({
        name: 'ST',
        value: stats.transmit.en || NO_DATA
      });
      // Encoder codec name (this field replaces EN and ST)
      qosData.push({
        name: 'EN_NAME',
        value: stats.transmit.en || NO_DATA
      });
      // PLL (Packets lost local)
      qosData.push({
        name: 'PLL',
        value: processPacketLost(stats.transmit.pl)
      });
      if (stats.packetLossSendViolations) {
        // PLL_VI (PLL Violations)
        qosData.push({
          name: 'PLL_VI',
          value: stats.packetLossSendViolations
        });
      }
      if (stats.transmit.ecmaxMax >= 0) {
        // ECHO_AVG (Max echo likelihood)
        qosData.push({
          name: 'ECHO_MAX',
          value: stats.transmit.ecmaxMax
        });
      }
      if (stats.transmit.ecmaxAvg >= 0) {
        // ECHO_AVG (Average echo likelihood)
        qosData.push({
          name: 'ECHO_AVG',
          value: stats.transmit.ecmaxAvg.toFixed(1)
        });
      }
      if (stats.googResidualEchoLikelihoodRecentMaxViolations) {
        // ECHO_VI (Echo Violations)
        qosData.push({
          name: 'ECHO_VI',
          value: stats.googResidualEchoLikelihoodRecentMaxViolations
        });
      }
    }

    function addReceiveStats(stats, qosData) {
      if (!stats.receive) {
        stats.receive = {};
      }
      // SRCR (SSRC receiving stream)
      qosData.push({
        name: 'SRCR',
        value: stats.receive.ssrc || NO_DATA
      });
      // PR (Received packets)
      qosData.push({
        name: 'PR',
        value: stats.receive.pr >= 0 ? stats.receive.pr : NO_DATA_24_BIT
      });
      // JI (Jitter)
      qosData.push({
        name: 'JI',
        value: stats.receive.jr >= 0 ? stats.receive.jr : NO_DATA_16_BIT
      });
      if (stats.receive.jrMax >= 0) {
        // JI_MAX (Max jitter)
        qosData.push({
          name: 'JI_MAX',
          value: stats.receive.jrMax
        });
      }
      if (stats.receive.jrAvg >= 0) {
        // JI_AVG (Average jitter)
        qosData.push({
          name: 'JI_AVG',
          value: Math.round(stats.receive.jrAvg)
        });
      }
      if (stats.googJitterReceivedViolations) {
        // JI_VI (Jitter Violations)
        qosData.push({
          name: 'JI_VI',
          value: stats.googJitterReceivedViolations
        });
      }
      // PLR (Packets lost remote)
      qosData.push({
        name: 'PLR',
        value: processPacketLost(stats.receive.pl)
      });
      if (stats.packetLossRecvViolations) {
        // PLR_VI (PLR Violations)
        qosData.push({
          name: 'PLR_VI',
          value: stats.packetLossRecvViolations
        });
      }
      // OR (Octets received)
      qosData.push({
        name: 'OR',
        value: stats.receive.or >= 0 ? stats.receive.or : NO_DATA_24_BIT
      });
      // Deprecated: DE (Decoding codec payload type)
      // Information not available through webRTC stats
      qosData.push({
        name: 'DE',
        value: NO_DATA
      });
      // Deprecated: ST (Decoding codec payload subtype - name)
      qosData.push({
        name: 'ST',
        value: stats.receive.en || NO_DATA
      });
      // Decoder codec name (this field replaces DE and ST)
      qosData.push({
        name: 'DE_NAME',
        value: stats.receive.en || NO_DATA
      });
    }

    function buildQOSArray(call, mediaType, rtpStats) {
      var qosData = [];
      var type, stats;
      switch (mediaType) {
        case Constants.QOSMediaType.AUDIO:
          type = 'audio';
          stats = rtpStats.audio;
          break;
        case Constants.QOSMediaType.VIDEO:
          type = 'video';
          stats = rtpStats.video;
          break;
        case Constants.QOSMediaType.SCREEN_SHARE:
          type = 'screen share';
          stats = rtpStats.video;
          break;
        default:
          // Unknown type
          return qosData;
      }
      // Get the start of report timestamp (NTP format)
      var tb = rtpStats.startTime || Date.now();
      // 2208988800 is the number of seconds between 1/1/1900 and 1/1/1970 (Unix epoch)
      tb = Math.floor(tb / 1000) + 2208988800;
      // Get the end of report timestamp in NTP format (epoch is Jan 1, 1900)
      var te = new Date().getTime();
      te = Math.floor(te / 1000) + 2208988800;
      // MT (Media type)
      qosData.push({
        name: 'MT',
        value: type
      });
      // TB (Date and Time for begin of report period - NTP timestamp)
      qosData.push({
        name: 'TB',
        value: tb
      });
      // TE (Date and Time for end of report period - NTP timestamp)
      qosData.push({
        name: 'TE',
        value: te
      });
      // LA (Round trip delay)
      qosData.push({
        name: 'LA',
        value: stats.rtt >= 0 ? stats.rtt : NO_DATA_16_BIT
      });
      if (stats.rttAvg >= 0) {
        // LA_AVG (Average round trip delay)
        qosData.push({
          name: 'LA_AVG',
          value: Math.round(stats.rttAvg)
        });
      }
      if (stats.rttMax >= 0) {
        // LA_MAX (Max round trip delay)
        qosData.push({
          name: 'LA_MAX',
          value: stats.rttMax
        });
      }
      if (stats.googRttViolations) {
        // LA_VI (LA Violations)
        qosData.push({
          name: 'LA_VI',
          value: stats.googRttViolations
        });
      }
      addDeviceInfo(qosData, mediaType, call);
      addChannelInfo(stats, qosData);
      addCallInfo(call, stats, qosData);
      addTransmitStats(stats, qosData);
      addReceiveStats(stats, qosData);
      // Make sure that value is a string for all stats
      qosData.forEach(function (data) {
        data.value += '';
      });
      return qosData;
    }

    function buildTelephonyQosData(data, call) {
      var qosData = [{ name: 'CR', value: $rootScope.localUser.phoneNumber }];
      var relevantValues = ['TB', 'TE', 'IPL', 'PTL', 'IPR', 'PTR', 'SRCS', 'SRCR', 'MT', 'ST', 'PR', 'JI', 'LA', 'PLL', 'PS'];
      relevantValues.forEach(function (relevantValue) {
        data.some(function (pair) {
          if (pair.name === relevantValue) {
            if (pair.name === 'TB' || pair.name === 'TE') {
              // Special case: Need to convert Time values from NTP format to Unix Epoch
              qosData.push({ name: pair.name, value: pair.value - 2208988800 + '.0' });
            } else {
              qosData.push(pair);
            }
            return true;
          }
          return false;
        });
      });
      if (call && call.peerUser && call.peerUser.phoneNumber) {
        qosData.push({ name: 'CD', value: call.peerUser.phoneNumber });
      }
      qosData.push({ name: 'ET', value: $rootScope.browser.type + ' ' + $rootScope.browser.version });
      qosData.push({ name: 'CV', value: $rootScope.clientVersion });
      qosData.push({ name: 'TI', value: $rootScope.localUser.tenantId });
      return qosData;
    }

    function checkViolationsRecord(stat, savedStats) {
      var start = stat + 'LastViStart';
      var viProperty = stat + 'Violations';
      if (savedStats && savedStats[start]) {
        var violations = savedStats[viProperty];
        // There's an ongoing violation, add it to the records
        return (violations ? violations + ',' : '') + '(' + savedStats[start] + ')';
      }
      return savedStats[viProperty];
    }

    function returnMax(val1, val2) {
      if (val1 !== undefined) {
        if (val2 !== undefined) {
          return val1 >= val2 ? val1 : val2;
        } else {
          return val1;
        }
      } else if (val2 !== undefined) {
        return val2;
      }
      return undefined;
    }

    function finalizeStats(stats, saved) {
      // Process max, average and number of violations
      if (saved) {
        var channelId, violations;
        if (stats.receive && saved[stats.receive.ssrc]) {
          channelId = stats.receive.channelId;
          stats.receive.jrMax = returnMax(saved[stats.receive.ssrc].jrMax, stats.receive.jr);
          stats.receive.jrAvg = processAverage('jr', stats.receive, saved[stats.receive.ssrc]);
          violations = ['googJitterReceived', 'packetLossRecv'];
          violations.forEach(function (v) {
            stats.receive[v + 'Violations'] = checkViolationsRecord(v, saved[stats.receive.ssrc]);
          });
        }
        if (stats.transmit && saved[stats.transmit.ssrc]) {
          channelId = stats.transmit.channelId;
          stats.transmit.ecmaxMax = returnMax(saved[stats.transmit.ssrc].ecmaxMax, stats.transmit.ecmax);
          stats.transmit.ecmaxAvg = processAverage('ecmax', stats.transmit, saved[stats.transmit.ssrc]);
          violations = ['packetLossSend', 'googResidualEchoLikelihoodRecentMax'];
          violations.forEach(function (v) {
            stats.transmit[v + 'Violations'] = checkViolationsRecord(v, saved[stats.transmit.ssrc]);
          });
        }

        if (channelId && saved.channelInfo) {
          var savedChannelInfo = saved.channelInfo[channelId];
          if (savedChannelInfo) {
            stats.rttAvg = processAverage('rtt', stats, savedChannelInfo);
            stats.rttMax = returnMax(savedChannelInfo.rttMax, stats.rtt);
            stats.googRttViolations = checkViolationsRecord('googRtt', savedChannelInfo);
          }
        }
      } else {
        // There's no history available, so assign the last reading to average and max fields
        if (stats.receive) {
          stats.receive.jrMax = stats.receive.jrAvg = stats.receive.jr;
        }
        if (stats.transmit) {
          stats.transmit.ecmaxMax = stats.transmit.ecmaxAvg = stats.transmit.ecmax;
        }
        stats.rttAvg = stats.rttMax = stats.rtt;
      }
    }

    function onSubmitQosFailed(err, localCall, qosData) {
      if (err === Constants.ReturnCode.REQUEST_TIMEOUT || err === Constants.ReturnCode.DISCONNECTED || err === Constants.ReturnCode.FAILED_TO_SEND) {
        // Failed to send the QoS report because we're not connected to the server
        LogSvc.warn('[InstrumentationSvc]: Error submitting QoS data for call ID: ' + localCall.callId + '. Will try again when reconnected', err);

        var records = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.QOS_PENDING_RECORDS) || [];
        if (!Array.isArray(records)) {
          LogSvc.warn('[InstrumentationSvc]: Invalid stored QoS records discarded');
          records = [qosData];
        } else {
          records.push(qosData);
          if (records.length > MAX_NUM_OF_CALLS_PENDING_QOS) {
            records = records.slice(-MAX_NUM_OF_CALLS_PENDING_QOS);
            LogSvc.warn('[InstrumentationSvc]: Discarded older QoS records');
          }
        }
        LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.QOS_PENDING_RECORDS, records);
      } else {
        LogSvc.warn('[InstrumentationSvc]: Error submitting QoS data for call ID: ' + localCall.callId + '. ', err);
      }
    }

    function submitQOSData(localCall, rtpStats, savedRtpStats, streamQualityData) {
      var qosData = [],
        mediaData = [],
        videoList = [],
        screenList = [],
        videoQosCandidate = null;
      Object.keys(rtpStats.channelInfo).forEach(function (channelId) {
        // Build audio and video send/receive pairs
        var pairs = {
          audio: [],
          video: []
        };
        ['audio', 'video'].forEach(function (media) {
          if (rtpStats[media]) {
            var pair = {};
            ['transmit', 'receive'].forEach(function (direction) {
              Object.keys(rtpStats[media][direction]).forEach(function (ssrc) {
                var trackInfo = rtpStats[media][direction][ssrc];
                if (trackInfo.channelId === channelId) {
                  if (pair[direction]) {
                    pairs[media].push(pair);
                    pair = {};
                  }
                  pair.rtt = trackInfo.channelInfo.rtt;
                  pair.channelInfo = trackInfo.channelInfo;
                  pair[direction] = trackInfo;
                }
              });
            });
            if (pair.transmit || pair.receive) {
              pairs[media].push(pair);
            }
          }
        });

        pairs.audio.forEach(function (audio) {
          if (audio.transmit || audio.receive) {
            finalizeStats(audio, savedRtpStats);
            mediaData = buildQOSArray(localCall, Constants.QOSMediaType.AUDIO, { audio: audio, startTime: rtpStats.startTime });
            qosData.push({
              convID: localCall.convId,
              mediaType: Constants.QOSMediaType.AUDIO,
              data: mediaData,
              rtcSessionId: localCall.callId,
              rtcInstanceId: localCall.instanceId,
              associatedClientId: localCall.clientId
            });
          }
        });

        pairs.video.forEach(function (video) {
          if (video.transmit || video.receive) {
            if ((video.transmit && video.transmit.content) === 'screen' || (video.receive && video.receive.content) === 'screen') {
              video.videoType = Constants.QOSMediaType.SCREEN_SHARE;
              screenList.push(video);
            } else {
              video.videoType = Constants.QOSMediaType.VIDEO;
              if (video.transmit) {
                // Add transmitting pair to the QoS
                videoList.push(video);
              } else if (!videoQosCandidate) {
                // Randomly chosen receive-only pair
                videoQosCandidate = video;
              }
            }
          }
        });
      });

      if (!videoList.length && videoQosCandidate) {
        // For video, we report the screenshare and a video pair that contains a transmitting video,
        // but if we're not transmitting video, pick a random receiving video connection
        videoList.push(videoQosCandidate);
      }
      Array.prototype.push.apply(videoList, screenList);
      videoList.forEach(function (video) {
        finalizeStats(video, savedRtpStats);
        mediaData = buildQOSArray(localCall, video.videoType, { video: video, startTime: rtpStats.startTime });
        qosData.push({
          convID: localCall.convId,
          mediaType: video.videoType,
          data: mediaData,
          rtcSessionId: localCall.callId,
          rtcInstanceId: localCall.instanceId,
          associatedClientId: localCall.clientId
        });
      });

      if (qosData.length) {
        _clientApiHandler.submitQOSData(qosData, streamQualityData, function (err) {
          if (err) {
            onSubmitQosFailed(err, localCall, qosData);
          }
        });
        if (localCall.isTelephonyCall && $rootScope.localUser.associatedTelephonyUserID) {
          var data = {
            content: {
              type: 'QOS',
              qosData: buildTelephonyQosData(qosData[0].data, localCall)
            },
            destUserId: $rootScope.localUser.associatedTelephonyUserID
          };
          _userToUserHandler.sendAtcRequest(data);
        }
      } else {
        LogSvc.warn('[InstrumentationSvc]: No QoS data to be submitted');
      }

      // Update call object with the current clientId (in case it changed)
      localCall.clientId = _clientApiHandler.clientId;
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/registration/state', function (state) {
      LogSvc.debug('[InstrumentationSvc]: Received /registration/state event');
      if (state === RegistrationState.Registered) {
        submitInstrData();
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.sendQOSData = function (localCall, mediaType, rtpStats, savedStats, streamQualityData) {
      if (!localCall || !rtpStats) {
        return;
      }
      submitQOSData(localCall, rtpStats, savedStats, streamQualityData);
      LogSvc.info('[InstrumentationSvc]: Send QoS data for call Id:', localCall.callId);
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.InstrumentationSvcImpl = InstrumentationSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global chrome, require*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var ChromeExtension = unifyPhone.ChromeExtension;
  var ExtensionConnHandlerSingleton = unifyPhone.ExtensionConnHandlerSingleton;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-params, max-lines-per-function
  function ExtensionSvcImpl($rootScope, $timeout, $window, $q, LogSvc, PubSubSvc, PopupSvc, RegistrationSvc) {
    // NOSONAR
    LogSvc.debug('New Service: ExtensionSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var SANDBOX_HOSTNAME = 'unifyPhonesandbox.net';
    var BETA_HOSTNAME = 'beta.unifyPhone.com';
    var T_AND_I_HOSTNAME = 'tandi.unifyPhonesandbox.net';
    var UNIVERGEBLUE_HOSTNAME = 'teams.univergeblue.com';
    var BLUEBETA_HOSTNAME = 'bluebeta.unifyPhone.com';

    var CHROME_URL = 'https://chrome.google.com/webstore/detail/';
    var EDGE_URL = 'https://microsoftedge.microsoft.com/addons/detail/';
    var DEFAULT_EDGE_URL = 'https://microsoftedge.microsoft.com/';

    // This defines the minimum extension version supported by the webclient.
    // Update this as old extension versions no longer work with the webclient.
    var MIN_EXTENSION_VERSION_STR = '0.0.242';
    var MIN_EXTENSION_VERSION = Utils.convertVersionToNumber(MIN_EXTENSION_VERSION_STR);

    var ExtensionInstallErrorCodes = Object.freeze({
      UNSUPPORTED_BROWSER: 'UNSUPPORTED_BROWSER',
      UNSUPPORTED_EXT_VERSION: 'UNSUPPORTED_EXT_VERSION',
      USER_CANCELLED_INSTALL: 'USER_CANCELLED_INSTALL',
      INTERNAL_ERROR: 'INTERNAL_ERROR'
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _self = this;
    var _extConnHandler = typeof ExtensionConnHandlerSingleton === 'object' ? ExtensionConnHandlerSingleton.getInstance() : null;

    var _extensionVersion = '';
    var _extensionVersionSupported = true;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function getExtensionIdFromHostName() {
      var hostname = $window.location.hostname;
      var id;
      switch (hostname) {
        case BETA_HOSTNAME:
          id = 'jafbhekoolndfieffjkikfbmekjppnec';
          LogSvc.info('[ExtensionSvc]: Set extension ID to Beta-UnifyPhone extension: ', id);
          break;
        case SANDBOX_HOSTNAME:
          id = 'ogdfpjnhdggplglemglldmioiblhkadm';
          LogSvc.info('[ExtensionSvc]: Set extension ID to Sandbox-UnifyPhone extension: ', id);
          break;
        case T_AND_I_HOSTNAME:
          id = 'gdgfgnopapmocggkadgfkjekdlcohmdl';
          LogSvc.info('[ExtensionSvc]: Set extension ID to T&I-UnifyPhone extension: ', id);
          break;
        case UNIVERGEBLUE_HOSTNAME:
        case BLUEBETA_HOSTNAME:
          id = 'jhpcobccapenojpdohnfmhmdmppngdad';
          LogSvc.info('[ExtensionSvc]: Set extension ID to UNIVERGE BLUE TC extension: ', id);
          break;
        default:
          id = 'mhkbaognlahkdimlfcfhbeihldmjofgg';
          LogSvc.info('[ExtensionSvc]: Set extension ID to UnifyPhone extension: ', id);
          break;
      }
      return id;
    }

    function getExtensionInstallUrl() {
      var url = $rootScope.browser.subType === 'edge' ? EDGE_URL : CHROME_URL;
      var extensionId;
      if ($rootScope.browser.subType === 'edge') {
        LogSvc.info('[ExtensionSvc]: Set URL to Edge extension webstore link');
        extensionId = RegistrationSvc.getEdgeExtensionId();
        if (extensionId) {
          LogSvc.info('[ExtensionSvc]: Set Edge extension URL using Extension ID set in global properties: ', extensionId);
          url += extensionId;
        } else {
          LogSvc.info('[ExtensionSvc]: Set Default Edge extension URL');
          url = DEFAULT_EDGE_URL;
        }
        return url;
      }

      extensionId = RegistrationSvc.getChromeExtensionId();
      if (extensionId) {
        LogSvc.info('[ExtensionSvc]: Set Chrome extension URL using Extension ID set in global properties: ', extensionId);
      } else {
        extensionId = getExtensionIdFromHostName();
        LogSvc.info('[ExtensionSvc]: Set Chrome extension URL using Extension ID from host name: ', extensionId);
      }
      url += extensionId;
      return url;
    }

    function init() {
      $rootScope.isBrowserExtRunning = _self.isExtensionRunning();
    }

    function invokeHandlerApi(apiName, params, cb) {
      LogSvc.debug('[ExtensionSvc]: Invoke ExtensionConnHandler API: ', apiName);
      if (!_extConnHandler) {
        LogSvc.debug('[ExtensionSvc]: ExtensionConnHandler is not loaded');
        cb &&
          $timeout(function () {
            cb('No ExtensionConnHandler');
          }, 0);
        return undefined;
      }
      if (!_extConnHandler.isExtensionRunning()) {
        LogSvc.debug('[ExtensionSvc]: Chrome extension is not running');
        cb &&
          $timeout(function () {
            cb('Extension not running');
          }, 0);
        return undefined;
      }
      if (cb) {
        params = params || [];
        // Push a callback wrapper that applies the digest cycle
        params.push(function (err, data) {
          $rootScope.$apply(function () {
            cb(err, data);
          });
        });
      }

      return _extConnHandler[apiName].apply(_extConnHandler, params);
    }

    function validateExtensionVersion() {
      LogSvc.info('[ExtensionSvc]: Validate the installed extension version: ', _extensionVersion);
      _extensionVersionSupported = Utils.convertVersionToNumber(_extensionVersion) >= MIN_EXTENSION_VERSION;
      if (!_extensionVersionSupported) {
        LogSvc.warn(
          '[ExtensionSvc]: The installed extension version (' + _extensionVersion + ') is not supported. Minimum version is ' + MIN_EXTENSION_VERSION_STR
        );
      }
    }

    function extensionInstallPreChecks(showErrorPopup) {
      if (!$rootScope.browser || !$rootScope.browser.chrome) {
        return ExtensionInstallErrorCodes.UNSUPPORTED_BROWSER;
      }

      // Check if the extension needs to be updated
      if (!_extensionVersionSupported) {
        LogSvc.warn('[ExtensionSvc]: We are running an outdated extension:', _extensionVersion);
        if (showErrorPopup && PopupSvc) {
          PopupSvc.error({ message: 'res_UnsupportedExtensionVersion' });
        }
        return ExtensionInstallErrorCodes.UNSUPPORTED_EXT_VERSION;
      }
      return null;
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    if (_extConnHandler) {
      _extConnHandler.addEventListener('extensionInitialized', function (evt) {
        $rootScope.$apply(function () {
          _extensionVersion = evt.data.version;
          validateExtensionVersion();
          if (_extensionVersionSupported) {
            $rootScope.isBrowserExtRunning = true;
            LogSvc.setExtensionVersion(_extensionVersion);
            LogSvc.debug('[ExtensionSvc]: Publish /chromeExt/initialized event');
            PubSubSvc.publish('/chromeExt/initialized');
          }
        });
      });

      _extConnHandler.addEventListener('extensionUnregistered', function () {
        $rootScope.$apply(function () {
          $rootScope.isBrowserExtRunning = false;
          LogSvc.debug('[ExtensionSvc]: Publish /chromeExt/unregistered event');
          PubSubSvc.publish('/chromeExt/unregistered');
        });
      });

      var onConnHandlerEvent = function (eventName, evt) {
        $rootScope.$apply(function () {
          var data = evt.data && evt.data.data;
          LogSvc.debug('[ExtensionSvc]: Publish ' + eventName + ' event: ', data);
          PubSubSvc.publish(eventName, [data]);
        });
      };

      _extConnHandler.addEventListener('headsetAppLaunched', onConnHandlerEvent.bind(null, '/chromeExt/headsetAppLaunched'));
      _extConnHandler.addEventListener('headsetAppUninstalled', onConnHandlerEvent.bind(null, '/chromeExt/headsetAppUninstalled'));
      _extConnHandler.addEventListener('webRTCIPHandlingPolicyChanged', onConnHandlerEvent.bind(null, '/chromeExt/webRTCIPHandlingPolicyChanged'));
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    /**
     * Checks if Chrome Extension is installed and running.
     *
     * @returns {bool} true is extension is installed and running.
     */
    this.isExtensionRunning = function () {
      return _extConnHandler && _extConnHandler.isExtensionRunning() && _extensionVersionSupported;
    };

    /**
     * Returns extension version
     *
     * @returns {String} Extension version
     */
    this.getExtensionVersion = function () {
      return _extensionVersion;
    };

    /**
     * Returns whether the version of the installed extension is supported or not
     *
     * @returns {Boolean} true if supported, false otherwise
     */
    this.isExtensionVersionSupported = function () {
      return _extensionVersionSupported;
    };

    /***
     * This method opens up a modal asking for a confirmation from the user before actually installing the extension
     *
     * @param {Boolean} showErrorPopup Indicates whether this API should show a popup in case of error.
     * @returns {Promise} A promise that is resolved once the extension is installed.
     */
    this.installExtension = function (showErrorPopup) {
      return new $q(function (resolve, reject) {
        if (_self.isExtensionRunning()) {
          // Extension is already installed
          resolve();
          return;
        }

        if (!PopupSvc) {
          LogSvc.error('[ExtensionSvc]: installExtension API requires PopupSvc');
          reject(ExtensionInstallErrorCodes.INTERNAL_ERROR);
          return;
        }

        var error = extensionInstallPreChecks(showErrorPopup);
        if (error) {
          reject(error);
          return;
        }

        var options = {
          message: 'res_ActionInstallExtensionText',
          title: 'res_ActionInstallExtensionTitle',
          yesLabel: 'res_ActionInstall',
          noLabel: 'res_Close',
          backdrop: true
        };

        PopupSvc.confirm(options)
          .result.then(function () {
            _self.inlineInstallation(showErrorPopup).then(resolve).catch(reject);
          })
          .catch(function () {
            reject(ExtensionInstallErrorCodes.USER_CANCELLED_INSTALL);
          });
      });
    };

    /***
     * This method performs the actual inline installation, skipping the modal confirmation.
     *
     * @param {Boolean} showErrorPopup Indicates whether this API should show a popup in case of error.
     * @returns {Promise} A promise that is resolved once the extension is installed.
     */
    this.inlineInstallation = function (showErrorPopup) {
      return new $q(function (resolve, reject) {
        var error = extensionInstallPreChecks(showErrorPopup);
        if (error) {
          reject(error);
          return;
        }

        var extensionUrl = getExtensionInstallUrl();
        LogSvc.debug('[ExtensionSvc]: Open store link for ', extensionUrl);
        $window.open(extensionUrl);
        resolve();
      });
    };

    this.addEventListener = function (target, cb) {
      _extConnHandler && _extConnHandler.addEventListener(target, cb);
    };

    /******************************************************
     * Exchange Connector APIs
     ******************************************************/
    this.exchangeGetConnectionStatus = function (key, cb) {
      return invokeHandlerApi('exchangeGetConnectionStatus', [key], cb);
    };

    this.exchangeConnect = function (settings, cb) {
      return invokeHandlerApi('exchangeConnect', [settings], cb);
    };

    this.exchangeSearchContacts = function (key, searchStr, resCount, cb) {
      return invokeHandlerApi('exchangeSearchContacts', [key, searchStr, resCount], cb);
    };

    this.exchangeGetCapabilities = function (cb) {
      return invokeHandlerApi('exchangeGetCapabilities', [], cb);
    };

    this.exchangeResolveContact = function (key, contactData, cb) {
      return invokeHandlerApi('exchangeResolveContact', [key, contactData], cb);
    };

    this.exchangeGetContact = function (key, exchangeEmail, cb) {
      return invokeHandlerApi('exchangeGetContact', [key, exchangeEmail], cb);
    };

    this.exchangeDisconnect = function (cb) {
      return invokeHandlerApi('exchangeDisconnect', [], cb);
    };

    this.exchangeOnRenewedToken = function (key, reqId, token, cb) {
      return invokeHandlerApi('exchangeOnRenewedToken', [key, reqId, token], cb);
    };

    this.storeExchCredentials = function (credentials, cb) {
      return invokeHandlerApi('exchangeStoreCredentials', [credentials], cb);
    };

    this.getStoredCredentials = function (key, cb) {
      return invokeHandlerApi('getStoredCredentials', [key], cb);
    };

    this.getAllPersonalContacts = function (key, cb) {
      return invokeHandlerApi('getAllPersonalContacts', [key], cb);
    };

    this.syncAllPersonalContacts = function (key, syncState, cb) {
      return invokeHandlerApi('syncAllPersonalContacts', [key, syncState], cb);
    };

    this.exchangeCancelReqCallback = function (reqId) {
      return invokeHandlerApi('exchangeCancelReqCallback', [reqId]);
    };

    this.getAppointments = function (key, startDate, endDate, resCount, cb) {
      return invokeHandlerApi('getAppointments', [key, startDate, endDate, resCount], cb);
    };

    this.supportsGetAppointments = function () {
      if (!$rootScope.localUser || !$rootScope.localUser.msExchangeEnabled) {
        // If Exchange Integration is not enabled it doesn't support GetAppointments
        return false;
      }
      if (!_self.isExtensionRunning()) {
        // Assume the feature will be supported once the user installs the Chrome extension
        return !!($rootScope.browser && $rootScope.browser.chrome);
      }
      return true;
    };

    this.supportsExchangeAutodiscover = function () {
      return _self.isExtensionRunning();
    };

    this.supportsExchangeAutodiscoverSvc = function () {
      return _self.isExtensionRunning();
    };

    this.supportsExchangeAutodiscoverGetServer = function () {
      // Requires SP96 extension (1.2.3300) or higher
      return Utils.convertVersionToNumber(_extensionVersion) >= 1023300;
    };

    this.supportsOOO = function () {
      // OOO requires SP91 extension (1.2.2800) or higher
      return Utils.convertVersionToNumber(_extensionVersion) >= 1022800;
    };

    this.supportsUserAvailability = function () {
      // UserAvailability requires SP119 extension (1.2.5800) or higher
      return Utils.convertVersionToNumber(_extensionVersion) >= 1025800;
    };

    this.getOooMsg = function (key, fromEmail, email, cb) {
      if (!_self.supportsOOO()) {
        cb && cb(null, { response: ChromeExtension.ExchangeConnResponse.UNSUPPORTED_METHOD });
      }
      invokeHandlerApi('getOooMsg', [key, fromEmail, email], cb);
    };

    this.getUserAvailability = function (key, email, cb) {
      if (!_self.supportsUserAvailability()) {
        cb && cb(null, { response: ChromeExtension.ExchangeConnResponse.UNSUPPORTED_METHOD });
      }
      invokeHandlerApi('getUserAvailability', [key, email], cb);
    };

    /******************************************************
     * Headset App Manager APIs
     ******************************************************/
    this.launchHeadsetIntegrationApp = function (appId, localizedStrings, cb) {
      return invokeHandlerApi('launchHeadsetIntegrationApp', [appId, localizedStrings], cb);
    };

    this.getHeadsetIntegrationAppStatus = function (appId, cb) {
      return invokeHandlerApi('getHeadsetIntegrationAppStatus', [appId], cb);
    };

    this.restartHeadsetApp = function (appId, cb) {
      return invokeHandlerApi('restartHeadsetApp', [appId], cb);
    };

    /******************************************************
     * Privacy Settings APIs
     ******************************************************/
    this.supportsPolicySettings = function () {
      // Requires SP96 extension (1.2.3300) or higher
      return Utils.convertVersionToNumber(_extensionVersion) >= 1023300;
    };

    this.setIPHandlingPolicy = function (policy, localizedStrings, cb) {
      invokeHandlerApi('setIPHandlingPolicy', [policy, localizedStrings], cb);
    };

    this.getIPHandlingPolicy = function (cb) {
      if (typeof cb === 'function') {
        invokeHandlerApi('getIPHandlingPolicy', [], cb);
      }
    };

    /******************************************************
     * Internal APIs
     ******************************************************/
    this.bringToFront = function (cb) {
      invokeHandlerApi('bringToFront', [], cb);
    };

    this.getFileFromExtension = function (file, lang, cb) {
      return invokeHandlerApi('getFile', [file, lang], cb);
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initializations
    ///////////////////////////////////////////////////////////////////////////////////////
    init();

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.ExtensionSvcImpl = ExtensionSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global process, require*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-params, max-lines-per-function
  function LocationSvcImpl($rootScope, $window, $http, $timeout, LogSvc, LocalizeSvc, LocalStoreSvc) {
    // NOSONAR
    LogSvc.debug('New Service: LocationSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var ACCURACY_THRESHOLD = 1000; // 1 km
    var SIGNIFICANCE_FACTOR_RAD = 0.05; // Around 5km in radius
    var DEFAULT_MAX_AGE = Utils.isMobile() ? 3600000 : 7200000; // 1 hour for mobile and 2 hours web/DA

    var ERROR = {
      GEOLOCATION_NOT_ACCURATE: 'GEOLOCATION_NOT_ACCURATE',
      GEOLOCATION_NOT_SUPPORTED: 'GEOLOCATION_NOT_SUPPORTED',
      GEOLOCATION_NO_DATA_FOUND: 'GEOLOCATION_NO_DATA_FOUND',
      POSITION_NOT_AVAILABLE: 'POSITION_NOT_AVAILABLE',
      GEOLOCATION_NO_INFO_FOUND_FOR_GIVEN_COORDS: 'GEOLOCATION_NO_INFO_FOUND_FOR_GIVEN_COORDS',
      TIMEOUT: 'TIMEOUT'
    };

    var _isLocationBlocked = false;

    var _lastPosition = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.LAST_POSITION) || null;
    var _savedLocation = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.LAST_SAVED_LOCATION) || {};

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////

    // Returns the text representation of the location based on given coordinates
    function getLocationTextForCoordinates(longitude, latitude, cb) {
      getLocationData(
        longitude,
        latitude,
        function (locationData) {
          // The rules to calculate the location are as follows (see BO7754)
          //
          // - Use locality to capture city name (1st position)
          //   - If not available, use administrative_area_level_3
          //   - If not available, use administrative_area_level_2
          // - Use administrative_area_level_1 for 2nd position (if it’s not the same with 1st position)
          //   - If not available, use postal_town (if it’s not the same with 1st position)
          // - Use country as 3rd position.
          var positions = [];
          var firstPos = locationData.locality || locationData.administrative_area_level_3 || locationData.administrative_area_level_2;
          var secondPos = locationData.administrative_area_level_1 || locationData.postal_town;
          var thirdPos = locationData.country;

          if (firstPos) {
            positions.push(firstPos);
          }
          if (secondPos && secondPos !== firstPos) {
            positions.push(secondPos);
          }
          if (thirdPos) {
            positions.push(thirdPos);
          }

          var locationText = positions.join(', ');
          // Do not ever log the location - security and privacy
          LogSvc.debug('[LocationSvc]: Setting location');

          cb && cb(null, locationText);
        },
        function (err) {
          LogSvc.debug('[LocationSvc]: An error occurred while trying to retrieve location data', err);
          cb && cb(err, '');
        }
      );
    }

    function getPosition(successCb, errorCb, options) {
      try {
        // navigator.geolocation is implemented by the mobile client
        if (!$window.navigator.geolocation) {
          LogSvc.error('[LocationSvc]: Geolocation API not supported');
          errorCb && errorCb(ERROR.GEOLOCATION_NOT_SUPPORTED);
          return;
        }
        LogSvc.debug('[LocationSvc]: Starting request to get current position');

        var safetyTimeout = $timeout(function () {
          safetyTimeout = null;
          LogSvc.error('[LocationSvc]: Timed out waiting for Geolocation API');
          errorCb && errorCb(ERROR.TIMEOUT);
        }, options.timeout + 500);

        $window.navigator.geolocation.getCurrentPosition(
          function (pos) {
            if (safetyTimeout) {
              $timeout.cancel(safetyTimeout);
              prepareLocationAndReturn(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy, successCb, errorCb);
            }
          },
          function (err) {
            if (!safetyTimeout) {
              return;
            }
            $timeout.cancel(safetyTimeout);
            $rootScope.$apply(function () {
              if (err.code === err.PERMISSION_DENIED) {
                LogSvc.warn('[LocationSvc]: Permission denied to get geolocation data. ', err);
                _isLocationBlocked = true;
              }
              LogSvc.warn('[LocationSvc]: Failed to get geolocation data. ', err);
              errorCb && errorCb(ERROR.GEOLOCATION_NO_DATA_FOUND);
            });
          },
          options
        );
      } catch (e) {
        LogSvc.error('[LocationSvc]: Failed to get geolocation data. ', e);
        errorCb && errorCb(ERROR.GEOLOCATION_NO_DATA_FOUND);
      }
    }

    // Get URL for the geolocation
    function getGeolocationUrl(longitude, latitude) {
      LogSvc.debug('[LocationSvc]: getLocationData using the geolocation mechanism that is based on geonames data.');
      return '/geocode?latlng=' + latitude + ',' + longitude + '&language=' + LocalizeSvc.getLanguageOnly();
    }

    function getLocationData(longitude, latitude, successCb, errorCb) {
      if (!successCb) {
        // There is no point in getting the location data without a success callback
        LogSvc.error('[LocationSvc]: Could not getLocationData: Success callback is missing.');
        return;
      }
      if (longitude && latitude) {
        var url = getGeolocationUrl(longitude, latitude);
        $http
          .get(url)
          .then(function (response) {
            var data = response.data;
            if (data && data.results && data.results[0]) {
              var result = data.results[0];
              var locationData = {};
              result.address_components.forEach(function (component) {
                component.types &&
                  component.types.forEach(function (type) {
                    locationData[type] = component.long_name;
                  });
              });
              // Do not ever log the location - security and privacy
              LogSvc.debug('[LocationSvc]: Obtained location data');
              successCb(locationData);
            } else {
              LogSvc.error('[LocationSvc]: No results');
              errorCb && errorCb(ERROR.GEOLOCATION_NO_INFO_FOUND_FOR_GIVEN_COORDS);
            }
          })
          .catch(function (response) {
            LogSvc.warn('[LocationSvc]: geocode request error: ', response.status);
            errorCb && errorCb(ERROR.GEOLOCATION_NO_INFO_FOUND_FOR_GIVEN_COORDS);
          });
      } else {
        LogSvc.warn('[LocationSvc]: No longitude or latitude data');
        errorCb && errorCb(ERROR.GEOLOCATION_NO_INFO_FOUND_FOR_GIVEN_COORDS);
      }
    }

    function significantlyChanged(latitude, longitude, prevLatitude, prevLongitude) {
      if (latitude && longitude) {
        if (!prevLatitude || !prevLongitude) {
          return true; // No previous values, position changed
        }
        var diffLat = Math.abs(latitude - prevLatitude);
        var diffLng = Math.abs(longitude - prevLongitude);
        return diffLng >= SIGNIFICANCE_FACTOR_RAD || diffLat >= SIGNIFICANCE_FACTOR_RAD;
      }
      return false;
    }

    function getLocationText(longitude, latitude, cb) {
      if (_savedLocation.locationText) {
        var hasMovedSignificantly = significantlyChanged(longitude, latitude, _savedLocation.longitude, _savedLocation.latitude);
        // Don't call getLocationTextForCoordinates if position has not changed more than 10km.
        if (!hasMovedSignificantly) {
          // No changes and already have a location. Keep using cached location.
          cb(_savedLocation.locationText);
          return;
        }
      }

      getLocationTextForCoordinates(longitude, latitude, function (err, locationText) {
        if (err || !locationText) {
          LogSvc.warn('[LocationSvc]: Failed to get location text: ', err);
          cb(''); // Reset location to force an update later
        } else {
          // Update the new location data in local store
          _savedLocation = {
            longitude: longitude,
            latitude: latitude,
            locationText: locationText
          };
          LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.LAST_SAVED_LOCATION, _savedLocation);
          cb(locationText);
        }
      });
    }

    function prepareLocationAndReturn(latitude, longitude, accuracy, successCb, errorCb) {
      $rootScope.$apply(function () {
        _isLocationBlocked = false;

        if (!accuracy || !latitude || !longitude) {
          LogSvc.warn('[LocationSvc]: Geolocation inconsistent data');
          errorCb && errorCb(ERROR.GEOLOCATION_NO_DATA_FOUND);
          return;
        }
        if (accuracy > ACCURACY_THRESHOLD) {
          LogSvc.warn('[LocationSvc]: Geolocation data not accurate enough, Current accuracy: ' + accuracy + ', ACCURACY_THRESHOLD: ' + ACCURACY_THRESHOLD);
          errorCb && errorCb(ERROR.GEOLOCATION_NOT_ACCURATE);
          return;
        }
        latitude = Math.round(latitude * 100) / 100; // Round to only 2 last decimal digits
        longitude = Math.round(longitude * 100) / 100;

        // Do not ever log the location - security and privacy
        LogSvc.info('[LocationSvc]: Retrieved geolocation data -> accuracy:' + accuracy);

        // Save retrieved position
        _lastPosition = {
          latitude: latitude,
          longitude: longitude,
          accuracy: accuracy,
          timestamp: Date.now()
        };
        LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.LAST_POSITION, _lastPosition);

        successCb && successCb(latitude, longitude, accuracy);
      });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    /**
     * Get position google object {longitude,latitude,accuracy}
     *
     * @param {object} options {enableHighAccuracy,timeout,maximumAge}
     * @param {Function} errorCb The callback function when an error occurs.
     * @param {Function} successCb The callback function when position object is returned from server.
     */
    this.getPosition = function (successCb, errorCb, options) {
      options = options || {};
      options.enableHighAccuracy = options.enableHighAccuracy || true;
      options.timeout = options.timeout || 10000; // 10 sec
      options.maximumAge = options.maximumAge || DEFAULT_MAX_AGE;

      if (_lastPosition && Date.now() - _lastPosition.timestamp <= options.maximumAge) {
        // Return saved location
        LogSvc.debug('[LocationSvc]: Use last position retrieved on ' + new Date(_lastPosition.timestamp));
        successCb && successCb(_lastPosition.latitude, _lastPosition.longitude, _lastPosition.accuracy);
      } else {
        getPosition(successCb, errorCb, options);
      }
    };

    /**
     * Get locationData object
     *
     * @param {object} presenceObj {enableHighAccuracy,timeout,maximumAge}
     * @param {Function} errorCb The callback function when an error occurs.
     * @param {Function} successCb The callback function when position object is returned from server.
     */
    this.reverseGeocode = function (presenceObj, successCb, errorCb) {
      getLocationData(presenceObj.longitude, presenceObj.latitude, successCb, errorCb);
    };

    /**
     * Get Timezone Offset
     */
    this.getTimeZoneOffset = function () {
      return new Date().getTimezoneOffset();
    };

    /**
     * Retrieves location text (reverse geocoding) based on coordinates if significant position change based on localStore saved value
     *
     * @param {String} longitude
     * @param {String} latitude
     * @param cb callback
     */
    this.getLocationText = function (longitude, latitude, cb) {
      getLocationText(longitude, latitude, cb);
    };

    this.isLocationBlocked = function () {
      return !!_isLocationBlocked;
    };

    /**
     * Checks whether the new coordinates have changed significantly compared with the old coordinates.
     *
     * @param {Number} latitude
     * @param {Number} longitude
     * @param {Number} prevLatitude
     * @param {Number} prevLongitude
     * @return true if the coordinates have changed; false otherwise.
     */
    this.significantlyChanged = significantlyChanged;

    /**
     * Exposes the error constants to be used outside this service
     */
    this.ERROR = ERROR;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.LocationSvcImpl = LocationSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/* global UIEventType */

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var Constants = unifyPhone.Constants;
  var IdleState = unifyPhone.Enums.IdleState;
  var Utils = unifyPhone.Utils;

  ///////////////////////////////////////////////////////////////////////////////////////
  // PanelType enum
  ///////////////////////////////////////////////////////////////////////////////////////
  var PanelType = Object.freeze({
    PHONE: 'PHONE'
  });

  // eslint-disable-next-line max-params, max-lines-per-function
  function UISvcImpl($rootScope, $document, $timeout, $animate, $q, LogSvc, PubSubSvc, LocalStoreSvc) {
    // NOSONAR
    LogSvc.debug('New Service: UISvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var IDLE_TIMEOUT = 2400000; // 40 minutes (BO7754)
    var IDLE_INTERVAL_CHECK = 30000; // 30 seconds

    var _self = this;
    var _isIdle = false;
    var _mouseMoved = false;
    var _lastActivityDetected = Date.now();
    var _checkActivityInterval = null;

    // ClientApiHandler is not created for Popout Call Stage app
    var _clientApiHandler = ClientApiHandler ? ClientApiHandler.getInstance() : null;

    // Is input field overlaid by popup window.
    var _overlayPopup = false;
    var _uiSettings = null;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function executeAsync(cb) {
      // We need to execute this regardless if app has focus or not.
      // window.requestAnimationFrame is blocked until app regains focus
      // so we will use $timeout for app-not-in-focus cases
      if (window.document.hasFocus()) {
        window.requestAnimationFrame(cb);
      } else {
        $timeout(cb, 0, false);
      }
    }

    function wrapApply(originalCallback) {
      if (typeof originalCallback !== 'function') {
        return null;
      }
      return function wrappedCallback() {
        var args = arguments;
        var self = this;
        var retval;
        $timeout(function () {
          retval = originalCallback.apply(self, args);
        }, 0);
        return retval;
      };
    }

    function scrollTo(obj) {
      if (!obj) {
        return;
      }
      executeAsync(function () {
        if (obj.itemId || obj.creationTime) {
          $rootScope.$broadcast(UIEventType.SCROLL_TO_ITEM, obj);
        } else if (obj.element) {
          $rootScope.$broadcast(UIEventType.SCROLL_TO_ELEMENT, obj);
        } else {
          $rootScope.$broadcast(UIEventType.SCROLL_TO_OFFSET, obj);
        }
      });
    }

    function checkThreadVisibility(obj) {
      if (!obj) {
        return;
      }
      executeAsync(function () {
        if (obj.threadId) {
          $rootScope.$broadcast(UIEventType.CHECK_THREAD_VISIBILITY, obj);
        }
      });
    }

    function isItemVisible(containerName, itemId, cb) {
      if (!containerName || !itemId || typeof cb !== 'function') {
        return;
      }

      executeAsync(function () {
        $rootScope.$broadcast(UIEventType.CHECK_ITEM_VISIBILITY, {
          containerName: containerName,
          itemId: itemId,
          callback: wrapApply(cb)
        });
      });
    }

    function setFloaterVisible(containerName, visible, cb) {
      if (!containerName) {
        return;
      }

      executeAsync(function () {
        $rootScope.$broadcast(UIEventType.SHOW_FLOATER, {
          containerName: containerName,
          isVisible: !!visible,
          callback: wrapApply(cb)
        });
      });
    }

    function scrollDirectionChanged(obj) {
      if (!obj && !obj.direction) {
        return;
      }
      executeAsync(function () {
        $rootScope.$broadcast(UIEventType.SCROLL_DIRECTION_CHANGED, obj);
      });
    }

    function resetInfiniteScroll(obj) {
      if (!obj || !obj.containerName) {
        return;
      }
      executeAsync(function () {
        $rootScope.$broadcast(UIEventType.RESET_INFINITE_SCROLL, obj);
      });
    }

    function stopInterval() {
      if (_checkActivityInterval) {
        window.clearInterval(_checkActivityInterval);
        _checkActivityInterval = null;
      }
    }

    function startInterval() {
      if (_checkActivityInterval) {
        return;
      }
      _checkActivityInterval = window.setInterval(function () {
        if (_mouseMoved) {
          _mouseMoved = false;
          _lastActivityDetected = Date.now();
          return;
        }

        if (!_isIdle && Date.now() - _lastActivityDetected > IDLE_TIMEOUT) {
          $rootScope.$apply(function () {
            _isIdle = true;
            stopInterval();
            LogSvc.debug('[UISvc]: Publish /internal/svc/idle/state event: ', IdleState.Idle);
            PubSubSvc.publish('/internal/svc/idle/state', [IdleState.Idle]);
          });
        }
      }, IDLE_INTERVAL_CHECK);
    }

    // Detecting idle functionality. Publish /idle/state events on idle state change.
    function activityDetected() {
      _mouseMoved = true;
      if (_isIdle) {
        $rootScope.$apply(function () {
          _isIdle = false;
          startInterval();
          LogSvc.debug('[UISvc]: Publish /internal/svc/idle/state event: ', IdleState.Active);
          PubSubSvc.publish('/internal/svc/idle/state', [IdleState.Active]);
        });
      }
    }

    // Initialize idle detection.
    function startActivityChecking() {
      startInterval();
    }

    function adjustAddedCommentsScroll(obj) {
      if (!obj && !obj.containerName) {
        return;
      }
      executeAsync(function () {
        if (obj.itemIds && obj.itemIds.length > 0) {
          $rootScope.$broadcast(UIEventType.ADJUST_ITEMS_SCROLL, obj);
        }
      });
    }

    function adjustAddedThreadsScroll(obj) {
      if (!obj && !obj.containerName) {
        return;
      }
      obj.threadsOnly = true;
      executeAsync(function () {
        if (obj.itemIds && obj.itemIds.length > 0) {
          $rootScope.$broadcast(UIEventType.ADJUST_ITEMS_SCROLL, obj);
        }
      });
    }

    function adjustRemovedThreadScroll(obj) {
      if (!obj) {
        return;
      }
      executeAsync(function () {
        if (obj.threadId) {
          $rootScope.$broadcast(UIEventType.ADJUST_REMOVED_THREAD_SCROLL, obj);
        }
      });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////

    // Used to keep information about layout overlay.
    function onPopupOpened() {
      _overlayPopup = true;
    }

    function onPopupClosed() {
      _overlayPopup = false;
    }

    PubSubSvc.subscribe('/popup/opened', onPopupOpened);
    PubSubSvc.subscribe('/popup/closed', onPopupClosed);

    ///////////////////////////////////////////////////////////////////////////////////////////////
    // Event listeners
    ///////////////////////////////////////////////////////////////////////////////////////////////
    $document.on('visibilitychange', function () {
      if (_clientApiHandler && $document[0].visibilityState === 'visible') {
        // Ping the access server to ensure that we are still connected
        LogSvc.debug('[UISvc]: Client became visible. Ping the server to check the connection state.');
        _clientApiHandler.pingServer();
      }

      // Workaround for Angular animate issue which causes classes to stall in the intermediate
      // animation states when window is minimized.
      $animate.enabled(!this.hidden);
    });

    $document.on('mousemove', activityDetected);

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    /*
            Focus an element containing the uSetFocus directive of the given name.
        **/
    this.setFocus = function (name) {
      executeAsync(function () {
        $rootScope.$broadcast(UIEventType.FOCUS, name);
      });
    };

    /*
         Focus an element containing the uSetFocusElement directive of the given id.
         **/
    this.setFocusToElement = function (id) {
      executeAsync(function () {
        $rootScope.$broadcast(UIEventType.FOCUS_ELEMENT, id);
      });
    };

    /*
            Scrolls to an offset in the conversation selector container given the container name and
            an offset as percentage or pixels.
            Options:
              offsetTop:        offset from the top
              animateDuration:  duration of animation in ms
            Example: {offsetTop: 0, animateDuration: 200}
        **/
    this.scrollToSelectorOffset = function (options, containerName, cb) {
      scrollTo({
        containerName: containerName || 'selectorDefault',
        options: options,
        doneCallback: wrapApply(cb)
      });
    };

    /*
            Scrolls to an item in the conversation selector container or any other container, given the
            itemId, container name and optional offset as percentage or pixels.
            Options:
              offsetTop:        offset from the top in pixel
              offsetPercentage: offset from the top as percentage
              animateDuration:  duration of animation in ms
            Example: {offsetTop: 10, animateDuration: 200}
        **/
    this.scrollToContainerItem = function (itemId, options, containerName, cb) {
      scrollTo({
        containerName: containerName || 'selectorDefault',
        itemId: itemId,
        options: options,
        doneCallback: wrapApply(cb)
      });
    };

    this.scrollToFeedOffset = function (options, cb) {
      scrollTo({
        containerName: 'feed',
        options: options,
        doneCallback: wrapApply(cb)
      });
    };

    this.scrollToFeedItem = function (itemId, creationTime, options, cb) {
      scrollTo({
        containerName: 'feed',
        itemId: itemId,
        creationTime: creationTime,
        options: options,
        doneCallback: wrapApply(cb)
      });
    };

    this.scrollToFeedElement = function (element, options, cb) {
      scrollTo({
        containerName: 'feed',
        element: element,
        options: options,
        doneCallback: wrapApply(cb)
      });
    };

    this.scrollToTopicListOffset = function (options, cb) {
      scrollTo({
        containerName: 'topics',
        options: options,
        doneCallback: wrapApply(cb)
      });
    };

    this.scrollToFocusedTopicOffset = function (options, cb) {
      scrollTo({
        containerName: 'focusedTopic',
        options: options,
        doneCallback: wrapApply(cb)
      });
    };

    this.scrollToFocusedTopicElement = function (element, options, cb) {
      scrollTo({
        containerName: 'focusedTopic',
        element: element,
        options: options,
        doneCallback: wrapApply(cb)
      });
    };

    this.resetInfiniteScroll = function (containerName, cb) {
      resetInfiniteScroll({
        containerName: containerName,
        doneCallback: wrapApply(cb)
      });
    };

    /*
     * Checks if a given feed item is visible. Callback returns boolean with visibility indication.
     **/
    this.isItemVisible = isItemVisible;

    this.isSelectorItemVisible = function (convId, cb) {
      isItemVisible('selectorDefault', convId, cb);
    };

    this.isFeedItemVisible = function (itemId, cb) {
      isItemVisible('feed', itemId, cb);
    };

    this.isThreadVisible = function (threadId, cb) {
      checkThreadVisibility({
        threadId: threadId,
        minVisiblePercentage: 0,
        callback: wrapApply(cb)
      });
    };

    this.setFloaterVisible = setFloaterVisible;

    this.showFeedFloater = function (cb) {
      setFloaterVisible('feed', true, cb);
    };

    this.hideFeedFloater = function (cb) {
      setFloaterVisible('feed', false, cb);
    };

    this.showSelectorFloater = function (containerName, cb) {
      setFloaterVisible(containerName || 'selectorDefault', true, cb);
    };

    this.hideSelectorFloater = function (containerName, cb) {
      setFloaterVisible(containerName || 'selectorDefault', false, cb);
    };

    this.showTopicListFloater = function (cb) {
      setFloaterVisible('topics', true, cb);
    };

    this.hideTopicListFloater = function (cb) {
      setFloaterVisible('topics', false, cb);
    };

    this.showFocusedTopicFloater = function (cb) {
      setFloaterVisible('focusedTopic', true, cb);
    };

    this.hideFocusedTopicFloater = function (cb) {
      setFloaterVisible('focusedTopic', false, cb);
    };

    this.feedScrollDirectionChanged = function (direction, cb) {
      scrollDirectionChanged({
        containerName: 'feed',
        direction: direction,
        callback: wrapApply(cb)
      });
    };

    this.selectorScrollDirectionChanged = function (direction, containerName, cb) {
      scrollDirectionChanged({
        containerName: containerName || 'selectorDefault',
        direction: direction,
        callback: wrapApply(cb)
      });
    };

    this.topicListScrollDirectionChanged = function (direction, cb) {
      scrollDirectionChanged({
        containerName: 'topics',
        direction: direction,
        callback: wrapApply(cb)
      });
    };

    this.focusedTopicScrollDirectionChanged = function (direction, cb) {
      scrollDirectionChanged({
        containerName: 'focusedTopic',
        direction: direction,
        callback: wrapApply(cb)
      });
    };

    this.clearSearch = function () {
      executeAsync(function () {
        $rootScope.$broadcast(UIEventType.CLEAR_SEARCH);
      });
    };

    this.showOverlay = function (data) {
      executeAsync(function () {
        $rootScope.$emit(UIEventType.SHOW_OVERLAY, data);
      });
    };

    this.hideOverlay = function (data) {
      executeAsync(function () {
        $rootScope.$emit(UIEventType.HIDE_OVERLAY, data);
      });
    };

    this.hasOverlayPopup = function () {
      return _overlayPopup;
    };

    // Locks feed scroll based on a added comments height
    this.adjustAddedCommentsScroll = function (itemIds, cb) {
      itemIds = itemIds instanceof Array ? itemIds : [];
      adjustAddedCommentsScroll({
        containerName: 'feed',
        itemIds: itemIds,
        callback: wrapApply(cb)
      });
    };

    // Locks feed scroll based on added threads height
    this.adjustAddedThreadsScroll = function (threadIds, cb) {
      threadIds = threadIds instanceof Array ? threadIds : [];
      adjustAddedThreadsScroll({
        containerName: 'feed',
        itemIds: threadIds,
        callback: wrapApply(cb)
      });
    };

    // Locks feed scroll based on an thread reordering
    this.adjustRemovedThreadScroll = function (threadId, cb) {
      adjustRemovedThreadScroll({
        threadId: threadId,
        callback: wrapApply(cb)
      });
    };

    // Checks if input fields have data - used for thread-input field communication
    this.areFieldsEmpty = function (ids, cb) {
      if (typeof cb !== 'function') {
        return;
      }
      if (!ids) {
        cb([]);
        return;
      }

      if (!Array.isArray(ids)) {
        ids = [ids];
      }

      var promises = ids.map(function (id) {
        var deferred = $q.defer();
        var callback = function (isEmpty) {
          deferred.resolve({ id: id, isEmpty: isEmpty });
        };
        executeAsync(function () {
          $rootScope.$broadcast(UIEventType.IS_FIELD_EMPTY, {
            id: id,
            callback: callback
          });
        });
        return deferred.promise;
      });

      $q.all(promises).then(function (fieldData) {
        cb(
          fieldData
            .filter(function (field) {
              return !field.isEmpty;
            })
            .map(function (fields) {
              return fields.id;
            })
        );
      });
    };

    // Checks item height within a container
    this.getThreadItemHeight = function (itemId, cb) {
      executeAsync(function () {
        $rootScope.$broadcast(UIEventType.GET_THREAD_ITEM_HEIGHT, {
          itemId: itemId,
          callback: cb
        });
      });
    };

    // Checks thread height within a container
    this.getThreadHeight = function (threadId, cb) {
      executeAsync(function () {
        $rootScope.$broadcast(UIEventType.GET_THREAD_HEIGHT, {
          threadId: threadId,
          callback: cb
        });
      });
    };

    // Focuses in first focusable element inside a container
    this.focusFirst = function (container) {
      if (!container || !container.offsetParent) {
        // Return if not visible (hidden through ng-show)
        return;
      }
      // Find elements with auto-focus attribute first
      var elms = container.querySelectorAll('[auto-focus="true"]');
      if (elms.length === 0) {
        // If none is found, fallback to first focusable element in general
        elms = angular.element(container.querySelectorAll(Utils.TABBABLE_ELEMENTS_PATTERN)).filter(':visible');
      }
      if (elms.length > 0) {
        elms[0].focus();
      }
    };

    /**
     * The following functions manage the state of the various UI elements of conversation sidebar
     * as they need to retain their state across conversations or Spaces.
     */
    this.getUISetting = function (setting) {
      if (!_uiSettings) {
        // First time accessing the UI settings. Need to get value from local storage.
        _uiSettings = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.UI_SETTINGS) || {};
        // Now normalize the settings
        Object.keys(Constants.UISettings).forEach(function (key) {
          if (_uiSettings[key] === undefined) {
            // Add missing keys with default value
            _uiSettings[key] = Constants.DefaultUISettingsValues[key];
          }
        });
        Object.keys(_uiSettings).forEach(function (key) {
          if (!Constants.UISettings[key]) {
            delete _uiSettings[key]; // Remove obsolete/invalid keys
          }
        });
        // Save normalized value back to local storage
        LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.UI_SETTINGS, _uiSettings);
        LogSvc.debug('[ConversationSvc]: Retrieved UI settings from local store. ', _uiSettings);
      }
      return setting ? _uiSettings[setting] : Object.assign({}, _uiSettings);
    };

    this.setUISetting = function (setting, value) {
      if (!Constants.UISettings[setting]) {
        // Invalid setting
        return;
      }
      if (!_uiSettings) {
        _self.getUISetting();
      }
      LogSvc.debug('[ConversationSvc]: Update ' + setting + ' UI settings to ', !!value);
      _uiSettings[setting] = !!value;
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.UI_SETTINGS, _uiSettings);
    };

    this.startActivityChecking = startActivityChecking;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.PanelType = PanelType;
  unifyPhone.UISvcImpl = UISvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  ///////////////////////////////////////////////////////////////////////////////////////
  /// Service to load and edit Pictures for editing.
  ///////////////////////////////////////////////////////////////////////////////////////

  // eslint-disable-next-line max-params, max-lines-per-function
  function PictureEditSvcImpl($window, $q) {
    // NOSONAR
    var $document = $window.document;

    this.getPixelRatio = function (context) {
      // Determine the 'device pixel ratio'
      // This takes care of canvas blurriness on high density displays
      //  see: http://www.html5rocks.com/en/tutorials/canvas/hidpi/
      var backingStore =
        context.backingStorePixelRatio ||
        context.webkitBackingStorePixelRatio ||
        context.mozBackingStorePixelRatio ||
        context.msBackingStorePixelRatio ||
        context.oBackingStorePixelRatio ||
        context.backingStorePixelRatio ||
        1;

      return ($window.devicePixelRatio || 1) / backingStore;
    };

    this.rotateImage = function (image, orientation) {
      // Rotates the image according to the orientation
      // [0, 1, 2, 3] -> [0, 90, 180, 270]deg
      var canvas = $document.createElement('canvas');
      var context = canvas.getContext('2d');
      canvas.width = orientation % 2 ? image.height : image.width;
      canvas.height = orientation % 2 ? image.width : image.height;

      context.translate(canvas.width / 2, canvas.height / 2);
      context.rotate((Math.PI * orientation) / 2);
      context.drawImage(image, -image.width / 2, -image.height / 2, image.width, image.height);
      return canvas;
    };

    this.resizeImage = function (image, newSize) {
      var canvas = $document.createElement('canvas');
      var ctx = canvas.getContext('2d');

      var diff = [0, 0];
      if (image.width > image.height) {
        diff[0] = image.width - image.height;
      } else {
        diff[1] = image.height - image.width;
      }

      canvas.width = newSize;
      canvas.height = newSize;
      // draw canvas image
      ctx.drawImage(image, diff[0] / 2, diff[1] / 2, image.width - diff[0], image.height - diff[1], 0, 0, newSize, newSize);
      // convert canvas to jpeg url
      return canvas.toDataURL('image/jpeg');
    };

    this.loadImageFromURL = function (url) {
      var deferred = $q.defer();
      var image = $document.createElement('img');
      image.onload = function () {
        // Bind load event
        deferred.resolve(this);
      };
      image.setAttribute('src', url); // Set content
      return deferred.promise;
    };

    this.loadUrlFromFile = function (file) {
      var deferred = $q.defer(),
        reader = new FileReader();

      function readerLoaded(e) {
        deferred.resolve(e.target.result);
      }

      if (!/image/i.test(file.type)) {
        deferred.reject('Not an image file');
        return deferred.promise;
      }

      reader.onload = readerLoaded;
      // begin reader read operation
      reader.readAsDataURL(file);
      return deferred.promise;
    };

    this.loadImageFromFile = function (file) {
      return this.loadUrlFromFile(file).then(this.loadImageFromURL);
    };

    function Vector(x, y) {
      this.x = x;
      this.y = y;
    }
    Vector.prototype.add = function (p2, p3, p4) {
      var newVector = new Vector(this.x + p2.x, this.y + p2.y);
      if (p3) {
        newVector.x += p3.x;
        newVector.y += p3.y;
      }
      if (p4) {
        newVector.x += p4.x;
        newVector.y += p4.y;
      }
      return newVector;
    };
    Vector.prototype.subtract = function (p2, p3, p4) {
      var newVector = new Vector(this.x - p2.x, this.y - p2.y);
      if (p3) {
        newVector.x -= p3.x;
        newVector.y -= p3.y;
      }
      if (p4) {
        newVector.x -= p4.x;
        newVector.y -= p4.y;
      }
      return newVector;
    };
    Vector.prototype.scale = function (factor) {
      return new Vector(factor * this.x, factor * this.y);
    };
    Vector.prototype.toString = function () {
      return '(' + this.x + ',' + this.y + ')';
    };

    this.helpers = {
      Vector: Vector,
      vector: function (x, y) {
        return new Vector(x, y);
      }
    };
  }

  // Exports
  unifyPhone.PictureEditSvcImpl = PictureEditSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var ChromeExtension = unifyPhone.ChromeExtension;
  var UserToUserHandler = unifyPhone.UserToUserHandlerSingleton;
  var Utils = unifyPhone.Utils;

  ///////////////////////////////////////////////////////////////////////////////////////
  // Constants
  ///////////////////////////////////////////////////////////////////////////////////////
  var ExchangeUserToUserMessage = Object.freeze({
    EXCHANGE: 'EXCHANGE',
    CONNECT: 'EXCHANGE_CONNECT',
    DISCONNECT: 'EXCHANGE_DISCONNECT',
    SEARCH_CONTACTS: 'EXCHANGE_SEARCH_CONTACTS',
    GET_ALL_PERSONAL_CONTACTS: 'EXCHANGE_GET_ALL_PERSONAL_CONTACTS',
    GET_APPOINTMENTS: 'EXCHANGE_GET_APPOINTMENTS',
    GET_CAPABILITIES: 'EXCHANGE_GET_CAPABILITIES',
    GET_CONNECTION_STATUS: 'EXCHANGE_GET_CONNECTION_STATUS',
    GET_CONTACT: 'EXCHANGE_GET_CONTACT',
    RESOLVE_CONTACT: 'EXCHANGE_RESOLVE_CONTACT',
    STORE_CREDENTIALS: 'EXCHANGE_STORE_CREDENTIALS',
    SYNC_ALL_PERSONAL_CONTACTS: 'EXCHANGE_SYNC_ALL_PERSONAL_CONTACTS'
  });

  var ExchangeUserToUserResponseCode = Object.freeze({
    OK: 'ok',
    FAILED: 'failed'
  });

  // eslint-disable-next-line max-params, max-lines-per-function
  function ExchangeUserToUserSvcImpl($rootScope, $timeout, $window, LogSvc, PubSubSvc) {
    // NOSONAR
    LogSvc.info('New Service: ExchangeUserToUserSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _userToUserHandler = UserToUserHandler.getInstance();
    var _isExchangeConnectorRunning = false;
    var _self = this;
    var _evtCallBacks = {};

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function sendExchangeRequest(request, cb) {
      var req = {
        content: {
          type: ExchangeUserToUserMessage.EXCHANGE,
          requestId: Utils.createTransactionId(),
          request: request
        },
        destUserId: $rootScope.localUser.userId
      };

      _userToUserHandler.sendContactCardRequest(req, function (err, resp) {
        $rootScope.$apply(function () {
          if (err === Constants.ReturnCode.REQUEST_TIMEOUT) {
            err = ChromeExtension.ResponseError.TIMEOUT;
          }
          cb && cb(err, resp);
        });
      });

      return req.content.requestId;
    }

    function cancelReqCallback(reqId) {
      _userToUserHandler.cancelReqCallback(reqId);
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // UserToUser Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    _userToUserHandler.on('CONTACT_CARD.EXCHANGE_SYNC_ALL_PERSONAL_CONTACTS_RESPONSE', function (data) {
      try {
        LogSvc.debug('[ExchangeUserToUserSvc]: Received EXCHANGE_SYNC_ALL_PERSONAL_CONTACTS_RESPONSE');

        var cbList = _evtCallBacks[ChromeExtension.BgTarget.EXCHANGE_CONNECTOR];
        if (!cbList || cbList.length === 0) {
          LogSvc.info('[ExchangeUserToUserSvc]: There is no registered event handler for ', ChromeExtension.BgTarget.EXCHANGE_CONNECTOR);
        } else {
          var evtSyncAll = {
            data: {
              type: ChromeExtension.BgExchangeMsgType.SYNC_ALL_PERSONAL_CONTACTS
            }
          };

          if ((data.added && data.added.length > 0) || (data.changed && data.changed.length > 0) || (data.deleted && data.deleted.length > 0)) {
            evtSyncAll.contacts = {
              added: data.added,
              changed: data.changed,
              deleted: data.deleted
            };

            LogSvc.debug(
              '[ExchangeUserToUserSvc]: Contacts added: ' +
                ((data.added && data.added.length) || 0) +
                ', Contacts changed: ' +
                ((data.changed && data.changed.length) || 0) +
                ', Contacts deleted: ' +
                ((data.deleted && data.deleted.length) || 0)
            );
          }

          var evtContactFolders = {
            data: {
              type: ChromeExtension.BgExchangeMsgType.CONTACT_FOLDERS_SYNC_STATE,
              syncState: data.syncState
            }
          };

          LogSvc.debug('[ExchangeUserToUserSvc]: Fire event: ', evtSyncAll.data.type);

          cbList.forEach(function (cbInfo) {
            cbInfo.cb(evtSyncAll);
            cbInfo.cb(evtContactFolders);
          });
        }
      } catch (e) {
        LogSvc.error('[ExchangeUserToUserSvc]: Exception handling EXCHANGE_SYNC_ALL_PERSONAL_CONTACTS_RESPONSE event. ', e);
      }
    });

    _userToUserHandler.on('CONTACT_CARD.EXCHANGE_GET_ALL_PERSONAL_CONTACTS_RESPONSE', function (data /*, routing*/) {
      try {
        LogSvc.debug('[ExchangeUserToUserSvc]: Received GET_ALL_PERSONAL_CONTACTS, requestId: ' + data.requestId);

        var evt;
        var cbList = _evtCallBacks[ChromeExtension.BgTarget.EXCHANGE_CONNECTOR];
        if (!cbList || cbList.length === 0) {
          LogSvc.info('[ExchangeUserToUserSvc]: There is no registered event handler for ', ChromeExtension.BgTarget.EXCHANGE_CONNECTOR);
        } else {
          evt = {
            data: {
              type: ChromeExtension.BgExchangeMsgType.GET_ALL_PERSONAL_CONTACTS,
              contacts: data.contacts
            }
          };

          LogSvc.debug('[ExchangeUserToUserSvc]: Fire event' + evt.data.type + ' with contacts.count: ' + evt.data.contacts.length);
          cbList.forEach(function (cbInfo) {
            cbInfo.cb(evt);
          });
        }

        if (data.finished) {
          evt = {
            data: {
              type: ChromeExtension.BgExchangeMsgType.GET_ALL_PERSONAL_CONTACTS
            }
          };

          LogSvc.debug('[ExchangeUserToUserSvc]: Fire event' + evt.data.type + ' with empty contacts. GetAllPersonalContacts has finished.');
          cbList.forEach(function (cbInfo) {
            cbInfo.cb(evt);
          });
        }
      } catch (e) {
        LogSvc.error('[ExchangeUserToUserSvc]: Exception handling GET_ALL_PERSONAL_CONTACTS event. ', e);
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.isExchangeConnectorRunning = function () {
      return _isExchangeConnectorRunning;
    };

    this.isExtensionRunning = function () {
      return true;
    };

    /******************************************************
     * Exchange Connector APIs
     ******************************************************/
    this.exchangeConnect = function (settings, cb) {
      LogSvc.debug('[ExchangeUserToUserSvc] Connect to Exchange via Contact Card...');
      var request = {
        type: ExchangeUserToUserMessage.CONNECT,
        requestId: Utils.createTransactionId()
      };
      return sendExchangeRequest(request, function (err, data) {
        if (err) {
          _isExchangeConnectorRunning = false;
        } else if (data && data.response === ExchangeUserToUserResponseCode.OK && data.email) {
          settings.exchInfo.email = data.email;
        }
        cb && cb(err, data);
      });
    };

    this.exchangeDisconnect = function (cb) {
      LogSvc.debug('[ExchangeUserToUserSvc] Disconnect from Exchange...');
      var request = {
        type: ExchangeUserToUserMessage.DISCONNECT
      };
      return sendExchangeRequest(request, cb);
    };

    this.exchangeGetConnectionStatus = function (key, cb) {
      LogSvc.debug('[ExchangeUserToUserSvc] Get Exchange connection status...');
      var request = {
        type: ExchangeUserToUserMessage.GET_CONNECTION_STATUS,
        requestId: Utils.createTransactionId(),
        data: {
          key: key
        }
      };
      return sendExchangeRequest(request, cb);
    };

    this.exchangeSearchContacts = function (key, searchStr, resCount, cb) {
      LogSvc.debug('[ExchangeUserToUserSvc] Search Exchange contact...');
      var request = {
        type: ExchangeUserToUserMessage.SEARCH_CONTACTS,
        data: {
          searchStr: searchStr,
          resCount: resCount
        }
      };
      return sendExchangeRequest(request, cb);
    };

    this.exchangeGetCapabilities = function (cb) {
      LogSvc.debug('[ExchangeUserToUserSvc] Get Exchange capabilities...');
      var request = {
        type: ExchangeUserToUserMessage.GET_CAPABILITIES,
        requestId: Utils.createTransactionId()
      };
      return sendExchangeRequest(request, function (err, data) {
        var capabilities = data && data.capabilities;
        _isExchangeConnectorRunning = !!capabilities;
        $rootScope.isExchangeConnectorRunning = _isExchangeConnectorRunning;

        cb && cb(err, capabilities);
      });
    };

    this.exchangeResolveContact = function (key, contactData, cb) {
      LogSvc.debug('[ExchangeUserToUserSvc] Resolve Exchange contact...');
      var request = {
        type: ExchangeUserToUserMessage.RESOLVE_CONTACT,
        requestId: Utils.createTransactionId(),
        data: {
          key: key,
          resolveData: contactData
        }
      };
      return sendExchangeRequest(request, cb);
    };

    this.exchangeGetContact = function (key, exchangeEmail, cb) {
      LogSvc.debug('[ExchangeUserToUserSvc] Get Exchange contact...');
      var request = {
        type: ExchangeUserToUserMessage.GET_CONTACT,
        data: {
          exchangeEmail: exchangeEmail
        }
      };
      return sendExchangeRequest(request, cb);
    };

    this.getAppointments = function (key, startDate, endDate, resCount, cb) {
      LogSvc.debug('[ExchangeUserToUserSvc] Get Exchange appointments...');
      var request = {
        type: ExchangeUserToUserMessage.GET_APPOINTMENTS,
        data: {
          startDate: new Date(startDate).toUTCString(),
          endDate: endDate === 0 ? endDate : new Date(endDate).toUTCString(),
          resCount: resCount,
          origin: $window.location.hostname
        }
      };
      return sendExchangeRequest(request, function (error, data) {
        // The DateTime object needs to be adapted from .Net Framework String to Javascript DateTime object
        if (data && data.calendarItems) {
          data.calendarItems.forEach(function (calendarItem) {
            calendarItem.startTime = calendarItem.startTime && new Date(calendarItem.startTime).getTime();
            calendarItem.endTime = calendarItem.endTime && new Date(calendarItem.endTime).getTime();
            calendarItem.reminderDueBy = calendarItem.reminderDueBy && new Date(calendarItem.reminderDueBy).getTime();
          });
        }
        cb && cb(error, data);
      });
    };

    this.getAllPersonalContacts = function (key, cb) {
      LogSvc.debug('[ExchangeUserToUserSvc] Search Exchange contact...');
      var request = {
        type: ExchangeUserToUserMessage.GET_ALL_PERSONAL_CONTACTS,
        data: {
          key: key
        }
      };
      return sendExchangeRequest(request, cb);
    };

    this.syncAllPersonalContacts = function (key, syncState, cb) {
      LogSvc.debug('[ExchangeUserToUserSvc] Sync All Personal contacts...');
      var request = {
        type: ExchangeUserToUserMessage.SYNC_ALL_PERSONAL_CONTACTS,
        data: {
          syncState: syncState
        }
      };
      return sendExchangeRequest(request, cb);
    };

    this.storeExchCredentials = function (credentials, cb) {
      // This API is not supported for Exchange integration via Contact Card
      LogSvc.info('[ExchangeUserToUserSvc] Credentials are not stored');
      cb && $timeout(cb);
    };

    this.getStoredCredentials = function (key, cb) {
      // This API is not supported for Exchange integration via Contact Card
      LogSvc.info('[ExchangeUserToUserSvc] Credentials are not stored');
      cb && $timeout(cb);
    };

    this.exchangeCancelReqCallback = function (reqId) {
      cancelReqCallback(reqId);
    };

    this.supportsGetAppointments = function () {
      return true;
    };

    this.supportsExchangeAutodiscover = function () {
      return false;
    };

    this.supportsExchangeAutodiscoverSvc = function () {
      return false;
    };

    this.supportsOOO = function () {
      return false;
    };

    this.supportsUserAvailability = function () {
      return false;
    };

    this.supportsPolicySettings = function () {
      return false;
    };

    this.addEventListener = function (msgType, cb) {
      if (msgType && typeof cb === 'function') {
        _evtCallBacks[msgType] = _evtCallBacks[msgType] || [];
        // Make sure this is not a duplicate registration
        var isDuplicate = _evtCallBacks[msgType].some(function (cbInfo) {
          return cbInfo.cb === cb;
        });
        if (!isDuplicate) {
          _evtCallBacks[msgType].push({ cb: cb });
        }
      }
    };

    this.removeEventListener = function (msgType, cb) {
      if (!msgType && !cb) {
        // Unregister all event handlers
        _evtCallBacks = {};
      } else if (!cb) {
        // Unregister the event handlers for the given message type
        delete _evtCallBacks[msgType];
      } else if (msgType && typeof cb === 'function') {
        // Unregister the given event handler
        var cbList = _evtCallBacks[msgType];
        cbList &&
          cbList.some(function (cbInfo, idx) {
            if (cbInfo.cb === cb) {
              cbList.splice(idx, 1);
              return true;
            }
            return false;
          });
      }
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc listeners
    ///////////////////////////////////////////////////////////////////////////////////////
    function onLocalUserInit() {
      LogSvc.info('[ExchangeUserToUserSvc] Received /localUser/init event');
      _self.exchangeGetCapabilities();
    }
    PubSubSvc.subscribeOnce('/localUser/init', onLocalUserInit);

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initializations
    ///////////////////////////////////////////////////////////////////////////////////////
    $rootScope.isExchangeConnectorRunning = this.isExchangeConnectorRunning();

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.ExchangeUserToUserSvcImpl = ExchangeUserToUserSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global require*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var BusyHandlingOptions = unifyPhone.BusyHandlingOptions;
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var CallForwardingTypes = unifyPhone.Enums.CallForwardingTypes;
  var Constants = unifyPhone.Constants;
  var Conversation = unifyPhone.Conversation;
  var IdleState = unifyPhone.Enums.IdleState;
  var PhoneNumberFormatter = unifyPhone.PhoneNumberFormatter;
  var RoutingOptions = unifyPhone.RoutingOptions;
  var UserProfile = unifyPhone.UserProfile;
  var Utils = unifyPhone.Utils;

  var ExchangeConnError = Object.freeze({
    COULD_NOT_CONNECT: 'couldNotConnect', // Cannot reach Exchange Server
    UNAUTHORIZED: 'unauthorized', // Could connect to the server, but could not authenticate user
    FAILED: 'failed', // The requested method failed to be completed
    UNAVAILABLE: 'unavailable', // Failed to connect to connector
    INTERNAL_ERROR: 'internalError', // Internal error
    TIMEOUT: 'timeout', // Timed out waiting for a response
    EXCEED_MAX_ATTEMPT: 'exceedMaxAttempt', // Exceed the max attempts to connect
    MAILBOX_NOT_FOUND: 'mailboxNotFound', // Mailbox not found for this user on the configured Exchange server
    NTLM_NOT_SUPPORTED: 'ntlmNotSupported', // NTLM authentication is not supported on the configured Exchange server
    UNEXPECTED: 'unexpected', // Unexpected error
    NO_RESULT: 'NO_RESULT' // Could not retrieve exchange settings
  });

  // eslint-disable-next-line max-params, max-lines-per-function
  function UserProfileSvcImpl($rootScope, $q, $interval, $timeout, LogSvc, PubSubSvc, LocalizeSvc, LocalStoreSvc) {
    // NOSONAR
    LogSvc.debug('New Service: UserProfileSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var DEFAULT_SETTINGS = [
      {
        key: Constants.UserSettingKey.AUDIO_WEB_SOUNDS,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: true
      },
      {
        key: Constants.UserSettingKey.ATC_ROUTING,
        dataType: Constants.UserSettingDataType.STRING,
        stringValue: RoutingOptions.DefaultRouting.name
      },
      {
        key: Constants.UserSettingKey.DESKTOP_CONVERSATION_CHANGE,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: true
      },
      {
        key: Constants.UserSettingKey.DESKTOP_MESSAGE_NOTIFICATIONS_SETTING,
        dataType: Constants.UserSettingDataType.NUMBER,
        numberValue: Constants.MessageNotificationsSetting.ALL
      },
      {
        key: Constants.UserSettingKey.DESKTOP_MISSED_MESSAGE,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: true
      },
      {
        key: Constants.UserSettingKey.DESKTOP_RTC,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: true
      },
      {
        key: Constants.UserSettingKey.DESKTOP_SYSINFO,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: true
      },
      {
        key: Constants.UserSettingKey.EMAIL_INVITES,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: true
      },
      {
        key: Constants.UserSettingKey.EMAIL_MISSED_RTC,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: true
      },
      {
        key: Constants.UserSettingKey.EMAIL_SUMMARY,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: true
      },
      {
        key: Constants.UserSettingKey.ENHANCED_DESKTOP_MESSAGE_NOTIFICATION,
        dataType: Constants.UserSettingDataType.NUMBER,
        numberValue: Constants.EnhancedMessageNotificationsSetting.ALL
      },
      {
        key: Constants.UserSettingKey.ENHANCED_MOBILE_MESSAGE_NOTIFICATION,
        dataType: Constants.UserSettingDataType.NUMBER,
        numberValue: Constants.EnhancedMessageNotificationsSetting.ALL
      },
      {
        key: Constants.UserSettingKey.FIRST_WEB_LOGIN_TIMESTAMP,
        dataType: Constants.UserSettingDataType.TIMESTAMP,
        numberValue: 0
      },
      {
        key: Constants.UserSettingKey.HIDE_PROFILE_EXTERNAL_ENABLED,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: false
      },
      {
        key: Constants.UserSettingKey.KEYBOARD_SHORTCUTS_ENABLED,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: true
      },
      {
        key: Constants.UserSettingKey.MOBILE_MESSAGE_NOTIFICATIONS_SETTING,
        dataType: Constants.UserSettingDataType.NUMBER,
        numberValue: Constants.MessageNotificationsSetting.ALL
      },
      {
        key: Constants.UserSettingKey.OPT_OUT_PRESENCE,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: false
      },
      {
        key: Constants.UserSettingKey.PLAY_PICKUP_SOUND,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: true
      },
      {
        key: Constants.UserSettingKey.PLAY_SOUND_MESSAGE_NOTIFICATIONS,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: false
      },
      {
        key: Constants.UserSettingKey.PLAY_SOUND_RTC,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: true
      },
      {
        key: Constants.UserSettingKey.PLAY_SYSTEM_SOUNDS,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: true
      },
      {
        key: Constants.UserSettingKey.RELEASE_NOTES_WEB_VERSION,
        dataType: Constants.UserSettingDataType.NUMBER,
        numberValue: 0
      },
      {
        key: Constants.UserSettingKey.SECOND_TELEPHONY_CALL_ROUTING,
        dataType: Constants.UserSettingDataType.STRING,
        stringValue: BusyHandlingOptions.DefaultRouting.name
      },
      {
        key: Constants.UserSettingKey.SHARE_LOCATION,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: true
      },
      {
        key: Constants.UserSettingKey.STATUS_MESSAGE_TEXT,
        dataType: Constants.UserSettingDataType.STRING,
        stringValue: ''
      },
      {
        key: Constants.UserSettingKey.VOICEMAIL_ENABLED,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: false
      },
      {
        key: Constants.UserSettingKey.VOICEMAIL_TIMEOUT,
        dataType: Constants.UserSettingDataType.NUMBER,
        numberValue: 20
      },
      {
        key: Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETING_ENABLED,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: false
      },
      {
        key: Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETING_URI,
        dataType: Constants.UserSettingDataType.STRING,
        stringValue: ''
      },
      {
        key: Constants.UserSettingKey.ANONYMOUS_RTC_PARTICIPANT,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: false
      },
      {
        key: Constants.UserSettingKey.VOICEMAIL_NO_RECORDING,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: false
      },
      {
        key: Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETINGS,
        dataType: Constants.UserSettingDataType.STRING,
        stringValue: ''
      },
      {
        key: Constants.UserSettingKey.EMERGENCY_CALL_DISCLAIMER,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: false
      },
      {
        key: Constants.UserSettingKey.MSTEAMS_APPLICATION_ID,
        dataType: Constants.UserSettingDataType.STRING,
        stringValue: ''
      },
      {
        key: Constants.UserSettingKey.MS_TEAMS_CALL_INCOMING_CALL_ROUTING,
        dataType: Constants.UserSettingDataType.BOOLEAN,
        booleanValue: false
      }
    ];

    var CLIENT_PRESENCE_FIELDS = ['state', 'mobile', 'isSystemDefined'];

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _self = this;

    var _clientApiHandler = ClientApiHandler.getInstance();
    var _userSettingsLoadComplete = false;
    //var _isMobileAsleep = false; // true: indicates a mobile client in backgroup mode
    var _userSettings = DEFAULT_SETTINGS;
    var _userIdleState = IdleState.Active;
    var _defaultCallerId = null;
    var _tenantData;
    var _showIntegrations = Utils.isIntegrationsSupported();

    // Local client presence state
    var _clientPresenceState = {
      state: Constants.PresenceState.AVAILABLE,
      mobile: Utils.isMobile()
    };

    // Map to keep a common variable naming to be used, together with default values
    var _tenantDataDefaults = {};
    _tenantDataDefaults[Constants.TenantSettingsType.HELP_URL] = { name: 'helpUrl', defaultValue: Utils.DEFAULT_HELP_URL };
    _tenantDataDefaults[Constants.TenantSettingsType.CREDENTIALS_LOGIN_ENABLED] = { name: 'loginWithCredentialsEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.PLUGIN_SELF_MANAGED_ENABLED] = { name: 'pluginSelfManagedEnabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_MICROSOFT_EXCHANGE_ENABLED] = { name: 'msExchangeEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_MICROSOFT_SHAREPOINT_ENABLED] = { name: 'msSharepointEnabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_BOX_ENABLED] = { name: 'boxEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_GOOGLE_DRIVE_ENABLED] = { name: 'gDriveEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_ONE_DRIVE_ENABLED] = { name: 'oneDriveEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_JABRA_ENABLED] = { name: 'jabraEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_PLANTRONICS_ENABLED] = { name: 'plantronicsEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.READ_ONLY_MODE] = { name: 'readOnlyModeEnabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.DT_LEGAL_INFORMATION] = { name: 'hideLegalInformation', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.DA_AUTO_UPDATE] = { name: 'daAutoUpdate', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_SENNHEISER_ENABLED] = { name: 'sennheiserEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.MUTE_ON_ENTRY] = { name: 'muteOnEntry', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.MUTE_ON_ENTRY_CMR] = { name: 'muteOnEntryCMR', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.MUTE_ON_ENTRY_OUTCALL] = { name: 'muteOnEntryOutcall', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.REMOTE_CONTROL_ENABLED] = { name: 'remoteControlEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.REMOTE_CONTROL_EXTERNAL_ENABLED] = { name: 'remoteControlExternalEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.PLANTRONICS_HUB_DOWNLOAD_URL] = {
      name: 'plantronicsHubDownloadUrl',
      defaultValue: 'https://support.hp.com/us-en/document/ish_9728794-9728758-16'
    };
    _tenantDataDefaults[Constants.TenantSettingsType.EPOS_CONNECT_DOWNLOAD_URL] = {
      name: 'eposConnectDownloadUrl',
      defaultValue: 'https://www.eposaudio.com/en/us/software/epos-connect'
    };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_JPL_ENABLED] = { name: 'jplEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.SCREENSHARE_DISABLED] = { name: 'screenshareDisabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.FILESHARE_DISABLED] = { name: 'fileshareDisabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.TRANSCRIPTION_ENABLED] = { name: 'transcriptionEnabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.USER_CONTROLLED_WEBRTC_POLICY_ENABLED] = {
      name: 'userControlledWebrtcPolicyEnabled',
      defaultValue: false
    };
    _tenantDataDefaults[Constants.TenantSettingsType.DATA_RETENTION_RECORDING_PERIOD] = { name: 'dataRetentionRecordingPeriod', defaultValue: 0 };
    _tenantDataDefaults[Constants.TenantSettingsType.DATA_RETENTION_EXTENDED_RECORDING_PERIOD] = {
      name: 'dataRetentionExtendedRecordingPeriod',
      defaultValue: 0
    };
    _tenantDataDefaults[Constants.TenantSettingsType.GLOBAL_SPACE_CREATION_ENABLED] = { name: 'spaceCreationEnabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.ALLOW_EXTERNAL_SPACES] = { name: 'allowExternalSpacesEnabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.FAQ_URL] = { name: 'faqUrl', defaultValue: Utils.DEFAULT_FAQ_URL };
    _tenantDataDefaults[Constants.TenantSettingsType.ANONYMOUS_PARTICIPANTS_ENABLED] = { name: 'anonymousParticipantsEnabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.EVENT_ORGANIZER_CONFIG] = { name: 'eventOrganizerConfig', defaultValue: null };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_LOGITECH_ENABLED] = { name: 'logitechEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.SPACE_STATISTIC_LINK_ENABLED] = { name: 'spaceStatisticsLinkEnabled', defaultValue: '' };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_GIGASET_ION_ENABLED] = { name: 'gigasetIonEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_PLATHOSYS_ENABLED] = { name: 'plathosysEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.COMFORT_NOISE_DISABLED] = { name: 'comfortNoiseDisabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_YEALINK_ENABLED] = { name: 'yealinkEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_VBET_ENABLED] = { name: 'vbetEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.PRESENCE_HIDDEN] = { name: 'presenceHidden', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.CLOUDLINK_CHAT_ENABLED] = { name: 'cloudlinkChatEnabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.CHAT_ATTACHMENT_UPLOAD_DISABLED] = { name: 'chatAttachmentUploadDisabled', defaultValue: false };

    // Not supported at Sotel
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_EMBRAVA_ENABLED] = { name: 'embravaEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_KUANDO_ENABLED] = { name: 'kuandoEnabled', defaultValue: true };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_PLANTRONICS_STATUS_ENABLED] = { name: 'plantronicsStatusEnabled', defaultValue: true };

    // Integrations
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_C4G_ENABLED] = { name: 'unifyPhoneForGoogleEnabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_C4C_ENABLED] = { name: 'unifyPhoneForGoogleCalendar', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_ZAPIER_ENABLED] = { name: 'zapierEnabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.EXTENSION_MEISTERTASK_ENABLED] = { name: 'meistertaskEnabled', defaultValue: false };
    _tenantDataDefaults[Constants.TenantSettingsType.EXCHANGE_ONLINE_CLIENT_ID] = { name: 'exchangeOnlineClientId', defaultValue: '' };
    _tenantDataDefaults[Constants.TenantSettingsType.EXCHANGE_ONLINE_REDIRECT_URI] = { name: 'exchangeOnlineRedirectUri', defaultValue: '' };
    _tenantDataDefaults[Constants.TenantSettingsType.GOOGLE_CONNECTOR_CLIENT_ID] = { name: 'googleConnectorClientId', defaultValue: '' };
    _tenantDataDefaults[Constants.TenantSettingsType.GOOGLE_CONNECTOR_REDIRECT_URI] = { name: 'googleConnectorRedirectUri', defaultValue: '' };

    // IC Settings
    _tenantDataDefaults[Constants.TenantSettingsType.IC_SUPPORT_PHONE_NUMBER] = { name: 'expertConfigPhone', defaultValue: '' };
    _tenantDataDefaults[Constants.TenantSettingsType.IC_SUPPORT_EMAIL_ADDRESS] = { name: 'expertConfigEmail', defaultValue: '' };
    _tenantDataDefaults[Constants.TenantSettingsType.IC_SUPPORT_OPERATING_HOURS_FROM] = { name: 'expertConfigHourFrom', defaultValue: '' };
    _tenantDataDefaults[Constants.TenantSettingsType.IC_SUPPORT_OPERATING_HOURS_TO] = { name: 'expertConfigHourTo', defaultValue: '' };
    _tenantDataDefaults[Constants.TenantSettingsType.IC_SUPPORT_OPERATING_HOURS_TIMEZONE] = { name: 'expertConfigTimezone', defaultValue: '' };
    _tenantDataDefaults[Constants.TenantSettingsType.IC_USER_MANUAL_URL] = { name: 'expertConfigManualURL', defaultValue: '' };
    _tenantDataDefaults[Constants.TenantSettingsType.IC_SAFETY_NOTICE_URL] = { name: 'expertConfigSafetyURL', defaultValue: '' };
    _tenantDataDefaults[Constants.TenantSettingsType.IC_ADDITIONAL_TERMS_OF_SERVICE_URL] = { name: 'expertConfigAdditionalURL', defaultValue: '' };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function setLocalUserPresence(data, cb) {
      LogSvc.debug('[UserProfileSvc]: setLocalUserPresence was called with state = ' + data.state);
      // First get the user presence state
      var userPresenceState = Utils.shallowCopy($rootScope.localUser.userPresenceState);
      delete _clientPresenceState.state;
      delete _clientPresenceState.isSystemDefined;
      data.hasOwnProperty('state') && (_clientPresenceState.state = data.state);
      data.hasOwnProperty('isSystemDefined') && (_clientPresenceState.isSystemDefined = data.isSystemDefined);

      CLIENT_PRESENCE_FIELDS.forEach(function (field) {
        userPresenceState[field] = _clientPresenceState[field];
      });

      _clientApiHandler.setPresence(userPresenceState, function (err, newState) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.warn('[UserProfileSvc]: Error setting presence: ', err);
            cb && cb(err);
            return;
          }

          UserProfile.updateUserPresenceState($rootScope.localUser, newState);
          cb && cb(null, $rootScope.localUser.userPresenceState);

          LogSvc.debug('[UserProfileSvc]: Raise a /localUser/update event');
          PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
        });
      });
    }

    function setUnifiedPresence(data, cb) {
      if (!$rootScope.localUser || !data.state) {
        LogSvc.warn('[UserProfileSvc]: setUnifiedPresence invoked without localUser or state being set');
        cb && cb();
        return;
      }
      LogSvc.debug('[UserProfileSvc]: setUnifiedPresence invoked with state = ', data.state || '<NONE>');

      setLocalUserPresence(data, cb);
    }

    function setStatusMessage(msg) {
      $rootScope.localUser.userPresenceState.statusMessage = msg;

      LogSvc.debug('[UserProfileSvc]: Publish /user/statusMessage/update');
      PubSubSvc.publish('/user/statusMessage/update');
    }

    function updateSetting(key, value, isEventNotification) {
      if (!key || value === undefined) {
        return null;
      }
      var setting = _userSettings.find(function (set) {
        return set.key === key;
      });
      if (setting) {
        var fieldName;
        switch (setting.dataType) {
          case Constants.UserSettingDataType.BOOLEAN:
            fieldName = 'booleanValue';
            break;
          case Constants.UserSettingDataType.NUMBER:
            fieldName = 'numberValue';
            break;
          case Constants.UserSettingDataType.STRING:
            fieldName = 'stringValue';
            break;
          case Constants.UserSettingDataType.TIMESTAMP:
            fieldName = 'timestampValue';
            break;
          default:
            LogSvc.warn('[UserProfileSvc] Unknown dataType', setting.dataType);
            break;
        }
        if (fieldName && setting[fieldName] !== value) {
          if (setting.key === Constants.UserSettingKey.PLAY_SOUND_MESSAGE_NOTIFICATIONS && _self.isSystemNotificSoundSuported()) {
            return null;
          }
          setting[fieldName] = value;
          LogSvc.debug('[UserProfileSvc] Updated setting: ', setting);

          if ($rootScope.localUser) {
            switch (setting.key) {
              case Constants.UserSettingKey.SHARE_LOCATION:
                if (isEventNotification) {
                  _self.updateLocation();
                }
                break;
              case Constants.UserSettingKey.STATUS_MESSAGE_TEXT:
                setStatusMessage(value || '');
                if (isEventNotification) {
                  LogSvc.debug('[UserProfileSvc]: Updated status message. Publish /localUser/update event');
                  PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
                }
                break;
              case Constants.UserSettingKey.SECOND_TELEPHONY_CALL_ROUTING:
                $rootScope.localUser.selectedBusyHandlingOption = value || BusyHandlingOptions.DefaultRouting.name;
                if (isEventNotification) {
                  LogSvc.debug('[UserProfileSvc]: Updated busy handling option. Publish /localUser/update event');
                  PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
                }
                break;
              case Constants.UserSettingKey.MS_TEAMS_CALL_INCOMING_CALL_ROUTING:
                $rootScope.localUser.isTeamsCallBusyHandlingEnabled = value || false;
                if (isEventNotification) {
                  LogSvc.debug('[UserProfileSvc]: Updated Ms teams call busy handling option. Publish /localUser/update event');
                  PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
                }
                break;
              case Constants.UserSettingKey.ATC_ROUTING:
                $rootScope.localUser.selectedRoutingOption = value || RoutingOptions.DefaultRouting.name;
                if (isEventNotification) {
                  LogSvc.debug('[UserProfileSvc]: Updated routing option. Publish /localUser/update event');
                  PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
                }
                break;
              case Constants.UserSettingKey.KEYBOARD_SHORTCUTS_ENABLED:
                $rootScope.localUser.keyboardShortcutsEnabled = value;
                break;
            }
          }
          return setting;
        }
      }
      return null;
    }

    function updateSettings(settings, isEventNotification) {
      settings &&
        settings.forEach(function (setting) {
          var key = setting.key;
          var value;
          if (setting.dataType) {
            switch (setting.dataType) {
              case Constants.UserSettingDataType.BOOLEAN:
                value = !!setting.booleanValue;
                break;
              case Constants.UserSettingDataType.NUMBER:
                value = setting.numberValue;
                break;
              case Constants.UserSettingDataType.STRING:
                value = setting.stringValue;
                break;
              case Constants.UserSettingDataType.TIMESTAMP:
                value = setting.timestampValue;
                break;
              default:
                LogSvc.warn('[UserProfileSvc] Unknown Data Type. Setting is not updated. Data Type: ', setting.dataType);
                return;
            }
            updateSetting(key, value, isEventNotification);
          }
        });
    }

    function setReroutingPhoneNumber(reroutingPhoneNumber, cb) {
      var account = $rootScope.localUser && $rootScope.localUser.accounts && $rootScope.localUser.accounts[0];
      if (!account) {
        LogSvc.error('[UserProfileSvc]: setReroutingPhoneNumber - There is no local user account.');
        $rootScope.localUser.reroutingPhoneNumber = '';
        cb && cb(Constants.ReturnCode.SERVICE_EXCEPTION);
        return;
      }
      reroutingPhoneNumber = PhoneNumberFormatter.format(reroutingPhoneNumber);

      account.telephonyConfiguration = account.telephonyConfiguration || {};
      var oldNumber = account.telephonyConfiguration.reroutingPhoneNumber;
      account.telephonyConfiguration.reroutingPhoneNumber = reroutingPhoneNumber;
      $rootScope.localUser.alternativeNumber = $rootScope.localUser.reroutingPhoneNumber = reroutingPhoneNumber;

      _clientApiHandler.assignTelephonyConfiguration(account.accountId, account.telephonyConfiguration, function (err) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.error('[UserProfileSvc]: Error updating reroutingPhoneNumber: ', err);
            // Revert changes
            account.telephonyConfiguration.reroutingPhoneNumber = oldNumber;
            $rootScope.localUser.alternativeNumber = $rootScope.localUser.reroutingPhoneNumber = oldNumber;
            cb && cb(err);
          } else {
            if ($rootScope.localUser.selectedRoutingOption === RoutingOptions.AlternativeNumber.name) {
              // In case of ATC, change after re-registration
              if ($rootScope.localUser.isOsBizCTIEnabled && !$rootScope.localUser.reroutingPhoneNumber) {
                $rootScope.localUser.selectedRoutingOption = RoutingOptions.DefaultRouting.name;
                _self.saveSetting(Constants.UserSettingKey.ATC_ROUTING, $rootScope.localUser.selectedRoutingOption);
              }
              LogSvc.debug('[UserProfileSvc]: Publish /routing/alternativeNumber/changed event');
              PubSubSvc.publish('/routing/alternativeNumber/changed');
            }

            // NGTC-5478 - After change in the alternative number we need to trigger a telephonyConfigUpdated
            // event in order to re-register the client in PBX
            LogSvc.debug('[UserProfileSvc]: Publish /localUser/telephonyConfigUpdated event');
            PubSubSvc.publish('/localUser/telephonyConfigUpdated');
            cb && cb(null, $rootScope.localUser);
          }
        });
      });
    }

    function setLanguage(locale, cb) {
      $rootScope.localUser.locale = LocalizeSvc.setLanguage(locale);
      var data = {
        userId: $rootScope.localUser.userId,
        locale: Utils.normalizeLocaleProto(locale)
      };

      _clientApiHandler.updateUser(data, function (err, userData) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.error('[UserProfileSvc]: Error updating user locale: ', err);
            cb && cb(err);
            return;
          }
          cb && cb(null, userData);
        });
      });
    }

    // Set phoneNumber, callerId and cstaNumber for localUser based on assigned phone numbers.
    // Returns true if localUser.callerId has been changed
    function updateTelephonyNumbers() {
      var user = $rootScope.localUser;
      var currentCallerId = user.callerId;

      var account = user.accounts && user.accounts[0];
      var telConfig = account && account.telephonyConfiguration;
      if (telConfig) {
        user.phoneNumber = telConfig.phoneNumber || null;
        if (user.isATC) {
          // User is assigned to an ATC
          user.callerId = telConfig.phoneNumber;

          user.cstaNumber = user.isMiVB ? Utils.cleanMiVBPhoneNumber(telConfig.phoneNumber) : Utils.cleanPhoneNumber(telConfig.phoneNumber);

          user.registerNumber = user.cstaNumber;
          user.isRegisterTC = true;

          // Initialize telephony settings fields
          user.reroutingPhoneNumber = user.reroutingPhoneNumber || '';
          user.selectedBusyHandlingOption = user.selectedBusyHandlingOption || BusyHandlingOptions.DefaultRouting.name;
        } else if (user.isSTC) {
          // User is assigned to a STC
          user.callerId = telConfig.phoneNumber;
          user.registerNumber = Utils.cleanPhoneNumber(telConfig.phoneNumber);
          user.cstaNumber = null;
          user.isRegisterTC = true;
        } else {
          // User is assigned to GTC/ETC
          user.callerId = telConfig.callerId || telConfig.phoneNumber || _defaultCallerId;
          user.cstaNumber = null;
          user.registerNumber = null;
          user.isRegisterTC = false;
        }
      } else {
        user.phoneNumber = null;
        user.callerId = _defaultCallerId;
        user.cstaNumber = null;
        user.registerNumber = null;
        user.isRegisterTC = false;
      }

      if (user.isOsBizCTIEnabled) {
        RoutingOptions.DefaultRouting.desc = 'res_DefaultRoutingDescriptionOSBiz';
        RoutingOptions.DeskPhone.desc = 'res_DeskphoneRoutingDescriptionOSBiz';
        RoutingOptions.AlternativeNumber.desc = 'res_AlternativeNumberRoutingDescriptionOSBiz';
      } else if (user.isMiV5000) {
        RoutingOptions.DefaultRouting.desc = 'res_DefaultRoutingDescriptionMiV5000';
        RoutingOptions.DeskPhone.desc = 'res_DeskphoneRoutingDescriptionMiV5000';
        RoutingOptions.UnifyPhoneClient.desc = 'res_ProductNameClientRoutingDescriptionMiV5000';
      } else {
        RoutingOptions.DefaultRouting.desc = 'res_DefaultRoutingDescription';
        RoutingOptions.DeskPhone.desc = 'res_DeskphoneRoutingDescription';
        RoutingOptions.AlternativeNumber.desc = 'res_AlternativeNumberRoutingDescription';
      }

      return currentCallerId !== user.callerId;
    }

    function updateSecondCallRouting() {
      var setting = _self.getSetting(Constants.UserSettingKey.SECOND_TELEPHONY_CALL_ROUTING);
      $rootScope.localUser.selectedBusyHandlingOption = setting.stringValue;
    }

    function updateMsTeamsIncomingCallRouting() {
      var setting = _self.getSetting(Constants.UserSettingKey.MS_TEAMS_CALL_INCOMING_CALL_ROUTING);
      $rootScope.localUser.isTeamsCallBusyHandlingEnabled = setting.booleanValue;
    }

    function updateATCRouting() {
      var setting = _self.getSetting(Constants.UserSettingKey.ATC_ROUTING);
      $rootScope.localUser.selectedRoutingOption = setting.stringValue;
    }

    // Populates a given object with tenant variables, initialized with default values and then populated from tenant data
    function getTenantVariables(settings) {
      var obj = {};
      Object.keys(_tenantDataDefaults).forEach(function (key) {
        var attrs = _tenantDataDefaults[key];
        obj[attrs.name] = attrs.defaultValue;
      });

      settings.forEach(function (setting) {
        if (!setting.value) {
          return;
        }
        var attrs = _tenantDataDefaults[setting.key];
        var attr = attrs && attrs.name;
        if (attr) {
          if (typeof attrs.defaultValue === 'boolean') {
            if (setting.value === 'true') {
              obj[attr] = true;
            } else if (setting.value === 'false') {
              obj[attr] = false;
            }
          } else if (typeof attrs.defaultValue === 'number') {
            obj[attr] = parseInt(setting.value, 10);
          } else {
            obj[attr] = setting.value;
          }
        }
      });

      // Hardcoded variables for different Systems. Yes, this is as confusing as it gets....
      // To be removed once we can configure which extensions are available per system (ANS-68463)

      // Hide integrations if not on a valid domain
      if (!_showIntegrations) {
        obj.zapierEnabled = false;
        obj.meistertaskEnabled = false;
        obj.unifyPhoneForGoogleEnabled = false;
        obj.unifyPhoneForGoogleCalendar = false;
      }

      return obj;
    }

    function updateTenantSettings(settings) {
      var tenantVariables = getTenantVariables(settings);
      if ($rootScope.localUser) {
        // Update user object
        Object.keys(tenantVariables).forEach(function (attr) {
          var config;
          var value = tenantVariables[attr];
          if (attr === 'daAutoUpdate') {
            // Save tenant auto update setting to have access from login app.
            LocalStoreSvc.setString(LocalStoreSvc.keys.DA_AUTO_UPDATE, value.toString());
          }
          if (attr === 'eventOrganizerConfig') {
            try {
              config = value && JSON.parse(value);
              if (Array.isArray(config)) {
                $rootScope.localUser.eventOrganizerConfig = config;
                $rootScope.localUser.eventOrganizerConfigMap = config.reduce(function (map, obj) {
                  obj.permission = 'EVENT_ORGANIZER_' + obj.type.replace('EVENT_', '');
                  map[obj.type] = obj;
                  return map;
                }, {});
              }
            } catch (e) {
              LogSvc.error('[UserProfileSvc]: Error parsing eventOrganizerConfig. ', e);
            }
          } else {
            $rootScope.localUser[attr] = value;
          }
        });

        $rootScope.localUser.recordingDeletionEnabledTenant =
          tenantVariables.dataRetentionRecordingPeriod > 0 && tenantVariables.dataRetentionExtendedRecordingPeriod > 0;

        if (_userSettingsLoadComplete) {
          LogSvc.debug('[UserProfileSvc]: Publish /localUser/tenantSettingsUpdate event');
          PubSubSvc.publish('/localUser/tenantSettingsUpdate', [$rootScope.localUser]);
        }
      }
      return tenantVariables;
    }

    function setTenantData(tenant) {
      if (!tenant) {
        return null;
      }

      _tenantData = tenant;
      _tenantData.settings = _tenantData.settings || [];
      _tenantData.emergencyNumbers = _tenantData.emergencyNumbers || [];
      $rootScope.localUser.emergencyNumbers = _tenantData.emergencyNumbers || [];
      return updateTenantSettings(_tenantData.settings);
    }

    function getOldSettings(keys) {
      return _userSettings
        .filter(function (setting) {
          return keys.includes(setting.key);
        })
        .map(function (setting) {
          return Utils.shallowCopy(setting);
        });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Client API Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    _clientApiHandler.on('User.USER_SETTING_UPDATED', function (data) {
      LogSvc.debug('[UserProfileSvc]: Received User.USER_SETTING_UPDATED event');
      if (!data.userId || !$rootScope.localUser) {
        LogSvc.error('[UserProfileSvc]: User.USER_SETTING_UPDATED event does not have a user');
        return;
      }

      if (data.userId !== $rootScope.localUser.userId) {
        LogSvc.error('[UserProfileSvc]: User.USER_SETTING_UPDATED event not for local user');
        return;
      }

      if (!data.settings) {
        LogSvc.error('[UserProfileSvc]: User.USER_SETTING_UPDATED event with no settings');
        return;
      }

      $rootScope.$apply(function () {
        updateSettings(data.settings, true);

        LogSvc.debug('[UserProfileSvc]: Publish /user/settings/update event');
        PubSubSvc.publish('/user/settings/update', [_userSettings]);
      });
    });

    _clientApiHandler.on('User.USER_PRESENCE_CHANGE', function (data) {
      if (!$rootScope.localUser || data.userId !== $rootScope.localUser.userId) {
        // Event is not for local user. This is handled by UserSvc,
        return;
      }

      LogSvc.debug('[UserProfileSvc]: Received User.USER_PRESENCE_CHANGE event');

      var newState = data.newState;
      var isSystemDefined = data.isSystemDefined;

      if (!UserProfile.updateUserPresenceState($rootScope.localUser, newState, isSystemDefined)) {
        LogSvc.debug('[UserProfileSvc]: State has not changed. Ignore event.');
        LogSvc.debug('[UserProfileSvc]: Publish /localUser/presence/checkIdle.');
        PubSubSvc.publish('/localUser/presence/checkIdle', [$rootScope.localUser.userPresenceState]);
        return;
      }

      $rootScope.$apply(function () {
        LogSvc.debug('[UserProfileSvc]: Received new state information. ', newState);
        LogSvc.debug('[UserProfileSvc]: Publish /localUser/update event');
        PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
        LogSvc.debug('[UserProfileSvc]: Publish /localUser/presence/checkIdle.');
        PubSubSvc.publish('/localUser/presence/checkIdle', [$rootScope.localUser.userPresenceState]);
      });
    });

    _clientApiHandler.on('User.USER_UPDATED', function (data) {
      if (!data.user || !$rootScope.localUser) {
        return;
      }

      if (data.user.userId === $rootScope.localUser.userId) {
        LogSvc.debug('[UserProfileSvc]: Received User.USER_UPDATED event');
        $rootScope.$apply(function () {
          // Do NOT update the local UserPresenceState
          data.user.userPresenceState = $rootScope.localUser.userPresenceState;
          UserProfile.update($rootScope.localUser, data.user);
          if (data.user.locale) {
            var newLocale = Utils.normalizeLocale(data.user.locale);
            $rootScope.localUser.locale = LocalizeSvc.setLanguage(newLocale);
          }
          updateTelephonyNumbers();
          LogSvc.debug('[UserProfileSvc]: Publish /localUser/update event');
          PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
        });
      }
    });

    _clientApiHandler.on('Account.TELEPHONY_CONFIGURATION_UPDATED', function (data) {
      if (!data.configuration || !$rootScope.localUser) {
        return;
      }
      var account = $rootScope.localUser.accounts && $rootScope.localUser.accounts[0];
      if (!account) {
        return;
      }

      $rootScope.$apply(function () {
        LogSvc.debug('[UserProfileSvc]: Received Account.TELEPHONY_CONFIGURATION_UPDATED event');
        account.telephonyConfiguration = data.configuration;
        UserProfile.syncTelephonyConfig($rootScope.localUser);
        updateTelephonyNumbers();

        LogSvc.debug('[UserProfileSvc]: Publish /localUser/update event');
        PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);

        LogSvc.debug('[UserProfileSvc]: Publish /localUser/telephonyConfigUpdated event');
        PubSubSvc.publish('/localUser/telephonyConfigUpdated');
      });
    });

    _clientApiHandler.on('Account.TELEPHONY_PREVIOUS_ALTERNATIVE_NUMBERS_UPDATED', function (data) {
      if (!data.previousAlternativeNumbers || !$rootScope.localUser) {
        return;
      }
      var account = $rootScope.localUser.accounts && $rootScope.localUser.accounts[0];
      if (!account) {
        return;
      }

      $rootScope.$apply(function () {
        LogSvc.debug('[UserProfileSvc]: Received Account.TELEPHONY_PREVIOUS_ALTERNATIVE_NUMBERS_UPDATED event');
        UserProfile.updateAlternativeNumbers($rootScope.localUser, data);

        LogSvc.debug('[UserProfileSvc]: Publish /localUser/update event');
        PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
      });
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////

    /**
     * Subscribes to telephony data changes
     */
    PubSubSvc.subscribe('/telephony/data', function (data, telephonyConv) {
      LogSvc.debug('[UserProfileSvc]: Received /telephony/data event');
      var userUpdated = false;
      // If the defaultCallerID changed, then re-evaluate the user's caller ID
      if (_defaultCallerId !== data.defaultCallerId) {
        _defaultCallerId = data.defaultCallerId;
        userUpdated = updateTelephonyNumbers();
      }
      if ($rootScope.localUser.telephonyAvailable !== data.telephonyAvailableForUser) {
        $rootScope.localUser.telephonyAvailable = data.telephonyAvailableForUser;
        LogSvc.debug('[UserProfileSvc]: Publish /conversations/showTelephonyConversation event');
        PubSubSvc.publish('/conversations/showTelephonyConversation', [$rootScope.localUser.telephonyAvailable, telephonyConv]);
        userUpdated = true;
      }
      if ($rootScope.localUser.conferenceDialOutAvailable !== data.conferenceDialOutAvailable) {
        $rootScope.localUser.conferenceDialOutAvailable = data.conferenceDialOutAvailable;
        userUpdated = true;
      }
      if (userUpdated) {
        LogSvc.debug('[UserProfileSvc]: Telephony data has been updated. Publish /localUser/update event');
        PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
      }
    });

    /**
     * Subscribes to the temporary and permanent users presence, but the permanent
     * subscription list is not cleared so that we can subscribe to it on wakeUp.
     */
    PubSubSvc.subscribe('/internal/svc/go2Sleep', function () {
      LogSvc.debug('[UserProfileSvc]: Go to Sleep');
      //_isMobileAsleep = true;
    });

    /**
     * Subscribes to the permanent users presence.
     * The mobile client is responsible to call subscribeTemporaryPresence to
     * subscribe to the temporary users again when selecting a conversation.
     */
    PubSubSvc.subscribe('/internal/svc/wakeUp', function () {
      LogSvc.debug('[UserProfileSvc]: Wake up');
      //_isMobileAsleep = false;
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.initSettings = function (settings) {
      LogSvc.debug('[UserProfileSvc]: Initialize user settings');
      updateSettings(settings);
    };

    this.setLocalUser = function (user, tenant, supportInfo) {
      if (!user) {
        return;
      }
      LogSvc.debug('[UserProfileSvc]: Init localUser');

      var raiseInit = true;
      if ($rootScope.localUser) {
        // We already initialized the localUser. Need to update it instead.
        UserProfile.update($rootScope.localUser, user);
        UserProfile.updateUserPresenceState($rootScope.localUser, user.userPresenceState);
        user = $rootScope.localUser;
        raiseInit = false;
      } else {
        // Make sure the received user object has been extended
        user = UserProfile.extend(user);
        // Initialize call forwarding data on local user object
        user.cfData = {};
        Object.keys(CallForwardingTypes).forEach(function (key) {
          user.cfData[key] = {
            cfwType: key,
            cfwAvailable: false
          };
        });

        $rootScope.localUser = user;
        Conversation.localUser = user;
        UserProfile.localUser = user;
      }

      // Initialize localUser's callerId and second call routing (i.e. busy handling) setting
      updateTelephonyNumbers();
      updateSecondCallRouting();
      updateMsTeamsIncomingCallRouting();
      updateATCRouting();

      // Initialize support on localUser
      supportInfo = supportInfo || {};
      user.supportEmailAddress = supportInfo.supportEmailAddress || null;
      if (!supportInfo.supportEmailAddress) {
        user.supportType = Constants.SupportType.CONVERSATION;
      } else {
        user.supportType = supportInfo.supportType || Constants.SupportType.CONVERSATION;
      }

      if (raiseInit) {
        setTenantData(tenant);

        var shortcutsSetting = _self.getSetting(Constants.UserSettingKey.KEYBOARD_SHORTCUTS_ENABLED);
        user.keyboardShortcutsEnabled = !!(shortcutsSetting && shortcutsSetting.booleanValue);

        _userSettingsLoadComplete = true;

        // Do not log full user object because it has location data - security and privacy
        LogSvc.debug('[UserProfileSvc]: Publish /localUser/init event. User = ', user.userId);
        PubSubSvc.publish('/localUser/init', [user]);
      } else {
        tenant && setTenantData(tenant);

        LogSvc.debug('[UserProfileSvc]: Publish /localUser/update event');
        PubSubSvc.publish('/localUser/update', [user]);
      }

      // The following code must be done after /localUser/init has been published to ensure that Lab features have been initialized.
      // Finally, set the client language using the locale configured for the user.
      LocalizeSvc.setLanguageAsync(user.locale).then(function (language) {
        if ($rootScope.localUser.locale !== language) {
          // User never selected preference language. Store browser's language, if supported,
          // in the user's profile.
          LogSvc.info("[UserProfileSvc] Set user's language using browser's language: ", language);
          $rootScope.localUser.locale = language;
          setLanguage($rootScope.localUser.locale);
        }
      });
    };

    /**
     * Verifies if the logged on user has the specified permission.
     * @returns {boolean} Returns true if the user has the specified permission. Otherwise, returns false.
     */
    this.hasPermission = function (permission) {
      return !!$rootScope.localUser && $rootScope.localUser.hasPermission(permission);
    };

    /**
     * Verifies if the logged on user can invite other users.
     * @returns {boolean} Returns true if the user is allowed to invite other users.
     */
    this.canInviteUsers = function () {
      return !!(
        $rootScope.localUser &&
        $rootScope.localUser.loginWithCredentialsEnabled &&
        $rootScope.localUser.hasPermission(Constants.SystemPermission.INVITE_USER)
      );
    };

    /**
     * Get a promised that is resolved when the user settings are loaded.
     * @returns {object} Promise object.
     */
    this.getUserSettingsPromise = function () {
      var deferred = $q.defer();
      if (_userSettingsLoadComplete) {
        $timeout(function () {
          deferred.resolve(_userSettings);
        });
      } else {
        PubSubSvc.subscribeOnce('/localUser/init', function () {
          deferred.resolve(_userSettings);
        });
      }
      return deferred.promise;
    };

    /**
     * Get a promised that is resolved when the localUser is loaded.
     * @returns {object} Promise object.
     */
    this.getLocalUserPromise = function () {
      var deferred = $q.defer();
      if (_userSettingsLoadComplete) {
        $timeout(function () {
          deferred.resolve($rootScope.localUser);
        });
      } else {
        PubSubSvc.subscribeOnce('/localUser/init', function () {
          deferred.resolve($rootScope.localUser);
        });
      }
      return deferred.promise;
    };

    this.getLocalUser = function () {
      return $rootScope.localUser;
    };

    this.getSettings = function () {
      return _userSettings;
    };

    this.getSetting = function (key) {
      return _userSettings.find(function (setting) {
        return setting.key === key;
      });
    };

    this.getSettingPromise = function (key) {
      if (!key) {
        return $q.reject('No key');
      }

      var deferred = $q.defer();
      if (_userSettingsLoadComplete) {
        deferred.resolve(this.getSetting(key));
      } else {
        var that = this;
        PubSubSvc.subscribeOnce('/localUser/init', function () {
          deferred.resolve(that.getSetting(key));
        });
      }
      return deferred.promise;
    };

    this.saveSettings = function (settings, cb) {
      if (!$rootScope.localUser) {
        cb && cb('No local user');
        return;
      }
      var keys = settings.map(function (setting) {
        return setting.key;
      });
      var oldSettings = getOldSettings(keys);

      updateSettings(settings);
      _clientApiHandler.setUserSettings(settings, function (err) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.warn('[UserProfileSvc] Error updating user settings on server. ', err);
            // Restore old setting
            LogSvc.debug('[UserProfileSvc] Restore old settings');
            updateSettings(oldSettings);
          }
          cb && cb(err);
        });
      });
    };

    this.saveSetting = function (key, value, cb) {
      var oldSettings = getOldSettings([key]);
      var setting = updateSetting(key, value);
      if (setting) {
        _clientApiHandler.setUserSettings(setting, function (err) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.warn('[UserProfileSvc] Error updating user setting on server. ', err);
              // Restore old setting
              LogSvc.debug('[UserProfileSvc] Restore old setting');
              updateSettings(oldSettings);
            }
            cb && cb(err);
          });
        });
      } else {
        cb && cb();
      }
    };

    this.getPresenceStatus = function () {};

    this.changePassword = function (oldPassword, newPassword, cb) {
      _clientApiHandler.changePassword(oldPassword, newPassword, function (err, changeResult) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.error('[UserProfileSvc]: The change password request was not successful');
            cb && cb(err);
            return;
          }
          cb && cb(changeResult);
        });
      });
    };

    this.setIdleState = function (idleState) {
      if (!$rootScope.localUser) {
        return false;
      }
      _userIdleState = idleState;
      var presenceChanged = false;
      if (!$rootScope.localUser.isRingCentral) {
        var localUserPresenceState = $rootScope.localUser.userPresenceState.state;
        if (localUserPresenceState === Constants.PresenceState.AVAILABLE && idleState === IdleState.Idle) {
          setUnifiedPresence({ state: Constants.PresenceState.AWAY, isSystemDefined: true });
          presenceChanged = true;
        } else if (localUserPresenceState === Constants.PresenceState.AWAY && idleState === IdleState.Active) {
          // We need isSystemDefined flag, to differentiate the action of mouse event from the dropdown action
          setUnifiedPresence({ state: Constants.PresenceState.AVAILABLE, isSystemDefined: true });
          presenceChanged = true;
        }
        if (presenceChanged) {
          LogSvc.debug('[UserProfileSvc]: Publish /internal/svc/idle/state event: ', idleState);
          PubSubSvc.publish('/internal/svc/idle/state', [idleState]);
          return true;
        }
      }
      return false;
    };

    this.removeAlternativeNumber = function (number, cb) {
      _clientApiHandler.removeAlternativeNumberHandler(number, function (err) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.warn('[UserProfileSvc]: Error removing alternative number: ', err);
            cb && cb(err);
            return;
          }
          cb && cb();
        });
      });
    };

    this.getExchangeSettings = function () {
      LogSvc.debug('[UserProfileSvc]: getExchangeSettings...');
      var deferred = $q.defer();
      _clientApiHandler.getExchangeSettings(function (err, settings) {
        if (err) {
          LogSvc.warn('[UserProfileSvc]: Unable to get settings from server. Error: ', err);
          deferred.reject(err);
          return;
        }
        if (!settings) {
          LogSvc.debug('[UserProfileSvc]: No settings available for this user');
        }
        deferred.resolve(settings);
      });
      return deferred.promise;
    };

    /**
     * Save Exchange settings
     * @param {Object} settings - Required object containing Exchange settings:
     * - email: email associated with Exchange
     * - password: Exchange password
     * - server: Required, exchange server address
     * - domain: Optional, domain name
     * - version - Required, exchange version
     * @returns {Promise<Object} Promise
     */
    this.saveExchangeSettings = function (settings) {
      LogSvc.debug('[UserProfileSvc]: saveExchangeSettings...');

      var deferred = $q.defer();

      // Create a shallow copy so we don't modify the given object.
      settings = Utils.shallowCopy(settings);
      settings.username = settings.username || $rootScope.localUser.emailAddress;

      // Save settings on back end
      var request = {
        username: settings.username,
        password: settings.password,
        server: settings.server,
        domain: settings.domain || '',
        version: settings.version || ''
      };

      _clientApiHandler.saveExchangeSettings(request, function (err, data) {
        if (err || !data) {
          LogSvc.warn('[UserProfileSvc]: Unable to save Exchange settings in the server. Error: ', err);
          deferred.reject(ExchangeConnError.INTERNAL_ERROR);
          return;
        }
        settings.encryptionKey = data.encryptionKey;
        deferred.resolve(settings);
      });
      return deferred.promise;
    };
    /////////////////////////////////////////////////////////////////////////////
    // FRN8499 - Email Change
    /////////////////////////////////////////////////////////////////////////////
    this.requestEmailChange = function (newEmail, cb) {
      // Check if user with requested email already exists
      _clientApiHandler.getUserByEmail(newEmail, function (getUserErr, user) {
        if (getUserErr || !user) {
          if (getUserErr === Constants.ReturnCode.STORAGE_OVERLOADED) {
            cb && cb(getUserErr);
          } else {
            _clientApiHandler.requestEmailChange($rootScope.localUser.userId, newEmail, function (err) {
              if (err) {
                LogSvc.error('[UserProfileSvc]: The change email request was not successful');
              }
              cb && cb(err);
            });
          }
          return;
        }
        cb && cb('EMAIL_ALREADY_EXISTS');
      });
    };

    this.updateUserProfile = function (userProfile, cb) {
      var data = {
        userId: $rootScope.localUser.userId,
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        largeImageUri: userProfile.largeImageUri,
        smallImageUri: userProfile.smallImageUri,
        phoneNumbers: userProfile.phoneNumbers,
        emailAddresses: userProfile.emailAddresses,
        jobTitle: userProfile.jobTitle,
        company: userProfile.company,
        reroutingPhoneNumber: userProfile.reroutingPhoneNumber
      };
      _clientApiHandler.updateUser(data, function (err, userData) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.error('[UserProfileSvc]: Error updating user data: ', err);
            cb && cb(err);
            return;
          }

          LogSvc.debug('[UserProfileSvc]: User profile was successfully updated');
          // Do NOT update the local UserPresenceState
          userData.userPresenceState = $rootScope.localUser.userPresenceState;
          UserProfile.update($rootScope.localUser, userData);
          updateTelephonyNumbers();

          cb && cb(null, $rootScope.localUser);

          LogSvc.debug('[UserProfileSvc]: Publish /localUser/update event');
          PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
        });
      });
    };

    /**
     * This function starts notifications manual snooze.
     *
     * @function
     * @param {int} value : 0 = Today, >1 = hours of snoozing.
     */
    this.snooze = function (value) {
      LogSvc.debug('[UserProfileSvc]: Snooze with value =', value);
      // var snoozeStart = !_manualSnoozeOn;

      // _manualSnoozeOn = true;
      // snoozeNotifications(value);

      // if (snoozeStart) {
      //     // FRN8574: Publish event to play sound
      //     LogSvc.debug('[UserProfileSvc]: Publish /user/snooze/start event');
      //     PubSubSvc.publish('/user/snooze/start');
      // }
    };

    /**
     * This function resumes notifications, including auto-snoozed notifications.
     */
    this.resume = function () {
      LogSvc.debug('[UserProfileSvc]: Resume snooze');
      //resumeNotifications();
    };

    /**
     * This function starts the auto-snooze when a feature requires notifications to be blocked
     * while the feature is active (e.g. Screenshare). This function is supposed to be invoked
     * as soon as the feature started.
     */
    this.startAutoSnooze = function () {
      LogSvc.debug('[UserProfileSvc]: Start Auto-Snooze');
      // _autoSnoozeOn = true;

      // if (_manualSnoozeOn) {
      //     // There is a snooze running.
      //     LogSvc.debug('[UserProfileSvc]: User already started snooze. Do not change dndUntil.');
      //     // Reset the local timer to take in consideration that auto-snooze is on
      //     startSnoozeTimer();
      //     return;
      // }

      // // Start auto-snooze. The client will keep refreshing the time as needed.
      // snoozeNotifications(AUTO_SNOOZE_TIME);

      // LogSvc.debug('[UserProfileSvc]: Publish /user/autoSnooze/start event');
      // PubSubSvc.publish('/user/autoSnooze/start');
    };

    /**
     * This function cancels an auto-snooze, but does not affect a manual snooze
     * that may be running.
     *
     * @function
     */
    this.cancelAutoSnooze = function () {
      LogSvc.debug('[UserProfileSvc]: Cancel Auto-Snooze');
      // if (!_autoSnoozeOn) {
      //     LogSvc.debug('[UserProfileSvc]: Auto-Snooze was not set');
      //     return;
      // }

      // if (_manualSnoozeOn) {
      //     // Manual snooze still running
      //     LogSvc.debug('[UserProfileSvc]: Cancel auto-snooze but do not resume notifications');
      //     _autoSnoozeOn = false;
      //     // Reset the local timer to take in consideration that auto-snooze is off
      //     startSnoozeTimer();
      //     return;
      // }
      // // Resume notifications immediately
      // resumeNotifications();
    };

    /**
     * This function returns true if unlimited snooze is set
     */
    this.isUnlimitedSnoozeSet = function () {
      //return $rootScope.localUser.userPresenceState.dndUntil === MAX_DATE;
      return false;
    };

    this.setPresenceWithLocation = function (state, cb) {
      if (state === _clientPresenceState.state) {
        // No changes
        cb && cb(null, $rootScope.localUser.userPresenceState);
        //return;
      }
      //setPresenceWithLocation(state, cb);
    };

    this.setUnifiedPresence = function (data, cb) {
      setUnifiedPresence(data, cb);
    };

    this.setUnifiedPresenceV2 = function (data, cb) {
      if (!data) {
        cb && cb();
        return;
      }
      setUnifiedPresence(data, cb);
    };

    /**
     * Saves the status message of the user as a setting and sets the user presence.
     * @param {String} msg - The new status msg
     * @param {Boolean} raiseSetPresence - Flag indicating whether or not to also raise a SET_PRESENCE request
     */
    this.setStatusMessage = function (msg, raiseSetPresence, cb) {
      if (msg) {
        LogSvc.debug('[UserProfileSvc]: Set status message');
      } else {
        LogSvc.debug('[UserProfileSvc]: Clear status message');
      }

      var oldMsg = $rootScope.localUser.userPresenceState.statusMessage;
      var newMsg = msg || '';
      setStatusMessage(newMsg);

      // Persist the status message
      _self.saveSetting(Constants.UserSettingKey.STATUS_MESSAGE_TEXT, newMsg, function (err) {
        if (err) {
          LogSvc.error('[UserProfileSvc]: Error saving status message. ', err);
          setStatusMessage(oldMsg);
          cb && cb(err);
          return;
        }

        LogSvc.debug('[UserProfileSvc]: Successfully saved the status message');
        if (!raiseSetPresence) {
          cb && cb(null, $rootScope.localUser.userPresenceState);
          LogSvc.debug('[UserProfileSvc]: Publish /localUser/update event');
          PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
          return;
        }

        // Now that message is saved, update the user presence with the new message
        setLocalUserPresence(null, cb);
      });
    };

    this.updateLocation = function () {
      // var shareLocation = isLocationShared();
      // if (shareLocation) {
      //     LogSvc.debug('[UserProfileSvc]: User is sharing location. Retrieve location data and update user presence.');
      //     setPresenceWithLocation(null);
      // } else if (hasLocation()) {
      //     LogSvc.debug('[UserProfileSvc]: User is not sharing location. Remove location data from local user presence.');
      //     // Remove location data and timeZoneOffset
      //     clearLocation(_clientPresenceState, true);
      //     clearLocation($rootScope.localUser.userPresenceState, true);
      //     PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
      // }
    };

    this.setLanguage = setLanguage;

    this.setReroutingPhoneNumber = setReroutingPhoneNumber;

    this.getUserLocale = function (languageOnly) {
      var locale = ($rootScope.localUser && $rootScope.localUser.locale) || 'en-US';
      if (languageOnly) {
        locale = locale.substr(0, 2);
      }
      return locale;
    };

    this.getUserIdleState = function () {
      return _userIdleState;
    };

    this.getTenantData = function () {
      return _tenantData;
    };

    this.getTenantDataDefaults = function () {
      return _tenantDataDefaults;
    };

    this.refreshTenantData = function (tenant) {
      return setTenantData(tenant);
    };

    this.updateTenantSettings = function (cb, tenantContext) {
      _clientApiHandler.getUserTenantSettings(function (err, settings) {
        if (err) {
          LogSvc.error('[UserProfileSvc]: Failed to get tenant settins. ', err);
          cb && cb(err);
        } else {
          var tenantVariables = updateTenantSettings(settings);
          LogSvc.debug('[UserProfileSvc]: Updated tenant settings: ', tenantVariables);
          cb && cb(null, tenantVariables);
        }
      }, tenantContext);
    };

    this.parseTenantSettings = function (tenant) {
      var settings = (tenant && tenant.settings) || [];
      return getTenantVariables(settings);
    };

    this.resetOpenScapeDevicePins = function (directoryNumber) {
      var deferred = $q.defer();

      LogSvc.debug('[UserProfileSvc]: Reset OpenScape device PINs for ', directoryNumber);
      _clientApiHandler.resetOpenScapeDevicePins($rootScope.localUser.userId, directoryNumber, function (err, devicePins) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.error('[UserProfileSvc]: Error resetting OpenScape device PINs: ', err);
            deferred.reject(err);
          } else {
            deferred.resolve(devicePins);
          }
        });
      });

      return deferred.promise;
    };

    this.isSystemNotificSoundSuported = function () {
      var isWin10 = Utils.isWindowsOs && Utils.getOSInfo(window.navigator.userAgent).version === '10';
      return (isWin10 && $rootScope.browser.chrome) || (Utils.isMacOs && $rootScope.browser.firefox);
    };

    this.isUserSettingSupported = function (/*key*/) {
      return true;
    };

    this.updateTelephonyNumbers = updateTelephonyNumbers;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.UserProfileSvcImpl = UserProfileSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global PasswordMgmt, RegistrationState, require*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var ConnectionState = unifyPhone.Enums.ConnectionState;
  var Constants = unifyPhone.Constants;
  var IdleState = unifyPhone.Enums.IdleState;
  var UserProfile = unifyPhone.UserProfile;
  var Utils = unifyPhone.Utils;
  var WebRTCAdapter = unifyPhone.WebRTCAdapter;
  var RtcSessionController = unifyPhone.RtcSessionController;

  // eslint-disable-next-line max-params, max-lines-per-function
  function RegistrationSvcImpl($rootScope, $timeout, $q, $http, LogSvc, PubSubSvc, LocalStoreSvc, UserProfileSvc) {
    // NOSONAR
    LogSvc.debug('New Service: RegistrationSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var PENDING_CONNECTION_DELAY = 50;
    var DEFAULT_MAX_UPLOAD_SIZE = 50 * 1024 * 1024; // byte
    var DEFAULT_SHOW_PRESENCE_ON_ADD_USER = false;
    var RETRY_DELAY = 1000;
    var RETRY_INTERVAL = 8000;
    var CHECK_CONNECTIVITY_TIMEOUT = 10000; // Check connectivity timeout

    // Set Logout timeout to 2s. We don't actually need to wait for the response.
    // The timer is just to give some time for the logout request to be sent out to the wire before WebSocket is closed.
    var LOGOUT_TIMEOUT = 2000;

    // NGTC-4801: New logons 12 hours before session expiration (7 days) should force logout and inform user on login page
    var SESSION_EXPIR_ANTICIPATION_TIME = 43200000; // 12 hours

    var _userData = null;
    var _regState = RegistrationState.Disconnected;
    var _clientSettings = null;
    var _isMobileAsleep = false; // true: indicates a mobile client in background mode
    var _voipNotificationsDisabled = false;
    var _that = this;

    // Initialize global properties
    var _globalProperties = {
      showPresenceOnAddUser: DEFAULT_SHOW_PRESENCE_ON_ADD_USER,
      chromeExtensionId: '',
      edgeExtensionId: '',
      baseUrl: '',
      maxFavorites: 50
    };

    var _clientApiHandler = ClientApiHandler.getInstance();

    var _pendingDisconnect = false;
    var _pendingSetupConnections = null;
    var _pendingSetupConnectionsTimeout = null;

    var _cachedStuff = null;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function setState(newState) {
      if (newState !== _regState) {
        _regState = newState;
        if (newState === RegistrationState.Disconnected) {
          _pendingDisconnect = false;
          if (_pendingSetupConnections) {
            // Wait a little for any pending Client API requests to be aborted
            _pendingSetupConnectionsTimeout = $timeout(function () {
              _pendingSetupConnectionsTimeout = null;
              _pendingSetupConnections();
              _pendingSetupConnections = null;
            }, PENDING_CONNECTION_DELAY);
          }
        }

        LogSvc.info('[RegistrationSvc]: Publish /registration/state event. State = ', newState);
        PubSubSvc.publish('/registration/state', [newState]);
      }
    }

    function setUserData(data) {
      LogSvc.debug('[RegistrationSvc]: Received user data from server. ', data);
      if (!data || !data.clientSettings) {
        LogSvc.error('[RegistrationSvc]: Server did not return client settings');
        return;
      }

      _userData = data;

      if (data.ssoLogin !== undefined && $rootScope.ssoLogin !== data.ssoLogin) {
        LogSvc.info('[RegistrationSvc]: Update ssoLogin to ', data.ssoLogin);
        $rootScope.ssoLogin = data.ssoLogin;
        LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.SSOLOGIN, $rootScope.ssoLogin);
      }

      $rootScope.conceptboardConfig = data.conceptboardConfig;

      if (typeof $rootScope.unifyPhoneLabs.SIMULCAST === 'boolean') {
        data.clientSettings = data.clientSettings || {};
        data.clientSettings.addAdditionalLowVideoStream = $rootScope.unifyPhoneLabs.SIMULCAST;
      }
      RtcSessionController.processClientSettings(data.clientSettings);

      var settings = data.clientSettings;
      settings.clientVersion = data.clientVersion;
      settings.runningMode = data.mode;
      settings.serverTime = data.serverTime;
      settings.serverName = data.serverName;

      $rootScope.developmentMode = settings.runningMode === 'development';
      _clientApiHandler.setDevelopmentMode($rootScope.developmentMode);
      _clientApiHandler.setProxyTarget(data.wsTargetURL);

      _clientSettings = settings;

      LogSvc.debug('[RegistrationSvc]: Publish /clientSettings/loaded event. settings = ', settings);
      PubSubSvc.publish('/clientSettings/loaded', [settings]);
    }

    function onRegistrationError(errorCb, err) {
      if (_regState === RegistrationState.Reconnecting) {
        LogSvc.error('[RegistrationSvc]: Failed to reconnect client. ', err);

        if (err === Constants.ReturnCode.DISCONNECTED) {
          LogSvc.info('[RegistrationSvc]: Reconnection failed because socket was disconnected. Wait for next reconnection.');
        } else {
          var delay = RETRY_DELAY + Utils.randomNumber(0, RETRY_INTERVAL);
          LogSvc.info('[RegistrationSvc]: Try to reconnect in ' + delay + ' milliseconds');
          _clientApiHandler.reconnect(delay);
        }
        return;
      }

      LogSvc.error('[RegistrationSvc]: Failed to register client. ', err);
      _userData = null;
      _clientApiHandler.disconnect();
      setState(RegistrationState.Disconnected);
      if (errorCb) {
        if (err === Constants.ReturnCode.OVERLOADED) {
          err = 'res_BackendOverloadedError';
        } else if (typeof err !== 'string' || !err.startsWith('res_')) {
          err = 'res_InternalServerError';
        }
        errorCb(err);
      }
    }

    function setGlobalProperties(globalProperties) {
      globalProperties &&
        globalProperties.forEach(function (prop) {
          switch (prop.name) {
            case Constants.GlobalPropertyName.CHROME_EXTENSION_ID:
              if (prop.value) {
                _globalProperties.chromeExtensionId = prop.value;
              } else {
                _globalProperties.chromeExtensionId = '';
                LogSvc.info('[RegistrationSvc]: No value for CHROME_EXTENSION_ID. Use local hardcoded IDs.');
              }
              break;
            case Constants.GlobalPropertyName.EDGE_EXTENSION_ID:
              if (prop.value) {
                _globalProperties.edgeExtensionId = prop.value;
              } else {
                _globalProperties.edgeExtensionId = '';
                LogSvc.info('[RegistrationSvc]: No value for EDGE_EXTENSION_ID. Use local hardcoded URL.');
              }
              break;
            case Constants.GlobalPropertyName.SHOW_PRESENCE_ON_ADD_USER:
              if (prop.value) {
                _globalProperties.showPresenceOnAddUser = prop.value === 'true';
              } else {
                LogSvc.warn('[RegistrationSvc]: Invalid SHOW_PRESENCE_ON_ADD_USER value. Fallback to default.');
                _globalProperties.showPresenceOnAddUser = DEFAULT_SHOW_PRESENCE_ON_ADD_USER;
              }
              break;
            case Constants.GlobalPropertyName.EXCHANGE_ONLINE_CONFIG:
              if (prop.value) {
                _globalProperties.exchangeOnlineConfig = prop.value;
              } else {
                LogSvc.info('[RegistrationSvc]: No value for EXCHANGE_ONLINE_CONFIG.');
              }
              break;
            case Constants.GlobalPropertyName.BASE_URL:
              if (prop.value) {
                _globalProperties.baseUrl = prop.value;
              } else {
                _globalProperties.baseUrl = '';
                LogSvc.info('[RegistrationSvc]: No value for BASE_URL. Use local hardcoded URL.');
              }
              break;
            case Constants.GlobalPropertyName.MAX_FAVORITES:
              if (prop.value) {
                _globalProperties.maxFavorites = prop.value;
              } else {
                _globalProperties.maxFavorites = 50;
                LogSvc.info('[RegistrationSvc]: No value for MAX_FAVORITES. Use local hardcoded number.');
              }
              break;
          }
        });
    }

    function processGetStuffResult(stuff, successCb, errorCb) {
      var user = stuff.user;
      var tenant = stuff.tenant;
      var supportInfo = stuff.supportInfo;
      delete stuff.user;

      user.secId = tenant.secId;
      user.domainName = tenant.company;
      user.isStandalone = tenant.isStandalone;
      user.isRingCentral = tenant.isRingCentral;
      user.isCloudLink = tenant.isCloudLink;
      user.isHydra = tenant.isHydra;
      user.isHydraOnPrem = tenant.isHydraOnPremMode;
      user.supportsCloudLinkFeatures = tenant.supportsCloudLinkFeatures;
      user.tenantMode = tenant.mode;
      user.accountPackageName = user.accounts && user.accounts[0] && user.accounts[0].parentPackageName;
      user.chatEnabled = tenant.chatEnabled;
      user.skipBackendSearch = !!user.isHydraOnPrem;

      // Make sure the setUserInfo API is available (iOS does not support it)
      if (typeof LogSvc.setUserInfo === 'function') {
        // We need to pass the non-extended user object to the logger
        LogSvc.setUserInfo(user, LocalStoreSvc.isCachingDisabled());
      }

      if (_cachedStuff) {
        // We only ask for Telephony conversation IDs once, so copy from cached data (if available).
        stuff.telephonyConvId = _cachedStuff.telephonyConvId || stuff.telephonyConvId;
      }

      LocalStoreSvc.setLoggedOnUser(user)
        .then(function () {
          if (_pendingSetupConnections) {
            LogSvc.info('[RegistrationSvc]: Abort previous connection');
            return;
          }

          user = UserProfile.extend(user);

          // Set username in local store in case rememberMe is set
          LogSvc.info('[RegistrationSvc]: Set lastUserName in local store to ', user.emailAddress);
          LocalStoreSvc.setString(LocalStoreSvc.keys.LAST_USERNAME, user.emailAddress);

          LogSvc.info('[RegistrationSvc]: Set ssologin in local store to ', $rootScope.ssoLogin);
          LocalStoreSvc.setString(LocalStoreSvc.keys.SSOLOGIN, $rootScope.ssoLogin);

          // Parse global properties
          setGlobalProperties(stuff.globalProperties);
          LogSvc.info('[RegistrationSvc]: Updated global properties: ', _globalProperties);

          // Add parsed global properties to stuff object
          stuff.parsedGlobalProperties = _globalProperties;

          if (_isMobileAsleep) {
            LogSvc.info('[RegistrationSvc]: The mobile client is in sleep mode, trigger wake up');
            _that.wakeUp();
          }

          UserProfileSvc.initSettings(stuff.settings);
          UserProfileSvc.setLocalUser(user, tenant, supportInfo);
          // $rootScope.localUser is either initialized or updated

          setClientCapabilities();

          setState(RegistrationState.Registered);

          // Cache the getStuff response
          _cachedStuff = stuff;

          // Publish the initial loaded stuff (excluding the user object).
          LogSvc.info('[RegistrationSvc]: Publish /registration/stuff event');
          PubSubSvc.publish('/registration/stuff', [stuff]);

          if (WebRTCAdapter.enabled && !Utils.isMobile()) {
            // Check if we have access to a microphone
            WebRTCAdapter.getMediaSources(function (audioSources) {
              if (!audioSources || !audioSources.length) {
                var options = { message: 'res_AccessToAudioInputDeviceFailed', manual: true };
                PubSubSvc.publish('/infoMessage/show', [options]);
              }
            });
          }

          successCb && successCb($rootScope.localUser);
        })
        .catch(function (dbError) {
          onRegistrationError(errorCb, dbError);
        });
    }

    // Replaces getLoggedOn and all its subsequent calls to get the information needed at log on time
    function getStuff(successCb, errorCb) {
      var types = [
        Constants.GetStuffType.USER,
        Constants.GetStuffType.ACCOUNTS,
        Constants.GetStuffType.PRESENCE_STATE,
        Constants.GetStuffType.SETTINGS,
        Constants.GetStuffType.GLOBAL_PROPERTIES,
        Constants.GetStuffType.TENANT,
        Constants.GetStuffType.SUPPORT_INFO
      ];

      if (!_cachedStuff || !_cachedStuff.telephonyConvId) {
        types.push(Constants.GetStuffType.TELEPHONY_CONVERSATION_ID);
      }

      _clientApiHandler.getStuff(types, function (err, stuff) {
        $rootScope.$apply(function () {
          if (err) {
            onRegistrationError(errorCb, err);
          } else {
            processGetStuffResult(stuff, successCb, errorCb);
          }
        });
      });
    }

    function validateSession() {
      LogSvc.debug('[RegistrationSvc]: Send GET /validateSession to validate authentication');

      var url = unifyPhone.__domain + '/api' + unifyPhone.__path + '/validateSession';
      return $http
        .get(url)
        .then(function () {
          LogSvc.debug('[RegistrationSvc]: GET /validateSession was successful');
          return true;
        })
        .catch(function (response) {
          var status = response.status;
          LogSvc.debug('[RegistrationSvc]: Failed to validate session. status =', status);
          // Session is only considered invalid if return code is 401
          return status !== 401;
        });
    }

    function getUserDataAndInit(url, successCb, errorCb, withCredentials, skipExpirationCheck) {
      setState(RegistrationState.Connecting);

      LogSvc.debug('[RegistrationSvc]: Send GET /data/userData request');
      $http
        .get(url, { withCredentials: withCredentials })
        .then(function (response) {
          var data = response.data;
          LogSvc.debug('[RegistrationSvc]: Received GET /data/userData response');
          if (_pendingDisconnect || _pendingSetupConnections) {
            // There was either a disconnect or a new setupConnection request while we were waiting for the HTTP response
            onRegistrationError(errorCb, _pendingDisconnect ? 'Disconnected' : 'New setupConnections pending');
            return;
          }
          var remainingTime = data && data.remainingSessionTime;
          if (!skipExpirationCheck && remainingTime && remainingTime < SESSION_EXPIR_ANTICIPATION_TIME) {
            LogSvc.warn('[RegistrationSvc]: Session expiration is approaching, force new sign in. Remaining time: ', remainingTime);
            setState(RegistrationState.Disconnected);
            errorCb('res_auth_SessionExpiring');
            return;
          }

          setUserData(data);

          LogSvc.info('[RegistrationSvc]: Connecting to access server');

          _clientApiHandler.connect(
            function () {
              $rootScope.$apply(function () {
                if (_pendingDisconnect || _pendingSetupConnections) {
                  // There was either a disconnect or a new setupConnection request while we were waiting for the WS connection
                  onRegistrationError(errorCb, _pendingDisconnect ? 'Disconnected' : 'New setupConnections pending');
                  return;
                }
                LogSvc.info('[RegistrationSvc]: Successfully connected to access server. Get the logged on user.');
                setState(RegistrationState.Connected);
                getStuff(successCb, errorCb);
              });
            },
            function (err) {
              // Check if session is still valid
              validateSession().then(function (isValid) {
                onRegistrationError(errorCb, isValid ? err : 'res_auth_InvalidCredentials');
              });
            }
          );
        })
        .catch(function (response) {
          var status = response.status;
          LogSvc.warn('[RegistrationSvc]: Failed to retrieve user data from server. status = ', status);

          setState(RegistrationState.Disconnected);
          if (errorCb) {
            var resId;
            switch (status) {
              case 401:
                resId = 'res_auth_InvalidCredentials';
                break;
              case 502:
                resId = 'res_auth_ClosedConnection';
                break;
              // -1001 is the iOS NSError code for an HTTP request time out. This can happen for a number of
              // reason, more likely because the server is unreachable from the network the device is
              // using.
              case -1001:
                resId = 'res_auth_HttpTimeout';
                break;
              default:
                resId = 'res_auth_InternalError';
                break;
            }
            errorCb(resId);
          }
        });
    }

    function logout(reason) {
      LogSvc.info('[RegistrationSvc]: Logging Out');

      // Raise /registration/state event with state 'LoggingOut' so that the services can clean up the data
      LogSvc.info('[RegistrationSvc]: Publish /registration/state event. State = LoggingOut');
      PubSubSvc.publish('/registration/state', [RegistrationState.LoggingOut]);

      _voipNotificationsDisabled = false;

      var logoutCb = function () {
        // Disconnect websocket connections
        _clientApiHandler.disconnect();
        LogSvc.debug('[RegistrationSvc]: Publish /session/logout event - [' + reason + ']');
        PubSubSvc.publish('/session/logout', [reason]);
      };

      // Start 2s timer waiting for logout response. If no response after 2s, continue logging out anyway.
      var logoutPromise = $timeout(function () {
        LogSvc.debug('[RegistrationSvc]: Timed out waiting for logout response. Continue logging out.');
        logoutPromise = null;
        logoutCb();
      }, LOGOUT_TIMEOUT);

      _clientApiHandler.logout(function () {
        if (logoutPromise) {
          $timeout.cancel(logoutPromise);
          logoutCb();
        }
      });
    }

    function setClientCapabilities() {
      var capabilities = 0;
      /*eslint-disable no-bitwise*/
      if (Utils.isMobile()) {
        if (Utils.isIOS()) {
          capabilities |= Constants.ClientCapability.MUTABLE_APN;
          capabilities |= Constants.ClientCapability.IOS13_VOIP_APN;
        }
        if (_voipNotificationsDisabled) {
          capabilities |= Constants.ClientCapability.DISABLE_VOIP_NOTIFICATIONS;
        }
      }

      var debug = Object.keys(Constants.ClientCapability).reduce(function (acc, key) {
        if (capabilities & Constants.ClientCapability[key]) {
          // NOSONAR
          acc.push(key);
        }
        return acc;
      }, []);
      /*eslint-enable no-bitwise*/

      LogSvc.debug('[RegistrationSvc]: Set client capabilities = ', debug);
      _clientApiHandler.setClientCapabilities(capabilities);
    }

    function setupConnections(successCb, errorCb, host, clientInfo, skipExpirationCheck, path) {
      if (_regState === RegistrationState.Registered) {
        LogSvc.warn('[RegistrationSvc]: Client is already registered');
        successCb && successCb($rootScope.localUser);
        return;
      }
      _pendingDisconnect = false;
      if (_regState !== RegistrationState.Disconnected) {
        LogSvc.warn('[RegistrationSvc]: Current state is ' + _regState + '. Disconnect before processing new setupConnections request.');
        $timeout.cancel(_pendingSetupConnectionsTimeout);
        _pendingSetupConnectionsTimeout = null;
        _pendingSetupConnections = function () {
          LogSvc.info('[RegistrationSvc]: Continue processing queued setupConnections request.');
          setupConnections(successCb, errorCb, host, clientInfo, skipExpirationCheck, path);
        };
        if (_regState !== RegistrationState.Connecting) {
          // Disconnect websocket connections
          _clientApiHandler.disconnect();
        }
        return;
      }
      var url = '/data/userData';

      path = path || '';
      host = host || '';
      unifyPhone.__domain = host ? 'https://' + host : '';
      unifyPhone.__path = path;
      url = unifyPhone.__domain + '/api' + unifyPhone.__path + url;

      if (!host && path) {
        // If host is not provided, ClientApiHandler uses window.location.host + path
        host = window.location.host + path;
      } else {
        host = host + path;
      }

      // Set ClientApiHandler target.
      // If host is not provided, ClientApiHandler uses window.location.host
      _clientApiHandler.setTarget(host);
      _clientApiHandler.setClientInfo(clientInfo);
      getUserDataAndInit(url, successCb, errorCb, !!host, skipExpirationCheck);
    }

    function reconnect(keepActiveCalls) {
      LogSvc.info('[RegistrationSvc]: Reconnect, keepActiveCalls: ', !!keepActiveCalls);

      if (_regState === RegistrationState.Disconnected) {
        LogSvc.warn('[RegistrationSvc]: Cannot reconnect socket in Disconnected state. You must use setupConnections instead.');
        return false;
      }

      // Force a socket reconnect. This method will close the current socket and will
      // automatically attempt to reconnect it.
      if (keepActiveCalls) {
        // If code is not 1000 or 1001, Access will keep client in reconnecting
        // 4000 is the first unreserved code, for private use
        _clientApiHandler.reconnectV2({ code: 4000 });
      } else {
        _clientApiHandler.reconnect();
      }

      return true;
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    _clientApiHandler.addEventListener('connectionStateChange', function (evt) {
      if (_regState === RegistrationState.Disconnected) {
        // Ignore the event
        return;
      }

      $rootScope.$apply(function () {
        LogSvc.debug('[RegistrationSvc]: Received connectionStateChange from ClientApiHandler. State = ', evt.newState);
        switch (evt.newState) {
          case ConnectionState.Disconnected:
            setState(RegistrationState.Disconnected);
            break;
          case ConnectionState.Reconnecting:
            setState(RegistrationState.Reconnecting);
            break;
          case ConnectionState.Connected:
            if (_regState === RegistrationState.Reconnecting) {
              LogSvc.debug('[RegistrationSvc]: Publish /application/updateCheck event');
              PubSubSvc.publish('/application/updateCheck');

              var headers;
              var withCredentials;

              if (unifyPhone.__domain) {
                withCredentials = true;
              }

              // The client has successfully reconnected the Client API websocket
              // Refresh the client settings
              var url = unifyPhone.__domain + '/api' + unifyPhone.__path + '/data/userData';
              $http
                .get(url, { headers: headers, withCredentials: withCredentials, crossDomain: withCredentials })
                .then(function (response) {
                  // Since we got the user data we might as well update it
                  setUserData(response.data);
                })
                .catch(function (response) {
                  LogSvc.warn('[RegistrationSvc]: Failed to retrieve user data from server. status =', response.status);
                });

              // Retrieve the local user data again
              getStuff();
            }
            break;
        }
      });
    });

    _clientApiHandler.addEventListener('reconnectFailed', function () {
      LogSvc.debug('[RegistrationSvc]: Received reconnectFailed from ClientApiHandler');

      validateSession().then(function (isValid) {
        if (!isValid) {
          // Unauthorized, redirect to login showing message with reason 'timeout'.
          LogSvc.warn('[RegistrationSvc]: Client is unauthorized, redirect to login page with reason=timeout');
          // Client must stop attempting to reconnect WebSocket
          _clientApiHandler.disconnect();
          LogSvc.debug('[RegistrationSvc]: Publish /session/expiry event');
          PubSubSvc.publish('/session/expiry');
        }
      });
    });

    _clientApiHandler.on('User.SESSION_EXPIRES', function () {
      if (!$rootScope.localUser) {
        return;
      }
      $rootScope.$apply(function () {
        if (UserProfileSvc.getUserIdleState() === IdleState.Idle) {
          // Redirect to login showing message with reason 'timeout'
          LogSvc.debug('[RegistrationSvc]: Publish /session/expiry event');
          PubSubSvc.publish('/session/expiry');
        } else {
          _clientApiHandler.renewToken(function (err) {
            if (err) {
              LogSvc.debug('[RegistrationSvc]: Publish /session/renewTokenError event. err = ', err);
              PubSubSvc.publish('/session/renewTokenError', [err]);
            }
          });
        }
      });
    });

    _clientApiHandler.on('User.SESSION_EXPIRING', function () {
      if (!$rootScope.localUser) {
        return;
      }
      $rootScope.$apply(function () {
        if (UserProfileSvc.getUserIdleState() === IdleState.Active) {
          // Logout and redirect to login showing message with reason 'sessionExpiring'
          LogSvc.debug('[RegistrationSvc]: Publish /session/logout event');
          PubSubSvc.publish('/session/logout', 'sessionExpiring');
        } else {
          // Notify user without interrupting any other task
          var options = { message: 'res_auth_SessionExpiring', manual: true };
          PubSubSvc.publish('/infoMessage/show', [options]);
        }
      });
    });

    _clientApiHandler.on('User.SESSION_CLOSED', function (evt) {
      $rootScope.$apply(function () {
        LogSvc.debug('[RegistrationSvc]: Received Session Closed event from backend. Close connections and exit. ');

        // Disconnect the WebSocket connections
        _clientApiHandler.disconnect();

        if (evt) {
          switch (evt.reason) {
            case Constants.SessionClosedReason.NEW_CONNECTION_DETECTED:
              LogSvc.debug('[RegistrationSvc]: Publish /session/newConnectionDetected event');
              PubSubSvc.publish('/session/newConnectionDetected');
              return;
            case Constants.SessionClosedReason.SUSPENDED:
            case Constants.SessionClosedReason.TENANT_SUSPENDED:
              LogSvc.debug('[RegistrationSvc]: Publish /session/suspended event');
              PubSubSvc.publish('/session/suspended');
              return;
            case Constants.SessionClosedReason.DELETED:
            case Constants.SessionClosedReason.TENANT_DELETED:
              LogSvc.debug('[RegistrationSvc]: Publish /session/deleted event');
              PubSubSvc.publish('/session/deleted');
              return;
            case Constants.SessionClosedReason.REVOKED:
              LogSvc.debug('[RegistrationSvc]: Publish /session/logout event');
              PubSubSvc.publish('/session/logout', 'sessionExpiring');
              return;
          }
        }
        // If there is no specific reason, raise a generic /session/closed event
        LogSvc.debug('[RegistrationSvc]: Publish /session/closed event');
        PubSubSvc.publish('/session/closed');
      });
    });

    _clientApiHandler.on('User.PASSWORD_CHANGED', function (evt) {
      if (evt && evt.logout) {
        $rootScope.$apply(function () {
          // Redirect to login showing message with reason 'passwordChanged'
          LogSvc.debug('[RegistrationSvc]: Publish /session/passwordChanged event');
          PubSubSvc.publish('/session/passwordChanged');
        });
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    this.setupConnections = function (successCb, errorCb, host, clientInfo) {
      LogSvc.debug('[RegistrationSvc]: setupConnections - host = ', host || '(use default)');
      setupConnections(successCb, errorCb, host, clientInfo);
    };

    this.setupConnectionsV2 = function (data, successCb, errorCb) {
      errorCb = errorCb || function () {};
      if (!data) {
        errorCb('Invalid input');
        return;
      }
      LogSvc.debug('[RegistrationSvc]: setupConnectionsV2 - host = ', data.host || '(use default)');
      setupConnections(successCb, errorCb, data.host, data.clientInfo, data.skipExpiration, data.path || '');
    };

    this.jwtLogon = function (data) {
      if (!data && !data.accessToken) {
        return $q.reject('No valid data provided. accessToken is needed');
      }
      var deferred = $q.defer();
      var host = data.host;
      var withCredentials = !!host;
      var body = JSON.stringify({ provider: data.provider });

      var headers = {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + data.accessToken
      };

      var url = '/api' + data.path + '/login';
      if (host) {
        url = 'https://' + host + url;
      }

      $http
        .post(url, body, { headers: headers, withCredentials: withCredentials, crossDomain: withCredentials })
        .then(function (response) {
          if (response.status < 200 || response.status >= 300) {
            LogSvc.warn('[RegistrationSvc]: Failed to login with jwt. status = ', response.status);
            var resId = response.status === 401 ? 'res_auth_InvalidCredentials' : 'res_auth_InternalError';
            deferred.reject(resId);
            return;
          }
          deferred.resolve();
        })
        .catch(function (err) {
          LogSvc.warn('[RegistrationSvc]: Failed to login with jwt. status = ', err.status);
          var resId = err.status === 401 ? 'res_auth_InvalidCredentials' : 'res_auth_InternalError';
          deferred.reject(resId);
        });

      return deferred.promise;
    };

    this.jwtLogout = function (data) {
      data = data || {};
      var deferred = $q.defer();
      var host = data.host;
      var withCredentials = !!host;
      var headers;

      var url = '/api' + data.path + '/logout';
      if (host) {
        url = 'https://' + host + url;
      }

      $http
        .get(url, { headers: headers, withCredentials: withCredentials, crossDomain: withCredentials })
        .then(function (response) {
          if (response.status < 200 || response.status >= 300) {
            LogSvc.warn('[RegistrationSvc]: Failed to logout. status = ', response.status);
            deferred.reject();
            return;
          }
          // Disconnect websocket connection
          _that.disconnect();
          deferred.resolve();
        })
        .catch(function (err) {
          LogSvc.warn('[RegistrationSvc]: Failed to logout. status = ', err.status);
          deferred.reject();
        });

      return deferred.promise;
    };

    this.hasAuthenticatedSession = function (data) {
      if (!data) {
        return $q.reject('No data provided');
      }
      var deferred = $q.defer();
      var host = data.host;
      var path = data.path || '';
      var withCredentials = data.withCredentials;

      var url = '/api' + path + '/data/userData';
      if (host) {
        url = 'https://' + host + url;
      }
      if (withCredentials) {
        $http
          .ajax({
            url: url,
            dataType: 'GET',
            xhrFields: {
              withCredentials: true
            },
            crossDomain: true
          })
          .then(
            function (respData) {
              deferred.resolve(path ? respData && respData.data : true);
            },
            function () {
              deferred.resolve();
            }
          );
      } else {
        $http
          .get(url)
          .then(function (respData) {
            deferred.resolve(path ? respData && respData.data : true);
          })
          .catch(function () {
            deferred.resolve();
          });
      }
      return deferred.promise;
    };

    this.logout = function (reason) {
      logout(reason);
    };

    this.reconnect = function () {
      return reconnect();
    };

    this.disconnect = function () {
      LogSvc.info('[RegistrationSvc]: Disconnect');
      if (_pendingSetupConnections) {
        _pendingSetupConnections = null;
        $timeout.cancel(_pendingSetupConnectionsTimeout);
        _pendingSetupConnectionsTimeout = null;
      }
      if (_regState === RegistrationState.Disconnected) {
        LogSvc.debug('[RegistrationSvc]: Registration state is already Disconnected');
      } else if (_regState === RegistrationState.Connecting) {
        LogSvc.debug('[RegistrationSvc]: Registration state is Connecting. Set pendingDisconnect to true.');
        _pendingDisconnect = true;
      } else {
        // Disconnect websocket connections
        _clientApiHandler.disconnect();
      }
    };

    this.pingServer = function () {
      return _clientApiHandler.pingServer();
    };

    this.checkConnectivity = function (timeout, cb) {
      if (_regState !== RegistrationState.Connected && _regState !== RegistrationState.Registered) {
        cb && cb('NOT_CONNECTED');
        return;
      }
      timeout = Number(timeout);
      if (isNaN(timeout) || !timeout || timeout > CHECK_CONNECTIVITY_TIMEOUT) {
        timeout = CHECK_CONNECTIVITY_TIMEOUT;
      }
      var data = {
        timeout: timeout
      };
      LogSvc.debug('[RegistrationSvc]: Checking socket connectivity, data: ', data);
      _clientApiHandler.checkConnectivity(data, function (err) {
        if (err) {
          LogSvc.error('[RegistrationSvc]: Could not check for connectivity ', err);
          if (err === Constants.ReturnCode.REQUEST_TIMEOUT) {
            // Reconnect, keep active calls if possible, wait some ms for other actions on socket
            // , e.g. ping, that take place when request times out.
            $timeout(function () {
              reconnect(true);
            }, 200);
          }
          cb && cb(err);
          return;
        }
        cb && cb();
      });
    };

    this.state = function () {
      return _regState;
    };

    this.isRegistered = function () {
      return _regState === RegistrationState.Registered;
    };

    this.getUserData = function () {
      return _userData || {};
    };

    this.getMaxUploadSize = function () {
      return DEFAULT_MAX_UPLOAD_SIZE;
    };

    this.getShowPresenceOnAddUser = function () {
      return _globalProperties.showPresenceOnAddUser;
    };

    this.getExchangeOnlineConfig = function () {
      return _globalProperties.exchangeOnlineConfig || {};
    };

    /**
     * Used by the mobile client when entering the background.
     * This function will inform the other services and invoke their
     * go2Sleep function.
     *
     * @function
     */
    this.go2Sleep = function () {
      if (_isMobileAsleep) {
        LogSvc.error('[RegistrationSvc]: Mobile client is already asleep');
        return;
      }
      _isMobileAsleep = true;
      if (_regState === RegistrationState.Registered) {
        _clientApiHandler.goToSleep($rootScope.localUser.userId);
      }
      LogSvc.info('[RegistrationSvc]: Mobile - entering the background');
      PubSubSvc.publish('/internal/svc/go2Sleep');
    };

    /**
     * Used by the mobile client when entering the foreground.
     *
     * @function
     */
    this.wakeUp = function () {
      if (!_isMobileAsleep) {
        LogSvc.error('[RegistrationSvc]: mobile client is not asleep');
        return;
      }

      _isMobileAsleep = false;
      _clientApiHandler.wakeUp();
      LogSvc.info('[RegistrationSvc]: Mobile - entering the foreground');
      PubSubSvc.publish('/internal/svc/wakeUp');
    };

    /**
     * Get the client settings retrieved from access server
     *
     * @function
     * @returns {Object} The client settings object
     */
    this.getClientSettings = function () {
      return _clientSettings || {};
    };

    /**
     * Verify if the backend supports the given capability.
     *
     * @function
     * @param {String} capability The capability to be verified
     * @returns {Boolean} Returns true if the given capability is supported
     */
    this.checkClientSettingsCapability = function (capability) {
      if (!_clientSettings || !Array.isArray(_clientSettings.capabilities)) {
        return false;
      }
      return _clientSettings.capabilities.indexOf(capability) !== -1;
    };

    /**
     * Return Chrome extension ID configured in Global Properties.
     *
     * @returns {String} Chrome Extension ID
     */
    this.getChromeExtensionId = function () {
      return _globalProperties.chromeExtensionId;
    };

    /**
     * Return Edge extension ID configured in Global Properties.
     *
     * @returns {String} Edge Extension ID
     */
    this.getEdgeExtensionId = function () {
      return _globalProperties.edgeExtensionId;
    };

    /**
     * Return Base Url configured in Global Properties.
     *
     * @returns {String} Base Url
     */
    this.getBaseUrl = function () {
      return _globalProperties.baseUrl;
    };

    /**
     * Return Max Favorites configured in Global Properties.
     *
     * @returns {number} maxFavorites
     */
    this.getMaxFavorites = function () {
      return _globalProperties.maxFavorites;
    };

    /**
     * Get regions for GUEST client before switching to JsEngine to guest mode
     *
     * @function
     * @param {String} token to be verified before returning regions
     * @returns {promise List<RegionInfo>} Returns Promise with list of regions or null
     */
    this.getRegions = function (token) {
      LogSvc.warn('[RegistrationSvc]: getRegions started token: ', token);
      return new $q(function (resolve) {
        _clientApiHandler.getRegions(token, function (err, data) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.warn('[RegistrationSvc]: getRegions failed. ', err);
              resolve(null);
            } else {
              var result = (data && data.regions) || null;
              LogSvc.info('[RegistrationSvc]: getRegions succeeded. result = ', result);
              resolve(result);
            }
          });
        });
      });
    };

    if (Utils.isMobile()) {
      this.disableVoipNotifications = function () {
        LogSvc.info('[RegistrationSvc]: VoIP notifications have been disabled');
        _voipNotificationsDisabled = true;
        setClientCapabilities();
      };

      this.enableVoipNotifications = function () {
        LogSvc.info('[RegistrationSvc]: VoIP notifications have been enabled');
        _voipNotificationsDisabled = false;
        setClientCapabilities();
      };
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.RegistrationSvcImpl = RegistrationSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global RegistrationState, AuthenticationContext, require*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var ChromeExtension = unifyPhone.ChromeExtension;
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var Utils = unifyPhone.Utils;

  var ExchangeConnError = Object.freeze({
    COULD_NOT_CONNECT: 'couldNotConnect', // Cannot reach Exchange Server (returned by Connector)
    UNAUTHORIZED: 'unauthorized', // Could connect to the server, but could not authenticate user (returned by Connector)
    FAILED: 'failed', // The requested method failed to be completed (returned by Connector)
    NO_EXTENSION: 'extensionNotRunning', // Chrome extension is not running
    UNAVAILABLE: 'unavailable', // Failed to connect to connector
    INTERNAL_ERROR: 'internalError', // Internal error
    TIMEOUT: 'timeout', // Timed out waiting for a response
    EXCEED_MAX_ATTEMPT: 'exceedMaxAttempt', // Exceed the max attempts to connect
    MAILBOX_NOT_FOUND: 'mailboxNotFound', // Mailbox not found for this user on the configured Exchange server
    NTLM_NOT_SUPPORTED: 'ntlmNotSupported', // NTLM authentication is not supported on the configured Exchange server
    UNEXPECTED: 'unexpected', // Unexpected error returned by connector
    UNSUPPORTED_METHOD: 'unsupportedMethod', // Method not supportde by extension
    NO_RESULT: 'NO_RESULT' // Could not retrieve exchange settings
  });

  var ExchangeAuthentication = Object.freeze({
    BASIC: 'BASIC',
    OFFICE365: 'OFFICE365',
    INTEGRATED: 'INTEGRATED'
  });

  var Office365Cloud = Object.freeze({
    GLOBAL: 'GLOBAL',
    GERMANY: 'GERMANY'
  });

  var ExchangeOnlineApp = Object.freeze({
    GLOBAL: {
      Office365Cloud: Office365Cloud.GLOBAL,
      oAuthServer: 'login.microsoftonline.com',
      server: 'outlook.office365.com',
      url: 'https://outlook.office365.com/',
      instance: 'https://login.microsoftonline.com/',
      clientId: '3fbe68ed-8c3e-44e1-834c-d6003e917e72' // The id of the Exchange Connector Authentication app on http://portal.azure.com
    },

    GERMANY: {
      Office365Cloud: Office365Cloud.GERMANY,
      oAuthServer: 'login.microsoftonline.de',
      server: 'outlook.office.de',
      url: 'https://outlook.office.de/',
      instance: 'https://login.microsoftonline.de/',
      clientId: '58b0add5-07f2-43f0-af31-0725409d12ce'
    }
  });

  ///////////////////////////////////////////////////////////////////////////////////////
  // ExchangeConnSvc Implementation
  ///////////////////////////////////////////////////////////////////////////////////////

  // eslint-disable-next-line max-params, max-lines-per-function
  function ExchangeConnSvcImpl($rootScope, $q, $timeout, $injector, LogSvc, PubSubSvc, ExtensionSvc, RegistrationSvc, LocalStoreSvc) {
    // NOSONAR

    LogSvc.debug('New Service: ExchangeConnSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var DEFAULT_NUM_CONTACTS = 5;
    var DEFAULT_SETTINGS = {
      email: null,
      server: null,
      domain: null,
      username: null,
      useSSL: Utils.isWindowsOs,
      authentication: {}
    };

    var RETRY_DELAY = 1000;
    var RETRY_INTERVAL = 5000;
    var MAX_INTERVAL = 60000;
    var MAX_ATTEMPTS = 10;
    var MAX_SHORT_ATTEMPTS = 3;
    var EXPIRE_OFFSET = 300;
    var COMMON_CHAR_REGEX = /[-()\s]/g;
    var DIGIT_LENGTH_REGEX = /^\+?[\d#*]{3,}\b/g;
    var PREFIX_CHAR_PATTERN = '[\\+\\b]?[\\*#]*';
    var PREFIX_PATTERN = '[\\+\\b]?[\\d\\*#]*';
    var POSTFIX_PATTERN = '[\\d\\*#]*\\b';

    var SYNC_CONTACTS_DELAY = 2 * 60 * 1000;
    var PERSONAL_CONTACTS_SYNC_MIN_INTERVAL = 60 * 60 * 1000; // Sync Exchange private contacts cache every hour

    var _checkingStatus = false;
    var _self = this;

    // Indicates whether or not we are connected to the Exchange server
    var _exchangeConnected; // Keep it as undefined
    // Auto-connect during startup
    var _autoConnectEnabled;
    // Save last connect error
    var _lastConnectError;

    // Exchange online server to be connected
    var _exchangeOnline = null;

    // Exchange settings
    var _settings = Utils.shallowCopy(DEFAULT_SETTINGS);

    var _clientApiHandler = ClientApiHandler.getInstance();

    // For backward compatibility reasons
    var _useSSLStoredLocally = false;

    // Force INTEGRATED authentication for Windows users at Siemens
    var _basicAuthAllowed = true;
    var _forceSSL = false;

    var _personalContacts = {}; // Personal contact objects keyed by their Ids
    var _personalContactsPhoneMap = {}; // Map phone number to contact ID
    var _phoneLookup = ''; // Phone lookup string

    var _syncState;
    var _lastPersonalContactSync = 0;
    var _pendingSyncTimeout = null;

    var _retryInterval = RETRY_DELAY;
    var _retryAttempts = 0;
    var _retryTimeout = null;
    var _authenticationContext = null;
    var _authenticationContextConfig = null;
    var _oauthData = {
      isAuthenticated: false,
      userName: '',
      loginError: '',
      profile: ''
    };
    var _office365Supported = false;
    var _loginDeferred;
    var _getAuthorizationCodeDeferred;
    var _electronOAuthDeferred;
    var _phoneNumberLookupSearchId = 0;

    if ($rootScope.browser && !$rootScope.browser.chrome && Utils.isWindowsOs) {
      LogSvc.debug('[ExchangeConnSvc]: Inject ExchangeUserToUserSvc instead of ExtensionSvc for non-Chrome browsers');
      ExtensionSvc = $injector.get('ExchangeUserToUserSvc');
    } else {
      window.addEventListener('adal:loginCallback', function () {
        getOAuthToken(function (err, token) {
          if (err) {
            _loginDeferred && _loginDeferred.reject(err);
            _loginDeferred = null;
          } else {
            setUserInfo(token && token.accessToken, _exchangeOnline.url);
            LogSvc.debug('[ExchangeConnSvc]: LoginCallback token: ', obfuscateToken(token.accessToken));
            _loginDeferred && _loginDeferred.resolve(token);
            _loginDeferred = null;
          }
        });
      });

      window.addEventListener('adal:getAuthorizationCodeCallback', function () {
        getAuthorizationCode(function (err, authData) {
          if (err) {
            _getAuthorizationCodeDeferred && _getAuthorizationCodeDeferred.reject(err);
            _getAuthorizationCodeDeferred = null;
          } else {
            LogSvc.debug('[ExchangeConnSvc]: getAuthorizationCodeCallback code: ', obfuscateToken(authData.authCode));
            _getAuthorizationCodeDeferred && _getAuthorizationCodeDeferred.resolve(authData);
            _getAuthorizationCodeDeferred = null;
          }
        });
      });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function setUserInfo(token, resource) {
      if (_authenticationContext) {
        _oauthData.isAuthenticated = token && token.length > 0;
        var user = _authenticationContext.getCachedUser(resource) || {
          userName: ''
        };
        _oauthData.userName = user.userName;
        _oauthData.profile = user.profile;
        _oauthData.loginError = _authenticationContext.getLoginError();
        $rootScope.userInfo = _oauthData;
      }
    }

    function getAuthorizationCode(cb) {
      if (_authenticationContext) {
        _authenticationContext.getAuthCode(_exchangeOnline.url, function (desc, authData, err) {
          cb && cb(err, authData);
        });
      } else {
        cb && cb();
      }
    }

    function getOAuthToken(cb) {
      if (_authenticationContext) {
        _authenticationContext.acquireToken(_exchangeOnline.url, function (desc, token, err) {
          setUserInfo(token, _exchangeOnline.url);
          cb &&
            cb(err, {
              accessToken: token,
              expiry: _authenticationContext.getTokenExpiry(_exchangeOnline.url),
              expireOffsetSeconds: EXPIRE_OFFSET
            });
        });
      } else {
        cb && cb();
      }
    }

    function resetPersonalContactsCache() {
      _personalContacts = {};
      _personalContactsPhoneMap = {};
    }

    function validateToken(cb) {
      if (_settings && _settings.authentication && _settings.authentication.method === ExchangeAuthentication.OFFICE365) {
        if (_authenticationContext) {
          _authenticationContext.acquireToken(_exchangeOnline.url, function (desc, token, err) {
            if (err) {
              LogSvc.error('[ExchangeConnSvc]: acquireToken error ', err);
              cb && cb(err);
            } else {
              if (_settings.oAuthToken !== token) {
                setUserInfo(token, _exchangeOnline.url);
                if (_settings) {
                  _settings.oAuthToken = token;
                  _settings.token = {
                    accessToken: token,
                    expiry: _authenticationContext.getTokenExpiry(_exchangeOnline.url),
                    expireOffsetSeconds: EXPIRE_OFFSET
                  };
                } else {
                  err = 'Settings object is NULL';
                  LogSvc.error('[ExchangeConnSvc]: ' + err);
                  cb && cb(err);
                  return;
                }
              }
              cb && cb(err, _settings.token);
            }
          });
        } else {
          cb && cb('No authenticationContext object available');
        }
      } else {
        cb && cb();
      }
    }

    function validateReq(inputParam, keyReq, cb) {
      if (keyReq && !_exchangeConnected) {
        LogSvc.debug('[ExchangeConnSvc]: Exchange is not connected');
        cb && cb(ExchangeConnError.INTERNAL_ERROR);
        return false;
      }
      if (inputParam === undefined) {
        LogSvc.warn('[ExchangeConnSvc]: Missing mandatory input parameter');
        cb && cb(ExchangeConnError.INTERNAL_ERROR);
        return false;
      }
      if (keyReq && !_settings.encryptionKey) {
        LogSvc.warn('[ExchangeConnSvc]: No encryption key available. Cannot invoke Exchange services');
        cb && cb(ExchangeConnError.INTERNAL_ERROR);
        setExchangeConnected(false);
        return false;
      }
      if (!ExtensionSvc.isExtensionRunning()) {
        LogSvc.warn('[ExchangeConnSvc]: Chrome extension is not running');
        cb && cb(ExchangeConnError.NO_EXTENSION);
        setExchangeConnected(false);
        return false;
      }
      return true;
    }

    function getExchangeConnErr(error, data) {
      if (error || !data || !data.response) {
        if (error === ChromeExtension.ResponseError.TIMEOUT) {
          return ExchangeConnError.TIMEOUT;
        }
        return ExchangeConnError.INTERNAL_ERROR;
      }

      switch (data.response) {
        case ChromeExtension.ExchangeConnResponse.OK:
        case ChromeExtension.ExchangeConnResponse.NO_RESULT: // Handle as OK
          return null;
        case ChromeExtension.ExchangeConnResponse.COULD_NOT_CONNECT:
          return ExchangeConnError.COULD_NOT_CONNECT;
        case ChromeExtension.ExchangeConnResponse.UNAUTHORIZED:
          return ExchangeConnError.UNAUTHORIZED;
        case ChromeExtension.ExchangeConnResponse.FAILED:
          return ExchangeConnError.FAILED;
        case ChromeExtension.ExchangeConnResponse.TIMED_OUT:
          return ExchangeConnError.TIMEOUT;
        case ChromeExtension.ExchangeConnResponse.MAILBOX_NOT_FOUND:
          return ExchangeConnError.MAILBOX_NOT_FOUND;
        case ChromeExtension.ExchangeConnResponse.NTLM_NOT_SUPPORTED:
          return ExchangeConnError.NTLM_NOT_SUPPORTED;
        case ChromeExtension.ExchangeConnResponse.UNSUPPORTED_METHOD:
          return ExchangeConnError.UNSUPPORTED_METHOD;
      }
      return ExchangeConnError.UNEXPECTED;
    }

    function publishConnEvent(data) {
      if (_exchangeConnected) {
        LogSvc.debug('[ExchangeConnSvc]: Publish /exchange/connected event');
        PubSubSvc.publish('/exchange/connected', [data]);
      } else {
        LogSvc.debug('[ExchangeConnSvc]: Publish /exchange/disconnected event');
        PubSubSvc.publish('/exchange/disconnected', [data]);
      }
    }

    function setExchangeConnected(value, error, doNotShowError) {
      value = !!value;
      if (!_settings.encryptionKey || _exchangeConnected === value) {
        return;
      }
      _exchangeConnected = value;
      if (_pendingSyncTimeout) {
        $timeout.cancel(_pendingSyncTimeout);
        _pendingSyncTimeout = null;
      }

      if (_exchangeConnected) {
        _lastConnectError = null;
        publishConnEvent();

        if (!LocalStoreSvc.isCachingDisabled()) {
          LogSvc.debug('[ExchangeConnSvc]: Start timeout to sync personal contacts');
          _pendingSyncTimeout = $timeout(
            function () {
              LogSvc.debug('[ExchangeConnSvc]: Timeout expired. Sync personal contacts.');
              _pendingSyncTimeout = null;
              _self.syncAllPersonalContacts();
            },
            SYNC_CONTACTS_DELAY,
            false
          );
        }
      } else {
        if (error) {
          _lastConnectError = error;
        }
        publishConnEvent({
          error: error, // Error information (error code)
          doNotShowError: doNotShowError // Indicate if this event should show or not the error.
        });
      }
    }

    function getExchangeStatus(cb) {
      if (!cb) {
        return;
      }
      LogSvc.debug('[ExchangeConnSvc]: getExchangeStatus...');

      _checkingStatus = true;

      ExtensionSvc.exchangeGetCapabilities(function (errGetCap, cap) {
        if (errGetCap || !cap) {
          // Here we're not checking the capabilities themselves, but we can safely assume
          // that if the Extension returned an error, it doesn't support Exchange
          _checkingStatus = false;
          LogSvc.warn('[ExchangeConnSvc]: Error getting exchange connector capabilities:', errGetCap);
          cb(ExchangeConnError.UNAVAILABLE);
          return;
        }
        // Get the current Exchange connection status. If it's already connected as another
        // user, the encryptionKey validation will fail and the user will have to manually
        // connect again as the correct user.
        ExtensionSvc.exchangeGetConnectionStatus(_settings.encryptionKey, function (errGetStatus, resGetStatus) {
          _checkingStatus = false;
          if (errGetStatus || !resGetStatus || !resGetStatus.response) {
            LogSvc.warn('[ExchangeConnSvc]: Error getting exchange connection status: ', errGetStatus);
            cb(ExchangeConnError.INTERNAL_ERROR);
          } else {
            var status = resGetStatus.response;
            LogSvc.debug('[ExchangeConnSvc]: Exchange connection status: ', status);
            cb(null, status);
          }
        });
      });
    }

    function getExchangeConnSvcParams() {
      var params = {
        exchInfo: _settings,
        localizedStrings: {
          res_Yes: $rootScope.i18n.map.res_Yes,
          res_No: $rootScope.i18n.map.res_No,
          res_MicrosoftExchangeTitle: $rootScope.i18n.map.res_MicrosoftExchangeTitle,
          res_ExchangeServerAuthorize: $rootScope.i18n.map.res_ExchangeServerAuthorize
        }
      };
      if (_settings.authentication && _settings.authentication.oAuth) {
        _settings.authentication.oAuth.config = _settings.authentication.oAuth.config || _self.getAuthenticationContextConfig(_settings.encryptionKey);
      }
      return params;
    }

    function getExchangeOnlineApp(office365Cloud) {
      if (office365Cloud === Office365Cloud.GERMANY) {
        return unifyPhone.Enums.ExchangeOnlineApp.GERMANY;
      } else {
        return unifyPhone.Enums.ExchangeOnlineApp.GLOBAL;
      }
    }

    function setExchangeOnlineSettings(authentication, encryptionKey) {
      !_authenticationContext && _self.createAuthenticationContext(encryptionKey);
      if (_exchangeOnline && authentication && _authenticationContext && authentication.office365Cloud !== _exchangeOnline.office365Cloud) {
        _authenticationContext.clearCache();
      }

      _exchangeOnline = getExchangeOnlineApp(authentication && authentication.office365Cloud);
      _authenticationContext && _authenticationContext.setInstance(_exchangeOnline.instance);
      _authenticationContext && _authenticationContext.setClientId(_exchangeOnline.clientId);
    }

    function setLocalExchangeSettings(settings) {
      if (settings && !settings.email) {
        settings.email = $rootScope.localUser.emailAddress;
      }
      if (!settings.encryptionKey && _settings.encryptionKey) {
        settings.encryptionKey = _settings.encryptionKey;
      }
      _settings = settings || {};
      _settings.authentication = _settings.authentication || {};
      _settings.getOAuthToken = getOAuthToken;
      setExchangeOnlineSettings(_settings.authentication, _settings.encryptionKey);
      LogSvc.debug(
        '[ExchangeConnSvc]: setLocalExchangeSettings ' +
          'server: ' +
          (_settings.server || '') +
          ', method: ' +
          (_settings.authentication.method || '') +
          ', useSSL: ' +
          _settings.useSSL
      );
    }

    function setSettings(settings, cb) {
      settings = settings || {
        encryptionKey: '*'
      };
      if (_forceSSL) {
        LogSvc.debug('[ExchangeConnSvc]: Only INTEGRATED authentication is supported');
        settings.useSSL = true;
      } else if (_useSSLStoredLocally) {
        // Use authentication method from local storage
        settings.useSSL = _settings.useSSL;
      }
      if (settings.useSSL) {
        settings.username = '*';
        settings.password = '*';
        settings.domain = '*';
      }
      settings.authentication = settings.authentication || {};
      if (settings.server && settings.server.match(/office365/i)) {
        settings.authentication.method = ExchangeAuthentication.OFFICE365;
      }

      if (settings.encryptionKey) {
        ExtensionSvc.getStoredCredentials(settings.encryptionKey, function (err, data) {
          if (err || !data || !data.credentials || !data.credentials.username) {
            LogSvc.debug('[ExchangeConnSvc]: Credentials configured in extension could not be retrieved');
          } else {
            if (!settings.useSSL && !settings.username) {
              settings.username = data.credentials.username;
              settings.password = data.credentials.password;
            }
            settings.authentication = data.credentials.authentication || {};
            settings.authentication.oAuth = _settings.authentication.oAuth || {};
            settings.authentication.oAuth.config = _self.getAuthenticationContextConfig(settings.encryptionKey);
          }
          setLocalExchangeSettings(settings);
          cb && cb(null, settings);
        });
      } else {
        setLocalExchangeSettings(settings);
        cb && cb(null, settings);
      }
    }

    function getExchangeSettings(cb) {
      LogSvc.debug('[ExchangeConnSvc]: Get ExchangeSettings');
      _clientApiHandler.getExchangeSettings(function (getSettingsErr, settings) {
        $rootScope.$apply(function () {
          if (getSettingsErr && getSettingsErr !== ExchangeConnError.NO_RESULT) {
            LogSvc.warn('[ExchangeConnSvc]: Unable to get settings from server. Error: ', getSettingsErr);
            cb && cb(ExchangeConnError.INTERNAL_ERROR);
            return;
          }

          if (!settings) {
            // Exchange Connector has never been enabled for this user. Prompt for settings
            LogSvc.debug('[ExchangeConnSvc]: No settings available for this user');
            cb && cb(getSettingsErr === ExchangeConnError.NO_RESULT ? getSettingsErr : ExchangeConnError.UNAUTHORIZED);
            return;
          }

          var setSettingsCb = function (err, savedSettings) {
            if (err) {
              cb && cb(err);
            }

            if (savedSettings.authentication.method === ExchangeAuthentication.OFFICE365) {
              setExchangeOnlineSettings(savedSettings.authentication, savedSettings.encryptionKey);

              getOAuthToken(function (getTokenErr, token) {
                if (!getTokenErr) {
                  setUserInfo(token && token.accessToken, _exchangeOnline.url);
                  savedSettings.authentication = savedSettings.authentication || {};
                  savedSettings.authentication.token = token;
                } else {
                  $rootScope.userInfo = _oauthData;
                }
                setSettings(savedSettings, cb);
              });
            } else {
              setSettings(savedSettings, cb);
            }
          };

          if (!settings && getSettingsErr === ExchangeConnError.NO_RESULT) {
            LogSvc.debug('[ExchangeConnSvc]: Settings not available for this user. Save default values.');
            var exchangeSettings = {
              encryptionKey: '',
              email: $rootScope.localUser.emailAddress,
              server: '',
              useSSL: true
            };
            _clientApiHandler.saveExchangeSettings(exchangeSettings, function (err, data) {
              if (err) {
                cb && cb(err);
              } else {
                setSettings(data, setSettingsCb);
              }
            });
          } else {
            setSettings(settings, setSettingsCb);
          }
        });
      });
    }

    function canRetryToConnect(errorCode) {
      return (
        errorCode !== ChromeExtension.ExchangeConnResponse.UNAUTHORIZED &&
        errorCode !== ChromeExtension.ExchangeConnResponse.MAILBOX_NOT_FOUND &&
        errorCode !== ChromeExtension.ExchangeConnResponse.NTLM_NOT_SUPPORTED
      );
    }

    function sendConnectRequest(autoRetry, cb) {
      LogSvc.debug('[ExchangeConnSvc]: Connect to Exchange server.');

      if (_retryTimeout) {
        window.clearTimeout(_retryTimeout);
        _retryTimeout = null;
      }

      if (_exchangeConnected) {
        LogSvc.debug('[ExchangeConnSvc]: Already connected to Exchange.');
        cb && cb();
        return;
      }

      var params = getExchangeConnSvcParams();

      ExtensionSvc.exchangeConnect(params, function (error, connData) {
        var connError = getExchangeConnErr(error, connData);
        if (!connError) {
          LogSvc.debug('[ExchangeConnSvc]: Succesfully connected to Exchange server');
          _retryInterval = RETRY_DELAY;
          _retryAttempts = 0;
          if (!_settings.useSSL && !_settings.username) {
            ExtensionSvc.getStoredCredentials(_settings.encryptionKey, function (err, data) {
              if (err || !data || !data.credentials || !data.credentials.username) {
                LogSvc.debug('[ExchangeConnSvc]: Credentials configured in extension could not be retrieved');
                return;
              }
              _settings.username = data.credentials.username;
              _settings.password = data.credentials.password;
            });
          }
          // Save it in localstorage so we auto-connect next time
          saveAutoConnect(true);
          cb && cb();
        } else {
          LogSvc.warn('[ExchangeConnSvc]: Failed to connect to Exchange server - ', connError);

          if (!autoRetry || !canRetryToConnect(connError)) {
            cb && cb(connError);
            return;
          }

          if (_retryAttempts >= MAX_ATTEMPTS) {
            LogSvc.warn('[ExchangeConnSvc]: Reached max number of attempts to reconnect to Exchange');
            cb && cb(ExchangeConnError.EXCEED_MAX_ATTEMPT);
            return;
          }

          LogSvc.info('[ExchangeConnSvc]: Retry to connect to Exchange in ' + Math.floor(_retryInterval / 1000) + ' seconds');
          _retryTimeout = window.setTimeout(function () {
            _retryTimeout = null;
            sendConnectRequest(autoRetry, cb);
          }, _retryInterval);

          // After the first attempt (random), set the interval to RETRY_INTERVAL
          // for MAX_SHORT_ATTEMPTS, then start doubling it up to MAX_INTERVAL
          _retryAttempts++;
          _retryInterval = _retryAttempts > MAX_SHORT_ATTEMPTS ? _retryInterval * 2 : RETRY_INTERVAL;
          _retryInterval = Math.min(_retryInterval, MAX_INTERVAL);
        }
      });
    }

    function exchangeConnect(autoRetry, cb) {
      _retryInterval = RETRY_DELAY;
      _retryAttempts = 0;
      if (_retryTimeout) {
        window.clearTimeout(_retryTimeout);
        _retryTimeout = null;
      }
      // Get the Exchange connection status first. Only invoke exchangeConnect()
      // if not connected.
      getExchangeStatus(function (err, status) {
        if (err) {
          // There was an error, don't try to connect
          cb && cb(err);
          return;
        }
        if (status !== 'connected') {
          sendConnectRequest(autoRetry, cb);
        } else {
          cb && cb();
        }
      });
    }

    function sendAsyncResp(cb, resp) {
      if (typeof cb === 'function') {
        $timeout(function () {
          cb(resp);
        }, 0);
      }
    }

    function connect(autoRetry, cb) {
      LogSvc.debug('[ExchangeConnSvc]: connect...');

      if (!ExtensionSvc.isExtensionRunning()) {
        sendAsyncResp(cb, ExchangeConnError.NO_EXTENSION);
        return;
      }

      if (!_settings.encryptionKey) {
        // Get Settings from server
        getExchangeSettings(function (err) {
          if (!err) {
            exchangeConnect(autoRetry, cb);
          } else if (!$rootScope.browser.chrome) {
            setLocalExchangeSettings(Utils.shallowCopy(DEFAULT_SETTINGS));
            _self.saveSettings(_settings, function () {
              exchangeConnect(autoRetry, cb);
            });
          } else {
            cb && cb(err);
          }
        });
      } else {
        exchangeConnect(autoRetry, cb);
      }
    }

    function connectOnInit() {
      LogSvc.debug('[ExchangeConnSvc]: Exchange connector enabled, auto-connect it');
      _autoConnectEnabled = true;
      connect(true, function (err) {
        if (err) {
          if (err === ExchangeConnError.UNAUTHORIZED) {
            LogSvc.debug('[ExchangeConnSvc]: Auto-connect disabled due to wrong credentials');
            _autoConnectEnabled = false;
          }
          setExchangeConnected(false, err);
        } else {
          setExchangeConnected(true);
        }
      });
    }

    function onInitEvent(state) {
      if (state !== RegistrationState.Registered) {
        return;
      }
      var connector = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.EXCHANGE_CONNECTOR);
      var enabled = false;
      if (connector) {
        if (typeof connector === 'boolean') {
          LogSvc.debug('[ExchangeConnSvc]: Only the enabled connection flag stored locally');
          enabled = connector;
        } else {
          LogSvc.debug('[ExchangeConnSvc]: Enabled connection flag and authentication method stored locally');
          enabled = connector.enabled;
          _useSSLStoredLocally = !_forceSSL;
          _settings.useSSL = _forceSSL || connector.useSSL;
        }
      }
      if (enabled) {
        if (!LocalStoreSvc.isCachingDisabled() && !_phoneLookup) {
          // Get state from last personal contact sync
          var syncState = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.EXCHANGE_SYNC_STATE);
          if (syncState) {
            LogSvc.debug('[ExchangeConnSvc]: Loaded Exchange personal contacts sync state from local storage');
            _syncState = syncState;
            LocalStoreSvc.getAllExchangeContacts()
              .then(function (contacts) {
                if (contacts && contacts.length) {
                  contactsLoadedFromDb(contacts);
                } else {
                  LogSvc.warn('[ExchangeConnSvc]: No contacts loaded from indexed DB on startup. All contacts will be retrieved from Exchange');
                  _syncState = null;
                }
                connectOnInit();
              })
              .catch(function () {});
          } else {
            LogSvc.info('[ExchangeConnSvc]: No Exchange personal contacts loaded from indexed DB during startup. All contacts will be retrieved from Exchange');
            connectOnInit();
          }
        } else {
          connectOnInit();
        }
      } else {
        getExchangeSettings(function (err) {
          if (err && err === ExchangeConnError.NO_RESULT) {
            _self.saveSettings(_settings);
          }
        });
        LogSvc.debug('[ExchangeConnSvc]: Exchange connector not enabled');
      }
    }

    function handleConnError(connError) {
      if (connError === ExchangeConnError.UNAUTHORIZED) {
        setExchangeConnected(false, connError);
        ExtensionSvc.exchangeDisconnect();
      }
    }

    function updateUserWithLocalContactName(user) {
      if (_phoneLookup.length === 0 || !user || !(user.phoneNumber || user.fullyQualifiedNumber)) {
        return false;
      }
      var number = user.fullyQualifiedNumber || user.phoneNumber;
      // First remove commonly used phone number formatting characters: (, ), - and white spaces. In case the user is copy/pasting the phone number
      // We can't simply remove all non-digit chars, because that would cause search strings like "conferenceroom123" to trigger a phone number lookup
      var numberLookupStr = number.replace(COMMON_CHAR_REGEX, '');
      // Search for the number lookup in the cached private contacts
      if (numberLookupStr.match(DIGIT_LENGTH_REGEX)) {
        LogSvc.debug('[ExchangeConnSvc]: Searching number in Personal Contacts cache');
        // Make sure that special characters in the numberLookupStr are escaped
        numberLookupStr = RegExp.escape(numberLookupStr);
        var regex = new RegExp(PREFIX_CHAR_PATTERN + numberLookupStr + '\\b', 'g');
        var matches = _phoneLookup.match(regex);
        if (matches && matches.length === 1) {
          var phone = matches[0];
          var id = _personalContactsPhoneMap[phone];
          var c = _personalContacts[id];
          if (c) {
            user.displayName = ((c.firstName || '') + ' ' + (c.lastName || '')).trim();
            return true;
          }
        }
      }
      return false;
    }

    function searchContacts(searchStr, constraints, resCount, cb) {
      cb = cb || function () {};
      var contacts = [];

      if (constraints && (constraints.phoneNumberLookup || constraints.reversePhoneNumberLookup)) {
        // First remove commonly used phone number formatting characters: (, ), - and white spaces. In case the user is copy/pasting the phone number
        // We can't simply remove all non-digit chars, because that would cause search strings like "conferenceroom123" to trigger a phone number lookup
        var numberLookupStr = searchStr.replace(COMMON_CHAR_REGEX, '');
        // 1. If it's number lookup (numberLookupStr begins with 3 or more digits), search for it in the cached private contacts
        if (_phoneLookup.length > 0 && numberLookupStr.match(DIGIT_LENGTH_REGEX)) {
          LogSvc.debug('[ExchangeConnSvc]: Searching number in Personal Contacts cache. Number of phone numbers in cache: ', _phoneLookup.length);
          var singleMatch = numberLookupStr[0] === '+'; // If numberLookupStr is in E164 format, there can be only one match
          // Make sure that special characters in the numberLookupStr are escaped
          numberLookupStr = RegExp.escape(numberLookupStr);
          var regex;
          if (constraints.reversePhoneNumberLookup) {
            // Full match
            singleMatch = true;
            regex = new RegExp(PREFIX_CHAR_PATTERN + numberLookupStr + '\\b', 'g');
          } else {
            // Partial match
            regex = new RegExp(PREFIX_PATTERN + numberLookupStr + POSTFIX_PATTERN, 'g');
          }
          var matches = _phoneLookup.match(regex);
          if (matches) {
            var l = matches.length;
            if (singleMatch && l > 1) {
              LogSvc.warn('[ExchangeConnSvc]: searchContacts. Multiple private contacts found for a E164 number. No private contacts returned');
            } else {
              LogSvc.debug('[ExchangeConnSvc]: Number of matches in local cache:', l);
              // Copy all matches to contacts
              for (var i = 0; i < l; i++) {
                var phone = matches[i];
                var id = _personalContactsPhoneMap[phone];
                var c = _personalContacts[id];
                if (c) {
                  contacts.push({
                    firstName: c.firstName,
                    lastName: c.lastName,
                    phoneNumbers: [
                      {
                        type: c.phoneType,
                        number: phone
                      }
                    ],
                    emailAddresses: c.emailAddresses,
                    department: c.department,
                    sourceIntegrationRes: 'res_ExchangeContact'
                  });
                }
              }
            }
          }
          cb(null, contacts);
          _phoneNumberLookupSearchId++;

          return _phoneNumberLookupSearchId;
        }
      }

      if (!validateReq(searchStr, true, cb)) {
        return null;
      }
      // 2. Search in Exchange only if it's not a number lookup
      resCount = resCount || DEFAULT_NUM_CONTACTS;
      var searchId = ExtensionSvc.exchangeSearchContacts(_settings.encryptionKey, searchStr, resCount, function (error, data) {
        var connError = getExchangeConnErr(error, data);
        if (!connError) {
          LogSvc.debug('[ExchangeConnSvc]: Search contacts was successful. SearchId:', searchId);
          if (data.contacts && data.contacts.length) {
            data.contacts.forEach(function (contact) {
              contact.sourceIntegrationRes = 'res_ExchangeContact';
            });
            Array.prototype.push.apply(contacts, data.contacts);
          }
          cb(null, contacts, searchId);
        } else {
          LogSvc.warn('[ExchangeConnSvc]: Search contacts failed - ', connError);
          handleConnError(connError);
          cb(connError);
        }
      });
      return searchId;
    }

    function getContact(exchangeEmail, cb) {
      cb = cb || function () {};

      ExtensionSvc.exchangeGetContact(_settings.encryptionKey, exchangeEmail, function (error, data) {
        var connError = getExchangeConnErr(error, data);
        if (!connError) {
          if (data.response === ChromeExtension.ExchangeConnResponse.NO_RESULT) {
            LogSvc.debug('[ExchangeConnSvc]: Contact could not be resolved');
            cb(null, null);
            return;
          }
          if (data.contact) {
            data.contact.sourceIntegrationRes = 'res_ExchangeContact';
          }
          LogSvc.debug('[ExchangeConnSvc]: Get contact was successful');
          cb(null, data.contact);
        } else {
          LogSvc.warn('[ExchangeConnSvc]: Get contact failed - ', connError);
          handleConnError(connError);
          cb(connError);
        }
      });
    }

    function processPhoneNumber(p) {
      if (!p || !p.number) {
        return false;
      }
      if (!Utils.PHONE_DIAL_PATTERN.test(p.number)) {
        var match = p.number.match(Utils.PHONE_WITH_EXTENSION_PATTERN);
        if (match) {
          // Phone numbers in Exchange might contain extension numbers as well
          // so strip the extension out
          p.number = match[1] || '';
        } else {
          return false;
        }
      }
      // Remove any non-digit character from the phone number (except +)
      p.number = p.number.replace(/[^\d+*#]/g, '');
      if (!p.number) {
        return false;
      }
      return true;
    }

    function processContact(c, addToLookupStr) {
      var processPhone = function (p) {
        if (!processPhoneNumber(p)) {
          return;
        }
        _personalContactsPhoneMap[p.number] = c.id;
        if (addToLookupStr) {
          _phoneLookup += '\t' + p.number;
        }
      };
      var phones = c.phoneNumbers;
      if (phones) {
        phones.forEach(processPhone);
      }
      _personalContacts[c.id] = c;
    }

    function deleteContact(id) {
      if (_personalContacts[id]) {
        delete _personalContacts[id];
      }
      // Stale contacts will be left in _personalContactsPhoneMap, but they'll be cleaned up after next logon
    }

    function buildPhoneLookupStr() {
      var phoneNumbers = Object.keys(_personalContactsPhoneMap);
      _phoneLookup = phoneNumbers.join('\t');
    }

    function onGetRenewedToken(reqId) {
      validateToken(function (err, token) {
        if (err) {
          LogSvc.error('ValidateToken error: ', err);
        }
        LogSvc.debug('[ExchangeConnSvc]: Renewed token: ', obfuscateToken(token));
        ExtensionSvc.exchangeOnRenewedToken(_settings.encryptionKey, reqId, token);
      });
    }

    function resolveOAuthPromise(url) {
      LogSvc.debug('[ExchangeConnSvc]: resolveOAuthPromise url:', url && url.substring(0, url.indexOf('=')));
      var err, errDesc, hash;
      if (!url) {
        err = 'login_required';
      } else if (url.match(/error=/i)) {
        var params = url.replace(_authenticationContext.config.redirectUri + '#', '').split('&');
        params.forEach(function (param) {
          if (param.startsWith('error=')) {
            err = param.replace('error=', '');
          } else if (param.startsWith('error_description=')) {
            errDesc = param.replace('error_description=', '');
          }
        });
      } else if (url.match(/access_token/i)) {
        hash = url.replace(_authenticationContext.config.redirectUri + '#', '');
      } else if (url.match(/code=/i)) {
        hash = url.replace(_authenticationContext.config.redirectUri + '?', '');
      }

      if (hash) {
        LogSvc.debug('[ExchangeConnSvc]: resolveOAuthPromise hash:', obfuscateToken(hash));
        _electronOAuthDeferred && _electronOAuthDeferred.resolve(hash);
      } else {
        LogSvc.debug('[ExchangeConnSvc]: resolveOAuthPromise url:', url);
        LogSvc.debug('[ExchangeConnSvc]: resolveOAuthPromise err:' + err + ', err description:' + errDesc);
        _electronOAuthDeferred && _electronOAuthDeferred.reject(err, errDesc);
      }
    }

    function onSyncContactsEvent(evt) {
      if (!evt) {
        return;
      }
      if (evt.reponse === ChromeExtension.ExchangeConnResponse.FAILED) {
        // Any error, invalidate the syncState, which will force a full sync from scratch
        _syncState = null;
        LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.EXCHANGE_SYNC_STATE, _syncState);
        return;
      }

      if (!evt.contacts) {
        return;
      }
      if (evt.contacts.added && evt.contacts.added.length) {
        evt.contacts.added.forEach(function (contact) {
          processContact(contact, true);
        });
        LocalStoreSvc.putExchangeContacts(evt.contacts.added);
      }
      if (evt.contacts.changed && evt.contacts.changed.length) {
        evt.contacts.changed.forEach(function (contact) {
          processContact(contact);
        });
        // Here we have to rebuild the map and lookup string
        _personalContactsPhoneMap = {};
        var ids = Object.keys(_personalContacts);
        for (var i = 0, l = ids.length; i < l; i++) {
          var c = _personalContacts[ids[i]];
          if (c.phoneNumbers) {
            c.phoneNumbers.forEach(function (p) {
              _personalContactsPhoneMap[p.number] = c.id;
            });
          }
        }
        buildPhoneLookupStr();
        LocalStoreSvc.putExchangeContacts(evt.contacts.changed);
      }
      if (evt.contacts.deleted && evt.contacts.deleted.length) {
        evt.contacts.deleted.forEach(function (id) {
          deleteContact(id);
        });
        LocalStoreSvc.removeExchangeContacts(evt.contacts.deleted);
      }
    }

    function onGetContactsEvent(evt) {
      if (evt.contacts) {
        if (evt.contacts.length) {
          var list = evt.contacts;
          for (var i = 0, length = list.length; i < length; i++) {
            processContact(list[i]);
          }
        }
      } else {
        // Done retrieving all contacts
        LogSvc.info('[ExchangeConnSvc]: Loaded all personal contacts from Exchange server. Num of phones:', Object.keys(_personalContactsPhoneMap).length);
        buildPhoneLookupStr();
        saveContactsInLocalStore(_personalContacts);
      }
    }

    function saveContactsInLocalStore(contacts, cb) {
      if (!contacts) {
        cb && cb('No contacts to be saved');
        return;
      }
      var ids = Object.keys(contacts);
      var array = ids.map(function (id) {
        // Get the contact object and make sure it has an id property, otherwise
        // the put operation in LocalStoreSvc will fail
        var obj = contacts[id];
        obj.id = id;
        return obj;
      });
      LocalStoreSvc.putExchangeContacts(array)
        .then(function () {
          cb && cb();
        })
        .catch(function (err) {
          cb && cb(err);
        });
    }

    function contactsLoadedFromDb(contacts) {
      if (!contacts) {
        return;
      }
      resetPersonalContactsCache();
      for (var i = 0, l = contacts.length; i < l; i++) {
        processContact(contacts[i]);
      }
      LogSvc.info('[ExchangeConnSvc]: Loaded Exchange personal contacts from indexed DB. Num of phones:', Object.keys(_personalContactsPhoneMap).length);
      buildPhoneLookupStr();
    }

    function obfuscateToken(token) {
      if (_authenticationContext) {
        return _authenticationContext.obfuscateToken(token);
      } else {
        return token;
      }
    }

    function saveAutoConnect(enabled) {
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.EXCHANGE_CONNECTOR, {
        enabled: enabled,
        useSSL: _settings.useSSL
      });
      _autoConnectEnabled = enabled;
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/chromeExt/initialized', function () {
      LogSvc.debug('[ExchangeConnSvc]: Received /chromeExt/initialized event. registrationState = ', RegistrationSvc.state());
      if (_exchangeConnected) {
        // Desktop app sometimes send a /chromeExt/initialized event without a prior /chromeExt/unregistered
        setExchangeConnected(false);
      }
      onInitEvent(RegistrationSvc.state());

      ExtensionSvc.exchangeGetCapabilities(function (errGetCap, cap) {
        if (cap && cap.capabilities && cap.capabilities.match(/office365ex/i)) {
          _office365Supported = true;
        }
      });
    });

    PubSubSvc.subscribe('/chromeExt/unregistered', function () {
      LogSvc.debug('[ExchangeConnSvc]: Received /chromeExt/unregistered event');
      // checking to avoid popup message when it is disconnected.
      if (_exchangeConnected) {
        setExchangeConnected(false);
      }
    });

    PubSubSvc.subscribe('/registration/state', function (state) {
      LogSvc.debug('[ExchangeConnSvc]: Received /registration/state event');
      onInitEvent(state);
    });

    PubSubSvc.subscribe('/oauth/redirectUrl/exchange', function (newUrl) {
      LogSvc.debug('[ExchangeConnSvc]: Received /oauth/redirectUrl/exchange event');
      resolveOAuthPromise(newUrl);
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Extension events handler
    ///////////////////////////////////////////////////////////////////////////////////////
    ExtensionSvc.addEventListener &&
      ExtensionSvc.addEventListener(ChromeExtension.BgTarget.EXCHANGE_CONNECTOR, function (evt) {
        LogSvc.debug('[ExchangeConnSvc]: Received Exchange connector event. Evt type:', evt.data && evt.data.type);
        if (evt.data) {
          switch (evt.data.type) {
            case ChromeExtension.BgExchangeMsgType.GET_ALL_PERSONAL_CONTACTS:
              onGetContactsEvent(evt.data);
              break;
            case ChromeExtension.BgExchangeMsgType.SYNC_ALL_PERSONAL_CONTACTS:
              onSyncContactsEvent(evt.data);
              break;
            case ChromeExtension.BgExchangeMsgType.CONTACT_FOLDERS_SYNC_STATE:
              _syncState = evt.data.syncState;
              _lastPersonalContactSync = Date.now();
              LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.EXCHANGE_SYNC_STATE, _syncState);
              break;
            case ChromeExtension.BgExchangeMsgType.GET_RENEWED_TOKEN:
              onGetRenewedToken(evt.data.reqId);
              break;
            default:
              LogSvc.info('[ExchangeConnSvc]: Received unexpected Exchange connector event. Evt type:', evt.data.type);
              break;
          }
        }
      });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    // Define the read-only properties to access the internal variables
    Object.defineProperties(this, {
      name: {
        get: function () {
          return 'ExchangeConnSvc';
        },
        enumerable: true,
        configurable: false
      },
      checkingStatus: {
        get: function () {
          return _checkingStatus;
        },
        enumerable: true,
        configurable: false
      },
      connected: {
        get: function () {
          return _exchangeConnected;
        },
        enumerable: true,
        configurable: true
      },
      autoConnectEnabled: {
        get: function () {
          return _autoConnectEnabled;
        },
        enumerable: true,
        configurable: false
      },
      lastConnectError: {
        get: function () {
          return _lastConnectError;
        },
        enumerable: true,
        configurable: false
      }
    });

    this.connect = function (cb) {
      connect(false, function (err) {
        if (err) {
          // Manual connection failed. Clear the auto-connect flag in the local store so we won't auto-connect during next startup
          saveAutoConnect(false);
          setExchangeConnected(false, err, true);
        } else {
          setExchangeConnected(true);
        }
        cb && cb(err);
      });
    };

    this.disconnect = function (cb) {
      LogSvc.debug('[ExchangeConnSvc]: disconnect...');

      if (!ExtensionSvc.isExtensionRunning()) {
        // We already are disconnected
        sendAsyncResp(cb);
        return;
      }

      ExtensionSvc.exchangeDisconnect(function (error, data) {
        var connError = getExchangeConnErr(error, data);
        if (!connError) {
          LogSvc.debug('[ExchangeConnSvc]: Succesfully disconnected from Exchange settings');

          if (!_settings.useSSL) {
            // Clear saved password
            _settings.password = '';
          }
        } else {
          LogSvc.warn('[ExchangeConnSvc]: Failed to disconnect - ', connError);
        }

        setExchangeConnected(false, null, true);
        // Save this disconnected state so we won't try to automatically reconnect
        // during the next logon or when the extension is reloaded.
        saveAutoConnect(false);
        cb && cb();
      });
    };

    this.getSettings = function () {
      var settings = Utils.shallowCopy(_settings || DEFAULT_SETTINGS);

      // The useSSL parameter is currently misused to differentiate between BASIC and INTEGRATED
      // authentication. useSSL set to true indicates INTEGRATED authentication.
      if (settings.useSSL) {
        settings.username = '';
        settings.password = '';
        settings.domain = '';
      }
      return settings;
    };

    /**
     * Save Exchange settings
     * @param {Object} settings - Required object containing Exchange settings:
     * - email: email associated with Exchange
     * - server: Optional, exchange server address
     * - domain: Required, domain name
     * - username - Required, exchange user name
     * - password - Required, exchange password
     * @param {function} cb - Callback that will be invoked with the settings object
     * returned from the server (with the encryption key)
     */
    this.saveSettings = function (settings, cb) {
      LogSvc.debug('[ExchangeConnSvc]: saveSettings...');

      if (!validateReq(settings, false, cb)) {
        return;
      }
      cb = cb || function () {};

      // Create a shallow copy so we don't modify the given object.
      settings = Utils.shallowCopy(settings);

      // The useSSL parameter is currently misused to differentiate between BASIC and INTEGRATED
      // authentication. useSSL set to true indicates INTEGRATED authentication.
      settings.useSSL = _forceSSL || !!settings.useSSL;

      settings.email = settings.email || $rootScope.localUser.emailAddress;

      // For INTEGRATED authentication we need to set the username, password and domain to '*'.
      if (settings.useSSL) {
        settings.username = '*';
        settings.password = '*';
        settings.domain = '*';
      }

      // Save settings on back end, get encryption key, and store credentials on chrome ext.
      var request = {
        encryptionKey: '',
        email: settings.email,
        server: settings.server || '',
        domain: settings.domain,
        useSSL: settings.useSSL
      };

      if (request.useSSL) {
        // This device uses windows authentication. Do not overwrite domain in the back end.
        delete request.domain;
      }

      _clientApiHandler.saveExchangeSettings(request, function (err, data) {
        if (err) {
          LogSvc.warn('[ExchangeConnSvc]: Unable to save settings in the server. Error: ', err);
          cb(ExchangeConnError.INTERNAL_ERROR);
          return;
        }
        settings.encryptionKey = data.encryptionKey;
        setLocalExchangeSettings(settings);
        ExtensionSvc.storeExchCredentials(
          {
            encryptionKey: settings.encryptionKey,
            username: settings.username,
            password: settings.password,
            authentication: settings.authentication
          },
          function (error) {
            if (error) {
              var connError = getExchangeConnErr(error);
              LogSvc.warn('[ExchangeConnSvc]: Failed to store credentials. Error: ', error);
              cb(connError);
            } else {
              cb();
            }
          }
        );
      });
    };

    this.searchContactsWithConstraints = function (searchStr, constraints, resCount, cb) {
      if (!searchStr || typeof searchStr !== 'string') {
        cb && cb(null, []);
        return null;
      }
      if (
        !(constraints && (constraints.phoneNumberLookup || constraints.reversePhoneNumberLookup)) &&
        // Call validateReq() only if it's not a number lookup
        !validateReq(searchStr, true, cb)
      ) {
        return null;
      }
      LogSvc.debug('[ExchangeConnSvc]: searchContacts. Search string:', searchStr);
      return searchContacts(searchStr, constraints, resCount, cb);
    };

    this.cancelSearchContacts = function (reqId) {
      return ExtensionSvc.exchangeCancelReqCallback(reqId);
    };

    this.getContact = function (exchangeEmail, cb) {
      LogSvc.debug('[ExchangeConnSvc]: getContact...');

      if (!validateReq(exchangeEmail, true, cb)) {
        return;
      }
      getContact(exchangeEmail, cb);
    };

    this.getAllPersonalContacts = function () {
      if (LocalStoreSvc.isCachingDisabled() || !validateReq(true, true)) {
        return;
      }
      ExtensionSvc.getAllPersonalContacts(_settings.encryptionKey);
    };

    this.supportsGetAppointments = ExtensionSvc.supportsGetAppointments.bind(ExtensionSvc);

    this.getAppointments = function (startDate, endDate, resCount, cb) {
      LogSvc.debug('[ExchangeConnSvc]: getAppointments...');

      if (!ExtensionSvc.supportsGetAppointments()) {
        cb && cb(ExchangeConnError.UNSUPPORTED_METHOD);
        return;
      }

      if (!validateReq(true, true, cb)) {
        return;
      }
      ExtensionSvc.getAppointments(_settings.encryptionKey, startDate, endDate, resCount, function (error, data) {
        var connError = getExchangeConnErr(error, data);
        if (!connError) {
          LogSvc.debug('[ExchangeConnSvc]: Get appointments was successful');
          cb && cb(null, data.calendarItems || []);
        } else {
          LogSvc.warn('[ExchangeConnSvc]: Get appointments failed - ', connError);
          handleConnError(connError);
          cb(connError);
        }
      });
    };

    this.syncAllPersonalContacts = function () {
      if (LocalStoreSvc.isCachingDisabled() || !validateReq(true, true)) {
        return;
      }
      if (_pendingSyncTimeout) {
        $timeout.cancel(_pendingSyncTimeout);
        _pendingSyncTimeout = null;
      }
      if (_syncState) {
        var now = Date.now();
        if (now - _lastPersonalContactSync > PERSONAL_CONTACTS_SYNC_MIN_INTERVAL) {
          ExtensionSvc.syncAllPersonalContacts(_settings.encryptionKey, _syncState);
          // Sync'ing is not done yet, but updating _lastPersonalContactSync now will avoid
          // multiple requests being processed at the same time
          _lastPersonalContactSync = now;
        } else {
          LogSvc.debug(
            '[ExchangeConnSvc]: syncAllPersonalContacts. Last sync was ' +
              new Date(_lastPersonalContactSync).toLocaleTimeString() +
              '. Next sync after ' +
              new Date(_lastPersonalContactSync + PERSONAL_CONTACTS_SYNC_MIN_INTERVAL).toLocaleTimeString()
          );
        }
      } else {
        // No sync state found, so we need to retrieve all contacts
        LogSvc.debug('[ExchangeConnSvc]: syncAllPersonalContacts. No sync state found, get all personal contacts');
        _self.getAllPersonalContacts();
      }
    };

    this.supportsOOO = ExtensionSvc.supportsOOO.bind(ExtensionSvc);

    this.getOooMsg = function (email) {
      return new $q(function (resolve, reject) {
        LogSvc.debug('[ExchangeConnSvc]: getOooMsg...');

        if (!ExtensionSvc.supportsOOO()) {
          LogSvc.warn('[ExchangeConnSvc]: OOS is not supported');
          reject(ExchangeConnError.UNSUPPORTED_METHOD);
          return;
        }

        if (!validateReq(true, true, reject)) {
          return;
        }

        ExtensionSvc.getOooMsg(_settings.encryptionKey, $rootScope.localUser.emailAddress, email, function (error, data) {
          var connError = getExchangeConnErr(error, data);
          if (connError) {
            reject(connError);
            return;
          }
          var oooMsg = Utils.sanitize(data.oooMsg) || '';
          oooMsg = Utils.linkifyContent(oooMsg);

          // Trim empty spaces and empty lines at beginning and end of message
          oooMsg = oooMsg
            .trim()
            .replace(/^(<br>)+/, '')
            .replace(/(<br>)+$/, '');

          resolve(oooMsg);
        });
      });
    };

    this.isOutOfOffice = function (email) {
      return _self.getOooMsg(email).then(function (oooMsg) {
        return !!oooMsg;
      });
    };

    this.supportsUserAvailability = ExtensionSvc.supportsUserAvailability.bind(ExtensionSvc);

    this.getUserAvailability = function (email) {
      return new $q(function (resolve, reject) {
        LogSvc.debug('[ExchangeConnSvc]: getUserAvailability...');
        try {
          if (!ExtensionSvc.supportsUserAvailability || !ExtensionSvc.supportsUserAvailability()) {
            LogSvc.warn('[ExchangeConnSvc]: UserAvailability is not supported');
            reject(ExchangeConnError.UNSUPPORTED_METHOD);
            return;
          }

          if (!validateReq(true, true, reject)) {
            return;
          }

          ExtensionSvc.getUserAvailability(_settings.encryptionKey, email, function (error, data) {
            var connError = getExchangeConnErr(error, data);
            if (connError) {
              reject(connError);
              return;
            }
            var busyCalendarEvents = [];
            var calendarEvent =
              data.userAvailability &&
              data.userAvailability.FreeBusyResponse &&
              data.userAvailability.FreeBusyResponse.FreeBusyView &&
              data.userAvailability.FreeBusyResponse.FreeBusyView.CalendarEventArray &&
              data.userAvailability.FreeBusyResponse.FreeBusyView.CalendarEventArray.CalendarEvent;

            if (calendarEvent) {
              var pushBusyCalendarEvent = function (event) {
                if (event && event.BusyType && event.BusyType.match(/busy/i)) {
                  busyCalendarEvents.push({
                    busyType: event.BusyType,
                    startTime: event.StartTime && new Date(event.StartTime).getTime(),
                    endTime: event.EndTime && new Date(event.EndTime).getTime()
                  });
                }
              };

              if (calendarEvent.BusyType) {
                pushBusyCalendarEvent(calendarEvent);
              } else {
                Object.keys(calendarEvent).forEach(function (key) {
                  pushBusyCalendarEvent(calendarEvent[key]);
                });
              }
            }
            LogSvc.debug('[ExchangeConnSvc]: Retrieved ' + busyCalendarEvents.length + ' busy calendar events');
            resolve(busyCalendarEvents);
          });
        } catch (ex) {
          LogSvc.error('[ExchangeConnSvc]: getUserAvailability exception. ', ex);
          reject(ExchangeConnError.INTERNAL_ERROR);
        }
      });
    };

    this.getErrorMessage = function (err) {
      if (!err) {
        return '';
      }
      switch (err) {
        case ExchangeConnError.COULD_NOT_CONNECT:
          return 'res_ExchangeConnError_CouldNotConnect';
        case ExchangeConnError.UNAUTHORIZED:
          return 'res_ExchangeConnError_Unauthorized';
        case ExchangeConnError.FAILED:
          return 'res_ExchangeConnError_Failed';
        case ExchangeConnError.NO_EXTENSION:
          return 'res_ExchangeConnError_NoExtension';
        case ExchangeConnError.UNAVAILABLE:
          return $rootScope.browser.chrome ? 'res_ExchangeConnError_NoConnector' : 'res_ExchangeConnError_NoExternalConnector';
        case ExchangeConnError.TIMEOUT:
          return $rootScope.browser.chrome ? 'res_ExchangeConnError_Timeout' : 'res_ExchangeConnError_ExternalTimeout';
        case ExchangeConnError.MAILBOX_NOT_FOUND:
          return 'res_ExchangeConnError_MailboxNotFound';
        case ExchangeConnError.NTLM_NOT_SUPPORTED:
          return 'res_ExchangeConnError_NtlmNotSupported';
      }
      return 'res_ExchangeConnError_Other';
    };

    this.isOffice365Supported = function () {
      return _office365Supported;
    };

    this.basicAuthAllowed = function () {
      return _basicAuthAllowed;
    };

    this.forceSSL = function () {
      return _forceSSL;
    };

    this.adalClearCache = function () {
      _authenticationContext && _authenticationContext.clearCache();
    };

    this.getAuthenticationContextConfig = function (encryptionKey) {
      if (!_authenticationContextConfig) {
        _authenticationContextConfig = {
          encryptionKey: encryptionKey || (_settings && _settings.encryptionKey),
          redirectUri: window.location.origin + '/dist/blank.html',
          instance: ExchangeOnlineApp.GLOBAL.instance,
          // The id of the Exchange Connector Authentication app on http://portal.azure.com for the tenant common (multi-tenant suppport)
          clientId: ExchangeOnlineApp.GLOBAL.clientId,
          extraQueryParameter: 'nux=1',
          logging: {
            level: 3, // level can be set -1,0,1,2 and 3 which turns on 'no log', 'error', 'warning', 'info' or 'verbose' level logging respectively
            log: LogSvc // object to provide the log to the library, it should implement functions error, warn, info and debug
          },
          popUp: true,
          expireOffsetSeconds: EXPIRE_OFFSET,
          cacheLocation: 'localstorage',
          openExternalPopup: null
        };
      }
      return _authenticationContextConfig;
    };

    this.createAuthenticationContext = function (encryptionKey) {
      _authenticationContext = new AuthenticationContext(this.getAuthenticationContextConfig(encryptionKey));
    };

    this.adalGetAuthorizationCode = function (data) {
      !_authenticationContext && this.createAuthenticationContext();

      _authenticationContext && _authenticationContext.setInstance(data.instance);
      _authenticationContext && _authenticationContext.setClientId(data.clientId);

      _getAuthorizationCodeDeferred = $q.defer();
      _authenticationContext.getAuthorizationCode(data.url);
      return _getAuthorizationCodeDeferred.promise;
    };

    this.adalLogin = function (resource, prompt) {
      _loginDeferred = $q.defer();

      if (_authenticationContext) {
        _authenticationContext.login(resource, null, prompt, true);
      } else {
        _loginDeferred.reject('Error on adal login');
      }
      return _loginDeferred.promise;
    };

    this.adalAcquireToken = function (resource) {
      // automated token request call
      var deferred = $q.defer();
      _authenticationContext._renewActive = true;
      _authenticationContext.acquireToken(resource, function (errorDesc, tokenOut, error) {
        _authenticationContext._renewActive = false;
        if (error) {
          $rootScope.$broadcast('adal:acquireTokenFailure', errorDesc, error);
          _authenticationContext.error('Error when acquiring token for resource: ' + resource, error);
          deferred.reject(errorDesc + '|' + error);
        } else {
          var token = {
            accessToken: tokenOut,
            expiry: _authenticationContext.getTokenExpiry(),
            expireOffsetSeconds: EXPIRE_OFFSET
          };
          $rootScope.$broadcast('adal:acquireTokenSuccess', token);
          LogSvc.debug('[ExchangeConnSvc]: Token: ', obfuscateToken(token.accessToken));
          deferred.resolve(token);
        }
      });

      return deferred.promise;
    };

    this.updateUserWithLocalContactName = updateUserWithLocalContactName;

    this.getExchangeOnlineApp = getExchangeOnlineApp;

    this.disableAutoConnect = function () {
      saveAutoConnect(false);
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////////////////
    resetPersonalContactsCache();

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.ExchangeConnError = ExchangeConnError;
  unifyPhone.Enums.ExchangeConnAuthentication = ExchangeAuthentication;
  unifyPhone.Enums.Office365Cloud = Office365Cloud;
  unifyPhone.Enums.ExchangeOnlineApp = ExchangeOnlineApp;
  unifyPhone.ExchangeConnSvcImpl = ExchangeConnSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var ExchangeConnError = unifyPhone.Enums.ExchangeConnError;

  var MailboxServices = {
    ExchangeOnlineSvc: {
      //permission: 'msExchangeEnabled',
      labelRes: 'res_MicrosoftExchangeTitle',
      searchRes: 'res_ExchangeContacts'
    },
    ExchangeOnPremiseSvc: {
      //     //permission: 'msExchangeEnabled',
      labelRes: 'res_MicrosoftExchangeTitle',
      searchRes: 'res_ExchangeContacts'
    },
    GoogleSvc: {
      //     //permission: 'msExchangeEnabled',
      labelRes: 'res_GoogleTitle',
      searchRes: 'res_GoogleContacts'
    }
  };

  ///////////////////////////////////////////////////////////////////////////////////////
  // MailboxConnSvcImpl Implementation
  // This service manage multiple mailboxes services that individually connect to
  // different mailbox providers
  // At this momment they are
  //    ExchangeOnlineSvc: connects to Microsoft o365 Server through GraphAPI
  //    ExchangeOnPremiseSvc: connects to Microsoft Exchange Server through EWS interface
  ///////////////////////////////////////////////////////////////////////////////////////

  // eslint-disable-next-line max-params, max-lines-per-function
  function MailboxConnSvcImpl($rootScope, $q, $injector, LogSvc, PubSubSvc) {
    // NOSONAR
    LogSvc.debug('New Service: MailboxConnSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _mailboxServices = [];

    var _searchId = 0;
    var _searchIds = {};
    var _exchangeOnlineConnected = false;
    var _exchangeOnPremiseConnected = false;
    var _googleConnected = false;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function getNextSearchId() {
      _searchId++;
      return _searchId;
    }

    function addService(serviceName) {
      var hadService = _mailboxServices.some(function (service) {
        return serviceName === service.name;
      });
      if (!hadService) {
        var serviceInstance = $injector.get(serviceName);
        if (!serviceInstance) {
          return false;
        }
        _mailboxServices.push(serviceInstance);
      }

      return !hadService;
    }

    function removeService(serviceName) {
      return _mailboxServices.some(function (service, idx) {
        if (serviceName === service.name) {
          _mailboxServices.splice(idx, 1);
          return true;
        }
        return false;
      });
    }

    function isServiceEnabled(serviceName) {
      var serviceData = MailboxServices[serviceName];
      return (
        (!serviceData.permission || $rootScope.localUser[serviceData.permission]) &&
        (!serviceData.labFeature || $rootScope.unifyPhoneLabs[serviceData.labFeature])
      );
    }

    function initConnectorServices() {
      if ($rootScope.localUser) {
        var changed = false;
        Object.keys(MailboxServices).forEach(function (serviceName) {
          changed = (isServiceEnabled(serviceName) ? addService : removeService)(serviceName) || changed;
        });
        if (changed) {
          var serviceNames = _mailboxServices.map(function (service) {
            return service.name;
          });
          LogSvc.debug('[MailboxConnSvc]: Publish /mailbox/connectorChanged event. Enabled connectors: ', serviceNames);
          PubSubSvc.publish('/mailbox/connectorChanged', []);
        }
      }
    }

    function publishConnectionStatus() {
      if (!_exchangeOnlineConnected && !_exchangeOnPremiseConnected && !_googleConnected) {
        LogSvc.debug('[MailboxConnSvc]: Publish /mailbox/disconnected event');
        PubSubSvc.publish('/mailbox/disconnected', []);
      } else {
        LogSvc.debug('[MailboxConnSvc]: Publish /mailbox/connected event');
        PubSubSvc.publish('/mailbox/connected', []);
      }
    }

    function getConnected(service) {
      if (!service) {
        return false;
      }
      return typeof service.getConnected === 'function' ? service.getConnected() : service.connected;
    }
    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc listeners
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/exchangeOnline/connected', function () {
      LogSvc.debug('[MailboxConnSvc]: Received /exchangeOnline/connected event');
      _exchangeOnlineConnected = true;
      publishConnectionStatus();
    });

    PubSubSvc.subscribe('/exchangeOnline/disconnected', function () {
      LogSvc.debug('[MailboxConnSvc]: Received /exchangeOnline/disconnected event');
      _exchangeOnlineConnected = false;
      publishConnectionStatus();
    });

    PubSubSvc.subscribe('/exchangeOnPremise/connected', function () {
      LogSvc.debug('[MailboxConnSvc]: Received /exchangeOnPremise/connected event');
      _exchangeOnPremiseConnected = true;
      publishConnectionStatus();
    });

    PubSubSvc.subscribe('/exchangeOnPremise/disconnected', function () {
      LogSvc.debug('[MailboxConnSvc]: Received /exchangeOnPremise/disconnected event');
      _exchangeOnPremiseConnected = false;
      publishConnectionStatus();
    });

    PubSubSvc.subscribe('/googleContacts/connected', function () {
      LogSvc.debug('[MailboxConnSvc]: Received /googleContacts/connected event');
      _googleConnected = true;
      publishConnectionStatus();
    });

    PubSubSvc.subscribe('/googleContacts/disconnected', function () {
      LogSvc.debug('[MailboxConnSvc]: Received /googleContacts/disconnected event');
      _googleConnected = false;
      publishConnectionStatus();
    });

    PubSubSvc.subscribe('/localUser/tenantSettingsUpdate', function () {
      LogSvc.debug('[MailboxConnSvc]: Received /localUser/tenantSettingsUpdate event');
      initConnectorServices();
    });

    PubSubSvc.subscribe('/localUser/init', function () {
      LogSvc.debug('[MailboxConnSvc]: Received /localUser/init event');
      initConnectorServices();
    });

    PubSubSvc.subscribe('/feature/state/changed', function () {
      LogSvc.debug('[MailboxConnSvc]: Received /feature/state/changed event');
      initConnectorServices();
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    // Define the read-only properties to access the internal variables
    Object.defineProperties(this, {
      connected: {
        get: function () {
          return _exchangeOnlineConnected || _exchangeOnPremiseConnected || _googleConnected;
        },
        enumerable: true,
        configurable: false
      }
    });

    this.getMeetingsNeedServiceText = function () {
      var getIntegrationLabel = function (idx) {
        var labelRes = MailboxServices[_mailboxServices[idx].name].labelRes;
        return $rootScope.i18n.map[labelRes];
      };

      if (_mailboxServices.length === 1) {
        return $rootScope.i18n.localize('res_MeetingsNeedOneMailboxTagline', [getIntegrationLabel(0)]);
      } else if (_mailboxServices.length > 1) {
        return $rootScope.i18n.localize('res_MeetingsNeedTwoMailboxTagline', [getIntegrationLabel(0), getIntegrationLabel(1)]);
      }
      return '';
    };

    this.getSearchResultsTitleRes = function () {
      var connectedServices = _mailboxServices.filter(function (service) {
        return getConnected(service);
      });
      if (connectedServices.length === 1) {
        return MailboxServices[connectedServices[0].name].searchRes;
      }
      return 'res_ExternalContacts';
    };

    this.searchContactsWithConstraints = function (searchStr, constraints, resCount, cb) {
      LogSvc.debug('[MailboxConnSvc] searchContactsWithConstraints', searchStr, constraints, resCount);
      var searchId = getNextSearchId();

      if (hasConnectorService(cb)) {
        var promises = [];
        _mailboxServices.forEach(function (service) {
          if (
            service.searchContactsWithConstraints &&
            (getConnected(service) || (constraints && constraints.phoneNumberLookup) || (constraints && constraints.reversePhoneNumberLookup))
          ) {
            promises.push(
              new $q(function (resolve) {
                var searchCallback = function (err, contacts) {
                  resolve({
                    err: err,
                    contacts: contacts,
                    serviceName: service.name
                  });
                };
                try {
                  var serviceSearchId = service.searchContactsWithConstraints(searchStr, constraints, resCount, searchCallback);
                  if (serviceSearchId) {
                    _searchIds[searchId] = _searchIds[searchId] || {};
                    _searchIds[searchId][service.name] = _searchIds[searchId][service.name] || {};
                    _searchIds[searchId][service.name] = serviceSearchId;
                  } else {
                    resolve({
                      err: ExchangeConnError.INTERNAL_ERROR
                    });
                  }
                } catch (ex) {
                  LogSvc.error('[MailboxConnSvc]: Search Contacts exception - ', [service.name, ex]);
                  resolve({
                    err: ExchangeConnError.INTERNAL_ERROR
                  });
                }
              })
            );
          }
        });
        if (promises.length > 0) {
          $q.all(promises)
            .then(function (results) {
              var allContacts = [];
              var allErrors = [];
              results.forEach(function (result) {
                if (result.err) {
                  LogSvc.error('[MailboxConnSvc]: Search Contacts failed - ', [result.err, result.serviceName]);
                  allErrors.push(result.err);
                }
                result.contacts && Array.prototype.push.apply(allContacts, result.contacts);
              });
              if (_searchIds[searchId]) {
                delete _searchIds[searchId];
              }

              if (allErrors.length > 0 && results.length === allErrors.length && allContacts.length === 0) {
                cb(allErrors[0]);
              } else {
                cb(null, allContacts);
              }
            })
            .catch(function () {
              cb(ExchangeConnError.INTERNAL_ERROR);
            });
        } else {
          cb(null, []);
        }
      }
      return searchId;
    };

    this.cancelSearchContacts = function (searchId) {
      var searchIds = _searchIds[searchId];
      searchIds &&
        _mailboxServices.forEach(function (service) {
          searchIds[service.name] && service.cancelSearchContacts && service.cancelSearchContacts(searchIds[service.name]);
        });
      delete _searchIds[searchId];
    };

    this.getContact = function (exchangeEmail, cb) {
      if (!hasConnectorService(cb)) {
        return;
      }
      var promises = [];
      _mailboxServices.forEach(function (service) {
        if (service.getContact && getConnected(service)) {
          promises.push(
            new $q(function (resolve) {
              service.getContact(exchangeEmail, function (err, contact) {
                resolve({
                  err: err,
                  contact: contact,
                  serviceName: service.name
                });
              });
            })
          );
        }
      });

      $q.all(promises)
        .then(function (results) {
          var allContacts = [];
          var allErrors = [];
          results.forEach(function (result) {
            if (result.err) {
              LogSvc.error('[MailboxConnSvc]: Get Contact failed - ', [result.err, result.serviceName]);
              allErrors.push(result.err);
            }
            result.contact && allContacts.push(result.contact);
          });

          if (allErrors.length > 0 && results.length === allErrors.length && allContacts.length === 0) {
            cb(allErrors[0]);
          } else {
            cb(null, allContacts[0]);
          }
        })
        .catch(function () {
          cb(ExchangeConnError.INTERNAL_ERROR);
        });
    };

    this.getAllPersonalContacts = function () {
      _mailboxServices.forEach(function (service) {
        service.getAllPersonalContacts && getConnected(service) && service.getAllPersonalContacts();
      });
    };

    this.getOooMsg = function (email) {
      return new $q(function (resolve, reject) {
        var promises = [];
        _mailboxServices.forEach(function (service) {
          if (service.getOooMsg && getConnected(service)) {
            promises.push(service.getOooMsg(email));
          }
        });

        $q.all(promises)
          .then(function (results) {
            var oooMsg = results.find(function (msg) {
              return msg;
            });
            resolve(oooMsg || '');
          })
          .catch(reject);
      });
    };

    this.isOutOfOffice = function (email) {
      return new $q(function (resolve, reject) {
        var promises = [];
        _mailboxServices.forEach(function (service) {
          if (service.isOutOfOffice && getConnected(service)) {
            promises.push(service.isOutOfOffice(email));
          }
        });

        $q.all(promises)
          .then(function (results) {
            var isOutOfOffice = results.some(function (ooo) {
              return ooo;
            });
            resolve(isOutOfOffice);
          })
          .catch(reject);
      });
    };

    this.supportsUserAvailability = function () {
      return _mailboxServices.some(function (service) {
        return service.supportsUserAvailability && service.supportsUserAvailability();
      });
    };

    this.getUserAvailability = function (email) {
      return new $q(function (resolve, reject) {
        var promises = [];
        _mailboxServices.forEach(function (service) {
          if (service.supportsUserAvailability() && getConnected(service)) {
            promises.push(service.getUserAvailability(email));
          }
        });

        $q.all(promises)
          .then(function (results) {
            var userAvailability = [];
            results.forEach(function (result) {
              Array.isArray(result) && Array.prototype.push.apply(userAvailability, result);
            });
            userAvailability.sort(function (userAvailabilibyA, userAvailabilityB) {
              return userAvailabilibyA.startTime - userAvailabilityB.startTime;
            });
            resolve(userAvailability);
          })
          .catch(function (err) {
            reject(err);
          });
      });
    };

    this.supportsOOO = function () {
      return _mailboxServices.some(function (service) {
        return service.supportsOOO && service.supportsOOO();
      });
    };

    this.supportsGetAppointments = function () {
      return _mailboxServices.some(function (service) {
        return service.supportsGetAppointments && service.supportsGetAppointments();
      });
    };

    function hasConnectorService(cb) {
      if (_mailboxServices.length === 0) {
        LogSvc.debug('[MailboxConnSvc]: No Connector service available');
        cb && cb(ExchangeConnError.INTERNAL_ERROR);
        return false;
      }
      return true;
    }

    this.getAppointments = function (startDate, endDate, resCount, cb) {
      if (!hasConnectorService(cb)) {
        return;
      }
      var promises = [];
      _mailboxServices.forEach(function (service) {
        if (service.getAppointments && getConnected(service) && service.supportsGetAppointments()) {
          promises.push(
            new $q(function (resolve) {
              service.getAppointments(startDate, endDate, resCount, function (err, appointments) {
                resolve({
                  serviceName: service.name,
                  err: err,
                  appointments: appointments
                });
              });
            })
          );
        }
      });

      $q.all(promises)
        .then(function (results) {
          var allAppointments = [];
          var allErrors = [];
          results.forEach(function (result) {
            if (result.err) {
              LogSvc.error('[MailboxConnSvc]: Get Contact failed - ', [result.err, result.serviceName]);
              allErrors.push(result.err);
            }
            result.appointments && Array.prototype.push.apply(allAppointments, result.appointments);
          });

          if (allErrors.length > 0 && results.length === allErrors.length && allAppointments.length === 0) {
            cb(allErrors[0]);
          } else {
            cb(null, allAppointments);
          }
        })
        .catch(function () {
          cb(ExchangeConnError.INTERNAL_ERROR);
        });
    };

    this.syncAllPersonalContacts = function () {
      _mailboxServices.forEach(function (service) {
        service.syncAllPersonalContacts && getConnected(service) && service.syncAllPersonalContacts();
      });
    };

    this.updateUserWithLocalContactName = function (user) {
      _mailboxServices.some(function (service) {
        return service.updateUserWithLocalContactName && service.updateUserWithLocalContactName(user);
      });
    };

    this.disableAutoConnect = function () {
      _mailboxServices.forEach(function (service) {
        service.disableAutoConnect && service.disableAutoConnect();
      });
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////////////////
    initConnectorServices();

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  unifyPhone.MailboxConnSvcImpl = MailboxConnSvcImpl;
  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global ControllerRouteError, RegistrationState*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var Constants = unifyPhone.Constants;
  var Conversation = unifyPhone.Conversation;
  var UserProfile = unifyPhone.UserProfile;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-params, max-lines-per-function
  function UserSvcImpl($rootScope, $timeout, $interval, $q, LocalStoreSvc, LogSvc, PubSubSvc, MailboxConnSvc) {
    // NOSONAR
    LogSvc.debug('New Service: UserSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Class
    ///////////////////////////////////////////////////////////////////////////////////////
    // eslint-disable-next-line max-lines-per-function
    function UserSearch() {
      // NOSONAR
      var _foundUsers = [];
      var _pendingReqs = 1; // Start in 1
      var _query = '';
      var _searchId = null;
      var _exchangeSearchId = null;
      var _exchangeSearchTimer = null;
      var _doneCb = null;
      var _timer = null;
      var _phoneCallSearch = false;

      function findMatch(newUser) {
        if (!newUser) {
          return -1;
        }

        var emailAddresses = [];
        if (newUser.emailAddress) {
          emailAddresses.push(newUser.emailAddress);
        }
        if (newUser.emailAddresses && newUser.emailAddresses.length) {
          Array.prototype.push.apply(
            emailAddresses,
            newUser.emailAddresses.map(function (e) {
              return e.address;
            })
          );
        }
        if (emailAddresses.length === 0) {
          return -1;
        }

        return _foundUsers.findIndex(function (u) {
          if ((newUser.isExchangeContact && u.isExchangeContact) || (!newUser.isExchangeContact && !u.isExchangeContact)) {
            // Same type
            return false;
          }
          return emailAddresses.some(function (address) {
            if (u.emailAddress && Utils.compareStrings(address, u.emailAddress)) {
              return true;
            }
            return (
              u.emailAddresses &&
              u.emailAddresses.some(function (email) {
                return Utils.compareStrings(address, email.address);
              })
            );
          });
        });
      }

      function updateUserIdx(idx, newUser) {
        var unifyPhoneUser = newUser.isExchangeContact ? _foundUsers[idx] : newUser;
        var contact = newUser.isExchangeContact ? newUser : _foundUsers[idx];
        if (!unifyPhoneUser || !contact) {
          return;
        }

        if (!unifyPhoneUser.extendedForPhoneCallSearch) {
          unifyPhoneUser = createUserForPhoneCallSearch(unifyPhoneUser);
        }

        var externallyManagedFields = unifyPhoneUser.externallyManagedFields || [];

        if (externallyManagedFields.indexOf('phoneNumbers') === -1) {
          LogSvc.debug('[UserSvc]: Add exchange contact numbers to user ', unifyPhoneUser.emailAddress);
          contact.phoneNumbers &&
            contact.phoneNumbers.forEach(function (newNumber) {
              var found = unifyPhoneUser.phoneNumbers.some(function (existingNumber) {
                return existingNumber.phoneNumber === newNumber.phoneNumber;
              });
              if (found) {
                LogSvc.debug('[UserSvc]: Number already exists = ', newNumber.phoneNumber);
              } else {
                LogSvc.debug('[UserSvc]: Adding new number = ', newNumber.phoneNumber);
                unifyPhoneUser.phoneNumbers.push(newNumber); // Number does not exist add it
              }
            });
          unifyPhoneUser.phoneNumbers.sort(comparePhoneType);
        }
        if (externallyManagedFields.indexOf('department') === -1) {
          unifyPhoneUser.department = unifyPhoneUser.department || contact.department;
        }

        // Make sure the UnifyPhone user object is in the search results
        _foundUsers[idx] = unifyPhoneUser;
      }

      return {
        get users() {
          return _foundUsers;
        },
        set users(users) {
          _foundUsers = users;
        },
        get pendingReqs() {
          return _pendingReqs;
        },
        set pendingReqs(pendingReqs) {
          _pendingReqs = pendingReqs;
        },
        get query() {
          return _query;
        },
        set query(query) {
          _query = query;
        },
        get searchId() {
          return _searchId;
        },
        set searchId(searchId) {
          _searchId = searchId;
        },
        get exchangeSearchId() {
          return _exchangeSearchId;
        },
        set exchangeSearchId(exchangeSearchId) {
          _exchangeSearchId = exchangeSearchId;
        },
        get exchangeSearchTimer() {
          return _exchangeSearchTimer;
        },
        set exchangeSearchTimer(timer) {
          _exchangeSearchTimer = timer;
        },
        get doneCb() {
          return _doneCb;
        },
        set doneCb(cb) {
          _doneCb = cb;
        },
        get timer() {
          return _timer;
        },
        set timer(timer) {
          _timer = timer;
        },
        get phoneCallSearch() {
          return _phoneCallSearch;
        },
        set phoneCallSearch(value) {
          _phoneCallSearch = !!value;
        },
        hasResults: function () {
          return !!_foundUsers.length;
        },
        clear: function () {
          Utils.emptyArray(_foundUsers);
          _pendingReqs = 1; // Reset to 1 for next search request
          _query = '';
          _searchId = null;
          _exchangeSearchId = null;
          _doneCb = null;
          if (_timer) {
            $timeout.cancel(_timer);
            _timer = null;
          }
          if (_exchangeSearchTimer) {
            $timeout.cancel(_exchangeSearchTimer);
            _exchangeSearchTimer = null;
          }
          _phoneCallSearch = false;
        },
        insertUser: function (user) {
          if (!user) {
            return;
          }
          if (_phoneCallSearch) {
            var idx = findMatch(user);
            if (idx !== -1) {
              updateUserIdx(idx, user);
              return;
            }
          }

          // Insert the user in the right display order:
          // 1. The results that match the firstname are listed first.
          // 2. For names that match the firstname, sort them by firstname.
          // 3. For names that match the lastname, sort them by lastname.
          if (!insertSortedUser(user, _query, _foundUsers)) {
            LogSvc.warn('[UserSvc]: Failed to insert user to the search results: ', user.userId);
          }
        },
        insertExchangeContact: function (contact) {
          if (!contact) {
            return;
          }
          contact.isExchangeContact = true;

          // Rename "number" field to "phoneNumber"
          contact.phoneNumbers.forEach(function (number) {
            number.phoneNumber = number.phoneNumber || number.number;
            delete number.number;
          });

          var idx = findMatch(contact);
          if (idx === -1) {
            // Add contact to search results
            contact.displayName = (contact.firstName + ' ' + contact.lastName).trim();
            contact.displayNameEscaped = Utils.textToHtmlEscaped(contact.displayName);
            contact.phoneNumbers.sort(comparePhoneType);
            insertSortedUser(contact, _query, _foundUsers);
          } else {
            updateUserIdx(idx, contact);
          }
        }
      };
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var MAX_RECENT_USERS = 16; // Max number of users stored in _recentUsers
    var MAX_PHONE_SEARCH_RESULTS = 30; // Max number of users returned for a phone call search

    var MAX_PRESENCE_SUBSCRIPTIONS = 70;
    var MAX_TEMPORARY_SUBSCRIPTIONS = 30;

    var MAX_GET_USERS_BY_IDS = 50; // At most 50 userIds in each get_users_by_ids request

    var ONE_MIN = 60 * 1000;
    var TEN_MIN = 10 * ONE_MIN;
    var ONE_HOUR = 60 * ONE_MIN;
    var ONE_DAY = 24 * ONE_HOUR;
    var USER_LOGIN_REFRESH_INTERVAL = 1 * ONE_MIN; // After login, wait 1 minute and then refresh oldest users
    var USER_ALL_REFRESH_INTERVAL = 10 * ONE_MIN; // Refresh recent users every 10 minutes
    var USER_REFRESH_MIN_INTERVAL = 6 * ONE_HOUR; // Only refresh users if they are older than 6 hours
    var USER_GET_BY_ID_REFRESH_INTERVAL = 0; // Refresh user if getUsersByIds is invoked more than X minutes after last refresh

    var SEARCH_START_DELAY = { reversePhoneNumberLookup: 0, email: 2000, char1: 2000, char2: 1000, other: 500 }; // Delay to being searching after user types letters
    var EXCHANGE_SEARCH_RESULT_LIMIT = 50;

    var MAX_CONVERSATION_PARTICIPANTS_FOR_LOCAL_SEARCH = 50; // Max number of conversation participant to force local search on
    var GET_PARTICIPANTS_TO_MENTION_DELAY = 300; // Delay before sending the query to the backend

    var _self = this;
    var _clientApiHandler = ClientApiHandler.getInstance();

    var _usersLoaded = false;
    var _users = []; // Contains list of UserProfile objects
    var _usersHashtable = {}; // Hashtable of user IDs to UserProfile objects
    var _recentUsers = [];
    var _localCall = null; // Reference to the local call

    var _permanentPresenceSubscriptions = []; // Subscribed user ids array for recent direct conversations
    var _temporaryPresenceSubscriptions = []; // Subscribed user ids array for temporary subscriptions
    var _userStatusSubscriptions = []; // Subscribed for status AVAILABLE user ids array (tell me when user is available)

    var _isMobileAsleep = false; // Indicates a mobile client in backgroup mode
    var _userRefreshLoginTimer; // Timer to wait after login to refresh users
    var _userRefreshIntervalTimer; // Interval timer to refresh cached users

    // Phone numbers display preference (Work, Mobile, Home, Fax, Other)
    var _phonePreferenceOrder = {
      UCAAS: 1,
      WORK: 2,
      MOBILE: 3,
      HOME: 4,
      FAX: 5
    };

    // getParticipantsToMention variables
    var _getParticipantsToMentionTimer = null;
    var _lastMentionQueryResult = {};
    var _discardParticipantsToMentionTimer = null;
    var _pendingMentionQuery = {};

    var _userSearch = new UserSearch(); // Search results for current user search

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function comparePhoneType(x, y) {
      return (_phonePreferenceOrder[x.type] || 99) - (_phonePreferenceOrder[y.type] || 99);
    }

    function createUserForPhoneCallSearch(user) {
      if (user && !user.extendedForPhoneCallSearch) {
        user = Object.create(user);
        user.extendedForPhoneCallSearch = true;
        user.phoneNumbers = user.phoneNumbers.slice(0);
        if (user.exchangeData && user.exchangeData.phoneNumbers && user.exchangeData.phoneNumbers.length > 0) {
          user.exchangeData.phoneNumbers.forEach(function (phoneNumber) {
            user.phoneNumbers.push({
              type: phoneNumber.type,
              phoneNumber: phoneNumber.number
            });
          });
        }
        user.phoneNumbers.sort(comparePhoneType);
      }
      return user;
    }

    function getBaseUser(user) {
      return user && user.isExtended ? Utils.getBaseObject(user, true) : user;
    }

    function canSaveToDB(/* user */) {
      // NGTC-1541: Disable saving user to localstore
      return false;
      //return !!user && !user.noDB && !user.noData && !user.isInactive && (user.userId !== $rootScope.localUser.userId);
    }

    function updateUserInDB(user) {
      if (canSaveToDB(user)) {
        // Update user data in indexedDB
        LocalStoreSvc.putUsers([getBaseUser(user)]);
      }
    }

    function saveUsersToDB(users) {
      if (users) {
        var toBeSaved = users.filter(canSaveToDB).map(getBaseUser);
        if (toBeSaved.length > 0) {
          // Update user data in indexedDB
          LocalStoreSvc.putUsers(toBeSaved).then(
            function () {
              LogSvc.debug('[UserSvc]: Users have been successfully updated in DB');
            },
            function (err) {
              LogSvc.warn('[UserSvc]: Failed to update users in DB. ', err);
            }
          );
        }
      }
    }

    function hasActiveSubscription(userId) {
      return userId === $rootScope.localUser.userId || _permanentPresenceSubscriptions.includes(userId) || _temporaryPresenceSubscriptions.includes(userId);
    }

    function updateUserPresenceState(presenceState) {
      var userId = presenceState.userId;
      // Only update the userPresenceState if we have an active presence subscription
      if (hasActiveSubscription(userId)) {
        var user = _usersHashtable[userId];
        if (user) {
          if (UserProfile.updateUserPresenceState(user, presenceState)) {
            LogSvc.debug('[UserSvc]: User presence has changed. Publish /user/update event. userId =', user.userId);
            PubSubSvc.publish('/user/update', [user]);
          }
          return user.userPresenceState;
        }
      }
      return UserProfile.normalizeUserPresenceState(userId, presenceState);
    }

    function getPresenceFromCachedUsers(userIds) {
      var usersPresence = [];
      userIds.forEach(function (userId) {
        if (userId === $rootScope.localUser.userId) {
          usersPresence.push($rootScope.localUser.userPresenceState);
        } else if (_usersHashtable[userId]) {
          usersPresence.push(_usersHashtable[userId].userPresenceState);
        }
      });
      return usersPresence;
    }

    /**
     * @param {String[]} userIds  The userIds for which we want to retrieve the presence.
     * @param {Boolean forceUpdateSubscribed} Force-retrieve the presence from the server for the given userIds, regardless If I am subscribed or not.
     * By default server data are retrieved only for users I haven't subscribed yet. For already subscribed ones by default cached presence data are used.
     * @param {cb} the callback to call
     */
    function getUsersPresence(userIds, forceUpdateSubscribed, cb) {
      userIds = userIds || [];
      // If forceUpdateSubscribed then we need to getPresence for already subscribed as well, eg in case we just subscribed but don't have full presence
      var unsubscribedUserIds = forceUpdateSubscribed
        ? userIds
        : userIds.filter(function (userId) {
            return !hasActiveSubscription(userId);
          });
      if (unsubscribedUserIds.length > 0) {
        _clientApiHandler.getPresence(unsubscribedUserIds, true, function (err, userPresenceStates) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.error('[UserSvc]: Error getting users presence: ', err);
              cb && cb(err);
              return;
            }
            userPresenceStates = userPresenceStates || [];
            userPresenceStates = userPresenceStates.map(updateUserPresenceState);

            if (forceUpdateSubscribed) {
              cb && cb(null, userPresenceStates);
            } else {
              var subscribedUserIds = userIds.filter(function (userId) {
                return unsubscribedUserIds.indexOf(userId) === -1;
              });
              cb && cb(null, getPresenceFromCachedUsers(subscribedUserIds).concat(userPresenceStates));
            }
          });
        });
      } else {
        cb && cb(null, getPresenceFromCachedUsers(userIds));
      }
    }

    function unsubscribeFromUsersPresence(otherList, userIds) {
      // Unubscribe from users' presence change events
      if (!userIds || userIds.length === 0) {
        return;
      }

      // Make sure the user is not in the other list
      var removeIds = userIds.filter(function (userId) {
        return !otherList.includes(userId);
      });

      // Remove userPresenceState from cached user objects
      removeIds.forEach(function (userId) {
        var user = _usersHashtable[userId];
        if (user) {
          UserProfile.clearUserPresenceState(user);
          LogSvc.debug('[UserSvc]: Publish /user/update event. userId =', userId);
          PubSubSvc.publish('/user/update', [user]);
        }
      });

      // Now invoke the unsubscribe presence API
      _clientApiHandler.unsubscribePresence(removeIds, function (err) {
        if (err) {
          LogSvc.error('[UserSvc]: Error unsubscribing to users presence: ', err);
        }
      });
    }

    function sendSubscribePresence(userIds, cb) {
      if (!userIds || !userIds.length) {
        cb && cb(null);
        return;
      }
      _clientApiHandler.subscribePresence(userIds, function (err, presenceStates) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.error('[UserSvc]: Error subscribing to users presence: ', err);
            cb && cb(err);
            return;
          }
          presenceStates && presenceStates.forEach(updateUserPresenceState);
          cb && cb(null);
        });
      });
    }

    function subscribeToUsersPresence(isPermanent, userIds, prioritized, cb) {
      cb = cb || function () {};

      // Remove duplicates, local user and non-cached users
      userIds = userIds.filter(function (userId, pos, arr) {
        // Filter out duplicates
        var isFirstOccurence = arr.indexOf(userId) === pos;
        return isFirstOccurence && $rootScope.localUser.userId !== userId && _usersHashtable[userId];
      });

      LogSvc.debug('[UserSvc]: Subscribe for temporary presence subscriptions. Users = ', userIds);

      if (userIds.length === 0) {
        cb(null);
        return;
      }

      var subscriptionList = isPermanent ? _permanentPresenceSubscriptions : _temporaryPresenceSubscriptions;
      var otherList = isPermanent ? _temporaryPresenceSubscriptions : _permanentPresenceSubscriptions;
      var maxSubscriptions = isPermanent ? MAX_PRESENCE_SUBSCRIPTIONS : MAX_TEMPORARY_SUBSCRIPTIONS;

      if (!prioritized && subscriptionList.length >= maxSubscriptions) {
        LogSvc.debug('[UserSvc]: Subscription list is already full. Only prioritized subscriptions can be processed.');
        cb('Subscription limit reached');
        return;
      }

      // Keep only max subscriptions
      if (userIds.length > maxSubscriptions) {
        LogSvc.info('[UserSvc]: Cannot subscribe to all users. Max number of subscriptions is ', maxSubscriptions);
        userIds.splice(maxSubscriptions);
      }

      var newIds = userIds.filter(function (userId) {
        var idx = subscriptionList.indexOf(userId);
        if (idx !== -1) {
          // User is already in the list
          if (prioritized) {
            // Remove user from list. It will be added again to the front of the list later.
            subscriptionList.splice(idx, 1);
          }
          return false;
        }
        // Check if user is in the other subscription list
        return !otherList.includes(userId);
      });

      if (prioritized) {
        // Add prioritized user IDs to the front
        Array.prototype.unshift.apply(subscriptionList, userIds);
        // Check if we are going over the limit and if so, unsubscribe from some users
        if (subscriptionList.length > maxSubscriptions) {
          // Remove the rest and unsubscribe
          var goners = subscriptionList.splice(maxSubscriptions);
          unsubscribeFromUsersPresence(otherList, goners);
        }
      }

      if (newIds.length === 0) {
        // There are no new subscriptions
        LogSvc.debug('[UserSvc]: We are already subscribed to the requested users');
        cb(null);
      }

      if (!prioritized) {
        var maxAllowed = maxSubscriptions - subscriptionList.length;
        if (newIds.length > maxAllowed) {
          // We are asking for more than we can accept. Trim the list.
          LogSvc.debug('[UserSvc]: Cannot subscribe to all users. Remaining subscriptions available is ', maxAllowed);
          newIds.splice(maxAllowed);
        }
        // Add new IDs to the back
        Array.prototype.push.apply(subscriptionList, newIds);
      }

      sendSubscribePresence(newIds, cb);
    }

    function subscribePermanentPresence(userIds, prioritized) {
      if (!userIds) {
        return;
      }
      if (!Array.isArray(userIds)) {
        userIds = [userIds];
      }
      if (userIds.length === 0) {
        return;
      }
      LogSvc.debug('[UserSvc]: Subscribe for permanent presence subscriptions. Users = ', userIds);
      subscribeToUsersPresence(true, userIds, prioritized);
    }

    function subscribeTemporaryPresenceAndNotify(userIds, cb) {
      if (!userIds) {
        cb && cb(null);
        return;
      }
      if (!Array.isArray(userIds)) {
        userIds = [userIds];
      }
      if (userIds.length === 0) {
        cb && cb(null);
        return;
      }
      subscribeToUsersPresence(false, userIds, true, cb);
    }

    // TODO: Remove updatePresence parameter when presence subscriptions are implemented
    function addUsersToCache(users, cacheOnly, retrieveIncomplete, updatePresence) {
      if (!users) {
        return;
      }
      if (!Array.isArray(users)) {
        users = [users];
      }

      // Add users to cached array and hashtable for lookup.
      var toBeSaved = [];
      var incompleteUsers = [];
      var initializedUsers = [];

      users.forEach(function (user) {
        if (!user || !user.userId || user.userId === $rootScope.localUser.userId) {
          return;
        }

        if (retrieveIncomplete && user.noData && !user.reqPending) {
          // We still haven't retrieved the user data for this user
          incompleteUsers.push(user);
        }

        var cachedUser = _usersHashtable[user.userId];
        if (cachedUser) {
          if (cachedUser !== user && !user.noData) {
            if (cachedUser.noData) {
              cachedUser.noData = false;
              initializedUsers.push(user.userId);
            }
            var userPresenceState = user.userPresenceState;
            UserProfile.update(cachedUser, getBaseUser(user));
            updatePresence && UserProfile.updateUserPresenceState(cachedUser, userPresenceState);
            if (!cachedUser.noDB) {
              toBeSaved.push(cachedUser);
            }
          }
          if (!cacheOnly && cachedUser.noDB) {
            // User exists in cache but not saved in DB
            cachedUser.noDB = false;
            toBeSaved.push(cachedUser);
          }
        } else {
          if (!user.isExtended) {
            user = UserProfile.extend(user);
          }
          if (!cacheOnly) {
            toBeSaved.push(user);
          } else {
            // Mark the user to indicate that it should not be saved to DB
            user.noDB = true;
          }
          _users.push(user);
          _usersHashtable[user.userId] = user;
        }
      });

      if (toBeSaved.length > 0) {
        saveUsersToDB(toBeSaved);
      }
      if (incompleteUsers.length > 0) {
        refreshUsers(incompleteUsers);
      }
      if (initializedUsers.length > 0) {
        LogSvc.debug('[UserSvc]: Publish /users/initialized event.', initializedUsers);
        PubSubSvc.publish('/users/initialized', [initializedUsers]);
      }
    }
    // Set the Conversation.addUsersToCache static function
    Conversation.addUsersToCache = addUsersToCache.bind(this);

    function checkForUnknownUsers(userIds) {
      var unknown = $rootScope.i18n.map.res_Unknown;

      var unknownUserIds = userIds.filter(function (id) {
        var cachedUser = _usersHashtable[id];
        if (cachedUser && cachedUser.noData) {
          // User is cached, but has no data.
          if (cachedUser.displayName === '') {
            // Set user name to 'Unknown'.
            LogSvc.debug('[UserSvc]: Set user as "Unknown". userId =', cachedUser.userId);
            cachedUser.displayName = unknown;
            cachedUser.displayNameEscaped = unknown;
            cachedUser.firstName = unknown;

            LogSvc.debug('[UserSvc]: Publish /user/update event. userId =', cachedUser.userId);
            PubSubSvc.publish('/user/update', [cachedUser]);
          }
          return true;
        }
        return false;
      });

      if (unknownUserIds.length > 0) {
        LogSvc.warn('[UserSvc]: Could not get info for these user Ids: ', unknownUserIds);
      }
    }

    function checkForPurgedUsers(users) {
      // Set the name for purged users
      var purged = $rootScope.i18n.map.res_Purged;
      return users.filter(function (u) {
        if (u.userState === Constants.UserState.PURGED) {
          u.displayName = purged;
          u.firstName = purged;
          u.lastName = '';
          return true;
        }
        return false;
      });
    }

    function getUsersByIds(userIds, throttle, cb) {
      var reqUserIds, pendingUserIds;
      if (throttle) {
        reqUserIds = userIds.slice(0, MAX_GET_USERS_BY_IDS);
        pendingUserIds = userIds.slice(MAX_GET_USERS_BY_IDS);
      } else {
        reqUserIds = userIds;
        pendingUserIds = [];
      }

      setReqPending(reqUserIds);
      _clientApiHandler.getUsersByIds(reqUserIds, function (err, users) {
        $rootScope.$apply(function () {
          clearReqPending(reqUserIds);
          if (err) {
            LogSvc.warn('[UserSvc]: Failed to get users. ', err);
          }
          users = users || [];
          var summary = users.map(function (user) {
            return {
              userId: user.userId,
              displayName: user.displayName,
              userType: user.userType,
              userState: user.userState
            };
          });
          LogSvc.debug('[UserSvc]: Retrieved ' + summary.length + ' users - ', summary);

          checkForPurgedUsers(users);

          var now = Date.now();
          users.forEach(function (user) {
            user.updatedTimeStamp = now;
          });

          cb && cb(users, pendingUserIds.length === 0);

          if (pendingUserIds.length > 0) {
            LogSvc.debug('[UserSvc]: Get remaining pending users');
            getUsersByIds(pendingUserIds, true, cb);
          }
        });
      });
    }

    function syncExchangePersonalContacts() {
      if (_localCall && _localCall.isPresent()) {
        // Skip sync to reduce freezes seen with SOAP requests while on a call
        LogSvc.info('[UserSvc]: User is in local call. Skip sync of personal contacts.');
        return;
      }
      MailboxConnSvc.syncAllPersonalContacts && MailboxConnSvc.syncAllPersonalContacts();
    }

    function refreshOutdatedUsers() {
      var outdatedTimestamp = Date.now() - USER_REFRESH_MIN_INTERVAL;

      var outdatedUsers = _users
        .filter(function (user) {
          return !user.noData && !user.refreshPending && (!user.updatedTimeStamp || user.updatedTimeStamp < outdatedTimestamp);
        })
        .sort(function (a, b) {
          return (a.updatedTimeStamp || 0) - (b.updatedTimeStamp || 0);
        });

      if (!outdatedUsers.length) {
        LogSvc.debug('[UserSvc]: There are no outdated users that need to be refreshed');
        return;
      }

      LogSvc.info('[UserSvc]: Found ' + outdatedUsers.length + ' outdated users. Refresh oldest ones.');

      // Only refresh up to MAX_GET_USERS_BY_IDS users
      refreshUsers(outdatedUsers.slice(0, MAX_GET_USERS_BY_IDS));
    }

    function startUserRefreshTimers() {
      clearUserRefreshTimers();

      if (_isMobileAsleep) {
        // Do not start timers when mobile is sleeping
        return;
      }

      // At login only request non-cached users from server then wait some time and refresh
      // the data for the recent users then periodic requests occur at longer intervals.
      _userRefreshLoginTimer = $timeout(function () {
        LogSvc.debug('[UserSvc]: Initial user refresh after timer expiry');
        _userRefreshLoginTimer = null;
        refreshOutdatedUsers();

        // Start a interval to refresh users periodically
        _userRefreshIntervalTimer = $interval(function () {
          LogSvc.debug('[UserSvc]: Subsequent user refresh after timer expiry');
          refreshOutdatedUsers();
          syncExchangePersonalContacts();
        }, USER_ALL_REFRESH_INTERVAL);
      }, USER_LOGIN_REFRESH_INTERVAL);
    }

    function clearUserRefreshTimers() {
      if (_userRefreshLoginTimer) {
        $timeout.cancel(_userRefreshLoginTimer);
        _userRefreshLoginTimer = null;
      }
      if (_userRefreshIntervalTimer) {
        $interval.cancel(_userRefreshIntervalTimer);
        _userRefreshIntervalTimer = null;
      }
    }

    function refreshUsers(usersToRefresh) {
      if (!usersToRefresh || !usersToRefresh.length) {
        return;
      }

      // Remove local user and users for which we have pending requests
      usersToRefresh = usersToRefresh.filter(function (u) {
        if (!u.userId || u.userId === $rootScope.localUser.userId || u.reqPending) {
          return false;
        }
        return true;
      });

      if (usersToRefresh.length === 0) {
        return;
      }

      var reqUserIds = usersToRefresh.map(function (u) {
        return u.userId;
      });

      LogSvc.info('[UserSvc]: Retrieve latest user data.');
      getUsersByIds(reqUserIds, true, function (users, isComplete) {
        if (users && users.length > 0) {
          LogSvc.info('[UserSvc]: Retrieved latest user data. Update local store and cache.');

          // Array of users that needs to be saved to DB
          var toBeSaved = [];

          // Array of users that have been initialized. Contains the user IDs.
          var initializedUsers = [];

          users.forEach(function (user) {
            var cachedUser = _usersHashtable[user.userId];
            if (!cachedUser) {
              // We should never get here, but shit happens. Add user to cache-only.
              LogSvc.warn('[UserSvc]: Received getUserById response for user not in cache?!? userId = ', user.userId);

              user = UserProfile.extend(user);
              user.noDB = true;
              _users.push(user);
              _usersHashtable[user.userId] = user;

              LogSvc.debug('[UserSvc]: Publish /user/update event. userId = ', user.userId);
              PubSubSvc.publish('/user/update', [user]);
            } else {
              if (cachedUser.noData) {
                cachedUser.noData = false;
                initializedUsers.push(user.userId);
              }

              UserProfile.update(cachedUser, user);

              if (!cachedUser.noDB) {
                toBeSaved.push(cachedUser);
              }

              LogSvc.debug('[UserSvc]: Publish /user/update event. userId =', cachedUser.userId);
              PubSubSvc.publish('/user/update', [cachedUser]);
            }
          });

          if (initializedUsers.length > 0) {
            LogSvc.debug('[UserSvc]: Publish /users/initialized event.', initializedUsers);
            PubSubSvc.publish('/users/initialized', [initializedUsers]);
          }

          if (toBeSaved.length > 0) {
            saveUsersToDB(toBeSaved);
          }

          recalculateRecentUsers();
        }

        if (isComplete) {
          checkForUnknownUsers(reqUserIds);
        }
      });
    }

    function addUsersAndReturnCached(users, updatePresence) {
      var cachedUsers = [];
      if (users && users.length) {
        addUsersToCache(users, true, false, updatePresence);
        users.forEach(function (user) {
          if (user.userId !== $rootScope.localUser.userId) {
            cachedUsers.push(_usersHashtable[user.userId]);
          }
        });
      }
      return cachedUsers;
    }

    function addUserAndReturnCached(user) {
      if (user) {
        addUsersToCache([user], true, false);
        return _usersHashtable[user.userId];
      }
      return null;
    }

    function setReqPending(userIds) {
      userIds &&
        userIds.forEach(function (userId) {
          var user = _usersHashtable[userId];
          if (user) {
            user.reqPending = true;
          }
        });
    }

    function clearReqPending(userIds) {
      userIds &&
        userIds.forEach(function (userId) {
          var user = _usersHashtable[userId];
          if (user) {
            delete user.reqPending;
          }
        });
    }

    function isValidRecentUser(u) {
      return !!u && !u.noDB && !u.noData && !u.hasSpecialRole && !u.isDeleted;
    }

    function recalculateRecentUsers() {
      // Get users from cache that are 'indexedDB' users and have a lastActivity timestamp, then sort them.
      _recentUsers = _users
        .filter(function (u) {
          return u.lastActivity && isValidRecentUser(u);
        })
        .sort(function (a, b) {
          return b.lastActivity - a.lastActivity;
        })
        .slice(0, MAX_RECENT_USERS);
    }

    function getUserFromCache(userId) {
      return userId === $rootScope.localUser.userId ? $rootScope.localUser : _usersHashtable[userId];
    }
    // Set the Conversation.getUserFromCache static function
    Conversation.getUserFromCache = getUserFromCache.bind(this);

    function getExtendedUser(userId) {
      if (!$rootScope.localUser) {
        return null;
      }

      var extUser = getUserFromCache(userId);
      if (!extUser) {
        extUser = UserProfile.extend({ userId: userId });
        extUser.noData = true;
        // Add new user object to cache
        addUsersToCache(extUser, true, false);
      }
      return extUser;
    }
    // Set the Conversation.getExtendedUser static function
    Conversation.getExtendedUser = getExtendedUser.bind(this);

    function getUserByEmail(emailAddress, cb) {
      if ($rootScope.localUser && emailAddress === $rootScope.localUser.emailAddress) {
        cb && cb(null, $rootScope.localUser);
        return;
      }

      _clientApiHandler.getUserByEmail(emailAddress, function (err, user) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.info('[UserSvc]: Get user by email failed. ', err);
            cb && cb(err);
          } else {
            // Add user to cache (or update if already in cache)
            var cachedUser = addUserAndReturnCached(user);
            cb && cb(null, cachedUser);
          }
        });
      });
    }
    /**
     *  Retrieves users with email addresses
     *
     *  @param {Object} filter
     *  @param {string} filter.emailAddresses - Email addresses to search for
     *  @param {boolean} filter.xmppLookup - Flag indicating if XMPP users should be searched for.
     *  @param {Function} cb Callback returning error and user parameters
     */
    function getUsersByEmails(filter, cb) {
      cb = cb || function () {};
      _clientApiHandler.getUsersByEmails(filter, function (err, users) {
        $rootScope.$apply(function () {
          if (err && err !== Constants.ReturnCode.NO_RESULT) {
            LogSvc.info('[UserSvc]: Get users by emails failed. ', err);
            cb(err);
          } else {
            var cachedUsers = addUsersAndReturnCached(users);
            cb(null, cachedUsers);
          }
        });
      });
    }

    // Comparator to sort users by the following rules:
    // 1. The results that match the firstname are listed first.
    // 2. For names that match the firstname, sort them by firstname.
    // 3. For names that match the lastname, sort them by lastname.
    function compareSortUsers(a, b, searchString) {
      if (!searchString) {
        return a.displayName < b.displayName ? -1 : 1;
      }
      if (!a.firstName && !b.firstName) {
        return a.displayName < b.displayName ? -1 : 1;
      }
      if (!a.firstName || !b.firstName) {
        return a.firstName ? -1 : 1;
      }

      searchString = new RegExp('^' + RegExp.escape(searchString), 'i');

      // Result is in first name only
      if (a.firstName.search(searchString) > -1 && b.firstName.search(searchString) === -1) {
        return -1;
      }
      // Result is in last name only
      if (a.firstName.search(searchString) === -1 && b.firstName.search(searchString) > -1) {
        return 1;
      }
      // Both results are in first name
      if (a.firstName.search(searchString) > -1 && b.firstName.search(searchString) > -1) {
        if (a.firstName === b.firstName) {
          return a.lastName < b.lastName ? -1 : 1;
        }
        return a.firstName < b.firstName ? -1 : 1;
      }
      // Both results are in last name
      if (a.lastName.search(searchString) > -1 && b.lastName.search(searchString) > -1) {
        if (a.lastName === b.lastName) {
          return a.firstName < b.firstName ? -1 : 1;
        }
        return a.lastName < b.lastName ? -1 : 1;
      }
      return 0;
    }

    // Helper method to sort user arrays
    function sortUsers(searchString) {
      return function (a, b) {
        return compareSortUsers(a, b, searchString);
      };
    }

    // Insert users based on the compareSortUsers rules
    function insertSortedUser(newUser, searchString, users) {
      var minIndex = 0;
      var maxIndex = users.length - 1;
      var currentIndex;
      var currentElement;
      var found = false;

      while (minIndex <= maxIndex) {
        currentIndex = Math.floor((minIndex + maxIndex) / 2);
        currentElement = users[currentIndex];

        var res = compareSortUsers(currentElement, newUser, searchString);

        if (res < 0) {
          minIndex = currentIndex + 1;
        } else if (res > 0) {
          maxIndex = currentIndex - 1;
        } else {
          found = true;
          break;
        }
      }

      var indexToInsert = found ? currentIndex + 1 : minIndex;
      if (indexToInsert >= 0) {
        users.splice(indexToInsert, 0, newUser);
        return true;
      }
      return false;
    }

    /**
     * Searches for users on the server. Matching first and last names but only from the beginning.
     *
     * @param {Object} constraints The search constraints.
     * @param {callback} doneCb The callback when search is finished.
     */
    function searchByName(constraints, doneCb) {
      var query = constraints && constraints.query && constraints.query.trim();
      if (!query) {
        doneCb && doneCb();
        return;
      }

      _userSearch.query = query;
      _userSearch.doneCb = doneCb;
      _userSearch.includeLocalUser = constraints.includeLocalUser;

      // LOCAL SEARCH DISABLED BECAUSE OF SLUGGISH UI
      // NEEDS UPDATE TO BE ASYNCHRONOUS
      // Get local matching users first
      // var isSupportClient = $rootScope.localUser.hasSupportRole;
      // var filteredUsers = userNameFilter(_users, _userSearch.query, false);
      // filteredUsers = filterUsers(filteredUsers, isSupportClient);
      // _userSearch.users.push.apply(_userSearch.users, filteredUsers);

      // Start server search after some delay (first char 1s, 2nd char 500ms, starting from 3rd 300ms)
      var delay = SEARCH_START_DELAY.other; // three or more chars (except email)
      if (constraints.reversePhoneNumberLookup) {
        delay = SEARCH_START_DELAY.reverseLookup;
      } else if (query.length === 1) {
        delay = SEARCH_START_DELAY.char1; // first char
      } else if (query.length === 2) {
        delay = SEARCH_START_DELAY.char2; // 2nd char
      } else if (query.match(Utils.EMAIL_ADDRESS_PATTERN)) {
        delay = SEARCH_START_DELAY.email;
      }

      function userSearchCb(err, searchId) {
        if (_userSearch.timer) {
          // There is another pending search
          LogSvc.debug('[UserSvc]: There is another pending search. Cancel the previous search.');
          _clientApiHandler.cancelSearch(searchId);
          return;
        }
        if (err) {
          LogSvc.warn('[UserSvc]: Start user search failed. ', err);
          doneCb &&
            $rootScope.$apply(function () {
              doneCb();
            });
        } else {
          // save searchId and may be used to cancel this search
          _userSearch.searchId = searchId;
        }
      }

      _userSearch.timer = $timeout(function () {
        _userSearch.timer = null;
        if (query && query.match(Utils.EMAIL_ADDRESS_PATTERN)) {
          getUserByEmail(query, function (err, user) {
            var emailUser;
            if (user && (user.userId !== $rootScope.localUser.userId || _userSearch.includeLocalUser)) {
              emailUser = user;
              _userSearch.insertUser(user); // Insert found users in order
            }
            doneCb && doneCb(err, emailUser); // Callback with emailUser object for UI
          });
        } else if (constraints.searchContext) {
          _clientApiHandler.startAdvancedUserSearch(constraints, userSearchCb);
        } else {
          _clientApiHandler.startUserSearch(constraints, userSearchCb);
        }
      }, delay);
    }

    function searchExchangeByName(constraints, cb) {
      if (!constraints || !constraints.query) {
        cb && cb();
        return;
      }
      var query = constraints.query;

      var delay = SEARCH_START_DELAY.other; // three or more chars
      if (constraints.reversePhoneNumberLookup) {
        delay = SEARCH_START_DELAY.reverseLookup;
      } else if (query.length === 1) {
        delay = SEARCH_START_DELAY.char1; // first char
      } else if (query.length === 2) {
        delay = SEARCH_START_DELAY.char2; // 2nd char
      }
      if (_userSearch.exchangeSearchTimer) {
        $timeout.cancel(_userSearch.exchangeSearchTimer);
      }
      _userSearch.exchangeSearchTimer = $timeout(function () {
        if (typeof MailboxConnSvc.searchContactsWithConstraints === 'function') {
          _userSearch.exchangeSearchId = MailboxConnSvc.searchContactsWithConstraints(query, constraints, EXCHANGE_SEARCH_RESULT_LIMIT, cb);
        } else {
          // Backwards compatibility for clients that don't support the new API
          _userSearch.exchangeSearchId = MailboxConnSvc.searchContacts(query, EXCHANGE_SEARCH_RESULT_LIMIT, cb);
        }
      }, delay);
    }

    function clearUserSearch() {
      var searchId = _userSearch.searchId;
      if (searchId) {
        _clientApiHandler.cancelSearch(searchId, function (err) {
          if (err) {
            LogSvc.warn('[UserSvc]: Failed to cancel server search. ', err);
          } else {
            LogSvc.debug('[UserSvc]: Canceled server search for searchId =  ', searchId);
          }
        });
      }
      if (_userSearch.exchangeSearchId) {
        MailboxConnSvc.cancelSearchContacts(_userSearch.exchangeSearchId);
      }
      _userSearch.doneCb && _userSearch.doneCb({ cleared: true });
      _userSearch.clear();
    }

    /**
     * Filter the users based on support user, tenantId, and active
     * LocalUser will be excluded from the list.
     * The passed in users may be the result returned from the server and will be extended.
     *
     * @param {Array} users An array of users to be filtered
     * @param {Boolean} includeSupportUser Include support users or not
     * @param {Boolean} includeLocalUser Include local user or not
     * @returns {Array} The filtered users
     */
    function filterUsers(users, includeSupportUser, includeLocalUser) {
      var filteredUsers = [];
      if (users.length > 0) {
        filteredUsers = users
          .filter(function (user) {
            // Don't return local user and special users
            if (user.userId === $rootScope.localUser.userId && !includeLocalUser) {
              return false;
            }
            return (
              ((user.hasSupportRole && includeSupportUser) || !user.hasSpecialRole) &&
              user.userState !== Constants.UserState.DELETED &&
              user.userState !== Constants.UserState.INITIAL
            );
          })
          .map(function (user) {
            // Use cached UserProfile object if available
            return _usersHashtable[user.userId] || UserProfile.extend(user);
          });
      }
      return filteredUsers;
    }

    /**
     * A helper filter to return the matched names from a list of names. The match in names
     * only starts from beginning of user's firstName or lastName. The filter returns users
     * sorted by compareSortUsers rules.
     *
     * @param {Array} users An array of users to be filtered.
     * @param {String} query The string to be matched.
     * @param {Boolean} caseSensitive Match is case sensitive or not.
     * @returns {Array} Array of users after filtering.
     */
    function userNameFilter(users, query, caseSensitive) {
      if (!users) {
        return null;
      }
      if (query) {
        query = query.toString();
        var sortUsersByQuery = sortUsers(query);
        var flag = caseSensitive ? 'g' : 'gi';
        return users
          .filter(function (contact) {
            return contact && Utils.matchNames(contact.displayName, query, flag);
          })
          .sort(sortUsersByQuery);
      }
      // no filtering
      return users;
    }

    function clearUserSvcCache() {
      Utils.emptyArray(_users);
      _usersHashtable = {};
      Utils.emptyArray(_recentUsers);
      Utils.emptyArray(_permanentPresenceSubscriptions);
      _userSearch.clear();
    }

    function decrementPendingSearchReqs() {
      _userSearch.pendingReqs--;

      // Do user search callback only when all users have been retrieved
      if (_userSearch.pendingReqs <= 0) {
        _userSearch.doneCb && _userSearch.doneCb();
        _userSearch.searchId = null;
        _userSearch.doneCb = null;
      } else {
        LogSvc.debug('[UserSvc]: Server search waiting on ' + _userSearch.pendingReqs + ' more request(s)');
      }
    }

    function normalizeUserContacts(searchConstraints) {
      searchConstraints = searchConstraints || {};

      var count = 0;
      var localUsers = searchConstraints.localUsers || [];

      // UnifyPhone search contacts are added to _userSearch.users as they are found
      // When search is complete normalize all non-exchange users
      for (var i = _userSearch.users.length - 1; i >= 0; i--) {
        var user = _userSearch.users[i];
        if (!user.isExchangeContact) {
          if (!searchConstraints.includeUsersWithoutPhone && localUsers.indexOf(user.userId) === -1 && (!user.phoneNumbers || user.phoneNumbers.length === 0)) {
            // Remove users which are not participants and don't have a phone number
            _userSearch.users.splice(i, 1);
            count++;
          } else if (!user.extendedForPhoneCallSearch) {
            _userSearch.users[i] = createUserForPhoneCallSearch(user);
          }
        }
      }
      LogSvc.debug('[UserSvc]: Removed ' + count + ' search results without phone numbers');
    }

    function normalizeExchangeContacts(contacts) {
      if (!contacts) {
        return;
      }

      // Insert valid exchange users
      var filteredOut = 0;
      contacts.forEach(function (contact) {
        var originalPhoneNumbersCount = (contact.phoneNumbers && contact.phoneNumbers.length) || 0;
        var preNormFirstName = (contact.firstName || '').trim();
        var preNormLastName = (contact.lastName || '').trim();
        var preNormPhoneNumbers = (contact.phoneNumbers || []).map(function (p) {
          return {
            number: (p && p.number) || '',
            phoneNumber: (p && p.phoneNumber) || '',
            type: (p && p.type) || ''
          };
        });
        UserProfile.normalizeExchangeData(contact);

        contact.firstName = (contact.firstName || '').trim();
        contact.lastName = (contact.lastName || '').trim();

        if ((!contact.firstName && !contact.lastName) || !contact.phoneNumbers || !contact.phoneNumbers.length) {
          var normalizedPhoneNumbers = (contact.phoneNumbers || []).map(function (p) {
            return {
              number: (p && p.number) || '',
              phoneNumber: (p && p.phoneNumber) || '',
              type: (p && p.type) || ''
            };
          });
          var problematicFields = [];
          var problematicValues = {};
          if (!contact.firstName && !contact.lastName) {
            problematicFields.push('firstName', 'lastName');
            problematicValues.firstName = { pre: preNormFirstName, post: contact.firstName };
            problematicValues.lastName = { pre: preNormLastName, post: contact.lastName };
          }
          if (!contact.phoneNumbers || !contact.phoneNumbers.length) {
            problematicFields.push('phoneNumbers');
            problematicValues.phoneNumbers = {
              preCount: originalPhoneNumbersCount,
              postCount: normalizedPhoneNumbers.length,
              pre: preNormPhoneNumbers,
              post: normalizedPhoneNumbers
            };
          }
          LogSvc.debug(
            '[UserSvc]: Dropping Exchange search result after normalization. problematicFields=' +
              problematicFields.join('|') +
              ', problematicValues=' +
              JSON.stringify(problematicValues)
          );
          // Do not add contacts without phone numbers
          filteredOut++;
          return;
        }
        _userSearch.insertExchangeContact(contact);
      });
      LogSvc.debug('[UserSvc]: Removed ' + filteredOut + ' Exchange search results without name or phone numbers');
    }

    /**
     * Execute user search in UnifyPhone and Exchange. Search local users and then send two server search request to retrieve more results.
     * Matches are added to _userSearch.users as new PhoneCallSearch User Objects
     *
     * @function
     * @param {Object} searchConstraints The search options.
     * @searchConstraints.query {String} The filter string to match from beginning of Firstname or Lastname.
     * @searchConstraints.includeUsersWithoutPhone {Boolean} Indicates whether UnifyPhone users without a phone number should be included in search results.
     * @searchConstraints.includeMeetingPoints {Boolean} Indicates whether Meeting Points should be included in search results.
     * @param {Function} doneCb Callback function when the search is finished.
     */
    function startPhoneCallSearch(searchConstraints, doneCb) {
      /*
                PhoneCallSearch User object definition for UI:
                    displayName: displayName,
                    firstName: firstName,
                    lastName: lastName,
                    emailAddress: emailAddress,
                    emailAddresses: [{
                      address: email,
                      type: type
                     }]
                    userId: userId,
                    avatar: avatar,
                    userPresenceState: userPresenceState
                    phoneNumbers: [{
                       phoneNumber: phoneNumber,
                       type: type
                    }]
                    isExchangeContact: boolean if user result is from exchange server or ansible
            */
      searchConstraints = searchConstraints || {};

      clearUserSearch();
      _userSearch.phoneCallSearch = true;

      var promises = [];

      // Use _clientApiHandler.startUserSearch to get UnifyPhone users and in parallel call MailboxConnSvc.searchContactsWithConstraints
      promises.push(
        new $q(function (resolve, reject) {
          searchByName(searchConstraints, function (err) {
            if (err && err.cleared) {
              reject(err);
              return;
            }
            LogSvc.debug('[UserSvc]: Server search is finished. Initial number of matches:', _userSearch.users.length);
            resolve();
          });
        })
      );

      promises.push(
        new $q(function (resolve) {
          searchExchangeByName(searchConstraints, function (err, contacts, searchId) {
            if (err) {
              resolve();
              return;
            }
            if (searchId && searchId !== _userSearch.exchangeSearchId) {
              LogSvc.debug('[UserSvc]: Discarding Exchange results for searchId:', searchId);
              resolve();
              return;
            }
            _userSearch.exchangeSearchId = null;
            LogSvc.debug('[UserSvc]: Exchange server search is finished. Initial number of matches: ', contacts && contacts.length);
            normalizeExchangeContacts(contacts);
            resolve();
          });
        })
      );

      $q.all(promises)
        .then(function () {
          normalizeUserContacts(searchConstraints);
          LogSvc.debug('[UserSvc]: User search is finished. Total users = ', _userSearch.users.length);
          doneCb && doneCb();
        })
        .catch(function () {
          LogSvc.debug('[UserSvc]: Previous user search was canceled');
        });
    }

    function publishStatusNotification(user, state) {
      if (user && state) {
        LogSvc.debug('[UserSvc]: Publish /user/status/notification event. userId = ', user.userId);
        PubSubSvc.publish('/user/status/notification', [user, state]);
      }
    }

    function updateUserStatusSubscriptions(userId, subscribed) {
      if (!userId) {
        return null;
      }
      var user = _usersHashtable[userId];
      var idx = _userStatusSubscriptions.indexOf(userId);

      if (subscribed) {
        idx === -1 && _userStatusSubscriptions.push(userId);
        if (!user) {
          // Create an empty user object. User data will only be retrieved later (if needed)
          user = UserProfile.extend({ userId: userId });
          user.noData = true;
          user.notifyWhenAvailable = true;
          addUsersToCache(user, true, false);
        } else if (!user.notifyWhenAvailable) {
          user.notifyWhenAvailable = true;
          return user;
        }
      } else {
        idx !== -1 && _userStatusSubscriptions.splice(idx, 1);
        if (user && user.notifyWhenAvailable) {
          user.notifyWhenAvailable = false;
          return user;
        }
      }
      return null;
    }

    function clearUserStatusSubscriptions() {
      _userStatusSubscriptions.forEach(function (userId) {
        var user = _usersHashtable[userId];
        if (user) {
          user.notifyWhenAvailable = false;
        }
      });
      _userStatusSubscriptions = [];
    }

    function loadUsersFromDb() {
      if (_users.length > 0) {
        // Already loaded the users
        return $q.resolve();
      }

      return LocalStoreSvc.getAllUsers()
        .then(function (users) {
          _usersLoaded = true;
          if (users) {
            LogSvc.debug('[UserSvc]: Loaded users from DB: ', users.length);
            var purged = $rootScope.i18n.map.res_Purged;

            // Do not use addUsersToCache here. Just store the users directly in the cache.
            users.forEach(function (user) {
              // Update the name for purged users in case the language has changed
              if (user.userState === Constants.UserState.PURGED) {
                user.displayName = purged;
                user.firstName = purged;
              }
              user = UserProfile.extend(user);
              _users.push(user);
              _usersHashtable[user.userId] = user;
            });
          }
        })
        .catch(function (err) {
          _usersLoaded = true;
          LogSvc.error('[UserSvc]: Error retrieving users from db: ', err);
        });
    }

    /**
     * Creates result for extended search when there was no query passed (default result). It either contains
     * a passed list of users or can have the recent user list.
     *
     * @function
     * @param {boolean} isPhoneSearch True if this is an extended phone search. False if this is an extended user search.
     * @param {Array[]} userIds - Array containing fixed list of results
     * @param {Function} doneCb Callback to pass loaded user objects to
     */
    function createSearchResultForEmptyQuery(isPhoneSearch, userIds, doneCb) {
      if (!userIds || userIds.length === 0) {
        doneCb(isPhoneSearch ? [] : _self.getRecentUsers());
      } else {
        // Create a temporary callback function so we can check if the current search
        // is still valid.
        var dummyCb = function () {};
        _userSearch.doneCb = dummyCb;

        // Limit number of returned users
        userIds = userIds.slice(0, MAX_PHONE_SEARCH_RESULTS);
        _self.getUsersByIds(userIds, function (err, userObjs) {
          if (_userSearch.doneCb === dummyCb) {
            // This is still the current search
            _userSearch.doneCb = null;
            if (!err) {
              _userSearch.users = isPhoneSearch ? userObjs.map(createUserForPhoneCallSearch) : userObjs;
              doneCb(_userSearch.users);
              return;
            }
          }
          doneCb(isPhoneSearch ? [] : _self.getRecentUsers());
        });
      }
    }

    function resetDiscardParticipantsToMentionTimer() {
      // Cancel any clear timers from previous results
      if (_discardParticipantsToMentionTimer) {
        $timeout.cancel(_discardParticipantsToMentionTimer);
        _discardParticipantsToMentionTimer = null;
      }

      _discardParticipantsToMentionTimer = $timeout(function () {
        _lastMentionQueryResult = {};
      }, ONE_MIN); // 1 min
    }

    function clearExternalStatus(user) {
      if (user.exchangeData && (user.exchangeData.outOfOffice || user.exchangeData.userAvailability)) {
        LogSvc.info('[UserSvc]: Reset OOO indication for user ', user.emailAddress);
        delete user.exchangeData.outOfOffice;
        delete user.exchangeData.userAvailability;
        delete user.exchangeData.lastSyncUserAvail;
        delete user.exchangeData.lastSync;
        updateUserInDB(user);
      }
    }

    function updateUserExchangeData(user, exchangeData) {
      if (!exchangeData.userAvailability) {
        if (user.exchangeData && user.exchangeData.userAvailability) {
          // Keep the userAvailability from user if doesn't exist
          exchangeData.userAvailability = user.exchangeData.userAvailability;
          exchangeData.lastSyncUserAvail = user.exchangeData.lastSyncUserAvail;
        }
        exchangeData.lastSync = Date.now();
      }
      UserProfile.mergeWithExchangeData(user, exchangeData);
      updateUserInDB(user);
      PubSubSvc.publish('/user/exchangeData/update', [user]);
    }

    function getContact(user) {
      return new $q(function (resolve, reject) {
        MailboxConnSvc.getContact(user.emailAddress, function (getContactErr, contact) {
          if (getContactErr) {
            LogSvc.warn('[UserSvc]: Error retrieving user exchange information: ', getContactErr);
            clearExternalStatus(user);
            reject(getContactErr);
            return;
          }
          if (contact) {
            if (typeof MailboxConnSvc.isOutOfOffice === 'function') {
              // Retrieve Out of Office status
              MailboxConnSvc.isOutOfOffice(user.emailAddress)
                .then(function (outOfOffice) {
                  contact.outOfOffice = outOfOffice;
                })
                .catch(function (oooErr) {
                  LogSvc.warn('[UserSvc]: Error retrieving OOO status: ', oooErr);
                })
                .then(function () {
                  LogSvc.debug('[UserSvc]: Sync exchange data finished with OOO');
                  updateUserExchangeData(user, contact);
                  resolve();
                });
            } else {
              LogSvc.debug('[UserSvc]: Sync exchange data finished without OOO');
              updateUserExchangeData(user, contact);
              resolve();
            }
          } else {
            LogSvc.info('[UserSvc]: Exchange connector did not find contact for ', user.emailAddress);
            // Continue so we update the user object to prevent asking again for now.
            contact = {};
            updateUserExchangeData(user, contact);
            resolve();
          }
        });
      });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Event Listeners
    ///////////////////////////////////////////////////////////////////////////////////////
    _clientApiHandler.on('User.USER_PRESENCE_CHANGE', function (data) {
      if (!$rootScope.localUser) {
        // Not ready to process events
        return;
      }
      if (data.userId === $rootScope.localUser.userId || data.userId === $rootScope.localUser.associatedTelephonyUserID) {
        // Event is for local user or associated TC.
        // This is handled by UserProfileSvc or AtcRegistrationSvc respectively.
        return;
      }

      LogSvc.debug('[UserSvc]: Received User.USER_PRESENCE_CHANGE event');

      // Check if we need to show a status notification for this user
      var idx = _userStatusSubscriptions.indexOf(data.userId);
      var needsNotification = idx !== -1 && data.newState.state === Constants.PresenceState.AVAILABLE;
      if (needsNotification) {
        LogSvc.debug('[UserSvc]: Need to show online status notification for user');
        // Remove user from subscription list
        _userStatusSubscriptions.splice(idx, 1);
      } else if (!hasActiveSubscription(data.userId)) {
        LogSvc.debug('[UserSvc]: There is no presence subscription for this user. Ignore event.');
        return;
      }

      var user = _usersHashtable[data.userId];
      if (!user) {
        LogSvc.debug('[UserSvc]: Cannot find user in cache. userId = ', data.userId);
        if (needsNotification) {
          LogSvc.debug('[UserSvc]: Get user information to raise status notification');
          _self.getUserById(data.userId, function (err, retrievedUser) {
            publishStatusNotification(retrievedUser, Constants.PresenceState.AVAILABLE);
          });
        }
        return;
      }

      if (user.isInactive) {
        // Inactive user has registered. Update the user data.
        refreshUsers([user]);
      }

      var hasUpdatedUser = UserProfile.updateUserPresenceState(user, data.newState);
      if (needsNotification || hasUpdatedUser) {
        $rootScope.$apply(function () {
          if (needsNotification) {
            user.notifyWhenAvailable = false;
            publishStatusNotification(user, Constants.PresenceState.AVAILABLE);
          }
          LogSvc.debug('[UserSvc]: Publish /user/update event');
          PubSubSvc.publish('/user/update', [user]);
        });
      } else {
        LogSvc.debug('[UserProfileSvc]: User presence state has not changed. Ignore event.');
      }
    });

    _clientApiHandler.on('User.NOTIFICATION_SUBSCRIPTION_CHANGE', function (data) {
      if (!$rootScope.localUser) {
        // Not ready to process events
        return;
      }

      if (!data || !data.userId || !data.subscriptionAction || !data.subscriptionType) {
        LogSvc.error('[UserSvc]: User.NOTIFICATION_SUBSCRIPTION_CHANGE event does not contain valid data.');
        return;
      }

      if (data.subscriptionType === Constants.NotificationSubscriptionType.ONLINE_STATUS) {
        var updatedUser = null;
        switch (data.subscriptionAction) {
          case Constants.NotificationSubscriptionAction.SUBSCRIBE:
            updatedUser = updateUserStatusSubscriptions(data.userId, true);
            break;
          case Constants.NotificationSubscriptionAction.UNSUBSCRIBE:
            updatedUser = updateUserStatusSubscriptions(data.userId, false);
            break;
        }

        if (updatedUser) {
          $rootScope.$apply(function () {
            LogSvc.debug('[UserSvc]: Publish /user/update event. userId =', updatedUser.userId);
            PubSubSvc.publish('/user/update', [updatedUser]);
          });
        }
      }
    });

    _clientApiHandler.on('User.USER_UPDATED', function (data) {
      if (!$rootScope.localUser) {
        // Not ready to process events
        return;
      }
      if (!data.user) {
        LogSvc.error('[UserSvc]: User.USER_UPDATED event does not have a user');
        return;
      }
      if (data.user.userId === $rootScope.localUser.userId) {
        return;
      }
      LogSvc.debug('[UserSvc]: Received User.USER_UPDATED event. ', data);
      $rootScope.$apply(function () {
        var user = addUserAndReturnCached(data.user);
        LogSvc.debug('[UserSvc]: Publish /user/update event. userId =', user.userId);
        PubSubSvc.publish('/user/update', [user]);
      });
    });

    _clientApiHandler.on('User.USER_DELETED', function (data) {
      if (!$rootScope.localUser) {
        // Not ready to process events
        return;
      }
      LogSvc.debug('[UserSvc]: Received User.USER_DELETED event. ', data);
      if (!data.userId) {
        LogSvc.error('[UserSvc]: User.USER_DELETED event does not have a user id');
        return;
      }

      $rootScope.$apply(function () {
        var user = _usersHashtable[data.userId];
        if (user) {
          UserProfile.update(user, { userState: Constants.UserState.DELETED }, true);
          updateUserInDB(user);
          LogSvc.debug('[UserSvc]: Publish /user/update event. userId =', user.userId);
          PubSubSvc.publish('/user/update', [user]);
        } else {
          LogSvc.debug('[UserSvc]: Publish /user/delete event. userId =', data.userId);
          PubSubSvc.publish('/user/delete', [data.userId]);
        }
      });
    });

    _clientApiHandler.on('Search.BASIC_SEARCH_RESULT', function (data) {
      if (data.searchId !== _userSearch.searchId || !data.users) {
        return;
      }

      $rootScope.$apply(function () {
        if (data.details && data.details.hasOwnProperty('clWorkerId')) {
          data.details.clWorkerId
            ? LogSvc.debug('[UserSvc]: Received event Search.BASIC_SEARCH_RESULT for CloudLink search with workerId:', data.details.clWorkerId)
            : LogSvc.debug('[UserSvc]: Received event Search.BASIC_SEARCH_RESULT for CloudLink search without workerId.');
        } else {
          LogSvc.debug('[UserSvc]: Received event Search.BASIC_SEARCH_RESULT.');
        }

        var userIds = data.users.filter(function (userId) {
          return userId !== $rootScope.localUser.userId || _userSearch.includeLocalUser;
        });

        _userSearch.pendingReqs++;
        _self.getUsersByIds(userIds, function (err, users) {
          // Make sure the same search is still active
          if (data.searchId !== _userSearch.searchId) {
            return;
          }
          if (users) {
            // Insert found users in the proper order
            filterUsers(users, $rootScope.localUser.hasSupportRole, _userSearch.includeLocalUser).forEach(_userSearch.insertUser);
          }
          // Decrement the pending requests and invoke the doneCb if appropriate
          decrementPendingSearchReqs();
        });
      });
    });

    _clientApiHandler.on('Search.SEARCH_STATUS', function (data) {
      if (data.searchId !== _userSearch.searchId) {
        return;
      }

      $rootScope.$apply(function () {
        var statusMsg = '[UserSvc]: Received event Search.SEARCH_STATUS: ' + data.status;
        if (data.details && data.details.hasOwnProperty('clWorkerId')) {
          data.details.clWorkerId
            ? (statusMsg = statusMsg + ' for CloudLink search with workerId: ' + data.details.clWorkerId)
            : (statusMsg = statusMsg + ' for CloudLink search without workerId');
        }
        LogSvc.debug(statusMsg);
        // Decrement the pending requests and invoke the doneCb if appropriate
        decrementPendingSearchReqs();
      });
    });

    _clientApiHandler.on('User.TELEPHONY_CHANGED', function (event) {
      LogSvc.debug('[UserSvc]: Received event User.TELEPHONY_CHANGED: ', event.telephonyChangeType);
      var eventType;
      switch (event.telephonyChangeType) {
        case Constants.TelephonyChangeType.ADDED:
          eventType = '/user/telephony/added';
          break;
        case Constants.TelephonyChangeType.UPDATED:
          eventType = '/user/telephony/updated';
          break;
        case Constants.TelephonyChangeType.REMOVED:
          eventType = '/user/telephony/removed';
          break;
        default:
          break;
      }

      if (eventType) {
        $rootScope.$apply(function () {
          LogSvc.debug('[UserSvc]: Publish ' + eventType + ' event');
          PubSubSvc.publish(eventType);
        });
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/registration/stuff', function (data) {
      LogSvc.debug('[UserSvc]: Received /registration/stuff event');

      // First clear all existing subscriptions (if any)
      clearUserStatusSubscriptions();

      loadUsersFromDb().then(function () {
        // Now process the subscriptions which are still active
        data.notificationSubscription &&
          data.notificationSubscription.some(function (subscription) {
            if (subscription.key === Constants.NotificationSubscriptionType.ONLINE_STATUS) {
              subscription.value &&
                subscription.value.forEach(function (userId) {
                  updateUserStatusSubscriptions(userId, true);
                });
              LogSvc.debug('[UserSvc]: Initialized online status subscription list with ', _userStatusSubscriptions);
              return true;
            }
            return false;
          });

        var userIds = _permanentPresenceSubscriptions.concat(_temporaryPresenceSubscriptions);
        if (userIds.length > 0) {
          LogSvc.debug("[UserSvc]: Resubscribe to users' presence");
          sendSubscribePresence(userIds);
        }

        LogSvc.debug('[UserSvc]: Publish /users/loaded event');
        PubSubSvc.publish('/users/loaded', null);
      });
    });

    PubSubSvc.subscribe('/registration/state', function (state) {
      LogSvc.debug('[UserSvc]: Received /registration/state event');

      switch (state) {
        case RegistrationState.Registered:
          startUserRefreshTimers();
          break;

        case RegistrationState.LoggingOut:
          clearUserRefreshTimers();
          clearUserSvcCache();
          break;

        default:
          clearUserRefreshTimers();
          break;
      }
    });

    PubSubSvc.subscribe('/language/update', function onLanguageUpdate(language) {
      LogSvc.debug('[UserSvc]: Received /language/update event. Language = ', language);

      // Need to update the display name for all purged users
      var updatedUsers = checkForPurgedUsers(_users);
      updatedUsers.forEach(function (user) {
        user.displayNameEscaped = user.displayName;
        Utils.syncBaseObject(user);
      });
    });

    PubSubSvc.subscribe('/conversation/update', function (conversation) {
      LogSvc.debug('[UserSvc]: Received /conversation/update event.');

      if (_lastMentionQueryResult.convId === conversation.convId) {
        // Reset the last query results
        _lastMentionQueryResult = {};
        if (_discardParticipantsToMentionTimer) {
          $timeout.cancel(_discardParticipantsToMentionTimer);
          _discardParticipantsToMentionTimer = null;
        }
      }
    });

    PubSubSvc.subscribe('/conversation/item/add', function (item) {
      LogSvc.debug('[UserSvc]: Received /conversation/item/add event.');

      var user = _usersHashtable[item.creatorId];
      if (user && user.isInactive) {
        // Received message from an inactive user, update its data.
        refreshUsers([user]);
      }
      recalculateRecentUsers();
    });

    PubSubSvc.subscribe('/conversation/items/updated', function () {
      LogSvc.debug('[UserSvc]: Received /conversation/items/updated event.');
      recalculateRecentUsers();
    });

    PubSubSvc.subscribe('/conversation/items/loaded', function () {
      LogSvc.debug('[UserSvc]: Received /conversation/items/loaded event.');
      recalculateRecentUsers();
    });

    PubSubSvc.subscribe('/internal/svc/go2Sleep', function () {
      LogSvc.debug('[UserSvc]: Go to Sleep');
      _isMobileAsleep = true;
      clearUserRefreshTimers();
    });

    PubSubSvc.subscribe('/internal/svc/wakeUp', function () {
      LogSvc.debug('[UserSvc]: Wake up');
      _isMobileAsleep = false;
      startUserRefreshTimers();
    });

    PubSubSvc.subscribe('/call/state', function onCallState(call) {
      if (call && !call.isRemote && _localCall !== call) {
        LogSvc.debug('[UserSvc]: Keep reference to active call');
        _localCall = call;
      }
    });

    PubSubSvc.subscribe('/call/ended', function onCallEnded(call) {
      if (call && call.sameAs(_localCall)) {
        LogSvc.debug('[UserSvc]: Clear reference to active call');
        _localCall = null;
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    /**
     * Get a promised that is resolved when the users have been loaded from the cache
     * @returns {object} Promise object.
     */
    this.getUsersLoadedPromise = function () {
      return new $q(function (resolve) {
        if (_usersLoaded) {
          resolve();
        } else {
          PubSubSvc.subscribeOnce('/users/loaded', function () {
            resolve();
          });
        }
      });
    };

    this.addUsersToCache = function (users, cacheOnly, retrieveIncomplete) {
      if (users) {
        cacheOnly = !!cacheOnly;
        retrieveIncomplete = !!retrieveIncomplete;
        LogSvc.debug('[UserSvc]: addUsersToCache. cacheOnly = ' + cacheOnly + ', retrieveIncomplete = ' + retrieveIncomplete);
        addUsersToCache(users, cacheOnly, retrieveIncomplete);
      }
    };

    this.refreshUsers = function (users) {
      if (users && users.length) {
        LogSvc.debug('[UserSvc]: refreshUsers...');
        var cachedUsers = users
          .map(function (user) {
            return user && user.userId && _usersHashtable[user.userId];
          })
          .filter(function (user) {
            return !!user;
          });
        refreshUsers(cachedUsers);
      }
    };

    this.refreshUser = function (userId) {
      if (userId && typeof userId === 'string') {
        var cachedUser = _usersHashtable[userId];
        if (cachedUser) {
          LogSvc.debug('[UserSvc]: refreshUser - userId = ', userId);
          refreshUsers([cachedUser]);
        }
      }
    };

    this.updateUserInDB = updateUserInDB;

    // Return local users
    this.getCachedUsers = function () {
      return _users;
    };

    // Return initialzed local users
    this.getCachedUsersWithData = function () {
      return _users.filter(function (u) {
        return !u.noData && !u.isDeleted && (u.userType === Constants.UserType.REGULAR || u.userType === Constants.UserType.XMPP);
      });
    };

    // Return local cached users
    this.getUserFromCache = getUserFromCache;
    this.getUser = getUserFromCache; // DEPRECATED: Will be removed as soon as mobile clients start using new getUserFromCache API

    /**
     * Return local cached user if exists. Otherwise creates the placeholder User object with noData property.
     *
     * @function
     * @param {String} userId  The userId of the user to be retrieved
     * @returns {Object} The user object
     */
    this.getExtendedUser = getExtendedUser;

    this.hasUser = function (userId) {
      return !!_usersHashtable[userId];
    };

    this.getRecentUsers = function (fillUp, maxUsers) {
      LogSvc.debug('[UserSvc]: getRecentUsers...');
      maxUsers = Math.min(maxUsers || MAX_RECENT_USERS, MAX_RECENT_USERS);
      var recentUsers = _recentUsers.slice(0, maxUsers);
      if (fillUp && recentUsers.length < maxUsers) {
        // If we don't have enough users yet, then fill the array with users from
        // other group conversations (i.e. those that don't have a lastActvity timestamp yet).
        _users.some(function (u) {
          if (!u.lastActivity && isValidRecentUser(u)) {
            recentUsers.push(u);
          }
          return recentUsers.length === maxUsers;
        });
      }
      return recentUsers;
    };

    /**
     * This function retrieves cached presence or presence from server for given userIds (cached for subscribed,
     * server presence for unsubscribed)
     *
     * @function
     * @param {String[]} userIds  The userIds for which we want to retrieve the presence.
     */
    this.getUsersPresence = function (userIds, cb) {
      getUsersPresence(userIds, false, cb);
    };

    /**
     * This function starts a permanent presence subscription for the given userIds.
     *
     * @function
     * @param {String[]} userIds  The userIds for which we are supposed to subscribe.
     * @param {Boolean} prioritized  If true subscriptions are prioritized in case subscription list is already full.
     */
    this.addPermanentPresenceSubscription = function (userIds, prioritized) {
      subscribePermanentPresence(userIds, !!prioritized);
    };

    /**
     * This function starts the presence subscription for the peer users in the most recent
     * direct conversations. This function is supposed to be invoked as soon as the conversations
     * are initially loaded.
     *
     * @function
     * @param {String[]} userIds  The userIds for which we are supposed to subscribe.
     */
    this.initPermanentPresenceSubscriptions = function (userIds) {
      LogSvc.info('[UserSvc]: Initialize permanent presence subscriptions');
      subscribePermanentPresence(userIds, true);
    };

    /**
     * This function starts a temporary presence subscribtion to the users presence unless already
     * subscribed to. The subscriptions are maintained until we need to free space for newer temporary
     * subscriptions.
     *
     * @function
     * @param {String[]} userIds  The userIds for which we are supposed to subscribe.
     */
    this.subscribeTemporaryPresence = function (userIds) {
      subscribeTemporaryPresenceAndNotify(userIds);
    };

    /**
     * This function starts a temporary presence subscribtion to the users presence unless already
     * subscribed to. The subscriptions are maintained until we need to free space for newer temporary
     * subscriptions.
     *
     * @function
     * @param {Object} data An object containing data like
     *                {String[]} userIds  The userIds for which we are supposed to subscribe.
     * *              {Object[]} users    The user objects containing the userIds for which we are supposed to subscribe.
     */
    this.subscribeTemporaryPresenceV2 = function (data) {
      if (!data || (!Array.isArray(data.userIds) && !Array.isArray(data.users))) {
        return;
      }

      var userIds = data.userIds || [];
      if (Array.isArray(data.users)) {
        userIds = userIds.concat(
          data.users.map(function (u) {
            return u.userId;
          })
        );
      }

      subscribeTemporaryPresenceAndNotify(userIds);
    };

    /**
     * This function starts a temporary presence subscribtion to the users presence unless already
     * subscribed to. The subscriptions are maintained until we need to free space for newer temporary
     * subscriptions. Once subscription process finishes callback is called.
     *
     * @function
     * @param {String[]} userIds  The userIds for which we are supposed to subscribe.
     * @param {function} cb Callback with parameters: (error, userIds)
     */
    this.subscribeTemporaryPresenceAndNotify = subscribeTemporaryPresenceAndNotify;

    /**
     * Gets the presence state for the given user.
     *
     * @function
     * @param {userId} userId to be updated with presence data
     * @param {function} cb Callback with parameters: error and userPresenceState
     */
    this.getUserPresence = function (userId, cb) {
      LogSvc.debug('[UserSvc]: Get presence and location for userId = ', userId);
      getUsersPresence([userId], false, function (err, presenceStates) {
        if (!err && presenceStates && presenceStates.length > 0) {
          LogSvc.debug('[UserSvc]: Got presence status for user ', userId);
          cb && cb(null, presenceStates[0]);
          return;
        }
        cb && cb(err || 'No results');
      });
    };

    /**
     * Execute user search. Search local users and then send server search request to retrieve more results.
     *
     * @function
     * @param {Object} constraints
     * @param {Array[]} constraints.query - Contains the query string, the match is from beginning of Firstname or Lastname.
     * @param {boolean} constraints.includeXmppUsers - The flag indicates if Xmpp users should be retrieved
     * @param {Function} doneCb Callback function when the search is finished.
     */
    this.startUserSearch = function (constraints, doneCb) {
      clearUserSearch();
      searchByName(constraints, doneCb);
    };

    /**
     * Execute extended phone search. Search local users and then send server search request and query exchange connector to retrieve more results.
     *
     * @function
     * @param {Object} searchConstraints The search options.
     * @searchConstraints.query {String} The filter string to match from beginning of Firstname or Lastname.
     * @searchConstraints.localUsers {Object} Array with local user set (e.g. predefined users for popover).
     * @searchConstraints.includeMeetingPoints {Boolean} Indicates whether Meeting Points should be included in search results.
     * @param {Function} doneCb Callback function when the search is finished.
     */
    this.startExtendedPhoneSearch = function (searchConstraints, doneCb) {
      doneCb = doneCb || function () {};

      clearUserSearch();

      if (searchConstraints.query) {
        startPhoneCallSearch(searchConstraints, function () {
          doneCb(_userSearch.users);
        });
      } else {
        createSearchResultForEmptyQuery(true, searchConstraints.localUsers, doneCb);
      }
    };

    /**
     * Execute extended user search either returning the list of recent users or start server search by user name or email address.
     *
     * @function
     * @param {Object} searchConstraints The search options.
     * @searchConstraints.query {String} The filter string to match from beginning of Firstname or Lastname.
     * @searchConstraints.triggerEmailSearch {Boolean} Marker that user entered email address for search.
     * @searchConstraints.includeXmppUsers {Boolean} Indicates whether XMPP users should be included in search results.
     * @searchConstraints.includeMeetingPoints {Boolean} Indicates whether Meeting Points should be included in search results.
     * @param {Function} doneCb Callback function when the search is finished.
     */
    this.startExtendedUserSearch = function (searchConstraints, doneCb) {
      doneCb = doneCb || function () {};

      var resultInfo = {};
      if (!searchConstraints.query) {
        createSearchResultForEmptyQuery(false, searchConstraints.localUsers, doneCb);
      } else {
        _self.startUserSearch(searchConstraints, function (err, emailUser) {
          if (err && err.cleared) {
            // This search is obsolete. Ignore the result
            return;
          }
          if (searchConstraints.triggerEmailSearch) {
            if (emailUser && !emailUser.notFound) {
              // User has pressed space to confirm this is the exact email to search and is valid
              resultInfo.emailValid = true;
              resultInfo.emailUser = emailUser;
            } else if (searchConstraints.query.match(Utils.EMAIL_ADDRESS_PATTERN)) {
              // User has pressed space to confirm this is the exact email to search but it is invalid
              var userObj = {
                displayName: searchConstraints.query,
                displayNameEscaped: Utils.textToHtmlEscaped(searchConstraints.query),
                emailAddress: searchConstraints.query,
                presence: {
                  state: Constants.PresenceState.OFFLINE
                },
                notFound: true,
                storageOverloaded: err === Constants.ReturnCode.STORAGE_OVERLOADED
              };
              resultInfo.emailInvalid = true;
              resultInfo.emailUser = userObj;
            }
          }
          var result = _userSearch.users.slice();
          doneCb(result, resultInfo);
        });
      }
    };

    /**
     * Execute user search in UnifyPhone and Exchange. Search local users and then send two server search request to retrieve more results.
     * Matches are added to _userSearch.users as new PhoneCallSearch User Objects
     *
     * @function
     * @param {String} query The query string, the match is from beginning of Firstname or Lastname, or partial user profile's phone numbers match.
     * @param {Function} doneCb Callback function when phone number search is finished.
     * @param {Boolean} includeUsersWithoutPhone Control if users without phones are filtered out (false) or not (true)
     */
    this.startPhoneCallSearch = function (query, doneCb, includeUsersWithoutPhone) {
      var searchConstraints = {
        query: query,
        includeUsersWithoutPhone: !!includeUsersWithoutPhone,
        phoneNumberLookup: true
      };
      startPhoneCallSearch(searchConstraints, doneCb);
    };

    /**
     * Execute user search in UnifyPhone and Exchange. Search local users and then send two server search request to retrieve more results.
     * Matches are added to _userSearch.users as new PhoneCallSearch User Objects
     *
     * @function
     * @param {String} query The query string, the match is from beginning of Firstname or Lastname, or partial user profile's phone numbers match.
     * @param {Function} doneCb Callback function when phone number search is finished. If search resulted in one and only one match, this user is passed as a parameter.
     */
    this.startReverseLookUp = function (phoneNumber, doneCb) {
      if ($rootScope.localUser.skipBackendSearch) {
        doneCb && doneCb();
        return;
      }
      var searchConstraints = {
        query: phoneNumber,
        reversePhoneNumberLookup: true
      };
      startPhoneCallSearch(searchConstraints, function () {
        var users = [];
        // If there are more than one matches then prioritize UnifyPhone results over Exchange
        if (_userSearch.users.length > 1) {
          users = _userSearch.users.filter(function (user) {
            return !user.isExchangeContact;
          });
        } else if (_userSearch.users.length === 1) {
          users = _userSearch.users.slice(0);
        }
        doneCb && doneCb(users.length === 1 ? users[0] : null);
      });
    };

    /**
     * Clear the user search.
     *
     * @function
     */
    this.clearUserSearch = clearUserSearch;

    /**
     * Get the search user list.
     *
     * @returns {SearchResults.user} Returns the user list results from search
     */
    this.getUserSearchList = function () {
      return _userSearch.users;
    };

    /**
     * Expose sort function for user arrays (using Array.sort).
     * @function
     * @param {String} Optional search term to sort the list.
     */
    this.sortUsersFn = sortUsers;

    /**
     * Insert users based on the following sort rules:
     * 1. The results that match the firstname are listed first.
     * 2. For names that match the firstname, sort them by firstname.
     * 3. For names that match the lastname, sort them by lastname.
     * @function
     * @param {User} New user to be inserted.
     * @param {String} Search term to sort the insertion.
     * @param {User[]} User list to insert.
     */
    this.insertSortedUser = insertSortedUser;

    /**
     * Filter the users using the query string. LocalUser will be excluded from the list.
     * The passed in users maybe the result returned from the server and will be extended.
     *
     * @param {Array} users An array of users to be filtered.
     * @param {String} query The string to be matched from beginning of first name or last name.
     * @param {Boolean} includeSpecialUser Include the special users or not, default not including.
     * @returns {Array} Array of users after filtering.
     */
    this.filterUsers = function (users, query, includeSpecialUser) {
      return filterUsers(userNameFilter(users, query), includeSpecialUser);
    };

    /**
     * Retrieve exchange information for users
     *
     * @param {Object} user The user to query for exchange information
     */
    this.syncUserExchangeInfo = function (user, fasterSync) {
      return new $q(function (resolve, reject) {
        if (!user) {
          reject('User is not valid');
          return;
        }
        LogSvc.debug('[UserSvc]: Started syncUserExchangeInfo for user ', user.emailAddress);

        if (!MailboxConnSvc.connected) {
          clearExternalStatus(user);
          reject('Not connected');
          return;
        }

        var isSyncTimeExpired = function (fasterSyncTime, syncTime, lastSync) {
          if (lastSync) {
            // If fasterSync is set, check if data is older than fasterSyncTime.
            // Otherwise, check if data is older than a syncTime.
            var timeToCheck = fasterSync ? fasterSyncTime : syncTime;
            if (Date.now() - lastSync <= timeToCheck) {
              // Use cached data
              return false;
            }
          }
          return true;
        };

        var updateUserAvailability = function (userAvailability) {
          if (userAvailability) {
            user.exchangeData = user.exchangeData || {};
            user.exchangeData.userAvailability = userAvailability;
            user.exchangeData.lastSyncUserAvail = Date.now();
            updateUserExchangeData(user, user.exchangeData);
          }
        };

        var promiseUserAvailability = $q.resolve();

        // UserAvailability
        // If fasterSync is set, check if data is older than 30 min. Otherwise, 15 min.
        if (
          MailboxConnSvc.supportsUserAvailability() &&
          isSyncTimeExpired(ONE_MIN, TEN_MIN, user.exchangeData && user.exchangeData.userAvailability && user.exchangeData.lastSyncUserAvail)
        ) {
          if (typeof MailboxConnSvc.getUserAvailability === 'function') {
            LogSvc.debug('[UserSvc]: Sync UserAvailability for ', user.emailAddress);
            if (user.exchangeData) {
              user.exchangeData.lastSyncUserAvail = Date.now();
            }
            // Retrieve In Meeting status
            promiseUserAvailability = MailboxConnSvc.getUserAvailability(user.emailAddress)
              .then(function (userAvailability) {
                updateUserAvailability(userAvailability);
                LogSvc.debug('[UserSvc]: Sync UserAvailability finished');
              })
              .catch(function (err) {
                LogSvc.warn('[UserSvc]: Error retrieving UserAvailability status: ', err);
              });
          }
        }

        promiseUserAvailability
          .then(function () {
            var promiseContact = $q.resolve();
            // Exchange Contact & OOO
            // If fasterSync is set, check if data is older than 1 hour. Otherwise, 1 day.
            if (isSyncTimeExpired(ONE_HOUR, ONE_DAY, user.exchangeData && user.exchangeData.lastSync)) {
              LogSvc.debug('[UserSvc]: Sync exchange data for ', user.emailAddress);
              promiseContact = getContact(user);
            }

            promiseContact
              .then(function () {
                // Log full data only in development mode
                LogSvc.debug('[UserSvc]: Finished syncUserExchangeInfo for user ', $rootScope.developmentMode ? user.exchangeData : user.emailAddress);
                resolve();
              })
              .catch(function (err) {
                LogSvc.debug('[UserSvc]: Error syncing exchange data for user. ', err);
                reject(err);
              });
          })
          .catch(function (err) {
            LogSvc.debug('[UserSvc]: Error syncing user availability. ', err);
            reject(err);
          });
      });
    };

    /**
     * Retrieve a user from the server, based on its email address
     *
     * @param {String} emailAddress The user's main email address
     * @param {Function} cb Callback returning error and user parameters
     */
    this.getUserByEmail = getUserByEmail;

    /**
     * Retrieve a user from the server, based on its email address
     *
     * @param {String} emailAddress User Main Email Address
     * @param {Promise}
     */
    this.getUserByEmailPromise = function (email) {
      var deferred = $q.defer();
      getUserByEmail(email, function (err, user) {
        if (err || !user) {
          deferred.reject(Constants.ReturnCode.NO_RESULT);
          return;
        }
        deferred.resolve(user);
      });
      return deferred.promise;
    };

    /**
     * Retrieve a list of users from the server, based on their email addresses
     *
     * @param {Array} emailAddresses The user(s) main email addresses
     * @param {Function} cb Callback returning error and user parameters
     * @param {Object} filter
     * @param {boolean} filter.xmppLookup - Flag indicating if XMPP users should be searched for.
     */
    this.getUsersByEmails = function (emailAddresses, cb, filter) {
      filter = filter || {};
      filter.emailAddresses = emailAddresses;
      getUsersByEmails(filter, cb);
    };

    /**
     * Retrieve user from cache (if available) or from server.
     *
     * @param {String} userId User ID
     * @param {Function} cb Callback returning error and user parameters
     */
    this.getUserById = function (userId, cb) {
      cb = cb || function () {};
      if (!userId) {
        cb(Constants.ReturnCode.NO_RESULT);
        return;
      }

      if ($rootScope.localUser && userId === $rootScope.localUser.userId) {
        cb(null, $rootScope.localUser);
        return;
      }

      var outdatedTimestamp = Date.now() - USER_GET_BY_ID_REFRESH_INTERVAL;
      var user = _usersHashtable[userId];
      if (user) {
        if (!user.noData && user.updatedTimeStamp > outdatedTimestamp) {
          cb(null, user);
          return;
        }
      }
      getUsersByIds([userId], false, function (users) {
        if (!users.length) {
          cb(Constants.ReturnCode.NO_RESULT);
        } else {
          user = addUserAndReturnCached(users[0]);
          cb(null, user);
        }
      });
    };

    this.getUserPromise = function (userId) {
      var defer = $q.defer();
      _self.getUserById(userId, function (err, user) {
        err ? defer.reject(ControllerRouteError.USER_NOT_FOUND) : defer.resolve(user);
      });
      return defer.promise;
    };

    /**
     * Retrieve users from cache (if available) or from server. Order is not preserved.
     *
     * @param {String} userIds User IDs
     * @param {Function} cb Callback returning error and users parameters
     */
    this.getUsersByIds = function (userIds, cb) {
      cb = cb || function () {};
      if (!Array.isArray(userIds) || userIds.length === 0) {
        cb(Constants.ReturnCode.NO_RESULT);
        return;
      }

      var users = [];
      var pendingUserIds = [];
      var localUserId = $rootScope.localUser && $rootScope.localUser.userId;
      var outdatedTimestamp = Date.now() - USER_GET_BY_ID_REFRESH_INTERVAL;

      userIds.forEach(function (userId) {
        if (userId === localUserId) {
          users.push($rootScope.localUser);
          return;
        }
        var user = _usersHashtable[userId];
        if (user) {
          if ((!user.noData && user.updatedTimeStamp > outdatedTimestamp) || user.reqPending) {
            users.push(user);
            return;
          }
        }
        pendingUserIds.push(userId);
      });

      if (pendingUserIds.length === 0) {
        cb(null, users);
        return;
      }

      getUsersByIds(pendingUserIds, true, function (otherUsers, finished) {
        if (otherUsers.length > 0) {
          // TODO: Remove updatePresence parameter when presence subscriptions are implemented
          Array.prototype.push.apply(users, addUsersAndReturnCached(otherUsers, true));
        }

        if (finished) {
          if (users.length > 0) {
            cb(null, users);
          } else {
            cb(Constants.ReturnCode.NO_RESULT);
          }
        }
      });
    };

    this.toggleUserStatusUpdateSubscription = function (userIdsList, subscribe, cb) {
      _clientApiHandler.updateStatusSubscription(userIdsList, subscribe, function (err) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.error('[UserSvc]: Error updating status subscription.', err);
          }
          cb && cb(err);
        });
      });
    };

    this.updateUserWithExchangeContactName = function (user) {
      if (MailboxConnSvc.updateUserWithLocalContactName) {
        MailboxConnSvc.updateUserWithLocalContactName(user);
      }
    };

    /**
     * Search conversation participants for mention.
     * Retrieves the participants of the conversation whose name matches the given query string.
     *
     * @param {String} query The user name to search for.
     * @param {String} convId The conversation Id.
     * @returns {Promise} returning the array of participants that match the query.
     */
    this.searchConversationParticipants = function (query, convId) {
      if (!convId) {
        return $q.reject('No conversation ID provided');
      }
      if (!query || !query.trim()) {
        return $q.reject('Invalid input');
      }

      var deferred = $q.defer();

      _clientApiHandler.searchConversationParticipants(query, convId, function (err, participants) {
        if (err) {
          LogSvc.error('[UserSvc]: Error in API searchConversationParticipants: ', err);
          deferred.reject(err);
          return;
        }

        // Normalize the participant avatars
        participants = participants.filter(function (p) {
          if (p.userId !== $rootScope.localUser.userId) {
            UserProfile.setAvatars(p);
            p.displayName = (p.firstName + ' ' + p.lastName).trim();
            return true;
          }
          return false;
        });

        deferred.resolve(participants);
      });

      return deferred.promise;
    };

    /**
     * Get users for mention.
     * Retrieves the participants of the conversation whose name matches the given query string. The current user can also be part of the resulting list.
     *
     * @param {String} conv The conversation.
     * @param {String} query The user name to search for
     * @param {Function} cb Callback containing the searchPointer, hasMore and array of participant if query is made, else just filtered cached participants
     * objects which internally contain the user object.
     */
    this.getParticipantsToMention = function (conv, query, cb) {
      if (!conv || !conv.convId) {
        cb('No conversation object provided');
        return;
      }
      query = query || '';
      query = query.toLowerCase();

      var filteredParticipants = [];

      // Cancel any pending timer
      if (_getParticipantsToMentionTimer) {
        $timeout.cancel(_getParticipantsToMentionTimer);
        _getParticipantsToMentionTimer = null;
      }

      if (_lastMentionQueryResult.convId === conv.convId && query.startsWith(_lastMentionQueryResult.query)) {
        // A similar request has already been made and the response contained all matches. Filter and return the cached result.
        filteredParticipants = UserProfile.filterUsersByName(_lastMentionQueryResult.participants, query);
        cb(null, filteredParticipants);
        return;
      }

      if (conv.peerUsers && conv.peerUsers.length <= MAX_CONVERSATION_PARTICIPANTS_FOR_LOCAL_SEARCH && !Conversation.hasIncompleteUsers(conv)) {
        // Force local search
        filteredParticipants = UserProfile.filterUsersByName(conv.peerUsers, query);
        if (UserProfile.filterUserByName($rootScope.localUser, query)) {
          filteredParticipants.push($rootScope.localUser);
        }

        _lastMentionQueryResult = {
          convId: conv.convId,
          query: query,
          participants: filteredParticipants
        };
        resetDiscardParticipantsToMentionTimer();
        cb(null, filteredParticipants);
        return;
      }

      // Set the pending mention query
      _pendingMentionQuery = {
        convId: conv.convId,
        query: query
      };

      // Don't send the request as soon as the user starts typing the mention
      _getParticipantsToMentionTimer = $timeout(function () {
        _getParticipantsToMentionTimer = null;

        _self
          .searchConversationParticipants(query, conv.convId)
          .then(function (foundParticipants) {
            if (_pendingMentionQuery.convId !== conv.convId || _pendingMentionQuery.query !== query) {
              // This is not the latest query. Ignore the results
              return;
            }

            // Add current user to resulting list if it matches the query
            if (UserProfile.filterUserByName($rootScope.localUser, query)) {
              foundParticipants.push($rootScope.localUser);
            }

            // We have all the participants for the selected query. Save the query results.
            _lastMentionQueryResult = {
              convId: conv.convId,
              query: query,
              participants: foundParticipants
            };
            resetDiscardParticipantsToMentionTimer();

            cb(null, foundParticipants);
          })
          .catch(cb);
      }, GET_PARTICIPANTS_TO_MENTION_DELAY);
    };

    /**
     * Returns additional support information for the given user ID.
     * @param {String} userId  The userId of the user to retrieve the support info.
     * @returns {Promise} Promise with the support information data.
     */
    this.getSupportData = function (userId) {
      if (!$rootScope.localUser.hasSupportRole) {
        return $q.reject('Not a support user');
      }
      return new $q(function (resolve, reject) {
        _clientApiHandler.getSupportData(userId, function (err, data) {
          $rootScope.$apply(function () {
            if (err && !data) {
              LogSvc.error('[UserSvc]: Error getting support data. ', err);
              reject(err);
              return;
            }
            LogSvc.debug('[UserSvc]: Retrieved support data for userId = ', userId);
            resolve(data);
          });
        });
      });
    };

    this.isIncompleteUser = function (user, cacheOnly) {
      return !!(user && user.userId && ((user.noData && !user.reqPending) || (user.noDB && !cacheOnly)));
    };

    this.retrieveIncompleteUsers = function (elements, properties, cacheOnly) {
      if (!elements || !properties || !properties.length) {
        return;
      }
      if (!Array.isArray(elements)) {
        elements = [elements];
      }

      // Retrieve user data for the incomplete users (i.e. noData === true).
      var incompleteUsers = [];
      var helperHash = {};

      var checkUser = function (user) {
        if (_self.isIncompleteUser(user, cacheOnly) && !helperHash[user.userId]) {
          incompleteUsers.push(user);
          helperHash[user.userId] = true;
        }
      };

      elements.forEach(function (element) {
        element &&
          properties.forEach(function (property) {
            checkUser(element[property]);
          });
      });

      if (incompleteUsers.length > 0) {
        LogSvc.debug('[UserSvc]: Retrieve the data for incomplete users. Total = ', incompleteUsers.length);
        _self.addUsersToCache(incompleteUsers, !!cacheOnly, true);
      }
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.UserSvcImpl = UserSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/* global require*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var CallState = unifyPhone.Enums.CallState;
  var Constants = unifyPhone.Constants;
  var Utils = unifyPhone.Utils;

  ///////////////////////////////////////////////////////////////////////////////////////
  // NotificationType enum
  ///////////////////////////////////////////////////////////////////////////////////////
  var NotificationType = Object.freeze({
    INCOMING_VOICE_CALL: 'IncomingVoiceCall',
    INCOMING_VIDEO_CALL: 'IncomingVideoCall',
    INCOMING_MESSAGE: 'IncomingMessage',
    SNOOZING_MESSAGE: 'SnoozingMessage',
    HIGH_PRIORITY: 'HighPriority'
  });

  var NotificationActionType = Object.freeze({
    ANSWER: 'answer',
    DECLINE: 'decline',
    IGNORE: 'ignore',
    MUTE: 'mute',
    UN_MUTE: 'un-mute'
  });

  // eslint-disable-next-line max-params, max-lines-per-function
  function NotificationSvcImpl($rootScope, $timeout, $window, LogSvc, PubSubSvc, UserSvc) {
    // NOSONAR
    LogSvc.debug('New Service: NotificationSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var DISPLAY_NOTIFICATION_SAFETY_TIMEOUT = 60000;
    var DISPLAY_NOTIFICATION_TIME = 5000;
    var MAX_TEXT_LENGTH = 80;
    var MAX_TITLE_LENGTH = 32;
    var PERMISSION_GRANTED = 'granted';
    var PERMISSION_DEFAULT = 'default';
    var PERMISSION_DENIED = 'denied';
    var SYSTEM_NOTIFICATION_ICON = 'content/images/logo-toast.png';

    var _onFocus = Utils.appIsFocused(true);
    var _doNotDisturbCall = null;
    var _enableNotifications = false;
    var _self = this;

    // Native OS notifications in browsers are used on Mac (Chrome, FF).
    // Chrome on Windows 10 also uses native notifications. TODO: Add it to _useNativeWebNotifications variable
    // when https://bugs.chromium.org/p/chromium/issues/detail?id=936796 is fixed
    // TODO: Firefox v67 uses native notification on Win10. Check and update code when v67 released
    var _useNativeWebNotifications = Utils.isMacOs;
    var _conversationNotifications = {};

    var _isNotificationActionsSupported = false; // Set to true when notification service worker registers

    // We only need to check the actual permission when loading the app
    var _notificationPermission = $window.Notification && $window.Notification.permission;

    var _that = this;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function limitTextLength(text, limit) {
      if (text && text.length > limit) {
        text = text.substring(0, limit) + ' …';
      }
      return text;
    }

    function getNotificationText(item, conversation, user) {
      if (!item) {
        return '';
      }
      var text =
        item.plainTextContent ||
        (item.text && item.text.subject) ||
        (item.text && item.text.preview && item.text.preview.srcURL) ||
        (item.rtc && item.rtc.type === Constants.RTCItemType.MISSED && $rootScope.i18n.map.res_CallMissed);

      var colon = ': ';

      if (!text) {
        var numFiles = item.files ? item.files.length : 0;
        var numImages = item.images ? item.images.length : 0;
        var numVideos = item.videos ? item.videos.length : 0;
        var numAudios = item.audios ? item.audios.length : 0;
        var totalAttachments = numFiles + numImages + numVideos + numAudios;

        colon = ' ';

        if (numVideos && numVideos === totalAttachments) {
          // The attachements are videos only. One or more
          if (numVideos > 1) {
            text = $rootScope.i18n.localize('res_NotificationSharedVideos', [numVideos]);
          } else {
            text = $rootScope.i18n.map.res_NotificationSharedVideo;
          }
        } else if (numImages && numImages === totalAttachments) {
          // The attachments are images only. One or more
          if (numImages > 1) {
            text = $rootScope.i18n.localize('res_NotificationSharedImages', [numImages]);
          } else {
            text = $rootScope.i18n.map.res_NotificationSharedImage;
          }
        } else if (totalAttachments) {
          if (totalAttachments > 1) {
            text = $rootScope.i18n.localize('res_NotificationSharedFiles', [totalAttachments]);
          } else {
            text = $rootScope.i18n.map.res_NotificationSharedFile;
          }
        } else {
          LogSvc.error('[NotificationSvc]: Incoming message without text, attachments or URL preview.');
          return '';
        }
      }

      if (conversation && user && conversation.type !== Constants.ConversationType.DIRECT) {
        text = user.firstName + colon + text;
      }

      return text;
    }

    function getPhoneTitle(extras, user) {
      // Get phone number from phoneNumber or from call object
      var phoneNumber = extras.phoneNumber || (extras.call && extras.call.peerUser && extras.call.peerUser.phoneNumber);
      var title = user.displayName || extras.displayName;
      if (phoneNumber) {
        title = title ? title + ' (' + phoneNumber + ')' : phoneNumber;
      }
      return title;
    }

    function isTelephonyCall(extras) {
      return (extras.conversation && extras.conversation.isTelephony) || (extras.call && extras.call.isTelephonyCall);
    }

    function prepareIncomingCallNoti(noti, notificationType, user, extras) {
      if (isTelephonyCall(extras)) {
        noti.title = getPhoneTitle(extras, user);
      } else {
        noti.title = user.displayName || extras.displayName;
      }
      if (extras.call && extras.call.pickupNotification) {
        noti.text = $rootScope.i18n.localize('res_CallPickupNotification', [
          extras.call.redirectingUser.displayName || extras.call.redirectingUser.phoneNumber
        ]);
      } else {
        noti.text =
          notificationType === NotificationType.INCOMING_VOICE_CALL ? $rootScope.i18n.map.res_IncomingCall : $rootScope.i18n.map.res_IncomingVideoCall;
      }
      noti.timeout = DISPLAY_NOTIFICATION_SAFETY_TIMEOUT;
    }

    function prepareIncomingMessageNoti(noti, user, extras) {
      if (extras.conversation && extras.conversationItem) {
        if (extras.conversation.isTelephony) {
          noti.title = getPhoneTitle(extras, user);
        } else {
          noti.title = extras.conversation.topic || extras.conversation.participantFirstNames || user.displayName;
        }
        noti.text = getNotificationText(extras.conversationItem, extras.conversation, user);
      }
    }

    function buildNotification(notificationType, user, extras) {
      if (!notificationType || !user) {
        return null;
      }
      extras = extras || {};

      var noti = {
        timeout: DISPLAY_NOTIFICATION_TIME,
        icon: extras.toastAvatar || user.avatarLarge
      };

      LogSvc.debug('[NotificationSvc]: Build notification with notification type ', notificationType);

      switch (notificationType) {
        case NotificationType.INCOMING_VOICE_CALL:
        case NotificationType.INCOMING_VIDEO_CALL:
          if (extras.call && extras.call.state !== CallState.Ringing) {
            // If call is no longer ringing, don't show notification
            return null;
          }
          prepareIncomingCallNoti(noti, notificationType, user, extras);
          break;

        case NotificationType.INCOMING_MESSAGE:
          prepareIncomingMessageNoti(noti, user, extras);
          break;

        case NotificationType.SNOOZING_MESSAGE:
        case NotificationType.HIGH_PRIORITY:
          if (extras.title && extras.text) {
            noti.icon = SYSTEM_NOTIFICATION_ICON;
            noti.title = extras.title;
            noti.text = extras.text;
          }
          break;

        default:
          LogSvc.error('[NotificationSvc]: Invalid or not supported notification type: ', notificationType);
          break;
      }

      if (!noti.text) {
        LogSvc.error('[NotificationSvc]: Missing mandatory parameter for notification type: ', notificationType);
        return null;
      }

      noti.title = limitTextLength(noti.title, MAX_TITLE_LENGTH);
      noti.text = limitTextLength(noti.text, MAX_TEXT_LENGTH);
      return noti;
    }

    function dismiss(notification, cb) {
      cb = cb || function () {};
      if (notification) {
        LogSvc.debug('[NotificationSvc]: Dismiss active notification');

        closeNotification(notification); // close web notification - NGTC-15205: PRB000086546 immediate name resolution

        // This is not needed in UP
        navigator.serviceWorker.ready.then(function (registration) {
          // Get notification by callId and close it
          registration
            .getNotifications({
              tag: notification.callId
            })
            .then(function (notifications) {
              closeNotification(notifications[0]);
              cb();
            })
            .catch(cb);
        });
      } else {
        cb();
      }
    }

    function createNotification(type, user, extras) {
      extras = extras || {};

      var notification = buildNotification(type, user, extras);
      if (!notification || !notification.text) {
        return;
      }

      if (!extras.call || !extras.call.pickupNotification) {
        LogSvc.debug('[NotificationSvc]: Publish /notification/show event');
        PubSubSvc.publish('/notification/show', [type, extras.conversation]);
      }

      var actions;
      var callId = extras.call && extras.call.callId;
      var convId = extras.conversation && extras.conversation.convId;

      if (_isNotificationActionsSupported && isCallNotification(type)) {
        actions = [{ action: NotificationActionType.ANSWER, title: $rootScope.i18n.map.res_Answer }];

        if (extras.call.pickupNotification) {
          actions.push({ action: NotificationActionType.IGNORE, title: $rootScope.i18n.map.res_Ignore });
        } else {
          actions.push({ action: NotificationActionType.DECLINE, title: $rootScope.i18n.map.res_Decline });
        }
      }

      if (actions) {
        LogSvc.info('[NotificationSvc]: Showing service worker notification with title ', notification.title);
        extras.call.activeNotification = {
          callId: callId,
          title: notification.title
        };
        navigator.serviceWorker.ready.then(function (registration) {
          registration.showNotification(notification.title, {
            body: notification.text,
            icon: notification.icon,
            actions: actions,
            tag: callId,
            requireInteraction: true
          });
        });
        return;
      }

      LogSvc.info('[NotificationSvc]: Showing notification with title ', notification.title);
      var options = {
        windowTitle: $rootScope.i18n.map.res_AccessibilityUnifyPhoneNotification,
        icon: notification.icon,
        body: notification.text
      };
      // Show action buttons in Desktop notifications for calls (Windows only)
      var hash = Utils.rstring();

      var webNotification = new $window.Notification(notification.title, options);

      if (_useNativeWebNotifications && convId) {
        if (!_conversationNotifications[convId]) {
          _conversationNotifications[convId] = [];
        }
        _conversationNotifications[convId].push(webNotification);
      }

      // Start timer to prevent the notification from staying opened indefinitely
      // Native notifications are closed automatically by the system
      var notificationTimer =
        !_useNativeWebNotifications &&
        $timeout(function () {
          LogSvc.debug('[NotificationSvc]: Close notification after timeout');
          notificationTimer = null;
          webNotification.close(hash);
        }, notification.timeout);

      webNotification.onclose = function () {
        LogSvc.debug('[NotificationSvc]: Notification has been closed');
        !_useNativeWebNotifications && $timeout.cancel(notificationTimer);
        webNotification.onclick = null;
        webNotification.onclose = null;
      };

      webNotification.onclick = function () {
        if (_useNativeWebNotifications) {
          webNotification.close();
        } else {
          $timeout.cancel(notificationTimer);
          closeNotification(webNotification);
        }
        onClick(type, extras.conversation);
      };

      if (extras.call) {
        extras.call.activeNotification = webNotification;
      }
    }

    function closeNotification(notification) {
      if (notification) {
        notification.close();
      }
    }

    function onClick(type, conversation) {
      LogSvc.debug('[NotificationSvc]: User clicked on notification. Bring window back to focus.');

      if (
        conversation &&
        (type === NotificationType.INCOMING_MESSAGE || type === NotificationType.INCOMING_VOICE_CALL || type === NotificationType.INCOMING_VIDEO_CALL)
      ) {
        // Click on incoming notification:
        //   - Navigate to message's conversation
        //   - Reset last scroll position and update conversation feed (only text message)
        //   - Clear search results if any

        $rootScope.$apply(function () {
          PubSubSvc.publish('/conversation/navigate', [conversation, type === NotificationType.INCOMING_MESSAGE]);
          PubSubSvc.publish('/search/clear');
        });
      }

      $window.focus();
    }

    function isCallNotification(type) {
      return type === NotificationType.INCOMING_VOICE_CALL || type === NotificationType.INCOMING_VIDEO_CALL;
    }

    function closeAllNotifications() {
      if (!_useNativeWebNotifications) {
        return;
      }

      LogSvc.debug('[NotificationSvc]: Clear all notifications');
      Object.values(_conversationNotifications).forEach(function (notiArray) {
        notiArray &&
          notiArray.forEach(function (noti) {
            closeNotification(noti);
          });
      });
      _conversationNotifications = {};
    }

    function canShowNotification(options) {
      if (_notificationPermission !== PERMISSION_GRANTED) {
        // Permission has not been granted. Don't show notification.
        LogSvc.debug('[NotificationSvc]: show(): User has not granted permission to show notifications');
        return false;
      }

      if (options.type !== NotificationType.HIGH_PRIORITY) {
        var showCallNotification = false;

        if (_onFocus === true && !_enableNotifications && !showCallNotification) {
          // Window is in focus. Don't show notification (except for call notifications with action buttons).
          LogSvc.debug('[NotificationSvc]: show(): No permission or window is in focus');
          return false;
        }

        // Check if DND call is in progress or snoozed notifications
        if (
          _doNotDisturbCall ||
          ($rootScope.localUser.userPresenceState.state === Constants.PresenceState.DND && options.type !== NotificationType.SNOOZING_MESSAGE)
        ) {
          LogSvc.debug('[NotificationSvc]: Notification not shown because it is snoozed or there is a DND call in progress.');
          return false;
        }
      }
      return true;
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    $window.addEventListener('focus', function () {
      _onFocus = true;
    });

    $window.addEventListener('blur', function () {
      _onFocus = false;
    });

    $window.addEventListener('beforeunload', function () {
      closeAllNotifications();
    });

    function onCallState(call) {
      LogSvc.debug('[NotificationSvc]: Received /call/state event');
      if (call.hasLocalScreenShare()) {
        _doNotDisturbCall = call;
      } else if (_doNotDisturbCall === call) {
        _doNotDisturbCall = null;
      }
      if (call.activeNotification) {
        if (call.isEstablished()) {
          dismiss(call.activeNotification);
          call.activeNotification = null;
        } else if (call.isTelephonyCall && call.state === CallState.Ringing) {
          var data = {
            extras: { call: call }
          };
          if (call.peerUser) {
            data.userId = call.peerUser.userId;
            if (!data.userId) {
              data.user = call.peerUser;
            }
            data.extras.phoneNumber = call.peerUser.phoneNumber;
            data.extras.displayName = call.peerUser.displayName;
          }
          var updatedNotification = buildNotification(NotificationType.INCOMING_VOICE_CALL, data.user, data.extras);
          if (updatedNotification.title && updatedNotification.title !== call.activeNotification.title) {
            dismiss(call.activeNotification, function () {
              call.activeNotification = null;
              _that.show({
                type: NotificationType.INCOMING_VOICE_CALL,
                user: data.user,
                extras: data.extras
              });
            });
          }
        }
      }
    }

    function onCallEnded(call) {
      LogSvc.debug('[NotificationSvc]: Received /call/ended event. callId =', call.callId);
      if (_doNotDisturbCall === call) {
        _doNotDisturbCall = null;
      }
      if (call.activeNotification) {
        dismiss(call.activeNotification);
        call.activeNotification = null;
      }
    }

    function onUserBecameAvailable(user, status) {
      if (!user || !status) {
        return;
      }
      LogSvc.info('[NotificationSvc]: Received /user/status/notification event.');
      _self.show({
        type: status,
        user: user,
        userId: user.userId
      });
    }

    function onNotificationClick(data) {
      LogSvc.info('[NotificationSvc]: Received /notification/click event.');
      onClick(data.type, data.convId);
    }

    function onTotalUnreadCountChanged(unreadCount, data) {
      var notifications = data && data.convId && _conversationNotifications[data.convId];
      if (!notifications || !notifications.length || !data.markAsRead) {
        return;
      }
      LogSvc.debug('[NotificationSvc]: Clear notifications for ', data.convId);
      notifications.forEach(function (notification) {
        closeNotification(notification);
      });
      delete _conversationNotifications[data.convId];
    }

    function onServiceWorkerRegistered(isSuccess) {
      LogSvc.info('[NotificationSvc]: /serviceWorker/registered event is received. Success=', isSuccess);
      if (!isSuccess) {
        return;
      }

      navigator.serviceWorker.ready
        .then(function (reg) {
          if (!reg) {
            return;
          }

          LogSvc.info('[NotificationSvc]: Notification service worker is registered');
          _isNotificationActionsSupported = true;

          navigator.serviceWorker.addEventListener('message', function (event) {
            LogSvc.debug('[NotificationSvc]: Message from SW received. Data: ', event.data);
            if (event.data.eventName === 'notificationAction' && event.data.callId) {
              $rootScope.$apply(function () {
                LogSvc.debug('[NotificationSvc]: Publish /call/action event');
                PubSubSvc.publish('/call/action', [event.data]);
              });
            }
          });
        })
        .catch(function (error) {
          LogSvc.error('[NotificationSvc]: Service worker registration failed. ' + error);
        });
    }

    PubSubSvc.subscribe('/call/state', onCallState);
    PubSubSvc.subscribe('/call/ended', onCallEnded);
    PubSubSvc.subscribe('/user/status/notification', onUserBecameAvailable);
    PubSubSvc.subscribe('/notification/click', onNotificationClick);
    if (_useNativeWebNotifications) {
      PubSubSvc.subscribe('/conversations/totalUnreadCountChanged', onTotalUnreadCountChanged);
    }
    if (Utils.getBrowserInfo().chrome && 'serviceWorker' in navigator) {
      PubSubSvc.subscribeOnce('/serviceWorker/registered', onServiceWorkerRegistered);
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.show = function (options) {
      options = options || {};

      if ($window.Notification) {
        LogSvc.debug('[NotificationSvc]: show(): userId: ' + options.userId + ' type: ' + options.type);
        LogSvc.debug('[NotificationSvc]: show(): Permission: ' + _notificationPermission + ', window has focus = ' + _onFocus);

        if (!canShowNotification(options)) {
          return;
        }

        var user = options.user;
        if (!user && options.userId) {
          if (options.userId === $rootScope.localUser.userId) {
            user = $rootScope.localUser;
          } else {
            user = UserSvc.getUserFromCache(options.userId);
          }
        }

        if (user && user.noData) {
          UserSvc.getUserById(user.userId, function (err, retrievedUser) {
            createNotification(options.type, retrievedUser, options.extras);
          });
        } else {
          createNotification(options.type, user, options.extras);
        }
      } else {
        LogSvc.debug('[NotificationSvc]: Notifications not supported');
      }
    };

    this.hasPermissionBeenSet = function () {
      return _notificationPermission !== PERMISSION_DEFAULT;
    };

    this.isPermissionDenied = function () {
      return _notificationPermission === PERMISSION_DENIED;
    };

    this.requestPermission = function (cb) {
      if (_notificationPermission !== PERMISSION_DEFAULT || !$window.Notification) {
        // Callback must be async since callback below is also async
        $timeout(function () {
          cb && cb(_notificationPermission !== PERMISSION_DEFAULT);
        }, 0);
        return;
      }
      $window.Notification.requestPermission(function (permission) {
        LogSvc.debug('[NotificationSvc]: Notification permission requested. Reponse: ', permission);
        _notificationPermission = permission;
        cb && cb(_notificationPermission !== PERMISSION_DEFAULT);
      });
    };

    this.dismiss = dismiss;

    this.getNotificationText = getNotificationText;

    this.limitTextLength = limitTextLength;

    this.isAppInFocus = function () {
      return _onFocus;
    };

    this.enableNotifications = function (bool) {
      _enableNotifications = bool;
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.NotificationType = NotificationType;
  unifyPhone.Enums.NotificationActionType = NotificationActionType;
  unifyPhone.NotificationSvcImpl = NotificationSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global ControllerRouteError, RegistrationState*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var Constants = unifyPhone.Constants;
  var Conversation = unifyPhone.Conversation;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-params, max-lines-per-function
  function ConversationSvcImpl($rootScope, $q, $timeout, LogSvc, PubSubSvc) {
    // NOSONAR

    LogSvc.debug('New Service: ConversationSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _self = this;

    var _clientApiHandler = ClientApiHandler.getInstance();

    var _conversationsLoadComplete = false;
    var _telephonyConvId;
    var _telephonyConv;
    var _telephonyConvUnreadData = { unreadItems: 0 };
    var _totalUnreadMessages = 0; // Used for browser title display and conversation floater display
    var _readyToProcessServerEvents = false; // We need this flag so we won't process server events during initialization

    ///////////////////////////////////////////////////////////////////////////////////////
    // Cache Objects
    ///////////////////////////////////////////////////////////////////////////////////////
    var _conversations = []; // Not sorted
    var _conversationsHashtable = {}; // Hashtable of conversation IDs to conversation objects

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function clearConversationSvcCache() {
      // Clear the cache objects
      Utils.emptyArray(_conversations);
      _conversationsHashtable = {};

      // Now reset the other internal variables
      _conversationsLoadComplete = false;
      _totalUnreadMessages = 0;
    }

    function getTelephonyConv() {
      if (!_telephonyConv) {
        var conversation = {
          convId: _telephonyConvId,
          creationTime: Date.now(),
          creatorId: $rootScope.localUser.userId,
          creatorTenantId: $rootScope.localUser.tenantId,
          isTelephony: true,
          participants: [
            {
              userId: $rootScope.localUser.userId
            },
            {
              userId: $rootScope.localUser.associatedTelephonyUserID
            }
          ],
          rtcSessionId: _telephonyConvId,
          topic: $rootScope.i18n.localize('res_PhoneCalls'),
          type: Constants.ConversationType.DIRECT,
          userData: _telephonyConvUnreadData
        };
        _telephonyConv = Conversation.extend(conversation);
        _conversationsHashtable[_telephonyConvId] = _telephonyConv;
        _conversations.push(_telephonyConv);
      }
      return _telephonyConv;
    }

    function resolveTelephonyConversationPromise(deferred) {
      if (!_telephonyConvId) {
        deferred.reject(ControllerRouteError.CONVERSATION_NOT_FOUND);
      } else {
        deferred.resolve(getTelephonyConv());
      }
    }

    function resolveConversationPromise(deferred, convId) {
      if (!_telephonyConvId || convId !== _telephonyConv) {
        deferred.reject(ControllerRouteError.CONVERSATION_NOT_FOUND);
      } else {
        deferred.resolve(getTelephonyConv());
      }
    }

    function setTelephonyConvId(telephonyConvId) {
      if (_telephonyConvId !== telephonyConvId) {
        _telephonyConvId = telephonyConvId || null;
        LogSvc.info('[ConversationSvc]: Set _telephonyConvId to ', _telephonyConvId);
        LogSvc.debug('[ConversationSvc]: Publish /telephony/enabled event: ', !!_telephonyConvId);
        PubSubSvc.publish('/telephony/enabled', !!_telephonyConvId);
      }
    }

    function setUnreadData(unreadItems, lastReadTimestamp) {
      _telephonyConvUnreadData.unreadItems = unreadItems || 0;
      _telephonyConvUnreadData.lastReadTimestamp = lastReadTimestamp;
      if (_telephonyConv) {
        var baseConv = _telephonyConv;
        // Need to update these in case this is a reconnection and properties have changed
        if (_telephonyConv.isExtended) {
          baseConv = Utils.getBaseObject(_telephonyConv, true);
        }
        baseConv.userData.unreadItems = _telephonyConvUnreadData.unreadItems;
        baseConv.userData.lastReadTimestamp = _telephonyConvUnreadData.lastReadTimestamp;
      }
      //Used by mobiles to update indicator
      LogSvc.debug('[ConversationSvc]: Publish /unread/changed event');
      PubSubSvc.publish('/unread/changed', _telephonyConvUnreadData.unreadItems);

      _totalUnreadMessages = _telephonyConvUnreadData.unreadItems;

      LogSvc.debug('[ConversationSvc]: Publish /conversations/totalUnreadCountChanged event');
      PubSubSvc.publish('/conversations/totalUnreadCountChanged', _totalUnreadMessages);
    }

    function setTotalUnreadCount() {
      // Each conversation contains its unread count.
      var count = 0;
      _conversations.forEach(function (c) {
        if (c.isTelephony && !$rootScope.localUser.telephonyAvailable) {
          return;
        }
        if (c.userData && !c.muted && !c.hidden) {
          count += c.userData.unreadItems || 0;
        }
      });

      if (count !== _totalUnreadMessages) {
        _totalUnreadMessages = count;
        LogSvc.debug('[ConversationSvc]: Set total unread count = ', _totalUnreadMessages);
        LogSvc.debug('[ConversationSvc]: Publish /conversations/totalUnreadCountChanged event');
        PubSubSvc.publish('/conversations/totalUnreadCountChanged', _totalUnreadMessages);
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Properties
    ///////////////////////////////////////////////////////////////////////////////////////
    // Define the read-only properties to access the internal variables
    Object.defineProperties(this, {
      showTelephonyConversation: {
        get: function () {
          return !!($rootScope.localUser && $rootScope.localUser.telephonyAvailable);
        },
        enumerable: true,
        configurable: !!unifyPhone.isUnitTestRun
      },
      telephonyConvId: {
        get: function () {
          return _telephonyConvId;
        },
        enumerable: true,
        configurable: !!unifyPhone.isUnitTestRun
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/registration/state', function (state) {
      LogSvc.debug('[ConversationSvc]: Received /registration/state event');
      if (state === RegistrationState.LoggingOut) {
        clearConversationSvcCache();
      } else if (state !== RegistrationState.Registered && _readyToProcessServerEvents) {
        // If we disconnect, don't process server events until we reconnect and finish the
        // initialization sequence again
        LogSvc.warn('[ConversationSvc]: Not processing Conversation events until registered again');
        _readyToProcessServerEvents = false;
      }
    });

    function onRegistrationStuff(stuff) {
      LogSvc.debug('[ConversationSvc]: Received /registration/stuff event');

      setTelephonyConvId(stuff.telephonyConvId);
      setUnreadData(stuff.journalUnreadMissedCount, stuff.journalLastTimeStamp);
    }

    PubSubSvc.subscribe('/registration/stuff', onRegistrationStuff);

    PubSubSvc.subscribe('/users/loaded', function () {
      // We load the users and labels before we load the conversations
      LogSvc.debug('[ConversationSvc]: Received /users/loaded event');
      _conversationsLoadComplete = false;

      // We don't need to load anything since there are no conversations in NGTC, so just publish the
      // necessary events in a different event cycle.
      $timeout(function () {
        _conversationsLoadComplete = true;
        LogSvc.debug('[ConversationSvc]: Publish /conversations/loadComplete event');
        PubSubSvc.publish('/conversations/loadComplete');
      }, 0);
    });

    PubSubSvc.subscribe('/conversations/showTelephonyConversation', function () {
      LogSvc.debug('[ConversationSvc]: Received /conversations/showTelephonyConversation event');
      setTotalUnreadCount();
    });

    PubSubSvc.subscribe('/language/update', function () {
      var conv = _telephonyConvId && _conversationsHashtable[_telephonyConvId];
      if (conv) {
        conv.topic = $rootScope.i18n.localize('res_PhoneCalls');
        conv.topicEscaped = conv.topic;
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    /**
     * Checks if the conversations have already been fully loaded after startup.
     *
     * @returns {Boolean} Return true if the conversations loading is complete.
     */
    this.isConversationsLoadComplete = function () {
      return _conversationsLoadComplete;
    };

    /**
     * Get a promise that is resolved as soon as the conversations hasve been fully loaded after startup.
     *
     * @returns {object} Promise object resolving a conversation.
     */
    this.getConversationsLoadCompletePromise = function () {
      return new $q(function (resolve) {
        if (_conversationsLoadComplete) {
          resolve();
        } else {
          PubSubSvc.subscribeOnce('/conversations/loadComplete', function () {
            resolve();
          });
        }
      });
    };

    /**
     * Get a promised conversation. Promise is resolved once the
     * conversation is available on the client. Promise is rejected
     * if the conversation is accessible by the logged on user.
     *
     * @params {String} The conversation id to fetch
     * @returns {object} Promise object resolving a conversation.
     */
    this.getConversationPromise = function (convId) {
      var deferred = $q.defer();
      if (_conversationsLoadComplete) {
        resolveConversationPromise(deferred, convId);
      } else {
        PubSubSvc.subscribeOnce('/conversations/loadComplete', function () {
          resolveConversationPromise(deferred, convId);
        });
      }
      return deferred.promise;
    };

    /**
     * Get list of active and remote calls for selector use.
     *
     * @returns {Object} Object containing an array of call objects
     */
    this.getCallsForSelector = function (includeArchived) {
      var calls = [];
      _conversations.forEach(function (conv) {
        if (conv.call && (includeArchived || !conv.muted || !conv.call.isGroupCallStarted)) {
          calls.push(conv.call);
        }
      });

      if (Utils.isMobile()) {
        // Return a lightweight call object without data that is not needed
        // by the mobile applications.
        calls = calls.map(Utils.trimCallForMobile);
      }

      return calls;
    };

    /**
     * Get the conversation that is in cache with the specified convId.
     *
     * @param {String} convId The convId of the conversation that needs to be returned.
     * @returns {Conversation} The conversation object with specified convId or null.
     */
    this.getConversationFromCache = function (convId) {
      return (convId && _conversationsHashtable[convId]) || null;
    };

    /**
     * Get the conversation from server with specified convId.
     *
     * @param {String} convId The convId of the conversation that needs to be returned.
     * @param {Object} options
     *   randomLoaded (default = true): Randomly means this conversation could be out of sequence, e.g. via search or via URL loading
     *   loadItems (default = false): Load the conversation items.
     * @param {Function} cb The callback function when the conversation is returned from server.
     */
    this.getConversationById = function (convId, options, cb) {
      if (_telephonyConvId && convId === _telephonyConvId) {
        cb(null, getTelephonyConv());
      } else {
        cb(ControllerRouteError.CONVERSATION_NOT_FOUND);
      }
    };

    /**
     * Get the conversations with specified conversation IDs. If the conversation is not yet cached, the
     * client will retrieve it from the server.
     *
     * @returns {Promise}
     */
    this.getConversationsByIds = function () {
      return $q.reject('OBSOLETE');
    };

    this.getConversationByRtcSession = function (rtcSessionId) {
      var id = rtcSessionId.split('#')[0];
      return _conversations.find(function (conv) {
        return conv.rtcSessionId === id;
      });
    };

    /**
     * Flags recording within an rtc item of an existing conversation.
     *
     * @param {String} itemId of a conversation item.
     * @param {Boolean} extendedDelay If true, the extended delay will be applied to delete the RTC item by data retention.
     *  If false, the default delay will be applied to delete the RTC item by data retention.
     * @returns {Promise} Promise indicating that the operation has been completed.
     */
    this.setRecordingDeletionDelay = function (itemId, extendedDelay) {
      if (!itemId) {
        return $q.reject('Invalid itemId');
      }

      if (!$rootScope.localUser.recordingDeletionEnabledTenant) {
        return $q.reject('Operation not allowed');
      }

      return new $q(function (resolve, reject) {
        _clientApiHandler.setRecordingDeletionDelay(itemId, extendedDelay, function (err) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.error('[ConversationSvc]: Error delaying recording deletion. itemId: ', itemId);
              reject(err);
            } else {
              resolve();
            }
          });
        });
      });
    };

    this.addParticipant = function (convId, participant, addToCall, cb) {
      cb && cb('OBSOLETE');
    };

    this.removeParticipant = function (convId, userId, cb) {
      cb && cb('OBSOLETE');
    };

    this.getParticipants = function () {
      return $q.reject('OBSOLETE');
    };

    /**
     * Get a list of conversation ids with call.
     *
     * @returns {String[]} A list of conversation ids.
     */
    this.getConversationIdsWithCall = function () {
      var convIds = [];
      _conversations.forEach(function (c) {
        if (c.call) {
          convIds.push(c.convId);
        }
      });
      return convIds;
    };

    /**
     * Get telephony conversation promise
     *
     * @returns {Promise}
     */
    this.getTelephonyConversationPromise = function () {
      var deferred = $q.defer();
      if (_conversationsLoadComplete) {
        resolveTelephonyConversationPromise(deferred);
      } else {
        PubSubSvc.subscribeOnce('/conversations/loadComplete', function () {
          resolveTelephonyConversationPromise(deferred);
        });
      }
      return deferred.promise;
    };

    /**
     * Get telephony conversation
     *
     * @param {Function} cb The callback function invoked when telephony conversation is retrieved from local cache or returned from server.
     */
    this.getTelephonyConversation = function (cb) {
      if (typeof cb !== 'function') {
        return;
      }
      _self
        .getTelephonyConversationPromise()
        .then(function (conv) {
          cb(null, conv);
        })
        .catch(function (err) {
          cb(err);
        });
    };

    this.getCachedTelephonyConversation = function () {
      if (!_telephonyConvId) {
        return null;
      }
      return _conversationsHashtable[_telephonyConvId] || null;
    };

    /**
     * Validates the given session token and retrieves the associated conversation. If you also want to
     * retrieve the custom acceptance text that must be presented to the user you need to populate the
     * textLanguage parameter with the desired language.
     *
     * @param {String} token The session invite token.
     * @param {Function} cb Callback returning the validation result.
     * @param {String} textLanguage The language of the custom acceptance text that must be presented to the user joining the session.
     */
    this.validateSessionInviteToken = function (token, cb, textLanguage) {
      _clientApiHandler.validateSessionInvite(token, textLanguage, function (err, sessionData) {
        $rootScope.$apply(function () {
          if (textLanguage && sessionData && sessionData.configurableTexts) {
            sessionData.acceptanceText = Utils.extractConfigurableText(
              sessionData.configurableTexts,
              Constants.TenantConfigurableTextType.GUEST_ACCESS_ACCEPTANCE_TEXT,
              textLanguage
            );
          }
          cb(err, sessionData);
        });
      });
    };

    /**
     * Validates the given session PIN and retrieves the associated conversation. If you also want to
     * retrieve the custom acceptance text that must be presented to the user you need to populate the
     * textLanguage parameter with the desired language.
     *
     * @param {String} pin The session invite PIN.
     * @param {String} textLanguage The language of the custom acceptance text that must be presented to the user joining the session.
     * @returns {Promise} Validation result
     */
    this.validateSessionInvitePin = function (pin, textLanguage) {
      return new $q(function (resolve, reject) {
        _clientApiHandler.validateSessionPin(pin, textLanguage, function (err, sessionData) {
          $rootScope.$apply(function () {
            if (err) {
              reject(err);
              return;
            }
            if (textLanguage && sessionData && sessionData.configurableTexts) {
              sessionData.acceptanceText = Utils.extractConfigurableText(
                sessionData.configurableTexts,
                Constants.TenantConfigurableTextType.GUEST_ACCESS_ACCEPTANCE_TEXT,
                textLanguage
              );
            }
            resolve(sessionData);
          });
        });
      });
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.ConversationSvcImpl = ConversationSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global ControllerRouteError*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var CallLogItem = unifyPhone.CallLogItem;
  var JournalFilter = unifyPhone.Constants.JournalFilter;
  var Utils = unifyPhone.Utils;
  var NotificationType = unifyPhone.Enums.NotificationType;

  // eslint-disable-next-line max-params, max-lines-per-function
  function PhoneCallLogSvcImpl($rootScope, $q, ConversationSvc, LogSvc, PubSubSvc, UserSvc, NotificationSvc) {
    // NOSONAR

    LogSvc.debug('New Service: PhoneCallLogSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var DEFAULT_NUMBER_OF_ENTRIES = 25;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _clientApiHandler = ClientApiHandler.getInstance();
    var _showTelephonyConvReceived = false;
    var _telephonyConversation;

    var _cachedItemsHash = {};
    var _cachedData = {};
    var _unheardVoicemailCount = -1;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function clearCachedData(hasOldestItem) {
      Object.keys(JournalFilter).forEach(function (filter) {
        _cachedData[filter] = {
          cachedItems: [],
          hasOldestItem: !!hasOldestItem
        };
      });
      _cachedItemsHash = {};
    }

    function resolveTelephonyConversationPromise(deferred) {
      if ($rootScope.localUser.telephonyAvailable && _telephonyConversation) {
        deferred.resolve(_telephonyConversation);
      } else {
        deferred.reject(ControllerRouteError.CONVERSATION_NOT_FOUND);
      }
    }

    function getApplicableFilters(item) {
      var filters = [];

      if (item.sentByMe) {
        filters.push(JournalFilter.DIALED);
      } else if (item.redirectedToUser) {
        filters.push(JournalFilter.DIVERTED);
        if (item.callLogType === unifyPhone.Enums.CallLogItemType.MISSED) {
          filters.push(JournalFilter.MISSED);
        }
      } else {
        if (item.callLogType === unifyPhone.Enums.CallLogItemType.MISSED) {
          filters.push(JournalFilter.MISSED);
        } else {
          filters.push(JournalFilter.RECEIVED);
        }
        if (item.hasVoicemail || (item.recordingUploadState && item.recordingUploadState.state === Constants.RecordingUploadState.IN_PROGRESS)) {
          filters.push(JournalFilter.VOICEMAILS);
          if (item.isVoicemailUnheard) {
            filters.push(JournalFilter.UNHEARD_VOICEMAILS);
          }
        }
      }
      return filters;
    }

    function retrieveIncompleteUsers(elements, cacheOnly) {
      UserSvc.retrieveIncompleteUsers(elements, ['peerUser', 'redirectedToUser', 'redirectingUser'], cacheOnly);
    }

    function itemExists(item, itemArray) {
      return itemArray.some(function (itemCached) {
        return itemCached.itemId === item.itemId;
      });
    }

    function processAddItem(item, activeFilter, skipUnreadCount) {
      if (!item || !item.rtc) {
        // Ignore
        return null;
      }
      // Check if the item is already cached
      var cachedItem = _cachedItemsHash[item.itemId];
      var update = !!cachedItem;
      if (!cachedItem) {
        cachedItem = CallLogItem.extend(item);
        _cachedItemsHash[item.itemId] = cachedItem;
      }

      if (!cachedItem.sendingState) {
        // Update the conversation modification times if this is not a failed message
        _telephonyConversation.lastItemModificationTime = Math.max(_telephonyConversation.lastItemModificationTime, cachedItem.modificationTime || 0);
        _telephonyConversation.lastModification = Math.max(_telephonyConversation.lastModification, _telephonyConversation.lastItemModificationTime);
        !skipUnreadCount && processUnreadItems(item);
      }

      resolvePhoneNumbersFromExchange(cachedItem);

      if (!activeFilter || activeFilter === JournalFilter.ALL) {
        // Update cache for all applicable filters
        var applicableFilters = getApplicableFilters(cachedItem);
        // Add to ALL filter as well
        applicableFilters.push(JournalFilter.ALL);
        applicableFilters.forEach(function (filter) {
          // NGTC-1594: If item is in cache but not in the _cached data of the filter
          // it should be inserted instead of updated.
          if (update && itemExists(cachedItem, _cachedData[filter].cachedItems)) {
            CallLogItem.updateItem(_cachedData[filter].cachedItems, cachedItem);
          } else {
            CallLogItem.insertItem(_cachedData[filter].cachedItems, cachedItem);
          }
        });
      } else {
        // Only update the active filter
        CallLogItem.insertItem(_cachedData[activeFilter].cachedItems, cachedItem);
        if (activeFilter === JournalFilter.VOICEMAILS) {
          if (!cachedItem.hasVoicemail) {
            LogSvc.warn('[PhoneCallLogSvc]: Voicemail call journal without voicemail. Id: ', cachedItem.itemId);
          }
          if (cachedItem.isVoicemailUnheard) {
            // Add item to UNHEARD_VOICEMAILS cache as well
            CallLogItem.insertItem(_cachedData[JournalFilter.UNHEARD_VOICEMAILS].cachedItems, cachedItem);
          }
        }
      }
      return cachedItem;
    }

    function removeCachedItem(item) {
      // Remove the item from all applicable caches
      Object.keys(JournalFilter).forEach(function (filter) {
        Utils.removeArrayElement(_cachedData[filter].cachedItems, item);
      });
    }

    function getCallJournalEntries(numberOfEntries, filter) {
      return new $q(function (resolve, reject) {
        LogSvc.debug('[PhoneCallLogSvc]: Get ' + numberOfEntries + ' call journal entries with filter ', filter);

        var cachedItems = _cachedData[filter].cachedItems;
        if (cachedItems.length >= numberOfEntries) {
          LogSvc.debug('[PhoneCallLogSvc]: Return cached journal entries');
          // Cached items are ordered from oldest to newest, so return the last items in the array
          resolve(cachedItems.slice(-numberOfEntries));
          return;
        }

        if (_cachedData[filter].hasOldestItem) {
          LogSvc.debug('[PhoneCallLogSvc]: All journal entries are cached for filter ', filter);
          resolve(cachedItems.slice());
          return;
        }

        var timestamp = cachedItems.length > 0 ? cachedItems[0].creationTime - 1 : null;
        var numToRetrieve = numberOfEntries - cachedItems.length;
        var getJournalData = {
          convId: _telephonyConversation.convId,
          timestamp: timestamp,
          direction: Constants.SearchDirection.BEFORE,
          numberOfEntries: numToRetrieve,
          journalFilter: filter
        };

        _clientApiHandler.getJournalEntries(getJournalData, function (err, entries) {
          if (err) {
            LogSvc.error('[PhoneCallLogSvc]: Failed to retrieve call journal entries. ', err);
            reject(err);
          } else {
            entries = entries || [];
            // Add items to cache
            var callLogItems = entries.map(function (item) {
              return processAddItem(item, filter, true);
            });
            retrieveIncompleteUsers(callLogItems);

            if (entries.length < numToRetrieve) {
              // We received less entries than we requested
              if (filter === JournalFilter.ALL) {
                // If we have the oldest item for the ALL cache then we must also have the
                // oldest item in all other caches.
                Object.keys(JournalFilter).forEach(function (key) {
                  _cachedData[key].hasOldestItem = true;
                });
              } else {
                _cachedData[filter].hasOldestItem = true;
                if (filter === JournalFilter.VOICEMAILS) {
                  // If we have all voicemails, we must have all UNHEARD_VOICEMAILS as well
                  _cachedData[JournalFilter.UNHEARD_VOICEMAILS].hasOldestItem = true;
                }
              }
            }
            resolve(cachedItems.slice());
          }
        });
      });
    }

    function addToUnheardCount(add) {
      if (_unheardVoicemailCount >= 0) {
        var newValue = _unheardVoicemailCount + add;
        newValue = newValue > 0 ? newValue : 0;
        if (_unheardVoicemailCount !== newValue) {
          _unheardVoicemailCount = newValue;
          LogSvc.debug('[PhoneCallLogSvc]: Publish /callHistory/unheardVoicemail/updated event: ', _unheardVoicemailCount);
          PubSubSvc.publish('/callHistory/unheardVoicemail/updated', [_unheardVoicemailCount]);
        }
      }
    }

    function handleUpdatedItem(item) {
      var cachedItem = _cachedItemsHash[item.itemId];
      if (!cachedItem) {
        return;
      }

      var hadVoicemail = cachedItem.hasVoicemail;
      var hadUnheardVoicemail = cachedItem.isVoicemailUnheard;

      var voicemailItems = _cachedData[JournalFilter.VOICEMAILS].cachedItems;
      var unheardVmItems = _cachedData[JournalFilter.UNHEARD_VOICEMAILS].cachedItems;

      // Update cached item
      CallLogItem.update(cachedItem, item);

      resolvePhoneNumbersFromExchange(cachedItem);

      if (!hadVoicemail && cachedItem.hasVoicemail) {
        // Voicemail has been attached to the item. Add to caches.
        LogSvc.debug('[PhoneCallLogSvc]: Add item to VOICEMAILS cache');
        CallLogItem.insertItem(voicemailItems, cachedItem);
        if (cachedItem.isVoicemailUnheard) {
          LogSvc.debug('[PhoneCallLogSvc]: Add item to UNHEARD_VOICEMAILS cache');
          CallLogItem.insertItem(unheardVmItems, cachedItem);
          addToUnheardCount(1);
        }
      } else if (hadVoicemail) {
        if (!cachedItem.hasVoicemail) {
          // Voicemail has been deleted. Remove from caches.
          LogSvc.debug('[PhoneCallLogSvc]: Remove item from VOICEMAILS cache');
          Utils.removeArrayElement(voicemailItems, cachedItem);
        }
        if (hadUnheardVoicemail && !cachedItem.isVoicemailUnheard) {
          // Voicemail has been heard or deleted. Remove from unheard cache.
          LogSvc.debug('[PhoneCallLogSvc]: Remove item from UNHEARD_VOICEMAILS cache');
          Utils.removeArrayElement(unheardVmItems, cachedItem);
          addToUnheardCount(-1);
        }
      }

      LogSvc.debug('[PhoneCallLogSvc]: Publish /callHistory/item/updated event');
      PubSubSvc.publish('/callHistory/item/updated', [cachedItem]);
    }

    function updateUserWithExchangeData(user) {
      if (user && user.userType === Constants.RTCParticipantType.TELEPHONY) {
        UserSvc.updateUserWithExchangeContactName(user);
      }
    }

    function resolvePhoneNumbersFromExchange(item) {
      if (item) {
        updateUserWithExchangeData(item.peerUser);
        updateUserWithExchangeData(item.redirectingUser);
        updateUserWithExchangeData(item.redirectedToUser);
      }
    }

    function deleteCachedItem(cachedItem) {
      removeCachedItem(cachedItem);
      delete _cachedItemsHash[cachedItem.itemId];
    }

    function processUnreadItems(item) {
      if (item.includeInUnreadCount && $rootScope.localUser.userId !== item.creatorId /*Do not use !item.sentByMe here, since it might not be set yet*/) {
        // Increment unread count
        _telephonyConversation.userData.unreadItems = _telephonyConversation.userData.unreadItems || 0; // init if not there yet
        changeUnreadItemsBy(1);
      }
    }

    /**
     * Calculate the number of unread messages in telephony conversation using the creationTime.
     *
     * @returns {int} The amount of unread messages in this conversation.
     */
    function calculateUnreadCount() {
      var count = 0;
      if (!_telephonyConversation || !_telephonyConversation.items) {
        return count;
      }
      var lastReadTimestamp = _telephonyConversation.userData.lastReadTimestamp;
      for (var i = _telephonyConversation.items.length - 1; i >= 0; i--) {
        var item = _telephonyConversation.items[i];
        if (item.creationTime <= lastReadTimestamp) {
          break;
        }
        if (item.includeInUnreadCount && !item.sentByMe) {
          count++;
        }
      }
      return count;
    }

    function changeUnreadItemsBy(difference) {
      if (!difference) {
        return;
      }

      var unreadSum = _telephonyConversation.userData.unreadItems + difference;
      _telephonyConversation.userData.unreadItems = Math.max(unreadSum, 0);

      LogSvc.debug('[PhoneCallLogSvc]: Publish /unread/changed event');
      PubSubSvc.publish('/unread/changed', _telephonyConversation.userData.unreadItems);

      LogSvc.debug('[ConversationSvc]: Publish /conversations/totalUnreadCountChanged event');
      PubSubSvc.publish('/conversations/totalUnreadCountChanged', _telephonyConversation.userData.unreadItems);
    }

    function processReadItemsData(lastReadTimestamp) {
      if (!_telephonyConversation) {
        LogSvc.error('[PhoneCallLogSvc]: No telephony conversation loaded');
        return;
      }

      if (_telephonyConversation.userData.lastReadTimestamp !== lastReadTimestamp) {
        $rootScope.$apply(function () {
          var previousUnreadCount = _telephonyConversation.userData.unreadItems;
          _telephonyConversation.userData.lastReadTimestamp = lastReadTimestamp;
          _telephonyConversation.userData.unreadItems = calculateUnreadCount();

          changeUnreadItemsBy(_telephonyConversation.userData.unreadItems - previousUnreadCount);
          PubSubSvc.publish('/conversation/update', _telephonyConversation);
        });
      }
    }
    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/conversations/showTelephonyConversation', function (show, conv) {
      LogSvc.debug('[PhoneCallLogSvc]: Received /conversations/showTelephonyConversation event. show = ', show);
      _showTelephonyConvReceived = true;
      if (show) {
        _telephonyConversation = conv;
      }
    });

    PubSubSvc.subscribe('/conversation/update', function (conversation) {
      if (_telephonyConversation && _telephonyConversation.convId === conversation.convId) {
        _telephonyConversation = conversation;
      }
    });

    PubSubSvc.subscribe('/conversation/items/discarded', function (convId) {
      if (_telephonyConversation && _telephonyConversation.convId === convId) {
        LogSvc.debug('[PhoneCallLogSvc]: Received /conversation/items/discarded event');
        clearCachedData(false);
        PubSubSvc.publish('/callHistory/items/cacheCleared');
      }
    });

    // In NGTC we need to clear cache every time conversations are loaded (in case of re-connection)
    PubSubSvc.subscribe('/conversations/loadComplete', function () {
      LogSvc.debug('[PhoneCallLogSvc]: Received /conversations/loadComplete event');
      clearCachedData(false);
      PubSubSvc.publish('/callHistory/items/cacheCleared');
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Client API Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    _clientApiHandler.on('Conversation.ADD_ITEM', function (data) {
      var item = data.item;
      if (!_telephonyConversation || !item || _telephonyConversation.convId !== item.convId) {
        // No cached journal entries or not a journal item
        return;
      }

      $rootScope.$apply(function () {
        LogSvc.debug('[PhoneCallLogSvc]: Received Conversation.ADD_ITEM event. itemId: ', item.itemId);

        // Update topLevelItem
        _telephonyConversation.topLevelItem = item;

        var callLogItem = processAddItem(item);
        if (!callLogItem) {
          return;
        }

        retrieveIncompleteUsers(callLogItem);
        LogSvc.debug('[PhoneCallLogSvc]: Publish /callHistory/item/added event');
        PubSubSvc.publish('/callHistory/item/added', [callLogItem, getApplicableFilters(callLogItem)]);

        if (callLogItem.rtc.type === Constants.RTCItemType.MISSED && !callLogItem.sentByMe) {
          var options = {
            type: NotificationType.INCOMING_MESSAGE,
            userId: callLogItem.creatorId,
            user: callLogItem.peerUser,
            extras: {
              conversation: _telephonyConversation,
              conversationItem: callLogItem
            }
          };
          NotificationSvc.show(options);
        }
      });
    });

    _clientApiHandler.on('Conversation.UPDATE_ITEM', function (data) {
      var item = data.item;
      if (!_telephonyConversation || !item || _telephonyConversation.convId !== item.convId) {
        // No cached journal entries or not a journal item
        return;
      }

      LogSvc.debug('[PhoneCallLogSvc]: Received Conversation.UPDATE_ITEM event. itemId: ', item.itemId);

      // We do not expect other update than voicemail finished processing (attachements)
      var cachedItem = _cachedItemsHash[item.itemId];
      if (!cachedItem) {
        LogSvc.info('[PhoneCallLogSvc]: Updated call journal entry is not cached. Ignore event.');
        return;
      }

      $rootScope.$apply(function () {
        handleUpdatedItem(item);
      });
    });

    _clientApiHandler.on('Conversation.JOURNAL_ENTRY_DELETED', function (data) {
      var item = data.deletedJournalEntry;
      if (!_telephonyConversation || !item) {
        // No cached journal entries or not a journal item
        return;
      }

      LogSvc.debug('[PhoneCallLogSvc]: Received Conversation.JOURNAL_ENTRY_DELETED event. itemId: ', item.itemId);

      var cachedItem = _cachedItemsHash[item.itemId];
      if (!cachedItem) {
        LogSvc.debug('[PhoneCallLogSvc]: Deleted call journal entry is not cached. Ignore event.');
        return;
      }

      $rootScope.$apply(function () {
        if (cachedItem.isVoicemailUnheard) {
          addToUnheardCount(-1);
        }
        deleteCachedItem(cachedItem);
        LogSvc.debug('[PhoneCallLogSvc]: Publish /callHistory/item/deleted event');
        PubSubSvc.publish('/callHistory/item/deleted', [cachedItem]);
      });
    });

    _clientApiHandler.on('Conversation.ALL_JOURNAL_ENTRIES_DELETED', function (data) {
      if (!_telephonyConversation) {
        // No cached journal entries
        return;
      }

      LogSvc.debug('[PhoneCallLogSvc]: Received Conversation.ALL_JOURNAL_ENTRIES_DELETED event. Journal Filter: ', data.journalFilter);

      $rootScope.$apply(function () {
        if (data.journalFilter === JournalFilter.ALL) {
          clearCachedData(true);
        } else if (_cachedData[data.journalFilter]) {
          var deletedItems = _cachedData[data.journalFilter].cachedItems;
          _cachedData[data.journalFilter].cachedItems = [];
          _cachedData[data.journalFilter].hasOldestItem = true;
          deletedItems.forEach(deleteCachedItem);
        }

        switch (data.journalFilter) {
          case JournalFilter.ALL:
          case JournalFilter.MISSED:
          case JournalFilter.VOICEMAILS:
          case JournalFilter.UNHEARD_VOICEMAILS:
            // All unheard voicemails have been deleted
            if (_unheardVoicemailCount > 0) {
              _unheardVoicemailCount = 0;
              LogSvc.debug('[PhoneCallLogSvc]: Set unheardVoicemailCount to 0');
              PubSubSvc.publish('/callHistory/unheardVoicemail/updated', [_unheardVoicemailCount]);
            }
            break;
        }

        LogSvc.debug('[PhoneCallLogSvc]: Publish /callHistory/items/cacheCleared event');
        PubSubSvc.publish('/callHistory/items/cacheCleared', [data.journalFilter]);
      });
    });

    _clientApiHandler.on('Conversation.READ_ITEMS', function (data) {
      if (!_telephonyConversation) {
        // No cached conversation
        return;
      }
      LogSvc.debug('[PhoneCallLogSvc]: Received Conversation.READ_ITEMS event. lastReadTimestamp: ' + data.lastReadTimestamp);

      processReadItemsData(data.lastReadTimestamp);
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.getTelephonyConversation = function () {
      var deferred = $q.defer();
      if (_showTelephonyConvReceived) {
        resolveTelephonyConversationPromise(deferred);
      } else {
        PubSubSvc.subscribeOnce('/conversations/showTelephonyConversation', function () {
          resolveTelephonyConversationPromise(deferred);
        });
      }
      return deferred.promise;
    };

    this.deleteCallLog = function (itemId) {
      if (!_telephonyConversation) {
        return $q.reject('No telephony conversation');
      }
      if (!itemId) {
        return $q.reject('Missing itemId');
      }

      return new $q(function (resolve, reject) {
        _clientApiHandler.deleteCallLog(itemId, function (err) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.error('[PhoneCallLogSvc]: Error deleting item. ', err);
              reject(err);
            } else {
              resolve();
            }
          });
        });
      });
    };

    /**
     * Returns the call history items for the given options.
     *
     * @param {Object} [queryData] The query options for the request.
     * @param {JournalFilter} [queryData.journalFilter] The journal filter for the call histiry items. Default is ALL.
     * @param {Number} [queryData.numberOfResults] Number of results to fetch.
     * @param {String} [queryData.pagePointer] The pointer to fetch more results. If not set the first results are returned.
     *
     * @returns {Promise} Promise for object with call history items, pagePointer for next page and hasMore boolean.
     */
    this.getCallHistoryItems = function (queryData) {
      if (!_telephonyConversation) {
        return $q.reject('No telephony conversation');
      }
      queryData = queryData || {};
      queryData.journalFilter = queryData.journalFilter || JournalFilter.ALL;
      queryData.numberOfResults = queryData.numberOfResults || DEFAULT_NUMBER_OF_ENTRIES;

      LogSvc.debug('[PhoneCallLogSvc]: Return call history items for ', queryData);

      var cachedData = _cachedData[queryData.journalFilter];

      var timestamp = parseInt(queryData.pagePointer, 10);
      var startIdx = 0;
      if (timestamp) {
        var cachedItems = cachedData.cachedItems;
        // Look for index in cached data (oldest items are in front)
        var oldestIdx = cachedItems.findIndex(function (item) {
          return item.creationTime >= timestamp;
        });
        startIdx = cachedItems.length - Math.max(oldestIdx, 0);
      }
      var totalEntries = startIdx + queryData.numberOfResults;

      return getCallJournalEntries(totalEntries, queryData.journalFilter).then(function (items) {
        // Reverse array so newest items are in front
        var oldestItem = items[0];
        items.reverse();

        var data = {
          items: items.slice(startIdx),
          hasMore: !cachedData.hasOldestItem || cachedData.cachedItems.length > totalEntries
        };
        if (data.hasMore) {
          data.pagePointer = oldestItem.creationTime + '';
        }
        return data;
      });
    };

    this.deleteVoiceMail = function (itemId) {
      if (!_telephonyConversation) {
        return $q.reject('No telephony conversation');
      }
      if (!itemId) {
        return $q.reject('Invalid itemId');
      }

      LogSvc.debug('[PhoneCallLogSvc]: Delete voicemail for itemId: ', itemId);

      return new $q(function (resolve, reject) {
        _clientApiHandler.updateRtcItemAttachments(itemId, [], function (error, item) {
          $rootScope.$apply(function () {
            if (error || !item) {
              LogSvc.error('[PhoneCallLogSvc]: Error deleting voicemail. itemId: ', itemId);
              reject(error);
              return;
            }
            handleUpdatedItem(item);
            resolve();
          });
        });
      });
    };

    this.markCallHistoryAsRead = function () {
      if (!_telephonyConversation) {
        return $q.reject('No telephony conversation');
      }

      var cachedItems = _cachedData[JournalFilter.ALL].cachedItems;
      var lastItem = cachedItems.length > 0 && cachedItems[cachedItems.length - 1];
      LogSvc.debug("[PhoneCallLogSvc]: Mark telephony conversation's last received item as read");

      if (!lastItem) {
        return $q.reject('Last item not found');
      }
      var creationTime = lastItem.creationTime;
      LogSvc.debug('[PhoneCallLogSvc]: Marked ' + _telephonyConversation.userData.unreadItems + ' items as read');

      var unreadItems = _telephonyConversation.userData.unreadItems;
      changeUnreadItemsBy(-_telephonyConversation.userData.unreadItems);
      _telephonyConversation.userData.unreadItems = 0;

      var lastReadTimestamp = _telephonyConversation.userData.lastReadTimestamp;
      if (!lastReadTimestamp || creationTime > lastReadTimestamp || unreadItems > 0) {
        LogSvc.debug('[PhoneCallLogSvc]: Marking all items as read');
        _telephonyConversation.userData.lastReadTimestamp = creationTime;

        return new $q(function (resolve, reject) {
          _clientApiHandler.setReadPointer(creationTime, function (err) {
            if (err) {
              LogSvc.error('[PhoneCallLogSvc]: Error in API markItemsAsRead: ', err);
              reject(err);
            } else {
              PubSubSvc.publish('/conversation/update', [_telephonyConversation]);
              resolve(_telephonyConversation.userData.unreadItems);
            }
          });
        });
      }
      LogSvc.debug('[PhoneCallLogSvc]: All items were already read');
      return $q.resolve(0);
    };

    this.markVoicemailAsHeard = function (itemId) {
      if (!_telephonyConversation) {
        return $q.reject('No telephony conversation');
      }
      if (!itemId) {
        return $q.reject('Invalid itemId');
      }

      var cachedItem = _cachedItemsHash[itemId];
      if (cachedItem && !cachedItem.isVoicemailUnheard) {
        return $q.resolve(); // Voicemail already heard, nothing to do
      }

      LogSvc.debug('[PhoneCallLogSvc]: Mark voicemail as heard for itemId: ', itemId);
      return new $q(function (resolve, reject) {
        _clientApiHandler.setVoicemailAsHeard(itemId, function (err) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.warn('[PhoneCallLogSvc]: Failed to mark voicemail as heard. ', err);
              reject(err);
            } else {
              resolve();
            }
          });
        });
      });
    };

    this.getUnheardVoicemailsCount = function () {
      if (!_telephonyConversation) {
        return $q.reject('No telephony conversation');
      }
      if (_unheardVoicemailCount >= 0) {
        return $q.resolve(_unheardVoicemailCount);
      }
      return new $q(function (resolve, reject) {
        _clientApiHandler.getUnheardVoicemailsCount(_telephonyConversation.convId, function (err, count) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.warn('[PhoneCallLogSvc]: Failed to get unheard voicemails count. ', err);
              reject(err);
            } else {
              _unheardVoicemailCount = count;
              resolve(count);
            }
          });
        });
      });
    };

    this.createFeedGroups = function (items) {
      if (!_telephonyConversation) {
        return $q.reject('No telephony conversation');
      }
      if (!items) {
        return null;
      }
      LogSvc.debug('[PhoneCallLogSvc]: Grouping call history feed items. Number of items: ', items.length);

      if (!items.length) {
        return [];
      }

      var feedGroups = [];
      var currDate = null;
      var currGroup = {
        items: []
      };
      items.forEach(function (item) {
        if (item.type !== Constants.ConversationItemType.RTC) {
          // Only show RTC items
          return;
        }
        var date = new Date(item.creationTime).toDateString();
        if (currDate && currDate !== date) {
          // Create a new group
          feedGroups.push(currGroup);
          currGroup = {
            items: []
          };
        }
        currDate = date;
        currGroup.timestamp = currGroup.timestamp || item.creationTime;
        currGroup.items.push(item);
      });
      feedGroups.push(currGroup);
      return feedGroups;
    };

    this.setRecordingDeletionDelay = function (itemId, retention) {
      if (!_telephonyConversation) {
        return $q.reject('No telephony conversation');
      }
      if (!itemId) {
        return $q.reject('Invalid itemId');
      }
      return ConversationSvc.setRecordingDeletionDelay(itemId, retention);
    };

    this.deleteAllCallLogs = function (journalFilter) {
      if (!_telephonyConversation) {
        return $q.reject('No telephony conversation');
      }

      journalFilter = journalFilter || JournalFilter.ALL;
      LogSvc.debug('[PhoneCallLogSvc]: Delete all call journals for filter ', journalFilter);
      return new $q(function (resolve, reject) {
        _clientApiHandler.deleteAllCallLogs(_telephonyConversation.convId, journalFilter, function (err) {
          if (err) {
            LogSvc.warn('[PhoneCallLogSvc]: Failed to delete all call journals. Error: ', err);
            reject(err);
          } else {
            resolve();
          }
        });
      });
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////////////////
    clearCachedData(false);

    return this;
  }

  // Exports
  unifyPhone.PhoneCallLogSvcImpl = PhoneCallLogSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global RegistrationState */

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var AtcInfoMessage = unifyPhone.Enums.AtcInfoMessage;
  var AtcMessage = unifyPhone.Enums.AtcMessage;
  var AtcRegistrationState = unifyPhone.Enums.AtcRegistrationState;
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var Constants = unifyPhone.Constants;
  var PhoneNumberFormatter = unifyPhone.PhoneNumberFormatter;
  var RoutingOptions = unifyPhone.RoutingOptions;
  var UserToUserHandler = unifyPhone.UserToUserHandlerSingleton;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-params, max-lines-per-function
  function AtcRegistrationSvcImpl($rootScope, $timeout, LogSvc, PubSubSvc, RegistrationSvc, ConversationSvc) {
    // NOSONAR
    LogSvc.debug('New Service: AtcRegistrationSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var MIN_ATC_REGISTRATION_RETRY_TIMER = 8; // Min Retry timer in seconds
    var MAX_ATC_REGISTRATION_RETRY_TIMER = 64; // Max Retry timer in seconds
    var MIN_ATC_REGISTRATION_DELAY_TIMER = 500; // Min Delay timer in miliseconds
    var MAX_ATC_REGISTRATION_DELAY_TIMER = 10000; // Max Delay timer in miliseconds
    var MIN_RENEW_ASSOCIATED_TC_TIMER = 30000; // Min renew timer in miliseconds
    var MAX_RENEW_ASSOCIATED_TC_TIMER = 180000; // Max renew timer in miliseconds
    var UNREGISTER_TIMER = 2000; // Timer in miliseconds

    var DEFAULT_TELEPHONY_DATA = {
      state: Constants.TrunkState.DOWN,
      telephonyAvailableForTenant: false,
      telephonyAvailableForUser: false,
      conferenceDialOutAvailable: false,
      phoneCallsAvailable: false
    };

    var SupportedFeatures = {
      cFC1: 'pbxCallForwardToVoiceMailSupported', // CFVM
      dND: 'pbxDNDSupported', // DND
      zCaOC: 'pbxCampOnSupported', // CampOn
      zPrfD: 'pbxCallRoutingOptionsSupported', // OSBiz Call Routing
      zGPC: 'pbxCallPickupSupported' // Call pickup
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _clientApiHandler = ClientApiHandler.getInstance();
    var _userToUserHandler = UserToUserHandler.getInstance();

    var _atcRegistrationData = {};
    var _atcRegState = AtcRegistrationState.Disconnected;
    var _configurationUpdated = false;
    var _presenceSubscribedTrunkId = null;
    var _telephonyTrunkSubscriptions = {};

    // The associated trunk state (or null if user is not associated to a TC)
    var _trunkState = null;

    var _telephonyData = Object.assign({}, DEFAULT_TELEPHONY_DATA);
    var _telephonyConversation = null;
    var _reRegister = false;

    var _savedRoutingOption = null;

    var _savedCallForwardingStatus = false;

    // Device type registering with ATC
    var _deviceType = Utils.isMobile() ? Constants.DeviceType.MOBILE : Constants.DeviceType.WEB;

    var _renewRegistration = false;

    var _unregisteredEventReceived = false;

    // Timers
    var _retryTimer = null; // Timer to retry registration request
    var _retryTime = MIN_ATC_REGISTRATION_RETRY_TIMER;
    var _renewTimer = null; // Timer to renew TC association
    var _unregisterTimer = null; // Timer to unregister after state changes to DOWN

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function cancelRetryTimer() {
      if (_retryTimer) {
        LogSvc.info('[AtcRegistrationSvc]: Cancel retry timer');
        $timeout.cancel(_retryTimer);
        _retryTimer = null;
      }
    }

    function cancelRenewTimer() {
      if (_renewTimer) {
        LogSvc.info('[AtcRegistrationSvc]: Cancel renew timer');
        $timeout.cancel(_renewTimer);
        _renewTimer = null;
      }
    }

    function cancelUnregisterTimer() {
      if (_unregisterTimer) {
        LogSvc.info('[AtcRegistrationSvc]: Cancel unregister timer');
        $timeout.cancel(_unregisterTimer);
        _unregisterTimer = null;
      }
    }

    function setAtcState(newState) {
      if (newState !== _atcRegState) {
        _atcRegState = newState;
        LogSvc.info('[AtcRegistrationSvc]: Publish /atcRegistration/state event. State = ', newState);
        PubSubSvc.publish('/atcRegistration/state', [newState]);

        if ($rootScope.localUser.isRegisterTC && newState !== AtcRegistrationState.Registering) {
          processTelephonyData(_telephonyData);
        }
      }
    }

    function isTelephonyAvailableForUserUpdated() {
      var telephonyAvailable = !!$rootScope.localUser.telephonyAvailable;
      var phoneConfigured = !!$rootScope.localUser.callerId;
      return telephonyAvailable !== phoneConfigured;
    }

    function startRetryTimeout() {
      cancelRetryTimer();

      LogSvc.info('[AtcRegistrationSvc]: Retry ATC registration in ' + _retryTime + ' seconds');
      _retryTimer = $timeout(function () {
        _retryTimer = null;
        if (_retryTime < MAX_ATC_REGISTRATION_RETRY_TIMER) {
          _retryTime = Math.min(_retryTime * 2, MAX_ATC_REGISTRATION_RETRY_TIMER);
        }
        atcRegister();
      }, _retryTime * 1000);
    }

    function retryOnError(err) {
      return (
        !!err &&
        (err === Constants.ReturnCode.FAILED_TO_SEND ||
          err === Constants.ReturnCode.REQUEST_TIMEOUT ||
          err.code === 503 ||
          err.code === 480 ||
          err.code === 408)
      );
    }

    function saveDataForLogging(registerResult) {
      $rootScope.localUser.telephonyConnectorData = {
        pbxType: $rootScope.localUser.isOsBizCTIEnabled ? 'OSBiz' : registerResult.configurationData.pbx || 'Unavailable',
        pbxVersion: registerResult.configurationData.pbxVersion || 'Unavailable',
        tcType: $rootScope.localUser.isOsBizCTIEnabled ? 'OSBIZ' : 'ATC'
      };

      if (registerResult.telephonyConnectorData) {
        Object.assign($rootScope.localUser.telephonyConnectorData, registerResult.telephonyConnectorData);
      }
    }

    function onAtcRegistrationError(err) {
      if (err.code === 600) {
        // If error.code === 600 then user has no valid telephony license.
        // Client needs to logout
        LogSvc.debug('[AtcRegistrationSvc]: Publish /session/logout event');
        PubSubSvc.publish('/session/logout', 'invalidAtcLicense');
        return;
      }
      if (retryOnError(err) && RegistrationSvc.isRegistered() && _atcRegState === AtcRegistrationState.Registering) {
        startRetryTimeout();
      }

      setAtcState(AtcRegistrationState.Unregistered);
    }

    function delayAtcRegister() {
      // Cancel any pending timer (in case of quick TC state changes)
      cancelRetryTimer();

      // Delay sending a Registration to avoid all clients on this telephony connector registering at the same time.
      var delayTime = Utils.randomNumber(MIN_ATC_REGISTRATION_DELAY_TIMER, MAX_ATC_REGISTRATION_DELAY_TIMER);
      LogSvc.info('[AtcRegistrationSvc]: Delay ATC registration by ' + delayTime + ' miliseconds');
      _retryTimer = $timeout(function () {
        _retryTimer = null;

        LogSvc.info('[AtcRegistrationSvc]: Retry timer has expired');
        // Just check whether the registration state is Unregistered
        // There are cases where state is DOWN and _retryTimer is canceled
        // In such cases client never attempts to connect back
        if (_atcRegState === AtcRegistrationState.Unregistered) {
          _retryTime = MIN_ATC_REGISTRATION_RETRY_TIMER;
          atcRegister();
        } else {
          LogSvc.info('[AtcRegistrationSvc]: State is not Unregistered, so cancel new registration');
        }
      }, delayTime);
    }

    function processSupportedFeatures(supportedFeatures) {
      Object.keys(SupportedFeatures).forEach(function (feature) {
        var propertyName = SupportedFeatures[feature];
        $rootScope.localUser[propertyName] = !!supportedFeatures && supportedFeatures.includes(feature);
      });
    }

    function atcRegister() {
      var localUser = $rootScope.localUser;
      var data = {
        content: {
          type: AtcMessage.REGISTER,
          phoneNumber: localUser.registerNumber,
          reroutingPhoneNumber: localUser.isOsBizCTIEnabled ? '' : Utils.cleanPhoneNumber(localUser.reroutingPhoneNumber),
          onsHash: localUser.onsSipAuthenticationHash || undefined,
          ondHash: localUser.ondSipAuthenticationHash || undefined,
          routeToDesk: localUser.selectedRoutingOption === RoutingOptions.DeskPhone.name && !localUser.cfData.Immediate.cfwEnabled,
          tcPool: localUser.associatedTelephonyTrunkGroupId,
          pbxCallLog: true,
          deviceType: _deviceType,
          userId: localUser.userId,
          conversationId: _telephonyConversation.convId,
          rtcSessionId: _telephonyConversation.rtcSessionId,
          lastItemTime: _telephonyConversation.lastItemModificationTime,
          renewRegistration: _renewRegistration || undefined,
          sTC: localUser.isSTC || undefined
        },
        destUserId: localUser.associatedTelephonyUserID,
        keysToOmitFromLogging: ['content.onsHash', 'content.ondHash']
      };

      cancelRetryTimer();
      cancelUnregisterTimer();

      _atcRegistrationData.phoneNumber = localUser.registerNumber;
      _atcRegistrationData.associatedTelephonyUserID = localUser.associatedTelephonyUserID;
      _atcRegistrationData.associatedTelephonyTrunkGroupId = localUser.associatedTelephonyTrunkGroupId;
      _atcRegistrationData.reroutingPhoneNumber = Utils.cleanPhoneNumber(localUser.reroutingPhoneNumber);
      _atcRegistrationData.onsSipAuthenticationHash = localUser.onsSipAuthenticationHash;
      _atcRegistrationData.ondSipAuthenticationHash = localUser.ondSipAuthenticationHash;
      _savedRoutingOption = localUser.selectedRoutingOption;
      _savedCallForwardingStatus = !!localUser.cfData.Immediate.cfwEnabled;

      // Initialize to false when registering
      localUser.noCallLog = false;

      setAtcState(AtcRegistrationState.Registering);

      LogSvc.info(
        '[AtcRegistrationSvc]: Registering with ATC, phoneNumber: ' +
          _atcRegistrationData.phoneNumber +
          ', associatedTelephonyUserID: ' +
          _atcRegistrationData.associatedTelephonyUserID
      );

      _reRegister = false;
      _userToUserHandler.sendAtcRequest(data, function (err, registerResult) {
        $rootScope.$apply(function () {
          if (_configurationUpdated && !_telephonyConversation.call) {
            LogSvc.info('[AtcRegistrationSvc]: There was a configuration update while registering. Need to register again');
            _configurationUpdated = false;
            if (!err && registerResult && !registerResult.err) {
              _reRegister = true;
              atcUnregister();
            } else {
              atcRegister();
            }
            return;
          }

          if (err || (registerResult && registerResult.err)) {
            err = err || registerResult.err;
            LogSvc.error('[AtcRegistrationSvc]: Register with ATC failed with error: ', err);
            onAtcRegistrationError(err);
            return;
          }

          // Successfully registered with the platform
          if (registerResult && registerResult.configurationData) {
            LogSvc.info('[AtcRegistrationSvc]: Received ATC configuration data: ', registerResult.configurationData);
            LogSvc.debug('[AtcRegistrationSvc]: Registration refresh will be handled by access server');
            _renewRegistration = false;
            if (localUser.isSTC) {
              setAtcState(AtcRegistrationState.Registered);
              handlePresenceState(Constants.TrunkState.UP);
              localUser.stcCapabilities = registerResult.capabilities;
              return;
            }
            _retryTime = MIN_ATC_REGISTRATION_RETRY_TIMER;
            _atcRegistrationData.configurationData = registerResult.configurationData;
            _atcRegistrationData.reroutingPhoneNumber = registerResult.configurationData.cell;
            _atcRegistrationData.capabilities = registerResult.capabilities;

            // Update localUser data
            if (!localUser.isOsBizCTIEnabled) {
              // In case of OSBiz, do not overwrite rerouting number.
              localUser.reroutingPhoneNumber = PhoneNumberFormatter.format(registerResult.configurationData.cell);
              // It will be set again once getForwarding response is received.
              $rootScope.localUser.ringDurationEnabled = false;
            }
            localUser.vmNumber = registerResult.configurationData.vm;
            localUser.vmGNF = registerResult.configurationData.vmGNF;
            localUser.isOSV = registerResult.configurationData.pbx === 'OSV';
            localUser.isOS4K = registerResult.configurationData.pbx === 'OS4K';
            localUser.PBXCallLogSupported =
              _atcRegistrationData.capabilities && _atcRegistrationData.capabilities.includes(Constants.AtcCapabilities.PBX_CALL_LOG);
            processSupportedFeatures(registerResult.configurationData.supportedFeatures);

            saveDataForLogging(registerResult);

            setAtcState(AtcRegistrationState.Registered);
            if (localUser.PBXCallLogSupported && !localUser.noCallLog) {
              sendEnablePBXCallLogToATC(true);
              // Assume that if the client is connected to an OSV, the PBX supports sending Call Logs
              // The Call Log feature was implemented in OSV V8R1
              // Don't need to wait for the response to the ENABLE_PBX_CALL_LOG request
              localUser.noCallLog = localUser.isOSV;
            }
            handlePresenceState(Constants.TrunkState.UP);
          } else {
            setAtcState(AtcRegistrationState.Unregistered);
          }
        });
      });
    }

    function atcUnregister() {
      LogSvc.info('[AtcRegistrationSvc]: Unregister with ATC');

      cancelRetryTimer();
      cancelUnregisterTimer();

      if (_atcRegState === AtcRegistrationState.Unregistered || _atcRegState === AtcRegistrationState.Disconnected) {
        LogSvc.debug('[AtcRegistrationSvc]: The client is not registered with ATC');
        return;
      }

      // Send USER_TO_USER message if client is registered
      if (RegistrationSvc.state() === RegistrationState.Registered) {
        var data = {
          content: {
            type: AtcMessage.UNREGISTER,
            phoneNumber: _atcRegistrationData.phoneNumber
          },
          destUserId: _atcRegistrationData.associatedTelephonyUserID
        };

        _userToUserHandler.sendAtcRequest(data, function (err) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.error('[AtcRegistrationSvc]: Unregister with ATC failed with: ', err);
            } else {
              LogSvc.info('[AtcRegistrationSvc]: Unregistered successfully with ATC');
            }
            if (_reRegister) {
              atcRegister();
            }
          });
        });
      }

      // If the associated ATC is changed, the client needs to indicate that to the new ATC so that it renews
      // the registration with the PBX
      _renewRegistration = $rootScope.localUser.associatedTelephonyUserID !== _atcRegistrationData.associatedTelephonyUserID;
      _atcRegistrationData.associatedTelephonyUserID = '';

      setAtcState(AtcRegistrationState.Unregistered);
    }

    function handlePresenceState(newTrunkState) {
      if (newTrunkState !== _trunkState) {
        _trunkState = newTrunkState;
        LogSvc.debug('[AtcRegistrationSvc]: Set trunk state to ', _trunkState);
        if (_telephonyData.state !== _trunkState || !$rootScope.localUser.isRegisterTC) {
          $rootScope.$apply(function () {
            var data = Object.assign({}, _telephonyData, { state: _trunkState });
            handleTelephonyDataChange(data);
          });
        }
      }
    }

    function unregisterAndRetry() {
      atcUnregister();
      // Start a retry timer in case there is an issue with Telephony UP event
      _retryTime = MIN_ATC_REGISTRATION_RETRY_TIMER + Utils.randomNumber(0, MAX_ATC_REGISTRATION_RETRY_TIMER);
      startRetryTimeout();
    }

    function handleTelephonyDataChange(data) {
      if (!data) {
        return;
      }

      LogSvc.debug('[AtcRegistrationSvc]: Handle telephony data change with state = ', data.state);

      var pendingUnregister = !!_unregisterTimer;
      cancelUnregisterTimer();

      if ($rootScope.localUser.isRegisterTC) {
        if (data.state === Constants.TrunkState.DOWN) {
          if (_atcRegState !== AtcRegistrationState.Registered) {
            unregisterAndRetry();
          } else {
            _telephonyData.state = Constants.TrunkState.DOWN;
            LogSvc.info('[AtcRegistrationSvc]: Start unregister timeout in case TC immediately reconnects');
            _unregisterTimer = $timeout(function () {
              _unregisterTimer = null;
              LogSvc.info('[AtcRegistrationSvc]: Unregister timeout has expired. state = ', _telephonyData.state);
              if (_telephonyData.state === Constants.TrunkState.DOWN) {
                unregisterAndRetry();
              }
            }, UNREGISTER_TIMER);
            // return so processTelephonyData is not called
            return;
          }
        } else if (_telephonyData.state === Constants.TrunkState.DOWN && data.state === Constants.TrunkState.UP) {
          _telephonyData.state = Constants.TrunkState.UP;
          if (_atcRegState === AtcRegistrationState.Registering) {
            LogSvc.debug('[AtcRegistrationSvc]: Client is already registering. Wait...');
          } else if (pendingUnregister) {
            LogSvc.debug('[AtcRegistrationSvc]: Client was pending unregistration. Send a registration refresh.');
            _retryTime = MIN_ATC_REGISTRATION_RETRY_TIMER;
            atcRegister();
          } else {
            delayAtcRegister();
          }
          // return so processTelephonyData is not called
          return;
        }
      }
      processTelephonyData(data);
    }

    function raiseTelephonyData() {
      LogSvc.info('[AtcRegistrationSvc]: Publish /telephony/data event. data = ', _telephonyData);
      PubSubSvc.publish('/telephony/data', [Object.assign({}, _telephonyData), _telephonyConversation, _atcRegState]);
    }

    function processTelephonyData(data) {
      if (!data) {
        return;
      }

      _telephonyData = data;

      var isPhoneCallsAvailable;
      if ($rootScope.localUser.isRegisterTC && ConversationSvc.telephonyConvId) {
        _telephonyData.state = _atcRegState === AtcRegistrationState.Registered ? Constants.TrunkState.UP : Constants.TrunkState.DOWN;
        _telephonyData.defaultCallerId = null;
        _telephonyData.isGTCEnabled = true;
        isPhoneCallsAvailable = _atcRegState === AtcRegistrationState.Registered;
      } else {
        _telephonyData.defaultCallerId = _telephonyData.defaultCallerId || null;
        isPhoneCallsAvailable = _telephonyData.state !== Constants.TrunkState.DOWN && _trunkState !== Constants.TrunkState.DOWN;
      }

      _telephonyData.telephonyAvailableForTenant = !!_telephonyData.isGTCEnabled;
      _telephonyData.telephonyAvailableForUser = !!(_telephonyData.isGTCEnabled && ($rootScope.localUser.callerId || _telephonyData.defaultCallerId));
      _telephonyData.conferenceDialOutAvailable = _telephonyData.telephonyAvailableForTenant && isPhoneCallsAvailable;
      _telephonyData.phoneCallsAvailable = _telephonyData.telephonyAvailableForUser && isPhoneCallsAvailable;

      if (!_telephonyData.telephonyAvailableForUser) {
        raiseTelephonyData();
      } else {
        ConversationSvc.getTelephonyConversationPromise()
          .then(function (conv) {
            _telephonyConversation = conv;
            raiseTelephonyData();
          })
          .catch(function (err) {
            LogSvc.error('[AtcRegistrationSvc]: Failed to retrieve telephony conversation. ', err);
            _telephonyData.telephonyAvailableForUser = false;
            _telephonyData.conferenceDialOutAvailable = false;
            _telephonyData.phoneCallsAvailable = false;
            raiseTelephonyData();
          });
      }
    }

    function getTelephonyData() {
      if (ConversationSvc.telephonyConvId) {
        LogSvc.info('[AtcRegistrationSvc]: Getting telephony data');
        _clientApiHandler.getTelephonyData(function (err, data) {
          $rootScope.$apply(function () {
            if (!err) {
              handleTelephonyDataChange(data);
            } else if (err !== Constants.ReturnCode.NO_RESULT) {
              LogSvc.error('[AtcRegistrationSvc]: Error getting telephony data: ', err);
              // We need to raise the /telephony/data event anyway
              processTelephonyData(_telephonyData);
            }
          });
        });
      } else {
        LogSvc.info('[AtcRegistrationSvc]: There is no telephony conversation. Set telephony state to DOWN.');
        processTelephonyData({
          state: Constants.TrunkState.DOWN
        });
      }
    }

    function checkPresenceSubscription() {
      if (_presenceSubscribedTrunkId && _presenceSubscribedTrunkId !== $rootScope.localUser.associatedTelephonyUserID) {
        LogSvc.debug('[AtcRegistrationSvc]: Unsubscribe from previously associated TC presence');
        if (!_telephonyTrunkSubscriptions[_presenceSubscribedTrunkId]) {
          _clientApiHandler.unsubscribePresence([_presenceSubscribedTrunkId]);
        }
        _presenceSubscribedTrunkId = null;
        _trunkState = null;
        _renewRegistration = true;
      }

      if (!$rootScope.localUser.associatedTelephonyUserID) {
        // User is not associated to any TC
        _trunkState = null;
      } else if (_presenceSubscribedTrunkId !== $rootScope.localUser.associatedTelephonyUserID) {
        _presenceSubscribedTrunkId = $rootScope.localUser.associatedTelephonyUserID;

        // We can cancel the timer since we just got associated to a new TC.
        cancelRenewTimer();

        if (!$rootScope.localUser.isRegisterTC || !_telephonyTrunkSubscriptions[_presenceSubscribedTrunkId]) {
          // Subscribe to presence (Do this even if we are already subscribed so we can get the current state)
          _clientApiHandler.subscribePresence([$rootScope.localUser.associatedTelephonyUserID], function (err, states) {
            if (err) {
              LogSvc.warn('[AtcRegistrationSvc]: Unable to subscribe to associated TC presence');
              _presenceSubscribedTrunkId = null;
              return;
            }
            if (states && states.length > 0 && !$rootScope.localUser.isRegisterTC) {
              var newTrunkState = states[0].state === Constants.PresenceState.AVAILABLE ? Constants.TrunkState.UP : Constants.TrunkState.DOWN;
              handlePresenceState(newTrunkState);
            }
          });
        }
      }
    }

    function checkLocalUserConfiguration(loadComplete) {
      checkPresenceSubscription();

      if (!$rootScope.localUser.isRegisterTC || !$rootScope.localUser.registerNumber) {
        LogSvc.debug('[AtcRegistrationSvc]: User is not associated with ATC or STC');
        if (_atcRegState === AtcRegistrationState.Registered) {
          atcUnregister();
          getTelephonyData();
        } else if (loadComplete || isTelephonyAvailableForUserUpdated()) {
          // Get the telephony data for non-ATC users
          getTelephonyData();
        }
        setAtcState(AtcRegistrationState.Disconnected);
        return;
      }

      if (_savedCallForwardingStatus !== $rootScope.localUser.cfData.Immediate.cfwEnabled) {
        _savedCallForwardingStatus = $rootScope.localUser.cfData.Immediate.cfwEnabled;
        if ($rootScope.localUser.selectedRoutingOption === RoutingOptions.DeskPhone.name) {
          sendRouteToDeskInfoToAtc();
        }
      }

      if (
        $rootScope.localUser.associatedTelephonyUserID === _atcRegistrationData.associatedTelephonyUserID &&
        $rootScope.localUser.registerNumber === _atcRegistrationData.phoneNumber &&
        $rootScope.localUser.onsSipAuthenticationHash === _atcRegistrationData.onsSipAuthenticationHash &&
        $rootScope.localUser.ondSipAuthenticationHash === _atcRegistrationData.ondSipAuthenticationHash &&
        (_atcRegState === AtcRegistrationState.Registered || _atcRegState === AtcRegistrationState.Registering)
      ) {
        // In case of OSBiz, there is no need to re-register due to changing alternative number.
        if (
          $rootScope.localUser.isOsBizCTIEnabled ||
          Utils.cleanPhoneNumber($rootScope.localUser.reroutingPhoneNumber) === _atcRegistrationData.reroutingPhoneNumber
        ) {
          LogSvc.debug('[AtcRegistrationSvc]: Client is already registered or registering with ATC and the associatedTelephonyUserID is the same');
          return;
        }
      }

      if (_atcRegState === AtcRegistrationState.Registering) {
        // We need to wait for the previous registration to complete before registering again
        LogSvc.debug('[AtcRegistrationSvc]: Client is registering and configuration has changed. Wait...');
        _configurationUpdated = true;
        return;
      }

      if (_atcRegState === AtcRegistrationState.Registered) {
        LogSvc.debug('[AtcRegistrationSvc]: Client is already registered and configuration has changed.');
        if (!_telephonyConversation.call) {
          _reRegister = true;
          atcUnregister();
        } else {
          LogSvc.debug('[AtcRegistrationSvc]: There is an active telephony call, wait...');
          _configurationUpdated = true;
          return;
        }
      }

      if (!_reRegister) {
        // Make sure we have the telephony conversation
        if (!_telephonyConversation) {
          LogSvc.debug('[AtcRegistrationSvc]: Get telephony conversation before proceeding with ATC register');
          ConversationSvc.getTelephonyConversationPromise()
            .then(function (conv) {
              _telephonyConversation = conv;
              LogSvc.debug('[AtcRegistrationSvc]: Got telephony conversation. Proceed with ATC register.');
              checkLocalUserConfiguration();
            })
            .catch(function () {
              LogSvc.error('[AtcRegistrationSvc]: Failed to retrieve telephony conversation. Cannot register with ATC.');
              processTelephonyData({
                state: Constants.TrunkState.DOWN
              });
            });

          if (!loadComplete && isTelephonyAvailableForUserUpdated()) {
            // Get the telephony data for ATC users
            getTelephonyData();
          }
          return;
        }

        if (_atcRegState !== AtcRegistrationState.ForcedUnregistered) {
          // Do not attempt to register if we were forced unregistered by ATC
          atcRegister();
        }
      }
    }

    function sendRouteToDeskInfoToAtc() {
      if (_atcRegState === AtcRegistrationState.Registered && !$rootScope.localUser.isOsBizCTIEnabled) {
        var status = $rootScope.localUser.selectedRoutingOption === RoutingOptions.DeskPhone.name && !$rootScope.localUser.cfData.Immediate.cfwEnabled;

        LogSvc.debug('[AtcRegistrationSvc]: Send SET_ROUTE_TO_DESK event to ATC. status = ', status);

        var data = {
          content: {
            type: AtcMessage.INFO,
            phoneNumber: $rootScope.localUser.cstaNumber,
            info: {
              type: AtcInfoMessage.SET_ROUTE_TO_DESK,
              status: status
            }
          },
          destUserId: $rootScope.localUser.associatedTelephonyUserID
        };
        // Send request
        _userToUserHandler.sendAtcRequest(data);
      }
    }

    function sendEnablePBXCallLogToATC(status) {
      var data = {
        content: {
          type: AtcMessage.INFO,
          phoneNumber: $rootScope.localUser.cstaNumber,
          info: {
            type: AtcInfoMessage.ENABLE_PBX_CALL_LOG,
            status: status
          }
        },
        destUserId: $rootScope.localUser.associatedTelephonyUserID
      };
      // Send request
      _userToUserHandler.sendAtcRequest(data);
    }

    function renewAssociatedTC(retry, delayTime) {
      cancelRenewTimer();
      if (!$rootScope.localUser.associatedTelephonyTrunkGroupId) {
        // User is not associated to any pool. Ignore the request.
        LogSvc.debug('[AtcRegistrationSvc]: User is not associated to any pool. Ignore the TC reassignment request.');
        return;
      }

      // Use given delay or set it to a random time between MIN and MAX
      delayTime = delayTime || Utils.randomNumber(MIN_RENEW_ASSOCIATED_TC_TIMER, MAX_RENEW_ASSOCIATED_TC_TIMER);

      LogSvc.debug('[AtcRegistrationSvc]: Renew associated telephony connector in ' + delayTime + ' miliseconds');

      _renewTimer = $timeout(function () {
        _renewTimer = null;
        LogSvc.debug('[AtcRegistrationSvc]: Renew associated telephony connector');
        _clientApiHandler.renewAssociatedTelephonyUser($rootScope.localUser.associatedTelephonyUserID, function (err) {
          if (err) {
            LogSvc.error('[AtcRegistrationSvc]: Failed to renew associated telephony connector. ', err);
            retry && renewAssociatedTC(true, MAX_RENEW_ASSOCIATED_TC_TIMER);
          }
        });
      }, delayTime);
    }

    function processRefreshFailedEvent() {
      if (_atcRegState === AtcRegistrationState.Registered) {
        if (_telephonyConversation.call && !_telephonyConversation.call.isRemote) {
          LogSvc.info(
            '[AtcRegistrationSvc]: There is an active call, ignoring ATC re-register for now. If error persists on server another event will come later.'
          );
        } else {
          $rootScope.$apply(function () {
            LogSvc.info('[AtcRegistrationSvc]: New registration must be triggered.');
            _reRegister = true;
            atcUnregister();
          });
        }
      } else {
        // Other processing has been already started on client, so just ignore
        LogSvc.info('[AtcRegistrationSvc]: Client is not registered, ignoring failed refresh on server.');
      }
    }

    function processUnregisteredEvent() {
      // Event from ATC that indicates the the client was forced unregistered due to a problem
      if (_atcRegState === AtcRegistrationState.Registered) {
        if (_telephonyConversation.call && !_telephonyConversation.call.isRemote) {
          LogSvc.info('[AtcRegistrationSvc]: There is an active call, ignoring ATC Unregistered event for now.');
          _unregisteredEventReceived = true;
        } else {
          $rootScope.$apply(function () {
            setAtcState(AtcRegistrationState.ForcedUnregistered);
          });
        }
      } else {
        // Other processing has been already started on client, so just ignore
        LogSvc.info('[AtcRegistrationSvc]: Client is not registered, ignoring Unregistered event.');
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/localUser/telephonyConfigUpdated', function () {
      if (RegistrationSvc.isRegistered() && ConversationSvc.isConversationsLoadComplete()) {
        LogSvc.debug('[AtcRegistrationSvc]: Received /localUser/telephonyConfigUpdated event');
        checkLocalUserConfiguration();
      }
    });

    PubSubSvc.subscribe('/conversations/loadComplete', function () {
      LogSvc.debug('[AtcRegistrationSvc]: Received /conversations/loadComplete event');
      checkLocalUserConfiguration(true);
    });

    PubSubSvc.subscribe('/registration/state', function (state) {
      if (!ConversationSvc.isConversationsLoadComplete()) {
        return;
      }

      LogSvc.debug('[AtcRegistrationSvc]: Received /registration/state event');
      if (state === RegistrationState.LoggingOut || state === RegistrationState.Reconnecting || state === RegistrationState.Disconnected) {
        // Cancel retry timer (if running)
        cancelRetryTimer();
        // If needed, the TC will be reassigned during reconnection
        cancelRenewTimer();
        // Allow others to process the /registration/state event before changing the telephony state
        $timeout(
          $rootScope.localUser.isRegisterTC
            ? atcUnregister
            : function () {
                _telephonyData.state = Constants.TrunkState.DOWN;
                _presenceSubscribedTrunkId = null;
                _trunkState = null;
                _telephonyTrunkSubscriptions = {};
                processTelephonyData(_telephonyData);
              }
        );
      }
    });

    PubSubSvc.subscribe('/conversation/update', function (conversation) {
      if (conversation.isTelephony) {
        LogSvc.debug('[AtcRegistrationSvc]: Received /conversation/update event');
        _telephonyConversation = conversation;
        if (!_configurationUpdated && !_unregisteredEventReceived) {
          return;
        }

        if (conversation.call) {
          LogSvc.debug('[AtcRegistrationSvc]: There is still an active telephony call, wait...');
        } else if (_atcRegState !== AtcRegistrationState.Registering) {
          if (_unregisteredEventReceived) {
            setAtcState(AtcRegistrationState.ForcedUnregistered);
            _unregisteredEventReceived = false;
            return;
          }
          _configurationUpdated = false;
          if (_atcRegState === AtcRegistrationState.Registered) {
            _reRegister = true;
            atcUnregister();
          }
        }
      }
    });

    PubSubSvc.subscribe('/user/settings/update', function () {
      if (!ConversationSvc.isConversationsLoadComplete()) {
        return;
      }

      LogSvc.debug('[AtcRegistrationSvc]: Received /user/settings/update event');

      if (_savedRoutingOption !== $rootScope.localUser.selectedRoutingOption) {
        _savedRoutingOption = $rootScope.localUser.selectedRoutingOption;
        sendRouteToDeskInfoToAtc();
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Client API Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    _clientApiHandler.on('User.TELEPHONY_DATA', function (evt) {
      $rootScope.$apply(function () {
        try {
          var data = evt.data;
          if (data) {
            LogSvc.debug('[AtcRegistrationSvc]: Received User.TELEPHONY_DATA with data: ', data);
            data.isGTCEnabled = true;
            // Check if there are any changes
            Object.keys(data).some(function (key) {
              if (_telephonyData[key] !== data[key]) {
                handleTelephonyDataChange(data);
                return true;
              }
              return false;
            });
          }
        } catch (e) {
          LogSvc.error('[AtcRegistrationSvc]: Exception handling User.TELEPHONY_DATA event.', e);
        }
      });
    });

    _clientApiHandler.on('User.USER_PRESENCE_CHANGE', function (data) {
      if (!data.userId) {
        // No need to process this event
        return;
      }
      if (data.userId === _presenceSubscribedTrunkId) {
        LogSvc.debug('[AtcRegistrationSvc]: Received User.USER_PRESENCE_CHANGE for associated TC. newState = ', data.newState);
        var newTrunkState = data.newState.state === Constants.PresenceState.AVAILABLE ? Constants.TrunkState.UP : Constants.TrunkState.DOWN;
        handlePresenceState(newTrunkState);
      }
      if (_telephonyTrunkSubscriptions[data.userId]) {
        LogSvc.info('[AtcRegistrationSvc]: Publish /trunk/update event. newState = ', data.newState);
        PubSubSvc.publish('/trunk/update', [data.newState]);
      }
    });

    _clientApiHandler.on('User.ATC_REFRESH_FAILED', function () {
      LogSvc.error('[AtcRegistrationSvc]: Received User.ATC_REFRESH_FAILED event.');
      processRefreshFailedEvent();
    });

    _userToUserHandler.on('ATC.REFRESH_FAILED', function () {
      LogSvc.error('[AtcRegistrationSvc]: Received UserToUser.ATC.REFRESH_FAILED event.');
      processRefreshFailedEvent();
    });

    _clientApiHandler.on('Account.TC_START_REASSIGNMENT', function (evt) {
      LogSvc.debug('[AtcRegistrationSvc]: Account.TC_START_REASSIGNMENT event');
      var maxThrottlingTime = Math.max((evt && evt.maxThrottlingTime) || MAX_RENEW_ASSOCIATED_TC_TIMER, MIN_RENEW_ASSOCIATED_TC_TIMER);

      // Set delay to a random time between MIN and MIN + maxThrottlingTime before sending the renew request
      var delayTime = MIN_RENEW_ASSOCIATED_TC_TIMER + Utils.randomNumber(0, maxThrottlingTime);
      renewAssociatedTC(true, delayTime);
    });

    _userToUserHandler.on('ATC.INFO', function (data) {
      LogSvc.debug('[AtcRegistrationSvc]: Received ATC.INFO with data = ', data);
      if ($rootScope.localUser && data.hasOwnProperty('pbxCallLogStatus')) {
        $rootScope.localUser.noCallLog = !!data.pbxCallLogStatus;
      }
    });

    _userToUserHandler.on('ATC.START_REASSIGNMENT', function () {
      LogSvc.debug('[AtcRegistrationSvc]: Received ATC.START_REASSIGNMENT event');
      renewAssociatedTC(false);
    });

    _userToUserHandler.on('ATC.UNREGISTERED', function () {
      LogSvc.error('[AtcRegistrationSvc]: Received UserToUser.ATC.UNREGISTERED event.');
      processUnregisteredEvent();
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Functions
    ///////////////////////////////////////////////////////////////////////////////////////

    // Used by SDK to initialize service
    this.initAtcForSdk = function () {
      LogSvc.debug('[AtcRegistrationSvc]: initAtcForSdk');
      _deviceType = Constants.DeviceType.SDK;
      checkLocalUserConfiguration(true);
    };

    this.atcState = function () {
      return _atcRegState;
    };

    this.getAtcRegistrationData = function () {
      return _atcRegistrationData.configurationData;
    };

    /**
     * Retrieves the current telephony data.
     * @returns {Object} The cached telephony data
     */
    this.getTelephonyData = function (cb) {
      if (!cb) {
        var data = _atcRegState === AtcRegistrationState.Registering ? DEFAULT_TELEPHONY_DATA : _telephonyData;
        return Object.assign({}, data);
      }
      if (typeof cb === 'function') {
        // This API used to receive a callback as input
        if (_atcRegState === AtcRegistrationState.Registering) {
          cb('ATC Registering');
        } else {
          cb(null, Object.assign({}, _telephonyData));
        }
      }
      return null;
    };

    this.isExtendedAlertingSupported = function () {
      if (_atcRegistrationData && _atcRegistrationData.capabilities) {
        return _atcRegistrationData.capabilities.includes(Constants.AtcCapabilities.EXTENDED_ALERTING);
      }
      return false;
    };

    this.isClearConnectionBusySupported = function () {
      if (_atcRegistrationData && _atcRegistrationData.capabilities) {
        return _atcRegistrationData.capabilities.includes(Constants.AtcCapabilities.CLEAR_CONNECTION_BUSY);
      }
      return false;
    };

    this.subscribeConnectorPresence = function (ids, cb) {
      ids = ids.filter(function (id) {
        return !_telephonyTrunkSubscriptions[id];
      });
      if (ids.length) {
        LogSvc.debug('[AtcRegistrationSvc]: Subscribe for connectors presence. ', ids);
        _clientApiHandler.subscribePresence(ids, function (err, states) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.error('[AtcRegistrationSvc]: Failed to subscribe to connectors presence. ', err);
              cb && cb(err);
            } else if (states) {
              LogSvc.debug('[AtcRegistrationSvc]: Successfully subscribed for connectors presence');
              states.forEach(function (userPresenceState) {
                _telephonyTrunkSubscriptions[userPresenceState.userId] = true;
              });
              cb && cb(null, states);
            }
          });
        });
      }
    };

    this.unsubscribeConnectorPresence = function (ids, cb) {
      if (ids) {
        ids = ids.filter(function (id) {
          if (_telephonyTrunkSubscriptions[id]) {
            delete _telephonyTrunkSubscriptions[id];
            return id !== _presenceSubscribedTrunkId;
          }
          return false;
        });
      } else {
        ids = Object.keys(_telephonyTrunkSubscriptions).filter(function (id) {
          return id !== _presenceSubscribedTrunkId;
        });
        _telephonyTrunkSubscriptions = {};
      }
      if (ids.length) {
        LogSvc.debug('[AtcRegistrationSvc]: Unsubscribe for connectors presence. ', ids);
        _clientApiHandler.unsubscribePresence(ids, cb);
      }
    };

    this.isPBXCallLogSupported = function () {
      if (_atcRegistrationData && _atcRegistrationData.capabilities) {
        return _atcRegistrationData.capabilities.includes(Constants.AtcCapabilities.PBX_CALL_LOG);
      }
      return false;
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.AtcRegistrationSvcImpl = AtcRegistrationSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global DeviceStatistics*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';
  // Imports

  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var Constants = unifyPhone.Constants;
  var Proto = unifyPhone.Proto;
  var Utils = unifyPhone.Utils;
  var WebRTCAdapter = unifyPhone.WebRTCAdapter;
  var RtcSessionController = unifyPhone.RtcSessionController;

  // eslint-disable-next-line max-params, max-lines-per-function
  function DeviceDiagnosticSvcImpl($rootScope, $timeout, LogSvc, PubSubSvc, LocalStoreSvc) {
    // NOSONAR
    LogSvc.debug('New Service: DeviceDiagnosticSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var JOIN_DELAY_TIMEOUT = 3000;

    var MAX_CACHED_RTC_CLIENT_SIZE = 60000; // Maximum size of cached diagnostics data in local storage
    var TRIMMED_RTC_CLIENT_SIZE = 50000; // Max size after data is trimmed

    var _clientApiHandler = ClientApiHandler.getInstance();
    var _cachedClientInfo = [];
    var _supportsOfflineFailures = !!LocalStoreSvc;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function trimAndStoreData() {
      if (!_supportsOfflineFailures) {
        return;
      }
      if (_cachedClientInfo.length === 0) {
        LocalStoreSvc.removeItem(LocalStoreSvc.keys.RTC_CLIENT_INFOS);
        return;
      }
      var serializedData = JSON.stringify(_cachedClientInfo);
      if (serializedData.length > MAX_CACHED_RTC_CLIENT_SIZE) {
        LogSvc.warn('[DeviceDiagnosticSvc]: Stored device diagnostics exceeded max size. Discard old data.');
        var discardedData = '';
        var discardSize = serializedData.length - TRIMMED_RTC_CLIENT_SIZE;
        _cachedClientInfo.some(function (oldInfo, idx) {
          discardedData += JSON.stringify(oldInfo);
          if (discardedData.length > discardSize) {
            LogSvc.warn('[DeviceDiagnosticSvc]: Number of discarded records: ', idx + 1);
            _cachedClientInfo.splice(0, idx + 1);
            serializedData = JSON.stringify(_cachedClientInfo);
            return true;
          }
          return false;
        });
      }
      LocalStoreSvc.setString(LocalStoreSvc.keys.RTC_CLIENT_INFOS, serializedData);
    }

    function init() {
      if (_supportsOfflineFailures) {
        _cachedClientInfo = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.RTC_CLIENT_INFOS) || [];
        if (!Array.isArray(_cachedClientInfo)) {
          _cachedClientInfo = [];
        }
        trimAndStoreData();
      }
    }

    function cancelJoinDelayTimer(diagnostics) {
      if (diagnostics && diagnostics.joinDelayTimer) {
        $timeout.cancel(diagnostics.joinDelayTimer);
        diagnostics.joinDelayTimer = null;
      }
    }

    function startDiagnostics(call) {
      if (call && !call.isRemote) {
        LogSvc.info('[DeviceDiagnosticSvc]: Start diagnostics collection for callId = ', call.callId);
        call.deviceDiagnostics = call.deviceDiagnostics || {
          data: {
            actionInfo: []
          }
        };
      }
    }

    function startJoinDelayTimer(call) {
      if (!call || !call.deviceDiagnostics) {
        return;
      }
      LogSvc.debug('[DeviceDiagnosticSvc]: Start join delay timer for callId = ', call.callId);

      var diagnostics = call.deviceDiagnostics;

      cancelJoinDelayTimer(diagnostics);
      diagnostics.joinDelayTimer = $timeout(function () {
        diagnostics.joinDelayTimer = null;
        onJoinDelay(call);
      }, JOIN_DELAY_TIMEOUT);

      diagnostics.joinDelayed = false;
      diagnostics.waitingToSend = false;
      diagnostics.iceGatheringFinished = false;

      call.deviceDiagnostics = diagnostics;
    }

    function createActionInfo(call, actionType) {
      LogSvc.debug('[DeviceDiagnosticSvc]: Create action info ', actionType);

      if (!call || !call.deviceDiagnostics || !actionType) {
        return null;
      }

      var info = {
        actionType: actionType
      };

      switch (actionType) {
        case Constants.RtcDiagnosticsAction.SDP_ANSWER:
        case Constants.RtcDiagnosticsAction.SDP_CONNECTED:
        case Constants.RtcDiagnosticsAction.REMOTE_ICE_CANDIDATES:
          info.type = Constants.RtcActionInfoType.EVENT;
          info.timestampEvent = Date.now();
          break;

        default:
          info.type = Constants.RtcActionInfoType.REQUEST_RESPONSE;
          info.timestampRequest = Date.now();
          break;
      }

      call.deviceDiagnostics.data.actionInfo.push(info);
      return info;
    }

    function hasPendingActionInfo(call) {
      // Check if all action infos are complete.
      if (!call || !call.deviceDiagnostics) {
        return false;
      }
      var pending = call.deviceDiagnostics.data.actionInfo.some(function (actionInfo) {
        return !actionInfo.complete;
      });
      return pending || !call.deviceDiagnostics.iceGatheringFinished;
    }

    function finishActionInfo(call, info) {
      if (!info) {
        return;
      }

      LogSvc.debug('[DeviceDiagnosticSvc]: Finish action info ', info.actionType);

      info.complete = true;
      if (info.type === Constants.RtcActionInfoType.REQUEST_RESPONSE) {
        info.timestampResponse = Date.now();
      }

      if (call && call.deviceDiagnostics) {
        if (info.isEndOfCandidates) {
          call.deviceDiagnostics.iceGatheringFinished = true;
          LogSvc.debug('[DeviceDiagnosticSvc]: Ice gathering finished');
        }
        // Device Diagnostics won't be sent until collection is complete, or at least on terminate
        if (call.deviceDiagnostics.waitingToSend && !hasPendingActionInfo(call)) {
          forceFinishDeviceDiagnostics(call);
        }
      }
    }

    function forceFinishDeviceDiagnostics(call) {
      if (!call || !call.deviceDiagnostics) {
        return;
      }

      LogSvc.debug('[DeviceDiagnosticSvc]: Force finish device diagnostics');
      var diagnostics = call.deviceDiagnostics;
      cancelJoinDelayTimer(diagnostics);

      if (diagnostics.joinDelayed) {
        sendDeviceDiagnostics(call);
      }
      clearDeviceDiagnostics(call);
    }

    function onJoinDelay(call) {
      if (!call || !call.deviceDiagnostics) {
        return;
      }

      var diagnostics = call.deviceDiagnostics;

      LogSvc.debug('[DeviceDiagnosticSvc]: Join action delayed, preparing device diagnostics');
      cancelJoinDelayTimer(diagnostics);
      diagnostics.joinDelayed = true;

      if (typeof DeviceStatistics !== 'undefined') {
        var deviceStats = DeviceStatistics.getStatistics();

        LogSvc.info('[DeviceDiagnosticSvc]: Received native device diagnostics: ', deviceStats);
        diagnostics.data.usedRAMApp = deviceStats.usedRAMApp;
        diagnostics.data.availableRAMApp = deviceStats.availableRAMApp;
        diagnostics.data.usedRAMDevice = deviceStats.usedRAMDevice;
        diagnostics.data.availableRAMDevice = deviceStats.availableRAMDevice;
        diagnostics.data.virtualMemory = deviceStats.virtualMemory;
        diagnostics.data.usagePerCore = deviceStats.usagePerCore;
        diagnostics.data.numberOfCores = deviceStats.numberOfCores;
        diagnostics.data.networkType = deviceStats.networkType;
      }
    }

    function finishDeviceDiagnostics(call) {
      if (!call || !call.deviceDiagnostics) {
        return;
      }
      LogSvc.debug('[DeviceDiagnosticSvc]: Finish device diagnostics');

      var diagnostics = call.deviceDiagnostics;
      cancelJoinDelayTimer(diagnostics);

      if (diagnostics.joinDelayed) {
        if (!hasPendingActionInfo(call)) {
          sendDeviceDiagnostics(call);
        } else {
          diagnostics.waitingToSend = true;
          LogSvc.debug('[DeviceDiagnosticSvc]: Send device diagnostic delayed');
        }
      } else {
        clearDeviceDiagnostics(call);
      }
    }

    function clearDeviceDiagnostics(call) {
      if (!call || !call.deviceDiagnostics) {
        return;
      }

      LogSvc.debug('[DeviceDiagnosticSvc]: Clear device diagnostics');
      cancelJoinDelayTimer(call.deviceDiagnostics);
      delete call.deviceDiagnostics;
    }

    function storeClientInfo(info) {
      if (_supportsOfflineFailures) {
        LogSvc.debug('[DeviceDiagnosticSvc]: Store device diagnostics to send when client reconnects');
        _cachedClientInfo.push(info);
        trimAndStoreData();
      }
    }

    function sendDeviceDiagnostics(call) {
      if (!call || !call.deviceDiagnostics) {
        LogSvc.debug('[DeviceDiagnosticSvc]: sendDeviceDiagnostics - Nothing to send');
        return;
      }
      var clientInfo = {
        sdpOrigin: call.sdpOrigin || null,
        userId: $rootScope.localUser.userId,
        convId: call.convId,
        clientInfoType: Constants.RtcClientInfoType.DEVICE_DIAGNOSTICS,
        reason: Constants.RtcClientInfoReason.JOIN_DELAY,
        timestamp: Date.now(),
        sessionId: call.callId,
        deviceDiagnostics: Utils.shallowCopy(call.deviceDiagnostics.data)
      };

      if (call.instanceId) {
        clientInfo.instanceId = call.instanceId;
      }

      clearDeviceDiagnostics(call);

      LogSvc.debug('[DeviceDiagnosticSvc]: Sending device diagnostics...');
      _clientApiHandler.sendClientInfo([clientInfo], function (err) {
        if (err) {
          LogSvc.debug('[DeviceDiagnosticSvc]: Failed to send device diagnostics. ', err);
          if (Proto.isOfflineFailure(err)) {
            storeClientInfo(clientInfo);
          }
        }
      });
    }

    function sendOfflineJoinFailures() {
      if (_cachedClientInfo.length > 0) {
        var clientInfos = _cachedClientInfo;
        _cachedClientInfo = [];
        LocalStoreSvc.removeItem(LocalStoreSvc.keys.RTC_CLIENT_INFOS);

        LogSvc.info('[DeviceDiagnosticSvc]: Send offline join failures to backend');
        _clientApiHandler.sendClientInfo(clientInfos, function (err) {
          if (err) {
            LogSvc.error('[DeviceDiagnosticSvc]: Failed to send offline join failures. ', err);
            if (Proto.isOfflineFailure(err)) {
              // The client is offline. Save the data again.
              Array.prototype.unshift.apply(_cachedClientInfo, clientInfos);
              trimAndStoreData();
            }
          } else {
            LogSvc.debug('[DeviceDiagnosticSvc]: Sent offline join failures');
          }
        });
      }
    }

    function getDeviceStatistics() {
      if (typeof DeviceStatistics !== 'undefined') {
        return DeviceStatistics.getStatistics();
      }
      return null;
    }

    function getDeviceName(device) {
      if (!device || !device.label || typeof device.label !== 'string') {
        return '';
      }
      var label = device.label;
      if (label.startsWith('res_')) {
        return $rootScope.i18n.localize(label) || '';
      }
      return label;
    }

    function sendCallConnected(call) {
      LogSvc.debug('[DeviceDiagnosticSvc]: Send call connected event to backend. call = ', call.callId);

      var stats = getDeviceStatistics() || {};

      WebRTCAdapter.getMediaSources(function (audioSources, videoSources, audioOutputDevices) {
        // Additional data for call connected
        var device = Utils.selectMediaDevice(audioSources, RtcSessionController.recordingDevices);
        stats.recordingDevice = getDeviceName(device);
        device = Utils.selectMediaDevice(audioOutputDevices, RtcSessionController.playbackDevices);
        stats.playbackDevice = getDeviceName(device);
        stats.negotiationInfos = [];

        var clientInfo = {
          sdpOrigin: call.sdpOrigin || undefined,
          userId: $rootScope.localUser.userId,
          convId: call.convId,
          clientInfoType: Constants.RtcClientInfoType.CALL_CONNECTED,
          reason: Constants.RtcClientInfoReason.CALL_CONNECTED,
          timestamp: Date.now(),
          sessionId: call.callId,
          deviceDiagnostics: stats,
          instanceId: call.instanceId
        };

        _clientApiHandler.sendClientInfo([clientInfo], function (err) {
          if (err) {
            LogSvc.debug('[DeviceDiagnosticSvc]: Failed to send call connected event. ', err);
          }
        });
      });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    if (_supportsOfflineFailures) {
      PubSubSvc.subscribe('/conversations/loadComplete', function () {
        LogSvc.debug('[DeviceDiagnosticSvc]: Received /conversations/loadComplete event');
        sendOfflineJoinFailures();
      });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.startDiagnostics = startDiagnostics;

    this.startJoinDelayTimer = startJoinDelayTimer;

    this.createActionInfo = createActionInfo;

    this.finishActionInfo = finishActionInfo;

    this.finishDeviceDiagnostics = finishDeviceDiagnostics;

    this.forceFinishDeviceDiagnostics = forceFinishDeviceDiagnostics;

    this.storeClientInfo = storeClientInfo;

    this.sendCallConnected = sendCallConnected;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////////////////
    init();

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.DeviceDiagnosticSvcImpl = DeviceDiagnosticSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global RegistrationState, require*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  var AtcCallInfo = unifyPhone.AtcCallInfo;
  var AtcMessage = unifyPhone.Enums.AtcMessage;
  var BusyHandlingOptions = unifyPhone.BusyHandlingOptions;
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var Constants = unifyPhone.Constants;
  var Enums = unifyPhone.Enums;
  var LocalCall = unifyPhone.LocalCall;
  var Proto = unifyPhone.Proto;
  var RedirectionTypes = unifyPhone.Enums.RedirectionTypes;
  var RemoteCall = unifyPhone.RemoteCall;
  var RoutingOptions = unifyPhone.RoutingOptions;
  var RtcParticipant = unifyPhone.RtcParticipant;
  var RtcSessionController = unifyPhone.RtcSessionController;
  var sdpParser = unifyPhone.sdpParser;
  var UserToUserHandler = unifyPhone.UserToUserHandlerSingleton;
  var Utils = unifyPhone.Utils;
  var PhoneNumberFormatter = unifyPhone.PhoneNumberFormatter;
  var WebRTCAdapter = unifyPhone.WebRTCAdapter;

  var HD_VIDEO_RESOLUTIONS = Object.values(unifyPhone.Enums.VideoResolutionLevel);

  ///////////////////////////////////////////////////////////////////////////////////////
  // UnifyPhoneCallControlSvc Implementation
  // Controls and manages WebRTC calls.
  ///////////////////////////////////////////////////////////////////////////////////////

  // eslint-disable-next-line max-params, max-lines-per-function
  function UnifyPhoneCallControlSvcImpl( // NOSONAR
    $rootScope,
    $timeout,
    $interval,
    $window,
    $q,
    LogSvc,
    PubSubSvc,
    UserSvc,
    ConversationSvc,
    NotificationSvc,
    InstrumentationSvc,
    DeviceDiagnosticSvc
  ) {
    // The following imports need to be defined inside UnifyPhoneCallControlSvcImpl due to JS-SDK
    var Conversation = unifyPhone.Conversation;
    var UserProfile = unifyPhone.UserProfile;

    LogSvc.debug('New Service: UnifyPhoneCallControlSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var CLOSE_CALL_DELAY = 3200; // Set the delay a little over 3 seconds to give enough time for 3 failed tones
    var TURN_TTL_RECOVER_TIME = 60000; // 60 seconds
    var ATC_HANDOVER_TIME = 6000; // 6 seconds
    var REVERSE_LOOKUP_MAX_TIME = 2000; // 2 seconds
    var ATC_PICKUP_TIMER = 4000; // 4 seconds
    var MAX_TRANSCRIPTIONS = 10;
    var SET_VIDEO_RECEIVER_CONFIGURATION_DELAY = 500; // 500ms
    var STREAM_ENDED_DELAY = 500; // If a media stream has ended (e.g.: microphone), wait 500ms before acting upon it

    var NOP = function () {};

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _that = this;

    var _clientApiHandler = ClientApiHandler.getInstance();
    var _userToUserHandler = UserToUserHandler.getInstance();

    var _isMobile = Utils.isMobile();
    var _isCordova = !!$window.cordova;

    // All calls, local and remote
    var _calls = [];

    var _primaryLocalCall = null;
    var _secondaryLocalCall = null;

    // Calls established on other clients
    var _activeRemoteCalls = [];

    // Incoming alerting calls, we only support one for now
    var _incomingCalls = [];

    // Last ended call. Save the last ended call until a new call exists. Used for move calls scenarios.
    var _lastEndedCall = null;

    // Call id for the remote call that has handoverInProgress. Populated during /csta/handover event
    var _callIdToBeReplaced = null;

    var _wasMuted = false;
    var _disableRemoteVideoByDefault = false;
    var _sessionHash = {};
    var _activeSpeakerPromise = null;
    var _clientDiagnosticsDisabled = false;
    var _conversationLoaded = false;
    var _pendingInvites = {};
    var _pendingRemoteCalls = {};
    var _handoverTimer = null;
    var _turnCredentials = null;
    var _pickupTimer = null;
    var _callToPickup = null;
    var _pendingSetVideoReceiverConfiguration = null;
    var _streamEndedTimeout = null;
    var _agentHuntGroupEnabled = false;

    var _negotiationFailureCauses = [
      Constants.RTCSessionParticipantLeftCause.TRANSPORT_NEGOTIATION_FAILED,
      Constants.RTCSessionParticipantLeftCause.NEGOTIATION_FAILED,
      Constants.RTCSessionParticipantLeftCause.SECURITY_NEGOTIATION_FAILED
    ];

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function updateAgentHuntGroupEnabled(enabled) {
      _agentHuntGroupEnabled = !!enabled;
    }

    function getConversation(convId) {
      return ConversationSvc.getConversationFromCache(convId);
    }

    function updateTurnExpireTime() {
      if (_turnCredentials) {
        var ttl = (_turnCredentials.ttl || 0) * 1000; // Convert to ms
        ttl = Math.max(ttl - TURN_TTL_RECOVER_TIME, 0);
        _turnCredentials.expirationTimestamp = Date.now() + ttl;
      }
    }

    function adjustMediaTypeConstraints(mediaType) {
      mediaType.audio = !!mediaType.audio;
      mediaType.video = !!mediaType.video;
      mediaType.desktop = !!mediaType.desktop;

      if (!mediaType.video) {
        mediaType.hdVideo = false;
      }
      if (!mediaType.desktop) {
        mediaType.hdDesktop = false;
      }
      if (unifyPhone.isSDK || (!mediaType.video && !mediaType.desktop)) {
        // For now we're bypassing HD permission checks for SDK apps
        return;
      }

      // Note that the BL must only automatically enable HD if hdVideo/hdDesktop is not set (i.e. typeof !== 'boolean')
      if (mediaType.video) {
        mediaType.hdVideo = !!($rootScope.localUser.canUseHDVideo && (typeof mediaType.hdVideo === 'boolean' ? mediaType.hdVideo : true));
      }
      if (mediaType.desktop) {
        mediaType.hdDesktop = !!($rootScope.localUser.canUseHDScreenShare && (typeof mediaType.hdDesktop === 'boolean' ? mediaType.hdDesktop : true));
      }
    }

    function getTurnCredentials(call) {
      return new $q(function (resolve, reject) {
        var expirationTimestamp = (_turnCredentials && _turnCredentials.expirationTimestamp) || 0;
        if (Date.now() < expirationTimestamp) {
          var expirationDate = new Date(expirationTimestamp);
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Existing credentials are still valid. Expiration: ' + expirationDate);
          resolve(_turnCredentials);
          return;
        }

        LogSvc.debug('[UnifyPhoneCallControlSvc]: Get new TURN credentials');
        var renewTurnCredentialsInfo = DeviceDiagnosticSvc.createActionInfo(call, Constants.RtcDiagnosticsAction.RENEW_TURN_CREDENTIALS);
        _clientApiHandler.renewTurnCredentials(call.callId, function (err, servers) {
          $rootScope.$apply(function () {
            if (renewTurnCredentialsInfo) {
              renewTurnCredentialsInfo.data = JSON.stringify(servers);
              DeviceDiagnosticSvc.finishActionInfo(call, renewTurnCredentialsInfo);
            }
            if (err) {
              LogSvc.error('[UnifyPhoneCallControlSvc]: Error renewing TURN credentials.', err);
              reject(err);
              return;
            }
            if (!servers || !servers.length) {
              LogSvc.error('[UnifyPhoneCallControlSvc]: Error renewing TURN credentials. No server(s) available.');
              reject('No TURN server(s) available');
              return;
            }
            servers = servers[0];
            LogSvc.info('[UnifyPhoneCallControlSvc]: Received new TURN credentials=', servers.turnServer);
            _turnCredentials = servers;
            updateTurnExpireTime();
            resolve(_turnCredentials);
          });
        });
      });
    }

    function prepareSession(conv, localCall, options) {
      return new $q(function (resolve, reject) {
        var prepareSessionData = {
          convId: conv.convId,
          rtcSessionId: localCall.callId,
          ownerId: conv.creatorId,
          mediaNode: localCall.getMediaNode(),
          isTelephonyConversation: conv.isTelephony,
          replaces: options.replaces && options.replaces.callId,
          desiredRegion: options.desiredRegion
        };

        DeviceDiagnosticSvc.startDiagnostics(localCall);
        // Only start the join delay timer for group call here, for direct calls we start it in sdpAnswer or answerRtcCall.
        if (!localCall.isDirect) {
          DeviceDiagnosticSvc.startJoinDelayTimer(localCall);
        }

        var prepareActionInfo = DeviceDiagnosticSvc.createActionInfo(localCall, Constants.RtcDiagnosticsAction.PREPARE);

        _clientApiHandler.prepareSession(prepareSessionData, function (err, servers, newRtcSessionId) {
          $rootScope.$apply(function () {
            if (prepareActionInfo) {
              prepareActionInfo.data = err ? err.toString() : JSON.stringify(servers);
              DeviceDiagnosticSvc.finishActionInfo(localCall, prepareActionInfo);
            }

            if (err) {
              if (err === Constants.ErrorCode.RTC_CONCURRENT_INCOMING_CALL) {
                LogSvc.debug('[UnifyPhoneCallControlSvc]: Concurrent incoming call, aborting own attempt.');
              } else if (err === Constants.ErrorCode.RTC_NO_MEDIA_NODES_AVAILABLE) {
                LogSvc.error('[UnifyPhoneCallControlSvc]: No media nodes available, call not possible.');
              } else if (err === Constants.ErrorCode.RTC_MEDIA_NODE_UNREACHABLE) {
                LogSvc.error('[UnifyPhoneCallControlSvc]: Specified media node is not reachable, call not possible.');
              } else {
                LogSvc.error('[UnifyPhoneCallControlSvc]: Error retrieving start session data. ', err);
              }

              reject(err);
              return;
            }

            if (!servers || !servers.length) {
              LogSvc.error('[UnifyPhoneCallControlSvc]: Error getting TURN credentials. No server(s) available.');
              reject('No TURN server(s) available');
              return;
            }

            LogSvc.info('[UnifyPhoneCallControlSvc]: Received new TURN credentials');
            servers = servers[0];
            _turnCredentials = servers;
            updateTurnExpireTime();

            if (localCall.isTelephonyCall && newRtcSessionId && newRtcSessionId !== localCall.callId) {
              // For telephony calls, the call ID (i.e. rtc session ID) can be different from the
              // conversation's rtcSessionId since we can support up to two calls in the same conversation.
              var telephonyConv = getConversation(localCall.convId);

              if (telephonyConv && (Utils.isMobile() || unifyPhone.isSDK)) {
                // For mobile clients and SDK we need to "terminate" the old call and create a new call.
                localCall.setCallIdForTelephony(newRtcSessionId);

                // Create a temporary call object with old rtc session id to raise call ended event to UI
                // The "replaced" flag is set to true in the ended message so that the
                // mobile client knows this is part of a call update.
                var oldCall = new LocalCall(telephonyConv, { clientId: localCall.clientId });
                telephonyConv.call = oldCall;

                // Terminate the call to clear its resources.
                oldCall.terminate();

                // End temporary call associated to old coversation
                LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/ended event');
                PubSubSvc.publish('/call/ended', [oldCall, true]);

                telephonyConv.call = localCall;

                publishConversationUpdate(telephonyConv);

                // Inform the UI that call has been moved
                LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/moved event');
                PubSubSvc.publish('/call/moved', [oldCall.callId, localCall.callId]);

                // The /call/state event must be after /call/moved event (required by iOS)
                publishCallState(localCall);
              } else {
                // For web/DA we can simply update the call ID and keep using the same call object
                var oldCallId = localCall.callId;
                localCall.setCallIdForTelephony(newRtcSessionId);

                // Events have already been published for the old call ID, so we need to notify the GUI that
                // this call has a new call ID
                publishCallState(localCall, oldCallId);
              }
            }
            resolve(_turnCredentials);
          });
        });
      });
    }

    function getJoinErrorText(error) {
      if (error) {
        if (error.startsWith('res_')) {
          return error;
        }
        if (error === Constants.ErrorCode.PERMISSION_DENIED) {
          return 'res_JoinRTCSessionFailedPermission';
        }
        if (error === Constants.SdpFailedCause.JOIN_FORBIDDEN) {
          return 'res_JoinRTCSessionFailedForbidden';
        }
        if (error === Constants.ErrorCode.RTC_NO_MEDIA_NODES_AVAILABLE) {
          return 'res_JoinRTCSessionFailedMissingResources';
        }
        if (error === Constants.ReturnCode.CHOOSE_DESKTOP_MEDIA_CANCELLED) {
          return error;
        }
        if (error === Constants.ReturnCode.DISCONNECTED || error === Constants.ReturnCode.FAILED_TO_SEND) {
          return 'res_NotConnectedTryAgain';
        }
      }
      // Default error
      return 'res_JoinRTCSessionFailed';
    }

    /**
     * Randomly generate an ACTIVE_SPEAKER and VIDEO_ACTIVE_SPEAKER events
     */
    function mockActiveSpeakerEvents() {
      // Randomly execute this code
      if (!Utils.randomBoolean()) {
        return;
      }

      // Only get the participants that are active in the call
      var activeParticipants = _primaryLocalCall.participants.filter(function (p) {
        return p.pcState === Enums.ParticipantState.Active;
      });

      if (activeParticipants.length < 2) {
        // Not a group call with 3+ participants
        return;
      }

      // Add local user
      activeParticipants.push($rootScope.localUser);

      // 1) ACTIVE_SPEAKER event
      var speakerEvt = { sessionId: _primaryLocalCall.callId };
      var fields = ['first', 'second', 'third'];

      var numSpeakers = Utils.randomNumber(0, 3);
      // Get the active speakers
      var activeSpeakers = Utils.randomArrayCopy(activeParticipants, numSpeakers);

      // The first participant in the array is the main speaker, so we will include
      // it more often.
      if (activeSpeakers.indexOf(activeParticipants[0]) === -1 && Utils.randomBoolean()) {
        // Add main speaker
        activeSpeakers.unshift(activeParticipants[0]);
        if (numSpeakers < 3) {
          numSpeakers++;
        }
      }

      for (var idx = 0; idx < numSpeakers; idx++) {
        speakerEvt[fields[idx]] = activeSpeakers[idx].userId;
      }

      LogSvc.debug('[UnifyPhoneCallControlSvc]: Firing mocked ACTIVE_SPEAKER event: ', speakerEvt);
      onActiveSpeakerEvent(speakerEvt);
    }

    function isActiveSpeakerSimulationNeeded() {
      return _primaryLocalCall && _primaryLocalCall.isMocked && !_primaryLocalCall.isDirect && _primaryLocalCall.participants.length > 1;
    }

    function simulateActiveSpeakers() {
      if (_activeSpeakerPromise || !isActiveSpeakerSimulationNeeded()) {
        return;
      }

      _activeSpeakerPromise = $interval(
        function () {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Active speaker simulation interval has fired');
          if (!isActiveSpeakerSimulationNeeded()) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Active speaker simulation is no longer needed. Stop interval.');
            $interval.cancel(_activeSpeakerPromise);
            _activeSpeakerPromise = null;
          } else if (_primaryLocalCall.isEstablished()) {
            // Mock ActiveSpeaker event
            mockActiveSpeakerEvents();
          }
        },
        2000,
        0,
        false
      ); // apply MUST be set to false here
    }

    function addCallToList(call, incomingOnly) {
      if (call.checkState(Enums.CallState.Ringing)) {
        _incomingCalls.push(call);
        if (incomingOnly) {
          return;
        }
      }
      for (var idx = 0; idx < _calls.length; idx++) {
        if (call.sameAs(_calls[idx])) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Updating call with callId = ' + call.callId);
          _calls[idx] = call;
          return;
        }
      }
      // This is a new call
      _calls.push(call);

      if (call === _primaryLocalCall) {
        simulateActiveSpeakers();
      }
      if (
        ($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiVB) &&
        call.isTelephonyCall &&
        !call.isRemote &&
        _primaryLocalCall &&
        call !== _primaryLocalCall
      ) {
        _secondaryLocalCall = call;
      }

      _lastEndedCall = null;
    }

    function hasScreenControlFeature(call) {
      return !!call && $rootScope.localUser.remoteControlEnabled;
    }

    function removeCallFromList(call) {
      // Remove it from the call list
      var idx;
      for (idx = 0; idx < _calls.length; idx++) {
        if (call.sameAs(_calls[idx])) {
          _calls.splice(idx, 1);
          break;
        }
      }
      for (idx = 0; idx < _incomingCalls.length; idx++) {
        if (call.sameAs(_incomingCalls[idx]) && !call.isHandoverInProgress) {
          _incomingCalls.splice(idx, 1);
          break;
        }
      }
      for (idx = 0; idx < _activeRemoteCalls.length; idx++) {
        if (call.sameAs(_activeRemoteCalls[idx])) {
          _activeRemoteCalls.splice(idx, 1);
          break;
        }
      }
      if (!call.isRemote) {
        _wasMuted = call.sessionCtrl && call.sessionCtrl.isMuted();
        _lastEndedCall = call;
        $timeout(function () {
          if (_lastEndedCall === call) {
            _lastEndedCall = null;
          }
        }, CLOSE_CALL_DELAY);
      } else {
        _lastEndedCall = null;
      }
    }

    function publishConversationUpdate(conversation) {
      if (conversation) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /conversation/update event. convId = ', conversation.convId);
        PubSubSvc.publish('/conversation/update', [conversation]);
      }
    }

    function publishCallState(call, oldCallId) {
      if (call && call.state !== Enums.CallState.Terminated) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/state event. callId = ' + call.callId + ', state = ' + call.state.name);
        PubSubSvc.publish('/call/state', oldCallId ? [call, oldCallId] : [call]);
      }
    }

    function publishIceConnectionState(call, iceConnectionState) {
      if (call) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/rtp/iceConnectionState event. callId = ' + call.callId + ', iceState = ' + iceConnectionState);
        PubSubSvc.publish('/call/rtp/iceConnectionState', [call.callId, iceConnectionState]);
      }
    }

    function getCallReplaced(call) {
      if (_primaryLocalCall && _primaryLocalCall.sameAs(call)) {
        // Check if there is an incoming call replacing this call
        var alertingCall = _that.getIncomingCall();
        return !!(alertingCall && alertingCall.replaces && alertingCall.replaces.sameAs(call));
      }
      return !!(_primaryLocalCall && _primaryLocalCall.replaces && _primaryLocalCall.replaces.sameAs(call));
    }

    function isOfflineFailure(reason) {
      switch (reason) {
        case Enums.CallClientTerminatedReason.DISCONNECTED:
        case Enums.CallClientTerminatedReason.FAILED_TO_SEND:
        case Enums.CallClientTerminatedReason.REQUEST_TIMEOUT:
          return true;
        default:
          return false;
      }
    }

    function terminateCall(call, reason, suppressEvent) {
      if (!call) {
        return;
      }

      LogSvc.info('[UnifyPhoneCallControlSvc]: Terminating call with reason: ', reason);
      var replaced = getCallReplaced(call);

      if (_pendingSetVideoReceiverConfiguration && _pendingSetVideoReceiverConfiguration.callId === call.callId) {
        _pendingSetVideoReceiverConfiguration.cancel();
      }

      // Dismiss incoming call notification if present
      dismissNotification(call);

      if (!call.isRemote) {
        if (reason) {
          call.terminateReason = reason;
          if (isOfflineFailure(reason)) {
            storeOfflineJoinFailure(call);
          }
        }

        DeviceDiagnosticSvc.forceFinishDeviceDiagnostics(call);
      }

      if (call.rejectGetScreenPromise) {
        call.rejectGetScreenPromise(Constants.ReturnCode.CHOOSE_DESKTOP_MEDIA_CANCELLED);
        call.rejectGetScreenPromise = null;
      }

      if (!call.isOsBizSecondCall) {
        unregisterSessionController(call);
      }
      removeCallFromList(call);
      call.terminate();

      // First terminate the call
      if (_primaryLocalCall && _primaryLocalCall.sameAs(call)) {
        // Cancel terminate timer (if running)
        $timeout.cancel(_primaryLocalCall.terminateTimer);
        // HYD-9299: Do not promote a not established secondary call to primary for MiVB- it
        // hasn't gone through answerCallContinue yet and isn't a real active call to switch to
        var isSecondaryUnansweredMiVBCall = $rootScope.localUser.isMiVB && _secondaryLocalCall && !_secondaryLocalCall.isEstablished();
        _primaryLocalCall = isSecondaryUnansweredMiVBCall ? null : _secondaryLocalCall;
        _secondaryLocalCall = null;
      } else if (_secondaryLocalCall && _secondaryLocalCall.sameAs(call)) {
        $timeout.cancel(_secondaryLocalCall.terminateTimer);
        _secondaryLocalCall = null;
      }

      // Now remove it from the conversation
      var conversation = getConversation(call.convId);
      if (conversation && conversation.call && conversation.call.sameAs(call) && !call.isHandoverInProgress) {
        if (call.isTelephonyCall) {
          var otherCall = findActivePhoneCall() || findHeldPhoneCall();
          conversation.call = otherCall || null;
        } else {
          conversation.call = null;
        }

        publishConversationUpdate(conversation);
      }

      // If there are no more calls, cancel the activeSpeakerPromise timer
      if (_activeSpeakerPromise && !_primaryLocalCall) {
        $interval.cancel(_activeSpeakerPromise);
        _activeSpeakerPromise = null;
      }

      if (!suppressEvent) {
        // Check if _primaryLocalCall has replaced this call
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/ended event. Replaced=', replaced);
        PubSubSvc.publish('/call/ended', [call, replaced]);

        if (call.ratingEvent) {
          publishShowRatingDialog(call.ratingEvent);
        }
      }
    }

    function storeOfflineJoinFailure(call) {
      if (!call) {
        return;
      }

      var failureReason = '';
      switch (call.terminateReason) {
        case Enums.CallClientTerminatedReason.DISCONNECTED:
          failureReason = Constants.RtcClientInfoReason.DISCONNECTED;
          break;
        case Enums.CallClientTerminatedReason.FAILED_TO_SEND:
          failureReason = Constants.RtcClientInfoReason.FAILED_TO_SEND;
          break;
        case Enums.CallClientTerminatedReason.REQUEST_TIMEOUT:
          failureReason = Constants.RtcClientInfoReason.REQUEST_TIMEOUT;
          break;
        default:
          // For now we store only no network connection issues here
          return;
      }

      var clientInfo = {
        userId: $rootScope.localUser.userId,
        convId: call.convId,
        clientInfoType: Constants.RtcClientInfoType.OFFLINE_JOIN_FAILURE,
        reason: failureReason,
        timestamp: Date.now(),
        sessionId: call.callId
      };

      // We need to check the calls which are remoteActive for this session Id whether it contains instanceId
      var conv = ConversationSvc.getConversationByRtcSession(call.callId);
      if (conv && conv.call && conv.call.instanceId) {
        clientInfo.instanceId = conv.call.instanceId;
      }

      LogSvc.info('[UnifyPhoneCallControlSvc]: Store new offline join failure. ', clientInfo);
      DeviceDiagnosticSvc.storeClientInfo(clientInfo);
    }

    function findLocalCallByCallId(callId) {
      if (_primaryLocalCall && _primaryLocalCall.callId === callId) {
        return _primaryLocalCall;
      }
      if (_secondaryLocalCall && _secondaryLocalCall.callId === callId) {
        return _secondaryLocalCall;
      }
      return null;
    }

    function findCall(rtcSessionId) {
      var idx;
      for (idx = 0; idx < _calls.length; idx++) {
        if (_calls[idx].callId === rtcSessionId) {
          return _calls[idx];
        }
      }

      // There's maybe an incoming call hidden from main call list
      for (idx = 0; idx < _incomingCalls.length; idx++) {
        if (_incomingCalls[idx].callId === rtcSessionId) {
          return _incomingCalls[idx];
        }
      }

      // We could not find the call in the call list. Let's make sure
      // that we don't find the call in the conversation.
      var conversation = ConversationSvc.getConversationByRtcSession(rtcSessionId);
      if (
        conversation &&
        conversation.call &&
        !conversation.call.isAtcRemote &&
        conversation.call.state.established &&
        conversation.call.callId === rtcSessionId
      ) {
        // Somehow we are out of synch.
        // Add the call to the call list and continue.
        _calls.push(conversation.call);
        return conversation.call;
      }
      return findLocalCallByCallId(rtcSessionId);
    }

    function lookupParticipant(rtcParticipant, call) {
      if (rtcParticipant.participantType === Constants.RTCParticipantType.TELEPHONY && rtcParticipant.phoneNumber) {
        var participant = _primaryLocalCall.getParticipant(rtcParticipant.userId);
        //remove spaces from String
        var rtcParticipantPhoneNumber = rtcParticipant.phoneNumber.replace(/\s/g, '');
        if (
          participant &&
          participant.phoneNumber &&
          (participant.phoneNumber === rtcParticipant.phoneNumber || participant.phoneNumber === rtcParticipantPhoneNumber)
        ) {
          // Not need to lookup and update
          return;
        }
        UserSvc.startReverseLookUp(rtcParticipantPhoneNumber, function (user) {
          if (call.isPresent() && user) {
            var callParticipant = _primaryLocalCall.getParticipant(rtcParticipant.userId);
            if (callParticipant) {
              callParticipant.firstName = user.firstName;
              callParticipant.lastName = user.lastName;
              callParticipant.displayName = user.displayName;
              // Store the resolved userId in the resolvedUserId field, since the participant object
              // already has a userId field, which is generated for TELEPHONY participants
              callParticipant.resolvedUserId = user.userId;
              // Publish /call/participant/updated so the application can update the participant's name
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/updated event. userId =', callParticipant.userId);
              PubSubSvc.publish('/call/participant/updated', [_primaryLocalCall.callId, callParticipant]);
            }
          }
        });
      }
    }

    function findActivePhoneCall(onlyLocal) {
      return (
        _calls.find(function (call) {
          return call.isTelephonyCall && call.state.established && !call.isHolding() && (!onlyLocal || !call.isRemote);
        }) || null
      );
    }

    function findHeldPhoneCall(onlyLocal) {
      return (
        _calls.find(function (call) {
          return call.isTelephonyCall && call.state.established && call.isHolding() && (!onlyLocal || !call.isRemote);
        }) || null
      );
    }

    function isAtcDefaultBusyHandlingSelected() {
      return (
        !$rootScope.localUser.selectedBusyHandlingOption ||
        $rootScope.localUser.selectedBusyHandlingOption === BusyHandlingOptions.DefaultRouting.name ||
        $rootScope.localUser.isSTC
      );
    }

    function setCallPeerUser(call, phoneNumber, fqNumber, displayName, userId, cb) {
      if (call.atcCallInfo) {
        // this is an atc call
        if (!fqNumber && call.atcCallInfo.peerFQN) {
          // the fqn is available in the atcCallInfo
          fqNumber = call.atcCallInfo.peerFQN;
        } else if (fqNumber && !call.atcCallInfo.peerFQN) {
          // the fqn is not available in the atcCallInfo yet
          call.atcCallInfo.peerFQN = fqNumber;
        }
      }

      if (fqNumber && call.peerUser && call.peerUser.fqnLookedUp === fqNumber) {
        // fqNumber already looked up
        cb && cb();
        return;
      }

      // Temporarily display whatever is available until user search is done
      call.setPeerUser(phoneNumber, displayName, userId);

      if (userId || !fqNumber) {
        cb && cb();
        return;
      }

      var maxWaitTime = null;
      if (cb) {
        maxWaitTime = $timeout(function () {
          maxWaitTime = null;
          if (call.isPresent()) {
            cb();
            cb = null;
          }
        }, REVERSE_LOOKUP_MAX_TIME);
      }

      UserSvc.startReverseLookUp(fqNumber, function (user) {
        $timeout.cancel(maxWaitTime);
        if (call.isPresent()) {
          if (user) {
            var data = {
              extAvatarUri: user.extAvatarUri,
              smallImageUri: user.smallImageUri,
              largeImageUri: user.largeImageUri
            };
            call.setPeerUser(phoneNumber, user.displayName, user.userId, fqNumber, data);
            // After setting the peer user a /call/state event shall be triggered to
            // update mobile call stage unless a callback has been passed. In this
            // case it is assumed all proper events will be raised by the callback.
            cb ? cb() : publishCallState(call);
          } else {
            cb && cb();
          }
        }
      });
    }

    function setRedirectingUser(call, phoneNumber, fqNumber, displayName, type) {
      if (call.redirectingUser && call.redirectingUser.redirectionType) {
        return;
      }
      call.setRedirectingUser(phoneNumber, fqNumber, displayName, null, type);

      if (fqNumber) {
        UserSvc.startReverseLookUp(fqNumber, function (user) {
          if (user && call.isPresent()) {
            call.setRedirectingUser(phoneNumber, fqNumber, user.displayName, user.userId, type);
            publishCallState(call);
          }
        });
      } else {
        publishCallState(call);
      }
    }

    function normalizeApiParticipant(apiParticipant, mediaType) {
      var isUnifyPhoneUser = !apiParticipant.participantType || apiParticipant.participantType === Constants.RTCParticipantType.USER;

      var user;
      if (apiParticipant.userId === $rootScope.localUser.userId) {
        user = $rootScope.localUser;
      } else if (isUnifyPhoneUser) {
        // The participant is a regular UnifyPhone user. Get the user from the cache.
        user = UserSvc.getUserFromCache(apiParticipant.userId);
      }

      if (!user) {
        apiParticipant.userDisplayName = apiParticipant.userDisplayName || apiParticipant.phoneNumber || $rootScope.i18n.map.res_Unknown;

        var tmp = apiParticipant.userDisplayName.split(' ');
        apiParticipant.firstName = tmp[0];
        apiParticipant.lastName = tmp.splice(1).join(' ');

        if (isUnifyPhoneUser) {
          // User is not yet cached. Create an empty user profile with noData, so the user
          // information gets retrieved in addParticipantToCallObj.
          user = UserProfile.extend({
            userId: apiParticipant.userId,
            firstName: apiParticipant.firstName,
            lastName: apiParticipant.lastName,
            displayName: apiParticipant.userDisplayName,
            location: apiParticipant.location,
            userType: Constants.UserType.REGULAR
          });

          // Add new user object to cache IF this is not a simulated participant
          if (apiParticipant.isSimulated) {
            user.isSimulated = true;
          } else {
            user.noData = true;
            UserSvc.addUsersToCache(user, true, false);
          }
        } else {
          // Create a User object based on the participant data
          if (apiParticipant.participantType === Constants.RTCParticipantType.SESSION_GUEST) {
            apiParticipant.userType = Constants.UserType.SESSION_GUEST;
          }
          user = UserProfile.extend(apiParticipant);
        }
      }

      // Create a derived object
      var participant = RtcParticipant.createFromUser(user, Enums.ParticipantState.Active);
      participant.apiDisplayName = apiParticipant.userDisplayName || apiParticipant.phoneNumber;
      participant.streamId = apiParticipant.videoStreamId || '';
      participant.screenStreamId = apiParticipant.screenStreamId || '';
      participant.mediaType = Proto.getMediaType(apiParticipant.mediaTypes || mediaType);
      participant.muted = apiParticipant.muted || !participant.mediaType.audio;
      participant.participantType = apiParticipant.participantType || Constants.RTCParticipantType.USER;
      participant.screenSharePointerSupported = !!apiParticipant.screenSharePointerSupported;
      participant.isModerator = !!apiParticipant.isModerator;
      participant.isMeetingGuest = apiParticipant.isMeetingGuest;
      participant.rtcSupportedFeatures = apiParticipant.rtcSupportedFeatures || [];
      participant.flags = apiParticipant.flags || [];
      return participant;
    }

    function putSessionTurn(sessionId, turnServers) {
      if (!turnServers || !turnServers.length) {
        return;
      }
      var cachedSession = _sessionHash[sessionId];
      if (cachedSession) {
        cachedSession.turnServers = turnServers;
      } else {
        _sessionHash[sessionId] = { turnServers: turnServers };
      }
    }

    function getSessionTurnUris(rtcSessionId) {
      var cachedSession = _sessionHash[rtcSessionId];
      if (cachedSession && cachedSession.turnServers && cachedSession.turnServers.length) {
        return cachedSession.turnServers;
      }
      return null;
    }

    function getClientTerminatedReason(retCode) {
      switch (retCode) {
        case Constants.ReturnCode.DISCONNECTED:
          return Enums.CallClientTerminatedReason.DISCONNECTED;
        case Constants.ReturnCode.FAILED_TO_SEND:
          return Enums.CallClientTerminatedReason.FAILED_TO_SEND;
        case Constants.ReturnCode.REQUEST_TIMEOUT:
          return Enums.CallClientTerminatedReason.REQUEST_TIMEOUT;
        default:
          return Enums.CallClientTerminatedReason.REQUEST_TO_SERVER_FAILED;
      }
    }

    function terminateNewLocalCall(newLocalCall, cb) {
      if (!newLocalCall) {
        return;
      }
      LogSvc.debug('[UnifyPhoneCallControlSvc]: New call has been terminated during call setup. Call Id:', newLocalCall.callId);
      if (newLocalCall.callState !== Enums.CallState.Terminated) {
        terminateCall(newLocalCall, Enums.CallClientTerminatedReason.USER_ENDED);
      }
      cb && cb('Call already terminated');
    }

    function terminateRemoteAndAddLocal(call, conversation, options) {
      if (!call || !call.isRemote || call.isAtcRemote) {
        return;
      }
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Terminate the remote call and add the new local call to the conversation');
      if (options.handover) {
        _primaryLocalCall.activeClient = call.activeClient; // Indicates pull in progress
        if (call.isTelephonyCall) {
          if (call.atcCallInfo) {
            _primaryLocalCall.atcCallInfo = call.atcCallInfo;
            // Since this is a local call the position must be changed to WebRTC
            _primaryLocalCall.setPosition(Enums.Targets.WebRTC);
          }
          setCallPeerUser(_primaryLocalCall, call.peerUser.phoneNumber, null, call.peerUser.displayName);
          _primaryLocalCall.participants = call.participants;
          options.pulledConference && (_primaryLocalCall.pulledConference = options.pulledConference);
        }
        conversation.call = _primaryLocalCall;
      }
      _primaryLocalCall.hadRemoteCall = true;
      if (!call.isTelephonyCall) {
        terminateCall(call);
      }
    }

    function createLocalCall(conversation, mediaType, options, cb) {
      if (!canInitiateCall(conversation, options, cb)) {
        return false;
      }
      if (_primaryLocalCall) {
        _secondaryLocalCall = _primaryLocalCall;
      }
      var newCallOptions = {
        clientId: _clientApiHandler.clientId,
        midMappingEnabled: isMidMappingEnabled(),
        isSTC: $rootScope.localUser.isSTC && conversation.isTelephony,
        stcCapabilities: conversation.isTelephony ? $rootScope.localUser.stcCapabilities : [],
        canReceiveHdVideo: $rootScope.localUser.canUseHDVideo || $rootScope.localUser.canUseHDScreenShare
      };
      if (options.replaces && mediaType.desktop) {
        // This call should reuse the desktop stream from the call it's replacing (option used by VDI)
        newCallOptions.reuseDesktopStreamFrom = options.replaces.sessionCtrl;
      }
      _primaryLocalCall = new LocalCall(conversation, newCallOptions);
      _primaryLocalCall.setState(Enums.CallState.Initiated);
      _primaryLocalCall.pickUpFromUser = options.pickUpFromUser;
      _primaryLocalCall.pickUpUserId = options.pickUpUserId;

      addCallToList(_primaryLocalCall);

      var call = conversation.call;

      terminateRemoteAndAddLocal(call, conversation, options);

      _primaryLocalCall.setCallIdForTelephony(options.callId);
      _primaryLocalCall.setTransactionId();

      if (_disableRemoteVideoByDefault || conversation.isTelephony) {
        LogSvc.info('[UnifyPhoneCallControlSvc] Disabling remote video for outgoing call');
        _primaryLocalCall.disableRemoteVideo();
      }
      _primaryLocalCall.direction = Enums.CallDirection.OUTGOING;
      _primaryLocalCall.mediaType = mediaType;
      if (options.replaces) {
        _primaryLocalCall.replaces = options.replaces;
        var oldStream = options.replaces.sessionCtrl.getLocalStream(RtcSessionController.LOCAL_STREAMS.DESKTOP);
        if (oldStream) {
          LogSvc.debug('[UnifyPhoneCallControlSvc] Reusing desktop stream from call ID=', options.replaces.callId);
          _primaryLocalCall.sessionCtrl.setLocalStream(RtcSessionController.LOCAL_STREAMS.DESKTOP, oldStream);
          // Set old desktop stream to null so it won't be stopped by the old RtcSessionController
          options.replaces.sessionCtrl.setLocalStream(RtcSessionController.LOCAL_STREAMS.DESKTOP, null);
        }
        oldStream = options.replaces.sessionCtrl.getLocalStream(RtcSessionController.LOCAL_STREAMS.AUDIO_VIDEO);
        if (oldStream) {
          LogSvc.debug('[UnifyPhoneCallControlSvc] Reusing audio/video stream from call ID=', options.replaces.callId);
          _primaryLocalCall.sessionCtrl.setLocalStream(RtcSessionController.LOCAL_STREAMS.AUDIO_VIDEO, oldStream);
          // Set old audio/video stream to null so it won't be stopped by the old RtcSessionController
          options.replaces.sessionCtrl.setLocalStream(RtcSessionController.LOCAL_STREAMS.AUDIO_VIDEO, null);
        }
      }
      if (options.joiningGroupCall) {
        _primaryLocalCall.isJoiningGroupCall = true;
      }

      if (options.callOut) {
        // For call out scenarios, add all the conversation participants
        // to the call object...
        _primaryLocalCall.setPeerUsersAsParticipants();
        // ... and save this flag in the call obj
        _primaryLocalCall.isCallOut = true;
      }

      if (_primaryLocalCall.isTelephonyCall) {
        if (!options.handover) {
          setCallPeerUser(_primaryLocalCall, options.dialedDn, null, options.toName, options.userId);
          // Store the DTMF digits on call object
          if (options.dtmfDigits) {
            _primaryLocalCall.dtmfDigits = options.dtmfDigits;
          }
        } else {
          var activeRemoteCall = getActiveRemoteCall(_primaryLocalCall.callId);
          if (activeRemoteCall) {
            _primaryLocalCall.atcCallInfo = activeRemoteCall.atcCallInfo;
            if (!activeRemoteCall.isAtcConferenceCall()) {
              setCallPeerUser(_primaryLocalCall, activeRemoteCall.peerUser.phoneNumber, null, activeRemoteCall.peerUser.displayName);
            }
            terminateCall(activeRemoteCall);
          }
          _primaryLocalCall.handover = true;
        }
      }

      publishCallState(_primaryLocalCall);

      if ((!conversation.call || !(conversation.call.isRemote && options.handover)) && !_primaryLocalCall.conferenceCall) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/outgoing event');
        PubSubSvc.publish('/call/outgoing', [_primaryLocalCall]);
      }

      _primaryLocalCall.isNewSession = !conversation.call || (conversation.call !== _primaryLocalCall && !options.handover);
      conversation.call = _primaryLocalCall;
      publishConversationUpdate(conversation);
      return true;
    }

    function getRtcSupportedFeatures(call) {
      var rtcSupportedFeatures = isMidMappingEnabled() ? [Constants.RtcSupportedFeatures.MID_STREAM_MAPPING] : [];
      if (call.isDirect) {
        rtcSupportedFeatures.push(Constants.RtcSupportedFeatures.UPGRADE_TO_CONFERENCE);
      }

      if (isVideoAndScreenShareEnabled(call)) {
        rtcSupportedFeatures.push(Constants.RtcSupportedFeatures.SCREEN_SHARE_AND_ACTIVE_SPEAKER_VIDEO);
      }

      if (call.isDirect && call.checkState(Enums.CallState.Initiated)) {
        rtcSupportedFeatures.push(Constants.RtcSupportedFeatures.CALL_PICKUP);
      }
      return rtcSupportedFeatures;
    }

    function joinSession(conversation, mediaType, options, cb) {
      if (!_primaryLocalCall) {
        cb(getJoinErrorText());
        LogSvc.error("[UnifyPhoneCallControlSvc]: joinSession failed because there's no local call");
        return;
      }
      cb = cb || NOP;

      LogSvc.debug('[UnifyPhoneCallControlSvc]: joinSession - rtcSessionId=', options.callId || conversation.rtcSessionId);

      var newLocalCall = _primaryLocalCall;
      var sessionCtrl = _primaryLocalCall.sessionCtrl;

      var onSdpOffer = function (evt) {
        sessionCtrl.onSessionDescription = null;
        sessionCtrl.onClosed = null;

        if (_primaryLocalCall !== newLocalCall) {
          terminateNewLocalCall(newLocalCall, cb);
          return;
        }

        // Let's update the local call's mediaType in case we were not
        // able to access all required media streams.
        _primaryLocalCall.mediaType = sessionCtrl.getMediaConstraints();

        var joinData = {
          convId: conversation.convId,
          rtcSessionId: _primaryLocalCall.callId || conversation.rtcSessionId,
          ownerId: conversation.creatorId,
          sdp: evt.sdp,
          mediaType: _primaryLocalCall.mediaType,
          callOut: options.callOut,
          handover: options.handover,
          sendInviteCancel: !options.callOut,
          replaces: options.replaces && options.replaces.callId,
          isTelephonyConversation: _primaryLocalCall.isTelephonyCall,
          displayName: $rootScope.localUser.displayName,
          transactionId: _primaryLocalCall.transactionId,
          screenSharePointerSupported: !!(_primaryLocalCall.pointer && _primaryLocalCall.pointer.isEnabled),
          rtcSupportedFeatures: getRtcSupportedFeatures(_primaryLocalCall),
          pickUpSession: options.pickUpSession,
          sipHeaders: options.sipHeaders
        };

        if (_primaryLocalCall.isTelephonyCall) {
          var formattedDialedDn = PhoneNumberFormatter.format(options.dialedDn);

          joinData.fromDn = $rootScope.localUser.isMiVB
            ? Utils.cleanMiVBPhoneNumber($rootScope.localUser.callerId)
            : Utils.cleanPhoneNumber($rootScope.localUser.callerId);
          joinData.fromName = $rootScope.localUser.displayName;
          joinData.fromUserId = $rootScope.localUser.userId;
          joinData.dialedDn = Utils.cleanPhoneNumber(formattedDialedDn);
          joinData.toName = options.toName;
          joinData.toUserId = options.userId;
          joinData.noCallLog = $rootScope.localUser.noCallLog;
        }

        if (conversation.isTemporary && conversation.guestToken) {
          joinData.guestToken = conversation.guestToken;
        }

        // For testing incoming telephony calls from vgtc user: Send fromDn and fromName if localUser has role: VIRTUAL_TELEPHONY_CONNECTOR
        if ($rootScope.localUser.hasTelephonyRole) {
          joinData.fromName = 'Gru';
          joinData.fromDn = '+19998455246';
        }

        var joinInfo = DeviceDiagnosticSvc.createActionInfo(_primaryLocalCall, Constants.RtcDiagnosticsAction.JOIN);
        if (joinInfo) {
          joinInfo.data = JSON.stringify(joinData.sdp);
        }

        _clientApiHandler.joinRtcCall(joinData, function (err) {
          $rootScope.$apply(function () {
            DeviceDiagnosticSvc.finishActionInfo(_primaryLocalCall, joinInfo);

            if (_primaryLocalCall !== newLocalCall) {
              terminateNewLocalCall(newLocalCall, cb);
              return;
            }
            if (err) {
              var replaces = _primaryLocalCall.replaces;
              var reason = getClientTerminatedReason(err);
              if (_primaryLocalCall.hadRemoteCall) {
                // This method will re-create the remote call
                _that.endCallWithCauseCode(_primaryLocalCall.callId, reason);
              } else {
                var callLeaveMethod = _primaryLocalCall.isDirect ? 'terminateRtcCall' : 'leaveRtcCall';
                _clientApiHandler[callLeaveMethod](_primaryLocalCall.callId, _primaryLocalCall.disconnectCause, function (terminateErr) {
                  terminateErr && LogSvc.warn('[UnifyPhoneCallControlSvc]: Error terminating RTC call. ', terminateErr);
                });
                terminateCall(_primaryLocalCall, reason);
              }
              if (replaces) {
                _primaryLocalCall = replaces;
              }
              cb(getJoinErrorText(err));
            } else {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Join RTC session call was successful');

              if (_primaryLocalCall.conferenceCall) {
                LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /conference/participant/joining event');
                PubSubSvc.publish('/conference/participant/joining', [_primaryLocalCall]);
              }
              cb(null, options.warning, _primaryLocalCall);
            }
          });
        });
      };

      var errorCb = function (err) {
        if (_primaryLocalCall === newLocalCall) {
          var reason = getClientTerminatedReason(err);
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Failed to initiate call. ', err);
          if (!options.handover) {
            _primaryLocalCall.setDisconnectCause(Constants.DisconnectCause.CALL_SETUP_FAILED, err);
            if (_primaryLocalCall.hadRemoteCall) {
              // This method will re-create the remote call
              _that.endCallWithCauseCode(_primaryLocalCall.callId, reason);
            } else {
              var callLeaveMethod = _primaryLocalCall.isDirect ? 'terminateRtcCall' : 'leaveRtcCall';
              _clientApiHandler[callLeaveMethod](_primaryLocalCall.callId, _primaryLocalCall.disconnectCause, function (terminateErr) {
                terminateErr && LogSvc.warn('[UnifyPhoneCallControlSvc]: Error terminating RTC call. ', terminateErr);
              });
              terminateCall(_primaryLocalCall, reason);
            }
          } else {
            terminateCall(_primaryLocalCall, reason);
          }
        } else {
          terminateNewLocalCall(newLocalCall, cb);
          return;
        }
        cb(getJoinErrorText(err));
      };

      var onClosed = function () {
        sessionCtrl.onClosed = null;
        sessionCtrl.onSessionDescription = null;
        if (newLocalCall && newLocalCall.state === Enums.CallState.Initiated && newLocalCall.terminateReason === Enums.CallClientTerminatedReason.USER_ENDED) {
          // Avoid error popup when call is cancelled by user at early stage
          cb && cb();
        } else {
          cb && cb(getJoinErrorText());
        }
      };

      var turnPromise;
      if (_primaryLocalCall.isNewSession) {
        // Session initiating side
        turnPromise = prepareSession(conversation, _primaryLocalCall, options);
      } else {
        turnPromise = getTurnCredentials(_primaryLocalCall);
      }
      turnPromise
        .then(function (turnCredentials) {
          if (turnCredentials) {
            putSessionTurn(conversation.rtcSessionId, turnCredentials.turnServer);
            sessionCtrl.setTurnUris(turnCredentials.turnServer);
            sessionCtrl.setTurnCredentials(turnCredentials);
          }

          function warmupMedia() {
            adjustMediaTypeConstraints(mediaType);
            sessionCtrl.warmup(
              mediaType,
              null,
              function () {
                if (_primaryLocalCall !== newLocalCall) {
                  terminateNewLocalCall(newLocalCall, cb);
                  return;
                }

                $rootScope.$apply(function () {
                  LogSvc.debug('[UnifyPhoneCallControlSvc]: The connection warmup was successful. Start the call.');
                  sessionCtrl.onSessionDescription = onSdpOffer;
                  sessionCtrl.onClosed = onClosed;
                  registerSessionController(_primaryLocalCall);
                  sessionCtrl.start(mediaType, null);
                });
              },
              function (err) {
                if (mediaType.audio && (mediaType.video || mediaType.hdVideo)) {
                  LogSvc.info('[UnifyPhoneCallControlSvc]: Fetching audio and video media failed, trying again without video');
                  mediaType.video = false;
                  mediaType.hdVideo = false;
                  warmupMedia();
                  return;
                }
                $rootScope.$apply(errorCb.bind(null, err));
              },
              options.screenShareOptions
            );
          }

          warmupMedia();
        })
        .catch(errorCb);
    }

    function changeMediaType(data, cb) {
      LogSvc.info('[UnifyPhoneCallControlSvc]: changeMediaType - data: ', data);
      var call = findLocalCallByCallId(data.callId);
      if (!call) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: changeMediaType - Cannot find local call');
        cb('No active call');
        return;
      }
      if (!data.externallyTriggered && !call.isEstablished()) {
        LogSvc.info('[UnifyPhoneCallControlSvc]: changeMediaType - The local call is not established');
        cb('Call not established');
        return;
      }

      var sessionCtrl = call.sessionCtrl;
      if (!sessionCtrl.isConnStable()) {
        cb('Connection not stable');
        return;
      }
      var invokeCb = function (err) {
        if (data.dontReuseAudioStream) {
          // We set the dontReuseAudioStream property in sessionCtrl
          // now we have to reset it
          sessionCtrl.dontReuseAudioStream = false;
        }
        PubSubSvc.publish('/call/changeMediaType/completed', [call, err]);
        cb(err);
      };

      PubSubSvc.publish('/call/changeMediaType/started', [call, data]);

      call.pendingTransactionId = null;
      sessionCtrl.dontReuseAudioStream = data.dontReuseAudioStream;
      var onSdp = function (evt) {
        sessionCtrl.onSessionDescription = null;

        if (!call.pendingTransactionId || call.pendingTransactionId !== call.transactionId) {
          // ANS-1337 - Change the transactionId when requesting ChangeMediaType
          call.setTransactionId(data.transactionId);
        }
        call.pendingTransactionId = null;

        var changeMediaData = {
          rtcSessionId: call.callId,
          sdp: evt.sdp,
          mediaType: data.mediaType,
          transactionId: call.transactionId,
          screenSharePointerSupported: !!(call.pointer && call.pointer.isEnabled),
          hosted: data.hosted,
          replaces: data.replaces,
          rtcSupportedFeatures: getRtcSupportedFeatures(call)
        };

        _clientApiHandler.changeMediaType(changeMediaData, function (err) {
          $rootScope.$apply(function () {
            call.clearTransactionId();
            if (err) {
              sessionCtrl.renegotiationFailed(err);
              invokeCb(err);
            } else {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: changeMediaType was successful. Wait for SDP_ANSWER event');
            }
          });
        });
      };

      var screenShareWasActive = call.localMediaType.desktop;
      getTurnCredentials(call)
        .then(function (turnCredentials) {
          sessionCtrl.setTurnUris(turnCredentials.turnServer);
          sessionCtrl.setTurnCredentials(turnCredentials);
          sessionCtrl.onSessionDescription = onSdp;

          if (typeof data.hold === 'boolean') {
            var holdRetrieve = data.hold ? sessionCtrl.holdCall : sessionCtrl.retrieveCall;
            holdRetrieve(function (err) {
              if (err) {
                LogSvc.error('[UnifyPhoneCallControlSvc]: Error in hold/retrieve media');
                sessionCtrl.onSessionDescription = null;
                sessionCtrl.renegotiationFailed(err);
                invokeCb('Error in hold/retrieve media');
              } else {
                invokeCb();
              }
            });
          } else {
            call.lastMediaType = call.mediaType;

            var options = {
              changeDesktopMedia: data.changeDesktopMedia,
              screenShareOptions: data.screenShareOptions,
              videoOptions: data.videoOptions,
              isDirectUpgradingToConf: !!(call.isDirect && data.hosted && data.replaces)
            };

            adjustMediaTypeConstraints(data.mediaType);
            sessionCtrl.changeMediaType(data.mediaType, options, function (err) {
              $rootScope.$apply(function () {
                if (err) {
                  LogSvc.warn('[UnifyPhoneCallControlSvc]: changeMediaType failed - reason: ', err);
                  sessionCtrl.onSessionDescription = null;
                  sessionCtrl.renegotiationFailed(err);
                  invokeCb(err);
                } else {
                  if (screenShareWasActive && !call.localMediaType.desktop) {
                    // Screen share was removed
                    LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /screenshare/ended event');
                    PubSubSvc.publish('/screenshare/ended');
                    call.pointer.isSupported = call.pointer.isEnabled = false;
                  }
                  invokeCb();
                }
              });
            });
          }
        })
        .catch(function (err) {
          invokeCb(getJoinErrorText(err));
        });
    }

    function joinSessionCalled(conversation, mediaType, options, cb) {
      if (!_primaryLocalCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: joinSessionCalled invoked without _primaryLocalCall');
        return;
      }
      var call = _primaryLocalCall;
      var sessionCtrl = call.sessionCtrl;
      options = options || {};

      var onSdp = function (evt) {
        sessionCtrl.onSessionDescription = null;

        var data = {
          convId: conversation.convId,
          rtcSessionId: call.callId || conversation.rtcSessionId,
          ownerId: conversation.creatorId,
          sdp: evt.sdp,
          mediaType: mediaType,
          callOut: false,
          handover: false,
          sendInviteCancel: true,
          isTelephonyConversation: conversation.isTelephony,
          displayName: $rootScope.localUser.displayName,
          transactionId: call.transactionId,
          rtcSupportedFeatures: getRtcSupportedFeatures(call)
        };

        function joinAnswerCb(err) {
          $rootScope.$apply(function () {
            call.clearTransactionId();
            if (err) {
              terminateCall(call, getClientTerminatedReason(err));
              cb(getJoinErrorText(err));
            } else {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Join/Answer RTC session was successful.');
              cb(null, options.warning, call);
            }
          });
        }

        if (data.sdp && data.sdp.type === 'offer') {
          _clientApiHandler.joinRtcCall(data, joinAnswerCb);
        } else {
          DeviceDiagnosticSvc.startJoinDelayTimer(call);

          _clientApiHandler.answerRtcCall(data, joinAnswerCb);
        }
      };

      sessionCtrl.onSessionDescription = onSdp;
      if (call.startedEarlyMedia) {
        sessionCtrl.startConnectedTimer();
        if (call.earlyMediaSdp) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Client already created early media. Use early media SDP for the Answer event.');
          onSdp({ sdp: call.earlyMediaSdp });
          call.earlyMediaSdp = null;
        } else {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: RTC session has already been started. Just wait for completion.');
        }
      } else if (!sessionCtrl.start(mediaType, null)) {
        terminateCall(call, Enums.CallClientTerminatedReason.RTC_SESSION_START_FAILED);
        cb(getJoinErrorText());
      }
    }

    function addRemoteCall(conversation, activeClient, session, sessionId, preprocessor) {
      if (!conversation) {
        return null;
      }
      if (conversation.isTemporary && !activeClient && !conversation.guestToken) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /conversation/temporary/ended event');
        PubSubSvc.publish('/conversation/temporary/ended', [conversation]);
        return null;
      }

      var remoteCall = new RemoteCall(conversation);
      remoteCall.setState(activeClient ? Enums.CallState.ActiveRemote : Enums.CallState.Started);

      if (session) {
        // Ensure remoteCalls get the instanceId to store join failures in state disconnected
        session.instanceId && remoteCall.setInstanceId(session.instanceId);
        session.hosted && remoteCall.setDirectUpgradedToConf();
      }

      if (sessionId) {
        remoteCall.setCallIdForTelephony(sessionId);
      }

      addCallToList(remoteCall);
      if (activeClient) {
        remoteCall.setActiveClient(activeClient);
        addActiveRemoteCall(remoteCall);
        if (session) {
          if (session.participants.length === 1 && session.callOut) {
            remoteCall.pullNotAllowed = true;
          }
          remoteCall.mediaType = Proto.getMediaType(session.mediaTypes);
        }
      }

      if (typeof preprocessor === 'function') {
        // Preprocess the remoteCall object before raising the events
        preprocessor(remoteCall);
      }

      conversation.call = remoteCall;
      publishConversationUpdate(conversation);
      publishCallState(remoteCall);

      return remoteCall;
    }

    function changeRemoteCallToStarted(activeRemoteCall) {
      activeRemoteCall.setState(Enums.CallState.Started);
      activeRemoteCall.activeClient = null;
      publishCallState(activeRemoteCall);
      _activeRemoteCalls.some(function (call, idx) {
        if (call.callId === activeRemoteCall.callId) {
          _activeRemoteCalls.splice(idx, 1);
          return true;
        }
        return false;
      });
    }

    function sendBusy(inviteEvt) {
      _clientApiHandler.declineRtcCall({
        convId: inviteEvt.convId,
        rtcSessionId: inviteEvt.sessionId,
        cause: Constants.InviteRejectCause.BUSY,
        transactionId: inviteEvt.transactionId
      });
    }

    function publishAtcCall(conversation, inviteEvt, first) {
      var call = new LocalCall(conversation, { clientId: _clientApiHandler.clientId, midMappingEnabled: isMidMappingEnabled() });
      call.setInstanceId(inviteEvt.instanceId);
      call.setTransactionId(inviteEvt.transactionId);
      call.setState(Enums.CallState.Ringing);
      if (inviteEvt.from) {
        setCallPeerUser(call, inviteEvt.from.phoneNumber, inviteEvt.from.fullyQualifiedNumber, inviteEvt.from.displayName, null, function () {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish ' + first ? 'atccall/firstcall' : '/atccall/secondcall/' + ' event.');
          PubSubSvc.publish(first ? '/atccall/firstcall' : '/atccall/secondcall', [call]);
        });
      }
    }

    function decline(incomingCall, params, suppressEvent, cb) {
      cb = cb || NOP;
      incomingCall = incomingCall || _primaryLocalCall;
      removeCallFromList(incomingCall);
      if (!incomingCall || (incomingCall.state !== Enums.CallState.Ringing && incomingCall.state !== Enums.CallState.Answering)) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: call invalid to be declined');
        cb('Call Invalid');
        return;
      }

      LogSvc.debug('[UnifyPhoneCallControlSvc]: decline - rtcSessionId: ', incomingCall.callId);

      var data = {
        convId: incomingCall.convId,
        rtcSessionId: incomingCall.callId,
        cause: params.type,
        transactionId: incomingCall.transactionId
      };

      var eventParams = [incomingCall];
      params.err && eventParams.push(params.err);

      _clientApiHandler.declineRtcCall(data, function (err) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.warn('[UnifyPhoneCallControlSvc]: Error declining the RTC call. ', err);
            cb(err);
          } else {
            cb();
          }
        });
      });

      if (!incomingCall.isDirect) {
        // Add remote call
        var conversation = getConversation(incomingCall.convId);
        addRemoteCall(conversation);
      }

      LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/declining event');
      PubSubSvc.publish('/call/declining', eventParams);
      terminateCall(incomingCall, params.type, suppressEvent);
    }

    // Leave a local call
    function leaveCall(localCall, declineReason, qosReason, isLastParticipant, cb) {
      localCall = localCall || _primaryLocalCall;

      if (!localCall) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: There is no local call');
        cb && $timeout(cb);
        return;
      }
      if (localCall.terminateTimer) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Local call is already being terminated. Ignore leave request.');
        cb && $timeout(cb);
        return;
      }
      cb = cb || NOP;
      if (localCall.checkState([Enums.CallState.Ringing, Enums.CallState.Answering])) {
        decline(localCall, { type: declineReason || Constants.InviteRejectCause.DECLINE }, false, cb);
        return;
      }

      LogSvc.debug('[UnifyPhoneCallControlSvc]: leaveCall - rtcSessionId=', localCall.callId);
      var conversation;
      localCall.clearAtcHandoverInProgress();

      if (localCall.activeClient) {
        // Call terminated while pull in progress, create an active remote call so user can pull again
        conversation = getConversation(localCall.convId);
        addRemoteCall(conversation, localCall.activeClient, null, null, function (call) {
          if (_primaryLocalCall.isTelephonyCall) {
            call.atcCallInfo = _primaryLocalCall.atcCallInfo;
            call.peerUser = _primaryLocalCall.peerUser;
          }
        });
        terminateCall(localCall, qosReason);
        return;
      }

      if (localCall.isDirect) {
        _clientApiHandler.terminateRtcCall(
          localCall.callId,
          localCall.disconnectCause,
          function (err) {
            $rootScope.$apply(function () {
              if (err) {
                LogSvc.warn('[UnifyPhoneCallControlSvc]: Error terminating RTC call. ', err);
              }
              cb && cb(err);
            });
          },
          localCall.pickUpUserId || (localCall.pickUpFromUser && localCall.pickUpFromUser.userId)
        );
      } else {
        if (!localCall.isTestCall) {
          conversation = getConversation(localCall.convId);
          if (conversation && (localCall.participants.length || conversation.isTemporary || localCall.hadRemoteCall)) {
            // Create the remote call to show that the session is still active for group sessions and guest conferences.
            var remoteCall = addRemoteCall(conversation);
            localCall.isDirectUpgradedToConf && remoteCall.setDirectUpgradedToConf();
          }
        }
        _clientApiHandler.leaveRtcCall(localCall.callId, localCall.disconnectCause, function (err) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.warn('[UnifyPhoneCallControlSvc]: Error leaving the RTC call. ', err);
            }
            cb && cb(err);
          });
        });
      }

      if (qosReason === Enums.CallClientTerminatedReason.PAGE_UNLOADED) {
        localCall.terminateReason = qosReason;
        // Since the page is unloading, immediately send the QoS report
        localCall.qosSubmitted = true;
        InstrumentationSvc.sendQOSData(localCall, localCall.mediaType, localCall.sessionCtrl.getLastSavedStats());
      }

      if (!isLastParticipant) {
        if (localCall.isDirect) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/terminating event');
          PubSubSvc.publish('/call/terminating', [_primaryLocalCall]);
        } else {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /conference/leaving event');
          PubSubSvc.publish('/conference/leaving', [localCall]);
        }
      }

      if (localCall.outgoingFailed()) {
        // Delay the terminate to keep the call header visible for a couple seconds
        localCall.terminateTimer = $timeout(function () {
          localCall.terminateTimer = null;
          terminateCall(localCall, qosReason);
        }, CLOSE_CALL_DELAY);
        localCall.terminateReason = qosReason; // In case the call is terminated before the timer pops, we have the actual reason.
      } else {
        terminateCall(localCall, qosReason);
      }
    }

    function terminateConference(call, cb) {
      cb = cb || NOP;

      if (call.isDirect) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Direct call. Ignore terminate conference request.');
        $timeout(cb);
        return;
      }

      LogSvc.debug('[UnifyPhoneCallControlSvc]: terminateConference - rtcSessionId=', call.callId);

      _clientApiHandler.terminateRtcCall(call.callId, call.disconnectCause, function (err) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.warn('[UnifyPhoneCallControlSvc]: Error terminating the RTC call. ', err);

            if (call === _primaryLocalCall) {
              var conversation = getConversation(call.convId);
              if (conversation && call.isEstablished()) {
                addRemoteCall(conversation);
              }
            }
          }
          cb && cb(err);
        });
      });
      if (call === _primaryLocalCall) {
        terminateCall(call, Enums.CallClientTerminatedReason.USER_ENDED);
      }
    }

    function endRemoteCall(remoteCall, cb) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: endRemoteCall - rtcSessionId=', remoteCall.callId);
      if (remoteCall.isDirect) {
        _clientApiHandler.terminateRtcCall(remoteCall.callId, remoteCall.disconnectCause, function (err) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.warn('[UnifyPhoneCallControlSvc]: Error terminating remote RTC call. ', err);
            }
            cb(err);
          });
        });
      } else {
        _clientApiHandler.leaveRtcCall(remoteCall.callId, remoteCall.disconnectCause, function (err) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.warn('[UnifyPhoneCallControlSvc]: Error leaving remote RTC call. ', err);
            }
            cb(err);
          });
        });
      }
    }

    function getActiveRemoteCall(callId) {
      return (
        _activeRemoteCalls.find(function (call) {
          return call.callId === callId;
        }) || null
      );
    }

    function getIncomingCall(callId) {
      return (
        _incomingCalls.find(function (call) {
          return call.callId === callId;
        }) || null
      );
    }

    function addActiveRemoteCall(remoteCall) {
      var found = _activeRemoteCalls.some(function (c, idx) {
        if (c === remoteCall) {
          return true;
        }
        if (c.callId === remoteCall.callId) {
          _activeRemoteCalls[idx] = remoteCall;
          return true;
        }
        return false;
      });
      if (!found) {
        _activeRemoteCalls.push(remoteCall);
      }
    }

    function removeSessionParticipant(userId, cb) {
      cb = cb || NOP;
      if (_primaryLocalCall) {
        var callParticipant = _primaryLocalCall.getParticipant(userId);
        if (callParticipant) {
          if (callParticipant.actions.includes(Enums.ParticipantAction.Drop)) {
            var data = {
              rtcSessionId: _primaryLocalCall.callId,
              userId: userId
            };
            _clientApiHandler.removeSessionParticipant(data, function (err) {
              $rootScope.$apply(function () {
                if (err) {
                  LogSvc.warn('[UnifyPhoneCallControlSvc]: Failed to remove participant');
                  cb('res_RemoveParticipantFailed');
                } else {
                  cb();
                }
              });
            });
          } else {
            LogSvc.warn('[UnifyPhoneCallControlSvc]: remove call participant, unsupported pcState: ', callParticipant.pcState.name);
            cb('Participant not active');
          }
        } else {
          LogSvc.error('[UnifyPhoneCallControlSvc]: call participant not found: ', userId);
          cb('Participant not found');
        }
      } else {
        cb('No active call');
      }
    }

    function verifyStreamIds(call, participant) {
      // Go through the participants list and make sure that no one
      // is using the same video stream ID of the supplied participant
      if (!participant.streamId) {
        return;
      }
      if (!call.isMocked) {
        call.participants.forEach(function (p) {
          if (p.streamId === participant.streamId && p.userId !== participant.userId) {
            p.streamId = '';
            LogSvc.debug(
              '[UnifyPhoneCallControlSvc]: The video stream has been re-assigned. Clear the streamId for previous participant. [userId, streamId] = ',
              [p.userId, participant.streamId]
            );

            _primaryLocalCall.setParticipantRemoteVideoStream(p);
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/updated event. userId =', p.userId);
            PubSubSvc.publish('/call/participant/updated', [call.callId, p]);
          }
        });
      }
    }

    function setParticipantActions(call, participant) {
      var conversation = getConversation(call.convId);
      participant.setActions(conversation, $rootScope.localUser);
    }

    function checkSessionParticipantFlags(call, participant) {
      var overloadLevel = 0;
      if (participant && participant.flags) {
        if (participant.flags.includes(Constants.RTCSessionParticipantFlag.CPU_LOAD)) {
          overloadLevel = 2;
        } else if (participant.flags.includes(Constants.RTCSessionParticipantFlag.CPU_LOAD_TRANSCODE)) {
          overloadLevel = 1;
        }
      }
      if (overloadLevel > (call.vrsOverloadLevel || 0)) {
        call.vrsOverloadLevel = overloadLevel;
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/vrsOverloadLevelChanged event. userId =', participant.userId + ', level = ' + overloadLevel);
        PubSubSvc.publish('/call/vrsOverloadLevelChanged', [call, participant]);
      }
    }

    function addParticipantToCallObj(call, participant, state, update) {
      var added = call.addParticipant(participant, state, update);
      if (added) {
        verifyStreamIds(call, added);
        setParticipantActions(call, added);
        checkSessionParticipantFlags(call, added);

        // Check if we already have the user data
        if (participant.noData && !participant.reqPending) {
          // Get the user data and add to cache
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Get user data for added participant. userId: ', participant.userId);
          UserSvc.getUserById(participant.userId, function (err, user) {
            if (!user) {
              return;
            }

            // We successfully retrieved the user data. The participant object is automatically updated
            // since it has the user object as its prototype. However, the firstName and lastName fields
            // need to be manually updated because they are also set directly on the participant object.
            // See RtcParticipant.createFromUser() for details.
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Successfully retrieved participant data. userId: ', participant.userId);

            // Check if the call is still active and the participant is still a member
            if (call.isPresent() && call.hasParticipant(participant.userId)) {
              // Raise a /call/participant/updated event for mobile clients)
              participant.firstName = user.firstName;
              participant.lastName = user.lastName;

              LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/updated event. userId =', participant.userId);
              PubSubSvc.publish('/call/participant/updated', [call.callId, participant]);
            }
          });
        }
      }
      return added;
    }

    function updateParticipantInCallObj(call, participant, state) {
      var userId = participant.userId;
      var currentData = call.getParticipant(userId);
      if (currentData && currentData.isMeetingGuest) {
        LogSvc.debug('[ConversationSvc]: Publish /call/guestBecameConversationParticipant event for userId = ', userId);
        PubSubSvc.publish('/call/guestBecameConversationParticipant', [call.callId, userId]);
      }

      var updated = call.updateParticipant(participant, state);
      if (updated) {
        verifyStreamIds(call, updated);
        setParticipantActions(call, updated);
        checkSessionParticipantFlags(call, updated);
      }
      return updated;
    }

    function removeCallParticipant(call, userId, cause) {
      var state = Enums.ParticipantState.Terminated;
      switch (cause) {
        case Constants.RTCSessionParticipantLeftCause.CONNECTION_LOST:
        case Constants.RTCSessionParticipantLeftCause.STREAM_LOST:
          state = Enums.ParticipantState.ConnectionLost;
          break;
        case Constants.RTCSessionParticipantLeftCause.REMOVED:
          state = Enums.ParticipantState.Removed;
          break;
        case Constants.RTCSessionParticipantLeftCause.USER_LEFT_STAGE:
          state = Enums.ParticipantState.OffStage;
          break;
      }
      call.setParticipantState(userId, state);

      var removedParticipant = call.removeParticipant(userId);

      if (!call.hasOtherParticipants()) {
        // everybody left but the localUser; return to the waiting state
        call.setState(Enums.CallState.Waiting);
      }

      if (removedParticipant) {
        // Update the call's media type
        call.updateMediaType();

        if (call.conferenceCall) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /conference/participant/left event');
          PubSubSvc.publish('/conference/participant/left', [call, cause]);
        }
        var leftDataWrapper = cause ? { leftCause: cause } : null;
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/removed event');
        PubSubSvc.publish('/call/participant/removed', [call.callId, removedParticipant, leftDataWrapper]);
      }
      publishCallState(call);
    }

    function setRecordingInfoData(localCall, data) {
      if (!localCall || !data || !data.state || data.state === Constants.RecordingInfoState.INITIAL) {
        return;
      }
      if (data.recordingMediaTypes && data.recordingMediaTypes.includes(Constants.RecordingMediaType.TEXT)) {
        // At moment only set state
        localCall.transcription.state = data.state;

        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/transcription/info event');
        PubSubSvc.publish('/call/transcription/info', [localCall]);
      }
      if (
        !data.recordingMediaTypes ||
        data.recordingMediaTypes.includes(Constants.RealtimeMediaType.AUDIO) ||
        data.recordingMediaTypes.includes(Constants.RealtimeMediaType.VIDEO)
      ) {
        setAudioVideoRecordingData(localCall.recording, data);

        if (localCall.recording.recordingUserId) {
          localCall.recording.recordingUser = localCall.getParticipant(localCall.recording.recordingUserId);

          if (!localCall.recording.recordingUser) {
            UserSvc.getUserById(localCall.recording.recordingUserId, function (err, user) {
              if (!user || err) {
                LogSvc.error('[UnifyPhoneCallControlSvc]: Cannot retrieve recording user data.', err || '');
                return;
              }

              localCall.recording.recordingUser = user;
              LogSvc.debug('[UnifyPhoneCallControlSvc]: User info retrieved. Publish /call/recording/info event');
              PubSubSvc.publish('/call/recording/info', [localCall]);
            });
            return;
          }
        }
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/recording/info event');
        PubSubSvc.publish('/call/recording/info', [localCall]);
      }
    }

    function setAudioVideoRecordingData(recording, data) {
      recording.recordingMediaTypes = data.recordingMediaTypes;

      if (recording.state !== data.state) {
        recording.notifyByCurtain =
          data.state === Constants.RecordingInfoState.START_PENDING ||
          (data.state === Constants.RecordingInfoState.STARTED && recording.state === Constants.RecordingInfoState.START_PENDING);
        recording.notifyByUser =
          !recording.notifyByCurtain || (data.state === Constants.RecordingInfoState.START_PENDING && recording.state !== Constants.RecordingInfoState.STARTED);
      } else {
        recording.notifyByCurtain = false;
        recording.notifyByUser = false;
      }

      recording.state = data.state;
      recording.duration = data.duration || recording.duration;
      recording.reason = data.reason;
      recording.recordingUserId = null;
      recording.recordingUser = null;
      if (data.state === Constants.RecordingInfoState.STARTED) {
        recording.resumeTime = Date.now();

        if (
          data.layout &&
          data.layout.layoutMapping &&
          data.layout.layoutMapping.length &&
          data.layout.layoutName === Constants.RecordingVideoLayoutName.SINGLE_VIDEO
        ) {
          recording.recordingUserId = data.layout.layoutMapping[0].tileContent;
        }
      }

      if (data.starter && data.starter.userId && data.starter.userId !== recording.starter.userId) {
        recording.starter = data.starter;
        if (recording.starter.userId === $rootScope.localUser.userId) {
          recording.starter.res = 'res_StartedRecording_You';
        } else {
          recording.starter.name = recording.starter.displayName;
          recording.starter.res = recording.starter.name ? 'res_StartedRecording_Other' : 'res_StartedRecording';
        }
      }
    }

    function updateAttendeeCount(attendeeCount) {
      if (_primaryLocalCall && attendeeCount !== undefined && _primaryLocalCall.attendeeCount !== attendeeCount) {
        _primaryLocalCall.attendeeCount = attendeeCount;
        _primaryLocalCall.updateCallState();

        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/attendeeCountChanged');
        PubSubSvc.publish('/call/attendeeCountChanged', [_primaryLocalCall.callId, _primaryLocalCall.attendeeCount]);
      }
    }

    function testCandidatesCollection(turnCredentials) {
      return new $q(function (resolve, reject) {
        var sessionCtrl = new RtcSessionController({ disableTrickleIce: true, isDirectCall: true, candidatesCollectionTest: true });

        var terminate = function () {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Terminate ICE candidates collection test');
          sessionCtrl.onSessionDescription = null;
          sessionCtrl.onRtcError = null;
          sessionCtrl.terminate();
        };

        if (turnCredentials) {
          sessionCtrl.setTurnUris(turnCredentials.turnServer);
          sessionCtrl.setTurnCredentials(turnCredentials);
        }
        sessionCtrl.disableRemoteVideo();
        sessionCtrl.onSessionDescription = function (event) {
          var candidates = unifyPhone.sdpParser.getIceCandidates(event.sdp.sdp);
          LogSvc.debug('[UnifyPhoneCallControlSvc]: testIceCandidates - candidates: ', candidates);
          terminate();
          resolve({
            turnServers: (turnCredentials && turnCredentials.turnServer) || [],
            candidates: candidates
          });
        };
        sessionCtrl.onRtcError = function (event) {
          LogSvc.error('[UnifyPhoneCallControlSvc]: Error collecting candidates. ', event.error);
          terminate();
          reject(event.error);
        };

        LogSvc.debug('[UnifyPhoneCallControlSvc]: testIceCandidates - Start collection');
        sessionCtrl.start({ audio: false }, null);
      });
    }

    function processReplacedCall(localCall, terminate) {
      if (!localCall || !localCall.replaces) {
        return;
      }
      var replaceCall = findCall(localCall.replaces.callId);
      if (replaceCall) {
        if (terminate) {
          leaveCall(replaceCall, null, Enums.CallClientTerminatedReason.CALL_MOVED_TO_ANOTHER_CONV);
        }
      }
      // Inform the UI that call has been moved
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/moved event');
      PubSubSvc.publish('/call/moved', [localCall.replaces.callId, localCall.callId]);

      // The /call/state event must be after /call/moved event (required by iOS)
      localCall.replaces = null;
      publishCallState(localCall);
    }

    function isVideoAndScreenShareEnabled(call) {
      // The feature should work if the one is supported by backend AND:
      // 1. it is a UnifyPhone user AND:
      //    a. it is not a direct call
      //    b. it is not a Mobile client
      return !(call && call.isDirect) && !Utils.isMobile();
    }

    function isMidMappingEnabled() {
      return true;
    }

    function isValidVideoResolution(resolution) {
      if (!HD_VIDEO_RESOLUTIONS.includes(resolution)) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Invalid video resolution: ', resolution);
        return false;
      }
      return true;
    }

    function publishShowRatingDialog(evt) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/showRatingDialog event');
      PubSubSvc.publish('/call/showRatingDialog', [evt]);
    }

    function handleRtcError(call, event) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: handleRtcError: ', event.error);
      PubSubSvc.publish('/call/rtcError', [call, event.error]);
      if (call.checkState([Enums.CallState.Ringing, Enums.CallState.Answering])) {
        var rejectCause = Constants.InviteRejectCause.BUSY;
        decline(call, { type: rejectCause });
        return;
      }

      if (call.sameAs(_primaryLocalCall)) {
        if (event.error === 'res_CallMediaFailed') {
          call.setDisconnectCause(Constants.DisconnectCause.NEGOTIATION_FAILED);
          _that.endCallWithCauseCode(call.callId, Enums.CallClientTerminatedReason.ICE_TIMED_OUT);
        } else if (event.error === 'res_CallMediaDisconnected') {
          call.setDisconnectCause(Constants.DisconnectCause.STREAM_LOST);
          _that.endCallWithCauseCode(call.callId, Enums.CallClientTerminatedReason.LOST_MEDIA_STREAM);
          // Since media connection has been lost, ping Access Server to check if websocket connection is still up
          _clientApiHandler.pingServer();
        } else if (!call.isEstablished()) {
          _that.endCall(call.callId);
        }
      }
    }

    /////////////////////////////////////////////////////////////////////////////
    // RtcSessionController event handlers
    /////////////////////////////////////////////////////////////////////////////
    function registerSessionController(call) {
      var sessionCtrl = call && call.sessionCtrl;
      if (!sessionCtrl) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Failed to get RtcSessionController for given call. ', call);
        return;
      }

      sessionCtrl.onIceCandidate = onIceCandidate.bind(null, call);
      // sessionCtrl.onSessionDescription is registered on-demand
      // sessionCtrl.sessionClosed is registered on demand
      sessionCtrl.onSdpConnected = onSdpConnected.bind(null, call);
      sessionCtrl.onIceConnected = onIceConnected.bind(null, call);
      sessionCtrl.onIceDisconnected = onIceDisconnected.bind(null, call);
      sessionCtrl.onRemoteVideoAdded = onRemoteVideoAdded.bind(null, call);
      sessionCtrl.onRemoteVideoRemoved = onRemoteVideoRemoved.bind(null, call);
      sessionCtrl.onRemoteStreamUpdated = onRemoteStreamUpdated.bind(null, call);
      sessionCtrl.onDTMFToneChange = onDTMFToneChange.bind(null, call);
      sessionCtrl.onRtcError = onRtcError.bind(null, call);
      sessionCtrl.onRtcWarning = onRtcWarning.bind(null, call);
      sessionCtrl.onLocalStreamEnded = onLocalStreamEnded.bind(null, call);
      sessionCtrl.onQosAvailable = onQosAvailable.bind(null, call);
      sessionCtrl.onStatsThresholdExceeded = onStatsThresholdExceeded.bind(null, call);
      sessionCtrl.onStatsNoOutgoingPackets = onStatsNoOutgoingPackets.bind(null, call);
      sessionCtrl.onNetworkQuality = onNetworkQuality.bind(null, call);
      sessionCtrl.onGetUserMediaException = onGetUserMediaException.bind(null, call);
    }

    function unregisterSessionController(call) {
      var sessionCtrl = call && call.sessionCtrl;
      if (sessionCtrl) {
        sessionCtrl.onIceCandidate = null;
        sessionCtrl.onSessionDescription = null;
        sessionCtrl.sessionClosed = null;
        sessionCtrl.onSdpConnected = null;
        sessionCtrl.onIceConnected = null;
        sessionCtrl.onIceDisconnected = null;
        sessionCtrl.onRemoteVideoAdded = null;
        sessionCtrl.onRemoteVideoRemoved = null;
        sessionCtrl.onRemoteStreamUpdated = null;
        sessionCtrl.onDTMFToneChange = null;
        sessionCtrl.onRtcError = null;
        sessionCtrl.onRtcWarning = null;
        sessionCtrl.onLocalStreamEnded = null;
        // Do not unregister handler for onQoSAvailable. The session controller will
        // automatically remove the handler after the stats are collected.
        sessionCtrl.onStatsThresholdExceeded = null;
        sessionCtrl.onStatsNoOutgoingPackets = null;
        sessionCtrl.onNetworkQuality = null;
      }
    }

    function onIceCandidate(call, event) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onIceCandidate');
      var data = {
        rtcSessionId: call.callId,
        userId: $rootScope.localUser.userId,
        origin: event.origin,
        candidates: event.candidates.map(JSON.stringify.bind(JSON))
      };

      var iceInfo = DeviceDiagnosticSvc.createActionInfo(call, Constants.RtcDiagnosticsAction.ICE_CANDIDATES);
      if (iceInfo) {
        iceInfo.data = data.candidates;
        iceInfo.isEndOfCandidates = event.endOfCandidates;
      }

      _clientApiHandler.sendIceCandidates(data, function (err) {
        DeviceDiagnosticSvc.finishActionInfo(call, iceInfo);
        if (err) {
          LogSvc.warn('Failed to send ICE candidate: ', err);
        }
      });
    }

    function onSdpConnected(call, event) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onSdpConnected');
      $rootScope.$apply(function () {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/sdpConnected event');
        PubSubSvc.publish('/call/sdpConnected', [call]);

        var sdpConnectedInfo = DeviceDiagnosticSvc.createActionInfo(call, Constants.RtcDiagnosticsAction.SDP_CONNECTED);
        DeviceDiagnosticSvc.finishActionInfo(call, sdpConnectedInfo);

        if (call.isTelephonyCall) {
          // For telephony calls the backend won't send events when SDP is connected.
          // So set the call state to waiting here.
          if (!call.state.established) {
            call.setState(Enums.CallState.Waiting);
          }
          call.updateCallState();
        } else if (call.isDirect) {
          call.setState(Enums.CallState.Active);
        }
        call.sdpOrigin = event.sdpOrigin;
        publishCallState(call);
        DeviceDiagnosticSvc.finishDeviceDiagnostics(call);
        if (call.instanceId) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: InstanceId is available. Send call connected diagnostics data.');
          DeviceDiagnosticSvc.sendCallConnected(call);
        } else {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: InstanceId is not available. Postpone sending call connected diagnostics data.');
          call.callConnectedPostponed = true;
        }
      });
    }

    function onIceConnected(call, event) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onIceConnected. pcType:', event.pcType);
      $rootScope.$apply(function () {
        if (event.pcType === 'audio/video') {
          if (call.isEstablished() && call.disconnectCause && call.disconnectCause.cause === Constants.DisconnectCause.STREAM_LOST) {
            // Call reconnected
            call.disconnectCause = null;
          }
          publishIceConnectionState(call, 'connected');
        }
      });
    }

    function onIceDisconnected(call, event) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onIceDisconnected. pcType:', event.pcType);
      $rootScope.$apply(function () {
        if (event.pcType === 'audio/video' && call.isEstablished() && !call.isHeld()) {
          call.setDisconnectCause(Constants.DisconnectCause.STREAM_LOST);
          publishIceConnectionState(call, 'disconnected');
        }
      });
    }

    function onRemoteVideoAdded(/*call*/) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onRemoteVideoAdded');
    }

    function onRemoteVideoRemoved(/*call*/) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onRemoteVideoRemoved');
    }

    function onRemoteStreamUpdated(call) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onRemoteStreamUpdated');
      $rootScope.$apply(function () {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/remoteStreamUpdated event');
        PubSubSvc.publish('/call/remoteStreamUpdated', [call]);
      });
    }

    function onDTMFToneChange(call, event) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onDTMFToneChange');

      $rootScope.$apply(function () {
        if (event.tone) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Sent DTMF Digit');
          PubSubSvc.publish('/call/singleDigitSent', [call, event.tone]);
        } else if (event.tone === '') {
          LogSvc.info('[UnifyPhoneCallControlSvc]: Digits played completely');
          PubSubSvc.publish('/call/digitsSent', [call]);
        }
      });
    }

    function onRtcError(call, event) {
      $rootScope.$apply(function () {
        handleRtcError(call, event);
      });
    }

    function onRtcWarning(call, event) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onRtcWarning');
      $rootScope.$apply(function () {
        PubSubSvc.publish('/call/rtcWarning', [call, event.warning]);
      });
    }

    function onLocalStreamEnded(call) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onStreamEnded');
      if (_streamEndedTimeout) {
        // We're already processing an onLocalStreamEnded, so ignore this one
        return;
      }
      _streamEndedTimeout = $timeout(
        function () {
          _streamEndedTimeout = null;
          // Trigger a media renegotiation so a new device can be picked up
          _that.renegotiateMedia(call.callId, function (err) {
            if (err) {
              $rootScope.$apply(function () {
                LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/localStreamEnded event');
                PubSubSvc.publish('/call/localStreamEnded', [call]);
              });
            }
          });
        },
        STREAM_ENDED_DELAY,
        false
      );
    }

    function onQosAvailable(call, event) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onQosAvailable');
      if (event.renegotiationInProgress) {
        call.terminateReason = Enums.CallClientTerminatedReason.MEDIA_RENEGOTIATION;
      }
      if (call.sessionCtrl) {
        if (call.qosSubmitted) {
          LogSvc.info('[UnifyPhoneCallControlSvc]: QoS already submitted for call Id:', call.callId);
          return;
        }
        InstrumentationSvc.sendQOSData(call, call.lastMediaType, event.qos, event.lastSavedStats, event.streamQualityData);
      }

      if (event.renegotiationInProgress) {
        call.terminateReason = null;
        // Update media devices only after the QoS is sent out (so it contains the correct devices)
        call.updateMediaDevices();
      }
    }

    function onStatsThresholdExceeded(call, event) {
      if (!_clientDiagnosticsDisabled) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onStatsThresholdExceeded. Cleared: ', !!(event && event.cleared));

        $rootScope.$apply(function () {
          if (event && event.cleared) {
            PubSubSvc.publish('/call/rtp/threshholdExceeded/cleared', [call.callId]);
          } else {
            PubSubSvc.publish('/call/rtp/threshholdExceeded/detected', [call.callId]);
          }
        });
      }
    }

    function onStatsNoOutgoingPackets(call) {
      if (!call.isHeld()) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onStatsNoOutgoingPackets. Publish /call/rtp/noOutgoingPackets event');
        $rootScope.$apply(function () {
          PubSubSvc.publish('/call/rtp/noOutgoingPackets', [call.callId]);
        });
      }
    }

    function onNetworkQuality(call, event) {
      if (!event) {
        return;
      }
      $rootScope.$apply(function () {
        var eventData = null;
        if (event.quality) {
          eventData = event.quality.level;
        }
        call.networkQuality = eventData;
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/network/quality event for quality=', eventData);
        PubSubSvc.publish('/call/network/quality', [call.callId, eventData]);
      });
    }

    function onGetUserMediaException(call, event) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: RtcSessionController - onGetUserMediaException. Publish /call/getUserMediaException event');
      $rootScope.$apply(function () {
        PubSubSvc.publish('/call/getUserMediaException', [call, event.info]);
      });
    }

    function onActiveSpeakerEvent(evt) {
      try {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.ACTIVE_SPEAKER');

        var localCall = findLocalCallByCallId(evt.sessionId);
        if (!localCall) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Unexpected event. There is no local call');
          return;
        }

        if (localCall.isDirect || localCall.participants.length < 2) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Ignore RTC.ACTIVE_SPEAKER event for 1-2-1 calls');
          return;
        }

        var activeSpeakers = [];
        evt.first && activeSpeakers.push(evt.first);
        evt.second && activeSpeakers.push(evt.second);
        evt.third && activeSpeakers.push(evt.third);
        var hasChanged = localCall.setActiveSpeakers(activeSpeakers);

        if (!hasChanged) {
          // No changes
          return;
        }

        // The active speakers have changed. Raise an event to notify the UI
        // NOTE: Do not invoke $rootScope.$apply for this event. We will invoke a $scope.$digest in
        // uCallStage if the call stage is visible.
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participants/activeSpeakers event. ', activeSpeakers);
        PubSubSvc.publish('/call/participants/activeSpeakers', [localCall.callId, activeSpeakers]);
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTC.ACTIVE_SPEAKER event. ', e);
      }
    }

    function onActiveVideoSpeakerEvent(evt) {
      try {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.VIDEO_ACTIVE_SPEAKER');

        var localCall = findLocalCallByCallId(evt.sessionId);
        if (!localCall) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Unexpected event. There is no local call');
          return;
        }

        var participant = localCall.getParticipant(evt.userId);
        if (!participant) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Participant info not available. Ignoring VIDEO_ACTIVE_SPEAKER. userId=', evt.userId);
          return;
        }

        if (participant.streamId === evt.videoStreamId) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Participant is already assigned to the received videoStreamId. Ignoring VIDEO_ACTIVE_SPEAKER');
          return;
        }

        $rootScope.$apply(function () {
          participant.streamId = evt.videoStreamId;
          verifyStreamIds(localCall, participant);
          localCall.setParticipantRemoteVideoStream(participant);
          localCall.checkForActiveRemoteVideo();

          LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/updated event. userId =', participant.userId);
          PubSubSvc.publish('/call/participant/updated', [localCall.callId, participant]);
        });
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCSession.VIDEO_ACTIVE_SPEAKER event.', e);
      }
    }

    function processInitActiveSession(session) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Processing active session for convId = ', session.convId);

      if (session.isOriginated) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Originated session found...');
        // Session.initialInvite should be populated from connector if call is still RINGING or ORIGINATED (active, not established, not yet ringing)
        if (session.initialInvite && session.initialInvite.invite) {
          handleInviteEvent(session.initialInvite.invite);
        }
        return;
      }
      if (!session.participants || session.participants.length === 0) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: The active session does not have participants. rtcSessionId = ', session.rtcSessionId);
        return;
      }

      var conversation = ConversationSvc.getConversationByRtcSession(session.rtcSessionId);
      if (!conversation || (!conversation.hasJoined && !conversation.isTemporary)) {
        return;
      }

      if (session.turnServers) {
        putSessionTurn(session.rtcSessionId, session.turnServers);
      }

      if (getIncomingCall(session.rtcSessionId)) {
        // Session correspond to an already ringing incoming call
        return;
      }

      var activeClient;
      var lostSession = false;
      var joinedCall = session.participants.some(function (p) {
        if (p.userId === $rootScope.localUser.userId) {
          activeClient = p;
          if (p.clientId === _clientApiHandler.clientId) {
            // Session for current client without local call
            lostSession = true;
          }
          return true;
        }
        return false;
      });

      if (!joinedCall) {
        // Session.initialInvite should be populated from connector if call is still RINGING or ORIGINATED
        if (session.initialInvite && session.initialInvite.invite) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Session: ' + session.rtcSessionId + ' contains pending invite, handle it.');
          handleInviteEvent(session.initialInvite.invite);
          return;
        }
      }

      if (conversation.call) {
        var remoteCall = conversation.call;
        if (remoteCall.isRemote && conversation.isTemporary && activeClient) {
          // Guest conversations might contain a placeholder remote call, so just update it
          conversation.call.setState(Enums.CallState.ActiveRemote);
          remoteCall.setActiveClient(activeClient);
          addActiveRemoteCall(remoteCall);
          remoteCall.mediaType = Proto.getMediaType(session.mediaTypes);
          return;
        }
        // Somehow we ended up with a race condition
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Conversation already has an associated call');
        return;
      }

      if (conversation.type === Constants.ConversationType.DIRECT && !session.hosted && !activeClient) {
        return;
      }

      if (lostSession) {
        var terminateCb = function (e) {
          if (e) {
            LogSvc.warn('[UnifyPhoneCallControlSvc] Lost session could not be terminated. Session Id=', session.rtcSessionId);
          }
        };
        var disconnectCause = {
          disconnectCause: Constants.DisconnectCause.CONNECTION_LOST,
          disconnectReason: Constants.DisconnectReason.CALL_LOST
        };
        // Terminate lost session
        if (conversation.type === Constants.ConversationType.DIRECT) {
          _clientApiHandler.terminateRtcCall(session.rtcSessionId, disconnectCause, terminateCb);
          return;
        } else {
          _clientApiHandler.leaveRtcCall(session.rtcSessionId, disconnectCause, terminateCb);
        }
      }

      // If remote call is active on another client
      addRemoteCall(conversation, activeClient, session, session.rtcSessionId, function (call) {
        if (activeClient) {
          setPeerUserForTelephonyCall(call, session);
        }
      });
    }

    function setPeerUserForTelephonyCall(remoteCall, session) {
      if (remoteCall && remoteCall.isTelephonyCall && session && session.participants && session.participants.length <= 2) {
        session.participants.some(function (p) {
          if (p.participantType === Constants.RTCParticipantType.TELEPHONY) {
            setCallPeerUser(remoteCall, p.phoneNumber, null, p.userDisplayName);
            return true;
          }
          return false;
        });
      }
    }

    function hasLocalCall(session) {
      var foundLocalCall = false;
      var localCall = findLocalCallByCallId(session.rtcSessionId);
      if (localCall && session.participants) {
        foundLocalCall = session.participants.some(function (p) {
          return p.userId === $rootScope.localUser.userId && p.clientId === _clientApiHandler.clientId;
        });
      }
      return foundLocalCall;
    }

    function processConvPromises(convPromises, resolve, reject) {
      LogSvc.info('[UnifyPhoneCallControlSvc]: Wait for get conversation promises to be resolved.');
      $q.all(convPromises)
        .then(function (convs) {
          var convIDs = convs.map(function (c) {
            return c.convId;
          });
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Retrieved the missing conversation(s): ', convIDs);
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Now that we have all the conversations, check if they still have active RTC sessions.');
          _clientApiHandler.getActiveSessions(function (err, activeSessions) {
            $rootScope.$apply(function () {
              if (err) {
                LogSvc.error('[UnifyPhoneCallControlSvc]: Error getting active sessions: ', err);
                activeSessions = [];
              }
              activeSessions.forEach(function (s) {
                if (convIDs.indexOf(s.convId) === -1) {
                  return;
                }
                // If session is still in originated state, we need to check for pending invite
                if (s.isOriginated || !hasLocalCall(s)) {
                  processInitActiveSession(s);
                }
              });
              resolve();
            });
          });
        })
        .catch(function (err) {
          LogSvc.error('[UnifyPhoneCallControlSvc]: Failed to retrieve the conversation(s). ', err);
          reject(err);
        });
    }

    function initActiveSessions() {
      // Inject hasActiveCall API into ConnectionController
      _clientApiHandler.injectHasActiveCall(_that.hasActiveCall);

      return new $q(function (resolve, reject) {
        if (!_conversationLoaded) {
          reject('Not ready to initialize active sessions');
          return;
        }
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Initialize active RTC sessions');

        _clientApiHandler.getActiveSessions(function (err, activeSessions) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.error('[UnifyPhoneCallControlSvc]: Error getting active sessions: ', err);
            }
            activeSessions = activeSessions || [];

            Utils.emptyArray(_activeRemoteCalls);
            // Terminate any existing remote calls except temporary guests.
            // They will be recreated if they are still there.
            for (var i = _calls.length - 1; i >= 0; i--) {
              // Reverse since elements will be deleted
              if (_calls[i].isRemote) {
                _calls[i].guestToken ? _activeRemoteCalls.push(_calls[i]) : terminateCall(_calls[i]);
              }
            }

            var conversation = null;
            var foundLocalPrimaryCall = false;
            var foundLocalSecondaryCall = false;
            var convPromises = [];
            activeSessions.forEach(function (s) {
              // If session is still in originated state, we need to check for pending invite and not process it
              // as an normal active session
              if (!s.isOriginated) {
                var localCall = findLocalCallByCallId(s.rtcSessionId);
                if (localCall) {
                  if (hasLocalCall(s)) {
                    foundLocalPrimaryCall = foundLocalPrimaryCall || localCall === _primaryLocalCall;
                    foundLocalSecondaryCall = foundLocalSecondaryCall || localCall === _secondaryLocalCall;
                    LogSvc.info('[UnifyPhoneCallControlSvc]: Found localCall in active sessions. Trigger media renegotiation.');

                    // Trigger a media renegotiation to refresh the ICE candidates
                    _that.renegotiateMedia(localCall.callId, function () {
                      PubSubSvc.publish('/call/sessionInitialized', [localCall]);
                    });
                    return;
                  }

                  // If it gets to this point is because there is an active session for this user but the
                  // client Id does not match the one of the local user (recent reconnection). Local call
                  // shall be cleaned up so the remote call is recreated.
                  terminateCall(localCall, Enums.CallClientTerminatedReason.LOST_WEBSOCKET_CONNECTION);
                }

                conversation = ConversationSvc.getConversationByRtcSession(s.rtcSessionId);
                if (!conversation || !conversation.hasJoined) {
                  if (!conversation) {
                    LogSvc.info('[UnifyPhoneCallControlSvc]: Conversation associated with session is not cached. Need to get it.');
                    convPromises.push(ConversationSvc.getConversationPromise(s.convId));
                  }
                  return;
                }
              }
              processInitActiveSession(s);
            });

            PubSubSvc.publish('/activeSessions/received');

            if (_secondaryLocalCall && !foundLocalSecondaryCall) {
              LogSvc.warn('[UnifyPhoneCallControlSvc]: Terminating secondaryLocalCall with id: ', _secondaryLocalCall.callId);
              terminateCall(_secondaryLocalCall, Enums.CallClientTerminatedReason.LOST_WEBSOCKET_CONNECTION);
            }
            if (_primaryLocalCall && !foundLocalPrimaryCall) {
              LogSvc.warn('[UnifyPhoneCallControlSvc]: Terminating primaryLocalCall with id: ', _primaryLocalCall.callId);
              terminateCall(_primaryLocalCall, Enums.CallClientTerminatedReason.LOST_WEBSOCKET_CONNECTION);
            }

            if (convPromises.length > 0) {
              processConvPromises(convPromises, resolve, reject);
            } else {
              resolve();
            }
          });
        });
      });
    }

    function updateCall(newConversation, oldConversation, call) {
      // Create a temporary call object with old rtc session id to raise call ended event to UI
      // The "replaced" flag is set to true in the ended message so that the
      // mobile client knows this is part of a call update.
      var oldCall;
      if (call.isRemote) {
        oldCall = new RemoteCall(oldConversation);
      } else {
        oldCall = new LocalCall(oldConversation, { clientId: call.clientId });
      }
      oldConversation.call = oldCall;

      // Terminate the call to clear its resources.
      oldCall.terminate();

      // End temporary call associated to old coversation
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/ended event');
      PubSubSvc.publish('/call/ended', [oldCall, true]);

      oldConversation.call = null;

      // Update old conversation
      publishConversationUpdate(oldConversation);

      call.updateCall(newConversation);

      newConversation.call = call;

      // Update new conversation
      publishConversationUpdate(newConversation);

      // Inform the UI that call has been moved
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/moved event');
      PubSubSvc.publish('/call/moved', [oldCall.callId, call.callId]);

      // The /call/state event must be after /call/moved event (required by iOS)
      publishCallState(call);
    }

    function sendChangeMediaReject(callId, transactionId) {
      var changeMediaRejectData = {
        rtcSessionId: callId,
        transactionId: transactionId
      };
      _clientApiHandler.changeMediaReject(changeMediaRejectData, function (err) {
        if (err) {
          LogSvc.error('[UnifyPhoneCallControlSvc]: changeMediaReject sending error');
        } else {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: changeMediaReject sent');
        }
      });
    }

    function presentIncomingCall(conversation, userId, doNotPublishEvent) {
      if (!doNotPublishEvent) {
        if (conversation.call.pickupNotification) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/pickupNotification event');
          PubSubSvc.publish('/call/pickupNotification', [conversation.call]);
        } else {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/incoming event');
          PubSubSvc.publish('/call/incoming', [conversation.call]);
        }
      }
      if (NotificationSvc) {
        var notificationType = conversation.call.mediaType.video ? Enums.NotificationType.INCOMING_VIDEO_CALL : Enums.NotificationType.INCOMING_VOICE_CALL;
        var notification = {
          type: notificationType,
          userId: userId,
          extras: {
            conversation: conversation,
            call: conversation.call
          }
        };
        if (conversation.isTelephony && conversation.call.peerUser) {
          // For telephony calls, the userId should be the one resolved by lookup is any.
          notification.userId = conversation.call.peerUser.userId;
          if (!notification.userId) {
            // If not resolved provide user object to satisfy notification service.
            notification.user = conversation.call.peerUser;
          }
          // Original calling phone number may be overwritten in the user object
          notification.extras.phoneNumber = conversation.call.peerUser.phoneNumber;
          notification.extras.displayName = conversation.call.peerUser.displayName;
        }
        NotificationSvc.show(notification);
      }

      conversation.call.startRingingTimer(function () {
        decline(conversation.call, { type: Constants.InviteRejectCause.TIMEOUT }, false);
      });
    }

    function setRedirectingUserFromTelephonyInfo(incomingCall, telephonyInfo) {
      if (telephonyInfo && telephonyInfo.redirectingUser) {
        var redirectingUser = telephonyInfo.redirectingUser;
        setRedirectingUser(
          incomingCall,
          redirectingUser.phoneNumber,
          redirectingUser.fullyQualifiedNumber,
          redirectingUser.displayName,
          RedirectionTypes.CallForward
        );
      }
    }

    function setRedirectingUserFromPickupNotification(existingCall, incomingCall) {
      if (existingCall && incomingCall) {
        var redirectingUser = existingCall.getRedirectingUser();
        if (!incomingCall.atcCallInfo) {
          incomingCall.atcCallInfo = new AtcCallInfo();
          incomingCall.setCstaState(Enums.CstaCallState.Active);
        }
        incomingCall.setRedirectingUser(
          redirectingUser.phoneNumber,
          redirectingUser.fullyQualifiedNumber,
          redirectingUser.displayName,
          redirectingUser.userId,
          RedirectionTypes.CallPickedUp
        );
        incomingCall.pickUpFromUser = redirectingUser;
      }
    }

    function processInviteWithLocalCall(evt, conversation, data) {
      if (!_primaryLocalCall) {
        return true;
      }
      if (_primaryLocalCall.callId === evt.replaces) {
        // Save the muted state now, because we'll lose this information if the replaced call
        // is terminated before we answer the new _primaryLocalCall
        data.userIsMuted = _primaryLocalCall.sessionCtrl && _primaryLocalCall.sessionCtrl.isMuted();
        data.replaces = _primaryLocalCall;
      } else if (_primaryLocalCall.terminateTimer) {
        // There's a soon-to-be-terminated local call
        if (_primaryLocalCall.callId === evt.sessionId) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Reject incoming call. Current call still not terminated');
          sendBusy(evt);
          return false;
        }
        // The new call is for a different conversation. Terminate the current call.
        terminateCall(_primaryLocalCall);
        // Keep handling the INVITE event
      } else {
        if (_primaryLocalCall.callId === evt.sessionId) {
          if (_primaryLocalCall.isDirect) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: INVITE for same local call. Reject it');
            sendBusy(evt);
          } else {
            LogSvc.warn('[UnifyPhoneCallControlSvc]: INVITE for same conversation as local call. Ignore it.');
          }
          return false;
        }
        if (
          !_primaryLocalCall.isEstablished() &&
          !(evt.pickUpSession && evt.pickUpSession === _primaryLocalCall.callId) &&
          !(conversation.isTelephony && !isAtcDefaultBusyHandlingSelected())
        ) {
          // Cannot accept the second incoming call - if the second incoming call is a telephony call and default
          // busy handling is not selected, the second call should be handled by CSTA
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Reject incoming call. Current call still not established');
          sendBusy(evt);
          return false;
        }
      }
      return true;
    }

    function checkTelephonyTimers(evt, data) {
      if (evt.to && evt.to.displayName && evt.to.displayName.startsWith('handover-from')) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received handover call');
        if (_handoverTimer) {
          data.autoAnswer = true;
          $timeout.cancel(_handoverTimer);
          _handoverTimer = null;
        } else {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: INVITE for handover call from another client. Ignore it.');
          data.replacesActiveRemote = true;
        }
      } else if (_pickupTimer) {
        data.autoAnswer = true;
        $timeout.cancel(_pickupTimer);
        _pickupTimer = null;
      } else if (evt.autoAnswer) {
        data.immediateAnswer = true;
      }
    }

    function processInvite(evt, conversation) {
      if (!evt || !conversation) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: processInvite called without evt or conversation');
        return;
      }

      var data = {
        autoAnswer: false,
        callPickedUp: false,
        replaces: null,
        replacesActiveRemote: false,
        userIsMuted: false
      };

      if (evt.replaces && getActiveRemoteCall(evt.replaces)) {
        data.replacesActiveRemote = true;
      } else if (!processInviteWithLocalCall(evt, conversation, data)) {
        return;
      }

      if (conversation.isTelephony) {
        checkTelephonyTimers(evt, data);
      } else if (_lastEndedCall && _lastEndedCall.callId === evt.replaces) {
        data.userIsMuted = _wasMuted;
        data.replaces = _lastEndedCall;
        _lastEndedCall = null;
      }

      var found = getIncomingCall(evt.sessionId);
      if (found) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: INVITE for existing ringing call. Ignore it.');
        return;
      }

      // Reject 2nd incoming call
      if (
        !data.replacesActiveRemote &&
        !evt.pickUpSession &&
        !data.replaces &&
        _incomingCalls.length >= 1 &&
        !data.autoAnswer &&
        !(conversation.isTelephony && !isAtcDefaultBusyHandlingSelected())
      ) {
        // if the second incoming call is a telephony call and default busy handling is not selected,
        // the second call should be handled by CSTA
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Reject second incoming call.');
        sendBusy(evt);
        return;
      }

      if (
        $rootScope.localUser.isTeamsCallBusyHandlingEnabled &&
        $rootScope.localUser.userPresenceState.state === Constants.PresenceState.BUSY_IN_AN_EXTERNAL_CALL &&
        !_agentHuntGroupEnabled
      ) {
        // if the second incoming call is a telephony call and default busy handling is not selected,
        // the second call should be handled by CSTA
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Reject incoming call, isTeamsCallBusyHandlingEnabled');
        sendBusy(evt);
        return;
      }

      if (!processIncomingTelephonyCall(evt, conversation, data)) {
        return;
      }

      var incomingCall = createIncomingCall(evt, conversation, data);
      handleIncomingCall(evt, conversation, incomingCall, data);
    }

    function processIncomingTelephonyCall(evt, conversation, data) {
      if (conversation.isTelephony && !data.autoAnswer && !data.replacesActiveRemote) {
        if ($rootScope.localUser.selectedRoutingOption === RoutingOptions.DeskPhone.name && $rootScope.localUser.isATC) {
          publishAtcCall(conversation, evt, true);
          return false;
        }
        if (
          !$rootScope.localUser.selectedBusyHandlingOption ||
          $rootScope.localUser.selectedBusyHandlingOption === BusyHandlingOptions.DefaultRouting.name ||
          $rootScope.localUser.isSTC
        ) {
          if (_primaryLocalCall && _primaryLocalCall.isPresent() && _secondaryLocalCall && _secondaryLocalCall.isPresent()) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Reject incoming call. We cannot handle more than 2 local phone calls');
            sendBusy(evt); // We can't handle more than 2 local phone calls, reject it
            return false;
          }
        } else if (
          (_primaryLocalCall && _primaryLocalCall.isTelephonyCall) ||
          _incomingCalls.length >= 1 ||
          !Utils.isEmptyArray(_activeRemoteCalls) ||
          (conversation.call && conversation.call.isAtcRemote && !conversation.call.pickupNotification)
        ) {
          publishAtcCall(conversation, evt, false);
          return false;
        }
      }
      return true;
    }

    function createIncomingCall(evt, conversation, data) {
      var options = {
        // For OSBiz, only one media negotation is done for pull.
        isAtcPullCall: conversation.isTelephony && data.autoAnswer && evt.sdp.type === 'offer' && !_callToPickup && !$rootScope.localUser.isOsBizCTIEnabled,
        clientId: _clientApiHandler.clientId,
        midMappingEnabled: isMidMappingEnabled(),
        isSTC: $rootScope.localUser.isSTC && conversation.isTelephony,
        stcCapabilities: conversation.isTelephony ? $rootScope.localUser.stcCapabilities : [],
        canReceiveHdVideo: $rootScope.localUser.canUseHDVideo || $rootScope.localUser.canUseHDScreenShare
      };
      if (data.replaces && data.replaces.localMediaType.desktop) {
        // This call should reuse the desktop stream from the call it's replacing (option used by VDI)
        options.reuseDesktopStreamFrom = data.replaces.sessionCtrl;
      }
      var incomingCall = new LocalCall(conversation, options);
      incomingCall.setCallIdForTelephony(evt.sessionId);
      incomingCall.setInstanceId(evt.instanceId);
      incomingCall.setTransactionId(evt.transactionId);

      if (_primaryLocalCall && evt.pickUpSession === _primaryLocalCall.callId) {
        data.replaces = _primaryLocalCall;
        data.replaces.pickUpUserId = evt.userId;
        incomingCall.pickUpFromUser = data.replaces.peerUser;
      }

      if (data.replaces) {
        incomingCall.replaces = data.replaces;

        var oldStream = data.replaces.sessionCtrl.getLocalStream(RtcSessionController.LOCAL_STREAMS.DESKTOP);
        if (oldStream) {
          LogSvc.debug('[UnifyPhoneCallControlSvc] Reusing desktop stream from call ID=', data.replaces.callId);
          incomingCall.sessionCtrl.setLocalStream(RtcSessionController.LOCAL_STREAMS.DESKTOP, oldStream);
          // Set old desktop stream to null so it won't be stopped by the old RtcSessionController
          data.replaces.sessionCtrl.setLocalStream(RtcSessionController.LOCAL_STREAMS.DESKTOP, null);
        }
        oldStream = data.replaces.sessionCtrl.getLocalStream(RtcSessionController.LOCAL_STREAMS.AUDIO_VIDEO);
        if (oldStream) {
          LogSvc.debug('[UnifyPhoneCallControlSvc] Reusing audio/video stream from call ID=', data.replaces.callId);
          incomingCall.sessionCtrl.setLocalStream(RtcSessionController.LOCAL_STREAMS.AUDIO_VIDEO, oldStream);
          // Set old desktop stream to null so it won't be stopped by the old RtcSessionController
          data.replaces.sessionCtrl.setLocalStream(RtcSessionController.LOCAL_STREAMS.AUDIO_VIDEO, null);
        }
      }
      if (_disableRemoteVideoByDefault) {
        LogSvc.info('[UnifyPhoneCallControlSvc] Disabling remote video for incoming call');
        incomingCall.disableRemoteVideo();
      }
      incomingCall.setState(Enums.CallState.Ringing);
      incomingCall.direction = Enums.CallDirection.INCOMING;
      if ($rootScope.localUser.isATC && incomingCall.isTelephonyCall) {
        incomingCall.atcCallInfo = new AtcCallInfo();

        if (evt.atcAdvancing) {
          LogSvc.debug('[UnifyPhoneCallControlSvc] This ATC call has already advanced to the desk phone');
          incomingCall.atcAdvancing = true;
          incomingCall.sessionCtrl.setIgnoreNextConnection(true);
        }

        if (_callToPickup) {
          incomingCall.atcCallInfo.setCstaConnection(_callToPickup.atcCallInfo.getCstaConnection());

          var peerUser = _callToPickup.peerUser;
          incomingCall.setPeerUser(peerUser.phoneNumber, peerUser.displayName, peerUser.userId, null, peerUser);
          var redirectingUser = _callToPickup.getRedirectingUser();
          incomingCall.setRedirectingUser(
            redirectingUser.phoneNumber,
            redirectingUser.fullyQualifiedNumber,
            redirectingUser.displayName,
            redirectingUser.userId,
            RedirectionTypes.CallPickedUp
          );

          _callToPickup = null;
          data.callPickedUp = true;
        }
      }
      return incomingCall;
    }

    function handleIncomingCall(evt, conversation, incomingCall, data) {
      // MediaType should contain 'AUDIO' by default
      var evtMediaType = evt.mediaType || ['AUDIO'];
      addParticipantToCallObj(incomingCall, normalizeApiParticipant({ userId: evt.userId }, evtMediaType), Enums.ParticipantState.Calling);

      incomingCall.ringingTimeout = evt.ringingTimeout || incomingCall.ringingTimeout;

      var mediaType = Proto.getMediaType(evtMediaType);
      incomingCall.mediaType = mediaType;

      if (mediaType.desktop) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: INVITE for a Screen Share');
      }
      if (data.replacesActiveRemote) {
        // Do not show this call until answered
        addCallToList(incomingCall, true);
        return;
      }
      addCallToList(incomingCall);

      var onSessionWarmup = function () {
        $rootScope.$apply(function () {
          // The warmup was successful, so we can continue processing the call
          if (incomingCall.state === Enums.CallState.Terminated) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: New alerting call has been terminated already');
            return;
          }

          registerSessionController(incomingCall);
          LogSvc.debug('[UnifyPhoneCallControlSvc]: New alerting call: ', incomingCall.callId);

          if (conversation.call && !conversation.isTelephony) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Terminate the remote call and add the new local call to the conversation');
            terminateCall(conversation.call);
          }

          var oldCallId = null;
          if (conversation.call && conversation.call.isAtcRemote) {
            oldCallId = conversation.call.callId;
            if (conversation.call.pickupNotification && data.autoAnswer) {
              setRedirectingUserFromPickupNotification(conversation.call, incomingCall);
            }
          }

          conversation.call = incomingCall;
          if (conversation.isTelephony && data.autoAnswer && !data.callPickedUp) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /atccall/replace event');
            //In case /csta/handover event has arrived _callIdToBeReplaced has value, so use this id instead of oldCallId
            oldCallId = _callIdToBeReplaced ? _callIdToBeReplaced : oldCallId;
            _callIdToBeReplaced = null;
            PubSubSvc.publish('/atccall/replace', [incomingCall, oldCallId]);
          } else {
            // Show new incoming call
            publishConversationUpdate(conversation);
          }

          if (data.replaces) {
            _that.answerCall(incomingCall.callId, data.replaces.localMediaType, function (err) {
              if (!err) {
                if (data.userIsMuted) {
                  _primaryLocalCall.sessionCtrl && _primaryLocalCall.sessionCtrl.mute();
                }
              }
            });
            return;
          }

          if (data.autoAnswer || data.immediateAnswer) {
            if (incomingCall.isTelephonyCall && evt.from && !data.callPickedUp) {
              setCallPeerUser(incomingCall, evt.from.phoneNumber, evt.from.fullyQualifiedNumber, evt.from.displayName, null, function () {
                setRedirectingUserFromTelephonyInfo(incomingCall, evt.telephonyInfo);
              });
            }
            _that.answerCall(incomingCall.callId, mediaType);
            return;
          }

          // Notify the server that we're alerting
          var progressData = {
            rtcSessionId: incomingCall.callId,
            invitingUserId: evt.userId,
            localUserId: $rootScope.localUser.userId
          };

          _clientApiHandler.sendProgress(progressData, function (err) {
            if (err) {
              LogSvc.error('[UnifyPhoneCallControlSvc]: sendProgress error - ', err);
            }
          });

          if (incomingCall.isTelephonyCall && evt.from && !incomingCall.transferredOnRinging) {
            setCallPeerUser(incomingCall, evt.from.phoneNumber, evt.from.fullyQualifiedNumber, evt.from.displayName, null, function () {
              if (conversation.call === incomingCall) {
                presentIncomingCall(conversation, evt.userId);
              }
              setRedirectingUserFromTelephonyInfo(incomingCall, evt.telephonyInfo);
            });
            return;
          }

          presentIncomingCall(conversation, evt.userId);
        });
      };

      var onWarmupFailed = function (err) {
        // Ignore the incoming call rather than rejecting it. The user may have other clients logged on that
        // support webrtc and have a mic, so ignoring this call is the correct action.
        LogSvc.warn('[UnifyPhoneCallControlSvc]: Warmup failed. Ignore the incoming call: ', err);
        PubSubSvc.publish('/call/warmupFailed', [err, incomingCall]);
        terminateCall(incomingCall, null, true);

        if (conversation.isTemporary) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /conversation/temporary/ended event');
          PubSubSvc.publish('/conversation/temporary/ended', [conversation]);
        }
      };

      // Get the RtcSessionController object
      var sessionCtrl = incomingCall.sessionCtrl;
      var turnUris = getSessionTurnUris(conversation.rtcSessionId);

      if (!WebRTCAdapter.enabled) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: Client does not support WebRTC.');
        onWarmupFailed('res_NoWebRTC');
        return;
      }

      getTurnCredentials(incomingCall)
        .then(function (turnCredentials) {
          if (!incomingCall.isPresent()) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: There is no incoming call anymore');
            return;
          }
          if (turnUris) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Using TURN servers associated with session: ', turnUris);
            sessionCtrl.setTurnUris(turnUris);
          } else {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Using retrieved TURN servers');
            sessionCtrl.setTurnUris(turnCredentials.turnServer);
          }
          sessionCtrl.setTurnCredentials(turnCredentials);
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Set TURN credentials');

          // Warmup the connection to see if we can handle the call
          var localMediaType;
          if (incomingCall.replaces) {
            localMediaType = incomingCall.replaces.localMediaType;
          } else {
            // For Firefox we cannot access the getUserMedia API if the browser is inactive,
            // so we need to bypass the getUserMedia warmup by not adding any media type.
            var isFirefox = WebRTCAdapter.browser === 'firefox';
            // For Android we need to bypass the getUserMedia warmup too
            // because we should create new LocalMediaStream only when user answered a call.
            var isAndroid = WebRTCAdapter.browser === 'android';

            localMediaType = { audio: !isFirefox && !isAndroid && !_isCordova, video: false };
          }

          checkMediaSources(localMediaType, function (normalizedMediaType) {
            sessionCtrl.warmup(normalizedMediaType, evt.sdp, onSessionWarmup, onWarmupFailed);
          });
        })
        .catch(onWarmupFailed);
    }

    function processActiveSessions(conversation, activeSessions) {
      if (!conversation || !activeSessions) {
        return;
      }

      if (!conversation.hasJoined && !conversation.isTemporary) {
        // This should not happen with the actual backend, but the mock also sends the
        // events for conversations the user is no longer a participant.
        LogSvc.debug('[UnifyPhoneCallControlSvc]: User is not a conversation participant. Ignore active session.');
        return;
      }

      activeSessions.some(function (s) {
        if (conversation.rtcSessionId === s.rtcSessionId) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Processing active session for convId = ', conversation.convId);

          if (!s.participants || s.participants.length === 0) {
            LogSvc.warn('[UnifyPhoneCallControlSvc]: The active session does not have participants. rtcSessionId = ', s.rtcSessionId);
            return true;
          }

          var activeClient;
          s.participants.some(function (p) {
            if (p.userId === $rootScope.localUser.userId) {
              activeClient = p;
              return true;
            }
            return false;
          });

          if (conversation.type === Constants.ConversationType.DIRECT && !s.hosted && !activeClient) {
            return true;
          }
          if (conversation.call) {
            // Somehow we ended up with a race condition
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Conversation already has an associated call');
            return true;
          }

          addRemoteCall(conversation, activeClient, s, null, function (call) {
            if (activeClient) {
              setPeerUserForTelephonyCall(call, s);
            } else if (call.conferenceCall) {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /conference/started event');
              PubSubSvc.publish('/conference/started', [call]);
            }
          });
          return true;
        }
        return false;
      });
    }

    function sendDTMFDigits(callId, digits, cb) {
      var localCall = findLocalCallByCallId(callId);

      if (!localCall) {
        LogSvc.info('[UnifyPhoneCallControlSvc]: sendDTMFDigits - The given call ID does not match the local call');
        cb('Invalid callId');
        return;
      }

      LogSvc.info('[UnifyPhoneCallControlSvc]: Sending DTMF Digits. Num of digits: ', digits && digits.length);

      if (!localCall.sessionCtrl.sendDTMFDigits(digits)) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Cannot send DTMF digits');
        cb('res_CannotSendDTMF');
      } else {
        cb();
      }
    }

    function checkMediaSourcesAndJoin(conversation, mediaType, options, cb) {
      // Clone mediaType object before changing it
      mediaType = Object.assign({}, mediaType);

      checkMediaSources(mediaType, function (normalizedMediaType, warning) {
        if (warning) {
          if (conversation.isTelephony) {
            // Don't allow phone call to continue if we cannot access a microphone
            LogSvc.warn('[UnifyPhoneCallControlSvc]: Phone calls are not possible without a microphone. Terminate local call.');
            terminateCall(_primaryLocalCall);
            cb('res_MakeCallFailedNoMic');
            return;
          }
          options.warning = warning;
        }
        joinSession(conversation, normalizedMediaType, options, cb);
      });
    }

    function checkMediaSources(mediaType, cb) {
      adjustMediaTypeConstraints(mediaType);
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Check media sources for mediaType = ', mediaType);

      var warning;
      mediaType = mediaType ? Utils.shallowCopy(mediaType) : { audio: true, video: false };
      if (_isMobile || _isCordova || (!mediaType.audio && !mediaType.video)) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Normalized mediaType = ', mediaType);
        cb(mediaType);
        return;
      }

      LogSvc.debug('[UnifyPhoneCallControlSvc]: Get audio and video media sources');
      WebRTCAdapter.getMediaSources(function (audioSources, videoSources) {
        if (mediaType.video && (!videoSources || videoSources.length < 1)) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: No camera detected');
          mediaType.video = false;
          warning = 'res_AnswerWarnNoCamera';
        }
        if (mediaType.audio && (!audioSources || audioSources.length < 1)) {
          mediaType.audio = false;
          LogSvc.warn('[UnifyPhoneCallControlSvc]: No microphone detected');
          if (warning === 'res_AnswerWarnNoCamera') {
            warning = 'res_AccessToMediaInputDevicesFailed';
          } else {
            warning = 'res_AccessToAudioInputDeviceFailedWarning';
          }
        }
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Normalized mediaType = ', mediaType);
        cb(mediaType, warning);
      });
    }

    function dismissNotification(call) {
      // Dismiss incoming call notification if present
      if (call && call.activeNotification && !call.isHandoverInProgress) {
        NotificationSvc && NotificationSvc.dismiss(call.activeNotification);
        call.activeNotification = null;
      }
    }

    function disableAllAudio(callId) {
      // Mute the local user
      _that.mute(callId);
      // Mute the remote audio stream
      _that.disableRemoteAudio(callId);
    }

    function addParticipant(call, participant, cb) {
      cb = cb || NOP;
      if (!call || !participant) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: addParticipant - Invalid arguments');
        cb('Invalid arguments');
        return;
      }
      if (call.isDirect && !call.upgradeToConfSupported) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: addParticipant - Cannot upgrade call to conference');
        cb('Cannot upgrade to conference');
        return;
      }

      var dialOutPhoneNumber = participant.dialOutPhoneNumber;
      var contact = !dialOutPhoneNumber
        ? participant
        : {
            userId: 'gtc-callout-' + dialOutPhoneNumber.replace(/[+ ]+/g, ''),
            phoneNumber: Utils.cleanPhoneNumber(dialOutPhoneNumber),
            displayName: participant.displayName,
            resolvedUserId: participant.userId,
            participantType: Constants.RTCParticipantType.TELEPHONY,
            pstnDialIn: false
          };

      if (!contact.userId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: addParticipant - Participant missing userId');
        cb('Missing userId');
        return;
      }
      if (call.hasParticipant(contact.userId) || (contact.resolvedUserId && call.hasParticipant(contact.resolvedUserId))) {
        LogSvc.info('[UnifyPhoneCallControlSvc]: addParticipantTo - Participant is already in the call');
        cb('Participant exists');
        return;
      }
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Adding participant to existing RTC session. userId =', contact.userId);
      // Audio must be enabled for invited users.
      var mediaType = Utils.shallowCopy(call.mediaType);
      mediaType.audio = true;
      var data = {
        rtcSessionId: call.callId,
        userId: contact.userId,
        mediaType: mediaType
      };
      if (dialOutPhoneNumber) {
        data.from = {
          phoneNumber: Utils.cleanPhoneNumber($rootScope.localUser.callerId),
          displayName: $rootScope.localUser.displayName
        };
        data.to = {
          phoneNumber: contact.phoneNumber,
          displayName: contact.displayName,
          resolvedUserId: contact.resolvedUserId
        };
      }

      _clientApiHandler.addParticipantToRtcSession(data, function (err) {
        $rootScope.$apply(function () {
          if (err) {
            if (err === Constants.ErrorCode.PERMISSION_DENIED) {
              LogSvc.info('[UnifyPhoneCallControlSvc]: Failed to add participant. Reached conference participants limit');
              cb('res_MaxConversationParticipantsLimit');
              return;
            }
            LogSvc.warn('[UnifyPhoneCallControlSvc]: Failed to add participant');
            cb('res_AddParticipantToRtcSessionFailed');
          } else {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Add participant request was successful');
            // Set direction to outgoing
            call.direction = Enums.CallDirection.OUTGOING;

            if (!call.isRemote) {
              // Add participant to call stage
              var normalizedParticipant = normalizeApiParticipant(contact, ['AUDIO']);
              var pState = Enums.ParticipantState.Initiated;
              var addedParticipant = addParticipantToCallObj(call, normalizedParticipant, pState);

              if (addedParticipant) {
                LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/added event');
                PubSubSvc.publish('/call/participant/added', [call.callId, addedParticipant]);
              }
            }
            cb();
          }
        });
      });
    }

    function publishMutedEvent(call, requesterId) {
      var requester = requesterId ? call.getParticipant(requesterId) : null;

      LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/localUser/muted event');
      PubSubSvc.publish('/call/localUser/muted', [call.callId, call.remotelyMuted, call.locallyMuted, requester]);

      if (requester && call.remotelyMuted && NotificationSvc) {
        NotificationSvc.show({
          type: Enums.NotificationType.HIGH_PRIORITY,
          user: $rootScope.localUser,
          extras: {
            title: $rootScope.i18n.map.res_Muted,
            text: $rootScope.i18n.localize('res_MutedByNotification', [requester.displayName])
          }
        });
      }
    }

    function canInitiateCall(conversation, options, cb) {
      cb = cb || NOP;
      if (_incomingCalls.length >= 1) {
        cb('res_CannotJoinMultipleSessions');
        return false;
      }
      var primaryCallPresent = _primaryLocalCall && _primaryLocalCall.isPresent();

      if (primaryCallPresent) {
        if (_primaryLocalCall.isTelephonyCall) {
          if (!conversation.isTelephony || !$rootScope.localUser.isRegisterTC) {
            cb('res_CannotJoinMultipleSessions');
            return false;
          }
          if (_secondaryLocalCall) {
            cb('res_CannotInitiateThirdPhoneCall');
            return false;
          }
        } else if (!options.replaces) {
          cb('res_CannotJoinMultipleSessions');
          return false;
        }
      }

      for (var idx = 0; idx < _calls.length; idx++) {
        if (!_calls[idx].state.established && _calls[idx].isTelephonyCall) {
          LogSvc.error('[UnifyPhoneCallControlSvc]: Cannot initiate second phone call if existing call is not established');
          cb('res_JoinRTCSessionFailed');
          return false;
        }
      }

      if (conversation.isTelephony) {
        if (!options.handover) {
          options.dialedDn = options.dialedDn ? options.dialedDn.trim() : null;
          if (!options.dialedDn) {
            cb('Missing Dialed DN');
            return false;
          }
        }
        if (!$rootScope.localUser.callerId) {
          cb('Missing Caller ID');
          return false;
        }
        if ($rootScope.localUser.isOsBizCTIEnabled && !options.handover && conversation.call) {
          // If OsBiz CTI, do not allow initiating a second phone call
          cb('res_CannotJoinMultipleSessions');
          return false;
        }
      }
      return true;
    }

    function createCallOptions(options, additionalOptions) {
      options = options || {};
      additionalOptions && Object.assign(options, additionalOptions);
      options.callOut = !!options.callOut;
      options.handover = !!options.handover;
      return options;
    }

    function addRemoveMedia(callId, mediaToChange, logMethodName, options) {
      mediaToChange = mediaToChange || {};
      logMethodName = logMethodName || 'addRemoveMedia';
      options = options || {};

      var logTopic = '[UnifyPhoneCallControlSvc]: ' + logMethodName + ': ';
      var localCall = findLocalCallByCallId(callId);
      var incomingCall = null;
      var currentMediaType = {};

      if (!localCall) {
        incomingCall = getIncomingCall(callId);
        if (!incomingCall || incomingCall.callId !== callId) {
          LogSvc.warn(logTopic + 'There is no local call');
          return $q.reject('No active call');
        }
        currentMediaType = incomingCall.mediaType;
      } else {
        currentMediaType = localCall.localMediaType;
      }

      LogSvc.debug(logTopic, mediaToChange);

      var data = {
        callId: callId,
        dontReuseAudioStream: !!options.dontReuseAudioStream,
        mediaType: Object.assign({}, currentMediaType, mediaToChange)
      };

      // When adding/removing video or screenshare we need to reset the corresponding HD settings, so
      // they are properly initialized in adjustMediaTypeConstraints().
      if (typeof mediaToChange.video === 'boolean' && typeof mediaToChange.hdVideo !== 'boolean') {
        delete data.mediaType.hdVideo;
      }
      if (typeof mediaToChange.desktop === 'boolean' && typeof mediaToChange.hdDesktop !== 'boolean') {
        delete data.mediaType.hdDesktop;
      }

      if (!isVideoAndScreenShareEnabled(localCall)) {
        // Only support either video or desktop
        if (mediaToChange.video) {
          data.mediaType.desktop = false;
        } else if (mediaToChange.desktop) {
          data.mediaType.video = false;
        }
      }

      return new $q(function (resolve, reject) {
        if (incomingCall) {
          // This is an incoming call so we can only re-warmup the session by getting the local media again
          checkMediaSources(data.mediaType, function (normalizedMediaType) {
            // To force a getUserMedia, set the current local stream to null
            incomingCall.sessionCtrl.replaceLocalStream(RtcSessionController.LOCAL_STREAMS.AUDIO_VIDEO, null);
            incomingCall.sessionCtrl.warmup(
              normalizedMediaType,
              null,
              function () {
                LogSvc.debug(logTopic + 're-warmup succeeded');
                resolve();
              },
              function (err) {
                LogSvc.warn(logTopic + 're-warmup failed - ', err);
                reject(err);
              }
            );
          });
        } else {
          changeMediaType(data, function (err) {
            if (err) {
              LogSvc.warn(logTopic + 'failed - ', err);
              reject(err);
            } else {
              resolve();
            }
          });
        }
      });
    }

    /**
     * Changes the video receiving configuration.
     *
     * A receiver configuration contains the following fields:
     *  - id         (required) the stream ID of the receiving line, the same as used in the participant data, so either MID or MSID
     *  - quality    (optional) one of Constants.VideoQuality.[LOW, NORMAL, HIGH]
     *  - pinnedUser (optional) ID of user to pin to this line (will not get switched by active speaker)
     *  - disabled   (optional) true if receiving line should not be used (e.g. to turn off incoming video)
     *
     * so e.g. {id: '1', quality: 'NORMAL', pinnedUser: '6e725181-109e-4858-8e10-6b21284fe619'}
     *
     * @param {String} callId the ID of the call
     * @param {Array} configuration an array of receiver configurations
     * @returns {Promise} Promise that resolves when operation is completed.
     */
    function setVideoReceiverConfiguration(callId, configuration) {
      var activeCall = _that.getActiveCall();
      if (!activeCall || activeCall.callId !== callId) {
        return $q.reject('Call not found');
      }
      if (!configuration || !configuration.length) {
        return $q.reject('Invalid configuration');
      }
      if (_pendingSetVideoReceiverConfiguration && _pendingSetVideoReceiverConfiguration.callId === callId) {
        _pendingSetVideoReceiverConfiguration.configuration = configuration;
      } else {
        if (_pendingSetVideoReceiverConfiguration) {
          // Cancel pending request for different call
          _pendingSetVideoReceiverConfiguration.cancel();
        }
        var pendingConfiguration = {
          callId: callId,
          configuration: configuration
        };
        pendingConfiguration.promise = new $q(function (resolve, reject) {
          // Timeout is used here to avoid sending too many backend requests in case multiple
          // changes are done in a short time (e.g. when switching UI layout).
          pendingConfiguration.timeout = $timeout(
            function () {
              if (_pendingSetVideoReceiverConfiguration !== pendingConfiguration) {
                return;
              }
              _pendingSetVideoReceiverConfiguration = null;
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Set video receiver configuration to ', pendingConfiguration.configuration);
              var data = {
                rtcSessionId: callId,
                configuration: pendingConfiguration.configuration
              };
              _clientApiHandler.setVideoReceiverConfiguration(data, function (err) {
                $rootScope.$apply(function () {
                  err ? reject(err) : resolve();
                });
              });
            },
            SET_VIDEO_RECEIVER_CONFIGURATION_DELAY,
            false
          );

          pendingConfiguration.cancel = function () {
            if (_pendingSetVideoReceiverConfiguration !== pendingConfiguration) {
              return;
            }
            _pendingSetVideoReceiverConfiguration = null;
            LogSvc.debug('[UnifyPhoneCallControlSvc] Canceling pending setVideoReceiverConfiguration request');
            reject('Cancelled');
            $timeout.cancel(pendingConfiguration.timeout);
          };
        });
        _pendingSetVideoReceiverConfiguration = pendingConfiguration;
      }
      return _pendingSetVideoReceiverConfiguration.promise;
    }

    function handleInviteEvent(evt) {
      try {
        var conversation = getConversation(evt.convId);
        if (!conversation) {
          LogSvc.info('[UnifyPhoneCallControlSvc]: Could not find corresponding conversation. Save pending INVITE and retrieve conversation.');
          // Save the INVITE event and get the conversation
          var sessionId = evt.sessionId;
          _pendingInvites[sessionId] = evt;
          var options = { randomLoaded: true, sessionId: evt.sessionId };
          ConversationSvc.getConversationById(evt.convId, options, function (getConvErr, conv) {
            if (getConvErr) {
              LogSvc.warn('[UnifyPhoneCallControlSvc]: Could not retrieve corresponding conversation. Delete Pending Invite');
            } else if (_pendingInvites[sessionId]) {
              LogSvc.info('[UnifyPhoneCallControlSvc]: Successfully retrieved conversation. Process the pending INVITE message.');
              processInvite(_pendingInvites[sessionId], conv);
            } else {
              LogSvc.info('[UnifyPhoneCallControlSvc]: Successfully retrieved conversation. Check if there are any active sessions.');
              _clientApiHandler.getActiveSessions(function (getSessionsErr, activeSessions) {
                $rootScope.$apply(function () {
                  if (getSessionsErr) {
                    LogSvc.error('[UnifyPhoneCallControlSvc]: Error getting active sessions: ', getSessionsErr);
                    activeSessions = [];
                  }
                  processActiveSessions(conv, activeSessions);
                });
              });
            }
            delete _pendingInvites[sessionId];
          });
          return;
        }

        if (!conversation.hasJoined) {
          // This should not happen with the actual backend, but the mock also sends the
          // events for conversations the user is no longer a participant.
          LogSvc.info('[UnifyPhoneCallControlSvc]: User is not a conversation participant. Ignore event.');
          return;
        }

        $rootScope.$apply(function () {
          processInvite(evt, conversation);
        });
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCCall.INVITE event. ', e);
      }
    }

    function updateHosted(evt, call) {
      if (evt.session.hosted && call.isDirect) {
        call.setDirectUpgradedToConf();
      } else if (call.isDirectUpgradedToConf && !evt.session.hosted) {
        call.clearDirectUpgradedToConf();
      }
    }

    function addRemoteCallForSessionUpdate(evt, activeClient) {
      var getConversationPromise;
      var conversation = ConversationSvc.getConversationByRtcSession(evt.sessionId);
      if (!conversation) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: Could not find corresponding conversation');
        return;
      } else {
        getConversationPromise = $q.resolve(conversation);
      }

      if (activeClient.clientId === _clientApiHandler.clientId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: Event is for a local call that has been terminated. Ignore event.');
        return;
      }

      _pendingRemoteCalls[evt.sessionId] = evt;
      getConversationPromise
        .then(function (resolvedConv) {
          if (_pendingRemoteCalls[evt.sessionId]) {
            addRemoteCall(resolvedConv, activeClient, evt.session, evt.sessionId, function (call) {
              // When user rejects or doesn't answer direct call and voicemail is enabled,
              // SESSION_UPDATED event triggers, user stays on call, but in header there shouldn't be
              // any buttons. So call should be marked as voicemailConnected, and that property is used for hiding
              // addParticipant and more buttons in call header.
              call.voicemailConnected = evt.session.participants.some(function (p) {
                return p.participantType === Constants.RTCParticipantType.VOICE_MAIL;
              });

              setPeerUserForTelephonyCall(call, evt.session);
            });
            delete _pendingRemoteCalls[evt.sessionId];
          }
        })
        .catch(function (err) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Could not retrieve guest conversation. Remote call will not be displayed.', err);
        });
    }

    function handleMovedCallEvent(evt, activeClient, localCall) {
      var conversation = getConversation(localCall.convId);
      var getConversationPromise = $q.resolve(conversation);

      var phoneNumber = null;
      var displayName = null;
      var participants = null;
      if (localCall.isTelephonyCall) {
        phoneNumber = localCall.peerUser.phoneNumber;
        displayName = localCall.peerUser.displayName;
        participants = localCall.participants;
      }

      var atcCallInfo = localCall.atcCallInfo;
      var pickUpFromUser = localCall.pickUpFromUser;
      var pickUpUserId = localCall.pickUpUserId;

      terminateCall(localCall, Enums.CallClientTerminatedReason.ANOTHER_CLIENT_PULLED_CALL);

      _pendingRemoteCalls[evt.sessionId] = evt;
      getConversationPromise
        .then(function (resolvedConv) {
          if (_pendingRemoteCalls[evt.sessionId]) {
            addRemoteCall(resolvedConv, activeClient, evt.session, evt.sessionId, function (call) {
              call.atcCallInfo = atcCallInfo;
              if (call.isTelephonyCall) {
                setCallPeerUser(call, phoneNumber, null, displayName);
                call.participants = participants;
                //NGTC-7108:When a conference is pulled we need to know it, because participants in remote call are not the same as in local call.
                //When one of the users leaves the call we need to calculate properly if it's direct or conference call.
                if (call.isAtcConferenceCall()) {
                  call.pulledConference = true;
                }
              }
              call.pickUpFromUser = pickUpFromUser;
              call.pickUpUserId = pickUpUserId;
            });
            delete _pendingRemoteCalls[evt.sessionId];
          }
        })
        .catch(function (err) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Could not retrieve guest conversation. Remote call will not be displayed.', err);
        });
    }

    function updateCallFeaturesFromSessionUpdate(evt, localCall) {
      if (evt.session.muted !== localCall.sessionMuted) {
        localCall.sessionMuted = evt.session.muted;
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/muted event');
        PubSubSvc.publish('/call/muted', [localCall]);
      }
      if (evt.session.locked !== localCall.sessionLocked) {
        localCall.sessionLocked = evt.session.locked;
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/locked event');
        PubSubSvc.publish('/call/locked', [localCall]);
      }

      if (evt.session.testMode) {
        localCall.testMode = evt.session.testMode;
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/testMode event (session updated)');
        PubSubSvc.publish('/call/testMode', [localCall]);
      }

      var whiteboardEnabled = !!evt.session.whiteBoardEnabled;
      if (localCall.whiteboardEnabled !== whiteboardEnabled) {
        localCall.whiteboardEnabled = whiteboardEnabled;
        PubSubSvc.publish('/call/whiteboard/update', [localCall]);
      }

      var pollEnabled = !!evt.session.pollEnabled;
      if (localCall.pollEnabled !== pollEnabled) {
        localCall.pollEnabled = pollEnabled;
        PubSubSvc.publish('/call/poll/update', [localCall]);
      }
    }

    function processSessionParticipants(evt, localCall) {
      var data = {
        addedParticipants: [],
        updatedParticipants: [],
        removedParticipantIds: []
      };

      var sessionParticipants = evt.session.participants || [];

      // Since we are not a Meeting Room, let's remove ourselves from the participants list
      sessionParticipants.some(function (p, idx) {
        if (p.userId === $rootScope.localUser.userId) {
          sessionParticipants.splice(idx, 1);
          return true;
        }
        return false;
      });

      if (!localCall.isDirect && !localCall.isAtcConferenceCall()) {
        // Initialize with previous participants. We'll filter the participants that are still in the session below.
        data.removedParticipantIds = localCall.participants
          .filter(function (p) {
            // Ignore ringing participants that have been added by the local user
            return p.pcState !== Enums.ParticipantState.Initiated;
          })
          .map(function (p) {
            return p.userId;
          });
      }

      if (sessionParticipants.length > 0 || localCall.conferenceCall) {
        // There are other participants already in the call

        var mediaType = Proto.getMediaType(evt.session.mediaTypes);
        // Safeguard if server sends wrong media types
        mediaType.audio = mediaType.audio || localCall.localMediaType.audio;
        mediaType.video = mediaType.video || localCall.localMediaType.video;
        mediaType.desktop = mediaType.desktop || localCall.localMediaType.desktop;
        localCall.mediaType = mediaType;
        localCall.activeClient = null;

        processReplacedCall(localCall, sessionParticipants.length > 0);

        if (!localCall.isAtcConferenceCall()) {
          sessionParticipants.forEach(function (p) {
            var mediaParticipantUpdate = false;
            var normalizedParticipant = normalizeApiParticipant(p);

            var pState = normalizedParticipant.muted ? Enums.ParticipantState.Muted : Enums.ParticipantState.Active;

            var callParticipant = localCall.getParticipant(p.userId);
            lookupParticipant(normalizedParticipant, localCall);

            if (callParticipant) {
              Utils.removeArrayElement(data.removedParticipantIds, p.userId);

              var oldPcState = callParticipant.pcState;
              var oldVideoStream = callParticipant.videoStream;

              if (
                callParticipant.streamId !== normalizedParticipant.streamId ||
                callParticipant.screenStreamId !== normalizedParticipant.screenStreamId ||
                !callParticipant.hasSameMediaType(normalizedParticipant)
              ) {
                mediaParticipantUpdate = true;
              }

              var updatedParticipant = updateParticipantInCallObj(localCall, normalizedParticipant, pState);
              if (
                updatedParticipant &&
                (mediaParticipantUpdate || updatedParticipant.pcState !== oldPcState || updatedParticipant.videoStream !== oldVideoStream)
              ) {
                data.updatedParticipants.push(updatedParticipant);
              }
            } else {
              var addedParticipant = addParticipantToCallObj(localCall, normalizedParticipant, pState);
              if (addedParticipant) {
                data.addedParticipants.push(addedParticipant);
              }
            }
          });
        }
      }
      return data;
    }

    function processLocalUserLeftEvt(evt, localCall) {
      var remover = localCall && evt.cause === Constants.RTCSessionParticipantLeftCause.REMOVED && evt.requesterId && localCall.getParticipant(evt.requesterId);

      var params = [evt.sessionId, evt.cause];
      remover && params.push(remover);
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /localUser/leftConference event');
      PubSubSvc.publish('/localUser/leftConference', params);

      if (remover && NotificationSvc) {
        NotificationSvc.show({
          type: Enums.NotificationType.HIGH_PRIORITY,
          user: $rootScope.localUser,
          extras: {
            title: $rootScope.i18n.map.res_RemovedFromConferenceTitle,
            text: $rootScope.i18n.localize('res_RemovedFromConferenceBy', [remover.displayName])
          }
        });
      }
    }

    function processParticipantLeftForAlertingCall(evt) {
      var alertingCall = getIncomingCall(evt.sessionId);
      if (alertingCall && !alertingCall.isDirect && alertingCall.hasParticipant(evt.userId)) {
        // Caller left the conference
        LogSvc.info('[UnifyPhoneCallControlSvc]: Event is for an incoming call. Caller has left the conversation.');
        removeCallParticipant(alertingCall, evt.userId, evt.cause);
        // Create a new active remote call and terminate the alerting call.
        var alertingConversation = getConversation(alertingCall.convId);
        if (alertingConversation) {
          addRemoteCall(alertingConversation);
          terminateCall(alertingCall, Enums.CallClientTerminatedReason.CALLER_LEFT_CONFERENCE);
        }
      } else {
        LogSvc.info('[UnifyPhoneCallControlSvc]: Event is not for local or alerting call. Ignore it.');
      }
    }

    function getDefaultHandler(cb) {
      if (typeof cb !== 'function') {
        return NOP;
      }
      return function (err) {
        $rootScope.$apply(function () {
          cb(err);
        });
      };
    }

    function handlePendingSessionUpdatedActions(call) {
      if (call.callConnectedPostponed) {
        call.callConnectedPostponed = false;
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Send pending call connected diagnostics data for instanceId = ', call.instanceId);
        DeviceDiagnosticSvc.sendCallConnected(call);
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/conversation/update', function (conv, data) {
      if (!data) {
        // Event was not triggered by a Conversation.UPDATE event
        return;
      }
      var localCall = findLocalCallByCallId(conv.rtcSessionId);
      if (!localCall || localCall.isDirect) {
        // Event is not applicable
        return;
      }
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received /conversation/update event');

      if (data.isModerated !== undefined) {
        // Update the participant actions in case the conversation moderation has been toggled
        localCall.participants.forEach(function (p) {
          p.setActions(conv, $rootScope.localUser);
        });
      }
    });

    PubSubSvc.subscribe('/conversation/upgrade', function (oldConversation, newConversation) {
      LogSvc.debug(
        '[UnifyPhoneCallControlSvc]: Received /conversation/upgrade event. Old convId = ' + oldConversation.convId + '. New convId = ',
        newConversation.convId
      );

      if (
        oldConversation.call &&
        newConversation.convId !== oldConversation.convId &&
        (oldConversation.call.checkState(Enums.CallState.Active) ||
          (oldConversation.type !== Constants.ConversationType.DIRECT && oldConversation.call.checkState(Enums.CallState.Waiting)))
      ) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Move Session');
        if (oldConversation.type === Constants.ConversationType.DIRECT && newConversation.type === Constants.ConversationType.GROUP) {
          var options = createCallOptions({ callOut: true, handover: false, replaces: oldConversation.call });
          if (createLocalCall(newConversation, oldConversation.call.localMediaType, options)) {
            joinSession(newConversation, oldConversation.call.localMediaType, options, function (err) {
              if (!err) {
                if (oldConversation.call.sessionCtrl && oldConversation.call.sessionCtrl.isMuted()) {
                  newConversation.call.sessionCtrl && newConversation.call.sessionCtrl.mute();
                }
              }
            });
          }
        } else {
          var data = {
            sessionId: oldConversation.rtcSessionId,
            conversationId: oldConversation.convId,
            newSessionId: newConversation.rtcSessionId,
            newConversationId: newConversation.convId
          };

          _clientApiHandler.moveRtcSession(data, function (err) {
            if (err) {
              LogSvc.warn('[UnifyPhoneCallControlSvc]: Error moving RTC Session. ', err);
            }
          });
        }
      }
    });

    PubSubSvc.subscribe('/conversation/left', function (conversation) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received /conversation/left event. convId = ', conversation.convId);

      if (conversation && conversation.call) {
        _that.hideRemoteCall(conversation.call.callId);
      }
    });

    PubSubSvc.subscribe('/conversation/participants/add', function (call, participants) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received /conversation/participants/add event.');
      if (call && participants) {
        participants.forEach(function (participant) {
          addParticipant(call, participant);
        });
      }
    });

    PubSubSvc.subscribe('/csta/handover', function (callId) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received /csta/handover event. callId = ', callId);
      _callIdToBeReplaced = callId;
      _handoverTimer = $timeout(function () {
        _handoverTimer = null;
      }, ATC_HANDOVER_TIME);
    });

    PubSubSvc.subscribe('/atccall/moveFailed', function () {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received /atccall/moveFailed event.');
      if (_handoverTimer) {
        $timeout.cancel(_handoverTimer);
        _handoverTimer = null;
      }
    });

    PubSubSvc.subscribe('/atccall/pickUpInProgress', function (call) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received /atccall/pickUpInProgress event.');
      _callToPickup = call;
      _pickupTimer = $timeout(function () {
        _pickupTimer = null;
        _callToPickup = null;
      }, ATC_PICKUP_TIMER);
    });

    PubSubSvc.subscribe('/conversations/loadComplete', function () {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received /conversations/loadComplete event');
      _conversationLoaded = true;
      initActiveSessions();
    });

    PubSubSvc.subscribe('/registration/state', function (state) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received /registration/state event');
      if (state !== RegistrationState.Registered) {
        if (_primaryLocalCall && _primaryLocalCall.isTestCall) {
          // Test calls cannot be recovered
          terminateCall(_primaryLocalCall, Enums.CallClientTerminatedReason.LOST_WEBSOCKET_CONNECTION);
        }

        // Clear all incoming calls, since we can't answer them anymore
        _incomingCalls.forEach(function (c) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Lost websocket connection, terminate local incoming call');
          terminateCall(c, Enums.CallClientTerminatedReason.LOST_WEBSOCKET_CONNECTION);
        });

        if (state === RegistrationState.Disconnected || state === RegistrationState.Waiting) {
          // Clear all other calls
          _calls.forEach(function (c) {
            terminateCall(c, Enums.CallClientTerminatedReason.LOST_WEBSOCKET_CONNECTION);
          });
        }
      }
    });

    PubSubSvc.subscribe('/conversation/re-added', function (conv) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received /conversation/re-added event. convId = ', conv.convId);
      _clientApiHandler.getSession(conv.rtcSessionId, function (err, session) {
        if (err) {
          LogSvc.warn('[UnifyPhoneCallControlSvc] Error retrieving session. Err: ', err);
        } else if (session.participants && session.participants.length > 0) {
          $rootScope.$apply(function () {
            processActiveSessions(conv, [session]);
          });
        }
      });
    });

    PubSubSvc.subscribe('/call/meetingPointInvitee', function (callId) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received /call/meetingPointInvitee event');
      if (callId) {
        var localCall = findLocalCallByCallId(callId);
        if (localCall) {
          localCall.setMeetingPointInviteState(true);
          disableAllAudio(callId);
        }
      }
    });

    PubSubSvc.subscribe('/call/removeConferenceAsGuest', function (call) {
      LogSvc.debug('UnifyPhoneCallControlSvc]: /call/removeConferenceAsGuest');
      _that.removeConferenceAsGuest(call);
    });

    PubSubSvc.subscribe('/agent/status/enabled', function (enabled) {
      LogSvc.debug('UnifyPhoneCallControlSvc]: /agent/status/enabled event', enabled);
      updateAgentHuntGroupEnabled(enabled);
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Client API Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    _userToUserHandler.on('ATC.ADVANCING', function (data) {
      try {
        if (data.type === AtcMessage.ADVANCING) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Received UserToUser ATC.ADVANCING');
          var call = getIncomingCall(data.rtcSessionId);
          if (call) {
            call.atcAdvancing = true;
            if (!call.startedEarlyMedia) {
              call.sessionCtrl.setIgnoreNextConnection(true);
            }
          }
        }
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling UserToUser ATC.ADVANCING event. ', e);
      }
    });

    _clientApiHandler.on('RTCCall.SDP_ANSWER', function (evt) {
      try {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCCall.SDP_ANSWER');

        var localCall = findLocalCallByCallId(evt.sessionId);
        if (!localCall) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Unexpected event. There is no local call');
          return;
        }

        if (_clientApiHandler.clientId !== evt.clientID) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Event is not for this client: ', _clientApiHandler.clientId);
          return;
        }
        if (localCall.isDirect) {
          DeviceDiagnosticSvc.startJoinDelayTimer(localCall);
        }

        $rootScope.$apply(function () {
          // Make sure we have a valid SDP
          var sdp = evt.sdp;
          if (sdp) {
            var sdpAnswerInfo = DeviceDiagnosticSvc.createActionInfo(localCall, Constants.RtcDiagnosticsAction.SDP_ANSWER);

            if (!sdp.sdp || sdp.sdp === 'sdp' || sdp.sdp === 'data') {
              // The mock keeps getting changed, so we need to adapt...
              sdp.sdp = 'sdp';
              LogSvc.debug('[UnifyPhoneCallControlSvc]: This is a mocked call');
              localCall.setMockedCall();
            }

            localCall.setInstanceId(evt.instanceId);
            // We also need to call setRemoteDescription for mock scenarios in order
            // to clear the media renegotiation flags.
            localCall.sessionCtrl.setRemoteDescription(sdp, function (err) {
              localCall.clearTransactionId();

              if (err) {
                LogSvc.error('[UnifyPhoneCallControlSvc]: Error setting remote description: ', err);
                localCall.setDisconnectCause(Constants.DisconnectCause.REMOTE_SDP_FAILED, 'type=' + sdp.type + ' origin=' + sdpParser.getOrigin(sdp.sdp));
                leaveCall(localCall, null, Enums.CallClientTerminatedReason.SET_REMOTE_SDP_FAILED);
                if (sdpAnswerInfo) {
                  sdpAnswerInfo.data = err;
                }
              }

              DeviceDiagnosticSvc.finishActionInfo(localCall, sdpAnswerInfo);
            });
          }
          evt.localMute &&
            _that.mute(localCall.callId, function (err) {
              err && LogSvc.warn('[UnifyPhoneCallControlSvc]: Error synchronizing client mute state. Error: ', err);
            });
        });
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCCall.SDP_ANSWER event. ', e);
      }
    });

    _clientApiHandler.on('RTCCall.ICE_CANDIDATES', function (evt) {
      try {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCCall.ICE_CANDIDATES');

        var localCall = findLocalCallByCallId(evt.sessionId);
        if (!localCall) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Unexpected event. There is no local call');
          return;
        }

        if (_clientApiHandler.clientId !== evt.clientId) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Event is not for this client: ', _clientApiHandler.clientId);
          return;
        }

        localCall.sessionCtrl.addIceCandidates(evt.origin, evt.candidates);

        var iceInfo = DeviceDiagnosticSvc.createActionInfo(localCall, Constants.RtcDiagnosticsAction.REMOTE_ICE_CANDIDATES);
        if (iceInfo) {
          iceInfo.data = evt.candidates;
          DeviceDiagnosticSvc.finishActionInfo(localCall, iceInfo);
        }
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCCall.ICE_CANDIDATES event. ', e);
      }
    });

    _clientApiHandler.on('RTCSession.SESSION_UPDATED', function (evt) {
      $rootScope.$apply(function () {
        try {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.SESSION_UPDATED');

          var activeClient = evt.session.participants.find(function (p) {
            return p.userId === $rootScope.localUser.userId;
          });

          var localCall = findLocalCallByCallId(evt.sessionId);
          if (activeClient) {
            var activeRemoteCall = getActiveRemoteCall(evt.sessionId);
            if (activeRemoteCall) {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Event is for an existing active remote call');
              activeRemoteCall.mediaType = Proto.getMediaType(evt.session.mediaTypes);
              activeRemoteCall.setActiveClient(activeClient);
              updateHosted(evt, activeRemoteCall);
              publishCallState(activeRemoteCall);
              return;
            }

            var alertingCall = getIncomingCall(evt.sessionId);
            if (alertingCall) {
              // For alerting calls, the active remote call is supposed to be created with
              // the INVITE_CANCEL message.
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Event is for an existing active remote call');
              return;
            }

            if (!localCall || (localCall.callId !== evt.sessionId && !findActivePhoneCall(true))) {
              // The event is not for the local call. We need to create a new active remote call in this case.
              addRemoteCallForSessionUpdate(evt, activeClient);
              return;
            }

            // IF we get to this point then the event is for the local call.

            if (activeClient.clientId !== _clientApiHandler.clientId) {
              // Call has been moved. Create a new active remote call and terminate the local call.
              handleMovedCallEvent(evt, activeClient, localCall);
              return;
            }

            // Check if the local user has been muted / unmuted
            if (localCall.remotelyMuted !== activeClient.muted) {
              localCall.remotelyMuted = activeClient.muted;
              publishMutedEvent(localCall);
            }
          } else {
            LogSvc.info('[UnifyPhoneCallControlSvc]: Not a large conference and local user is not in the participants list. Ignore event.');
            return;
          }

          if (!localCall.instanceId) {
            // Make sure we always have the instanceId set in our call object (important for Qos)
            localCall.setInstanceId(evt.session.instanceId);
          }

          updateCallFeaturesFromSessionUpdate(evt, localCall);

          updateHosted(evt, localCall);

          localCall.hadRemoteCall = false; // At this stage of the call we don't need this flag anymore. So reset it.

          var participantsData = processSessionParticipants(evt, localCall);

          evt.session.recentActiveSpeakers = evt.session.recentActiveSpeakers || [];
          localCall.recentActiveSpeakers = evt.session.recentActiveSpeakers.filter(function (id) {
            return localCall.hasParticipant(id);
          });

          var oldState = localCall.state;
          updateAttendeeCount(evt.session.attendeeCount);
          if (localCall.conferenceCall || localCall.checkState(Enums.CallState.Answering)) {
            if (!localCall.isEstablished()) {
              localCall.setState(Enums.CallState.Waiting);
            }
            // Invoke updateCallState so call object changes state to Active if applicable
            localCall.updateCallState();
          }
          if (!localCall.checkState(oldState)) {
            // state has changed
            publishCallState(localCall);
          }

          if (localCall.conferenceCall && !localCall.hasOtherParticipants() && evt.participantId === $rootScope.localUser.userId) {
            // This is the first user joining the conference
            // Note that evt.participantId is only included in the first SESSION_UPDATED event after client joins conference
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /conference/starting event');
            PubSubSvc.publish('/conference/starting', [localCall]);
          }

          participantsData.removedParticipantIds.forEach(function (userId) {
            var removedParticipant = localCall.removeParticipant(userId);
            if (removedParticipant) {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/removed event. userId = ', userId);
              PubSubSvc.publish('/call/participant/removed', [localCall.callId, removedParticipant]);
            }
          });

          if (participantsData.updatedParticipants.length > 0) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participants/updated event');
            PubSubSvc.publish('/call/participants/updated', [localCall.callId, participantsData.updatedParticipants]);
          }
          if (participantsData.addedParticipants.length > 0) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participants/added event');
            PubSubSvc.publish('/call/participants/added', [localCall.callId, participantsData.addedParticipants]);
          }

          // Check if the session is being recorded or a recording has been paused
          // This block of code (including the event) should not be run while on an echo test call
          if (!localCall.isTestCall) {
            if (evt.session.recordings) {
              evt.session.recordings.forEach(function (data) {
                setRecordingInfoData(localCall, data);
              });
            } else {
              setRecordingInfoData(localCall, evt.session.recordingInfo);
            }
          }

          // Reset isJoiningGroupCall flag so we don't show a recording notification on the next SESSION_UPDATED
          localCall.isJoiningGroupCall = false;

          simulateActiveSpeakers();
          handlePendingSessionUpdatedActions(localCall);
        } catch (e) {
          LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCSession.SESSION_UPDATED event. ', e);
        }
      });
    });

    _clientApiHandler.on('RTCSession.SESSION_STARTED', function (evt) {
      if (!_conversationLoaded) {
        LogSvc.info('[UnifyPhoneCallControlSvc]: Conversations are not loaded. Do not process the SESSION_STARTED event.');
        return;
      }

      try {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.SESSION_STARTED');
        var localCall = findLocalCallByCallId(evt.sessionId);
        if (localCall && localCall.isTestCall) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Received SESSION_STARTED event for echo test call. Just ignore it.');
          return;
        }

        if (evt.session.turnServers) {
          putSessionTurn(evt.sessionId, evt.session.turnServers);
        }

        var conversation = getConversation(evt.convId);
        if (!conversation) {
          LogSvc.info('[UnifyPhoneCallControlSvc]: Could not find corresponding conversation');
          var sessionCreatorId = evt.session.participants.length === 1 && evt.session.participants[0].userId;
          // Even if callOut, continue to fetch conversation if local user started session (on any device)
          if (evt.callOut && sessionCreatorId !== $rootScope.localUser.userId) {
            return;
          }
          LogSvc.info('[UnifyPhoneCallControlSvc]: Get the conversation from the server');
          ConversationSvc.getConversationById(evt.convId, { randomLoaded: true }, function (getConvErr, conv) {
            if (getConvErr) {
              LogSvc.warn('[UnifyPhoneCallControlSvc]: Could not retrieve corresponding conversation. Delete Pending Invite');
            } else {
              LogSvc.info('[UnifyPhoneCallControlSvc]: Successfully retrieved the conversation. Check if there are any active sessions.');
              _clientApiHandler.getActiveSessions(function (getSessionsErr, activeSessions) {
                $rootScope.$apply(function () {
                  if (getSessionsErr) {
                    LogSvc.error('[UnifyPhoneCallControlSvc]: Error getting active sessions: ', getSessionsErr);
                    activeSessions = [];
                  }
                  processActiveSessions(conv, activeSessions);
                });
              });
            }
          });
          return;
        }

        if (!conversation.hasJoined) {
          // This should not happen with the actual backend, but the mock also sends the
          // events for conversations the user is no longer a participant.
          LogSvc.info('[UnifyPhoneCallControlSvc]: User is not a conversation participant. Ignore event.');
          return;
        }

        $rootScope.$apply(function () {
          if (localCall) {
            if (!localCall.instanceId) {
              // Make sure we always have the instanceId set in our call object (important for Qos)
              localCall.setInstanceId(evt.session.instanceId);
            }
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Found local call for SESSION_STARTED event');
            LogSvc.info('[UnifyPhoneCallControlSvc]: Ignore SESSION_STARTED. State =', localCall.state.name);
          } else if (evt.callOut) {
            var pickUpFromUser;
            if (_callToPickup && _callToPickup.callId === evt.sessionId) {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: The call was picked up by a remote client');
              pickUpFromUser = _callToPickup.pickUpFromUser;
              _that.ignorePickupCall();
            }
            evt.session.participants.some(function (p) {
              if (p.userId === $rootScope.localUser.userId && p.clientId !== _clientApiHandler.clientId) {
                // This an outgoing call on remote device
                addRemoteCall(conversation, p, evt.rtcSession, evt.sessionId, function (call) {
                  call.pullNotAllowed = true;
                  if (pickUpFromUser) {
                    call.pickUpFromUser = pickUpFromUser;
                  }
                });
                return true;
              }
              return false;
            });
          } else if (conversation.type === Constants.ConversationType.GROUP || conversation.type === Constants.ConversationType.LARGE) {
            // This is a new conference call.
            // Create a remote call and allow the user to join.
            var activeClient = evt.session.participants.find(function (p) {
              return p.userId === $rootScope.localUser.userId && p.clientId !== _clientApiHandler.clientId;
            });
            addRemoteCall(conversation, activeClient, evt.rtcSession, evt.sessionId, function (call) {
              if (!activeClient) {
                LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /conference/started event');
                PubSubSvc.publish('/conference/started', [call]);
              }
            });
          }
          updateAttendeeCount(evt.session.attendeeCount);
        });
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCSession.SESSION_STARTED event: ', e);
      }
    });

    _clientApiHandler.on('RTCSession.PARTICIPANT_JOINED', function (evt) {
      try {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.PARTICIPANT_JOINED');
        var userId = evt.participant.userId;
        if ($rootScope.localUser.userId === userId) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Ignore JOINED event for own user');
          return;
        }

        var activeRemoteCall = getActiveRemoteCall(evt.sessionId);
        if (activeRemoteCall) {
          $rootScope.$apply(function () {
            if (activeRemoteCall.pullNotAllowed) {
              activeRemoteCall.pullNotAllowed = false;
            }
            if (activeRemoteCall.isTelephonyCall && !activeRemoteCall.atcCallInfo) {
              setCallPeerUser(activeRemoteCall, evt.participant.phoneNumber, null, evt.participant.userDisplayName);
            }
          });
          return;
        }
        var localCall = findLocalCallByCallId(evt.sessionId);
        if (!localCall) {
          LogSvc.info('[UnifyPhoneCallControlSvc]: Event is not for local call. Ignore it.');
          return;
        }

        if (!localCall.isEstablished() && localCall.direction === Enums.CallDirection.INCOMING) {
          // Ignore the JOINED events for incoming alerting calls
          LogSvc.info('[UnifyPhoneCallControlSvc]: Ignore JOINED event for incoming alerting calls');
          return;
        }

        $rootScope.$apply(function () {
          if (evt.participant.participantType === Constants.RTCParticipantType.VOICE_MAIL) {
            localCall.sessionCtrl.setCallStatsOptions({ sendOnlyStream: true });
          }

          if (localCall.isTelephonyCall && (!localCall.atcCallInfo || !localCall.peerUser)) {
            setCallPeerUser(localCall, evt.participant.phoneNumber, null, evt.participant.userDisplayName);
          }

          if (localCall.checkState([Enums.CallState.Delivered, Enums.CallState.Waiting])) {
            if (localCall.checkState(Enums.CallState.Delivered)) {
              // This is for outgoing call when the other participant answers the call.
              localCall.setState(Enums.CallState.Waiting);
            } else if (localCall.conferenceCall && localCall.sessionCtrl.isConnected()) {
              // This is for conference call when a second participant joins the call,
              // the call state of the first one should be changed to active.
              localCall.setState(Enums.CallState.Active);
            }
            localCall.activeClient = null;
            processReplacedCall(localCall, true);
          }

          var normalizedParticipant = normalizeApiParticipant(evt.participant);
          var pState = normalizedParticipant.muted ? Enums.ParticipantState.Muted : Enums.ParticipantState.Active;
          if (!localCall.isTelephonyCall) {
            lookupParticipant(normalizedParticipant, localCall);
          }

          if (localCall.hasParticipant(userId)) {
            var updatedParticipant = updateParticipantInCallObj(localCall, normalizedParticipant, pState);
            if (updatedParticipant) {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/updated event');
              PubSubSvc.publish('/call/participant/updated', [localCall.callId, updatedParticipant]);
            }
          } else {
            var addedParticipant = addParticipantToCallObj(localCall, normalizedParticipant, pState);
            if (addedParticipant) {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/added event');
              PubSubSvc.publish('/call/participant/added', [localCall.callId, addedParticipant]);
            }
          }

          var joinDataWrapper = evt.userEnteredStage ? { userEnteredStage: evt.userEnteredStage } : null;
          var participant = localCall.getParticipant(normalizedParticipant.userId);
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/joined event');
          PubSubSvc.publish('/call/participant/joined', [localCall, participant, joinDataWrapper]);
          simulateActiveSpeakers();

          // Update the call's media type
          localCall.updateMediaType();

          // If recorded user joined call, update recording info
          if (!localCall.isTestCall && localCall.recording.recordingUserId === participant.userId) {
            localCall.recording.recordingUser = participant;
          }

          publishCallState(localCall);
        });
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTC.PARTICIPANT_JOINED event. ', e);
      }
    });

    _clientApiHandler.on('RTCSession.SESSION_MOVED', function (evt) {
      try {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.SESSION_MOVED');

        // Is this event for a pending invite
        if (_pendingInvites[evt.oldRtcSessionId]) {
          // Yes, remove the pending invite. Still unable to process the SESSION_MOVED
          LogSvc.info('[UnifyPhoneCallControlSvc]: Event is for a pending INVITE. Ignore it.');
          delete _pendingInvites[evt.oldRtcSessionId];
          return;
        }

        var call = findCall(evt.oldRtcSessionId);
        if (!call) {
          LogSvc.info('[UnifyPhoneCallControlSvc]: Event is not for local or remote call. Ignore it.');
          return;
        }

        // Get old and new conversations
        var oldConversation = getConversation(evt.oldConversationId);
        var newConversation = getConversation(evt.newConversationId);
        if (!oldConversation || !newConversation) {
          // Wait for conversation service to load the conversation data
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Could not find corresponding conversations');
          return;
        }

        $rootScope.$apply(function () {
          updateCall(newConversation, oldConversation, call);

          if (call.isRemote) {
            // Session Moved for Remote Call
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Session Moved for Remote Call');
            return;
          }

          // Add New Participants to the call if localUser is the originator of the session move
          if (newConversation.creatorId === $rootScope.localUser.userId) {
            var oldConvParticipantsHash = {};
            oldConversation.participants.forEach(function (participant) {
              oldConvParticipantsHash[participant.userId] = true;
            });
            newConversation.participants.forEach(function (participant) {
              if (!oldConvParticipantsHash[participant.userId]) {
                addParticipant(call, participant);
              }
            });
          }
        });
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTC.SESSION_MOVED event. ', e);
      }
    });

    _clientApiHandler.on('RTCCall.INVITE', function (evt) {
      if (!_conversationLoaded) {
        LogSvc.info('[UnifyPhoneCallControlSvc]: Conversations are not loaded. Do not process the INVITE event.');
        return;
      }
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCCall.INVITE');
      handleInviteEvent(evt);
    });

    _clientApiHandler.on('RTCSession.PARTICIPANT_LEFT', function (evt) {
      $rootScope.$apply(function () {
        try {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.PARTICIPANT_LEFT');
          var isLocalUser = $rootScope.localUser.userId === evt.userId;

          var localCall = findLocalCallByCallId(evt.sessionId);

          if (isLocalUser) {
            if (localCall && !localCall.isDirect && !localCall.isEstablished() && !_negotiationFailureCauses.includes(evt.cause)) {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Ignore PARTICIPANT_LEFT event for non-established group calls');
              return;
            }
            processLocalUserLeftEvt(evt, localCall);
          }

          var activeRemoteCall = getActiveRemoteCall(evt.sessionId);
          if (activeRemoteCall) {
            LogSvc.info('[UnifyPhoneCallControlSvc]: Event is for active remote call.');
            if (isLocalUser) {
              changeRemoteCallToStarted(activeRemoteCall);
            }
            return;
          }

          if (!localCall) {
            processParticipantLeftForAlertingCall(evt);
            return;
          }

          if (localCall.isTestCall) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Ignore PARTICIPANT_LEFT event for test calls');
            return;
          }

          if (localCall.isDirect && !localCall.isDirectUpgradedToConf) {
            if (
              !isLocalUser &&
              (evt.cause === Constants.RTCSessionParticipantLeftCause.CONNECTION_LOST || evt.cause === Constants.RTCSessionParticipantLeftCause.STREAM_LOST)
            ) {
              localCall.setParticipantState(evt.userId, Enums.ParticipantState.ConnectionLost);
            } else {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Ignore PARTICIPANT_LEFT event for direct calls');
            }
            return;
          }

          // This is a local GROUP session.
          var userId = evt.userId;
          var conversation = getConversation(localCall.convId);
          if (!isLocalUser) {
            if (evt.cause === Constants.RTCSessionParticipantLeftCause.MAX_PARTICIPANTS_REACHED) {
              // Participant tried to join, but couldn't due to limitations.
              var isConvUser = conversation.participants.some(function (participant) {
                return participant.userId === userId;
              });
              var sendEventCb = function (err, user) {
                if (err) {
                  return;
                }
                LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/rejected event');
                PubSubSvc.publish('/call/participant/rejected', [localCall.callId, user]);
              };
              // If the user is in conversation, at first get the participant data.
              // Otherwise the participant is a guest, send the event without user data.
              isConvUser ? UserSvc.getUserById(userId, sendEventCb) : sendEventCb();
              return;
            }

            // Remove the leaving participant
            removeCallParticipant(localCall, userId, evt.cause);
          } else {
            addRemoteCall(conversation);
            if (evt.cause === Constants.RTCSessionParticipantLeftCause.REMOVED) {
              var requester = localCall.getParticipant(evt.requesterId);
              PubSubSvc.publish('/call/droppedRemotely', [localCall, requester]);
            } else if (_negotiationFailureCauses.includes(evt.cause)) {
              handleRtcError(localCall, { error: 'res_CallMediaFailed' });
              return;
            }
            terminateCall(localCall, Enums.CallServerTerminatedReason.SERVER_ENDED_PRFX + evt.cause);
          }
        } catch (e) {
          LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCSession.PARTICIPANT_LEFT event. ', e);
        }
      });
    });

    _clientApiHandler.on('RTCCall.SDP_FAILED', function (evt) {
      try {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCCall.SDP_FAILED with cause =', evt.cause);
        var localCall = findLocalCallByCallId(evt.sessionId);
        if (!localCall) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Unexpected event. There is no local call');
          return;
        }

        if (_clientApiHandler.clientId !== evt.clientID) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Event is not for this client');
          return;
        }

        if (!localCall.instanceId) {
          localCall.setInstanceId(evt.instanceId);
        }

        if (evt.cause === Constants.SdpFailedCause.SESSION_TERMINATED) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: SDP_FAILED due to session terminated. Ignore');
          return;
        }

        $rootScope.$apply(function () {
          if (!localCall.isEstablished()) {
            if (evt.cause === Constants.SdpFailedCause.MAX_PARTICIPANTS_REACHED) {
              handleRtcError(localCall, { error: 'res_JoinRTCSessionFailedPermission' });
            } else if (!localCall.isDirect || !localCall.isCallOut) {
              // We won't get an INVITE_FAILED for these scenarios, so we have to
              // terminate the call here
              if (evt.cause === Constants.SdpFailedCause.SESSION_STARTED_FAILED) {
                terminateCall(localCall, Enums.CallServerTerminatedReason.SERVER_ENDED_PRFX + evt.cause);
              } else {
                leaveCall(localCall, null, Enums.CallClientTerminatedReason.REQUEST_TO_SERVER_FAILED);
              }
            }
          } else {
            localCall.sessionCtrl.renegotiationFailed('SDP failed received');
          }
        });
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCCall.SDP_FAILED event. ', e);
      }
    });

    _clientApiHandler.on('RTCSession.SESSION_TERMINATED', function (evt) {
      try {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.SESSION_TERMINATED');

        if (_sessionHash[evt.sessionId]) {
          delete _sessionHash[evt.sessionId];
        }
        // Is this event for a pending invite
        if (_pendingInvites[evt.sessionId]) {
          // Yes, remove the pending invite. Still unable to process the SESSION_TERMINATED
          LogSvc.info('[UnifyPhoneCallControlSvc]: Event is for a pending INVITE. Ignore it.');
          delete _pendingInvites[evt.sessionId];
          return;
        }

        if (_pendingRemoteCalls[evt.sessionId]) {
          LogSvc.info('[UnifyPhoneCallControlSvc]: Aborting pending creation of remote call for rtcSessionId=', evt.sessionId);
          delete _pendingRemoteCalls[evt.sessionId];
        }

        var call = findCall(evt.sessionId);
        if (!call) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Could not find call with rtcSessionId =', evt.sessionId);
          return;
        }

        if (call.terminateTimer) {
          LogSvc.info('[UnifyPhoneCallControlSvc]: Call is already being terminated. Ignore event');
          return;
        }

        if (call.isTelephonyCall && _handoverTimer) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Call is being moved to another device, keep showing');
          call.setAtcHandoverInProgress();
          $timeout.cancel(_handoverTimer);
          _handoverTimer = null;
        }

        $rootScope.$apply(function () {
          var cause = evt.cause;
          LogSvc.info('[UnifyPhoneCallControlSvc]: Publish /call/terminated. cause = ', cause);
          PubSubSvc.publish('/call/terminated', [call, cause]);
          terminateCall(call, Enums.CallServerTerminatedReason.SERVER_ENDED_PRFX + cause);
          if ($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiVB) {
            var otherCall = call.isOsBizFirstCall ? _that.findOsBizSecondCall() : _that.findOsBizFirstCall();
            if (otherCall && !otherCall.checkState(Enums.CallState.Terminated)) {
              PubSubSvc.publish('/call/terminated', [otherCall]);
              terminateCall(otherCall);
            }
          }
        });
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCSession.SESSION_TERMINATED event. ', e);
      }
    });

    _clientApiHandler.on('RTCCall.INVITE_CANCEL', function (evt) {
      try {
        if (_clientApiHandler.clientId === evt.clientID) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Ignore own INVITE_CANCEL');
          return;
        }

        // Is this event for a pending invite
        if (_pendingInvites[evt.sessionId]) {
          // Yes, remove the pending invite. Still unable to process the iNVITE_CANCEL
          LogSvc.info('[UnifyPhoneCallControlSvc]: Event is for a pending INVITE. Ignore it.');
          delete _pendingInvites[evt.sessionId];
          return;
        }

        $rootScope.$apply(function () {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCCall.INVITE_CANCEL with cause: ', evt.cause);
          var alertingCall = getIncomingCall(evt.sessionId);
          if (!alertingCall) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: There is no corresponding alerting call');
            if (evt.cause === Constants.InviteCancelCause.ACCEPT) {
              var existingCall = findCall(evt.sessionId);
              if (existingCall && existingCall.isRemote) {
                LogSvc.debug('[UnifyPhoneCallControlSvc]: There is a remote call, probably ringing timeout or another client called in');
                existingCall.activeClient = { clientId: evt.clientID }; // Should be updated by subsequent SESSION_UPDATED
                existingCall.setState(Enums.CallState.ActiveRemote);
                addActiveRemoteCall(existingCall);
                publishCallState(existingCall);
              }
            }
            return;
          }

          if (!alertingCall.isDirect || evt.cause === Constants.InviteCancelCause.ACCEPT) {
            var conversation = getConversation(alertingCall.convId);
            var activeClient = evt.cause === Constants.InviteCancelCause.ACCEPT ? { clientId: evt.clientID } : null;
            addRemoteCall(conversation, activeClient, null, evt.sessionId, function (call) {
              if (activeClient && call.isTelephonyCall) {
                setCallPeerUser(call, alertingCall.peerUser.phoneNumber, null, alertingCall.peerUser.displayName);
              }
            });
          }
          var reason = Enums.CallClientTerminatedReason.ANOTHER_CLIENT_REJECTED;
          if (evt.cause === Constants.InviteCancelCause.ACCEPT) {
            reason = Enums.CallClientTerminatedReason.ANOTHER_CLIENT_ANSWERED;
          } else if (evt.cause === Constants.InviteCancelCause.REVOKED) {
            reason = Enums.CallClientTerminatedReason.ENDED_BY_ANOTHER_USER;
          }
          terminateCall(alertingCall, reason);
        });
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCCall.INVITE_CANCEL event. ', e);
      }
    });

    _clientApiHandler.on('RTCCall.INVITE_FAILED', function (evt) {
      try {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCCall.INVITE_FAILED');
        var localCall = findLocalCallByCallId(evt.sessionId);
        if (!localCall) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Unexpected event. There is no local call');
          return;
        }
        if (!localCall.isDirect) {
          var callParticipant = localCall.getParticipant(evt.userId);
          if (evt.to) {
            callParticipant.displayName = evt.to.displayName;
            var updatedParticipant = updateParticipantInCallObj(localCall, callParticipant);
            if (updatedParticipant) {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/updated event');
              PubSubSvc.publish('/call/participant/updated', [localCall.callId, updatedParticipant]);
            }
          }
          if (callParticipant && callParticipant.isActive()) {
            // This may happen as a result of race condition
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Unexpected event. participant already connected: ', evt.userId);
            return;
          }
        }

        if (evt.clientID && _clientApiHandler.clientId !== evt.clientID) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Event is not for this client: ', _clientApiHandler.clientId);
          return;
        }

        if (localCall.isTelephonyCall && evt.to) {
          var displayName = evt.to.displayName ? evt.to.displayName : localCall.peerUser.displayName;
          setCallPeerUser(localCall, evt.to.phoneNumber, evt.to.fullyQualifiedNumber, displayName, evt.to.userId);
        }

        $rootScope.$apply(function () {
          var call = localCall;
          var cause = evt.cause;
          var userId = evt.userId;
          var pcState;
          switch (cause) {
            case Constants.InviteFailedCause.NOT_REACHABLE:
              pcState = Enums.ParticipantState.Offline;
              // NGTC-3452: In case of transfer on ringing, we don't want to show 'Call couldn't connect' message
              if (call.consultation !== false || call.atcCallInfo.cstaState.name !== 'Delivered') {
                call.isDirect && call.setState(Enums.CallState.Failed);
              }
              break;
            case Constants.InviteFailedCause.BUSY:
              pcState = Enums.ParticipantState.Busy;
              call.isDirect && call.setState(Enums.CallState.Busy);
              break;
            case Constants.InviteFailedCause.DECLINE:
              pcState = Enums.ParticipantState.Declined;
              call.isDirect && call.setState(Enums.CallState.Declined);
              break;
            case Constants.InviteFailedCause.TIMEOUT:
              pcState = Enums.ParticipantState.Timeout;
              call.isDirect && call.setState(Enums.CallState.NotAnswered);
              break;
            case Constants.InviteFailedCause.TEMPORARILY_UNAVAILABLE:
            case Constants.InviteFailedCause.INVALID_NUMBER:
              pcState = Enums.ParticipantState.Busy;
              call.isDirect && call.setState(Enums.CallState.Failed);
              break;
            case Constants.InviteFailedCause.REVOKED:
              pcState = Enums.ParticipantState.Removed;
              call.isDirect && call.setState(Enums.CallState.Failed);
              break;
            default:
              LogSvc.warn('[UnifyPhoneCallControlSvc]: Unexpected RTCCall.INVITE_FAILED cause =', evt.cause);
              pcState = Enums.ParticipantState.Busy;
              call.isDirect && call.setState(Enums.CallState.Failed);
              break;
          }

          if (call.isDirect) {
            call.setParticipantState(userId, pcState);
            publishCallState(call);
            if (call.hasVideo()) {
              call.removeParticipant(userId);
            }
            leaveCall(call, null, Enums.CallServerTerminatedReason.SERVER_ENDED_PRFX + cause, true);
          } else {
            call.setParticipantState(userId, pcState);
            var removedParticipant = call.removeParticipant(userId);
            if (removedParticipant) {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/removed event');
              PubSubSvc.publish('/call/participant/removed', [call.callId, removedParticipant]);

              if (Utils.isEmptyArray(call.participants)) {
                // Everyone left the conference
                if (call.conferenceCall) {
                  // Conference is still running, make sure Enums.CallState is Waiting
                  if (!call.checkState(Enums.CallState.Waiting)) {
                    call.setState(Enums.CallState.Waiting);
                    publishCallState(call);
                  }
                } else {
                  call.setState(Enums.CallState.NotAnswered);
                  publishCallState(call);
                  leaveCall(null, null, Enums.CallServerTerminatedReason.SERVER_ENDED_PRFX + cause, true);
                }
              }
            }
          }
        });
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTC.INVITE_FAILED event. ', e);
      }
    });

    _clientApiHandler.on('RTCSession.ACTIVE_SPEAKER', onActiveSpeakerEvent);

    _clientApiHandler.on('RTCSession.VIDEO_ACTIVE_SPEAKER', onActiveVideoSpeakerEvent);

    _clientApiHandler.on('RTCCall.CHANGE_MEDIA_TYPE_REQUESTED', function (evt) {
      try {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.CHANGE_MEDIA_TYPE_REQUESTED');
        var alertingCall = getIncomingCall(evt.sessionId);
        if (alertingCall) {
          sendChangeMediaReject(evt.sessionId, evt.transactionId);
          return;
        }
        var localCall = findLocalCallByCallId(evt.sessionId);
        if (!localCall || localCall.isRemote) {
          LogSvc.info('[UnifyPhoneCallControlSvc]: Event is not for local call. Ignore it.');
          return;
        }

        if (evt.clientID && _clientApiHandler.clientId !== evt.clientID) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Event is not for this client: ', _clientApiHandler.clientId);
          return;
        }
        if (localCall.activeClient) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Pull in progress. leave it for active client to handle');
          return;
        }
        if ((!localCall.isEstablished() && localCall.direction === Enums.CallDirection.INCOMING) || !localCall.isDirect) {
          sendChangeMediaReject(localCall.callId, evt.transactionId);
          return;
        }

        $rootScope.$apply(function () {
          if (!evt.sdp) {
            sendChangeMediaReject(localCall.callId, evt.transactionId);
            return;
          }

          var isNoOffer = sdpParser.isNoOfferSdp(evt.sdp);
          var sessionCtrl = localCall.sessionCtrl;

          var onSdp = function (sdpEvt) {
            sessionCtrl.onSessionDescription = null;

            var changeMediaAcceptData = {
              rtcSessionId: localCall.callId,
              sdp: sdpEvt.sdp,
              transactionId: evt.transactionId
            };
            _clientApiHandler.changeMediaAccept(changeMediaAcceptData, function (err) {
              $rootScope.$apply(function () {
                localCall.clearTransactionId();
                if (err) {
                  LogSvc.error('[UnifyPhoneCallControlSvc]: changeMediaAccept sdp answer sending error');
                } else {
                  LogSvc.debug('[UnifyPhoneCallControlSvc]: changeMediaAccept sdp answer sent');
                }
              });
            });
          };

          var processEvent = function () {
            localCall.setTransactionId(evt.transactionId);

            if (localCall.isTelephonyCall && localCall.atcAdvancing && evt.sdp.type !== 'nooffer') {
              // If PBX is 4K, one change_media_request without SDP is received when client answers after advancing the call.
              // OSV sends two change_media_request(s) with SDP.
              localCall.sessionCtrl.setIgnoreTurnOnNextPC();
            }

            LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/changeMediaTypeRequested event');
            PubSubSvc.publish('/call/changeMediaTypeRequested', [localCall.callId, isNoOffer]);

            localCall.atcAdvancing = false;
            getTurnCredentials(localCall)
              .then(function (turnCredentials) {
                sessionCtrl.setTurnCredentials(turnCredentials);
                if (isNoOffer) {
                  var data = {
                    callId: localCall.callId,
                    transactionId: evt.transactionId,
                    mediaType: localCall.localMediaType,
                    hosted: evt.hosted,
                    replaces: evt.replaces,
                    externallyTriggered: true
                  };
                  changeMediaType(data, function (err) {
                    if (err) {
                      LogSvc.warn('[UnifyPhoneCallControlSvc]: Failed to renegotiate the media');
                    }
                  });
                } else {
                  localCall.lastMediaType = localCall.mediaType;
                  sessionCtrl.onSessionDescription = onSdp;
                  sessionCtrl.setRemoteDescription(evt.sdp, function (err) {
                    if (err) {
                      LogSvc.error('[UnifyPhoneCallControlSvc]: Error setting remote description: ', err);
                      sessionCtrl.onSessionDescription = null;
                      if (localCall.transactionId === evt.transactionId) {
                        sendChangeMediaReject(localCall.callId, evt.transactionId);
                      } else {
                        LogSvc.warn('[UnifyPhoneCallControlSvc]: TransactionId: ' + evt.transactionId + ' has already been completed.');
                      }
                    }
                  });
                }
              })
              .catch(function () {
                sendChangeMediaReject(localCall.callId, evt.transactionId);
              });
          };

          if (sessionCtrl.isConnStable()) {
            processEvent();
          } else if (localCall.isTelephonyCall && sessionCtrl.canAbortRenegotiation()) {
            // We are already in the middle of creating a new SDP Offer
            if (isNoOffer) {
              LogSvc.info('[UnifyPhoneCallControlSvc]: Client has already started a renegotiation and SDP Offer is pending');
              localCall.pendingTransactionId = evt.transactionId;
              localCall.setTransactionId(evt.transactionId);
              localCall.atcAdvancing = false;
            } else {
              LogSvc.warn('[UnifyPhoneCallControlSvc]: Abort current renegotiation so we can process the CHANGE_MEDIA_TYPE_REQUESTED event');
              sessionCtrl.abortPendingNegotiation(function () {
                LogSvc.info('[UnifyPhoneCallControlSvc]: Renegotiation was aborted. Process the pending CHANGE_MEDIA_TYPE_REQUESTED event.');
                processEvent();
              });
            }
          } else {
            sendChangeMediaReject(localCall.callId, evt.transactionId);
          }
        });
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCSession.CHANGE_MEDIA_TYPE_REQUESTED event. ', e);
      }
    });

    _clientApiHandler.on('RTCCall.CHANGE_MEDIA_TYPE_FORCED', function (evt) {
      try {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.CHANGE_MEDIA_TYPE_FORCED');
        var localCall = findLocalCallByCallId(evt.sessionId);
        if (!localCall) {
          LogSvc.info('[UnifyPhoneCallControlSvc]: Event is not for local call. Ignore it.');
          return;
        }
        if (localCall.isDirect) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Event is not applicable for direct calls. Ignore it.');
          return;
        }
        if (evt.clientID && _clientApiHandler.clientId !== evt.clientID) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Event is not for this client: ', _clientApiHandler.clientId);
          return;
        }
        if (localCall.activeClient) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Pull in progress. leave it for active client to handle');
          return;
        }
        if (evt.removeMediaTypes) {
          var mediaToChange = null;
          if (evt.removeMediaTypes.includes(Constants.RealtimeMediaType.AUDIO)) {
            mediaToChange = { audio: false };
          }
          if (evt.removeMediaTypes.includes(Constants.RealtimeMediaType.VIDEO)) {
            mediaToChange = mediaToChange || {};
            mediaToChange.video = false;
          }
          if (mediaToChange) {
            $rootScope.$apply(function () {
              addRemoveMedia(localCall.callId, mediaToChange, 'changeMediaForced').catch();
            });
          }
        }
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCSession.CHANGE_MEDIA_TYPE_FORCED event. ', e);
      }
    });

    _clientApiHandler.on('RTCCall.RTC_QUALITY_RATING_EVENT', function (evt) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCCall.RTC_QUALITY_RATING_EVENT');
      var call = _primaryLocalCall || _secondaryLocalCall;
      if (call) {
        LogSvc.info('[UnifyPhoneCallControlSvc]: Postpone call quality rating during call');
        call.ratingEvent = evt;
        return;
      }
      $rootScope.$apply(function () {
        publishShowRatingDialog(evt);
      });
    });

    _clientApiHandler.on('RTCSession.PARTICIPANT_UPDATED', function (evt) {
      try {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.PARTICIPANT_UPDATED');

        var localCall = findLocalCallByCallId(evt.sessionId);
        var call = getActiveRemoteCall(evt.sessionId) || localCall;
        if (call && call.callId === evt.sessionId && call.isTelephonyCall && !call.atcCallInfo && $rootScope.localUser.userId !== evt.participant.userId) {
          setCallPeerUser(call, evt.participant.phoneNumber, null, evt.participant.userDisplayName);
        }

        if (!localCall) {
          LogSvc.info('[UnifyPhoneCallControlSvc]: Event is not for local call. Ignore it.');
          return;
        }

        $rootScope.$apply(function () {
          var userId = evt.participant.userId;
          var muted = evt.participant.muted;
          if ($rootScope.localUser.userId === userId) {
            if (localCall.remotelyMuted !== muted) {
              localCall.remotelyMuted = muted;

              publishMutedEvent(localCall, evt.mutedBy);
            }
            return;
          }

          if (!localCall.isEstablished() && localCall.direction === Enums.CallDirection.INCOMING) {
            // Ignore the UPDATED events for incoming alerting calls
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Ignore UPDATED event for incoming alerting calls');
            return;
          }

          var callParticipant = localCall.getParticipant(evt.participant.userId);
          if (!callParticipant) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Ignore UPDATED event, can not find participant to update for userId: ', userId);
            return;
          }
          var normalizedParticipant = normalizeApiParticipant(evt.participant);
          var pState = normalizedParticipant.muted ? Enums.ParticipantState.Muted : Enums.ParticipantState.Active;

          var mutedUpdate = callParticipant.muted !== normalizedParticipant.muted;
          var mediaUpdate = !callParticipant.hasSameMediaType(normalizedParticipant) || callParticipant.streamId !== normalizedParticipant.streamId;

          var oldMediaType = callParticipant.mediaType;

          var updatedParticipant = updateParticipantInCallObj(localCall, normalizedParticipant, pState);
          if (updatedParticipant) {
            lookupParticipant(updatedParticipant, localCall);

            if (mediaUpdate) {
              // Update the call's media type
              localCall.updateMediaType();
            }

            if (mediaUpdate || mutedUpdate) {
              LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/participant/updated event');
              PubSubSvc.publish('/call/participant/updated', [localCall.callId, updatedParticipant, oldMediaType]);
            }
          }
          publishCallState(localCall);
        });
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCSession.PARTICIPANT_UPDATED event. ', e);
      }
    });

    _clientApiHandler.on('RTCSession.SESSION_RECORDING_INFO', function (evt) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.SESSION_RECORDING_INFO');
      var localCall = findLocalCallByCallId(evt.sessionId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: Unexpected event. There is no local call');
        return;
      }
      $rootScope.$apply(function () {
        setRecordingInfoData(localCall, evt.recordingInfo);
      });
    });

    _clientApiHandler.on('RTCSession.SESSION_TERMINATION_TIMER_STARTED', function (evt) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.SESSION_TERMINATION_TIMER_STARTED');
      var localCall = findLocalCallByCallId(evt.sessionId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: Unexpected event. There is no local call');
        return;
      }
      $rootScope.$apply(function () {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/sessionTerminateTimerChanged (started)');
        PubSubSvc.publish('/call/sessionTerminateTimerChanged', [localCall.callId, evt.endTime]);
      });
    });

    _clientApiHandler.on('RTCSession.SESSION_TERMINATION_TIMER_CANCELLED', function (evt) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.SESSION_TERMINATION_TIMER_CANCELLED');
      var localCall = findLocalCallByCallId(evt.sessionId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: Unexpected event. There is no local call');
        return;
      }
      $rootScope.$apply(function () {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/sessionTerminateTimerChanged (cancelled)');
        PubSubSvc.publish('/call/sessionTerminateTimerChanged', [localCall.callId, null]);
      });
    });

    _clientApiHandler.on('RTCCall.PROGRESS', function (evt) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCCall.PROGRESS');

      var localCall = findLocalCallByCallId(evt.sessionId);
      if (!localCall) {
        LogSvc.info('[UnifyPhoneCallControlSvc]: Event is not for local or remote call. Ignore it.');
        return;
      }

      if (localCall.isDirect) {
        if (!localCall.instanceId) {
          // Make sure we always have the instanceId set in our call object (important for Qos)
          localCall.setInstanceId(evt.instanceId);
        }
        if (!evt.sdp && !localCall.earlyMedia && evt.progressType === Constants.RTCProgressType.ALERTING) {
          if (localCall.isTelephonyCall) {
            // Telephony calls might have media renegotiations, signal the session controller to
            // pre-allocate a new peer connection
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Pre-allocate a new peer connection in case of immediate renegotiations');
            localCall.sessionCtrl.prepareForRenegotiation();
          }
          localCall.receivedAlerting = true;
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /sound/call/alerting/started, call ID: ', localCall.callId);
          PubSubSvc.publish('/sound/call/alerting/started', [localCall]);
        } else if (evt.sdp && evt.progressType !== Constants.RTCProgressType.EARLY_CONNECT) {
          localCall.earlyMedia = true;
          if (localCall.receivedAlerting) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /sound/call/alerting/stopped, call ID: ', localCall.callId);
            PubSubSvc.publish('/sound/call/alerting/stopped', [localCall]);
          }
        }
      }

      if (!localCall.checkState([Enums.CallState.Initiated, Enums.CallState.Delivered])) {
        LogSvc.info('[UnifyPhoneCallControlSvc]: Ignore PROGRESS. Call state =', localCall.state.name);
        return;
      }

      if (evt.to && localCall.isTelephonyCall && !$rootScope.localUser.isATC) {
        // For ATC users the display will be updated by the CSTA Delivered event
        setCallPeerUser(localCall, evt.to.phoneNumber, evt.to.fullyQualifiedNumber, evt.to.displayName, evt.to.userId);
      }

      try {
        $rootScope.$apply(function () {
          if (evt.sdp && (evt.sdp.type === 'answer' || evt.sdp.type === 'pranswer')) {
            // This is an early media (pranswer) indication.
            evt.sdp.type = 'pranswer';
            localCall.isPranswer = true;
            localCall.sessionCtrl.setRemoteDescription(evt.sdp, function (err) {
              if (err) {
                LogSvc.error('[UnifyPhoneCallControlSvc]: Error setting remote description: ', err);
                localCall.setDisconnectCause(
                  Constants.DisconnectCause.REMOTE_SDP_FAILED,
                  'type=' + evt.sdp.type + ' origin=' + sdpParser.getOrigin(evt.sdp.sdp)
                );
                leaveCall(localCall, null, Enums.CallClientTerminatedReason.SET_REMOTE_SDP_FAILED);
              }
            });
          }
          localCall.setState(Enums.CallState.Delivered);
          publishCallState(localCall);
        });
      } catch (e) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Exception handling RTCCall.PROGRESS event.', e);
      }
    });

    _clientApiHandler.on('RTCSession.LIVE_TRANSCRIPTION', function (evt) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Received RTCSession.LIVE_TRANSCRIPTION');
      var localCall = findLocalCallByCallId(evt.sessionId);
      if (!localCall) {
        LogSvc.info('[UnifyPhoneCallControlSvc]: Event is not for local call. Ignore it.');
        return;
      }

      if (!evt.text) {
        // Transcription has no text !?!?!
        return;
      }

      $rootScope.$apply(function () {
        // Add timestamp and user object to transcription data
        evt.timestamp = Date.now();
        evt.user = evt.userId === $rootScope.localUser.userId ? $rootScope.localUser : localCall.getParticipant(evt.userId);

        var transcription = localCall.transcription;
        if (evt.intermediate) {
          LogSvc.info('[UnifyPhoneCallControlSvc]: Handle intermediate transcription from ', evt.userId);
          evt.text += ' ...';
          transcription.intermediate = transcription.intermediate || [];
          // See if there is already an intermediate transcription for this user
          var found = transcription.intermediate.some(function (data) {
            if (data.userId === evt.userId) {
              // Update existing intermediate transcription
              data.timestamp = evt.timestamp;
              data.text = evt.text;
              return true;
            }
            return false;
          });
          if (!found) {
            // Add new intermediate transcription to the end
            transcription.intermediate.push(evt);
          }
        } else {
          LogSvc.info('[UnifyPhoneCallControlSvc]: Handle final transcription from ', evt.userId);
          // Remove any intermediate transcription from the same user
          transcription.intermediate &&
            transcription.intermediate.some(function (data, idx) {
              if (data.userId === evt.userId) {
                transcription.intermediate.splice(idx, 1);
                return true;
              }
              return false;
            });
          transcription.history = transcription.history || [];
          transcription.history.push(evt);
          if (transcription.history.length > MAX_TRANSCRIPTIONS) {
            // Discard the oldest transcription
            transcription.history.shift();
          }
        }
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/liveTranscription event');
        PubSubSvc.publish('/call/liveTranscription', [localCall, evt]);
      });
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.simulateRtcSessionEvent = function (rtcSession, evtForLocalCall) {
      var evt = {
        type: Constants.ContentType.RTC_SESSION,
        rtcSession: rtcSession
      };
      if (evtForLocalCall) {
        var activeCall = _that.getActiveCall();
        if (activeCall) {
          evt.rtcSession.sessionId = activeCall.callId;
        }
      }
      _clientApiHandler.simulateEvent(evt);
    };

    /**
     * Used by SDK to initialize active sessions.
     * Returns a promise that is fullfilled when all sessions have been processed
     * and the different calls arrays are populated.
     */
    this.initActiveSessionsForSdk = function () {
      _conversationLoaded = true;
      return initActiveSessions();
    };

    /**
     * Used by unit tests to place the UnifyPhoneCallControlSvc in a state where it can
     * start processing the RTC events.
     */
    this.initForUnitTests = function () {
      _conversationLoaded = true;
    };

    /**
         * Set indication of whether incoming remote video should be disabled by default for
         * new incoming and outgoing UnifyPhone calls.
    .    *
         * @param {boolean} value true indicates that incoming video is disabled by default.
         */
    this.setDisableRemoteVideoByDefault = function (value) {
      LogSvc.info('[UnifyPhoneCallControlSvc] Setting _disableRemoteVideoByDefault to ', !!value);
      _disableRemoteVideoByDefault = !!value;
    };

    /**
         * Get indication of whether incoming remote video is currently disabled by default.
    .    *
         * @returns {boolean} true indicates that incoming video is disabled by default.
         */
    this.getDisableRemoteVideoByDefault = function () {
      return _disableRemoteVideoByDefault;
    };

    /**
         * Set indication of whether Client Diagnostics are enabled/disabled
    .
         *
         * @param {boolean} value true=disabled; false=enabled
         */
    this.setClientDiagnosticsDisabled = function (value) {
      _clientDiagnosticsDisabled = value;
    };

    /**
     * Get all of the calls.
     *
     * @returns {Array} An array of Call object.
     */
    this.getCalls = function () {
      return _calls;
    };

    /**
     * Get all the phone calls
     *
     * @param {boolean} onlyLocal true=only local phone calls
     * @return {Array} An array of Call object
     */
    this.getPhoneCalls = function (onlyLocal) {
      return _calls.filter(function (call) {
        return call.isTelephonyCall && (!onlyLocal || !call.isRemote);
      });
    };

    this.getEstablishedLocalPhoneCalls = function () {
      return _calls.filter(function (call) {
        return call.isTelephonyCall && call.state.established && !call.isRemote;
      });
    };

    /**
     * Get the active local WebRTC call.
     *
     * @param {String} callId The ID of existing call or conference
     * @returns {LocalCall} The LocalCall object.
     */
    this.getActiveCall = function (callId) {
      if (callId) {
        return findLocalCallByCallId(callId);
      }
      if (_primaryLocalCall && _primaryLocalCall.replaces) {
        return _primaryLocalCall.replaces;
      }
      return _primaryLocalCall || null;
    };

    /**
     * Returns true if client has an active local WebRTC call.
     *
     * @returns {Boolean}
     */
    this.hasActiveCall = function () {
      return !!_primaryLocalCall;
    };

    /**
     * Checks if there is an active local WebRTC call being established or
     * in the middle of a media renegotiation.
     *
     * @param {String} callId The ID of existing call or conference
     * @returns {Booleam} True if there is a connection in progress.
     */
    this.isConnectionInProgress = function (callId) {
      var activeCall = _that.getActiveCall(callId);
      return !!activeCall && !activeCall.sessionCtrl.isConnStable();
    };

    /**
     * Get the ringing incoming WebRTC call,
     * null will be returned if there is no incoming call.
     *
     * @returns {LocalCall} The LocalCall object.
     */
    this.getIncomingCall = function () {
      return _incomingCalls[0] || null;
    };

    /**
     * Get the active remote WebRTC call.
     *
     * @returns {Array} The RemoteCall object array.
     */
    this.getActiveRemoteCall = function () {
      return _activeRemoteCalls;
    };

    /**
     * Join an existing group call or conference
     *
     * @param {String} callId The ID of existing group call or conference
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error
     */
    this.joinGroupCall = function (callId, mediaType, cb) {
      cb = cb || NOP;
      if (!callId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: joinGroupCall - Invalid Call Id');
        cb('Missing callId');
        return;
      }
      var call = findCall(callId);
      if (!call || !call.isRemote || call.state.name !== Enums.CallState.Started.name || !!call.activeClient) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: joinGroupCall invoked without a valid call');
        cb('Invalid call');
        return;
      }
      var conversation = getConversation(call.convId);
      if (!conversation || !conversation.call || conversation.call.callId !== call.callId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: joinGroupCall - Cannot find valid conversation');
        cb('Invalid conversation');
        return;
      }
      var options = createCallOptions({
        callOut: false,
        handover: false,
        joiningGroupCall: true
      });
      if (createLocalCall(conversation, mediaType, options, cb)) {
        checkMediaSourcesAndJoin(conversation, mediaType, options, cb);
      }
    };

    /**
     * Make a new outgoing call in an existing conversation.
     *
     * @param {String} convId The conversation ID for the conversation to start the call.
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error
     */
    this.makeCall = function (convId, mediaType, cb) {
      cb = cb || NOP;
      if (!convId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: makeCall - Invalid Conversation Id');
        cb('Missing convId');
        return;
      }
      var conversation = getConversation(convId);
      if (!conversation) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: makeCall - Cannot find conversation with id ', convId);
        cb('Conversation not found');
        return;
      }
      var callOptions = createCallOptions({ callOut: true, handover: false });
      if (createLocalCall(conversation, mediaType, callOptions, cb)) {
        checkMediaSourcesAndJoin(conversation, mediaType, callOptions, cb);
      }
    };

    this.ignorePickupCall = function () {
      if (!_callToPickup) {
        return;
      }
      LogSvc.info('[UnifyPhoneCallControlSvc]: Ignore pickup notification. callId = ', _callToPickup.callId);
      dismissNotification(_callToPickup);
      var conversation = getConversation(_callToPickup.convId);
      if (conversation && conversation.isTemporary) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /conversation/temporary/ended event');
        PubSubSvc.publish('/conversation/temporary/ended', [conversation]);
      }
      terminateCall(_callToPickup);
      LogSvc.info('[UnifyPhoneCallControlSvc]: Publish /call/terminated');
      PubSubSvc.publish('/call/terminated', [_callToPickup]);

      _callToPickup = null;
    };

    /**
     * Make a test call.
     *
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Function} onConversationCreated A callback function that is immediately invoked with the rtcSessionId for the test call.
     * @param {Function} onCallStarted A callback function when the call is started or in case there is an error.
     */
    this.makeEchoTestCall = function (mediaType, onConversationCreated, onCallStarted) {
      _that.makeEchoTestCallWithOptions(mediaType, null, onConversationCreated, onCallStarted);
    };

    /**
     * Make a test call.
     *
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Object} additionalCallOptions The additional call options object, e.g. {desiredRegion: 'eu'}.
     * @param {Function} onConversationCreated A callback function that is immediately invoked with the rtcSessionId for the test call.
     * @param {Function} onCallStarted A callback function when the call is started or in case there is an error.
     */
    this.makeEchoTestCallWithOptions = function (mediaType, additionalCallOptions, onConversationCreated, onCallStarted) {
      onConversationCreated = onConversationCreated || NOP;
      onCallStarted = onCallStarted || NOP;
      var testConvId = 'echo_' + $rootScope.localUser.clientId;

      var conversation = {
        creatorId: $rootScope.localUser.userId,
        convId: testConvId,
        rtcSessionId: 'echo_' + $rootScope.localUser.clientId,
        participants: [{ userId: $rootScope.localUser.userId }],
        testCall: true
      };
      conversation = Conversation.extend(conversation);
      onConversationCreated(conversation.rtcSessionId);

      // Wait a little before initiating the test call
      $timeout(function () {
        var options = createCallOptions({ callOut: false, handover: false }, additionalCallOptions);
        if (createLocalCall(conversation, mediaType, options, onCallStarted)) {
          joinSession(conversation, mediaType, options, onCallStarted);
        }
      }, 200);
    };

    /**
     * Make a new outgoing call to a given DN.
     *
     * @param {String} dialedDn  The dialed number.
     * @param {String} toName The display name of the called unifyPhone user
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error, executed when the call is successfully placed.
     */
    this.dialNumber = function (dialedDn, toName, mediaType, cb) {
      cb = cb || NOP;
      ConversationSvc.getTelephonyConversation(function (err, conversation) {
        if (err || !conversation) {
          cb(err);
        } else {
          var callOptions = createCallOptions({
            callOut: true,
            handover: false,
            dialedDn: dialedDn,
            toName: toName
          });
          if (createLocalCall(conversation, mediaType, callOptions, cb)) {
            checkMediaSourcesAndJoin(conversation, mediaType, callOptions, cb);
          }
        }
      });
    };

    /**
     * Make a new outgoing call to a given DN.
     *
     * @param {Object} [destination] Object The destination to be called.
     * @param {String} [destination.dialedDn] The dialed number.
     * @param {String} [destination.toName] The display name of the called unifyPhone user.
     * @param {String} [destination.userId] Id of the dialed user (if available). Otherwise null or empty string.
     * @param {Object} [destination.mediaType] The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error, executed when the call is successfully placed.
     */
    this.dialPhoneNumber = function (destination, cb) {
      cb = cb || NOP;
      destination = destination || {};
      ConversationSvc.getTelephonyConversation(function (err, conversation) {
        if (err || !conversation) {
          cb(err);
        } else {
          var options = createCallOptions({
            callOut: true,
            handover: false,
            dialedDn: destination.dialedDn,
            toName: destination.toName,
            userId: destination.userId,
            dtmfDigits: destination.dtmfDigits,
            sipHeaders: destination.sipHeaders
          });
          if (!canInitiateCall(conversation, options, cb)) {
            return;
          }
          if (_primaryLocalCall && _primaryLocalCall.isTelephonyCall && !_primaryLocalCall.isHolding()) {
            _that.holdCall(_primaryLocalCall.callId, function (holdErr) {
              if (holdErr) {
                cb && cb(holdErr);
              } else if (createLocalCall(conversation, destination.mediaType, options, cb)) {
                checkMediaSourcesAndJoin(conversation, destination.mediaType, options, cb);
              }
            });
            return;
          }
          if (createLocalCall(conversation, destination.mediaType, options, cb)) {
            checkMediaSourcesAndJoin(conversation, destination.mediaType, options, cb);
          }
        }
      });
    };

    /**
     * Start a conference in an existing group conversation.
     *
     * @param {String} convId The ID of existing group conversation to start the conference.
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error
     */
    this.startConference = function (convId, mediaType, cb) {
      _that.startConferenceWithOptions(convId, mediaType, null, cb);
    };

    /**
     * Start a conference in an existing group conversation.
     *
     * @param {String} convId The ID of existing group conversation to start the conference.
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Object} additionalCallOptions The additional call options object, e.g. {desiredRegion: 'eu'}.
     * @param {Function} cb A callback function replying with an error
     */
    this.startConferenceWithOptions = function (convId, mediaType, additionalCallOptions, cb) {
      mediaType = mediaType || { audio: true, video: false };
      cb = cb || NOP;
      if (!convId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: startConference - Invalid Conversation Id');
        cb('Missing convId');
        return;
      }
      var conversation = getConversation(convId);
      if (!conversation) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: startConference - Cannot find conversation with id ', convId);
        cb('Conversation not found');
        return;
      }
      if (conversation.type !== Constants.ConversationType.GROUP && conversation.type !== Constants.ConversationType.LARGE) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: startConference - Invalid conversation type');
        cb('Invalid conversation type');
        return;
      }

      var callOptions = createCallOptions({ callOut: false, handover: false }, additionalCallOptions);

      if (createLocalCall(conversation, mediaType, callOptions, cb)) {
        checkMediaSourcesAndJoin(conversation, mediaType, callOptions, cb);
      }
    };

    /**
     * Answer the incoming call. Any existing active call will be terminated.
     *
     * @param {String} callId The call ID of incoming call to be answered.
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error
     */
    this.answerCall = function (callId, mediaType, cb) {
      cb = cb || NOP;
      var incomingCall = null;
      var incomingCallIdx;
      _incomingCalls.some(function (call, idx) {
        if (callId === call.callId) {
          incomingCall = call;
          incomingCallIdx = idx;
          return true;
        }
        return false;
      });
      if (!incomingCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: answerCall - There is no alerting call');
        cb('No alerting call');
        return;
      }

      var normalizedMediaType, warning;
      function answerCallContinue() {
        if (!incomingCall.isPresent()) {
          cb('No call to answer');
          return;
        }
        _secondaryLocalCall = _primaryLocalCall;
        _primaryLocalCall = incomingCall;
        simulateActiveSpeakers();

        _incomingCalls.splice(incomingCallIdx, 1);
        dismissNotification(_primaryLocalCall);

        //Cancel ringing timer if running
        _primaryLocalCall.stopRingingTimer();

        _primaryLocalCall.setState(_primaryLocalCall.isATCCall ? Enums.CallState.Waiting : Enums.CallState.Answering);

        var conversation = getConversation(_primaryLocalCall.convId);
        if (!conversation || !conversation.call || conversation.call.callId !== _primaryLocalCall.callId) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: joinCall - Cannot find valid conversation');
          decline(_primaryLocalCall, { type: Constants.InviteRejectCause.BUSY }, false);
          cb('Conversation not found');
          return;
        }

        publishCallState(_primaryLocalCall);
        joinSessionCalled(conversation, normalizedMediaType, { warning: warning }, cb);
      }

      function getMediaSources() {
        checkMediaSources(mediaType, function (updatedMediaType, warn) {
          if (warn) {
            var conversation = ConversationSvc.getConversationFromCache(incomingCall.convId);
            if (conversation && conversation.isTelephony) {
              // Don't allow phone call to continue if we cannot access a microphone
              LogSvc.warn('[UnifyPhoneCallControlSvc]: Phone calls are not possible without a microphone. Do not answer call.');
              cb('res_AnswerCallFailedNoMic');
              return;
            }
          }
          normalizedMediaType = updatedMediaType;
          warning = warn;
          answerCallContinue();
        });
      }

      if (
        _primaryLocalCall &&
        _primaryLocalCall.isPresent() &&
        !incomingCall.replaces &&
        (!_primaryLocalCall.isTelephonyCall || !incomingCall.isTelephonyCall || !$rootScope.localUser.isRegisterTC)
      ) {
        leaveCall(_primaryLocalCall, null, Enums.CallClientTerminatedReason.USER_ENDED);
        if (_secondaryLocalCall) {
          leaveCall(_secondaryLocalCall, null, Enums.CallClientTerminatedReason.USER_ENDED);
        }
      } else if (_primaryLocalCall && _primaryLocalCall.isTelephonyCall && $rootScope.localUser.isRegisterTC && !_primaryLocalCall.isHolding()) {
        _that.holdCall(_primaryLocalCall.callId, function (err) {
          if (err) {
            cb(err);
          } else {
            getMediaSources();
          }
        });
        return;
      }
      getMediaSources();
    };

    /**
     * Send early media for the incoming call. This is only applicable for incoming audio-only telephony calls.
     *
     * @param {String} callId The call ID of incoming call to be procesed.
     * @returns {Promise} Promise resolved when operation is completed.
     */
    this.prepareEarlyMedia = function (callId) {
      return new $q(function (resolve, reject) {
        var incomingCall = _incomingCalls.find(function (call) {
          return callId === call.callId;
        });
        if (!incomingCall) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: prepareEarlyMedia - There is no alerting call');
          reject('No alerting call');
          return;
        }

        var sessionCtrl = incomingCall.sessionCtrl;
        if (!sessionCtrl.getWarmedUpSdp()) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: prepareEarlyMedia - Not warmed up session');
          reject('Not warmed up');
          return;
        }

        var onSdp = function (evt) {
          sessionCtrl.onSessionDescription = null;

          LogSvc.info('[UnifyPhoneCallControlSvc]: Save early media SDP for ANSWER: ', evt.sdp);
          incomingCall.earlyMediaSdp = evt.sdp;
        };

        sessionCtrl.onSessionDescription = onSdp;

        LogSvc.info('[UnifyPhoneCallControlSvc]: Start RTC session to send early media for callId ', incomingCall.callId);
        if (sessionCtrl.start({ audio: true }, null, true)) {
          incomingCall.startedEarlyMedia = true;
          resolve();
        }
      });
    };

    /**
     * End a local call.
     *
     * @param {String} callId The call ID of local call to be terminated.
     * @param {Function} cb A callback function replying with an error
     */
    this.endCall = function (callId, cb) {
      var cause = Constants.InviteRejectCause.DECLINE;
      var activeCall = _that.getActiveCall();
      if (activeCall && activeCall.callId === callId) {
        if (activeCall.isEstablished() || activeCall.direction === Enums.CallDirection.OUTGOING) {
          cause = null; // This will be defaulted to USER_ENDED
        }
      }
      _that.endCallWithCauseCode(callId, cause, cb);
    };

    /**
     * End a local call specifying the cause.
     *
     * @param {String} callId The call ID of local call to be terminated.
     * @param {String} cause Cause code for terminating the call. It can be either InviteRejectCause or CallClientTerminatedReason.
     * @param {Function} cb A callback function replying with an error
     */
    this.endCallWithCauseCode = function (callId, cause, cb) {
      var qosCause = Enums.CallClientTerminatedReason.USER_ENDED;
      var inviteRejectCause = Constants.InviteRejectCause.DECLINE;

      if (cause) {
        qosCause = cause;
        if (Constants.InviteRejectCause[cause]) {
          // cause is an InviteRejectCause value
          inviteRejectCause = cause;
        }
      }

      var localCall = findLocalCallByCallId(callId);
      if (localCall) {
        if (localCall.replaces && localCall.checkState(Enums.CallState.Answering)) {
          cb('Auto answer in progress');
        } else if (localCall.terminateTimer) {
          terminateCall(localCall, qosCause);
          // Only initiate timeout if there is a callback
          cb && $timeout(cb);
        } else {
          leaveCall(localCall, inviteRejectCause, qosCause, false, cb);
        }
      } else if (_primaryLocalCall && _primaryLocalCall.replaces && _primaryLocalCall.replaces.callId === callId) {
        leaveCall(_primaryLocalCall.replaces, inviteRejectCause, qosCause, false, cb);
      } else {
        var activeCall = findCall(callId);
        if (activeCall) {
          if (activeCall.terminateTimer) {
            terminateCall(activeCall);
            cb && $timeout(cb);
          } else {
            leaveCall(activeCall, inviteRejectCause, qosCause, false, cb);
          }
          return;
        }
        var alertingCall = getIncomingCall(callId);
        if (alertingCall) {
          decline(alertingCall, { type: inviteRejectCause }, false, cb);
        } else {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: endCall - There is no local or alerting call');
          var conversation = ConversationSvc.getConversationByRtcSession(callId);
          if (conversation && conversation.call) {
            conversation.call = null;
            publishConversationUpdate(conversation);
          }

          cb && cb('Call not found');
        }
      }
    };

    /**
     * End conference (terminate rtc call).
     *
     * @param {String} callId The call ID of local call used to terminate the conference.
     * @param {Function} cb A callback function replying with an error
     */
    this.endConference = function (callId, cb) {
      var call = findCall(callId);
      if (!call) {
        cb && cb('Call not found');
      } else {
        terminateConference(call, cb);
      }
    };

    /**
     * Add a video stream as screen share to an existing RTC session. The video and bandwidth
     * constraints of screen share will be used.
     * For adding it as video, please use addVideoStream() instead.
     *
     * @param {String} callId The call ID of call to add screen share to.
     * @param {MediaStream} stream The media stream to share.
     * @param {Function} cb A callback function replying with an error
     */
    this.addMediaStream = function (callId, stream, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: addMediaStream - There is no local call');
        cb('No active call');
        return;
      }

      var mediaType = Object.assign({}, localCall.localMediaType);
      mediaType.desktop = true;
      if (!isVideoAndScreenShareEnabled(localCall)) {
        mediaType.video = false;
      }

      var data = {
        callId: callId,
        mediaType: mediaType,
        screenShareOptions: {
          mediaStream: stream
        }
      };

      changeMediaType(data, function (err) {
        if (err) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: addMediaStream failed: ', err);
          cb('res_AddMediaStreamFailed');
        } else {
          cb();
        }
      });
    };

    /**
     * Add a video stream to an existing RTC session.
     * For adding a screen share stream, please use addMediaStream() instead.
     *
     * @param {String} callId The call ID of call to add video to.
     * @param {MediaStream} stream The video media stream to be added.
     * @return {Promise} Promise resolved when adding the video stream is successfully initiated
     */
    this.addVideoStream = function (callId, stream) {
      return new $q(function (resolve, reject) {
        var localCall = findLocalCallByCallId(callId);
        if (!localCall) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: addVideoStream - There is no local call');
          reject('No active call');
          return;
        }
        if (!stream) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: addVideoStream - There is stream to be added');
          reject('No stream to be added');
          return;
        }

        var mediaType = Object.assign({}, localCall.localMediaType);
        mediaType.video = true;
        if (!isVideoAndScreenShareEnabled(localCall)) {
          // This client cannot transmit video and screenshare simultaneously: turn off screenshare
          mediaType.desktop = false;
        }

        var data = {
          callId: callId,
          mediaType: mediaType,
          videoOptions: {
            mediaStream: stream
          }
        };

        changeMediaType(data, function (err) {
          if (err) {
            LogSvc.warn('[UnifyPhoneCallControlSvc]: addVideoStream failed: ', err);
            reject('res_AddVideoFailed');
          } else {
            resolve();
          }
        });
      });
    };

    /**
     * Remove a media stream from an existing RTC session.
     *
     * @param {String} callId The call ID of call to remove stream from.
     * @param {Function} cb A callback function replying with an error
     */
    this.removeMediaStream = function (callId, cb) {
      cb = cb || NOP;
      addRemoveMedia(callId, { desktop: false }, 'removeMediaStream')
        .then(cb)
        .catch(function () {
          cb('res_RemoveMediaStreamFailed');
        });
    };

    /**
     * Enable audio in an existing RTC session.
     *
     * @param {String} callId The call ID of call to add audio to.
     * @param {Function} cb A callback function replying with an error
     */
    this.addAudio = function (callId, cb) {
      cb = cb || NOP;
      addRemoveMedia(callId, { audio: true }, 'addAudio')
        .then(cb)
        .catch(function () {
          cb('Renegotiate for receive-only media failed');
        });
    };

    /**
     * Disable audio in an existing RTC session.
     *
     * @param {String} callId The call ID of call to remove audio from.
     * @param {Function} cb A callback function replying with an error
     */
    this.removeAudio = function (callId, cb) {
      cb = cb || NOP;
      addRemoveMedia(callId, { audio: false }, 'removeAudio')
        .then(cb)
        .catch(function () {
          cb('Renegotiate for receive-only media failed');
        });
    };

    /**
     * Enable remote audio stream in an active call
     *
     * @param {String} callId The call ID to be muted
     */
    this.enableRemoteAudio = function (callId) {
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: enableRemoteAudio - There is no local call');
        return;
      }

      if (localCall.remoteAudioDisabled) {
        localCall.enableRemoteAudio();
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/remoteStreamUpdated event');
        PubSubSvc.publish('/call/remoteStreamUpdated', [localCall]);
      }
    };

    /**
     * Disable remote audio stream in an active call
     *
     * @param {String} callId The call ID to be muted
     */
    this.disableRemoteAudio = function (callId) {
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: disableRemoteAudio - There is no local call');
        return;
      }

      if (!localCall.remoteAudioDisabled) {
        localCall.disableRemoteAudio();
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/remoteStreamUpdated event');
        PubSubSvc.publish('/call/remoteStreamUpdated', [localCall]);
      }
    };

    /**
     * Enable video in an existing RTC session.
     *
     * @param {String} callId The call ID of call to add video to.
     * @param {Function} cb A callback function replying with an error
     */
    this.addVideo = function (callId, cb) {
      cb = cb || NOP;
      addRemoveMedia(callId, { video: true }, 'addVideo')
        .then(cb)
        .catch(function () {
          cb('res_AddVideoFailed');
        });
    };

    /**
     * Disable video in an existing RTC session.
     *
     * @param {String} callId The call ID of call to remove video from.
     * @param {Function} cb A callback function replying with an error
     */
    this.removeVideo = function (callId, cb) {
      cb = cb || NOP;
      addRemoveMedia(callId, { video: false }, 'removeVideo')
        .then(cb)
        .catch(function () {
          cb('res_RemoveVideoFailed');
        });
    };

    /**
     * Disable remote video streams only in an existing RTC session.
     * Remote Screen share is be allowed.
     * @param {String} callId The call ID of call to remove remote video from.
     * @param {Function} cb A callback function replying with an error
     */
    this.disableRemoteVideoOnly = function (callId, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: toggleRemoteVideo - There is no local call');
        cb('No active call');
        return;
      }
      LogSvc.debug('[UnifyPhoneCallControlSvc]: toggleRemoteVideo...');

      var sessionCtrl = localCall.sessionCtrl;
      if (!sessionCtrl.isConnStable()) {
        cb('Connection not stable');
        return;
      }

      // Make sure that remote video is allowed
      if (localCall.remoteVideoDisabled) {
        cb('Remote video and screen share are already disabled');
        return;
      }

      // Make sure that this option is not already enabled
      if (localCall.remoteVideoScreenOnlyAllowed) {
        cb('Remote video is already disabled');
        return;
      }

      var errorCb = function (err) {
        // The media renegotiation failed. So restore the original state.
        localCall.disableRemoteVideoScreenOnly();
        LogSvc.warn('[UnifyPhoneCallControlSvc]: disableRemoteVideoOnly failed: ', err);
        cb('res_ToggleVideoFailed');
      };

      var receiverConfiguration = localCall.enableRemoteVideoScreenOnly();

      if (localCall.supportsVideoReceiverConfiguration()) {
        setVideoReceiverConfiguration(localCall.callId, receiverConfiguration)
          .then(function () {
            // Publish event here to prevent UI changes during disabling video streams
            PubSubSvc.publish('/call/toggleRemoteVideo');
            cb();
          })
          .catch(errorCb);
      } else {
        // Publish event here to prevent UI changes during disabling video streams
        PubSubSvc.publish('/call/toggleRemoteVideo');

        var data = {
          callId: callId,
          mediaType: localCall.localMediaType
        };

        changeMediaType(data, function (err) {
          if (err) {
            errorCb(err);
          } else {
            cb();
          }
        });
      }
    };

    /**
     * Enable remote and screen share video streams in an existing
     * RTC session.
     *
     * @param {String} callId The call ID of call to remove remote video from.
     * @param {Function} cb A callback function replying with an error
     */
    this.enableRemoteVideo = function (callId, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: enableRemoteVideo - There is no local call');
        cb('No active call');
        return;
      }
      LogSvc.debug('[UnifyPhoneCallControlSvc]: enableRemoteVideo...');

      var sessionCtrl = localCall.sessionCtrl;
      if (!sessionCtrl.isConnStable()) {
        cb('Connection not stable');
        return;
      }

      var receiverConfiguration = localCall.enableRemoteVideo();

      var successCb = function () {
        cb();
        !localCall.remoteVideoDisabled && PubSubSvc.publish('/call/toggleRemoteVideo');
      };

      var errorCb = function (err) {
        // The media renegotiation failed. So restore the original state.
        localCall.enableRemoteVideoScreenOnly();
        LogSvc.warn('[UnifyPhoneCallControlSvc]: enableRemoteVideo failed: ', err);
        cb('res_ToggleVideoFailed');
      };

      if (localCall.supportsVideoReceiverConfiguration()) {
        setVideoReceiverConfiguration(localCall.callId, receiverConfiguration).then(successCb).catch(errorCb);
      } else {
        var data = {
          callId: callId,
          mediaType: localCall.localMediaType
        };

        changeMediaType(data, function (err) {
          if (err) {
            errorCb(err);
          } else {
            successCb();
          }
        });
      }
    };

    /**
     * Toggle (allow or block) remote video streams in an existing RTC session.
     *
     * @param {String} callId The call ID of call to remove remote video from.
     * @param {Function} cb A callback function replying with an error
     */
    this.toggleRemoteVideo = function (callId, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: toggleRemoteVideo - There is no local call');
        cb('No active call');
        return;
      }
      LogSvc.debug('[UnifyPhoneCallControlSvc]: toggleRemoteVideo...');

      var sessionCtrl = localCall.sessionCtrl;
      if (!sessionCtrl.isConnStable()) {
        cb('Connection not stable');
        return;
      }

      var receiverConfiguration;
      if (localCall.remoteVideoDisabled) {
        receiverConfiguration = localCall.enableRemoteVideo();
      } else {
        receiverConfiguration = localCall.disableRemoteVideo();
        // Publish event here to prevent UI changes during disabling video
        PubSubSvc.publish('/call/toggleRemoteVideo');
      }

      var successCb = function () {
        cb();
        !localCall.remoteVideoDisabled && PubSubSvc.publish('/call/toggleRemoteVideo');
      };

      var errorCb = function (err) {
        // The media renegotiation failed. So restore the original state.
        if (localCall.remoteVideoDisabled) {
          localCall.enableRemoteVideo();
        } else {
          localCall.disableRemoteVideo();
        }
        LogSvc.warn('[UnifyPhoneCallControlSvc]: toggleRemoteVideo failed: ', err);
        cb('res_ToggleVideoFailed');
      };

      if (localCall.supportsVideoReceiverConfiguration()) {
        LogSvc.debug('New video receiver configuration: ', receiverConfiguration);
        setVideoReceiverConfiguration(localCall.callId, receiverConfiguration).then(successCb).catch(errorCb);
      } else {
        var data = {
          callId: callId,
          mediaType: localCall.localMediaType
        };

        changeMediaType(data, function (err) {
          if (err) {
            errorCb(err);
          } else {
            successCb();
          }
        });
      }
    };

    /**
     * Change the HD video in an existing RTC session.
     *
     * @param {String} callId The call ID of call for which video will be toggled.
     * @param {boolean} hdVideo Turn the HD video on/off.
     * @param {Function} cb A callback function replying with an error (1st parameter). If the desired video resolution is not supported,
     * it will attempt to use a lower resolution defined in UnifyPhone.Enums.VideoResolutionLevel. If successful,
     * 'res_ToggleVideoResolutionNotSupported' is returned as a warning (2nd parameter).
     * @param {VideoResolutionLevel} videoResolution Desired HD resolution. Defaults to max HD resolution supported by camera.
     */
    this.changeHDVideo = function (callId, hdVideo, cb, videoResolution) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: changeHDVideo - There is no local call');
        cb('No active call');
        return;
      }
      if (!localCall.localMediaType.video) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: changeHDVideo - Local call has no video');
        cb('No video');
        return;
      }
      hdVideo = !!hdVideo;

      if (!unifyPhone.isSDK && !$rootScope.localUser.canUseHDVideo) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: changeHDVideo - User does not have HD video permission');
        cb('No HD permission');
        return;
      }

      LogSvc.debug('[UnifyPhoneCallControlSvc]: changeHDVideo - hdVideo = ', hdVideo);

      if (localCall.localMediaType.hdVideo === hdVideo) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: changeHDVideo - No changes');
        cb(null, null);
        return;
      }

      if (!isValidVideoResolution(videoResolution)) {
        videoResolution = null;
      }

      var mediaType = Object.assign({}, localCall.localMediaType);
      mediaType.hdVideo = hdVideo;
      mediaType.videoResolution = videoResolution;

      var data = {
        callId: callId,
        mediaType: mediaType
      };

      changeMediaType(data, function (err) {
        if (err) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: changeHDVideo failed: ', err);
          cb('res_ToggleVideoFailed');
        } else {
          var warning = null;
          if (videoResolution && videoResolution !== localCall.localMediaType.videoResolution) {
            warning = 'res_ToggleVideoResolutionNotSupported';
          }
          cb(null, warning);
        }
      });
    };

    /**
     * Change video resolution in an existing RTC session.
     *
     * @param {String} callId The call ID of call for which video will be toggled.
     * @param {String} newResolution New HD video resolution.
     * @param {Function} cb A callback function replying with an error (1st parameter). If the new resolution is not supported,
     * it will attempt to use a lower resolution defined in UnifyPhone.Enums.VideoResolutionLevel. If successful,
     * 'res_ToggleVideoResolutionNotSupported' is returned as a warning (2nd parameter).
     */
    this.changeVideoResolution = function (callId, newResolution, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: changeVideoResolution - There is no local call');
        cb('No active call');
        return;
      }
      if (!localCall.localMediaType.video || !localCall.localMediaType.hdVideo) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: changeVideoResolution - Local call has no HD video');
        cb('No HD video');
        return;
      }

      LogSvc.debug('[UnifyPhoneCallControlSvc]: changeVideoResolution - newResolution = ', newResolution);

      if (!isValidVideoResolution(newResolution)) {
        cb('Invalid resolution');
        return;
      }

      if (localCall.localMediaType.videoResolution === newResolution) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: changeVideoResolution - No changes');
        cb(null, null);
        return;
      }

      var mediaType = Object.assign({}, localCall.localMediaType);
      mediaType.videoResolution = newResolution;

      var data = {
        callId: callId,
        mediaType: mediaType
      };

      changeMediaType(data, function (err) {
        if (err) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: changeVideoResolution failed: ', err);
          cb('res_ToggleVideoFailed');
        } else {
          cb(null, localCall.localMediaType.videoResolution !== newResolution ? 'res_ToggleVideoResolutionNotSupported' : null);
        }
      });
    };

    /**
     * Toggle the video in an existing RTC session.
     *
     * @param {String} callId The call ID of call for which video will be toggled.
     * @param {Function} cb A callback function replying with an error
     */
    this.toggleVideo = function (callId, cb) {
      cb = cb || NOP;

      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: toggleVideo - There is no local call');
        cb('No active call');
        return;
      }

      var data = { video: !localCall.localMediaType.video };
      addRemoveMedia(callId, data, 'toggleVideo')
        .then(cb)
        .catch(function () {
          cb('res_ToggleVideoFailed');
        });
    };

    /**
     * Use a new camera in an existing RTC session. The video constraints will remain the same.
     *
     * @param {String} callId The call ID of call for which video will be toggled.
     * @param {String} deviceId The device ID of the camera to be used.
     * @param {Function} cb A callback function replying with an error
     */
    this.toggleCamera = function (callId, deviceId, cb) {
      cb = cb || NOP;

      if (!deviceId) {
        cb('No device ID');
        return;
      }
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: toggleVideo - There is no local call');
        cb('No active call');
        return;
      }
      if (!localCall.localMediaType.video) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: changeHDVideo - Local call has no video');
        cb('No video');
        return;
      }

      WebRTCAdapter.getMediaSources(function (input, video) {
        var deviceObject = video.find(function (d) {
          return d.id === deviceId;
        });
        if (deviceObject) {
          Utils.addDeviceToPreferredList(RtcSessionController.videoDevices, deviceObject);
          addRemoveMedia(callId, {}, 'toggleCamera')
            .then(cb)
            .catch(function () {
              cb('res_ToggleVideoFailed');
            });
        } else {
          cb('Device not found');
        }
      });
    };

    /**
     * Starts a media renegotiation from the client without changing the current media types.
     *
     * @param {String} callId The call ID of the call for which to start the media renegotiation.
     * @param {Function} cb A callback function replying with an error
     * @param {Object} options Object with options
     */
    this.renegotiateMedia = function (callId, cb, options) {
      cb = cb || NOP;
      addRemoveMedia(callId, {}, 'renegotiateMedia', options)
        .then(cb)
        .catch(function (err) {
          cb(err || 'Renegotiate media failed');
        });
    };

    /**
     * Set the audio and/or video media type for the current call.
     *
     * @param {String} callId The call ID of the call for which to start the media renegotiation.
     * @param {Object} mediaType Object with audio and video boolean attributes.
     * @param {Function} cb A callback function replying with an error
     */
    this.setMediaType = function (callId, mediaType, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: setMediaType - There is no local call');
        cb('No active call');
        return;
      }
      LogSvc.debug('[UnifyPhoneCallControlSvc]: setMediaType...');

      var data = {
        callId: callId,
        mediaType: mediaType
      };

      changeMediaType(data, function (err) {
        if (err) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Failed to renegotiate the media');
          cb('Renegotiate media failed');
        } else {
          cb();
        }
      });
    };

    /**
     * Starts a media renegotiation from the client with inactive mode and without changing the media types
     *
     * @param {String} callId The call ID of the call to be held
     * @param {Function} cb A callback function replying with an error
     */
    this.holdCall = function (callId, cb) {
      cb = cb || NOP;
      var call = findLocalCallByCallId(callId);
      if (!call) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: holdCall - There is no call');
        cb('No active call');
        return;
      }
      if (call.isHolding() || call.isHoldInProgress()) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: holdCall - The call is already held');
        cb(); // No error, just return since we might be answering a second telephony call
        return;
      }
      LogSvc.debug('[UnifyPhoneCallControlSvc]: holdCall...');
      call.setHoldInProgress();

      var data = {
        callId: callId,
        mediaType: call.localMediaType,
        hold: true
      };

      changeMediaType(data, function (err) {
        call.clearHoldInProgress();
        if (err) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Failed to hold the call');
          cb('res_HoldCallFailed');
        } else {
          cb();
        }
      });
    };

    /**
     * Starts a media renegotiation from the client to retrieve a held call without changing the media types
     *
     * @param {String} callId The call ID of the call to be retrieved
     * @param {Function} cb A callback function replying with an error
     */
    this.retrieveCall = function (callId, cb) {
      cb = cb || NOP;
      var call = findLocalCallByCallId(callId);
      if (call && !call.isHolding()) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: retrieveCall - There is no held local call');
        cb();
        return;
      }
      LogSvc.debug('[UnifyPhoneCallControlSvc]: retrieveCall...');
      call.setRetrieveInProgress();

      var data = {
        callId: callId,
        mediaType: call.localMediaType,
        hold: false
      };

      changeMediaType(data, function (err) {
        call.clearRetrieveInProgress();
        if (err) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Failed to retrieve the call');
          cb('res_RetrieveCallFailed');
        } else {
          if (call === _secondaryLocalCall) {
            _secondaryLocalCall = _primaryLocalCall;
            _primaryLocalCall = call;
          }
          var conversation = getConversation(_primaryLocalCall.convId);
          conversation.call = _primaryLocalCall;
          publishConversationUpdate(conversation);
          cb();
        }
      });
    };

    this.swapCall = function (callId, cb) {
      cb = cb || NOP;
      _secondaryLocalCall = _that.getPhoneCalls(true).length > 1 ? findHeldPhoneCall(true) : null;
      if (!_secondaryLocalCall || !_primaryLocalCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: swapCall - There are no held and active local calls');
        cb('No calls to swap');
        return;
      }

      if (_secondaryLocalCall && _secondaryLocalCall.callId !== callId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: swapCall - Wrong callId of the held call');
        cb('Wrong callId');
        return;
      }

      var heldCallId = _secondaryLocalCall.callId;
      var activeCallId = _primaryLocalCall.callId;
      _that.holdCall(activeCallId, function (err) {
        if (err) {
          cb && cb(err);
        } else {
          _that.retrieveCall(heldCallId, cb);
        }
      });
    };

    /**
     * Move a remote call to the local client
     *
     * @param {String} callId The call ID of existing remote call established on another client.
     * @param {BOOL} audioOnly flag to check whether to pull with audio only.
     * @param {Function} cb A callback function replying with an error
     */
    this.pullRemoteCall = function (callId, audioOnly, cb) {
      cb = cb || NOP;
      var activeRemoteCall = getActiveRemoteCall(callId);
      if (!activeRemoteCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: pullRemoteCall invoked without a valid call');
        cb('No active remote call');
        return;
      }
      if (activeRemoteCall.pullNotAllowed) {
        cb('res_PullCallNotAllowed');
        return;
      }
      var conversation = getConversation(activeRemoteCall.convId);
      if (!conversation || (!conversation.isTelephony && conversation.call.callId !== callId)) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: pullRemoteCall - Cannot find valid conversation');
        cb('Conversation not found');
        return;
      }

      // ANS-67474 Pull call disables video
      audioOnly = true;

      function pullCallContinue() {
        var activeClient = activeRemoteCall.activeClient || {};
        var mediaType = {
          audio: true,
          video: !!(activeClient.mediaType && activeClient.mediaType.video)
        };

        if (audioOnly) {
          mediaType.video = false;
        }

        //NGTC-7108:When a conference is pulled we need to know it, because participants in remote call are not the same as in local call.
        //When one of the users leaves the call we need to calculate properly if it's direct or conference call.
        if (activeRemoteCall.isAtcConferenceCall()) {
          activeRemoteCall.pulledConference = true;
        }

        var options = createCallOptions({
          callOut: false,
          handover: true,
          callId: callId,
          pickUpFromUser: activeRemoteCall.pickUpFromUser,
          pickUpUserId: activeRemoteCall.pickUpUserId,
          pulledConference: activeRemoteCall.pulledConference
        });

        if (createLocalCall(conversation, mediaType, options, cb)) {
          checkMediaSourcesAndJoin(conversation, mediaType, options, cb);
        }
      }

      if (_primaryLocalCall && _primaryLocalCall.isTelephonyCall) {
        _that.holdCall(_primaryLocalCall.callId, function (err) {
          if (err) {
            cb && cb(err);
          } else {
            pullCallContinue();
          }
        });
        return;
      }

      pullCallContinue();
    };

    /**
     * End a remote call established on another client
     *
     * @param {String} callId The call ID of existing remote call established on another client.
     * @param {Function} cb A callback function replying with an error
     */
    this.endRemoteCall = function (callId, cb) {
      cb = cb || NOP;
      var activeRemoteCall = getActiveRemoteCall(callId);
      if (!activeRemoteCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: endRemoteCall invoked without a valid call');
        cb('No active remote call');
        return;
      }
      var conversation = getConversation(activeRemoteCall.convId);
      if (!conversation || conversation.call.callId !== callId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: endRemoteCall - Cannot find valid conversation');
        cb('Conversation not found');
        return;
      }

      endRemoteCall(activeRemoteCall, function (err) {
        if (err) {
          cb('res_EndRemoteCallFailed');
        } else {
          changeRemoteCallToStarted(activeRemoteCall);
          cb();
        }
      });
    };

    /**
     * Hide a remote call.
     *
     * @param {String} callId The call ID of remote call to be hidden.
     */
    this.hideRemoteCall = function (callId) {
      var activeRemoteCall = findCall(callId);
      if (!activeRemoteCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: hideRemoteCall invoked without a valid call');
        return;
      }
      terminateCall(activeRemoteCall);
    };

    /**
     * Remove participant in a group call.
     *
     * @param {String} callId The call ID of a group call or conference.
     * @param {Object} participant The call participant object.
     * @param {Function} cb A callback function replying with an error
     */
    this.dropParticipant = function (callId, participant, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall || localCall.isDirect) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: dropParticipant - invalid call');
        cb('Call invalid');
        return;
      }
      if (!participant || !participant.userId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: dropParticipant - Invalid participant');
        cb('Participant invalid');
        return;
      }
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Dropping participant in existing RTC session. userId =', participant.userId);
      removeSessionParticipant(participant.userId, cb);
    };

    /**
     * Add participant to a local call.
     *
     * @param {String} callId The call ID
     * @param {Object} participant The participant object.
     * @param {Function} cb A callback function replying with an error
     */
    this.addParticipantToCall = function (callId, participant, cb) {
      var call = findLocalCallByCallId(callId);
      if (!call) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: addParticipantToCall - There is no local call');
        cb && cb('Invalid callId');
      } else {
        addParticipant(call, participant, cb);
      }
    };

    /**
     * Add participant to an RTC Session. Unlike addParticipantToCall this API does not rely
     * on a local call being present.
     *
     * @param {String} callId The call ID
     * @param {Object} participant The participant object.
     * @param {Function} cb A callback function replying with an error
     */
    this.addParticipantToRtcSession = function (callId, participant, cb) {
      var call = findCall(callId);
      if (!call) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: addParticipantToRtcSession - Call not found');
        cb && cb('Invalid callId');
      } else {
        addParticipant(call, participant, cb);
      }
    };

    /**
     * mute self locally in an active call
     *
     * @param {String} callId The call ID to be muted
     * @param {Function} cb A callback function replying with an error
     */
    this.mute = function (callId, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall || callId !== localCall.callId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: mute - There is no local call');
        cb('Call not valid');
        return;
      }

      if (!localCall.locallyMuted) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Mute the local call');
        localCall.mute(function () {
          $rootScope.$apply(function () {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/localUser/mutedSelf event');
            PubSubSvc.publish('/call/localUser/mutedSelf', [callId, true]);

            _that.muteParticipant(localCall.callId, $rootScope.localUser, function (err) {
              err && LogSvc.warn('[UnifyPhoneCallControlSvc]: Error synchronizing local mute with backend.', err);
            });
            cb();
          });
        });
      } else {
        cb('Call already muted');
      }
    };

    /**
     * unmute locally only (used for large conference)
     *
     * @param {Function} cb A callback function called on success
     */
    this.unmuteLocally = function (cb) {
      cb = cb || NOP;
      if (_primaryLocalCall) {
        if (_primaryLocalCall.locallyMuted) {
          LogSvc.debug('[UnifyPhoneCallControlSvc]: Unmute the local call');
          _primaryLocalCall.unmute(function (err) {
            $rootScope.$apply(function () {
              if (!err) {
                LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/localUser/mutedSelf event');
                PubSubSvc.publish('/call/localUser/mutedSelf', [_primaryLocalCall.callId, false]);
              }
              cb(err);
            });
          });
        } else {
          // Already unmuted
          cb();
        }
      } else {
        cb('Call not found');
      }
    };

    /**
     * unmute self in an active call, which may have been muted locally or remotely
     *
     * @param {String} callId The call ID to be unmuted
     * @param {Function} cb A callback function replying with an error
     */
    this.unmute = function (callId, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: unmute - There is no local call');
        return;
      }

      if (!localCall.isMuted()) {
        cb('Call not muted');
        return;
      }

      // First unmute locally
      _that.unmuteLocally(function (err) {
        if (err) {
          cb(err);
        } else {
          // Unmute remotely
          _that.unmuteParticipant(localCall.callId, $rootScope.localUser, cb);
        }
      });
    };

    /**
     * toggle between mute and unmute
     *
     * @param {String} callId The call ID to toggle mute
     * @param {Function} cb A callback function replying with an error
     */
    this.toggleMute = function (callId, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: toggleMute - There is no local call');
        cb('Call not valid');
        return;
      }
      if (!localCall.isLocalMuteAllowed()) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: toggleMute - Local Mute is not allowed');
        cb();
        return;
      }
      if (localCall.isMuted()) {
        _that.unmute(localCall.callId, cb);
      } else {
        _that.mute(localCall.callId, cb);
      }
    };

    /**
     * mute remote participant
     *
     * @param {String} callId The call ID of active call
     * @param {Object} participant The participant object to be muted
     * @param {Function} cb A callback function replying with an error
     */
    this.muteParticipant = function (callId, participant, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: muteParticipant - There is no local call');
        cb('Call invalid');
        return;
      }
      var participantId = participant && participant.userId;
      if (!participantId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: muteParticipant - Invalid participant ID');
        cb('Participant invalid');
        return;
      }

      LogSvc.debug('[UnifyPhoneCallControlSvc]: Mute participant in existing RTC session. participantId =', participantId);

      var data = {
        rtcSessionId: localCall.callId,
        cstaCallId: localCall.cstaCallId,
        usersIds: [participantId],
        muted: true
      };

      _clientApiHandler.muteParticipant(data, function (err) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.warn('[UnifyPhoneCallControlSvc]: Failed to mute participant');
            cb('res_MuteParticipantFailed');
          } else {
            cb();
          }
        });
      });
    };

    /**
     * check if there are unmuted participants
     *
     * @param {String} callId The call ID of active call
     */
    this.hasUnmutedParticipants = function (callId) {
      var localCall = findLocalCallByCallId(callId);
      if (!localCall || callId !== localCall.callId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: hasUnmutedParticipants - There is no local call');
        return false;
      }
      return localCall.hasUnmutedParticipants();
    };

    /**
     * mute RTC session
     *
     * @param {String} callId The call ID of active call
     * @param {Function} cb A callback function replying with an error
     */
    this.muteRtcSession = function (callId, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall || callId !== localCall.callId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: muteRtcSession - There is no local call');
        cb('Call invalid');
        return;
      }
      if (localCall.hasUnmutedParticipants()) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Mute RTC session (all other participants)');

        var data = {
          rtcSessionId: localCall.callId,
          muted: true,
          excludedUserIds: [$rootScope.localUser.userId]
        };

        _clientApiHandler.muteRtcSession(data, function (err) {
          $rootScope.$apply(function () {
            if (err) {
              LogSvc.warn('[UnifyPhoneCallControlSvc]: Failed to mute RTC session');
              cb('res_MuteAllOtherParticipantsFailed');
            } else {
              cb();
            }
          });
        });
      }
    };

    /**
     * unmute self which has been muted remotely
     *
     * @param {String} callId The call ID of active call
     * @param {Object} participant The participant object to be unmuted
     * @param {Function} cb A callback function replying with an error
     */
    this.unmuteParticipant = function (callId, participant, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: unmuteParticipant - There is no local call');
        cb('Call invalid');
        return;
      }
      var participantId = participant && participant.userId;
      if (!participantId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: unmuteParticipant - Invalid participant ID');
        cb('Participant invalid');
        return;
      }

      if (participantId !== $rootScope.localUser.userId) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: Cannot unmute remote participant');
        cb('Cannot unmute others');
        return;
      }

      LogSvc.debug('[UnifyPhoneCallControlSvc]: Unmute participant in existing RTC session. userId =', participantId);

      var data = {
        rtcSessionId: localCall.callId,
        cstaCallId: localCall.cstaCallId,
        usersIds: [participantId],
        muted: false
      };

      _clientApiHandler.muteParticipant(data, function (err) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.warn('[UnifyPhoneCallControlSvc]: Failed to unmute participant');
            cb('res_UnmuteParticipantFailed');
          } else {
            localCall.remotelyMuted = false;
            publishMutedEvent(localCall);
            cb();
          }
        });
      });
    };

    /**
     * toggle between participant mute and unmute
     *
     * @param {String} callId The call ID of active call
     * @param {Object} participant The participant object for which to toggle mute
     * @param {Function} cb A callback function replying with an error
     */
    this.toggleMuteParticipant = function (callId, participant, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: toggleMuteParticipant - There is no local call');
        cb('Call invalid');
        return;
      }
      if (participant && participant.userId) {
        if (participant.muted) {
          _that.unmuteParticipant(callId, participant, cb);
        } else {
          _that.muteParticipant(callId, participant, cb);
        }
      } else {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: unmuteParticipant - Invalid participant ID');
        cb('Participant invalid');
      }
    };

    this.stopRingingTone = function (callId) {
      var alertingCall = getIncomingCall(callId);

      if (!alertingCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: stopRingingTone - incoming call not found: ', callId);
        return;
      }

      alertingCall.ringToneStopped = true;
      LogSvc.debug('[UnifyPhoneCallControlSvc]: stopRingingTone for call with callId = ', callId);
      PubSubSvc.publish('/call/ringingTone/stop', [callId]);
    };

    /**
     * End active call.
     *
     * @param {Function} cb A callback function replying with a status
     */
    this.endActiveCall = function (cb) {
      cb = cb || NOP;
      if (_primaryLocalCall) {
        _that.endCall(_primaryLocalCall.callId, cb);
      } else {
        cb('No active call');
      }
    };

    this.startRecording = function (cb, mediaTypes, videoLayout) {
      if (!_primaryLocalCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: startRecording - There is no local call');
        cb('No active call');
        return;
      }
      var types;
      if (typeof mediaTypes === 'boolean') {
        // Backwards compatibility with old interface that had allowScreenshareRecording parameter instead of mediaTypes
        types = { audio: true, video: mediaTypes, text: false };
      } else if (mediaTypes instanceof Object) {
        types = mediaTypes;
      } else {
        // Assume audio and video by default
        types = { audio: true, video: true, text: false };
      }
      videoLayout = videoLayout || {
        layoutName: Constants.RecordingVideoLayoutName.SINGLE,
        layoutMapping: []
      };

      var data = {
        convId: _primaryLocalCall.convId,
        rtcSessionId: _primaryLocalCall.callId,
        recordingMediaTypes: types,
        videoLayout: videoLayout
      };
      _clientApiHandler.startRecording(data, getDefaultHandler(cb));
    };

    this.stopRecording = function (cb, mediaTypes) {
      if (!_primaryLocalCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: stopRecording - There is no local event call');
        cb('No active call');
        return;
      }

      var data = {
        convId: _primaryLocalCall.convId,
        rtcSessionId: _primaryLocalCall.callId,
        recordingMediaTypes: mediaTypes || { audio: true, video: true, text: false }
      };
      _clientApiHandler.stopRecording(data, getDefaultHandler(cb));
    };

    /**
     * Starts transcription for the active call.
     *
     * @return {Promise} Promise resolved when transcription is started
     */
    this.startTranscription = function () {
      return new $q(function (resolve, reject) {
        _that.startRecording(
          function (err) {
            if (err) {
              LogSvc.error('[UnifyPhoneCallControlSvc]: Failed to start transcription. ', err);
              reject(err);
            } else {
              resolve();
            }
          },
          { text: true }
        );
      });
    };

    /**
     * Stops transcription for the active call.
     *
     * @return {Promise} Promise resolved when transcription is stopped
     */
    this.stopTranscription = function () {
      return new $q(function (resolve, reject) {
        _that.stopRecording(
          function (err) {
            if (err) {
              LogSvc.error('[UnifyPhoneCallControlSvc]: Failed to stop transcription. ', err);
              reject(err);
            } else {
              resolve();
            }
          },
          { text: true }
        );
      });
    };

    this.switchRecordingLayout = function (videoLayout, cb) {
      if (!_primaryLocalCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: switchRecordingLayout - There is no local call');
        cb('No active call');
        return;
      }

      if (!videoLayout || !videoLayout.layoutName) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: switchRecordingLayout - Missing or invalid videoLayout');
        cb('Invalid videoLayout');
        return;
      }

      var data = {
        rtcSessionId: _primaryLocalCall.callId,
        videoLayout: videoLayout
      };
      _clientApiHandler.switchRecordingLayout(data, getDefaultHandler(cb));
    };

    this.startRecordingParticipant = function (userId) {
      return new $q(function (resolve, reject) {
        if (!_primaryLocalCall) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: startRecordingParticipant - There is no local call');
          reject('No active call');
          return;
        }
        var videoLayout = {
          layoutName: Constants.RecordingVideoLayoutName.SINGLE_VIDEO,
          layoutMapping: [
            {
              tileId: 'Video',
              tileContent: userId
            }
          ]
        };

        if (_primaryLocalCall.recording.isActive()) {
          // Switch current layout
          LogSvc.info('[UnifyPhoneCallControlSvc]: startRecordingParticipant - Switch layout to record userId = ', userId);
          _that.switchRecordingLayout(videoLayout, function (err) {
            err ? reject(err) : resolve();
          });
        } else {
          LogSvc.info('[UnifyPhoneCallControlSvc]: startRecordingParticipant - Start recording call with focus on userId = ', userId);
          _that.startRecording(
            function (err) {
              err ? reject(err) : resolve();
            },
            { audio: true, video: true, text: false },
            videoLayout
          );
        }
      });
    };

    this.submitCallQualityRating = function (ratingData, callData, cb) {
      if (!ratingData || !ratingData.hasOwnProperty('rating') || !callData) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: submitCallQualityRating - Missing rating or call data');
        cb && cb('No rating data');
        return;
      }
      Object.assign(ratingData, callData);
      _clientApiHandler.submitCallQualityRating([ratingData], function (err) {
        $rootScope.$apply(function () {
          cb && cb(err);

          if (!err) {
            LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /call/rated event');
            PubSubSvc.publish('/call/rated');
          }
        });
      });
    };

    /**
     * Retrieve list of nodes with states. Currently only used for testcall.
     *
     * @param {String} nodeType requested node type (currently only MEDIA_ACCESS)
     * @param {String} tenantId name of the tenant or empty for all
     * @param {Function} cb A callback function replying with an error or success
     */
    this.getNodeState = function (nodeType, tenantId, cb) {
      var data = {
        convId: 'noCall',
        nodeType: nodeType,
        tenantId: tenantId || ''
      };
      _clientApiHandler.getNodeState(data, function (err, nodeData) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.error('[UnifyPhoneCallControlSvc]: Failed to get node(s). ', err);
          }
          cb && cb(err, nodeData);
        });
      });
    };

    /**
     * Only applicable to local call (Siemens AG temporary solution)
     */
    this.canSendWebRTCDigits = function () {
      return _primaryLocalCall && _primaryLocalCall.sessionCtrl.canSendDTMFDigits();
    };

    /**
     * Send DTMF digits
     *
     * @param {String} callId The call ID of active call
     * @param {String} digits The digits to be sent
     * @param {Function} cb A callback function replying with a status
     */
    this.sendDigits = function (callId, digits, cb) {
      cb = cb || NOP;
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: sendDigits - There is no local call');
        cb('Call not valid');
        return;
      }

      if (!digits) {
        cb('res_InvalidDtmf');
        return;
      }

      if (!/^[\d#*,]+$/.exec(digits)) {
        LogSvc.error('[UnifyPhoneCallControlSvc]: Digits cannot be sent: ' + digits + ' (Allowed: 0-9,#,*)');
        cb('res_InvalidDtmf');
        return;
      }

      sendDTMFDigits(callId, digits, cb);
    };

    /**
     * Locally disable the remote incoming video (this doesn't trigger a
     * media renegotiation)
     * @param {Object} call The existing group call or conference
     * @returns {undefined}
     */
    this.disableIncomingVideo = function (call) {
      if (_primaryLocalCall && _primaryLocalCall.sameAs(call)) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Disabling remote incoming video. Call ID=', _primaryLocalCall.callId);
        _primaryLocalCall.sessionCtrl.disableIncomingVideo();
      }
    };

    /**
     * Locally enable the remote incoming video (this doesn't trigger a
     * media renegotiation)
     * @param {Object} call The existing group call or conference
     * @returns {undefined}
     */
    this.enableIncomingVideo = function (call) {
      if (_primaryLocalCall && _primaryLocalCall.sameAs(call)) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Enabling remote incoming video. Call ID=', _primaryLocalCall.callId);
        _primaryLocalCall.sessionCtrl.enableIncomingVideo();
      }
    };

    /**
     * Enable or disable participant pointer
     *
     * @param {String} callId The ID of existing group call or conference
     * @returns {undefined}
     */
    this.toggleParticipantPointer = function (callId) {
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: toggleParticipantPointer - There is no local call');
        return;
      }

      if (!localCall.pointer || !localCall.pointer.isSupported) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: toggleParticipantPointer - Local call does not support participant pointer');
        return;
      }
      localCall.pointer.isEnabled = !localCall.pointer.isEnabled;

      changeMediaType({
        callId: localCall.callId,
        transactionId: localCall.transactionId,
        mediaType: localCall.localMediaType
      });
    };

    /**
     * Find active or incoming local call
     * @returns {LocalCall} The LocalCall object.
     */
    this.findCall = findCall;

    this.findLocalCallByCallId = findLocalCallByCallId;

    this.findActivePhoneCall = findActivePhoneCall;

    this.findHeldPhoneCall = findHeldPhoneCall;

    this.removeCallFromList = removeCallFromList;

    this.isSecondCall = function () {
      return _primaryLocalCall || _incomingCalls.length >= 1 || !Utils.isEmptyArray(_activeRemoteCalls);
    };

    this.updateActiveCallMediaDevices = function () {
      if (_primaryLocalCall) {
        _primaryLocalCall.updateMediaDevices();
      }
    };

    /**
     * Checks screen control Feature support for a call
     *
     * @param call to check
     * @returns {boolean}
     */
    this.hasScreenControlFeature = hasScreenControlFeature;

    this.canStartScreenShare = function (callId) {
      var localCall = findLocalCallByCallId(callId);
      if (!localCall) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: canStartScreenShare - There is no local call');
        return false;
      }
      if (!localCall.hasRemoteScreenShare()) {
        return true;
      }
      var moderatorSharing = localCall.participants.some(function (p) {
        return p.isModerator && p.mediaType && p.mediaType.desktop;
      });
      if (!moderatorSharing) {
        return true;
      }
      var conversation = ConversationSvc.getConversationFromCache(localCall.convId);
      return !!conversation && conversation.userIsModerator($rootScope.localUser);
    };

    this.canStartRecording = function (callId) {
      var localCall = findLocalCallByCallId(callId);
      var conversation = ConversationSvc.getConversationFromCache(localCall.convId);
      if (!localCall || !conversation) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: canStartRecording - Cannot find active call or conversation');
        return false;
      }
      return !!(
        !localCall.isDirect &&
        !localCall.moderatorLeftNotifyTime &&
        localCall.checkState([Enums.CallState.Active, Enums.CallState.Waiting]) &&
        (!conversation.isModerated || conversation.userIsModerator($rootScope.localUser))
      );
    };

    this.canTakeScreenshot = function (callId, p) {
      var call = findLocalCallByCallId(callId);
      if (!call) {
        return false;
      }

      if (p.mediaType && p.mediaType.desktop) {
        // All users can take picture from screenshare
        return !!((call.isDirect && p.streamId) || (p.streams && p.streams.desktop));
      }

      return false;
    };

    this.addConferenceAsGuest = function (convId) {
      var conversation = getConversation(convId);
      if (!conversation || !conversation.isTemporary || !conversation.guestToken) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: addConferenceAsGuest - No temporary conversation or guest token is missing');
        return;
      }
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Create placeholder remote call for temporary guest conversation with convId = ', conversation.convId);

      var remoteCall = new RemoteCall(conversation);
      remoteCall.setState(Enums.CallState.NotStarted);
      remoteCall.guestToken = conversation.guestToken;
      addCallToList(remoteCall);

      conversation.call = remoteCall;
      publishConversationUpdate(conversation);
      publishCallState(remoteCall);
    };

    this.removeConferenceAsGuest = function (callId) {
      var call = findCall(callId);
      if (!call) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: removeConferenceAsGuest - Could not find call');
        return;
      }

      if (!call.isRemote) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: removeConferenceAsGuest - Cannot remove temporary conversation with active call');
        return;
      }

      var conversation = getConversation(call.convId);
      if (!conversation || !conversation.isTemporary) {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: removeConferenceAsGuest - No temporary conversation');
        return;
      }

      terminateCall(call);

      LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /conversation/temporary/ended event');
      PubSubSvc.publish('/conversation/temporary/ended', [conversation]);
    };

    /**
     * Test ICE candidates collection
     * @returns {Promise} Returns an object containing the assigned TURN servers and the gathered ICE candidates.
     */
    this.testCandidatesCollection = function (useTurn) {
      if (!useTurn) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Test ICE candidates collection without TURN...');
        return testCandidatesCollection();
      }

      LogSvc.debug('[UnifyPhoneCallControlSvc]: Test ICE candidates collection with TURN...');
      return new $q(function (resolve, reject) {
        var rtcSessionId = 'echo_' + $rootScope.localUser.clientId;

        // If the guest is registered for test call, use conversationId, otherwise use clientId
        var prepareSessionData = {
          convId: rtcSessionId,
          rtcSessionId: rtcSessionId,
          ownerId: $rootScope.localUser.userId,
          isTelephonyConversation: false,
          mediaNode: RtcSessionController.mediaNode
        };

        LogSvc.debug('[UnifyPhoneCallControlSvc]: testIceCandidates - Get TURN servers');
        _clientApiHandler.prepareSession(prepareSessionData, function (err, servers) {
          if (err) {
            LogSvc.error('[UnifyPhoneCallControlSvc]: Failed to prepare session data. ', err);
            reject(err);
            return;
          }
          if (!servers || !servers.length) {
            _clientApiHandler.terminateRtcCall(rtcSessionId, Constants.DisconnectCause.HANGUP);
            LogSvc.error('[UnifyPhoneCallControlSvc]: Failed to prepare session data. No TURN servers.');
            reject('No TURN servers');
            return;
          }

          var turnCredentials = servers[0];
          LogSvc.debug('[UnifyPhoneCallControlSvc]: testIceCandidates - TURN servers: ', turnCredentials.turnServer);

          testCandidatesCollection(turnCredentials)
            .then(function (data) {
              _clientApiHandler.terminateRtcCall(rtcSessionId, Constants.DisconnectCause.HANGUP, function () {
                resolve(data);
              });
            })
            .catch(function (testErr) {
              _clientApiHandler.terminateRtcCall(rtcSessionId, Constants.DisconnectCause.HANGUP, function () {
                reject(testErr);
              });
            });
        });
      });
    };

    this.addCallToList = addCallToList;

    this.moveOsBizCalls = function (oldCall, newCall) {
      LogSvc.debug('[UnifyPhoneCallControlSvc]: Move oldCall: ' + oldCall.callId + ' to newCall: ' + newCall.callId);
      if (oldCall.isOsBizFirstCall) {
        var newCallAtcCallInfo = newCall.atcCallInfo;
        //move establishedTime from new to old call object for creating proper call Log
        var newCallEstablishedTime = newCall.establishedTime;
        oldCall.atcCallInfo = newCallAtcCallInfo;
        oldCall.establishedTime = newCallEstablishedTime;
        oldCall.setPeerUser(oldCall.atcCallInfo.peerDn, oldCall.atcCallInfo.peerName);
        oldCall.setCstaState(Enums.CstaCallState.Active);
        oldCall.setState(Enums.CallState.Active);
        oldCall.isOsBizFirstCall = false;
        // Inform the UI that call has been moved, needed for mobile clients
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Publish /oldCall/moved event');
        PubSubSvc.publish('/oldCall/moved', [oldCall.callId, newCall.callId]);
        terminateCall(newCall);
      } else {
        terminateCall(oldCall);
        newCall.isOsBizFirstCall = false;
        newCall.isOsBizSecondCall = false;
      }
    };

    this.findOsBizFirstCall = function () {
      return (
        _calls.find(function (call) {
          return call.isOsBizFirstCall;
        }) || null
      );
    };

    this.findOsBizSecondCall = function () {
      return (
        _calls.find(function (call) {
          return call.isOsBizSecondCall;
        }) || null
      );
    };

    this.getCallToPickup = function () {
      return _callToPickup;
    };

    /**
     * Returns the list of participants for the call with the given ID.
     *
     * @param {String} callId The ID of the call.
     * @returns {Promise} A promise resolving to the list of participants.
     */
    this.getConferenceParticipants = function (callId) {
      return new $q(function (resolve, reject) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Get participants for call with callId = ', callId);

        var call = findCall(callId);
        if (!call) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Call does not exist');
          reject('Call does not exist');
          return;
        }

        if (!call.isRemote) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: This is a local call. Return local participants.');
          resolve(call.participants);
          return;
        }

        _clientApiHandler.getSession(callId, function (error, session) {
          $rootScope.$apply(function () {
            if (error || !session) {
              LogSvc.warn('[UnifyPhoneCallControlSvc] Error retrieving RTC session. Err: ', error);
              reject(error);
            } else {
              // Resolve with list of normalized participants, retrieve not yet cached ones before
              var normalizedParticipants = session.participants.map(function (p) {
                return normalizeApiParticipant(p);
              });
              var userIdsToRetrieve = normalizedParticipants
                .filter(function (normalizedParticipant) {
                  return normalizedParticipant.noData;
                })
                .map(function (filteredNormalizedParticipant) {
                  return filteredNormalizedParticipant.userId;
                });

              UserSvc.getUsersByIds(userIdsToRetrieve, function () {
                // The normalizedParticipants will be automatically updated when the user data is retrieved
                resolve(normalizedParticipants);
              });
            }
          });
        });
      });
    };

    /**
     * Merges two phone calls by moving them to the media server
     *
     * @param {String} callId1 The ID of the held call
     * @param {String} callId2 The ID of the active or hosted call
     * @returns {Promise} A promise resolving to the result of the merge
     */
    this.mergeCalls = function (callId1, callId2) {
      return new $q(function (resolve, reject) {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Merge calls ', [callId1, callId2]);

        var call1 = findCall(callId1);
        var call2 = findCall(callId2);

        if (!call1 || !call2) {
          LogSvc.warn('[UnifyPhoneCallControlSvc]: Call does not exist');
          reject('Call does not exist');
          return;
        }

        _clientApiHandler.mergeRtcCalls(callId1, callId2, function (error) {
          // The API response indicates if the backend accepted or rejected the request. By the time of the
          // response, the two calls have not been merged yet.
          if (error) {
            LogSvc.warn('[UnifyPhoneCallControlSvc]: Error merging calls. Err: ', error);
            reject(error);
          } else {
            resolve();
          }
        });
      });
    };

    this.changeInputDevices = function (callId, inputDevices) {
      var activeCall = _that.getActiveCall();
      if (activeCall && activeCall.callId === callId) {
        if (!inputDevices || (!inputDevices.audio && !inputDevices.video)) {
          return $q.resolve(); // Nothing to do
        }
        if (inputDevices.video) {
          return new $q(function (resolve, reject) {
            // If video resolution changes, it requires SDP changes. So we need
            // a full renegotiation (for now)
            _that.renegotiateMedia(callId, function (err) {
              err ? reject(err) : resolve();
            });
          });
        }
        return activeCall.sessionCtrl.changeInputDevices(inputDevices);
      } else {
        return $q.reject('Call not found');
      }
    };

    /**
     * @typedef ReceiverConfiguration
     * @type {Object}
     * @property {String} contentType - The IncomingVideoContentType of stream to be received: VIDEO or SCREEN.
     * @property {String} [videoQuality] - The VideoQuality for the stream. Default is NORMAL.
     * @property {String} [userId] - The userId of the participant that should be pinned. If not specified backend uses video active speaker logic.
     */

    /**
     * Sets the video receiver configuration for the specified streams.
     *
     * @param {String} callId The callId of the active call.
     * @param {Array<ReceiverConfiguration>} [configuration] Array of configuration objects for video streams
     *      If null or undefined is passed, the default configuration is applied (i.e. reset to what has been negotiated in Offer/Answer).
     *      If an empty array is passed, the client will temporarily disable all incoming video streams.
     * @returns {Promise} Promise that resolves when operation is completed.
     */
    this.setIncomingVideoStreams = function (callId, configuration) {
      var activeCall = _that.getActiveCall();
      if (!activeCall || activeCall.callId !== callId) {
        return $q.reject('Call not found');
      } else if (!_that.isPinVideoSupported(callId)) {
        return $q.reject('Not supported');
      } else {
        LogSvc.debug('[UnifyPhoneCallControlSvc]: Set video receiver configuration: ', configuration || 'DEFAULT');
        var cfg = activeCall.setIncomingVideoStreams(configuration);
        if (cfg && cfg.length) {
          return setVideoReceiverConfiguration(callId, cfg);
        } else {
          return $q.resolve(); // Nothing to do
        }
      }
    };

    this.isTranscriptionSupported = _clientApiHandler.isTranscriptionSupported;

    /**
     * Get network quality info.
     *
     * @param {String} [callId] the ID of the call (optional: if not supplied, the active call will be used)
     * @returns {Object} Object containing:
     * - level: Overall network quality level defined in UnifyPhone.Enums.RtpQualityLevel
     * - audio: Object with audio quality parameters and their values: property names in the object are defined in
     * Constants.AudioQualityParam with values defined in UnifyPhone.Enums.RtpQualityLevel
     * - video: Object with video quality parameters and their values: property names in the object are defined in
     * Constants.VideoQualityParam with values defined in UnifyPhone.Enums.RtpQualityLevel
     * - videoInfo: Object containing:
     *   - resolution: Object containing:
     *     - height: current outgoing resolution height (Number)
     *     - width: current outgoing resolution width (Number)
     *   - hdVideo: value defined in UnifyPhone.Enums.VideoResolutionLevel. Applicable only if resolution exactly matches
     * a predefined resolution, otherwise it's undefined.
     *
     * If call could not be found or no quality info is available, null is returned.
     */
    this.getNetworkQualityInfo = function (callId) {
      var call = _that.getActiveCall(callId);
      if (call && call.sessionCtrl) {
        var stats = call.sessionCtrl.getLastSavedStats();
        if (stats.quality) {
          return stats.quality;
        }
      } else {
        LogSvc.warn('[UnifyPhoneCallControlSvc]: getNetworkQualityInfo: ' + (callId ? 'Call not found: ' + callId : 'No active call'));
      }
      return null;
    };

    this.isPinVideoSupported = function (callId) {
      var call = callId && _that.getActiveCall(callId);
      return !call || call.supportsVideoReceiverConfiguration();
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.UnifyPhoneCallControlSvcImpl = UnifyPhoneCallControlSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/* global RegistrationState */

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var AgentState = unifyPhone.Enums.AgentState;
  var AtcCallInfo = unifyPhone.AtcCallInfo;
  var AtcMessage = unifyPhone.Enums.AtcMessage;
  var AtcRegistrationState = unifyPhone.Enums.AtcRegistrationState;
  var AtcRemoteCall = unifyPhone.AtcRemoteCall;
  var BusyHandlingOptions = unifyPhone.BusyHandlingOptions;
  var CallDirection = unifyPhone.Enums.CallDirection;
  var CallForwardingTypes = unifyPhone.Enums.CallForwardingTypes;
  var CallState = unifyPhone.Enums.CallState;
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var Constants = unifyPhone.Constants;
  var CstaCallState = unifyPhone.Enums.CstaCallState;
  var CstaParser = unifyPhone.CstaParser;
  var JournalEntryTypes = unifyPhone.Enums.JournalEntryTypes;
  var LocalCall = unifyPhone.LocalCall;
  var MissedReasonTypes = unifyPhone.Enums.MissedReasonTypes;
  var PhoneNumberFormatter = unifyPhone.PhoneNumberFormatter;
  var RedirectionTypes = unifyPhone.Enums.RedirectionTypes;
  var RoutingOptions = unifyPhone.RoutingOptions;
  var Targets = unifyPhone.Enums.Targets;
  var TransferCallFailedCauses = unifyPhone.Enums.TransferCallFailedCauses;
  var UserToUserHandler = unifyPhone.UserToUserHandlerSingleton;
  var Utils = unifyPhone.Utils;

  var AgentUIState = Object.freeze({
    AVAILABLE: 'available',
    PARTIAL: 'partiallyUnavailable',
    UNAVAILABLE: 'unavailable'
  });

  // eslint-disable-next-line max-params, max-lines-per-function
  function CstaSvcImpl($rootScope, $timeout, $q, LogSvc, PubSubSvc, UnifyPhoneCallControlSvc, AtcRegistrationSvc, UserSvc, UserProfileSvc) { // NOSONAR
    LogSvc.debug('New Service: CstaSvc');

    ///////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////
    var TRANSFER_TIMEOUT = 4000; // Transferred event should be received within 4s
    var REVERSE_LOOKUP_MAX_TIME = 2000; // 2s - Max time to wait for user search by phone number
    var MAX_ENDED_CALL_WAIT_TIME = 2000; // 2s - Max time to wait for a local ended call
    var VALID_LOCAL_STATES = ['connected', 'hold', 'queued'];
    var VALID_PARTNER_STATES = ['connected', 'hold'];

    var NUMBER_NOT_AVAILABLE = 'Number not available';
    var ANONYMOUS_MIV5000_DEVICE = 'N<****>';

    var DEFAULT_TRANSFER_CALLID = '00000000000000000000000000000000';
    var DEFAULT_CALCULATED_TRANSFER_CALLID = '02000000000000000000000000000000';

    var FORWARD_EXPR = /Forward/;

    var MAX_NUMBER_OF_LOOPS = 3;
    var LOOP_DETECTION_WAIT_TIME = 60000; // Wait for 1 min before trying to set the timers again after loop detection
    var LOOP_DETECTION_INTERVAL = 2000; // 2s interval in which two subsequent Forwarding events indicate that there may be a loop
    var OSBIZ_RECONNECT_TIMEOUT = 2000; // 2s Wait for 2 sec before reconnecting the first call after a failed consulted call

    ///////////////////////////////////////////////////////////////////////////
    // Local variables
    ///////////////////////////////////////////////////////////////////////////
    var _that = this;

    var _regData = null;
    var _osmoData = {
      forwardList: []
    };

    var _userToUserHandler = UserToUserHandler.getInstance();
    var _clientApiHandler = ClientApiHandler.getInstance();
    var _cstaParser = new CstaParser();

    var _telephonyConversation = null;

    // The local WebRTC calls
    var _activeCall = null;
    var _heldCall = null;
    var _alertingCall = null;

    // List with all the remote calls
    var _atcRemoteCalls = {};

    var _webrtcRemoteCall = null;

    var _calls = [];

    // Track transferred calls
    var _trackTransferCallIds = {};

    // track the transferred calls
    var _transferredCalls = {};

    var _snapshotPerformed = false;

    var _onsUnregistered = true;

    var _primaryClient = true;

    var _incomingCallConnection = {};

    var _movingCall = {};

    var _lastTwoCallPartners = [];

    var _conferenceParticipants = [];

    var _resolving = false;

    var _wresExternalPeerInfo = null;

    // Last ended call. Save the last ended call for racing conditions
    var _lastEndedCall = null;
    var _endedCallWaitTimer = null;

    var _regState = null;
    var _savedStaticOnd = null;
    var _supportsStaticOndSync = false;

    var _userBusyState = false;

    var _savedVMRingDuration = null;

    var _savedMainRingDuration = null;

    var _savedClientRingDuration = null;

    var _savedCellRingDuration = null;

    var _receivedActiveSessions = false;

    var _lastSetForwardingTimestamp = 0;
    var _loopCounter = 0;
    var _loopDetectionTimer = null;
    var _pendingFwdRequest = null;
    var _numberNotAvailable = NUMBER_NOT_AVAILABLE;
    var _huntGroupList = null;
    //array of promises
    var deferredArr = [];
    var gPromise;

    ///////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////
    function isPartnerAnonymous(partnerData, anonymousStr) {
      return typeof partnerData === 'string' && partnerData.includes(anonymousStr);
    }

    function isSelectedRouting(routingOption) {
      return !!$rootScope.localUser && $rootScope.localUser.selectedRoutingOption === routingOption.name;
    }

    function setRoutingOption(routingOption) {
      if ($rootScope.localUser) {
        $rootScope.localUser.selectedRoutingOption = routingOption.name;
      }
    }

    function publishPeerUserUpdate(call, cb) {
      LogSvc.debug('[CstaSvc]: Publish /call/peerUser/update event. callId = ' + call.callId + ', peerUserId = ' + call.peerUser.userId);
      LogSvc.debug('[CstaSvc]: Publish /call/peerUser/update event. peerUser', call.peerUser);
      PubSubSvc.publish('/call/peerUser/update', [call]);
      cb && cb();
    }

    function chainExecute() {
      if (deferredArr.length > 0) {
        var fn = deferredArr.shift();
        return fn().then(chainExecute);
      }
      gPromise = null;
      return $q.resolve();
    }

    function enqueueChain() {
      if (!gPromise) {
        gPromise = chainExecute();
      } else {
        gPromise.then(chainExecute);
      }
    }

    function setCallPeerUser(call, phoneNumber, fqNumber, displayName, cb) {
      if (fqNumber && call.peerUser && call.peerUser.fqnLookedUp === fqNumber) {
        // This number has already been looked up
        cb && cb();
        return;
      }

      // Temporarily display whatever is available until user search is done
      call.setPeerUser(phoneNumber, displayName);
      if (call.peerUser.userId || !fqNumber || !Utils.PHONE_PATTERN.test(fqNumber)) {
        LogSvc.debug('[CstaSvc]: User already resolved or number is not fully qualified to do a reverse phone lookup');
        publishPeerUserUpdate(call, cb);
        return;
      }
      var maxWaitTime = $timeout(
        function () {
          maxWaitTime = null;
          if (call.isPresent()) {
            publishPeerUserUpdate(call, cb);
            cb = null;
          }
        },
        REVERSE_LOOKUP_MAX_TIME,
        false
      );

      UserSvc.startReverseLookUp(fqNumber, function (user) {
        $timeout.cancel(maxWaitTime);
        if (call.isPresent()) {
          var userId;
          var data;
          if (user) {
            displayName = user.displayName;
            userId = user.userId;
            data = {
              extAvatarUri: user.extAvatarUri,
              smallImageUri: user.smallImageUri,
              largeImageUri: user.largeImageUri
            };
          }
          // Regardless if the reverse lookup succeeded, we should invoke setPeerUser so the fqNumber
          // is recorded and we won't do another reverse lookup for the same number again (during this call)
          call.setPeerUser(phoneNumber, displayName, userId, fqNumber, data);
          publishPeerUserUpdate(call, cb);
        }
      });
    }

    function setCallPeerUserSync(call, phoneNumber, fqNumber, displayName, cb) {
      if (fqNumber && call.peerUser && call.peerUser.fqnLookedUp === fqNumber) {
        // This number has already been looked up
        cb && cb();
        return false;
      }

      // Temporarily display whatever is available until user search is done
      call.setPeerUser(phoneNumber, displayName);
      if (call.peerUser.userId || !fqNumber || !Utils.PHONE_PATTERN.test(fqNumber)) {
        LogSvc.debug('[CstaSvc]: User already resolved or number is not fully qualified to do a reverse phone lookup');
        cb && cb();
        return false;
      }

      return true;
    }

    function setRedirectingUser(call, phoneNumber, fqNumber, displayName, type, cb) {
      if (call.redirectingUser && call.redirectingUser.redirectionType) {
        return;
      }
      call.setRedirectingUser(phoneNumber, fqNumber, displayName, null, type);
      if (!fqNumber || !Utils.PHONE_PATTERN.test(fqNumber)) {
        LogSvc.debug('[CstaSvc]: number is not fully qualified to do a reverse phone lookup');
        cb && cb();
        return;
      }
      var maxWaitTime = $timeout(
        function () {
          maxWaitTime = null;
          if (call.isPresent()) {
            cb && cb();
            cb = null;
          }
        },
        REVERSE_LOOKUP_MAX_TIME,
        false
      );

      UserSvc.startReverseLookUp(fqNumber, function (user) {
        $timeout.cancel(maxWaitTime);
        if (call.isPresent()) {
          if (user) {
            call.setRedirectingUser(phoneNumber, fqNumber, user.displayName, user.userId, type);
          }
          cb && cb();
        }
      });
    }

    function setRedirectingUserDetails(userDetails, redirectionType) {
      return {
        fqNumber: userDetails.fqn,
        phoneNumber: userDetails.dn,
        displayName: userDetails.name,
        type: redirectionType
      };
    }

    function setRedirectingUserSync(call, phoneNumber, fqNumber, displayName, type, cb) {
      if (call.redirectingUser && call.redirectingUser.displayName && call.redirectingUser.fqNumber && call.redirectingUser.redirectionType) {
        return false;
      }
      call.setRedirectingUser(phoneNumber, fqNumber, displayName, null, type);
      if (!fqNumber || !Utils.PHONE_PATTERN.test(fqNumber)) {
        LogSvc.debug('[CstaSvc]: number is not fully qualified to do a reverse phone lookup');
        cb && cb();
        return false;
      }

      return true;
    }

    function reverseLookupAndSetUserData(call, fqNumber, isPeerUser, isRedirectingUser, peerUserDetails, redirectingUserDetails, cb) {
      var maxWaitTime = $timeout(
        function () {
          maxWaitTime = null;
          if (call.isPresent()) {
            cb && cb();
            cb = null;
          }
        },
        REVERSE_LOOKUP_MAX_TIME,
        false
      );

      UserSvc.startReverseLookUp(fqNumber, function (user) {
        $timeout.cancel(maxWaitTime);
        if (call.isPresent()) {
          var userId;
          var data;

          if (user) {
            if (isPeerUser) {
              peerUserDetails.displayName = user.displayName;
              userId = user.userId;
              data = {
                extAvatarUri: user.extAvatarUri,
                smallImageUri: user.smallImageUri,
                largeImageUri: user.largeImageUri
              };
            } else if (isRedirectingUser) {
              call.setRedirectingUser(
                redirectingUserDetails.phoneNumber,
                redirectingUserDetails.fqNumber,
                user.displayName,
                user.userId,
                redirectingUserDetails.type
              );
            }
          }
          // Regardless if the reverse lookup succeeded, we should invoke setPeerUser so the fqNumber
          // is recorded and we won't do another reverse lookup for the same number again (during this call)
          isPeerUser && call.setPeerUser(peerUserDetails.phoneNumber, peerUserDetails.displayName, userId, peerUserDetails.fqNumber, data);
          cb && cb();
        }
      });
    }

    function resolvePeerAndRedirectingUser(call, peerUserDetails, redirectingUserDetails, setCallPeerUserCb, setCallRedirectingUserCb) {
      var deferred = $q.defer();
      if (peerUserDetails && peerUserDetails.fqNumber) {
        reverseLookupAndSetUserData(call, peerUserDetails.fqNumber, true, false, peerUserDetails, null, function () {
          setCallPeerUserCb();
          deferred.resolve();
        });
      } else {
        deferred.resolve();
      }
      if (redirectingUserDetails && redirectingUserDetails.fqNumber) {
        deferred.promise.then(function () {
          reverseLookupAndSetUserData(call, redirectingUserDetails.fqNumber, false, true, null, redirectingUserDetails, setCallRedirectingUserCb);
        });
      }
    }

    function resolveParticipants() {
      if (!_conferenceParticipants.length) {
        _resolving = false;
        return;
      }
      var participant = _conferenceParticipants.splice(0, 1);
      UserSvc.startReverseLookUp(participant[0].phoneNumber, function () {
        var users = UserSvc.getUserSearchList().slice(0);
        var call = participant[0].call;
        delete participant[0].call;
        var addedParticipant = null;
        if (users.length === 1) {
          // Exact match
          addedParticipant = call.addParticipant({
            userId: users[0].userId || participant[0].userId,
            phoneNumber: participant[0].phoneNumber,
            displayName: users[0].displayName,
            participantId: participant[0].participantId,
            avatar: users[0].avatar,
            hasAvatarPicture: users[0].hasAvatarPicture
          });
        } else {
          addedParticipant = call.addParticipant(participant[0]);
        }

        // Used by iOS client to update UI e.g., after a merge call action
        if (addedParticipant && _telephonyConversation) {
          LogSvc.debug('[CstaSvc]: Publish /atccall/participant/added');
          PubSubSvc.publish('/atccall/participant/added', [call.callId, addedParticipant]);
        }

        $timeout(resolveParticipants, 0);
      });
    }

    function refreshData() {
      if ($rootScope.localUser.isATC && _telephonyConversation) {
        Object.assign(_osmoData, AtcRegistrationSvc.getAtcRegistrationData());
        _activeCall = findActiveCall();
        _heldCall = findHeldCall();
        _alertingCall = findAlertingCall();
        _webrtcRemoteCall = findWebRTCRemoteCall();
        if (_calls.length > 0) {
          _calls = _calls.filter(function (call) {
            if (call.checkState(CallState.Terminated)) {
              if (_activeCall === call) {
                _activeCall = null;
              }
              if (_heldCall === call) {
                _heldCall = null;
              }
              if (_alertingCall === call) {
                _alertingCall = null;
              }
              if (_webrtcRemoteCall === call) {
                _webrtcRemoteCall = null;
              }
              if (_wresExternalPeerInfo && _wresExternalPeerInfo.cstaCallId === call.cstaCallId) {
                _wresExternalPeerInfo = null;
              }
              return false;
            }
            return true;
          });
        }

        if (!_activeCall && !_alertingCall && !_heldCall && !_webrtcRemoteCall) {
          Utils.emptyArray(_calls);
        }
      }
    }

    function updateExistingCall(newCall) {
      for (var idx = 0; idx < _calls.length; idx++) {
        if (_calls[idx].callId === newCall.callId) {
          var atcCallInfo = _calls[idx].atcCallInfo;
          if (newCall.peerUser.phoneNumber !== _calls[idx].peerUser.phoneNumber) {
            atcCallInfo.clearServicesPermitted();
          }
          _calls[idx] = newCall;
          if (!newCall.atcCallInfo || !newCall.atcCallInfo.cstaConn) {
            _calls[idx].atcCallInfo = atcCallInfo;
          }
          return _calls[idx];
        }
      }
      return null;
    }

    function findActiveCall() {
      var activeCall = UnifyPhoneCallControlSvc.getActiveCall();
      if (!activeCall || !activeCall.isTelephonyCall) {
        return null;
      }
      var updatedCall = updateExistingCall(activeCall);
      if (updatedCall) {
        return updatedCall;
      }
      var atcActiveCall = Object.create(activeCall);
      atcActiveCall.atcCallInfo = activeCall.atcCallInfo || new AtcCallInfo();
      _calls.push(atcActiveCall);
      return atcActiveCall;
    }

    function findHeldCall() {
      var heldCall = UnifyPhoneCallControlSvc.findHeldPhoneCall();
      if (!heldCall) {
        return null;
      }
      var updatedCall = updateExistingCall(heldCall);
      if (updatedCall) {
        return updatedCall;
      }
      var atcHeldCall = Object.create(heldCall);
      atcHeldCall.atcCallInfo = heldCall.atcCallInfo || new AtcCallInfo();
      _calls.push(atcHeldCall);
      return atcHeldCall;
    }

    function findAlertingCall() {
      var alertingCall = UnifyPhoneCallControlSvc.getIncomingCall();
      if (!alertingCall || !alertingCall.isTelephonyCall) {
        return null;
      }
      var updatedCall = updateExistingCall(alertingCall);
      if (updatedCall) {
        return updatedCall;
      }
      var atcAlertingCall = Object.create(alertingCall);
      atcAlertingCall.atcCallInfo = alertingCall.atcCallInfo || new AtcCallInfo();
      _calls.push(atcAlertingCall);
      return atcAlertingCall;
    }

    function findWebRTCRemoteCall() {
      var webRTCRemoteCall = UnifyPhoneCallControlSvc.getActiveRemoteCall()[0];
      if (!webRTCRemoteCall || !webRTCRemoteCall.isTelephonyCall) {
        return null;
      }
      var updatedCall = updateExistingCall(webRTCRemoteCall);
      if (updatedCall) {
        return updatedCall;
      }
      webRTCRemoteCall.atcCallInfo = webRTCRemoteCall.atcCallInfo || new AtcCallInfo();
      _calls.push(webRTCRemoteCall);
      return webRTCRemoteCall;
    }

    function removeAtcRemoteCall(callId, noCallLog) {
      if (_atcRemoteCalls[callId]) {
        LogSvc.debug('[CstaSvc]: Remove remote call with Call Id = ' + callId);

        var remoteCall = _atcRemoteCalls[callId];
        !noCallLog && createJournalEntry(remoteCall);
        remoteCall.setCstaState(CstaCallState.Idle);

        // Update the UI
        publishAtcCallInfo(remoteCall);

        delete _atcRemoteCalls[callId];
      }
    }

    function setMissedReason(journalEntry, reason) {
      switch (reason) {
        case MissedReasonTypes.DEST_OUT_OF_ORDER:
        case MissedReasonTypes.DEST_NOT_ONBTAINABLE:
          journalEntry.missedReason = Constants.RTCItemMissed.UNREACHABLE;
          break;
        case MissedReasonTypes.REORDER_TONE:
          journalEntry.missedReason = Constants.RTCItemMissed.INVALID_NUMBER;
          break;
        case MissedReasonTypes.BUSY:
          journalEntry.missedReason = Constants.RTCItemMissed.USER_BUSY;
          break;
        case MissedReasonTypes.CANCELLED:
          journalEntry.missedReason = Constants.RTCItemMissed.CANCELED;
          break;
        case MissedReasonTypes.DECLINED:
          journalEntry.missedReason = Constants.RTCItemMissed.DECLINED;
          break;
        case MissedReasonTypes.TRANSFERRED:
          journalEntry.type = JournalEntryTypes.REGULAR;
          break;
        default:
          journalEntry.missedReason = Constants.RTCItemMissed.TEMPORARILY_UNAVAILABLE;
      }
    }

    // eslint-disable-next-line complexity
    function createJournalEntry(remoteCall) {
      // If it's a pickupNotification call the $rootScope.localUser.noCallLog should always be true, but we need
      // to add an extra check in case the OSV call log monitoring fails to be initiated
      if (($rootScope.localUser.noCallLog && !$rootScope.localUser.isOS4K) || remoteCall.pickupNotification) {
        return;
      }
      if (!remoteCall) {
        LogSvc.error('[CstaSvc]: No remoteCall');
        return;
      }
      var atcCallInfo = remoteCall.atcCallInfo;
      if (!atcCallInfo) {
        LogSvc.warn('[CstaSvc]: RemoteCall has no atcCallInfo');
        return;
      }
      if (!atcCallInfo.pickUp && !atcCallInfo.getIgnoreCall() && (remoteCall.isAtcRemote || remoteCall.isOsBizSecondCall || remoteCall.pulled)) {
        if (!_telephonyConversation) {
          LogSvc.error('[CstaSvc]: No telephony conversation');
          return;
        }
        if (!remoteCall.peerUser.phoneNumber && remoteCall.peerUser.displayName === _telephonyConversation.peerUser.displayName) {
          LogSvc.warn('[CstaSvc]: This call has no peer display information.');
          return;
        }
        var originalPartnerChanged =
          atcCallInfo.originalPartnerDisplay &&
          atcCallInfo.originalPartnerDisplay.fqn &&
          (atcCallInfo.originalPartnerDisplay.fqn !== atcCallInfo.peerFQN || !remoteCall.peerUser.displayName) &&
          !(remoteCall.pickedUp && remoteCall.direction === CallDirection.INCOMING);

        var partner = {
          phoneNumber: originalPartnerChanged ? atcCallInfo.originalPartnerDisplay.dn : remoteCall.peerUser.phoneNumber,
          displayName: originalPartnerChanged ? atcCallInfo.originalPartnerDisplay.name : remoteCall.peerUser.displayName,
          userId: (!originalPartnerChanged && remoteCall.peerUser.userId) || _telephonyConversation.peerUser.userId,
          resolvedUser: !!remoteCall.peerUser.userId && !originalPartnerChanged && !remoteCall.peerUser.shouldNotBeResolved,
          fqn: originalPartnerChanged ? atcCallInfo.originalPartnerDisplay.fqn : atcCallInfo.peerFQN
        };

        var journalEntry = {
          convId: _telephonyConversation.convId,
          starter: remoteCall.direction === CallDirection.INCOMING ? partner.userId : $rootScope.localUser.userId,
          source: remoteCall.direction === CallDirection.INCOMING ? partner.fqn : $rootScope.localUser.phoneNumber,
          destination: remoteCall.direction === CallDirection.OUTGOING ? partner.fqn : $rootScope.localUser.phoneNumber,
          startTime: remoteCall.creationTime,
          duration: remoteCall.establishedTime ? Date.now() - remoteCall.establishedTime : 0,
          type: remoteCall.establishedTime === 0 ? JournalEntryTypes.MISSED : JournalEntryTypes.REGULAR,
          participants: [
            {
              userId: $rootScope.localUser.userId,
              type: 'USER',
              phoneNumber: $rootScope.localUser.phoneNumber,
              displayName: $rootScope.localUser.displayName
            },
            {
              userId: partner.userId,
              type: 'TELEPHONY',
              phoneNumber: partner.phoneNumber,
              displayName: partner.displayName,
              resolvedUser: partner.resolvedUser
            }
          ]
        };
        if (journalEntry.type === JournalEntryTypes.MISSED) {
          setMissedReason(journalEntry, atcCallInfo.getMissedReason());
        }

        addJournalEntry(journalEntry);
      }
    }

    function addJournalEntry(journalEntry) {
      if (_primaryClient) {
        _clientApiHandler.addJournalEntry(journalEntry);
      } else {
        // Start a timeout between 1 and 2 seconds
        var delay = 1000 + 1000 * Math.random();
        $timeout(
          function () {
            if (!entryAlreadySent(journalEntry)) {
              _clientApiHandler.addJournalEntry(journalEntry);
            }
          },
          delay,
          false
        );
      }
    }

    function entryAlreadySent(journalEntry) {
      var journalParticipant = journalEntry && journalEntry.participants && journalEntry.participants[1];
      if (journalParticipant) {
        return _lastTwoCallPartners.some(function (participant, idx) {
          if (
            Utils.cleanPhoneNumber(participant.phoneNumber) === Utils.cleanPhoneNumber(journalParticipant.phoneNumber) &&
            participant.displayName === journalParticipant.displayName
          ) {
            LogSvc.debug('[CstaSvc]: Call log entry already sent by another client');
            _lastTwoCallPartners.splice(idx, 1);
            return true;
          }
          return false;
        });
      }
      return false;
    }

    function clearAtcRemoteCalls() {
      for (var key in _atcRemoteCalls) {
        if (_atcRemoteCalls.hasOwnProperty(key)) {
          removeAtcRemoteCall(key, true);
        }
      }
    }

    function findAtcRemoteCall(callId) {
      return (
        Object.values(_atcRemoteCalls).find(function (call) {
          return call.callId === callId;
        }) || null
      );
    }

    function findAtcRemoteEstablishedCall() {
      return (
        Object.values(_atcRemoteCalls).find(function (call) {
          return call.isEstablished;
        }) || null
      );
    }

    function findAtcRemoteQueuedCall() {
      return (
        Object.values(_atcRemoteCalls).find(function (call) {
          return call.atcCallInfo && call.atcCallInfo.isQueuedIncomingCall();
        }) || null
      );
    }

    function findAtcRemoteHeldCall() {
      return (
        Object.values(_atcRemoteCalls).find(function (call) {
          return call.isHolding();
        }) || null
      );
    }

    function findOtherAtcRemoteCall(callId) {
      return (
        Object.values(_atcRemoteCalls).find(function (call) {
          return callId !== call.cstaCallId;
        }) || null
      );
    }

    function processQueuedCall(position) {
      if ($rootScope.localUser.isOsBizCTIEnabled) {
        // If there is a queued call, then allow it to ring now
        var queuedCall = findAtcRemoteQueuedCall();
        if (queuedCall) {
          LogSvc.debug('[CstaSvc]: There is a call waiting. Allow it to ring');
          queuedCall.atcCallInfo.setQueuedIncomingCall(false);
          if (position === Targets.WebRTC) {
            createCampOnLocalCall(queuedCall);
          } else {
            publishAtcCallInfo(queuedCall);
          }
        }
      }
    }

    function createAtcRemoteCall(callId) {
      if (!_telephonyConversation) {
        return null;
      }
      var conversation = Object.create(_telephonyConversation);
      conversation.rtcSessionId = callId;
      return new AtcRemoteCall(conversation);
    }

    function createAtcRemoteCallFailedEvent(failedEvent, callId) {
      var call = createAtcRemoteCall(callId);
      // As we are at Incoming case, fill all missing fields accordingly
      // Always Incoming calls
      call.direction = CallDirection.INCOMING;
      // Extract display info from networkCallingDevice if exists otherwise from callingDevice
      var partnerDeviceId = getCallingDevice(failedEvent);
      var display = getDisplayInfo(partnerDeviceId);
      call.setPeerUser(display.dn, display.name);
      // New flag, so we can use it at createJournalEntry to force user to be resolved from backend
      call.peerUser.shouldNotBeResolved = true;
      _atcRemoteCalls[callId] = call;
      LogSvc.debug('[CstaSvc]: Created new Remote Call object from FailedEvent for callID= ', callId);

      return call;
    }

    function getCallingDevice(event) {
      return $rootScope.localUser.isOsBizCTIEnabled && event.networkCallingDevice ? event.networkCallingDevice : event.callingDevice;
    }

    function peerUserResolveFn(nCall, peerDn, peerFqn, peerName) {
      var deferred = $q.defer();
      setCallPeerUser(nCall, peerDn, peerFqn, peerName, function () {
        deferred.resolve();
      });
      return deferred.promise;
    }

    function createCallFromSnapshot(snapshotCallResp) {
      var data = snapshotCallResp.endpoints;
      var callingDevice = snapshotCallResp.callingDevice;
      var calledDevice = snapshotCallResp.calledDevice;
      var direction = isMyDeviceId(callingDevice) ? CallDirection.OUTGOING : CallDirection.INCOMING;

      if (!data || data.length < 2) {
        return null;
      }

      var newCall = null;
      var callId = data[0].callIdentifier.cID;

      var callState = data.length > 2 ? CstaCallState.Conference : CstaCallState.Active;

      // First process our device
      data.forEach(function (ep) {
        if (!callState) {
          return;
        }

        if (isMyDeviceId(ep.deviceOnCall)) {
          if (VALID_LOCAL_STATES.indexOf(ep.localConnectionState) === -1) {
            callState = null;
            return;
          }
          var position = getCallPosition(ep.deviceOnCall, snapshotCallResp.epid);

          if (position === Targets.WebRTC && (_activeCall || _webrtcRemoteCall)) {
            if (_activeCall) {
              newCall = _activeCall;
            } else {
              newCall = _webrtcRemoteCall;
              position = Targets.Other;
            }
          } else {
            newCall = createAtcRemoteCall(callId);
          }

          if (!newCall.direction) {
            newCall.direction = direction;
          }
          newCall.setPosition(position);
          newCall.atcCallInfo.setCstaConnection(ep.callIdentifier);

          // Extend top level servicesPermitted (Private data) with my endpoint's service permitted.
          Object.assign(snapshotCallResp.servicesPermitted, ep.servicesPermitted);

          if (ep.localConnectionState === 'queued') {
            callState = CstaCallState.Parked;
          } else if (ep.localConnectionState === 'hold') {
            if (data.length > 2) {
              callState = CstaCallState.ConferenceHolding;
            } else {
              callState = callState === CstaCallState.Held ? CstaCallState.HoldOnHold : CstaCallState.Holding;
            }
          }
        }
      });

      // Now process other devices
      var shouldPublishAtc = false;
      if (newCall && data.length === 2) {
        data.forEach(function (ep) {
          if (!isMyDeviceId(ep.deviceOnCall)) {
            if (VALID_PARTNER_STATES.indexOf(ep.localConnectionState) === -1) {
              callState = null;
              return;
            }
            var display;
            if (newCall.direction === CallDirection.INCOMING) {
              display = snapshotCallResp.presentationRestrictedDevice1 ? getDisplayInfo() : getDisplayInfo(callingDevice);
            } else if (newCall.direction === CallDirection.OUTGOING) {
              display = getDisplayInfo(calledDevice);
            }

            newCall.atcCallInfo.setPartnerDisplay(display);
            deferredArr.push(peerUserResolveFn.bind(null, newCall, newCall.atcCallInfo.peerDn, newCall.atcCallInfo.peerFQN, newCall.atcCallInfo.peerName));
            shouldPublishAtc = true;

            if (ep.localConnectionState === 'hold') {
              callState = callState === CstaCallState.Holding ? CstaCallState.HoldOnHold : CstaCallState.Held;
            }
          }
        });
      }

      // Building conference participants
      if (newCall && data.length > 2) {
        data.forEach(function (ep) {
          if (!isMyDeviceId(ep.deviceOnCall)) {
            if (VALID_PARTNER_STATES.indexOf(ep.localConnectionState) === -1) {
              callState = null;
              return;
            }
            var participantDisplay = (ep && getDisplayInfo(ep.deviceOnCall)) || getDisplayInfo(ep.callIdentifier.dID);
            _conferenceParticipants.push({
              call: newCall,
              userId: ep.callIdentifier.dID,
              participantId: ep.callIdentifier.dID,
              phoneNumber: participantDisplay.dn,
              displayName: participantDisplay.name
            });
            if (!_resolving) {
              _resolving = true;
              resolveParticipants();
            }
          }
        });
      }
      if (callState) {
        newCall.atcCallInfo.setServicesPermitted(snapshotCallResp.servicesPermitted);
        newCall.setCstaState(callState);
        if (newCall.isAtPosition(Targets.WebRTC)) {
          if (data.length <= 2) {
            LogSvc.debug('[CstaSvc]: This is a webRTC call. The reconnection is being handled by UnifyPhoneCallControlSvc');
            return null;
          }
          var remoteCall = findWebRTCRemoteCall();
          if (!remoteCall) {
            return null;
          }
          LogSvc.debug('[CstaSvc]: This is a webRTC conference call. Updating existing call properties.');

          remoteCall.atcCallInfo = newCall.atcCallInfo;
          remoteCall.peerUser = newCall.peerUser;
          remoteCall.participants = newCall.participants;
          remoteCall.direction = newCall.direction;

          return remoteCall;
        }
        _atcRemoteCalls[callId] = newCall;
        // If peer user update took place, update UI
        if (shouldPublishAtc) {
          var atcFn = function (c) {
            return $q.resolve(publishAtcCallInfo(c));
          };
          deferredArr.push(atcFn.bind(null, newCall));
          enqueueChain();
        }
        LogSvc.debug('[CstaSvc]: Created new Remote Call object: ', newCall);
        return newCall;
      }
      return null;
    }

    function isSecondaryLine(deviceId) {
      return deviceId.includes('keyId=');
    }

    function isMyDeviceId(deviceId) {
      if (!deviceId || deviceId.rstr === '') {
        return false;
      }
      // For OSbiz, trim display before comparison
      var device = getDeviceFromDeviceId(deviceId);

      if (!$rootScope.localUser.isOsBizCTIEnabled && !$rootScope.localUser.isMiVB && !device.includes(_osmoData.onsFQN)) {
        return false;
      }

      if ($rootScope.localUser.isOsBizCTIEnabled && device !== _osmoData.onsFQN) {
        //  For OSBiz, check also device list if onsFQN does not match
        if (!_osmoData.osmoDeviceList || !_osmoData.osmoDeviceList.includes(device)) {
          return false;
        }
      }

      if ($rootScope.localUser.isMiVB && device !== _osmoData.onsFQN) {
        // For MiVB, deviceId can be in the format "5602/5602", keep only the last part after "/"
        if (device.includes('/')) {
          device = device.split('/').pop();
        }
        if (!_osmoData.osmoDeviceList || !_osmoData.osmoDeviceList.includes(device)) {
          return false;
        }
      }
      if (device.includes('uid=')) {
        return false;
      }
      return !isSecondaryLine(device);
    }

    function isVMDeviceId(deviceId) {
      return !!deviceId && isVMNumber(getDisplayInfo(deviceId).dn);
    }

    function isOSBizOns(deviceId) {
      return $rootScope.localUser.isOsBizCTIEnabled && getDeviceFromDeviceId(deviceId) === _osmoData.onsFQN;
    }

    function extractOnd(deviceId) {
      if ($rootScope.localUser.isOsBizCTIEnabled) {
        // If case of mulap configuration, format at this point is "N<dn/dn>display name"
        // In case of station configuration, deviceId is "N<dn>display name"
        return (_osmoData.osmoDeviceList && /\/(\+?\**\d+)/.exec(deviceId)) || /<(\+?\**\d+)/.exec(deviceId) || [deviceId, deviceId];
      } else if ($rootScope.localUser.isMiV5000) {
        return (_osmoData.osmoDeviceList && /\/(\+?\d+!\d+)/.exec(deviceId)) || /<(\+?\d+!\d+)/.exec(deviceId) || [deviceId, deviceId];
      } else if ($rootScope.localUser.isMiVB) {
        return (_osmoData.osmoDeviceList && /\/(\+?\**.*)/.exec(deviceId)) || /<(\+?\**\d+)/.exec(deviceId) || [deviceId, deviceId];
      } else {
        return /ond=(\+?\d+)/.exec(deviceId);
      }
    }

    function getCallPosition(deviceId, epid, direction) {
      LogSvc.debug('[CstaSvc]: Get call position for ' + deviceId);

      if (!isMyDeviceId(deviceId)) {
        LogSvc.error('[CstaSvc]: Invalid device ID (' + deviceId + '). Cannot determine position.');
        return Targets.Other;
      }

      var position = Targets.Other;
      var tmp = extractOnd(deviceId);
      if (!tmp || !tmp[1]) {
        position = Targets.Desk;
      } else {
        var ond = tmp[1];
        if (ond === _osmoData.osmoFQN) {
          if (epid && epid !== _osmoData.epid) {
            // This is a call on another OSMO
            position = Targets.Other;
          } else if (isOSBizCallWithAternativeNumber(direction)) {
            // OSBiz: DE/EE in call to alternative number is the same as for local call.
            // Call cannot be picked up locally.
            position = Targets.Cell;
          } else {
            // This is a call on WebRTC
            position = Targets.WebRTC;
          }
        } else if (ond === _osmoData.cell) {
          position = Targets.Cell;
        } else if (ond === _osmoData.parkingDn) {
          position = Targets.Queue;
        } else if (_osmoData.osmoDeviceList && _osmoData.osmoDeviceList.includes(ond)) {
          position = Targets.Desk;
        }
      }

      LogSvc.debug('[CstaSvc]: Call position is:', position.name);
      return position;
    }

    function getDisplayInfo(deviceId) {
      var display = {
        fqn: '', // Fully Qualified Number
        dn: _numberNotAvailable, // Display Number
        name: '' // Display Name
      };

      // deviceId is not valid if the CSTA device Id is <notKnown/> or <restricted/>
      if (deviceId) {
        // CSTA Device Id Pattern
        var pattern = /N<([^>]*)>([^;]*)((?:[^;]*;)*(?:displayNumber=(\d*)))?(?:;.*)*/;
        var tmp = pattern.exec(deviceId);
        if (tmp) {
          if (($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiVB) && tmp[1]) {
            // osbiz sends number as ons/ond if the peer is in the same osbiz and configured in a mulap. Use ons only
            // MiVB sends number as MDUG/member_number instead of just MDUG.
            tmp[1] = tmp[1].replace(/\/.*/, '');
          }

          display.fqn = tmp[1] || '';
          display.name = tmp[2] || '';
          display.dn = tmp[4] || tmp[1] || _numberNotAvailable;
        } else {
          //4K sends N+1122334455 if name is not available
          pattern = /N([^;]*)/;
          tmp = pattern.exec(deviceId);
          if (tmp) {
            display.fqn = tmp[1] || '';
            display.dn = tmp[1] || _numberNotAvailable;
          } else {
            display.fqn = deviceId;
            display.dn = deviceId;
          }
        }
      }

      LogSvc.debug('[CstaSvc]: Retrieved partner display: ', display);
      return display;
    }

    function updateDisplayNameFromPeerUser(display, peerUser, atcCallInfo) {
      if (_wresExternalPeerInfo && atcCallInfo && _wresExternalPeerInfo.cstaCallId === atcCallInfo.getCstaCallId()) {
        LogSvc.debug('[CstaSvc] updateDisplayNameFromPeerUser: update peer displayName with wresExternal name');
        display.name = _wresExternalPeerInfo.displayName;
      }

      // NGTC-10026: If fqn changes, early return will avoid updating from peerUser.displayName (eg. alternative -> VM)
      // NGTC-15762: If fqn changes, we should clear userId, to reverse lookup user (eg. MLHG case)
      if (atcCallInfo && atcCallInfo.getPartnerDisplay().fqn !== '' && atcCallInfo.getPartnerDisplay().fqn !== display.fqn) {
        peerUser.userId = null;
        return;
      }

      // If no display name is available from the CSTA event check if it is already available from peerUser
      if (
        !display.name &&
        display.dn !== _numberNotAvailable &&
        peerUser.displayName &&
        peerUser.displayName !== 'anonymous' &&
        peerUser.displayName !== _numberNotAvailable
      ) {
        display.name = peerUser.displayName;
      }
    }

    function checkSSTIsPrivateDevice2(event, direction) {
      var call;
      //In case of outgoing call when PR2 is present for OS4K
      if (direction === CallDirection.OUTGOING && (event.presentationRestrictedDevice2 || event.presentationRestrictedDevice13)) {
        //Blind transfer cases
        if (event.cause === 'singleStepTransfer' && event.localConnectionInfo === 'connected') {
          call = findWebRTCCall(event.connection.cID) || _atcRemoteCalls[event.connection.cID];
          if (call) {
            return true;
          }
          //NGTC-4099: Single step transfer established event
        } else if (event.name === 'EstablishedEvent' && event.cause === 'redirected' && event.localConnectionInfo === 'connected') {
          call = findWebRTCCall(event.establishedConnection.cID) || _atcRemoteCalls[event.establishedConnection.cID];
          if (call) {
            return true;
          }
          //NGTC-3750: DNIT with agent supress number
        } else if (
          event.presentationRestrictedDevice2 &&
          event.calledDevice &&
          getDisplayInfo(event.presentationRestrictedDevice2).dn !== getDisplayInfo(event.calledDevice).dn
        ) {
          return true;
          //NGTC-4278: OS4k - Number is displayed in CCW when the call is forwarded to external secret number
          //(2nd Delivered or Established Event has no info for pr1 or pr2 instead we are having pr13)
        } else if (
          event.presentationRestrictedDevice13 &&
          event.calledDevice &&
          getDisplayInfo(event.presentationRestrictedDevice13).dn !== getDisplayInfo(event.calledDevice).dn
        ) {
          return true;
        }
      }
      return false;
    }

    function findWebRTCCall(callId) {
      refreshData();
      // Find the local WebRTC call that matches the callId
      var calls = UnifyPhoneCallControlSvc.getPhoneCalls();
      var unassociatedCalls = [];
      for (var idx = 0; idx < calls.length; idx++) {
        if (!calls[idx].atcCallInfo || !calls[idx].atcCallInfo.getCstaCallId()) {
          var redirectingUser = calls[idx].getRedirectingUser();
          calls[idx].atcCallInfo = new AtcCallInfo();
          if (redirectingUser) {
            calls[idx].atcCallInfo.redirectingUser = redirectingUser;
          }
          unassociatedCalls.push(calls[idx]);
        } else if (calls[idx].atcCallInfo.getCstaCallId() === callId) {
          _activeCall = calls[idx];
          return _activeCall;
        }
      }
      if (_alertingCall && _alertingCall.atcCallInfo.getCstaCallId() === callId) {
        return _alertingCall;
      } else if (_alertingCall && !_alertingCall.atcCallInfo.getCstaCallId()) {
        unassociatedCalls.push(_alertingCall);
      }

      return unassociatedCalls[0];
    }

    function findLocalCallByCstaCID(localConnection) {
      var call = _calls.find(function (c) {
        return c.atcCallInfo && c.atcCallInfo.getCstaCallId() === localConnection.cID;
      });
      //If call is not found in the _calls array check if it is the last ended call.
      if (!call && _lastEndedCall && _lastEndedCall.atcCallInfo && _lastEndedCall.atcCallInfo.getCstaCallId() === localConnection.cID) {
        call = _lastEndedCall;
      }
      //_lastEnded call is for local call, check also webRTC remote calls
      !call && (call = findWebRTCRemoteCall(localConnection.cID));
      return call;
    }

    function isForwardingImmediate(forwardingType) {
      return (
        forwardingType === 'forwardImmediate' ||
        ($rootScope.localUser.isOsBizCTIEnabled && (forwardingType === 'forwardImmInt' || forwardingType === 'forwardImmExt'))
      );
    }

    function isForwardingNoAnswer(forwardingType) {
      return forwardingType === 'forwardNoAns';
    }

    function isForwardingBusy(forwardingType) {
      return forwardingType === 'forwardBusy';
    }

    function isOSBizCallWithAternativeNumber(direction) {
      return (
        $rootScope.localUser.isOsBizCTIEnabled &&
        $rootScope.localUser.reroutingPhoneNumber &&
        isSelectedRouting(RoutingOptions.AlternativeNumber) &&
        direction === CallDirection.INCOMING
      );
    }

    function snapshotRemoteCalls() {
      var sendGLDI = true;
      clearAtcRemoteCalls();
      refreshData();
      _that.snapshotDevice(function (snapshotDevErr, snapshotDevResp) {
        _snapshotPerformed = true;
        if (snapshotDevErr) {
          if (snapshotDevErr.indexOf('deviceOutOfService') > -1) {
            _onsUnregistered = true;
            publishCstaDeviceChanged();
          }
          return;
        } else {
          // With OSV-8235, OSV is enhanced to send a new field in the private data of the snapshot device
          // response to indicate if the desk phone is registered or not
          if ($rootScope.localUser.isOSV && snapshotDevResp.hasOwnProperty('deskPhoneRegistered')) {
            _onsUnregistered = !snapshotDevResp.deskPhoneRegistered;
            sendGLDI = false;
          } else {
            _onsUnregistered =
              ($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiV5000 || $rootScope.localUser.isMiVB) &&
              !(_osmoData.osmoDeviceList && _osmoData.osmoDeviceList.length > 1);
          }
          publishCstaDeviceChanged();
        }

        // If the ONS is a profile only OSV subscriber, the snapshot device response cannot be used to know
        // if there is a registered desk phone. We need to send GetLogicalDeviceInformation request.
        if (sendGLDI && $rootScope.localUser.isOSV && !_onsUnregistered) {
          _that.getLogicalDeviceInformation();
        }

        if (!snapshotDevResp.activeCalls) {
          return;
        }

        try {
          snapshotDevResp.activeCalls.forEach(function (call) {
            if (call.localCallState.length < 2) {
              return;
            }

            if (call.localCallState.length === 2) {
              if (VALID_LOCAL_STATES.indexOf(call.localCallState[0]) === -1 || VALID_PARTNER_STATES.indexOf(call.localCallState[1]) === -1) {
                return;
              }
            }

            _that.snapshotCall(call.connectionId, function (snapshotCallErr, snapshotCallResp) {
              if (!snapshotCallErr && snapshotCallResp) {
                var newCall = createCallFromSnapshot(snapshotCallResp);
                if (newCall) {
                  // Notify the UI about the remote call
                  publishAtcCallInfo(newCall);
                }
              }
            });
          });
        } catch (e) {
          LogSvc.error(e);
        }
      });
    }

    function isDNDSupported() {
      return $rootScope.localUser.isOsBizCTIEnabled && $rootScope.localUser.pbxDNDSupported;
    }

    function isOSBizCallRoutingSupported() {
      return $rootScope.localUser.pbxCallRoutingOptionsSupported;
    }

    function getGlobalOS4KClassicHuntState() {
      return !!($rootScope.localUser && $rootScope.localUser.isOS4KClassicHuntReady);
    }

    function getGlobalAgentStateReady() {
      return !!($rootScope.localUser && $rootScope.localUser.agentStateReady);
    }

    function getAgentStatePromise(device) {
      return new $q(function (resolve) {
        _that.getAgentState(device, function (err, data) {
          if (err) {
            LogSvc.error('[CstaSvc]: Failed to get Agent State. ', err);
          }
          resolve(data || null);
        });
      });
    }

    function getOSBizDeviceState() {
      // Check device state of client and desk phone if mulap user
      if (!isOSBizCallRoutingSupported() || !_osmoData || !_osmoData.osmoDeviceList || _osmoData.osmoDeviceList.length <= 1) {
        return;
      }

      var agentStatePromises = [
        getAgentStatePromise(_osmoData.osmoDeviceList[0]), // Desk
        getAgentStatePromise(_osmoData.osmoDeviceList[1]) // Client
      ];

      $q.all(agentStatePromises).then(function (agentStates) {
        var oldRoutingOption = $rootScope.localUser.selectedRoutingOption;

        // Check desk
        var data = agentStates[0];
        if (data) {
          if (data.agentState !== AgentState.Ready) {
            setRoutingOption(RoutingOptions.UnifyPhoneClient);
          } else if (isSelectedRouting(RoutingOptions.UnifyPhoneClient)) {
            setRoutingOption(RoutingOptions.DefaultRouting);
          }
        }
        // Check client
        data = agentStates[1];
        if (data) {
          if (data.agentState !== AgentState.Ready) {
            setRoutingOption(RoutingOptions.DeskPhone);
          } else if (isSelectedRouting(RoutingOptions.DeskPhone)) {
            setRoutingOption(RoutingOptions.DefaultRouting);
          }
        }

        if ($rootScope.localUser.selectedRoutingOption !== oldRoutingOption) {
          LogSvc.debug('[CstaSvc]: Publish /csta/agentStateUpdated');
          PubSubSvc.publish('/csta/agentStateUpdated');
        }
      });
    }

    function snapshotRemoteCallsAndGetFeatureData() {
      snapshotRemoteCalls();
      _that.getForwarding(handleGetForwardingResp);

      if (!$rootScope.localUser.isOsBizCTIEnabled) {
        _that.getDoNotDisturb(handleGetDoNotDisturbResp);
      }
      _that.getAgentState(null, handleGetAgentStateResponseATC);
    }

    function getDesktopDevice() {
      var tmp = _osmoData.osmoDeviceList.filter(function (device) {
        if (device === _osmoData.osmoFQN) {
          return false;
        }
        return true;
      });
      return tmp[0];
    }

    function buildNewDestination(target, destination, withoutEpid, withOSBizTargetDevice) {
      var newDestination = !$rootScope.localUser.isOsBizCTIEnabled && !$rootScope.localUser.isMiVB ? _osmoData.onsFQN + ';ond=' : '';

      switch (target) {
        case Targets.WebRTC:
          if (withOSBizTargetDevice && _osmoData.osmoDeviceList && _osmoData.osmoDeviceList.length > 1) {
            newDestination = 'N<' + _osmoData.onsFQN + '/' + _osmoData.osmoDeviceList[1] + '>';
          } else if (withoutEpid) {
            newDestination += _osmoData.osmoFQN;
          } else {
            newDestination += _osmoData.osmoFQN + ';epid=' + _osmoData.epid;
          }
          break;

        case Targets.Cell:
          if ($rootScope.localUser.isOsBizCTIEnabled && $rootScope.localUser.reroutingPhoneNumber) {
            if (isSelectedRouting(RoutingOptions.AlternativeNumber)) {
              if (_osmoData.osmoDeviceList && _osmoData.osmoDeviceList.length > 1) {
                newDestination = 'N<' + _osmoData.onsFQN + '/' + _osmoData.osmoDeviceList[1] + '>';
              } else {
                newDestination = _osmoData.onsFQN;
              }
            } else {
              newDestination = $rootScope.localUser.reroutingPhoneNumber;
            }
          } else if (_osmoData.cell) {
            newDestination += _osmoData.cell;
          } else {
            LogSvc.error('[CstaSvc]: Cell phone is not configured');
            return null;
          }
          break;

        case Targets.Desk:
          newDestination += _osmoData.onsFQN;
          if (withOSBizTargetDevice && _osmoData.osmoDeviceList) {
            newDestination = 'N<' + newDestination + '/' + _osmoData.osmoDeviceList[0] + '>';
          }
          var desktop;
          if ($rootScope.localUser.isMiV5000) {
            desktop = getDesktopDevice();
            if (desktop) {
              newDestination = _osmoData.onsFQN + '/' + desktop;
            } else {
              LogSvc.error('[CstaSvc]: Desk phone is not configured');
              return null;
            }
          }
          break;

        case Targets.VM:
          newDestination = _osmoData.vm;
          break;

        case Targets.Other:
          if (destination) {
            newDestination += destination;
          } else {
            // If destination is not set, Targets.Other means deflect to Voice Mail in the UI
            newDestination = _osmoData.vm;
          }
          break;

        default:
          LogSvc.error('[CstaSvc]: Invalid target: ' + target);
          return null;
      }
      return newDestination;
    }

    function isDssCallMove(localConnectionInfo, calling, called) {
      if (
        $rootScope.localUser.isOsBizCTIEnabled &&
        localConnectionInfo === 'connected' &&
        getCallPosition(calling) === Targets.Desk &&
        getCallPosition(called) === Targets.WebRTC
      ) {
        LogSvc.debug('[CstaSvc]: DSS call move from desk to client');
        return true;
      }
      return false;
    }

    // Calculate transfer-failed CallId
    function calculateTransferFailCallId(newCallId) {
      // OSV: FailedCallId = transferredCallId's first 2 hex byte + 2
      // MiVB: FailedCallId = transferredCallId's hex + 2 (HYD-4181, MIVB-43334)
      var failedCallId;
      if ($rootScope.localUser.isMiVB) {
        failedCallId = (parseInt(newCallId, 16) + 2).toString(16).toUpperCase();
      } else {
        var pre = (parseInt(newCallId.substring(0, 2), 16) + 2).toString(16).toUpperCase();
        var post = newCallId.substring(2);

        if (pre.length < 2) {
          failedCallId = '0' + pre + post;
        } else {
          failedCallId = pre + post;
        }
      }
      LogSvc.debug('[CstaSvc]: FailedCallId to be tracked: ' + failedCallId);
      return failedCallId;
    }

    function getRegistrationData(cb) {
      Object.assign(_osmoData, AtcRegistrationSvc.getAtcRegistrationData());
      _regData = {
        subscriber: $rootScope.localUser.cstaNumber
      };
      if (!_regData.subscriber || !_osmoData) {
        cb && cb('There is no registered user.');
        return false;
      }
      return true;
    }

    function sendCstaRequest(msg, cb, keysToOmitFromLogging) {
      var cstaRq = _cstaParser.genCstaRequest(msg);
      if (!cstaRq) {
        LogSvc.error('[CstaSvc]: Failed to build CSTA request: ' + msg.request);
        cb && cb('Failed to send the request');
        return;
      }

      if (_regState !== AtcRegistrationState.Registered) {
        cb && cb('Client is not registered with ATC');
        return;
      }

      var data = {
        content: {
          type: AtcMessage.CSTA,
          phoneNumber: $rootScope.localUser.cstaNumber,
          CSTA: cstaRq
        },
        destUserId: $rootScope.localUser.associatedTelephonyUserID
      };

      if (keysToOmitFromLogging) {
        data.keysToOmitFromLogging = keysToOmitFromLogging.map(function (k) {
          return 'content.CSTA.' + k;
        });
      }

      // Send request
      _userToUserHandler.sendAtcRequest(data, function (error, rs) {
        $rootScope.$apply(function () {
          if (error) {
            LogSvc.warn('[CstaSvc]: ', error);
            cb && cb(error);
          } else {
            var parsedResp = _cstaParser.parseResponse(msg.request, rs.CSTA);
            if (!parsedResp && parsedResp !== '') {
              cb && cb('Failed to parse response from server');
            } else if (parsedResp.error) {
              if (parsedResp.errorCategory === 'Operation Error' && parsedResp.errorValue === 'invalidConnectionID') {
                if (msg.reason !== 'busy' && msg.request !== 'AcceptCall' && msg.request !== 'DeflectCall') {
                  snapshotRemoteCalls();
                }
              }
              cb && cb(parsedResp.errorCategory + ' - ' + parsedResp.errorValue);
            } else {
              LogSvc.debug('[CstaSvc]: Parsed CSTA Response: ', parsedResp);
              cb && cb(null, parsedResp);
            }
          }
        });
      });
    }

    // Store Transferred Calls initiated from the client
    // For SingleStepTransfer and SilentHandover, we need to track the transfer-failed CallId
    // To determine the transfer result
    function storeTransferredCall(callId, rs) {
      if (rs.transferredCall && !rs.seamlessHandover) {
        _transferredCalls[callId] = {
          // HYD-4181, MIVB-43334: MiVB rs.transferredCall.cID call id is not used in the flow of transfer
          //  e.g. in transfer event after transfer request
          //  that's why for correlation reasons we keep the current call id of the call that is supposed
          //  to be transferred
          newCallId: !$rootScope.localUser.isMiVB ? rs.transferredCall.cID : callId,
          failedCallId: calculateTransferFailCallId(rs.transferredCall.cID),
          timeout: $timeout(function () {
            delete _transferredCalls[callId];
          }, TRANSFER_TIMEOUT)
        };
      }
    }

    function isTargetAllowed(call, target) {
      if (!call || !getRegistrationData()) {
        return false;
      }

      if (target !== Targets.Other && call.isAtPosition(target)) {
        return false;
      }

      var myDevices = getMyDevices();
      if (myDevices.includes(target)) {
        return true;
      } else {
        LogSvc.error('[CstaSvc]: isTargetAllowed invoked with invalid target: ' + target);
        return false;
      }
    }

    function getDeviceFromDeviceId(deviceId) {
      var device = deviceId;
      if ($rootScope.localUser.isOsBizCTIEnabled) {
        var tmp = extractOnd(deviceId);
        if (tmp && tmp[1]) {
          device = tmp[1];
        }
      }
      return device;
    }

    function syncRoutingOption() {
      if ($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiV5000) {
        return;
      }
      LogSvc.debug('[CstaSvc]: Synchronize the selected routing option');
      _that.updateRoutingOption($rootScope.localUser.selectedRoutingOption);
    }

    function publishAtcCallInfo(call) {
      if (!call) {
        return;
      }
      // Notify the UI about the remote call
      LogSvc.debug('[CstaSvc]: Publish /atccall/info event');
      PubSubSvc.publish('/atccall/info', [call]);
    }

    function publishCstaDeviceChanged() {
      var hasDesk = !_onsUnregistered;
      var hasCell = !!((_osmoData && _osmoData.cell) || $rootScope.localUser.reroutingPhoneNumber);

      LogSvc.debug('[CstaSvc]: Publish /csta/deviceChange event. desk=' + hasDesk + ', cell=' + hasCell);
      PubSubSvc.publish('/csta/deviceChange');
    }

    function getCstaDeviceId() {
      if (!$rootScope.localUser.isOsBizCTIEnabled) {
        return $rootScope.localUser.isOSV ? Utils.normalizeDn($rootScope.localUser.cstaNumber) : $rootScope.localUser.cstaNumber;
      } else {
        return _osmoData.onsFQN;
      }
    }

    function moveOsBizCalls(callId) {
      var localCall = UnifyPhoneCallControlSvc.findOsBizFirstCall();
      var secondCall = UnifyPhoneCallControlSvc.findOsBizSecondCall();
      if (localCall && secondCall) {
        if (localCall.atcCallInfo.getCstaCallId() === callId) {
          LogSvc.debug('[CstaSvc]: First local call with callId: ', callId);
          LogSvc.debug('[CstaSvc]: Second local call with callId: ', secondCall.atcCallInfo.getCstaCallId());
          UnifyPhoneCallControlSvc.moveOsBizCalls(localCall, secondCall);
          return true;
        } else if (secondCall.atcCallInfo.getCstaCallId() === callId) {
          LogSvc.debug('[CstaSvc]: Second local call with callId: ', callId);
          LogSvc.debug('[CstaSvc]: First local call with callId: ', localCall.atcCallInfo.getCstaCallId());
          UnifyPhoneCallControlSvc.moveOsBizCalls(secondCall, localCall);
          return true;
        }
      } else if (!localCall && !secondCall) {
        var remoteCall = findWebRTCRemoteCall();
        if (remoteCall && remoteCall.atcCallInfo && remoteCall.atcCallInfo.getCstaCallId() === callId) {
          LogSvc.debug('[CstaSvc]: Remote call with callId: ', callId);
          var remoteHeldCall = findAtcRemoteHeldCall() || findOtherAtcRemoteCall(callId);
          if (remoteHeldCall) {
            LogSvc.debug('[CstaSvc]: Second remote call with callId: ', remoteHeldCall.atcCallInfo.getCstaCallId());
            var origAtcCallInfo = remoteCall.atcCallInfo;
            remoteCall.atcCallInfo = remoteHeldCall.atcCallInfo;
            remoteCall.setPeerUser(remoteCall.atcCallInfo.peerDn, remoteCall.atcCallInfo.peerName);
            remoteHeldCall.atcCallInfo = origAtcCallInfo;
            delete _atcRemoteCalls[remoteCall.atcCallInfo.getCstaCallId()];
            _atcRemoteCalls[origAtcCallInfo.getCstaCallId()] = remoteHeldCall;
            removeAtcRemoteCall(remoteHeldCall.atcCallInfo.getCstaCallId());
            return true;
          }
        }
      }
      return false;
    }

    function isCallAlreadyNotified(callingFqn, callingName) {
      // Checks if there is a already a handled notification for this call. With 4K the team notifications don't
      // have the FQN, so we need to check if the name is the same
      return Object.values(_atcRemoteCalls).some(function (call) {
        return call.atcCallInfo.pickUp && ($rootScope.localUser.isOSV ? call.atcCallInfo.peerFQN === callingFqn : call.atcCallInfo.peerName === callingName);
      });
    }

    function notifyCall(data) {
      LogSvc.debug('[CstaSvc]: New pickup call for: ', data.calledDisplay.displayName);
      var call = createAtcRemoteCall(data.callId);
      call.atcCallInfo.pickUp = true;
      call.setPosition(Targets.Other);
      call.atcCallInfo.setPartnerDisplay(data.callingDisplay);
      _atcRemoteCalls[data.callId] = call;
      if (data.event) {
        // Notification of OSV Group Pickup feature
        call.atcCallInfo.setServicesPermitted(data.event.servicesPermitted);
        call.atcCallInfo.setCstaConnection(data.event.connection);
      } else if (data.callingUserId) {
        // Notification of Team Pickup feature
        call.pickupSession = data.callId;
        call.peerGtcUserId = data.callingUserId;
        call.setRedirectingUser(
          data.calledDisplay.dn,
          data.calledDisplay.fqn,
          data.calledDisplay.name,
          data.calledUserId,
          RedirectionTypes.CallPickupNotification
        );
      }

      call.setCstaState(CstaCallState.Ringing);
      call.direction = CallDirection.INCOMING;

      call.atcCallInfo.setOriginalPartnerDisplay(data.calledDisplay);
      setCallPeerUser(call, call.atcCallInfo.peerDn, call.atcCallInfo.peerFQN, call.atcCallInfo.peerName, function () {
        if (!data.callingUserId) {
          setRedirectingUser(
            call,
            data.calledDisplay.dn,
            data.calledDisplay.fqn,
            data.calledDisplay.name,
            RedirectionTypes.CallPickupNotification,
            function () {
              showPickupNotification(call);
            }
          );
        } else {
          showPickupNotification(call);
        }
      });
    }

    function isTwoPartyCall(call) {
      return (call.isRemote && call.participants.length === 1) || (!call.isRemote && call.participants.length === 2);
    }

    function isTwoPartyPulledCall(call) {
      return (call.isRemote && call.participants.length === 2) || (!call.isRemote && call.participants.length === 1);
    }

    function eventForConference(droppedConnectionDID, call) {
      call.participants.some(function (p) {
        if (p.participantId === droppedConnectionDID) {
          call.removeParticipant(p.userId);
          return true;
        } else if ($rootScope.localUser.isMiV5000 && getDisplayInfo(p.participantId).dn === getDisplayInfo(droppedConnectionDID).dn) {
          call.removeParticipant(p.userId);
          return true;
        }
        return false;
      });
      var lastParticipant;
      if (isTwoPartyCall(call)) {
        lastParticipant = call.participants[call.isRemote ? 0 : 1];
        downgradeConference(call, lastParticipant);
        // Call has been pulled while in conference
        // Execute this block always after isTwoPartyCall(call) in case call is pulled
        // and then pulled back to the original client
      } else if (call.pulledConference && isTwoPartyPulledCall(call)) {
        lastParticipant = call.participants[call.isRemote ? 1 : 0];
        downgradeConference(call, lastParticipant);
      }
    }

    function downgradeConference(call, lastParticipant) {
      call.removeParticipant(lastParticipant.userId);
      setCallPeerUser(call, lastParticipant.phoneNumber, lastParticipant.phoneNumber, lastParticipant.displayName);

      if (call.checkCstaState(CstaCallState.Conference)) {
        call.setCstaState(CstaCallState.Active);
      } else if (call.checkCstaState(CstaCallState.ConferenceHolding)) {
        call.setCstaState(CstaCallState.Holding);
      }
    }

    function checkTransferCallId(droppedConnection, cause) {
      var transferCallId = _trackTransferCallIds[droppedConnection.cID] || _trackTransferCallIds[DEFAULT_CALCULATED_TRANSFER_CALLID];
      if (transferCallId) {
        var call = $rootScope.localUser.isMiVB ? _atcRemoteCalls[droppedConnection.cID] : createAtcRemoteCall(transferCallId);
        call.atcCallInfo.setCstaConnection(droppedConnection);
        // cause = normal indicate Transfer is successful
        // the other causes will reflect the failure reason of transfer - FRN5227 is a prerequisite to set cause correctly
        if (cause !== 'normalClearing') {
          call.setCstaState(CstaCallState.TransferFailed);
          call.atcCallInfo.setTransferCallFailedCause(cause);
        }
        publishAtcCallInfo(call);

        delete _trackTransferCallIds[droppedConnection.cID];
        delete _trackTransferCallIds[DEFAULT_CALCULATED_TRANSFER_CALLID];
      }
    }

    function isDeliveredForOsBizParallelRinging(cause, position, callId, direction) {
      if (
        $rootScope.localUser.isOsBizCTIEnabled &&
        (cause === 'keyOperation' || cause === 'callForwardNoAnswer' || cause === 'singleStepTransfer') &&
        position === Targets.Desk &&
        direction === CallDirection.INCOMING
      ) {
        if (!_atcRemoteCalls[callId] && !isSelectedRouting(RoutingOptions.DeskPhone)) {
          LogSvc.debug('[CstaSvc]: This event is received for parallel ringing on device.');
          return true;
        }
      }
      return false;
    }

    function isForwardedOrOsBizSecondCall(call, event) {
      if (call.direction === CallDirection.OUTGOING && FORWARD_EXPR.test(event.cause)) {
        var divertingDeviceDisplay = getDisplayInfo(event.divertingDevice);
        setRedirectingUser(call, divertingDeviceDisplay.dn, divertingDeviceDisplay.fqn, divertingDeviceDisplay.name, RedirectionTypes.CallForward);
        var newDestinationDisplay = getDisplayInfo(event.newDestination);
        call.setPeerUser(newDestinationDisplay.dn, newDestinationDisplay.name);
        call.atcCallInfo.setOriginalPartnerDisplay(divertingDeviceDisplay);
        return true;
      } else if (
        $rootScope.localUser.isOsBizCTIEnabled &&
        call.direction === CallDirection.INCOMING &&
        call.isOsBizSecondCall &&
        isMyDeviceId(event.divertingDevice) &&
        moveOsBizCalls(event.connection.cID)
      ) {
        LogSvc.debug('[CstaSvc]: Second call was diverted and released');
        return true;
      }
      return false;
    }

    function isDistributed(event) {
      if (event.cause === 'distributed' && isMyDeviceId(event.divertingDevice) && getCallPosition(event.newDestination) === Targets.Desk) {
        LogSvc.debug('[CstaSvc]: Ignore Diverted event with cause = distributed and newDestination = desk');
        return true;
      }
      return false;
    }

    function setRedirected(divertedEvent, call, redirectingUser) {
      if (divertedEvent.cause === 'redirected' && divertedEvent.lastRedirectionDevice && !isMyDeviceId(divertedEvent.lastRedirectionDevice)) {
        var lastRedirectionDeviceDisplay = getDisplayInfo(divertedEvent.lastRedirectionDevice);
        setRedirectingUser(
          call,
          lastRedirectionDeviceDisplay.dn,
          lastRedirectionDeviceDisplay.fqn,
          lastRedirectionDeviceDisplay.name,
          RedirectionTypes.CallForward
        );
        _atcRemoteCalls[divertedEvent.connection.cID] = call;
      } else if (
        redirectingUser &&
        $rootScope.localUser.isOS4K &&
        FORWARD_EXPR.test(divertedEvent.cause) &&
        divertedEvent.calledDevice &&
        !isMyDeviceId(divertedEvent.calledDevice) &&
        FORWARD_EXPR.test(divertedEvent.cause)
      ) {
        // NGTC-8261 OS4k - Forwarded from "undefined" in CFNR scenario when stations are in a PU group
        // redirectingUser was defined in DeliveredEvent and we use the info here
        setRedirectingUser(call, redirectingUser.phoneNumber, redirectingUser.fqNumber, redirectingUser.displayName, RedirectionTypes.CallForward);
        _atcRemoteCalls[divertedEvent.connection.cID] = call;
      }
      // else if (!$rootScope.localUser.isOsBizCTIEnabled && divertedEvent.calledDevice && !isMyDeviceId(divertedEvent.calledDevice) &&
      //         (FORWARD_EXPR.test(divertedEvent.cause) || divertedEvent.cause === 'redirected' || divertedEvent.cause === 'distributed')) {
      //     //NGTC-6280: Agent No forwarding info displayed when DNIT call is answered and phone was locked
      //     var redirectionDeviceId = divertedEvent.calledDevice;
      //     var redirectionDeviceDisplay = getDisplayInfo(redirectionDeviceId);
      //     setRedirectingUser(call, redirectionDeviceDisplay.dn, redirectionDeviceDisplay.fqn,
      //         redirectionDeviceDisplay.name, RedirectionTypes.CallForward);
      //     _atcRemoteCalls[divertedEvent.connection.cID] = call;
      // }
    }

    function determineCallCharacteristics(establishedEvent) {
      var callCharacteristics = {};
      if (
        isMyDeviceId(establishedEvent.callingDevice) &&
        !isDssCallMove(establishedEvent.localConnectionInfo, establishedEvent.callingDevice, establishedEvent.answeringDevice)
      ) {
        // We are the calling party
        callCharacteristics.localConnection = {
          cID: establishedEvent.establishedConnection.cID,
          dID: establishedEvent.callingDevice
        };
        // In case of OSV external anonymous user answeringDevice and calledDevice is empty
        callCharacteristics.partnerDeviceId = establishedEvent.answeringDevice
          ? establishedEvent.answeringDevice
          : getDeviceFromDeviceId(establishedEvent.establishedConnection.dID);
        callCharacteristics.direction = CallDirection.OUTGOING;
        callCharacteristics.originalPartnerDisplay = getDisplayInfo(establishedEvent.calledDevice);
      } else if (isMyDeviceId(establishedEvent.answeringDevice)) {
        // We are the answering party
        callCharacteristics.localConnection = establishedEvent.establishedConnection;
        callCharacteristics.partnerDeviceId = getCallingDevice(establishedEvent);
        callCharacteristics.direction = CallDirection.INCOMING;
        callCharacteristics.originalPartnerDisplay = establishedEvent.presentationRestrictedDevice1
          ? getDisplayInfo()
          : getDisplayInfo(establishedEvent.callingDevice);
      } else {
        LogSvc.error('[CstaSvc]: ONS DN is neither in callingDevice nor in answeringDevice?!?');
      }
      return callCharacteristics;
    }

    function isMasterParallelHgCall(call) {
      return call && call.atcCallInfo && call.atcCallInfo.masterParallelHgCall;
    }

    function convertCstaCauseToReason(cause) {
      var reason = MissedReasonTypes.DEFAULT;
      switch (cause) {
        case 'busy':
          reason = MissedReasonTypes.BUSY;
          break;
        case 'reorderTone':
          reason = MissedReasonTypes.REORDER_TONE;
          break;
        case 'blocked':
          reason = MissedReasonTypes.DECLINED;
          break;
        case 'destOutOfOrder':
          reason = MissedReasonTypes.DEST_OUT_OF_ORDER;
          break;
        case 'destNotObtainable':
          reason = MissedReasonTypes.DEST_NOT_OBTAINABLE;
          break;
      }
      return reason;
    }

    function checkForTransferCb(call) {
      if (call && call.atcCallInfo && call.atcCallInfo.transferCb) {
        call.atcCallInfo.transferCb && call.atcCallInfo.transferCb(null, null, call);
        delete call.atcCallInfo.transferCb;
      }
    }

    function setCstaStateFromLCO(call, event) {
      if (call) {
        if (event.localConnectionInfo === 'hold') {
          call.setCstaState(CstaCallState.Holding);
        } else {
          call.setCstaState(CstaCallState.Active);
          call.clearRetrieveInProgress();
        }
      }
    }

    function setLocalCallAsPulled(callId, destination) {
      var localCall = findWebRTCCall(callId);
      if (localCall) {
        localCall.pulled = getCallPosition(destination) === Targets.WebRTC || isOSBizOns(destination);
        localCall.atcCallInfo.setIgnoreCall(false);
        LogSvc.debug('[CstaSvc]: Set localCall.pulled to ', localCall.pulled);
      }
    }

    function setPartnerDisplayForDeliveredEvent(event) {
      var partnerDisplayId;
      //NGTC-9477: For OSBiz group Call peer is the group and not the alerting device
      if ($rootScope.localUser.isOsBizCTIEnabled && event.cause === 'multipleAlerting' && event.lastRedirectionDevice) {
        partnerDisplayId = event.lastRedirectionDevice;
      } else {
        partnerDisplayId = event.alertingDevice ? event.alertingDevice : getDeviceFromDeviceId(event.connection.dID);
      }
      return partnerDisplayId;
    }

    function checkForCallReject() {
      var shouldReject =
        $rootScope.localUser.selectedBusyHandlingOption !== BusyHandlingOptions.DefaultRouting.name ||
        ($rootScope.localUser.isTeamsCallBusyHandlingEnabled &&
          $rootScope.localUser.userPresenceState.state === Constants.PresenceState.BUSY_IN_AN_EXTERNAL_CALL &&
          !getAgentHuntGroupAvailable());
      LogSvc.debug('[CstaSvc]: checkForCallReject: ', shouldReject);
      return shouldReject;
    }
    ///////////////////////////////////////////////////////////////////////////
    // User to User event handlers
    ///////////////////////////////////////////////////////////////////////////
    _userToUserHandler.on('ATC.CSTA', function (data) {
      if (data.subtype === AtcMessage.OS4K_CLASSIC_HUNT) {
        $rootScope.$apply(function () {
          handleOS4KHuntResponse(data.classicHuntResponse);
        });
      }

      if (data.type === AtcMessage.CSTA && data.CSTA) {
        $rootScope.$apply(function () {
          handleCstaEvent(data);
        });
      }
    });

    _userToUserHandler.on('ATC.PRIMARYCLIENT', function (data) {
      LogSvc.info('[CstaSvc]: Received PRIMARYCLIENT event:', data);
      if (data.type === AtcMessage.PRIMARYCLIENT && data.primaryClientId) {
        handlePrimaryClientEvent(data.primaryClientId);
      }
    });

    _userToUserHandler.on('ATC.ADVANCING', function (data) {
      if (data.type === AtcMessage.ADVANCING) {
        LogSvc.debug('[CstaSvc]: Received UserToUser ATC.ADVANCING');
        var call = UnifyPhoneCallControlSvc.getIncomingCall(data.rtcSessionId);
        if (call) {
          _movingCall = {
            newCallId: call.atcCallInfo.getCstaCallId(),
            direction: call.direction,
            redirectingUser: call.redirectingUser
          };
        }
      }
    });

    _clientApiHandler.on('Team.NOTIFY', function (evt) {
      if (!evt.callingUser) {
        LogSvc.debug('[CstaSvc]: Ignore team notification for UnifyPhone call');
        return;
      }
      if (!_telephonyConversation || !$rootScope.localUser.phoneNumber) {
        // If the user does not have telephony or does not have a DID number, it cannot pickup a team call
        LogSvc.error('[CstaSvc]: No telephony conversation or phoneNumber. Cannot handle team notification');
        return;
      }
      if ($rootScope.localUser.cfData.Immediate.cfwEnabled || $rootScope.localUser.doNotDisturbOn) {
        LogSvc.debug('[CstaSvc]: Ignore team notification as the user has call forwarding immediate or DND enabled');
        return;
      }
      if (isCallAlreadyNotified(evt.callingUser.fullyQualifiedNumber, evt.callingUser.displayName)) {
        LogSvc.debug('[CstaSvc]: There is already a pickup notification for this call. Ignore it.');
        return;
      }

      LogSvc.debug('[CstaSvc]: Handle the team notification for phone call');
      $rootScope.$apply(function () {
        UserSvc.getUserById(evt.calledUserId, function (err, calledUser) {
          if (err || !calledUser) {
            LogSvc.error('[CstaSvc]: Error getting called user');
            return;
          }
          if (calledUser.userId === $rootScope.localUser.userId) {
            LogSvc.debug('[CstaSvc]: Ignore team notification when called user is the local user');
            return;
          }
          var callingDisplay = {
            dn: evt.callingUser.phoneNumber,
            fqn: evt.callingUser.fullyQualifiedNumber,
            name: evt.callingUser.displayName
          };
          var calledDisplay = {
            dn: calledUser.phoneNumber,
            fqn: calledUser.fullyQualifiedNumber,
            name: calledUser.displayName
          };
          notifyCall({
            callingDisplay: callingDisplay,
            calledDisplay: calledDisplay,
            callId: evt.rtcSessionId,
            callingUserId: evt.callingUserId
          });
        });
      });
    });

    _clientApiHandler.on('Team.NOTIFY_CANCEL', function (evt) {
      var callToPickup = _atcRemoteCalls[evt.rtcSessionId];
      if (callToPickup && !callToPickup.isHandoverInProgress) {
        // If the call is being picked up, the event should be ignored
        $rootScope.$apply(function () {
          removeAtcRemoteCall(evt.rtcSessionId);
        });
      }
    });

    function isVMNumber(number) {
      return number === _osmoData.vm || (!!_osmoData.vmGNF && number === _osmoData.vmGNF);
    }

    ///////////////////////////////////////////////////////////////////////////
    // Event handlers
    ///////////////////////////////////////////////////////////////////////////
    function handleCstaEvent(data) {
      if (!data.CSTA) {
        return;
      }

      if (data.CSTA.wres && data.CSTA.wres.reqName === 'DirectoryLookupNameRes') {
        LogSvc.debug('Received wres DirectoryLookupNameRes CSTA event:', data.CSTA);
        var wresExternalItem = data.CSTA.wres.answer && data.CSTA.wres.answer.entries && data.CSTA.wres.answer.entries.item;
        if (
          wresExternalItem &&
          wresExternalItem.cID &&
          (wresExternalItem.displayName || wresExternalItem.commonName || wresExternalItem.givenName || wresExternalItem.sn)
        ) {
          // cache peerInfo so peerUser displayName updates on Delivered and Established event
          // TODO: handle case where it is received after the Established event?
          _wresExternalPeerInfo = {
            displayName: wresExternalItem.displayName || wresExternalItem.commonName || [wresExternalItem.givenName, wresExternalItem.sn].join(' ').trim(),
            cstaCallId: wresExternalItem.cID
          };
        } else {
          LogSvc.error('[CstaSvc]: Invalid DirectoryLookupNameRes CSTA event. It does not contain valid peerInfo.');
        }
        return;
      }

      var eventData = _cstaParser.parseEvent(data.CSTA);

      // Supress any private data from logs
      data.CSTA.keysToOmitFromLogging = ['DGE.dGL'];
      LogSvc.debug('[CstaSvc]: Received CSTA event: ', data.CSTA);

      if (!eventData) {
        return;
      }
      LogSvc.debug('[CstaSvc]: Parsed CSTA Event: ', eventData);
      delete eventData.keysToOmitFromLogging;
      switch (eventData.category) {
        case 'CallControl':
        case 'CallAssociatedFeature':
          _that.handleCallEvent(eventData);
          break;
        case 'LogicalDeviceFeature':
          _that.handleLogicalDeviceEvent(eventData);
          break;
        case 'PhysicalDeviceFeature':
          _that.handlePhysicalDeviceEvent(eventData);
          break;
        case 'DeviceMaintenance':
          _that.handleDeviceEvent(eventData);
          break;
        case 'CtiCallControl':
          _that.handleCti(eventData);
          break;
        default:
          LogSvc.debug('[CstaSvc]: Unsupported CSTA Event category: ' + eventData.category);
          break;
      }
    }

    function handleOS4KHuntResponse(data) {
      if (!data || !data.state) {
        LogSvc.error('[CstaSvc]: handleOS4KHuntResponse data / state is missing', data);
        return;
      }

      LogSvc.debug('[CstaSvc]: handleOS4KHuntResponse: User is in OS4K hunt group with state:', data.state);

      switch (data.state) {
        case 'READY':
          $rootScope.localUser.isOS4KClassicHuntReady = true;
          break;
        case 'NOT_READY':
          $rootScope.localUser.isOS4KClassicHuntReady = false;
          break;
        default:
          LogSvc.warn('[CstaSvc]: Unsupported OS4K Classic Hunt state: ', data.state);
          return;
      }

      $rootScope.localUser.isOS4KClassicHuntGroup = true;
      $rootScope.localUser.isAgent = true;

      LogSvc.debug('[CstaSvc]: handleOS4KHuntResponse - isAgent:', $rootScope.localUser.isAgent);
      LogSvc.debug('[CstaSvc]: Publish /agent/state/ready event');
      PubSubSvc.publish('/agent/state/ready', [_that.getGlobalAgentState(), $rootScope.localUser]);
    }

    function handlePrimaryClientEvent(primaryClientId) {
      if ($rootScope.localUser.clientId !== primaryClientId) {
        LogSvc.debug('[CstaSvc]: This is not the Primary ATC client');
        _primaryClient = false;
      } else {
        LogSvc.debug('[CstaSvc]: This is the primary ATC client');
        _primaryClient = true;

        if ($rootScope.localUser.isOsBizCTIEnabled) {
          setBusy(_userBusyState);
          isDNDSupported() && _that.getDoNotDisturb(handleGetDoNotDisturbResp);
          getOSBizDeviceState();
        }
      }
    }

    function handleCallInformationEvent(event) {
      // CallInformation usually updates the services permitted
      var callId = event.connection.cID;
      var call = null;
      if (event.pickupNotificationEarly) {
        // This a Group Pickup notification
        if (isMyDeviceId(event.connection.dID)) {
          LogSvc.debug('[CstaSvc]: The pickup notification is for a call to this user. Ignore it');
          return;
        }

        if (isMyDeviceId(event.callingDevice)) {
          // OS4K in some scenarios sends call pickup notification to the calling party, which we need to ignore
          LogSvc.debug('[CstaSvc]: The calling party of this pickup notification call is this user. Ignore it');
          return;
        }
        call = _atcRemoteCalls[callId];
        var calledDisplay = getDisplayInfo(event.connection.dID);
        var callingDisplay;
        if (event.presentationRestrictedDevice1) {
          LogSvc.debug('[CstaSvc]: Calling device is private.');
          callingDisplay = getDisplayInfo();
        } else {
          callingDisplay = getDisplayInfo(event.callingDevice);
        }
        if (!call) {
          if (isCallAlreadyNotified(callingDisplay.fqn, callingDisplay.name)) {
            LogSvc.debug('[CstaSvc]: There is already a pickup notification for this call. Ignore it');
            return;
          }

          notifyCall({
            callingDisplay: callingDisplay,
            calledDisplay: calledDisplay,
            callId: callId,
            evt: event
          });
        } else {
          LogSvc.debug('[CstaSvc]: Existing pickup call for: ', calledDisplay.dn);
          call.atcCallInfo.setCstaConnection(event.connection);
        }
        return;
      }
      var position = getCallPosition(event.connection.dID, event.epid);
      call = position === Targets.WebRTC ? findWebRTCCall(callId) : _atcRemoteCalls[callId];

      if (!call) {
        LogSvc.warn('[CstaSvc]: Could not find call corresponding to CallInformation Event.');
        return;
      }
      call.atcCallInfo.setCstaConnection(event.connection);
      if (event.callingDevice && !isMyDeviceId(event.callingDevice)) {
        var display;
        // callingDevice is the partner
        if (event.presentationRestrictedDevice1) {
          LogSvc.debug('[CstaSvc]: Calling device is private.');
          display = getDisplayInfo();
        } else {
          display = getDisplayInfo(event.callingDevice);
        }
        call.atcCallInfo.setPartnerDisplay(display);
        setCallPeerUser(call, call.atcCallInfo.peerDn, call.atcCallInfo.peerFQN, call.atcCallInfo.peerName);
      }

      if (event.servicesPermitted) {
        // Update Services Permitted
        call.atcCallInfo.setServicesPermitted(event.servicesPermitted);
        if (event.servicesPermitted.eSP && event.servicesPermitted.eSP.sST) {
          // FRN5227 is a prerequisite and SST is not allowed if it's in a conference
          // So when SST is allowed, it's not in a conference anymore
          if (call.checkCstaState(CstaCallState.ConferenceHolding)) {
            call.setCstaState(CstaCallState.Holding);
          } else if (call.checkCstaState(CstaCallState.Conference)) {
            call.setCstaState(CstaCallState.Active);
          }
        }
      }

      // NGTC-11064: As a user, I want to choose how my client will behave in case of an incoming call
      // when UP is busy "On a Teams call"
      if (
        call &&
        $rootScope.localUser.isTeamsCallBusyHandlingEnabled &&
        $rootScope.localUser.userPresenceState.state === Constants.PresenceState.BUSY_IN_AN_EXTERNAL_CALL &&
        !getAgentHuntGroupAvailable()
      ) {
        LogSvc.debug('[CstaSvc]: Terminate Call Information event call with cause: busy');
        event && event.connection && event.connection.cID && removeAtcRemoteCall(event.connection.cID);
        return;
      }

      // Update the UI
      if (call.checkCstaState([CstaCallState.Offered, CstaCallState.ExtendedRinging, CstaCallState.Idle])) {
        LogSvc.debug('[CstaSvc]: Do not publish /atccall/info event at Offered state');
      } else {
        publishAtcCallInfo(call);
      }
    }

    function handleConferencedEvent(event) {
      if (event.secondaryOldCall) {
        // At this point, OSBiz has a local call and 2 csta calls. Remove the secondaryOldCall csta call.
        if (($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiVB) && moveOsBizCalls(event.secondaryOldCall.cID)) {
          LogSvc.debug('[CstaSvc]: Cleared secondary atc call with Call Id = ', event.secondaryOldCall.cID);
        } else {
          // We are the conferencing party, remove the secondaryOldCall
          removeAtcRemoteCall(event.secondaryOldCall.cID);
        }
      }

      // update the call in primaryOldCall with the one in conferenceConnections
      var call = null;
      var position = getCallPosition(event.primaryOldCall.dID, event.epid);
      // With OS4K the conference call is the second local call of the conference initiator
      var callId =
        $rootScope.localUser.isOSV ||
        $rootScope.localUser.isMiV5000 ||
        $rootScope.localUser.isOsBizCTIEnabled ||
        $rootScope.localUser.isMiVB ||
        position !== Targets.WebRTC ||
        !event.secondaryOldCall
          ? event.primaryOldCall.cID
          : event.secondaryOldCall.cID;

      if (position === Targets.WebRTC) {
        call = findWebRTCCall(callId);
      } else {
        call = _atcRemoteCalls[callId];
      }

      if (!call) {
        LogSvc.debug('[CstaSvc]: Could not find a call with Call Id = ' + callId);
        return;
      }

      // Update the CSTA Connection ID for the call.
      event.conferenceConnections.forEach(function (confConn) {
        if ((confConn.endpoint && isMyDeviceId(confConn.endpoint)) || (confConn.connection && isMyDeviceId(confConn.connection.dID))) {
          call.atcCallInfo.setCstaConnection(confConn.connection);
          if (call.isAtcRemote) {
            var atcCallInfo = call.atcCallInfo;
            var peerUser = call.peerUser;
            var participants = call.participants;
            var direction = call.direction;
            call.setCstaState(CstaCallState.Idle);

            publishAtcCallInfo(call);
            delete _atcRemoteCalls[callId];

            call = createAtcRemoteCall(confConn.connection.cID);
            call.atcCallInfo = atcCallInfo;
            call.peerUser = peerUser;
            call.participants = participants;
            call.direction = direction;
            _atcRemoteCalls[confConn.connection.cID] = call;
          }
          LogSvc.info('[CstaSvc]: Updated the CSTA connection ID for the call: ', confConn.connection);
        } else {
          // NGTC-13178: Add isMiV5000PartnerAnonymous check for MiV5000 anynomous cases.
          var isMiV5000PartnerAnonymous = isPartnerAnonymous(confConn.endpoint, ANONYMOUS_MIV5000_DEVICE);

          var participantDisplay;
          if (confConn.endpoint && (confConn.endpoint.rstr === '' || isMiV5000PartnerAnonymous)) {
            //Handling for restricted number
            participantDisplay = getDisplayInfo();
          } else {
            participantDisplay = (confConn.endpoint && getDisplayInfo(confConn.endpoint)) || getDisplayInfo(confConn.connection.dID);
          }
          _conferenceParticipants.push({
            call: call,
            userId: confConn.connection.dID,
            participantId: confConn.connection.dID,
            phoneNumber: participantDisplay.dn,
            displayName: participantDisplay.name
          });

          if (!_resolving) {
            _resolving = true;
            resolveParticipants();
          }
        }
      });

      if (event.localConnectionInfo === 'hold') {
        call.setCstaState(CstaCallState.ConferenceHolding);
      } else {
        call.setCstaState(CstaCallState.Conference);
        if (!call.isRemote && ($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiVB)) {
          call.setState(CallState.Active);
        }
      }

      call.atcCallInfo.setServicesPermitted(event.servicesPermitted);

      publishAtcCallInfo(call);
    }

    function handleConnectionClearedEvent(event) {
      var callId = event.droppedConnection.cID;
      var call = findWebRTCCall(callId) || _atcRemoteCalls[callId];
      _incomingCallConnection = {};

      if (call) {
        if (call.pickupNotification && call.isHandoverInProgress) {
          return;
        }
        if (event.pickupNotificationTerminated && (!event.droppedConnection.dID || isMyDeviceId(event.droppedConnection.dID))) {
          // This a Group Pickup notification terminated event
          LogSvc.debug('[CstaSvc]: The pickup notification terminated event is for this user. Ignore it');
          return;
        }

        if (event.localConnectionInfo !== 'null') {
          if ($rootScope.localUser.isOsBizCTIEnabled && event.cause === 'multipleAlerting') {
            LogSvc.info('[CstaSvc]: Discarding ConnectionClearEvent due to parallel ringing. Do not overwrite services permitted.');
            return;
          }

          LogSvc.info('[CstaSvc]: Update services permitted.');
          call.atcCallInfo.setServicesPermitted(event.servicesPermitted);

          if (call.participants.length > 1) {
            eventForConference(event.droppedConnection.dID, call);
          }
          if (!call.atcCallInfo.getMissedReason() && !call.establishedTime) {
            if (call.direction === CallDirection.INCOMING) {
              call.atcCallInfo.setMissedReason(isMyDeviceId(event.releasingDevice) ? MissedReasonTypes.DECLINED : MissedReasonTypes.CANCELLED);
            } else {
              call.atcCallInfo.setMissedReason(isMyDeviceId(event.releasingDevice) ? MissedReasonTypes.CANCELLED : MissedReasonTypes.DECLINED);
            }
          }
          publishAtcCallInfo(call);
          return;
        } else {
          LogSvc.info('[CstaSvc]: Local connection info is null.');

          LogSvc.info('[CstaSvc]: Update services permitted.');
          call.atcCallInfo.setServicesPermitted(event.servicesPermitted);

          if (!call.atcCallInfo.getMissedReason() && !call.establishedTime) {
            call.atcCallInfo.setMissedReason(
              call.direction === CallDirection.INCOMING && event.cause === 'normalClearing' ? MissedReasonTypes.DECLINED : MissedReasonTypes.CANCELLED
            );
          }
          call.clearAtcHandoverInProgress();
          call.atcCallInfo.clearOutgoingCallCampedOn();

          if ($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiVB) {
            if (call.atcCallInfo.getCstaReconnectId().cID) {
              // Call is released but there is a pending consultation call (local call hold using consultationCall case). Release that too.
              // OSbiz will improve holdCall support with V2R7
              var data = {
                request: 'ClearConnection',
                connectionToBeCleared: call.atcCallInfo.getCstaReconnectId()
              };
              sendCstaRequest(data);
              call.atcCallInfo.setCstaReconnectId({});
            }

            if (_webrtcRemoteCall && _webrtcRemoteCall.checkState(CallState.ActiveRemote)) {
              _webrtcRemoteCall.pullBlocked = false;
            }

            if (moveOsBizCalls(callId)) {
              return;
            }
          }
        }
      }

      checkTransferCallId(event.droppedConnection, event.cause);
      removeAtcRemoteCall(callId);
    }

    // eslint-disable-next-line complexity, max-lines-per-function
    function handleDeliveredEvent(event) {
      var localConnection;
      var partnerDeviceId;
      var calledDeviceId;
      var redirectionDeviceId;
      var redirectionDeviceDisplay;
      var callState;
      var direction;
      var call = null;
      var resolvePeerUser;
      var resolveRedirectingUser;
      var redirectingUserDetails;
      var peerUserDetails;
      var setCallRedirectingPeerUserCb = function () {
        LogSvc.debug('[CstaSvc]: Publish /call/peerUser/update event.', [
          'callId = ' + call.callId,
          'peerUserId = ' + call.peerUser.userId,
          'peerUser = ',
          call.peerUser,
          'redirectingUser = ',
          call.redirectingUser
        ]);
        PubSubSvc.publish('/call/peerUser/update', [call]);
      };

      if (isMyDeviceId(event.callingDevice) && !isDssCallMove(event.localConnectionInfo, event.callingDevice, event.alertingDevice)) {
        // outgoing call - My device is the calling party
        callState = CstaCallState.Delivered;
        // In case of OSV external anonymous user alertingDevice and calledDevice is empty
        //TODO: get partner from lastredirection
        partnerDeviceId = setPartnerDisplayForDeliveredEvent(event);
        //partnerDeviceId = event.alertingDevice ? event.alertingDevice : getDeviceFromDeviceId(event.connection.dID);
        calledDeviceId = event.calledDevice;
        localConnection = {
          cID: event.connection.cID,
          dID: event.callingDevice
        };
        direction = CallDirection.OUTGOING;
      } else if (isMyDeviceId(event.alertingDevice)) {
        // incoming call - My device is the alerting party
        callState = CstaCallState.Ringing;
        partnerDeviceId = getCallingDevice(event);
        localConnection = event.connection;
        direction = CallDirection.INCOMING;

        // NGTC-3658: Event cause is callForwardNoAnswer for OS4K and redirected for OSV
        // In case of OSBiz setting an internal number as alternative is not supported
        // NGTC-4261: OS4k - Inconsistency in Call Control when an Agent receives a call through ACD
        if (
          !$rootScope.localUser.isOsBizCTIEnabled &&
          !isMyDeviceId(event.calledDevice) &&
          (FORWARD_EXPR.test(event.cause) || event.cause === 'redirected' || event.cause === 'distributed')
        ) {
          redirectionDeviceId = event.calledDevice;
          redirectionDeviceDisplay = getDisplayInfo(redirectionDeviceId);
        }
      } else {
        LogSvc.error('[CstaSvc]: ONS DN is neither in callingDevice nor in alertingDevice?!?');
        return;
      }

      var position = getCallPosition(localConnection.dID, event.epid, direction);

      // NGTC-13178: Add isMiV5000PartnerAnonymous check for MiV5000 anynomous cases.
      var isMiV5000PartnerAnonymous = isPartnerAnonymous(event.callingDevice, ANONYMOUS_MIV5000_DEVICE);

      var display;
      if (
        (event.presentationRestrictedDevice1 && direction === CallDirection.INCOMING) ||
        checkSSTIsPrivateDevice2(event, direction) ||
        isMiV5000PartnerAnonymous
      ) {
        LogSvc.debug('[CstaSvc]: Calling device is private.');
        display = getDisplayInfo();
      } else {
        display = getDisplayInfo(partnerDeviceId);
      }

      if (event.cause === 'enteringDistribution') {
        LogSvc.debug('[CstaSvc]: This event is for the master of the parallel hunt group');
        call = _atcRemoteCalls[localConnection.cID];
        if (!call) {
          call = createAtcRemoteCall(localConnection.cID);
          _atcRemoteCalls[localConnection.cID] = call;
          LogSvc.debug('[CstaSvc]: Created new Remote Call object for callID= ', localConnection.cID);
        }
        call.setCstaState(callState);
        call.setPosition(position);
        call.atcCallInfo.setCstaConnection(localConnection);
        call.atcCallInfo.setIgnoreCall(false);
        call.atcCallInfo.masterParallelHgCall = true;
        return;
      }

      if (isDeliveredForOsBizParallelRinging(event.cause, position, localConnection.cID, direction)) {
        // OSBiz supports parallel ringing. Avoid processing Delivered event for a new remote call. Local call will be used for UI.
        return;
      }

      if (position === Targets.WebRTC) {
        // Local WebRTC call, check only _alertingCall
        call = findWebRTCCall(localConnection.cID);
        if (call) {
          call.atcCallInfo.setCstaConnection(localConnection);
          call.atcCallInfo.setServicesPermitted(event.servicesPermitted);
          call.setCstaState(callState);
          call.atcCallInfo.setQueuedIncomingCall(false);
          if (!(event.cause === 'singleStepTransfer' && direction === CallDirection.OUTGOING && callState === CstaCallState.Delivered)) {
            // HYD-6853 For OSBiz/MiVB secondCall (checked in publishOsBizCallState) do not publish state
            // in order to avoid displaying "calling / outgoing call" when peer (called with consultation) performs a SST
            // This way UI matches no consultation scenario
            publishOsBizCallState(call, CallState.Delivered);
            if (direction === CallDirection.OUTGOING) {
              publishMiVBConsulationSound(call, CallState.Delivered);
            }
          }
          position = call.isRemote ? Targets.Other : position;
          call.setPosition(position);
          if (display) {
            call.peerUser && updateDisplayNameFromPeerUser(display, call.peerUser, call.atcCallInfo);
            LogSvc.debug('[CstaSvc]: Update partner display to ', display);
            call.atcCallInfo.setPartnerDisplay(display);
            var setCallPeerUserCb = function () {
              //NGTC-4263: Publishing call state instead of atccall/info because in anonymous scenarios when call state is Idle,
              //this will trigger the /call/ended flow
              LogSvc.debug('[CstaSvc]: Publish /call/state event. callId = ' + call.callId + ', state = ' + call.state.name);
              PubSubSvc.publish('/call/state', [call]);
            };

            resolvePeerUser = setCallPeerUserSync(call, call.atcCallInfo.peerDn, call.atcCallInfo.peerFQN, call.atcCallInfo.peerName, setCallPeerUserCb);

            var remoteCall = findAtcRemoteCall(localConnection.cID);
            if (remoteCall) {
              if (remoteCall.forwarded) {
                resolveRedirectingUser = setRedirectingUserSync(
                  call,
                  remoteCall.redirectingUser.phoneNumber,
                  remoteCall.redirectingUser.fqNumber,
                  remoteCall.redirectingUser.displayName,
                  remoteCall.redirectingUser.redirectionType
                );
                if (resolveRedirectingUser) {
                  redirectingUserDetails = {
                    fqNumber: remoteCall.redirectingUser.fqNumber,
                    phoneNumber: remoteCall.redirectingUser.phoneNumber,
                    displayName: remoteCall.redirectingUser.displayName,
                    type: remoteCall.redirectingUser.redirectionType
                  };
                }
              }
              remoteCall.atcCallInfo.setIgnoreCall(true);
              removeAtcRemoteCall(localConnection.cID);
              // NGTC-4261: OS4k - Inconsistency in Call Control when an Agent receives a call through ACD
            } else if (redirectionDeviceId && event.cause === 'distributed') {
              resolveRedirectingUser = setRedirectingUserSync(
                call,
                redirectionDeviceDisplay.dn,
                redirectionDeviceDisplay.fqn,
                redirectionDeviceDisplay.name,
                RedirectionTypes.CallForward
              );
              if (resolveRedirectingUser) {
                redirectingUserDetails = setRedirectingUserDetails(redirectionDeviceDisplay, RedirectionTypes.CallForward);
              }
              // NGTC-13505: When a UP user receives a forwarded call from another device, group or virtual station only the number is shown and not the name
            } else if (
              $rootScope.localUser.isOsBizCTIEnabled &&
              call.direction === CallDirection.INCOMING &&
              event.lastRedirectionDevice &&
              event.lastRedirectionDevice !== event.callingDevice
            ) {
              var lastRedirectionDeviceDisplay = getDisplayInfo(event.lastRedirectionDevice);
              resolveRedirectingUser = setRedirectingUserSync(
                call,
                lastRedirectionDeviceDisplay.dn,
                lastRedirectionDeviceDisplay.fqn,
                lastRedirectionDeviceDisplay.name,
                RedirectionTypes.CallForward
              );
              if (resolveRedirectingUser) {
                redirectingUserDetails = setRedirectingUserDetails(lastRedirectionDeviceDisplay, RedirectionTypes.CallForward);
              }
            }

            if (resolvePeerUser) {
              peerUserDetails = {
                fqNumber: call.atcCallInfo.peerFQN,
                phoneNumber: call.atcCallInfo.peerDn,
                displayName: call.atcCallInfo.peerName
              };
            }
            resolvePeerAndRedirectingUser(call, peerUserDetails, redirectingUserDetails, setCallPeerUserCb, setCallRedirectingPeerUserCb);
          }

          // only clear the _incomingCallConnection if the Delivered event is for a local alerting webRTC call
          // if the Delivered event is from the PROGRESS of another client, the _incomingCallConnection needs to be kept
          // in case the call needs to be rejected later on
          _incomingCallConnection = {};
          // update UI
          publishAtcCallInfo(call);
          return;
        } else {
          // This is the racing condition scenario where the Delivered event was received after the call was terminated
          if (_lastEndedCall && _lastEndedCall.atcCallInfo && _lastEndedCall.atcCallInfo.getCstaCallId() === event.connection.cId) {
            LogSvc.warn('[CstaSvc]: The Delivered event is for a local call that was just terminated. Ignore it');
            return;
          }
          // This is most likely a scenario where the CSTA Delivered event arrives before
          // the UnifyPhone INVITE. See note under /call/incoming event handler for details.
          LogSvc.warn('[CstaSvc]: There is no local call associated with the Delivered Event');
          // Let the code proceed below so we create a remote ATC call, but keep the position
          // set to WebRTC. This will be needed in case we get the /call/incoming event.
        }
      }

      // Remote call
      var callId = localConnection.cID;
      call = _atcRemoteCalls[callId];
      if (!call) {
        if ($rootScope.localUser.isMiVB) {
          var webrtcCall = findWebRTCCall(callId);
          if (webrtcCall) {
            LogSvc.warn('[CstaSvc]: The Delivered Event is for a remote call for which a local call already exists. Skip duplicate remote call.');
            return;
          }
        }
        call = createAtcRemoteCall(callId);
        _atcRemoteCalls[callId] = call;
        LogSvc.debug('[CstaSvc]: Created new Remote Call object for callID= ', callId);
      }
      if (isMasterParallelHgCall(call)) {
        LogSvc.debug('[CstaSvc]: Ignore Delivered event for master parallel hunt group');
        return;
      }
      call.setCstaState(callState);
      call.atcCallInfo.setServicesPermitted(event.servicesPermitted);
      call.setPosition(position);
      call.atcCallInfo.setCstaConnection(localConnection);
      call.atcCallInfo.setQueuedIncomingCall(false);
      call.clearAtcHandoverInProgress();
      call.direction = direction;
      call.receivedAlerting = true;

      // NGTC-11064: [Spike] As a user, I want to choose how my client will behave in case of an incoming call
      // when UP is busy "On a Teams call"
      if (
        $rootScope.localUser.isTeamsCallBusyHandlingEnabled &&
        $rootScope.localUser.userPresenceState.state === Constants.PresenceState.BUSY_IN_AN_EXTERNAL_CALL &&
        !getAgentHuntGroupAvailable()
      ) {
        LogSvc.debug('[CstaSvc]: User has enable BUSY_IN_AN_EXTERNAL_CALL, terminate incoming call with Busy cause');
        sendRejectCstaRequest(call);
        return;
      }

      if (position === Targets.Desk && direction === CallDirection.INCOMING) {
        if ($rootScope.localUser.isOsBizCTIEnabled && event.cause === 'recallNoAnswer') {
          // For Mulap users, OSBiz does not send a SIP call in case of call recall. It also does not support csta deflect
          // to the client. Allow only to answer the call on desk device in this case.
          call.atcCallInfo.setRecalledCall(true);
        }
        if (isSelectedRouting(RoutingOptions.DeskPhone)) {
          // Do not ring on the headset if the call is set to ring only on desk device
          call.doNotRingOnHeadset = true;
        }
      }

      // NGTC-3658: When a forwarded call is redirected to the alternative device, and the alternative device is an NGTC user,
      // the redirecting user information is lost because the call is dropped
      if (redirectionDeviceId && (position === Targets.Cell || position === Targets.Other)) {
        resolveRedirectingUser = setRedirectingUserSync(
          call,
          redirectionDeviceDisplay.dn,
          redirectionDeviceDisplay.fqn,
          redirectionDeviceDisplay.name,
          RedirectionTypes.CallForward
        );
        if (resolveRedirectingUser) {
          redirectingUserDetails = setRedirectingUserDetails(redirectionDeviceDisplay, RedirectionTypes.CallForward);
        }
      }

      if (display) {
        // Update partner's display information if presented
        call.atcCallInfo.setPartnerDisplay(display);
        if (calledDeviceId) {
          var calledDisplay = getDisplayInfo(calledDeviceId);
          if (calledDisplay.fqn !== display.fqn) {
            call.atcCallInfo.setOriginalPartnerDisplay(calledDisplay);
          } else {
            call.atcCallInfo.setOriginalPartnerDisplay(display);
          }
        }
        var setCallPeerUserCb1 = function () {
          if (_snapshotPerformed) {
            if (call.direction === CallDirection.INCOMING) {
              LogSvc.debug('[CstaSvc]: Publish /call/remote/incoming event');
              PubSubSvc.publish('/call/remote/incoming', [call]);
            }
            publishAtcCallInfo(call);
          }
        };
        resolvePeerUser = setCallPeerUserSync(call, call.atcCallInfo.peerDn, call.atcCallInfo.peerFQN, call.atcCallInfo.peerName, setCallPeerUserCb1);
        if (resolvePeerUser) {
          peerUserDetails = {
            fqNumber: call.atcCallInfo.peerFQN,
            phoneNumber: call.atcCallInfo.peerDn,
            displayName: call.atcCallInfo.peerName
          };
        }
        resolvePeerAndRedirectingUser(call, peerUserDetails, redirectingUserDetails, setCallPeerUserCb1, setCallRedirectingPeerUserCb);
        return;
      }

      if (call.direction === CallDirection.INCOMING) {
        LogSvc.debug('[CstaSvc]: Publish /call/remote/incoming event');
        PubSubSvc.publish('/call/remote/incoming', [call]);
      }

      publishAtcCallInfo(call);
    }

    function handleDigitsGeneratedEvent(event) {
      var callId = event.connection.cID;
      var call = findWebRTCCall(callId);
      if (!call) {
        // find again from _atcRemoteCalls
        call = _atcRemoteCalls[callId];
      }

      if (call && isMyDeviceId(event.connection.dID)) {
        // DTMF digits have been generated
        // Raise an event so we can request additional digits to be generated (if pending)
        LogSvc.info('[CstaSvc]: Number of DTMF digits played: ', event.digitGeneratedList && event.digitGeneratedList.length);
        PubSubSvc.publish('/call/singleDigitSent', [call, event.digitGeneratedList]);
      }
    }

    // eslint-disable-next-line complexity
    function handleDivertedEvent(event) {
      var divertingDeviceDisplay, newDestinationDisplay, redirectingUser;
      var call = findWebRTCCall(event.connection.cID);
      if (call) {
        if (isForwardedOrOsBizSecondCall(call, event)) {
          return;
        }
        redirectingUser = call.redirectingUser;
      }
      if (isDistributed(event)) {
        return;
      }
      call = _atcRemoteCalls[event.connection.cID];
      if (!call) {
        // Even though the remotecall hasn't been created yet, we need to
        // notify the UnifyPhoneCallControlSvc about this event
        call = createAtcRemoteCall(event.connection.cID);
      } else if (isMasterParallelHgCall(call)) {
        LogSvc.debug('[CstaSvc]: Ignore Diverted event for master parallel hunt group');
        return;
      }

      // NGTC-11064: As a user, I want to choose how my client will behave in case of an incoming call
      // when UP is busy "On a Teams call"
      // Publish /agent/status/enabled transferred event for RTC.Invite (OSV)
      PubSubSvc.publish('/agent/status/enabled', [getAgentHuntGroupAvailable()]);
      if (
        call &&
        $rootScope.localUser.isTeamsCallBusyHandlingEnabled &&
        $rootScope.localUser.userPresenceState.state === Constants.PresenceState.BUSY_IN_AN_EXTERNAL_CALL &&
        !getAgentHuntGroupAvailable()
      ) {
        LogSvc.debug('[CstaSvc]: Terminate Diverted event call with cause: busy');
        call.atcCallInfo.setMissedReason(MissedReasonTypes.BUSY);
        event && event.connection && event.connection.cID && removeAtcRemoteCall(event.connection.cID);
        return;
      }
      if (event.localConnectionInfo === 'null' && isMyDeviceId(event.divertingDevice)) {
        if (!isMyDeviceId(event.newDestination)) {
          // If the call is diverted to VM or it's currently on the client and is
          // diverted to another user (e.g. forwarding, pickup), the client should
          // not create a journal entry.
          // If OSBiz CTI user, do not generate journal now. It is added by backend when the call is fw/redirected.
          // In 4K the call that is diverted to vm shouldn't be ignored since the backend won't create any journal
          if (
            (isVMDeviceId(event.newDestination) && !$rootScope.localUser.isOS4K) ||
            getCallPosition(event.connection.dID) === Targets.WebRTC ||
            $rootScope.localUser.isOsBizCTIEnabled
          ) {
            call.atcCallInfo.setIgnoreCall(true);
          }
          call.setCstaState(CstaCallState.Idle);
          call.direction = CallDirection.INCOMING;
          call.atcCallInfo.setMissedReason(MissedReasonTypes.CANCELLED);
          if (FORWARD_EXPR.test(event.cause)) {
            _atcRemoteCalls[event.connection.cID] = call;
            var callingDisplay;
            if (event.presentationRestrictedDevice1) {
              LogSvc.debug('[CstaSvc]: Calling device is private.');
              callingDisplay = getDisplayInfo();
            } else {
              callingDisplay = getDisplayInfo(event.callingDevice);
            }
            LogSvc.debug('[CstaSvc]: Update partner display to ', callingDisplay);
            call.atcCallInfo.setPartnerDisplay(callingDisplay);
            setCallPeerUser(call, call.atcCallInfo.peerDn, call.atcCallInfo.peerFQN, call.atcCallInfo.peerName);
          }
          publishAtcCallInfo(call);
        } else {
          if (getCallPosition(event.newDestination) === Targets.WebRTC) {
            _incomingCallConnection = { cID: event.connection.cID, dID: event.newDestination };
          }
          if (call) {
            if (call.pickupNotification) {
              return;
            }

            setRedirected(event, call, redirectingUser);

            if (
              $rootScope.localUser.isOsBizCTIEnabled &&
              event.cause === 'park' &&
              getCallPosition(event.newDestination) !== getCallPosition(event.divertingDevice)
            ) {
              // Call is moving between the user devices.
              call.setRedirectionType(RedirectionTypes.Dss);
              _atcRemoteCalls[event.connection.cID] = call;
              return;
            }
            if (call.forwarded) {
              return;
            }
            if (getCallPosition(event.newDestination) === Targets.Cell) {
              // Call is moving to alternative device.
              LogSvc.debug('[CstaSvc]: Diverting to altenative device');
              return;
            }
            if (call.checkCstaState(CstaCallState.Ringing) && getCallPosition(event.newDestination) === call.getPosition()) {
              LogSvc.debug('[CstaSvc]: new destination has the same position as the current alerting position. Ignore event');
              _incomingCallConnection = {};
              return;
            }
            // This is a deflect to another OND
            call.atcCallInfo.setIgnoreCall(true);
            publishAtcCallInfo(call);
          }
        }
        if (call && !call.isHandoverInProgress) {
          removeAtcRemoteCall(event.connection.cID);
        }
      } else if (!isMyDeviceId(event.divertingDevice)) {
        if (call) {
          var display = getDisplayInfo(event.divertingDevice);
          if (display) {
            call.atcCallInfo.setOriginalPartnerDisplay(display);
          }
          if (call.direction === CallDirection.OUTGOING && FORWARD_EXPR.test(event.cause)) {
            divertingDeviceDisplay = getDisplayInfo(event.divertingDevice);
            setRedirectingUser(call, divertingDeviceDisplay.dn, divertingDeviceDisplay.fqn, divertingDeviceDisplay.name, RedirectionTypes.CallForward);
            newDestinationDisplay = getDisplayInfo(event.newDestination);
            call.setPeerUser(newDestinationDisplay.dn, newDestinationDisplay.name);
            return;
          }
          call.atcCallInfo.setMissedReason(MissedReasonTypes.CANCELLED);
          publishAtcCallInfo(call);
        }
      }
    }

    function copyDataFromMovingCall(call, callId) {
      if (_movingCall && _movingCall.newCallId === callId) {
        call.direction = _movingCall.direction;
        call.setRedirectingUser(
          _movingCall.redirectingUser.phoneNumber,
          _movingCall.redirectingUser.fqNumber,
          _movingCall.redirectingUser.displayName,
          _movingCall.redirectingUser.userId,
          _movingCall.redirectingUser.redirectionType
        );
        call.atcCallInfo.transferCb = _movingCall.transferCb;
        delete _movingCall.transferCb;
        _movingCall = {};
      }
    }

    function setWebRTCCallForEstablishedEvent(call, localConnection, display, event) {
      LogSvc.info('[CstaSvc]: The Established Event is for a local WebRTC call');

      var position = Targets.WebRTC;
      var resolvePeerUser;
      var resolveRedirectingUser;
      var redirectingUserDetails;
      var peerUserDetails;
      var setCallRedirectingPeerUserCb = function () {
        LogSvc.debug('[CstaSvc]: Publish /call/peerUser/update event.', [
          'callId = ' + call.callId,
          'peerUserId = ' + call.peerUser.userId,
          'peerUser = ',
          call.peerUser,
          'redirectingUser = ',
          call.redirectingUser
        ]);
        PubSubSvc.publish('/call/peerUser/update', [call]);
      };

      call.atcCallInfo.setCstaConnection(localConnection);
      LogSvc.info('[CstaSvc]: Set the CSTA connection ID for the active call: ', localConnection);

      if (call.isRemote) {
        LogSvc.warn('[CstaSvc]: There is no local call associated with the Established Event');
        position = Targets.Other;
      }
      call.atcCallInfo.setServicesPermitted(event.servicesPermitted);
      setCstaStateFromLCO(call, event);
      publishOsBizCallState(call, CallState.Active);
      call.peerUser && updateDisplayNameFromPeerUser(display, call.peerUser);
      call.atcCallInfo.setPartnerDisplay(display);
      call.setPosition(position);
      call.clearAtcHandoverInProgress();
      call.atcCallInfo.clearOutgoingCallCampedOn();
      call.receivedAlerting = true;
      call.atcCallInfo.setQueuedIncomingCall(false);

      var setCallPeerUserCb = function () {
        LogSvc.debug('[CstaSvc]: Publish /call/state event. callId = ' + call.callId + ', state = ' + call.state.name);
        PubSubSvc.publish('/call/state', [call]);
      };
      resolvePeerUser = setCallPeerUserSync(call, call.atcCallInfo.peerDn, call.atcCallInfo.peerFQN, call.atcCallInfo.peerName, setCallPeerUserCb);
      copyDataFromMovingCall(call, localConnection.cID);

      var remoteCall = findAtcRemoteCall(localConnection.cID);
      if (remoteCall && (remoteCall.forwarded || remoteCall.pickupNotification)) {
        var redirectionType =
          remoteCall.redirectingUser.redirectionType === RedirectionTypes.CallForward ? RedirectionTypes.CallForward : RedirectionTypes.CallPickedUp;
        resolveRedirectingUser = setRedirectingUserSync(
          call,
          remoteCall.redirectingUser.phoneNumber,
          remoteCall.redirectingUser.fqNumber,
          remoteCall.redirectingUser.displayName,
          redirectionType
        );
        if (resolveRedirectingUser) {
          redirectingUserDetails = {
            phoneNumber: remoteCall.redirectingUser.phoneNumber,
            fqNumber: remoteCall.redirectingUser.fqNumber,
            displayName: remoteCall.redirectingUser.displayName,
            type: redirectionType
          };
        }
        remoteCall.atcCallInfo.setIgnoreCall(true);
        removeAtcRemoteCall(localConnection.cID);
      } else if (event.cause === 'callPickup') {
        if (call.pickupNotification) {
          call.setRedirectionType(RedirectionTypes.CallPickedUp);
        } else if (event.lastRedirectionDevice) {
          var lastRedirectionDeviceDisplay = getDisplayInfo(event.lastRedirectionDevice);
          resolveRedirectingUser = setRedirectingUserSync(
            call,
            lastRedirectionDeviceDisplay.dn,
            lastRedirectionDeviceDisplay.fqn,
            lastRedirectionDeviceDisplay.name,
            RedirectionTypes.CallPickedUp
          );
          if (resolveRedirectingUser) {
            redirectingUserDetails = setRedirectingUserDetails(lastRedirectionDeviceDisplay, RedirectionTypes.CallPickedUp);
          }
        }
      } else if (event.cause === 'singleStepTransfer') {
        checkForTransferCb(call);
      } else if (
        $rootScope.localUser.isOS4K &&
        event.cause === 'normal' &&
        !(call.redirectingUser && call.redirectingUser.redirectionType) &&
        isMyDeviceId(event.answeringDevice) &&
        !isMyDeviceId(event.calledDevice)
      ) {
        //NGTC-6280: this is a forwarded incoming call while in push notification.
        //In all other cases redirected user info is filled from delivered event
        var redirectionDeviceDisplay = getDisplayInfo(event.calledDevice);
        resolveRedirectingUser = setRedirectingUserSync(
          call,
          redirectionDeviceDisplay.dn,
          redirectionDeviceDisplay.fqn,
          redirectionDeviceDisplay.name,
          RedirectionTypes.CallForward
        );
        if (resolveRedirectingUser) {
          redirectingUserDetails = setRedirectingUserDetails(redirectionDeviceDisplay, RedirectionTypes.CallForward);
        }
      }

      // In case we end up with one remote call with the same cstaCallId as the webrtc call of the Established event this call needs to be removed
      // since is being replaced by the webRTC call
      if (remoteCall && remoteCall.cstaCallId === call.cstaCallId && remoteCall.checkState([CallState.Ringing])) {
        remoteCall.atcCallInfo.setIgnoreCall(true);
        removeAtcRemoteCall(localConnection.cID);
      }

      if (resolvePeerUser) {
        peerUserDetails = {
          fqNumber: call.atcCallInfo.peerFQN,
          phoneNumber: call.atcCallInfo.peerDn,
          displayName: call.atcCallInfo.peerName
        };
      }

      resolvePeerAndRedirectingUser(call, peerUserDetails, redirectingUserDetails, setCallPeerUserCb, setCallRedirectingPeerUserCb);

      if (!call.isRemote && !call.isOsBizSecondCall) {
        call.updateCallState();
      }
      publishAtcCallInfo(call);

      processQueuedCall(position);
    }

    // eslint-disable-next-line complexity
    function handleEstablishedEvent(event) {
      var call;
      _incomingCallConnection = {};

      var callCharacteristics = determineCallCharacteristics(event);
      var localConnection = callCharacteristics.localConnection;
      var partnerDeviceId = callCharacteristics.partnerDeviceId;
      var direction = callCharacteristics.direction;
      var originalPartnerDisplay = callCharacteristics.originalPartnerDisplay;

      LogSvc.debug('[CstaSvc]: LocalConnection: ', localConnection);
      LogSvc.debug('[CstaSvc]: Partner Device ID: ', partnerDeviceId);

      // NGTC-13178: Add isMiV5000PartnerAnonymous check for MiV5000 anynomous cases.
      var isMiV5000PartnerAnonymous = isPartnerAnonymous(event.callingDevice, ANONYMOUS_MIV5000_DEVICE);

      var display;
      if (
        (event.presentationRestrictedDevice1 && direction === CallDirection.INCOMING) ||
        checkSSTIsPrivateDevice2(event, direction) ||
        isMiV5000PartnerAnonymous
      ) {
        LogSvc.debug('[CstaSvc]: Calling device is private.');
        display = getDisplayInfo();
      } else {
        display = getDisplayInfo(partnerDeviceId);
      }

      // Determine call position
      var position = getCallPosition(localConnection.dID, event.epid, direction);
      if (position === Targets.WebRTC) {
        LogSvc.info('[CstaSvc]: The Established Event is for a local WebRTC call');

        // In some scenarios, like handover, the active call doesn't have a CSTA Connection ID until the Established Event
        // is received, so we must fallback to the active call in case we cannot find a local call with the received CSTA Call ID.
        call = findWebRTCCall(localConnection.cID);
        if (call) {
          setWebRTCCallForEstablishedEvent(call, localConnection, display, event);
          return;
        }
        if (event.cause === 'recall' || event.cause === 'callPickup') {
          call = UnifyPhoneCallControlSvc.findActivePhoneCall();
          LogSvc.info('[CstaSvc]: Original call callId:', call.atcCallInfo.getCstaCallId());
          call.atcCallInfo.setCstaConnection(localConnection);
          call.atcCallInfo.setServicesPermitted(event.servicesPermitted);
          if (event.localConnectionInfo === 'hold') {
            call.setCstaState(CstaCallState.Holding);
          } else {
            call.setCstaState(CstaCallState.Active);
          }
          call.updateCallState();
          publishAtcCallInfo(call);
          return;
        }

        if ($rootScope.localUser.isOsBizCTIEnabled && $rootScope.localUser.reroutingPhoneNumber && isSelectedRouting(RoutingOptions.AlternativeNumber)) {
          // OSBiz calls on alternative number have the same DE/EE as a local call
          position = Targets.Cell;
        } else {
          // For some reason the Established event is for a local call that does not exist anymore
          LogSvc.debug('[CstaSvc]: There is no local or remote call, change the position to Other');
          position = Targets.Other;
        }

        // If the call is answered by the VM, the established event has no epid. This call should
        // not be shown
        if (isMyDeviceId(event.answeringDevice) && !event.epid && !$rootScope.localUser.isOsBizCTIEnabled) {
          LogSvc.debug('[CstaSvc]: This is a call answered by VM. Ignore it');
          return;
        }

        // This is the racing condition scenario where the Established event was received after the call was terminated
        if (_lastEndedCall && _lastEndedCall.atcCallInfo && _lastEndedCall.atcCallInfo.getCstaCallId() === localConnection.cId) {
          LogSvc.warn('[CstaSvc]: The Established event is for a local call that was just terminated. Ignore it');
          return;
        }
      }

      var newCall = false;
      call = _atcRemoteCalls[localConnection.cID];
      if (!call) {
        // This is a new call
        newCall = true;
        call = createAtcRemoteCall(localConnection.cID);
        //NGTC-5246: Try to retrieve redirecting user fromExisting WebRTCCall or lastEndedCall
        var localCallExists = findLocalCallByCstaCID(localConnection);
        if (localCallExists && localCallExists.redirectingUser) {
          call.setRedirectingUser(
            localCallExists.redirectingUser.phoneNumber,
            localCallExists.redirectingUser.fqNumber,
            localCallExists.redirectingUser.displayName,
            localCallExists.redirectingUser.userId,
            localCallExists.redirectingUser.redirectionType
          );
        }
        copyDataFromMovingCall(call, localConnection.cID);
      }

      if (isMasterParallelHgCall(call)) {
        LogSvc.debug('[CstaSvc]: Ignore Established event for master parallel hunt group');
        return;
      }
      call.setPosition(position);
      call.atcCallInfo.setPartnerDisplay(display);
      call.atcCallInfo.setOriginalPartnerDisplay(originalPartnerDisplay);
      call.direction = direction;
      setCallPeerUser(call, call.atcCallInfo.peerDn, call.atcCallInfo.peerFQN, call.atcCallInfo.peerName, function () {
        //NGTC-4263: Publishing call state instead of atccall/info because in anonymous scenarios when call state is Idle,
        //this will trigger the /call/ended flow
        LogSvc.debug('[CstaSvc]: Publish /call/state event. callId = ' + call.callId + ', state = ' + call.state.name);
        PubSubSvc.publish('/call/state', [call]);
      });
      call.atcCallInfo.setServicesPermitted(event.servicesPermitted);
      call.atcCallInfo.setCstaConnection(localConnection);
      call.atcCallInfo.setRecalledCall(false);

      checkForTransferCb(call);
      call.clearAtcHandoverInProgress();
      call.atcCallInfo.clearOutgoingCallCampedOn();
      call.receivedAlerting = true;
      call.atcCallInfo.setQueuedIncomingCall(false);
      call.atcCallInfo.setIgnoreCall(false);

      if ($rootScope.localUser.isOsBizCTIEnabled && position === Targets.Cell) {
        call.atcCallInfo.setOsbizCallOnAlternativeNumber();
      }

      setCstaStateFromLCO(call, event);

      if (event.cause === 'callPickup') {
        if (call.pickupNotification) {
          call.setRedirectionType(RedirectionTypes.CallPickedUp);
          // Since the call is picked up, need to clear the pickUp indication, in order for the client
          // to generate a call log entry (OS4K or OsBIZ)
          call.atcCallInfo.pickUp = false;
        } else if (call.getRedirectionType() === RedirectionTypes.Dss) {
          // When moving call between user's devices, do not show pickup.
          call.setRedirectionType(null);
        } else if (event.lastRedirectionDevice) {
          setRedirectingUser(
            call,
            getDisplayInfo(event.lastRedirectionDevice).dn,
            getDisplayInfo(event.lastRedirectionDevice).fqn,
            getDisplayInfo(event.lastRedirectionDevice).name,
            RedirectionTypes.CallPickedUp
          );
        }
      }

      if (newCall) {
        LogSvc.debug('[CstaSvc]: Created new Remote Call object: ', call);
        // Add call to _atcRemoteCalls
        _atcRemoteCalls[localConnection.cID] = call;
      }

      if ($rootScope.localUser.isOsBizCTIEnabled && _webrtcRemoteCall && _calls.length > 1) {
        // For OsBiz, pull is blocked if the user has more than one calls
        _webrtcRemoteCall.pullBlocked = true;
      }

      publishAtcCallInfo(call);

      processQueuedCall(position);
    }

    function handleHeldEvent(event) {
      if ($rootScope.localUser.isMiV5000) {
        var localCall = findWebRTCCall();
        var remoteCall = findAtcRemoteEstablishedCall();
        if (localCall && remoteCall && remoteCall.isHandoverInProgress) {
          LogSvc.debug('[CstaSvc]: 5K Pull call detected');
          localCall.peerUser = remoteCall.peerUser;
          localCall.atcCallInfo.setCstaConnection(event.heldConnection);
          localCall.atcCallInfo.setServicesPermitted(event.servicesPermitted);
          removeAtcRemoteCall(event.heldConnection.cID);
        }
      }

      var callId = event.heldConnection.cID;

      var call = findWebRTCCall(callId);
      if (!call) {
        // find again from _atcRemoteCalls
        call = _atcRemoteCalls[callId];

        if (!call) {
          LogSvc.warn('[CstaSvc]: Could not find call corresponding to Held Event.');
          return;
        }
      }

      // Update Call State
      LogSvc.debug('[CstaSvc]: Current csta call state: ', call.getCstaState());
      if (isMyDeviceId(event.holdingDevice)) {
        // The subscriber held the call
        call.atcCallInfo.setCstaConnection(event.heldConnection);
        switch (call.getCstaState()) {
          case CstaCallState.Held:
            call.setCstaState(CstaCallState.HoldOnHold);
            break;
          case CstaCallState.Conference:
            call.setCstaState(CstaCallState.ConferenceHolding);
            if ($rootScope.localUser.isOsBizCTIEnabled && !call.isRemote) {
              call.setState(CallState.Holding);
            }
            break;
          default:
            call.setCstaState(CstaCallState.Holding);
            if (!call.isRemote) {
              call.setState(CallState.Holding);
            }
            break;
        }
        call.clearHoldInProgress();
      } else if (event.localConnectionInfo === 'hold') {
        if (!call.checkCstaState(CstaCallState.ConferenceHolding)) {
          call.setCstaState(CstaCallState.HoldOnHold);
        }
      } else if (!call.checkCstaState(CstaCallState.Conference)) {
        call.setCstaState(CstaCallState.Held);
      }

      LogSvc.debug('[CstaSvc]: New csta call state: ', call.getCstaState());

      // Update Services Permitted
      call.atcCallInfo.setServicesPermitted(event.servicesPermitted);

      // Update the UI
      publishAtcCallInfo(call);
    }

    function getCstaConnectionForRequest(callData) {
      if (!$rootScope.localUser.isOsBizCTIEnabled && !$rootScope.localUser.isMiVB) {
        return callData.getCstaConnection();
      } else {
        return { cID: callData.getCstaCallId(), dID: _osmoData.onsFQN };
      }
    }

    function handleOfferedEvent(event) {
      var localConnection;
      var partnerDeviceId;
      var data;

      if (isMyDeviceId(event.offeredDevice)) {
        // This is a call offered to the ONS user
        localConnection = event.offeredConnection;
        partnerDeviceId = event.callingDevice;
      } else {
        LogSvc.debug('[CstaSvc]: ONS DN is not the offered device. Ignore Offered event.');
        return;
      }

      LogSvc.debug('[CstaSvc]: LocalConnection: ', localConnection);
      LogSvc.debug('[CstaSvc]: Partner Device ID: ', partnerDeviceId);

      if ($rootScope.localUser.cfData.Immediate.cfwEnabled) {
        LogSvc.debug('[CstaSvc]: Immediate call forwarding is enabled. Accept call');
        data = {
          request: 'AcceptCall',
          callToBeAccepted: localConnection
        };
        sendCstaRequest(data);
        return;
      }

      // Get partner's display information
      var display;
      if (event.presentationRestrictedDevice1) {
        LogSvc.debug('[CstaSvc]: Calling device is private.');
        display = getDisplayInfo();
      } else {
        display = getDisplayInfo(partnerDeviceId);
      }

      var call = _atcRemoteCalls[localConnection.cID];
      if (call && call.pickupNotification) {
        return;
      }
      call = createAtcRemoteCall(localConnection.cID);
      call.setPosition(Targets.Desk);
      call.atcCallInfo.setPartnerDisplay(display);
      setCallPeerUser(call, call.atcCallInfo.peerDn, call.atcCallInfo.peerFQN, call.atcCallInfo.peerName);
      call.atcCallInfo.setServicesPermitted(event.servicesPermitted);
      call.setCstaState(CstaCallState.Offered);
      call.direction = CallDirection.INCOMING;
      call.atcCallInfo.setCstaConnection(localConnection);
      if (FORWARD_EXPR.test(event.cause)) {
        // If in case of multiple forwarding we want to display the original called party, then we should use the calledDevice
        // The OpenStage phones show the last redirecting party
        var lastRedirectionDeviceDisplay = getDisplayInfo(event.lastRedirectionDevice);
        setRedirectingUser(
          call,
          lastRedirectionDeviceDisplay.dn,
          lastRedirectionDeviceDisplay.fqn,
          lastRedirectionDeviceDisplay.name,
          RedirectionTypes.CallForward
        );
      }

      LogSvc.debug('[CstaSvc]: Created new Remote Call object: ', call);

      // Add call to _atcRemoteCalls
      _atcRemoteCalls[localConnection.cID] = call;
      _incomingCallConnection = { cID: localConnection.cID, dID: buildNewDestination(Targets.WebRTC, null, true) };

      if (isSelectedRouting(RoutingOptions.DeskPhone)) {
        // If Desk is the selected incoming call routing, the call will be deflected to the desk phone by ATC
        return;
      }
      if (
        UnifyPhoneCallControlSvc.isSecondCall() ||
        (_telephonyConversation.call && _telephonyConversation.call.isAtcRemote && !_telephonyConversation.call.atcCallInfo.pickUp)
      ) {
        var shouldReject = checkForCallReject(call);
        handleSecondCall(call, event.cause, shouldReject);
      } else {
        // NGTC-11064: As a user, I want to choose how my client will behave in case of an incoming call when UP is busy "On a Teams call"
        // Reject only when teams busy in external call is enabled
        handleFirstReceivedCall(
          call,
          event,
          $rootScope.localUser.isTeamsCallBusyHandlingEnabled &&
            $rootScope.localUser.userPresenceState.state === Constants.PresenceState.BUSY_IN_AN_EXTERNAL_CALL &&
            !getAgentHuntGroupAvailable()
        );
      }
    }

    // eslint-disable-next-line complexity
    function handleQueuedEvent(event) {
      var localConnection;
      var partnerDeviceId;
      var position;
      var callId;
      var call;

      // NGTC-10571: Need to remove ATC call if QueuedEvent received and callWaitingRejectionOnFlag is true from previous FailedEvent
      callId = event.queuedConnection.cID;
      call = _atcRemoteCalls[callId];
      if (call && call.callWaitingRejectionOnFlag) {
        // If call.callWaitingRejectionOnFlag is true, it means that we have received a FailedEvent before with this flag.
        // If so, we need to clear all call related info created at FailedEvent, before proceed with QueuedEvent handling.
        removeAtcRemoteCall(callId, true);
      }

      if (isMyDeviceId(event.queue)) {
        if ($rootScope.localUser.isMiV5000) {
          LogSvc.debug('[CstaSvc]: Ignore Queued event for MiV5000 for now');
          return;
        }

        // This is a call parked to the user's private queue
        localConnection = event.queuedConnection;
        partnerDeviceId = event.callingDevice;

        LogSvc.debug('[CstaSvc]: LocalConnection: ', localConnection);
        LogSvc.debug('[CstaSvc]: Partner Device ID: ', partnerDeviceId);

        // Determine call position
        position = getCallPosition(localConnection.dID, event.epid);

        // Get partner's display information
        var display = getDisplayInfo(partnerDeviceId);
        callId = localConnection.cID;
        call = _atcRemoteCalls[callId];
        if (!call) {
          if (event.cause === 'noAvailableAgents') {
            // This event indicates that the MLHG that the ONS belongs to is busy. Needs to be ignored
            LogSvc.debug('[CstaSvc]: Cause=noAvailableAgents. Ignore Queued event');
            return;
          }
          if ($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiVB) {
            if (!$rootScope.localUser.pbxCampOnSupported && (event.cause === 'multipleQueuing' || event.cause === 'keyOperation' || event.cause === 'campOn')) {
              // This event indicates that there is another call waiting but OSBiz does not support yet campOn feature
              // with UnifyPhone clients. Cannot not process this event in client.
              LogSvc.debug('[CstaSvc]: CampOn is not supported. Ignore this event.');
              return;
            }
            if ($rootScope.localUser.pbxCampOnSupported && (event.cause === 'multipleQueuing' || event.cause === 'keyOperation' || event.cause === 'campOn')) {
              // This event indicates that there is another call waiting and OSBiz supports campOn feature
              // with UnifyPhone clients. We need to ignore this event in case we already have 2 local calls.
              // For some reason when the second call is created through consultation _calls.length is not increased. We need to handle this case too
              // eslint-disable-next-line max-len
              if (
                _calls &&
                _calls.length > 0 &&
                !_calls[0].isRemote &&
                (_calls.length === 2 || (_calls.length === 1 && _telephonyConversation.call.atcCallInfo.peerDn !== _calls[0].atcCallInfo.peerDn))
              ) {
                LogSvc.debug('[CstaSvc]: Three local calls are not supported. Sending ClearConnection request.');
                //NGTC-2208: send ClearConnection with a reason, so third incoming call is dropped as it is not supported
                if (event.queuedConnection.cID) {
                  var data = {
                    request: 'ClearConnection',
                    connectionToBeCleared: { cID: event.queuedConnection.cID, dID: getDeviceFromDeviceId(event.callingDevice) },
                    reason: 'busy'
                  };
                  sendCstaRequest(data);
                }
                return;
              }
            }
          }

          call = createAtcRemoteCall(callId);

          if ($rootScope.localUser.isOsBizCTIEnabled && event.cause === 'campOn' && event.lastRedirectionDevice) {
            // If in case of redirected campon, show redirection info
            var lastRedirectionDeviceDisplay = getDisplayInfo(event.lastRedirectionDevice);
            setRedirectingUser(
              call,
              lastRedirectionDeviceDisplay.dn,
              lastRedirectionDeviceDisplay.fqn,
              lastRedirectionDeviceDisplay.name,
              RedirectionTypes.CallForward
            );
          }

          _atcRemoteCalls[callId] = call;
          LogSvc.debug('[CstaSvc]: Created new Remote Call object for callID= ', callId);
        }
        call.setPosition(position);
        call.atcCallInfo.setPartnerDisplay(display);
        setCallPeerUser(call, call.atcCallInfo.peerDn, call.atcCallInfo.peerFQN, call.atcCallInfo.peerName);
        call.setCstaState(CstaCallState.Parked);
        call.atcCallInfo.setCstaConnection(localConnection);

        if ($rootScope.localUser.isOsBizCTIEnabled && $rootScope.localUser.pbxCampOnSupported && (event.cause === 'campOn' || event.cause === 'keyOperation')) {
          // This event indicates that there is a second call incoming
          if (_activeCall && position === Targets.WebRTC) {
            LogSvc.debug('[CstaSvc]: Cause=campOn. A local call is established and user receives new incoming call');
            if (position === Targets.WebRTC) {
              createCampOnLocalCall(call, event.servicesPermitted);
              return;
            }
          } else if (findAtcRemoteEstablishedCall() && position === Targets.Desk) {
            LogSvc.debug('[CstaSvc]: Cause=campOn. A remote call is established and user receives new incoming call');

            // Publish conversation update so that second call is shown as ringing
            _telephonyConversation.call = call;
            LogSvc.debug('[CstaSvc]: Publish /conversation/update event. convId = ', _telephonyConversation.convId);
            PubSubSvc.publish('/conversation/update', [_telephonyConversation]);

            LogSvc.debug('[CstaSvc]: Publish /call/remote/incoming event');
            PubSubSvc.publish('/call/remote/incoming', [call]);

            call.direction = CallDirection.INCOMING;
            call.setCstaState(CstaCallState.Ringing);
          } else if (_alertingCall) {
            LogSvc.debug('[CstaSvc]: Cause=campOn. A call is ringing and user receives new incoming call. Do not display yet.');
            // Do not show call on the client until the first call is answered, rejected or cancelled.
            call.setCstaState(CstaCallState.Ringing);
            call.direction = CallDirection.INCOMING;
            call.atcCallInfo.setQueuedIncomingCall(true);
          }
        }
      } else if ($rootScope.localUser.isOsBizCTIEnabled && isMyDeviceId(event.callingDevice) && event.cause === 'campOn') {
        // This is an outgoing call camping On a busy party
        position = getCallPosition(event.callingDevice);
        callId = event.queuedConnection.cID;
        call = position === Targets.WebRTC ? findWebRTCCall(callId) : _atcRemoteCalls[callId];
        if (!call) {
          LogSvc.debug('[CstaSvc]: Call not found for callID= ', callId);
          return;
        }
        call.atcCallInfo.setOutgoingCallCampedOn();
        if (call.isRemote && call.checkCstaState(CstaCallState.Busy)) {
          call.setCstaState(CstaCallState.Delivered);
        }
      } else {
        LogSvc.debug('[CstaSvc]: ONS DN is not the queue device. Ignore Queued event.');
        return;
      }

      call.atcCallInfo.setServicesPermitted(event.servicesPermitted);
      publishAtcCallInfo(call);
    }

    function handleRetrievedEvent(event) {
      var callId = event.retrievedConnection.cID;

      var call = findWebRTCCall(callId);
      if (!call) {
        // find again from _atcRemoteCalls
        call = _atcRemoteCalls[callId];

        if (!call) {
          LogSvc.warn('[CstaSvc]: Could not find call corresponding to Retrieved Event.');
          return;
        }
      }

      // Update Call State
      if (isMyDeviceId(event.retrievingDevice)) {
        call.atcCallInfo.setCstaConnection(event.retrievedConnection);
        // The subscriber retrieved the call
        if (call.checkCstaState(CstaCallState.HoldOnHold)) {
          call.setCstaState(CstaCallState.Held);

          // Prior to V7, the OSV has some problems with Hold-On-Hold scenarios and
          // it doesn't report all the events as expected.
          // Therefore, we should do a Snapshot Call to make sure we don't get stuck
          // in Held state. For now, as a workaround, just check whether Silent Handover
          // is allowed for this call. If it is, this means we are actually in Active state
          if (event.servicesPermitted.eSP && event.servicesPermitted.eSP.zsiHo) {
            call.setCstaState(CstaCallState.Active);
          }
        } else if (call.checkCstaState(CstaCallState.ConferenceHolding)) {
          // Still in a conference
          call.setCstaState(CstaCallState.Conference);

          // But if SST is allowed, Per FRN5227, it is not in a conference anymore
          if (event.servicesPermitted.eSP && event.servicesPermitted.eSP.sST) {
            call.setCstaState(CstaCallState.Active);
          }
        } else {
          call.setCstaState(CstaCallState.Active);
        }
        if (!call.isRemote) {
          call.setState(CallState.Active);
        }
        call.clearRetrieveInProgress();
        call.atcCallInfo.setCstaReconnectId({});
      } else if (call.checkCstaState([CstaCallState.ConferenceHolding, CstaCallState.Conference])) {
        if (event.localConnectionInfo === 'hold') {
          call.setCstaState(CstaCallState.ConferenceHolding);
        } else {
          call.setCstaState(CstaCallState.Conference);
        }
      } else {
        if (event.localConnectionInfo === 'hold') {
          call.setCstaState(CstaCallState.Holding);
        } else {
          call.setCstaState(CstaCallState.Active);
        }

        if (call.isRemote) {
          // Call may have been retrieved by different party. Update the display just in case.
          var display;
          if (event.presentationRestrictedDevice1 && call.direction === CallDirection.INCOMING) {
            LogSvc.debug('[CstaSvc]: Calling device is private.');
            display = getDisplayInfo();
          } else {
            display = getDisplayInfo(event.retrievingDevice);
          }
          call.atcCallInfo.setPartnerDisplay(display);
          setCallPeerUser(call, call.atcCallInfo.peerDn, call.atcCallInfo.peerFQN, call.atcCallInfo.peerName);
        }
      }

      // Update Services Permitted
      call.atcCallInfo.setServicesPermitted(event.servicesPermitted);

      // Update the UI
      publishAtcCallInfo(call);
    }

    function handleServiceInitiatedEvent(event) {
      /*
       * Process the Service Initiated Event to find out the CSTA Connection ID
       * for outgoing calls from the WebRTC client or Remote Device.
       */
      var call;
      var position = getCallPosition(event.initiatingDevice, event.epid);
      if (position === Targets.WebRTC) {
        call = findWebRTCCall(event.initiatedConnection.cID);
        // During Consultation, the 2nd new call becomes _activeCall
        // In 3PCC MakeCall/ConsultationCall scenarios, local state is Ringing when ServiceIniated event comes
        if (call) {
          call.atcCallInfo.setCstaConnection(event.initiatedConnection);
          call.atcCallInfo.setServicesPermitted(event.servicesPermitted);
          if (!call.isRemote && call.checkState([CallState.Initiated, CallState.Ringing, CallState.Delivered])) {
            LogSvc.info('[CstaSvc]: Set the CSTA connection ID for the call: ', event.initiatedConnection);

            call.setCstaState(CstaCallState.Initiated);
            publishOsBizCallState(call, CallState.Delivered);
            // update UI
            publishAtcCallInfo(call);
          } else if (call.isRemote) {
            LogSvc.info('[CstaSvc]: Set the CSTA connection ID for the remote call: ', event.initiatedConnection);
            return;
          } else {
            LogSvc.error('[CstaSvc]: Call Initiated for WebRTC client has no active call or not in Initiated/Ringing state');
          }
          return;
        }
      }

      call = _atcRemoteCalls[event.initiatedConnection.cID];
      if (!call) {
        // This is a new remote call
        call = createAtcRemoteCall(event.initiatedConnection.cID);
        call.setCstaState(CstaCallState.Initiated);
      }

      call.setPosition(position);
      call.atcCallInfo.setServicesPermitted(event.servicesPermitted);

      call.direction = CallDirection.OUTGOING;
      call.atcCallInfo.setCstaConnection(event.initiatedConnection);
      _atcRemoteCalls[event.initiatedConnection.cID] = call;
      LogSvc.debug('[CstaSvc]: Created new Remote Call object: ', call);
    }

    // eslint-disable-next-line complexity
    function handleTransferredEvent(event) {
      if (event.localConnectionInfo === 'null') {
        // We are the transferring party
        // MiVB sends secondaryOldCall with empty cID in case of failed transfer, while non-MiVB clients do not include secondaryOldCall at all
        if ((!$rootScope.localUser.isMiVB && event.secondaryOldCall) || ($rootScope.localUser.isMiVB && event.secondaryOldCall && event.secondaryOldCall.cID)) {
          removeAtcRemoteCall(event.primaryOldCall.cID);
          var secondaryCall = _atcRemoteCalls[event.secondaryOldCall.cID];
          if (secondaryCall) {
            secondaryCall.atcCallInfo.setMissedReason(MissedReasonTypes.TRANSFERRED);
          }
          $timeout(function () {
            removeAtcRemoteCall(event.secondaryOldCall.cID);
          }, 1000);
        } else {
          if (isMyDeviceId(event.transferringDevice) && isMyDeviceId(event.transferredToDevice)) {
            var remoteCall = _atcRemoteCalls[event.primaryOldCall.cID];
            if (remoteCall) {
              remoteCall.atcCallInfo.setIgnoreCall(true);
            }

            setLocalCallAsPulled(event.primaryOldCall.cID, event.transferredToDevice);
          }
          if (!$rootScope.localUser.isMiVB) {
            removeAtcRemoteCall(event.primaryOldCall.cID);
          }
        }

        // NGTC-11064: As a user, I want to choose how my client will behave in case of an incoming call
        // when UP is busy "On a Teams call"
        // Publish /agent/status/enabled transferred event for RTC.Invite (OSV)
        PubSubSvc.publish('/agent/status/enabled', [getAgentHuntGroupAvailable()]);

        if (event.cause === 'singleStepTransfer') {
          // For SingleStepTransfer and SilentHandover, TransferredEvent(null) does not indicate the transfer is successful or not
          // We have to track the Failed (fail, blocked) + ConnectionCleared event in order to determine the result
          // Only track the transfer operation that is initiated through EVO
          var transferredCall = _that.getTransferredCall(event.primaryOldCall.cID);
          if (transferredCall) {
            // remove stored transferredCall from _cstaReqHandler and track the failedCallId locally
            _that.clearTransferredCall(event.primaryOldCall.cID);
            _trackTransferCallIds[transferredCall.failedCallId] = event.primaryOldCall.cID;
          }
        } else if (event.cause === 'transfer') {
          // To popup a success message after consult transfer
          var tmpCall = createAtcRemoteCall(event.secondaryOldCall.cID);
          publishAtcCallInfo(tmpCall);
        }

        return;
      }

      // We are the transferred or transferred-to party
      var display = null;
      var callId = event.primaryOldCall.cID;
      var call = findWebRTCCall(callId) || _atcRemoteCalls[callId];

      if (!call) {
        LogSvc.debug('[CstaSvc]: Could not find a call with Call Id = ' + callId);
        return;
      }

      //5K sends TE in case Conference intiator leaves the call NGTC-11196
      if ($rootScope.localUser.isMiV5000 && call.participants.length > 1) {
        eventForConference(event.transferringDevice, call);
      }

      // For OSBiz DSS call move, user may not press 'transfer' on desk device. In that case,
      // this transferEvent needs to update the local call and release the remote call
      if (
        call.isAtcRemote &&
        event.secondaryOldCall &&
        event.secondaryOldCall.cID &&
        event.transferringDevice &&
        event.transferredToDevice &&
        isDssCallMove(event.localConnectionInfo, event.transferringDevice, event.transferredToDevice)
      ) {
        var localCall = findWebRTCCall(event.secondaryOldCall.cID);
        if (localCall) {
          // release remote call
          call.setCstaState(CstaCallState.Idle);
          publishAtcCallInfo(call);
          delete _atcRemoteCalls[callId];

          // update local call now
          call = localCall;
          call.atcCallInfo.setCstaCallId(callId);
        }
      }

      // NGTC-11064: As a user, I want to choose how my client will behave in case of an incoming call
      // when UP is busy "On a Teams call"
      if (
        $rootScope.localUser.isTeamsCallBusyHandlingEnabled &&
        $rootScope.localUser.userPresenceState.state === Constants.PresenceState.BUSY_IN_AN_EXTERNAL_CALL &&
        !getAgentHuntGroupAvailable()
      ) {
        LogSvc.debug('[CstaSvc]: Terminate Transferred call with cause: busy');
        sendRejectCstaRequest(call);
        return;
      }
      var transferringDeviceDisplay = getDisplayInfo(event.transferringDevice);
      var transferredToDeviceDisplay = getDisplayInfo(event.transferredToDevice);

      // Update the CSTA Connection ID for the call.
      event.transferredConnections.forEach(function (xferConn) {
        if ((xferConn.endpoint && isMyDeviceId(xferConn.endpoint)) || (xferConn.connection && isMyDeviceId(xferConn.connection.dID))) {
          call.atcCallInfo.setCstaConnection(xferConn.connection);
          LogSvc.info('[CstaSvc]: Updated the CSTA connection ID for the call: ', xferConn.connection);
          if (call.isAtcRemote) {
            call.setCallIdForTelephony(xferConn.connection.cID);
            delete _atcRemoteCalls[callId];
            _atcRemoteCalls[call.callId] = call;
            publishAtcCallInfo(call);
          }
        } else {
          if (call.isAtcRemote) {
            var atcCallInfo = call.atcCallInfo;
            call.setCstaState(CstaCallState.Idle);
            var peerUser = call.peerUser;
            var direction = call.direction;
            var establishedTime = call.establishedTime;
            publishAtcCallInfo(call);
            delete _atcRemoteCalls[callId];

            call = createAtcRemoteCall(xferConn.connection.cID);
            call.atcCallInfo = atcCallInfo;

            // NGTC-3728: In case other party is doing push pull and we are receiving this event for the existing connected
            // remote call, atcCallInfo cstaConnection should remain as is
            transferringDeviceDisplay.dn !== transferredToDeviceDisplay.dn && call.atcCallInfo.setCstaConnection(xferConn.connection);
            call.setCstaState(event.localConnectionInfo === 'alerting' ? CstaCallState.Ringing : CstaCallState.Active);
            LogSvc.info('[CstaSvc]: Updated the CSTA connection ID for the call: ', xferConn.connection);
            setCallPeerUser(call, peerUser.phoneNumber, peerUser.phoneNumber, peerUser.displayName);
            call.direction = direction;
            call.establishedTime = establishedTime;
            var originalPartnerDisplay = getDisplayInfo(event.transferringDevice);
            call.atcCallInfo.setOriginalPartnerDisplay(originalPartnerDisplay);

            _atcRemoteCalls[call.callId] = call;
          }
          // NGTC-13178: Add isMiV5000PartnerAnonymous check for MiV5000 anynomous cases.
          var isMiV5000PartnerAnonymous = isPartnerAnonymous(event.transferredToDevice, ANONYMOUS_MIV5000_DEVICE);
          // Update the partner display
          if (
            (event.presentationRestrictedDevice1 && call.direction === CallDirection.INCOMING) ||
            (event.presentationRestrictedDevice2 &&
              checkOs4kPrivateDevice2(call.direction, event, transferringDeviceDisplay, transferredToDeviceDisplay, xferConn)) ||
            (xferConn.endpoint && xferConn.endpoint.rstr === '') ||
            isMiV5000PartnerAnonymous
          ) {
            LogSvc.debug('[CstaSvc]: Calling device is private.');
            display = getDisplayInfo();
          } else {
            display = (xferConn.endpoint && getDisplayInfo(xferConn.endpoint)) || (xferConn.connection.dID && getDisplayInfo(xferConn.connection.dID));
            //NGTC-3966: OS4K external number has display info in event.transferredToDeviceDisplay
            if (transferredToDeviceDisplay.dn === display.dn && !display.name) {
              display = transferredToDeviceDisplay;
            }
          }
        }
      });

      if (!call.isRemote && !call.isEstablished()) {
        LogSvc.debug('[CstaSvc]: This is a WebRTC call that has not been answered. Update the display');
        if (display) {
          call.atcCallInfo.setPartnerDisplay(display);
          // Indicate that the call was transferred on ringing so that the displays should not be updated by
          // UnifyPhoneCallControlSvc when processing the incoming call
          call.transferredOnRinging = true;
          setCallPeerUser(call, call.atcCallInfo.peerDn, call.atcCallInfo.peerFQN, call.atcCallInfo.peerName, function () {
            // Wait for reverse lookup of peer user display data to update the call object before publishing the event.
            // Note, the callControlSvc listens for this event and will in this case publish a call state event used
            // (at least by iOS) to update call display data.
            if (_snapshotPerformed) {
              publishAtcCallInfo(call);
            }
          });
        }
        return;
      }

      switch (event.localConnectionInfo) {
        case 'hold':
          call.setCstaState(CstaCallState.Holding);
          break;
        case 'connected':
          call.setCstaState(CstaCallState.Active);
          break;
        case 'alerting':
          call.setCstaState(CstaCallState.Ringing);
          break;
        default:
          LogSvc.debug('[CstaSvc]: Unexpected localConnectionInfo: ', event.localConnectionInfo);
          break;
      }

      call.atcCallInfo.setServicesPermitted(event.servicesPermitted);
      if (display) {
        call.atcCallInfo.setPartnerDisplay(display);
        setCallPeerUser(call, call.atcCallInfo.peerDn, call.atcCallInfo.peerFQN, call.atcCallInfo.peerName, function () {
          // Wait for reverse lookup of peer user display data to update the call object before publishing the event.
          // Note, the callControlSvc listens for this event and will in this case publish a call state event used
          // (at least by iOS) to update call display data.
          if (_snapshotPerformed) {
            publishAtcCallInfo(call);
          }
        });
      }

      publishAtcCallInfo(call);
    }

    function checkOs4kPrivateDevice2(direction, event, transferringDeviceDisplay, transferredToDeviceDisplay, xferConn) {
      var transferredConnection = xferConn.endpoint || (xferConn.connection && xferConn.connection.dID);
      var xferConnDisplay = getDisplayInfo(transferredConnection);
      var presentationRestrictedDevice2Display = getDisplayInfo(event.presentationRestrictedDevice2);
      if (
        event.cause === 'transfer' &&
        direction === CallDirection.INCOMING &&
        isMyDeviceId(event.transferredToDevice) &&
        xferConnDisplay.dn === presentationRestrictedDevice2Display.dn
      ) {
        return true;
      } else if (direction === CallDirection.OUTGOING && transferringDeviceDisplay.dn !== transferredToDeviceDisplay.dn) {
        return true;
      }
      return false;
    }

    // eslint-disable-next-line complexity
    function handleFailedEvent(event) {
      var callId = event.failedConnection.cID;
      var localFailedConnection = event.failedConnection;
      //Special handling for failing event due to busy signal setting from other party
      if (event.localConnectionInfo === 'connected' && isMyDeviceId(event.callingDevice)) {
        // outgoing call - My device is the calling party
        localFailedConnection = {
          cID: event.failedConnection.cID,
          dID: event.callingDevice
        };
      }
      var position = getCallPosition(localFailedConnection.dID);
      var call = position === Targets.WebRTC ? findWebRTCCall(callId) : _atcRemoteCalls[callId];

      if (!call && $rootScope.localUser.isOsBizCTIEnabled && event.localConnectionInfo === 'fail') {
        LogSvc.warn('[CstaSvc]: Could not find call corresponding to handle FailedEvent Event. Creating new Remote call.');
        call = createAtcRemoteCallFailedEvent(event, callId);

        // NGTC-10571: PRB000075513:-:No entry in the call history journal when Unify Phone is busy (Call Waiting Rejection On)
        if (event.cause === 'busy') {
          // Create specific flag, so we can handle remote call, depending on "Call Waiting Rejection On" flag is enabled at OSBiz
          call.callWaitingRejectionOnFlag = true;
        }
        // NGTC-10819: PRB000076567:-:[OsBiz] User on DND doesn't get missed call on history
        if (event.cause === 'doNotDisturb') {
          // Set missed reason to type BUSY for this specific case
          call.atcCallInfo.setMissedReason(MissedReasonTypes.BUSY);
        }
      }

      // MiVB handling for Failed Transfer scenarios
      if ((!call || call.isAtcRemote) && $rootScope.localUser.isMiVB && event.localConnectionInfo === 'fail' && isMyDeviceId(event.callingDevice)) {
        if (_trackTransferCallIds && _trackTransferCallIds[event.failedConnection.cID]) {
          var initialCallId = _trackTransferCallIds[event.failedConnection.cID];
          LogSvc.debug('[CstaSvc]: Failed transfer for callId: ', initialCallId, ' with failed callId: ', event.failedConnection.cID);
          var failedCall = _atcRemoteCalls[event.failedConnection.cID];
          var heldCall = position === Targets.WebRTC ? findWebRTCCall(initialCallId) : _atcRemoteCalls[initialCallId];
          checkTransferCallId(event.failedConnection, event.cause);

          if (failedCall) {
            switch (event.cause) {
              case 'reorderTone':
              case 'destinationOutOfOrder':
              case 'destNotObtainable':
              case 'busy':
                failedCall.setCstaState(CstaCallState.Failed);
                publishAtcCallInfo(failedCall);
                $timeout(function () {
                  if (heldCall.isPresent()) {
                    hangupMiVBFailedTransferCall(heldCall, failedCall);
                  }
                }, OSBIZ_RECONNECT_TIMEOUT);
                break;
              default:
                break;
            }
          }
        }
      }

      if (!call) {
        LogSvc.warn('[CstaSvc]: Could not find call corresponding to handle FailedEvent Event.');
        return;
      }
      var display;

      if (event.localConnectionInfo === 'fail' && isMyDeviceId(event.failingDevice)) {
        if (isMasterParallelHgCall(call)) {
          LogSvc.debug('[CstaSvc]: Ignore Failed event for master parallel hunt group');
          return;
        }
        if (isMyDeviceId(event.callingDevice)) {
          display = getDisplayInfo(event.calledDevice);
          if (display.dn !== _numberNotAvailable && call && !call.pickedUp && display.fqn !== Utils.cleanPhoneNumber(call.atcCallInfo.peerFQN)) {
            setCallPeerUser(call, display.dn, display.fqn, display.name);
            call.atcCallInfo.setPartnerDisplay(display);
          }
        }
        switch (event.cause) {
          case 'blocked':
            // track the transfer result
            if (_trackTransferCallIds[callId]) {
              LogSvc.debug('[CstaSvc]: Received Failed event for transfer, waiting for ConnectionCleared event');
            }
            if (!$rootScope.localUser.isOSV) {
              if (call.isHandoverInProgress) {
                LogSvc.debug('[CstaSvc]: Received Failed event from non OSV PBX while handover is in progress');
                call.clearAtcHandoverInProgress();
                LogSvc.debug('[CstaSvc]: Publish /atccall/moveFailed event');
                PubSubSvc.publish('/atccall/moveFailed');
              }
              // If HFA device is off-hook after call is released, do not allow call services.
              if ($rootScope.localUser.isOsBizCTIEnabled && call.isRemote && call.atcCallInfo) {
                call.atcCallInfo.setServicesPermitted(event.servicesPermitted);
              }
            }
            break;

          case 'doNotDisturb':
            // ONS has DND active, the incoming call is blocked
            // Raise an event to inform the user about the blocked call
            publishAtcCallInfo(call);

            break;
          case 'reorderTone':
            call.atcCallInfo.setMissedReason(MissedReasonTypes.REORDER_TONE);
            publishAtcCallInfo(call);
            break;
          case 'busy':
            call.atcCallInfo.setMissedReason(MissedReasonTypes.BUSY);
            // OS4K case where the call fails in the Offered state due to busy
            if (call.checkCstaState(CstaCallState.Offered)) {
              call.setCstaState(CstaCallState.Failed);
            }
            call.atcCallInfo.clearOutgoingCallCampedOn();
            publishAtcCallInfo(call);
            break;
          case 'destNotObtainable':
            $timeout(function () {
              if (call.isPresent()) {
                hangupOsBizFailedSecondCall(call);
              }
            }, OSBIZ_RECONNECT_TIMEOUT);
            break;
          default:
            break;
        }
      } else if (event.localConnectionInfo === 'connected' && isMyDeviceId(event.callingDevice)) {
        call.atcCallInfo.setServicesPermitted(event.servicesPermitted);
        display = getDisplayInfo(event.failingDevice || event.calledDevice);
        if (display) {
          // NGTC-4238: In case of failed event we need to update the partner display for the journal
          var updateOriginalPartner =
            call.atcCallInfo.originalPartnerDisplay && call.atcCallInfo.getPartnerDisplay().fqn === call.atcCallInfo.originalPartnerDisplay.fqn;

          // Update partner's display information if presented
          call.atcCallInfo.setPartnerDisplay(display);
          updateOriginalPartner && call.atcCallInfo.setOriginalPartnerDisplay(display);
          setCallPeerUser(call, call.atcCallInfo.peerDn, call.atcCallInfo.peerFQN, call.atcCallInfo.peerName);
        }
        if (call.isRemote && event.cause === 'busy') {
          // Raise an event to inform the user about the busy call
          call.setCstaState(CstaCallState.Busy);
        } else if (!call.isRemote && event.cause === 'busy') {
          call.setCstaState(CstaCallState.Busy);
          call.atcCallInfo.setMissedReason(MissedReasonTypes.BUSY);
          publishOsBizCallState(call, CallState.Busy);
          $timeout(function () {
            hangupOsBizFailedSecondCall(call);
          }, OSBIZ_RECONNECT_TIMEOUT);
        }

        call.atcCallInfo.setMissedReason(convertCstaCauseToReason(event.cause));
        publishAtcCallInfo(call);
      } else if ($rootScope.localUser.isMiVB && event.localConnectionInfo === 'fail' && isMyDeviceId(event.callingDevice)) {
        // HYD-5249: [MiVB] No error message displayed, when Hydra client does consultation to an invalid number
        // we accept that MiVB implementation specifies failingDevice as the number we call, so we use in our
        // error handling logic for MiVB specific case
        LogSvc.debug('[CstaSvc]: Received Failed event from MiVB, event cause: ', event.cause);
        switch (event.cause) {
          case 'reorderTone':
            call.atcCallInfo.setMissedReason(MissedReasonTypes.REORDER_TONE);
            call.setCstaState(CstaCallState.Failed);
            publishOsBizCallState(call, CallState.Failed);
            $timeout(function () {
              if (call.isPresent()) {
                // TODO: refactor for hangupOsBizFailedSecondCall is needed, function name needs to be changed
                // it is used both for OsBiz and MiVB
                hangupOsBizFailedSecondCall(call);
              }
            }, OSBIZ_RECONNECT_TIMEOUT);
            break;
          case 'destNotObtainable':
            call.atcCallInfo.setMissedReason(MissedReasonTypes.DEST_NOT_OBTAINABLE);
            call.setCstaState(CstaCallState.Failed);
            publishOsBizCallState(call, CallState.Failed);
            $timeout(function () {
              if (call.isPresent()) {
                hangupOsBizFailedSecondCall(call);
              }
            }, OSBIZ_RECONNECT_TIMEOUT);
            publishAtcCallInfo(call);
            break;
          default:
            break;
        }
      }
    }

    function handleServiceCompletionFailureEvent(event) {
      var callId = event.primaryCall.connectionID.cID;
      var position = getCallPosition(event.primaryCall.connectionID.dID, _osmoData.epid);
      var call = position === Targets.WebRTC ? findWebRTCCall(callId) : _atcRemoteCalls[callId];

      if (!call) {
        LogSvc.debug('[CstaSvc]: Could not find a call with Call Id = ' + callId);
        LogSvc.debug('[CstaSvc]: Publish /atccall/hangingcall event');
        PubSubSvc.publish('/atccall/hangingcall', callId);
        return;
      }

      LogSvc.debug('[CstaSvc]: Publish /atccall/moveFailed event');
      PubSubSvc.publish('/atccall/moveFailed');
      // If the original call is already in Terminated state, it needs to be recreated
      if (call.checkState([CallState.Terminated, CallState.Idle])) {
        var atcCallInfo = call.atcCallInfo;
        var peerUser = call.peerUser;
        var establishedTime = call.establishedTime;
        var direction = call.direction;
        delete _atcRemoteCalls[callId];

        call = createAtcRemoteCall(callId);
        call.atcCallInfo = atcCallInfo;
        call.peerUser = peerUser;
        call.establishedTime = establishedTime;
        call.direction = direction;
        _atcRemoteCalls[callId] = call;
      }
      call.clearAtcHandoverInProgress();
      publishAtcCallInfo(call);
    }

    function handleBackInServiceEvent() {
      _onsUnregistered = false;
      publishCstaDeviceChanged();
    }

    function handleOutOfServiceEvent() {
      _onsUnregistered = true;
      publishCstaDeviceChanged();
    }

    function checkSetForwardingLoop(cb) {
      if (_loopDetectionTimer) {
        LogSvc.warn('[CstaSvc]: Queue new forwarding request until loop detected timer expires');
        _pendingFwdRequest = cb;
        return;
      }

      if (Date.now() - _lastSetForwardingTimestamp < LOOP_DETECTION_INTERVAL) {
        // Increase the loop counter if the last SetForwarding was sent less than 2s from the previous
        _loopCounter++;
      } else {
        _loopCounter = 0;
      }
      _lastSetForwardingTimestamp = Date.now();

      LogSvc.debug('[CstaSvc]: Forward loop counter: ' + _loopCounter + ' timestamp: ' + _lastSetForwardingTimestamp);
      if (_loopCounter > MAX_NUMBER_OF_LOOPS) {
        // If the loop counter exceeds the maximum number of loops then a loop is detected
        LogSvc.error('[CstaSvc]: Loop detected while trying to update forwarding data.');
        _pendingFwdRequest = cb;
        _loopDetectionTimer = $timeout(
          function () {
            LogSvc.debug('[CstaSvc]: Loop detection timeout has expired. Process queued request.');
            _loopCounter = 0;
            _loopDetectionTimer = null;
            _pendingFwdRequest && _pendingFwdRequest();
            _pendingFwdRequest = null;
          },
          LOOP_DETECTION_WAIT_TIME,
          false
        );
      } else {
        cb && cb();
      }
    }

    // eslint-disable-next-line complexity
    function handleForwardingEvent(data) {
      if (!data) {
        return;
      }
      var found = _osmoData.forwardList.some(function (element) {
        if (element.forwardingType === data.forwardingType || (isForwardingImmediate(element.forwardingType) && isForwardingImmediate(data.forwardingType))) {
          element.forwardStatus = data.forwardStatus;
          element.forwardDN = data.forwardTo;
          return true;
        }
        return false;
      });
      if (!found) {
        _osmoData.forwardList.push({
          forwardDN: data.forwardTo,
          forwardStatus: data.forwardStatus,
          forwardingType: data.forwardingType
        });
      }

      var cfType = convertFromCstaFWType(data.forwardingType);
      switch (cfType) {
        case CallForwardingTypes.Immediate.name:
          $rootScope.localUser.cfData.Immediate.cfwAvailable = true;
          $rootScope.localUser.cfData.Immediate.cfwNumber = !isVMNumber(data.forwardTo) && PhoneNumberFormatter.format(data.forwardTo);
          $rootScope.localUser.cfData.Immediate.cfwEnabled = data.forwardStatus && !isVMNumber(data.forwardTo);

          $rootScope.localUser.cfData.VM.cfwAvailable = !!(_osmoData.vm || _osmoData.vmGNF);
          $rootScope.localUser.cfData.VM.cfwNumber = _osmoData.vm;
          $rootScope.localUser.cfData.VM.cfwEnabled = data.forwardStatus && isVMNumber(data.forwardTo);
          break;
        case CallForwardingTypes.NoAnswer.name:
          if ($rootScope.localUser.isOsBizCTIEnabled && isOSBizCallRoutingSupported() && !data.forwardStatus) {
            $rootScope.localUser.reroutingPhoneNumber = '';
          } else if ($rootScope.unifyPhoneLabs.CALL_FORWARD_NO_REPLY && $rootScope.localUser.isOSV) {
            $rootScope.localUser.cfData.NoAnswer.cfwAvailable = true;
            $rootScope.localUser.cfData.NoAnswer.cfwNumber = PhoneNumberFormatter.format(data.forwardTo);
            $rootScope.localUser.cfData.NoAnswer.cfwEnabled = data.forwardStatus;
          } else if ($rootScope.localUser.isMiV5000) {
            $rootScope.localUser.cfData.NoAnswer.cfwAvailable = true;
            $rootScope.localUser.cfData.NoAnswer.cfwNumber = !isVMNumber(data.forwardTo) && PhoneNumberFormatter.format(data.forwardTo);
            $rootScope.localUser.cfData.NoAnswer.cfwEnabled = data.forwardStatus && !isVMNumber(data.forwardTo);

            $rootScope.localUser.cfData.VMNoAnswer.cfwAvailable = !!(_osmoData.vm || _osmoData.vmGNF);
            $rootScope.localUser.cfData.VMNoAnswer.cfwNumber = _osmoData.vm;
            $rootScope.localUser.cfData.VMNoAnswer.cfwEnabled = data.forwardStatus && isVMNumber(data.forwardTo);
          }
          break;
        case CallForwardingTypes.Busy.name:
          if ($rootScope.localUser.isMiV5000) {
            $rootScope.localUser.cfData.Busy.cfwAvailable = true;
            $rootScope.localUser.cfData.Busy.cfwNumber = !isVMNumber(data.forwardTo) && PhoneNumberFormatter.format(data.forwardTo);
            $rootScope.localUser.cfData.Busy.cfwEnabled = data.forwardStatus && !isVMNumber(data.forwardTo);

            $rootScope.localUser.cfData.VMBusy.cfwAvailable = !!(_osmoData.vm || _osmoData.vmGNF);
            $rootScope.localUser.cfData.VMBusy.cfwNumber = _osmoData.vm;
            $rootScope.localUser.cfData.VMBusy.cfwEnabled = data.forwardStatus && isVMNumber(data.forwardTo);
          }
          break;
        case 'preferredDevice':
          // OSBiz routing option = alternative number
          if (isOSBizCallRoutingSupported()) {
            if (!data.forwardStatus && isSelectedRouting(RoutingOptions.AlternativeNumber)) {
              setRoutingOption(RoutingOptions.DefaultRouting);
            } else if (data.forwardStatus && $rootScope.localUser.reroutingPhoneNumber) {
              setRoutingOption(RoutingOptions.AlternativeNumber);
            }
          }
          break;
        default:
          break;
      }

      if (data.hasOwnProperty('staticOndActive')) {
        if (data.staticOndActive) {
          _savedStaticOnd = data.staticOndDN;
          if (data.staticOndDN === Utils.cleanPhoneNumber($rootScope.localUser.reroutingPhoneNumber)) {
            setRoutingOption(RoutingOptions.AlternativeNumber);
          } else {
            setRoutingOption(RoutingOptions.Other);
            $rootScope.localUser.otherRoutingNumber = PhoneNumberFormatter.format(data.staticOndDN);
          }
        } else {
          _savedStaticOnd = '';
          if (isSelectedRouting(RoutingOptions.AlternativeNumber) || isSelectedRouting(RoutingOptions.Other)) {
            setRoutingOption(RoutingOptions.DefaultRouting);
          }
        }

        LogSvc.debug('[CstaSvc]: Publish /csta/staticOndUpdated event');
        PubSubSvc.publish('/csta/staticOndUpdated');
      }

      if (data.hasOwnProperty('voicemailActive')) {
        $rootScope.localUser.voicemailPBXEnabled = true;
        $rootScope.localUser.voicemailActive = data.voicemailActive;
        $rootScope.localUser.voicemailRingDuration = data.voicemailRingDuration;
        _savedVMRingDuration = data.voicemailRingDuration;
      }

      if (data.hasOwnProperty('mainRingDuration')) {
        $rootScope.localUser.ringDurationEnabled = true;
        $rootScope.localUser.mainRingDuration = data.mainRingDuration;
        $rootScope.localUser.clientRingDuration = data.clientRingDuration;
        $rootScope.localUser.cellRingDuration = data.cellRingDuration;
        _savedMainRingDuration = data.mainRingDuration;
        _savedClientRingDuration = data.clientRingDuration;
        _savedCellRingDuration = data.cellRingDuration;
        $rootScope.localUser.alternativeNumber = data.alternativeNumber;
        $rootScope.localUser.routeToCell = data.routeToCell;
        $rootScope.localUser.ringDurationConfigurable = data.hasOwnProperty('overrideProfile') ? data.overrideProfile : !$rootScope.localUser.isOSV;
      }

      if ($rootScope.unifyPhoneLabs.CALL_FORWARD_NO_REPLY && data.forwardingCapsChanged) {
        $rootScope.localUser.cfData.Immediate.cfwAvailable = $rootScope.localUser.cfData.NoAnswer.cfwAvailable = false;
        _that.getForwarding(handleGetForwardingResp);
      }
      LogSvc.debug('[CstaSvc]: Publish /localUser/update event');
      PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);

      LogSvc.debug('[CstaSvc]: Publish /csta/forwardingEvent');
      PubSubSvc.publish('/csta/forwardingEvent');
    }

    function handleAgentEvent(event) {
      if ($rootScope.localUser) {
        $rootScope.localUser.isOsBizCTIEnabled ? handleAgentEventOSBiz(event) : handleAgentEventATC(event);
      }
    }

    function handleAgentEventOSBiz(event) {
      if (!event || !event.agentDevice || !getRegistrationData() || event.agentGroup !== _osmoData.onsFQN) {
        //NGTC-15026: Support hunt groups for OsBiz
        switch (event.name) {
          case 'AgentReadyEvent':
          case 'AgentNotReadyEvent':
            if (event.huntGroupList && _huntGroupList) {
              //find the hunt group in the _huntGroupList and update the state from event.huntGroupList
              if (event.huntGroupList.length > 0) {
                // Iterate through all hunt groups in the event and update their state in _huntGroupList
                event.huntGroupList.forEach(function (eventHg) {
                  var hg = _huntGroupList.find(function (huntGroup) {
                    return huntGroup.pilotDn === eventHg.pilotDn;
                  });
                  if (hg) {
                    hg.available = eventHg.available;
                  }
                });
              }

              // Set agentStateReady to true if all hunt groups are available else false.
              // _huntGroupList is expected to be small; .every() is efficient for this use case.
              $rootScope.localUser.agentStateReady = _huntGroupList.every(function (huntGroup) {
                return huntGroup.available;
              });

              LogSvc.debug('[CstaSvc]: handleAgentEventOSBiz - isAgent:', $rootScope.localUser.isAgent);
              LogSvc.debug('[CstaSvc]: Publish /agent/state/ready event');
              PubSubSvc.publish('/agent/state/ready', [_that.getGlobalAgentState(), $rootScope.localUser]);
            }
            break;
          default:
            return;
        }
        return;
      }

      var agentTarget = getCallPosition(event.agentDevice);
      var oldRoutingOption = $rootScope.localUser.selectedRoutingOption;
      switch (event.name) {
        case 'AgentReadyEvent':
          if (
            (agentTarget === Targets.WebRTC && isSelectedRouting(RoutingOptions.DeskPhone)) ||
            (agentTarget === Targets.Desk && isSelectedRouting(RoutingOptions.UnifyPhoneClient))
          ) {
            setRoutingOption(RoutingOptions.DefaultRouting);
          }
          break;
        case 'AgentNotReadyEvent':
          if (agentTarget === Targets.WebRTC) {
            setRoutingOption(RoutingOptions.DeskPhone);
          } else if (agentTarget === Targets.Desk) {
            setRoutingOption(RoutingOptions.UnifyPhoneClient);
          }
          break;
      }

      if ($rootScope.localUser.selectedRoutingOption !== oldRoutingOption) {
        LogSvc.debug('[CstaSvc]: Publish /csta/agentStateUpdated');
        PubSubSvc.publish('/csta/agentStateUpdated');
      }
    }

    function handleAgentEventATC(event) {
      LogSvc.debug('[CstaSvc]: handleAgentEventATC event:', event.name);

      switch (event.name) {
        case 'AgentReadyEvent':
          $rootScope.localUser.agentStateReady = true;
          break;
        case 'AgentNotReadyEvent':
        case 'AgentBusyEvent':
        case 'AgentWorkingAfterCallEvent':
          $rootScope.localUser.agentStateReady = false;
          break;
        case 'AgentLoggedOffEvent':
          $rootScope.localUser.isOSVOrOS4KAgent = false;
          $rootScope.localUser.agentStateReady = false;
          $rootScope.localUser.isAgent = $rootScope.localUser.isOS4KClassicHuntGroup;
          break;
        case 'AgentLoggedOnEvent':
          $rootScope.localUser.isOSVOrOS4KAgent = true;
          $rootScope.localUser.agentStateReady = false;
          $rootScope.localUser.isAgent = true;
          break;
        default:
          return;
      }
      if ($rootScope.localUser.isOSV) {
        _huntGroupList = event.huntGroupList;
      }

      LogSvc.debug('[CstaSvc]: handleAgentEventATC - isAgent:', $rootScope.localUser.isAgent);
      LogSvc.debug('[CstaSvc]: Publish /agent/state/ready event');
      PubSubSvc.publish('/agent/state/ready', [_that.getGlobalAgentState(), $rootScope.localUser]);
    }

    function handover(call, target, destination, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }

      if (
        !call.atcCallInfo ||
        (!call.checkCstaState([CstaCallState.Active]) && !call.atcCallInfo.isHandoverAllowed() && !call.atcCallInfo.isDeflectAllowed())
      ) {
        cb && cb('res_MoveCallFailed');
        return;
      }
      if (!isTargetAllowed(call, target)) {
        cb && cb('The selected target is not available for this call.');
        return;
      }

      var isOsBizOrMiVB = $rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiVB;
      var newDestination = buildNewDestination(target, destination, isOsBizOrMiVB, isOsBizOrMiVB);
      if (!newDestination) {
        cb && cb('Failed to create new destination.');
        return;
      }

      var data = {
        autoAnswer: target === Targets.Desk || target === Targets.WebRTC
      };

      if (call.checkCstaState([CstaCallState.Ringing, CstaCallState.Parked])) {
        // Need to send a CSTA Deflect Call request instead of CSTA SST request
        data.request = 'DeflectCall';
        data.callToBeDiverted = getCstaConnectionForRequest(call.atcCallInfo);
        data.newDestination = newDestination;

        // 4K sends the DeflectCall response after sending the new INVITE to the destination, so we need to inform
        // unifyPhoneCallControlSvc earlier about the incoming handover call
        if (!$rootScope.localUser.isOSV) {
          LogSvc.debug('[CstaSvc]: Publish /csta/handover event');
          PubSubSvc.publish('/csta/handover', call.atcCallInfo.getCstaCallId());
        }
        call.setAtcHandoverInProgress();
        sendCstaRequest(data, function (err) {
          if (err) {
            cb && cb('res_MoveCallFailed');
            call.clearAtcHandoverInProgress();
            if (!$rootScope.localUser.isOSV) {
              // Inform unifyPhoneCallControlSvc that the handover failed
              LogSvc.debug('[CstaSvc]: Publish /atccall/moveFailed event');
              PubSubSvc.publish('/atccall/moveFailed', [true]);
            }
          } else {
            if ($rootScope.localUser.isOSV) {
              LogSvc.debug('[CstaSvc]: Publish /csta/handover event');
              PubSubSvc.publish('/csta/handover', call.atcCallInfo.getCstaCallId());
            }
            // 4K sends the Diverted after the call has been redirected, which is too late to set the
            // ignoreCall flag
            if (target === Targets.WebRTC) {
              call.atcCallInfo.setIgnoreCall(true);
            }
            call.atcCallInfo.transferCb = cb;

            setLocalCallAsPulled(call.atcCallInfo.getCstaCallId(), newDestination);
          }
        });
      } else {
        data.request = 'SingleStepTransferCall';
        data.activeCall = getCstaConnectionForRequest(call.atcCallInfo);
        data.transferredTo = newDestination;
        data.seamlessHandover = call.atcCallInfo.isSeamlessHandoverAllowed();

        // 4K sends the SST response after sending the new INVITE to the destination, so we need to inform
        // unifyPhoneCallControlSvc earlier about the incoming handover call
        if (!$rootScope.localUser.isOSV) {
          LogSvc.debug('[CstaSvc]: Publish /csta/handover event');
          PubSubSvc.publish('/csta/handover', call.atcCallInfo.getCstaCallId());
        }
        call.setAtcHandoverInProgress();

        sendCstaRequest(data, function (err, rs) {
          if (err || !rs) {
            cb && cb('res_MoveCallFailed');
            call.clearAtcHandoverInProgress();
            if (!$rootScope.localUser.isOSV) {
              // Inform unifyPhoneCallControlSvc that the handover failed
              LogSvc.debug('[CstaSvc]: Publish /atccall/moveFailed event');
              PubSubSvc.publish('/atccall/moveFailed', [true]);
            }
          } else {
            storeTransferredCall(data.activeCall.cID, rs);
            _movingCall = {
              primaryCallId: data.activeCall.cID,
              newCallId: rs.transferredCall.cID,
              direction: call.direction,
              redirectingUser: call.redirectingUser,
              transferCb: cb
            };

            if ($rootScope.localUser.isOSV) {
              LogSvc.debug('[CstaSvc]: Publish /csta/handover event');
              PubSubSvc.publish('/csta/handover', call.atcCallInfo.getCstaCallId());
            }
            call.atcCallInfo.transferCb = cb;
          }
        });
      }
    }

    function createEarlyAtcRemoteCall(destination, target) {
      var earlyAtcRemoteCall = createAtcRemoteCall(destination.dialedDn);
      setCallPeerUser(earlyAtcRemoteCall, destination.dialedDn, destination.dialedDn, destination.toName);
      earlyAtcRemoteCall.setCstaState(CstaCallState.Delivered);
      earlyAtcRemoteCall.direction = CallDirection.OUTGOING;
      earlyAtcRemoteCall.setPosition(target);
      var display = {
        dn: destination.dialedDn,
        fqn: destination.dialedDn,
        name: destination.toName || ''
      };
      earlyAtcRemoteCall.atcCallInfo.setPartnerDisplay(display);
      earlyAtcRemoteCall.atcCallInfo.setIgnoreCall(true);
      _atcRemoteCalls[destination.dialedDn] = earlyAtcRemoteCall;

      if (target === Targets.WebRTC && ($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiVB)) {
        var options = { reuseSessionCtrl: _activeCall && _activeCall.sessionCtrl };
        var secondOsBizLocalCall = new LocalCall(_telephonyConversation, options);
        secondOsBizLocalCall.atcCallInfo = earlyAtcRemoteCall.atcCallInfo;
        setCallPeerUser(secondOsBizLocalCall, earlyAtcRemoteCall.peerUser.phoneNumber, null, earlyAtcRemoteCall.peerUser.displayName);
        secondOsBizLocalCall.isOsBizSecondCall = true;
        secondOsBizLocalCall.setState(CallState.Initiated);
        secondOsBizLocalCall.direction = CallDirection.OUTGOING;
        removeAtcRemoteCall(destination.dialedDn, true);
        _telephonyConversation.call = secondOsBizLocalCall;
        LogSvc.debug('[CstaSvc]: Publish /conversation/update event. convId = ', _telephonyConversation.convId);
        PubSubSvc.publish('/conversation/update', [_telephonyConversation]);

        UnifyPhoneCallControlSvc.addCallToList(secondOsBizLocalCall);
        LogSvc.debug('[CstaSvc]: Publish /call/state event. callId = ' + secondOsBizLocalCall.callId + ', state = ' + secondOsBizLocalCall.state.name);
        PubSubSvc.publish('/call/state', [secondOsBizLocalCall]);
      } else {
        publishAtcCallInfo(earlyAtcRemoteCall);
        LogSvc.debug('[CstaSvc]: Created new Remote Call object: ', earlyAtcRemoteCall);
      }
    }

    function updateEarlyAtcRemoteCall(connection, destination, target) {
      var call = findAtcRemoteCall(destination.dialedDn);
      if (!call) {
        call = createAtcRemoteCall(connection.cID);
        setCallPeerUser(call, destination.dialedDn, destination.dialedDn, destination.toName);
        call.setCstaState(CstaCallState.Delivered);
        call.direction = CallDirection.OUTGOING;
        call.atcCallInfo.setCstaConnection(connection);
        call.setPosition(target);
        var display = {
          dn: destination.dialedDn,
          fqn: destination.dialedDn,
          name: destination.toName || ''
        };
        call.atcCallInfo.setPartnerDisplay(display);
      } else {
        call.atcCallInfo.setCstaConnection(connection);
        call.setCallIdForTelephony(connection.cID);
        call.setCstaState(CstaCallState.Delivered);
        call.atcCallInfo.setIgnoreCall(false);
        if (destination.dtmfDigits) {
          call.dtmfDigits = destination.dtmfDigits;
        }
        delete _atcRemoteCalls[destination.dialedDn];
      }
      call.atcCallInfo.setOriginalPartnerDisplay(call.atcCallInfo.getPartnerDisplay());
      _atcRemoteCalls[connection.cID] = call;
      publishAtcCallInfo(call);
      LogSvc.debug('[CstaSvc]: Created new Remote Call object: ', call);
    }

    function createCampOnLocalCall(earlyRemoteAtcCall, servicesPermitted) {
      if (!earlyRemoteAtcCall || !_activeCall) {
        return;
      }

      LogSvc.debug('[CstaSvc]: Creating local waiting call');

      earlyRemoteAtcCall.direction = CallDirection.INCOMING;
      earlyRemoteAtcCall.setPosition(Targets.WebRTC);
      if (servicesPermitted) {
        earlyRemoteAtcCall.atcCallInfo.setServicesPermitted(servicesPermitted);
      }
      earlyRemoteAtcCall.atcCallInfo.setIgnoreCall(false);

      // Create new local incoming call in state ringing
      var options = { reuseSessionCtrl: _activeCall && _activeCall.sessionCtrl };
      var secondOsBizLocalCall = new LocalCall(_telephonyConversation, options);
      secondOsBizLocalCall.atcCallInfo = earlyRemoteAtcCall.atcCallInfo;
      setCallPeerUser(secondOsBizLocalCall, earlyRemoteAtcCall.peerUser.phoneNumber, null, earlyRemoteAtcCall.peerUser.displayName);
      secondOsBizLocalCall.isOsBizSecondCall = true;
      _activeCall.isOsBizFirstCall = true;
      secondOsBizLocalCall.direction = CallDirection.INCOMING;

      _telephonyConversation.call = secondOsBizLocalCall;
      LogSvc.debug('[CstaSvc]: Publish /conversation/update event. convId = ', _telephonyConversation.convId);
      PubSubSvc.publish('/conversation/update', [_telephonyConversation]);

      secondOsBizLocalCall.setState(CallState.Ringing);
      secondOsBizLocalCall.setCstaState(CstaCallState.Ringing);

      UnifyPhoneCallControlSvc.addCallToList(secondOsBizLocalCall);

      // Publish incoming call so that early remote atc call is released and second local call is displayed.
      // Play call waiting sound to user
      LogSvc.debug('[CstaSvc]: Publish /call/incoming event');
      PubSubSvc.publish('/call/incoming', [_telephonyConversation.call]);
    }

    function getCallDevices() {
      var status = {
        webRTCAdapterEnabled: unifyPhone.WebRTCAdapter.enabled,
        browser: unifyPhone.WebRTCAdapter.browser,
        _onsUnregistered: _onsUnregistered
      };
      LogSvc.debug('[CstaSvc]: getCallDevices: ', JSON.stringify(status));
      var devices = [];
      if (unifyPhone.WebRTCAdapter.enabled) {
        devices.push(Targets.WebRTC);
      }
      if (!_onsUnregistered) {
        devices.push(Targets.Desk);
      }
      if (
        (_osmoData && _osmoData.cell) ||
        ($rootScope.localUser.isOsBizCTIEnabled && $rootScope.localUser.reroutingPhoneNumber && isSelectedRouting(RoutingOptions.AlternativeNumber))
      ) {
        devices.push(Targets.Cell);
      }
      return devices;
    }

    function getPushDevices() {
      var devices = [];
      if (!_onsUnregistered) {
        devices.push(Targets.Desk);
      }
      if (_osmoData && _osmoData.cell) {
        devices.push(Targets.Cell);
      }
      return devices;
    }

    function isOsBizTwoRemoteCalls() {
      var hasRemoteCall = _calls.some(function (call) {
        return call.isRemote && !call.isAtcRemote;
      });
      var hasAtcRemoteCall = Object.values(_atcRemoteCalls).some(function (call) {
        return call.isRemote && call.isAtcRemote;
      });
      return $rootScope.localUser.isOsBizCTIEnabled && hasRemoteCall && hasAtcRemoteCall;
    }

    function getAnswerDevices(call) {
      if (!call) {
        return [];
      }
      var devices = [Targets.WebRTC];

      if (call.pickupSession) {
        return devices;
      }

      var hasVM = false;

      if (!_onsUnregistered && !(call.isOsBizSecondCall || isOsBizTwoRemoteCalls())) {
        // A second incoming call from OSBiz can only be answered in the same device as the first call
        devices.push(Targets.Desk);
      }
      if (_osmoData && _osmoData.cell && call.getPosition() !== Targets.Cell) {
        devices.push(Targets.Cell);
      }
      // Only add VM in the list if VM is active
      if (_osmoData && _osmoData.vm && !call.pickupNotification && ($rootScope.localUser.voicemailActive || $rootScope.localUser.isOsBizCTIEnabled)) {
        devices.push(Targets.VM);
        hasVM = true;
      }

      if (!$rootScope.localUser.isOsBizCTIEnabled) {
        return devices;
      }

      // Additional checks for OSBiz
      if (call.atcCallInfo && call.atcCallInfo.isRecalledCall()) {
        // Recalled call on desk cannot be answered locally
        devices.shift();
      }

      switch ($rootScope.localUser.selectedRoutingOption) {
        case RoutingOptions.AlternativeNumber.name:
          if (call.getPosition() === Targets.Cell) {
            // Call is on alternative number, so it cannot be answered locally or on desk.
            // It can only be deflected to VM, if configured.
            devices = hasVM ? [Targets.VM] : [];
          }
          break;
        case RoutingOptions.DeskPhone.name:
          if (call.getPosition() === Targets.Desk) {
            // Call can only be answered in desk or deflected to VM, if configured.
            devices = [Targets.Desk];
            if (hasVM) {
              devices.push(Targets.VM);
            }
          }
          break;
        case RoutingOptions.UnifyPhoneClient.name:
          if (call.direction === CallDirection.INCOMING) {
            // Call cannot be answered on desk
            var idx = devices.indexOf(Targets.Desk);
            if (idx !== -1) {
              devices.splice(idx, 1);
            }
          }
          break;
      }

      if (call.direction === CallDirection.INCOMING && $rootScope.localUser.reroutingPhoneNumber && !isSelectedRouting(RoutingOptions.AlternativeNumber)) {
        // Call can be deflected to alternative number
        devices.push(Targets.Cell);
      }

      return devices;
    }

    function getMyDevices() {
      var devices = getCallDevices();
      if (_osmoData && _osmoData.vm) {
        devices.push(Targets.VM);
      }

      // For OSBiz, alternative number can be a deflect target device but not a makeCall device.
      if ($rootScope.localUser.isOsBizCTIEnabled && $rootScope.localUser.reroutingPhoneNumber && !devices.includes(Targets.Cell)) {
        devices.push(Targets.Cell);
      }

      devices.push(Targets.Other);
      return devices;
    }

    function handleFirstCall(call) {
      var connection = (call.atcCallInfo && call.atcCallInfo.getCstaConnection()) || _incomingCallConnection;
      if (_primaryClient) {
        var data = {
          request: 'DeflectCall',
          callToBeDiverted: connection,
          newDestination: buildNewDestination(Targets.Desk)
        };
        sendCstaRequest(data);
      }
    }

    function handleDataForRejectedCall(call, eventCause) {
      var data = null;
      var connection = (call.atcCallInfo && call.atcCallInfo.getCstaConnection()) || _incomingCallConnection;
      LogSvc.debug('[CstaSvc]: handleSecondCall: connection: ', connection);
      if (
        $rootScope.localUser.isTeamsCallBusyHandlingEnabled &&
        $rootScope.localUser.userPresenceState.state === Constants.PresenceState.BUSY_IN_AN_EXTERNAL_CALL &&
        !getAgentHuntGroupAvailable()
      ) {
        data = {
          request: 'ClearConnection',
          connectionToBeCleared: connection,
          reason: 'busy'
        };
        return data;
      }
      if ($rootScope.localUser.selectedBusyHandlingOption !== BusyHandlingOptions.DefaultRouting.name) {
        switch ($rootScope.localUser.selectedBusyHandlingOption) {
          case BusyHandlingOptions.SendToAlternativeNumber.name:
            data = {
              request: 'DeflectCall',
              callToBeDiverted: connection,
              newDestination: buildNewDestination(Targets.Cell)
            };
            break;
          case BusyHandlingOptions.BusySignal.name:
            data = {
              request: 'ClearConnection',
              connectionToBeCleared: connection,
              reason: 'busy'
            };
            break;
          case BusyHandlingOptions.SendToVM.name:
            // NGTC-10773: OS4K-Pilot HG-Hanging call when 1st HG member has busy settings to VM
            if ($rootScope.localUser && $rootScope.localUser.isOS4KClassicHuntGroup && eventCause === 'multipleAlerting') {
              LogSvc.debug('[CstaSvc]: handleSecondCall: sendToVM while on OS4KClassic HG, send busy');
              data = {
                request: 'ClearConnection',
                connectionToBeCleared: connection,
                reason: 'busy'
              };
            } else {
              data = {
                request: 'DeflectCall',
                callToBeDiverted: connection,
                newDestination: buildNewDestination(Targets.VM)
              };
            }
            break;
          default:
            return data;
        }
      }
      return data;
    }

    function sendRejectCstaRequest(call, eventCause) {
      var data = null;
      var connection = (call.atcCallInfo && call.atcCallInfo.getCstaConnection()) || _incomingCallConnection;
      LogSvc.debug('[CstaSvc]: handleSecondCall: connection: ', connection);
      if (connection && connection.cID && _primaryClient) {
        data = handleDataForRejectedCall(call, eventCause);
        sendCstaRequest(data, function (err) {
          if (!err) {
            if (data.request === 'ClearConnection') {
              if (call.atcCallInfo) {
                call.atcCallInfo.setMissedReason(MissedReasonTypes.BUSY);
                // This is the case that the second call was rejected upon the Offered event. The CSTA
                // state needs to be changed
                if (call.checkCstaState(CstaCallState.Offered)) {
                  call.setCstaState(CstaCallState.Failed);
                }
              } else if (AtcRegistrationSvc.isClearConnectionBusySupported()) {
                var remoteCall = _atcRemoteCalls[connection.cID] || createAtcRemoteCall(connection.cID);
                remoteCall.peerUser = call.peerUser;
                remoteCall.direction = CallDirection.INCOMING;
                remoteCall.creationTime = call.creationTime;
                remoteCall.atcCallInfo.setMissedReason(MissedReasonTypes.BUSY);
                _atcRemoteCalls[connection.cID] = remoteCall;
                removeAtcRemoteCall(connection.cID);
              }
            }
          }
        });
        _incomingCallConnection = {};
      }
    }

    function getAgentHuntGroupAvailable() {
      if (_that.getGlobalAgentState()) {
        return true;
      }
      var availability = false;
      if ($rootScope.localUser.isOSV || $rootScope.localUser.isOsBizCTIEnabled) {
        availability =
          _huntGroupList &&
          _huntGroupList.some(function (huntGroup) {
            return huntGroup.available;
          });
      }
      return availability;
    }

    function handleFirstReceivedCall(call, event, shouldRejectCall) {
      var data = null;
      LogSvc.debug('[CstaSvc]: handleFirstReceivedCall shouldReject: ', shouldRejectCall);
      // In case of user isAgent (HG) reject call only if agent state in unavailable
      if (shouldRejectCall) {
        sendRejectCstaRequest(call, event.cause);
      } else {
        data = {
          request: 'AcceptCall',
          callToBeAccepted: getCstaConnectionForRequest(call.atcCallInfo)
        };
        sendCstaRequest(data);
        _incomingCallConnection = {};
      }
    }

    function handleSecondCall(call, eventCause, shouldRejectCall) {
      LogSvc.debug('[CstaSvc]: handleSecondCall: reject call: ', shouldRejectCall);
      // In case of user isAgent (HG) reject call only if agent state in unavailable
      // or handle user Busy settings
      if (shouldRejectCall) {
        sendRejectCstaRequest(call, eventCause);
      } else if (call.checkCstaState(CstaCallState.Offered)) {
        var data = {
          request: 'AcceptCall',
          callToBeAccepted: call.atcCallInfo.getCstaConnection()
        };
        sendCstaRequest(data);
        _incomingCallConnection = {};
      }
    }

    function updateRoutingOptionATC(selectedOption, oldOption, cb) {
      cb = cb || function () {};
      switch (selectedOption) {
        case RoutingOptions.AlternativeNumber.name:
          _that.updateStaticOnd(true, function (err) {
            if (!err) {
              _that.setForwardingToVM(false, cb);
            } else {
              cb(err);
            }
          });
          break;
        case RoutingOptions.VM.name:
          _that.setForwardingToVM(true, function (err) {
            if (err) {
              cb(err);
            } else {
              _that.updateStaticOnd(false, cb);
            }
          });
          break;
        default:
          _that.setForwardingToVM(false, function (err) {
            if (err) {
              cb(err);
            } else if (!_supportsStaticOndSync || oldOption === RoutingOptions.AlternativeNumber.name || oldOption === RoutingOptions.Other.name) {
              _that.updateStaticOnd(false, cb);
            } else {
              cb();
            }
          });
          break;
      }
    }

    function updateRoutingOptionOSBiz(selectedOption, oldOption, cb) {
      if (selectedOption === oldOption) {
        cb && cb();
        return;
      }
      switch (selectedOption) {
        case RoutingOptions.AlternativeNumber.name:
          disableRoutingOption(oldOption, function (err) {
            if (err) {
              cb && cb(err);
            } else {
              _that.setRoutingOptionReroutingNumber($rootScope.localUser.reroutingPhoneNumber, cb);
            }
          });
          break;

        case RoutingOptions.DeskPhone.name:
          disableRoutingOption(oldOption, function (err) {
            if (err) {
              cb && cb(err);
            } else {
              _that.setAgentStateOSBiz(false, RoutingOptions.UnifyPhoneClient.name, cb);
            }
          });
          break;

        case RoutingOptions.UnifyPhoneClient.name:
          disableRoutingOption(oldOption, function (err) {
            if (err) {
              cb && cb(err);
            } else {
              _that.setAgentStateOSBiz(false, RoutingOptions.DeskPhone.name, cb);
            }
          });
          break;

        default:
          // Set default routing option
          disableRoutingOption(oldOption, cb);
          break;
      }
    }

    function evaluateDndState(newState) {
      // If snooze status does not match to OSBiz DND status, update UnifyPhone client
      if ($rootScope.localUser.userPresenceState.state !== Constants.PresenceState.DND && newState) {
        LogSvc.debug('[CstaSvc]: DND is enabled on the switch. Set snooze on UnifyPhone.');
        UserProfileSvc.snooze(-1);
      } else if ($rootScope.localUser.userPresenceState.state === Constants.PresenceState.DND && !newState) {
        LogSvc.debug('[CstaSvc]: DND is disabled on the switch. Resume snooze on UnifyPhone.');
        UserProfileSvc.resume();
      }
    }

    function handleDoNotDisturbEvent(data) {
      if (isDNDSupported() && data && _primaryClient) {
        evaluateDndState(data.doNotDisturbOn);
      } else {
        $rootScope.localUser.doNotDisturbOn = data.doNotDisturbOn;
      }
    }

    function handleGetDoNotDisturbResp(err, data) {
      if (!err && data) {
        if ($rootScope.localUser.isOsBizCTIEnabled) {
          evaluateDndState(data.doNotDisturbOn);
        } else {
          $rootScope.localUser.doNotDisturbOn = data.doNotDisturbOn;
        }
      }
    }

    function convertToCstaFWType(cfType) {
      switch (cfType) {
        case CallForwardingTypes.Immediate.name:
        case CallForwardingTypes.VM.name:
          return 'forwardImmediate';
        case CallForwardingTypes.NoAnswer.name:
        case CallForwardingTypes.VMNoAnswer.name:
          return 'forwardNoAns';
        case CallForwardingTypes.Busy.name:
        case CallForwardingTypes.VMBusy.name:
          return 'forwardBusy';
        default:
          return '';
      }
    }

    function convertFromCstaFWType(cstaFWType) {
      switch (cstaFWType) {
        case 'forwardImmediate':
          return CallForwardingTypes.Immediate.name;
        case 'forwardImmInt':
        case 'forwardImmExt':
          return $rootScope.localUser.isOsBizCTIEnabled ? CallForwardingTypes.Immediate.name : '';
        case 'forwardNoAns':
          return CallForwardingTypes.NoAnswer.name;
        case 'preferredDevice':
          return $rootScope.localUser.isOsBizCTIEnabled ? 'preferredDevice' : '';
        case 'forwardBusy':
          return CallForwardingTypes.Busy.name;
        default:
          return '';
      }
    }

    function processForwardList(forwardList, preferredDeviceOnly) {
      // eslint-disable-next-line complexity
      forwardList &&
        forwardList.forEach(function (element) {
          var cfType = convertFromCstaFWType(element.forwardingType);
          if (preferredDeviceOnly && cfType !== 'preferredDevice') {
            return;
          }
          switch (cfType) {
            case CallForwardingTypes.Immediate.name:
              $rootScope.localUser.cfData.Immediate.cfwAvailable = true;
              $rootScope.localUser.cfData.Immediate.cfwEnabled = element.forwardStatus && !isVMNumber(element.forwardDN);
              $rootScope.localUser.cfData.Immediate.cfwNumber = !isVMNumber(element.forwardDN) && PhoneNumberFormatter.format(element.forwardDN);

              $rootScope.localUser.cfData.VM.cfwAvailable = !!(_osmoData.vm || _osmoData.vmGNF);
              $rootScope.localUser.cfData.VM.cfwEnabled = element.forwardStatus && isVMNumber(element.forwardDN);
              $rootScope.localUser.cfData.VM.cfwNumber = _osmoData.vm;
              break;
            case CallForwardingTypes.Busy.name:
              if ($rootScope.localUser.isMiV5000) {
                $rootScope.localUser.cfData.Busy.cfwAvailable = true;
                $rootScope.localUser.cfData.Busy.cfwEnabled = element.forwardStatus && !isVMNumber(element.forwardDN);
                $rootScope.localUser.cfData.Busy.cfwNumber = !isVMNumber(element.forwardDN) && PhoneNumberFormatter.format(element.forwardDN);

                $rootScope.localUser.cfData.VMBusy.cfwAvailable = !!(_osmoData.vm || _osmoData.vmGNF);
                $rootScope.localUser.cfData.VMBusy.cfwEnabled = element.forwardStatus && isVMNumber(element.forwardDN);
                $rootScope.localUser.cfData.VMBusy.cfwNumber = _osmoData.vm;
              }
              break;
            case CallForwardingTypes.NoAnswer.name:
              if ($rootScope.localUser.isOsBizCTIEnabled && isOSBizCallRoutingSupported()) {
                if (!element.forwardStatus) {
                  $rootScope.localUser.reroutingPhoneNumber = '';
                  if (_osmoData && _osmoData.osmoDeviceList && isSelectedRouting(RoutingOptions.AlternativeNumber)) {
                    // For mulap user, also make sure that routing option is not set to alternative number
                    setRoutingOption(RoutingOptions.DefaultRouting);
                  }
                } else {
                  var target = element.forwardDN && extractOnd(element.forwardDN);
                  if (target && target[1] !== $rootScope.localUser.reroutingPhoneNumber) {
                    $rootScope.localUser.reroutingPhoneNumber = target[1];
                  }
                  if (_osmoData && _osmoData.osmoDeviceList && _osmoData.osmoDeviceList.length > 1) {
                    // For mulap user, it is also necessary to set GF using mobile number to get the state of the alternativeNumber Routing option
                    _that.getForwardingPreferredDevice(_osmoData.osmoDeviceList[1], function (err, data) {
                      data && (data.preferredDeviceOnly = true);
                      handleGetForwardingResp(err, data);
                    });
                  }
                }
              } else if ($rootScope.unifyPhoneLabs.CALL_FORWARD_NO_REPLY && $rootScope.localUser.isOSV) {
                $rootScope.localUser.cfData.NoAnswer.cfwType = CallForwardingTypes.NoAnswer.name;
                $rootScope.localUser.cfData.NoAnswer.cfwAvailable = true;
                $rootScope.localUser.cfData.NoAnswer.cfwEnabled = element.forwardStatus;
                $rootScope.localUser.cfData.NoAnswer.cfwNumber = PhoneNumberFormatter.format(element.forwardDN);
              } else if ($rootScope.localUser.isMiV5000) {
                $rootScope.localUser.cfData.NoAnswer.cfwAvailable = true;
                $rootScope.localUser.cfData.NoAnswer.cfwEnabled = element.forwardStatus && !isVMNumber(element.forwardDN);
                $rootScope.localUser.cfData.NoAnswer.cfwNumber = !isVMNumber(element.forwardDN) && PhoneNumberFormatter.format(element.forwardDN);

                $rootScope.localUser.cfData.VMNoAnswer.cfwAvailable = !!(_osmoData.vm || _osmoData.vmGNF);
                $rootScope.localUser.cfData.VMNoAnswer.cfwEnabled = element.forwardStatus && isVMNumber(element.forwardDN);
                $rootScope.localUser.cfData.VMNoAnswer.cfwNumber = _osmoData.vm;
              }
              break;
            case 'preferredDevice':
              // OSBiz routing option = alternative number
              if (!element.forwardStatus && isSelectedRouting(RoutingOptions.AlternativeNumber)) {
                setRoutingOption(RoutingOptions.DefaultRouting);
              } else if (element.forwardStatus && $rootScope.localUser.reroutingPhoneNumber) {
                setRoutingOption(RoutingOptions.AlternativeNumber);
              }
              break;
            default:
              break;
          }
        });
    }

    function initCfData() {
      Object.values(CallForwardingTypes).forEach(function (cfwType) {
        $rootScope.localUser.cfData[cfwType.name] = {
          cfwType: cfwType.name,
          cfwEnabled: false,
          cfwAvailable: false,
          cfwNumber: '',
          label: $rootScope.localUser.isMiV5000 ? cfwType.labelMiV5000 : cfwType.label,
          description: $rootScope.localUser.isMiV5000 ? cfwType.descriptionMiV5000 : cfwType.description,
          message: cfwType.message
        };
      });
    }

    function handleGetForwardingResp(err, data) {
      // When handling Forward response for mulap (preferredDeviceOnly) coming as a secondary response do not init data again
      if (!data || !data.preferredDeviceOnly) {
        initCfData();
      }

      if (err || !data) {
        return;
      }

      $rootScope.localUser.cfData.Immediate.cfwAvailable = !$rootScope.localUser.isOSV;
      processForwardList(data.forwardList, data.preferredDeviceOnly);

      _osmoData.forwardList = data.forwardList || [];

      if (data.hasOwnProperty('voicemailActive')) {
        $rootScope.localUser.voicemailPBXEnabled = true;
        $rootScope.localUser.voicemailActive = data.voicemailActive;
        $rootScope.localUser.voicemailRingDuration = data.voicemailRingDuration;
        _savedVMRingDuration = data.voicemailRingDuration;
      }

      if (data.mainRingDuration) {
        $rootScope.localUser.ringDurationEnabled = true;
        $rootScope.localUser.mainRingDuration = data.mainRingDuration;
        $rootScope.localUser.clientRingDuration = data.clientRingDuration;
        $rootScope.localUser.cellRingDuration = data.cellRingDuration;
        _savedMainRingDuration = data.mainRingDuration;
        _savedClientRingDuration = data.clientRingDuration;
        _savedCellRingDuration = data.cellRingDuration;

        $rootScope.localUser.alternativeNumber = data.alternativeNumber || Utils.cleanPhoneNumber($rootScope.localUser.reroutingPhoneNumber);
        $rootScope.localUser.routeToCell = data.routeToCell;
        $rootScope.localUser.ringDurationConfigurable = data.hasOwnProperty('overrideProfile') ? data.overrideProfile : !$rootScope.localUser.isOSV;
      }

      if (data.hasOwnProperty('staticOndActive')) {
        _supportsStaticOndSync = true;
        if (data.staticOndActive) {
          _savedStaticOnd = data.staticOndDN;
          if (isSelectedRouting(RoutingOptions.AlternativeNumber) && !$rootScope.localUser.reroutingPhoneNumber) {
            // reRegistration after removing alternative number
            setRoutingOption(RoutingOptions.DefaultRouting);
          } else if (data.staticOndDN === Utils.cleanPhoneNumber($rootScope.localUser.reroutingPhoneNumber)) {
            setRoutingOption(RoutingOptions.AlternativeNumber);
          } else {
            setRoutingOption(RoutingOptions.Other);
            $rootScope.localUser.otherRoutingNumber = PhoneNumberFormatter.format(data.staticOndDN);
          }
        } else {
          _savedStaticOnd = '';
          if (isSelectedRouting(RoutingOptions.AlternativeNumber) || isSelectedRouting(RoutingOptions.Other)) {
            setRoutingOption(RoutingOptions.DefaultRouting);
          }
        }

        LogSvc.debug('[CstaSvc]: Publish /csta/staticOndUpdated event');
        PubSubSvc.publish('/csta/staticOndUpdated');
      } else {
        // If OSV does not have OSV-3432 fixed, then StaticOnd status cannot be synchronized.
        _supportsStaticOndSync = false;
      }

      LogSvc.debug('[CstaSvc]: Publish /localUser/update event');
      PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);

      LogSvc.debug('[CstaSvc]: Publish /csta/getForwardingResponse');
      PubSubSvc.publish('/csta/getForwardingResponse');
    }

    function handleGetAgentStateResponseATC(err, data) {
      if (err || !data) {
        return;
      }
      LogSvc.debug('[CstaSvc]: Received GetAgentStateResponse loggedOn state:', data.loggedOnState);

      $rootScope.localUser.isOSVOrOS4KAgent = data.loggedOnState;
      $rootScope.localUser.isAgent = $rootScope.localUser.isOSVOrOS4KAgent || $rootScope.localUser.isOS4KClassicHuntGroup;
      LogSvc.debug('[CstaSvc]: handleGetAgentStateResponseATC - isAgent:', $rootScope.localUser.isAgent);

      if ($rootScope.localUser.isAgent) {
        $rootScope.localUser.agentStateReady = data.agentState === AgentState.Ready;
        if ($rootScope.localUser.isOSV || $rootScope.localUser.isOsBizCTIEnabled) {
          _huntGroupList = data.huntGroupList;
        }
      }

      // NGTC-10415: OS4k - Android and iOS - Agent status is shown in mobile clients,
      // even if agent was logged off during background mode.
      LogSvc.debug('[CstaSvc]: Publish /agent/state/ready event');
      PubSubSvc.publish('/agent/state/ready', [_that.getGlobalAgentState(), $rootScope.localUser]);
    }

    function showPickupNotification(call) {
      if (!Utils.isMobile() && !UnifyPhoneCallControlSvc.getActiveCall()) {
        call =
          call ||
          Object.values(_atcRemoteCalls).find(function (remoteCall) {
            return remoteCall.pickupNotification;
          });
        // NGTC-11064: [Spike] As a user, I want to choose how my client will behave in case of an incoming call
        // when UP is busy "On a Teams call"
        if (
          call &&
          $rootScope.localUser.isTeamsCallBusyHandlingEnabled &&
          $rootScope.localUser.userPresenceState.state === Constants.PresenceState.BUSY_IN_AN_EXTERNAL_CALL
        ) {
          LogSvc.debug('[CstaSvc] showPickupNotification: User has enable BUSY_IN_AN_EXTERNAL_CALL, terminate incoming call with Busy cause');
          sendRejectCstaRequest(call);
          return;
        }
        if (call && call.isPresent() && !call.pickupNotificationShown) {
          publishAtcCallInfo(call);
          LogSvc.debug('[CstaSvc]: Publish /call/pickupNotification event');
          PubSubSvc.publish('/call/pickupNotification', [call]);
          call.pickupNotificationShown = true;
        }
      }
    }

    function setBusy(status, cb) {
      // Do not not send busy if not registration data or
      // if user is standalone (only phone calls exists then, so pbx already knows state)
      if (!getRegistrationData(cb) || $rootScope.localUser.isStandalone) {
        return;
      }
      var data = {
        request: 'SetBusy',
        device: getCstaDeviceId(),
        busyState: status
      };
      sendCstaRequest(data, cb);
    }

    function checkBusyState() {
      if (!$rootScope.localUser.isRingCentral || !$rootScope.localUser.isOsBizCTIEnabled) {
        // No Need to update Busy State
        return;
      }

      // Set busy when user is in a RC side call. Reset busy when user is not in a RC side call.
      var hasCall = $rootScope.localUser.userPresenceState.state === Constants.PresenceState.BUSY;
      var isAvailable = $rootScope.localUser.userPresenceState.state === Constants.PresenceState.AVAILABLE;

      LogSvc.debug('[CstaSvc]: Current busy state: ', _userBusyState);

      if ((_userBusyState && (!hasCall || isAvailable)) || (!_userBusyState && hasCall && !isAvailable)) {
        _userBusyState = !_userBusyState;
        if (_primaryClient) {
          LogSvc.debug('[CstaSvc]: Update busy state. New State: ', _userBusyState);
          setBusy(_userBusyState);
        }
      }
    }

    function publishOsBizCallState(call, state) {
      if (call.isOsBizSecondCall) {
        call.setState(state);
        LogSvc.debug('[CstaSvc]: Publish /conversation/update event. convId = ', _telephonyConversation.convId);
        PubSubSvc.publish('/conversation/update', [_telephonyConversation]);
      }
    }

    function publishMiVBConsulationSound(call) {
      if ($rootScope.localUser.isMiVB && call.isOsBizSecondCall) {
        LogSvc.debug('[CstaSvc]: Publish /sound/call/alerting/started, call ID: ', call.callId);
        PubSubSvc.publish('/sound/call/alerting/started', [call]);
      }
    }

    function hangupOsBizFailedSecondCall(call, cb) {
      var firstCall = call.isOsBizSecondCall ? UnifyPhoneCallControlSvc.findOsBizFirstCall() : findAtcRemoteHeldCall();
      if (call && firstCall && getRegistrationData(cb)) {
        var data = {
          request: 'ReconnectCall',
          heldCall: getCstaConnectionForRequest(firstCall.atcCallInfo),
          activeCall: getCstaConnectionForRequest(call.atcCallInfo)
        };
        sendCstaRequest(data, cb);
        return;
      }
      cb && cb();
    }

    function hangupMiVBFailedTransferCall(heldCall, failedCall, cb) {
      if (heldCall && failedCall && getRegistrationData(cb)) {
        var data = {
          request: 'ReconnectCall',
          heldCall: getCstaConnectionForRequest(heldCall.atcCallInfo),
          activeCall: getCstaConnectionForRequest(failedCall.atcCallInfo)
        };
        sendCstaRequest(data, cb);
        return;
      }
      cb && cb();
    }

    function disableRoutingOption(option, cb) {
      if ($rootScope.localUser.isOsBizCTIEnabled && option) {
        switch (option) {
          case RoutingOptions.AlternativeNumber.name:
            _that.setRoutingOptionReroutingNumber(null, cb);
            return;
          case RoutingOptions.DeskPhone.name:
            _that.setAgentStateOSBiz(true, RoutingOptions.UnifyPhoneClient.name, cb);
            return;
          case RoutingOptions.UnifyPhoneClient.name:
            _that.setAgentStateOSBiz(true, RoutingOptions.DeskPhone.name, cb);
            return;
        }
      }
      cb && cb();
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/conversations/showTelephonyConversation', function (show, conv) {
      LogSvc.debug('[CstaSvc]: Received /conversations/showTelephonyConversation event. show = ', show);
      if (show) {
        _telephonyConversation = conv;
      }
    });

    PubSubSvc.subscribe('/conversation/update', function (conversation) {
      if (_telephonyConversation && _telephonyConversation.convId === conversation.convId) {
        _telephonyConversation = conversation;
      }
    });

    PubSubSvc.subscribe('/atcRegistration/state', function (newState) {
      LogSvc.debug('[CstaSvc]: Received /atcRegistration/state event.');
      _regState = newState;
      if ($rootScope.localUser.isATC) {
        if (!_snapshotPerformed && newState === AtcRegistrationState.Registered && _receivedActiveSessions) {
          snapshotRemoteCallsAndGetFeatureData();
        } else {
          _snapshotPerformed = false;
        }
        if (newState !== AtcRegistrationState.Registered) {
          clearAtcRemoteCalls();
        }
      }
    });

    PubSubSvc.subscribe('/activeSessions/received', function () {
      LogSvc.debug('[CstaSvc]: Received /activeSessions/received event');
      _receivedActiveSessions = true;
      if (!_snapshotPerformed) {
        if (_regState === AtcRegistrationState.Registered) {
          snapshotRemoteCallsAndGetFeatureData();
        }
      }
    });

    PubSubSvc.subscribe('/registration/state', function (state) {
      LogSvc.debug('[CstaSvc]: Received /registration/state event');
      if (state !== RegistrationState.Registered) {
        _receivedActiveSessions = false;
      }
    });

    PubSubSvc.subscribe('/call/incoming', function (incomingCall) {
      if (incomingCall && _telephonyConversation && incomingCall.convId === _telephonyConversation.convId) {
        LogSvc.debug('[CstaSvc]: Received /call/incoming event.');
        // ANS-11246: Since we still don't support a PROGRESS(alerting) message, it is
        // possible for the CSTA Delivered event to arrive before the UnifyPhone INVITE
        // message. This happens because the ATC immediately sends the SIP 180 Ringing
        // response back to the switch without waiting for the alerting indication from
        // the client. Let's check if this is the case here!
        Object.keys(_atcRemoteCalls).some(function (callId) {
          var remoteCall = _atcRemoteCalls[callId];
          if (remoteCall.checkCstaState(CstaCallState.Ringing) && remoteCall.isAtPosition(Targets.WebRTC)) {
            // Found it
            _alertingCall = findAlertingCall();
            if (_alertingCall) {
              LogSvc.debug('[CstaSvc]: Found a matching ATC remote call. Move call info to alerting call.');
              _alertingCall.atcCallInfo = remoteCall.atcCallInfo;
              remoteCall.atcCallInfo = new AtcCallInfo();
              remoteCall.atcCallInfo.setIgnoreCall(true);
              removeAtcRemoteCall(callId);
            }
            return true;
          }
          return false;
        });
      }
    });

    PubSubSvc.subscribe('/atccall/firstcall', function (call) {
      LogSvc.debug('[CstaSvc]: Received /atccall/firstcall event');
      handleFirstCall(call);
    });

    PubSubSvc.subscribe('/atccall/secondcall', function (call) {
      LogSvc.debug('[CstaSvc]: Received /atccall/secondcall event.');
      var shouldReject = checkForCallReject(call);
      handleSecondCall(call, null, shouldReject);
    });

    PubSubSvc.subscribe('/atccall/replace', function (call, oldCallId) {
      LogSvc.debug('[CstaSvc]: Received /atccall/replace event');
      var remoteCall = _atcRemoteCalls[oldCallId];
      if (remoteCall) {
        call.establishedTime = remoteCall.establishedTime;
        var remoteCallAtcCallInfo = remoteCall.atcCallInfo;
        if (!call.atcCallInfo) {
          call.atcCallInfo = new AtcCallInfo();
        }
        call.atcCallInfo.setOriginalPartnerDisplay(remoteCallAtcCallInfo.originalPartnerDisplay);
        removeAtcRemoteCall(oldCallId, true);
      }
    });

    PubSubSvc.subscribe('/callHistory/item/added', function (item) {
      if (item && item.rtc && item.rtc.rtcParticipants && item.rtc.rtcParticipants.length === 2) {
        _lastTwoCallPartners.unshift(item.rtc.rtcParticipants[0].type === 'TELEPHONY' ? item.rtc.rtcParticipants[0] : item.rtc.rtcParticipants[1]);
        if (_lastTwoCallPartners.length > 2) {
          _lastTwoCallPartners.pop();
        }
      }
    });

    PubSubSvc.subscribe('/call/ended', function (call) {
      if (call && call.isTelephonyCall && !call.isRemote) {
        _lastEndedCall = call;
        $timeout.cancel(_endedCallWaitTimer);
        _endedCallWaitTimer = $timeout(function () {
          _lastEndedCall = null;
          _endedCallWaitTimer = null;
        }, MAX_ENDED_CALL_WAIT_TIME);
        if (call.isOsBizSecondCall || !(call.isHandoverInProgress || _atcRemoteCalls[call.cstaCallId])) {
          createJournalEntry(call);
        }
      }
      if (!call.pickupNotification && !call.isRemote) {
        showPickupNotification();
      }
      refreshData();
    });

    PubSubSvc.subscribe('/routing/alternativeNumber/changed', function () {
      LogSvc.debug('[CstaSvc]: Received /routing/alternativeNumber/changed event');
      if ($rootScope.localUser.isOsBizCTIEnabled) {
        _that.setRoutingOptionReroutingNumber($rootScope.localUser.reroutingPhoneNumber);
        // For OSBiz, changing routing option may affect the calling device list
        LogSvc.debug('[TelephonySettingsSvc]: Publish /csta/deviceChange event');
        PubSubSvc.publish('/csta/deviceChange');
      }
      syncRoutingOption();
    });

    PubSubSvc.subscribe('/localUser/update', function () {
      if ($rootScope.localUser.isATC) {
        LogSvc.debug('[CstaSvc]: Received /localUser/update event');
        checkBusyState();
        syncRoutingOption();
      }
    });

    PubSubSvc.subscribe('/user/snooze/start', function () {
      if (isDNDSupported() && _primaryClient) {
        LogSvc.info('[CstaSvc]: Received /user/snooze/start event');
        _that.setDoNotDisturb(true);
      }
    });

    PubSubSvc.subscribe('/user/autoSnooze/start', function () {
      if (isDNDSupported() && _primaryClient) {
        LogSvc.info('[CstaSvc]: Received /user/autoSnooze/start event');
        _that.setDoNotDisturb(true);
      }
    });

    PubSubSvc.subscribe('/user/snooze/end', function () {
      if (isDNDSupported() && _primaryClient) {
        LogSvc.info('[CstaSvc]: Received /user/snooze/end event');
        _that.setDoNotDisturb(false);
      }
    });

    PubSubSvc.subscribe('/user/snooze/resume', function () {
      if (isDNDSupported() && _primaryClient) {
        LogSvc.info('[CstaSvc]: Received /user/snooze/resume event.');
        _that.setDoNotDisturb(false);
      }
    });

    PubSubSvc.subscribe('/language/update', function () {
      _numberNotAvailable = $rootScope.i18n.map.res_NumberNotAvailable;
    });

    PubSubSvc.subscribe('/feature/state/changed', function (featureName, value) {
      if (featureName === Constants.LabFeatureName.CALL_FORWARD_NO_REPLY) {
        LogSvc.debug('[CstaSvc]: Call Forward No Reply has changed status');
        if (value) {
          _that.getForwarding(handleGetForwardingResp);
        } else {
          $rootScope.localUser.cfData.NoAnswer.cfwAvailable = false;
          LogSvc.debug('[CstaSvc]: Publish /csta/forwardingEvent');
          PubSubSvc.publish('/csta/forwardingEvent');
        }
      }
    });

    ///////////////////////////////////////////////////////////////////////////
    // Public interfaces
    ///////////////////////////////////////////////////////////////////////////
    this.alternate = function (heldCall, activeCall, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }
      if (!heldCall || !activeCall) {
        cb && cb('Invalid calls to alternate');
        return;
      }
      heldCall.setRetrieveInProgress();
      activeCall.setHoldInProgress();

      var data = {
        request: 'AlternateCall',
        activeCall: getCstaConnectionForRequest(activeCall.atcCallInfo),
        heldCall: getCstaConnectionForRequest(heldCall.atcCallInfo)
      };
      sendCstaRequest(data, function (err) {
        if (err) {
          // If for some reason the Alternate request failed, reset the inProgress indication
          heldCall.clearRetrieveInProgress();
          activeCall.clearHoldInProgress();
          cb && cb('res_RetrieveCallFailed');
        } else {
          if ($rootScope.localUser.isOsBizCTIEnabled) {
            _telephonyConversation.call = heldCall;
            LogSvc.debug('[CstaSvc]: Publish /conversation/update event. convId = ', _telephonyConversation.convId);
            PubSubSvc.publish('/conversation/update', [_telephonyConversation]);
          }
          cb && cb(null);
        }
      });
    };

    this.conference = function (heldCall, activeCall, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }
      if (!heldCall || !activeCall) {
        cb && cb('Invalid calls to conference');
        return;
      }

      var data = {
        request: 'ConferenceCall',
        activeCall: getCstaConnectionForRequest(activeCall.atcCallInfo),
        heldCall: getCstaConnectionForRequest(heldCall.atcCallInfo)
      };
      sendCstaRequest(data, function (err, rs) {
        if (err || !rs) {
          cb && cb('res_MergeCallFailed');
        } else {
          cb && cb(null);
        }
      });
    };

    this.answer = function (callId, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }

      var call = findAtcRemoteCall(callId);
      if (!call) {
        // Check if there is an alerting call in advancing state
        if (_alertingCall && _alertingCall.atcAdvancing) {
          call = _alertingCall;
          var connection = getCstaConnectionForRequest(call.atcCallInfo);
          connection.dID = _osmoData.onsFQN;
          call.atcCallInfo.setCstaConnection(connection);
        } else {
          cb && cb('There is no call to be answered');
          return;
        }
      }
      var position = call.atcAdvancing ? Targets.Desk : call.getPosition();
      // Check if there is also an active call on the same device
      var activeCall = Object.values(_atcRemoteCalls).find(function (remoteCall) {
        return remoteCall.callId !== callId && remoteCall.isAtPosition(position);
      });

      // There is an active call on the same device. Send Alternate Call request instead of Answer Call
      if (activeCall && !$rootScope.localUser.isOsBizCTIEnabled) {
        _that.alternate(call, activeCall, cb);
        return;
      }

      var data = {
        request: 'AnswerCall',
        callToBeAnswered: getCstaConnectionForRequest(call.atcCallInfo)
      };
      sendCstaRequest(data, cb);
    };

    this.answerCstaCall = function (callId, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }
      var data = {
        request: 'AnswerCall',
        callToBeAnswered: { cID: callId, dID: _osmoData.onsFQN }
      };
      sendCstaRequest(data, cb);
    };

    this.pickupCall = function (callId, device, cb) {
      cb = cb || function () {};
      var call = findAtcRemoteCall(callId);
      if (!call || !call.pickupNotification) {
        cb('There is no call to be picked up');
        return;
      }
      if (!getRegistrationData(cb)) {
        return;
      }
      // PBX Group Pickup Notification
      device = device || Targets.WebRTC;
      var data = {
        request: 'GroupPickupCall',
        newDestination: buildNewDestination(device)
      };
      sendCstaRequest(data, function (err) {
        if (err) {
          cb('res_PickUpCallFailed');
          // NGTC-5245: PU from alternative is not supported from OS4k side,
          // do not remove call so it can be answered in different position
          if (!$rootScope.localUser.isOS4K || ($rootScope.localUser.isOS4K && device !== Targets.Cell)) {
            removeAtcRemoteCall(callId, true);
          } else {
            call.clearAtcHandoverInProgress();
          }
          return;
        }
        cb();
      });
      LogSvc.debug('[CstaSvc]: Publish /atccall/pickUpInProgress');
      PubSubSvc.publish('/atccall/pickUpInProgress', [call]);
      call.setAtcHandoverInProgress();
    };

    this.clearTransferredCall = function (callID) {
      if (!_transferredCalls[callID]) {
        return;
      }
      $timeout.cancel(_transferredCalls[callID].timeout);
      delete _transferredCalls[callID];
    };

    this.consultationTransfer = function (activeCall, heldCall, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }

      var data = {
        request: 'TransferCall',
        heldCall: getCstaConnectionForRequest(heldCall.atcCallInfo),
        activeCall: getCstaConnectionForRequest(activeCall.atcCallInfo)
      };
      sendCstaRequest(data, function (err, rs) {
        if (err || !rs) {
          cb && cb(TransferCallFailedCauses.Unreachable.ui);
        } else {
          cb && cb(null);
        }
      });
    };

    this.deflect = function (callId, target, destination, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }

      var call = findWebRTCCall(callId) || UnifyPhoneCallControlSvc.getIncomingCall() || findAtcRemoteCall(callId);

      if (!call) {
        cb && cb('There is no call to be deflected');
        return;
      }

      if (!isTargetAllowed(call, target)) {
        cb && cb('The selected target is not available for this call.');
        return;
      }

      // If the call is advancing, it means that it's ringing on the Desk. Send Answer Call instead of Deflect Call
      if (call.atcAdvancing && target === Targets.Desk) {
        _that.answer(callId, cb);
        return;
      }

      var newDestination = buildNewDestination(target, destination);
      if (!newDestination) {
        cb && cb('Failed to create new destination.');
        return;
      }

      var data = {
        request: 'DeflectCall',
        callToBeDiverted: getCstaConnectionForRequest(call.atcCallInfo),
        newDestination: newDestination,
        autoAnswer: target === Targets.Desk || target === Targets.WebRTC
      };

      if ($rootScope.localUser.isOsBizCTIEnabled && target === Targets.Cell && isSelectedRouting(RoutingOptions.UnifyPhoneClient)) {
        data.callToBeDiverted.dID = _osmoData.osmoFQN;
      }

      sendCstaRequest(data, function (err) {
        if (err) {
          cb && cb('res_MoveCallFailed');
          return;
        }
        // OSBiz: deflect to alternative number has no binding.
        if (target !== Targets.VM && (!$rootScope.localUser.isOsBizCTIEnabled || target !== Targets.Cell)) {
          call.setAtcHandoverInProgress();
        }
      });
    };

    this.generateDigits = function (call, digits, cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'GenerateDigits',
          connectionToSendDigits: getCstaConnectionForRequest(call.atcCallInfo),
          charactersToSend: digits
        };
        sendCstaRequest(
          data,
          function (err) {
            cb && cb(err);
            if ($rootScope.localUser.isOsBizCTIEnabled && !err) {
              // OSBIZ does not support digitsGeneratedEvent. Use GDR instead.
              LogSvc.info('[CstaSvc]: Number of DTMF digits played: ', digits && digits.length);
              PubSubSvc.publish('/call/singleDigitSent', [call, digits]);
            }
          },
          ['GD.cTS']
        );
      }
    };

    this.getDoNotDisturb = function (cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'GetDoNotDisturb',
          device: getCstaDeviceId()
        };
        sendCstaRequest(data, cb);
      }
    };

    this.getForwarding = function (cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'GetForwarding',
          device: getCstaDeviceId()
        };
        sendCstaRequest(data, cb);
      }
    };

    this.getMessageWaiting = function (cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'GetMessageWaitingIndicator',
          device: getCstaDeviceId()
        };
        sendCstaRequest(data, cb);
      }
    };

    this.getTransferredCall = function (callID) {
      return _transferredCalls[callID];
    };

    this.pullRemoteCall = function (callId, cb) {
      var call = findAtcRemoteCall(callId);
      if (call) {
        if (call.pickupNotification) {
          _that.pickupCall(callId, null, cb);
        } else {
          handover(call, Targets[Targets.WebRTC.name], null, cb);
        }
      } else {
        LogSvc.warn('[CstaSvc]: pullRemoteCall invoked without a valid call');
        cb('No active remote call');
      }
    };

    this.pushLocalCall = function (callId, target, cb) {
      refreshData();
      var call;
      if (_activeCall && _activeCall.callId === callId) {
        call = _activeCall;
      } else if (_heldCall && _heldCall.callId === callId) {
        call = _heldCall;
      }
      if (call) {
        handover(call, Targets[target], null, cb);
      } else {
        LogSvc.warn('[CstaSvc]: pushLocalCall invoked without a valid call');
        cb('No active local call');
      }
    };

    this.endRemoteCall = function (callId, cb) {
      var call = findAtcRemoteCall(callId);
      if (call) {
        _that.hangupRemote(call, cb);
      } else {
        LogSvc.warn('[CstaSvc]: endRemoteCall invoked without a valid call');
        cb && cb('No active remote call');
      }
    };

    this.endCallOnRemoteDevices = function (cId) {
      if (getRegistrationData() && _osmoData.osmoDeviceList && cId) {
        var data = {
          request: 'ClearConnection',
          connectionToBeCleared: { cID: cId, dID: _osmoData.onsFQN }
        };
        sendCstaRequest(data);
      }
    };

    this.endCstaCall = function (cId) {
      if (getRegistrationData() && cId) {
        var data = {
          request: 'ClearConnection',
          connectionToBeCleared: { cID: cId, dID: _osmoData.onsFQN }
        };
        sendCstaRequest(data);
      }
    };

    this.hangupRemote = function (call, cb) {
      if (call.isRemote && getRegistrationData(cb)) {
        if (!getCstaConnectionForRequest(call.atcCallInfo).cID) {
          LogSvc.warn('[CstaSvc]: hangupRemote invoked without a valid call');
          cb && cb('No active remote call');
          return;
        }
        var data = {
          request: 'ClearConnection',
          connectionToBeCleared: getCstaConnectionForRequest(call.atcCallInfo)
        };
        sendCstaRequest(data, function (err) {
          if (err) {
            cb && cb(err);
          } else {
            if (!call.getCstaState().established && !call.atcCallInfo.getMissedReason()) {
              if (call.direction === CallDirection.INCOMING) {
                call.atcCallInfo.setMissedReason(MissedReasonTypes.DECLINED);
              } else {
                call.atcCallInfo.setMissedReason(MissedReasonTypes.CANCELLED);
              }
              removeAtcRemoteCall(call.atcCallInfo.getCstaCallId());
            }
            cb && cb();
          }
        });
      }
    };

    this.hold = function (call, cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'HoldCall',
          callToBeHeld: getCstaConnectionForRequest(call.atcCallInfo)
        };

        if ($rootScope.localUser.isOsBizCTIEnabled && call.isRemote && _osmoData.supportedFeatures && _osmoData.supportedFeatures.includes('zhOs')) {
          data.holdOptions = 'exclusiveHold';
        }

        call.setHoldInProgress();
        sendCstaRequest(data, function (err) {
          if (err) {
            // If for some reason the Hold request failed, the holdInProgress indication needs to be reset
            call.clearHoldInProgress();
            cb && cb('res_HoldCallFailed');
          } else {
            cb && cb(null);
          }
        });
      }
    };

    this.holdWithConsultationCall = function (call, cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'ConsultationCall',
          existingCall: getCstaConnectionForRequest(call.atcCallInfo)
        };
        call.setHoldInProgress();
        sendCstaRequest(data, function (err, rs) {
          if (err) {
            // If for some reason the ConsultationCall request failed, the holdInProgress indication needs to be reset
            call.clearHoldInProgress();
            cb && cb('res_HoldCallFailed');
          } else {
            rs && rs.iC && rs.iC.cID && call.atcCallInfo.setCstaReconnectId({ cID: rs.iC.cID, dID: _osmoData.onsFQN });
            cb && cb(null);
          }
        });
      }
    };

    this.reconnect = function (activeCall, heldCall, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }

      var data = {
        request: 'ReconnectCall',
        heldCall: getCstaConnectionForRequest(heldCall.atcCallInfo),
        activeCall: getCstaConnectionForRequest(activeCall.atcCallInfo)
      };
      sendCstaRequest(data, cb);
    };

    this.retrieve = function (call, cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'RetrieveCall',
          callToBeRetrieved: getCstaConnectionForRequest(call.atcCallInfo)
        };
        call.setRetrieveInProgress();
        sendCstaRequest(data, function (err) {
          if (err) {
            // If for some reason the Retrieve request failed, the RetrieveInProgress indication needs to be reset
            call.clearRetrieveInProgress();
            cb && cb('res_RetrieveCallFailed');
          } else {
            cb && cb(null);
          }
        });
      }
    };

    this.retrieveWithReconnectCall = function (call, cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'ReconnectCall',
          heldCall: getCstaConnectionForRequest(call.atcCallInfo),
          activeCall: call.atcCallInfo.getCstaReconnectId()
        };
        call.setRetrieveInProgress();
        sendCstaRequest(data, function (err) {
          if (err) {
            // If for some reason the Reconnect request failed, the RetrieveInProgress indication needs to be reset
            call.clearRetrieveInProgress();
            call.atcCallInfo.setCstaReconnectId({});
            cb && cb('res_RetrieveCallFailed');
          } else {
            cb && cb(null);
          }
        });
      }
    };

    this.setDoNotDisturb = function (doNotDisturbOn, cb) {
      if (getRegistrationData(cb)) {
        LogSvc.debug('[CstaSvc]: Set DND to ', doNotDisturbOn);
        var data = {
          request: 'SetDoNotDisturb',
          device: getCstaDeviceId(),
          doNotDisturbOn: doNotDisturbOn
        };
        sendCstaRequest(data, cb);
      }
    };

    this.setForwarding = function (data, cb) {
      if (getRegistrationData(cb)) {
        var msg = {
          request: 'SetForwarding',
          device: data.device || getCstaDeviceId(),
          activateForward: data.activateForward,
          forwardDN: data.forwardDN || '',
          forwardingType: data.forwardingType,
          ringCount: data.ringCount
        };

        checkSetForwardingLoop(function () {
          sendCstaRequest(msg, cb);
        });
      }
    };

    this.snapshotCall = function (conn, cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'SnapshotCall',
          snapshotObject: conn
        };
        sendCstaRequest(data, cb);
      }
    };

    this.snapshotDevice = function (cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'SnapshotDevice',
          snapshotObject: _osmoData.onsFQN,
          osmo: _osmoData.osmo
        };
        sendCstaRequest(data, cb);
      }
    };

    this.getLogicalDeviceInformation = function (cb) {
      cb = cb || function () {};
      if (getRegistrationData(cb)) {
        var data = {
          request: 'GetLogicalDeviceInformation',
          device: getCstaDeviceId()
        };
        sendCstaRequest(data, function (err, rs) {
          if (!err && rs && !rs.hasPhysicalDeviceInformation && !_onsUnregistered) {
            _onsUnregistered = true;
            publishCstaDeviceChanged();
          }
          cb(err, rs);
        });
      }
    };

    this.transfer = function (call, destination, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }

      var data = {};

      if (call.checkCstaState([CstaCallState.Ringing, CstaCallState.Parked])) {
        // Need to send a CSTA Deflect Call request instead of CSTA SST request
        data.request = 'DeflectCall';
        data.callToBeDiverted = getCstaConnectionForRequest(call.atcCallInfo);
        data.newDestination = destination;

        sendCstaRequest(data, cb);
      } else {
        data.request = 'SingleStepTransferCall';
        data.activeCall = getCstaConnectionForRequest(call.atcCallInfo);
        data.transferredTo = destination;

        sendCstaRequest(data, function (err, rs) {
          if (err || !rs) {
            if (err && err.indexOf('internalResourceBusy') !== -1) {
              // If the renegotiation, before the transfer, takes too long, OSV sends CSTA Error with
              // internalResourceBusy, but the transfer may succeed afterwards. So we need to continue
              // tracking this call
              storeTransferredCall(data.activeCall.cID, { transferredCall: { cID: DEFAULT_TRANSFER_CALLID } });
              cb && cb(null);
              return;
            }
            cb && cb(TransferCallFailedCauses.Unreachable.ui);
          } else {
            storeTransferredCall(data.activeCall.cID, rs);
            cb && cb(null);
          }
        });
      }
    };

    this.consultationCall = function (call, destination, cb) {
      if (!getRegistrationData(cb) || !destination) {
        return;
      }
      var data = {
        request: 'ConsultationCall',
        consultedDevice: destination.dialedDn,
        existingCall: getCstaConnectionForRequest(call.atcCallInfo)
      };

      createEarlyAtcRemoteCall(destination, call.getPosition());
      var newCall = _atcRemoteCalls[destination.dialedDn];
      sendCstaRequest(data, function (err, rs) {
        if (err || !rs) {
          cb && cb('res_MakeCallFailed');
          if (newCall) {
            if (err.endsWith('invalidCalledDeviceID')) {
              newCall.atcCallInfo.setMissedReason(MissedReasonTypes.REORDER_TONE);
            } else if (err.endsWith('requestIncompatibleWithCallingDevice') || err.endsWith('invalidDeviceID')) {
              removeAtcRemoteCall(destination.dialedDn, true);
              return;
            } else {
              newCall.atcCallInfo.setMissedReason(MissedReasonTypes.DEST_OUT_OF_ORDER);
            }
            newCall.atcCallInfo.setIgnoreCall(false);
            removeAtcRemoteCall(destination.dialedDn);
          }
        } else {
          if (!call.isRemote && ($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiVB)) {
            call.isOsBizFirstCall = true;
            // NGTC-18699: If the call was held via OSBiz hold, the PBX reuses that consultation connection
            // and no new ServiceInitiatedEvent is sent. Associate the connection with the second local call here.
            var secondCall = UnifyPhoneCallControlSvc.findOsBizSecondCall();
            if (
              $rootScope.localUser.isOsBizCTIEnabled &&
              rs &&
              rs.iC &&
              rs.iC.cID &&
              rs.iC.cID === call.atcCallInfo.getCstaReconnectId().cID &&
              secondCall &&
              !secondCall.atcCallInfo.getCstaCallId()
            ) {
              LogSvc.info('[CstaSvc]: Set the CSTA connection ID for the second call from ConsultationCall response: ', rs.iC);
              secondCall.atcCallInfo.setCstaConnection(rs.iC);
            }
          } else {
            // Update the new call with the consultationCall response in order to show it in the UI
            updateEarlyAtcRemoteCall(rs.iC, destination, call.getPosition());
          }
          cb && cb();
        }
      });
    };

    this.makeCall = function (target, destination, cb) {
      if (!getRegistrationData(cb) || !destination) {
        return;
      }
      var data = {
        request: 'MakeCall',
        calledDirectoryNumber: destination.dialedDn,
        callingDevice: buildNewDestination(target),
        autoOriginate: target === Targets.Desk || target === Targets.WebRTC ? 'doNotPrompt' : 'prompt'
      };

      createEarlyAtcRemoteCall(destination, target);
      var call = _atcRemoteCalls[destination.dialedDn];

      sendCstaRequest(data, function (err, rs) {
        if (err) {
          cb && cb('res_MakeCallFailed');
          if (err.endsWith('invalidCalledDeviceID')) {
            call.atcCallInfo.setMissedReason(MissedReasonTypes.REORDER_TONE);
          } else if (err.endsWith('requestIncompatibleWithCallingDevice') || err.endsWith('invalidDeviceID')) {
            removeAtcRemoteCall(destination.dialedDn, true);
            return;
          } else {
            call.atcCallInfo.setMissedReason(MissedReasonTypes.DEST_OUT_OF_ORDER);
          }
          call.atcCallInfo.setIgnoreCall(false);
          removeAtcRemoteCall(destination.dialedDn);
        } else {
          if (rs) {
            // Update the new call with the makeCall response in order to show it in the UI
            updateEarlyAtcRemoteCall(rs.caD, destination, target);
          } else {
            removeAtcRemoteCall(destination.dialedDn, true);
          }
          cb && cb();
        }
      });
    };

    this.ignoreCall = function (call, cb) {
      removeAtcRemoteCall(call.callId);
      cb && cb();
    };

    this.callBackCallRelated = function (call, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }
      var data = {
        request: 'CallBackCallRelated',
        callBackConnection: call.atcCallInfo.getCstaConnection()
      };
      sendCstaRequest(data, cb);
    };

    this.callBackNonCallRelated = function (target, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }
      var data = {
        request: 'CallBackNonCallRelated',
        orD: _osmoData.onsFQN,
        tD: target
      };
      sendCstaRequest(data, cb);
    };

    this.cancelCallBack = function (target, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }
      var data = {
        request: 'CancelCallBack',
        orD: _osmoData.onsFQN,
        td: target || undefined
      };
      sendCstaRequest(data, cb);
    };

    this.setMicrophoneMute = function (mute, cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'SetMicrophoneMute',
          device: getCstaDeviceId(),
          mute: mute
        };
        sendCstaRequest(data, cb);
      }
    };

    this.getMicrophoneMute = function (cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'GetMicrophoneMute',
          device: getCstaDeviceId()
        };
        sendCstaRequest(data, cb);
      }
    };

    this.callLogSnapShot = function (cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'CallLogSnapShot',
          device: getCstaDeviceId()
        };
        sendCstaRequest(data, cb);
      }
    };

    this.snapshot = function () {
      snapshotRemoteCalls();
    };

    this.getAgentState = function (device, cb) {
      if (getRegistrationData(cb)) {
        var data = {
          request: 'GetAgentState',
          device: device || getCstaDeviceId()
        };
        //when device is not empty, need to set data.group because it's separate request for each mulap member
        if ($rootScope.localUser.isOsBizCTIEnabled && device) {
          data.group = getCstaDeviceId();
        }
        sendCstaRequest(data, cb);
      }
    };

    this.getGlobalAgentState = function () {
      return getGlobalOS4KClassicHuntState() || getGlobalAgentStateReady();
    };

    this.isSetAgentStatePerGroupSupported = function () {
      return !!($rootScope.localUser && ($rootScope.localUser.isOSV || $rootScope.localUser.isOsBizCTIEnabled));
    };

    this.setAgentState = function (ready, group, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }

      ready = !!ready;

      // SEND ATC MESSAGE TO OS4K CLASSIC HUNT GROUP
      if ($rootScope.localUser.isOS4KClassicHuntGroup && ready !== getGlobalOS4KClassicHuntState()) {
        _userToUserHandler.sendAtcRequest({
          content: {
            type: AtcMessage.OS4K_CLASSIC_HUNT
          },
          destUserId: $rootScope.localUser.associatedTelephonyUserID
        });
      }

      // SEND CSTA MESSAGE TO REST HUNT GROUPS
      var agentStateReady = getGlobalAgentStateReady();
      if (group) {
        var agentState =
          _huntGroupList &&
          _huntGroupList.find(function (huntgroup) {
            return huntgroup.pilotDn === group;
          });
        agentStateReady = !!(agentState && agentState.available);
      }

      if ($rootScope.localUser.isOSVOrOS4KAgent && ready !== agentStateReady) {
        var data = {
          request: 'SetAgentState',
          device: getCstaDeviceId(),
          state: ready ? AgentState.Ready : AgentState.NotReady,
          localUser: $rootScope.localUser,
          group: group
        };
        sendCstaRequest(data, cb);
      }
    };

    this.setAgentStateOSBiz = function (ready, routing, cb) {
      if (!getRegistrationData(cb)) {
        return;
      }

      ready = !!ready;
      var device;
      if (routing === RoutingOptions.UnifyPhoneClient.name) {
        device = _osmoData.osmoFQN;
      } else if (routing === RoutingOptions.DeskPhone.name && _osmoData.osmoDeviceList && _osmoData.osmoDeviceList.length > 0) {
        device = _osmoData.osmoDeviceList[0];
      } else {
        device = getCstaDeviceId();
      }
      var data = {
        request: 'SetAgentState',
        device: device,
        state: ready ? AgentState.Ready : AgentState.NotReady,
        group: getCstaDeviceId(),
        localUser: $rootScope.localUser
      };

      sendCstaRequest(data, cb);
    };

    this.handleCallEvent = function (eventData) {
      if (!eventData) {
        return;
      }
      refreshData();

      if (!_snapshotPerformed) {
        LogSvc.debug('[CstaSvc]: Ignore any call event until snapshot is performed');
        return;
      }

      try {
        switch (eventData.name) {
          case 'CallInformationEvent':
            handleCallInformationEvent(eventData);
            break;
          case 'ConferencedEvent':
            handleConferencedEvent(eventData);
            break;
          case 'ConnectionClearedEvent':
            handleConnectionClearedEvent(eventData);
            break;
          case 'DeliveredEvent':
            handleDeliveredEvent(eventData);
            break;
          case 'DigitsGeneratedEvent':
            handleDigitsGeneratedEvent(eventData);
            break;
          case 'DivertedEvent':
            handleDivertedEvent(eventData);
            break;
          case 'EstablishedEvent':
            handleEstablishedEvent(eventData);
            break;
          case 'FailedEvent':
            handleFailedEvent(eventData);
            break;
          case 'HeldEvent':
            handleHeldEvent(eventData);
            break;
          case 'OfferedEvent':
            handleOfferedEvent(eventData);
            break;
          case 'QueuedEvent':
            handleQueuedEvent(eventData);
            break;
          case 'RetrievedEvent':
            handleRetrievedEvent(eventData);
            break;
          case 'ServiceCompletionFailureEvent':
            handleServiceCompletionFailureEvent(eventData);
            break;
          case 'ServiceInitiatedEvent':
            handleServiceInitiatedEvent(eventData);
            break;
          case 'TransferredEvent':
            handleTransferredEvent(eventData);
            break;
          default:
            LogSvc.debug('[CstaSvc]: Ignore CSTA Call Event (' + eventData.name + ')');
            break;
        }
      } catch (error) {
        LogSvc.error(error);
      }
    };

    this.handleDeviceEvent = function (eventData) {
      if (!eventData) {
        return;
      }

      try {
        switch (eventData.name) {
          case 'BackInServiceEvent':
            handleBackInServiceEvent();
            break;
          case 'OutOfServiceEvent':
            handleOutOfServiceEvent();
            break;
          default:
            LogSvc.debug('[CstaSvc]: Ignore CSTA Device Event (' + eventData.name + ')');
            break;
        }
      } catch (error) {
        LogSvc.error(error);
      }
    };

    this.handleCti = function (eventData) {
      if (!eventData) {
        return;
      }

      LogSvc.debug('[CstaSvc]: Will publish uaCsta: ', eventData);
      PubSubSvc.publish('/cti/cmd', [eventData]);
    };

    this.handlePhysicalDeviceEvent = function (eventData) {
      if (!eventData) {
        return;
      }

      var hasVoicemail = eventData.messageWaitingOn;

      $rootScope.localUser.msgWaitingIndicator = hasVoicemail;
      LogSvc.debug('[CstaSvc]: Publish /csta/msgWaitingIndicatorUpdated');
      PubSubSvc.publish('/csta/msgWaitingIndicatorUpdated', [hasVoicemail]);
    };

    this.handleLogicalDeviceEvent = function (eventData) {
      if (!eventData) {
        return;
      }

      try {
        switch (eventData.name) {
          case 'ForwardingEvent':
            handleForwardingEvent(eventData);
            break;
          case 'DoNotDisturbEvent':
            handleDoNotDisturbEvent(eventData);
            break;
          case 'AgentReadyEvent':
          case 'AgentNotReadyEvent':
          case 'AgentBusyEvent':
          case 'AgentLoggedOffEvent':
          case 'AgentLoggedOnEvent':
          case 'AgentWorkingAfterCallEvent':
            handleAgentEvent(eventData);
            break;
          default:
            LogSvc.debug('[CstaSvc]: Ignore CSTA Logical Device Event (' + eventData.name + ')');
            break;
        }
      } catch (error) {
        LogSvc.error(error);
      }
    };

    this.isPrimaryClient = function () {
      return _primaryClient;
    };

    this.getDisplayInfo = getDisplayInfo;

    this.getCallDevices = getCallDevices;

    this.getPushDevices = getPushDevices;

    this.getAnswerDevices = getAnswerDevices;

    this.updateStaticOnd = function (status, cb) {
      if (getRegistrationData(cb)) {
        status = !!status;
        LogSvc.debug('[CstaSvc]: Request to update static OND status to ' + status + ' . _savedStaticOnd = ' + _savedStaticOnd);
        var data = {
          staticOndActive: status
        };

        if (status) {
          data.staticOndDN = Utils.cleanPhoneNumber($rootScope.localUser.reroutingPhoneNumber);
          if (!data.staticOndDN) {
            cb && cb('Cannot activate static OND without the static OND DN');
            return;
          }
        }

        var hasSavedStaticOnd = !!_savedStaticOnd;
        if (status !== hasSavedStaticOnd || (status && data.staticOndDN !== _savedStaticOnd)) {
          var msg = {
            request: 'SetForwarding',
            device: getCstaDeviceId(),
            staticOndActive: data.staticOndActive,
            staticOndDN: data.staticOndDN
          };

          sendCstaRequest(msg, function (err) {
            if (err) {
              cb && cb(err);
            } else {
              _savedStaticOnd = data.staticOndDN || '';
              cb && cb(null);
            }
          });
        } else {
          cb && cb(null);
        }
      }
    };

    this.updateRoutingOption = function (selectedOption, oldOption, cb) {
      if ($rootScope.localUser.isMiV5000) {
        return;
      }
      $rootScope.localUser.isOsBizCTIEnabled ? updateRoutingOptionOSBiz(selectedOption, oldOption, cb) : updateRoutingOptionATC(selectedOption, oldOption, cb);
    };

    this.setForwardingType = function (cfType, status, number, cb) {
      number = number || $rootScope.localUser.cfData[cfType].cfwNumber;
      if (cfType === CallForwardingTypes.NoAnswer.name && $rootScope.localUser.isOsBizCTIEnabled && number) {
        number = 'N<' + Utils.cleanPhoneNumber(number) + '>PreferredDevice';
      } else {
        number = Utils.cleanPhoneNumber(number);
      }
      var data = {
        activateForward: number ? !!status : false,
        forwardDN: number,
        forwardingType: convertToCstaFWType(cfType)
      };
      _that.setForwarding(data, cb);
    };

    this.setForwardingImmediate = function (status, number, cb) {
      number = number || $rootScope.localUser.cfData.Immediate.cfwNumber;
      var data = {
        activateForward: !!status,
        forwardDN: Utils.cleanPhoneNumber(number),
        forwardingType: 'forwardImmediate'
      };
      _that.setForwarding(data, cb);
    };

    this.setForwardingToVM = function (status, cb) {
      if (_that.getForwardingVMStatus() !== status) {
        var data = {
          activateForward: !!status,
          forwardDN: Utils.cleanPhoneNumber(_osmoData.vm),
          forwardingType: 'forwardImmediate'
        };
        _that.setForwarding(data, cb);
      } else {
        cb && cb();
      }
    };

    this.setForwardingNoAnswer = function (number, status, cb) {
      if (status && !number) {
        cb && cb('Failed to set call forward no answer');
      } else {
        // OSBiz: Set alternative number
        if ($rootScope.localUser.isOsBizCTIEnabled && number) {
          number = 'N<' + Utils.cleanPhoneNumber(number) + '>PreferredDevice';
        } else {
          number = Utils.cleanPhoneNumber(number);
        }

        var data = {
          activateForward: !!status,
          forwardDN: number,
          forwardingType: 'forwardNoAns'
        };
        _that.setForwarding(data, cb);
      }
    };

    this.setForwardingToVMNoAnswer = function (status, cb) {
      if (_that.getForwardingVMNoAnswerStatus() !== status) {
        var data = {
          activateForward: !!status,
          forwardDN: Utils.cleanPhoneNumber(_osmoData.vm),
          forwardingType: 'forwardNoAns'
        };
        _that.setForwarding(data, cb);
      } else {
        cb && cb();
      }
    };

    this.setForwardingBusy = function (number, status, cb) {
      number = number || $rootScope.localUser.cfData.Busy.cfwNumber;
      var data = {
        activateForward: !!status,
        forwardDN: Utils.cleanPhoneNumber(number),
        forwardingType: 'forwardBusy'
      };
      _that.setForwarding(data, cb);
    };

    this.setForwardingToVMBusy = function (status, cb) {
      if (_that.getForwardingVMBusyStatus() !== status) {
        var data = {
          activateForward: !!status,
          forwardDN: Utils.cleanPhoneNumber(_osmoData.vm),
          forwardingType: 'forwardBusy'
        };
        _that.setForwarding(data, cb);
      } else {
        cb && cb();
      }
    };

    // OSBiz: Set routing option = alternative number
    this.setRoutingOptionReroutingNumber = function (number, cb) {
      if (getRegistrationData(cb)) {
        number = number || '';
        var status = !!number;
        number = status ? 'N<' + Utils.cleanPhoneNumber(number) + '>PreferredDevice' : '';
        var device = _osmoData.onsFQN;
        if (_osmoData.osmoDeviceList && _osmoData.osmoDeviceList.length > 1) {
          device = 'N<' + _osmoData.onsFQN + '/' + _osmoData.osmoDeviceList[1] + '>';
        }

        var data = {
          device: device,
          activateForward: status,
          forwardDN: number,
          forwardingType: 'preferredDevice'
        };
        _that.setForwarding(data, cb);
      }
    };

    this.getForwardingData = function () {
      return _osmoData.forwardList;
    };

    this.getForwardingImmediate = function () {
      var forwardImmediate = _osmoData.forwardList.find(function (element) {
        return isForwardingImmediate(element.forwardingType) && !isVMNumber(element.forwardDN);
      });

      return {
        status: !!(forwardImmediate && forwardImmediate.forwardStatus),
        forwardDN: forwardImmediate && forwardImmediate.forwardDN
      };
    };

    this.disableCallForwardingImmediate = function (cb) {
      if (
        _that.getForwardingImmediate().status ||
        (($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiV5000) && _that.getForwardingVMStatus())
      ) {
        _that.setForwardingImmediate(false, null, function () {
          cb && cb();
        });
      } else {
        cb && cb();
      }
    };

    this.getForwardingVMStatus = function () {
      return _osmoData.forwardList.some(function (element) {
        return isForwardingImmediate(element.forwardingType) && isVMNumber(element.forwardDN) && element.forwardStatus;
      });
    };

    this.getForwardingVMBusyStatus = function () {
      return _osmoData.forwardList.some(function (element) {
        return isForwardingBusy(element.forwardingType) && isVMNumber(element.forwardDN) && element.forwardStatus;
      });
    };

    this.getForwardingVMNoAnswerStatus = function () {
      return _osmoData.forwardList.some(function (element) {
        return isForwardingNoAnswer(element.forwardingType) && isVMNumber(element.forwardDN) && element.forwardStatus;
      });
    };

    this.getForwardingPreferredDevice = function (device, cb) {
      var data = {
        request: 'GetForwarding',
        device: device
      };
      sendCstaRequest(data, cb);
    };

    this.getForwardingNoAnswer = function () {
      var forwardNoAnswer = _osmoData.forwardList.find(function (element) {
        return isForwardingNoAnswer(element.forwardingType);
      });

      return {
        status: !!(forwardNoAnswer && forwardNoAnswer.forwardStatus),
        forwardDN: forwardNoAnswer && forwardNoAnswer.forwardDN
      };
    };

    this.disableCallForwardingNoAnswer = function (cb) {
      if (_that.getForwardingNoAnswer().status || ($rootScope.localUser.isMiV5000 && _that.getForwardingVMNoAnswerStatus())) {
        _that.setForwardingNoAnswer(null, false, function () {
          cb && cb();
        });
      } else {
        cb && cb();
      }
    };

    this.getForwardingBusy = function () {
      var forwardBusy = _osmoData.forwardList.find(function (element) {
        return isForwardingBusy(element.forwardingType);
      });

      return {
        status: !!(forwardBusy && forwardBusy.forwardStatus),
        forwardDN: forwardBusy && forwardBusy.forwardDN
      };
    };

    this.disableCallForwardingBusy = function (cb) {
      if (_that.getForwardingBusy().status || ($rootScope.localUser.isMiV5000 && _that.getForwardingVMBusyStatus())) {
        _that.setForwardingBusy(null, false, function () {
          cb && cb();
        });
      } else {
        cb && cb();
      }
    };

    this.setVoicemail = function (status, duration, cb) {
      if (getRegistrationData(cb)) {
        duration = duration || _savedVMRingDuration;
        if ($rootScope.localUser.pbxCallForwardToVoiceMailSupported && $rootScope.localUser.voicemailPBXEnabled) {
          LogSvc.debug('[CstaSvc]: Set voice mail duration to: ', duration);
          if (status !== $rootScope.localUser.voicemailActive || (status && _savedVMRingDuration !== duration)) {
            // If the status is not true, then the duration cannot be modified
            var msg = {
              request: 'SetForwarding',
              device: getCstaDeviceId(),
              voicemailActive: status,
              voicemailRingDuration: status ? duration : undefined
            };
            checkSetForwardingLoop(function () {
              sendCstaRequest(msg, function (err) {
                if (err) {
                  cb && cb(err);
                } else {
                  _savedVMRingDuration = duration;
                  cb && cb(null);
                }
              });
            });
          } else {
            cb && cb(null);
          }
        } else {
          cb && cb('Cannot configure VM on PBX');
        }
      }
    };

    this.disableAllCallForwarding = function () {
      _that.disableCallForwardingImmediate(function () {
        _that.disableCallForwardingNoAnswer(function () {
          _that.disableCallForwardingBusy();
        });
      });
    };

    this.setRoutingTimers = function (timers, cb) {
      if (getRegistrationData(cb)) {
        if ($rootScope.localUser.ringDurationEnabled) {
          LogSvc.debug('[CstaSvc]: Set routing timers to: ', timers);
          if (
            _savedMainRingDuration !== timers.mainRingDuration ||
            _savedCellRingDuration !== timers.cellRingDuration ||
            _savedClientRingDuration !== timers.clientRingDuration ||
            (!$rootScope.localUser.routeToCell && !!$rootScope.localUser.alternativeNumber) ||
            ($rootScope.localUser.routeToCell && !$rootScope.localUser.alternativeNumber)
          ) {
            var msg = {
              request: 'SetForwarding',
              device: getCstaDeviceId(),
              mainRingDuration: timers.mainRingDuration || undefined,
              clientRingDuration: timers.clientRingDuration || undefined,
              cellRingDuration: timers.cellRingDuration || undefined,
              routeToCell: !!$rootScope.localUser.alternativeNumber
            };
            checkSetForwardingLoop(function () {
              sendCstaRequest(msg, function (err) {
                if (err) {
                  cb && cb(err);
                } else {
                  _savedMainRingDuration = timers.mainRingDuration || _savedMainRingDuration;
                  _savedClientRingDuration = timers.clientRingDuration || _savedClientRingDuration;
                  _savedCellRingDuration = timers.cellRingDuration || _savedCellRingDuration;
                  cb && cb(null);
                }
              });
            });
          } else {
            cb && cb(null);
          }
        } else {
          LogSvc.debug('[CstaSvc]: Cannot set routing timers');
          cb && cb('Cannot configure routing timers on PBX');
        }
      }
    };

    this.startCall = function (existingCallOnTarget, target, destination, cb) {
      if (getRegistrationData(cb)) {
        if (existingCallOnTarget) {
          // For OSBiz, if the first remote call is already on hold, special treatment is necessary
          if ($rootScope.localUser.isOsBizCTIEnabled && existingCallOnTarget.isHolding() && existingCallOnTarget.isRemote) {
            if (_osmoData.supportedFeatures && _osmoData.supportedFeatures.includes('zhOs')) {
              LogSvc.debug('[CstaSvc]: OSBiz supports exlussive hold for remote call. Send MakeCall');
              _that.makeCall(target, destination, cb);
            } else {
              // OSBiz is not able to process consultationCall if the call is already on hold.
              // This should be solved with OSBiz V2R7.
              _that.retrieve(existingCallOnTarget, function (err) {
                if (err) {
                  cb && cb('res_MakeCallFailed');
                } else {
                  _that.consultationCall(existingCallOnTarget, destination, cb);
                }
              });
            }
          } else {
            _that.consultationCall(existingCallOnTarget, destination, cb);
          }
        } else {
          _that.makeCall(target, destination, cb);
        }
      }
    };

    /**
     * Returns the list of hunt groups that the user belongs to
     * @returns {Array} An array of elements: {name: HuntGroupName, pilotDn: Pilot DN,
     * available: The availability of the agent in the Hunt Group}
     */
    this.getHuntGroupList = function () {
      return _huntGroupList;
    };

    this.getUIAgentState = function () {
      if (!$rootScope.localUser || !($rootScope.localUser.isOSV || $rootScope.localUser.isOsBizCTIEnabled)) {
        return null;
      }
      if (_that.getGlobalAgentState()) {
        return AgentUIState.AVAILABLE;
      }
      var availability =
        _huntGroupList &&
        _huntGroupList.some(function (huntGroup) {
          return huntGroup.available;
        });
      return availability ? AgentUIState.PARTIAL : AgentUIState.UNAVAILABLE;
    };

    return this;
  }

  // Exports
  unifyPhone.CstaSvcImpl = CstaSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var CallState = unifyPhone.Enums.CallState;
  var CstaCallState = unifyPhone.Enums.CstaCallState;
  var Constants = unifyPhone.Constants;
  var Enums = unifyPhone.Enums;
  var ParticipantAction = unifyPhone.Enums.ParticipantAction;
  var Targets = unifyPhone.Enums.Targets;
  var TransferCallFailedCauses = unifyPhone.Enums.TransferCallFailedCauses;
  var Utils = unifyPhone.Utils;
  var UserToUserHandler = unifyPhone.UserToUserHandlerSingleton;

  ///////////////////////////////////////////////////////////////////////////////////////
  // CallControlSvc Implementation
  // Service that controls and manages WebRTC and CSTA calls
  ///////////////////////////////////////////////////////////////////////////////////////

  // eslint-disable-next-line max-params, max-lines-per-function
  function CallControlSvcImpl($rootScope, $timeout, LogSvc, PubSubSvc, UnifyPhoneCallControlSvc, ConversationSvc, CstaSvc, NotificationSvc, UserProfileSvc) { // NOSONAR
    LogSvc.debug('New Service: CallControlSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var DTMF_PAUSE = 2000; // 2 seconds

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _atcRemoteCalls = [];
    var _that = this;
    var _userToUserHandler = UserToUserHandler.getInstance();
    var _defaultCallDevice;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function addAtcRemoteCallToList(call) {
      for (var idx = 0; idx < _atcRemoteCalls.length; idx++) {
        if (call.sameAs(_atcRemoteCalls[idx])) {
          LogSvc.debug('[CallControlSvc]: Updating call with callId = ' + call.callId);
          _atcRemoteCalls[idx] = call;
          return;
        }
      }
      _atcRemoteCalls.push(call);
    }

    function sendDtmf(call) {
      var digits = call.dtmfDigits;
      if (digits) {
        delete call.dtmfDigits;

        // Count number of commas before the string
        var numCommas = digits.match(/^,*/)[0].length;
        var timerVal = numCommas * DTMF_PAUSE + 1000;
        if (numCommas) {
          digits = digits.substr(numCommas);
        }

        call.sendDtmfTimer = $timeout(function () {
          call.sendDtmfTimer = null;
          // If call is still present, send the digits
          if (call.isPresent()) {
            LogSvc.debug('[CallControlSvc]: Send delayed DTMF digits ' + digits + ' for call ' + call.callId);
            _that.sendDigits(call.callId, digits);
          }
        }, timerVal);
      }
    }

    function removeAtcRemoteCallFromList(call) {
      // Remove it from the call list
      _atcRemoteCalls.some(function (remoteCall, idx) {
        if (call.sameAs(remoteCall)) {
          _atcRemoteCalls.splice(idx, 1);
          return true;
        }
        return false;
      });
    }

    function findAtcRemoteCall(callId) {
      return (
        _atcRemoteCalls.find(function (call) {
          return call.callId === callId;
        }) || null
      );
    }

    function findAtcRemoteCallOnTarget(target) {
      return (
        _atcRemoteCalls.find(function (call) {
          return call.isAtPosition(target);
        }) || null
      );
    }

    function findActiveAtcRemoteCall() {
      var activeRemoteConference = null;
      for (var idx = 0; idx < _atcRemoteCalls.length; idx++) {
        if (_atcRemoteCalls[idx].state === CallState.ActiveRemote) {
          // Found an active call. Return it.
          return _atcRemoteCalls[idx];
        }
        if (_atcRemoteCalls[idx].checkCstaState(CstaCallState.Conference)) {
          // Keep the remote conference as backup, but continue looking
          activeRemoteConference = _atcRemoteCalls[idx];
        }
      }
      return activeRemoteConference;
    }

    function findRingingAtcRemoteCall() {
      return (
        _atcRemoteCalls.find(function (call) {
          return call.state === CallState.Ringing;
        }) || null
      );
    }

    function getActivePhoneCall() {
      var activeCall = _that.getActiveCall();
      if (activeCall && activeCall.isTelephonyCall) {
        return activeCall;
      }
      return null;
    }

    function publishCallState(call) {
      LogSvc.debug('[CallControlSvc]: Publish /call/state event. callId = ' + call.callId + ', state = ' + call.state.name);
      PubSubSvc.publish('/call/state', [call]);
    }

    function terminateCall(call) {
      call.terminate();
    }

    function findConversationByCallId(callId) {
      var conversation = ConversationSvc.getConversationByRtcSession(callId);
      if (conversation) {
        return conversation;
      }

      for (var idx = 0; idx < _atcRemoteCalls.length; idx++) {
        if (_atcRemoteCalls[idx].callId === callId) {
          return ConversationSvc.getConversationFromCache(_atcRemoteCalls[idx].convId);
        }
      }
      return null;
    }

    function getAtcHandoverInProgressCall() {
      for (var idx = 0; idx < _atcRemoteCalls.length; idx++) {
        if (_atcRemoteCalls[idx].isHandoverInProgress) {
          return _atcRemoteCalls[idx];
        }
      }
      return null;
    }

    function publishConversationUpdate(conversation) {
      if (conversation) {
        LogSvc.debug('[CallControlSvc]: Publish /conversation/update event. convId = ', conversation.convId);
        PubSubSvc.publish('/conversation/update', [conversation]);
      }
    }

    function setStateFromCstaState(call, conversation) {
      switch (call.getCstaState()) {
        case CstaCallState.Initiated:
        case CstaCallState.Offered:
        case CstaCallState.ExtendedRinging:
          call.setState(CallState.Started);
          break;
        case CstaCallState.Delivered:
          call.setState(CallState.Delivered);
          break;
        case CstaCallState.Busy:
          call.setState(CallState.Busy);
          break;
        case CstaCallState.Ringing:
          if (!call.checkState(CallState.Ringing)) {
            call.setState(CallState.Ringing);
            // Show the notification for this remote ATC call
            NotificationSvc &&
              NotificationSvc.show({
                type: unifyPhone.Enums.NotificationType.INCOMING_VOICE_CALL,
                user: call.peerUser,
                extras: {
                  conversation: conversation,
                  call: call
                }
              });
          }
          break;
        case CstaCallState.Idle:
        case CstaCallState.TransferFailed:
        case CstaCallState.Failed:
          call.setState(CallState.Idle);
          break;
        case CstaCallState.Terminated:
          call.setState(CallState.Terminated);
          break;
        default:
          call.setState(CallState.ActiveRemote);
          break;
      }
    }

    function handleAtcCallInfo(call) {
      if (!call) {
        LogSvc.warn('[CallControlSvc]: Invalid call object in /atccall/info event');
        return;
      }
      if (!call.isAtcRemote) {
        publishCallState(call);
        return;
      }
      // If the call.isAtcRemote (which means that it's on the desk or another OND device) then we publish the
      // /call/state event after mapping the CSTA state to a unifyPhone call state.
      var conversation = ConversationSvc.getConversationFromCache(call.convId);
      if (!conversation || !conversation.isTelephony) {
        LogSvc.warn('[CallControlSvc]: Call in /atccall/info event is not for telephony conversation');
        return;
      }

      if (!call.checkCstaState([CstaCallState.Idle, CstaCallState.Terminated])) {
        addAtcRemoteCallToList(call);
        if (
          !conversation.call ||
          (conversation.call.isHandoverInProgress && !conversation.call.sameAs(call)) ||
          (conversation.call.isAtcRemote && (conversation.call.isHolding() || conversation.call.isHoldInProgress()))
        ) {
          if (conversation.call && conversation.call.isHandoverInProgress) {
            var oldCall = conversation.call;
            oldCall.clearAtcHandoverInProgress();
            call.establishedTime = oldCall.establishedTime;
            if (oldCall.isAtcRemote) {
              removeAtcRemoteCallFromList(oldCall);
            } else {
              UnifyPhoneCallControlSvc.removeCallFromList(oldCall);
            }
            // We're terminating a local call, so set a terminate reason
            oldCall.terminateReason = Enums.CallClientTerminatedReason.USER_ENDED;
            terminateCall(oldCall);
            LogSvc.debug('[CallControlSvc]: Publish /call/ended event.');
            PubSubSvc.publish('/call/ended', [oldCall, false]);
          }
          conversation.call = call;
        }
      }

      setStateFromCstaState(call, conversation);

      if (call.state !== CallState.Idle && call.state !== CallState.Terminated && call.state !== CallState.Started) {
        publishConversationUpdate(conversation);
        publishCallState(call);
      } else if (call.state === CallState.Idle || call.state === CallState.Terminated) {
        terminateCall(call);
        removeAtcRemoteCallFromList(call);

        LogSvc.debug('[CallControlSvc]: Publish /call/ended event');
        PubSubSvc.publish('/call/ended', [call, false]);

        if (!call.isHandoverInProgress) {
          if (conversation.call && conversation.call.sameAs(call)) {
            conversation.call = findActiveAtcRemoteCall();
          }
        }
        publishConversationUpdate(conversation);
      }
    }

    function handleAtcCallReplace(call) {
      var oldCall = getAtcHandoverInProgressCall();
      if (oldCall) {
        if (oldCall.atcCallInfo.transferCb) {
          oldCall.atcCallInfo.transferCb(null, null, call);
          delete oldCall.atcCallInfo.transferCb;
        }
        var conversation = ConversationSvc.getConversationFromCache(call.convId);
        call.establishedTime = oldCall.establishedTime;
        terminateCall(oldCall);
        removeAtcRemoteCallFromList(oldCall);
        if (conversation && conversation.call) {
          conversation.call = call;
          publishConversationUpdate(conversation);
        }
      }
    }

    function handleAtcHangingCall(callId) {
      ConversationSvc.getTelephonyConversation(function (err, conversation) {
        if (!err && conversation && conversation.call && conversation.call.atcCallInfo.getCstaCallId() === callId) {
          conversation.call = null;
          publishConversationUpdate(conversation);
        }
      });
    }

    function isLocalCall(callId) {
      var localCall = UnifyPhoneCallControlSvc.getActiveCall();
      return !!localCall && callId === localCall.callId;
    }

    function getOsBizConsultationCalls() {
      if (!$rootScope.localUser.isOsBizCTIEnabled && !$rootScope.localUser.isMiVB) {
        return null;
      }
      var firstCall = UnifyPhoneCallControlSvc.findOsBizFirstCall();
      if (!firstCall) {
        return null;
      }
      var secondCall = UnifyPhoneCallControlSvc.findOsBizSecondCall();
      if (!secondCall) {
        return null;
      }
      var firstCallHolding = firstCall.checkCstaState(Enums.CstaCallState.Holding);
      return {
        activeCall: firstCallHolding ? secondCall : firstCall,
        heldCall: firstCallHolding ? firstCall : secondCall
      };
    }

    function hasTwoCallsWithOneRinging(calls) {
      return (
        (calls.activeCall && calls.activeCall.checkCstaState(Enums.CstaCallState.Ringing)) ||
        (calls.heldCall && calls.heldCall.checkCstaState(Enums.CstaCallState.Ringing))
      );
    }

    function sendTransferRequest(activeCall, destination, cb) {
      var otherCall;
      if (typeof destination === 'object') {
        otherCall = destination;
      }
      var data = {
        content: {
          type: Enums.StcMessage.TRANSFER,
          phoneNumber: $rootScope.localUser.callerId,
          transferRequest: {
            sessionId: activeCall.callId,
            target: {
              dn: otherCall ? otherCall.peerUser.phoneNumber : destination,
              sessionId: otherCall ? otherCall.callId : undefined
            }
          }
        },
        destUserId: $rootScope.localUser.associatedTelephonyUserID
      };
      _userToUserHandler.sendStcRequest(data, function (err, resp) {
        if (err || (resp && resp.error)) {
          var transferFailedCause = TransferCallFailedCauses.Unreachable.name;
          if (resp && TransferCallFailedCauses[resp.error]) {
            transferFailedCause = resp.error;
          }
          cb && cb(TransferCallFailedCauses[transferFailedCause].ui);
        } else {
          if (otherCall && otherCall.isPresent() && !otherCall.state.established) {
            _that.endCall(otherCall.callId);
          }
          cb && cb();
        }
      });
    }

    function canStartVideoParticipant(participant) {
      if (!participant.toggleVideoInProgress && participant.isMeetingPointInvitee) {
        return participant.mediaType && !participant.mediaType.video;
      }
      return false;
    }

    function canStopVideoParticipant(participant) {
      if (!participant.toggleVideoInProgress && participant.isMeetingPointInvitee) {
        return participant.mediaType && participant.mediaType.video;
      }
      return false;
    }

    function checkAndToggleVideoParticipant(call, participant, cb) {
      LogSvc.buttonPressed('Toggle video for participant with userId: ', participant.userId);
      if (!participant.isMeetingPointInvitee) {
        LogSvc.info('[CallControlSvc]: Cannot toggle video participant for ', participant.userId);
      } else {
        participant.toggleVideoInProgress = true;
        // To simplify code, because conversation is used only here, he just retrieve it from cache instead of binding it
        var conv = ConversationSvc.getConversationFromCache(call.convId);
        participant.setActions(conv, $rootScope.localUser);
        _that.toggleVideoParticipant(call.callId, participant, function (err) {
          participant.toggleVideoInProgress = false;
          participant.setActions(conv, $rootScope.localUser);
          if (err) {
            LogSvc.error('[CallControlSvc]: Cannot toggle video participant. ', err);
            cb(err);
          }
        });
      }
    }

    function canMuteParticipant(participant) {
      return !participant.muted && (participant.userId === $rootScope.localUser.userId || participant.actions.includes(ParticipantAction.Mute));
    }

    function canUnmuteParticipant(participant) {
      return participant.muted && (participant.userId === $rootScope.localUser.userId || participant.actions.includes(ParticipantAction.Unmute));
    }

    function mergeCalls(callId1, callId2, cb) {
      UnifyPhoneCallControlSvc.mergeCalls(callId1, callId2)
        .then(function () {
          cb();
        })
        .catch(function (err) {
          LogSvc.warn('[CallControlSvc]: Merge call failed');
          cb(err);
        });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/atccall/info', function (call) {
      LogSvc.debug('[CallControlSvc]: Received /atccall/info event');
      handleAtcCallInfo(call);
    });

    PubSubSvc.subscribe('/atccall/replace', function (call) {
      LogSvc.debug('[CallControlSvc]: Received /atccall/replace event');
      handleAtcCallReplace(call);
    });

    PubSubSvc.subscribe('/call/ended', function (call) {
      LogSvc.debug('[CallControlSvc]: Received /call/ended event');
      // Cancel the DTMF timer if its running
      if (call.sendDtmfTimer) {
        $timeout.cancel(call.sendDtmfTimer);
        call.sendDtmfTimer = null;
      }
      delete call.dtmfDigits;

      if (call.isAtcRemote) {
        call.clearAtcHandoverInProgress();
        removeAtcRemoteCallFromList(call);
      } else {
        var conversation = ConversationSvc.getConversationFromCache(call.convId);
        if (conversation && conversation.isTelephony && !conversation.call) {
          conversation.call = findActiveAtcRemoteCall() || findRingingAtcRemoteCall();
          if (conversation.call) {
            publishConversationUpdate(conversation);
          }
        }
        if (!call.isRemote && !UnifyPhoneCallControlSvc.getActiveCall()) {
          // Cancel the auto snooze if applicable
          UserProfileSvc.cancelAutoSnooze();
        }
      }
    });

    PubSubSvc.subscribe('/atccall/hangingcall', function (callid) {
      LogSvc.debug('[CallControlSvc]: Received /atccall/hangingcall event');
      handleAtcHangingCall(callid);
    });

    PubSubSvc.subscribe('/call/state', function (call) {
      LogSvc.debug('[CallControlSvc]: Received /call/state event');
      if (call && call.state.established) {
        if (call.dtmfDigits) {
          sendDtmf(call);
        }
      }
    });

    PubSubSvc.subscribe('/screenshare/started', function () {
      LogSvc.info('[CallControlSvc]: Received /screenshare/started event. Screenshare was started. Start Auto-Snooze.');
      UserProfileSvc.startAutoSnooze();
    });

    PubSubSvc.subscribe('/screenshare/ended', function () {
      LogSvc.info('[CallControlSvc]: Received /screenshare/ended event. Screenshare was ended. Resume from Auto-Snooze.');
      UserProfileSvc.cancelAutoSnooze();
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    /**
     * Used by SDK to initialize active sessions.
     * Returns a promise that is fullfilled when all sessions have been processed
     * and the different calls arrays are populated.
     */
    this.initActiveSessionsForSdk = UnifyPhoneCallControlSvc.initActiveSessionsForSdk;

    /**
         * Set indication of whether incoming remote video should be disabled by default for
         * new incoming and outgoing UnifyPhone calls.
    .    *
         * @param {boolean} true indicates that incoming video is disabled by default.
         */
    this.setDisableRemoteVideoByDefault = UnifyPhoneCallControlSvc.setDisableRemoteVideoByDefault;

    /**
         * Get indication of whether incoming remote video is currently disabled by default.
    .    *
         * @returns {boolean} true indicates that incoming video is disabled by default.
         */
    this.getDisableRemoteVideoByDefault = UnifyPhoneCallControlSvc.getDisableRemoteVideoByDefault;

    /**
         * Set indication of whether Client Diagnostics are enabled/disabled
    .    *
         * @param {boolean} true=disabled; false=enabled
         */
    this.setClientDiagnosticsDisabled = UnifyPhoneCallControlSvc.setClientDiagnosticsDisabled;

    /**
     * Get all of the calls.
     *
     * @returns {Array} An array of Call object.
     */
    this.getCalls = UnifyPhoneCallControlSvc.getCalls;

    /**
     * Get all of the phone calls.
     *
     * @returns {Array} An array of Call object
     */
    this.getPhoneCalls = UnifyPhoneCallControlSvc.getPhoneCalls;

    /**
     * Get the active local WebRTC call.
     *
     * @returns {LocalCall} The LocalCall object.
     */
    this.getActiveCall = UnifyPhoneCallControlSvc.getActiveCall;

    /**
     * Checks if there is an active local WebRTC call being established or
     * in the middle of a media renegotiation.
     *
     * @param {String} callId The ID of existing call or conference (optional)
     * @returns {Booleam} True if there is a connection in progress.
     */
    this.isConnectionInProgress = UnifyPhoneCallControlSvc.isConnectionInProgress;

    /**
     * Get the ringing incoming WebRTC call,
     * null will be returned if there is no incoming call.
     *
     * @returns {LocalCall} The LocalCall object.
     */
    this.getIncomingCall = UnifyPhoneCallControlSvc.getIncomingCall;

    /**
     * Get the active remote WebRTC call.
     *
     * @returns {Array} The RemoteCall object array.
     */
    this.getActiveRemoteCall = UnifyPhoneCallControlSvc.getActiveRemoteCall;

    /**
     * Join an existing group call or conference
     *
     * @param {Object} call The existing group call or conference
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error
     */
    this.joinGroupCall = UnifyPhoneCallControlSvc.joinGroupCall;

    /**
     * Make a new outgoing call in an existing conversation.
     *
     * @param {String} convId The conversation ID for the conversation to start the call.
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error
     */
    this.makeCall = UnifyPhoneCallControlSvc.makeCall;

    /**
     * Make a new CSTA call via the ATC.
     *
     * @param {String} target The target device where the call will be started.
     * @param {Object} destination The destination object for the call.
     * @param {Function} cb A callback function replying with an error
     */
    this.makeAtcCall = function (target, destination, cb) {
      var dialedDn = Utils.cleanPhoneNumber(typeof destination === 'object' ? destination.dialedDn : destination);
      if (typeof destination === 'object') {
        destination.dialedDn = dialedDn;
      } else {
        destination = {
          dialedDn: dialedDn
        };
      }

      if (destination.dtmfDigits) {
        destination.dtmfDigits = Utils.cleanPhoneNumberDigitsWithPin(destination.dtmfDigits);
      }
      var existingCallOnTarget =
        findAtcRemoteCallOnTarget(target) || (($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiVB) && getActivePhoneCall());

      CstaSvc.startCall(existingCallOnTarget, target, destination, cb);
    };

    /**
     * Make a test call.
     *
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Function} onConversationCreated A callback function that is immediately invoked with the rtcSessionId for the test call.
     * @param {Function} onCallStarted A callback function when the call is started or in case there is an error.
     */
    this.makeEchoTestCall = UnifyPhoneCallControlSvc.makeEchoTestCall;

    /**
     * Make a test call.
     *
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Object} additionalCallOptions The additional call options object, e.g. {desiredRegion: 'eu'}.
     * @param {Function} onConversationCreated A callback function that is immediately invoked with the rtcSessionId for the test call.
     * @param {Function} onCallStarted A callback function when the call is started or in case there is an error.
     */
    this.makeEchoTestCallWithOptions = UnifyPhoneCallControlSvc.makeEchoTestCallWithOptions;

    /**
     * Make a new outgoing call to a given DN.
     *
     * @param {String} dialedDn  The dialed number.
     * @param {String} toName The display name of the called unifyPhone user
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error, executed when the call is successfully placed.
     */

    this.dialNumber = UnifyPhoneCallControlSvc.dialNumber;

    /**
     * Make a new outgoing call to a given DN.
     *
     * @param {Object} [destination] Object The destination to be called.
     * @param {String} [destination.dialedDn] The dialed number.
     * @param {String} [destination.toName] The display name of the called unifyPhone user.
     * @param {String} [destination.userId] Id of the dialed user (if available). Otherwise null or empty string.
     * @param {Object} [destination.mediaType] The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error, executed when the call is successfully placed.
     */

    this.dialPhoneNumber = UnifyPhoneCallControlSvc.dialPhoneNumber;

    /**
     * Pull call by dialing a feature code. This is used for 5K.
     *
     * @param {Object} [destination] Object The destination to be called.
     * @param {String} [destination.dialedDn] The dialed number.
     * @param {String} [destination.toName] The display name of the called unifyPhone user.
     * @param {String} [destination.userId] Id of the dialed user (if available). Otherwise null or empty string.
     * @param {Object} [destination.mediaType] The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error, executed when the call is successfully placed.
     */

    this.pullWithFeatureCode = function (destination, cb) {
      var remoteCall = findActiveAtcRemoteCall();
      if (!remoteCall) {
        LogSvc.debug('[CallControlSvc]: Cannot find active remote call to pull!');
        cb('Cannot find active remote call to pull');
        return;
      }
      remoteCall.setAtcHandoverInProgress();
      this.dialUsingDefaultDevice(destination, cb);
    };

    /**
     * Make a new outgoing call to the selected device.
     *
     * Remarks: this function is intended to replace PhoneCallCtrl.dial function
     *
     * @param {Object} [destination] Object The destination to be called.
     * @param {String} [destination.dialedDn] The dialed number.
     * @param {String} [destination.toName] The display name of the called unifyPhone user.
     * @param {String} [destination.userId] Id of the dialed user (if available). Otherwise null or empty string.
     * @param {Object} [destination.mediaType] The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error, executed when the call is successfully placed.
     */
    this.dialUsingDefaultDevice = function (destination, cb) {
      cb = cb || function () {};

      var number = destination && destination.dialedDn;
      //FIXME: adapt for MiVB
      if (!$rootScope.localUser.isMiVB && (!number || !Utils.PHONE_DIAL_PATTERN.test(number))) {
        cb('Missing or wrong number');
        return;
      }
      LogSvc.debug('[CallControlSvc]: Calling ' + number + ' with name: ' + destination.toName);
      ConversationSvc.getTelephonyConversation(function (err, conversation) {
        if (err || !conversation) {
          LogSvc.debug('[CallControlSvc]: Error on getTelephonyConversation. ', err);
          cb(err || 'No telephony conversation');
          return;
        }

        if (conversation.call && conversation.call.consultation) {
          var target = conversation.call.getPosition() || _defaultCallDevice;
          if ((target && target !== Targets.WebRTC) || $rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiVB) {
            LogSvc.info('[CallControlSvc]: Start consultation from ' + target.name + ' to ', number);
            _that.makeAtcCall(target, destination, cb);
            conversation.call.consultation = false;
            return;
          }
        } else if (_defaultCallDevice && (_defaultCallDevice !== Targets.WebRTC || (conversation.call && $rootScope.localUser.isOsBizCTIEnabled))) {
          LogSvc.info('[CallControlSvc]: Make call from ' + _defaultCallDevice.name + ' to ', number);
          _that.makeAtcCall(_defaultCallDevice, destination, cb);
          return;
        }

        destination.mediaType = destination.mediaType || { audio: true, video: false, desktop: false };
        _that.dialPhoneNumber(destination, cb);
      });
    };

    /**
     * Set the default device to be used for new outgoing call.
     *
     * @param {Object} [device] The default device to be used.
     */
    this.setDefaultCallDevice = function (device) {
      if (!device || !device.name) {
        return;
      }
      // Make sure this is a valid Target device
      Object.keys(Targets).some(function (key) {
        if (device.name === Targets[key].name) {
          _defaultCallDevice = Targets[key];
          LogSvc.debug('[CallControlSvc]: Set defaultCallDevice to ', _defaultCallDevice.name);
          return true;
        }
        return false;
      });
    };

    /**
     * Start a conference in an existing group conversation.
     *
     * @param {Conversation} conversation The existing group conversation to start the conference.
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error
     */
    this.startConference = UnifyPhoneCallControlSvc.startConference;

    /**
     * Start a conference in an existing group conversation.
     *
     * @param {Conversation} conversation The existing group conversation to start the conference.
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Object} additionalCallOptions The additional call options object, e.g. {desiredRegion: 'eu'}.
     * @param {Function} cb A callback function replying with an error
     */
    this.startConferenceWithOptions = UnifyPhoneCallControlSvc.startConferenceWithOptions;

    /**
     * Returns true if there is an incoming call which cannot be answered before releasing
     * another active call.
     */
    this.cannotHandleSecondCall = function () {
      var incomingCall = _that.getIncomingCall();
      if (!incomingCall) {
        return false;
      }

      var activeCall = _that.getActiveCall();
      if (!activeCall) {
        return false;
      }
      return (
        (activeCall.isTelephonyCall && !incomingCall.isTelephonyCall) ||
        (!activeCall.isTelephonyCall && activeCall.checkState([CallState.Active, CallState.Waiting]))
      );
    };

    /**
     * Answer the incoming call. Any existing active call will be terminated.
     *
     * @param {String} callId The call ID of incoming call to be answered.
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Function} cb A callback function replying with an error
     */
    this.answerCall = function (callId, mediaType, cb) {
      var localPhoneCalls = UnifyPhoneCallControlSvc.getEstablishedLocalPhoneCalls();
      if (localPhoneCalls.length > 1) {
        LogSvc.warn('[CallControlSvc]: answerCall - There are already 2 local phone calls');
        cb && cb('Can not answer the call');
        return;
      }
      var call = UnifyPhoneCallControlSvc.findCall(callId);
      if (call) {
        if (($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiVB) && call.isTelephonyCall && call.atcCallInfo && call.isOsBizSecondCall) {
          CstaSvc.answerCstaCall(call.atcCallInfo.getCstaCallId(), cb);
        } else {
          UnifyPhoneCallControlSvc.answerCall(callId, mediaType, cb);
        }
        return;
      }

      call = findAtcRemoteCall(callId);
      var activeCall = UnifyPhoneCallControlSvc.getActiveCall();
      var incomingCall = UnifyPhoneCallControlSvc.getIncomingCall();
      if (call) {
        if (activeCall && !activeCall.isTelephonyCall) {
          UnifyPhoneCallControlSvc.endActiveCall(function () {
            CstaSvc.pullRemoteCall(callId, cb);
          });
        } else if (incomingCall && !incomingCall.isTelephonyCall) {
          UnifyPhoneCallControlSvc.endCallWithCauseCode(incomingCall.callId, Constants.InviteRejectCause.BUSY, function () {
            CstaSvc.pullRemoteCall(callId, cb);
          });
        } else {
          CstaSvc.pullRemoteCall(callId, cb);
        }
      } else {
        LogSvc.warn('[CallControlSvc]: answerCall - There is no alerting call');
        cb && cb('No alerting call');
      }
    };

    /**
     * Start the RtcSessionController and prepares the SDP answer for an incoming telephony call.
     *
     * @param {String} callId The call ID of incoming telephony call.
     * @returns {Promise} Promise resolved when request is accepted.
     */
    this.prepareEarlyMedia = UnifyPhoneCallControlSvc.prepareEarlyMedia;

    /**
     * Answer the incoming ATC call on any device. Any existing active call will be terminated
     *
     * @param {String} callId The call ID of incoming call to be answered.
     * @param {Object} mediaType The media type object, e.g. {audio: true, video: false}.
     * @param {Object} device The device object
     * @param {Function} cb A callback function replying with an error
     */
    this.answerCallOnDevice = function (callId, mediaType, device, cb) {
      cb = cb || function () {};
      if (!device || device === Targets.WebRTC) {
        _that.answerCall(callId, mediaType, cb);
        return;
      }

      var call = UnifyPhoneCallControlSvc.findCall(callId);
      if (call) {
        if (call.isTelephonyCall) {
          if (
            ($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiV5000 || $rootScope.localUser.isMiVB) &&
            device !== Targets.VM &&
            device !== Targets.Cell
          ) {
            CstaSvc.answerCstaCall(call.atcCallInfo.getCstaCallId(), cb);
          } else {
            CstaSvc.deflect(callId, device, null, cb);
          }
        } else {
          LogSvc.warn('[CallControlSvc]: answerCallOnDevice - The active call is not a telephony call');
          cb('Not telephony call');
        }
        return;
      }

      call = findAtcRemoteCall(callId);
      if (call) {
        if (call.pickupNotification) {
          CstaSvc.pickupCall(callId, device, cb);
        } else if (call.getPosition() !== device) {
          CstaSvc.deflect(callId, device, null, cb);
        } else {
          CstaSvc.answer(callId, cb);
        }
      } else {
        LogSvc.warn('[CallControlSvc]: answerCallOnDevice - There is no alerting call');
        cb('No alerting call');
      }
    };

    /**
     * End a call. This could be a local or remote UnifyPhone call, or an ATC call.
     *
     * @param {String} callId The call ID of the ongoing call to be terminated.
     * @param {Function} cb A callback function replying with an error
     */
    this.endCall = function (callId, cb) {
      _that.endCallWithCauseCode(callId, null, cb);
    };

    /**
     * End a call specifying the cause. This could be a local or remote UnifyPhone call, or an ATC call.
     *
     * @param {String} callId The call ID of local call to be terminated.
     * @param {String} cause Cause code for terminating the call. It can be either InviteRejectCause or CallClientTerminatedReason.
     * @param {Function} cb A callback function replying with an error
     */
    this.endCallWithCauseCode = function (callId, cause, cb) {
      var call = UnifyPhoneCallControlSvc.findCall(callId);

      if (call) {
        if (call.isOsBizFirstCall) {
          var secondCall = UnifyPhoneCallControlSvc.findOsBizSecondCall();
          if (secondCall && secondCall.direction === Enums.CallDirection.INCOMING && secondCall.checkState(CallState.Ringing)) {
            CstaSvc.endCstaCall(call.atcCallInfo.getCstaCallId());
          } else {
            CstaSvc.reconnect(call, UnifyPhoneCallControlSvc.findOsBizSecondCall(), cb);
          }
        } else if (call.isOsBizSecondCall) {
          CstaSvc.reconnect(call, UnifyPhoneCallControlSvc.findOsBizFirstCall(), cb);
        } else if (call.isRemote) {
          UnifyPhoneCallControlSvc.endRemoteCall(callId, cb);
        } else if (cause) {
          UnifyPhoneCallControlSvc.endCallWithCauseCode(callId, cause, cb);
        } else {
          // For OSBiz call with parallel ringing, when rejecting a call, also end the call to remote devices
          if ($rootScope.localUser.isOsBizCTIEnabled && call.isTelephonyCall && call.checkState(CallState.Ringing)) {
            CstaSvc.endCallOnRemoteDevices(call.atcCallInfo.getCstaCallId());
          }
          UnifyPhoneCallControlSvc.endCall(callId, cb);
        }
        return;
      }

      call = findAtcRemoteCall(callId);

      if (call && _atcRemoteCalls.length > 1) {
        if (_that.reconnectCall(callId, cb)) {
          return;
        }
      }
      if (call) {
        CstaSvc.endRemoteCall(callId, cb);
      } else {
        LogSvc.warn('[CallControlSvc]: endCall invoked without a valid call. callId = ', callId);
        cb && cb('Call not found');
      }
    };

    /**
     * End a held phone call. This API is intended for clients that cannot disable the call release
     * button when holding a call but pbx requires that the call be retrieved before releasing it.
     *
     * @param {String} callId The call ID of local call to be terminated.
     * @param {Function} cb A callback function replying with an error
     */
    this.retrieveAndEndCall = function (callId, cb) {
      cb = cb || function () {};

      var call = UnifyPhoneCallControlSvc.findCall(callId) || findAtcRemoteCall(callId);
      if (!call) {
        LogSvc.warn('[CallControlSvc]: retrieveAndEndCall invoked without a valid call');
        cb('Call not found');
        return;
      }

      if (!call.isTelephonyCall || !call.isHolding()) {
        // Just end the call
        _that.endCall(callId, cb);
        return;
      }

      var endCallAfterRetrieval = function (err) {
        if (err) {
          LogSvc.warn('[CallControlSvc]: Failed to retrieve call');
          cb(err);
          return;
        }
        _that.endCall(callId, cb);
      };

      if ($rootScope.localUser.isOsBizCTIEnabled && (!call.isAtcRemote || call.isAtPosition(Targets.Cell))) {
        // In case of OSBiz, there is no SIP call control signaling involved in hold/retrieve, only CSTA
        CstaSvc.retrieveWithReconnectCall(call, endCallAfterRetrieval);
      } else if (call.isAtcRemote) {
        CstaSvc.retrieve(call, endCallAfterRetrieval);
      } else {
        UnifyPhoneCallControlSvc.retrieveCall(callId, endCallAfterRetrieval);
      }
    };

    /**
     * End conference using local UnifyPhone call.
     */
    this.endConference = UnifyPhoneCallControlSvc.endConference;

    /**
     * Enable audio in an existing RTC session.
     *
     * @param {String} callId The call ID of call to add audio to.
     * @param {Function} cb A callback function replying with an error
     */
    this.addAudio = UnifyPhoneCallControlSvc.addAudio;

    /**
     * Disable audio in an existing RTC session.
     *
     * @param {String} callId The call ID of call to remove audio from.
     * @param {Function} cb A callback function replying with an error
     */
    this.removeAudio = UnifyPhoneCallControlSvc.removeAudio;

    /**
     * Enable remote audio stream in an active call
     *
     * @param {String} callId The call ID to be muted
     */
    this.enableRemoteAudio = UnifyPhoneCallControlSvc.enableRemoteAudio;

    /**
     * Disable remote audio stream in an active call
     *
     * @param {String} callId The call ID to be muted
     */
    this.disableRemoteAudio = UnifyPhoneCallControlSvc.disableRemoteAudio;

    /**
     * Enable video in an existing RTC session.
     *
     * @param {String} callId The call ID of call to add video to.
     * @param {Function} cb A callback function replying with an error
     */
    this.addVideo = UnifyPhoneCallControlSvc.addVideo;

    /**
     * Disable video in an existing RTC session.
     *
     * @param {String} callId The call ID of call to remove video from.
     * @param {Function} cb A callback function replying with an error
     */
    this.removeVideo = UnifyPhoneCallControlSvc.removeVideo;

    /**
     * Add a video stream to an existing RTC session.
     * For adding a screen share stream, please use addMediaStream() instead.
     *
     * @param {String} callId The call ID of call to add video to.
     * @param {MediaStream} stream The video media stream to be added.
     * @param {Object} options Options object
     * @return {Promise} Promise resolved when adding the video stream is successfully initiated
     */
    this.addVideoStream = UnifyPhoneCallControlSvc.addVideoStream;

    /**
     * Toggle (allow or block) remote video streams in an existing RTC session.
     *
     * @param {String} callId The call ID of call to remove remote video from.
     * @param {Function} cb A callback function replying with an error
     */
    this.toggleRemoteVideo = UnifyPhoneCallControlSvc.toggleRemoteVideo;

    /**
     * Disable remote video streams only in an existing RTC session.
     * Remote Screen share is be allowed.
     * @param {String} callId The call ID of call to remove remote video from.
     * @param {Function} cb A callback function replying with an error
     */
    this.disableRemoteVideoOnly = UnifyPhoneCallControlSvc.disableRemoteVideoOnly;

    /**
     * Enable remote and screen share video streams in an existing
     * RTC session.
     *
     * @param {String} callId The call ID of call to remove remote video from.
     * @param {Function} cb A callback function replying with an error
     */
    this.enableRemoteVideo = UnifyPhoneCallControlSvc.enableRemoteVideo;

    /**
     * Toggle the video in an existing RTC session.
     *
     * @param {String} callId The call ID of call for which video will be toggled.
     * @param {Function} cb A callback function replying with an error
     */
    this.toggleVideo = UnifyPhoneCallControlSvc.toggleVideo;

    /**
     * Use a new camera in an existing RTC session. The video constraints will remain the same.
     *
     * @param {String} callId The call ID of call for which video will be toggled.
     * @param {String} deviceId The device ID of the camera to be used.
     * @param {Function} cb A callback function replying with an error
     */
    this.toggleCamera = UnifyPhoneCallControlSvc.toggleCamera;

    /**
     * Enable/disable streaming HD on an existing RTC session.
     *
     * @param {String} callId The call ID of call for which video will be toggled.
     * @param {Boolean} hdQuality Flag to enable/disable HD.
     * @param {Function} cb A callback function replying with an error
     * @param {VideoResolutionLevel} newResolution Desired HD resolution. Defaults to max HD resolution supported by camera.
     */
    this.changeHDVideo = UnifyPhoneCallControlSvc.changeHDVideo;

    /**
     * Change video resolution in an existing RTC session.
     *
     * @param {String} callId The call ID of call for which video will be toggled.
     * @param {VideoResolutionLevel} newResolution Desired HD resolution.
     * @param {Function} cb A callback function replying with an error
     */
    this.changeVideoResolution = UnifyPhoneCallControlSvc.changeVideoResolution;

    /**
     * Starts a media renegotiation from the client without changing the current media types.
     *
     * @param {String} callId The call ID of the call for which to start the media renegotiation.
     * @param {Function} cb A callback function replying with an error
     */
    this.renegotiateMedia = UnifyPhoneCallControlSvc.renegotiateMedia;

    /**
     * Set the audio and/or video media type for the current call.
     *
     * @param {String} callId The call ID of the call for which to start the media renegotiation.
     * @param {Object} mediaType Object with audio and video boolean attributes.
     * @param {Function} cb A callback function replying with an error
     */
    this.setMediaType = UnifyPhoneCallControlSvc.setMediaType;

    /**
     * Add a video stream as screen share to an existing RTC session. The video and bandwidth
     * constraints of screen share will be used.
     * For adding it as video, please use addVideoStream() instead.
     *
     * @param {String} callId The call ID of call to add screen share to.
     * @param {MediaStream} stream The media stream to share.
     * @param {Function} cb A callback function replying with an error
     */
    this.addMediaStream = UnifyPhoneCallControlSvc.addMediaStream;

    /**
     * Remove a media stream from an existing RTC session.
     *
     * @param {String} callId The call ID of call to remove stream from.
     * @param {Function} cb A callback function replying with an error
     */
    this.removeMediaStream = UnifyPhoneCallControlSvc.removeMediaStream;

    /**
     * Move a remote call to the local client
     *
     * @param {String} callId The call ID of existing remote call established on another client.
     * @param {BOOL} fallbackToAudio flag to check whether to pull with audio only.
     * @param {Function} cb A callback function replying with an error
     */
    this.pullRemoteCall = function (callId, fallbackToAudio, cb) {
      var call = UnifyPhoneCallControlSvc.findCall(callId);

      if (call) {
        UnifyPhoneCallControlSvc.pullRemoteCall(callId, fallbackToAudio, cb);
        return;
      }

      call = findAtcRemoteCall(callId);
      if (call) {
        var activeCall = UnifyPhoneCallControlSvc.getActiveCall();
        if (activeCall) {
          if (activeCall.isTelephonyCall) {
            UnifyPhoneCallControlSvc.holdCall(activeCall.callId, function () {
              CstaSvc.pullRemoteCall(callId, cb);
            });
            return;
          } else {
            UnifyPhoneCallControlSvc.endActiveCall(function () {
              CstaSvc.pullRemoteCall(callId, cb);
            });
            return;
          }
        }
        CstaSvc.pullRemoteCall(callId, cb);
      } else {
        LogSvc.warn('[CallControlSvc]: pullRemoteCall invoked without a valid call');
        cb && cb('No active remote call');
      }
    };

    this.pushLocalCall = CstaSvc.pushLocalCall;

    /**
     * Gets a list with all the ATC calls (i.e. the CSTA calls).
     */
    this.getAtcCalls = function () {
      return _atcRemoteCalls;
    };

    /**
     * Alternates (swaps) between two calls.
     *
     * @param {String} callId The call ID of the ATC call that is on hold.
     */
    this.swapCall = function (callId, cb) {
      cb = cb || function () {};
      var osBizCalls = getOsBizConsultationCalls();
      if (osBizCalls) {
        // This is a local OsBiz consultation call
        CstaSvc.alternate(osBizCalls.heldCall, osBizCalls.activeCall, cb);
        return;
      }

      var call = UnifyPhoneCallControlSvc.findCall(callId);
      if (call && call.isTelephonyCall && !call.isRemote) {
        UnifyPhoneCallControlSvc.swapCall(callId, cb);
        return;
      }

      if (_atcRemoteCalls.length > 1) {
        var heldCall = _atcRemoteCalls.find(function (remoteCall) {
          return remoteCall.isHolding() && remoteCall.callId === callId;
        });
        if (heldCall) {
          var position = heldCall.getPosition();
          var activeCall = _atcRemoteCalls.find(function (remoteCall) {
            return (
              remoteCall.checkCstaState([CstaCallState.Active, CstaCallState.Conference]) && remoteCall.isAtPosition(position) && remoteCall.callId !== callId
            );
          });
          if (activeCall) {
            CstaSvc.alternate(heldCall, activeCall, cb);
            return;
          }
        }
      }

      LogSvc.warn('[CallControlSvc]: swapCall possible only with 2 calls');
      cb('No calls to swap');
    };

    /**
     * Conferences (merges) two ATC calls.
     *
     * @param {String} callId The call ID of the remote ATC call to be merged.
     */
    this.mergeCall = function (callId, cb) {
      cb = cb || function () {};
      var localCalls = UnifyPhoneCallControlSvc.getPhoneCalls(true);
      if (_atcRemoteCalls.length <= 1 && localCalls.length <= 1) {
        LogSvc.warn('[CallControlSvc]: mergeCall possible only with 2 calls');
        cb('No calls to merge');
        return;
      }
      var activeCall, heldCall;

      if (localCalls.length > 1) {
        activeCall = UnifyPhoneCallControlSvc.findActivePhoneCall(true);
        heldCall = UnifyPhoneCallControlSvc.findHeldPhoneCall(true);
        if (activeCall && heldCall) {
          // Merge local calls
          if ($rootScope.localUser.isSTC) {
            if (heldCall.isDirectUpgradedToConf) {
              UnifyPhoneCallControlSvc.retrieveCall(heldCall.callId, function (err) {
                if (err) {
                  LogSvc.error('[CallControlSvc]: Error retrieving the hosted call');
                  cb('Error in retrieving the hosted call');
                } else {
                  mergeCalls(activeCall.callId, heldCall.callId, cb);
                }
              });
            } else {
              mergeCalls(heldCall.callId, activeCall.callId, cb);
            }
            return;
          } else if (heldCall.isAtcConferenceCall() && !$rootScope.localUser.isOsBizCTIEnabled) {
            // If the held call is a PBX conference, we need to alternate to it before adding the consulted
            // party to the conference. This is not necessary for OSBiz calls.
            _that.swapCall(heldCall.callId, function (err) {
              if (err) {
                LogSvc.warn('[CallControlSvc]: Swap call failed. Cannot proceed with merge call.');
                cb(err);
              } else {
                CstaSvc.conference(activeCall, heldCall, cb);
              }
            });
          } else {
            CstaSvc.conference(heldCall, activeCall, cb);
          }
          return;
        }
      }

      // Try merging remote calls
      activeCall = _atcRemoteCalls.find(function (call) {
        return call.checkCstaState([CstaCallState.Active, CstaCallState.Conference]) && call.callId === callId;
      });

      if (activeCall) {
        var position = activeCall.getPosition();
        heldCall = _atcRemoteCalls.find(function (call) {
          return call.isHolding() && call.isAtPosition(position) && call.callId !== callId;
        });
        if (heldCall) {
          CstaSvc.conference(heldCall, activeCall, cb);
          return;
        }
      }

      LogSvc.warn('[CallControlSvc]: mergeCall possible only with 2 calls');
      cb('No calls to merge');
    };

    /**
     * Transfers ATC calls.
     *
     * @param {String} callId The call ID of the remote ATC call to be merged.
     */
    this.transferCall = function (callId, number, cb) {
      cb = cb || function () {};
      var osBizCalls = getOsBizConsultationCalls();
      var activeCall = UnifyPhoneCallControlSvc.findCall(callId);

      if (osBizCalls) {
        if (hasTwoCallsWithOneRinging(osBizCalls)) {
          CstaSvc.transfer(activeCall, number, cb);
          return;
        }
        // This is a local OsBiz consultation call
        CstaSvc.consultationTransfer(osBizCalls.activeCall, osBizCalls.heldCall, cb);
        return;
      }

      var localCalls = UnifyPhoneCallControlSvc.getPhoneCalls(true);
      var heldCall;

      number = Utils.cleanPhoneNumber(number);

      if (activeCall && localCalls.length === 1) {
        if (activeCall.isTelephonyCall && activeCall.atcCallInfo) {
          CstaSvc.transfer(activeCall, number, cb);
        } else if (activeCall.isSTC) {
          sendTransferRequest(activeCall, number, cb);
        } else {
          LogSvc.warn('[CallControlSvc]: The active call is not a telephony call');
          cb('The active call is not a telephony call');
        }
        return;
      }

      if (activeCall && localCalls.length === 2) {
        heldCall = UnifyPhoneCallControlSvc.findHeldPhoneCall(true);
        if (activeCall.isTelephonyCall && activeCall.atcCallInfo) {
          CstaSvc.consultationTransfer(activeCall, heldCall, cb);
        } else if (activeCall.isSTC) {
          sendTransferRequest(activeCall.state.established ? activeCall : heldCall, activeCall.state.established ? heldCall : activeCall, cb);
        } else {
          LogSvc.warn('[CallControlSvc]: The active call is not a telephony call');
          cb('The active call is not a telephony call');
        }
        return;
      }

      if (_atcRemoteCalls.length > 0) {
        if (_atcRemoteCalls.length === 1) {
          if (!_atcRemoteCalls[0].isHolding()) {
            activeCall = _atcRemoteCalls[0];
            CstaSvc.transfer(activeCall, number, cb);
            return;
          }
        }

        activeCall = _atcRemoteCalls.find(function (call) {
          return !call.isHolding() && call.callId === callId;
        });
        if (activeCall) {
          var position = activeCall.getPosition();
          heldCall = _atcRemoteCalls.find(function (call) {
            return call.isHolding() && call.isAtPosition(position) && call.callId !== callId;
          });
          if (heldCall) {
            CstaSvc.consultationTransfer(activeCall, heldCall, cb);
            return;
          }
        }
      }

      LogSvc.warn('[CallControlSvc]: transferCall possible only with 1 or 2 calls');
      cb('Not enough calls for the transfer');
    };

    /**
     * Hold an ATC call.
     *
     * @param {String} callId The call ID of the ATC call to be held.
     */
    this.holdCall = function (callId, cb) {
      cb = cb || function () {};
      var call = UnifyPhoneCallControlSvc.findCall(callId);
      if (call) {
        if (!call.isHolding()) {
          // In case of OSBiz, there is no SIP call control signaling involved in hold/retrieve, only CSTA
          $rootScope.localUser.isOsBizCTIEnabled && call.isTelephonyCall
            ? CstaSvc.holdWithConsultationCall(call, cb)
            : UnifyPhoneCallControlSvc.holdCall(callId, cb);
        } else {
          LogSvc.warn('[CallControlSvc]: Call is not in valid state');
          cb('Call is not in valid state');
        }
        return;
      }

      call = findAtcRemoteCall(callId);
      if (call) {
        if (!call.isHolding()) {
          $rootScope.localUser.isOsBizCTIEnabled && call.getPosition() === Targets.Cell ? CstaSvc.holdWithConsultationCall(call, cb) : CstaSvc.hold(call, cb);
        } else {
          LogSvc.warn('[CallControlSvc]: Call is not in valid state');
          cb('Call is not in valid state');
        }
      } else {
        LogSvc.warn('[CallControlSvc]: holdCall invoked without a valid call');
        cb('Call not found');
      }
    };

    /**
     * Retrieve an ATC held call.
     *
     * @param {String} callId The call ID of the ATC call to be retrieved.
     */
    this.retrieveCall = function (callId, cb) {
      cb = cb || function () {};
      var call = UnifyPhoneCallControlSvc.findCall(callId);
      if (call) {
        if (call.isHolding()) {
          // In case of OSBiz, there is no SIP call control signaling involved in hold/retrieve, only CSTA
          $rootScope.localUser.isOsBizCTIEnabled && call.isTelephonyCall
            ? CstaSvc.retrieveWithReconnectCall(call, cb)
            : UnifyPhoneCallControlSvc.retrieveCall(callId, cb);
        } else {
          LogSvc.warn('[CallControlSvc]: Call is not in valid state');
          cb('Call is not in valid state');
        }
        return;
      }

      call = findAtcRemoteCall(callId);
      if (call) {
        if (call.isHolding()) {
          $rootScope.localUser.isOsBizCTIEnabled && call.getPosition() === Targets.Cell
            ? CstaSvc.retrieveWithReconnectCall(call, cb)
            : CstaSvc.retrieve(call, cb);
        } else {
          LogSvc.warn('[CallControlSvc]: Call is not in valid state');
          cb('Call is not in valid state');
        }
      } else {
        LogSvc.warn('[CallControlSvc]: retrieveCall invoked without a valid call');
        cb('Call not found');
      }
    };

    /**
     * Reconnect an ATC call.
     *
     * @param {String} callId The call ID of the ATC active call.
     */
    this.reconnectCall = function (callId, cb) {
      if (_atcRemoteCalls.length > 1) {
        var activeCall = _atcRemoteCalls.find(function (call) {
          return !call.isHolding() && call.callId === callId;
        });
        if (activeCall) {
          var position = activeCall.getPosition();
          var heldCall = _atcRemoteCalls.find(function (call) {
            return call.isHolding() && call.isAtPosition(position) && call.callId !== callId;
          });
          if (heldCall) {
            CstaSvc.reconnect(activeCall, heldCall, cb);
            return true;
          }
        }
      }

      LogSvc.warn('[CallControlSvc]: reconnectCall possible only with 2 calls');
      cb('No calls to reconnect');
      return false;
    };

    /**
     * Ignore a pickup notification call
     *
     * @param {String} callId The call ID of the pickup notification call
     */
    this.ignoreCall = function (callId, cb) {
      var call = findAtcRemoteCall(callId);
      if (call && call.pickupNotification) {
        CstaSvc.ignoreCall(call, cb);
      } else {
        var callToPickup = UnifyPhoneCallControlSvc.getCallToPickup();
        if (callToPickup && (callToPickup.pickupSession === callId || callToPickup.callId === callId)) {
          UnifyPhoneCallControlSvc.ignorePickupCall();
        } else {
          cb && cb('No pickup call');
        }
      }
    };

    /**
     * Hide a remote call.
     *
     * @param {String} callId The call ID of remote call to be hidden.
     */
    this.hideRemoteCall = UnifyPhoneCallControlSvc.hideRemoteCall;

    /**
     * Remove participant in a group call.
     *
     * @param {String} callId The call ID of a group call or conference.
     * @param {Object} participant The call participant object.
     * @param {Function} cb A callback function replying with an error
     */
    this.dropParticipant = UnifyPhoneCallControlSvc.dropParticipant;

    /**
     * Add participant to a call.
     *
     * @param {String} callId The call ID
     * @param {Object} participant The participant object.
     * @param {Function} cb A callback function replying with an error
     */
    this.addParticipantToCall = UnifyPhoneCallControlSvc.addParticipantToCall;

    /**
     * Add participant to an RTC session.
     *
     * @param {String} callId The call ID
     * @param {Object} participant The participant object.
     * @param {Function} cb A callback function replying with an error
     */
    this.addParticipantToRtcSession = UnifyPhoneCallControlSvc.addParticipantToRtcSession;

    /**
     * mute self locally in an active call
     *
     * @param {String} callId The call ID to be muted
     * @param {Function} cb A callback function replying with an error
     */
    this.mute = UnifyPhoneCallControlSvc.mute;

    /**
     * unmute locally only (used for large conference)
     *
     * @param {Function} cb A callback function called on success
     */
    this.unmuteLocally = UnifyPhoneCallControlSvc.unmuteLocally;

    /**
     * unmute self in an active call, which may have been muted locally or remotely
     *
     * @param {String} callId The call ID to be unmuted
     * @param {Function} cb A callback function replying with an error
     */
    this.unmute = UnifyPhoneCallControlSvc.unmute;

    /**
     * toggle between mute and unmute
     *
     * @param {String} callId The call ID to toggle mute
     * @param {Function} cb A callback function replying with an error
     */
    this.toggleMute = UnifyPhoneCallControlSvc.toggleMute;

    /**
     * mute remote participant
     *
     * @param {String} callId The call ID of active call
     * @param {Object} participant The participant object to be muted
     * @param {Function} cb A callback function replying with an error
     */
    this.muteParticipant = function (callId, participant, cb) {
      cb = cb || function () {};
      if (!callId || !participant) {
        cb('Missing callId or participant');
        return;
      }

      if (!isLocalCall(callId)) {
        LogSvc.warn('[CallControlSvc]: muteParticipant - There is no local call');
        cb('Call invalid');
        return;
      }

      UnifyPhoneCallControlSvc.muteParticipant(callId, participant, cb);
    };

    /**
     * check if there are unmuted participants
     *
     * @param {String} callId The call ID of active call
     */
    this.hasUnmutedParticipants = UnifyPhoneCallControlSvc.hasUnmutedParticipants;

    /**
     * check for Permission for Screen Control
     * @param {Object} call The object of the current call
     */
    this.hasScreenControlFeature = UnifyPhoneCallControlSvc.hasScreenControlFeature;

    /**
     * mute RTC session
     *
     * @param {String} callId The call ID of active call
     * @param {Function} cb A callback function replying with an error
     */
    this.muteRtcSession = UnifyPhoneCallControlSvc.muteRtcSession;

    /**
     * unmute self which has been muted remotely
     *
     * @param {String} callId The call ID of active call
     * @param {Object} participant The participant object to be unmuted
     * @param {Function} cb A callback function replying with an error
     */
    this.unmuteParticipant = function (callId, participant, cb) {
      cb = cb || function () {};
      if (!callId || !participant) {
        cb('Missing callId or participant');
        return;
      }

      if (!isLocalCall(callId)) {
        LogSvc.warn('[CallControlSvc]: unmuteParticipant - There is no local call');
        cb('Call invalid');
        return;
      }
      UnifyPhoneCallControlSvc.unmuteParticipant(callId, participant, cb);
    };

    /**
     * toggle between participant mute and unmute
     *
     * @param {String} callId The call ID of active call
     * @param {Object} participant The participant object for which to toggle mute
     * @param {Function} cb A callback function replying with an error
     */
    this.toggleMuteParticipant = function (callId, participant, cb) {
      if (!callId || !participant) {
        cb('Missing callId or participant');
        return;
      }
      if (participant.muted) {
        _that.unmuteParticipant(callId, participant, cb);
      } else {
        _that.muteParticipant(callId, participant, cb);
      }
    };

    /**
     * toggle video on MeetingPoing participant
     *
     * @param {String} callId The call ID of active call
     * @param {Object} participant The participant object for which to toggle video
     * @param {Function} cb A callback function replying with an error
     */
    this.toggleVideoParticipant = function (callId, participant, cb) {
      cb = cb || function () {};
      if (!callId || !participant || !participant.userId) {
        cb('Missing callId or participant');
        return;
      }

      if (!isLocalCall(callId)) {
        LogSvc.warn('[CallControlSvc]: toggleVideoParticipant - There is no local call');
        cb('Call invalid');
        return;
      }

      LogSvc.warn('[CallControlSvc]: Cannot toggle video MeetingPoint participant: ', participant.userId);
      cb('res_StartVideoParticipantFailed');
    };

    this.toggleScreenSharing = function () {};

    this.toggleConferencePoll = function () {};

    this.toggleWhiteboard = function () {};

    this.toggleVideoFeature = function () {};

    this.toggleLayout = function () {};

    this.swapDisplays = function () {};

    this.invokeParticipantAction = function (data, cb) {
      switch (data.action) {
        case ParticipantAction.StartVideo:
          if (canStartVideoParticipant(data.participant)) {
            checkAndToggleVideoParticipant(data.call, data.participant, cb);
          }
          break;
        case ParticipantAction.StopVideo:
          if (canStopVideoParticipant(data.participant)) {
            checkAndToggleVideoParticipant(data.call, data.participant, cb);
          }
          break;
        case ParticipantAction.Mute:
          _that.checkAndMuteParticipant(data.call.callId, data.participant);
          break;
        case ParticipantAction.Unmute:
          _that.checkAndUnmuteParticipant(data.call.callId, data.participant);
          break;
      }
    };

    this.canUnmuteParticipant = canUnmuteParticipant;

    this.checkAndMuteParticipant = function (callId, participant) {
      if (canMuteParticipant(participant)) {
        LogSvc.buttonPressed('Mute other participant with userId: ', participant.userId);
        _that.muteParticipant(callId, participant);
      } else {
        LogSvc.info('[CallControlSvc]: Cannot mute remote participant');
      }
    };

    this.checkAndUnmuteParticipant = function (callId, participant) {
      if (canUnmuteParticipant(participant)) {
        LogSvc.buttonPressed('Unmute other participant with userId: ', participant.userId);
        _that.unmuteParticipant(callId, participant);
      } else {
        LogSvc.info('[CallControlSvc]: Cannot unmute remote participant');
      }
    };

    /**
     * Check whether video option is available
     *
     * @param {Object} participant The participant object for which to allow to show video option
     * @param {Boolean} isAnyFeatureEnabled Flag indicating if whiteboard or screenshare is turned on
     *
     * @return {Boolean}
     */
    this.isAllowedToAddVideoFeature = function (participant, isAnyFeatureEnabled) {
      if (!participant || !participant.isMeetingPointInvitee || !participant.cmrData) {
        return false;
      }
      return !!(
        participant.cmrData.displayCount === 1 &&
        isAnyFeatureEnabled &&
        (participant.cmrData.isLayoutsEnabled ? !!participant.cmrData.primaryScreen : !participant.cmrData.primaryScreen)
      );
    };

    this.stopRingingTone = UnifyPhoneCallControlSvc.stopRingingTone;

    /**
     * End active call.
     *
     * @param {Function} cb A callback function replying with a status
     */
    this.endActiveCall = UnifyPhoneCallControlSvc.endActiveCall;

    this.startRecording = UnifyPhoneCallControlSvc.startRecording;

    this.stopRecording = UnifyPhoneCallControlSvc.stopRecording;

    this.startTranscription = UnifyPhoneCallControlSvc.startTranscription;

    this.stopTranscription = UnifyPhoneCallControlSvc.stopTranscription;

    this.switchRecordingLayout = UnifyPhoneCallControlSvc.switchRecordingLayout;

    this.startRecordingParticipant = UnifyPhoneCallControlSvc.startRecordingParticipant;

    this.submitCallQualityRating = UnifyPhoneCallControlSvc.submitCallQualityRating;

    /**
     * retrieve list of nodes with states
     *    currently only used for testcall
     * @param {String} nodeType requested node type(currently only MEDIA_ACCESS)
     * @param {String} tenantId name of the tenant or empty for all
     * @param {Function} cb A callback function replying with an error or success
     */
    this.getNodeState = UnifyPhoneCallControlSvc.getNodeState;

    this.canSendWebRTCDigits = UnifyPhoneCallControlSvc.canSendWebRTCDigits;

    /**
     * Send DTMF digits
     *
     * @param {String} callId The call ID of active call
     * @param {String} digits The digits to be sent
     * @param {Function} cb A callback function replying with a status
     */
    this.sendDigits = function (callId, digits, cb) {
      cb = cb || function () {};

      if (!digits) {
        LogSvc.warn('[CallControlSvc]: sendDigits invoked without any digit');
        cb('No digits');
        return;
      }
      var call = UnifyPhoneCallControlSvc.findCall(callId);
      if (call) {
        if (call.sessionCtrl.canSendDTMFDigits()) {
          UnifyPhoneCallControlSvc.sendDigits(callId, digits, cb);
        } else if (call.atcCallInfo) {
          // For ATC calls we can use CSTA to generate DTMF digits (if we can't use WebRTC)
          CstaSvc.generateDigits(call, digits, function (err) {
            if (err) {
              cb('res_SendDtmfFailed');
            } else {
              cb();
            }
          });
        } else {
          cb('res_SendDtmfFailed');
        }
        return;
      }

      call = findAtcRemoteCall(callId);
      if (call) {
        CstaSvc.generateDigits(call, digits, cb);
      } else {
        LogSvc.warn('[CallControlSvc]: SendDigits invoked without a valid call');
        cb('Call not found');
      }
    };

    /**
     * Locally disable the remote incoming video (this doesn't trigger a
     * media renegotiation)
     * @returns {undefined}
     */
    this.disableIncomingVideo = UnifyPhoneCallControlSvc.disableIncomingVideo;

    /**
     * Locally enable the remote incoming video (this doesn't trigger a
     * media renegotiation)
     * @returns {undefined}
     */
    this.enableIncomingVideo = UnifyPhoneCallControlSvc.enableIncomingVideo;

    /**
     * Enable or disable participant pointer
     *
     * @param {boolean} true=enabled, false=disabled.
     * @returns {undefined}
     */
    this.toggleParticipantPointer = UnifyPhoneCallControlSvc.toggleParticipantPointer;

    /**
     * Find active or incoming local call
     * @returns {LocalCall} The LocalCall object.
     */
    this.findCall = UnifyPhoneCallControlSvc.findCall;

    /**
     * Find the conversation for a given callId
     *
     * @param callId
     * @returns {Conversation} The conversation object
     */
    this.findConversationByCallId = findConversationByCallId;

    this.getMessageWaiting = CstaSvc.getMessageWaiting;

    /**
     * Returns the ATC devices that can initiate a call
     * @returns {Object[]} The device list
     */
    this.getCallDevices = CstaSvc.getCallDevices;

    /**
     * Returns the ATC devices that the call can be pushed
     * @returns {Object[]} The push device list
     */
    this.getPushDevices = CstaSvc.getPushDevices;

    /**
     * Returns the ATC devices the call can be answered at.
     * @param callId Call ID
     * @returns {Object[]} The answer device list
     */
    this.getAnswerDevices = function (callId) {
      if (callId && typeof callId === 'object') {
        // Backwards compatibility
        return CstaSvc.getAnswerDevices(callId);
      }
      var call = UnifyPhoneCallControlSvc.findCall(callId) || findAtcRemoteCall(callId);
      return CstaSvc.getAnswerDevices(call);
    };

    this.findLocalCallByCallId = UnifyPhoneCallControlSvc.findLocalCallByCallId;
    /**
     * Use this method to force the active call object to query the currently used
     * audio/video devices.
     *
     * @returns {undefined}
     */
    this.updateActiveCallMediaDevices = UnifyPhoneCallControlSvc.updateActiveCallMediaDevices;

    this.canStartScreenShare = UnifyPhoneCallControlSvc.canStartScreenShare;

    this.canStartRecording = UnifyPhoneCallControlSvc.canStartRecording;

    this.canTakeScreenshot = UnifyPhoneCallControlSvc.canTakeScreenshot;

    this.addConferenceAsGuest = UnifyPhoneCallControlSvc.addConferenceAsGuest;

    this.removeConferenceAsGuest = UnifyPhoneCallControlSvc.removeConferenceAsGuest;

    /**
     * Test ICE candidates collection
     * @returns {Promise} Returns an object containing the assigned TURN servers and the gathered ICE candidates.
     */
    this.testCandidatesCollection = UnifyPhoneCallControlSvc.testCandidatesCollection;

    this.findOsBizSecondCall = UnifyPhoneCallControlSvc.findOsBizSecondCall;

    this.getConferenceParticipants = UnifyPhoneCallControlSvc.getConferenceParticipants;

    /**
     * Changes the input device(s) used in the call. By invoking this method, a media renegotiation
     * may or may not be triggered. Don't use this method to add/remove audio or video to the call, use
     * {@link addVideo}, {@link remmoveVideo}, {@link addAudio} or {@link removeAudio} instead.
     *
     * @param {String} callId ID of the call that will be changed.
     * @param {Object} newInputDevices New input devices. This object can have 2 properties: audio and video,
     * which contains the device IDs to be used.
     * @returns {Promise} Fulfilled when the operation finishes (including the media renegotiation, if required).
     */
    this.changeInputDevices = UnifyPhoneCallControlSvc.changeInputDevices;

    this.setIncomingVideoStreams = UnifyPhoneCallControlSvc.setIncomingVideoStreams;

    /**
     * Get network quality info.
     *
     * @param {String} [callId] the ID of the call (optional: if not supplied, the active call will be used)
     * @returns {Object} Object containing:
     * - level: Overall network quality level defined in UnifyPhone.Enums.RtpQualityLevel
     * - audio: Object with audio quality parameters and their values: property names in the object are defined in
     * Constants.AudioQualityParam with values defined in UnifyPhone.Enums.RtpQualityLevel
     * - video: Object with video quality parameters and their values: property names in the object are defined in
     * Constants.VideoQualityParam with values defined in UnifyPhone.Enums.RtpQualityLevel
     * - videoInfo: Object containing:
     *   - resolution: Object containing:
     *     - height: current outgoing resolution height (Number)
     *     - width: current outgoing resolution width (Number)
     *   - hdVideo: value defined in UnifyPhone.Enums.VideoResolutionLevel. Applicable only if resolution exactly matches
     * a predefined resolution, otherwise it's undefined.
     *
     * If call could not be found or no quality info is available, null is returned.
     */
    this.getNetworkQualityInfo = UnifyPhoneCallControlSvc.getNetworkQualityInfo;

    this.isPinVideoSupported = UnifyPhoneCallControlSvc.isPinVideoSupported;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.CallControlSvcImpl = CallControlSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/* global RegistrationState */

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var CallState = unifyPhone.Enums.CallState;
  var Utils = unifyPhone.Utils;
  var RtcSessionController = unifyPhone.RtcSessionController;
  var WebRTCAdapter = unifyPhone.WebRTCAdapter;

  ///////////////////////////////////////////////////////////////////////////////////////
  // SoundSvc Implementation
  ///////////////////////////////////////////////////////////////////////////////////////
  var SoundCategory = Object.freeze({
    INCOMING_CALL: 'incomingCall', // This also applies to any notification from remote call
    IN_CALL: 'inCall',
    OUTGOING_CALL: 'outgoingCall',
    SNOOZE_NOTIFICATION: 'snoozeNotification',
    ACCOUNT: 'account',
    ERROR_ALERT: 'errorAlert',
    CALL_NOTIFICATION: 'callNotification'
  });

  // Added for iOS client to understand which mode the sound should be played
  var PlayMode = Object.freeze({
    SOLO_AMBIENT: 'soloAmbient', // Currently played sounds are stopped, new sound is played in solo in ambient
    PLAY_AND_RECORD: 'playAndRecord', // The same as soloAmbient but is intended to be used under VoIP mode
    AMBIENT: 'ambient' // Currently played sounds are not interrupted and new sound is added to ambient
  });

  var ConversationContext = Object.freeze({
    ARCHIVED: 'archived',
    OPEN: 'open'
  });

  var RtcSessionContext = Object.freeze({
    CALL_ACTIVE: 'callActive',
    CALL_INCOMING: 'callIncoming',
    CALL_OUTGOING: 'callOutgoing',
    CALL_REMOTE_ACTIVE: 'callRemoteActive'
  });

  var PresenceContext = Object.freeze({
    DND: 'DND'
  });

  var FocusContext = Object.freeze({
    IN_FOCUS: 'inFocus'
  });

  var DEFAULT_RINGTONE = 'Ringtone_1';
  var SoundType = Object.freeze({
    ACCOUNT_LOGGING_IN: {
      // After successfully logging in to the client, this melody is played as a Welcome Sound
      soundName: 'AccountLoggingIn',
      soundFile: 'sound-account-logging-in',
      mobile: false,
      repeat: false,
      vibrate: false,
      playMode: PlayMode.AMBIENT,
      category: SoundCategory.ACCOUNT
    },
    CALL_CONTROL_HANGING_UP: {
      // User hangs up call and ends the active call
      soundName: 'CallControlHangingUp',
      soundFile: 'sound-call-control-hanging-up',
      mobile: false,
      repeat: false,
      vibrate: false,
      playMode: PlayMode.AMBIENT,
      category: SoundCategory.IN_CALL
    },
    CALL_CONTROL_HUNG_UP: {
      // Contact hangs up call and ends the active call
      soundName: 'CallControlHungUp',
      soundFile: 'sound-call-control-hung-up',
      mobile: true,
      repeat: false,
      vibrate: false,
      playMode: PlayMode.AMBIENT,
      category: SoundCategory.IN_CALL
    },
    CALL_CONTROL_MUTED: {
      // Contact (moderator) mutes the user in a call
      soundName: 'CallControlMuted',
      soundFile: 'sound-call-control-muted',
      mobile: true,
      repeat: false,
      vibrate: false,
      playMode: PlayMode.AMBIENT,
      category: SoundCategory.IN_CALL
    },
    INCOMING_CALL_DURING_CALL: {
      // Contact calls while user is already in an active realtime communication (audio call, video call or screen share)
      soundName: 'IncomingCallDuringCall',
      soundFile: 'sound-calling-incoming-call-during-call',
      mobile: true,
      repeat: false,
      vibrate: true,
      playMode: PlayMode.SOLO_AMBIENT,
      category: SoundCategory.INCOMING_CALL
    },
    INCOMING_CALL_REJECTING: {
      // User rejects call
      soundName: 'IncomingCallRejecting',
      soundFile: 'sound-calling-rejecting-call',
      mobile: true,
      repeat: false,
      vibrate: false,
      playMode: PlayMode.AMBIENT,
      category: SoundCategory.INCOMING_CALL
    },
    INCOMING_CALL_RINGING: {
      // Contact calls user. Audio or video call
      soundName: 'IncomingCallRinging',
      soundFile: 'sound-calling-incoming-call',
      mobile: true,
      repeat: true,
      vibrate: true,
      playMode: PlayMode.SOLO_AMBIENT,
      category: SoundCategory.INCOMING_CALL
    },
    MEDIA_STREAM_STOPPED: {
      // When user in a call and loses the audio connection
      soundName: 'MediaStreamStopped',
      soundFile: 'sound-calling-call-error',
      mobile: true,
      repeat: true,
      vibrate: false,
      playMode: PlayMode.AMBIENT,
      category: SoundCategory.IN_CALL
    },
    OUTGOING_CALL_ACCEPTED: {
      // Contact accepted the call
      soundName: 'OutgoingCallAccepted',
      soundFile: 'sound-calling-call-accepted',
      mobile: false,
      repeat: false,
      vibrate: false,
      playMode: PlayMode.AMBIENT,
      category: SoundCategory.IN_CALL
    },
    OUTGOING_CALL_BUSY: {
      // Contact is busy. Initiated call is stopped, no ringing tone
      soundName: 'OutgoingCallBusy',
      soundFile: 'sound-calling-busy',
      mobile: true,
      repeat: true,
      vibrate: false,
      playMode: PlayMode.PLAY_AND_RECORD,
      category: SoundCategory.IN_CALL
    },
    OUTGOING_CALL_ERROR: {
      // Call cannot be initiated (e.g. no connection). Call is stopped, no ringing tone
      soundName: 'OutgoingCallError',
      soundFile: 'sound-calling-call-error',
      mobile: true,
      repeat: true,
      vibrate: false,
      playMode: PlayMode.AMBIENT,
      category: SoundCategory.IN_CALL
    },
    OUTGOING_CALL_INITIATING: {
      // User clicks the call or video call button to initiate a call
      soundName: 'OutgoingCallInitiating',
      soundFile: 'sound-calling-initiating-call',
      mobile: true,
      repeat: false,
      vibrate: false,
      playMode: PlayMode.PLAY_AND_RECORD,
      category: SoundCategory.OUTGOING_CALL
    },
    OUTGOING_CALL_REJECTED: {
      // Contact rejects the call
      soundName: 'OutgoingCallRejected',
      soundFile: 'sound-calling-call-rejected',
      mobile: true,
      repeat: false,
      vibrate: false,
      playMode: PlayMode.AMBIENT,
      category: SoundCategory.OUTGOING_CALL
    },
    OUTGOING_CALL_RINGBACK_TONE: {
      // Ringing sound while waiting for the called contact to accept or decline
      soundName: 'OutgoingCallRingbackTone',
      soundFile: 'sound-calling-ringback-tone',
      mobile: true,
      repeat: true,
      vibrate: false,
      playMode: PlayMode.PLAY_AND_RECORD,
      category: SoundCategory.OUTGOING_CALL
    },
    PICKUP_NOTIFICATION: {
      // Pickup call
      soundName: 'PickupNotification',
      soundFile: 'sound-calling-incoming-call-during-call',
      mobile: true,
      repeat: false,
      vibrate: true,
      playMode: PlayMode.SOLO_AMBIENT,
      category: SoundCategory.CALL_NOTIFICATION
    },
    PRESENCE_END_SNOOZE_NOTIFICATIONS: {
      // User ends SN and opens up the communication channels again
      soundName: 'PresenceEndSnoozeNotifications',
      soundFile: 'sound-presence-end-snooze-notifications',
      mobile: true,
      repeat: false,
      vibrate: false,
      playMode: PlayMode.AMBIENT,
      category: SoundCategory.SNOOZE_NOTIFICATION
    },
    PRESENCE_START_SNOOZE_NOTIFICATIONS: {
      // User starts snooze notifications. All incoming sounds are redirected to voicemail and all message notifications and sounds are muted. Presence changes to red
      soundName: 'PresenceStartSnoozeNotifications',
      soundFile: 'sound-presence-start-snooze-notifications',
      mobile: false,
      repeat: false,
      vibrate: false,
      playMode: PlayMode.AMBIENT,
      category: SoundCategory.SNOOZE_NOTIFICATION
    }
  });

  var SoundConfig = Object.freeze([
    { selector: { soundCategory: SoundCategory.OUTGOING_CALL }, rule: { play: true } },
    { selector: { soundCategory: SoundCategory.SNOOZE_NOTIFICATION }, rule: { play: true } },

    // In a RTC - All sounds off, except call related sounds
    { selector: { rtcSessionCtxt: RtcSessionContext.CALL_ACTIVE, soundName: SoundType.INCOMING_CALL_DURING_CALL.soundName }, rule: { play: true } },
    { selector: { rtcSessionCtxt: RtcSessionContext.CALL_ACTIVE, soundCategory: SoundCategory.CALL_NOTIFICATION }, rule: { play: false } },
    { selector: { rtcSessionCtxt: RtcSessionContext.CALL_ACTIVE, soundCategory: SoundCategory.INCOMING_CALL }, rule: { play: false } },
    { selector: { rtcSessionCtxt: RtcSessionContext.CALL_ACTIVE, soundCategory: SoundCategory.SNOOZE_NOTIFICATION }, rule: { play: false } },

    // In a RTC - All sounds off on 2nd device, except outgoing call
    { selector: { rtcSessionCtxt: RtcSessionContext.CALL_REMOTE_ACTIVE, soundCategory: SoundCategory.CALL_NOTIFICATION }, rule: { play: false } },
    { selector: { rtcSessionCtxt: RtcSessionContext.CALL_REMOTE_ACTIVE, soundCategory: SoundCategory.IN_CALL }, rule: { play: false } },
    { selector: { rtcSessionCtxt: RtcSessionContext.CALL_REMOTE_ACTIVE, soundCategory: SoundCategory.INCOMING_CALL }, rule: { play: false } },
    { selector: { rtcSessionCtxt: RtcSessionContext.CALL_REMOTE_ACTIVE, soundCategory: SoundCategory.SNOOZE_NOTIFICATION }, rule: { play: false } },

    // Snooze notification mode - All incoming notification sounds off
    { selector: { presenceCtxt: PresenceContext.DND, soundCategory: SoundCategory.CALL_NOTIFICATION }, rule: { play: false } },
    { selector: { presenceCtxt: PresenceContext.DND, soundCategory: SoundCategory.INCOMING_CALL }, rule: { play: false } },

    // Conversation is archived - All incoming notifications sounds off
    { selector: { conversationCtxt: ConversationContext.ARCHIVED, soundCategory: SoundCategory.CALL_NOTIFICATION }, rule: { play: false } }
  ]);

  var Ringtones = Object.freeze([
    'Ringtone_1',
    'Ringtone_2',
    'Ringtone_3',
    'Ringtone_4',
    'Ringtone_5',
    'Ringtone_6',
    'Ringtone_7',
    'Ringtone_8',
    'Ringtone_9',
    'Ringtone_10',
    'Ringtone_11',
    'Ringtone_12',
    'Ringtone_13',
    'Ringtone_14'
  ]);

  var SOUND_URL = 'content/sounds/';
  var RINGTONES_FOLDER = 'ringtones/';
  var MS_TEAMS_STOP_RETRY_TIMEOUT = 1000; // Retry sound stop while in MSTeams in mobile

  // eslint-disable-next-line max-params, max-lines-per-function
  function SoundSvcImpl($rootScope, $window, CallControlSvc, ConversationSvc, LogSvc, NotificationSvc, PubSubSvc, LocalStoreSvc, UserProfileSvc) {
    // NOSONAR
    LogSvc.debug('New Service: SoundSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _currentLoopingSound = null;
    var _currentLoopingSoundType = null;

    var _audioContext; // Keep it set to undefined
    var _audioContextDisabled = true; // Disable AudioContext implementation

    var _audioBuffers = {};
    var _activeSources = 0; // Counter of active sound sources

    // Define file extension to use
    var _browserInfo = Utils.getBrowserInfo();
    var _soundExtension = _browserInfo.safari ? '.mp3' : '.ogg';

    // Audio configuration settings
    var _soundSettings = {};

    var _currentRingtone = null;

    var _isMobile = Utils.isMobile();

    // Create adapter for mobile client
    var AudioAdapter = !_isMobile
      ? Audio
      : function (soundFile) {
          return Audio.createAudio(soundFile);
        };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function setRingtone(ringtone) {
      if (ringtone !== _currentRingtone) {
        LogSvc.info('[SoundSvc]: Setting new ringtone: ', ringtone);
        _currentRingtone = ringtone;
        SoundType.INCOMING_CALL_RINGING.soundFile = RINGTONES_FOLDER + _currentRingtone;
      }
    }

    function initRingtone() {
      var ringtone = LocalStoreSvc.getStringSync(LocalStoreSvc.keys.RINGTONE);
      if (!ringtone || !Ringtones.includes(ringtone)) {
        LogSvc.debug('[SoundSvc]: No or invalid ringtone stored in local store. Using default ringtone.');
        ringtone = DEFAULT_RINGTONE;
      }
      setRingtone(ringtone);
    }

    function suspendAudio(cb) {
      if (_audioContext && _audioContext.state === 'running') {
        _audioContext.suspend().then(function () {
          LogSvc.debug('[SoundSvc]: Suspending audio context');
          cb && cb();
        });
        return;
      }
      cb && cb();
    }

    function resumeAudio(cb) {
      if (_audioContext && _audioContext.state === 'suspended') {
        _audioContext.resume().then(function () {
          LogSvc.debug('[SoundSvc]: Resuming audio context');
          cb && cb();
        });
        return;
      }
      cb && cb();
    }

    function loadSounds() {
      if (_audioContext) {
        LogSvc.debug('[SoundSvc]: Loading sound files...');

        Object.keys(SoundType).forEach(function (sound) {
          var request = new XMLHttpRequest();
          request._soundName = SoundType[sound].soundName;
          request.open('GET', SOUND_URL + SoundType[sound].soundFile + _soundExtension, true);
          request.responseType = 'arraybuffer';
          request.addEventListener('load', bufferSound, false);
          request.send();
        });
      }
    }

    function bufferSound(event) {
      var request = event.target;
      resumeAudio(function () {
        _audioContext.decodeAudioData(
          request.response,
          function onSuccess(buffer) {
            _audioBuffers[request._soundName] = buffer;
            LogSvc.debug('[SoundSvc]: Successfully buffered sound file:', request._soundName);
          },
          function onFailure() {
            LogSvc.warn('[SoundSvc]: Failed to buffer sound file:', request._soundName);
          }
        );
      });
    }

    function resolvePlayRequest(request) {
      LogSvc.debug('[SoundSvc]: sound requested: ', request);
      var rule = { play: true };
      SoundConfig.some(function (config) {
        var match = Object.keys(config.selector).every(function (key) {
          return config.selector[key] === request[key];
        });
        if (match) {
          LogSvc.debug('[SoundSvc]: matched sound configure: ', config);
          rule = config.rule;
          return true;
        }
        return false;
      });
      return rule;
    }

    function conversationContext(conv) {
      if (!conv) {
        return null;
      }
      if (conv.muted) {
        return ConversationContext.ARCHIVED;
      } else if (conv.type === Constants.ConversationType.OPEN) {
        return ConversationContext.OPEN;
      }
      return null;
    }

    function rtcSessionContext(call) {
      if (call.isRemote && call.activeClient) {
        return RtcSessionContext.CALL_REMOTE_ACTIVE;
      }
      var rtcSessionCtxt;

      switch (call.state) {
        case CallState.Initiated:
        case CallState.Connecting:
        case CallState.Delivered:
          rtcSessionCtxt = RtcSessionContext.CALL_OUTGOING;
          break;
        case CallState.Ringing:
        case CallState.Answering:
          rtcSessionCtxt = RtcSessionContext.CALL_INCOMING;
          break;
        default:
          rtcSessionCtxt = RtcSessionContext.CALL_ACTIVE;
      }
      return rtcSessionCtxt;
    }

    function stopAudioElement(audioElement) {
      if (audioElement) {
        if (audioElement.constructor.name === 'AudioBufferSourceNode') {
          audioElement.stop();
        } else {
          audioElement.pause();
          LogSvc.debug('[SoundSvc]: stopAudioElement was called for browser: ', _browserInfo);
          if ($rootScope.isTeamsApp && _browserInfo.subType === 'webview') {
            setTimeout(function () {
              LogSvc.debug(
                '[SoundSvc]: MSTeams Mobile WebView app detected, attempting to stop again current sound, in case of audio reservation by other apps'
              );
              audioElement && audioElement.pause();
            }, MS_TEAMS_STOP_RETRY_TIMEOUT);
          }
        }
      }
    }

    function setAudioElementSrcAndPlay(audioElement, sound) {
      if (audioElement.loop && _currentLoopingSound !== audioElement) {
        // This request is no longer applicable
        return;
      }

      audioElement.src = SOUND_URL + sound.soundFile + _soundExtension;
      var res = audioElement.play();

      if (Utils.isPromise(res)) {
        res
          .then(function () {
            LogSvc.debug('[SoundSvc]: Started playing ', sound.soundName);
            if (audioElement.loop && _currentLoopingSound !== audioElement) {
              // This play took too long to start and has already been stopped
              stopAudioElement(audioElement);
            }
          })
          .catch(function (err) {
            LogSvc.error('[SoundSvc]: Error playing ' + sound.soundName + '. ', err && err.message);
          });
      }
    }

    function playWithAudioContext(sound) {
      LogSvc.info('[SoundSvc] Play using AudioContext. sound: ', sound.soundName);
      resumeAudio(function () {
        var newSource = _audioContext.createBufferSource(); // creates a sound source
        newSource.buffer = _audioBuffers[sound.soundName]; // tell the source which sound to play
        newSource.loop = sound.repeat;
        newSource.connect(_audioContext.destination); // connect the source to the context's destination (the speakers)
        if (newSource.loop) {
          stop();
          _currentLoopingSound = newSource;
          _currentLoopingSoundType = sound.soundName;
        }
        newSource.start(0); // play the source now
        _activeSources++;
        newSource.onended = function () {
          _activeSources--;
          _activeSources === 0 && suspendAudio(); // Clear AudioContext only if no active sound
        };
      });
    }

    function playWithAudioElement(sound) {
      LogSvc.info('[SoundSvc] Play using <audio> element. sound: ', sound.soundName);
      // Use <audio> element to play sound
      // Due to Chrome https://developer.chrome.com/blog/autoplay restriction, we need to manually enable audio, by playing the logging sound muted
      // It seems that the muted for autoplay policy works only for video tag
      var isLoggingIn = sound === SoundType.ACCOUNT_LOGGING_IN;
      var audioElement = document.createElement(isLoggingIn ? 'video' : 'audio'); // create HTML5 audio element
      if (isLoggingIn) {
        audioElement.muted = true;
      }
      audioElement.loop = sound.repeat;
      if (audioElement.loop) {
        stop();
        _currentLoopingSound = audioElement;
        _currentLoopingSoundType = sound.soundName;
      }

      if (
        RtcSessionController.playbackDevices.length &&
        (sound.category === SoundCategory.OUTGOING_CALL ||
          ((sound.category === SoundCategory.IN_CALL || sound.category === SoundCategory.INCOMING_CALL) && CallControlSvc.getActiveCall()))
      ) {
        // These sounds need to played on the current playback device
        WebRTCAdapter.attachSinkIdToAudioElement(audioElement, RtcSessionController.playbackDevices, function (err) {
          if (err) {
            LogSvc.warn('[SoundSvc]: Failed to attach sinkId. ', err);
          }
          // The element src has to be set AFTER the sinkId
          setAudioElementSrcAndPlay(audioElement, sound); // Play sound whether attach sink ID succeeded or not
        });
      } else if (RtcSessionController.ringingDevices.length && sound.category === SoundCategory.INCOMING_CALL) {
        WebRTCAdapter.attachSinkIdToAudioElement(audioElement, RtcSessionController.ringingDevices, function (err) {
          if (err) {
            LogSvc.warn('[SoundSvc]: Failed to attach sinkId. ', err);
          }
          // The element src has to be set AFTER the sinkId
          setAudioElementSrcAndPlay(audioElement, sound); // Play sound whether attach sink ID succeeded or not
        });
      } else {
        setAudioElementSrcAndPlay(audioElement, sound);
      }
    }

    function playWithAudioAPI(sound) {
      LogSvc.info('[SoundSvc] Play using Audio API. sound: ', sound.soundName);
      var newSound = new AudioAdapter(SOUND_URL + sound.soundFile + _soundExtension);
      newSound.loop = sound.repeat;
      newSound.playMode = sound.playMode;
      newSound.vibrate = sound.vibrate;
      if (newSound.loop) {
        stop();
        _currentLoopingSound = newSound;
        _currentLoopingSoundType = sound.soundName;
      }
      newSound.play();
    }

    function play(sound, options) {
      if (!sound) {
        // Invalid input
        return;
      }

      if (!sound.mobile && _isMobile) {
        // Ignore sound for mobile clients
        return;
      }

      if (_currentLoopingSoundType) {
        if (sound.soundName === _currentLoopingSoundType) {
          // It is already playing this sound, just return
          return;
        }
        if (sound.repeat) {
          // Only a looping sound can interrupt another looping sound
          stop();
        } else if (_isMobile) {
          // Don't play multiple sounds in mobile clients
          return;
        }
      }

      options = options || { overrideSnooze: false };

      // Only check for conditions and settings if playUnconditionally flag is not set
      if (!options.playUnconditionally) {
        if (!isSoundEnabled(sound)) {
          LogSvc.info('[SoundSvc] Sound suppressed due to user settings. sound: ', sound.soundName);
          return;
        }

        var playRequest = options.playCtxt || {};
        playRequest.soundCategory = sound.category;

        if ($rootScope.localUser.userPresenceState.state === Constants.PresenceState.DND && !options.overrideSnooze) {
          // User has snooze enabled. Block all audio notifications.
          playRequest.presenceCtxt = PresenceContext.DND;
        }

        var callContext = CallControlSvc.getActiveCall() || CallControlSvc.getActiveRemoteCall()[0] || CallControlSvc.getIncomingCall();

        if (callContext) {
          playRequest.rtcSessionCtxt = rtcSessionContext(callContext);
        }

        if (NotificationSvc.isAppInFocus()) {
          playRequest.focusCtxt = FocusContext.IN_FOCUS;
        }

        playRequest.soundName = sound.soundName;

        if (!resolvePlayRequest(playRequest).play) {
          return;
        }
      }

      if (_audioContext && _audioBuffers[sound.soundName]) {
        playWithAudioContext(sound);
      } else if (!_isMobile) {
        playWithAudioElement(sound);
      } else {
        playWithAudioAPI(sound);
      }
    }

    function stop(sound) {
      if (_currentLoopingSound) {
        if (sound && sound.soundName !== _currentLoopingSoundType) {
          return; // We're currently playing another sound. Ignore this stop request
        }
        LogSvc.info('[SoundSvc] stop(): type: ', _currentLoopingSoundType);
        stopAudioElement(_currentLoopingSound);
        _currentLoopingSound = null;
        _currentLoopingSoundType = null;
      }
    }

    function getAudioSetting(key) {
      var setting = UserProfileSvc.getSetting(key);
      if (setting && setting.booleanValue !== undefined) {
        return setting.booleanValue;
      }
      // Return true by default
      return true;
    }

    function updateSettings() {
      _soundSettings = {
        rtc: getAudioSetting(Constants.UserSettingKey.PLAY_SOUND_RTC),
        system: getAudioSetting(Constants.UserSettingKey.PLAY_SYSTEM_SOUNDS),
        pickupNotification: getAudioSetting(Constants.UserSettingKey.PLAY_PICKUP_SOUND)
      };

      LogSvc.info('[SoundSvc]: Updated sound settings: ', _soundSettings);
    }

    function isSoundEnabled(sound) {
      // Current user settings are only applicable for web/DA
      if (_isMobile) {
        return true;
      }
      switch (sound) {
        case SoundType.INCOMING_CALL_DURING_CALL:
        case SoundType.INCOMING_CALL_RINGING:
          return _soundSettings.rtc;
        case SoundType.PICKUP_NOTIFICATION:
          return _soundSettings.pickupNotification;
        default:
          return _soundSettings.system;
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Sound Settings
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/localUser/init', function () {
      LogSvc.debug('[SoundSvc]: Received /localUser/init event');
      updateSettings();
      initRingtone();
    });

    PubSubSvc.subscribe('/user/settings/update', function () {
      LogSvc.debug('[SoundSvc]: Received /user/settings/update event');
      updateSettings();
    });

    PubSubSvc.subscribe('/feature/state/initialized', function () {
      LogSvc.debug('[SoundSvc]: Received /feature/state/initialized');
      initRingtone();
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Account Sounds
    ///////////////////////////////////////////////////////////////////////////////////////
    !$rootScope.isMobileApp &&
      PubSubSvc.subscribe('/registration/state', function (state) {
        if (state === RegistrationState.Registered) {
          LogSvc.info('[SoundSvc]: Received /registration/state event');
          play(SoundType.ACCOUNT_LOGGING_IN);
        }
      });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Call Sounds
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/call/state', function (call) {
      if (!call || call.isRemote) {
        return;
      }
      LogSvc.debug('[SoundSvc]: Received /call/state event. state =', call.state.name);
      var option = { playCtxt: { conversationCtxt: conversationContext(ConversationSvc.getConversationFromCache(call.convId)) } };

      if (call.state !== CallState.Delivered) {
        stop(SoundType.OUTGOING_CALL_RINGBACK_TONE);
      }

      switch (call.state) {
        case CallState.Failed:
          play(SoundType.OUTGOING_CALL_ERROR, option);
          break;
        case CallState.Busy:
        case CallState.NotAnswered:
          play(SoundType.OUTGOING_CALL_BUSY, option);
          break;
        case CallState.Declined:
          play(SoundType.OUTGOING_CALL_REJECTED, option);
          break;
        case CallState.Answering:
          stop();
          break;
        default:
          break;
      }
    });

    PubSubSvc.subscribe('/call/outgoing', function (call) {
      LogSvc.debug('[SoundSvc]: Received /call/outgoing event');
      if (!call) {
        return;
      }
      var option = { playCtxt: { conversationCtxt: conversationContext(ConversationSvc.getConversationFromCache(call.convId)) } };

      play(SoundType.OUTGOING_CALL_INITIATING, option);
    });

    PubSubSvc.subscribe('/call/incoming', function (call) {
      if (!call) {
        return;
      }

      var option = { playCtxt: { conversationCtxt: conversationContext(ConversationSvc.getConversationFromCache(call.convId)) } };

      LogSvc.debug('[SoundSvc]: Received /call/incoming event');
      if (CallControlSvc.getActiveCall()) {
        play(SoundType.INCOMING_CALL_DURING_CALL, option);
      } else {
        play(SoundType.INCOMING_CALL_RINGING, option);
      }
    });

    PubSubSvc.subscribe('/call/participant/joined', function (call) {
      if (!call) {
        return;
      }
      LogSvc.debug('[SoundSvc]: Received /call/participant/joined event');
      stop();
    });

    PubSubSvc.subscribe('/call/declining', function (call) {
      if (!call) {
        return;
      }
      LogSvc.debug('[SoundSvc]: Received /call/declining event');
      stop();
      var option = { playCtxt: { conversationCtxt: conversationContext(ConversationSvc.getConversationFromCache(call.convId)) } };
      play(SoundType.INCOMING_CALL_REJECTING, option);
    });

    PubSubSvc.subscribe('/call/ended', function (call) {
      if (call && !call.isRemote) {
        LogSvc.debug('[SoundSvc]: Received /call/ended event');
        stop();
      }
    });

    PubSubSvc.subscribe('/call/terminating', function (call) {
      if (!call || call.isRemote) {
        return;
      }
      // The local user is terminating the call
      LogSvc.debug('[SoundSvc]: Received /call/terminating event');
      stop();
      var option = { playCtxt: { conversationCtxt: conversationContext(ConversationSvc.getConversationFromCache(call.convId)) } };
      play(SoundType.CALL_CONTROL_HANGING_UP, option);
    });

    PubSubSvc.subscribe('/call/terminated', function (call /*, cause*/) {
      if (!call || call.isRemote) {
        return;
      }
      LogSvc.debug('[SoundSvc]: Received /call/terminated event');
      stop();
      var option = { playCtxt: { conversationCtxt: conversationContext(ConversationSvc.getConversationFromCache(call.convId)) } };

      if (call.isDirect) {
        play(SoundType.CALL_CONTROL_HUNG_UP, option);
      }
    });

    PubSubSvc.subscribe('/call/ringingTone/stop', function () {
      LogSvc.debug('[SoundSvc]: Received /call/ringingTone/stop event');
      stop();
    });

    PubSubSvc.subscribe('/sound/call/alerting/started', function (call) {
      if (!call) {
        return;
      }
      LogSvc.debug('[SoundSvc]: Received /sound/call/alerting/started event');
      // Set this flag so it gets played regardless of any configuration or context
      var option = { playUnconditionally: true };
      play(SoundType.OUTGOING_CALL_RINGBACK_TONE, option);
    });

    PubSubSvc.subscribe('/sound/call/alerting/stopped', function () {
      LogSvc.debug('[SoundSvc]: Received /sound/call/alerting/stopped event');
      stop(SoundType.OUTGOING_CALL_RINGBACK_TONE);
    });

    PubSubSvc.subscribe('/call/pickupNotification', function (call) {
      if (!call) {
        return;
      }
      LogSvc.debug('[SoundSvc]: Received /call/pickupNotification event');
      var option = { playCtxt: { conversationCtxt: conversationContext(ConversationSvc.getConversationFromCache(call.convId)) } };
      play(SoundType.PICKUP_NOTIFICATION, option);
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Call Control Sounds
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/call/localUser/muted', function (callId, muted, locallyMuted) {
      // User has been muted by someone else
      LogSvc.debug('[SoundSvc]: Received /call/localUser/muted event');
      if (muted && !locallyMuted) {
        play(SoundType.CALL_CONTROL_MUTED);
      }
    });

    PubSubSvc.subscribe('/call/rtp/iceConnectionState', function (callId, state) {
      LogSvc.debug('[SoundSvc]: Received /call/rtp/iceConnectionState event. state:', state);
      if (state === 'disconnected') {
        var option = { playUnconditionally: true };
        play(SoundType.MEDIA_STREAM_STOPPED, option);
      } else {
        stop(SoundType.MEDIA_STREAM_STOPPED);
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Presence Sounds
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/user/snooze/start', function () {
      LogSvc.debug('[SoundSvc]: Received /user/snooze/start event');
      //play(SoundType.PRESENCE_START_SNOOZE_NOTIFICATIONS);
    });

    PubSubSvc.subscribe('/user/snooze/end', function () {
      LogSvc.debug('[SoundSvc]: Received /user/snooze/end event');
      //play(SoundType.PRESENCE_END_SNOOZE_NOTIFICATIONS);
    });

    PubSubSvc.subscribe('/user/snooze/resume', function () {
      LogSvc.debug('[SoundSvc]: Received /user/snooze/resume event');
      //play(SoundType.PRESENCE_END_SNOOZE_NOTIFICATIONS);
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    Object.defineProperties(this, {
      ringtone: {
        get: function () {
          return _currentRingtone;
        },
        set: function (ringtone) {
          if (!ringtone || !Ringtones.includes(ringtone)) {
            LogSvc.warn('[SoundSvc]: Setting invalid ringtone. Ringtone: ', ringtone || 'no ringtone');
            return;
          }
          setRingtone(ringtone);
          LocalStoreSvc.setString(LocalStoreSvc.keys.RINGTONE, ringtone);
        },
        enumerable: true,
        configurable: false
      }
    });

    this.init = function () {
      if (_audioContext !== undefined) {
        LogSvc.warn('[SoundSvc] init(): SoundSvc has already been initialized');
        return;
      }

      _audioContext = null;
      try {
        // Firefox can use Web Audio API, but the perfomance problems still exists.
        // Use <audio> element instead of the Web Audio API for playing sounds in FF,
        // to prevent sound interruption when DOM reflows (e.g. when incoming call appears
        // and user switch between conversations/move to details tab/other user interaction).
        if (!_audioContextDisabled && $window.AudioContext && $rootScope.browser.chrome) {
          _audioContext = new $window.AudioContext();
          _audioContext.suspend();
          LogSvc.info('[SoundSvc] New AudioContext instance created and suspended until needed');
        }
      } catch (e) {
        LogSvc.warn('[SoundSvc] Failed to create AudioContext instance');
      }

      if (_audioContext) {
        loadSounds();
      }
    };

    this.buildSoundFileURL = function (sound) {
      if (!sound || !sound.soundFile) {
        return '';
      }
      return SOUND_URL + sound.soundFile + _soundExtension;
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.SoundSvcImpl = SoundSvcImpl;
  unifyPhone.SoundType = SoundType;
  unifyPhone.Ringtones = Ringtones;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;

  // eslint-disable-next-line max-params, max-lines-per-function
  function ManagedDevicesSvcImpl($q, $rootScope, LogSvc) {
    // NOSONAR
    LogSvc.debug('New Service: ManagedDevicesSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _clientApiHandler = ClientApiHandler.getInstance();

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    /**
     * This function revokes all managed devices except the one triggering it.
     *
     * @function
     * @returns {Promise}
     */
    this.revokeAllManagedDevices = function () {
      var deferred = $q.defer();
      _clientApiHandler.revokeAllManagedDevices(function (err) {
        $rootScope.$apply(function () {
          if (err) {
            LogSvc.error('[ManagedDevicesSvc]: Failed to revoke all managed devices. ', err);
            deferred.reject(err);
            return;
          }
          deferred.resolve();
        });
      });

      return deferred.promise;
    };

    /**
     * This function returns a list of all security token information (managed devices).
     *
     * @function
     * @returns {Promise}
     */
    this.getSecurityTokenInfo = function () {
      LogSvc.debug('[ManagedDevicesSvc]: Get connected apps');

      var deferred = $q.defer();
      _clientApiHandler.getSecurityTokenInfo(function (error, info) {
        $rootScope.$apply(function () {
          if (error) {
            LogSvc.error('[ManagedDevicesSvc]: Error retrieving token infos. ', error);
            deferred.reject(error);
            return;
          }
          info = info || [];
          LogSvc.info('[ManagedDevicesSvc]: Retrieved ' + info.length + ' token infos.');

          info.sort(function (a, b) {
            return (+b.currentSession).toString() + b.lastLoginTime - ((+a.currentSession).toString() + a.lastLoginTime);
          });
          deferred.resolve(info);
        });
      });
      return deferred.promise;
    };

    /**
     * Revokes acces to a security token
     * @function
     * @param {String} tokenIdHash The security token ID hash to be revoked.
     * @returns {Promise}
     */
    this.revokeSecurityToken = function (tokenIdHash) {
      LogSvc.debug('[ManagedDevicesSvc]: Revoke security token:', tokenIdHash);
      var deferred = $q.defer();
      _clientApiHandler.revokeSecurityToken(tokenIdHash, function (error) {
        $rootScope.$apply(function () {
          if (error) {
            LogSvc.error('[ManagedDevicesSvc] Error revoking security token. ', error);
            deferred.reject(error);
            return;
          }
          deferred.resolve();
        });
      });
      return deferred.promise;
    };

    return this;
  }

  // Exports
  unifyPhone.ManagedDevicesSvcImpl = ManagedDevicesSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Enums = unifyPhone.Enums;

  ///////////////////////////////////////////////////////////////////////////////////////
  // NavigationSvc Implementation
  // Provides navigation API to be used by controllers only.
  ///////////////////////////////////////////////////////////////////////////////////////

  // eslint-disable-next-line max-params, max-lines-per-function
  function NavigationSvcImpl($location, $route, LogSvc, PubSubSvc, ConversationSvc, UISvc, CallControlSvc) {
    // NOSONAR
    LogSvc.debug('New Service: NavigationSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _pendingNavigateToCall = false;
    var _self = this;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function go(path, urlParams) {
      LogSvc.info('[NavigationSvc]: Route request to ', path);

      // Remove search parameters such as ?item
      $location.url($location.path());

      path = path.replace('#', '');
      if ($location.path() === path) {
        $route.reload();
      } else {
        $location.path(path);
      }

      // Add custom search parameters to url
      if (urlParams) {
        Object.keys(urlParams).forEach(function (key) {
          $location.search(key, urlParams[key]);
        });
      }

      // Clear the search when routing to a draft
      if (path === '/draft') {
        LogSvc.debug('[NavigationSvc]: Clear the search');
        UISvc.clearSearch();
      }
    }

    function goToConversation(convId, urlParams) {
      LogSvc.debug('[NavigationSvc]: Go to conversation: ', convId);
      if (convId.startsWith('draft_')) {
        go('/draft/' + convId, urlParams);
      } else {
        var c = ConversationSvc.getConversationFromCache(convId);
        if (c && c.isTelephony) {
          go('/phone/' + convId, urlParams);
        } else {
          go('/conversation/' + convId, urlParams);
        }
      }
    }

    function navigateToPhoneConversation(convId) {
      if (!convId) {
        return;
      }
      _pendingNavigateToCall = false;
      var path = $location.path();
      var pathSplit = path.split('/');
      if (pathSplit.length >= 2 && pathSplit[1] === 'phone') {
        // Conversation already selected, no need to navigate
        return;
      }
      LogSvc.debug('[NavigationSvc]: Navigating to phone conversation');
      _self.selectConversation({
        route: 'phone',
        convId: convId
      });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    function onCallState(call) {
      if (!call) {
        return;
      }
      if (
        call.isTelephonyCall &&
        _pendingNavigateToCall &&
        (call.state === Enums.CallState.Initiated || call.getCstaState() === Enums.CstaCallState.Delivered)
      ) {
        navigateToPhoneConversation(call.convId);
      }
    }
    PubSubSvc.subscribe('/call/state', onCallState);

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    /**
     * Navigate to the specified route.
     *
     * @param {Object} routeInfo Contains attributes 'route' and 'convId'.
     * @param {Hashtable} urlParams URL parameters with key/value pairs.
     */
    this.selectConversation = function (routeInfo, urlParams) {
      LogSvc.debug('[NavigationSvc]: Select conversation: ', routeInfo);
      if (!routeInfo.route) {
        if (routeInfo.convId) {
          goToConversation(routeInfo.convId, urlParams);
        }
        return;
      }

      var convId = routeInfo.convId || '';
      var route = routeInfo.route;
      if (route === 'conversation' && convId.startsWith('draft_')) {
        route = 'draft';
      }
      var url = '/' + route + '/' + convId;
      if (routeInfo.guestToken) {
        url += '/' + routeInfo.guestToken;
      }
      go(url, urlParams);
    };

    /**
     * Navigate to the root URL.
     */
    this.goToRoot = function () {
      LogSvc.debug('[NavigationSvc]: Go to root');
      go('/');
    };

    /**
     * Navigate to phone calls conversation and dial the number.
     *
     * @param {String} Number to dial.
     */
    this.dialNumber = function (number, userId) {
      if (number) {
        LogSvc.debug('[NavigationSvc]: Dial number ', number);
        LogSvc.debug('[NavigationSvc]: Publish /shortcut/newPhoneCall event');
        PubSubSvc.publish('/shortcut/newPhoneCall', [number, null, userId]);
      }
    };

    /**
     * Navigate to phone calls conversation and dial the user by name.
     *
     * @param {String} Number to dial.
     */
    this.dialByName = function (name, userId) {
      if (name) {
        LogSvc.debug('[NavigationSvc]: Dial by name - ', name);
        LogSvc.debug('[NavigationSvc]: Publish /shortcut/newPhoneCall event');
        PubSubSvc.publish('/shortcut/newPhoneCall', [null, name, userId]);
      }
    };

    /**
     * Navigate to the direct conversation with the given user or to an empty draft in
     * case we still don't have a direct conversation.
     *
     * @param {String} The user Id.
     */
    this.goToDirectConversation = function (userId) {
      LogSvc.debug('[NavigationSvc]: Go to user page for userId ', userId);
      _self.go('/user/' + userId);
    };

    /**
     * Navigate to the specified location path.
     *
     * @param {String} path URL path to navigate to. E.g. 'forgotPassword'
     * @param {Hashtable} urlParams URL parameters with key/value pairs.
     */
    this.go = function (path, urlParams) {
      go(path, urlParams);
    };

    /**
     * Navigate to the specified item in the corresponding conversation or Space.
     *
     * @param {Object} item Conversation or Space item object.
     */
    this.goToItem = function (item) {
      if (!item) {
        return;
      }
      // Update the URL without reloading route.
      // Note: The second parameter in $location.path (i.e. skipReload) is a UnifyPhone extension.
      if (item.convId) {
        LogSvc.debug('[NavigationSvc]: Go to item ' + item.itemId + ' in conv ' + item.convId);
        $location.path('conversation/' + item.convId, false).search('item', item.itemId);
      } else {
        LogSvc.warn('[NavigationSvc]: goToItem invoked with invalid item. ', item);
      }
    };

    /**
     * Navigate to an empty conversation draft.
     */
    this.goToEmptyDraft = function (draftType, userId) {
      LogSvc.debug('[NavigationSvc]: Go to empty draft with type ', draftType);
      var urlParams = {
        draftType: draftType
      };
      if (userId) {
        urlParams.user = userId;
      }
      go('/draft', urlParams);
    };

    /**
     * Check that conversation with specified id is open.
     *
     * @param {String} convId conversation id to check.
     * @return {Boolean}
     */
    this.isConversationSelected = function (convId) {
      if (!convId) {
        return false;
      }
      return $location.path().includes('conversation/' + convId);
    };

    /**
     * Activate/deactivate automatic route to phone conversation when the next phone call's
     * state goes to Initiated (local) or Delivered (remote)
     *
     * @param {Boolean} route true to activate automatic route, false otherwise
     */
    this.setRouteToPhone = function (route) {
      if (route) {
        var calls = CallControlSvc.getPhoneCalls();
        if (calls.length) {
          // We have a phone call visible, so we can navigate right away
          navigateToPhoneConversation(calls[0].convId);
          return;
        }
      }
      _pendingNavigateToCall = !!route;
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.NavigationSvcImpl = NavigationSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global Plantronics*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var CstaCallState = unifyPhone.Enums.CstaCallState;
  var Constants = unifyPhone.Constants;

  // eslint-disable-next-line max-params, max-lines-per-function
  function PlantronicsControlSvcImpl($rootScope, $interval, $timeout, $q, LogSvc, PubSubSvc, CallControlSvc, LocalStoreSvc) {
    // NOSONAR
    LogSvc.debug('New Service: PlantronicsControlSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var DEFAULT_SPOKES_URL = 'https://127.0.0.1:32018/Spokes';
    var PLUGIN_NAME = 'UnifyPhone';
    var POLLING_INTERVAL = 1000; // 1 second
    var HEADSET_TYPE = 'plantronics';
    var MAX_NUM_FAILED_REQ = 3; // Only consider disconnected if 3 consecutive requests failed (we don't monitor spacing between failed requests)

    var POLLING_TIMEOUT_CONNECTED = 600000; // 10 minutes
    var MIN_POLLING_TIMEOUT_DISCONNECTED = 30000; // 30 seconds
    var MAX_POLLING_TIMEOUT_DISCONNECTED = 300000; // 5 minutes

    var _spokes = null;
    var _connected = false;
    var _connectionError = false;
    var _alertingCall = null; // The alerting call object
    var _activeCall = null; // Active call object
    var _callIdHash = {}; // A hash to map from UnifyPhone Call Id to SpokesCallId
    var _nextSpokesCallId = 1;
    var _pollingInterval = null;
    var _failedReqCounter = 0;
    var _connectionPollingTimer = null;
    var _connectionRetries = 0;
    var _headsetMuted = null; // Keep a local muted status of the headset to avoid race conditions
    var _integrationEnabled = false;
    var _delayedMute = {};
    var _that = this;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function init() {
      _integrationEnabled = getEnabledIntegrationTypes().indexOf(HEADSET_TYPE) >= 0;
      if (_integrationEnabled) {
        _that.connect(true).catch(function () {
          LogSvc.warn('[PlantronicsControlSvc]: Failed to connect. Start connection polling to keep retrying.');
          startConnectionPolling();
        });
      }
    }

    function getEnabledIntegrationTypes() {
      return LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.HEADSET_TYPES_ENABLED) || [];
    }

    function saveIntegration(enabled) {
      var types = getEnabledIntegrationTypes();
      var index = types.indexOf(HEADSET_TYPE);
      if (index >= 0) {
        if (!enabled) {
          types.splice(index, 1);
        }
      } else if (enabled) {
        types.push(HEADSET_TYPE);
      } else {
        return;
      }
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.HEADSET_TYPES_ENABLED, types);
      _integrationEnabled = enabled;
    }

    function unregisterPlugin() {
      return new $q(function (resolve) {
        if (!_connected) {
          LogSvc.debug('[PlantronicsControlSvc]: UnifyPhone plugin is not registered');
          resolve();
          return;
        }
        clearAllDeviceCalls();
        stopEventPolling();

        LogSvc.debug('[PlantronicsControlSvc]: Unregister UnifyPhone plugin');
        _spokes.Plugin.unRegister(PLUGIN_NAME, function (res) {
          if (res.isError) {
            LogSvc.warn('[PlantronicsControlSvc]: Failed to unregister UnifyPhone plugin. ', res.Err.Description);
          } else {
            LogSvc.info('[PlantronicsControlSvc]: Unregistered UnifyPhone plugin');
          }
          // Always resolve the promise
          resolve();
        });

        // Change the state immediately
        _connected = false;
      });
    }

    function registerPlugin() {
      return new $q(function (resolve, reject) {
        if (_connected) {
          LogSvc.debug('[PlantronicsControlSvc]: UnifyPhone plugin is already registered');
          resolve();
          return;
        }

        LogSvc.debug('[PlantronicsControlSvc]: Register UnifyPhone plugin');

        _connectionError = false;
        _spokes.Plugin.register(PLUGIN_NAME, function (registerRes) {
          if (registerRes.isError && registerRes.Err.Description !== 'Plugin exists') {
            $rootScope.$apply(function () {
              _connectionError = true;
              LogSvc.warn('[PlantronicsControlSvc]: Failed to register plugin. ', registerRes.Err.Description);
              reject(registerRes.Err.Description);
            });
            return;
          }
          LogSvc.debug('[PlantronicsControlSvc]: UnifyPhone plugin is registered. Make sure it is active.');
          _connected = true;

          // Make sure the plug-in is active
          _spokes.Plugin.isActive(PLUGIN_NAME, true, function (isActiveRes) {
            if (isActiveRes.isError || !isActiveRes.Result) {
              $rootScope.$apply(function () {
                LogSvc.warn('[PlantronicsControlSvc]: UnifyPhone plugin is not active. Unregister it.');
                _connectionError = true;
                unregisterPlugin().then(function () {
                  reject(isActiveRes.isError ? isActiveRes.Err.Description : 'Plugin is not active');
                });
              });
              return;
            }

            LogSvc.debug('[PlantronicsControlSvc]: Set UnifyPhone as default softphone.');
            _spokes.UserPreference.setDefaultSoftphone(PLUGIN_NAME, function (setDefaultRes) {
              $rootScope.$apply(function () {
                if (setDefaultRes.isError) {
                  LogSvc.warn('[PlantronicsControlSvc]: Could not set UnifyPhone as preferred softphone. Unregister it.');
                  _connectionError = true;
                  unregisterPlugin().then(function () {
                    reject(setDefaultRes.Err.Description);
                  });
                } else {
                  LogSvc.info('[PlantronicsControlSvc]: Successfully registered UnifyPhone plugin');
                  _connectionError = false;
                  _connected = true;
                  _connectionRetries = 0;
                  resolve();
                }
              });
            });
          });
        });
      });
    }

    function clearAlertingCallId() {
      if (_alertingCall) {
        if (!_alertingCall.sameAs(_activeCall)) {
          delete _callIdHash[_alertingCall.callId];
        }
        _alertingCall = null;
      }
    }

    function clearActiveCallId() {
      if (_activeCall) {
        if (!_alertingCall || !_activeCall.sameAs(_alertingCall)) {
          delete _callIdHash[_activeCall.callId];
        }
        _activeCall = null;
      }
    }

    function setAlertingCall(call) {
      clearAlertingCallId();
      _alertingCall = call;
    }

    function setActiveCall(call) {
      LogSvc.debug('[PlantronicsControlSvc]: Set active call to ', call && call.callId);
      _activeCall = call;
    }

    function processCallEvents(callEvents) {
      if (!callEvents || !callEvents.length) {
        return;
      }

      LogSvc.debug('[PlantronicsControlSvc]: Received call events: ', callEvents);

      // Get the Spokes call Ids
      var alertingId = _alertingCall && _callIdHash[_alertingCall.callId];
      var activeId = _activeCall && _callIdHash[_activeCall.callId];

      // Only process the last mute (ON or OFF) event for the active call
      var hasMuteEvt = false;
      for (var i = callEvents.length - 1; i >= 0; i--) {
        var evtId = callEvents[i].Action;
        var callId = callEvents[i].CallId.Id;
        if ((evtId === Plantronics.SessionCallState.MuteON || evtId === Plantronics.SessionCallState.MuteOFF) && activeId === callId) {
          if (hasMuteEvt) {
            callEvents.splice(i, 1); // There's another mute event already in the queue, so discard it
          } else {
            hasMuteEvt = true;
          }
        }
      }

      callEvents.forEach(function (evt) {
        var callStateName = Plantronics.SessionCallState.Lookup[evt.Action] || '(' + evt.Action + ')';
        var id = '' + (evt.CallId && evt.CallId.Id);

        LogSvc.info('[PlantronicsControlSvc]: Processing call event with action ' + callStateName + ' for callId = ' + id);

        var isAlerting = alertingId && alertingId.Id === id;
        var isActive = activeId && activeId.Id === id;

        switch (evt.Action) {
          case Plantronics.SessionCallState.AcceptCall:
            if (isAlerting) {
              LogSvc.info('[PlantronicsControlSvc]: Answer alerting call');
              CallControlSvc.answerCall(_alertingCall.callId, { audio: true });
            }
            break;
          case Plantronics.SessionCallState.TerminateCall:
            if (isActive) {
              LogSvc.info('[PlantronicsControlSvc]: End active call');
              _activeCall && CallControlSvc.endCall(_activeCall.callId);
            }
            break;
          case Plantronics.SessionCallState.RejectCall:
            if (isAlerting) {
              LogSvc.info('[PlantronicsControlSvc]: Decline alerting call');
              CallControlSvc.endCall(_alertingCall.callId);
            }
            break;
          case Plantronics.SessionCallState.MuteON:
            if (isActive) {
              if (!_headsetMuted) {
                LogSvc.info('[PlantronicsControlSvc]: Mute active call');
                _headsetMuted = true;
                _activeCall && CallControlSvc.mute(_activeCall.callId);
              } else {
                LogSvc.debug('[PlantronicsControlSvc]: MuteON call event discarded');
              }
            }
            break;
          case Plantronics.SessionCallState.MuteOFF:
            if (isActive) {
              if (_headsetMuted) {
                LogSvc.info('[PlantronicsControlSvc]: Unmute active call');
                _headsetMuted = false;
                _activeCall && CallControlSvc.unmute(_activeCall.callId);
              } else {
                LogSvc.debug('[PlantronicsControlSvc]: MuteOFF call event discarded');
              }
            }
            break;
        }
      });
    }

    function stopEventPolling() {
      $interval.cancel(_pollingInterval);
      _pollingInterval = null;
    }

    function startEventPolling() {
      if (!_pollingInterval) {
        _pollingInterval = $interval(
          function () {
            if (!_alertingCall && !_activeCall) {
              LogSvc.debug('[PlantronicsControlSvc]: There are no local calls. Stop polling.');
              stopEventPolling();
              return;
            }

            _spokes.Plugin.callEvents(
              PLUGIN_NAME,
              function (res) {
                if (res.isError) {
                  LogSvc.warn('[PlantronicsControlSvc]: Failed to poll call events. ', res.Err.Description);
                  _failedReqCounter++;
                  if (_failedReqCounter >= MAX_NUM_FAILED_REQ) {
                    LogSvc.warn('[PlantronicsControlSvc]: Multiple requests failed in a row. Change state to disconnected.');
                    _connectionError = true;
                    unregisterPlugin().then(function () {
                      cancelConnectionPolling();
                      startConnectionPolling();
                    });
                  }
                  return;
                }
                _failedReqCounter = 0;
                processCallEvents(res.Result);
              },
              true
            );
          },
          POLLING_INTERVAL,
          0,
          false
        );
      }
    }

    function startConnectionPolling() {
      if (_connectionPollingTimer) {
        // Timer already running
        return;
      }
      var timeout;
      if (_connected) {
        _connectionRetries = 0;
        timeout = _failedReqCounter === 0 ? POLLING_TIMEOUT_CONNECTED : MIN_POLLING_TIMEOUT_DISCONNECTED;
      } else {
        _connectionRetries++;
        timeout = Math.min(MAX_POLLING_TIMEOUT_DISCONNECTED, _connectionRetries * MIN_POLLING_TIMEOUT_DISCONNECTED);
      }

      _connectionPollingTimer = $timeout(function () {
        _connectionPollingTimer = null;
        if (_connected) {
          LogSvc.debug('[PlantronicsControlSvc]: Check connection status with Plantronics Hub.');

          _that
            .getActiveDevice()
            .then(function () {
              _failedReqCounter = 0;
            })
            .catch(function () {
              _failedReqCounter++;
              if (_failedReqCounter >= MAX_NUM_FAILED_REQ) {
                // Lost connection with Plantronics Hub
                LogSvc.warn('[PlantronicsControlSvc]: Multiple requests failed in a row. Change state to disconnected.');
                _connectionError = true;
                unregisterPlugin();
              } else {
                LogSvc.warn('[PlantronicsControlSvc]: Failed to get active device. Try again in a few seconds.');
              }
            })
            .then(startConnectionPolling);
        } else {
          _that.connect().catch(startConnectionPolling);
        }
      }, timeout);
    }

    function cancelConnectionPolling() {
      _failedReqCounter = 0;
      if (_connectionPollingTimer) {
        $timeout.cancel(_connectionPollingTimer);
        _connectionPollingTimer = null;
      }
    }

    function getSpokesCallId(unifyPhoneCallId) {
      var spokesCallId = null;
      if (unifyPhoneCallId) {
        spokesCallId = _callIdHash[unifyPhoneCallId];
        if (!spokesCallId) {
          var id = '' + _nextSpokesCallId;
          _nextSpokesCallId++;

          LogSvc.debug(
            '[PlantronicsControlSvc]: Created new spokesCallId: ' + id + ' (' + unifyPhoneCallId + '). Current number of spokes calls: ',
            Object.keys(_callIdHash).length
          );
          spokesCallId = new Plantronics.SpokesCallId({ Id: id });
          _callIdHash[unifyPhoneCallId] = spokesCallId;
        }
      }
      return spokesCallId;
    }

    function incomingCall(call) {
      if ($rootScope.localUser.userPresenceState.state !== Constants.PresenceState.DND && !call.doNotRingOnHeadset) {
        var spokesCallId = getSpokesCallId(call.callId);
        var contact = new Plantronics.SpokesContact({ Name: encodeURIComponent(call.convTitle) });

        LogSvc.debug('[PlantronicsControlSvc]: Send incoming call notification to device');
        _spokes.Plugin.incomingCall(PLUGIN_NAME, spokesCallId, contact, 'Unknown', 'ToHeadset', function (res) {
          LogSvc.debug('[PlantronicsControlSvc]: incomingCall response. Result: ', res.Result);
        });
      } else {
        LogSvc.debug('[PlantronicsControlSvc]: Do not ring this call. Not sending incoming call notification to device');
      }
    }

    function answerCall(call) {
      var spokesCallId = _callIdHash[call.callId];
      if (spokesCallId) {
        // We should send an answer only for an existing call on the device (presumably in incoming state)
        LogSvc.debug('[PlantronicsControlSvc]: Send answer call notification to device');
        _headsetMuted = null;
        _spokes.Plugin.answerCall(PLUGIN_NAME, spokesCallId, function (res) {
          LogSvc.debug('[PlantronicsControlSvc]: answerCall response. Result: ', res.Result);
          if (call.sameAs(_activeCall)) {
            muteCall(call.isMuted());
          }
        });
      } else {
        // Since we couldn't find an existing call, treat it as an outgoing call
        outgoingCall(call);
      }
    }

    function outgoingCall(call) {
      var spokesCallId = _callIdHash[call.callId];
      if (spokesCallId) {
        LogSvc.debug('[PlantronicsControlSvc]: Call already present on the device: ', call.callId);
        // Set current muted state
        muteCall(call.isMuted());
        return;
      }
      spokesCallId = getSpokesCallId(call.callId);
      var contact = new Plantronics.SpokesContact({ Name: encodeURIComponent(call.convTitle) });

      LogSvc.debug('[PlantronicsControlSvc]: Send outgoing call notification to device');
      _headsetMuted = null;
      _spokes.Plugin.outgoingCall(PLUGIN_NAME, spokesCallId, contact, 'ToHeadset', function (res) {
        LogSvc.debug('[PlantronicsControlSvc]: outgoingCall response. Result: ', res.Result);
        if (call.sameAs(_activeCall)) {
          muteCall(call.isMuted());
        }
      });
    }

    function terminateCall(call, cb) {
      if (!call) {
        return;
      }
      var spokesCallId = _callIdHash[call.callId];

      if (spokesCallId) {
        LogSvc.debug('[PlantronicsControlSvc]: Send terminate call notification to device');
        _spokes.Plugin.terminateCall(PLUGIN_NAME, spokesCallId, function (res) {
          LogSvc.debug('[PlantronicsControlSvc]: terminateCall response. Result: ', res.Result);
          cb && cb(res.Result);
        });
      }
    }

    function muteCall(muted, cb) {
      if (_delayedMute.command !== undefined) {
        LogSvc.debug('[PlantronicsControlSvc]: Queued muted = ' + _delayedMute.command + ' request to device discarded');
      }
      _delayedMute.command = !!muted;
      if (!_delayedMute.timeout) {
        // Wait 500ms before sending the mute request to the device. We send only
        // the last request at the end of these 500ms (all the other ones are discarded)
        _delayedMute.timeout = $timeout(
          function () {
            var muteCommand = _delayedMute.command;
            if (_headsetMuted !== muteCommand) {
              LogSvc.debug('[PlantronicsControlSvc]: Send muted call notification to device. muted = ', muteCommand);
              _spokes.Plugin.muteCall(PLUGIN_NAME, muteCommand, function (res) {
                LogSvc.debug('[PlantronicsControlSvc]: muteCall response. Result: ', res.Result);
                _headsetMuted = muteCommand;
                cb && cb();
              });
            } else {
              LogSvc.debug('[PlantronicsControlSvc]: Request muted = ' + muteCommand + ' not sent to device. Device already in correct state.');
            }
            _delayedMute = {};
          },
          500,
          false
        );
      }
    }

    function terminateActiveCall(cb) {
      LogSvc.debug('[PlantronicsControlSvc]: Active call has ended');
      // Reset device's mute state and terminate call
      if (_headsetMuted) {
        // Unmute the headset before ending the call
        muteCall(false, function () {
          terminateCall(_activeCall, cb);
          clearActiveCallId();
        });
      } else {
        if (_delayedMute.timeout) {
          $timeout.cancel(_delayedMute.timeout);
          _delayedMute = {};
        }
        terminateCall(_activeCall, cb);
        clearActiveCallId();
      }
    }

    function clearAllDeviceCalls() {
      _activeCall = null;
      _alertingCall = null;
      var spokes = Object.keys(_callIdHash);
      LogSvc.debug('[PlantronicsControlSvc]: Clear all device calls. Number of calls: ', spokes.length);
      spokes.forEach(function (unifyPhoneId) {
        LogSvc.debug('[PlantronicsControlSvc]: Send terminate call notification to device');
        _spokes.Plugin.terminateCall(PLUGIN_NAME, _callIdHash[unifyPhoneId], function (res) {
          LogSvc.debug('[PlantronicsControlSvc]: terminateCall response. Result: ', res.Result);
        });
        delete _callIdHash[unifyPhoneId];
      });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc listeners
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribeOnce('/localUser/init', function () {
      LogSvc.debug('[PlantronicsControlSvc]: Received /localUser/init event');
      init();
    });

    function onCallIncoming(call) {
      if (!call) {
        return;
      }
      if (!_connected || (_alertingCall && call.callId === _alertingCall.callId)) {
        return;
      }
      if (_activeCall) {
        LogSvc.debug("[PlantronicsControlSvc]: Ignore incoming call since there's an active call. Incoming call ID: ", call.callId);
        return;
      }

      LogSvc.debug('[PlantronicsControlSvc]: Received incoming call. Set alerting call to', call.callId);
      setAlertingCall(call);
      incomingCall(call);
      startEventPolling();
    }
    PubSubSvc.subscribe('/call/incoming', onCallIncoming);

    function onCallState(call, oldCallId) {
      if (!call) {
        return;
      }
      if (call.isRemote && call.checkCstaState([CstaCallState.Ringing, CstaCallState.ExtendedRinging])) {
        LogSvc.debug('[PlantronicsControlSvc]: New ATC remote call ringing');
        onCallIncoming(call);
        return;
      }
      if (!_connected || call.isRemote || !(call.isOutgoingState() || call.isEstablished())) {
        return;
      }

      LogSvc.debug('[PlantronicsControlSvc]: Received /call/state event for established call');

      if (oldCallId && _callIdHash[oldCallId]) {
        var spokesCallId = _callIdHash[oldCallId];
        _callIdHash[call.callId] = spokesCallId;
        LogSvc.debug('[PlantronicsControlSvc]: Replaced UnifyPhone call ID ' + oldCallId + ' with ' + call.callId + '. Spokes call ID:', spokesCallId);
        delete _callIdHash[oldCallId];
      }

      if (!call.sameAs(_activeCall) && call.sameAs(CallControlSvc.getActiveCall())) {
        setActiveCall(call);

        if (_alertingCall && call.callId === _alertingCall.callId) {
          LogSvc.debug('[PlantronicsControlSvc]: Alerting call has been answered');
          answerCall(call);
          clearAlertingCallId();
        } else {
          outgoingCall(call);
        }
        startEventPolling();
      } else if (_alertingCall && call.callId === _alertingCall.callId) {
        // This would be an error condition where _activeCall and _alertingCall
        // are pointing to the same call. Just clear the alerting call.
        clearAlertingCallId();
      }
    }
    PubSubSvc.subscribe('/call/state', onCallState);

    function onCallEnded(endedCall, replaced) {
      if (!endedCall) {
        return;
      }
      if (!_connected || endedCall.isRemote) {
        return;
      }

      LogSvc.info('[PlantronicsControlSvc]: Received /call/ended event. Replaced:', !!replaced);

      if (_alertingCall && endedCall.callId === _alertingCall.callId) {
        LogSvc.debug('[PlantronicsControlSvc]: Alerting call has been canceled');
        terminateCall(_alertingCall);
        clearAlertingCallId();
      } else if (endedCall.sameAs(_activeCall) && !replaced) {
        terminateActiveCall(function () {
          var call = CallControlSvc.getActiveCall();
          if (call && (call.isEstablished() || call.isOutgoingState())) {
            setActiveCall(call);
            outgoingCall(call);
          } else {
            call = CallControlSvc.getIncomingCall();
            if (call) {
              onCallIncoming(call);
            }
          }
        });
      } else {
        terminateCall(endedCall);
        delete _callIdHash[endedCall.callId];
      }
    }
    PubSubSvc.subscribe('/call/ended', onCallEnded);

    function onCallLocalUserMuted(callId, remotelyMuted, locallyMuted) {
      if (_connected && _activeCall && _activeCall.callId === callId) {
        var muted = !!(remotelyMuted || locallyMuted);
        LogSvc.debug('[PlantronicsControlSvc]: Local user has been ' + (muted ? 'muted' : 'unmuted') + '. Send notification to device.');
        muteCall(!!muted);
      }
    }
    PubSubSvc.subscribe('/call/localUser/muted', onCallLocalUserMuted);

    function onCallLocalUserMutedSelf(callId, muted) {
      if (_connected && _activeCall && _activeCall.callId === callId) {
        muted = !!muted;
        LogSvc.debug('[PlantronicsControlSvc]: Local user has ' + (muted ? 'muted' : 'unmuted') + ' self. Send notification to device.');
        muteCall(muted);
      }
    }
    PubSubSvc.subscribe('/call/localUser/mutedSelf', onCallLocalUserMutedSelf);

    function onAtcRemoteCallInfo(atcCall) {
      if (_alertingCall && _alertingCall.callId === atcCall.callId) {
        if (atcCall.isEstablished() || atcCall.checkCstaState([CstaCallState.Idle])) {
          LogSvc.debug('[PlantronicsControlSvc]: Remote call was answered or terminated. Stop ringing');
          terminateCall(_alertingCall);
          clearAlertingCallId();
        }
      }
    }
    PubSubSvc.subscribe('/atccall/info', onAtcRemoteCallInfo);

    function onAtcCallReplace() {
      LogSvc.debug('[PlantronicsControlSvc]: Received /atccall/replace event');
      if (_alertingCall && _alertingCall.isHandoverInProgress) {
        LogSvc.debug('[PlantronicsControlSvc]: Remote call was answered locally. Stop ringing');
        terminateCall(_alertingCall);
        clearAlertingCallId();
      }
    }
    PubSubSvc.subscribe('/atccall/replace', onAtcCallReplace);

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    Object.defineProperties(this, {
      connected: {
        get: function () {
          return _connected;
        },
        enumerable: true,
        configurable: false
      },
      connectionError: {
        get: function () {
          return _connectionError;
        },
        enumerable: true,
        configurable: false
      }
    });

    this.connect = function (publishError) {
      LogSvc.debug('[PlantronicsControlSvc]: Connect to Plantronics Hub');
      var deferred = $q.defer();

      if (!$rootScope.localUser.plantronicsEnabled) {
        LogSvc.warn('[PlantronicsControlSvc]: Plantronics device integration is disabled. Do not connect.');
        deferred.reject('Plantronics integration disabled by administrator');
        return deferred.promise;
      }

      cancelConnectionPolling();
      if (!_spokes) {
        Plantronics.init();
        Plantronics.setLogger(LogSvc);
        _spokes = new Plantronics.Spokes(DEFAULT_SPOKES_URL);
      }

      registerPlugin()
        .then(function () {
          // Save the connected state so it will be automatically reconnected in the next login
          saveIntegration(true);
          PubSubSvc.publish('/headset/plantronics/connectSucceeded');
          var call = CallControlSvc.getActiveCall();
          if (call && (call.isEstablished() || call.isOutgoingState())) {
            setActiveCall(call);
            outgoingCall(call);
            startEventPolling();
          } else {
            call = CallControlSvc.getIncomingCall();
            if (call) {
              onCallIncoming(call);
            }
          }
          startConnectionPolling();
          deferred.resolve();
        })
        .catch(function () {
          if (publishError) {
            PubSubSvc.publish('/headset/plantronics/connectFailed');
          }
          deferred.reject();
        });
      return deferred.promise;
    };

    this.disconnect = function () {
      LogSvc.debug('[PlantronicsControlSvc]: Disconnect from Plantronics Hub');
      cancelConnectionPolling();
      // Reset connection error and unregister
      _connectionError = false;

      // Save the disconnected state so it won't be automatically reconnected in the next login
      saveIntegration(false);
      return unregisterPlugin();
    };

    this.getActiveDevice = function () {
      return new $q(function (resolve, reject) {
        if (!_connected) {
          reject('Not connected');
          return;
        }
        _spokes.Device.deviceInfo(function (res) {
          $rootScope.$apply(function () {
            if (res.isError && res.Err.Description !== 'There are no supported devices') {
              LogSvc.warn('[PlantronicsControlSvc]: Failed to get active device. ', res.Err.Description);
              reject(res.Err.Description);
            } else {
              resolve(res.Result);
            }
          });
        });
      });
    };

    this.isIntegrationEnabled = function () {
      return _integrationEnabled;
    };

    this.disableIntegration = function () {
      cancelConnectionPolling();
      saveIntegration(false);
      _connectionError = false;
    };

    // Use this method to quickly and synchronously disconnect from PLT hub, without
    // disabling the integration (e.g.: client being closed)
    this.release = function () {
      if (!_spokes) {
        return;
      }
      _spokes.Plugin.unRegister(PLUGIN_NAME);
      $timeout(function () {
        // Finish the rest of the disconnection process asynchronously, since we don't
        // want to block this call for too long. Note that this code won't run if
        // the browser is being closed (which is fine!)
        clearAllDeviceCalls();
        stopEventPolling();
        _connected = false;
      });
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initializations
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.PlantronicsControlSvcImpl = PlantronicsControlSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var RtcSessionController = unifyPhone.RtcSessionController;
  var WebRTCAdapter = unifyPhone.WebRTCAdapter;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-params, max-lines-per-function, no-unused-vars
  function DeviceHandlerSvcImpl($window, $q, LogSvc, PubSubSvc, LocalStoreSvc, HeadsetHidControlSvc) {
    // NOSONAR
    LogSvc.debug('New Service: DeviceHandlerSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var DEFAULT_BEEP = {
      freq: 450,
      duration: 0.05
    };

    var _videoMediaStream = null;
    var _pendingGetVideoMedia = false;
    var _stopPendingGetVideoMedia = false;
    var _mediaPromise = $q.when(); // empty promise
    var _that = this;

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc listeners
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function init() {
      !$window.AudioContext && LogSvc.warn('[DeviceHandlerSvc]: AudioContext not supported, no audio feedback will be played while sending DTMF digits');
    }

    function createNewAudioContext() {
      if ($window.AudioContext) {
        LogSvc.debug('[DeviceHandlerSvc]: Creating new AudioContext');
        return new $window.AudioContext();
      }
      return null;
    }

    function initRtcSettings() {
      var agcData = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.AUDIOAGC);
      var ecData = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.AUDIOEC);

      RtcSessionController.enableAudioAGC = agcData === null ? RtcSessionController.DEFAULT_ENABLE_AUDIO_AGC : !!agcData;
      RtcSessionController.enableAudioEC = ecData === null ? RtcSessionController.DEFAULT_ENABLE_AUDIO_EC : !!ecData;

      WebRTCAdapter.getMediaSources(function (audioSources, videoSources, audioOutputDevices) {
        RtcSessionController.recordingDevices = _that.getRecordingDevice(audioSources);
        RtcSessionController.videoDevices = _that.getVideoDevice(videoSources);
        if (WebRTCAdapter.audioOutputSelectionSupported) {
          RtcSessionController.playbackDevices = _that.getPlaybackDevice(audioOutputDevices);
          RtcSessionController.ringingDevices = _that.getRingingDevice(audioOutputDevices);
        }
      });
    }

    function stopMediaStream(stream) {
      WebRTCAdapter.stopMediaStream(stream);
    }

    function playBeep(param) {
      var audioCtx = createNewAudioContext();
      if (!audioCtx) {
        return;
      }
      param = param || DEFAULT_BEEP;

      // Oscillator setup
      var compressor = audioCtx.createDynamicsCompressor();
      compressor.connect(audioCtx.destination);
      var osc = audioCtx.createOscillator();
      osc.type = 'sine'; // sin wave
      osc.frequency.value = param.freq;
      osc.connect(compressor);

      // Play it
      var ctime = audioCtx.currentTime;
      osc.start(ctime);
      osc.stop(ctime + param.duration);
      osc.onended = function () {
        LogSvc.debug('[DeviceHandlerSvc]: Destroying current AudioContext');
        audioCtx.close();
        audioCtx = null;
      };
    }

    /**
     * This method is used to check if any device in deviceList had their IDs changed
     *
     * @param {Array} deviceList - List of devices to be checked
     * @param {Array} updatedDeviceList - List of devices that might have new IDs
     */
    function checkForChangedDeviceIds(deviceList, updatedDeviceList) {
      if (!deviceList || !deviceList.length || !updatedDeviceList || !updatedDeviceList.length) {
        return;
      }
      deviceList.forEach(function (device, idx) {
        // Look for this device in updatedDeviceList
        var found = Utils.selectMediaDevice(updatedDeviceList, [device], true);
        if (found && found.id !== device.id) {
          // Device found and its ID has changed, update it
          deviceList[idx] = found;
        }
      });
    }

    function savePlaybackDevice(device) {
      var deviceList = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.MEDIA_DEVICE_LIST) || {};
      deviceList.playback = Utils.addDeviceToPreferredList(deviceList.playback, device);
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.MEDIA_DEVICE_LIST, deviceList);
      RtcSessionController.playbackDevices = deviceList.playback;

      LogSvc.info('[DeviceHandlerSvc]: savePlaybackDevice saved:', device);
    }

    function saveRecordingDevice(device) {
      var deviceList = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.MEDIA_DEVICE_LIST) || {};
      deviceList.recording = Utils.addDeviceToPreferredList(deviceList.recording, device);
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.MEDIA_DEVICE_LIST, deviceList);
      RtcSessionController.recordingDevices = deviceList.recording;

      LogSvc.info('[DeviceHandlerSvc]: saveRecordingDevice saved:', device);
    }

    function saveVideoDevice(device) {
      var deviceList = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.MEDIA_DEVICE_LIST) || {};
      deviceList.video = Utils.addDeviceToPreferredList(deviceList.video, device);
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.MEDIA_DEVICE_LIST, deviceList);
      RtcSessionController.videoDevices = deviceList.video;

      LogSvc.info('[DeviceHandlerSvc]: saveVideoDevice saved:', device);
    }

    function saveRingingDevice(device) {
      var deviceList = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.MEDIA_DEVICE_LIST) || {};
      deviceList.ringing = Utils.addDeviceToPreferredList(deviceList.ringing, device);
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.MEDIA_DEVICE_LIST, deviceList);
      RtcSessionController.ringingDevices = deviceList.ringing;

      LogSvc.info('[DeviceHandlerSvc]: saveRingingDevice saved:', device);
    }

    function getDeviceList(deviceType, updatedDeviceList) {
      var deviceList = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.MEDIA_DEVICE_LIST) || {};
      deviceList[deviceType] = deviceList[deviceType] || [];

      // Use the updated device list to update any saved device that had their ID changed
      checkForChangedDeviceIds(deviceList[deviceType], updatedDeviceList);
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.MEDIA_DEVICE_LIST, deviceList);

      return deviceList[deviceType];
    }

    function selectDevice(constraints, sources, previouslySelected) {
      var dev = Utils.selectMediaDevice(sources, previouslySelected);
      if (dev) {
        constraints.sourceId = dev.id;
      }
    }

    function getSourceIds(constraints) {
      if (WebRTCAdapter.getMediaSourcesSupported) {
        var selectAudioSource = constraints.audio && constraints.audio.sourceId;
        var selectVideoSource = constraints.video && constraints.video.sourceId;
        // Get currently selected input and/or video device only if no sourceId is specified
        if (selectAudioSource || selectVideoSource) {
          var defer = $q.defer();
          WebRTCAdapter.getMediaSources(function (audioSources, videoSources) {
            if (selectAudioSource) {
              selectDevice(constraints.audio, audioSources, RtcSessionController.recordingDevices);
            }
            if (selectVideoSource) {
              selectDevice(constraints.video, videoSources, RtcSessionController.videoDevices);
            }
            defer.resolve();
          });
          return defer.promise;
        }
      }
      return $q.resolve();
    }

    function getUserMedia(constraints, successCb, errorCb) {
      return new $q(function (resolve) {
        var updatedConstraints = {
          audio: !!constraints.audio && WebRTCAdapter.getAudioOptions(constraints.audio),
          video: !!constraints.video && WebRTCAdapter.getVideoOptions(constraints.video)
        };
        LogSvc.debug('[DeviceHandlerSvc]: getUserMedia with constraints ', updatedConstraints);
        WebRTCAdapter.getUserMedia(
          updatedConstraints,
          function (stream) {
            try {
              successCb(stream);
            } catch (e) {}
            LogSvc.debug('[DeviceHandlerSvc]: Publish /getUserMedia/success event');
            PubSubSvc.publish('/getUserMedia/success');
            resolve();
          },
          function (error) {
            try {
              errorCb(error);
            } catch (e) {}
            resolve();
          }
        );
      });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc listeners
    ///////////////////////////////////////////////////////////////////////////////////////
    function onLocalUserInit() {
      LogSvc.debug('[DeviceHandlerSvc]: Received /localUser/init event');
      initRtcSettings();
    }
    PubSubSvc.subscribeOnce('/localUser/init', onLocalUserInit);

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    /**
     * This method encapsulates the call to WebRTCAdapter.getUserMedia behind a promise
     * to ensure that the calls are processed one at a time.
     */
    this.getUserMedia = function (constraints, successCb, errorCb) {
      _mediaPromise = _mediaPromise.finally(function () {
        constraints = constraints || {};
        return getSourceIds(constraints).then(function () {
          return getUserMedia(constraints, successCb, errorCb);
        });
      });
    };

    /**
     * This method returns the current browser's camera media URL
     * @param {function} cb Callback that will receive the media URL or an error object
     */
    this.startCameraStream = function (cb, sourceId) {
      cb = cb || function () {};

      if (_pendingGetVideoMedia) {
        LogSvc.debug('[DeviceHandlerSvc]: startCameraStream: There is a pending get user media');
        cb('ALREADY_IN_PROGRESS');
        return;
      }
      if (_videoMediaStream) {
        cb(null, _videoMediaStream);
        return;
      }

      _pendingGetVideoMedia = true;

      var constraints = {
        audio: false,
        video: { sourceId: sourceId }
      };

      this.getUserMedia(
        constraints,
        function (stream) {
          _pendingGetVideoMedia = false;
          LogSvc.debug('[DeviceHandlerSvc]: Got camera stream. ', stream);

          if (_stopPendingGetVideoMedia) {
            // This get user media has been cancelled
            LogSvc.debug('[DeviceHandlerSvc]: startCameraStream: Cancel pending get user media');
            _stopPendingGetVideoMedia = false;
            stopMediaStream(stream);
            cb('CANCELLED_GET_USER_MEDIA');
            return;
          }

          _videoMediaStream = stream;
          cb(null, stream);
        },
        function (err) {
          _pendingGetVideoMedia = false;
          _stopPendingGetVideoMedia = false;
          cb(err);
        }
      );
    };

    /**
     * Stop the current camera media stream.
     */
    this.stopCameraStream = function () {
      if (_pendingGetVideoMedia) {
        LogSvc.debug('[DeviceHandlerSvc]: stopCameraStream: Cancel pending get user media');
        _stopPendingGetVideoMedia = true;
        return;
      }
      stopMediaStream(_videoMediaStream);
      _videoMediaStream = null;
    };

    this.getAudioContext = function () {
      return createNewAudioContext();
    };

    this.playBeep = function () {
      playBeep(DEFAULT_BEEP);
    };

    this.setPlaybackDevice = function (playbackDevice) {
      if (playbackDevice && playbackDevice.id && WebRTCAdapter.audioOutputSelectionSupported) {
        LogSvc.info('[DeviceHandlerSvc]: User selected new audio output. deviceId = ', playbackDevice.id);
        savePlaybackDevice(playbackDevice);
        // Reset the Jabra active device to match the selected audio output device
        PubSubSvc.publish('/devices/activeDeviceReset', playbackDevice);
        // HeadsetHidControlSvc.resetActiveDevice(playbackDevice);
      }
    };

    this.setRecordingDevice = function (recordingDevice) {
      if (recordingDevice) {
        LogSvc.info('[DeviceHandlerSvc]: User selected new microphone. deviceId = ', recordingDevice.id);
        saveRecordingDevice(recordingDevice);
      }
    };

    this.setVideoDevice = function (videoDevice) {
      if (videoDevice) {
        LogSvc.info('[DeviceHandlerSvc]: User selected new camera. deviceId = ', videoDevice.id);
        saveVideoDevice(videoDevice);
      }
    };

    this.setRingingDevice = function (ringingDevice) {
      if (ringingDevice) {
        LogSvc.info('[DeviceHandlerSvc]: User selected new ringing device. deviceId = ', ringingDevice.id);
        saveRingingDevice(ringingDevice);
      }
    };

    this.getPlaybackDevice = getDeviceList.bind(this, 'playback');

    this.getRecordingDevice = getDeviceList.bind(this, 'recording');

    this.getVideoDevice = getDeviceList.bind(this, 'video');

    this.getRingingDevice = getDeviceList.bind(this, 'ringing');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initializations
    ///////////////////////////////////////////////////////////////////////////////////////
    init();

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.DeviceHandlerSvcImpl = DeviceHandlerSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var AudioVideoDeviceCategories = unifyPhone.Constants.AudioVideoDeviceCategories;
  var RtcSessionController = unifyPhone.RtcSessionController;
  var Utils = unifyPhone.Utils;
  var VideoResolutionLevel = unifyPhone.Enums.VideoResolutionLevel;
  var WebRTCAdapter = unifyPhone.WebRTCAdapter;

  // eslint-disable-next-line max-params, max-lines-per-function
  function DeviceSettingsSvcImpl($rootScope, $q, $timeout, $window, LogSvc, PubSubSvc, DeviceHandlerSvc, CallControlSvc) {
    // NOSONAR
    LogSvc.debug('New Service: DeviceSettingsSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var ALL_CATEGORIES = [
      AudioVideoDeviceCategories.AUDIO_OUTPUT,
      AudioVideoDeviceCategories.RINGING_OUTPUT,
      AudioVideoDeviceCategories.AUDIO_INPUT,
      AudioVideoDeviceCategories.VIDEO_INPUT
    ];

    var AUDIO_INPUT_DEFAULT_SRC = {
      label: 'res_DefaultDeviceLabel',
      altLabel: 'res_DefaultMicrophone',
      noPermissionLabel: 'res_NoMicrophonesFound'
    };
    var VIDEO_INPUT_DEFAULT_SRC = {
      label: 'res_DefaultDeviceLabel',
      altLabel: 'res_DefaultCamera',
      noPermissionLabel: 'res_NoCamera'
    };
    var AUDIO_OUTPUT_DEFAULT_SRC = {
      label: 'res_DefaultDeviceLabel',
      altLabel: 'res_DefaultAudioOutput',
      noPermissionLabel: 'res_NoAudioOutput'
    };
    var RINGING_OUTPUT_DEFAULT_SRC = {
      label: 'res_DefaultDeviceLabel',
      altLabel: 'res_DefaultAudioOutput',
      noPermissionLabel: 'res_NoAudioOutput'
    };

    // getUserMedia errors
    var GUM_CONSTRAINT_ERROR = 'ConstraintNotSatisfiedError';
    var GUM_OVERCONSTRAINT_ERROR = 'OverconstrainedError';

    var GET_DEVICES_TIMOUT = 20000; // 20 seconds
    var GET_MEDIA_SOURCE_WAITING_TIMOUT = 250; // 0.25 second

    var _selectOutputDeviceEnabled = WebRTCAdapter.audioOutputSelectionSupported;
    var _openMediaStream = null;
    var _hasRetriedGetDevices = false;
    var _connectedDevices = { playback: [], recording: [], video: [] };
    var _deviceChangeTimeout = null;
    var _retryGetDevicesTimeout = null;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function init() {
      // Get a initial list of media devices that are connected so we can track devices that are added/removed
      WebRTCAdapter.getMediaSources(function (audioSources, videoSources, audioOutputDevices) {
        _connectedDevices = {
          playback: audioOutputDevices,
          recording: audioSources,
          video: videoSources
        };

        if (!audioSources || !audioSources.length) {
          retryGetDevices();
        }

        registerOnDeviceChangeEvent();
      });
    }

    function registerOnDeviceChangeEvent() {
      if (!$window.navigator.mediaDevices) {
        return;
      }

      // Listen for USB media device changes
      $window.navigator.mediaDevices.ondevicechange = function (event) {
        if (event.type !== 'devicechange') {
          return;
        }
        $timeout.cancel(_retryGetDevicesTimeout);
        _retryGetDevicesTimeout = null;

        $timeout.cancel(_deviceChangeTimeout);
        _deviceChangeTimeout = $timeout(function () {
          _deviceChangeTimeout = null;
          _hasRetriedGetDevices = false;
          WebRTCAdapter.clearMediaSourcesCache();
          getConnectedDevices();
        }, GET_MEDIA_SOURCE_WAITING_TIMOUT);
      };
    }

    function getConnectedDevices() {
      // Get currently connected devices and find out what changed
      WebRTCAdapter.getMediaSources(function (audioSources, videoSources, audioOutputDevices) {
        $rootScope.$apply(function () {
          var added = { playback: [], recording: [], video: [] };
          var removed = { playback: [], recording: [], video: [] };

          inspectDeviceList(audioSources, _connectedDevices.recording, added.recording, removed.recording);
          inspectDeviceList(videoSources, _connectedDevices.video, added.video, removed.video);
          inspectDeviceList(audioOutputDevices, _connectedDevices.playback, added.playback, removed.playback);

          if (!handleGetMediaSourcesResult(added.recording) && (!audioSources || !audioSources.length)) {
            // Handle situation to show popup message only here to not annoy user in all other cases
            LogSvc.debug('[DeviceSettingsSvc]: No recording device(s) are available, publish /infoMessage/show event');
            PubSubSvc.publish('/infoMessage/show', [
              {
                message: 'res_AccessToAudioInputDeviceFailed',
                manual: true
              }
            ]);

            // For some reason navigator.mediaDevices.enumerateDevices response can be received in more than 10 seconds.
            // It is a bad case, so callback function called with empty values after waiting for 3 seconds.
            // As result info message with no device displayed to user. Do another attempt on 30 seconds
            // to hide the message in success case
            retryGetDevices();
          }

          _connectedDevices = {
            playback: audioOutputDevices,
            recording: audioSources,
            video: videoSources
          };

          if (added.playback.length + added.recording.length + added.video.length > 0) {
            LogSvc.info('[DeviceSettingsSvc]: Media devices added:', added);
            PubSubSvc.publish('/media/device/added', [added, _connectedDevices]);

            // If a device was added, we need to trigger a media renegotiation, since it could be a
            // default device and it might have to be used by this call
            var call = CallControlSvc.getActiveCall();
            if (call) {
              LogSvc.info('[DeviceSettingsSvc]: Device added: Renegotiate media so the added device(s) can be used for this call:', call.callId);
              CallControlSvc.renegotiateMedia(
                call.callId,
                function (err) {
                  handleRenegotiateMediaResponse(call, err);
                },
                { dontReuseAudioStream: true }
              );
            }
          }
          if (removed.playback.length + removed.recording.length + removed.video.length > 0) {
            LogSvc.info('[DeviceSettingsSvc]: Media devices removed:', removed);
            PubSubSvc.publish('/media/device/removed', [removed, _connectedDevices]);
          }
        });
      });
    }

    function handleGetMediaSourcesResult(recording) {
      if ((!_connectedDevices.recording || !_connectedDevices.recording.length) && recording.length) {
        // Mic(s) became available, send '/infoMessage/close' event in case we have a 'No mic available' message up
        LogSvc.debug('[DeviceSettingsSvc]: Recording device(s) became available, publish /infoMessage/close event');
        PubSubSvc.publish('/infoMessage/close', ['res_AccessToAudioInputDeviceFailed']);
        return true;
      }
      return false;
    }

    function retryGetDevices() {
      if (_hasRetriedGetDevices || _retryGetDevicesTimeout) {
        return;
      }

      _retryGetDevicesTimeout = $timeout(function () {
        LogSvc.debug('[DeviceSettingsSvc]: Another attempt to retrieve device list');
        _retryGetDevicesTimeout = null;
        _hasRetriedGetDevices = true;
        getConnectedDevices();
      }, GET_DEVICES_TIMOUT);
    }

    function isSameDevice(device, id) {
      // Id of unset/default device is initially null/undefined so it should equal
      // device without id (fake default device) or a real default device (with id default)
      return device && ((!id && (!device.id || device.id === 'default')) || id === device.id);
    }

    /**
     * Method to return which devices were added and/or removed
     *
     * @param {Array} source - Current list of devices
     * @param {Array} previous - Previous list of devices
     * @param {Array} added - Devices added between current and previous list
     * @param {Array} removed - Devices removed between current and previous list
     * @returns {undefined}
     */
    function inspectDeviceList(source, previous, added, removed) {
      if (!source || !previous || !added || !removed) {
        return;
      }
      previous.forEach(function (d) {
        removed.push(d.id);
      });
      source.forEach(function (d) {
        var index = removed.indexOf(d.id);
        if (index >= 0) {
          removed.splice(index, 1);
        } else {
          added.push(d);
        }
      });
    }

    function getSourceFromId(sources, id) {
      // Allow matching null/undefined id (maps to fake default device or real 'default' device)
      if (Array.isArray(sources)) {
        return (
          sources.find(function (src) {
            return isSameDevice(src, id);
          }) || null
        );
      }
      return null;
    }

    function preprocessSources(sources, defaultSource) {
      // remove duplicates (e.g. Firefox)
      var filteredSources = sources.filter(function (elem, index, self) {
        return (
          index ===
          self.findIndex(function (fiElem) {
            return fiElem.id === elem.id;
          })
        );
      });
      // check default device entry
      var defSrc = getSourceFromId(filteredSources, 'default');
      if (!defSrc) {
        filteredSources.unshift(defaultSource);
      } else {
        // assure that label for default is always the same
        defSrc.label = defaultSource.label;
        defSrc.altLabel = defaultSource.altLabel;
      }
      return filteredSources;
    }

    function applySources(category, catSources) {
      catSources = catSources || [];
      var checkPermissions = catSources.find(function (src) {
        return !src.label;
      });
      var filteredSources;
      if (!checkPermissions) {
        // remove duplicates (e.g. Firefox)
        filteredSources = preprocessSources(catSources, category.defaultSrc);
      } else {
        // add default source for audio output category
        filteredSources = [];
        if (category.id === AudioVideoDeviceCategories.AUDIO_OUTPUT || category.id === AudioVideoDeviceCategories.RINGING_OUTPUT) {
          filteredSources.push(category.defaultSrc);
        }
      }
      filteredSources.forEach(function (s) {
        s.categoryId = category.id;
      });
      category.sources = filteredSources;
      category.checkPermissions = checkPermissions;

      var currentSelected;
      switch (category.id) {
        case AudioVideoDeviceCategories.AUDIO_OUTPUT:
          currentSelected = Utils.selectMediaDevice(filteredSources, RtcSessionController.playbackDevices);
          break;
        case AudioVideoDeviceCategories.RINGING_OUTPUT:
          currentSelected = Utils.selectMediaDevice(filteredSources, RtcSessionController.ringingDevices);
          break;
        case AudioVideoDeviceCategories.AUDIO_INPUT:
          currentSelected = Utils.selectMediaDevice(filteredSources, RtcSessionController.recordingDevices);
          break;
        case AudioVideoDeviceCategories.VIDEO_INPUT:
          currentSelected = Utils.selectMediaDevice(filteredSources, RtcSessionController.videoDevices);
          break;
      }

      var deviceInfo = getSourceFromId(category.sources, currentSelected && currentSelected.id);
      category.setSelectedDevice(deviceInfo);
      category.initialDeviceId = deviceInfo && category.selectedDevice.id;
      return category;
    }

    function createCategory(catId, catName, catDefaultSrc) {
      var category = {
        id: catId,
        name: catName,
        defaultSrc: catDefaultSrc,
        sources: catDefaultSrc ? [catDefaultSrc] : [],
        setSelectedDevice: function (device) {
          if (device) {
            device.categoryId = this.id;
          }
          // Note that the selected device can be null, which means there's no preferred device or it hasn't been found (unplugged)
          this.selectedDevice = device;
        }
      };
      category.setSelectedDevice(category.defaultSrc);
      return category;
    }

    function findMatchingOutputDevice(inputDev, audioOutCategory) {
      return audioOutCategory.sources.find(function (dev) {
        var inputId = inputDev.id || '';
        var outputId = dev.id || '';
        if (inputId.match(/default|communications/) || outputId.match(/default|communications/)) {
          return inputDev.id === dev.id;
        }
        return inputDev.groupId === dev.groupId;
      });
    }

    function matchInputOutputDevices(category, audioOutCategory, audioInpCategory) {
      category.checkPermissions = audioInpCategory.checkPermissions;
      // On macOS/WKWebView, setSinkId is not available so audioOutputDevices is null and
      // audioOutCategory only contains the fake default source (no id, no groupId).
      // In that case fall back to pairing every input with the default output so that
      // real microphones are still surfaced in the AUDIO_DEVICE category.
      var hasRealOutputDevices = audioOutCategory.sources.some(function (dev) {
        return !!dev.id;
      });
      audioInpCategory.sources.forEach(function (input) {
        var output = findMatchingOutputDevice(input, audioOutCategory);
        if (!output && !hasRealOutputDevices && audioOutCategory.sources.length > 0) {
          output = audioOutCategory.sources[0];
        }
        if (output) {
          var inputSplit = (input.label && input.label.split(/\s/)) || [];
          var outputSplit = (output.label && output.label.split(/\s/)) || [];

          var deviceLabel = '';
          inputSplit.forEach(function (w) {
            if (outputSplit.includes(w)) {
              deviceLabel += (deviceLabel ? ' ' : '') + w;
            }
          });

          var regexMatch = deviceLabel.match(/^(\()*(\w.*\w)[\s-]*(\))*$/);
          if (regexMatch) {
            // Remove parentheses and dangling dashes at the end
            deviceLabel = regexMatch[2];
            if (regexMatch[3] && !regexMatch[1]) {
              // Add a closing parenthesis back, since the label doesn't begin with parenthesis
              deviceLabel += ')';
            }
          }
          if (!deviceLabel) {
            // There's nothing common between the 2 labels, so display a combination of them
            deviceLabel = input.label + '/' + output.label;
          }
          var combinedDevice = {
            id: input.id + output.id,
            inputDev: input,
            outputDev: output,
            altLabel: deviceLabel,
            label: deviceLabel
          };
          LogSvc.debug('[DeviceSettingsSvc]: Found matching input and output devices: ', combinedDevice);
          if (deviceLabel === 'res_DefaultDeviceLabel') {
            category.defaultSrc = combinedDevice;
          }
          if (isSameDevice(audioInpCategory.selectedDevice, input.id) && isSameDevice(audioOutCategory.selectedDevice, output.id)) {
            category.setSelectedDevice(combinedDevice);
            category.initialDeviceId = combinedDevice.id;
          }
          category.sources.push(combinedDevice);
        }
      });
    }

    function createAndFetchAudioOutput(fetchCategories) {
      var cat = createCategory(AudioVideoDeviceCategories.AUDIO_OUTPUT, 'res_AudioOutput', AUDIO_OUTPUT_DEFAULT_SRC);
      fetchCategories[AudioVideoDeviceCategories.AUDIO_OUTPUT] = cat;
      return cat;
    }

    function createAndFetchAudioInput(fetchCategories) {
      var cat = createCategory(AudioVideoDeviceCategories.AUDIO_INPUT, 'res_Microphone', AUDIO_INPUT_DEFAULT_SRC);
      fetchCategories[AudioVideoDeviceCategories.AUDIO_INPUT] = cat;
      return cat;
    }

    function handleRenegotiateMediaResponse(call, error) {
      if (error) {
        LogSvc.warn('[DeviceSettingsSvc]: Media renegotiation failed: ', error);
        PubSubSvc.publish('/call/changeMediatype/failed', [call, error]);
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    /*
     * Fetches the available devices grouped by categories (microphone, camera, audio output and ringing).
     * @param {Array} limitCategories Use when only a subset of the available device categories is needed.
     * @return  {Promise} A promise which resolve to device category list. Promise uses notify to provide optional initial data.
     */
    this.fetchDevices = function (limitCategories) {
      var deferred = $q.defer();
      $timeout(function () {
        limitCategories = limitCategories || ALL_CATEGORIES;
        var fetchCategories = {};
        var finalCategories = [];
        var audioOutCategory, audioInpCategory, audioDeviceCategory;

        if (limitCategories.includes(AudioVideoDeviceCategories.AUDIO_DEVICE)) {
          // For AUDIO_DEVICE category, we need to fetch AUDIO_OUTPUT and AUDIO_INPUT
          audioDeviceCategory = createCategory(AudioVideoDeviceCategories.AUDIO_DEVICE, 'res_AudioDevices');
          finalCategories.push(audioDeviceCategory);
          audioOutCategory = createAndFetchAudioOutput(fetchCategories);
          audioInpCategory = createAndFetchAudioInput(fetchCategories);
        } else {
          if (limitCategories.includes(AudioVideoDeviceCategories.AUDIO_OUTPUT) && _selectOutputDeviceEnabled) {
            audioOutCategory = createAndFetchAudioOutput(fetchCategories);
          }
          if (limitCategories.includes(AudioVideoDeviceCategories.AUDIO_INPUT)) {
            audioInpCategory = createAndFetchAudioInput(fetchCategories);
          }
        }

        if (limitCategories.includes(AudioVideoDeviceCategories.RINGING_OUTPUT) && _selectOutputDeviceEnabled) {
          var ringingCategory = createCategory(AudioVideoDeviceCategories.RINGING_OUTPUT, 'res_RingingOutput', RINGING_OUTPUT_DEFAULT_SRC);
          fetchCategories[AudioVideoDeviceCategories.RINGING_OUTPUT] = ringingCategory;
        }

        if (limitCategories.includes(AudioVideoDeviceCategories.VIDEO_INPUT)) {
          var videoInpCategory = createCategory(AudioVideoDeviceCategories.VIDEO_INPUT, 'res_Camera', VIDEO_INPUT_DEFAULT_SRC);
          fetchCategories[AudioVideoDeviceCategories.VIDEO_INPUT] = videoInpCategory;
        }

        limitCategories.forEach(function (cat) {
          var fetch = fetchCategories[cat];
          if (fetch) {
            finalCategories.push(fetch);
          }
        });
        deferred.notify(finalCategories);

        WebRTCAdapter.getMediaSources(function (audioSources, videoSources, audioOutputDevices) {
          handleGetMediaSourcesResult(audioSources);
          _connectedDevices = {
            playback: audioOutputDevices,
            recording: audioSources,
            video: videoSources
          };
          Object.keys(fetchCategories).forEach(function (k) {
            var category = fetchCategories[k];
            switch (category.id) {
              case AudioVideoDeviceCategories.AUDIO_OUTPUT:
              case AudioVideoDeviceCategories.RINGING_OUTPUT:
                applySources(category, audioOutputDevices);
                break;
              case AudioVideoDeviceCategories.AUDIO_INPUT:
                applySources(category, audioSources);
                break;
              case AudioVideoDeviceCategories.VIDEO_INPUT:
                applySources(category, videoSources);
                break;
            }
          });

          if (audioDeviceCategory) {
            matchInputOutputDevices(audioDeviceCategory, audioOutCategory, audioInpCategory);
          }

          // Close media stream if exists (from triggerPermissionRequest)
          if (_openMediaStream) {
            WebRTCAdapter.stopMediaStream(_openMediaStream);
            _openMediaStream = null;
          }
          deferred.resolve(finalCategories);
        });
      });
      return deferred.promise;
    };

    /*
     * Sets the selected devices in the appropriate services and start media renegotiation in
     * case of an active call (and changed device).
     * @param {Array} selectedDevices A list of selected devices.
     */
    this.setDevices = function (selectedDevices) {
      selectedDevices = selectedDevices || [];
      var needsRenegotiation = false,
        publishEvent = false;
      var call = CallControlSvc.getActiveCall() || CallControlSvc.getIncomingCall();
      var savedDevice;

      var selectedAudioDevice = selectedDevices.find(function (s) {
        return s && s.categoryId === AudioVideoDeviceCategories.AUDIO_DEVICE;
      });
      if (selectedAudioDevice) {
        // Automatically select input and output and ignore the AUDIO_INPUT and AUDIO_OUTPUT
        // members in selectedDevices
        selectedDevices = selectedDevices.filter(function (s) {
          return (
            s &&
            s.categoryId !== AudioVideoDeviceCategories.AUDIO_OUTPUT &&
            s.categoryId !== AudioVideoDeviceCategories.AUDIO_INPUT &&
            s.categoryId !== AudioVideoDeviceCategories.AUDIO_DEVICE
          );
        });
        selectedDevices.push(selectedAudioDevice.outputDev);
        selectedDevices.push(selectedAudioDevice.inputDev);
      }
      selectedDevices.forEach(function (selectedDevice) {
        if (selectedDevice && selectedDevice.categoryId) {
          switch (selectedDevice.categoryId) {
            case AudioVideoDeviceCategories.AUDIO_OUTPUT:
              savedDevice = RtcSessionController.playbackDevices[0] || {};
              if (!isSameDevice(selectedDevice, savedDevice.id)) {
                DeviceHandlerSvc.setPlaybackDevice(selectedDevice);
                publishEvent = needsRenegotiation = true;
              }
              break;
            case AudioVideoDeviceCategories.RINGING_OUTPUT:
              savedDevice = RtcSessionController.ringingDevices[0] || {};
              if (!isSameDevice(selectedDevice, savedDevice.id)) {
                DeviceHandlerSvc.setRingingDevice(selectedDevice);
                publishEvent = true;
              }
              break;
            case AudioVideoDeviceCategories.AUDIO_INPUT:
              savedDevice = RtcSessionController.recordingDevices[0] || {};
              if (!isSameDevice(selectedDevice, savedDevice.id)) {
                DeviceHandlerSvc.setRecordingDevice(selectedDevice);
                publishEvent = needsRenegotiation = true;
              }
              break;
            case AudioVideoDeviceCategories.VIDEO_INPUT:
              savedDevice = RtcSessionController.videoDevices[0] || {};
              if (!isSameDevice(selectedDevice, savedDevice.id)) {
                DeviceHandlerSvc.setVideoDevice(selectedDevice);
                // Media renegotiation is only needed if the user is sending video
                needsRenegotiation = needsRenegotiation || (call && !!call.localMediaType.video);
                publishEvent = true;
              }
          }
        }
      });
      if (call && needsRenegotiation) {
        LogSvc.info('[DeviceSettingsSvc]: Trigger a media renegotiation for the changes to take effect');
        CallControlSvc.renegotiateMedia(call.callId, function (err) {
          handleRenegotiateMediaResponse(call, err);
        });
      }
      if (publishEvent) {
        LogSvc.info('[DeviceSettingsSvc]: Device selection changed. Publish /media/device/updated event');
        PubSubSvc.publish('/media/device/updated');
      }
    };

    /*
     * Triggers the browsers permission request popup.
     * @param {function} cb Callback when user allowed or rejected access to media.
     * @param {object} constraints Optional constraint field to specify which media should be requested.
     */
    this.triggerPermissionRequest = function (cb, categoryId) {
      var constraints = {};
      if (categoryId === AudioVideoDeviceCategories.VIDEO_INPUT) {
        LogSvc.info('[DeviceSettingsSvc]: Trigger permission request for camera');
        constraints.video = true;
      } else {
        LogSvc.info('[DeviceSettingsSvc]: Trigger permission request for microphone');
        constraints.audio = true;
      }
      DeviceHandlerSvc.getUserMedia(
        constraints,
        function (stream) {
          // stream must kept open until the device list was fetched
          // this is only for Firefox and its AllowOnce
          _openMediaStream = stream;
          cb && cb();
        },
        function (err) {
          err = err || { name: 'unknown' };
          var msg = err.name + (err.message ? ' (' + err.message + ')' : '');
          LogSvc.warn('[DeviceSettingsSvc]: triggerPermissionRequest failed: ', msg);
          cb && cb(err);
        }
      );
    };

    /*
     * Define the max supported camera resolution
     * @param {array} videoDevices Array of video devices. By default we use RtcSessionController.videoDevices.
     * @returns {Promise} A promise with the max camera resolution for the specified video device(s).
     */
    this.getMaxCameraResolution = function (videoDevices) {
      return new $q(function (resolve, reject) {
        videoDevices = videoDevices || RtcSessionController.videoDevices;

        LogSvc.debug('[DeviceSettingsSvc]: Get max camera resolution. videoDevices = ', videoDevices);
        WebRTCAdapter.getMediaSources(function (audioSources, videoSources) {
          if (!videoSources || !videoSources.length) {
            reject('No cameras available');
            return;
          }

          var resolutions = Object.values(VideoResolutionLevel);
          var videoDevice = Utils.selectMediaDevice(videoSources, videoDevices);
          var config = videoDevice ? { sourceId: videoDevice.id } : {};
          var constraints = { audio: false };

          // Perform this function until proposed resolution can be used with camera
          var getLocalMedia = function () {
            config.videoResolution = resolutions.shift();
            constraints.video = WebRTCAdapter.getVideoOptions(config);

            WebRTCAdapter.getUserMedia(
              constraints,
              function (stream) {
                WebRTCAdapter.stopMediaStream(stream);
                LogSvc.debug('[DeviceSettingsSvc]: Max camera resolution is ', config.videoResolution);
                resolve(config.videoResolution);
              },
              function (error) {
                var errorName = (error && error.name) || 'Unspecified';
                if (resolutions.length > 0 && (errorName === GUM_CONSTRAINT_ERROR || errorName === GUM_OVERCONSTRAINT_ERROR)) {
                  LogSvc.debug('[DeviceSettingsSvc]: Failed to access local media with constraints: ', constraints.video);
                  getLocalMedia();
                  return;
                }
                LogSvc.warn('[DeviceSettingsSvc]: Max camera resolution could not be determined. Error: ', errorName);
                reject(errorName);
              }
            );
          };

          getLocalMedia();
        });
      });
    };

    /*
     * Checks if device matches the given Id. Assures correct identification of default device
     * (id may be null or 'default')
     * @param {object} device The device to check.
     * @param {string} id The ID that the device should be matched with.
     * @return {boolean} true if device matches ID.
     */
    this.isSameDevice = isSameDevice;

    /* Get source from ID
     *
     * @param {array} sources The device sources to check.
     * @param {string} id The device ID.
     * @return {object} source If device matches ID.
     */
    this.getSourceFromId = getSourceFromId;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initializations
    ///////////////////////////////////////////////////////////////////////////////////////
    init();

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.DeviceSettingsSvcImpl = DeviceSettingsSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var ContactCardMessage = unifyPhone.Enums.ContactCardMessage;
  var ContactCardResponseCode = unifyPhone.Enums.ContactCardResponseCode;
  var UserToUserHandler = unifyPhone.UserToUserHandlerSingleton;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-params, max-lines-per-function
  function ContactCardSvcImpl($rootScope, $q, LogSvc, UtilSvc, PubSubSvc, PopupSvc, UserSvc, ConversationSvc, CallControlSvc, ExtensionSvc) {
    // NOSONAR

    LogSvc.debug('New Service: ContactCardSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var START_CALL_TIME_OUT = 30000;
    var ANSWER_CALL_TIME_OUT = 30000;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _processingPendingRequest = false;
    var _phoneCallsAvailable = false;
    var _userToUserHandler = UserToUserHandler.getInstance();

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function isClientBusy(cb) {
      var activeCall = CallControlSvc.getActiveCall();
      if ((activeCall && activeCall.isPresent()) || _processingPendingRequest) {
        cb(ContactCardResponseCode.BUSY);
        return true;
      }
      return false;
    }

    function onCallInitiated(cb, error, warn, call) {
      _processingPendingRequest = false;
      if (error || warn) {
        PopupSvc.handleCallError(error, warn);
        if (error) {
          cb(ContactCardResponseCode.CALL_FAILED);
          return;
        }
      }
      cb(ContactCardResponseCode.OK, call && call.callId);
    }

    function startTelephonyCall(contactNumber, contactName, mediaType, cb) {
      if (isClientBusy(cb)) {
        return;
      }
      if (!_phoneCallsAvailable) {
        cb(ContactCardResponseCode.TELEPHONY_NOT_AVAILABLE);
        return;
      }

      _processingPendingRequest = true;
      ConversationSvc.getTelephonyConversation(function (err, conv) {
        if (err || !conv) {
          _processingPendingRequest = false;
          cb(ContactCardResponseCode.CONVERSATION_NOT_FOUND);
        } else {
          var destination = {
            dialedDn: contactNumber,
            toName: contactName,
            mediaType: mediaType
          };
          CallControlSvc.dialUsingDefaultDevice(destination, onCallInitiated.bind(null, cb));
          LogSvc.debug('[ContactCardSvc]: Publish /conversation/navigate event with convId = ', conv.convId);
          PubSubSvc.publish('/conversation/navigate', [conv]);
        }
      });
    }

    function sendResponse(type, destUserId, destClientId, reqId, respCode, data) {
      if (!destClientId || !reqId) {
        LogSvc.error('[ContactCardSvc]: sendResponse invoked without required parameters');
        return;
      }
      LogSvc.debug('[ContactCardSvc]: Send response for ', type);

      var response = {
        content: {
          type: type,
          requestId: reqId,
          response: {
            code: respCode || ContactCardResponseCode.OK,
            data: data
          }
        },
        destUserId: destUserId,
        destClientId: destClientId
      };
      _userToUserHandler.sendContactCardRequest(response);
    }

    function normalizeMediaType(mediaType) {
      switch (mediaType) {
        case 'telephony':
        case 'audio':
          return { audio: true, video: false, desktop: false };
        case 'video':
          return { audio: true, video: true, desktop: false };
        default:
          return null;
      }
    }

    function invokeCallControlApi(apiName, data, cb) {
      if (isClientBusy(cb)) {
        return;
      }

      var normalizedMediaType = normalizeMediaType(data.mediaType || 'audio');
      _processingPendingRequest = true;
      CallControlSvc[apiName](data.callId || data.convId, normalizedMediaType, onCallInitiated.bind(null, cb));
      ExtensionSvc.bringToFront();
    }

    function handleClickToCallRequest(data, cb) {
      var normalizedMediaType = normalizeMediaType(data.mediaType);
      startTelephonyCall(data.contact.phoneNumber, data.contact.displayName, normalizedMediaType, cb);
      ExtensionSvc.bringToFront();
    }

    function handleGenericNavigate(data, cb) {
      if (data.path === 'myprofile') {
        navigateUser($rootScope.localUser.userId, cb);
        return;
      }
      var path = data.path.split(':');
      if (path.length !== 2) {
        LogSvc.error('[ContactCardSvc]: Incorrect path in NAVIGATE_REQUEST request: ', data.path);
        cb(ContactCardResponseCode.NAVIGATE_REQUEST_FAILED);
        return;
      }
      var type = path[0];
      var action = path[1];
      switch (type) {
        case 'settings':
          navigateSettings(action, cb);
          break;
        case 'user':
          navigateUser(action, cb);
          break;
        default:
          LogSvc.error('[ContactCardSvc]: Incorrect path in NAVIGATE_REQUEST request: ', data.path);
          cb(ContactCardResponseCode.NAVIGATE_REQUEST_FAILED);
      }
    }

    function navigateSettings(tab, cb) {
      ExtensionSvc.bringToFront();
      $rootScope.openView('settings', { settings: { selectedTab: tab } });
      cb(ContactCardResponseCode.OK);
    }

    function navigateUser(userId, cb) {
      ExtensionSvc.bringToFront();
      if (userId === $rootScope.localUser.userId) {
        $rootScope.openView('myprofile');
        cb(ContactCardResponseCode.OK);
        return;
      }
      UserSvc.getUserPromise(userId)
        .then(function () {
          $rootScope.viewUserProfile(userId);
          cb(ContactCardResponseCode.OK);
        })
        .catch(function (err) {
          LogSvc.warn('[ContactCardSvc] Failed to retrieve user profile. ', err);
          cb(ContactCardResponseCode.NAVIGATE_REQUEST_FAILED);
        });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    function onTelephonyData(data) {
      LogSvc.debug('[ContactCardSvc] Received /telephony/data event. phoneCallsAvailable = ', data.phoneCallsAvailable);
      _phoneCallsAvailable = data.phoneCallsAvailable;
    }

    PubSubSvc.subscribe('/telephony/data', onTelephonyData);

    ///////////////////////////////////////////////////////////////////////////////////////
    // Client API Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    if (!Utils.isMobile()) {
      // CLICK_TO_CALL_REQUEST, CLICK_TO_ANSWER_REQUEST and NAVIGATE_REQUEST are only applicable for web and desktop app.
      _userToUserHandler.on('CONTACT_CARD.CLICK_TO_CALL_REQUEST', function (data, routing) {
        try {
          LogSvc.debug('[ContactCardSvc]: Received CLICK_TO_CALL_REQUEST with data: ', data);

          var onResponseHandler = sendResponse.bind(null, ContactCardMessage.CLICK_TO_CALL_RESPONSE, routing.srcUserId, routing.srcClientId, data.requestId);

          if (!data.contact || data.mediaType !== 'telephony') {
            LogSvc.error('[ContactCardSvc]: Missing contact object in CLICK_TO_CALL_REQUEST request');
            onResponseHandler(ContactCardResponseCode.CALL_FAILED);
            return;
          }

          if (data.promptBeforeDial) {
            var _startCallOptions = {
              message: data.sdk ? 'res_SdkStartCallText' : 'res_ContactCardStartCallText',
              title: 'res_StartCall',
              yesLabel: 'res_Call',
              noLabel: 'res_Cancel',
              timeOut: START_CALL_TIME_OUT,
              backdrop: true
            };

            ExtensionSvc.bringToFront();
            PopupSvc.confirm(_startCallOptions)
              .result.then(function () {
                handleClickToCallRequest(data, onResponseHandler);
              })
              .catch(function () {
                onResponseHandler(ContactCardResponseCode.CALL_DECLINED);
              });
          } else {
            handleClickToCallRequest(data, onResponseHandler);
          }
        } catch (e) {
          LogSvc.error('[ContactCardSvc]: Exception handling CLICK_TO_CALL_REQUEST event. ', e);
        }
      });

      _userToUserHandler.on('CONTACT_CARD.CLICK_TO_ANSWER_REQUEST', function (data, routing) {
        try {
          LogSvc.debug('[ContactCardSvc]: Received CLICK_TO_ANSWER_REQUEST with data: ', data);

          var onResponseHandler = sendResponse.bind(null, ContactCardMessage.CLICK_TO_ANSWER_RESPONSE, routing.srcUserId, routing.srcClientId, data.requestId);

          if (data.promptBeforeAnswer) {
            var _answerCallOptions = {
              message: 'res_SdkAnswerCallText',
              title: 'res_AnswerCall',
              yesLabel: 'res_Answer',
              noLabel: 'res_Cancel',
              timeOut: ANSWER_CALL_TIME_OUT,
              backdrop: true
            };

            ExtensionSvc.bringToFront();
            PopupSvc.confirm(_answerCallOptions)
              .result.then(function () {
                invokeCallControlApi('answerCall', data, onResponseHandler);
              })
              .catch(function () {
                onResponseHandler(ContactCardResponseCode.CALL_DECLINED);
              });
          } else {
            invokeCallControlApi('answerCall', data, onResponseHandler);
          }
        } catch (e) {
          LogSvc.error('[ContactCardSvc]: Exception handling CLICK_TO_ANSWER_REQUEST event. ', e);
        }
      });

      _userToUserHandler.on('CONTACT_CARD.NAVIGATE_REQUEST', function (data, routing) {
        try {
          LogSvc.debug('[ContactCardSvc]: Received NAVIGATE_REQUEST with data: ', data);

          var onResponseHandler = sendResponse.bind(null, ContactCardMessage.NAVIGATE_REQUEST_RESPONSE, routing.srcUserId, routing.srcClientId, data.requestId);

          if (!data.path) {
            LogSvc.error('[ContactCardSvc]: Missing path in NAVIGATE_REQUEST request');
            onResponseHandler(ContactCardResponseCode.NAVIGATE_REQUEST_FAILED);
            return;
          }
          $rootScope.$apply(function () {
            handleGenericNavigate(data, onResponseHandler);
          });
        } catch (e) {
          LogSvc.error('[ContactCardSvc]: Exception handling NAVIGATE_REQUEST request. ', e);
        }
      });
    }

    _userToUserHandler.on('CONTACT_CARD.MUTE_DEVICE_REQUEST', function (data, routing) {
      try {
        LogSvc.debug('[ContactCardSvc]: Received MUTE_DEVICE_REQUEST with data: ', data);

        var onResponseHandler = sendResponse.bind(null, ContactCardMessage.MUTE_DEVICE_RESPONSE, routing.srcUserId, routing.srcClientId, data.requestId);

        if (!data.option) {
          LogSvc.error('[ContactCardSvc]: Missing option object in MUTE_DEVICE_REQUEST request');
          onResponseHandler(ContactCardResponseCode.MUTE_DEVICE_FAILED);
          return;
        }
        if (!data.callId) {
          LogSvc.error('[ContactCardSvc]: Missing callId in MUTE_DEVICE_REQUEST request');
          onResponseHandler(ContactCardResponseCode.MUTE_DEVICE_FAILED);
          return;
        }

        var callId = data.callId;

        var localCall = CallControlSvc.getActiveCall(callId);
        if (!localCall) {
          LogSvc.debug('[ContactCardSvc]: Could not find corresponding local call');
          onResponseHandler(ContactCardResponseCode.CALL_NOT_FOUND);
          return;
        }

        var promises = [];

        // Speaker (not a promise)
        var speakerFn = data.option.speaker ? CallControlSvc.disableRemoteAudio : CallControlSvc.enableRemoteAudio;
        speakerFn(callId);

        // Mic (a promise)
        if (localCall.locallyMuted !== data.option.mic) {
          var micFn = UtilSvc.promisify(data.option.mic ? CallControlSvc.mute : CallControlSvc.unmute, CallControlSvc);
          promises.push(micFn(callId));
        }

        $q.all(promises)
          .then(function () {
            LogSvc.info('[ContactCardSvc]: Successfully muted client and disabled remote audio');
            onResponseHandler();
          })
          .catch(function (err) {
            LogSvc.error('[ContactCardSvc]: MUTE_DEVICE_REQUEST execution failed: ', err);
            onResponseHandler(ContactCardResponseCode.MUTE_DEVICE_FAILED);
          });
      } catch (e) {
        LogSvc.error('[ContactCardSvc]: Exception handling MUTE_DEVICE_REQUEST request. ', e);
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.ContactCardSvcImpl = ContactCardSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global require*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  var TranslationProvider = {
    GOOGLE: 'Google',
    SYSTRAN: 'Systran'
  };

  // eslint-disable-next-line max-params, max-lines-per-function
  function TranslationSvcImpl($q, $http, $rootScope, LogSvc, PubSubSvc, LocalStoreSvc, LocalizeSvc) {
    // NOSONAR
    LogSvc.debug('New Service: TranslationSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////

    /////////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    /////////////////////////////////////////////////////////////////////////////////////////
    var _self = this;
    var _translationSettings = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.TRANSLATION) || {
      selectedProvider: TranslationProvider.GOOGLE,
      apiKeys: {}
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function hasApiKey() {
      return !!_translationSettings.apiKeys[_translationSettings.selectedProvider];
    }

    function setRequestUrl(text, language, isHtml) {
      var provider = _translationSettings.selectedProvider;
      var apiKey = _translationSettings.apiKeys[provider];
      if (!apiKey) {
        return $q.reject('res_MissingTranslationApiKey');
      }

      var url;
      switch (provider) {
        case TranslationProvider.GOOGLE:
          url =
            'https://translation.googleapis.com/language/translate/v2?q=' +
            encodeURIComponent(text) +
            '&target=' +
            language +
            '&format=' +
            (isHtml ? 'html' : 'text') +
            '&key=' +
            apiKey;
          break;
        case TranslationProvider.SYSTRAN:
          if (language === 'zh') {
            language = 'zh-Hans';
          }
          url =
            'https://api-platform.systran.net/translation/text/translate?input=' +
            encodeURIComponent(text) +
            '&source=auto&target=' +
            language +
            '&key=' +
            apiKey;
          break;
        default:
          LogSvc.warn('[TranslationSvc]: Unsupported translation provider: ', provider);
          return $q.reject('Invalid provider');
      }
      return $q.resolve(url);
    }

    function sendRequest(url) {
      return $http.get(url).catch(function (error) {
        LogSvc.error('[TranslationSvc]: Failed to translate text. ', error.data);
        return $q.reject(error.statusText || error.status);
      });
    }

    function processGoogleResponse(data) {
      try {
        var translation = data.data.translations[0];
        if (translation) {
          LogSvc.debug('[TranslationSvc]: Translation was successful');
          return $q.resolve({
            translatedText: translation.translatedText,
            detectedSourceLanguage: translation.detectedSourceLanguage
          });
        }
      } catch (err) {}
      LogSvc.warn('[TranslationSvc]: Unexpected translation response. ', data);
      return $q.reject('Unexpected response');
    }

    function processSystranResponse(data) {
      try {
        var translation = data.outputs[0];
        if (translation) {
          LogSvc.debug('[TranslationSvc]: Translation was successful');
          return $q.resolve({
            translatedText: translation.output,
            detectedSourceLanguage: translation.detectedLanguage
          });
        }
      } catch (err) {}
      LogSvc.warn('[TranslationSvc]: Unexpected translation response. ', data);
      return $q.reject('Unexpected response');
    }

    function processTranslationResponse(resp) {
      switch (_translationSettings.selectedProvider) {
        case TranslationProvider.GOOGLE:
          return processGoogleResponse(resp.data);
        case TranslationProvider.SYSTRAN:
          return processSystranResponse(resp.data);
        default:
          return $q.reject('Invalid provider');
      }
    }

    function translate(text, language, isHtml) {
      if (!text || !language) {
        return $q.reject('Invalid input');
      }
      if (!$rootScope.unifyPhoneLabs.TRANSCRIPTION_AND_TRANSLATION) {
        return $q.reject('Feature not enabled');
      }

      LogSvc.debug('[TranslationSvc]: Send translation request to ', _translationSettings.selectedProvider);

      return setRequestUrl(text, language, isHtml).then(sendRequest).then(processTranslationResponse);
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/call/liveTranscription', function (call, transcription) {
      if ($rootScope.unifyPhoneLabs.TRANSCRIPTION_AND_TRANSLATION && hasApiKey() && !transcription.intermediate) {
        LogSvc.debug('[TranslationSvc]: Received /call/liveTranscription event');

        if (transcription.userId === $rootScope.localUser.userId) {
          LogSvc.debug('[TranslationSvc]: This is a transcription for the local user. Ignore translation.');
          return;
        }

        var language = LocalizeSvc.getLanguageOnly();
        LogSvc.debug('[TranslationSvc]: Translate transcription to ', language);

        _self
          .translate(transcription.text, language, false)
          .then(function (data) {
            if (data.detectedSourceLanguage === language) {
              LogSvc.debug('[TranslationSvc]: Detected source language for transcription matches requested language. Ignore translation.');
            } else {
              LogSvc.debug('[TranslationSvc]: Add translation to transcription. Detected source language was ', data.detectedSourceLanguage);
              transcription.translatedText = data.translatedText;
              // NOTE: Since this is not yet supported by mobile clients we don't need to raise an event
            }
          })
          .catch(function (err) {
            LogSvc.debug('[TranslationSvc]: Failed to translate transcription. ', err);
          });
      }
    });

    /////////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    /////////////////////////////////////////////////////////////////////////////////////////
    this.isTranslationPossible = function () {
      if (!$rootScope.unifyPhoneLabs.TRANSCRIPTION_AND_TRANSLATION) {
        return false;
      }
      var provider = _translationSettings.selectedProvider;
      var apiKey = _translationSettings.apiKeys[provider];
      return !!apiKey;
    };

    this.translate = translate;

    this.setTranslationProvider = function (provider) {
      if (!Object.values(TranslationProvider).includes(provider)) {
        return null;
      }
      if (_translationSettings.selectedProvider !== provider) {
        LogSvc.debug('[TranslationSvc]: Set translation provider to ', provider);
        _translationSettings.selectedProvider = provider;
        LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.TRANSLATION, _translationSettings);
      }
      // Return API key for selected provider
      return _translationSettings.apiKeys[_translationSettings.selectedProvider];
    };

    this.setProviderApiKey = function (provider, apiKey) {
      if (!Object.values(TranslationProvider).includes(provider)) {
        return;
      }
      if (apiKey) {
        LogSvc.debug('[TranslationSvc]: Set translation API key for ', provider);
        _translationSettings.apiKeys[provider] = apiKey;
      } else {
        LogSvc.debug('[TranslationSvc]: Clear translation API key for ', provider);
        delete _translationSettings.apiKeys[provider];
      }
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.TRANSLATION, _translationSettings);
    };

    this.getSettings = function () {
      return {
        provider: _translationSettings.selectedProvider,
        apiKey: _translationSettings.apiKeys[_translationSettings.selectedProvider] || ''
      };
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.TranslationSvcImpl = TranslationSvcImpl;
  unifyPhone.TranslationProvider = TranslationProvider;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global Base64, Uint8Array*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  var AudioRecorder = unifyPhone.AudioRecorder;
  var RecordingState = unifyPhone.Constants.RecordingState;
  var TranscriptionError = unifyPhone.Constants.TranscriptionError;

  var TranscriptionProvider = {
    GOOGLE: 'Google'
  };

  // eslint-disable-next-line max-params, max-lines-per-function
  function TranscriptionSvcImpl($rootScope, $q, LogSvc, PubSubSvc, LocalStoreSvc, LocalizeSvc) {
    // NOSONAR
    LogSvc.debug('New Service: TranscriptionSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var WAV_HDR_LEN = 44; // Length of WAV header
    var SAMPLE_RATE_16KHZ = 16000;
    var DEFAULT_SILENCE_DETECTION_TIME = 3000;
    var REALTIME_SILENCE_DETECTION_TIME = 1000;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _config = {};
    var _recorder;
    var _audioSupported = AudioRecorder.isSupported();
    var _recordingState = RecordingState.IDLE;

    var _startTime;
    var _onStateChange;
    var _activeRecordingResolve;
    var _activeRecordingReject;

    var _transcriptionSettings = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.TRANSCRIPTION) || {
      selectedProvider: TranscriptionProvider.GOOGLE,
      apiKeys: {},
      language: 'en-US'
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function setState(newState) {
      if (_recordingState !== newState) {
        LogSvc.debug('[TranscriptionSvc]: Changed state to ', newState);
        _recordingState = newState;
        try {
          _onStateChange && _onStateChange(newState);
        } catch (e) {}
      }
    }

    function destroyRecorder() {
      if (_recorder) {
        _recorder.stop();
        _recorder = null;
      }
    }

    function resetData() {
      destroyRecorder();
      setState(RecordingState.IDLE);
      if (_config.call) {
        _config.call.localTranscriptionEnabled = false;
      }
      _config = {};
      _onStateChange = null;
      _activeRecordingReject = null;
      _activeRecordingResolve = null;
    }

    function transcribeAudioGoogle(audioInput, audioLength, apiKey) {
      var gReq = new XMLHttpRequest();
      gReq.open('POST', 'https://speech.googleapis.com/v1/speech:recognize?key=' + apiKey, true);

      gReq.addEventListener('load', function () {
        var data = JSON.parse(gReq.response);
        var transcript = (data.results && data.results[0] && data.results[0].alternatives[0].transcript) || '';
        if (transcript) {
          transcript = transcript.substring(0, 1).toUpperCase() + transcript.substring(1);
        }
        var response = {
          sampleRate: SAMPLE_RATE_16KHZ,
          roundtrip: parseFloat((Date.now() - _startTime) / 1000).toFixed(3),
          audioLength: audioLength,
          transcript: transcript,
          keysToOmitFromLogging: ['transcription']
        };
        LogSvc.debug('[TranscriptionSvc]: Google Speech response: ', response);
        if (!_config.keepRecording) {
          _activeRecordingResolve && _activeRecordingResolve(response);
          resetData();
        }
      });

      gReq.addEventListener('error', function (error) {
        LogSvc.error('[TranscriptionSvc]: Google Speech error: ', error);
        if (!_config.keepRecording) {
          _activeRecordingReject && _activeRecordingReject(error);
          resetData();
        }
      });

      var reader = new FileReader();
      reader.addEventListener('loadend', function () {
        // RUE convert WAV to raw LINEAR16 PCM base64 encoded
        // reader.result contains the contents of blob as a typed array
        var b64audio = Base64.fromByteArray(new Uint8Array(reader.result));

        LogSvc.debug('[TranscriptionSvc]: Send request to Google Speech API');
        gReq.send(
          JSON.stringify({
            config: {
              encoding: 'LINEAR16',
              sampleRateHertz: SAMPLE_RATE_16KHZ,
              languageCode: _transcriptionSettings.language || 'en-US',
              enableAutomaticPunctuation: true,
              profanityFilter: true,
              speechContexts: [
                {
                  phrases: ['Unify', 'Atos', 'UnifyPhone']
                }
              ]
            },
            audio: {
              content: b64audio
            }
          })
        );
      });
      reader.readAsArrayBuffer(audioInput.slice(WAV_HDR_LEN));
    }

    function transcribeAudio(audioInput) {
      var duration = Date.now() - _startTime;

      if (_config.keepRecording) {
        // If we need to keep recording restart the start time.
        _startTime = Date.now();
      } else {
        // If we don't need to keep recording, change state and destroy recorder.
        setState(RecordingState.PROCESSING);
        destroyRecorder();
      }

      // If duration is less than silence detection time it means the entire recording
      // is silence. Add 400 ms to silence detection time to consider short noises and delay.
      if (duration < _config.silenceDetectionTime + 400) {
        LogSvc.debug('[TranscriptionSvc]: No input detected');
        if (!_config.keepRecording) {
          var response = {
            audioLength: 0,
            transcript: ''
          };
          _activeRecordingResolve && _activeRecordingResolve(response);
          resetData();
        }
        return;
      }

      var audioLength = parseFloat(duration / 1000).toFixed(3);
      var provider = _transcriptionSettings.selectedProvider;
      var apiKey = _transcriptionSettings.apiKeys[provider];

      if (provider === TranscriptionProvider.GOOGLE) {
        transcribeAudioGoogle(audioInput, audioLength, apiKey);
      } else {
        LogSvc.warn('[TranscriptionSvc]: Unsupported transcription provider: ', provider);
        _activeRecordingReject && _activeRecordingReject(TranscriptionError.INVALID_PROVIDER);
        resetData();
      }
    }

    function exportWAV() {
      if (_recordingState === RecordingState.LISTENING && _recorder) {
        _recorder.exportWAV(function (blob) {
          $rootScope.$apply(function () {
            transcribeAudio(blob);
          });
        }, _config.sampleRate || SAMPLE_RATE_16KHZ);
      }
    }

    function onSilenceDetected() {
      if (_recordingState === RecordingState.LISTENING && _recorder) {
        $rootScope.$apply(function () {
          LogSvc.debug('[TranscriptionSvc]: Silence has been detected');
          exportWAV();
        });
      }
    }

    function isRecordingSupported(reject) {
      if (!$rootScope.unifyPhoneLabs.TRANSCRIPTION_AND_TRANSLATION || !_audioSupported) {
        LogSvc.warn('[TranscriptionSvc]: Recording is not supported');
        reject && reject(TranscriptionError.RECORDING_NOT_SUPPORTED);
        return false;
      }
      return true;
    }

    function hasActiveRecording(reject) {
      if (_recordingState !== RecordingState.LISTENING || !_recorder) {
        LogSvc.warn('[TranscriptionSvc]: Recording has not been started');
        reject(TranscriptionError.RECORDING_NOT_STARTED);
        return false;
      }
      return true;
    }

    function isRecordingPossible(reject) {
      if (!isRecordingSupported(reject)) {
        return false;
      }
      var provider = _transcriptionSettings.selectedProvider;
      var apiKey = _transcriptionSettings.apiKeys[provider];
      if (!apiKey) {
        reject && reject('res_MissingTranscriptionApiKey');
        return false;
      }
      if (_recordingState !== RecordingState.IDLE || _recorder) {
        LogSvc.warn('[TranscriptionSvc]: Recording is already is progress');
        reject && reject(TranscriptionError.RECORDING_IN_PROGRESS);
        return false;
      }
      return true;
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/call/ended', function (call) {
      if (_config.call && _config.call.callId === call.callId) {
        LogSvc.debug('[TranscriptionSvc]: Received /call/ended event. Terminate real time transcription.');
        resetData();
      }
    });

    PubSubSvc.subscribe('/conversation/item/add', function (item) {
      if (_config.call && _config.call.instanceId === item.rtcInstanceId && !_config.threadId) {
        LogSvc.debug('[TranscriptionSvc]: Received /conversation/item/add for transcription thread. Save threadId.');
        _config.threadId = item.parentId || item.itemId;
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.isRecordingPossible = function () {
      return isRecordingPossible();
    };

    this.getState = function () {
      return _recordingState;
    };

    this.startRecording = function (onStateChange, onAudioData, config) {
      return new $q(function (resolve, reject) {
        LogSvc.debug('[TranscriptionSvc]: Start recording request');
        if (!isRecordingPossible(reject)) {
          return;
        }
        _config = config || {};
        _config.silenceDetectionTime = _config.silenceDetectionTime || DEFAULT_SILENCE_DETECTION_TIME;
        _config.keeRecording = false;
        _onStateChange = onStateChange || Function.prototype;

        LogSvc.debug('[TranscriptionSvc]: Create Recorder instance and start recording');

        _recorder = AudioRecorder.createRecorder(_config);
        _recorder.record(onSilenceDetected, onAudioData);
        _startTime = Date.now();
        setState(RecordingState.LISTENING);

        _activeRecordingResolve = resolve;
        _activeRecordingReject = reject;
      });
    };

    this.pauseRecording = function () {
      return new $q(function (resolve, reject) {
        LogSvc.debug('[TranscriptionSvc]: Pause recording request');
        if (hasActiveRecording(reject)) {
          _recorder.pause();
          resolve();
        }
      });
    };

    this.stopRecording = function () {
      return new $q(function (resolve, reject) {
        LogSvc.debug('[TranscriptionSvc]: Stop recording request');
        if (hasActiveRecording(reject)) {
          exportWAV();
          resolve();
        }
      });
    };

    this.abortRecording = function () {
      LogSvc.debug('[TranscriptionSvc]: Abort recording request');
      _activeRecordingReject && _activeRecordingReject(TranscriptionError.RECORDING_ABORTED);
      resetData();
    };

    this.enableCallTranscription = function (call, onAudioData) {
      return new $q(function (resolve, reject) {
        if (!call || call.isRemote || !call.isEstablished()) {
          reject(TranscriptionError.INVALID_CALL);
          return;
        }
        LogSvc.debug('[TranscriptionSvc]: Enable transcription for active call');

        if (!isRecordingPossible(reject)) {
          return;
        }

        call.localTranscriptionEnabled = true;
        _config = {
          call: call,
          stream: call.sessionCtrl.getLocalStream(unifyPhone.RtcSessionController.LOCAL_STREAMS.AUDIO_VIDEO),
          silenceDetectionTime: REALTIME_SILENCE_DETECTION_TIME,
          keepRecording: true
        };

        _onStateChange = Function.prototype;

        LogSvc.debug('[TranscriptionSvc]: Create Recorder instance and start recording');
        _recorder = AudioRecorder.createRecorder(_config);
        _recorder.record(onSilenceDetected, onAudioData);
        _startTime = Date.now();
        setState(RecordingState.LISTENING);
        resolve();
      });
    };

    this.disableCallTranscription = function (callId) {
      return new $q(function (resolve, reject) {
        if (!callId || !_config.call || callId !== _config.call.callId) {
          reject(TranscriptionError.INVALID_CALL);
        } else {
          resetData();
          resolve();
        }
      });
    };

    this.setTranscriptionProvider = function (provider) {
      if (!Object.values(TranscriptionProvider).includes(provider)) {
        return null;
      }
      if (_transcriptionSettings.selectedProvider !== provider) {
        LogSvc.debug('[TranscriptionSvc]: Set translation provider to ', provider);
        _transcriptionSettings.selectedProvider = provider;
        LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.TRANSCRIPTION, _transcriptionSettings);
      }
      // Return API key for selected provider
      return _transcriptionSettings.apiKeys[_transcriptionSettings.selectedProvider];
    };

    this.setProviderApiKey = function (provider, apiKey) {
      if (!Object.values(TranscriptionProvider).includes(provider)) {
        return;
      }
      if (apiKey) {
        LogSvc.debug('[TranscriptionSvc]: Set translation API key for ', provider);
        _transcriptionSettings.apiKeys[provider] = apiKey;
      } else {
        LogSvc.debug('[TranscriptionSvc]: Clear translation API key for ', provider);
        delete _transcriptionSettings.apiKeys[provider];
      }
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.TRANSCRIPTION, _transcriptionSettings);
    };

    this.setTranscriptionLanguage = function (language) {
      if (!language || !LocalizeSvc.getBridgeLanguageByValue(language)) {
        return;
      }
      _transcriptionSettings.language = language;
      LogSvc.debug('[TranscriptionSvc]: Set transcription language to ', language);
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.TRANSCRIPTION, _transcriptionSettings);
    };

    this.getSettings = function () {
      return {
        provider: _transcriptionSettings.selectedProvider,
        apiKey: _transcriptionSettings.apiKeys[_transcriptionSettings.selectedProvider] || '',
        language: _transcriptionSettings.language || 'en-US'
      };
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.TranscriptionSvcImpl = TranscriptionSvcImpl;
  unifyPhone.TranscriptionProvider = TranscriptionProvider;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;

  // eslint-disable-next-line max-params, max-lines-per-function
  function MeetingSvcImpl($q, $timeout, LogSvc, PubSubSvc, MailboxConnSvc, ConversationSvc, CallControlSvc) {
    // NOSONAR
    LogSvc.debug('New Service: MeetingSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var DEFAULT_CONV_AVATAR = 'content/images/icon-general-emptyconvo-avatar';

    var ONE_MIN = 60000;
    // ANS-76595 Google Connector gets disconnected randomly.
    // Setting to 31 minutes to avoid requests to be sent to the calendar servers on a hourly pace
    // Google Calendar authentication tokens expire every hour
    // Sending requests close to the expire time makes the Google Calendar API
    // to respond randomly with authentication error
    var UPDATE_PERIOD = 31 * ONE_MIN; // update meetings every 31 minutes
    var MAX_CHECK_TIMEOUT = 5 * ONE_MIN;

    var DELTA_BEFORE_MEETING_STARTS = 15 * ONE_MIN;
    var DELTA_AFTER_MEETING_STARTS = 10 * ONE_MIN;

    var GUEST_REGEXP = /\/guest\?token=([^&]+)/;
    var CONV_REGEXP = /\/#\/conversation\/([^/?]+)/;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _hasMeetingAboutToStart = false;
    var _meetings = [];

    var _lastUpdate = 0;
    var _checkMeetingsTimeout = null;
    var _resyncTimeout = null;

    var _retrieveInProgress = false;
    var _pendingCallbacks = [];
    var _self = this;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function cancelCheckMeetingsTimeout() {
      if (_checkMeetingsTimeout) {
        $timeout.cancel(_checkMeetingsTimeout);
        _checkMeetingsTimeout = null;
      }
    }

    function cancelResyncTimeout() {
      if (_resyncTimeout) {
        $timeout.cancel(_resyncTimeout);
        _resyncTimeout = null;
      }
    }

    function resetMeetings() {
      cancelCheckMeetingsTimeout();
      cancelResyncTimeout();

      _meetings = [];
      _hasMeetingAboutToStart = false;
      _lastUpdate = 0;
    }

    function removeFinishedMeetings(initialCheck) {
      var now = Date.now();

      var numPreviousMeetings = _meetings.length;
      _meetings = _meetings.filter(function (meeting) {
        return meeting.endTime > now;
      });
      if (!initialCheck && _meetings.length < numPreviousMeetings) {
        LogSvc.debug('[MeetingSvc]: Removed finished meetings. Raise /meetings/updated event. meetings.length = ', _meetings.length);
        PubSubSvc.publish('/meetings/updated', [_meetings]);
      }
    }

    function checkMeetings(initialCheck) {
      cancelCheckMeetingsTimeout();

      // First remove meetings that have already finished
      removeFinishedMeetings(initialCheck);

      // Now check for meetings about to start
      if (_meetings.length === 0) {
        _hasMeetingAboutToStart = false;
        return;
      }

      var options = {
        startNotification: false,
        maxCheckTimeout: MAX_CHECK_TIMEOUT,
        deltaBeforeMeetingStarts: DELTA_BEFORE_MEETING_STARTS,
        deltaAfterMeetingStarts: DELTA_AFTER_MEETING_STARTS
      };
      var nextCheckDelay = _self.getNextMeetingCheckDelay(_meetings, options);

      if (_hasMeetingAboutToStart !== options.startNotification) {
        _hasMeetingAboutToStart = options.startNotification;
        LogSvc.debug('[MeetingSvc]: Raise /meeting/toggleStartNotification event. hasMeetingAboutToStart = ', _hasMeetingAboutToStart);
        PubSubSvc.publish('/meeting/toggleStartNotification', [_hasMeetingAboutToStart]);
      }

      LogSvc.debug('[MeetingSvc]: Check conversations again in ' + Math.floor(nextCheckDelay / 1000) + ' seconds.');

      _checkMeetingsTimeout = $timeout(function () {
        _checkMeetingsTimeout = null;
        checkMeetings(false);
      }, nextCheckDelay);
    }

    function processMeetingUrl(meetingUrl) {
      return new $q(function (resolve, reject) {
        LogSvc.debug('[MeetingSvc]: Process meeting URL: ', meetingUrl);

        var match = GUEST_REGEXP.exec(meetingUrl);
        var token = match && match[1];
        if (token) {
          // This is a guest link
          ConversationSvc.validateSessionInviteToken(token, function (err, res) {
            // Do not show meetings with invalid links.
            if (err || res.validateResult === Constants.SessionInviteInfoResult.INVALID) {
              reject('Failed to validate token');
              return;
            }
            resolve({
              conv: {
                convId: res.convId,
                topic: res.convTopic,
                avatar: {
                  avatar: DEFAULT_CONV_AVATAR + '.png',
                  avatarLarge: DEFAULT_CONV_AVATAR + '-XL.png',
                  hasAvatarPicture: false
                }
              },
              convId: res.convId,
              isGuest: true,
              guestToken: token
            });
          });
          return;
        }

        match = CONV_REGEXP.exec(meetingUrl);
        var convId = match && match[1];
        if (convId) {
          resolve({ convId: convId });
          return;
        }

        reject('Invalid meeting URL');
      });
    }

    function getMeetingConversation(convSummary) {
      return ConversationSvc.getConversationPromise(convSummary.convId)
        .then(function (conv) {
          convSummary.conv = conv;
          convSummary.isGuest = !conv.hasJoined;
          return $q.resolve(convSummary);
        })
        .catch(function () {
          // If this is a guest conversation, just return the conversation summary
          return convSummary.isGuest ? $q.resolve(convSummary) : $q.reject('Cannot access conversation');
        });
    }

    function invokePendingCbs() {
      _pendingCallbacks.forEach(function (cb) {
        cb && cb(_meetings);
      });
      _pendingCallbacks = [];
    }

    function resyncMeetings() {
      cancelResyncTimeout();

      if (CallControlSvc.getActiveCall()) {
        // Skip resync to reduce freezes seen with SOAP requests while on a call
        LogSvc.info('[MeetingSvc]: User is in local call. Skip resync of meetings.');
        // Restart the timeout
        _resyncTimeout = $timeout(function () {
          _resyncTimeout = null;
          resyncMeetings();
        }, UPDATE_PERIOD);
      } else {
        getMeetings();
      }
    }

    function getMeetings(cb) {
      if (!MailboxConnSvc.supportsGetAppointments()) {
        cb && cb(null);
        return;
      }

      cb && _pendingCallbacks.push(cb);

      if (_retrieveInProgress) {
        return;
      }

      LogSvc.info('[MeetingSvc]: Refresh meetings list');

      _retrieveInProgress = true;

      cancelCheckMeetingsTimeout();
      cancelResyncTimeout();

      _resyncTimeout = $timeout(function () {
        _resyncTimeout = null;
        resyncMeetings();
      }, UPDATE_PERIOD);

      // Get all incoming meetings for today
      // The second param (endTime) is 0 - to get meetings for today. The third (count) - to get all meetings.
      MailboxConnSvc.getAppointments(Date.now(), 0, 0, function (getAppointmentsErr, meetingsArr) {
        if (getAppointmentsErr) {
          _retrieveInProgress = false;
          _meetings = [];
          invokePendingCbs();
          LogSvc.debug('[MeetingSvc]: Failed to get appointments, show empty state.');
          PubSubSvc.publish('/meetings/updated', [_meetings]);
          return;
        }

        var promises = meetingsArr.map(function (meeting) {
          // Verify if we have already processed this meeting
          var existingMeeting = _meetings.find(function (m) {
            return meeting.itemId === m.itemId;
          });

          if (existingMeeting && existingMeeting.conversationLink === meeting.conversationLink) {
            // If the start time hasn't changed, return the existing object
            if (existingMeeting.startTime === meeting.startTime && existingMeeting.endTime === meeting.endTime) {
              return $q.resolve(existingMeeting);
            }
            // Update the new meeting with the existing conversation data
            meeting.conv = existingMeeting.conv;
            meeting.convId = existingMeeting.convId;
            meeting.isGuest = existingMeeting.isGuest;
            meeting.guestToken = existingMeeting.guestToken;
            meeting.keysToOmitFromLogging = existingMeeting.keysToOmitFromLogging;
            return $q.resolve(meeting);
          }

          return processMeetingUrl(meeting.conversationLink)
            .then(getMeetingConversation)
            .then(function (convSummary) {
              meeting.conv = convSummary.conv;
              meeting.convId = convSummary.convId;
              meeting.isGuest = convSummary.isGuest;
              meeting.guestToken = convSummary.guestToken;

              // Do not log the full conversation object
              meeting.keysToOmitFromLogging = ['subject', 'location', 'conv'];

              LogSvc.debug('[MeetingSvc]: Add meeting data to global panel: ', meeting);
              return meeting;
            })
            .catch(function (err) {
              LogSvc.warn('[MeetingSvc]: Failed to validate ' + meeting.conversationLink + '. ', err);
              return null;
            });
        });

        // Sort and show all meetings after get all meetings info.
        $q.all(promises).then(function (results) {
          _retrieveInProgress = false;

          _meetings = results
            .filter(function (meeting) {
              return !!meeting && (!meeting.isGuest || !!meeting.guestToken);
            })
            .sort(function (first, second) {
              return first.startTime - second.startTime;
            });

          checkMeetings(true);

          _lastUpdate = Date.now();

          LogSvc.debug('[MeetingSvc]: Finished processing meeting URLs. Found ' + _meetings.length + ' UnifyPhone meetings for today.');
          invokePendingCbs();

          LogSvc.debug('[MeetingSvc]: Raise /meetings/updated event');
          PubSubSvc.publish('/meetings/updated', [_meetings]);
        });
      });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc listeners
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/mailbox/connected', function () {
      LogSvc.debug('[MeetingSvc]: Received /mailbox/connected event');
      getMeetings();
    });

    PubSubSvc.subscribe('/mailbox/disconnected', function () {
      LogSvc.debug('[MeetingSvc]: Received /mailbox/disconnected event');
      resetMeetings();
    });

    PubSubSvc.subscribe('/conversation/update', function (conv) {
      if (conv.isTemporary || _meetings.length === 0) {
        // Don't process events if there are no meetings OR for temporary conversation
        return;
      }
      LogSvc.debug('[MeetingSvc]: Received /conversation/update event');

      var hasChanges = false;
      var numPreviousMeetings = _meetings.length;
      _meetings = _meetings.filter(function (meeting) {
        if (meeting.convId === conv.convId && meeting.conv !== conv) {
          meeting.conv = conv;
          meeting.isGuest = !conv.hasJoined;
          hasChanges = true;
        }
        return !meeting.isGuest || !!meeting.guestToken;
      });

      if (_meetings.length < numPreviousMeetings) {
        hasChanges = true;
        checkMeetings(true);
      }

      if (hasChanges) {
        LogSvc.debug('[MeetingSvc]: Raise /meetings/updated event. Number of meetings = ', _meetings.length);
        PubSubSvc.publish('/meetings/updated', [_meetings]);
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.supportsMeetings = MailboxConnSvc.supportsGetAppointments.bind(MailboxConnSvc);

    this.getMeetings = function (cb) {
      if (Date.now() - _lastUpdate < ONE_MIN) {
        // We have just updated the meetings
        cb && cb(_meetings);
        return;
      }
      getMeetings(cb);
    };

    this.hasMeetingAboutToStart = function () {
      return _hasMeetingAboutToStart;
    };

    this.getCachedMeetings = function () {
      return _meetings;
    };

    this.getNextMeetingCheckDelay = function (meetings, options) {
      if (Array.isArray(meetings)) {
        var now = Date.now();
        var nextCheckTime = now + options.maxCheckTimeout;
        meetings.forEach(function (meeting) {
          var beforeTime = meeting.startTime - options.deltaBeforeMeetingStarts;
          var afterTime = Math.min(meeting.startTime + options.deltaAfterMeetingStarts, meeting.endTime);

          if (!options.startNotification) {
            // Check if we need to present the meeting about to start notification
            options.startNotification = now >= beforeTime && now < afterTime;
          }

          if (beforeTime > now) {
            nextCheckTime = Math.min(nextCheckTime, beforeTime);
          } else if (afterTime > now) {
            nextCheckTime = Math.min(nextCheckTime, afterTime);
          } else if (meeting.endTime > now) {
            nextCheckTime = Math.min(nextCheckTime, meeting.endTime);
          }
        });
        return nextCheckTime - now;
      } else {
        return options.maxCheckTimeout;
      }
    };

    this.getMeetingStatus = function (meetings) {
      var aggregateMeeting;
      if (Array.isArray(meetings)) {
        var now = Date.now();
        meetings.forEach(function (meeting) {
          if (meeting.busyType && meeting.busyType.match(/busy/i)) {
            var startTime = meeting.startTime && new Date(meeting.startTime).getTime();
            var endTime = meeting.endTime && new Date(meeting.endTime).getTime();
            if (!aggregateMeeting && startTime && endTime && now >= startTime && now <= endTime) {
              aggregateMeeting = {
                startTime: meeting.startTime,
                endTime: meeting.endTime
              };
            }
            if (aggregateMeeting && meeting.startTime <= aggregateMeeting.endTime && meeting.endTime > aggregateMeeting.endTime) {
              aggregateMeeting.endTime = meeting.endTime;
            }
          }
        });
      }
      return aggregateMeeting;
    };
  }

  // Exports
  unifyPhone.MeetingSvcImpl = MeetingSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global Uint8Array*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;

  // eslint-disable-next-line max-params, max-lines-per-function
  function VoicemailSvcImpl($rootScope, $window, $q, $http, LogSvc, PubSubSvc, FileApiSvc, UserProfileSvc) {
    // NOSONAR
    LogSvc.debug('New Service: VoicemailSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var DEFAULT_VM_TIMEOUT = 20;
    var VM_SETTINGS = [
      Constants.UserSettingKey.VOICEMAIL_ENABLED,
      Constants.UserSettingKey.VOICEMAIL_NO_RECORDING,
      Constants.UserSettingKey.VOICEMAIL_TIMEOUT,
      Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETING_ENABLED,
      Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETING_URI,
      Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETINGS
    ];

    var _self = this;
    var _vmSettings = {};

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function getSettingValue(key, defaultValue) {
      var setting = _vmSettings[key];
      if (setting) {
        switch (setting.dataType) {
          case Constants.UserSettingDataType.BOOLEAN:
            return setting.booleanValue;
          case Constants.UserSettingDataType.NUMBER:
            return setting.numberValue;
          case Constants.UserSettingDataType.STRING:
            return setting.stringValue;
        }
      }
      return defaultValue;
    }

    function assignSettingValue(setting, value) {
      switch (setting.dataType) {
        case Constants.UserSettingDataType.BOOLEAN:
          setting.booleanValue = value;
          break;
        case Constants.UserSettingDataType.NUMBER:
          setting.numberValue = value;
          break;
        case Constants.UserSettingDataType.STRING:
          setting.stringValue = value;
          break;
      }
      return setting;
    }

    function setSettingValue(key, value) {
      var setting = _vmSettings[key];
      if (setting) {
        assignSettingValue(setting, value);
      }
    }

    function saveSetting(name, value) {
      var deferred = $q.defer();
      UserProfileSvc.saveSetting(name, value, function (err) {
        if (err) {
          deferred.reject(err);
        } else {
          setSettingValue(name, value);
          LogSvc.debug('[VoicemailSvc]: Successfully set ' + name + ' to ', value);
          deferred.resolve(value);
        }
      });
      return deferred.promise;
    }

    function saveAllSettings(keyValues) {
      var settings = [];
      Object.keys(keyValues).forEach(function (key) {
        var vmSetting = _vmSettings[key];
        var value = keyValues[key];
        if (vmSetting && value !== undefined) {
          settings.push(assignSettingValue(Object.assign({}, vmSetting), value));
        }
      });
      if (!settings.length) {
        return $q.reject('No settings provided');
      }
      return new $q(function (resolve, reject) {
        UserProfileSvc.saveSettings(settings, function (err) {
          if (err) {
            updateSettings(UserProfileSvc.getSettings());
            reject(err);
          } else {
            resolve();
          }
        });
      });
    }

    function updateSettings(settings) {
      VM_SETTINGS.forEach(function (name) {
        _vmSettings[name] = settings.find(function (setting) {
          return setting.key === name;
        });
      });
    }

    function dataURLtoBlob(dataURL, type) {
      var binary = $window.atob(dataURL.split(',')[1]);
      var array = [];
      var fileType = type || 'audio/wav';
      for (var i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i));
      }
      return new Blob([new Uint8Array(array)], { type: fileType });
    }

    function getGreetingId() {
      return getSettingValue(Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETING_URI, null);
    }

    function getCustomGreetingSettings() {
      var settings;
      try {
        var value = getSettingValue(Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETINGS, null);
        settings = value ? JSON.parse(value) : {};
      } catch (err) {
        LogSvc.error('[VoocemailSvc]: Failed to parse custom greetings. ', err);
        settings = {};
      }
      if (!settings.customGreetings) {
        settings.customGreetings = {};
      }
      return settings;
    }

    function setLegacySettings(settings, enabled, uri, noRecording) {
      settings[Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETING_ENABLED] = enabled;
      settings[Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETING_URI] = uri;
      settings[Constants.UserSettingKey.VOICEMAIL_NO_RECORDING] = noRecording;
    }

    function updateCustomGreetingSettings(fileId, name, enabled, noRecording, replacedFileId) {
      var settings = {};
      enabled = !!(fileId && enabled);
      noRecording = !!noRecording;

      var greetingSettings = getCustomGreetingSettings();
      if (replacedFileId) {
        delete greetingSettings.customGreetings[replacedFileId];
      }
      if (fileId) {
        greetingSettings.customGreetings[fileId] = {
          fileId: fileId,
          name: name,
          noRecording: noRecording
        };
      }
      settings[Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETINGS] = JSON.stringify(greetingSettings);

      var greetingId = getGreetingId();
      if (enabled || (greetingId && (greetingId === fileId || greetingId === replacedFileId))) {
        setLegacySettings(settings, enabled, fileId || '', enabled && noRecording);
      }
      return saveAllSettings(settings);
    }

    function uploadGreetingBlob(blob, fileIdToReplace) {
      var deferred = $q.defer();
      LogSvc.debug('[VoicemailSvc]: Uploading greeting ', { blobName: blob.name, type: blob.type, size: blob.size });
      $http
        .post(unifyPhone.__domain + '/api' + unifyPhone.__path + '/fileapi', blob, {
          headers: {
            'Content-Type': blob.type,
            'Content-Disposition': 'attachment; filename="' + blob.name + '"'
          },
          transformRequest: angular.identity
        })
        .then(function (res) {
          var uploadedFileId = res.data[0].id;
          if (uploadedFileId) {
            LogSvc.debug('[VoicemailSvc]: Uploaded greeting - new fileId = ', uploadedFileId);
            if (fileIdToReplace) {
              LogSvc.debug('[VoicemailSvc]: Removing previous greeting - fileId = ', fileIdToReplace);
              FileApiSvc.deleteFile(fileIdToReplace)
                .then(function () {
                  LogSvc.debug('[VoicemailSvc]: Previous greeting removed Successfully');
                  deferred.resolve(uploadedFileId);
                })
                .catch(function () {
                  FileApiSvc.deleteFile(uploadedFileId);
                  deferred.reject('Failed to remove file: ' + fileIdToReplace);
                });
            } else {
              deferred.resolve(uploadedFileId);
            }
          } else {
            deferred.reject('Upload failed. Missing file ID.');
          }
        });
      return deferred.promise;
    }

    function uploadGreeting(file, fileIdToReplace) {
      var deferred = $q.defer();
      if (!file) {
        deferred.reject('Missing file parameter.');
      } else if (file.size <= 0) {
        deferred.reject('The file is empty.');
      } else {
        var blob;
        var upload = function () {
          uploadGreetingBlob(blob, fileIdToReplace)
            .then(function (uploadedFileId) {
              deferred.resolve(uploadedFileId);
            })
            .catch(function (error) {
              deferred.reject(error);
            });
        };
        if (!file.name) {
          blob = file;
          blob.name = 'greeting-' + $rootScope.localUser.userId + '.wav';
          upload();
        } else {
          var reader = new FileReader();
          reader.onload = function (e) {
            blob = dataURLtoBlob(e.target.result, file.type);
            blob.name = file.name;
            upload();
          };
          reader.readAsDataURL(file);
        }
      }
      return deferred.promise;
    }

    function init() {
      if ($rootScope.localUser) {
        updateSettings(UserProfileSvc.getSettings());
      } else {
        PubSubSvc.subscribeOnce('/localUser/init', function () {
          updateSettings(UserProfileSvc.getSettings());
        });
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc listeners
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/user/settings/update', function (settings) {
      LogSvc.debug('[VoicemailSvc]: Received /user/settings/update event');
      updateSettings(settings);
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    /*
     * Get setting for voicemail enabled flag.
     * @return {Boolean} The current state for voicemail enabled flag.
     */
    this.isVoicemailEnabled = function () {
      return getSettingValue(Constants.UserSettingKey.VOICEMAIL_ENABLED, false);
    };

    /*
     * Stores setting for voicemail enabled flag.
     * @param {Boolean} voicemailEnabled The new state for voicemail enabled flag
     * @return {Boolean} The new state for voicemail enabled flag.
     */
    this.setVoicemailEnabled = function (voicemailEnabled) {
      return saveSetting(Constants.UserSettingKey.VOICEMAIL_ENABLED, !!voicemailEnabled);
    };

    /*
     * Get setting for voicemail no recording flag.
     * @return {Boolean} The current state for voicemail no recording flag.
     */
    this.isVoicemailNoRecording = function () {
      return getSettingValue(Constants.UserSettingKey.VOICEMAIL_NO_RECORDING, false);
    };

    /*
     * Stores setting for voicemail no recording flag.
     * @param {Boolean} voicemailNoRecording The new state for voicemail no recording flag
     * @return {Boolean} The new state for voicemail no recording flag.
     */
    this.setVoicemailNoRecording = function (voicemailNoRecording) {
      return saveSetting(Constants.UserSettingKey.VOICEMAIL_NO_RECORDING, !!voicemailNoRecording);
    };

    /*
     * Get setting for voicemail timeout.
     * @return {Number} The current voicemail timeout.
     */
    this.getVoicemailTimeout = function () {
      return getSettingValue(Constants.UserSettingKey.VOICEMAIL_TIMEOUT, DEFAULT_VM_TIMEOUT);
    };

    /*
     * Stores setting for voicemail timeout.
     * @param {Number} voicemailTimeout The new voicemail timeout.
     * @return {String} The new voicemail timeout.
     */
    this.setVoicemailTimeout = function (voicemailTimeout) {
      if (!voicemailTimeout || typeof voicemailTimeout !== 'number') {
        return $q.reject('Timeout must be a number');
      }
      return saveSetting(Constants.UserSettingKey.VOICEMAIL_TIMEOUT, voicemailTimeout);
    };

    /*
     * Get setting for voicemail custom greeting enabled flag.
     * @return {Boolean} The current state for voicemail custom greeting enabled flag.
     */
    this.isVoicemailCustomGreetingEnabled = function () {
      return getSettingValue(Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETING_ENABLED, false);
    };

    /*
     * Stores setting for voicemail custom greeting enabled flag.
     * @param {Boolean} voicemailCustomGreetingEnabled The new state for voicemail custom greeting enabled flag
     * @return {Boolean} The new state for voicemail custom greeting enabled flag.
     */
    this.setVoicemailCustomGreetingEnabled = function (voicemailCustomGreetingEnabled) {
      return saveSetting(Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETING_ENABLED, voicemailCustomGreetingEnabled);
    };

    /*
     * Gets the URL of the current custom greeting on the back-end storage.
     *
     * @return {String} the URL of the custom greeting or null
     */
    this.getVoicemailCustomGreetingUri = function () {
      return _self.fileIdToUri(getGreetingId());
    };

    /**
     * Returns custom greeting file settings.
     *
     * @returns {Object} settings as key value entries: [fileId]: {fileId: ... name: ..., ...}
     */
    this.getVoicemailCustomGreetings = function () {
      var legacyFileId = getGreetingId();
      var settings = getCustomGreetingSettings();
      Object.values(settings.customGreetings).forEach(function (greeting) {
        greeting.uri = _self.fileIdToUri(greeting.fileId);
      });
      if (legacyFileId && !settings.customGreetings[legacyFileId]) {
        settings.customGreetings[legacyFileId] = {
          fileId: legacyFileId,
          name: $rootScope.i18n.map.res_VmCustomGreetingCustom,
          noRecording: getSettingValue(Constants.UserSettingKey.VOICEMAIL_NO_RECORDING),
          uri: _self.fileIdToUri(legacyFileId)
        };
      }
      return settings.customGreetings;
    };

    /**
     * Update custom greeting file settings.
     *
     * @param {String} fileId greeting file ID
     * @param {String} name name of the greeting
     * @param {Boolean} enabled enable/disable this greeting
     * @param {Boolean} noRecording enable/disable greeting only mode
     * @returns {Promise} promise that will be resolved on success or rejected with error otherwise
     */
    this.updateCustomGreeting = function (fileId, name, enabled, noRecording) {
      if (!fileId) {
        return $q.reject('fileId cannot be empty.');
      }
      if (!name) {
        return $q.reject('Name cannot be empty.');
      }
      return updateCustomGreetingSettings(fileId, name, enabled, noRecording);
    };

    /**
     * (legacy) Uploads custom greeting file or recorded blob to the back-end storage.
     *
     * @param {File|Blob} file the custom greeting file to be uploaded
     * @returns {Promise} promise that will be resolved on success (empty string) or rejected with error otherwise
     */
    this.uploadCustomGreeting = function (file) {
      return uploadGreeting(file, getGreetingId())
        .then(function (fileId) {
          var settings = {};
          setLegacySettings(settings, true, fileId);
          var greetingSettings = getCustomGreetingSettings();
          delete greetingSettings.customGreetings[getGreetingId()];
          greetingSettings.customGreetings[fileId] = {
            fileId: fileId,
            name: $rootScope.i18n.map.res_VmCustomGreetingCustom,
            noRecording: _self.isVoicemailNoRecording()
          };
          settings[Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETINGS] = JSON.stringify(greetingSettings);
          return saveAllSettings(settings);
        })
        .then(function () {
          return $q.resolve('');
        });
    };

    /**
     * Uploads custom greeting file or recorded blob to the back-end storage.
     *
     * @param {File|Blob} file the custom greeting file to be uploaded
     * @param {String} name name of the greeting
     * @param {Boolean} enabled enable/disable this greeting
     * @param {Boolean} noRecording enable/disable greeting only mode
     * @param {String} fileIdToReplace file ID which should be replaced
     * @returns {Promise} promise that will be resolved on success (fileId) or rejected with error otherwise
     */
    this.uploadVoicemailCustomGreeting = function (file, name, enabled, noRecording, fileIdToReplace) {
      if (!name) {
        return $q.reject('Name cannot be empty.');
      }
      return uploadGreeting(file, fileIdToReplace).then(function (fileId) {
        return updateCustomGreetingSettings(fileId, name, enabled, noRecording, fileIdToReplace).then(function () {
          return fileId;
        });
      });
    };

    /**
     * Deletes current custom greeting file from the back-end storage.
     *
     * @returns {Promise} promise that will be resolved on success or rejected with error otherwise
     */
    this.deleteCustomGreeting = function (id) {
      var fileId = id || getGreetingId();
      return FileApiSvc.deleteFile(fileId).then(function () {
        if (id) {
          return updateCustomGreetingSettings(null, null, false, false, fileId);
        } else {
          // legacy part
          var settings = {};
          setLegacySettings(settings, false, '');
          var greetingSettings = getCustomGreetingSettings();
          delete greetingSettings.customGreetings[fileId];
          settings[Constants.UserSettingKey.VOICEMAIL_CUSTOMGREETINGS] = JSON.stringify(greetingSettings);
          return saveAllSettings(settings);
        }
      });
    };

    /**
     * Converts file ID into URI string.
     */
    this.fileIdToUri = function (fileId) {
      return fileId ? unifyPhone.__domain + '/api' + unifyPhone.__path + '/fileapi?fileid=' + fileId : null;
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////////////////
    init();

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.VoicemailSvcImpl = VoicemailSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  var Constants = unifyPhone.Constants;
  var RoutingOptions = unifyPhone.RoutingOptions;
  var BusyHandlingOptions = unifyPhone.BusyHandlingOptions;
  var CallForwardingTypes = unifyPhone.Enums.CallForwardingTypes;
  var Targets = unifyPhone.Enums.Targets;
  var Utils = unifyPhone.Utils;
  var PhoneNumberFormatter = unifyPhone.PhoneNumberFormatter;

  // eslint-disable-next-line max-params, max-lines-per-function
  function TelephonySettingsSvcImpl($rootScope, $q, LogSvc, PubSubSvc, CstaSvc, UserProfileSvc, VoicemailSvc) {
    // NOSONAR
    LogSvc.debug('New Service: TelephonySettingsSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var VM_OFFSET = 3; // The amount of extra seconds that the VM timer is set longer than the main ring duration

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _that = this;
    var _routingOptions = [];
    var _savedRoutingOption = null;
    var _savedCallForwardingNumber;
    var _savedCallForwardingNoAnswerNumber;
    var _savedCallForwardingBusyNumber;
    var _savedCallForwardingStatus = false;
    var _savedCallForwardingBusyStatus = false;
    var _savedCallForwardingNoAnswerStatus = false;
    var _savedCallForwardingVMStatus = false;
    var _savedCallForwardingVMBusyStatus = false;
    var _savedCallForwardingVMNoAnswerStatus = false;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function isSelectedRouting(routingOption) {
      return !!(routingOption && $rootScope.localUser.selectedRoutingOption === routingOption.name);
    }

    function setRoutingOption(routingOption) {
      if (routingOption) {
        $rootScope.localUser.selectedRoutingOption = routingOption.name;
      }
    }

    function setRoutingOptions() {
      if (!$rootScope.localUser.isATC) {
        return;
      }
      $rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiV5000 || $rootScope.localUser.isMiVB
        ? setRoutingOptionsOSBiz()
        : setRoutingOptionsATC();
      _that.updateRoutingOption();
    }

    function setRoutingOptionsATC() {
      // Check 'Default Routing'
      _routingOptions = [RoutingOptions.DefaultRouting];

      // Check 'Desk phone'
      var answerDevices = CstaSvc.getCallDevices();
      var option = RoutingOptions.DeskPhone;
      option.disabled = !answerDevices.includes(Targets.Desk) || !!_that.isCallForwardingEnabled();
      if (option.disabled && isSelectedRouting(RoutingOptions.DeskPhone)) {
        LogSvc.warn('[TelephonySettingsSvc]: Invalid routing option. Change incoming routing from DeskPhone number to Default routing');
        setRoutingOption(RoutingOptions.DefaultRouting);
      }
      _routingOptions.push(option);

      // Check 'Alternative number'
      if ($rootScope.localUser.isOSV) {
        option = RoutingOptions.AlternativeNumber;
        option.disabled = !$rootScope.localUser.reroutingPhoneNumber || !!_that.isCallForwardingNoAnswerEnabled();
        _routingOptions.push(option);
        if (option.disabled && isSelectedRouting(option)) {
          setRoutingOption(RoutingOptions.DefaultRouting);
        }
      } else if (isSelectedRouting(RoutingOptions.AlternativeNumber)) {
        LogSvc.warn('[TelephonySettingsSvc]: Invalid routing option. Change incoming routing from Alternative number to Default routing');
        setRoutingOption(RoutingOptions.DefaultRouting);
      }

      // Check 'Voicemail'
      var vmOption = $rootScope.localUser.hasVoicemailPermission ? RoutingOptions.VM : null;
      if (vmOption) {
        setVoiceMailOptions(vmOption);
        _routingOptions.push(vmOption);
      }

      if ((!vmOption || vmOption.disabled) && isSelectedRouting(RoutingOptions.VM)) {
        LogSvc.warn('[TelephonySettingsSvc]: Invalid routing option. Change incoming routing from Voicemail to Default routing');
        setRoutingOption(RoutingOptions.DefaultRouting);
      }

      // Check 'Other'
      if (isSelectedRouting(RoutingOptions.Other)) {
        var other = RoutingOptions.Other;
        other.number = $rootScope.localUser.otherRoutingNumber;
        _routingOptions.push(other);
      }

      // Check ring duration option
      if (
        $rootScope.localUser.ringDurationEnabled &&
        Utils.cleanPhoneNumber($rootScope.localUser.alternativeNumber) !== Utils.cleanPhoneNumber($rootScope.localUser.reroutingPhoneNumber)
      ) {
        UserProfileSvc.setReroutingPhoneNumber($rootScope.localUser.alternativeNumber);
      }
    }

    function setRoutingOptionsOSBiz() {
      // Check 'Default Routing'
      _routingOptions = [RoutingOptions.DefaultRouting];

      // Check 'Desk phone'
      var answerDevices = CstaSvc.getCallDevices();
      var option = RoutingOptions.DeskPhone;
      option.disabled = !answerDevices.includes(Targets.Desk) || !!_that.isCallForwardingEnabled();
      if (option.disabled && isSelectedRouting(RoutingOptions.DeskPhone)) {
        LogSvc.warn('[TelephonySettingsSvc]: Invalid routing option. Change incoming routing from DeskPhone number to Default routing');
        setRoutingOption(RoutingOptions.DefaultRouting);
      }
      _routingOptions.push(option);

      // Check 'UnifyPhone client'
      option = RoutingOptions.UnifyPhoneClient;
      option.disabled = !answerDevices.includes(Targets.Desk) || !!_that.isCallForwardingEnabled();
      if (option.disabled && isSelectedRouting(RoutingOptions.UnifyPhoneClient)) {
        LogSvc.warn('[TelephonySettingsSvc]: Invalid routing option. Change incoming routing from UnifyPhoneClient number to Default routing');
        setRoutingOption(RoutingOptions.DefaultRouting);
      }
      _routingOptions.push(option);

      if ($rootScope.localUser.isOsBizCTIEnabled) {
        // Check 'Alternative number'
        option = RoutingOptions.AlternativeNumber;
        option.disabled = !$rootScope.localUser.reroutingPhoneNumber;
        _routingOptions.push(option);
      }
    }

    function initForwardingSettings() {
      var cfData = $rootScope.localUser.cfData;
      cfData.Immediate.cfwNumber = cfData.Immediate.cfwNumber || '';
      cfData.NoAnswer.cfwNumber = cfData.NoAnswer.cfwNumber || '';
      cfData.Busy.cfwNumber = cfData.Busy.cfwNumber || '';

      _savedCallForwardingNumber = cfData.Immediate.cfwNumber;
      _savedCallForwardingStatus = !!cfData.Immediate.cfwEnabled;

      _savedCallForwardingNoAnswerNumber = cfData.NoAnswer.cfwNumber;
      _savedCallForwardingNoAnswerStatus = !!cfData.NoAnswer.cfwEnabled;

      _savedCallForwardingBusyNumber = cfData.Busy.cfwNumber;
      _savedCallForwardingBusyStatus = !!cfData.Busy.cfwEnabled;

      _savedCallForwardingVMStatus = !!cfData.VM.cfwEnabled;
      _savedCallForwardingVMNoAnswerStatus = !!cfData.VMNoAnswer.cfwEnabled;
      _savedCallForwardingVMBusyStatus = !!cfData.VMBusy.cfwEnabled;
    }

    function syncVMConfiguration() {
      if (!$rootScope.localUser.isATC || $rootScope.localUser.isOsBizCTIEnabled) {
        return;
      }
      if ($rootScope.localUser.voicemailPBXEnabled) {
        var vmTimeout = Math.max($rootScope.localUser.mainRingDuration + VM_OFFSET, VoicemailSvc.getVoicemailTimeout());
        _that.setVoicemail(VoicemailSvc.isVoicemailEnabled(), vmTimeout);
      }
    }

    function syncRoutingTimers() {
      if (!$rootScope.localUser.isATC || $rootScope.localUser.isOsBizCTIEnabled) {
        return;
      }
      var cellRingDurationTimer = Math.min(VoicemailSvc.getVoicemailTimeout(), 25);
      CstaSvc.setRoutingTimers({
        clientRingDuration: 1,
        mainRingDuration: $rootScope.localUser.mainRingDuration,
        cellRingDuration: cellRingDurationTimer
      });
    }

    function updateVoiceMailSettingOptions() {
      if (!$rootScope.localUser.isATC || $rootScope.localUser.isOsBizCTIEnabled) {
        return;
      }
      var routingOption = _routingOptions.find(function (option) {
        return option === RoutingOptions.VM;
      });
      setVoiceMailOptions(routingOption);
    }

    function setVoiceMailOptions(option) {
      if (option) {
        option.disabled = !VoicemailSvc.isVoicemailEnabled() || !$rootScope.localUser.vmNumber;
        option.ui = $rootScope.localUser.vmNumber ? 'res_BusyHandling_Voicemail' : 'res_BusyHandling_Voicemail_Not_Configured';

        if (VoicemailSvc.isVoicemailEnabled() && $rootScope.localUser.vmNumber && $rootScope.localUser.voicemailPBXEnabled) {
          option.disabled = !!(_that.isCallForwardingEnabled() || _that.isCallForwardingNoAnswerEnabled());
        }
      }
    }

    function updateRoutingOptionInPBX() {
      if ($rootScope.localUser.isOsBizCTIEnabled) {
        return _savedRoutingOption !== null;
      }
      return (
        _savedRoutingOption === RoutingOptions.AlternativeNumber.name ||
        _savedRoutingOption === RoutingOptions.Other.name ||
        _savedRoutingOption === RoutingOptions.VM.name ||
        isSelectedRouting(RoutingOptions.AlternativeNumber) ||
        isSelectedRouting(RoutingOptions.VM)
      );
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    function onCstaForwardingEvent() {
      commonForwarding();
      if ($rootScope.localUser.ringDurationConfigurable) {
        syncRoutingTimers();
      }
    }

    function onUserSettingsUpdate() {
      LogSvc.debug('[TelephonySettingsSvc]: Received /user/settings/update event.');
      _savedRoutingOption = $rootScope.localUser.selectedRoutingOption;
      LogSvc.info('[TelephonySettingsSvc]: Publish /atc/routingForwardingEvent event.');
      PubSubSvc.publish('/atc/routingForwardingEvent', [_that.isAnyCallForwardingEnabled()]);
      syncVMConfiguration();
      syncRoutingTimers();
      if (!$rootScope.localUser.voicemailPBXEnabled) {
        updateVoiceMailSettingOptions();
      }
    }

    function onCstaGetForwardingResponse() {
      commonForwarding();
      syncRoutingTimers();
    }

    function onCstaAgentStateUpdated() {
      // Reset stored call routing. Client needs to update unifyPhone side only. PBX is already updated.
      _savedRoutingOption = null;
      setRoutingOptions();
    }

    function onDeviceChangedEvent() {
      setRoutingOptions();
    }

    function commonForwarding() {
      setRoutingOptions();
      initForwardingSettings();
      syncVMConfiguration();
      LogSvc.info('[TelephonySettingsSvc]: Publish /atc/routingForwardingEvent event.');
      PubSubSvc.publish('/atc/routingForwardingEvent', [_that.isAnyCallForwardingEnabled()]);
      updateVoiceMailSettingOptions();
    }

    PubSubSvc.subscribe('/csta/forwardingEvent', onCstaForwardingEvent);
    PubSubSvc.subscribe('/csta/getForwardingResponse', onCstaGetForwardingResponse);
    PubSubSvc.subscribe('/csta/agentStateUpdated', onCstaAgentStateUpdated);
    PubSubSvc.subscribe('/user/settings/update', onUserSettingsUpdate);
    PubSubSvc.subscribe('/csta/deviceChange', onDeviceChangedEvent);

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    /**
     * Updates the user's routing option for telephony calls.
     * @param {Object} option The routing option object.
     */
    this.updateRoutingOption = function (option) {
      option = option || $rootScope.localUser.selectedRoutingOption;
      if (!$rootScope.localUser.isATC) {
        return $q.resolve();
      }
      if (option === _savedRoutingOption) {
        return $q.resolve();
      }

      return new $q(function (resolve, reject) {
        $rootScope.localUser.selectedRoutingOption = option;
        if (updateRoutingOptionInPBX()) {
          LogSvc.debug('[TelephonySettingsSvc]: Update routing option on PBX to ', $rootScope.localUser.selectedRoutingOption);
          CstaSvc.updateRoutingOption($rootScope.localUser.selectedRoutingOption, _savedRoutingOption, function (err) {
            if (err) {
              LogSvc.error('[TelephonySettingsSvc]: Failed to update routing on PBX. ', err);
              $rootScope.localUser.selectedRoutingOption = _savedRoutingOption;
              reject('res_FailedToSetRoutingOption');
              return;
            }
            LogSvc.info('[TelephonySettingsSvc]: Successfully updated routing on PBX. Set routing option to ', $rootScope.localUser.selectedRoutingOption);
            UserProfileSvc.saveSetting(Constants.UserSettingKey.ATC_ROUTING, $rootScope.localUser.selectedRoutingOption);
            _savedRoutingOption = $rootScope.localUser.selectedRoutingOption;

            // For OSBiz, changing routing option may affect the calling device list
            if ($rootScope.localUser.isOsBizCTIEnabled) {
              LogSvc.debug('[TelephonySettingsSvc]: Publish /csta/deviceChange event');
              PubSubSvc.publish('/csta/deviceChange');
            }
            resolve();
          });
        } else {
          LogSvc.info('[TelephonySettingsSvc]: Set routing option to ', $rootScope.localUser.selectedRoutingOption);
          UserProfileSvc.saveSetting(Constants.UserSettingKey.ATC_ROUTING, $rootScope.localUser.selectedRoutingOption);
          _savedRoutingOption = $rootScope.localUser.selectedRoutingOption;
          resolve();
        }
      });
    };

    /**
     * @deprecated
     * Use {@link setCallForwarding}
     */
    this.setForwardingImmediate = function (status, cb) {
      cb = cb || function () {};
      if (!_that.isCallForwardingAvailable()) {
        cb('res_FailedToSetForwardingNotAvailable');
        return;
      }
      status = !!status;

      var localUser = $rootScope.localUser;

      localUser.cfData.Immediate.cfwNumber = localUser.cfData.Immediate.cfwNumber || '';
      _savedCallForwardingNumber = _savedCallForwardingNumber || '';

      if (localUser.cfData.Immediate.cfwNumber && !Utils.isDialableNumber(localUser.cfData.Immediate.cfwNumber)) {
        // Not a valid number. Wait for user to correct the number.
        cb('Invalid phone number');
        return;
      }
      if (localUser.cfData.Immediate.cfwNumber !== _savedCallForwardingNumber || status !== _savedCallForwardingStatus) {
        CstaSvc.setForwardingImmediate(status, localUser.cfData.Immediate.cfwNumber, function (err) {
          if (err) {
            LogSvc.warn('[TelephonySettingsSvc]: Failed to set call forwarding immediate: ', err);
            localUser.cfData.Immediate.cfwNumber = _savedCallForwardingNumber;
            if (/privilegeViolationSpecifiedDevice/.test(err) || /invalidForwardingType/.test(err)) {
              localUser.cfData.Immediate.cfwAvailable = !localUser.isOSV;
              setRoutingOptions();
              cb('res_FailedToSetForwardingNotAvailable');
              return;
            }
            cb('res_FailedToSetForwarding');
          } else {
            _savedCallForwardingNumber = $rootScope.localUser.cfData.Immediate.cfwNumber;
            _savedCallForwardingStatus = status;
            if (status && localUser.selectedRoutingOption === RoutingOptions.VM.name) {
              localUser.selectedRoutingOption = RoutingOptions.DefaultRouting.name;
              LogSvc.info('[TelephonySettingsSvc]: Set routing option to ', $rootScope.localUser.selectedRoutingOption);
              UserProfileSvc.saveSetting(Constants.UserSettingKey.ATC_ROUTING, $rootScope.localUser.selectedRoutingOption);
              _savedRoutingOption = $rootScope.localUser.selectedRoutingOption;
            }
            cb(null);
          }
        });
      } else {
        // No changes
        cb(null);
      }
    };

    /**
     * @deprecated
     * Use {@link setCallForwarding}
     */
    this.setForwardingToVM = function (status, cb) {
      // Set the callForwardingNumber as Voicemail number
      $rootScope.localUser.cfData.VM.cfwNumber = $rootScope.localUser.vmNumber;
      return CstaSvc.setForwardingToVM(status, function (err) {
        if (err) {
          LogSvc.warn('[TelephonySettingsSvc]: Failed to set call forwarding to VM: ', err);
          $rootScope.localUser.cfData.VM.cfwNumber = '';
          $rootScope.localUser.cfData.VM.cfwEnabled = !status;
          cb && cb('res_FailedToSetForwarding');
        } else {
          $rootScope.localUser.cfData.VM.cfwEnabled = status;
          cb && cb(null);
        }
      });
    };

    /**
     * Returns the available telephony devices.
     * @returns {Array} the list of available telephony devices.
     */
    this.getCallDevices = function () {
      return CstaSvc.getCallDevices();
    };

    /**
     * Returns the available routing options.
     * @returns {Array} the list of routing optins.
     */
    this.getRoutingOptions = function () {
      //If Routing options are empty, Populate it
      if (!_routingOptions.length) {
        setRoutingOptions();
      }
      return _routingOptions;
    };

    /**
     * @deprecated
     * Use {@link voicemailSvc.setVoicemailxxxx}
     */
    this.setVoicemail = function (status, duration, cb) {
      CstaSvc.setVoicemail(status, duration, cb);
    };

    /**
     * Set the autopilot routing timers.
     * @param {Array} timers List of timer objects.
     */
    this.setRoutingTimers = function (timers, cb) {
      if (!timers.hasOwnProperty('cellRingDuration')) {
        timers.cellRingDuration = Math.min(VoicemailSvc.getVoicemailTimeout(), 25);
      }
      if (!timers.hasOwnProperty('clientRingDuration')) {
        timers.clientRingDuration = 1;
      }
      CstaSvc.setRoutingTimers(timers, function (err) {
        if (!err) {
          LogSvc.debug('[TelephonySettingsSvc]: Successfully set the routing timers: ', timers);
          $rootScope.localUser.mainRingDuration = timers.mainRingDuration;
          $rootScope.localUser.cellRingDuration = timers.cellRingDuration;
          $rootScope.localUser.clientRingDuration = timers.clientRingDuration;
          LogSvc.debug('[TelephonySettingsSvc]: Publish /localUser/update event');
          PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
        }
        cb && cb(err);
      });
    };

    /**
     * Set the rerouting phone number (alternative number) on the user's telephony configuration.
     * @param {String} number The rerouting phone number.
     * @returns {Promise} that resolves without parameters when the update was successful, it rejects with error string (resource string) otherwise.
     */
    this.setReroutingPhoneNumber = function (number, shouldPreformat) {
      number = number || '';

      number && shouldPreformat && (number = PhoneNumberFormatter.format(number));
      if (number === $rootScope.localUser.reroutingPhoneNumber) {
        return $q.resolve();
      }

      if (!number && $rootScope.localUser.selectedRoutingOption === RoutingOptions.AlternativeNumber.name) {
        // NGTC-1541: If the request times out we should catch the rejection
        _that.updateRoutingOption(RoutingOptions.DefaultRouting.name).catch();
      }

      return new $q(function (resolve, reject) {
        var validateAndResolve = function () {
          if (!number && $rootScope.localUser.selectedBusyHandlingOption === BusyHandlingOptions.SendToAlternativeNumber.name) {
            // The selected option is not allowed. Change it to Default Routing.
            _that.saveBusyHandlingOption(BusyHandlingOptions.DefaultRouting.name);
          }
          resolve();
        };

        var lastReroutingPhoneNumber = $rootScope.localUser.reroutingPhoneNumber;

        UserProfileSvc.setReroutingPhoneNumber(number, function () {
          // For OSBiz, alternative number is set using CSTA wihout re-registration.
          // The number needs to be sent when disabling too.
          if ($rootScope.localUser.isOsBizCTIEnabled) {
            if ($rootScope.localUser.reroutingPhoneNumber !== number) {
              return;
            }
            var reroutingPhoneNumber = $rootScope.localUser.reroutingPhoneNumber || lastReroutingPhoneNumber;
            CstaSvc.setForwardingNoAnswer(reroutingPhoneNumber, !!$rootScope.localUser.reroutingPhoneNumber, function (err) {
              if (err) {
                LogSvc.warn('[TelephonySettingsSvc]: Failed to set alternative number: ', err);
                $rootScope.localUser.reroutingPhoneNumber = '';
                reject('res_FailedToSetAlternativeNumber');

                LogSvc.debug('[TelephonySettingsSvc]: Publish /localUser/update event');
                PubSubSvc.publish('/localUser/update', [$rootScope.localUser]);
              } else {
                validateAndResolve();
              }
            });
          } else {
            validateAndResolve();
          }
        });
      });
    };

    /**
     * Returns the name of the current selected routing option.
     * @returns {String} the name of the selected routing option.
     */
    this.getSelectedRoutingOption = function () {
      return $rootScope.localUser.selectedRoutingOption || RoutingOptions.DefaultRouting.name;
    };

    /**
     * Returns whether the PBX exposes the ringing duration time.
     * @returns {Boolean} true if the PBX exposes the ringing duration time, false otherwise.
     */
    this.getRingDurationEnabled = function () {
      return !!$rootScope.localUser.ringDurationEnabled;
    };

    /**
     * Returns whether the ringing duration time is configurable.
     * @returns {Boolean} true if the duration time is configurable.
     */
    this.getRingDurationConfigurable = function () {
      return !!$rootScope.localUser.ringDurationConfigurable;
    };

    /**
     * Returns the routing timers.
     * @returns {Object} the routing times.
     */
    this.getRoutingTimers = function () {
      return {
        mainRingDuration: $rootScope.localUser.mainRingDuration,
        clientRingDuration: $rootScope.localUser.clientRingDuration,
        cellRingDuration: $rootScope.localUser.cellRingDuration
      };
    };

    /**
     * Returns the default routing option.
     * @returns {Object} the default routing option.
     */
    this.getDefaultRoutingOption = function () {
      return RoutingOptions.DefaultRouting;
    };

    /**
     * Returns whether call forwarding is available.
     * @returns {Boolean} true if call forwarding is available, false otherwise.
     */
    this.isCallForwardingAvailable = function () {
      return !!$rootScope.localUser.cfData.Immediate.cfwAvailable;
    };

    /**
     * Returns whether call forwarding is currently enabled.
     * @returns {Boolean} true if call forwarding is enabled.
     */
    this.isCallForwardingEnabled = function () {
      return !!$rootScope.localUser.cfData.Immediate.cfwEnabled;
    };

    /**
     * Returns whether call forwarding to voice mail is currently enabled.
     * @returns {Boolean} true if call forwarding to voice mail is enabled.
     */
    this.isCallForwardingToVMEnabled = function () {
      return !!$rootScope.localUser.cfData.VM.cfwEnabled;
    };

    /**
     * Returns whether call forwarding no answer is available.
     * @returns {Boolean} true if call forwarding no answer is available, false otherwise.
     */
    this.isCallForwardingNoAnswerAvailable = function () {
      return !!$rootScope.localUser.cfData.NoAnswer.cfwAvailable;
    };

    /**
     * Returns whether call forwarding no answer is currently enabled.
     * @returns {Boolean} true if call forwarding no answer is enabled.
     */
    this.isCallForwardingNoAnswerEnabled = function () {
      return Object.values($rootScope.localUser.cfData).some(function (entry) {
        return (
          entry.cfwAvailable &&
          entry.cfwEnabled &&
          (entry.cfwType === CallForwardingTypes.NoAnswer.name || (entry.cfwType === CallForwardingTypes.VMNoAnswer.name && $rootScope.localUser.isMiV5000))
        );
      });
    };

    /**
     * Returns whether call forwarding immediate is currently enabled.
     * @returns {Boolean} true if call forwarding no answer is enabled.
     */
    this.isCallForwardingImmediateEnabled = function () {
      return Object.values($rootScope.localUser.cfData).some(function (entry) {
        return (
          entry.cfwAvailable &&
          entry.cfwEnabled &&
          (entry.cfwType === CallForwardingTypes.Immediate.name ||
            (entry.cfwType === CallForwardingTypes.VM.name && ($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiV5000)))
        );
      });
    };

    /**
     * Returns whether call forwarding busy is currently enabled.
     * @returns {Boolean} true if call forwarding no answer is enabled.
     */
    this.isCallForwardingBusyEnabled = function () {
      return Object.values($rootScope.localUser.cfData).some(function (entry) {
        return (
          entry.cfwAvailable &&
          entry.cfwEnabled &&
          (entry.cfwType === CallForwardingTypes.Busy.name || (entry.cfwType === CallForwardingTypes.VMBusy.name && $rootScope.localUser.isMiV5000))
        );
      });
    };

    /**
     * Returns whether call forwarding immediate or call forwarding no answer are currently available
     * @returns {boolean} true if call forwarding immediate or call forwarding no reply are available
     */
    this.isAnyCallForwardingAvailable = function () {
      return Object.values($rootScope.localUser.cfData).some(function (entry) {
        return entry.cfwAvailable;
      });
    };

    /**
     * Returns whether any call forwarding is enabled.
     * @returns {Boolean} true if any call forwarding is enabled.
     */
    this.isAnyCallForwardingEnabled = function () {
      return Object.values($rootScope.localUser.cfData).some(function (entry) {
        return (
          entry.cfwAvailable &&
          entry.cfwEnabled &&
          (entry.cfwType === CallForwardingTypes.Immediate.name ||
            entry.cfwType === CallForwardingTypes.NoAnswer.name ||
            entry.cfwType === CallForwardingTypes.Busy.name ||
            (entry.cfwType === CallForwardingTypes.VM.name && ($rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.isMiV5000)) ||
            (entry.cfwType === CallForwardingTypes.VMBusy.name && $rootScope.localUser.isMiV5000) ||
            (entry.cfwType === CallForwardingTypes.VMNoAnswer.name && $rootScope.localUser.isMiV5000))
        );
      });
    };

    /**
     * Returns whether any call forwarding is enabled (OsBiz specific).
     * @returns {Boolean} true if any call forwarding is enabled.
     */
    this.isCallForwardingEnabledOsBiz = _that.isAnyCallForwardingEnabled;

    /**
     * Returns the call forwarding number.
     * @returns {String} the call forwarding number.
     */
    this.getCallForwardingNumber = function () {
      return $rootScope.localUser.cfData.Immediate.cfwNumber || '';
    };

    /**
     * Returns the call forwarding no answer number.
     * @returns {String} the call forwarding no answer number.
     */
    this.getCallForwardingNoAnswerNumber = function () {
      return $rootScope.localUser.cfData.NoAnswer.cfwNumber || '';
    };

    /**
     * Returns the call forwarding no answer number.
     * @returns {String} the call forwarding no answer number.
     */
    this.getCallForwardingBusyNumber = function () {
      return $rootScope.localUser.cfData.Busy.cfwNumber || '';
    };

    /**
     * Enable, disables or changes the user call forwarding.
     * @returns {Promise} that resolves with no parameters when call forwarding is successfully updated,
     * it is rejected with an error string (resource string) otherwise.
     */
    this.setCallForwarding = function (cfenabled, cftype, cfnumber) {
      if (
        cftype !== CallForwardingTypes.Immediate.name &&
        cftype !== CallForwardingTypes.VM.name &&
        cftype !== CallForwardingTypes.NoAnswer.name &&
        cftype !== CallForwardingTypes.VMNoAnswer.name &&
        cftype !== CallForwardingTypes.Busy.name &&
        cftype !== CallForwardingTypes.VMBusy.name
      ) {
        LogSvc.err('[TelephonySettingsSvc]: Invalid call forwarding type ', cftype);
        return $q.reject('res_ErrorMsgInternal');
      }

      if (!_that.isAnyCallForwardingAvailable()) {
        return $q.reject('res_FailedToSetForwardingNotAvailable');
      }

      if (cftype === CallForwardingTypes.VM.name || cftype === CallForwardingTypes.VMNoAnswer.name || cftype === CallForwardingTypes.VMBusy.name) {
        cfnumber = _that.getVoiceMailNumber();
      }

      if (cfnumber && !Utils.isDialableNumber(cfnumber)) {
        // Not a valid number. Wait for user to correct the number.
        return $q.reject('res_ErrorPhoneNumber');
      }

      cfenabled = !!cfenabled;

      if (
        (cftype === CallForwardingTypes.Immediate.name && cfenabled === _savedCallForwardingStatus && cfnumber === _savedCallForwardingNumber) ||
        (cftype === CallForwardingTypes.VM.name && cfenabled === _savedCallForwardingVMStatus) ||
        (cftype === CallForwardingTypes.NoAnswer.name && cfenabled === _savedCallForwardingNoAnswerStatus && cfnumber === _savedCallForwardingNoAnswerNumber) ||
        (cftype === CallForwardingTypes.VMNoAnswer.name && cfenabled === _savedCallForwardingVMNoAnswerStatus) ||
        (cftype === CallForwardingTypes.Busy.name && cfenabled === _savedCallForwardingBusyStatus && cfnumber === _savedCallForwardingBusyNumber) ||
        (cftype === CallForwardingTypes.VMBusy.name && cfenabled === _savedCallForwardingVMBusyStatus)
      ) {
        // Nothing changed
        return $q.resolve();
      }

      return new $q(function (resolve, reject) {
        var localUser = $rootScope.localUser;
        CstaSvc.setForwardingType(cftype, cfenabled, cfnumber, function (err) {
          if (err) {
            LogSvc.warn('[TelephonySettingsSvc]: Failed to set call forwarding ' + cftype + ':', err);
            if (cftype === CallForwardingTypes.Immediate.name) {
              localUser.cfData[cftype].cfwEnabled = _savedCallForwardingStatus;
              localUser.cfData[cftype].cfwNumber = _savedCallForwardingNumber;
            } else if (cftype === CallForwardingTypes.NoAnswer.name) {
              localUser.cfData[cftype].cfwEnabled = _savedCallForwardingNoAnswerStatus;
              localUser.cfData[cftype].cfwNumber = _savedCallForwardingNoAnswerNumber;
            } else if (cftype === CallForwardingTypes.Busy.name) {
              localUser.cfData[cftype].cfwEnabled = _savedCallForwardingBusyStatus;
              localUser.cfData[cftype].cfwNumber = _savedCallForwardingBusyNumber;
            } else if (cftype === CallForwardingTypes.VMBusy.name) {
              localUser.cfData[cftype].cfwEnabled = _savedCallForwardingVMBusyStatus;
            } else if (cftype === CallForwardingTypes.VMNoAnswer.name) {
              localUser.cfData[cftype].cfwEnabled = _savedCallForwardingVMNoAnswerStatus;
            } else {
              localUser.cfData[cftype].cfwEnabled = _savedCallForwardingVMStatus;
            }
            if (/privilegeViolationSpecifiedDevice/.test(err) || /invalidForwardingType/.test(err)) {
              localUser.cfData[cftype].cfwAvailable = !localUser.isOSV;
              if (cftype === CallForwardingTypes.Immediate.name) {
                setRoutingOptions();
              }
              reject('res_FailedToSetForwardingNotAvailable');
              return;
            }
            reject('res_FailedToSetForwarding');
          } else {
            localUser.cfData[cftype].cfwNumber = cfnumber;
            localUser.cfData[cftype].cfwEnabled = cfenabled;
            if (cftype === CallForwardingTypes.Immediate.name) {
              _savedCallForwardingNumber = cfnumber;
              _savedCallForwardingStatus = cfenabled;
            } else if (cftype === CallForwardingTypes.NoAnswer.name) {
              _savedCallForwardingNoAnswerNumber = cfnumber;
              _savedCallForwardingNoAnswerStatus = cfenabled;
            } else if (cftype === CallForwardingTypes.Busy.name) {
              _savedCallForwardingBusyNumber = cfnumber;
              _savedCallForwardingBusyStatus = cfenabled;
            } else if (cftype === CallForwardingTypes.VMBusy.name) {
              _savedCallForwardingVMBusyStatus = cfenabled;
            } else if (cftype === CallForwardingTypes.VMNoAnswer.name) {
              _savedCallForwardingVMNoAnswerStatus = cfenabled;
            } else {
              _savedCallForwardingVMStatus = cfenabled;
            }
            if (
              cfenabled &&
              (localUser.selectedRoutingOption === RoutingOptions.VM.name || localUser.selectedRoutingOption === RoutingOptions.AlternativeNumber.name)
            ) {
              localUser.selectedRoutingOption = RoutingOptions.DefaultRouting.name;
              LogSvc.info('[TelephonySettingsSvc]: Set routing option to ', $rootScope.localUser.selectedRoutingOption);
              UserProfileSvc.saveSetting(Constants.UserSettingKey.ATC_ROUTING, $rootScope.localUser.selectedRoutingOption);
              _savedRoutingOption = $rootScope.localUser.selectedRoutingOption;
            }
            resolve();
          }
        });
      });
    };

    /**
     * Returns the call forwarding data object
     * @returns {{cfData}}
     */
    this.getCallForwardingData = function () {
      return JSON.parse(JSON.stringify($rootScope.localUser.cfData));
    };

    /**
     * Returns the call forwarding entry that is enabled
     * @returns {string}
     */
    this.getEnabledCallForwarding = function () {
      return Object.values($rootScope.localUser.cfData).find(function (cfEntry) {
        return cfEntry.cfwEnabled;
      });
    };

    /**
     * Returns the call forwarding entry that is enabled
     * @returns {Array}
     */
    this.getEnabledCallForwardingOptions = function () {
      return Object.values($rootScope.localUser.cfData).filter(function (cfEntry) {
        return cfEntry.cfwEnabled;
      });
    };

    /**
     * Returns the user's voicemail number.
     * @returns {String} the user's voicemail number.
     */
    this.getVoiceMailNumber = function () {
      return $rootScope.localUser.vmNumber || '';
    };

    /**
     * Returns whether busy handling options are available for the user.
     * @returns {Boolean} true if the user has busy handling options availabe, false otherwise.
     */
    this.canHandleBusyOptions = function () {
      return !!$rootScope.localUser.isATC;
    };

    /**
     * Returns the available busy handling options.
     * @returns {Array} list of available busy handling options.
     */
    this.getBusyHandlingOptions = function () {
      if (!_that.canHandleBusyOptions()) {
        return [];
      }

      var busyHandlingOptions = [BusyHandlingOptions.DefaultRouting, BusyHandlingOptions.BusySignal];

      // Add other options but disable them if not applicable
      var option = BusyHandlingOptions.SendToAlternativeNumber;
      option.disabled = !$rootScope.localUser.reroutingPhoneNumber;
      busyHandlingOptions.push(option);

      if ($rootScope.localUser.hasVoicemailPermission) {
        // If Voicemail is disabled or Voice Mail number is not received in Register Response, Disable VoiceMail
        var vmOption = Object.assign({}, BusyHandlingOptions.SendToVM);
        vmOption.disabled = !$rootScope.localUser.vmNumber || !VoicemailSvc.isVoicemailEnabled();
        vmOption.ui = $rootScope.localUser.vmNumber ? 'res_BusyHandling_Voicemail' : 'res_BusyHandling_Voicemail_Not_Configured';
        busyHandlingOptions.push(vmOption);
      }

      return busyHandlingOptions;
    };

    /**
     * Returns the current user's busy handling option.
     * @returns {String} the user's busy handling option name.
     */
    this.getSelectedBusyHandlingOption = function () {
      return $rootScope.localUser.selectedBusyHandlingOption;
    };

    /**
     * Saves the user's busy handling option.
     * @param {Object} option the user's busy handling option name.
     */
    this.saveBusyHandlingOption = function (option) {
      if (option && option !== $rootScope.localUser.selectedBusyHandlingOption) {
        $rootScope.localUser.selectedBusyHandlingOption = option;
        LogSvc.info('[TelephonySettingsSvc]: Set busy handling option to ', option);
        UserProfileSvc.saveSetting(Constants.UserSettingKey.SECOND_TELEPHONY_CALL_ROUTING, option);
      }
    };

    /**
     * Saves the user's busy handling selection when on teams call.
     * @param {Object} option the user's busy handling enabled/disabled selection.
     */
    this.saveTeamsCallBusyHandlingOption = function (enabled) {
      if (enabled !== $rootScope.localUser.isTeamsCallBusyHandlingEnabled) {
        $rootScope.localUser.isTeamsCallBusyHandlingEnabled = enabled;
        LogSvc.info('[TelephonySettingsSvc]: Set teams call busy handling to ', enabled);
        UserProfileSvc.saveSetting(Constants.UserSettingKey.MS_TEAMS_CALL_INCOMING_CALL_ROUTING, enabled);
      }
    };

    /**
     * Returns whether telephony is available to the user.
     * @returns {Boolean} true if telephony is available to the user, false otherwise.
     */
    this.isTelephonyAvailable = function () {
      return !!$rootScope.localUser.telephonyAvailable;
    };

    /**
     * Returns the user's caller id if it is different from the user's phone number
     * @returns {String} the user's called id.
     */
    this.getUnifyPhoneCallerId = function () {
      return $rootScope.localUser.phoneNumber !== $rootScope.localUser.callerId ? $rootScope.localUser.callerId : null;
    };

    /**
     * Returns the rerouting phone number (alternative number) from the user's telephony configutation.
     * @returns {String} the rerouting phone number.
     */
    this.getReroutingPhoneNumber = function () {
      return $rootScope.localUser.reroutingPhoneNumber;
    };

    /**
     * Returns whether rerouting is enabled.
     * @returns {Boolean} true if rerouting is enabled, false otherwise.
     */
    this.isReroutingEnabled = function () {
      return !!$rootScope.localUser.reroutingPhoneNumber;
    };

    /**
     * Returns the type of the telephony connector associated to the user.
     * @returns {Boolean} the telephony connector type.
     */
    this.getAssociatedTelephonyConnectorType = function () {
      return $rootScope.localUser.associatedTelephonyUserType;
    };

    /**
     * Returns whether the user's PBX has pick up group feature.
     * @returns {Boolean} true if the PBX has pick up group feature.
     */
    this.isPickupGroupAvailable = function () {
      return !!$rootScope.localUser.pbxCallPickupSupported;
    };

    /**
     * Returns whether the user's PBX voice mail service is enabled.
     * @returns {Boolean} true if the user PBX voice mail service is enabled, false otherwise.
     */
    this.isPBXVoiceMailEnabled = function () {
      return !!$rootScope.localUser.voicemailPBXEnabled;
    };

    /**
     * Returns whether the user has a telephony service that requires SIP registration.
     * @returns {Boolean} true if SIP registration is required.
     */
    this.isSIPRegistrationTelephony = function () {
      return !!$rootScope.localUser.isRegisterTC;
    };

    /**
     * Returns whether the uses has a CTI enabled OsBiz telephony service.
     * @returns {Boolean} true if the user has a CTI enabled OsBiz telephony service, false otherwise.
     */
    this.isOsBizCTIEnabled = function () {
      return !!$rootScope.localUser.isOsBizCTIEnabled;
    };
    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.TelephonySettingsSvcImpl = TelephonySettingsSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global ArrayBuffer, DataView, Float32Array, Float64Array*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Utils = unifyPhone.Utils;
  var WebRTCAdapter = unifyPhone.WebRTCAdapter;
  var RtcSessionController = unifyPhone.RtcSessionController;

  var RecState = Object.freeze({
    REPLAYING: 'replaying',
    PRE_RECORDING: 'pre-recording',
    RECORDING: 'recording',
    SAVING: 'saving'
  });

  // eslint-disable-next-line max-params, max-lines-per-function
  function RecorderSvcImpl($window, $q, $timeout, LogSvc, DeviceHandlerSvc) {
    // NOSONAR
    LogSvc.debug('New Service: RecorderSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var BUFFER_SIZE = 4096;
    var NUMBER_OF_CHANNELS = 1;
    var MAX_RECORDING_DURATION = 30; // value of seconds

    var SUPPORTED_TYPES = [
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/wav',
      'audio/webm;codecs=opus',
      'video/mp4',
      'video/webm;codecs=vp8, opus',
      'video/webm;codecs=vp9, opus'
    ];

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _stream = null;
    var _activeRecording = null;
    var _recordingTimeout = null;

    // Media Recorder API properties
    var _recorder;
    var _chunks = [];

    // Web Audio API properties
    var _audioCtx = null;
    var _processor = null;
    var _source = null;
    var _config = null;
    var _channel = [];
    var _length = 0;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function isTypeSupported(type) {
      return ($window.MediaRecorder && $window.MediaRecorder.isTypeSupported(type)) || (type === 'audio/wav' && $window.AudioContext);
    }

    function clearData() {
      _chunks = [];
      _channel = [];
      _length = 0;
    }

    function stopMediaRecorder() {
      if (_recorder) {
        try {
          _recorder.ondataavailable = null;
          _recorder.stop();
        } catch (e) {
          LogSvc.error('[RecorderSvc]: Error stopping MediaRecorder. ', e);
        } finally {
          _recorder = null;
        }
      }
    }

    function disconnectAudioContext() {
      if (_processor) {
        try {
          _processor.audioDataAvailable = null;
          _processor.disconnect();
        } catch (e) {
          LogSvc.error('[RecorderSvc]: Error disconnecting processor. ', e);
        } finally {
          _processor = null;
        }
      }
      if (_source) {
        try {
          _source.disconnect();
        } catch (e) {
          LogSvc.error('[RecorderSvc]: Error disconnecting source. ', e);
        } finally {
          _source = null;
        }
      }
      if (_audioCtx) {
        try {
          _audioCtx.close();
        } catch (e) {
          LogSvc.error('[RecorderSvc]: Error closing AudioContext. ', e);
        } finally {
          _audioCtx = null;
        }
      }
    }

    function audioDataAvailable(e) {
      if (!_stream) {
        // We shouldn't get here. Disconnect the audio context.
        disconnectAudioContext();
        return;
      }

      if (e.inputBuffer.numberOfChannels > 0) {
        if (_length === 0) {
          LogSvc.debug('[RecorderSvc]: Audio data is available.');
        }
        var data = e.inputBuffer.getChannelData(0);
        _channel.push(new Float32Array(data));
        _length += _processor.bufferSize;
      }
    }

    function mergeBuffers(channelBuffer, length) {
      LogSvc.debug('[RecorderSvc]: Merging buffers: ', length);
      var result = new Float64Array(length);
      var offset = 0;
      var len = channelBuffer.length;

      for (var i = 0; i < len; i++) {
        var buffer = channelBuffer[i];
        result.set(buffer, offset);
        offset += buffer.length;
      }

      return result;
    }

    function linearInterpolate(before, after, atPoint) {
      return before + (after - before) * atPoint;
    }

    function interpolateArray(data, sampleRate, recordingSampleRate) {
      if (!sampleRate || recordingSampleRate === sampleRate) {
        return data;
      }
      var count = Math.round(data.length * (sampleRate / recordingSampleRate));
      var springFactor = Number((data.length - 1) / (count - 1));

      var result = [data[0]]; // for new allocation
      for (var i = 1; i < count - 1; i++) {
        var tmp = i * springFactor;
        var before = Number(Math.floor(tmp)).toFixed();
        var after = Number(Math.ceil(tmp)).toFixed();
        var atPoint = tmp - before;
        result[i] = linearInterpolate(data[before], data[after], atPoint);
      }
      result[count - 1] = data[data.length - 1]; // for new allocation
      return result;
    }

    function writeUTFBytes(view, offset, string) {
      var len = string.length;
      for (var i = 0; i < len; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    }

    function createWav() {
      try {
        var data = mergeBuffers(_channel, _length);
        var interleaved = interpolateArray(data, _config.sampleRate, _config.recordingSampleRate);
        var interleavedLength = interleaved.length;

        LogSvc.debug('[RecorderSvc]: Creating WAV file: ' + NUMBER_OF_CHANNELS + ' channels, ' + _config.sampleRate + 'Hz, ' + interleavedLength);

        var resultingBufferLength = 44 + interleavedLength * 2;

        var buffer = new ArrayBuffer(resultingBufferLength);

        var view = new DataView(buffer);

        // RIFF chunk descriptor/identifier
        writeUTFBytes(view, 0, 'RIFF');

        // RIFF chunk length
        view.setUint32(4, 44 + interleavedLength * 2, true);

        // RIFF type
        writeUTFBytes(view, 8, 'WAVE');

        // format chunk identifier
        // FMT sub-chunk
        writeUTFBytes(view, 12, 'fmt ');

        // format chunk length
        view.setUint32(16, 16, true);

        // sample format (raw)
        view.setUint16(20, 1, true);

        // stereo (2 channels)
        view.setUint16(22, NUMBER_OF_CHANNELS, true);

        // sample rate
        view.setUint32(24, _config.sampleRate, true);

        // byte rate (sample rate * block align)
        view.setUint32(28, _config.sampleRate * 2, true);

        // block align (channel count * bytes per sample)
        view.setUint16(32, NUMBER_OF_CHANNELS * 2, true);

        // bits per sample
        view.setUint16(34, 16, true);

        // data sub-chunk
        // data chunk identifier
        writeUTFBytes(view, 36, 'data');

        // data chunk length
        view.setUint32(40, interleavedLength * 2, true);

        // write the PCM samples
        var len = interleavedLength;
        var index = 44;
        var volume = 1;
        for (var i = 0; i < len; i++) {
          view.setInt16(index, interleaved[i] * (0x7fff * volume), true);
          index += 2;
        }

        _chunks = [view];
      } catch (e) {
        LogSvc.error('[RecorderSvc]: Failed to create wav file. ', e);
        _chunks = [];
      }
    }

    function stopResources() {
      if (!_stream) {
        return;
      }

      try {
        $timeout.cancel(_recordingTimeout);
        _recordingTimeout = null;

        if (_recorder) {
          stopMediaRecorder();
        } else if (_audioCtx) {
          disconnectAudioContext();
        }
        WebRTCAdapter.stopMediaStream(_stream);
      } catch (e) {
        LogSvc.debug('[RecorderSvc]: Error freeing resources. ', e);
      }

      _stream = null;
    }

    function stopRecording() {
      if (!_activeRecording) {
        return $q.reject('Recorder was not started.');
      }

      var deferred = $q.defer();
      try {
        LogSvc.debug('[RecorderSvc]: Stopping active recording.');
        stopResources();

        if (!_chunks.length && _channel.length) {
          createWav();
        }

        var blob = new Blob(_chunks, { type: _config.mimeType });
        clearData();

        _activeRecording.blob = blob;
        if (typeof _activeRecording.ondone === 'function') {
          _activeRecording.ondone(blob);
        }

        deferred.resolve();
      } catch (error) {
        LogSvc.error('[RecorderSvc]: Failed to stop recording: ', error);
        deferred.reject(error);
      }

      _activeRecording = null;

      return deferred.promise;
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal recording constructor
    ///////////////////////////////////////////////////////////////////////////////////////
    function Recording() {
      this.blob = null;

      this.ondone = null;
      this.oncanceled = null;

      this.isActive = function () {
        return this === _activeRecording;
      };

      this.stop = function () {
        if (this === _activeRecording) {
          return stopRecording();
        }
        return $q.reject('Recording has already stopped');
      };
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    /**
     * Gets supported audio/video or audio-only recording types.
     *
     * @returns {Array} array of all supported types
     */
    this.getSupportedTypes = function () {
      return SUPPORTED_TYPES.filter(function (type) {
        return isTypeSupported(type);
      });
    };

    /**
     * Checks whether the specified recording type is supported.
     *
     * @param {String} type the media type to check
     * @returns {Boolean} true if supported, otherwise false
     */
    this.isTypeSupported = isTypeSupported;

    /**
     * Checks whether there is a recording in progress
     *
     * @returns {Boolean} true if thre is a recording in progress, otherwise false
     */
    this.isRecording = function () {
      return !!_activeRecording;
    };

    /**
     * Starts media recorder with optional config parameter. If this parameter is missing or does not contain
     * mimeType property, then 'audio/wav' with 22kHz is assumed.
     *
     * This method returns a promise, which when resolved will provide the recording object. The caller must set
     * 'ondone' listener to it in order to get the recorded blob.
     *
     * If recorder is already started or the specified recording type is not supported, returned promise will be
     * rejected with error description.
     *
     * @param {Object} config recorder configuration object
     * @returns {Promise<Blob>} promisse that will be resolved on success or rejected with error otherwise
     * @example
     * RecorderSvc.start().then(function (activeRecording) {
     *   activeRecording.ondone = function (blob) {
     *     audio.src = URL.createObjectURL(blob);
     *     audio.play();
     *   }
     * });
     * @example
     * var recording = null;
     * RecorderSvc.start().then(function (activeRecording) {
     *   recording = activeRecording;
     * });
     * // to stop recording later
     * if (recording) {
     *   recording.stop().then(function () {
     *     audio.src = URL.createObjectURL(recording.blob);
     *     audio.play();
     *   });
     * }
     */
    this.start = function (config) {
      if (_stream) {
        return $q.reject('Recording is already in progress.');
      }

      _config = config || {};
      if (!_config.mimeType) {
        _config.mimeType = 'audio/wav';
        _config.sampleRate = 22050;
      }

      if (!isTypeSupported(_config.mimeType)) {
        return $q.reject('Media type is not supported by this recorder: ' + _config.mimeType);
      }

      var deferred = $q.defer();
      WebRTCAdapter.getMediaSources(function (audioSources) {
        var source = Utils.selectMediaDevice(audioSources, RtcSessionController.recordingDevices);
        var audioConstraints = {
          enableAudioAGC: false,
          enableAudioEC: false,
          disableAudioCN: false,
          sourceId: source && source.id
        };
        DeviceHandlerSvc.getUserMedia(
          { audio: audioConstraints, video: false },
          function (stream) {
            _stream = stream;
            clearData();
            try {
              _recorder = new $window.MediaRecorder(stream, _config);
              _recorder.ondataavailable = function (e) {
                _stream && _chunks.push(e.data);
              };
            } catch (e) {
              LogSvc.warn('[RecorderSvc]: Failed to create MediaRecorder.');
              _audioCtx = DeviceHandlerSvc.getAudioContext();
              if (!_audioCtx) {
                LogSvc.warn('[RecorderSvc]: Failed to create AudioContext.');
                deferred.reject('Recorder cannot be started.');
                return;
              }
              _source = _audioCtx.createMediaStreamSource(stream);
              _processor = _audioCtx.createScriptProcessor(BUFFER_SIZE, NUMBER_OF_CHANNELS, NUMBER_OF_CHANNELS);
              _processor.onaudioprocess = audioDataAvailable;
              _processor.connect(_audioCtx.destination);
              _source.connect(_processor);
              _config.recordingSampleRate = _audioCtx.sampleRate;
            }
            LogSvc.info('[RecorderSvc]: Media stream has been successfully started.');
            var recording = new Recording();

            _recordingTimeout = $timeout(
              function () {
                LogSvc.info('[RecorderSvc]: Max duration reached. Stop the recording.');
                _recordingTimeout = null;
                recording.stop();
              },
              (_config.maxDuration || MAX_RECORDING_DURATION) * 1000
            );

            _activeRecording = recording;
            deferred.resolve(_activeRecording);
          },
          function (error) {
            LogSvc.error('[RecorderSvc]: Failed to access user media: ', error);
            deferred.reject(error);
          }
        );
      });
      return deferred.promise;
    };

    /**
     * Cancels the active media recorder. This method stops all running resources and clears recording data.
     * If recorder was not started no operation is done.
     */
    this.cancelRecording = function () {
      if (!_activeRecording) {
        return;
      }

      try {
        LogSvc.debug('[RecorderSvc]: Cancel active recording');
        stopResources();
        clearData();

        if (typeof _activeRecording.oncanceled === 'function') {
          _activeRecording.oncanceled();
        }
      } catch (e) {}
      _activeRecording = null;
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.RecorderSvcImpl = RecorderSvcImpl;
  unifyPhone.Enums = unifyPhone.Enums || {};
  unifyPhone.Enums.RecState = RecState;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global RegistrationState*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Enums = unifyPhone.Enums;

  // eslint-disable-next-line max-params, max-lines-per-function
  function CallAssociatedNotificationsSvcImpl($rootScope, $timeout, LogSvc, PubSubSvc, PopupSvc, CallControlSvc, DeviceHandlerSvc) { // NOSONAR
    LogSvc.debug('New Service: CallAssociatedNotificationsSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var TELEPHONY_DOWN_TIMEOUT = 5000;

    var CallWarningType = Object.freeze({
      RTP_STATS: 'RTP_STATS',
      OUTPUT_DEVICE: 'OUTPUT_DEVICE'
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var _telephonyDownTimer = null;
    var _telephonyStatus = null;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function showInfo(options) {
      options = options || {};
      var activeCall;
      if (!options.call) {
        activeCall = CallControlSvc.getActiveCall();
        if (!options.callId || !activeCall || activeCall.callId !== options.callId) {
          LogSvc.warn('[CallAssociatedNotificationsSvc]: No or different active call. Do not show warning message:', options);
          return;
        }
      } else {
        activeCall = options.call;
      }
      if (!options.type || !options.res) {
        LogSvc.warn('[CallAssociatedNotificationsSvc]: Missing parameters. Do not show warning message:', options);
        return;
      }
      var rememberField;
      if (options.rememberDismissed) {
        switch (options.type) {
          case CallWarningType.RTP_STATS:
            rememberField = 'doNotReportQualityIssues';
            break;
          case CallWarningType.OUTPUT_DEVICE:
            rememberField = 'doNotReportAudioDeviceIssues';
            break;
        }
        if (!rememberField || activeCall[rememberField]) {
          // User already dismissed this message. Don't show again
          return;
        }
      }
      var popupOptions = {
        id: 'id_Active' + options.type + 'Warning_' + activeCall.callId,
        message: options.res,
        manual: true
      };
      if (options.resParams) {
        popupOptions.messageParams = options.resParams;
      }

      if (options.rememberDismissed) {
        popupOptions.onManualClose = function () {
          // User dismissed modal manually, don't show it again for this call
          activeCall[rememberField] = true;
          LogSvc.info('[CallAssociatedNotificationsSvc]: User dismissed ' + options.type + ' warning message. Do not show again for this call.');
        };
      }
      PopupSvc.info(popupOptions);
    }

    function closeInfo(options) {
      options = options || {};
      if (!options.callId) {
        return;
      }
      if (options.type) {
        PopupSvc.closeInfo('id_Active' + options.type + 'Warning_' + options.callId);
        return;
      }
      // Close all warning messages related to this call
      Object.keys(CallWarningType).forEach(function (key) {
        PopupSvc.closeInfo('id_Active' + CallWarningType[key] + 'Warning_' + options.callId);
      });
    }

    function publishTelephonyAvailability() {
      LogSvc.debug('[CallAssociatedNotificationsSvc]: Publish /telephony/availability event: ', _telephonyStatus);
      PubSubSvc.publish('/telephony/availability', [!!_telephonyStatus]);
    }

    function cancelTelephonyDownTimer() {
      if (_telephonyDownTimer) {
        $timeout.cancel(_telephonyDownTimer);
        _telephonyDownTimer = null;
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    function onCallRtpStatsWarnRaised(callId) {
      LogSvc.debug('[CallAssociatedNotificationsSvc]: Received /call/rtp/threshholdExceeded/detected');
      showInfo({
        type: CallWarningType.RTP_STATS,
        callId: callId,
        res: 'res_PoorConnectionQuality',
        rememberDismissed: true
      });
    }
    PubSubSvc.subscribe('/call/rtp/threshholdExceeded/detected', onCallRtpStatsWarnRaised);

    function onCallRtpStatsWarnCleared(callId) {
      LogSvc.debug('[CallAssociatedNotificationsSvc]: Received /call/rtp/threshholdExceeded/cleared');
      closeInfo({ callId: callId, type: CallWarningType.RTP_STATS });
    }
    PubSubSvc.subscribe('/call/rtp/threshholdExceeded/cleared', onCallRtpStatsWarnCleared);

    function onCallNoOutgoingRtpPackets(callId) {
      LogSvc.debug('[CallAssociatedNotificationsSvc]: Received /call/rtp/noOutgoingPackets');
      // Dismiss the current warning message and show a new one
      closeInfo({ callId: callId, type: CallWarningType.RTP_STATS });
      showInfo({
        type: CallWarningType.RTP_STATS,
        callId: callId,
        res: 'res_NoOutgoingRtpPackets'
      });
    }
    PubSubSvc.subscribe('/call/rtp/noOutgoingPackets', onCallNoOutgoingRtpPackets);

    function onCallIceConnectionState(callId, state) {
      LogSvc.debug('[CallAssociatedNotificationsSvc]: Received /call/rtp/iceConnectionState event, state:', state);
      closeInfo({ callId: callId, type: CallWarningType.RTP_STATS });
      if (state === 'disconnected') {
        showInfo({
          type: CallWarningType.RTP_STATS,
          callId: callId,
          res: 'res_RTCIceDisconnected'
        });
      }
    }
    PubSubSvc.subscribe('/call/rtp/iceConnectionState', onCallIceConnectionState);

    function onCallOutputDeviceFailed() {
      var activeCall = CallControlSvc.getActiveCall();
      if (activeCall) {
        LogSvc.debug('[CallAssociatedNotificationsSvc]: Received /call/devices/audio/outputFailed event');
        var devName = $rootScope.i18n.localize(activeCall.playbackDevice || 'res_Unknown');
        showInfo({
          type: CallWarningType.OUTPUT_DEVICE,
          call: activeCall,
          res: 'res_CallOutputDeviceCouldNotBeSelected',
          resParams: [devName],
          rememberDismissed: true
        });
      }
    }
    PubSubSvc.subscribe('/call/devices/audio/outputFailed', onCallOutputDeviceFailed);

    function onCallEnded(call) {
      LogSvc.debug('[CallAssociatedNotificationsSvc]: Received /call/ended event');
      closeInfo({ callId: call.callId });
    }
    PubSubSvc.subscribe('/call/ended', onCallEnded);

    function onSendDtmfFailed(err, digit) {
      LogSvc.debug('[CallAssociatedNotificationsSvc]: Received /call/sendDtmf/failed event');
      PopupSvc.info({
        message: err,
        messageParams: [digit]
      });
    }
    PubSubSvc.subscribe('/call/sendDtmf/failed', onSendDtmfFailed);

    function onSendDtmfSucceeded() {
      LogSvc.debug('[CallAssociatedNotificationsSvc]: Received /call/singleDigitSent event');
      DeviceHandlerSvc.playBeep();
    }
    PubSubSvc.subscribe('/call/singleDigitSent', onSendDtmfSucceeded);

    function onTelephonyData(data, telConv, atcRegState) {
      if (!data || !data.telephonyAvailableForUser) {
        return;
      }
      LogSvc.debug('[CallAssociatedNotificationsSvc]: Received /telephony/data event');
      if (_telephonyStatus === null && data.phoneCallsAvailable) {
        // Don't show the info on first login, unless the status is DOWN
        _telephonyStatus = data.phoneCallsAvailable;
        publishTelephonyAvailability();
        return;
      }
      if (_telephonyStatus === data.phoneCallsAvailable) {
        return;
      }
      var lastStatus = _telephonyStatus;
      _telephonyStatus = data.phoneCallsAvailable;
      if (!data.phoneCallsAvailable) {
        if ($rootScope.registrationState === RegistrationState.Registered) {
          LogSvc.debug('[CallAssociatedNotificationsSvc]: Delay publishing telephony DOWN by 5 seconds');
          cancelTelephonyDownTimer();
          _telephonyDownTimer = $timeout(function () {
            _telephonyDownTimer = null;
            if (lastStatus && !_telephonyStatus) {
              // Only publish event if status went from Available to Unavailable
              publishTelephonyAvailability();
            }
            PopupSvc.info({
              id: 'id_TelephonyAlert',
              message:
                atcRegState === Enums.AtcRegistrationState.ForcedUnregistered ? 'res_DialingNumbersForcedUnregistered' : 'res_DialingNumbersNotAvailable',
              manual: true
            });
          }, TELEPHONY_DOWN_TIMEOUT);
          return;
        }
      } else if (_telephonyDownTimer) {
        cancelTelephonyDownTimer();
      } else {
        PopupSvc.closeInfo('id_TelephonyAlert');
        PopupSvc.info({ message: 'res_DialingNumbersAvailableAgain' });
      }
      publishTelephonyAvailability();
    }
    PubSubSvc.subscribe('/telephony/data', onTelephonyData);

    function onRegistrationState(state) {
      LogSvc.debug('[CallAssociatedNotificationsSvc]: Received /registration/state event');
      cancelTelephonyDownTimer();
      if (state === RegistrationState.Registered) {
        _telephonyStatus = null;
      } else {
        // Close telephony alert message (if open)
        PopupSvc.closeInfo('id_TelephonyAlert');
      }
    }
    PubSubSvc.subscribe('/registration/state', onRegistrationState);

    function onChangeMediaFailed(call, error) {
      LogSvc.debug('[CallAssociatedNotificationsSvc]: Received /call/changeMediatype/failed event');
      PopupSvc.handleCallError(error);
    }
    PubSubSvc.subscribe('/call/changeMediatype/failed', onChangeMediaFailed);

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.showInfo = showInfo;

    this.closeInfo = closeInfo;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.CallAssociatedNotificationsSvcImpl = CallAssociatedNotificationsSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var RtcSessionController = unifyPhone.RtcSessionController;

  // eslint-disable-next-line max-params, max-lines-per-function, no-unused-vars
  function RtcSvcImpl($rootScope, $q, LogSvc, PubSubSvc, PopupSvc, ExtensionSvc, UnifyPhoneCallControlSvc) {
    // NOSONAR
    LogSvc.debug('New Service: RtcSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal variables
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function checkPermission() {
      // Change audio codec from tenant
      // NGTC-8941: NGTC-OSV : Loud disturbing ringing noise instead of Comfort Noise.
      // When user refreshes or signs in then the default value for RtcSessionController.disableAudioCN is false
      // only when receiving comfortNoiseDisabled -> true we change it to true
      if ($rootScope.localUser && $rootScope.localUser.comfortNoiseDisabled) {
        RtcSessionController.disableAudioCN = true;
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/localUser/tenantSettingsUpdate', function () {
      checkPermission();
    });

    PubSubSvc.subscribe('/registration/stuff', function () {
      checkPermission();
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.RtcSvcImpl = RtcSvcImpl;
  unifyPhone.Enums = unifyPhone.Enums || {};

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var FaqArticle = unifyPhone.Enums.FaqArticle;
  var ShortcutActions = unifyPhone.ShortcutActions;
  var ShortcutActionTypes = unifyPhone.Enums.ShortcutActionTypes;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-params, max-lines-per-function
  function ShortcutSvcImpl($rootScope, LogSvc, UtilSvc, PubSubSvc, PopupSvc, LocalStoreSvc) {
    // NOSONAR
    LogSvc.debug('New Service: ShortcutSvc');

    ///////////////////////////////////////////////////////////////////////////
    // Internal variables
    ///////////////////////////////////////////////////////////////////////////
    var _displayedActions = null; // The display information for actions shown on the shortcuts information popup
    var _shiftActions = []; // The actions that are triggered in combination with the Shift-key (a search index)
    var _ctrlActions = []; // The actions that are triggered in combination with the Ctrl-key (a search index)
    var _cmdActions = []; // The actions that are triggered in combination with the Cmd-key (a search index, MacOS-only)
    var _altActions = []; // The actions that are triggered in combination with the Alt-key (a search index)

    var _shortcutsPopup = null; // The reference to the open shortcuts information popup
    var _openPopups = 0; // The number of currently open popups in the application

    ///////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////
    function assembleActionInformation() {
      _displayedActions = {};

      // Assemble display information for non-hidden actions shown on the shortcuts information popup
      ShortcutActions.forEach(function (action) {
        if (!action.hidden && (!action.labFeature || $rootScope.unifyPhoneLabs[action.labFeature])) {
          var information = getActionInformation(action);
          if (!_displayedActions[information.category]) {
            _displayedActions[information.category] = [];
          }
          _displayedActions[information.category].push(information); // map actions by category name
        }
      });
    }

    function getActionInformation(action) {
      var map = $rootScope.i18n.map || [];
      var category = map[action.category.name];
      var name = map[action.name];
      var modifiers = [];

      action.shift && modifiers.push(map.res_ShortcutModifierShift);
      action.ctrl && modifiers.push(map.res_ShortcutModifierCtrl);
      action.cmd && modifiers.push(map.res_ShortcutModifierCommand);
      action.alt && modifiers.push(map[Utils.isMacOs ? 'res_ShortcutModifierOption' : 'res_ShortcutModifierAlt']);
      modifiers.push(action.key.name ? map[action.key.name] : action.key.key);
      return { category: category, name: name, shortcut: modifiers.join(' + ') };
    }

    function toggleShortcutsPopup() {
      if (!_displayedActions) {
        assembleActionInformation();
      }
      if (!_shortcutsPopup) {
        _shortcutsPopup = PopupSvc.openCustomModal({
          templateUrl: 'views/modals/keyboardShortcuts.html',
          controller: 'GenericDialogCtrl',
          windowClass: 'keyboard-shortcuts',
          size: 'custom',
          resolve: {
            options: function () {
              return {
                actions: _displayedActions,
                accessibilityFAQ: UtilSvc.getFaqUrl(FaqArticle.ACCESSIBILITY),
                shortcutsFAQ: UtilSvc.getFaqUrl(FaqArticle.SHORTCUTS)
              };
            }
          }
        });
        _shortcutsPopup.result
          .catch(function () {}) // Handle reject to prevent 'Possibly unhandled rejection' error
          .then(function () {
            _shortcutsPopup = null;
          });
      } else {
        _shortcutsPopup.close();
      }
    }

    function triggerKeyboardAction(action) {
      if ($rootScope.isFullBrowser && !action.fullScreen) {
        return; // Ignore shortcut that is not supported for full browser or guest application
      }
      if (action.labFeature && !$rootScope.unifyPhoneLabs[action.labFeature]) {
        return; // Ignore shortcut that is not an activated lab feature
      }

      // eslint-disable-next-line complexity
      $rootScope.$apply(function () {
        // NOSONAR
        switch (
          action.id // NOSONAR
        ) {
          case ShortcutActionTypes.TOGGLE_SHORTCUTS_POPUP:
            toggleShortcutsPopup();
            break;
          case ShortcutActionTypes.NEW_PHONE_CALL:
            newPhoneCall();
            break;
          case ShortcutActionTypes.OPEN_SETTINGS:
            openSettings();
            break;
          case ShortcutActionTypes.ANSWER_INCOMING_CALL:
            answerIncomingCall();
            break;
          case ShortcutActionTypes.DECLINE_INCOMING_CALL:
            declineIncomingCall();
            break;
          case ShortcutActionTypes.START_AUDIO_CALL:
            startCall({ audio: true, video: false });
            break;
          case ShortcutActionTypes.START_VIDEO_CALL:
            startCall({ audio: true, video: true });
            break;
          case ShortcutActionTypes.TOGGLE_VIDEO_IN_CALL:
            toggleVideoInCall();
            break;
          case ShortcutActionTypes.TOGGLE_SCREEN_SHARE_IN_CALL:
            toggleScreenShareInCall();
            break;
          case ShortcutActionTypes.STOP_REMOTE_CONTROL:
            stopRemoteControl();
            break;
          case ShortcutActionTypes.MUTE_ALL_PARTICIPANTS:
            muteAllParticipants();
            break;
          case ShortcutActionTypes.STOP_LEAVE_CALL:
            stopLeaveCall();
            break;
          case ShortcutActionTypes.MUTE_UNMUTE_AUDIO:
            muteUnmuteAudio();
            break;
          case ShortcutActionTypes.TOGGLE_ZOOM_CONTROL:
            toggleZoomControl();
            break;
          case ShortcutActionTypes.ENABLE_ALPHA_FEATURES:
            enableAlphaFeatures();
            break;
          case ShortcutActionTypes.ENABLE_DEMO_FEATURES:
            enableDemoFeatures();
            break;
          case ShortcutActionTypes.ENABLE_CLOSED_BETA_FEATURES:
            enableClosedBetaFeatures();
            break;
          case ShortcutActionTypes.OPEN_LOG_MANAGEMENT_POPUP:
            openLogManagementPopup();
            break;
          case ShortcutActionTypes.REPORT_ISSUE:
            reportIssue();
            break;
          case ShortcutActionTypes.TEST_CANDIDATES_COLLECTION:
            testCandidatesCollection();
            break;
          default:
            break;
        }
      });
    }

    function newPhoneCall() {
      LogSvc.debug('[ShortcutSvc]: Publish /shortcut/newPhoneCall event.');
      PubSubSvc.publish('/shortcut/newPhoneCall');
    }

    function openSettings() {
      LogSvc.debug('[ShortcutSvc]: Publish /shortcut/openSettings event.');
      PubSubSvc.publish('/shortcut/openSettings');
    }

    function answerIncomingCall() {
      LogSvc.debug('[ShortcutSvc]: Publish /shortcut/answerIncomingCall event.');
      PubSubSvc.publish('/shortcut/answerIncomingCall');
    }

    function declineIncomingCall() {
      LogSvc.debug('[ShortcutSvc]: Publish /shortcut/declineIncomingCall event.');
      PubSubSvc.publish('/shortcut/declineIncomingCall');
    }

    function startCall(mediaType) {
      LogSvc.debug('[ShortcutSvc]: Publish /shortcut/startCall event. mediaType = ', mediaType);
      PubSubSvc.publish('/shortcut/startCall', [mediaType]);
    }

    function toggleVideoInCall() {
      LogSvc.debug('[ShortcutSvc]: Publish /shortcut/toggleVideoInCall event.');
      PubSubSvc.publish('/shortcut/toggleVideoInCall');
    }

    function toggleScreenShareInCall() {
      LogSvc.debug('[ShortcutSvc]: Publish /shortcut/toggleScreenShareInCall event.');
      PubSubSvc.publish('/shortcut/toggleScreenShareInCall');
    }

    function stopRemoteControl() {
      LogSvc.debug('[ShortcutSvc]: Publish /shortcut/stopRemoteControl event.');
      PubSubSvc.publish('/shortcut/stopRemoteControl');
    }

    function muteAllParticipants() {
      LogSvc.debug('[ShortcutSvc]: Publish /shortcut/muteAllParticipants event.');
      PubSubSvc.publish('/shortcut/muteAllParticipants');
    }

    function stopLeaveCall() {
      LogSvc.debug('[ShortcutSvc]: Publish /shortcut/stopLeaveCall event.');
      PubSubSvc.publish('/shortcut/stopLeaveCall');
    }

    function muteUnmuteAudio() {
      LogSvc.debug('[ShortcutSvc]: Publish /shortcut/muteUnmuteAudio event.');
      PubSubSvc.publish('/shortcut/muteUnmuteAudio');
    }

    function toggleZoomControl() {
      LogSvc.debug('[ShortcutSvc]: Publish /shortcut/toggleZoomControl event.');
      PubSubSvc.publish('/shortcut/toggleZoomControl');
    }

    function enableAlphaFeatures() {
      LogSvc.debug('[ShortcutSvc]: User pressed Ctrl+Shift+Alt+X. Enable alpha features.');
      $rootScope.enableAlphaFeatures = true;
      LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.ENABLE_ALPHA_TESTING, true);
    }

    function enableDemoFeatures() {
      LogSvc.debug('[ShortcutSvc]: User pressed Ctrl+Shift+Alt+D. Enable demo features.');
      $rootScope.showDemoFeatures = true;
    }

    function enableClosedBetaFeatures() {
      LogSvc.debug('[ShortcutSvc]: User pressed Ctrl+Shift+Alt+B. Enable closed beta features.');
      $rootScope.showBetaFeatures = true;
    }

    function openLogManagementPopup() {
      if (!_openPopups && LogSvc.isFileLogAvailable()) {
        LogSvc.debug('[ShortcutSvc]: User pressed Ctrl+Shift+Alt+L. Open logfile management.');
        PopupSvc.openCustomModal({
          templateUrl: 'views/modals/editLogLevel.html',
          controller: 'EditLogLevelCtrl'
        }).result.catch(function () {
          // Handle reject to prevent 'Possibly unhandled rejection' error
        });
      }
    }

    function reportIssue() {
      LogSvc.debug('[ShortcutSvc]: User pressed Ctrl+Shift+Alt+R. Start to report issue');
      var textItem = {
        contentType: Constants.TextItemContentType.RICH,
        fileList: [],
        supportType: 'issue',
        placeholder: 'res_SupportIssuePlaceholder'
      };
      LogSvc.getLogFilesForDownload(function (logFiles) {
        if (logFiles) {
          textItem.fileList = logFiles.slice(0);
        }
        LogSvc.debug('[ShortcutSvc]: Publish /conversation/supportMessage event.');
        PubSubSvc.publish('/conversation/supportMessage', [textItem]);
      }, false);
    }

    function testCandidatesCollection() {
      LogSvc.debug('[ShortcutSvc]: Publish /shortcut/testCandidatesCollection event.');
      PubSubSvc.publish('/shortcut/testCandidatesCollection');
    }

    function init() {
      // Create some indexes to allow searching only relevant actions according to the given event
      ShortcutActions.forEach(function (action) {
        action.shift && _shiftActions.push(action);
        action.ctrl && _ctrlActions.push(action);
        action.cmd && _cmdActions.push(action);
        action.alt && _altActions.push(action);
      });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/language/update', function () {
      LogSvc.debug('[ShortcutSvc]: Received /language/update event');
      assembleActionInformation();
    });

    PubSubSvc.subscribe('/popup/opened', function () {
      LogSvc.debug('[ShortcutSvc]: Received /popup/opened event');
      _openPopups++; // Track number of open popups to be able to ignore shortcuts while one is open
    });

    PubSubSvc.subscribe('/popup/closed', function () {
      LogSvc.debug('[ShortcutSvc]: Received /popup/closed event');
      _openPopups = Math.max(0, _openPopups - 1);
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    /**
     * Opens the keyboard shortcuts information popup.
     *
     * @returns {undefined}
     */
    this.openInformationPopup = function () {
      !_shortcutsPopup && toggleShortcutsPopup();
    };

    /**
     * Returns the shortcut text of the given shortcut action.
     *
     * @param {ShortcutActionTypes} shortcutActionType a string constant defined in ShortcutActionTypes
     * @returns {String} translated text of the shortcut
     */
    this.getShortcut = function (shortcutActionType) {
      var action = ShortcutActions.find(function (shortcutAction) {
        return shortcutAction.id === shortcutActionType;
      });
      return action ? getActionInformation(action).shortcut : '';
    };

    /**
     * Processes the given key event by executing the corresponding action that matches the pressed key combination. Those actions
     * that might manipulate the DOM tree will be processed within the corresponding shortcuts directive.
     *
     * @param {Object} event the key event
     * @returns {String} the ID of the matching action or null if none matched
     */
    this.processKeyEvent = function (event) {
      var cmdPressed = Utils.isMacOs && event.metaKey;
      if (event.ctrlKey || cmdPressed || event.altKey) {
        // There is no action with Shift modifier only!
        var searchedActions;
        if (event.shiftKey) {
          searchedActions = _shiftActions;
        } else if (event.ctrlKey) {
          searchedActions = _ctrlActions;
        } else {
          searchedActions = cmdPressed ? _cmdActions : _altActions;
        }

        // Retrieve the action that matches the pressed key combination
        var eventKey = event.key ? event.key.toUpperCase() : null;
        var action = searchedActions.find(function (shortcutAction) {
          return (
            event.shiftKey === shortcutAction.shift &&
            event.altKey === shortcutAction.alt &&
            event.ctrlKey === shortcutAction.ctrl &&
            cmdPressed === shortcutAction.cmd &&
            (eventKey === shortcutAction.key.key || event.keyCode === shortcutAction.key.code)
          );
        });

        // Execute the matching action only if no popup is open or request is to close the shortcuts popup
        if (action && !action.ignore && (!_openPopups || (_shortcutsPopup && action.id === ShortcutActionTypes.TOGGLE_SHORTCUTS_POPUP))) {
          // Check whether the user has enabled the shortcut keys in the settings
          var shortcutsEnabled = !$rootScope.localUser || $rootScope.localUser.keyboardShortcutsEnabled;
          if (!shortcutsEnabled && !action.bypassSettings) {
            return null;
          }

          triggerKeyboardAction(action);
          event.preventDefault();
          event.stopPropagation();
          return action.id; // return action ID for DOM related actions
        }
      }

      return null;
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Initialization
    ///////////////////////////////////////////////////////////////////////////////////////
    init();

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.ShortcutSvcImpl = ShortcutSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // eslint-disable-next-line max-params, max-lines-per-function
  function FileUploadSvcImpl($rootScope, $http, $q, LogSvc) {
    // NOSONAR

    LogSvc.debug('New Service: FileUploadSvc');

    // Imports
    // Keep it inside service, so unit tests can mock this modules
    var FileUpload = unifyPhone.FileUpload;
    var Utils = unifyPhone.Utils;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////

    function buildUploadData(userId, avatars) {
      return ['b', 's'].reduce(function (res, size) {
        var b64data = avatars[size];
        if (b64data === '') {
          return res;
        }
        var avatarImgFormat = Utils.getAvatarImageFormat(b64data);
        var type = 'image/' + avatarImgFormat;
        var name = userId + '_' + size + '.' + avatarImgFormat;

        var data = { b64Data: b64data, mimeType: type, name: name };
        var isSmall = size === 's';
        if (isSmall) {
          res.small = data;
        } else {
          res.large = data;
        }
        return res;
      }, {});
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////
    this.uploadAvatars = function (avatars) {
      // Upload the s/b avatar images, delete old images.
      var userId = $rootScope.localUser.userId;
      var url = unifyPhone.__domain + '/api' + unifyPhone.__path + '/fileapi';

      var uploadSizes = ['b', 's'].map(function (size) {
        var deferred = $q.defer();
        var b64data = avatars[size];
        if (b64data === '') {
          deferred.resolve([{ fileId: '' }, { fileId: '' }]);
          return deferred.promise;
        }
        var avatarImgFormat = Utils.getAvatarImageFormat(b64data);
        var blob = FileUpload.dataURItoBlob(b64data, 'image/' + avatarImgFormat);

        blob.name = userId + '_' + size + '.' + avatarImgFormat;
        if (size === 's') {
          blob.isSmall = true;
        }
        LogSvc.debug('[FileUploadSvc]: Uploading profile picture -', blob.name);

        $http
          .post(url, blob, {
            headers: {
              'Content-Type': 'image/' + avatarImgFormat,
              'Content-Disposition': 'attachment; filename="' + blob.name + '"'
            },
            transformRequest: angular.identity
          })
          .then(function (res) {
            var id = res.data[0].id;
            var uploadedFile = {
              fileId: id,
              url: url + '/' + blob.name + '?fileid=' + id,
              deleteUrl: url + '?fileid=' + id,
              fileName: blob.name,
              mimeType: blob.type,
              size: blob.size,
              isSmall: blob.isSmall,
              isImage: true
            };
            LogSvc.debug('[FileUploadSvc]: Uploaded profile picture -', uploadedFile);
            deferred.resolve(uploadedFile);
          })
          .catch(function (res) {
            LogSvc.error('[FileUploadSvc]: Upload failed - ' + blob.name + ' , status =' + res.status + ', error =', res.data);
            deferred.reject(res.data);
          });
        return deferred.promise;
      });

      return $q.all(uploadSizes).then(function (data) {
        return {
          b: data[0],
          s: data[1]
        };
      });
    };

    // Uploads data as json/base64 strings
    this.uploadAvatarsV2 = function (avatars) {
      var deferred = $q.defer();
      avatars = avatars || {};
      // Upload the s/b avatar images, delete old images.
      var userId = $rootScope.localUser.userId;
      var url = unifyPhone.__domain + '/api' + unifyPhone.__path + '/fileApi/avatars';
      var uploadData = buildUploadData(userId, avatars);
      var options = {
        headers: { 'Content-Type': 'application/json' }
      };
      $http
        .post(url, uploadData, options)
        .then(function (res) {
          deferred.resolve({
            b: res.data.large,
            s: res.data.small
          });
        })
        .catch(function (res) {
          LogSvc.error('[FileUploadSvc]: Upload failed, status = ' + res.status + ', error =', res.data);
          deferred.reject(res.data);
        });

      return deferred.promise;
    };
    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.FileUploadSvcImpl = FileUploadSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var Constants = unifyPhone.Constants;
  var CallState = unifyPhone.Enums.CallState;
  var CstaCallState = unifyPhone.Enums.CstaCallState;
  var CallDirection = unifyPhone.Enums.CallDirection;
  var Targets = unifyPhone.Enums.Targets;
  var Utils = unifyPhone.Utils;
  var RoutingOptions = unifyPhone.RoutingOptions;
  var UserToUserHandler = unifyPhone.UserToUserHandlerSingleton;
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;

  // eslint-disable-next-line max-params, max-lines-per-function
  function PhoneCallSvcImpl($rootScope, $q, $timeout, LocalStoreSvc, LogSvc, PubSubSvc, UserProfileSvc, CallControlSvc, AtcRegistrationSvc, PopupSvc) { // NOSONAR
    LogSvc.debug('New Service: PhoneCallSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////
    var SEND_DIGIT_TIMEOUT = 7000; // This timeout is to handle sending DTMF via CSTA
    var VOICEMAIL_TIMEOUT = 2000; // Add some time for ATC to reregister
    var _getMessageWaitingTimer = null;
    var _sendDigitsQueue = [];
    var _sendDigitTimeout = null;
    var _that = this;

    var _phoneConversation = null;
    var _telephonyAvailable = false;

    var _defaultCallDevice = null;
    var _callDevices = [];
    var _pushDevices = [];
    var _answerDevices = [];
    var _defaultAnswerDevice = null;
    var _defaultPushDevice = null;

    var _calls = [];
    var _atcCalls = [];
    var _multipleAtcCalls = false;

    var _userToUserHandler = UserToUserHandler.getInstance();
    var _clientApiHandler = ClientApiHandler.getInstance();

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function initPhoneCalls() {
      var phoneCalls = CallControlSvc.getPhoneCalls();

      if (phoneCalls.length > 0) {
        _calls = phoneCalls.slice(0);
      } else {
        _calls = [];
        if (_phoneConversation.call && !_phoneConversation.call.isAtcRemote) {
          _calls.push(_phoneConversation.call);
        }
      }

      var atcCalls = CallControlSvc.getAtcCalls();
      if (!Utils.isEmptyArray(_calls)) {
        atcCalls = atcCalls.filter(function (call) {
          return !call.pickupNotification && !(call.atcCallInfo && call.atcCallInfo.isQueuedIncomingCall());
        });
      }
      Array.prototype.push.apply(_calls, atcCalls);

      publishPhoneCallsInitEvent();

      var anyHeldAtcCall = atcCalls.some(function (call) {
        return call.isHolding();
      });
      var anyActiveAtcCall = atcCalls.some(function (call) {
        return call.checkCstaState([CstaCallState.Active, CstaCallState.Conference]);
      });
      _multipleAtcCalls = atcCalls.length > 1 && anyActiveAtcCall && anyHeldAtcCall;
      _atcCalls = atcCalls;

      var newAnswerDevices, newDefaultAnswerDevice;
      if (_phoneConversation.call) {
        // Use first device in list as default device.
        newAnswerDevices = CallControlSvc.getAnswerDevices(_phoneConversation.call.callId);
        newDefaultAnswerDevice = newAnswerDevices.shift();
      } else {
        newAnswerDevices = [];
        newDefaultAnswerDevice = Targets.WebRTC;
      }

      if (
        newDefaultAnswerDevice === Targets.WebRTC &&
        (hasAnyMultipleLocalCalls() || ($rootScope.localUser.isOsBizCTIEnabled && atcCalls.length > 1 && phoneCalls.length === 0))
      ) {
        // if there are more than one local calls, the 3rd incoming call can only be answered by one of the the other devices.
        // OSBiz only supports multiple calls using the same device. If only remote calls exists, do not offer to answer locally.
        newDefaultAnswerDevice = newAnswerDevices.shift();
      }

      if (!!newDefaultAnswerDevice && newDefaultAnswerDevice !== Targets.WebRTC && newAnswerDevices.length === 0) {
        // If no devices are left in list and local answer is not possible, then include this only answer device in the answer list too
        newAnswerDevices.push(newDefaultAnswerDevice);
      }

      if (!Utils.compareElements(newAnswerDevices, _answerDevices) || (!!newDefaultAnswerDevice && newDefaultAnswerDevice !== _defaultAnswerDevice)) {
        _defaultAnswerDevice = newDefaultAnswerDevice;
        _answerDevices = newAnswerDevices;
        publishDeviceChangedEvent();
      }
    }

    function getTargetCallDevice(device) {
      if (!device || !device.name) {
        return Targets.WebRTC;
      }
      var targetCallDevice = {};
      switch (device.name) {
        case Targets.WebRTC.name:
          targetCallDevice = Targets.WebRTC;
          break;
        case Targets.Desk.name:
          targetCallDevice = Targets.Desk;
          break;
        case Targets.Cell.name:
          targetCallDevice = Targets.Cell;
          break;
      }
      return _callDevices.indexOf(targetCallDevice) > -1 ? targetCallDevice : Targets.WebRTC;
    }

    function getTargetPushDevice(device) {
      if (!device || !device.name) {
        return _pushDevices[0];
      }
      var targetPushDevice = {};
      switch (device.name) {
        case Targets.Desk.name:
          targetPushDevice = Targets.Desk;
          break;
        case Targets.Cell.name:
          targetPushDevice = Targets.Cell;
          break;
      }
      return _pushDevices.indexOf(targetPushDevice) > -1 ? targetPushDevice : _pushDevices[0];
    }

    function setDefaultCallDevice(device, saveInStorage, dontPublishEvent) {
      if (device && _defaultCallDevice !== device) {
        _defaultCallDevice = device;
        CallControlSvc.setDefaultCallDevice(device);
        if (saveInStorage) {
          LogSvc.info('[PhoneCallSvc]: Saving default outgoing device in Local Storage:', device);
          LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.DEFAULT_PHONE_CALL_DEVICE, _defaultCallDevice);
        }
        if (!dontPublishEvent) {
          publishDeviceChangedEvent();
        }
      }
    }

    function setDefaultPushDevice(device, saveInStorage, dontPublishEvent) {
      if (_defaultPushDevice !== device) {
        // Unlike the _defaultCallDevice, the _defaultPushDevice can be null
        _defaultPushDevice = !$rootScope.localUser.isMiV5000 ? device : null;
        if (saveInStorage) {
          LogSvc.info('[PhoneCallSvc]: Saving default push device in Local Storage:', device);
          LocalStoreSvc.setObjectSync(LocalStoreSvc.keys.DEFAULT_PUSH_CALL_DEVICE, device);
        }
        if (!dontPublishEvent) {
          publishDeviceChangedEvent();
        }
      }
    }

    function initDevices() {
      if (!$rootScope.localUser.isATC && unifyPhone.WebRTCAdapter.enabled) {
        // UTC calls can only be made using WebRTC
        _callDevices = [Targets.WebRTC];
        setDefaultCallDevice(Targets.WebRTC);
        return;
      }

      _callDevices = CallControlSvc.getCallDevices();
      _pushDevices = CallControlSvc.getPushDevices();
      var device = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.DEFAULT_PHONE_CALL_DEVICE);
      setDefaultCallDevice(getTargetCallDevice(device), false, true);

      device = LocalStoreSvc.getObjectSync(LocalStoreSvc.keys.DEFAULT_PUSH_CALL_DEVICE);
      setDefaultPushDevice(getTargetPushDevice(device), true, true);

      _defaultAnswerDevice = Targets.WebRTC;
      publishDeviceChangedEvent();
    }

    function isNumberDialable(number) {
      if (!number) {
        return false;
      }
      if (!Utils.PHONE_DIAL_PATTERN.test(number)) {
        var match = number.match(Utils.PHONE_WITH_EXTENSION_PATTERN);
        return !!match;
      } else {
        return true;
      }
    }

    function pullWithFeatureCode(number) {
      if (!$rootScope.localUser.callerId) {
        return $q.reject('Telephony not available');
      }

      if (!isNumberDialable(number)) {
        return $q.reject('Number not dialable');
      }

      // Split dialed number from DTMF digits
      var split = number.split(',');
      var dialedNumber = split.shift();
      if (!dialedNumber) {
        return $q.reject('No number to dial');
      }

      var destination = {
        dialedDn: dialedNumber,
        mediaType: { audio: true, video: false, desktop: false }
      };

      var deferred = $q.defer();
      CallControlSvc.pullWithFeatureCode(destination, handleCallControlCb(deferred));

      return deferred.promise;
    }

    function dial(number, name, userId, sipHeaders) {
      if (!$rootScope.localUser.callerId) {
        return $q.reject('Telephony not available');
      }

      //FIXME: adapt for MIVB to accept sipuser-56003
      if (!$rootScope.localUser.isMiVB && !isNumberDialable(number)) {
        return $q.reject('Number not dialable');
      }

      // Split dialed number from DTMF digits
      var split = number.split(',');
      var dialedNumber = split.shift();
      var dtmfDigits = split.join(',');
      if (!dialedNumber) {
        return $q.reject('No number to dial');
      }

      var destination = {
        dialedDn: dialedNumber,
        toName: name,
        userId: userId,
        mediaType: { audio: true, video: false, desktop: false },
        sipHeaders: sipHeaders
      };

      if (dtmfDigits) {
        destination.dtmfDigits = Utils.cleanPhoneNumberDigitsWithPin(dtmfDigits);
      }

      var deferred = $q.defer();
      CallControlSvc.dialUsingDefaultDevice(destination, handleCallControlCb(deferred));

      return deferred.promise;
    }

    function canDialfromAlternativeNumber() {
      return !!(
        $rootScope.localUser.reroutingPhoneNumber &&
        (!$rootScope.localUser.isOsBizCTIEnabled || $rootScope.localUser.selectedRoutingOption === RoutingOptions.AlternativeNumber.name)
      );
    }

    function getDevice(deviceName) {
      return Object.values(Targets).find(function (target) {
        return target.name === deviceName;
      });
    }

    function dialFromDevice(deviceName, number, name, userId, sipHeaders) {
      var device = getDevice(deviceName);
      if (device && device !== _defaultCallDevice) {
        setDefaultCallDevice(device, true);
      }

      if (!isNumberDialable(number)) {
        return $q.reject('Number not dialable');
      }

      device = device || (_phoneConversation.call && _phoneConversation.call.getPosition()) || _defaultCallDevice;

      if (device === Targets.WebRTC) {
        return dial(number, name, userId, sipHeaders);
      }

      LogSvc.info('[PhoneCallSvc]: Make call from ' + device.name + ' to ', number);

      // Split dialed number from DTMF digits
      var split = number.split(',');
      var destination = {
        dialedDn: split.shift(),
        dtmfDigits: split.join(',')
      };

      var deferred = $q.defer();
      CallControlSvc.makeAtcCall(device, destination, handleCallControlCb(deferred));
      if (_phoneConversation.call) {
        _phoneConversation.consultation = false;
      }
      return deferred.promise;
    }

    function checkDisclaimer(fn) {
      // Check if disclaimer needs to be displayed
      var disclaimerAccepted = UserProfileSvc.getSetting(Constants.UserSettingKey.EMERGENCY_CALL_DISCLAIMER);
      if (disclaimerAccepted && disclaimerAccepted.booleanValue) {
        return fn();
      }

      if (!PopupSvc) {
        return $q.reject('res_EmergencyCallsDisclaimer');
      }

      return new $q(function (resolve, reject) {
        PopupSvc.confirm({
          title: 'res_EmergencyCallsDisclaimerTitle',
          message: 'res_EmergencyCallsDisclaimer',
          size: 'small',
          yesLabel: 'res_Accept',
          noLabel: null
        })
          .result.then(function () {
            LogSvc.debug('[PhoneCallSvc]: User has accepted the Emergency Calls Disclaimer');
            UserProfileSvc.saveSetting(Constants.UserSettingKey.EMERGENCY_CALL_DISCLAIMER, true);

            fn().then(resolve).catch(reject);
          })
          .catch(function () {
            LogSvc.debug('[PhoneCallSvc]: User has rejected the Emergency Calls Disclaimer');
            reject();
          });
      });
    }

    function pushLocalCall(callId, deviceName) {
      var call = getCall(callId);
      if (!call) {
        return $q.reject('Call not found');
      }
      var device = getDevice(deviceName);
      if (!device) {
        return $q.reject('Invalid device name');
      }
      if (device !== _defaultPushDevice) {
        setDefaultPushDevice(device, true);
      }
      LogSvc.info('[PhoneCallSvc]: Push call with ' + call.peerDn + ' to ' + device.name);
      var deferred = $q.defer();
      CallControlSvc.pushLocalCall(call.callId, device.name, handleCallControlCb(deferred));
      return deferred.promise;
    }

    function hasUnifyPhoneActiveCall() {
      var localActiveCall = CallControlSvc.getActiveCall();
      return !!(localActiveCall && localActiveCall.checkState([CallState.Active, CallState.Waiting]) && !localActiveCall.isTelephonyCall);
    }

    function multiplePhoneCalls(call) {
      if ($rootScope.ctiWebRtcCalls) {
        return (
          (call.isAtcRemote && _multipleAtcCalls) ||
          (!call.isRemote && _that.hasMultipleLocalCalls()) ||
          (call.isRemote && call.activeClient && _that.hasMultipleRemoteWebRtcCalls(call.activeClient)) ||
          ($rootScope.localUser.isOsBizCTIEnabled && call.isAtcRemote && _that.hasMultipleRemoteWebRtcCalls(call.activeClient))
        );
      }
      return (call.isAtcRemote && _multipleAtcCalls) || (!call.isRemote && _that.hasMultipleLocalCalls());
    }

    function hasAnyMultipleLocalCalls() {
      var localCalls = CallControlSvc.getPhoneCalls(true);
      var anyActiveCall = localCalls.some(function (call) {
        return call.checkState(CallState.Active);
      });
      var anyHeldCall = localCalls.some(function (call) {
        return call.isHolding();
      });
      var twoHeldCalls = localCalls.every(function (call) {
        return call.isHolding();
      });
      return localCalls.length > 1 && ((anyActiveCall && anyHeldCall) || twoHeldCalls);
    }

    function canConsultCall(call) {
      return _calls.length === 1 && (call.isConsultAllowed() || ($rootScope.localUser.isOsBizCTIEnabled && call.isAtcRemote && call.isMakeCallAllowed()));
    }

    function isCallOnHold(callId) {
      var call = getCall(callId);
      if ($rootScope.ctiWebRtcCalls) {
        return call && call.isHolding() && !multiplePhoneCalls(call);
      }

      return call && (!call.isRemote || call.isAtcRemote) && call.isHolding() && !multiplePhoneCalls(call);
    }

    function isOsbizCampOnRingingCall(call) {
      return (
        call &&
        $rootScope.localUser.isOsBizCTIEnabled &&
        (call.isOsBizSecondCall || (call.isAtcRemote && _calls.length > 1)) &&
        call.direction === CallDirection.INCOMING &&
        call.checkState(CallState.Ringing)
      );
    }

    function isOsbizCtiSecondRingingWebrtcCall(call) {
      return (
        call &&
        $rootScope.localUser.isOsBizCTIEnabled &&
        $rootScope.ctiWebRtcCalls &&
        _that.isOsBizTwoRemoteCalls() &&
        call.checkCstaState(CstaCallState.Parked)
      );
    }

    function canAnswerOsbizCall(call) {
      return !$rootScope.localUser.isOsBizCTIEnabled || call.getPosition() !== Targets.Cell || _defaultAnswerDevice === Targets.VM;
    }

    function isDeviceAvailableForAnswer(deviceName) {
      return (
        (_defaultAnswerDevice && _defaultAnswerDevice.name === deviceName) ||
        _answerDevices.some(function (d) {
          return d.name === deviceName;
        })
      );
    }

    function isOsbizRecalledCall(call) {
      return call && call.atcCallInfo && call.atcCallInfo.isRecalledCall();
    }

    function getCall(callId) {
      return _calls.find(function (c) {
        return c.callId === callId;
      });
    }

    function handleCallControlCb(deferred) {
      return function (err, warn, call) {
        err ? deferred.reject(err) : deferred.resolve(call);
      };
    }

    function publishDeviceChangedEvent() {
      var devices = {
        defaultCallDevice: _defaultCallDevice,
        callDevices: _callDevices,
        pushDevices: _pushDevices,
        answerDevices: _answerDevices,
        defaultAnswerDevice: _defaultAnswerDevice,
        defaultPushDevice: _defaultPushDevice
      };
      LogSvc.debug('[PhoneCallSvc]: Publishing /telephony/callDevices/changed event', devices);
      PubSubSvc.publish('/telephony/callDevices/changed', [devices]);
    }

    function publishPhoneCallsInitEvent() {
      LogSvc.debug('[PhoneCallSvc]: Publishing /telephony/phoneCalls/init event');
      PubSubSvc.publish('/telephony/phoneCalls/init');
    }

    function setTelephonyAvailable(newStatus) {
      newStatus = !!newStatus;

      if (newStatus !== _telephonyAvailable) {
        _telephonyAvailable = newStatus;
        LogSvc.debug('[PhoneCallSvc]: Publishing /telephony/available event: ', _telephonyAvailable);
        PubSubSvc.publish('/telephony/available', [_telephonyAvailable]);
      }

      // Now check for message waiting indicator in case voicemail available
      if ($rootScope.localUser.vmNumber) {
        $timeout.cancel(_getMessageWaitingTimer);

        // Add some time for ATC to reregister
        _getMessageWaitingTimer = $timeout(function () {
          _getMessageWaitingTimer = null;
          CallControlSvc.getMessageWaiting(function (error, res) {
            if (error) {
              LogSvc.error('[PhoneCallSvc]: getMessageWaiting', error);
              return;
            }

            var hasVoicemail = res && res.messageWaitingOn;

            $rootScope.localUser.msgWaitingIndicator = hasVoicemail;
            LogSvc.debug('[CstaSvc]: Publish /csta/msgWaitingIndicatorUpdated');
            PubSubSvc.publish('/csta/msgWaitingIndicatorUpdated', [hasVoicemail]);
          });
        }, VOICEMAIL_TIMEOUT);
      }
    }

    function canMergeCall(call) {
      return !!call && call.isConferenceCallAllowed() && multiplePhoneCalls(call);
    }

    function canSwapCall(call) {
      if ($rootScope.ctiWebRtcCalls) {
        return !!(call && multiplePhoneCalls(call) && call.isHolding() && !call.isHandoverInProgress);
      }
      return !!(call && multiplePhoneCalls(call) && (!call.isRemote || call.isAtcRemote) && call.isHolding() && !call.isHandoverInProgress);
    }

    function onSendDigitError(digit) {
      // If using CSTA to send digits, sometimes the sendDigits request fails and/or the DTMF event is not
      // generated, so we have to fail not only the last digit sent, but also the ones in the queue.
      // Tell the user the digits that failed so they can enter them again
      if (_phoneConversation.call) {
        digit = _sendDigitsQueue.join(' ');
        _sendDigitsQueue = [];
        PubSubSvc.publish('/call/sendDtmf/failed', ['res_SendDtmfFailed', digit]);
      }
    }

    function processNextDigit() {
      if (_sendDigitsQueue.length > 0) {
        var digit = _sendDigitsQueue[0];
        CallControlSvc.sendDigits(_phoneConversation.call.callId, digit, function (err) {
          if (err) {
            if (_sendDigitTimeout) {
              $timeout.cancel(_sendDigitTimeout);
              _sendDigitTimeout = null;
            }
            onSendDigitError(digit);
          }
        });
        if (_sendDigitTimeout) {
          $timeout.cancel(_sendDigitTimeout);
        }
        _sendDigitTimeout = $timeout(function () {
          _sendDigitTimeout = null;
          LogSvc.warn('[PhoneCallSvc]: Timed out entering digit: ' + digit + '. User will have to re-enter digits:', _sendDigitsQueue);
          onSendDigitError(digit);
        }, SEND_DIGIT_TIMEOUT);
      }
    }

    function isOsBizPushPullAllowed() {
      return (
        !$rootScope.localUser.isOsBizCTIEnabled || (_calls.length < 2 && $rootScope.localUser.selectedRoutingOption !== RoutingOptions.AlternativeNumber.name)
      );
    }

    function isMiVBPushPullAllowed() {
      return !$rootScope.localUser.isMiVB || !(_calls.length > 1);
    }

    function isMiV5000PullAllowed() {
      return !$rootScope.localUser.isMiV5000 || !(_atcCalls.length > 1);
    }

    function hasTwoCallsWithOneRinging() {
      return (
        _calls.length === 2 &&
        _calls.some(function (call) {
          return call.checkState(CallState.Ringing);
        })
      );
    }

    function validateCtiAction(evt) {
      var localCall = CallControlSvc.findLocalCallByCallId(evt.callId);
      if (!localCall && $rootScope.localUser.isOsBizCTIEnabled) {
        localCall = _calls.find(function (call) {
          return call.atcCallInfo.getCstaCallId() === evt.callId;
        });
        //Now set the callId of the localCall to event
        evt.callId = localCall.callId;
      }

      if (!localCall) {
        LogSvc.warn('[PhoneCallSvc]: Unexpected event. There is no local call');
        return false;
      }

      if (_clientApiHandler.clientId === evt.initClientId) {
        LogSvc.debug('[PhoneCallSvc]: Event is the same client that performed the cti action: ', _clientApiHandler.clientId);
        return false;
      }
      return true;
    }

    function handleCti(uaCsta) {
      if (!uaCsta) {
        return;
      }

      LogSvc.debug('[PhoneCallSvc]: Received uaCsta: ', uaCsta);

      var call;

      try {
        switch (uaCsta.name) {
          case 'MakeCall':
            dial(uaCsta.calledDevice);
            break;
          case 'ClearConnection':
            call = _calls.find(function (c) {
              return c.atcCallInfo.getCstaCallId() === uaCsta.connectionToBeCleared;
            });
            if (call) {
              _that.endCall(call.callId);
            }
            break;
          case 'AnswerCall':
            call = _calls.find(function (c) {
              return c.atcCallInfo.getCstaCallId() === uaCsta.callToBeAnswered;
            });
            if (call) {
              _that.answerCall(call.callId, Targets.WebRTC, false);
            }
            break;
          default:
            LogSvc.debug('[PhoneCallSvc]: Unsupported CTI: ' + uaCsta.name);
            break;
        }
      } catch (error) {
        LogSvc.error('Error while proccessing cti request', error);
      }
    }

    function findActiveClient(calls) {
      return (
        calls &&
        calls.find(function (c) {
          return c.activeClient;
        }).activeClient
      );
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // User To User Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    _userToUserHandler.on('ATC.TALK', function (data) {
      LogSvc.debug('[PhoneCallSvc]: Received UserToUser ATC.TALK');
      // Iphone app uses Apple CallKit. Apparently CallKit does not allow auto answering
      // an incoming call.
      if (Utils.isIOS()) {
        LogSvc.debug('[PhoneCallSvc]: ATC.TALK event is not supported on iOS devices');
        return;
      }

      try {
        _that
          .answerCall(data.callId, Targets.WebRTC, false)
          .then(function () {
            PubSubSvc.publish('/conversation/navigate', [_phoneConversation]);
          })
          .catch(function (err) {
            LogSvc.warn('[PhoneCallSvc]: Failed to answer call. ', err);
          });
      } catch (e) {
        LogSvc.error('[PhoneCallSvc]: Exception handling UserToUser ATC.TALK event. ', e);
      }
    });

    _userToUserHandler.on('ATC.MUTE_ON_OTHER', function (data) {
      try {
        LogSvc.debug('[PhoneCallSvc]: Received UserToUser ATC.MUTE_ON_OTHER');
        // find local/remote call (no atc remote calls)
        var activeCall = getCall(data.rtcSessionId);

        if ($rootScope.localUser && $rootScope.localUser.isOsBizCTIEnabled && _calls.length > 1) {
          // Second call in osBiz is atcRemoteCall
          var atcRemoteCall;
          if (!activeCall || activeCall.cstaCallId !== data.cstaCallId) {
            atcRemoteCall = getCall(data.cstaCallId);
          }
          if (atcRemoteCall && !atcRemoteCall.holding) {
            activeCall = atcRemoteCall;
          }
        }

        if (!activeCall) {
          LogSvc.info('[PhoneCallSvc]: ATC.MUTE_ON_OTHER found no call. Ignore it.');
          return;
        }
        $rootScope.$apply(function () {
          if (activeCall) {
            data.muted ? activeCall.mute() : activeCall.unmute();
            LogSvc.info('[PhoneCallSvc]: ATC.MUTE_ON_OTHER activeCall muted:', activeCall.isMuted());
          }
        });
      } catch (e) {
        LogSvc.error('[PhoneCallSvc]: Exception handling UserToUser ATC.MUTE_ON_OTHER event. ', e);
      }
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Client API Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    _clientApiHandler.on('CTICall.HOLD', function (evt) {
      LogSvc.debug('[PhoneCallSvc]: Received CTICall.HOLD');
      var data = {
        ctiReqId: evt.ctiReqId,
        initClientId: evt.initClientId
      };
      return new $q(function (resolve, reject) {
        if (validateCtiAction(evt)) {
          var error;
          _that
            .holdCall(evt.callId)
            .catch(function (err) {
              error = err;
            })
            .finally(function () {
              data.err = error;
              _clientApiHandler.ctiSendResponse(data);
              error ? reject(error) : resolve();
            });
          return;
        }
        data.err = 'CTI_ACTION_FAILED';
        reject(data.err);
      }).catch(function (err) {
        LogSvc.error('[PhoneCallSvc]: CTICall.HOLD event failed.', err);
        _clientApiHandler.ctiSendResponse(data);
      });
    });

    _clientApiHandler.on('CTICall.MUTE', function (evt) {
      LogSvc.debug('[PhoneCallSvc]: Received CTICall.MUTE');
      var data = {
        ctiReqId: evt.ctiReqId,
        initClientId: evt.initClientId
      };
      return new $q(function (resolve, reject) {
        if (validateCtiAction(evt)) {
          var error;
          _that
            .toggleMute(evt.callId)
            .catch(function (err) {
              error = err;
            })
            .finally(function () {
              data.err = error;
              _clientApiHandler.ctiSendResponse(data);
              error ? reject(error) : resolve();
            });
          return;
        }
        data.err = 'CTI_ACTION_FAILED';
        reject(data.err);
      }).catch(function (err) {
        LogSvc.error('[PhoneCallSvc]: CTICall.MUTE event failed.', err);
        _clientApiHandler.ctiSendResponse(data);
      });
    });

    _clientApiHandler.on('CTICall.RETRIEVE', function (evt) {
      LogSvc.debug('[PhoneCallSvc]: Received CTICall.RETRIEVE');
      var data = {
        ctiReqId: evt.ctiReqId,
        initClientId: evt.initClientId
      };
      return new $q(function (resolve, reject) {
        if (validateCtiAction(evt)) {
          var error;
          _that
            .retrieveCall(evt.callId)
            .catch(function (err) {
              error = err;
            })
            .finally(function () {
              data.err = error;
              _clientApiHandler.ctiSendResponse(data);
              error ? reject(error) : resolve();
            });
          return;
        }
        data.err = 'CTI_ACTION_FAILED';
        reject(data.err);
      }).catch(function (err) {
        LogSvc.error('[PhoneCallSvc]: CTICall.RETRIEVE event failed.', err);
        _clientApiHandler.ctiSendResponse(data);
      });
    });

    _clientApiHandler.on('CTICall.MERGE', function (evt) {
      LogSvc.debug('[PhoneCallSvc]: Received CTICall.MERGE');
      var data = {
        ctiReqId: evt.ctiReqId,
        initClientId: evt.initClientId
      };
      return new $q(function (resolve, reject) {
        if (validateCtiAction(evt)) {
          var error;
          _that
            .mergeCall(evt.callId)
            .catch(function (err) {
              error = err;
            })
            .finally(function () {
              data.err = error;
              _clientApiHandler.ctiSendResponse(data);
              error ? reject(error) : resolve();
            });
          return;
        }
        data.err = 'CTI_ACTION_FAILED';
        reject(data.err);
      }).catch(function (err) {
        LogSvc.error('[PhoneCallSvc]: CTICall.MERGE event failed.', err);
        _clientApiHandler.ctiSendResponse(data);
      });
    });

    _clientApiHandler.on('CTICall.SWAP', function (evt) {
      LogSvc.debug('[PhoneCallSvc]: Received CTICall.SWAP');
      var data = {
        ctiReqId: evt.ctiReqId,
        initClientId: evt.initClientId
      };
      return new $q(function (resolve, reject) {
        if (validateCtiAction(evt)) {
          var error;
          _that
            .swapCall(evt.callId)
            .catch(function (err) {
              error = err;
            })
            .finally(function () {
              data.err = error;
              _clientApiHandler.ctiSendResponse(data);
              error ? reject(error) : resolve();
            });
          return;
        }
        data.err = 'CTI_ACTION_FAILED';
        reject(data.err);
      }).catch(function (err) {
        LogSvc.error('[PhoneCallSvc]: CTICall.SWAP event failed.', err);
        _clientApiHandler.ctiSendResponse(data);
      });
    });

    _clientApiHandler.on('CTICall.CONSULT', function (evt) {
      LogSvc.debug('[PhoneCallSvc]: Received CTICall.CONSULT');
      var data = {
        ctiReqId: evt.ctiReqId,
        initClientId: evt.initClientId
      };
      return new $q(function (resolve, reject) {
        if (validateCtiAction(evt)) {
          var error;
          _that
            .checkDisclaimerAndDialFromDevice(evt.deviceName, evt.number)
            .catch(function (err) {
              error = err;
            })
            .finally(function () {
              data.err = error;
              _clientApiHandler.ctiSendResponse(data);
              error ? reject(error) : resolve();
            });
          return;
        }
        data.err = 'CTI_ACTION_FAILED';
        reject(data.err);
      }).catch(function (err) {
        LogSvc.error('[PhoneCallSvc]: CTICall.CONSULT event failed.', err);
        _clientApiHandler.ctiSendResponse(data);
      });
    });

    _clientApiHandler.on('CTICall.TRANSFER', function (evt) {
      LogSvc.debug('[PhoneCallSvc]: Received CTICall.TRANSFER');
      var data = {
        ctiReqId: evt.ctiReqId,
        initClientId: evt.initClientId
      };
      return new $q(function (resolve, reject) {
        if (validateCtiAction(evt)) {
          var error;
          _that
            .transferCall(evt.callId, evt.number)
            .catch(function (err) {
              error = err;
            })
            .finally(function () {
              data.err = error;
              _clientApiHandler.ctiSendResponse(data);
              error ? reject(error) : resolve();
            });
          return;
        }
        data.err = 'CTI_ACTION_FAILED';
        reject(data.err);
      }).catch(function (err) {
        LogSvc.error('[PhoneCallSvc]: CTICall.TRANSFER event failed.', err);
        _clientApiHandler.ctiSendResponse(data);
      });
    });

    _clientApiHandler.on('CTICall.DTMF', function (evt) {
      LogSvc.debug('[PhoneCallSvc]: Received CTICall.DTMF');
      var data = {
        ctiReqId: evt.ctiReqId,
        initClientId: evt.initClientId
      };
      return new $q(function (resolve, reject) {
        if (validateCtiAction(evt)) {
          var error;
          _that
            .sendDigits(evt.callId, evt.digits)
            .catch(function (err) {
              error = err;
            })
            .finally(function () {
              data.err = error;
              _clientApiHandler.ctiSendResponse(data);
              error ? reject(error) : resolve();
            });
          return;
        }
        data.err = 'CTI_ACTION_FAILED';
        reject(data.err);
      }).catch(function (err) {
        LogSvc.error('[PhoneCallSvc]: CTICall.DTMF event failed.', err);
        _clientApiHandler.ctiSendResponse(data);
      });
    });

    _clientApiHandler.on('CTICall.DIAL_ON_OTHER', function (evt) {
      LogSvc.debug('[PhoneCallSvc]: Received CTICall.DIAL_ON_OTHER');
      var data = {
        ctiReqId: evt.ctiReqId,
        initClientId: evt.initClientId
      };
      return new $q(function (resolve, reject) {
        if (_clientApiHandler.clientId !== evt.initClientId) {
          var error;
          //First need to set the default device
          _that
            .checkDisclaimerAndDialFromDevice(evt.deviceName, '', '', '')
            .catch(function (err) {
              if (err === 'Number not dialable') {
                //Now that the device is set we need to dial using default device
                _that.checkDisclaimerAndDial(evt.number, evt.name, evt.userId).catch(function (err2) {
                  error = err2;
                });
              } else {
                error = err;
              }
            })
            .finally(function () {
              data.err = error;
              _clientApiHandler.ctiSendResponse(data);
              error ? reject(error) : resolve();
            });
          return;
        }
        data.err = 'CTI_ACTION_FAILED';
        reject(data.err);
      }).catch(function (err) {
        LogSvc.error('[PhoneCallSvc]: CTICall.DIAL_ON_OTHER event failed.', err);
        _clientApiHandler.ctiSendResponse(data);
      });
    });

    _clientApiHandler.on('CTICall.ANSWER_ON_OTHER', function (evt) {
      LogSvc.debug('[PhoneCallSvc]: Received CTICall.ANSWER_ON_OTHER');
      var data = {
        ctiReqId: evt.ctiReqId,
        initClientId: evt.initClientId
      };
      return new $q(function (resolve, reject) {
        if (_clientApiHandler.clientId !== evt.initClientId) {
          var error;
          _that
            .answerCall(evt.callId)
            .catch(function (err) {
              error = err;
            })
            .finally(function () {
              data.err = error;
              _clientApiHandler.ctiSendResponse(data);
              error ? reject(error) : resolve();
            });
          return;
        }
        data.err = 'CTI_ACTION_FAILED';
        reject(data.err);
      }).catch(function (err) {
        LogSvc.error('[PhoneCallSvc]: CTICall.ANSWER_ON_OTHER event failed.', err);
        _clientApiHandler.ctiSendResponse(data);
      });
    });

    _clientApiHandler.on('CTICall.HANGUP', function (evt) {
      LogSvc.debug('[PhoneCallSvc]: Received CTICall.HANGUP');
      var data = {
        ctiReqId: evt.ctiReqId,
        initClientId: evt.initClientId
      };
      return new $q(function (resolve, reject) {
        if (validateCtiAction(evt)) {
          var error;
          _that
            .endCall(evt.callId)
            .catch(function (err) {
              error = err;
            })
            .finally(function () {
              data.err = error;
              _clientApiHandler.ctiSendResponse(data);
              error ? reject(error) : resolve();
            });
          return;
        }
        data.err = 'CTI_ACTION_FAILED';
        reject(data.err);
      }).catch(function (err) {
        LogSvc.error('[PhoneCallSvc]: CTICall.HANGUP event failed.', err);
        _clientApiHandler.ctiSendResponse(data);
      });
    });

    _clientApiHandler.on('CTICall.PUSH_WEBRTC_CALL', function (evt) {
      LogSvc.debug('[PhoneCallSvc]: Received CTICall.PUSH_WEBRTC_CALL');
      var data = {
        ctiReqId: evt.ctiReqId,
        initClientId: evt.initClientId
      };
      return new $q(function (resolve, reject) {
        if (validateCtiAction(evt)) {
          var error;
          _that
            .pushLocalCall(evt.callId, evt.deviceName)
            .catch(function (err) {
              error = err;
            })
            .finally(function () {
              data.err = error;
              _clientApiHandler.ctiSendResponse(data);
              error ? reject(error) : resolve();
            });
          return;
        }
        data.err = 'CTI_ACTION_FAILED';
        reject(data.err);
      }).catch(function (err) {
        LogSvc.error('[PhoneCallSvc]: CTICall.PUSH_WEBRTC_CALL event failed.', err);
        _clientApiHandler.ctiSendResponse(data);
      });
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/conversations/showTelephonyConversation', function (show, conv) {
      LogSvc.debug('[PhoneCallSvc]: Received /conversations/showTelephonyConversation event. show = ', show);
      if (show) {
        _phoneConversation = conv;
        var data = AtcRegistrationSvc.getTelephonyData();
        setTelephonyAvailable(data.phoneCallsAvailable);
        initDevices();
        initPhoneCalls();
      }
    });

    PubSubSvc.subscribe('/csta/deviceChange', function () {
      LogSvc.debug('[PhoneCallSvc]: Received /csta/deviceChange');
      initDevices();
    });

    PubSubSvc.subscribe('/call/ended', function (call) {
      if (call && _phoneConversation && call.convId === _phoneConversation.convId && !call.isHandoverInProgress) {
        LogSvc.debug('[PhoneCallSvc]: Received /call/ended event for telephony call');
        initPhoneCalls();
      }
    });

    PubSubSvc.subscribe('/conversation/update', function (c) {
      if (_phoneConversation && _phoneConversation.convId === c.convId) {
        LogSvc.debug('[PhoneCallSvc]: Received /conversation/update event');
        _phoneConversation = c;
        initPhoneCalls();
      }
    });

    PubSubSvc.subscribe('/telephony/data', function (data) {
      LogSvc.debug('[PhoneCallSvc]: Received /telephony/data event');
      setTelephonyAvailable(data.phoneCallsAvailable);
    });

    PubSubSvc.subscribe('/call/singleDigitSent', function (call, digit) {
      LogSvc.debug('[PhoneCallSvc]: Received /call/singleDigitSent event');
      var allDigitsSent = false;
      var digits = _sendDigitsQueue[0];
      digit = digit || digits; // If no digits are provided in the event, assume it's the last we sent
      if (digits === digit) {
        allDigitsSent = true;
      } else if (digits[0] === digit) {
        // We're processing one digit at a time
        digits = digits.slice(1);
        if (!digits) {
          allDigitsSent = true;
        } else {
          // This entry from the queue still has digits to be sent
          _sendDigitsQueue[0] = digits;
        }
      } else {
        LogSvc.error('[PhoneCallSvc]: Received unexpected digit(s)');
      }

      if (allDigitsSent) {
        // All digits have been sent, remove this entry from the queue
        _sendDigitsQueue.splice(0, 1);
        if (_sendDigitTimeout) {
          $timeout.cancel(_sendDigitTimeout);
          _sendDigitTimeout = null;
        }
        processNextDigit();
      }
    });

    PubSubSvc.subscribe('/cti/cmd', function (uaCsta) {
      LogSvc.debug('[PhoneCallSvc]: Received /cti/cmd event');
      handleCti(uaCsta);
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    /**
     * Returns object with all devices configured and available for telephony calls.
     *
     * @returns {Object} Keys:
     * defaultCallDevice {Enums.Targets}: Default device when making calls.
     * callDevices {Enums.Targets[]}: Array with devices that can be used for making calls.
     * pushDevices {Enums.Targets[]}: Array with devices where calls can be pushed to.
     * answerDevices {Enums.Targets[]}: Array with devices that can answer calls.
     * defaultAnswerDevice {Enums.Targets}: Default device when answering calls.
     * defaultPushDevice {Enums.Targets}: Default device when pushing calls.
     */
    this.getDevices = function () {
      return {
        defaultCallDevice: _defaultCallDevice,
        callDevices: _callDevices,
        pushDevices: _pushDevices,
        answerDevices: _answerDevices,
        defaultAnswerDevice: _defaultAnswerDevice,
        defaultPushDevice: _defaultPushDevice
      };
    };

    /**
     * Returns an array of call objects with all current phone calls (local and remote).
     * @returns {Array}
     */
    this.getPhoneCalls = function () {
      return _calls;
    };

    /**
     * Returns the Telephony conversation.
     * @returns {Object}
     */
    this.getPhoneConversation = function () {
      return _phoneConversation;
    };

    /**
     * Returns the local user private and public phone numbers. In some deployments, the user can have both or either one.
     * @returns {Object} Keys:
     * public: public phone number.
     * private: private phone number (UCaaS deployments).
     */
    this.getMyPhoneNumbers = function () {
      var osmoData = $rootScope.localUser.isOsBizCTIEnabled && AtcRegistrationSvc.getAtcRegistrationData();
      return {
        public: (osmoData && osmoData.onsFQN) || $rootScope.localUser.phoneNumber
      };
    };

    /**
     * Dials the specified phone number through the last device used to dial. You may use {@link canInitiatePhoneCall} to check if dialing is allowed.
     * @param {String} number Number to be dialed.
     * @param {String} [name] Name of the user being dialed.
     * @param {String} [userId] ID of the user being dialed.
     * @returns {Promise} Fulfillment handler receives the created local call object only if the device is 'webRTC', otherwise no call object is returned.
     * Rejection handler receives an error object.
     */
    this.dial = dial;

    /**
     * Checks whether user has accepted Emergency call disclaimer and dials the specified phone number through the last device used to dial.
     * @param {String} number Number to be dialed.
     * @param {String} [name] Name of the user being dialed.
     * @param {String} [userId] ID of the user being dialed.
     * @returns {Promise} Fulfillment handler receives the created local call object only if the device is 'webRTC', otherwise no call object is returned.
     * Rejection handler receives an error object.
     */
    this.checkDisclaimerAndDial = function (number, name, userId) {
      var fn = dial.bind(this, number, name, userId);
      return checkDisclaimer(fn);
    };

    /**
     * Dials the a phone number using the specified device. You may use {@link canDialFromDevice} to check if dialing is allowed.
     * @param {String} deviceName Name of the device (see {enum.Target}) to be dialed from.
     * @param {String} number Number to be dialed.
     * @param {String} [name] Name of the user being dialed.
     * @param {String} [userId] ID of the user being dialed.
     * @returns {Promise} Fulfillment handler receives the created local call object only if the device is 'webRTC', otherwise no call object is returned.
     * Rejection handler receives an error object.
     */
    this.dialFromDevice = dialFromDevice;

    /**
     * Checks whether user has accepted Emergency call disclaimer and dials the a phone number using the specified device.
     * @param {String} deviceName Name of the device (see {enum.Target}) to be dialed from.
     * @param {String} number Number to be dialed.
     * @param {String} [name] Name of the user being dialed.
     * @param {String} [userId] ID of the user being dialed.
     * @param {Object} [sipHeaders] Location data to be included with the call.
     * @returns {Promise} Fulfillment handler receives the created local call object only if the device is 'webRTC', otherwise no call object is returned.
     * Rejection handler receives an error object.
     */
    this.checkDisclaimerAndDialFromDevice = function (deviceName, number, name, userId, sipHeaders) {
      var fn = dialFromDevice.bind(this, deviceName, number, name, userId, sipHeaders);
      return checkDisclaimer(fn);
    };

    this.checkDisclaimerAndDialFromDeviceV2 = function (data) {
      this.checkDisclaimerAndDialFromDevice(data.deviceName, data.number, data.name, data.userId, data.sipHeaders);
    };

    /**
     * Pushes a local call to the specified device. You may use {@link canPushLocalCall} to check if push operation is allowed.
     * @param {String} callId ID of the local call to be pushed.
     * @param {String} deviceName Name of the device (see {enum.Target}) where the call will be pushed to.
     * @returns {Promise} Fulfillment handler receives the created remote call object. Rejection handler receives an error object.
     */
    this.pushLocalCall = pushLocalCall;

    /**
     * Alternates between two calls (1 held and 1 active). These calls can be both local or remote. You may use {@link canSwapCall} to check if swap operation is allowed.
     * @param {String} [callId] ID of the held call to be swapped. If not supplied, we'll try to find the held call.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.swapCall = function (callId) {
      var deferred = $q.defer();
      if (!callId) {
        if (_calls.length > 1) {
          var heldCall = _calls.find(function (call) {
            // Return first held call that we find
            return call.isHolding();
          });
          if (heldCall) {
            callId = heldCall.callId;
          } else if (!$rootScope.localUser.isOsBizCTIEnabled) {
            return $q.reject('No calls to swap');
          }
        } else {
          return $q.reject('No calls to swap');
        }
      }
      CallControlSvc.swapCall(callId, function (err) {
        err ? deferred.reject(err) : deferred.resolve();
      });
      return deferred.promise;
    };

    /**
     * Conferences in (merges) two calls (local or remote). You may use {@link canMergeCall} to check if merge calls is allowed.
     * @param {String} [callId] ID of the active call to be merged. If not supplied, we'll try to find it.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.mergeCall = function (callId) {
      var deferred = $q.defer();
      if (!callId) {
        var remoteCall = _atcCalls.find(function (call) {
          return call.checkCstaState([CstaCallState.Active, CstaCallState.Conference]);
        });
        if (remoteCall) {
          callId = remoteCall.callId;
        } else {
          var localCall = _calls.find(function (call) {
            return call.isSTC ? call.checkState(CallState.Active) : call.checkCstaState([CstaCallState.Active, CstaCallState.Conference]);
          });
          if (localCall) {
            callId = localCall.callId;
          } else {
            return $q.reject('No call to be merged found');
          }
        }
      }
      CallControlSvc.mergeCall(callId, function (err) {
        err ? deferred.reject(err) : deferred.resolve();
      });
      return deferred.promise;
    };

    /**
     * Transfers call (local or remote). There are 2 types of transfer: attended and unattended. Attended transfer happens when you first initiate
     * a consultation call then invoke the transfer. Unattended is when no consultation call is made. You may use {@link canTransferCall}
     * to check if transfer is allowed for a particular call.
     * @param {String} callId ID of the active call (in attended transfers, it's the consultation call).
     * @param {String} [number] Phone number of transfer destination. It's required only for unattended transfers.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.transferCall = function (callId, number) {
      return new $q(function (resolve, reject) {
        CallControlSvc.transferCall(callId, number, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    /**
     * Holds a call (local or remote). You may use {@link canHoldCall} to check if hold is allowed.
     * @param {String} callId ID of the call to be held.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.holdCall = function (callId) {
      return new $q(function (resolve, reject) {
        CallControlSvc.holdCall(callId, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    /**
     * Retrieves a held call (local or remote). You may use {@link canRetrieveCall} to check if retrieve is allowed.
     * @param {String} callId ID of the held call to be retrieved.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.retrieveCall = function (callId) {
      return new $q(function (resolve, reject) {
        CallControlSvc.retrieveCall(callId, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    /**
     * Ignore a pickup call notification. Use this method to dismiss a call pickup notification issue for the call pickup group.
     * @param {String} callId The call ID of the call from the pickup notification.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.ignoreCall = function (callId) {
      return new $q(function (resolve, reject) {
        CallControlSvc.ignoreCall(callId, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    /**
     * Toggle between mute and unmute.
     * @param {String} callId ID of the call to toggle mute.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.toggleMute = function (callId) {
      return new $q(function (resolve, reject) {
        CallControlSvc.toggleMute(callId, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    /**
     * CTI Holds a call (WebRtc remote). You may use {@link canHoldCall} to check if hold is allowed.
     * @param {object} call The call to be held.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.ctiHoldCall = function (call) {
      var activeClient;

      if (call.activeClient) {
        activeClient = call.activeClient;
      } else {
        activeClient = findActiveClient(_calls);
      }

      var data = {
        call: call,
        activeClient: activeClient
      };
      return new $q(function (resolve, reject) {
        _clientApiHandler.ctiHoldCall(data, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    /**
     * CTI Retrieves a held call (WebRtc remote). You may use {@link canRetrieveCall} to check if retrieve is allowed.
     * @param {object} call The call to be retrieved.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.ctiRetrieveCall = function (call) {
      var activeClient;

      if (call.activeClient) {
        activeClient = call.activeClient;
      } else {
        activeClient = findActiveClient(_calls);
      }

      var data = {
        call: call,
        activeClient: activeClient
      };
      return new $q(function (resolve, reject) {
        _clientApiHandler.ctiRetrieveCall(data, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    /**
     * CTI Merges a call (WebRtc remote). You may use {@link canMergeCall} to check if merge is allowed.
     * @param {object} call The call to be merged.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.ctiMergeCall = function (call) {
      var activeClient;

      if (call.activeClient) {
        activeClient = call.activeClient;
      } else {
        activeClient = findActiveClient(_calls);
      }

      var data = {
        call: call,
        activeClient: activeClient
      };
      return new $q(function (resolve, reject) {
        _clientApiHandler.ctiMergeCall(data, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    /**
     * CTI Mutes a call (WebRtc remote). You may use {@link canMuteCall} to check if mute is allowed.
     * @param {object} call The call to be merged.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.ctiMuteCall = function (call) {
      var activeClient;

      if (call.activeClient) {
        activeClient = call.activeClient;
      } else {
        activeClient = findActiveClient(_calls);
      }

      var data = {
        call: call,
        activeClient: activeClient
      };
      return new $q(function (resolve, reject) {
        _clientApiHandler.ctiMuteCall(data, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    /**
     * CTI Alternate between two calls (1 held and 1 active WebRtc remote). You may use {@link canSwapCall} to check if swap operation is allowed.
     * @param {object} call The call that is active on remote webclient.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.ctiSwapCall = function (call) {
      var activeClient;

      if (call.activeClient) {
        activeClient = call.activeClient;
      } else {
        activeClient = findActiveClient(_calls);
      }

      var data = {
        call: call,
        activeClient: activeClient
      };
      return new $q(function (resolve, reject) {
        _clientApiHandler.ctiSwapCall(data, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    /**
     * CTI Consult (WebRtc remote). You may use {@link canInitiateConsultationLocalCall} to check if consultation.
     * @param {object} call The call that is active on remote webclient.
     * @param {String} deviceName Name of the device (WebRTC, Cell, Desk or VM).
     * @param {String} number Number to be dialed.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.ctiConsultCall = function (call, deviceName, number) {
      var activeClient;

      if (call.activeClient) {
        activeClient = call.activeClient;
      } else {
        activeClient = findActiveClient(_calls);
      }

      var data = {
        call: call,
        activeClient: activeClient,
        deviceName: deviceName,
        number: number
      };
      return new $q(function (resolve, reject) {
        _clientApiHandler.ctiConsultCall(data, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    /**
     * CTI Transfers call (WebRtc remote). There are 2 types of transfer: attended and unattended. Attended transfer happens when you first initiate
     * a consultation call then invoke the transfer. Unattended is when no consultation call is made. You may use {@link canTransferCall}
     * to check if transfer is allowed for a particular call.
     * @param {object} call The call that is active on remote webclient.
     * @param {String} [number] Phone number of transfer destination. It's required only for unattended transfers.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.ctiTransferCall = function (call, number) {
      var activeClient;

      if (call.activeClient) {
        activeClient = call.activeClient;
      } else {
        activeClient = findActiveClient(_calls);
      }

      var data = {
        call: call,
        activeClient: activeClient,
        number: number
      };

      return new $q(function (resolve, reject) {
        _clientApiHandler.ctiTransferCall(data, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    this.ctiDtmfCall = function (call, digits) {
      var activeClient;

      if (call.activeClient) {
        activeClient = call.activeClient;
      } else {
        activeClient = findActiveClient(_calls);
      }

      var data = {
        call: call,
        activeClient: activeClient,
        digits: digits
      };

      return new $q(function (resolve, reject) {
        _clientApiHandler.ctiDtmfCall(data, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    this.ctiDial = function (deviceName, number, name, userId) {
      return new $q(function (resolve, reject) {
        _clientApiHandler.ctiDial(deviceName, number, name, userId, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    this.ctiAnswerCall = function (callId, deviceName) {
      return new $q(function (resolve, reject) {
        _clientApiHandler.ctiAnswerCall(callId, deviceName, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    this.ctiHangupCall = function (call) {
      var activeClient;

      if (call.activeClient) {
        activeClient = call.activeClient;
      } else {
        activeClient = findActiveClient(_calls);
      }

      var data = {
        call: call,
        activeClient: activeClient
      };

      return new $q(function (resolve, reject) {
        _clientApiHandler.ctiHangupCall(data, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    this.ctiPushCall = function (call, deviceName) {
      return new $q(function (resolve, reject) {
        var data = {
          call: call,
          deviceName: deviceName
        };
        _clientApiHandler.ctiPushCall(data, function (err) {
          err ? reject(err) : resolve();
        });
      });
    };

    /**
     * Returns if an operation is in progress on any call or not (local or remote). Use this method to display a progress indicator (e.g.: spinner).
     * @returns {Boolean} true if there's an operation is progress, false otherwise.
     */
    this.hasAnyCallInTransientState = function () {
      var localCalls = CallControlSvc.getPhoneCalls(true);
      var hasLocal = localCalls.some(function (call) {
        return call.isHoldInProgress() || call.isRetrieveInProgress() || call.isHandoverInProgress;
      });
      if (!hasLocal) {
        var remoteCalls = CallControlSvc.getAtcCalls();
        return remoteCalls.some(function (call) {
          return call.isHoldInProgress() || call.isRetrieveInProgress() || call.isHandoverInProgress;
        });
      } else {
        return true;
      }
    };

    /**
     * Returns if there are more than 1 local call or not. Currently UnifyPhone only supports at most 2 local phone calls.
     * @param {String} includeDelivered Include outgoing calls in Delivered state.
     * @returns {Boolean} true if there are more than 1 local call, false otherwise.
     */
    this.hasMultipleLocalCalls = function (includeDelivered) {
      var localCalls = CallControlSvc.getPhoneCalls(true);
      if (localCalls.length < 2) {
        return false;
      }
      var cstaCallStates = [CstaCallState.Active, CstaCallState.Conference];
      var callStates = $rootScope.localUser.isSTC ? [CallState.Active] : [];
      if (includeDelivered) {
        cstaCallStates.push(CstaCallState.Delivered);
        callStates.push(CallState.Delivered);
      }
      var anyActiveCall = localCalls.some(function (call) {
        return call.checkCstaState(cstaCallStates) || call.checkState(callStates);
      });
      var anyHeldCall = localCalls.some(function (call) {
        return call.isHolding();
      });
      return anyActiveCall && anyHeldCall;
    };

    /**
     * Returns if there are more than 1 remote WebRtc call or not. Currently UnifyPhone only supports at most 2 local phone calls.
     * @param {String} activeClient The client that has the active WebRtc call.
     * @param {String} includeDelivered Include outgoing calls in Delivered state.
     * @returns {Boolean} true if there are more than 1 remote WebRtc call, false otherwise.
     */
    this.hasMultipleRemoteWebRtcCalls = function (activeClient, includeDelivered) {
      if (!$rootScope.localUser.isOsBizCTIEnabled && !activeClient) {
        return false;
      }

      if (_calls.length < 2) {
        return false;
      }

      var cstaCallStates = [CstaCallState.Active, CstaCallState.Conference];
      var callStates = $rootScope.localUser.isSTC ? [CallState.Active] : [];
      if (includeDelivered) {
        cstaCallStates.push(CstaCallState.Delivered);
        callStates.push(CallState.Delivered);
      }

      var anyActiveCall = _calls.some(function (call) {
        return call.checkCstaState(cstaCallStates) || call.checkState(callStates);
      });
      var anyHeldCall = _calls.some(function (call) {
        return call.isHolding();
      });

      var remoteCalls;
      if ($rootScope.localUser.isOsBizCTIEnabled) {
        var remoteWebRTCCall = _calls.some(function (call) {
          return call.activeClient;
        });

        remoteWebRTCCall && (remoteCalls = _calls);
      } else {
        remoteCalls = _calls
          .filter(function (call) {
            return call.activeClient && call.activeClient.clientId === activeClient.clientId;
          })
          .map(function (call) {
            return call;
          });
      }

      if (remoteCalls && remoteCalls.length < 2) {
        return false;
      }

      return anyActiveCall && anyHeldCall;
    };

    this.isOsBizTwoRemoteCalls = function () {
      return (
        $rootScope.localUser.isOsBizCTIEnabled &&
        _calls.length > 1 &&
        _calls.some(function (call) {
          return call.isRemote && !call.isAtcRemote;
        })
      );
    };

    /**
     * Returns if merge call feature is available on the system. If false, merge is not possible for any call.
     * @returns {Boolean} true if merge is available, false otherwise. If true, make sure to also check {@link canMergeCall}.
     */
    this.isMergeCallAvailable = function () {
      return !!(
        $rootScope.localUser.isATC ||
        ($rootScope.localUser.stcCapabilities && $rootScope.localUser.stcCapabilities.includes(Constants.StcCapabilities.STC_MERGE))
      );
    };

    /**
     * Returns if consultation call feature is available on the system. If false, consultation is not possible for any call.
     * @returns {Boolean} true if consultation is available, false otherwise. If true, make sure to also check {@link canInitiateConsultationLocalCall}.
     * and {@link canInitiateConsultationLocalCall}
     */
    this.isConsultationAvailable = function () {
      return !!$rootScope.localUser.isRegisterTC;
    };

    /**
     * Returns the list of the available features on the system that depends on the telephony connector of the user
     * @returns {Array}
     */
    this.getAvailableFeatureList = function () {
      var featureList = [];
      if ($rootScope.localUser.isRegisterTC) {
        featureList.push(Constants.TcAvailableFeatures.HOLD, Constants.TcAvailableFeatures.CONSULTATION);
      }
      if ($rootScope.localUser.isATC) {
        featureList.push(Constants.TcAvailableFeatures.TRANSFER, Constants.TcAvailableFeatures.MERGE);
      } else if ($rootScope.localUser.stcCapabilities) {
        if ($rootScope.localUser.stcCapabilities.includes(Constants.StcCapabilities.STC_TRANSFER)) {
          featureList.push(Constants.TcAvailableFeatures.TRANSFER);
        }
        if ($rootScope.localUser.stcCapabilities.includes(Constants.StcCapabilities.STC_MERGE)) {
          featureList.push(Constants.TcAvailableFeatures.MERGE);
        }
      }
      return featureList;
    };

    /**
     * Returns if hangup (end) call feature is available for the specified call. In some switches, ending a remote call is not available.
     * @returns {Boolean} true if hangup is available, false otherwise. If true, make sure to also check {@link canHangupCall}.
     */
    this.isHangupCallAvailable = function (callId) {
      var call = getCall(callId);
      if ($rootScope.ctiWebRtcCalls) {
        return !!(call && !call.pickupNotification);
      }
      return !!(call && (!call.isRemote || (call.isAtcRemote && !_that.isOsBizTwoRemoteCalls())) && !call.pickupNotification);
    };

    /**
     * Checks if the specified outgoing call failed because the destination was busy or not. Use this method to display a busy indication.
     * This call will be automatically terminated after a few seconds.
     * @param {String} callId ID of the call to be checked.
     * @returns {Boolean} true if busy, false otherwise.
     */
    this.isCallBusy = function (callId) {
      var call = getCall(callId);
      return !!(call && (call.checkState(CallState.Busy) || call.isOutgoingCallCampedOn));
    };

    /**
     * Returns if local call is active or not. Use this method to check if consulation/transfer and merge calls (when applicable) should be offered to the user.
     * @param {String} [callId] ID of the call to be checked.
     * @returns {Boolean} true if active, false otherwise
     */
    this.isLocalCallActive = function (callId) {
      return _calls.some(function (call) {
        return !!(
          (!callId || callId === call.callId) &&
          !call.isRemote &&
          !call.checkState([CallState.Ringing, CallState.Waiting]) &&
          !call.isHandoverInProgress
        );
      });
    };

    /**
     * Returns if remote call is active or not. Use this method to check if consultation/transfer and merge calls (when applicable) should be offered to the user.
     * @param {String} [callId] ID of the call to be checked.
     * @returns {Boolean} true if active, false otherwise.
     */
    this.isRemoteCallActive = function (callId) {
      // In case of multiple OsBiz calls when call is webrtc then one call is RemoteCall and the rest are AtcRemoteCall.
      if ($rootScope.localUser && $rootScope.localUser.isOsBizCTIEnabled && _that.isOsBizTwoRemoteCalls()) {
        return false;
      }
      return _atcCalls.some(function (call) {
        return !!(
          (!callId || callId === call.callId) &&
          call.isAtcRemote &&
          call.checkState([CallState.ActiveRemote, CallState.Initiated, CallState.Delivered]) &&
          !call.isHandoverInProgress
        );
      });
    };

    /**
     * Returns if Telephony calls can be made and received. Network connection and/or Telephony Connector status affect
     * the Telephony availability on the client. Use this method to enable/disable call operations in general.
     * @returns {Boolean} true if available, false otherwise.
     */
    this.isTelephonyAvailable = function () {
      return _telephonyAvailable;
    };

    /**
     * Returns if there are more than 1 remote call in progress or not.
     * @returns {Boolean} true if in progress, false otherwise.
     */
    this.hasMultipleRemoteCalls = function () {
      return _atcCalls.length > 1;
    };

    /**
     * Returns if merge calls is possible or not. Use this method to show/hide the merge calls option.
     * @param {String} [callId] ID of the active call to be merged. If not supplied, we'll try to find it.
     * @returns {Boolean} true if possible, false otherwise (or the active call couldn't be found).
     */
    this.canMergeCall = function (callId) {
      if (callId) {
        var call = getCall(callId);
        return canMergeCall(call);
      }
      // Check all calls
      return _calls.some(function (c) {
        return canMergeCall(c);
      });
    };

    /**
     * Returns if the consultation call can be transferred or not. Use this method to show/hide the transfer call option.
     * @param {String} callId ID of the consultation call (local or remote).
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canConsultTransfer = function (callId) {
      var call = getCall(callId);
      return !!(
        !!call &&
        _calls.length === 2 &&
        (!call.isHolding() || call.isOsBizFirstCall || call.isOsBizSecondCall) &&
        (call.isTransferCallAllowed() || call.checkState(CallState.Delivered))
      );
    };

    /**
     * Returns if an unattended transfer call is possible or not. Use this method to show/hide the unattended option.
     * @param {String} callId ID of the active call (local or remote).
     * @returns {Boolean} true if possible, false otherwise
     */
    this.canTransferCall = function (callId) {
      var call = getCall(callId);
      return (
        !!call &&
        (_calls.length === 1 || ($rootScope.localUser.isOsBizCTIEnabled && hasTwoCallsWithOneRinging())) &&
        !call.isHolding() &&
        call.isTransferAllowed()
      );
    };

    /**
     * Returns if an incoming ringing call can be picked up or not. Call pickup is a feature where members of a team (call pickup group)
     * can pickup a ringing call from any member of the team. Note that if call pickup is possible, {@link canAnswerCall} returns false (except for the
     * team member that actually received the incoming call).
     * @param {String} callId ID of the ringing call.
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canPickupCall = function (callId) {
      var call = getCall(callId);
      return !!(call && call.pickupNotification && !call.isHandoverInProgress);
    };

    /**
     * Returns if local call can be pushed to another device or not. Use this method to show/hide the push call option.
     * @param {String} callId ID of the local call.
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canPushLocalCall = function (callId) {
      var call = getCall(callId);
      if ($rootScope.ctiWebRtcCalls) {
        return !!(
          call &&
          $rootScope.localUser.isATC &&
          _defaultPushDevice &&
          !call.isAtcRemote &&
          ((call.isRemote && call.activeClient) || !call.isRemote) &&
          call.isHandoverAllowed &&
          isOsBizPushPullAllowed() &&
          isMiVBPushPullAllowed()
        );
      }
      return !!(
        call &&
        $rootScope.localUser.isATC &&
        _defaultPushDevice &&
        !call.isAtcRemote &&
        !call.isRemote &&
        call.isHandoverAllowed &&
        isOsBizPushPullAllowed() &&
        isMiVBPushPullAllowed()
      );
    };

    /**
     * Returns if it's possible to dial from the specified device or not. Use this method to show which devices can be used to dial.
     * @param {String} deviceName Name of the device (WebRTC, Cell, Desk or VM).
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canDialFromDevice = function (deviceName) {
      return !!(!!deviceName && (deviceName !== Targets.Cell.name || canDialfromAlternativeNumber()) && deviceName !== Targets.VM.name);
    };

    /**
     * Returns if it's possible to hold a call or not. The call can be local or remote.
     * Use this method to show/hide the hold call option. Note that you should not invoke {@link holdCall} if
     * {@link hasAnyCallInTransientState} returns true.
     * @param {String} callId ID of the call (local or remote).
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canHoldCall = function (callId) {
      var call = getCall(callId);
      if (!call) {
        return false;
      }
      if ($rootScope.localUser.isOsBizCTIEnabled) {
        //OSBiz consultCall is now allowed when call is holding with SBIZ-63825
        if ($rootScope.ctiWebRtcCalls) {
          return !!(_calls.length < 2 && call.isConsultAllowed() && !call.isHolding());
        }
        return !!(_calls.length < 2 && call.isConsultAllowed() && (!call.isRemote || call.isAtcRemote) && !call.isHolding());
      }
      if ($rootScope.localUser.isSTC) {
        if ($rootScope.ctiWebRtcCalls) {
          return call.checkState([CallState.Active, CallState.ActiveRemote, CallState.Held]);
        }
        return !call.isRemote && call.checkState([CallState.Active, CallState.Held]);
      }
      if ($rootScope.ctiWebRtcCalls) {
        return !!($rootScope.localUser.isATC && call.isHoldAllowed());
      }
      return !!($rootScope.localUser.isATC && (!call.isRemote || call.isAtcRemote) && call.isHoldAllowed());
    };

    /**
     * Returns if it's possible to hangup a call or not. The call can be local or remote.
     * Use this method to show/hide the end call option. Note that you should not invoke {@link endCall} if
     * {@link hasAnyCallInTransientState} returns true.
     * @param {String} callId ID of the call to be terminated (local or remote).
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canHangupCall = function (callId) {
      if (_that.isHangupCallAvailable(callId)) {
        var call = getCall(callId);
        // Don't allow hangup if we're in the middle of an operation...
        // ...or if OsBiz doesn't allow it
        // ...or if Os4K doesn't allow it
        return !!(
          call &&
          !_that.hasAnyCallInTransientState() &&
          ((!$rootScope.localUser.isOsBizCTIEnabled && !$rootScope.localUser.isOS4K && !$rootScope.localUser.isMiVB) ||
            ($rootScope.localUser.isOsBizCTIEnabled &&
              !_that.canSwapCall(call.callId) &&
              !_that.canRetrieveCall(call.callId) &&
              !call.isHolding() &&
              !(isOsbizCampOnRingingCall(call) || isOsbizCtiSecondRingingWebrtcCall(call)) &&
              !isOsbizRecalledCall(call)) ||
            ($rootScope.localUser.isOS4K && !(call.isHolding() && (call.getPosition() === Targets.Desk || call.getPosition() === Targets.Cell))) ||
            ($rootScope.localUser.isMiVB &&
              (call.isHangupAllowed() || (!_that.canSwapCall(call.callId) && !_that.canRetrieveCall(call.callId) && !call.isHolding()))))
        );
      }
      return false;
    };

    /**
     * Returns if it's possible to initiate a telephony call or not. Use this method to show/hide the initiate call option to the user.
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canInitiatePhoneCall = function () {
      return !!(_telephonyAvailable && ($rootScope.webRtcEnabled || ($rootScope.localUser.isATC && _callDevices.length > 0)));
    };

    /**
     * Returns the number of devices that can be used to initiate a telephony call.
     * @returns {Number} Number of available devices (0 to 3 devices).
     */
    this.getNumOfAvailableCallDevices = function () {
      if ($rootScope.localUser.isATC && _phoneConversation && _phoneConversation.call && _phoneConversation.call.consultation && _callDevices.length > 0) {
        return 1;
      }
      return _callDevices.length;
    };

    /**
     * Returns if a remote consultation call can be made or not. Use this method to show/hide the option of initiating a remote consultation call.
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canInitiateConsultationRemoteCall = function () {
      return !!(
        _phoneConversation &&
        _that.isConsultationAvailable() &&
        _atcCalls.length === 1 &&
        _phoneConversation.call &&
        !_phoneConversation.call.consultation &&
        canConsultCall(_phoneConversation.call)
      );
    };

    /**
     * Returns if a local consultation call can be made or not. Use this method to show/hide the option of initiating a local consultation call.
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canInitiateConsultationLocalCall = function () {
      return !!(
        _phoneConversation &&
        _that.isConsultationAvailable() &&
        _calls.length === 1 &&
        _phoneConversation.call &&
        !_phoneConversation.call.consultation &&
        _phoneConversation.call.state.established &&
        canConsultCall(_phoneConversation.call)
      );
    };

    /**
     * Returns if a call can be answered or not. Use this method to show/hide the option of answering an incoming call.
     * @param {String} callId ID of the call to be answered (local or remote).
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canAnswerCall = function (callId) {
      var call = getCall(callId);
      if (!call) {
        return false;
      }

      if ($rootScope.ctiWebRtcCalls) {
        if (_that.isOsBizTwoRemoteCalls()) {
          if (call.checkCstaState(CstaCallState.Parked)) {
            return !call.isHandoverInProgress && canAnswerOsbizCall(call);
          }
        }
      }

      if (call.checkState(CallState.Ringing)) {
        if ($rootScope.localUser.isATC) {
          return !call.isHandoverInProgress && canAnswerOsbizCall(call);
        } else {
          return !_that.hasMultipleLocalCalls();
        }
      }
      return false;
    };

    /**
     * Returns if a call can be answered on the specified device or not. Use this method to show which devices can be used to answer the call.
     * @param {String} deviceName Name of the device (WebRTC, Cell, Desk or VM).
     * @param {String} callId ID of the call to be answered (local or remote).
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canAnswerOnDevice = function (deviceName, callId) {
      var call = getCall(callId);
      return !!(call && ((deviceName && isDeviceAvailableForAnswer(deviceName)) || (!deviceName && call.getPosition() !== Targets.Cell)));
    };

    /**
     * Returns if a call can be answered locally.
     * @param {String} callId ID of the call to be answered (local or remote).
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canAnswerLocally = function (callId) {
      return _that.canAnswerOnDevice(Targets.WebRTC.name, callId);
    };

    /**
     * Returns if a remote call be pulled by this UnifyPhone client or not. Use this method to show/hide the option of pulling the remote call.
     * @param {String} callId ID of the remote call to be pulled.
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canPullRemoteCall = function (callId) {
      var call = getCall(callId);
      return !!(call && call.isPullAllowed && !hasAnyMultipleLocalCalls() && isOsBizPushPullAllowed() && isMiV5000PullAllowed() && isMiVBPushPullAllowed());
    };

    /**
     * Returns if swapping 2 calls (1 active and 1 held) is possible or not. Use this method to show/hide the swap option on the held call.
     * @param {String} [callId] ID of the held call (local or remote). If not supplied, we'll try to find the held call.
     * @returns {Boolean} true if possible, false otherwise (or the held call could not be found).
     */
    this.canSwapCall = function (callId) {
      if (callId) {
        var call = getCall(callId);
        return canSwapCall(call);
      }
      // Check all calls
      return _calls.some(function (c) {
        return canSwapCall(c);
      });
    };

    /**
     * Returns if a held call can be retrieved or not. Use this method to show/hide the option of retrieving the call to the user.
     * @param {String} callId ID of the call to be retrieved (local or remote).
     * @returns {Boolean} true if possible, false otherwise.
     */
    this.canRetrieveCall = function (callId) {
      var call = getCall(callId);
      if (!call) {
        return false;
      }
      if ($rootScope.localUser.isOsBizCTIEnabled) {
        if ($rootScope.ctiWebRtcCalls) {
          return _calls.length < 2 && call.isHolding() && (call.isReconnectAllowed() || call.isRetrieveAllowed());
        }
        return (
          _calls.length < 2 &&
          call.isHolding() &&
          ((call.isReconnectAllowed() && (!call.isRemote || call.getPosition() === Targets.Cell)) || (call.isRetrieveAllowed() && call.isAtcRemote))
        );
      }
      if ($rootScope.localUser.isSTC) {
        return !multiplePhoneCalls(call) && call.isHolding();
      }
      return !multiplePhoneCalls(call) && isCallOnHold(callId) && call.isRetrieveAllowed();
    };

    /**
     * Answers a local or remote call on the specified device.
     * @param {String} callId ID of the call to be answered (local or remote).
     * @param {String} [deviceName=previoulsly set device] Name of the device (WebRTC, Cell, Desk or VM).
     * @param {Boolean} [endActiveCall] If false, the deviceName is 'WebRTC' and there is a local active call, a rejected promised will be
     *                                returned indicating the user need to be prompted to ask what to do with the local call.
     *                                If true, the current active call will be ended before answering the incoming call.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.answerCall = function (callId, deviceName, endActiveCall) {
      var call = getCall(callId);
      // check if callId is any of atcCallInfo.csta
      var localCall = _calls.find(function (c) {
        if (c.atcCallInfo) {
          return c.atcCallInfo.getCstaCallId() === callId;
        } else {
          return false;
        }
      });
      if (!call && localCall) {
        call = localCall;
        callId = call.callId;
      } else if (!call) {
        return $q.reject('Call not found');
      }
      var device = getDevice(deviceName) || this.defaultAnswerDevice;
      var deferred = $q.defer();

      if (device === Targets.WebRTC) {
        if (endActiveCall) {
          // Terminate the active call (if there is one), before answering the call
          CallControlSvc.endActiveCall(function () {
            CallControlSvc.answerCallOnDevice(callId, { audio: true }, device, handleCallControlCb(deferred));
          });
          return deferred.promise;
        } else if (CallControlSvc.cannotHandleSecondCall()) {
          // Cannot handle a second call. Prompt user to terminate the active call.
          deferred.reject('res_TerminateActiveCall');
          return deferred.promise;
        }
      }

      // Answer call on device
      CallControlSvc.answerCallOnDevice(callId, { audio: true }, device, handleCallControlCb(deferred));
      return deferred.promise;
    };

    /**
     * Initiates the process of making a consultation call. Use this method to show any special UI to initiate a consultation call.
     * @returns {void}
     */
    this.initiateConsultation = function (callId) {
      var call = getCall(callId);
      if (call) {
        call.consultation = true;
        PubSubSvc.publish('/consultation/status/changed', [call]);
      }
    };

    /**
     * Cancels the process of making a consultation call. Use this method to hide any special UI to initiate a consultation call.
     * @returns {void}
     */
    this.cancelConsultation = function (callId) {
      var call = getCall(callId);
      if (call) {
        call.consultation = false;
        PubSubSvc.publish('/consultation/status/changed', [call]);
      }
    };

    /**
     * Ends a local or remote call. You may use {@link canHangupCall} to check if ending a call is allowed.
     * @param {String} callId ID of the call to be terminated.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.endCall = function (callId) {
      if (!_that.canHangupCall(callId)) {
        return $q.reject('Call cannot be ended at the moment');
      }
      var deferred = $q.defer();
      CallControlSvc.endCall(callId, handleCallControlCb(deferred));
      return deferred.promise;
    };

    /**
     * Ends a call that is held. This API is intended for clients
     * that cannot disable the call release button when holding a
     * call but pbx requires that the call be retrieved before
     * releasing it.
     * @param {String} callId ID of the call to be terminated.
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.retrieveAndEndCall = function (callId) {
      if (!$rootScope.localUser.isOsBizCTIEnabled || _that.canHangupCall(callId)) {
        return _that.endCall(callId);
      }
      if (!isCallOnHold(callId)) {
        return $q.reject('Call cannot be ended at the moment');
      }
      var deferred = $q.defer();
      CallControlSvc.retrieveAndEndCall(callId, handleCallControlCb(deferred));
      return deferred.promise;
    };

    /**
     * Pulls a remote telephony call. You may use {@link canPullRemoteCall} to check if pulling a remote call is allowed.
     * @param {String} callId ID of the call to be pulled.
     * @param {Boolean} endActiveCall If false and there is a local active call, a rejected promised will be returned indicating
     *                                the user need to be prompted to ask what to do with the local call.
     *                                If the user wants to terminate the local call this function needs to be invoked again with this
     *                                parameter set to true.
     * @returns {Promise} Fulfillment handler receives the created call object. Rejection handler receives an error object.
     */
    this.pullRemoteCall = function (callId, endActiveCall) {
      if (hasUnifyPhoneActiveCall() && !endActiveCall) {
        return $q.reject('res_TerminateActiveCall');
      }
      if ($rootScope.localUser.isMiV5000) {
        var call = getCall(callId);
        if (call && call.getPosition() === Targets.Desk) {
          var pullCallFeatureCode = AtcRegistrationSvc.getAtcRegistrationData().pullCallFeatureCode;
          if (pullCallFeatureCode) {
            setDefaultCallDevice(Targets.WebRTC, true);
            LogSvc.debug('[PhoneCallSvc]: Pulling call with feature code:', pullCallFeatureCode);
            return pullWithFeatureCode(pullCallFeatureCode);
          }
          LogSvc.warn('[PhoneCallSvc]: Could not find feature code to pull the call');
          return $q.reject('Feature code not available');
        }
      }
      var deferred = $q.defer();
      CallControlSvc.pullRemoteCall(callId, false, handleCallControlCb(deferred));
      return deferred.promise;
    };

    /**
     * Send DTMF digits
     * @param {String} callId The call ID of active call
     * @param {String} digits The digits to be sent
     * @returns {Promise} Fulfillment handler receives no parameters. Rejection handler receives an error object.
     */
    this.sendDigits = function (callId, digits) {
      var call = _phoneConversation && _phoneConversation.call;
      if (!call || callId !== call.callId) {
        return $q.reject('Not active call');
      }
      if (!call.isDtmfAllowed) {
        return $q.reject('DTMF not allowed');
      }
      if (_sendDigitsQueue.push(digits) === 1) {
        processNextDigit();
      } else {
        LogSvc.debug('[PhoneCallSvc]: Digit(s) enqueued:', digits);
      }
      return $q.resolve();
    };

    /**
     * Stops playing the ringtone for the specified incoming call.
     * @param {String} callId ID of the ringing call.
     * @returns {void}
     */
    this.stopRingingTone = CallControlSvc.stopRingingTone;

    /**
     * Returns the participants of a merged call or an empty array if the call is not merged
     * @param {String} callId The callId of the call
     * @returns {Array}
     */
    this.getMergedCallParticipants = function (callId) {
      var call = getCall(callId);
      if (call) {
        var isMergedCall = call.isSTC ? call.isDirectUpgradedToConf : call.checkCstaState([CstaCallState.Conference, CstaCallState.ConferenceHolding]);
        if (isMergedCall) {
          if (call.isSTC) {
            return call.participants.slice();
          }
          var participants = call.participants.filter(function (p) {
            return !p.hasTelephonyRole;
          });
          if (participants.length > 1) {
            // ATC merged calls always have at least 3 participants (participant length > 1) otherwise the PBX reverts the call to 1-2-1 call.
            // STC merged calls do not revert to basic calls even if there are two parties left
            return participants;
          }
        }
      }
      return [];
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.PhoneCallSvcImpl = PhoneCallSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*global RegistrationState*/

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var ClientApiHandler = unifyPhone.ClientApiHandlerSingleton;
  var UserProfile = unifyPhone.UserProfile;
  var Utils = unifyPhone.Utils;

  // eslint-disable-next-line max-params, max-lines-per-function
  function ContactSvcImpl($rootScope, $q, LogSvc, PubSubSvc) {
    // NOSONAR
    LogSvc.debug('New Service: ContactSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Constants
    ///////////////////////////////////////////////////////////////////////////////////////
    var Constants = unifyPhone.Constants;

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Variables
    ///////////////////////////////////////////////////////////////////////////////////////

    var ONE_MIN = 60 * 1000;
    var HALF_HOUR = 30 * ONE_MIN;
    var MAX_GET_CONTACTS_BY_IDS = 50; // At most 50 contactIds in each get_contacts_by_ids request
    var CONTACT_GET_BY_ID_REFRESH_INTERVAL = HALF_HOUR; // Refresh contact if getContactsByIds is invoked more than 30 minutes after last refresh

    var _self = this;
    var _clientApiHandler = ClientApiHandler.getInstance();

    var _contacts = [];
    var _contactsHashtable = {}; // Hashtable of contact IDs to contact objects
    var _favorites = [];
    var _favoritesInitialized = false;
    var _reorderFavoritesQueue = [];

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal Functions
    ///////////////////////////////////////////////////////////////////////////////////////

    function sendFavoritePositionToServer() {
      if (_reorderFavoritesQueue.length === 0) {
        return;
      }
      // Get the first element, but don't remove from the list
      var item = _reorderFavoritesQueue[0];

      var processResponse = function (error) {
        _self.getCachedFavoritesPromise().then(function (favorites) {
          if (error) {
            // Need to cancel all pending requests
            _reorderFavoritesQueue.forEach(function (queuedItem) {
              queuedItem.cb && queuedItem.cb(error, favorites);
              queuedItem.cb = null;
            });
            Utils.emptyArray(_reorderFavoritesQueue);
          } else {
            _reorderFavoritesQueue.shift();
            if (_reorderFavoritesQueue.length > 0) {
              // We still have pending requests, so don't send the updated favorites list in the callback
              item.cb && item.cb(null);
              sendFavoritePositionToServer();
            } else {
              item.cb && item.cb(null, favorites);
            }
            item.cb = null;
          }
        });
      };

      _clientApiHandler.setFavoritePosition(item.favoriteId, item.position, function (error) {
        if (error) {
          LogSvc.error('[ContactSvc]: Error moving favorite id' + item.favoriteId + ' to position ' + item.position);
        } else {
          LogSvc.info('[ContactSvc]: Favorite ' + item.favoriteId + ' moved to position ' + item.position);
          var id = _favorites.indexOf(item.favoriteId);
          Utils.moveArrayElement(_favorites, id, item.position);
        }
        processResponse(error);
      });
    }

    function isOptionalStringPropertyValid(property) {
      return !property || typeof property === 'string';
    }

    function isContactDataValid(contactData) {
      var optionalStringProperties = ['displayName', 'firstName', 'lastName', 'extAvatarUri', 'smallImageUri', 'largeImageUri', 'originUserId'];

      return (
        contactData &&
        Array.isArray(contactData.emailAddresses) &&
        contactData.emailAddresses.every(function (emailObj) {
          return emailObj && emailObj.address && emailObj.type;
        }) &&
        Array.isArray(contactData.phoneNumbers) &&
        contactData.phoneNumbers.length &&
        contactData.phoneNumbers.every(function (numObj) {
          return numObj && numObj.phoneNumber && numObj.type;
        }) &&
        contactData.originType &&
        Object.keys(Constants.ContactOriginType).includes(contactData.originType) &&
        optionalStringProperties.every(function (prop) {
          return isOptionalStringPropertyValid(contactData[prop]);
        })
      );
    }

    function removeFromContacts(contactId) {
      _contacts = _contacts.filter(function (con) {
        return con._id !== contactId;
      });
      delete _contactsHashtable[contactId];
      // Related favorites will be removed from the backend so it should be removed from cache as well
      _favorites = _favorites.filter(function (fav) {
        return fav.contactId !== contactId;
      });
    }

    function addToFavorites(favorite) {
      if (
        _favorites.some(function (fav) {
          return fav._id === favorite._id;
        })
      ) {
        return;
      }
      _favorites.push(favorite);
      _self.getContactsByIds([favorite.contactId]);
    }

    function removeFromFavorites(favoriteId) {
      _favorites = _favorites.filter(function (fav) {
        return fav._id !== favoriteId;
      });
    }

    function removeFromContactsFavoritesByOriginType(originType) {
      var contactIdsToBeDeleted = [];

      _contacts = _contacts.filter(function (con) {
        if (con.originType === originType) {
          contactIdsToBeDeleted.push(con._id);
          return false;
        }
        return true;
      });

      _favorites = _favorites.filter(function (fav) {
        return !contactIdsToBeDeleted.includes(fav.contactId);
      });

      LogSvc.debug('[ContactSvc]: Publish /contacts/originType/deleted event');
      PubSubSvc.publish('/contacts/originType/deleted', [originType]);
    }

    function addFavorite(contactData, phoneNumber, cb) {
      if (!(contactData.contactId ? typeof contactData.contactId === 'string' : isContactDataValid(contactData)) || !phoneNumber) {
        LogSvc.error('[ContactSvc] addFavorite called with invalid contact data or no phoneNumber');
        cb && cb('Invalid data');
        return;
      }

      var dataForBackend = {
        contactData: contactData,
        phoneNumber: phoneNumber
      };

      _clientApiHandler.addFavorite(dataForBackend, function (err, data) {
        if (err) {
          LogSvc.error('[ContactSvc]: Error adding favorite: ', err);
          var addFavoriteErrorResourceName = err === Constants.ReturnCode.MAX_FAVORITES_REACHED ? 'res_MaxFavoritesReached' : 'res_FavoriteToggleError';
          cb && cb(addFavoriteErrorResourceName);
          return;
        }
        cb && cb(null, data);
      });
    }

    function removeFavorite(favoriteId, cb) {
      if (!favoriteId) {
        LogSvc.error('[ContactSvc]: removeFavorite called with missing favoriteId');
        cb && cb('Invalid data');
        return;
      }

      _clientApiHandler.removeFavorite(favoriteId, function (err, data) {
        if (err) {
          LogSvc.error('[ContactSvc]: Error removing favorite: ', err);
          cb && cb(err);
          return;
        }
        cb && cb(null, data);
      });
    }

    function setReqPending(contactIds) {
      contactIds &&
        contactIds.forEach(function (contactId) {
          var contact = _contactsHashtable[contactId];
          if (contact) {
            contact.reqPending = true;
          }
        });
    }

    function clearReqPending(contactIds) {
      contactIds &&
        contactIds.forEach(function (contactId) {
          var contact = _contactsHashtable[contactId];
          if (contact) {
            delete contact.reqPending;
          }
        });
    }

    function getContactsByIds(contactIds, throttle, cb) {
      var reqContactIds, pendingContactIds;
      if (throttle) {
        reqContactIds = contactIds.slice(0, MAX_GET_CONTACTS_BY_IDS);
        pendingContactIds = contactIds.slice(MAX_GET_CONTACTS_BY_IDS);
      } else {
        reqContactIds = contactIds;
        pendingContactIds = [];
      }

      setReqPending(reqContactIds);
      _clientApiHandler.getContactsByIds(reqContactIds, function (err, contacts) {
        clearReqPending(reqContactIds);
        if (err) {
          LogSvc.warn('[ContactSvc]: Failed to get contacts. ', err);
        }
        contacts = contacts || [];
        var summary = contacts.map(function (contact) {
          return {
            _id: contact._id,
            displayName: contact.displayName,
            originType: contact.originType,
            originUserId: contact.originUserId
          };
        });
        LogSvc.debug('[ContactSvc]: Retrieved ' + summary.length + ' contacts - ', summary);

        var now = Date.now();
        contacts.forEach(function (contact) {
          contact.updatedTimeStamp = now;
        });

        cb && cb(contacts, pendingContactIds.length === 0);

        if (pendingContactIds.length > 0) {
          LogSvc.debug('[ContactSvc]: Get remaining pending contacts');
          getContactsByIds(pendingContactIds, true, cb);
        }
      });
    }

    function getBaseContact(contact) {
      return contact && contact.isExtended ? Utils.getBaseObject(contact, true) : contact;
    }

    function normalizeContact(contact) {
      delete contact.userId;
      switch (contact.originType) {
        case Constants.ContactOriginType.EXCHANGE_ONLINE:
          contact.isExchangeContact = true;
          contact.isExchangeOnline = true;
          break;
        case Constants.ContactOriginType.EXCHANGE_ONPREM:
          contact.isExchangeContact = true;
          contact.isExchangeOnPremise = true;
          break;
        case Constants.ContactOriginType.GOOGLE_CONTACTS:
          contact.isGoogleContact = true;
          break;
        case Constants.ContactOriginType.OSBIZ_CONTACTS:
          contact.isOsbizContact = true;
          break;
        case Constants.ContactOriginType.MiV5000_CONTACTS:
          contact.isMiV5000Contact = true;
          break;
        case Constants.ContactOriginType.DEVICE:
          contact.isDeviceContact = true;
          break;
      }
    }

    function addContactsToCache(contacts) {
      if (!contacts) {
        return;
      }
      if (!Array.isArray(contacts)) {
        contacts = [contacts];
      }

      // Add contacts to cached array and hashtable for lookup.
      contacts.forEach(function (contact) {
        if (!contact || !contact._id) {
          return;
        }

        normalizeContact(contact);

        var cachedContact = _contactsHashtable[contact._id];
        if (cachedContact && cachedContact !== contact) {
          UserProfile.update(cachedContact, getBaseContact(contact));
        } else {
          if (!contact.isExtended) {
            contact = UserProfile.extend(contact);
          }
          _contacts.push(contact);
          _contactsHashtable[contact._id] = contact;
        }
      });
    }

    function addContactsAndReturnCached(contacts) {
      var cachedContacts = [];
      if (contacts && contacts.length) {
        addContactsToCache(contacts);
        contacts.forEach(function (contact) {
          cachedContacts.push(_contactsHashtable[contact._id]);
        });
      }
      return cachedContacts;
    }

    function addContactAndReturnCached(contact) {
      if (contact) {
        addContactsToCache([contact]);
        return _contactsHashtable[contact._id];
      }
      return null;
    }

    function initFavorites(force) {
      return new $q(function (resolve) {
        if (_favoritesInitialized && !force) {
          resolve();
        } else {
          // First fetch the favorites of the user
          _clientApiHandler.getFavorites(function (err, favorites) {
            if (err) {
              LogSvc.error('[ContactSvc]: Error getting favorites: ', err);
              resolve(err);
              return;
            }
            _favorites = favorites;

            // If no favorites, there is no need to get contacts
            if (!_favorites.length) {
              _favoritesInitialized = true;
              resolve();
              return;
            }

            // Then get the related contacts
            var contactIdsHash = {};
            _favorites.forEach(function (favorite) {
              contactIdsHash[favorite.contactId] = favorite.contactId;
            });

            _self.getContactsByIds(Object.keys(contactIdsHash), function (error) {
              if (error) {
                LogSvc.error('[ContactSvc]: Error getting contacts: ', error);
                resolve(error);
                return;
              }
              _favoritesInitialized = true;
              resolve();
            });
          });
        }
      });
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // Client API Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////

    _clientApiHandler.on('Contact.FAVORITE_ADDED', function (data) {
      LogSvc.debug('[ContactSvc]: Received Contact.FAVORITE_ADDED event');
      if (!_favoritesInitialized) {
        LogSvc.debug('[ContactSvc]: Ignoring Contact.FAVORITE_ADDED because favorites are not initialized yet');
        return;
      }

      if (!data.userId || !$rootScope.localUser) {
        LogSvc.error('[ContactSvc]: Contact.FAVORITE_ADDED event does not have a user');
        return;
      }

      if (data.userId !== $rootScope.localUser.userId) {
        LogSvc.error('[ContactSvc]: Contact.FAVORITE_ADDED event not for local user');
        return;
      }

      if (!data.favoriteData || !data.favoriteData.contactId || !data.favoriteData._id || !data.favoriteData.phoneNumber) {
        LogSvc.error('[ContactSvc]: Contact.FAVORITE_ADDED event with no contactId or favoriteId or phoneNumber data');
        return;
      }

      addToFavorites(data.favoriteData);

      LogSvc.debug('[ContactSvc]: Publish /contacts/favorite/added event');
      PubSubSvc.publish('/contacts/favorite/added', [data.favoriteData]);
    });

    _clientApiHandler.on('Contact.FAVORITE_REMOVED', function (data) {
      LogSvc.debug('[ContactSvc]: Received Contact.FAVORITE_REMOVED event');
      if (!_favoritesInitialized) {
        LogSvc.debug('[ContactSvc]: Ignoring Contact.FAVORITE_REMOVED because favorites are not initialized yet');
        return;
      }

      if (!data.userId || !$rootScope.localUser) {
        LogSvc.error('[ContactSvc]: Contact.FAVORITE_REMOVED event does not have a user');
        return;
      }

      if (data.userId !== $rootScope.localUser.userId) {
        LogSvc.error('[ContactSvc]: Contact.FAVORITE_REMOVED event not for local user');
        return;
      }

      if (!data.favoriteData || !data.favoriteData._id) {
        LogSvc.error('[ContactSvc]: Contact.FAVORITE_REMOVED event with no favoriteId data');
        return;
      }

      removeFromFavorites(data.favoriteData._id);

      LogSvc.debug('[ContactSvc]: Publish /contacts/favorite/removed event');
      PubSubSvc.publish('/contacts/favorite/removed', [data.favoriteData._id]);
    });

    _clientApiHandler.on('Contact.CONTACT_CREATED', function (data) {
      LogSvc.debug('[ContactSvc]: Received Contact.CONTACT_CREATED event');
      if (!data.userId || !$rootScope.localUser) {
        LogSvc.error('[ContactSvc]: Contact.CONTACT_CREATED event does not have a user');
        return;
      }

      if (data.userId !== $rootScope.localUser.userId) {
        LogSvc.error('[ContactSvc]: Contact.CONTACT_CREATED event not for local user');
        return;
      }

      if (!data.contactData) {
        LogSvc.error('[ContactSvc]: Contact.CONTACT_CREATED event with no contactData');
        return;
      }

      var contact = data.contactData;
      var summary = {
        _id: contact._id,
        displayName: contact.displayName,
        originType: contact.originType,
        originUserId: contact.originUserId
      };

      LogSvc.debug('[ContactSvc]: Contact.CONTACT_CREATED contact - ', summary);

      contact.updatedTimeStamp = Date.now();
      var contactData = addContactAndReturnCached(data.contactData);

      LogSvc.debug('[ContactSvc]: Publish /contacts/contact/created event');
      PubSubSvc.publish('/contacts/contact/created', [contactData]);
    });

    _clientApiHandler.on('Contact.CONTACT_DELETED', function (data) {
      LogSvc.debug('[ContactSvc]: Received Contact.CONTACT_DELETED event');
      if (!data.userId || !$rootScope.localUser) {
        LogSvc.error('[ContactSvc]: Contact.CONTACT_DELETED event does not have a user');
        return;
      }

      if (data.userId !== $rootScope.localUser.userId) {
        LogSvc.error('[ContactSvc]: Contact.CONTACT_DELETED event not for local user');
        return;
      }

      if (!data.contactData || !data.contactData._id) {
        LogSvc.error('[ContactSvc]: Contact.CONTACT_DELETED event with no contact _id');
        return;
      }

      removeFromContacts(data.contactData._id);

      LogSvc.debug('[ContactSvc]: Publish /contacts/contact/deleted event');
      PubSubSvc.publish('/contacts/contact/deleted', [data.contactData._id]);
    });

    _clientApiHandler.on('Contact.CONTACTS_FAVORITES_DELETED_BY_ORIGIN_TYPE', function (data) {
      LogSvc.debug('[ContactSvc]: Received Contact.CONTACTS_FAVORITES_DELETED_BY_ORIGIN_TYPE event');

      if (!_favoritesInitialized) {
        LogSvc.debug('[ContactSvc]: Ignoring Contact.CONTACTS_FAVORITES_DELETED_BY_ORIGIN_TYPE because favorites are not initialized yet');
        return;
      }

      if (!data.userId || !$rootScope.localUser) {
        LogSvc.error('[ContactSvc]: Contact.CONTACTS_FAVORITES_DELETED_BY_ORIGIN_TYPE event does not have a user');
        return;
      }

      if (data.userId !== $rootScope.localUser.userId) {
        LogSvc.error('[ContactSvc]: Contact.CONTACTS_FAVORITES_DELETED_BY_ORIGIN_TYPE event not for local user');
        return;
      }

      if (!data.originTypeData || !data.originTypeData.originType || !Object.values(Constants.ContactOriginType).includes(data.originTypeData.originType)) {
        LogSvc.error('[ContactSvc]: Contact.CONTACTS_FAVORITES_DELETED_BY_ORIGIN_TYPE event with no/invalid originType');
        return;
      }

      removeFromContactsFavoritesByOriginType(data.originTypeData.originType);
    });

    _clientApiHandler.on('Contact.FAVORITE_POSITION_CHANGED', function (favoritePositionChanged) {
      if (!_favoritesInitialized) {
        LogSvc.debug('[ContactSvc]: Ignoring Contact.FAVORITE_POSITION_CHANGED because favorites are not initialized yet');
        return;
      }

      var id = _favorites.findIndex(function (item) {
        return favoritePositionChanged.favoriteId === item._id;
      });
      Utils.moveArrayElement(_favorites, id, favoritePositionChanged.position);

      LogSvc.debug('[ContactSvc]: Publish /contact/favoritePositionChanged event');
      PubSubSvc.publish('/contacts/favoritePositionChanged', favoritePositionChanged);
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////

    function onRegistrationState(state) {
      LogSvc.debug('[ContactSvc]: Received /registration/state event');
      if (state === RegistrationState.Registered && _favoritesInitialized) {
        LogSvc.info('[ContactSvc]: Client is registered again. Refreshing cached favorites');
        initFavorites(true);
      }
    }
    PubSubSvc.subscribe('/registration/state', onRegistrationState);

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    this.addFavorite = addFavorite;
    this.removeFavorite = removeFavorite;

    /**
     * Gets the contacts from cache.
     * If favorites are not initialized, it fetches them and then fetches the relevant contacts.
     *
     * @returns {Promise<Array<object>>} Promise The array of cached contacts.
     */
    this.getCachedContactsPromise = function () {
      return new $q(function (resolve, reject) {
        if (_favoritesInitialized) {
          resolve(_contacts);
        } else {
          initFavorites().then(function (err) {
            if (err) {
              return reject(err);
            }
            return resolve(_contacts);
          });
        }
      });
    };

    /**
     * Gets the favorites from cache.
     * If favorites are not initialized, it fetches them and then fetches the relevant contacts.
     *
     * @returns {Promise<Array<object>>} Promise The array of cached favorites.
     */
    this.getCachedFavoritesPromise = function () {
      return new $q(function (resolve, reject) {
        if (_favoritesInitialized) {
          resolve(_favorites);
        } else {
          initFavorites().then(function (err) {
            if (err) {
              return reject(err);
            }
            return resolve(_favorites);
          });
        }
      });
    };

    /**
     * Retrieve contacts from cache (if available) or from server. Order is not preserved.
     *
     * @param {Array<String>} contactIds Contact IDs
     * @param {Function} cb Callback returning error and contacts parameters
     */
    this.getContactsByIds = function (contactIds, cb) {
      cb = cb || function () {};
      if (!Array.isArray(contactIds) || contactIds.length === 0) {
        cb(Constants.ReturnCode.NO_RESULT);
        return;
      }

      var contacts = [];
      var pendingContactIds = [];
      var outdatedTimestamp = Date.now() - CONTACT_GET_BY_ID_REFRESH_INTERVAL;

      contactIds.forEach(function (contactId) {
        var contact = _contactsHashtable[contactId];

        if (contact && (contact.updatedTimeStamp > outdatedTimestamp || contact.reqPending)) {
          contacts.push(contact);
          return;
        }

        pendingContactIds.push(contactId);
      });

      if (pendingContactIds.length === 0) {
        cb(null, contacts);
        return;
      }

      getContactsByIds(pendingContactIds, true, function (otherContacts, finished) {
        if (otherContacts.length > 0) {
          Array.prototype.push.apply(contacts, addContactsAndReturnCached(otherContacts));
        }

        if (finished) {
          if (contacts.length > 0) {
            cb(null, contacts);
          } else {
            cb(Constants.ReturnCode.NO_RESULT);
          }
        }
      });
    };

    /**
     * Delete all favorites and contacts of a user by originType.
     *
     * @param {string} originType The type of the favorites & contacts to delete
     * @param {Function} cb Callback returning error
     */
    this.deleteContactDataByOriginType = function (originType, cb) {
      cb = cb || function () {};

      if (!Object.values(Constants.ContactOriginType).includes(originType)) {
        cb('Invalid originType: ' + originType);
        return;
      }

      _clientApiHandler.deleteContactDataByOriginType(originType, function (err, data) {
        if (err) {
          LogSvc.error('[ContactSvc]: Error removing data by originType: ', err);
          cb && cb(err);
          return;
        }
        cb && cb(null, data);
      });
    };

    /**
     * Sets a favorite number to a new position
     *
     * @param {String} favoriteId The favorited id
     * @param {Number} position The position to set the favorite.
     * @param {Function} cb Callback to handle server error.
     */
    this.setFavoritePosition = function (favoriteId, position, cb) {
      cb = cb || function () {};

      LogSvc.debug('[ContactSvc]: setFavoritePosition favorite id ' + favoriteId + ' to position: ' + position);

      if (!favoriteId || typeof position !== 'number' || position < 0) {
        LogSvc.error('[ContactSvc]: setFavoritePosition called with invalid data');
        cb('Invalid favoriteId or position');
        return;
      }

      var item = {
        favoriteId: favoriteId,
        position: position,
        cb: cb
      };

      _reorderFavoritesQueue.push(item);

      // Call function to apply changes to the server for the first item in the queue.
      // For additional items in the queue sendFavoritePositionToServer will be called recursively
      if (_reorderFavoritesQueue.length === 1) {
        sendFavoritePositionToServer();
      }
    };

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.ContactSvcImpl = ContactSvcImpl;

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  // Imports
  var RtcSessionController = unifyPhone.RtcSessionController;

  // eslint-disable-next-line max-params, max-lines-per-function, no-unused-vars
  function RtcSvcImpl($rootScope, $q, LogSvc, PubSubSvc, PopupSvc, ExtensionSvc, UnifyPhoneCallControlSvc) {
    // NOSONAR
    LogSvc.debug('New Service: RtcSvc');

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal variables
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // Internal functions
    ///////////////////////////////////////////////////////////////////////////////////////
    function checkPermission() {
      // Change audio codec from tenant
      // NGTC-8941: NGTC-OSV : Loud disturbing ringing noise instead of Comfort Noise.
      // When user refreshes or signs in then the default value for RtcSessionController.disableAudioCN is false
      // only when receiving comfortNoiseDisabled -> true we change it to true
      if ($rootScope.localUser && $rootScope.localUser.comfortNoiseDisabled) {
        RtcSessionController.disableAudioCN = true;
      }
    }

    ///////////////////////////////////////////////////////////////////////////////////////
    // PubSubSvc Event Handlers
    ///////////////////////////////////////////////////////////////////////////////////////
    PubSubSvc.subscribe('/localUser/tenantSettingsUpdate', function () {
      checkPermission();
    });

    PubSubSvc.subscribe('/registration/stuff', function () {
      checkPermission();
    });

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Interface
    ///////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////////////////////////////////////////////////////////
    // Public Factory Interface for Angular
    ///////////////////////////////////////////////////////////////////////////////////////
    return this;
  }

  // Exports
  unifyPhone.RtcSvcImpl = RtcSvcImpl;
  unifyPhone.Enums = unifyPhone.Enums || {};

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define

var UnifyPhone = (function (unifyPhone) {
  'use strict';

  var logger = unifyPhone.logger;

  // Start by creating the base Angular objects
  logger.debug('[impServicesLoader]: Creating Angular objects');

  //---------------------------------------------
  // Create Angular $rootScope object
  //---------------------------------------------
  // var rootScope = {
  //     isRegistered: false,
  //     isChromeApp: false,
  //     isMobileApp: true,
  //     localUser: null,
  //     webRtcEnabled: true,
  //     browser: {
  //         ios: true,
  //         type: 'ios'
  //     },

  //     // Angular interfaces
  //     $$phase: true,
  //     $apply: function (callback) {
  //         callback && callback();
  //     },
  //     $digest: function () {
  //     }
  // };

  logger.debug('[impServicesLoader]: Created Angular objects');

  /* eslint-enable max-len, max-lines-per-function */

  // Instantiate services either for guest or normal user
  function loadServices(guest, productName, data) {
    var services = {};
    logger = unifyPhone.logger;

    var rootScopeFn = data.rootScope;
    rootScopeFn.isSessionGuest = !!guest;
    unifyPhone.productName = productName || 'Unify Phone';
    rootScopeFn.isRebrandedSystem = unifyPhone.productName !== 'Unify Phone';

    unifyPhone.__serverName = data.serverName;
    unifyPhone.__wsOverrideImpl = data.wsOverrideImpl;

    logger.info('[impServicesLoader]: loadServices: guest = ' + rootScopeFn.isSessionGuest + ', productName = ' + unifyPhone.productName);

    //---------------------------------------------------------------------------
    // Create the services.
    //---------------------------------------------------------------------------
    loadCommonServices(data, services);
    loadNormalServices(data, services);

    unifyPhone.serviceInstances = services;
    return true;
  }

  function loadCommonServices(data, services) {
    data = data || {};
    var rootScopeFn = data.rootScope;
    var promiseFn = data.promise;
    var timeoutFn = data.timeout;
    var httpFn = data.http;
    var popupFn = data.popupFn;

    try {
      services.logSvc = new unifyPhone.LogSvcImpl(rootScopeFn, timeoutFn);
      logger.debug('[impServicesLoader]: Instantiated LogSvc service');
      services.logSvc.startFileLogger(false, window.__clientVersion);

      // UtilSvc
      services.utilSvc = new unifyPhone.UtilSvcImpl(rootScopeFn, promiseFn, timeoutFn, logger);
      logger.debug('[impServicesLoader]: Instantiated UtilSvc service');

      // PubSubSvc
      services.pubSubSvc = new unifyPhone.PubSubSvcImpl(logger);
      logger.debug('[impServicesLoader]: Instantiated PubSubSvc service');

      // HttpSvc
      services.httpSvc = new unifyPhone.HttpSvcImpl(rootScopeFn, window, [document], timeoutFn, httpFn, promiseFn, logger);

      logger.debug('[impServicesLoader]: Created HttpSvc service');

      // LocalizeSvc
      services.localizeSvc = new unifyPhone.LocalizeSvcImpl(
        rootScopeFn,
        window,
        null, // $locale
        promiseFn,
        logger,
        services.utilSvc,
        services.pubSubSvc,
        services.httpSvc
      );
      logger.debug('[impServicesLoader]: Instantiated LocalizeSvc service');

      // PopupSvc
      services.popupSvc = popupFn;
      logger.debug('[impServicesLoader]: Created PopupSvc service');

      // LocalStoreSvc
      services.localStoreSvc = new unifyPhone.LocalStoreSvcImpl(rootScopeFn, timeoutFn, promiseFn, window, logger);
      logger.debug('[impServicesLoader]: Instantiated LocalStoreSvcImpl service');

      // UnifyPhoneLabSvc
      services.unifyPhoneLabSvc = new unifyPhone.UnifyPhoneLabSvcImpl(
        rootScopeFn,
        window,
        logger,
        services.pubSubSvc,
        services.localStoreSvc,
        services.localizeSvc
      );
      logger.debug('[impServicesLoader]: Instantiated UnifyPhoneLabSvcImpl service');

      // InstrumentationSvc
      services.instrumentationSvc = new unifyPhone.InstrumentationSvcImpl(rootScopeFn, logger, services.pubSubSvc, services.localStoreSvc);
      logger.debug('[impServicesLoader]: Instantiated InstrumentationSvc service');

      // PictureEditSvc
      services.pictureEditSvc = new unifyPhone.PictureEditSvcImpl(window, promiseFn);
      logger.debug('[impServicesLoader]: Instantiated InstrumentationSvc service');

      // DeviceDiagnosticSvc
      services.deviceDiagnosticSvc = new unifyPhone.DeviceDiagnosticSvcImpl(rootScopeFn, timeoutFn, logger, services.pubSubSvc, services.localStoreSvc);

      logger.debug('[impServicesLoader]: Instantiated DeviceDiagnosticSvc service');
    } catch (e) {
      logger.error('[impServicesLoader]: Failed to create common service instances. ', e);
    }
  }

  // eslint-disable-next-line max-lines-per-function
  function loadNormalServices(data, services) {
    // NOSONAR
    data = data || {};
    var rootScopeFn = data.rootScope;
    var promiseFn = data.promise;
    var timeoutFn = data.timeout;
    var intervalFn = data.interval;
    var httpFn = data.http;
    var injectorFn = data.injectorFn;

    // Load normal user services
    try {
      // FileApiSvc
      services.fileApiSvc = new unifyPhone.FileApiSvcImpl(httpFn, promiseFn, logger);
      logger.debug('[impServicesLoader]: Created FileApiSvc service');

      // LocationSvc
      services.locationSvc = new unifyPhone.LocationSvcImpl(rootScopeFn, window, httpFn, timeoutFn, logger, services.localizeSvc, services.localStoreSvc);
      logger.debug('[impServicesLoader]: Instantiated LocationSvc service');

      // UserProfileSvc
      services.userProfileSvc = new unifyPhone.UserProfileSvcImpl(
        rootScopeFn,
        promiseFn,
        intervalFn,
        timeoutFn,
        logger,
        services.pubSubSvc,
        services.localizeSvc,
        services.localStoreSvc
      );
      logger.debug('[impServicesLoader]: Instantiated UserProfileSvc service');

      // ContactSvc
      services.contactSvc = new unifyPhone.ContactSvcImpl(rootScopeFn, promiseFn, logger, services.pubSubSvc);
      logger.debug('[impServicesLoader]: Instantiated ContactSvc service');

      // RegistrationSvc
      services.registrationSvc = new unifyPhone.RegistrationSvcImpl(
        rootScopeFn,
        timeoutFn,
        promiseFn,
        httpFn,
        logger,
        services.pubSubSvc,
        services.localStoreSvc,
        services.userProfileSvc
      );
      logger.debug('[impServicesLoader]: Instantiated RegistrationSvc service');

      // ExtensionSvc
      services.extensionSvc = new unifyPhone.ExtensionSvcImpl(
        rootScopeFn,
        timeoutFn,
        window,
        promiseFn,
        logger,
        services.pubSubSvc,
        services.popupSvc,
        services.registrationSvc
      );
      logger.debug('[impServicesLoader]: Instantiated ExtensionSvc service');

      // ExchangeConnSvc
      services.exchangeConnSvc = {
        searchContactsWithConstraints: function (searchStr, constraints, resCount, cb) {
          cb && cb();
        }
      };

      // MailboxConnSvc
      services.mailboxConnSvc = new unifyPhone.MailboxConnSvcImpl(rootScopeFn, promiseFn, injectorFn, logger, services.pubSubSvc);

      logger.debug('[impServicesLoader]: Instantiated MailboxConnSvc service');

      // UserSvc
      services.userSvc = new unifyPhone.UserSvcImpl(
        rootScopeFn,
        timeoutFn,
        intervalFn,
        promiseFn,
        services.localStoreSvc,
        logger,
        services.pubSubSvc,
        services.mailboxConnSvc
      );
      logger.debug('[impServicesLoader]: Instantiated UserSvc service');

      services.notificationSvc = new unifyPhone.NotificationSvcImpl(rootScopeFn, timeoutFn, window, logger, services.pubSubSvc, services.userSvc);
      logger.debug('[impServicesLoader]: Instantiated NotificationSvc service');

      // ConversationSvc
      services.conversationSvc = new unifyPhone.ConversationSvcImpl(rootScopeFn, promiseFn, timeoutFn, logger, services.pubSubSvc);
      logger.debug('[impServicesLoader]: Instantiated ConversationSvc service');

      // PhoneCallLogSvc
      if (unifyPhone.PhoneCallLogSvcImpl) {
        services.phoneCallLogSvc = new unifyPhone.PhoneCallLogSvcImpl(
          rootScopeFn,
          promiseFn,
          services.conversationSvc,
          logger,
          services.pubSubSvc,
          services.userSvc,
          services.notificationSvc
        );
        logger.debug('[impServicesLoader]: Instantiated PhoneCallLogSvc service');
      }

      // AtcRegistrationSvc
      services.atcRegistrationSvc = new unifyPhone.AtcRegistrationSvcImpl(
        rootScopeFn,
        timeoutFn,
        logger,
        services.pubSubSvc,
        services.registrationSvc,
        services.conversationSvc
      );
      logger.debug('[impServicesLoader]: Instantiated AtcRegistrationSvc service');

      // UnifyPhoneCallControlSvc
      services.unifyPhoneCallControlSvc = new unifyPhone.UnifyPhoneCallControlSvcImpl(
        rootScopeFn,
        timeoutFn,
        intervalFn,
        window,
        promiseFn,
        logger,
        services.pubSubSvc,
        services.userSvc,
        services.conversationSvc,
        services.notificationSvc,
        services.instrumentationSvc,
        services.deviceDiagnosticSvc
      );
      logger.debug('[impServicesLoader]: Instantiated UnifyPhoneCallControlSvc service');

      // CstaSvc
      services.cstaSvc = new unifyPhone.CstaSvcImpl(
        rootScopeFn,
        timeoutFn,
        promiseFn,
        logger,
        services.pubSubSvc,
        services.unifyPhoneCallControlSvc,
        services.atcRegistrationSvc,
        services.userSvc,
        services.userProfileSvc
      );
      logger.debug('[impServicesLoader]: Instantiated CstaSvc service');

      // CallControlSvc
      services.callControlSvc = new unifyPhone.CallControlSvcImpl(
        rootScopeFn,
        timeoutFn,
        logger,
        services.pubSubSvc,
        services.unifyPhoneCallControlSvc,
        services.conversationSvc,
        services.cstaSvc,
        services.notificationSvc,
        services.userProfileSvc
      );
      logger.debug('[impServicesLoader]: Instantiated CallControlSvc service');

      // SoundSvc
      services.soundSvc = new unifyPhone.SoundSvcImpl(
        rootScopeFn,
        window,
        services.callControlSvc,
        services.conversationSvc,
        logger,
        services.notificationSvc,
        services.pubSubSvc,
        services.localStoreSvc,
        services.userProfileSvc
      );
      logger.debug('[impServicesLoader]: Instantiated SoundSvc service');

      // HeadsetHidControlSvc
      // services.headsetHidControlSvc = new unifyPhone.HeadsetHidControlSvcImpl(
      //     rootScopeFn,
      //     window,
      //     timeoutFn,
      //     promiseFn,
      //     logger,
      //     services.pubSubSvc,
      //     services.popupSvc,
      //     services.localStoreSvc,
      //     services.userProfileSvc,
      //     services.registrationSvc,
      //     services.callControlSvc,
      //     services.extensionSvc
      // );
      // logger.debug('[impServicesLoader]: Instantiated HeadsetHidControlSvc service');

      // PlantronicsControlSvc
      services.plantronicsControlSvc = new unifyPhone.PlantronicsControlSvcImpl(
        rootScopeFn,
        intervalFn,
        timeoutFn,
        promiseFn,
        logger,
        services.pubSubSvc,
        services.callControlSvc,
        services.localStoreSvc
      );
      logger.debug('[impServicesLoader]: Instantiated PlantronicsControlSvc service');

      // DeviceHandlerSvc
      services.deviceHandlerSvc = new unifyPhone.DeviceHandlerSvcImpl(
        window,
        promiseFn,
        logger,
        services.pubSubSvc,
        services.localStoreSvc,
        null // services.headsetHidControlSvc
      );
      logger.debug('[impServicesLoader]: Instantiated DeviceHandlerSvc service');

      // DeviceSettingsSvc
      services.deviceSettingsSvc = new unifyPhone.DeviceSettingsSvcImpl(
        rootScopeFn,
        promiseFn,
        timeoutFn,
        window,
        logger,
        services.pubSubSvc,
        services.deviceHandlerSvc,
        services.callControlSvc
      );
      logger.debug('[impServicesLoader]: Instantiated DeviceSettingsSvc service');

      // CallAssociatedNotificationsSvc
      services.callAssociatedNotificationsSvc = new unifyPhone.CallAssociatedNotificationsSvcImpl(
        rootScopeFn,
        timeoutFn,
        logger,
        services.pubSubSvc,
        services.popupSvc,
        services.callControlSvc,
        services.deviceHandlerSvc
      );
      logger.debug('[impServicesLoader]: Instantiated DeviceSettingsSvc service');

      // // AccountManagementSvc
      // services.accountManagementSvc = new unifyPhone.AccountManagementSvcImpl(
      //     promiseFn,
      //     rootScopeFn,
      //     timeoutFn,
      //     httpFn,
      //     logger,
      //     services.utilSvc,
      //     services.pubSubSvc,
      //     services.userSvc);
      // logger.debug('[impServicesLoader]: Instantiated AccountManagementSvc service');

      // ContactCardSvc
      services.contactCardSvc = new unifyPhone.ContactCardSvcImpl(
        rootScopeFn,
        promiseFn,
        logger,
        services.utilSvc,
        services.pubSubSvc,
        services.popupSvc,
        services.userSvc,
        services.conversationSvc,
        services.callControlSvc,
        services.extensionSvc
      );
      logger.debug('[impServicesLoader]: Instantiated ContactCardSvc service');

      // VoicemailSvc
      services.voicemailSvc = new unifyPhone.VoicemailSvcImpl(
        rootScopeFn,
        window,
        promiseFn,
        httpFn,
        logger,
        services.pubSubSvc,
        services.fileApiSvc,
        services.userProfileSvc
      );
      logger.debug('[impServicesLoader]: Instantiated VoicemailSvc service');

      // TelephonySettingsSvc.js
      services.telephonySettingsSvc = new unifyPhone.TelephonySettingsSvcImpl(
        rootScopeFn,
        promiseFn,
        logger,
        services.pubSubSvc,
        services.cstaSvc,
        services.userProfileSvc,
        services.voicemailSvc
      );
      logger.debug('[impServicesLoader]: Instantiated TelephonySettingsSvc service');

      // PictureEditSvc
      services.pictureEditSvc = new unifyPhone.PictureEditSvcImpl(window, promiseFn);
      logger.debug('[impServicesLoader]: Instantiated InstrumentationSvc service');

      // FileUploadSvc
      services.fileUploadSvc = new unifyPhone.FileUploadSvcImpl(rootScopeFn, httpFn, promiseFn, logger);
      logger.debug('[impServicesLoader]: Instantiated FileUploadSvc service');

      // PhoneCallSvc.js
      if (unifyPhone.PhoneCallSvcImpl) {
        services.phoneCallSvc = new unifyPhone.PhoneCallSvcImpl(
          rootScopeFn,
          promiseFn,
          timeoutFn,
          services.localStoreSvc,
          logger,
          services.pubSubSvc,
          services.userProfileSvc,
          services.callControlSvc,
          services.atcRegistrationSvc,
          services.popupSvc
        ); // PopupSvc
        logger.debug('[impServicesLoader]: Instantiated PhoneCallSvc service');
      }
      // RtcSvc.js
      services.rtcSvc = new unifyPhone.RtcSvcImpl(
        rootScopeFn,
        promiseFn,
        logger,
        services.pubSubSvc,
        services.popupSvc,
        services.extensionSvc,
        services.unifyPhoneCallControlSvc
      );
      logger.debug('[impServicesLoader]: Instantiated RtcSvc service');
    } catch (e) {
      logger.error('[impServicesLoader]: Failed to create normal user specific service instances. ', e);
    }
  }

  unifyPhone.initBL = function (data) {
    loadServices(false, 'Unify Phone', data);
  };

  return unifyPhone;
})(UnifyPhone || {}); //eslint-disable-line no-use-before-define
/*******************************************************************************
 * Copyright (c) 2013, 2016 IBM Corp.
 *
 * All rights reserved. This program and the accompanying materials
 * are made available under the terms of the Eclipse Public License v1.0
 * and Eclipse Distribution License v1.0 which accompany this distribution.
 *
 * The Eclipse Public License is available at
 *    http://www.eclipse.org/legal/epl-v10.html
 * and the Eclipse Distribution License is available at
 *   http://www.eclipse.org/org/documents/edl-v10.php.
 *
 *******************************************************************************/

(function (t, q) {
  'object' === typeof exports && 'object' === typeof module
    ? (module.exports = q())
    : 'function' === typeof define && define.amd
      ? define(q)
      : 'object' === typeof exports
        ? (exports = q())
        : (t.Paho = q());
})(this, function () {
  return (function (t) {
    function q(a, b, c) {
      b[c++] = a >> 8;
      b[c++] = a % 256;
      return c;
    }
    function r(a, b, c, k) {
      k = q(b, c, k);
      E(a, c, k);
      return k + b;
    }
    function n(a) {
      for (var b = 0, c = 0; c < a.length; c++) {
        var k = a.charCodeAt(c);
        2047 < k ? (55296 <= k && 56319 >= k && (c++, b++), (b += 3)) : 127 < k ? (b += 2) : b++;
      }
      return b;
    }
    function E(a, b, c) {
      for (var k = 0; k < a.length; k++) {
        var e = a.charCodeAt(k);
        if (55296 <= e && 56319 >= e) {
          var g = a.charCodeAt(++k);
          if (isNaN(g)) throw Error(f(h.MALFORMED_UNICODE, [e, g]));
          e = ((e - 55296) << 10) + (g - 56320) + 65536;
        }
        127 >= e
          ? (b[c++] = e)
          : (2047 >= e
              ? (b[c++] = ((e >> 6) & 31) | 192)
              : (65535 >= e ? (b[c++] = ((e >> 12) & 15) | 224) : ((b[c++] = ((e >> 18) & 7) | 240), (b[c++] = ((e >> 12) & 63) | 128)),
                (b[c++] = ((e >> 6) & 63) | 128)),
            (b[c++] = (e & 63) | 128));
      }
      return b;
    }
    function F(a, b, c) {
      for (var k = '', e, g = b; g < b + c; ) {
        e = a[g++];
        if (!(128 > e)) {
          var m = a[g++] - 128;
          if (0 > m) throw Error(f(h.MALFORMED_UTF, [e.toString(16), m.toString(16), '']));
          if (224 > e) e = 64 * (e - 192) + m;
          else {
            var d = a[g++] - 128;
            if (0 > d) throw Error(f(h.MALFORMED_UTF, [e.toString(16), m.toString(16), d.toString(16)]));
            if (240 > e) e = 4096 * (e - 224) + 64 * m + d;
            else {
              var l = a[g++] - 128;
              if (0 > l) throw Error(f(h.MALFORMED_UTF, [e.toString(16), m.toString(16), d.toString(16), l.toString(16)]));
              if (248 > e) e = 262144 * (e - 240) + 4096 * m + 64 * d + l;
              else throw Error(f(h.MALFORMED_UTF, [e.toString(16), m.toString(16), d.toString(16), l.toString(16)]));
            }
          }
        }
        65535 < e && ((e -= 65536), (k += String.fromCharCode(55296 + (e >> 10))), (e = 56320 + (e & 1023)));
        k += String.fromCharCode(e);
      }
      return k;
    }
    var s =
        t.localStorage ||
        (function () {
          var a = {};
          return {
            setItem: function (b, c) {
              a[b] = c;
            },
            getItem: function (b) {
              return a[b];
            },
            removeItem: function (b) {
              delete a[b];
            }
          };
        })(),
      A = function (a, b) {
        for (var c in a)
          if (a.hasOwnProperty(c))
            if (b.hasOwnProperty(c)) {
              if (typeof a[c] !== b[c]) throw Error(f(h.INVALID_TYPE, [typeof a[c], c]));
            } else {
              c = 'Unknown property, ' + c + '. Valid properties are:';
              for (var k in b) b.hasOwnProperty(k) && (c = c + ' ' + k);
              throw Error(c);
            }
      },
      u = function (a, b) {
        return function () {
          return a.apply(b, arguments);
        };
      },
      h = {
        OK: { code: 0, text: 'AMQJSC0000I OK.' },
        CONNECT_TIMEOUT: { code: 1, text: 'AMQJSC0001E Connect timed out.' },
        SUBSCRIBE_TIMEOUT: { code: 2, text: 'AMQJS0002E Subscribe timed out.' },
        UNSUBSCRIBE_TIMEOUT: { code: 3, text: 'AMQJS0003E Unsubscribe timed out.' },
        PING_TIMEOUT: { code: 4, text: 'AMQJS0004E Ping timed out.' },
        INTERNAL_ERROR: { code: 5, text: 'AMQJS0005E Internal error. Error Message: {0}, Stack trace: {1}' },
        CONNACK_RETURNCODE: { code: 6, text: 'AMQJS0006E Bad Connack return code:{0} {1}.' },
        SOCKET_ERROR: { code: 7, text: 'AMQJS0007E Socket error:{0}.' },
        SOCKET_CLOSE: { code: 8, text: 'AMQJS0008I Socket closed.' },
        MALFORMED_UTF: { code: 9, text: 'AMQJS0009E Malformed UTF data:{0} {1} {2}.' },
        UNSUPPORTED: { code: 10, text: 'AMQJS0010E {0} is not supported by this browser.' },
        INVALID_STATE: { code: 11, text: 'AMQJS0011E Invalid state {0}.' },
        INVALID_TYPE: { code: 12, text: 'AMQJS0012E Invalid type {0} for {1}.' },
        INVALID_ARGUMENT: { code: 13, text: 'AMQJS0013E Invalid argument {0} for {1}.' },
        UNSUPPORTED_OPERATION: { code: 14, text: 'AMQJS0014E Unsupported operation.' },
        INVALID_STORED_DATA: { code: 15, text: 'AMQJS0015E Invalid data in local storage key\x3d{0} value\x3d{1}.' },
        INVALID_MQTT_MESSAGE_TYPE: { code: 16, text: 'AMQJS0016E Invalid MQTT message type {0}.' },
        MALFORMED_UNICODE: { code: 17, text: 'AMQJS0017E Malformed Unicode string:{0} {1}.' },
        BUFFER_FULL: { code: 18, text: 'AMQJS0018E Message buffer is full, maximum buffer size: {0}.' }
      },
      H = {
        0: 'Connection Accepted',
        1: 'Connection Refused: unacceptable protocol version',
        2: 'Connection Refused: identifier rejected',
        3: 'Connection Refused: server unavailable',
        4: 'Connection Refused: bad user name or password',
        5: 'Connection Refused: not authorized'
      },
      f = function (a, b) {
        var c = a.text;
        if (b)
          for (var k, e, g = 0; g < b.length; g++)
            if (((k = '{' + g + '}'), (e = c.indexOf(k)), 0 < e))
              var h = c.substring(0, e),
                c = c.substring(e + k.length),
                c = h + b[g] + c;
        return c;
      },
      B = [0, 6, 77, 81, 73, 115, 100, 112, 3],
      C = [0, 4, 77, 81, 84, 84, 4],
      p = function (a, b) {
        this.type = a;
        for (var c in b) b.hasOwnProperty(c) && (this[c] = b[c]);
      };
    p.prototype.encode = function () {
      var a = (this.type & 15) << 4,
        b = 0,
        c = [],
        k = 0,
        e;
      void 0 !== this.messageIdentifier && (b += 2);
      switch (this.type) {
        case 1:
          switch (this.mqttVersion) {
            case 3:
              b += B.length + 3;
              break;
            case 4:
              b += C.length + 3;
          }
          b += n(this.clientId) + 2;
          void 0 !== this.willMessage &&
            ((b += n(this.willMessage.destinationName) + 2),
            (e = this.willMessage.payloadBytes),
            e instanceof Uint8Array || (e = new Uint8Array(h)),
            (b += e.byteLength + 2));
          void 0 !== this.userName && (b += n(this.userName) + 2);
          void 0 !== this.password && (b += n(this.password) + 2);
          break;
        case 8:
          for (var a = a | 2, g = 0; g < this.topics.length; g++) ((c[g] = n(this.topics[g])), (b += c[g] + 2));
          b += this.requestedQos.length;
          break;
        case 10:
          a |= 2;
          for (g = 0; g < this.topics.length; g++) ((c[g] = n(this.topics[g])), (b += c[g] + 2));
          break;
        case 6:
          a |= 2;
          break;
        case 3:
          this.payloadMessage.duplicate && (a |= 8);
          a = a |= this.payloadMessage.qos << 1;
          this.payloadMessage.retained && (a |= 1);
          var k = n(this.payloadMessage.destinationName),
            h = this.payloadMessage.payloadBytes,
            b = b + (k + 2) + h.byteLength;
          h instanceof ArrayBuffer ? (h = new Uint8Array(h)) : h instanceof Uint8Array || (h = new Uint8Array(h.buffer));
      }
      var f = b,
        g = Array(1),
        d = 0;
      do {
        var z = f % 128,
          f = f >> 7;
        0 < f && (z |= 128);
        g[d++] = z;
      } while (0 < f && 4 > d);
      f = g.length + 1;
      b = new ArrayBuffer(b + f);
      d = new Uint8Array(b);
      d[0] = a;
      d.set(g, 1);
      if (3 == this.type) f = r(this.payloadMessage.destinationName, k, d, f);
      else if (1 == this.type) {
        switch (this.mqttVersion) {
          case 3:
            d.set(B, f);
            f += B.length;
            break;
          case 4:
            (d.set(C, f), (f += C.length));
        }
        a = 0;
        this.cleanSession && (a = 2);
        void 0 !== this.willMessage && ((a = a | 4 | (this.willMessage.qos << 3)), this.willMessage.retained && (a |= 32));
        void 0 !== this.userName && (a |= 128);
        void 0 !== this.password && (a |= 64);
        d[f++] = a;
        f = q(this.keepAliveInterval, d, f);
      }
      void 0 !== this.messageIdentifier && (f = q(this.messageIdentifier, d, f));
      switch (this.type) {
        case 1:
          f = r(this.clientId, n(this.clientId), d, f);
          void 0 !== this.willMessage &&
            ((f = r(this.willMessage.destinationName, n(this.willMessage.destinationName), d, f)),
            (f = q(e.byteLength, d, f)),
            d.set(e, f),
            (f += e.byteLength));
          void 0 !== this.userName && (f = r(this.userName, n(this.userName), d, f));
          void 0 !== this.password && r(this.password, n(this.password), d, f);
          break;
        case 3:
          d.set(h, f);
          break;
        case 8:
          for (g = 0; g < this.topics.length; g++) ((f = r(this.topics[g], c[g], d, f)), (d[f++] = this.requestedQos[g]));
          break;
        case 10:
          for (g = 0; g < this.topics.length; g++) f = r(this.topics[g], c[g], d, f);
      }
      return b;
    };
    var G = function (a, b) {
        this._client = a;
        this._keepAliveInterval = 1e3 * b;
        this.isReset = !1;
        var c = new p(12).encode(),
          k = function (a) {
            return function () {
              return e.apply(a);
            };
          },
          e = function () {
            this.isReset
              ? ((this.isReset = !1),
                this._client._trace('Pinger.doPing', 'send PINGREQ'),
                this._client.socket.send(c),
                (this.timeout = setTimeout(k(this), this._keepAliveInterval)))
              : (this._client._trace('Pinger.doPing', 'Timed out'), this._client._disconnected(h.PING_TIMEOUT.code, f(h.PING_TIMEOUT)));
          };
        this.reset = function () {
          this.isReset = !0;
          clearTimeout(this.timeout);
          0 < this._keepAliveInterval && (this.timeout = setTimeout(k(this), this._keepAliveInterval));
        };
        this.cancel = function () {
          clearTimeout(this.timeout);
        };
      },
      v = function (a, b, c, f) {
        b || (b = 30);
        this.timeout = setTimeout(
          (function (a, b, c) {
            return function () {
              return a.apply(b, c);
            };
          })(c, a, f),
          1e3 * b
        );
        this.cancel = function () {
          clearTimeout(this.timeout);
        };
      },
      d = function (a, b, c, d, e) {
        if (!('WebSocket' in t && null !== t.WebSocket)) throw Error(f(h.UNSUPPORTED, ['WebSocket']));
        if (!('ArrayBuffer' in t && null !== t.ArrayBuffer)) throw Error(f(h.UNSUPPORTED, ['ArrayBuffer']));
        this._trace('Paho.Client', a, b, c, d, e);
        this.host = b;
        this.port = c;
        this.path = d;
        this.uri = a;
        this.clientId = e;
        this._wsuri = null;
        this._localKey = b + ':' + c + ('/mqtt' != d ? ':' + d : '') + ':' + e + ':';
        this._msg_queue = [];
        this._buffered_msg_queue = [];
        this._sentMessages = {};
        this._receivedMessages = {};
        this._notify_msg_sent = {};
        this._message_identifier = 1;
        this._sequence = 0;
        for (var g in s) (0 !== g.indexOf('Sent:' + this._localKey) && 0 !== g.indexOf('Received:' + this._localKey)) || this.restore(g);
      };
    d.prototype.host = null;
    d.prototype.port = null;
    d.prototype.path = null;
    d.prototype.uri = null;
    d.prototype.clientId = null;
    d.prototype.socket = null;
    d.prototype.connected = !1;
    d.prototype.maxMessageIdentifier = 65536;
    d.prototype.connectOptions = null;
    d.prototype.hostIndex = null;
    d.prototype.onConnected = null;
    d.prototype.onConnectionLost = null;
    d.prototype.onMessageDelivered = null;
    d.prototype.onMessageArrived = null;
    d.prototype.traceFunction = null;
    d.prototype._msg_queue = null;
    d.prototype._buffered_msg_queue = null;
    d.prototype._connectTimeout = null;
    d.prototype.sendPinger = null;
    d.prototype.receivePinger = null;
    d.prototype._reconnectInterval = 1;
    d.prototype._reconnecting = !1;
    d.prototype._reconnectTimeout = null;
    d.prototype.disconnectedPublishing = !1;
    d.prototype.disconnectedBufferSize = 5e3;
    d.prototype.receiveBuffer = null;
    d.prototype._traceBuffer = null;
    d.prototype._MAX_TRACE_ENTRIES = 100;
    d.prototype.connect = function (a) {
      var b = this._traceMask(a, 'password');
      this._trace('Client.connect', b, this.socket, this.connected);
      if (this.connected) throw Error(f(h.INVALID_STATE, ['already connected']));
      if (this.socket) throw Error(f(h.INVALID_STATE, ['already connected']));
      this._reconnecting && (this._reconnectTimeout.cancel(), (this._reconnectTimeout = null), (this._reconnecting = !1));
      this.connectOptions = a;
      this._reconnectInterval = 1;
      this._reconnecting = !1;
      a.uris ? ((this.hostIndex = 0), this._doConnect(a.uris[0])) : this._doConnect(this.uri);
    };
    d.prototype.subscribe = function (a, b) {
      this._trace('Client.subscribe', a, b);
      if (!this.connected) throw Error(f(h.INVALID_STATE, ['not connected']));
      var c = new p(8);
      c.topics = a.constructor === Array ? a : [a];
      void 0 === b.qos && (b.qos = 0);
      c.requestedQos = [];
      for (var d = 0; d < c.topics.length; d++) c.requestedQos[d] = b.qos;
      b.onSuccess &&
        (c.onSuccess = function (a) {
          b.onSuccess({ invocationContext: b.invocationContext, grantedQos: a });
        });
      b.onFailure &&
        (c.onFailure = function (a) {
          b.onFailure({ invocationContext: b.invocationContext, errorCode: a, errorMessage: f(a) });
        });
      b.timeout &&
        (c.timeOut = new v(this, b.timeout, b.onFailure, [
          { invocationContext: b.invocationContext, errorCode: h.SUBSCRIBE_TIMEOUT.code, errorMessage: f(h.SUBSCRIBE_TIMEOUT) }
        ]));
      this._requires_ack(c);
      this._schedule_message(c);
    };
    d.prototype.unsubscribe = function (a, b) {
      this._trace('Client.unsubscribe', a, b);
      if (!this.connected) throw Error(f(h.INVALID_STATE, ['not connected']));
      var c = new p(10);
      c.topics = a.constructor === Array ? a : [a];
      b.onSuccess &&
        (c.callback = function () {
          b.onSuccess({ invocationContext: b.invocationContext });
        });
      b.timeout &&
        (c.timeOut = new v(this, b.timeout, b.onFailure, [
          { invocationContext: b.invocationContext, errorCode: h.UNSUBSCRIBE_TIMEOUT.code, errorMessage: f(h.UNSUBSCRIBE_TIMEOUT) }
        ]));
      this._requires_ack(c);
      this._schedule_message(c);
    };
    d.prototype.send = function (a) {
      this._trace('Client.send', a);
      var b = new p(3);
      b.payloadMessage = a;
      if (this.connected)
        (0 < a.qos ? this._requires_ack(b) : this.onMessageDelivered && (this._notify_msg_sent[b] = this.onMessageDelivered(b.payloadMessage)),
          this._schedule_message(b));
      else if (this._reconnecting && this.disconnectedPublishing) {
        if (Object.keys(this._sentMessages).length + this._buffered_msg_queue.length > this.disconnectedBufferSize)
          throw Error(f(h.BUFFER_FULL, [this.disconnectedBufferSize]));
        0 < a.qos ? this._requires_ack(b) : ((b.sequence = ++this._sequence), this._buffered_msg_queue.unshift(b));
      } else throw Error(f(h.INVALID_STATE, ['not connected']));
    };
    d.prototype.disconnect = function () {
      this._trace('Client.disconnect');
      this._reconnecting && (this._reconnectTimeout.cancel(), (this._reconnectTimeout = null), (this._reconnecting = !1));
      if (!this.socket) throw Error(f(h.INVALID_STATE, ['not connecting or connected']));
      var a = new p(14);
      this._notify_msg_sent[a] = u(this._disconnected, this);
      this._schedule_message(a);
    };
    d.prototype.getTraceLog = function () {
      if (null !== this._traceBuffer) {
        this._trace('Client.getTraceLog', new Date());
        this._trace('Client.getTraceLog in flight messages', this._sentMessages.length);
        for (var a in this._sentMessages) this._trace('_sentMessages ', a, this._sentMessages[a]);
        for (a in this._receivedMessages) this._trace('_receivedMessages ', a, this._receivedMessages[a]);
        return this._traceBuffer;
      }
    };
    d.prototype.startTrace = function () {
      null === this._traceBuffer && (this._traceBuffer = []);
      this._trace('Client.startTrace', new Date(), '1.1.0-2018-11-22T11:35:36Z');
    };
    d.prototype.stopTrace = function () {
      delete this._traceBuffer;
    };
    d.prototype._doConnect = function (a) {
      this.connectOptions.useSSL && ((a = a.split(':')), (a[0] = 'wss'), (a = a.join(':')));
      this._wsuri = a;
      this.connected = !1;
      this.socket = 4 > this.connectOptions.mqttVersion ? new WebSocket(a, ['mqttv3.1']) : new WebSocket(a, ['mqtt']);
      this.socket.binaryType = 'arraybuffer';
      this.socket.onopen = u(this._on_socket_open, this);
      this.socket.onmessage = u(this._on_socket_message, this);
      this.socket.onerror = u(this._on_socket_error, this);
      this.socket.onclose = u(this._on_socket_close, this);
      this.sendPinger = new G(this, this.connectOptions.keepAliveInterval);
      this.receivePinger = new G(this, this.connectOptions.keepAliveInterval);
      this._connectTimeout && (this._connectTimeout.cancel(), (this._connectTimeout = null));
      this._connectTimeout = new v(this, this.connectOptions.timeout, this._disconnected, [h.CONNECT_TIMEOUT.code, f(h.CONNECT_TIMEOUT)]);
    };
    d.prototype._schedule_message = function (a) {
      this._msg_queue.unshift(a);
      this.connected && this._process_queue();
    };
    d.prototype.store = function (a, b) {
      var c = { type: b.type, messageIdentifier: b.messageIdentifier, version: 1 };
      switch (b.type) {
        case 3:
          b.pubRecReceived && (c.pubRecReceived = !0);
          c.payloadMessage = {};
          for (var d = '', e = b.payloadMessage.payloadBytes, g = 0; g < e.length; g++) d = 15 >= e[g] ? d + '0' + e[g].toString(16) : d + e[g].toString(16);
          c.payloadMessage.payloadHex = d;
          c.payloadMessage.qos = b.payloadMessage.qos;
          c.payloadMessage.destinationName = b.payloadMessage.destinationName;
          b.payloadMessage.duplicate && (c.payloadMessage.duplicate = !0);
          b.payloadMessage.retained && (c.payloadMessage.retained = !0);
          0 === a.indexOf('Sent:') && (void 0 === b.sequence && (b.sequence = ++this._sequence), (c.sequence = b.sequence));
          break;
        default:
          throw Error(f(h.INVALID_STORED_DATA, [a + this._localKey + b.messageIdentifier, c]));
      }
      s.setItem(a + this._localKey + b.messageIdentifier, JSON.stringify(c));
    };
    d.prototype.restore = function (a) {
      var b = s.getItem(a),
        c = JSON.parse(b),
        d = new p(c.type, c);
      switch (c.type) {
        case 3:
          for (var b = c.payloadMessage.payloadHex, e = new ArrayBuffer(b.length / 2), e = new Uint8Array(e), g = 0; 2 <= b.length; ) {
            var m = parseInt(b.substring(0, 2), 16),
              b = b.substring(2, b.length);
            e[g++] = m;
          }
          b = new w(e);
          b.qos = c.payloadMessage.qos;
          b.destinationName = c.payloadMessage.destinationName;
          c.payloadMessage.duplicate && (b.duplicate = !0);
          c.payloadMessage.retained && (b.retained = !0);
          d.payloadMessage = b;
          break;
        default:
          throw Error(f(h.INVALID_STORED_DATA, [a, b]));
      }
      0 === a.indexOf('Sent:' + this._localKey)
        ? ((d.payloadMessage.duplicate = !0), (this._sentMessages[d.messageIdentifier] = d))
        : 0 === a.indexOf('Received:' + this._localKey) && (this._receivedMessages[d.messageIdentifier] = d);
    };
    d.prototype._process_queue = function () {
      for (var a = null; (a = this._msg_queue.pop()); )
        (this._socket_send(a), this._notify_msg_sent[a] && (this._notify_msg_sent[a](), delete this._notify_msg_sent[a]));
    };
    d.prototype._requires_ack = function (a) {
      var b = Object.keys(this._sentMessages).length;
      if (b > this.maxMessageIdentifier) throw Error('Too many messages:' + b);
      for (; void 0 !== this._sentMessages[this._message_identifier]; ) this._message_identifier++;
      a.messageIdentifier = this._message_identifier;
      this._sentMessages[a.messageIdentifier] = a;
      3 === a.type && this.store('Sent:', a);
      this._message_identifier === this.maxMessageIdentifier && (this._message_identifier = 1);
    };
    d.prototype._on_socket_open = function () {
      var a = new p(1, this.connectOptions);
      a.clientId = this.clientId;
      this._socket_send(a);
    };
    d.prototype._on_socket_message = function (a) {
      this._trace('Client._on_socket_message', a.data);
      a = this._deframeMessages(a.data);
      for (var b = 0; b < a.length; b += 1) this._handleMessage(a[b]);
    };
    d.prototype._deframeMessages = function (a) {
      a = new Uint8Array(a);
      var b = [];
      if (this.receiveBuffer) {
        var c = new Uint8Array(this.receiveBuffer.length + a.length);
        c.set(this.receiveBuffer);
        c.set(a, this.receiveBuffer.length);
        a = c;
        delete this.receiveBuffer;
      }
      try {
        for (c = 0; c < a.length; ) {
          var d;
          a: {
            var e = a,
              g = c,
              m = g,
              n = e[g],
              l = n >> 4,
              z = n & 15,
              g = g + 1,
              x = void 0,
              D = 0,
              q = 1;
            do {
              if (g == e.length) {
                d = [null, m];
                break a;
              }
              x = e[g++];
              D += (x & 127) * q;
              q *= 128;
            } while (0 !== (x & 128));
            x = g + D;
            if (x > e.length) d = [null, m];
            else {
              var y = new p(l);
              switch (l) {
                case 2:
                  e[g++] & 1 && (y.sessionPresent = !0);
                  y.returnCode = e[g++];
                  break;
                case 3:
                  var m = (z >> 1) & 3,
                    s = 256 * e[g] + e[g + 1],
                    g = g + 2,
                    t = F(e, g, s),
                    g = g + s;
                  0 < m && ((y.messageIdentifier = 256 * e[g] + e[g + 1]), (g += 2));
                  var r = new w(e.subarray(g, x));
                  1 == (z & 1) && (r.retained = !0);
                  8 == (z & 8) && (r.duplicate = !0);
                  r.qos = m;
                  r.destinationName = t;
                  y.payloadMessage = r;
                  break;
                case 4:
                case 5:
                case 6:
                case 7:
                case 11:
                  y.messageIdentifier = 256 * e[g] + e[g + 1];
                  break;
                case 9:
                  ((y.messageIdentifier = 256 * e[g] + e[g + 1]), (g += 2), (y.returnCode = e.subarray(g, x)));
              }
              d = [y, x];
            }
          }
          var u = d[0],
            c = d[1];
          if (null !== u) b.push(u);
          else break;
        }
        c < a.length && (this.receiveBuffer = a.subarray(c));
      } catch (v) {
        d = 'undefined' == v.hasOwnProperty('stack') ? v.stack.toString() : 'No Error Stack Available';
        this._disconnected(h.INTERNAL_ERROR.code, f(h.INTERNAL_ERROR, [v.message, d]));
        return;
      }
      return b;
    };
    d.prototype._handleMessage = function (a) {
      this._trace('Client._handleMessage', a);
      try {
        switch (a.type) {
          case 2:
            this._connectTimeout.cancel();
            this._reconnectTimeout && this._reconnectTimeout.cancel();
            if (this.connectOptions.cleanSession) {
              for (var b in this._sentMessages) {
                var c = this._sentMessages[b];
                s.removeItem('Sent:' + this._localKey + c.messageIdentifier);
              }
              this._sentMessages = {};
              for (b in this._receivedMessages) {
                var d = this._receivedMessages[b];
                s.removeItem('Received:' + this._localKey + d.messageIdentifier);
              }
              this._receivedMessages = {};
            }
            if (0 === a.returnCode) ((this.connected = !0), this.connectOptions.uris && (this.hostIndex = this.connectOptions.uris.length));
            else {
              this._disconnected(h.CONNACK_RETURNCODE.code, f(h.CONNACK_RETURNCODE, [a.returnCode, H[a.returnCode]]));
              break;
            }
            a = [];
            for (var e in this._sentMessages) this._sentMessages.hasOwnProperty(e) && a.push(this._sentMessages[e]);
            if (0 < this._buffered_msg_queue.length)
              for (e = null; (e = this._buffered_msg_queue.pop()); )
                (a.push(e), this.onMessageDelivered && (this._notify_msg_sent[e] = this.onMessageDelivered(e.payloadMessage)));
            a = a.sort(function (a, b) {
              return a.sequence - b.sequence;
            });
            e = 0;
            for (var g = a.length; e < g; e++)
              if (((c = a[e]), 3 == c.type && c.pubRecReceived)) {
                var m = new p(6, { messageIdentifier: c.messageIdentifier });
                this._schedule_message(m);
              } else this._schedule_message(c);
            if (this.connectOptions.onSuccess) this.connectOptions.onSuccess({ invocationContext: this.connectOptions.invocationContext });
            c = !1;
            this._reconnecting && ((c = !0), (this._reconnectInterval = 1), (this._reconnecting = !1));
            this._connected(c, this._wsuri);
            this._process_queue();
            break;
          case 3:
            this._receivePublish(a);
            break;
          case 4:
            if ((c = this._sentMessages[a.messageIdentifier]))
              if ((delete this._sentMessages[a.messageIdentifier], s.removeItem('Sent:' + this._localKey + a.messageIdentifier), this.onMessageDelivered))
                this.onMessageDelivered(c.payloadMessage);
            break;
          case 5:
            if ((c = this._sentMessages[a.messageIdentifier]))
              ((c.pubRecReceived = !0), (m = new p(6, { messageIdentifier: a.messageIdentifier })), this.store('Sent:', c), this._schedule_message(m));
            break;
          case 6:
            d = this._receivedMessages[a.messageIdentifier];
            s.removeItem('Received:' + this._localKey + a.messageIdentifier);
            d && (this._receiveMessage(d), delete this._receivedMessages[a.messageIdentifier]);
            var n = new p(7, { messageIdentifier: a.messageIdentifier });
            this._schedule_message(n);
            break;
          case 7:
            c = this._sentMessages[a.messageIdentifier];
            delete this._sentMessages[a.messageIdentifier];
            s.removeItem('Sent:' + this._localKey + a.messageIdentifier);
            if (this.onMessageDelivered) this.onMessageDelivered(c.payloadMessage);
            break;
          case 9:
            if ((c = this._sentMessages[a.messageIdentifier])) {
              c.timeOut && c.timeOut.cancel();
              if (128 === a.returnCode[0]) {
                if (c.onFailure) c.onFailure(a.returnCode);
              } else if (c.onSuccess) c.onSuccess(a.returnCode);
              delete this._sentMessages[a.messageIdentifier];
            }
            break;
          case 11:
            if ((c = this._sentMessages[a.messageIdentifier]))
              (c.timeOut && c.timeOut.cancel(), c.callback && c.callback(), delete this._sentMessages[a.messageIdentifier]);
            break;
          case 13:
            this.sendPinger.reset();
            break;
          case 14:
            this._disconnected(h.INVALID_MQTT_MESSAGE_TYPE.code, f(h.INVALID_MQTT_MESSAGE_TYPE, [a.type]));
            break;
          default:
            this._disconnected(h.INVALID_MQTT_MESSAGE_TYPE.code, f(h.INVALID_MQTT_MESSAGE_TYPE, [a.type]));
        }
      } catch (l) {
        ((c = 'undefined' == l.hasOwnProperty('stack') ? l.stack.toString() : 'No Error Stack Available'),
          this._disconnected(h.INTERNAL_ERROR.code, f(h.INTERNAL_ERROR, [l.message, c])));
      }
    };
    d.prototype._on_socket_error = function (a) {
      this._reconnecting || this._disconnected(h.SOCKET_ERROR.code, f(h.SOCKET_ERROR, [a.data]));
    };
    d.prototype._on_socket_close = function () {
      this._reconnecting || this._disconnected(h.SOCKET_CLOSE.code, f(h.SOCKET_CLOSE));
    };
    d.prototype._socket_send = function (a) {
      if (1 == a.type) {
        var b = this._traceMask(a, 'password');
        this._trace('Client._socket_send', b);
      } else this._trace('Client._socket_send', a);
      this.socket.send(a.encode());
      this.sendPinger.reset();
    };
    d.prototype._receivePublish = function (a) {
      switch (a.payloadMessage.qos) {
        case 'undefined':
        case 0:
          this._receiveMessage(a);
          break;
        case 1:
          var b = new p(4, { messageIdentifier: a.messageIdentifier });
          this._schedule_message(b);
          this._receiveMessage(a);
          break;
        case 2:
          this._receivedMessages[a.messageIdentifier] = a;
          this.store('Received:', a);
          a = new p(5, { messageIdentifier: a.messageIdentifier });
          this._schedule_message(a);
          break;
        default:
          throw Error('Invaild qos\x3d' + a.payloadMessage.qos);
      }
    };
    d.prototype._receiveMessage = function (a) {
      if (this.onMessageArrived) this.onMessageArrived(a.payloadMessage);
    };
    d.prototype._connected = function (a, b) {
      if (this.onConnected) this.onConnected(a, b);
    };
    d.prototype._reconnect = function () {
      this._trace('Client._reconnect');
      this.connected ||
        ((this._reconnecting = !0),
        this.sendPinger.cancel(),
        this.receivePinger.cancel(),
        128 > this._reconnectInterval && (this._reconnectInterval *= 2),
        this.connectOptions.uris ? ((this.hostIndex = 0), this._doConnect(this.connectOptions.uris[0])) : this._doConnect(this.uri));
    };
    d.prototype._disconnected = function (a, b) {
      this._trace('Client._disconnected', a, b);
      if (void 0 !== a && this._reconnecting) this._reconnectTimeout = new v(this, this._reconnectInterval, this._reconnect);
      else if (
        (this.sendPinger.cancel(),
        this.receivePinger.cancel(),
        this._connectTimeout && (this._connectTimeout.cancel(), (this._connectTimeout = null)),
        (this._msg_queue = []),
        (this._buffered_msg_queue = []),
        (this._notify_msg_sent = {}),
        this.socket &&
          ((this.socket.onopen = null),
          (this.socket.onmessage = null),
          (this.socket.onerror = null),
          (this.socket.onclose = null),
          1 === this.socket.readyState && this.socket.close(),
          delete this.socket),
        this.connectOptions.uris && this.hostIndex < this.connectOptions.uris.length - 1)
      )
        (this.hostIndex++, this._doConnect(this.connectOptions.uris[this.hostIndex]));
      else if ((void 0 === a && ((a = h.OK.code), (b = f(h.OK))), this.connected)) {
        this.connected = !1;
        if (this.onConnectionLost) this.onConnectionLost({ errorCode: a, errorMessage: b, reconnect: this.connectOptions.reconnect, uri: this._wsuri });
        a !== h.OK.code && this.connectOptions.reconnect && ((this._reconnectInterval = 1), this._reconnect());
      } else if (4 === this.connectOptions.mqttVersion && !1 === this.connectOptions.mqttVersionExplicit)
        (this._trace('Failed to connect V4, dropping back to V3'),
          (this.connectOptions.mqttVersion = 3),
          this.connectOptions.uris ? ((this.hostIndex = 0), this._doConnect(this.connectOptions.uris[0])) : this._doConnect(this.uri));
      else if (this.connectOptions.onFailure)
        this.connectOptions.onFailure({ invocationContext: this.connectOptions.invocationContext, errorCode: a, errorMessage: b });
    };
    d.prototype._trace = function () {
      if (this.traceFunction) {
        var a = Array.prototype.slice.call(arguments),
          b;
        for (b in a) 'undefined' !== typeof a[b] && a.splice(b, 1, JSON.stringify(a[b]));
        b = a.join('');
        this.traceFunction({ severity: 'Debug', message: b });
      }
      if (null !== this._traceBuffer)
        for (b = 0, a = arguments.length; b < a; b++)
          (this._traceBuffer.length == this._MAX_TRACE_ENTRIES && this._traceBuffer.shift(),
            0 === b
              ? this._traceBuffer.push(arguments[b])
              : 'undefined' === typeof arguments[b]
                ? this._traceBuffer.push(arguments[b])
                : this._traceBuffer.push('  ' + JSON.stringify(arguments[b])));
    };
    d.prototype._traceMask = function (a, b) {
      var c = {},
        d;
      for (d in a) a.hasOwnProperty(d) && (c[d] = d == b ? '******' : a[d]);
      return c;
    };
    var w = function (a) {
      var b;
      if ('string' === typeof a || a instanceof ArrayBuffer || (ArrayBuffer.isView(a) && !(a instanceof DataView))) b = a;
      else throw f(h.INVALID_ARGUMENT, [a, 'newPayload']);
      var c,
        d = 0,
        e = !1,
        g = !1;
      Object.defineProperties(this, {
        payloadString: {
          enumerable: !0,
          get: function () {
            return 'string' === typeof b ? b : F(b, 0, b.length);
          }
        },
        payloadBytes: {
          enumerable: !0,
          get: function () {
            if ('string' === typeof b) {
              var a = new ArrayBuffer(n(b)),
                a = new Uint8Array(a);
              E(b, a, 0);
              return a;
            }
            return b;
          }
        },
        destinationName: {
          enumerable: !0,
          get: function () {
            return c;
          },
          set: function (a) {
            if ('string' === typeof a) c = a;
            else throw Error(f(h.INVALID_ARGUMENT, [a, 'newDestinationName']));
          }
        },
        qos: {
          enumerable: !0,
          get: function () {
            return d;
          },
          set: function (a) {
            if (0 === a || 1 === a || 2 === a) d = a;
            else throw Error('Invalid argument:' + a);
          }
        },
        retained: {
          enumerable: !0,
          get: function () {
            return e;
          },
          set: function (a) {
            if ('boolean' === typeof a) e = a;
            else throw Error(f(h.INVALID_ARGUMENT, [a, 'newRetained']));
          }
        },
        topic: {
          enumerable: !0,
          get: function () {
            return c;
          },
          set: function (a) {
            c = a;
          }
        },
        duplicate: {
          enumerable: !0,
          get: function () {
            return g;
          },
          set: function (a) {
            g = a;
          }
        }
      });
    };
    return {
      Client: function (a, b, c, k) {
        var e;
        if ('string' !== typeof a) throw Error(f(h.INVALID_TYPE, [typeof a, 'host']));
        if (2 == arguments.length) {
          k = b;
          e = a;
          var g = e.match(/^(wss?):\/\/((\[(.+)\])|([^\/]+?))(:(\d+))?(\/.*)$/);
          if (g) ((a = g[4] || g[2]), (b = parseInt(g[7])), (c = g[8]));
          else throw Error(f(h.INVALID_ARGUMENT, [a, 'host']));
        } else {
          3 == arguments.length && ((k = c), (c = '/mqtt'));
          if ('number' !== typeof b || 0 > b) throw Error(f(h.INVALID_TYPE, [typeof b, 'port']));
          if ('string' !== typeof c) throw Error(f(h.INVALID_TYPE, [typeof c, 'path']));
          e = 'ws://' + (-1 !== a.indexOf(':') && '[' !== a.slice(0, 1) && ']' !== a.slice(-1) ? '[' + a + ']' : a) + ':' + b + c;
        }
        for (var m = (g = 0); m < k.length; m++) {
          var n = k.charCodeAt(m);
          55296 <= n && 56319 >= n && m++;
          g++;
        }
        if ('string' !== typeof k || 65535 < g) throw Error(f(h.INVALID_ARGUMENT, [k, 'clientId']));
        var l = new d(e, a, b, c, k);
        Object.defineProperties(this, {
          host: {
            get: function () {
              return a;
            },
            set: function () {
              throw Error(f(h.UNSUPPORTED_OPERATION));
            }
          },
          port: {
            get: function () {
              return b;
            },
            set: function () {
              throw Error(f(h.UNSUPPORTED_OPERATION));
            }
          },
          path: {
            get: function () {
              return c;
            },
            set: function () {
              throw Error(f(h.UNSUPPORTED_OPERATION));
            }
          },
          uri: {
            get: function () {
              return e;
            },
            set: function () {
              throw Error(f(h.UNSUPPORTED_OPERATION));
            }
          },
          clientId: {
            get: function () {
              return l.clientId;
            },
            set: function () {
              throw Error(f(h.UNSUPPORTED_OPERATION));
            }
          },
          onConnected: {
            get: function () {
              return l.onConnected;
            },
            set: function (a) {
              if ('function' === typeof a) l.onConnected = a;
              else throw Error(f(h.INVALID_TYPE, [typeof a, 'onConnected']));
            }
          },
          disconnectedPublishing: {
            get: function () {
              return l.disconnectedPublishing;
            },
            set: function (a) {
              l.disconnectedPublishing = a;
            }
          },
          disconnectedBufferSize: {
            get: function () {
              return l.disconnectedBufferSize;
            },
            set: function (a) {
              l.disconnectedBufferSize = a;
            }
          },
          onConnectionLost: {
            get: function () {
              return l.onConnectionLost;
            },
            set: function (a) {
              if ('function' === typeof a) l.onConnectionLost = a;
              else throw Error(f(h.INVALID_TYPE, [typeof a, 'onConnectionLost']));
            }
          },
          onMessageDelivered: {
            get: function () {
              return l.onMessageDelivered;
            },
            set: function (a) {
              if ('function' === typeof a) l.onMessageDelivered = a;
              else throw Error(f(h.INVALID_TYPE, [typeof a, 'onMessageDelivered']));
            }
          },
          onMessageArrived: {
            get: function () {
              return l.onMessageArrived;
            },
            set: function (a) {
              if ('function' === typeof a) l.onMessageArrived = a;
              else throw Error(f(h.INVALID_TYPE, [typeof a, 'onMessageArrived']));
            }
          },
          trace: {
            get: function () {
              return l.traceFunction;
            },
            set: function (a) {
              if ('function' === typeof a) l.traceFunction = a;
              else throw Error(f(h.INVALID_TYPE, [typeof a, 'onTrace']));
            }
          }
        });
        this.connect = function (a) {
          a = a || {};
          A(a, {
            timeout: 'number',
            userName: 'string',
            password: 'string',
            willMessage: 'object',
            keepAliveInterval: 'number',
            cleanSession: 'boolean',
            useSSL: 'boolean',
            invocationContext: 'object',
            onSuccess: 'function',
            onFailure: 'function',
            hosts: 'object',
            ports: 'object',
            reconnect: 'boolean',
            mqttVersion: 'number',
            mqttVersionExplicit: 'boolean',
            uris: 'object'
          });
          void 0 === a.keepAliveInterval && (a.keepAliveInterval = 60);
          if (4 < a.mqttVersion || 3 > a.mqttVersion) throw Error(f(h.INVALID_ARGUMENT, [a.mqttVersion, 'connectOptions.mqttVersion']));
          void 0 === a.mqttVersion ? ((a.mqttVersionExplicit = !1), (a.mqttVersion = 4)) : (a.mqttVersionExplicit = !0);
          if (void 0 !== a.password && void 0 === a.userName) throw Error(f(h.INVALID_ARGUMENT, [a.password, 'connectOptions.password']));
          if (a.willMessage) {
            if (!(a.willMessage instanceof w)) throw Error(f(h.INVALID_TYPE, [a.willMessage, 'connectOptions.willMessage']));
            a.willMessage.stringPayload = null;
            if ('undefined' === typeof a.willMessage.destinationName)
              throw Error(f(h.INVALID_TYPE, [typeof a.willMessage.destinationName, 'connectOptions.willMessage.destinationName']));
          }
          'undefined' === typeof a.cleanSession && (a.cleanSession = !0);
          if (a.hosts) {
            if (!(a.hosts instanceof Array)) throw Error(f(h.INVALID_ARGUMENT, [a.hosts, 'connectOptions.hosts']));
            if (1 > a.hosts.length) throw Error(f(h.INVALID_ARGUMENT, [a.hosts, 'connectOptions.hosts']));
            for (var b = !1, d = 0; d < a.hosts.length; d++) {
              if ('string' !== typeof a.hosts[d]) throw Error(f(h.INVALID_TYPE, [typeof a.hosts[d], 'connectOptions.hosts[' + d + ']']));
              if (/^(wss?):\/\/((\[(.+)\])|([^\/]+?))(:(\d+))?(\/.*)$/.test(a.hosts[d]))
                if (0 === d) b = !0;
                else {
                  if (!b) throw Error(f(h.INVALID_ARGUMENT, [a.hosts[d], 'connectOptions.hosts[' + d + ']']));
                }
              else if (b) throw Error(f(h.INVALID_ARGUMENT, [a.hosts[d], 'connectOptions.hosts[' + d + ']']));
            }
            if (b) a.uris = a.hosts;
            else {
              if (!a.ports) throw Error(f(h.INVALID_ARGUMENT, [a.ports, 'connectOptions.ports']));
              if (!(a.ports instanceof Array)) throw Error(f(h.INVALID_ARGUMENT, [a.ports, 'connectOptions.ports']));
              if (a.hosts.length !== a.ports.length) throw Error(f(h.INVALID_ARGUMENT, [a.ports, 'connectOptions.ports']));
              a.uris = [];
              for (d = 0; d < a.hosts.length; d++) {
                if ('number' !== typeof a.ports[d] || 0 > a.ports[d]) throw Error(f(h.INVALID_TYPE, [typeof a.ports[d], 'connectOptions.ports[' + d + ']']));
                var b = a.hosts[d],
                  g = a.ports[d];
                e = 'ws://' + (-1 !== b.indexOf(':') ? '[' + b + ']' : b) + ':' + g + c;
                a.uris.push(e);
              }
            }
          }
          l.connect(a);
        };
        this.subscribe = function (a, b) {
          if ('string' !== typeof a && a.constructor !== Array) throw Error('Invalid argument:' + a);
          b = b || {};
          A(b, { qos: 'number', invocationContext: 'object', onSuccess: 'function', onFailure: 'function', timeout: 'number' });
          if (b.timeout && !b.onFailure) throw Error('subscribeOptions.timeout specified with no onFailure callback.');
          if ('undefined' !== typeof b.qos && 0 !== b.qos && 1 !== b.qos && 2 !== b.qos) throw Error(f(h.INVALID_ARGUMENT, [b.qos, 'subscribeOptions.qos']));
          l.subscribe(a, b);
        };
        this.unsubscribe = function (a, b) {
          if ('string' !== typeof a && a.constructor !== Array) throw Error('Invalid argument:' + a);
          b = b || {};
          A(b, { invocationContext: 'object', onSuccess: 'function', onFailure: 'function', timeout: 'number' });
          if (b.timeout && !b.onFailure) throw Error('unsubscribeOptions.timeout specified with no onFailure callback.');
          l.unsubscribe(a, b);
        };
        this.send = function (a, b, c, d) {
          var e;
          if (0 === arguments.length) throw Error('Invalid argument.length');
          if (1 == arguments.length) {
            if (!(a instanceof w) && 'string' !== typeof a) throw Error('Invalid argument:' + typeof a);
            e = a;
            if ('undefined' === typeof e.destinationName) throw Error(f(h.INVALID_ARGUMENT, [e.destinationName, 'Message.destinationName']));
          } else ((e = new w(b)), (e.destinationName = a), 3 <= arguments.length && (e.qos = c), 4 <= arguments.length && (e.retained = d));
          l.send(e);
        };
        this.publish = function (a, b, c, d) {
          var e;
          if (0 === arguments.length) throw Error('Invalid argument.length');
          if (1 == arguments.length) {
            if (!(a instanceof w) && 'string' !== typeof a) throw Error('Invalid argument:' + typeof a);
            e = a;
            if ('undefined' === typeof e.destinationName) throw Error(f(h.INVALID_ARGUMENT, [e.destinationName, 'Message.destinationName']));
          } else ((e = new w(b)), (e.destinationName = a), 3 <= arguments.length && (e.qos = c), 4 <= arguments.length && (e.retained = d));
          l.send(e);
        };
        this.disconnect = function () {
          l.disconnect();
        };
        this.getTraceLog = function () {
          return l.getTraceLog();
        };
        this.startTrace = function () {
          l.startTrace();
        };
        this.stopTrace = function () {
          l.stopTrace();
        };
        this.isConnected = function () {
          return l.connected;
        };
      },
      Message: w
    };
  })('undefined' !== typeof global ? global : 'undefined' !== typeof self ? self : 'undefined' !== typeof window ? window : {});
}); /*! Hammer.JS - v2.0.7 - 2016-04-22
 * http://hammerjs.github.io/
 *
 * Copyright (c) 2016 Jorik Tangelder;
 * Licensed under the MIT license */
!(function (a, b, c, d) {
  'use strict';
  function e(a, b, c) {
    return setTimeout(j(a, c), b);
  }
  function f(a, b, c) {
    return Array.isArray(a) ? (g(a, c[b], c), !0) : !1;
  }
  function g(a, b, c) {
    var e;
    if (a)
      if (a.forEach) a.forEach(b, c);
      else if (a.length !== d) for (e = 0; e < a.length; ) (b.call(c, a[e], e, a), e++);
      else for (e in a) a.hasOwnProperty(e) && b.call(c, a[e], e, a);
  }
  function h(b, c, d) {
    var e = 'DEPRECATED METHOD: ' + c + '\n' + d + ' AT \n';
    return function () {
      var c = new Error('get-stack-trace'),
        d =
          c && c.stack
            ? c.stack
                .replace(/^[^\(]+?[\n$]/gm, '')
                .replace(/^\s+at\s+/gm, '')
                .replace(/^Object.<anonymous>\s*\(/gm, '{anonymous}()@')
            : 'Unknown Stack Trace',
        f = a.console && (a.console.warn || a.console.log);
      return (f && f.call(a.console, e, d), b.apply(this, arguments));
    };
  }
  function i(a, b, c) {
    var d,
      e = b.prototype;
    ((d = a.prototype = Object.create(e)), (d.constructor = a), (d._super = e), c && la(d, c));
  }
  function j(a, b) {
    return function () {
      return a.apply(b, arguments);
    };
  }
  function k(a, b) {
    return typeof a == oa ? a.apply(b ? b[0] || d : d, b) : a;
  }
  function l(a, b) {
    return a === d ? b : a;
  }
  function m(a, b, c) {
    g(q(b), function (b) {
      a.addEventListener(b, c, !1);
    });
  }
  function n(a, b, c) {
    g(q(b), function (b) {
      a.removeEventListener(b, c, !1);
    });
  }
  function o(a, b) {
    for (; a; ) {
      if (a == b) return !0;
      a = a.parentNode;
    }
    return !1;
  }
  function p(a, b) {
    return a.indexOf(b) > -1;
  }
  function q(a) {
    return a.trim().split(/\s+/g);
  }
  function r(a, b, c) {
    if (a.indexOf && !c) return a.indexOf(b);
    for (var d = 0; d < a.length; ) {
      if ((c && a[d][c] == b) || (!c && a[d] === b)) return d;
      d++;
    }
    return -1;
  }
  function s(a) {
    return Array.prototype.slice.call(a, 0);
  }
  function t(a, b, c) {
    for (var d = [], e = [], f = 0; f < a.length; ) {
      var g = b ? a[f][b] : a[f];
      (r(e, g) < 0 && d.push(a[f]), (e[f] = g), f++);
    }
    return (
      c &&
        (d = b
          ? d.sort(function (a, c) {
              return a[b] > c[b];
            })
          : d.sort()),
      d
    );
  }
  function u(a, b) {
    for (var c, e, f = b[0].toUpperCase() + b.slice(1), g = 0; g < ma.length; ) {
      if (((c = ma[g]), (e = c ? c + f : b), e in a)) return e;
      g++;
    }
    return d;
  }
  function v() {
    return ua++;
  }
  function w(b) {
    var c = b.ownerDocument || b;
    return c.defaultView || c.parentWindow || a;
  }
  function x(a, b) {
    var c = this;
    ((this.manager = a),
      (this.callback = b),
      (this.element = a.element),
      (this.target = a.options.inputTarget),
      (this.domHandler = function (b) {
        k(a.options.enable, [a]) && c.handler(b);
      }),
      this.init());
  }
  function y(a) {
    var b,
      c = a.options.inputClass;
    return new (b = c ? c : xa ? M : ya ? P : wa ? R : L)(a, z);
  }
  function z(a, b, c) {
    var d = c.pointers.length,
      e = c.changedPointers.length,
      f = b & Ea && d - e === 0,
      g = b & (Ga | Ha) && d - e === 0;
    ((c.isFirst = !!f),
      (c.isFinal = !!g),
      f && (a.session = {}),
      (c.eventType = b),
      A(a, c),
      a.emit('hammer.input', c),
      a.recognize(c),
      (a.session.prevInput = c));
  }
  function A(a, b) {
    var c = a.session,
      d = b.pointers,
      e = d.length;
    (c.firstInput || (c.firstInput = D(b)), e > 1 && !c.firstMultiple ? (c.firstMultiple = D(b)) : 1 === e && (c.firstMultiple = !1));
    var f = c.firstInput,
      g = c.firstMultiple,
      h = g ? g.center : f.center,
      i = (b.center = E(d));
    ((b.timeStamp = ra()),
      (b.deltaTime = b.timeStamp - f.timeStamp),
      (b.angle = I(h, i)),
      (b.distance = H(h, i)),
      B(c, b),
      (b.offsetDirection = G(b.deltaX, b.deltaY)));
    var j = F(b.deltaTime, b.deltaX, b.deltaY);
    ((b.overallVelocityX = j.x),
      (b.overallVelocityY = j.y),
      (b.overallVelocity = qa(j.x) > qa(j.y) ? j.x : j.y),
      (b.scale = g ? K(g.pointers, d) : 1),
      (b.rotation = g ? J(g.pointers, d) : 0),
      (b.maxPointers = c.prevInput ? (b.pointers.length > c.prevInput.maxPointers ? b.pointers.length : c.prevInput.maxPointers) : b.pointers.length),
      C(c, b));
    var k = a.element;
    (o(b.srcEvent.target, k) && (k = b.srcEvent.target), (b.target = k));
  }
  function B(a, b) {
    var c = b.center,
      d = a.offsetDelta || {},
      e = a.prevDelta || {},
      f = a.prevInput || {};
    ((b.eventType !== Ea && f.eventType !== Ga) || ((e = a.prevDelta = { x: f.deltaX || 0, y: f.deltaY || 0 }), (d = a.offsetDelta = { x: c.x, y: c.y })),
      (b.deltaX = e.x + (c.x - d.x)),
      (b.deltaY = e.y + (c.y - d.y)));
  }
  function C(a, b) {
    var c,
      e,
      f,
      g,
      h = a.lastInterval || b,
      i = b.timeStamp - h.timeStamp;
    if (b.eventType != Ha && (i > Da || h.velocity === d)) {
      var j = b.deltaX - h.deltaX,
        k = b.deltaY - h.deltaY,
        l = F(i, j, k);
      ((e = l.x), (f = l.y), (c = qa(l.x) > qa(l.y) ? l.x : l.y), (g = G(j, k)), (a.lastInterval = b));
    } else ((c = h.velocity), (e = h.velocityX), (f = h.velocityY), (g = h.direction));
    ((b.velocity = c), (b.velocityX = e), (b.velocityY = f), (b.direction = g));
  }
  function D(a) {
    for (var b = [], c = 0; c < a.pointers.length; ) ((b[c] = { clientX: pa(a.pointers[c].clientX), clientY: pa(a.pointers[c].clientY) }), c++);
    return { timeStamp: ra(), pointers: b, center: E(b), deltaX: a.deltaX, deltaY: a.deltaY };
  }
  function E(a) {
    var b = a.length;
    if (1 === b) return { x: pa(a[0].clientX), y: pa(a[0].clientY) };
    for (var c = 0, d = 0, e = 0; b > e; ) ((c += a[e].clientX), (d += a[e].clientY), e++);
    return { x: pa(c / b), y: pa(d / b) };
  }
  function F(a, b, c) {
    return { x: b / a || 0, y: c / a || 0 };
  }
  function G(a, b) {
    return a === b ? Ia : qa(a) >= qa(b) ? (0 > a ? Ja : Ka) : 0 > b ? La : Ma;
  }
  function H(a, b, c) {
    c || (c = Qa);
    var d = b[c[0]] - a[c[0]],
      e = b[c[1]] - a[c[1]];
    return Math.sqrt(d * d + e * e);
  }
  function I(a, b, c) {
    c || (c = Qa);
    var d = b[c[0]] - a[c[0]],
      e = b[c[1]] - a[c[1]];
    return (180 * Math.atan2(e, d)) / Math.PI;
  }
  function J(a, b) {
    return I(b[1], b[0], Ra) + I(a[1], a[0], Ra);
  }
  function K(a, b) {
    return H(b[0], b[1], Ra) / H(a[0], a[1], Ra);
  }
  function L() {
    ((this.evEl = Ta), (this.evWin = Ua), (this.pressed = !1), x.apply(this, arguments));
  }
  function M() {
    ((this.evEl = Xa), (this.evWin = Ya), x.apply(this, arguments), (this.store = this.manager.session.pointerEvents = []));
  }
  function N() {
    ((this.evTarget = $a), (this.evWin = _a), (this.started = !1), x.apply(this, arguments));
  }
  function O(a, b) {
    var c = s(a.touches),
      d = s(a.changedTouches);
    return (b & (Ga | Ha) && (c = t(c.concat(d), 'identifier', !0)), [c, d]);
  }
  function P() {
    ((this.evTarget = bb), (this.targetIds = {}), x.apply(this, arguments));
  }
  function Q(a, b) {
    var c = s(a.touches),
      d = this.targetIds;
    if (b & (Ea | Fa) && 1 === c.length) return ((d[c[0].identifier] = !0), [c, c]);
    var e,
      f,
      g = s(a.changedTouches),
      h = [],
      i = this.target;
    if (
      ((f = c.filter(function (a) {
        return o(a.target, i);
      })),
      b === Ea)
    )
      for (e = 0; e < f.length; ) ((d[f[e].identifier] = !0), e++);
    for (e = 0; e < g.length; ) (d[g[e].identifier] && h.push(g[e]), b & (Ga | Ha) && delete d[g[e].identifier], e++);
    return h.length ? [t(f.concat(h), 'identifier', !0), h] : void 0;
  }
  function R() {
    x.apply(this, arguments);
    var a = j(this.handler, this);
    ((this.touch = new P(this.manager, a)), (this.mouse = new L(this.manager, a)), (this.primaryTouch = null), (this.lastTouches = []));
  }
  function S(a, b) {
    a & Ea ? ((this.primaryTouch = b.changedPointers[0].identifier), T.call(this, b)) : a & (Ga | Ha) && T.call(this, b);
  }
  function T(a) {
    var b = a.changedPointers[0];
    if (b.identifier === this.primaryTouch) {
      var c = { x: b.clientX, y: b.clientY };
      this.lastTouches.push(c);
      var d = this.lastTouches,
        e = function () {
          var a = d.indexOf(c);
          a > -1 && d.splice(a, 1);
        };
      setTimeout(e, cb);
    }
  }
  function U(a) {
    for (var b = a.srcEvent.clientX, c = a.srcEvent.clientY, d = 0; d < this.lastTouches.length; d++) {
      var e = this.lastTouches[d],
        f = Math.abs(b - e.x),
        g = Math.abs(c - e.y);
      if (db >= f && db >= g) return !0;
    }
    return !1;
  }
  function V(a, b) {
    ((this.manager = a), this.set(b));
  }
  function W(a) {
    if (p(a, jb)) return jb;
    var b = p(a, kb),
      c = p(a, lb);
    return b && c ? jb : b || c ? (b ? kb : lb) : p(a, ib) ? ib : hb;
  }
  function X() {
    if (!fb) return !1;
    var b = {},
      c = a.CSS && a.CSS.supports;
    return (
      ['auto', 'manipulation', 'pan-y', 'pan-x', 'pan-x pan-y', 'none'].forEach(function (d) {
        b[d] = c ? a.CSS.supports('touch-action', d) : !0;
      }),
      b
    );
  }
  function Y(a) {
    ((this.options = la({}, this.defaults, a || {})),
      (this.id = v()),
      (this.manager = null),
      (this.options.enable = l(this.options.enable, !0)),
      (this.state = nb),
      (this.simultaneous = {}),
      (this.requireFail = []));
  }
  function Z(a) {
    return a & sb ? 'cancel' : a & qb ? 'end' : a & pb ? 'move' : a & ob ? 'start' : '';
  }
  function $(a) {
    return a == Ma ? 'down' : a == La ? 'up' : a == Ja ? 'left' : a == Ka ? 'right' : '';
  }
  function _(a, b) {
    var c = b.manager;
    return c ? c.get(a) : a;
  }
  function aa() {
    Y.apply(this, arguments);
  }
  function ba() {
    (aa.apply(this, arguments), (this.pX = null), (this.pY = null));
  }
  function ca() {
    aa.apply(this, arguments);
  }
  function da() {
    (Y.apply(this, arguments), (this._timer = null), (this._input = null));
  }
  function ea() {
    aa.apply(this, arguments);
  }
  function fa() {
    aa.apply(this, arguments);
  }
  function ga() {
    (Y.apply(this, arguments), (this.pTime = !1), (this.pCenter = !1), (this._timer = null), (this._input = null), (this.count = 0));
  }
  function ha(a, b) {
    return ((b = b || {}), (b.recognizers = l(b.recognizers, ha.defaults.preset)), new ia(a, b));
  }
  function ia(a, b) {
    ((this.options = la({}, ha.defaults, b || {})),
      (this.options.inputTarget = this.options.inputTarget || a),
      (this.handlers = {}),
      (this.session = {}),
      (this.recognizers = []),
      (this.oldCssProps = {}),
      (this.element = a),
      (this.input = y(this)),
      (this.touchAction = new V(this, this.options.touchAction)),
      ja(this, !0),
      g(
        this.options.recognizers,
        function (a) {
          var b = this.add(new a[0](a[1]));
          (a[2] && b.recognizeWith(a[2]), a[3] && b.requireFailure(a[3]));
        },
        this
      ));
  }
  function ja(a, b) {
    var c = a.element;
    if (c.style) {
      var d;
      (g(a.options.cssProps, function (e, f) {
        ((d = u(c.style, f)), b ? ((a.oldCssProps[d] = c.style[d]), (c.style[d] = e)) : (c.style[d] = a.oldCssProps[d] || ''));
      }),
        b || (a.oldCssProps = {}));
    }
  }
  function ka(a, c) {
    var d = b.createEvent('Event');
    (d.initEvent(a, !0, !0), (d.gesture = c), c.target.dispatchEvent(d));
  }
  var la,
    ma = ['', 'webkit', 'Moz', 'MS', 'ms', 'o'],
    na = b.createElement('div'),
    oa = 'function',
    pa = Math.round,
    qa = Math.abs,
    ra = Date.now;
  la =
    'function' != typeof Object.assign
      ? function (a) {
          if (a === d || null === a) throw new TypeError('Cannot convert undefined or null to object');
          for (var b = Object(a), c = 1; c < arguments.length; c++) {
            var e = arguments[c];
            if (e !== d && null !== e) for (var f in e) e.hasOwnProperty(f) && (b[f] = e[f]);
          }
          return b;
        }
      : Object.assign;
  var sa = h(
      function (a, b, c) {
        for (var e = Object.keys(b), f = 0; f < e.length; ) ((!c || (c && a[e[f]] === d)) && (a[e[f]] = b[e[f]]), f++);
        return a;
      },
      'extend',
      'Use `assign`.'
    ),
    ta = h(
      function (a, b) {
        return sa(a, b, !0);
      },
      'merge',
      'Use `assign`.'
    ),
    ua = 1,
    va = /mobile|tablet|ip(ad|hone|od)|android/i,
    wa = 'ontouchstart' in a,
    xa = u(a, 'PointerEvent') !== d,
    ya = wa && va.test(navigator.userAgent),
    za = 'touch',
    Aa = 'pen',
    Ba = 'mouse',
    Ca = 'kinect',
    Da = 25,
    Ea = 1,
    Fa = 2,
    Ga = 4,
    Ha = 8,
    Ia = 1,
    Ja = 2,
    Ka = 4,
    La = 8,
    Ma = 16,
    Na = Ja | Ka,
    Oa = La | Ma,
    Pa = Na | Oa,
    Qa = ['x', 'y'],
    Ra = ['clientX', 'clientY'];
  x.prototype = {
    handler: function () {},
    init: function () {
      (this.evEl && m(this.element, this.evEl, this.domHandler),
        this.evTarget && m(this.target, this.evTarget, this.domHandler),
        this.evWin && m(w(this.element), this.evWin, this.domHandler));
    },
    destroy: function () {
      (this.evEl && n(this.element, this.evEl, this.domHandler),
        this.evTarget && n(this.target, this.evTarget, this.domHandler),
        this.evWin && n(w(this.element), this.evWin, this.domHandler));
    }
  };
  var Sa = { mousedown: Ea, mousemove: Fa, mouseup: Ga },
    Ta = 'mousedown',
    Ua = 'mousemove mouseup';
  i(L, x, {
    handler: function (a) {
      var b = Sa[a.type];
      (b & Ea && 0 === a.button && (this.pressed = !0),
        b & Fa && 1 !== a.which && (b = Ga),
        this.pressed && (b & Ga && (this.pressed = !1), this.callback(this.manager, b, { pointers: [a], changedPointers: [a], pointerType: Ba, srcEvent: a })));
    }
  });
  var Va = { pointerdown: Ea, pointermove: Fa, pointerup: Ga, pointercancel: Ha, pointerout: Ha },
    Wa = { 2: za, 3: Aa, 4: Ba, 5: Ca },
    Xa = 'pointerdown',
    Ya = 'pointermove pointerup pointercancel';
  (a.MSPointerEvent && !a.PointerEvent && ((Xa = 'MSPointerDown'), (Ya = 'MSPointerMove MSPointerUp MSPointerCancel')),
    i(M, x, {
      handler: function (a) {
        var b = this.store,
          c = !1,
          d = a.type.toLowerCase().replace('ms', ''),
          e = Va[d],
          f = Wa[a.pointerType] || a.pointerType,
          g = f == za,
          h = r(b, a.pointerId, 'pointerId');
        (e & Ea && (0 === a.button || g) ? 0 > h && (b.push(a), (h = b.length - 1)) : e & (Ga | Ha) && (c = !0),
          0 > h || ((b[h] = a), this.callback(this.manager, e, { pointers: b, changedPointers: [a], pointerType: f, srcEvent: a }), c && b.splice(h, 1)));
      }
    }));
  var Za = { touchstart: Ea, touchmove: Fa, touchend: Ga, touchcancel: Ha },
    $a = 'touchstart',
    _a = 'touchstart touchmove touchend touchcancel';
  i(N, x, {
    handler: function (a) {
      var b = Za[a.type];
      if ((b === Ea && (this.started = !0), this.started)) {
        var c = O.call(this, a, b);
        (b & (Ga | Ha) && c[0].length - c[1].length === 0 && (this.started = !1),
          this.callback(this.manager, b, { pointers: c[0], changedPointers: c[1], pointerType: za, srcEvent: a }));
      }
    }
  });
  var ab = { touchstart: Ea, touchmove: Fa, touchend: Ga, touchcancel: Ha },
    bb = 'touchstart touchmove touchend touchcancel';
  i(P, x, {
    handler: function (a) {
      var b = ab[a.type],
        c = Q.call(this, a, b);
      c && this.callback(this.manager, b, { pointers: c[0], changedPointers: c[1], pointerType: za, srcEvent: a });
    }
  });
  var cb = 2500,
    db = 25;
  i(R, x, {
    handler: function (a, b, c) {
      var d = c.pointerType == za,
        e = c.pointerType == Ba;
      if (!(e && c.sourceCapabilities && c.sourceCapabilities.firesTouchEvents)) {
        if (d) S.call(this, b, c);
        else if (e && U.call(this, c)) return;
        this.callback(a, b, c);
      }
    },
    destroy: function () {
      (this.touch.destroy(), this.mouse.destroy());
    }
  });
  var eb = u(na.style, 'touchAction'),
    fb = eb !== d,
    gb = 'compute',
    hb = 'auto',
    ib = 'manipulation',
    jb = 'none',
    kb = 'pan-x',
    lb = 'pan-y',
    mb = X();
  V.prototype = {
    set: function (a) {
      (a == gb && (a = this.compute()),
        fb && this.manager.element.style && mb[a] && (this.manager.element.style[eb] = a),
        (this.actions = a.toLowerCase().trim()));
    },
    update: function () {
      this.set(this.manager.options.touchAction);
    },
    compute: function () {
      var a = [];
      return (
        g(this.manager.recognizers, function (b) {
          k(b.options.enable, [b]) && (a = a.concat(b.getTouchAction()));
        }),
        W(a.join(' '))
      );
    },
    preventDefaults: function (a) {
      var b = a.srcEvent,
        c = a.offsetDirection;
      if (this.manager.session.prevented) return void b.preventDefault();
      var d = this.actions,
        e = p(d, jb) && !mb[jb],
        f = p(d, lb) && !mb[lb],
        g = p(d, kb) && !mb[kb];
      if (e) {
        var h = 1 === a.pointers.length,
          i = a.distance < 2,
          j = a.deltaTime < 250;
        if (h && i && j) return;
      }
      return g && f ? void 0 : e || (f && c & Na) || (g && c & Oa) ? this.preventSrc(b) : void 0;
    },
    preventSrc: function (a) {
      ((this.manager.session.prevented = !0), a.preventDefault());
    }
  };
  var nb = 1,
    ob = 2,
    pb = 4,
    qb = 8,
    rb = qb,
    sb = 16,
    tb = 32;
  ((Y.prototype = {
    defaults: {},
    set: function (a) {
      return (la(this.options, a), this.manager && this.manager.touchAction.update(), this);
    },
    recognizeWith: function (a) {
      if (f(a, 'recognizeWith', this)) return this;
      var b = this.simultaneous;
      return ((a = _(a, this)), b[a.id] || ((b[a.id] = a), a.recognizeWith(this)), this);
    },
    dropRecognizeWith: function (a) {
      return f(a, 'dropRecognizeWith', this) ? this : ((a = _(a, this)), delete this.simultaneous[a.id], this);
    },
    requireFailure: function (a) {
      if (f(a, 'requireFailure', this)) return this;
      var b = this.requireFail;
      return ((a = _(a, this)), -1 === r(b, a) && (b.push(a), a.requireFailure(this)), this);
    },
    dropRequireFailure: function (a) {
      if (f(a, 'dropRequireFailure', this)) return this;
      a = _(a, this);
      var b = r(this.requireFail, a);
      return (b > -1 && this.requireFail.splice(b, 1), this);
    },
    hasRequireFailures: function () {
      return this.requireFail.length > 0;
    },
    canRecognizeWith: function (a) {
      return !!this.simultaneous[a.id];
    },
    emit: function (a) {
      function b(b) {
        c.manager.emit(b, a);
      }
      var c = this,
        d = this.state;
      (qb > d && b(c.options.event + Z(d)), b(c.options.event), a.additionalEvent && b(a.additionalEvent), d >= qb && b(c.options.event + Z(d)));
    },
    tryEmit: function (a) {
      return this.canEmit() ? this.emit(a) : void (this.state = tb);
    },
    canEmit: function () {
      for (var a = 0; a < this.requireFail.length; ) {
        if (!(this.requireFail[a].state & (tb | nb))) return !1;
        a++;
      }
      return !0;
    },
    recognize: function (a) {
      var b = la({}, a);
      return k(this.options.enable, [this, b])
        ? (this.state & (rb | sb | tb) && (this.state = nb), (this.state = this.process(b)), void (this.state & (ob | pb | qb | sb) && this.tryEmit(b)))
        : (this.reset(), void (this.state = tb));
    },
    process: function (a) {},
    getTouchAction: function () {},
    reset: function () {}
  }),
    i(aa, Y, {
      defaults: { pointers: 1 },
      attrTest: function (a) {
        var b = this.options.pointers;
        return 0 === b || a.pointers.length === b;
      },
      process: function (a) {
        var b = this.state,
          c = a.eventType,
          d = b & (ob | pb),
          e = this.attrTest(a);
        return d && (c & Ha || !e) ? b | sb : d || e ? (c & Ga ? b | qb : b & ob ? b | pb : ob) : tb;
      }
    }),
    i(ba, aa, {
      defaults: { event: 'pan', threshold: 10, pointers: 1, direction: Pa },
      getTouchAction: function () {
        var a = this.options.direction,
          b = [];
        return (a & Na && b.push(lb), a & Oa && b.push(kb), b);
      },
      directionTest: function (a) {
        var b = this.options,
          c = !0,
          d = a.distance,
          e = a.direction,
          f = a.deltaX,
          g = a.deltaY;
        return (
          e & b.direction ||
            (b.direction & Na
              ? ((e = 0 === f ? Ia : 0 > f ? Ja : Ka), (c = f != this.pX), (d = Math.abs(a.deltaX)))
              : ((e = 0 === g ? Ia : 0 > g ? La : Ma), (c = g != this.pY), (d = Math.abs(a.deltaY)))),
          (a.direction = e),
          c && d > b.threshold && e & b.direction
        );
      },
      attrTest: function (a) {
        return aa.prototype.attrTest.call(this, a) && (this.state & ob || (!(this.state & ob) && this.directionTest(a)));
      },
      emit: function (a) {
        ((this.pX = a.deltaX), (this.pY = a.deltaY));
        var b = $(a.direction);
        (b && (a.additionalEvent = this.options.event + b), this._super.emit.call(this, a));
      }
    }),
    i(ca, aa, {
      defaults: { event: 'pinch', threshold: 0, pointers: 2 },
      getTouchAction: function () {
        return [jb];
      },
      attrTest: function (a) {
        return this._super.attrTest.call(this, a) && (Math.abs(a.scale - 1) > this.options.threshold || this.state & ob);
      },
      emit: function (a) {
        if (1 !== a.scale) {
          var b = a.scale < 1 ? 'in' : 'out';
          a.additionalEvent = this.options.event + b;
        }
        this._super.emit.call(this, a);
      }
    }),
    i(da, Y, {
      defaults: { event: 'press', pointers: 1, time: 251, threshold: 9 },
      getTouchAction: function () {
        return [hb];
      },
      process: function (a) {
        var b = this.options,
          c = a.pointers.length === b.pointers,
          d = a.distance < b.threshold,
          f = a.deltaTime > b.time;
        if (((this._input = a), !d || !c || (a.eventType & (Ga | Ha) && !f))) this.reset();
        else if (a.eventType & Ea)
          (this.reset(),
            (this._timer = e(
              function () {
                ((this.state = rb), this.tryEmit());
              },
              b.time,
              this
            )));
        else if (a.eventType & Ga) return rb;
        return tb;
      },
      reset: function () {
        clearTimeout(this._timer);
      },
      emit: function (a) {
        this.state === rb &&
          (a && a.eventType & Ga
            ? this.manager.emit(this.options.event + 'up', a)
            : ((this._input.timeStamp = ra()), this.manager.emit(this.options.event, this._input)));
      }
    }),
    i(ea, aa, {
      defaults: { event: 'rotate', threshold: 0, pointers: 2 },
      getTouchAction: function () {
        return [jb];
      },
      attrTest: function (a) {
        return this._super.attrTest.call(this, a) && (Math.abs(a.rotation) > this.options.threshold || this.state & ob);
      }
    }),
    i(fa, aa, {
      defaults: { event: 'swipe', threshold: 10, velocity: 0.3, direction: Na | Oa, pointers: 1 },
      getTouchAction: function () {
        return ba.prototype.getTouchAction.call(this);
      },
      attrTest: function (a) {
        var b,
          c = this.options.direction;
        return (
          c & (Na | Oa) ? (b = a.overallVelocity) : c & Na ? (b = a.overallVelocityX) : c & Oa && (b = a.overallVelocityY),
          this._super.attrTest.call(this, a) &&
            c & a.offsetDirection &&
            a.distance > this.options.threshold &&
            a.maxPointers == this.options.pointers &&
            qa(b) > this.options.velocity &&
            a.eventType & Ga
        );
      },
      emit: function (a) {
        var b = $(a.offsetDirection);
        (b && this.manager.emit(this.options.event + b, a), this.manager.emit(this.options.event, a));
      }
    }),
    i(ga, Y, {
      defaults: { event: 'tap', pointers: 1, taps: 1, interval: 300, time: 250, threshold: 9, posThreshold: 10 },
      getTouchAction: function () {
        return [ib];
      },
      process: function (a) {
        var b = this.options,
          c = a.pointers.length === b.pointers,
          d = a.distance < b.threshold,
          f = a.deltaTime < b.time;
        if ((this.reset(), a.eventType & Ea && 0 === this.count)) return this.failTimeout();
        if (d && f && c) {
          if (a.eventType != Ga) return this.failTimeout();
          var g = this.pTime ? a.timeStamp - this.pTime < b.interval : !0,
            h = !this.pCenter || H(this.pCenter, a.center) < b.posThreshold;
          ((this.pTime = a.timeStamp), (this.pCenter = a.center), h && g ? (this.count += 1) : (this.count = 1), (this._input = a));
          var i = this.count % b.taps;
          if (0 === i)
            return this.hasRequireFailures()
              ? ((this._timer = e(
                  function () {
                    ((this.state = rb), this.tryEmit());
                  },
                  b.interval,
                  this
                )),
                ob)
              : rb;
        }
        return tb;
      },
      failTimeout: function () {
        return (
          (this._timer = e(
            function () {
              this.state = tb;
            },
            this.options.interval,
            this
          )),
          tb
        );
      },
      reset: function () {
        clearTimeout(this._timer);
      },
      emit: function () {
        this.state == rb && ((this._input.tapCount = this.count), this.manager.emit(this.options.event, this._input));
      }
    }),
    (ha.VERSION = '2.0.7'),
    (ha.defaults = {
      domEvents: !1,
      touchAction: gb,
      enable: !0,
      inputTarget: null,
      inputClass: null,
      preset: [
        [ea, { enable: !1 }],
        [ca, { enable: !1 }, ['rotate']],
        [fa, { direction: Na }],
        [ba, { direction: Na }, ['swipe']],
        [ga],
        [ga, { event: 'doubletap', taps: 2 }, ['tap']],
        [da]
      ],
      cssProps: { userSelect: 'none', touchSelect: 'none', touchCallout: 'none', contentZooming: 'none', userDrag: 'none', tapHighlightColor: 'rgba(0,0,0,0)' }
    }));
  var ub = 1,
    vb = 2;
  ((ia.prototype = {
    set: function (a) {
      return (
        la(this.options, a),
        a.touchAction && this.touchAction.update(),
        a.inputTarget && (this.input.destroy(), (this.input.target = a.inputTarget), this.input.init()),
        this
      );
    },
    stop: function (a) {
      this.session.stopped = a ? vb : ub;
    },
    recognize: function (a) {
      var b = this.session;
      if (!b.stopped) {
        this.touchAction.preventDefaults(a);
        var c,
          d = this.recognizers,
          e = b.curRecognizer;
        (!e || (e && e.state & rb)) && (e = b.curRecognizer = null);
        for (var f = 0; f < d.length; )
          ((c = d[f]),
            b.stopped === vb || (e && c != e && !c.canRecognizeWith(e)) ? c.reset() : c.recognize(a),
            !e && c.state & (ob | pb | qb) && (e = b.curRecognizer = c),
            f++);
      }
    },
    get: function (a) {
      if (a instanceof Y) return a;
      for (var b = this.recognizers, c = 0; c < b.length; c++) if (b[c].options.event == a) return b[c];
      return null;
    },
    add: function (a) {
      if (f(a, 'add', this)) return this;
      var b = this.get(a.options.event);
      return (b && this.remove(b), this.recognizers.push(a), (a.manager = this), this.touchAction.update(), a);
    },
    remove: function (a) {
      if (f(a, 'remove', this)) return this;
      if ((a = this.get(a))) {
        var b = this.recognizers,
          c = r(b, a);
        -1 !== c && (b.splice(c, 1), this.touchAction.update());
      }
      return this;
    },
    on: function (a, b) {
      if (a !== d && b !== d) {
        var c = this.handlers;
        return (
          g(q(a), function (a) {
            ((c[a] = c[a] || []), c[a].push(b));
          }),
          this
        );
      }
    },
    off: function (a, b) {
      if (a !== d) {
        var c = this.handlers;
        return (
          g(q(a), function (a) {
            b ? c[a] && c[a].splice(r(c[a], b), 1) : delete c[a];
          }),
          this
        );
      }
    },
    emit: function (a, b) {
      this.options.domEvents && ka(a, b);
      var c = this.handlers[a] && this.handlers[a].slice();
      if (c && c.length) {
        ((b.type = a),
          (b.preventDefault = function () {
            b.srcEvent.preventDefault();
          }));
        for (var d = 0; d < c.length; ) (c[d](b), d++);
      }
    },
    destroy: function () {
      (this.element && ja(this, !1), (this.handlers = {}), (this.session = {}), this.input.destroy(), (this.element = null));
    }
  }),
    la(ha, {
      INPUT_START: Ea,
      INPUT_MOVE: Fa,
      INPUT_END: Ga,
      INPUT_CANCEL: Ha,
      STATE_POSSIBLE: nb,
      STATE_BEGAN: ob,
      STATE_CHANGED: pb,
      STATE_ENDED: qb,
      STATE_RECOGNIZED: rb,
      STATE_CANCELLED: sb,
      STATE_FAILED: tb,
      DIRECTION_NONE: Ia,
      DIRECTION_LEFT: Ja,
      DIRECTION_RIGHT: Ka,
      DIRECTION_UP: La,
      DIRECTION_DOWN: Ma,
      DIRECTION_HORIZONTAL: Na,
      DIRECTION_VERTICAL: Oa,
      DIRECTION_ALL: Pa,
      Manager: ia,
      Input: x,
      TouchAction: V,
      TouchInput: P,
      MouseInput: L,
      PointerEventInput: M,
      TouchMouseInput: R,
      SingleTouchInput: N,
      Recognizer: Y,
      AttrRecognizer: aa,
      Tap: ga,
      Pan: ba,
      Swipe: fa,
      Pinch: ca,
      Rotate: ea,
      Press: da,
      on: m,
      off: n,
      each: g,
      merge: ta,
      extend: sa,
      assign: la,
      inherit: i,
      bindFn: j,
      prefixed: u
    }));
  var wb = 'undefined' != typeof a ? a : 'undefined' != typeof self ? self : {};
  ((wb.Hammer = ha),
    'function' == typeof define && define.amd
      ? define(function () {
          return ha;
        })
      : 'undefined' != typeof module && module.exports
        ? (module.exports = ha)
        : (a[c] = ha));
})(window, document, 'Hammer');
//# sourceMappingURL=hammer.min.js.map
//# sourceMappingURL=scripts.js.map

/*******************************************************************************
 * Copyright (c) 2013, 2016 IBM Corp.
 *
 * All rights reserved. This program and the accompanying materials
 * are made available under the terms of the Eclipse Public License v1.0
 * and Eclipse Distribution License v1.0 which accompany this distribution.
 *
 * The Eclipse Public License is available at
 *    http://www.eclipse.org/legal/epl-v10.html
 * and the Eclipse Distribution License is available at
 *   http://www.eclipse.org/org/documents/edl-v10.php.
 *
 *******************************************************************************/
(function(t,q){"object"===typeof exports&&"object"===typeof module?module.exports=q():"function"===typeof define&&define.amd?define(q):"object"===typeof exports?exports=q():t.Paho=q()})(this,function(){return function(t){function q(a,b,c){b[c++]=a>>8;b[c++]=a%256;return c}function r(a,b,c,k){k=q(b,c,k);E(a,c,k);return k+b}function n(a){for(var b=0,c=0;c<a.length;c++){var k=a.charCodeAt(c);2047<k?(55296<=k&&56319>=k&&(c++,b++),b+=3):127<k?b+=2:b++}return b}function E(a,b,c){for(var k=0;k<a.length;k++){var e=
    a.charCodeAt(k);if(55296<=e&&56319>=e){var g=a.charCodeAt(++k);if(isNaN(g))throw Error(f(h.MALFORMED_UNICODE,[e,g]));e=(e-55296<<10)+(g-56320)+65536}127>=e?b[c++]=e:(2047>=e?b[c++]=e>>6&31|192:(65535>=e?b[c++]=e>>12&15|224:(b[c++]=e>>18&7|240,b[c++]=e>>12&63|128),b[c++]=e>>6&63|128),b[c++]=e&63|128)}return b}function F(a,b,c){for(var k="",e,g=b;g<b+c;){e=a[g++];if(!(128>e)){var m=a[g++]-128;if(0>m)throw Error(f(h.MALFORMED_UTF,[e.toString(16),m.toString(16),""]));if(224>e)e=64*(e-192)+m;else{var d=
    a[g++]-128;if(0>d)throw Error(f(h.MALFORMED_UTF,[e.toString(16),m.toString(16),d.toString(16)]));if(240>e)e=4096*(e-224)+64*m+d;else{var l=a[g++]-128;if(0>l)throw Error(f(h.MALFORMED_UTF,[e.toString(16),m.toString(16),d.toString(16),l.toString(16)]));if(248>e)e=262144*(e-240)+4096*m+64*d+l;else throw Error(f(h.MALFORMED_UTF,[e.toString(16),m.toString(16),d.toString(16),l.toString(16)]));}}}65535<e&&(e-=65536,k+=String.fromCharCode(55296+(e>>10)),e=56320+(e&1023));k+=String.fromCharCode(e)}return k}
    var s=t.localStorage||function(){var a={};return{setItem:function(b,c){a[b]=c},getItem:function(b){return a[b]},removeItem:function(b){delete a[b]}}}(),A=function(a,b){for(var c in a)if(a.hasOwnProperty(c))if(b.hasOwnProperty(c)){if(typeof a[c]!==b[c])throw Error(f(h.INVALID_TYPE,[typeof a[c],c]));}else{c="Unknown property, "+c+". Valid properties are:";for(var k in b)b.hasOwnProperty(k)&&(c=c+" "+k);throw Error(c);}},u=function(a,b){return function(){return a.apply(b,arguments)}},h={OK:{code:0,text:"AMQJSC0000I OK."},
    CONNECT_TIMEOUT:{code:1,text:"AMQJSC0001E Connect timed out."},SUBSCRIBE_TIMEOUT:{code:2,text:"AMQJS0002E Subscribe timed out."},UNSUBSCRIBE_TIMEOUT:{code:3,text:"AMQJS0003E Unsubscribe timed out."},PING_TIMEOUT:{code:4,text:"AMQJS0004E Ping timed out."},INTERNAL_ERROR:{code:5,text:"AMQJS0005E Internal error. Error Message: {0}, Stack trace: {1}"},CONNACK_RETURNCODE:{code:6,text:"AMQJS0006E Bad Connack return code:{0} {1}."},SOCKET_ERROR:{code:7,text:"AMQJS0007E Socket error:{0}."},SOCKET_CLOSE:{code:8,
    text:"AMQJS0008I Socket closed."},MALFORMED_UTF:{code:9,text:"AMQJS0009E Malformed UTF data:{0} {1} {2}."},UNSUPPORTED:{code:10,text:"AMQJS0010E {0} is not supported by this browser."},INVALID_STATE:{code:11,text:"AMQJS0011E Invalid state {0}."},INVALID_TYPE:{code:12,text:"AMQJS0012E Invalid type {0} for {1}."},INVALID_ARGUMENT:{code:13,text:"AMQJS0013E Invalid argument {0} for {1}."},UNSUPPORTED_OPERATION:{code:14,text:"AMQJS0014E Unsupported operation."},INVALID_STORED_DATA:{code:15,text:"AMQJS0015E Invalid data in local storage key\x3d{0} value\x3d{1}."},
    INVALID_MQTT_MESSAGE_TYPE:{code:16,text:"AMQJS0016E Invalid MQTT message type {0}."},MALFORMED_UNICODE:{code:17,text:"AMQJS0017E Malformed Unicode string:{0} {1}."},BUFFER_FULL:{code:18,text:"AMQJS0018E Message buffer is full, maximum buffer size: {0}."}},H={0:"Connection Accepted",1:"Connection Refused: unacceptable protocol version",2:"Connection Refused: identifier rejected",3:"Connection Refused: server unavailable",4:"Connection Refused: bad user name or password",5:"Connection Refused: not authorized"},
    f=function(a,b){var c=a.text;if(b)for(var k,e,g=0;g<b.length;g++)if(k="{"+g+"}",e=c.indexOf(k),0<e)var h=c.substring(0,e),c=c.substring(e+k.length),c=h+b[g]+c;return c},B=[0,6,77,81,73,115,100,112,3],C=[0,4,77,81,84,84,4],p=function(a,b){this.type=a;for(var c in b)b.hasOwnProperty(c)&&(this[c]=b[c])};p.prototype.encode=function(){var a=(this.type&15)<<4,b=0,c=[],k=0,e;void 0!==this.messageIdentifier&&(b+=2);switch(this.type){case 1:switch(this.mqttVersion){case 3:b+=B.length+3;break;case 4:b+=C.length+
    3}b+=n(this.clientId)+2;void 0!==this.willMessage&&(b+=n(this.willMessage.destinationName)+2,e=this.willMessage.payloadBytes,e instanceof Uint8Array||(e=new Uint8Array(h)),b+=e.byteLength+2);void 0!==this.userName&&(b+=n(this.userName)+2);void 0!==this.password&&(b+=n(this.password)+2);break;case 8:for(var a=a|2,g=0;g<this.topics.length;g++)c[g]=n(this.topics[g]),b+=c[g]+2;b+=this.requestedQos.length;break;case 10:a|=2;for(g=0;g<this.topics.length;g++)c[g]=n(this.topics[g]),b+=c[g]+2;break;case 6:a|=
    2;break;case 3:this.payloadMessage.duplicate&&(a|=8);a=a|=this.payloadMessage.qos<<1;this.payloadMessage.retained&&(a|=1);var k=n(this.payloadMessage.destinationName),h=this.payloadMessage.payloadBytes,b=b+(k+2)+h.byteLength;h instanceof ArrayBuffer?h=new Uint8Array(h):h instanceof Uint8Array||(h=new Uint8Array(h.buffer))}var f=b,g=Array(1),d=0;do{var z=f%128,f=f>>7;0<f&&(z|=128);g[d++]=z}while(0<f&&4>d);f=g.length+1;b=new ArrayBuffer(b+f);d=new Uint8Array(b);d[0]=a;d.set(g,1);if(3==this.type)f=r(this.payloadMessage.destinationName,
    k,d,f);else if(1==this.type){switch(this.mqttVersion){case 3:d.set(B,f);f+=B.length;break;case 4:d.set(C,f),f+=C.length}a=0;this.cleanSession&&(a=2);void 0!==this.willMessage&&(a=a|4|this.willMessage.qos<<3,this.willMessage.retained&&(a|=32));void 0!==this.userName&&(a|=128);void 0!==this.password&&(a|=64);d[f++]=a;f=q(this.keepAliveInterval,d,f)}void 0!==this.messageIdentifier&&(f=q(this.messageIdentifier,d,f));switch(this.type){case 1:f=r(this.clientId,n(this.clientId),d,f);void 0!==this.willMessage&&
    (f=r(this.willMessage.destinationName,n(this.willMessage.destinationName),d,f),f=q(e.byteLength,d,f),d.set(e,f),f+=e.byteLength);void 0!==this.userName&&(f=r(this.userName,n(this.userName),d,f));void 0!==this.password&&r(this.password,n(this.password),d,f);break;case 3:d.set(h,f);break;case 8:for(g=0;g<this.topics.length;g++)f=r(this.topics[g],c[g],d,f),d[f++]=this.requestedQos[g];break;case 10:for(g=0;g<this.topics.length;g++)f=r(this.topics[g],c[g],d,f)}return b};var G=function(a,b){this._client=
    a;this._keepAliveInterval=1E3*b;this.isReset=!1;var c=(new p(12)).encode(),k=function(a){return function(){return e.apply(a)}},e=function(){this.isReset?(this.isReset=!1,this._client._trace("Pinger.doPing","send PINGREQ"),this._client.socket.send(c),this.timeout=setTimeout(k(this),this._keepAliveInterval)):(this._client._trace("Pinger.doPing","Timed out"),this._client._disconnected(h.PING_TIMEOUT.code,f(h.PING_TIMEOUT)))};this.reset=function(){this.isReset=!0;clearTimeout(this.timeout);0<this._keepAliveInterval&&
    (this.timeout=setTimeout(k(this),this._keepAliveInterval))};this.cancel=function(){clearTimeout(this.timeout)}},v=function(a,b,c,f){b||(b=30);this.timeout=setTimeout(function(a,b,c){return function(){return a.apply(b,c)}}(c,a,f),1E3*b);this.cancel=function(){clearTimeout(this.timeout)}},d=function(a,b,c,d,e){if(!("WebSocket"in t&&null!==t.WebSocket))throw Error(f(h.UNSUPPORTED,["WebSocket"]));if(!("ArrayBuffer"in t&&null!==t.ArrayBuffer))throw Error(f(h.UNSUPPORTED,["ArrayBuffer"]));this._trace("Paho.Client",
    a,b,c,d,e);this.host=b;this.port=c;this.path=d;this.uri=a;this.clientId=e;this._wsuri=null;this._localKey=b+":"+c+("/mqtt"!=d?":"+d:"")+":"+e+":";this._msg_queue=[];this._buffered_msg_queue=[];this._sentMessages={};this._receivedMessages={};this._notify_msg_sent={};this._message_identifier=1;this._sequence=0;for(var g in s)0!==g.indexOf("Sent:"+this._localKey)&&0!==g.indexOf("Received:"+this._localKey)||this.restore(g)};d.prototype.host=null;d.prototype.port=null;d.prototype.path=null;d.prototype.uri=
    null;d.prototype.clientId=null;d.prototype.socket=null;d.prototype.connected=!1;d.prototype.maxMessageIdentifier=65536;d.prototype.connectOptions=null;d.prototype.hostIndex=null;d.prototype.onConnected=null;d.prototype.onConnectionLost=null;d.prototype.onMessageDelivered=null;d.prototype.onMessageArrived=null;d.prototype.traceFunction=null;d.prototype._msg_queue=null;d.prototype._buffered_msg_queue=null;d.prototype._connectTimeout=null;d.prototype.sendPinger=null;d.prototype.receivePinger=null;d.prototype._reconnectInterval=
    1;d.prototype._reconnecting=!1;d.prototype._reconnectTimeout=null;d.prototype.disconnectedPublishing=!1;d.prototype.disconnectedBufferSize=5E3;d.prototype.receiveBuffer=null;d.prototype._traceBuffer=null;d.prototype._MAX_TRACE_ENTRIES=100;d.prototype.connect=function(a){var b=this._traceMask(a,"password");this._trace("Client.connect",b,this.socket,this.connected);if(this.connected)throw Error(f(h.INVALID_STATE,["already connected"]));if(this.socket)throw Error(f(h.INVALID_STATE,["already connected"]));
    this._reconnecting&&(this._reconnectTimeout.cancel(),this._reconnectTimeout=null,this._reconnecting=!1);this.connectOptions=a;this._reconnectInterval=1;this._reconnecting=!1;a.uris?(this.hostIndex=0,this._doConnect(a.uris[0])):this._doConnect(this.uri)};d.prototype.subscribe=function(a,b){this._trace("Client.subscribe",a,b);if(!this.connected)throw Error(f(h.INVALID_STATE,["not connected"]));var c=new p(8);c.topics=a.constructor===Array?a:[a];void 0===b.qos&&(b.qos=0);c.requestedQos=[];for(var d=
    0;d<c.topics.length;d++)c.requestedQos[d]=b.qos;b.onSuccess&&(c.onSuccess=function(a){b.onSuccess({invocationContext:b.invocationContext,grantedQos:a})});b.onFailure&&(c.onFailure=function(a){b.onFailure({invocationContext:b.invocationContext,errorCode:a,errorMessage:f(a)})});b.timeout&&(c.timeOut=new v(this,b.timeout,b.onFailure,[{invocationContext:b.invocationContext,errorCode:h.SUBSCRIBE_TIMEOUT.code,errorMessage:f(h.SUBSCRIBE_TIMEOUT)}]));this._requires_ack(c);this._schedule_message(c)};d.prototype.unsubscribe=
    function(a,b){this._trace("Client.unsubscribe",a,b);if(!this.connected)throw Error(f(h.INVALID_STATE,["not connected"]));var c=new p(10);c.topics=a.constructor===Array?a:[a];b.onSuccess&&(c.callback=function(){b.onSuccess({invocationContext:b.invocationContext})});b.timeout&&(c.timeOut=new v(this,b.timeout,b.onFailure,[{invocationContext:b.invocationContext,errorCode:h.UNSUBSCRIBE_TIMEOUT.code,errorMessage:f(h.UNSUBSCRIBE_TIMEOUT)}]));this._requires_ack(c);this._schedule_message(c)};d.prototype.send=
    function(a){this._trace("Client.send",a);var b=new p(3);b.payloadMessage=a;if(this.connected)0<a.qos?this._requires_ack(b):this.onMessageDelivered&&(this._notify_msg_sent[b]=this.onMessageDelivered(b.payloadMessage)),this._schedule_message(b);else if(this._reconnecting&&this.disconnectedPublishing){if(Object.keys(this._sentMessages).length+this._buffered_msg_queue.length>this.disconnectedBufferSize)throw Error(f(h.BUFFER_FULL,[this.disconnectedBufferSize]));0<a.qos?this._requires_ack(b):(b.sequence=
    ++this._sequence,this._buffered_msg_queue.unshift(b))}else throw Error(f(h.INVALID_STATE,["not connected"]));};d.prototype.disconnect=function(){this._trace("Client.disconnect");this._reconnecting&&(this._reconnectTimeout.cancel(),this._reconnectTimeout=null,this._reconnecting=!1);if(!this.socket)throw Error(f(h.INVALID_STATE,["not connecting or connected"]));var a=new p(14);this._notify_msg_sent[a]=u(this._disconnected,this);this._schedule_message(a)};d.prototype.getTraceLog=function(){if(null!==
    this._traceBuffer){this._trace("Client.getTraceLog",new Date);this._trace("Client.getTraceLog in flight messages",this._sentMessages.length);for(var a in this._sentMessages)this._trace("_sentMessages ",a,this._sentMessages[a]);for(a in this._receivedMessages)this._trace("_receivedMessages ",a,this._receivedMessages[a]);return this._traceBuffer}};d.prototype.startTrace=function(){null===this._traceBuffer&&(this._traceBuffer=[]);this._trace("Client.startTrace",new Date,"1.1.0-2018-11-22T11:35:36Z")};d.prototype.stopTrace=
    function(){delete this._traceBuffer};d.prototype._doConnect=function(a){this.connectOptions.useSSL&&(a=a.split(":"),a[0]="wss",a=a.join(":"));this._wsuri=a;this.connected=!1;this.socket=4>this.connectOptions.mqttVersion?new WebSocket(a,["mqttv3.1"]):new WebSocket(a,["mqtt"]);this.socket.binaryType="arraybuffer";this.socket.onopen=u(this._on_socket_open,this);this.socket.onmessage=u(this._on_socket_message,this);this.socket.onerror=u(this._on_socket_error,this);this.socket.onclose=u(this._on_socket_close,
    this);this.sendPinger=new G(this,this.connectOptions.keepAliveInterval);this.receivePinger=new G(this,this.connectOptions.keepAliveInterval);this._connectTimeout&&(this._connectTimeout.cancel(),this._connectTimeout=null);this._connectTimeout=new v(this,this.connectOptions.timeout,this._disconnected,[h.CONNECT_TIMEOUT.code,f(h.CONNECT_TIMEOUT)])};d.prototype._schedule_message=function(a){this._msg_queue.unshift(a);this.connected&&this._process_queue()};d.prototype.store=function(a,b){var c={type:b.type,
    messageIdentifier:b.messageIdentifier,version:1};switch(b.type){case 3:b.pubRecReceived&&(c.pubRecReceived=!0);c.payloadMessage={};for(var d="",e=b.payloadMessage.payloadBytes,g=0;g<e.length;g++)d=15>=e[g]?d+"0"+e[g].toString(16):d+e[g].toString(16);c.payloadMessage.payloadHex=d;c.payloadMessage.qos=b.payloadMessage.qos;c.payloadMessage.destinationName=b.payloadMessage.destinationName;b.payloadMessage.duplicate&&(c.payloadMessage.duplicate=!0);b.payloadMessage.retained&&(c.payloadMessage.retained=
    !0);0===a.indexOf("Sent:")&&(void 0===b.sequence&&(b.sequence=++this._sequence),c.sequence=b.sequence);break;default:throw Error(f(h.INVALID_STORED_DATA,[a+this._localKey+b.messageIdentifier,c]));}s.setItem(a+this._localKey+b.messageIdentifier,JSON.stringify(c))};d.prototype.restore=function(a){var b=s.getItem(a),c=JSON.parse(b),d=new p(c.type,c);switch(c.type){case 3:for(var b=c.payloadMessage.payloadHex,e=new ArrayBuffer(b.length/2),e=new Uint8Array(e),g=0;2<=b.length;){var m=parseInt(b.substring(0,
    2),16),b=b.substring(2,b.length);e[g++]=m}b=new w(e);b.qos=c.payloadMessage.qos;b.destinationName=c.payloadMessage.destinationName;c.payloadMessage.duplicate&&(b.duplicate=!0);c.payloadMessage.retained&&(b.retained=!0);d.payloadMessage=b;break;default:throw Error(f(h.INVALID_STORED_DATA,[a,b]));}0===a.indexOf("Sent:"+this._localKey)?(d.payloadMessage.duplicate=!0,this._sentMessages[d.messageIdentifier]=d):0===a.indexOf("Received:"+this._localKey)&&(this._receivedMessages[d.messageIdentifier]=d)};
    d.prototype._process_queue=function(){for(var a=null;a=this._msg_queue.pop();)this._socket_send(a),this._notify_msg_sent[a]&&(this._notify_msg_sent[a](),delete this._notify_msg_sent[a])};d.prototype._requires_ack=function(a){var b=Object.keys(this._sentMessages).length;if(b>this.maxMessageIdentifier)throw Error("Too many messages:"+b);for(;void 0!==this._sentMessages[this._message_identifier];)this._message_identifier++;a.messageIdentifier=this._message_identifier;this._sentMessages[a.messageIdentifier]=
    a;3===a.type&&this.store("Sent:",a);this._message_identifier===this.maxMessageIdentifier&&(this._message_identifier=1)};d.prototype._on_socket_open=function(){var a=new p(1,this.connectOptions);a.clientId=this.clientId;this._socket_send(a)};d.prototype._on_socket_message=function(a){this._trace("Client._on_socket_message",a.data);a=this._deframeMessages(a.data);for(var b=0;b<a.length;b+=1)this._handleMessage(a[b])};d.prototype._deframeMessages=function(a){a=new Uint8Array(a);var b=[];if(this.receiveBuffer){var c=
    new Uint8Array(this.receiveBuffer.length+a.length);c.set(this.receiveBuffer);c.set(a,this.receiveBuffer.length);a=c;delete this.receiveBuffer}try{for(c=0;c<a.length;){var d;a:{var e=a,g=c,m=g,n=e[g],l=n>>4,z=n&15,g=g+1,x=void 0,D=0,q=1;do{if(g==e.length){d=[null,m];break a}x=e[g++];D+=(x&127)*q;q*=128}while(0!==(x&128));x=g+D;if(x>e.length)d=[null,m];else{var y=new p(l);switch(l){case 2:e[g++]&1&&(y.sessionPresent=!0);y.returnCode=e[g++];break;case 3:var m=z>>1&3,s=256*e[g]+e[g+1],g=g+2,t=F(e,g,s),
    g=g+s;0<m&&(y.messageIdentifier=256*e[g]+e[g+1],g+=2);var r=new w(e.subarray(g,x));1==(z&1)&&(r.retained=!0);8==(z&8)&&(r.duplicate=!0);r.qos=m;r.destinationName=t;y.payloadMessage=r;break;case 4:case 5:case 6:case 7:case 11:y.messageIdentifier=256*e[g]+e[g+1];break;case 9:y.messageIdentifier=256*e[g]+e[g+1],g+=2,y.returnCode=e.subarray(g,x)}d=[y,x]}}var u=d[0],c=d[1];if(null!==u)b.push(u);else break}c<a.length&&(this.receiveBuffer=a.subarray(c))}catch(v){d="undefined"==v.hasOwnProperty("stack")?
    v.stack.toString():"No Error Stack Available";this._disconnected(h.INTERNAL_ERROR.code,f(h.INTERNAL_ERROR,[v.message,d]));return}return b};d.prototype._handleMessage=function(a){this._trace("Client._handleMessage",a);try{switch(a.type){case 2:this._connectTimeout.cancel();this._reconnectTimeout&&this._reconnectTimeout.cancel();if(this.connectOptions.cleanSession){for(var b in this._sentMessages){var c=this._sentMessages[b];s.removeItem("Sent:"+this._localKey+c.messageIdentifier)}this._sentMessages=
    {};for(b in this._receivedMessages){var d=this._receivedMessages[b];s.removeItem("Received:"+this._localKey+d.messageIdentifier)}this._receivedMessages={}}if(0===a.returnCode)this.connected=!0,this.connectOptions.uris&&(this.hostIndex=this.connectOptions.uris.length);else{this._disconnected(h.CONNACK_RETURNCODE.code,f(h.CONNACK_RETURNCODE,[a.returnCode,H[a.returnCode]]));break}a=[];for(var e in this._sentMessages)this._sentMessages.hasOwnProperty(e)&&a.push(this._sentMessages[e]);if(0<this._buffered_msg_queue.length)for(e=
    null;e=this._buffered_msg_queue.pop();)a.push(e),this.onMessageDelivered&&(this._notify_msg_sent[e]=this.onMessageDelivered(e.payloadMessage));a=a.sort(function(a,b){return a.sequence-b.sequence});e=0;for(var g=a.length;e<g;e++)if(c=a[e],3==c.type&&c.pubRecReceived){var m=new p(6,{messageIdentifier:c.messageIdentifier});this._schedule_message(m)}else this._schedule_message(c);if(this.connectOptions.onSuccess)this.connectOptions.onSuccess({invocationContext:this.connectOptions.invocationContext});
    c=!1;this._reconnecting&&(c=!0,this._reconnectInterval=1,this._reconnecting=!1);this._connected(c,this._wsuri);this._process_queue();break;case 3:this._receivePublish(a);break;case 4:if(c=this._sentMessages[a.messageIdentifier])if(delete this._sentMessages[a.messageIdentifier],s.removeItem("Sent:"+this._localKey+a.messageIdentifier),this.onMessageDelivered)this.onMessageDelivered(c.payloadMessage);break;case 5:if(c=this._sentMessages[a.messageIdentifier])c.pubRecReceived=!0,m=new p(6,{messageIdentifier:a.messageIdentifier}),
    this.store("Sent:",c),this._schedule_message(m);break;case 6:d=this._receivedMessages[a.messageIdentifier];s.removeItem("Received:"+this._localKey+a.messageIdentifier);d&&(this._receiveMessage(d),delete this._receivedMessages[a.messageIdentifier]);var n=new p(7,{messageIdentifier:a.messageIdentifier});this._schedule_message(n);break;case 7:c=this._sentMessages[a.messageIdentifier];delete this._sentMessages[a.messageIdentifier];s.removeItem("Sent:"+this._localKey+a.messageIdentifier);if(this.onMessageDelivered)this.onMessageDelivered(c.payloadMessage);
    break;case 9:if(c=this._sentMessages[a.messageIdentifier]){c.timeOut&&c.timeOut.cancel();if(128===a.returnCode[0]){if(c.onFailure)c.onFailure(a.returnCode)}else if(c.onSuccess)c.onSuccess(a.returnCode);delete this._sentMessages[a.messageIdentifier]}break;case 11:if(c=this._sentMessages[a.messageIdentifier])c.timeOut&&c.timeOut.cancel(),c.callback&&c.callback(),delete this._sentMessages[a.messageIdentifier];break;case 13:this.sendPinger.reset();break;case 14:this._disconnected(h.INVALID_MQTT_MESSAGE_TYPE.code,
    f(h.INVALID_MQTT_MESSAGE_TYPE,[a.type]));break;default:this._disconnected(h.INVALID_MQTT_MESSAGE_TYPE.code,f(h.INVALID_MQTT_MESSAGE_TYPE,[a.type]))}}catch(l){c="undefined"==l.hasOwnProperty("stack")?l.stack.toString():"No Error Stack Available",this._disconnected(h.INTERNAL_ERROR.code,f(h.INTERNAL_ERROR,[l.message,c]))}};d.prototype._on_socket_error=function(a){this._reconnecting||this._disconnected(h.SOCKET_ERROR.code,f(h.SOCKET_ERROR,[a.data]))};d.prototype._on_socket_close=function(){this._reconnecting||
    this._disconnected(h.SOCKET_CLOSE.code,f(h.SOCKET_CLOSE))};d.prototype._socket_send=function(a){if(1==a.type){var b=this._traceMask(a,"password");this._trace("Client._socket_send",b)}else this._trace("Client._socket_send",a);this.socket.send(a.encode());this.sendPinger.reset()};d.prototype._receivePublish=function(a){switch(a.payloadMessage.qos){case "undefined":case 0:this._receiveMessage(a);break;case 1:var b=new p(4,{messageIdentifier:a.messageIdentifier});this._schedule_message(b);this._receiveMessage(a);
    break;case 2:this._receivedMessages[a.messageIdentifier]=a;this.store("Received:",a);a=new p(5,{messageIdentifier:a.messageIdentifier});this._schedule_message(a);break;default:throw Error("Invaild qos\x3d"+a.payloadMessage.qos);}};d.prototype._receiveMessage=function(a){if(this.onMessageArrived)this.onMessageArrived(a.payloadMessage)};d.prototype._connected=function(a,b){if(this.onConnected)this.onConnected(a,b)};d.prototype._reconnect=function(){this._trace("Client._reconnect");this.connected||(this._reconnecting=
    !0,this.sendPinger.cancel(),this.receivePinger.cancel(),128>this._reconnectInterval&&(this._reconnectInterval*=2),this.connectOptions.uris?(this.hostIndex=0,this._doConnect(this.connectOptions.uris[0])):this._doConnect(this.uri))};d.prototype._disconnected=function(a,b){this._trace("Client._disconnected",a,b);if(void 0!==a&&this._reconnecting)this._reconnectTimeout=new v(this,this._reconnectInterval,this._reconnect);else if(this.sendPinger.cancel(),this.receivePinger.cancel(),this._connectTimeout&&
    (this._connectTimeout.cancel(),this._connectTimeout=null),this._msg_queue=[],this._buffered_msg_queue=[],this._notify_msg_sent={},this.socket&&(this.socket.onopen=null,this.socket.onmessage=null,this.socket.onerror=null,this.socket.onclose=null,1===this.socket.readyState&&this.socket.close(),delete this.socket),this.connectOptions.uris&&this.hostIndex<this.connectOptions.uris.length-1)this.hostIndex++,this._doConnect(this.connectOptions.uris[this.hostIndex]);else if(void 0===a&&(a=h.OK.code,b=f(h.OK)),
    this.connected){this.connected=!1;if(this.onConnectionLost)this.onConnectionLost({errorCode:a,errorMessage:b,reconnect:this.connectOptions.reconnect,uri:this._wsuri});a!==h.OK.code&&this.connectOptions.reconnect&&(this._reconnectInterval=1,this._reconnect())}else if(4===this.connectOptions.mqttVersion&&!1===this.connectOptions.mqttVersionExplicit)this._trace("Failed to connect V4, dropping back to V3"),this.connectOptions.mqttVersion=3,this.connectOptions.uris?(this.hostIndex=0,this._doConnect(this.connectOptions.uris[0])):
    this._doConnect(this.uri);else if(this.connectOptions.onFailure)this.connectOptions.onFailure({invocationContext:this.connectOptions.invocationContext,errorCode:a,errorMessage:b})};d.prototype._trace=function(){if(this.traceFunction){var a=Array.prototype.slice.call(arguments),b;for(b in a)"undefined"!==typeof a[b]&&a.splice(b,1,JSON.stringify(a[b]));b=a.join("");this.traceFunction({severity:"Debug",message:b})}if(null!==this._traceBuffer)for(b=0,a=arguments.length;b<a;b++)this._traceBuffer.length==
    this._MAX_TRACE_ENTRIES&&this._traceBuffer.shift(),0===b?this._traceBuffer.push(arguments[b]):"undefined"===typeof arguments[b]?this._traceBuffer.push(arguments[b]):this._traceBuffer.push("  "+JSON.stringify(arguments[b]))};d.prototype._traceMask=function(a,b){var c={},d;for(d in a)a.hasOwnProperty(d)&&(c[d]=d==b?"******":a[d]);return c};var w=function(a){var b;if("string"===typeof a||a instanceof ArrayBuffer||ArrayBuffer.isView(a)&&!(a instanceof DataView))b=a;else throw f(h.INVALID_ARGUMENT,[a,
    "newPayload"]);var c,d=0,e=!1,g=!1;Object.defineProperties(this,{payloadString:{enumerable:!0,get:function(){return"string"===typeof b?b:F(b,0,b.length)}},payloadBytes:{enumerable:!0,get:function(){if("string"===typeof b){var a=new ArrayBuffer(n(b)),a=new Uint8Array(a);E(b,a,0);return a}return b}},destinationName:{enumerable:!0,get:function(){return c},set:function(a){if("string"===typeof a)c=a;else throw Error(f(h.INVALID_ARGUMENT,[a,"newDestinationName"]));}},qos:{enumerable:!0,get:function(){return d},
    set:function(a){if(0===a||1===a||2===a)d=a;else throw Error("Invalid argument:"+a);}},retained:{enumerable:!0,get:function(){return e},set:function(a){if("boolean"===typeof a)e=a;else throw Error(f(h.INVALID_ARGUMENT,[a,"newRetained"]));}},topic:{enumerable:!0,get:function(){return c},set:function(a){c=a}},duplicate:{enumerable:!0,get:function(){return g},set:function(a){g=a}}})};return{Client:function(a,b,c,k){var e;if("string"!==typeof a)throw Error(f(h.INVALID_TYPE,[typeof a,"host"]));if(2==arguments.length){k=
    b;e=a;var g=e.match(/^(wss?):\/\/((\[(.+)\])|([^\/]+?))(:(\d+))?(\/.*)$/);if(g)a=g[4]||g[2],b=parseInt(g[7]),c=g[8];else throw Error(f(h.INVALID_ARGUMENT,[a,"host"]));}else{3==arguments.length&&(k=c,c="/mqtt");if("number"!==typeof b||0>b)throw Error(f(h.INVALID_TYPE,[typeof b,"port"]));if("string"!==typeof c)throw Error(f(h.INVALID_TYPE,[typeof c,"path"]));e="ws://"+(-1!==a.indexOf(":")&&"["!==a.slice(0,1)&&"]"!==a.slice(-1)?"["+a+"]":a)+":"+b+c}for(var m=g=0;m<k.length;m++){var n=k.charCodeAt(m);
    55296<=n&&56319>=n&&m++;g++}if("string"!==typeof k||65535<g)throw Error(f(h.INVALID_ARGUMENT,[k,"clientId"]));var l=new d(e,a,b,c,k);Object.defineProperties(this,{host:{get:function(){return a},set:function(){throw Error(f(h.UNSUPPORTED_OPERATION));}},port:{get:function(){return b},set:function(){throw Error(f(h.UNSUPPORTED_OPERATION));}},path:{get:function(){return c},set:function(){throw Error(f(h.UNSUPPORTED_OPERATION));}},uri:{get:function(){return e},set:function(){throw Error(f(h.UNSUPPORTED_OPERATION));
    }},clientId:{get:function(){return l.clientId},set:function(){throw Error(f(h.UNSUPPORTED_OPERATION));}},onConnected:{get:function(){return l.onConnected},set:function(a){if("function"===typeof a)l.onConnected=a;else throw Error(f(h.INVALID_TYPE,[typeof a,"onConnected"]));}},disconnectedPublishing:{get:function(){return l.disconnectedPublishing},set:function(a){l.disconnectedPublishing=a}},disconnectedBufferSize:{get:function(){return l.disconnectedBufferSize},set:function(a){l.disconnectedBufferSize=
    a}},onConnectionLost:{get:function(){return l.onConnectionLost},set:function(a){if("function"===typeof a)l.onConnectionLost=a;else throw Error(f(h.INVALID_TYPE,[typeof a,"onConnectionLost"]));}},onMessageDelivered:{get:function(){return l.onMessageDelivered},set:function(a){if("function"===typeof a)l.onMessageDelivered=a;else throw Error(f(h.INVALID_TYPE,[typeof a,"onMessageDelivered"]));}},onMessageArrived:{get:function(){return l.onMessageArrived},set:function(a){if("function"===typeof a)l.onMessageArrived=
    a;else throw Error(f(h.INVALID_TYPE,[typeof a,"onMessageArrived"]));}},trace:{get:function(){return l.traceFunction},set:function(a){if("function"===typeof a)l.traceFunction=a;else throw Error(f(h.INVALID_TYPE,[typeof a,"onTrace"]));}}});this.connect=function(a){a=a||{};A(a,{timeout:"number",userName:"string",password:"string",willMessage:"object",keepAliveInterval:"number",cleanSession:"boolean",useSSL:"boolean",invocationContext:"object",onSuccess:"function",onFailure:"function",hosts:"object",
    ports:"object",reconnect:"boolean",mqttVersion:"number",mqttVersionExplicit:"boolean",uris:"object"});void 0===a.keepAliveInterval&&(a.keepAliveInterval=60);if(4<a.mqttVersion||3>a.mqttVersion)throw Error(f(h.INVALID_ARGUMENT,[a.mqttVersion,"connectOptions.mqttVersion"]));void 0===a.mqttVersion?(a.mqttVersionExplicit=!1,a.mqttVersion=4):a.mqttVersionExplicit=!0;if(void 0!==a.password&&void 0===a.userName)throw Error(f(h.INVALID_ARGUMENT,[a.password,"connectOptions.password"]));if(a.willMessage){if(!(a.willMessage instanceof
    w))throw Error(f(h.INVALID_TYPE,[a.willMessage,"connectOptions.willMessage"]));a.willMessage.stringPayload=null;if("undefined"===typeof a.willMessage.destinationName)throw Error(f(h.INVALID_TYPE,[typeof a.willMessage.destinationName,"connectOptions.willMessage.destinationName"]));}"undefined"===typeof a.cleanSession&&(a.cleanSession=!0);if(a.hosts){if(!(a.hosts instanceof Array))throw Error(f(h.INVALID_ARGUMENT,[a.hosts,"connectOptions.hosts"]));if(1>a.hosts.length)throw Error(f(h.INVALID_ARGUMENT,
    [a.hosts,"connectOptions.hosts"]));for(var b=!1,d=0;d<a.hosts.length;d++){if("string"!==typeof a.hosts[d])throw Error(f(h.INVALID_TYPE,[typeof a.hosts[d],"connectOptions.hosts["+d+"]"]));if(/^(wss?):\/\/((\[(.+)\])|([^\/]+?))(:(\d+))?(\/.*)$/.test(a.hosts[d]))if(0===d)b=!0;else{if(!b)throw Error(f(h.INVALID_ARGUMENT,[a.hosts[d],"connectOptions.hosts["+d+"]"]));}else if(b)throw Error(f(h.INVALID_ARGUMENT,[a.hosts[d],"connectOptions.hosts["+d+"]"]));}if(b)a.uris=a.hosts;else{if(!a.ports)throw Error(f(h.INVALID_ARGUMENT,
    [a.ports,"connectOptions.ports"]));if(!(a.ports instanceof Array))throw Error(f(h.INVALID_ARGUMENT,[a.ports,"connectOptions.ports"]));if(a.hosts.length!==a.ports.length)throw Error(f(h.INVALID_ARGUMENT,[a.ports,"connectOptions.ports"]));a.uris=[];for(d=0;d<a.hosts.length;d++){if("number"!==typeof a.ports[d]||0>a.ports[d])throw Error(f(h.INVALID_TYPE,[typeof a.ports[d],"connectOptions.ports["+d+"]"]));var b=a.hosts[d],g=a.ports[d];e="ws://"+(-1!==b.indexOf(":")?"["+b+"]":b)+":"+g+c;a.uris.push(e)}}}l.connect(a)};
    this.subscribe=function(a,b){if("string"!==typeof a&&a.constructor!==Array)throw Error("Invalid argument:"+a);b=b||{};A(b,{qos:"number",invocationContext:"object",onSuccess:"function",onFailure:"function",timeout:"number"});if(b.timeout&&!b.onFailure)throw Error("subscribeOptions.timeout specified with no onFailure callback.");if("undefined"!==typeof b.qos&&0!==b.qos&&1!==b.qos&&2!==b.qos)throw Error(f(h.INVALID_ARGUMENT,[b.qos,"subscribeOptions.qos"]));l.subscribe(a,b)};this.unsubscribe=function(a,
    b){if("string"!==typeof a&&a.constructor!==Array)throw Error("Invalid argument:"+a);b=b||{};A(b,{invocationContext:"object",onSuccess:"function",onFailure:"function",timeout:"number"});if(b.timeout&&!b.onFailure)throw Error("unsubscribeOptions.timeout specified with no onFailure callback.");l.unsubscribe(a,b)};this.send=function(a,b,c,d){var e;if(0===arguments.length)throw Error("Invalid argument.length");if(1==arguments.length){if(!(a instanceof w)&&"string"!==typeof a)throw Error("Invalid argument:"+
    typeof a);e=a;if("undefined"===typeof e.destinationName)throw Error(f(h.INVALID_ARGUMENT,[e.destinationName,"Message.destinationName"]));}else e=new w(b),e.destinationName=a,3<=arguments.length&&(e.qos=c),4<=arguments.length&&(e.retained=d);l.send(e)};this.publish=function(a,b,c,d){var e;if(0===arguments.length)throw Error("Invalid argument.length");if(1==arguments.length){if(!(a instanceof w)&&"string"!==typeof a)throw Error("Invalid argument:"+typeof a);e=a;if("undefined"===typeof e.destinationName)throw Error(f(h.INVALID_ARGUMENT,
    [e.destinationName,"Message.destinationName"]));}else e=new w(b),e.destinationName=a,3<=arguments.length&&(e.qos=c),4<=arguments.length&&(e.retained=d);l.send(e)};this.disconnect=function(){l.disconnect()};this.getTraceLog=function(){return l.getTraceLog()};this.startTrace=function(){l.startTrace()};this.stopTrace=function(){l.stopTrace()};this.isConnected=function(){return l.connected}},Message:w}}("undefined"!==typeof global?global:"undefined"!==typeof self?self:"undefined"!==typeof window?window:
    {})});
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val, i = Math.floor(Math.abs(val)), t = parseInt(val.toString().replace(/^[^.]*\.?|0+$/g, ''), 10) || 0;

if (n === 1 || !(t === 0) && (i === 0 || i === 1))
    return 1;
return 5;
}
    global.ng.common.locales['da'] = ["da",[["a","p"],["AM","PM"]],[["AM","PM"]],[["S","M","T","O","T","F","L"],["søn.","man.","tirs.","ons.","tors.","fre.","lør."],["søndag","mandag","tirsdag","onsdag","torsdag","fredag","lørdag"],["sø.","ma.","ti.","on.","to.","fr.","lø."]],u,[["J","F","M","A","M","J","J","A","S","O","N","D"],["jan.","feb.","mar.","apr.","maj","jun.","jul.","aug.","sep.","okt.","nov.","dec."],["januar","februar","marts","april","maj","juni","juli","august","september","oktober","november","december"]],u,[["fKr","eKr"],["f.Kr.","e.Kr."],["før Kristus","efter Kristus"]],1,[6,0],["dd.MM.y","d. MMM y","d. MMMM y","EEEE 'den' d. MMMM y"],["HH.mm","HH.mm.ss","HH.mm.ss z","HH.mm.ss zzzz"],["{1} {0}",u,u,u],[",",".",";","%","+","-","E","×","‰","∞","NaN","."],["#,##0.###","#,##0 %","#,##0.00 ¤","#E0"],"DKK","kr.","dansk krone",{"AUD":["AU$","$"],"BYN":[u,"Br."],"DKK":["kr."],"ISK":[u,"kr."],"JPY":["JP¥","¥"],"NOK":[u,"kr."],"PHP":[u,"₱"],"RON":[u,"L"],"SEK":[u,"kr."],"THB":["฿"],"TWD":["NT$"],"USD":["US$","$"]},"ltr", plural, [[["midnat","om morgenen","om formiddagen","om eftermiddagen","om aftenen","om natten"],u,u],[["midnat","morgen","formiddag","eftermiddag","aften","nat"],u,u],["00:00",["05:00","10:00"],["10:00","12:00"],["12:00","18:00"],["18:00","24:00"],["00:00","05:00"]]]];
  })(globalThis);
    
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val, i = Math.floor(Math.abs(val)), v = val.toString().replace(/^[^.]*\.?/, '').length;

if (i === 1 && v === 0)
    return 1;
return 5;
}
    global.ng.common.locales['de'] = ["de",[["AM","PM"]],u,[["S","M","D","M","D","F","S"],["So.","Mo.","Di.","Mi.","Do.","Fr.","Sa."],["Sonntag","Montag","Dienstag","Mittwoch","Donnerstag","Freitag","Samstag"],["So.","Mo.","Di.","Mi.","Do.","Fr.","Sa."]],[["S","M","D","M","D","F","S"],["So","Mo","Di","Mi","Do","Fr","Sa"],["Sonntag","Montag","Dienstag","Mittwoch","Donnerstag","Freitag","Samstag"],["So.","Mo.","Di.","Mi.","Do.","Fr.","Sa."]],[["J","F","M","A","M","J","J","A","S","O","N","D"],["Jan.","Feb.","März","Apr.","Mai","Juni","Juli","Aug.","Sept.","Okt.","Nov.","Dez."],["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"]],[["J","F","M","A","M","J","J","A","S","O","N","D"],["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"],["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"]],[["v. Chr.","n. Chr."]],1,[6,0],["dd.MM.yy","dd.MM.y","d. MMMM y","EEEE, d. MMMM y"],["HH:mm","HH:mm:ss","HH:mm:ss z","HH:mm:ss zzzz"],["{1}, {0}",u,u,u],[",",".",";","%","+","-","E","·","‰","∞","NaN",":"],["#,##0.###","#,##0 %","#,##0.00 ¤","#E0"],"EUR","€","Euro",{"ATS":["öS"],"AUD":["AU$","$"],"BGM":["BGK"],"BGO":["BGJ"],"BYN":[u,"р."],"CUC":[u,"Cub$"],"DEM":["DM"],"FKP":[u,"Fl£"],"GHS":[u,"₵"],"GNF":[u,"F.G."],"KMF":[u,"FC"],"PHP":[u,"₱"],"RON":[u,"L"],"RUR":[u,"р."],"RWF":[u,"F.Rw"],"SYP":[u,"SYP"],"THB":["฿"],"TWD":["NT$"],"XXX":[],"ZMW":[u,"K"]},"ltr", plural, [[["Mitternacht","morgens","vorm.","mittags","nachm.","abends","nachts"],u,["Mitternacht","morgens","vormittags","mittags","nachmittags","abends","nachts"]],[["Mitternacht","Morgen","Vorm.","Mittag","Nachm.","Abend","Nacht"],u,["Mitternacht","Morgen","Vormittag","Mittag","Nachmittag","Abend","Nacht"]],["00:00",["05:00","10:00"],["10:00","12:00"],["12:00","13:00"],["13:00","18:00"],["18:00","24:00"],["00:00","05:00"]]]];
  })(globalThis);
    
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val, i = Math.floor(Math.abs(val)), v = val.toString().replace(/^[^.]*\.?/, '').length;

if (i === 1 && v === 0)
    return 1;
return 5;
}
    global.ng.common.locales['en'] = ["en",[["a","p"],["AM","PM"]],[["AM","PM"]],[["S","M","T","W","T","F","S"],["Sun","Mon","Tue","Wed","Thu","Fri","Sat"],["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],["Su","Mo","Tu","We","Th","Fr","Sa"]],u,[["J","F","M","A","M","J","J","A","S","O","N","D"],["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],["January","February","March","April","May","June","July","August","September","October","November","December"]],u,[["B","A"],["BC","AD"],["Before Christ","Anno Domini"]],0,[6,0],["M/d/yy","MMM d, y","MMMM d, y","EEEE, MMMM d, y"],["h:mm a","h:mm:ss a","h:mm:ss a z","h:mm:ss a zzzz"],["{1}, {0}",u,u,u],[".",",",";","%","+","-","E","×","‰","∞","NaN",":"],["#,##0.###","#,##0%","¤#,##0.00","#E0"],"USD","$","US Dollar",{},"ltr", plural, [[["mi","n","in the morning","in the afternoon","in the evening","at night"],["midnight","noon","in the morning","in the afternoon","in the evening","at night"],u],[["midnight","noon","morning","afternoon","evening","night"],u,u],["00:00","12:00",["00:00","12:00"],["12:00","18:00"],["18:00","21:00"],["21:00","24:00"]]]];
  })(globalThis);
    
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val, i = Math.floor(Math.abs(val)), v = val.toString().replace(/^[^.]*\.?/, '').length, e = parseInt(val.toString().replace(/^[^e]*(e([-+]?\d+))?/, '$2')) || 0;

if (n === 1)
    return 1;
if (e === 0 && (!(i === 0) && (i % 1000000 === 0 && v === 0)) || !(e >= 0 && e <= 5))
    return 4;
return 5;
}
    global.ng.common.locales['es'] = ["es",[["a. m.","p. m."],u,["a. m.","p. m."]],u,[["D","L","M","X","J","V","S"],["dom","lun","mar","mié","jue","vie","sáb"],["domingo","lunes","martes","miércoles","jueves","viernes","sábado"],["DO","LU","MA","MI","JU","VI","SA"]],u,[["E","F","M","A","M","J","J","A","S","O","N","D"],["ene","feb","mar","abr","may","jun","jul","ago","sept","oct","nov","dic"],["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"]],u,[["a. C.","d. C."],u,["antes de Cristo","después de Cristo"]],1,[6,0],["d/M/yy","d MMM y","d 'de' MMMM 'de' y","EEEE, d 'de' MMMM 'de' y"],["H:mm","H:mm:ss","H:mm:ss z","H:mm:ss (zzzz)"],["{1}, {0}",u,u,u],[",",".",";","%","+","-","E","×","‰","∞","NaN",":"],["#,##0.###","#,##0 %","#,##0.00 ¤","#E0"],"EUR","€","euro",{"AUD":[u,"$"],"BRL":[u,"R$"],"BYN":[u,"р."],"CAD":[u,"$"],"CNY":[u,"¥"],"EGP":[u,"EGP"],"ESP":["₧"],"GBP":[u,"£"],"HKD":[u,"$"],"ILS":[u,"₪"],"INR":[u,"₹"],"JPY":[u,"¥"],"KRW":[u,"₩"],"MXN":[u,"$"],"NZD":[u,"$"],"PHP":[u,"₱"],"RON":[u,"L"],"THB":["฿"],"TWD":[u,"NT$"],"USD":["US$","$"],"XAF":[],"XCD":[u,"$"],"XOF":[]},"ltr", plural, [[["del mediodía","de la madrugada","de la mañana","de la tarde","de la noche"],u,u],[["mediodía","madrugada","mañana","tarde","noche"],u,u],["12:00",["00:00","06:00"],["06:00","12:00"],["12:00","20:00"],["20:00","24:00"]]]];
  })(globalThis);
    
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val, i = Math.floor(Math.abs(val)), v = val.toString().replace(/^[^.]*\.?/, '').length, e = parseInt(val.toString().replace(/^[^e]*(e([-+]?\d+))?/, '$2')) || 0;

if (i === 0 || i === 1)
    return 1;
if (e === 0 && (!(i === 0) && (i % 1000000 === 0 && v === 0)) || !(e >= 0 && e <= 5))
    return 4;
return 5;
}
    global.ng.common.locales['fr'] = ["fr",[["AM","PM"]],u,[["D","L","M","M","J","V","S"],["dim.","lun.","mar.","mer.","jeu.","ven.","sam."],["dimanche","lundi","mardi","mercredi","jeudi","vendredi","samedi"],["di","lu","ma","me","je","ve","sa"]],u,[["J","F","M","A","M","J","J","A","S","O","N","D"],["janv.","févr.","mars","avr.","mai","juin","juil.","août","sept.","oct.","nov.","déc."],["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"]],u,[["av. J.-C.","ap. J.-C."],u,["avant Jésus-Christ","après Jésus-Christ"]],1,[6,0],["dd/MM/y","d MMM y","d MMMM y","EEEE d MMMM y"],["HH:mm","HH:mm:ss","HH:mm:ss z","HH:mm:ss zzzz"],["{1} {0}","{1}, {0}",u,u],[","," ",";","%","+","-","E","×","‰","∞","NaN",":"],["#,##0.###","#,##0 %","#,##0.00 ¤","#E0"],"EUR","€","euro",{"ARS":["$AR","$"],"AUD":["$AU","$"],"BEF":["FB"],"BMD":["$BM","$"],"BND":["$BN","$"],"BYN":[u,"р."],"BZD":["$BZ","$"],"CAD":["$CA","$"],"CLP":["$CL","$"],"CNY":[u,"¥"],"COP":["$CO","$"],"CYP":["£CY"],"EGP":[u,"£E"],"FJD":["$FJ","$"],"FKP":["£FK","£"],"FRF":["F"],"GBP":["£GB","£"],"GIP":["£GI","£"],"HKD":[u,"$"],"IEP":["£IE"],"ILP":["£IL"],"ITL":["₤IT"],"JPY":[u,"¥"],"KMF":[u,"FC"],"LBP":["£LB","£L"],"MTP":["£MT"],"MXN":["$MX","$"],"NAD":["$NA","$"],"NIO":[u,"$C"],"NZD":["$NZ","$"],"PHP":[u,"₱"],"RHD":["$RH"],"RON":[u,"L"],"RWF":[u,"FR"],"SBD":["$SB","$"],"SGD":["$SG","$"],"SRD":["$SR","$"],"TOP":[u,"$T"],"TTD":["$TT","$"],"TWD":[u,"NT$"],"USD":["$US","$"],"UYU":["$UY","$"],"WST":["$WS"],"XCD":[u,"$"],"XPF":["FCFP"],"ZMW":[u,"Kw"]},"ltr", plural, [[["minuit","midi","mat.","ap.m.","soir","matin"],["minuit","midi","matin","après-midi","soir","matin"],["minuit","midi","du matin","de l’après-midi","du soir","du matin"]],[["minuit","midi","mat.","ap.m.","soir","matin"],u,["minuit","midi","matin","après-midi","soir","matin"]],["00:00","12:00",["04:00","12:00"],["12:00","18:00"],["18:00","24:00"],["00:00","04:00"]]]];
  })(globalThis);
    
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val, i = Math.floor(Math.abs(val)), v = val.toString().replace(/^[^.]*\.?/, '').length, e = parseInt(val.toString().replace(/^[^e]*(e([-+]?\d+))?/, '$2')) || 0;

if (i === 0 || i === 1)
    return 1;
if (e === 0 && (!(i === 0) && (i % 1000000 === 0 && v === 0)) || !(e >= 0 && e <= 5))
    return 4;
return 5;
}
    global.ng.common.locales['fr-ca'] = ["fr-CA",[["a","p"],["a.m.","p.m."]],[["a.m.","p.m."]],[["D","L","M","M","J","V","S"],["dim.","lun.","mar.","mer.","jeu.","ven.","sam."],["dimanche","lundi","mardi","mercredi","jeudi","vendredi","samedi"],["di","lu","ma","me","je","ve","sa"]],u,[["J","F","M","A","M","J","J","A","S","O","N","D"],["janv.","févr.","mars","avr.","mai","juin","juill.","août","sept.","oct.","nov.","déc."],["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"]],u,[["av. J.-C.","ap. J.-C."],u,["avant Jésus-Christ","après Jésus-Christ"]],0,[6,0],["y-MM-dd","d MMM y","d MMMM y","EEEE d MMMM y"],["HH 'h' mm","HH 'h' mm 'min' ss 's'","HH 'h' mm 'min' ss 's' z","HH 'h' mm 'min' ss 's' zzzz"],["{1} {0}","{1}, {0}",u,u],[","," ",";","%","+","-","E","×","‰","∞","NaN",":"],["#,##0.###","#,##0 %","#,##0.00 ¤","#E0"],"CAD","$","dollar canadien",{"AUD":["$ AU","$"],"BEF":["FB"],"BYN":[u,"Br"],"CAD":["$"],"CYP":["£CY"],"EGP":[u,"£E"],"FRF":["F"],"GEL":[u,"GEL"],"HKD":["$ HK","$"],"IEP":["£IE"],"ILP":["£IL"],"ILS":[u,"₪"],"INR":[u,"₹"],"ITL":["₤IT"],"KRW":[u,"₩"],"LBP":[u,"£L"],"MTP":["£MT"],"MXN":[u,"$"],"NZD":["$ NZ","$"],"PHP":[u,"₱"],"RHD":["$RH"],"RON":[u,"L"],"RWF":[u,"FR"],"SGD":["$ SG","$"],"TOP":[u,"$T"],"TWD":[u,"NT$"],"USD":["$ US","$"],"VND":[u,"₫"],"XAF":[],"XCD":[u,"$"],"XOF":[],"XPF":[]},"ltr", plural, [[["minuit","midi","mat.","après-midi","du soir","mat."],["minuit","midi","du mat.","après-midi","du soir","du mat."],["minuit","midi","du matin","de l’après-midi","du soir","du matin"]],[["minuit","midi","mat.","après-midi","du soir","mat."],["minuit","midi","mat.","après-midi","du soir","du mat."],["minuit","midi","matin","après-midi","soir","matin"]],["00:00","12:00",["04:00","12:00"],["12:00","18:00"],["18:00","24:00"],["00:00","04:00"]]]];
  })(globalThis);
    
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val, i = Math.floor(Math.abs(val)), v = val.toString().replace(/^[^.]*\.?/, '').length, e = parseInt(val.toString().replace(/^[^e]*(e([-+]?\d+))?/, '$2')) || 0;

if (i === 1 && v === 0)
    return 1;
if (e === 0 && (!(i === 0) && (i % 1000000 === 0 && v === 0)) || !(e >= 0 && e <= 5))
    return 4;
return 5;
}
    global.ng.common.locales['it'] = ["it",[["m.","p."],["AM","PM"]],u,[["D","L","M","M","G","V","S"],["dom","lun","mar","mer","gio","ven","sab"],["domenica","lunedì","martedì","mercoledì","giovedì","venerdì","sabato"],["dom","lun","mar","mer","gio","ven","sab"]],u,[["G","F","M","A","M","G","L","A","S","O","N","D"],["gen","feb","mar","apr","mag","giu","lug","ago","set","ott","nov","dic"],["gennaio","febbraio","marzo","aprile","maggio","giugno","luglio","agosto","settembre","ottobre","novembre","dicembre"]],u,[["aC","dC"],["a.C.","d.C."],["avanti Cristo","dopo Cristo"]],1,[6,0],["dd/MM/yy","d MMM y","d MMMM y","EEEE d MMMM y"],["HH:mm","HH:mm:ss","HH:mm:ss z","HH:mm:ss zzzz"],["{1}, {0}",u,"{1} {0}",u],[",",".",";","%","+","-","E","×","‰","∞","NaN",":"],["#,##0.###","#,##0%","#,##0.00 ¤","#E0"],"EUR","€","euro",{"BRL":[u,"R$"],"BYN":[u,"Br"],"EGP":[u,"£E"],"HKD":[u,"$"],"INR":[u,"₹"],"JPY":[u,"¥"],"KRW":[u,"₩"],"MXN":[u,"$"],"NOK":[u,"NKr"],"THB":["฿"],"TWD":[u,"NT$"],"USD":[u,"$"],"VND":[u,"₫"]},"ltr", plural, [[["mezzanotte","mezzogiorno","di mattina","di pomeriggio","di sera","di notte"],u,["mezzanotte","mezzogiorno","di mattina","del pomeriggio","di sera","di notte"]],[["mezzanotte","mezzogiorno","mattina","pomeriggio","sera","notte"],u,u],["00:00","12:00",["06:00","12:00"],["12:00","18:00"],["18:00","24:00"],["00:00","06:00"]]]];
  })(globalThis);
    
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val;

if (n === 1)
    return 1;
return 5;
}
    global.ng.common.locales['nb'] = ["nb",[["a","p"],["a.m.","p.m."]],[["a.m.","p.m."]],[["S","M","T","O","T","F","L"],["søn.","man.","tir.","ons.","tor.","fre.","lør."],["søndag","mandag","tirsdag","onsdag","torsdag","fredag","lørdag"],["sø.","ma.","ti.","on.","to.","fr.","lø."]],u,[["J","F","M","A","M","J","J","A","S","O","N","D"],["jan.","feb.","mars","apr.","mai","juni","juli","aug.","sep.","okt.","nov.","des."],["januar","februar","mars","april","mai","juni","juli","august","september","oktober","november","desember"]],[["J","F","M","A","M","J","J","A","S","O","N","D"],["jan","feb","mar","apr","mai","jun","jul","aug","sep","okt","nov","des"],["januar","februar","mars","april","mai","juni","juli","august","september","oktober","november","desember"]],[["f.Kr.","e.Kr."],u,["før Kristus","etter Kristus"]],1,[6,0],["dd.MM.y","d. MMM y","d. MMMM y","EEEE d. MMMM y"],["HH:mm","HH:mm:ss","HH:mm:ss z","HH:mm:ss zzzz"],["{1}, {0}",u,u,u],[","," ",";","%","+","−","E","×","‰","∞","NaN",":"],["#,##0.###","#,##0 %","#,##0.00 ¤;-#,##0.00 ¤","#E0"],"NOK","kr","norske kroner",{"AUD":[u,"$"],"BRL":[u,"R$"],"BYN":[u,"р."],"CAD":[u,"$"],"CNY":[u,"¥"],"HKD":[u,"$"],"ILS":[u,"₪"],"INR":[u,"₹"],"JPY":[u,"¥"],"KRW":[u,"₩"],"MXN":[u,"$"],"NOK":["kr"],"NZD":[u,"$"],"PHP":[u,"₱"],"RON":[u,"L"],"TWD":[u,"NT$"],"USD":[u,"$"],"VND":[u,"₫"],"XAF":[],"XCD":[u,"$"],"XPF":[],"XXX":[]},"ltr", plural, [[["mn.","mg.","fm.","em.","kv.","nt."],["midn.","morg.","form.","etterm.","kveld","natt"],["midnatt","på morgenen","på formiddagen","på ettermiddagen","på kvelden","på natten"]],[["mn.","mg.","fm.","em.","kv.","nt."],["midn.","morg.","form.","etterm.","kveld","natt"],["midnatt","morgen","formiddag","ettermiddag","kveld","natt"]],["00:00",["06:00","10:00"],["10:00","12:00"],["12:00","18:00"],["18:00","24:00"],["00:00","06:00"]]]];
  })(globalThis);
    
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val, i = Math.floor(Math.abs(val)), v = val.toString().replace(/^[^.]*\.?/, '').length;

if (i === 1 && v === 0)
    return 1;
return 5;
}
    global.ng.common.locales['nl'] = ["nl",[["a.m.","p.m."]],u,[["Z","M","D","W","D","V","Z"],["zo","ma","di","wo","do","vr","za"],["zondag","maandag","dinsdag","woensdag","donderdag","vrijdag","zaterdag"],["zo","ma","di","wo","do","vr","za"]],u,[["J","F","M","A","M","J","J","A","S","O","N","D"],["jan","feb","mrt","apr","mei","jun","jul","aug","sep","okt","nov","dec"],["januari","februari","maart","april","mei","juni","juli","augustus","september","oktober","november","december"]],u,[["v.C.","n.C."],["v.Chr.","n.Chr."],["voor Christus","na Christus"]],1,[6,0],["dd-MM-y","d MMM y","d MMMM y","EEEE d MMMM y"],["HH:mm","HH:mm:ss","HH:mm:ss z","HH:mm:ss zzzz"],["{1}, {0}",u,u,u],[",",".",";","%","+","-","E","×","‰","∞","NaN",":"],["#,##0.###","#,##0%","¤ #,##0.00;¤ -#,##0.00","#E0"],"EUR","€","Euro",{"AUD":["AU$","$"],"BYN":[u,"р."],"CAD":["C$","$"],"FJD":["FJ$","$"],"JPY":["JP¥","¥"],"PHP":[u,"₱"],"RUR":[u,"р."],"SBD":["SI$","$"],"THB":["฿"],"TWD":["NT$"],"USD":["US$","$"],"XCG":["Cg.","Cg"],"XPF":[],"XXX":[]},"ltr", plural, [[["middernacht","’s ochtends","’s middags","’s avonds","’s nachts"],u,u],[["middernacht","ochtend","middag","avond","nacht"],u,u],["00:00",["06:00","12:00"],["12:00","18:00"],["18:00","24:00"],["00:00","06:00"]]]];
  })(globalThis);
    
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val, i = Math.floor(Math.abs(val)), v = val.toString().replace(/^[^.]*\.?/, '').length;

if (i === 1 && v === 0)
    return 1;
if (v === 0 && (i % 10 === Math.floor(i % 10) && (i % 10 >= 2 && i % 10 <= 4) && !(i % 100 >= 12 && i % 100 <= 14)))
    return 3;
if (v === 0 && (!(i === 1) && (i % 10 === Math.floor(i % 10) && (i % 10 >= 0 && i % 10 <= 1))) || (v === 0 && (i % 10 === Math.floor(i % 10) && (i % 10 >= 5 && i % 10 <= 9)) || v === 0 && (i % 100 === Math.floor(i % 100) && (i % 100 >= 12 && i % 100 <= 14))))
    return 4;
return 5;
}
    global.ng.common.locales['pl'] = ["pl",[["a","p"],["AM","PM"]],u,[["n","p","w","ś","c","p","s"],["niedz.","pon.","wt.","śr.","czw.","pt.","sob."],["niedziela","poniedziałek","wtorek","środa","czwartek","piątek","sobota"],["nie","pon","wto","śro","czw","pią","sob"]],[["N","P","W","Ś","C","P","S"],["niedz.","pon.","wt.","śr.","czw.","pt.","sob."],["niedziela","poniedziałek","wtorek","środa","czwartek","piątek","sobota"],["nie","pon","wto","śro","czw","pią","sob"]],[["s","l","m","k","m","c","l","s","w","p","l","g"],["sty","lut","mar","kwi","maj","cze","lip","sie","wrz","paź","lis","gru"],["stycznia","lutego","marca","kwietnia","maja","czerwca","lipca","sierpnia","września","października","listopada","grudnia"]],[["S","L","M","K","M","C","L","S","W","P","L","G"],["sty","lut","mar","kwi","maj","cze","lip","sie","wrz","paź","lis","gru"],["styczeń","luty","marzec","kwiecień","maj","czerwiec","lipiec","sierpień","wrzesień","październik","listopad","grudzień"]],[["p.n.e.","n.e."],u,["przed naszą erą","naszej ery"]],1,[6,0],["d.MM.y","d MMM y","d MMMM y","EEEE, d MMMM y"],["HH:mm","HH:mm:ss","HH:mm:ss z","HH:mm:ss zzzz"],["{1}, {0}",u,"{1} {0}",u],[","," ",";","%","+","-","E","×","‰","∞","NaN",":"],["#,##0.###","#,##0%","#,##0.00 ¤","#E0"],"PLN","zł","złoty polski",{"AUD":[u,"$"],"CAD":[u,"$"],"CNY":[u,"¥"],"GBP":[u,"£"],"HKD":[u,"$"],"ILS":[u,"₪"],"INR":[u,"₹"],"JPY":[u,"¥"],"KRW":[u,"₩"],"MXN":[u,"$"],"NZD":[u,"$"],"PHP":[u,"₱"],"PLN":["zł"],"RON":[u,"lej"],"TWD":[u,"NT$"],"USD":[u,"$"],"VND":[u,"₫"]},"ltr", plural, [[["o półn.","w poł.","rano","przed poł.","po poł.","wiecz.","w nocy"],["o północy","w południe","rano","przed południem","po południu","wieczorem","w nocy"],u],[["półn.","poł.","rano","przedpoł.","popoł.","wiecz.","noc"],["północ","południe","rano","przedpołudnie","popołudnie","wieczór","noc"],u],["00:00","12:00",["06:00","10:00"],["10:00","12:00"],["12:00","18:00"],["18:00","21:00"],["21:00","06:00"]]]];
  })(globalThis);
    
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val, i = Math.floor(Math.abs(val)), v = val.toString().replace(/^[^.]*\.?/, '').length, e = parseInt(val.toString().replace(/^[^e]*(e([-+]?\d+))?/, '$2')) || 0;

if (i === Math.floor(i) && (i >= 0 && i <= 1))
    return 1;
if (e === 0 && (!(i === 0) && (i % 1000000 === 0 && v === 0)) || !(e >= 0 && e <= 5))
    return 4;
return 5;
}
    global.ng.common.locales['pt-pt'] = ["pt-PT",[["a.m.","p.m."],u,["da manhã","da tarde"]],[["a.m.","p.m."],u,["manhã","tarde"]],[["D","S","T","Q","Q","S","S"],["domingo","segunda","terça","quarta","quinta","sexta","sábado"],["domingo","segunda-feira","terça-feira","quarta-feira","quinta-feira","sexta-feira","sábado"],["dom.","seg.","ter.","qua.","qui.","sex.","sáb."]],u,[["J","F","M","A","M","J","J","A","S","O","N","D"],["jan.","fev.","mar.","abr.","mai.","jun.","jul.","ago.","set.","out.","nov.","dez."],["janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"]],u,[["a.C.","d.C."],u,["antes de Cristo","depois de Cristo"]],0,[6,0],["dd/MM/yy","dd/MM/y","d 'de' MMMM 'de' y","EEEE, d 'de' MMMM 'de' y"],["HH:mm","HH:mm:ss","HH:mm:ss z","HH:mm:ss zzzz"],["{1}, {0}",u,u,u],[","," ",";","%","+","-","E","×","‰","∞","NaN",":"],["#,##0.###","#,##0%","#,##0.00 ¤","#E0"],"EUR","€","euro",{"AUD":["AU$","$"],"BYN":[u,"р."],"JPY":["JP¥","¥"],"PHP":[u,"₱"],"PTE":["​"],"RON":[u,"L"],"THB":["฿"],"TWD":["NT$"],"USD":["US$","$"]},"ltr", plural, [[["meia-noite","meio-dia","manhã","tarde","noite","madrugada"],["meia-noite","meio-dia","da manhã","da tarde","da noite","da madrugada"],u],[["meia-noite","meio-dia","manhã","tarde","noite","madrugada"],u,u],["00:00","12:00",["06:00","12:00"],["12:00","19:00"],["19:00","24:00"],["00:00","06:00"]]]];
  })(globalThis);
    
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val, i = Math.floor(Math.abs(val)), v = val.toString().replace(/^[^.]*\.?/, '').length;

if (v === 0 && (i % 10 === 1 && !(i % 100 === 11)))
    return 1;
if (v === 0 && (i % 10 === Math.floor(i % 10) && (i % 10 >= 2 && i % 10 <= 4) && !(i % 100 >= 12 && i % 100 <= 14)))
    return 3;
if (v === 0 && i % 10 === 0 || (v === 0 && (i % 10 === Math.floor(i % 10) && (i % 10 >= 5 && i % 10 <= 9)) || v === 0 && (i % 100 === Math.floor(i % 100) && (i % 100 >= 11 && i % 100 <= 14))))
    return 4;
return 5;
}
    global.ng.common.locales['ru'] = ["ru",[["AM","PM"]],u,[["В","П","В","С","Ч","П","С"],["вс","пн","вт","ср","чт","пт","сб"],["воскресенье","понедельник","вторник","среда","четверг","пятница","суббота"],["вс","пн","вт","ср","чт","пт","сб"]],u,[["Я","Ф","М","А","М","И","И","А","С","О","Н","Д"],["янв.","февр.","мар.","апр.","мая","июн.","июл.","авг.","сент.","окт.","нояб.","дек."],["января","февраля","марта","апреля","мая","июня","июля","августа","сентября","октября","ноября","декабря"]],[["Я","Ф","М","А","М","И","И","А","С","О","Н","Д"],["янв.","февр.","март","апр.","май","июнь","июль","авг.","сент.","окт.","нояб.","дек."],["январь","февраль","март","апрель","май","июнь","июль","август","сентябрь","октябрь","ноябрь","декабрь"]],[["до н.э.","н.э."],["до н. э.","н. э."],["до Рождества Христова","от Рождества Христова"]],1,[6,0],["dd.MM.y","d MMM y 'г'.","d MMMM y 'г'.","EEEE, d MMMM y 'г'."],["HH:mm","HH:mm:ss","HH:mm:ss z","HH:mm:ss zzzz"],["{1}, {0}",u,u,u],[","," ",";","%","+","-","E","×","‰","∞","не число",":"],["#,##0.###","#,##0 %","#,##0.00 ¤","#E0"],"RUB","₽","российский рубль",{"BYN":[u,"р."],"GEL":[u,"ლ"],"PHP":[u,"₱"],"RON":[u,"L"],"RUB":["₽"],"RUR":["р."],"THB":["฿"],"TMT":["ТМТ"],"TWD":["NT$"],"UAH":["₴"],"XXX":["XXXX"]},"ltr", plural, [[["полн.","полд.","утра","дня","веч.","ночи"],["полн.","полд.","утра","дня","вечера","ночи"],["полночь","полдень","утра","дня","вечера","ночи"]],[["полн.","полд.","утро","день","веч.","ночь"],u,["полночь","полдень","утро","день","вечер","ночь"]],["00:00","12:00",["04:00","12:00"],["12:00","18:00"],["18:00","22:00"],["22:00","04:00"]]]];
  })(globalThis);
    
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val, i = Math.floor(Math.abs(val)), v = val.toString().replace(/^[^.]*\.?/, '').length;

if (i === 1 && v === 0)
    return 1;
return 5;
}
    global.ng.common.locales['sv'] = ["sv",[["fm","em"]],[["fm","em"],u,["förmiddag","eftermiddag"]],[["S","M","T","O","T","F","L"],["sön","mån","tis","ons","tors","fre","lör"],["söndag","måndag","tisdag","onsdag","torsdag","fredag","lördag"],["sö","må","ti","on","to","fr","lö"]],u,[["J","F","M","A","M","J","J","A","S","O","N","D"],["jan.","feb.","mars","apr.","maj","juni","juli","aug.","sep.","okt.","nov.","dec."],["januari","februari","mars","april","maj","juni","juli","augusti","september","oktober","november","december"]],u,[["f.Kr.","e.Kr."],u,["före Kristus","efter Kristus"]],1,[6,0],["y-MM-dd","d MMM y","d MMMM y","EEEE d MMMM y"],["HH:mm","HH:mm:ss","HH:mm:ss z","HH:mm:ss zzzz"],["{1} {0}",u,u,u],[","," ",";","%","+","−","×10^","×","‰","∞","NaN",":"],["#,##0.###","#,##0 %","#,##0.00 ¤","#E0"],"SEK","kr","svensk krona",{"AUD":[u,"$"],"BBD":["Bds$","$"],"BMD":["BM$","$"],"BRL":["BR$","R$"],"BSD":["BS$","$"],"BYN":[u,"р."],"BZD":["BZ$","$"],"CNY":[u,"¥"],"DKK":["Dkr","kr"],"DOP":["RD$","$"],"EEK":["Ekr"],"EGP":["EG£","E£"],"ESP":[u,"ESP"],"GBP":[u,"£"],"HKD":[u,"$"],"IEP":["IE£"],"INR":[u,"₹"],"ISK":["Ikr","kr"],"JMD":["JM$","$"],"JPY":[u,"¥"],"KRW":[u,"₩"],"NOK":["Nkr","kr"],"NZD":[u,"$"],"PHP":[u,"₱"],"RON":[u,"L"],"SEK":["kr"],"TWD":[u,"NT$"],"USD":["US$","$"],"VND":[u,"₫"]},"ltr", plural, [[["midn.","på morg.","på förm.","på efterm.","på kvällen","på natten"],["midnatt","på morg.","på förm.","på efterm.","på kvällen","på natten"],["midnatt","på morgonen","på förmiddagen","på eftermiddagen","på kvällen","på natten"]],[["midn.","morg.","förm.","efterm.","kväll","natt"],["midnatt","morgon","förm.","efterm.","kväll","natt"],["midnatt","morgon","förmiddag","eftermiddag","kväll","natt"]],["00:00",["05:00","10:00"],["10:00","12:00"],["12:00","18:00"],["18:00","24:00"],["00:00","05:00"]]]];
  })(globalThis);
    
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

// THIS CODE IS GENERATED - DO NOT MODIFY.
  (function(global) {
    global.ng ??= {};
    global.ng.common ??= {};
    global.ng.common.locales ??= {};
    const u = undefined;
    function plural(val) {
const n = val;

return 5;
}
    global.ng.common.locales['zh'] = ["zh",[["上午","下午"]],u,[["日","一","二","三","四","五","六"],["周日","周一","周二","周三","周四","周五","周六"],["星期日","星期一","星期二","星期三","星期四","星期五","星期六"],["周日","周一","周二","周三","周四","周五","周六"]],u,[["1","2","3","4","5","6","7","8","9","10","11","12"],["1月","2月","3月","4月","5月","6月","7月","8月","9月","10月","11月","12月"],["一月","二月","三月","四月","五月","六月","七月","八月","九月","十月","十一月","十二月"]],u,[["公元前","公元"]],1,[6,0],["y/M/d","y年M月d日",u,"y年M月d日EEEE"],["HH:mm","HH:mm:ss","z HH:mm:ss","zzzz HH:mm:ss"],["{1} {0}",u,u,u],[".",",",";","%","+","-","E","×","‰","∞","NaN",":"],["#,##0.###","#,##0%","¤#,##0.00","#E0"],"CNY","¥","人民币",{"AUD":["AU$","$"],"BYN":[u,"р."],"CNY":["¥"],"ILR":["ILS"],"JPY":["JP¥","¥"],"PHP":[u,"₱"],"RUR":[u,"р."],"TWD":["NT$"],"USD":["US$","$"],"XXX":[]},"ltr", plural, [[["午夜","早上","上午","中午","下午","晚上","凌晨"],u,["午夜","清晨","上午","中午","下午","晚上","凌晨"]],[["午夜","早上","上午","中午","下午","晚上","凌晨"],u,u],["00:00",["05:00","08:00"],["08:00","12:00"],["12:00","13:00"],["13:00","19:00"],["19:00","24:00"],["00:00","05:00"]]]];
  })(globalThis);
    